/* eslint-disable */
/* Bazooka Vault — access limited to @bazookabreaks.com until June 18 */
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { auth, db, googleProvider, storage } from "./firebase";
import { signInWithPopup, signInWithRedirect, getRedirectResult, GoogleAuthProvider, signOut, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, updateProfile, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { collection, doc, setDoc, deleteDoc, onSnapshot, query, orderBy, where, getDoc, getDocs, getDocFromServer, deleteField, arrayUnion, arrayRemove, updateDoc, limit, writeBatch, documentId } from "firebase/firestore";
import { ref, uploadBytes, uploadString, getDownloadURL } from "firebase/storage";

// --- Early-access allowlist ---------------------------------------------
// Until June 18, only @bazookabreaks.com team + these specific emails can get in.
// Add any individual emails you want to grant early access to (lowercase).
// Ensure the Bazooka design tokens (--bz-ink etc.) are ALWAYS defined document-wide, on every
// page/route — otherwise any `var(--bz-ink)` text falls back to a dark default and reads as
// black-on-black. Injected once at module load, before any component renders.
if (typeof document !== "undefined" && !document.getElementById("bz-global-tokens")) {
  const _tok = document.createElement("style");
  _tok.id = "bz-global-tokens";
  _tok.textContent = `:root{
    --bz-bg:#0b0709; --bz-s1:#130d11; --bz-s2:#1a1218; --bz-s3:#241820;
    --bz-line:rgba(255,255,255,0.06); --bz-line-2:rgba(255,255,255,0.11);
    --bz-ink:#f6eef2; --bz-ink-2:#c9bdc4; --bz-ink-3:#9a8a94;
    --bz-pink:#E8317A; --bz-pink-hot:#ff4d94; --bz-pink-dim:rgba(232,49,122,0.14); --bz-pink-line:rgba(232,49,122,0.30);
    --bz-green:#34d399; --bz-amber:#fbbf24; --bz-blue:#7c9cff; --bz-violet:#a78bfa;
    --bz-radius:16px; --bz-radius-sm:11px;
  }`;
  document.head.appendChild(_tok);
}

// Downscale + re-encode a user-uploaded image before it hits Firebase Storage.
// A straight-from-phone photo can be 3-5MB; this typically brings it to ~150-400KB, which
// massively cuts Storage cost, bandwidth, and load time at scale. Falls back to the original
// file if anything goes wrong (so uploads never break).
async function compressImage(file, { maxDim = 1400, quality = 0.82 } = {}) {
  try {
    if (!file || !file.type || !file.type.startsWith("image/")) return file;
    if (file.type === "image/gif") return file; // don't flatten animations
    const dataUrl = await new Promise((res, rej) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
    });
    const img = await new Promise((res, rej) => {
      const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = dataUrl;
    });
    let { width, height } = img;
    if (width <= maxDim && height <= maxDim && file.size < 400 * 1024) return file; // already small
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const w = Math.round(width * scale), h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;
    // If compression somehow made it bigger, keep the original.
    if (blob.size >= file.size) return file;
    return new File([blob], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch (e) {
    console.warn("image compression failed, using original:", e);
    return file;
  }
}

const EARLY_ACCESS_EMAILS = [
  "christopher.e.ohara@gmail.com",
  "sydxelyse@icloud.com",
  "matthewjessell@gmail.com",
  "mikerfried@aol.com",
];
// Dynamic allowlist loaded from Firestore (config/early_access) — editable in the admin UI
// without a redeploy. Once the Firestore doc exists it is the SOLE source of truth (so removals
// stick); until then, the founding emails above act as the default seed.
let DYNAMIC_EARLY_ACCESS = [];
let DYNAMIC_EARLY_ACCESS_LOADED = false;
function setDynamicEarlyAccess(list, loaded) {
  DYNAMIC_EARLY_ACCESS = (list||[]).map(e=>String(e).toLowerCase().trim()).filter(Boolean);
  if (loaded) DYNAMIC_EARLY_ACCESS_LOADED = true;
}
function hasEarlyAccess(email) {
  const e = (email || "").toLowerCase().trim();
  if (!e) return false;
  if (e.endsWith("@bazookabreaks.com")) return true;
  // Once the Firestore list has loaded, it is authoritative (includes any removals).
  if (DYNAMIC_EARLY_ACCESS_LOADED) return DYNAMIC_EARLY_ACCESS.includes(e);
  // Before it loads, fall back to the founding seed + any cached list.
  return EARLY_ACCESS_EMAILS.includes(e) || DYNAMIC_EARLY_ACCESS.includes(e);
}
// Seed the dynamic list from localStorage immediately (so the gate has it on first paint),
// then refresh from Firestore in the background.
try { const _cached = JSON.parse(localStorage.getItem("early_access_v1")||"[]"); if(Array.isArray(_cached)) setDynamicEarlyAccess(_cached); } catch(e){}
// ------------------------------------------------------------------------

// Pre-fetch BoJax card images for loading screen — runs once at module load
const LOADING_CARD_IMAGES = { urls: [], loaded: false };
(async () => {
  try {
    const CACHE_KEY = "bazooka_loading_imgs_v1";
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { urls, ts } = JSON.parse(cached);
      if (urls?.length > 0 && Date.now() - ts < 7*24*60*60*1000) {
        LOADING_CARD_IMAGES.urls = urls;
        LOADING_CARD_IMAGES.loaded = true;
        return;
      }
    }
    // Don't fetch these on a cold first load. On a first visit the app is already pulling the ~12MB
    // card list, and this query — plus the 60 card images it points at — competes for the same
    // bandwidth. The flying-card animation is a nicety; the card list is the product. So fetch it
    // only once the checklist is cached: a returning user gets the animation, a new user gets all
    // the bandwidth for the thing they're actually waiting for.
    let _hasCards = false;
    try { _hasCards = !!localStorage.getItem("boba_checklist_cache_v3"); } catch(e) {}
    if (!_hasCards) return;
    const snap = await getDocs(query(collection(db,"boba_checklist"), where("hero","==","BoJax")));
    const urls = snap.docs.map(d=>d.data().imageUrl).filter(Boolean).slice(0,60);
    if (urls.length > 0) {
      LOADING_CARD_IMAGES.urls = urls;
      LOADING_CARD_IMAGES.loaded = true;
      localStorage.setItem(CACHE_KEY, JSON.stringify({ urls, ts: Date.now() }));
    }
  } catch(e) {}
})();

const CARD_TYPES = ["Giveaway Cards","Insurance Cards","First-Timer Cards","Chaser Cards"];


const POOL_TYPES  = ["Giveaway Cards","Insurance Cards"]; // bulk pools

// ── DECK FORMATS (BoBA 2026 National) ────────────────────────────────────────
// Every format's rules in one place, so the builder, the legality check and the UI can't drift
// apart. Anything not listed uses the default 60 cards / max 6 per power level.
//
//   size         deck size (max). Spec+ is 70; Blast is 30.
//   powerCap     max power for any one Hero (Spec's 160). Absent = uncapped.
//   perPower     max Heroes sharing a single power level.
//   totalPower   cap on the deck's SUMMED power (Elite's 8,250).
//   set          restrict to a single set (Blast → 2025 Alpha Blast).
//   excludeSets  banned sets (Elite bans Trainer cards, i.e. the Battle Trainer Kit).
//   treatment    restrict to one treatment (Power Glove).
//   weapon       restrict to one weapon (Brawl).
//   unique       no duplicate cards at all (Power Glove).
//   minTreat     minimum number of cards per named treatment (GG HiLo needs 10 of each).
//   coreSize/coreCap/highTiers → Spec+ only: a COMPLETE 60-card Spec-legal core is required
//                before any high-power Hero counts, then up to 10 more with a hard cap at each
//                power level (165 ×2, 170 ×2, one each of 175–200, nothing above 200).
const DECK_FORMATS = {
  none:        { label:"No Restrictions",         short:"—",           size:60, perPower:6 },
  spec:        { label:"Spec (≤160 power)",       short:"SPEC",        size:60, perPower:6, powerCap:160 },
  apex:        { label:"Apex (no power cap)",     short:"APEX",        size:60, perPower:6 },
  elite:       { label:"Elite (8,250 total)",     short:"ELITE",       size:60, perPower:6, totalPower:8250,
                 excludeSets:["2024 Battle Trainer Kit"] },
  specplus:    { label:"Spec+ (60 core + 10)",    short:"SPEC+",       size:70, perPower:6,
                 coreSize:60, coreCap:160,
                 highTiers:{ 165:2, 170:2, 175:1, 180:1, 185:1, 190:1, 195:1, 200:1 } },
  blast:       { label:"Blast (30 cards)",        short:"BLAST",       size:30, perPower:3, set:"2025 Alpha Blast" },
  powerglove:  { label:"Power Glove (60 unique)", short:"POWER GLOVE", size:60, perPower:6, treatment:"Power Glove", unique:true },
  brawl:       { label:"Brawl",                   short:"BRAWL",       size:60, perPower:6, weapon:"Brawl" },
  // GG HiLo — the deck may ONLY contain cards from three groups, with at least 10 of each:
  //   1. Grandma's Linoleum Battlefoil    (treatment)
  //   2. Great Grandma Linoleum Battlefoil (treatment)
  //   3. ANY card with the Gum weapon      (weapon — so Inspired Ink Gum counts, not just Bubblegum)
  // Expressed as groups rather than treatments because #3 is a weapon match, not a treatment match.
  gghilo:      { label:"GG HiLo (Captain)",       short:"GG CAPT",     size:60, perPower:6,
                 groups:[
                   { id:"grandma",      label:"Grandma's Linoleum",       min:10, match:c=>(c.treatment||"")==="Grandma's Linoleum Battlefoil" },
                   { id:"greatgrandma", label:"Great Grandma Linoleum",   min:10, match:c=>(c.treatment||"")==="Great Grandma Linoleum Battlefoil" },
                   { id:"gum",          label:"Gum weapon (any insert)",  min:10, match:c=>canonWeapon(c.weapon)==="Gum" },
                 ] },
  // GG HiLo (Coach) — identical legal pool to Captain, but the auto-builder fills with your LOWEST
  // power cards instead of your highest. The `lowPower` flag flips the power-preference sorts.
  gghilo_coach:{ label:"GG HiLo (Coach)",         short:"GG COACH",    size:60, perPower:6, lowPower:true,
    groups:[
      { id:"grandma",      label:"Grandma's Linoleum",       min:10, match:c=>(c.treatment||"")==="Grandma's Linoleum Battlefoil" },
      { id:"greatgrandma", label:"Great Grandma Linoleum",   min:10, match:c=>(c.treatment||"")==="Great Grandma Linoleum Battlefoil" },
      { id:"gum",          label:"Gum weapon (any insert)",  min:10, match:c=>canonWeapon(c.weapon)==="Gum" },
    ] },
  // Tecmo HiLo \u2014 Tecmo Bowl cards only, no power cap in either direction. The Captain/Coach split is
  // the same idea as GG HiLo: identical legal pool, opposite goal. Captain wants the highest total
  // power it can field; Coach wants the lowest, which the `lowPower` flag drives by flipping every
  // power-preference sort in the auto-builder.
  tecmohilo:      { label:"Tecmo HiLo (Captain)", short:"TECMO CAPT",  size:60, perPower:6, set:"Tecmo Bowl" },
  tecmohilo_coach:{ label:"Tecmo HiLo (Coach)",   short:"TECMO COACH", size:60, perPower:6, set:"Tecmo Bowl", lowPower:true },
  // Madness: start from a 60-card Spec deck. Every 10 matching Inserts (= treatments) unlocks one
  // Apex Hero (>160), max 6. Four different Foil Hot Dogs unlock four more. Optimised = 70.
  apexmadness: { label:"Apex Madness",            short:"MADNESS",     size:70, perPower:6,
                 coreCap:160, insertUnlock:10, maxInsertUnlocks:6, hotDogUnlocks:4 },
};
const fmtOf = t => DECK_FORMATS[t] || DECK_FORMATS.none;
// Apex Madness counts "10 core unlocks 1 apex" PER INSERT. Most treatments are their own insert, but
// Inspired Ink is a single insert with several finishes (Battlefoil, Metallic Battlefoil, Superfoil,
// Bubble Gum, Magic Gum…). Counting those separately meant 6/10 + 4/10 instead of one unlocked 10/10.
// This collapses every Inspired Ink variant to one key; everything else keeps its own treatment name.
// The four colour Battlefoils are ONE insert in Madness, so ten cards spread across green, orange,
// blue and pink is a complete set of ten \u2014 not four part-finished piles that never unlock anything.
// Deliberately an explicit list rather than a "<colour> Battlefoil" pattern: a pattern would also
// swallow Silver Battlefoil and any future colour, and those are their own inserts.
const COLOR_BATTLEFOILS = new Set([
  "green battlefoil", "orange battlefoil", "blue battlefoil", "pink battlefoil",
]);
// Accepts either a treatment string or a whole card. It needs the card for set-level grouping:
// everything in Alpha Blast is a single insert regardless of its treatment, and the treatment
// alone cannot tell you that. Callers that only have a string still work unchanged.
const insertKey = (arg) => {
  const isCard = arg && typeof arg === "object";
  const t = String((isCard ? arg.treatment : arg) || "").toLowerCase().trim();
  if (isCard) {
    const set = String(arg.setName || "").toLowerCase();
    // Whole-set inserts: every card in the set counts toward ONE unlock pile, whatever its finish.
    if (set.includes("alpha blast")) return "alpha blast";
  }
  if (t.includes("inspired ink")) return "inspired ink";
  if (COLOR_BATTLEFOILS.has(t)) return "color battlefoil";
  return t;
};
// Display name for an insert key — "inspired ink" shows as a single combined insert.
const insertLabel = (key, fallback) =>
  key === "inspired ink"     ? "Inspired Ink (all)" :
  key === "color battlefoil" ? "Color Battlefoils (all)" :
  key === "alpha blast"      ? "Alpha Blast (all)" :
  (fallback || key);
// Plain "Battlefoil" is the BASE foil parallel, not an insert — it earns no Apex unlock in Madness
// and can't be one of the six inserts a core is built from. Named foils (Blue Battlefoil, Inspired
// Ink Battlefoil, 80's Rad Battlefoil…) are real inserts and still count.
const NON_INSERT_TREATMENTS = new Set(["battlefoil", "base", "paper", ""]);
// Takes a treatment string OR a card \u2014 insertKey handles both, and set-level grouping needs the
// card. A whole-set insert like Alpha Blast is always a real insert, never a base parallel.
const isInsertTreatment = (arg) => !NON_INSERT_TREATMENTS.has(insertKey(arg));

// Only Plays and Bonus Plays actually have a play cost. The DBS checklist import writes a cost
// column for every row, so ordinary heroes ended up carrying playCost:0 and rendering a stray
// "Cost: 0" chip that means nothing. Gate the display on BOTH the treatment and a real value.
function hasPlayCost(card) {
  if (!card) return false;
  const v = card.playCost;
  if (v === undefined || v === null || v === "") return false;
  const t = String(card.treatment || "").toLowerCase();
  if (!/\bplay\b|\bplays\b/.test(t)) return false;   // "Play", "Bonus Play", "Bonus Plays"
  const n = parseFloat(v);
  return !(isNaN(n) && String(v).trim() === "");
}
// Cards that are not Heroes: Plays, Bonus Plays and Hot Dogs. They live in your collection and are
// legal to hold, but they do not fill a Hero slot \u2014 so the auto-builder must not spend deck slots on
// them. Matched on the treatment, with word boundaries so a hero named e.g. "Playmaker" is not caught.
function isSupportCard(card) {
  const t = String(card?.treatment || "").toLowerCase();
  if (!t) return false;
  return /\bplay\b|\bplays\b|\bhot dog\b|\bhot dogs\b/.test(t);
}
const INDIV_TYPES = ["First-Timer Cards","Chaser Cards"];  // individual tracking
const BREAKERS = ["Dev","Dre","Krystal","BigU","Vinny","Stephen"];
// Remote breakers front their own supplies/shipping and get reimbursed — same
// treatment as BigU across recap, commission math, True Net, and the report.
const REMOTE_BREAKERS = ["bigu","vinny","stephen"];

// Internal business collections — everything the ops dashboard runs on. Used by the
// admin "Export Internal Data (JSON)" button to pull a full client-side snapshot.
const INTERNAL_EXPORT_COLLECTIONS = [
  "streams","planned_streams","stream_templates","breaks","breaker_vacations","product_usage",
  "follower_snapshots","cash_expenses","historical_data","pay_stubs","sku_price_history",
  "inventory","card_pools","supply_links","shipments","shipping_issues","missing_cards",
  "buyers","directory","comps","quotes","quotes_private","negotiation_history","deal_threads",
  "market_sales","market_offers","chases","chase_submissions","super_claims","player_notes",
  "config","meta","bug_reports","csv_imports",
];

// Pull every internal collection client-side and download ONE FILE PER COLLECTION,
// each ready to import straight into Base44 (a plain array of records = one table).
// Files are kept under ~8MB (auto-chunked if a collection is huge), so nothing hits
// Base44's 10MB import cap. Firestore Timestamps are converted to ISO date strings
// so Base44 reads them correctly. No Terminal, no service account — runs in-browser
// with the signed-in admin's permissions. Downloads a _manifest.json with counts so
// you can verify every table imported completely (counts should match in Base44).
const B44_MAX_BYTES = 8 * 1024 * 1024;

function b44Plain(value) {
  if (value === null || value === undefined) return value;
  // Firestore Timestamp → ISO string (Base44 can't read the timestamp object).
  if (value && typeof value.toDate === "function") { try { return value.toDate().toISOString(); } catch (e) {} }
  if (Array.isArray(value)) return value.map(b44Plain);
  if (typeof value === "object") {
    const out = {};
    for (const k of Object.keys(value)) out[k] = b44Plain(value[k]);
    return out;
  }
  return value;
}

function b44Download(filename, dataObj) {
  const blob = new Blob([JSON.stringify(dataObj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

async function exportInternalDataJson(setStatus, collections) {
  const list = collections && collections.length ? collections : INTERNAL_EXPORT_COLLECTIONS;
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const manifest = { exportedAt: new Date().toISOString(), forBase44: true, collections: {} };
  let totalDocs = 0, fileCount = 0;

  for (const name of list) {
    if (setStatus) setStatus(`Exporting ${name}…`);
    try {
      const snap = await getDocs(collection(db, name));
      const records = snap.docs.map(d => ({ id: d.id, ...b44Plain(d.data()) }));
      totalDocs += records.length;

      if (records.length === 0) { manifest.collections[name] = { records: 0, files: 0 }; continue; }

      // Chunk by size so no file exceeds Base44's cap.
      const chunks = [];
      let cur = [], curBytes = 2;
      for (const rec of records) {
        const b = new Blob([JSON.stringify(rec)]).size + 1;
        if (cur.length && curBytes + b > B44_MAX_BYTES) { chunks.push(cur); cur = []; curBytes = 2; }
        cur.push(rec); curBytes += b;
      }
      if (cur.length) chunks.push(cur);

      if (chunks.length === 1) {
        b44Download(`${name}.json`, chunks[0]);
        fileCount++;
      } else {
        chunks.forEach((ch, i) => { b44Download(`${name}.part-${String(i+1).padStart(2,"0")}.json`, ch); fileCount++; });
      }
      manifest.collections[name] = { records: records.length, files: chunks.length };
    } catch (e) {
      manifest.collections[name] = { error: e.message };
    }
  }

  manifest.totalRecords = totalDocs;
  if (setStatus) setStatus(`Exporting manifest…`);
  b44Download(`_manifest-${stamp}.json`, manifest);
  if (setStatus) setStatus(`✓ ${totalDocs} records · ${fileCount} files`);
  return manifest;
}
function isRemoteBreaker(name){ return REMOTE_BREAKERS.includes((name||"").toLowerCase().replace(/\s+/g,"")); }
// Remote breakers front their own supplies and get reimbursed — but not for everything.
//  • Top loaders: Bazooka provides these to everyone. Never reimbursed.
//  • Magpros: Bazooka provides them to Stephen. Reimbursed for BigU and Vinny only.
function reimbursesMagpros(name){
  const n = (name||"").toLowerCase().replace(/\s+/g,"");
  return isRemoteBreaker(n) && n !== "stephen";
}
//  • Packaging: reimbursed for remote breakers EXCEPT Stephen (Bazooka covers his
//    packaging directly, so it's not part of his end-of-month reimbursement).
function reimbursesPackaging(name){
  const n = (name||"").toLowerCase().replace(/\s+/g,"");
  return isRemoteBreaker(n) && n !== "stephen";
}
// Resolve which breaker a logged-in user is — tolerant of display-name vs breaker-name
// differences (e.g. "Big U" vs "BigU"), checking first name, full name, and email.
function resolveBreaker(user) {
  const norm = x => String(x||"").replace(/\s+/g,"").toLowerCase();
  const first = norm(user?.displayName?.split(" ")[0]);
  const dn = norm(user?.displayName);
  const em = norm(user?.email);
  return BREAKERS.find(b => {
    const nb = norm(b);
    return first===nb || first.includes(nb) || nb.includes(first) || dn.includes(nb) || em.includes(nb);
  });
}
const LOCATIONS = ["BZKA HQ","BIGU HQ","VINNY HQ","STEPHEN HQ"];
// Which HQ each breaker's cards live at — remote streamers can only log chasers
// physically at their location.
const BREAKER_LOCATION = {
  dev:"BZKA HQ", dre:"BZKA HQ", krystal:"BZKA HQ",
  bigu:"BIGU HQ", vinny:"VINNY HQ", stephen:"STEPHEN HQ",
};
function locationForBreaker(name){
  return BREAKER_LOCATION[(name||"").toLowerCase().replace(/\s+/g,"")] || null;
}
const WOTF_SETS = ["Dragon Box","Collector Booster","Play Booster","Wonders of The First"];
const BOBA_SETS = ["Alpha Edition","Alpha Update","Griffey 2026","Tecmo Bowl"];

// Some cards were printed in more than one release with the SAME card number, so the checklist
// cannot tell them apart on its own. Alpha Blast Hot Dogs are the known case: an original Alpha
// print and an Alpha Update reprint share a number, and collectors care which they hold.
//
// Rather than fork the catalog into near-duplicate documents, the version is recorded PER COPY.
// You can own two originals and one update under a single card, and the detail view shows the
// split. Deliberately a narrow list \u2014 prompting on every card would make adding cards a chore.
const CARD_VERSIONS = [
  {
    match: c => /alpha blast/i.test(c?.setName||"") && /hot dog/i.test(c?.treatment||""),
    label: "Which print is it?",
    options: ["Alpha", "Alpha Update"],
    help: "Alpha Blast Hot Dogs were printed twice with the same card number.",
  },
];
function versionSpecFor(card) {
  if (!card) return null;
  return CARD_VERSIONS.find(v => { try { return v.match(card); } catch { return false; } }) || null;
}

// ── SINGLE CANONICAL STREAM CALC ─────────────────────────────────────────────
// A stream is "singles" (100% pass-through) if flagged either way — via the
// isSinglesShow boolean or the sessionType dropdown. Keep these in sync.
function isSinglesStream(s){ return !!(s && (s.isSinglesShow || s.sessionType === "singles")); }
function calcStreamRaw(s, targetBreaker=null) {
  const gross        = parseFloat(s.grossRevenue)||0;
  const fees         = parseFloat(s.whatnotFees)||0;
  const coupons      = parseFloat(s.coupons)||0;
  const isSingles     = !!(s.isSinglesShow) || s.sessionType === "singles";
  const streamExp    = (parseFloat(s.whatnotPromo)||0)+(parseFloat(s.magpros)||0)+(parseFloat(s.packagingMaterial)||0)+(parseFloat(s.topLoaders)||0)+(parseFloat(s.chaserCards)||0);
  const splitBase    = gross - fees - coupons;
  const externalCh   = !!s.externalChannel;

  // Singles Show: 100% of net goes to breaker, no 70/30 split
  const bazNet       = isSingles ? 0 : splitBase * 0.30;
  const imcNet       = isSingles ? 0 : externalCh ? 0 : splitBase * 0.70;
  const collabAmt    = 0;
  const bazOwnShare  = isSingles ? 0 : bazNet;
  const isBigU        = isRemoteBreaker(s.breaker);
  // Remote breakers (BigU, Vinny, Stephen) front their own supplies, so those costs are NOT
  // deducted from their commission — they're settled through the reimbursement below instead.
  // Still reported to IMC for the 70% reimbursement.
  //
  // Note this is the DEDUCTION side. What actually gets paid back is decided separately in
  // biguReimb: top loaders are supplied by Bazooka and never reimbursed, and Stephen's magpros
  // are supplied too. Either way they don't come out of anyone's commission.
  const repExpBase    = isBigU
    ? (parseFloat(s.whatnotPromo)||0) + (parseFloat(s.chaserCards)||0)
    : streamExp;
  const rate         = isSingles ? 1 : getRate(s);
  const commAmt      = isSingles ? splitBase : bazOwnShare * rate;
  const repExpShare  = isSingles ? 0 : repExpBase * (rate * 0.30);
  const bazExpShare  = isSingles ? 0 : streamExp * ((1-rate) * 0.30);
  const tips         = parseFloat(s.tips)||0;
  const salesBonus   = parseFloat(s.salesBonus)||0;
  const eventStaffArr = Array.isArray(s.eventStaff) ? s.eventStaff : [];
  const eventStaffAmt = isSingles ? 0 : eventStaffArr.reduce((sum,_)=>sum+Math.min(1000, bazOwnShare*0.15), 0);
  const imcReimb      = isSingles ? 0 : externalCh ? 0 : streamExp * 0.70;
  const imcDirectReimb = parseFloat(s.imcReimbursement)||0;
  const splitPct      = s.splitRep ? parseFloat(s.splitPct||50)/100 : 1;
  const primaryCommAmt = isSingles ? splitBase : (s.splitRep ? commAmt*splitPct : commAmt);
  const splitRepAmt    = s.splitRep ? commAmt*(1-splitPct) : 0;
  const biguCardCosts = isBigU ? (parseFloat(s.biguGiveawayCards)||0)+(parseFloat(s.biguInsuranceCards)||0)+(parseFloat(s.biguChaserCards)||0) : 0;
  // Shipping is split 50/50: BigU fronts it, Bazooka reimburses half (and eats that half as a cost)
  const biguShippingHalf = isBigU ? ((parseFloat(s.biguShipping)||0) * 0.5) : 0;
  const bazTrueNet    = isSingles ? 0 : bazOwnShare - commAmt - eventStaffAmt - salesBonus + repExpShare - bazExpShare + imcReimb + imcDirectReimb - biguCardCosts - biguShippingHalf;
  let myComm = isSingles ? splitBase + tips : primaryCommAmt - repExpShare * splitPct + salesBonus + tips;
  if (targetBreaker) {
    const myStaff    = eventStaffArr.find(es=>es && es.breaker===targetBreaker);
    const isSplitRep  = s.splitRep === targetBreaker;
    const isOwner     = s.breaker === targetBreaker;
    // "Event only" = they're event staff but NOT the breaker and NOT the split rep (no other commission on this stream)
    const isEventOnly = !!myStaff && !isOwner && !isSplitRep;
    const myEventFee  = myStaff ? Math.min(1000, bazOwnShare*0.15) : 0;
    myComm = isEventOnly ? myEventFee
           : isSplitRep  ? (splitRepAmt - repExpShare * (1-splitPct)) + myEventFee
           : (primaryCommAmt - repExpShare * splitPct + salesBonus + tips) + myEventFee;
  }
  const biguReimb = isBigU
    ? (reimbursesMagpros(s.breaker) ? (parseFloat(s.magpros)||0) : 0)+(reimbursesPackaging(s.breaker) ? (parseFloat(s.packagingMaterial)||0) : 0)+(parseFloat(s.biguGiveawayCards)||0)+(parseFloat(s.biguInsuranceCards)||0)+(parseFloat(s.biguChaserCards)||0)+biguShippingHalf
    : 0;

  return { gross, fees, coupons, streamExp, splitBase, netRev:splitBase, bazNet, bazOwnShare, imcNet, rate, commAmt, repExpShare, bazExpShare, tips, salesBonus, collabAmt, eventStaffAmt, imcReimb, imcDirectReimb, splitPct, primaryCommAmt, splitRepAmt, splitRep:s.splitRep||"", bazTrueNet, myComm, totalExp:fees+coupons+streamExp, commBase:bazNet, externalChannel:externalCh, isSingles, isBigU, biguReimb, biguCardCosts, biguShippingHalf, excludeFinancials:!!s.excludeFinancials };
}
function getStreamBrand(s) {
  const prods = s.streamSkuPrices ? Object.keys(s.streamSkuPrices) : [];
  const name = (s.streamName||"").toLowerCase();
  const hasWotF = prods.some(p=>WOTF_SETS.some(w=>p.toLowerCase().includes(w.toLowerCase()))) ||
                  WOTF_SETS.some(w=>name.includes(w.toLowerCase())) ||
                  name.includes("wonders") || name.includes("wotf") || name.includes("dragon box");
  if (hasWotF) return "wotf";
  return "boba";
}
const OFFICE_STAFF = [
  { id:"devin",   name:"Devin",   color:"#E8317A", role:"CEO" },
  { id:"dre",     name:"Dre",     color:"#A855F7", role:"Streamer" },
  { id:"krystal", name:"Krystal", color:"#22C55E", role:"Streamer" },
  { id:"jake",    name:"Jake",    color:"#FBBF24", role:"Shipping" },
  { id:"cameron", name:"Cameron", color:"#F97316", role:"Shipping" },
];
const CHANNELS = ["Bazooka Vault", "Bazooka Breaks", "Orbital Society"];
// Effective channel for a stream. BigU always counts as Bazooka Breaks (except
// Orbital Society, which is tracked separately). For everyone else, an explicit
// channel wins, otherwise Bazooka Vault.
function channelOf(s){
  const isBigU = s && (s.breaker||"").toLowerCase().replace(/\s+/g,"") === "bigu";
  if (isBigU) return s.channel === "Orbital Society" ? "Orbital Society" : "Bazooka Breaks";
  if (s && s.channel) return s.channel;
  return "Bazooka Vault";
}

// Canonical weapon names — collapse case/punctuation dupes (ALT==Alt, FIRE==Fire, etc.)
// Search synonyms. Collectors say "auto", the checklist says "Inspired Ink" — and expecting someone
// to type the exact printed treatment name to find their card is a bad search box.
//
// These are folded into the search INDEX (one string per card, built once), not applied per keystroke,
// so matching stays a plain substring test and costs nothing at type time. Keys are matched as
// substrings of the treatment, so "inspired ink" catches "Magic Gum Inspired Ink" too.
const SEARCH_SYNONYMS = {
  "inspired ink": ["auto", "autograph", "signed", "signature"],
  "linoleum":     ["lino"],
  "battlefoil":   ["foil", "bf"],
  "refractor":    ["refract"],
  "prismatic":    ["prizm", "prism"],
  "home team discount": ["htd", "discount"],
  "plays":        ["play"],
  "secret 1/1":   ["1of1", "1 of 1", "one of one", "oneofone"],
  "super":        ["superfoil", "super foil"],
};
// Any synonym that applies to a card, as a flat string to append to its search text.
function synonymsFor(c) {
  const hay = [c.treatment, c.notation, c.variation].filter(Boolean).join(" ").toLowerCase();
  let out = "";
  for (const key in SEARCH_SYNONYMS) {
    if (hay.includes(key)) out += " " + SEARCH_SYNONYMS[key].join(" ");
  }
  return out;
}

const WEAPON_CANON = { alt:"Alt", fire:"Fire", glow:"Glow", gum:"Gum", hex:"Hex", ice:"Ice", steel:"Steel", super:"Super", brawl:"Brawl", cyber:"Cyber", medal:"Medal", metallic:"Metallic" };
function canonWeapon(w) {
  if (!w) return w;
  const k = String(w).trim().toLowerCase();
  return WEAPON_CANON[k] || (String(w).trim().charAt(0).toUpperCase() + String(w).trim().slice(1).toLowerCase());
}
// Preferred display order for weapon dropdowns
// A card is a one-of-one if it says so, OR if it belongs to a category that is 1/1 by definition:
// the Super weapon (Superfoil) and the Pink Battlefoil insert. Central so every counter agrees.
function isOneOfOne(c) {
  if (!c) return false;
  const notation  = String(c.notation||"").toLowerCase();
  const cardNum   = String(c.cardNum||"").toLowerCase();
  const weapon    = String(canonWeapon(c.weapon)||"").toLowerCase().trim();  // "superfoil" also canonicalises to Super
  const treatment = String(c.treatment||"").toLowerCase();
  if (notation.includes("1/1") || cardNum.includes("1/1")) return true;
  if (notation.includes("one of one") || notation.includes("1 of 1")) return true;
  if (weapon === "super") return true;                       // Superfoil = one of one
  if (treatment.includes("pink battlefoil")) return true;    // that insert is a 1/1
  return false;
}
const WEAPON_ORDER = ["Brawl","Steel","Fire","Ice","Glow","Hex","Gum","Super","Alt","Cyber","Medal","Metallic"];
function sortWeapons(list) {
  return [...list].sort((a,b) => {
    const ia = WEAPON_ORDER.indexOf(a), ib = WEAPON_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1; if (ib === -1) return -1;
    return ia - ib;
  });
}

// ── Gzip helpers for the card snapshot (cuts ~13MB → ~1.5MB on first load) ──
function _loadPako() {
  return new Promise((resolve, reject) => {
    if (window.pako) return resolve(window.pako);
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/pako/2.1.0/pako.min.js";
    s.onload = () => resolve(window.pako);
    s.onerror = () => reject(new Error("pako load failed"));
    document.body.appendChild(s);
  });
}
// Write the card array to Storage as gzip (boba_checklist.json.gz). Returns true on success.
async function writeCardSnapshot(all, cacheSeconds) {
  const pako = await _loadPako();
  const gz = pako.gzip(JSON.stringify(all));
  const blob = new Blob([gz], { type: "application/json" });
  const gzRef = ref(storage, "card_data/boba_checklist.json.gz");
  await uploadBytes(gzRef, blob, { contentType: "application/json", contentEncoding: "identity", cacheControl: `public,max-age=${cacheSeconds||300}` });
  return true;
}
// Read + decompress the gz snapshot. Returns array or null.
async function readCardSnapshot(bust) {
  const pako = await _loadPako();
  const url = await getDownloadURL(ref(storage, "card_data/boba_checklist.json.gz"));
  const r = await fetch(url + (bust ? ((url.includes("?")?"&":"?") + "v=" + Date.now()) : ""));
  const buf = new Uint8Array(await r.arrayBuffer());
  const text = pako.ungzip(buf, { to: "string" });
  const arr = JSON.parse(text);
  return Array.isArray(arr) ? arr : null;
}

// ── Card back image ──────────────────────────────────────────────────────────
// The BoBA card back, used everywhere a face-down or shuffled card shows: the
// shuffle ceremony and the face-down heroes in the arena. Stored in Firebase
// Storage so it can be swapped without a code change.
//
// Resolved once and memoised. The promise is cached (not just the URL) so a
// burst of cards asking at once triggers a single fetch. A CSS diamond pattern
// stays as the fallback if the image can't be reached, so a face-down card is
// never blank.
const CARD_BACK_PATH = "card_data/hero_back.webp";
const CARD_BACK_FALLBACK =
  "repeating-linear-gradient(45deg,#7f1d1d,#7f1d1d 6px,#991b1b 6px,#991b1b 12px)";
let _cardBackPromise = null;
let _cardBackUrl = null;
const _cardBackSubs = new Set();   // hook setters waiting on the URL
function _cardBackNotify() { _cardBackSubs.forEach(fn => { try { fn(_cardBackUrl); } catch (e) {} }); }
function loadCardBackUrl() {
  if (_cardBackUrl) return Promise.resolve(_cardBackUrl);
  if (_cardBackPromise) return _cardBackPromise;
  _cardBackPromise = getDownloadURL(ref(storage, CARD_BACK_PATH))
    .then(u => { _cardBackUrl = u; _cardBackNotify(); return u; })
    .catch(() => { _cardBackPromise = null; return null; });   // allow a retry later
  return _cardBackPromise;
}
// Call after an upload to push the new URL to every mounted card back at once.
function setCardBackUrl(u) { _cardBackUrl = u; _cardBackPromise = Promise.resolve(u); _cardBackNotify(); }
// Hook: returns the back-image URL once resolved, else null (use the fallback).
// Subscribes to the shared cache so ANY component that resolves or uploads the
// image updates every other card back on screen, not just its own.
function useCardBack() {
  const [url, setUrl] = React.useState(_cardBackUrl);
  React.useEffect(() => {
    _cardBackSubs.add(setUrl);
    if (!_cardBackUrl) loadCardBackUrl();
    else setUrl(_cardBackUrl);
    return () => { _cardBackSubs.delete(setUrl); };
  }, []);
  return url;
}

// ── Playmat (arena playing surface) — same pattern as the card back above. An
// admin uploads an image; it's stored once in Storage and every arena board reads
// it as its background. Falls back to a dark gradient when none is set. ──
const PLAYMAT_PATH = "card_data/playmat.webp";
let _playmatPromise = null;
let _playmatUrl = null;
const _playmatSubs = new Set();
function _playmatNotify() { _playmatSubs.forEach(fn => { try { fn(_playmatUrl); } catch (e) {} }); }
function loadPlaymatUrl() {
  if (_playmatUrl) return Promise.resolve(_playmatUrl);
  if (_playmatPromise) return _playmatPromise;
  _playmatPromise = getDownloadURL(ref(storage, PLAYMAT_PATH))
    .then(u => { _playmatUrl = u; _playmatNotify(); return u; })
    .catch(() => { _playmatPromise = null; return null; });
  return _playmatPromise;
}
function setPlaymatUrl(u) { _playmatUrl = u; _playmatPromise = Promise.resolve(u); _playmatNotify(); }
function usePlaymat() {
  const [url, setUrl] = React.useState(_playmatUrl);
  React.useEffect(() => {
    _playmatSubs.add(setUrl);
    if (!_playmatUrl) loadPlaymatUrl();
    else setUrl(_playmatUrl);
    return () => { _playmatSubs.delete(setUrl); };
  }, []);
  return url;
}

// ── Arena board layout (admin-arranged slot positions on the playmat) ──────
// Every placeable element is stored as {x,y,w} in PERCENT of the board box, so
// it scales on any screen. Height derives from width via the 5:7 card ratio for
// cards, or a fixed height for the thin zone-number chips. The default roughly
// mirrors the old grid so the board looks sane before anyone arranges it.
const ARENA_LAYOUT_PATH = ["config", "arena_layout"];
function defaultArenaLayout() {
  const L = {};
  // Single-POV mat: MY 7 battle slots across the upper-middle, the zone-result
  // numbers just below them, and my four piles along the bottom. No opponent
  // elements — they have their own mat.
  const cw = 12, gap = 1.2, x0 = 2.5;
  for (let i = 0; i < 7; i++) {
    const x = x0 + i * (cw + gap);
    L["my"  + i]  = { x, y: 14, w: cw };
    L["zone" + i] = { x, y: 58, w: cw, h: 6 };
  }
  const pileW = 11, py = 74, gapP = 2, totalP = 4*pileW + 3*gapP, x0p = (100 - totalP)/2;
  ["Deck","Playbook","Hotdogs","Discard"].forEach((k,i)=>{
    L["my"+k] = { x: x0p + i*(pileW+gapP), y: py, w: pileW };
  });
  return L;
}
let _arenaLayout = null;
let _arenaLayoutPromise = null;
const _arenaLayoutSubs = new Set();
function _arenaLayoutNotify(){ _arenaLayoutSubs.forEach(fn=>{ try{ fn(_arenaLayout); }catch(e){} }); }
function loadArenaLayout() {
  if (_arenaLayout) return Promise.resolve(_arenaLayout);
  if (_arenaLayoutPromise) return _arenaLayoutPromise;
  _arenaLayoutPromise = getDoc(doc(db, ARENA_LAYOUT_PATH[0], ARENA_LAYOUT_PATH[1]))
    .then(s => { _arenaLayout = (s.exists() && s.data().layout) ? s.data().layout : defaultArenaLayout(); _arenaLayoutNotify(); return _arenaLayout; })
    .catch(() => { _arenaLayoutPromise = null; _arenaLayout = defaultArenaLayout(); _arenaLayoutNotify(); return _arenaLayout; });
  return _arenaLayoutPromise;
}
async function saveArenaLayout(layout) {
  _arenaLayout = layout; _arenaLayoutPromise = Promise.resolve(layout); _arenaLayoutNotify();
  try { await setDoc(doc(db, ARENA_LAYOUT_PATH[0], ARENA_LAYOUT_PATH[1]), { layout }, { merge:true }); } catch(e){ /* keep local copy */ }
}
function useArenaLayout() {
  const [lay, setLay] = React.useState(_arenaLayout);
  React.useEffect(() => {
    _arenaLayoutSubs.add(setLay);
    if (!_arenaLayout) loadArenaLayout(); else setLay(_arenaLayout);
    return () => { _arenaLayoutSubs.delete(setLay); };
  }, []);
  return lay || _arenaLayout || defaultArenaLayout();
}

// ── IndexedDB cache (localStorage caps ~5MB; the 12MB card list needs IndexedDB) ──
function _idbOpen() {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open("bazooka_cards", 1);
      req.onupgradeneeded = () => { try { req.result.createObjectStore("kv"); } catch(e){} };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch(e) { reject(e); }
  });
}
async function idbGetCards() {
  try {
    const dbi = await _idbOpen();
    return await new Promise((resolve) => {
      const tx = dbi.transaction("kv", "readonly");
      const rq = tx.objectStore("kv").get("cards_v3");
      rq.onsuccess = () => resolve(rq.result || null);
      rq.onerror = () => resolve(null);
    });
  } catch(e) { return null; }
}
async function idbSetCards(cards, ts) {
  try {
    const dbi = await _idbOpen();
    await new Promise((resolve) => {
      const tx = dbi.transaction("kv", "readwrite");
      tx.objectStore("kv").put({ cards, ts: ts||Date.now() }, "cards_v3");
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch(e) {}
}
async function idbClearCards() {
  try {
    const dbi = await _idbOpen();
    await new Promise((resolve) => {
      const tx = dbi.transaction("kv", "readwrite");
      tx.objectStore("kv").delete("cards_v3");
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch(e) {}
}

const FLAT_RATE_CHANNELS = []; // no flat-rate channels currently
const FLAT_RATE_BREAKERS = []; // all breakers use standard tiered commission

function getRate(s) {
  if (s.commissionOverride !== "" && s.commissionOverride != null) return parseFloat(s.commissionOverride)/100;
  if (s.isEvent) return 0.15;
  const newBuyerBonus = (parseInt(s.newBuyers)||0) >= 5 ? 0.05 : 0;
  const isFlat = (s.channel && FLAT_RATE_CHANNELS.includes(s.channel)) || (!s.channel && FLAT_RATE_BREAKERS.includes(s.breaker));
  if (isFlat) return Math.min(0.55, 0.50 + newBuyerBonus);
  if (s.binOnly) return 0.35;
  const mm = parseFloat(s.marketMultiple)||0;
  const base = mm>=1.8?0.55:mm>=1.7?0.50:mm>=1.6?0.45:mm>=1.5?0.40:0.35;
  return Math.min(0.60, base + newBuyerBonus);
}
const PRODUCT_SETS = {
  "Alpha Edition":        ["Blaster","Booster","Hobby","Jumbo"],
  "Alpha Update":         ["Blaster","Booster","Hobby","Jumbo"],
  "Alpha Blast":          ["Blast Box"],
  "Griffey 2026":         ["Blaster","Double Mega","Hobby","Jumbo"],
  "Tecmo Bowl":           ["Double Mega","Hobby"],
  "Wonders of The First": ["Dragon Box","Collector Booster","Play Booster"],
};
const PRODUCT_TYPES = [
  ...Object.entries(PRODUCT_SETS).flatMap(([set,types])=>types.map(t=>`${set} - ${t}`)),
  "Miscellaneous",
];
const USAGE_TYPES = ["Giveaway","Insurance","First-Timer Pack","Chaser Pull"];
const SOURCES = ["Discord","Facebook","Other"];
const PAYMENT_METHODS = ["PayPal","Zelle"];
const ROLES = {
  "devin":   { role:"Admin",         label:"CEO",                color:"#E8317A", bg:"#FFF0F5" },
  "derrik":  { role:"Admin",         label:"CFO",                color:"#E8317A", bg:"#FFF0F5" },
  "dre":     { role:"Streamer",      label:"Streamer",           color:"#A855F7", bg:"#F3EAF9" },
  "krystal": { role:"Streamer",      label:"Streamer",           color:"#22C55E", bg:"#E0F7F4" },
  "bigu":    { role:"Streamer",      label:"Streamer",           color:"#F97316", bg:"#FFF3E8" },
  "vinny":   { role:"Streamer",      label:"Streamer",           color:"#EF4444", bg:"#FDECEC" },
  "stephen": { role:"Streamer",      label:"Streamer",           color:"#FBBF24", bg:"#FEF9E7" },
  "alison":  { role:"Streamer",      label:"Streamer",           color:"#F97316", bg:"#FFF3E8" },
  "orbitalsociety": { role:"Streamer", label:"Orbital Society", color:"#34d399", bg:"#ECFDF5" },
  "john":    { role:"Procurement",   label:"Procurement Mgr",    color:"var(--bz-ink)", bg:"#E8F0FB" },
  "jake":    { role:"Shipping",      label:"Shipping/Logistics", color:"var(--bz-ink-2)", bg:"#FFF0CC" },
  "cameron": { role:"Shipping",      label:"Shipping/Logistics", color:"var(--bz-ink-2)", bg:"#FFF0CC" },
};
const TARGETS = {
  "Giveaway Cards":   { monthly:2000, buffer:300 },
  "Insurance Cards":  { monthly:2000, buffer:300 },
  "First-Timer Cards":{ monthly:200,  buffer:50  },
  "Chaser Cards":     { monthly:150,  buffer:30  },
};
const CC = {
  "Giveaway Cards":   { bg:"#0a1a0f", text:"#4ade80",  border:"#2E7D52" },
  "Insurance Cards":  { bg:"#0a0f1a", text:"#7B9CFF",  border:"#3730a3" },
  "First-Timer Cards":{ bg:"#1a0810", text:"#F472B6",  border:"#9d174d" },
  "Chaser Cards":     { bg:"#2a1520", text:"#E8317A",  border:"#E8317A" },
};
const BC = {
  Dev:     { bg:"#0a0e1a", text:"#3B82F6", border:"#1e40af" },
  Dre:     { bg:"#12081a", text:"#A855F7", border:"#6b21a8" },
  Krystal: { bg:"#08181a", text:"#22C55E", border:"#15803d" },
  BigU:    { bg:"#1a0e00", text:"#F97316", border:"#9a3412" },
  Vinny:   { bg:"#1a0006", text:"#EF4444", border:"#991b1b" },
  Stephen: { bg:"#1a1400", text:"#FBBF24", border:"#a16207" },
  "Orbital Society": { bg:"#0a1a10", text:"#34d399", border:"#065f46" },
};
const CAN_DELETE        = ["Admin"];
const CAN_LOG_BREAKS    = ["Admin","Streamer","Procurement","Shipping"];
const CAN_VIEW_LOT_COMP = ["Admin","Procurement","Streamer","Shipping","Viewer"];

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(()=>{ const h=()=>setW(window.innerWidth); window.addEventListener("resize",h); return()=>window.removeEventListener("resize",h); },[]);

  return w;
}

function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2); }
const fmt = n => isNaN(n) || n === "" || n === null ? "--" : "$" + parseFloat(n).toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 });

// -- useCountUp hook -- animates numbers from 0 to target ----------
function useCountUp(target, duration=600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target || isNaN(target)) { setVal(target); return; }
    const start = Date.now();
    const from = 0;
    const to = parseFloat(target);
    function tick() {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease out cubic
      setVal(from + (to - from) * eased);
      if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }, [target]);
  return val;
}

function AnimatedNumber({ value, format="dollar", duration=700 }) {
  const num = useCountUp(parseFloat(value)||0, duration);
  if (format === "dollar") return <>{fmt(num)}</>;
  return <>{Math.round(num).toLocaleString()}</>;
}

function getUserRole(user) {
  if (!user) return null;
  const email = (user.email||"").toLowerCase();
  // Only allow @bazookabreaks.com emails
  if (!email.endsWith("@bazookabreaks.com")) return null;
  const name = (user.displayName||"").toLowerCase();
  for (const [key, val] of Object.entries(ROLES)) {
    if (name.includes(key) || email.includes(key)) return val;
  }
  return { role:"Viewer", label:"Viewer", color:"var(--bz-ink-2)", bg:"#F3F4F6" };
}
function getZone(pct) {
  if (!pct || isNaN(pct)) return null;
  if (pct < 0.65)  return { label:"\uD83D\uDFE2 Green",  color:"#E8317A", bg:"#D6F4E3" };
  if (pct <= 0.70) return { label:"\uD83D\uDFE1 Yellow", color:"var(--bz-ink-2)", bg:"#FFF9DB" };
  return                   { label:"\uD83D\uDD34 Red",    color:"#E8317A", bg:"#FEE2E2" };
}

const DARK_T = {
  pageBg:"#000000", card:"var(--bz-s1)", cardBorder:"var(--bz-line-2)",
  inp:"var(--bz-s2)", inpBorder:"#333333", text:"var(--bz-ink)",
  textSub:"#999999", textMute:"#777777",
  rowA:"var(--bz-s1)", rowB:"var(--bz-s1)", rowHover:"var(--bz-s2)",
  border:"var(--bz-line-2)", thBg:"#000000", tdBorder:"rgba(255,255,255,0.04)",
};
const LIGHT_T = {
  pageBg:"#F7F4F8", card:"#FFFFFF", cardBorder:"#EDE0EC",
  inp:"#FDFCFE", inpBorder:"#E8D0DC", text:"#111827",
  textSub:"var(--bz-ink-3)", textMute:"var(--bz-ink-2)",
  rowA:"#FFFFFF", rowB:"#FFF8FB", rowHover:"#FFF0F5",
  border:"var(--bz-line)", thBg:"#1A1A2E", tdBorder:"#FFF0F5",
};

function makeS(dark) {
  const T = dark ? DARK_T : LIGHT_T;
  return {
    card: { background:dark ? "linear-gradient(160deg,#181016,#130d11)" : T.card, border:`1px solid ${T.cardBorder}`, borderRadius:16, padding:"18px 20px", boxShadow:dark ? "0 1px 0 rgba(255,255,255,0.03) inset, 0 6px 22px rgba(0,0,0,0.35)" : "none" },
    inp:  { background:T.inp, border:`1px solid ${T.inpBorder}`, borderRadius:8, padding:"8px 12px", color:T.text, fontSize:13, fontFamily:"inherit", outline:"none", width:"100%", boxSizing:"border-box", transition:"border-color 0.15s ease" },
    lbl:  { fontSize:9, fontWeight:700, color:"#999", textTransform:"uppercase", letterSpacing:"1.2px", display:"block", marginBottom:5 },
    th:   { padding:"8px 12px", background:"var(--bz-bg)", color:"#E8317A", fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"1px", textAlign:"left", whiteSpace:"nowrap", borderBottom:"1px solid rgba(232,49,122,0.12)" },
    td:   { padding:"9px 12px", borderBottom:`1px solid ${T.tdBorder}`, fontSize:13, color:T.text },
    T,
  };
}

// Default S = dark (components that don't receive darkMode prop use this)
const S = makeS(true);



function SectionLabel({ t }) {
  return (
    <div style={{ fontSize:11, fontWeight:800, color:"var(--bz-ink)", letterSpacing:"-0.01em", marginBottom:14, display:"flex", alignItems:"center", gap:9 }}>
      <span style={{ width:3, height:14, borderRadius:2, background:"linear-gradient(180deg,var(--bz-pink-hot),var(--bz-pink))", flexShrink:0 }}/>
      <span style={{ textTransform:"uppercase", letterSpacing:"0.8px", fontSize:10.5, color:"var(--bz-ink-2)" }}>{t}</span>
      <div style={{ flex:1, height:"1px", background:"linear-gradient(90deg,var(--bz-pink-line),transparent)" }}/>
    </div>
  );
}
function Badge({ children, bg="#F3F4F6", color="var(--bz-ink-2)" }) {
  return <span style={{ background:bg, color, borderRadius:5, padding:"2px 8px", fontSize:11, fontWeight:700, whiteSpace:"nowrap", letterSpacing:"0.2px" }}>{children}</span>;
}
function ZoneBadge({ pct }) {
  const z = getZone(pct);
  if (!z) return <span style={{ color:"#8a8a8a", fontSize:11 }}>—</span>;
  const cls = pct >= 0.70 ? "zone-red" : pct >= 0.65 ? "zone-yellow" : "";
  return <span className={cls} style={{ background:z.bg, color:z.color, border:`1px solid ${z.color}33`, borderRadius:5, padding:"2px 8px", fontSize:11, fontWeight:700, whiteSpace:"nowrap", display:"inline-block" }}>{z.label} · {(pct*100).toFixed(1)}%</span>;
}
function Btn({ children, onClick, variant="gold", disabled, style:extra }) {
  const V = {
    gold:  { bg:"#E8317A", c:"#fff", hover:"#c41e5a" },
    green: { bg:"rgba(74,222,128,0.15)", c:"#4ade80", hover:"rgba(74,222,128,0.25)" },
    ghost: { bg:"rgba(255,255,255,0.06)", c:"#888", hover:"rgba(255,255,255,0.1)" },
    red:   { bg:"rgba(239,68,68,0.12)", c:"#ef4444", hover:"rgba(239,68,68,0.2)" },
  };
  const v = V[variant]||V.gold;
  return (
    <button onClick={onClick} disabled={disabled} className="btn-lift"
      style={{ background:v.bg, color:v.c, border:"none", borderRadius:8, padding:"8px 18px", fontSize:12, fontWeight:700, cursor:disabled?"not-allowed":"pointer", opacity:disabled?0.35:1, fontFamily:"inherit", whiteSpace:"nowrap", transition:"all 0.15s ease", ...extra }}
      onMouseEnter={e=>{ if(!disabled) e.currentTarget.style.background=v.hover; }}
      onMouseLeave={e=>{ e.currentTarget.style.background=v.bg; }}>
      {children}
    </button>
  );
}
function Field({ label, children }) {
  return <div style={{ display:"flex", flexDirection:"column", gap:4 }}><label style={S.lbl}>{label}</label>{children}</div>;
}
function TextInput({ label, value, onChange, type="text", placeholder }) {
  return (
    <Field label={label}>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={S.inp}/>
    </Field>
  );
}
function SelectInput({ label, value, onChange, options }) {
  return (
    <Field label={label}>
      <select value={value} onChange={e=>onChange(e.target.value)} style={{ ...S.inp, color:value?"var(--bz-ink)":"#555", cursor:"pointer" }}>
        <option value="">Select...</option>
        {options.map(o=><option key={o} value={o}>{o}</option>)}
      </select>
    </Field>
  );
}
function EmptyRow({ msg, cols=10 }) {
  return (
    <tr><td colSpan={cols} style={{ padding:"48px 0", textAlign:"center" }}>
      <div style={{ fontSize:28, marginBottom:10, opacity:0.25 }}>—</div>
      <div style={{ fontSize:13, color:"var(--bz-ink-3)", fontWeight:500 }}>{msg}</div>
    </td></tr>
  );
}
function EmptyState({ icon="—", title, body }) {
  return (
    <div style={{ textAlign:"center", padding:"48px 24px" }}>
      <div style={{ fontSize:36, marginBottom:12, opacity:0.3 }}>{icon}</div>
      {title && <div style={{ fontSize:15, fontWeight:700, color:"var(--bz-ink-3)", marginBottom:6 }}>{title}</div>}
      {body  && <div style={{ fontSize:12, color:"#8a8a8a", lineHeight:1.6, maxWidth:320, margin:"0 auto" }}>{body}</div>}
    </div>
  );
}
function AccessDenied({ msg }) {
  return (
    <div style={{ ...S.card, textAlign:"center", padding:"60px 40px" }}>
      <div style={{ fontSize:36, marginBottom:12, opacity:0.4 }}>🔒</div>
      <div style={{ fontSize:16, fontWeight:700, color:"var(--bz-ink)", marginBottom:8 }}>Access Restricted</div>
      <div style={{ fontSize:13, color:"var(--bz-ink-3)", lineHeight:1.6 }}>{msg}</div>
    </div>
  );
}

// ── PERFORMANCE HOOKS ─────────────────────────────────────────────────────────
function useDebounce(value, delay=220) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Memoized calcStream — cache results by stream id+fingerprint so we don't
// recalculate 100 streams on every keystroke
const streamCalcCache = new Map();
// calcStream is now the CACHED entry point — every caller benefits automatically.
// The heavy settlement math lives in calcStreamRaw; results are memoized by a key
// of every input that affects the outcome, so re-renders reuse prior results
// instead of recomputing the full P&L for every stream on every paint.
function calcStream(s, targetBreaker=null) {
  if (!s) return calcStreamRaw(s, targetBreaker);
  const key = `${s.id||""}:${s.grossRevenue}:${s.marketMultiple}:${s.newBuyers}:${s.commissionOverride}:${s.channel}:${s.collabPct}:${s.externalChannel}:${s.whatnotPromo}:${s.magpros}:${s.packagingMaterial}:${s.topLoaders}:${s.chaserCards}:${s.isSinglesShow}:${s.sessionType||""}:${s.excludeFinancials}:${s.manualCommission}:${s.splitRep||""}:${s.splitPct||""}:${s.biguGiveawayCards}:${s.biguInsuranceCards}:${s.biguChaserCards}:${s.biguShipping}:${(s.eventStaff||[]).map(e=>e.breaker).join(",")}:${targetBreaker||""}`;
  const hit = streamCalcCache.get(key);
  if (hit) return hit;
  const result = calcStreamRaw(s, targetBreaker);
  streamCalcCache.set(key, result);
  if (streamCalcCache.size > 2000) {
    const firstKey = streamCalcCache.keys().next().value;
    streamCalcCache.delete(firstKey);
  }
  return result;
}
// calcStreamMemo is kept as an alias — calcStream is already cached now.
function calcStreamMemo(s, targetBreaker=null) {
  return calcStream(s, targetBreaker);
}

function GlobalStyles() {
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      /* ============ BAZOOKA DESIGN TOKENS (facelift v1) ============ */
      :root{
        --bz-bg:#0b0709; --bz-s1:#130d11; --bz-s2:#1a1218; --bz-s3:#241820;
        --bz-line:rgba(255,255,255,0.06); --bz-line-2:rgba(255,255,255,0.11);
        --bz-ink:#f6eef2; --bz-ink-2:#b9a9b2; --bz-ink-3:#7a6b74;
        --bz-pink:#E8317A; --bz-pink-hot:#ff4d94; --bz-pink-dim:rgba(232,49,122,0.14); --bz-pink-line:rgba(232,49,122,0.30);
        --bz-green:#34d399; --bz-amber:#fbbf24; --bz-blue:#7c9cff; --bz-violet:#a78bfa;
        --bz-radius:16px; --bz-radius-sm:11px;
        --bz-shadow:0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 30px rgba(0,0,0,0.4);
        --bz-sidebar-w:224px;
      }
      .bz-shell{display:flex;min-height:100vh;background:radial-gradient(1200px 600px at 80% -10%, rgba(232,49,122,0.08), transparent 60%), var(--bz-bg);}
      .bz-side{width:var(--bz-sidebar-w);flex-shrink:0;border-right:1px solid var(--bz-line);padding:20px 12px;position:sticky;top:0;height:100vh;overflow-y:auto;background:linear-gradient(180deg, rgba(255,255,255,0.015), transparent);z-index:50;}
      .bz-brand{display:flex;align-items:center;gap:10px;padding:0 8px 18px;margin-bottom:6px;border-bottom:1px solid var(--bz-line);}
      .bz-navsec{font-size:10px;letter-spacing:1.6px;text-transform:uppercase;color:var(--bz-ink-3);padding:16px 10px 7px;font-weight:700;}
      .bz-navitem{display:flex;align-items:center;gap:11px;padding:9px 11px;border-radius:10px;font-size:13.5px;color:var(--bz-ink-2);font-weight:500;cursor:pointer;transition:background .15s,color .15s;margin-bottom:1px;font-family:inherit;border:none;background:none;width:100%;text-align:left;}
      .bz-navitem:hover{background:var(--bz-s2);color:var(--bz-ink);}
      .bz-navitem.on{background:var(--bz-pink-dim);color:var(--bz-pink-hot);font-weight:600;}
      .bz-navitem .bz-ic{font-size:15px;width:18px;text-align:center;flex-shrink:0;}
      .bz-main{flex:1;min-width:0;}
      .bz-topbar{position:sticky;top:0;z-index:40;display:flex;align-items:center;justify-content:space-between;gap:14px;padding:14px 26px;border-bottom:1px solid var(--bz-line);background:rgba(11,7,9,0.82);backdrop-filter:blur(14px);}
      .bz-sub{display:flex;gap:2px;padding:8px 22px;border-bottom:1px solid var(--bz-line);flex-wrap:wrap;background:var(--bz-bg);}
      .bz-subitem{font-size:12.5px;font-weight:600;color:var(--bz-ink-2);padding:6px 12px;border-radius:8px;cursor:pointer;transition:all .15s;border:none;background:none;font-family:inherit;}
      .bz-subitem:hover{background:var(--bz-s2);color:var(--bz-ink);}
      .bz-subitem.on{background:var(--bz-pink);color:#fff;}
      .bz-fin-card:hover{border-color:var(--bz-line-2)!important;transform:translateY(-2px);box-shadow:var(--bz-shadow);}
      .bz-mobile-menu{display:none;}
      @media print {
        /* Print ONLY the pick-list sheet. The sheet lives inside a position:fixed, display:flex,
           overflow:auto overlay. Two things cause "page 1 repeats": (1) the fixed/flex/overflow
           context clipping to one screen, and (2) using visibility:hidden, which keeps the giant
           app's layout space and spills blank/duplicate pages. So we DISPLAY:NONE the whole app via
           #root's direct children, then re-show the overlay chain as static block flow. */
        html, body { height: auto !important; overflow: visible !important; background: #fff !important; }
        /* Hide everything at the top level... */
        body > * { display: none !important; }
        /* ...but keep the React root visible so the overlay inside it can print. */
        #root { display: block !important; }
        /* Inside #root, hide the app but keep the ancestor chain of the pick list visible. */
        #root * { visibility: hidden !important; }
        .pick-list-overlay, .pick-list-overlay * { visibility: visible !important; }
        .pick-list-overlay {
          position: absolute !important; left: 0 !important; top: 0 !important; right: auto !important; bottom: auto !important;
          inset: auto !important; overflow: visible !important; display: block !important;
          padding: 0 !important; margin: 0 !important; background: #fff !important; backdrop-filter: none !important;
          height: auto !important; max-height: none !important; width: 100% !important; z-index: auto !important;
          align-items: initial !important; justify-content: initial !important;
        }
        .pick-list-sheet {
          position: static !important; display: block !important; box-shadow: none !important; border-radius: 0 !important;
          max-width: 100% !important; width: 100% !important; margin: 0 !important;
          max-height: none !important; height: auto !important; overflow: visible !important;
          padding: 0 !important; transform: none !important;
        }
        .pick-list-sheet table { page-break-inside: auto; width: 100% !important; }
        .pick-list-sheet tr { page-break-inside: avoid; page-break-after: auto; }
        .pick-list-sheet thead { display: table-header-group; }
        .pick-list-controls { display: none !important; }
        @page { margin: 14mm; }
      }
      @media (max-width:900px){
        .bz-side{position:fixed;left:0;top:0;transform:translateX(-100%);transition:transform .25s ease;box-shadow:0 0 40px rgba(0,0,0,0.6);background:#140d11!important;z-index:60;}
        .bz-side.open{transform:translateX(0);}
        .bz-mobile-menu{display:inline-flex!important;align-items:center;justify-content:center;}
        .bz-shell{display:block;}
        .bz-topbar{padding:10px 14px;gap:8px;}
        .bz-topbar .bz-search{display:none!important;}
        .bz-main{width:100%;}
      }
      * { box-sizing: border-box; }
      html, body { overflow-x: hidden; max-width: 100vw; }
      body { background: var(--bz-bg) !important; color: var(--bz-ink); font-family: inherit; -webkit-font-smoothing: antialiased; }
      #root { background: var(--bz-bg); min-height: 100vh; overflow-x: hidden; }
      table { width: 100%; border-collapse: collapse; }
      .tab-content { width: 100%; min-width: 0; }

      /* Image protection — prevent easy right-click save */
      img { -webkit-user-drag: none; user-drag: none; pointer-events: none; }
      .card-img-wrap { position: relative; display: inline-block; }
      .card-img-wrap::after { content: ""; position: absolute; inset: 0; z-index: 1; cursor: default; }
      input::placeholder { color: #444 !important; }
      select option { background: #111; color: #F0F0F0; }
      input, select, textarea { transition: border-color 0.15s ease, box-shadow 0.15s ease !important; }
      input:focus, select:focus, textarea:focus { outline: none !important; border-color: rgba(232,49,122,0.6) !important; box-shadow: 0 0 0 3px rgba(232,49,122,0.12) !important; }
      input[type="date"], input[type="month"] { color-scheme: dark; }
      input[type="date"]::-webkit-calendar-picker-indicator,
      input[type="month"]::-webkit-calendar-picker-indicator { filter: invert(0.6) sepia(1) saturate(5) hue-rotate(290deg); cursor: pointer; }
      input[type="checkbox"] { cursor: pointer; accent-color: #E8317A; }
      ::selection { background: rgba(232,49,122,0.25); color: #fff; }

      /* Scrollbars */
      ::-webkit-scrollbar { width: 4px; height: 4px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background: #222; border-radius: 10px; }
      ::-webkit-scrollbar-thumb:hover { background: #E8317A; }

      /* Card hover */
      .card-hover { transition: transform 0.18s cubic-bezier(0.22,1,0.36,1), box-shadow 0.18s ease, border-color 0.18s ease !important; }
      .card-hover:hover { transform: translateY(-2px) !important; box-shadow: 0 12px 32px rgba(0,0,0,0.5) !important; border-color: rgba(232,49,122,0.2) !important; }

      /* Buttons */
      .btn-lift { transition: transform 0.15s cubic-bezier(0.34,1.56,0.64,1), opacity 0.15s ease !important; }
      .btn-lift:hover:not(:disabled) { transform: translateY(-1px) scale(1.02) !important; }
      .btn-lift:active:not(:disabled) { transform: translateY(0) scale(0.98) !important; }

      /* Stat cards */
      .stat-card { transition: transform 0.2s cubic-bezier(0.22,1,0.36,1), box-shadow 0.2s ease, border-color 0.2s ease !important; }
      .stat-card:hover { transform: translateY(-3px) !important; box-shadow: 0 16px 40px rgba(0,0,0,0.6) !important; }

      /* Table rows */
      .inv-row, .break-row { transition: background 0.1s ease !important; }
      .inv-row:hover, .break-row:hover { background: rgba(255,255,255,0.03) !important; }
      .clickable-row { transition: background 0.1s ease !important; cursor: pointer !important; }
      .clickable-row:hover { background: rgba(255,255,255,0.03) !important; box-shadow: inset 3px 0 0 #E8317A !important; }

      /* Nav */
      .dash-tab:hover { background: rgba(232,49,122,0.06) !important; color: rgba(232,49,122,0.8) !important; border-bottom: 2px solid rgba(232,49,122,0.4) !important; }
      .nav-bazooka { transition: opacity 0.2s ease; }
      .nav-bazooka:hover { opacity: 0.8; }

      /* Animations */
      @keyframes fadeSlideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
      @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
      @keyframes slideIn { from { opacity:0; transform:translateX(16px); } to { opacity:1; transform:translateX(0); } }
      @keyframes numPop { from { transform:scale(0.8); opacity:0; } to { transform:scale(1); opacity:1; } }
      /* Notification bell. The header is busy, so a static bell is easy to miss entirely. This
         breathes rather than flashes: a slow scale with a brief pause between beats reads as
         "something is waiting" without the nagging quality of a fast blink. */
      @keyframes bellPulse {
        0%, 55%, 100% { transform: scale(1); }
        62%           { transform: scale(1.22) rotate(-9deg); }
        70%           { transform: scale(1.12) rotate(8deg); }
        78%           { transform: scale(1.18) rotate(-5deg); }
        88%           { transform: scale(1); }
      }
      @keyframes bellHalo {
        0%, 55%, 100% { opacity:0; transform: scale(0.7); }
        65%           { opacity:0.5; transform: scale(1); }
        100%          { opacity:0; transform: scale(1.5); }
      }
      .bz-bell-live { animation: bellPulse 2.6s ease-in-out infinite; transform-origin: 50% 15%; }
      /* Anyone who finds motion distracting or is prone to motion sickness has usually told their
         operating system already \u2014 honour that rather than making them ask. */
      @media (prefers-reduced-motion: reduce) {
        .bz-bell-live { animation: none; }
        .bz-bell-halo { animation: none; opacity: 0; }
      }
      @keyframes spin { to { transform:rotate(360deg); } }
      @keyframes fabIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
      @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
      @keyframes toastIn { from { opacity:0; transform:translateY(20px) scale(0.95); } to { opacity:1; transform:translateY(0) scale(1); } }
      @keyframes expandDown { from { opacity:0; transform:scaleY(0.96) translateY(-6px); transform-origin:top; } to { opacity:1; transform:scaleY(1) translateY(0); } }
      @keyframes saveFlash { 0%{box-shadow:0 0 0 0 rgba(22,101,52,0.6);} 50%{box-shadow:0 0 0 12px rgba(22,101,52,0.02);} 100%{box-shadow:none;} }
      @keyframes pulsRed { 0%,100%{box-shadow:0 0 0 0 rgba(153,27,27,0.5);} 50%{box-shadow:0 0 0 8px rgba(153,27,27,0);} }
      @keyframes tickerScroll { from { transform:translateX(0); } to { transform:translateX(-50%); } }
      @keyframes vtickerScroll { from { transform:translateY(0); } to { transform:translateY(-50%); } }
      .vticker-wrap { overflow:hidden; }
      .vticker-track { display:flex; flex-direction:column; animation:vtickerScroll 18s linear infinite; }
      .vticker-wrap:hover .vticker-track { animation-play-state:paused; }
      .ticker-track { display:inline-flex; gap:10px; animation:tickerScroll 30s linear infinite; }
      .ticker-wrap:hover .ticker-track { animation-play-state:paused; }
      @keyframes pulsYellow { 0%,100%{box-shadow:0 0 0 0 rgba(146,64,14,0.4);} 50%{box-shadow:0 0 0 6px rgba(146,64,14,0);} }
      @keyframes pulsCritical { 0%,100%{box-shadow:0 0 0 0 rgba(220,38,38,0.6);} 50%{box-shadow:0 0 0 8px rgba(220,38,38,0.04);} }
      .toast { animation: toastIn 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards; }
      .num-pop { animation: numPop 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards; }
      .fade-in { animation: fadeIn 0.25s ease forwards; }
      .slide-in { animation: slideIn 0.22s cubic-bezier(0.22,1,0.36,1) forwards; }
      .drill-down { animation: expandDown 0.22s cubic-bezier(0.22,1,0.36,1) forwards; }
      .save-flash { animation: saveFlash 0.5s ease forwards; }
      .zone-red { animation: pulsRed 2.5s ease-in-out infinite; }
      .zone-yellow { animation: pulsYellow 2.5s ease-in-out infinite; }
      .status-critical { animation: pulsCritical 2s ease-in-out infinite; }
      .boba-flip-card { transform-style: preserve-3d; }
      .boba-flip-card > div { backface-visibility: hidden; -webkit-backface-visibility: hidden; }
      .boba-card-flip { transform-style: preserve-3d; transition: transform 0.5s ease; }
      .boba-card-flip:hover { transform: rotateY(180deg); }
      .boba-flip-pill { opacity: 0; transform: translateY(4px); transition: opacity 0.18s ease, transform 0.18s ease; }
      .boba-card-hover:hover .boba-flip-pill { opacity: 1; transform: translateY(0); }
      .boba-card-hover:hover .boba-quickadd { opacity: 1; }
      @media (hover: none) { .boba-flip-pill { opacity: 1; transform: none; } }

      /* Mobile */
      @media (max-width: 768px) {
        .tab-content { padding: 8px !important; }
        table { font-size: 12px !important; }
        .mobile-stack { flex-direction: column !important; }
        .mobile-full { width: 100% !important; grid-template-columns: 1fr !important; }
        .mobile-2col { grid-template-columns: 1fr 1fr !important; }
        .mobile-hide { display: none !important; }
        input, select, textarea { font-size: 16px !important; }

        /* --- Broad mobile safety net (catches inline-styled layouts app-wide) --- */
        /* React serializes inline styles as kebab-case in the DOM, so match that. */
        [style*="grid-template-columns"][style*="repeat(3"],
        [style*="grid-template-columns"][style*="repeat(4"] {
          grid-template-columns: repeat(2, minmax(0,1fr)) !important;
        }
        [style*="grid-template-columns"][style*="repeat(5"],
        [style*="grid-template-columns"][style*="repeat(6"],
        [style*="grid-template-columns"][style*="repeat(7"],
        [style*="grid-template-columns"][style*="repeat(8"] {
          grid-template-columns: repeat(2, minmax(0,1fr)) !important;
        }
        /* 3- and 4-across fr grids (e.g. "1fr 1fr 1fr") -> 2 across */
        [style*="1fr 1fr 1fr"] { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
        /* keep grid children from forcing overflow */
        [style*="grid-template-columns"] > * { min-width: 0 !important; }
        /* tables never overflow the viewport */
        table { display: block !important; overflow-x: auto !important; -webkit-overflow-scrolling: touch; white-space: nowrap; max-width: 100%; }
        /* long money/number values shouldn't blow out their card */
        .stat-card > div:first-child { font-size: 20px !important; overflow: hidden; text-overflow: ellipsis; }
        /* opt-out hook: add class "no-mobile-collapse" to preserve a grid as-is */
        .no-mobile-collapse { grid-template-columns: inherit !important; }
      }
      .mobile-show { display: inline !important; }
      .nav-tab-label { display: inline; }
      .boba-card-grid { grid-template-columns: repeat(2,1fr) !important; gap:6px !important; }
      .lot-comp-grid { grid-template-columns: 1fr !important; }
      .stats-grid-4 { grid-template-columns: 1fr 1fr !important; }
      .mobile-scroll-x { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
      .mobile-scroll-x table { min-width: 500px; }
      .view-mode-row { overflow-x: auto !important; flex-wrap: nowrap !important; -webkit-overflow-scrolling: touch; }
      .view-mode-row::-webkit-scrollbar { display: none; }
      .checklist-actions { flex-wrap: wrap !important; }
      .deck-pb-layout { grid-template-columns: 1fr !important; }
      .dash-grid-5 { grid-template-columns: repeat(2,1fr) !important; }
      .dash-grid-4 { grid-template-columns: repeat(2,1fr) !important; }
      .dash-grid-3 { grid-template-columns: repeat(2,1fr) !important; }
      .dash-fin-card { font-size: 18px !important; padding: 12px !important; }
      .period-btns { gap: 4px !important; }
      .period-btns button { padding: 4px 8px !important; font-size: 10px !important; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  return null;
}

function LoginScreen() {
  const [error, setError] = useState(null);
  async function handleLogin() {
    try { await signInWithPopup(auth, googleProvider); }
    catch { setError("Login failed. Please try again."); }
  }
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",background:"#000",fontFamily:"'Trebuchet MS',sans-serif",overflow:"hidden",position:"relative"}}>
      <style>{`
        @keyframes loginOrb{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(40px,-30px) scale(1.1)}66%{transform:translate(-25px,20px) scale(0.9)}}
        @keyframes loginSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes loginSpinR{from{transform:rotate(0deg)}to{transform:rotate(-360deg)}}
        @keyframes loginFade{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
        @keyframes loginPulse{0%,100%{opacity:0.5;transform:scale(0.95)}50%{opacity:1;transform:scale(1.05)}}
        .login-btn{transition:all 0.2s cubic-bezier(0.34,1.56,0.64,1) !important;}
        .login-btn:hover{transform:translateY(-2px) scale(1.02) !important;box-shadow:0 16px 48px rgba(232,49,122,0.5) !important;}
      `}</style>
      <div style={{position:"absolute",width:600,height:600,borderRadius:"50%",background:"radial-gradient(circle,rgba(232,49,122,0.07) 0%,transparent 70%)",top:"-15%",left:"-10%",animation:"loginOrb 12s ease-in-out infinite",pointerEvents:"none"}}/>
      <div style={{position:"absolute",width:500,height:500,borderRadius:"50%",background:"radial-gradient(circle,rgba(123,47,247,0.07) 0%,transparent 70%)",bottom:"-10%",right:"-10%",animation:"loginOrb 15s ease-in-out infinite reverse",pointerEvents:"none"}}/>
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:40,animation:"loginFade 0.7s ease both",position:"relative",zIndex:1}}>
        <div style={{position:"relative",width:120,height:120}}>
          <div style={{position:"absolute",inset:0,borderRadius:"50%",border:"1.5px solid rgba(232,49,122,0.2)",animation:"loginSpin 10s linear infinite"}}/>
          <div style={{position:"absolute",inset:0,borderRadius:"50%",border:"1.5px dashed rgba(123,47,247,0.15)",animation:"loginSpinR 14s linear infinite"}}/>
          <div style={{position:"absolute",inset:14,borderRadius:"50%",border:"2px solid rgba(232,49,122,0.5)",animation:"loginSpin 5s linear infinite",boxShadow:"0 0 20px rgba(232,49,122,0.2)"}}/>
          <div style={{position:"absolute",inset:28,borderRadius:"50%",border:"1.5px solid rgba(123,47,247,0.4)",animation:"loginSpinR 3s linear infinite"}}/>
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <img src="/BazookaLogo.png" alt="" style={{width:56,height:56,objectFit:"contain",filter:"drop-shadow(0 0 12px rgba(232,49,122,0.6))",animation:"loginPulse 2.5s ease-in-out infinite"}}/>
          </div>
        </div>
        <div style={{textAlign:"center"}}>
          <img src="/Bazooka_Logo_cropped.png" alt="Bazooka" style={{height:"clamp(44px,8vw,68px)",width:"auto",maxWidth:"80vw",objectFit:"contain",display:"block",margin:"0 auto 12px",filter:"drop-shadow(0 4px 16px rgba(232,49,122,0.35))"}}/>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.2)",letterSpacing:3,textTransform:"uppercase"}}>Internal Operations</div>
        </div>
        <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:20,padding:"32px 40px",textAlign:"center",minWidth:320}}>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.35)",marginBottom:24,lineHeight:1.7}}>Access restricted to<br/>Bazooka team members</div>
          <button onClick={handleLogin} className="login-btn" style={{display:"flex",alignItems:"center",gap:12,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:12,padding:"14px 28px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit",width:"100%",justifyContent:"center",boxShadow:"0 8px 32px rgba(232,49,122,0.3)"}}>
            <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#fff" fillOpacity="0.9" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 002.38-5.88c0-.57-.05-.66-.15-1.18z"/><path fill="#fff" fillOpacity="0.9" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 01-7.18-2.54H1.83v2.07A8 8 0 008.98 17z"/><path fill="#fff" fillOpacity="0.9" d="M4.5 10.52a4.8 4.8 0 010-3.04V5.41H1.83a8 8 0 000 7.18l2.67-2.07z"/><path fill="#fff" fillOpacity="0.9" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 001.83 5.4L4.5 7.49a4.77 4.77 0 014.48-3.31z"/></svg>
            Sign in with Google
          </button>
          {error && <div style={{marginTop:16,color:"#E8317A",fontSize:12,fontWeight:600}}>{error}</div>}
        </div>
      </div>
    </div>
  );
}

// Rep's month panel — own stats + everyone's market multiple (with streams + boxes).
// Shown on the rep Dashboard. Reps see all breakers' MM but only their own $.
// Rep's weekly commission — Mon–Sun weeks, paid the following Friday.
// Shows the current week (building) and last week (pending this Friday's payout).
// myComm already rolls in break commission + singles pass-through + tips.
function RepWeekPanel({ streams=[], matchedBreaker }) {
  const [expanded, setExpanded] = useState(null); // "last" | "this" | null
  if (!matchedBreaker) return null;
  const norm = x => (x||"").toLowerCase().replace(/\s+/g,"");
  const mine = streams.filter(s => norm(s.breaker) === norm(matchedBreaker));

  // Monday of the week containing `d` (local, at midnight)
  const mondayOf = (d) => {
    const x = new Date(d); x.setHours(0,0,0,0);
    const dow = x.getDay();            // 0=Sun..6=Sat
    const diff = (dow === 0 ? -6 : 1 - dow); // shift back to Monday
    x.setDate(x.getDate() + diff);
    return x;
  };
  const now = new Date();
  const thisMon = mondayOf(now);
  const thisSun = new Date(thisMon); thisSun.setDate(thisMon.getDate()+6); thisSun.setHours(23,59,59,999);
  const lastMon = new Date(thisMon); lastMon.setDate(thisMon.getDate()-7);
  const lastSun = new Date(thisMon); lastSun.setDate(thisMon.getDate()-1); lastSun.setHours(23,59,59,999);

  const streamsInRange = (start, end) => mine
    .filter(s => { if(!s.date) return false; const d = parseLocalDate(s.date); return d >= start && d <= end; })
    .sort((a,b)=>(a.date||"").localeCompare(b.date||""));

  const thisWeekList = streamsInRange(thisMon, thisSun);
  const lastWeekList = streamsInRange(lastMon, lastSun);
  const thisWeekPay = thisWeekList.reduce((sum,s)=>sum+(calcStream(s).myComm||0),0);
  const lastWeekPay = lastWeekList.reduce((sum,s)=>sum+(calcStream(s).myComm||0),0);
  const thisWeekN = thisWeekList.length;
  const lastWeekN = lastWeekList.length;

  // Payout Fridays: last week is paid this coming Friday; current week pays the Friday after.
  const fridayAfter = (sunday) => { const f = new Date(sunday); f.setDate(f.getDate()+5); return f; }; // Sun + 5 = Friday
  const fmtD = d => d.toLocaleDateString("en-US",{month:"short",day:"numeric"});
  const fmt$ = n => `$${n.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const weekRange = (a,b) => `${fmtD(a)} – ${fmtD(b)}`;

  if (thisWeekN === 0 && lastWeekN === 0) return null;

  // Per-stream breakdown list for an expanded week
  const breakdown = (list, accent) => (
    <div style={{ marginTop:12, borderTop:`1px solid ${accent}33`, paddingTop:10, display:"flex", flexDirection:"column", gap:6 }}>
      {list.length === 0 && <div style={{ fontSize:11, color:"#999", fontStyle:"italic" }}>No streams logged in this week yet.</div>}
      {list.map(s => {
        const c = calcStream(s);
        const isSingles = c.isSingles;
        const d = parseLocalDate(s.date);
        return (
          <div key={s.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, fontSize:12 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <span style={{ color:"var(--bz-ink)", fontWeight:700 }}>{s.streamName || s.breakType || "Break"}</span>
              {isSingles && <span style={{ marginLeft:6, fontSize:9, color:"#FBBF24", background:"rgba(251,191,36,0.12)", border:"1px solid rgba(251,191,36,0.3)", borderRadius:4, padding:"1px 5px", fontWeight:800 }}>🃏 Singles</span>}
              {parseFloat(s.tips)>0 && <span style={{ marginLeft:6, fontSize:9, color:"#4ade80" }}>+tips</span>}
            </div>
            <span style={{ color:"var(--bz-ink-3)", fontSize:10, whiteSpace:"nowrap" }}>{d.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}</span>
            <span style={{ color:accent, fontWeight:800, minWidth:72, textAlign:"right" }}>{fmt$(c.myComm||0)}</span>
          </div>
        );
      })}
    </div>
  );

  const weekCard = (key, list, total, n, mon, sun, opts) => {
    const isOpen = expanded === key;
    return (
      <div style={{ background:opts.bg, border:`1px solid ${opts.border}`, borderRadius:12, padding:"16px 18px" }}>
        <div style={{ fontSize:10, fontWeight:800, color:opts.accent, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>{opts.label}</div>
        <div style={{ fontSize:28, fontWeight:900, color:opts.accent, lineHeight:1 }}>{fmt$(total)}</div>
        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:8 }}>Week of {weekRange(mon,sun)} · {n} stream{n!==1?"s":""}</div>
        <div style={{ fontSize:12, fontWeight:700, color:opts.payColor, marginTop:4 }}>{opts.payLabel}</div>
        {n > 0 && (
          <button onClick={()=>setExpanded(isOpen?null:key)} style={{ marginTop:10, background:"transparent", border:`1px solid ${opts.accent}55`, color:opts.accent, borderRadius:7, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
            {isOpen ? "▾ Hide streams" : `▸ See ${n} stream${n!==1?"s":""}`}
          </button>
        )}
        {isOpen && breakdown(list, opts.accent)}
      </div>
    );
  };

  return (
    <div style={{ ...S.card, marginBottom:16 }}>
      <SectionLabel t="💸 Your Pay Weeks" />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:12, marginTop:4, alignItems:"start" }}>
        {weekCard("last", lastWeekList, lastWeekPay, lastWeekN, lastMon, lastSun, {
          bg:"rgba(74,222,128,0.06)", border:"rgba(74,222,128,0.3)", accent:"#4ade80",
          label:"Pending Payout", payColor:"#4ade80", payLabel:`💵 Paid Friday ${fmtD(fridayAfter(lastSun))}`
        })}
        {weekCard("this", thisWeekList, thisWeekPay, thisWeekN, thisMon, thisSun, {
          bg:"rgba(232,49,122,0.05)", border:"rgba(232,49,122,0.25)", accent:"#E8317A",
          label:"This Week (Building)", payColor:"var(--bz-ink-2)", payLabel:`📅 Pays Friday ${fmtD(fridayAfter(thisSun))}`
        })}
      </div>
      <div style={{ fontSize:10, color:"#8a8a8a", marginTop:10, fontStyle:"italic" }}>Includes break commission, singles pass-through, and tips. Estimate — final pay confirmed on your pay stub.</div>
    </div>
  );
}

// Quote/comp history. Streamers see only comps they submitted (savedBy match);
// admins see everyone's.
function RepQuoteHistory({ comps=[], user, userRole }) {
  const isAdmin = ["Admin"].includes(userRole?.role);
  const norm = x => (x||"").toLowerCase().replace(/\s+/g,"");
  const me = norm(user?.displayName);
  const mine = isAdmin ? comps : comps.filter(c => norm(c.savedBy) === me || norm(c.quotedBy) === me);
  const sorted = [...mine].sort((a,b)=> new Date(b.dateAdded||0) - new Date(a.dateAdded||0));
  const fmt$ = n => `$${(parseFloat(n)||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const statusChip = (s) => {
    const map = { accepted:["#4ade80","Accepted"], declined:["#E8317A","Declined"], countered:["#FBBF24","Countered"], pending:["#7B9CFF","Pending"] };
    const [c,l] = map[s] || ["#888", s||"—"];
    return <span style={{ fontSize:10, fontWeight:800, color:c, background:`${c}18`, border:`1px solid ${c}44`, borderRadius:20, padding:"2px 10px" }}>{l}</span>;
  };
  return (
    <div style={{ maxWidth:900, margin:"0 auto" }}>
      <div style={{ ...S.card }}>
        <SectionLabel t={isAdmin ? "📋 All Quote History" : "📋 Your Quote History"} />
        <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginBottom:14 }}>
          {isAdmin ? "Every comp submitted across the team." : "Comps you've submitted."} · {sorted.length} total
        </div>
        {sorted.length === 0 ? (
          <div style={{ textAlign:"center", padding:"40px", color:"var(--bz-ink-3)", fontSize:13 }}>No quotes yet.</div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {sorted.map(c => (
              <div key={c.id} style={{ background:"rgba(255,255,255,0.02)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                <div style={{ flex:1, minWidth:180 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                    <span style={{ fontSize:14, fontWeight:800, color:"var(--bz-ink)" }}>{c.seller || "Unknown seller"}</span>
                    {statusChip(c.status)}
                  </div>
                  <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:3 }}>
                    {c.date || (c.dateAdded ? new Date(c.dateAdded).toLocaleDateString() : "")} · {c.totalCards||0} card{c.totalCards!==1?"s":""}
                    {isAdmin && c.savedBy ? ` · by ${c.savedBy.split(" ")[0]}` : ""}
                  </div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:16, fontWeight:900, color:"#E8317A" }}>{fmt$(c.offer)}</div>
                  <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>offer · MV {fmt$(c.totalMarket)}</div>
                </div>
                {c.id && <a href={`/quote/${c.id}`} target="_blank" rel="noreferrer" style={{ fontSize:11, fontWeight:700, color:"#7B9CFF", textDecoration:"none", border:"1px solid rgba(123,156,255,0.3)", borderRadius:7, padding:"5px 12px" }}>View ↗</a>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RepMonthPanel({ streams=[], matchedBreaker }) {
  if (!matchedBreaker) return null;
  const now = new Date();
  const thisMonth = streams.filter(s => {
    if (!s.date) return false;
    const d = parseLocalDate(s.date);
    return d.getMonth()===now.getMonth() && d.getFullYear()===now.getFullYear();
  });
  if (thisMonth.length === 0) return null;
  const boxesOf = s => PRODUCT_TYPES.reduce((sum,pt)=>sum+(parseInt(s[`prod_${pt}`])||0),0);
  const mmStreams = thisMonth.filter(s => parseFloat(s.marketMultiple) > 0 && (s.channel||"Bazooka Vault") !== "Orbital Society");
  const byBreaker = {};
  mmStreams.forEach(s => {
    const b = s.breaker || "—";
    if (!byBreaker[b]) byBreaker[b] = { sum:0, n:0, boxes:0 };
    byBreaker[b].sum += parseFloat(s.marketMultiple); byBreaker[b].n++; byBreaker[b].boxes += boxesOf(s);
  });
  const mmRows = Object.entries(byBreaker).map(([b,v])=>({ b, mm:v.sum/v.n, n:v.n, boxes:v.boxes })).sort((a,b)=>b.mm-a.mm);
  const norm = x => (x||"").toLowerCase().replace(/\s+/g,"");
  const mine = thisMonth.filter(s => norm(s.breaker) === norm(matchedBreaker));
  // Split singles (pass-through income) out from commission so they're not conflated.
  const mineBreaks  = mine.filter(s => !isSinglesStream(s));
  const mineSingles = mine.filter(s => isSinglesStream(s));
  const myGross = mineBreaks.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);
  const myComm  = mineBreaks.reduce((s,x)=>s+(calcStream(x).myComm||0),0);
  const mySingles = mineSingles.reduce((s,x)=>s+(calcStream(x).myComm||0),0); // singles pass-through to breaker
  const hasSingles = mineSingles.length > 0;
  const myMMs = mineBreaks.filter(x=>parseFloat(x.marketMultiple)>0);
  const myAvgMM = myMMs.length ? myMMs.reduce((s,x)=>s+parseFloat(x.marketMultiple),0)/myMMs.length : 0;
  const mmColor = m => m>=1.5?"#4ade80":m>=1.3?"#FBBF24":"#E8317A";
  const statCards = [
    { l:"Your Gross",      v:`$${myGross.toLocaleString("en-US",{maximumFractionDigits:0})}`, c:"#E8317A" },
    { l:"Your Commission", v:`$${myComm.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`, c:"#4ade80" },
    ...(hasSingles ? [{ l:"🃏 Singles Rev (pass-through)", v:`$${mySingles.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`, c:"#FBBF24" }] : []),
    { l:"Your Streams",    v:mine.length, c:"var(--bz-ink)" },
    { l:"Your Avg MM",     v:myAvgMM>0?`${myAvgMM.toFixed(2)}x`:"--", c:mmColor(myAvgMM) },
  ];
  return (
    <div style={{ ...S.card, marginBottom:16 }}>
      <SectionLabel t={`📊 Your Month — ${matchedBreaker}`} />
      <div style={{ display:"grid", gridTemplateColumns:`repeat(${statCards.length},1fr)`, gap:12, marginBottom:20 }}>
        {statCards.map(({l,v,c})=>(
          <div key={l} className="stat-card" style={{ ...S.card, textAlign:"center" }}>
            <div style={{ fontSize:24, fontWeight:900, color:c }}>{v}</div>
            <div style={{ fontSize:10, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1 }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize:11, fontWeight:800, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>Team Market Multiple — This Month</div>
      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        {mmRows.map(({b,mm,n,boxes})=>(
          <div key={b} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background: b===matchedBreaker ? "rgba(232,49,122,0.08)" : "rgba(255,255,255,0.02)", border:`1px solid ${b===matchedBreaker ? "rgba(232,49,122,0.3)" : "rgba(255,255,255,0.06)"}`, borderRadius:8 }}>
            <span style={{ fontSize:13, fontWeight:700, color: b===matchedBreaker ? "#E8317A" : "#ccc", flex:1 }}>{b}{b===matchedBreaker?" (you)":""}</span>
            <span style={{ fontSize:11, color:"#999", width:80, textAlign:"right" }}>{n} stream{n!==1?"s":""}</span>
            <span style={{ fontSize:11, color:"#7B9CFF", width:90, textAlign:"right" }}>{boxes} box{boxes!==1?"es":""}</span>
            <span style={{ fontSize:15, fontWeight:900, color:mmColor(mm), width:70, textAlign:"right" }}>{mm.toFixed(2)}x</span>
          </div>
        ))}
      </div>
      <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:10, fontStyle:"italic" }}>You can see everyone's market multiple, but only your own revenue and commission.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD HERO — new visual system (gradient hero + live gradient charts).
// Self-contained + additive: takes already-computed financial values as props so
// it never touches the settlement math. Renders the big True Net hero, KPI cards
// with sparklines, a Revenue-vs-Net gradient bar chart, and a commission-tier donut.
// ─────────────────────────────────────────────────────────────────────────────
function useChartJs() {
  const [loaded, setLoaded] = useState(!!window.Chart);
  useEffect(() => {
    if (window.Chart) { setLoaded(true); return; }
    const existing = document.querySelector('script[data-chartjs]');
    if (existing) { existing.addEventListener("load", () => setLoaded(true)); return; }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
    s.setAttribute("data-chartjs", "1");
    s.onload = () => setLoaded(true);
    document.head.appendChild(s);
  }, []);
  return loaded;
}

function DashHeroSpark({ id, data, up = true }) {
  const loaded = useChartJs();
  useEffect(() => {
    if (!loaded || !window.Chart) return;
    const el = document.getElementById(id);
    if (!el) return;
    if (el._chart) el._chart.destroy();
    const ctx = el.getContext("2d");
    const col = up ? "#E8317A" : "#fbbf24";
    el._chart = new window.Chart(el, {
      type: "line",
      data: { labels: data.map((_, i) => i), datasets: [{
        data,
        borderColor: (c) => { const a = c.chart.chartArea; if (!a) return col; const g = c.chart.ctx.createLinearGradient(a.left,0,a.right,0); g.addColorStop(0, up?"#ff8fc0":"#ffd67a"); g.addColorStop(1, col); return g; },
        borderWidth: 2.5, pointRadius: 0, tension: 0.4, fill: true,
        backgroundColor: (c) => { const a = c.chart.chartArea; if (!a) return "transparent"; const g = c.chart.ctx.createLinearGradient(0,a.top,0,a.bottom); g.addColorStop(0, up?"rgba(232,49,122,0.38)":"rgba(251,191,36,0.34)"); g.addColorStop(1,"rgba(232,49,122,0)"); return g; }
      }]},
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { enabled: false } }, scales: { x: { display: false }, y: { display: false } }, animation: { duration: 600 } }
    });
    return () => { if (el._chart) { el._chart.destroy(); el._chart = null; } };
  }, [loaded, id, JSON.stringify(data), up]);
  return <canvas id={id} role="img" aria-label="trend" />;
}

function DashMiniSpark({ id, data, c1, c2, up = true }) {
  const loaded = useChartJs();
  useEffect(() => {
    if (!loaded || !window.Chart) return;
    const el = document.getElementById(id);
    if (!el) return;
    if (el._chart) el._chart.destroy();
    const color = up ? c1 : "#fbbf24";
    el._chart = new window.Chart(el, {
      type: "line",
      data: { labels: data.map((_, i) => i), datasets: [{
        data,
        borderColor: (ctx) => { const a = ctx.chart.chartArea; if (!a) return color; const g = ctx.chart.ctx.createLinearGradient(a.left,0,a.right,0); g.addColorStop(0, up?c1:"#ffd67a"); g.addColorStop(1, up?c2:"#d99614"); return g; },
        borderWidth: 2, pointRadius: 0, tension: 0.4, fill: false
      }]},
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { enabled: false } }, scales: { x: { display: false }, y: { display: false } }, animation: { duration: 500 } }
    });
    return () => { if (el._chart) { el._chart.destroy(); el._chart = null; } };
  }, [loaded, id, JSON.stringify(data), c1, c2, up]);
  return <canvas id={id} role="img" aria-label="trend" />;
}

function DashRevChart({ id, labels, gross, net }) {
  const loaded = useChartJs();
  useEffect(() => {
    if (!loaded || !window.Chart) return;
    const el = document.getElementById(id);
    if (!el) return;
    if (el._chart) el._chart.destroy();
    const gGross = (c) => { const a = c.chart.chartArea; if (!a) return "#E8317A"; const g = c.chart.ctx.createLinearGradient(0,a.top,0,a.bottom); g.addColorStop(0,"#ff5c9e"); g.addColorStop(1,"#c01f5f"); return g; };
    const gNet = (c) => { const a = c.chart.chartArea; if (!a) return "#34d399"; const g = c.chart.ctx.createLinearGradient(0,a.top,0,a.bottom); g.addColorStop(0,"#5ee6a0"); g.addColorStop(1,"#1f9d6b"); return g; };
    el._chart = new window.Chart(el, {
      type: "line",
      data: { labels, datasets: [
        { label: "Gross", data: gross, borderColor: "#E8317A", borderWidth: 2.5, pointRadius: 0, tension: 0.35, fill: true,
          backgroundColor: (c) => { const a = c.chart.chartArea; if (!a) return "transparent"; const g = c.chart.ctx.createLinearGradient(0,a.top,0,a.bottom); g.addColorStop(0,"rgba(232,49,122,0.35)"); g.addColorStop(1,"rgba(232,49,122,0)"); return g; } },
        { label: "Net", data: net, borderColor: "#34d399", borderWidth: 2.5, pointRadius: 0, tension: 0.35, fill: true,
          backgroundColor: (c) => { const a = c.chart.chartArea; if (!a) return "transparent"; const g = c.chart.ctx.createLinearGradient(0,a.top,0,a.bottom); g.addColorStop(0,"rgba(52,211,153,0.28)"); g.addColorStop(1,"rgba(52,211,153,0)"); return g; } }
      ]},
      options: { responsive: true, maintainAspectRatio: false, animation: { duration: 700 },
        interaction: { mode: "index", intersect: false },
        plugins: { legend: { display: false }, tooltip: { backgroundColor: "#1a1218", borderColor: "rgba(232,49,122,0.3)", borderWidth: 1, padding: 9, callbacks: { label: (c) => c.dataset.label + ": $" + c.parsed.y.toLocaleString() } } },
        scales: { x: { grid: { display: false }, ticks: { color: "#9a8a94", font: { size: 10, weight: "700" } }, border: { color: "rgba(255,255,255,0.1)" } },
          y: { grid: { color: "rgba(255,255,255,0.05)" }, ticks: { color: "#7a6b74", font: { size: 9 }, callback: (v) => "$" + (v/1000) + "k" }, border: { display: false } } } }
    });
    return () => { if (el._chart) { el._chart.destroy(); el._chart = null; } };
  }, [loaded, id, JSON.stringify(labels), JSON.stringify(gross), JSON.stringify(net)]);
  return <canvas id={id} role="img" aria-label="Revenue vs true net" />;
}

function DashTierChart({ id, tiers }) {
  const loaded = useChartJs();
  useEffect(() => {
    if (!loaded || !window.Chart) return;
    const el = document.getElementById(id);
    if (!el) return;
    if (el._chart) el._chart.destroy();
    const labels = tiers.map(t => t.label), vals = tiers.map(t => t.count), base = tiers.map(t => t.color);
    const shade = (hex, amt) => { const n = parseInt(hex.slice(1),16); const r = Math.max(0,((n>>16)&255)+amt), g = Math.max(0,((n>>8)&255)+amt), b = Math.max(0,(n&255)+amt); return `rgb(${r},${g},${b})`; };
    const makeCols = (chart) => { const a = chart.chartArea; if (!a) return base; return base.map(col => { const g = chart.ctx.createLinearGradient(a.left,a.top,a.right,a.bottom); g.addColorStop(0,col); g.addColorStop(1,shade(col,-32)); return g; }); };
    el._chart = new window.Chart(el, {
      type: "doughnut",
      data: { labels, datasets: [{ data: vals, backgroundColor: makeCols, borderColor: "#130d11", borderWidth: 3, hoverOffset: 6 }] },
      options: { responsive: true, maintainAspectRatio: false, cutout: "62%", animation: { duration: 700 },
        plugins: { legend: { display: false }, tooltip: { backgroundColor: "#1a1218", padding: 9, callbacks: { label: (c) => c.label + " tier: " + c.parsed + " streams" } } } }
    });
    return () => { if (el._chart) { el._chart.destroy(); el._chart = null; } };
  }, [loaded, id, JSON.stringify(tiers)]);
  return <canvas id={id} role="img" aria-label="Commission tier mix" />;
}

function DashWaterfall({ id, steps }) {
  // steps: [{label, from, to, kind}] where kind is 'total' | 'down' | 'up'
  const loaded = useChartJs();
  useEffect(() => {
    if (!loaded || !window.Chart) return;
    const el = document.getElementById(id);
    if (!el) return;
    if (el._chart) el._chart.destroy();
    const colorFor = (k) => k === "total" ? ["#8a1533","#ff4d94"] : k === "up" ? ["#159d64","#34d399"] : ["#7a6b74","#b8a5ae"];
    el._chart = new window.Chart(el, {
      type: "bar",
      data: {
        labels: steps.map(s => s.label),
        datasets: [{
          data: steps.map(s => [s.from, s.to]),
          backgroundColor: (c) => { const a = c.chart.chartArea; const s = steps[c.dataIndex]; if (!s) return "#E8317A"; const [c1,c2] = colorFor(s.kind); if (!a) return c2; const g = c.chart.ctx.createLinearGradient(0,a.top,0,a.bottom); g.addColorStop(0,c2); g.addColorStop(1,c1); return g; },
          borderRadius: 4, barPercentage: 0.72, categoryPercentage: 0.82,
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false, animation: { duration: 750 },
        plugins: { legend: { display: false }, tooltip: { backgroundColor: "#1a1218", borderColor: "rgba(232,49,122,0.3)", borderWidth: 1, padding: 9, callbacks: { label: (c) => { const s = steps[c.dataIndex]; const amt = Math.abs(s.to - s.from); return (s.kind === "total" ? s.label + ": $" : (s.kind === "up" ? "+ $" : "− $")) + Math.round(amt).toLocaleString(); } } } },
        scales: { x: { grid: { display: false }, ticks: { color: "#9a8a94", font: { size: 9, weight: "700" }, maxRotation: 30, minRotation: 0, autoSkip: false }, border: { color: "rgba(255,255,255,0.1)" } },
          y: { grid: { color: "rgba(255,255,255,0.05)" }, ticks: { color: "#7a6b74", font: { size: 9 }, callback: (v) => "$" + Math.round(v/1000) + "k" }, border: { display: false } } }
      }
    });
    return () => { if (el._chart) { el._chart.destroy(); el._chart = null; } };
  }, [loaded, id, JSON.stringify(steps)]);
  return <canvas id={id} role="img" aria-label="P&L money flow waterfall" />;
}

function DashboardHero({ net, netDelta, rev, comm, mm, breaksCount, heroSpark, miniRev, miniComm, miniMM, miniBreaks, revLabels, revGross, revNet, tiers, board, fmt, flow, flowNet }) {
  const [dRev, setDRev] = useState(0), [dComm, setDComm] = useState(0), [dMM, setDMM] = useState(0), [dBreaks, setDBreaks] = useState(0), [dNet, setDNet] = useState(0);
  useEffect(() => {
    const tgt = { net, rev, comm, mm, breaksCount };
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / 650), e = 1 - Math.pow(1 - p, 3);
      setDNet(net * e); setDRev(rev * e); setDComm(comm * e); setDMM(mm * e); setDBreaks(Math.round(breaksCount * e));
      if (p < 1) raf = requestAnimationFrame(step);
      else { setDNet(net); setDRev(rev); setDComm(comm); setDMM(mm); setDBreaks(breaksCount); }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [net, rev, comm, mm, breaksCount]);

  const card = { background: "linear-gradient(160deg,#181016,#130d11)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "13px 15px" };
  const kpiLabel = { fontSize: 10.5, fontWeight: 800, color: "#9a8a94", textTransform: "uppercase", letterSpacing: 1 };
  const maxBoard = Math.max(1, ...board.map(b => b.gross));

  return (
    <div style={{ marginBottom: 16 }}>
      {/* HERO */}
      <div style={{ position: "relative", background: "linear-gradient(135deg,#24101c 0%,#160d13 55%,#130d11 100%)", border: "1px solid rgba(232,49,122,0.28)", borderRadius: 20, padding: "22px 26px", marginBottom: 14, overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#E8317A,#ff4d94,#a78bfa,#7c9cff)" }} />
        <div style={{ position: "absolute", right: -50, top: -60, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle,rgba(232,49,122,0.22),transparent 70%)" }} />
        <div style={{ position: "absolute", left: "30%", bottom: -80, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle,rgba(167,139,250,0.12),transparent 70%)" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16, position: "relative" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#ff4d94", textTransform: "uppercase", letterSpacing: 2.5, marginBottom: 2 }}>Bazooka True Net</div>
            <div style={{ fontSize: 60, fontWeight: 900, lineHeight: 1, letterSpacing: -1.5, background: "linear-gradient(180deg,#ffffff 0%,#ffd9e8 60%,#ff8fc0 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", filter: "drop-shadow(0 0 24px rgba(232,49,122,0.45))" }}>{fmt(dNet)}</div>
            {netDelta != null && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                <span style={{ background: netDelta >= 0 ? "linear-gradient(135deg,rgba(52,211,153,0.25),rgba(52,211,153,0.1))" : "linear-gradient(135deg,rgba(239,68,68,0.25),rgba(239,68,68,0.1))", color: netDelta >= 0 ? "#34d399" : "#ef4444", fontSize: 12, fontWeight: 800, padding: "3px 10px", borderRadius: 20 }}>{netDelta >= 0 ? "▲" : "▼"} {Math.abs(netDelta).toFixed(1)}%</span>
                <span style={{ fontSize: 12, color: "#9a8a94" }}>vs last period</span>
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 200, maxWidth: 340 }}>
            <div style={{ position: "relative", height: 92 }}><DashHeroSpark id="dashHeroSpark" data={heroSpark} up={netDelta == null || netDelta >= 0} /></div>
          </div>
        </div>
      </div>

      {/* KPI ROW */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 11, marginBottom: 14 }}>
        <div style={card}>
          <div style={kpiLabel}>Gross Revenue</div>
          <div style={{ fontSize: 23, fontWeight: 900, marginTop: 5 }}>{fmt(dRev)}</div>
          <div style={{ position: "relative", height: 32, marginTop: 4 }}><DashMiniSpark id="dashM1" data={miniRev} c1="#ff8fc0" c2="#E8317A" /></div>
        </div>
        <div style={card}>
          <div style={kpiLabel}>Commission</div>
          <div style={{ fontSize: 23, fontWeight: 900, marginTop: 5, color: "#34d399" }}>{fmt(dComm)}</div>
          <div style={{ position: "relative", height: 32, marginTop: 4 }}><DashMiniSpark id="dashM2" data={miniComm} c1="#5ee6a0" c2="#159d64" /></div>
        </div>
        <div style={card}>
          <div style={kpiLabel}>Avg Mkt Mult</div>
          <div style={{ fontSize: 23, fontWeight: 900, marginTop: 5, color: "#7c9cff" }}>{dMM.toFixed(2)}x</div>
          <div style={{ position: "relative", height: 32, marginTop: 4 }}><DashMiniSpark id="dashM3" data={miniMM} c1="#a9c0ff" c2="#5578e0" /></div>
        </div>
        <div style={card}>
          <div style={kpiLabel}>Breaks</div>
          <div style={{ fontSize: 23, fontWeight: 900, marginTop: 5, color: "#fbbf24" }}>{dBreaks}</div>
          <div style={{ position: "relative", height: 32, marginTop: 4 }}><DashMiniSpark id="dashM4" data={miniBreaks} c1="#ffd67a" c2="#d99614" /></div>
        </div>
      </div>

      {/* CHARTS ROW */}
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 12, marginBottom: 14 }}>
        <div style={{ ...card, padding: "15px 17px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800 }}>Revenue vs True Net <span style={{ fontSize: 10, fontWeight: 600, color: "#7a6b74" }}>· running total</span></div>
            <div style={{ display: "flex", gap: 12, fontSize: 10.5, color: "#9a8a94" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: "linear-gradient(180deg,#ff4d94,#E8317A)" }} />Gross</span>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: "linear-gradient(180deg,#4ade80,#34d399)" }} />Net</span>
            </div>
          </div>
          <div style={{ position: "relative", height: 180 }}><DashRevChart id="dashRevChart" labels={revLabels} gross={revGross} net={revNet} /></div>
        </div>
        <div style={{ ...card, padding: "15px 17px" }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, marginBottom: 8 }}>Commission Tier Mix</div>
          <div style={{ position: "relative", height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}><DashTierChart id="dashTierChart" tiers={tiers} /></div>
        </div>
      </div>

      {/* MONEY FLOW — clean labeled breakdown: Bazooka share → deductions/adds → True Net */}
      {flow && flow.length > 0 && (
        <div style={{ background: "linear-gradient(160deg,#181016,#130d11)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, padding: "16px 18px", marginBottom: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, marginBottom: 12 }}>Money Flow <span style={{ fontSize: 10, fontWeight: 600, color: "#7a6b74" }}>· how Bazooka's share becomes True Net</span></div>
          <div>
            {flow.map((r, i) => {
              const isStart = r.kind === "start";
              const isIn = r.kind === "in";
              const color = isStart ? "#f6eef2" : isIn ? "#34d399" : "#ff6b8a";
              const sign = isStart ? "" : isIn ? "+ " : "− ";
              return (
                <div key={r.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, opacity: isStart ? 1 : 0.8, flexShrink: 0 }} />
                    <span style={{ fontSize: 12.5, fontWeight: isStart ? 800 : 600, color: isStart ? "#f6eef2" : "#c9bdc4" }}>{r.label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>{sign}{fmt(Math.abs(r.val))}</span>
                </div>
              );
            })}
            {/* Result row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 0 3px", marginTop: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 900, color: "#ff4d94", textTransform: "uppercase", letterSpacing: 1 }}>= Bazooka True Net</span>
              <span style={{ fontSize: 20, fontWeight: 900, color: "#ff4d94", fontVariantNumeric: "tabular-nums", filter: "drop-shadow(0 0 12px rgba(232,49,122,0.4))" }}>{fmt(flowNet)}</span>
            </div>
          </div>
        </div>
      )}

      {/* LEADERBOARD */}
      {board.length > 0 && (
        <div style={{ ...card, padding: "15px 17px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 13 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800 }}>Breaker Leaderboard</div>
            <div style={{ fontSize: 10.5, color: "#9a8a94" }}>gross · Bazooka net · avg mult</div>
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "0 6px 6px", fontSize: 9.5, fontWeight: 700, color: "#7a6b74", textTransform: "uppercase", letterSpacing: 0.5 }}>
              <div style={{ width: 18 }} />
              <div style={{ width: 64 }}>Breaker</div>
              <div style={{ flex: 1 }} />
              <div style={{ width: 64, textAlign: "right" }}>Gross</div>
              <div style={{ width: 48, textAlign: "right" }}>Baz Net</div>
              <div style={{ width: 38, textAlign: "right" }}>Mult</div>
            </div>
            {board.map((r, i) => {
              const w = Math.round(r.gross / maxBoard * 100);
              return (
                <div key={r.name} style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: i < board.length - 1 ? 10 : 0, padding: "5px 6px", borderRadius: 8 }}>
                  <div style={{ width: 18, fontSize: 12, fontWeight: 900, color: "#7a6b74" }}>{i + 1}</div>
                  <div style={{ width: 64, fontSize: 12, fontWeight: 800 }}>{r.name}</div>
                  <div style={{ flex: 1, height: 20, background: "rgba(255,255,255,0.04)", borderRadius: 6, overflow: "hidden", position: "relative" }}>
                    <div style={{ height: "100%", width: w + "%", background: `linear-gradient(90deg,${r.color}99,${r.color})`, borderRadius: 6, boxShadow: `0 0 12px ${r.color}66`, transition: "width .9s cubic-bezier(.22,1,.36,1)" }} />
                  </div>
                  <div style={{ width: 64, textAlign: "right", fontSize: 12, fontWeight: 800, color: r.color }}>{fmt(r.gross)}</div>
                  <div style={{ width: 48, textAlign: "right", fontSize: 11, color: "#9a8a94" }}>{fmt(r.net)}</div>
                  <div style={{ width: 38, textAlign: "right", fontSize: 11, fontWeight: 700, color: "#7c9cff" }}>{r.mm.toFixed(2)}x</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard({ inventory, breaks, user, userRole, streams=[], historicalData=[], onSaveHistorical, onDeleteHistorical, payStubs=[], onDismissPayStub, quotes=[], onDismissQuoteNotif, cardPools=[], imcAdjustmentsData={}, onSaveImcAdjustments, plannedStreams=[] }) {
  const canSeeFinancials = ["Admin"].includes(userRole?.role);
  const curUser    = user?.displayName?.split(" ")[0] || "";
  const myBreaker  = resolveBreaker(user);
  const GOAL_KEY   = `bz_yeargoals_${new Date().getFullYear()}`;
  const [goals,     setGoals]     = useState(()=>{ try { return JSON.parse(localStorage.getItem(GOAL_KEY)||"{}"); } catch(e) { return {}; } });
  const [editGoals, setEditGoals] = useState(false);
  const [goalForm,  setGoalForm]  = useState(()=>{ try { return JSON.parse(localStorage.getItem(GOAL_KEY)||"{}"); } catch(e) { return {}; } });

  // Pay stub notifications for this breaker
  const myStubs = payStubs.filter(s => s.breaker === myBreaker && !s.read);
  // Quote notifications for admins
  const quoteNotifs = canSeeFinancials ? quotes.filter(q => !q.notified && (["accepted","declined","countered"].includes(q.status) || q.submittedBySeller)) : [];
  const [viewStub,  setViewStub]  = useState(null);
  const [viewQuote, setViewQuote] = useState(null);
  const [lightbox,  setLightbox]  = useState(null);
  const [quotesCollapsed, setQuotesCollapsed] = useState(true); // lot submissions collapsed by default
  const [financialPeriod, setFinancialPeriod] = useState("month");
  const [exportStatus, setExportStatus] = useState("");   // internal-data JSON export
  const [exportStatus2, setExportStatus2] = useState(""); // financial-only export (streams + history)
  const [exportStatus3, setExportStatus3] = useState(""); // buyer + campaign export
  const [customStart,     setCustomStart]     = useState("");
  const [customEnd,       setCustomEnd]       = useState("");
  const [drillDown,       setDrillDown]       = useState(null);
  const [showHist,    setShowHist]    = useState(false);
  const [histForm,    setHistForm]    = useState({ yearMonth:"", grossRevenue:"", netRevenue:"", imcReimb:"", newBuyers:"", notes:"" });
  const [editingId,   setEditingId]   = useState(null);
  const [imcAdjustments, setImcAdjustments] = useState(imcAdjustmentsData||{});
  useEffect(() => { setImcAdjustments(imcAdjustmentsData||{}); }, [imcAdjustmentsData]);
  function updateImcAdj(mk, val) {
    const next = { ...imcAdjustments, [mk]: val };
    setImcAdjustments(next);
    if (onSaveImcAdjustments) onSaveImcAdjustments(next);
  }
  function clearImcAdj() {
    setImcAdjustments({});
    if (onSaveImcAdjustments) onSaveImcAdjustments({});
  }
  const [showImcAdj,  setShowImcAdj]  = useState(false);
  const [migrating,   setMigrating]   = useState(false);
  const [migDone,     setMigDone]     = useState(false);
  const [opsPeriod,    setOpsPeriod]    = useState("month");
  const [opsFrom,      setOpsFrom]      = useState("");
  const [opsTo,        setOpsTo]        = useState("");
  const usedIds    = new Set(breaks.filter(b=>!b.isPoolLog).map(b => b.inventoryId));
  const transitIds = new Set(inventory.filter(c => c.cardStatus === "in_transit").map(c => c.id));
  const USAGE_TO_CT = { "Giveaway":"Giveaway Cards", "Insurance":"Insurance Cards", "First-Timer Pack":"First-Timer Cards", "Chaser Pull":"Chaser Cards", "Chaser":"Chaser Cards" };
  // Count individual-card usage by usage type (exclude pool logs — pool usage comes from pool.usedQty)
  const usedByType = {};
  CARD_TYPES.forEach(ct => { usedByType[ct] = 0; });
  breaks.forEach(b => {
    if (b.isPoolLog) return; // pool usage counted separately below
    const ct = USAGE_TO_CT[b.usage] || b.cardType;
    if (ct && usedByType[ct] !== undefined) usedByType[ct]++;
  });
  // Add pool usage (usedQty is the ground truth for pools, qty-aware)
  cardPools.forEach(p => {
    const ct = p.cardType;
    if (ct && usedByType[ct] !== undefined) usedByType[ct] += (parseInt(p.usedQty)||0);
  });
  const stats = {};
  CARD_TYPES.forEach(ct => { stats[ct] = { total:0, avail:0, used:0, inTransit:0, invested:0, investedAll:0, market:0 }; });
  inventory.forEach(c => {
    const s = stats[c.cardType]; if (!s) return;
    s.total++;
    s.investedAll += (c.costPerCard||0);
    if (usedIds.has(c.id)) return; // skip used cards for avail/cost/market
    s.invested += (c.costPerCard||0);
    s.market   += (c.marketValue||0);
    if (c.cardStatus === "in_transit") { s.inTransit++; } else { s.avail++; }
  });
  // Add pool available cards to the relevant card type
  cardPools.forEach(p => {
    const s = stats[p.cardType]; if (!s) return;
    const poolAvail = Math.max(0, (parseInt(p.totalQty)||0) - (parseInt(p.usedQty)||0));
    s.total += parseInt(p.totalQty)||0;
    s.avail += poolAvail;
  });
  CARD_TYPES.forEach(ct => { stats[ct].used = usedByType[ct]; });
  const totInv      = Object.values(stats).reduce((a,b) => a+b.invested, 0);
  const totInvAll   = Object.values(stats).reduce((a,b) => a+b.investedAll, 0);
  const totMkt      = Object.values(stats).reduce((a,b) => a+b.market, 0);
  const oPct        = totMkt > 0 ? totInv/totMkt : null;
  const oz          = getZone(oPct);
  const usedCount   = [...usedIds].length + cardPools.reduce((s,p)=>s+(parseInt(p.usedQty)||0),0);
  const transitCount = inventory.filter(c => c.cardStatus === "in_transit" && !usedIds.has(c.id)).length;
  const poolAvailTotal = cardPools.reduce((s,p)=>s+Math.max(0,(parseInt(p.totalQty)||0)-(parseInt(p.usedQty)||0)),0);
  const availCount  = inventory.length - [...usedIds].length - transitCount + poolAvailTotal;

  const runway = {};
  CARD_TYPES.forEach(ct => {
    const avail = stats[ct].avail;
    const ctBreaks = breaks.filter(b => !b.isPoolLog && (USAGE_TO_CT[b.usage] || b.cardType) === ct);
    // For pools, estimate daily usage from usedQty and pool creation date
    const poolUsed = cardPools.filter(p=>p.cardType===ct).reduce((s,p)=>s+(parseInt(p.usedQty)||0),0);
    const totalUsedForRate = ctBreaks.length + poolUsed;
    if (totalUsedForRate === 0) { runway[ct] = 999; return; }
    const allDates = [
      ...ctBreaks.map(b => new Date(b.dateAdded||b.date)),
    ].filter(d => !isNaN(d));
    if (!allDates.length) { runway[ct] = 999; return; }
    const earliest = allDates.reduce((mn, d) => d < mn ? d : mn, new Date());
    const days = Math.max(1, Math.floor((new Date() - earliest) / 86400000));
    runway[ct] = Math.floor(avail / (totalUsedForRate / days));
  });

  const alerts = CARD_TYPES.filter(ct => (stats[ct].avail) < TARGETS[ct].buffer);

  const calcStreamDash = (s) => calcStreamMemo(s);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* Lightbox overlay */}
      {lightbox && createPortal(
        <div onClick={()=>setLightbox(null)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:2147483600, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"zoom-out", gap:14 }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:700 }}>📷 Photo — click anywhere to close</div>
          <img src={lightbox} alt="Lot photo"
            style={{ maxWidth:"90vw", maxHeight:"80vh", objectFit:"contain", borderRadius:10, border:"2px solid rgba(255,255,255,0.15)", background:"var(--bz-s1)", boxShadow:"0 0 60px rgba(0,0,0,0.8)" }}
            onClick={e=>e.stopPropagation()} onError={e=>{ e.currentTarget.alt="⚠️ Image failed to load — use Open original below"; e.currentTarget.style.minWidth="300px"; e.currentTarget.style.minHeight="200px"; }}/>
          <a href={lightbox} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{ fontSize:13, color:"#7B9CFF", background:"rgba(0,0,0,0.6)", padding:"8px 18px", borderRadius:8, textDecoration:"none", fontWeight:700 }}>Open original in new tab ↗</a>
          <button onClick={()=>setLightbox(null)}
            style={{ position:"fixed", top:20, right:24, background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.2)", color:"#fff", borderRadius:"50%", width:40, height:40, fontSize:20, cursor:"pointer", fontFamily:"inherit", lineHeight:1 }}>✕</button>
        </div>, document.body
      )}

      {/* -- QUOTE NOTIFICATIONS (Admin) -- */}
      {quoteNotifs.length > 0 && (
        <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:14, overflow:"hidden" }}>
          <div onClick={()=>setQuotesCollapsed(c=>!c)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", cursor:"pointer" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ fontSize:20 }}>📬</span>
              <span style={{ fontSize:14, fontWeight:800, color:"#7B9CFF" }}>New Lot Submissions</span>
              <span style={{ fontSize:11, fontWeight:800, color:"#fff", background:"#7B9CFF", borderRadius:20, padding:"2px 9px" }}>{quoteNotifs.length}</span>
            </div>
            <span style={{ fontSize:12, color:"var(--bz-ink-3)", fontWeight:700 }}>{quotesCollapsed ? "▸ Show" : "▾ Hide"}</span>
          </div>
          {!quotesCollapsed && (
          <div style={{ display:"flex", flexDirection:"column", gap:12, padding:"0 16px 16px" }}>
      {quoteNotifs.map(q => {
        const cfg = {
          accepted: { icon:"\uD83C\uDF89", color:"#4ade80", bg:"#0a1a0a", border:"#4ade8033", title:"Offer Accepted!", body:`${q.seller?.name||"Seller"} accepted your offer of $${parseFloat(q.dispOffer||0).toFixed(2)}` },
          declined: { icon:"\u274C", color:"#E8317A", bg:"#1a0a0a", border:"#E8317A33", title:"Offer Declined", body:`${q.seller?.name||"Seller"} declined your offer of $${parseFloat(q.dispOffer||0).toFixed(2)}` },
          countered:{ icon:"\uD83E\uDD1D", color:"#FBBF24", bg:"#1a1400", border:"#FBBF2433", title:"Counter Offer!", body:`${q.seller?.name||"Seller"} countered at $${parseFloat(q.sellerCounter||0).toFixed(2)} (you offered $${parseFloat(q.currentOffer||q.dispOffer||0).toFixed(2)})` },
          pending:  q.submittedBySeller ? { icon:"📬", color:"#7B9CFF", bg:"#0a0a1a", border:"#7B9CFF33", title:"New Lot Submission!", body:`${q.seller?.name||"Someone"} submitted ${(q.cards||[]).length} card${(q.cards||[]).length!==1?"s":""} for a quote via bazookadash.com/sell` } : null,
        }[q.status] || (q.submittedBySeller ? { icon:"📬", color:"#7B9CFF", bg:"#0a0a1a", border:"#7B9CFF33", title:"New Lot Submission!", body:`${q.seller?.name||"Someone"} submitted ${(q.cards||[]).length} card${(q.cards||[]).length!==1?"s":""} for a quote via bazookadash.com/sell` } : null);
        if (!cfg) return null;
        return (
          <div key={q.id} style={{ background:cfg.bg, border:`2px solid ${cfg.border}`, borderRadius:14, padding:"18px 20px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:16, flexWrap:"wrap" }}>
              <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ fontSize:28 }}>{cfg.icon}</div>
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
                    <span style={{ fontSize:14, fontWeight:800, color:cfg.color }}>{cfg.title}</span>
                    {q.quoteRef && <span style={{ fontSize:11, fontWeight:700, color:"#7B9CFF", background:"rgba(123,156,255,0.08)", border:"1px solid rgba(123,156,255,0.2)", borderRadius:6, padding:"2px 8px", letterSpacing:0.5 }}>{q.quoteRef}</span>}
                    {(() => {
                      const offerVal = parseFloat(q.currentOffer || q.dispOffer || 0) || 0;
                      const alreadyQuoted = offerVal > 0 || !!q.quotedBy || (q.status && q.status!=="pending");
                      return alreadyQuoted
                        ? <span style={{ fontSize:11, fontWeight:800, color:"#4ade80", background:"rgba(74,222,128,0.12)", border:"1px solid rgba(74,222,128,0.35)", borderRadius:6, padding:"2px 9px" }}>✅ Already quoted{offerVal>0?` · $${offerVal.toFixed(2)}`:""}</span>
                        : <span style={{ fontSize:11, fontWeight:800, color:"#FBBF24", background:"rgba(251,191,36,0.12)", border:"1px solid rgba(251,191,36,0.35)", borderRadius:6, padding:"2px 9px" }}>⚠️ Not quoted yet</span>;
                    })()}
                  </div>
                  <div style={{ fontSize:12, color:"var(--bz-ink-2)" }}>{cfg.body}</div>
                  {q.quotedBy && <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:3 }}>Quoted by <strong style={{color:"var(--bz-ink-2)"}}>{q.quotedBy.split(" ")[0]}</strong></div>}
                  {/* Lot photos */}
                  {(q.photoUrls||[]).length > 0 && (
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:10 }}>
                      {(q.photoUrls||[]).map((url,i)=>(
                        <div key={i} role="button" tabIndex={0}
                          onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setLightbox(url); }}
                          onKeyDown={(e)=>{ if(e.key==="Enter"||e.key===" "){ e.preventDefault(); setLightbox(url); } }}
                          style={{ width:96, height:96, borderRadius:8, border:"1px solid rgba(123,156,255,0.3)", cursor:"zoom-in", overflow:"hidden", position:"relative", flexShrink:0 }}>
                          <img src={url} alt={`Photo ${i+1}`} style={{ width:"100%", height:"100%", objectFit:"cover", pointerEvents:"none", display:"block" }} onError={e=>{ e.target.parentElement.style.display="none"; }}/>
                          <div style={{ position:"absolute", bottom:0, right:0, background:"rgba(0,0,0,0.6)", color:"#fff", fontSize:10, padding:"1px 5px", borderTopLeftRadius:6 }}>🔍</div>
                        </div>
                      ))}
                      <div style={{ fontSize:11, color:"#7B9CFF", alignSelf:"center" }}>📸 {(q.photoUrls||[]).length} photo{(q.photoUrls||[]).length!==1?"s":""} · click to enlarge</div>
                    </div>
                  )}
                  {q.submittedBySeller && !(q.photoUrls||[]).length && (
                    <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:6, fontStyle:"italic" }}>No photos submitted</div>
                  )}
                  {q.status==="accepted" && q.sellerPayment && (
                    <div style={{ fontSize:12, color:"#4ade80", marginTop:4 }}>{"\uD83D\uDCB3 Wants payment via "}<strong>{q.sellerPayment}</strong>{q.sellerHandle ? ` — ${q.sellerHandle}` : ""}</div>
                  )}
                </div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <a href={`/quote/${q.id}`} target="_blank" rel="noreferrer" style={{ background:"var(--bz-s2)", color:cfg.color, border:`1px solid ${cfg.border}`, borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:700, textDecoration:"none" }}>{"View Quote \u2197"}</a>
                <button onClick={()=>{ if(onDismissQuoteNotif) onDismissQuoteNotif(q.id); }} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"#999", borderRadius:8, padding:"7px 12px", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>{"\u2713 Dismiss"}</button>
              </div>
            </div>
          </div>
        );
      })}
          </div>
          )}
        </div>
      )}

      {/* -- UPCOMING STREAMS (reps only) -- */}
      {!canSeeFinancials && myBreaker && (() => {
        const today = new Date(); today.setHours(0,0,0,0);
        const in7 = new Date(today); in7.setDate(today.getDate()+7);
        const normName = n => String(n||"").toLowerCase().replace(/\s+/g,"");
        const upcoming = plannedStreams
          .filter(p => {
            const d = parseLocalDate(p.date);
            return d >= today && d <= in7 && normName(p.breaker) === normName(myBreaker);
          })
          .sort((a,b) => a.date.localeCompare(b.date));
        if (!upcoming.length) {
          const anyMineEver = plannedStreams.some(p => normName(p.breaker) === normName(myBreaker));
          return (
          <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:14, padding:"18px 20px" }}>
            <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)", marginBottom:4 }}>📅 Your Next 7 Days</div>
            <div style={{ fontSize:12, color:"var(--bz-ink-3)", padding:"16px 0", textAlign:"center" }}>
              {plannedStreams.length === 0
                ? "Loading your schedule…"
                : anyMineEver
                  ? "Nothing scheduled in the next 7 days"
                  : "No streams scheduled — check with your team"}
            </div>
          </div>
          );
        }
        return (
          <div style={{ background:"var(--bz-s1)", border:"1px solid #1a1a2e", borderRadius:14, padding:"18px 20px" }}>
            <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)", marginBottom:14 }}>📅 Your Next 7 Days · <span style={{ color:"var(--bz-ink-3)", fontWeight:400 }}>{upcoming.length} stream{upcoming.length!==1?"s":""} scheduled</span></div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {upcoming.map((p,i) => {
                const d = parseLocalDate(p.date);
                const isToday = d.toDateString() === new Date().toDateString();
                const isTomorrow = d.toDateString() === new Date(Date.now()+86400000).toDateString();
                const dayLabel = isToday ? "🔴 Today" : isTomorrow ? "🟡 Tomorrow" : d.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});
                const sessionIcon = {day:"☀️",night:"🌙",weekend:"📅",singles:"🃏",event:"🎉"}[p.sessionType]||"📺";
                const bc = BC[p.breaker]?.text || "#E8317A";
                return (
                  <div key={p.id||i} style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 16px", background:isToday?"rgba(232,49,122,0.06)":"var(--bz-s1)", border:`1px solid ${isToday?"rgba(232,49,122,0.3)":"var(--bz-s2)"}`, borderRadius:10 }}>
                    <div style={{ fontSize:22 }}>{sessionIcon}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:14, fontWeight:800, color:"var(--bz-ink)" }}>{p.streamName||p.breaker}</div>
                      <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2, display:"flex", flexWrap:"wrap", alignItems:"center", gap:8 }}>
                        {p.startTime && <span style={{ color:"var(--bz-ink-2)" }}>🕐 {p.startTime}{p.endTime?` – ${p.endTime}`:""}</span>}
                        <span style={{ color:"#34d399" }}>📺 {channelOf(p)}</span>
                        {p.sessionType && <span style={{ color:"var(--bz-ink-2)", textTransform:"capitalize" }}>{p.sessionType}</span>}
                        {(p.products||[]).filter(pr=>pr.type).length > 0 && (
                          <span style={{ color:"#7B9CFF", fontWeight:700 }}>
                            {p.products.filter(pr=>pr.type).map(pr=>`${pr.qty}× ${pr.type}`).join(" · ")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:13, fontWeight:800, color:isToday?"#E8317A":"#FBBF24" }}>{dayLabel}</div>
                      {p.notes && <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2, maxWidth:120, textAlign:"right" }}>{p.notes}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Rep's month panel — own stats + team market multiple (reps only) */}
      {!canSeeFinancials && myBreaker && <RepWeekPanel streams={streams} matchedBreaker={myBreaker} />}
      {!canSeeFinancials && myBreaker && <RepMonthPanel streams={streams} matchedBreaker={myBreaker} />}

      {/* -- PAY STUB NOTIFICATIONS -- */}
      {myStubs.length > 0 && myStubs.map(stub => (
        <div key={stub.id} style={{ background:"linear-gradient(135deg,#0a1a0a,var(--bz-s1))", border:"2px solid #4ade80", borderRadius:14, padding:"18px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:16, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ fontSize:32 }}>{"\uD83D\uDCB5"}</div>
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:"#4ade80", marginBottom:4 }}>New Pay Stub from Bazooka!</div>
              <div style={{ fontSize:12, color:"var(--bz-ink-2)" }}>
                Period: <strong style={{color:"var(--bz-ink)"}}>{stub.period}</strong>
                &nbsp;·&nbsp; {stub.streamCount} stream{stub.streamCount!==1?"s":""}
                &nbsp;·&nbsp; Generated {new Date(stub.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:16, flexShrink:0 }}>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:28, fontWeight:900, color:"#4ade80" }}>{fmt(stub.totalComm)}</div>
              <div style={{ fontSize:10, color:"#999", textTransform:"uppercase", letterSpacing:1 }}>Commission Earned</div>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={()=>setViewStub(viewStub===stub.id?null:stub.id)} style={{ background:"#4ade80", color:"#000", border:"none", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                {viewStub===stub.id ? "\u25B2 Hide" : "\uD83D\uDC41 View Details"}
              </button>
              <button onClick={()=>{ if(onDismissPayStub) onDismissPayStub(stub.id); }} style={{ background:"transparent", border:"1px solid #555", color:"var(--bz-ink-2)", borderRadius:8, padding:"8px 12px", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>{"\u2713 Dismiss"}</button>
            </div>
          </div>
          {viewStub===stub.id && (
            <div style={{ width:"100%", borderTop:"1px solid var(--bz-line-2)", paddingTop:14, marginTop:4 }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>
                  {["Date","Type","Gross","Net","Rate","Commission"].map(h=><th key={h} style={S.th}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {(stub.streams||[]).map((s,i)=>(
                    <tr key={i} style={{ background:i%2===0?"var(--bz-s1)":"var(--bz-s1)" }}>
                      <td style={S.td}>{new Date(s.date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</td>
                      <td style={{ ...S.td, color:"var(--bz-ink-2)" }}>{s.breakType}{s.binOnly?" BIN":""}{s.sessionType?<span style={{marginLeft:5,fontSize:10,color:"#7B9CFF"}}>{({day:"☀️",night:"🌙",weekend:"📅",singles:"🃏",event:"🎉"})[s.sessionType]||""}</span>:""}</td>
                      <td style={{ ...S.td, color:"#E8317A", fontWeight:700 }}>{s.isEventOnly||s.gross===0?"—":fmt(s.gross)}</td>
                      <td style={{ ...S.td, color:"var(--bz-ink-2)" }}>{s.isEventOnly||!s.netRev?"—":fmt(s.netRev)}</td>
                      <td style={{ ...S.td, color:"var(--bz-ink-2)" }}>{s.rate===-1?"🎪 Event":s.rate!=null?(s.rate*100).toFixed(0)+"%":"—"}</td>
                      <td style={{ ...S.td, color:"#4ade80", fontWeight:900 }}>{fmt(s.commAmt)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background:"#0a1a0a", borderTop:"2px solid #4ade8033" }}>
                    <td colSpan={5} style={{ ...S.td, fontWeight:800, color:"var(--bz-ink)" }}>Total ({stub.streamCount} streams)</td>
                    <td style={{ ...S.td, color:"#4ade80", fontWeight:900, fontSize:15 }}>{fmt(stub.totalComm)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      ))}

      {/* -- FINANCIAL OVERVIEW (Admin only) -- */}
      {canSeeFinancials && (() => {
        // Period filter
        const now   = new Date();
        function inPeriod(dateStr) {
          if (!dateStr) return false;
          const d = parseLocalDate(dateStr);
          if (financialPeriod === "custom") {
            const s = customStart ? parseLocalDate(customStart) : new Date(0);
            s.setHours(0,0,0,0);
            const e = customEnd ? parseLocalDate(customEnd) : new Date();
            e.setHours(23,59,59,999);
            return d >= s && d <= e;
          }
          if (financialPeriod === "week") {
            const start = new Date(now);
            const day = now.getDay(); // 0=Sun,1=Mon,...6=Sat
            const daysFromMonday = day === 0 ? 6 : day - 1; // Mon=0 offset
            start.setDate(now.getDate() - daysFromMonday);
            start.setHours(0,0,0,0);
            const end = new Date(start); end.setDate(start.getDate()+6); end.setHours(23,59,59,999);
            return d >= start && d <= end;
          }
          if (financialPeriod === "month")   return d.getMonth()===now.getMonth() && d.getFullYear()===now.getFullYear();
          if (financialPeriod === "quarter") {
            const q = Math.floor(now.getMonth()/3);
            return Math.floor(d.getMonth()/3)===q && d.getFullYear()===now.getFullYear();
          }
          if (financialPeriod === "year")    return d.getFullYear()===now.getFullYear();
          return true;
        }

        const filtered = streams.filter(s => inPeriod(s.date));
        const streamTotals = filtered.reduce((acc,s) => {
          const c = calcStream(s);
          if (c.isSingles) { acc.singlesGross += c.gross; acc.singlesComm += c.myComm; return acc; }
          const exp = (parseFloat(s.whatnotPromo)||0)+(parseFloat(s.magpros)||0)+(parseFloat(s.packagingMaterial)||0)+(parseFloat(s.topLoaders)||0)+(parseFloat(s.chaserCards)||0);
          const splitPct = s.splitRep ? parseFloat(s.splitPct||50)/100 : 1;
          const primaryComm = s.splitRep ? c.commAmt*splitPct : c.commAmt;
          const splitRepComm = s.splitRep ? c.commAmt*(1-splitPct) : 0;
          const eventStaffComm = (s.eventStaff||[]).reduce((sum,_)=>sum+Math.min(1000,c.bazNet*0.15),0);
          // Commission ALWAYS counts (reps get paid). Excluded streams contribute $0 to the financial figures
          // (revenue was already counted from the imported CSV) but their commission still pays out.
          acc.comm     += (primaryComm - (c.repExpShare||0)) + splitRepComm + eventStaffComm + (c.salesBonus||0) + (c.tips||0);
          if (c.excludeFinancials) return acc;
          acc.gross    += c.gross;
          acc.imc      += c.imcNet - (c.imcDirectReimb||0);
          acc.baz      += c.bazNet;
          acc.trueNet  += c.bazTrueNet||0;
          acc.expenses += exp;
          acc.imcDirectReimb += c.imcDirectReimb||0;
          // Exact True Net formula terms (line-for-line with the bazTrueNet equation),
          // so the money-flow breakdown reconciles precisely to True Net.
          acc.bazOwnShare  += (c.bazOwnShare||0);
          acc.tnCommAmt    += (c.commAmt||0);
          acc.tnEventStaff += (c.eventStaffAmt||0);
          acc.tnSalesBonus += (c.salesBonus||0);
          acc.tnRepExpShare+= (c.repExpShare||0);
          acc.tnBazExpShare+= (c.bazExpShare||0);
          acc.tnImcReimb   += (c.imcReimb||0) + (c.imcDirectReimb||0);
          acc.tnRemoteCosts+= (c.biguCardCosts||0) + (c.biguShippingHalf||0);
          return acc;
        }, { gross:0, imc:0, comm:0, baz:0, trueNet:0, expenses:0, imcDirectReimb:0, singlesGross:0, singlesComm:0, bazOwnShare:0, tnCommAmt:0, tnEventStaff:0, tnSalesBonus:0, tnRepExpShare:0, tnBazExpShare:0, tnImcReimb:0, tnRemoteCosts:0 });

        // Months that already have real logged streams — don't also count historical
        // summaries for those months, or true net / gross would double up.
        const monthsWithStreams = new Set(
          streams.filter(s=>s.date && !isSinglesStream(s) && !s.excludeFinancials)
                 .map(s=>(s.date||"").slice(0,7))
        );
        // Merge historical monthly summaries into totals
        const histFiltered = historicalData.filter(h => {
          if (!h.yearMonth) return false;
          if (monthsWithStreams.has(h.yearMonth)) return false; // real streams take precedence
          const [y,m] = h.yearMonth.split("-").map(Number);
          const d = new Date(y, m-1, 15);
          return inPeriod(d.toISOString().split("T")[0]);
        });
        const histTotals = histFiltered.reduce((acc,h) => {
          const gross   = parseFloat(h.grossRevenue)||0;
          const net     = parseFloat(h.netRevenue)||0;
          const reimb   = parseFloat(h.imcReimb)||0;
          acc.gross    += gross;
          acc.imc      += net * 0.70;
          acc.baz      += net * 0.30;
          acc.trueNet  += net * 0.30 + reimb; // reimbursement adds to true net only
          return acc;
        }, { gross:0, imc:0, comm:0, baz:0, trueNet:0 });

        // Include historical monthly summaries (real revenue from before streams
        // were logged in-tool) for months that have no stream data. This keeps the
        // Financial Overview YTD consistent with the Year-End Projections YTD.
        const totals = {
          gross:         streamTotals.gross          + histTotals.gross,
          imc:           streamTotals.imc            + histTotals.imc,
          comm:          streamTotals.comm,
          baz:           streamTotals.baz            + histTotals.baz,
          trueNet:       streamTotals.trueNet        + histTotals.trueNet,
          expenses:      streamTotals.expenses || 0,
          imcDirectReimb: streamTotals.imcDirectReimb || 0,
          singlesGross:  streamTotals.singlesGross || 0,
          singlesComm:   streamTotals.singlesComm || 0,
          histGross:     histTotals.gross, // for drill-down note
        };

        const PERIOD_LABELS = { month:"This Month", quarter:"This Quarter", year:"This Year", all:"All Time", custom:"Custom Range" };

        // ── Hero visual data — derived from the SAME filtered streams + totals the
        //    rest of the dashboard uses, so the new visuals always match the numbers.
        const heroBreakerColors = { Krystal:"#E8317A", Dre:"#7c9cff", BigU:"#34d399", Vinny:"#fbbf24", Stephen:"#a78bfa", Alison:"#ff9f7c" };
        const financialStreams = filtered.filter(s => !isSinglesStream(s) && !s.excludeFinancials);
        // Per-breaker leaderboard (gross / net / avg market multiple)
        const heroBoardMap = {};
        financialStreams.forEach(s => {
          const c = calcStream(s);
          const b = s.breaker || "—";
          if (!heroBoardMap[b]) heroBoardMap[b] = { name: b, gross: 0, net: 0, mmSum: 0, mmN: 0, color: heroBreakerColors[b] || "#9a8a94" };
          heroBoardMap[b].gross += c.gross;
          heroBoardMap[b].net += c.bazTrueNet || 0;
          const mm = parseFloat(s.marketMultiple) || 0;
          if (mm > 0) { heroBoardMap[b].mmSum += mm; heroBoardMap[b].mmN++; }
        });
        const heroBoard = Object.values(heroBoardMap)
          .map(b => ({ ...b, mm: b.mmN ? b.mmSum / b.mmN : 0 }))
          .sort((a, b) => b.gross - a.gross).slice(0, 6);
        // Commission tier mix (by market multiple bracket)
        const tierDefs = [
          { label:"55%", color:"#E8317A", test: mm => mm >= 1.8 },
          { label:"50%", color:"#a78bfa", test: mm => mm >= 1.7 && mm < 1.8 },
          { label:"45%", color:"#7c9cff", test: mm => mm >= 1.6 && mm < 1.7 },
          { label:"40%", color:"#fbbf24", test: mm => mm >= 1.5 && mm < 1.6 },
          { label:"35%", color:"#7a6b74", test: mm => mm > 0 && mm < 1.5 },
        ];
        const heroTiers = tierDefs.map(t => ({ label: t.label, color: t.color, count: financialStreams.filter(s => t.test(parseFloat(s.marketMultiple) || 0)).length })).filter(t => t.count > 0);
        // Revenue vs net series — bucket the period into slices (weeks for month, months otherwise)
        // ── Real per-bucket time series for EVERY metric. Buckets = weeks (month view)
        //    or months (quarter/year). Each metric gets its true value per bucket, so the
        //    sparklines show real trends — not approximations.
        const heroBreaksCount = breaks.filter(b => { const d = b.date; return d && inPeriod(d); }).length;
        const bucketKeyOf = (dateStr) => {
          if (financialPeriod === "month") { const day = parseInt((dateStr || "").slice(8, 10)) || 1; return "Wk" + Math.min(4, Math.ceil(day / 7)); }
          return (dateStr || "").slice(0, 7);
        };
        const commissionOf = (s) => {
          const c = calcStream(s);
          const sp = s.splitRep ? parseFloat(s.splitPct || 50) / 100 : 1;
          const primary = s.splitRep ? c.commAmt * sp : c.commAmt;
          const splitRepC = s.splitRep ? c.commAmt * (1 - sp) : 0;
          const evt = (s.eventStaff || []).reduce((sum, _) => sum + Math.min(1000, c.bazNet * 0.15), 0);
          return (primary - (c.repExpShare || 0)) + splitRepC + evt + (c.salesBonus || 0) + (c.tips || 0);
        };
        const heroBuckets = (() => {
          const map = {};
          const ensure = (k) => (map[k] || (map[k] = { gross: 0, net: 0, comm: 0, breaks: 0, mmSum: 0, mmN: 0 }));
          financialStreams.forEach(s => {
            const c = calcStream(s); const k = bucketKeyOf(s.date); const b = ensure(k);
            b.gross += c.gross; b.net += c.bazTrueNet || 0; b.comm += commissionOf(s);
            const mm = parseFloat(s.marketMultiple) || 0; if (mm > 0) { b.mmSum += mm; b.mmN++; }
          });
          breaks.forEach(bk => { if (bk.date && inPeriod(bk.date)) ensure(bucketKeyOf(bk.date)).breaks++; });
          const keys = financialPeriod === "month" ? ["Wk1","Wk2","Wk3","Wk4"] : Object.keys(map).sort();
          const labels = financialPeriod === "month" ? keys : keys.map(k => { const [y,m] = k.split("-"); return new Date(y, m-1, 1).toLocaleDateString("en-US", { month: "short" }); });
          const val = (k, f) => { const b = map[k]; return b ? f(b) : 0; };
          // Running total so the line CLIMBS through the period and never dips.
          const cumulative = (perBucket) => { let run = 0; return perBucket.map(v => (run += v)); };
          const grossPer  = keys.map(k => val(k, b => b.gross));
          const netPer    = keys.map(k => val(k, b => b.net));
          const commPer   = keys.map(k => val(k, b => b.comm));
          const breaksPer = keys.map(k => val(k, b => b.breaks));
          // Market multiple is a RATE, not a dollar amount — it doesn't accumulate.
          // Show a running average (cumulative mm-sum / cumulative mm-count) so it's smooth.
          let mmSumRun = 0, mmNRun = 0;
          const mmRunAvg = keys.map(k => { const b = map[k]; if (b) { mmSumRun += b.mmSum; mmNRun += b.mmN; } return mmNRun ? +(mmSumRun / mmNRun).toFixed(2) : 0; });
          return {
            labels,
            gross:  cumulative(grossPer).map(Math.round),
            net:    cumulative(netPer).map(Math.round),
            comm:   cumulative(commPer).map(Math.round),
            breaks: cumulative(breaksPer).map(Math.round),
            mm:     mmRunAvg,
          };
        })();
        const heroSeries = { labels: heroBuckets.labels, gross: heroBuckets.gross, net: heroBuckets.net };
        // All hero series are cumulative running totals — they climb through the period.
        const heroSpark = heroBuckets.net.length >= 2 ? heroBuckets.net : [0, Math.round(totals.trueNet || 0)];
        const heroMiniRev    = heroBuckets.gross.length  >= 2 ? heroBuckets.gross  : [0, Math.round(totals.gross || 0)];
        const heroMiniComm   = heroBuckets.comm.length   >= 2 ? heroBuckets.comm   : [0, Math.round(totals.comm || 0)];
        const heroMiniBreaks = heroBuckets.breaks.length >= 2 ? heroBuckets.breaks : [0, heroBreaksCount];
        const heroMMList = financialStreams.map(s => parseFloat(s.marketMultiple) || 0).filter(m => m > 0);
        const heroAvgMM = heroMMList.length ? heroMMList.reduce((a, b) => a + b, 0) / heroMMList.length : 0;
        const heroMiniMM = heroBuckets.mm.filter(v => v > 0).length >= 2 ? heroBuckets.mm : [heroAvgMM, heroAvgMM];

        // Drill-down modal content
        const renderDrillDown = () => {
          if (!drillDown) return null;
          const config = {
            gross:      { label:"Gross Revenue",       color:"#E8317A", val: s => calcStream(s).gross },
            expenses:   { label:"Stream Expenses",     color:"#991b1b", val: s => (parseFloat(s.whatnotPromo)||0)+(parseFloat(s.magpros)||0)+(parseFloat(s.packagingMaterial)||0)+(parseFloat(s.topLoaders)||0)+(parseFloat(s.chaserCards)||0) },
            imc:        { label:"Owed to Imagination Mining (70%)", color:"#E8317A", val: s => calcStream(s).imcNet },
            commission: { label:"Commission Owed",     color:"#E8317A", val: s => { const c=calcStream(s); const sp=s.splitRep?parseFloat(s.splitPct||50)/100:1; const primary=s.splitRep?c.commAmt*sp:c.commAmt; const splitRepC=s.splitRep?c.commAmt*(1-sp):0; const evt=(s.eventStaff||[]).reduce((sum,_)=>sum+Math.min(1000,c.bazNet*0.15),0); return (primary-(c.repExpShare||0))+splitRepC+evt+(c.salesBonus||0)+(c.tips||0); } },
            bazooka:    { label:"Bazooka Earnings (30%)", color:"#E8317A", val: s => calcStream(s).bazNet },
            trueNet:    { label:"Bazooka True Net",      color:"#E8317A", val: s => calcStream(s).bazTrueNet||0 },
          }[drillDown];
          // Financial drill-downs exclude "Exclude from Financials" AND singles streams (singles have their own area).
          // Commission drill-down still shows excluded streams (reps get paid) but not singles (those pay in the singles area).
          const drillRows = drillDown==="commission"
            ? filtered.filter(s=>!isSinglesStream(s))
            : filtered.filter(s=>!s.excludeFinancials && !isSinglesStream(s));
          return (
            <div style={{ ...S.card, border:`1px solid var(--bz-line-2)`, marginTop:0, padding:0, overflow:"hidden" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", borderBottom:"1px solid var(--bz-line)" }}>
                <SectionLabel t={config.label} />
                <button onClick={()=>setDrillDown(null)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", cursor:"pointer", fontSize:14, borderRadius:6, padding:"4px 10px", fontFamily:"inherit" }}>✕ Close</button>
              </div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                  <thead>
                    <tr style={{ borderBottom:"1px solid var(--bz-line-2)", background:"var(--bz-bg)" }}>
                      {["Date","Breaker","Gross","Rate",
                        ...(drillDown==="trueNet" ? ["Baz Net","− Payouts","💙 IMC Reimb","= True Net"] : [
                          drillDown==="commission"?"Commission":drillDown==="imc"?"IMC (70%)":drillDown==="bazooka"?"Bazooka 30%":"Gross"
                        ])
                      ].map(h=><th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:10, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, whiteSpace:"nowrap" }}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {drillRows.length===0
                      ? <EmptyRow msg={streams.length===0 ? "No streams logged yet." : "No streams in this period."} cols={drillDown==="trueNet"?8:5}/>
                      : drillRows.map((s,i) => {
                          const c   = calcStream(s);
                          const bc  = BC[s.breaker]||{bg:"#F3F4F6",text:"var(--bz-ink-3)"};
                          const val = config.val(s);
                          const hasReimb = (c.imcDirectReimb||0) > 0;
                          return (
                            <tr key={s.id} style={{ borderBottom:"1px solid var(--bz-line)", background:i%2===0?"var(--bz-s1)":"var(--bz-s1)" }}>
                              <td style={{ padding:"10px 14px", color:"var(--bz-ink-2)", whiteSpace:"nowrap" }}>{new Date(s.date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</td>
                              <td style={{ padding:"10px 14px" }}><Badge bg={bc.bg} color={bc.text}>{s.breaker}</Badge></td>
                              <td style={{ padding:"10px 14px", color:"#E8317A", fontWeight:700 }}>{fmt(c.gross)}</td>
                              <td style={{ padding:"10px 14px", color:"var(--bz-ink-2)" }}>{(c.rate*100).toFixed(0)}%{s.isEvent?" Event":s.binOnly?" BIN":""}{s.externalChannel&&<span style={{ marginLeft:6, fontSize:10, color:"#7B9CFF", background:"rgba(123,156,255,0.1)", border:"1px solid rgba(123,156,255,0.2)", borderRadius:4, padding:"1px 5px" }}>🌐 Ext</span>}</td>
                              {drillDown==="trueNet" ? <>
                                <td style={{ padding:"10px 14px", color:"#E8317A", fontWeight:700 }}>{fmt(c.bazNet)}</td>
                                <td style={{ padding:"10px 14px", color:"#ef4444" }}>−{fmt(c.commAmt + (c.eventStaffAmt||0) + (c.salesBonus||0))}</td>
                                <td style={{ padding:"10px 14px" }}>
                                  {hasReimb ? (
                                    <div>
                                      <span style={{ color:"#60A5FA", fontWeight:700 }}>+{fmt(c.imcDirectReimb)}</span>
                                      {s.imcReimbNote && <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2, maxWidth:140 }}>{s.imcReimbNote}</div>}
                                    </div>
                                  ) : <span style={{ color:"var(--bz-ink-3)" }}>—</span>}
                                </td>
                                <td style={{ padding:"10px 14px", color:"#A78BFA", fontWeight:900 }}>{fmt(c.bazTrueNet)}</td>
                              </> : <td style={{ padding:"10px 14px", color:config.color, fontWeight:900 }}>{fmt(val)}</td>}
                            </tr>
                          );
                        })
                    }
                  </tbody>
                  <tfoot>
                    <tr style={{ background:"var(--bz-bg)", borderTop:"2px solid var(--bz-line-2)" }}>
                      <td colSpan={4} style={{ padding:"12px 14px", fontWeight:800, color:"var(--bz-ink)", fontSize:12 }}>Total ({drillRows.length} stream{drillRows.length!==1?"s":""})</td>
                      {drillDown==="trueNet" ? <>
                        <td style={{ padding:"12px 14px", fontWeight:900, color:"#E8317A", fontSize:14 }}>{fmt(drillRows.reduce((a,s)=>a+calcStream(s).bazNet,0))}</td>
                        <td style={{ padding:"12px 14px", fontWeight:900, color:"#ef4444", fontSize:14 }}>−{fmt(drillRows.reduce((a,s)=>{const c=calcStream(s);return a+c.commAmt+(c.eventStaffAmt||0)+(c.salesBonus||0);},0))}</td>
                        <td style={{ padding:"12px 14px", fontWeight:900, color:"#60A5FA", fontSize:14 }}>{drillRows.some(s=>calcStream(s).imcDirectReimb>0) ? "+"+fmt(drillRows.reduce((a,s)=>a+(calcStream(s).imcDirectReimb||0),0)) : "—"}</td>
                        <td style={{ padding:"12px 14px", fontWeight:900, color:"#A78BFA", fontSize:16 }}>{fmt(drillRows.reduce((a,s)=>a+(calcStream(s).bazTrueNet||0),0) - Object.entries(imcAdjustments).reduce((s,[mk,v])=>{ const [y,m]=mk.split("-").map(Number); return inPeriod(new Date(y,m-1,15).toISOString().split("T")[0]) ? s+(parseFloat(v)||0) : s; },0))}</td>
                      </> : <td style={{ padding:"12px 14px", fontWeight:900, color:config.color, fontSize:16 }}>{fmt(drillRows.reduce((a,s)=>a+config.val(s),0))}</td>}
                    </tr>
                  </tfoot>
                </table>
                {(() => {
                  // Historical monthly data (months with no streams) is included in the
                  // headline totals but not in this stream table — show it here so the
                  // numbers reconcile.
                  const histVal = {
                    gross:   histTotals.gross,
                    imc:     histTotals.imc,
                    bazooka: histTotals.baz,
                    trueNet: histTotals.trueNet,
                  }[drillDown];
                  if (!histVal) return null;
                  const streamSum = drillRows.reduce((a,s)=>a+config.val(s),0);
                  return (
                    <div style={{ padding:"10px 14px", fontSize:12, background:"rgba(123,156,255,0.06)", borderTop:"1px solid var(--bz-line)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ color:"#7B9CFF" }}>📁 + Historical (pre-tool months, imported summaries)</span>
                      <span style={{ color:"#7B9CFF", fontWeight:800 }}>{fmt(histVal)}</span>
                    </div>
                  );
                })()}
                {drillDown==="trueNet" && (() => {
                  const adj = Object.entries(imcAdjustments).reduce((s,[mk,v])=>{ const [y,m]=mk.split("-").map(Number); return inPeriod(new Date(y,m-1,15).toISOString().split("T")[0]) ? s+(parseFloat(v)||0) : s; },0);
                  if (!adj) return null;
                  return <div style={{ padding:"8px 14px", fontSize:11, color:"#A78BFA", background:"rgba(167,139,250,0.06)" }}>✏️ Includes a manual IMC adjustment of {adj>=0?"−":"+"}{fmt(Math.abs(adj))} for this period (applied to the True Net total above).</div>;
                })()}
              </div>
            </div>
          );
        };

        // Money-flow breakdown — the True Net settlement, line by line, reconciling
        // EXACTLY to True Net (built from the same terms as the bazTrueNet formula).
        // Positive = money in (green), negative = money out (red).
        const heroFlow = (() => {
          const t = totals;
          const rows = [
            { label: "Bazooka 30% share", val: t.bazOwnShare, kind: "start" },
            { label: "Rep commission", val: -t.tnCommAmt, kind: "out" },
            { label: "Event staff fees", val: -t.tnEventStaff, kind: "out" },
            { label: "Sales bonuses", val: -t.tnSalesBonus, kind: "out" },
            { label: "Bazooka expense share", val: -t.tnBazExpShare, kind: "out" },
            { label: "Remote-breaker costs", val: -t.tnRemoteCosts, kind: "out" },
            { label: "Rep reimburses expenses", val: t.tnRepExpShare, kind: "in" },
            { label: "IMC reimbursements", val: t.tnImcReimb, kind: "in" },
          ];
          // Drop zero rows so the list stays clean; keep the start + end always.
          return rows.filter(r => r.kind === "start" || Math.abs(r.val) >= 1);
        })();

        return (
          <>
          <DashboardHero
            net={totals.trueNet} netDelta={null}
            rev={totals.gross} comm={totals.comm} mm={heroAvgMM} breaksCount={heroBreaksCount}
            heroSpark={heroSpark} miniRev={heroMiniRev} miniComm={heroMiniComm} miniMM={heroMiniMM} miniBreaks={heroMiniBreaks}
            revLabels={heroSeries.labels} revGross={heroSeries.gross} revNet={heroSeries.net}
            tiers={heroTiers.length ? heroTiers : [{label:"—",color:"#7a6b74",count:1}]} board={heroBoard} fmt={fmt}
            flow={heroFlow} flowNet={totals.trueNet}
          />
          <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:"var(--bz-radius)", padding:"22px", boxShadow:"var(--bz-shadow)" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:10 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700, letterSpacing:"-0.01em", color:"var(--bz-ink)" }}>Financial Overview</div>
                <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginTop:3 }}>{PERIOD_LABELS[financialPeriod]} · {filtered.length} stream{filtered.length!==1?"s":""}</div>
                <button
                  onClick={async ()=>{ setExportStatus("Exporting…"); try { await exportInternalDataJson(setExportStatus); } catch(e){ setExportStatus("Export failed — try again"); } setTimeout(()=>setExportStatus(""), 5000); }}
                  title="Download a full JSON backup of all internal business data"
                  style={{ marginTop:8, background:"transparent", border:"1px solid var(--bz-line)", color:"var(--bz-ink-2)", borderRadius:8, padding:"6px 12px", fontSize:11.5, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  {exportStatus || "⬇ Export ALL Internal Data → Base44"}
                </button>
                <button
                  onClick={async ()=>{ setExportStatus2("Exporting…"); try { await exportInternalDataJson(setExportStatus2, ["streams","historical_data"]); } catch(e){ setExportStatus2("Export failed — try again"); } setTimeout(()=>setExportStatus2(""), 5000); }}
                  title="Download just streams + historical monthly summaries — the financial data for Base44"
                  style={{ marginTop:8, marginLeft:8, background:"linear-gradient(135deg,var(--bz-pink-hot),var(--bz-pink))", border:"none", color:"#0b0709", borderRadius:8, padding:"6px 12px", fontSize:11.5, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
                  {exportStatus2 || "⬇ Download Financial Data (streams + history)"}
                </button>
                <button
                  onClick={async ()=>{ setExportStatus3("Exporting…"); try { await exportInternalDataJson(setExportStatus3, ["buyers"]); } catch(e){ setExportStatus3("Export failed — try again"); } setTimeout(()=>setExportStatus3(""), 5000); }}
                  title="Download the buyers table — CRM + FIRSTTIME25 campaign data — for Base44"
                  style={{ marginTop:8, marginLeft:8, background:"linear-gradient(135deg,#7c9cff,#5578e0)", border:"none", color:"#0b0709", borderRadius:8, padding:"6px 12px", fontSize:11.5, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
                  {exportStatus3 || "⬇ Download Buyer + Campaign Data"}
                </button>
              </div>
              <div style={{ display:"inline-flex", gap:2, background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:10, padding:3 }}>
                {[["month","Month"],["quarter","Quarter"],["year","Year"],["all","All"],["custom","Custom"]].map(([val,label]) => (
                  <button key={val} onClick={()=>setFinancialPeriod(val)} style={{ background:financialPeriod===val?"var(--bz-pink)":"transparent", color:financialPeriod===val?"#fff":"var(--bz-ink-2)", border:"none", borderRadius:7, padding:"7px 14px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", transition:"all 0.15s", boxShadow:financialPeriod===val?"0 2px 8px rgba(232,49,122,0.35)":"none" }}>{label}</button>
                ))}
              </div>
            </div>

            {financialPeriod === "custom" && (
              <div style={{ display:"flex", gap:10, marginBottom:14, alignItems:"center" }}>
                <div><label style={S.lbl}>From</label><input type="date" value={customStart} onChange={e=>setCustomStart(e.target.value)} style={{ ...S.inp, width:"auto" }}/></div>
                <div><label style={S.lbl}>To</label><input type="date" value={customEnd} onChange={e=>setCustomEnd(e.target.value)} style={{ ...S.inp, width:"auto" }}/></div>
                <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginTop:14 }}>{filtered.length} stream{filtered.length!==1?"s":""} in range</div>
              </div>
            )}

            <div className="dash-grid-5" style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:12 }}>
              {[
                { key:"gross",      label:"Gross Revenue",      val:totals.gross,    color:"var(--bz-pink)", icon:"📈", sub:"breaks only · excl. singles" },
                { key:"expenses",   label:"Stream Expenses",    val:totals.expenses, color:"var(--bz-ink-2)",    icon:"📦", sub:"before IMC split" },
                { key:"imc",        label:"Owed to IMC",        val:totals.imc + Object.entries(imcAdjustments).reduce((s,[mk,v])=>{ const [y,m]=mk.split("-").map(Number); return inPeriod(new Date(y,m-1,15).toISOString().split("T")[0]) ? s+(parseFloat(v)||0) : s; },0), color:"var(--bz-blue)", icon:"💙", sub:"70% of split base" },
                { key:"bazooka",    label:"Bazooka 30%",        val:totals.baz,      color:"var(--bz-pink)", icon:"🏦", sub:"before commission" },
                { key:"trueNet",    label:"True Net",           val:totals.trueNet - Object.entries(imcAdjustments).reduce((s,[mk,v])=>{ const [y,m]=mk.split("-").map(Number); return inPeriod(new Date(y,m-1,15).toISOString().split("T")[0]) ? s+(parseFloat(v)||0) : s; },0), color:"var(--bz-green)", icon:"✨", sub:"after all deductions" },
                { key:"commission", label:"Commission Owed",    val:totals.comm,     color:"var(--bz-green)", icon:"💰", sub:"net rep payout" },
                ...(totals.singlesGross>0 ? [{ key:"singles", label:"Singles Revenue", val:totals.singlesGross, color:"var(--bz-amber)", icon:"🃏", sub:`$${totals.singlesComm.toFixed(0)} to breaker · 100%` }] : []),
              ].map(({key,label,val,color,icon,sub}) => {
                const isActive = drillDown === key;
                return (
                  <div
                    key={key}
                    onClick={()=>setDrillDown(isActive?null:key)}
                    className="stat-card bz-fin-card"
                    style={{
                      background: isActive ? "var(--bz-s2)" : "var(--bz-s1)",
                      border: `1px solid ${isActive ? "var(--bz-line-2)" : "var(--bz-line)"}`,
                      borderRadius: "var(--bz-radius-sm)",
                      padding: "16px 16px 12px",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      transition: "border-color 0.18s, transform 0.18s, box-shadow 0.18s",
                      boxShadow: isActive ? "var(--bz-shadow)" : "none",
                    }}
                  >
                    {isActive && <div style={{ position:"absolute", top:0, left:0, width:"100%", height:2, background:color }}/>}
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:11 }}>
                      <div style={{ fontSize:10.5, fontWeight:600, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:"0.7px" }}>{label}</div>
                      <div style={{ width:26, height:26, borderRadius:8, display:"grid", placeItems:"center", fontSize:13, background:`color-mix(in srgb, ${color} 14%, transparent)` }}>{icon}</div>
                    </div>
                    <div style={{ fontSize:26, fontWeight:800, color, lineHeight:1, marginBottom:7, letterSpacing:"-0.02em", fontFamily:"'Arial Narrow',sans-serif" }}>
                      {fmt(val)}
                    </div>
                    <div style={{ fontSize:10.5, color:"var(--bz-ink-3)", marginBottom:10 }}>{sub}</div>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:4, background: isActive ? `color-mix(in srgb, ${color} 16%, transparent)` : "var(--bz-s3)", borderRadius:7, padding:"5px 0", transition:"background 0.15s" }}>
                      <span style={{ fontSize:10, fontWeight:700, color: isActive ? color : "var(--bz-ink-3)" }}>
                        {isActive ? "▲ Hide breakdown" : "▼ See breakdown"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* IMC Manual Adjustment — per month */}
            {canSeeFinancials && (
              <div style={{marginTop:10}}>
                {!showImcAdj ? (
                  <button onClick={()=>setShowImcAdj(true)} style={{background:"none",border:"1px dashed rgba(255,255,255,0.08)",color:"var(--bz-ink-3)",borderRadius:7,padding:"4px 12px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>
                    ✏️ Adjust IMC by month {Object.keys(imcAdjustments).filter(k=>parseFloat(imcAdjustments[k])).length>0?`(${Object.keys(imcAdjustments).filter(k=>parseFloat(imcAdjustments[k])).length} active)`:""}
                  </button>
                ) : (
                  <div style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:10,padding:"14px 16px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                      <span style={{fontSize:12,fontWeight:700,color:"var(--bz-ink)"}}>IMC Adjustment by Month</span>
                      <button onClick={()=>setShowImcAdj(false)} style={{background:"none",border:"none",color:"var(--bz-ink-3)",cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>✕ Close</button>
                    </div>
                    <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:10}}>Enter the difference between the invoice amount and calculated amount. Use negative to reduce.</div>
                    {(() => {
                      // Build list of months that have streams
                      const monthsWithStreams = [...new Set(streams.filter(s=>s.date).map(s=>s.date.slice(0,7)))].sort().reverse().slice(0,12);
                      if (monthsWithStreams.length === 0) return <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>No streams logged yet.</div>;
                      const totalAdj = Object.values(imcAdjustments).reduce((s,v)=>s+(parseFloat(v)||0),0);
                      return (
                        <>
                          <div style={{display:"flex",flexDirection:"column",gap:8}}>
                            {monthsWithStreams.map(mk=>{
                              const [y,m] = mk.split("-");
                              const monthLabel = `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(m)-1]} ${y}`;
                              const adj = imcAdjustments[mk]||"";
                              const adjNum = parseFloat(adj)||0;
                              // Calc IMC for this month from streams
                              const monthStreams = streams.filter(s=>s.date&&s.date.startsWith(mk));
                              const calcImc = monthStreams.reduce((s,str)=>{ const c=calcStreamDash(str); return s + c.imcNet - (c.imcDirectReimb||0); },0);
                              return (
                                <div key={mk} style={{display:"grid",gridTemplateColumns:"100px 1fr 1fr auto",gap:8,alignItems:"center"}}>
                                  <span style={{fontSize:12,fontWeight:700,color:"var(--bz-ink)"}}>{monthLabel}</span>
                                  <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Calc: <strong style={{color:"var(--bz-ink-2)"}}>{fmt(calcImc)}</strong></span>
                                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                                    <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Adj:</span>
                                    <input type="number" step="0.01" value={adj}
                                      onChange={e=>updateImcAdj(mk, e.target.value)}
                                      placeholder="0.00"
                                      style={{background:"var(--bz-s1)",border:`1px solid ${adjNum?"rgba(251,191,36,0.4)":"var(--bz-line-2)"}`,borderRadius:6,color:adjNum?"#FBBF24":"var(--bz-ink)",padding:"4px 8px",fontSize:12,fontFamily:"inherit",outline:"none",width:100}}/>
                                  </div>
                                  <span style={{fontSize:11,fontWeight:700,color:adjNum?"#FBBF24":"#333"}}>
                                    {adjNum ? `→ ${fmt(calcImc+adjNum)}` : ""}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          {totalAdj !== 0 && (
                            <div style={{marginTop:10,padding:"8px 12px",background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.2)",borderRadius:7,fontSize:12,color:"#FBBF24",fontWeight:700}}>
                              Total adjustment (all months): {totalAdj>0?"+":""}{fmt(totalAdj)}
                            </div>
                          )}
                          <button onClick={()=>clearImcAdj()} style={{marginTop:8,background:"none",border:"none",color:"var(--bz-ink-3)",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>✕ Clear all adjustments</button>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
          {drillDown && <div className="drill-down">{renderDrillDown()}</div>}
          </>
        );
      })()}

      {/* Ops Summary */}
      {canSeeFinancials && (() => {
        function opsInPeriod(dateStr) {
          if (!dateStr) return false;
          const d = parseLocalDate(dateStr);
          const now = new Date();
          if (opsPeriod==="month")   return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
          if (opsPeriod==="quarter") { const q=Math.floor(now.getMonth()/3); return Math.floor(d.getMonth()/3)===q&&d.getFullYear()===now.getFullYear(); }
          if (opsPeriod==="year")    return d.getFullYear()===now.getFullYear();
          if (opsPeriod==="custom"&&opsFrom&&opsTo) { const f=new Date(opsFrom+"T00:00:00"),t=new Date(opsTo+"T23:59:59"); return d>=f&&d<=t; }
          return true;
        }

        const periodStreams = streams.filter(s => opsInPeriod(s.date) && !s.excludeFinancials);

        const totMagpros  = periodStreams.reduce((s,r)=>s+(parseFloat(r.magpros)||0),0);
        const totPack     = periodStreams.reduce((s,r)=>s+(parseFloat(r.packagingMaterial)||0),0);
        const totTopload  = periodStreams.reduce((s,r)=>s+(parseFloat(r.topLoaders)||0),0);
        const totChaser   = periodStreams.reduce((s,r)=>s+(parseFloat(r.chaserCards)||0),0);
        const totMagQty   = periodStreams.reduce((s,r)=>s+(parseInt(r.magprosQty)||0),0);
        const totPackQty  = periodStreams.reduce((s,r)=>s+(parseInt(r.packagingQty)||0),0);
        const totTopQty   = periodStreams.reduce((s,r)=>s+(parseInt(r.topLoadersQty)||0),0);
        const totZion     = periodStreams.reduce((s,r)=>s+(parseFloat(r.zionRevenue)||0),0);
        const totCoupons  = periodStreams.reduce((s,r)=>s+(parseFloat(r.coupons)||0),0);

        // Card usage costs by type
        const USAGE_TO_CT_OPS = { "Giveaway":"Giveaway Cards", "Insurance":"Insurance Cards", "First-Timer Pack":"First-Timer Cards", "Chaser Pull":"Chaser Cards", "Chaser":"Chaser Cards" };
        const cardCostByType = {};
        const cardQtyByType  = {};
        const cardRowsByType = {};
        CARD_TYPES.forEach(ct => { cardCostByType[ct]=0; cardQtyByType[ct]=0; cardRowsByType[ct]=[]; });
        breaks.forEach(b => {
          const ct = USAGE_TO_CT_OPS[b.usage] || b.cardType;
          if (!ct || !CARD_TYPES.includes(ct)) return;
          const breakDate = b.date || (b.dateAdded ? b.dateAdded.split("T")[0] : null);
          if (!opsInPeriod(breakDate)) return;
          const inv  = b.isPoolLog ? null : inventory.find(c => c.id === b.inventoryId);
          const cost = inv?.costPerCard || 0;
          const qty  = b.isPoolLog ? (parseInt(b.qty)||1) : 1;
          cardCostByType[ct] += cost * qty;
          cardQtyByType[ct]  += qty;
          cardRowsByType[ct].push({
            date: breakDate||"",
            cardName: inv?.cardName || b.cardName || "Unknown",
            usage: b.usage || ct,
            qty,
            costPerCard: cost,
            totalCost: cost*qty,
            streamId: b.streamId||"",
          });
        });

        function exportCardCSV(ct) {
          const rows = cardRowsByType[ct];
          if (!rows.length) return;
          const header = "Date,Card Name,Usage Type,Qty,Cost Per Card,Total Cost,Stream ID\n";
          const body   = rows.map(r=>`${r.date},"${r.cardName}","${r.usage}",${r.qty},${r.costPerCard.toFixed(2)},${r.totalCost.toFixed(2)},${r.streamId}`).join("\n");
          const blob   = new Blob([header+body], { type:"text/csv" });
          const a      = document.createElement("a");
          a.href       = URL.createObjectURL(blob);
          a.download   = `${ct.replace(" ","_")}_usage_${opsPeriod}.csv`;
          a.click();
        }

        function exportAllCardsCSV() {
          const header = "Date,Card Name,Card Type,Usage Type,Qty,Cost Per Card,Total Cost,Stream ID\n";
          const allRows = CARD_TYPES.flatMap(ct => cardRowsByType[ct].map(r=>({...r, cardType:ct})));
          allRows.sort((a,b)=>a.date.localeCompare(b.date));
          const body = allRows.map(r=>`${r.date},"${r.cardName}","${r.cardType}","${r.usage}",${r.qty},${r.costPerCard.toFixed(2)},${r.totalCost.toFixed(2)},${r.streamId}`).join("\n");
          const blob = new Blob([header+body], { type:"text/csv" });
          const a    = document.createElement("a");
          a.href     = URL.createObjectURL(blob);
          a.download = `all_cards_used_${opsPeriod}.csv`;
          a.click();
        }

        const periodLabel = opsPeriod==="month"?"This Month":opsPeriod==="quarter"?"This Quarter":opsPeriod==="year"?"This Year":opsPeriod==="custom"&&opsFrom&&opsTo?`${opsFrom} → ${opsTo}`:"All Time";
        const totalCardCost = CARD_TYPES.reduce((s,ct)=>s+cardCostByType[ct],0);
        const totalCardQty  = CARD_TYPES.reduce((s,ct)=>s+cardQtyByType[ct],0);

        return (
          <div style={{ ...S.card }}>
            {/* Header + period filter */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10, marginBottom:14 }}>
              <SectionLabel t="📦 Ops Summary" />
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
                {[["month","Month"],["quarter","Quarter"],["year","Year"],["all","All Time"],["custom","Custom"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setOpsPeriod(v)}
                    style={{ background:opsPeriod===v?"rgba(232,49,122,0.15)":"transparent", border:`1px solid ${opsPeriod===v?"#E8317A":"var(--bz-line-2)"}`, color:opsPeriod===v?"#E8317A":"#888", borderRadius:16, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {opsPeriod==="custom" && (
              <div style={{ display:"flex", gap:8, marginBottom:12, alignItems:"center" }}>
                <input type="date" value={opsFrom} onChange={e=>setOpsFrom(e.target.value)} style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:7, color:"var(--bz-ink)", padding:"6px 10px", fontSize:12, fontFamily:"inherit" }}/>
                <span style={{ color:"var(--bz-ink-3)" }}>to</span>
                <input type="date" value={opsTo} onChange={e=>setOpsTo(e.target.value)} style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:7, color:"var(--bz-ink)", padding:"6px 10px", fontSize:12, fontFamily:"inherit" }}/>
              </div>
            )}

            {/* Supplies */}
            <div style={{ fontSize:10, color:"var(--bz-ink-3)", fontWeight:700, textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Supplies — {periodLabel}</div>
            <div className="dash-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
              {[
                { l:"MagPros",       v:`$${totMagpros.toFixed(2)}`,  sub:totMagQty>0?`${totMagQty} units`:"",  c:"#7B9CFF" },
                { l:"Packaging",     v:`$${totPack.toFixed(2)}`,     sub:totPackQty>0?`${totPackQty} units`:"", c:"#7B9CFF" },
                ...(totTopload>0?[{ l:"Top Loaders",   v:`$${totTopload.toFixed(2)}`,  sub:totTopQty>0?`${totTopQty} units`:"",  c:"#7B9CFF" }]:[]),
                { l:"Chaser Cards",  v:`$${totChaser.toFixed(2)}`,   sub:"",                                    c:"#E8317A" },
                { l:"Coupons Given", v:`$${totCoupons.toFixed(2)}`,  sub:"",                                    c:"#FBBF24" },
                { l:"🟢 Zion Cases", v:totZion>0?`$${totZion.toFixed(2)}`:"--", sub:totZion>0?`~${Math.round(totZion/3)} units`:"Bazooka-only", c:"#4ade80" },
              ].map(({l,v,sub,c}) => (
                <div key={l} style={{ background:"var(--bz-s2)", borderRadius:8, padding:"12px 14px", border:"1px solid var(--bz-line-2)" }}>
                  <div style={{ fontSize:18, fontWeight:900, color:c }}>{v}</div>
                  <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginTop:2 }}>{l}</div>
                  {sub && <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>{sub}</div>}
                </div>
              ))}
            </div>

            {/* Card usage by type with export */}
            <div style={{ marginTop:14, paddingTop:14, borderTop:"1px solid var(--bz-line)" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10, flexWrap:"wrap", gap:8 }}>
                <div style={{ fontSize:10, color:"var(--bz-ink-3)", fontWeight:700, textTransform:"uppercase", letterSpacing:1 }}>Cards Used — {periodLabel}</div>
                {totalCardQty > 0 && (
                  <button onClick={exportAllCardsCSV}
                    style={{ background:"rgba(74,222,128,0.1)", border:"1px solid rgba(74,222,128,0.3)", color:"#4ade80", borderRadius:8, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                    ⬇ Export All ({totalCardQty} cards) CSV
                  </button>
                )}
              </div>
              <div className="dash-grid-4" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
                {CARD_TYPES.map(ct => {
                  const cc  = CC[ct]||{ text:"#888", bg:"var(--bz-s1)" };
                  const qty  = cardQtyByType[ct]||0;
                  const cost = cardCostByType[ct]||0;
                  return (
                    <div key={ct} style={{ background:"var(--bz-s2)", borderRadius:8, padding:"12px 14px", border:"1px solid var(--bz-line-2)" }}>
                      <div style={{ fontSize:18, fontWeight:900, color:cc.text }}>{qty}</div>
                      <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginTop:2 }}>{ct.replace(" Cards","")}</div>
                      {cost>0 && <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>${cost.toFixed(2)} cost</div>}
                      {qty>0 && (
                        <button onClick={()=>exportCardCSV(ct)}
                          style={{ marginTop:6, background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:6, padding:"3px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit", width:"100%" }}>
                          ⬇ Export CSV
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {totalCardCost > 0 && (
                <div style={{ marginTop:8, textAlign:"right", fontSize:12, color:"var(--bz-ink-3)" }}>
                  Total card cost: <strong style={{ color:"var(--bz-ink)" }}>${totalCardCost.toFixed(2)}</strong>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Year-End Projections */}
      {canSeeFinancials && (() => {
        const now = new Date();
        const dayOfYear  = Math.floor((now - new Date(now.getFullYear(),0,0)) / 86400000);
        const daysInYear = 365;
        const ytdStreams  = streams.filter(s => new Date(s.date+"T12:00:00").getFullYear()===now.getFullYear() && !s.excludeFinancials && !isSinglesStream(s));
        const skippedStreams = streams.filter(s => !s.date || new Date(s.date+"T12:00:00").getFullYear()!==now.getFullYear());
        const ytdMonthsWithStreams = new Set(ytdStreams.map(s=>(s.date||"").slice(0,7)));
        const ytdHist    = historicalData.filter(h => h.yearMonth?.startsWith(String(now.getFullYear())) && !ytdMonthsWithStreams.has(h.yearMonth));
        const ytdGross   = ytdStreams.reduce((sum,s) => sum+(parseFloat(s.grossRevenue)||0), 0)
                         + ytdHist.reduce((sum,h) => sum+(parseFloat(h.grossRevenue)||0), 0);
        const ytdNet     = ytdStreams.reduce((sum,s) => sum+(parseFloat(calcStreamDash(s).netRev)||0), 0)
                         + ytdHist.reduce((sum,h) => sum+(parseFloat(h.netRevenue)||0), 0);
        const ytdBaz     = ytdStreams.reduce((sum,s) => sum+calcStreamDash(s).bazNet, 0)
                         + ytdHist.reduce((sum,h) => sum+(parseFloat(h.netRevenue)||0)*0.30, 0);
        const ytdTrueNet  = ytdStreams.reduce((sum,s) => sum+calcStreamDash(s).bazTrueNet, 0)
                         + ytdHist.reduce((sum,h) => sum+(parseFloat(h.netRevenue)||0)*0.30-(parseFloat(h.commPaid)||0)+(parseFloat(h.imcReimb)||0), 0);
        const ytdNewBuyers = ytdStreams.reduce((sum,s) => sum+(parseInt(s.newBuyers)||0), 0)
                         + ytdHist.reduce((sum,h) => sum+(parseInt(h.newBuyers)||0), 0);
        if (ytdStreams.length === 0 && ytdHist.length === 0) return null;
        const pct  = Math.round(dayOfYear / daysInYear * 100);
        // Project based on weekly stream pace, not raw days elapsed
        const weeksElapsed = Math.max(1, dayOfYear / 7);
        const weeksInYear  = 52;
        const proj = v => weeksElapsed > 0 ? v / weeksElapsed * weeksInYear : 0;

        function saveGoals() {
          setGoals(goalForm);
          try { localStorage.setItem(GOAL_KEY, JSON.stringify(goalForm)); } catch(e) {}
          setEditGoals(false);
        }

        const metrics = [
          { key:"gross",    l:"Gross Revenue",    v:proj(ytdGross),    ytd:ytdGross,    c:"#E8317A" },
          { key:"net",      l:"Net Revenue",       v:proj(ytdNet),      ytd:ytdNet,      c:"#7B9CFF" },
          { key:"baz",      l:"Bazooka Earnings",  v:proj(ytdBaz),      ytd:ytdBaz,      c:"#6B2D8B" },
          { key:"trueNet",  l:"Bazooka True Net",  v:proj(ytdTrueNet),  ytd:ytdTrueNet,  c:"#4ade80" },
          { key:"buyers",   l:"New Buyers",        v:proj(ytdNewBuyers),ytd:ytdNewBuyers,c:"#FBBF24", count:true },
        ];

        return (
          <div style={{ background:"var(--bz-s1)", border:"1px solid #E8317A33", borderRadius:14, padding:"18px 20px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:6 }}>
              <div style={{ fontSize:10, fontWeight:800, color:"#E8317A", textTransform:"uppercase", letterSpacing:2.5, display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:14, height:2, background:"#E8317A", borderRadius:1, boxShadow:"0 0 8px rgba(232,49,122,0.6)" }}/>
                📈 {now.getFullYear()} Year-End Projections
              </div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>
                  {ytdStreams.length} stream{ytdStreams.length!==1?"s":""}
                  {ytdHist.length>0 ? ` + ${ytdHist.length} historical` : ""} · {pct}% through {now.getFullYear()}
                  {skippedStreams.length>0 && <span style={{color:"#FBBF24",marginLeft:6}}>⚠ {skippedStreams.length} streams excluded (wrong/missing date)</span>}
                </span>
                <button onClick={()=>{ setGoalForm(goals); setEditGoals(p=>!p); }}
                  style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:7, padding:"3px 10px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>
                  {editGoals ? "Cancel" : "🎯 Set Goals"}
                </button>
              </div>
            </div>

            {/* Month-by-month gross breakdown */}
            {(() => {
              const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
              const monthData = monthNames.map((label,i) => {
                const key = `${now.getFullYear()}-${String(i+1).padStart(2,"0")}`;
                // Match the rest of the dashboard: exclude singles (pass-through) and
                // "Exclude from Financials" streams (revenue already counted elsewhere).
                const monthStreamsForGross = streams.filter(s => (s.date||"").startsWith(key) && !isSinglesStream(s) && !s.excludeFinancials);
                const streamGross = monthStreamsForGross.reduce((s,r)=>s+(parseFloat(r.grossRevenue)||0),0);
                // If this month has real streams, don't also add a historical summary for it (double-count).
                const histGross = monthStreamsForGross.length > 0 ? 0 : historicalData
                  .filter(h => h.yearMonth===key)
                  .reduce((s,h)=>s+(parseFloat(h.grossRevenue)||0),0);
                const total = streamGross + histGross;
                const streamCount = monthStreamsForGross.length;
                return { label, key, total, streamCount, isFuture: i > now.getMonth() };
              });
              const maxMonth = Math.max(...monthData.map(m=>m.total), 1);
              return (
                <div style={{marginBottom:16,background:"var(--bz-bg)",borderRadius:10,padding:"14px 16px"}}>
                  <div style={{fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginBottom:10,fontWeight:700}}>Gross Revenue by Month</div>
                  <div style={{display:"flex",gap:4,alignItems:"flex-end",height:80}}>
                    {monthData.map(m=>(
                      <div key={m.key} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                        <div style={{width:"100%",height:64,display:"flex",alignItems:"flex-end"}}>
                          <div style={{
                            width:"100%",
                            height:m.total>0?`${Math.max((m.total/maxMonth)*64,3)}px`:"2px",
                            background:m.isFuture?"var(--bz-s2)":m.total===0&&!m.isFuture?"rgba(239,68,68,0.4)":"#E8317A",
                            borderRadius:"2px 2px 0 0",
                            position:"relative",
                            cursor:"default",
                          }} title={`${m.label}: $${Math.round(m.total).toLocaleString()} (${m.streamCount} streams)`}/>
                        </div>
                        <div style={{fontSize:9,color:m.total===0&&!m.isFuture?"#ef4444":"#555",fontWeight:m.total===0&&!m.isFuture?700:400}}>{m.label}</div>
                        {m.total>0&&<div style={{fontSize:8,color:"var(--bz-ink-3)"}}>${m.total>=1000?`${(m.total/1000).toFixed(0)}k`:Math.round(m.total)}</div>}
                        {m.total===0&&!m.isFuture&&<div style={{fontSize:8,color:"#ef4444"}}>⚠ $0</div>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Goal editor */}
            {editGoals && (
              <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"14px 16px", marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>Set {now.getFullYear()} Goals</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:12 }}>
                  {metrics.map(({key,l})=>(
                    <div key={key}>
                      <label style={{ fontSize:10, fontWeight:700, color:"#8a8a8a", textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:4 }}>{l}</label>
                      <input type="number" value={goalForm[key]||""} onChange={e=>setGoalForm(p=>({...p,[key]:e.target.value}))}
                        placeholder="0" style={{ background:"var(--bz-s3)", border:"1px solid var(--bz-line)", borderRadius:8, color:"var(--bz-ink)", padding:"7px 10px", fontSize:12, fontFamily:"inherit", outline:"none", width:"100%", boxSizing:"border-box" }}/>
                    </div>
                  ))}
                </div>
                <button onClick={saveGoals} style={{ background:"#166534", color:"#fff", border:"none", borderRadius:8, padding:"7px 20px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>💾 Save Goals</button>
              </div>
            )}

            {/* Metric tiles */}
            <div className="dash-grid-5" style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:14 }}>
              {metrics.map(({key,l,v,ytd,c,count}) => {
                const goal = parseFloat(goals[key]) || 0;
                const projPct = goal > 0 ? Math.min(100, v/goal*100) : 0;
                const ytdPct  = goal > 0 ? Math.min(100, ytd/goal*100) : 0;
                const onTrack = goal > 0 && v >= goal;
                return (
                  <div key={l} style={{ textAlign:"center" }}>
                    <div style={{ fontSize:20, fontWeight:900, color:onTrack?"#4ade80":c }}>{count ? Math.round(v).toLocaleString() : fmt(v)}</div>
                    <div style={{ fontSize:9, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginTop:4 }}>{l}</div>
                    <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:3 }}>{count ? Math.round(ytd).toLocaleString() : fmt(ytd)} YTD</div>
                    {goal > 0 && (
                      <>
                        <div style={{ fontSize:10, color:onTrack?"#4ade80":"#555", marginTop:3, fontWeight:700 }}>
                          {onTrack ? "✅ On track" : `${projPct.toFixed(0)}% of goal`}
                        </div>
                        <div style={{ height:3, background:"var(--bz-s2)", borderRadius:2, marginTop:5, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${ytdPct}%`, background:onTrack?"#4ade80":c, borderRadius:2, transition:"width 0.6s ease" }}/>
                        </div>
                        <div style={{ fontSize:9, color:"#8a8a8a", marginTop:2 }}>
                          {count ? Math.round(goal).toLocaleString() : fmt(goal)} goal
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Year progress bar */}
            <div style={{ height:6, background:"var(--bz-s2)", borderRadius:10, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${pct}%`, background:"linear-gradient(90deg,#E8317A,#6B2D8B)", borderRadius:10, transition:"width 0.6s ease" }}/>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
              <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>Jan 1</span>
              <span style={{ fontSize:10, color:"#E8317A", fontWeight:700 }}>Today ({pct}%)</span>
              <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>Dec 31</span>
            </div>
          </div>
        );
      })()}

      {canSeeFinancials && (
      <div style={{ ...S.card, border: alerts.length > 0 ? "2px solid #FCA5A5" : "2px solid #D6F4E3" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
          <SectionLabel t="Inventory Health Check" />
          <span style={{ fontSize:12, fontWeight:700, padding:"4px 12px", borderRadius:20, background:alerts.length===0?"#D6F4E3":alerts.length<=2?"#FFF9DB":"#FEE2E2", color:alerts.length===0?"#166534":alerts.length<=2?"#92400e":"#991b1b" }}>
            {alerts.length===0 ? "✅ All Good" : `🚨 ${alerts.length} Critical`}
          </span>
        </div>

        {/* Top KPIs — what you have */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:16 }}>
          {[
            { l:"Available Now", v:availCount,              c:"#4ade80" },
            { l:"In Transit",    v:transitCount,             c:"#7B9CFF" },
            { l:"Total Stock",   v:availCount + transitCount, c:"var(--bz-ink)" },
          ].map(({l,v,c}) => (
            <div key={l} className="bz-fin-card" style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:"var(--bz-radius-sm)", padding:"16px", textAlign:"center", transition:"border-color .18s, transform .18s, box-shadow .18s" }}>
              <div style={{ fontSize:28, fontWeight:800, color:c, marginBottom:3, fontFamily:"'Arial Narrow',sans-serif", letterSpacing:"-0.02em" }}>{v}</div>
              <div style={{ fontSize:10, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* Per card type — simplified */}
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {CARD_TYPES.map(ct => {
            const cc = CC[ct];
            const avail   = stats[ct].avail;
            const transit = stats[ct].inTransit;
            const target  = TARGETS[ct].buffer;
            const days    = runway[ct];
            const pct     = Math.min(avail / Math.max(target, 1), 1);
            const status  = avail === 0 ? { label:"❌ Out", color:"#991b1b", bg:"#FEE2E2" }
                          : avail < target ? { label:"⚠️ Low", color:"#92400e", bg:"#FFF9DB" }
                          : { label:"✅ Good", color:"#166534", bg:"#D6F4E3" };
            const runLabel = days >= 999 ? null : days <= 0 ? "Overdue" : `~${days}d left`;

            return (
              <div key={ct} style={{ background:"var(--bz-s1)", border:`1px solid ${avail < target ? (avail===0?"rgba(153,27,27,0.4)":"rgba(146,64,14,0.3)") : "var(--bz-line-2)"}`, borderRadius:10, padding:"12px 16px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:8 }}>
                  <span style={{ fontWeight:800, color:cc.text, fontSize:14, flex:1 }}>{ct}</span>
                  {/* Available count — the main number */}
                  <div style={{ textAlign:"center", minWidth:60 }}>
                    <div style={{ fontSize:22, fontWeight:900, color:avail===0?"#991b1b":avail<target?"#FBBF24":"#4ade80" }}>{avail}</div>
                    <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Available</div>
                  </div>
                  {transit > 0 && (
                    <div style={{ textAlign:"center", minWidth:50 }}>
                      <div style={{ fontSize:18, fontWeight:700, color:"#7B9CFF" }}>{transit}</div>
                      <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Coming</div>
                    </div>
                  )}
                  <div style={{ textAlign:"center", minWidth:50 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:"var(--bz-ink-3)" }}>{target}</div>
                    <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Min</div>
                  </div>
                  <span style={{ background:status.bg, color:status.color, fontSize:11, fontWeight:700, padding:"4px 10px", borderRadius:6, whiteSpace:"nowrap" }}>{status.label}</span>
                  {runLabel && <span style={{ fontSize:11, color:days<=7?"#991b1b":days<=14?"#92400e":"#555", fontWeight:700, whiteSpace:"nowrap" }}>{runLabel}</span>}
                </div>
                {/* Progress bar: available vs target */}
                <div style={{ height:5, background:"var(--bz-s2)", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${pct*100}%`, background:pct>=1?"#4ade80":pct>=0.5?"#FBBF24":"#991b1b", borderRadius:3, transition:"width 0.4s" }}/>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
                  <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>0</div>
                  <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>min: {target}</div>
                </div>
              </div>
            );
          })}
        </div>

        {canSeeFinancials && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginTop:14 }}>
          {[
            { l:"Market Value (in stock)", v:`$${totMkt.toFixed(2)}`, c:"#92400e" },
            { l:"Cost of Current Stock",  v:`$${totInv.toFixed(2)}`, c:"#6B2D8B" },
            { l:"Total Spent (all time)", v:`$${totInvAll.toFixed(2)}`, c:"#444" },
          ].map(({l,v,c}) => (
            <div key={l} style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"10px 14px", textAlign:"center" }}>
              <div style={{ fontSize:18, fontWeight:900, color:c, marginBottom:2 }}>{v}</div>
              <div style={{ fontSize:10, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1 }}>{l}</div>
            </div>
          ))}
        </div>
        )}
      </div>
      )}

      {canSeeFinancials && (<>
      <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
        <div style={{ padding:"16px 20px 12px" }}>
          <SectionLabel t="Inventory by Card Type" />
        </div>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ borderBottom:"1px solid var(--bz-line)" }}>
              <th style={{ ...S.th, textAlign:"left", paddingLeft:20, width:"30%" }}>Card Type</th>
              <th style={{ ...S.th, textAlign:"center" }}>Stock</th>
              <th style={{ ...S.th, textAlign:"center" }}>Used</th>
              <th style={{ ...S.th, textAlign:"center" }}>Avail</th>
              <th style={{ ...S.th, textAlign:"center" }}>Transit</th>
              <th style={{ ...S.th, textAlign:"center" }}>Min</th>
              <th style={{ ...S.th, textAlign:"right", paddingRight:20 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {CARD_TYPES.map((ct,i) => {
              const d = stats[ct]; const { buffer } = TARGETS[ct]; const cc = CC[ct];
              const avail   = d.avail;
              const transit = d.inTransit;
              const pct     = d.market > 0 ? d.invested/d.market : null;
              const ok = avail >= buffer; const warn = avail >= buffer*0.5;
              const sc = ok?"#4ade80":warn?"#FBBF24":"#E8317A";
              const sl = ok?"\u2705 Stocked":warn?"\u26A0\uFE0F Low":"\uD83D\uDEA8 Critical";
              return (<tr key={ct} style={{ background:i%2===0?"var(--bz-s1)":"var(--bz-s1)", borderBottom:"1px solid var(--bz-line)" }}>
                  <td style={{ padding:"14px 20px", fontWeight:800, color:cc.text, fontSize:14 }}>{ct}</td>
                  <td style={{ ...S.td, textAlign:"center", fontSize:20, fontWeight:900, color:cc.text }}>{d.total}</td>
                  <td style={{ ...S.td, textAlign:"center", fontSize:20, fontWeight:900, color:d.used>0?"#E8317A":"#333" }}>{d.used}</td>
                  <td style={{ ...S.td, textAlign:"center", fontSize:20, fontWeight:900, color:ok?"#4ade80":warn?"#FBBF24":"#E8317A" }}>{avail}</td>
                  <td style={{ ...S.td, textAlign:"center", fontSize:20, fontWeight:900, color:transit>0?"#7B9CFF":"#333" }}>{transit}</td>
                  <td style={{ ...S.td, textAlign:"center", fontSize:16, color:"var(--bz-ink-3)", fontWeight:700 }}>{buffer}</td>
                  <td style={{ padding:"14px 20px", textAlign:"right" }}>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
                      {canSeeFinancials && <ZoneBadge pct={pct} />}
                      <span style={{ background:ok?"#0a1a0a":warn?"#1a1400":"#1a0a0a", color:sc, border:`1px solid ${sc}33`, borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>{sl}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={S.card}>
        <SectionLabel t="Team Activity" />
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
          {BREAKERS.map(b => {
            const bc = BC[b];
            const bBreaks = breaks.filter(x => x.breaker === b);
            const bInv    = inventory.filter(x => x.addedBy?.toLowerCase().includes(b.toLowerCase()));
            const last    = bBreaks.length > 0 ? [...bBreaks].sort((a,z) => new Date(z.dateAdded)-new Date(a.dateAdded))[0] : null;
            const isYou   = user?.displayName?.toLowerCase().includes(b.toLowerCase());
            return (
              <div key={b} style={{ ...S.card, border:`1.5px solid ${bc.border}44`, background:bc.bg+"44", position:"relative" }}>
                {isYou && <div style={{ position:"absolute", top:10, right:10, fontSize:10, fontWeight:700, color:bc.text, background:bc.bg, border:`1px solid ${bc.border}`, borderRadius:10, padding:"2px 8px" }}>You</div>}
                <div style={{ fontWeight:900, fontSize:16, color:bc.text, marginBottom:10 }}>{b}</div>
                {[["Cards logged out",bBreaks.length],["Added to inventory",bInv.length],["Last break",last?new Date(last.dateAdded).toLocaleDateString():"--"]].map(([l,v]) => (
                  <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", borderBottom:"1px solid var(--bz-line-2)" }}>
                    <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{l}</span>
                    <span style={{ fontSize:11, fontWeight:700, color:bc.text }}>{v}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      </>)}

      {/* Historical Data -- Admin only */}
      {canSeeFinancials && (() => {

        function startEdit(h) {
          setHistForm({ yearMonth:h.yearMonth, grossRevenue:h.grossRevenue||"", netRevenue:h.netRevenue||"", imcReimb:h.imcReimb||"", newBuyers:h.newBuyers||"", notes:h.notes||"" });
          setEditingId(h.id);
          setShowHist(true);
        }
        function cancelEdit() {
          setHistForm({ yearMonth:"", grossRevenue:"", netRevenue:"", imcReimb:"", newBuyers:"", notes:"" });
          setEditingId(null);
        }

        async function saveHist() {
          if (!histForm.yearMonth || !histForm.grossRevenue) return;
          await onSaveHistorical({ ...histForm, id: histForm.yearMonth });
          setHistForm({ yearMonth:"", grossRevenue:"", netRevenue:"", imcReimb:"", newBuyers:"", notes:"" });
          setEditingId(null);
        }

        return (
          <div style={{ ...S.card, border:"2px solid var(--bz-line-2)333" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: showHist ? 14 : 0 }}>
              <SectionLabel t="📅 Historical Monthly Data" />
              <button onClick={()=>{ setShowHist(p=>!p); cancelEdit(); }} style={{ background:"transparent", border:"1.5px solid #6B2D8B", color:"#E8317A", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                {showHist ? "\u25B2 Hide" : "\u25BC Manage"}
              </button>
            </div>
            {showHist && (
              <>
                <div style={{ fontSize:12, color:"var(--bz-ink-2)", marginBottom:14 }}>{editingId ? `Editing ${editingId} -- update fields and save.` : "Enter monthly summary data for historical periods. These feed into YTD totals and projections on the dashboard."}</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr 2fr auto", gap:10, marginBottom:14, alignItems:"end" }}>
                  <div>
                    <label style={S.lbl}>Month (YYYY-MM)</label>
                    <input type="month" value={histForm.yearMonth} onChange={e=>setHistForm(p=>({...p,yearMonth:e.target.value}))} style={{ ...S.inp, opacity: editingId ? 0.5 : 1 }} disabled={!!editingId}/>
                  </div>
                  <div>
                    <label style={S.lbl}>Gross Revenue ($)</label>
                    <input type="number" step="0.01" value={histForm.grossRevenue} onChange={e=>setHistForm(p=>({...p,grossRevenue:e.target.value}))} placeholder="0.00" style={S.inp}/>
                  </div>
                  <div>
                    <label style={S.lbl}>Net Revenue ($)</label>
                    <input type="number" step="0.01" value={histForm.netRevenue} onChange={e=>setHistForm(p=>({...p,netRevenue:e.target.value}))} placeholder="0.00" style={S.inp}/>
                  </div>
                  <div>
                    <label style={S.lbl}>IMC Reimb ($)</label>
                    <input type="number" step="0.01" value={histForm.imcReimb} onChange={e=>setHistForm(p=>({...p,imcReimb:e.target.value}))} placeholder="0.00" style={S.inp}/>
                  </div>
                  <div>
                    <label style={S.lbl}>New Buyers</label>
                    <input type="number" min="0" value={histForm.newBuyers} onChange={e=>setHistForm(p=>({...p,newBuyers:e.target.value}))} placeholder="0" style={S.inp}/>
                  </div>
                  <div>
                    <label style={S.lbl}>Notes</label>
                    <input value={histForm.notes} onChange={e=>setHistForm(p=>({...p,notes:e.target.value}))} placeholder="e.g. Jan streams" style={S.inp}/>
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    <Btn onClick={saveHist} disabled={!histForm.yearMonth||!histForm.grossRevenue} variant="green">{editingId ? "\uD83D\uDCBE Save" : "+ Add"}</Btn>
                    {editingId && <Btn onClick={cancelEdit} variant="ghost">{"\u2715"}</Btn>}
                  </div>
                </div>
                {historicalData.length > 0 && (
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>{["Month","Gross","Net","Bazooka (30%)","IMC Reimb","True Net","\uD83C\uDF31 New Buyers","Notes",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {historicalData.map((h,i) => (
                        <tr key={h.id} style={{ background: editingId===h.id?"rgba(107,45,139,0.08)":i%2===0?"var(--bz-s1)":"var(--bz-s1)" }}>
                          <td style={{ ...S.td, fontWeight:700, color:"#E8317A" }}>{h.yearMonth}</td>
                          <td style={{ ...S.td, color:"#E8317A", fontWeight:700 }}>{fmt(parseFloat(h.grossRevenue)||0)}</td>
                          <td style={{ ...S.td, color:"var(--bz-ink)" }}>{fmt(parseFloat(h.netRevenue)||0)}</td>
                          <td style={{ ...S.td, color:"#E8317A", fontWeight:700 }}>{fmt((parseFloat(h.netRevenue)||0)*0.30)}</td>
                          <td style={{ ...S.td, color:"#E8317A" }}>{h.imcReimb?fmt(parseFloat(h.imcReimb)):"--"}</td>
                          <td style={{ ...S.td, color:"#E8317A", fontWeight:900 }}>{fmt((parseFloat(h.netRevenue)||0)*0.30 + (parseFloat(h.imcReimb)||0))}</td>
                          <td style={{ ...S.td, color:"#E8317A", fontWeight:700 }}>{h.newBuyers>0?`\uD83C\uDF31 ${h.newBuyers}`:"--"}</td>
                          <td style={{ ...S.td, color:"var(--bz-ink-2)" }}>{h.notes||"--"}</td>
                          <td style={S.td}>
                            <div style={{ display:"flex", gap:6 }}>
                              <button onClick={()=>startEdit(h)} style={{ background:"none", border:"1px solid var(--bz-line-2)", borderRadius:5, padding:"2px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit", color:"var(--bz-ink-2)" }}>{"\u270F\uFE0F"}</button>
                              <button onClick={()=>{ if(window.confirm("Delete this historical entry?")) onDeleteHistorical(h.id); }} style={{ background:"none", border:"1px solid #FCA5A5", color:"#E8317A", borderRadius:5, padding:"2px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDDD1"}</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Card Type Migration Tool -- Admin only */}
      {canSeeFinancials && (() => {
        const USAGE_MAP = { "Giveaway":"Giveaway Cards", "Insurance":"Insurance Cards", "First-Timer Pack":"First-Timer Cards", "Chaser Pull":"Chaser Cards", "Chaser":"Chaser Cards" };
        const mismatched = breaks.filter(b => {
          if (b.isPoolLog || !b.usage) return false;
          const correct = USAGE_MAP[b.usage];
          return correct && b.cardType !== correct;
        });
        if (mismatched.length === 0) return null;
        async function runMigration() {
          setMigrating(true);
          await Promise.all(mismatched.map(b =>
            setDoc(doc(db,"breaks",b.id), { cardType: USAGE_MAP[b.usage] }, { merge:true })
          ));
          setMigrating(false); setMigDone(true);
        }
        return (
          <div style={{ ...S.card, border:"2px solid rgba(251,191,36,0.3)", background:"rgba(251,191,36,0.04)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:800, color:"#FBBF24", marginBottom:4 }}>⚠️ Card Type Fix Available</div>
                <div style={{ fontSize:12, color:"var(--bz-ink-3)" }}>
                  {mismatched.length} existing break {mismatched.length===1?"entry":"entries"} {mismatched.length===1?"has":"have"} a card type that doesn't match how the card was actually used.
                  This is a one-time fix — it won't affect your inventory, just corrects the tracking labels.
                </div>
              </div>
              {!migDone ? (
                <button onClick={runMigration} disabled={migrating}
                  style={{ background:"rgba(251,191,36,0.15)", color:"#FBBF24", border:"1px solid rgba(251,191,36,0.3)", borderRadius:8, padding:"8px 18px", fontSize:12, fontWeight:800, cursor:migrating?"not-allowed":"pointer", fontFamily:"inherit", flexShrink:0 }}>
                  {migrating?`Fixing ${mismatched.length} entries...`:`✅ Fix ${mismatched.length} Entries`}
                </button>
              ) : (
                <div style={{ color:"#4ade80", fontWeight:700, fontSize:13 }}>✅ All fixed!</div>
              )}
            </div>
          </div>
        );
      })()}

    </div>
  );
}

function LotComp({ defaultMode="builder", onAccept, onSaveComp, onDeleteComp, comps, user, userRole, onSaveQuote, quotes=[], onCloseQuote, onReopenQuote, onBazookaCounter, cardPools=[], onDismissQuoteNotif, bobaCards=[] }) {
  const canSeeFinancials = ["Admin"].includes(userRole?.role);
  const [compMode,     setCompMode]     = useState(defaultMode);
  const [showClosedQuotes, setShowClosedQuotes] = useState(false);
  useEffect(() => { setCompMode(defaultMode); }, [defaultMode]);
  useEffect(()=>{setCompMode(defaultMode);},[defaultMode]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(()=>{ const h=()=>setWindowWidth(window.innerWidth); window.addEventListener("resize",h,{passive:true}); return()=>window.removeEventListener("resize",h); },[]);
  const isMobile = windowWidth < 768;
  const [seller,       setSeller]       = useState({ name:"", contact:"", date:"", source:"", payment:"", paymentHandle:"" });
  const [lotPct,       setLotPct]       = useState("");
  const [finalOffer,   setFOffer]       = useState("");
  const [custView,     setCustView]     = useState(false);
  const [custNote,     setCustNote]     = useState("");
  const [lotLocation,  setLotLocation]  = useState(LOCATIONS[0]);
  const [quoteLink,    setQuoteLink]    = useState(null);
  const [savedQuoteRef, setSavedQuoteRef] = useState(null);
  const [quoteCopied,  setQuoteCopied]  = useState(false);
  const [allowCounter, setAllowCounter] = useState(false);
  const [bzCounterAmt, setBzCounterAmt] = useState({});
  const [rows,         setRows]         = useState(() => Array.from({ length:8 }, () => ({ id:uid(), name:"", cardType:"", mktVal:"", qty:"1", include:true, costOverride:"", pctOverride:"", manualEntry:false })));
  const [quickCards,   setQuickCards]   = useState("");
  const [quickMktVal,  setQuickMktVal]  = useState("");
  const [quickPct,     setQuickPct]     = useState("");
  const [quickOffer,   setQuickOffer]   = useState("");
  const [counterOffer,        setCounterOffer]        = useState("");
  const [loadedCompId,        setLoadedCompId]        = useState(null);
  const [lightbox,            setLightbox]            = useState(null);
  const [loadedCompHadCards,  setLoadedCompHadCards]  = useState(true);
  const [acOpen,       setAcOpen]       = useState(null);  // row id with open autocomplete
  const [acQuery,      setAcQuery]      = useState({});    // {rowId: queryString}
  const [importing,    setImporting]    = useState(false);




  const pctNum    = parseFloat(lotPct)/100 || 0.60;
  const included  = rows.filter(r => r.name && r.include);
  const totalMkt  = included.reduce((s,r) => s + (parseFloat(r.mktVal)||0)*(parseInt(r.qty)||1), 0);
  // Base offer from global pct — this is the fixed total
  const baseOffer = totalMkt * pctNum;
  // Locked cards: those with costOverride OR pctOverride
  const lockedAmt = included.reduce((s,r) => {
    const mv = (parseFloat(r.mktVal)||0)*(parseInt(r.qty)||1);
    const co = parseFloat(r.costOverride);
    const po = parseFloat(r.pctOverride);
    if (!isNaN(co)) return s + co*(parseInt(r.qty)||1);
    if (!isNaN(po)) return s + mv*(po/100);
    return s;
  }, 0);
  const unlockedMkt = included.reduce((s,r) => {
    const co = parseFloat(r.costOverride);
    const po = parseFloat(r.pctOverride);
    return (isNaN(co) && isNaN(po)) ? s + (parseFloat(r.mktVal)||0)*(parseInt(r.qty)||1) : s;
  }, 0);
  // Total offer = locked amounts + unlocked cards at global pct
  const calcOffer = lockedAmt + unlockedMkt * pctNum;
  const offerAmt  = finalOffer !== "" ? parseFloat(finalOffer) : null;
  const counterAmt = counterOffer !== "" ? parseFloat(counterOffer) : null;
  // Priority: counter > manual override > calculated
  const dispOffer  = (counterAmt != null && counterAmt > 0) ? counterAmt : (offerAmt != null && offerAmt > 0) ? offerAmt : calcOffer;
  const dispPct    = totalMkt > 0 ? dispOffer / totalMkt : pctNum;
  const lotZone    = totalMkt > 0 ? getZone(dispOffer/totalMkt) : null;
  const totalCards = included.reduce((s,r) => s+(parseInt(r.qty)||1), 0);
  const quickTotal     = (parseFloat(quickMktVal)||0) * (parseInt(quickCards)||0);
  const quickCalcOffer = quickTotal * (parseFloat(quickPct)/100 || 0.60);
  const quickOfferAmt  = parseFloat(quickOffer) || quickCalcOffer;
  const quickZone      = quickTotal > 0 ? getZone(quickOfferAmt/quickTotal) : null;
  const counterZone    = totalMkt > 0 && counterAmt != null && counterAmt > 0 ? getZone(counterAmt/totalMkt) : null;

  // Cost allocation per card
  const manuallyAllocated = lockedAmt;
  const remainingOffer = Math.max(0, dispOffer - manuallyAllocated);
  function getCostPerCard(r) {
    const co = parseFloat(r.costOverride);
    if (!isNaN(co)) return co;
    const po = parseFloat(r.pctOverride);
    const mv = parseFloat(r.mktVal)||0;
    if (!isNaN(po)) return mv*(po/100);
    // Unlocked: proportional share of remaining offer
    if (unlockedMkt > 0) return (mv / unlockedMkt) * remainingOffer;
    return 0;
  }
  const totalAllocated = included.reduce((s,r) => s + getCostPerCard(r)*(parseInt(r.qty)||1), 0);
  const allocationDiff = dispOffer > 0 ? totalAllocated - dispOffer : 0;

  function upd(id,f,v) { setRows(p => p.map(r => r.id===id ? {...r,[f]:v} : r)); }
  function addRow() { setRows(p => [...p, { id:uid(), name:"", cardType:"", mktVal:"", qty:"1", include:true, costOverride:"", manualEntry:false }]); }

  function importFromFile(e) {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    const ext = file.name.split(".").pop().toLowerCase();

    function processText(text) {
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { alert("File appears empty or has only headers."); setImporting(false); return; }
      // Parse header row
      const headers = lines[0].split(",").map(h => h.replace(/^["']|["']$/g,"").trim().toLowerCase());
      // Auto-detect columns
      const nameIdx    = headers.findIndex(h => h.includes("hero") || h==="name" || h.includes("card name"));
      const weaponIdx  = headers.findIndex(h => h.includes("weapon"));
      const treatIdx   = headers.findIndex(h => h.includes("treatment") || h.includes("treat"));
      const qtyIdx     = headers.findIndex(h => h.includes("qty") || h.includes("quantity"));
      const valIdx     = headers.findIndex(h => h.includes("value") || h.includes("market") || h.includes("price") || h.includes("mkt"));
      const typeIdx    = headers.findIndex(h => h.includes("type"));
      if (nameIdx === -1) { alert("Could not find a Hero or Name column. Make sure your first row has column headers."); setImporting(false); return; }

      const newRows = [];
      for (let i = 1; i < lines.length; i++) {
        const cells = [];
        let cur = ""; let inQ = false;
        for (const ch of lines[i]) {
          if (ch==='"'||ch==="'") { inQ=!inQ; }
          else if (ch===","&&!inQ) { cells.push(cur.trim()); cur=""; }
          else { cur+=ch; }
        }
        cells.push(cur.trim());
        const hero   = cells[nameIdx]  ? cells[nameIdx].replace(/^["']|["']$/g,"").trim()   : "";
        const weapon = weaponIdx>=0 && cells[weaponIdx] ? cells[weaponIdx].replace(/^["']|["']$/g,"").trim() : "";
        const treat  = treatIdx>=0  && cells[treatIdx]  ? cells[treatIdx].replace(/^["']|["']$/g,"").trim()  : "";
        const name   = [hero, weapon, treat].filter(Boolean).join(" ");
        if (!name) continue;
        const qty    = qtyIdx>=0 && cells[qtyIdx]  ? String(Math.max(1,parseInt(cells[qtyIdx])||1)) : "1";
        const rawVal = valIdx>=0 && cells[valIdx]   ? cells[valIdx].replace(/[$,\s"']/g,"") : "";
        const mktVal = rawVal && !isNaN(parseFloat(rawVal)) ? String(parseFloat(rawVal)) : "";
        const cardType = typeIdx>=0 && cells[typeIdx] ? cells[typeIdx].replace(/^["']|["']$/g,"").trim() : "";
        newRows.push({ id:uid(), name, cardType, mktVal, qty, include:true, costOverride:"", pctOverride:"", manualEntry:true });
      }
      if (!newRows.length) { alert("No cards found. Check that your file has data rows below the header."); setImporting(false); return; }
      setRows(p => [...p.filter(r=>r.name.trim()), ...newRows]);
      setImporting(false);
      alert(`✅ Imported ${newRows.length} card${newRows.length!==1?"s":""}! Add market values in the list below.`);
    }

    if (ext === "csv") {
      const reader = new FileReader();
      reader.onload = ev => processText(ev.target.result);
      reader.onerror = () => { alert("Failed to read file."); setImporting(false); };
      reader.readAsText(file);
    } else {
      // XLSX — load SheetJS then convert to CSV-like text
      const loadXLSX = (cb) => {
        if (window.XLSX) { cb(); return; }
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
        s.onload = cb;
        s.onerror = () => { alert("Failed to load XLSX library. Please export your file as CSV and try again."); setImporting(false); };
        document.head.appendChild(s);
      };
      const reader = new FileReader();
      reader.onload = ev => {
        loadXLSX(() => {
          try {
            const wb = window.XLSX.read(new Uint8Array(ev.target.result), { type:"array" });
            const csv = window.XLSX.utils.sheet_to_csv(wb.Sheets[wb.SheetNames[0]]);
            processText(csv);
          } catch(err) { alert("Error reading XLSX: " + err.message); setImporting(false); }
        });
      };
      reader.onerror = () => { alert("Failed to read file."); setImporting(false); };
      reader.readAsArrayBuffer(file);
    }
  }

  function loadComp(comp) {
    setSeller({ name:comp.seller||"", contact:comp.contact||"", date:comp.date||"", source:comp.source||"", payment:comp.payment||"", paymentHandle:comp.paymentHandle||"" });
    const hasCards = comp.cards && comp.cards.length > 0;
    setRows(hasCards
      ? comp.cards.map(c => ({ id:uid(), name:c.name||"", cardType:c.cardType||"", mktVal:String(c.mktVal||""), qty:String(c.qty||1), include:true, costOverride:"", pctOverride:"", manualEntry:false }))
      : Array.from({ length:8 }, () => ({ id:uid(), name:"", cardType:"", mktVal:"", qty:"1", include:true, costOverride:"", pctOverride:"", manualEntry:false }))
    );
    setFOffer(comp.offer ? String(comp.offer) : "");
    setCounterOffer("");
    setLoadedCompId(comp.id);
    setLoadedCompHadCards(hasCards);
    setCompMode("builder");
    setTimeout(() => { const el = document.getElementById("comp-builder-top"); if (el) el.scrollIntoView({ behavior:"smooth", block:"start" }); }, 100);
  }

  function saveComp(status) {
    if (!onSaveComp) { alert("Save not available — please refresh and try again."); return; }
    if (included.length === 0) { alert("Add at least one card before saving."); return; }
    onSaveComp({
      id: loadedCompId || undefined,
      seller:seller.name, contact:seller.contact, date:seller.date||new Date().toLocaleDateString(),
      source:seller.source, payment:seller.payment, paymentHandle:seller.paymentHandle, totalCards, totalMarket:totalMkt,
      offer:dispOffer, blendedPct:totalMkt>0?dispOffer/totalMkt:0,
      zone:lotZone?.label||"--", status,
      cards:included.map(r=>({ name:r.name, cardType:r.cardType, qty:parseInt(r.qty)||1, mktVal:parseFloat(r.mktVal)||0, pctOverride:r.pctOverride||"", offerPerCard:getCostPerCard(r) }))
    });
  }

  function doAccept() {
    if (included.length === 0) return;
    const cards = [];
    const lotDate = seller.date || new Date().toISOString().split("T")[0]; // always ISO YYYY-MM-DD
    const lotSeller = seller.name?.trim() || "Unknown Seller";
    included.forEach(r => {
      const qty = parseInt(r.qty)||1;
      const mv  = parseFloat(r.mktVal)||0;
      const cardName = (r.name === "__new__" || r.name === "__manual__") ? (r._newName||"").trim() || r.cardType : r.name || r.cardType;
      const costPerCard = getCostPerCard(r);
      for (let i=0; i<qty; i++) {
        cards.push({ id:uid(), cardName, cardType:r.cardType, marketValue:mv, lotTotalPaid:dispOffer, cardsInLot:totalCards, costPerCard, buyPct:mv>0?costPerCard/mv:null, date:lotDate, source:seller.source||"", seller:lotSeller, payment:seller.payment||"", paymentHandle:seller.paymentHandle||"", dateAdded:new Date().toISOString(), location:lotLocation });
      }
    });
    onAccept(cards, { ...seller, name:lotSeller, date:lotDate }, user, custNote);
  }

  if (custView) return (
    <div>
      <div style={{ marginBottom:14 }}><Btn onClick={()=>setCustView(false)} variant="ghost">{"\u2190 Back to Builder"}</Btn></div>
      <div style={{ background:"var(--bz-s1)", border:"2px solid #E8317A55", borderRadius:16, overflow:"hidden", maxWidth:680, boxShadow:"0 4px 24px rgba(0,0,0,0.08)" }}>
        <div style={{ background:"#000000", padding:"28px 32px", textAlign:"center" }}>
          <div style={{ fontSize:32, fontWeight:900, color:"var(--bz-ink)", letterSpacing:4, marginBottom:6 }}>BAZOOKA</div>
          <div style={{ fontSize:11, color:"var(--bz-ink-2)", fontStyle:"italic" }}>Bo Jackson Battle Arena · Lot Purchase Offer</div>
        </div>
        <div style={{ padding:"14px 24px", borderBottom:"1px solid var(--bz-line-2)", display:"grid", gridTemplateColumns:"1fr 1fr", background:"var(--bz-s1)" }}>
          <div><span style={{ color:"var(--bz-ink-2)", fontSize:11 }}>Prepared for: </span><strong>{seller.name||"--"}</strong></div>
          <div style={{ textAlign:"right" }}><span style={{ color:"var(--bz-ink-2)", fontSize:11 }}>Date: </span><strong>{seller.date||new Date().toLocaleDateString()}</strong></div>
        </div>
        <div style={{ padding:"8px 24px 0" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr>{["#","Card Name","Qty","Value/Card","Offer/Card"].map(h=><th key={h} style={{ padding:"8px 10px", borderBottom:"2px solid #F0E0E8", color:"var(--bz-ink-2)", fontSize:10, fontWeight:700, textTransform:"uppercase", textAlign:"left" }}>{h}</th>)}</tr></thead>
            <tbody>
              {included.length===0 ? <EmptyRow msg="No cards added." cols={5}/> :
                included.map((r,i) => {
                  const mv = parseFloat(r.mktVal)||0;
                  return (
                    <tr key={r.id} style={{ borderBottom:"1px solid #FFF0F5" }}>
                      <td style={{ padding:"8px 10px", color:"var(--bz-ink-2)", fontSize:11, width:32, textAlign:"center" }}>{i+1}</td>
                      <td style={{ padding:"8px 10px", fontWeight:700, color:"var(--bz-ink)" }}>{r.name}</td>
                      <td style={{ padding:"8px 10px", color:"var(--bz-ink-2)", textAlign:"center" }}>{parseInt(r.qty)||1}</td>
                      <td style={{ padding:"8px 10px", color:"var(--bz-ink-2)", fontWeight:600 }}>${mv.toFixed(2)}</td>
                      <td style={{ padding:"8px 10px", color:"#E8317A", fontWeight:700 }}>${(mv*dispPct).toFixed(2)}</td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        <div style={{ padding:"16px 24px", borderTop:"2px solid var(--bz-line-2)333", marginTop:8 }}>
          {/* Notes -- rendered read-only in the quote */}
          {custNote.trim() && (
            <div style={{ marginBottom:14, padding:"12px 16px", background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderLeft:"3px solid #E8317A", borderRadius:8 }}>
              <div style={{ fontSize:10, fontWeight:700, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1.5, marginBottom:6 }}>Notes</div>
              <p style={{ margin:0, fontSize:13, color:"var(--bz-ink-2)", lineHeight:1.6, whiteSpace:"pre-wrap" }}>{custNote}</p>
            </div>
          )}
          {[[`Total Cards`,totalCards],...(canSeeFinancials?[[`Total Market Value`,`$${totalMkt.toFixed(2)}`]]:[])] .map(([l,v]) => (
            <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid #FFF0F5" }}>
              <span style={{ color:"var(--bz-ink-2)", fontSize:13 }}>{l}</span>
              <span style={{ color:"var(--bz-ink)", fontWeight:700 }}>{v}</span>
            </div>
          ))}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12, padding:"14px 20px", background:"var(--bz-s1)", borderRadius:10 }}>
            <span style={{ color:"#E8317A", fontWeight:800, fontSize:16 }}>Bazooka's Offer</span>
            <span style={{ color:"var(--bz-ink)", fontWeight:900, fontSize:22 }}>${dispOffer.toFixed(2)}</span>
          </div>
          {/* Ship-to address */}
          <div style={{ marginTop:14, padding:"12px 16px", background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:8 }}>
            <div style={{ fontSize:10, fontWeight:700, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1.5, marginBottom:6 }}>Ship Cards To</div>
            <div style={{ fontSize:13, color:"var(--bz-ink)", fontWeight:700, lineHeight:1.8 }}>
              Devin -- Bazooka<br/>
              2150 N Pointe Dr<br/>
              Warsaw, IN 46582
            </div>
          </div>

          {/* Payment section -- shows when payment method + handle entered */}
          {seller.payment && seller.paymentHandle && (() => {
            const handle = seller.paymentHandle.trim();
            const amt    = dispOffer > 0 ? dispOffer.toFixed(2) : "";

            const paymentConfig = {
              PayPal: {
                color: "#003087",
                hint:  `To: ${handle}`,
                icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .761-.641h6.927c2.34 0 4.02.646 4.956 1.92.434.588.676 1.24.728 1.98.056.812-.07 1.766-.376 2.838-.79 2.764-2.723 4.168-5.745 4.168H9.87a.77.77 0 0 0-.761.641l-.87 5.49a.641.641 0 0 1-.633.54l-.53.001z" fill="#003087"/><path d="M19.612 8.2c-.056-.392-.163-.758-.32-1.094-.62 3.4-2.76 5.13-6.354 5.13H10.71l-1.04 6.567h2.197a.641.641 0 0 0 .633-.54l.87-5.49a.77.77 0 0 1 .761-.641h1.325c2.594 0 4.325-1.068 5.03-3.208.323-.98.37-1.822.126-2.724z" fill="#0070E0"/></svg>,
              },
              Zelle: {
                color: "#6D1ED4",
                hint:  `To: ${handle}`,
                icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect width="24" height="24" rx="5" fill="#6D1ED4"/><path d="M16.5 6H7.5L6 9h7.2L6 15h1.8L16.5 9v-.5L18 6h-1.5zm0 3h-7.2L16.5 15H18L16.5 9z" fill="white"/></svg>,
              },
            };

            const cfg = paymentConfig[seller.payment];
            if (!cfg) return null;

            return (
              <div style={{ marginTop:14, padding:"14px 16px", background:"var(--bz-s1)", border:`2px solid ${cfg.color}33`, borderRadius:10 }}>
                <div style={{ fontSize:10, fontWeight:700, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1.5, marginBottom:10 }}>
                  Payment -- <span style={{ color:cfg.color }}>{seller.payment}</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    {cfg.icon}
                    <div>
                      <div style={{ fontWeight:700, fontSize:14, color:cfg.color }}>{cfg.hint}</div>
                      {amt && <div style={{ fontSize:12, color:"var(--bz-ink-2)", marginTop:2 }}>Amount: <strong style={{color:"var(--bz-ink)"}}>${amt}</strong></div>}
                    </div>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:4, alignItems:"flex-end" }}>
                    <div style={{ background:cfg.color, color:"var(--bz-ink)", borderRadius:9, padding:"10px 20px", fontSize:13, fontWeight:800 }}>Open {seller.payment} App</div>
                    <div style={{ fontSize:11, color:"var(--bz-ink-2)" }}>Send to: <strong style={{color:"var(--bz-ink)"}}>{handle}</strong></div>
                  </div>
                </div>
              </div>
            );
          })()}

          <div style={{ marginTop:12, textAlign:"center", color:"var(--bz-ink-2)", fontSize:11, fontStyle:"italic" }}>This offer is valid for 7 days. Thank you for bringing your collection to Bazooka!</div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      {lightbox && createPortal(
        <div onClick={()=>setLightbox(null)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:2147483600, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", cursor:"zoom-out", gap:14 }}>
          <div style={{ color:"#fff", fontSize:13, fontWeight:700 }}>📷 Photo — click anywhere to close</div>
          <img src={lightbox} alt="Lot photo" style={{ maxWidth:"90vw", maxHeight:"80vh", objectFit:"contain", borderRadius:10, border:"2px solid rgba(255,255,255,0.15)", background:"var(--bz-s1)" }} onClick={e=>e.stopPropagation()} onError={e=>{ e.currentTarget.alt="⚠️ Image failed to load — use Open original below"; e.currentTarget.style.minWidth="300px"; e.currentTarget.style.minHeight="200px"; }}/>
          <a href={lightbox} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{ fontSize:13, color:"#7B9CFF", background:"rgba(0,0,0,0.6)", padding:"8px 18px", borderRadius:8, textDecoration:"none", fontWeight:700 }}>Open original in new tab ↗</a>
          <button onClick={()=>setLightbox(null)} style={{ position:"fixed", top:20, right:24, background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.2)", color:"#fff", borderRadius:"50%", width:40, height:40, fontSize:20, cursor:"pointer", fontFamily:"inherit", lineHeight:1 }}>✕</button>
        </div>, document.body
      )}
      <div style={S.card}>
        <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
          {[["builder","\uD83E\uDDEE Builder"],["quick","\u26A1 Quick Mode"],...(["Admin","Procurement"].includes(userRole?.role)?[["history","\uD83D\uDCCB History"]]:[] )].map(([mode,label]) => (
            <button key={mode} onClick={()=>setCompMode(mode)} style={{ background:compMode===mode?"#1A1A2E":"transparent", color:compMode===mode?"#E8317A":"var(--bz-ink-2)", border:`1.5px solid ${compMode===mode?"#E8317A":"var(--bz-line)"}`, borderRadius:8, padding:"6px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{label}</button>
          ))}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)", gap:isMobile?6:8, marginTop:12 }}>
          {[
            { z:"\uD83D\uDFE2 Green",  p:"Under 65%", a:"Buy independently",   bg:"#0a1a0a", c:"#4ade80" },
            { z:"\uD83D\uDFE1 Yellow", p:"65–70%",    a:"Flag before buying",   bg:"#FFF9DB", c:"#92400e" },
            { z:"\uD83D\uDD34 Red",    p:"Over 70%",  a:"Pass or get approval", bg:"#FEE2E2", c:"#991b1b" },
          ].map(({z,p,a,bg,c}) => (
            <div key={z} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 12px", background:bg, border:`1px solid ${c}22`, borderRadius:7, flexWrap:"wrap" }}>
              <span style={{ fontWeight:800, color:c, fontSize:12 }}>{z}</span>
              <span style={{ color:c, fontSize:11 }}>{p}</span>
              {!isMobile && <span style={{ color:"var(--bz-ink-2)", fontSize:11 }}>— {a}</span>}
            </div>
          ))}
        </div>
      </div>

      {compMode==="quick" && (
        <div style={S.card}>
          <SectionLabel t="Quick Lot Comp" />
          <p style={{ fontSize:12, color:"var(--bz-ink-2)", marginBottom:16 }}>Enter total cards + avg market value per card for an instant offer.</p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12, marginBottom:16 }}>
            <div><label style={S.lbl}>Total Cards</label><input type="number" value={quickCards} onChange={e=>setQuickCards(e.target.value)} placeholder="0" style={S.inp}/></div>
            <div><label style={S.lbl}>Avg Value/Card ($)</label><input type="number" value={quickMktVal} onChange={e=>setQuickMktVal(e.target.value)} placeholder="0.00" style={S.inp}/></div>
            <div><label style={S.lbl}>Buy % (blank=60%)</label><input type="number" value={quickPct} onChange={e=>setQuickPct(e.target.value)} placeholder="60" style={S.inp}/></div>
            <div><label style={S.lbl}>Your Final Offer ($)</label><input type="number" value={quickOffer} onChange={e=>setQuickOffer(e.target.value)} placeholder={quickCalcOffer.toFixed(2)} style={S.inp}/></div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:`repeat(${canSeeFinancials?4:2},1fr)`, gap:10 }}>
            {[
              ...(canSeeFinancials ? [
                { l:"Total Market Value", v:`$${quickTotal.toFixed(2)}`,     c:"#92400e" },
                { l:"Calculated Offer",   v:`$${quickCalcOffer.toFixed(2)}`, c:"#166534" },
              ] : []),
              { l:"Your Offer",  v:`$${quickOfferAmt.toFixed(2)}`,  c:"#6B2D8B" },
              { l:"Lot Zone",    v:quickZone?quickZone.label:"--",   c:quickZone?.color||"var(--bz-ink-2)" },
            ].map(({l,v,c}) => (
              <div key={l} style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"12px", textAlign:"center" }}>
                <div style={{ fontSize:18, fontWeight:900, color:c, marginBottom:4 }}>{v}</div>
                <div style={{ fontSize:10, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      )}


      {compMode==="history" && (() => {
        const activeQuotes = quotes.filter(q => showClosedQuotes ? true : !q.bazookaClosed);

        return (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ display:"flex", justifyContent:"flex-end" }}>
            <button onClick={()=>setShowClosedQuotes(v=>!v)} style={{ background: showClosedQuotes?"rgba(123,156,255,0.12)":"none", border:"1px solid var(--bz-line-2)", color: showClosedQuotes?"#7B9CFF":"var(--bz-ink-3)", borderRadius:8, padding:"6px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              {showClosedQuotes ? "\u2713 Showing closed quotes" : "Show closed quotes"}
            </button>
          </div>

          {/* -- ACTIVE QUOTES -- */}
          {activeQuotes.length > 0 && (
            <div style={{ ...S.card, border:"2px solid rgba(232,49,122,0.3)" }}>
              <SectionLabel t="🔗 Active Quote Links" />
              {(() => {
                // Running total of dollars committed across all active quotes — "if everyone says yes"
                const offerOf = q => parseFloat((q.status==="countered" ? q.sellerCounter : q.currentOffer) || q.currentOffer || q.dispOffer || 0) || 0;
                const open = activeQuotes.filter(q => q.status==="pending" || q.status==="countered");
                const accepted = activeQuotes.filter(q => q.status==="accepted");
                const openTotal = open.reduce((s,q)=>s+offerOf(q),0);
                const acceptedTotal = accepted.reduce((s,q)=>s+offerOf(q),0);
                const maxExposure = openTotal + acceptedTotal;
                const fmt = n => "$"+n.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2});
                return (
                  <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:12 }}>
                    <div style={{ flex:1, minWidth:150, background:"rgba(232,49,122,0.08)", border:"1px solid rgba(232,49,122,0.3)", borderRadius:10, padding:"10px 14px" }}>
                      <div style={{ fontSize:10, fontWeight:800, color:"#E8317A", textTransform:"uppercase", letterSpacing:1 }}>Max if all accept</div>
                      <div style={{ fontSize:22, fontWeight:900, color:"#fff", lineHeight:1.1 }}>{fmt(maxExposure)}</div>
                      <div style={{ fontSize:10, color:"#a0a0a0", marginTop:2 }}>{activeQuotes.length} active quote{activeQuotes.length!==1?"s":""}</div>
                    </div>
                    <div style={{ flex:1, minWidth:130, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:10, padding:"10px 14px" }}>
                      <div style={{ fontSize:10, fontWeight:800, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1 }}>⏳ Awaiting</div>
                      <div style={{ fontSize:18, fontWeight:900, color:"#ccc", lineHeight:1.1 }}>{fmt(openTotal)}</div>
                      <div style={{ fontSize:10, color:"#a0a0a0", marginTop:2 }}>{open.length} out for response</div>
                    </div>
                    {accepted.length>0 && (
                      <div style={{ flex:1, minWidth:130, background:"rgba(74,222,128,0.06)", border:"1px solid rgba(74,222,128,0.25)", borderRadius:10, padding:"10px 14px" }}>
                        <div style={{ fontSize:10, fontWeight:800, color:"#4ade80", textTransform:"uppercase", letterSpacing:1 }}>✅ Accepted</div>
                        <div style={{ fontSize:18, fontWeight:900, color:"#4ade80", lineHeight:1.1 }}>{fmt(acceptedTotal)}</div>
                        <div style={{ fontSize:10, color:"#a0a0a0", marginTop:2 }}>{accepted.length} committed to buy</div>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {activeQuotes.map(q => {
                  // Auto-dismiss badge when admin views a responded quote
                  // Note: notifications are dismissed from the Dashboard, not auto-dismissed here
                  const statusCfg = {
                    pending:   { color:"var(--bz-ink-2)",    bg:"var(--bz-s2)",  label:"\u23F3 Awaiting Response" },
                    countered: { color:"#FBBF24", bg:"#1a1400",  label:"\uD83E\uDD1D Counter Received" },
                    accepted:  { color:"#4ade80", bg:"#0a1a0a",  label:"\u2705 Accepted" },
                    declined:  { color:"#E8317A", bg:"#1a0a0a",  label:"\u274C Declined" },
                  }[q.status] || { color:"var(--bz-ink-2)", bg:"var(--bz-s2)", label:q.status };

                  const quoteUrl = `${window.location.origin}/quote/${q.id}`;
                  const expiresAt = q.expiresAt ? new Date(q.expiresAt) : new Date(new Date(q.createdAt).getTime()+7*24*60*60*1000);
                  const daysLeft = q.neverExpires ? Infinity : Math.max(0,Math.ceil((expiresAt-new Date())/86400000));
                  const isExpiredRow = !q.neverExpires && daysLeft <= 0;

                  return (
                    <div key={q.id} style={{ background:statusCfg.bg, border:`1px solid ${statusCfg.color}33`, borderRadius:10, padding:"14px 16px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10, flexWrap:"wrap", gap:8 }}>
                        <div>
                          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
                            <span style={{ fontWeight:800, fontSize:14, color:"var(--bz-ink)" }}>{q.seller?.name||"Unknown Seller"}</span>
                            <span style={{ background:statusCfg.bg, color:statusCfg.color, border:`1px solid ${statusCfg.color}44`, borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700 }}>{statusCfg.label}</span>
                            {q.quoteRef && <span style={{ fontSize:11, fontWeight:700, color:"#7B9CFF", background:"rgba(123,156,255,0.08)", border:"1px solid rgba(123,156,255,0.2)", borderRadius:6, padding:"2px 8px", letterSpacing:0.5 }}>{q.quoteRef}</span>}
                          </div>
                          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4, flexWrap:"wrap" }}>
                            <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>
                              Quoted by <strong style={{ color:"var(--bz-ink-2)" }}>{q.quotedBy?.split(" ")[0] || "Unknown"}</strong>
                            </span>
                            {q.lastUpdatedBy && q.lastUpdatedBy !== q.quotedBy && (
                              <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>· Updated by <strong style={{ color:"var(--bz-ink-2)" }}>{q.lastUpdatedBy.split(" ")[0]}</strong></span>
                            )}
                            {q.createdAt && <span style={{ fontSize:11, color:"#8a8a8a" }}>· {new Date(q.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric"})}</span>}
                          </div>
                          <div style={{ fontSize:11, color:"#999" }}>
                            {q.cards?.length||0} cards · Offer: <strong style={{color:"#E8317A"}}>${parseFloat(q.currentOffer||q.dispOffer||0).toFixed(2)}</strong>
                            {q.status==="countered" && <> · Counter: <strong style={{color:"#FBBF24"}}>${parseFloat(q.sellerCounter||0).toFixed(2)}</strong></>}
                            {q.status==="accepted" && q.sellerPayment && <> · Payment: <strong style={{color:"#4ade80"}}>{q.sellerPayment}{q.sellerHandle?` -- ${q.sellerHandle}`:""}</strong></>}
                            &nbsp;· {q.neverExpires ? <span style={{color:"#4ade80",fontWeight:700}}>{"\u267E\uFE0F"} Never expires</span> : isExpiredRow ? <span style={{color:"#E8317A",fontWeight:700}}>Expired</span> : `${daysLeft}d left`}
                          </div>
                          {/* Lot photos */}
                          {(q.photoUrls||[]).length > 0 && (
                            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:6 }}>
                              {(q.photoUrls||[]).map((url,i)=>(
                                <img key={i} src={url} alt={`Lot photo ${i+1}`}
                                  onClick={(e)=>{ e.stopPropagation(); try { setLightbox(url); } catch(err) { window.open(url,"_blank"); } }}
                                  style={{ width:60, height:60, objectFit:"cover", borderRadius:6, border:"1px solid var(--bz-line-2)", cursor:"zoom-in" }}/>
                              ))}
                              <div style={{ fontSize:10, color:"var(--bz-ink-3)", alignSelf:"center" }}>📸 {(q.photoUrls||[]).length} photo{(q.photoUrls||[]).length!==1?"s":""} · click to enlarge</div>
                            </div>
                          )}
                          {/* View tracking */}
                          <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:4 }}>
                            {(q.viewCount||0) === 0
                              ? <span style={{ fontSize:11, color:"#8a8a8a" }}>{"\uD83D\uDC41 Not opened yet"}</span>
                              : <span style={{ fontSize:11, color:(q.viewCount||0)>=5?"#FBBF24":"#7B9CFF", fontWeight:700 }}>
                                  {"\uD83D\uDC41 Opened "}{q.viewCount} time{q.viewCount!==1?"s":""}
                                  {q.lastViewedAt && <span style={{color:"var(--bz-ink-3)",fontWeight:400}}> · Last: {new Date(q.lastViewedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}</span>}
                                  {(q.viewCount||0)>=5 && <span style={{color:"#FBBF24",marginLeft:6}}>{"\uD83D\uDD25 Interested!"}</span>}
                                </span>
                            }
                          </div>
                        </div>
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                          <button onClick={()=>{ navigator.clipboard?.writeText(quoteUrl); }} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:7, padding:"4px 10px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDCCB Copy Link"}</button>
                          <a href={quoteUrl} target="_blank" rel="noreferrer" style={{ background:"none", border:"1px solid #E8317A44", color:"#E8317A", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, textDecoration:"none" }}>{"View \u2197"}</a>
                          {parseFloat(q.currentOffer||q.dispOffer) > 0 && (
                            <button onClick={()=>{
                              const msg = `Hey${q.seller?.name?" "+q.seller.name:""}! We reviewed your cards and have an offer ready. Check it out here and let us know what you think:\n\n${quoteUrl}`;
                              navigator.clipboard?.writeText(msg);
                            }} style={{ background:"rgba(74,222,128,0.12)", border:"1px solid rgba(74,222,128,0.3)", color:"#4ade80", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"📤 Send Offer to Seller"}</button>
                          )}
                          <button onClick={()=>{
                            const cards = (q.cards||[]).map(c=>({ name:c.cardName||c.name||"", cardType:c.cardType||"", mktVal:c.mktVal||c.marketValue||0, qty:c.qty||1 }));
                            const totalMv = cards.reduce((s,c)=>(s+(parseFloat(c.mktVal)||0)*(parseInt(c.qty)||1)),0);
                            const offer = parseFloat(q.currentOffer||q.dispOffer)||0;
                            const pct = totalMv > 0 ? ((offer/totalMv)*100).toFixed(1) : "60";
                            loadComp({ id:q.id, seller:q.seller?.name||"", contact:q.seller?.contact||"", date:q.seller?.date||"", source:q.seller?.source||"", payment:q.seller?.payment||"", paymentHandle:q.seller?.paymentHandle||"", offer:0, cards });
                            setLotPct(pct);
                          }} style={{ background:"rgba(123,156,255,0.1)", border:"1px solid rgba(123,156,255,0.3)", color:"#7B9CFF", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\u270F\uFE0F Edit in Builder"}</button>
                          {onReopenQuote && (isExpiredRow || q.bazookaClosed) && <button onClick={()=>onReopenQuote(q.id, false)} style={{ background:"rgba(74,222,128,0.1)", border:"1px solid rgba(74,222,128,0.4)", color:"#4ade80", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\u21BA Reopen 7 days"}</button>}
                          {onReopenQuote && !q.neverExpires && <button onClick={()=>{ if(window.confirm("Make this link never expire? The seller will be able to open it indefinitely.")) onReopenQuote(q.id, true); }} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:7, padding:"4px 10px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{"\u267E\uFE0F Never expire"}</button>}
                          {onCloseQuote && <button onClick={()=>{ if(window.confirm("Close this quote? The seller's link will show as expired.")) onCloseQuote(q.id); }} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:7, padding:"4px 10px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDD12 Close"}</button>}
                        </div>
                      </div>

                      {/* Counter response UI */}
                      {q.status==="countered" && (
                        <div style={{ borderTop:"1px solid var(--bz-line-2)", paddingTop:10, display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap" }}>
                          <div style={{ flex:1, minWidth:160 }}>
                            <label style={{ fontSize:10, fontWeight:700, color:"#a0a0a0", textTransform:"uppercase", letterSpacing:1.5, display:"block", marginBottom:6 }}>Your Counter Back ($)</label>
                            <input
                              type="number" step="0.01" min="0"
                              value={bzCounterAmt[q.id]||""}
                              onChange={e=>setBzCounterAmt(p=>({...p,[q.id]:e.target.value}))}
                              placeholder={`Their counter: $${parseFloat(q.sellerCounter||0).toFixed(2)}`}
                              style={{ ...S.inp, color:"#FBBF24" }}
                            />
                            {/* Live % preview */}
                            {bzCounterAmt[q.id] && parseFloat(bzCounterAmt[q.id]) > 0 && (() => {
                              const totalMkt = (q.cards||[]).reduce((s,c)=>(s+(parseFloat(c.mktVal)||0)*(parseInt(c.qty)||1)),0);
                              const counterVal = parseFloat(bzCounterAmt[q.id]);
                              const pct = totalMkt > 0 ? (counterVal/totalMkt)*100 : null;
                              const origOffer = parseFloat(q.dispOffer||0);
                              const origPct = totalMkt > 0 ? (origOffer/totalMkt)*100 : null;
                              const sellerPct = totalMkt > 0 ? (parseFloat(q.sellerCounter||0)/totalMkt)*100 : null;
                              const zone = pct < 65 ? {c:"#4ade80",l:"\uD83D\uDFE2 Green Zone"} : pct < 70 ? {c:"#FBBF24",l:"\uD83D\uDFE1 Yellow Zone"} : {c:"#E8317A",l:"\uD83D\uDD34 Red Zone"};
                              return (
                                <div style={{ marginTop:8, display:"flex", gap:8, flexWrap:"wrap" }}>
                                  <div style={{ background:"var(--bz-s2)", border:`1px solid ${zone.c}44`, borderRadius:7, padding:"5px 10px", fontSize:11 }}>
                                    <span style={{ color:"#999" }}>Your counter: </span>
                                    <strong style={{ color:zone.c }}>{pct?pct.toFixed(1)+"%":"--"}</strong>
                                    <span style={{ color:"var(--bz-ink-3)", marginLeft:4 }}>{zone.l}</span>
                                  </div>
                                  {origPct && <div style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:7, padding:"5px 10px", fontSize:11 }}>
                                    <span style={{ color:"#999" }}>Our offer: </span>
                                    <strong style={{ color:"#E8317A" }}>{origPct.toFixed(1)}%</strong>
                                  </div>}
                                  {sellerPct && <div style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:7, padding:"5px 10px", fontSize:11 }}>
                                    <span style={{ color:"#999" }}>Their ask: </span>
                                    <strong style={{ color:"#FBBF24" }}>{sellerPct.toFixed(1)}%</strong>
                                  </div>}
                                </div>
                              );
                            })()}
                          </div>
                          <Btn onClick={()=>{ if(onBazookaCounter&&bzCounterAmt[q.id]) { onBazookaCounter(q.id,parseFloat(bzCounterAmt[q.id]),q.history||[]); setBzCounterAmt(p=>({...p,[q.id]:""})); }}} disabled={!bzCounterAmt[q.id]} variant="ghost">{"\uD83E\uDD1D Send Counter"}</Btn>
                          <Btn onClick={()=>{ if(onCloseQuote) onCloseQuote(q.id); }} variant="ghost">{"\u274C Close (admin only)"}</Btn>
                          {q.status==="countered" && (
                            <Btn onClick={async()=>{
                              // Accept their counter -- update offer to their counter amount
                              if(onBazookaCounter) {
                                await setDoc(doc(db,"quotes",q.id),{ status:"pending", currentOffer:parseFloat(q.sellerCounter), history:[...(q.history||[]),{type:"bazooka_accepted_counter",amount:parseFloat(q.sellerCounter),timestamp:new Date().toISOString()}], notified:false },{ merge:true });
                                showToast?.(`\u2705 Accepted counter at $${parseFloat(q.sellerCounter).toFixed(2)}`);
                              }
                            }} variant="green">{"\u2705 Accept Their Counter"}</Btn>
                          )}
                        </div>
                      )}

                      {/* Accepted -- import prompt */}
                      {q.status==="accepted" && (
                        <div style={{ borderTop:"1px solid var(--bz-line-2)", paddingTop:10, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
                          <span style={{ fontSize:12, color:"#4ade80" }}>{"\uD83C\uDF89 Seller accepted! Ready to import into inventory."}</span>
                          <button onClick={()=>{ loadComp({ seller:q.seller?.name, contact:q.seller?.contact, date:q.seller?.date, source:q.seller?.source, payment:q.sellerPayment, paymentHandle:q.sellerHandle, cards:q.cards, offer:parseFloat(q.currentOffer||q.dispOffer), id:q.id }); setCompMode("builder"); }} style={{ background:"#166534", color:"#fff", border:"none", borderRadius:8, padding:"7px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDCE5 Load into Builder & Import"}</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* -- SAVED COMPS -- */}
          {(!comps||comps.length===0)
            ? <div style={{ ...S.card, textAlign:"center", padding:"60px", color:"var(--bz-ink-2)" }}>No comps saved yet.</div>
            : comps.map(c => {
                const z = getZone(c.blendedPct);
                const savedByRole = Object.entries(ROLES).find(([k]) => (c.savedBy||"").toLowerCase().includes(k))?.[1];
                const savedAt = c.dateAdded ? new Date(c.dateAdded).toLocaleString() : c.date;
                return (
                  <div key={c.id} style={{ ...S.card, border:`1px solid ${z?.color||"#F0D0DC"}33` }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                      <div>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                          <span style={{ fontWeight:800, fontSize:15, color:"var(--bz-ink)" }}>{c.seller||"Unknown Seller"}</span>
                          <span style={{ background:c.status==="accepted"?"#D6F4E3":c.status==="passed"?"#FEE2E2":"#FFF9DB", color:c.status==="accepted"?"#166534":c.status==="passed"?"#991b1b":"#92400e", borderRadius:5, padding:"2px 8px", fontSize:11, fontWeight:700 }}>
                            {c.status==="accepted"?"\u2705 Accepted":c.status==="passed"?"\u274C Passed":"\uD83D\uDCBE Saved"}
                          </span>
                          {z && canSeeFinancials && <span style={{ background:z.bg, color:z.color, borderRadius:5, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{z.label}</span>}
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                          <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>Saved by</span>
                          <span style={{ fontWeight:700, fontSize:12, color:"var(--bz-ink)" }}>{c.savedBy||"--"}</span>
                          {savedByRole && <span style={{ background:savedByRole.bg, color:savedByRole.color, border:`1px solid ${savedByRole.color}33`, borderRadius:10, padding:"1px 7px", fontSize:10, fontWeight:700 }}>{savedByRole.label}</span>}
                          {c.quotedBy && c.quotedBy !== c.savedBy && <>
                            <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>·</span>
                            <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>Quoted by <strong style={{ color:"var(--bz-ink-2)" }}>{c.quotedBy?.split(" ")[0] || "Unknown"}</strong></span>
                          </>}
                          <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>·</span>
                          <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{savedAt}</span>
                          {c.quoteRef && <span style={{ fontSize:11, fontWeight:700, color:"#7B9CFF", background:"rgba(123,156,255,0.08)", border:"1px solid rgba(123,156,255,0.2)", borderRadius:6, padding:"2px 8px", letterSpacing:0.5 }}>{c.quoteRef}</span>}
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
                        <button onClick={()=>loadComp(c)} style={{ background:"#1A1A2E", color:"#E8317A", border:"1.5px solid #E8317A", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDCE5 Load into Builder"}</button>
                        {CAN_DELETE.includes(userRole?.role) && <button onClick={()=>{ if(window.confirm(`Delete this comp from history?\n\nSaved by: ${c.savedBy||"Unknown"}\nSeller: ${c.seller||"Unknown"}\n\nThis action will be logged.`)) onDeleteComp(c.id); }} style={{ background:"var(--bz-s1)", color:"#E8317A", border:"1.5px solid #fca5a5", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDDD1"}</button>}
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:16, flexWrap:"wrap", paddingTop:8, borderTop:"1px solid #FFF0F5" }}>
                      <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Cards: <strong style={{color:"var(--bz-ink)"}}>{c.totalCards}</strong></span>
                      {canSeeFinancials && <>
                        <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Market: <strong style={{color:"var(--bz-ink-2)"}}>${(c.totalMarket||0).toFixed(2)}</strong></span>
                        <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Offer: <strong style={{color:"#E8317A"}}>${(c.offer||0).toFixed(2)}</strong></span>
                        <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Blended: <strong style={{color:z?.color||"var(--bz-ink)"}}>{((c.blendedPct||0)*100).toFixed(1)}%</strong></span>
                      </>}
                      <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Source: <strong style={{color:"var(--bz-ink)"}}>{c.source||"--"}</strong></span>
                      <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>
                        {c.cards&&c.cards.length>0 ? <span style={{color:"#E8317A",fontWeight:700}}>{"\u2713"}{c.cards.length} card{c.cards.length!==1?"s":""} saved</span> : <span style={{color:"var(--bz-ink-2)",fontWeight:700}}>{"\u26A0 No card details"}</span>}
                      </span>
                    </div>
                  </div>
                );
              })
          }
        </div>
        );
      })()}

      {compMode==="builder" && <>
        <div className="lot-comp-grid" style={{ display:"grid", gridTemplateColumns: seller.name ? "1fr 300px" : "1fr", gap:14, alignItems:"start" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        {loadedCompId && (
          <div id="comp-builder-top" style={{ background: loadedCompHadCards ? "#D6F4E3" : "#FFF9DB", border: `1.5px solid ${loadedCompHadCards ? "#2E7D52" : "#92400e"}`, borderRadius:10, padding:"12px 18px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ fontSize:18 }}>{loadedCompHadCards ? "\u2705" : "\u26A0\uFE0F"}</span>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color: loadedCompHadCards ? "#166534" : "#92400e" }}>
                  {loadedCompHadCards ? "Comp loaded -- ready to edit and import" : "Comp loaded -- no card data saved"}
                </div>
                <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginTop:2 }}>
                  {loadedCompHadCards ? "All seller info, cards, and offer amount restored. Hit Accept & Import to add to inventory." : "Seller info and offer restored, but this comp was saved without per-card details. Add cards manually below."}
                </div>
              </div>
            </div>
            <button onClick={()=>setLoadedCompId(null)} style={{ background:"transparent", border:"none", color:"var(--bz-ink-2)", cursor:"pointer", fontSize:18, lineHeight:1 }}>{"\u2715"}</button>
          </div>
        )}
        <div id="comp-builder-top" style={S.card}>
          <SectionLabel t="Seller Information" />
          <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr 1fr", gap:12 }}>
            {/* Seller Name with autocomplete from history */}
            {(() => {
              const prevSellers = [...new Map(
                [...comps].reverse()
                  .filter(c => c.seller)
                  .map(c => [c.seller.toLowerCase(), c])
              ).values()].slice(0, 50);

              const q = (seller.name||"").toLowerCase();
              const suggestions = q.length >= 1
                ? prevSellers.filter(c => c.seller.toLowerCase().includes(q) && c.seller.toLowerCase() !== q)
                : [];

              function fillSeller(comp) {
                setSeller(p => ({
                  ...p,
                  name: comp.seller||"",
                  contact: comp.contact||p.contact,
                  source: comp.source||p.source,
                  payment: comp.payment||p.payment,
                  paymentHandle: comp.paymentHandle||p.paymentHandle,
                }));
              }

              return (
                <Field label="Seller Name">
                  <div style={{ position:"relative" }}>
                    <input
                      type="text"
                      value={seller.name}
                      onChange={e => setSeller(p=>({...p, name:e.target.value}))}
                      placeholder="Seller name..."
                      style={S.inp}
                      autoComplete="off"
                    />
                    {suggestions.length > 0 && (
                      <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:8, zIndex:999, overflow:"hidden", boxShadow:"0 8px 24px rgba(0,0,0,0.6)", maxHeight:240, overflowY:"auto" }}>
                        {suggestions.map((c,i) => {
                          const lastComp = comps.filter(x=>x.seller===c.seller).slice(-1)[0];
                          const totalBought = comps.filter(x=>x.seller===c.seller).reduce((s,x)=>s+(parseFloat(x.offer)||0),0);
                          const dealCount = comps.filter(x=>x.seller===c.seller).length;
                          return (
                            <div key={i} onMouseDown={()=>fillSeller(c)}
                              style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", borderBottom:"1px solid #111", cursor:"pointer", background:"var(--bz-s2)" }}
                              className="inv-row">
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)" }}>{c.seller}</div>
                                <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:1 }}>
                                  {dealCount} deal{dealCount!==1?"s":""} · ${Math.round(totalBought).toLocaleString()} total
                                  {c.source && <span style={{ marginLeft:6, color:"#7B9CFF" }}>{c.source}</span>}
                                </div>
                              </div>
                              <div style={{ textAlign:"right", flexShrink:0 }}>
                                {c.payment && <div style={{ fontSize:10, color:"#FBBF24" }}>{c.payment}</div>}
                                {lastComp?.date && <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>Last: {lastComp.date}</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </Field>
              );
            })()}
            <TextInput label="Contact"          value={seller.contact} onChange={v=>setSeller(p=>({...p,contact:v}))} />
            <TextInput label="Date" type="date" value={seller.date}    onChange={v=>setSeller(p=>({...p,date:v}))} />
            <SelectInput label="Payment Method" value={seller.payment} onChange={v=>setSeller(p=>({...p,payment:v,paymentHandle:""}))} options={PAYMENT_METHODS} />
            <TextInput
              label={seller.payment==="PayPal" ? "PayPal Username / Email" : seller.payment==="Zelle" ? "Zelle Email or Phone" : "Payment Handle / Info"}
              value={seller.paymentHandle}
              onChange={v=>setSeller(p=>({...p,paymentHandle:v}))}
              placeholder={seller.payment==="Venmo" ? "@theirhandle" : seller.payment==="PayPal" ? "username or email" : seller.payment==="Zelle" ? "email or phone" : "handle or account info"}
            />
            <SelectInput label="Source"         value={seller.source}  onChange={v=>setSeller(p=>({...p,source:v}))}  options={SOURCES} />
          </div>
        </div>

        {/* ── STICKY OFFER SUMMARY BAR ── */}
        <div style={{ position:"sticky", top:64, zIndex:200, background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:14, padding:"12px 16px", marginBottom:12, backdropFilter:"blur(12px)" }}>
          <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":"repeat(5,1fr)", gap:10, alignItems:"end" }}>

            {/* Buy % */}
            <div>
              <label style={{ ...S.lbl, color:"#E8317A" }}>Buy % of Market</label>
              <div style={{ position:"relative" }}>
                <input type="text" inputMode="decimal" value={lotPct}
                  onChange={e => { setLotPct(e.target.value); setFOffer(""); }}
                  placeholder="60"
                  style={{ ...S.inp, fontWeight:800, color:"#E8317A", fontSize:15, paddingRight:28, border:"1px solid rgba(232,49,122,0.3)" }}/>
                <span style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", fontSize:13, color:"var(--bz-ink-3)" }}>%</span>
              </div>
            </div>

            {/* Override $ */}
            <div>
              <label style={S.lbl}>Override Offer ($)</label>
              <input type="text" inputMode="decimal" value={finalOffer}
                onChange={e => { setFOffer(e.target.value); setLotPct(""); }}
                placeholder={totalMkt > 0 ? calcOffer.toFixed(2) : "0.00"}
                style={{ ...S.inp, fontWeight:700, color:(offerAmt!=null&&offerAmt>0)?"#E8317A":"var(--bz-ink-2)", border:(offerAmt!=null&&offerAmt>0)?"1.5px solid rgba(232,49,122,0.5)":"1px solid var(--bz-line-2)" }}/>
            </div>

            {/* Active Offer — big */}
            <div>
              <label style={S.lbl}>Active Offer</label>
              <div style={{ background:"rgba(232,49,122,0.08)", border:"1.5px solid rgba(232,49,122,0.25)", borderRadius:8, padding:"8px 14px", display:"flex", alignItems:"baseline", justifyContent:"space-between" }}>
                <span style={{ fontSize:20, fontWeight:900, color:"#E8317A" }}>${dispOffer.toFixed(2)}</span>
                <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{(dispPct*100).toFixed(0)}%</span>
              </div>
            </div>

            {/* Market value */}
            <div>
              <label style={S.lbl}>Total Market Value</label>
              <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:8, padding:"8px 14px", display:"flex", alignItems:"baseline", justifyContent:"space-between" }}>
                <span style={{ fontSize:16, fontWeight:700, color:"var(--bz-ink-2)" }}>{totalMkt>0?`$${totalMkt.toFixed(2)}`:"—"}</span>
                {totalMkt>0&&<span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{totalCards} card{totalCards!==1?"s":""}</span>}
              </div>
            </div>

            {/* Zone — big color chip */}
            {(() => {
              const z = lotZone;
              const ZONES = {
                "🟢 Green":  { bg:"rgba(74,222,128,0.1)",  border:"rgba(74,222,128,0.3)",  text:"#4ade80",  label:"Slam Dunk",    sub:"Under 65%" },
                "🟡 Yellow": { bg:"rgba(251,191,36,0.1)",  border:"rgba(251,191,36,0.3)",  text:"#FBBF24",  label:"Fair Deal",    sub:"65–70%" },
                "🔴 Red":    { bg:"rgba(239,68,68,0.1)",   border:"rgba(239,68,68,0.3)",   text:"#ef4444",  label:"Stretch",      sub:"Over 70%" },
              };
              const zc = z ? ZONES[z.label] : null;
              return (
                <div>
                  <label style={S.lbl}>Zone</label>
                  <div style={{ background: zc?.bg||"var(--bz-s1)", border:`1.5px solid ${zc?.border||"var(--bz-line-2)"}`, borderRadius:8, padding:"6px 14px", textAlign:"center" }}>
                    {zc ? (
                      <>
                        <div style={{ fontSize:14, fontWeight:900, color:zc.text }}>{zc.label}</div>
                        <div style={{ fontSize:10, color:zc.text, opacity:0.7 }}>{zc.sub} of market</div>
                      </>
                    ) : (
                      <div style={{ fontSize:12, color:"var(--bz-ink-3)" }}>{totalMkt>0?"—":"Add cards first"}</div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Est margin row */}
          {dispOffer>0 && totalMkt>0 && (
            <div style={{ display:"flex", gap:16, marginTop:8, paddingTop:8, borderTop:"1px solid var(--bz-line)", flexWrap:"wrap" }}>
              <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>Margin <strong style={{ color:"#4ade80" }}>${(totalMkt-dispOffer).toFixed(2)}</strong></span>
              <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>Per card <strong style={{ color:"var(--bz-ink-2)" }}>{totalCards>0?"$"+(dispOffer/totalCards).toFixed(2):"—"}</strong></span>
              {(offerAmt!=null&&offerAmt>0) && <button onClick={()=>setFOffer("")} style={{ background:"none", border:"none", color:"#E8317A", cursor:"pointer", fontSize:11, fontFamily:"inherit", textDecoration:"underline" }}>Clear override</button>}
              {Math.abs(allocationDiff) >= 0.01 && <span style={{ fontSize:11, fontWeight:700, color:allocationDiff>0?"#ef4444":"#FBBF24" }}>{allocationDiff>0?`⚠ $${allocationDiff.toFixed(2)} over offer`:`$${Math.abs(allocationDiff).toFixed(2)} unallocated`}</span>}
              {Math.abs(allocationDiff) < 0.01 && included.length>0 && <span style={{ fontSize:11, color:"#4ade80" }}>✓ Perfectly balanced</span>}
            </div>
          )}
        </div>

        <div style={S.card}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
            <SectionLabel t="Cards in This Lot" />
            <label style={{ background:"rgba(123,156,255,0.08)", border:"1px solid rgba(123,156,255,0.3)", color:"#7B9CFF", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
              {importing ? "⏳ Importing..." : "📂 Import CSV / XLSX"}
              <input type="file" accept=".csv,.xlsx,.xls" style={{ display:"none" }} disabled={importing} onChange={importFromFile}/>
            </label>
          </div>
          {isMobile ? (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {rows.map((r,i) => {
                const mv  = parseFloat(r.mktVal)||0;
                const qty = parseInt(r.qty)||1;
                const perCardOffer = totalMkt > 0 ? mv * dispPct : (totalCards > 0 ? dispOffer / totalCards : 0);
                const mInp = { background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:8, color:"var(--bz-ink)", padding:"10px 12px", fontSize:16, fontFamily:"inherit", outline:"none", width:"100%", boxSizing:"border-box" };
                return (
                  <div key={r.id} style={{ background:r.include?"var(--bz-s1)":"var(--bz-bg)", border:`1.5px solid ${r.include?"var(--bz-line-2)":"var(--bz-s2)"}`, borderRadius:10, padding:"12px", opacity:r.include?1:0.5 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                      <span style={{ fontSize:12, fontWeight:700, color:"var(--bz-ink-3)" }}>#{i+1}</span>
                      <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"var(--bz-ink-2)", cursor:"pointer" }}>
                        <input type="checkbox" checked={r.include} onChange={e=>upd(r.id,"include",e.target.checked)}/> Include
                      </label>
                    </div>
                    {/* Card Name */}
                    {POOL_TYPES.includes(r.cardType) ? (
                      <div style={{marginBottom:8}}>
                        {cardPools.filter(p=>p.cardType===r.cardType).length > 0 && !r.manualEntry ? (
                          <select value={r.name} onChange={e=>{
                            if (e.target.value==="__manual__") {
                              setRows(p=>p.map(x=>x.id===r.id?{...x,name:"",manualEntry:true}:x));
                            } else {
                              upd(r.id,"name",e.target.value);
                            }
                          }} style={{ ...mInp, cursor:"pointer" }}>
                            <option value="">-- Select Pool or Treatment --</option>
                            <optgroup label="Your Pools">
                              {cardPools.filter(p=>p.cardType===r.cardType).map(p=>(
                                <option key={p.id} value={p.cardName}>{p.cardName} ({(parseInt(p.totalQty)||0)-(parseInt(p.usedQty)||0)} avail)</option>
                              ))}
                            </optgroup>
                            <optgroup label="All Treatments">
                              {[...new Set(bobaCards.map(c=>c.treatment).filter(Boolean))].sort().map(t=>(
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </optgroup>
                            <option value="__manual__">✏️ Type manually instead...</option>
                          </select>
                        ) : (
                          <div style={{display:"flex",gap:6,alignItems:"center"}}>
                            <div style={{position:"relative",flex:1}}>
                              <input
                                value={acOpen===r.id?(acQuery[r.id]??r.name):r.name}
                                onChange={e=>{setAcOpen(r.id);setAcQuery(q=>({...q,[r.id]:e.target.value}));upd(r.id,"name",e.target.value);}}
                                onFocus={()=>{setAcOpen(r.id);setAcQuery(q=>({...q,[r.id]:r.name}));}}
                                onBlur={()=>setTimeout(()=>setAcOpen(p=>p===r.id?null:p),150)}
                                placeholder="Type hero name or card #..."
                                style={{...mInp}}
                              />
                              {acOpen===r.id&&(acQuery[r.id]||"").length>=1&&(()=>{
                                const raw=(acQuery[r.id]||"").toLowerCase();
                                const terms=raw.trim().split(/\s+/).filter(Boolean);
                                const hits=bobaCards.filter(c=>terms.every(t=>[c.hero||"",c.weapon||"",c.treatment||"",String(c.cardNum||""),c.notation||"",c.setName||""].join(" ").toLowerCase().includes(t))).slice(0,12);
                                if(!hits.length) return null;
                                return (
                                  <div style={{position:"absolute",top:"100%",left:0,right:0,background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,zIndex:999,boxShadow:"0 8px 24px rgba(0,0,0,0.8)",maxHeight:280,overflowY:"auto"}}>
                                    <div style={{padding:"4px 10px",fontSize:10,color:"var(--bz-ink-3)",borderBottom:"1px solid #111"}}>{hits.length} match{hits.length!==1?"es":""}</div>
                                    {hits.map(c=>{
                                      const wc=PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
                                      const label=[c.hero,c.treatment,c.weapon?"("+c.weapon+")":"",c.cardNum?"#"+c.cardNum:""].filter(Boolean).join(" — ");
                                      return (
                                        <div key={c.id} onMouseDown={()=>{upd(r.id,"name",label);if(c.mktValue||c.marketValue)upd(r.id,"mktVal",String(c.mktValue||c.marketValue||""));setAcOpen(null);}}
                                          style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",cursor:"pointer",borderBottom:"1px solid #111"}} className="inv-row">
                                          <div style={{flexShrink:0}}>
                                            {c.imageUrl
                                              ? <img src={c.imageUrl} alt={c.hero} style={{width:36,height:48,objectFit:"cover",borderRadius:4}}/>
                                              : <div style={{width:36,height:48,background:"var(--bz-line-2)",borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:"var(--bz-ink-3)",textAlign:"center",padding:2}}>{c.hero?.split(" ")[0]}</div>
                                            }
                                          </div>
                                          <div style={{flex:1,minWidth:0}}>
                                            <div style={{fontSize:13,fontWeight:700,color:"var(--bz-ink)",marginBottom:2}}>{c.hero}</div>
                                            <div style={{display:"flex",gap:6,fontSize:10,flexWrap:"wrap"}}>
                                              <span style={{color:"var(--bz-ink-3)"}}>#{c.cardNum}</span>
                                              {c.weapon&&<span style={{color:wc,fontWeight:700}}>{c.weapon}</span>}
                                              {c.treatment&&<span style={{color:"var(--bz-ink-2)"}}>{c.treatment}</span>}
                                              {c.setName&&<span style={{color:"#8a8a8a",fontStyle:"italic"}}>{c.setName}</span>}
                                            </div>
                                            {(c.mktValue||c.marketValue)&&<div style={{fontSize:10,color:"#4ade80",marginTop:2}}>${parseFloat(c.mktValue||c.marketValue).toFixed(2)}</div>}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                            </div>
                            {cardPools.filter(p=>p.cardType===r.cardType).length > 0 && (
                              <button onClick={()=>setRows(p=>p.map(x=>x.id===r.id?{...x,name:"",manualEntry:false}:x))} style={{background:"none",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:6,padding:"6px 8px",fontSize:11,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>↩ Pool</button>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ position:"relative", marginBottom:8 }}>
                        <input
                          value={acOpen===r.id ? (acQuery[r.id]??r.name) : r.name}
                          onChange={e=>{ setAcOpen(r.id); setAcQuery(q=>({...q,[r.id]:e.target.value})); upd(r.id,"name",e.target.value); }}
                          onFocus={()=>{ setAcOpen(r.id); setAcQuery(q=>({...q,[r.id]:r.name})); }}
                          onBlur={()=>setTimeout(()=>setAcOpen(p=>p===r.id?null:p),150)}
                          placeholder="Type hero name or card #..."
                          style={mInp}
                        />
                        {acOpen===r.id && (acQuery[r.id]||"").length>=1 && (() => {
                          const raw=(acQuery[r.id]||"").toLowerCase();
                          const terms=raw.trim().split(/\s+/).filter(Boolean);
                          const hits=bobaCards.filter(c=>terms.every(t=>[c.hero||"",c.weapon||"",c.treatment||"",String(c.cardNum||""),c.notation||"",c.setName||""].join(" ").toLowerCase().includes(t))).slice(0,12);
                          if(!hits.length) return null;
                          return (
                            <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:8, zIndex:999, boxShadow:"0 8px 24px rgba(0,0,0,0.8)", maxHeight:280, overflowY:"auto" }}>
                              <div style={{padding:"4px 10px",fontSize:10,color:"var(--bz-ink-3)",borderBottom:"1px solid #111"}}>{hits.length} match{hits.length!==1?"es":""}</div>
                              {hits.map(c=>{
                                const wc=PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
                                const label=[c.hero,c.treatment,c.weapon?"("+c.weapon+")":"",c.cardNum?"#"+c.cardNum:""].filter(Boolean).join(" — ");
                                return (
                                  <div key={c.id} onMouseDown={()=>{ upd(r.id,"name",label); if(c.mktValue||c.marketValue) upd(r.id,"mktVal",String(c.mktValue||c.marketValue||"")); setAcOpen(null); }}
                                    style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",cursor:"pointer",borderBottom:"1px solid #111"}} className="inv-row">
                                    <div style={{flexShrink:0}}>
                                      {c.imageUrl
                                        ? <img src={c.imageUrl} alt={c.hero} style={{width:36,height:48,objectFit:"cover",borderRadius:4}}/>
                                        : <div style={{width:36,height:48,background:"var(--bz-line-2)",borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:"var(--bz-ink-3)",textAlign:"center",padding:2}}>{c.hero?.split(" ")[0]}</div>
                                      }
                                    </div>
                                    <div style={{flex:1,minWidth:0}}>
                                      <div style={{fontSize:13,fontWeight:700,color:"var(--bz-ink)",marginBottom:2}}>{c.hero}</div>
                                      <div style={{display:"flex",gap:6,fontSize:10,flexWrap:"wrap"}}>
                                        <span style={{color:"var(--bz-ink-3)"}}>#{c.cardNum}</span>
                                        {c.weapon&&<span style={{color:wc,fontWeight:700}}>{c.weapon}</span>}
                                        {c.treatment&&<span style={{color:"var(--bz-ink-2)"}}>{c.treatment}</span>}
                                        {c.setName&&<span style={{color:"#8a8a8a",fontStyle:"italic"}}>{c.setName}</span>}
                                      </div>
                                      {(c.mktValue||c.marketValue)&&<div style={{fontSize:10,color:"#4ade80",marginTop:2}}>${parseFloat(c.mktValue||c.marketValue).toFixed(2)}</div>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:8 }}>
                      <div>
                        <div style={{ fontSize:11, color:"#999", marginBottom:4 }}>Type</div>
                        <select value={r.cardType} onChange={e=>upd(r.id,"cardType",e.target.value)} style={{ ...mInp, cursor:"pointer" }}>
                          <option value="">Type...</option>
                          {CARD_TYPES.map(ct=><option key={ct} value={ct}>{ct.replace(" Cards","")}</option>)}
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:"#999", marginBottom:4 }}>Qty</div>
                        <input type="number" value={r.qty} onChange={e=>upd(r.id,"qty",e.target.value)} placeholder="1" min="1" style={mInp}/>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:"#999", marginBottom:4 }}>Value/Card</div>
                        <input type="number" value={r.mktVal} onChange={e=>upd(r.id,"mktVal",e.target.value)} placeholder="0.00" style={mInp}/>
                      </div>
                    </div>
                    <div style={{ marginBottom:8, display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                      <div>
                        <div style={{ fontSize:11, color: r.costOverride?"#FBBF24":"#666", marginBottom:4, fontWeight:r.costOverride?700:400 }}>
                          {r.costOverride?"★ ":""}Cost/Card Override
                        </div>
                        <input type="text" inputMode="decimal" value={r.costOverride} onChange={e=>upd(r.id,"costOverride",e.target.value)} placeholder={`auto ($${getCostPerCard(r).toFixed(2)})`} style={{ ...mInp, border:r.costOverride?"1.5px solid #FBBF2488":"1px solid var(--bz-line-2)", color:r.costOverride?"#FBBF24":"#888" }}/>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color: r.pctOverride?"#A78BFA":"#666", marginBottom:4, fontWeight:r.pctOverride?700:400 }}>
                          {r.pctOverride?"★ ":""}Custom Comp % (e.g. 70)
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                          <input type="number" min="0" max="100" value={r.pctOverride} onChange={e=>{upd(r.id,"pctOverride",e.target.value); upd(r.id,"costOverride","");}} placeholder="global %" style={{ ...mInp, border:r.pctOverride?"1.5px solid #A78BFA88":"1px solid var(--bz-line-2)", color:r.pctOverride?"#A78BFA":"#888", flex:1 }}/>
                          <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>%</span>
                        </div>
                        {r.pctOverride && <div style={{ fontSize:10, color:"#A78BFA", marginTop:2 }}>${((parseFloat(r.mktVal)||0)*(parseFloat(r.pctOverride)/100)).toFixed(2)}/card</div>}
                      </div>
                    </div>
                    {(mv > 0 || perCardOffer > 0) && (
                      <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 10px", background:"var(--bz-s2)", borderRadius:6, fontSize:12 }}>
                        <span style={{ color:"var(--bz-ink-2)" }}>Total: <strong style={{ color:"var(--bz-ink)" }}>${(mv*qty).toFixed(2)}</strong></span>
                        <span style={{ color:"var(--bz-ink-2)" }}>Offer/card: <strong style={{ color:"#E8317A" }}>${perCardOffer.toFixed(2)}</strong></span>
                      </div>
                    )}
                  </div>
                );
              })}
              <button onClick={addRow} style={{ background:"transparent", border:"1.5px dashed var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:10, padding:"12px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>+ Add Card</button>
            </div>
          ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:750 }}>
              <thead>
                <tr style={{ background:"var(--bz-bg)" }}>
                  {["#","Card","Type","Qty","Mkt Val","Total","Offer/Card","Override","Zone","✓"].map(h=>(
                    <th key={h} style={{ ...S.th, fontSize:9, letterSpacing:"0.8px", padding:"8px 10px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r,i) => {
                  const mv  = parseFloat(r.mktVal)||0;
                  const qty = parseInt(r.qty)||1;
                  const perCardOffer = mv > 0 ? getCostPerCard(r) : (totalCards > 0 ? dispOffer / totalCards : 0);
                  const cardPct = mv > 0 ? perCardOffer / mv : 0;
                  const isLocked = r.costOverride || r.pctOverride;
                  // Per-card zone based on what this card actually gets
                  const cz = mv > 0 && perCardOffer > 0 ? (() => {
                    const p = cardPct;
                    if (p < 0.65) return { label:"Dunk",  color:"#4ade80", bg:"rgba(74,222,128,0.12)",  border:"rgba(74,222,128,0.3)" };
                    if (p <= 0.70) return { label:"Fair",  color:"#FBBF24", bg:"rgba(251,191,36,0.12)",  border:"rgba(251,191,36,0.3)" };
                    return              { label:"Stretch", color:"#ef4444", bg:"rgba(239,68,68,0.12)",   border:"rgba(239,68,68,0.3)" };
                  })() : null;

                  return (
                    <tr key={r.id} style={{ background: i%2===0?"var(--bz-s1)":"var(--bz-s1)", opacity:r.include?1:0.4, borderBottom:"1px solid var(--bz-line)" }}>

                      {/* # */}
                      <td style={{ ...S.td, color:"var(--bz-ink-3)", width:28, textAlign:"center", fontSize:11 }}>{i+1}</td>

                      {/* Card name */}
                      <td style={{ ...S.td, width:200, position:"relative" }}>
                        {POOL_TYPES.includes(r.cardType) ? (
                          <div style={{display:"flex",flexDirection:"column",gap:4,flex:1}}>
                            {cardPools.filter(p=>p.cardType===r.cardType).length > 0 && !r.manualEntry ? (
                              <select value={r.name} onChange={e=>{ if(e.target.value==="__manual__"){setRows(p=>p.map(x=>x.id===r.id?{...x,name:"",manualEntry:true}:x));} else {upd(r.id,"name",e.target.value);}}}
                                style={{ ...S.inp, padding:"5px 8px", fontSize:12, color:r.name?"var(--bz-ink)":"var(--bz-ink-2)", cursor:"pointer" }}>
                                <option value="">-- Pool / Treatment --</option>
                                <optgroup label="Your Pools">{cardPools.filter(p=>p.cardType===r.cardType).map(p=>(<option key={p.id} value={p.cardName}>{p.cardName} ({(parseInt(p.totalQty)||0)-(parseInt(p.usedQty)||0)} avail)</option>))}</optgroup>
                                <optgroup label="All Treatments">{[...new Set(bobaCards.map(c=>c.treatment).filter(Boolean))].sort().map(t=>(<option key={t} value={t}>{t}</option>))}</optgroup>
                                <option value="__manual__">✏️ Type manually...</option>
                              </select>
                            ) : (
                              <div style={{display:"flex",gap:4,alignItems:"center",flex:1}}>
                                <div style={{position:"relative",flex:1}}>
                                  <input value={acOpen===r.id?(acQuery[r.id]??r.name):r.name}
                                    onChange={e=>{setAcOpen(r.id);setAcQuery(q=>({...q,[r.id]:e.target.value}));upd(r.id,"name",e.target.value);}}
                                    onFocus={()=>{setAcOpen(r.id);setAcQuery(q=>({...q,[r.id]:r.name}));}}
                                    onBlur={()=>setTimeout(()=>setAcOpen(p=>p===r.id?null:p),150)}
                                    placeholder="Type name..." style={{ ...S.inp, padding:"5px 8px", fontSize:12, width:"100%" }}/>
                                  {acOpen===r.id&&(acQuery[r.id]||"").length>=1&&(()=>{const raw=(acQuery[r.id]||"").toLowerCase();const hits=bobaCards.filter(c=>[c.hero||"",c.weapon||"",c.treatment||"",String(c.cardNum||""),c.notation||"",c.setName||""].join(" ").toLowerCase().includes(raw)).slice(0,8);if(!hits.length)return null;return(<div style={{position:"absolute",top:"100%",left:0,right:0,background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,zIndex:999,boxShadow:"0 8px 24px rgba(0,0,0,0.8)",maxHeight:220,overflowY:"auto"}}>{hits.map(c=>{const label=[c.hero,c.treatment,c.weapon?"("+c.weapon+")":"",c.cardNum?"#"+c.cardNum:""].filter(Boolean).join(" — ");return<div key={c.id} onMouseDown={()=>{upd(r.id,"name",label);if(c.mktValue||c.marketValue)upd(r.id,"mktVal",String(c.mktValue||c.marketValue||""));setAcOpen(null);}} style={{padding:"7px 10px",borderBottom:"1px solid #111",cursor:"pointer",fontSize:12,color:"var(--bz-ink)"}} className="inv-row">{label}</div>;})}</div>);})()}
                                </div>
                                {cardPools.filter(p=>p.cardType===r.cardType).length>0&&<button onClick={()=>setRows(p=>p.map(x=>x.id===r.id?{...x,name:"",manualEntry:false}:x))} style={{background:"none",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:6,padding:"3px 7px",fontSize:11,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>↩</button>}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                            <div style={{ position:"relative", flex:1 }}>
                              <input value={acOpen===r.id?(acQuery[r.id]??r.name):r.name}
                                onChange={e=>{setAcOpen(r.id);setAcQuery(q=>({...q,[r.id]:e.target.value}));upd(r.id,"name",e.target.value);}}
                                onFocus={()=>{setAcOpen(r.id);setAcQuery(q=>({...q,[r.id]:r.name}));}}
                                onBlur={()=>setTimeout(()=>setAcOpen(p=>p===r.id?null:p),150)}
                                placeholder="Hero name or card #..."
                                style={{ ...S.inp, padding:"5px 8px", fontSize:12, width:"100%" }}/>
                              {acOpen===r.id&&(acQuery[r.id]||"").length>=1&&(()=>{
                                const raw=(acQuery[r.id]||"").toLowerCase();
                                const terms=raw.trim().split(/\s+/).filter(Boolean);
                                const hits=bobaCards.filter(c=>terms.every(t=>[c.hero||"",c.weapon||"",c.treatment||"",String(c.cardNum||""),c.notation||"",c.setName||""].join(" ").toLowerCase().includes(t))).sort((a,b)=>{const s=h=>h.toLowerCase()===raw?0:h.toLowerCase().startsWith(terms[0])?1:2;return s(a.hero||"")-s(b.hero||"");}).slice(0,10);
                                if(!hits.length)return null;
                                return(<div style={{position:"absolute",top:"100%",left:0,right:0,background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,zIndex:999,boxShadow:"0 8px 24px rgba(0,0,0,0.8)",maxHeight:260,overflowY:"auto"}}>
                                  {hits.map(c=>{const wc=PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";const label=[c.hero,c.treatment,c.weapon?"("+c.weapon+")":"",c.cardNum?"#"+c.cardNum:""].filter(Boolean).join(" — ");return(
                                    <div key={c.id} onMouseDown={()=>{upd(r.id,"name",label);if(c.mktValue||c.marketValue)upd(r.id,"mktVal",String(c.mktValue||c.marketValue||""));setAcOpen(null);}}
                                      style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",cursor:"pointer",borderBottom:"1px solid #111"}} className="inv-row">
                                      <div style={{flex:1,minWidth:0}}>
                                        <div style={{fontSize:12,fontWeight:700,color:"var(--bz-ink)"}}>{c.hero}</div>
                                        <div style={{display:"flex",gap:5,fontSize:10}}>
                                          {c.weapon&&<span style={{color:wc,fontWeight:700}}>{c.weapon}</span>}
                                          {c.treatment&&<span style={{color:"#999"}}>{c.treatment}</span>}
                                          {c.cardNum&&<span style={{color:"#8a8a8a"}}>#{c.cardNum}</span>}
                                        </div>
                                      </div>
                                      {(c.mktValue||c.marketValue)&&<span style={{fontSize:11,color:"#4ade80",fontWeight:700}}>${parseFloat(c.mktValue||c.marketValue).toFixed(2)}</span>}
                                    </div>);
                                  })}
                                </div>);
                              })()}
                            </div>
                          </div>
                        )}
                        <button onClick={()=>setRows(p=>p.filter(x=>x.id!==r.id))}
                          style={{position:"absolute",top:6,right:6,background:"none",border:"none",color:"var(--bz-ink-3)",cursor:"pointer",fontSize:13,padding:2,lineHeight:1}} title="Remove row">×</button>
                      </td>

                      {/* Type */}
                      <td style={{ ...S.td, width:130 }}>
                        <select value={r.cardType} onChange={e=>upd(r.id,"cardType",e.target.value)}
                          style={{ ...S.inp, padding:"4px 8px", fontSize:11, color:r.cardType?"var(--bz-ink)":"var(--bz-ink-2)", cursor:"pointer" }}>
                          <option value="">Type...</option>
                          {CARD_TYPES.map(ct=><option key={ct} value={ct}>{ct.replace(" Cards","")}</option>)}
                        </select>
                      </td>

                      {/* Qty */}
                      <td style={{ ...S.td, width:52 }}>
                        <input type="number" value={r.qty} onChange={e=>upd(r.id,"qty",e.target.value)} placeholder="1" min="1"
                          style={{ ...S.inp, padding:"4px 6px", fontSize:12, width:44, textAlign:"center" }}/>
                      </td>

                      {/* Mkt Val */}
                      <td style={{ ...S.td, width:90 }}>
                        <input type="number" value={r.mktVal} onChange={e=>upd(r.id,"mktVal",e.target.value)} placeholder="0.00"
                          style={{ ...S.inp, padding:"4px 8px", fontSize:12, color:mv?"var(--bz-ink-2)":"#333", width:78 }}/>
                      </td>

                      {/* Total mkt */}
                      <td style={{ ...S.td, color:"var(--bz-ink-3)", fontWeight:600, fontSize:12, width:80 }}>
                        {mv>0 ? `$${(mv*qty).toFixed(2)}` : "—"}
                      </td>

                      {/* Offer/card — highlighted */}
                      <td style={{ ...S.td, width:90 }}>
                        <div style={{ fontWeight:800, fontSize:13, color: isLocked?"#FBBF24":"#E8317A" }}>
                          ${perCardOffer.toFixed(2)}
                          {r.pctOverride&&<span style={{fontSize:9,color:"#A78BFA",marginLeft:3}}>%</span>}
                          {r.costOverride&&<span style={{fontSize:9,color:"#FBBF24",marginLeft:3}}>★</span>}
                        </div>
                        {mv>0&&<div style={{fontSize:9,color:"var(--bz-ink-3)",marginTop:1}}>{(cardPct*100).toFixed(0)}% of mkt</div>}
                      </td>

                      {/* Cost/pct override — two mini inputs */}
                      <td style={{ ...S.td, width:130 }}>
                        <div style={{ display:"flex", gap:4 }}>
                          <input type="text" inputMode="decimal" value={r.costOverride}
                            onChange={e=>{upd(r.id,"costOverride",e.target.value);if(e.target.value)upd(r.id,"pctOverride","");}}
                            placeholder="$"
                            style={{ ...S.inp, padding:"4px 6px", fontSize:11, width:50, color:r.costOverride?"#FBBF24":"#555", border:r.costOverride?"1px solid #FBBF2455":"1px solid var(--bz-line-2)", textAlign:"center" }}/>
                          <div style={{ display:"flex", alignItems:"center", gap:2 }}>
                            <input type="number" min="0" max="200" value={r.pctOverride}
                              onChange={e=>{upd(r.id,"pctOverride",e.target.value);if(e.target.value)upd(r.id,"costOverride","");}}
                              placeholder="%"
                              style={{ ...S.inp, padding:"4px 4px", fontSize:11, width:40, color:r.pctOverride?"#A78BFA":"#555", border:r.pctOverride?"1px solid #A78BFA55":"1px solid var(--bz-line-2)", textAlign:"center" }}/>
                            <span style={{fontSize:10,color:"var(--bz-ink-3)"}}>%</span>
                          </div>
                        </div>
                        {(r.costOverride||r.pctOverride)&&(
                          <button onClick={()=>{upd(r.id,"costOverride","");upd(r.id,"pctOverride","");}}
                            style={{background:"none",border:"none",color:"var(--bz-ink-3)",cursor:"pointer",fontSize:10,fontFamily:"inherit",padding:"2px 0"}}>↩ reset</button>
                        )}
                      </td>

                      {/* Zone chip */}
                      <td style={{ ...S.td, width:72 }}>
                        {cz ? (
                          <div style={{ background:cz.bg, border:`1px solid ${cz.border}`, borderRadius:6, padding:"3px 8px", textAlign:"center" }}>
                            <div style={{ fontSize:10, fontWeight:800, color:cz.color }}>{cz.label}</div>
                          </div>
                        ) : <span style={{ color:"var(--bz-ink-3)", fontSize:11 }}>—</span>}
                      </td>

                      {/* Include checkbox */}
                      <td style={{ ...S.td, textAlign:"center", width:32 }}>
                        <input type="checkbox" checked={r.include} onChange={e=>upd(r.id,"include",e.target.checked)}/>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
          {!isMobile && <div style={{ marginTop:10 }}><Btn onClick={addRow} variant="ghost">+ Add Row</Btn></div>}
        </div>

        <div style={{ ...S.card, border:"2px solid var(--bz-line-2)333" }}>
          <SectionLabel t="Confirm & Actions" />
          {canSeeFinancials && dispOffer > 0 && totalMkt > 0 && (
            <div style={{ marginBottom:16, padding:"8px 14px", background:"var(--bz-s1)", borderRadius:8, display:"flex", gap:20, flexWrap:"wrap" }}>
              <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Active offer: <strong style={{color:(counterAmt!=null&&counterAmt>0)?"#92400e":(offerAmt!=null&&offerAmt>0)?"#E8317A":"#166534"}}>${dispOffer.toFixed(2)} ({(dispPct*100).toFixed(1)}%)</strong></span>
              <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Est. Margin: <strong style={{color:"#E8317A"}}>${(totalMkt-dispOffer).toFixed(2)}</strong></span>
              <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Market Value: <strong style={{color:"var(--bz-ink-2)"}}>${totalMkt.toFixed(2)}</strong></span>
              <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Per Card: <strong style={{color:"#E8317A"}}>${totalCards>0?(dispOffer/totalCards).toFixed(2):"--"}</strong></span>
            </div>
          )}
          {/* Pay button -- appears when payment method + handle are filled */}
          {seller.payment && seller.paymentHandle && (() => {
            const handle      = seller.paymentHandle.trim();
            const cleanHandle = handle.replace(/^@/,"");
            const amt         = dispOffer > 0 ? dispOffer.toFixed(2) : "";
            const PCFG = {
              PayPal: { color:"#003087", bg:"#E8EEFF", hint:handle },
              Zelle:  { color:"#6D1ED4", bg:"#F3EEFF", hint:handle },
            };
            const cfg = PCFG[seller.payment];
            if (!cfg) return null;
            return (
              <div style={{ marginBottom:16, padding:"14px 16px", background:cfg.bg, border:`2px solid ${cfg.color}33`, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                <div>
                  <div style={{ fontSize:10, fontWeight:700, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1.5, marginBottom:4 }}>{"\uD83D\uDCB8 Send Payment"}</div>
                  <div style={{ fontWeight:800, fontSize:16, color:cfg.color }}>{cfg.hint}</div>
                  {amt && <div style={{ fontSize:12, color:"var(--bz-ink-2)", marginTop:2 }}>Amount: <strong style={{color:"var(--bz-ink)"}}>${amt}</strong></div>}
                </div>
                <div style={{ background:cfg.color, color:"var(--bz-ink)", borderRadius:9, padding:"12px 24px", fontSize:14, fontWeight:800 }}>
                  {`Open ${seller.payment} \u2192 ${cfg.hint}`}
                </div>
              </div>
            );
          })()}

          <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:16, alignItems:"center" }}>
            <Btn onClick={()=>setCustView(true)} variant="ghost">{"\uD83D\uDC41 Customer View"}</Btn>
            <div style={{ display:"flex", alignItems:"center", gap:8, background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:8, padding:"6px 12px", cursor:"pointer" }} onClick={()=>setAllowCounter(p=>!p)}>
              <div style={{ width:32, height:18, borderRadius:9, background:allowCounter?"#E8317A":"#333", position:"relative", transition:"background 0.2s", flexShrink:0 }}>
                <div style={{ position:"absolute", top:2, left:allowCounter?14:2, width:14, height:14, borderRadius:"50%", background:"#fff", transition:"left 0.2s" }}/>
              </div>
              <span style={{ fontSize:11, fontWeight:700, color:allowCounter?"#E8317A":"#666", whiteSpace:"nowrap" }}>Counter Offer {allowCounter?"ON":"OFF"}</span>
            </div>
            {loadedCompId ? (
              <div style={{ display:"flex", gap:6 }}>
                <Btn onClick={async()=>{
                  if (!onSaveQuote) return;
                  const { id } = await onSaveQuote({
                    existingId: loadedCompId,
                    seller, cards:included.map(r=>({ name:r.name, cardType:r.cardType, qty:parseInt(r.qty)||1, mktVal:parseFloat(r.mktVal)||0, pctOverride:r.pctOverride||"", offerPerCard:getCostPerCard(r) })),
                    dispOffer, dispPct, totalMkt, custNote,
                    payment:seller.payment, paymentHandle:seller.paymentHandle,
                    allowCounter,
                  });
                  const link = `${window.location.origin}/quote/${loadedCompId}`;
                  setQuoteLink(link);
                  navigator.clipboard?.writeText(link);
                  setQuoteCopied(true);
                  setTimeout(()=>setQuoteCopied(false), 3000);
                }} variant="green" disabled={included.length===0}>{"📤 Send Offer Back"}</Btn>
                <Btn onClick={async()=>{
                  if (!onSaveQuote) return;
                  const { id, quoteRef } = await onSaveQuote({
                    existingId: null,
                    seller, cards:included.map(r=>({ name:r.name, cardType:r.cardType, qty:parseInt(r.qty)||1, mktVal:parseFloat(r.mktVal)||0, pctOverride:r.pctOverride||"", offerPerCard:getCostPerCard(r) })),
                    dispOffer, dispPct, totalMkt, custNote,
                    payment:seller.payment, paymentHandle:seller.paymentHandle,
                    allowCounter,
                  });
                  const link = `${window.location.origin}/quote/${id}`;
                  setSavedQuoteRef(quoteRef || null);
                  setQuoteLink(link);
                  navigator.clipboard?.writeText(link);
                  setQuoteCopied(true);
                  setTimeout(()=>setQuoteCopied(false), 3000);
                }} variant="ghost" disabled={included.length===0}>{"\uD83D\uDD17 Generate New Link"}</Btn>
              </div>
            ) : (
            <Btn onClick={async()=>{
              if (!onSaveQuote) return;
              const { id, quoteRef } = await onSaveQuote({
                existingId: null,
                seller, cards:included.map(r=>({ name:r.name, cardType:r.cardType, qty:parseInt(r.qty)||1, mktVal:parseFloat(r.mktVal)||0, pctOverride:r.pctOverride||"", offerPerCard:getCostPerCard(r) })),
                dispOffer, dispPct, totalMkt, custNote,
                payment:seller.payment, paymentHandle:seller.paymentHandle,
                allowCounter,
              });
              const link = `${window.location.origin}/quote/${id}`;
              setSavedQuoteRef(quoteRef || null);
              setQuoteLink(link);
              navigator.clipboard?.writeText(link);
              setQuoteCopied(true);
              setTimeout(()=>setQuoteCopied(false), 3000);
            }} variant="ghost" disabled={included.length===0}>{"\uD83D\uDD17 Share Quote"}</Btn>
            )}
            {quoteLink && (
              <div style={{ display:"flex", flexDirection:"column", gap:6, flex:1 }}>
                {savedQuoteRef && (
                  <div style={{ display:"flex", alignItems:"center", gap:8, background:"#0a0a1a", border:"1px solid #7B9CFF33", borderRadius:8, padding:"6px 12px" }}>
                    <span style={{ fontSize:11, color:"#7B9CFF", fontWeight:700 }}>📋 Quote Ref:</span>
                    <span style={{ fontSize:14, fontWeight:900, color:"var(--bz-ink)", letterSpacing:1 }}>{savedQuoteRef}</span>
                    <button onClick={()=>{ navigator.clipboard?.writeText(savedQuoteRef); }} style={{ background:"none", border:"1px solid var(--bz-line-2)", borderRadius:5, color:"var(--bz-ink-3)", cursor:"pointer", fontSize:10, padding:"2px 8px", fontFamily:"inherit" }}>Copy</button>
                    <span style={{ fontSize:10, color:"var(--bz-ink-3)", marginLeft:"auto" }}>Use this in payment notes</span>
                  </div>
                )}
                <div style={{ display:"flex", alignItems:"center", gap:8, background:"#0a1a0a", border:"1px solid #4ade8033", borderRadius:8, padding:"6px 12px" }}>
                  <span style={{ fontSize:11, color:"#4ade80", fontWeight:700 }}>{quoteCopied ? "\u2705 Copied!" : "\uD83D\uDD17"}</span>
                  <span style={{ fontSize:11, color:"var(--bz-ink-2)", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{quoteLink}</span>
                  <button onClick={()=>{ navigator.clipboard?.writeText(quoteLink); setQuoteCopied(true); setTimeout(()=>setQuoteCopied(false),3000); }} style={{ background:"none", border:"1px solid var(--bz-line-2)", borderRadius:5, color:"var(--bz-ink-2)", cursor:"pointer", fontSize:11, padding:"2px 8px", fontFamily:"inherit", whiteSpace:"nowrap" }}>Copy</button>
                  <a href={quoteLink} target="_blank" rel="noreferrer" style={{ color:"#E8317A", fontSize:11, textDecoration:"none", whiteSpace:"nowrap" }}>{"Open \u2197"}</a>
                </div>
              </div>
            )}
            <Btn onClick={()=>saveComp("saved")} variant="ghost">{"\uD83D\uDCBE Save Comp"}</Btn>
            <Btn onClick={()=>saveComp("passed")} variant="ghost">{"\u274C Pass on Lot"}</Btn>
            <Btn onClick={()=>{saveComp("accepted");doAccept();}} disabled={included.length===0} variant="green">{"\u2705 Accept & Import"}{totalCards} card{totalCards!==1?"s":""}</Btn>
          </div>
          <div style={{ marginBottom:14, display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap" }}>
            <div style={{ flex:1, minWidth:180 }}>
              <label style={S.lbl}>Cards Going To <span style={{ color:"#E8317A" }}>*</span></label>
              <select value={lotLocation} onChange={e=>setLotLocation(e.target.value)}
                style={{ ...S.inp, fontWeight:700, color:"var(--bz-ink)", cursor:"pointer" }}>
                {LOCATIONS.map(l=><option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={{ ...S.lbl, color:"#E8317A" }}>Notes for Seller (shown on Customer View)</label>
            <textarea
              value={custNote}
              onChange={e=>setCustNote(e.target.value)}
              placeholder="e.g. Condition notes, grade estimates, any special considerations..."
              rows={2}
              style={{ ...S.inp, resize:"vertical", lineHeight:1.5, fontSize:12 }}
            />
          </div>
          <div style={{ borderTop:"1px solid #F0D0DC", paddingTop:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
              <div style={{ fontSize:10, fontWeight:700, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1.5 }}>Counter Offer Calculator</div>
              {(counterAmt!=null&&counterAmt>0) && <span style={{ background:"var(--bz-s1)", color:"var(--bz-ink-2)", border:"1px solid #92400e33", borderRadius:5, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{"\u26A0 Counter is active -- overrides your offer"}</span>}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12 }}>
              <div><label style={S.lbl}>Seller's Counter ($)</label><input type="number" value={counterOffer} onChange={e=>setCounterOffer(e.target.value)} placeholder="0.00" style={{ ...S.inp, border:(counterAmt!=null&&counterAmt>0)?"2px solid #E8317A":S.inp.border }}/></div>
              <div><label style={S.lbl}>Counter Zone</label><div style={{ ...S.inp, background:counterZone?.bg||"#F9FAFB", border:`1.5px solid ${counterZone?.color||"var(--bz-line)"}`, color:counterZone?.color||"var(--bz-ink-2)", fontWeight:700 }}>{counterZone?counterZone.label:totalMkt>0?"Enter counter":"Add cards first"}</div></div>
              <div><label style={S.lbl}>Counter Buy %</label><div style={{ ...S.inp, color:(counterAmt!=null&&counterAmt>0)?(counterZone?.color||"#6B2D8B"):"var(--bz-ink-2)", fontWeight:700 }}>{(counterAmt!=null&&counterAmt>0)&&totalMkt>0?`${((counterAmt/totalMkt)*100).toFixed(1)}%`:"--"}</div></div>
              <div><label style={S.lbl}>vs Your Offer</label><div style={{ ...S.inp, color:(counterAmt!=null&&counterAmt>(offerAmt!=null&&offerAmt>0?offerAmt:calcOffer))?"#991b1b":"#166534", fontWeight:700 }}>{(counterAmt!=null&&counterAmt>0)&&calcOffer>0?`$${Math.abs(counterAmt-(offerAmt>0?offerAmt:calcOffer)).toFixed(2)} ${counterAmt>(offerAmt!=null&&offerAmt>0?offerAmt:calcOffer)?"over":"under"}`:"--"}</div></div>
            </div>
            {(counterAmt!=null&&counterAmt>0) && totalMkt > 0 && (
              <div style={{ marginTop:8, display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Active offer: <strong style={{color:"var(--bz-ink)"}}>${counterAmt.toFixed(2)}</strong> at <strong style={{color:counterZone?.color||"var(--bz-ink)"}}>{((counterAmt/totalMkt)*100).toFixed(1)}%</strong> -- card values and zones updated</span>
                <button onClick={()=>setCounterOffer("")} style={{ background:"none", border:"none", color:"var(--bz-ink-2)", cursor:"pointer", fontSize:12, fontWeight:700, textDecoration:"underline" }}>Clear</button>
              </div>
            )}
          </div>
        </div>
        </div>{/* end left column */}

        {/* Seller Intelligence Panel -- right column */}
        {seller.name && (() => {
          const sellerComps = comps.filter(c => (c.seller||"").toLowerCase() === seller.name.toLowerCase());
          if (sellerComps.length === 0) return (
            <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"20px 16px", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8, minHeight:160, color:"var(--bz-ink-3)", position:"sticky", top:80 }}>
              <div style={{ fontSize:24 }}>{"\uD83C\uDD95"}</div>
              <div style={{ fontSize:12, fontWeight:700, color:"var(--bz-ink-3)" }}>New seller</div>
              <div style={{ fontSize:11, color:"var(--bz-ink-3)", textAlign:"center" }}>No previous history with {seller.name}</div>
            </div>
          );
          const totalPaid    = sellerComps.reduce((s,c)=>s+(parseFloat(c.offer)||0),0);
          const totalMktVal  = sellerComps.reduce((s,c)=>s+(parseFloat(c.totalMarket)||0),0);
          const avgPct       = totalMktVal > 0 ? totalPaid/totalMktVal : 0;
          const accepted     = sellerComps.filter(c=>c.status==="accepted").length;
          const declined     = sellerComps.filter(c=>c.status==="declined").length;
          const pending      = sellerComps.filter(c=>c.status==="pending").length;
          const counterCount = sellerComps.filter(c=>c.status==="countered").length;
          const avgLotSize   = sellerComps.reduce((s,c)=>s+(c.totalCards||0),0)/sellerComps.length;
          const sorted       = [...sellerComps].sort((a,b)=>(b.date||"").localeCompare(a.date||""));
          const statColor    = p => p > 0.65 ? "#E8317A" : p > 0.55 ? "#FBBF24" : "#4ade80";
          return (
            <div style={{ display:"flex", flexDirection:"column", gap:10, position:"sticky", top:80 }}>
              <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"14px 16px" }}>
                <div style={{ fontSize:14, fontWeight:900, color:"var(--bz-ink)", marginBottom:2 }}>{seller.name}</div>
                <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{sellerComps.length} deal{sellerComps.length!==1?"s":""} · Last: {sorted[0]?.date||"--"}</div>
                {seller.contact && <div style={{ fontSize:11, color:"#7B9CFF", marginTop:4 }}>{seller.contact}</div>}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                {[
                  { l:"Total Paid",   v:`$${Math.round(totalPaid).toLocaleString()}`,      c:"#4ade80" },
                  { l:"Avg % of Mkt", v:`${Math.round(avgPct*100)}%`,                      c:statColor(avgPct) },
                  { l:"Avg Lot Size", v:`${Math.round(avgLotSize)} cards`,                 c:"#7B9CFF" },
                  { l:"Accept Rate",  v:sellerComps.length>0?`${Math.round(accepted/sellerComps.length*100)}%`:"--", c:"#4ade80" },
                ].map(({l,v,c})=>(
                  <div key={l} style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
                    <div style={{ fontSize:18, fontWeight:900, color:c }}>{v}</div>
                    <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"12px 14px" }}>
                <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Outcomes</div>
                {[{l:"\u2705 Accepted",v:accepted,c:"#4ade80"},{l:"\u274C Declined",v:declined,c:"#E8317A"},{l:"\uD83E\uDD1D Countered",v:counterCount,c:"#FBBF24"},{l:"\u23F3 Pending",v:pending,c:"#555"}].filter(x=>x.v>0).map(({l,v,c})=>(
                  <div key={l} style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>{l}</span>
                    <span style={{ fontSize:12, fontWeight:700, color:c }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:10, overflow:"hidden" }}>
                <div style={{ padding:"10px 14px 6px", fontSize:11, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Deal History</div>
                <div style={{ maxHeight:300, overflowY:"auto" }}>
                  {sorted.map((c,i)=>{ const p=c.totalMarket>0?c.offer/c.totalMarket:0; const sc={accepted:"#4ade80",declined:"#E8317A",countered:"#FBBF24",pending:"#555"}; return (
                    <div key={c.id||i} style={{ padding:"8px 14px", borderTop:"1px solid var(--bz-line)", background:i%2===0?"var(--bz-s1)":"var(--bz-s1)" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                        <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{c.date||"--"}</span>
                        <span style={{ fontSize:10, fontWeight:700, color:sc[c.status]||"#555", textTransform:"capitalize" }}>{c.status||"--"}</span>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between" }}>
                        <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{c.totalCards||0} cards · ${Math.round(c.totalMarket||0).toLocaleString()} mkt</span>
                        <span style={{ fontSize:12, fontWeight:700, color:statColor(p) }}>${Math.round(c.offer||0).toLocaleString()} ({Math.round(p*100)}%)</span>
                      </div>
                    </div>
                  );})}
                </div>
              </div>
            </div>
          );
        })()}

        </div>{/* end grid */}
      </>}
    </div>
  );
}


// --- CARD POOL GLOBALS ---------------------------------------
const DEFAULT_PARALLELS = ["Base","Silver","Gold","Holo","Refractor","Auto","Prizm","Optic","Color Match","Superfractor","1/1","Other"];

// --- CARD POOLS ----------------------------------------------
function CardPools({ cardPools=[], onSavePool, onDeletePool, onLogPoolOut, onAddToPool, userRole, canSeeFinancials, bobaCards=[], inventory=[], breaks=[] }) {
  const isAdmin = ["Admin"].includes(userRole?.role);
  const EMPTY_POOL = { cardName:"", cardType:"Giveaway Cards", totalQty:"", costPerCard:"", marketValue:"", notes:"" };
  const [form,       setForm]       = useState(EMPTY_POOL);
  const [editing,    setEditing]    = useState(null);
  const [logForm,    setLogForm]    = useState({ poolId:"", qty:"", breaker:BREAKERS[0], date:todayLocal(), usage:"Giveaway" });

  // Derive counts from inventory for each pool
  // A pool matches inventory cards by cardType + cardName (treatment match)
  const usedInvIds = new Set(breaks.filter(b=>!b.isPoolLog).map(b=>b.inventoryId));

  function getPoolInventoryCounts(pool) {
    const sameTypePools = cardPools.filter(p => p.cardType === pool.cardType);
    // Try name-based match first
    const nameMatch = pool.cardName
      ? inventory.filter(c => c.cardType === pool.cardType && c.cardName?.toLowerCase().includes(pool.cardName.toLowerCase()))
      : [];
    // Fall back to all cards of that type only if:
    // - name match found nothing AND
    // - this is the only pool of that type (avoid double-counting across pools)
    const matching = nameMatch.length > 0
      ? nameMatch
      : (sameTypePools.length === 1 ? inventory.filter(c => c.cardType === pool.cardType) : []);
    const total     = matching.length;
    const used      = matching.filter(c => usedInvIds.has(c.id)).length;
    const inTransit = matching.filter(c => c.cardStatus === "in_transit" && !usedInvIds.has(c.id)).length;
    const avail     = total - used - inTransit;
    return { invTotal: total, invUsed: used, invInTransit: inTransit, invAvail: avail };
  }

  // Effective pool counts = manual pool qty + inventory-derived qty
  function getEffectiveCounts(pool) {
    const inv = getPoolInventoryCounts(pool);
    const poolTotal  = parseInt(pool.totalQty)||0;
    const poolUsed   = parseInt(pool.usedQty)||0;
    const poolAvail  = Math.max(0, poolTotal - poolUsed);
    return {
      total: poolTotal + inv.invTotal,
      used:  poolUsed  + inv.invUsed,
      avail: poolAvail + inv.invAvail,
      invTotal: inv.invTotal,
      invAvail: inv.invAvail,
    };
  }
  const [addForm,    setAddForm]    = useState({ poolId:"", qty:"" });
  const [showLog,    setShowLog]    = useState(false);
  const [showAdd,    setShowAdd]    = useState(false);

  const poolsByType = POOL_TYPES.reduce((acc, t) => {
    acc[t] = cardPools.filter(p => p.cardType === t);
    return acc;
  }, {});

  const totalCards = cardPools.reduce((s,p)=>s+getEffectiveCounts(p).total, 0);
  const totalUsed  = cardPools.reduce((s,p)=>s+getEffectiveCounts(p).used,  0);
  const totalAvail = cardPools.reduce((s,p)=>s+getEffectiveCounts(p).avail, 0);

  function startNew() { setForm(EMPTY_POOL); setEditing("new"); }
  function startEdit(p) { setForm({ cardName:p.cardName, cardType:p.cardType, totalQty:p.totalQty||"", costPerCard:p.costPerCard||"", marketValue:p.marketValue||"", notes:p.notes||"" }); setEditing(p.id); }
  function cancelEdit() { setEditing(null); setForm(EMPTY_POOL); }

  async function savePool() {
    if (!form.cardName.trim()) return;
    const data = { cardName:form.cardName.trim(), cardType:form.cardType, totalQty:parseInt(form.totalQty)||0, usedQty:editing!=="new" ? (cardPools.find(p=>p.id===editing)?.usedQty||0) : 0, costPerCard:parseFloat(form.costPerCard)||0, marketValue:parseFloat(form.marketValue)||0, notes:form.notes };
    if (editing !== "new") data.id = editing;
    await onSavePool(data);
    cancelEdit();
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
        {[
          { l:"Total in Pools",    v:totalCards, c:"var(--bz-ink)" },
          { l:"Used This Cycle",   v:totalUsed,  c:totalUsed>0?"#E8317A":"#333" },
          { l:"Available",         v:totalAvail, c:totalAvail>100?"#4ade80":"#FBBF24" },
        ].map(({l,v,c})=>(
          <div key={l} style={{ ...S.card, textAlign:"center" }}>
            <div style={{ fontSize:28, fontWeight:900, color:c }}>{v}</div>
            <div style={{ fontSize:10, color:"#999", textTransform:"uppercase", letterSpacing:1, marginTop:4 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Action buttons */}
      <div style={{ display:"flex", gap:8 }}>
        <Btn onClick={startNew} variant="green">+ New Pool</Btn>
        <Btn onClick={()=>setShowLog(p=>!p)} variant="ghost">{"\uD83D\uDCE4 Log Out Cards"}</Btn>
        <Btn onClick={()=>setShowAdd(p=>!p)} variant="ghost">{"\uD83D\uDCE5 Add to Pool"}</Btn>
        {isAdmin && cardPools.length > 0 && <Btn onClick={async()=>{ if(window.confirm(`Reset ALL ${cardPools.length} pools to 0? This clears all totals and used qtys.`)) { for(const p of cardPools) await onSavePool({...p, totalQty:0, usedQty:0}); }}} variant="ghost" style={{ color:"#FBBF24", border:"1px solid #FBBF2444" }}>{"\u21BA Reset All"}</Btn>}
      </div>

      {/* Log Out form */}
      {showLog && (
        <div style={{ ...S.card, border:"1px solid #E8317A33" }}>
          <SectionLabel t="📤 Log Cards Out of Pool" />
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr auto", gap:10, alignItems:"end" }}>
            <div>
              <label style={S.lbl}>Pool</label>
              <select value={logForm.poolId} onChange={e=>setLogForm(p=>({...p,poolId:e.target.value}))} style={{ ...S.inp, cursor:"pointer" }}>
                <option value="">-- Select Pool --</option>
                {cardPools.map(p=>{
                  const avail = getEffectiveCounts(p).avail;
                  return <option key={p.id} value={p.id}>{p.cardName} ({avail} avail)</option>;
                })}
              </select>
            </div>
            <div>
              <label style={S.lbl}>Qty</label>
              <input type="number" min="1" value={logForm.qty} onChange={e=>setLogForm(p=>({...p,qty:e.target.value}))} style={S.inp}/>
            </div>
            <div>
              <label style={S.lbl}>Breaker</label>
              <select value={logForm.breaker} onChange={e=>setLogForm(p=>({...p,breaker:e.target.value}))} style={{ ...S.inp, cursor:"pointer" }}>
                {BREAKERS.map(b=><option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label style={S.lbl}>Date</label>
              <input type="date" value={logForm.date} onChange={e=>setLogForm(p=>({...p,date:e.target.value}))} style={S.inp}/>
            </div>
            <Btn onClick={async()=>{
              if (!logForm.poolId||!logForm.qty) return;
              const pool = cardPools.find(p=>p.id===logForm.poolId);
              const avail = (parseInt(pool?.totalQty)||0)-(parseInt(pool?.usedQty)||0);
              if (parseInt(logForm.qty) > avail) { alert(`Only ${avail} available in this pool`); return; }
              await onLogPoolOut(logForm.poolId, parseInt(logForm.qty), logForm.breaker, logForm.date, logForm.usage);
              setLogForm(p=>({...p,qty:""}));
              setShowLog(false);
            }} variant="green" disabled={!logForm.poolId||!logForm.qty}>{"\u2705 Log Out"}</Btn>
          </div>
        </div>
      )}

      {/* Add to Pool form */}
      {showAdd && (
        <div style={{ ...S.card, border:"1px solid #4ade8033" }}>
          <SectionLabel t="📥 Add Cards to Pool" />
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr auto", gap:10, alignItems:"end" }}>
            <div>
              <label style={S.lbl}>Pool</label>
              <select value={addForm.poolId} onChange={e=>setAddForm(p=>({...p,poolId:e.target.value}))} style={{ ...S.inp, cursor:"pointer" }}>
                <option value="">-- Select Pool --</option>
                {cardPools.map(p=><option key={p.id} value={p.id}>{p.cardName} (currently {parseInt(p.totalQty)||0})</option>)}
              </select>
            </div>
            <div>
              <label style={S.lbl}>Qty to Add</label>
              <input type="number" min="1" value={addForm.qty} onChange={e=>setAddForm(p=>({...p,qty:e.target.value}))} style={S.inp}/>
            </div>
            <Btn onClick={async()=>{
              if (!addForm.poolId||!addForm.qty) return;
              await onAddToPool(addForm.poolId, parseInt(addForm.qty));
              setAddForm({poolId:"",qty:""});
              setShowAdd(false);
            }} variant="green" disabled={!addForm.poolId||!addForm.qty}>{"\u2705 Add"}</Btn>
          </div>
        </div>
      )}

      {/* New/Edit pool form */}
      {editing && (() => {
        const treatments = [...new Set(bobaCards.map(c=>c.treatment).filter(Boolean))].sort();
        const isCustom = form.cardName && !treatments.includes(form.cardName);
        return (
          <div style={{ ...S.card, border:"2px solid #E8317A44" }}>
            <SectionLabel t={editing==="new"?"New Card Pool":"Edit Pool"} />
            <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr", gap:10, marginBottom:10 }}>
              <div>
                <label style={S.lbl}>Treatment / Card Name</label>
                {treatments.length > 0 ? (
                  <>
                    <select
                      value={isCustom?"__custom__":form.cardName}
                      onChange={e=>{
                        if(e.target.value==="__custom__") setForm(p=>({...p,cardName:""}));
                        else setForm(p=>({...p,cardName:e.target.value}));
                      }}
                      style={{...S.inp,cursor:"pointer",marginBottom:isCustom?6:0}}
                    >
                      <option value="">-- Select Treatment --</option>
                      {treatments.map(t=><option key={t} value={t}>{t}</option>)}
                      <option value="__custom__">✏️ Custom name...</option>
                    </select>
                    {isCustom&&<input value={form.cardName} onChange={e=>setForm(p=>({...p,cardName:e.target.value}))} placeholder="Enter custom name" style={S.inp}/>}
                  </>
                ) : (
                  <input value={form.cardName} onChange={e=>setForm(p=>({...p,cardName:e.target.value}))} placeholder="e.g. Silver Battlefoil" style={S.inp}/>
                )}
              </div>
              <div>
                <label style={S.lbl}>Card Type</label>
                <select value={form.cardType} onChange={e=>setForm(p=>({...p,cardType:e.target.value}))} style={{...S.inp,cursor:"pointer"}}>
                  {POOL_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={S.lbl}>Starting Qty</label>
                <input type="number" min="0" value={form.totalQty} onChange={e=>setForm(p=>({...p,totalQty:e.target.value}))} style={S.inp} disabled={editing!=="new"}/>
              </div>
              <div>
                <label style={S.lbl}>Cost Per Card ($)</label>
                <input type="number" step="0.01" value={form.costPerCard} onChange={e=>setForm(p=>({...p,costPerCard:e.target.value}))} style={S.inp}/>
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:10, marginBottom:10 }}>
              <div>
                <label style={S.lbl}>Market Value Per Card ($)</label>
                <input type="number" step="0.01" value={form.marketValue} onChange={e=>setForm(p=>({...p,marketValue:e.target.value}))} style={S.inp}/>
              </div>
              <div>
                <label style={S.lbl}>Notes</label>
                <input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Optional notes" style={S.inp}/>
              </div>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <Btn onClick={savePool} variant="green" disabled={!form.cardName.trim()}>{"\uD83D\uDCBE Save Pool"}</Btn>
              <Btn onClick={cancelEdit} variant="ghost">Cancel</Btn>
            </div>
          </div>
        );
      })()}

      {/* Pool list by type */}
      {POOL_TYPES.map(type => {
        const pools = poolsByType[type] || [];
        const cc = CC[type] || { text:"#888", bg:"var(--bz-s1)", border:"#333" };
        return (
          <div key={type}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <span style={{ fontWeight:800, color:cc.text, fontSize:13, textTransform:"uppercase", letterSpacing:1 }}>{type}</span>
              <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{pools.length} pool{pools.length!==1?"s":""}</span>
            </div>
            {pools.length === 0
              ? <div style={{ color:"var(--bz-ink-3)", fontSize:12, padding:"10px 0" }}>No {type} pools yet</div>
              : <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {pools.map(p => {
                    const ec      = getEffectiveCounts(p);
                    const avail   = ec.avail;
                    const pct     = ec.total > 0 ? (avail/ec.total)*100 : 0;
                    const statusC = avail > 100 ? "#4ade80" : avail > 30 ? "#FBBF24" : "#E8317A";
                    return (
                      <div key={p.id} style={{ ...S.card, display:"grid", gridTemplateColumns:"1fr auto auto auto auto auto auto", gap:12, alignItems:"center", padding:"12px 16px" }}>
                        <div>
                          <div style={{ fontWeight:800, fontSize:14, color:"var(--bz-ink)" }}>{p.cardName}</div>
                          {ec.invTotal > 0 && <div style={{ fontSize:11, color:"#7B9CFF", marginTop:2 }}>📦 {ec.invTotal} from inventory ({ec.invAvail} avail{ec.invInTransit>0?`, ${ec.invInTransit} in transit`:""})</div>}
                          {p.notes && <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>{p.notes}</div>}
                        </div>
                        <div style={{ textAlign:"center" }}>
                          <div style={{ fontSize:20, fontWeight:900, color:"var(--bz-ink)" }}>{ec.total}</div>
                          <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Total</div>
                        </div>
                        <div style={{ textAlign:"center" }}>
                          <div style={{ fontSize:20, fontWeight:900, color:ec.used>0?"#E8317A":"#333" }}>{ec.used}</div>
                          <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Used</div>
                        </div>
                        {ec.invInTransit > 0 && (
                          <div style={{ textAlign:"center" }}>
                            <div style={{ fontSize:20, fontWeight:900, color:"#FBBF24" }}>{ec.invInTransit}</div>
                            <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Transit</div>
                          </div>
                        )}
                        <div style={{ textAlign:"center" }}>
                          <div style={{ fontSize:20, fontWeight:900, color:statusC }}>{avail}</div>
                          <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Avail</div>
                        </div>
                        {canSeeFinancials && p.costPerCard>0 && (
                          <div style={{ textAlign:"center" }}>
                            <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink-2)" }}>${parseFloat(p.costPerCard).toFixed(2)}</div>
                            <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Cost/Card</div>
                          </div>
                        )}
                        {/* Progress bar */}
                        <div style={{ width:80 }}>
                          <div style={{ background:"var(--bz-s2)", borderRadius:4, height:6, overflow:"hidden" }}>
                            <div style={{ width:`${Math.max(0,Math.min(100,pct))}%`, height:"100%", background:statusC, borderRadius:4, transition:"width 0.3s" }}/>
                          </div>
                          <div style={{ fontSize:9, color:"var(--bz-ink-3)", marginTop:3, textAlign:"center" }}>{pct.toFixed(0)}% left</div>
                        </div>
                        <div style={{ display:"flex", gap:6 }}>
                          {isAdmin && <button onClick={async()=>{ if(window.confirm(`Reset ${p.cardName} to 0? This clears total and used qty.`)) { await onSavePool({ ...p, totalQty:0, usedQty:0 }); }}} style={{ background:"none", border:"1px solid #FBBF2444", color:"#FBBF24", borderRadius:6, padding:"3px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }} title="Reset qty to 0">{"\u21BA Reset"}</button>}
                          {isAdmin && <button onClick={()=>startEdit(p)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:6, padding:"3px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{"\u270F\uFE0F"}</button>}
                          {isAdmin && <button onClick={()=>{ if(window.confirm(`Delete ${p.cardName} pool?`)) onDeletePool(p.id); }} style={{ background:"none", border:"1px solid #E8317A33", color:"#E8317A", borderRadius:6, padding:"3px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDDD1"}</button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
          </div>
        );
      })}

      {cardPools.length === 0 && !editing && (
        <div style={{ ...S.card, textAlign:"center", padding:"60px 40px", color:"var(--bz-ink-3)" }}>
          <div style={{ fontSize:32, marginBottom:12 }}>{"\uD83D\uDDC3"}</div>
          <div>No card pools yet -- click <strong style={{color:"#E8317A"}}>+ New Pool</strong> to create your first pool</div>
        </div>
      )}
    </div>
  );
}

function Inventory({ defaultTab="cards", inventory, breaks, onRemove, onBulkRemove, onSaveCardCost, onPutBack, onAdd, onBulkAdd, user, userRole, streams=[], lotTracking={}, onSaveLotTracking, lotNotes={}, onSaveLotNotes, onDeleteLot, shipments=[], productUsage=[], onSaveShipment, onDeleteShipment, skuPrices={}, onSaveSkuPrices, skuPriceHistory=[], onDeleteProductUsage, cardPools=[], onSavePool, onDeletePool, onLogPoolOut, onAddToPool, bobaCards=[] }) {
  const canSeeFinancials = ["Admin"].includes(userRole?.role);
  const [trackingEdit,   setTrackingEdit]   = useState(null);
  const [lotStatusFilter, setLotStatusFilter] = useState("all");
  const [expandedLot,    setExpandedLot]    = useState(null);
  const [trackingForm,   setTrackingForm]   = useState({ carrier:"", trackingNum:"", status:"", eta:"", notes:"" });
  const [poCopied,       setPoCopied]       = useState(false);

  const [search,   setSearch]   = useState("");
  const debouncedSearch = useDebounce(search, 250);
  const [typeF,    setTypeF]    = useState("");
  const [statusF,   setStatusF]   = useState("all");
  const [locationF, setLocationF] = useState("all");

  const transitCards = inventory.filter(c => c.cardStatus === "in_transit");
  const transitCount = transitCards.length;
  const [sortInv,  setSortInv]  = useState("date");
  const [logOutCard, setLogOutCard] = useState(null);
  const [logOutForm, setLogOutForm] = useState({ breaker:BREAKERS[0], date:todayLocal(), usage:"Giveaway" });
  const [bulkLogMode, setBulkLogMode] = useState(false);
  const [bulkLogSel,  setBulkLogSel]  = useState(new Set());
  const [bulkLogForm, setBulkLogForm] = useState({ breaker:BREAKERS[0], date:todayLocal(), usage:"Giveaway" });
  const [selected, setSelected] = useState(new Set());
  const [invTab,   setInvTab]   = useState(() => {
    // Non-admins can't see pools/product/lots — fall back to cards.
    if (["pools","product","lots"].includes(defaultTab) && !["Admin","Procurement"].includes(userRole?.role)) return "cards";
    return defaultTab;
  });
  useEffect(()=>{setInvTab(defaultTab);},[defaultTab]);
  const [editCostId,  setEditCostId]  = useState(null);
  const [editCostVal, setEditCostVal] = useState("");
  const usedIds  = new Set(breaks.map(b => b.inventoryId));
  const filtered = useMemo(() => inventory.filter(c => {
    const mn      = c.cardName?.toLowerCase().includes(debouncedSearch.toLowerCase());
    const mt      = !typeF || c.cardType===typeF;
    const used    = usedIds.has(c.id);
    const transit = !used && c.cardStatus === "in_transit";
    const ms      = statusF==="available"   ? (!used && !transit)
                  : statusF==="in_transit"  ? transit
                  : statusF==="used"        ? used
                  : true;
    const ml      = locationF==="all" || (c.location||"BZKA HQ") === locationF;
    return mn && mt && ms && ml;
  }).sort((a,b) => {
    if (sortInv==="name")    return (a.cardName||"").localeCompare(b.cardName||"");
    if (sortInv==="mv_desc") return (b.marketValue||0)-(a.marketValue||0);
    if (sortInv==="mv_asc")  return (a.marketValue||0)-(b.marketValue||0);
    if (sortInv==="cost_desc") return (b.costPerCard||0)-(a.costPerCard||0);
    if (sortInv==="cost_asc")  return (a.costPerCard||0)-(b.costPerCard||0);
    if (sortInv==="type")    return (a.cardType||"").localeCompare(b.cardType||"");
    return new Date(b.dateAdded||0)-new Date(a.dateAdded||0);
  }), [inventory, debouncedSearch, typeF, statusF, locationF, sortInv, breaks]);
  function toggleSelect(id) { setSelected(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; }); }
  function toggleAll() { setSelected(selected.size===filtered.length ? new Set() : new Set(filtered.map(c=>c.id))); }
  function handleBulkDelete() {
    if (selected.size===0) return;
    if (window.confirm(`Delete ${selected.size} card${selected.size!==1?"s":""}? Cannot be undone.`)) { onBulkRemove([...selected]); setSelected(new Set()); }
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* Restock Alerts */}
      {(() => {
        return null; // Inventory low-stock alerts removed per request
        // eslint-disable-next-line no-unreachable
        const USAGE_TO_CT2 = { "Giveaway":"Giveaway Cards","Insurance":"Insurance Cards","First-Timer Pack":"First-Timer Cards","Chaser Pull":"Chaser Cards","Chaser":"Chaser Cards" };
        const totalStreams = streams.length || 1;
        const burnPerStream = {};
        CARD_TYPES.forEach(ct=>{ burnPerStream[ct]=0; });
        breaks.forEach(b=>{ const ct=USAGE_TO_CT2[b.usage]||b.cardType; if(ct&&burnPerStream[ct]!==undefined) burnPerStream[ct]+=b.isPoolLog?(parseInt(b.qty)||1):1; });
        CARD_TYPES.forEach(ct=>{ burnPerStream[ct]=burnPerStream[ct]/totalStreams; });

        // Build used IDs set (fast)
        const usedIds = new Set(breaks.filter(b=>!b.isPoolLog).map(b=>b.inventoryId));

        const invAvail = {};
        CARD_TYPES.forEach(ct=>{
          // Individual inventory cards available (not used, not in transit)
          const indiv = inventory.filter(c=>
            c.cardType===ct &&
            !usedIds.has(c.id) &&
            c.cardStatus !== "in_transit"
          ).length;
          // Pool cards available (pool totalQty - usedQty from pool logs)
          const pool = cardPools.filter(p=>p.cardType===ct).reduce((s,p)=>s+Math.max(0,(parseInt(p.totalQty)||0)-(parseInt(p.usedQty)||0)),0);
          invAvail[ct] = indiv + pool;
        });

        const MONTHLY_TARGETS = { "Giveaway Cards":2000, "Insurance Cards":2000, "First-Timer Cards":50, "Chaser Cards":30 };
        const alerts = CARD_TYPES.map(ct=>{
          const avail   = invAvail[ct];
          const target  = MONTHLY_TARGETS[ct] || 0;
          const burn    = burnPerStream[ct];
          const runsOut = burn > 0 ? Math.floor(avail / burn) : 999;
          const urgent  = avail < (target * 0.25);
          const low     = avail < (target * 0.50);
          if (!low && !urgent) return null;
          return { ct, avail, target, runsOut, urgent };
        }).filter(Boolean);
        if (alerts.length === 0) return null;
        return (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {alerts.map(({ct,avail,target,runsOut,urgent})=>{
              const pct = target>0 ? Math.min(100,avail/target*100) : 100;
              return (
                <div key={ct} style={{ background:urgent?"rgba(220,38,38,0.06)":"rgba(251,191,36,0.05)", border:`1.5px solid ${urgent?"rgba(220,38,38,0.3)":"rgba(251,191,36,0.25)"}`, borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:16 }}>{urgent?"🚨":"⚠️"}</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:800, color:urgent?"#ef4444":"#FBBF24" }}>{ct.replace(" Cards","")} {urgent?"critically low":"running low"}</div>
                      <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>{avail.toLocaleString()} available · {target.toLocaleString()} monthly target · ~{runsOut} streams of runway</div>
                    </div>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:120, height:6, background:"rgba(255,255,255,0.06)", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:urgent?"#ef4444":"#FBBF24", borderRadius:3 }}/>
                    </div>
                    <span style={{ fontSize:12, fontWeight:700, color:urgent?"#ef4444":"#FBBF24" }}>{pct.toFixed(0)}%</span>
                    <button onClick={()=>setInvTab("cards")} style={{ background:"transparent", border:`1px solid ${urgent?"rgba(220,38,38,0.3)":"rgba(251,191,36,0.25)"}`, color:urgent?"#ef4444":"#FBBF24", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>View Cards →</button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      <div style={S.card}>
        <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
          {[["cards","\uD83D\uDCE6 Cards"],...(["Admin","Procurement"].includes(userRole?.role)?[["pools","\uD83D\uDDC3 Card Pools"],["lots","\uD83D\uDDC2 Lot History"],["product","\uD83C\uDF81 Product"]]:[])].map(([id,label]) => (
            <button key={id} onClick={()=>setInvTab(id)} style={{ background:invTab===id?"#1A1A2E":"transparent", color:invTab===id?"#E8317A":"var(--bz-ink-2)", border:`1.5px solid ${invTab===id?"#E8317A":"var(--bz-line)"}`, borderRadius:8, padding:"6px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{label}</button>
          ))}
        </div>

        {invTab==="lots" && (() => {
          const lots = {};
          inventory.forEach(c => {
            // Normalize date to YYYY-MM-DD so locale dates (5/28/2026) and ISO dates group together
            let normDate = c.date || "Unknown";
            if (normDate !== "Unknown") {
              const d = new Date(normDate);
              if (!isNaN(d)) normDate = d.toISOString().split("T")[0];
            }
            const sellerName = c.seller?.trim() || "Unknown Seller";
            const key = `${sellerName}__${normDate}`;
            if (!lots[key]) lots[key] = { key, seller:sellerName, date:normDate, source:c.source||"--", payment:c.payment||"--", lotPaid:c.lotTotalPaid||0, cards:[], addedBy:c.addedBy||"--" };
            lots[key].cards.push(c);
          });
          const lotList = Object.values(lots).sort((a,b) => new Date(b.date)-new Date(a.date));
          const filteredLots = lotStatusFilter==="all" ? lotList :
            lotStatusFilter==="delivered"  ? lotList.filter(l=>lotTracking[l.key]?.status==="Delivered") :
            lotStatusFilter==="in_transit" ? lotList.filter(l=>["In Transit","Shipped","Out for Delivery","Label Created"].includes(lotTracking[l.key]?.status)) :
            lotList.filter(l=>!lotTracking[l.key]?.status||lotTracking[l.key]?.status==="Ordered");

          // Find any note keys that don't match a current lot key (orphaned from old format)
          const currentLotKeys = new Set(lotList.map(l => l.key));
          const orphanedNotes  = Object.entries(lotNotes).filter(([k]) => !currentLotKeys.has(k));
          function migrateNotes() {
            let fixed = 0;
            const promises = [];
            for (const [oldKey, noteData] of orphanedNotes) {
              const sellerName = oldKey.split("__")[0];
              const match = lotList.find(l => l.seller.toLowerCase() === sellerName.toLowerCase());
              if (match && !lotNotes[match.key]) {
                promises.push(onSaveLotNotes(match.key, noteData.notes));
                fixed++;
              }
            }
            Promise.all(promises).then(() => alert(`Migrated ${fixed} note${fixed!==1?"s":""} to correct lot keys.`));
          }

          const CARRIERS = ["USPS","UPS","FedEx","DHL","Other"];
          const TRACKING_STATUSES = ["Ordered","Label Created","Shipped","In Transit","Out for Delivery","Delivered","Exception"];
          const STATUS_COLORS = {
            "Ordered":            { bg:"#F3F4F6", color:"var(--bz-ink-2)" },
            "Label Created":      { bg:"#EEF0FB", color:"var(--bz-ink)" },
            "Shipped":            { bg:"#FFF0CC", color:"var(--bz-ink-2)" },
            "In Transit":         { bg:"#E0F7F4", color:"#0D6E6E" },
            "Out for Delivery":   { bg:"#FCE8F3", color:"#8B1A5A" },
            "Delivered":          { bg:"#D6F4E3", color:"#E8317A" },
            "Exception":          { bg:"#FEE2E2", color:"#E8317A" },
          };

          return (
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginTop:12 }}>
              {/* Filter bar */}
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:4 }}>
                <span style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Filter:</span>
                {[["all","All Lots"],["in_transit","🚚 In Transit"],["delivered","✅ Delivered"],["other","📦 Other"]].map(([val,label])=>{
                  const count = val==="all" ? lotList.length :
                    val==="in_transit" ? lotList.filter(l=>["In Transit","Shipped","Out for Delivery","Label Created"].includes(lotTracking[l.key]?.status)).length :
                    val==="delivered"  ? lotList.filter(l=>lotTracking[l.key]?.status==="Delivered").length :
                    lotList.filter(l=>!lotTracking[l.key]?.status||lotTracking[l.key]?.status==="Ordered").length;
                  const active = lotStatusFilter===val;
                  return (
                    <button key={val} onClick={()=>setLotStatusFilter(val)}
                      style={{ background:active?"rgba(232,49,122,0.15)":"transparent", color:active?"#E8317A":"#555", border:`1.5px solid ${active?"#E8317A":"#333"}`, borderRadius:20, padding:"5px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                      {label} <span style={{color:active?"#E8317A":"#444"}}>{count}</span>
                    </button>
                  );
                })}
              </div>
              {orphanedNotes.length > 0 && CAN_DELETE.includes(userRole?.role) && (
                <div style={{ marginBottom:12, padding:"10px 16px", background:"var(--bz-s1)", border:"1.5px solid #92400e33", borderRadius:9, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
                  <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>{"\u26A0"}{orphanedNotes.length} note{orphanedNotes.length!==1?"s":""} from previous lots couldn't be matched automatically.</span>
                  <button onClick={migrateNotes} style={{ background:"#92400e", color:"#fff", border:"none", borderRadius:7, padding:"5px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>Fix Now</button>
                </div>
              )}
              {filteredLots.length===0
                ? <div style={{ textAlign:"center", color:"var(--bz-ink-2)", padding:"40px 0" }}>{lotStatusFilter==="all"?"No lots yet":"No lots match this filter"}</div>
                : filteredLots.map((lot,i) => {
                    const usedInLot    = lot.cards.filter(c=>usedIds.has(c.id)).length;
                    const transitInLot = lot.cards.filter(c=>!usedIds.has(c.id) && c.cardStatus==="in_transit").length;
                    const availInLot   = lot.cards.length - usedInLot - transitInLot;
                    const tracking  = lotTracking[lot.key] || {};
                    const isEditing = trackingEdit === lot.key;
                    const sc        = STATUS_COLORS[tracking.status] || { bg:"#F3F4F6", color:"var(--bz-ink-2)" };

                    return (
                      <div key={i} style={{ border:"1px solid var(--bz-line-2)", borderRadius:10, overflow:"hidden", background:"var(--bz-s1)" }}>
                        {/* Lot header */}
                        <div style={{ padding:"14px 18px", background:"var(--bz-s1)" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                            <div><span style={{ fontWeight:700, fontSize:14, color:"var(--bz-ink)" }}>{lot.seller}</span><span style={{ color:"var(--bz-ink-2)", fontSize:12, marginLeft:10 }}>{lot.date}</span></div>
                            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                              <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>{lot.source}{canSeeFinancials?` · ${lot.payment}`:""}</span>
                              {canSeeFinancials && <span style={{ fontWeight:700, color:"#E8317A" }}>${lot.lotPaid.toFixed(2)}</span>}
                              {CAN_DELETE.includes(userRole?.role) && (
                                <button
                                  onClick={() => onDeleteLot(lot.key, lot.cards.map(c=>c.id))}
                                  style={{ background:"var(--bz-s1)", color:"#E8317A", border:"1.5px solid #fca5a5", borderRadius:7, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}
                                  title="Delete entire lot">{"\uD83D\uDDD1 Delete Lot"}</button>
                              )}
                              <button onClick={()=>setExpandedLot(expandedLot===lot.key?null:lot.key)}
                                style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:7, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                                {expandedLot===lot.key?"▲ Hide Cards":"▼ See Cards"}
                              </button>
                            </div>
                          </div>
                          <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginBottom:8 }}>
                            <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Total: <strong style={{color:"var(--bz-ink)"}}>{lot.cards.length}</strong></span>
                            <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Available: <strong style={{color:"#E8317A"}}>{availInLot}</strong></span>
                            {transitInLot > 0 && <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>In Transit: <strong style={{color:"var(--bz-ink)"}}>{"\uD83D\uDE9A"}{transitInLot}</strong></span>}
                            <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Used: <strong style={{color:"#E8317A"}}>{usedInLot}</strong></span>
                            <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Added by: <strong style={{color:"var(--bz-ink)"}}>{lot.addedBy}</strong></span>
                            {lot.location && <span style={{ fontSize:11, fontWeight:700, color:"#7B9CFF", background:"rgba(123,156,255,0.1)", borderRadius:4, padding:"2px 8px" }}>📍 {lot.location}</span>}
                          </div>
                          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                            {CARD_TYPES.map(ct => { const count=lot.cards.filter(c=>c.cardType===ct).length; if(!count) return null; const cc=CC[ct]; return <span key={ct} style={{ background:cc.bg, color:cc.text, border:`1px solid ${cc.border}44`, borderRadius:5, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{ct}: {count}</span>; })}
                          </div>
                        </div>

                        {/* Tracking bar */}
                        <div style={{ borderTop:"1px solid var(--bz-line)", padding:"10px 18px", background:"var(--bz-s1)" }}>
                          {!isEditing ? (
                            <div>
                              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, marginBottom: (tracking.eta||tracking.lastEvent||tracking.lastEvent) ? 8 : 0 }}>
                                <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                                  <span style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1 }}>{"\uD83D\uDCE6 Tracking"}</span>
                                  {tracking.trackingNum || tracking.status
                                    ? <>
                                        {tracking.status && <span style={{ background:sc.bg, color:sc.color, border:`1px solid ${sc.color}33`, borderRadius:5, padding:"2px 10px", fontSize:12, fontWeight:700 }}>{tracking.status}</span>}
                                        {tracking.carrier && <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>{tracking.carrier}</span>}
                                        {tracking.trackingNum && (() => {
                                            const num = tracking.trackingNum;
                                            const CURL = {
                                              USPS:  `https://tools.usps.com/go/TrackConfirmAction?tLabels=${num}`,
                                              UPS:   `https://www.ups.com/track?tracknum=${num}`,
                                              FedEx: `https://www.fedex.com/fedextrack/?tracknumbers=${num}`,
                                              DHL:   `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${num}`,
                                            };
                                            const url = CURL[tracking.carrier] || `https://www.google.com/search?q=${encodeURIComponent((tracking.carrier||'')+" tracking "+num)}`;
                                            return <a key="tlink" href={url} target="_blank" rel="noreferrer" style={{ fontSize:12, color:"#E8317A", fontWeight:700, fontFamily:"monospace", textDecoration:"none" }}>{num} ↗</a>;
                                          })()}
                                        {tracking.lastChecked && <span style={{ fontSize:10, color:"var(--bz-ink-2)" }}>· checked {new Date(tracking.lastChecked).toLocaleString()}</span>}
                                      </>
                                    : <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>No tracking added yet</span>
                                  }
                                </div>
                                <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                                  {tracking.trackingNum && tracking.status !== "Delivered" && (
                                    <button
                                      onClick={() => onSaveLotTracking(lot.key, { ...tracking, status:"Delivered" })}
                                      style={{ background:"#166534", color:"#fff", border:"1.5px solid #14532d", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}
                                    >{"\u2705 Mark Delivered"}</button>
                                  )}
                                  {tracking.status === "Delivered" && (
                                    <button
                                      onClick={() => onSaveLotTracking(lot.key, { ...tracking, status:"In Transit" })}
                                      style={{ background:"var(--bz-s1)", color:"#E8317A", border:"1.5px solid #fca5a5", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}
                                    >{"\u21A9 Undo Delivered"}</button>
                                  )}
                                  <button
                                    onClick={() => { setTrackingEdit(lot.key); setTrackingForm({ carrier:tracking.carrier||"", trackingNum:tracking.trackingNum||"", status:tracking.status||"", eta:tracking.eta||"", notes:tracking.notes||"" }); }}
                                    style={{ background:"transparent", border:"1.5px solid #E8317A", color:"#E8317A", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}
                                  >{tracking.trackingNum ? "\u270F\uFE0F Edit" : "+ Add Tracking"}</button>
                                </div>
                              </div>
                              {/* ETA + last event row */}
                              {(tracking.eta || tracking.lastEvent) && (
                                <div style={{ display:"flex", gap:16, flexWrap:"wrap", padding:"8px 12px", background:"var(--bz-s1)", borderRadius:7, marginTop:4 }}>
                                  {tracking.eta && (
                                    <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>
                                      {"\uD83D\uDCC5 Est. Delivery:"}<strong style={{ color: tracking.status==="Delivered" ? "#166534" : "#1B4F8A" }}>{tracking.eta}</strong>
                                    </span>
                                  )}
                                  {tracking.lastEvent && (
                                    <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>
                                      {"\uD83D\uDCCD"}{tracking.lastLocation && <strong style={{color:"var(--bz-ink)"}}>{tracking.lastLocation} -- </strong>}{tracking.lastEvent}
                                    </span>
                                  )}

                                </div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <div style={{ fontSize:11, fontWeight:700, color:"#E8317A", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>{"\uD83D\uDCE6 Edit Tracking"}</div>
                              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                                <div>
                                  <label style={S.lbl}>Carrier</label>
                                  <select value={trackingForm.carrier} onChange={e=>setTrackingForm(p=>({...p,carrier:e.target.value}))} style={{ ...S.inp, cursor:"pointer", color:trackingForm.carrier?"var(--bz-ink)":"var(--bz-ink-2)" }}>
                                    <option value="">Select...</option>
                                    {CARRIERS.map(c=><option key={c} value={c}>{c}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label style={S.lbl}>Tracking Number</label>
                                  <input value={trackingForm.trackingNum} onChange={e=>setTrackingForm(p=>({...p,trackingNum:e.target.value, status:p.status||"In Transit"}))} placeholder="e.g. 9400111899..." style={S.inp}/>
                                </div>
                                <div>
                                  <label style={S.lbl}>Status</label>
                                  <select value={trackingForm.status} onChange={e=>setTrackingForm(p=>({...p,status:e.target.value}))} style={{ ...S.inp, cursor:"pointer", color:trackingForm.status?"var(--bz-ink)":"var(--bz-ink-2)" }}>
                                    <option value="">Select...</option>
                                    {TRACKING_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
                                  </select>
                                </div>

                              </div>

                              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                                <button
                                  onClick={() => { onSaveLotTracking(lot.key, trackingForm); setTrackingEdit(null); }}
                                  style={{ background:"#166534", color:"#fff", border:"1.5px solid #14532d", borderRadius:8, padding:"7px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}
                                >{"\uD83D\uDCBE Save Tracking"}</button>

                                <button
                                  onClick={() => setTrackingEdit(null)}
                                  style={{ background:"var(--bz-s1)", color:"var(--bz-ink-2)", border:"1.5px solid #E5E7EB", borderRadius:8, padding:"7px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}
                                >Cancel</button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Expanded card list */}
                        {expandedLot===lot.key && (
                          <div style={{ borderTop:"1px solid var(--bz-line)", padding:"12px 18px", background:"var(--bz-bg)" }}>
                            <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>Cards in this lot ({lot.cards.length})</div>
                            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                              {lot.cards.map(c => {
                                const isUsed    = usedIds.has(c.id);
                                const isTransit = !isUsed && c.cardStatus==="in_transit";
                                const cc = CC[c.cardType]||{bg:"var(--bz-s1)",text:"#888",border:"#333"};
                                const statusColor = isUsed?"#555":isTransit?"#7B9CFF":"#4ade80";
                                const statusLabel = isUsed?"Used":isTransit?"🚚 Transit":"✅ Avail";
                                return (
                                  <div key={c.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"7px 10px", background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:7, gap:8 }}>
                                    <div style={{ display:"flex", alignItems:"center", gap:8, flex:1, minWidth:0 }}>
                                      <span style={{ fontWeight:700, fontSize:12, color:isUsed?"#444":"var(--bz-ink)", textDecoration:isUsed?"line-through":"none", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.cardName||"Unnamed"}</span>
                                      <span style={{ background:cc.bg, color:cc.text, border:`1px solid ${cc.border}44`, borderRadius:4, padding:"1px 6px", fontSize:10, fontWeight:700, whiteSpace:"nowrap", flexShrink:0 }}>{(c.cardType||"").replace(" Cards","")}</span>
                                    </div>
                                    <div style={{ display:"flex", gap:10, alignItems:"center", flexShrink:0 }}>
                                      {c.location && <span style={{ fontSize:10, fontWeight:700, color:"#7B9CFF", background:"rgba(123,156,255,0.1)", borderRadius:4, padding:"1px 7px", whiteSpace:"nowrap" }}>📍 {c.location}</span>}
                                      {canSeeFinancials && c.costPerCard>0 && <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>${parseFloat(c.costPerCard).toFixed(2)}</span>}
                                      <span style={{ fontSize:11, fontWeight:700, color:statusColor }}>{statusLabel}</span>
                                      {isUsed && (() => { const b = breaks.find(x=>x.inventoryId===c.id); return b ? <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>by {b.loggedBy||b.breaker||"?"}{b.location?` · 📍 ${b.location}`:""}</span> : null; })()}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                      </div>
                    );
                  })
              }
            </div>
          );
        })()}

        {invTab==="aging" && (() => {
          const agingCards = inventory.filter(c => {
            if (usedIds.has(c.id)) return false;
            const d = c.dateAdded ? Math.floor((new Date()-new Date(c.dateAdded))/86400000) : null;
            return d !== null && d >= 30;
          }).sort((a,b) => new Date(a.dateAdded)-new Date(b.dateAdded));
          return (
            <div style={{ marginTop:12 }}>
              {agingCards.length===0
                ? <div style={{ textAlign:"center", color:"var(--bz-ink-2)", padding:"40px 0" }}>{"\uD83C\uDF89 No aging cards!"}</div>
                : agingCards.map(c => {
                    const d   = Math.floor((new Date()-new Date(c.dateAdded))/86400000);
                    const cc  = CC[c.cardType]||{bg:"#F3F4F6",text:"var(--bz-ink-3)"};
                    const urg = d>=90?{bg:"#FEE2E2",color:"#E8317A"}:d>=60?{bg:"#FEF3C7",color:"var(--bz-ink-2)"}:{bg:"#F9FAFB",color:"var(--bz-ink-2)"};
                    return (
                      <div key={c.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:urg.bg, border:`1px solid ${urg.color}22`, borderRadius:8, marginBottom:6 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <span style={{ fontWeight:700, color:"var(--bz-ink)" }}>{c.cardName}</span>
                          <Badge bg={cc.bg} color={cc.text}>{c.cardType}</Badge>
                        </div>
                        <span style={{ fontWeight:700, color:urg.color, fontSize:13 }}>{d} days in stock</span>
                      </div>
                    );
                  })
              }
            </div>
          );
        })()}
      </div>

      {invTab==="customers" && <Sellers inventory={inventory} breaks={breaks} userRole={userRole}/>}
      {invTab==="product" && ["Admin","Procurement"].includes(userRole?.role) && <ProductInventory shipments={shipments} productUsage={productUsage} onSaveShipment={onSaveShipment} onDeleteShipment={onDeleteShipment} onDeleteProductUsage={onDeleteProductUsage} user={user} userRole={userRole} skuPrices={skuPrices} onSaveSkuPrices={onSaveSkuPrices} streams={streams} skuPriceHistory={skuPriceHistory}/>}

      {invTab==="pools" && ["Admin","Procurement"].includes(userRole?.role) && <CardPools cardPools={cardPools} onSavePool={onSavePool} onDeletePool={onDeletePool} onLogPoolOut={onLogPoolOut} onAddToPool={onAddToPool} userRole={userRole} canSeeFinancials={canSeeFinancials} bobaCards={bobaCards} inventory={inventory} breaks={breaks}/>}

      {invTab==="cards" && <>

        {/* Purchase Order */}
        {(() => {
          const MONTHLY_TARGETS = { "Giveaway Cards":2000, "Insurance Cards":2000, "First-Timer Cards":50, "Chaser Cards":30 };
          const usedSet = new Set(breaks.filter(b=>!b.isPoolLog).map(b=>b.inventoryId));
          const avail = {};
          CARD_TYPES.forEach(ct => {
            const indiv = inventory.filter(c => c.cardType===ct && !usedSet.has(c.id)).length;
            const pool  = cardPools.filter(p=>p.cardType===ct).reduce((s,p)=>s+Math.max(0,(parseInt(p.totalQty)||0)-(parseInt(p.usedQty)||0)),0);
            avail[ct] = indiv + pool;
          });
          const items = CARD_TYPES.map(ct => {
            const have   = avail[ct] || 0;
            const target = MONTHLY_TARGETS[ct] || 0;
            const needed = Math.max(0, target - have);
            const pct    = target > 0 ? (have/target*100) : 100;
            const urgent = pct < 25;
            const low    = pct < 50;
            return { ct, have, target, needed, pct, urgent, low };
          }).filter(i => i.low);

          if (items.length === 0) return null;

          const [copied, setCopied] = [poCopied, setPoCopied];

          const poText = [
            "BAZOOKA BREAKS — CARD PURCHASE ORDER",
            `Generated: ${new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}`,
            "─".repeat(40),
            ...items.map(i => `${i.ct.replace(" Cards","").padEnd(18)} Need: ${i.needed.toLocaleString().padStart(6)}  (have ${i.have.toLocaleString()} of ${i.target.toLocaleString()} monthly target)`),
            "─".repeat(40),
            `Total cards needed: ${items.reduce((s,i)=>s+i.needed,0).toLocaleString()}`,
            "",
            "Sources: Facebook groups, Discord, local/estate sales",
          ].join("\n");

          return (
            <div style={{ background:"rgba(107,45,139,0.06)", border:"1.5px solid rgba(107,45,139,0.25)", borderRadius:12, padding:"16px 18px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:800, color:"#6B2D8B" }}>📋 Purchase Order</div>
                  <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>{items.length} card type{items.length!==1?"s":""} need restocking</div>
                </div>
                <button onClick={()=>{ navigator.clipboard?.writeText(poText); setCopied(true); setTimeout(()=>setCopied(false),2500); }}
                  style={{ background:"rgba(107,45,139,0.15)", color:"#6B2D8B", border:"1px solid rgba(107,45,139,0.3)", borderRadius:8, padding:"7px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  {copied ? "✅ Copied!" : "📋 Copy List"}
                </button>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {items.map(({ct,have,target,needed,pct,urgent}) => {
                  const cc = CC[ct]||{text:"#888",bg:"var(--bz-s1)"};
                  return (
                    <div key={ct} style={{ display:"grid", gridTemplateColumns:"1fr auto auto auto", alignItems:"center", gap:12, padding:"10px 14px", background:"var(--bz-s1)", borderRadius:8, border:`1px solid ${urgent?"rgba(220,38,38,0.2)":"rgba(107,45,139,0.15)"}` }}>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:cc.text }}>{ct.replace(" Cards","")}</div>
                        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>
                          {have.toLocaleString()} on hand · {target.toLocaleString()} monthly target
                        </div>
                        <div style={{ marginTop:6, height:4, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${Math.min(pct,100)}%`, background:urgent?"#ef4444":"#FBBF24", borderRadius:2 }}/>
                        </div>
                      </div>
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>Have</div>
                        <div style={{ fontSize:16, fontWeight:900, color:"var(--bz-ink)" }}>{have.toLocaleString()}</div>
                      </div>
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>Need</div>
                        <div style={{ fontSize:16, fontWeight:900, color:urgent?"#ef4444":"#FBBF24" }}>{needed.toLocaleString()}</div>
                      </div>
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>Stock</div>
                        <div style={{ fontSize:16, fontWeight:900, color:pct<25?"#ef4444":pct<50?"#FBBF24":"#4ade80" }}>{pct.toFixed(0)}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop:12, padding:"10px 14px", background:"var(--bz-bg)", borderRadius:8, fontFamily:"monospace", fontSize:11, color:"var(--bz-ink-3)", whiteSpace:"pre-wrap" }}>
                {poText}
              </div>
            </div>
          );
        })()}
        <div style={S.card}>
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search card name..." style={{ ...S.inp, flex:1, minWidth:180 }}/>
            <select value={typeF} onChange={e=>setTypeF(e.target.value)} style={{ ...S.inp, width:"auto", minWidth:160, color:typeF?"var(--bz-ink)":"var(--bz-ink-2)", cursor:"pointer" }}>
              <option value="">All Types</option>
              {CARD_TYPES.map(ct=><option key={ct} value={ct}>{ct}</option>)}
            </select>
            <select value={sortInv} onChange={e=>setSortInv(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
              <option value="date">Sort: Date Added</option>
              <option value="name">{"Sort: Name A\u2192Z"}</option>
              <option value="type">Sort: Type</option>
              <option value="mv_desc">{"Sort: MV High\u2192Low"}</option>
              <option value="mv_asc">{"Sort: MV Low\u2192High"}</option>
              <option value="cost_desc">{"Sort: Cost High\u2192Low"}</option>
              <option value="cost_asc">{"Sort: Cost Low\u2192High"}</option>
            </select>
            {transitCount > 0 && (
              <div onClick={()=>setStatusF("in_transit")} style={{ display:"flex", alignItems:"center", gap:10, background:"rgba(251,191,36,0.08)", border:"1px solid rgba(251,191,36,0.25)", borderRadius:8, padding:"8px 14px", marginBottom:10, cursor:"pointer" }}>
                <span style={{ fontSize:16 }}>🚚</span>
                <span style={{ fontSize:13, fontWeight:700, color:"#FBBF24" }}>{transitCount} card{transitCount!==1?"s":""} awaiting delivery</span>
                <span style={{ fontSize:11, color:"var(--bz-ink-2)", marginLeft:"auto" }}>Click to view →</span>
              </div>
            )}
            <div style={{ display:"flex", gap:4 }}>
              {[["available","✅ Available"],["in_transit","🚚 In Transit"],["used","🔴 Used"],["all","All"]].map(([val,label]) => (
                <button key={val} onClick={()=>setStatusF(val)} style={{ background:statusF===val?"#1A1A2E":"transparent", color:statusF===val?"#E8317A":"var(--bz-ink-2)", border:`1.5px solid ${statusF===val?"#E8317A":"var(--bz-line)"}`, borderRadius:7, padding:"6px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                  {label}{val==="in_transit"&&transitCount>0?<span style={{ marginLeft:5, background:"#FBBF24", color:"#000", borderRadius:10, padding:"1px 6px", fontSize:10 }}>{transitCount}</span>:""}
                </button>
              ))}
            </div>
            <div style={{ display:"flex", gap:4 }}>
              {[["all","📍 All Locations"],...LOCATIONS.map(l=>[l,l])].map(([val,label])=>(
                <button key={val} onClick={()=>setLocationF(val)}
                  style={{ background:locationF===val?"rgba(123,156,255,0.12)":"transparent", color:locationF===val?"#7B9CFF":"var(--bz-ink-2)", border:`1.5px solid ${locationF===val?"#7B9CFF":"var(--bz-line)"}`, borderRadius:7, padding:"6px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                  {label}
                </button>
              ))}
            </div>
            <span style={{ color:"var(--bz-ink-2)", fontSize:12 }}>{filtered.length} cards</span>
            {selected.size>0 && CAN_DELETE.includes(userRole?.role) && (
              <button onClick={handleBulkDelete} style={{ background:"var(--bz-s1)", color:"#E8317A", border:"1.5px solid #fca5a5", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDDD1 Delete"}{selected.size} selected</button>
            )}
            {selected.size>0 && onBulkAdd && (
              <button onClick={()=>setBulkLogMode(p=>!p)} style={{ background:bulkLogMode?"rgba(74,222,128,0.15)":"var(--bz-s1)", color:"#4ade80", border:"1.5px solid #4ade8044", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                {bulkLogMode?"✕ Cancel":"✅ Bulk Log Out"} {selected.size > 0 ? `${selected.size}` : ""}
              </button>
            )}
          </div>
        </div>

        {/* Bulk Log Out bar */}
        {bulkLogMode && selected.size > 0 && (
          <div style={{ background:"rgba(74,222,128,0.06)", border:"1.5px solid rgba(74,222,128,0.2)", borderRadius:10, padding:"14px 16px", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <span style={{ fontSize:13, fontWeight:700, color:"#4ade80" }}>✅ Log Out {selected.size} card{selected.size!==1?"s":""}</span>
            <select value={bulkLogForm.breaker} onChange={e=>setBulkLogForm(p=>({...p,breaker:e.target.value}))} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
              {BREAKERS.map(b=><option key={b} value={b}>{b}</option>)}
            </select>
            <select value={bulkLogForm.usage} onChange={e=>setBulkLogForm(p=>({...p,usage:e.target.value}))} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
              {USAGE_TYPES.map(u=><option key={u} value={u}>{u}</option>)}
            </select>
            <input type="date" value={bulkLogForm.date} onChange={e=>setBulkLogForm(p=>({...p,date:e.target.value}))} style={{ ...S.inp, width:"auto" }}/>
            <button onClick={()=>{
              const USAGE_TO_CT_INV = {"Giveaway":"Giveaway Cards","Insurance":"Insurance Cards","First-Timer Pack":"First-Timer Cards","Chaser Pull":"Chaser Cards","Chaser":"Chaser Cards"};
              const entries = [...selected].map(id=>{
                const card = inventory.find(c=>c.id===id);
                const resolvedType = USAGE_TO_CT_INV[bulkLogForm.usage] || card?.cardType || "";
                return { id:uid(), date:bulkLogForm.date, breaker:bulkLogForm.breaker, inventoryId:id, cardName:card?.cardName||"", cardType:resolvedType, usage:bulkLogForm.usage, notes:"Bulk logged from Inventory", dateAdded:new Date().toISOString(), loggedBy:user?.displayName||"Unknown", location:card?.location||"" };
              });
              onBulkAdd(entries);
              setSelected(new Set());
              setBulkLogMode(false);
            }} style={{ background:"linear-gradient(135deg,#4ade80,#22d3ee)", color:"#000", border:"none", borderRadius:8, padding:"8px 18px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
              Confirm Log Out
            </button>
          </div>
        )}
        <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:900 }}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width:40, textAlign:"center" }}><input type="checkbox" checked={filtered.length>0&&selected.size===filtered.length} onChange={toggleAll}/></th>
                  {["Card Name","Type",...(canSeeFinancials?["Market Value","Cost/Card","Lot Paid","Payment"]:[]),"Source","Seller","Date","Added By","Status",""].map(h=><th key={h} style={S.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.length===0 ? <EmptyRow msg="No cards yet -- accept a lot comp to add cards." cols={12}/> :
                  filtered.map((c,i) => {
                    const used  = usedIds.has(c.id);
                    const isSel = selected.has(c.id);
                    const cc    = CC[c.cardType]||{bg:"#F3F4F6",text:"var(--bz-ink-3)"};
                    const daysIn = c.dateAdded ? Math.floor((new Date()-new Date(c.dateAdded))/86400000) : null;
                    const isAging = !used && daysIn !== null && daysIn >= 60;
                    return (
                      <tr key={c.id} className="inv-row fade-in" style={{ background:isSel?"#1a0a0f":i%2===0?"var(--bz-s1)":"var(--bz-s1)", opacity:used?0.45:1 }}>
                        <td style={{ ...S.td, textAlign:"center" }}><input type="checkbox" checked={isSel} onChange={()=>toggleSelect(c.id)}/></td>
                        <td style={{ ...S.td, fontWeight:700 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            {c.cardName}
                            {isAging && <span style={{ background:"#1a1400", color:"var(--bz-ink-2)", border:"1px solid #FDE68A", borderRadius:4, padding:"1px 6px", fontSize:10, fontWeight:700 }}>{"\u23F0"}{daysIn}d</span>}
                          </div>
                        </td>
                        <td style={S.td}><Badge bg={cc.bg} color={cc.text}>{c.cardType}</Badge></td>
                        {canSeeFinancials && <>
                          <td style={{ ...S.td, color:"var(--bz-ink-2)", fontWeight:700 }}>${(c.marketValue||0).toFixed(2)}</td>
                          <td style={{ ...S.td, color:"#4ade80", fontWeight:700 }}>${(c.costPerCard||0).toFixed(2)}</td>
                          <td style={{ ...S.td, color:"var(--bz-ink-2)" }}>${(c.lotTotalPaid||0).toFixed(2)}</td>
                          <td style={{ ...S.td, color:"var(--bz-ink-2)", fontSize:12 }}>{c.payment||"--"}</td>
                        </>}
                        <td style={{ ...S.td, color:"var(--bz-ink-2)", fontSize:12 }}>{c.source||"--"}</td>
                        <td style={{ ...S.td, color:"var(--bz-ink-2)", fontSize:12 }}>{c.seller||"--"}</td>
                        <td style={{ ...S.td, color:"var(--bz-ink-2)", fontSize:11 }}>{c.date||"--"}</td>
                        <td style={{ ...S.td, color:"var(--bz-ink-2)", fontSize:12 }}>{c.addedBy||"--"}</td>
                        <td style={S.td}>{used
                          ? <Badge bg="#FEE2E2" color="#991b1b">Used</Badge>
                          : c.cardStatus==="in_transit"
                            ? <Badge bg="#EEF0FB" color="#2C3E7A">{"\uD83D\uDE9A In Transit"}</Badge>
                            : <Badge bg="#D6F4E3" color="#166534">Available</Badge>
                        }</td>
                        <td style={S.td}>
                          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                            {editCostId === c.id ? (
                              <>
                                <input
                                  type="number" step="0.01" autoFocus
                                  value={editCostVal}
                                  onChange={e=>setEditCostVal(e.target.value)}
                                  style={{ ...S.inp, width:80, padding:"3px 6px", fontSize:11 }}
                                  onKeyDown={e=>{
                                    if (e.key==="Enter") {
                                      const newCost = parseFloat(editCostVal)||0;
                                      onRemove(c.id); // we'll need a save handler -- see note below
                                      setEditCostId(null);
                                    }
                                    if (e.key==="Escape") setEditCostId(null);
                                  }}
                                />
                                <button onClick={async()=>{
                                  const newCost = parseFloat(editCostVal)||0;
                                  await onSaveCardCost(c.id, newCost);
                                  setEditCostId(null);
                                }} style={{ background:"#166534", color:"#fff", border:"none", borderRadius:5, padding:"3px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{"\u2713"}</button>
                                <button onClick={()=>setEditCostId(null)} style={{ background:"none", border:"none", color:"var(--bz-ink-2)", cursor:"pointer", fontSize:13 }}>{"\u2715"}</button>
                              </>
                            ) : (
                              <>
                                {canSeeFinancials && <button onClick={()=>{ setEditCostId(c.id); setEditCostVal((c.costPerCard||0).toFixed(2)); }} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:5, padding:"2px 7px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }} title="Edit cost">{"\u270F\uFE0F"}</button>}
                                {!usedIds.has(c.id) && <button onClick={()=>setLogOutCard(c)} style={{ background:"#1a0a0f", border:"1px solid #E8317A44", color:"#E8317A", borderRadius:5, padding:"2px 8px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>{"\uD83D\uDCE4 Log Out"}</button>}
                                {CAN_DELETE.includes(userRole?.role) && <button onClick={()=>onRemove(c.id)} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:14 }}>{"\u2715"}</button>}
                                {usedIds.has(c.id) && onPutBack && CAN_DELETE.includes(userRole?.role) && (
                                  <button onClick={()=>{ if(window.confirm(`Put "${c.cardName}" back in inventory?`)) onPutBack(c.id); }} style={{ background:"#0a1a0a", border:"1px solid #4ade8033", color:"#4ade80", borderRadius:5, padding:"2px 8px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>{"\u21A9 Put Back"}</button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          </div>
        </div>

      {/* Log Out Card Modal */}
      {logOutCard && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={()=>setLogOutCard(null)}>
          <div style={{ background:"var(--bz-s1)", border:"1.5px solid #E8317A44", borderRadius:14, padding:"24px", width:380, maxWidth:"90vw" }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontWeight:800, fontSize:15, color:"var(--bz-ink)", marginBottom:4 }}>{"\uD83D\uDCE4 Log Out Card"}</div>
            <div style={{ fontSize:12, color:"var(--bz-ink-2)", marginBottom:16 }}>{logOutCard.cardName} · {logOutCard.cardType}</div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div>
                <label style={S.lbl}>Breaker</label>
                <select value={logOutForm.breaker} onChange={e=>setLogOutForm(p=>({...p,breaker:e.target.value}))} style={{ ...S.inp, cursor:"pointer" }}>
                  {BREAKERS.map(b=><option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label style={S.lbl}>Date</label>
                <input type="date" value={logOutForm.date} onChange={e=>setLogOutForm(p=>({...p,date:e.target.value}))} style={S.inp}/>
              </div>
              <div>
                <label style={S.lbl}>Usage Type</label>
                <select value={logOutForm.usage} onChange={e=>setLogOutForm(p=>({...p,usage:e.target.value}))} style={{ ...S.inp, cursor:"pointer" }}>
                  <option value="Giveaway">Giveaway</option>
                  <option value="Insurance">Insurance</option>
                  <option value="First-Timer Pack">First-Timer Pack</option>
                  <option value="Chaser Pull">Chaser Pull</option>
                </select>
              </div>
            </div>
            <div style={{ display:"flex", gap:8, marginTop:16 }}>
              <Btn onClick={async()=>{
                const USAGE_TO_CT_MAP = { "Giveaway":"Giveaway Cards", "Insurance":"Insurance Cards", "First-Timer Pack":"First-Timer Cards", "Chaser Pull":"Chaser Cards", "Chaser":"Chaser Cards" };
                const resolvedType = USAGE_TO_CT_MAP[logOutForm.usage] || logOutCard.cardType;
                const entry = { id:uid(), date:logOutForm.date, breaker:logOutForm.breaker, inventoryId:logOutCard.id, cardName:logOutCard.cardName, cardType:resolvedType, usage:logOutForm.usage, notes:"Logged from Inventory", dateAdded:new Date().toISOString(), loggedBy:user?.displayName||"Unknown", location:logOutCard.location||"" };
                if (onAdd) await onAdd(entry);
                setLogOutCard(null);
              }} variant="green">{"\u2705 Log Out"}</Btn>
              <Btn onClick={()=>setLogOutCard(null)} variant="ghost">Cancel</Btn>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}

function BreakLog({ inventory, breaks, onAdd, onBulkAdd, onDeleteBreak, user, userRole, streams=[], onSaveStream, onDeleteStream, productUsage=[], onSaveProductUsage, shipments=[], recapOnly=false, cardsOnly=false, skuPrices={}, onUpsertBuyers, cardPools=[], imcFormUrl="", onSaveImcFormUrl, plannedStreams=[], csvImports=[] }) {
  const canSeeFinancials = ["Admin"].includes(userRole?.role);
  const isAdminOrStreamer = ["Admin","Streamer"].includes(userRole?.role);
  const userName       = user?.displayName || "";
  const matchedBreaker = BREAKERS.find(b => userName.toLowerCase().replace(/\s+/g,"").includes(b.toLowerCase().replace(/\s+/g,""))) || "";
  const [windowWidth,  setWindowWidth]  = useState(window.innerWidth);
  useEffect(()=>{ const h=()=>setWindowWidth(window.innerWidth); window.addEventListener("resize",h,{passive:true}); return()=>window.removeEventListener("resize",h); },[]);
  const isMobile = windowWidth < 768;
  const [breaker,    setBreaker]    = useState(matchedBreaker);
  const [date,       setDate]       = useState("");
  const [cardId,     setCardId]     = useState("");
  const [cardSearch, setCardSearch] = useState("");
  const [usage,      setUsage]      = useState("");
  const [notes,      setNotes]      = useState("");
  const [bulkMode,   setBulkMode]   = useState(false);
  const [bulkSel,    setBulkSel]    = useState(new Set());
  const [histSel,    setHistSel]    = useState(new Set());
  const [chaserSearch, setChaserSearch] = useState("");
  const [streamBulkSel, setStreamBulkSel] = useState(new Set());
  const [streamLogBreaker, setStreamLogBreaker] = useState("");
  const [streamLogMonth, setStreamLogMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`; // default: current month
  });
  const [streamLogCollapsed, setStreamLogCollapsed] = useState(false);

  // Stream recap state
  const EMPTY_RECAP = { grossRevenue:"", whatnotFees:"", coupons:"", whatnotPromo:"", magpros:"", packagingMaterial:"", topLoaders:"", magprosQty:"", packagingQty:"", topLoadersQty:"", chaserCards:"", chaserCardIds:"", marketMultiple:"", newBuyers:"", binOnly:false, isEvent:false, isSinglesShow:false, biguGiveawayCards:"", biguInsuranceCards:"", biguChaserCards:"", biguShipping:"", breakType:"auction", sessionType:"", commissionOverride:"", streamNotes:"", zionRevenue:"", collabPartner:"", collabPct:"", streamSkuPrices:{}, streamName:"", tips:"", salesBonus:"", salesBonusNote:"", imcReimbursement:"", imcReimbNote:"", eventStaff:[], splitRep:"", splitPct:"50", externalChannel:false, channel:"Bazooka Vault", excludeFinancials:false, manualCommission:"" };
  const EMPTY_USAGE = { doubleMega:"", hobby:"", jumbo:"", misc:"", miscNotes:"" };
  const [recap,       setRecap]       = useState(EMPTY_RECAP);
  const [linkedPlanId, setLinkedPlanId] = useState(null);
  const [prodUsage,   setProdUsage]   = useState(EMPTY_USAGE);
  const [recapSaving, setRecapSaving] = useState(false);
  const [recapSaved,  setRecapSaved]  = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const csvJustLoaded = useRef(false);
  const csvDataLoaded = useRef(false);
  const [csvMsg,       setCsvMsg]       = useState(null); // { type: 'success'|'error', text }

  // Check existing product usage for this breaker+date
  const existingUsage = productUsage.find(u => u.breaker === breaker && u.date === date);

  // Check if a stream recap already exists for this breaker+date
  const [editingStreamId, setEditingStreamId] = useState(null);

  // The stream being edited. ONLY set when you explicitly pick one to edit.
  //
  // This used to also match on breaker+date+channel, which meant that picking the breaker and date
  // for a SECOND stream that day would silently load the FIRST one into the form (and mark it
  // saved) — so there was no way to log two streams on the same day. The save path already mints a
  // fresh id for new recaps, so the data was never actually clobbered; the form just wouldn't let
  // you enter one. Same-day streams are normal, so the form now stays empty unless you say
  // otherwise.
  const existingStream = editingStreamId
    ? streams.find(s => s.id === editingStreamId)
    : null;

  // Streams this breaker already has on this date. Surfaced so you can jump into one to edit it,
  // rather than the form guessing for you.
  const sameDayStreams = (breaker && date)
    ? streams.filter(s => s.breaker === breaker && s.date === date)
    : [];

  // Load existing stream into form when breaker/date changes
  useEffect(() => {
    if (csvJustLoaded.current) { csvJustLoaded.current = false; return; }
    if (existingStream) {
      const prodFields = PRODUCT_TYPES.reduce((acc,pt) => { acc[`prod_${pt}`] = existingStream[`prod_${pt}`]||""; return acc; }, {});
      setRecap({ grossRevenue:existingStream.grossRevenue||"", whatnotFees:existingStream.whatnotFees||"", coupons:existingStream.coupons||"", whatnotPromo:existingStream.whatnotPromo||"", magpros:existingStream.magpros||"", packagingMaterial:existingStream.packagingMaterial||"", topLoaders:existingStream.topLoaders||"", magprosQty:existingStream.magprosQty||"", packagingQty:existingStream.packagingQty||"", topLoadersQty:existingStream.topLoadersQty||"", chaserCards:existingStream.chaserCards||"", chaserCardIds:existingStream.chaserCardIds||"", marketMultiple:existingStream.marketMultiple||"", newBuyers:existingStream.newBuyers||"", binOnly:existingStream.binOnly||false, isEvent:existingStream.isEvent||false, isSinglesShow:existingStream.isSinglesShow||false, biguGiveawayCards:existingStream.biguGiveawayCards||"", biguInsuranceCards:existingStream.biguInsuranceCards||"", biguChaserCards:existingStream.biguChaserCards||"", biguShipping:existingStream.biguShipping||"", breakType:existingStream.breakType||"auction", sessionType:existingStream.sessionType||"", commissionOverride:existingStream.commissionOverride||"", streamNotes:existingStream.notes||"", zionRevenue:existingStream.zionRevenue||"", collabPartner:existingStream.collabPartner||"", collabPct:existingStream.collabPct||"", streamSkuPrices:existingStream.streamSkuPrices||{}, streamName:existingStream.streamName||"", tips:existingStream.tips||"", salesBonus:existingStream.salesBonus||"", salesBonusNote:existingStream.salesBonusNote||"", imcReimbursement:existingStream.imcReimbursement||"", imcReimbNote:existingStream.imcReimbNote||"", eventStaff:existingStream.eventStaff||[], splitRep:existingStream.splitRep||"", splitPct:existingStream.splitPct||"50", externalChannel:existingStream.externalChannel||false, channel:existingStream.channel||"Bazooka Vault", excludeFinancials:existingStream.excludeFinancials||false, manualCommission:existingStream.manualCommission||"", ...prodFields });
      setRecapSaved(true);
      csvDataLoaded.current = false;
    } else if (!csvDataLoaded.current) {
      setRecap(EMPTY_RECAP);
      setRecapSaved(false);
      setChaserSearch("");
    }
    if (existingUsage) {
      setProdUsage({ doubleMega:existingUsage.doubleMega||"", hobby:existingUsage.hobby||"", jumbo:existingUsage.jumbo||"", misc:existingUsage.misc||"", miscNotes:existingUsage.miscNotes||"" });
    } else {
      setProdUsage(EMPTY_USAGE);
    }
    // editingStreamId belongs here too: picking a stream to edit must pull it into the form, and
    // clearing the selection must empty the form back out.
  }, [breaker, date, editingStreamId]);

  function rf(k) {
    return v => {
      setRecap(p => {
        const updated = { ...p, [k]: v };
        // When BIN is toggled on, clear the market multiple
        if (k === "binOnly" && v === true) {
          updated.marketMultiple = "";
          return updated;
        }
        // Auto-calculate market multiple when product counts or gross revenue change (not BIN)
        const isProductField = PRODUCT_TYPES.some(pt => k === `prod_${pt}`);
        if ((isProductField || k === "grossRevenue") && !updated.binOnly) {
          const gross = parseFloat(k === "grossRevenue" ? v : updated.grossRevenue) || 0;
          // Gross already includes coupons — do NOT add them back (that double-counts).
          const totalMktVal = PRODUCT_TYPES.reduce((sum, pt) => {
            const qty   = parseInt(k === `prod_${pt}` ? v : updated[`prod_${pt}`]) || 0;
            const price = parseFloat(updated.streamSkuPrices?.[pt] ?? skuPrices[pt]) || 0;
            return sum + (qty * price);
          }, 0);
          if (gross > 0 && totalMktVal > 0) {
            updated.marketMultiple = (gross / totalMktVal).toFixed(2);
          }
        }
        return updated;
      });
      setRecapSaved(false);
    };
  }

  function calcRecap() {
    // Map recap form fields to stream shape, then use global calcStream
    const s = { ...recap, breaker, grossRevenue:recap.grossRevenue, notes:recap.streamNotes };
    return calcStream(s, breaker);
  }

  async function handleSaveRecap() {
    if (!breaker || !date || (canSeeFinancials && !recap.grossRevenue)) return;
    setRecapSaving(true);
    try {
      // Reuse an existing stream ID ONLY when explicitly editing one.
      // A brand-new recap always gets a fresh ID, so a breaker can log two (or more)
      // streams on the same day/channel without one overwriting the other.
      const matchingStream = editingStreamId
        ? streams.find(s => s.id === editingStreamId)
        : null;
      const streamId = matchingStream?.id || uid();
      await onSaveStream({ ...(existingStream||{}), ...recap, notes:recap.streamNotes, streamName:recap.streamName||"", id:streamId, breaker, date });
      // Log selected chaser cards out of inventory
      if (recap.chaserCardIds) {
        const cardIds = recap.chaserCardIds.split(",").filter(Boolean);
        const alreadyLogged = new Set(breaks.filter(b=>b.streamId===streamId).map(b=>b.inventoryId));
        const toLog = cardIds.filter(id => !alreadyLogged.has(id));
        if (toLog.length > 0) {
          const entries = toLog.map(id => {
            const card = inventory.find(c=>c.id===id);
            return { id:uid(), date, breaker, inventoryId:id, cardName:card?.cardName||"", cardType:"Chaser Cards", usage:"Chaser Pull", notes:"Auto-logged from stream recap", streamId, dateAdded:new Date().toISOString(), loggedBy:user?.displayName||"Unknown" };
          });
          if (onBulkAdd) onBulkAdd(entries);
        }
      }
      // Save product usage from recap fields
      const prodFields = PRODUCT_TYPES.reduce((acc,pt) => { const v=parseInt(recap[`prod_${pt}`])||0; if(v>0) acc[pt]=v; return acc; }, {});
      if (Object.keys(prodFields).length > 0 && onSaveProductUsage) {
        await onSaveProductUsage({ id:uid(), streamId, breaker, date, ...prodFields });
      }
      setRecapSaved(true);
      setEditingStreamId(streamId); // lock to this stream for subsequent edits
      csvDataLoaded.current = false;
    } finally { setRecapSaving(false); }
  }

  const rc = calcRecap();
  const hasRecapData = !!(parseFloat(recap.grossRevenue)||0);

  const usedIds   = new Set(breaks.map(b => b.inventoryId));
  const available = inventory.filter(c => !usedIds.has(c.id) && c.cardStatus !== "in_transit");
  const selCard   = inventory.find(c => c.id===cardId);

  const USAGE_TO_CT_LOG = { "Giveaway":"Giveaway Cards", "Insurance":"Insurance Cards", "First-Timer Pack":"First-Timer Cards", "Chaser Pull":"Chaser Cards", "Chaser":"Chaser Cards" };
  function handleAdd() {
    if (!breaker||!cardId) return;
    const resolvedType = USAGE_TO_CT_LOG[usage] || selCard?.cardType || "";
    onAdd({ id:uid(), date, breaker, inventoryId:cardId, cardName:selCard?.cardName||"", cardType:resolvedType, usage, notes, dateAdded:new Date().toISOString(), loggedBy:user?.displayName||"Unknown" });
    setCardId(""); setCardSearch(""); setUsage(""); setNotes("");
  }
  function toggleBulk(id) { setBulkSel(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; }); }
  function handleBulkLog() {
    if (!breaker||bulkSel.size===0) return;
    const entries = [...bulkSel].map(id => {
      const card = inventory.find(c=>c.id===id);
      const resolvedType = USAGE_TO_CT_LOG[usage] || card?.cardType || "";
      return { id:uid(), date, breaker, inventoryId:id, cardName:card?.cardName||"", cardType:resolvedType, usage, notes, dateAdded:new Date().toISOString(), loggedBy:user?.displayName||"Unknown" };
    });
    onBulkAdd(entries); setBulkSel(new Set());
  }
  function toggleHistSel(id) { setHistSel(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; }); }
  function toggleAllHist() { setHistSel(histSel.size===breaks.length ? new Set() : new Set(breaks.map(b=>b.id))); }
  function handleBulkDeleteHist() {
    if (histSel.size===0) return;
    if (window.confirm(`Remove ${histSel.size} break log entr${histSel.size!==1?"ies":"y"}? These cards will become available again.`)) {
      [...histSel].forEach(id => onDeleteBreak(id));
      setHistSel(new Set());
    }
  }

  const sum = {};
  const USAGE_TO_CT = { "Giveaway":"Giveaway Cards", "Insurance":"Insurance Cards", "First-Timer Pack":"First-Timer Cards", "Chaser Pull":"Chaser Cards", "Chaser":"Chaser Cards" };
  BREAKERS.forEach(b => { sum[b]={total:0}; CARD_TYPES.forEach(ct=>{sum[b][ct]=0;}); });
  breaks.forEach(b => { if(sum[b.breaker]){ sum[b.breaker].total++; const ct=USAGE_TO_CT[b.usage]||b.cardType; if(ct)sum[b.breaker][ct]++; } });

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* -- STREAM RECAP -- */}
      {!cardsOnly && <div style={{ ...S.card, border: recapSaved ? "1px solid rgba(52,211,153,0.35)" : "1px solid var(--bz-pink-line)" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:8 }}>
          <SectionLabel t="Stream Recap" />
          {canSeeFinancials && (
          <div style={{ background:"#1a1400", border:"1px solid #FBBF2444", borderRadius:8, padding:"8px 14px", fontSize:12, color:"#FBBF24", fontWeight:600, marginBottom:8 }}>
            {"\u26A0\uFE0F Select your breaker first, then import the CSV"}</div>
          )}
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            {/* Whatnot CSV Import */}
            {canSeeFinancials && (
            <label style={{ display:"flex", alignItems:"center", gap:6, background:"var(--bz-s2)", border:"1.5px solid #E8317A44", borderRadius:8, padding:"5px 12px", fontSize:11, fontWeight:700, color:"#E8317A", cursor:"pointer", whiteSpace:"nowrap" }}>
              {"\uD83D\uDCE5 Whatnot CSV"}<input type="file" accept=".csv" style={{ display:"none" }} onChange={e => {
                const file = e.target.files[0]; if (!file) return;
                const reader = new FileReader();
                reader.onload = ev => {
                  try {
                    const raw = ev.target.result;
                    // Rejoin lines that are split inside quoted fields
                    const fixedLines = [];
                    let current = "";
                    let inQ = false;
                    for (let ci = 0; ci < raw.length; ci++) {
                      const ch = raw[ci];
                      if (ch === '"') inQ = !inQ;
                      if ((ch === '\n' || ch === '\r') && !inQ) {
                        if (current.trim()) fixedLines.push(current.replace(/\r/g,''));
                        current = "";
                      } else {
                        current += ch;
                      }
                    }
                    if (current.trim()) fixedLines.push(current.replace(/\r/g,''));
                    const lines = fixedLines;
                    const rawHeaders = lines[0].split(",").map(h=>h.replace(/"/g,"").toLowerCase().trim());
                    const getIdx = key => rawHeaders.findIndex(h=>h===key);
                    const origIdx   = getIdx("original_item_price");
                    const couponIdx = getIdx("coupon_price");
                    const cancelIdx = getIdx("cancelled_or_failed");
                    const dateIdx   = getIdx("placed_at");
                    const descIdx  = getIdx("product_description");
                    const nameIdx  = getIdx("product_name");
                    const titleIdx = getIdx("listing_title") !== -1 ? getIdx("listing_title") : getIdx("item_name");
                    // Zion Cases: check product_name first (that's where it lives), then description
                    if (origIdx === -1) { alert("Couldn't find original_item_price column. Make sure this is a Whatnot live sales CSV."); return; }
                    let gross=0, coupons=0, zionGross=0, streamDate="", skipped=0;
                    for (let i=1; i<lines.length; i++) {
                      const cols=[]; let cur="", inQuote=false;
                      for (const ch of lines[i]) {
                        if (ch==='"') { inQuote=!inQuote; }
                        else if (ch==="," && !inQuote) { cols.push(cur.trim()); cur=""; }
                        else { cur+=ch; }
                      }
                      cols.push(cur.trim());
                      const cancelVal = (cols[cancelIdx]||"").toLowerCase();
                      if (cancelVal === "true" || cancelVal === "failed") { skipped++; continue; }
                      const itemDesc  = descIdx  !== -1 ? (cols[descIdx]||"")  : "";
                      const itemName  = nameIdx  !== -1 ? (cols[nameIdx]||"")  : "";
                      const itemTitle = titleIdx !== -1 ? (cols[titleIdx]||"") : "";
                      const isZion = itemDesc.toLowerCase().includes("zion") || itemName.toLowerCase().includes("zion") || itemTitle.toLowerCase().includes("zion");
                      const rowGross  = parseFloat(cols[origIdx]||0)||0;
                      const rowCoupon = parseFloat(cols[couponIdx]||0)||0;
                      if (isZion) {
                        zionGross += rowGross + rowCoupon;
                      } else {
                        gross   += rowGross + rowCoupon;
                        coupons += rowCoupon;
                      }
                      if (!streamDate && cols[dateIdx]) streamDate = cols[dateIdx].split(" ")[0];
                    }
                    setRecap(p=>({ ...p, grossRevenue:gross.toFixed(2), coupons:coupons>0?coupons.toFixed(2):p.coupons, zionRevenue:zionGross>0?zionGross.toFixed(2):"" }));
                    csvDataLoaded.current = true;
                    setRecapSaved(false);
                    setCsvMsg({ type:"success", text:`\u2705 Imported! Gross: $${gross.toFixed(2)}${zionGross>0?` · Zion Cases (excluded): $${zionGross.toFixed(2)}`:""}${coupons>0?` · Coupons: $${coupons.toFixed(2)}`:""}${skipped>0?` · ${skipped} cancelled skipped`:""} — enter the stream date manually then fill in fees & expenses.` });
                    setTimeout(()=>setCsvMsg(null), 8000);

                    // Parse buyers for CRM
                    if (onUpsertBuyers) {
                      const buyerMap = {};
                      const usernameIdx    = rawHeaders.indexOf("buyer_username");
                      const addressIdx     = rawHeaders.indexOf("shipping_address");
                      const zipIdx         = rawHeaders.indexOf("postal_code");
                      const couponCodeIdx  = rawHeaders.indexOf("coupon_code");
                      const couponPriceIdx = rawHeaders.indexOf("coupon_price");
                      for (let i=1; i<lines.length; i++) {
                        const cols=[]; let cur="", inQuote=false;
                        for (const ch of lines[i]) {
                          if (ch==='"') { inQuote=!inQuote; }
                          else if (ch==="," && !inQuote) { cols.push(cur.trim()); cur=""; }
                          else { cur+=ch; }
                        }
                        cols.push(cur.trim());
                        const cv = (cols[cancelIdx]||"").toLowerCase();
                        if (cv === "true" || cv === "failed") continue;
                        const username = cols[usernameIdx]||"";
                        if (!username) continue;
                        const address = cols[addressIdx]||"";
                        const parts = address.split(",").map(p=>p.trim());
                        const fullName = parts[0]||"";
                        const city  = parts[parts.length-4]||"";
                        const state = parts[parts.length-3]||"";
                        const zip   = cols[zipIdx]||"";
                        const spend = parseFloat(cols[origIdx]||0)||0;
                        const couponCode     = (cols[couponCodeIdx]||"").trim().toUpperCase();
                        const couponDiscount = couponPriceIdx>=0 ? Math.abs(parseFloat(cols[couponPriceIdx]||0)||0) : 0;
                        const hasCoupon = !!couponCode;
                        if (!buyerMap[username]) buyerMap[username] = { username, fullName, city, state, zip, spend:0, orders:0, couponCount:0, couponDiscount:0, couponsUsed:[], date:streamDate };
                        buyerMap[username].spend += spend;
                        buyerMap[username].orders++;
                        if (hasCoupon) {
                          buyerMap[username].couponCount++;
                          buyerMap[username].couponDiscount += couponDiscount;
                          if (!buyerMap[username].couponsUsed.includes(couponCode)) {
                            buyerMap[username].couponsUsed.push(couponCode);
                          }
                        }
                      }
                      // Use actual stream Firestore ID so multiple streams on same day don't collide
                      const streamId = editingStreamId || `${breaker}_${streamDate}`;
                      // Simple content hash to detect duplicate file imports
                      let hash = 0;
                      for (let i=0; i<ev.target.result.length; i++) hash = ((hash<<5)-hash)+ev.target.result.charCodeAt(i);
                      hash = Math.abs(hash).toString(36);
                      onUpsertBuyers(Object.values(buyerMap), streamId, file.name, hash);
                    }
                  } catch(err) { setCsvMsg({ type:"error", text:"Could not parse CSV. Make sure it's a Whatnot live sales export." }); }
                };
                reader.readAsText(file);
                e.target.value="";
              }}/>
            </label>
            )}
            {/* Streams this breaker already logged today. Shown rather than auto-loaded, so a second
                stream on the same day is easy to add and editing the first is still one click. */}
            {!editingStreamId && sameDayStreams.length > 0 && (
              <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                <span style={{ fontSize:11, color:"var(--bz-ink-3)", fontWeight:700 }}>
                  {breaker} already has {sameDayStreams.length} stream{sameDayStreams.length!==1?"s":""} on {date}:
                </span>
                {sameDayStreams.map(s => (
                  <button key={s.id} onClick={()=>setEditingStreamId(s.id)}
                    title="Edit this stream instead of starting a new one"
                    style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                    ✏️ {s.streamName || s.channel || "Stream"}{s.grossRevenue?` · $${parseFloat(s.grossRevenue).toLocaleString()}`:""}
                  </button>
                ))}
                <span style={{ fontSize:11, color:"#4ade80", fontWeight:700 }}>
                  — or just fill the form below to add another
                </span>
              </div>
            )}
            {editingStreamId && existingStream && (
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ background:"var(--bz-s1)", color:"var(--bz-ink-2)", border:"1px solid #92400e33", borderRadius:6, padding:"2px 10px", fontSize:11, fontWeight:700 }}>
                  {"\u270F\uFE0F Editing:"}{existingStream.breaker} · {existingStream.date}
                </span>
                <button onClick={()=>{ csvDataLoaded.current=false; setRecap({...EMPTY_RECAP}); setRecapSaved(false); setEditingStreamId(null); }} style={{ background:"none", border:"none", color:"var(--bz-ink-2)", cursor:"pointer", fontSize:11, textDecoration:"underline", fontFamily:"inherit" }}>
                  Start new instead
                </button>
              </div>
            )}
            {recapSaved && <span style={{ background:"var(--bz-s1)", color:"#E8317A", border:"1px solid #2E7D5222", borderRadius:20, padding:"3px 12px", fontSize:11, fontWeight:700 }}>{"\u2705 Saved"}</span>}
          </div>
        </div>

        {/* CSV import message */}
        {csvMsg && (
          <div style={{ marginBottom:12, padding:"10px 14px", background:csvMsg.type==="success"?"#0a1a0a":"#1a0a0a", border:`1px solid ${csvMsg.type==="success"?"#4ade8033":"#E8317A33"}`, borderRadius:8, fontSize:12, color:csvMsg.type==="success"?"#4ade80":"#E8317A", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span>{csvMsg.text}</span>
            <button onClick={()=>setCsvMsg(null)} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:14, marginLeft:10 }}>{"\u2715"}</button>
          </div>
        )}

        {/* Live preview panel + form side by side */}
        <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>

        {/* ── LEFT: form ── */}
        <div style={{ flex:1, minWidth:0 }}>

        {/* Section helper */}
        {(()=>{
          const SectionHeader = ({label, icon, sectionKey, open, onToggle}) => (
            <button onClick={onToggle} style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", background:"transparent", border:"none", borderBottom:`1px solid ${open?"rgba(232,49,122,0.3)":"var(--bz-s2)"}`, padding:"10px 2px 8px", cursor:"pointer", fontFamily:"inherit", marginBottom:open?14:8 }}>
              <span style={{ fontSize:12, fontWeight:800, color:open?"#E8317A":"#888", letterSpacing:"0.5px", textTransform:"uppercase" }}>{icon} {label}</span>
              <span style={{ fontSize:12, color:open?"#E8317A":"#555" }}>{open?"▲":"▼"}</span>
            </button>
          );
          return null; // helper defined, used below
        })()}

        {/* Breaker + Channel + Date + Break Type — Admin sees full controls, streamers see read-only */}
        {userRole?.role === "Admin" ? (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr", gap:12, marginBottom:14 }}>
          <SelectInput label="Breaker" value={breaker} onChange={v=>{ setBreaker(v); setLinkedPlanId(null); }} options={BREAKERS}/>
          <div>
            <label style={S.lbl}>Channel</label>
            <select value={recap.channel || channelOf({breaker})} onChange={e=>rf("channel")(e.target.value)} style={{ ...S.inp, cursor:"pointer" }}>
              {CHANNELS.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            {FLAT_RATE_CHANNELS.includes(recap.channel) && <div style={{ fontSize:10, color:"#7B9CFF", marginTop:3 }}>⚡ Flat 50% rate</div>}
          </div>
          <TextInput label="Date" type="date" value={date} onChange={setDate}/>
          <div>
            <label style={S.lbl}>Stream Name</label>
            <input value={recap.streamName||""} onChange={e=>rf("streamName")(e.target.value)} placeholder="e.g. Friday Night Break #12" style={{ ...S.inp, color: recap.streamName?"var(--bz-ink)":"var(--bz-ink-2)" }}/>
          </div>
          <div>
            <label style={S.lbl}>Break Type</label>
            <select value={recap.isSinglesShow ? "singles" : recap.breakType} onChange={e=>{ if(e.target.value==="singles"){ rf("isSinglesShow")(true); } else { rf("isSinglesShow")(false); rf("breakType")(e.target.value); } }} style={{ ...S.inp, cursor:"pointer" }}>
              <option value="auction">Auction</option>
              <option value="bin">BIN</option>
              <option value="mixed">Mixed</option>
              <option value="singles">🃏 Singles (100% to breaker)</option>
            </select>
          </div>
        </div>
        ) : (
        <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"12px 16px", marginBottom:14 }}>
          {!breaker ? (
            <div style={{ marginBottom:10 }}>
              <label style={{ ...S.lbl, color:"#FBBF24" }}>Who are you? (select your name to save)</label>
              <select value={breaker} onChange={e=>{setBreaker(e.target.value);setLinkedPlanId(null);}} style={{ ...S.inp, cursor:"pointer" }}>
                <option value="">— Select your name —</option>
                {BREAKERS.map(b=><option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          ) : (
            <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)", marginBottom:4 }}>
              {breaker} · <span style={{ color:"var(--bz-ink-3)", fontWeight:400 }}>{date||"Select a date"}</span>
            </div>
          )}
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <TextInput label="Date" type="date" value={date} onChange={setDate}/>
            <div>
              <label style={S.lbl}>Stream Name</label>
              <input value={recap.streamName||""} onChange={e=>rf("streamName")(e.target.value)} placeholder="e.g. Friday Night Break #12" style={{ ...S.inp, color: recap.streamName?"var(--bz-ink)":"var(--bz-ink-2)" }}/>
            </div>
          </div>
        </div>
        )}

        {/* Link to planned stream */}
        {(() => {
          // Match plans on date + breaker, tolerant of format/whitespace/case differences.
          const normDate = d => String(d||"").split("T")[0].trim();
          const normName = n => String(n||"").toLowerCase().replace(/\s+/g,"");
          const dayPlans = plannedStreams.filter(p =>
            normDate(p.date) === normDate(date) &&
            normName(p.breaker) === normName(breaker)
          );
          if (!date || !breaker || dayPlans.length === 0) return null;
          return (
            <div style={{marginBottom:14,padding:"10px 14px",background:"rgba(123,156,255,0.05)",border:"1px solid rgba(123,156,255,0.2)",borderRadius:8,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <span style={{fontSize:12,fontWeight:700,color:"#7B9CFF",flexShrink:0}}>📅 Link to planned stream:</span>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",flex:1}}>
                {dayPlans.map(p=>(
                  <button key={p.id} onClick={()=>{
                    if (p.streamName) rf("streamName")(p.streamName);
                    if (p.sessionType) rf("sessionType")(p.sessionType);
                    // NOTE: do NOT copy the plan's estMultiple — market multiple must be
                    // calculated from actual gross ÷ product market value once gross is entered.
                    // Seed the recap's per-product quantity fields (prod_<TYPE>) from the plan
                    const plannedProducts = (p.products||[]).filter(pr=>pr.type);
                    if (plannedProducts.length > 0) {
                      setRecap(prev => {
                        const next = { ...prev };
                        plannedProducts.forEach(pr => {
                          const key = `prod_${pr.type}`;
                          next[key] = String((parseInt(next[key])||0) + (parseInt(pr.qty)||0));
                        });
                        if (p.streamName) next.streamName = p.streamName;
                        if (p.sessionType) next.sessionType = p.sessionType;
                        // Recalculate market multiple from actual gross ÷ product market value
                        const gross = parseFloat(next.grossRevenue)||0;
                        const totalMktVal = PRODUCT_TYPES.reduce((sum,pt)=>{
                          const qty = parseInt(next[`prod_${pt}`])||0;
                          const price = parseFloat(next.streamSkuPrices?.[pt] ?? skuPrices[pt])||0;
                          return sum + qty*price;
                        }, 0);
                        if (gross > 0 && totalMktVal > 0) next.marketMultiple = (gross/totalMktVal).toFixed(2);
                        return next;
                      });
                    }
                    setLinkedPlanId(p.id);
                    // Never overwrite the breaker — the recap breaker stays as-is
                  }}
                    style={{background: linkedPlanId===p.id ? "rgba(74,222,128,0.15)" : "rgba(123,156,255,0.1)", color: linkedPlanId===p.id ? "#4ade80" : "#7B9CFF", border:`1px solid ${linkedPlanId===p.id ? "rgba(74,222,128,0.4)" : "rgba(123,156,255,0.25)"}`, borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit"}}>
                    {linkedPlanId===p.id ? "✓ Linked · " : ""}{p.streamName||p.breaker||"Planned Stream"}
                    {p.sessionType?` · ${p.sessionType}`:""}
                    {(p.products||[]).filter(pr=>pr.type).length>0?` · ${p.products.filter(pr=>pr.type).map(pr=>pr.qty+"× "+pr.type).join(", ")}` :""}
                  </button>
                ))}
              </div>
              <span style={{fontSize:10,color:"var(--bz-ink-3)"}}>Tap to auto-fill</span>
            </div>
          );
        })()}

        {userRole?.role === "Admin" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:14 }}>
          <div>
            <label style={S.lbl}>Session Type</label>
            <select value={recap.sessionType||""} onChange={e=>rf("sessionType")(e.target.value)} style={{ ...S.inp, cursor:"pointer" }}>
              <option value="">-- Select --</option>
              <option value="day">{"\u2600\uFE0F Day Break (Mon-Thurs)"}</option>
              <option value="night">{"\uD83C\uDF19 Night Break (Mon-Thurs)"}</option>
              <option value="weekend">{"\uD83D\uDCC5 Weekend Break (Fri-Sun)"}</option>
              <option value="singles">{"\uD83C\uDCCF Singles Show"}</option>
              <option value="event">{"\uD83C\uDF89 Event"}</option>
            </select>
          </div>
          <div>
            <label style={S.lbl}>Market Multiple</label>
            <input type="number" step="0.01" value={recap.marketMultiple} onChange={e=>rf("marketMultiple")(e.target.value)} placeholder="Auto-calculated" style={{ ...S.inp, color: recap.marketMultiple?"#1B4F8A":"var(--bz-ink-2)" }} disabled={recap.binOnly}/>
            {recap.marketMultiple && !recap.binOnly && (() => {
              const mm = parseFloat(recap.marketMultiple);
              const pct = Math.round(mm * 100);
              const color = mm>=1.7?"#4ade80":mm>=1.5?"#86efac":mm>=1.4?"#FBBF24":"#E8317A";
              return <div style={{ fontSize:11, color, fontWeight:700, marginTop:3 }}>{pct}% to market</div>;
            })()}
          </div>
          <div>
            <label style={{ ...S.lbl, color:"#E8317A" }}>{"\uD83C\uDF31 New Buyers"}</label>
            <input type="number" min="0" step="1" value={recap.newBuyers||""} onChange={e=>rf("newBuyers")(e.target.value)} placeholder="0" style={{ ...S.inp, color:"#E8317A" }}/>
          </div>
        </div>
        )}

        {/* Financials — Admin only */}
        {userRole?.role === "Admin" && <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10, marginBottom:10 }}>
          {[
            ["grossRevenue",  "Gross Revenue ($)", "#166534"],
            ["whatnotFees",   "Whatnot Fees ($)",  "#991b1b"],
            ["coupons",       "Coupons ($)",        "#991b1b"],
            ["whatnotPromo",  "Whatnot Promo ($)",  "#991b1b"],
          ].map(([key, label, color]) => (
            <div key={key}>
              <label style={{ ...S.lbl, color: key==="grossRevenue"?"#166534":S.lbl.color }}>{label}</label>
              <input type="number" step="0.01" value={recap[key]||""} onChange={e=>rf(key)(e.target.value)} placeholder="0.00" style={{ ...S.inp, color}}/>
            </div>
          ))}
        </div>}

        {/* Tips — 100% to rep (buyer tips passed through) — Admin only */}
        {userRole?.role === "Admin" && <div style={{ background:"rgba(251,191,36,0.05)", border:"1px solid rgba(251,191,36,0.2)", borderRadius:8, padding:"10px 14px", marginBottom:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#FBBF24", marginBottom:2 }}>💵 Tips</div>
              <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>Buyer tips — passed 100% to the rep, on top of commission.</div>
            </div>
            <input type="number" step="0.01" value={recap.tips||""} onChange={e=>rf("tips")(e.target.value)} placeholder="0.00" style={{ ...S.inp, width:130, color:"#FBBF24", fontWeight:700 }}/>
            {parseFloat(recap.tips)>0 && <div style={{ fontSize:13, fontWeight:800, color:"#FBBF24" }}>+${parseFloat(recap.tips).toFixed(2)} to rep</div>}
          </div>
        </div>}

        {/* Sales Bonus — Admin only */}
        {userRole?.role === "Admin" && <div style={{ background:"rgba(139,92,246,0.05)", border:"1px solid rgba(139,92,246,0.2)", borderRadius:8, padding:"10px 14px", marginBottom:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#A78BFA", marginBottom:2 }}>🎁 Sales Bonus</div>
              <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>Flat bonus on top of commission — Whatnot promos, incentives, etc.</div>
            </div>
            <input type="number" step="0.01" value={recap.salesBonus||""} onChange={e=>rf("salesBonus")(e.target.value)} placeholder="0.00" style={{ ...S.inp, width:130, color:"#A78BFA", fontWeight:700 }}/>
            {parseFloat(recap.salesBonus)>0 && <div style={{ fontSize:13, fontWeight:800, color:"#A78BFA" }}>+${parseFloat(recap.salesBonus).toFixed(2)} to rep</div>}
          </div>
          {parseFloat(recap.salesBonus)>0 && (
            <input type="text" value={recap.salesBonusNote||""} onChange={e=>rf("salesBonusNote")(e.target.value)} placeholder="Reason (e.g. Whatnot promo, performance bonus...)" style={{ ...S.inp, marginTop:8, fontSize:11, color:"#A78BFA" }}/>
          )}
        </div>}

        {/* Zion Cases Revenue -- auto-filled, read-only, Bazooka only */}
        {parseFloat(recap.zionRevenue||0) > 0 && (
          <div style={{ background:"#0a1a0a", border:"1px solid #4ade8033", borderRadius:8, padding:"10px 16px", marginBottom:10, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:"#4ade80" }}>{"\uD83D\uDFE2 Zion Cases Revenue -- Bazooka Only"}</div>
              <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>Auto-detected from CSV · Not included in IMC gross</div>
            </div>
            <div style={{ fontSize:22, fontWeight:900, color:"#4ade80" }}>${parseFloat(recap.zionRevenue||0).toFixed(2)}</div>
          </div>
        )}
        {/* Chaser Cards -- picker + manual override */}
        <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"12px 14px", marginBottom:10 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
              <label style={{ ...S.lbl, color:"var(--bz-ink-2)", margin:0 }}>{"\uD83C\uDFC6 Cards Used as Chasers"}</label>
              {canSeeFinancials && (
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <label style={{ fontSize:11, color:"var(--bz-ink-2)" }}>Manual override ($)</label>
                <input type="number" step="0.01" value={recap.chaserCards||""} onChange={e=>rf("chaserCards")(e.target.value)} placeholder="0.00" style={{ ...S.inp, width:90, color:"var(--bz-ink-2)", padding:"4px 8px" }}/>
              </div>
              )}
            </div>
            {(() => {
              const usedIdSet = new Set(breaks.map(b=>b.inventoryId));
              const recapLoc = locationForBreaker(recap.breaker||breaker);
              const available = inventory.filter(c => {
                if (usedIdSet.has(c.id) || c.cardStatus==="in_transit") return false;
                // Remote streamers (non-admins) can only log chasers physically at
                // their own HQ. Admins see all locations.
                if (!canSeeFinancials && recapLoc) return (c.location||"") === recapLoc;
                return true;
              });
              const selectedChasers = recap.chaserCardIds ? recap.chaserCardIds.split(",").filter(Boolean) : [];
              const totalCost = selectedChasers.reduce((sum,id)=>{ const card=inventory.find(c=>c.id===id); return sum+(card?.costPerCard||0); }, 0);
              const visibleCards = chaserSearch.trim()
                ? available.filter(c => c.cardName?.toLowerCase().includes(chaserSearch.toLowerCase()) || c.cardType?.toLowerCase().includes(chaserSearch.toLowerCase()))
                : available;
              return (
                <div>
                  {available.length === 0
                    ? <div style={{ fontSize:12, color:"var(--bz-ink-2)", padding:"8px 0" }}>No available cards in inventory</div>
                    : <>
                        <div style={{ marginBottom:8, display:"flex", gap:8, alignItems:"center" }}>
                          <input
                            value={chaserSearch}
                            onChange={e=>setChaserSearch(e.target.value)}
                            placeholder="Search cards..."
                            style={{ ...S.inp, padding:"5px 10px", fontSize:12, flex:1 }}
                          />
                          {chaserSearch && <button onClick={()=>setChaserSearch("")} style={{ background:"none", border:"none", color:"var(--bz-ink-2)", cursor:"pointer", fontSize:14, flexShrink:0 }}>{"\u2715"}</button>}
                          <span style={{ fontSize:11, color:"#999", whiteSpace:"nowrap" }}>{visibleCards.length} card{visibleCards.length!==1?"s":""}</span>
                        </div>
                        <div style={{ maxHeight:200, overflowY:"auto", border:"1px solid var(--bz-line-2)", borderRadius:8, background:"var(--bz-s1)" }}>
                          {visibleCards.length === 0
                            ? <div style={{ padding:"12px 16px", color:"#999", fontSize:12 }}>No cards match "{chaserSearch}"</div>
                            : visibleCards.map(c => {
                                const isSel = selectedChasers.includes(c.id);
                          const cc = CC[c.cardType]||{bg:"#F3F4F6",text:"var(--bz-ink-3)"};
                          return (
                            <div key={c.id}
                              onClick={()=>{
                                const newSel = isSel ? selectedChasers.filter(x=>x!==c.id) : [...selectedChasers, c.id];
                                const newCost = newSel.reduce((sum,id)=>{ const card=inventory.find(x=>x.id===id); return sum+(card?.costPerCard||0); },0);
                                setRecap(p=>({...p, chaserCardIds:newSel.join(","), chaserCards:newCost>0?newCost.toFixed(2):p.chaserCards}));
                                setRecapSaved(false);
                              }}
                              style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 12px", cursor:"pointer", background:isSel?"#2a1520":"var(--bz-s1)", borderBottom:"1px solid var(--bz-line)" }}
                            >
                              <input type="checkbox" checked={isSel} readOnly style={{ flexShrink:0 }}/>
                              <span style={{ fontSize:12, fontWeight:isSel?700:400, color:"var(--bz-ink)", flex:1 }}>{c.cardName}</span>
                              <span style={{ background:cc.bg, color:cc.text, borderRadius:4, padding:"1px 6px", fontSize:10, fontWeight:700, whiteSpace:"nowrap" }}>{c.cardType}</span>
                              {canSeeFinancials && c.costPerCard>0 && <span style={{ fontSize:11, color:"var(--bz-ink-2)", fontWeight:700 }}>${c.costPerCard.toFixed(2)}</span>}
                            </div>
                          );
                        })}
                      </div>
                      </>
                  }
                  {selectedChasers.length > 0 && (
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:8 }}>
                      <span style={{ fontSize:12, color:"var(--bz-ink-2)", fontWeight:700 }}>
                        {"\u2705"}{selectedChasers.length} card{selectedChasers.length!==1?"s":""} selected{canSeeFinancials?` · auto-cost: $${totalCost.toFixed(2)}`:""}
                      </span>
                      <button onClick={()=>{ setRecap(p=>({...p, chaserCardIds:"", chaserCards:""})); setRecapSaved(false); }} style={{ background:"none", border:"1px solid var(--bz-line-2)", borderRadius:5, color:"var(--bz-ink-2)", cursor:"pointer", fontSize:11, padding:"2px 8px", fontFamily:"inherit" }}>{"\u2715 Clear"}</button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
          {/* Supply qty fields -- auto-calc from cost per unit */}
          <div style={{ display:"contents" }}>
              {[
                { qtyKey:"magprosQty",   dollarKey:"magpros",           label:"MagPros",             supplyKey:"supply_magpros"   },
                { qtyKey:"packagingQty", dollarKey:"packagingMaterial", label:"Packaging",           supplyKey:"supply_packaging" },
              ].map(({ qtyKey, dollarKey, label, supplyKey }) => {
                const costPer = parseFloat(skuPrices[supplyKey]) || 0;
                const qty     = parseInt(recap[qtyKey]) || 0;
                const total   = (qty * costPer).toFixed(2);
                return (
                  <div key={qtyKey}>
                    <label style={{ ...S.lbl, color:"#E8317A" }}>{label} (qty)</label>
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      <input
                        type="number" min="0" step="1"
                        value={recap[qtyKey]||""}
                        onChange={e => {
                          const q = parseInt(e.target.value)||0;
                          const amt = (q * costPer).toFixed(2);
                          setRecap(p => ({ ...p, [qtyKey]:e.target.value, [dollarKey]:amt }));
                          setRecapSaved(false);
                        }}
                        placeholder="0"
                        style={{ ...S.inp, color:"#E8317A", flex:1 }}
                      />
                      {canSeeFinancials && costPer > 0 && qty > 0 && (
                        <span style={{ fontSize:11, color:"#E8317A", fontWeight:700, whiteSpace:"nowrap" }}>${total}</span>
                      )}
                      {canSeeFinancials && !costPer && <span style={{ fontSize:10, color:"var(--bz-ink-2)", whiteSpace:"nowrap" }}>no cost set</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* BigU-only: giveaway + insurance card reimbursement */}
            {isRemoteBreaker(recap.breaker||breaker) && (canSeeFinancials || isRemoteBreaker(matchedBreaker)) && (
              <div style={{ background:"rgba(251,191,36,0.06)", border:"1px solid rgba(251,191,36,0.25)", borderRadius:10, padding:"12px 14px", marginBottom:10 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#FBBF24", marginBottom:10, textTransform:"uppercase", letterSpacing:1 }}>🔄 {recap.breaker||breaker||"Remote"} Reimbursables — Cards</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  <div>
                    <label style={{ ...S.lbl, color:"#FBBF24" }}>Giveaway Cards ($)</label>
                    <input type="number" step="0.01" value={recap.biguGiveawayCards||""} onChange={e=>{ rf("biguGiveawayCards")(e.target.value); setRecapSaved(false); }} placeholder="0.00" style={{ ...S.inp }}/>
                  </div>
                  <div>
                    <label style={{ ...S.lbl, color:"#FBBF24" }}>Insurance Cards ($)</label>
                    <input type="number" step="0.01" value={recap.biguInsuranceCards||""} onChange={e=>{ rf("biguInsuranceCards")(e.target.value); setRecapSaved(false); }} placeholder="0.00" style={{ ...S.inp }}/>
                  </div>
                  <div>
                    <label style={{ ...S.lbl, color:"#FBBF24" }}>Chaser Cards ($)</label>
                    <input type="number" step="0.01" value={recap.biguChaserCards||""} onChange={e=>{ rf("biguChaserCards")(e.target.value); setRecapSaved(false); }} placeholder="0.00" style={{ ...S.inp }}/>
                  </div>
                </div>
                <div style={{ marginTop:10 }}>
                  <label style={{ ...S.lbl, color:"#FBBF24" }}>Shipping ($) — split 50/50 with Bazooka</label>
                  <input type="number" step="0.01" value={recap.biguShipping||""} onChange={e=>{ rf("biguShipping")(e.target.value); setRecapSaved(false); }} placeholder="0.00" style={{ ...S.inp }}/>
                  {(parseFloat(recap.biguShipping)||0)>0 && (
                    <div style={{ fontSize:11, color:"#FBBF24", marginTop:4 }}>You're reimbursed ${((parseFloat(recap.biguShipping)||0)*0.5).toFixed(2)} (half of ${(parseFloat(recap.biguShipping)||0).toFixed(2)})</div>
                  )}
                </div>
                {rc && ((parseFloat(recap.biguGiveawayCards)||0)+(parseFloat(recap.biguInsuranceCards)||0)+(parseFloat(recap.biguChaserCards)||0)+((parseFloat(recap.biguShipping)||0)*0.5))>0 && (
                  <div style={{ fontSize:12, color:"#FBBF24", fontWeight:700, marginTop:8 }}>
                    Total reimb this stream: ${((parseFloat(recap.biguGiveawayCards)||0)+(parseFloat(recap.biguInsuranceCards)||0)+(parseFloat(recap.biguChaserCards)||0)+((parseFloat(recap.biguShipping)||0)*0.5)).toFixed(2)}
                  </div>
                )}
              </div>
            )}
          {userRole?.role === "Admin" && <>
          <div style={{ display:"flex", gap:16, marginBottom:14, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <input type="checkbox" checked={recap.binOnly||false} onChange={e=>{rf("binOnly")(e.target.checked); if(e.target.checked) rf("isEvent")(false);}} style={{ width:16, height:16 }}/>
            <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>BIN Break — flat 35% commission</span>
          </div>
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <input type="checkbox" checked={recap.isEvent||false} onChange={e=>{rf("isEvent")(e.target.checked); if(e.target.checked) rf("binOnly")(false);}} style={{ width:16, height:16, accentColor:"#A78BFA" }}/>
            <span style={{ fontSize:12, color: recap.isEvent?"#A78BFA":"var(--bz-ink-2)", fontWeight: recap.isEvent?700:400 }}>🎪 Event Break — 15% event fee</span>
            {recap.isEvent && rc && <span style={{ fontSize:12, fontWeight:800, color:"#A78BFA" }}>{fmt(rc.commAmt)} to {recap.breaker||breaker||"rep"}</span>}
          </div>
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <input type="checkbox" checked={recap.isSinglesShow||false} onChange={e=>rf("isSinglesShow")(e.target.checked)} style={{ width:16, height:16, accentColor:"#FBBF24" }}/>
            <span style={{ fontSize:12, color: recap.isSinglesShow?"#FBBF24":"var(--bz-ink-2)", fontWeight: recap.isSinglesShow?700:400 }}>🃏 Singles Show — 100% to breaker</span>
            {recap.isSinglesShow && rc && <span style={{ fontSize:12, fontWeight:800, color:"#FBBF24" }}>{fmt(rc.myComm)} to {recap.breaker||breaker||"rep"}</span>}
          </div>
          {canSeeFinancials && (
            <div style={{ display:"flex", alignItems:"center", gap:8, marginLeft:"auto" }}>
              <label style={{ fontSize:12, color:"#E8317A", fontWeight:700, whiteSpace:"nowrap" }}>{"\uD83D\uDD27 Override Commission %"}</label>
              <input
                type="number" min="0" max="100" step="1"
                value={recap.commissionOverride||""}
                onChange={e=>rf("commissionOverride")(e.target.value)}
                placeholder="e.g. 0"
                style={{ ...S.inp, width:80, color:"#E8317A", textAlign:"center" }}
              />
              {recap.commissionOverride !== "" && (
                <button onClick={()=>rf("commissionOverride")("")} style={{ background:"none", border:"none", color:"var(--bz-ink-2)", cursor:"pointer", fontSize:14, padding:0 }}>{"\u2715"}</button>
              )}
              <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{recap.commissionOverride !== "" ? `Using ${recap.commissionOverride}%` : "Leave blank to use tier rate"}</span>
            </div>
          )}
          </div>
          </>}

        {/* Split Commission — Admin only */}
        {userRole?.role === "Admin" && <div style={{ background:"rgba(251,191,36,0.04)", border:"1px solid rgba(251,191,36,0.2)", borderRadius:8, padding:"12px 16px", marginBottom:14 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: recap.splitRep ? 10 : 0 }}>
            <div>
              <span style={{ fontSize:12, fontWeight:700, color:"#FBBF24" }}>✂️ Split Commission</span>
              <span style={{ fontSize:11, color:"var(--bz-ink-3)", marginLeft:8 }}>Divide commission between two reps</span>
            </div>
            <select value={recap.splitRep||""} onChange={e=>rf("splitRep")(e.target.value)}
              style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:6, color: recap.splitRep?"#FBBF24":"#555", padding:"4px 10px", fontSize:11, fontFamily:"inherit", cursor:"pointer" }}>
              <option value="">-- No split --</option>
              {BREAKERS.filter(b=>b!==breaker).map(b=><option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          {recap.splitRep && (
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:4 }}>{breaker}'s share</div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <input type="number" min="0" max="100" value={recap.splitPct||"50"} onChange={e=>rf("splitPct")(e.target.value)}
                    style={{ ...S.inp, width:70, textAlign:"center", color:"#FBBF24", fontWeight:700 }}/>
                  <span style={{ color:"var(--bz-ink-3)", fontSize:12 }}>%</span>
                  {rc && <span style={{ fontSize:13, fontWeight:800, color:"#FBBF24" }}>{fmt(rc.commAmt * (parseFloat(recap.splitPct||50)/100))}</span>}
                </div>
              </div>
              <div style={{ fontSize:20, color:"var(--bz-ink-3)" }}>↔</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:4 }}>{recap.splitRep}'s share</div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ width:70, textAlign:"center", color:"#FBBF24", fontWeight:700, background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:8, padding:"9px 12px", fontSize:13 }}>{100-(parseFloat(recap.splitPct||50))}%</div>
                  <span style={{ color:"var(--bz-ink-3)", fontSize:12 }}>%</span>
                  {rc && <span style={{ fontSize:13, fontWeight:800, color:"#FBBF24" }}>{fmt(rc.commAmt * ((100-parseFloat(recap.splitPct||50))/100))}</span>}
                </div>
              </div>
            </div>
          )}
        </div>}

        {/* Event Staff — Admin only */}
        {userRole?.role === "Admin" && <div style={{ background:"rgba(167,139,250,0.04)", border:"1px solid rgba(167,139,250,0.2)", borderRadius:8, padding:"12px 16px", marginBottom:14 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:(recap.eventStaff||[]).length>0?10:0 }}>
            <div>
              <span style={{ fontSize:12, fontWeight:700, color:"#A78BFA" }}>🎪 Event Staff Pay</span>
              <span style={{ fontSize:11, color:"var(--bz-ink-3)", marginLeft:8 }}>15% of Bazooka Net · capped at $1,000/rep</span>
            </div>
            <button onClick={()=>rf("eventStaff")([...(recap.eventStaff||[]), {id:uid(), breaker:BREAKERS.find(b=>b!==breaker)||BREAKERS[0]}])}
              style={{ background:"rgba(167,139,250,0.15)", border:"1px solid rgba(167,139,250,0.3)", color:"#A78BFA", borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              + Add Rep
            </button>
          </div>
          {(recap.eventStaff||[]).map((es, i) => {
            const esAmt = rc ? Math.min(1000, rc.bazNet * 0.15) : 0;
            const isCapped = rc && rc.bazNet * 0.15 > 1000;
            return (
              <div key={es.id} style={{ display:"grid", gridTemplateColumns:"1fr auto auto", gap:8, alignItems:"center", marginBottom:6 }}>
                <select value={es.breaker} onChange={e=>rf("eventStaff")((recap.eventStaff||[]).map((x,j)=>j===i?{...x,breaker:e.target.value}:x))}
                  style={{ ...S.inp, fontSize:12, cursor:"pointer" }}>
                  {BREAKERS.map(b=><option key={b} value={b}>{b}</option>)}
                </select>
                {rc && <span style={{ fontSize:13, fontWeight:800, color:"#A78BFA", whiteSpace:"nowrap" }}>
                  {fmt(esAmt)}{isCapped&&<span style={{fontSize:10,color:"#FBBF24",marginLeft:4}}>⚠ capped</span>}
                </span>}
                <button onClick={()=>rf("eventStaff")((recap.eventStaff||[]).filter((_,j)=>j!==i))}
                  style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:16, padding:"0 4px" }}>×</button>
              </div>
            );
          })}
          {(recap.eventStaff||[]).length>0 && rc && (
            <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:6, borderTop:"1px solid rgba(167,139,250,0.1)", paddingTop:6 }}>
              Total event pay: <strong style={{color:"#A78BFA"}}>{fmt((recap.eventStaff||[]).length * Math.min(1000, rc.bazNet*0.15))}</strong>
              {" · "}{(recap.eventStaff||[]).map(es=>es.breaker).join(", ")}
            </div>
          )}
        </div>}

        {/* IMC Direct / External Channel / Collab — Admin only */}
        {userRole?.role === "Admin" && <>
        {/* IMC Direct Reimbursement */}
        <div style={{ background:"rgba(59,130,246,0.05)", border:"1px solid rgba(59,130,246,0.2)", borderRadius:8, padding:"10px 14px", marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#60A5FA", marginBottom:2 }}>💙 IMC Direct Reimbursement</div>
              <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>Direct payment from IMC — reduces amount owed, adds to Bazooka True Net</div>
            </div>
            <input type="number" step="0.01" value={recap.imcReimbursement||""} onChange={e=>rf("imcReimbursement")(e.target.value)} placeholder="0.00"
              style={{ ...S.inp, width:130, color:"#60A5FA", fontWeight:700, border:recap.imcReimbursement?"1.5px solid #60A5FA44":"1px solid var(--bz-line-2)" }}/>
            {parseFloat(recap.imcReimbursement)>0 && <div style={{ fontSize:13, fontWeight:800, color:"#60A5FA" }}>+${parseFloat(recap.imcReimbursement).toFixed(2)}</div>}
          </div>
          {parseFloat(recap.imcReimbursement)>0 && (
            <input type="text" value={recap.imcReimbNote||""} onChange={e=>rf("imcReimbNote")(e.target.value)} placeholder="Reason (e.g. promotional credit, correction...)"
              style={{ ...S.inp, marginTop:8, fontSize:11, color:"#60A5FA" }}/>
          )}
        </div>

        {/* External Channel */}
        <div style={{ background:"#0a0f1a", border:"1px solid #7B9CFF33", borderRadius:8, padding:"12px 16px", marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <input type="checkbox" checked={!!recap.externalChannel} onChange={e=>rf("externalChannel")(e.target.checked)} style={{ width:15, height:15, cursor:"pointer" }}/>
            <div>
              <span style={{ fontSize:12, color:"#7B9CFF", fontWeight:700 }}>🌐 External Channel (No IMC Obligation)</span>
              <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>Break happened on a partner's channel (e.g. Valley). Bazooka tracks the revenue internally but owes nothing to IMC — the partner handles their own IMC relationship.</div>
            </div>
          </div>
          {recap.externalChannel && (
            <div style={{ marginTop:10, padding:"8px 12px", background:"rgba(123,156,255,0.06)", border:"1px solid rgba(123,156,255,0.2)", borderRadius:6, fontSize:12, color:"#7B9CFF" }}>
              ✓ 70/30 split math unchanged — Bazooka keeps its 30%, commission applies normally. IMC obligation zeroed out.
            </div>
          )}
        </div>

        {/* Exclude from Financials — revenue already counted (e.g. from imported CSV); pay commission normally but keep it off the dashboard/IMC */}
        <div style={{ background:"#1a1206", border:"1px solid #F59E0B44", borderRadius:8, padding:"12px 16px", marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <input type="checkbox" checked={!!recap.excludeFinancials} onChange={e=>rf("excludeFinancials")(e.target.checked)} style={{ width:15, height:15, cursor:"pointer", accentColor:"#F59E0B" }}/>
            <div>
              <span style={{ fontSize:12, color:"#F59E0B", fontWeight:700 }}>🚫 Exclude from Financials (already counted)</span>
              <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>For revenue you already recorded elsewhere (e.g. an imported CSV). Commission still calculates normally — full rate, splits, event staff, bonuses — so reps get paid. The gross / IMC owed / Bazooka net just won't be added to the dashboard again.</div>
            </div>
          </div>
          {recap.excludeFinancials && rc && (
            <div style={{ marginTop:10, padding:"8px 12px", background:"rgba(245,158,11,0.06)", border:"1px solid rgba(245,158,11,0.2)", borderRadius:6, fontSize:11, color:"#F59E0B", lineHeight:1.5 }}>
              ✓ Commission pays out normally: <strong>{fmt(rc.myComm)}</strong> to {recap.breaker||breaker||"the rep"}{recap.splitRep?` (split with ${recap.splitRep})`:""}. Revenue stays off the dashboard and adds nothing to IMC.
            </div>
          )}
        </div>

        {/* Collab Stream */}
        <div style={{ background:"#0a0f1a", border:"1px solid #7B9CFF33", borderRadius:8, padding:"12px 16px", marginBottom:14 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <input type="checkbox" checked={!!recap.collabPartner} onChange={e=>{ if(!e.target.checked){ rf("collabPartner")(""); rf("collabPct")(""); } else rf("collabPartner")("_"); }} style={{ width:15, height:15 }}/>
            <span style={{ fontSize:12, color:"#7B9CFF", fontWeight:700 }}>{"\uD83E\uDD1D Collab Stream"}</span>
            {!!recap.collabPartner && (
              <>
                <input value={recap.collabPartner === "_" ? "" : recap.collabPartner} onChange={e=>rf("collabPartner")(e.target.value||"_")} placeholder="Partner name / channel" style={{ ...S.inp, flex:1, fontSize:12 }}/>
                <input type="number" min="0" max="100" step="1" value={recap.collabPct||""} onChange={e=>rf("collabPct")(e.target.value)} placeholder="%" style={{ ...S.inp, width:70, textAlign:"center", fontSize:12, color:"#7B9CFF" }}/>
                <span style={{ fontSize:11, color:"var(--bz-ink-3)", whiteSpace:"nowrap" }}>% of Bazooka Net</span>
              </>
            )}
          </div>
          {!!recap.collabPartner && recap.collabPct && (() => {
            const pct = parseFloat(recap.collabPct)||0;
            const baz = rc.bazNet || 0;
            const collabAmt = baz * (pct/100);
            const bazAfter  = baz - collabAmt;
            return (
              <div style={{ display:"flex", gap:16, fontSize:12, marginTop:8 }}>
                <span style={{ color:"var(--bz-ink-2)" }}>Collab payout: <strong style={{color:"#7B9CFF"}}>${collabAmt.toFixed(2)}</strong></span>
                <span style={{ color:"var(--bz-ink-2)" }}>Bazooka after collab: <strong style={{color:"#E8317A"}}>${bazAfter.toFixed(2)}</strong></span>
              </div>
            );
          })()}
        </div>
        </>}

        {/* Product used this stream */}
        <div style={{ marginBottom:14 }}>
          <label style={{ ...S.lbl, marginBottom:8, display:"block" }}>{"\uD83D\uDCE6 Product Used This Stream"}</label>
          {/* Show rows for any product that has a qty set */}
          {PRODUCT_TYPES.filter(pt => parseInt(recap[`prod_${pt}`]) > 0).map(pt => {
            const globalPrice = parseFloat(skuPrices[pt]) || 0;
            const streamPrice = recap.streamSkuPrices?.[pt];
            const effectivePrice = parseFloat(streamPrice ?? globalPrice) || 0;
            const qty = parseInt(recap[`prod_${pt}`]) || 0;
            const mktVal = qty * effectivePrice;
            return (
              <div key={pt} style={{ display:"grid", gridTemplateColumns:"1fr 80px 80px auto", gap:8, alignItems:"center", marginBottom:8 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#E8317A" }}>{pt}</div>
                <input type="number" min="0" step="1" value={recap[`prod_${pt}`]||""} onChange={e=>rf(`prod_${pt}`)(e.target.value)} placeholder="0 boxes" style={{ ...S.inp, color:"#E8317A", textAlign:"center" }}/>
                {canSeeFinancials ? (
                <input type="number" min="0" step="0.01" value={streamPrice ?? globalPrice}
                  onChange={e => {
                    const val = e.target.value;
                    setRecap(p => {
                      const newSku = { ...p.streamSkuPrices, [pt]: val };
                      const gross = parseFloat(p.grossRevenue) || 0;
                      const totalMktVal = PRODUCT_TYPES.reduce((sum, t) => {
                        const q = parseInt(p[`prod_${t}`]) || 0;
                        const pr = parseFloat(t === pt ? val : (newSku[t] ?? skuPrices[t])) || 0;
                        return sum + q * pr;
                      }, 0);
                      const skuGross = parseFloat(p.grossRevenue)||0;
                      const skuCoupons = parseFloat(p.coupons)||0;
                      const mm = (skuGross+skuCoupons) > 0 && totalMktVal > 0 ? ((skuGross+skuCoupons) / totalMktVal).toFixed(2) : p.marketMultiple;
                      return { ...p, streamSkuPrices: newSku, marketMultiple: mm };
                    });
                    setRecapSaved(false);
                  }}
                  style={{ ...S.inp, fontSize:11, padding:"3px 6px", color: streamPrice !== undefined && streamPrice !== String(globalPrice) ? "#FBBF24" : "#555" }}
                />
                ) : (
                <div style={{ fontSize:11, padding:"3px 6px", color:"var(--bz-ink-3)", textAlign:"center" }} title="Set by admin">${effectivePrice.toFixed(2)}</div>
                )}
                <button onClick={()=>rf(`prod_${pt}`)("")} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:16 }}>{"\u2715"}</button>
              </div>
            );
          })}
          {/* Add product dropdown */}
          <select onChange={e=>{ if(e.target.value) { rf(`prod_${e.target.value}`)("1"); e.target.value=""; } }}
            style={{ ...S.inp, color:"#E8317A", cursor:"pointer", marginTop:4, border:"1.5px dashed #E8317A44", background:"rgba(232,49,122,0.05)" }}>
            <option value="">➕ Add product to this stream...</option>
            {Object.entries(PRODUCT_SETS).map(([set,types])=>(
              <optgroup key={set} label={set}>
                {types.map(t=>{ const key=`${set} - ${t}`; return <option key={key} value={key} style={{color:"var(--bz-ink)",background:"var(--bz-s2)"}}>{set} — {t}</option>; })}
              </optgroup>
            ))}
            <option value="Miscellaneous" style={{color:"var(--bz-ink)",background:"var(--bz-s2)"}}>Miscellaneous</option>
          </select>
          {PRODUCT_TYPES.some(pt=>parseInt(recap[`prod_${pt}`])>0) && (
            <div style={{ marginTop:8, fontSize:12, color:"var(--bz-ink-3)" }}>
              Total MV: <strong style={{color:"var(--bz-ink)"}}>${PRODUCT_TYPES.reduce((sum,pt)=>{const q=parseInt(recap[`prod_${pt}`])||0;const pr=parseFloat(recap.streamSkuPrices?.[pt]??skuPrices[pt])||0;return sum+q*pr;},0).toFixed(0)}</strong>
            </div>
          )}
        </div>

        {/* Live commission preview */}
        {hasRecapData && (
          <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"12px 16px", marginBottom:14 }}>
            {(parseInt(recap.newBuyers)||0) >= 5 && !recap.isEvent && !recap.binOnly && (recap.commissionOverride===""||recap.commissionOverride==null) && (
              <div style={{ display:"flex", alignItems:"center", gap:10, background:"linear-gradient(135deg,rgba(74,222,128,0.15),rgba(34,197,94,0.08))", border:"1px solid rgba(74,222,128,0.45)", borderRadius:9, padding:"10px 14px", marginBottom:12 }}>
                <span style={{ fontSize:20 }}>🌱</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:900, color:"#4ade80" }}>+5% New Buyer Bonus Applied</div>
                  <div style={{ fontSize:11, color:"#86efac" }}>{recap.newBuyers} new buyers this stream (5+) — commission bumped +5%</div>
                </div>
              </div>
            )}
            {canSeeFinancials ? (
              <>
                {/* LIVE TRUE NET HERO — updates as you fill the recap */}
                <div style={{ position:"relative", background:"linear-gradient(135deg,#24101c 0%,#160d13 55%,#130d11 100%)", border:"1px solid rgba(232,49,122,0.28)", borderRadius:16, padding:"16px 20px", marginBottom:14, overflow:"hidden" }}>
                  <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"linear-gradient(90deg,#E8317A,#ff4d94,#a78bfa)" }} />
                  <div style={{ position:"absolute", right:-40, top:-50, width:180, height:180, borderRadius:"50%", background:"radial-gradient(circle,rgba(232,49,122,0.18),transparent 70%)" }} />
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12, position:"relative" }}>
                    <div>
                      <div style={{ fontSize:10.5, fontWeight:800, color:"#ff4d94", textTransform:"uppercase", letterSpacing:2, marginBottom:2 }}>Bazooka True Net {recapSaved ? "" : "· live"}</div>
                      <div style={{ fontSize:38, fontWeight:900, lineHeight:1, letterSpacing:-1, background:"linear-gradient(180deg,#ffffff 0%,#ffd9e8 60%,#ff8fc0 100%)", WebkitBackgroundClip:"text", backgroundClip:"text", WebkitTextFillColor:"transparent", filter:"drop-shadow(0 0 18px rgba(232,49,122,0.4))" }}>{fmt(rc.bazTrueNet)}</div>
                    </div>
                    <div style={{ display:"flex", gap:18, flexWrap:"wrap" }}>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:9.5, fontWeight:800, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Gross</div>
                        <div style={{ fontSize:17, fontWeight:900, color:"var(--bz-ink)", marginTop:2 }}>{fmt(rc.gross)}</div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:9.5, fontWeight:800, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Baz 30%</div>
                        <div style={{ fontSize:17, fontWeight:900, color:"var(--bz-pink)", marginTop:2 }}>{fmt(rc.bazNet)}</div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:9.5, fontWeight:800, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Commission</div>
                        <div style={{ fontSize:17, fontWeight:900, color:"var(--bz-green)", marginTop:2 }}>{fmt(rc.myComm)}</div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Row 1: top-level split */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:10 }}>
                  {[
                    { l:"Gross Revenue (true)",      v:fmt(rc.gross),   c:"var(--bz-ink)" },
                    { l:"Owed to Imagination Mining", v:fmt(rc.imcNet),  c:"#6B2D8B" },
                    { l:"Bazooka Earnings (30%)", v:fmt(rc.bazNet),  c:"#E8317A" },
                  ].map(({l,v,c}) => (
                    <div key={l} style={{ textAlign:"center", background:"linear-gradient(160deg,#181016,#130d11)", borderRadius:10, padding:"12px 8px", border:"1px solid var(--bz-line-2)" }}>
                      <div style={{ fontSize:20, fontWeight:900, color:c }}>{v}</div>
                      <div style={{ fontSize:9, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginTop:3 }}>{l}</div>
                    </div>
                  ))}
                </div>
                {recap.collabPartner && recap.collabPartner !== "_" && parseFloat(recap.collabPct) > 0 && (() => {
                  const collabAmt = rc.bazNet * (parseFloat(recap.collabPct)/100);
                  return (
                    <div style={{ marginBottom:10, padding:"8px 14px", background:"#0a0f1a", border:"1px solid #7B9CFF33", borderRadius:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ fontSize:12, color:"#7B9CFF", fontWeight:700 }}>{"\uD83E\uDD1D Collab --"}{recap.collabPartner} ({recap.collabPct}%)</span>
                      <span style={{ fontSize:14, fontWeight:900, color:"#7B9CFF" }}>{"\u2212"}{fmt(collabAmt)}</span>
                    </div>
                  );
                })()}
                {parseFloat(recap.zionRevenue||0) > 0 && (
                  <div style={{ marginTop:8, padding:"8px 14px", background:"#0a1a0a", border:"1px solid #4ade8033", borderRadius:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div>
                      <span style={{ fontSize:11, color:"#4ade80", fontWeight:700 }}>{"\uD83D\uDFE2 Zion Cases -- Bazooka Only"}</span>
                      <span style={{ fontSize:10, color:"var(--bz-ink-3)", marginLeft:8 }}>not in IMC split</span>
                    </div>
                    <span style={{ fontSize:15, fontWeight:900, color:"#4ade80" }}>{fmt(parseFloat(recap.zionRevenue||0))}</span>
                  </div>
                )}

                {/* Row 2: bazooka true net */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, paddingTop:10, borderTop:"1px solid var(--bz-line)" }}>
                  {[
                    { l:"Bazooka Earnings",            v:fmt(rc.bazNet),               c:"#E8317A" },
                    { l:"\u2212 Rep Commission",             v:"\u2212 "+fmt(rc.commAmt),         c:"#991b1b" },
                    ...(rc.tips>0 ? [{ l:"+ Tips (100% rep)", v:"+ "+fmt(rc.tips), c:"#FBBF24" }] : []),
                    ...(rc.salesBonus>0 ? [{ l:`🎁 Sales Bonus${recap.salesBonusNote?" — "+recap.salesBonusNote:""}`, v:"\u2212 "+fmt(rc.salesBonus), c:"#991b1b" }] : []),
                    ...(canSeeFinancials ? [{ l:"+ Rep Expense Share Back",   v:"+ "+fmt(rc.repExpShare||0),  c:"#4ade80" }] : []),
                    ...(canSeeFinancials ? [{ l:"− Bazooka Expense Share",    v:"− "+fmt(rc.bazExpShare||0), c:"#991b1b" }] : []),
                    ...(canSeeFinancials && isRemoteBreaker(recap.breaker) && ((reimbursesMagpros(recap.breaker)?(parseFloat(recap.magpros)||0):0)+(reimbursesPackaging(recap.breaker)?(parseFloat(recap.packagingMaterial)||0):0))>0 ? [{ l:"🔄 "+(recap.breaker||"Remote")+" Reimb (Mags/Pack)", v:"+ "+fmt((reimbursesMagpros(recap.breaker)?(parseFloat(recap.magpros)||0):0)+(reimbursesPackaging(recap.breaker)?(parseFloat(recap.packagingMaterial)||0):0)), c:"#FBBF24" }] : []),
                    ...(canSeeFinancials && isRemoteBreaker(recap.breaker) && (parseFloat(recap.biguShipping)||0)>0 ? [{ l:"📦 Shipping reimb (50% of "+fmt(parseFloat(recap.biguShipping)||0)+")", v:"+ "+fmt((parseFloat(recap.biguShipping)||0)*0.5), c:"#FBBF24" }] : []),
                    ...(canSeeFinancials && rc.eventStaffAmt>0 ? [{ l:`🎪 Event Staff (${(recap.eventStaff||[]).map(e=>e.breaker).join(", ")})`, v:"\u2212 "+fmt(rc.eventStaffAmt), c:"#A78BFA" }] : []),
                    ...(canSeeFinancials && rc.imcDirectReimb>0 ? [{ l:`💙 IMC Direct Reimb${recap.imcReimbNote?" — "+recap.imcReimbNote:""}`, v:"+ "+fmt(rc.imcDirectReimb), c:"#60A5FA" }] : []),
                    ...(canSeeFinancials ? [{ l:"+ IMC Expense Reimb (70%)", v:"+ "+fmt(rc.imcReimb||0), c:"#60A5FA" }] : []),
                    ...(canSeeFinancials ? [{ l:"Bazooka True Net",           v:fmt(rc.bazTrueNet),           c:"#166534" }] : []),
                  ].map(({l,v,c}) => (
                    <div key={l} style={{ textAlign:"center", background: l==="Bazooka True Net"?"#D6F4E3":"#FFFFFF", borderRadius:8, padding:"10px 8px", border:`1px solid ${l==="Bazooka True Net"?"#16653444":"#F0E0E8"}` }}>
                      <div style={{ fontSize:20, fontWeight:900, color:c }}>{v}</div>
                      <div style={{ fontSize:9, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginTop:3 }}>{l}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10 }}>
                {[
                  { l:`Your Commission (${(rc.rate*100).toFixed(0)}%)`, v:fmt(rc.commAmt), c:"#4ade80" },
                  ...(rc.tips>0?[{ l:"Tips (yours, 100%)", v:fmt(rc.tips), c:"#FBBF24" }]:[]),
                  ...(rc.salesBonus>0?[{ l:`🎁 Sales Bonus${recap.salesBonusNote?" — "+recap.salesBonusNote:""}`, v:"+"+fmt(rc.salesBonus), c:"#A78BFA" }]:[]),
                  ...((rc.tips>0||rc.salesBonus>0)?[{ l:"Total You Earn", v:fmt(rc.commAmt+rc.tips+rc.salesBonus), c:"#4ade80" }]:[]),
                ].map(({l,v,c}) => (
                  <div key={l} style={{ textAlign:"center" }}>
                    <div style={{ fontSize:18, fontWeight:900, color:c }}>{v}</div>
                    <div style={{ fontSize:9, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginTop:3 }}>{l}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          <Btn onClick={handleSaveRecap} disabled={!breaker||!date||(canSeeFinancials&&!recap.grossRevenue)||recapSaving} variant="green">
            {recapSaving ? "Saving..." : recapSaved ? "\u2705 Update Recap" : "\uD83D\uDCBE Save Stream Recap"}
          </Btn>
          {recapSaved && (() => {
            // Build IMC pre-fill URL — only use the saved URL from Firestore, no fallback
            const formBase = (imcFormUrl||"").trim();
            const hobby  = parseInt(recap[`prod_Hobby`])||0;
            const jumbo  = parseInt(recap[`prod_Jumbo`])||0;
            const dmega  = parseInt(recap[`prod_Double Mega`])||0;
            const misc   = parseInt(recap[`prod_Miscellaneous`])||0;
            const streamExpenses = [
              parseFloat(recap.whatnotPromo)>0?`WN Promo: $${parseFloat(recap.whatnotPromo).toFixed(2)}`:"",
              parseFloat(recap.coupons)>0?`Coupons: $${parseFloat(recap.coupons).toFixed(2)}`:"",
              (() => {
                const v = parseFloat(recap.magpros)||0;
                const q = parseInt(recap.magprosQty)||0;
                const price = parseFloat(recap.streamSkuPrices?.supply_magpros ?? skuPrices?.supply_magpros)||0;
                const calc = q > 0 && price > 0 ? q * price : 0;
                const final = v > 0 ? v : calc;
                return final > 0 ? `MagPros: $${final.toFixed(2)}` : "";
              })(),
              (() => {
                const v = parseFloat(recap.packagingMaterial)||0;
                const q = parseInt(recap.packagingQty)||0;
                const price = parseFloat(recap.streamSkuPrices?.supply_packaging ?? skuPrices?.supply_packaging)||0;
                const calc = q > 0 && price > 0 ? q * price : 0;
                const final = v > 0 ? v : calc;
                return final > 0 ? `Packaging: $${final.toFixed(2)}` : "";
              })(),
              (() => {
                const v = parseFloat(recap.topLoaders)||0;
                const q = parseInt(recap.topLoadersQty)||0;
                const price = parseFloat(recap.streamSkuPrices?.supply_topLoaders ?? skuPrices?.supply_topLoaders)||0;
                const calc = q > 0 && price > 0 ? q * price : 0;
                const final = v > 0 ? v : calc;
                return final > 0 ? `Top Loaders: $${final.toFixed(2)}` : "";
              })(),
              parseFloat(recap.chaserCards)>0?`Chasers: $${parseFloat(recap.chaserCards).toFixed(2)}`:"",
            ].filter(Boolean).join(", ") || "None";
            const totalStreamExp = (
              (parseFloat(recap.whatnotPromo)||0) +
              (parseFloat(recap.coupons)||0) +
              (parseFloat(recap.magpros)||0) +
              (parseFloat(recap.packagingMaterial)||0) +
              (parseFloat(recap.topLoaders)||0) +
              (parseFloat(recap.chaserCards)||0)
            ).toFixed(2);
            const formDate = date ? new Date(date+"T12:00:00").toLocaleDateString("en-US",{month:"2-digit",day:"2-digit",year:"numeric"}) : "";
            const params = new URLSearchParams({
              [`entry.546325134`]:  "Bazooka",
              [`entry.53983190`]:   formDate,
              [`entry.1397101824`]: hobby||"0",
              [`entry.473640875`]:  jumbo||"0",
              [`entry.2005003030`]: dmega||"0",
              [`entry.1594275904`]: misc>0?`Miscellaneous: ${misc} box${misc!==1?"es":""}. `:"",
              [`entry.1550026312`]: (parseFloat(recap.grossRevenue)||0).toFixed(2),
              [`entry.1898010524`]: (parseFloat(recap.whatnotFees)||0).toFixed(2),
              [`entry.2063681927`]: streamExpenses,
              [`entry.1117405477`]: "Devin Street",
            });
            const imcUrl = `${formBase}?usp=pp_url&entry.emailAddress=devin%40bazookabreaks.com&${params.toString()}`;
            if (!formBase) return <span style={{ fontSize:11, color:"#E8317A" }}>⚠ Set IMC Form URL below before submitting</span>;
            return (
              <a href={imcUrl} target="_blank" rel="noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#1a0a0f", border:"1.5px solid #E8317A44", color:"#E8317A", borderRadius:9, padding:"8px 16px", fontSize:12, fontWeight:700, textDecoration:"none", whiteSpace:"nowrap" }}>
                {"\uD83D\uDCCB Submit to IMC \u2197"}</a>
            );
          })()}
          {recapSaved && (
            <Btn onClick={()=>{ csvDataLoaded.current=false; setDate(""); setRecap({...EMPTY_RECAP}); setRecapSaved(false); setEditingStreamId(null); }} variant="ghost">
              + New Stream
            </Btn>
          )}
          {existingStream && !recapSaved && <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{"\u26A0 Unsaved changes"}</span>}
        </div>
        {/* IMC Form URL setting -- Admin only */}
        {userRole?.role === "Admin" && (
          <div style={{ marginTop:12 }}>
            <div style={{ fontSize:10, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginBottom:5 }}>IMC Form URL (updates for all future submissions)</div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <input
                key={imcFormUrl}
                defaultValue={imcFormUrl}
                onBlur={e=>{ if(onSaveImcFormUrl && e.target.value.trim()) onSaveImcFormUrl(e.target.value.trim()); }}
                onKeyDown={e=>{ if(e.key==="Enter"&&onSaveImcFormUrl&&e.target.value.trim()) { onSaveImcFormUrl(e.target.value.trim()); e.target.blur(); }}}
                placeholder="Paste new Google Form URL here..."
                style={{ ...S.inp, fontSize:11, padding:"6px 10px", color:imcFormUrl?"#7B9CFF":"#555", border:imcFormUrl?"1px solid rgba(123,156,255,0.3)":"1px solid var(--bz-line-2)", flex:1 }}
              />
              {imcFormUrl && <span style={{ fontSize:10, color:"#4ade80", whiteSpace:"nowrap" }}>✓ Saved</span>}
              {!imcFormUrl && <span style={{ fontSize:10, color:"#E8317A", whiteSpace:"nowrap" }}>⚠ No URL set</span>}
            </div>
            {imcFormUrl && <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{imcFormUrl}</div>}
          </div>
        )}

        </div>{/* end left column */}
        {(()=>{
          const rc = hasRecapData ? calcStream({ ...recap, breaker, date }) : null;
          const mm = parseFloat(recap.marketMultiple)||0;
          const mmColor = mm>=1.8?"#4ade80":mm>=1.7?"#86efac":mm>=1.5?"#FBBF24":mm>0?"#E8317A":"#555";
          const rate = rc ? rc.rate : 0;
          return (
            <div style={{ width:210, flexShrink:0, position:"sticky", top:80 }}>
              <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:14, padding:"16px 14px" }}>
                <div style={{ fontSize:10, fontWeight:800, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:"1px", marginBottom:12 }}>Live Preview</div>
                {!hasRecapData ? (
                  <div style={{ textAlign:"center", padding:"20px 0", color:"var(--bz-ink-3)", fontSize:12 }}>Enter gross revenue<br/>to see the math</div>
                ) : (
                  <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                    {mm > 0 && (
                      <div style={{ background:`${mmColor}12`, border:`1px solid ${mmColor}33`, borderRadius:8, padding:"6px 10px", display:"flex", justifyContent:"space-between" }}>
                        <span style={{ fontSize:11, color:mmColor, fontWeight:700 }}>{mm.toFixed(2)}x MM</span>
                        <span style={{ fontSize:11, color:mmColor, fontWeight:800 }}>{(rate*100).toFixed(0)}% rate</span>
                      </div>
                    )}
                    {canSeeFinancials && [
                      { l:"Gross",       v:rc.gross,          c:"var(--bz-ink)" },
                      { l:"Split Base",  v:rc.splitBase,      c:"#888" },
                      { l:"IMC (70%)",   v:rc.imcNet,         c:"#7B9CFF" },
                      { l:"Baz (30%)",   v:rc.bazNet,         c:"#E8317A" },
                      ...(rc.collabAmt>0?[{ l:"Collab cut",   v:-rc.collabAmt,        c:"#7B9CFF" }]:[]),
                      { l:"Commission",  v:-rc.primaryCommAmt, c:"#ef4444" },
                      { l:"Exp share",   v:-rc.repExpShare,    c:"#ef4444" },
                      ...(rc.imcReimb>0?[{ l:"IMC Reimb",     v:rc.imcReimb,          c:"#4ade80" }]:[]),
                    ].filter(x=>x.v!==0).map(({l,v,c},i,arr) => (
                      <div key={l} style={{ display:"flex", justifyContent:"space-between", paddingBottom:i<arr.length-1?"6px":0, borderBottom:i<arr.length-1?"1px solid var(--bz-s2)":"none" }}>
                        <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{l}</span>
                        <span style={{ fontSize:12, fontWeight:700, color:c }}>{v<0?"-":""}${Math.abs(v).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                      </div>
                    ))}
                    <div style={{ background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.2)", borderRadius:8, padding:"10px", marginTop:2 }}>
                      <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.5px" }}>Rep payout</div>
                      <div style={{ fontSize:20, fontWeight:900, color:"#4ade80" }}>
                        ${rc.myComm.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}
                      </div>
                      {(parseFloat(recap.tips)||0)>0&&<div style={{ fontSize:10, color:"#FBBF24", marginTop:3 }}>incl. ${parseFloat(recap.tips).toFixed(2)} tips</div>}
                      {(parseFloat(recap.salesBonus)||0)>0&&<div style={{ fontSize:10, color:"#A78BFA", marginTop:2 }}>+ ${parseFloat(recap.salesBonus).toFixed(2)} bonus</div>}
                    </div>
                    {canSeeFinancials && (
                    <div style={{ display:"flex", justifyContent:"space-between", borderTop:"1px solid var(--bz-line)", paddingTop:8 }}>
                      <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>True Net</span>
                      <span style={{ fontSize:14, fontWeight:900, color:"#A78BFA" }}>${rc.bazTrueNet.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                    </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        </div>{/* end two-column layout */}
      </div>}
      {!cardsOnly && (() => {
        const calcS = (s) => calcStream(s);
        const myStreams = (canSeeFinancials ? streams : streams.filter(s => s.breaker === matchedBreaker))
          .filter(s => !streamLogBreaker || s.breaker === streamLogBreaker)
          .filter(s => !streamLogMonth || (s.date||"").startsWith(streamLogMonth));
        return (
          <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"14px 20px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap", cursor:"pointer" }} onClick={()=>setStreamLogCollapsed(p=>!p)}>
              <SectionLabel t={`Stream Log (${myStreams.length})`} />
              <div style={{ display:"flex", gap:8, alignItems:"center" }} onClick={e=>e.stopPropagation()}>
                {(() => {
                  const months = [...new Set((canSeeFinancials ? streams : streams.filter(s=>s.breaker===matchedBreaker)).map(s=>(s.date||"").slice(0,7)).filter(Boolean))].sort().reverse();
                  const fmtMonth = ym => { const [y,m]=ym.split("-"); return new Date(+y,+m-1,1).toLocaleDateString("en-US",{month:"short",year:"numeric"}); };
                  return (
                    <select value={streamLogMonth} onChange={e=>setStreamLogMonth(e.target.value)} style={{ ...S.inp, width:"auto", fontSize:11, padding:"4px 10px", cursor:"pointer" }}>
                      <option value="">All Months</option>
                      {months.map(ym=><option key={ym} value={ym}>{fmtMonth(ym)}</option>)}
                    </select>
                  );
                })()}
                {canSeeFinancials && (
                  <select value={streamLogBreaker} onChange={e=>setStreamLogBreaker(e.target.value)} style={{ ...S.inp, width:"auto", fontSize:11, padding:"4px 10px", cursor:"pointer" }}>
                    <option value="">All Breakers</option>
                    {BREAKERS.map(b=><option key={b} value={b}>{b}</option>)}
                  </select>
                )}
                {streamBulkSel.size > 0 && (
                  <button onClick={()=>{
                    if(window.confirm(`Delete ${streamBulkSel.size} stream${streamBulkSel.size!==1?"s":""}? Chaser cards will be restored.`)) {
                      [...streamBulkSel].forEach(id => { if(onDeleteStream) onDeleteStream(id); });
                      setStreamBulkSel(new Set());
                    }
                  }} style={{ background:"#1a0a0a", color:"#E8317A", border:"1.5px solid #fca5a5", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                    {"\uD83D\uDDD1 Delete"}{streamBulkSel.size} stream{streamBulkSel.size!==1?"s":""}
                  </button>
                )}
                <span style={{ color:"var(--bz-ink-2)", fontSize:14, userSelect:"none" }}>{streamLogCollapsed ? "▼" : "▲"}</span>
              </div>
            </div>
            {!streamLogCollapsed && (myStreams.length === 0
              ? <div style={{ textAlign:"center", color:"var(--bz-ink-2)", padding:"30px 0" }}>No streams logged yet -- save a stream recap above to get started</div>
              : <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...S.th, width:40, textAlign:"center" }}>
                      <input type="checkbox"
                        checked={myStreams.length>0 && streamBulkSel.size===myStreams.length}
                        onChange={()=>setStreamBulkSel(streamBulkSel.size===myStreams.length ? new Set() : new Set(myStreams.map(s=>s.id)))}
                      />
                    </th>
                    {["Date","Breaker","Stream Name","Gross","Net Rev",canSeeFinancials?"Owed to IM":null,canSeeFinancials?"Baz Earnings":null,"Commission",canSeeFinancials?"True Net":null,"Rate","New Buyers","Collab","Product",""].filter(Boolean).map(h=><th key={h} style={S.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {myStreams.map((s,i) => {
                    const c = calcS(s);
                    const bc = BC[s.breaker]||{bg:"#F3F4F6",text:"var(--bz-ink-3)"};
                    const isActive = existingStream?.id === s.id;
                    return (
                      <tr key={s.id}
                        onClick={()=>{ setBreaker(s.breaker); setDate(s.date); setEditingStreamId(s.id); setRecapSaved(false); }}
                        className="clickable-row"
                        style={{ background:isActive?"#2a1520":i%2===0?"var(--bz-s1)":"var(--bz-s1)", cursor:"pointer", borderBottom:"1px solid #FFF0F5" }}
                        title="Click to load this stream"
                      >
                        <td style={{ ...S.td, textAlign:"center" }} onClick={e=>e.stopPropagation()}>
                          <input type="checkbox" checked={streamBulkSel.has(s.id)}
                            onChange={()=>setStreamBulkSel(prev=>{ const n=new Set(prev); n.has(s.id)?n.delete(s.id):n.add(s.id); return n; })}
                          />
                        </td>
                        <td style={S.td}>{s.date}</td>
                        <td style={S.td}><Badge bg={bc.bg} color={bc.text}>{s.breaker}</Badge></td>
                        <td style={{ ...S.td, color: s.streamName?"var(--bz-ink)":"#444", fontStyle: s.streamName?"normal":"italic", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.streamName||"—"}</td>
                        <td style={{ ...S.td, color:"var(--bz-ink)", fontWeight:700 }}>{fmt(c.gross)}</td>
                        <td style={{ ...S.td, color:"var(--bz-ink)" }}>{fmt(c.netRev)}</td>
                        {canSeeFinancials && <td style={{ ...S.td, color:"#E8317A" }}>{fmt(c.imcNet)}</td>}
                        {canSeeFinancials && <td style={{ ...S.td, color:"#E8317A" }}>{fmt(c.bazNet)}</td>}
                        <td style={{ ...S.td, color:"#E8317A", fontWeight:700 }}>{fmt(c.commAmt)}</td>
                        {canSeeFinancials && <td style={{ ...S.td, color:"#E8317A", fontWeight:900 }}>{fmt(c.bazTrueNet)}</td>}
                        <td style={{ ...S.td, color:"var(--bz-ink-2)" }}>{(c.rate*100).toFixed(0)}%{s.isEvent?" Event":s.binOnly?" BIN":""}</td>
                        <td style={{ ...S.td, color:"#E8317A" }}>{parseInt(s.newBuyers)||0 > 0 ? `\uD83C\uDF31 ${s.newBuyers}` : "--"}</td>
                        <td style={S.td}>
                          {s.collabPartner && s.collabPartner !== "_"
                            ? <span style={{ fontSize:11, fontWeight:700, color:"#7B9CFF", background:"rgba(123,156,255,0.12)", border:"1px solid rgba(123,156,255,0.25)", borderRadius:20, padding:"2px 8px", whiteSpace:"nowrap" }}>🤝 {s.collabPartner}{s.collabPct?` (${s.collabPct}%)`:""}</span>
                            : <span style={{ color:"var(--bz-ink-3)" }}>--</span>
                          }
                        </td>
                        <td style={{ ...S.td, fontSize:10, color:"var(--bz-ink-2)", maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {PRODUCT_TYPES.filter(pt=>(parseInt(s[`prod_${pt}`])||0)>0).map(pt=>`${pt.split(" - ").pop()}×${s[`prod_${pt}`]}`).join(", ")||"--"}
                        </td>
                        <td style={S.td}>
                          <button
                            onClick={e=>{ e.stopPropagation(); if(window.confirm("Delete this stream?")) { if(onDeleteStream) onDeleteStream(s.id); if(existingStream?.id===s.id){ setRecap({...EMPTY_RECAP}); setRecapSaved(false); setEditingStreamId(null); } }}}
                            style={{ background:"none", border:"1px solid #FCA5A5", color:"#E8317A", borderRadius:5, padding:"2px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}
                          >{"\uD83D\uDDD1"}</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>)}
          </div>
        );
      })()}

      {/* -- LOG CARDS -- */}
      {!recapOnly && <>
      <div style={S.card}>
        <SectionLabel t="Log Card Out" />
        <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr 1fr":"1fr 1fr 1fr", gap:12, marginBottom:12 }}>
          <SelectInput label="Breaker"    value={breaker} onChange={v=>{setBreaker(v);setLinkedPlanId(null);}} options={BREAKERS}/>
          <TextInput   label="Date" type="date" value={date} onChange={setDate}/>
          <SelectInput label="Usage Type" value={usage}   onChange={setUsage}   options={USAGE_TYPES}/>
        </div>
        <div style={{ marginBottom:12 }}>
          <Field label="Search Card">
            <input value={cardSearch} onChange={e=>{setCardSearch(e.target.value);setCardId("");}} placeholder="Type to search available cards..." style={{...S.inp, fontSize:isMobile?16:13, padding:isMobile?"12px 14px":"8px 12px"}}/>
            {cardSearch.length > 0 && (
              <div style={{ border:"1px solid var(--bz-line-2)", borderRadius:8, overflow:"hidden", maxHeight:220, overflowY:"auto", background:"var(--bz-s1)", boxShadow:"0 4px 12px rgba(232,49,122,0.1)", marginTop:4 }}>
                {available.filter(c=>c.cardName.toLowerCase().includes(cardSearch.toLowerCase())).length===0
                  ? <div style={{ padding:"12px 16px", color:"var(--bz-ink-2)", fontSize:13 }}>No cards found</div>
                  : available.filter(c=>c.cardName.toLowerCase().includes(cardSearch.toLowerCase())).map(c => {
                      const cc = CC[c.cardType]||{bg:"#F3F4F6",text:"var(--bz-ink-3)"};
                      return (
                        <div key={c.id} onClick={()=>{setCardId(c.id);setCardSearch(c.cardName);}} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:isMobile?"14px 16px":"10px 16px", cursor:"pointer", background:cardId===c.id?"#1a0a0f":"var(--bz-s1)", borderBottom:"1px solid #FFF0F5" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <span style={{ fontWeight:700, fontSize:isMobile?15:13 }}>{c.cardName}</span>
                            <Badge bg={cc.bg} color={cc.text}>{c.cardType}</Badge>
                          </div>
                          {canSeeFinancials && <span style={{ fontSize:12, color:"var(--bz-ink-2)", fontWeight:600 }}>${(c.marketValue||0).toFixed(2)}</span>}
                        </div>
                      );
                    })
                }
              </div>
            )}
          </Field>
        </div>
        {selCard && (
          <div style={{ marginBottom:12, padding:"10px 14px", background:"var(--bz-s1)", borderRadius:8, display:"flex", gap:14, alignItems:"center", flexWrap:"wrap" }}>
            <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Selected: <strong style={{color:"var(--bz-ink)"}}>{selCard.cardName}</strong></span>
            <Badge bg={CC[selCard.cardType]?.bg} color={CC[selCard.cardType]?.text}>{selCard.cardType}</Badge>
            {canSeeFinancials && <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Value: <strong style={{color:"var(--bz-ink-2)"}}>${(selCard.marketValue||0).toFixed(2)}</strong></span>}
          </div>
        )}
        <div style={{ display:"flex", flexDirection:isMobile?"column":"row", gap:10, alignItems:isMobile?"stretch":"end" }}>
          <div style={{ flex:1 }}><TextInput label="Notes (optional)" value={notes} onChange={setNotes} placeholder="e.g. Break #2"/></div>
          <Btn onClick={handleAdd} disabled={!breaker||!cardId} variant="green">Log Card Out</Btn>
          <Btn onClick={()=>{setBulkMode(p=>!p);setBulkSel(new Set());}} variant="ghost">{bulkMode?"Cancel Bulk":"Bulk Log Out"}</Btn>
        </div>
        {bulkMode && (
          <div style={{ marginTop:16, borderTop:"1px solid #F0D0DC", paddingTop:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1.5, marginBottom:10 }}>Select cards to log out</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px,1fr))", gap:8, maxHeight:280, overflowY:"auto", marginBottom:12 }}>
              {available.map(c => {
                const cc=CC[c.cardType]||{bg:"#F3F4F6",text:"var(--bz-ink-3)"};
                const isSel=bulkSel.has(c.id);
                return (
                  <div key={c.id} onClick={()=>toggleBulk(c.id)} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", background:isSel?"#1a0a0f":"#FAFAFA", border:`1.5px solid ${isSel?"#E8317A":"#F0D0DC"}`, borderRadius:8, cursor:"pointer" }}>
                    <input type="checkbox" checked={isSel} onChange={()=>toggleBulk(c.id)} onClick={e=>e.stopPropagation()}/>
                    <div><div style={{ fontSize:12, fontWeight:700, color:"var(--bz-ink)" }}>{c.cardName}</div><Badge bg={cc.bg} color={cc.text}>{c.cardType}</Badge></div>
                  </div>
                );
              })}
            </div>
            {bulkSel.size>0 && <Btn onClick={handleBulkLog} disabled={!breaker} variant="green">{"\u2705 Log Out"}{bulkSel.size} Card{bulkSel.size!==1?"s":""}</Btn>}
          </div>
        )}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)", gap:12 }}>
        {BREAKERS.map(b => {
          const bc=BC[b]; const s=sum[b];
          return (
            <div key={b} style={{ ...S.card, border:`1px solid ${bc.border}44` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:isMobile?6:10 }}>
                <div style={{ fontWeight:900, fontSize:16, color:bc.text }}>{b}</div>
                <div style={{ fontSize:isMobile?20:24, fontWeight:900, color:bc.text }}>{s.total} <span style={{ fontSize:11, color:"var(--bz-ink-2)", fontWeight:400 }}>used</span></div>
              </div>
              {isMobile ? (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:4 }}>
                  {CARD_TYPES.map(ct => (
                    <div key={ct} style={{ display:"flex", justifyContent:"space-between", padding:"4px 8px", background:"var(--bz-s2)", borderRadius:6 }}>
                      <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{ct.replace(" Cards","")}</span>
                      <span style={{ fontSize:11, fontWeight:700, color:CC[ct]?.text }}>{s[ct]}</span>
                    </div>
                  ))}
                </div>
              ) : (
                CARD_TYPES.map(ct => (
                  <div key={ct} style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", borderBottom:"1px solid var(--bz-line)" }}>
                    <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{ct}</span>
                    <span style={{ fontSize:11, fontWeight:700, color:CC[ct]?.text }}>{s[ct]}</span>
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>

      <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
        <div style={{ padding:"16px 20px 0" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
            <SectionLabel t="Break History"/>
            {histSel.size > 0 && (
              <button onClick={handleBulkDeleteHist} style={{ background:"var(--bz-s1)", color:"#E8317A", border:"1.5px solid #fca5a5", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", marginBottom:14 }}>
                {"\uD83D\uDDD1 Remove"}{histSel.size} selected
              </button>
            )}
          </div>
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr>
              <th style={{ ...S.th, width:40, textAlign:"center" }}>
                <input type="checkbox" checked={breaks.length>0&&histSel.size===breaks.length} onChange={toggleAllHist}/>
              </th>
              {["Date","Breaker","Card Name","Card Type","Usage","Logged By","Notes",""].map(h=><th key={h} style={S.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {breaks.length===0 ? <EmptyRow msg="No breaks logged yet." cols={9}/> :
                [...breaks].reverse().map((b,i) => {
                  const bc=BC[b.breaker]||{bg:"#F3F4F6",text:"var(--bz-ink-3)"};
                  const cc=CC[b.cardType]||{bg:"#F3F4F6",text:"var(--bz-ink-3)"};
                  const isSel=histSel.has(b.id);
                  return (
                    <tr key={b.id} className="break-row fade-in" style={{ background:isSel?"#1a0a0f":i%2===0?"var(--bz-s1)":"var(--bz-s1)" }}>
                      <td style={{ ...S.td, textAlign:"center" }}><input type="checkbox" checked={isSel} onChange={()=>toggleHistSel(b.id)}/></td>
                      <td style={{ ...S.td, color:"var(--bz-ink-2)", fontSize:11 }}>{b.date}</td>
                      <td style={S.td}><Badge bg={bc.bg} color={bc.text}>{b.breaker}</Badge></td>
                      <td style={{ ...S.td, fontWeight:700 }}>{b.cardName}</td>
                      <td style={S.td}><Badge bg={cc.bg} color={cc.text}>{b.cardType}</Badge></td>
                      <td style={{ ...S.td, color:"var(--bz-ink-2)", fontSize:12 }}>{b.usage||"--"}</td>
                      <td style={{ ...S.td, color:"var(--bz-ink-2)", fontSize:12 }}>{b.loggedBy||"--"}</td>
                      <td style={{ ...S.td, color:"var(--bz-ink-2)", fontSize:12 }}>{b.notes||"--"}</td>
                      <td style={S.td}>
                        <button onClick={()=>{ if(window.confirm(`Remove "${b.cardName}" from break log? This will make the card available again.`)) onDeleteBreak(b.id); }} style={{ background:"none", border:"none", color:"var(--bz-ink-2)", cursor:"pointer", fontSize:14, padding:2 }} title="Remove from break log">{"\u2715"}</button>
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>
      </>}
    </div>
  );
}


function BuyersCRM({ defaultTab="table", buyers=[], csvImports=[], onDeleteImport, onClearAll, userRole, streams=[] }) {
  const isAdmin = ["Admin","Streamer"].includes(userRole?.role);
  const [search,       setSearch]       = useState("");
  const debouncedSearch = useDebounce(search, 250);
  const [sortBy,       setSortBy]       = useState("totalSpend");
  const [filterNew,    setFilterNew]    = useState(false);
  const [filterZero,   setFilterZero]   = useState(false);
  const [selected,     setSelected]     = useState(null);
  const [showImports,  setShowImports]  = useState(false);
  const [activeTab,    setActiveTab]    = useState(defaultTab);
  useEffect(()=>{setActiveTab(defaultTab);},[defaultTab]);
  const [selectedState, setSelectedState] = useState(null);

  // Export filters
  const [exportPeriod,  setExportPeriod]  = useState("all");
  const [exportFrom,    setExportFrom]    = useState("");
  const [exportTo,      setExportTo]      = useState("");
  const now = new Date();

  const fmt = n => `$${(parseFloat(n)||0).toFixed(2)}`;

  function buyerInPeriod(b) {
    if (exportPeriod === "all") return true;
    const d = b.lastSeen || b.firstSeen || "";
    if (!d) return false;
    const dt = new Date(d + "T12:00:00");
    if (exportPeriod === "month")   return dt.getMonth()===now.getMonth()&&dt.getFullYear()===now.getFullYear();
    if (exportPeriod === "quarter") { const q=Math.floor(now.getMonth()/3); return Math.floor(dt.getMonth()/3)===q&&dt.getFullYear()===now.getFullYear(); }
    if (exportPeriod === "year")    return dt.getFullYear()===now.getFullYear();
    if (exportPeriod === "custom"&&exportFrom&&exportTo) {
      const f=new Date(exportFrom+"T00:00:00"), t=new Date(exportTo+"T23:59:59");
      return dt>=f&&dt<=t;
    }
    return true;
  }

  // Calculate spend for the active period from spendHistory
  function getPeriodSpend(b) {
    if (exportPeriod === "all" || !(b.spendHistory||[]).length) return b.totalSpend||0;
    return (b.spendHistory||[]).filter(h => {
      if (!h.date) return false;
      const dt = new Date(h.date + "T12:00:00");
      if (exportPeriod === "month")   return dt.getMonth()===now.getMonth()&&dt.getFullYear()===now.getFullYear();
      if (exportPeriod === "quarter") { const q=Math.floor(now.getMonth()/3); return Math.floor(dt.getMonth()/3)===q&&dt.getFullYear()===now.getFullYear(); }
      if (exportPeriod === "year")    return dt.getFullYear()===now.getFullYear();
      if (exportPeriod === "custom"&&exportFrom&&exportTo) {
        const f=new Date(exportFrom+"T00:00:00"), t=new Date(exportTo+"T23:59:59");
        return dt>=f&&dt<=t;
      }
      return false;
    }).reduce((s,h)=>s+(h.spend||0),0);
  }

  function getPeriodOrders(b) {
    if (exportPeriod === "all" || !(b.spendHistory||[]).length) return b.orderCount||0;
    return (b.spendHistory||[]).filter(h => {
      if (!h.date) return false;
      const dt = new Date(h.date + "T12:00:00");
      if (exportPeriod === "month")   return dt.getMonth()===now.getMonth()&&dt.getFullYear()===now.getFullYear();
      if (exportPeriod === "quarter") { const q=Math.floor(now.getMonth()/3); return Math.floor(dt.getMonth()/3)===q&&dt.getFullYear()===now.getFullYear(); }
      if (exportPeriod === "year")    return dt.getFullYear()===now.getFullYear();
      if (exportPeriod === "custom"&&exportFrom&&exportTo) {
        const f=new Date(exportFrom+"T00:00:00"), t=new Date(exportTo+"T23:59:59");
        return dt>=f&&dt<=t;
      }
      return false;
    }).reduce((s,h)=>s+(h.orders||0),0);
  }

  function exportCSV() {
    const rows = buyers.filter(buyerInPeriod).sort((a,b)=>(b.totalSpend||0)-(a.totalSpend||0));
    const headers = ["Username","Full Name","City","State","ZIP","Total Spend","Order Count","First Seen","Last Seen","New Buyer"];
    const lines = [headers.join(","), ...rows.map(b=>[
      `"${b.username||""}"`,
      `"${b.fullName||""}"`,
      `"${b.city||""}"`,
      `"${b.state||""}"`,
      `"${b.zip||""}"`,
      (b.totalSpend||0).toFixed(2),
      b.orderCount||0,
      `"${b.firstSeen||""}"`,
      `"${b.lastSeen||""}"`,
      b.isNew?"Yes":"No",
    ].join(","))];
    const blob = new Blob([lines.join("\n")], {type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href=url; a.download=`bazooka-buyers-${exportPeriod}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  const filtered = useMemo(() => buyers.filter(b => {
    if (!buyerInPeriod(b)) return false;
    if (filterNew && !b.isNew) return false;
    if (filterZero && (b.totalSpend||0) === 0) return false;
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      return (b.username||"").toLowerCase().includes(q) ||
             (b.fullName||"").toLowerCase().includes(q) ||
             (b.city||"").toLowerCase().includes(q) ||
             (b.state||"").toLowerCase().includes(q);
    }
    return true;
  }).sort((a,b) => {
    if (sortBy==="totalSpend")  return getPeriodSpend(b)-getPeriodSpend(a);
    if (sortBy==="orderCount")  return getPeriodOrders(b)-getPeriodOrders(a);
    if (sortBy==="lastSeen")    return (b.lastSeen||"").localeCompare(a.lastSeen||"");
    if (sortBy==="username")    return (a.username||"").localeCompare(b.username||"");
    return 0;
  }), [buyers, debouncedSearch, filterNew, filterZero, sortBy, exportPeriod, exportFrom, exportTo]);

  const totalBuyers   = filtered.length;
  const totalRevenue  = filtered.reduce((s,b)=>s+getPeriodSpend(b),0);
  const newBuyers     = filtered.filter(b=>b.isNew).length;
  const avgSpend      = totalBuyers > 0 ? totalRevenue/totalBuyers : 0;

  // State breakdown
  const stateData = {};
  buyers.forEach(b => {
    const s = (b.state||"").toUpperCase().trim();
    if (!s || s.length !== 2) return;
    if (!stateData[s]) stateData[s] = { buyers:0, revenue:0 };
    stateData[s].buyers++;
    stateData[s].revenue += (b.totalSpend||0);
  });
  const stateEntries = Object.entries(stateData).sort((a,b)=>b[1].revenue-a[1].revenue);

  // Time zone breakdown
  const ZONE_MAP = {
    'ET':['CT','DC','DE','FL','GA','IN','KY','MA','MD','ME','MI','NC','NH','NJ','NY','OH','PA','RI','SC','TN','VA','VT','WV'],
    'CT':['AL','AR','IA','IL','KS','LA','MN','MO','MS','NE','ND','OK','SD','TX','WI'],
    'MT':['AZ','CO','ID','MT','NM','UT','WY'],
    'PT':['AK','CA','HI','NV','OR','WA'],
  };
  const zoneData = { 'ET':{ buyers:0, revenue:0 }, 'CT':{ buyers:0, revenue:0 }, 'MT':{ buyers:0, revenue:0 }, 'PT':{ buyers:0, revenue:0 }, 'Other':{ buyers:0, revenue:0 } };
  buyers.forEach(b => {
    const s = (b.state||"").toUpperCase().trim();
    let zone = 'Other';
    for (const [z, states] of Object.entries(ZONE_MAP)) { if (states.includes(s)) { zone = z; break; } }
    zoneData[zone].buyers++;
    zoneData[zone].revenue += (b.totalSpend||0);
  });

  const S = {
    card: { background:"linear-gradient(160deg,#181016,#130d11)", border:"1px solid var(--bz-line)", borderRadius:16, padding:"14px 16px", boxShadow:"0 1px 0 rgba(255,255,255,0.03) inset, 0 6px 22px rgba(0,0,0,0.35)" },
    inp:  { background:"var(--bz-s3)", border:"1px solid var(--bz-line)", borderRadius:8, color:"var(--bz-ink)", padding:"6px 10px", fontSize:12, fontFamily:"inherit", outline:"none" },
    th:   { padding:"10px 12px", textAlign:"left", fontSize:11, color:"var(--bz-ink-3)", fontWeight:600, textTransform:"uppercase", letterSpacing:1, borderBottom:"1px solid var(--bz-line)", whiteSpace:"nowrap" },
    td:   { padding:"10px 12px", borderBottom:"1px solid var(--bz-line)", fontSize:12 },
  };

  const maxRevenue = stateEntries.length > 0 ? stateEntries[0][1].revenue : 1;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* Stats row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
        {[
          { l:"Total Buyers",    v:totalBuyers.toLocaleString(),     c:"#7B9CFF" },
          { l:"Total Revenue",   v:`$${Math.round(totalRevenue).toLocaleString()}`, c:"#4ade80" },
          { l:"New Buyers",      v:newBuyers.toLocaleString(),        c:"#FBBF24" },
          { l:"Avg Spend",       v:fmt(avgSpend),                     c:"#E8317A" },
        ].map(({l,v,c})=>(
          <div key={l} style={{ ...S.card, textAlign:"center" }}>
            <div style={{ fontSize:22, fontWeight:900, color:c }}>{v}</div>
            <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:4 }}>{l}</div>
          </div>
        ))}
      </div>
      {isAdmin && buyers.length > 0 && (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
          {/* Export controls */}
          <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
            <span style={{ fontSize:11, color:"var(--bz-ink-3)", fontWeight:700 }}>Export:</span>
            {[["all","All Time"],["month","This Month"],["quarter","This Quarter"],["year","This Year"],["custom","Custom"]].map(([id,l])=>(
              <button key={id} onClick={()=>setExportPeriod(id)}
                style={{ background:exportPeriod===id?"rgba(232,49,122,0.15)":"transparent", border:`1px solid ${exportPeriod===id?"#E8317A":"#333"}`, color:exportPeriod===id?"#E8317A":"#555", borderRadius:16, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{l}</button>
            ))}
            {exportPeriod==="custom" && <>
              <input type="date" value={exportFrom} onChange={e=>setExportFrom(e.target.value)}
                style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:8, color:"var(--bz-ink)", padding:"3px 8px", fontSize:11, fontFamily:"inherit" }}/>
              <span style={{ color:"var(--bz-ink-3)", fontSize:11 }}>to</span>
              <input type="date" value={exportTo} onChange={e=>setExportTo(e.target.value)}
                style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:8, color:"var(--bz-ink)", padding:"3px 8px", fontSize:11, fontFamily:"inherit" }}/>
            </>}
            <button onClick={exportCSV}
              style={{ background:"linear-gradient(135deg,#E8317A,#c02060)", color:"#fff", border:"none", borderRadius:8, padding:"5px 14px", fontSize:11, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
              ⬇️ Download CSV ({buyers.filter(buyerInPeriod).length} buyers)
            </button>
          </div>
          <button onClick={()=>{ if(onClearAll) onClearAll(); }}
            style={{ background:"#1a0a0a", border:"1px solid #E8317A44", color:"#E8317A", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
            {"🗑 Clear All Buyers & Imports"}</button>
        </div>
      )}

      {/* Analytics tabs */}
      <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
        <div style={{ display:"flex", borderBottom:"1px solid var(--bz-line)" }}>
          {[["table","\uD83D\uDC65 Buyers"],["map","\uD83D\uDDFA\uFE0F By State"],["zones","\uD83D\uDD50 By Time Zone"]].map(([id,label])=>(
            <button key={id} onClick={()=>setActiveTab(id)}
              style={{ background:activeTab===id?"#1A1A2E":"transparent", color:activeTab===id?"#E8317A":"#555", border:"none", borderBottom:activeTab===id?"2px solid #E8317A":"2px solid transparent", padding:"10px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              {label}
            </button>
          ))}
        </div>

        {/* Buyers table */}
        {activeTab === "table" && (
          <>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", padding:"10px 14px", borderBottom:"1px solid var(--bz-line)" }}>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search username, name, city..." style={{ ...S.inp, flex:1, minWidth:180 }}/>
              <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
                <option value="totalSpend">Sort: Total Spend</option>
                <option value="orderCount">Sort: Orders</option>
                <option value="lastSeen">Sort: Last Seen</option>
                <option value="username">Sort: Username</option>
              </select>
              <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"var(--bz-ink-2)", cursor:"pointer" }}>
                <input type="checkbox" checked={filterNew} onChange={e=>setFilterNew(e.target.checked)}/>
                New only
              </label>
              <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"var(--bz-ink-2)", cursor:"pointer" }}>
                <input type="checkbox" checked={filterZero} onChange={e=>setFilterZero(e.target.checked)}/>
                Hide $0 buyers
              </label>
              {isAdmin && buyers.filter(b=>(b.totalSpend||0)===0).length > 0 && (
                <button onClick={async ()=>{
                  const zeroBuyers = buyers.filter(b=>(b.totalSpend||0)===0);
                  if (!window.confirm(`Remove ${zeroBuyers.length} $0 buyers (giveaway winners)? This cannot be undone.`)) return;
                  await Promise.all(zeroBuyers.map(b=>deleteDoc(doc(db,"buyers",b.id))));
                }}
                  style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)", color:"#ef4444", borderRadius:8, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  🗑 Remove {buyers.filter(b=>(b.totalSpend||0)===0).length} $0 buyers
                </button>
              )}
              <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{filtered.length} of {totalBuyers}</span>
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:600 }}>
                <thead>
                  <tr>{["Username","Name","Location","Orders",exportPeriod==="all"?"Total Spend":`Spend (${exportPeriod==="month"?"This Month":exportPeriod==="quarter"?"This Quarter":exportPeriod==="year"?"This Year":"Period"})`,"Avg/Order","Last Seen"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={7} style={{ ...S.td, textAlign:"center", color:"var(--bz-ink-3)", padding:32 }}>
                      {buyers.length === 0 ? "No buyers yet -- import a CSV from the Streams tab" : "No buyers match your search"}
                    </td></tr>
                  ) : filtered.map((b,i) => {
                    const pSpend  = getPeriodSpend(b);
                    const pOrders = getPeriodOrders(b);
                    const avgOrder = pOrders > 0 ? pSpend/pOrders : 0;
                    const loc = [b.city, b.state].filter(Boolean).join(", ");
                    const showingPeriod = exportPeriod !== "all";
                    return (
                      <tr key={b.id} onClick={()=>setSelected(selected?.id===b.id?null:b)}
                        style={{ cursor:"pointer", background:selected?.id===b.id?"#1A1A2E":i%2===0?"var(--bz-s1)":"var(--bz-s1)" }}
                        className="clickable-row">
                        <td style={S.td}>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            {b.isNew && <span style={{ fontSize:9, background:"#FBBF2422", color:"#FBBF24", border:"1px solid #FBBF2444", borderRadius:4, padding:"1px 5px", fontWeight:700 }}>NEW</span>}
                            <span style={{ color:"#7B9CFF", fontWeight:700 }}>@{b.username}</span>
                          </div>
                        </td>
                        <td style={{ ...S.td, color:"var(--bz-ink)" }}>{b.fullName||"--"}</td>
                        <td style={{ ...S.td, color:"var(--bz-ink-2)" }}>{loc||"--"}</td>
                        <td style={{ ...S.td, color:"var(--bz-ink)", textAlign:"center" }}>{pOrders}</td>
                        <td style={{ ...S.td, fontWeight:700 }}>
                          <span style={{ color:"#4ade80" }}>{fmt(pSpend)}</span>
                          {showingPeriod && pSpend !== (b.totalSpend||0) && <span style={{ color:"var(--bz-ink-3)", fontSize:10, marginLeft:6 }}>({fmt(b.totalSpend)} all time)</span>}
                        </td>
                        <td style={{ ...S.td, color:"var(--bz-ink-2)" }}>{fmt(avgOrder)}</td>
                        <td style={{ ...S.td, color:"var(--bz-ink-3)" }}>{b.lastSeen ? new Date(b.lastSeen).toLocaleDateString() : "--"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {selected && (
              <div style={{ borderTop:"1px solid var(--bz-line)", padding:"14px 16px", background:"var(--bz-bg)" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <div style={{ fontSize:14, fontWeight:800, color:"#7B9CFF" }}>@{selected.username}</div>
                  <button onClick={()=>setSelected(null)} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:16 }}>{"\u00D7"}</button>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:8 }}>
                  {[
                    { l:"Full Name",    v:selected.fullName||"--" },
                    { l:"Location",     v:[selected.city,selected.state,selected.zip].filter(Boolean).join(", ")||"--" },
                    { l:"Total Spend",  v:fmt(selected.totalSpend), c:"#4ade80" },
                    { l:"Orders",       v:selected.orderCount||0 },
                    { l:"Coupons Used", v:selected.couponCount||0 },
                    { l:"First Seen",   v:selected.firstSeen ? new Date(selected.firstSeen).toLocaleDateString() : "--" },
                    { l:"Last Seen",    v:selected.lastSeen  ? new Date(selected.lastSeen).toLocaleDateString()  : "--" },
                    { l:"Streams",      v:(selected.streams||[]).length },
                  ].map(({l,v,c})=>(
                    <div key={l} style={{ background:"var(--bz-s1)", borderRadius:7, padding:"8px 10px" }}>
                      <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:3 }}>{l}</div>
                      <div style={{ fontSize:13, fontWeight:700, color:c||"var(--bz-ink)" }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* By State */}
        {activeTab === "map" && (() => {
          const maxBuyers = stateEntries.length > 0 ? stateEntries[0][1].buyers : 1;
          function stateColor(abbr) {
            const d = stateData[abbr];
            if (!d) return "var(--bz-s2)";
            const t = d.buyers / maxBuyers;
            if (t > 0.8) return "#E8317A";
            if (t > 0.6) return "#cc2a68";
            if (t > 0.4) return "#a82255";
            if (t > 0.2) return "#7a1a3f";
            return "#4a1028";
          }
          return (
            <div style={{ padding:"12px 16px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10, fontSize:10, color:"var(--bz-ink-3)" }}>
                <span>Fewer buyers</span>
                {["#4a1028","#7a1a3f","#a82255","#cc2a68","#E8317A"].map(c=>(
                  <div key={c} style={{ width:20, height:12, background:c, borderRadius:2 }}/>
                ))}
                <span>More buyers</span>
                <span style={{ marginLeft:"auto", color:"var(--bz-ink-3)" }}>Click a state to see buyers</span>
              </div>

              {selectedState && (() => {
                const stateBuyers = buyers.filter(b=>(b.state||"").toUpperCase()===selectedState);
                const d = stateData[selectedState];
                return (
                  <div style={{ marginTop:12, background:"var(--bz-bg)", border:"1px solid #E8317A44", borderRadius:10, overflow:"hidden" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", borderBottom:"1px solid var(--bz-line)" }}>
                      <div>
                        <span style={{ fontSize:14, fontWeight:800, color:"#E8317A" }}>{selectedState}</span>
                        <span style={{ fontSize:12, color:"var(--bz-ink-3)", marginLeft:8 }}>{d?.buyers||0} buyer{d?.buyers!==1?"s":""} · {fmt(d?.revenue||0)}</span>
                      </div>
                      <button onClick={()=>setSelectedState(null)} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:16 }}>{"\u00D7"}</button>
                    </div>
                    <div style={{ maxHeight:260, overflowY:"auto" }}>
                      {stateBuyers.sort((a,b)=>(b.totalSpend||0)-(a.totalSpend||0)).map((b,i)=>(
                        <div key={b.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 14px", borderBottom:"1px solid #111", background:i%2===0?"var(--bz-bg)":"var(--bz-s1)" }}>
                          <span style={{ color:"#7B9CFF", fontWeight:700, fontSize:12 }}>@{b.username}</span>
                          {b.fullName && <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{b.fullName}</span>}
                          {b.city && <span style={{ fontSize:11, color:"#8a8a8a" }}>{b.city}</span>}
                          <span style={{ marginLeft:"auto", color:"#4ade80", fontWeight:700, fontSize:12 }}>{fmt(b.totalSpend)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}
        {/* By Time Zone */}
        {activeTab === "zones" && (
          <div style={{ padding:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"var(--bz-ink)", marginBottom:12 }}>Revenue by Time Zone</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:10 }}>
              {Object.entries(zoneData).map(([zone, data]) => {
                const colors = { ET:"#7B9CFF", CT:"#4ade80", MT:"#FBBF24", PT:"#E8317A", Other:"#555" };
                return (
                  <div key={zone} style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:8, padding:"12px 14px" }}>
                    <div style={{ fontSize:16, fontWeight:900, color:colors[zone] }}>{zone}</div>
                    <div style={{ fontSize:20, fontWeight:800, color:"var(--bz-ink)", marginTop:4 }}>{fmt(data.revenue)}</div>
                    <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>{data.buyers} buyer{data.buyers!==1?"s":""}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* CSV Imports -- collapsible */}
      <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
        <button onClick={()=>setShowImports(p=>!p)}
          style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px", background:"transparent", border:"none", color:"var(--bz-ink)", cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:700 }}>
          <span>{"\uD83D\uDCE5 CSV Imports ("}{csvImports.length})</span>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            {isAdmin && buyers.length > 0 && (
              <span onClick={e => { e.stopPropagation(); if(onClearAll) onClearAll(); }}
                style={{ fontSize:11, color:"#E8317A", border:"1px solid #E8317A33", borderRadius:6, padding:"2px 8px", cursor:"pointer", fontWeight:700 }}>
                {"\uD83D\uDDD1 Clear All"}</span>
            )}
            <span style={{ color:"var(--bz-ink-3)", fontSize:11 }}>{showImports?"\u25B2 collapse":"\u25BC expand"}</span>
          </div>
        </button>
        {showImports && csvImports.length > 0 && (
          <div style={{ borderTop:"1px solid var(--bz-line)", padding:"10px 14px", display:"flex", flexDirection:"column", gap:6 }}>
            {csvImports.map(imp => {
              const stream = streams.find(s=>s.id===imp.streamId);
              return (
                <div key={imp.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 10px", background:"var(--bz-bg)", borderRadius:7, border:"1px solid var(--bz-line)" }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"var(--bz-ink)" }}>{imp.filename}</div>
                    <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>
                      {imp.rowCount} buyers · {new Date(imp.importedAt).toLocaleDateString()}
                      {stream && <span style={{ color:"#7B9CFF", marginLeft:8 }}>{stream.name||stream.id}</span>}
                    </div>
                  </div>
                  {isAdmin && (
                    <button onClick={()=>{ if(window.confirm(`Delete import "${imp.filename}"?`)) onDeleteImport(imp.id); }}
                      style={{ background:"transparent", border:"1px solid #E8317A33", color:"#E8317A", borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                      Delete
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {showImports && csvImports.length === 0 && (
          <div style={{ borderTop:"1px solid var(--bz-line)", padding:24, textAlign:"center", color:"var(--bz-ink-3)", fontSize:12 }}>No CSV imports yet</div>
        )}
      </div>
    </div>
  );
}

// ── REP REPORT CARD ──────────────────────────────────────────────────────────
function RepReportCard({ streams=[], isAdmin }) {
  const [selBreaker, setSelBreaker] = useState(BREAKERS[0]);
  const [period, setPeriod] = useState("month");
  const [brand, setBrand] = useState("boba");
  const now = new Date();
  const fmt = v => "$"+Number(v||0).toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0});
  const fmt2 = v => "$"+Number(v||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});

  function filterStreams(slist, b) {
    return slist.filter(s => {
      if (s.breaker !== b) return false;
      if (brand !== "all" && getStreamBrand(s) !== brand) return false;
      const d = parseLocalDate(s.date);
      if (period==="week") { const wStart=new Date(now); wStart.setDate(now.getDate()-(now.getDay()===0?6:now.getDay()-1)); wStart.setHours(0,0,0,0); const wEnd=new Date(wStart); wEnd.setDate(wStart.getDate()+6); return d>=wStart&&d<=wEnd; }
      if (period==="month") return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
      if (period==="quarter") { const q=Math.floor(now.getMonth()/3); return Math.floor(d.getMonth()/3)===q&&d.getFullYear()===now.getFullYear(); }
      if (period==="year") return d.getFullYear()===now.getFullYear();
      return true;
    });
  }

  const repStreams = filterStreams(streams, selBreaker);
  const allBreakerStreams = BREAKERS.map(b=>filterStreams(streams,b));
  const teamStreams = streams.filter(s=>{ const d=parseLocalDate(s.date); if(period==="month") return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear(); if(period==="year") return d.getFullYear()===now.getFullYear(); return true; });

  function calcStats(slist, targetBreaker=null) {
    if (!slist.length) return { gross:0, mm:0, comm:0, newBuyers:0, streams:0, perStream:0 };
    const gross = slist.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);
    const mmList = slist.filter(x=>parseFloat(x.marketMultiple)>0);
    const mm = mmList.length ? mmList.reduce((s,x)=>s+(parseFloat(x.marketMultiple)||0),0)/mmList.length : 0;
    const newBuyers = slist.reduce((s,x)=>s+(parseInt(x.newBuyers)||0),0);
    const comm = slist.reduce((s,x)=>s+calcStream(x, targetBreaker).myComm, 0);
    return { gross, mm, comm, newBuyers, streams:slist.length, perStream:gross/slist.length };
  }

  const repStats = calcStats(repStreams, selBreaker);
  const teamStats = calcStats(teamStreams);
  const mmColor = v => v>=1.7?"#4ade80":v>=1.5?"#86efac":v>=1.4?"#FBBF24":"#E8317A";

  // Week-by-week MM trend (last 8 weeks)
  const weekTrends = Array.from({length:8},(_,i)=>{
    const wEnd = new Date(now); wEnd.setDate(now.getDate() - i*7); wEnd.setHours(23,59,59,999);
    const wStart = new Date(wEnd); wStart.setDate(wEnd.getDate()-6); wStart.setHours(0,0,0,0);
    const ws = repStreams.filter(s=>{ const d=parseLocalDate(s.date); return d>=wStart&&d<=wEnd; });
    const mmList = ws.filter(s=>parseFloat(s.marketMultiple)>0);
    const mm = mmList.length ? mmList.reduce((s,x)=>s+(parseFloat(x.marketMultiple)||0),0)/mmList.length : null;
    return { label:`W${8-i}`, mm, gross:ws.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0), streams:ws.length };
  }).reverse();

  const best = repStreams.length ? repStreams.reduce((a,b)=>(parseFloat(b.grossRevenue)||0)>(parseFloat(a.grossRevenue)||0)?b:a) : null;
  const worst = repStreams.length ? repStreams.reduce((a,b)=>(parseFloat(b.grossRevenue)||0)<(parseFloat(a.grossRevenue)||0)?b:a) : null;

  const maxMM = Math.max(...weekTrends.map(w=>w.mm||0), 2.0);
  const bc = BC[selBreaker]||"#E8317A";

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      {/* Controls */}
      <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:6}}>
          {BREAKERS.map(b=><button key={b} onClick={()=>setSelBreaker(b)}
            style={{background:selBreaker===b?`${BC[b]}22`:"transparent",border:`1.5px solid ${selBreaker===b?BC[b]:"#333"}`,color:selBreaker===b?BC[b]:"#555",borderRadius:20,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{b}</button>)}
        </div>
        <div style={{display:"flex",gap:4}}>
          {[["boba","🃏 BoBA"],["wotf","🐉 WotF"],["all","All"]].map(([id,l])=>(
            <button key={id} onClick={()=>setBrand(id)} style={{background:brand===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${brand===id?"#E8317A":"#333"}`,color:brand===id?"#E8317A":"#555",borderRadius:16,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
          ))}
        </div>
        <div style={{display:"flex",gap:6,marginLeft:"auto"}}>
          {[["week","Week"],["month","Month"],["quarter","Quarter"],["year","Year"],["all","All Time"]].map(([id,l])=>(
            <button key={id} onClick={()=>setPeriod(id)} style={{background:period===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${period===id?"#E8317A":"#333"}`,color:period===id?"#E8317A":"#555",borderRadius:16,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
          ))}
        </div>
      </div>

      {/* Header card */}
      <div style={{background:`linear-gradient(135deg,${bc}11,var(--bz-s1))`,border:`2px solid ${bc}33`,borderRadius:14,padding:"20px 24px"}}>
        <div style={{fontSize:20,fontWeight:900,color:"var(--bz-ink)",marginBottom:4}}>{selBreaker} · Report Card</div>
        <div style={{fontSize:12,color:"var(--bz-ink-3)"}}>{repStreams.length} streams in period</div>
      </div>

      {/* KPI grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
        {[
          {label:"Gross Revenue",     rep:fmt(repStats.gross),     team:fmt(teamStats.gross/Math.max(1,BREAKERS.length)),   better: repStats.gross >= teamStats.gross/Math.max(1,BREAKERS.length) },
          {label:"Avg Per Stream",    rep:fmt(repStats.perStream), team:fmt(teamStats.perStream), better: repStats.perStream >= teamStats.perStream },
          {label:"Avg Market Mult",   rep:repStats.mm?repStats.mm.toFixed(2)+"x":"--", team:teamStats.mm?teamStats.mm.toFixed(2)+"x":"--", better: repStats.mm >= teamStats.mm, color: repStats.mm ? mmColor(repStats.mm) : "#555" },
          {label:"New Buyers",        rep:repStats.newBuyers,      team:Math.round(teamStats.newBuyers/Math.max(1,BREAKERS.length)), better: repStats.newBuyers >= teamStats.newBuyers/Math.max(1,BREAKERS.length) },
          {label:"Streams",           rep:repStats.streams,        team:Math.round(teamStats.streams/Math.max(1,BREAKERS.length)), better: repStats.streams >= teamStats.streams/Math.max(1,BREAKERS.length) },
          {label:"Commission",        rep:fmt(repStats.comm),      team:fmt(teamStats.comm/Math.max(1,BREAKERS.length)),    better: repStats.comm >= teamStats.comm/Math.max(1,BREAKERS.length) },
        ].map(({label,rep,team,better,color})=>(
          <div key={label} style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"14px 16px"}}>
            <div style={{fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>{label}</div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
              <div>
                <div style={{fontSize:22,fontWeight:900,color:color||(better?"#4ade80":"#E8317A")}}>{rep}</div>
                <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:2}}>vs team avg: {team}</div>
              </div>
              <div style={{fontSize:18}}>{better?"✅":"⚠️"}</div>
            </div>
          </div>
        ))}
      </div>

      {/* MM trend chart */}
      <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px"}}>
        <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:14}}>Market Multiple Trend (Last 8 Weeks)</div>
        <div style={{display:"flex",gap:6,alignItems:"flex-end",height:120}}>
          {weekTrends.map((w,i)=>{
            const barH = w.mm ? (w.mm/maxMM)*100 : 0;
            const col = w.mm ? mmColor(w.mm) : "var(--bz-line-2)";
            return (
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <div style={{fontSize:9,color:col,fontWeight:700}}>{w.mm?w.mm.toFixed(2)+"x":""}</div>
                <div style={{width:"100%",height:90,display:"flex",alignItems:"flex-end"}}>
                  <div style={{width:"100%",height:`${barH}%`,background:col,borderRadius:"3px 3px 0 0",minHeight: w.mm?4:0,opacity:0.8}}/>
                </div>
                <div style={{fontSize:9,color:"var(--bz-ink-3)"}}>{w.label}</div>
                {w.streams>0&&<div style={{fontSize:9,color:"#8a8a8a"}}>{w.streams}s</div>}
              </div>
            );
          })}
        </div>
        {/* 1.5x reference line label */}
        <div style={{fontSize:10,color:"#FBBF24",marginTop:8}}>— 1.5x target line</div>
      </div>

      {/* Best / Worst streams */}
      {(best||worst) && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          {best && <div style={{background:"rgba(74,222,128,0.05)",border:"1px solid rgba(74,222,128,0.2)",borderRadius:12,padding:"14px 16px"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#4ade80",marginBottom:6}}>🏆 Best Stream</div>
            <div style={{fontSize:14,fontWeight:900,color:"var(--bz-ink)"}}>{best.streamName||best.date}</div>
            <div style={{fontSize:13,color:"#4ade80",marginTop:4}}>{fmt(parseFloat(best.grossRevenue)||0)} · {best.marketMultiple?best.marketMultiple+"x":""}</div>
          </div>}
          {worst && <div style={{background:"rgba(232,49,122,0.05)",border:"1px solid rgba(232,49,122,0.2)",borderRadius:12,padding:"14px 16px"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#E8317A",marginBottom:6}}>📉 Lowest Stream</div>
            <div style={{fontSize:14,fontWeight:900,color:"var(--bz-ink)"}}>{worst.streamName||worst.date}</div>
            <div style={{fontSize:13,color:"#E8317A",marginTop:4}}>{fmt(parseFloat(worst.grossRevenue)||0)} · {worst.marketMultiple?worst.marketMultiple+"x":""}</div>
          </div>}
        </div>
      )}
    </div>
  );
}

// ── TEAM LEADERBOARD ─────────────────────────────────────────────────────────
function TeamLeaderboard({ streams=[], allStreams=[], isAdmin }) {
  const [period, setPeriod] = useState("month");
  const [sortBy, setSortBy] = useState("mm");
  const [brand, setBrand]   = useState("boba");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo,   setCustomTo]   = useState("");
  const now = new Date();
  const fmt = v => "$"+Number(v||0).toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0});

  function filterByPeriod(slist) {
    return slist.filter(s=>{ const d=parseLocalDate(s.date);
      if (brand !== "all" && getStreamBrand(s) !== brand) return false;
      if(period==="week"){const wS=new Date(now);wS.setDate(now.getDate()-(now.getDay()===0?6:now.getDay()-1));wS.setHours(0,0,0,0);const wE=new Date(wS);wE.setDate(wS.getDate()+6);return d>=wS&&d<=wE;}
      if(period==="month") return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
      if(period==="quarter"){const q=Math.floor(now.getMonth()/3);return Math.floor(d.getMonth()/3)===q&&d.getFullYear()===now.getFullYear();}
      if(period==="year") return d.getFullYear()===now.getFullYear();
      if(period==="custom"&&customFrom&&customTo){const f=new Date(customFrom);f.setHours(0,0,0,0);const t=new Date(customTo);t.setHours(23,59,59,999);return d>=f&&d<=t;}
      return true;
    });
  }

  const filtered = filterByPeriod(allStreams);

  const rankings = BREAKERS.map(b=>{
    const ownStreams = filtered.filter(s=>s.breaker===b);
    const allMyStreams = filtered.filter(s=>s.breaker===b||(s.eventStaff||[]).some(es=>es.breaker===b)||s.splitRep===b);
    if (!allMyStreams.length) return null;
    const gross = ownStreams.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);
    const mmList = ownStreams.filter(x=>parseFloat(x.marketMultiple)>0);
    const mm = mmList.length?mmList.reduce((s,x)=>s+(parseFloat(x.marketMultiple)||0),0)/mmList.length:0;
    const newBuyers = ownStreams.reduce((s,x)=>s+(parseInt(x.newBuyers)||0),0);
    const comm = allMyStreams.reduce((s,x)=>s+calcStream(x,b).myComm,0);
    return { b, gross, mm, newBuyers, comm, streams:ownStreams.length, perStream:ownStreams.length?gross/ownStreams.length:0 };
  }).filter(Boolean).sort((a,z)=>{
    if(sortBy==="mm") return z.mm-a.mm;
    if(sortBy==="gross") return z.gross-a.gross;
    if(sortBy==="newbuyers") return z.newBuyers-a.newBuyers;
    if(sortBy==="comm") return z.comm-a.comm;
    return z.perStream-a.perStream;
  });

  const medals = ["🥇","🥈","🥉"];
  const mmColor = v => v>=1.7?"#4ade80":v>=1.5?"#86efac":v>=1.4?"#FBBF24":"#E8317A";

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {[["week","Week"],["month","Month"],["quarter","Quarter"],["year","Year"],["all","All Time"],["custom","Custom"]].map(([id,l])=>(
            <button key={id} onClick={()=>setPeriod(id)} style={{background:period===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${period===id?"#E8317A":"#333"}`,color:period===id?"#E8317A":"#555",borderRadius:16,padding:"5px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
          ))}
          {period==="custom" && <>
            <input type="date" value={customFrom} onChange={e=>setCustomFrom(e.target.value)}
              style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"4px 10px",fontSize:11,fontFamily:"inherit"}}/>
            <span style={{color:"var(--bz-ink-3)",fontSize:11}}>to</span>
            <input type="date" value={customTo} onChange={e=>setCustomTo(e.target.value)}
              style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"4px 10px",fontSize:11,fontFamily:"inherit"}}/>
          </>}
          <span style={{color:"var(--bz-ink-3)",fontSize:11}}>|</span>
          {[["boba","🃏 BoBA"],["wotf","🐉 WotF"],["all","All"]].map(([id,l])=>(
            <button key={id} onClick={()=>setBrand(id)} style={{background:brand===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${brand===id?"#E8317A":"#333"}`,color:brand===id?"#E8317A":"#555",borderRadius:16,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
          ))}
        </div>
        <div style={{display:"flex",gap:6,marginLeft:"auto",alignItems:"center"}}>
          <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Rank by:</span>
          {[["mm","Avg MM"],["gross","Gross"],["perStream","Per Stream"],["newbuyers","New Buyers"],["comm","Commission"]].map(([id,l])=>(
            <button key={id} onClick={()=>setSortBy(id)} style={{background:sortBy===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${sortBy===id?"#E8317A":"#333"}`,color:sortBy===id?"#E8317A":"#555",borderRadius:16,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
          ))}
        </div>
      </div>

      {rankings.length===0 ? <div style={{textAlign:"center",color:"var(--bz-ink-3)",padding:"40px",fontSize:13}}>No streams in this period</div> :
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {rankings.map((r,i)=>{
          const bc = BC[r.b]?.text||"#E8317A";
          return (
            <div key={r.b} style={{background:"var(--bz-s1)",border:`2px solid ${i===0?bc+"55":"var(--bz-s2)"}`,borderRadius:12,padding:"16px 20px",display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
              <div style={{fontSize:28,width:36,textAlign:"center"}}>{medals[i]||`#${i+1}`}</div>
              <div style={{minWidth:120}}>
                <div style={{fontSize:16,fontWeight:900,color:bc}}>{r.b}</div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>{r.streams} streams</div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:16,flex:1}}>
                {[
                  {l:"Avg MM",      v:r.mm?r.mm.toFixed(2)+"x":"--",      c:r.mm?mmColor(r.mm):"#555", bold:sortBy==="mm"},
                  {l:"Gross",       v:fmt(r.gross),                         c:"#E8317A",                 bold:sortBy==="gross"},
                  {l:"Per Stream",  v:fmt(r.perStream),                     c:"var(--bz-ink)",                 bold:sortBy==="perStream"},
                  {l:"New Buyers",  v:r.newBuyers,                          c:"#166534",                 bold:sortBy==="newbuyers"},
                  {l:"Commission",  v:fmt(r.comm),                          c:"#4ade80",                 bold:sortBy==="comm"},
                ].map(({l,v,c,bold})=>(
                  <div key={l} style={{textAlign:"center",background:bold?"rgba(232,49,122,0.06)":"transparent",borderRadius:8,padding:"6px 4px"}}>
                    <div style={{fontSize:15,fontWeight:900,color:c}}>{v}</div>
                    <div style={{fontSize:9,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginTop:2}}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>}
    </div>
  );
}

// ── MARKET MULTIPLE TREND ─────────────────────────────────────────────────────
function MMTrend({ streams=[], isAdmin, visibleBreakers=[] }) {
  const [view, setView] = useState("weekly");
  const [brand, setBrand] = useState("boba");
  const brandStreams = brand==="all" ? streams : streams.filter(s=>getStreamBrand(s)===brand);
  const now = new Date();
  const mmColor = v => v>=1.7?"#4ade80":v>=1.5?"#86efac":v>=1.4?"#FBBF24":"#E8317A";

  const weeks = Array.from({length:12},(_,i)=>{
    const wEnd=new Date(now); wEnd.setDate(now.getDate()-i*7); wEnd.setHours(23,59,59,999);
    const wStart=new Date(wEnd); wStart.setDate(wEnd.getDate()-6); wStart.setHours(0,0,0,0);
    const label=`${wStart.toLocaleDateString("en-US",{month:"short",day:"numeric"})}`;
    const byBreaker={};
    BREAKERS.forEach(b=>{
      const bs=brandStreams.filter(s=>{ const d=parseLocalDate(s.date); return s.breaker===b&&d>=wStart&&d<=wEnd&&parseFloat(s.marketMultiple)>0; });
      byBreaker[b]=bs.length?bs.reduce((s,x)=>s+(parseFloat(x.marketMultiple)||0),0)/bs.length:null;
    });
    return {label,byBreaker,wStart,wEnd};
  }).reverse();

  const allMMs = weeks.flatMap(w=>Object.values(w.byBreaker).filter(Boolean));
  const maxMM = Math.max(...allMMs, 2.0);
  const minMM = Math.min(...allMMs, 1.0);
  const range = maxMM - minMM || 1;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)"}}>Market Multiple Trend — Last 12 Weeks</div>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          {[["boba","🃏 BoBA"],["wotf","🐉 WotF"],["all","All"]].map(([id,l])=>(
            <button key={id} onClick={()=>setBrand(id)} style={{background:brand===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${brand===id?"#E8317A":"#333"}`,color:brand===id?"#E8317A":"#555",borderRadius:16,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
          ))}
          {BREAKERS.map(b=><div key={b} style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:BC[b]?.text||"#E8317A"}}><div style={{width:10,height:3,background:BC[b]?.text||"#E8317A",borderRadius:2}}/>{b}</div>)}
        </div>
      </div>

      <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"20px",overflowX:"auto"}}>
        <div style={{minWidth:600}}>
          {/* Reference lines */}
          <div style={{position:"relative",height:200,marginBottom:8}}>
            {[1.4,1.5,1.7,2.0].map(ref=>{
              const y=((maxMM-ref)/range)*100;
              if(y<0||y>100) return null;
              return <div key={ref} style={{position:"absolute",top:`${y}%`,left:0,right:0,borderTop:`1px dashed ${ref===1.5?"#FBBF24":"var(--bz-s2)"}`,display:"flex",alignItems:"center"}}>
                <span style={{fontSize:9,color:ref===1.5?"#FBBF24":"#333",marginLeft:4}}>{ref}x</span>
              </div>;
            })}
            {/* Data points and lines */}
            {BREAKERS.map(b=>{
              const bc=BC[b]?.text||"#E8317A";
              const points=weeks.map((w,i)=>({ x:(i/(weeks.length-1))*100, y:w.byBreaker[b]?((maxMM-w.byBreaker[b])/range)*100:null, mm:w.byBreaker[b] }));
              return (
                <svg key={b} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",overflow:"visible"}} preserveAspectRatio="none">
                  {points.map((p,i)=>{
                    if(!p.mm||!points[i+1]?.mm) return null;
                    const np=points[i+1];
                    return <line key={i} x1={`${p.x}%`} y1={`${p.y}%`} x2={`${np.x}%`} y2={`${np.y}%`} stroke={bc} strokeWidth="2" strokeOpacity="0.8"/>;
                  })}
                  {points.map((p,i)=>p.mm?<circle key={i} cx={`${p.x}%`} cy={`${p.y}%`} r="4" fill={bc} stroke="var(--bz-s1)" strokeWidth="1.5"/>:null)}
                </svg>
              );
            })}
          </div>
          {/* X labels */}
          <div style={{display:"flex",justifyContent:"space-between"}}>
            {weeks.map((w,i)=><div key={i} style={{fontSize:9,color:"var(--bz-ink-3)",textAlign:"center",flex:1}}>{i%2===0?w.label:""}</div>)}
          </div>
        </div>
      </div>

      {/* Per-rep summary table */}
      <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"16px 20px"}}>
        <div style={{fontSize:12,fontWeight:800,color:"var(--bz-ink)",marginBottom:12}}>Last 4 Weeks vs Previous 4 Weeks</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {BREAKERS.map(b=>{
            const recent=weeks.slice(-4).flatMap(w=>streams.filter(s=>s.breaker===b&&s.date>=w.wStart?.toISOString().split("T")[0]&&s.date<=w.wEnd?.toISOString().split("T")[0]&&parseFloat(s.marketMultiple)>0));
            const prior=weeks.slice(-8,-4).flatMap(w=>streams.filter(s=>s.breaker===b&&s.date>=w.wStart?.toISOString().split("T")[0]&&s.date<=w.wEnd?.toISOString().split("T")[0]&&parseFloat(s.marketMultiple)>0));
            const recentMM=recent.length?recent.reduce((s,x)=>s+(parseFloat(x.marketMultiple)||0),0)/recent.length:0;
            const priorMM=prior.length?prior.reduce((s,x)=>s+(parseFloat(x.marketMultiple)||0),0)/prior.length:0;
            const trend=recentMM-priorMM;
            const bc=BC[b]?.text||"#E8317A";
            return (
              <div key={b} style={{display:"flex",alignItems:"center",gap:16,padding:"8px 12px",background:"var(--bz-bg)",borderRadius:8}}>
                <div style={{width:100,fontSize:13,fontWeight:700,color:bc}}>{b}</div>
                <div style={{fontSize:18,fontWeight:900,color:recentMM?mmColor(recentMM):"#333"}}>{recentMM?recentMM.toFixed(2)+"x":"--"}</div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>Last 4 weeks</div>
                {priorMM>0&&<div style={{fontSize:13,fontWeight:700,color:trend>=0?"#4ade80":"#E8317A"}}>{trend>=0?"▲":"▼"} {Math.abs(trend).toFixed(2)}x {trend>=0?"↑":"↓"} vs prior</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── TIME ANALYSIS ─────────────────────────────────────────────────────────────
function TimeAnalysis({ streams=[], isAdmin, visibleBreakers=[] }) {
  const [metric, setMetric] = useState("gross");
  const [brand, setBrand] = useState("boba");
  const [period, setPeriod] = useState("all");
  const now = new Date();
  // Period filter
  const inPeriod = s => {
    if(!s.date) return false;
    const d = parseLocalDate(s.date);
    if(period==="month")   return d.getMonth()===now.getMonth() && d.getFullYear()===now.getFullYear();
    if(period==="quarter") { const q=Math.floor(now.getMonth()/3); return Math.floor(d.getMonth()/3)===q && d.getFullYear()===now.getFullYear(); }
    if(period==="year")    return d.getFullYear()===now.getFullYear();
    return true;
  };
  const streams2 = (brand==="all" ? streams : streams.filter(s=>getStreamBrand(s)===brand)).filter(inPeriod);
  const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const SESSION_TYPES = ["day","night","weekend","singles","event"];
  const fmt = v => "$"+Number(v||0).toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0});

  // By day of week
  const byDay = DAYS.map((day,i)=>{
    const ds=streams2.filter(s=>s.date&&parseLocalDate(s.date).getDay()===i);
    if(!ds.length) return {day,streams:0,gross:0,mm:0,newBuyers:0};
    const gross=ds.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);
    const mmList=ds.filter(x=>parseFloat(x.marketMultiple)>0);
    const mm=mmList.length?mmList.reduce((s,x)=>s+(parseFloat(x.marketMultiple)||0),0)/mmList.length:0;
    const newBuyers=ds.reduce((s,x)=>s+(parseInt(x.newBuyers)||0),0);
    return {day,streams:ds.length,gross:gross/ds.length,mm,newBuyers:newBuyers/ds.length};
  });

  // By session type
  const bySession = ["day","night","weekend","event",""].map(t=>{
    const label=t==="day"?"☀️ Day":t==="night"?"🌙 Night":t==="weekend"?"📅 Weekend":t==="singles"?"🃏 Singles":t==="event"?"🎉 Event":"📌 Untagged";
    const ds=streams2.filter(s=>s.sessionType===t||(t===""&&!s.sessionType));
    if(!ds.length) return null;
    const gross=ds.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);
    const mmList=ds.filter(x=>parseFloat(x.marketMultiple)>0);
    const mm=mmList.length?mmList.reduce((s,x)=>s+(parseFloat(x.marketMultiple)||0),0)/mmList.length:0;
    const newBuyers=ds.reduce((s,x)=>s+(parseInt(x.newBuyers)||0),0);
    return {label,streams:ds.length,gross:gross/ds.length,mm,newBuyers:newBuyers/ds.length};
  }).filter(Boolean);

  const getVal = (row,m) => m==="gross"?row.gross:m==="mm"?row.mm:row.newBuyers;

  // By breaker (admin sees all; a rep sees just their own)
  const byBreaker = (visibleBreakers.length?visibleBreakers:[...new Set(streams2.map(s=>s.breaker).filter(Boolean))]).map(bk=>{
    const ds=streams2.filter(s=>s.breaker===bk);
    if(!ds.length) return {breaker:bk,streams:0,gross:0,mm:0,newBuyers:0};
    const gross=ds.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);
    const mmList=ds.filter(x=>parseFloat(x.marketMultiple)>0);
    const mm=mmList.length?mmList.reduce((s,x)=>s+(parseFloat(x.marketMultiple)||0),0)/mmList.length:0;
    const newBuyers=ds.reduce((s,x)=>s+(parseInt(x.newBuyers)||0),0);
    return {breaker:bk,streams:ds.length,gross:gross/ds.length,mm,newBuyers:newBuyers/ds.length,totalGross:gross};
  }).filter(b=>b.streams>0).sort((a,b)=>getVal(b,metric)-getVal(a,metric));

  const maxDay = Math.max(...byDay.map(d=>getVal(d,metric)),1);
  const mmColor = v => v>=1.7?"#4ade80":v>=1.5?"#86efac":v>=1.4?"#FBBF24":"#E8317A";

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        <span style={{fontSize:12,color:"var(--bz-ink-3)"}}>Metric:</span>
        {[["gross","Avg Gross"],["mm","Avg MM"],["newBuyers","Avg New Buyers"]].map(([id,l])=>(
          <button key={id} onClick={()=>setMetric(id)} style={{background:metric===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${metric===id?"#E8317A":"#333"}`,color:metric===id?"#E8317A":"#555",borderRadius:16,padding:"5px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
        ))}
        <span style={{color:"var(--bz-ink-3)",fontSize:11,marginLeft:4}}>|</span>
        {[["boba","🃏 BoBA"],["wotf","🐉 WotF"],["all","All"]].map(([id,l])=>(
          <button key={id} onClick={()=>setBrand(id)} style={{background:brand===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${brand===id?"#E8317A":"#333"}`,color:brand===id?"#E8317A":"#555",borderRadius:16,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
        ))}
      </div>

      {/* Period filter */}
      <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
        <span style={{fontSize:12,color:"var(--bz-ink-3)"}}>Period:</span>
        {[["month","This Month"],["quarter","This Quarter"],["year","This Year"],["all","All Time"]].map(([id,l])=>(
          <button key={id} onClick={()=>setPeriod(id)} style={{background:period===id?"rgba(74,222,128,0.15)":"transparent",border:`1px solid ${period===id?"#4ade80":"#333"}`,color:period===id?"#4ade80":"#888",borderRadius:16,padding:"5px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
        ))}
        <span style={{fontSize:11,color:"var(--bz-ink-3)",marginLeft:4}}>{streams2.length} streams</span>
      </div>

      {/* By day of week */}
      <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px"}}>
        <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:14}}>Performance by Day of Week</div>
        <div style={{display:"flex",gap:8,alignItems:"flex-end",height:140}}>
          {byDay.map((d,i)=>{
            const val=getVal(d,metric);
            const barH=maxDay>0?(val/maxDay)*100:0;
            const col=metric==="mm"&&val?mmColor(val):"#E8317A";
            return (
              <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <div style={{fontSize:10,fontWeight:700,color:col}}>{metric==="gross"?fmt(val):metric==="mm"?val?val.toFixed(2)+"x":"--":val?val.toFixed(1):""}</div>
                <div style={{width:"100%",height:100,display:"flex",alignItems:"flex-end"}}>
                  <div style={{width:"100%",height:`${barH}%`,background:col,borderRadius:"3px 3px 0 0",minHeight:d.streams?4:0,opacity:0.8}}/>
                </div>
                <div style={{fontSize:11,fontWeight:700,color:"var(--bz-ink)"}}>{d.day}</div>
                <div style={{fontSize:9,color:"var(--bz-ink-3)"}}>{d.streams} streams</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* By session type */}
      <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px"}}>
        <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:14}}>Performance by Session Type</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {bySession.sort((a,b)=>getVal(b,metric)-getVal(a,metric)).map(s=>{
            const val=getVal(s,metric);
            const maxS=Math.max(...bySession.map(x=>getVal(x,metric)),1);
            const pct=maxS>0?val/maxS*100:0;
            return (
              <div key={s.label} style={{display:"grid",gridTemplateColumns:"120px 1fr 100px 60px",gap:12,alignItems:"center"}}>
                <div style={{fontSize:12,color:"var(--bz-ink-2)"}}>{s.label}</div>
                <div style={{height:8,background:"var(--bz-s2)",borderRadius:4,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${pct}%`,background:"#E8317A",borderRadius:4}}/>
                </div>
                <div style={{fontSize:13,fontWeight:700,color:metric==="mm"?mmColor(val):"#E8317A",textAlign:"right"}}>{metric==="gross"?fmt(val):metric==="mm"?val?val.toFixed(2)+"x":"--":val?val.toFixed(1):""}</div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",textAlign:"right"}}>{s.streams} streams</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* By breaker */}
      {byBreaker.length>0 && (
        <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:14}}>Performance by Breaker</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {byBreaker.map(b=>{
              const val=getVal(b,metric);
              const maxB=Math.max(...byBreaker.map(x=>getVal(x,metric)),1);
              const pct=maxB>0?val/maxB*100:0;
              const col=metric==="mm"&&val?mmColor(val):"#7B9CFF";
              return (
                <div key={b.breaker} style={{display:"grid",gridTemplateColumns:"120px 1fr 100px 70px",gap:12,alignItems:"center"}}>
                  <div style={{fontSize:12,fontWeight:700,color:"var(--bz-ink)"}}>{b.breaker}</div>
                  <div style={{height:8,background:"var(--bz-s2)",borderRadius:4,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:col,borderRadius:4}}/>
                  </div>
                  <div style={{fontSize:13,fontWeight:700,color:col,textAlign:"right"}}>{metric==="gross"?fmt(val):metric==="mm"?val?val.toFixed(2)+"x":"--":val?val.toFixed(1):"--"}</div>
                  <div style={{fontSize:11,color:"var(--bz-ink-3)",textAlign:"right"}}>{b.streams} streams</div>
                </div>
              );
            })}
          </div>
          {metric==="gross" && <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:10}}>Bars show avg gross per stream. Total gross varies with volume.</div>}
        </div>
      )}
    </div>
  );
}
// ── BUYER RETENTION ──────────────────────────────────────────────────────────
// ── CAMPAIGN TRACKER ─────────────────────────────────────────────────────────
const CAMPAIGN_CODE = "FIRSTTIME25";
const COUPON_VALUE  = 25;

function CampaignTracker({ buyers=[], streams=[] }) {
  const fmt2 = v => "$"+Number(v||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
  const [search,     setSearch]     = useState("");
  const [saving,     setSaving]     = useState(false);
  const [importMsg,  setImportMsg]  = useState(null);
  const [importing,  setImporting]  = useState(false);
  const localToday = () => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };
  const [importDate, setImportDate] = useState(localToday());

  const tagged   = buyers.filter(b => (b.couponsUsed||[]).includes(CAMPAIGN_CODE));
  const untagged = buyers.filter(b => !(b.couponsUsed||[]).includes(CAMPAIGN_CODE));

  // Build per-day data — use couponTagDate if set, otherwise try spendHistory to find earliest stream
  const byDay = {};
  tagged.forEach(b => {
    let date = b.couponTagDate;
    if (!date && b.spendHistory?.length) {
      // Use the earliest stream date in their spend history as a proxy
      date = b.spendHistory.reduce((min,h)=>(!min||h.date<min)?h.date:min, null);
    }
    const key = date || "unknown";
    if (!byDay[key]) byDay[key] = [];
    byDay[key].push(b);
  });
  const sortedDays = Object.keys(byDay).filter(d=>d!=="unknown").sort();
  if (byDay.unknown?.length) sortedDays.push("unknown");

  const totalTagged   = tagged.length;
  const totalSpend    = tagged.reduce((s,b)=>s+(b.totalSpend||0),0);
  const totalDiscount = totalTagged * COUPON_VALUE;
  const totalReturned = tagged.filter(b=>(b.orderCount||0)>1).length;
  const retPct        = totalTagged ? (totalReturned/totalTagged*100) : 0;
  const roi           = totalDiscount > 0 ? totalSpend/totalDiscount : 0;

  const searchResults = search.length > 1
    ? untagged.filter(b=>(b.username||"").toLowerCase().includes(search.toLowerCase())).slice(0,10)
    : [];

  async function tagBuyer(buyer) {
    setSaving(true);
    await setDoc(doc(db,"buyers",buyer.id), {
      couponsUsed: [...(buyer.couponsUsed||[]), CAMPAIGN_CODE],
      couponTagDate: importDate,
    }, { merge:true });
    setSearch(""); setSaving(false);
  }

  async function untagBuyer(buyer) {
    await setDoc(doc(db,"buyers",buyer.id), {
      couponsUsed: (buyer.couponsUsed||[]).filter(c=>c!==CAMPAIGN_CODE),
    }, { merge:true });
  }

  function importCSV(file) {
    if (!file) return;
    setImporting(true); setImportMsg(null);
    const streamDate = importDate;
    const reader = new FileReader();
    reader.onload = async e => {
      try {
        const lines = e.target.result.split(/\r?\n/).filter(l=>l.trim());
        const headers = lines[0].split(",").map(h=>h.replace(/^"|"$/g,"").toLowerCase().trim());
        const uIdx = headers.findIndex(h=>h.includes("buyer_username")||h==="username");
        const cIdx = headers.findIndex(h=>h.includes("coupon_code")||h.includes("coupon code"));
        const dIdx = headers.findIndex(h=>h.includes("coupon_price")||h.includes("discount"));
        const xIdx = headers.findIndex(h=>h.includes("cancelled")||h.includes("failed"));
        if (uIdx===-1) { setImportMsg(`❌ No username column. Found: ${headers.join(", ")}`); setImporting(false); return; }
        if (cIdx===-1) { setImportMsg(`❌ No coupon_code column. Found: ${headers.join(", ")}`); setImporting(false); return; }
        const couponUsers = {};
        for (let i=1; i<lines.length; i++) {
          const cols=[]; let cur="",inQ=false;
          for (const ch of lines[i]) { if(ch==='"')inQ=!inQ; else if(ch===','&&!inQ){cols.push(cur.trim());cur="";}else cur+=ch; }
          cols.push(cur.trim());
          if (xIdx>=0&&["true","yes","failed"].includes((cols[xIdx]||"").toLowerCase())) continue;
          const u=(cols[uIdx]||"").replace(/^"|"$/g,"").trim();
          const c=(cols[cIdx]||"").replace(/^"|"$/g,"").trim().toUpperCase();
          if (!u||c!==CAMPAIGN_CODE) continue;
          const disc=dIdx>=0?Math.abs(parseFloat((cols[dIdx]||"").replace(/[$,"]/g,""))||0):0;
          if (!couponUsers[u]) couponUsers[u]=0;
          couponUsers[u]+=disc;
        }
        const found=Object.entries(couponUsers);
        if (!found.length) { setImportMsg(`⚠️ No ${CAMPAIGN_CODE} rows found in this file.`); setImporting(false); return; }
        let tagged=0, missing=[];
        await Promise.all(found.map(([username,disc])=>{
          const buyer=buyers.find(b=>(b.username||"").toLowerCase()===username.toLowerCase());
          if (!buyer) { missing.push(username); return Promise.resolve(); }
          tagged++;
          return setDoc(doc(db,"buyers",buyer.id),{
            couponsUsed:[...new Set([...(buyer.couponsUsed||[]),CAMPAIGN_CODE])],
            couponTagDate: buyer.couponTagDate || streamDate,
          },{ merge:true });
        }));
        let msg=`✅ Tagged ${tagged} buyer${tagged!==1?"s":""} on ${streamDate}`;
        if (missing.length) msg+=` · ${missing.length} not in CRM: ${missing.slice(0,3).join(", ")}${missing.length>3?` +${missing.length-3}`:""}`;
        setImportMsg(msg);
      } catch(err) { setImportMsg("❌ "+err.message); }
      setImporting(false);
    };
    reader.readAsText(file);
  }

  function exportCSV() {
    const couponValue = COUPON_VALUE;
    const rows = [
      ["Username","Coupon Discount","Lifetime Spend","ROI","Returned","Orders"],
      ...tagged.sort((a,b)=>(b.totalSpend||0)-(a.totalSpend||0)).map(b => {
        const spend = b.totalSpend||0;
        const disc  = couponValue;
        const roi   = (spend/disc).toFixed(2);
        const returned = (b.orderCount||0)>1 ? "Yes" : "No";
        return [b.username, `$${disc.toFixed(2)}`, `$${spend.toFixed(2)}`, `${roi}x`, returned, b.orderCount||0];
      })
    ];
    const csv = rows.map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8,"+encodeURIComponent(csv);
    a.download = `${CAMPAIGN_CODE}-campaign-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  }

  const StatBox = ({label,value,color,sub}) => (
    <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"14px 16px", textAlign:"center" }}>
      <div style={{ fontSize:22, fontWeight:900, color, lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>{sub}</div>}
      <div style={{ fontSize:9, color:"#8a8a8a", textTransform:"uppercase", letterSpacing:"1px", marginTop:6 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14, padding:20 }}>

      {/* ── OVERALL SUMMARY ── */}
      <div style={{ ...S.card }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:0 }}>
          <SectionLabel t={`🎯 ${CAMPAIGN_CODE} — Campaign Overview`}/>
          {tagged.length > 0 && (
            <button onClick={exportCSV} style={{ background:"rgba(74,222,128,0.1)", border:"1px solid rgba(74,222,128,0.25)", color:"#4ade80", borderRadius:7, padding:"6px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
              ⬇ Export CSV
            </button>
          )}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))", gap:10, marginBottom:16 }}>
          <StatBox label="Total Tagged"   value={totalTagged}              color="var(--bz-ink)"/>
          <StatBox label="Returned"       value={`${retPct.toFixed(0)}%`}  color={retPct>=30?"#4ade80":"#FBBF24"} sub={`${totalReturned} of ${totalTagged}`}/>
          <StatBox label="Total Spend"    value={fmt2(totalSpend)}         color="#7B9CFF"/>
          <StatBox label="Discounts Out"  value={fmt2(totalDiscount)}      color="#FBBF24" sub={`${totalTagged} × $${COUPON_VALUE}`}/>
          <StatBox label="Overall ROI"    value={roi>0?`${roi.toFixed(1)}x`:"—"} color={roi>=5?"#4ade80":roi>=2?"#FBBF24":"#E8317A"}/>
        </div>

        {/* Import + date */}
        <div style={{ display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap", padding:"10px 14px", background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:8 }}>
          <div>
            <div style={{ fontSize:9, color:"var(--bz-ink-3)", marginBottom:4, textTransform:"uppercase", letterSpacing:1 }}>Stream Date</div>
            <input type="date" value={importDate} onChange={e=>setImportDate(e.target.value)} style={{ ...S.inp, width:"auto" }}/>
          </div>
          <label style={{ background:"rgba(123,156,255,0.1)", border:"1px solid rgba(123,156,255,0.3)", color:"#7B9CFF", borderRadius:7, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:"pointer" }}>
            {importing?"⏳ Importing...":"📂 Import Whatnot CSV"}
            <input type="file" accept=".csv" style={{ display:"none" }} disabled={importing} onChange={e=>{ if(e.target.files[0]) importCSV(e.target.files[0]); e.target.value=""; }}/>
          </label>
          <div style={{ fontSize:11, color:"#8a8a8a" }}>Only updates coupon data — won't duplicate spend</div>
        </div>
        {importMsg && <div style={{ marginTop:8, fontSize:12, color:importMsg.startsWith("✅")?"#4ade80":importMsg.startsWith("⚠️")?"#FBBF24":"#ef4444", padding:"6px 10px", background:"var(--bz-bg)", borderRadius:6 }}>{importMsg}</div>}
      </div>

      <div style={{ ...S.card }}>
        <SectionLabel t="✋ Manually Tag a Buyer"/>
        <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginBottom:10 }}>For buyers who forgot to use the coupon. Uses the Stream Date selected above.</div>
        <div style={{ position:"relative", maxWidth:380 }}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by username..."
            style={{ ...S.inp, width:"100%" }}/>
          {searchResults.length > 0 && (
            <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0, background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:10, zIndex:99, maxHeight:240, overflowY:"auto", boxShadow:"0 8px 24px rgba(0,0,0,0.7)" }}>
              {searchResults.map(b=>(
                <div key={b.id} onClick={()=>!saving&&tagBuyer(b)}
                  style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", cursor:"pointer", borderBottom:"1px solid #111" }}
                  className="inv-row">
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)" }}>{b.username}</div>
                    <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{b.orderCount||0} orders · {fmt2(b.totalSpend||0)}</div>
                  </div>
                  <div style={{ fontSize:12, fontWeight:700, color:"#E8317A", background:"rgba(232,49,122,0.1)", borderRadius:6, padding:"4px 12px" }}>{saving?"...":"+ Tag"}</div>
                </div>
              ))}
            </div>
          )}
          {search.length>1&&!searchResults.length&&<div style={{ marginTop:6, fontSize:12, color:"var(--bz-ink-3)" }}>No untagged buyers matching "{search}"</div>}
        </div>
      </div>

      {tagged.length===0&&(
        <div style={{ ...S.card, textAlign:"center", padding:"40px 20px" }}>
          <div style={{ fontSize:32, opacity:0.3, marginBottom:10 }}>🎯</div>
          <div style={{ fontSize:14, fontWeight:700, color:"var(--bz-ink-3)" }}>No buyers tagged yet</div>
          <div style={{ fontSize:12, color:"#8a8a8a", marginTop:4 }}>Import a Whatnot CSV above or manually search and tag buyers</div>
        </div>
      )}
    </div>
  );
}
function BuyerRetention({ buyers=[], streams=[] }) {
  const now = new Date();
  const fmt = v => "$"+Number(v||0).toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0});
  const [view, setView] = useState("overview"); // overview | cohort | firsttime

  // Per buyer: which months were they active?
  function buyerActiveMonths(b) {
    const active = new Set();
    (b.spendHistory||[]).forEach(h => { if(h.date) active.add(h.date.slice(0,7)); });
    if(!active.size && b.firstSeen) active.add(b.firstSeen.slice(0,7));
    return active;
  }

  // First-time buyer return analysis
  const firstTimers    = buyers.filter(b => b.firstSeen);
  const returnedOnce   = firstTimers.filter(b => (b.orderCount||0) >= 2 || buyerActiveMonths(b).size >= 2);
  const returned2plus  = firstTimers.filter(b => (b.orderCount||0) >= 5 || buyerActiveMonths(b).size >= 3);
  const returned5plus  = firstTimers.filter(b => (b.orderCount||0) >= 10 || buyerActiveMonths(b).size >= 4);
  const neverReturned  = firstTimers.filter(b => (b.orderCount||0) <= 1 && buyerActiveMonths(b).size <= 1);
  const returnRate     = firstTimers.length > 0 ? returnedOnce.length/firstTimers.length*100 : 0;

  // Revenue from returners vs one-timers
  const returnerRevenue   = returnedOnce.reduce((s,b)=>s+(b.totalSpend||0),0);
  const oneTimerRevenue   = neverReturned.reduce((s,b)=>s+(b.totalSpend||0),0);
  const totalRevenue      = buyers.reduce((s,b)=>s+(b.totalSpend||0),0);

  // Build month list (last 6 months)
  const months = Array.from({length:6},(_,i)=>{
    const d = new Date(now.getFullYear(), now.getMonth()-i, 1);
    return { key:`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`, label:d.toLocaleDateString("en-US",{month:"short",year:"2-digit"}) };
  }).reverse();

  // Month stats
  const monthStats = months.map((m,i) => {
    const activeBuyers = buyers.filter(b => buyerActiveMonths(b).has(m.key));
    const newBuyers    = activeBuyers.filter(b => (b.firstSeen||"").startsWith(m.key));
    const returning    = activeBuyers.filter(b => !(b.firstSeen||"").startsWith(m.key));
    let retentionRate = null;
    if (i > 0) {
      const prevKey    = months[i-1].key;
      const prevActive = buyers.filter(b => buyerActiveMonths(b).has(prevKey));
      const retained   = prevActive.filter(b => buyerActiveMonths(b).has(m.key));
      retentionRate    = prevActive.length > 0 ? retained.length/prevActive.length*100 : null;
    }
    return { ...m, total:activeBuyers.length, new:newBuyers.length, returning:returning.length, retentionRate };
  });

  // Cohort table
  const cohorts = months.slice(0,5).map(m => {
    const cohortBuyers = buyers.filter(b=>(b.firstSeen||"").startsWith(m.key));
    const followMonths = months.filter(fm=>fm.key>m.key);
    return {
      ...m,
      size: cohortBuyers.length,
      follow: followMonths.map(fm=>({
        key: fm.key, label: fm.label,
        retained: cohortBuyers.filter(b=>buyerActiveMonths(b).has(fm.key)).length,
        pct: cohortBuyers.length > 0 ? cohortBuyers.filter(b=>buyerActiveMonths(b).has(fm.key)).length/cohortBuyers.length*100 : 0,
      }))
    };
  });

  const retColor  = pct => pct >= 40 ? "#4ade80" : pct >= 20 ? "#FBBF24" : "#E8317A";
  const latestRetention = monthStats.filter(m=>m.retentionRate!==null).slice(-1)[0];

  // Spend buckets for one-timers
  const spendBuckets = [
    { label:"$0–$25",   buyers: neverReturned.filter(b=>(b.totalSpend||0)<25) },
    { label:"$25–$100", buyers: neverReturned.filter(b=>(b.totalSpend||0)>=25&&(b.totalSpend||0)<100) },
    { label:"$100–$250",buyers: neverReturned.filter(b=>(b.totalSpend||0)>=100&&(b.totalSpend||0)<250) },
    { label:"$250+",    buyers: neverReturned.filter(b=>(b.totalSpend||0)>=250) },
  ];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>

      {/* View tabs */}
      <div style={{display:"flex",gap:6}}>
        {[["overview","📊 Overview"],["firsttime","🔄 First-Time Return Rate"],["tiers","💎 Buyer Tiers"],["cohort","📅 Cohort Table"]].map(([v,l])=>(
          <button key={v} onClick={()=>setView(v)}
            style={{background:view===v?"rgba(232,49,122,0.15)":"var(--bz-s1)",border:`1.5px solid ${view===v?"#E8317A":"var(--bz-s2)"}`,color:view===v?"#E8317A":"#888",borderRadius:20,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            {l}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {view==="overview" && <>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
          <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"16px 18px",textAlign:"center"}}>
            <div style={{fontSize:28,fontWeight:900,color:latestRetention?retColor(latestRetention.retentionRate):"#555"}}>
              {latestRetention?.retentionRate!=null?latestRetention.retentionRate.toFixed(1)+"%":"--"}
            </div>
            <div style={{fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginTop:4}}>Monthly Retention Rate</div>
            <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:2}}>buyers who came back vs prior month</div>
          </div>
          <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"16px 18px",textAlign:"center"}}>
            <div style={{fontSize:28,fontWeight:900,color:"#4ade80"}}>{monthStats[monthStats.length-1]?.returning||0}</div>
            <div style={{fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginTop:4}}>Returning This Month</div>
          </div>
          <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"16px 18px",textAlign:"center"}}>
            <div style={{fontSize:28,fontWeight:900,color:"#7B9CFF"}}>{monthStats[monthStats.length-1]?.new||0}</div>
            <div style={{fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginTop:4}}>New Buyers This Month</div>
          </div>
        </div>

        {/* Month chart */}
        <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:14}}>Monthly Active Buyers & Retention</div>
          <div style={{display:"flex",gap:4,alignItems:"flex-end",height:140,marginBottom:12}}>
            {monthStats.map((m,i)=>{
              const maxT=Math.max(...monthStats.map(x=>x.total),1);
              const newH=(m.new/maxT)*120; const retH=(m.returning/maxT)*120;
              return (
                <div key={m.key} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                  {m.retentionRate!=null&&<div style={{fontSize:9,fontWeight:700,color:retColor(m.retentionRate)}}>{m.retentionRate.toFixed(0)}%</div>}
                  <div style={{width:"100%",display:"flex",alignItems:"flex-end",height:120,gap:1}}>
                    <div style={{flex:1,height:`${retH}px`,background:"#4ade80",borderRadius:"2px 2px 0 0",opacity:0.8}}/>
                    <div style={{flex:1,height:`${newH}px`,background:"#7B9CFF",borderRadius:"2px 2px 0 0",opacity:0.8}}/>
                  </div>
                  <div style={{fontSize:9,color:"var(--bz-ink-3)",textAlign:"center"}}>{m.label}</div>
                  <div style={{fontSize:9,color:"var(--bz-ink-3)"}}>{m.total}</div>
                </div>
              );
            })}
          </div>
          <div style={{display:"flex",gap:16}}>
            <div style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#4ade80"}}><div style={{width:10,height:10,background:"#4ade80",borderRadius:2}}/> Returning</div>
            <div style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#7B9CFF"}}><div style={{width:10,height:10,background:"#7B9CFF",borderRadius:2}}/> New</div>
          </div>
        </div>
      </>}

      {/* FIRST-TIME RETURN RATE */}
      {view==="firsttime" && <>
        {/* Hero stat */}
        <div style={{background:"linear-gradient(135deg,#0d0005,#0a000d)",border:"1px solid rgba(232,49,122,0.3)",borderRadius:16,padding:"24px 28px",textAlign:"center"}}>
          <div style={{fontSize:56,fontWeight:900,color:retColor(returnRate)}}>{returnRate.toFixed(1)}%</div>
          <div style={{fontSize:14,color:"var(--bz-ink-2)",marginTop:6}}>of first-time buyers have come back for at least a second purchase</div>
          <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:4}}>{returnedOnce.length} returned out of {firstTimers.length} total buyers</div>
        </div>

        {/* Breakdown tiles */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12}}>
          <div style={{background:"var(--bz-s1)",border:"1px solid rgba(74,222,128,0.2)",borderRadius:12,padding:"16px 18px"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#4ade80",textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>✅ Returned Buyers</div>
            {[
              {l:"Bought again (2+ orders)",         v:returnedOnce.length,  pct:returnRate},
              {l:"Loyal (5+ orders or 3+ months)",   v:returned2plus.length, pct:firstTimers.length>0?returned2plus.length/firstTimers.length*100:0},
              {l:"Super fans (10+ orders / 4+ mo)",  v:returned5plus.length, pct:firstTimers.length>0?returned5plus.length/firstTimers.length*100:0},
            ].map(({l,v,pct})=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid var(--bz-line)"}}>
                <div style={{fontSize:12,color:"var(--bz-ink-2)"}}>{l}</div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{fontSize:12,fontWeight:700,color:"var(--bz-ink)"}}>{v}</div>
                  <div style={{fontSize:11,fontWeight:700,color:"#4ade80",width:42,textAlign:"right"}}>{pct.toFixed(1)}%</div>
                </div>
              </div>
            ))}
            <div style={{marginTop:10,fontSize:12,color:"var(--bz-ink-3)"}}>Revenue from returners</div>
            <div style={{fontSize:20,fontWeight:900,color:"#4ade80",marginTop:2}}>{fmt(returnerRevenue)}</div>
            <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>{totalRevenue>0?(returnerRevenue/totalRevenue*100).toFixed(1):0}% of total revenue</div>
          </div>

          <div style={{background:"var(--bz-s1)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:12,padding:"16px 18px"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#ef4444",textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>👻 One-Time Buyers</div>
            <div style={{fontSize:32,fontWeight:900,color:"#ef4444",marginBottom:4}}>{neverReturned.length}</div>
            <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:12}}>{firstTimers.length>0?((100-returnRate).toFixed(1)):0}% of all buyers — bought once, never returned</div>
            <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:6}}>Revenue left on the table</div>
            <div style={{fontSize:18,fontWeight:900,color:"#ef4444",marginBottom:12}}>{fmt(oneTimerRevenue)}</div>
            {/* Spend buckets */}
            <div style={{fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>By Spend Amount</div>
            {spendBuckets.map(({label,buyers:bs})=>(
              <div key={label} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"4px 0",borderBottom:"1px solid var(--bz-line)"}}>
                <span style={{color:"var(--bz-ink-2)"}}>{label}</span>
                <span style={{color:"var(--bz-ink)",fontWeight:700}}>{bs.length} buyers</span>
              </div>
            ))}
            <div style={{marginTop:10,fontSize:11,color:"var(--bz-ink-3)",fontStyle:"italic"}}>
              💡 {spendBuckets[3]?.buyers?.length||0} buyers spent $250+ and never came back — highest win-back priority
            </div>
          </div>
        </div>

        {/* Win-back opportunity */}
        <div style={{background:"var(--bz-s1)",border:"1px solid rgba(251,191,36,0.2)",borderRadius:12,padding:"16px 18px"}}>
          <div style={{fontSize:12,fontWeight:800,color:"#FBBF24",marginBottom:6}}>💰 Win-Back Opportunity</div>
          <div style={{fontSize:13,color:"var(--bz-ink-2)",lineHeight:1.6}}>
            If you could convert just <strong style={{color:"#FBBF24"}}>10%</strong> of your {neverReturned.length} one-time buyers into returning customers at their average spend of <strong style={{color:"#FBBF24"}}>{fmt(neverReturned.length>0?oneTimerRevenue/neverReturned.length:0)}</strong>, that's roughly <strong style={{color:"#4ade80"}}>{fmt(neverReturned.length>0?(oneTimerRevenue/neverReturned.length)*neverReturned.length*0.1:0)}</strong> in additional annual revenue.
          </div>
        </div>
      </>}

      {/* BUYER TIERS */}
      {view==="tiers" && (() => {
        const sorted = [...buyers].filter(b=>b.totalSpend>0).sort((a,b)=>(b.totalSpend||0)-(a.totalSpend||0));
        const total  = sorted.length;
        if (total === 0) return <div style={{color:"var(--bz-ink-3)",padding:32,textAlign:"center"}}>No buyer data yet</div>;

        const totalRev = sorted.reduce((s,b)=>s+(b.totalSpend||0),0);

        // Tier definitions
        const tiers = [
          { label:"🐳 Whales",      desc:"Top 5%",   color:"#A78BFA", bg:"rgba(167,139,250,0.08)", border:"rgba(167,139,250,0.2)", buyers: sorted.slice(0, Math.ceil(total*0.05)) },
          { label:"💎 VIPs",        desc:"Top 6–20%", color:"#FBBF24", bg:"rgba(251,191,36,0.08)",  border:"rgba(251,191,36,0.2)",  buyers: sorted.slice(Math.ceil(total*0.05), Math.ceil(total*0.20)) },
          { label:"⭐ Regulars",    desc:"Top 21–50%",color:"#4ade80", bg:"rgba(74,222,128,0.08)",  border:"rgba(74,222,128,0.2)",  buyers: sorted.slice(Math.ceil(total*0.20), Math.ceil(total*0.50)) },
          { label:"👤 Casuals",     desc:"Bottom 50%",color:"var(--bz-ink-2)",   bg:"rgba(100,100,100,0.06)", border:"rgba(100,100,100,0.15)", buyers: sorted.slice(Math.ceil(total*0.50)) },
        ];

        const topSpender  = sorted[0];
        const avgSpend    = totalRev / total;
        const medianSpend = sorted[Math.floor(total/2)]?.totalSpend||0;

        const totalStreamGross = streams.reduce((s,b)=>s+(parseFloat(b.grossRevenue)||0),0);
        const trackingGap = totalStreamGross - totalRev;

        return (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {/* Summary KPIs */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
              {[
                { l:"Total Buyers",   v:total.toLocaleString(),                    c:"var(--bz-ink)" },
                { l:"Tracked Revenue",v:"$"+Math.round(totalRev).toLocaleString(), c:"#4ade80" },
                { l:"Avg Spend",      v:"$"+Math.round(avgSpend).toLocaleString(), c:"#7B9CFF" },
                { l:"Median Spend",   v:"$"+Math.round(medianSpend).toLocaleString(), c:"#FBBF24" },
              ].map(({l,v,c})=>(
                <div key={l} style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"14px 16px",textAlign:"center"}}>
                  <div style={{fontSize:24,fontWeight:900,color:c}}>{v}</div>
                  <div style={{fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginTop:4}}>{l}</div>
                </div>
              ))}
            </div>

            {/* Gap warning */}
            {trackingGap > 0 && (
              <div style={{background:"rgba(239,68,68,0.05)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:10,padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                <div style={{fontSize:12,color:"var(--bz-ink-2)"}}>
                  ⚠️ <strong style={{color:"#ef4444"}}>${Math.round(trackingGap).toLocaleString()}</strong> in gross revenue isn't reflected here — streams with no buyer CSV imported.
                  <span style={{color:"var(--bz-ink-3)",marginLeft:6}}>Actual gross: <strong style={{color:"var(--bz-ink)"}}>${Math.round(totalStreamGross).toLocaleString()}</strong></span>
                </div>
                <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Go to Streams tab to see which ones are missing</span>
              </div>
            )}

            {/* Top spender callout */}
            {topSpender && (
              <div style={{background:"linear-gradient(135deg,#0d0005,#0a000d)",border:"1px solid rgba(167,139,250,0.3)",borderRadius:12,padding:"14px 20px",display:"flex",alignItems:"center",gap:20}}>
                <div style={{fontSize:32}}>👑</div>
                <div>
                  <div style={{fontSize:11,color:"#A78BFA",fontWeight:700,textTransform:"uppercase",letterSpacing:1}}>Top Buyer</div>
                  <div style={{fontSize:18,fontWeight:900,color:"var(--bz-ink)"}}>@{topSpender.username}</div>
                  {topSpender.fullName&&<div style={{fontSize:12,color:"var(--bz-ink-3)"}}>{topSpender.fullName}</div>}
                </div>
                <div style={{marginLeft:"auto",textAlign:"right"}}>
                  <div style={{fontSize:28,fontWeight:900,color:"#A78BFA"}}>${Math.round(topSpender.totalSpend||0).toLocaleString()}</div>
                  <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>lifetime spend · {topSpender.orderCount||0} orders</div>
                </div>
              </div>
            )}

            {/* Tier breakdown */}
            {tiers.map(tier=>{
              const tierRev  = tier.buyers.reduce((s,b)=>s+(b.totalSpend||0),0);
              const revPct   = totalRev>0?tierRev/totalRev*100:0;
              const buyerPct = total>0?tier.buyers.length/total*100:0;
              const tierAvg  = tier.buyers.length>0?tierRev/tier.buyers.length:0;
              const top3     = tier.buyers.slice(0,3);

              return (
                <div key={tier.label} style={{background:tier.bg,border:`1px solid ${tier.border}`,borderRadius:12,padding:"16px 20px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:12,flexWrap:"wrap"}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:16,fontWeight:900,color:tier.color}}>{tier.label}</div>
                      <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:1}}>{tier.desc} · {tier.buyers.length} buyers</div>
                    </div>
                    <div style={{textAlign:"center"}}>
                      <div style={{fontSize:22,fontWeight:900,color:tier.color}}>${Math.round(tierRev).toLocaleString()}</div>
                      <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>total revenue</div>
                    </div>
                    <div style={{textAlign:"center",minWidth:60}}>
                      <div style={{fontSize:22,fontWeight:900,color:"var(--bz-ink)"}}>{revPct.toFixed(1)}%</div>
                      <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>of revenue</div>
                    </div>
                    <div style={{textAlign:"center",minWidth:60}}>
                      <div style={{fontSize:22,fontWeight:900,color:"var(--bz-ink-2)"}}>{buyerPct.toFixed(1)}%</div>
                      <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>of buyers</div>
                    </div>
                    <div style={{textAlign:"center",minWidth:60}}>
                      <div style={{fontSize:18,fontWeight:700,color:"var(--bz-ink-2)"}}>${Math.round(tierAvg).toLocaleString()}</div>
                      <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>avg spend</div>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div style={{height:4,background:"rgba(255,255,255,0.05)",borderRadius:2,overflow:"hidden",marginBottom:10}}>
                    <div style={{height:"100%",width:`${revPct}%`,background:tier.color,borderRadius:2}}/>
                  </div>
                  {/* Top buyers in tier */}
                  {top3.length>0&&(
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {top3.map(b=>(
                        <div key={b.id} style={{background:"rgba(0,0,0,0.3)",borderRadius:8,padding:"5px 10px",fontSize:11}}>
                          <span style={{color:tier.color,fontWeight:700}}>@{b.username}</span>
                          <span style={{color:"var(--bz-ink-3)",marginLeft:6}}>${Math.round(b.totalSpend||0).toLocaleString()}</span>
                        </div>
                      ))}
                      {tier.buyers.length>3&&<div style={{color:"var(--bz-ink-3)",fontSize:11,alignSelf:"center"}}>+{tier.buyers.length-3} more</div>}
                    </div>
                  )}
                </div>
              );
            })}

            {/* 80/20 insight */}
            {(() => {
              let cumRev=0, cutoffIdx=0;
              for(let i=0;i<sorted.length;i++){
                cumRev+=sorted[i].totalSpend||0;
                if(cumRev>=totalRev*0.80){cutoffIdx=i+1;break;}
              }
              const pct=(cutoffIdx/total*100).toFixed(1);
              return (
                <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"14px 20px",display:"flex",alignItems:"center",gap:16}}>
                  <div style={{fontSize:28}}>💡</div>
                  <div style={{fontSize:13,color:"var(--bz-ink-2)",lineHeight:1.6}}>
                    <strong style={{color:"var(--bz-ink)"}}>{pct}%</strong> of your buyers ({cutoffIdx} people) account for <strong style={{color:"#4ade80"}}>80% of your revenue</strong>. These are the buyers worth protecting most — personal outreach, early access, VIP treatment.
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* COHORT TABLE */}
      {view==="cohort" && (
        <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px",overflowX:"auto"}}>
          <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:4}}>Cohort Retention Table</div>
          <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:14}}>% of each month's new buyers who came back</div>
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:400}}>
            <thead>
              <tr>
                <th style={{padding:"6px 10px",textAlign:"left",fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1}}>Cohort</th>
                <th style={{padding:"6px 10px",textAlign:"center",fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1}}>Size</th>
                {months.slice(1).map(m=><th key={m.key} style={{padding:"6px 10px",textAlign:"center",fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1}}>{m.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {cohorts.map(c=>(
                <tr key={c.key}>
                  <td style={{padding:"8px 10px",fontSize:12,fontWeight:700,color:"var(--bz-ink)"}}>{c.label}</td>
                  <td style={{padding:"8px 10px",fontSize:12,color:"var(--bz-ink-3)",textAlign:"center"}}>{c.size}</td>
                  {months.slice(1).map(m=>{
                    const f=c.follow.find(x=>x.key===m.key);
                    if(!f) return <td key={m.key} style={{padding:"8px 10px",textAlign:"center",color:"var(--bz-ink-3)",fontSize:11}}>—</td>;
                    return (
                      <td key={m.key} style={{padding:"8px 10px",textAlign:"center"}}>
                        {f.retained>0?(
                          <div style={{display:"inline-block",background:`${retColor(f.pct)}18`,border:`1px solid ${retColor(f.pct)}44`,borderRadius:6,padding:"2px 8px"}}>
                            <div style={{fontSize:12,fontWeight:700,color:retColor(f.pct)}}>{f.pct.toFixed(0)}%</div>
                            <div style={{fontSize:9,color:"var(--bz-ink-3)"}}>{f.retained}</div>
                          </div>
                        ):<span style={{color:"var(--bz-ink-3)",fontSize:11}}>0%</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function BuyerFunnel({ streams=[], isAdmin, visibleBreakers=[] }) {
  const [period, setPeriod] = useState("month");
  const [brand, setBrand] = useState("boba");
  const now = new Date();

  function filterByPeriod(slist) {
    return slist.filter(s=>{ const d=parseLocalDate(s.date);
      if(brand !== "all" && getStreamBrand(s) !== brand) return false;
      if(period==="month") return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
      if(period==="quarter"){const q=Math.floor(now.getMonth()/3);return Math.floor(d.getMonth()/3)===q&&d.getFullYear()===now.getFullYear();}
      if(period==="year") return d.getFullYear()===now.getFullYear();
      return true;
    });
  }

  const filtered = filterByPeriod(streams);
  const totalStreams = filtered.length;
  const totalNewBuyers = filtered.reduce((s,x)=>s+(parseInt(x.newBuyers)||0),0);
  const avgNewBuyers = totalStreams?totalNewBuyers/totalStreams:0;

  // Per breaker breakdown
  const byBreaker = BREAKERS.map(b=>{
    const bs=filtered.filter(s=>s.breaker===b);
    const nb=bs.reduce((s,x)=>s+(parseInt(x.newBuyers)||0),0);
    const perStream=bs.length?nb/bs.length:0;
    const best=bs.length?Math.max(...bs.map(s=>parseInt(s.newBuyers)||0)):0;
    return {b,streams:bs.length,newBuyers:nb,perStream,best};
  }).filter(r=>r.streams>0).sort((a,z)=>z.newBuyers-a.newBuyers);

  // Weekly trend
  const weeklyTrend = Array.from({length:8},(_,i)=>{
    const wEnd=new Date(now); wEnd.setDate(now.getDate()-i*7); wEnd.setHours(23,59,59,999);
    const wStart=new Date(wEnd); wStart.setDate(wEnd.getDate()-6); wStart.setHours(0,0,0,0);
    const ws=streams.filter(s=>{ const d=parseLocalDate(s.date); return d>=wStart&&d<=wEnd; });
    return { label:`W${8-i}`, newBuyers:ws.reduce((s,x)=>s+(parseInt(x.newBuyers)||0),0), streams:ws.length };
  }).reverse();
  const maxNB = Math.max(...weeklyTrend.map(w=>w.newBuyers),1);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
        {[["month","Month"],["quarter","Quarter"],["year","Year"],["all","All Time"]].map(([id,l])=>(
          <button key={id} onClick={()=>setPeriod(id)} style={{background:period===id?"rgba(22,101,52,0.2)":"transparent",border:`1px solid ${period===id?"#166534":"#333"}`,color:period===id?"#4ade80":"#555",borderRadius:16,padding:"5px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
        ))}
        <span style={{color:"var(--bz-ink-3)",fontSize:11}}>|</span>
        {[["boba","🃏 BoBA"],["wotf","🐉 WotF"],["all","All"]].map(([id,l])=>(
          <button key={id} onClick={()=>setBrand(id)} style={{background:brand===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${brand===id?"#E8317A":"#333"}`,color:brand===id?"#E8317A":"#555",borderRadius:16,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
        ))}
      </div>

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
        {[
          {l:"Total New Buyers",  v:totalNewBuyers, c:"#4ade80"},
          {l:"Avg Per Stream",    v:avgNewBuyers.toFixed(1), c:"#4ade80"},
          {l:"Streams Analyzed",  v:totalStreams, c:"var(--bz-ink)"},
        ].map(({l,v,c})=>(
          <div key={l} style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"14px 16px",textAlign:"center"}}>
            <div style={{fontSize:28,fontWeight:900,color:c}}>{v}</div>
            <div style={{fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginTop:4}}>{l}</div>
          </div>
        ))}
      </div>

      {/* Weekly trend */}
      <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px"}}>
        <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:14}}>New Buyers by Week</div>
        <div style={{display:"flex",gap:6,alignItems:"flex-end",height:120}}>
          {weeklyTrend.map((w,i)=>(
            <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
              <div style={{fontSize:10,fontWeight:700,color:"#4ade80"}}>{w.newBuyers||""}</div>
              <div style={{width:"100%",height:90,display:"flex",alignItems:"flex-end"}}>
                <div style={{width:"100%",height:`${(w.newBuyers/maxNB)*100}%`,background:"rgba(74,222,128,0.5)",borderRadius:"3px 3px 0 0",minHeight:w.newBuyers?4:0}}/>
              </div>
              <div style={{fontSize:9,color:"var(--bz-ink-3)"}}>{w.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* By breaker */}
      <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"16px 20px"}}>
        <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:12}}>New Buyers by Rep</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {byBreaker.map(r=>{
            const bc=BC[r.b]?.text||"#E8317A";
            const pct=totalNewBuyers?r.newBuyers/totalNewBuyers*100:0;
            return (
              <div key={r.b} style={{display:"grid",gridTemplateColumns:"110px 1fr 60px 80px 80px",gap:12,alignItems:"center"}}>
                <div style={{fontSize:13,fontWeight:700,color:bc}}>{r.b}</div>
                <div style={{height:8,background:"var(--bz-s2)",borderRadius:4,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${pct}%`,background:bc,borderRadius:4}}/>
                </div>
                <div style={{fontSize:12,fontWeight:700,color:"#4ade80",textAlign:"right"}}>{r.newBuyers}</div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",textAlign:"right"}}>{r.perStream.toFixed(1)}/stream</div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",textAlign:"right"}}>Best: {r.best}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── REVENUE CONCENTRATION ─────────────────────────────────────────────────────
function RevenueConcentration({ streams=[], isAdmin }) {
  const [period, setPeriod] = useState("month");
  const now = new Date();

  const filtered = streams.filter(s=>{ const d=parseLocalDate(s.date);
    if(period==="month") return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
    if(period==="quarter"){const q=Math.floor(now.getMonth()/3);return Math.floor(d.getMonth()/3)===q&&d.getFullYear()===now.getFullYear();}
    if(period==="year") return d.getFullYear()===now.getFullYear();
    return true;
  });

  const totalGross = filtered.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);
  const totalNB = filtered.reduce((s,x)=>s+(parseInt(x.newBuyers)||0),0);
  const avgNBperStream = filtered.length?totalNB/filtered.length:0;
  const avgGrossPerStream = filtered.length?totalGross/filtered.length:0;

  // Top streams by gross
  const topStreams = [...filtered].sort((a,b)=>(parseFloat(b.grossRevenue)||0)-(parseFloat(a.grossRevenue)||0)).slice(0,10);
  const top3Gross = topStreams.slice(0,3).reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);
  const top10Gross = topStreams.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);
  const top3Pct = totalGross?top3Gross/totalGross*100:0;
  const top10Pct = totalGross?top10Gross/totalGross*100:0;

  const fmt = v => "$"+Number(v||0).toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0});

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",gap:6}}>
        {[["month","Month"],["quarter","Quarter"],["year","Year"],["all","All Time"]].map(([id,l])=>(
          <button key={id} onClick={()=>setPeriod(id)} style={{background:period===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${period===id?"#E8317A":"#333"}`,color:period===id?"#E8317A":"#555",borderRadius:16,padding:"5px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
        ))}
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
        {[
          {l:"Top 3 Streams % of Revenue", v:top3Pct.toFixed(1)+"%", c:top3Pct>60?"#E8317A":top3Pct>40?"#FBBF24":"#4ade80"},
          {l:"Top 10 Streams % of Revenue",v:top10Pct.toFixed(1)+"%",c:top10Pct>80?"#E8317A":top10Pct>60?"#FBBF24":"#4ade80"},
          {l:"Avg Gross Per Stream",        v:fmt(avgGrossPerStream),  c:"var(--bz-ink)"},
        ].map(({l,v,c})=>(
          <div key={l} style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"14px 16px",textAlign:"center"}}>
            <div style={{fontSize:26,fontWeight:900,color:c}}>{v}</div>
            <div style={{fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginTop:4}}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px"}}>
        <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:12}}>Top Streams by Revenue</div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {topStreams.map((s,i)=>{
            const g=parseFloat(s.grossRevenue)||0;
            const pct=totalGross?g/totalGross*100:0;
            const bc=BC[s.breaker]||"#E8317A";
            const cumPct=topStreams.slice(0,i+1).reduce((sum,x)=>sum+(parseFloat(x.grossRevenue)||0)/totalGross*100,0);
            return (
              <div key={s.id||i} style={{display:"grid",gridTemplateColumns:"24px 90px 90px 1fr 70px 50px",gap:10,alignItems:"center"}}>
                <div style={{fontSize:12,color:"var(--bz-ink-3)",textAlign:"center"}}>#{i+1}</div>
                <div style={{fontSize:11,color:bc,fontWeight:700}}>{s.breaker}</div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>{s.date}</div>
                <div style={{height:6,background:"var(--bz-s2)",borderRadius:3,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${pct}%`,background:"#E8317A",borderRadius:3}}/>
                </div>
                <div style={{fontSize:12,fontWeight:700,color:"#E8317A",textAlign:"right"}}>{fmt(g)}</div>
                <div style={{fontSize:10,color:"var(--bz-ink-3)",textAlign:"right"}}>{pct.toFixed(1)}%</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── SET PERFORMANCE ───────────────────────────────────────────────────────────
function SetPerformance({ streams=[], isAdmin }) {
  const [metric, setMetric] = useState("mm");
  const fmt = v => "$"+Number(v||0).toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0});
  const mmColor = v => v>=1.7?"#4ade80":v>=1.5?"#86efac":v>=1.4?"#FBBF24":"#E8317A";

  // Extract set names from streamSkuPrices or product fields
  const setData = {};
  streams.forEach(s=>{
    const prods = s.streamSkuPrices ? Object.keys(s.streamSkuPrices) : [];
    const gross = parseFloat(s.grossRevenue)||0;
    const mm = parseFloat(s.marketMultiple)||0;
    const nb = parseInt(s.newBuyers)||0;
    // If has SKU prices, attribute to those sets
    if(prods.length>0) {
      prods.forEach(p=>{
        if(!setData[p]) setData[p]={streams:0,gross:0,mmSum:0,mmCount:0,newBuyers:0};
        setData[p].streams++;
        setData[p].gross+=gross/prods.length;
        if(mm>0){setData[p].mmSum+=mm;setData[p].mmCount++;}
        setData[p].newBuyers+=nb;
      });
    } else if(s.sessionType||s.streamName) {
      const key=s.streamName||"Untagged";
      if(!setData[key]) setData[key]={streams:0,gross:0,mmSum:0,mmCount:0,newBuyers:0};
      setData[key].streams++;
      setData[key].gross+=gross;
      if(mm>0){setData[key].mmSum+=mm;setData[key].mmCount++;}
      setData[key].newBuyers+=nb;
    }
  });

  const setRows = Object.entries(setData).map(([set,d])=>({
    set, streams:d.streams, gross:d.gross, perStream:d.streams?d.gross/d.streams:0,
    mm:d.mmCount?d.mmSum/d.mmCount:0, newBuyers:d.newBuyers
  })).filter(r=>r.streams>0).sort((a,b)=>{
    if(metric==="mm") return b.mm-a.mm;
    if(metric==="gross") return b.perStream-a.perStream;
    return b.newBuyers-a.newBuyers;
  });

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <span style={{fontSize:12,color:"var(--bz-ink-3)"}}>Rank by:</span>
        {[["mm","Market Multiple"],["gross","Avg Revenue"],["newBuyers","New Buyers"]].map(([id,l])=>(
          <button key={id} onClick={()=>setMetric(id)} style={{background:metric===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${metric===id?"#E8317A":"#333"}`,color:metric===id?"#E8317A":"#555",borderRadius:16,padding:"5px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
        ))}
      </div>

      {setRows.length===0 ? <div style={{textAlign:"center",color:"var(--bz-ink-3)",padding:"40px",fontSize:13}}>Add products to stream recaps to see set performance</div> :
      <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px"}}>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {setRows.map((r,i)=>(
            <div key={r.set} style={{display:"grid",gridTemplateColumns:"24px 1fr 70px 90px 90px 80px",gap:12,alignItems:"center",padding:"8px 12px",background:i===0?"rgba(232,49,122,0.04)":"transparent",borderRadius:8}}>
              <div style={{fontSize:12,color:"var(--bz-ink-3)"}}>#{i+1}</div>
              <div style={{fontSize:13,fontWeight:700,color:"var(--bz-ink)"}}>{r.set}</div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",textAlign:"center"}}>{r.streams} streams</div>
              <div style={{fontSize:14,fontWeight:900,color:r.mm?mmColor(r.mm):"#555",textAlign:"right"}}>{r.mm?r.mm.toFixed(2)+"x":"--"}</div>
              <div style={{fontSize:13,fontWeight:700,color:"#E8317A",textAlign:"right"}}>{fmt(r.perStream)}</div>
              <div style={{fontSize:12,color:"#166534",textAlign:"right"}}>+{r.newBuyers} NB</div>
            </div>
          ))}
        </div>
      </div>}
    </div>
  );
}

// ── GOAL TRACKING ─────────────────────────────────────────────────────────────
function GoalTracking({ streams=[], isAdmin, visibleBreakers=[] }) {
  const now = new Date();
  const GOAL_STORE_KEY = `bz_perf_goals_${now.getFullYear()}_${now.getMonth()+1}`;
  const [goals, setGoals] = useState(() => {
    try { return JSON.parse(localStorage.getItem(GOAL_STORE_KEY)||"{}"); } catch { return {}; }
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({...goals});
  const fmt = v => "$"+Number(v||0).toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0});
  const mmColor = v => v>=1.7?"#4ade80":v>=1.5?"#86efac":v>=1.4?"#FBBF24":"#E8317A";

  const monthStreams = streams.filter(s=>{ const d=parseLocalDate(s.date); return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear(); });
  const monthGross = monthStreams.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);
  const monthNB = monthStreams.reduce((s,x)=>s+(parseInt(x.newBuyers)||0),0);
  const mmList = monthStreams.filter(x=>parseFloat(x.marketMultiple)>0);
  const monthMM = mmList.length?mmList.reduce((s,x)=>s+(parseFloat(x.marketMultiple)||0),0)/mmList.length:0;

  function saveGoals() {
    setGoals({...draft});
    localStorage.setItem(GOAL_STORE_KEY, JSON.stringify(draft));
    setEditing(false);
  }

  const GOAL_ITEMS = [
    { key:"gross",    label:"Monthly Gross Revenue", actual:monthGross, fmt:v=>fmt(v), prefix:"$", color:"#E8317A" },
    { key:"newBuyers",label:"New Buyers",            actual:monthNB,    fmt:v=>v,      prefix:"",  color:"#4ade80" },
    { key:"mm",       label:"Avg Market Multiple",   actual:monthMM,    fmt:v=>v?v.toFixed(2)+"x":"--", prefix:"", color:monthMM?mmColor(monthMM):"#555" },
    { key:"streams",  label:"Total Streams",         actual:monthStreams.length, fmt:v=>v, prefix:"", color:"var(--bz-ink)" },
  ];

  // Per-rep goals
  const repGoalItems = BREAKERS.map(b=>{
    const bs=monthStreams.filter(s=>s.breaker===b);
    const gross=bs.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);
    const nb=bs.reduce((s,x)=>s+(parseInt(x.newBuyers)||0),0);
    const mmL=bs.filter(x=>parseFloat(x.marketMultiple)>0);
    const mm=mmL.length?mmL.reduce((s,x)=>s+(parseFloat(x.marketMultiple)||0),0)/mmL.length:0;
    return {b,gross,nb,mm,streams:bs.length};
  });

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)"}}>
          🎯 {now.toLocaleDateString("en-US",{month:"long",year:"numeric"})} Goals
        </div>
        {isAdmin && <button onClick={()=>{setDraft({...goals});setEditing(p=>!p)}}
          style={{background:editing?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${editing?"#E8317A":"#333"}`,color:editing?"#E8317A":"#555",borderRadius:16,padding:"6px 16px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
          {editing?"Cancel":"✏️ Set Goals"}
        </button>}
      </div>

      {/* Team goals */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12}}>
        {GOAL_ITEMS.map(({key,label,actual,fmt:fmtFn,color})=>{
          const goal=parseFloat(goals[key])||0;
          const pct=goal>0?Math.min(actual/goal*100,100):0;
          const over=goal>0&&actual>=goal;
          return (
            <div key={key} style={{background:"var(--bz-s1)",border:`1px solid ${over?"rgba(74,222,128,0.3)":"var(--bz-s2)"}`,borderRadius:12,padding:"16px 18px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                <div>
                  <div style={{fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{label}</div>
                  <div style={{fontSize:24,fontWeight:900,color}}>{fmtFn(actual)}</div>
                </div>
                {goal>0 && <div style={{textAlign:"right"}}>
                  <div style={{fontSize:12,fontWeight:700,color:over?"#4ade80":"#FBBF24"}}>{over?"✅ Hit!":pct.toFixed(0)+"%"}</div>
                  <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>Goal: {fmtFn(goal)}</div>
                </div>}
              </div>
              {goal>0 && <div style={{height:6,background:"var(--bz-s2)",borderRadius:3,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${pct}%`,background:over?"#4ade80":"#E8317A",borderRadius:3,transition:"width 0.5s"}}/>
              </div>}
              {editing && <input type="number" placeholder={`Set ${label} goal...`} value={draft[key]||""} onChange={e=>setDraft(p=>({...p,[key]:e.target.value}))}
                style={{marginTop:10,width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:6,color:"var(--bz-ink)",padding:"6px 10px",fontSize:12,fontFamily:"inherit",boxSizing:"border-box"}}/>}
            </div>
          );
        })}
      </div>

      {editing && <button onClick={saveGoals} style={{background:"linear-gradient(135deg,#E8317A,#c02060)",color:"#fff",border:"none",borderRadius:10,padding:"10px 24px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",alignSelf:"flex-start"}}>Save Goals</button>}

      {/* Per-rep progress */}
      {goals.repGross && (
        <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"16px 20px"}}>
          <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:12}}>Rep Gross Goal Progress</div>
          {repGoalItems.map(r=>{
            const goal=parseFloat(goals[`${r.b}_gross`])||parseFloat(goals.repGross)||0;
            const pct=goal>0?Math.min(r.gross/goal*100,100):0;
            const bc=BC[r.b]?.text||"#E8317A";
            return (
              <div key={r.b} style={{marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:13,fontWeight:700,color:bc}}>{r.b}</span>
                  <span style={{fontSize:12,color:"var(--bz-ink-3)"}}>{fmt(r.gross)} {goal>0?`/ ${fmt(goal)} (${pct.toFixed(0)}%)`:""}</span>
                </div>
                {goal>0&&<div style={{height:8,background:"var(--bz-s2)",borderRadius:4,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${pct}%`,background:bc,borderRadius:4}}/>
                </div>}
              </div>
            );
          })}
          {editing&&<>
            <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:8}}>Set per-rep gross goal (or set a shared goal above):</div>
            {BREAKERS.map(b=><div key={b} style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
              <span style={{fontSize:12,color:BC[b]?.text||"#E8317A",minWidth:120}}>{b}</span>
              <input type="number" placeholder="Goal..." value={draft[`${b}_gross`]||""} onChange={e=>setDraft(p=>({...p,[`${b}_gross`]:e.target.value}))}
                style={{flex:1,background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:6,color:"var(--bz-ink)",padding:"5px 10px",fontSize:12,fontFamily:"inherit"}}/>
            </div>)}
          </>}
        </div>
      )}
    </div>
  );
}

function Performance({ defaultPeriod="all", defaultPerfTab="stats", breaks, user, userRole, streams=[], buyers=[], historicalData=[] }) {
  const isAdmin        = userRole?.role === "Admin";
  const currentUser    = user?.displayName?.split(" ")[0] || "";
  const matchedBreaker = BREAKERS.find(b => currentUser.toLowerCase().replace(/\s+/g,"").includes(b.toLowerCase().replace(/\s+/g,"")));
  const visibleBreakers = isAdmin ? BREAKERS : (matchedBreaker ? [matchedBreaker] : []);
  const now  = new Date();
  const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const [perfPeriod, setPerfPeriod] = useState(defaultPeriod);
  useEffect(()=>{setPerfPeriod(defaultPeriod);},[defaultPeriod]);
  const [perfFrom,   setPerfFrom]   = useState("");
  const [perfTo,     setPerfTo]     = useState("");
  const [perfTab,    setPerfTab]    = useState(defaultPerfTab);
  useEffect(()=>{setPerfTab(defaultPerfTab);},[defaultPerfTab]);

  function getPerfStreams() {
    return streams.filter(s => {
      if (!s.date) return false;
      if ((s.channel||"Bazooka Vault") === "Orbital Society") return false; // tracked separately
      if (isSinglesStream(s)) return false;     // singles are pass-through, not break performance
      if (s.excludeFinancials) return false; // already counted elsewhere — keep out of all performance stats
      const d = parseLocalDate(s.date);
      if (perfPeriod === "week") {
        const day=d.getDay(), diff=day===0?6:day-1;
        const wStart=new Date(now); wStart.setDate(now.getDate()-(now.getDay()===0?6:now.getDay()-1)); wStart.setHours(0,0,0,0);
        const wEnd=new Date(wStart); wEnd.setDate(wStart.getDate()+6); wEnd.setHours(23,59,59,999);
        return d >= wStart && d <= wEnd;
      }
      if (perfPeriod === "month") return d.getMonth()===now.getMonth() && d.getFullYear()===now.getFullYear();
      if (perfPeriod === "quarter") { const q=Math.floor(now.getMonth()/3); return Math.floor(d.getMonth()/3)===q && d.getFullYear()===now.getFullYear(); }
      if (perfPeriod === "year") return d.getFullYear()===now.getFullYear();
      if (perfPeriod === "custom" && perfFrom && perfTo) {
        const f=new Date(perfFrom); f.setHours(0,0,0,0);
        const t=new Date(perfTo);   t.setHours(23,59,59,999);
        return d >= f && d <= t;
      }
      return true;
    });
  }

  const filteredStreams = getPerfStreams();
  const thisMonth = filteredStreams;
  const thisYear  = streams.filter(s => parseLocalDate(s.date).getFullYear()===now.getFullYear());

  function boxesForStreams(slist) {
    return PRODUCT_TYPES.reduce((acc, pt) => {
      acc[pt] = slist.reduce((sum, s) => sum + (parseInt(s[`prod_${pt}`])||0), 0);
      return acc;
    }, {});
  }

  const monthBoxes = boxesForStreams(thisMonth);
  const yearBoxes  = boxesForStreams(thisYear);
  const monthTotal = Object.values(monthBoxes).reduce((a,b)=>a+b,0);
  const yearTotal  = Object.values(yearBoxes).reduce((a,b)=>a+b,0);
  const monthGross = thisMonth.reduce((sum,s) => sum+(parseFloat(s.grossRevenue)||0), 0);
  const monthNewBuyers = thisMonth.reduce((sum,s) => sum+(parseInt(s.newBuyers)||0), 0);

  const PT_COLORS = {
    "Double Mega":   "#FFFFFF",
    "Hobby":         "#FFFFFF",
    "Jumbo":         "#FFFFFF",
    "Miscellaneous": "#FFFFFF",
  };

  function getStats(breaker) {
    const all   = breaks.filter(b => b.breaker===breaker);
    const month = all.filter(b => { const d=new Date(b.dateAdded||b.date); return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear(); });
    const byType = {}; CARD_TYPES.forEach(ct => { byType[ct]=all.filter(b=>b.cardType===ct).length; });
    const byDay  = [0,0,0,0,0,0,0];
    all.forEach(b => { const d=new Date(b.dateAdded||b.date); if(!isNaN(d)) byDay[d.getDay()]++; });
    const last7 = [];
    for (let i=6; i>=0; i--) { const d=new Date(); d.setDate(d.getDate()-i); last7.push({ date:d.toLocaleDateString("en",{weekday:"short"}), count:all.filter(b=>new Date(b.dateAdded||b.date).toDateString()===d.toDateString()).length }); }
    let streak=0;
    for (let i=0; i<30; i++) { const d=new Date(); d.setDate(d.getDate()-i); if(all.some(b=>new Date(b.dateAdded||b.date).toDateString()===d.toDateString())) streak++; else if(i>0) break; }
    const topType = CARD_TYPES.reduce((a,ct) => byType[ct]>byType[a]?ct:a, CARD_TYPES[0]);
    // Boxes ripped this month by breaker
    const breakerMonthStreams = thisMonth.filter(s => s.breaker===breaker);
    const breakerBoxes = boxesForStreams(breakerMonthStreams);
    const breakerBoxTotal = Object.values(breakerBoxes).reduce((a,b)=>a+b,0);
    const breakerGross = breakerMonthStreams.reduce((sum,s) => sum+(parseFloat(s.grossRevenue)||0), 0);
    const breakerNewBuyers = breakerMonthStreams.reduce((sum,s) => sum+(parseInt(s.newBuyers)||0), 0);
    const mmStreams = breakerMonthStreams.filter(s=>parseFloat(s.marketMultiple)>0 && (s.channel||"Bazooka Vault")!=="Orbital Society");
    const breakerAvgMM = mmStreams.length>0 ? mmStreams.reduce((sum,s)=>sum+(parseFloat(s.marketMultiple)||0),0)/mmStreams.length : null;
    return { all, month, byType, byDay, last7, streak, topType, breakerBoxes, breakerBoxTotal, breakerGross, breakerNewBuyers, breakerAvgMM };
  }

  if (visibleBreakers.length===0) return <div style={{ ...S.card, textAlign:"center", padding:"60px" }}><div style={{ fontSize:32, marginBottom:12 }}>{"\uD83D\uDCC8"}</div><div style={{ color:"var(--bz-ink-2)" }}>Your account isn't linked to a streamer profile.</div></div>;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

      {/* Tab nav — dropdown style */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
        <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>View:</span>
        <select value={perfTab} onChange={e=>setPerfTab(e.target.value)}
          style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:10, color:"var(--bz-ink)", padding:"8px 14px", fontSize:13, fontFamily:"inherit", cursor:"pointer", fontWeight:600 }}>
          {[
            ["stats","📊 Stats"],
            ["weekly","📋 Weekly Report"],
            ["reportcard","🏆 Rep Report Card"],
            ["leaderboard","🥇 Leaderboard"],
            ["mmtrend","📈 MM Trend"],
            ["timeslots","⏰ Time Analysis"],
            ["newbuyers","🌱 Buyer Funnel"],
            ["retention","🔄 Buyer Retention"],
            ["concentration","💎 Revenue Concentration"],
            ["sets","📦 Set Performance"],
            ["goals","🎯 Goals"],
            ["followers","📡 Followers"],
          ].filter(([id]) => id !== "reportcard" || isAdmin).map(([id,label])=>(
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
      </div>

      {perfTab==="followers"    && <WhatnotFollowerTracker isAdmin={isAdmin} />}
      {perfTab==="weekly"       && <WeeklyReport streams={streams} userRole={userRole} />}
      {perfTab==="reportcard"   && <RepReportCard streams={streams} isAdmin={isAdmin} />}
      {perfTab==="leaderboard"  && <TeamLeaderboard streams={filteredStreams} allStreams={streams} isAdmin={isAdmin} />}
      {perfTab==="mmtrend"      && <MMTrend streams={streams} isAdmin={isAdmin} visibleBreakers={visibleBreakers} />}
      {perfTab==="timeslots"    && <TimeAnalysis streams={streams} isAdmin={isAdmin} visibleBreakers={visibleBreakers} />}
      {perfTab==="newbuyers"    && <BuyerFunnel streams={streams} isAdmin={isAdmin} visibleBreakers={visibleBreakers} />}
      {perfTab==="retention"    && <BuyerRetention buyers={buyers} streams={streams} />}
      {perfTab==="concentration"&& <RevenueConcentration streams={streams} isAdmin={isAdmin} />}
      {perfTab==="sets"         && <SetPerformance streams={streams} isAdmin={isAdmin} />}
      {perfTab==="goals"        && <GoalTracking streams={streams} isAdmin={isAdmin} visibleBreakers={visibleBreakers} />}

      {perfTab==="stats" && <>

      {/* Period Filter */}
      <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
        {[["month","This Month"],["quarter","This Quarter"],["year","This Year"],["all","All Time"],["custom","Custom"]].map(([val,label]) => (
          <button key={val} onClick={()=>setPerfPeriod(val)} style={{ background:perfPeriod===val?"#E8317A":"var(--bz-s2)", color:perfPeriod===val?"#fff":"#888", border:`1px solid ${perfPeriod===val?"#E8317A":"var(--bz-line-2)"}`, borderRadius:7, padding:"5px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{label}</button>
        ))}
        {perfPeriod==="custom" && (
          <>
            <input type="date" value={perfFrom} onChange={e=>setPerfFrom(e.target.value)} style={{ ...S.inp, width:"auto", fontSize:12 }}/>
            <span style={{ color:"var(--bz-ink-3)", fontSize:12 }}>{"\u2192"}</span>
            <input type="date" value={perfTo} onChange={e=>setPerfTo(e.target.value)} style={{ ...S.inp, width:"auto", fontSize:12 }}/>
          </>
        )}
      </div>

      {/* Combined Team Recap */}

      {isAdmin && (thisMonth.length > 0 || historicalData?.length > 0) && (() => {
        // Include historical data in totals for accurate all-time/year numbers
        const histInPeriod = (historicalData||[]).filter(h => {
          if (!h.yearMonth) return false;
          if (perfPeriod==="month") return h.yearMonth===`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
          if (perfPeriod==="quarter") { const q=Math.floor(now.getMonth()/3); const hm=parseInt(h.yearMonth.split("-")[1])-1; return Math.floor(hm/3)===q&&h.yearMonth.startsWith(String(now.getFullYear())); }
          if (perfPeriod==="year") return h.yearMonth.startsWith(String(now.getFullYear()));
          if (perfPeriod==="all") return true;
          if (perfPeriod==="custom"&&perfFrom&&perfTo) return h.yearMonth>=perfFrom.slice(0,7)&&h.yearMonth<=perfTo.slice(0,7);
          return false;
        });
        const histGross = histInPeriod.reduce((s,h)=>s+(parseFloat(h.grossRevenue)||0),0);
        const histNewBuyers = histInPeriod.reduce((s,h)=>s+(parseInt(h.newBuyers)||0),0);
        const mmStreams = thisMonth.filter(s => parseFloat(s.marketMultiple) > 0 && (s.channel||"Bazooka Vault") !== "Orbital Society");
        const avgMM = mmStreams.length > 0 ? mmStreams.reduce((sum,s) => sum+(parseFloat(s.marketMultiple)||0),0)/mmStreams.length : null;
        const totalGross = thisMonth.filter(s=>!isSinglesStream(s) && !s.excludeFinancials).reduce((sum,s) => sum+(parseFloat(s.grossRevenue)||0), 0) + histGross;
        const totalNewBuyers = thisMonth.reduce((sum,s) => sum+(parseInt(s.newBuyers)||0), 0) + histNewBuyers;
        const mmBuckets = { "< 1.4x":0, "1.4–1.5x":0, "1.5–1.7x":0, "1.7x+":0 };
        mmStreams.forEach(s => {
          const mm = parseFloat(s.marketMultiple);
          if (mm < 1.4)       mmBuckets["< 1.4x"]++;
          else if (mm < 1.5)  mmBuckets["1.4–1.5x"]++;
          else if (mm < 1.7)  mmBuckets["1.5–1.7x"]++;
          else                mmBuckets["1.7x+"]++;
        });
        const mmColor = !avgMM ? "#555" : avgMM >= 1.7 ? "#4ade80" : avgMM >= 1.5 ? "#86efac" : avgMM >= 1.4 ? "#FBBF24" : "#E8317A";
        const breakerBreakdown = BREAKERS.map(b => {
          const bStreams = thisMonth.filter(s => s.breaker === b);
          const bMM = bStreams.filter(s=>parseFloat(s.marketMultiple)>0);
          const bAvgMM = bMM.length > 0 ? bMM.reduce((sum,s)=>sum+(parseFloat(s.marketMultiple)||0),0)/bMM.length : null;
          const bGross = bStreams.filter(s=>!isSinglesStream(s) && !s.excludeFinancials).reduce((sum,s)=>sum+(parseFloat(s.grossRevenue)||0),0);
          return { breaker:b, streams:bStreams.length, gross:bGross, avgMM:bAvgMM };
        }).filter(b => b.streams > 0);

        return (
          <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:"var(--bz-radius)", padding:"22px", boxShadow:"var(--bz-shadow)" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:8 }}>
              <SectionLabel t="🏆 Team Recap" />
              <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{thisMonth.length} stream{thisMonth.length!==1?"s":""} · all breakers</span>
            </div>

            {/* Top KPIs */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
              {[
                { l:"Avg Market Multiple", v:avgMM ? `${avgMM.toFixed(2)}x` : "--", c:mmColor, ic:"🎯" },
                { l:"Gross Revenue",       v:`$${totalGross.toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0})}`, c:"var(--bz-pink)", ic:"📈" },
                { l:"Streams",             v:thisMonth.length, c:"var(--bz-ink)", ic:"🎬" },
                { l:"New Buyers",          v:totalNewBuyers, c:"var(--bz-green)", ic:"🌱" },
              ].map(({l,v,c,ic})=>(
                <div key={l} className="bz-fin-card" style={{ background:"var(--bz-s1)", borderRadius:"var(--bz-radius-sm)", padding:"16px", border:"1px solid var(--bz-line)", transition:"border-color .18s, transform .18s, box-shadow .18s" }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:11 }}>
                    <div style={{ fontSize:10.5, fontWeight:600, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:"0.7px" }}>{l}</div>
                    <div style={{ width:26, height:26, borderRadius:8, display:"grid", placeItems:"center", fontSize:13, background:`color-mix(in srgb, ${c} 14%, transparent)` }}>{ic}</div>
                  </div>
                  <div style={{ fontSize:29, fontWeight:800, color:c, lineHeight:1, letterSpacing:"-0.02em", fontFamily:"'Arial Narrow',sans-serif" }}>{v}</div>
                </div>
              ))}
            </div>

            {/* Market multiple distribution */}
            {mmStreams.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Market Multiple Distribution</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {Object.entries(mmBuckets).map(([label,count])=>{
                    if (!count) return null;
                    const pct = mmStreams.length > 0 ? count/mmStreams.length : 0;
                    const col = label==="1.7x+"?"#4ade80":label==="1.5–1.7x"?"#86efac":label==="1.4–1.5x"?"#FBBF24":"#E8317A";
                    return (
                      <div key={label} style={{ background:`${col}15`, border:`1px solid ${col}33`, borderRadius:8, padding:"6px 12px", textAlign:"center", minWidth:80 }}>
                        <div style={{ fontSize:16, fontWeight:900, color:col }}>{count}</div>
                        <div style={{ fontSize:10, color:col, opacity:0.7 }}>{label}</div>
                        <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{(pct*100).toFixed(0)}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Per-breaker breakdown */}
            {breakerBreakdown.length > 1 && (
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>By Breaker</div>
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  {breakerBreakdown.map(b => {
                    const bc = BC[b.breaker]||{text:"#888",bg:"var(--bz-s1)",border:"#333"};
                    const mmCol = !b.avgMM?"#555":b.avgMM>=1.7?"#4ade80":b.avgMM>=1.5?"#86efac":b.avgMM>=1.4?"#FBBF24":"#E8317A";
                    return (
                      <div key={b.breaker} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:"var(--bz-bg)", borderRadius:8, border:`1px solid var(--bz-s2)` }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <span style={{ background:bc.bg, color:bc.text, border:`1px solid ${bc.border}44`, borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700 }}>{b.breaker}</span>
                          <span style={{ fontSize:12, color:"var(--bz-ink-3)" }}>{b.streams} stream{b.streams!==1?"s":""}</span>
                        </div>
                        <div style={{ display:"flex", gap:20, alignItems:"center" }}>
                          <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>${b.gross.toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0})}</span>
                          <span style={{ fontSize:14, fontWeight:900, color:mmCol }}>{b.avgMM?`${b.avgMM.toFixed(2)}x`:"--"}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Market Multiple by Channel */}
            {(() => {
              const channels = [...new Set(thisMonth.map(s=>channelOf(s)))].sort();
              if (channels.length < 2) return null;
              const mmCol = v => !v?"#555":v>=1.7?"#4ade80":v>=1.5?"#86efac":v>=1.4?"#FBBF24":"#E8317A";
              return (
                <div style={{ marginTop:16 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>📺 Market Multiple by Channel</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {channels.map(ch => {
                      const chStreams = thisMonth.filter(s=>channelOf(s)===ch);
                      const chMM = chStreams.filter(s=>parseFloat(s.marketMultiple)>0);
                      const chAvg = chMM.length>0 ? chMM.reduce((s,x)=>s+parseFloat(x.marketMultiple),0)/chMM.length : null;
                      const chGross = chStreams.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);

                      // Breaker breakdown per channel
                      const breakersInCh = [...new Set(chStreams.map(s=>s.breaker).filter(Boolean))];

                      return (
                        <div key={ch} style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"12px 14px" }}>
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: breakersInCh.length>0?10:0 }}>
                            <div>
                              <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)" }}>{ch}</div>
                              <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>{chStreams.length} stream{chStreams.length!==1?"s":""} · ${chGross.toLocaleString("en-US",{maximumFractionDigits:0})}</div>
                            </div>
                            <div style={{ textAlign:"right" }}>
                              <div style={{ fontSize:22, fontWeight:900, color:mmCol(chAvg) }}>{chAvg?`${chAvg.toFixed(2)}x`:"—"}</div>
                              <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Avg MM</div>
                            </div>
                          </div>
                          {/* Breakers within this channel */}
                          {breakersInCh.length > 1 && (
                            <div style={{ display:"flex", gap:6, flexWrap:"wrap", paddingTop:8, borderTop:"1px solid var(--bz-line)" }}>
                              {breakersInCh.map(b => {
                                const bChStreams = chStreams.filter(s=>s.breaker===b);
                                const bChMM = bChStreams.filter(s=>parseFloat(s.marketMultiple)>0);
                                const bChAvg = bChMM.length>0 ? bChMM.reduce((s,x)=>s+parseFloat(x.marketMultiple),0)/bChMM.length : null;
                                const bc = BC[b]||{text:"#888"};
                                return (
                                  <div key={b} style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:7, padding:"5px 12px", display:"flex", alignItems:"center", gap:8 }}>
                                    <span style={{ fontSize:11, fontWeight:700, color:bc.text }}>{b}</span>
                                    <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{bChStreams.length}str</span>
                                    <span style={{ fontSize:13, fontWeight:800, color:mmCol(bChAvg) }}>{bChAvg?`${bChAvg.toFixed(2)}x`:"—"}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {breakersInCh.length === 1 && (
                            <div style={{ fontSize:11, color:"var(--bz-ink-3)", paddingTop:4 }}>
                              {breakersInCh[0]} · {chStreams.length} stream{chStreams.length!==1?"s":""}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        );
      })()}


      {(monthTotal > 0 || yearTotal > 0 || monthGross > 0 || monthNewBuyers > 0) && (
      <div style={S.card}>
        <SectionLabel t={`\uD83D\uDCE6 ${perfPeriod==="month"?"This Month's":perfPeriod==="week"?"This Week's":perfPeriod==="year"?"This Year's":perfPeriod==="quarter"?"This Quarter's":perfPeriod==="custom"?"Selected Period":""} Key Metrics`} />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:16 }}>
          <div style={{ ...S.card, textAlign:"center", background:"var(--bz-s1)" }}>
            <div style={{ fontSize:32, fontWeight:900, color:"var(--bz-ink)" }} className="num-pop"><AnimatedNumber value={monthTotal} format="count"/></div>
            <div style={{ fontSize:10, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginTop:4 }}>Boxes Ripped</div>
            <div style={{ display:"flex", gap:6, justifyContent:"center", flexWrap:"wrap", marginTop:8 }}>
              {PRODUCT_TYPES.map(pt => monthBoxes[pt]>0 ? (
                <span key={pt} style={{ color:PT_COLORS[pt], fontSize:11, fontWeight:700 }}>{pt.replace(" ","")}: {monthBoxes[pt]}</span>
              ) : null)}
            </div>
          </div>
          <div style={{ ...S.card, textAlign:"center", background:"var(--bz-s1)" }}>
            <div style={{ fontSize:32, fontWeight:900, color:"#E8317A" }} className="num-pop"><AnimatedNumber value={monthGross}/></div>
            <div style={{ fontSize:10, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginTop:4 }}>Gross Revenue</div>
            <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginTop:6 }}>{thisMonth.length} stream{thisMonth.length!==1?"s":""}</div>
          </div>
          <div style={{ ...S.card, textAlign:"center", background:"var(--bz-s1)" }}>
            <div style={{ fontSize:32, fontWeight:900, color:"#E8317A" }} className="num-pop"><AnimatedNumber value={monthNewBuyers} format="count"/></div>
            <div style={{ fontSize:10, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginTop:4 }}>New Buyers</div>
          </div>
        </div>
        {/* Per-breaker box bars */}
        {isAdmin && monthTotal > 0 && (
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Boxes by Breaker</div>
            {BREAKERS.map(b => {
              const bStreams = thisMonth.filter(s=>s.breaker===b);
              const bTotal  = Object.values(boxesForStreams(bStreams)).reduce((a,x)=>a+x,0);
              const bGross  = bStreams.reduce((sum,s)=>sum+(parseFloat(s.grossRevenue)||0),0);
              const bBuyers = bStreams.reduce((sum,s)=>sum+(parseInt(s.newBuyers)||0),0);
              if (bTotal===0 && bGross===0 && bBuyers===0) return null;
              const bc = BC[b]||{bg:"#F3F4F6",text:"var(--bz-ink-3)"};
              return (
                <div key={b} style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <Badge bg={bc.bg} color={bc.text}>{b}</Badge>
                  <div style={{ flex:1, height:6, background:"var(--bz-s1)", borderRadius:3, overflow:"hidden" }}>
                    <div style={{ height:"100%", background:bc.text, borderRadius:3, width:`${monthTotal>0?(bTotal/monthTotal*100):0}%` }}/>
                  </div>
                  <span style={{ fontSize:12, fontWeight:700, color:bc.text, minWidth:24 }}>{bTotal} boxes</span>
                  <span style={{ fontSize:11, color:"#E8317A", minWidth:60 }}>{fmt(bGross)}</span>
                  <span style={{ fontSize:11, color:"#E8317A", minWidth:30 }}>{"\uD83C\uDF31"}{bBuyers}</span>
                </div>
              );
            })}
          </div>
        )}
        {/* Year totals */}
        {yearTotal > 0 && (
          <div style={{ borderTop:"1px solid var(--bz-line)", paddingTop:14 }}>
            <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>This Year</div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
              <span style={{ fontSize:22, fontWeight:900, color:"var(--bz-ink)" }}>{yearTotal} boxes</span>
              {PRODUCT_TYPES.map(pt => yearBoxes[pt]>0 ? (
                <span key={pt} style={{ background:"var(--bz-s1)", border:`1.5px solid ${PT_COLORS[pt]}33`, color:PT_COLORS[pt], borderRadius:7, padding:"3px 10px", fontSize:11, fontWeight:700 }}>
                  {pt}: {yearBoxes[pt]}
                </span>
              ) : null)}
            </div>
          </div>
        )}
      </div>
      )}
      {visibleBreakers.map((breaker,bi) => {
        const bc    = BC[breaker];
        const stats = getStats(breaker);
        return (
          <div key={breaker}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
              <div style={{ width:44, height:44, borderRadius:"50%", background:bc.bg, border:`2px solid ${bc.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:900, color:bc.text }}>{breaker[0]}</div>
              <div>
                <div style={{ fontSize:18, fontWeight:900, color:"var(--bz-ink)" }}>{breaker}</div>
                <div style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{stats.all.length} total cards logged</div>
              </div>
              {stats.streak>0 && <div style={{ marginLeft:"auto", background:bc.bg, border:`1.5px solid ${bc.border}`, borderRadius:10, padding:"6px 14px", textAlign:"center" }}>
                <div style={{ fontSize:18, fontWeight:900, color:bc.text }}>{"\uD83D\uDD25"}{stats.streak}</div>
                <div style={{ fontSize:9, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1 }}>Day Streak</div>
              </div>}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:12 }}>
              {[
                { l:"Total Cards Used",     v:stats.all.length,                                          c:bc.text },
                { l:"This Month",           v:stats.month.length,                                        c:bc.text },
                { l:"\uD83D\uDCE6 Boxes This Month",  v:stats.breakerBoxTotal||0,                                  c:stats.breakerBoxTotal>0?"#6B2D8B":"var(--bz-ink-2)" },
                { l:"\uD83D\uDCB0 Gross This Month",  v:stats.breakerGross>0?fmt(stats.breakerGross):"--",          c:stats.breakerGross>0?"#E8317A":"var(--bz-ink-2)" },
                { l:"\uD83C\uDF31 New Buyers",        v:stats.breakerNewBuyers||0,                                 c:stats.breakerNewBuyers>0?"#166534":"var(--bz-ink-2)" },
                { l:"Top Card Type",        v:stats.topType.replace(" Cards",""),                        c:CC[stats.topType]?.text||bc.text },
                { l:"\uD83D\uDCC8 Avg Market Multiple", v:stats.breakerAvgMM?`${stats.breakerAvgMM.toFixed(2)}x`:"--", c:stats.breakerAvgMM>=1.6?"#166534":stats.breakerAvgMM>=1.5?"#92400e":"var(--bz-ink-2)" },
                { l:"Active Streak",        v:stats.streak>0?`${stats.streak}d`:"--",                    c:stats.streak>0?"#E8317A":"var(--bz-ink-2)" },
              ].map(({l,v,c}) => (
                <div key={l} className="stat-card" style={{ ...S.card, textAlign:"center" }}>
                  <div style={{ fontSize:20, fontWeight:900, color:c, marginBottom:4 }}>{v}</div>
                  <div style={{ fontSize:10, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1 }}>{l}</div>
                </div>
              ))}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
              <div style={S.card}>
                <SectionLabel t="Card Type Breakdown" />
                {CARD_TYPES.map(ct => {
                  const cc=CC[ct]; const cnt=stats.byType[ct]; const pct=stats.all.length>0?cnt/stats.all.length:0;
                  return (
                    <div key={ct} style={{ marginBottom:8 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                        <span style={{ fontSize:12, fontWeight:700, color:cc.text }}>{ct}</span>
                        <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>{cnt} ({(pct*100).toFixed(0)}%)</span>
                      </div>
                      <div style={{ height:6, background:"var(--bz-s1)", borderRadius:3, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${pct*100}%`, background:cc.border, borderRadius:3 }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={S.card}>
                <SectionLabel t="Last 7 Days" />
                <div style={{ display:"flex", alignItems:"flex-end", gap:6, height:100 }}>
                  {stats.last7.map((d,i) => {
                    const max=Math.max(...stats.last7.map(x=>x.count),1);
                    const h=Math.max((d.count/max)*80, d.count>0?8:2);
                    return (
                      <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                        <div style={{ fontSize:10, fontWeight:700, color:d.count>0?bc.text:"var(--bz-ink-2)" }}>{d.count||""}</div>
                        <div style={{ width:"100%", height:h, background:d.count>0?bc.border:"#F0E0E8", borderRadius:"3px 3px 0 0" }}/>
                        <div style={{ fontSize:9, color:"var(--bz-ink-2)" }}>{d.date}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={S.card}>
              <SectionLabel t="Activity by Day of Week" />
              <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:8 }}>
                {DAYS.map((day,i) => {
                  const count=stats.byDay[i]; const max=Math.max(...stats.byDay,1); const intensity=count/max;
                  const r=parseInt(bc.border.slice(1,3),16), g=parseInt(bc.border.slice(3,5),16), b2=parseInt(bc.border.slice(5,7),16);
                  return (
                    <div key={day} style={{ textAlign:"center", padding:"10px 4px", background:count===0?"#F0E0E8":`rgba(${r},${g},${b2},${0.2+intensity*0.8})`, borderRadius:8, border:"1px solid var(--bz-line-2)" }}>
                      <div style={{ fontSize:11, fontWeight:700, color:count>0?bc.text:"var(--bz-ink-2)" }}>{day}</div>
                      <div style={{ fontSize:16, fontWeight:900, color:count>0?bc.text:"var(--bz-ink-2)", marginTop:2 }}>{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {isAdmin && bi < visibleBreakers.length-1 && <div style={{ height:1, background:"var(--bz-s1)", margin:"8px 0 16px" }}/>}
          </div>
        );
      })}
      </>}
    </div>
  );
}

// --- WHATNOT FOLLOWER TRACKER ------------------------------------
const WN_CHANNELS = [
  { key:"bazookavault",   label:"Bazooka Vault",      url:"https://www.whatnot.com/user/bazookavault",   color:"#E8317A" },
  { key:"bazookabreaks",  label:"Bazooka Breaks",     url:"https://www.whatnot.com/user/bazookabreaks",  color:"#7B9CFF" },
  { key:"pullsandpars",   label:"Pulls & Pars",        url:"https://www.whatnot.com/user/pullsandpars",   color:"#60A5FA" },
  { key:"valleyhithouse", label:"Valley Hit House",    url:"https://www.whatnot.com/user/valleyhithouse", color:"#4ade80" },
];

function WhatnotFollowerTracker({ isAdmin }) {
  const STORE_KEY = "bz_wn_followers_v1";
  const [entries,    setEntries]    = useState(()=>{ try { return JSON.parse(localStorage.getItem(STORE_KEY)||"[]"); } catch(e){ return []; } });
  const [form,       setForm]       = useState({ date:todayLocal(), ...Object.fromEntries(WN_CHANNELS.map(c=>[c.key,""])) });
  const [adding,     setAdding]     = useState(false);
  const [saved,      setSaved]      = useState(false);
  const [activeView, setActiveView] = useState("chart"); // chart | table

  // Vault member (registered user) counts
  const [userStats, setUserStats] = useState(null); // { total, newWeek, newMonth }
  useEffect(()=>{
    getDocs(collection(db,"users")).then(snap=>{
      const now = Date.now();
      const WEEK = 7*24*60*60*1000, MONTH = 30*24*60*60*1000;
      let newWeek=0, newMonth=0;
      snap.docs.forEach(d=>{
        const fs = d.data().firstSeen;
        if (typeof fs==="number") { if (now-fs<=WEEK) newWeek++; if (now-fs<=MONTH) newMonth++; }
      });
      setUserStats({ total:snap.size, newWeek, newMonth });
    }).catch(()=>{});
  },[]);

  // Persist to localStorage + Firestore
  async function saveEntry() {
    const hasAny = WN_CHANNELS.some(c => form[c.key] !== "");
    if (!form.date || !hasAny) return;
    const entry = { id:uid(), date:form.date, ...Object.fromEntries(WN_CHANNELS.map(c=>[c.key, parseInt(form[c.key])||null])) };
    const next = [...entries.filter(e=>e.date!==form.date), entry].sort((a,b)=>a.date.localeCompare(b.date));
    setEntries(next);
    try { localStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch(e){}
    try { await setDoc(doc(db,"follower_snapshots",entry.id), entry); } catch(e){}
    setAdding(false);
    setSaved(true);
    setTimeout(()=>setSaved(false), 2500);
    setForm({ date:todayLocal(), ...Object.fromEntries(WN_CHANNELS.map(c=>[c.key,""])) });
  }

  async function deleteEntry(id) {
    const next = entries.filter(e=>e.id!==id);
    setEntries(next);
    try { localStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch(e){}
    try { await deleteDoc(doc(db,"follower_snapshots",id)); } catch(e){}
  }

  // Load from Firestore on mount
  useEffect(()=>{
    getDocs(query(collection(db,"follower_snapshots"), orderBy("date","asc"))).then(snap=>{
      if (snap.docs.length > 0) {
        const data = snap.docs.map(d=>({...d.data(),id:d.id}));
        setEntries(data);
        try { localStorage.setItem(STORE_KEY, JSON.stringify(data)); } catch(e){}
      }
    }).catch(()=>{});
  },[]);

  // Build chart data
  const chartW = 600, chartH = 180, padL = 50, padR = 20, padT = 16, padB = 32;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;

  const allVals = entries.flatMap(e => WN_CHANNELS.map(c=>e[c.key]).filter(Boolean));
  const maxVal = allVals.length ? Math.max(...allVals) : 1000;
  const minVal = allVals.length ? Math.max(0, Math.min(...allVals) - Math.round(Math.max(...allVals)*0.05)) : 0;
  const range  = maxVal - minVal || 1;

  function px(i)   { return padL + (i/(Math.max(entries.length-1,1))) * innerW; }
  function py(val) { return padT + innerH - ((val-minVal)/range)*innerH; }

  function fmtK(n) { return n>=1000 ? `${(n/1000).toFixed(1)}k` : String(n); }

  // Week-over-week delta for latest entry
  const latest = entries[entries.length-1];
  const prev   = entries[entries.length-2];

  // Auto-prompt if it's been 2+ days since last snapshot
  const daysSinceLast = latest ? (Date.now() - new Date(latest.date+"T12:00:00").getTime()) / (1000*60*60*24) : 999;
  const needsUpdate = daysSinceLast >= 2;

  return (
    <div style={{ marginTop:24 }}>
      {/* Vault Members — registered user count */}
      <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:14, padding:"18px 20px", marginBottom:16 }}>
        <div style={{ fontSize:14, fontWeight:800, color:"var(--bz-ink)", marginBottom:14 }}>👥 Vault Members</div>
        {userStats ? (
          <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
            <div style={{ flex:"1 1 120px", background:"rgba(232,49,122,0.08)", border:"1px solid rgba(232,49,122,0.25)", borderRadius:10, padding:"14px 16px" }}>
              <div style={{ fontSize:28, fontWeight:900, color:"#E8317A", lineHeight:1 }}>{userStats.total.toLocaleString()}</div>
              <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginTop:6, fontWeight:600 }}>Total accounts</div>
            </div>
            <div style={{ flex:"1 1 120px", background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.25)", borderRadius:10, padding:"14px 16px" }}>
              <div style={{ fontSize:28, fontWeight:900, color:"#4ade80", lineHeight:1 }}>+{userStats.newWeek.toLocaleString()}</div>
              <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginTop:6, fontWeight:600 }}>New this week</div>
            </div>
            <div style={{ flex:"1 1 120px", background:"rgba(123,156,255,0.08)", border:"1px solid rgba(123,156,255,0.25)", borderRadius:10, padding:"14px 16px" }}>
              <div style={{ fontSize:28, fontWeight:900, color:"#7B9CFF", lineHeight:1 }}>+{userStats.newMonth.toLocaleString()}</div>
              <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginTop:6, fontWeight:600 }}>New this month</div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize:12, color:"var(--bz-ink-3)" }}>Loading member counts…</div>
        )}
        <div style={{ fontSize:10, color:"#8a8a8a", marginTop:12 }}>Counts everyone who has signed in. "New" is based on first sign-in date (tracked going forward).</div>
      </div>

      <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:14, padding:"18px 20px" }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:8 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:800, color:"var(--bz-ink)" }}>📡 Whatnot Follower Tracker</div>
            <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>
              {entries.length} snapshot{entries.length!==1?"s":""} · {needsUpdate ? <span style={{color:"#FBBF24",fontWeight:700}}>⏰ Due for update — {Math.floor(daysSinceLast)} days since last log</span> : `last logged ${Math.floor(daysSinceLast)} day${Math.floor(daysSinceLast)===1?"":"s"} ago`}
            </div>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {["chart","table"].map(v=>(
              <button key={v} onClick={()=>setActiveView(v)}
                style={{ background:activeView===v?"rgba(232,49,122,0.15)":"transparent", color:activeView===v?"#E8317A":"#555", border:`1px solid ${activeView===v?"#E8317A":"#333"}`, borderRadius:20, padding:"4px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                {v==="chart"?"📈 Chart":"📋 Table"}
              </button>
            ))}
            {isAdmin && (
              <button onClick={()=>setAdding(p=>!p)}
                style={{ background:needsUpdate?"rgba(251,191,36,0.15)":adding?"transparent":"rgba(232,49,122,0.12)", color:needsUpdate?"#FBBF24":"#E8317A", border:`1.5px solid ${needsUpdate?"#FBBF2444":"#E8317A44"}`, borderRadius:20, padding:"5px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                {adding ? "Cancel" : saved ? "✅ Saved!" : needsUpdate ? "⏰ Log Counts Now" : "+ Log Counts"}
              </button>
            )}
          </div>
        </div>

        {/* Auto-prompt banner */}
        {needsUpdate && isAdmin && !adding && (
          <div onClick={()=>setAdding(true)} style={{ background:"rgba(251,191,36,0.08)", border:"1px solid rgba(251,191,36,0.25)", borderRadius:8, padding:"10px 14px", marginBottom:14, display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer" }}>
            <div style={{ fontSize:12, color:"#FBBF24" }}>📊 It's been {Math.floor(daysSinceLast)} days — time to log new follower counts to keep your growth chart accurate</div>
            <div style={{ fontSize:11, fontWeight:700, color:"#FBBF24", whiteSpace:"nowrap", marginLeft:12 }}>Log Now →</div>
          </div>
        )}

        {/* Log form */}
        {adding && isAdmin && (
          <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"14px 16px", marginBottom:16 }}>
            <div style={{ display:"grid", gridTemplateColumns:`auto repeat(${WN_CHANNELS.length},1fr) auto`, gap:10, alignItems:"end" }}>
              <div>
                <label style={{ fontSize:10, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:4 }}>Date</label>
                <input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))}
                  style={{ background:"var(--bz-s3)", border:"1px solid var(--bz-line)", borderRadius:8, color:"var(--bz-ink)", padding:"8px 10px", fontSize:12, fontFamily:"inherit", outline:"none" }}/>
              </div>
              {WN_CHANNELS.map(c=>(
                <div key={c.key}>
                  <label style={{ fontSize:10, fontWeight:700, color:c.color, textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:4 }}>{c.label}</label>
                  <input type="number" value={form[c.key]} onChange={e=>setForm(p=>({...p,[c.key]:e.target.value}))}
                    placeholder="e.g. 12400"
                    style={{ background:"var(--bz-s1)", border:`1px solid ${c.color}44`, borderRadius:7, color:"var(--bz-ink)", padding:"8px 10px", fontSize:12, fontFamily:"inherit", outline:"none", width:"100%", boxSizing:"border-box" }}/>
                </div>
              ))}
              <button onClick={saveEntry}
                style={{ background:"#166534", color:"#fff", border:"none", borderRadius:8, padding:"8px 18px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                💾 Save
              </button>
            </div>
          </div>
        )}

        {entries.length === 0 ? (
          <div style={{ textAlign:"center", padding:"40px 0", color:"var(--bz-ink-3)" }}>
            <div style={{ fontSize:32, marginBottom:8 }}>📡</div>
            <div style={{ fontSize:13, fontWeight:700, color:"#8a8a8a" }}>No snapshots yet</div>
            <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:4 }}>Log your first follower counts to start tracking growth</div>
          </div>
        ) : <>

          {/* Current counts + deltas */}
          <div style={{ display:"grid", gridTemplateColumns:`repeat(${WN_CHANNELS.length},1fr)`, gap:12, marginBottom:16 }}>
            {WN_CHANNELS.map(c=>{
              const cur  = latest?.[c.key];
              const prv  = prev?.[c.key];
              const delta = cur!=null && prv!=null ? cur-prv : null;
              const firstEntry = entries.find(e=>e[c.key]!=null)?.[c.key];
              const totalGrowth = cur!=null && firstEntry!=null ? cur-firstEntry : null;
              return (
                <div key={c.key} style={{ background:`${c.color}0d`, border:`1px solid ${c.color}22`, borderRadius:10, padding:"12px 14px" }}>
                  <div style={{ fontSize:10, fontWeight:700, color:c.color, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>{c.label}</div>
                  <div style={{ fontSize:24, fontWeight:900, color:"var(--bz-ink)" }}>{cur!=null ? fmtK(cur) : "—"}</div>
                  {delta!=null && (
                    <div style={{ fontSize:11, fontWeight:700, color:delta>0?"#4ade80":delta<0?"#ef4444":"#555", marginTop:4 }}>
                      {delta>0?"+":""}{delta.toLocaleString()} since last log
                    </div>
                  )}
                  {totalGrowth!=null && entries.length>1 && (
                    <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>
                      +{totalGrowth.toLocaleString()} total · {entries.length} snapshots
                    </div>
                  )}
                  <a href={c.url} target="_blank" rel="noreferrer" style={{ fontSize:10, color:c.color, opacity:0.6, textDecoration:"none", marginTop:4, display:"block" }}>
                    View on Whatnot ↗
                  </a>
                </div>
              );
            })}
          </div>

          {/* Chart */}
          {activeView==="chart" && entries.length >= 1 && (
            <div style={{ overflowX:"auto" }}>
              <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width:"100%", maxWidth:chartW, display:"block" }}>
                {/* Grid lines */}
                {[0,0.25,0.5,0.75,1].map(t=>{
                  const y = padT + innerH*t;
                  const val = Math.round(maxVal - t*range);
                  return (
                    <g key={t}>
                      <line x1={padL} y1={y} x2={chartW-padR} y2={y} stroke="var(--bz-s2)" strokeWidth={1}/>
                      <text x={padL-6} y={y+4} textAnchor="end" fontSize={9} fill="#444">{fmtK(val)}</text>
                    </g>
                  );
                })}
                {/* X axis labels */}
                {entries.map((e,i)=>{
                  if (entries.length>8 && i%Math.ceil(entries.length/6)!==0 && i!==entries.length-1) return null;
                  return <text key={e.id} x={px(i)} y={chartH-4} textAnchor="middle" fontSize={9} fill="#444">{e.date.slice(5)}</text>;
                })}
                {/* Lines + dots per channel */}
                {WN_CHANNELS.map(c=>{
                  const pts = entries.map((e,i)=>e[c.key]!=null ? {x:px(i),y:py(e[c.key]),val:e[c.key],date:e.date} : null).filter(Boolean);
                  if (pts.length < 2) return null;
                  const d = pts.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
                  return (
                    <g key={c.key}>
                      <path d={d} fill="none" stroke={c.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" opacity={0.8}/>
                      {pts.map((p,i)=>(
                        <g key={i}>
                          <circle cx={p.x} cy={p.y} r={3} fill={c.color} opacity={0.9}/>
                          {i===pts.length-1&&(
                            <text x={p.x+5} y={p.y+4} fontSize={9} fill={c.color} fontWeight="bold">{fmtK(p.val)}</text>
                          )}
                        </g>
                      ))}
                    </g>
                  );
                })}
              </svg>
              {/* Legend */}
              <div style={{ display:"flex", gap:16, justifyContent:"center", marginTop:8 }}>
                {WN_CHANNELS.map(c=>(
                  <div key={c.key} style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <div style={{ width:12, height:3, background:c.color, borderRadius:2 }}/>
                    <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {activeView==="chart" && entries.length < 1 && (
            <div style={{ textAlign:"center", color:"var(--bz-ink-3)", fontSize:12, padding:"20px 0" }}>Log at least 1 snapshot to see the chart</div>
          )}

          {/* Table */}
          {activeView==="table" && (
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ borderBottom:"1px solid var(--bz-line)" }}>
                  <th style={{ textAlign:"left", padding:"6px 10px", fontSize:10, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Date</th>
                  {WN_CHANNELS.map(c=><th key={c.key} style={{ textAlign:"right", padding:"6px 10px", fontSize:10, fontWeight:700, color:c.color, textTransform:"uppercase", letterSpacing:1 }}>{c.label}</th>)}
                  {isAdmin && <th/>}
                </tr>
              </thead>
              <tbody>
                {[...entries].reverse().map((e,i)=>{
                  const prevE = [...entries].reverse()[i+1];
                  return (
                    <tr key={e.id} style={{ borderBottom:"1px solid #111", background:i%2===0?"var(--bz-s1)":"var(--bz-s1)" }}>
                      <td style={{ padding:"8px 10px", fontSize:12, color:"var(--bz-ink-2)" }}>{e.date}</td>
                      {WN_CHANNELS.map(c=>{
                        const cur = e[c.key]; const prv = prevE?.[c.key];
                        const delta = cur!=null&&prv!=null ? cur-prv : null;
                        return (
                          <td key={c.key} style={{ padding:"8px 10px", textAlign:"right" }}>
                            <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)" }}>{cur!=null?fmtK(cur):"—"}</div>
                            {delta!=null && <div style={{ fontSize:10, color:delta>0?"#4ade80":delta<0?"#ef4444":"#555" }}>{delta>0?"+":""}{delta.toLocaleString()}</div>}
                          </td>
                        );
                      })}
                      {isAdmin && (
                        <td style={{ padding:"8px 10px", textAlign:"center" }}>
                          <button onClick={()=>{ if(window.confirm("Delete this snapshot?")) deleteEntry(e.id); }}
                            style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:13 }}>🗑</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>}
      </div>
    </div>
  );
}

// --- PRODUCT INVENTORY -------------------------------------------
function ProductInventory({ shipments=[], productUsage=[], onSaveShipment, onDeleteShipment, onDeleteProductUsage, user, userRole, skuPrices={}, onSaveSkuPrices, streams=[], skuPriceHistory=[] }) {
  const canEdit = ["Admin"].includes(userRole?.role);
  const EMPTY   = { date:todayLocal(), productType:"Hobby", qty:"", notes:"" };
  const [form,          setForm]          = useState(EMPTY);
  const [adding,        setAdding]        = useState(false);
  const [editId,        setEditId]        = useState(null);
  const [skuForm,       setSkuForm]       = useState({});
  const [skuEditing,    setSkuEditing]    = useState(false);
  const [supplyEditing, setSupplyEditing] = useState(false);
  const [supplyForm,    setSupplyForm]    = useState({});

  const SUPPLY_ITEMS = [
    { key:"magpros",     label:"MagPros"              },
    { key:"packaging",   label:"Packaging Materials"  },
    { key:"topLoaders",  label:"Top Loaders"          },
  ];

  useEffect(() => {
    setSkuForm(skuPrices);
    setSupplyForm(skuPrices); // supply costs stored alongside sku prices
  }, [JSON.stringify(skuPrices)]);

  // Stock = total received - total used per product type
  const stock = PRODUCT_TYPES.reduce((acc, pt) => {
    const received = shipments.reduce((s, sh) => s + (sh.productType===pt ? (parseInt(sh.qty)||0) : 0), 0);
    const used     = productUsage.reduce((s, u) => s + (parseInt(u[pt])||0), 0);
    acc[pt] = { received, used, current: received - used };
    return acc;
  }, {});

  function openAdd()    { setForm({...EMPTY}); setAdding(true); setEditId(null); }
  function openEdit(s)  { setForm({date:s.date, productType:s.productType, qty:String(s.qty), notes:s.notes||""}); setEditId(s.id); setAdding(true); }
  function cancelForm() { setAdding(false); setEditId(null); setForm(EMPTY); }

  async function handleSave() {
    if (!form.productType || !form.qty || !form.date) return;
    await onSaveShipment({ ...(editId ? shipments.find(s=>s.id===editId)||{} : {}), ...form, qty:parseInt(form.qty)||0, id:editId||uid() });
    cancelForm();
  }

  const PT_COLORS = {
    "Double Mega":   { bg:"#222222", text:"#FFFFFF", border:"#444444" },
    "Hobby":         { bg:"#222222", text:"#FFFFFF", border:"#444444" },
    "Jumbo":         { bg:"#222222", text:"#FFFFFF", border:"#444444" },
    "Miscellaneous": { bg:"#222222", text:"#FFFFFF", border:"#444444" },
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* SKU Price History Chart */}
      {(() => {
        // Build price history: global saves + per-stream overrides merged and sorted by date
        const history = {};
        PRODUCT_TYPES.forEach(pt => history[pt] = []);

        // 1. Global SKU price saves (from sku_price_history collection)
        skuPriceHistory.forEach(h => {
          PRODUCT_TYPES.forEach(pt => {
            const price = parseFloat(h.prices?.[pt]);
            if (price > 0) history[pt].push({ date: h.date, price, source: "global" });
          });
        });

        // 2. Per-stream overrides (deduplicate by date, override wins)
        [...streams]
          .filter(s => s.date && s.streamSkuPrices && Object.keys(s.streamSkuPrices).length > 0)
          .sort((a,b) => new Date(a.date)-new Date(b.date))
          .forEach(s => {
            PRODUCT_TYPES.forEach(pt => {
              const price = parseFloat(s.streamSkuPrices[pt]);
              if (price > 0) {
                // Only add if no global entry on same date
                if (!history[pt].some(e => e.date === s.date && e.source === "global")) {
                  history[pt].push({ date: s.date, price, source: "stream" });
                }
              }
            });
          });

        // Sort each product's history by date and deduplicate same-day entries (keep last)
        PRODUCT_TYPES.forEach(pt => {
          const byDate = {};
          history[pt].forEach(e => { byDate[e.date] = e; });
          history[pt] = Object.values(byDate).sort((a,b) => a.date.localeCompare(b.date));
        });

        const hasData = PRODUCT_TYPES.some(pt => history[pt].length >= 1);
        if (!hasData) return (
          <div style={{ ...S.card, border:"1px solid var(--bz-line-2)", textAlign:"center", color:"var(--bz-ink-3)", fontSize:12, padding:"20px" }}>
            {"\uD83D\uDCC8 SKU price history will appear here once SKU prices have been saved"}</div>
        );
        const COLORS = { "Double Mega":"#E8317A", "Hobby":"#7B9CFF", "Jumbo":"#4ade80", "Miscellaneous":"#FBBF24" };
        const [activePt, setActivePt] = useState(PRODUCT_TYPES.find(pt => history[pt].length >= 1) || PRODUCT_TYPES[0]);
        const pts = history[activePt] || [];
        const color = COLORS[activePt] || "#E8317A";
        const minP = Math.min(...pts.map(p=>p.price));
        const maxP = Math.max(...pts.map(p=>p.price));
        const range = maxP - minP || 1;
        const W = 600, H = 200, PAD = { t:24, r:20, b:40, l:60 };
        const chartW = W - PAD.l - PAD.r;
        const chartH = H - PAD.t - PAD.b;
        const points = pts.map((p, i) => ({
          x: PAD.l + (pts.length > 1 ? (i / (pts.length-1)) * chartW : chartW/2),
          y: PAD.t + chartH - ((p.price - minP) / range) * chartH,
          price: p.price, date: p.date, source: p.source
        }));
        const pathD = points.map((p,i) => `${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
        const areaD = points.length > 0 ? `${pathD} L${points[points.length-1].x},${PAD.t+chartH} L${points[0].x},${PAD.t+chartH} Z` : "";
        return (
          <div style={{ ...S.card, border:"1px solid var(--bz-line-2)" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
              <SectionLabel t="📈 SKU Price History" />
              <div style={{ display:"flex", gap:6 }}>
                {PRODUCT_TYPES.filter(pt => history[pt].length > 0).map(pt => (
                  <button key={pt} onClick={()=>setActivePt(pt)} style={{ background:activePt===pt?COLORS[pt]+"22":"transparent", color:activePt===pt?COLORS[pt]:"#555", border:`1.5px solid ${activePt===pt?COLORS[pt]:"#333"}`, borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{pt}</button>
                ))}
              </div>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto" }}>
              {[0,0.25,0.5,0.75,1].map(t => {
                const y = PAD.t + chartH * (1-t);
                const val = minP + range * t;
                return <g key={t}>
                  <line x1={PAD.l} y1={y} x2={W-PAD.r} y2={y} stroke="var(--bz-s2)" strokeWidth="1"/>
                  <text x={PAD.l-6} y={y+4} textAnchor="end" fill="#555" fontSize="10">${val.toFixed(0)}</text>
                </g>;
              })}
              {areaD && <path d={areaD} fill={color} fillOpacity="0.08"/>}
              {pathD && <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>}
              {points.map((p,i) => (
                <g key={i}>
                  <circle cx={p.x} cy={p.y} r="4" fill={p.source==="stream"?color+"88":color} stroke="var(--bz-s1)" strokeWidth="1.5"/>
                  <text x={p.x} y={p.y-10} textAnchor="middle" fill={color} fontSize="10" fontWeight="700">${p.price.toFixed(0)}</text>
                </g>
              ))}
              {points.map((p,i) => (
                <text key={i} x={p.x} y={H-6} textAnchor="middle" fill="#555" fontSize="9">{p.date.slice(5)}</text>
              ))}
              <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t+chartH} stroke="#333" strokeWidth="1"/>
              <line x1={PAD.l} y1={PAD.t+chartH} x2={W-PAD.r} y2={PAD.t+chartH} stroke="#333" strokeWidth="1"/>
            </svg>
            {pts.length > 1 && (() => {
              const first = pts[0].price, last = pts[pts.length-1].price;
              const diff = last - first, pct = ((diff/first)*100).toFixed(1);
              return <div style={{ fontSize:11, color:diff>=0?"#4ade80":"#E8317A", textAlign:"right", marginTop:4 }}>
                {diff>=0?"\u2191":"\u2193"} ${Math.abs(diff).toFixed(0)} ({Math.abs(pct)}%) since {pts[0].date}
              </div>;
            })()}
          </div>
        );
      })()}

      {/* SKU Pricing -- Admin only */}
      {canEdit && (
        <div style={{ ...S.card, border:"2px solid var(--bz-line-2)333" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: skuEditing ? 14 : 0 }}>
            <SectionLabel t="SKU Market Values" />
            <button onClick={()=>setSkuEditing(p=>!p)} style={{ background:"transparent", border:"1.5px solid #E8317A", color:"#E8317A", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              {skuEditing ? "Cancel" : "\u270F\uFE0F Edit"}
            </button>
          </div>
          {skuEditing && (
            <>
              <div style={{ fontSize:12, color:"var(--bz-ink-2)", marginBottom:16 }}>Set the retail/market value per unit for each product. Used to auto-calculate market multiple in Stream Recap.</div>
              {Object.entries(PRODUCT_SETS).map(([set,types])=>(
                <div key={set} style={{ marginBottom:16 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#E8317A", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>{set}</div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:10 }}>
                    {types.map(t => { const key=`${set} - ${t}`; return (
                      <div key={key}>
                        <label style={{ ...S.lbl }}>{t} ($)</label>
                        <input type="number" step="0.01" min="0" value={skuForm[key]||""} onChange={e=>setSkuForm(p=>({...p,[key]:e.target.value}))} placeholder="0.00" style={S.inp}/>
                      </div>
                    );})}
                  </div>
                </div>
              ))}
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Other</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:10 }}>
                  <div>
                    <label style={S.lbl}>Miscellaneous ($)</label>
                    <input type="number" step="0.01" min="0" value={skuForm["Miscellaneous"]||""} onChange={e=>setSkuForm(p=>({...p,Miscellaneous:e.target.value}))} placeholder="0.00" style={S.inp}/>
                  </div>
                </div>
              </div>
              <Btn onClick={async()=>{ await onSaveSkuPrices(skuForm); setSkuEditing(false); }} variant="green">{"\uD83D\uDCBE Save SKU Prices"}</Btn>
            </>
          )}
        </div>
      )}

      {/* Supply Cost Config -- Admin only */}
      {canEdit && (
        <div style={{ ...S.card, border:"2px solid #6B2D8B22" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: supplyEditing ? 14 : 0 }}>
            <SectionLabel t="Supply Cost Per Unit" />
            <div style={{ display:"flex", gap:10, alignItems:"center" }}>
              {!supplyEditing && SUPPLY_ITEMS.map(({ key, label }) => skuPrices[`supply_${key}`] ? (
                <span key={key} style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{label}: <strong style={{color:"var(--bz-ink)"}}>${parseFloat(skuPrices[`supply_${key}`]).toFixed(3)}</strong></span>
              ) : null)}
              <button onClick={()=>setSupplyEditing(p=>!p)} style={{ background:"transparent", border:"1.5px solid #6B2D8B", color:"#E8317A", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                {supplyEditing ? "Cancel" : "\u270F\uFE0F Edit"}
              </button>
            </div>
          </div>
          {supplyEditing && (
            <>
              <div style={{ fontSize:12, color:"var(--bz-ink-2)", marginBottom:12 }}>Set cost per unit for supplies. In Stream Recap, enter quantities and costs auto-calculate.</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:14 }}>
                {SUPPLY_ITEMS.map(({ key, label }) => (
                  <div key={key}>
                    <label style={{ ...S.lbl, color:"#E8317A" }}>{label} ($ per unit)</label>
                    <input
                      type="number" step="0.001" min="0"
                      value={supplyForm[`supply_${key}`]||""}
                      onChange={e=>setSupplyForm(p=>({...p,[`supply_${key}`]:e.target.value}))}
                      placeholder="e.g. 0.25"
                      style={{ ...S.inp, color:"#E8317A" }}
                    />
                  </div>
                ))}
              </div>
              <Btn onClick={async()=>{ await onSaveSkuPrices({...skuPrices,...supplyForm}); setSupplyEditing(false); }} variant="green">{"\uD83D\uDCBE Save Supply Costs"}</Btn>
            </>
          )}
        </div>
      )}

      {/* Stock summary — compact grouped by set */}
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {Object.entries(PRODUCT_SETS).map(([set,types])=>{
          const SET_GRADIENTS = {
            "Alpha Edition":        "135deg, #1a0a2e, #2d1060",
            "Alpha Update":         "135deg, #0a1a2e, #10306e",
            "Alpha Blast":          "135deg, #1a0a0a, #6e1010",
            "Griffey 2026":         "135deg, #0a1a0a, #106e30",
            "Tecmo Bowl":           "135deg, #1a1a0a, #6e5510",
            "Wonders of The First": "135deg, #0a1a1a, #0e5a5a",
          };
          const SET_ACCENT = {
            "Alpha Edition":        "#A855F7",
            "Alpha Update":         "#60A5FA",
            "Alpha Blast":          "#EF4444",
            "Griffey 2026":         "#4ade80",
            "Tecmo Bowl":           "#FBBF24",
            "Wonders of The First": "#2DD4BF",
          };
          const gradient = SET_GRADIENTS[set] || "135deg, #111, #222";
          const accent   = SET_ACCENT[set]    || "#E8317A";
          const setTypes = types.map(t=>`${set} - ${t}`);
          return (
            <div key={set} style={{ background:`linear-gradient(${gradient})`, border:`1px solid ${accent}22`, borderRadius:12, overflow:"hidden" }}>
              <div style={{ padding:"8px 16px", borderBottom:`1px solid ${accent}22` }}>
                <span style={{ fontSize:11, fontWeight:700, color:accent, textTransform:"uppercase", letterSpacing:1 }}>{set}</span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:`repeat(${Math.min(types.length,4)},1fr)` }}>
                {setTypes.map((pt,i) => {
                  const s   = stock[pt] || { current:0, received:0, used:0 };
                  const out = s.current <= 0;
                  const low = s.current <= 2 && !out;
                  const shortName = pt.replace(`${set} - `,"");
                  return (
                    <div key={pt} style={{ padding:"14px 12px", borderRight: i < setTypes.length-1 ? `1px solid ${accent}15` : "none", textAlign:"center" }}>
                      <div style={{ fontSize:10, fontWeight:700, color:`${accent}99`, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>{shortName}</div>
                      <div style={{ fontSize:26, fontWeight:900, color: out?"#ef4444":low?"#FBBF24":"var(--bz-ink)", marginBottom:2 }}>{s.current}</div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,0.2)" }}>in stock</div>
                      <div style={{ display:"flex", justifyContent:"center", gap:8, marginTop:5 }}>
                        <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>↑{s.received}</span>
                        <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>↓{s.used}</span>
                      </div>
                      {skuPrices[pt] && <div style={{ marginTop:4, fontSize:10, color:accent, fontWeight:700 }}>${parseFloat(skuPrices[pt]).toFixed(2)}</div>}
                      {out && <div style={{ marginTop:5, fontSize:9, fontWeight:700, color:"#ef4444" }}>🚨 Out</div>}
                      {low && <div style={{ marginTop:5, fontSize:9, fontWeight:700, color:"#FBBF24" }}>⚠ Low</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {/* Miscellaneous */}
        {(() => { const s = stock["Miscellaneous"] || { current:0, received:0, used:0 };
          const out = s.current<=0, low = s.current<=2&&!out;
          return (
            <div style={{ background:"linear-gradient(135deg,var(--bz-s1),var(--bz-s2))", border:"1px solid var(--bz-line-2)", borderRadius:12, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:12, fontWeight:700, color:"var(--bz-ink-3)" }}>Miscellaneous</span>
              <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>↑{s.received} · ↓{s.used}</span>
                <span style={{ fontSize:22, fontWeight:900, color:out?"#ef4444":low?"#FBBF24":"var(--bz-ink)" }}>{s.current}</span>
                {skuPrices["Miscellaneous"]&&<span style={{ fontSize:10, color:"#E8317A", fontWeight:700 }}>${parseFloat(skuPrices["Miscellaneous"]).toFixed(2)}</span>}
                {out&&<span style={{ fontSize:10, color:"#ef4444", fontWeight:700 }}>🚨 Out</span>}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Add shipment */}
      {canEdit && (
        <>
          {!adding
            ? <Btn onClick={openAdd} variant="gold">+ Add Shipment</Btn>
            : (
              <div style={{ ...S.card, border:"2px solid var(--bz-line-2)333" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                  <SectionLabel t={editId ? "Edit Shipment" : "Add Shipment"} />
                  <button onClick={cancelForm} style={{ background:"none", border:"none", color:"var(--bz-ink-2)", cursor:"pointer", fontSize:18 }}>{"\u2715"}</button>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 2fr", gap:12, marginBottom:14 }}>
                  <div><label style={S.lbl}>Date</label><input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} style={S.inp}/></div>
                  <div>
                    <label style={S.lbl}>Product Type</label>
                    <select value={form.productType} onChange={e=>setForm(p=>({...p,productType:e.target.value}))} style={{ ...S.inp, cursor:"pointer" }}>
                      {PRODUCT_TYPES.map(pt=><option key={pt} value={pt}>{pt}</option>)}
                    </select>
                  </div>
                  <div><label style={S.lbl}>Qty Received</label><input type="number" min="1" value={form.qty} onChange={e=>setForm(p=>({...p,qty:e.target.value}))} placeholder="e.g. 12" style={S.inp}/></div>
                  <div><label style={S.lbl}>Notes (optional)</label><input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="e.g. Special edition, damaged box..." style={S.inp}/></div>
                </div>
                <div style={{ display:"flex", gap:10 }}>
                  <Btn onClick={handleSave} disabled={!form.productType||!form.qty||!form.date} variant="green">{"\uD83D\uDCBE"}{editId?"Update":"Save"} Shipment</Btn>
                  <Btn onClick={cancelForm} variant="ghost">Cancel</Btn>
                </div>
              </div>
            )
          }
        </>
      )}

      {/* Shipment history */}
      <div style={S.card}>
        <SectionLabel t="Shipment History" />
        {shipments.length === 0
          ? <div style={{ textAlign:"center", color:"var(--bz-ink-2)", padding:"30px 0" }}>No shipments yet -- add one above</div>
          : (
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>{["Date","Product","Qty","Notes",""].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {shipments.map((s,i) => {
                  const pc = PT_COLORS[s.productType] || { bg:"#F3F4F6", text:"var(--bz-ink-3)" };
                  return (
                    <tr key={s.id} style={{ background:"var(--bz-s1)" }}>
                      <td style={S.td}>{s.date}</td>
                      <td style={S.td}><span style={{ background:pc.bg, color:pc.text, borderRadius:5, padding:"2px 9px", fontSize:11, fontWeight:700 }}>{s.productType}</span></td>
                      <td style={{ ...S.td, fontWeight:700, color:"#E8317A", fontSize:15 }}>+{s.qty}</td>
                      <td style={{ ...S.td, color:"var(--bz-ink-2)" }}>{s.notes||"--"}</td>
                      <td style={S.td}>
                        {canEdit && (
                          <div style={{ display:"flex", gap:6 }}>
                            <button onClick={()=>openEdit(s)} style={{ background:"none", border:"1px solid var(--bz-line-2)", borderRadius:5, padding:"2px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit", color:"var(--bz-ink-2)" }}>{"\u270F\uFE0F"}</button>
                            <button onClick={()=>{ if(window.confirm("Delete this shipment?")) onDeleteShipment(s.id); }} style={{ background:"none", border:"1px solid #FCA5A5", borderRadius:5, padding:"2px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit", color:"#E8317A" }}>{"\uD83D\uDDD1"}</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        }
      </div>

      {/* Usage log */}
      <div style={S.card}>
        <SectionLabel t="Usage Log (from Streams)" />
        {productUsage.length === 0
          ? <div style={{ textAlign:"center", color:"var(--bz-ink-2)", padding:"20px 0" }}>No product usage logged yet</div>
          : <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {productUsage.map((u,i) => {
                const usedProducts = PRODUCT_TYPES.filter(pt => (parseInt(u[pt])||0) > 0);
                return (
                  <div key={u.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:8, gap:12, flexWrap:"wrap" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
                      <span style={{ fontSize:12, color:"var(--bz-ink-3)" }}>{u.date}</span>
                      <Badge bg={BC[u.breaker]?.bg||"#F3F4F6"} color={BC[u.breaker]?.text||"var(--bz-ink-3)"}>{u.breaker}</Badge>
                    </div>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap", flex:1 }}>
                      {usedProducts.length === 0
                        ? <span style={{ fontSize:12, color:"#8a8a8a" }}>No products logged</span>
                        : usedProducts.map(pt => (
                            <span key={pt} style={{ fontSize:12, color:"var(--bz-ink)", background:"rgba(153,27,27,0.15)", border:"1px solid rgba(153,27,27,0.3)", borderRadius:6, padding:"2px 10px", fontWeight:700 }}>
                              {pt.includes(" - ") ? pt.split(" - ").pop() : pt}: <span style={{color:"#E8317A"}}>−{u[pt]}</span>
                            </span>
                          ))
                      }
                    </div>
                    {canEdit && onDeleteProductUsage && (
                      <button onClick={()=>{ if(window.confirm("Delete this usage entry? Stock will be restored.")) onDeleteProductUsage(u.id); }}
                        style={{ background:"none", border:"1px solid #FCA5A5", color:"#E8317A", borderRadius:5, padding:"2px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit", flexShrink:0 }}>
                        {"\uD83D\uDDD1"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
        }
      </div>
    </div>
  );
}

// --- CUSTOMERS CRM ----------------------------------------------
function Sellers({ inventory, breaks, userRole }) {
  const canSeeFinancials = ["Admin"].includes(userRole?.role);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 250);
  const [sortBy, setSortBy] = useState("spent"); // spent | cards | lots | recent

  const usedIds = new Set(breaks.map(b => b.inventoryId));

  // Build seller profiles from inventory
  const sellerMap = {};
  inventory.forEach(c => {
    const name = c.seller?.trim() || "Unknown";
    if (!sellerMap[name]) {
      sellerMap[name] = {
        name,
        lots:    {},
        cards:   0,
        spent:   0,
        sources: {},
        payments:{},
        lastDate: null,
      };
    }
    const s = sellerMap[name];
    s.cards++;
    s.spent += c.costPerCard || 0;
    const lotKey = `${c.seller||"Unknown"}__${c.date||"Unknown"}`;
    if (!s.lots[lotKey]) s.lots[lotKey] = { key:lotKey, date:c.date||"--", cards:[], lotPaid:c.lotTotalPaid||0, source:c.source||"--", payment:c.payment||"--" };
    s.lots[lotKey].cards.push(c);
    if (c.source) s.sources[c.source] = (s.sources[c.source]||0) + 1;
    if (c.payment) s.payments[c.payment] = (s.payments[c.payment]||0) + 1;
    if (!s.lastDate || new Date(c.dateAdded) > new Date(s.lastDate)) s.lastDate = c.dateAdded;
  });

  const sellers = Object.values(sellerMap).map(s => ({
    ...s,
    lotCount:      Object.keys(s.lots).length,
    lotList:       Object.values(s.lots).sort((a,b) => new Date(b.date)-new Date(a.date)),
    topSource:     Object.entries(s.sources).sort((a,b)=>b[1]-a[1])[0]?.[0] || "--",
    topPayment:    Object.entries(s.payments).sort((a,b)=>b[1]-a[1])[0]?.[0] || "--",
  }));

  const filtered = sellers
    .filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b) => {
      if (sortBy==="cards")  return b.cards - a.cards;
      if (sortBy==="lots")   return b.lotCount - a.lotCount;
      if (sortBy==="recent") return new Date(b.lastDate||0) - new Date(a.lastDate||0);
      return b.spent - a.spent; // default: spent
    });

  const SOURCE_COLORS = { Discord:"#5865F2", Facebook:"#1877F2", Other:"var(--bz-ink-3)" };

  // -- SELLER DETAIL ----------------------------------------------
  if (selectedSeller) {
    const s = sellers.find(x => x.name === selectedSeller);
    if (!s) { setSelectedSeller(null); return null; }
    const totalCards = s.cards;
    const totalSpent = s.spent;
    const allCards   = s.lotList.flatMap(l=>l.cards);
    const usedCount  = allCards.filter(c=>usedIds.has(c.id)).length;
    const availCount = totalCards - usedCount;

    return (
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        {/* Back + header */}
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={()=>setSelectedSeller(null)} style={{ background:"var(--bz-s1)", border:"1.5px solid #E5E7EB", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", color:"var(--bz-ink-2)" }}>{"\u2190 Back"}</button>
          <div>
            <div style={{ fontSize:22, fontWeight:900, color:"var(--bz-ink)" }}>{s.name}</div>
            <div style={{ fontSize:12, color:"var(--bz-ink-2)", marginTop:2 }}>
              {s.topSource !== "--" && <span style={{ color:SOURCE_COLORS[s.topSource]||"var(--bz-ink-3)", fontWeight:700 }}>{s.topSource}</span>}
              {s.topSource !== "--" && " · "}
              Last purchase {s.lastDate ? new Date(s.lastDate).toLocaleDateString() : "--"}
            </div>
          </div>
        </div>

        {/* KPI cards */}
        <div style={{ display:"grid", gridTemplateColumns:`repeat(${canSeeFinancials?4:3},1fr)`, gap:12 }}>
          {[
            { l:"Total Lots",    v:s.lotCount,   c:"#1A1A2E" },
            { l:"Total Cards",   v:totalCards,   c:"var(--bz-ink)" },
            { l:"Available",     v:availCount,   c:"#166534" },
            ...(canSeeFinancials ? [{ l:"Total Spent", v:`$${totalSpent.toFixed(2)}`, c:"#6B2D8B" }] : []),
          ].map(({l,v,c}) => (
            <div key={l} className="stat-card" style={{ ...S.card, textAlign:"center" }}>
              <div style={{ fontSize:24, fontWeight:900, color:c, marginBottom:4 }}>{v}</div>
              <div style={{ fontSize:10, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1 }}>{l}</div>
            </div>
          ))}
        </div>

        {/* Details row */}
        <div style={S.card}>
          <SectionLabel t="Customer Details" />
          <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
            <span style={{ fontSize:13, color:"var(--bz-ink-2)" }}>Primary Source: <strong style={{color:"var(--bz-ink)"}}>{s.topSource}</strong></span>
            <span style={{ fontSize:13, color:"var(--bz-ink-2)" }}>Preferred Payment: <strong style={{color:"var(--bz-ink)"}}>{s.topPayment}</strong></span>
            <span style={{ fontSize:13, color:"var(--bz-ink-2)" }}>Cards Used: <strong style={{color:"#E8317A"}}>{usedCount}</strong></span>
          </div>
        </div>

        {/* Lot history */}
        <div style={S.card}>
          <SectionLabel t={`Purchase History (${s.lotCount} lot${s.lotCount!==1?"s":""})`} />
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {s.lotList.map((lot,i) => {
              const lotUsed  = lot.cards.filter(c=>usedIds.has(c.id)).length;
              const lotAvail = lot.cards.length - lotUsed;
              return (
                <div key={i} style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"14px 18px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <div>
                      <span style={{ fontWeight:700, fontSize:14, color:"var(--bz-ink)" }}>Lot #{s.lotCount - i}</span>
                      <span style={{ color:"var(--bz-ink-2)", fontSize:12, marginLeft:10 }}>{lot.date}</span>
                    </div>
                    <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                      <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>{lot.source}</span>
                      <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>{lot.payment}</span>
                      {canSeeFinancials && <span style={{ fontWeight:700, color:"#E8317A", fontSize:13 }}>${lot.lotPaid.toFixed(2)}</span>}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
                    <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Cards: <strong style={{color:"var(--bz-ink)"}}>{lot.cards.length}</strong></span>
                    <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Available: <strong style={{color:"#E8317A"}}>{lotAvail}</strong></span>
                    <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Used: <strong style={{color:"#E8317A"}}>{lotUsed}</strong></span>
                  </div>
                  {lot.cards.length > 0 && (
                    <div style={{ marginTop:8, display:"flex", gap:6, flexWrap:"wrap" }}>
                      {CARD_TYPES.map(ct => {
                        const count = lot.cards.filter(c=>c.cardType===ct).length;
                        if (!count) return null;
                        const cc = CC[ct];
                        return <span key={ct} style={{ background:cc.bg, color:cc.text, border:`1px solid ${cc.border}44`, borderRadius:5, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{ct}: {count}</span>;
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // -- SELLER LIST ------------------------------------------------
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <div style={S.card}>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search customers..." style={{ ...S.inp, flex:1, minWidth:180 }}/>
          <div style={{ display:"flex", gap:4 }}>
            {[["spent","\uD83D\uDCB0 Top Spend"],["cards","\uD83D\uDCE6 Most Cards"],["lots","\uD83D\uDDC2 Most Lots"],["recent","\uD83D\uDD50 Recent"]].map(([val,label]) => (
              canSeeFinancials || val !== "spent" ? (
                <button key={val} onClick={()=>setSortBy(val)} style={{ background:sortBy===val?"#1A1A2E":"transparent", color:sortBy===val?"#E8317A":"var(--bz-ink-2)", border:`1.5px solid ${sortBy===val?"#E8317A":"var(--bz-line)"}`, borderRadius:7, padding:"6px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>{label}</button>
              ) : null
            ))}
          </div>
          <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>{filtered.length} customers</span>
        </div>
      </div>

      {filtered.length === 0
        ? <div style={{ ...S.card, textAlign:"center", padding:"60px", color:"var(--bz-ink-2)" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>{"\uD83D\uDC65"}</div>
            <div>No customers yet -- start importing lots from Lot Comp</div>
          </div>
        : <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {filtered.map((s,i) => {
              const rank = i + 1;
              const srcColor = SOURCE_COLORS[s.topSource] || "var(--bz-ink-3)";
              return (
                <div
                  key={s.name}
                  onClick={() => setSelectedSeller(s.name)}
                  className="card-hover" style={{ ...S.card, cursor:"pointer", display:"flex", alignItems:"center", gap:16, transition:"box-shadow 0.15s" }}
                  className="inv-row fade-in"
                >
                  {/* Rank */}
                  <div style={{ width:32, height:32, borderRadius:"50%", background: rank<=3?"#1A1A2E":"#F3F4F6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:900, color:rank<=3?"#E8317A":"var(--bz-ink-2)", flexShrink:0 }}>
                    {rank}
                  </div>

                  {/* Name + meta */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:800, fontSize:15, color:"var(--bz-ink)", marginBottom:3 }}>{s.name}</div>
                    <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                      {s.topSource !== "--" && <span style={{ fontSize:11, color:srcColor, fontWeight:700 }}>{s.topSource}</span>}
                      {s.topPayment !== "--" && <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{s.topPayment}</span>}
                      <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>Last: {s.lastDate ? new Date(s.lastDate).toLocaleDateString() : "--"}</span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{ display:"flex", gap:20, alignItems:"center", flexShrink:0 }}>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:18, fontWeight:900, color:"var(--bz-ink)" }}>{s.lotCount}</div>
                      <div style={{ fontSize:9, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1 }}>Lots</div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:18, fontWeight:900, color:"var(--bz-ink)" }}>{s.cards}</div>
                      <div style={{ fontSize:9, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1 }}>Cards</div>
                    </div>
                    {canSeeFinancials && (
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontSize:18, fontWeight:900, color:"#E8317A" }}>${s.spent.toFixed(0)}</div>
                        <div style={{ fontSize:9, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1 }}>Spent</div>
                      </div>
                    )}
                    <div style={{ color:"var(--bz-ink-2)", fontSize:18 }}>{"\u203A"}</div>
                  </div>
                </div>
              );
            })}
          </div>
      }

      {/* SKU Market Price History */}
      {isAdmin && (() => {
        const snapStreams = [...streams].filter(s => s.date && s.marketSnapshot).sort((a,b) => new Date(a.date)-new Date(b.date));
        if (snapStreams.length < 2) return null;
        return (
          <div style={{ ...S.card, marginTop:14 }}>
            <SectionLabel t="📈 SKU Market Price History" />
            <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:10 }}>Price per box at time of each stream -- frozen at save</div>
            <div style={{ position:"relative", height:260 }}>
              <canvas id="skuPriceChart" style={{ width:"100%", height:"100%" }}/>
            </div>
            <SkuPriceChart streams={snapStreams}/>
          </div>
        );
      })()}
    </div>
  );
}

function SkuPriceChart({ streams }) {
  const [loaded, setLoaded] = useState(!!window.Chart);
  useEffect(() => {
    if (window.Chart) { setLoaded(true); return; }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
    s.onload = () => setLoaded(true);
    document.head.appendChild(s);
  }, []);
  useEffect(() => {
    if (!loaded || !window.Chart) return;
    const el = document.getElementById("skuPriceChart");
    if (!el) return;
    if (window._skuChart) window._skuChart.destroy();
    const PT_COLORS = { "Double Mega":"#C2410C", "Hobby":"#7B9CFF", "Jumbo":"#4ade80", "Miscellaneous":"#FBBF24" };
    const labels = streams.map(s => s.date);
    const datasets = ["Double Mega","Hobby","Jumbo","Miscellaneous"].map(pt => ({
      label: pt,
      data: streams.map(s => parseFloat(s.marketSnapshot[pt])||null),
      borderColor: PT_COLORS[pt],
      backgroundColor: PT_COLORS[pt]+"33",
      tension: 0.3, pointRadius: 4, pointHoverRadius: 6, spanGaps: true,
    }));
    window._skuChart = new window.Chart(el, {
      type:"line", data:{ labels, datasets },
      options:{
        responsive:true, maintainAspectRatio:false,
        plugins:{ legend:{ labels:{ color:"var(--bz-ink-2)", font:{ size:11 }}}, tooltip:{ callbacks:{ label: c => c.dataset.label+": $"+(c.parsed.y?.toFixed(2)||"--") }}},
        scales:{
          x:{ grid:{ color:"var(--bz-s2)" }, ticks:{ color:"var(--bz-ink-2)", font:{ size:10 }, maxTicksLimit:12 }},
          y:{ grid:{ color:"var(--bz-s2)" }, ticks:{ color:"var(--bz-ink-2)", font:{ size:11 }, callback: v => "$"+v }, beginAtZero:false }
        }
      }
    });
  }, [loaded, streams.length]);
  return null;
}

// --- STREAMS (wrapper: recap + cards + commission) ---------------
// --- BREAK PLANNER -------------------------------------------
function BreakPlanner({ skuPrices={}, userRole }) {
  const isAdmin = ["Admin"].includes(userRole?.role);
  const EMPTY_PRODUCT = { type:"", qty:"1" };
  const [products,    setProducts]    = useState([{ ...EMPTY_PRODUCT, id:uid() }]);
  const [targetPct,   setTargetPct]   = useState("60");
  const [spots,       setSpots]       = useState("30");
  const [breaker,     setBreaker]     = useState(BREAKERS[0]);
  const [whatnotPct,  setWhatnotPct]  = useState("8");  // Whatnot fee %
  const [couponAmt,   setCouponAmt]   = useState("0");

  function addProduct() { setProducts(p=>[...p, { ...EMPTY_PRODUCT, id:uid() }]); }
  function removeProduct(id) { setProducts(p=>p.filter(x=>x.id!==id)); }
  function updateProduct(id, field, val) { setProducts(p=>p.map(x=>x.id===id?{...x,[field]:val}:x)); }

  // Calculate total market value from selected products
  const totalMktVal = products.reduce((sum, p) => {
    const price = parseFloat(skuPrices[p.type]) || 0;
    const qty   = parseInt(p.qty) || 0;
    return sum + price * qty;
  }, 0);

  const targetMultiple = totalMktVal > 0 ? parseFloat(targetPct)/100 : 0;
  const targetGross    = totalMktVal * targetMultiple;
  const numSpots       = parseInt(spots) || 1;
  const spotPrice      = targetGross / numSpots;

  // Financial projections
  const whatnotFee  = targetGross * (parseFloat(whatnotPct)||0) / 100;
  const coupons     = parseFloat(couponAmt) || 0;
  const streamExp   = coupons;
  const reimbExp    = 0; // no reimbursable expenses in planner (coupons not reimbursed)
  const netRev      = targetGross - whatnotFee - streamExp;
  const bazNet      = netRev * 0.30;
  const grossForComm = targetGross - streamExp;
  const bazNetForComm = grossForComm * 0.30;
  const repExp      = streamExp * 0.135;
  const mm          = targetGross > 0 ? targetGross / totalMktVal : 0;
  const rate        = mm>=1.8?0.55:mm>=1.7?0.50:mm>=1.6?0.45:mm>=1.5?0.40:0.35;
  const commBase    = bazNet;
  const commAmt     = commBase * rate;
  const bazTrueNet  = bazNet - commAmt;

  // Zone based on market multiple (targetGross / totalMktVal)
  // 1.5x+ = green, 1.3-1.5x = yellow, <1.3x = red
  const zone = targetMultiple >= 1.5 ? { c:"#4ade80", bg:"#0a1a0a", l:"\uD83D\uDFE2 Green · "+targetMultiple.toFixed(2)+"x" }
             : targetMultiple >= 1.3 ? { c:"#FBBF24", bg:"#1a1400", l:"\uD83D\uDFE1 Yellow · "+targetMultiple.toFixed(2)+"x" }
             : { c:"#E8317A", bg:"#1a0a0a", l:"\uD83D\uDD34 Red · "+targetMultiple.toFixed(2)+"x" };

  const breakEvenGross  = totalMktVal * 1.0;
  const breakEvenSpot   = breakEvenGross / numSpots;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* Inputs */}
      <div style={S.card}>
        <SectionLabel t="🧮 Break Planner" />
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

          {/* Products */}
          <div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
              <label style={S.lbl}>Products to Rip</label>
              <button onClick={addProduct} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"#E8317A", borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>+ Add Product</button>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {products.map(p => {
                const price = parseFloat(skuPrices[p.type]) || 0;
                const subtotal = price * (parseInt(p.qty)||0);
                return (
                  <div key={p.id} style={{ display:"grid", gridTemplateColumns:"1fr 80px auto auto", gap:8, alignItems:"center" }}>
                    <select value={p.type} onChange={e=>updateProduct(p.id,"type",e.target.value)} style={{ ...S.inp, cursor:"pointer" }}>
                      <option value="">-- Select Product --</option>
                      {Object.entries(PRODUCT_SETS).map(([set,types])=>(
                        <optgroup key={set} label={set}>
                          {types.map(t=>{const key=`${set} - ${t}`;return<option key={key} value={key}>{t}{skuPrices[key]?` ($${parseFloat(skuPrices[key]).toFixed(2)}/box)`:" (no price set)"}</option>;})}
                        </optgroup>
                      ))}
                      <option value="Miscellaneous">Miscellaneous{skuPrices["Miscellaneous"]?` ($${parseFloat(skuPrices["Miscellaneous"]).toFixed(2)}/box)`:" (no price set)"}</option>
                    </select>
                    <input type="number" min="1" value={p.qty} onChange={e=>updateProduct(p.id,"qty",e.target.value)} placeholder="Qty" style={{ ...S.inp, textAlign:"center" }}/>
                    <div style={{ fontSize:13, fontWeight:700, color:subtotal>0?"#E8317A":"#333", whiteSpace:"nowrap", minWidth:80, textAlign:"right" }}>
                      {subtotal>0?`$${subtotal.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`:"--"}
                    </div>
                    {products.length > 1 && <button onClick={()=>removeProduct(p.id)} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:16 }}>{"\u2715"}</button>}
                  </div>
                );
              })}
            </div>
            {totalMktVal > 0 && (
              <div style={{ marginTop:10, padding:"10px 14px", background:"var(--bz-s2)", borderRadius:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Total Market Value</span>
                <span style={{ fontSize:18, fontWeight:900, color:"var(--bz-ink)" }}>${totalMktVal.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
              </div>
            )}
          </div>

          {/* Settings row */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr", gap:12 }}>
            <div>
              <label style={S.lbl}>Number of Spots</label>
              <input type="number" min="1" value={spots} onChange={e=>setSpots(e.target.value)} style={S.inp}/>
            </div>
            <div>
              <label style={S.lbl}>Target Market % (e.g. 65)</label>
              <input type="number" min="1" max="200" value={targetPct} onChange={e=>setTargetPct(e.target.value)} style={{ ...S.inp, color: targetMultiple<0.65?"#4ade80":targetMultiple<0.70?"#FBBF24":"#E8317A" }}/>
            </div>
            <div>
              <label style={S.lbl}>Whatnot Fee %</label>
              <input type="number" min="0" max="100" value={whatnotPct} onChange={e=>setWhatnotPct(e.target.value)} style={S.inp}/>
            </div>
            <div>
              <label style={S.lbl}>Est. Coupons ($)</label>
              <input type="number" min="0" value={couponAmt} onChange={e=>setCouponAmt(e.target.value)} style={S.inp}/>
            </div>
            <div>
              <label style={S.lbl}>Breaker</label>
              <select value={breaker} onChange={e=>setBreaker(e.target.value)} style={{ ...S.inp, cursor:"pointer" }}>
                {BREAKERS.map(b=><option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      {totalMktVal > 0 && targetGross > 0 && (
        <>
          {/* Spot price hero */}
          <div style={{ ...S.card, background:zone.bg, border:`2px solid ${zone.c}44`, textAlign:"center", padding:"28px 20px" }}>
            <div style={{ fontSize:11, fontWeight:700, color:zone.c, textTransform:"uppercase", letterSpacing:2, marginBottom:8 }}>{zone.l} · {targetPct}% of Market Value</div>
            <div style={{ fontSize:13, color:"var(--bz-ink-2)", marginBottom:6 }}>Price per spot to hit your target</div>
            <div style={{ fontSize:56, fontWeight:900, color:zone.c, letterSpacing:-1 }}>${spotPrice.toFixed(2)}</div>
            <div style={{ fontSize:13, color:"#999", marginTop:6 }}>{numSpots} spots × ${spotPrice.toFixed(2)} = <strong style={{color:"var(--bz-ink)"}}>${targetGross.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</strong> gross</div>
          </div>

          {/* Financial breakdown */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
            {[
              { l:"Est. Gross Revenue",  v:`$${targetGross.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`, c:"var(--bz-ink)", sub:`${numSpots} spots @ $${spotPrice.toFixed(2)}` },
              { l:"Bazooka Net (30%)",   v:fmt(bazNet),    c:"#E8317A",  sub:`After ${whatnotPct}% Whatnot fee` },
              { l:`${breaker}'s Commission`, v:fmt(commAmt), c:"#4ade80", sub:`${(rate*100).toFixed(0)}% rate · ${(mm).toFixed(2)}x multiple` },
            ].map(({l,v,c,sub})=>(
              <div key={l} style={{ ...S.card, textAlign:"center" }}>
                <div style={{ fontSize:24, fontWeight:900, color:c, marginBottom:4 }}>{v}</div>
                <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{l}</div>
                <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{sub}</div>
              </div>
            ))}
          </div>

          {/* Comparison table */}
          <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
            <div style={{ padding:"14px 20px 10px" }}><SectionLabel t="Spot Price Scenarios" /></div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr>
                {["Target %","Spot Price","Est. Gross","Bazooka Net","Commission","Zone"].map(h=><th key={h} style={S.th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {[110,120,130,140,150,160,170,180].map((pct,i) => {
                  const g = totalMktVal * pct/100;
                  const sp = g / numSpots;
                  const wf = g * (parseFloat(whatnotPct)||0) / 100;
                  const nr = g - wf - (parseFloat(couponAmt)||0);
                  const bn = nr * 0.30;
                  const gfc = g - (parseFloat(couponAmt)||0);
                  const bnc = gfc * 0.30;
                  const re = (parseFloat(couponAmt)||0) * 0.135;
                  const mult = totalMktVal > 0 ? g/totalMktVal : 0;
                  const r = mult>=1.8?0.55:mult>=1.7?0.50:mult>=1.6?0.45:mult>=1.5?0.40:0.35;
                  const ca = (bnc-re) * r;
                  const isTarget = pct === parseInt(targetPct);
                  const zc = pct>=150?"#4ade80":pct>=130?"#FBBF24":"#E8317A";
                  const zl = pct>=150?"\uD83D\uDFE2 Green":pct>=130?"\uD83D\uDFE1 Yellow":"\uD83D\uDD34 Red";
                  return (<tr key={pct} style={{ background:isTarget?"#1a1520":i%2===0?"var(--bz-s1)":"var(--bz-s1)", borderBottom:"1px solid var(--bz-line)" }}>
                      <td style={{ ...S.td, fontWeight:isTarget?900:400, color:zc }}>{pct}%{isTarget?" \u2190 target":""}</td>
                      <td style={{ ...S.td, fontWeight:900, color:isTarget?zc:"var(--bz-ink)" }}>${sp.toFixed(2)}</td>
                      <td style={{ ...S.td, color:"var(--bz-ink)" }}>${g.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                      <td style={{ ...S.td, color:"#E8317A" }}>{fmt(bn)}</td>
                      <td style={{ ...S.td, color:"#4ade80", fontWeight:700 }}>{fmt(ca)}</td>
                      <td style={{ ...S.td }}><span style={{ color:zc, fontWeight:700, fontSize:11 }}>{zl}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Break-even callout */}
          <div style={{ ...S.card, background:"#0a0f1a", border:"1px solid #7B9CFF33", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:"#E8317A", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{"\uD83D\uDEA8 Break-Even Spot Price (1.0x)"}</div>
              <div style={{ fontSize:12, color:"#999" }}>At this price you're just recovering market value -- no profit</div>
            </div>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:28, fontWeight:900, color:"#7B9CFF" }}>${breakEvenSpot.toFixed(2)}</div>
              <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>${breakEvenGross.toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})} gross needed</div>
            </div>
          </div>
        </>
      )}

      {totalMktVal === 0 && (
        <div style={{ ...S.card, textAlign:"center", padding:"60px 40px", color:"var(--bz-ink-3)" }}>
          <div style={{ fontSize:32, marginBottom:12 }}>{"\uD83E\uDDEE"}</div>
          <div style={{ fontSize:14 }}>{"Select a product and set your prices in Inventory \u2192 Product Tracking to get started"}</div>
        </div>
      )}
    </div>
  );
}

function StreamCalendar({ streams=[], skuPrices={}, inventory=[], breaks=[], cardPools=[], userRole, user }) {
  const canSeeFinancials = ["Admin"].includes(userRole?.role);
  const myBreaker = resolveBreaker(user); // rep's own breaker (null for admins/unknown)
  const canBuildCalendar = userRole?.role === "Admin"; // only admins add/edit/delete planned streams; reps view only
  const fmt2 = v => "$" + parseFloat(v||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});

  // -- State --
  const today = new Date();
  const [viewMode,     setViewMode]     = useState("month");
  const [curYear,      setCurYear]      = useState(today.getFullYear());
  const [curMonth,     setCurMonth]     = useState(today.getMonth());
  const [plans,        setPlans]        = useState([]);
  const [vacations,    setVacations]    = useState([]);
  const [templates,    setTemplates]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [modalDate,    setModalDate]    = useState(null);
  const [editingId,    setEditingId]    = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName,   setTemplateName]   = useState("");
  const [copyWeekModal,  setCopyWeekModal]  = useState(false);
  const [copyWeekSrc,    setCopyWeekSrc]    = useState("");
  const [copyWeekDst,    setCopyWeekDst]    = useState("");
  const [copyingWeek,    setCopyingWeek]    = useState(false);
  const [shareWeekModal, setShareWeekModal] = useState(false);
  const [shareWeekStart, setShareWeekStart] = useState(""); // Sunday of week to share
  const [confettiActive, setConfettiActive] = useState(false);
  const confettiCanvas   = useRef(null);
  const confettiAnimRef  = useRef(null);
  const confettiTriggered = useRef(new Set()); // track which milestones already fired
  const [monthTargets, setMonthTargets] = useState({});
  const [boxesCollapsed, setBoxesCollapsed] = useState(false);
  const [paceCollapsed,  setPaceCollapsed]  = useState(false);
  const [burnRateOverrides, setBurnRateOverrides] = useState(() => {
    try { return JSON.parse(localStorage.getItem("stream_burn_rates")||"{}"); } catch(e) { return {}; }
  });
  const [planAssumptions, setPlanAssumptions] = useState(() => {
    try { return JSON.parse(localStorage.getItem("stream_plan_assumptions")||"{}"); } catch(e) { return {}; }
  });
  // defaults: 8% Whatnot fee, $200 avg stream expenses
  const whatnotFeePct  = parseFloat(planAssumptions.whatnotFeePct  ?? 8);
  const avgStreamExp   = parseFloat(planAssumptions.avgStreamExp   ?? 200);
  function saveAssumption(key, val) {
    const next = { ...planAssumptions, [key]: val };
    setPlanAssumptions(next);
    try { localStorage.setItem("stream_plan_assumptions", JSON.stringify(next)); } catch(e) {}
  }
  function calcPlanEarnings(gross) {
    if (!gross || gross <= 0) return null;
    const netRev     = gross * 0.81;
    const splitBase  = netRev;
    const bazNet     = splitBase * 0.30;
    const imcNet     = splitBase * 0.70;
    const rate       = 0.45; // estimated avg rate
    const commAmt    = bazNet * rate;
    const bazTrueNet = bazNet - commAmt;
    return { gross, netRev, splitBase, bazNet, imcNet, commAmt, imcReimb:0, bazTrueNet, rate };
  }

  const EMPTY_PLAN = { breaker:BREAKERS[0], channel:"Bazooka Vault", products:[{id:uid(),type:"",qty:"1"}], estRevenue:"", estMultiple:"", sessionType:"", notes:"", streamName:"", repeat:"none", repeatDays:[], repeatUntil:"", staffOnDuty:[], startTime:"", endTime:"" };
  const [form, setForm] = useState(EMPTY_PLAN);

  const S2 = { inp:{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:8, color:"var(--bz-ink)", padding:"9px 12px", fontSize:13, fontFamily:"inherit", outline:"none", width:"100%", boxSizing:"border-box" }, card:{ background:"linear-gradient(160deg,#181016,#130d11)", border:"1px solid var(--bz-line)", borderRadius:16, padding:"16px 20px", boxShadow:"0 1px 0 rgba(255,255,255,0.03) inset, 0 6px 22px rgba(0,0,0,0.35)" } };

  // -- Firestore --
  useEffect(() => {
    const unsub = onSnapshot(collection(db,"planned_streams"), snap => {
      setPlans(snap.docs.map(d=>({...d.data(),id:d.id})));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db,"breaker_vacations"), snap => {
      setVacations(snap.docs.map(d=>({...d.data(),id:d.id})));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db,"stream_templates"), snap => {
      setTemplates(snap.docs.map(d=>({...d.data(),id:d.id})).sort((a,b)=>a.name.localeCompare(b.name)));
    });
    return () => unsub();
  }, []);

  // Confetti engine
  function launchConfetti() {
    const canvas = confettiCanvas.current;
    if (!canvas) return;
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;
    setConfettiActive(true);
    const ctx = canvas.getContext("2d");
    const COLORS = ["#E8317A","#7B2FF7","#4ade80","#FBBF24","#22d3ee","var(--bz-ink)","#C084FC"];
    const pieces = Array.from({length:150}, () => ({
      x: Math.random()*canvas.width, y: -20,
      w: 6+Math.random()*8, h: 10+Math.random()*6,
      color: COLORS[Math.floor(Math.random()*COLORS.length)],
      rot: Math.random()*360, rotV: (Math.random()-0.5)*8,
      vx: (Math.random()-0.5)*6, vy: 2+Math.random()*5,
      opacity: 1,
    }));
    let frame = 0;
    function animate() {
      ctx.clearRect(0,0,canvas.width,canvas.height);
      pieces.forEach(p => {
        p.x += p.vx; p.y += p.vy + frame*0.02;
        p.rot += p.rotV; p.vy += 0.08;
        if (frame > 120) p.opacity = Math.max(0, p.opacity - 0.012);
        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot * Math.PI/180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w/2, -p.h/2, p.w, p.h);
        ctx.restore();
      });
      frame++;
      if (frame < 220) confettiAnimRef.current = requestAnimationFrame(animate);
      else { ctx.clearRect(0,0,canvas.width,canvas.height); setConfettiActive(false); }
    }
    if (confettiAnimRef.current) cancelAnimationFrame(confettiAnimRef.current);
    confettiAnimRef.current = requestAnimationFrame(animate);
  }

  // Watch for milestone hits — fire confetti once per milestone per month
  useEffect(() => {
    if (!canSeeFinancials) return;
    const mKey      = monthKey(curYear, curMonth);
    const mActuals  = monthActuals(curYear, curMonth);
    const actRev    = actualRevenue(mActuals);
    const target    = parseFloat(monthTargets[mKey]) || 0;
    const mkt       = totalMonthMkt(curYear, curMonth);
    const milestones = [
      ...(target > 0    ? [`${mKey}-target-${Math.floor(target)}`]   : []),
      ...(mkt > 0       ? [`${mKey}-1.5x-${Math.floor(mkt*1.5)}`]   : []),
      ...(mkt > 0       ? [`${mKey}-1.7x-${Math.floor(mkt*1.7)}`]   : []),
      ...(mkt > 0       ? [`${mKey}-1.9x-${Math.floor(mkt*1.9)}`]   : []),
    ];
    milestones.forEach(key => {
      const threshold = parseFloat(key.split("-").pop());
      if (actRev >= threshold && !confettiTriggered.current.has(key)) {
        confettiTriggered.current.add(key);
        setTimeout(launchConfetti, 300);
      }
    });
  }, [streams, curYear, curMonth, monthTargets]); // eslint-disable-line

  // -- Helpers --
  function monthKey(y,m) { return `${y}-${String(m+1).padStart(2,"0")}`; }
  function daysInMonth(y,m) { return new Date(y,m+1,0).getDate(); }
  function firstDow(y,m) { return new Date(y,m,1).getDay(); }
  function dateStr(y,m,d) { return `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`; }
  function plansForDate(ds) { return plans.filter(p=>p.date===ds); }
  function actualForDate(ds) { return streams.filter(s=>s.date===ds); }
  function prevMonth() { if(curMonth===0){setCurYear(y=>y-1);setCurMonth(11);}else setCurMonth(m=>m-1); }
  function nextMonth() { if(curMonth===11){setCurYear(y=>y+1);setCurMonth(0);}else setCurMonth(m=>m+1); }

  function openModal(ds, plan=null) {
    if (!canBuildCalendar) return; // reps can view the calendar but not build/edit it
    setModalDate(ds);
    if (plan) { setEditingId(plan.id); setForm({breaker:plan.breaker||BREAKERS[0],channel:plan.channel||"Bazooka Vault",products:plan.products||[{id:uid(),type:"",qty:"1"}],estRevenue:plan.estRevenue||"",estMultiple:plan.estMultiple||"",sessionType:plan.sessionType||"",notes:plan.notes||"",streamName:plan.streamName||"",repeat:"none",repeatDays:[],repeatUntil:"",staffOnDuty:plan.staffOnDuty||[],startTime:plan.startTime||"",endTime:plan.endTime||""}); }
    else { setEditingId(null); setForm(EMPTY_PLAN); }
  }
  function closeModal() { setModalDate(null); setEditingId(null); setForm(EMPTY_PLAN); }

  // Generate all dates for a repeat pattern starting from startDate
  function generateRepeatDates(startDate, repeat, repeatDays, repeatUntil) {
    const dates = [];
    const end = repeatUntil ? new Date(repeatUntil+"T12:00:00") : new Date(curYear, curMonth+1, 0, 12, 0, 0);
    let cur = new Date(startDate+"T12:00:00");
    // Skip the start date itself (already saved as main event)
    if (repeat === "daily") {
      cur.setDate(cur.getDate()+1);
      while (cur <= end) {
        dates.push(cur.toISOString().slice(0,10));
        cur.setDate(cur.getDate()+1);
      }
    } else if (repeat === "weekly") {
      cur.setDate(cur.getDate()+7);
      while (cur <= end) {
        dates.push(cur.toISOString().slice(0,10));
        cur.setDate(cur.getDate()+7);
      }
    } else if (repeat === "biweekly") {
      cur.setDate(cur.getDate()+14);
      while (cur <= end) {
        dates.push(cur.toISOString().slice(0,10));
        cur.setDate(cur.getDate()+14);
      }
    } else if (repeat === "custom" && repeatDays.length > 0) {
      // Custom days of week — generate all matching days from tomorrow to end
      cur.setDate(cur.getDate()+1);
      while (cur <= end) {
        if (repeatDays.includes(cur.getDay())) dates.push(cur.toISOString().slice(0,10));
        cur.setDate(cur.getDate()+1);
      }
    }
    return dates;
  }

  async function savePlan() {
    if (!canBuildCalendar) { alert("Only admins can build the sales calendar."); return; }
    if (!modalDate) return;
    setSaving(true);
    const { repeat, repeatDays, repeatUntil, ...planData } = form;
    const data = { ...planData, date:modalDate, updatedAt:new Date().toISOString() };
    // Save the main event
    const id = editingId || uid();
    await setDoc(doc(db,"planned_streams",id), data);
    // Save repeating occurrences
    if (!editingId && repeat !== "none") {
      const repeatDates = generateRepeatDates(modalDate, repeat, repeatDays, repeatUntil);
      await Promise.all(repeatDates.map(ds =>
        setDoc(doc(db,"planned_streams",uid()), { ...planData, date:ds, updatedAt:new Date().toISOString(), isRecurring:true, recurringFrom:id })
      ));
    }
    closeModal(); setSaving(false);
  }
  async function deletePlan(id, deleteAll=false) {
    if (!canBuildCalendar) { alert("Only admins can edit the sales calendar."); return; }
    const plan = plans.find(p=>p.id===id);
    if (!plan) return;
    if (deleteAll && plan.recurringFrom) {
      // Delete all events from the same series
      const series = plans.filter(p=>p.recurringFrom===plan.recurringFrom||p.id===plan.recurringFrom);
      if (window.confirm(`Remove all ${series.length+1} events in this series?`)) {
        await Promise.all([...series.map(p=>deleteDoc(doc(db,"planned_streams",p.id))), deleteDoc(doc(db,"planned_streams",id))]);
      }
    } else {
      if (window.confirm("Remove this planned stream?")) await deleteDoc(doc(db,"planned_streams",id));
    }
  }

  const [vacModal,    setVacModal]    = useState(false);
  const [vacForm,     setVacForm]     = useState({ breaker:BREAKERS[0], startDate:"", endDate:"", note:"" });
  const [editVacId,   setEditVacId]   = useState(null);
  const [savingVac,   setSavingVac]   = useState(false);

  function vacationsForDate(ds) {
    return vacations.filter(v => ds >= v.startDate && ds <= v.endDate);
  }
  function vacationsForMonth(y, m) {
    const first = dateStr(y, m, 1);
    const last  = dateStr(y, m, daysInMonth(y, m));
    return vacations.filter(v => v.startDate <= last && v.endDate >= first);
  }
  async function saveVacation() {
    if (!canBuildCalendar) { alert("Only admins can edit the sales calendar."); return; }
    if (!vacForm.startDate || !vacForm.endDate || !vacForm.breaker) return;
    setSavingVac(true);
    const id = editVacId || uid();
    await setDoc(doc(db,"breaker_vacations",id), { ...vacForm, updatedAt:new Date().toISOString() });
    setVacModal(false); setEditVacId(null); setVacForm({ breaker:BREAKERS[0], startDate:"", endDate:"", note:"" });
    setSavingVac(false);
  }
  async function deleteVacation(id) {
    if (!canBuildCalendar) { alert("Only admins can edit the sales calendar."); return; }
    if (window.confirm("Remove this time-off entry?")) await deleteDoc(doc(db,"breaker_vacations",id));
  }

  async function saveAsTemplate(name) {
    if (!name.trim()) return;
    const { repeat, repeatDays, repeatUntil, estRevenue, estMultiple, ...tplData } = form;
    await setDoc(doc(db,"stream_templates",uid()), { ...tplData, name:name.trim(), createdAt:new Date().toISOString() });
    setTemplateName(""); setSavingTemplate(false);
  }
  async function deleteTemplate(id) {
    if (window.confirm("Delete this template?")) await deleteDoc(doc(db,"stream_templates",id));
  }
  function applyTemplate(t) {
    setForm(p=>({ ...p, breaker:t.breaker||p.breaker, products:t.products?.map(pr=>({...pr,id:uid()}))||p.products, sessionType:t.sessionType||p.sessionType, notes:t.notes||p.notes, streamName:t.streamName||p.streamName, estRevenue:"", estMultiple:"" }));
    setShowTemplates(false);
  }

  // Get all weeks (Sunday starts) in current month view
  function getWeeksInMonth(y, m) {
    const weeks = [];
    const days = daysInMonth(y, m);
    for (let d=1; d<=days; d++) {
      const ds = dateStr(y, m, d);
      const dow = new Date(ds+"T12:00:00").getDay();
      if (dow === 0 || d === 1) {
        // Sunday = start of week
        const weekStart = dow === 0 ? ds : (() => {
          const prev = new Date(ds+"T12:00:00"); prev.setDate(prev.getDate()-dow);
          return prev.toISOString().slice(0,10);
        })();
        if (!weeks.find(w=>w===weekStart)) weeks.push(weekStart);
      }
    }
    return weeks;
  }

  async function copyWeek() {
    if (!canBuildCalendar) { alert("Only admins can build the sales calendar."); return; }
    if (!copyWeekSrc || !copyWeekDst) return;
    setCopyingWeek(true);
    const srcStart = new Date(copyWeekSrc+"T12:00:00");
    const dstStart = new Date(copyWeekDst+"T12:00:00");
    const diffMs   = dstStart - srcStart;
    const diffDays = Math.round(diffMs / 86400000);
    // Find all plans in src week (Sun–Sat)
    const srcEnd = new Date(srcStart); srcEnd.setDate(srcStart.getDate()+6);
    const srcEndStr = srcEnd.toISOString().slice(0,10);
    const weekPlans = plans.filter(p=>p.date>=copyWeekSrc&&p.date<=srcEndStr);
    if (weekPlans.length === 0) { setCopyingWeek(false); setCopyWeekModal(false); return; }
    await Promise.all(weekPlans.map(p=>{
      const newDate = new Date(p.date+"T12:00:00");
      newDate.setDate(newDate.getDate()+diffDays);
      const newDs = newDate.toISOString().slice(0,10);
      const { id:_id, isRecurring:_r, recurringFrom:_rf, ...rest } = p;
      return setDoc(doc(db,"planned_streams",uid()), { ...rest, date:newDs, updatedAt:new Date().toISOString() });
    }));
    setCopyingWeek(false); setCopyWeekModal(false);
  }
  function monthPlans(y,m) { return plans.filter(p=>{const d=p.date||"";return d.startsWith(monthKey(y,m));}); }
  function monthActuals(y,m) { return streams.filter(s=>{const d=s.date||"";return d.startsWith(monthKey(y,m));}); }
  function planMktValue(p) { return (p.products||[]).reduce((s,pr)=>s+(parseFloat(skuPrices[pr.type])||0)*(parseInt(pr.qty)||0),0); }
  function estimateRevenue(p, mult=1.5) { return planMktValue(p) * mult; }
  function projectedRevenue(planList, mult=1.5) {
    return planList.reduce((s,p) => {
      // Manual dollar estimate always wins when entered (estMultiple is cleared when
      // the user types a manual amount; it's only set when they tap a multiple tier).
      const manual = parseFloat(p.estRevenue);
      const hasMultiple = p.estMultiple !== "" && p.estMultiple != null;
      if (manual > 0 && !hasMultiple) return s + manual;
      const mkt = planMktValue(p);
      if (mkt > 0) {
        const storedMult = parseFloat(p.estMultiple);
        const effectiveMult = (storedMult >= 0.5 && storedMult <= 5) ? storedMult : mult;
        return s + mkt * effectiveMult;
      }
      // No products — use manually entered estRevenue as-is
      return s + (parseFloat(p.estRevenue)||0);
    }, 0);
  }
  // Match the Financial Overview's "breaks only" gross scope: exclude singles
  // shows and any streams flagged out of financials, so calendar pace/projection
  // numbers line up with the official gross instead of over-counting.
  function actualRevenue(actuals) {
    return actuals
      .filter(a => !a.isSinglesShow && !a.excludeFinancials)
      .reduce((s,a)=>s+(parseFloat(a.grossRevenue)||0),0);
  }
  // Admin estimate: per-breaker commission from scheduled (not-yet-run) streams.
  // Uses the same rate ladder as live payroll, floored at 35% on Bazooka's 30% net.
  // Singles shows pay the breaker 100% of net. Estimated revenue stands in for gross;
  // fees/coupons/expenses aren't known at planning time, so this is a planning floor.
  function estCommissionForPlan(p) {
    // Owners (Dev/Derrik) don't draw rep commission — they take owner profit instead.
    const OWNER_BREAKERS = ["Dev","Devin","Derrik"];
    if (OWNER_BREAKERS.includes(p.breaker)) return { breaker: p.breaker, comm: 0, isOwner: true };
    // estimated gross for this stream (same priority as projectedRevenue)
    const mkt = planMktValue(p);
    let estGross;
    const manualEst = parseFloat(p.estRevenue);
    const hasMult = p.estMultiple !== "" && p.estMultiple != null;
    if (manualEst > 0 && !hasMult) {
      estGross = manualEst;
    } else if (mkt > 0) {
      const storedMult = parseFloat(p.estMultiple);
      const effMult = (storedMult >= 0.5 && storedMult <= 5) ? storedMult : 1.5;
      estGross = mkt * effMult;
    } else {
      estGross = parseFloat(p.estRevenue) || 0;
    }
    if (estGross <= 0) return { breaker: p.breaker, comm: 0, isSingles: false };
    const isSingles = p.sessionType === "singles" || !!p.isSinglesShow;
    // Net base after fees/coupons/etc. ~ 81% of gross (matches typical Whatnot take).
    const NET_FACTOR = 0.81;
    const netBase = estGross * NET_FACTOR;
    if (isSingles) return { breaker: p.breaker, comm: netBase, isSingles: true };
    // rate from estMultiple via the real ladder; floor 35%
    const mm = parseFloat(p.estMultiple) || 0;
    const rate = mm>=1.8?0.55:mm>=1.7?0.50:mm>=1.6?0.45:mm>=1.5?0.40:0.35;
    const bazNet = netBase * 0.30;       // Bazooka's 30% share after IMC 70%
    return { breaker: p.breaker, comm: bazNet * rate, rate, isSingles: false };
  }
  function projectedRepCommissions(planList) {
    const byBreaker = {};
    let total = 0;
    planList.forEach(p => {
      const { breaker, comm } = estCommissionForPlan(p);
      if (!breaker) return;
      byBreaker[breaker] = (byBreaker[breaker] || 0) + comm;
      total += comm;
    });
    return { byBreaker, total };
  }
  function totalMonthMkt(y,m) { return monthPlans(y,m).reduce((s,p)=>s+planMktValue(p),0); }
  // Always returns revenue based on current SKU prices when products exist
  function liveRevenue(p) {
    // Manual dollar estimate always wins when the user typed one (no multiple set).
    const manual = parseFloat(p.estRevenue);
    const hasMultiple = p.estMultiple !== "" && p.estMultiple != null;
    if (manual > 0 && !hasMultiple) return manual;
    const mkt = planMktValue(p);
    if (mkt > 0) {
      const mult = parseFloat(p.estMultiple) || (p.estRevenue && mkt > 0 ? parseFloat(p.estRevenue)/mkt : 1.5);
      const effectiveMult = mult > 0.5 && mult < 5 ? mult : 1.5;
      return mkt * effectiveMult;
    }
    return parseFloat(p.estRevenue) || 0;
  }

  // -- Inventory needs --
  const USAGE_TO_CT2 = { "Giveaway":"Giveaway Cards","Insurance":"Insurance Cards","First-Timer Pack":"First-Timer Cards","Chaser Pull":"Chaser Cards","Chaser":"Chaser Cards" };
  const totalActualStreams = streams.length || 1;
  const burnPerStream = {};
  CARD_TYPES.forEach(ct=>{ burnPerStream[ct]=0; });
  breaks.forEach(b=>{ const ct=USAGE_TO_CT2[b.usage]||b.cardType; if(ct&&burnPerStream[ct]!==undefined)burnPerStream[ct]+=b.isPoolLog?(parseInt(b.qty)||1):1; });
  CARD_TYPES.forEach(ct=>{ burnPerStream[ct]=burnPerStream[ct]/totalActualStreams; });
  const invAvail = {};
  CARD_TYPES.forEach(ct=>{
    const indiv = inventory.filter(c=>c.cardType===ct&&!breaks.find(b=>!b.isPoolLog&&b.inventoryId===c.id)).length;
    const poolAvail = cardPools.filter(p=>p.cardType===ct).reduce((s,p)=>s+Math.max(0,(parseInt(p.totalQty)||0)-(parseInt(p.usedQty)||0)),0);
    invAvail[ct] = indiv + poolAvail;
  });

  // -- Quarter calcs --
  const quarterMonths = [0,1,2].map(i=>{ const m=(curMonth+i)%12; const y=curYear+(curMonth+i>=12?1:0); return{y,m}; });

  const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const BC = { Dev:"#3B82F6", Dre:"#A855F7", Krystal:"#22C55E", BigU:"#F97316", Vinny:"#EF4444", Stephen:"#FBBF24" };

  // -- Calendar grid --
  function renderCalendar(y, m, compact=false) {
    const days = daysInMonth(y,m);
    const startDow = firstDow(y,m);
    const mPlans = monthPlans(y,m);
    const mActuals = monthActuals(y,m);
    const mKey = monthKey(y,m);
    const target = parseFloat(monthTargets[mKey])||0;
    const projRev = projectedRevenue(mPlans);
    const actRev = actualRevenue(mActuals);

    const BREAKER_GRADIENTS = {
      Dev:     { from:"#1e40af", to:"#3B82F6", dot:"#3B82F6" },  // Blue
      Dre:     { from:"#6b21a8", to:"#A855F7", dot:"#A855F7" },  // Purple
      Krystal: { from:"#15803d", to:"#22C55E", dot:"#22C55E" },  // Green
      BigU:    { from:"#9a3412", to:"#F97316", dot:"#F97316" },  // Orange
      Vinny:   { from:"#991b1b", to:"#EF4444", dot:"#EF4444" },  // Red
      Stephen: { from:"#a16207", to:"#FBBF24", dot:"#FBBF24" },  // Gold
      "Orbital Society": { from:"#065f46", to:"#0d9488", dot:"#34d399" },
    };

    return (
      <div style={{ ...S2.card, padding:compact?"10px":"16px 20px", minWidth:0, overflow:"hidden" }}>
        <style>{`
          @keyframes todayPulse { 0%,100%{box-shadow:0 0 0 0 rgba(232,49,122,0.4)} 50%{box-shadow:0 0 0 4px rgba(232,49,122,0)} }
          @keyframes chipSlide { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
          .cal-day:hover { transform: scale(1.04) !important; z-index:2; }
          .cal-chip { animation: chipSlide 0.2s ease both; }
          .cal-chip:hover { filter: brightness(1.2); transform: scale(1.02); }
        `}</style>

        {/* Month header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
          <div>
            <div style={{ fontSize:compact?14:20, fontWeight:900, color:"var(--bz-ink)", letterSpacing:-0.5 }}>{MONTH_NAMES[m]} {y}</div>
            <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2, display:"flex", gap:10 }}>
              <span>{mPlans.length} planned · {mActuals.length} done</span>
              {canSeeFinancials && projRev>0 && <span style={{color:"#7B9CFF"}}>{fmt2(projRev)} projected</span>}
              {canSeeFinancials && actRev>0 && <span style={{color:"#4ade80"}}>{fmt2(actRev)} actual</span>}
            </div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {!compact && (
              <>
                {canSeeFinancials && <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Target:</span>
                  <input type="text" inputMode="decimal" value={monthTargets[mKey]||""} onChange={e=>setMonthTargets(p=>({...p,[mKey]:e.target.value}))} placeholder="$0" style={{...S2.inp,width:90,fontSize:12,padding:"4px 8px"}}/>
                </div>}
                <button onClick={prevMonth} style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink)",borderRadius:8,padding:"5px 14px",cursor:"pointer",fontFamily:"inherit",fontSize:16,transition:"all 0.15s"}}>‹</button>
                <button onClick={nextMonth} style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink)",borderRadius:8,padding:"5px 14px",cursor:"pointer",fontFamily:"inherit",fontSize:16,transition:"all 0.15s"}}>›</button>
                {canSeeFinancials && mPlans.length > 0 && (
                  <button onClick={async()=>{
                    if (!window.confirm(`Delete all ${mPlans.length} planned streams for ${MONTH_NAMES[m]} ${y}?`)) return;
                    await Promise.all(mPlans.map(p => deleteDoc(doc(db,"planned_streams",p.id))));
                  }} style={{background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.3)",color:"#ef4444",borderRadius:8,padding:"5px 12px",cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:700,transition:"all 0.15s"}}>
                    🗑 Clear {MONTH_NAMES[m]}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Target progress bar */}
        {canSeeFinancials && target > 0 && !compact && (
          <div style={{marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
              <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Revenue progress</span>
              <span style={{fontSize:11,fontWeight:700,color:actRev>=target?"#4ade80":projRev>=target?"#FBBF24":"#E8317A"}}>
                {fmt2(actRev)} actual · {fmt2(projRev)} projected · {fmt2(target)} target
              </span>
            </div>
            <div style={{height:7,background:"var(--bz-s2)",borderRadius:4,overflow:"hidden",position:"relative"}}>
              <div style={{position:"absolute",height:"100%",width:`${Math.min(100,projRev/target*100)}%`,background:"rgba(123,156,255,0.25)",borderRadius:4,transition:"width 0.6s ease"}}/>
              <div style={{position:"absolute",height:"100%",width:`${Math.min(100,actRev/target*100)}%`,background:"linear-gradient(90deg,#4ade80,#22d3ee)",borderRadius:4,transition:"width 0.6s ease",boxShadow:"0 0 8px rgba(74,222,128,0.4)"}}/>
            </div>
          </div>
        )}

        {/* Projected commission from scheduled streams — admins see all, reps see their own */}
        {(canSeeFinancials || myBreaker) && !compact && mPlans.length > 0 && (() => {
          const { byBreaker, total } = projectedRepCommissions(mPlans);
          let rows = Object.entries(byBreaker).filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]);
          const norm = x => (x||"").toLowerCase().replace(/\s+/g,"");
          if (!canSeeFinancials) {
            // rep: only their own row
            rows = rows.filter(([b])=>norm(b)===norm(myBreaker));
          }
          if (rows.length === 0) return null;
          const shownTotal = canSeeFinancials ? total : rows.reduce((s,[,v])=>s+v,0);
          return (
            <div style={{ marginBottom:14, background:"rgba(232,49,122,0.04)", border:"1px solid rgba(232,49,122,0.18)", borderRadius:10, padding:"12px 14px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <span style={{ fontSize:11, fontWeight:800, color:"#E8317A", textTransform:"uppercase", letterSpacing:1 }}>💸 {canSeeFinancials ? "Projected Rep Commissions" : "Your Projected Commission"}</span>
                <span style={{ fontSize:15, fontWeight:900, color:"#fff" }}>{fmt2(shownTotal)}</span>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))", gap:8 }}>
                {rows.map(([breaker, amt]) => {
                  const col = BC[breaker] || "#999";
                  return (
                    <div key={breaker} style={{ background:"rgba(255,255,255,0.03)", border:`1px solid ${col}33`, borderRadius:8, padding:"8px 10px" }}>
                      <div style={{ fontSize:11, fontWeight:800, color:col, marginBottom:2 }}>{breaker}</div>
                      <div style={{ fontSize:16, fontWeight:900, color:"#fff", lineHeight:1.1 }}>{fmt2(amt)}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize:9, color:"var(--bz-ink-3)", marginTop:9, fontStyle:"italic", lineHeight:1.5 }}>
                Estimate only. Assumes ~81% of gross is net after fees/coupons, then 35% minimum commission on Bazooka's 30% net (higher if a market multiple is set on the stream); singles shows pay the breaker full net. Actual pay depends on final revenue, fees, and expenses.
              </div>
            </div>
          );
        })()}

        {/* DOW headers */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:3}}>
          {DOW.map(d=><div key={d} style={{textAlign:"center",fontSize:10,fontWeight:700,color:"var(--bz-ink-3)",padding:"3px 0",letterSpacing:1}}>{d}</div>)}
        </div>

        {/* Day grid */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
          {Array.from({length:startDow}).map((_,i)=><div key={`e${i}`}/>)}
          {Array.from({length:days}).map((_,i)=>{
            const day = i+1;
            const ds = dateStr(y,m,day);
            const dayPlans = plansForDate(ds);
            const dayActuals = actualForDate(ds);
            const isToday = ds===dateStr(today.getFullYear(),today.getMonth(),today.getDate());
            const isPast = new Date(ds) < new Date(dateStr(today.getFullYear(),today.getMonth(),today.getDate()));
            const dayProjRev = dayPlans.reduce((s,p)=>s+(liveRevenue(p)),0);
            const dayActRev  = dayActuals.reduce((s,a)=>s+(parseFloat(a.grossRevenue)||0),0);
            const heatRev    = dayActRev || dayProjRev;
            const maxRev     = Math.max(...Array.from({length:days},(_,j)=>{
              const d2=dateStr(y,m,j+1);
              const p2=plansForDate(d2).reduce((s,p)=>s+(liveRevenue(p)),0);
              const a2=actualForDate(d2).reduce((s,a)=>s+(parseFloat(a.grossRevenue)||0),0);
              return a2||p2;
            }), 1);
            const heatPct = maxRev > 0 ? heatRev/maxRev : 0;
            const isMissed = dayPlans.length > 0 && dayPlans.some(p => !streams.find(s=>s.date===ds&&s.breaker===p.breaker)) && isPast;
            const hasActivity = dayActuals.length > 0 || dayPlans.length > 0;
            const isWeekend = [0,6].includes(new Date(ds+"T12:00:00").getDay());

            const heatBg = isToday
              ? "radial-gradient(circle at 50% 0%, #2d0a1a, #1a0a14)"
              : dayActRev > 0
                ? `radial-gradient(circle at 50% 0%, rgba(74,222,128,${0.08+heatPct*0.18}), rgba(10,26,10,0.6))`
                : dayProjRev > 0
                  ? `radial-gradient(circle at 50% 0%, rgba(123,156,255,${0.07+heatPct*0.16}), rgba(10,10,26,0.6))`
                  : isMissed
                    ? "radial-gradient(circle at 50% 0%, rgba(251,191,36,0.1), rgba(26,20,0,0.6))"
                    : isPast ? "#090909" : isWeekend ? "#0e0e0e" : "var(--bz-s1)";

            const borderCol = isToday ? "#E8317A66"
              : dayActRev > 0 ? "rgba(74,222,128,0.2)"
              : dayProjRev > 0 ? "rgba(123,156,255,0.15)"
              : isMissed ? "rgba(251,191,36,0.2)"
              : "var(--bz-s2)";

            return (
              <div key={day} className="cal-day" onClick={()=>openModal(ds)}
                style={{
                  minHeight:compact?40:74,
                  minWidth:0,
                  overflow:"hidden",
                  background:heatBg,
                  border:`1px solid ${borderCol}`,
                  borderRadius:8,
                  padding:"5px 4px 4px",
                  cursor:"pointer",
                  position:"relative",
                  transition:"transform 0.15s ease, box-shadow 0.15s ease",
                  animation: isToday ? "todayPulse 2s ease-in-out infinite" : "none",
                  boxShadow: isToday ? "0 0 12px rgba(232,49,122,0.25)" : dayActRev>0 ? "0 0 8px rgba(74,222,128,0.1)" : "none",
                }}>
                {/* Day number */}
                <div style={{
                  fontSize:11, fontWeight:isToday?900:500,
                  color:isToday?"#E8317A":isPast?"#333":"#666",
                  marginBottom:3, lineHeight:1,
                  display:"flex", alignItems:"center", gap:3
                }}>
                  {day}
                  {isToday && <div style={{width:4,height:4,borderRadius:"50%",background:"#E8317A",boxShadow:"0 0 4px #E8317A"}}/>}
                </div>

                {/* Vacation bands */}
                {vacationsForDate(ds).map(v=>{
                  const isStart = v.startDate===ds;
                  const isEnd   = v.endDate===ds;
                  return (
                    <div key={v.id} className="cal-chip" style={{
                      fontSize:8,fontWeight:700,color:"#fff",
                      background:"linear-gradient(90deg,rgba(220,38,38,0.85),rgba(185,28,28,0.85))",
                      borderRadius:isStart&&isEnd?4:isStart?"4px 0 0 4px":isEnd?"0 4px 4px 0":0,
                      padding:"2px 5px",marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                      textDecoration:"line-through",textDecorationColor:"rgba(255,255,255,0.5)",
                      borderLeft: isStart ? "2px solid rgba(255,255,255,0.3)" : "none",
                    }}>
                      {isStart?`🏖 ${v.breaker}`:isEnd?"← back":"·· "+v.breaker}
                    </div>
                  );
                })}

                {/* Actuals */}
                {dayActuals.slice(0,1).map((a,ai)=>{
                  const bg = BREAKER_GRADIENTS[a.breaker];
                  return (
                    <div key={a.id} className="cal-chip" style={{
                      fontSize:8,fontWeight:700,color:"#fff",
                      background: bg ? `linear-gradient(90deg,${bg.from},${bg.to})` : "linear-gradient(90deg,#166534,#15803d)",
                      borderRadius:4,padding:"2px 5px",marginBottom:2,
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                      boxShadow:"0 1px 4px rgba(0,0,0,0.4)",
                      animationDelay:`${ai*0.05}s`,
                      display:"flex",alignItems:"center",gap:3,
                    }}>
                      <span style={{opacity:0.8}}>✅</span>
                      {a.breaker&&<span style={{opacity:0.7}}>{a.breaker}</span>}
                      {!compact&&a.streamName&&<span style={{opacity:0.9}}>{a.streamName}</span>}
                    </div>
                  );
                })}

                {/* Plans */}
                {dayPlans.slice(0,compact?1:6).map((p,pi)=>{
                  const bg = BREAKER_GRADIENTS[p.breaker];
                  const missed = isMissed && !streams.find(s=>s.date===ds&&s.breaker===p.breaker);
                  const products = (p.products||[]).filter(x=>x.type&&x.qty);
                  const totalBoxes = products.reduce((s,x)=>s+(parseInt(x.qty)||0),0);
                  return (
                    <div key={p.id} className="cal-chip" style={{
                      fontSize:8, fontWeight:700,
                      color: missed ? "#FBBF24" : "#fff",
                      background: missed
                        ? "linear-gradient(90deg,rgba(120,80,0,0.7),rgba(80,50,0,0.7))"
                        : bg ? `linear-gradient(135deg,${bg.from}cc,${bg.to}cc)` : "rgba(255,255,255,0.08)",
                      border: missed ? "1px solid rgba(251,191,36,0.4)" : `1px solid ${bg?bg.dot+"44":"rgba(255,255,255,0.12)"}`,
                      borderLeft: bg ? `3px solid ${bg.dot}` : undefined,
                      borderRadius:4, padding:compact?"2px 4px":"3px 5px", marginBottom:2,
                      overflow:"hidden", whiteSpace:"nowrap",
                      animationDelay:`${(pi+1)*0.05}s`,
                    }}>
                      {/* Compact (quarter view): just the breaker, single line */}
                      {compact ? (
                        <div style={{ display:"flex", alignItems:"center", gap:3, overflow:"hidden" }}>
                          {bg && <div style={{width:4,height:4,borderRadius:"50%",background:bg.dot,flexShrink:0}}/>}
                          <span style={{fontWeight:900,fontSize:8,letterSpacing:"0.2px",overflow:"hidden",textOverflow:"ellipsis"}}>{p.breaker||"TBD"}</span>
                          {totalBoxes>0 && <span style={{opacity:0.6,fontSize:7,marginLeft:"auto",flexShrink:0}}>{totalBoxes}×</span>}
                        </div>
                      ) : (<>
                      {/* Row 1: breaker + time */}
                      <div style={{ display:"flex", alignItems:"center", gap:3, marginBottom: (products.length>0||p.channel)?2:0 }}>
                        {bg && <div style={{width:5,height:5,borderRadius:"50%",background:bg.dot,flexShrink:0,boxShadow:`0 0 3px ${bg.dot}`}}/>}
                        <span style={{fontWeight:900,fontSize:9,letterSpacing:"0.3px",overflow:"hidden",textOverflow:"ellipsis"}}>{p.breaker||"TBD"}</span>
                        {p.startTime && <span style={{opacity:0.6,fontSize:7,marginLeft:"auto",flexShrink:0}}>{p.startTime}{p.endTime?`–${p.endTime}`:""}</span>}
                      </div>
                      {/* Row 2: channel (if not Bazooka Vault) */}
                      {p.channel && p.channel !== "Bazooka Vault" && (
                        <div style={{ fontSize:7, color:"#7B9CFF", background:"rgba(123,156,255,0.15)", borderRadius:3, padding:"1px 4px", marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          📺 {p.channel}
                        </div>
                      )}
                      {/* Row 3: sets */}
                      {products.length > 0 && (
                        <div style={{ display:"flex", gap:2, flexWrap:"nowrap", overflow:"hidden" }}>
                          {products.slice(0,3).map((prod,pi2)=>(
                            <span key={pi2} style={{ background:"rgba(255,255,255,0.12)", borderRadius:3, padding:"1px 4px", fontSize:7, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:60 }}>
                              {parseInt(prod.qty)>1?`${prod.qty}× `:""}{prod.type?.split(" - ")[0]||prod.type}
                            </span>
                          ))}
                          {products.length > 3 && <span style={{fontSize:7,opacity:0.5}}>+{products.length-3}</span>}
                        </div>
                      )}
                      {p.streamName && !products.length && (
                        <div style={{fontSize:7,opacity:0.6,overflow:"hidden",textOverflow:"ellipsis"}}>{p.streamName}</div>
                      )}
                      </>)}
                    </div>
                  );
                })}
                {dayPlans.length>6&&!compact&&<div style={{fontSize:7,color:"#8a8a8a",marginTop:1}}>+{dayPlans.length-6} more</div>}
                {dayPlans.length>1&&compact&&<div style={{fontSize:7,color:"#999",marginTop:1,fontWeight:700}}>+{dayPlans.length-1} more</div>}

                {/* Revenue micro-label */}
                {!compact && dayActRev>0 && canSeeFinancials && (
                  <div style={{fontSize:7,fontWeight:700,color:"#4ade80",marginTop:2,opacity:0.8}}>{fmt2(dayActRev)}</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Vacation summary for this month */}
        {!compact && (() => {
          const mVacs = vacationsForMonth(y, m);
          return (
            <div style={{marginTop:12,borderTop:"1px solid var(--bz-line)",paddingTop:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:mVacs.length>0?8:0}}>
                <span style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-3)"}}>🏖 Time Off</span>
                {canBuildCalendar && <button onClick={()=>{setVacForm({breaker:BREAKERS[0],startDate:dateStr(y,m,1),endDate:dateStr(y,m,1),note:""});setEditVacId(null);setVacModal(true);}}
                  style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.4)",borderRadius:7,padding:"3px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>
                  + Add Time Off
                </button>}
              </div>
              {mVacs.length > 0 && (
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {mVacs.map(v=>{
                    const bc = BC[v.breaker]||"#888";
                    const days = Math.round((new Date(v.endDate+"T12:00:00")-new Date(v.startDate+"T12:00:00"))/86400000)+1;
                    return (
                      <div key={v.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"5px 10px",background:`${bc}10`,border:`1px solid ${bc}22`,borderRadius:7}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:12,fontWeight:700,color:bc}}>{v.breaker}</span>
                          <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>{v.startDate.slice(5).replace("-","/")} → {v.endDate.slice(5).replace("-","/")} · {days} day{days!==1?"s":""}</span>
                          {v.note&&<span style={{fontSize:10,color:"#8a8a8a"}}>{v.note}</span>}
                        </div>
                        <div style={{display:"flex",gap:4}}>
                          {canBuildCalendar && <><button onClick={()=>{setVacForm({breaker:v.breaker,startDate:v.startDate,endDate:v.endDate,note:v.note||""});setEditVacId(v.id);setVacModal(true);}}
                            style={{background:"none",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:5,padding:"2px 7px",fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>Edit</button>
                          <button onClick={()=>deleteVacation(v.id)}
                            style={{background:"none",border:"none",color:"#8a8a8a",cursor:"pointer",fontSize:12,padding:"0 4px"}}>✕</button></>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    );
  }

  // -- Inventory needs section --
  function renderInventoryNeeds() {
    const mPlans = monthPlans(curYear, curMonth);
    const planned = mPlans.length;
    if (planned === 0) return null;

    function saveBurnRate(ct, val) {
      const next = { ...burnRateOverrides, [ct]: val };
      setBurnRateOverrides(next);
      try { localStorage.setItem("stream_burn_rates", JSON.stringify(next)); } catch(e) {}
    }

    return (
      <div style={S2.card}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)"}}>🃏 Card Inventory Needs — {MONTH_NAMES[curMonth]}</div>
            <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:2}}>
              {planned} planned stream{planned!==1?"s":""} · edit cards/stream to override the estimate
            </div>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
          {CARD_TYPES.map(ct=>{
            const histRate = burnPerStream[ct];
            const override = burnRateOverrides[ct];
            const rateToUse = override !== undefined && override !== "" ? parseFloat(override)||0 : histRate;
            const needed = Math.ceil(rateToUse * planned);
            const avail  = invAvail[ct];
            const ok     = avail >= needed;
            const pct    = needed > 0 ? Math.min(100, avail/needed*100) : 100;
            const cc     = CC[ct]||{text:"#888",bg:"var(--bz-s1)",border:"#222"};
            const isOverridden = override !== undefined && override !== "";
            return (
              <div key={ct} style={{background:"var(--bz-s2)",border:`1px solid ${ok?"var(--bz-line-2)":"#E8317A33"}`,borderRadius:8,padding:"12px 14px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <span style={{fontSize:12,fontWeight:700,color:cc.text}}>{ct.replace(" Cards","")}</span>
                  <span style={{fontSize:11,fontWeight:700,color:ok?"#4ade80":"#E8317A"}}>{avail} avail / {needed} needed</span>
                </div>
                {/* Cards per stream edit */}
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                  <span style={{fontSize:11,color:"var(--bz-ink-3)",flexShrink:0}}>Per stream:</span>
                  <input
                    type="number"
                    min="0"
                    value={override !== undefined ? override : ""}
                    onChange={e=>saveBurnRate(ct, e.target.value)}
                    placeholder={histRate > 0 ? histRate.toFixed(1) : "0"}
                    style={{background:"var(--bz-s1)",border:`1px solid ${isOverridden?"rgba(123,156,255,0.4)":"var(--bz-line-2)"}`,borderRadius:6,color:isOverridden?"#7B9CFF":"var(--bz-ink)",padding:"4px 8px",fontSize:12,fontFamily:"inherit",outline:"none",width:70}}
                  />
                  {isOverridden && (
                    <button onClick={()=>saveBurnRate(ct,"")} title="Reset to historical avg"
                      style={{background:"none",border:"none",color:"#8a8a8a",cursor:"pointer",fontSize:12,padding:"0 2px",fontFamily:"inherit"}}>↩</button>
                  )}
                  <span style={{fontSize:10,color:"var(--bz-ink-3)",marginLeft:2}}>
                    {isOverridden ? "manual" : histRate>0 ? "avg" : "no data"}
                  </span>
                </div>
                <div style={{height:4,background:"var(--bz-s1)",borderRadius:2,overflow:"hidden",marginBottom:4}}>
                  <div style={{height:"100%",width:`${pct}%`,background:ok?"#4ade80":"#E8317A",borderRadius:2,transition:"width 0.3s"}}/>
                </div>
                <div style={{fontSize:10,color:ok?"#555":"#E8317A"}}>{ok ? `${avail-needed} buffer` : `⚠ Need ${needed-avail} more`}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // -- Product / Boxes Summary --
  function renderProductSummary() {
    const mPlans   = monthPlans(curYear, curMonth);
    const mActuals = monthActuals(curYear, curMonth);
    if (mPlans.length === 0) return null;

    const PT_COLORS = {"Double Mega":"#C2410C","Hobby":"#2C3E7A","Jumbo":"#166534","Miscellaneous":"#6B2D8B"};
    const PT_EMOJI  = {"Double Mega":"📦","Hobby":"📬","Jumbo":"📫","Miscellaneous":"🗃️"};

    // Planned boxes per type from plan.products
    const plannedBoxes = {};
    const plannedMkt   = {};
    PRODUCT_TYPES.forEach(pt=>{ plannedBoxes[pt]=0; plannedMkt[pt]=0; });
    mPlans.forEach(p=>{
      (p.products||[]).forEach(pr=>{
        if(!pr.type||!PRODUCT_TYPES.includes(pr.type)) return;
        const qty = parseInt(pr.qty)||0;
        plannedBoxes[pr.type] += qty;
        plannedMkt[pr.type]   += qty * (parseFloat(skuPrices[pr.type])||0);
      });
    });

    // Actual boxes ripped from logged streams (prod_Double Mega, prod_Hobby, etc.)
    const actualBoxes = {};
    PRODUCT_TYPES.forEach(pt=>{ actualBoxes[pt]=0; });
    mActuals.forEach(s=>{
      PRODUCT_TYPES.forEach(pt=>{
        actualBoxes[pt] += parseInt(s[`prod_${pt}`])||0;
      });
    });

    const totalPlannedBoxes = PRODUCT_TYPES.reduce((s,pt)=>s+plannedBoxes[pt],0);
    const totalActualBoxes  = PRODUCT_TYPES.reduce((s,pt)=>s+actualBoxes[pt],0);
    const totalPlannedMkt   = PRODUCT_TYPES.reduce((s,pt)=>s+plannedMkt[pt],0);
    if (totalPlannedBoxes === 0) return null;

    // Market value left to rip = planned streams that haven't been logged yet
    const todayStr = dateStr(today.getFullYear(), today.getMonth(), today.getDate());
    const futureMktValue = mPlans
      .filter(p => p.date >= todayStr)
      .reduce((s,p) => s + planMktValue(p), 0);

    // Avg boxes per stream from history
    const avgBoxesPerStream = {};
    PRODUCT_TYPES.forEach(pt=>{ avgBoxesPerStream[pt] = streams.length > 0 ? streams.reduce((s,str)=>s+(parseInt(str[`prod_${pt}`])||0),0)/streams.length : 0; });

    return (
      <div style={S2.card}>
        {/* Header with collapse toggle */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom: boxesCollapsed ? 0 : 14,flexWrap:"wrap",gap:8}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <button onClick={()=>setBoxesCollapsed(p=>!p)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--bz-ink-3)",fontSize:14,padding:"2px 4px",fontFamily:"inherit",lineHeight:1}}>
              {boxesCollapsed ? "▶" : "▼"}
            </button>
            <div>
              <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)"}}>📦 Boxes to Rip — {MONTH_NAMES[curMonth]}</div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:2}}>
                {totalPlannedBoxes} total planned · {totalActualBoxes > 0 ? `${totalActualBoxes} ripped so far · ` : ""}{fmt2(totalPlannedMkt)} market value
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:22,fontWeight:900,color:"#7B9CFF"}}>{totalPlannedBoxes}</div>
              <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>boxes planned</div>
            </div>
            {totalActualBoxes > 0 && (
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:22,fontWeight:900,color:"#4ade80"}}>{totalActualBoxes}</div>
                <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>ripped so far</div>
              </div>
            )}
            {futureMktValue > 0 && (
              <div style={{textAlign:"right",background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.2)",borderRadius:8,padding:"6px 12px"}}>
                <div style={{fontSize:16,fontWeight:900,color:"#FBBF24"}}>{fmt2(futureMktValue)}</div>
                <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>mkt value left to rip</div>
              </div>
            )}
          </div>
        </div>

        {/* Pink progress bar — gets brighter/deeper as progress increases */}
        {totalActualBoxes > 0 && !boxesCollapsed && (() => {
          const pct = Math.min(100, totalActualBoxes / totalPlannedBoxes * 100);
          // Shade transitions: pale pink → mid pink → deep pink → hot pink
          const barColor = pct >= 100 ? "#4ade80"
            : pct >= 75 ? "#E8317A"
            : pct >= 50 ? "#f472b6"
            : pct >= 25 ? "#fbb6ce"
            : "#fce7f3";
          return (
            <div style={{marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                <span style={{fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1}}>Boxes Ripped</span>
                <span style={{fontSize:11,fontWeight:700,color:barColor}}>{totalActualBoxes} / {totalPlannedBoxes} ({pct.toFixed(0)}%)</span>
              </div>
              <div style={{height:8,background:"var(--bz-s2)",borderRadius:4,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${pct}%`,background:`linear-gradient(90deg, #fce7f3, ${barColor})`,borderRadius:4,transition:"width 0.4s ease"}}/>
              </div>
            </div>
          );
        })()}

        {!boxesCollapsed && <>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10,marginBottom:14}}>
          {PRODUCT_TYPES.filter(pt=>plannedBoxes[pt]>0).map(pt=>{
            const planned = plannedBoxes[pt];
            const actual  = actualBoxes[pt];
            const mkt     = plannedMkt[pt];
            const color   = PT_COLORS[pt]||"#888";
            const pct     = planned > 0 ? Math.min(100, actual/planned*100) : 0;
            const barColor = pct >= 100 ? "#4ade80" : pct >= 75 ? "#E8317A" : pct >= 50 ? "#f472b6" : pct >= 25 ? "#fbb6ce" : "#fce7f3";
            return (
              <div key={pt} style={{background:"var(--bz-s2)",border:`1px solid ${actual>=planned&&actual>0?"rgba(74,222,128,0.2)":"var(--bz-line-2)"}`,borderRadius:8,padding:"12px 14px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <span style={{fontSize:12,fontWeight:700,color}}>{PT_EMOJI[pt]||"📦"} {pt}</span>
                  {actual >= planned && actual > 0 && <span style={{fontSize:10,color:"#4ade80",fontWeight:700}}>✅ Done</span>}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:8}}>
                  <div style={{textAlign:"center",background:"rgba(0,0,0,0.3)",borderRadius:6,padding:"6px 4px"}}>
                    <div style={{fontSize:18,fontWeight:900,color}}>{planned}</div>
                    <div style={{fontSize:9,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1}}>Planned</div>
                  </div>
                  <div style={{textAlign:"center",background:"rgba(0,0,0,0.3)",borderRadius:6,padding:"6px 4px"}}>
                    <div style={{fontSize:18,fontWeight:900,color:actual>0?"#4ade80":"#333"}}>{actual || "—"}</div>
                    <div style={{fontSize:9,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1}}>Ripped</div>
                  </div>
                </div>
                {actual > 0 && (
                  <div style={{height:4,background:"rgba(255,255,255,0.06)",borderRadius:2,overflow:"hidden",marginBottom:6}}>
                    <div style={{height:"100%",width:`${pct}%`,background:`linear-gradient(90deg,#fce7f3,${barColor})`,borderRadius:2,transition:"width 0.3s"}}/>
                  </div>
                )}
                <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"var(--bz-ink-3)"}}>
                  <span>{fmt2(mkt)} mkt value</span>
                  {skuPrices[pt]&&<span>{fmt2(parseFloat(skuPrices[pt]))} /box</span>}
                </div>
                {avgBoxesPerStream[pt]>0&&<div style={{fontSize:10,color:"#8a8a8a",marginTop:3}}>~{avgBoxesPerStream[pt].toFixed(1)}/stream historically</div>}
              </div>
            );
          })}
        </div>

        {/* Per-stream product breakdown */}
        {mPlans.some(p=>(p.products||[]).some(pr=>pr.type&&parseInt(pr.qty)>0)) && (
          <div>
            <div style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>By Stream</div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              {mPlans.filter(p=>(p.products||[]).some(pr=>pr.type&&parseInt(pr.qty)>0)).sort((a,b)=>a.date.localeCompare(b.date)).map(p=>{
                const streamBoxes = (p.products||[]).filter(pr=>pr.type&&parseInt(pr.qty)>0);
                const streamMkt = streamBoxes.reduce((s,pr)=>s+(parseFloat(skuPrices[pr.type])||0)*(parseInt(pr.qty)||0),0);
                const actual = actualForDate(p.date);
                const wasLogged = actual.length > 0;
                return (
                  <div key={p.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:wasLogged?"rgba(74,222,128,0.04)":"rgba(0,0,0,0.3)",border:`1px solid ${wasLogged?"rgba(74,222,128,0.15)":"rgba(255,255,255,0.04)"}`,borderRadius:8,flexWrap:"wrap",gap:6}}>
                    <div>
                      <span style={{fontSize:12,fontWeight:700,color:wasLogged?"#4ade80":"var(--bz-ink)"}}>{wasLogged?"✅ ":""}{p.streamName||p.breaker}</span>
                      <span style={{fontSize:10,color:"var(--bz-ink-3)",marginLeft:8}}>{p.date}</span>
                    </div>
                    <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                      {streamBoxes.map(pr=>(
                        <span key={pr.id} style={{fontSize:11,fontWeight:700,color:PT_COLORS[pr.type]||"#888",background:"rgba(0,0,0,0.4)",borderRadius:5,padding:"2px 8px"}}>
                          {pr.qty}× {pr.type}
                        </span>
                      ))}
                      {streamMkt > 0 && <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>{fmt2(streamMkt)} mkt</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </>}
      </div>
    );
  }
  function renderMonthSummary() {
    const mPlans = monthPlans(curYear, curMonth);
    const mActuals = monthActuals(curYear, curMonth);
    if (mPlans.length===0 && mActuals.length===0) return null;
    const breakerPlan = {};
    BREAKERS.forEach(b=>{ breakerPlan[b]={planned:0,projRev:0}; });
    mPlans.forEach(p=>{ if(breakerPlan[p.breaker]){ breakerPlan[p.breaker].planned++; breakerPlan[p.breaker].projRev+=liveRevenue(p); } });
    return (
      <div style={S2.card}>
        <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:12}}>👥 By Breaker — {MONTH_NAMES[curMonth]}</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:10}}>
          {BREAKERS.map(b=>{
            const bp=breakerPlan[b];
            const bc=BC[b]||{text:"#888"};
            const bActuals=mActuals.filter(a=>a.breaker===b);
            return (
              <div key={b} style={{background:"var(--bz-s2)",border:`1px solid ${bc.border||"var(--bz-line-2)"}`,borderRadius:8,padding:"12px 14px"}}>
                <div style={{fontSize:14,fontWeight:900,color:bc.text,marginBottom:8}}>{b}</div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:3}}>Planned: <strong style={{color:"var(--bz-ink)"}}>{bp.planned} streams</strong></div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:3}}>Actual: <strong style={{color:"#4ade80"}}>{bActuals.length} done</strong></div>
                {canSeeFinancials && bp.projRev>0&&<div style={{fontSize:11,color:"var(--bz-ink-3)"}}>Projected: <strong style={{color:"#E8317A"}}>{fmt2(bp.projRev)}</strong></div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // -- Month Health Score --
  function getHealthScore() {
    const mPlans   = monthPlans(curYear, curMonth);
    const mActuals = monthActuals(curYear, curMonth);
    const mKey     = monthKey(curYear, curMonth);
    const target   = parseFloat(monthTargets[mKey]) || 0;
    const actRev   = actualRevenue(mActuals);
    const projRev  = projectedRevenue(mPlans);
    const todayStr = dateStr(today.getFullYear(), today.getMonth(), today.getDate());
    const pastPlans = mPlans.filter(p=>p.date<=todayStr);
    const projSoFar = projectedRevenue(pastPlans);
    let score = 0;

    // Pace (40pts) — only judge streams that were DUE, not future ones
    if (pastPlans.length === 0) {
      score += 40; // no streams due yet, full marks
    } else if (projSoFar > 0) {
      score += Math.min(40, Math.round((actRev/projSoFar)*40));
    } else {
      score += 40; // due streams had no projected revenue, neutral
    }

    // Projection vs target (30pts) — full marks if no target set
    if (target > 0) score += Math.min(30, Math.round((projRev/target)*30));
    else score += 30;

    // Inventory (30pts) — based on planned streams this month
    const planned = mPlans.length;
    if (planned > 0) {
      const invOk = CARD_TYPES.filter(ct=>{
        const override = burnRateOverrides[ct];
        const rate = override!==""&&override!==undefined ? parseFloat(override)||0 : burnPerStream[ct];
        return invAvail[ct] >= Math.ceil(rate*planned);
      }).length;
      score += Math.round((invOk/CARD_TYPES.length)*30);
    } else {
      score += 30;
    }

    const grade = score>=90?"A":score>=75?"B":score>=60?"C":"D";
    const color = score>=90?"#4ade80":score>=75?"#FBBF24":score>=60?"#F97316":"#E8317A";
    return { score, grade, color };
  }

  // -- Tomorrow Alert --
  // Burnout detection — flag anyone working 7+ consecutive days
  function getBurnoutAlerts() {
    const alerts = [];
    OFFICE_STAFF.forEach(staff => {
      // Get all dates this person is scheduled (planned streams they're on duty for)
      const workDates = plans
        .filter(p => (p.staffOnDuty||[]).includes(staff.id) || (staff.id==="dre"&&p.breaker==="Dre") || (staff.id==="krystal"&&p.breaker==="Krystal") || (staff.id==="devin"&&(p.breaker==="Dev"||p.breaker==="Devin")))
        .map(p => p.date)
        .sort();
      if (workDates.length < 7) return;
      // Find consecutive streaks
      let streak = 1, maxStreak = 1, streakStart = workDates[0];
      for (let i = 1; i < workDates.length; i++) {
        const prev = new Date(workDates[i-1]+"T12:00:00");
        const curr = new Date(workDates[i]+"T12:00:00");
        const diff = (curr - prev) / (1000*60*60*24);
        if (diff === 1) { streak++; if (streak > maxStreak) { maxStreak = streak; streakStart = workDates[i-streak+1]; } }
        else { streak = 1; }
      }
      if (maxStreak >= 7) alerts.push({ staff, streak:maxStreak, startDate:streakStart });
    });
    return alerts;
  }

  function renderBurnoutAlerts() {
    if (!canSeeFinancials) return null;
    const alerts = getBurnoutAlerts();
    if (!alerts.length) return null;
    return (
      <div style={{background:"rgba(239,68,68,0.06)",border:"1.5px solid rgba(239,68,68,0.3)",borderRadius:12,padding:"14px 18px",marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:800,color:"#ef4444",marginBottom:10}}>⚠️ Burnout Risk — Extended Schedules Detected</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {alerts.map(({staff,streak,startDate})=>(
            <div key={staff.id} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 12px",background:"rgba(239,68,68,0.05)",borderRadius:8}}>
              <div style={{width:10,height:10,borderRadius:"50%",background:staff.color,flexShrink:0}}/>
              <div style={{flex:1,fontSize:13,color:"var(--bz-ink)",fontWeight:700}}>{staff.name}</div>
              <div style={{fontSize:12,color:"#ef4444",fontWeight:700}}>{streak} consecutive days</div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>starting {new Date(startDate+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderTomorrowAlert() {
    if (!canSeeFinancials) return null; // reps don't see inventory alerts
    const tmrw = new Date(today); tmrw.setDate(today.getDate()+1);
    const tmrwStr = dateStr(tmrw.getFullYear(), tmrw.getMonth(), tmrw.getDate());
    const tmrwPlans = plansForDate(tmrwStr);
    if (tmrwPlans.length === 0) return null;
    const issues = CARD_TYPES.filter(ct=>{
      const override = burnRateOverrides[ct];
      const rate = override!==""&&override!==undefined ? parseFloat(override)||0 : burnPerStream[ct];
      return invAvail[ct] < Math.ceil(rate);
    });
    if (issues.length === 0) return null;
    return (
      <div style={{background:"rgba(251,191,36,0.06)",border:"2px solid rgba(251,191,36,0.3)",borderRadius:12,padding:"12px 16px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <span style={{fontSize:18}}>⚠️</span>
        <div style={{flex:1}}>
          <div style={{fontSize:13,fontWeight:800,color:"#FBBF24"}}>Stream tomorrow — inventory may be short</div>
          <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:2}}>
            {tmrwPlans.map(p=>p.streamName||p.breaker).join(", ")} · Short on: {issues.map(ct=>ct.replace(" Cards","")).join(", ")}
          </div>
        </div>
      </div>
    );
  }

  // -- Stream Scorecard --
  function renderStreamScorecard() {
    const sorted = [...streams].sort((a,b) => b.date.localeCompare(a.date));
    const latest = sorted[0];
    if (!latest) return null;

    // Inline calc (calcStreamDash not in scope here)
    const gross = parseFloat(latest.grossRevenue)||0;
    const fees = parseFloat(latest.whatnotFees)||0;
    const coupons = parseFloat(latest.coupons)||0;
    const splitBase = gross - fees - coupons;
    const bazNet = splitBase * 0.30;
    const trueGross = gross + coupons;
    const mm = parseFloat(latest.marketMultiple)||0;
    const mmColor = mm>=1.7?"#4ade80":mm>=1.5?"#86efac":mm>=1.4?"#FBBF24":"#E8317A";

    // Try to find a matching plan for grade
    const matchedPlan = plans.find(p => p.date === latest.date && p.breaker === latest.breaker);
    const planned = matchedPlan ? liveRevenue(matchedPlan) : 0;
    const pct = planned > 0 ? trueGross / planned : null;
    const grade = pct ? (pct>=1.1?"A+":pct>=1.0?"A":pct>=0.9?"B":pct>=0.75?"C":"D") : null;
    const gradeColor = pct ? (pct>=1.0?"#4ade80":pct>=0.9?"#FBBF24":"#E8317A") : "#555";

    const bc = BC[latest.breaker]||"#E8317A";

    return (
      <div style={{background:"rgba(0,0,0,0.3)",border:`2px solid ${gradeColor}33`,borderRadius:12,padding:"14px 18px"}}>
        <div style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>🏆 Last Stream Scorecard</div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
          <div>
            <div style={{fontSize:15,fontWeight:900,color:"var(--bz-ink)"}}>{latest.streamName||latest.breaker}</div>
            <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:2}}>{latest.date} · <span style={{color:bc}}>{latest.breaker}</span></div>
          </div>
          <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
            <div style={{textAlign:"center"}}><div style={{fontSize:11,color:"var(--bz-ink-3)"}}>Gross</div><div style={{fontSize:15,fontWeight:900,color:"var(--bz-ink)"}}>{fmt2(trueGross)}</div></div>
            <div style={{textAlign:"center"}}><div style={{fontSize:11,color:"var(--bz-ink-3)"}}>Market</div><div style={{fontSize:15,fontWeight:900,color:mmColor}}>{mm>0?mm+"x":"--"}</div></div>
            <div style={{textAlign:"center"}}><div style={{fontSize:11,color:"var(--bz-ink-3)"}}>Bazooka</div><div style={{fontSize:15,fontWeight:900,color:"#E8317A"}}>{fmt2(bazNet)}</div></div>
            {planned > 0 && (
              <div style={{textAlign:"center",background:gradeColor+"22",border:`2px solid ${gradeColor}44`,borderRadius:10,padding:"8px 16px"}}>
                <div style={{fontSize:28,fontWeight:900,color:gradeColor,lineHeight:1}}>{grade}</div>
                <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:2}}>{(pct*100).toFixed(0)}% of plan</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // -- Best Day Predictor --
  function renderBestDayPredictor() {
    if (!canSeeFinancials) return null;
    const todayStr = dateStr(today.getFullYear(), today.getMonth(), today.getDate());
    const days = daysInMonth(curYear, curMonth);
    const openDays = [];
    for (let d=1; d<=days; d++) {
      const ds = dateStr(curYear, curMonth, d);
      if (ds <= todayStr) continue;
      const dow = new Date(ds+"T12:00:00").getDay();
      if (plansForDate(ds).length === 0) openDays.push({ ds, dow });
    }
    if (openDays.length === 0) return null;
    const dowRevs = Array.from({length:7},()=>({count:0,total:0}));
    streams.forEach(s=>{ if(!s.date||!s.grossRevenue)return; const d=new Date(s.date+"T12:00:00").getDay(); dowRevs[d].count++; dowRevs[d].total+=parseFloat(s.grossRevenue)||0; });
    const dowAvg = dowRevs.map(d=>d.count>0?d.total/d.count:0);
    const byDow = {};
    openDays.forEach(d=>{ if(!byDow[d.dow])byDow[d.dow]=[]; byDow[d.dow].push(d); });
    const insights = Object.entries(byDow)
      .filter(([dow])=>dowAvg[dow]>0)
      .map(([dow,days])=>({ dow:parseInt(dow), count:days.length, avg:dowAvg[dow], potential:days.length*dowAvg[dow], days }))
      .sort((a,b)=>b.potential-a.potential).slice(0,3);
    if (insights.length === 0) return null;
    const DOW_FULL = ["Sundays","Mondays","Tuesdays","Wednesdays","Thursdays","Fridays","Saturdays"];
    return (
      <div style={{background:"rgba(123,156,255,0.04)",border:"1px solid rgba(123,156,255,0.12)",borderRadius:12,padding:"14px 18px"}}>
        <div style={{fontSize:12,fontWeight:800,color:"#7B9CFF",marginBottom:10}}>💡 Best Day Opportunities</div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {insights.map(({dow,count,avg,potential,days})=>(
            <div key={dow} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:"rgba(0,0,0,0.3)",borderRadius:8,flexWrap:"wrap",gap:8}}>
              <div>
                <span style={{fontSize:12,fontWeight:700,color:"#7B9CFF"}}>{count} open {DOW_FULL[dow]}</span>
                <span style={{fontSize:11,color:"var(--bz-ink-3)",marginLeft:8}}>~{fmt2(avg)}/stream historically</span>
              </div>
              <div style={{textAlign:"right"}}>
                <span style={{fontSize:13,fontWeight:900,color:"#4ade80"}}>+{fmt2(potential)} potential</span>
                <span style={{fontSize:10,color:"var(--bz-ink-3)",display:"block"}}>{days.map(d=>d.ds.slice(5).replace("-","/")).join(", ")}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // -- Streak Tracker --
  function renderStreakTracker() {
    if (!canSeeFinancials) return null;
    if (streams.length === 0) return null;

    // Group streams by month, calc weighted avg market multiple per month
    const monthData = {};
    streams.forEach(s => {
      if (!s.date || !s.grossRevenue) return;
      const mk = s.date.slice(0,7); // "YYYY-MM"
      if (!monthData[mk]) monthData[mk] = { gross:0, weightedMult:0, count:0 };
      const gross = parseFloat(s.grossRevenue)||0;
      const mult  = parseFloat(s.marketMultiple)||0;
      monthData[mk].gross += gross;
      if (mult > 0) monthData[mk].weightedMult += gross * mult;
      monthData[mk].count++;
    });

    // Resolve monthly multiple
    const months = Object.keys(monthData).sort();
    const monthResults = months.map(mk => {
      const d = monthData[mk];
      const avgMult = d.gross > 0 && d.weightedMult > 0 ? d.weightedMult / d.gross : null;
      const tier = avgMult === null ? null : avgMult >= 1.9 ? 1.9 : avgMult >= 1.7 ? 1.7 : avgMult >= 1.5 ? 1.5 : null;
      return { mk, gross:d.gross, avgMult, tier, count:d.count };
    });

    // Calculate streaks for each tier threshold
    function calcStreak(threshold) {
      let current = 0, best = 0, bestStart = "", curStart = "";
      monthResults.forEach(r => {
        if (r.tier !== null && r.tier >= threshold) {
          if (current === 0) curStart = r.mk;
          current++;
          if (current > best) { best = current; bestStart = curStart; }
        } else {
          current = 0; curStart = "";
        }
      });
      return { current, best, bestStart };
    }

    const s15 = calcStreak(1.5);
    const s17 = calcStreak(1.7);
    const s19 = calcStreak(1.9);

    // Last 6 months for sparkline
    const last6 = monthResults.slice(-6);

    const TIER_CFG = [
      { label:"1.5x+", sublabel:"Minimum", streak:s15, color:"#FBBF24" },
      { label:"1.7x+", sublabel:"Good",    streak:s17, color:"#4ade80" },
      { label:"1.9x+", sublabel:"Great",   streak:s19, color:"#E8317A" },
    ];

    return (
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:14,padding:"16px 20px"}}>
        <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:14}}>🔥 Streak Tracker</div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
          {TIER_CFG.map(({label,sublabel,streak,color})=>(
            <div key={label} style={{background:`${color}08`,border:`1px solid ${color}22`,borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
              <div style={{fontSize:10,fontWeight:700,color,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>{label} {sublabel}</div>
              <div style={{fontSize:32,fontWeight:900,color,lineHeight:1}}>{streak.current}</div>
              <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:4}}>month streak</div>
              {streak.best > 0 && streak.best !== streak.current && (
                <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:4}}>Best: {streak.best} mo.</div>
              )}
              {streak.current > 0 && streak.current === streak.best && streak.best > 1 && (
                <div style={{fontSize:10,color,marginTop:4,fontWeight:700}}>🏆 Personal best!</div>
              )}
            </div>
          ))}
        </div>

        {/* Month-by-month history */}
        {last6.length > 0 && (
          <div>
            <div style={{fontSize:10,fontWeight:700,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Last {last6.length} months</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {last6.map(r=>{
                const color = r.tier===1.9?"#E8317A":r.tier===1.7?"#4ade80":r.tier===1.5?"#FBBF24":"#333";
                const label = r.tier===1.9?"1.9x":r.tier===1.7?"1.7x":r.tier===1.5?"1.5x":r.avgMult?`${r.avgMult.toFixed(1)}x`:"—";
                const [y,m] = r.mk.split("-");
                return (
                  <div key={r.mk} style={{flex:1,minWidth:60,background:`${color}12`,border:`1px solid ${color}33`,borderRadius:8,padding:"8px 6px",textAlign:"center"}}>
                    <div style={{fontSize:9,color:"var(--bz-ink-3)",marginBottom:4}}>{MONTH_NAMES[parseInt(m)-1].slice(0,3)} {y.slice(2)}</div>
                    <div style={{fontSize:13,fontWeight:900,color}}>{label}</div>
                    <div style={{fontSize:9,color:"var(--bz-ink-3)",marginTop:3}}>{fmt2(r.gross)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // -- Month-over-Month Comparison --
  function renderMonthOverMonth() {
    if (!canSeeFinancials) return null;

    // Last month
    const lmMonth = curMonth === 0 ? 11 : curMonth - 1;
    const lmYear  = curMonth === 0 ? curYear - 1 : curYear;

    const thisPlans   = monthPlans(curYear, curMonth);
    const thisActuals = monthActuals(curYear, curMonth);
    const lastActuals = monthActuals(lmYear, lmMonth);

    if (thisPlans.length === 0 && thisActuals.length === 0 && lastActuals.length === 0) return null;

    const thisProj   = projectedRevenue(thisPlans);
    const thisAct    = actualRevenue(thisActuals);
    const lastAct    = actualRevenue(lastActuals);
    const lastPlans  = monthPlans(lmYear, lmMonth);
    const lastProj   = projectedRevenue(lastPlans);

    // Per-breaker comparison
    const breakerRows = BREAKERS.map(b => {
      const thisBPlans   = thisPlans.filter(p=>p.breaker===b);
      const thisBActuals = thisActuals.filter(s=>s.breaker===b);
      const lastBActuals = lastActuals.filter(s=>s.breaker===b);
      return {
        breaker: b,
        thisStreams: thisBActuals.length,
        thisPlanned: thisBPlans.length,
        thisRev: actualRevenue(thisBActuals),
        lastStreams: lastBActuals.length,
        lastRev: actualRevenue(lastBActuals),
      };
    }).filter(r => r.thisPlanned > 0 || r.thisStreams > 0 || r.lastStreams > 0);

    const revDiff   = lastAct > 0 ? thisAct - lastAct : null;
    const projDiff  = lastAct > 0 ? thisProj - lastAct : null;
    const streamDiff = thisActuals.length - lastActuals.length;

    const barMax = Math.max(thisProj, thisAct, lastAct, 1);

    return (
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:14,padding:"16px 20px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)"}}>📈 Month-over-Month</div>
            <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:2}}>{MONTH_NAMES[lmMonth]} actuals vs {MONTH_NAMES[curMonth]} plan</div>
          </div>
          {revDiff !== null && (
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:18,fontWeight:900,color:revDiff>=0?"#4ade80":"#E8317A"}}>{revDiff>=0?"+":""}{fmt2(revDiff)}</div>
              <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>vs last month actual</div>
            </div>
          )}
        </div>

        {/* Bar comparison */}
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
          {[
            {label:`${MONTH_NAMES[lmMonth]} Actual`, val:lastAct,  color:"rgba(255,255,255,0.2)"},
            {label:`${MONTH_NAMES[curMonth]} Projected`, val:thisProj, color:"rgba(251,191,36,0.5)"},
            ...(thisAct>0?[{label:`${MONTH_NAMES[curMonth]} Actual`, val:thisAct, color:"#4ade80"}]:[]),
          ].map(({label,val,color})=>(
            <div key={label} style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:11,color:"var(--bz-ink-3)",width:130,flexShrink:0}}>{label}</span>
              <div style={{flex:1,height:8,background:"rgba(255,255,255,0.04)",borderRadius:4,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${Math.min(100,val/barMax*100)}%`,background:color,borderRadius:4,transition:"width 0.4s"}}/>
              </div>
              <span style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-2)",width:90,textAlign:"right",flexShrink:0}}>{fmt2(val)}</span>
            </div>
          ))}
        </div>

        {/* KPI tiles */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8,marginBottom:breakerRows.length>0?14:0}}>
          {[
            {l:`${MONTH_NAMES[lmMonth]} Streams`, v:lastActuals.length, c:"#555"},
            {l:`${MONTH_NAMES[curMonth]} Planned`,  v:thisPlans.length,   c:"#7B9CFF"},
            ...(thisActuals.length>0?[{l:`${MONTH_NAMES[curMonth]} Done`, v:thisActuals.length, c:"#4ade80"}]:[]),
            ...(streamDiff!==0?[{l:"Stream Δ", v:(streamDiff>0?"+":"")+streamDiff, c:streamDiff>0?"#4ade80":"#E8317A"}]:[]),
            ...(projDiff!==null?[{l:"Proj vs Last Act", v:(projDiff>=0?"+":"")+fmt2(projDiff), c:projDiff>=0?"#4ade80":"#E8317A"}]:[]),
          ].map(({l,v,c})=>(
            <div key={l} style={{background:"rgba(0,0,0,0.3)",borderRadius:8,padding:"10px",textAlign:"center"}}>
              <div style={{fontSize:15,fontWeight:900,color:c}}>{v}</div>
              <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:2,textTransform:"uppercase",letterSpacing:1}}>{l}</div>
            </div>
          ))}
        </div>

        {/* Per-breaker rows */}
        {breakerRows.length > 0 && (
          <div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:0,marginBottom:4}}>
              {["Breaker",`${MONTH_NAMES[lmMonth]} Streams`,`${MONTH_NAMES[lmMonth]} Rev`,`${MONTH_NAMES[curMonth]} Planned`,`${MONTH_NAMES[curMonth]} Rev`].map(h=>(
                <div key={h} style={{fontSize:9,fontWeight:700,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,padding:"4px 8px"}}>{h}</div>
              ))}
            </div>
            {breakerRows.map(r=>{
              const bc = BC[r.breaker]||"#888";
              const diff = r.lastRev > 0 ? r.thisRev - r.lastRev : null;
              return (
                <div key={r.breaker} style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr",gap:0,borderTop:"1px solid var(--bz-line)",padding:"6px 0"}}>
                  <div style={{fontSize:12,fontWeight:800,color:bc,padding:"0 8px"}}>{r.breaker}</div>
                  <div style={{fontSize:12,color:"var(--bz-ink-3)",padding:"0 8px"}}>{r.lastStreams} streams</div>
                  <div style={{fontSize:12,color:"var(--bz-ink-2)",padding:"0 8px"}}>{r.lastRev>0?fmt2(r.lastRev):"—"}</div>
                  <div style={{fontSize:12,color:"#7B9CFF",padding:"0 8px"}}>{r.thisPlanned} planned</div>
                  <div style={{fontSize:12,fontWeight:700,padding:"0 8px",color:diff===null?"#555":diff>=0?"#4ade80":"#E8317A"}}>
                    {r.thisRev>0?fmt2(r.thisRev):"—"}
                    {diff!==null&&r.thisRev>0&&<span style={{fontSize:10,marginLeft:4}}>({diff>=0?"+":""}{fmt2(diff)})</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // -- Revenue Gap Advisor --
  function renderGapAdvisor() {
    if (!canSeeFinancials) return null;
    const mKey    = monthKey(curYear, curMonth);
    const target  = parseFloat(monthTargets[mKey]) || 0;
    if (target === 0) return null;

    const mPlans   = monthPlans(curYear, curMonth);
    const mActuals = monthActuals(curYear, curMonth);
    const actRev   = actualRevenue(mActuals);
    const projRev  = projectedRevenue(mPlans);
    const effectiveBase = actRev + projectedRevenue(mPlans.filter(p => p.date > dateStr(today.getFullYear(), today.getMonth(), today.getDate())));
    const gap = target - effectiveBase;
    const isStretch = gap <= 0; // already at or over target

    // Historical avg revenue per stream
    const histRevs = streams.filter(s => parseFloat(s.grossRevenue) > 0).map(s => parseFloat(s.grossRevenue));
    const avgStreamRev = histRevs.length > 0 ? histRevs.reduce((a,b)=>a+b,0)/histRevs.length : 0;

    // Revenue by day of week from history
    const dowRevs = Array.from({length:7},()=>({count:0,total:0}));
    streams.forEach(s => {
      if (!s.date || !s.grossRevenue) return;
      const d = new Date(s.date+"T12:00:00");
      const dow = d.getDay();
      dowRevs[dow].count++;
      dowRevs[dow].total += parseFloat(s.grossRevenue)||0;
    });
    const dowAvg = dowRevs.map(d => d.count>0 ? d.total/d.count : avgStreamRev);
    const DOW_NAMES2 = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

    // Find open days this month (future, no plan yet)
    const todayStr2 = dateStr(today.getFullYear(), today.getMonth(), today.getDate());
    const days = daysInMonth(curYear, curMonth);
    const openDays = [];
    for (let d=1; d<=days; d++) {
      const ds = dateStr(curYear, curMonth, d);
      if (ds <= todayStr2) continue;
      const dow = new Date(ds+"T12:00:00").getDay();
      const existingPlans = plansForDate(ds).length;
      openDays.push({ ds, dow, avgRev: dowAvg[dow] || avgStreamRev, hasPlans: existingPlans > 0 });
    }
    // Sort open days by expected revenue desc
    openDays.sort((a,b) => b.avgRev - a.avgRev);

    // Option A: Add new streams
    const topOpenDays = openDays.filter(d => !d.hasPlans).slice(0, 8);
    let optAStreams = [], optARevenue = 0;
    for (const d of topOpenDays) {
      if (optARevenue >= gap) break;
      optAStreams.push(d);
      optARevenue += d.avgRev;
    }
    const optAGap = Math.max(0, gap - optARevenue);

    // Option B: Upgrade existing planned streams (add a product box)
    const avgBoxValue = Object.values(skuPrices).filter(v=>parseFloat(v)>0).map(v=>parseFloat(v));
    const avgBoxMktVal = avgBoxValue.length > 0 ? avgBoxValue.reduce((a,b)=>a+b,0)/avgBoxValue.length : 0;
    const avgBoxRevLift = avgBoxMktVal * 1.5; // 150% = typical market multiple
    const futurePlans = mPlans.filter(p => p.date > todayStr2);
    const upgradesNeeded = avgBoxRevLift > 0 ? Math.ceil(gap / avgBoxRevLift) : null;

    // Best breaker by avg revenue
    const breakerRevs = {};
    BREAKERS.forEach(b=>{ breakerRevs[b]={count:0,total:0}; });
    streams.forEach(s=>{ if(s.breaker&&s.grossRevenue){breakerRevs[s.breaker].count++;breakerRevs[s.breaker].total+=parseFloat(s.grossRevenue)||0;} });
    const bestBreaker = BREAKERS.sort((a,b)=>(breakerRevs[b].total/Math.max(1,breakerRevs[b].count))-(breakerRevs[a].total/Math.max(1,breakerRevs[a].count)))[0];

    const DOW_SHORT = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

    return (
      <div style={{background:"rgba(123,156,255,0.04)",border:"2px solid rgba(123,156,255,0.15)",borderRadius:14,padding:"20px 22px"}}>
        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontSize:15,fontWeight:900,color:isStretch?"#4ade80":"#7B9CFF"}}>
              {isStretch ? "🔥 Stretch Mode — Let's Go Further" : "🎯 Revenue Gap Advisor"}
            </div>
            <div style={{fontSize:12,color:"var(--bz-ink-3)",marginTop:3}}>
              {isStretch
                ? <>You're <strong style={{color:"#4ade80"}}>{fmt2(Math.abs(gap))} ahead</strong> of your {fmt2(target)} target — here's how to push even further</>
                : <>You need <strong style={{color:"#E8317A"}}>{fmt2(gap)}</strong> more to hit your {fmt2(target)} target</>
              }
            </div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:22,fontWeight:900,color:isStretch?"#4ade80":"#E8317A"}}>{isStretch?"+":""}{fmt2(isStretch?Math.abs(gap):gap)}</div>
            <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>{isStretch?"ahead of target":"gap to close"}</div>
          </div>
        </div>

        {/* Gap bar */}
        <div style={{marginBottom:18}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>
            <span>Current projection: {fmt2(effectiveBase)}</span>
            <span>Target: {fmt2(target)}</span>
          </div>
          <div style={{height:10,background:"rgba(255,255,255,0.04)",borderRadius:5,overflow:"hidden",position:"relative"}}>
            <div style={{position:"absolute",height:"100%",width:`${Math.min(100,effectiveBase/Math.max(target,effectiveBase)*100)}%`,background:isStretch?"linear-gradient(90deg,#4ade80,#22d3ee)":"linear-gradient(90deg,#4ade80,#22d3ee)",borderRadius:5,transition:"width 0.4s"}}/>
            {!isStretch&&<div style={{position:"absolute",right:0,top:0,height:"100%",width:`${Math.min(100,gap/target*100)}%`,background:"rgba(232,49,122,0.25)",borderRadius:"0 5px 5px 0"}}/>}
          </div>
          <div style={{fontSize:11,fontWeight:700,marginTop:4,color:isStretch?"#4ade80":"#E8317A"}}>
            {isStretch
              ? `✅ ${(effectiveBase/target*100).toFixed(0)}% of target — already there! Here's how to go bigger:`
              : `${(effectiveBase/target*100).toFixed(0)}% of target — ${fmt2(gap)} to go`
            }
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>

          {/* Option A: Add streams */}
          {optAStreams.length > 0 && (
            <div style={{background:"rgba(74,222,128,0.05)",border:"1px solid rgba(74,222,128,0.15)",borderRadius:10,padding:"14px 16px"}}>
              <div style={{fontSize:12,fontWeight:800,color:"#4ade80",marginBottom:8}}>
                📅 {isStretch ? `Stack ${optAStreams.length} More Stream${optAStreams.length!==1?"s":""}` : `Option A — Add ${optAStreams.length} Stream${optAStreams.length!==1?"s":""}`}
              </div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:10}}>
                {isStretch
                  ? `Best open days to pile on · est. +${fmt2(optARevenue)}`
                  : `Based on your best open days · est. ${fmt2(optARevenue)} revenue${optAGap>0?` · still ${fmt2(optAGap)} short`:""}`
                }
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                {optAStreams.map((d,i)=>(
                  <div key={d.ds} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 10px",background:"rgba(0,0,0,0.3)",borderRadius:7}}>
                    <div>
                      <span style={{fontSize:11,fontWeight:700,color:"var(--bz-ink)"}}>{DOW_NAMES2[d.dow]} {d.ds.slice(5).replace("-","/")}</span>
                      {bestBreaker&&i===0&&<span style={{fontSize:10,color:"#7B9CFF",marginLeft:6}}>→ {bestBreaker} (highest avg)</span>}
                    </div>
                    <span style={{fontSize:11,fontWeight:700,color:"#4ade80"}}>~{fmt2(d.avgRev)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Option B: Upgrade existing streams */}
          {futurePlans.length > 0 && upgradesNeeded !== null && avgBoxRevLift > 0 && (
            <div style={{background:"rgba(251,191,36,0.05)",border:"1px solid rgba(251,191,36,0.15)",borderRadius:10,padding:"14px 16px"}}>
              <div style={{fontSize:12,fontWeight:800,color:"#FBBF24",marginBottom:8}}>
                📦 {isStretch ? "Load Up Existing Streams" : `Option B — Upgrade ${Math.min(upgradesNeeded,futurePlans.length)} Existing Stream${Math.min(upgradesNeeded,futurePlans.length)!==1?"s":""}`}
              </div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:10}}>
                {isStretch
                  ? `Add more product to planned streams · ~+${fmt2(avgBoxRevLift)} lift each`
                  : `Add ~1 more box to each · est. +${fmt2(avgBoxRevLift)} lift per stream`
                }
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                {futurePlans.slice(0,Math.min(isStretch?5:upgradesNeeded,5)).map(p=>(
                  <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 10px",background:"rgba(0,0,0,0.3)",borderRadius:7}}>
                    <div>
                      <span style={{fontSize:11,fontWeight:700,color:BC[p.breaker]||"#E8317A"}}>{p.streamName||p.breaker}</span>
                      <span style={{fontSize:10,color:"var(--bz-ink-3)",marginLeft:6}}>{p.date}</span>
                    </div>
                    <span style={{fontSize:11,fontWeight:700,color:"#FBBF24"}}>+~{fmt2(avgBoxRevLift)}</span>
                  </div>
                ))}
                {!isStretch && upgradesNeeded > 5 && <div style={{fontSize:10,color:"var(--bz-ink-3)",padding:"4px 10px"}}>+{upgradesNeeded-5} more upgrades needed to fully close gap</div>}
              </div>
            </div>
          )}

          {/* Option C: Mix / Full Send */}
          {optAStreams.length > 0 && futurePlans.length > 0 && (
            <div style={{background:"rgba(232,49,122,0.04)",border:"1px solid rgba(232,49,122,0.12)",borderRadius:10,padding:"14px 16px"}}>
              <div style={{fontSize:12,fontWeight:800,color:"#E8317A",marginBottom:8}}>
                {isStretch ? "🚀 Full Send — Both" : "⚡ Option C — Mix It Up"}
              </div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:10}}>
                {isStretch ? "New streams + loaded product = max month" : "Fastest path to close the gap"}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {optAStreams.slice(0,2).map(d=>(
                  <div key={d.ds} style={{display:"flex",alignItems:"center",gap:8,fontSize:11}}>
                    <span style={{color:"#4ade80",fontWeight:700}}>+stream</span>
                    <span style={{color:"var(--bz-ink)"}}>{DOW_NAMES2[d.dow]} {d.ds.slice(5).replace("-","/")} ~{fmt2(d.avgRev)}</span>
                  </div>
                ))}
                {futurePlans.slice(0,Math.ceil((isStretch?3:upgradesNeeded)/2)).map(p=>(
                  <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,fontSize:11}}>
                    <span style={{color:"#FBBF24",fontWeight:700}}>+upgrade</span>
                    <span style={{color:"var(--bz-ink)"}}>{p.streamName||p.breaker} {p.date} ~+{fmt2(avgBoxRevLift)}</span>
                  </div>
                ))}
                <div style={{marginTop:6,fontSize:12,fontWeight:900,color:"#E8317A"}}>
                  {isStretch
                    ? `Could push to ~${fmt2(effectiveBase + optAStreams.slice(0,2).reduce((s,d)=>s+d.avgRev,0) + futurePlans.slice(0,3).length*avgBoxRevLift)}`
                    : `Est. gap closed: ${fmt2(optAStreams.slice(0,2).reduce((s,d)=>s+d.avgRev,0)+futurePlans.slice(0,Math.ceil(upgradesNeeded/2)).length*avgBoxRevLift)} of ${fmt2(gap)} needed`
                  }
                </div>
              </div>
            </div>
          )}
        </div>

        {avgStreamRev > 0 && (
          <div style={{marginTop:12,fontSize:11,color:"var(--bz-ink-3)"}}>
            Based on your historical avg of {fmt2(avgStreamRev)}/stream across {histRevs.length} logged streams · best day: <strong style={{color:"#7B9CFF"}}>{DOW_NAMES2[dowAvg.indexOf(Math.max(...dowAvg))]}</strong>
          </div>
        )}
      </div>
    );
  }

  // -- Revenue Tiers --
  function renderRevenueTiers() {
    if (!canSeeFinancials) return null;
    const mPlans = monthPlans(curYear, curMonth);
    const mActuals = monthActuals(curYear, curMonth);
    const mkt = totalMonthMkt(curYear, curMonth);
    const actRev = actualRevenue(mActuals);
    if (mkt === 0 && mPlans.length === 0) return null;

    const revTiers = [
      { mult:1.0, label:"1.0x", sublabel:"At Market",   color:"var(--bz-ink-3)", bg:"rgba(107,114,128,0.06)", border:"rgba(107,114,128,0.2)" },
      { mult:1.3, label:"1.3x", sublabel:"Minimum",     color:"#FBBF24", bg:"rgba(251,191,36,0.06)",  border:"rgba(251,191,36,0.2)"  },
      { mult:1.5, label:"1.5x", sublabel:"Good",        color:"#86efac", bg:"rgba(134,239,172,0.06)", border:"rgba(134,239,172,0.2)" },
      { mult:1.7, label:"1.7x", sublabel:"🔥 Great",    color:"#4ade80", bg:"rgba(74,222,128,0.06)",  border:"rgba(74,222,128,0.2)"  },
    ];

    return (
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:14,padding:"16px 20px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)"}}>📊 Revenue Tiers — {MONTH_NAMES[curMonth]}</div>
            {mkt > 0 && <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:2}}>Based on {fmt2(mkt)} total planned product market value</div>}
          </div>
          {actRev > 0 && <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>Actual so far: <strong style={{color:"#4ade80"}}>{fmt2(actRev)}</strong></div>}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
          {revTiers.map(({mult,label,sublabel,color,bg,border})=>{
            const tierRev = mkt > 0 ? mkt * mult : projectedRevenue(mPlans, mult);
            const achieved = actRev >= tierRev;
            const pct = tierRev > 0 ? Math.min(100, actRev/tierRev*100) : 0;
            return (
              <div key={mult} style={{background:bg,border:`1.5px solid ${achieved?"rgba(74,222,128,0.5)":border}`,borderRadius:10,padding:"14px 16px",textAlign:"center",position:"relative"}}>
                {achieved && <div style={{position:"absolute",top:6,right:8,fontSize:10,fontWeight:800,color:"#4ade80"}}>✅ HIT</div>}
                <div style={{fontSize:11,fontWeight:700,color,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{label} {sublabel}</div>
                <div style={{fontSize:22,fontWeight:900,color,marginBottom:6}}>{fmt2(tierRev)}</div>
                <div style={{height:4,background:"rgba(255,255,255,0.06)",borderRadius:2,overflow:"hidden",marginBottom:4}}>
                  <div style={{height:"100%",width:`${pct}%`,background:color,borderRadius:2,transition:"width 0.4s"}}/>
                </div>
                <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>{pct.toFixed(0)}% there{actRev>0?` · ${fmt2(Math.max(0,tierRev-actRev))} to go`:""}</div>
                {/* Set as target button */}
                <button onClick={()=>setMonthTargets(p=>({...p,[monthKey(curYear,curMonth)]:tierRev.toFixed(2)}))}
                  style={{marginTop:8,background:"transparent",border:`1px solid ${border}`,color,borderRadius:6,padding:"3px 10px",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit",width:"100%"}}>
                  Set as Target
                </button>
              </div>
            );
          })}
        </div>
        {mkt === 0 && mPlans.length > 0 && (
          <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:10,textAlign:"center"}}>Add products to your planned streams to see market-based tiers</div>
        )}

        {/* Bazooka Earnings Estimate */}
        {canSeeFinancials && totalMonthMkt(curYear,curMonth) > 0 && (
          <div style={{marginTop:14}}>
            <div style={{fontSize:12,fontWeight:800,color:"#E8317A",marginBottom:10}}>💰 Bazooka Earnings Estimate <span style={{fontSize:10,color:"var(--bz-ink-3)",fontWeight:400,marginLeft:6}}>Gross × 81% = Net · 30/70 split · commission from net</span></div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",minWidth:420}}>
                <thead>
                  <tr>
                    {["Tier","Gross","Net Rev (81%)","IMC 70%","Bazooka 30%"].map(h=>(
                      <th key={h} style={{padding:"6px 10px",borderBottom:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:1,textAlign:h==="Tier"?"left":"right"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {revTiers.map(({mult,label,sublabel,color})=>{
                    const tierRev = mkt > 0 ? mkt * mult : projectedRevenue(mPlans, mult);
                    const e = calcPlanEarnings(tierRev);
                    if (!e) return null;
                    return (
                      <tr key={mult} style={{borderBottom:"1px solid var(--bz-line)"}}>
                        <td style={{padding:"8px 10px",fontSize:12,fontWeight:800,color}}>{label} {sublabel}</td>
                        <td style={{padding:"8px 10px",fontSize:12,color:"var(--bz-ink)",textAlign:"right"}}>{fmt2(e.gross)}</td>
                        <td style={{padding:"8px 10px",fontSize:12,color:"var(--bz-ink)",textAlign:"right"}}>{fmt2(e.netRev)}</td>
                        <td style={{padding:"8px 10px",fontSize:12,color:"#7B9CFF",textAlign:"right"}}>{fmt2(e.imcNet)}</td>
                        <td style={{padding:"8px 10px",fontSize:13,fontWeight:900,color:"#E8317A",textAlign:"right"}}>{fmt2(e.bazNet)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // -- Pace Report --
  function renderPaceReport() {
    const mPlans   = monthPlans(curYear, curMonth);
    const mActuals = monthActuals(curYear, curMonth);
    if (mPlans.length === 0 && mActuals.length === 0) return null;

    const actRev    = actualRevenue(mActuals);
    const todayStr  = dateStr(today.getFullYear(), today.getMonth(), today.getDate());
    const pastPlans   = mPlans.filter(p => p.date < todayStr);  // strictly before today — today's streams aren't due yet
    const todayPlans  = mPlans.filter(p => p.date === todayStr);
    const futurePlans = mPlans.filter(p => p.date > todayStr);
    const projSoFar = projectedRevenue(pastPlans);

    // Per-stream results — match actual to planned stream by date AND breaker
    const streamResults = mPlans.map(p => {
      const actuals = streams.filter(s => s.date===p.date && s.breaker===p.breaker);
      if (!actuals.length) return null;
      const planned = liveRevenue(p);
      const actual  = actuals.reduce((s,a)=>s+(parseFloat(a.grossRevenue)||0),0);
      return { date:p.date, breaker:p.breaker, streamName:p.streamName||p.breaker, planned, actual, diff:actual-planned };
    }).filter(Boolean);
    const totalDiff = streamResults.reduce((s,r)=>s+r.diff,0);

    // State machine — only "behind" if streams were DUE and not met
    let paceStatus;
    if (pastPlans.length === 0 && mActuals.length === 0) paceStatus = "future";
    else if (pastPlans.length > 0 && mActuals.length === 0 && projSoFar > 0) paceStatus = "no-actuals-yet";
    else if (projSoFar === 0) paceStatus = mActuals.length > 0 ? "ahead" : "future";
    else paceStatus = actRev >= projSoFar*1.05 ? "ahead" : actRev >= projSoFar*0.95 ? "on-pace" : "behind";

    const nextPlan = [...futurePlans].sort((a,b)=>a.date.localeCompare(b.date))[0];
    const cfg = {
      "future":         {label:"📅 Month hasn't started yet",      color:"#7B9CFF", bg:"rgba(123,156,255,0.04)", border:"rgba(123,156,255,0.12)"},
      "no-actuals-yet": {label:"⏳ Streams due — log your recaps",  color:"#FBBF24", bg:"rgba(251,191,36,0.04)",  border:"rgba(251,191,36,0.15)"},
      "ahead":          {label:"🟢 Ahead of pace",                  color:"#4ade80", bg:"rgba(74,222,128,0.04)",  border:"rgba(74,222,128,0.15)"},
      "on-pace":        {label:"🟡 On pace",                        color:"#FBBF24", bg:"rgba(251,191,36,0.04)",  border:"rgba(251,191,36,0.15)"},
      "behind":         {label:"🔴 Behind pace",                    color:"#E8317A", bg:"rgba(232,49,122,0.04)",  border:"rgba(232,49,122,0.15)"},
    }[paceStatus];

    return (
      <div style={{background:cfg.bg,border:`2px solid ${cfg.border}`,borderRadius:14,padding:"18px 20px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom: paceCollapsed ? 0 : 12,flexWrap:"wrap",gap:8}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <button onClick={()=>setPaceCollapsed(p=>!p)} style={{background:"none",border:"none",cursor:"pointer",color:cfg.color,fontSize:13,padding:"2px 4px",fontFamily:"inherit",lineHeight:1}}>
              {paceCollapsed ? "▶" : "▼"}
            </button>
            <div>
              <div style={{fontSize:15,fontWeight:900,color:cfg.color}}>{cfg.label}</div>
              <div style={{fontSize:12,color:"var(--bz-ink-3)",marginTop:3}}>
                {paceStatus==="future" && (nextPlan
                  ? <>First planned stream: <strong style={{color:"var(--bz-ink)"}}>{nextPlan.streamName||nextPlan.breaker}</strong> on {nextPlan.date} · {mPlans.length} planned this month</>
                  : <>{mPlans.length} stream{mPlans.length!==1?"s":""} planned — none due yet</>
                )}
                {paceStatus==="no-actuals-yet" && <>{pastPlans.length} stream{pastPlans.length!==1?"s":""} on {pastPlans.map(p=>p.date.slice(5)).join(", ")} — log their recaps to update pace{todayPlans.length>0?` (${todayPlans.length} more planned today)`:""}</>}
                {(paceStatus==="ahead"||paceStatus==="on-pace"||paceStatus==="behind") && <>{streamResults.length} of {mPlans.length} planned streams completed · {mPlans.length - streamResults.length} still ahead</>}
              </div>
            </div>
          </div>
          {canSeeFinancials && mActuals.length > 0 && (
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:24,fontWeight:900,color:cfg.color}}>{fmt2(actRev)}</div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>{projSoFar>0?`vs ${fmt2(projSoFar)} expected by now`:"actual so far"}</div>
            </div>
          )}
        </div>

        {!paceCollapsed && <>

        {/* Future: show potential tiers */}
        {paceStatus==="future" && canSeeFinancials && mPlans.length > 0 && (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8}}>
            {[{l:"Planned",v:mPlans.length+" streams",c:"#7B9CFF"},{l:"1.0x Market",v:fmt2(projectedRevenue(mPlans,1.0)),c:"#555"},{l:"Min 1.5x",v:fmt2(projectedRevenue(mPlans,1.5)),c:"#FBBF24"},{l:"Good 1.7x",v:fmt2(projectedRevenue(mPlans,1.7)),c:"#86efac"},{l:"Great 1.9x",v:fmt2(projectedRevenue(mPlans,1.9)),c:"#4ade80"}].map(({l,v,c})=>(
              <div key={l} style={{background:"rgba(0,0,0,0.3)",borderRadius:8,padding:"10px",textAlign:"center"}}>
                <div style={{fontSize:15,fontWeight:900,color:c}}>{v}</div>
                <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:2,textTransform:"uppercase",letterSpacing:1}}>{l}</div>
              </div>
            ))}
          </div>
        )}

        {/* Active: KPIs + per-stream results */}
        {canSeeFinancials && (paceStatus==="ahead"||paceStatus==="on-pace"||paceStatus==="behind") && (
          <>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8,marginBottom:streamResults.length>0?12:0}}>
              {[
                {l:"Actual",         v:fmt2(actRev),                                                                   c:"#4ade80"},
                ...(projSoFar>0?[{l:"Due by today",   v:fmt2(projSoFar),                                              c:"#FBBF24"}]:[]),
                ...(projSoFar>0?[{l:"Variance",       v:(actRev-projSoFar>=0?"+":"")+fmt2(actRev-projSoFar),           c:actRev>=projSoFar?"#4ade80":"#E8317A"}]:[]),
                ...(streamResults.length>0?[{l:"vs Plan",v:(totalDiff>=0?"+":"")+fmt2(totalDiff),                      c:totalDiff>=0?"#4ade80":"#E8317A"}]:[]),
                ...(mPlans.length-streamResults.length>0?[{l:"Remaining",v:(mPlans.length-streamResults.length)+" streams (incl. today)", c:"#7B9CFF"}]:[]),
              ].map(({l,v,c})=>(
                <div key={l} style={{background:"rgba(0,0,0,0.3)",borderRadius:8,padding:"10px",textAlign:"center"}}>
                  <div style={{fontSize:15,fontWeight:900,color:c}}>{v}</div>
                  <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:2,textTransform:"uppercase",letterSpacing:1}}>{l}</div>
                </div>
              ))}
            </div>
            {streamResults.length > 0 && (
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                {streamResults.map((r,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:"rgba(0,0,0,0.3)",borderRadius:8,flexWrap:"wrap",gap:6}}>
                    <div>
                      <span style={{fontSize:12,fontWeight:700,color:"var(--bz-ink)"}}>{r.streamName}</span>
                      <span style={{fontSize:10,color:"var(--bz-ink-3)",marginLeft:8}}>{r.date}</span>
                    </div>
                    <div style={{display:"flex",gap:10,alignItems:"center"}}>
                      <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Plan: <strong style={{color:"#FBBF24"}}>{fmt2(r.planned)}</strong></span>
                      <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Actual: <strong style={{color:"#4ade80"}}>{fmt2(r.actual)}</strong></span>
                      <span style={{fontSize:12,fontWeight:900,color:r.diff>=0?"#4ade80":"#E8317A"}}>{r.diff>=0?"+":""}{fmt2(r.diff)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        </>}
      </div>
    );
  }

  // -- Month Projection --
  function renderMonthProjection() {
    if (!canSeeFinancials) return null;
    const mPlans   = monthPlans(curYear, curMonth);
    const mActuals = monthActuals(curYear, curMonth);
    if (mActuals.length === 0 || mPlans.length === 0) return null;

    const todayStr    = dateStr(today.getFullYear(), today.getMonth(), today.getDate());
    const pastPlans   = mPlans.filter(p => p.date < todayStr);
    const futurePlans = mPlans.filter(p => p.date >= todayStr);

    const actRev    = actualRevenue(mActuals);
    const projSoFar = projectedRevenue(pastPlans);
    if (projSoFar === 0 || futurePlans.length === 0) return null;

    // Pace ratio: how much above/below expectation are we so far
    const paceRatio = actRev / projSoFar;

    // Project remaining planned streams at the same pace ratio
    const projRemaining  = projectedRevenue(futurePlans) * paceRatio;
    const projMonthTotal = actRev + projRemaining;
    const projMonthFlat  = actRev + projectedRevenue(futurePlans);

    const pctAbove = (paceRatio - 1) * 100;
    const isAhead  = paceRatio >= 1;
    const color    = paceRatio >= 1.05 ? "#4ade80" : paceRatio >= 0.95 ? "#FBBF24" : "#E8317A";

    const daysInMonth = new Date(curYear, curMonth+1, 0).getDate();
    const dayOfMonth  = curYear === today.getFullYear() && curMonth === today.getMonth() ? today.getDate() : daysInMonth;
    const pctMonthElapsed = dayOfMonth / daysInMonth;

    return (
      <div style={{ background:"var(--bz-s1)", border:`1px solid ${color}22`, borderRadius:12, padding:"16px 18px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:8 }}>
          <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)" }}>📈 Month Projection</div>
          <div style={{ fontSize:11, fontWeight:700, color:color, background:`${color}15`, border:`1px solid ${color}33`, borderRadius:20, padding:"3px 12px" }}>
            {isAhead ? "+" : ""}{pctAbove.toFixed(1)}% vs expectation
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom:14 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
            <span style={{ fontSize:10, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Month Progress</span>
            <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{dayOfMonth}/{daysInMonth} days</span>
          </div>
          <div style={{ height:6, background:"var(--bz-s2)", borderRadius:3, overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${pctMonthElapsed*100}%`, background:"#333", borderRadius:3 }}/>
          </div>
        </div>

        {/* Key numbers */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
          <div style={{ background:"var(--bz-bg)", borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
            <div style={{ fontSize:15, fontWeight:900, color:"#4ade80" }}>{fmt2(actRev)}</div>
            <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginTop:3 }}>Actual So Far</div>
            <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>{mActuals.length} stream{mActuals.length!==1?"s":""}</div>
          </div>
          <div style={{ background:"var(--bz-bg)", borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
            <div style={{ fontSize:15, fontWeight:900, color:color }}>{fmt2(projMonthTotal)}</div>
            <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginTop:3 }}>Projected Total</div>
            <div style={{ fontSize:10, color:color, marginTop:2 }}>at current pace</div>
          </div>
          <div style={{ background:"var(--bz-bg)", borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
            <div style={{ fontSize:15, fontWeight:900, color:"var(--bz-ink-2)" }}>{fmt2(projMonthFlat)}</div>
            <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginTop:3 }}>Base Projection</div>
            <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>{futurePlans.length} streams left</div>
          </div>
        </div>

        {/* Variance explanation */}
        <div style={{ marginTop:10, fontSize:11, color:"var(--bz-ink-3)", textAlign:"center" }}>
          {isAhead
            ? `Running ${pctAbove.toFixed(1)}% above plan — projecting ${fmt2(projMonthTotal - projMonthFlat)} extra this month`
            : `Running ${Math.abs(pctAbove).toFixed(1)}% below plan — ${fmt2(projMonthFlat - projMonthTotal)} gap to close`
          }
        </div>
      </div>
    );
  }

  // -- Vacation Modal --
  function renderVacationModal() {
    if (!vacModal) return null;
    return (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setVacModal(false)}>
        <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",borderRadius:16,padding:24,maxWidth:400,width:"100%"}} onClick={e=>e.stopPropagation()}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:15,fontWeight:900,color:"var(--bz-ink)"}}>🏖 Time Off</div>
            <button onClick={()=>setVacModal(false)} style={{background:"none",border:"none",color:"var(--bz-ink-3)",cursor:"pointer",fontSize:20}}>×</button>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Breaker</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {BREAKERS.map(b=>{
                  const bc = BC[b]||"#888";
                  return (
                    <button key={b} onClick={()=>setVacForm(p=>({...p,breaker:b}))}
                      style={{background:vacForm.breaker===b?`${bc}20`:"transparent",color:vacForm.breaker===b?bc:"rgba(255,255,255,0.3)",border:`1.5px solid ${vacForm.breaker===b?bc:"rgba(255,255,255,0.08)"}`,borderRadius:8,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                      {b}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Start Date</div>
                <input type="date" value={vacForm.startDate} onChange={e=>setVacForm(p=>({...p,startDate:e.target.value,endDate:p.endDate<e.target.value?e.target.value:p.endDate}))}
                  style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 10px",fontSize:12,fontFamily:"inherit",outline:"none",width:"100%",boxSizing:"border-box"}}/>
              </div>
              <div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>End Date</div>
                <input type="date" value={vacForm.endDate} min={vacForm.startDate} onChange={e=>setVacForm(p=>({...p,endDate:e.target.value}))}
                  style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 10px",fontSize:12,fontFamily:"inherit",outline:"none",width:"100%",boxSizing:"border-box"}}/>
              </div>
            </div>
            {vacForm.startDate && vacForm.endDate && (
              <div style={{fontSize:12,color:BC[vacForm.breaker]||"#888",fontWeight:700}}>
                {Math.round((new Date(vacForm.endDate+"T12:00:00")-new Date(vacForm.startDate+"T12:00:00"))/86400000)+1} day{Math.round((new Date(vacForm.endDate+"T12:00:00")-new Date(vacForm.startDate+"T12:00:00"))/86400000)+1!==1?"s":""} off
              </div>
            )}
            <div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Note (optional)</div>
              <input value={vacForm.note} onChange={e=>setVacForm(p=>({...p,note:e.target.value}))} placeholder="e.g. Family vacation, Surgery..."
                style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 10px",fontSize:13,fontFamily:"inherit",outline:"none",width:"100%",boxSizing:"border-box"}}/>
            </div>
            <div style={{display:"flex",gap:8,marginTop:4}}>
              <button onClick={saveVacation} disabled={savingVac||!vacForm.startDate||!vacForm.endDate}
                style={{flex:1,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                {savingVac?"Saving...":editVacId?"💾 Update":"🏖 Save Time Off"}
              </button>
              <button onClick={()=>setVacModal(false)} style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:10,padding:"10px 16px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // -- Plan modal --
  function renderModal() {
    if (!modalDate) return null;
    const dayPlans = plansForDate(modalDate);
    const dayActuals = actualForDate(modalDate);
    return (
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={closeModal}>
        <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",borderRadius:16,padding:24,maxWidth:480,width:"100%",maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
            <div style={{fontSize:16,fontWeight:900,color:"var(--bz-ink)"}}>📅 {modalDate}</div>
            <button onClick={closeModal} style={{background:"none",border:"none",color:"var(--bz-ink-3)",cursor:"pointer",fontSize:20}}>×</button>
          </div>

          {/* Existing actuals */}
          {dayActuals.length>0&&(
            <div style={{marginBottom:14,padding:"10px 14px",background:"#0a1a0a",border:"1px solid #4ade8033",borderRadius:8}}>
              <div style={{fontSize:11,fontWeight:700,color:"#4ade80",marginBottom:6}}>✅ Actual Stream{dayActuals.length>1?"s":""} Logged</div>
              {dayActuals.map(a=><div key={a.id} style={{fontSize:12,color:"var(--bz-ink-2)"}}>{a.streamName||a.breaker} — {canSeeFinancials?fmt2(a.grossRevenue):"logged"}</div>)}
            </div>
          )}

          {/* Existing plans for this date */}
          {dayPlans.length>0&&(
            <div style={{marginBottom:14}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-2)",marginBottom:6,textTransform:"uppercase",letterSpacing:1}}>Planned Streams</div>
              {dayPlans.map(p=>(
                <div key={p.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:"var(--bz-s2)",borderRadius:8,marginBottom:6}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:BC[p.breaker]||"#E8317A"}}>{p.streamName||p.breaker}</div>
                    <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>{p.breaker}{p.sessionType?" · "+p.sessionType:""}{canSeeFinancials&&liveRevenue(p)>0?" · "+fmt2(liveRevenue(p)):""}</div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    {canBuildCalendar && <><button onClick={()=>{setEditingId(p.id);setForm({breaker:p.breaker||BREAKERS[0],products:p.products||[{id:uid(),type:"",qty:"1"}],estRevenue:p.estRevenue||"",sessionType:p.sessionType||"",notes:p.notes||"",streamName:p.streamName||"",repeat:"none",repeatDays:[],repeatUntil:""});}} style={{background:"#222",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:6,padding:"3px 8px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Edit</button>
                    <button onClick={()=>deletePlan(p.id,false)} style={{background:"none",border:"1px solid #E8317A33",color:"#E8317A",borderRadius:6,padding:"3px 8px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>✕ This</button>
                    {(p.isRecurring||p.recurringFrom)&&<button onClick={()=>deletePlan(p.id,true)} style={{background:"none",border:"1px solid #E8317A55",color:"#E8317A",borderRadius:6,padding:"3px 8px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>✕ All</button>}</>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add/edit form */}
          <div style={{borderTop:"1px solid var(--bz-line)",paddingTop:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-2)",textTransform:"uppercase",letterSpacing:1}}>{editingId?"Edit Plan":"+ Add Planned Stream"}</div>
              {templates.length > 0 && (
                <button onClick={()=>setShowTemplates(p=>!p)}
                  style={{background:showTemplates?"rgba(251,191,36,0.15)":"rgba(255,255,255,0.04)",color:showTemplates?"#FBBF24":"#888",border:`1px solid ${showTemplates?"rgba(251,191,36,0.3)":"rgba(255,255,255,0.08)"}`,borderRadius:8,padding:"4px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  ⚡ Templates ({templates.length})
                </button>
              )}
            </div>

            {/* Template picker */}
            {showTemplates && (
              <div style={{marginBottom:12,background:"rgba(251,191,36,0.04)",border:"1px solid rgba(251,191,36,0.15)",borderRadius:8,padding:"10px 12px"}}>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:8}}>Tap to apply</div>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  {templates.map(t=>(
                    <div key={t.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                      <button onClick={()=>applyTemplate(t)}
                        style={{flex:1,display:"flex",alignItems:"center",gap:8,background:"rgba(0,0,0,0.3)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:7,padding:"7px 10px",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
                        <span style={{fontSize:13}}>⚡</span>
                        <div>
                          <div style={{fontSize:12,fontWeight:700,color:"var(--bz-ink)"}}>{t.name}</div>
                          <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>{t.breaker}{t.sessionType?" · "+t.sessionType:""}{(t.products||[]).filter(p=>p.type).length>0?" · "+(t.products.filter(p=>p.type).map(p=>p.qty+"× "+p.type).join(", ")):""}</div>
                        </div>
                      </button>
                      <button onClick={()=>deleteTemplate(t.id)} style={{background:"none",border:"none",color:"var(--bz-ink-3)",cursor:"pointer",fontSize:14,padding:"4px 6px",flexShrink:0}}>✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
              <div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Breaker</div>
                <select value={form.breaker} onChange={e=>setForm(p=>({...p,breaker:e.target.value}))} style={{...S2.inp,cursor:"pointer"}}>
                  {BREAKERS.map(b=><option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Channel</div>
                <select value={form.channel||"Bazooka Vault"} onChange={e=>setForm(p=>({...p,channel:e.target.value}))} style={{...S2.inp,cursor:"pointer",color:form.channel&&form.channel!=="Bazooka Vault"?"#7B9CFF":"var(--bz-ink)"}}>
                  {CHANNELS.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Session Type</div>
                <select value={form.sessionType} onChange={e=>setForm(p=>({...p,sessionType:e.target.value}))} style={{...S2.inp,cursor:"pointer"}}>
                  <option value="">-- Select --</option>
                  <option value="day">☀️ Day Break</option>
                  <option value="night">🌙 Night Break</option>
                  <option value="weekend">📅 Weekend Break</option>
                  <option value="singles">🃏 Singles Show</option>
                  <option value="event">🎉 Event</option>
                </select>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>🕐 Start Time</div>
                <input type="time" value={form.startTime||""} onChange={e=>setForm(p=>({...p,startTime:e.target.value}))} style={{...S2.inp,cursor:"pointer"}}/>
              </div>
              <div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>🕑 End Time</div>
                <input type="time" value={form.endTime||""} onChange={e=>setForm(p=>({...p,endTime:e.target.value}))} style={{...S2.inp,cursor:"pointer"}}/>
              </div>
            </div>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Stream Name (optional)</div>
              <input value={form.streamName} onChange={e=>setForm(p=>({...p,streamName:e.target.value}))} placeholder="e.g. Friday Night Break #12" style={S2.inp}/>
            </div>
            {/* Products */}
            <div style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>Products</div>
                <button onClick={()=>setForm(p=>({...p,products:[...p.products,{id:uid(),type:"",qty:"1"}]}))} style={{background:"none",border:"1px solid var(--bz-line-2)",color:"#E8317A",borderRadius:6,padding:"2px 8px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>+ Add</button>
              </div>
              {form.products.map((pr,i)=>(
                <div key={pr.id} style={{display:"grid",gridTemplateColumns:"1fr 70px auto",gap:6,marginBottom:6,alignItems:"center"}}>
                  <select value={pr.type} onChange={e=>setForm(p=>({...p,products:p.products.map(x=>x.id===pr.id?{...x,type:e.target.value}:x)}))} style={{...S2.inp,cursor:"pointer",fontSize:12}}>
                    <option value="">-- Product --</option>
                    {PRODUCT_TYPES.map(pt=><option key={pt} value={pt}>{pt}</option>)}
                  </select>
                  <input type="number" min="1" value={pr.qty} onChange={e=>setForm(p=>({...p,products:p.products.map(x=>x.id===pr.id?{...x,qty:e.target.value}:x)}))} placeholder="Qty" style={{...S2.inp,textAlign:"center",fontSize:12}}/>
                  {form.products.length>1&&<button onClick={()=>setForm(p=>({...p,products:p.products.filter(x=>x.id!==pr.id)}))} style={{background:"none",border:"none",color:"var(--bz-ink-3)",cursor:"pointer",fontSize:16,padding:"0 4px"}}>×</button>}
                </div>
              ))}
            </div>
            {canSeeFinancials&&<div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Est. Revenue ($)</div>
              {(() => {
                const mkt = (form.products||[]).reduce((s,pr)=>s+(parseFloat(skuPrices[pr.type])||0)*(parseInt(pr.qty)||0),0);
                return mkt > 0 ? (
                  <div style={{display:"flex",gap:5,marginBottom:6,flexWrap:"wrap"}}>
                    {[[1.3,"1.3x","#FBBF24"],[1.5,"1.5x","#4ade80"],[1.7,"1.7x 🔥","#E8317A"]].map(([mult,label,color])=>(
                      <button key={mult} onClick={()=>setForm(p=>({...p,estRevenue:(mkt*mult).toFixed(2),estMultiple:String(mult)}))}
                        style={{background:parseFloat(form.estMultiple)===mult?`${color}30`:`${color}15`,color,border:`1px solid ${parseFloat(form.estMultiple)===mult?color:color+"44"}`,borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                        {label} = {fmt2(mkt*mult)}{parseFloat(form.estMultiple)===mult?" ✓":""}
                      </button>
                    ))}
                  </div>
                ) : null;
              })()}
              <input type="text" inputMode="decimal" value={form.estRevenue} onChange={e=>setForm(p=>({...p,estRevenue:e.target.value,estMultiple:""}))} placeholder="Tap a multiple above or enter manually" style={S2.inp}/>
            </div>}
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Notes</div>
              <input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Any notes..." style={S2.inp}/>
            </div>

            {/* Staff on Duty */}
            <div style={{marginBottom:14,padding:"12px 14px",background:"rgba(251,191,36,0.04)",border:"1px solid rgba(251,191,36,0.15)",borderRadius:8}}>
              <div style={{fontSize:11,fontWeight:700,color:"#FBBF24",marginBottom:10}}>👥 Staff on Duty</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {OFFICE_STAFF.map(s=>{
                  const active=(form.staffOnDuty||[]).includes(s.id);
                  return (
                    <button key={s.id} onClick={()=>setForm(p=>({...p,staffOnDuty:active?(p.staffOnDuty||[]).filter(x=>x!==s.id):[...(p.staffOnDuty||[]),s.id]}))}
                      style={{background:active?`${s.color}22`:"transparent",color:active?s.color:"var(--bz-ink-3)",border:`1.5px solid ${active?s.color:"var(--bz-ink-3)"}`,borderRadius:20,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                      {s.name} <span style={{fontSize:9,opacity:0.7}}>{s.role}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Repeat section — only for new events */}
            {!editingId && (
              <div style={{marginBottom:14,padding:"12px 14px",background:"rgba(123,156,255,0.05)",border:"1px solid rgba(123,156,255,0.15)",borderRadius:8}}>
                <div style={{fontSize:11,fontWeight:700,color:"#7B9CFF",marginBottom:10}}>🔁 Repeat</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:form.repeat!=="none"?10:0}}>
                  {[["none","No repeat"],["weekly","Weekly"],["biweekly","Every 2 wks"],["daily","Daily"],["custom","Custom days"]].map(([v,l])=>(
                    <button key={v} onClick={()=>setForm(p=>({...p,repeat:v,repeatDays:[]}))}
                      style={{background:form.repeat===v?"rgba(123,156,255,0.2)":"transparent",color:form.repeat===v?"#7B9CFF":"rgba(255,255,255,0.3)",border:`1px solid ${form.repeat===v?"rgba(123,156,255,0.4)":"rgba(255,255,255,0.06)"}`,borderRadius:20,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}>
                      {l}
                    </button>
                  ))}
                </div>
                {form.repeat==="custom"&&(
                  <div style={{display:"flex",gap:5,marginBottom:10,flexWrap:"wrap"}}>
                    {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d,i)=>(
                      <button key={i} onClick={()=>setForm(p=>({...p,repeatDays:p.repeatDays.includes(i)?p.repeatDays.filter(x=>x!==i):[...p.repeatDays,i]}))}
                        style={{background:form.repeatDays.includes(i)?"rgba(123,156,255,0.25)":"rgba(255,255,255,0.03)",color:form.repeatDays.includes(i)?"#7B9CFF":"rgba(255,255,255,0.3)",border:`1px solid ${form.repeatDays.includes(i)?"rgba(123,156,255,0.4)":"rgba(255,255,255,0.06)"}`,borderRadius:8,padding:"6px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                        {d}
                      </button>
                    ))}
                  </div>
                )}
                {form.repeat!=="none"&&(
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{fontSize:11,color:"var(--bz-ink-3)",flexShrink:0}}>Until:</div>
                    <input type="date" value={form.repeatUntil||dateStr(curYear,curMonth,daysInMonth(curYear,curMonth))} onChange={e=>setForm(p=>({...p,repeatUntil:e.target.value}))} style={{...S2.inp,fontSize:12,padding:"6px 10px"}}/>
                    {(() => {
                      const dates = generateRepeatDates(modalDate||"", form.repeat, form.repeatDays, form.repeatUntil||dateStr(curYear,curMonth,daysInMonth(curYear,curMonth)));
                      return dates.length>0 ? <span style={{fontSize:11,color:"#7B9CFF",fontWeight:700,flexShrink:0}}>+{dates.length} occurrences</span> : null;
                    })()}
                  </div>
                )}
              </div>
            )}
            {/* Save as template */}
            {!editingId && (
              <div style={{marginBottom:12}}>
                {savingTemplate ? (
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <input autoFocus value={templateName} onChange={e=>setTemplateName(e.target.value)}
                      onKeyDown={e=>{ if(e.key==="Enter") saveAsTemplate(templateName); if(e.key==="Escape") setSavingTemplate(false); }}
                      placeholder="Template name (e.g. Dev Friday Night)" style={{...S2.inp,flex:1,fontSize:12,padding:"6px 10px"}}/>
                    <button onClick={()=>saveAsTemplate(templateName)} disabled={!templateName.trim()}
                      style={{background:"rgba(251,191,36,0.15)",color:"#FBBF24",border:"1px solid rgba(251,191,36,0.3)",borderRadius:8,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Save</button>
                    <button onClick={()=>setSavingTemplate(false)}
                      style={{background:"none",border:"none",color:"var(--bz-ink-3)",cursor:"pointer",fontSize:14,flexShrink:0}}>✕</button>
                  </div>
                ) : (
                  <button onClick={()=>setSavingTemplate(true)}
                    style={{background:"transparent",border:"1px dashed rgba(255,255,255,0.08)",color:"var(--bz-ink-3)",borderRadius:8,padding:"6px 14px",fontSize:11,cursor:"pointer",fontFamily:"inherit",width:"100%"}}>
                    ⚡ Save current setup as a template
                  </button>
                )}
              </div>
            )}
            <div style={{display:"flex",gap:8}}>
              <button onClick={savePlan} disabled={saving} style={{flex:1,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:13,fontWeight:800,cursor:saving?"not-allowed":"pointer",fontFamily:"inherit"}}>{saving?"Saving...":editingId?"💾 Update":"📋 Add to Calendar"}</button>
              <button onClick={closeModal} style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:10,padding:"10px 16px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div style={{textAlign:"center",padding:60,color:"var(--bz-ink-3)"}}>Loading calendar...</div>;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {/* Confetti canvas */}
      <canvas ref={confettiCanvas} style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:99999,display:confettiActive?"block":"none"}}/>
      {renderModal()}
      {renderVacationModal()}

      {/* Share Week Modal */}
      {shareWeekModal && (() => {
        const weekDays = Array.from({length:7},(_,i)=>{
          const d = new Date(shareWeekStart+"T12:00:00"); d.setDate(d.getDate()+i);
          return d.toISOString().slice(0,10);
        });
        const DOW_FULL = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
        const weekPlans = weekDays.map(ds=>({ds, plans:plansForDate(ds), actuals:actualForDate(ds)})).filter(d=>d.plans.length>0||d.actuals.length>0);

        function openPrintWindow() {
          const rows = weekDays.map((ds,i)=>{
            const dayPlans = plansForDate(ds);
            const dayActuals = actualForDate(ds);
            if (dayPlans.length===0&&dayActuals.length===0) return "";
            const items = [
              ...dayActuals.map(a=>`<div style="margin:4px 0;padding:6px 10px;background:#e8f5e9;border-left:3px solid #4caf50;border-radius:4px;font-size:13px;">✅ <strong>${a.streamName||a.breaker||"Stream"}</strong>${a.breaker?` · ${a.breaker}`:""}</div>`),
              ...dayPlans.map(p=>`<div style="margin:4px 0;padding:6px 10px;background:#e8eaf6;border-left:3px solid #5c6bc0;border-radius:4px;font-size:13px;">📋 <strong>${p.streamName||p.breaker||"Planned"}</strong>${p.breaker?` · ${p.breaker}`:""}${p.sessionType?` · ${p.sessionType}`:""}${(p.products||[]).filter(pr=>pr.type).length>0?` · ${p.products.filter(pr=>pr.type).map(pr=>pr.qty+"× "+pr.type).join(", ")}`:""}</div>`),
            ].join("");
            return `<tr><td style="padding:10px 14px;font-weight:700;color:#333;white-space:nowrap;vertical-align:top;width:110px;">${DOW_FULL[i]}<br/><span style="font-weight:400;font-size:12px;color:#888;">${ds.slice(5).replace("-","/")}</span></td><td style="padding:10px 14px;">${items}</td></tr>`;
          }).join("");
          const endSun = weekDays[6];
          const html = `<!DOCTYPE html><html><head><title>Bazooka Breaks — Week of ${shareWeekStart}</title><style>body{font-family:'Helvetica Neue',Arial,sans-serif;margin:0;padding:24px;background:#fff;color:#111;}h1{font-size:22px;font-weight:900;color:#E8317A;margin-bottom:4px;}h2{font-size:14px;font-weight:400;color:#888;margin:0 0 20px;}table{width:100%;border-collapse:collapse;}tr{border-bottom:1px solid #eee;}td{vertical-align:top;}@media print{body{padding:12px;}button{display:none!important;}}</style></head><body><div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;"><div><h1>Bazooka Breaks</h1><h2>Week of ${shareWeekStart} – ${endSun}</h2></div><button onclick="window.print()" style="background:#E8317A;color:#fff;border:none;border-radius:8px;padding:10px 20px;font-size:14px;font-weight:700;cursor:pointer;margin-right:8px;">🖨️ Print</button><button onclick="window.close(); if(!window.closed){ document.body.innerHTML='<p style=\\'font-family:sans-serif;padding:40px;text-align:center;color:#888\\'>You can close this tab now.</p>'; }" style="background:#333;color:#fff;border:none;border-radius:8px;padding:10px 20px;font-size:14px;font-weight:700;cursor:pointer;">← Close</button></div><table>${rows}</table></body></html>`;
          const w = window.open("","_blank");
          w.document.write(html);
          w.document.close();
        }

        return (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setShareWeekModal(false)}>
            <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",borderRadius:16,padding:24,maxWidth:500,width:"100%",maxHeight:"85vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                <div>
                  <div style={{fontSize:15,fontWeight:900,color:"var(--bz-ink)"}}>📤 Share Weekly Schedule</div>
                  <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:2}}>Week of {shareWeekStart}</div>
                </div>
                <button onClick={()=>setShareWeekModal(false)} style={{background:"none",border:"none",color:"var(--bz-ink-3)",cursor:"pointer",fontSize:20}}>×</button>
              </div>

              {/* Week picker */}
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
                <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Week starting:</span>
                <input type="date" value={shareWeekStart} onChange={e=>setShareWeekStart(e.target.value)}
                  style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:6,color:"var(--bz-ink)",padding:"5px 8px",fontSize:12,fontFamily:"inherit",outline:"none"}}/>
              </div>

              {/* Preview */}
              <div style={{marginBottom:16,display:"flex",flexDirection:"column",gap:6}}>
                {weekDays.map((ds,i)=>{
                  const dayPlans = plansForDate(ds);
                  const dayActuals = actualForDate(ds);
                  if (dayPlans.length===0&&dayActuals.length===0) return (
                    <div key={ds} style={{display:"flex",gap:10,alignItems:"center",padding:"6px 0",borderBottom:"1px solid var(--bz-line)"}}>
                      <span style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-3)",width:90,flexShrink:0}}>{DOW_FULL[i]} {ds.slice(5)}</span>
                      <span style={{fontSize:11,color:"#8a8a8a"}}>—</span>
                    </div>
                  );
                  return (
                    <div key={ds} style={{padding:"8px 0",borderBottom:"1px solid var(--bz-line)"}}>
                      <div style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-3)",marginBottom:4}}>{DOW_FULL[i]} · {ds.slice(5).replace("-","/")}</div>
                      {dayActuals.map(a=>(
                        <div key={a.id} style={{fontSize:12,color:"#4ade80",marginBottom:2}}>✅ {a.streamName||a.breaker||"Stream"}</div>
                      ))}
                      {dayPlans.map(p=>(
                        <div key={p.id} style={{fontSize:12,color:"#7B9CFF",marginBottom:2}}>
                          📋 {p.streamName||p.breaker} {p.sessionType?`· ${p.sessionType}`:""} {(p.products||[]).filter(pr=>pr.type).length>0?`· ${p.products.filter(pr=>pr.type).map(pr=>pr.qty+"× "+pr.type).join(", ")}` :""}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>

              <div style={{display:"flex",gap:8}}>
                <button onClick={openPrintWindow}
                  style={{flex:1,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                  🖨️ Open & Print
                </button>
                <button onClick={()=>{
                  const lines = weekDays.flatMap((ds,i)=>{
                    const dayPlans = plansForDate(ds);
                    const dayActuals = actualForDate(ds);
                    if (!dayPlans.length&&!dayActuals.length) return [];
                    return [`${DOW_FULL[i]} ${ds.slice(5)}:`,...dayActuals.map(a=>`  ✅ ${a.streamName||a.breaker}`),...dayPlans.map(p=>`  📋 ${p.streamName||p.breaker}${p.sessionType?" · "+p.sessionType:""}${(p.products||[]).filter(pr=>pr.type).length>0?" · "+p.products.filter(pr=>pr.type).map(pr=>pr.qty+"× "+pr.type).join(", "):""}`),""];
                  });
                  navigator.clipboard.writeText(`Bazooka Breaks — Week of ${shareWeekStart}\n\n${lines.join("\n")}`);
                }}
                  style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:10,padding:"11px 16px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
                  📋 Copy Text
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {copyWeekModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setCopyWeekModal(false)}>
          <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",borderRadius:16,padding:24,maxWidth:400,width:"100%"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:900,color:"var(--bz-ink)",marginBottom:4}}>📋 Copy Week</div>
            <div style={{fontSize:12,color:"var(--bz-ink-3)",marginBottom:16}}>Duplicate a week's streams to another week</div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:6}}>Copy FROM (week starting)</div>
              {(() => {
                const weeks = getWeeksInMonth(curYear, curMonth);
                return (
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    {weeks.map(w=>{
                      const wEnd = new Date(w+"T12:00:00"); wEnd.setDate(wEnd.getDate()+6);
                      const wEndStr = wEnd.toISOString().slice(0,10);
                      const wPlans = plans.filter(p=>p.date>=w&&p.date<=wEndStr);
                      if (wPlans.length===0) return null;
                      return (
                        <button key={w} onClick={()=>setCopyWeekSrc(w)}
                          style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:copyWeekSrc===w?"rgba(232,49,122,0.12)":"rgba(0,0,0,0.3)",border:`1px solid ${copyWeekSrc===w?"rgba(232,49,122,0.3)":"rgba(255,255,255,0.06)"}`,borderRadius:8,cursor:"pointer",fontFamily:"inherit",color:"var(--bz-ink)"}}>
                          <span style={{fontSize:12,fontWeight:700}}>Week of {w.slice(5).replace("-","/")}</span>
                          <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>{wPlans.length} stream{wPlans.length!==1?"s":""}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            {copyWeekSrc && (
              <div style={{marginBottom:16}}>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:6}}>Paste TO (week starting)</div>
                <input type="date" value={copyWeekDst} onChange={e=>setCopyWeekDst(e.target.value)}
                  style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",outline:"none",width:"100%",boxSizing:"border-box"}}/>
                {copyWeekSrc && copyWeekDst && (() => {
                  const srcEnd = new Date(copyWeekSrc+"T12:00:00"); srcEnd.setDate(srcEnd.getDate()+6);
                  const wPlans = plans.filter(p=>p.date>=copyWeekSrc&&p.date<=srcEnd.toISOString().slice(0,10));
                  return <div style={{fontSize:11,color:"#7B9CFF",marginTop:6}}>Will create {wPlans.length} new stream{wPlans.length!==1?"s":""}</div>;
                })()}
              </div>
            )}
            <div style={{display:"flex",gap:8}}>
              <button onClick={copyWeek} disabled={!copyWeekSrc||!copyWeekDst||copyingWeek}
                style={{flex:1,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:13,fontWeight:800,cursor:(!copyWeekSrc||!copyWeekDst||copyingWeek)?"not-allowed":"pointer",fontFamily:"inherit",opacity:(!copyWeekSrc||!copyWeekDst)?0.5:1}}>
                {copyingWeek?"Copying...":"📋 Copy Week"}
              </button>
              <button onClick={()=>setCopyWeekModal(false)} style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:10,padding:"10px 16px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* View toggle */}
      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        {[["month","📅 Month"],["quarter","📊 Quarter"]].map(([v,l])=>(
          <button key={v} onClick={()=>setViewMode(v)} style={{background:viewMode===v?"rgba(232,49,122,0.15)":"transparent",color:viewMode===v?"#E8317A":"rgba(255,255,255,0.4)",border:`1.5px solid ${viewMode===v?"#E8317A":"rgba(255,255,255,0.08)"}`,borderRadius:20,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"all 0.2s"}}>{l}</button>
        ))}
        {viewMode==="month"&&(
          <>
            <button onClick={prevMonth} style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink)",borderRadius:8,padding:"6px 14px",cursor:"pointer",fontFamily:"inherit"}}>‹ Prev</button>
            <span style={{fontSize:13,fontWeight:700,color:"var(--bz-ink)",minWidth:140,textAlign:"center"}}>{MONTH_NAMES[curMonth]} {curYear}</span>
            <button onClick={nextMonth} style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink)",borderRadius:8,padding:"6px 14px",cursor:"pointer",fontFamily:"inherit"}}>Next ›</button>
            <button onClick={()=>{setCurYear(today.getFullYear());setCurMonth(today.getMonth());}} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.4)",borderRadius:8,padding:"6px 12px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Today</button>
            {canBuildCalendar && <button onClick={()=>{setCopyWeekSrc("");setCopyWeekDst("");setCopyWeekModal(true);}} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.4)",borderRadius:8,padding:"6px 12px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>📋 Copy Week</button>}
            <button onClick={()=>{
              const todayStr2 = dateStr(today.getFullYear(),today.getMonth(),today.getDate());
              const dow = new Date(todayStr2+"T12:00:00").getDay();
              const sun = new Date(todayStr2+"T12:00:00"); sun.setDate(sun.getDate()-dow);
              setShareWeekStart(sun.toISOString().slice(0,10));
              setShareWeekModal(true);
            }} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.4)",borderRadius:8,padding:"6px 12px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>📤 Share Week</button>
          </>
        )}
      </div>

      {/* Legend */}
      <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:8,height:8,borderRadius:2,background:"#4ade80"}}/><span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Recap logged</span></div>
        <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:8,height:8,borderRadius:2,background:"#7B9CFF"}}/><span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Planned</span></div>
        <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:8,height:8,borderRadius:2,background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.25)"}}/><span style={{fontSize:11,color:"var(--bz-ink-3)"}}>No recap yet</span></div>
        <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:8,height:8,borderRadius:2,background:"#1a0a14",border:"1px solid #E8317A44"}}/><span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Today</span></div>
        <div style={{display:"flex",alignItems:"center",gap:5}}><span style={{fontSize:11}}>🏖</span><span style={{fontSize:11,color:"var(--bz-ink-3)"}}>Time off</span></div>
      </div>

      {viewMode==="month"&&(
        <>
          {renderBurnoutAlerts()}
          {renderTomorrowAlert()}
          {canSeeFinancials && renderPaceReport()}
          {canSeeFinancials && renderMonthProjection()}
          {canSeeFinancials && renderStreamScorecard()}
          {renderCalendar(curYear,curMonth)}

          {/* Weekly product summary — admin only */}
          {canSeeFinancials && (() => {
            // Build weeks for current month
            const days = daysInMonth(curYear,curMonth);
            const weeks = [];
            let week = null;
            for (let d=1; d<=days; d++) {
              const ds = dateStr(curYear,curMonth,d);
              const dow = new Date(ds+"T12:00:00").getDay(); // 0=Sun
              if (!week || dow === 0) {
                if (week) weeks.push(week);
                const weekStart = ds;
                const weekEnd = dateStr(curYear,curMonth,Math.min(d+6-dow,days));
                week = { label:`Week of ${new Date(weekStart+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}`, start:weekStart, end:weekEnd, plans:[] };
              }
              plansForDate(ds).forEach(p => week.plans.push({ ...p, date:ds }));
            }
            if (week) weeks.push(week);

            return (
              <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:14, padding:"16px 18px", marginTop:12 }}>
                <div style={{ fontSize:9, fontWeight:800, color:"#E8317A", textTransform:"uppercase", letterSpacing:"2px", marginBottom:14 }}>📦 Weekly Product Schedule — {MONTH_NAMES[curMonth]}</div>
                <div style={{ display:"grid", gap:10 }}>
                  {weeks.map((w,wi) => {
                    // Aggregate boxes by product type
                    const byProduct = {};
                    let totalBoxes = 0;
                    w.plans.forEach(p => {
                      (p.products||[]).forEach(prod => {
                        if (!prod.type) return;
                        const qty = parseInt(prod.qty)||0;
                        byProduct[prod.type] = (byProduct[prod.type]||0) + qty;
                        totalBoxes += qty;
                      });
                    });
                    const breakers = [...new Set(w.plans.map(p=>p.breaker).filter(Boolean))];
                    const channels = [...new Set(w.plans.map(p=>channelOf(p)))];
                    const streamCount = w.plans.length;

                    return (
                      <div key={wi} style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"12px 14px" }}>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:streamCount>0?10:0, flexWrap:"wrap", gap:6 }}>
                          <div>
                            <div style={{ fontSize:12, fontWeight:700, color:"var(--bz-ink)" }}>{w.label}</div>
                            <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>
                              {streamCount > 0
                                ? `${streamCount} stream${streamCount!==1?"s":""} · ${breakers.join(", ")}`
                                : <span style={{ color:"var(--bz-ink-3)" }}>No streams scheduled</span>}
                            </div>
                            {streamCount > 0 && channels.length > 0 && (
                              <div style={{ display:"flex", gap:4, marginTop:4, flexWrap:"wrap" }}>
                                {channels.map(ch=>(
                                  <span key={ch} style={{ fontSize:9, color:ch==="Bazooka Vault"?"#E8317A":ch==="Orbital Society"?"#7B9CFF":"#FBBF24", background:ch==="Bazooka Vault"?"rgba(232,49,122,0.08)":ch==="Orbital Society"?"rgba(123,156,255,0.08)":"rgba(251,191,36,0.08)", borderRadius:4, padding:"1px 6px" }}>
                                    {ch}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          {totalBoxes > 0 && (
                            <div style={{ background:"rgba(123,156,255,0.1)", border:"1px solid rgba(123,156,255,0.2)", borderRadius:8, padding:"5px 12px", textAlign:"center" }}>
                              <div style={{ fontSize:16, fontWeight:900, color:"#7B9CFF" }}>{totalBoxes}</div>
                              <div style={{ fontSize:9, color:"var(--bz-ink-3)" }}>total boxes</div>
                            </div>
                          )}
                        </div>

                        {Object.keys(byProduct).length > 0 && (
                          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                            {Object.entries(byProduct).sort((a,b)=>b[1]-a[1]).map(([type,qty])=>(
                              <div key={type} style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:6, padding:"5px 10px", display:"flex", alignItems:"center", gap:6 }}>
                                <span style={{ fontSize:11, fontWeight:900, color:"#FBBF24" }}>{qty}×</span>
                                <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{type}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {streamCount === 0 && (
                          <div style={{ fontSize:11, color:"#8a8a8a", fontStyle:"italic" }}>—</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          {canSeeFinancials && renderBestDayPredictor()}
          {canSeeFinancials && renderRevenueTiers()}
          {canSeeFinancials && renderGapAdvisor()}
          {canSeeFinancials && renderProductSummary()}
          {canSeeFinancials && renderMonthSummary()}
          {canSeeFinancials && renderInventoryNeeds()}
        </>
      )}

      {viewMode==="quarter"&&(
        <div style={{display:"grid",gridTemplateColumns:(typeof window!=="undefined"&&window.innerWidth<768)?"1fr":"repeat(3,1fr)",gap:14,alignItems:"start"}}>
          {quarterMonths.map(({y,m})=>(
            <div key={`${y}-${m}`} style={{minWidth:0}}>
              {renderCalendar(y,m,true)}
              {canSeeFinancials&&(()=>{
                const mPlans=monthPlans(y,m),mActuals=monthActuals(y,m);
                const projRev=projectedRevenue(mPlans),actRev=actualRevenue(mActuals);
                if(mPlans.length===0&&mActuals.length===0) return null;
                return(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginTop:6}}>
                    {[{l:"Streams",v:mPlans.length,c:"#7B9CFF"},{l:"Projected",v:fmt2(projRev),c:"#FBBF24"},{l:"Actual",v:fmt2(actRev),c:"#4ade80"}].map(({l,v,c})=>(
                      <div key={l} style={{background:"var(--bz-s2)",borderRadius:6,padding:"8px 10px",textAlign:"center"}}>
                        <div style={{fontSize:14,fontWeight:900,color:c}}>{v}</div>
                        <div style={{fontSize:9,color:"var(--bz-ink-3)",marginTop:2,textTransform:"uppercase",letterSpacing:1}}>{l}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── HERO BREAK DATA ──────────────────────────────────────────────────────────
const HERO_SETS = {
  "Tecmo Bowl": [
    // Featured Autos
    {hero:"BoJax",inspired:"Bo Jackson",tier:"Featured Auto",power:250},
    {hero:"Attak",inspired:"Dak Prescott",tier:"Featured Auto",power:200},
    {hero:"Pukadot",inspired:"Puka Nacua",tier:"Featured Auto",power:200},
    {hero:"Brees",inspired:"Drew Brees",tier:"Featured Auto",power:200},
    {hero:"Fear Himself",inspired:"Lawrence Taylor",tier:"Featured Auto",power:200},
    {hero:"Mossed",inspired:"Randy Moss",tier:"Featured Auto",power:200},
    {hero:"Nigerian Nightmare",inspired:"Christian Okoye",tier:"Featured Auto",power:200},
    {hero:"Marino",inspired:"Dan Marino",tier:"Featured Auto",power:195},
    {hero:"Young-Gunner",inspired:"Steve Young",tier:"Featured Auto",power:195},
    {hero:"Gronk",inspired:"Rob Gronkowski",tier:"Featured Auto",power:195},
    {hero:"Hitt Man",inspired:"Ronnie Lott",tier:"Featured Auto",power:195},
    {hero:"Island Time",inspired:"Darrelle Revis",tier:"Featured Auto",power:195},
    {hero:"Emmitt-164",inspired:"Emmitt Smith",tier:"Featured Auto",power:195},
    {hero:"Troy of Dallas",inspired:"Troy Aikman",tier:"Featured Auto",power:190},
    {hero:"Goggles",inspired:"Eric Dickerson",tier:"Featured Auto",power:190},
    {hero:"Howietzer",inspired:"Howie Long",tier:"Featured Auto",power:190},
    {hero:"Thurmanator",inspired:"Thurman Thomas",tier:"Featured Auto",power:190},
    {hero:"Bus",inspired:"Jerome Bettis",tier:"Featured Auto",power:190},
    {hero:"Machine Gun",inspired:"Jim Kelly",tier:"Featured Auto",power:190},
    {hero:"Cutback",inspired:"Barry Sanders",tier:"Featured Auto",power:185},
    {hero:"Sea-Largent",inspired:"Steve Largent",tier:"Featured Auto",power:185},
    {hero:"Boz",inspired:"Brian Bosworth",tier:"Featured Auto",power:185},
    {hero:"Allenwrench",inspired:"Marcus Allen",tier:"Featured Auto",power:185},
    {hero:"Ultimate Weapon",inspired:"Randall Cunningham",tier:"Featured Auto",power:185},
    {hero:"Shuffler",inspired:"Jim McMahon",tier:"Featured Auto",power:185},
    {hero:"Boom",inspired:"Boomer Esiason",tier:"Featured Auto",power:180},
    {hero:"Dentist",inspired:"Richard Dent",tier:"Featured Auto",power:180},
    {hero:"Vinniverde",inspired:"Vinny Testaverde",tier:"Featured Auto",power:180},
    {hero:"Afterbern",inspired:"Bernie Kosar",tier:"Featured Auto",power:180},
    {hero:"Sack King",inspired:"Bruce Smith",tier:"Featured Auto",power:180},
    {hero:"Incredible Faulk",inspired:"Marshall Faulk",tier:"Featured Auto",power:180},
    {hero:"Mann-O-War",inspired:"Charles Mann",tier:"Featured Auto",power:175},
    {hero:"Timmy",inspired:"Tim Brown",tier:"Featured Auto",power:175},
    {hero:"Haley's Comet",inspired:"Charles Haley",tier:"Featured Auto",power:175},
    {hero:"Ickey",inspired:"Ickey Woods",tier:"Featured Auto",power:175},
    {hero:"Full Moon",inspired:"Warren Moon",tier:"Featured Auto",power:175},
    {hero:"Philament",inspired:"Phil Simms",tier:"Featured Auto",power:175},
    {hero:"Key Keeper",inspired:"Luke Kuechly",tier:"Featured Auto",power:170},
    {hero:"T.D.",inspired:"Tony Dorsett",tier:"Featured Auto",power:170},
    {hero:"Sterling",inspired:"Sterling Sharpe",tier:"Featured Auto",power:170},
    {hero:"Too Tall",inspired:'Ed "Too Tall" Jones',tier:"Featured Auto",power:170},
    {hero:"Fridge",inspired:"William Perry",tier:"Featured Auto",power:170},
    {hero:"Samurai Mike",inspired:"Mike Singletary",tier:"Featured Auto",power:170},
    {hero:"Monk",inspired:"Art Monk",tier:"Featured Auto",power:165},
    {hero:"Krieghawk",inspired:"Dave Krieg",tier:"Featured Auto",power:165},
    {hero:"Undrafted Wrecking Ball",inspired:"Gary Clark",tier:"Featured Auto",power:165},
    {hero:"Highjump",inspired:"Alonzo Highsmith",tier:"Featured Auto",power:165},
    {hero:"Curt Locker",inspired:"Curt Warner",tier:"Featured Auto",power:165},
    {hero:"Calf Roper",inspired:"Eric Metcalf",tier:"Featured Auto",power:165},
    {hero:"Novacaine",inspired:"Jay Novacek",tier:"Featured Auto",power:160},
    {hero:"Carrier",inspired:"Mark Carrier",tier:"Featured Auto",power:160},
    {hero:"Amigo One",inspired:"Mark Jackson",tier:"Featured Auto",power:160},
    {hero:"Quick Draw",inspired:"Mike Quick",tier:"Featured Auto",power:160},
    {hero:"Bank Robber",inspired:"Carl Banks",tier:"Featured Auto",power:160},
    {hero:"Claymore",inspired:"Clay Matthews Jr.",tier:"Featured Auto",power:160},
    {hero:"Biscuit",inspired:"Cornelius Bennett",tier:"Featured Auto",power:155},
    {hero:"Underdoug",inspired:"Doug Williams",tier:"Featured Auto",power:155},
    {hero:"Mountain Drew",inspired:"Drew Bledsoe",tier:"Featured Auto",power:155},
    {hero:"Dwight Noise",inspired:"Dwight Stephenson",tier:"Featured Auto",power:155},
    {hero:"Green Light",inspired:"Harold Green",tier:"Featured Auto",power:155},
    {hero:"K-Mack",inspired:"Kevin Mack",tier:"Featured Auto",power:155},
    {hero:"Pepper Spray",inspired:"Pepper Johnson",tier:"Featured Auto",power:150},
    {hero:"The Manster",inspired:"Randy White",tier:"Featured Auto",power:150},
    {hero:"Woodchipper",inspired:"Rod Woodson",tier:"Featured Auto",power:150},
    {hero:"Gaultfather",inspired:"Willie Gault",tier:"Featured Auto",power:150},
    {hero:"Jolly Roger",inspired:"Roger Craig",tier:"Featured Auto",power:150},
    // Highlighted
    {hero:"Severning",inspired:"Tom Brady",tier:"Highlighted",power:200},
    {hero:"Sweetest of Sweet",inspired:"Walter Payton",tier:"Highlighted",power:200},
    {hero:"Neon",inspired:"Deion Sanders",tier:"Highlighted",power:195},
    {hero:"Sheriff",inspired:"Peyton Manning",tier:"Highlighted",power:195},
    {hero:"Joe Cool",inspired:"Joe Montana",tier:"Highlighted",power:195},
    {hero:"Hands",inspired:"Jerry Rice",tier:"Highlighted",power:190},
    // Base
    {hero:"Joe Cool",inspired:"Joe Montana",tier:"Base",power:190},
    {hero:"Hands",inspired:"Jerry Rice",tier:"Base",power:185},
    {hero:"Merlomes",inspired:"Patrick Mahomes",tier:"Base",power:185},
    {hero:"Dart-Board",inspired:"Jaxson Dart",tier:"Base",power:185},
    {hero:"Bison",inspired:"Josh Allen",tier:"Base",power:185},
    {hero:"Warp",inspired:"Lamar Jackson",tier:"Base",power:185},
    {hero:"Bayou",inspired:"Ja'Marr Chase",tier:"Base",power:185},
    {hero:"J-Jetts",inspired:"Justin Jefferson",tier:"Base",power:185},
    {hero:"Reindeer Hunter",inspired:"Myles Garrett",tier:"Base",power:180},
    {hero:"Swervin'",inspired:"Michael Irvin",tier:"Base",power:180},
    {hero:"MoD",inspired:"Reggie White",tier:"Base",power:180},
    {hero:"Quads",inspired:"Saquon Barkley",tier:"Base",power:180},
    {hero:"Darn Old",inspired:"Sam Darnold",tier:"Base",power:180},
    {hero:"Phoenix",inspired:"Bo Nix",tier:"Base",power:180},
    {hero:"Jax-In-The-Box",inspired:"Jaxon Smith-Njigba",tier:"Base",power:175},
    {hero:"Eagle-Eye",inspired:"Jalen Hurts",tier:"Base",power:175},
    {hero:"Shrouded",inspired:"C.J. Stroud",tier:"Base",power:175},
    {hero:"Myracle",inspired:"Jahmyr Gibbs",tier:"Base",power:175},
    {hero:"Skatter",inspired:"Cam Skattebo",tier:"Base",power:175},
    {hero:"Warden",inspired:"Cam Ward",tier:"Base",power:175},
    {hero:"Yeti",inspired:"Travis Kelce",tier:"Base",power:170},
    {hero:"BrockNess",inspired:"Brock Bowers",tier:"Base",power:170},
    {hero:"McArmyKnife",inspired:"Christian McCaffrey",tier:"Base",power:170},
    {hero:"Muffin Man",inspired:"Baker Mayfield",tier:"Base",power:170},
    {hero:"Youngblood",inspired:"Bryce Young",tier:"Base",power:170},
    {hero:"Switchblade",inspired:"Travis Hunter",tier:"Base",power:170},
    {hero:"McVillain",inspired:"Tetairoa McMillan",tier:"Base",power:165},
    {hero:"Quarter Staff",inspired:"Matthew Stafford",tier:"Base",power:165},
    {hero:"Judkernaught",inspired:"Quinshon Judkins",tier:"Base",power:165},
    {hero:"Jeanetic",inspired:"Ashton Jeanty",tier:"Base",power:165},
    {hero:"Coinslot",inspired:"Daniel Jones",tier:"Base",power:165},
    {hero:"Chanesaw",inspired:"De'Von Achane",tier:"Base",power:165},
    {hero:"Mr. Irrelevant",inspired:"Brock Purdy",tier:"Base",power:160},
    {hero:"Shadowstrike",inspired:"Davante Adams",tier:"Base",power:160},
    {hero:"Shepherd",inspired:"CeeDee Lamb",tier:"Base",power:160},
    {hero:"Scary",inspired:"Terry McLaurin",tier:"Base",power:160},
    {hero:"Lawman",inspired:"Trevor Lawrence",tier:"Base",power:160},
    {hero:"Buttman",inspired:"Marion Butts",tier:"Base",power:160},
    {hero:"Criscross",inspired:"Cris Carter",tier:"Base",power:155},
    {hero:"Rockhead",inspired:"Chris Doleman",tier:"Base",power:155},
    {hero:"Brawn",inspired:"A.J. Brown",tier:"Base",power:155},
    {hero:"DeVaulta",inspired:"DeVonta Smith",tier:"Base",power:155},
    {hero:"Hot Sauce",inspired:"Sauce Gardner",tier:"Base",power:155},
    {hero:"First Leap",inspired:"LeRoy Butler",tier:"Base",power:155},
    {hero:"Friday",inspired:"Deebo Samuel",tier:"Base",power:150},
    {hero:"Barreler",inspired:"Cooper Kupp",tier:"Base",power:150},
    {hero:"Watterslide",inspired:"Ricky Watters",tier:"Base",power:150},
    {hero:"Cannon",inspired:"Rich Gannon",tier:"Base",power:150},
    {hero:"Furnest",inspired:"Earnest Byner",tier:"Base",power:150},
    {hero:"Hot Rod",inspired:"Rodney Hampton",tier:"Base",power:150},
    {hero:"Golden Bullet",inspired:"Matthew Golden",tier:"Base",power:145},
    {hero:"Billiard",inspired:"Dalton Hilliard",tier:"Base",power:145},
    {hero:"Ryptillian",inspired:"Mark Rypien",tier:"Base",power:145},
    {hero:"Flippa",inspired:"Flipper Anderson",tier:"Base",power:145},
    {hero:"Slaughterhouse",inspired:"Webster Slaughter",tier:"Base",power:145},
    {hero:"Hammer",inspired:"Omarion Hampton",tier:"Base",power:145},
    {hero:"Majik Man",inspired:"Don Majkowski",tier:"Base",power:140},
    {hero:"A Bear",inspired:"Bobby Hebert",tier:"Base",power:140},
    {hero:"Moose",inspired:"Daryl Johnston",tier:"Base",power:140},
    {hero:"Hillicopter",inspired:"Drew Hill",tier:"Base",power:140},
  ],
};

const TIER_CFG = {
  "Featured Auto": { color:"#FBBF24", bg:"rgba(251,191,36,0.08)", border:"rgba(251,191,36,0.25)", badge:"⭐ Featured Auto" },
  "Highlighted":   { color:"#C084FC", bg:"rgba(192,132,252,0.08)", border:"rgba(192,132,252,0.25)", badge:"💜 Highlighted" },
  "Base":          { color:"#60A5FA", bg:"rgba(96,165,250,0.06)",  border:"rgba(96,165,250,0.2)",  badge:"🔵 Base" },
};

// ── BOBA SELLER TOOLS ────────────────────────────────────────────────────────
function BobaPrimerSection({ data, isAdmin, save }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(data.primer||"");
  const [copied,  setCopied]  = useState(false);
  useEffect(()=>{ setDraft(data.primer||""); },[data.primer]);
  async function savePrimer() { await save({ ...data, primer:draft }); setEditing(false); }
  function copy() { navigator.clipboard.writeText(data.primer||"").then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2500); }); }
  return (
    <div style={{ ...S.card }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:0 }}>
        <SectionLabel t="🎙 Game Primer"/>
        <div style={{ display:"flex", gap:8 }}>
          {data.primer && <Btn onClick={copy}>{copied?"✅ Copied":"📋 Copy"}</Btn>}
          {isAdmin && <Btn variant="ghost" onClick={()=>{ setDraft(data.primer||""); setEditing(!editing); }}>{editing?"Cancel":"✏️ Edit"}</Btn>}
        </div>
      </div>
      <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:10 }}>What to say when a new viewer asks what BoBA is</div>
      {editing ? (
        <><textarea value={draft} onChange={e=>setDraft(e.target.value)} rows={10} style={{ ...S.inp, width:"100%", resize:"vertical", lineHeight:1.7, fontSize:13, marginBottom:10 }}/><Btn onClick={savePrimer} disabled={!draft.trim()}>💾 Save</Btn></>
      ) : data.primer ? (
        <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"16px 18px", whiteSpace:"pre-wrap", fontSize:13, color:"var(--bz-ink-2)", lineHeight:1.8, maxHeight:360, overflowY:"auto" }}>{data.primer}</div>
      ) : (
        <div style={{ textAlign:"center", padding:"28px 0", color:"#8a8a8a", fontSize:12 }}>{isAdmin ? <Btn onClick={()=>setEditing(true)}>+ Write Game Primer</Btn> : "No game primer added yet"}</div>
      )}
    </div>
  );
}

function BobaWeaponsSection({ data, isAdmin, save }) {
  const [editIdx, setEditIdx] = useState(null);
  const [draft,   setDraft]   = useState({ name:"", rarity:"", description:"", tip:"" });
  const [adding,  setAdding]  = useState(false);
  const [copied,  setCopied]  = useState(null);
  const WEAPON_COLORS = { Steel:"#94A3B8", Brawl:"#EF4444", Fire:"#F97316", Ice:"#38BDF8", Glow:"#4ade80", Hex:"#A78BFA", Gum:"#EC4899", Super:"#FBBF24" };
  function startAdd() { setDraft({ name:"", rarity:"", description:"", tip:"" }); setAdding(true); setEditIdx(null); }
  function startEdit(i) { setDraft({ ...data.weapons[i] }); setEditIdx(i); setAdding(false); }
  async function saveWeapon() {
    const weapons = [...(data.weapons||[])];
    if (editIdx!==null) weapons[editIdx]=draft; else weapons.push({ ...draft, id:Date.now() });
    await save({ ...data, weapons }); setAdding(false); setEditIdx(null);
  }
  async function deleteWeapon(i) { if (!window.confirm(`Delete ${data.weapons[i].name}?`)) return; await save({ ...data, weapons:(data.weapons||[]).filter((_,idx)=>idx!==i) }); }
  function copy(text, key) { navigator.clipboard.writeText(text).then(()=>{ setCopied(key); setTimeout(()=>setCopied(null),2500); }); }
  return (
    <div style={{ ...S.card }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <SectionLabel t="⚔️ Weapon Guide"/>
        {isAdmin && <Btn variant="ghost" onClick={startAdd}>+ Add Weapon</Btn>}
      </div>
      <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>Rarity ladder from common to 1/1 — know what every hit means</div>
      {(adding||editIdx!==null) && (
        <div style={{ background:"var(--bz-bg)", border:"1px solid rgba(232,49,122,0.2)", borderRadius:10, padding:"16px", marginBottom:14 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
            <div><label style={S.lbl}>Weapon Name</label><input value={draft.name} onChange={e=>setDraft(p=>({...p,name:e.target.value}))} placeholder="e.g. Hex" style={S.inp}/></div>
            <div><label style={S.lbl}>Rarity / Print Run</label><input value={draft.rarity} onChange={e=>setDraft(p=>({...p,rarity:e.target.value}))} placeholder="e.g. Inspired Ink /10" style={S.inp}/></div>
          </div>
          <div style={{ marginBottom:10 }}><label style={S.lbl}>Description</label><textarea value={draft.description} onChange={e=>setDraft(p=>({...p,description:e.target.value}))} rows={3} style={{ ...S.inp, width:"100%", resize:"vertical", fontSize:13 }}/></div>
          <div><label style={S.lbl}>On-Stream Tip</label><input value={draft.tip} onChange={e=>setDraft(p=>({...p,tip:e.target.value}))} placeholder="What to say when this hits..." style={S.inp}/></div>
          <div style={{ display:"flex", gap:8, marginTop:10 }}><Btn onClick={saveWeapon} disabled={!draft.name.trim()}>💾 Save</Btn><Btn variant="ghost" onClick={()=>{ setAdding(false); setEditIdx(null); }}>Cancel</Btn></div>
        </div>
      )}
      {(data.weapons||[]).length===0&&!adding && <div style={{ textAlign:"center", padding:"20px 0", color:"#8a8a8a", fontSize:12 }}>{isAdmin?"Add the weapon types":"No weapons documented yet"}</div>}
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {(data.weapons||[]).map((w,i)=>{
          const wc = WEAPON_COLORS[w.name]||"#888";
          return (
            <div key={w.id||i} style={{ background:"var(--bz-bg)", border:`1px solid ${wc}22`, borderLeft:`3px solid ${wc}`, borderRadius:10, padding:"12px 16px" }}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:6 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:14, fontWeight:900, color:wc }}>{w.name}</span>
                  {w.rarity && <span style={{ fontSize:10, color:"var(--bz-ink-3)", background:"var(--bz-s2)", borderRadius:5, padding:"2px 8px" }}>{w.rarity}</span>}
                </div>
                <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                  {w.tip && <button onClick={()=>copy(w.tip,i)} style={{ background:`${wc}18`, border:`1px solid ${wc}33`, color:wc, borderRadius:6, padding:"3px 10px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{copied===i?"✅":"📋"} Copy Tip</button>}
                  {isAdmin && <><button onClick={()=>startEdit(i)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:6, padding:"3px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>✏️</button><button onClick={()=>deleteWeapon(i)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"#8a8a8a", borderRadius:6, padding:"3px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>✕</button></>}
                </div>
              </div>
              {w.description && <div style={{ fontSize:12, color:"var(--bz-ink-2)", lineHeight:1.6, marginBottom:w.tip?6:0 }}>{w.description}</div>}
              {w.tip && <div style={{ fontSize:11, color:wc, fontStyle:"italic", opacity:0.8 }}>💬 "{w.tip}"</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BobaHypeLinesSection({ data, isAdmin, save }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState((data.hypelines||[]).join("\n"));
  const [copied,  setCopied]  = useState(null);
  useEffect(()=>{ setDraft((data.hypelines||[]).join("\n")); },[data.hypelines]);
  async function saveLines() { await save({ ...data, hypelines:draft.split("\n").map(l=>l.trim()).filter(Boolean) }); setEditing(false); }
  function copyLine(line,i) { navigator.clipboard.writeText(line).then(()=>{ setCopied(i); setTimeout(()=>setCopied(null),2000); }); }
  return (
    <div style={{ ...S.card }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <SectionLabel t="⚡ Hype Lines"/>
        {isAdmin && <Btn variant="ghost" onClick={()=>{ setDraft((data.hypelines||[]).join("\n")); setEditing(!editing); }}>{editing?"Cancel":"✏️ Edit"}</Btn>}
      </div>
      <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:10 }}>Tap to copy instantly during a pull — one per line</div>
      {editing ? (
        <><textarea value={draft} onChange={e=>setDraft(e.target.value)} rows={8} style={{ ...S.inp, width:"100%", resize:"vertical", lineHeight:1.7, fontSize:13, fontFamily:"inherit", marginBottom:10 }}/><Btn onClick={saveLines} disabled={!draft.trim()}>💾 Save</Btn></>
      ) : (data.hypelines||[]).length>0 ? (
        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          {(data.hypelines||[]).map((line,i)=>(
            <button key={i} onClick={()=>copyLine(line,i)} style={{ background:copied===i?"rgba(74,222,128,0.12)":"rgba(255,255,255,0.04)", border:`1px solid ${copied===i?"rgba(74,222,128,0.3)":"var(--bz-line-2)"}`, color:copied===i?"#4ade80":"var(--bz-ink-2)", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}>
              {copied===i?"✅ Copied!":'"'+line+'"'}
            </button>
          ))}
        </div>
      ) : <div style={{ textAlign:"center", padding:"20px 0", color:"#8a8a8a", fontSize:12 }}>{isAdmin?<Btn onClick={()=>setEditing(true)}>+ Add Hype Lines</Btn>:"No hype lines yet"}</div>}
    </div>
  );
}

function BobaFAQSection({ data, isAdmin, save }) {
  const [openIdx, setOpenIdx] = useState(null);
  const [adding,  setAdding]  = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [draft,   setDraft]   = useState({ q:"", a:"" });
  const [copied,  setCopied]  = useState(null);
  async function saveQA() { const faq=[...(data.faq||[])]; if(editIdx!==null)faq[editIdx]=draft;else faq.push({...draft,id:Date.now()}); await save({...data,faq}); setAdding(false); setEditIdx(null); }
  async function deleteQA(i) { if(!window.confirm("Delete?"))return; await save({...data,faq:(data.faq||[]).filter((_,idx)=>idx!==i)}); }
  function copy(text,i) { navigator.clipboard.writeText(text).then(()=>{ setCopied(i); setTimeout(()=>setCopied(null),2500); }); }
  return (
    <div style={{ ...S.card }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <SectionLabel t="❓ Buyer FAQ"/>
        {isAdmin && <Btn variant="ghost" onClick={()=>{ setDraft({q:"",a:""}); setAdding(true); setEditIdx(null); }}>+ Add Q&A</Btn>}
      </div>
      <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>Scripted answers for common viewer questions</div>
      {(adding||editIdx!==null) && (
        <div style={{ background:"var(--bz-bg)", border:"1px solid rgba(232,49,122,0.2)", borderRadius:10, padding:"16px", marginBottom:14 }}>
          <div style={{ marginBottom:10 }}><label style={S.lbl}>Question</label><input value={draft.q} onChange={e=>setDraft(p=>({...p,q:e.target.value}))} placeholder="e.g. Are the autographs real?" style={S.inp}/></div>
          <div><label style={S.lbl}>Answer</label><textarea value={draft.a} onChange={e=>setDraft(p=>({...p,a:e.target.value}))} rows={4} style={{ ...S.inp, width:"100%", resize:"vertical", lineHeight:1.7, fontSize:13 }}/></div>
          <div style={{ display:"flex", gap:8, marginTop:10 }}><Btn onClick={saveQA} disabled={!draft.q.trim()||!draft.a.trim()}>💾 Save</Btn><Btn variant="ghost" onClick={()=>{ setAdding(false); setEditIdx(null); }}>Cancel</Btn></div>
        </div>
      )}
      {(data.faq||[]).length===0&&!adding&&<div style={{ textAlign:"center", padding:"20px 0", color:"#8a8a8a", fontSize:12 }}>{isAdmin?"Add common viewer questions":"No FAQ entries yet"}</div>}
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {(data.faq||[]).map((item,i)=>(
          <div key={item.id||i} style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, overflow:"hidden" }}>
            <div onClick={()=>setOpenIdx(openIdx===i?null:i)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 16px", cursor:"pointer" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}><span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{openIdx===i?"▼":"▶"}</span><span style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)" }}>{item.q}</span></div>
              {isAdmin && <div style={{ display:"flex", gap:6 }} onClick={e=>e.stopPropagation()}>
                <button onClick={()=>{ setDraft({...item}); setEditIdx(i); setAdding(false); }} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:5, padding:"2px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>✏️</button>
                <button onClick={()=>deleteQA(i)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"#8a8a8a", borderRadius:5, padding:"2px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>✕</button>
              </div>}
            </div>
            {openIdx===i && <div style={{ padding:"0 16px 14px", borderTop:"1px solid var(--bz-line)" }}>
              <div style={{ fontSize:13, color:"var(--bz-ink-2)", lineHeight:1.7, whiteSpace:"pre-wrap", marginTop:10, marginBottom:10 }}>{item.a}</div>
              <button onClick={()=>copy(item.a,i)} style={{ background:copied===i?"rgba(74,222,128,0.1)":"rgba(123,156,255,0.08)", border:`1px solid ${copied===i?"rgba(74,222,128,0.25)":"rgba(123,156,255,0.2)"}`, color:copied===i?"#4ade80":"#7B9CFF", borderRadius:6, padding:"5px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                {copied===i?"✅ Copied":"📋 Copy Answer"}
              </button>
            </div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function BobaSetGuidesSection({ data, isAdmin, save }) {
  const [editIdx, setEditIdx] = useState(null);
  const [draft,   setDraft]   = useState({ name:"", overview:"", allSkus:"", hobbyOnly:"", doubleMegaOnly:"", notes:"" });
  const [adding,  setAdding]  = useState(false);
  const [openIdx, setOpenIdx] = useState(0); // first set open by default
  const [copied,  setCopied]  = useState(null);

  function startAdd() { setDraft({ name:"", overview:"", allSkus:"", hobbyOnly:"", doubleMegaOnly:"", notes:"" }); setAdding(true); setEditIdx(null); }
  function startEdit(i) { setDraft({ ...data.sets[i] }); setEditIdx(i); setAdding(false); }
  async function saveSet() {
    const sets = [...(data.sets||[])];
    if (editIdx!==null) sets[editIdx]=draft; else sets.push({ ...draft, id:Date.now() });
    await save({ ...data, sets }); setAdding(false); setEditIdx(null);
  }
  async function deleteSet(i) { if(!window.confirm(`Delete "${data.sets[i].name}"?`))return; await save({ ...data, sets:(data.sets||[]).filter((_,idx)=>idx!==i) }); }
  function copy(text,key) { navigator.clipboard.writeText(text).then(()=>{ setCopied(key); setTimeout(()=>setCopied(null),2500); }); }

  return (
    <div style={{ ...S.card }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <SectionLabel t="📦 Set Guides"/>
        {isAdmin && <Btn variant="ghost" onClick={startAdd}>+ Add Set</Btn>}
      </div>

      {(adding||editIdx!==null) && (
        <div style={{ background:"var(--bz-bg)", border:"1px solid rgba(232,49,122,0.2)", borderRadius:10, padding:"16px", marginBottom:14 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
            <div><label style={S.lbl}>Set Name</label><input value={draft.name} onChange={e=>setDraft(p=>({...p,name:e.target.value}))} placeholder="e.g. Tecmo Bowl Edition" style={S.inp}/></div>
            <div><label style={S.lbl}>Overview</label><input value={draft.overview} onChange={e=>setDraft(p=>({...p,overview:e.target.value}))} placeholder="e.g. 130 heroes, football legends" style={S.inp}/></div>
          </div>
          {[["allSkus","Found in All SKUs"],["hobbyOnly","Hobby Only"],["doubleMegaOnly","Double Mega Only"],["notes","Streamer Notes"]].map(([k,l])=>(
            <div key={k} style={{ marginBottom:10 }}>
              <label style={S.lbl}>{l}</label>
              <textarea value={draft[k]} onChange={e=>setDraft(p=>({...p,[k]:e.target.value}))} rows={3} style={{ ...S.inp, width:"100%", resize:"vertical", fontSize:12, lineHeight:1.6 }}/>
            </div>
          ))}
          <div style={{ display:"flex", gap:8 }}><Btn onClick={saveSet} disabled={!draft.name.trim()}>💾 Save</Btn><Btn variant="ghost" onClick={()=>{ setAdding(false); setEditIdx(null); }}>Cancel</Btn></div>
        </div>
      )}

      {(data.sets||[]).length===0&&!adding && <div style={{ textAlign:"center", padding:"20px 0", color:"#8a8a8a", fontSize:12 }}>{isAdmin?"Add set guides":"No set guides yet"}</div>}

      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {(data.sets||[]).map((s,i)=>{
          const isOpen = openIdx===i;
          return (
            <div key={s.id||i} style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, overflow:"hidden" }}>
              <div onClick={()=>setOpenIdx(isOpen?null:i)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 16px", cursor:"pointer" }}>
                <div>
                  <span style={{ fontSize:14, fontWeight:800, color:"var(--bz-ink)" }}>{s.name}</span>
                  {s.overview && <span style={{ fontSize:11, color:"var(--bz-ink-3)", marginLeft:10 }}>{s.overview}</span>}
                </div>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  {isAdmin && <div onClick={e=>e.stopPropagation()} style={{ display:"flex", gap:6 }}>
                    <button onClick={()=>startEdit(i)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:5, padding:"2px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>✏️</button>
                    <button onClick={()=>deleteSet(i)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"#8a8a8a", borderRadius:5, padding:"2px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>✕</button>
                  </div>}
                  <span style={{ color:"var(--bz-ink-3)", fontSize:12 }}>{isOpen?"▲":"▼"}</span>
                </div>
              </div>

              {isOpen && (
                <div style={{ padding:"0 16px 16px", borderTop:"1px solid var(--bz-line)" }}>
                  {[
                    { key:"allSkus", label:"🎴 Found in All SKUs", color:"#7B9CFF" },
                    { key:"hobbyOnly", label:"📦 Hobby Only", color:"#E8317A" },
                    { key:"doubleMegaOnly", label:"💎 Double Mega Only", color:"#FBBF24" },
                    { key:"notes", label:"💬 Streamer Notes", color:"#4ade80" },
                  ].filter(({key})=>s[key]).map(({key,label,color})=>(
                    <div key={key} style={{ marginTop:12 }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:5 }}>
                        <div style={{ fontSize:10, fontWeight:700, color, textTransform:"uppercase", letterSpacing:1 }}>{label}</div>
                        <button onClick={()=>copy(s[key],`${i}-${key}`)} style={{ background:`${color}18`, border:`1px solid ${color}33`, color, borderRadius:5, padding:"2px 8px", fontSize:9, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                          {copied===`${i}-${key}`?"✅":"📋"} Copy
                        </button>
                      </div>
                      <div style={{ fontSize:12, color:"var(--bz-ink-2)", lineHeight:1.7, whiteSpace:"pre-wrap", background:"var(--bz-s1)", borderRadius:7, padding:"8px 12px" }}>{s[key]}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BoBASellerTools({ userRole }) {
  const isAdmin = ["Admin"].includes(userRole?.role);
  const [data, setData] = useState({ primer:"", weapons:[], hypelines:[], faq:[] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoc(doc(db,"config","bobaTools")).then(snap => {
      if (snap.exists() && snap.data().seeded) {
        setData({ primer:"", weapons:[], hypelines:[], faq:[], ...snap.data() });
      } else {
        const seed = {
          seeded: true,
          primer: `Welcome to Bo Jackson Battle Arena — one of the most exciting collectible card games in live breaking right now!

BoBA is a comic book-style trading card game where legendary athletes are reimagined as powerful warriors called Heroes. Every hero comes in multiple weapon versions — and those weapons determine rarity and value.

Here's the rarity ladder from most common to rarest:
• Steel & Brawl — your base cards, great for gameplay
• Fire & Ice — Inspired Ink ON-CARD autographs, serialized to just /50 copies each
• Glow — Inspired Ink auto, only /25 in the world
• Hex — Inspired Ink auto, only /10 in the world
• Gum — Secret Rare, ultra-limited
• Super — Superfoil 1/1. ONE copy exists. Period.

The key thing to know: ALL autographs in BoBA are ON-CARD ONLY. No sticker autos, no separate sheets — the athlete signs directly on the card. That's a premium product and serious collectors know the difference.

Named after Bo Jackson — the only athlete in history to be an All-Star in both the NFL and MLB — this game is built around athletic greatness.`,

          sets: [
            {
              id: 1,
              name: "Tecmo Bowl Edition",
              overview: "130 heroes · 64 autos · football legends",
              allSkus: `On-Card Inspired Ink Autographs
Alpha
Helmets *
News *
80's Rad
Scoreboard *
Logofoils
Sore Thumb *
Rage Quit *

* New with Tecmo Bowl Edition`,
              hobbyOnly: `Gum
Blow On It *
Gold Coinflip *
Skyline Ice/Fire *
8-Bit *
Color Battlefoils
Halftime Show *
Metallic Inspired Ink Autograph

* New with Tecmo Bowl Edition`,
              doubleMegaOnly: `Endzone *
Skyline Glow *
Big Pixel *
Silver Coinflip *
Helmet Icons *

* New with Tecmo Bowl Edition`,
              notes: `Weapon notes specific to Tecmo Bowl:
• Steel & Brawl — Battlefoil AND Non-Foil versions (Common)
• Fire & Ice — Battlefoil AND Non-Foil versions (Rare, on-card Inspired Ink autos)
• Glow — Battlefoil ONLY (Ultra Rare, on-card Inspired Ink auto)
• Hex & Gum — Battlefoil ONLY (Secret Rare)
• Super — Superfoil ONLY (1/1)

64 heroes have on-card Inspired Ink autographs in this set. Key autos to hype:
BoJax (Bo Jackson), Attak (Dak Prescott), Brees (Drew Brees), Mossed (Randy Moss), Marino (Dan Marino), Cutback (Barry Sanders), Machine Gun (Jim Kelly), Troy of Dallas (Troy Aikman), Gronk (Rob Gronkowski), Fear Himself (Lawrence Taylor)`
            }
          ],
          weapons: [
            { id:1, name:"Steel", rarity:"Common — base card", description:"The foundation of every deck. Every hero has a Steel version. Great for gameplay and building.", tip:"Steel is where we start — every hero has one and they're great for new players." },
            { id:2, name:"Brawl", rarity:"Common — newer weapon", description:"Raw combat power. Brawl cards bring close-quarters energy to the Arena. Strong gameplay piece.", tip:"Brawl is new to the lineup — these hit different in the deck." },
            { id:3, name:"Fire", rarity:"Inspired Ink /50 — on-card auto", description:"Serialized autograph, only 50 in existence. ON-CARD signature only — no sticker autos in BoBA.", tip:"FIRE — Inspired Ink, serialized to 50. That's an ON-CARD auto. Real signature, real value!" },
            { id:4, name:"Ice", rarity:"Inspired Ink /50 — on-card auto", description:"Same rarity as Fire — serialized to 50 copies. Different energy, same elite status.", tip:"ICE — same print run as Fire, just 50 in the world. That auto is going straight on someone's shelf." },
            { id:5, name:"Glow", rarity:"Inspired Ink /25 — on-card auto", description:"Ultra Rare. Only 25 copies exist. The radioactive hit — harder to pull than Fire or Ice.", tip:"GLOW — only 25 of these. You just pulled one of 25. Let that sink in." },
            { id:6, name:"Hex", rarity:"Inspired Ink /10 — on-card auto", description:"Elite rarity. Only 10 copies in the world. Dark magic energy — and serious collector value.", tip:"HEX — there are TEN of these. Ten. In the entire world. That is an elite card." },
            { id:7, name:"Gum", rarity:"Secret Rare — ultra limited", description:"Hidden in the set, no published print run. The secret hit — when it drops, the room goes crazy.", tip:"GUM! The secret rare — this one wasn't even supposed to be here tonight!" },
            { id:8, name:"Super", rarity:"Superfoil 1/1 — one of one", description:"The absolute pinnacle. One copy exists in the world. Named in honor of the hero who wields it.", tip:"SUPERFOIL. ONE OF ONE. There is ONLY ONE of this card IN EXISTENCE. This is the holy grail!" },
          ],

          hypelines: [
            "INSPIRED INK — that is an ON-CARD AUTOGRAPH. Not a sticker. The athlete signed directly on this card!",
            "FIRE! Serialized to 50 — only 50 of these exist anywhere in the world!",
            "ICE! /50 — same level as Fire, different weapon. Just as rare, just as real!",
            "GLOW — Ultra Rare, only 25 copies. You just pulled one of TWENTY-FIVE!",
            "HEX — only TEN in existence. Ten cards. This is an elite, elite pull!",
            "GUM! The secret rare dropped! Nobody saw this coming!",
            "SUPERFOIL 1/1 — ONE OF ONE! THE ONLY COPY! There is nothing rarer than this!",
            "This is Bo Jackson Battle Arena — named after the greatest two-sport athlete who ever lived. And we just pulled FIRE!",
            "All autos in BoBA are ON-CARD ONLY. No stickers, no shortcuts. Real signatures on real cards.",
            "Rainbow time — we're chasing every weapon for this hero. Every version, one collection.",
          ],

          faq: [
            { id:1, q:"Are the autographs real?", a:"100% real — and that's what makes BoBA special. Every autograph in Bo Jackson Battle Arena is an on-card signature only. No sticker autos, no separate sheets. The athlete signed directly on the card. That's the standard and it's a big deal in the hobby." },
            { id:2, q:"Who is Bo Jackson?", a:"Bo Jackson is arguably the greatest two-sport athlete in history — a Pro Bowl NFL running back AND an MLB All-Star outfielder at the same time. Nike built the original Air Trainer around him. He's a legend, and this entire game is built in his honor." },
            { id:3, q:"What's the rarest card I can pull?", a:"The Super — a Superfoil 1/1. One copy exists in the entire world. After that: Hex (/10), Gum (secret rare), Glow (/25), then Fire and Ice (both /50). Every level is an on-card autograph except Gum and Super." },
            { id:4, q:"What are Battlefoils?", a:"Battlefoils are foil treatment parallels — alternate versions of base cards with premium foil finishes. They come in Red, Silver, Blue, Orange, Green, and Pink. They add serious visual pop to any collection and are a great addition to a rainbow chase." },
            { id:5, q:"What's a rainbow?", a:"A rainbow is collecting every weapon version of one hero — Steel, Brawl, Fire, Ice, Glow, Hex, Gum, and Super. It's the ultimate collector goal for a single hero. Some people only chase one hero their whole collection life." },
            { id:6, q:"Can you actually play this as a game?", a:"Yes! BoBA is a fully playable deck-building card game. You build a deck around your favorite heroes and battle opponents in the Arena. The game adds real gameplay value on top of the collecting — cards you love to pull are also cards you want to play." },
            { id:7, q:"Where can I buy more BoBA?", a:"Bazooka Breaks is one of your best sources — you're watching it live right now. You can also find BoBA at local card shops using the store locator at bobattlearena.com, and on the secondary market on eBay and similar platforms." },
          ]
        };
        setData(seed);
        setDoc(doc(db,"config","bobaTools"), seed, { merge:true });
      }
      setLoading(false);
    }).catch(()=>setLoading(false));
  }, []);

  async function save(updated) {
    setData(updated);
    await setDoc(doc(db,"config","bobaTools"), updated, { merge:true });
  }

  if (loading) return <div style={{ padding:40, textAlign:"center", color:"var(--bz-ink-3)" }}>Loading...</div>;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14, padding:"0 0 20px" }}>
      <div style={{ ...S.card, background:"var(--bz-bg)", padding:"14px 18px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:20 }}>⚔️</span>
          <div>
            <div style={{ fontSize:14, fontWeight:800, color:"var(--bz-ink)" }}>BoBA Seller Tools</div>
            <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>Reference guide for breaking Bo Jackson Battle Arena — editable by admins, used by all streamers</div>
          </div>
        </div>
      </div>
      <BobaPrimerSection data={data} isAdmin={isAdmin} save={save}/>
      <BobaSetGuidesSection data={data} isAdmin={isAdmin} save={save}/>
      <BobaWeaponsSection data={data} isAdmin={isAdmin} save={save}/>
      <BobaHypeLinesSection data={data} isAdmin={isAdmin} save={save}/>
      <BobaFAQSection data={data} isAdmin={isAdmin} save={save}/>
    </div>
  );
}

// ── WOTF SELLER TOOLS ────────────────────────────────────────────────────────
function WotFPrimerSection({ data, isAdmin, save }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(data.primer||"");
  const [copied,  setCopied]  = useState(false);
  useEffect(()=>{ setDraft(data.primer||""); },[data.primer]);
  async function savePrimer() { await save({ ...data, primer:draft }); setEditing(false); }
  function copy() { navigator.clipboard.writeText(data.primer||"").then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2500); }); }
  return (
    <div style={{ ...S.card }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:0 }}>
        <SectionLabel t="🎙 Game Primer"/>
        <div style={{ display:"flex", gap:8 }}>
          {data.primer && <Btn onClick={copy}>{copied?"✅ Copied":"📋 Copy"}</Btn>}
          {isAdmin && <Btn variant="ghost" onClick={()=>{ setDraft(data.primer||""); setEditing(!editing); }}>{editing?"Cancel":"✏️ Edit"}</Btn>}
        </div>
      </div>
      <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:10 }}>What to say when someone asks "what is this game?" — script for any streamer</div>
      {editing ? (
        <>
          <textarea value={draft} onChange={e=>setDraft(e.target.value)} rows={10}
            style={{ ...S.inp, width:"100%", resize:"vertical", lineHeight:1.7, fontSize:13, marginBottom:10 }}/>
          <Btn onClick={savePrimer} disabled={!draft.trim()}>💾 Save Primer</Btn>
        </>
      ) : data.primer ? (
        <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"16px 18px", whiteSpace:"pre-wrap", fontSize:13, color:"var(--bz-ink-2)", lineHeight:1.8, maxHeight:360, overflowY:"auto" }}>{data.primer}</div>
      ) : (
        <div style={{ textAlign:"center", padding:"28px 0", color:"#8a8a8a", fontSize:12 }}>
          {isAdmin ? <Btn onClick={()=>setEditing(true)}>+ Write Game Primer</Btn> : "No game primer added yet — ask an admin"}
        </div>
      )}
    </div>
  );
}

function WotFSetsSection({ data, isAdmin, save }) {
  const [editIdx, setEditIdx] = useState(null);
  const [draft,   setDraft]   = useState({ name:"", description:"", notes:"" });
  const [adding,  setAdding]  = useState(false);
  const [copied,  setCopied]  = useState(null);
  function startAdd() { setDraft({ name:"", description:"", notes:"" }); setAdding(true); setEditIdx(null); }
  function startEdit(i) { setDraft({ ...data.sets[i] }); setEditIdx(i); setAdding(false); }
  async function saveSet() {
    const sets = [...(data.sets||[])];
    if (editIdx !== null) sets[editIdx] = draft; else sets.push({ ...draft, id:Date.now() });
    await save({ ...data, sets }); setAdding(false); setEditIdx(null);
  }
  async function deleteSet(i) {
    if (!window.confirm(`Delete "${data.sets[i].name}"?`)) return;
    await save({ ...data, sets:data.sets.filter((_,idx)=>idx!==i) });
  }
  function copy(text, key) { navigator.clipboard.writeText(text).then(()=>{ setCopied(key); setTimeout(()=>setCopied(null),2500); }); }
  return (
    <div style={{ ...S.card }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <SectionLabel t="📦 Set Guides"/>
        {isAdmin && <Btn variant="ghost" onClick={startAdd}>+ Add Set</Btn>}
      </div>
      {(adding || editIdx !== null) && (
        <div style={{ background:"var(--bz-bg)", border:"1px solid rgba(232,49,122,0.2)", borderRadius:10, padding:"16px", marginBottom:14 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
            <div><label style={S.lbl}>Set Name</label><input value={draft.name} onChange={e=>setDraft(p=>({...p,name:e.target.value}))} placeholder="e.g. Existence" style={S.inp}/></div>
            <div><label style={S.lbl}>One-line description</label><input value={draft.description} onChange={e=>setDraft(p=>({...p,description:e.target.value}))} placeholder="e.g. The original WotF set" style={S.inp}/></div>
          </div>
          <div><label style={S.lbl}>Streamer Notes</label><textarea value={draft.notes} onChange={e=>setDraft(p=>({...p,notes:e.target.value}))} rows={5} style={{ ...S.inp, width:"100%", resize:"vertical", lineHeight:1.7, fontSize:13 }}/></div>
          <div style={{ display:"flex", gap:8, marginTop:10 }}>
            <Btn onClick={saveSet} disabled={!draft.name.trim()}>💾 Save</Btn>
            <Btn variant="ghost" onClick={()=>{ setAdding(false); setEditIdx(null); }}>Cancel</Btn>
          </div>
        </div>
      )}
      {(data.sets||[]).length === 0 && !adding && <div style={{ textAlign:"center", padding:"20px 0", color:"#8a8a8a", fontSize:12 }}>{isAdmin ? "Click + Add Set to document your first set" : "No sets documented yet"}</div>}
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {(data.sets||[]).map((s,i) => (
          <div key={s.id||i} style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"14px 16px" }}>
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:s.notes?8:0 }}>
              <div><div style={{ fontSize:14, fontWeight:800, color:"var(--bz-ink)" }}>{s.name}</div>{s.description && <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginTop:2 }}>{s.description}</div>}</div>
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                {s.notes && <button onClick={()=>copy(s.notes,i)} style={{ background:"rgba(74,222,128,0.1)", border:"1px solid rgba(74,222,128,0.2)", color:"#4ade80", borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{copied===i?"✅":"📋"} Copy</button>}
                {isAdmin && <><button onClick={()=>startEdit(i)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:6, padding:"4px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>✏️</button><button onClick={()=>deleteSet(i)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"#8a8a8a", borderRadius:6, padding:"4px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>✕</button></>}
              </div>
            </div>
            {s.notes && <div style={{ fontSize:12, color:"var(--bz-ink-2)", whiteSpace:"pre-wrap", lineHeight:1.7 }}>{s.notes}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function WotFHypeLinesSection({ data, isAdmin, save }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState((data.hypelines||[]).join("\n"));
  const [copied,  setCopied]  = useState(null);
  useEffect(()=>{ setDraft((data.hypelines||[]).join("\n")); },[data.hypelines]);
  async function saveLines() { const hypelines=draft.split("\n").map(l=>l.trim()).filter(Boolean); await save({ ...data, hypelines }); setEditing(false); }
  function copyLine(line,i) { navigator.clipboard.writeText(line).then(()=>{ setCopied(i); setTimeout(()=>setCopied(null),2000); }); }
  return (
    <div style={{ ...S.card }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <SectionLabel t="⚡ Hype Lines"/>
        {isAdmin && <Btn variant="ghost" onClick={()=>{ setDraft((data.hypelines||[]).join("\n")); setEditing(!editing); }}>{editing?"Cancel":"✏️ Edit"}</Btn>}
      </div>
      <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:10 }}>One-tap copy for when a big card hits — keep energy high during the pull</div>
      {editing ? (
        <><textarea value={draft} onChange={e=>setDraft(e.target.value)} rows={8} style={{ ...S.inp, width:"100%", resize:"vertical", lineHeight:1.7, fontSize:13, fontFamily:"inherit", marginBottom:10 }}/><Btn onClick={saveLines} disabled={!draft.trim()}>💾 Save Hype Lines</Btn></>
      ) : (data.hypelines||[]).length > 0 ? (
        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          {(data.hypelines||[]).map((line,i) => (
            <button key={i} onClick={()=>copyLine(line,i)} style={{ background:copied===i?"rgba(74,222,128,0.12)":"rgba(255,255,255,0.04)", border:`1px solid ${copied===i?"rgba(74,222,128,0.3)":"var(--bz-line-2)"}`, color:copied===i?"#4ade80":"var(--bz-ink-2)", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}>
              {copied===i ? "✅ Copied!" : `"${line}"`}
            </button>
          ))}
        </div>
      ) : (
        <div style={{ textAlign:"center", padding:"20px 0", color:"#8a8a8a", fontSize:12 }}>{isAdmin ? <Btn onClick={()=>setEditing(true)}>+ Add Hype Lines</Btn> : "No hype lines added yet"}</div>
      )}
    </div>
  );
}

function WotFFAQSection({ data, isAdmin, save }) {
  const [openIdx, setOpenIdx] = useState(null);
  const [adding,  setAdding]  = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [draft,   setDraft]   = useState({ q:"", a:"" });
  const [copied,  setCopied]  = useState(null);
  async function saveQA() {
    const faq=[...(data.faq||[])];
    if (editIdx!==null) faq[editIdx]=draft; else faq.push({ ...draft, id:Date.now() });
    await save({ ...data, faq }); setAdding(false); setEditIdx(null);
  }
  async function deleteQA(i) { if (!window.confirm("Delete this Q&A?")) return; await save({ ...data, faq:(data.faq||[]).filter((_,idx)=>idx!==i) }); }
  function copy(text,i) { navigator.clipboard.writeText(text).then(()=>{ setCopied(i); setTimeout(()=>setCopied(null),2500); }); }
  return (
    <div style={{ ...S.card }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <SectionLabel t="❓ Buyer FAQ"/>
        {isAdmin && <Btn variant="ghost" onClick={()=>{ setDraft({q:"",a:""}); setAdding(true); setEditIdx(null); }}>+ Add Q&A</Btn>}
      </div>
      <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>Scripted answers for common viewer questions — tap to expand, copy the answer</div>
      {(adding||editIdx!==null) && (
        <div style={{ background:"var(--bz-bg)", border:"1px solid rgba(232,49,122,0.2)", borderRadius:10, padding:"16px", marginBottom:14 }}>
          <div style={{ marginBottom:10 }}><label style={S.lbl}>Question</label><input value={draft.q} onChange={e=>setDraft(p=>({...p,q:e.target.value}))} placeholder="e.g. Is this like Pokémon?" style={S.inp}/></div>
          <div><label style={S.lbl}>Scripted Answer</label><textarea value={draft.a} onChange={e=>setDraft(p=>({...p,a:e.target.value}))} rows={4} style={{ ...S.inp, width:"100%", resize:"vertical", lineHeight:1.7, fontSize:13 }} placeholder="Write a friendly answer..."/></div>
          <div style={{ display:"flex", gap:8, marginTop:10 }}><Btn onClick={saveQA} disabled={!draft.q.trim()||!draft.a.trim()}>💾 Save</Btn><Btn variant="ghost" onClick={()=>{ setAdding(false); setEditIdx(null); }}>Cancel</Btn></div>
        </div>
      )}
      {(data.faq||[]).length===0&&!adding && <div style={{ textAlign:"center", padding:"20px 0", color:"#8a8a8a", fontSize:12 }}>{isAdmin?"Add common viewer questions":"No FAQ entries yet"}</div>}
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {(data.faq||[]).map((item,i) => (
          <div key={item.id||i} style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, overflow:"hidden" }}>
            <div onClick={()=>setOpenIdx(openIdx===i?null:i)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 16px", cursor:"pointer" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}><span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{openIdx===i?"▼":"▶"}</span><span style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)" }}>{item.q}</span></div>
              {isAdmin && <div style={{ display:"flex", gap:6 }} onClick={e=>e.stopPropagation()}>
                <button onClick={()=>{ setDraft({...item}); setEditIdx(i); setAdding(false); }} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:5, padding:"2px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>✏️</button>
                <button onClick={()=>deleteQA(i)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"#8a8a8a", borderRadius:5, padding:"2px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>✕</button>
              </div>}
            </div>
            {openIdx===i && (
              <div style={{ padding:"0 16px 14px", borderTop:"1px solid var(--bz-line)" }}>
                <div style={{ fontSize:13, color:"var(--bz-ink-2)", lineHeight:1.7, whiteSpace:"pre-wrap", marginTop:10, marginBottom:10 }}>{item.a}</div>
                <button onClick={()=>copy(item.a,i)} style={{ background:copied===i?"rgba(74,222,128,0.1)":"rgba(123,156,255,0.08)", border:`1px solid ${copied===i?"rgba(74,222,128,0.25)":"rgba(123,156,255,0.2)"}`, color:copied===i?"#4ade80":"#7B9CFF", borderRadius:6, padding:"5px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  {copied===i?"✅ Copied":"📋 Copy Answer"}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function WotFSellerTools({ userRole }) {
  const isAdmin = ["Admin"].includes(userRole?.role);

  // Firestore state
  const [data, setData] = useState({ primer:"", sets:[], hypelines:[], faq:[] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoc(doc(db,"config","wotfTools")).then(snap => {
      if (snap.exists() && snap.data().seeded) {
        setData({ primer:"", sets:[], hypelines:[], faq:[], ...snap.data() });
      } else {
        // First load — seed with default content
        const seed = {
          seeded: true,
          primer: `Welcome to Wonders of The First — one of the hottest new collectible card games on the market right now!

Wonders of The First is a strategic CCG where you play as a Stoneseeker — a powerful being who battles across 7 realms called Orbitals. Instead of heroes, you summon creatures called Wonders to fight for control of powerful Stones. Control the most Stones at the end of Round 7 and you win.

What makes this game so exciting to collect:
• 401 cards in the base Existence set — huge variety
• 1st Edition serialized cards — numbered and one-of-a-kind
• Alternate art exclusives and one-of-ones that are truly rare
• 6 unique Orbital Starter Decks: Petraia, Thalwind, Solfera, Umbrathene, Heliosynth, and Boundless
• Raised $1.2 million on Kickstarter with over 2,100 backers — this community is real

Whether you're a player, a collector, or just getting started — this is a ground floor opportunity on something special.`,

          sets: [
            {
              id:1,
              name: "Existence",
              description: "The original WotF set — 401 cards across 6 Orbitals",
              notes: `The foundational set. 401 cards in the base set + 6 exclusive mythics in Starter Decks.\n\nKey talking points:\n• 6 Orbitals (worlds): Petraia, Thalwind, Solfera, Umbrathene, Heliosynth, Boundless\n• Chase cards: 1st Edition serialized, alt art, one-of-ones\n• US standard sleeve size (same as Magic: The Gathering)\n• Available on TableTop Simulator for free playtesting\n\nHype moments: Any serialized pull, any alternate art reveal, any legendary Wonder`
            },
            {
              id:2,
              name: "Call of the Stones",
              description: "The second WotF set — now available at Target",
              notes: `The follow-up to Existence — now available at Target stores nationwide.\n\nKey talking points:\n• Continues the 7-Orbital storyline\n• New Collect & Play Bundle available\n• Bigger distribution = more mainstream awareness = better investment\n• Checklist available at wondersccg.com/checklists\n\nHype moments: Any cross-set interaction, Target exclusive pulls, new Wonder reveals`
            }
          ],

          hypelines: [
            "OH THAT'S A 1ST EDITION — THAT IS SERIALIZED! Someone's getting history right now!",
            "That is a CHASE CARD. Chat, do you see what just came out of that pack?!",
            "ONE OF ONE. There is only ONE of this card in existence — and it just landed here tonight!",
            "That's an alternate art — you cannot pull that anywhere else. That is EXCLUSIVE.",
            "Wonders of the First — Kickstarter funded to the tune of 1.2 MILLION dollars. This game is real and the cards are already moving.",
            "This is ground floor collecting right here. Get in early — you will thank yourself later.",
            "That Wonder right there is from the Boundless Orbital — one of the rarest realms in the game.",
            "Legendaries are hit! ONE per legendary allowed in a deck — this card is always in demand.",
          ],

          faq: [
            {
              id:1,
              q: "What is this game? Is it like Pokémon or Magic?",
              a: "It's its own thing — Wonders of The First is a brand new CCG where you battle across 7 realms called Orbitals. Instead of Pokémon or Magic heroes, you summon creatures called Wonders. The collecting side is huge — serialized 1st Editions, one-of-one cards, and alternate art exclusives. It raised over $1.2 million on Kickstarter and just hit Target. Think of it as ground-floor Pokémon — get in early."
            },
            {
              id:2,
              q: "Is this game actually popular or is it a risk?",
              a: "1,228,288 dollars raised on Kickstarter by 2,106 backers before the game even shipped. It's now in Target stores nationwide. The competitive scene is already running tournaments. The community is real and growing — this isn't a gamble, this is an opportunity."
            },
            {
              id:3,
              q: "What's a Stoneseeker?",
              a: "That's YOU as the player. In WotF, you're a Stoneseeker — a powerful being who controls Wonders and battles across Orbitals (the 7 realms) to capture Stones. Control the most Stones at the end of Round 7, you win. On the collecting side, it just means you're part of the lore — every buyer is part of the universe."
            },
            {
              id:4,
              q: "What's the most valuable card to look for?",
              a: "Serialized 1st Edition cards are the chase — they're numbered, limited, and already trading. One-of-ones exist too — literally one card in the world. Legendary Wonders (only 1 allowed per deck) are always in demand for play. Right now the market is early so pricing is still accessible — that won't last forever."
            },
            {
              id:5,
              q: "Can I actually play this game?",
              a: "Yes! There are 6 Starter Decks — one per Orbital — that you can play right out of the box. Two players, 30 minutes, no extra setup needed. There's also free online play at compete.wondersccg.com and a TableTop Simulator mod on Steam if you want to try before buying."
            },
            {
              id:6,
              q: "What size sleeves does it use?",
              a: "US standard size — same as Magic: The Gathering. If you're already a card player you have everything you need."
            },
            {
              id:7,
              q: "Where can I buy it outside of here?",
              a: "Target carries Call of the Stones now — you can also find it at local game stores using the store locator at wondersccg.com. But honestly, breaking it here live is the most fun way to open it."
            }
          ]
        };
        setData(seed);
        setDoc(doc(db,"config","wotfTools"), seed, { merge:true });
      }
      setLoading(false);
    }).catch(()=>setLoading(false));
  }, []);

  async function save(updated) {
    setData(updated);
    await setDoc(doc(db,"config","wotfTools"), updated, { merge:true });
  }


  if (loading) return <div style={{ padding:40, textAlign:"center", color:"var(--bz-ink-3)" }}>Loading...</div>;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14, padding:"0 0 20px" }}>
      <div style={{ ...S.card, background:"var(--bz-bg)", padding:"14px 18px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:20 }}>🌟</span>
          <div>
            <div style={{ fontSize:14, fontWeight:800, color:"var(--bz-ink)" }}>WotF Seller Tools</div>
            <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>Reference guide for breaking Wonders of The First — editable by admins, used by all streamers</div>
          </div>
        </div>
      </div>
      <WotFPrimerSection data={data} isAdmin={isAdmin} save={save}/>
      <WotFSetsSection data={data} isAdmin={isAdmin} save={save}/>
      <WotFHypeLinesSection data={data} isAdmin={isAdmin} save={save}/>
      <WotFFAQSection data={data} isAdmin={isAdmin} save={save}/>
    </div>
  );
}

// ── SHOW NOTES ───────────────────────────────────────────────────────────────
function ShowNotes({ userRole }) {
  const isAdmin = ["Admin"].includes(userRole?.role);
  const [notes,       setNotes]       = useState({});   // { name: text }
  const [activeName,  setActiveName]  = useState(null);
  const [noteName,    setNoteName]    = useState("");
  const [noteText,    setNoteText]    = useState("");
  const [saving,      setSaving]      = useState(false);
  const [copied,      setCopied]      = useState(false);
  const [editing,     setEditing]     = useState(false);

  useEffect(() => {
    getDoc(doc(db,"config","showNotes")).then(snap => {
      if (snap.exists()) {
        const data = snap.data().notes || {};
        setNotes(data);
        if (!activeName && Object.keys(data).length > 0) {
          setActiveName(Object.keys(data)[0]);
        }
      }
    }).catch(()=>{});
  }, []);

  async function saveNote() {
    if (!noteName.trim() || !noteText.trim()) return;
    setSaving(true);
    const updated = { ...notes, [noteName.trim()]: noteText };
    setNotes(updated);
    await setDoc(doc(db,"config","showNotes"), { notes: updated }, { merge:true });
    setActiveName(noteName.trim());
    setNoteName(""); setNoteText(""); setEditing(false);
    setSaving(false);
  }

  async function deleteNote(name) {
    if (!window.confirm(`Delete "${name}"?`)) return;
    const updated = { ...notes };
    delete updated[name];
    setNotes(updated);
    await setDoc(doc(db,"config","showNotes"), { notes: updated }, { merge:true });
    if (activeName === name) setActiveName(Object.keys(updated)[0]||null);
  }

  function startEdit(name) {
    setNoteName(name);
    setNoteText(notes[name]||"");
    setEditing(true);
  }

  function copyNote() {
    const text = notes[activeName]||"";
    navigator.clipboard.writeText(text).then(()=>{
      setCopied(true); setTimeout(()=>setCopied(false),2500);
    }).catch(()=>{
      const ta=document.createElement("textarea"); ta.value=text;
      document.body.appendChild(ta); ta.select(); document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true); setTimeout(()=>setCopied(false),2500);
    });
  }

  const allNames = Object.keys(notes);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14, padding:"0 0 20px" }}>

      {/* Template selector */}
      {allNames.length > 0 && (
        <div style={{ ...S.card }}>
          <SectionLabel t="📝 Show Notes Templates"/>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom: activeName ? 14 : 0 }}>
            {allNames.map(name => (
              <div key={name} style={{ display:"flex", alignItems:"center" }}>
                <button onClick={()=>{ setActiveName(name); setEditing(false); }}
                  style={{ background:activeName===name?"rgba(232,49,122,0.12)":"var(--bz-s1)", border:`1.5px solid ${activeName===name?"#E8317A":"var(--bz-line-2)"}`, color:activeName===name?"#E8317A":"#888", borderRadius:isAdmin?"8px 0 0 8px":"8px", padding:"7px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  {name}
                </button>
                {isAdmin && <>
                  <button onClick={()=>startEdit(name)}
                    style={{ background:"var(--bz-bg)", border:"1.5px solid var(--bz-line-2)", borderLeft:"none", color:"var(--bz-ink-3)", borderRadius:"0", padding:"7px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }} title="Edit">✏️</button>
                  <button onClick={()=>deleteNote(name)}
                    style={{ background:"var(--bz-bg)", border:"1.5px solid var(--bz-line-2)", borderLeft:"none", color:"#8a8a8a", borderRadius:"0 8px 8px 0", padding:"7px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }} title="Delete">✕</button>
                </>}
              </div>
            ))}
            {isAdmin && (
              <button onClick={()=>{ setEditing(true); setNoteName(""); setNoteText(""); setActiveName(null); }}
                style={{ background:"transparent", border:"1.5px dashed var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                + New Template
              </button>
            )}
          </div>

          {/* Preview + copy */}
          {activeName && notes[activeName] && !editing && (
            <>
              <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:8 }}>
                <Btn onClick={copyNote}>{copied?"✅ Copied!":"📋 Copy to Clipboard"}</Btn>
              </div>
              <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"16px 18px", whiteSpace:"pre-wrap", fontSize:13, color:"var(--bz-ink-2)", lineHeight:1.8, maxHeight:500, overflowY:"auto", fontFamily:"inherit" }}>
                {notes[activeName]}
              </div>
              <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:8 }}>
                Click <strong style={{ color:"#7B9CFF" }}>Copy to Clipboard</strong> then paste directly into Whatnot's show notes field.
              </div>
            </>
          )}
        </div>
      )}

      {/* Empty state */}
      {allNames.length === 0 && !editing && (
        <div style={{ ...S.card, textAlign:"center", padding:"40px 20px" }}>
          <div style={{ fontSize:32, opacity:0.3, marginBottom:10 }}>📝</div>
          <div style={{ fontSize:14, fontWeight:700, color:"var(--bz-ink-3)", marginBottom:6 }}>No show notes templates yet</div>
          {isAdmin
            ? <Btn onClick={()=>setEditing(true)}>+ Create First Template</Btn>
            : <div style={{ fontSize:12, color:"#8a8a8a" }}>Ask an admin to add show notes templates</div>}
        </div>
      )}

      {/* Admin: create / edit form */}
      {isAdmin && editing && (
        <div style={{ ...S.card }}>
          <SectionLabel t={noteName && notes[noteName] ? `✏️ Editing — ${noteName}` : "✨ New Template"}/>
          <div style={{ display:"grid", gridTemplateColumns:"220px 1fr", gap:12, alignItems:"flex-start" }}>
            <div>
              <label style={S.lbl}>Template Name</label>
              <input value={noteName} onChange={e=>setNoteName(e.target.value)}
                placeholder="e.g. Standard Break, Tecmo Bowl Night"
                style={{ ...S.inp }}/>
              <div style={{ display:"flex", gap:8, marginTop:10 }}>
                <Btn onClick={saveNote} disabled={!noteName.trim()||!noteText.trim()||saving}>
                  {saving?"⏳ Saving...":"💾 Save"}
                </Btn>
                <Btn variant="ghost" onClick={()=>{ setEditing(false); setNoteName(""); setNoteText(""); }}>Cancel</Btn>
              </div>
            </div>
            <div>
              <label style={S.lbl}>Show Notes Content</label>
              <textarea value={noteText} onChange={e=>setNoteText(e.target.value)}
                placeholder={"Paste your show notes template here...\n\nUse placeholders like [SET NAME], [DATE], [BREAKER] and swap them out before each stream.\n\nExample:\n🔴 LIVE — [SET NAME] Break!\n\nHow to buy:\n1. Click any item to bid\n2. Highest bid wins\n..."}
                rows={14}
                style={{ ...S.inp, resize:"vertical", lineHeight:1.7, fontSize:13 }}/>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── BREAK SPOTS ──────────────────────────────────────────────────────────────
function BreakSpots() {
  const fmt = v => v;
  const [setName,    setSetName]    = useState("");
  const [pastedList, setPastedList] = useState("");
  const [savedSets,  setSavedSets]  = useState(() => {
    // Instant paint from cache; refresh from Firestore in the background.
    try { const c = localStorage.getItem("breakSpots_cache_v1"); return c ? JSON.parse(c) : {}; }
    catch { return {}; }
  });
  const [loading,    setLoading]    = useState(true);
  const [copied,     setCopied]     = useState(false);
  const [activeSet,  setActiveSet]  = useState(null);
  const [shuffle,    setShuffle]    = useState(false);
  const [saving,     setSaving]     = useState(false);

  // Live subscription so every user always sees the same shared spot lists in
  // real time — when Dev saves a set, reps see it immediately.
  useEffect(() => {
    const unsub = onSnapshot(doc(db,"config","breakSpots"), snap => {
      const sets = (snap.exists() ? snap.data().sets : {}) || {};
      setSavedSets(sets);
      try { localStorage.setItem("breakSpots_cache_v1", JSON.stringify(sets)); } catch {}
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, []);

  async function persistSets(updated) {
    setSavedSets(updated);
    try { localStorage.setItem("breakSpots_cache_v1", JSON.stringify(updated)); } catch {}
    // Write the full sets object (no merge) so deleted sets are actually removed —
    // merge:true only adds/updates keys and would resurrect deleted lists.
    await setDoc(doc(db,"config","breakSpots"), { sets: updated });
  }

  async function saveSet() {
    if (!setName.trim() || !pastedList.trim()) return;
    setSaving(true);
    const lines = pastedList.split("\n").map(l=>l.trim()).filter(Boolean);
    const updated = { ...savedSets, [setName.trim()]: lines };
    await persistSets(updated);
    setActiveSet(setName.trim());
    setSetName(""); setPastedList("");
    setSaving(false);
  }

  async function deleteSet(name) {
    if (!window.confirm(`Delete "${name}" spots list?`)) return;
    const updated = { ...savedSets };
    delete updated[name];
    await persistSets(updated);
    if (activeSet === name) setActiveSet(null);
  }

  // Build spots from bobaCards for a known set
  const currentSpots = activeSet ? (savedSets[activeSet]||[]) : [];
  const displaySpots = shuffle
    ? [...currentSpots].sort(()=>Math.random()-0.5)
    : currentSpots;

  function copyToClipboard() {
    const text = displaySpots.join("\n");
    navigator.clipboard.writeText(text).then(()=>{
      setCopied(true);
      setTimeout(()=>setCopied(false), 2500);
    }).catch(()=>{
      // fallback
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta);
      ta.select(); document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(()=>setCopied(false), 2500);
    });
  }

  const allSetNames = Object.keys(savedSets);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14, padding:"0 0 20px" }}>

      {/* ── Saved sets row ── */}
      <div style={{ ...S.card }}>
        <SectionLabel t="🎯 Break Spots"/>
        <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginBottom:14 }}>
          Build a hero list for each set, then copy it to paste into Whatnot as individual break spots.
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"200px 1fr auto", gap:10, alignItems:"flex-start" }}>
          <div>
            <label style={S.lbl}>Set Name</label>
            <input value={setName} onChange={e=>setSetName(e.target.value)}
              placeholder="e.g. Tecmo Bowl"
              style={{ ...S.inp }}/>
          </div>
          <div>
            <label style={S.lbl}>Heroes (one per line)</label>
            <textarea value={pastedList} onChange={e=>setPastedList(e.target.value)}
              placeholder={"Paste your hero list here...\nOne hero per line\n\ne.g.\nBo Jackson\nBarry Sanders\nJerry Rice"}
              rows={6}
              style={{ ...S.inp, resize:"vertical", fontFamily:"monospace", fontSize:12, lineHeight:1.6 }}/>
          </div>
          <div style={{ paddingTop:20 }}>
            <Btn onClick={saveSet} disabled={!setName.trim()||!pastedList.trim()||saving}>
              {saving ? "⏳ Saving..." : "💾 Save List"}
            </Btn>
          </div>
        </div>
      </div>

      {/* ── Set selector + copy panel ── */}
      {loading && allSetNames.length === 0 && (
        <div style={{ ...S.card, textAlign:"center", color:"var(--bz-ink-2)", fontSize:13, padding:"20px" }}>Loading saved spot lists…</div>
      )}

      {allSetNames.length > 0 && (
        <div style={{ ...S.card }}>
          <SectionLabel t="Copy for Whatnot"/>

          {/* Set picker */}
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:16 }}>
            {allSetNames.map(name => (
              <div key={name} style={{ display:"flex", alignItems:"center", gap:0 }}>
                <button onClick={()=>setActiveSet(activeSet===name?null:name)}
                  style={{ background:activeSet===name?"rgba(232,49,122,0.12)":"var(--bz-s1)", border:`1.5px solid ${activeSet===name?"#E8317A":"var(--bz-line-2)"}`, color:activeSet===name?"#E8317A":"#888", borderRadius:"8px 0 0 8px", padding:"7px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  {name} <span style={{ fontSize:10, opacity:0.6 }}>({(savedSets[name]||[]).length})</span>
                </button>
                <button onClick={()=>deleteSet(name)}
                  style={{ background:"var(--bz-bg)", border:"1.5px solid var(--bz-line-2)", borderLeft:"none", color:"#8a8a8a", borderRadius:"0 8px 8px 0", padding:"7px 8px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>✕</button>
              </div>
            ))}
          </div>

          {activeSet && currentSpots.length > 0 && (
            <>
              {/* Controls */}
              <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:12, flexWrap:"wrap" }}>
                <div style={{ fontSize:13, color:"var(--bz-ink-2)" }}><strong style={{ color:"var(--bz-ink)" }}>{currentSpots.length}</strong> spots for <strong style={{ color:"#E8317A" }}>{activeSet}</strong></div>
                <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"var(--bz-ink-2)", cursor:"pointer" }}>
                  <input type="checkbox" checked={shuffle} onChange={e=>setShuffle(e.target.checked)}/> Shuffle order
                </label>
                {shuffle && <button onClick={()=>setShuffle(false)||setShuffle(true)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:6, padding:"4px 10px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>🔀 Re-shuffle</button>}
                <Btn onClick={copyToClipboard} style={{ marginLeft:"auto" }}>
                  {copied ? "✅ Copied!" : "📋 Copy All Spots"}
                </Btn>
              </div>

              {/* Preview list */}
              <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"12px 16px", maxHeight:400, overflowY:"auto" }}>
                {displaySpots.map((hero,i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"5px 0", borderBottom:"1px solid var(--bz-line)" }}>
                    <span style={{ fontSize:10, color:"var(--bz-ink-3)", minWidth:24, textAlign:"right" }}>{i+1}</span>
                    <span style={{ fontSize:13, color:"var(--bz-ink)", fontWeight:600 }}>{hero}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:8 }}>
                Click <strong style={{ color:"#7B9CFF" }}>Copy All Spots</strong> then paste directly into Whatnot's break spot input — each hero becomes its own row.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function HeroBreakBuilder({ userRole, bobaCards=[] }) {
  const [selectedSets,  setSelectedSets]  = useState(new Set(["Tecmo Bowl"]));
  const [breakMode,     setBreakMode]     = useState("single");
  const [tierFilter,    setTierFilter]    = useState("all");
  const [search,        setSearch]        = useState("");
  const debouncedSearch = useDebounce(search, 250);
  const [sortBy,        setSortBy]        = useState("power-desc");
  const [showAssigned,  setShowAssigned]  = useState(true);
  const [squads,        setSquads]        = useState([{ id:uid(), name:"Squad 1", heroes:[] }]);
  const [activeSquad,   setActiveSquad]   = useState(null);
  const [checkedHeroes, setCheckedHeroes] = useState(new Set());
  const [savedBreaks,   setSavedBreaks]   = useState([]);
  const [viewMode,      setViewMode]      = useState("grid");

  // Only show these explicitly known sets in the Hero Break Builder
  const BOBA_BREAK_SETS = ["Alpha Edition", "Alpha Update", "Griffey 2026", "Tecmo Bowl"];

  // Hero treatments that are valid for breaks (everything else excluded)
  const HERO_TREATMENTS = ["base", "featured auto", "highlighted", "hobby", "blaster", "jumbo", "double mega", "blast box", "dragon box", "collector booster", "play booster"];

  const bobaSetNames = BOBA_BREAK_SETS.filter(s => bobaCards.some(c => c.setName === s));
  const bobaHeroSets = {};
  bobaSetNames.forEach(setName => {
    const heroCards = bobaCards
      .filter(c => {
        if (c.setName !== setName) return false;
        if (!c.hero) return false; // must have hero name
        const t = (c.treatment || "").toLowerCase().trim();
        // Must be a known hero treatment
        return HERO_TREATMENTS.some(ht => t === ht || t.startsWith(ht));
      })
      .map(c => ({
        hero: c.hero,
        inspired: c.notation || "",
        tier: c.treatment || "Base",
        power: parseInt(c.cardNum) || 0,
        cardNum: c.cardNum,
      }));
    if (heroCards.length > 0) bobaHeroSets[setName] = heroCards;
  });

  const ALL_SETS = { ...bobaHeroSets, ...HERO_SETS };
  const setNames = Object.keys(ALL_SETS);
  const filteredBobaSetNames = bobaSetNames; // already filtered to known sets with heroes

  // Merge heroes from all selected sets
  const heroes = [...selectedSets].flatMap(s => (ALL_SETS[s]||[]).map(h => ({...h, setName:s})));
  const allInSquads = new Set(squads.flatMap(sq => sq.heroes.map(h => `${h.hero}-${h.tier}-${h.setName||""}`)));

  // Tier options from selected sets
  const allTiersInSelected = new Set(heroes.map(h => h.tier).filter(Boolean));
  const knownTiers = ["Featured Auto","Highlighted","Base"].filter(t => allTiersInSelected.has(t));
  const otherTiers = [...allTiersInSelected].filter(t => !knownTiers.includes(t)).sort();
  const tierOptions = ["all", ...knownTiers, ...otherTiers];

  const filtered = heroes.filter(h => {
    const heroKey = `${h.hero}-${h.tier}-${h.setName||""}`;
    if (tierFilter !== "all" && h.tier !== tierFilter) return false;
    if (search && !h.hero.toLowerCase().includes(search.toLowerCase()) &&
        !h.inspired.toLowerCase().includes(search.toLowerCase())) return false;
    // Hide assigned toggle
    if (!showAssigned) {
      const isAssigned = breakMode === "single" ? checkedHeroes.has(heroKey) : allInSquads.has(heroKey);
      if (isAssigned) return false;
    }
    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case "power-desc": return (b.power||b.cardNum||0) - (a.power||a.cardNum||0);
      case "power-asc":  return (a.power||a.cardNum||0) - (b.power||b.cardNum||0);
      case "name-asc":   return a.hero.localeCompare(b.hero);
      case "name-desc":  return b.hero.localeCompare(a.hero);
      case "tier":       {
        const tOrder = ["Featured Auto","Highlighted","Base"];
        const ai = tOrder.indexOf(a.tier); const bi = tOrder.indexOf(b.tier);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      }
      default: return 0;
    }
  });

  const tierCounts = tierOptions.reduce((acc, t) => {
    acc[t] = t === "all" ? heroes.length : heroes.filter(h => h.tier === t).length;
    return acc;
  }, {});

  // Power color
  function powerColor(p) {
    if (p >= 200) return "#FBBF24";
    if (p >= 185) return "#C084FC";
    if (p >= 170) return "#60A5FA";
    if (p >= 155) return "#34d399";
    return "var(--bz-ink-2)";
  }

  // Single break: toggle hero checked
  function toggleHero(heroKey) {
    setCheckedHeroes(prev => {
      const next = new Set(prev);
      next.has(heroKey) ? next.delete(heroKey) : next.add(heroKey);
      return next;
    });
  }

  // Squad break: add hero to active squad
  function addToSquad(hero) {
    if (!activeSquad) return;
    const heroKey = `${hero.hero}-${hero.tier}-${hero.setName||""}`;
    if (allInSquads.has(heroKey)) return; // already assigned
    setSquads(prev => prev.map(sq =>
      sq.id === activeSquad
        ? { ...sq, heroes: [...sq.heroes, { ...hero, addedAt: Date.now(), sid: uid() }] }
        : sq
    ));
  }

  function removeFromSquad(squadId, sid) {
    setSquads(prev => prev.map(sq =>
      sq.id === squadId
        ? { ...sq, heroes: sq.heroes.filter(h => h.sid !== sid) }
        : sq
    ));
  }

  function addSquad() {
    const id = uid();
    setSquads(prev => [...prev, { id, name:`Squad ${prev.length + 1}`, heroes:[] }]);
    setActiveSquad(id);
  }

  function resetAll() {
    if (!window.confirm("Reset all progress?")) return;
    setCheckedHeroes(new Set());
    setSquads([{ id:uid(), name:"Squad 1", heroes:[] }]);
    setActiveSquad(null);
  }

  // Build Whatnot-ready break text
  function buildBreakText(mode) {
    if (mode === "single") {
      const checked = heroes.filter(h => checkedHeroes.has(`${h.hero}-${h.tier}`));
      if (!checked.length) return "";
      return checked.map((h, i) =>
        `Spot ${i+1}: ${h.hero} (${h.inspired}) — ${h.tier}${h.power ? ` ⚡${h.power}` : ""}`
      ).join("\n");
    } else {
      return squads
        .filter(sq => sq.heroes.length > 0)
        .map((sq, i) => {
          const heroLines = sq.heroes.map(h =>
            `  • ${h.hero} (${h.inspired})${h.power ? ` ⚡${h.power}` : ""}`
          ).join("\n");
          const avg = Math.round(sq.heroes.reduce((s,h)=>s+(h.power||0),0)/sq.heroes.length);
          return `${sq.name} (${sq.heroes.length} heroes${avg?" · avg ⚡"+avg:""})\n${heroLines}`;
        }).join("\n\n");
    }
  }

  function copyBreak() {
    const text = buildBreakText(breakMode);
    if (!text) return;
    navigator.clipboard?.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  const [copied, setCopied] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showRandomizer, setShowRandomizer] = useState(false);
  const [randSquadCount, setRandSquadCount] = useState(4);
  const [randHeroesPerSquad, setRandHeroesPerSquad] = useState(3);
  const [randBalance, setRandBalance] = useState(true); // balance tiers across squads

  function randomizeSquads() {
    const pool = [...heroes];
    if (pool.length === 0) { alert("No heroes available. Select a set first."); return; }

    const activeSets = [...selectedSets];
    const multiSet = activeSets.length > 1;

    function shuffle(arr) {
      const a = [...arr];
      for (let i=a.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[a[i],a[j]]=[a[j],a[i]];}
      return a;
    }

    const actualSquadCount = Math.max(randSquadCount, Math.ceil(pool.length / randHeroesPerSquad));
    const squadSlots = Array.from({length: actualSquadCount}, () => []);

    // Step 1: Sort heroes by power descending for fair distribution
    // Add tiny random jitter so equal-power heroes randomize
    const sorted = [...pool].sort((a,b) => {
      const pa = (a.power||0) + Math.random()*0.5;
      const pb = (b.power||0) + Math.random()*0.5;
      return pb - pa;
    });

    // Step 2: Multi-set guarantee seed — pick one from each set per squad first
    let remaining = [...sorted];
    if (multiSet && randHeroesPerSquad >= activeSets.length) {
      const setGroups = {};
      activeSets.forEach(s => { setGroups[s] = shuffle(remaining.filter(h => h.setName === s)); });
      const seeded = new Set();
      for (let si = 0; si < actualSquadCount; si++) {
        for (const s of activeSets) {
          if (setGroups[s].length > 0) {
            const h = setGroups[s].shift();
            squadSlots[si].push(h);
            seeded.add(`${h.hero}-${h.tier}-${h.setName||""}`);
          }
        }
      }
      remaining = remaining.filter(h => !seeded.has(`${h.hero}-${h.tier}-${h.setName||""}`));
    }

    // Step 3: Tier-paired snake draft
    // Sort remaining by power desc within each tier
    const tierOrder = ["Featured Auto", "Highlighted", "Base"];
    const tierGroups = {};
    remaining.forEach(h => {
      const t = tierOrder.includes(h.tier) ? h.tier : "Base";
      if (!tierGroups[t]) tierGroups[t] = [];
      tierGroups[t].push(h);
    });
    // Sort each group by power desc + jitter
    Object.values(tierGroups).forEach(g => g.sort((a,b) => ((b.power||0)+Math.random()*0.5) - ((a.power||0)+Math.random()*0.5)));

    const featuredPool  = [...(tierGroups["Featured Auto"]||[])];
    const highlightPool = [...(tierGroups["Highlighted"]||[])];
    const basePool      = [...(tierGroups["Base"]||[])];

    // Interleave: for each squad slot, try to give it a Featured (or Highlighted) + Base mix
    // Snake direction flips each round
    let snakeDir = 1, snakeSi = 0;
    function snakeNext() {
      const cur = snakeSi;
      snakeSi += snakeDir;
      if (snakeSi >= actualSquadCount) { snakeSi = actualSquadCount-1; snakeDir = -1; }
      else if (snakeSi < 0) { snakeSi = 0; snakeDir = 1; }
      return cur;
    }

    // First pass: assign one Featured (or Highlighted) to each squad via snake
    const primaryPool = [...featuredPool, ...highlightPool].sort((a,b)=>((b.power||0)+Math.random()*0.5)-((a.power||0)+Math.random()*0.5));
    for (const h of primaryPool) squadSlots[snakeNext()].push(h);

    // Second pass: assign Base heroes via snake
    snakeDir = 1; snakeSi = 0; // reset snake for base
    for (const h of basePool) squadSlots[snakeNext()].push(h);

    // Shuffle within each squad so power order isn't obvious
    squadSlots.forEach(sq => { for(let i=sq.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[sq[i],sq[j]]=[sq[j],sq[i]];} });

    // Squad names based on top hero
    const nameTemplates = [h=>`${h.hero}'s Crew`, h=>`Team ${h.hero}`, h=>`${h.hero} & Co.`, h=>`${h.hero} Squad`, h=>`The ${h.hero}s`];
    const squadNames = squadSlots.map(sq => {
      if (!sq.length) return "Empty Squad";
      const top = sq.reduce((b,h)=>(h.power||0)>(b.power||0)?h:b, sq[0]);
      return nameTemplates[Math.floor(Math.random()*nameTemplates.length)](top);
    });

    const newSquads = squadSlots
      .map((heroes, i) => ({ id:uid(), name:squadNames[i], heroes:heroes.map(h=>({...h,sid:uid(),addedAt:Date.now()})) }))
      .filter(sq => sq.heroes.length > 0);

    if (actualSquadCount > randSquadCount) {
      const extra = actualSquadCount - randSquadCount;
      setTimeout(()=>alert(`All ${pool.length} heroes accounted for! Created ${extra} extra squad${extra!==1?"s":""} to fit everyone.`),100);
    }

    // Show avg power per squad in console for verification
    newSquads.forEach(sq => {
      const avg = sq.heroes.reduce((s,h)=>s+(h.power||0),0)/sq.heroes.length;
      console.log(`${sq.name}: ${sq.heroes.length} heroes, avg ⚡${avg.toFixed(1)}`);
    });

    setSquads(newSquads);
    setBreakMode("squad");
    setShowExport(false);
  }

  const checkedCount = breakMode === "single" ? checkedHeroes.size : allInSquads.size;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* Header */}
      <div style={{ background:"linear-gradient(135deg,#0d0d1a,var(--bz-s1))", border:"1px solid #1a1a2e", borderRadius:14, padding:"20px 24px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
          <div>
            <div style={{ fontSize:20, fontWeight:900, color:"var(--bz-ink)" }}>🏈 Hero Break Builder</div>
            <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginTop:4 }}>Plan single hero breaks or build squads for group breaks</div>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
            <span style={{ fontSize:12, color:"var(--bz-ink-3)" }}>{checkedCount} / {heroes.length} assigned · showing {filtered.length}</span>
            {/* Randomizer inline controls — always visible */}
            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
              <input type="number" min="1" max="20" value={randSquadCount} onChange={e=>setRandSquadCount(Math.max(1,parseInt(e.target.value)||1))}
                style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:6, color:"#A78BFA", padding:"4px 8px", fontSize:11, fontFamily:"inherit", width:52, textAlign:"center" }}/>
              <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>squads</span>
              <input type="number" min="1" max="10" value={randHeroesPerSquad} onChange={e=>setRandHeroesPerSquad(Math.max(1,parseInt(e.target.value)||1))}
                style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:6, color:"#A78BFA", padding:"4px 8px", fontSize:11, fontFamily:"inherit", width:44, textAlign:"center" }}/>
              <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>/squad</span>
              <button onClick={()=>{ setBreakMode("squad"); randomizeSquads(); }}
                disabled={heroes.length < randSquadCount * randHeroesPerSquad}
                style={{ background:"linear-gradient(135deg,rgba(124,58,237,0.3),rgba(167,139,250,0.2))", border:"1px solid #7C3AED55", color:"#A78BFA", borderRadius:8, padding:"5px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", opacity: heroes.length < randSquadCount*randHeroesPerSquad ? 0.4 : 1 }}>
                🎲 Randomize
              </button>
            </div>
            {checkedCount > 0 && (
              <button onClick={()=>setShowExport(p=>!p)}
                style={{ background:"rgba(74,222,128,0.1)", border:"1px solid rgba(74,222,128,0.3)", color:"#4ade80", borderRadius:8, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                📋 Export Break
              </button>
            )}
            <button onClick={resetAll} style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)", color:"#ef4444", borderRadius:8, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>↺ Reset</button>
          </div>
        </div>

        {/* Controls row */}
        <div style={{ display:"grid", gridTemplateColumns:"auto auto 1fr auto", gap:12, marginTop:16, alignItems:"center", flexWrap:"wrap" }}>
          {/* Set selector */}
          {/* Set selector — multi-select pills */}
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <div style={{ fontSize:10, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Sets ({selectedSets.size} selected)</div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {filteredBobaSetNames.length > 0 && filteredBobaSetNames.map(s => (
                <button key={s} onClick={()=>{ setSelectedSets(prev => { const n=new Set(prev); n.has(s)?n.delete(s):n.add(s); return n; }); setCheckedHeroes(new Set()); setTierFilter("all"); }}
                  style={{ background:selectedSets.has(s)?"rgba(232,49,122,0.15)":"var(--bz-s2)", color:selectedSets.has(s)?"#E8317A":"#555", border:`1.5px solid ${selectedSets.has(s)?"#E8317A44":"var(--bz-line-2)"}`, borderRadius:20, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                  🃏 {s}
                </button>
              ))}
              {Object.keys(HERO_SETS).map(s => (
                <button key={s} onClick={()=>{ setSelectedSets(prev => { const n=new Set(prev); n.has(s)?n.delete(s):n.add(s); return n; }); setCheckedHeroes(new Set()); setTierFilter("all"); }}
                  style={{ background:selectedSets.has(s)?"rgba(96,165,250,0.15)":"var(--bz-s2)", color:selectedSets.has(s)?"#60A5FA":"#555", border:`1.5px solid ${selectedSets.has(s)?"#60A5FA44":"var(--bz-line-2)"}`, borderRadius:20, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                  🏈 {s}
                </button>
              ))}
            </div>
          </div>

          {/* Break mode */}
          <div style={{ display:"flex", background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:8, overflow:"hidden" }}>
            {[["single","⚡ Single"],["squad","🛡 Squad"]].map(([m,l]) => (
              <button key={m} onClick={()=>setBreakMode(m)}
                style={{ background:breakMode===m?"#E8317A":"transparent", color:breakMode===m?"#fff":"#555", border:"none", padding:"7px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                {l}
              </button>
            ))}
          </div>

          {/* Search */}
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search heroes or players..."
            style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:8, color:"var(--bz-ink)", padding:"8px 12px", fontSize:13, fontFamily:"inherit", outline:"none" }}/>

          {/* View mode */}
          <div style={{ display:"flex", background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:8, overflow:"hidden" }}>
            {[["grid","⊞"],["list","☰"]].map(([m,l]) => (
              <button key={m} onClick={()=>setViewMode(m)}
                style={{ background:viewMode===m?"#333":"transparent", color:viewMode===m?"var(--bz-ink)":"#555", border:"none", padding:"7px 12px", fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Tier filter + sort row */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12, flexWrap:"wrap", gap:8 }}>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {tierOptions.map(t => {
              const cfg = TIER_CFG[t];
              return (
                <button key={t} onClick={()=>setTierFilter(t)}
                  style={{ background:tierFilter===t?(cfg?.bg||"rgba(232,49,122,0.12)"):"transparent", color:tierFilter===t?(cfg?.color||"#E8317A"):"#555", border:`1px solid ${tierFilter===t?(cfg?.border||"#E8317A44"):"var(--bz-line-2)"}`, borderRadius:20, padding:"4px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  {t === "all" ? `All (${tierCounts.all})` : `${cfg?.badge || t} (${tierCounts[t]})`}
                </button>
              );
            })}
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {/* Sort */}
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
              style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:8, color:"var(--bz-ink)", padding:"5px 10px", fontSize:11, fontFamily:"inherit", cursor:"pointer" }}>
              <option value="power-desc">⚡ Power: High → Low</option>
              <option value="power-asc">⚡ Power: Low → High</option>
              <option value="name-asc">A → Z Hero Name</option>
              <option value="name-desc">Z → A Hero Name</option>
              <option value="tier">By Tier</option>
            </select>
            {/* Hide assigned */}
            <button onClick={()=>setShowAssigned(p=>!p)}
              style={{ background:!showAssigned?"rgba(232,49,122,0.12)":"transparent", color:!showAssigned?"#E8317A":"#555", border:`1px solid ${!showAssigned?"#E8317A44":"var(--bz-line-2)"}`, borderRadius:8, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
              {showAssigned ? "👁 Show All" : "✓ Hide Assigned"}
            </button>
          </div>
        </div>
      </div>

      {/* Export Panel */}
      {showExport && checkedCount > 0 && (
        <div style={{ background:"#0a1a0a", border:"2px solid rgba(74,222,128,0.3)", borderRadius:12, padding:"16px 18px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div style={{ fontSize:13, fontWeight:800, color:"#4ade80" }}>📋 Whatnot Break Format</div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={copyBreak}
                style={{ background: copied?"rgba(74,222,128,0.2)":"rgba(74,222,128,0.1)", border:"1px solid rgba(74,222,128,0.4)", color:"#4ade80", borderRadius:8, padding:"6px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                {copied ? "✅ Copied!" : "📋 Copy All"}
              </button>
              <button onClick={()=>setShowExport(false)}
                style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:8, padding:"6px 10px", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>×</button>
            </div>
          </div>

          {breakMode === "single" ? (
            // Single break — numbered spot list
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              {heroes.filter(h => checkedHeroes.has(`${h.hero}-${h.tier}`)).map((h, i) => {
                const cfg = TIER_CFG[h.tier] || TIER_CFG.Base;
                return (
                  <div key={`${h.hero}-${h.tier}`}
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 10px", background:"rgba(0,0,0,0.3)", borderRadius:7, fontFamily:"monospace" }}>
                    <span style={{ fontSize:11, color:"var(--bz-ink-3)", minWidth:28 }}>#{i+1}</span>
                    <span style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)", flex:1 }}>{h.hero}</span>
                    <span style={{ fontSize:11, color:"#999" }}>{h.inspired}</span>
                    <span style={{ fontSize:10, color:cfg.color }}>{h.tier === "Featured Auto" ? "⭐ FA" : h.tier === "Highlighted" ? "💜 HL" : "🔵"}</span>
                    {h.power>0 && <span style={{ fontSize:11, fontWeight:700, color:powerColor(h.power) }}>⚡{h.power}</span>}
                    <button onClick={()=>{
                      const line = `Spot ${i+1}: ${h.hero} (${h.inspired})${h.power?` ⚡${h.power}`:""}`;
                      navigator.clipboard?.writeText(line);
                    }} style={{ background:"none", border:"none", color:"#8a8a8a", cursor:"pointer", fontSize:11, padding:"0 4px" }} title="Copy this line">⎘</button>
                  </div>
                );
              })}
            </div>
          ) : (
            // Squad break — grouped by squad
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {squads.filter(sq => sq.heroes.length > 0).map((sq, qi) => {
                const avg = sq.heroes.length ? Math.round(sq.heroes.reduce((s,h)=>s+(h.power||0),0)/sq.heroes.length) : 0;
                const sqText = `${sq.name} (${sq.heroes.length} heroes${avg?" · avg ⚡"+avg:""})\n` +
                  sq.heroes.map(h=>`  • ${h.hero} (${h.inspired})${h.power?` ⚡${h.power}`:""}`).join("\n");
                return (
                  <div key={sq.id} style={{ background:"rgba(0,0,0,0.3)", borderRadius:8, padding:"10px 14px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                      <div>
                        <span style={{ fontSize:13, fontWeight:800, color:"#4ade80" }}>{sq.name}</span>
                        <span style={{ fontSize:11, color:"var(--bz-ink-3)", marginLeft:8 }}>{sq.heroes.length} heroes{avg?` · avg ⚡${avg}`:""}</span>
                      </div>
                      <button onClick={()=>navigator.clipboard?.writeText(sqText)}
                        style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:6, padding:"3px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>
                        ⎘ Copy Squad
                      </button>
                    </div>
                    {sq.heroes.map(h => {
                      const cfg = TIER_CFG[h.tier] || TIER_CFG.Base;
                      return (
                        <div key={h.sid} style={{ display:"flex", alignItems:"center", gap:8, padding:"4px 0", borderTop:"1px solid var(--bz-line)" }}>
                          <span style={{ fontSize:10, color:cfg.color }}>•</span>
                          <span style={{ fontSize:12, fontWeight:700, color:"var(--bz-ink)", flex:1 }}>{h.hero}</span>
                          <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{h.inspired}</span>
                          {h.power>0 && <span style={{ fontSize:11, fontWeight:700, color:powerColor(h.power) }}>⚡{h.power}</span>}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* Raw text area for manual copy */}
          <div style={{ marginTop:12 }}>
            <div style={{ fontSize:10, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Raw text — paste into Whatnot description</div>
            <textarea readOnly value={buildBreakText(breakMode)}
              style={{ width:"100%", background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:8, color:"var(--bz-ink-2)", padding:"10px 12px", fontSize:11, fontFamily:"monospace", lineHeight:1.6, resize:"vertical", minHeight:120, boxSizing:"border-box" }}
              onClick={e=>e.target.select()}
            />
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns: breakMode === "squad" ? "1fr 340px" : "1fr", gap:16 }}>

        {/* Hero grid/list */}
        <div>
          {viewMode === "grid" ? (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:10 }}>
              {filtered.map(h => {
                const heroKey = `${h.hero}-${h.tier}-${h.setName||""}`;
                const isChecked = breakMode === "single" ? checkedHeroes.has(heroKey) : allInSquads.has(heroKey);
                const cfg = TIER_CFG[h.tier] || TIER_CFG.Base;
                const pc = powerColor(h.power);
                return (
                  <div key={heroKey}
                    onClick={() => breakMode === "single" ? toggleHero(heroKey) : addToSquad(h)}
                    style={{ background: isChecked ? cfg.bg : "var(--bz-s1)", border:`1.5px solid ${isChecked ? cfg.color : "var(--bz-s2)"}`, borderRadius:10, padding:"12px", cursor: isChecked && breakMode==="squad" ? "not-allowed" : "pointer", position:"relative", opacity: isChecked ? 0.45 : 1, transition:"all 0.15s" }}>

                    {/* Tier badge + set name if multiple sets */}
                    <div style={{ fontSize:9, fontWeight:800, color:cfg.color, textTransform:"uppercase", letterSpacing:1, marginBottom:6, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span>{h.tier === "Featured Auto" ? "⭐ FA" : h.tier === "Highlighted" ? "💜 HL" : cfg?.badge || h.tier}</span>
                      {selectedSets.size > 1 && h.setName && <span style={{ color:"#8a8a8a", fontWeight:600, fontSize:8 }}>{h.setName.length > 12 ? h.setName.slice(0,12)+"…" : h.setName}</span>}
                    </div>

                    {/* Hero name */}
                    <div style={{ fontSize:14, fontWeight:900, color: isChecked ? cfg.color : "var(--bz-ink)", lineHeight:1.2, marginBottom:4 }}>{h.hero}</div>
                    <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginBottom:8 }}>{h.inspired}</div>

                    {/* Power */}
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ fontSize:11, fontWeight:800, color:pc }}>⚡{h.power}</span>
                      {isChecked && (
                        <span style={{ fontSize:14 }}>{breakMode === "single" ? "✅" : "🛡"}</span>
                      )}
                    </div>

                    {/* Squad label */}
                    {breakMode === "squad" && allInSquads.has(heroKey) && (() => {
                      const sq = squads.find(s => s.heroes.some(x => `${x.hero}-${x.tier}-${x.setName||""}` === heroKey));
                      return sq ? <div style={{ fontSize:9, color:cfg.color, marginTop:4, fontWeight:700 }}>{sq.name}</div> : null;
                    })()}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {filtered.map(h => {
                const heroKey = `${h.hero}-${h.tier}-${h.setName||""}`;
                const isChecked = breakMode === "single" ? checkedHeroes.has(heroKey) : allInSquads.has(heroKey);
                const cfg = TIER_CFG[h.tier] || TIER_CFG.Base;
                const pc = powerColor(h.power);
                return (
                  <div key={heroKey}
                    onClick={() => breakMode === "single" ? toggleHero(heroKey) : addToSquad(h)}
                    style={{ background: isChecked ? cfg.bg : "var(--bz-s1)", border:`1.5px solid ${isChecked ? cfg.color : "var(--bz-s2)"}`, borderRadius:8, padding:"10px 14px", cursor: isChecked && breakMode==="squad" ? "not-allowed" : "pointer", display:"flex", alignItems:"center", gap:12, opacity: isChecked ? 0.45 : 1, transition:"all 0.15s" }}>
                    <div style={{ fontSize:10, fontWeight:800, color:cfg.color, minWidth:28 }}>
                      {h.tier === "Featured Auto" ? "⭐" : h.tier === "Highlighted" ? "💜" : "🔵"}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:800, color: isChecked ? cfg.color : "var(--bz-ink)" }}>{h.hero}</div>
                      <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{h.inspired}</div>
                    </div>
                    <div style={{ fontSize:12, fontWeight:800, color:pc }}>⚡{h.power}</div>
                    {isChecked && <span style={{ fontSize:16 }}>{breakMode === "single" ? "✅" : "🛡"}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Squad panel */}
        {breakMode === "squad" && (
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)" }}>🛡 Squads</div>
              <button onClick={addSquad}
                style={{ background:"rgba(232,49,122,0.12)", border:"1px solid #E8317A44", color:"#E8317A", borderRadius:8, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                + Add Squad
              </button>
            </div>

            {squads.map(sq => (
              <div key={sq.id}
                style={{ background: activeSquad === sq.id ? "#0d1a0d" : "var(--bz-s1)", border:`2px solid ${activeSquad === sq.id ? "#4ade80" : "var(--bz-s2)"}`, borderRadius:10, padding:"12px 14px", cursor:"pointer" }}
                onClick={() => setActiveSquad(sq.id === activeSquad ? null : sq.id)}>

                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: sq.heroes.length ? 10 : 0 }}>
                  <div>
                    <input
                      value={sq.name}
                      onClick={e => e.stopPropagation()}
                      onChange={e => setSquads(prev => prev.map(s => s.id === sq.id ? {...s, name: e.target.value} : s))}
                      style={{ background:"transparent", border:"none", color: activeSquad === sq.id ? "#4ade80" : "var(--bz-ink)", fontSize:13, fontWeight:800, fontFamily:"inherit", outline:"none", width:140 }}
                    />
                    <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{sq.heroes.length} hero{sq.heroes.length !== 1 ? "s" : ""} · {activeSquad === sq.id ? "Active — click a hero to add" : "Click to select"}</div>
                  </div>
                  <div style={{ fontSize:16, color: activeSquad === sq.id ? "#4ade80" : "#333" }}>
                    {activeSquad === sq.id ? "✏️" : "○"}
                  </div>
                </div>

                {sq.heroes.length > 0 && (
                  <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                    {sq.heroes.map(h => {
                      const cfg = TIER_CFG[h.tier] || TIER_CFG.Base;
                      return (
                        <div key={h.sid} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 8px", background:"rgba(0,0,0,0.3)", borderRadius:6 }}>
                          <span style={{ fontSize:10, color:cfg.color }}>{h.tier === "Featured Auto" ? "⭐" : h.tier === "Highlighted" ? "💜" : "🔵"}</span>
                          <span style={{ fontSize:12, fontWeight:700, color:"var(--bz-ink)", flex:1 }}>{h.hero}</span>
                          <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{h.inspired}</span>
                          <span style={{ fontSize:11, fontWeight:800, color:powerColor(h.power) }}>⚡{h.power}</span>
                          <button onClick={e => { e.stopPropagation(); removeFromSquad(sq.id, h.sid); }}
                            style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:12, padding:"0 2px" }}>×</button>
                        </div>
                      );
                    })}
                    <div style={{ fontSize:10, color:"var(--bz-ink-3)", textAlign:"right", marginTop:4 }}>
                      Avg Power: <strong style={{color:"var(--bz-ink)"}}>{sq.heroes.length ? Math.round(sq.heroes.reduce((s,h)=>s+h.power,0)/sq.heroes.length) : "--"}</strong>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Streams({ defaultStreamTab="recap", inventory, breaks, onAdd, onBulkAdd, onDeleteBreak, user, userRole, streams=[], onSaveStream, onDeleteStream, productUsage=[], onSaveProductUsage, shipments=[], skuPrices={}, historicalData=[], onSavePayStub, onUpsertBuyers, payStubs=[], onDeletePayStub, cardPools=[], imcFormUrl="", onSaveImcFormUrl, plannedStreams=[], bobaCards=[], csvImports=[] }) {
  const isAdmin    = ["Admin"].includes(userRole?.role);
  const isShipping = userRole?.role === "Shipping";
  const ALL_STREAM_TABS = [
    { id:"recap",       label:"\uD83D\uDCCB Stream Recap",     roles:["Admin","Streamer","StreamerLite"] },
    { id:"cards",       label:"\uD83C\uDCCF Log Cards",        roles:["Admin","Streamer","Shipping","StreamerLite"] },
    { id:"commission",  label:"\uD83D\uDCB5 Commission",       roles:["Admin","Streamer","StreamerLite"] },
    { id:"breakspots",  label:"🎯 Break Spots",                roles:["Admin","Streamer","StreamerLite"] },
    { id:"shownotes",   label:"📝 Show Notes",                 roles:["Admin","Streamer","StreamerLite"] },
    { id:"wotftools",   label:"🌟 WotF Seller Tools",          roles:["Admin"] },
    { id:"bobatools",   label:"⚔️ BoBA Seller Tools",          roles:["Admin"] },
    { id:"planner",     label:"\uD83E\uDDEE Break Planner",    roles:["Admin","Streamer","StreamerLite"] },
    { id:"calendar",    label:"\uD83D\uDCC5 Bazooka Calendar",  roles:["Admin","Streamer","StreamerLite"] },
    { id:"herobreak",   label:"\uD83C\uDFC8 Hero Breaks",      roles:["Admin","Streamer","StreamerLite"] },
  ];
  const STREAM_TABS = ALL_STREAM_TABS.filter(t => t.roles.includes(userRole?.role));
  const [streamTab, setStreamTab] = useState(defaultStreamTab !== "recap" ? defaultStreamTab : (isShipping ? "cards" : "recap"));
  useEffect(()=>{if(defaultStreamTab!=="recap")setStreamTab(defaultStreamTab);},[defaultStreamTab]);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      {/* Sub-tab bar */}
      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
        {STREAM_TABS.map(t => (
          <button key={t.id} onClick={()=>setStreamTab(t.id)}
            style={{ background:streamTab===t.id?"rgba(232,49,122,0.12)":"transparent", color:streamTab===t.id?"#E8317A":"#555", border:`1.5px solid ${streamTab===t.id?"#E8317A44":"var(--bz-line-2)"}`, borderRadius:20, padding:"6px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
            {t.label}
          </button>
        ))}
      </div>

      {streamTab === "recap"      && <BreakLog      inventory={inventory} breaks={breaks} onAdd={onAdd} onBulkAdd={onBulkAdd} onDeleteBreak={onDeleteBreak} user={user} userRole={userRole} streams={streams} onSaveStream={onSaveStream} onDeleteStream={onDeleteStream} productUsage={productUsage} onSaveProductUsage={onSaveProductUsage} shipments={shipments} recapOnly={true} skuPrices={skuPrices} onUpsertBuyers={onUpsertBuyers} imcFormUrl={imcFormUrl} onSaveImcFormUrl={onSaveImcFormUrl} plannedStreams={plannedStreams} csvImports={csvImports}/>}
      {streamTab === "cards"      && <BreakLog      inventory={inventory} breaks={breaks} onAdd={onAdd} onBulkAdd={onBulkAdd} onDeleteBreak={onDeleteBreak} user={user} userRole={userRole} streams={streams} onSaveStream={onSaveStream} productUsage={productUsage} onSaveProductUsage={onSaveProductUsage} shipments={shipments} cardsOnly={true} cardPools={cardPools}/>}
      {streamTab === "commission" && <Commission    streams={streams} onSave={onSaveStream} onDelete={onDeleteStream} user={user} userRole={userRole} historicalData={historicalData} onSavePayStub={onSavePayStub} payStubs={payStubs} onDeletePayStub={onDeletePayStub}/>}
      {streamTab === "planner"    && <BreakPlanner  skuPrices={skuPrices} userRole={userRole}/>}
      {streamTab === "calendar"   && <StreamCalendar streams={streams} skuPrices={skuPrices} inventory={inventory} breaks={breaks} cardPools={cardPools} userRole={userRole} user={user}/>}
      {streamTab === "herobreak"  && <HeroBreakBuilder userRole={userRole} bobaCards={bobaCards}/>}
      {streamTab === "breakspots" && <BreakSpots bobaCards={bobaCards}/>}
      {streamTab === "shownotes"  && <ShowNotes userRole={userRole}/>}
      {streamTab === "wotftools"  && <WotFSellerTools userRole={userRole}/>}
      {streamTab === "bobatools"  && <BoBASellerTools userRole={userRole}/>}
    </div>
  );
}

// --- COMMISSION --------------------------------------------------
function StubRow({ stub, S, onDeletePayStub }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, overflow:"hidden" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 16px", flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ width:32, height:32, borderRadius:"50%", background:"var(--bz-s2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>{"\uD83D\uDCB5"}</div>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontWeight:800, fontSize:13, color:"var(--bz-ink)" }}>{stub.breaker}</span>
              <span style={{ fontSize:11, color:"#999" }}>{stub.period}</span>
              {!stub.read && <span style={{ background:"#E8317A22", color:"#E8317A", border:"1px solid #E8317A44", borderRadius:20, padding:"1px 8px", fontSize:10, fontWeight:700 }}>Unread</span>}
            </div>
            <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>
              {stub.streamCount} stream{stub.streamCount!==1?"s":""} · Generated {new Date(stub.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})} by {stub.createdBy||"Admin"}
            </div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:18, fontWeight:900, color:"#4ade80" }}>{fmt(stub.totalComm)}</div>
            <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Commission</div>
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={()=>setExpanded(p=>!p)} style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:7, padding:"5px 10px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{expanded?"\u25B2 Hide":"\u25BC View"}</button>
            <button onClick={()=>{ if(window.confirm(`Delete pay stub for ${stub.breaker}?\n\nThis removes it from their dashboard too.`)) onDeletePayStub(stub.id); }} style={{ background:"#1a0a0a", border:"1px solid #E8317A33", color:"#E8317A", borderRadius:7, padding:"5px 10px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDDD1"}</button>
          </div>
        </div>
      </div>
      {expanded && (
        <div style={{ borderTop:"1px solid var(--bz-line)", padding:"12px 16px" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr>
              {["Date","Type","Gross","Baz Net","Rate","Commission"].map(h=><th key={h} style={S.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {(stub.streams||[]).map((s,i)=>(
                <tr key={i} style={{ background:i%2===0?"var(--bz-s1)":"var(--bz-s1)" }}>
                  <td style={S.td}>{new Date(s.date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</td>
                  <td style={{ ...S.td, color:"var(--bz-ink-2)" }}>{s.breakType}{s.binOnly?" BIN":""}{s.sessionType?<span style={{marginLeft:5,fontSize:11,color:"#7B9CFF"}}>{({day:"\u2600\uFE0F",night:"\uD83C\uDF19",weekend:"\uD83D\uDCC5",event:"\uD83C\uDF89"})[s.sessionType]||""}</span>:""}</td>
                  <td style={{ ...S.td, color:"#E8317A", fontWeight:700 }}>{fmt(s.gross)}</td>
                  <td style={{ ...S.td, color:"#7B9CFF" }}>{fmt(s.bazNet||0)}</td>
                  <td style={{ ...S.td, color:"var(--bz-ink-2)" }}>{(s.rate*100).toFixed(0)}%</td>
                  <td style={{ ...S.td, color:"#4ade80", fontWeight:900 }}>{fmt(s.commAmt)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background:"#0a1a0a", borderTop:"2px solid #4ade8033" }}>
                <td colSpan={5} style={{ ...S.td, fontWeight:800, color:"var(--bz-ink)" }}>Total ({stub.streamCount} streams)</td>
                <td style={{ ...S.td, color:"#4ade80", fontWeight:900, fontSize:15 }}>{fmt(stub.totalComm)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function Commission({ streams, onSave, onDelete, user, userRole, historicalData=[], onSavePayStub, payStubs=[], onDeletePayStub }) {
  const isAdmin    = ["Admin"].includes(userRole?.role);
  const [isMobileC, setIsMobileC] = useState(typeof window!=="undefined" && window.innerWidth < 768);
  useEffect(()=>{
    const onR=()=>setIsMobileC(window.innerWidth<768);
    window.addEventListener("resize",onR);
    return ()=>window.removeEventListener("resize",onR);
  },[]);
  const curUser    = user?.displayName?.split(" ")[0] || "";
  const myBreaker  = resolveBreaker(user);

  const EMPTY = { date:"", breaker:"", breakType:"auction", grossRevenue:"", whatnotFees:"", coupons:"", whatnotPromo:"", magpros:"", packagingMaterial:"", topLoaders:"", chaserCards:"", chaserCardIds:"", marketMultiple:"", newBuyers:"", binOnly:false, notes:"" };
  const [form,      setForm]      = useState(EMPTY);
  const [editing,   setEditing]   = useState(null);
  const [viewStream,setViewStream]= useState(null);
  const [importing, setImporting] = useState(false);
  const [csvError,  setCsvError]  = useState("");
  const [showStub,     setShowStub]     = useState(false);
  const [showStubHist, setShowStubHist] = useState(false);
  const [stubBreaker,  setStubBreaker]  = useState("");
  const [stubPeriod,   setStubPeriod]   = useState("week");
  const [stubHistFilter, setStubHistFilter] = useState("");
  const [stubAdminView,  setStubAdminView]  = useState(false); // false = rep view (default for sending)
  const [stubFrom,    setStubFrom]    = useState("");
  const [stubTo,      setStubTo]      = useState("");

  // Commission rate from comp plan
  function getCommRate(stream) { return getRate(stream); }

  const calcStreamDash = (s) => calcStreamMemo(s);

  // Admins see all streams; streamers see only their own
  const CEO_NAMES = ["Dev","Devin","Derrik"];
  const isCEO = CEO_NAMES.some(n => curUser.toLowerCase().includes(n.toLowerCase()));
  const visibleStreams = isAdmin
    ? streams
    : streams.filter(s => s.breaker === myBreaker || (s.eventStaff||[]).some(es => es.breaker === myBreaker) || s.splitRep === myBreaker);

  // Period filter -- available to everyone
  const [period,        setPeriod]        = useState("month");
  const [customFrom,    setCustomFrom]    = useState("");
  const [customTo,      setCustomTo]      = useState("");
  const [breakerFilter, setBreakerFilter] = useState("all");

  function inPeriod(dateStr) {
    const d   = parseLocalDate(dateStr);
    const now = new Date();
    if (period === "week") {
      const start = new Date(now);
      const day = now.getDay();
      start.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
      start.setHours(0,0,0,0);
      const end = new Date(start); end.setDate(start.getDate()+6); end.setHours(23,59,59,999);
      return d >= start && d <= end;
    }
    if (period === "month")   return d.getMonth()===now.getMonth() && d.getFullYear()===now.getFullYear();
    if (period === "quarter") {
      const q = Math.floor(now.getMonth()/3);
      return Math.floor(d.getMonth()/3)===q && d.getFullYear()===now.getFullYear();
    }
    if (period === "year")    return d.getFullYear()===now.getFullYear();
    if (period === "custom" && customFrom && customTo) {
      const from = parseLocalDate(customFrom); from.setHours(0,0,0,0);
      const to   = parseLocalDate(customTo);   to.setHours(23,59,59,999);
      return d >= from && d <= to;
    }
    return true; // "all"
  }

  const periodFiltered = useMemo(() => visibleStreams.filter(s => inPeriod(s.date)),
    [visibleStreams, period, customFrom, customTo]);
  const filteredStreams = useMemo(() => isAdmin && breakerFilter !== "all"
    ? periodFiltered.filter(s => s.breaker === breakerFilter || (s.eventStaff||[]).some(es => es.breaker === breakerFilter) || s.splitRep === breakerFilter)
    : periodFiltered,
    [periodFiltered, isAdmin, breakerFilter]);

  // Aggregates
  const totals = filteredStreams.reduce((acc, s) => {
    const targetBreaker = !isAdmin ? myBreaker : (breakerFilter !== "all" ? breakerFilter : null);
    const c = targetBreaker ? calcStream(s, targetBreaker) : calcStreamDash(s);
    const myStaff = targetBreaker ? (s.eventStaff||[]).find(es => es.breaker === targetBreaker) : null;
    const isSplitRep = targetBreaker && s.splitRep === targetBreaker;
    const isEventOnly = !!myStaff && s.breaker !== targetBreaker && !isSplitRep;
    const myEventFee = myStaff ? Math.min(1000, c.bazNet * 0.15) : 0;
    const ownStream = !isEventOnly && !isSplitRep && !c.excludeFinancials && !c.isSingles;
    acc.gross     += ownStream ? c.gross : 0;
    acc.net       += ownStream ? c.netRev : 0;
    acc.baz       += ownStream ? c.bazNet : 0;
    const eventStaffTotal = (s.eventStaff||[]).reduce((sum,_)=>sum+Math.min(1000, c.bazNet*0.15), 0);
    // SINGLES: pass-through revenue (breaker keeps 100%) — track separately from earned break commission.
    if (c.isSingles) {
      acc.singlesRev += c.myComm; // myComm for singles = the full pass-through amount
    } else {
      // With a target breaker, c.myComm already includes their event fee (computed in calcStream).
      acc.comm    += !targetBreaker
                   ? (c.primaryCommAmt||c.commAmt) - (c.repExpShare||0) + (c.salesBonus||0) + (c.tips||0) + (c.splitRepAmt||0) + eventStaffTotal
                   : c.myComm;
    }
    acc.trueNet   += ownStream ? (c.bazTrueNet||0) : 0;
    acc.imcReimb  += c.imcReimb||0;
    acc.biguReimb += c.biguReimb||0;
    acc.newBuyers += parseInt(s.newBuyers)||0;
    if (!isEventOnly && parseFloat(s.marketMultiple) > 0 && (s.channel||"Bazooka Vault") !== "Orbital Society") {
      acc.mmSum   += parseFloat(s.marketMultiple);
      acc.mmCount += 1;
    }
    return acc;
  }, { gross:0, net:0, baz:0, comm:0, singlesRev:0, trueNet:0, imcReimb:0, biguReimb:0, newBuyers:0, mmSum:0, mmCount:0 });

  function openNew()   { setForm({...EMPTY, date:todayLocal()}); setEditing("new"); setViewStream(null); }
  function openEdit(s) { setForm({...s}); setEditing(s.id); setViewStream(null); }
  function cancelEdit(){ setEditing(null); setForm(EMPTY); }

  function f(k) { return v => setForm(p=>({...p,[k]:v})); }

  async function handleSave() {
    if (!form.date || !form.breaker) return;
    await onSave({ ...form, id: editing === "new" ? uid() : editing });
    setEditing(null); setForm(EMPTY);
  }

  // CSV import -- parse Whatnot export
  function handleCSV(e) {
    const file = e.target.files[0]; if (!file) return;
    setCsvError("");
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const lines = ev.target.result.split("\n").map(l=>l.trim()).filter(Boolean);
        const headers = lines[0].split(",").map(h=>h.replace(/"/g,"").toLowerCase().trim());
        const row = lines[1]?.split(",").map(c=>c.replace(/"/g,"").trim());
        if (!row) { setCsvError("No data rows found in CSV."); return; }
        const get = (key) => { const i=headers.findIndex(h=>h.includes(key)); return i>=0?row[i]:""; };
        const gross   = parseFloat(get("gross")  || get("revenue") || get("total sales") || "0") || 0;
        const fees    = parseFloat(get("fee")    || get("platform fee") || "0") || 0;
        const coupons = parseFloat(get("coupon") || "0") || 0;
        setForm(p=>({ ...p, grossRevenue:gross.toFixed(2), whatnotFees:fees.toFixed(2), coupons:coupons.toFixed(2) }));
        setImporting(false);
      } catch(err) { setCsvError("Could not parse CSV. Try entering values manually."); }
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  // -- DETAIL VIEW ----------------------------------------------
  if (viewStream) {
    const s = streams.find(x=>x.id===viewStream);
    if (!s) { setViewStream(null); return null; }
    const c = calcStreamDash(s);
    const bc = BC[s.breaker] || { bg:"#EEF0FB", text:"#2C3E7A", border:"#3730a3" };
    const EXPENSE_ROWS = [
      { l:"Whatnot Fees",        v:parseFloat(s.whatnotFees)||0 },
      { l:"Coupons",             v:parseFloat(s.coupons)||0 },
      { l:"Whatnot Promo",       v:parseFloat(s.whatnotPromo)||0 },
      { l:"MagPros",             v:parseFloat(s.magpros)||0 },
      { l:"Packaging Materials", v:parseFloat(s.packagingMaterial)||0 },
      { l:"Top Loaders",         v:parseFloat(s.topLoaders)||0 },
      { l:"Chaser Cards",        v:parseFloat(s.chaserCards)||0 },
    ];
    return (
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={()=>setViewStream(null)} style={{ background:"var(--bz-s1)", border:"1.5px solid #E5E7EB", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", color:"var(--bz-ink-2)" }}>{"\u2190 Back"}</button>
          <div>
            <div style={{ fontSize:18, fontWeight:900, color:"var(--bz-ink)" }}>{new Date(s.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}</div>
            <div style={{ fontSize:12, color:"var(--bz-ink-2)", marginTop:2, display:"flex", gap:10 }}>
              <Badge bg={bc.bg} color={bc.text}>{s.breaker}</Badge>
              <span>{s.isEvent ? "🎪 Event Break (15% event fee)" : s.binOnly ? "BIN Break (flat 35%)" : `${s.breakType} · ${(c.rate*100).toFixed(0)}% commission`}</span>
              {s.newBuyers>0 && <span style={{ background:"var(--bz-s1)", color:"#E8317A", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700 }}>{"\uD83C\uDF31"}{s.newBuyers} new buyers</span>}
              {s.collabPartner && s.collabPartner !== "_" && <span style={{ background:"rgba(123,156,255,0.12)", color:"#7B9CFF", border:"1px solid rgba(123,156,255,0.25)", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700 }}>🤝 Collab: {s.collabPartner}{s.collabPct?` (${s.collabPct}%)`:""}</span>}
            </div>
          </div>

        </div>


        {/* Commission calc */}
        <div style={{ ...S.card, border:"2px solid #166534" }}>
          <SectionLabel t={`${s.breaker}'s Commission`} />
          <div style={{ display:"flex", flexDirection:"column", gap:4, marginBottom:16 }}>
            {(isAdmin ? [
              { l:"Gross Revenue (true)",                                             v:fmt(c.gross),                                      c:"var(--bz-ink)", indent:false },
              { l:`\u2212 Whatnot Fees`,                                                   v:"\u2212 "+fmt(parseFloat(s.whatnotFees)||0),         c:"#666",    indent:true  },
              { l:`\u2212 Coupons`,                                                        v:"\u2212 "+fmt(parseFloat(s.coupons)||0),             c:"#666",    indent:true  },
              { l:"= Split Base (70/30 applied here)",                               v:fmt(c.splitBase),                                  c:"var(--bz-ink)", indent:false, bold:true },
              { l:"\u00D7 30% \u2192 Bazooka Share",                                       v:fmt(c.bazNet),                                     c:"#E8317A", indent:true  },
              ...(c.collabAmt>0?[{ l:`\u2212 Collab (${s.collabPartner} ${s.collabPct}%)`, v:"\u2212 "+fmt(c.collabAmt), c:"#7B9CFF", indent:true }]:[]),
              { l:`\u2212 Rep Commission (${(c.rate*100).toFixed(0)}%)`,                   v:"\u2212 "+fmt(c.commAmt),                           c:"#991b1b", indent:true  },
              ...(c.splitRep ? [
                { l:`  ↳ ${s.breaker} (${Math.round((c.splitPct||1)*100)}%)`,        v:fmt(c.primaryCommAmt), c:"#FBBF24", indent:true },
                { l:`  ↳ ${c.splitRep} (${Math.round((1-(c.splitPct||1))*100)}%)`,   v:fmt(c.splitRepAmt),    c:"#FBBF24", indent:true },
              ] : []),
              { l:`\u2212 Bazooka Expense Share (${((1-c.rate)*0.30*100).toFixed(1)}%)`,   v:"\u2212 "+fmt(c.bazExpShare||0),                    c:"#991b1b", indent:true  },
              ...(c.eventStaffAmt>0?[{ l:`🎪 Event Staff (${(s.eventStaff||[]).map(e=>e.breaker).join(", ")})`, v:"\u2212 "+fmt(c.eventStaffAmt), c:"#A78BFA", indent:true }]:[]),
              { l:`+ Rep Reimburses ${(c.rate*0.30*100 * (c.splitPct||1)).toFixed(1)}% of expenses`, v:"+ "+fmt((c.repExpShare||0)*(c.splitPct||1)), c:"#4ade80", indent:true  },
              ...(c.imcReimb>0 ? [{ l:`💙 IMC Reimburses 70% of expenses`, v:"+ "+fmt(c.imcReimb), c:"#60A5FA", indent:true }] : []),
              ...(c.imcDirectReimb>0 ? [{ l:`💙 IMC Direct Reimb${s.imcReimbNote?" — "+s.imcReimbNote:""}`, v:"+ "+fmt(c.imcDirectReimb), c:"#60A5FA", indent:true }] : []),
            ] : [
              { l:"Gross Revenue (true)",                                             v:fmt(c.gross),                                      c:"var(--bz-ink)", indent:false },
              { l:`− Whatnot Fees`,                                                   v:"− "+fmt(parseFloat(s.whatnotFees)||0),         c:"#666",    indent:true  },
              { l:`− Coupons`,                                                        v:"− "+fmt(parseFloat(s.coupons)||0),             c:"#666",    indent:true  },
              { l:"= Net Revenue",                                                    v:fmt(c.netRev),                                     c:"var(--bz-ink)", indent:false, bold:true },
              ...(c.collabAmt>0?[{ l:`− Collab (${s.collabPartner} ${s.collabPct}%)`, v:"− "+fmt(c.collabAmt), c:"#7B9CFF", indent:true }]:[]),
              { l:`Your Commission (${(c.rate*100).toFixed(0)}%)`,                    v:fmt(c.primaryCommAmt||c.commAmt),              c:"#4ade80", indent:true  },
              { l:`− Your Expense Share (${(c.rate*0.30*(c.splitPct||1)*100).toFixed(1)}%)`, v:"− "+fmt((c.repExpShare||0)*(c.splitPct||1)), c:"#991b1b", indent:true  },
            ]).map(({l,v,c:clr,indent,bold}) => (
              <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"7px 12px", borderBottom:"1px solid var(--bz-line)", paddingLeft:indent?"24px":"12px" }}>
                <span style={{ fontSize:13, color:bold?"var(--bz-ink)":"var(--bz-ink-2)", fontWeight:bold?700:400 }}>{l}</span>
                <span style={{ fontSize:13, fontWeight:700, color:clr }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 18px", background:"#0a1a0a", border:"1px solid rgba(22,101,52,0.4)", borderRadius:10, marginBottom:4 }} className="save-flash">
            <div style={{flex:1}}>
              <span style={{ fontWeight:800, fontSize:16, color:"#4ade80" }}>{"💵 Total Commission Paid Out"}</span>
              {c.splitRep ? (
                <div style={{marginTop:6,display:"flex",flexDirection:"column",gap:3}}>
                  <div style={{fontSize:12,color:"var(--bz-ink-2)"}}>
                    <span style={{color:BC[s.breaker]?.text||"#E8317A",fontWeight:700}}>{s.breaker}</span>
                    {" "}{fmt(c.primaryCommAmt)} − {fmt((c.repExpShare||0)*(c.splitPct||0.5))} exp = <span style={{color:"#4ade80",fontWeight:700}}>{fmt(c.primaryCommAmt-(c.repExpShare||0)*(c.splitPct||0.5)+(c.salesBonus||0)+(c.tips||0))}</span>
                  </div>
                  <div style={{fontSize:12,color:"var(--bz-ink-2)"}}>
                    <span style={{color:BC[c.splitRep]?.text||"#FBBF24",fontWeight:700}}>{c.splitRep}</span>
                    {" "}{fmt(c.splitRepAmt)} − {fmt((c.repExpShare||0)*(1-(c.splitPct||0.5)))} exp = <span style={{color:"#4ade80",fontWeight:700}}>{fmt(c.splitRepAmt-(c.repExpShare||0)*(1-(c.splitPct||0.5)))}</span>
                  </div>
                </div>
              ) : (
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:2}}>
                  {fmt(c.commAmt)} gross − {fmt(c.repExpShare||0)} expenses
                  {c.salesBonus>0&&` + ${fmt(c.salesBonus)} bonus`}
                  {c.tips>0&&` + ${fmt(c.tips)} tips`}
                </div>
              )}
            </div>
            <span style={{ fontWeight:900, fontSize:28, color:"#4ade80", marginLeft:16 }}>
              {fmt(c.commAmt - (c.repExpShare||0) + (c.salesBonus||0) + (c.tips||0))}
            </span>
          </div>
          {isAdmin && (
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 18px", background:"var(--bz-s1)", borderRadius:10 }}>
            <span style={{ fontWeight:800, fontSize:16, color:"#E8317A" }}>{"\uD83C\uDFE6 Bazooka True Net"}</span>
            <span style={{ fontWeight:900, fontSize:28, color:"#E8317A" }}>{fmt(c.bazTrueNet)}</span>
          </div>
          )}
          {s.marketMultiple && !s.binOnly && (
            <div style={{ marginTop:10, fontSize:12, color:"var(--bz-ink-2)", textAlign:"right" }}>Market multiple: {s.marketMultiple}x → {(c.rate*100).toFixed(0)}% rate</div>
          )}
          {s.notes && <div style={{ marginTop:10, padding:"8px 12px", background:"var(--bz-s1)", borderRadius:7, fontSize:12, color:"var(--bz-ink-2)", fontStyle:"italic" }}>{s.notes}</div>}
        </div>
      </div>
    );
  }

  // -- FORM -----------------------------------------------------
  if (editing) {
    const preview = calcStream(form);
    return (
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={cancelEdit} style={{ background:"var(--bz-s1)", border:"1.5px solid #E5E7EB", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", color:"var(--bz-ink-2)" }}>{"\u2190 Cancel"}</button>
          <div style={{ fontSize:16, fontWeight:800, color:"var(--bz-ink)" }}>{editing==="new"?"New Stream":"Edit Stream"}</div>
          {importing && (
            <label style={{ background:"#1A1A2E", color:"#E8317A", border:"1.5px solid #E8317A", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
              {"\uD83D\uDCC2 Select Whatnot CSV"}<input type="file" accept=".csv" onChange={handleCSV} style={{ display:"none" }}/>
            </label>
          )}
          <Btn onClick={()=>setImporting(p=>!p)} variant="ghost">{importing?"Cancel Import":"\uD83D\uDCC2 Import CSV"}</Btn>
        </div>
        {csvError && <div style={{ padding:"10px 14px", background:"var(--bz-s1)", borderRadius:8, color:"#E8317A", fontSize:13 }}>{csvError}</div>}

        {/* Stream info */}
        <div style={S.card}>
          <SectionLabel t="Stream Info" />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12 }}>
            <div><label style={S.lbl}>Date</label><input type="date" value={form.date} onChange={e=>f("date")(e.target.value)} style={S.inp}/></div>
            <div>
              <label style={S.lbl}>Breaker</label>
              <select value={form.breaker} onChange={e=>f("breaker")(e.target.value)} style={{ ...S.inp, cursor:"pointer", color:form.breaker?"var(--bz-ink)":"var(--bz-ink-2)" }}>
                <option value="">Select...</option>
                {BREAKERS.map(b=><option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label style={S.lbl}>Break Type</label>
              <select value={form.breakType} onChange={e=>f("breakType")(e.target.value)} style={{ ...S.inp, cursor:"pointer" }}>
                <option value="auction">Auction</option>
                <option value="bin">BIN</option>
                <option value="mixed">Mixed</option>
              </select>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              <label style={S.lbl}>BIN Break (flat 35%)?</label>
              <div style={{ display:"flex", alignItems:"center", gap:8, paddingTop:6 }}>
                <input type="checkbox" checked={form.binOnly||false} onChange={e=>f("binOnly")(e.target.checked)} style={{ width:18, height:18 }}/>
                <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Override to flat 35%</span>
              </div>
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:12 }}>
            <div><label style={S.lbl}>Market Multiple (e.g. 1.6)</label><input type="number" step="0.1" value={form.marketMultiple} onChange={e=>f("marketMultiple")(e.target.value)} placeholder="e.g. 1.6" style={S.inp} disabled={form.binOnly}/></div>
            <div><label style={S.lbl}>Notes (optional)</label><input value={form.notes} onChange={e=>f("notes")(e.target.value)} placeholder="e.g. Holiday stream, slow night..." style={S.inp}/></div>
          </div>
        </div>

        {/* Revenue */}
        {isAdmin && <div style={S.card}>
          <SectionLabel t="Revenue & Expenses" />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12 }}>
            {[
              ["grossRevenue",      "Gross Revenue ($)",        "#166534"],
              ["whatnotFees",       "Whatnot Fees ($)",         "#991b1b"],
              ["coupons",           "Coupons ($)",              "#991b1b"],
              ["whatnotPromo",      "Whatnot Promo ($)",        "#991b1b"],
              ["magpros",           "MagPros ($)",              "#991b1b"],
              ["packagingMaterial", "Packaging Materials ($)",  "#991b1b"],
              ["topLoaders",        "Top Loaders ($)",          "#991b1b"],
              ["chaserCards",       "Chaser Cards ($)",         "#991b1b"],
            ].map(([key, label, color]) => (
              <div key={key}>
                <label style={{ ...S.lbl, color: key==="grossRevenue"?"#166534":S.lbl.color }}>{label}</label>
                <input type="number" step="0.01" value={form[key]||""} onChange={e=>f(key)(e.target.value)} placeholder="0.00" style={{ ...S.inp, color }}/>
              </div>
            ))}
          </div>
        </div>}

        {/* Live preview */}
        {(parseFloat(form.grossRevenue)||0) > 0 && (
          <div style={{ ...S.card, border:"2px solid #166534" }}>
            <SectionLabel t="Live Preview" />
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10 }}>
              {[
                { l:"Net Revenue",   v:`$${preview.netRev.toFixed(2)}`,   c:"#1B4F8A" },
                { l:"Bazooka 30%",   v:`$${preview.bazNet.toFixed(2)}`,   c:"#E8317A" },
                { l:"BoBA 70%",      v:`$${preview.bobaNet.toFixed(2)}`,  c:"var(--bz-ink-3)" },
                { l:"Rep Expenses",  v:`$${preview.repExpShare.toFixed(2)}`,   c:"#991b1b" },
                { l:`Commission (${(preview.rate*100).toFixed(0)}%)`, v:`$${preview.commAmt.toFixed(2)}`, c:"#166534" },
              ].map(({l,v,c}) => (
                <div key={l} style={{ textAlign:"center", background:"var(--bz-s1)", borderRadius:8, padding:"10px 8px" }}>
                  <div style={{ fontSize:18, fontWeight:900, color:c }}>{v}</div>
                  <div style={{ fontSize:9, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginTop:3 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display:"flex", gap:10 }}>
          <Btn onClick={handleSave} disabled={!form.date||!form.breaker} variant="green">{"\uD83D\uDCBE Save Stream"}</Btn>
          <Btn onClick={cancelEdit} variant="ghost">Cancel</Btn>
        </div>
      </div>
    );
  }

  // -- LIST VIEW ------------------------------------------------
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* -- PAY STUB GENERATOR -- */}
      {isAdmin && (
        <div style={{ ...S.card, border:"2px solid rgba(232,49,122,0.3)" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: showStub?14:0 }}>
            <div style={{ fontSize:10, fontWeight:800, color:"#E8317A", textTransform:"uppercase", letterSpacing:2.5, display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:14, height:2, background:"#E8317A", borderRadius:1 }}/>
              {"\uD83D\uDCB5 Pay Stub Generator"}</div>
            <button onClick={()=>setShowStub(p=>!p)} style={{ background:"transparent", border:"1.5px solid #E8317A", color:"#E8317A", borderRadius:7, padding:"4px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              {showStub ? "\u25B2 Hide" : "\u25BC Generate"}
            </button>
          </div>
          {showStub && (() => {
            // Build stub data
            const now = new Date();
            function getWeekStart(d) {
              const day = d.getDay();
              const diff = day === 0 ? 6 : day - 1;
              const s = new Date(d); s.setDate(d.getDate()-diff); s.setHours(0,0,0,0);
              return s;
            }
            const weekStart = getWeekStart(now);
            const weekEnd   = new Date(weekStart); weekEnd.setDate(weekStart.getDate()+6); weekEnd.setHours(23,59,59,999);

            function inStubPeriod(dateStr) {
              const parts = dateStr.split("-");
              const d = parts.length === 3
                ? new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]), 12, 0, 0)
                : new Date(dateStr);
              if (stubPeriod === "week") return d >= weekStart && d <= weekEnd;
              if (stubPeriod === "custom" && stubFrom && stubTo) {
                const fp = stubFrom.split("-");
                const tp = stubTo.split("-");
                const f = fp.length===3 ? new Date(parseInt(fp[0]),parseInt(fp[1])-1,parseInt(fp[2]),0,0,0) : new Date(stubFrom);
                const t = tp.length===3 ? new Date(parseInt(tp[0]),parseInt(tp[1])-1,parseInt(tp[2]),23,59,59) : new Date(stubTo);
                t.setHours(23,59,59,999);
                return d >= f && d <= t;
              }
              return false;
            }

            const targetBreaker = stubBreaker || (isAdmin ? BREAKERS[0] : myBreaker);
            const stubStreams = streams.filter(s => inStubPeriod(s.date) && (
              s.breaker === targetBreaker ||
              (s.eventStaff||[]).some(es => es.breaker === targetBreaker) ||
              s.splitRep === targetBreaker
            ));

            const calcS = (s) => {
              const c = calcStream(s, targetBreaker);
              const myStaff = (s.eventStaff||[]).find(es=>es.breaker===targetBreaker);
              const isSplitRep  = s.splitRep === targetBreaker;
              const isEventOnly = !!myStaff && s.breaker !== targetBreaker && !isSplitRep;
              const myEventFee = myStaff ? Math.min(1000, c.bazNet * 0.15) : 0;
              const alsoEventStaff = !!myStaff && !isEventOnly;
              // c.myComm already includes the event fee (calcStream adds it when targetBreaker is in eventStaff)
              return { ...c, isEventOnly, isSplitRep, myEventFee, alsoEventStaff, baseComm: c.myComm - myEventFee };
            };

            // Exclude event-only AND split-rep streams from gross/baz totals — only own streams count
            const totals = stubStreams.reduce((acc,s)=>{ const c=calcS(s); const ownStream=!c.isEventOnly&&!c.isSplitRep&&!c.isSingles; if(c.isSingles){ acc.singlesRev+=c.myComm; } else { acc.comm+=c.myComm; } acc.gross+=ownStream?c.gross:0; acc.baz+=ownStream?c.bazNet:0; acc.tips+=c.tips; acc.salesBonus+=(c.salesBonus||0); acc.repExpShare+=c.repExpShare; acc.trueNet+=ownStream?c.bazTrueNet:0; acc.biguReimb+=(c.biguReimb||0); return acc; }, {gross:0,baz:0,comm:0,singlesRev:0,tips:0,salesBonus:0,repExp:0,trueNet:0,biguReimb:0});
            const periodLabel = stubPeriod==="week"
              ? `${weekStart.toLocaleDateString("en-US",{month:"short",day:"numeric"})} - ${weekEnd.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}`
              : stubFrom && stubTo ? `${stubFrom} - ${stubTo}` : "Select dates";

            function printStub() {
              const adminPDF = stubAdminView;
              const w = window.open("","_blank","width=800,height=900");
              const bc = BC[targetBreaker]||{text:"#E8317A"};
              const streamRows = stubStreams.map(s => {
                const c = calcS(s);
                const typeLabel = c.isEventOnly ? `🎪 Event Fee (${s.breaker}'s stream)`
                                : s.isSinglesShow ? `🃏 Singles Show`
                                : c.isSplitRep  ? `✂️ Split (${s.breaker}'s stream)`
                                : `${s.breakType||"Auction"}${s.binOnly?" (BIN)":""}${c.alsoEventStaff?" + 🎪 Event Fee":""}`;
                return adminPDF ? `
                  <tr style="border-bottom:1px solid #eee;">
                    <td style="padding:10px 12px;font-size:13px;">${new Date(s.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}</td>
                    <td style="padding:10px 12px;font-size:13px;">${typeLabel}</td>
                    <td style="padding:10px 12px;font-size:13px;text-align:right;">${c.isEventOnly||c.isSplitRep?"—":fmt(c.gross)}</td>
                    <td style="padding:10px 12px;font-size:13px;text-align:right;">${c.isEventOnly||c.isSplitRep?"—":fmt(c.bazNet)}</td>
                    <td style="padding:10px 12px;font-size:13px;text-align:right;color:#991b1b;">${c.isEventOnly||c.isSplitRep?"—":fmt(c.repExpShare)}</td>
                    <td style="padding:10px 12px;font-size:13px;text-align:right;">${c.isEventOnly?"🎪 Event":c.isSplitRep?"✂️ Split":(c.rate*100).toFixed(0)+"%"}</td>
                    <td style="padding:10px 12px;font-size:13px;text-align:right;color:#991b1b;">${c.isEventOnly||c.isSplitRep?"—":"-"+fmt(c.commAmt)}</td>
                    <td style="padding:10px 12px;font-size:13px;text-align:right;font-weight:700;color:#166534;">${c.isEventOnly||c.isSplitRep?"—":fmt(c.bazTrueNet)}</td>
                  </tr>` : `
                  <tr style="border-bottom:1px solid #eee;">
                    <td style="padding:10px 12px;font-size:13px;">${new Date(s.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}</td>
                    <td style="padding:10px 12px;font-size:13px;">${typeLabel}</td>
                    <td style="padding:10px 12px;font-size:13px;text-align:right;">${c.isEventOnly?"—":fmt(c.gross)}</td>
                    <td style="padding:10px 12px;font-size:13px;text-align:right;">${c.isEventOnly?"—":fmt(c.bazNet)}</td>
                    <td style="padding:10px 12px;font-size:13px;text-align:right;">${c.isEventOnly?"🎪 Event":c.isSplitRep?"✂️ Split":(c.rate*100).toFixed(0)+"%"}</td>
                    <td style="padding:10px 12px;font-size:13px;text-align:right;font-weight:700;color:#166534;">${fmt(c.myComm)}</td>
                    ${c.tips>0?`<td style="padding:10px 12px;font-size:13px;text-align:right;color:#d97706;">+${fmt(c.tips)} tips</td>`:"<td></td>"}
                  </tr>`;
              }).join("");

              w.document.write(`<!DOCTYPE html><html><head><title>Bazooka Pay Stub -- ${targetBreaker}</title>
                <style>
                  * { box-sizing:border-box; margin:0; padding:0; font-family:'Trebuchet MS',sans-serif; }
                  body { background:#fff; color:#111; padding:40px; }
                  @media print { body { padding:20px; } .no-print { display:none; } }
                  .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:32px; padding-bottom:20px; border-bottom:3px solid #E8317A; }
                  .logo { font-size:28px; font-weight:900; letter-spacing:3px; color:#E8317A; }
                  .sub { font-size:11px; color:#888; text-transform:uppercase; letter-spacing:2px; margin-top:4px; }
                  .meta { text-align:right; }
                  .meta div { font-size:13px; color:#444; margin-bottom:4px; }
                  .meta strong { color:#111; }
                  .breaker-badge { display:inline-block; background:#f0f0f0; border-radius:20px; padding:4px 16px; font-size:14px; font-weight:700; color:#333; margin-bottom:20px; }
                  table { width:100%; border-collapse:collapse; margin-bottom:24px; }
                  thead tr { background:#111; color:#E8317A; }
                  thead th { padding:10px 12px; font-size:10px; text-transform:uppercase; letter-spacing:1px; text-align:left; }
                  thead th:nth-child(n+3) { text-align:right; }
                  tbody tr:nth-child(even) { background:#fafafa; }
                  .totals { background:#f8f8f8; border:2px solid #E8317A22; border-radius:12px; padding:20px 24px; margin-bottom:24px; }
                  .totals-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:16px; }
                  .tot-item { text-align:center; }
                  .tot-val { font-size:22px; font-weight:900; }
                  .tot-lbl { font-size:10px; text-transform:uppercase; letter-spacing:1px; color:#888; margin-top:4px; }
                  .payout { background:#111; color:#fff; border-radius:12px; padding:20px 28px; display:flex; justify-content:space-between; align-items:center; margin-bottom:24px; }
                  .payout-label { font-size:16px; font-weight:700; color:#4ade80; }
                  .payout-amt { font-size:32px; font-weight:900; color:#4ade80; }
                  .footer { text-align:center; font-size:11px; color:#aaa; border-top:1px solid #eee; padding-top:16px; }
                  .print-btn { background:#E8317A; color:#fff; border:none; border-radius:8px; padding:10px 24px; font-size:14px; font-weight:700; cursor:pointer; margin-bottom:24px; margin-right:8px; }
                  .close-btn { background:#333; color:#fff; border:none; border-radius:8px; padding:10px 24px; font-size:14px; font-weight:700; cursor:pointer; margin-bottom:24px; }
                </style></head><body>
                <button class="no-print print-btn" onclick="window.print()">🖨 Print / Save as PDF</button>
                <button class="no-print close-btn" onclick="window.close(); if(!window.closed){ history.length>1 ? history.back() : (document.body.innerHTML='<p style=\\'font-family:sans-serif;padding:40px;text-align:center;color:#888\\'>You can close this tab now.</p>'); }">← Close</button>
                <div class="header">
                  <div>
                    <div class="logo">BAZOOKA</div>
                    <div class="sub">Commission Pay Stub</div>
                  </div>
                  <div class="meta">
                    <div>Pay Period: <strong>${periodLabel}</strong></div>
                    <div>Generated: <strong>${now.toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</strong></div>
                    <div>Streams: <strong>${stubStreams.length}</strong></div>
                  </div>
                </div>
                <div class="breaker-badge">🎯 ${targetBreaker}</div>
                ${stubStreams.length === 0 ? '<p style="color:#888;text-align:center;padding:40px 0;">No streams found for this period.</p>' : `
                <table>
                  <thead><tr>
                    ${adminPDF
                      ? `<th>Date</th><th>Type</th><th style="text-align:right">Gross</th><th style="text-align:right">Bazooka Net</th><th style="text-align:right">Rep Exp</th><th style="text-align:right">Rate</th><th style="text-align:right">− Commission</th><th style="text-align:right">True Net</th>`
                      : `<th>Date</th><th>Type</th><th style="text-align:right">Gross</th><th style="text-align:right">Bazooka Net</th><th style="text-align:right">Rate</th><th style="text-align:right">Commission</th>`
                    }
                  </tr></thead>
                  <tbody>${streamRows}</tbody>
                </table>
                <div class="totals">
                  <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#888;margin-bottom:14px;">Period Summary</div>
                  <div class="totals-grid" style="grid-template-columns:${adminPDF?"repeat(4,1fr)":"repeat(3,1fr)"}">
                    <div class="tot-item"><div class="tot-val" style="color:#E8317A;">${fmt(totals.gross)}</div><div class="tot-lbl">Total Gross</div></div>
                    <div class="tot-item"><div class="tot-val" style="color:#A78BFA;">${fmt(totals.trueNet)}</div><div class="tot-lbl">Bazooka True Net</div></div>
                    ${adminPDF ? `
                    <div class="tot-item"><div class="tot-val" style="color:#991b1b;">-${fmt(totals.comm)}</div><div class="tot-lbl">Commission Paid</div></div>
                    <div class="tot-item"><div class="tot-val" style="color:#166534;">${fmt(totals.trueNet)}</div><div class="tot-lbl">Bazooka True Net</div></div>
                    ` : `
                    <div class="tot-item"><div class="tot-val" style="color:#166534;">${fmt(totals.comm)}</div><div class="tot-lbl">Total Commission</div></div>
                    ${totals.tips>0?`<div class="tot-item"><div class="tot-val" style="color:#d97706;">${fmt(totals.tips)}</div><div class="tot-lbl">Tips (100% yours)</div></div>`:""}
                    ${totals.salesBonus>0?`<div class="tot-item"><div class="tot-val" style="color:#A78BFA;">${fmt(totals.salesBonus)}</div><div class="tot-lbl">Sales Bonus</div></div>`:""}
                    `}
                  </div>
                </div>
                <div class="payout">
                  <div class="payout-label">💵 Total Earned This Period</div>
                  <div class="payout-amt">${fmt(totals.comm+totals.singlesRev+totals.tips+totals.salesBonus)}</div>
                  ${(totals.singlesRev>0||totals.tips>0||totals.salesBonus>0)?`<div style="font-size:13px;color:#4ade80;margin-top:4px;">${fmt(totals.comm)} break commission${totals.singlesRev>0?" + "+fmt(totals.singlesRev)+" singles (pass-through)":""}${totals.tips>0?" + "+fmt(totals.tips)+" tips":""}${totals.salesBonus>0?" + "+fmt(totals.salesBonus)+" bonus":""}</div>`:""}
                </div>`}
                <div class="footer">Bazooka Breaks, LLC &nbsp;·&nbsp; This document is confidential and intended for the named recipient only.</div>
              </body></html>`);
              w.document.close();
            }

            return (
              <div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr auto", gap:12, marginBottom:16, alignItems:"end" }}>
                  <div>
                    <label style={S.lbl}>Breaker</label>
                    <select value={stubBreaker||targetBreaker} onChange={e=>setStubBreaker(e.target.value)} style={{ ...S.inp, cursor:"pointer" }}>
                      {BREAKERS.map(b=><option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={S.lbl}>Period</label>
                    <select value={stubPeriod} onChange={e=>setStubPeriod(e.target.value)} style={{ ...S.inp, cursor:"pointer" }}>
                      <option value="week">This Week (Mon-Sun)</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>
                  {stubPeriod==="custom" && <>
                    <div>
                      <label style={S.lbl}>From</label>
                      <input type="date" value={stubFrom} onChange={e=>setStubFrom(e.target.value)} style={S.inp}/>
                    </div>
                    <div>
                      <label style={S.lbl}>To</label>
                      <input type="date" value={stubTo} onChange={e=>setStubTo(e.target.value)} style={S.inp}/>
                    </div>
                  </>}
                  <Btn onClick={()=>{ printStub(); }} variant="ghost">{"\uD83D\uDC41 Preview PDF"}</Btn>
                  <Btn onClick={async()=>{
                    printStub();
                    try {
                      if (onSavePayStub) await onSavePayStub({
                        breaker: targetBreaker,
                        period: periodLabel,
                        periodType: stubPeriod,
                        streamCount: stubStreams.length,
                        totalGross: totals.gross,
                        totalBaz: totals.baz,
                        totalComm: totals.comm,
                        totalTips: totals.tips,
                        streams: stubStreams.map(s=>{ const c=calcS(s); return { date:s.date, breakType:c.isEventOnly?`🎪 Event Fee (${s.breaker}'s stream)`:s.isSinglesShow?"🃏 Singles Show":s.breakType||"Auction", binOnly:s.binOnly, gross:c.isEventOnly?0:c.gross, bazNet:c.isEventOnly?0:c.bazNet, repExp:c.repExpShare, rate:c.isEventOnly?-1:c.rate, commAmt:c.myComm, tips:c.tips, isEventOnly:c.isEventOnly }; }),
                      });
                    } catch(e) { console.error("Pay stub save failed:", e); alert("Failed to send stub: " + e.message); }
                  }} variant="green" disabled={stubStreams.length===0}>{"\uD83D\uDCE4 Send to"}{targetBreaker}</Btn>
                  <div style={{ display:"flex", alignItems:"center", gap:8, background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:8, padding:"5px 12px" }}>
                    <span style={{ fontSize:11, color:"#999" }}>PDF View:</span>
                    <button onClick={()=>setStubAdminView(false)} style={{ background:!stubAdminView?"#E8317A":"transparent", color:!stubAdminView?"#fff":"#888", border:"none", borderRadius:5, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Rep</button>
                    <button onClick={()=>setStubAdminView(true)} style={{ background:stubAdminView?"#1A1A2E":"transparent", color:stubAdminView?"#E8317A":"#888", border:"none", borderRadius:5, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Admin</button>
                  </div>
                </div>
                {/* Preview */}
                <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"16px 20px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                    <div>
                      <span style={{ fontWeight:800, color:"#E8317A", fontSize:14 }}>{targetBreaker}</span>
                      <span style={{ color:"#999", fontSize:12, marginLeft:10 }}>{periodLabel}</span>
                    </div>
                    <span style={{ fontSize:12, color:"#999" }}>{stubStreams.length} stream{stubStreams.length!==1?"s":""}</span>
                  </div>
                  {stubStreams.length === 0
                    ? <div style={{ color:"var(--bz-ink-3)", fontSize:12, padding:"12px 0" }}>No streams found for this period.</div>
                    : <>
                        <div style={{ display:"grid", gridTemplateColumns:`repeat(${isAdmin?5:3},1fr)`, gap:10, marginBottom:12 }}>
                          {[
                            { l:"Gross Revenue",        v:fmt(totals.gross),   c:"#E8317A" },
                            { l:"Bazooka True Net",     v:fmt(totals.trueNet), c:"#A78BFA" },
                            ...(isAdmin ? [
                              { l:"− Commission",        v:fmt(totals.comm),    c:"#991b1b" },
                              { l:"Bazooka True Net",    v:fmt(totals.trueNet), c:"#4ade80" },
                              ...(totals.biguReimb>0?[{ l:"🔄 Expense Reimb", v:"+"+fmt(totals.biguReimb), c:"#FBBF24" }]:[]),
                            ] : [
                              { l:"💵 Break Commission",  v:fmt(totals.comm),    c:"#4ade80" },
                              ...((totals.singlesRev||0)>0?[{ l:"🃏 Singles (pass-through)", v:fmt(totals.singlesRev), c:"#FBBF24" }]:[]),
                              ...(totals.tips>0?[{ l:"Tips (100% yours)", v:fmt(totals.tips), c:"#FBBF24" }]:[]),
                              ...(totals.salesBonus>0?[{ l:"🎁 Sales Bonus", v:"+"+fmt(totals.salesBonus), c:"#A78BFA" }]:[]),
                              { l:"Total Earned", v:fmt(totals.comm+(totals.singlesRev||0)+totals.tips+totals.salesBonus), c:"#4ade80" },
                              ...(totals.biguReimb>0?[{ l:"🔄 Expense Reimbursement", v:"+"+fmt(totals.biguReimb), c:"#FBBF24" }]:[]),
                            ]),
                          ].map(({l,v,c})=>(
                            <div key={l} style={{ textAlign:"center", background:"var(--bz-s1)", borderRadius:8, padding:"10px" }}>
                              <div style={{ fontSize:18, fontWeight:900, color:c }}>{v}</div>
                              <div style={{ fontSize:9, color:"#999", textTransform:"uppercase", letterSpacing:1, marginTop:3 }}>{l}</div>
                            </div>
                          ))}
                        </div>
                        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                          <thead><tr>
                            {["Date","Type","Gross","Baz Net",...(isAdmin?["Rep Exp","IMC Reimb","True Net"]:["Rate","Commission"]),...(totals.biguReimb>0?["Reimb"]:[])] .map(h=><th key={h} style={{ ...S.th, fontSize:9, padding:"6px 10px" }}>{h}</th>)}
                          </tr></thead>
                          <tbody>
                            {stubStreams.map((s,i)=>{
                              const c=calcS(s);
                              return <tr key={s.id} style={{ background:i%2===0?"var(--bz-s1)":"var(--bz-s1)" }}>
                                <td style={{ ...S.td, padding:"6px 10px" }}>{new Date(s.date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</td>
                                <td style={{ ...S.td, padding:"6px 10px", color:"var(--bz-ink-2)" }}>{s.breakType||"Auction"}{s.binOnly?" BIN":""}</td>
                                <td style={{ ...S.td, padding:"6px 10px", color:"#E8317A", fontWeight:700 }}>{fmt(c.gross)}</td>
                                <td style={{ ...S.td, padding:"6px 10px", color:"#1B4F8A", fontWeight:700 }}>{fmt(c.bazNet)}</td>
                                {isAdmin ? <>
                                  <td style={{ ...S.td, padding:"6px 10px", color:"#991b1b" }}>{fmt(c.repExpShare)}</td>
                                  <td style={{ ...S.td, padding:"6px 10px", color:"#166534" }}>+{fmt(c.bazExpShare)}</td>
                                  <td style={{ ...S.td, padding:"6px 10px", color:"#4ade80", fontWeight:900 }}>{fmt(c.bazTrueNet)}</td>
                                </> : <>
                                  <td style={{ ...S.td, padding:"6px 10px", color:"var(--bz-ink-2)" }}>{(c.rate*100).toFixed(0)}%</td>
                                  <td style={{ ...S.td, padding:"6px 10px", color:"#4ade80", fontWeight:900 }}>{fmt(c.commAmt)}</td>
                                </>}
                              </tr>;
                            })}
                          </tbody>
                        </table>
                      </>
                  }
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* -- REMOTE BREAKER EXPENSE REIMBURSEMENT (one card per remote breaker) -- */}
      {isAdmin && BREAKERS.filter(b=>isRemoteBreaker(b)).map(remoteBreaker => {
        const biguStreams = filteredStreams.filter(s =>
          (s.breaker||"").toLowerCase().replace(/\s+/g,"") === remoteBreaker.toLowerCase().replace(/\s+/g,"") &&
          ((reimbursesMagpros(remoteBreaker) ? (parseFloat(s.magpros)||0) : 0)+(parseFloat(s.packagingMaterial)||0)+(parseFloat(s.biguGiveawayCards)||0)+(parseFloat(s.biguInsuranceCards)||0)+(parseFloat(s.biguShipping)||0)) > 0
        );
        if (!biguStreams.length) return null;
        // Top loaders are supplied by Bazooka, so they are never reimbursed. Stephen's magpros
        // are supplied too — reimbursesMagpros() is the single source of truth for that rule.
        const totalReimb = biguStreams.reduce((sum,s)=>sum+(reimbursesMagpros(remoteBreaker) ? (parseFloat(s.magpros)||0) : 0)+(reimbursesPackaging(remoteBreaker) ? (parseFloat(s.packagingMaterial)||0) : 0)+(parseFloat(s.biguGiveawayCards)||0)+(parseFloat(s.biguInsuranceCards)||0)+((parseFloat(s.biguShipping)||0)*0.5), 0);
        return (
          <div key={remoteBreaker} style={{ ...S.card, borderLeft:"3px solid #FBBF24" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div style={{ fontSize:14, fontWeight:800, color:"#FBBF24" }}>🔄 {remoteBreaker} Expense Reimbursement</div>
              <div style={{ fontSize:24, fontWeight:900, color:"#FBBF24" }}>${totalReimb.toFixed(2)}</div>
            </div>
            <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginBottom:12 }}>Owed to {remoteBreaker} at end of month — he pays these upfront, we reimburse 100%</div>
            {isMobileC ? (
              /* Mobile: stacked cards, one per stream */
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {biguStreams.map(s=>{
                  // Magpros only count for breakers we don't supply (i.e. not Stephen).
                  const mags=reimbursesMagpros(remoteBreaker) ? (parseFloat(s.magpros)||0) : 0;
                  const pack=reimbursesPackaging(remoteBreaker) ? (parseFloat(s.packagingMaterial)||0) : 0;
                  const giveaway=parseFloat(s.biguGiveawayCards)||0, insurance=parseFloat(s.biguInsuranceCards)||0;
                  const ship=(parseFloat(s.biguShipping)||0)*0.5;
                  const tot=mags+pack+giveaway+insurance+ship;
                  const line=(lbl,v)=> v>0 ? (
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12 }}>
                      <span style={{ color:"#a0a0a0" }}>{lbl}</span><span style={{ color:"var(--bz-ink)" }}>${v.toFixed(2)}</span>
                    </div>
                  ) : null;
                  return (
                    <div key={s.id} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(251,191,36,0.15)", borderRadius:10, padding:"10px 12px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)" }}>{s.streamName||s.breakType||"Break"}</div>
                          <div style={{ fontSize:10, color:"#999" }}>{new Date(s.date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</div>
                        </div>
                        <div style={{ fontSize:16, fontWeight:900, color:"#FBBF24" }}>${tot.toFixed(2)}</div>
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
                        {line("MagPros",mags)}
                        {line("Packaging",pack)}
                        {line("Giveaway Cards",giveaway)}
                        {line("Insurance Cards",insurance)}
                        {line("Shipping (half)",ship)}
                      </div>
                    </div>
                  );
                })}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", borderTop:"1px solid rgba(251,191,36,0.3)", paddingTop:10, marginTop:2 }}>
                  <span style={{ fontSize:12, fontWeight:800, color:"var(--bz-ink-2)" }}>TOTAL OWED TO {remoteBreaker.toUpperCase()}</span>
                  <span style={{ fontSize:18, fontWeight:900, color:"#FBBF24" }}>${totalReimb.toFixed(2)}</span>
                </div>
              </div>
            ) : (
            <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", fontSize:11, borderCollapse:"collapse" }}>
              <thead><tr style={{ borderBottom:"1px solid rgba(251,191,36,0.2)" }}>
                {["Date","Stream","MagPros","Packaging","Giveaway Cards","Insurance Cards","Shipping ½","Total"].map(h=>(
                  <th key={h} style={{ padding:"4px 8px", textAlign:"left", color:"var(--bz-ink-3)", fontWeight:700, fontSize:10, textTransform:"uppercase", letterSpacing:1 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {biguStreams.map(s=>{
                  // Top loaders are Bazooka-supplied and never reimbursed, so the column is gone.
                  // Magpros are Bazooka-supplied for Stephen, so they only count for BigU and Vinny.
                  const mags=reimbursesMagpros(remoteBreaker) ? (parseFloat(s.magpros)||0) : 0;
                  const pack=reimbursesPackaging(remoteBreaker) ? (parseFloat(s.packagingMaterial)||0) : 0;
                  const giveaway=parseFloat(s.biguGiveawayCards)||0, insurance=parseFloat(s.biguInsuranceCards)||0;
                  const ship=(parseFloat(s.biguShipping)||0)*0.5;
                  const tot=mags+pack+giveaway+insurance+ship;
                  return (
                    <tr key={s.id} style={{ borderBottom:"1px solid var(--bz-line)" }}>
                      <td style={{ padding:"6px 8px", color:"var(--bz-ink-2)" }}>{new Date(s.date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</td>
                      <td style={{ padding:"6px 8px", color:"var(--bz-ink-2)" }}>{s.streamName||s.breakType||"Break"}</td>
                      <td style={{ padding:"6px 8px", color:mags>0?"var(--bz-ink)":"#333" }}>{mags>0?"$"+mags.toFixed(2):"—"}</td>
                      <td style={{ padding:"6px 8px", color:pack>0?"var(--bz-ink)":"#333" }}>{pack>0?"$"+pack.toFixed(2):"—"}</td>
                      <td style={{ padding:"6px 8px", color:giveaway>0?"var(--bz-ink)":"#333" }}>{giveaway>0?"$"+giveaway.toFixed(2):"—"}</td>
                      <td style={{ padding:"6px 8px", color:insurance>0?"var(--bz-ink)":"#333" }}>{insurance>0?"$"+insurance.toFixed(2):"—"}</td>
                      <td style={{ padding:"6px 8px", color:ship>0?"var(--bz-ink)":"#333" }}>{ship>0?"$"+ship.toFixed(2):"—"}</td>
                      <td style={{ padding:"6px 8px", color:"#FBBF24", fontWeight:700 }}>${tot.toFixed(2)}</td>
                    </tr>
                  );
                })}
                <tr style={{ borderTop:"1px solid rgba(251,191,36,0.3)" }}>
                  <td colSpan={5} style={{ padding:"8px 8px", color:"var(--bz-ink-2)", fontWeight:700 }}>TOTAL OWED TO {remoteBreaker.toUpperCase()}</td>
                  <td colSpan={3} style={{ padding:"8px 8px", color:"#FBBF24", fontWeight:900, fontSize:16, textAlign:"right" }}>${totalReimb.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
            </div>
            )}
          </div>
        );
      })}

      {/* -- PAY STUB HISTORY -- */}
      {isAdmin && payStubs.length > 0 && (
        <div style={{ ...S.card, border:"1px solid var(--bz-line-2)" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: showStubHist ? 14 : 0 }}>
            <div style={{ fontSize:10, fontWeight:800, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:2.5, display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:14, height:2, background:"var(--bz-ink-2)", borderRadius:1 }}/>
              {"\uD83D\uDCCB Statement History ("}{payStubs.length})
            </div>
            <button onClick={()=>setShowStubHist(p=>!p)} style={{ background:"transparent", border:"1.5px solid #333", color:"var(--bz-ink-2)", borderRadius:7, padding:"4px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              {showStubHist?"\u25B2 Hide":"\u25BC Show"}
            </button>
          </div>
          {showStubHist && (
            <div>
              {/* Filter by breaker */}
              <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
                <span style={{ fontSize:11, color:"#999" }}>Filter:</span>
                <button onClick={()=>setStubHistFilter("")} style={{ background:!stubHistFilter?"#1A1A2E":"transparent", color:!stubHistFilter?"#E8317A":"#888", border:`1.5px solid ${!stubHistFilter?"#E8317A":"var(--bz-line-2)"}`, borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>All</button>
                {BREAKERS.map(b=>(
                  <button key={b} onClick={()=>setStubHistFilter(b)} style={{ background:stubHistFilter===b?"#1A1A2E":"transparent", color:stubHistFilter===b?"#E8317A":"#888", border:`1.5px solid ${stubHistFilter===b?"#E8317A":"var(--bz-line-2)"}`, borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{b}</button>
                ))}
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {payStubs
                  .filter(s => !stubHistFilter || s.breaker === stubHistFilter)
                  .map(stub => {
                    return <StubRow key={stub.id} stub={stub} S={S} onDeletePayStub={onDeletePayStub}/>;
                  })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Period filter */}
      <div style={{ ...S.card, padding:"12px 16px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <span style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, marginRight:4 }}>Period:</span>
          {[["month","This Month"],["quarter","This Quarter"],["year","This Year"],["all","All Time"],["custom","Custom"]].map(([val,label]) => (
            <button key={val} onClick={()=>{ setPeriod(val); setViewStream(null); }}
              style={{ background:period===val?"#1A1A2E":"transparent", color:period===val?"#E8317A":"var(--bz-ink-2)", border:`1.5px solid ${period===val?"#E8317A":"var(--bz-line)"}`, borderRadius:7, padding:"5px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              {label}
            </button>
          ))}
          {period==="custom" && (
            <>
              <input type="date" value={customFrom} onChange={e=>setCustomFrom(e.target.value)} style={{ ...S.inp, width:140, fontSize:11, padding:"4px 8px" }}/>
              <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>to</span>
              <input type="date" value={customTo} onChange={e=>setCustomTo(e.target.value)} style={{ ...S.inp, width:140, fontSize:11, padding:"4px 8px" }}/>
            </>
          )}
          <span style={{ marginLeft:"auto", fontSize:11, color:"var(--bz-ink-2)" }}>{filteredStreams.length} stream{filteredStreams.length!==1?"s":""}</span>
        </div>
      </div>

      {/* Summary */}
      {(() => {
        const avgMM = totals.mmCount > 0 ? (totals.mmSum / totals.mmCount) : 0;
        const mmColor = avgMM>=1.7?"#4ade80":avgMM>=1.5?"#86efac":avgMM>=1.4?"#FBBF24":"#E8317A";
        const hasSingles = (totals.singlesRev||0) > 0;
        const cols = (isAdmin?6:4) + (hasSingles?1:0);
        return (
          <div style={{ display:"grid", gridTemplateColumns:isMobileC?"repeat(2,1fr)":`repeat(${cols},1fr)`, gap:12 }}>
            {[
              { l:"Total Streams",     v:filteredStreams.length,                                    c:"var(--bz-ink)" },
              { l:"💵 Break Commission",  v:fmt(totals.comm),                                       c:"#4ade80" },
              ...(hasSingles ? [{ l:"🃏 Singles Revenue (pass-through)", v:fmt(totals.singlesRev), c:"#FBBF24" }] : []),
              { l:"\uD83C\uDF31 New Buyers",     v:totals.newBuyers,                               c:"#166534" },
              { l:"Avg Market Multiple", v:avgMM>0?`${avgMM.toFixed(2)}x`:"--",                    c:mmColor   },
              ...(isAdmin ? [
                { l:"Total Gross",       v:fmt(totals.gross),   c:"#E8317A" },
                { l:"Bazooka True Net",  v:fmt(totals.trueNet), c:"#6B2D8B" },
              ] : []),
            ].map(({l,v,c}) => (
              <div key={l} className="stat-card bz-fin-card" style={{ ...S.card, textAlign:"center", transition:"border-color .18s, transform .18s, box-shadow .18s" }}>
                <div style={{ fontSize:isMobileC?20:27, fontWeight:800, color:c, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", letterSpacing:"-0.02em", fontFamily:"'Arial Narrow',sans-serif" }}>{v}</div>
                <div style={{ fontSize:10, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginTop:5 }}>{l}</div>
              </div>
            ))}
          </div>
        );
      })()}



      {/* Breaker filter -- admin only */}
      {isAdmin && (() => {
        // Anyone with streams in the data, even if they're no longer on the active roster. A rep
        // who leaves still has commission owed on the streams they ran — driving this list purely
        // off BREAKERS made those people (and their money) invisible the moment they were removed.
        const seen = [...new Set(visibleStreams.map(s=>s.breaker).filter(Boolean))];
        const former = seen.filter(b => !BREAKERS.includes(b)).sort();
        const chips = ["all", ...BREAKERS, ...former];
        return (
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {chips.map(b => {
            const isFormer = former.includes(b);
            return (
            <button key={b} onClick={()=>{ setBreakerFilter(b); setViewStream(null); setEditing(null); }}
              title={isFormer ? "No longer an active rep — shown because they still have streams (and commission) on record" : undefined}
              style={{ background:breakerFilter===b?"var(--bz-pink-dim)":"var(--bz-s1)", color:breakerFilter===b?"var(--bz-pink-hot)":(isFormer?"var(--bz-ink-3)":"var(--bz-ink-2)"), border:`1px solid ${breakerFilter===b?"var(--bz-pink-line)":(isFormer?"var(--bz-line-2)":"var(--bz-line)")}`, borderRadius:999, padding:"6px 14px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", transition:"all .15s", opacity:isFormer&&breakerFilter!==b?0.75:1 }}>
              {b === "all" ? "\uD83D\uDC65 All Breakers" : b}
              {isFormer && <span style={{ marginLeft:5, fontSize:9, opacity:0.7 }}>(past)</span>}
              {b !== "all" && <span style={{ marginLeft:6, background:"var(--bz-s3)", color:"var(--bz-pink)", borderRadius:10, padding:"0 6px", fontSize:10 }}>
                {visibleStreams.filter(s=>s.breaker===b).length}
              </span>}
            </button>
            );
          })}
        </div>
        );
      })()}

      {/* Stream list */}
      {filteredStreams.length === 0
        ? <div style={{ ...S.card, textAlign:"center", padding:"60px" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>{"\uD83D\uDCB5"}</div>
            <div style={{ color:"var(--bz-ink-2)" }}>{visibleStreams.length === 0 ? "No streams logged yet. Stream recaps are entered in the Break Log tab." : `No streams for ${breakerFilter} yet.`}</div>
          </div>
        : filteredStreams.map(s => {
            const targetBreaker = !isAdmin ? myBreaker : (breakerFilter !== "all" ? breakerFilter : null);
            const c  = targetBreaker ? calcStream(s, targetBreaker) : calcStreamDash(s);
            const bc = BC[s.breaker] || { bg:"#EEF0FB", text:"#2C3E7A", border:"#3730a3" };
            const myStaff = targetBreaker ? (s.eventStaff||[]).find(es => es.breaker === targetBreaker) : null;
            const isEventOnly = !!myStaff && s.breaker !== targetBreaker;
            const isSplitRep = targetBreaker && s.splitRep === targetBreaker;
            const myEventFee = myStaff ? Math.min(1000, c.bazNet * 0.15) : 0;
            const myRepNet = c.myComm; // already includes event fee when targetBreaker set
            const alsoEventStaff = !!myStaff && !isEventOnly; // earned commission/split AND an event fee on top

            // Color-code performance by MM
            const mm = parseFloat(s.marketMultiple)||0;
            const mmColor = mm>=1.5?"#4ade80":mm>=1.3?"#FBBF24":mm>0?"#E8317A":"#555";
            const mmBg    = mm>=1.5?"rgba(74,222,128,0.08)":mm>=1.3?"rgba(251,191,36,0.08)":mm>0?"rgba(232,49,122,0.06)":"transparent";
            const accentColor = isEventOnly?"#A78BFA":isSplitRep?"#FBBF24":"#4ade80";

            return (
              <div key={s.id} onClick={()=>setViewStream(s.id)} className="card-hover"
                style={{ background:"var(--bz-s1)", borderRadius:12, border:`1px solid ${isEventOnly?"rgba(167,139,250,0.25)":isSplitRep?"rgba(251,191,36,0.25)":"var(--bz-s2)"}`, cursor:"pointer", overflow:"hidden" }}>

                {/* Top accent bar — color = performance */}
                {mm > 0 && !isEventOnly && <div style={{ height:3, background:mmColor, opacity:0.7 }}/>}

                <div style={{ padding:"12px 14px" }}>
                  {/* Header row: date + breaker + channel + badges + arrow */}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:7, flexWrap:"wrap" }}>
                      <span style={{ fontWeight:700, fontSize:13, color:"var(--bz-ink)" }}>
                        {new Date(s.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}
                      </span>
                      <span style={{ background:bc.bg, color:bc.text, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{s.breaker}</span>
                      {s.channel && s.channel !== "Bazooka Vault" && <span style={{ fontSize:10, color:"#7B9CFF", background:"rgba(123,156,255,0.1)", borderRadius:4, padding:"1px 6px", fontWeight:700 }}>📺 {s.channel}</span>}
                      {s.binOnly && <span style={{ fontSize:10, color:"var(--bz-ink-2)", background:"var(--bz-s2)", borderRadius:4, padding:"1px 6px" }}>BIN</span>}
                      {s.isSinglesShow && <span style={{ fontSize:10, color:"#FBBF24", background:"rgba(251,191,36,0.1)", border:"1px solid rgba(251,191,36,0.25)", borderRadius:4, padding:"1px 6px", fontWeight:700 }}>🃏 Singles</span>}
                      {isEventOnly && <span style={{ fontSize:10, color:"#A78BFA", background:"rgba(167,139,250,0.1)", border:"1px solid rgba(167,139,250,0.25)", borderRadius:4, padding:"1px 6px", fontWeight:700 }}>🎪 Event Fee</span>}
                      {isSplitRep && <span style={{ fontSize:10, color:"#FBBF24", background:"rgba(251,191,36,0.1)", border:"1px solid rgba(251,191,36,0.25)", borderRadius:4, padding:"1px 6px", fontWeight:700 }}>✂️ Split</span>}
                      {s.newBuyers>0 && <span style={{ fontSize:10, color:"#4ade80", background:"rgba(74,222,128,0.08)", borderRadius:4, padding:"1px 6px", fontWeight:700 }}>🌱 {s.newBuyers} new</span>}
                    </div>
                    <span style={{ color:"var(--bz-ink-3)", fontSize:16, flexShrink:0 }}>›</span>
                  </div>

                  {/* Main numbers grid */}
                  <div style={{ display:"grid", gridTemplateColumns: isAdmin ? "1.4fr 1fr 1fr 1fr" : "1.4fr 1fr 1fr", gap:8 }}>

                    {/* Gross — largest, most prominent */}
                    <div style={{ background:"var(--bz-bg)", borderRadius:8, padding:"10px 12px" }}>
                      <div style={{ fontSize:18, fontWeight:900, color:"var(--bz-ink)", letterSpacing:"-0.5px" }}>{fmt(c.gross)}</div>
                      <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:"0.8px", marginTop:3 }}>Gross</div>
                    </div>

                    {/* Rate + MM — color coded */}
                    <div style={{ background:mm>0?mmBg:"var(--bz-s1)", borderRadius:8, padding:"10px 12px", border:`1px solid ${mm>0?mmColor+"22":"transparent"}` }}>
                      <div style={{ fontSize:15, fontWeight:800, color:mm>0?mmColor:"#888" }}>
                        {isEventOnly ? "15% evt" : `${(c.rate*100).toFixed(0)}%`}
                      </div>
                      {mm > 0 && !s.binOnly && !isEventOnly && (
                        <div style={{ fontSize:10, color:mmColor, fontWeight:700, marginTop:1 }}>{mm.toFixed(2)}x MM</div>
                      )}
                      <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:"0.8px", marginTop:2 }}>Rate</div>
                    </div>

                    {/* Rep payout — always highlighted */}
                    <div style={{ background: isEventOnly?"rgba(167,139,250,0.08)":isSplitRep?"rgba(251,191,36,0.08)":"rgba(74,222,128,0.06)", borderRadius:8, padding:"10px 12px", border:`1px solid ${accentColor}22` }}>
                      <div style={{ fontSize:15, fontWeight:900, color:accentColor }}>{fmt(myRepNet)}</div>
                      <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:"0.8px", marginTop:3 }}>
                        {isEventOnly?"Event Fee":isSplitRep?"Split":"Rep Net"}{alsoEventStaff?" + 🎪 Event Fee":""}
                      </div>
                      {alsoEventStaff && (
                        <div style={{ fontSize:9, color:"#A78BFA", marginTop:3 }}>{fmt(myRepNet-myEventFee)} comm + {fmt(myEventFee)} event</div>
                      )}
                    </div>

                    {/* True Net — admin only */}
                    {isAdmin && (
                      <div style={{ background:"var(--bz-bg)", borderRadius:8, padding:"10px 12px" }}>
                        <div style={{ fontSize:15, fontWeight:700, color:"#A78BFA" }}>{fmt(c.bazTrueNet)}</div>
                        <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:"0.8px", marginTop:3 }}>True Net</div>
                      </div>
                    )}
                  </div>

                  {/* Stream name if present */}
                  {s.streamName && (
                    <div style={{ marginTop:8, fontSize:10, color:"var(--bz-ink-3)", fontStyle:"italic" }}>{s.streamName}</div>
                  )}
                </div>
              </div>
            );
          })
      }
    </div>
  );
}

const PUBLIC_WEAPON_COLORS = { Fire:"#F97316", Ice:"#60A5FA", Steel:"#C0C0C0", Brawl:"#EF4444", Glow:"#4ade80", Hex:"#A855F7", Gum:"#F472B6", Metallic:"var(--bz-line)", Alt:"#FFFFFF", Super:"#F59E0B" };
const PUBLIC_DECK_SIZE = 60;
const PUBLIC_PLAY_LIMIT = 30;

// ── BoBA Checklist-Format Events ──────────────────────────────────────────────
// Event-specific builds. Each event pairs a hero-deck format (e.g. SPEC) with a
// curated Checklist of legal Plays for the Playbook. The Plays restriction is
// wired into the Playbook builder; the deck format defaults the hero deck.
const BOBA_EVENTS = {
  vegasbaby: {
    name: "Vegas Baby",
    color: "#F59E0B",
    deckFormat: "spec", // hero deck plays in SPEC Playmaker
    tagline: "Big moments. Big risks. Big payouts.",
    region: "Official Regional — winner earns a direct entry card to the BoBA World Championships.",
    description: "Las Vegas is all about taking chances. This format is built around coin flips, dice rolls, swingy momentum shifts, and high-risk/high-reward gameplay. Played in SPEC Playmaker, every roll matters — decision-making and planning still count, but knowing WHEN to take a risk matters just as much. Fast, explosive, unpredictable, and entertaining start to finish.",
    rules: [
      "Played in SPEC Playmaker (hero deck: 160 power ceiling, no floor)",
      "Playbook may ONLY use Plays from the Vegas Baby Checklist",
      "Standard playbook limits apply (max 30 Plays, unlimited Bonus Plays)",
      "Curated for variance, momentum, and calculated risk",
    ],
    // The curated legal Plays for this event (from the official checklist).
    plays: [
      "1/6 For 15","3rd Time Charm","3rd Time Charm HTD","4 New Plays Baby!","Add Firepower","All or Nothing","Back From The Dumps","Big Swinger","Big Win Energy","Bigger Steel Roll","Blind Substitution","Blind Substitution HTD","C'mon 6","Change The Future","Cheap Trick","Cloudy With A Chance Of Hot Dogs","Coin Gambit","Comeback Season","Comeback Time","Consolation Combo","Crystal Ball","Cursed Coin","Cursed Coin HTD","Deck Burn","Deep In The Playbook","Delayed Recovery","Dice Duel","Dice Trap","Dicey Discard","Discard Flip","Discard Or 10","Dog Gone Flip","Dog Gone Streak","Double Down","Double or Nothin'","Double-Edged Flip","Emergency Shutdown","Even Money","Even Odds","Feast Or Famine","Fire Roll","Firework","Flip & Glow","Flip Ya For 2 Plays","Forced Retreat","Forced Retreat HTD","Four-Flip Frenzy","Gambler's Feast","Good Fortune","Good Guess","Greedy Gamble","Ha! Gotcha","Heads I Win, Tails You Lose","Heads I Win, Tails You Lose HTD","Heads-Up!","Heads-Up! HTD","Heavy Swing","Hex Flipper","High Fastball","High Stakes Pump-Up","Hollow Bat","Hollow Bat HTD","Hot Dog Flip Out","Ice Blast","Ice Roll","Indestructible","Indestructible HTD","It's Gonna Cost Ya","Jump Ball","Last Chance","Last-Minute Re-Org","Last-Minute Re-Org HTD","Leave It To Chance","Leave It To Fate","Line Drive","Loan Sharked","Loan Sharked HTD","Luck Of The Draw","Lucky 7","Lucky Bounce","Lucky Discard","Lucky Gum","Lucky Shot","Make Up Call","Make Up Meal","Might Of The Underdog","Momentum Meal","Money Line","Nasty Or Nada","Nasty Or Nada HTD","One-And-One","One-And-One HTD","Only Upside","Only Upside HTD","Over Under","Over Under HTD","Pay The Price","Play Re-Order","Please Be Low","Power Pick","Pre-Game Ritual","Press Your Luck","Protein Bar","Pulling The Plug","Pulling The Plug HTD","Risky Draw","Risky Reload","Roll For 30","Roll Some Plays","Sack Streak","Scrappy Flip","Shooters Shoot","Shooters Shoot HTD","Steel Flipper","Steel Helmet","Steel Smash","Streaky","Super Lucky","Three Strikes You're Out","Toss And Trade","Trade-Up","Victory Dinner","Wildcard Wager","Win The Toss","Worth The Risk?","Worth The Risk? HTD","Vegas Baby",
    ],
    bonusPlays: [
      "A Hard Bargain","A Game Of War","Big Spender Bonus","Bonus Recovery","Bull Market","Bundle Deal","Called Shot","Clean Slate","Competitive Disadvantage","Copy Cat","Fair Trade","Hero's Resolve","Lineup Randomizer","Lunch Table","Play Reset","Plays or Dogs","Pre-Game Spy","Risky Recovery","Roll And Hope","Roller Dogs","Sacrifice it All to Win","Sweet Relish","Tear a page","The Heroes Favorite Hot Dogs","Turn The Tide","Win or Weiners",
    ],
  },
};
const PUBLIC_DBS_CAP = 1000;

// --- PUBLIC DECK BUILDER (no auth required) --------------------
// ── SHARED DECK (/deck/<id>) ─────────────────────────────────────────────────
// A read-only view of someone's deck. No login needed, so you can send the link to anyone.
// Shows the cards, the format it's built to, that format's rules, and whether it's actually legal.
const SHARED_DECK_RULES = {
  none:["Up to 60 cards","No duplicate cards","Max 6 at any power level"],
  spec:["Up to 60 cards","Power ceiling: 160","Max 6 at any power level"],
  apex:["Up to 60 cards","No power cap","Max 6 at any power level"],
  specplus:["Up to 70 Heroes","A complete 60-card \u2264160 core is REQUIRED","Then up to 10 higher: 165 \u00D72, 170 \u00D72, one each of 175\u2013200","Nothing above 200"],
  elite:["Up to 60 cards","8,250 TOTAL power across the whole deck","No Trainer cards","Max 6 at any power level"],
  brawl:["Up to 60 cards","Brawl weapon only","Max 6 at any power level"],
  gghilo_coach:["Up to 60 cards (lowest-power build)","Grandma\u2019s Linoleum (min 10)","Great Grandma Linoleum (min 10)","Any Gum-weapon card (min 10)"],
  gghilo:["Up to 60 cards","Grandma\u2019s Linoleum (min 10)","Great Grandma Linoleum (min 10)","Any Gum-weapon card (min 10)"],
  powerglove:["60 cards","Power Glove treatment only","Every card must be unique"],
  blast:["30-card deck","2025 Alpha Blast cards only","Max 3 at any power level"],
  tecmohilo:["Up to 60 cards","Tecmo Bowl cards only","Max 6 at any power level","No power cap \u2014 aim as HIGH as you can"],
  tecmohilo_coach:["Up to 60 cards (lowest-power build)","Tecmo Bowl cards only","Max 6 at any power level","No power cap \u2014 aim as LOW as you can"],
  apexmadness:["60-card Spec core (\u2264160)","Every 10 matching Inserts unlocks an Apex Hero (max 6)","Four different Foil Hot Dogs unlock four more","Ceiling: 70 Heroes"],
  vegasbaby:["Spec rules (\u2264160 power)","Max 6 at any power level"],
};

function SharedDeck({ deckId }) {
  const [deck,  setDeck]  = useState(null);
  const [cards, setCards] = useState([]);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "boba_decks", deckId));
        if (!snap.exists()) { setErr("That deck doesn't exist, or it's been deleted."); setLoading(false); return; }
        const d = { id: snap.id, ...snap.data() };
        setDeck(d);
        // Resolve just this deck's cards.
        //
        // Prefer the gzipped checklist in Storage — it's one compressed request for everything.
        // Don't cache-bust it (readCardSnapshot(true) forces a fresh multi-MB download on every
        // page view, which made shared links crawl); a shared deck does not need second-fresh data.
        //
        // If Storage fails, fetch ONLY the ~60 cards this deck actually uses, not the whole 35k
        // checklist. Firestore `in` queries cap at 30 ids, so chunk them.
        const ids = Array.from(new Set(d.cardIds || []));
        let all = null;
        // Fetch ONLY this deck's ~60 cards first. This used to download the whole gzipped checklist
        // \u2014 several megabytes of ~35k cards \u2014 just to display sixty of them, which is why a shared
        // link sat on a loading screen for so long. Firestore `in` queries cap at 30 ids, so chunk.
        try {
          const chunks = [];
          for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));
          const results = await Promise.all(
            chunks.map(ch => getDocs(query(collection(db, "boba_checklist"), where(documentId(), "in", ch))))
          );
          const found = results.flatMap(r => r.docs.map(dd => ({ ...dd.data(), id: dd.id })));
          // Only accept it if we actually resolved the deck. A partial result means some ids are
          // stale, and the full snapshot is a better source than a half-empty deck.
          if (found.length >= ids.length) all = found;
        } catch (e1) { /* fall through to the snapshot */ }
        // Fallback: the whole checklist. Slower, but it still renders when per-id reads fail or the
        // deck references cards whose ids have since changed.
        if (!all) {
          try { all = await readCardSnapshot(false); }
          catch (e2) { setErr("Couldn't load the card list. The deck exists, but its cards can't be shown."); }
        }
        setCards(Array.isArray(all) ? all : []);
        if (d.userId) {
          try { const p = await getDoc(doc(db, "boba_profiles", d.userId)); if (p.exists()) setOwner(p.data()); } catch(e){}
        }
      } catch(e) { setErr(e.message || String(e)); }
      setLoading(false);
    })();
  }, [deckId]);

  const byId = useMemo(() => { const m = {}; cards.forEach(c=>{m[c.id]=c;}); return m; }, [cards]);
  const deckCards = useMemo(() => (deck?.cardIds || []).map(id => byId[id]).filter(Boolean), [deck, byId]);

  // Same rules as the builder, so a deck can't read legal here and illegal there.
  const legality = useMemo(() => {
    if (!deck || deckCards.length === 0) return null;
    const F = fmtOf(deck.deckType);
    const problems = [];
    const total = deckCards.reduce((s,c)=>s+(parseFloat(c.power)||0), 0);
    // Deck size: the MAX is F.size. The MINIMUM required is F.coreSize when the format
    // has an optional-extra tier (Spec+: 60 core required, up to 70 allowed) — the extra
    // slots are a bonus unlock, not a requirement. Otherwise the deck must be exactly F.size.
    const SZ = F.size || 60;
    const MIN = F.coreSize || SZ;
    if (deckCards.length > SZ) problems.push(`${deckCards.length} cards — ${F.short} allows ${SZ}`);
    if (deckCards.length < MIN) problems.push(`${deckCards.length}/${MIN} cards — ${MIN} are required`);
    if (F.totalPower && total > F.totalPower) problems.push(`${total.toLocaleString()} total power \u2014 caps at ${F.totalPower.toLocaleString()}`);
    if (F.powerCap) {
      const over = deckCards.filter(c=>(parseFloat(c.power)||0) > F.powerCap).length;
      if (over) problems.push(`${over} card${over!==1?"s":""} above ${F.powerCap} power`);
    }
    const pp = {};
    deckCards.forEach(c => { const p = String(c.power||"0"); pp[p] = (pp[p]||0)+1; });
    Object.entries(pp).forEach(([p,n]) => { if (n > (F.perPower||6)) problems.push(`${n} cards at power ${p} \u2014 max ${F.perPower||6}`); });
    if (F.coreSize && F.coreCap) {
      const core = deckCards.filter(c=>(parseFloat(c.power)||0) <= F.coreCap).length;
      const high = deckCards.length - core;
      if (high > 0 && core < F.coreSize) problems.push(`Core incomplete: ${core}/${F.coreSize} at \u2264${F.coreCap}`);
    }
    if (F.groups) {
      const stray = deckCards.filter(c => !F.groups.some(g=>g.match(c))).length;
      if (stray) problems.push(`${stray} card${stray!==1?"s":""} outside the allowed inserts`);
      F.groups.forEach(g => {
        if (!g.min) return;
        const have = deckCards.filter(c=>g.match(c)).length;
        if (have < g.min) problems.push(`${have}/${g.min} ${g.label}`);
      });
    }
    if (F.unique) {
      const ids = deckCards.map(c=>c.id);
      if (new Set(ids).size !== ids.length) problems.push("Duplicate cards \u2014 every card must be unique");
    }
    return { ok: problems.length===0, problems, total };
  }, [deck, deckCards]);

  const stats = useMemo(() => {
    if (!deckCards.length) return null;
    const total = deckCards.reduce((s,c)=>s+(parseFloat(c.power)||0), 0);
    const weapons = {}, inserts = {};
    deckCards.forEach(c => {
      const w = canonWeapon(c.weapon) || "\u2014"; weapons[w] = (weapons[w]||0)+1;
      const t = c.treatment || "\u2014"; inserts[t] = (inserts[t]||0)+1;
    });
    return {
      total, avg: Math.round(total/deckCards.length),
      heroes: new Set(deckCards.map(c=>c.hero)).size,
      weapons: Object.entries(weapons).sort((a,b)=>b[1]-a[1]),
      inserts: Object.entries(inserts).sort((a,b)=>b[1]-a[1]),
    };
  }, [deckCards]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false), 2000); });
  };

  const wrap = { minHeight:"100vh", background:"var(--bz-bg)", color:"#fff", fontFamily:"'DM Sans',system-ui,sans-serif", padding:"32px 20px" };

  // A shared deck link is often the first thing someone sees of Bazooka \u2014 frequently a stranger,
  // on a phone, from a Whatnot chat. A line of grey text on a black page reads as broken. A skeleton
  // in the shape of the real thing communicates "this is loading" without needing to say it, and
  // makes the wait feel shorter because the layout is already there when the cards arrive.
  if (loading) {
    const shimmer = { background:"linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.09) 50%, rgba(255,255,255,0.04) 75%)",
                      backgroundSize:"200% 100%", animation:"deckSkeleton 1.4s ease-in-out infinite" };
    return (
      <div style={wrap}>
        <style>{`
          @keyframes deckSkeleton { 0%{background-position:200% 0;} 100%{background-position:-200% 0;} }
          @media (prefers-reduced-motion: reduce){ [data-skel]{animation:none !important;} }
        `}</style>
        <div style={{maxWidth:1100, margin:"0 auto", padding:"22px 16px"}}>
          <div data-skel style={{...shimmer, height:26, width:"46%", borderRadius:7, marginBottom:10}}/>
          <div data-skel style={{...shimmer, height:14, width:"28%", borderRadius:6, marginBottom:22}}/>
          <div style={{display:"flex", gap:10, flexWrap:"wrap", marginBottom:22}}>
            {[0,1,2,3].map(i=>(
              <div key={i} data-skel style={{...shimmer, height:52, flex:"1 1 120px", minWidth:110, borderRadius:10}}/>
            ))}
          </div>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(104px, 1fr))", gap:10}}>
            {Array.from({length:18}).map((_,i)=>(
              // Staggered so the grid ripples rather than pulsing as one block, which looks stuck.
              <div key={i} data-skel style={{...shimmer, aspectRatio:"3/4", borderRadius:9, animationDelay:`${(i%6)*0.09}s`}}/>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (err || !deck) return <div style={{...wrap, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:14}}>
    <div style={{fontSize:34}}>{"\uD83C\uDCCF"}</div>
    <div style={{fontSize:16, fontWeight:800}}>{deck ? "Couldn't show this deck" : "Deck not found"}</div>
    <div style={{fontSize:13, color:"rgba(255,255,255,0.45)"}}>{err || "That deck doesn't exist."}</div>
    <a href="/cards" style={{marginTop:8, color:"#E8317A", fontSize:13, fontWeight:700}}>Go to Bazooka Dash →</a>
  </div>;

  const F = fmtOf(deck.deckType);
  const RULES = SHARED_DECK_RULES[deck.deckType] || SHARED_DECK_RULES.none;

  return (
    <div style={wrap}>
      <div style={{maxWidth:1100, margin:"0 auto"}}>
        <div style={{display:"flex", alignItems:"flex-start", gap:14, flexWrap:"wrap", marginBottom:22}}>
          <div style={{flex:1, minWidth:240}}>
            <div style={{fontSize:11, fontWeight:800, color:"#E8317A", letterSpacing:1.4, textTransform:"uppercase", marginBottom:4}}>Bazooka · Shared Deck</div>
            <h1 style={{fontSize:30, fontWeight:900, margin:"0 0 6px", letterSpacing:-0.5}}>{deck.name || "Untitled deck"}</h1>
            <div style={{fontSize:13, color:"rgba(255,255,255,0.45)"}}>
              {owner?.displayName ? <>Built by <strong style={{color:"rgba(255,255,255,0.75)"}}>{owner.displayName}</strong> {"\u00B7"} </> : null}
              {deckCards.length} card{deckCards.length!==1?"s":""}
            </div>
          </div>
          <button onClick={copyLink} style={{background:copied?"rgba(74,222,128,0.15)":"rgba(255,255,255,0.05)", border:`1px solid ${copied?"#4ade80":"rgba(255,255,255,0.14)"}`, color:copied?"#4ade80":"rgba(255,255,255,0.7)", borderRadius:9, padding:"9px 16px", fontSize:12.5, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap"}}>
            {copied ? "\u2713 Link copied" : "\uD83D\uDD17 Copy link"}
          </button>
        </div>

        <div style={{background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.09)", borderRadius:14, padding:"16px 18px", marginBottom:18}}>
          <div style={{display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:10}}>
            <span style={{fontSize:12, fontWeight:900, color:"#FBBF24", background:"rgba(251,191,36,0.12)", border:"1px solid rgba(251,191,36,0.35)", borderRadius:7, padding:"4px 11px", letterSpacing:0.6}}>
              {F.short === "\u2014" ? "NO RESTRICTIONS" : F.short}
            </span>
            <span style={{fontSize:13, color:"rgba(255,255,255,0.6)"}}>{F.label}</span>
            {legality && (legality.ok
              ? <span style={{marginLeft:"auto", fontSize:12.5, fontWeight:800, color:"#4ade80"}}>{"\u2705"} Legal deck</span>
              : <span style={{marginLeft:"auto", fontSize:12.5, fontWeight:800, color:"#EF4444"}}>{"\u26A0\uFE0F"} Not legal</span>)}
          </div>
          <div style={{display:"flex", flexWrap:"wrap", gap:"5px 18px"}}>
            {RULES.map((r,i)=>(
              <span key={i} style={{fontSize:11.5, color:"rgba(255,255,255,0.5)", display:"flex", alignItems:"center", gap:6}}>
                <span style={{color:"#FBBF24"}}>{"\u2022"}</span>{r}
              </span>
            ))}
          </div>
          {legality && !legality.ok && (
            <div style={{marginTop:10, paddingTop:10, borderTop:"1px solid rgba(239,68,68,0.25)"}}>
              {legality.problems.map((p,i)=>(<div key={i} style={{fontSize:11.5, color:"#FCA5A5", lineHeight:1.7}}>{"\u2022"} {p}</div>))}
            </div>
          )}
        </div>

        {stats && (
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10, marginBottom:18}}>
            {[
              { l:"Total power",   v: stats.total.toLocaleString(), c:"#E8317A" },
              { l:"Avg power",     v: stats.avg,                    c:"#FBBF24" },
              { l:"Cards",         v: deckCards.length,             c:"#7B9CFF" },
              { l:"Unique heroes", v: stats.heroes,                 c:"#4ade80" },
            ].map(s=>(
              <div key={s.l} style={{background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:11, padding:"12px 14px"}}>
                <div style={{fontSize:22, fontWeight:900, color:s.c}}>{s.v}</div>
                <div style={{fontSize:10, color:"rgba(255,255,255,0.35)", fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, marginTop:2}}>{s.l}</div>
              </div>
            ))}
          </div>
        )}

        {stats && (
          <div style={{display:"flex", gap:18, flexWrap:"wrap", marginBottom:20, fontSize:11.5, color:"rgba(255,255,255,0.45)"}}>
            <div><strong style={{color:"rgba(255,255,255,0.7)"}}>Weapons:</strong> {stats.weapons.map(([w,n])=>`${w} ${n}`).join(" \u00B7 ")}</div>
            <div style={{minWidth:0}}><strong style={{color:"rgba(255,255,255,0.7)"}}>Inserts:</strong> {stats.inserts.slice(0,5).map(([t,n])=>`${t} ${n}`).join(" \u00B7 ")}{stats.inserts.length>5?` +${stats.inserts.length-5} more`:""}</div>
          </div>
        )}

        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))", gap:10}}>
          {[...deckCards].sort((a,b)=>(parseFloat(b.power)||0)-(parseFloat(a.power)||0)).map((c,i)=>{
            const wc = PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)] || "#666";
            return (
              <div key={`${c.id}_${i}`} style={{background:"var(--bz-s1)", border:"1px solid #262626", borderRadius:9, overflow:"hidden"}}>
                <div style={{position:"relative", aspectRatio:"3/4", background:`linear-gradient(135deg, ${wc}18, var(--bz-bg) 70%)`}}>
                  {c.imageUrl
                    ? <img src={c.imageUrl} alt={c.hero} loading="lazy" decoding="async" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                    : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"rgba(255,255,255,0.2)"}}>no image</div>}
                  <div style={{position:"absolute", top:5, right:6, background:"rgba(0,0,0,0.75)", color:"#FBBF24", borderRadius:5, padding:"1px 6px", fontSize:11, fontWeight:900}}>{c.power}</div>
                </div>
                <div style={{padding:"6px 8px"}}>
                  <div style={{fontSize:11, fontWeight:800, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{c.hero}</div>
                  <div style={{fontSize:9.5, color:"rgba(255,255,255,0.35)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                    <span style={{color:wc, fontWeight:700}}>{canonWeapon(c.weapon)}</span> {"\u00B7"} {c.treatment}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{marginTop:30, textAlign:"center", fontSize:12, color:"rgba(255,255,255,0.3)"}}>
          Built with <a href="/cards" style={{color:"#E8317A", fontWeight:700, textDecoration:"none"}}>Bazooka Dash</a>
        </div>
      </div>
    </div>
  );
}


// Public view of a TRADE PACKAGE. No login needed. Reads boba_trade_packages/{id}, which carries a
// self-contained snapshot of the cards (so it renders instantly and survives later card re-imports),
// plus the owner's contact line so the recipient can reply.
function SharedTradePackage({ packageId }) {
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "boba_trade_packages", packageId));
        if (!snap.exists()) { setErr("This trade package doesn't exist, or it's been deleted."); setLoading(false); return; }
        setPkg({ id: snap.id, ...snap.data() });
      } catch(e) { setErr("Couldn't load this trade package."); }
      setLoading(false);
    })();
  }, [packageId]);

  if (loading) return (
    <div style={{minHeight:"100vh",background:"radial-gradient(ellipse at top,#16101d 0%,#0a0a0f 60%)",color:"#eee",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:"system-ui,sans-serif",gap:22}}>
      <style>{`
        @keyframes bzcardflip { 0%,100%{ transform:rotateY(0deg) translateY(0);} 50%{ transform:rotateY(180deg) translateY(-8px);} }
        @keyframes bzglow { 0%,100%{ opacity:.35; transform:scale(1);} 50%{ opacity:.7; transform:scale(1.15);} }
        @keyframes bzdots { 0%,80%,100%{ opacity:.2;} 40%{ opacity:1;} }
        @keyframes bzshimmer { 0%{ background-position:-200% 0;} 100%{ background-position:200% 0;} }
      `}</style>
      <div style={{position:"relative",width:96,height:128}}>
        <div style={{position:"absolute",inset:-18,borderRadius:"50%",background:"radial-gradient(circle,#E8317A55,transparent 70%)",animation:"bzglow 1.8s ease-in-out infinite"}}/>
        <div style={{width:96,height:128,borderRadius:12,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",boxShadow:"0 12px 40px rgba(232,49,122,0.4)",animation:"bzcardflip 1.8s ease-in-out infinite",transformStyle:"preserve-3d",display:"flex",alignItems:"center",justifyContent:"center",fontSize:34}}>{"\uD83E\uDD1D"}</div>
      </div>
      <div style={{fontSize:22,fontWeight:900,letterSpacing:0.5,background:"linear-gradient(90deg,#E8317A,#fff,#7B2FF7,#E8317A)",backgroundSize:"200% auto",WebkitBackgroundClip:"text",backgroundClip:"text",color:"transparent",animation:"bzshimmer 2.5s linear infinite"}}>BAZOOKA</div>
      <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",fontWeight:600}}>
        Loading trade package<span style={{animation:"bzdots 1.4s infinite"}}>.</span><span style={{animation:"bzdots 1.4s infinite .2s"}}>.</span><span style={{animation:"bzdots 1.4s infinite .4s"}}>.</span>
      </div>
    </div>
  );
  if (err) return <div style={{minHeight:"100vh",background:"#0a0a0f",color:"#E8317A",display:"flex",alignItems:"center",justifyContent:"center",padding:24,textAlign:"center",fontFamily:"system-ui,sans-serif"}}>{err}</div>;

  const items = pkg.items || [];
  const totalCards = items.reduce((s,it)=>s+(parseInt(it.qty)||1), 0);
  return (
    <div style={{minHeight:"100vh",background:"#0a0a0f",color:"#eee",fontFamily:"system-ui,sans-serif",padding:"0 0 60px"}}>
      <div style={{maxWidth:900,margin:"0 auto",padding:"0 18px"}}>
        <div style={{padding:"30px 0 18px",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
          <div style={{display:"inline-block",fontSize:11,fontWeight:800,letterSpacing:1,color:"#4ade80",background:"rgba(74,222,128,0.1)",border:"1px solid rgba(74,222,128,0.3)",borderRadius:20,padding:"4px 12px",marginBottom:12}}>{"\uD83E\uDD1D"} TRADE PACKAGE</div>
          <div style={{fontSize:26,fontWeight:900,color:"#fff",marginBottom:6}}>{pkg.title || "Trade package"}</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.5)"}}>From {pkg.ownerName || "a collector"} {"\u00b7"} {totalCards} card{totalCards===1?"":"s"}</div>
          {pkg.status === "completed" && (
            <div style={{marginTop:14,display:"flex",alignItems:"center",gap:10,background:"rgba(74,222,128,0.12)",border:"1px solid rgba(74,222,128,0.4)",borderRadius:12,padding:"12px 16px"}}>
              <span style={{fontSize:22}}>{"\u2705"}</span>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:"#4ade80"}}>Trade completed</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.6)"}}>{pkg.completedAt ? `Marked traded on ${new Date(pkg.completedAt).toLocaleDateString()}` : "This trade has been completed."} {"\u00b7"} Kept as a receipt for both sides.</div>
              </div>
            </div>
          )}
          {pkg.totalValue > 0 && <div style={{marginTop:12,display:"inline-block",background:"rgba(74,222,128,0.1)",border:"1px solid rgba(74,222,128,0.35)",borderRadius:10,padding:"8px 16px"}}><span style={{fontSize:12,color:"rgba(255,255,255,0.55)",fontWeight:700}}>Total value{"  "}</span><span style={{fontSize:20,fontWeight:900,color:"#4ade80"}}>${Number(pkg.totalValue).toFixed(2)}</span></div>}
          {pkg.note && <div style={{marginTop:14,fontSize:14,lineHeight:1.6,color:"rgba(255,255,255,0.8)",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,padding:"12px 15px"}}>{pkg.note}</div>}
          {pkg.contact && (
            <div style={{marginTop:14,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <span style={{fontSize:12,fontWeight:800,color:"#4ade80"}}>Reply to:</span>
              <span style={{fontSize:14,fontWeight:700,color:"#fff",background:"rgba(74,222,128,0.1)",border:"1px solid rgba(74,222,128,0.3)",borderRadius:8,padding:"6px 12px"}}>{pkg.contact}</span>
            </div>
          )}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:12,paddingTop:22}}>
          {items.map((it,idx) => (
            <div key={it.id||idx} style={{background:"#14141c",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,overflow:"hidden"}}>
              <div style={{position:"relative",aspectRatio:"3/4",background:"#0a0a0f",display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                {it.imageUrl ? <img src={it.imageUrl} alt={it.hero} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <span style={{color:"#333",fontSize:11}}>No image</span>}
                {it.isMyPhoto && it.imageUrl && <div style={{position:"absolute",bottom:5,left:5,background:"rgba(74,222,128,0.9)",color:"#06240f",fontSize:9,fontWeight:800,borderRadius:4,padding:"2px 5px"}}>Actual photo</div>}
              </div>
              <div style={{padding:"9px 10px"}}>
                <div style={{fontSize:13,fontWeight:800,color:"#fff",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{it.hero||"\u2014"}</div>
                {it.playName && <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{it.playName}</div>}
                <div style={{fontSize:10.5,color:"rgba(255,255,255,0.4)",marginTop:3}}>{[it.treatment, it.cardNum?("#"+it.cardNum):""].filter(Boolean).join(" \u00b7 ")}</div>
                {(parseInt(it.qty)||1) > 1 && <div style={{fontSize:10.5,fontWeight:800,color:"#4ade80",marginTop:3}}>{"\u00d7"}{it.qty} available</div>}
                {Number(it.value) > 0 && <div style={{fontSize:13,fontWeight:900,color:"#4ade80",marginTop:3}}>${Number(it.value).toFixed(2)}</div>}
              </div>
            </div>
          ))}
        </div>
        <div style={{marginTop:30,textAlign:"center",fontSize:11,color:"rgba(255,255,255,0.25)"}}>Shared via Bazooka Dash</div>
      </div>
    </div>
  );
}


function PublicDeckBuilder() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deckCards, setDeckCards] = useState([]);
  const [deckName, setDeckName] = useState("My Deck");
  const [deckType, setDeckType] = useState("none");
  const [deckSearch, setDeckSearch] = useState("");
  const [deckFilterWeap, setDeckFilterWeap] = useState("");
  const [deckFilterHero, setDeckFilterHero] = useState("");
  const [deckFilterPower, setDeckFilterPower] = useState("");
  const [deckFilterPowers, setDeckFilterPowers] = useState(new Set());
  const [deckFilterSet,    setDeckFilterSet]    = useState("");
  const [deckFilterTreat,  setDeckFilterTreat]  = useState("");
  const [deckFilterSku,    setDeckFilterSku]    = useState("");
  const [deckSlotSort, setDeckSlotSort] = useState("added");
  const [user, setUser] = useState(null);
  const [savedDecks, setSavedDecks] = useState([]);
  const [deckLoadId, setDeckLoadId] = useState(null);
  const [deckSaving, setDeckSaving] = useState(false);
  const [deckSaved, setDeckSaved] = useState(false);

  useEffect(() => onAuthStateChanged(auth, u => setUser(u)), []);
  useEffect(() => {
    if (!user) { setSavedDecks([]); return; }
    const unsub = onSnapshot(query(collection(db, "boba_decks"), where("userId","==",user.uid)), snap => {
      setSavedDecks(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.savedAt||"").localeCompare(a.savedAt||"")));
    }, e=>console.error("load decks failed:", e));
    return unsub;
  }, [user]);

  async function saveDeck() {
    if (!deckName.trim() || deckCards.length === 0) { alert("Name your deck and add at least one card before saving."); return; }
    if (!user) { alert("Please sign in to save your deck. Tap your profile / sign-in to get started."); return; }
    setDeckSaving(true);
    const id = deckLoadId || `deck_${Date.now()}`;
    try {
      await setDoc(doc(db,"boba_decks",id), {
        id, userId: user.uid, name: deckName.trim(),
        cardIds: deckCards, cardCount: deckCards.length,
        deckType, savedAt: new Date().toISOString(),
      }, { merge:true });
      setDeckLoadId(id);
      setDeckSaved(true); setTimeout(()=>setDeckSaved(false), 1800);
    } catch(e) {
      console.error("save deck failed:", e);
      alert("Couldn't save your deck: " + (e?.message || e) + "\n\nIf this mentions permissions, the Firestore rules for boba_decks need to be published.");
    } finally { setDeckSaving(false); }
  }
  async function deleteDeck(id) {
    if (!window.confirm("Delete this deck?")) return;
    try { await deleteDoc(doc(db,"boba_decks",id)); } catch(e){ alert("Delete failed: "+(e?.message||e)); return; }
    if (deckLoadId === id) { setDeckLoadId(null); setDeckName("My Deck"); setDeckCards([]); }
  }
  // Mark a deck as registered (or clear it). Registration is a real-world step —
  // logging the deck with the league/event — so this is just a flag Devin sets
  // himself to track which decks are squared away vs still pending.
  async function toggleRegistered(deck) {
    const next = !deck.registered;
    // Optimistic: flip locally first so the badge updates the instant you click.
    setSavedDecks(list => list.map(d => d.id === deck.id ? { ...d, registered: next } : d));
    try {
      await updateDoc(doc(db,"boba_decks",deck.id), {
        registered: next,
        registeredAt: next ? new Date().toISOString() : null,
      });
    } catch (e) {
      // Roll back on failure so the UI never lies about what's saved.
      setSavedDecks(list => list.map(d => d.id === deck.id ? { ...d, registered: !next } : d));
      alert("Couldn't update registration: " + (e?.message || e));
    }
  }
  function loadDeck(deck) {
    setDeckLoadId(deck.id); setDeckName(deck.name||"My Deck");
    setDeckCards(deck.cardIds||[]); setDeckType(deck.deckType||"none");
  }
  function newDeck() {
    setDeckLoadId(null); setDeckName("My Deck"); setDeckCards([]);
  }

  const loadCardsFromDB = async (force) => {
    const CACHE_KEY = "boba_checklist_cache_v3";
    if (!force) {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) {
          const { cards: cc, ts } = JSON.parse(raw);
          if (Date.now() - ts < 5*60*1000 && cc?.length > 0) { setCards(cc.filter(c=>{ const t=(c.treatment||"").toLowerCase(); return t!=="plays"&&t!=="bonus plays"&&t!=="home team discount"; })); setLoading(false); return; }
        }
      } catch(e) {}
    }
    setLoading(true);
    const snap = await getDocs(collection(db, "boba_checklist"));
    const all = snap.docs.map(d=>({...d.data(), id:d.id}));
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ cards: all, ts: Date.now() })); } catch(e) {}
    setCards(all.filter(c=>{ const t=(c.treatment||"").toLowerCase(); return t!=="plays"&&t!=="bonus plays"&&t!=="home team discount"; }));
    setLoading(false);
  };

  useEffect(() => {
    loadCardsFromDB(false);
  }, []);

  const deckSet = new Set(deckCards);
  const inDeck  = cards.filter(c => deckSet.has(c.id));
  const empty   = PUBLIC_DECK_SIZE - inDeck.length;
  const isSpec        = deckType==="spec";
  const isApex        = deckType==="apex";
  const isApexMadness = deckType==="apexmadness";
  const hasRules = isSpec||isApex||isApexMadness;
  const dupKey  = c => `${(c.hero||"").toLowerCase()}|${(c.variation||"").toLowerCase()}|${c.power||""}|${(c.weapon||"").toLowerCase()}`;
  const inDeckDupKeys = new Set(inDeck.map(dupKey));
  const powerCount = {};
  inDeck.forEach(c => { const p=c.power||"0"; powerCount[p]=(powerCount[p]||0)+1; });
  const treatCountCore = {}, treatCountApex = {};
  if (isApexMadness) {
    inDeck.forEach(c => {
      const t=(c.treatment||"Unknown").toLowerCase(), p=parseFloat(c.power||0);
      // No power FLOOR on the core count. The 10-inserts-unlocks-an-apex rule is about how many
      // inserts of a treatment are in the deck, not how strong they are \u2014 and a thinner collection
      // often has to reach below 115 to field a full deck. Requiring 115+ meant those cards sat in
      // the deck contributing nothing toward an unlock.
      if (p<=160) treatCountCore[t]=(treatCountCore[t]||0)+1;
      if (p>160)          treatCountApex[t]=(treatCountApex[t]||0)+1;
    });
  }
  const totalPower = inDeck.reduce((s,c)=>s+(parseFloat(c.power)||0),0);
  const weaponBreak = {}, heroCover = new Set();
  inDeck.forEach(c=>{ const w=c.weapon||"Unknown"; weaponBreak[w]=(weaponBreak[w]||0)+1; if(c.hero) heroCover.add(c.hero); });
  const weaponEntries = Object.entries(weaponBreak).sort((a,b)=>b[1]-a[1]);

  function canAdd(c) {
    if (deckSet.has(c.id)) return { ok:false, reason:"Already in deck" };
    if (inDeck.length >= PUBLIC_DECK_SIZE) return { ok:false, reason:"Deck full" };
    if (hasRules && inDeckDupKeys.has(dupKey(c))) return { ok:false, reason:"Duplicate card" };
    if (hasRules && (powerCount[c.power||"0"]||0)>=6) return { ok:false, reason:`6 cards already at power ${c.power}` };
    const p = parseFloat(c.power||0);
    if (isSpec && p>160) return { ok:false, reason:`Power ${c.power} exceeds 160` };
    if (isApexMadness && p>160) {
      const t=(c.treatment||"Unknown").toLowerCase();
      const core=treatCountCore[t]||0;
      if (core<10) return { ok:false, reason:`Need 10 core ${c.treatment} cards first (have ${core}/10)` };
      if ((treatCountApex[t]||0)>=1) return { ok:false, reason:`Already have 1 unlocked ${c.treatment} apex card` };
    }
    return { ok:true };
  }

  const weapons = sortWeapons([...new Set(cards.map(c=>canonWeapon(c.weapon)).filter(Boolean))]);
  const heroes  = [...new Set(cards.map(c=>c.hero).filter(Boolean))].sort();
  const powers  = [...new Set(cards.map(c=>c.power!=null&&c.power!==""?String(c.power):null).filter(Boolean))].sort((a,b)=>parseFloat(b)-parseFloat(a));

  const available = cards.filter(c => {
    if (deckSet.has(c.id)) return false;
    if (deckFilterWeap && c.weapon!==deckFilterWeap) return false;
    if (deckFilterHero && c.hero!==deckFilterHero) return false;
    if (deckFilterPowers.size > 0 && !deckFilterPowers.has(String(c.power||""))) return false;
    if (deckFilterSet && c.setName!==deckFilterSet) return false;
    if (deckFilterTreat && c.treatment!==deckFilterTreat) return false;
    if (deckSearch && !`${c.hero} ${c.cardNum} ${c.treatment}`.toLowerCase().includes(deckSearch.toLowerCase())) return false;
    return true;
  }).sort((a,b)=>(parseFloat(b.power)||0)-(parseFloat(a.power)||0));

  const S = { inp:{ background:"var(--bz-s3)", border:"1px solid var(--bz-line)", borderRadius:8, color:"var(--bz-ink)", padding:"6px 10px", fontSize:12, fontFamily:"inherit", outline:"none", width:"100%" }, card:{ background:"linear-gradient(160deg,#181016,#130d11)", border:"1px solid var(--bz-line)", borderRadius:16, padding:"14px 16px", boxShadow:"0 1px 0 rgba(255,255,255,0.03) inset, 0 6px 22px rgba(0,0,0,0.35)" } };

  if (loading) return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"var(--bz-bg)", color:"#E8317A", fontSize:16, fontWeight:700 }}>Loading cards...</div>;

  return (
    <div style={{ background:"var(--bz-bg)", minHeight:"100vh", fontFamily:"'Trebuchet MS',sans-serif", color:"var(--bz-ink)", padding:20 }}>
      <div style={{ maxWidth:1400, margin:"0 auto" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
          <a href="/" style={{ color:"var(--bz-ink-3)", fontSize:12, textDecoration:"none" }}>{"\u2190 Back"}</a>
          <div style={{ fontSize:22, fontWeight:900, color:"#E8317A" }}>{"\u2694\uFE0F Deck Builder"}</div>
          <select value={deckType} onChange={e=>setDeckType(e.target.value)} style={{ ...S.inp, width:"auto", fontWeight:700, color:deckType==="spec"?"#FBBF24":deckType==="apex"?"#A855F7":"#888" }}>
            {Object.entries(DECK_FORMATS).map(([k,f])=><option key={k} value={k}>{f.label}</option>)}
          </select>
          <span style={{ fontSize:12, color:empty===0?"#4ade80":"#FBBF24", fontWeight:700 }}>{inDeck.length}/{PUBLIC_DECK_SIZE} cards</span>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginLeft:"auto" }}>
            <input value={deckName} onChange={e=>setDeckName(e.target.value)} placeholder="Deck name" style={{ ...S.inp, width:140, padding:"6px 10px", fontSize:12 }}/>
            <button onClick={saveDeck} disabled={deckSaving||deckCards.length===0} style={{ background:deckSaved?"#0a2a0a":"#0a1a0a", border:`1px solid ${deckSaved?"#4ade80":"#4ade8044"}`, color:"#4ade80", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:700, cursor:deckCards.length===0?"not-allowed":"pointer", fontFamily:"inherit", opacity:deckCards.length===0?0.4:1, whiteSpace:"nowrap" }}>
              {deckSaving?"Saving...":deckSaved?"✓ Saved":"💾 Save Deck"}</button>
            <button onClick={newDeck} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"7px 12px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>+ New</button>
          </div>
        </div>
        {savedDecks.length > 0 && (
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14, marginTop:-6 }}>
            <span style={{ fontSize:11, color:"var(--bz-ink-3)", fontWeight:700, alignSelf:"center" }}>Saved:</span>
            {(() => {
              const reg = savedDecks.filter(d=>d.registered).length;
              const pending = savedDecks.length - reg;
              if (!pending) return <span style={{ fontSize:11, color:"#4ade80", fontWeight:700, alignSelf:"center" }}>{"\u2713 all "+savedDecks.length+" registered"}</span>;
              return <span style={{ fontSize:11, color:"#FBBF24", fontWeight:700, alignSelf:"center" }}>{reg+"/"+savedDecks.length+" registered \u00B7 "+pending+" pending"}</span>;
            })()}
            {savedDecks.map(d=>(
              <div key={d.id} style={{ display:"flex", alignItems:"center", gap:4, background:deckLoadId===d.id?"#1A1A2E":"var(--bz-s2)", border:`1px solid ${d.registered?"#4ade80":(deckLoadId===d.id?"#7B9CFF":"var(--bz-line-2)")}`, borderRadius:8, padding:"4px 10px" }}>
                <button onClick={()=>toggleRegistered(d)} title={d.registered?"Registered — click to unmark":"Mark as registered"}
                  style={{ background:"none", border:"none", cursor:"pointer", fontSize:13, lineHeight:1, padding:"0 2px",
                           color:d.registered?"#4ade80":"#555" }}>
                  {d.registered?"\u2713":"\u25CB"}
                </button>
                <button onClick={()=>loadDeck(d)} style={{ background:"none", border:"none", color:deckLoadId===d.id?"#7B9CFF":"#888", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{d.name} <span style={{ color:"var(--bz-ink-3)", fontWeight:400 }}>({d.cardCount})</span></button>
                <button onClick={()=>deleteDeck(d.id)} style={{ background:"none", border:"none", color:"#8a8a8a", cursor:"pointer", fontSize:14, lineHeight:1, padding:"0 2px" }}>×</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) clamp(260px,28%,340px)", gap:14, alignItems:"start" }}>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              <input value={deckSearch} onChange={e=>setDeckSearch(e.target.value)} placeholder="Search hero, card #, treatment..." style={{ ...S.inp, flex:1, minWidth:140 }}/>
              <select value={deckFilterWeap} onChange={e=>setDeckFilterWeap(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
                <option value="">All Weapons</option>
                {sortWeapons([...new Set((deckFilterSet ? cards.filter(c=>c.setName===deckFilterSet) : cards).map(c=>canonWeapon(c.weapon)).filter(Boolean))]).map(w=><option key={w} value={w}>{w}</option>)}
              </select>
              <select value={deckFilterSet} onChange={e=>{ setDeckFilterSet(e.target.value); setDeckFilterWeap(""); setDeckFilterTreat(""); setDeckFilterHero(""); }} style={{ ...S.inp, width:"auto", cursor:"pointer", color:deckFilterSet?"#7B9CFF":"#888" }}>
                <option value="">All Sets</option>
                {[...new Set(cards.map(c=>c.setName).filter(Boolean))].sort().map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              <select value={deckFilterTreat} onChange={e=>setDeckFilterTreat(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer", color:deckFilterTreat?"#FBBF24":"#888" }}>
                <option value="">All Treatments</option>
                {[...new Set((deckFilterSet ? cards.filter(c=>c.setName===deckFilterSet) : cards).map(c=>c.treatment).filter(Boolean))].sort().map(t=><option key={t} value={t}>{t}</option>)}
              </select>
              <select value={deckFilterHero} onChange={e=>setDeckFilterHero(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
                <option value="">All Heroes</option>
                {[...new Set((deckFilterSet ? cards.filter(c=>c.setName===deckFilterSet) : cards).map(c=>c.hero).filter(Boolean))].sort().map(h=><option key={h} value={h}>{h}</option>)}
              </select>
              <span style={{ fontSize:11, color:"var(--bz-ink-3)", alignSelf:"center" }}>{available.length} cards</span>
            </div>
            {/* Multi-select power chips */}
            <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
              {[...new Set(cards.map(c=>c.power!=null&&c.power!==""?String(c.power):null).filter(Boolean))].sort((a,b)=>parseFloat(b)-parseFloat(a)).map(p=>{
                const sel = deckFilterPowers.has(p);
                const over = (isSpec||isApexMadness) && parseFloat(p)>160;
                return (
                  <button key={p} onClick={()=>setDeckFilterPowers(prev=>{ const n=new Set(prev); n.has(p)?n.delete(p):n.add(p); return n; })}
                    style={{ background:sel?(over?"#E8317A":"#FBBF2433"):"var(--bz-s1)", border:`1px solid ${sel?(over?"#E8317A":"#FBBF24"):"var(--bz-line-2)"}`, color:sel?(over?"#E8317A":"#FBBF24"):"#555", borderRadius:6, padding:"3px 9px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                    {p}{over?" \u26A0":""}
                  </button>
                );
              })}
              {deckFilterPowers.size > 0 && (
                <button onClick={()=>setDeckFilterPowers(new Set())} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:6, padding:"3px 9px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{"\u2715 Clear"}</button>
              )}
            </div>
            <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, overflow:"hidden", maxHeight:560, overflowY:"auto" }}>
              {available.map((c,i)=>{
                const { ok, reason } = canAdd(c);
                const wc = PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
                return (
                  <div key={c.id} onClick={()=>{ if(ok) setDeckCards(p=>[...p,c.id]); }}
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", borderBottom:"1px solid #111", background:i%2===0?"var(--bz-bg)":"var(--bz-s1)", cursor:ok?"pointer":"not-allowed", opacity:ok?1:0.35 }}
                    title={!ok?reason:""}>
                    {c.imageUrl && <img src={c.imageUrl} alt={c.hero} style={{ width:36, height:48, objectFit:"cover", borderRadius:4, flexShrink:0 }}/>}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)" }}>{c.hero}</div>
                      <div style={{ display:"flex", gap:6, marginTop:2, flexWrap:"wrap", fontSize:10 }}>
                        <span style={{ color:"var(--bz-ink-3)" }}>#{c.cardNum}</span>
                        {c.weapon && <span style={{ color:wc, fontWeight:700 }}>{c.weapon}</span>}
                        {c.treatment && <span style={{ color:"var(--bz-ink-3)" }}>{c.treatment}</span>}
                        {!ok && <span style={{ color:"#E8317A" }}>{reason}</span>}
                      </div>
                    </div>
                    {c.power && <div style={{ fontSize:16, fontWeight:900, color:isSpec&&parseFloat(c.power)>160?"#E8317A":wc, flexShrink:0 }}>{c.power}</div>}
                    {ok && <div style={{ fontSize:18, color:"#4ade80", flexShrink:0 }}>+</div>}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <div style={{ ...S.card }}>
              <div style={{ fontSize:12, fontWeight:800, color:"var(--bz-ink)", marginBottom:10 }}>{"\u2694\uFE0F Stats"}</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                {[{l:"Cards",v:`${inDeck.length}/${PUBLIC_DECK_SIZE}`,c:inDeck.length===PUBLIC_DECK_SIZE?"#4ade80":"#FBBF24"},{l:"Total Power",v:Math.round(totalPower).toLocaleString(),c:"#E8317A"},{l:"Heroes",v:heroCover.size,c:"#7B9CFF"},{l:"Avg Power",v:inDeck.length>0?Math.round(totalPower/inDeck.length):0,c:"#FBBF24"}].map(({l,v,c})=>(
                  <div key={l} style={{ background:"var(--bz-s1)", borderRadius:8, padding:"8px 10px", textAlign:"center" }}>
                    <div style={{ fontSize:18, fontWeight:900, color:c }}>{v}</div>
                    <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>{l}</div>
                  </div>
                ))}
              </div>
              {weaponEntries.length > 0 && <div>{weaponEntries.map(([w,cnt])=>{ const wc=PUBLIC_WEAPON_COLORS[w]||"#444"; const pct=Math.round(cnt/inDeck.length*100); return (<div key={w} style={{ marginBottom:5 }}><div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}><span style={{ fontSize:11, color:wc, fontWeight:700 }}>{w}</span><span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{cnt} ({pct}%)</span></div><div style={{ height:4, background:"var(--bz-s2)", borderRadius:2 }}><div style={{ width:`${pct}%`, height:"100%", background:wc, borderRadius:2 }}/></div></div>); })}</div>}
              {isApexMadness && inDeck.length > 0 && (
                <div style={{ marginTop:12 }}>
                  <div style={{ fontSize:10, color:"#A855F7", fontWeight:700, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Treatment Unlocks</div>
                  {[...new Set(inDeck.map(c=>c.treatment).filter(Boolean))].sort().map(t => {
                    const tl=t.toLowerCase(), core=treatCountCore[tl]||0, apex=treatCountApex[tl]||0, unlocked=core>=10;
                    return (
                      <div key={t} style={{ marginBottom:6 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                          <span style={{ fontSize:11, color:unlocked?"#A855F7":"#888", fontWeight:unlocked?700:400 }}>{unlocked?"\uD83D\uDD13":"\uD83D\uDD12"} {t}</span>
                          <span style={{ fontSize:11, color:unlocked?"#A855F7":"#555" }}>{core}/10{unlocked?` · ${apex}/1 apex`:""}</span>
                        </div>
                        <div style={{ height:3, background:"var(--bz-s2)", borderRadius:2 }}>
                          <div style={{ width:`${Math.min(100,core/10*100)}%`, height:"100%", background:unlocked?"#A855F7":"#333", borderRadius:2 }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ ...S.card }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                <span style={{ fontSize:12, fontWeight:800, color:"var(--bz-ink)" }}>Deck Slots -- {empty>0?<span style={{ color:"#FBBF24" }}>{empty} empty</span>:<span style={{ color:"#4ade80" }}>{"Full! \u2705"}</span>}</span>
                <select value={deckSlotSort} onChange={e=>setDeckSlotSort(e.target.value)} style={{ ...S.inp, width:"auto", fontSize:10, padding:"3px 8px", cursor:"pointer" }}>
                  <option value="added">Order Added</option><option value="power">{"Power \u2193"}</option><option value="name">{"Name A\u2192Z"}</option><option value="weapon">Weapon</option>
                </select>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:4 }}>
                {(()=>{ const sorted=[...inDeck].sort((a,b)=>{ if(deckSlotSort==="power") return (parseFloat(b.power)||0)-(parseFloat(a.power)||0); if(deckSlotSort==="name") return (a.hero||"").localeCompare(b.hero||""); if(deckSlotSort==="weapon") return (a.weapon||"").localeCompare(b.weapon||""); return 0; }); return Array.from({length:PUBLIC_DECK_SIZE}).map((_,i)=>{ const c=sorted[i]; if(c){ const wc=PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)]||"#444"; return (<div key={i} title={`${c.hero} -- ${c.weapon||""} ${c.power||""}`} onClick={()=>setDeckCards(p=>p.filter(id=>id!==c.id))} style={{ aspectRatio:"3/4", borderRadius:4, overflow:"hidden", position:"relative", cursor:"pointer", border:`1.5px solid ${wc}44`, background:"var(--bz-s2)" }}>{c.imageUrl?<img src={c.imageUrl} alt={c.hero} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>:<div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:7, color:wc, fontWeight:700, textAlign:"center", padding:2 }}>{c.hero?.split(" ")[0]}</div>}</div>); } return (<div key={i} style={{ aspectRatio:"3/4", borderRadius:4, border:"1px dashed var(--bz-s2)", background:"#080808", display:"flex", alignItems:"center", justifyContent:"center" }}><span style={{ fontSize:9, color:"#aaa", fontWeight:700 }}>{i+1}</span></div>); }); })()}
              </div>
              {inDeck.length>0 && <button onClick={()=>{ if(window.confirm("Clear deck?")) setDeckCards([]); }} style={{ marginTop:10, background:"transparent", border:"1px solid #E8317A22", color:"#E8317A", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", width:"100%" }}>{"\u2715 Clear"}</button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- PUBLIC PLAYBOOK BUILDER (no auth required) --------------------
function PublicPlaybookBuilder() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pbCards, setPbCards] = useState([]);
  const [pbSearch, setPbSearch] = useState("");
  const [pbSort, setPbSort] = useState("name");
  const [pbName, setPbName] = useState("My Playbook");
  const [user, setUser] = useState(null);
  const _pbAdmin = (user?.email||"").toLowerCase().endsWith("@bazookabreaks.com");
  const [dbsEdits, setDbsEdits] = useState({}); // cardId -> in-progress value
  async function savePbDbs(cardId, val) {
    const v = val === "" ? "" : (parseFloat(val)||0);
    try { await setDoc(doc(db,"boba_checklist",cardId), { dbs: v }, { merge:true }); } catch(e) { console.error("save dbs failed", e); }
  }
  const [savedPlaybooks, setSavedPlaybooks] = useState([]);
  const [pbLoadId, setPbLoadId] = useState(null);
  const [pbSaving, setPbSaving] = useState(false);
  const [pbSaved, setPbSaved] = useState(false);

  useEffect(() => onAuthStateChanged(auth, u => setUser(u)), []);
  useEffect(() => {
    if (!user) { setSavedPlaybooks([]); return; }
    const unsub = onSnapshot(query(collection(db, "boba_playbooks"), where("userId","==",user.uid)), snap => {
      setSavedPlaybooks(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.savedAt||"").localeCompare(a.savedAt||"")));
    }, e=>console.error("load playbooks failed:", e));
    return unsub;
  }, [user]);

  async function savePlaybook() {
    if (!pbName.trim() || pbCards.length === 0) { alert("Name your playbook and add at least one card before saving."); return; }
    if (!user) { alert("Please sign in to save your playbook."); return; }
    setPbSaving(true);
    const id = pbLoadId || `pb_${Date.now()}`;
    const plays = pbCards.filter(e=>e.type==="play").length;
    const bonus = pbCards.filter(e=>e.type==="bonus").length;
    try {
      await setDoc(doc(db,"boba_playbooks",id), {
        id, userId: user.uid, name: pbName.trim(),
        entries: pbCards, playCount: plays, bonusCount: bonus,
        savedAt: new Date().toISOString(),
      }, { merge:true });
      setPbLoadId(id);
      setPbSaved(true); setTimeout(()=>setPbSaved(false), 1800);
    } catch(e) {
      console.error("save playbook failed:", e);
      alert("Couldn't save your playbook: " + (e?.message || e) + "\n\nIf this mentions permissions, the Firestore rules for boba_playbooks need to be published.");
    } finally { setPbSaving(false); }
  }
  async function deletePlaybook(id) {
    if (!window.confirm("Delete this playbook?")) return;
    try { await deleteDoc(doc(db,"boba_playbooks",id)); } catch(e){ alert("Delete failed: "+(e?.message||e)); return; }
    if (pbLoadId === id) { setPbLoadId(null); setPbName("My Playbook"); setPbCards([]); }
  }
  function loadPlaybook(pb) {
    setPbLoadId(pb.id); setPbName(pb.name||"My Playbook"); setPbCards(pb.entries||[]);
  }
  // Copy an existing playbook into the builder as a NEW, unsaved one. pbLoadId is cleared, so
  // hitting Save writes a fresh doc rather than overwriting the original — you can tweak the copy
  // freely without risking the playbook you copied from.
  function duplicatePlaybook(pb) {
    setPbLoadId(null);
    const base = (pb.name || "My Playbook").replace(/\s*\(copy( \d+)?\)$/i, "");
    // If "Foo (copy)" already exists, number it: "Foo (copy 2)", "Foo (copy 3)", …
    const taken = new Set(savedPlaybooks.map(p=>(p.name||"").toLowerCase()));
    let name = `${base} (copy)`;
    let n = 2;
    while (taken.has(name.toLowerCase())) { name = `${base} (copy ${n})`; n++; }
    setPbName(name);
    setPbCards((pb.entries||[]).map(e=>({...e})));  // clone entries so edits don't touch the source
    if (typeof window !== "undefined") window.scrollTo({ top:0, behavior:"smooth" });
  }
  function newPlaybook() {
    setPbLoadId(null); setPbName("My Playbook"); setPbCards([]);
  }

  useEffect(() => {
    async function load() {
      const CACHE_KEY = "boba_checklist_cache_v3";
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) {
          const { cards: cc, ts } = JSON.parse(raw);
          if (Date.now() - ts < 5*60*1000 && cc?.length > 0) {
            const plays = cc.filter(c=>{ const t=(c.treatment||"").toLowerCase(); return t==="plays"||t==="bonus plays"||t==="home team discount"; });
            setCards(plays); setLoading(false); return;
          }
        }
      } catch(e) {}
      const snap = await getDocs(collection(db, "boba_checklist"));
      const all = snap.docs.map(d=>({...d.data(), id:d.id}));
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ cards: all, ts: Date.now() })); } catch(e) {}
      setCards(all.filter(c=>{ const t=(c.treatment||"").toLowerCase(); return t==="plays"||t==="bonus plays"||t==="home team discount"; }));
      setLoading(false);
    }
    load();
  }, []);

  const pbEntryIds = new Set(pbCards.map(e=>e.id));
  const playCount  = pbCards.filter(e=>e.type==="play").length;
  const bonusCount = pbCards.filter(e=>e.type==="bonus").length;
  const playFull   = playCount >= PUBLIC_PLAY_LIMIT;
  const pbResolved = pbCards.map(e=>({...e, card:cards.find(c=>c.id===e.id)})).filter(e=>e.card);
  const totalDbs   = pbResolved.reduce((s,e)=>s+(parseFloat(e.card.dbs)||0),0);
  const dbsLeft    = PUBLIC_DBS_CAP - totalDbs;
  const dbsPct     = Math.min(totalDbs/PUBLIC_DBS_CAP*100,100);
  const dbsOver    = totalDbs > PUBLIC_DBS_CAP;

  const isPlay  = c => { const t=(c.treatment||"").toLowerCase(); return t==="plays"||t==="home team discount"; };
  const isBonus = c => (c.treatment||"").toLowerCase()==="bonus plays";

  const available = cards.filter(c => {
    if (pbEntryIds.has(c.id)) return false;
    if (pbSearch && !`${c.hero} ${c.cardNum} ${c.playAbility||""}`.toLowerCase().includes(pbSearch.toLowerCase())) return false;
    return true;
  }).sort((a,b)=>{
    if (pbSort==="dbs_desc") return (parseFloat(b.dbs)||0)-(parseFloat(a.dbs)||0);
    if (pbSort==="dbs_asc")  return (parseFloat(a.dbs)||0)-(parseFloat(b.dbs)||0);
    return (a.hero||"").localeCompare(b.hero||"");
  });

  // Export all plays with their DBS values as a CSV (respects the current search filter).
  function exportPlaysDbs() {
    const rows = [["Play Name","Card #","Set","Weapon","Play Cost","DBS Value","Ability"]];
    available.forEach(c => {
      rows.push([
        c.hero || "",
        c.cardNum || "",
        c.setName || "",
        c.weapon || "",
        c.playCost ?? "",
        c.dbs ?? "",
        (c.playAbility || "").replace(/\s+/g, " ").trim(),
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `plays-dbs-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }

  const S = { inp:{ background:"var(--bz-s3)", border:"1px solid var(--bz-line)", borderRadius:8, color:"var(--bz-ink)", padding:"6px 10px", fontSize:12, fontFamily:"inherit", outline:"none", width:"100%" }, card:{ background:"linear-gradient(160deg,#181016,#130d11)", border:"1px solid var(--bz-line)", borderRadius:16, padding:"14px 16px", boxShadow:"0 1px 0 rgba(255,255,255,0.03) inset, 0 6px 22px rgba(0,0,0,0.35)" } };

  if (loading) return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"var(--bz-bg)", color:"#E8317A", fontSize:16, fontWeight:700 }}>Loading plays...</div>;

  return (
    <div style={{ background:"var(--bz-bg)", minHeight:"100vh", fontFamily:"'Trebuchet MS',sans-serif", color:"var(--bz-ink)", padding:20 }}>
      <div style={{ maxWidth:1400, margin:"0 auto" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
          <a href="/" style={{ color:"var(--bz-ink-3)", fontSize:12, textDecoration:"none" }}>{"\u2190 Back"}</a>
          <div style={{ fontSize:22, fontWeight:900, color:"#E8317A" }}>{"\uD83D\uDCD6 Playbook Builder"}</div>
          <span style={{ fontSize:12, color:playFull?"#E8317A":"#4ade80", fontWeight:700 }}>{playCount}/{PUBLIC_PLAY_LIMIT} plays</span>
          {bonusCount>0 && <span style={{ fontSize:12, color:"#7B9CFF", fontWeight:700 }}>· {bonusCount} BPL</span>}
          <div style={{ display:"flex", alignItems:"center", gap:8, marginLeft:"auto" }}>
            {!pbLoadId && pbCards.length>0 && (
              <span title="This isn't saved yet — hitting Save creates a new playbook and leaves any original untouched."
                style={{ fontSize:10.5, fontWeight:800, color:"#FBBF24", background:"rgba(251,191,36,0.12)", border:"1px solid rgba(251,191,36,0.35)", borderRadius:12, padding:"3px 9px", whiteSpace:"nowrap" }}>
                Unsaved — will save as new
              </span>
            )}
            <input value={pbName} onChange={e=>setPbName(e.target.value)} placeholder="Playbook name" style={{ ...S.inp, width:150, padding:"6px 10px", fontSize:12 }}/>
            <button onClick={savePlaybook} disabled={pbSaving||pbCards.length===0} style={{ background:pbSaved?"#0a2a0a":"#0a1a0a", border:`1px solid ${pbSaved?"#4ade80":"#4ade8044"}`, color:"#4ade80", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:700, cursor:pbCards.length===0?"not-allowed":"pointer", fontFamily:"inherit", opacity:pbCards.length===0?0.4:1, whiteSpace:"nowrap" }}>
              {pbSaving?"Saving...":pbSaved?"✓ Saved":"💾 Save"}</button>
            <button onClick={newPlaybook} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"7px 12px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>+ New</button>
          </div>
        </div>
        {savedPlaybooks.length > 0 && (
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14, marginTop:-6 }}>
            <span style={{ fontSize:11, color:"var(--bz-ink-3)", fontWeight:700, alignSelf:"center" }}>Saved:</span>
            {savedPlaybooks.map(p=>(
              <div key={p.id} style={{ display:"flex", alignItems:"center", gap:4, background:pbLoadId===p.id?"#1A1A2E":"var(--bz-s2)", border:`1px solid ${pbLoadId===p.id?"#7B9CFF":"var(--bz-line-2)"}`, borderRadius:8, padding:"4px 10px" }}>
                <button onClick={()=>loadPlaybook(p)} style={{ background:"none", border:"none", color:pbLoadId===p.id?"#7B9CFF":"#888", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{p.name} <span style={{ color:"var(--bz-ink-3)", fontWeight:400 }}>({(p.playCount||0)+(p.bonusCount||0)})</span></button>
                <button onClick={()=>duplicatePlaybook(p)} title="Duplicate — opens a copy you can edit and save separately" style={{ background:"none", border:"none", color:"#8a8a8a", cursor:"pointer", fontSize:12, lineHeight:1, padding:"0 3px" }}>⧉</button>
                <button onClick={()=>deletePlaybook(p.id)} title="Delete this playbook" style={{ background:"none", border:"none", color:"#8a8a8a", cursor:"pointer", fontSize:14, lineHeight:1, padding:"0 2px" }}>×</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) clamp(260px,28%,340px)", gap:14, alignItems:"start" }}>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              <input value={pbSearch} onChange={e=>setPbSearch(e.target.value)} placeholder="Search play name or ability..." style={{ ...S.inp, flex:1 }}/>
              <select value={pbSort} onChange={e=>setPbSort(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
                <option value="name">Sort: Name</option>
                <option value="dbs_desc">{"DBS: High \u2192 Low"}</option>
                <option value="dbs_asc">{"DBS: Low \u2192 High"}</option>
              </select>
              <span style={{ fontSize:11, color:"var(--bz-ink-3)", alignSelf:"center" }}>{available.length} plays</span>
              <button onClick={exportPlaysDbs} title="Export all plays with their DBS values to CSV"
                style={{ background:"linear-gradient(135deg,#A855F7,#7c3aed)", border:"none", color:"#fff", borderRadius:8, padding:"6px 12px", fontSize:11.5, fontWeight:800, cursor:"pointer", fontFamily:"inherit", alignSelf:"center", whiteSpace:"nowrap" }}>
                ⬇ Export DBS
              </button>
            </div>
            <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, overflow:"hidden", maxHeight:560, overflowY:"auto" }}>
              {available.map((c,i)=>{
                const wc = PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
                const wouldExceedPlay = (parseFloat(c.dbs)||0)>0 && totalDbs+(parseFloat(c.dbs)||0)>PUBLIC_DBS_CAP;
                const wouldExceedBpl  = wouldExceedPlay;
                return (
                  <div key={c.id} style={{ borderBottom:"1px solid #111", background:i%2===0?"var(--bz-bg)":"var(--bz-s1)" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px" }}>
                      {c.imageUrl && <img src={c.imageUrl} alt={c.hero} style={{ width:36, height:48, objectFit:"cover", borderRadius:4, flexShrink:0 }}/>}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)" }}>{c.hero}</div>
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center", marginBottom:2, fontSize:10 }}>
                          <span style={{ color:"var(--bz-ink-3)" }}>#{c.cardNum}</span>
                          {c.setName&&<span style={{ color:"#8a8a8a", fontStyle:"italic" }}>{c.setName}</span>}
                          {hasPlayCost(c)&&<span style={{ color:"#FBBF24", fontWeight:700 }}>Cost: {c.playCost}</span>}
                          {_pbAdmin
                            ? <span style={{ display:"inline-flex", alignItems:"center", gap:3, color:"#A855F7", fontWeight:700 }}>DBS:
                                <input
                                  type="number"
                                  value={dbsEdits[c.id]!==undefined ? dbsEdits[c.id] : (c.dbs!==undefined?c.dbs:"")}
                                  onChange={e=>setDbsEdits(p=>({...p,[c.id]:e.target.value}))}
                                  onBlur={e=>{ if(dbsEdits[c.id]!==undefined && String(dbsEdits[c.id])!==String(c.dbs??"")) savePbDbs(c.id, e.target.value); }}
                                  onClick={e=>e.stopPropagation()}
                                  style={{ width:46, background:"#1a1030", border:"1px solid #A855F755", color:"#C084FC", borderRadius:4, padding:"1px 4px", fontSize:10, fontFamily:"inherit", textAlign:"center" }}/>
                              </span>
                            : (c.dbs!==undefined&&<span style={{ color:"#A855F7", fontWeight:700 }}>DBS: {c.dbs}</span>)}
                          {c.weapon&&<span style={{ color:wc, fontWeight:700 }}>{c.weapon}</span>}
                        </div>
                        {c.playAbility&&<div style={{ fontSize:10, color:"var(--bz-ink-2)", fontStyle:"italic", lineHeight:1.4 }}>{c.playAbility}</div>}
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:4, flexShrink:0 }}>
                        {isPlay(c)&&<button onClick={()=>{ if(!playFull&&!wouldExceedPlay) setPbCards(p=>[...p,{id:c.id,type:"play"}]); }} disabled={playFull||wouldExceedPlay} title={wouldExceedPlay?"Would exceed DBS cap":playFull?"Play slots full":""} style={{ background:"var(--bz-s2)", border:"1px solid #E8317A44", color:(playFull||wouldExceedPlay)?"#333":"#E8317A", borderRadius:6, padding:"3px 8px", fontSize:10, fontWeight:700, cursor:(playFull||wouldExceedPlay)?"not-allowed":"pointer", fontFamily:"inherit" }}>+ Play</button>}
                        {isBonus(c)&&<button onClick={()=>{ if(!wouldExceedBpl) setPbCards(p=>[...p,{id:c.id,type:"bonus"}]); }} disabled={wouldExceedBpl} title={wouldExceedBpl?"Would exceed DBS cap":""} style={{ background:"#0a0f1a", border:"1px solid #7B9CFF44", color:wouldExceedBpl?"#333":"#7B9CFF", borderRadius:6, padding:"3px 8px", fontSize:10, fontWeight:700, cursor:wouldExceedBpl?"not-allowed":"pointer", fontFamily:"inherit" }}>+ BPL</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <div style={{ ...S.card }}>
              <div style={{ fontSize:12, fontWeight:800, color:"var(--bz-ink)", marginBottom:10 }}>{"\uD83D\uDCD6 Playbook"}</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                {[{l:"Plays",v:`${playCount}/${PUBLIC_PLAY_LIMIT}`,c:playFull?"#E8317A":"#4ade80"},{l:"Bonus Plays",v:bonusCount,c:"#7B9CFF"}].map(({l,v,c})=>(
                  <div key={l} style={{ background:"var(--bz-s1)", borderRadius:8, padding:"8px 10px", textAlign:"center" }}>
                    <div style={{ fontSize:20, fontWeight:900, color:c }}>{v}</div>
                    <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom:10 }}>
                <div style={{ height:6, background:"var(--bz-s2)", borderRadius:3, overflow:"hidden" }}>
                  <div style={{ width:`${Math.min(playCount/PUBLIC_PLAY_LIMIT*100,100)}%`, height:"100%", borderRadius:3, background:playFull?"#E8317A":"linear-gradient(90deg,#E8317A,#7B2FF7)", transition:"width 0.3s" }}/>
                </div>
                <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:4 }}>{PUBLIC_PLAY_LIMIT-playCount} play slots remaining</div>
              </div>
              <div style={{ background:dbsOver?"#1a0a0a":"var(--bz-bg)", border:`1px solid ${dbsOver?"#E8317A44":"var(--bz-line-2)"}`, borderRadius:8, padding:"10px 12px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                  <span style={{ fontSize:11, fontWeight:800, color:dbsOver?"#E8317A":"#A855F7" }}>{"\uD83D\uDCB0 DBS"}</span>
                  <span style={{ fontSize:11, fontWeight:700, color:dbsOver?"#E8317A":dbsPct>80?"#FBBF24":"#4ade80" }}>{Math.round(totalDbs)} / {PUBLIC_DBS_CAP}</span>
                </div>
                <div style={{ height:8, background:"var(--bz-s2)", borderRadius:4, overflow:"hidden", marginBottom:6 }}>
                  <div style={{ width:`${dbsPct}%`, height:"100%", borderRadius:4, background:dbsOver?"#E8317A":dbsPct>80?"linear-gradient(90deg,#FBBF24,#E8317A)":"linear-gradient(90deg,#A855F7,#7B9CFF)", transition:"width 0.3s" }}/>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:10 }}>
                  <span style={{ color:"var(--bz-ink-3)" }}>Used: {Math.round(totalDbs)}</span>
                  <span style={{ color:dbsOver?"#E8317A":dbsLeft<100?"#FBBF24":"#4ade80", fontWeight:700 }}>{dbsOver?`\u26A0\uFE0F Over by ${Math.round(totalDbs-PUBLIC_DBS_CAP)}`:`${Math.round(dbsLeft)} remaining`}</span>
                </div>
              </div>
            </div>
            {pbResolved.length>0 && (
              <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
                {pbResolved.filter(e=>e.type==="play").length>0&&<div><div style={{ padding:"10px 14px 6px", fontSize:10, fontWeight:700, color:"#E8317A", textTransform:"uppercase", letterSpacing:1 }}>{"\u2694\uFE0F Plays ("}{pbResolved.filter(e=>e.type==="play").length})</div>{pbResolved.filter(e=>e.type==="play").map((e,i)=>{ const c=e.card; return (<div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 14px", borderTop:"1px solid #111", background:i%2===0?"var(--bz-s1)":"var(--bz-bg)" }}><div style={{ fontSize:12, color:"var(--bz-ink-3)", width:18, textAlign:"center", flexShrink:0 }}>{i+1}</div>{c.imageUrl&&<img src={c.imageUrl} alt={c.hero} style={{ width:28, height:37, objectFit:"cover", borderRadius:3, flexShrink:0 }}/>}<div style={{ flex:1, minWidth:0 }}><div style={{ fontSize:12, fontWeight:800, color:"var(--bz-ink)" }}>{c.hero}</div><div style={{ display:"flex", gap:6, fontSize:10, marginTop:1 }}>{hasPlayCost(c)&&<span style={{ color:"#FBBF24" }}>Cost: {c.playCost}</span>}{c.dbs!==undefined&&<span style={{ color:"#A855F7" }}>DBS: {c.dbs}</span>}{c.setName&&<span style={{ color:"#8a8a8a",fontStyle:"italic" }}>{c.setName}</span>}</div></div><button onClick={()=>{ const arr=[...pbCards]; const idx=arr.findIndex((x,j)=>x.type==="play"&&j===pbCards.filter((y,k)=>k<=j&&y.type==="play").length-1+pbCards.slice(0,pbCards.findIndex((y,k)=>{ let pi=0; for(let l=0;l<k;l++) if(pbCards[l].type==="play") pi++; return pi===i&&pbCards[k].type==="play"; })).length-1); const playArr=pbCards.filter(x=>x.type==="play"); const target=playArr[i]; const gi=pbCards.indexOf(target); const a=[...pbCards]; a.splice(gi,1); setPbCards(a); }} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:14, padding:"2px 4px", flexShrink:0 }}>{"\u00D7"}</button></div>); })}</div>}
                {pbResolved.filter(e=>e.type==="bonus").length>0&&<div><div style={{ padding:"10px 14px 6px", fontSize:10, fontWeight:700, color:"#7B9CFF", textTransform:"uppercase", letterSpacing:1, borderTop:"1px solid var(--bz-line)" }}>{"\u2B50 Bonus Plays ("}{pbResolved.filter(e=>e.type==="bonus").length})</div>{pbResolved.filter(e=>e.type==="bonus").map((e,i)=>{ const c=e.card; return (<div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 14px", borderTop:"1px solid #111", background:i%2===0?"var(--bz-s1)":"var(--bz-bg)" }}><div style={{ fontSize:12, color:"var(--bz-ink-3)", width:18, flexShrink:0 }}>B{i+1}</div>{c.imageUrl&&<img src={c.imageUrl} alt={c.hero} style={{ width:28, height:37, objectFit:"cover", borderRadius:3, flexShrink:0 }}/>}<div style={{ flex:1, minWidth:0 }}><div style={{ fontSize:12, fontWeight:800, color:"#7B9CFF" }}>{c.hero}</div><div style={{ display:"flex", gap:6, fontSize:10, marginTop:1 }}>{hasPlayCost(c)&&<span style={{ color:"#FBBF24" }}>Cost: {c.playCost}</span>}{c.dbs!==undefined&&<span style={{ color:"#A855F7" }}>DBS: {c.dbs}</span>}{c.setName&&<span style={{ color:"#8a8a8a",fontStyle:"italic" }}>{c.setName}</span>}</div></div><button onClick={()=>{ const bonusArr=pbCards.filter(x=>x.type==="bonus"); const target=bonusArr[i]; const gi=pbCards.indexOf(target); const a=[...pbCards]; a.splice(gi,1); setPbCards(a); }} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:14, padding:"2px 4px", flexShrink:0 }}>{"\u00D7"}</button></div>); })}</div>}
                <div style={{ padding:"10px 14px" }}><button onClick={()=>{ if(window.confirm("Clear playbook?")) setPbCards([]); }} style={{ background:"transparent", border:"1px solid #E8317A22", color:"#E8317A", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", width:"100%" }}>{"\u2715 Clear"}</button></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// --- BOBA SHOWCASE (public, no auth required) --------------------
function BobaShowcase({ uid }) {
  const [cards,     setCards]     = useState([]);
  const [owned,     setOwned]     = useState({});
  const [loading,   setLoading]   = useState(true);
  const [spotlight, setSpotlight] = useState(null);
  const [filterSet, setFilterSet] = useState("");
  const [filterWeapon, setFilterWeapon] = useState("");
  const [sortBy,    setSortBy]    = useState("set");
  const [page,      setPage]      = useState(0);
  const [pageDir,   setPageDir]   = useState(1);
  const [copied,    setCopied]    = useState(false);
  const [ownerName, setOwnerName] = useState("");
  const [ownerSocials, setOwnerSocials] = useState({ discord:"", whatnot:"" });
  const [publicIds, setPublicIds] = useState({});
  const CARDS_PER_PAGE = 9;
  const ownedDocId = uid || "owned";

  useEffect(() => {
    async function load() {
      try {
        // 0. Owner name + private flags (for a personal, privacy-respecting profile)
        if (uid) {
          try {
            const [uSnap, pSnap] = await Promise.all([
              getDoc(doc(db,"users",uid)),
              getDoc(doc(db,"boba_public",uid)),
            ]);
            if (uSnap.exists()) { const d=uSnap.data(); setOwnerName(d.username ? `@${d.username}` : ((d.displayName||d.email||"").split(" ")[0]||"")); setOwnerSocials({ discord:d.discordName||"", whatnot:d.whatnotName||"" }); }
            if (pSnap.exists()) setPublicIds(pSnap.data()||{});
          } catch(e) {}
        }
        // 1. Load owned doc
        const ownedSnap = await getDoc(doc(db, "boba_owned", ownedDocId));
        const ownedData = ownedSnap.exists() ? ownedSnap.data() : {};
        setOwned(ownedData);
        const ownedIds = Object.keys(ownedData);
        if (ownedIds.length === 0) { setLoading(false); return; }

        // 2. Check localStorage cache for image cards (valid 30 min)
        const CACHE_KEY = "boba_showcase_cards_v2";
        let cachedCards = null;
        try {
          const raw = localStorage.getItem(CACHE_KEY);
          if (raw) {
            const { cards: cc, ts } = JSON.parse(raw);
            if (Date.now() - ts < 30 * 60 * 1000 && cc.length > 0) cachedCards = cc;
          }
        } catch(e) {}

        if (cachedCards) {
          setCards(cachedCards.filter(c => ownedData[c.id]));
          setLoading(false);
          // Refresh cache in background
          getDocs(collection(db, "boba_checklist")).then(snap => {
            const fresh = snap.docs.map(d => ({...d.data(), id:d.id})).filter(c => c.imageUrl);
            try { localStorage.setItem(CACHE_KEY, JSON.stringify({ cards: fresh, ts: Date.now() })); } catch(e) {}
          });
          return;
        }

        // 3. No cache -- fetch all, filter to image-only
        const cardSnap = await getDocs(collection(db, "boba_checklist"));
        const allImageCards = cardSnap.docs.map(d => ({...d.data(), id:d.id})).filter(c => c.imageUrl);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ cards: allImageCards, ts: Date.now() })); } catch(e) {}
        setCards(allImageCards.filter(c => ownedData[c.id]));
      } catch(e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, [ownedDocId]);

  const ownedCards = cards.filter(c => owned[c.id] && publicIds[c.id]);
  const sorted = [...ownedCards].sort((a, b) => {
    if (sortBy === "set") {
      const s = (a.setName||"").localeCompare(b.setName||"");
      return s !== 0 ? s : String(a.cardNum).localeCompare(String(b.cardNum), undefined, { numeric:true });
    }
    if (sortBy === "power") return (parseFloat(b.power)||0) - (parseFloat(a.power)||0);
    if (sortBy === "hero")  return (a.hero||"").localeCompare(b.hero||"");
    if (sortBy === "rarity") return getRarity(b).minPower - getRarity(a).minPower;
    return 0;
  });
  const sets    = [...new Set(ownedCards.map(c => c.setName).filter(Boolean))].sort();
  const weapons = [...new Set(ownedCards.map(c => c.weapon).filter(Boolean))].sort();
  let filtered = sorted;
  if (filterSet)    filtered = filtered.filter(c => c.setName === filterSet);
  if (filterWeapon) filtered = filtered.filter(c => c.weapon  === filterWeapon);
  const totalPages = Math.ceil(filtered.length / CARDS_PER_PAGE);
  const pageCards  = filtered.slice(page * CARDS_PER_PAGE, (page + 1) * CARDS_PER_PAGE);
  const totalPower     = ownedCards.reduce((s, c) => s + (parseFloat(c.power)||0), 0);
  const heroCount      = [...new Set(ownedCards.map(c=>c.hero).filter(Boolean))].length;
  const legendaryCount = ownedCards.filter(c => getRarity(c).label === "Legendary").length;

  useEffect(() => { setPage(0); }, [filterSet, filterWeapon, sortBy]);
  function goPage(next) { setPageDir(next > page ? 1 : -1); setPage(Math.max(0, Math.min(totalPages - 1, next))); }
  function copyLink() {
    const shareUrl = uid
      ? `${window.location.origin}/showcase?uid=${uid}`
      : `${window.location.origin}/showcase`;
    navigator.clipboard.writeText(shareUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  if (loading) return (
    <div style={{ minHeight:"100vh", background:"#000", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Trebuchet MS',sans-serif" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:16 }}>{"\uD83C\uDCCF"}</div>
        <div style={{ color:"#E8317A", fontWeight:700, fontSize:16 }}>Loading Showcase...</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:"#050505", fontFamily:"'Trebuchet MS','Segoe UI',sans-serif", color:"var(--bz-ink)" }}>
      <div style={{ background:"linear-gradient(180deg,var(--bz-s1) 0%,#050505 100%)", borderBottom:"1px solid var(--bz-line)", padding:"24px 32px" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"flex-start", gap:20, marginBottom:20, flexWrap:"wrap" }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:28, fontWeight:900, letterSpacing:-1, lineHeight:1 }}>
                <span style={{ color:"#E8317A" }}>{ownerName ? `${ownerName}'s` : "BAZOOKA"}</span>
                <span style={{ color:"var(--bz-ink)" }}> Collection</span>
              </div>
              <div style={{ fontSize:11, color:"#8a8a8a", marginTop:4 }}>Bo Jackson Battle Arena · Bazooka Breaks, LLC</div>
              {(ownerSocials.discord || ownerSocials.whatnot) && (
                <div style={{ display:"flex", gap:8, marginTop:10, flexWrap:"wrap" }}>
                  {ownerSocials.discord && <span style={{ display:"inline-flex", alignItems:"center", gap:5, background:"rgba(88,101,242,0.12)", border:"1px solid rgba(88,101,242,0.35)", color:"#8b9cff", borderRadius:8, padding:"4px 10px", fontSize:12, fontWeight:700 }}>💬 {ownerSocials.discord}</span>}
                  {ownerSocials.whatnot && <a href={`https://www.whatnot.com/user/${ownerSocials.whatnot.replace(/^@/,"")}`} target="_blank" rel="noopener noreferrer" style={{ display:"inline-flex", alignItems:"center", gap:5, background:"rgba(251,191,36,0.1)", border:"1px solid rgba(251,191,36,0.35)", color:"#FBBF24", borderRadius:8, padding:"4px 10px", fontSize:12, fontWeight:700, textDecoration:"none" }}>🛍️ {ownerSocials.whatnot}</a>}
                </div>
              )}
            </div>
            <button onClick={copyLink} style={{ background: copied ? "#0a1a0a" : "var(--bz-s2)", border:`1px solid ${copied?"#4ade80":"var(--bz-line-2)"}`, color: copied ? "#4ade80" : "#888", borderRadius:10, padding:"8px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:6, transition:"all 0.2s" }}>
              {copied ? "\u2705 Copied!" : "\uD83D\uDD17 Share Link"}
            </button>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:12, marginBottom:20 }}>
            {[
              { label:"Cards Owned",  value:ownedCards.length.toLocaleString(), color:"#4ade80",  icon:"\uD83C\uDCCF" },
              { label:"Heroes",       value:heroCount,                           color:"#7B9CFF",  icon:"\u26A1" },
              { label:"Sets",         value:sets.length,                         color:"#FBBF24",  icon:"\uD83D\uDCE6" },
              { label:"Legendaries",  value:legendaryCount,                      color:"#FBBF24",  icon:"\uD83D\uDC51" },
              { label:"Power Score",  value:Math.round(totalPower).toLocaleString(), color:"#E8317A", icon:"\uD83D\uDCA5" },
            ].map(({ label, value, color, icon }) => (
              <div key={label} style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"12px 14px" }}>
                <div style={{ fontSize:10, color:"#8a8a8a", marginBottom:4 }}>{icon} {label}</div>
                <div style={{ fontSize:20, fontWeight:900, color }}>{value}</div>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
            <select value={filterSet} onChange={e=>{setFilterSet(e.target.value);}} style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              <option value="">All Sets</option>
              {sets.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filterWeapon} onChange={e=>setFilterWeapon(e.target.value)} style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              <option value="">All Weapons</option>
              {weapons.map(w => <option key={w} value={w} style={{ color: SHOWCASE_WEAPON_COLORS[w]||"#888" }}>{w}</option>)}
            </select>
            <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"5px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              <option value="set">Sort: Set</option>
              <option value="power">{"Sort: Power \u2193"}</option>
              <option value="rarity">Sort: Rarity</option>
              <option value="hero">{"Sort: Hero A\u2192Z"}</option>
            </select>
            <div style={{ flex:1 }}/>
            <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{filtered.length} cards · {totalPages} pages</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:1200, margin:"0 auto", padding:"32px" }}>
        {ownedCards.length === 0 ? (
          <div style={{ textAlign:"center", padding:"100px 0", color:"var(--bz-ink-3)" }}>
            <div style={{ fontSize:56, marginBottom:20 }}>{"\uD83C\uDCCF"}</div>
            <div style={{ fontSize:18, fontWeight:900, color:"var(--bz-ink-3)" }}>No public cards yet</div>
            <div style={{ fontSize:13, marginTop:8, color:"var(--bz-ink-3)" }}>Collections are private by default. Tap the 👁 icon on any owned card in the Card Database to make it public and show it here.</div>
          </div>
        ) : (
          <>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
              <button onClick={()=>goPage(page-1)} disabled={page===0}
                style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:page===0?"#222":"#888", borderRadius:8, padding:"8px 20px", fontSize:13, fontWeight:700, cursor:page===0?"default":"pointer", fontFamily:"inherit" }}>
                {"\u2190 Prev"}</button>
              <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                {Array.from({ length: Math.min(totalPages, 9) }).map((_, i) => {
                  const pIdx = totalPages <= 9 ? i : Math.max(0, Math.min(totalPages - 9, page - 4)) + i;
                  return (
                    <button key={pIdx} onClick={()=>goPage(pIdx)}
                      style={{ background:page===pIdx?"#E8317A":"transparent", color:page===pIdx?"#fff":"#444", border:`1px solid ${page===pIdx?"#E8317A":"var(--bz-line-2)"}`, borderRadius:6, width:30, height:30, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                      {pIdx+1}
                    </button>
                  );
                })}
                {totalPages > 9 && <span style={{ color:"var(--bz-ink-3)", fontSize:11 }}>{"\u2026"}{totalPages}</span>}
              </div>
              <button onClick={()=>goPage(page+1)} disabled={page>=totalPages-1}
                style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:page>=totalPages-1?"#222":"#888", borderRadius:8, padding:"8px 20px", fontSize:13, fontWeight:700, cursor:page>=totalPages-1?"default":"pointer", fontFamily:"inherit" }}>
                {"Next \u2192"}</button>
            </div>

            <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:16, padding:"28px", boxShadow:"0 24px 80px rgba(0,0,0,0.7)" }}>
              {pageCards[0] && (
                <div style={{ fontSize:11, color:"var(--bz-ink-3)", fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:20 }}>
                  {pageCards[0].setName} -- Page {page+1} of {totalPages}
                </div>
              )}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20 }}>
                {pageCards.map(c => (
                  <ShowcaseCard key={c.id} c={c} onClick={()=>setSpotlight(c)} />
                ))}
                {Array.from({ length: CARDS_PER_PAGE - pageCards.length }).map((_,i) => (
                  <div key={`empty-${i}`} style={{ aspectRatio:"3/4", border:"1px dashed #111", borderRadius:12, background:"#080808" }}/>
                ))}
              </div>
            </div>

            <div style={{ display:"flex", gap:16, marginTop:16, justifyContent:"center", flexWrap:"wrap" }}>
              {RARITY_TIERS.map(r => (
                <div key={r.label} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"#8a8a8a" }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:r.color }}/>
                  <span style={{ color:r.color, fontWeight:700 }}>{r.label}</span>
                  <span>({r.minPower === 0 ? "<130" : `${r.minPower}+`} power)</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {spotlight && (
        <div onClick={()=>setSpotlight(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.95)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
          <div onClick={e=>e.stopPropagation()} style={{ display:"flex", gap:48, alignItems:"center", maxWidth:900, width:"100%", flexWrap:"wrap" }}>
            <div style={{ width:280, flexShrink:0 }}>
              <ShowcaseCard c={spotlight} onClick={()=>{}} large />
            </div>
            <div style={{ flex:1, minWidth:260 }}>
              <div style={{ fontSize:11, color:"#8a8a8a", marginBottom:6, letterSpacing:1, textTransform:"uppercase" }}>
                {spotlight.setName} · #{spotlight.cardNum}
              </div>
              <div style={{ fontSize:38, fontWeight:900, color:"var(--bz-ink)", lineHeight:1.1, marginBottom:12 }}>{spotlight.hero}</div>
              {(() => { const r = getRarity(spotlight); return (
                <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:r.color+"22", border:`1px solid ${r.color}44`, borderRadius:20, padding:"4px 12px", marginBottom:14 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:r.color }}/>
                  <span style={{ fontSize:11, color:r.color, fontWeight:700 }}>{r.label}</span>
                </div>
              ); })()}
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:20 }}>
                {spotlight.weapon    && <span style={{ fontSize:12, color:SHOWCASE_WEAPON_COLORS[spotlight.weapon]||"#888", background:(SHOWCASE_WEAPON_COLORS[spotlight.weapon]||"#888")+"22", borderRadius:6, padding:"3px 10px", fontWeight:700 }}>{spotlight.weapon}</span>}
                {spotlight.treatment && <span style={{ fontSize:12, color:"var(--bz-ink-2)", background:"var(--bz-s2)", borderRadius:6, padding:"3px 10px" }}>{spotlight.treatment}</span>}
                {spotlight.notation  && <span style={{ fontSize:12, color:"#FBBF24", background:"#FBBF2422", borderRadius:6, padding:"3px 10px", fontWeight:700 }}>{spotlight.notation}</span>}
              </div>
              {spotlight.power && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:10, color:"#8a8a8a", marginBottom:4, letterSpacing:1 }}>POWER</div>
                  <div style={{ fontSize:56, fontWeight:900, color:SHOWCASE_WEAPON_COLORS[spotlight.weapon]||"#E8317A", lineHeight:1 }}>{spotlight.power}</div>
                </div>
              )}
              {spotlight.athlete && <div style={{ fontSize:13, color:"var(--bz-ink-3)", marginBottom:6 }}>{"\uD83C\uDFC5 Inspired by"}{spotlight.athlete}</div>}
              {spotlight.variation && <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginBottom:16 }}>{spotlight.variation}</div>}
              <div style={{ display:"flex", gap:10, marginTop:8 }}>
                <button onClick={()=>{ const i=filtered.indexOf(spotlight); if(i>0) setSpotlight(filtered[i-1]); }} disabled={filtered.indexOf(spotlight)===0}
                  style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"8px 14px", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>{"\u2190 Prev"}</button>
                <button onClick={()=>{ const i=filtered.indexOf(spotlight); if(i<filtered.length-1) setSpotlight(filtered[i+1]); }} disabled={filtered.indexOf(spotlight)===filtered.length-1}
                  style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"8px 14px", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>{"Next \u2192"}</button>
                <button onClick={()=>setSpotlight(null)}
                  style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:8, padding:"8px 16px", fontSize:12, cursor:"pointer", fontFamily:"inherit", marginLeft:"auto" }}>{"\u2715 Close"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ShowcaseCard({ c, onClick, large }) {
  const wc = SHOWCASE_WEAPON_COLORS[canonWeapon(c.weapon)] || "#444";
  const rarity = getRarity(c);
  const cardRef  = useRef(null);
  const foilRef  = useRef(null);
  const glareRef = useRef(null);
  const animRef  = useRef(null);
  const currentTilt = useRef({ x:0, y:0 });
  const targetTilt  = useRef({ x:0, y:0 });
  const isHovering  = useRef(false);

  function startAnimation() { if (!animRef.current) animRef.current = requestAnimationFrame(animate); }
  function onMouseMove(e) {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX-rect.left)/rect.width, y = (e.clientY-rect.top)/rect.height;
    targetTilt.current = { x:(y-0.5)*28, y:(x-0.5)*-28 };
    if (foilRef.current) { foilRef.current.style.setProperty("--foilpos",`${x*100}% ${y*100}%`); foilRef.current.style.opacity="1"; }
    if (glareRef.current) { glareRef.current.style.background=`radial-gradient(ellipse at ${x*100}% ${y*100}%, rgba(255,255,255,0.22) 0%, transparent 60%)`; glareRef.current.style.opacity="1"; }
    startAnimation();
  }
  function onMouseLeave() {
    isHovering.current=false; targetTilt.current={x:0,y:0};
    if (foilRef.current) foilRef.current.style.opacity="0";
    if (glareRef.current) glareRef.current.style.opacity="0";
    startAnimation();
  }
  function onMouseEnter() { isHovering.current=true; startAnimation(); }
  function animate() {
    const cur=currentTilt.current, tgt=targetTilt.current;
    cur.x+=(tgt.x-cur.x)*0.1; cur.y+=(tgt.y-cur.y)*0.1;
    if (cardRef.current) cardRef.current.style.transform=`perspective(600px) rotateX(${cur.x}deg) rotateY(${cur.y}deg) scale3d(${large?1.02:1.05},${large?1.02:1.05},${large?1.02:1.05})`;
    if (Math.abs(tgt.x-cur.x)>0.05||Math.abs(tgt.y-cur.y)>0.05||isHovering.current) { animRef.current=requestAnimationFrame(animate); }
    else { animRef.current=null; cur.x=0; cur.y=0; if(cardRef.current) cardRef.current.style.transform=""; }
  }

  return (
    <div style={{ aspectRatio:"3/4", cursor:onClick?"pointer":"default" }}
      onMouseMove={onMouseMove} onMouseLeave={onMouseLeave} onMouseEnter={onMouseEnter} onClick={onClick}>
      <div ref={cardRef} style={{ width:"100%", height:"100%", borderRadius:large?16:12, overflow:"hidden", position:"relative", willChange:"transform",
        boxShadow:`0 8px 32px rgba(0,0,0,0.7), 0 0 0 1px ${rarity.color}22` }}>
        <img src={c.imageUrl} alt={c.hero} loading="lazy" decoding="async" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
        <div ref={foilRef} style={{ position:"absolute", inset:0,
          background:"linear-gradient(115deg, transparent 20%, rgba(255,255,255,0.14) 30%, rgba(255,220,100,0.22) 40%, rgba(100,200,255,0.24) 50%, rgba(200,100,255,0.20) 60%, rgba(255,100,150,0.18) 70%, transparent 80%)",
          backgroundSize:"200% 200%", mixBlendMode:"screen", opacity:0, transition:"opacity 0.2s", pointerEvents:"none" }}/>
        <div ref={glareRef} style={{ position:"absolute", inset:0, mixBlendMode:"overlay", opacity:0, transition:"opacity 0.2s", pointerEvents:"none" }}/>
        <div style={{ position:"absolute", inset:0, borderRadius:large?16:12, boxShadow:`inset 0 0 ${large?30:20}px ${rarity.color}18`, pointerEvents:"none" }}/>
        <div style={{ position:"absolute", bottom:0, left:0, right:0, background:"linear-gradient(transparent, rgba(0,0,0,0.9))", padding:large?"36px 18px 16px":"24px 12px 10px" }}>
          <div style={{ fontSize:large?15:12, fontWeight:900, color:"var(--bz-ink)", lineHeight:1.2 }}>{c.hero}</div>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:3 }}>
            {c.weapon && <span style={{ fontSize:large?12:10, color:wc, fontWeight:700 }}>{c.weapon}</span>}
            {c.power  && <span style={{ fontSize:large?12:10, color:"var(--bz-ink-3)" }}>· {c.power}</span>}
            <span style={{ fontSize:large?11:9, color:rarity.color, marginLeft:"auto", fontWeight:700 }}>{rarity.label}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- PUBLIC QUOTE PAGE (no auth required) --------------------
// --- BOBA SHOWCASE (public, no auth required) ----------------
const SHOWCASE_WEAPON_COLORS = { Fire:"#F97316",Ice:"#60A5FA",Steel:"var(--bz-ink-2)",Brawl:"#E8317A",Glow:"#4ade80",Hex:"#A855F7",Gum:"#FBBF24",Super:"#F472B6",Alt:"var(--bz-ink-2)",Metallic:"var(--bz-line)" };
const RARITY_TIERS = [
  { label:"Legendary", minPower:200, color:"#FBBF24" },
  { label:"Elite",     minPower:160, color:"#A855F7" },
  { label:"Rare",      minPower:130, color:"#60A5FA" },
  { label:"Common",    minPower:0,   color:"var(--bz-ink-2)" },
];
function getRarity(c) {
  const p = parseFloat(c.power) || 0;
  return RARITY_TIERS.find(r => p >= r.minPower) || RARITY_TIERS[3];
}

let ATHLETE_SPORT = {
  // Baseball
  "Aaron Judge":"MLB","Adrian Beltr\u00E9":"MLB","Adri\u00E1n Beltr\u00E9":"MLB","Brent Rooker":"MLB",
  "Cal Raleigh":"MLB","Carlton Fisk":"MLB","Craig Biggio":"MLB","David Ortiz":"MLB",
  "Dennis Eckersley":"MLB","Edgar Martinez":"MLB","Elly De La Cruz":"MLB","Evan Carter":"MLB",
  "Fernando Tatis Jr.":"MLB","F\u00E9lix Hern\u00E1ndez":"MLB","George Bell":"MLB",
  "Gunnar Henderson":"MLB","Jack Morris":"MLB","Jackson Holliday":"MLB","Jeff Bagwell":"MLB",
  "Jim Rice":"MLB","John Smoltz":"MLB","Jung Hoo Lee":"MLB","Junior Caminero":"MLB",
  "Ken Griffey Jr.":"MLB","Ken Griffey Sr.":"MLB","Ketel Marte":"MLB","Kyle Tucker":"MLB",
  "Manny Machado":"MLB","Mariano Rivera":"MLB","Miguel Cabrera":"MLB","Nick Kurtz":"MLB",
  "Ozzie Albies":"MLB","Pedro Mart\u00EDnez":"MLB","Rafael Devers":"MLB","Reggie Jackson":"MLB",
  "Rick Sutcliffe":"MLB","Robin Yount":"MLB","Roger Clemens":"MLB","Rollie Fingers":"MLB",
  "Roman Anthony":"MLB","Scott Rolen":"MLB","Steve Garvey":"MLB","Tarik Skubal":"MLB",
  "Tim Raines":"MLB","Todd Helton":"MLB","Tom Glavine":"MLB","Vladimir Guerrero Sr.":"MLB",
  "William Contreras":"MLB","Wyatt Langford":"MLB","\u00C9ric Gagn\u00E9":"MLB","James Wood":"MLB",
  "Jac Caglianone":"MLB","Kristian Campbell":"MLB",
  // Football (NFL)
  "AJ Brown":"NFL","Aaron Rodgers":"NFL","Adrian Peterson":"NFL","Ashton Jeanty":"NFL",
  "Bo Jackson":"NFL/MLB","Brock Bowers":"NFL","Caleb Williams":"NFL","Cam Ward":"NFL",
  "Courtland Sutton":"NFL","DK Metcalf":"NFL","Dante Hall":"NFL","Donnie Shell":"NFL",
  "Doug Plank":"NFL","Drake Baldwin":"NFL","Jack Ham":"NFL","Jaxson Dart":"NFL",
  "Jordan Love":"NFL","Julius Peppers":"NFL","Justin Fields":"NFL",
  "Kenneth Walker lll":"NFL","Kenneth Walker III":"NFL","Matthew Golden":"NFL",
  "Pierre Gar\u00E7on":"NFL","Ron Jaworski":"NFL","Santana Moss":"NFL","Steve Spurrier":"NFL/NCAA",
  "TJ Watt":"NFL","Terry McLaurin":"NFL","Travis Hunter":"NFL","Troy Aikman":"NFL",
  "Bijon Robinson":"NFL","Collin Murray- Boyles":"NFL","Collin Murray-Boyles":"NFL",
  // Basketball (NBA)
  "Cade Cunningham":"NBA","Cam Thomas":"NBA","Chauncey Billups":"NBA",
  "Chris Mullin":"NBA","Cooper Flagg":"NBA","DeMarr Derozen":"NBA","DeMar DeRozan":"NBA",
  "Dominique Wilkins":"NBA","Dylan Harper":"NBA","Giannis Antetokounmpo":"NBA",
  "Jalen Brunson":"NBA","Jamal Murray":"NBA","John Starks":"NBA","Julius Erving":"NBA/ABA",
  "Kawhi Leonard":"NBA","Lauri Markkanen":"NBA","Patrick Ewing":"NBA","Paulo Banchero":"NBA",
  "Sam Perkins":"NBA","Stephon Castle":"NBA","Tyrese Haliburton":"NBA","Tyrese Maxey":"NBA",
  "Tyler Herro":"NBA","V.J. Edgecombe":"NBA","Victor Wembanyama":"NBA",
  "Alex Antetokounmpo":"NBA","Kostas Antetokounmpo":"NBA","Thanasis Antetokounmpo":"NBA",
  "Ace Bailey":"NBA",
  // WNBA
  "Alysha Clark":"WNBA","Cheryl Miller":"WNBA","Elena Delle Donne":"WNBA",
  "Paige Bueckers":"WNBA",
  // Hockey (NHL)
  "Alexander Ovechkin":"NHL","Henrik Lundqvist":"NHL","Sidney Crosby":"NHL",
  // Golf
  "Bryson DeChambeau":"PGA","Jordan Spieth":"PGA",
  // Soccer
  "Christie Pearce Rampone":"USWNT","Jozy Altidore":"MLS/USMNT",
  // Tennis
  "Jessica Pegula":"WTA","Paula Badosa":"WTA",
  // Boxing / MMA
  "Muhammed Ali":"Boxing","Muhammad Ali":"Boxing",
  // Celebrity / Other
  "Flavor Flav":"Music","Randy Quaid":"Acting",
  "Jim Boeheim":"NCAA Basketball",
};

function athleteSport(name) {
  if (!name) return null;
  return ATHLETE_SPORT[name.trim()] || ATHLETE_SPORT[name] || null;
}

function BobaCardImpl({ c, isOwned, isBorrowed=false, ownedQty, flippedCard, setFlippedCard, toggleOwned, setOwnedQty, toggleWant, wantList, WEAPON_COLORS, isAdmin, onDelete, onComp, onImageUpload, onImageClear, onLotEdit, lotCount=0, onCardActivity, onExpand, myScanPhoto, cardWidthHint=200, kidGroups=[], kidTags=null, onAssignKid, onSell, foundInMap={}, lentCount=0, lentWho="", onLend, onReturn }) {
  const wc = WEAPON_COLORS[canonWeapon(c.weapon)] || "#444";
  // Image priority: official admin imageUrl → my own private scan photo → coming-soon placeholder.
  // Foil/shine overlays only apply to the official art, not to a raw scan photo.
  const _officialImg = c.imageUrl;
  const _displayImg = _officialImg || myScanPhoto || null;
  const _isScanImg = !_officialImg && !!myScanPhoto;
  const isFlipped = flippedCard === c.id;
  const [bioOpen, setBioOpen] = useState(false);
  const _canHover = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(hover: hover)").matches;
  const qty = ownedQty || 0;
  const isWanted = !!(wantList && wantList[c.id]);
  const cardRef = useRef(null);
  // PERF: this used to create a ResizeObserver PER CARD and call setCardW() on mount, forcing a
  // second render of every tile in the grid. With ~100 tiles that's 100 observers and 100 extra
  // renders every time the list changes (i.e. on every search). The card width only matters for
  // one thing — hiding some detail on narrow tiles — so derive it from the grid density instead
  // of measuring each card individually.
  const isSmallCard = cardWidthHint < 165;
  const foilRef = useRef(null);
  const glareRef = useRef(null);
  const animRef = useRef(null);
  const currentTilt = useRef({ x:0, y:0 });
  const targetTilt  = useRef({ x:0, y:0 });
  const isHovering  = useRef(false);

  // No shine for paper (non-foil base) cards and Plays (but Bonus Plays still shine)
  const treatment = (c.treatment||"").toLowerCase();
  const cardType  = (c.cardType||c.variation||"").toLowerCase();
  const noShine = treatment === "base" ||
                  treatment === "paper" ||
                  treatment === "paper plays" ||
                  treatment === "play" ||
                  treatment === "plays" ||
                  treatment === "hot dog" ||
                  treatment.includes("hot dog") ||
                  (treatment.includes("play") && !treatment.includes("bonus")) ||
                  (String(c.cardNum||"").toUpperCase().startsWith("PL") && !String(c.cardNum||"").toUpperCase().startsWith("BPL"));

  // Pixel/cyber sparkle foil for Helmet Icon cards
  const isPixelFoil    = treatment.includes("helmet icon");
  const isMetallicFoil = treatment.includes("metallic") || treatment.includes("inspired ink metallic");
  // Cracked-ice / shattered-glass foil for Alpha Blast set cards
  const _setLower = (c.setName||"").toLowerCase();
  const _iceIsPaper = /\b(paper|base)\b/.test(treatment) && !treatment.includes("battlefoil") && !treatment.includes("foil");
  const isIceFoil = (_setLower.includes("alpha blast") || _setLower.includes("blast")) && !_iceIsPaper;
  // Base / paper cards have NO foil shine — only true foil treatments shimmer
  const _isPaperBase = /\b(paper|base)\b/.test(treatment) && !treatment.includes("battlefoil") && !treatment.includes("foil");
  // "bonus play" is named for what the card does, not what it is made of, so it matched none of
  // the material words below even though the insert is foil stock like the battlefoils.
  const isFoilTreatment = !_isPaperBase && /foil|linoleum|metallic|holo|prizm|refractor|chrome|sparkle|shimmer|rainbow|gold|silver|inspired ink|battlefoil|bonus play/.test(treatment);
  // Drag an image file straight onto the card to set its art. Admin-only, same as the button.
  const [dragOver, setDragOver] = useState(false);
  // True whenever a FILE is being dragged anywhere over the window. Used to arm the per-card drop
  // layers only during a drag, so they never interfere with normal mouse use.
  const [dragActive, setDragActive] = useState(false);
  const [dropBusy, setDropBusy] = useState(false);  // fetching a dragged-from-web image

  // One shared drop handler. The card has a drop layer over the artwork, but a card with NO image
  // shows a placeholder with its own "Add Image" label that sits above that layer and stops event
  // propagation \u2014 so blank cards, the exact ones you are trying to fill, never received the drop.
  // Both attach this.
  async function acceptDroppedImage(e){
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    const dt = e.dataTransfer;

    // A real file from disk.
    const f = [...(dt?.files||[])].find(x=>x.type.startsWith("image/"));
    if(f){ onImageUpload(c, f); return; }

    // Dragged out of another tab: only a URL is transferred, since the image is on a remote server.
    const uri = (dt?.getData?.("text/uri-list") || dt?.getData?.("text/plain") || "").trim();
    const url = uri.split(/[\r\n]/).find(l=>/^https?:\/\//i.test(l));
    if(!url){ alert("That drop carried no image and no link. Save the picture first, then drag it from your computer."); return; }
    try {
      setDropBusy(true);
      let blob = null;
      try {
        const r = await fetch(url, { mode:"cors" });
        if(r.ok){ const b = await r.blob(); if(b.type.startsWith("image/")) blob = b; }
      } catch(_) {}
      if(!blob){
        const r2 = await fetch("/api/fetch-image", {
          method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({ url }),
        });
        if(r2.ok){ const b2 = await r2.blob(); if(b2.type.startsWith("image/")) blob = b2; }
        else { let m=""; try{ m=(await r2.json())?.error||""; }catch(_){} throw new Error(m || `server said ${r2.status}`); }
      }
      if(!blob) throw new Error("could not read that image");
      const name = (url.split("/").pop()||"dropped").split("?")[0] || "dropped.jpg";
      onImageUpload(c, new File([blob], name, { type: blob.type || "image/jpeg" }));
    } catch(err) {
      alert("Couldn't pull that image from the web.\n\n" + (err?.message || err) +
            "\n\nIf this keeps happening, save the picture and drag it from your computer.");
    } finally { setDropBusy(false); }
  }
  const dropProps = (isAdmin && onImageUpload) ? {
    onDragOver: e=>{ e.preventDefault(); e.stopPropagation(); if(!dragOver) setDragOver(true); },
    onDragLeave: e=>{ if(e.currentTarget.contains(e.relatedTarget)) return; setDragOver(false); },
    onDrop: acceptDroppedImage,
  } : {};
  useEffect(()=>{
    if(!(isAdmin && onImageUpload)) return;
    let depth = 0;
    const isFile = e => [...(e.dataTransfer?.types||[])].includes("Files");
    const onEnter = e => { if(!isFile(e)) return; depth++; setDragActive(true); };
    const onLeave = e => { if(!isFile(e)) return; depth = Math.max(0, depth-1); if(depth===0) setDragActive(false); };
    const onDrop  = () => { depth = 0; setDragActive(false); };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDrop);
    };
  },[isAdmin, onImageUpload]);
  const pixelRef = useRef(null);
  const pixelAnim = useRef(null);
  const mousePos = useRef({ x:0.5, y:0.5 });

  const pixelCanvasRef = useRef(null);
  const pixelAnimRef = useRef(null);
  const metallicRef = useRef(null);
  const iceRef = useRef(null);
  const iceShardsRef = useRef(null); // cached shard SVG data URL

  // Build a dense field of iridescent angular shards once (cached), matching the cracked-ice look.
  // Cracked-ice foil: layered angular facet gradients (no SVG dependency) + icy sheen.
  // How the foil looks when nobody's hovering. Without this the card would be flat until you mouse
  // over it, and most cards in a grid are never hovered at all.
  function _iceRest() {
    return [
      `linear-gradient(115deg,
         transparent 30%,
         hsla(190, 90%, 92%, 0.16) 43%,
         hsla(280, 95%, 95%, 0.34) 50%,
         hsla(45, 90%, 92%, 0.16) 57%,
         transparent 70%)`,
      `linear-gradient(95deg,
         hsla(200, 85%, 70%, 0.10),
         hsla(280, 90%, 78%, 0.13) 35%,
         hsla(20, 90%, 78%, 0.13) 65%,
         hsla(120, 85%, 70%, 0.10))`,
    ].join(", ");
  }
  // Prismatic sheen for Alpha Blast cards.
  //
  // The previous effect stacked four repeating-linear-gradients at 63/112/158/9 degrees to fake
  // "cracked ice". Repeating gradients are inherently REGULAR, though, and real shattered glass is
  // irregular — so what it actually produced was a fine cross-hatch that read as moiré on a small
  // card tile, not as ice. CSS can't really do irregular facets; that wants an SVG or a texture.
  //
  // So instead: a clean prismatic sweep that tracks the cursor. Fewer layers, no texture, and it's
  // the look people already read as "premium card" — refractor, prizm, that family.
  function drawIceFoil(x, y) {
    if (!iceRef.current) return;
    const el = iceRef.current;

    // Hue rotates as the cursor moves across the card, so tilting it walks the rainbow.
    const hue = (x * 300 + y * 60) % 360;
    // The sheen band travels with the cursor; the angle leans slightly so it doesn't feel static.
    const band  = 20 + (x * 0.65 + y * 0.35) * 60;   // 20% .. 80%
    const angle = 100 + x * 40;

    el.style.backgroundImage = [
      // The bright specular band — the thing your eye actually follows.
      `linear-gradient(${angle}deg,
         transparent ${band - 26}%,
         hsla(${(hue + 300) % 360}, 90%, 88%, 0.20) ${band - 12}%,
         hsla(${hue}, 100%, 96%, 0.60) ${band}%,
         hsla(${(hue + 60) % 360}, 90%, 88%, 0.20) ${band + 12}%,
         transparent ${band + 26}%)`,
      // A wide, soft rainbow wash underneath so the whole card shifts colour, not just the band.
      `linear-gradient(${angle - 20}deg,
         hsla(${(hue + 200) % 360}, 85%, 70%, 0.13),
         hsla(${(hue + 280) % 360}, 90%, 78%, 0.16) 35%,
         hsla(${(hue + 20) % 360}, 90%, 78%, 0.16) 65%,
         hsla(${(hue + 120) % 360}, 85%, 70%, 0.13))`,
      // A gentle bloom under the cursor, so the card feels lit from where you're pointing.
      `radial-gradient(circle at ${(x*100).toFixed(0)}% ${(y*100).toFixed(0)}%,
         rgba(255,255,255,0.22), transparent 55%)`,
    ].join(", ");
    el.style.opacity = "1";
  }

  function drawMetallicFoil(x, y) {
    if (!metallicRef.current) return;
    const el = metallicRef.current;
    // Horizontal bands of light that shift with mouse Y
    // Rainbow color shift based on mouse X
    const hue  = (x * 240) % 360;
    const hue2 = (hue + 40) % 360;
    const band = y * 100; // band position as %
    el.style.background = [
      // Main bright horizontal sweep band
      `linear-gradient(to bottom,
        transparent ${Math.max(0,band-30)}%,
        hsla(${hue},60%,95%,0.15) ${Math.max(0,band-15)}%,
        hsla(${hue2},80%,98%,0.55) ${band}%,
        hsla(${hue},60%,95%,0.15) ${Math.min(100,band+15)}%,
        transparent ${Math.min(100,band+30)}%)`,
      // Subtle horizontal stripe texture
      `repeating-linear-gradient(to bottom,
        transparent 0px,
        rgba(255,255,255,0.04) 1px,
        transparent 2px,
        rgba(255,255,255,0.02) 3px,
        transparent 4px)`,
      // Color wash based on X
      `linear-gradient(to right,
        hsla(${(hue+180)%360},70%,60%,0.12),
        hsla(${hue},80%,80%,0.18) ${x*100}%,
        hsla(${hue2},70%,60%,0.12))`,
    ].join(", ");
    el.style.opacity = "1";
    el.style.mixBlendMode = "screen";
  }

  function drawPixelFoil(x, y) {
    if (!pixelRef.current || !isPixelFoil) return;
    const el = pixelRef.current;
    const hue  = (x * 180 + y * 90 + Date.now() * 0.05) % 360;
    const hue2 = (hue + 80) % 360;
    const hue3 = (hue + 160) % 360;
    el.style.background = [
      `radial-gradient(ellipse 60% 40% at ${x*100}% ${y*100}%, hsla(${hue},100%,75%,0.5) 0%, transparent 70%)`,
      `radial-gradient(ellipse 50% 60% at ${(1-x)*100}% ${y*100}%, hsla(${hue2},100%,70%,0.4) 0%, transparent 65%)`,
      `radial-gradient(ellipse 40% 50% at ${x*100}% ${(1-y)*100}%, hsla(${hue3},100%,75%,0.35) 0%, transparent 60%)`,
      `repeating-linear-gradient(${x*180}deg, hsla(${hue},100%,70%,0.12) 0px, transparent 4px, hsla(${hue2},100%,65%,0.08) 8px, transparent 12px)`,
      `repeating-linear-gradient(${x*180+90}deg, hsla(${hue3},100%,70%,0.10) 0px, transparent 4px, hsla(${hue},100%,65%,0.06) 8px, transparent 12px)`,
    ].join(", ");
    el.style.opacity = "1";
    el.style.mixBlendMode = "screen";
  }

  // Animate pixel foil continuously when hovered
  useEffect(() => {
    if (!isPixelFoil) return;
    return () => { if (pixelAnimRef.current) cancelAnimationFrame(pixelAnimRef.current); };
  }, [isPixelFoil]);

  function startPixelAnim(x, y) {
    if (!isPixelFoil) return;
    if (pixelAnimRef.current) cancelAnimationFrame(pixelAnimRef.current);
    function tick() {
      drawPixelFoil(x, y);
      pixelAnimRef.current = requestAnimationFrame(tick);
    }
    tick();
  }

  function stopPixelAnim() {
    if (pixelAnimRef.current) cancelAnimationFrame(pixelAnimRef.current);
    pixelAnimRef.current = null;
    if (pixelRef.current) pixelRef.current.style.opacity = "0";
  }


  function startAnimation() { if (animRef.current) return; animRef.current = requestAnimationFrame(animate); }
  function onMouseMove(e) {
    if (isFlipped) return;
    if (!_canHover) return; // touch devices: no hover tilt/foil (it'd get stuck with no mouse-leave)
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top)  / rect.height;
    targetTilt.current = { x: (y - 0.5) * 28, y: (x - 0.5) * -28 };
    if (!noShine) {
      if (isPixelFoil) {
        startPixelAnim(x, y);
      } else if (isIceFoil) {
        drawIceFoil(x, y);
      } else if (isMetallicFoil) {
        drawMetallicFoil(x, y);
      } else {
        if (foilRef.current) { foilRef.current.style.setProperty("--foilpos",`${x*100}% ${y*100}%`); foilRef.current.style.opacity = "1"; }
        if (glareRef.current) { glareRef.current.style.background = `radial-gradient(ellipse at ${x*100}% ${y*100}%, rgba(255,255,255,0.22) 0%, transparent 60%)`; glareRef.current.style.opacity = "1"; }
      }
    }
    startAnimation();
  }
  function onMouseLeave() {
    isHovering.current = false; targetTilt.current = { x:0, y:0 };
    if (foilRef.current) foilRef.current.style.opacity = "0";
    if (glareRef.current) glareRef.current.style.opacity = "0";
    if (isPixelFoil) stopPixelAnim();
    else if (isIceFoil && iceRef.current) { iceRef.current.style.backgroundImage = _iceRest(); iceRef.current.style.opacity = "0.75"; }
    else if (isMetallicFoil && metallicRef.current) metallicRef.current.style.opacity = "0";
    else if (pixelRef.current) pixelRef.current.style.opacity = "0";
    startAnimation();
  }
  function onMouseEnter() { isHovering.current = true; startAnimation(); }
  function animate() {
    const cur = currentTilt.current, tgt = targetTilt.current;
    cur.x += (tgt.x - cur.x) * 0.1; cur.y += (tgt.y - cur.y) * 0.1;
    if (cardRef.current && !isFlipped) { cardRef.current.style.transition = "box-shadow 0.2s ease"; cardRef.current.style.transform = `perspective(600px) rotateX(${cur.x}deg) rotateY(${cur.y}deg) scale3d(1.04,1.04,1.04)`; }
    if (Math.abs(tgt.x-cur.x)>0.05||Math.abs(tgt.y-cur.y)>0.05||isHovering.current) { animRef.current = requestAnimationFrame(animate); }
    else { animRef.current = null; cur.x = 0; cur.y = 0; if(cardRef.current && !isFlipped) { cardRef.current.style.transition = ""; cardRef.current.style.transform = ""; } }
  }
  function handleClick() {
    if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null; }
    currentTilt.current = { x:0, y:0 }; targetTilt.current = { x:0, y:0 };
    if (foilRef.current) foilRef.current.style.opacity = "0";
    if (glareRef.current) glareRef.current.style.opacity = "0";
    if (cardRef.current) { cardRef.current.style.transition = ""; cardRef.current.style.transform = ""; }
    isHovering.current = false;
    if (onExpand) { onExpand(c); return; }
    setFlippedCard(!isFlipped ? c.id : null);
  }

  const QtyControls = () => (
    <div style={{ display:"flex", alignItems:"center", gap:4 }} onClick={e=>e.stopPropagation()}>
      <button onClick={()=>setOwnedQty(c.id, Math.max(0, qty-1))} style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:5, width:22, height:22, fontSize:13, cursor:"pointer", fontFamily:"inherit", lineHeight:1, display:"flex", alignItems:"center", justifyContent:"center" }}>{"\u2212"}</button>
      <input
        type="number" inputMode="numeric" min="0" value={qty}
        onClick={e=>{e.stopPropagation(); e.target.select();}}
        onChange={e=>{ const v=parseInt(e.target.value,10); setOwnedQty(c.id, isNaN(v)?0:Math.max(0,v)); }}
        style={{ width:38, height:22, fontSize:12, fontWeight:700, color:qty>0?"#4ade80":"#888", textAlign:"center", background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:5, fontFamily:"inherit", padding:0, MozAppearance:"textfield" }}
      />
      <button onClick={()=>setOwnedQty(c.id, qty+1)} style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:5, width:22, height:22, fontSize:13, cursor:"pointer", fontFamily:"inherit", lineHeight:1, display:"flex", alignItems:"center", justifyContent:"center" }}>+</button>
    </div>
  );

  if (_displayImg) {
    return (
      <div className="boba-card-hover" data-card-drop={isAdmin && onImageUpload ? "1" : undefined} style={{ aspectRatio:"3/4", perspective:"1000px", position:"relative" }} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave} onMouseEnter={onMouseEnter} onPointerLeave={onMouseLeave} onPointerCancel={onMouseLeave} onTouchEnd={onMouseLeave}
        onDragOver={isAdmin && onImageUpload ? (e=>{ e.preventDefault(); e.stopPropagation(); if(!dragOver) setDragOver(true); }) : undefined}
        onDragLeave={isAdmin && onImageUpload ? (e=>{
          // Only clear when the pointer genuinely leaves the card \u2014 moving across a child element
          // fires dragleave too, which would make the highlight flicker.
          if(e.currentTarget.contains(e.relatedTarget)) return;
          setDragOver(false);
        }) : undefined}
        onDrop={isAdmin && onImageUpload ? (e=>{
          e.preventDefault(); e.stopPropagation();
          setDragOver(false);
          // Surface what actually arrived. A silent no-op here is indistinguishable from the drop
          // never reaching this handler at all, which is exactly the ambiguity that made this hard
          // to pin down: some sources (another browser tab, a preview pane, a URL) carry NO file
          // at all, only a text/uri-list, so files[] is empty and nothing happens.
          const dt = e.dataTransfer;
          const files = [...(dt?.files||[])];
          const f = files.find(x=>x.type.startsWith("image/"));
          if(f){ onImageUpload(c, f); return; }
          const kinds = dt ? [...(dt.items||[])].map(i=>`${i.kind}:${i.type}`).join(", ") : "none";
          alert(
            "Nothing was uploaded \u2014 that drop carried no image FILE.\n\n" +
            `What arrived: ${kinds || "(nothing)"}\n\n` +
            "Dragging a picture out of another browser tab usually sends a link, not a file. " +
            "Save the image to your computer first, then drag it from there."
          );
        }) : undefined}>
        {/* A dedicated drop layer ABOVE the 3D flipper. The card body lives inside a
            transform-style:preserve-3d container that is rotated in space, and hit-testing drag
            events against transformed 3D surfaces is unreliable across browsers \u2014 the drop can
            land on nothing. A flat, untransformed overlay is a dependable target. It stays
            transparent and only lights up once a drag is actually over the card. */}
        {isAdmin && onImageUpload && (
          <div style={{ position:"absolute", inset:0, zIndex:45, borderRadius:10,
            border: (dragOver||dropBusy) ? "2px dashed #4ade80" : "none",
            background: (dragOver||dropBusy) ? "rgba(74,222,128,0.18)" : "transparent",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:11, fontWeight:900, color:"#4ade80", textAlign:"center", padding:8,
            // Only becomes a real hit target while a file is being dragged over the page. The rest of
            // the time it is pointerEvents:none, so clicking, flipping and hovering the card are
            // untouched. dragActive is driven by a window-level dragenter, which fires regardless of
            // what is underneath \u2014 unlike the card's own handlers, which the 3D-transformed flipper
            // can swallow.
            pointerEvents: (dragActive||dropBusy) ? "auto" : "none" }}
            onDragOver={e=>{ e.preventDefault(); e.stopPropagation(); if(!dragOver) setDragOver(true); }}
            onDragLeave={e=>{ if(e.currentTarget.contains(e.relatedTarget)) return; setDragOver(false); }}
            onDrop={acceptDroppedImage}>
            {dropBusy ? "Fetching\u2026" : dragOver ? "Drop image" : null}
          </div>
        )}
        <div ref={cardRef} style={{ position:"relative", width:"100%", height:"100%", transition:"transform 0.2s ease, box-shadow 0.2s ease", borderRadius:10, cursor:"pointer", willChange:"transform" }} onClick={handleClick}>
         <div className="boba-flipper" style={{ position:"relative", width:"100%", height:"100%", transformStyle:"preserve-3d", transition:"transform 0.55s cubic-bezier(0.34,1.3,0.5,1)", transform:isFlipped?"rotateY(180deg)":"rotateY(0deg)", willChange:"transform" }}>
          <div style={{ position:"absolute", inset:0, backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden", borderRadius:10, overflow:"hidden", border:`2px solid ${isBorrowed?"#7B9CFF":isOwned?"#4ade80":"var(--bz-s2)"}`, boxShadow:isBorrowed?"0 0 0 1px rgba(123,156,255,0.4), 0 4px 18px rgba(123,156,255,0.25)":isOwned?"0 0 0 1px rgba(74,222,128,0.35), 0 4px 18px rgba(74,222,128,0.22)":"none" }}>
            <img src={_displayImg} alt={c.hero} loading="lazy" decoding="async" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
              onError={e=>{ e.currentTarget.style.visibility="hidden"; }}/>
            {_isScanImg && <div style={{ position:"absolute", top:6, left:6, zIndex:4, background:"rgba(0,0,0,0.7)", border:"1px solid rgba(74,222,128,0.5)", color:"#4ade80", borderRadius:6, padding:"2px 7px", fontSize:9, fontWeight:800, letterSpacing:0.5, backdropFilter:"blur(3px)" }}>📸 Your scan</div>}
            {!_isScanImg && isFoilTreatment && !isIceFoil && <div ref={foilRef} style={{ position:"absolute", inset:0, borderRadius:10, background:"linear-gradient(115deg, transparent 20%, rgba(255,255,255,0.14) 30%, rgba(255,220,100,0.22) 40%, rgba(100,200,255,0.24) 50%, rgba(200,100,255,0.20) 60%, rgba(255,100,150,0.18) 70%, transparent 80%)", backgroundSize:"200% 200%", backgroundPosition:"var(--foilpos,50% 50%)", mixBlendMode:"screen", opacity:0, transition:"opacity 0.2s ease", pointerEvents:"none" }}/>}
            {!_isScanImg && isIceFoil && <div ref={iceRef} style={{ position:"absolute", inset:0, borderRadius:10, backgroundImage:_iceRest(), mixBlendMode:"screen", opacity:0.75, transition:"opacity 0.2s ease", pointerEvents:"none", zIndex:3 }}/>}
            <div ref={glareRef} style={{ position:"absolute", inset:0, borderRadius:10, background:"radial-gradient(ellipse at 50% 50%, rgba(255,255,255,0.22) 0%, transparent 60%)", mixBlendMode:"overlay", opacity:0, transition:"opacity 0.2s ease", pointerEvents:"none" }}/>
            {!_isScanImg && isPixelFoil    && <div ref={pixelRef}    style={{ position:"absolute", inset:0, borderRadius:10, mixBlendMode:"screen", opacity:0, transition:"opacity 0.1s ease", pointerEvents:"none", zIndex:3 }}/>}
            {isMetallicFoil && <div ref={metallicRef} style={{ position:"absolute", inset:0, borderRadius:10, mixBlendMode:"screen", opacity:0, transition:"opacity 0.08s ease", pointerEvents:"none", zIndex:3 }}/>}
            {!onExpand && <div className="boba-flip-pill" style={{ position:"absolute", bottom:6, right:6, display:"flex", alignItems:"center", gap:3, fontSize:10, color:"#fff", fontWeight:700, background:"rgba(0,0,0,0.6)", borderRadius:12, padding:"3px 8px", backdropFilter:"blur(4px)", border:"1px solid rgba(255,255,255,0.15)", pointerEvents:"none" }}>{"\uD83D\uDD04"} flip</div>}
          </div>
          <div onPointerDown={()=>onCardActivity&&onCardActivity()} onPointerMove={()=>onCardActivity&&onCardActivity()} onKeyDown={()=>onCardActivity&&onCardActivity()} style={{ position:"absolute", inset:0, backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden", transform:"rotateY(180deg)", background:"var(--bz-s1)", border:`2px solid ${isBorrowed?"#7B9CFF":isOwned?"#4ade80":"var(--bz-line-2)"}`, borderRadius:10, padding:isSmallCard?"8px 9px":"12px 14px", display:"flex", flexDirection:"column", justifyContent:"space-between", overflow:"hidden" }}>
            <div style={{ overflowY:"auto", overflowX:"hidden", flex:1, minHeight:0, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>#{c.cardNum}</span>
                <QtyControls/>
              </div>
              <div style={{ fontSize:isSmallCard?12:15, fontWeight:900, color:"var(--bz-ink)", marginBottom:4, lineHeight:1.1 }}>{c.hero}</div>
              {!isSmallCard && <>
              <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:4 }}>
                {c.weapon && <span style={{ fontSize:10, color:wc, background:wc+"22", borderRadius:4, padding:"1px 6px", fontWeight:700 }}>{c.weapon}</span>}
                {c.treatment && <span style={{ fontSize:10, color:"var(--bz-ink-2)", background:"var(--bz-s2)", borderRadius:4, padding:"1px 6px" }}>{c.treatment}</span>}
                {c.notation && <span style={{ fontSize:10, color:"#FBBF24", background:"#FBBF2422", borderRadius:4, padding:"1px 6px", fontWeight:700 }}>{c.notation}</span>}
              </div>
              {(c.inspiredBy||c.athlete) && (()=>{
                const ath=c.inspiredBy||c.athlete;
                const sport=athleteSport(ath);
                const sportEmoji={"MLB":"⚾","NFL":"🏈","NBA":"🏀","WNBA":"🏀","NHL":"🏒","PGA":"⛳","WTA":"🎾","ATP":"🎾","Boxing":"🥊","MMA":"🥊","MLS":"⚽","USMNT":"⚽","USWNT":"⚽","Music":"🎤","Acting":"🎬"};
                const emoji=sport?(sport.split(/[\/\s]/).map(s=>sportEmoji[s]).find(Boolean)||"🏅"):"🏅";
                return (
                  <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginTop:4 }}>🏅 Inspired by <strong style={{ color:"var(--bz-ink-2)" }}>{ath}</strong>
                    {sport && <span style={{ display:"inline-flex", alignItems:"center", gap:3, marginLeft:6, background:"rgba(232,49,122,0.12)", border:"1px solid rgba(232,49,122,0.3)", borderRadius:5, padding:"1px 7px", fontSize:10, fontWeight:700, color:"#E8317A", verticalAlign:"middle" }}>{emoji} {sport}</span>}
                  </div>
                );
              })()}
              {(()=>{
                const bio=getPlayerNote(c.hero);
                if(!bio||(!bio.team&&!bio.notes)) return null;
                const longBio = bio.notes && bio.notes.length > 60;
                return (
                  <div style={{ marginTop:6, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:7, padding:"7px 9px", position:"relative" }}
                    onMouseEnter={()=>longBio&&setBioOpen(true)} onMouseLeave={()=>setBioOpen(false)}>
                    {bio.team && <div style={{ fontSize:10, fontWeight:700, color:"#7B9CFF", marginBottom:bio.notes?4:0 }}>🏟️ {bio.team}</div>}
                    {bio.notes && <div style={{ fontSize:10, color:"#999", lineHeight:1.4, display:"-webkit-box", WebkitLineClamp:1, WebkitBoxOrient:"vertical", overflow:"hidden", textOverflow:"ellipsis" }}>{bio.notes}</div>}
                    {longBio && <div onClick={e=>{e.stopPropagation();setBioOpen(o=>!o);}} style={{ fontSize:9, color:wc, fontWeight:700, marginTop:4, cursor:"pointer", display:"flex", alignItems:"center", gap:3 }}>📖 {bioOpen?"Close":"Read full bio"}</div>}
                    {bioOpen && longBio && (
                      <div onClick={e=>e.stopPropagation()} style={{ position:"absolute", left:-4, right:-4, bottom:"calc(100% + 6px)", zIndex:100, background:"rgba(12,12,16,0.98)", border:`1.5px solid ${wc}66`, borderRadius:10, padding:"12px 13px", boxShadow:"0 12px 40px rgba(0,0,0,0.7)", backdropFilter:"blur(8px)" }}>
                        {bio.team && <div style={{ fontSize:11, fontWeight:800, color:"#7B9CFF", marginBottom:6 }}>🏟️ {bio.team}</div>}
                        <div style={{ fontSize:11, color:"#ddd", lineHeight:1.55 }}>{bio.notes}</div>
                        {bio.player && <div style={{ fontSize:9, color:"#999", marginTop:8, fontStyle:"italic" }}>Inspired by {bio.player}</div>}
                      </div>
                    )}
                  </div>
                );
              })()}
              {c.variation && <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{c.variation}</div>}
              {(() => { const packs = foundInFor(c, foundInMap); if(!packs.length) return null; return <div style={{ display:"inline-flex", alignItems:"center", gap:5, marginTop:6, flexWrap:"wrap" }}><span style={{ color:"var(--bz-ink-3)", fontSize:10, fontWeight:700 }}>Found In:</span>{packs.map(pk => <span key={pk.id} style={{ background:`${pk.color}1a`, border:`1px solid ${pk.color}44`, borderRadius:6, padding:"2px 8px", fontSize:10, fontWeight:700, color:pk.color }}>{pk.label}</span>)}</div>; })()}
              </>}
            </div>
            <div>
              {c.power && (
                <div style={{ marginBottom:8 }}>
                  <div style={{ fontSize:isSmallCard?16:22, fontWeight:900, color:wc }}>
                    {c.power}{"\u26A1"}
                    {/* Buff / nerf: show what the power WAS, so the change is visible rather than silent. */}
                    {c.originalPower!=null && Number(c.originalPower)!==Number(c.power) && (
                      <span style={{ fontSize:isSmallCard?10:12, fontWeight:800, marginLeft:7, color: Number(c.power)>Number(c.originalPower) ? "#4ade80" : "#E8317A" }}>
                        {Number(c.power)>Number(c.originalPower) ? "\u25B2" : "\u25BC"} was {c.originalPower}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {/* Error card badge sits OUTSIDE the power block on purpose: a card with no power value
                  still needs to be flaggable, and nesting it under {c.power && ...} meant the badge
                  silently never rendered for those cards. */}
              {c.isError && (
                <div style={{ marginBottom:8, display:"inline-flex", alignItems:"center", gap:5, background:"rgba(251,191,36,0.12)", border:"1px solid rgba(251,191,36,0.4)", borderRadius:6, padding:"3px 8px", fontSize:10, fontWeight:800, color:"#FBBF24" }}>
                  {"\u26A0\uFE0F"} Error card
                </div>
              )}
              {c.errorNote && <div style={{ marginBottom:8, fontSize:10.5, color:"var(--bz-ink-3)", lineHeight:1.45 }}>{c.errorNote}</div>}
              {/* Who changed the power and when — internal provenance, admins only. */}
              {isAdmin && c.powerEditedAt && (
                <div style={{ marginBottom:8, fontSize:9.5, color:"rgba(255,255,255,0.3)" }}>
                  power edited {String(c.powerEditedAt).split("T")[0]}{c.powerEditedBy?` by ${c.powerEditedBy}`:""}
                </div>
              )}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                {toggleWant && <button onClick={e=>{e.stopPropagation();toggleWant(c.id);}} style={{ background:isWanted?"#1a0f00":"rgba(255,255,255,0.03)", border:`1px solid ${isWanted?"#FBBF24":"#333"}`, color:isWanted?"#FBBF24":"#999", borderRadius:7, padding:"7px 8px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{isWanted?"\uD83C\uDFAF Wanted":"+ Want"}</button>}
                {onComp && <button onClick={e=>{e.stopPropagation();onComp(c);}} style={{ background:"rgba(123,156,255,0.1)", border:"1px solid rgba(123,156,255,0.3)", color:"#7B9CFF", borderRadius:7, padding:"7px 8px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>📊 Comp</button>}
                {onLotEdit && isOwned && <button onClick={e=>{e.stopPropagation();onLotEdit();}} style={{ background:"rgba(232,49,122,0.1)", border:"1px solid rgba(232,49,122,0.3)", color:"#E8317A", borderRadius:7, padding:"7px 8px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>💰 Details{lotCount>0?` (${lotCount})`:""}</button>}
                {isAdmin && onImageUpload && (
                  <label onClick={e=>e.stopPropagation()} style={{ background:"rgba(74,222,128,0.1)", border:"1px solid rgba(74,222,128,0.3)", color:"#4ade80", borderRadius:7, padding:"7px 8px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", textAlign:"center" }}>
                    🖼 {c.imageUrl?"Replace":"Add"}
                    <input type="file" accept="image/*" style={{ display:"none" }} onChange={e=>{ const f=e.target.files?.[0]; if(f) onImageUpload(c,f); e.target.value=""; }}/>
                  </label>
                )}
                {isAdmin && onImageClear && c.imageUrl && (
                  <button onClick={e=>{ e.stopPropagation(); if(window.confirm(`Clear the image on ${c.hero}${c.cardNum?` #${c.cardNum}`:""}? This removes the wrong image so you can add the right one.`)) onImageClear(c); }} style={{ background:"rgba(251,191,36,0.1)", border:"1px solid rgba(251,191,36,0.35)", color:"#FBBF24", borderRadius:7, padding:"7px 8px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>🧹 Clear Img</button>
                )}
                {isAdmin && onDelete && <button onClick={e=>{e.stopPropagation();onDelete();}} style={{ background:"rgba(239,68,68,0.15)", border:"1px solid rgba(239,68,68,0.4)", color:"#EF4444", borderRadius:7, padding:"7px 8px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>🗑 Delete</button>}
              </div>
              <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:4, fontSize:10, color:"rgba(255,255,255,0.4)", fontWeight:700, marginTop:8, paddingTop:6, borderTop:"1px solid rgba(255,255,255,0.06)" }}>{"\uD83D\uDD04"} tap to flip back</div>
            </div>
            </div>
          </div>
         </div>
        </div>
      </div>
    );
  }
  return (
    <div onClick={handleClick} style={{ aspectRatio:"3/4", background:isBorrowed?"linear-gradient(160deg,#0d1424,#0a0f1a)":isOwned?"linear-gradient(160deg,#0d2416,#0a1a0a)":"var(--bz-s1)", border:`2px solid ${isBorrowed?"#7B9CFF":isOwned?"#4ade80":"var(--bz-s2)"}`, boxShadow:isBorrowed?"0 0 0 1px rgba(123,156,255,0.35), 0 4px 16px rgba(123,156,255,0.2)":isOwned?"0 0 0 1px rgba(74,222,128,0.3), 0 4px 16px rgba(74,222,128,0.18)":"none", borderRadius:10, overflow:"hidden", display:"flex", flexDirection:"column", cursor:"pointer" }}>
      {/* "Image coming soon" art placeholder — fills the card-art area */}
      <div {...dropProps} style={{ position:"relative", flex:"1 1 58%", minHeight:0, background:`linear-gradient(135deg, ${wc}18, var(--bz-bg) 70%)`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", borderBottom:`1px solid ${wc}22` }}>
        {(dragOver||dropBusy) && (
          <div style={{ position:"absolute", inset:0, zIndex:5, borderRadius:8, pointerEvents:"none",
            border:"2px dashed #4ade80", background:"rgba(74,222,128,0.2)",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:11, fontWeight:900, color:"#4ade80" }}>
            {dropBusy ? "Fetching\u2026" : "Drop image"}
          </div>
        )}
        <div style={{ fontSize:30, opacity:0.35, marginBottom:6 }}>🃏</div>
        <div style={{ fontSize:9, fontWeight:800, color:"rgba(255,255,255,0.4)", letterSpacing:1.5, textTransform:"uppercase" }}>Image coming soon</div>
        {c.power ? <div style={{ position:"absolute", top:8, right:10, fontSize:13, fontWeight:900, color:wc }}>{c.power}⚡</div> : null}
        {isOwned && (
          <div title={isBorrowed ? "Borrowed \u2014 not yours" : "In your collection"}
            style={{ position:"absolute", top:6, left:8, fontSize:15 }}>
            {isBorrowed ? "\uD83D\uDCE5" : "\u2705"}
          </div>
        )}
        {/* Label stops CLICKS so the card does not open. Drag events still reach the placeholder
            behind it, which is what carries the drop handler. */}
        {isAdmin && onImageUpload && (
          <label onClick={e=>e.stopPropagation()} style={{ pointerEvents:"auto", position:"absolute", bottom:8, background:"rgba(74,222,128,0.15)", border:"1px solid rgba(74,222,128,0.35)", color:"#4ade80", borderRadius:6, padding:"2px 10px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
            🖼 Add Image
            <input type="file" accept="image/*" style={{ display:"none" }} onChange={e=>{ const f=e.target.files?.[0]; if(f) onImageUpload(c,f); e.target.value=""; }}/>
          </label>
        )}
      </div>
      {/* Card info */}
      <div style={{ flex:"0 0 auto", padding:"8px 10px", display:"flex", flexDirection:"column", gap:5 }}>
        <div style={{ fontSize:13, fontWeight:900, color:isOwned?(isBorrowed?"#7B9CFF":"#4ade80"):"var(--bz-ink)", lineHeight:1.15, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{c.hero}</div>
        <div style={{ display:"flex", gap:4, flexWrap:"wrap", alignItems:"center" }}>
          <span style={{ fontSize:9, color:"var(--bz-ink-3)", fontWeight:700 }}>#{c.cardNum}</span>
          {lentCount > 0 && (
            <span title={lentWho ? `Lent to ${lentWho}` : "Lent out"}
              style={{ fontSize:9, color:"#FBBF24", background:"rgba(251,191,36,0.15)", borderRadius:4, padding:"1px 5px", fontWeight:800 }}>
              {"\uD83D\uDCE4"} {lentWho ? lentWho : "lent"}
            </span>
          )}
          {c.weapon && <span style={{ fontSize:9, color:wc, background:wc+"22", borderRadius:4, padding:"1px 5px", fontWeight:700 }}>{c.weapon}</span>}
          {c.treatment && <span style={{ fontSize:9, color:"var(--bz-ink-2)", background:"var(--bz-s2)", borderRadius:4, padding:"1px 5px" }}>{c.treatment}</span>}
          {c.notation && <span style={{ fontSize:9, color:"#FBBF24", background:"#FBBF2422", borderRadius:4, padding:"1px 5px", fontWeight:700 }}>{c.notation}</span>}
        </div>
        {(() => { const packs = foundInFor(c, foundInMap); if(!packs.length) return null; return <div style={{ display:"inline-flex", alignItems:"center", gap:3, flexWrap:"wrap", alignSelf:"flex-start" }}><span style={{ color:"var(--bz-ink-3)", fontSize:9, fontWeight:700 }}>Found In:</span>{packs.map(pk => <span key={pk.id} style={{ background:`${pk.color}1a`, border:`1px solid ${pk.color}44`, borderRadius:5, padding:"1px 5px", fontSize:9, fontWeight:700, color:pk.color }}>{pk.label}</span>)}</div>; })()}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:1 }}>
          <div style={{ display:"flex", gap:5, alignItems:"center" }}>
            {toggleWant && <button onClick={e=>{e.stopPropagation();toggleWant(c.id);}} style={{ background:isWanted?"#1a0f00":"transparent", border:`1px solid ${isWanted?"#FBBF24":"#333"}`, color:isWanted?"#FBBF24":"#444", borderRadius:5, padding:"1px 6px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{isWanted?"\uD83C\uDFAF":"+ Want"}</button>}
            {onLotEdit && isOwned && <button onClick={e=>{e.stopPropagation();onLotEdit();}} style={{ background:"rgba(232,49,122,0.1)", border:"1px solid rgba(232,49,122,0.3)", color:"#E8317A", borderRadius:5, padding:"1px 6px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>💰{lotCount>0?` ${lotCount}`:""}</button>}
            {isAdmin && onDelete && <button onClick={e=>{e.stopPropagation();onDelete();}} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"#8a8a8a", borderRadius:5, padding:"1px 6px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }} title="Delete card">🗑</button>}
            {/* Kid tag — only on cards you own, and only once you've set up a kid. Cycles through
                Mine → Kid 1 → Kid 2 → Mine, so tagging is a single tap while you're adding cards. */}
            {isOwned && onAssignKid && kidGroups.length>0 && (()=>{
              const qty = parseInt(ownedQty) || 1;
              // Multiple copies can belong to different people, so summarise rather than pretending
              // there's one owner. Tapping cycles ONLY when there's a single copy — with more than
              // one, open the card to tag each copy individually (the tile has no room for that).
              const tags = Array.from({length:qty}, (_,i)=> kidTags?.[i] ?? null);
              const uniq = [...new Set(tags)];
              if (qty > 1) {
                const names = uniq.map(t => t ? (kidGroups.find(k=>k.id===t)?.name || "?") : "Mine");
                const col = uniq.length===1 && uniq[0] ? (kidGroups.find(k=>k.id===uniq[0])?.color||"#888") : "#888";
                return (
                  <span title="Open the card to tag each copy"
                    style={{ background:"transparent", border:`1px solid ${col}55`, color:col, borderRadius:5, padding:"1px 6px", fontSize:10, fontWeight:700, whiteSpace:"nowrap", maxWidth:92, overflow:"hidden", textOverflow:"ellipsis" }}>
                    🧒 {names.join(" + ")}
                  </span>
                );
              }
              const cur = tags[0];
              const g = kidGroups.find(k=>k.id===cur);
              const next = () => {
                const i = kidGroups.findIndex(k=>k.id===cur);
                return i === -1 ? kidGroups[0].id : (i === kidGroups.length-1 ? null : kidGroups[i+1].id);
              };
              return (
                <button onClick={e=>{e.stopPropagation(); onAssignKid(c.id, next(), 0);}}
                  title={g?`${g.name}'s card — tap to change`:"Tap to tag this card to a kid"}
                  style={{ background:g?`${g.color}22`:"transparent", border:`1px solid ${g?g.color:"#333"}`, color:g?g.color:"#444", borderRadius:5, padding:"1px 6px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", maxWidth:78, overflow:"hidden", textOverflow:"ellipsis" }}>
                  {g ? `🧒 ${g.name}` : "🧒"}
                </button>
              );
            })()}
            {/* Sold / traded away — drops it from the active collection but keeps the record. */}
            {isOwned && onSell && (
              <button onClick={e=>{e.stopPropagation(); onSell();}} title="Sold, traded, or gave this away"
                style={{ background:"transparent", border:"1px solid #333", color:"#444", borderRadius:5, padding:"1px 6px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>↗</button>
            )}
            {/* One-click lend / return. Lent copies still count as owned but can't be
                decked; this writes the same lot lendState the editor does. */}
            {isOwned && onLend && (
              lentCount > 0
                ? <button onClick={e=>{e.stopPropagation(); onReturn && onReturn();}}
                    title={lentWho ? `Lent to ${lentWho} — click when returned` : "Lent out — click when returned"}
                    style={{ background:"rgba(251,191,36,0.12)", border:"1px solid #FBBF24", color:"#FBBF24", borderRadius:5, padding:"1px 6px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                    {"\uD83D\uDCE4"} back?
                  </button>
                : <button onClick={e=>{e.stopPropagation(); onLend();}} title="Lend this card to someone"
                    style={{ background:"transparent", border:"1px solid #333", color:"#444", borderRadius:5, padding:"1px 6px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                    {"\uD83D\uDCE4"}
                  </button>
            )}
          </div>
          <QtyControls/>
        </div>
      </div>
    </div>
  );
}

// PERF: the card grid renders 100+ tiles. Without memo, flipping/hovering ONE card re-rendered
// EVERY tile (because flippedCard lives in the parent), rebuilding all their DOM + animations.
// This comparator makes a tile re-render only when something about THAT tile actually changed —
// notably, flippedCard only matters if it involves this card.
const BobaCard = React.memo(BobaCardImpl, (prev, next) => {
  if (prev.c !== next.c) return false;
  if (prev.isOwned !== next.isOwned) return false;
  // Without this the memo skips the re-render and a borrowed card keeps the green owned styling
  // even after the flag flips \u2014 the prop was being passed correctly and simply never read.
  if (prev.isBorrowed !== next.isBorrowed) return false;
  if (prev.ownedQty !== next.ownedQty) return false;
  if (prev.isAdmin !== next.isAdmin) return false;
  if (prev.lotCount !== next.lotCount) return false;
  // Lent status for THIS card — without these the badge and button freeze after a
  // lend/return because the memo skips the re-render.
  if (prev.lentCount !== next.lentCount) return false;
  if (prev.lentWho !== next.lentWho) return false;
  if (prev.myScanPhoto !== next.myScanPhoto) return false;
  // Which packs a card is found in. Without this the memo skips the re-render and the badge
  // keeps showing stale data even though the live map has already updated.
  if (prev.foundInMap !== next.foundInMap) return false;
  // Compare by VALUE. These are small arrays, and comparing by reference meant a
  // fresh-but-identical array (exactly what a `= []` default prop yields on every render)
  // re-rendered every card in the grid for no reason — thousands of wasted renders.
  const kgPrev = prev.kidGroups || [], kgNext = next.kidGroups || [];
  if (kgPrev.length !== kgNext.length || kgPrev.some((g,i)=>g!==kgNext[i])) return false;
  const ktPrev = prev.kidTags || [], ktNext = next.kidTags || [];
  if (ktPrev.length !== ktNext.length || ktPrev.some((t,i)=>t!==ktNext[i])) return false;
  // Want status for THIS card only.
  const id = next.c?.id;
  if ((prev.wantList?.[id] ? 1 : 0) !== (next.wantList?.[id] ? 1 : 0)) return false;
  // Flip state only matters if this card is (or was) the flipped one.
  const wasFlipped = prev.flippedCard === id;
  const isFlipped  = next.flippedCard === id;
  if (wasFlipped !== isFlipped) return false;
  return true; // otherwise: skip the re-render
});

const PLAYER_NOTES = {
  // AUTOGRAPHS
  "BoJax":              { player:"Bo Jackson",          team:"LA Raiders / KC Royals", notes:"The only man to be an All-Star in two pro sports simultaneously. Raiders RB and Royals OF. Ran a 4.12 forty — fastest ever recorded. Famous for running through the end zone tunnel on MNF. THE most broken player in Tecmo history. Retired at 28 from a hip injury and we STILL never saw his ceiling.", lines:["\"Bo knows — and Bo knows how to empty a wallet 💸\"","\"Two pro sports. All-Star in both. Nobody has done it before or since.\"","\"Retired at 28 and was already a legend. What if he played to 35?\""] },
  "Attak":             { player:"Dak Prescott",         team:"Dallas Cowboys", notes:"Dak took over for Tony Romo and never looked back. Multiple Pro Bowls, led the Cowboys to big seasons. One of the most beloved QBs in Dallas in decades.", lines:["\"America's QB for the next generation\"","\"Dallas fans have been waiting for this guy since Troy Aikman\""] },
  "Pukadot":           { player:"Puka Nacua",           team:"LA Rams", notes:"Set the NFL record for most receptions by a rookie in his first two games. Came out of nowhere and immediately became a fantasy football hero. Samoan kid with route running beyond his years.", lines:["\"Rookie record in his first two games. TWO GAMES.\"","\"The most exciting unknown player in football right now\""] },
  "Brees":             { player:"Drew Brees",           team:"New Orleans Saints", notes:"All-time passing yards leader. 80,358 career yards. Rebuilt New Orleans after Hurricane Katrina — the city adopted him as their own. Super Bowl XLIV MVP. Pinpoint accurate, short to intermediate was his bread and butter.", lines:["\"80,000 passing yards. The all-time leader.\"","\"Drew Brees didn't just win a Super Bowl — he helped heal a city\"","\"Surgically precise. Every throw had a purpose.\""] },
  "Fear Himself":      { player:"Lawrence Taylor",      team:"NY Giants", notes:"Changed football forever. The sack didn't exist as an official stat until the NFL created it because of LT. 10x Pro Bowl, 2 Super Bowls, 1986 MVP. Bill Parcells said he was the only player who ever made him nervous.", lines:["\"They created a stat just to count what this man did\"","\"Every modern pass rusher is trying to be LT\"","\"QBs had nightmares about #56. Now you get to own him.\""] },
  "Mossed":            { player:"Randy Moss",           team:"Minnesota Vikings / NE Patriots", notes:"Single season TD record with 23 in 2007. Ran a 4.25 forty. Made cornerbacks look like they were standing still. 'Straight cash homie.' One of the most physically gifted receivers ever.", lines:["\"Straight cash homie 💰\"","\"23 touchdowns in a single season. TWENTY-THREE.\"","\"When Randy ran a go route, the corner was just a spectator\""] },
  "Nigerian Nightmare": { player:"Christian Okoye",     team:"KC Chiefs", notes:"6'1\", 253 lbs running back who ran a 4.48 forty. Led the NFL in rushing in 1989. Born in Nigeria, came to the US on a track scholarship, became an All-Pro. Defenders bounced off him like he was a freight train.", lines:["\"253 pounds of pure freight train\"","\"Came from Nigeria on a TRACK scholarship and became an All-Pro NFL rusher\"","\"Defenders didn't tackle the Nigerian Nightmare — they just tried to slow him down\""] },
  "Marino":            { player:"Dan Marino",           team:"Miami Dolphins", notes:"Never won a Super Bowl but nobody cares — one of the greatest QBs ever. Set the single-season TD and yardage records in 1984 that stood for 20 years. Fastest release in NFL history. Did it all without a running game.", lines:["\"Fastest release in NFL history. The ball was gone before you blinked.\"","\"No Super Bowl ring, but a Hall of Fame jacket and every QB record that mattered\"","\"In 1984 he threw 48 TDs. Nobody touched that for 20 years.\""] },
  "Young-Gunner":      { player:"Steve Young",          team:"SF 49ers", notes:"Highest passer rating in NFL history at retirement (96.8). Super Bowl XXIX MVP with 6 TD passes. Left-handed scrambler who sat behind Montana for years and then proved he belonged.", lines:["\"96.8 career passer rating. That's not a stat, that's a flex.\"","\"Six touchdowns in one Super Bowl. Montana who?\"","\"Waited behind the greatest ever and then proved he was one too\""] },
  "Gronk":             { player:"Rob Gronkowski",       team:"NE Patriots / Tampa Bay", notes:"Greatest tight end in NFL history. 4x Super Bowl champion. 3x All-Pro. Gronk parties as hard as he plays. Redefined what a TE could do — a wide receiver in a lineman's body.", lines:["\"Greatest tight end who ever lived. Full stop.\"","\"Gronk spikes are the most joyful thing in football\"","\"Four Super Bowls. Somehow finds time to party through all of them.\""] },
  "Hitt Man":          { player:"Ronnie Lott",          team:"SF 49ers", notes:"Had the tip of his pinky finger amputated rather than miss time. AMPUTATED. 10x Pro Bowl, 4x Super Bowl champion. Defined what a safety was supposed to be. Receivers thought twice before going over the middle.", lines:["\"Chose amputation over missing games. That's commitment.\"","\"Made receivers think twice before crossing the middle\"","\"Four Super Bowls and a Hall of Fame jacket — and the man still has 9.5 fingers\""] },
  "Island Time":       { player:"Darrelle Revis",       team:"NY Jets / others", notes:"Revis Island. When you were on his side of the field, you simply didn't exist as an option. Best shutdown corner of his generation. Made elite receivers disappear for 60 minutes.", lines:["\"Welcome to Revis Island — population: whatever receiver just got taken away\"","\"The best receivers in football just... stopped existing on his side\"","\"Revis Island wasn't just a nickname, it was a strategy\""] },
  "Emmitt-164":        { player:"Emmitt Smith",         team:"Dallas Cowboys", notes:"All-time rushing leader with 18,355 yards. Three Super Bowls. Won the 1993 Super Bowl MVP on a SEPARATED SHOULDER. 11 straight 1,000-yard seasons.", lines:["\"All-time rushing leader is right here in this pack\"","\"Super Bowl MVP on a separated shoulder — that's Emmitt\"","\"Eleven straight thousand-yard seasons. Consistency is a superpower\""] },
  "Troy of Dallas":    { player:"Troy Aikman",          team:"Dallas Cowboys", notes:"Three Super Bowls in four years with the Cowboys dynasty. Most accurate QB of his era. Never threw a lot of picks — efficient, clutch, and surrounded himself with greatness. Hall of Famer.", lines:["\"Three Super Bowls. The face of the Cowboys dynasty.\"","\"Most efficient QB of his era — didn't force it, just won\""] },
  "Goggles":           { player:"Eric Dickerson",       team:"LA Rams", notes:"Single season rushing record holder — 2,105 yards in 1984, wearing his iconic goggles and neck roll. Ran upright which shouldn't work but absolutely did. Five Pro Bowls.", lines:["\"2,105 yards in a single season — a record that stood for decades\"","\"Ran upright in goggles and a neck roll and nobody could catch him\""] },
  "Howietzer":         { player:"Howie Long",           team:"LA Raiders", notes:"Eight-time Pro Bowl defensive end for the Raiders dynasty. Super Bowl XVIII champion. Became a Fox NFL analyst and has been on your TV every Sunday since 1994. His son Chris Long won two Super Bowls.", lines:["\"8x Pro Bowl, Super Bowl champ, and your Sunday morning TV presence since 1994\"","\"The Raiders defensive line was terrifying and Howie was the reason\""] },
  "Thurmanator":       { player:"Thurman Thomas",       team:"Buffalo Bills", notes:"Four straight Super Bowl appearances with the Bills — never won, but the run is legendary. Led the NFL in yards from scrimmage four straight years. Hall of Famer who famously lost his helmet before a Super Bowl.", lines:["\"Four straight Super Bowls. Nobody has done that since.\"","\"Lost his helmet before a Super Bowl and STILL had a big game\"","\"The Bills may not have won but Thurman Thomas gave everything\""] },
  "Bus":               { player:"Jerome Bettis",        team:"Pittsburgh Steelers", notes:"The Bus. 5.8 yards per carry average was astronomical for a power back his size. Won Super Bowl XL in Detroit — his hometown — in his final game. Perfect Hollywood ending.", lines:["\"Won the Super Bowl in his hometown in his final game. Perfect.\"","\"The Bus didn't go around defenders. He went through them.\"","\"5.8 YPC for a 255-pound back. That's just not fair.\""] },
  "Machine Gun":       { player:"Jim Kelly",            team:"Buffalo Bills", notes:"Four straight Super Bowls — yes, lost all four, but nobody has ever led a team to four consecutive Super Bowls since. Hall of Famer who ran the no-huddle K-Gun offense that revolutionized football.", lines:["\"Four straight Super Bowls. Name another QB who did that.\"","\"The K-Gun no-huddle was 30 years ahead of its time\"","\"Winning isn't everything — the Bills of the 90s were must-watch football\""] },
  "Cutback":           { player:"Barry Sanders",        team:"Detroit Lions", notes:"Most elusive runner in history. Retired at his peak leaving $10M on the table. 10 straight 1,000-yard seasons with the worst O-lines in football. Jim Brown called him the greatest runner he ever saw.", lines:["\"Left $10 million on the table because football became a job. Respect.\"","\"Jim Brown said Barry was the best. Jim Brown.\"","\"No line, no Super Bowl, still 10 straight thousand-yard seasons\""] },
  "Sea-Largent":       { player:"Steve Largent",        team:"Seattle Seahawks", notes:"Held every major receiving record when he retired. Was cut by Houston as a rookie and became a Hall of Famer. Then ran for Congress and won. Literally one of the greatest stories in NFL history.", lines:["\"Cut as a rookie. Became a Hall of Famer. Then got elected to Congress.\"","\"Held every major receiving record when he retired\"","\"The Seahawks' first real superstar\""] },
  "Boz":               { player:"Brian Bosworth",       team:"Seattle Seahawks", notes:"The most hyped linebacker in NFL history coming out of Oklahoma. Biggest bust in NFL history. Bo Jackson ran over him on MNF and the clip lives forever. Had a movie career afterward.", lines:["\"The most hyped linebacker ever — then Bo Jackson happened\"","\"That MNF clip of Bo running him over might be the most watched moment in football history\"","\"Went from biggest bust to movie star. Not a bad pivot.\""] },
  "Allenwrench":       { player:"Marcus Allen",         team:"LA Raiders / KC Chiefs", notes:"Super Bowl XVIII MVP. Had a famous falling out with Al Davis. Went to Kansas City and proved he still had it at 32+. One of the most versatile backs ever — ran, caught, blocked.", lines:["\"Super Bowl MVP who had to fight his own owner to prove he wasn't done\"","\"32 years old in Kansas City and still one of the best backs in football\""] },
  "Ultimate Weapon":   { player:"Randall Cunningham",   team:"Philadelphia Eagles / Minnesota Vikings", notes:"The original dual-threat QB. Before Lamar, before Cam — there was Randall. Led the league in rushing multiple times as a QB. 1998 MVP season at 35 years old was one of the greatest comebacks in football.", lines:["\"Before Lamar Jackson, before Cam Newton — there was Randall Cunningham\"","\"35 years old, comeback season, MVP. Nobody expected it.\"","\"The original scrambler. Changed what a QB was supposed to be.\""] },
  "Shuffler":          { player:"Jim McMahon",          team:"Chicago Bears", notes:"Quarterback of the legendary 1985 Bears. Wore headbands with messages that got him fined. Super Bowl XX champion. The anti-establishment QB in the most dominant team in NFL history.", lines:["\"QB of the most dominant team in NFL history\"","\"Got fined for his headbands and wore them anyway\"","\"The 85 Bears were the most dominant team ever and McMahon was their chaos agent\""] },
  "Boom":              { player:"Boomer Esiason",       team:"Cincinnati Bengals / NY Jets", notes:"Left-handed QB who led the Bengals to Super Bowl XXIII. NFL MVP in 1988. Became one of the best NFL analysts on radio — you've heard him on CBS Sports Radio every morning.", lines:["\"Left-handed NFL MVP — the original Tebow before there was a Tebow\"","\"Led the Bengals to their best era. And they're STILL talking about it in Cincinnati.\""] },
  "Dentist":           { player:"Richard Dent",         team:"Chicago Bears", notes:"Super Bowl XX MVP from the defensive line — one of the only defensive ends to ever win that award. Was underrated for years before his Hall of Fame selection. The engine of the '85 Bears defense.", lines:["\"Defensive end. Super Bowl MVP. That doesn't happen often.\"","\"The '85 Bears defense was the stuff of nightmares and Dent was the nightmare\""] },
  "Vinniverde":        { player:"Vinny Testaverde",     team:"Multiple teams", notes:"Played 21 NFL seasons for seemingly every team in football. 1986 Heisman Trophy winner. The ultimate journeyman — never won a Super Bowl but competed forever.", lines:["\"21 seasons. TWENTY-ONE. He was playing when some of your buyers were born.\"","\"The most traveled QB in history — everywhere needed a Vinny\""] },
  "Afterbern":         { player:"Bernie Kosar",         team:"Cleveland Browns", notes:"Cleveland legend. Unorthodox release that somehow worked. Led the Browns to multiple AFC Championship games in the 80s. Grew up a Browns fan and then played for them — living the dream.", lines:["\"Grew up a Browns fan and then played for them. The dream.\"","\"Ugliest throwing motion in NFL history and it somehow worked perfectly\""] },
  "Sack King":         { player:"Bruce Smith",          team:"Buffalo Bills", notes:"All-time sack leader with 200 career sacks. Four straight Super Bowls with the Bills. Hall of Famer. Possibly the greatest defensive player in Bills history and one of the best ever.", lines:["\"200 career sacks. The all-time leader.\"","\"Four straight Super Bowls and the most feared pass rusher of his era\""] },
  "Incredible Faulk":  { player:"Marshall Faulk",       team:"Indianapolis Colts / St. Louis Rams", notes:"Greatest Rams player ever. The Greatest Show on Turf. 2000 NFL MVP. Combined 2,189 yards from scrimmage in 2001. One of the most complete backs ever — ran AND caught everything.", lines:["\"The Greatest Show on Turf had a leading man and it was Marshall Faulk\"","\"2,189 combined yards in one season. From scrimmage. Both running AND catching.\""] },
  "Mann-O-War":        { player:"Charles Mann",         team:"Washington Redskins", notes:"Four-time Pro Bowl DE for three Washington Super Bowl teams. One of the best pass rushers of the 80s and 90s. Hall of Very Good — maybe should have been Hall of Fame.", lines:["\"Three Super Bowl teams. One of Washington's greatest players ever.\"","\"Four Pro Bowls and a Hall of Very Good career\""] },
  "Timmy":             { player:"Tim Brown",            team:"LA / Oakland Raiders", notes:"Hall of Famer. 100 receiving TDs. The Raiders' most consistent offensive weapon for 17 seasons. Grew up in Dallas, became the heart of the Raiders.", lines:["\"100 receiving touchdowns and 17 seasons of Raiders loyalty\"","\"The one constant on Raiders offense for almost two decades\""] },
  "Haley's Comet":     { player:"Charles Haley",        team:"SF 49ers / Dallas Cowboys", notes:"The ONLY player to win five Super Bowl rings as a player. Five. Two with the 49ers, three with the Cowboys. Arguably the most decorated player in Super Bowl history.", lines:["\"Five Super Bowl rings. The only player ever.\"","\"Two dynasties, five rings, one Charles Haley\"","\"If you want to win a Super Bowl, apparently you need Charles Haley on your team\""] },
  "Ickey":             { player:"Ickey Woods",          team:"Cincinnati Bengals", notes:"Famous for the Ickey Shuffle touchdown dance. Led the Bengals to Super Bowl XXIII as a rookie. Had his career derailed by injury but the shuffle lives forever in NFL culture.", lines:["\"The Ickey Shuffle made America happy every Sunday in 1988\"","\"Rookie year, Super Bowl, legendary dance move — and then an injury. Still iconic.\""] },
  "Full Moon":         { player:"Warren Moon",          team:"Houston Oilers / others", notes:"Had to go to Canada because the NFL wouldn't draft a Black QB out of college. Dominated the CFL, then dominated the NFL. Hall of Famer. One of the greatest comeback stories in sports.", lines:["\"The NFL wouldn't draft him so he went to Canada, dominated, came back, and made the Hall of Fame\"","\"Never got a fair chance early — and still became one of the greatest ever\"","\"Warren Moon's career is one of the most inspiring stories in football history\""] },
  "Philament":         { player:"Phil Simms",           team:"NY Giants", notes:"Super Bowl XXI MVP with a 22/25 performance — 88% completion rate in a Super Bowl. One of the best single-game performances in Super Bowl history. Hall of Very Good but that game was Hall of Fame.", lines:["\"22 of 25 in the Super Bowl. 88% completion rate. In the SUPER BOWL.\"","\"That Super Bowl XXI performance might be the most efficient QB game ever played\""] },
  "Key Keeper":        { player:"Luke Kuechly",         team:"Carolina Panthers", notes:"Best linebacker of his generation. 2013 Defensive Player of the Year as a rookie. Retired at 28 at the top of his game to protect his long-term health — one of the most respected retirement decisions in NFL history.", lines:["\"Retired at 28 at the absolute top of his game\"","\"Best linebacker since LT — and he walked away on his own terms\"","\"DPOY as a ROOKIE. That's almost impossible.\""] },
  "T.D.":              { player:"Tony Dorsett",         team:"Dallas Cowboys", notes:"The longest run in NFL history — 99 yards. Super Bowl XII champion. Hall of Famer. The first pick of the 1977 draft and worth every bit of it.", lines:["\"99-yard touchdown run. The longest in NFL history. NINETY-NINE YARDS.\"","\"Super Bowl champ and one of the most electrifying backs of the 70s and 80s\""] },
  "Sterling":          { player:"Sterling Sharpe",      team:"Green Bay Packers", notes:"Had his career ended by a neck injury at 29. In 7 seasons he led the NFL in receiving 3 times. Would have been one of the greatest ever. His brother Shannon made the Hall of Fame — imagine if Sterling had stayed healthy.", lines:["\"Career ended at 29 and he STILL led the NFL in receiving three times\"","\"7 seasons. Imagine if he'd played 14.\"","\"His brother Shannon is in the Hall of Fame. Sterling might have been better.\""] },
  "Too Tall":          { player:"Ed 'Too Tall' Jones",  team:"Dallas Cowboys", notes:"6'9\" defensive end — literally too tall. Three Super Bowls with the Cowboys. Took a year off to box professionally (a perfect 6-0 with 5 KOs) then came back to the NFL. He even acted, appearing in the football comedy 'Necessary Roughness,' 'The Wedding Ringer,' and TV shows like 'Married... with Children.' Just an incredible life story.", lines:["\"6 foot 9. On a football field. Doing pass rush moves.\"","\"Took a year off to be a professional boxer and then came back to the NFL\"","\"Three Super Bowls, a perfect boxing record, AND a Hollywood acting career — Too Tall did it all.\""] },
  "Fridge":            { player:"William Perry",        team:"Chicago Bears", notes:"The Refrigerator. 6'2\", 335 pounds, used as a goal line fullback and scored touchdowns. Super Bowl XX champion and the most beloved player on the most beloved team in NFL history.", lines:["\"They gave a 335-pound defensive tackle the ball to score touchdowns. And it worked.\"","\"The Fridge is the most lovable player in NFL history — change my mind\"","\"The 85 Bears were perfect and The Fridge was the cherry on top\""] },
  "Samurai Mike":      { player:"Mike Singletary",      team:"Chicago Bears", notes:"The leader of the '85 Bears defense. Two-time Defensive Player of the Year. His stare alone was worth 5 yards. Hall of Famer. The most intense linebacker you've ever seen.", lines:["\"Mike Singletary's eyes could tackle you before his body got there\"","\"The '85 Bears defense was terrifying and Samurai Mike was the reason\"","\"Two-time Defensive Player of the Year and the heart of the greatest defense ever\""] },
  "Monk":              { player:"Art Monk",             team:"Washington Redskins", notes:"Three Super Bowl rings. First receiver ever to catch 100 passes in a season. One of the most consistent receivers in NFL history. Did it quietly, without drama, for 14 seasons.", lines:["\"First receiver in NFL history to catch 100 passes in a season\"","\"Three Super Bowl rings and zero drama. Pure professionalism.\""] },
  "Kriеghawk":         { player:"Dave Krieg",           team:"Seattle Seahawks", notes:"Led the Seahawks to their most successful era of the 80s. Undrafted, undersized, overlooked — became a franchise QB. The original Seahawks legend before Hasselbeck before Wilson.", lines:["\"Undrafted. Made the Hall of Very Good. The original Seahawks QB.\"","\"The most underrated QB of the 80s — ask any Seahawks fan from that era\""] },
  "Undrafted Wrecking Ball": { player:"Gary Clark",     team:"Washington Redskins", notes:"Three Super Bowl rings. Part of Washington's legendary receiving corps with Art Monk and Ricky Sanders. Undersized but faster than anyone expected.", lines:["\"Three Super Bowls as part of the best receiving trio in Washington history\"","\"They called him a wrecking ball because size didn't matter when you were that fast\""] },
  "Highjump":          { player:"Alonzo Highsmith",     team:"Houston Oilers", notes:"First round pick known for his physical running style. Career cut short by injuries but a fan favorite during his time with Houston.", lines:["\"The Oilers' power back in the run-and-shoot era\"","\"First round talent who never got to show his full potential\""] },
  "Calf Roper":        { player:"Eric Metcalf",         team:"Cleveland Browns / others", notes:"Return specialist extraordinaire. Scored 2 punt return TDs in the same game against Pittsburgh — one of the most electric performances on special teams ever. His father Terry was also an NFL player.", lines:["\"Two punt return touchdowns in ONE GAME against the Steelers\"","\"Most electric return man of his era — every return felt like it could go\""] },
  "Novacaine":         { player:"Jay Novacek",          team:"Dallas Cowboys", notes:"Three Super Bowls as Troy Aikman's security blanket tight end. Underrated piece of the Cowboys dynasty. Reliable, sure-handed, always open on third down.", lines:["\"Three Super Bowls as the most reliable target on the Cowboys dynasty\"","\"Novacek on third down was basically a guaranteed first\""] },
  "Carrier":           { player:"Mark Carrier",         team:"Chicago Bears", notes:"1990 Defensive Rookie of the Year with 10 interceptions — a rookie record at the time. Pure ball hawk safety who announced himself immediately in the NFL.", lines:["\"10 interceptions as a ROOKIE. Defensive Rookie of the Year.\"","\"The ball hawk who announced himself on day one in the NFL\""] },
  "Amigo One":         { player:"Mark Jackson",         team:"Denver Broncos", notes:"Part of John Elway's early receiving corps. Member of the Three Amigos with Vance Johnson and Ricky Nattiel. One of the most exciting wide receiver trios of the 80s.", lines:["\"The Three Amigos — one of the most exciting WR groups of the 80s\"","\"John Elway needed someone to catch it and Mark Jackson answered\""] },
  "Quick Draw":        { player:"Mike Quick",           team:"Philadelphia Eagles", notes:"5x Pro Bowl WR in the 80s for Philadelphia. One of the most underrated Eagles in history. Had a 99-yard TD catch in 1985 — one of the longest in NFL history.", lines:["\"99-yard touchdown catch in 1985 — one of the longest in NFL history\"","\"Five Pro Bowls that Eagles fans still talk about today\""] },
  "Bank Robber":       { player:"Carl Banks",           team:"NY Giants", notes:"Two Super Bowls as part of the Giants' legendary linebacking corps alongside LT. Underrated piece of one of the best defenses of the 80s.", lines:["\"Part of the Giants defense that LT made famous\"","\"Two Super Bowl rings alongside the greatest linebacker ever\""] },
  "Claymore":          { player:"Clay Matthews Jr.",    team:"Cleveland Browns", notes:"Father of Clay Matthews III (the Packers linebacker). Played 19 seasons himself — one of the longest careers for a linebacker in NFL history.", lines:["\"19 seasons at linebacker. His son played 11 and everyone knows him — imagine 19.\"","\"Football royalty — his son became a star too\""] },
  "Biscuit":           { player:"Cornelius Bennett",    team:"Buffalo Bills / others", notes:"Part of the Bills' four-straight Super Bowl teams. One of the most feared linebackers of his era. Should probably be in the Hall of Fame.", lines:["\"Four straight Super Bowls. One of the most feared linebackers of the 90s.\"","\"The Hall of Fame debate that Buffalo fans still have every year\""] },
  "Underdoug":         { player:"Doug Williams",        team:"Washington Redskins", notes:"First Black starting QB to win a Super Bowl. Super Bowl XXII MVP — threw 4 TD passes in one quarter, the most dominant quarter by a QB in Super Bowl history. Answered 'How long have you been a Black quarterback?' with a legendary performance.", lines:["\"First Black QB to win a Super Bowl. Then went out and was the MVP.\"","\"4 touchdowns in ONE QUARTER of the Super Bowl. Still a record.\"","\"The most important answer in NFL history was delivered on a football field\""] },
  "Mountain Drew":     { player:"Drew Bledsoe",         team:"NE Patriots / Buffalo Bills", notes:"His injury gave Tom Brady his chance — and sports history was changed forever. Before that, Bledsoe was the Patriots' franchise QB and the man who started the dynasty.", lines:["\"His injury gave Tom Brady a chance. Sports history was never the same.\"","\"Started the Patriots dynasty and then had to watch someone else win with it\"","\"The most important backup story in NFL history\""] },
  "Dwight Noise":      { player:"Dwight Stephenson",    team:"Miami Dolphins", notes:"The greatest center in NFL history according to many. Dan Marino's blindside protector. Hall of Famer who anchored the Dolphins offensive line for 8 seasons before a knee injury ended his career.", lines:["\"Greatest center in NFL history — Dan Marino's protector\"","\"Hall of Famer at the most underappreciated position in football\""] },
  "Green Light":       { player:"Harold Green",         team:"Cincinnati Bengals", notes:"Power running back for the Bengals in the early 90s. Reliable workhorse back who gave the Bengals an identity in the run game during a transitional era.", lines:["\"The workhorse back who gave Cincinnati an identity\""] },
  "K-Mack":            { player:"Kevin Mack",           team:"Cleveland Browns", notes:"First player in NFL history to rush for 1,000 yards and also have 1,000 yards receiving in a career... wait, ran for 1,000 yards alongside Earnest Byner in 1985 — making Cleveland the only team with two 1,000-yard rushers in the same season.", lines:["\"Two 1,000-yard rushers in one season — Kevin Mack and Earnest Byner did what no other team has done\"","\"Cleveland's backfield in '85 was the best in football\""] },
  "Pepper Spray":      { player:"Pepper Johnson",       team:"NY Giants / others", notes:"Two Super Bowl rings with the Giants. Tough, physical linebacker who was the unsung hero of New York's defense. Later became a respected NFL defensive line coach.", lines:["\"Two Super Bowls as the unsung hero of the Giants defense\"","\"Became one of the most respected coaches in football after playing\""] },
  "The Manster":       { player:"Randy White",          team:"Dallas Cowboys", notes:"Super Bowl XII co-MVP — shared with Harvey Martin, one of the only times a defensive player won. 'The Manster' — half man, half monster. One of the most dominant DTs of all time.", lines:["\"Super Bowl MVP from the defensive line — one of the only times ever\"","\"Half man, half monster. The Manster. Nobody else gets a nickname that good.\""] },
  "Woodchipper":       { player:"Rod Woodson",          team:"Pittsburgh Steelers / others", notes:"Greatest cornerback in NFL history by many metrics. Had 71 career interceptions. Came back from an ACL tear to win Super Bowl XXXVI at safety. Hall of Famer.", lines:["\"71 career interceptions. The most by any cornerback ever.\"","\"Tore his ACL, came back, and won a Super Bowl at a different position\"","\"Greatest cornerback in NFL history — the numbers don't lie\""] },
  "Gaultfather":       { player:"Willie Gault",         team:"Chicago Bears / LA Raiders", notes:"Olympic sprinter who became an NFL wide receiver. Ran a 4.28 forty. Part of the Super Bowl Shuffle video. The deep threat on the Bears dynasty.", lines:["\"Olympic sprinter. Wide receiver. Super Bowl champion. One résumé.\"","\"Part of the Super Bowl Shuffle — one of the greatest moments in sports pop culture\"","\"A 4.28 forty on a football field is basically cheating\""] },
  "Jolly Roger":       { player:"Roger Craig",          team:"SF 49ers", notes:"First player in NFL history to rush for 1,000 yards AND catch 1,000 yards in the same season. Three Super Bowl rings with the 49ers dynasty. Underrated piece of San Francisco's dynasty.", lines:["\"First player EVER with 1,000 rushing and 1,000 receiving in the same season\"","\"Three Super Bowls and a place in history for doing something nobody had done before\""] },
  // BASE & HIGHLIGHTED HEROES
  "Sevenring":         { player:"Tom Brady",            team:"NE Patriots / Tampa Bay", notes:"Seven Super Bowl rings. SEVEN. Greatest of all time, full stop. Won one at 43 years old in Tampa. 89,214 career passing yards. Somehow still polarizing despite being the most accomplished player in NFL history.", lines:["\"Seven rings. Nobody is close. Nobody.\"","\"Won a Super Bowl at 43 with a new team. Forty-three.\"","\"Love him or hate him — Tom Brady is the greatest football player who ever lived\""] },
  "Joe Cool":          { player:"Joe Montana",          team:"SF 49ers", notes:"4-0 in Super Bowls. Never threw a pick in Super Bowl play. The Drive. Back-to-back Super Bowl MVPs. 'Joe Cool' because nothing rattled him — ever.", lines:["\"Four Super Bowls. Zero losses. Zero interceptions.\"","\"The Drive — 92 yards, 5 minutes left, down 6. Just another Tuesday for Joe.\"","\"Joe Cool. Ice in his veins, fire in everyone else's.\""] },
  "Neon":              { player:"Deion Sanders",        team:"SF 49ers / Dallas Cowboys", notes:"Only athlete to play in a Super Bowl AND a World Series. Prime Time. 3x All-Pro, 8x Pro Bowl. The most electric entrance in sports. Shutdown corner who took half the field away every game.", lines:["\"Super Bowl. World Series. Prime Time did both.\"","\"You don't pull Neon — Neon pulls you\"","\"The most electric player in NFL history and he knew it\""] },
  "Sheriff":           { player:"Peyton Manning",       team:"Indianapolis Colts / Denver Broncos", notes:"Two Super Bowls. 2013 season — 55 TD passes, 5,477 yards, both single season records at the time. Changed pre-snap communication forever. The smartest QB who ever played.", lines:["\"55 touchdowns in one season. FIFTY-FIVE.\"","\"Changed how QBs communicate at the line of scrimmage forever\"","\"The Sheriff of the pocket — nothing happened until Peyton Manning allowed it\""] },
  "Sweetest of Sweet": { player:"Walter Payton",        team:"Chicago Bears", notes:"Sweetness. All-time rushing record holder for 18 years. Won Super Bowl XX. Most complete back ever — ran, caught, threw, and blocked. Never missed a game by choice.", lines:["\"Sweetness. Because that's the only word for what he did on a football field.\"","\"The 85 Bears defense gets the love — but Payton was the heart\"","\"16,726 yards of pure will. No excuses, no days off.\""] },
  "Hands":             { player:"Jerry Rice",           team:"SF 49ers", notes:"Greatest receiver of all time. 1,549 catches, 197 TDs, 3 Super Bowls. Was a 16th-round pick. Never missed a game until 37. Rice + Montana = game over in Tecmo.", lines:["\"16th round pick. Greatest of all time.\"","\"The man caught everything. Literally everything.\"","\"1,549 catches. 197 touchdowns. Numbers that will never be touched.\""] },
  "Merlomes":          { player:"Patrick Mahomes",      team:"Kansas City Chiefs", notes:"Three Super Bowls before 30. Highest paid player in NFL history. Makes throws that shouldn't be possible — behind the back, off the wrong foot, sidearm on a rollout. The most entertaining QB alive.", lines:["\"Three Super Bowls before 30. The race to Brady has a real contender.\"","\"Makes throws that shouldn't be physically possible and then winks at you\"","\"The most entertaining QB in NFL history — and he's not even close to done\""] },
  "Dart-Board":        { player:"Jaxson Dart",          team:"NY Giants", notes:"Former Ole Miss QB taken by the Giants. One of the most intriguing young QBs in the league. New York is counting on him to end the QB search.", lines:["\"New York's answer to the QB question — the city is watching\"","\"Ole Miss to the NFL — the most electric QB prospect in the 2025 class\""] },
  "Bison":             { player:"Josh Allen",           team:"Buffalo Bills", notes:"The biggest human to play QB since Cam Newton. 6'5\", can throw 80 yards from his knees. Multiple MVP-caliber seasons. Bills fans have waited decades for a QB like this — he's the one.", lines:["\"6'5\", 240 pounds, throws 80 yards off his back foot. Josh Allen is not real.\"","\"Buffalo waited 30 years for a QB like this. Bison is the payoff.\"","\"Most physically gifted QB in football. And he's getting better.\""] },
  "Warp":              { player:"Lamar Jackson",        team:"Baltimore Ravens", notes:"Two-time NFL MVP. 2019 unanimous MVP — broke Vick's QB rushing record. Runs the ball like a running back and throws like a top-10 QB. The most exciting player in football.", lines:["\"Two MVPs. Broke the QB rushing record. The most electric player alive.\"","\"Before Lamar, people said mobile QBs couldn't win. He proved everyone wrong.\"","\"Unanimous MVP in 2019 — they had to create a new category for what he does\""] },
  "Bayou":             { player:"Ja'Marr Chase",        team:"Cincinnati Bengals", notes:"Offensive Rookie of the Year. Broke the single-season receiving yards record for a rookie. His reunion with Joe Burrow from LSU was one of the best storylines in recent NFL history.", lines:["\"Broke the rookie receiving record in his first season.\"","\"Burrow to Chase at LSU. Burrow to Chase in the NFL. Story hasn't changed.\""] },
  "J-Jetts":           { player:"Justin Jefferson",     team:"Minnesota Vikings", notes:"Broke Jerry Rice's record for most receiving yards in a player's first four seasons. The griddy celebration. Most talented receiver in the NFL right now.", lines:["\"Broke Jerry Rice's record for most receiving yards in first four seasons. JERRY RICE'S RECORD.\"","\"The Griddy. You know it. You've done it.\"","\"The most complete receiver in football right now\""] },
  "Reindeer Hunter":   { player:"Myles Garrett",        team:"Cleveland Browns", notes:"The best defensive player in football. Multiple Defensive Player of the Year awards. Intellectual, artistic, and also the most terrifying pass rusher on the planet. Plays guitar. Paints.", lines:["\"Best defensive player in football and also paints and plays guitar. Myles Garrett contains multitudes.\"","\"The most terrifying pass rusher alive — and he's getting better\""] },
  "Swervin'":          { player:"Michael Irvin",        team:"Dallas Cowboys", notes:"The Playmaker. Three Super Bowls with the Cowboys dynasty. Caught everything in traffic, never ducked contact. One of the most physically dominant receivers ever.", lines:["\"The Playmaker. Three Super Bowls and never backed down from a hit.\"","\"Troy threw it and Irvin caught it — that's the Cowboys dynasty in one sentence\""] },
  "MoD":               { player:"Reggie White",         team:"Philadelphia Eagles / Green Bay Packers", notes:"The Minister of Defense. All-time sack leader when he retired. Super Bowl XXXI champion with the Packers. Ordained minister who was as dominant off the field as on it.", lines:["\"The Minister of Defense. 198 career sacks. Hall of Famer.\"","\"Reggie White was so dominant that teams built their entire game plan around him\"","\"An ordained minister who also terrorized offensive linemen. Reggie contained multitudes.\""] },
  "Quads":             { player:"Saquon Barkley",       team:"NY Giants / Philadelphia Eagles", notes:"2018 Offensive Rookie of the Year. Missed two seasons to injury and came back stronger. The most physically gifted RB since Adrian Peterson. Eagles fans immediately fell in love.", lines:["\"Most explosive running back since Adrian Peterson\"","\"Came back from two ACL tears and looked better than before. Built different.\""] },
  "Darn Old":          { player:"Sam Darnold",          team:"Carolina Panthers", notes:"Had a rough run in New York but found his footing. The NFL is full of second chance stories and Sam Darnold's might be the most satisfying.", lines:["\"Second chances are real in the NFL and Sam Darnold is proof\""] },
  "Phoenix":           { player:"Bo Nix",               team:"Denver Broncos", notes:"Oregon QB who rose from early career struggles to become a high NFL draft pick. The rise and the story behind it make him compelling.", lines:["\"The comeback story is what makes Bo Nix compelling\"","\"From Auburn to Oregon to the NFL — took the long road and made it\""] },
  "Jax-In-The-Box":   { player:"Jaxon Smith-Njigba",   team:"Seattle Seahawks", notes:"The slot receiver the Seahawks needed after trading DK. Ohio State product. Proven pass catcher at the college level who is just getting started.", lines:["\"Seattle needed a receiver and JSN answered the call\""] },
  "Eagle-Eye":         { player:"Jalen Hurts",          team:"Philadelphia Eagles", notes:"Led the Eagles to back-to-back Super Bowl appearances. Dual threat QB who has gotten dramatically better every year. The ultimate underdog story — was benched at Alabama by Nick Saban.", lines:["\"Benched at Alabama by Nick Saban. Led two Super Bowl runs in Philadelphia. Underdog forever.\"","\"Gets better every single season. The trajectory is scary.\""] },
  "Shrouded":          { player:"C.J. Stroud",          team:"Houston Texans", notes:"Offensive Rookie of the Year. Youngest QB to win a playoff game in NFL history. Ohio State product who turned Houston from a rebuilding team into a contender overnight.", lines:["\"Youngest QB to win a playoff game in NFL history\"","\"Rookie of the Year and turned a franchise around in one season\""] },
  "Myracle":           { player:"Jahmyr Gibbs",         team:"Detroit Lions", notes:"Part of the Lions' incredible backfield with David Montgomery. Electric in the open field, dangerous as a receiver. Detroit's offensive explosion has his fingerprints on it.", lines:["\"The Lions are back and Jahmyr Gibbs is a big reason why\"","\"Most dangerous dual-threat back in the NFC\""] },
  "Skatter":           { player:"Cam Skattebo",         team:"NY Giants", notes:"Arizona State product who took the college football world by storm. Physical runner with great vision.", lines:["\"Arizona State's best player in years — now proving it at the next level\""] },
  "Warden":            { player:"Cam Ward",             team:"Miami Hurricanes", notes:"One of the most exciting QB prospects in recent memory. Went from Incarnate Word to Washington State to Miami and lit up every stop. The NFL is next.", lines:["\"From Incarnate Word to the ACC — nobody took a stranger path to stardom\"","\"Every stop, every level — Cam Ward was the best player on the field\""] },
  "Yeti":              { player:"Travis Kelce",         team:"Kansas City Chiefs", notes:"Greatest tight end in NFL history. Multiple Super Bowl rings with Mahomes. Dating Taylor Swift — which means half your buyers know who he is before they even watch football.", lines:["\"Greatest tight end ever. Also the reason half the country started watching football.\"","\"Mahomes to Kelce is the most reliable connection in football\"","\"Taylor Swift effect is real and Kelce is at the center of it\""] },
  "BrockNess":         { player:"Brock Bowers",         team:"Las Vegas Raiders", notes:"Broke the rookie record for tight end receiving yards. Georgia product. The next great TE in a league that loves great TEs.", lines:["\"Broke the TE rookie receiving record in his first season\"","\"The next great tight end — and he's already here\""] },
  "McArmyKnife":       { player:"Christian McCaffrey",  team:"SF 49ers", notes:"Most complete running back in football. Leads the league in yards from scrimmage consistently. 2023 season was one of the best RB performances in a decade.", lines:["\"Best running back in football. Not close.\"","\"Rushes, catches, blocks — CMC does everything\"","\"2023 was one of the greatest RB seasons in 10 years\""] },
  "Muffin Man":        { player:"Baker Mayfield",       team:"Tampa Bay Buccaneers", notes:"Had the most improbable comeback of any QB in recent memory. Cut by Cleveland, bounced around, landed in Tampa and won in the playoffs. Cleveland fans have mixed feelings.", lines:["\"Cut by Cleveland, bounced to three teams, went to the playoffs. Baker is BUILT for chaos.\"","\"Nobody wrote a better revenge story in recent NFL history\""] },
  "Youngblood":        { player:"Bryce Young",          team:"Carolina Panthers", notes:"First overall pick. Won the Heisman at Alabama. The smallest QB ever taken first overall — and the pressure that comes with it.", lines:["\"First overall pick, Heisman winner, and the smallest QB ever taken #1\"","\"Carolina is betting on talent over size — and Bryce Young has plenty of talent\""] },
  "Switchblade":       { player:"Travis Hunter",        team:"Colorado", notes:"Two-way Heisman Trophy winner. Plays both WR and CB at the highest level simultaneously. Deion Sanders' guy at Colorado. The most talked about prospect in football.", lines:["\"Two-way Heisman winner. Plays receiver AND corner. Both at All-American level.\"","\"Coach Prime's prized pupil — and the most unique prospect in a generation\"","\"Travis Hunter is doing something no one has done since the leather helmet era\""] },
  "McVillain":         { player:"Tei'roa McMillan",     team:"Colorado", notes:"Part of Deion Sanders' Colorado rebuild. Skilled back who shows flashes of elite potential.", lines:["\"Part of the Coach Prime revolution at Colorado\""] },
  "Quarter Staff":     { player:"Matthew Stafford",     team:"Detroit Lions / LA Rams", notes:"Won the Super Bowl with the Rams after spending 12 years in Detroit. One of the most beloved Lions in history who finally got his ring with Los Angeles. Detroit celebrated for him.", lines:["\"12 years in Detroit, finally got his ring in LA — and Detroit fans CELEBRATED for him\"","\"Super Bowl champion after everyone thought his window had passed\"","\"The most loyal fan base moment — Detroit rooting for a guy who left\""] },
  "Judkernaught":      { player:"Quinshon Judkins",     team:"Ohio State", notes:"One of the most physical and productive RBs in recent college football. Ole Miss to Ohio State — followed the portal to chase a championship.", lines:["\"Followed the portal to Ohio State to chase a national championship\"","\"One of the most physical backs in college football\""] },
  "Jeanetic":          { player:"Ashton Jeanty",        team:"Boise State", notes:"Nearly broke Barry Sanders' single season rushing record. Boise State legend who took the country by storm. Heisman finalist.", lines:["\"Almost broke Barry Sanders' single season rushing record. BARRY SANDERS' RECORD.\"","\"Boise State to the national spotlight in one historic season\""] },
  "Coinslot":          { player:"Daniel Jones",         team:"NY Giants", notes:"Had his moment in New York, including a legendary playoff run in 2022. The debate about him is still alive in every NFL city.", lines:["\"That 2022 playoff run with the Giants was one of the most unexpected stories of the year\""] },
  "Chanesaw":          { player:"De'Von Achane",        team:"Miami Dolphins", notes:"The fastest skill player in football right now. Set records for yards per carry in his rookie season. If he's in open space, it's over.", lines:["\"Fastest player in football. Give him open space and it's a highlight.\"","\"Set rushing records as a rookie — the Dolphins found something special\""] },
  "Mr. Irrelevant":    { player:"Brock Purdy",          team:"SF 49ers", notes:"The last pick in the 2022 NFL Draft — Mr. Irrelevant — became the starting QB for one of the best teams in football. The ultimate underdog story. May be the greatest Mr. Irrelevant ever.", lines:["\"Last pick in the draft. Starting QB of a Super Bowl contender. Mr. Irrelevant is very relevant.\"","\"The most unbelievable underdog story in recent NFL history\"","\"From dead last in the draft to NFC Championship games. Brock Purdy is for real.\""] },
  "Shadowstrike":      { player:"Davante Adams",        team:"Las Vegas Raiders / NY Jets", notes:"One of the best route runners in football history. Aaron Rodgers' favorite target for years in Green Bay. His routes are textbook material for young receivers.", lines:["\"Best route runner in football — defenders know exactly where he's going and still can't stop him\"","\"Aaron Rodgers said he's the best receiver he ever played with. That's high praise.\""] },
  "Shepherd":          { player:"CeeDee Lamb",          team:"Dallas Cowboys", notes:"Over 1,700 receiving yards in 2023. The most complete receiver in football today. Dallas's best player and one of the most valuable fantasy assets in the sport.", lines:["\"1,700 receiving yards. In one season. CeeDee Lamb is the best receiver in football right now.\"","\"Dallas's entire offense runs through this man\""] },
  "Scary":             { player:"Terry McLaurin",       team:"Washington Commanders", notes:"Scary Terry. Has been great despite mediocre QB play throughout his career. One of the most underrated receivers in football.", lines:["\"Scary Terry has been great with below average QB play his entire career — imagine him with elite QB\"","\"Washington fans have been waiting for the right QB to unleash what McLaurin can do\""] },
  "Lawman":            { player:"Trevor Lawrence",      team:"Jacksonville Jaguars", notes:"First overall pick. Generational talent who has shown flashes of why he was the consensus best prospect in a decade. Jacksonville's franchise anchor.", lines:["\"Consensus #1 prospect in a decade — Jacksonville built around him\"","\"Trevor Lawrence is still 25. The best is ahead.\""] },
  "Buttman":           { player:"Marion Butts",         team:"San Diego Chargers", notes:"Power running back for the Chargers in the late 80s-early 90s. Led the NFL in rushing touchdowns in 1990. Underrated back who gave San Diego a reliable run game.", lines:["\"Led the NFL in rushing TDs in 1990 — the Chargers' workhorse\""] },
  "Criscross":         { player:"Cris Carter",          team:"Minnesota Vikings", notes:"Hall of Famer. 130 receiving touchdowns — still third all-time. Ran routes so precise that defenders knew where he was going and couldn't stop him. Famous for his sideline catches.", lines:["\"130 career touchdowns — third all-time\"","\"The most precise route runner of his era\"","\"Famous for two-foot sideline catches before the rule even mattered\""] },
  "Rockhead":          { player:"Chris Doleman",        team:"Minnesota Vikings", notes:"Hall of Fame defensive end with 150.5 career sacks. One of the most underrated pass rushers of all time. Part of the Vikings' fearsome front in the 80s and 90s.", lines:["\"150 career sacks and somehow still underrated\"","\"The Vikings were scary in the 80s because of Doleman\""] },
  "Brawn":             { player:"A.J. Brown",           team:"Tennessee Titans / Philadelphia Eagles", notes:"Physical mismatch for any cornerback. Catches contested balls like they're gifts. Super Bowl LVI loser with the Eagles but one of the best receivers in football.", lines:["\"No cornerback alive can cover AJ Brown one-on-one for 60 minutes\"","\"The most physical receiver in football — catches everything\""] },
  "DeVaulta":          { player:"DeVonta Smith",        team:"Philadelphia Eagles", notes:"Heisman Trophy winner despite weighing 170 pounds. Every scout said he was too small. He's proven them wrong at every level. Eagles fans love him.", lines:["\"170 pounds. Heisman Trophy. Every scout was wrong.\"","\"The Slim Reaper — too small to be great, and yet here we are\""] },
  "Hot Sauce":         { player:"Sauce Gardner",        team:"NY Jets", notes:"2022 Defensive Rookie of the Year. First cornerback taken first overall in years. Has barely been beaten since entering the league. Sauce isn't just a nickname — it's a description.", lines:["\"Defensive Rookie of the Year. Barely gets beat. Sauce is real.\"","\"The Jets draft first overall CBs sometimes. Sauce was worth it.\""] },
  "First Leap":        { player:"LeRoy Butler",         team:"Green Bay Packers", notes:"Invented the Lambeau Leap. That's his legacy and it's permanent — every Packers TD scored at home pays homage to LeRoy Butler.", lines:["\"Invented the Lambeau Leap. His legacy is on every Green Bay touchdown scored at home.\"","\"You've seen a thousand Lambeau Leaps. They all started with LeRoy Butler.\""] },
  "Friday":            { player:"Deebo Samuel",         team:"SF 49ers", notes:"'Wide back' — used as a running back despite being a receiver. Explosive player who the 49ers weaponize in unique ways. One of the most creative offensive deployments in football.", lines:["\"Wide back. It's not a position but Deebo made it one.\"","\"The 49ers use him like no team uses a receiver — and it works every time\""] },
  "Barreler":          { player:"Cooper Kupp",          team:"LA Rams", notes:"2021 Triple Crown receiver — led the NFL in receptions, yards, AND touchdowns. Super Bowl LVI champion and MVP. Mcvay's favorite target and one of the best route runners alive.", lines:["\"Triple Crown receiver in 2021 — receptions, yards, TDs. All three.\"","\"Super Bowl champion AND Super Bowl MVP in the same year\"","\"The most important receiver on the most creative offense in football\""] },
  "Watterslide":       { player:"Ricky Watters",        team:"SF 49ers / Philadelphia Eagles", notes:"Super Bowl XXIX champion with the 49ers. Had a famous quote after his first game in Philadelphia — 'For who? For what?' — that Philly fans never forgot or forgave... then he became a fan favorite anyway.", lines:["\"'For who? For what?' — the most infamous first game quote in Philly history\"","\"Somehow became a Philly fan favorite after almost the worst possible start\""] },
  "Cannon":            { player:"Rich Gannon",          team:"Oakland Raiders", notes:"2002 NFL MVP at 37 years old — one of the oldest MVPs ever. Led the Raiders to the Super Bowl that year. Career journeyman who found a home in Oakland and became the best QB in football.", lines:["\"MVP at 37. One of the oldest in NFL history.\"","\"Journeyman QB, found the right system, became the best QB in football at 37\""] },
  "Furnest":           { player:"Earnest Byner",        team:"Cleveland Browns / Washington Redskins", notes:"Famous for The Fumble — one of the most heartbreaking moments in Browns history. Redeemed himself with a Super Bowl ring in Washington. The ultimate redemption story.", lines:["\"The Fumble broke Cleveland's heart. The Super Bowl ring healed it.\"","\"Greatest redemption story in NFL history — from The Fumble to a championship\""] },
  "Hot Rod":           { player:"Rodney Hampton",       team:"NY Giants", notes:"Two-time Pro Bowl back for the Giants in the early 90s. Carried New York's run game when they needed it most. Underrated workhorse of the NFC.", lines:["\"New York's workhorse back in the early 90s — reliable every single week\""] },
  "Golden Bullet":     { player:"Matthew Golden",       team:"Houston Texans", notes:"Young receiver coming into his own in Houston's offensive system. Part of the next generation of Texans weapons.", lines:["\"The next piece of Houston's offensive puzzle\""] },
  "Billiard":          { player:"Dalton Hilliard",      team:"New Orleans Saints", notes:"Pro Bowl back famous for his massive, powerful thighs — he let his legs do the talking, squatting serious weight to power through tacklers. A New Orleans legend who led the Saints in rushing for multiple seasons (18 TDs in 1989). At LSU he became the Tigers' all-time career rushing leader and broke Herschel Walker's NCAA freshman scoring records — all from a 5'8\" frame people said was too small.", lines:["\"Those thighs were a weapon — he squatted his way through the SEC\"","\"LSU's all-time leading rusher and an NFL Pro Bowler\"","\"Broke Herschel Walker's freshman records as a true freshman at LSU\""] },
  "Ruptillian":        { player:"Mark Rypien",          team:"Washington Redskins", notes:"Super Bowl XXVI MVP. Led Washington to their third Super Bowl with a dominant performance. Often overlooked in the conversation about Washington QBs despite being a champion.", lines:["\"Super Bowl MVP who somehow still gets overlooked in the Washington QB conversation\"","\"Led Washington to their last championship — that matters\""] },
  "Flippa":            { player:"Flipper Anderson",     team:"LA Rams", notes:"Set the single-game receiving record with 336 yards in 1989 — a record that stood for decades. Had some of the most electrifying individual performances in NFL history.", lines:["\"336 yards in ONE GAME. Single-game receiving record.\"","\"The most yards anyone has ever caught in a single NFL game — Flipper Anderson did that\""] },
  "Slaughterhouse":    { player:"Webster Slaughter",    team:"Cleveland Browns", notes:"Speedster receiver who gave Cleveland a deep threat in the late 80s. Paired with Reggie Langhorne to give Bernie Kosar weapons on the outside.", lines:["\"Cleveland's deep threat in the Bernie Kosar era\""] },
  "Hammer":            { player:"Omarion Hampton",      team:"UNC / LA Rams", notes:"Physical running back from North Carolina who was a first round pick. Brings power and vision to LA's backfield.", lines:["\"First round pick who brings a power runner's mentality to the Rams\""] },
  "Majik Man":         { player:"Don Majkowski",        team:"Green Bay Packers", notes:"The Majik Man — was THE guy in Green Bay before Brett Favre. Had a legendary 1989 season and gave Packers fans hope. His injury gave Favre his chance.", lines:["\"His injury gave Brett Favre his shot. Green Bay history changed on that day.\"","\"The Majik Man before Favre — Green Bay's forgotten hero\""] },
  "A Bear":            { player:"Bobby Hebert",         team:"New Orleans Saints", notes:"The Cajun Cannon. QB of the Saints for years. New Orleans legend who gave the city a face for their franchise.", lines:["\"The Cajun Cannon — as Louisiana as a QB can get\"","\"New Orleans' franchise QB before the Saints became who they are today\""] },
  "Moose":             { player:"Daryl Johnston",       team:"Dallas Cowboys", notes:"The fullback who made Emmitt Smith great. Three Super Bowls as the lead blocker. Often called the most important unsung hero of the Cowboys dynasty.", lines:["\"Emmitt Smith won the rushing title. Daryl Johnston paved the way.\"","\"Three Super Bowls as the most important player nobody talked about\"","\"The best fullback of his era — blocking is an art form and Moose was Picasso\""] },
  "Hillicopter":       { player:"Drew Hill",            team:"Houston Oilers", notes:"Part of Warren Moon's aerial circus in Houston. Consistent receiver who helped make the run-and-shoot one of the most exciting offenses of the 80s.", lines:["\"Warren Moon's go-to in the Houston run-and-shoot — one of the most exciting offenses ever\""] },
  "A.I.":              { player:"Allen Iverson",        team:"Philadelphia 76ers", notes:"Pound-for-pound one of the toughest players ever. 2001 NBA MVP, 11x All-Star, 4x scoring champion. Carried the Sixers to the 2001 Finals on sheer will. Listed at 6'0\" and changed the culture of the game forever.", lines:["\"The Answer. 165 pounds and feared nobody.\"","\"We talkin' about practice? We talkin' about a Hall of Famer.\"","\"An MVP who carried a team to the Finals on heart alone\""] },
  "AMON-RA":           { player:"Amon-Ra St. Brown",    team:"Detroit Lions", notes:"The heartbeat of the Lions' resurgence. Multiple-time Pro Bowler and one of the most reliable, productive receivers in football. Famous for his relentless work ethic — caught a pass in dozens of straight games.", lines:["\"The Sun God — and the soul of Detroit's offense\"","\"Drafted in the 4th round and became a top-5 receiver. Pure work ethic.\""] },
  "Art Monk":          { player:"Art Monk",             team:"Washington Redskins", notes:"Hall of Fame receiver and the model of consistency. 3x Super Bowl champion. Once held the NFL record for career receptions. Quiet, professional, dominant.", lines:["\"Three Super Bowls and a record-setting career — all without saying a word\"","\"The quietest superstar in Washington history\""] },
  "BARRY \"CUTBACK\" SANDERS": { player:"Barry Sanders", team:"Detroit Lions", notes:"The most electrifying runner in NFL history. 1997 co-MVP, 10x Pro Bowler, Hall of Famer. Walked away at 30 still in his prime, just shy of the all-time rushing record. His cutbacks defied physics.", lines:["\"Nobody made tacklers miss like Barry. Nobody ever will.\"","\"Retired in his prime, a thousand yards from the record. A legend on his own terms.\""] },
  "Barry Sanders":     { player:"Barry Sanders",        team:"Detroit Lions", notes:"The most electrifying runner in NFL history. 1997 co-MVP, 10x Pro Bowler, Hall of Famer. Walked away at 30 still in his prime, just shy of the all-time rushing record. His cutbacks defied physics.", lines:["\"Nobody made tacklers miss like Barry. Nobody ever will.\"","\"Retired in his prime, a thousand yards from the record. A legend on his own terms.\""] },
  "BRANDI":            { player:"Brandi Chastain",      team:"USWNT", notes:"Scored the winning penalty in the 1999 Women's World Cup Final — and the celebration became one of the most iconic images in sports history. 2x World Cup champion, 2x Olympic gold medalist.", lines:["\"The most famous penalty kick in American soccer history\"","\"1999 World Cup champion and an icon for a generation\""] },
  "Bo Jackson":        { player:"Bo Jackson",           team:"LA Raiders / KC Royals", notes:"The only man to be an All-Star in two pro sports simultaneously. Raiders RB and Royals OF. Ran a 4.12 forty — fastest ever recorded. THE most broken player in Tecmo history. Retired at 28 from a hip injury.", lines:["\"Bo knows — and Bo knows how to empty a wallet 💸\"","\"Two pro sports. All-Star in both. Nobody has done it before or since.\""] },
  "Breesus":           { player:"Drew Brees",           team:"New Orleans Saints", notes:"Super Bowl XLIV champion and MVP. NFL all-time leader in completions and one of the most accurate passers ever. Rebuilt a city's spirit after Katrina. 13x Pro Bowler.", lines:["\"Brought a Super Bowl to New Orleans when the city needed it most\"","\"The most accurate passer of his generation — and a hero off the field\""] },
  "Brian Bosworth":    { player:"Brian Bosworth",       team:"Seattle Seahawks", notes:"The most hyped college linebacker of his era — 2x Butkus Award winner at Oklahoma. The Boz was pure attitude and marketing genius before injuries cut his NFL run short.", lines:["\"The most marketed college player ever — The Boz was a phenomenon\"","\"Two Butkus Awards and more attitude than anyone in the game\""] },
  "Bruce Smith":       { player:"Bruce Smith",          team:"Buffalo Bills", notes:"The NFL's all-time sack leader with 200. Hall of Famer, 11x Pro Bowler, anchor of the Bills' four straight Super Bowl teams. The most relentless pass rusher in history.", lines:["\"200 career sacks — the most anyone has ever recorded\"","\"Four straight Super Bowls and the greatest pass rusher of all time\""] },
  "Charles Haley":     { player:"Charles Haley",        team:"SF 49ers / Dallas Cowboys", notes:"The only player to win five Super Bowls. Hall of Fame pass rusher who was the missing piece on two different dynasties. Pure intensity.", lines:["\"Five Super Bowl rings — more than any player in history\"","\"The closer on two dynasties. Teams won wherever he went.\""] },
  "Charles Mann":      { player:"Charles Mann",         team:"Washington Redskins", notes:"3x Super Bowl champion defensive end. 4x Pro Bowler and a cornerstone of Washington's dominant defenses of the late 80s and early 90s.", lines:["\"Three Super Bowls anchoring one of the best defenses of the era\""] },
  "Christian Okoye":   { player:"Christian Okoye",      team:"Kansas City Chiefs", notes:"The Nigerian Nightmare. A 260-pound battering ram who led the NFL in rushing in 1989. One of the most physically punishing runners the game has ever seen.", lines:["\"The Nigerian Nightmare — defenders LITERALLY did not want to tackle him\"","\"260 pounds of pure punishment. A Tecmo cheat code.\""] },
  "Curt Locker":       { player:"Curt Warner",          team:"Seattle Seahawks", notes:"Seattle's first great running back. 3x Pro Bowler who set the franchise's early rushing records and powered the Seahawks' first playoff runs in the 80s.", lines:["\"Seattle's original franchise running back\""] },
  "Dak Prescott":      { player:"Dak Prescott",         team:"Dallas Cowboys", notes:"The Cowboys' franchise QB. Offensive Rookie of the Year, multiple-time Pro Bowler, and one of the most productive passers in Dallas history. Drafted in the 4th round and proved everyone wrong.", lines:["\"A 4th-round pick who became the face of America's Team\"","\"Rookie of the Year and the heartbeat of Dallas\""] },
  "Dan Marino":        { player:"Dan Marino",           team:"Miami Dolphins", notes:"One of the greatest pure passers ever. 1984 MVP, 9x Pro Bowler, Hall of Famer. Held nearly every passing record when he retired. The quickest release the game has seen.", lines:["\"The fastest release in NFL history\"","\"Rewrote the passing record book — a Hall of Fame legend\""] },
  "Darrelle Revis":    { player:"Darrelle Revis",       team:"New York Jets", notes:"Revis Island. The best cover corner of his generation — receivers simply disappeared against him. Super Bowl champion, Hall of Famer, 7x Pro Bowler.", lines:["\"Welcome to Revis Island. Population: whoever he was covering.\"","\"The best shutdown corner of his era — a true Hall of Famer\""] },
  "Drew Brees":        { player:"Drew Brees",           team:"New Orleans Saints", notes:"Super Bowl XLIV champion and MVP. NFL all-time leader in completions and one of the most accurate passers ever. Rebuilt a city's spirit after Katrina. 13x Pro Bowler.", lines:["\"Brought a Super Bowl to New Orleans when the city needed it most\"","\"The most accurate passer of his generation\""] },
  "Duke of Denver":    { player:"John Elway",           team:"Denver Broncos", notes:"2x Super Bowl champion and 1987 MVP. The master of the comeback — 'The Drive' is legend. Went out on top with back-to-back titles. Hall of Famer.", lines:["\"The greatest comeback QB ever — The Drive lives forever\"","\"Back-to-back Super Bowls to end a Hall of Fame career\""] },
  "Emmitt Smith":      { player:"Emmitt Smith",         team:"Dallas Cowboys", notes:"The NFL's all-time leading rusher. 3x Super Bowl champion, 1993 MVP, Hall of Famer. The engine of the Cowboys dynasty.", lines:["\"The all-time leading rusher in NFL history\"","\"Three Super Bowls and the rushing crown — the engine of a dynasty\""] },
  "Eric Dickerson":    { player:"Eric Dickerson",       team:"LA Rams", notes:"Still holds the single-season rushing record (2,105 yards in 1984). Hall of Famer with the most graceful, upright running style the game has ever seen.", lines:["\"2,105 yards in a season — a record that has stood for 40 years\"","\"The most graceful runner in NFL history\""] },
  "Howie Long":        { player:"Howie Long",           team:"LA Raiders", notes:"Hall of Fame defensive end and the face of the Raiders' defense in the 80s. 8x Pro Bowler, Super Bowl champion, and later a beloved broadcaster.", lines:["\"The face of Raiders defense and a Hall of Fame force\""] },
  "Ickey Woods":       { player:"Ickey Woods",          team:"Cincinnati Bengals", notes:"The Ickey Shuffle made him a cultural icon. Powered the Bengals to Super Bowl XXIII as a rookie before injuries cut his career short.", lines:["\"The Ickey Shuffle — one of the most famous celebrations in NFL history\""] },
  "Jerome Bettis":     { player:"Jerome Bettis",        team:"Pittsburgh Steelers", notes:"The Bus. Hall of Fame power back who won Super Bowl XL in his hometown of Detroit in his final game. One of the most beloved players in Steelers history.", lines:["\"The Bus rolled to a Super Bowl in his hometown and rode off into the sunset\"","\"One of the greatest power backs — and finest people — the game has known\""] },
  "Jim Kelly":         { player:"Jim Kelly",            team:"Buffalo Bills", notes:"Hall of Fame QB who led Buffalo to four straight Super Bowls running the K-Gun no-huddle. A symbol of toughness and resilience on and off the field.", lines:["\"Four straight Super Bowls running the legendary K-Gun offense\"","\"The toughest QB Buffalo ever had — on and off the field\""] },
  "Jim McMahon":       { player:"Jim McMahon",          team:"Chicago Bears", notes:"The punky QB who led the 1985 Bears to a Super Bowl title. All swagger, headbands, and confidence — the perfect quarterback for the greatest defense ever.", lines:["\"The punky QB who led the '85 Bears to glory\"","\"Headbands, swagger, and a Super Bowl ring\""] },
  "John Elway":        { player:"John Elway",           team:"Denver Broncos", notes:"2x Super Bowl champion and 1987 MVP. The master of the comeback — 'The Drive' is legend. Went out on top with back-to-back titles. Hall of Famer.", lines:["\"The greatest comeback QB ever — The Drive lives forever\"","\"Back-to-back Super Bowls to end a Hall of Fame career\""] },
  "Krieghawk":         { player:"Dave Krieg",           team:"Seattle Seahawks", notes:"Undrafted out of tiny Milton College, Krieg became a 3x Pro Bowler and one of the most productive QBs of the 80s. The ultimate underdog story.", lines:["\"Undrafted from a school that no longer exists — and made three Pro Bowls\""] },
  "Lawrence Taylor":   { player:"Lawrence Taylor",      team:"New York Giants", notes:"The greatest defensive player in NFL history. 1986 MVP (rare for a defender), 2x Super Bowl champion, 10x Pro Bowler. He changed how offenses were built.", lines:["\"LT changed football forever — offenses were rebuilt to stop him\"","\"An MVP from the defensive side. The most disruptive force ever.\""] },
  "MAGIC":             { player:"Magic Johnson",        team:"Los Angeles Lakers", notes:"5x NBA champion, 3x MVP, the engine of Showtime. Redefined the point guard position at 6'9\". Famously played all five positions in a Finals game as a rookie.", lines:["\"Showtime. Five rings. The greatest playmaker the game has seen.\"","\"A 6'9\" point guard who changed basketball forever\""] },
  "Marcus Allen":      { player:"Marcus Allen",         team:"LA Raiders / KC Chiefs", notes:"Super Bowl XVIII MVP, NFL MVP, Hall of Famer. His reversed-field touchdown run in the Super Bowl is one of the most iconic plays ever.", lines:["\"That Super Bowl run — reversing the entire field — is pure legend\"","\"A Super Bowl MVP and one of the smartest runners ever\""] },
  "Marshall Faulk":    { player:"Marshall Faulk",       team:"St. Louis Rams", notes:"The centerpiece of the Greatest Show on Turf. 2000 MVP, Super Bowl champion, Hall of Famer. The most complete dual-threat back of his era.", lines:["\"The engine of the Greatest Show on Turf\"","\"An MVP who could beat you running OR catching — unstoppable\""] },
  "Mike Singletary":   { player:"Mike Singletary",      team:"Chicago Bears", notes:"The heart of the legendary '85 Bears defense. 2x Defensive Player of the Year, Hall of Famer, famous for the intensity in his eyes.", lines:["\"Those eyes — the most intense stare in football history\"","\"The soul of the greatest defense ever assembled\""] },
  "Puka Nacua":        { player:"Puka Nacua",           team:"Los Angeles Rams", notes:"Set the NFL rookie records for receptions and receiving yards in 2023 — out of the 5th round. One of the toughest, most productive young receivers in football.", lines:["\"A 5th-round rookie who broke records that stood for decades\"","\"The toughest young receiver in football — pure heart after the catch\""] },
  "Randall Cunningham": { player:"Randall Cunningham",  team:"Philadelphia Eagles", notes:"The original dual-threat QB. 4x Pro Bowler who could throw it 70 yards or take off for 50. Years ahead of his time — the blueprint for the modern QB.", lines:["\"The Ultimate Weapon — decades ahead of his time\"","\"Could throw it 70 yards or run for 50. Nobody had seen anything like it.\""] },
  "Randy Moss":        { player:"Randy Moss",           team:"Minnesota Vikings", notes:"The most physically gifted receiver ever. Hall of Famer, holds the single-season TD record (23). 'Straight cash, homie.' Defenses simply couldn't cover him.", lines:["\"You don't cover Randy Moss. You just hope.\"","\"23 touchdowns in a season — a record that may never fall\""] },
  "Richard Dent":      { player:"Richard Dent",         team:"Chicago Bears", notes:"Super Bowl XX MVP and Hall of Fame pass rusher. The closer on the greatest defense ever assembled — the '85 Bears.", lines:["\"Super Bowl MVP off the greatest defense ever\"","\"The closer on the '85 Bears — a Hall of Fame force\""] },
  "Rob Gronkowski":    { player:"Rob Gronkowski",       team:"New England Patriots", notes:"The greatest tight end of his era. 4x Super Bowl champion, Hall of Fame lock. An unstoppable mismatch — too big for DBs, too fast for linebackers.", lines:["\"Gronk — four rings and the most fun anyone's ever had playing football\"","\"Too big, too fast, too strong. The perfect tight end.\""] },
  "Ronnie Lott":       { player:"Ronnie Lott",          team:"SF 49ers", notes:"The hardest hitter in NFL history. 4x Super Bowl champion, Hall of Famer. Famously had part of a finger amputated so he wouldn't miss a game. Pure toughness.", lines:["\"Cut off part of his own finger to keep playing. That's Ronnie Lott.\"","\"Four Super Bowls and the hardest hits the game has ever seen\""] },
  "Ryptillian":        { player:"Mark Rypien",          team:"Washington Redskins", notes:"Super Bowl XXVI MVP. Led Washington to a dominant title in 1991 with one of the best seasons by a QB that decade.", lines:["\"Super Bowl MVP — led Washington to a dominant championship\""] },
  "Steve Largent":     { player:"Steve Largent",        team:"Seattle Seahawks", notes:"Seattle's first Hall of Famer. Held the major career receiving records when he retired. Proof that precise routes and great hands beat raw speed.", lines:["\"Seattle's first Hall of Famer — and one of the great route runners ever\""] },
  "Steve Young":       { player:"Steve Young",          team:"SF 49ers", notes:"2x MVP and Super Bowl XXIX MVP, where he threw a record 6 TDs. Hall of Famer who stepped out of Joe Montana's shadow to become a legend himself.", lines:["\"Six TDs in a Super Bowl — a record that still stands\"","\"Stepped out of Montana's shadow and became a legend\""] },
  "Swervin’":          { player:"Michael Irvin",        team:"Dallas Cowboys", notes:"The Playmaker. 3x Super Bowl champion, Hall of Famer, and the emotional engine of the Cowboys dynasty. One of the toughest competitors the game has seen.", lines:["\"The Playmaker — three rings and pure fire\"","\"The emotional heartbeat of the Cowboys dynasty\""] },
  "The Kid":           { player:"Ken Griffey Jr.",      team:"Seattle Mariners", notes:"The sweetest swing in baseball history. 13x All-Star, 1997 AL MVP, 630 home runs, Hall of Famer. The backwards cap, the smile — he made baseball cool.", lines:["\"The sweetest swing the game has ever seen\"","\"630 homers, a backwards cap, and the smile that saved baseball in Seattle\""] },
  "Thurman Thomas":    { player:"Thurman Thomas",       team:"Buffalo Bills", notes:"1991 MVP and the dual-threat engine of Buffalo's four straight Super Bowl teams. Hall of Famer who did everything — run, catch, and block.", lines:["\"An MVP and the engine of four straight Super Bowl runs\""] },
  "Tim Brown":         { player:"Tim Brown",            team:"LA/Oakland Raiders", notes:"Heisman winner and Hall of Fame receiver. Mr. Raider — held nearly every franchise receiving record. 9x Pro Bowler and a model of consistency.", lines:["\"Mr. Raider — a Heisman winner and Hall of Fame receiver\""] },
  "Troy Aikman":       { player:"Troy Aikman",          team:"Dallas Cowboys", notes:"3x Super Bowl champion and Super Bowl XXVII MVP. Hall of Famer and the steady hand that led the Cowboys dynasty of the 90s.", lines:["\"Three Super Bowls and the calm at the center of a dynasty\""] },
  "William Perry":     { player:"William Perry",        team:"Chicago Bears", notes:"The Refrigerator. A 335-pound defensive lineman who famously scored a rushing TD in Super Bowl XX. One of the most beloved cult figures in NFL history.", lines:["\"The Fridge — 335 pounds and a Super Bowl rushing touchdown\"","\"One of the most beloved characters football has ever produced\""] },
  "BARRY \"CUTBACK\" SANDERS": { player:"Barry Sanders", team:"Detroit Lions", notes:"The most electrifying runner in NFL history. 1997 co-MVP, 10x Pro Bowler, Hall of Famer. Walked away at 30 still in his prime, just shy of the all-time rushing record.", lines:["\"Nobody made tacklers miss like Barry. Nobody ever will.\""] },
  "Braun": { player:"A.J. Brown", team:"Philadelphia Eagles", notes:"Super Bowl champion and one of the most physically dominant receivers in football. Multiple-time Pro Bowler who overpowers defensive backs. A cornerstone of the Eagles' offense.", lines:["\"Too big, too strong, too fast — A.J. Brown is a problem\""] },
  "All day": { player:"Adrian Peterson", team:"Minnesota Vikings", notes:"One of the greatest running backs ever. 2012 MVP — came within 9 yards of the single-season rushing record the year after a torn ACL. 7x Pro Bowler, future Hall of Famer.", lines:["\"AD — came back from a torn ACL and nearly broke the rushing record\"","\"One of the most physically gifted runners the game has ever seen\""] },
  "Camera": { player:"Alvin Kamara", team:"New Orleans Saints", notes:"Explosive dual-threat back and Offensive Rookie of the Year. Multiple-time Pro Bowler and one of the best receiving backs in the NFL. A New Orleans fan favorite.", lines:["\"One of the most dangerous dual-threat backs in football\""] },
  "Dijon": { player:"Bijan Robinson", team:"Atlanta Falcons", notes:"A do-it-all running back and one of the brightest young stars in the NFL. Elite as a runner and receiver — the centerpiece of Atlanta's offense.", lines:["\"A complete back — runs, catches, and dazzles\""] },
  "Breeze": { player:"Breece Hall", team:"New York Jets", notes:"Explosive young running back and the engine of the Jets' offense. A home-run threat every time he touches the ball.", lines:["\"A breakaway threat on every single touch\""] },
  "Thomstone": { player:"Brian Thomas Jr.", team:"Jacksonville Jaguars", notes:"Record-setting rookie receiver who exploded onto the scene with one of the best debut seasons in Jaguars history. A rising star.", lines:["\"A rookie season that announced a future star\""] },
  "Caliber": { player:"Caleb Williams", team:"Chicago Bears", notes:"The #1 overall pick and Heisman winner out of USC. The franchise quarterback the Bears have searched decades for — electric arm talent and playmaking.", lines:["\"The #1 pick and a Heisman winner — Chicago's franchise QB\""] },
  "DeGenie": { player:"Cooper DeJean", team:"Philadelphia Eagles", notes:"Versatile defensive back who made a splash with a pick-six in the Super Bowl as a rookie. A rising playmaker in the Eagles' secondary.", lines:["\"A Super Bowl pick-six as a rookie — a star is born\""] },
  "Courthouse": { player:"Courtland Sutton", team:"Denver Broncos", notes:"Big, physical No. 1 receiver and a Pro Bowler. The Broncos' go-to target in the red zone.", lines:["\"Denver's big-bodied No. 1 — a red-zone nightmare\""] },
  "DeKap": { player:"DK Metcalf", team:"Pittsburgh Steelers", notes:"One of the most physically imposing receivers in NFL history. A freak athlete — 4.3 speed in a 230-pound frame. Pro Bowler now bringing his dominance to Pittsburgh.", lines:["\"A 230-pound receiver running a 4.3 — a genetic miracle\""] },
  "Joystick": { player:"Dante Hall", team:"Kansas City Chiefs", notes:"The Human Joystick. One of the greatest return men ever — his jukes and reverses made him appointment viewing. A Pro Bowl returner who could flip a game instantly.", lines:["\"The Human Joystick — the most electric return man of his era\""] },
  "Woody": { player:"Darren Woodson", team:"Dallas Cowboys", notes:"3x Super Bowl champion and the Cowboys' all-time leading tackler. A hard-hitting safety and the backbone of the Dallas dynasty's defense.", lines:["\"Three rings and the Cowboys' all-time leading tackler\""] },
  "D-Hop": { player:"DeAndre Hopkins", team:"Houston Texans", notes:"One of the best contested-catch receivers ever. Multiple-time All-Pro famous for impossible one-handed grabs. A perennial Pro Bowler.", lines:["\"Nobody wins a contested catch like Nuk Hopkins\""] },
  "Tank": { player:"Derrick Henry", team:"Tennessee Titans", notes:"King Henry. One of the most punishing power backs ever — a 2,000-yard rusher and Offensive Player of the Year. A 247-pound battering ram who runs defenders over.", lines:["\"King Henry — a 2,000-yard season and pure punishment\"","\"247 pounds and a stiff-arm that ends careers\""] },
  "Shells": { player:"Donnie Shell", team:"Pittsburgh Steelers", notes:"4x Super Bowl champion and Hall of Fame safety. A hard-hitting cornerstone of the legendary Steel Curtain defense.", lines:["\"Four Super Bowls anchoring the Steel Curtain\""] },
  "Flutie": { player:"Doug Flutie", team:"Buffalo Bills", notes:"Heisman winner famous for the 'Hail Mary' at Boston College. A dynamic, undersized QB who became a legend in the CFL and NFL alike. Pure magic.", lines:["\"The Hail Mary — one of the most famous plays in football history\""] },
  "Walk The Plank": { player:"Doug Plank", team:"Chicago Bears", notes:"The hard-hitting safety whose jersey number named the legendary '46 defense.' Pure intimidation — one of the most feared hitters of his era.", lines:["\"The '46 defense' was literally named after his jersey number\""] },
  "Draco": { player:"Drake Maye", team:"New England Patriots", notes:"The Patriots' young franchise quarterback and a top draft pick. Big arm, mobility, and the future of New England.", lines:["\"New England's franchise QB of the future\""] },
  "Steady Eddie": { player:"Eddie George", team:"Tennessee Titans", notes:"Offensive Rookie of the Year and 4x Pro Bowler. A workhorse back who powered the Titans to a Super Bowl. One of the toughest runners of his era.", lines:["\"A workhorse who carried the Titans to the Super Bowl\""] },
  "Tikoff": { player:"Fred Biletnikoff", team:"Oakland Raiders", notes:"Hall of Fame receiver and Super Bowl XI MVP. The most sure-handed possession receiver of his time — the NFL's college receiver award is named after him.", lines:["\"A Super Bowl MVP with the surest hands in football\""] },
  "Hitstick": { player:"Jadeveon Clowney", team:"Houston Texans", notes:"The #1 overall pick and one of the most explosive pass rushers of his generation. Multiple-time Pro Bowler known for game-wrecking plays.", lines:["\"A #1 overall pick built to wreck games\""] },
  "Jaybird": { player:"Jayden Daniels", team:"Washington Commanders", notes:"Heisman winner and Offensive Rookie of the Year who instantly transformed Washington. A dynamic dual-threat QB and one of the brightest young stars in football.", lines:["\"A Heisman, Rookie of the Year, and an instant franchise-changer\""] },
  "Waddler": { player:"Jaylen Waddle", team:"Miami Dolphins", notes:"One of the fastest receivers in football. Set a rookie reception record and forms half of Miami's electric receiver duo. A big-play machine.", lines:["\"Game-breaking speed — one of the fastest men in the NFL\""] },
  "Kurse": { player:"Jevon Kearse", team:"Tennessee Titans", notes:"The Freak. A terrifying edge rusher with absurd length and speed who set a rookie sack record. 3x Pro Bowler and one of the most feared pass rushers of his era.", lines:["\"The Freak — length and speed that terrorized quarterbacks\""] },
  "HowBout'em": { player:"Jimmy Johnson", team:"Dallas Cowboys", notes:"The coach who built the Cowboys dynasty — back-to-back Super Bowls and a national title at Miami. A Hall of Fame architect and master motivator.", lines:["\"How 'bout them Cowboys! Two Super Bowls and a dynasty built from scratch\""] },
  "Burrocious": { player:"Joe Burrow", team:"Cincinnati Bengals", notes:"Heisman winner, #1 overall pick, and the QB who led the Bengals back to the Super Bowl. Ice-cold under pressure with elite accuracy.", lines:["\"Joe Cool — a Heisman, a #1 pick, and a Super Bowl run\""] },
  "Thighs Man": { player:"Joe Theismann", team:"Washington Redskins", notes:"NFL MVP and Super Bowl XVII champion. Led Washington to back-to-back Super Bowls before the famous injury that became part of LT lore.", lines:["\"An MVP and Super Bowl champion who led Washington's golden era\""] },
  "Boltage": { player:"Justin Herbert", team:"Los Angeles Chargers", notes:"Offensive Rookie of the Year with a cannon for an arm. Set the rookie touchdown record and is one of the most physically gifted QBs in the league.", lines:["\"A rocket arm and rewritten rookie records — pure talent\""] },
  "Slash": { player:"Kordell Stewart", team:"Pittsburgh Steelers", notes:"Slash — the original positionless weapon who played QB, RB, and WR. A Pro Bowl quarterback who could beat you any way imaginable.", lines:["\"Slash — QB, RB, WR. He did it all before it was cool.\""] },
  "Grand-Ma-Ma": { player:"Larry Johnson", team:"Kansas City Chiefs", notes:"A bruising power back who set the single-season carries record. 2x Pro Bowler who carried the Chiefs' offense on his back.", lines:["\"A workhorse who set the record for carries in a season\""] },
  "Mic'd Up": { player:"Mike Evans", team:"Tampa Bay Buccaneers", notes:"Super Bowl champion with an NFL-record streak of 1,000-yard seasons to start a career. A massive, dominant red-zone threat and lock for the Hall of Fame.", lines:["\"A 1,000-yard season every single year — historic consistency\""] },
  "Exterminaber": { player:"Malik Nabers", team:"New York Giants", notes:"Record-setting rookie receiver out of LSU. Led the Giants in every receiving category as a rookie and is already one of the most dynamic young WRs in football.", lines:["\"A rookie season for the ages — the Giants' new star\""] },
  "Marverati": { player:"Marvin Harrison Jr.", team:"Arizona Cardinals", notes:"Son of a Hall of Famer and one of the most polished young receivers to enter the league in years. A top draft pick and the Cardinals' centerpiece.", lines:["\"NFL bloodlines and elite polish — a future star\""] },
  "Line-Art": { player:"Matt Leinart", team:"Arizona Cardinals", notes:"Heisman winner and national champion at USC. One of the most decorated college QBs ever before his NFL run with the Cardinals.", lines:["\"A Heisman winner and USC legend\""] },
  "Mean-Joe": { player:"Mean Joe Greene", team:"Pittsburgh Steelers", notes:"The cornerstone of the Steel Curtain. 4x Super Bowl champion, 2x Defensive Player of the Year, Hall of Famer. The most important player on one of the greatest defenses ever — and star of the iconic Coke commercial.", lines:["\"The heart of the Steel Curtain — four rings and a Coke ad legend\""] },
  "Hoge-Ball": { player:"Merril Hoge", team:"Pittsburgh Steelers", notes:"Tough, dependable Steelers running back who became a respected NFL analyst. A grinder who embodied Pittsburgh football.", lines:["\"A Pittsburgh grinder through and through\""] },
  "Jeesaw": { player:"Najee Harris", team:"Los Angeles Chargers", notes:"Pro Bowl running back who opened his career with multiple 1,000-yard seasons. A durable, physical workhorse.", lines:["\"1,000 yards out of the gate — a true workhorse\""] },
  "PG-D3": { player:"Pierre Garçon", team:"Washington Redskins", notes:"Led the NFL in receptions in 2013. A tough, reliable receiver who fought his way from a tiny college to a long, productive NFL career.", lines:["\"Led the league in catches — a small-school success story\""] },
  "Rodder": { player:"Rod Smith", team:"Denver Broncos", notes:"Undrafted receiver who became a 2x Super Bowl champion and 3x Pro Bowler. The greatest undrafted receiver in NFL history.", lines:["\"Undrafted to two Super Bowls — the ultimate grinder\""] },
  "Star-Bach": { player:"Roger Staubach", team:"Dallas Cowboys", notes:"Captain America. Heisman winner, 2x Super Bowl champion, Super Bowl VI MVP, Hall of Famer. The original comeback king who coined the term 'Hail Mary.'", lines:["\"Captain America — two rings and the original Hail Mary\""] },
  "Portalize": { player:"Sam LaPorta", team:"Detroit Lions", notes:"Record-setting rookie tight end who instantly became one of the best at his position. A key weapon in Detroit's resurgence.", lines:["\"A rookie tight end record and an instant star in Detroit\""] },
  "SaQuads": { player:"Saquon Barkley", team:"Philadelphia Eagles", notes:"Offensive Rookie of the Year and a Super Bowl champion. A 2,000-yard rusher with breathtaking athleticism — one of the most electric backs in football.", lines:["\"A 2,000-yard season and a ring — pure electricity\"","\"The most jaw-dropping runs in football belong to Saquon\""] },
  "Gravedigger": { player:"Stefon Diggs", team:"New England Patriots", notes:"Multiple-time Pro Bowl receiver famous for the 'Minneapolis Miracle.' An elite route runner and big-play threat throughout his career.", lines:["\"The Minneapolis Miracle — one of the most famous plays ever\""] },
  "Tongue-Twister": { player:"Tua Tagovailoa", team:"Miami Dolphins", notes:"National champion at Alabama and a Pro Bowl QB who led the NFL in passing. The trigger man for Miami's high-speed offense.", lines:["\"A national title hero leading Miami's fastest-ever offense\""] },
  "Cheetah": { player:"Tyreek Hill", team:"Miami Dolphins", notes:"The fastest man in the NFL. 8x Pro Bowler, Super Bowl champion, and the most explosive deep threat in football. Olympic-caliber speed.", lines:["\"The Cheetah — the fastest man in football, full stop\""] },
  "InVinceable": { player:"Vince Young", team:"Tennessee Titans", notes:"National champion at Texas (one of the greatest title games ever) and Offensive Rookie of the Year. A dynamic dual-threat QB and 2x Pro Bowler.", lines:["\"That Rose Bowl performance is the stuff of legend\""] },
  "X.L.": { player:"Xavier Legette", team:"Carolina Panthers", notes:"Explosive young receiver and a first-round pick. A big, physical playmaker for Carolina's developing offense.", lines:["\"A big, fast playmaker on the rise in Carolina\""] },
  "THOMSTONE": { player:"Brian Thomas Jr.", team:"Jacksonville Jaguars", notes:"Record-setting rookie receiver who exploded onto the scene with one of the best debut seasons in Jaguars history. A rising star.", lines:["\"A rookie season that announced a future star\""] },
  "CALIBER": { player:"Caleb Williams", team:"Chicago Bears", notes:"The #1 overall pick and Heisman winner out of USC. The franchise quarterback the Bears have searched decades for — electric arm talent and playmaking.", lines:["\"The #1 pick and a Heisman winner — Chicago's franchise QB\""] },
  "TANK": { player:"Derrick Henry", team:"Tennessee Titans", notes:"King Henry. One of the most punishing power backs ever — a 2,000-yard rusher and Offensive Player of the Year. A 247-pound battering ram who runs defenders over.", lines:["\"King Henry — a 2,000-yard season and pure punishment\"","\"247 pounds and a stiff-arm that ends careers\""] },
  "JAYBIRD": { player:"Jayden Daniels", team:"Washington Commanders", notes:"Heisman winner and Offensive Rookie of the Year who instantly transformed Washington. A dynamic dual-threat QB and one of the brightest young stars in football.", lines:["\"A Heisman, Rookie of the Year, and an instant franchise-changer\""] },
  "WADDLER": { player:"Jaylen Waddle", team:"Miami Dolphins", notes:"One of the fastest receivers in football. Set a rookie reception record and forms half of Miami's electric receiver duo. A big-play machine.", lines:["\"Game-breaking speed — one of the fastest men in the NFL\""] },
  "BURROCIOUS": { player:"Joe Burrow", team:"Cincinnati Bengals", notes:"Heisman winner, #1 overall pick, and the QB who led the Bengals back to the Super Bowl. Ice-cold under pressure with elite accuracy.", lines:["\"Joe Cool — a Heisman, a #1 pick, and a Super Bowl run\""] },
  "GRAND-MA-MA": { player:"Larry Johnson", team:"Kansas City Chiefs", notes:"A bruising power back who set the single-season carries record. 2x Pro Bowler who carried the Chiefs' offense on his back.", lines:["\"A workhorse who set the record for carries in a season\""] },
  "EXTERMINABER": { player:"Malik Nabers", team:"New York Giants", notes:"Record-setting rookie receiver out of LSU. Led the Giants in every receiving category as a rookie and is already one of the most dynamic young WRs in football.", lines:["\"A rookie season for the ages — the Giants' new star\""] },
  "XL": { player:"Xavier Legette", team:"Carolina Panthers", notes:"Explosive young receiver and a first-round pick. A big, physical playmaker for Carolina's developing offense.", lines:["\"A big, fast playmaker on the rise in Carolina\""] },
  "Gaveler": { player:"Aaron Judge", team:"New York Yankees", notes:"3x AL MVP and the captain of the Yankees. Set the AL single-season home run record with 62. A towering, generational slugger and the face of baseball.", lines:["\"62 home runs — the AL record. The Judge holds court.\"","\"A three-time MVP and the captain of the Yankees\""] },
  "Cruschman": { player:"Adley Rutschman", team:"Baltimore Orioles", notes:"#1 overall pick and All-Star catcher who became the cornerstone of Baltimore's revival. A switch-hitting leader behind the plate.", lines:["\"The #1 pick who revived the Orioles\""] },
  "Goofin' Off": { player:"Adrián Beltré", team:"Texas Rangers", notes:"Hall of Famer with over 3,000 hits and 477 home runs. One of the greatest defensive third basemen ever — and the most fun player in baseball, famous for hating his head being touched.", lines:["\"3,000 hits, a Gold Glove magician, and the most fun player ever\""] },
  "Awesome Dawson": { player:"Andre Dawson", team:"Montreal Expos", notes:"The Hawk. Hall of Famer, 1987 NL MVP, 8x All-Star. A five-tool legend who did it all on bad knees. One of the most respected players of his era.", lines:["\"The Hawk — an MVP and Hall of Famer who played through anything\""] },
  "Clutch": { player:"Andrew McCutchen", team:"Pittsburgh Pirates", notes:"2013 NL MVP and the face of the Pirates' resurgence. 5x All-Star and one of the most beloved players in Pittsburgh history.", lines:["\"Cutch — an MVP and the heart of Pittsburgh baseball\""] },
  "Curaçao Kid": { player:"Andruw Jones", team:"Atlanta Braves", notes:"One of the greatest defensive center fielders ever — 10 straight Gold Gloves. A power-hitting force who once hit 51 homers in a season.", lines:["\"The greatest defensive center fielder of his generation\""] },
  "Bern Baby, Bern": { player:"Bernie Williams", team:"New York Yankees", notes:"4x World Series champion and the elegant center fielder of the Yankees dynasty. 5x All-Star, batting champion, and an accomplished jazz guitarist.", lines:["\"Four rings in center field for the Yankees dynasty\""] },
  "Gigawitt": { player:"Bobby Witt Jr.", team:"Kansas City Royals", notes:"2x All-Star, 2x Gold Glover, and a batting champion. The franchise cornerstone of the Royals and one of the best all-around shortstops in baseball — power, speed, and defense.", lines:["\"The best all-around shortstop in baseball — and a Royals lifer\""] },
  "Rook": { player:"Brent Rooker", team:"Athletics", notes:"All-Star slugger who broke out as one of the most productive power hitters in the American League. A waiver-claim success story.", lines:["\"From waiver claim to All-Star slugger\""] },
  "Harpoon": { player:"Bryce Harper", team:"Philadelphia Phillies", notes:"2x NL MVP and the face of the Phillies. A #1 overall pick at 17 who became a generational superstar and a fearsome October performer.", lines:["\"Two MVPs and one of the most electric players of his era\""] },
  "BuckShot": { player:"Buck Showalter", team:"Baltimore Orioles", notes:"3x Manager of the Year who revived multiple franchises. One of the sharpest baseball minds of his generation.", lines:["\"A three-time Manager of the Year and master tactician\""] },
  "Alien": { player:"CJ Abrams", team:"Washington Nationals", notes:"All-Star shortstop with rare speed and power. A dynamic young building block for the Nationals.", lines:["\"Speed, power, and star potential up the middle\""] },
  "Dumper": { player:"Cal Raleigh", team:"Seattle Mariners", notes:"Big Dumper. All-Star catcher who set the single-season home run record for a catcher. A switch-hitting power source and Seattle fan favorite.", lines:["\"Big Dumper — the most home runs ever by a catcher in a season\""] },
  "The Ironman": { player:"Cal Ripken Jr.", team:"Baltimore Orioles", notes:"The Iron Man. Played 2,632 consecutive games — a record that may never fall. 2x MVP, 19x All-Star, Hall of Famer who redefined the shortstop position.", lines:["\"2,632 straight games — the most unbreakable record in sports\"","\"The Iron Man redefined what shortstops could be\""] },
  "Bionic": { player:"Carlos Correa", team:"Houston Astros", notes:"World Series champion, Rookie of the Year, and All-Star shortstop. A clutch postseason performer and defensive standout.", lines:["\"A World Series champ and Rookie of the Year at short\""] },
  "Pudge": { player:"Carlton Fisk", team:"Boston Red Sox", notes:"Hall of Fame catcher famous for waving the 1975 World Series walk-off homer fair — one of baseball's most iconic moments. 11x All-Star.", lines:["\"That wave — one of the most iconic moments in World Series history\""] },
  "Carroller": { player:"Corbin Carroll", team:"Arizona Diamondbacks", notes:"Rookie of the Year and All-Star outfielder. A five-tool speedster who powered the Diamondbacks to a pennant. One of the brightest young stars in the game.", lines:["\"Rookie of the Year and a five-tool electric talent\""] },
  "Cowboy": { player:"Corey Seager", team:"Texas Rangers", notes:"2x World Series MVP — one of only a few players ever to win it twice. A premier power-hitting shortstop and October legend.", lines:["\"Two World Series MVPs — a true October legend\""] },
  "Big Geo": { player:"Craig Biggio", team:"Houston Astros", notes:"Hall of Famer with over 3,000 hits. 7x All-Star who played catcher, second base, AND outfield at an elite level. The ultimate Astros lifer.", lines:["\"3,000 hits and a Hall of Fame career all in Houston\""] },
  "Lieutenant": { player:"Dansby Swanson", team:"Chicago Cubs", notes:"#1 overall pick, World Series champion, and Gold Glove shortstop. A steady defensive leader and clutch contributor.", lines:["\"A #1 pick, a World Series ring, and Gold Glove defense\""] },
  "Smoke": { player:"Dave Stewart", team:"Oakland Athletics", notes:"World Series MVP and one of the most feared big-game pitchers of his era. Won 20+ games four straight years and owned October.", lines:["\"The most intimidating big-game pitcher of his era\""] },
  "Big Papi": { player:"David Ortiz", team:"Boston Red Sox", notes:"Big Papi. Hall of Famer, 3x World Series champion, and the most clutch postseason hitter ever. The heart and soul of the Red Sox who ended the curse.", lines:["\"Big Papi — the most clutch October hitter who ever lived\"","\"The man who ended the curse in Boston\""] },
  "ECKS": { player:"Dennis Eckersley", team:"Oakland Athletics", notes:"Hall of Famer who won both the MVP and Cy Young as a relief ace. A dominant closer who redefined the role after a successful career as a starter.", lines:["\"MVP and Cy Young out of the bullpen — a true original\""] },
  "Donnie Baseball": { player:"Don Mattingly", team:"New York Yankees", notes:"Donnie Baseball. 1985 MVP, 9x Gold Glover, and one of the most beloved Yankees ever. A pure hitter and Captain in pinstripes.", lines:["\"Donnie Baseball — an MVP and a Yankee icon\""] },
  "BaldWing": { player:"Drake Baldwin", team:"Atlanta Braves", notes:"Rising young catcher and one of the Braves' top building blocks. A polished hitter behind the plate.", lines:["\"A rising star behind the plate in Atlanta\""] },
  "Crews-Missle": { player:"Dylan Crews", team:"Washington Nationals", notes:"#2 overall pick and one of the top young outfield prospects in baseball. A five-tool talent and the future of the Nationals.", lines:["\"A #2 overall pick and a five-tool future star\""] },
  "Gar": { player:"Edgar Martinez", team:"Seattle Mariners", notes:"Hall of Famer and the greatest designated hitter ever — the DH award is named after him. A pure hitting machine and Seattle legend.", lines:["\"The greatest DH ever — the award literally bears his name\""] },
  "Cruze Control": { player:"Elly De La Cruz", team:"Cincinnati Reds", notes:"All-Star phenom with jaw-dropping speed and power. One of the most electric young talents in baseball — a true must-watch player.", lines:["\"The most electric young talent in baseball — speed and power for days\""] },
  "Pet Dog": { player:"Ethan Petry", team:"NCAA / Prospect", notes:"A power-hitting college star and top prospect on the rise. One to watch as he climbs toward the big leagues.", lines:["\"A power-hitting prospect on the rise\""] },
  "Go-Cart": { player:"Evan Carter", team:"Texas Rangers", notes:"Burst onto the scene as a postseason hero in the Rangers' World Series run. A toolsy young outfielder with a bright future.", lines:["\"A postseason hero in Texas's World Series run\""] },
  "Grin": { player:"Francisco Lindor", team:"New York Mets", notes:"Mr. Smile. 4x All-Star, multiple Gold Gloves and Silver Sluggers. An MVP-caliber shortstop and the leader of the Mets.", lines:["\"Mr. Smile — an MVP-caliber shortstop and a born leader\""] },
  "MVFREE": { player:"Freddie Freeman", team:"Los Angeles Dodgers", notes:"2020 NL MVP, multiple-time World Series champion, and World Series MVP. One of the most consistent and beloved hitters of his generation.", lines:["\"An MVP, a World Series MVP, and a model of consistency\""] },
  "King Felix": { player:"Félix Hernández", team:"Seattle Mariners", notes:"King Felix. Cy Young winner who threw a perfect game. 6x All-Star and the ace who defined an era of Mariners baseball.", lines:["\"King Felix — a Cy Young, a perfect game, and a Seattle legend\""] },
  "Cha-Ching": { player:"Jackson Chourio", team:"Milwaukee Brewers", notes:"One of the youngest players to reach a 20-20 season. A dynamic five-tool outfielder and the future of the Brewers.", lines:["\"A 20-20 season as a young phenom — a future superstar\""] },
  "Merri-Go-Round": { player:"Jackson Merrill", team:"San Diego Padres", notes:"Burst onto the scene as an All-Star and Rookie of the Year contender. A smooth, talented young centerpiece for the Padres.", lines:["\"An All-Star in his first full season — a budding star\""] },
  "Bone": { player:"Jay Buhner", team:"Seattle Mariners", notes:"Bone. A fan-favorite slugger with a cannon arm, famous power, and one of the most beloved personalities in Mariners history.", lines:["\"Bone — a cannon arm and a Seattle cult hero\""] },
  "Rockstar": { player:"Jazz Chisholm Jr.", team:"New York Yankees", notes:"One of the most electric and stylish players in the game. A power-speed All-Star with charisma to spare.", lines:["\"Pure flash and power — the most stylish player in baseball\""] },
  "Palmer": { player:"Jim Palmer", team:"Baltimore Orioles", notes:"Hall of Famer with 3 Cy Young Awards and 3 World Series rings. One of the greatest pitchers of his era and an Orioles icon.", lines:["\"Three Cy Youngs and three rings — a Baltimore legend\""] },
  "Botto": { player:"Joey Votto", team:"Cincinnati Reds", notes:"2010 NL MVP and 6x All-Star. One of the best pure hitters and on-base machines of his generation — and one of baseball's great characters.", lines:["\"An MVP, an on-base machine, and a baseball philosopher\""] },
  "Caveman": { player:"Johnny Damon", team:"Boston Red Sox", notes:"2x World Series champion and a key spark of the 2004 curse-breaking Red Sox. A speedy, charismatic leadoff man.", lines:["\"A curse-breaker in Boston and a championship spark\""] },
  "Bomber": { player:"Yordan Alvarez", team:"Houston Astros", notes:"World Series champion and one of the most feared sluggers in baseball. An All-Star DH with terrifying power.", lines:["\"One of the scariest power hitters in the game\""] },
  "Ram": { player:"José Ramírez", team:"Cleveland Guardians", notes:"Multiple-time All-Star and Silver Slugger. A perennial MVP candidate and one of the most complete players in baseball — power, speed, and switch-hitting.", lines:["\"A perennial MVP candidate and Cleveland's cornerstone\""] },
  "Chosen": { player:"Juan Soto", team:"New York Mets", notes:"One of the best hitters on the planet. World Series champion, multiple-time All-Star, and owner of the most feared batting eye in baseball — 'the Soto Shuffle.'", lines:["\"The Soto Shuffle — the best batting eye in the game\"","\"A World Series champ and a generational hitter\""] },
  "Lightningrod": { player:"Julio Rodríguez", team:"Seattle Mariners", notes:"Rookie of the Year and multiple-time All-Star. A five-tool superstar and the electric face of the Mariners.", lines:["\"J-Rod — a five-tool superstar and Seattle's franchise face\""] },
  "Nice Game Pretty Boy": { player:"Keith Hernández", team:"New York Mets", notes:"MVP, World Series champion, and the greatest defensive first baseman ever — 11 Gold Gloves. A beloved broadcaster and baseball icon.", lines:["\"Eleven Gold Gloves — the best defensive first baseman ever\""] },
  "Sprinkler": { player:"Luis Arraez", team:"San Diego Padres", notes:"A rare 3x batting champion in BOTH leagues. The best pure contact hitter in baseball — nearly impossible to strike out.", lines:["\"Three batting titles — the best pure hitter in the game\""] },
  "Pantera": { player:"Luis Robert Jr.", team:"Chicago White Sox", notes:"All-Star center fielder and Gold Glover with rare power-speed tools. One of the most physically gifted players in baseball.", lines:["\"A five-tool talent with rare power and speed\""] },
  "Ollie": { player:"Matt Olson", team:"Atlanta Braves", notes:"All-Star slugger and Gold Glover who broke the Braves single-season home run record. An elite power-hitting first baseman.", lines:["\"A franchise home run record and elite power\""] },
  "Wargod": { player:"Mike Trout", team:"Los Angeles Angels", notes:"Arguably the greatest player of his generation. 3x AL MVP, 11x All-Star. A five-tool marvel widely regarded as a future first-ballot Hall of Famer.", lines:["\"Three MVPs and the best all-around player of his generation\""] },
  "Gambler": { player:"Mookie Betts", team:"Los Angeles Dodgers", notes:"Former AL MVP, multiple-time World Series champion, and one of the most complete players in baseball. A Gold Glove right fielder turned star shortstop.", lines:["\"An MVP, multiple rings, and one of the most complete players alive\""] },
  "Aeronaut": { player:"Nolan Arenado", team:"St. Louis Cardinals", notes:"10x Gold Glover — the greatest defensive third baseman of his era. A perennial All-Star and power threat.", lines:["\"Ten Gold Gloves — defensive wizardry at the hot corner\""] },
  "Wizard": { player:"Ozzie Smith", team:"St. Louis Cardinals", notes:"The Wizard of Oz. Hall of Famer and the greatest defensive shortstop ever — 13 straight Gold Gloves. Famous for his backflips and acrobatic plays.", lines:["\"The Wizard — the greatest defensive shortstop who ever lived\""] },
  "Skeee": { player:"Paul Skenes", team:"Pittsburgh Pirates", notes:"Rookie of the Year and an All-Star Game starter in his debut season. The most electric young pitcher in baseball with a triple-digit fastball.", lines:["\"The most electric young arm in baseball — pure heat\""] },
  "Gigante": { player:"Pedro Martínez", team:"Boston Red Sox", notes:"Hall of Famer with 3 Cy Young Awards. Pound-for-pound one of the most dominant pitchers in history during his legendary peak.", lines:["\"Three Cy Youngs and the most dominant peak any pitcher ever had\""] },
  "Snowbear": { player:"Pete Alonso", team:"New York Mets", notes:"The Polar Bear. Rookie of the Year who set the rookie home run record. A 2x Home Run Derby champion and the Mets' premier slugger.", lines:["\"The Polar Bear — a rookie home run record and Derby crowns\""] },
  "Matata": { player:"Ronald Acuña Jr.", team:"Atlanta Braves", notes:"2023 NL MVP and the first-ever 40-70 season (homers-steals). One of the most electric five-tool players in the game.", lines:["\"The first 40-70 season ever — pure electricity\"","\"An MVP and one of the most exciting players alive\""] },
  "Showtime": { player:"Shohei Ohtani", team:"Los Angeles Dodgers", notes:"The greatest two-way player since Babe Ruth — and beyond. Multiple-time MVP, World Series champion, and the first-ever 50-50 season. A global icon redefining what's possible.", lines:["\"50 homers AND 50 steals — nobody has done what Ohtani does\"","\"The greatest two-way player in baseball history\""] },
  "Torque": { player:"Spencer Torkelson", team:"Detroit Tigers", notes:"#1 overall pick and a power-hitting first baseman. A key building block in Detroit's resurgence.", lines:["\"A #1 overall pick with serious power\""] },
  "Turntable": { player:"Trea Turner", team:"Philadelphia Phillies", notes:"World Series champion, batting champion, and multiple-time All-Star. One of the fastest players in baseball with elite all-around skills.", lines:["\"A batting title, a ring, and game-breaking speed\""] },
  "Man of Steal": { player:"Vince Coleman", team:"St. Louis Cardinals", notes:"One of the greatest base stealers ever — led the NL in steals six straight years and topped 100 three times. Pure speed and chaos on the bases.", lines:["\"100+ steals in a season — pure base-path terror\""] },
  "Vampire": { player:"Vladimir Guerrero Jr.", team:"Toronto Blue Jays", notes:"All-Star slugger, Home Run Derby champion, and son of a Hall of Famer. One of the most feared right-handed bats in baseball.", lines:["\"Elite bloodlines and one of the scariest bats in the game\""] },
  "Chicken Man": { player:"Wade Boggs", team:"Boston Red Sox", notes:"Hall of Famer with 5 batting titles and over 3,000 hits. One of the greatest pure hitters ever — and famous for eating chicken before every game.", lines:["\"Five batting titles, 3,000 hits, and a whole lot of chicken\""] },
  "Thrill": { player:"Will Clark", team:"San Francisco Giants", notes:"The Thrill. 6x All-Star with one of the sweetest swings in baseball history. A clutch hitter and Giants legend.", lines:["\"The Thrill — one of the sweetest swings the game has seen\""] },
  "Game Over": { player:"Éric Gagné", team:"Los Angeles Dodgers", notes:"Cy Young-winning closer who converted an MLB-record 84 consecutive saves. 'Game Over' when he entered — one of the most dominant closer runs ever.", lines:["\"84 straight saves — when Gagné entered, it was Game Over\""] },
  "Superbaby": { player:"Rafael Devers", team:"Boston Red Sox", notes:"All-Star slugger and one of the most feared left-handed power bats in baseball. A Silver Slugger at the hot corner.", lines:["\"One of the most dangerous lefty bats in the game\""] },
  "Wild Bill": { player:"William Contreras", team:"Milwaukee Brewers", notes:"All-Star catcher and one of the best offensive backstops in baseball. The anchor of the Brewers.", lines:["\"One of the best hitting catchers in the game\""] },
  "Zephyr": { player:"Jung Hoo Lee", team:"San Francisco Giants", notes:"The Korean superstar known as the 'Grandson of the Wind.' A smooth, contact-hitting center fielder who brought his stardom to MLB.", lines:["\"The Grandson of the Wind — pure hitting grace\""] },
  "Lumber": { player:"James Wood", team:"Washington Nationals", notes:"Towering young slugger and All-Star with massive raw power. One of the brightest building blocks in Washington.", lines:["\"Towering power and a sky-high ceiling\""] },
  "Ozzmosis": { player:"Ozzie Albies", team:"Atlanta Braves", notes:"Switch-hitting All-Star second baseman and World Series champion. A dynamic spark and fan favorite in Atlanta.", lines:["\"A switch-hitting spark and a World Series champ\""] },
  "Bell-Camp": { player:"Kristian Campbell", team:"Boston Red Sox", notes:"Top young infield prospect who broke through as one of Boston's building blocks. A polished, athletic talent.", lines:["\"A breakout young talent in Boston\""] },
  "Bangford": { player:"Wyatt Langford", team:"Texas Rangers", notes:"Top draft pick and breakout outfielder with rare power-speed. A foundational piece for the Rangers.", lines:["\"Power, speed, and a fast track to stardom\""] },
  "Action": { player:"Jackson Holliday", team:"Baltimore Orioles", notes:"#1 overall pick and son of a big-leaguer. One of the top young infielders and the future of the Orioles.", lines:["\"A #1 overall pick and the future in Baltimore\""] },
  "J-Cam": { player:"Junior Caminero", team:"Tampa Bay Rays", notes:"All-MLB power-hitting third baseman and one of the most exciting young sluggers in the game.", lines:["\"Explosive power and a star on the rise\""] },
  "Kettle-Bell": { player:"Ketel Marte", team:"Arizona Diamondbacks", notes:"Multiple-time All-Star and MVP candidate. A switch-hitting force and the heart of the Diamondbacks.", lines:["\"An MVP-caliber switch-hitter and Arizona's leader\""] },
  "JacHammer": { player:"Jac Caglianone", team:"Kansas City Royals", notes:"Two-way college phenom and top draft pick with massive power. One of the most exciting prospects in baseball.", lines:["\"A two-way phenom with light-tower power\""] },
  "Rocket": { player:"Roger Clemens", team:"Boston Red Sox", notes:"The Rocket. 7x Cy Young winner — the most ever. 354 career wins and one of the most dominant pitchers in baseball history.", lines:["\"Seven Cy Youngs — the most dominant pitcher of his era\""] },
  "Unanimous Sandman": { player:"Mariano Rivera", team:"New York Yankees", notes:"The greatest closer ever and baseball's first unanimous Hall of Famer. 5x World Series champion with an all-time record 652 saves and a legendary cutter.", lines:["\"The first unanimous Hall of Famer — and the greatest closer ever\"","\"652 saves and one unhittable cutter\""] },
  "Bags": { player:"Jeff Bagwell", team:"Houston Astros", notes:"Hall of Famer, 1994 NL MVP, and Rookie of the Year. The greatest first baseman in Astros history with a legendary crouched stance.", lines:["\"An MVP and the greatest first baseman in Astros history\""] },
  "Mr. October": { player:"Reggie Jackson", team:"New York Yankees", notes:"Mr. October. Hall of Famer and 5x World Series champion famous for three home runs on three pitches in a single World Series game.", lines:["\"Mr. October — three homers on three pitches in the World Series\""] },
  "Tigre": { player:"Miguel Cabrera", team:"Detroit Tigers", notes:"Triple Crown winner (the first in 45 years), 2x MVP, and a 3,000-hit, 500-homer Hall of Famer. One of the greatest right-handed hitters ever.", lines:["\"A Triple Crown and a first-ballot Hall of Fame bat\""] },
  "Senior": { player:"Ken Griffey Sr.", team:"Cincinnati Reds", notes:"2x World Series champion with the Big Red Machine and a 3x All-Star. Famously played alongside his son Ken Griffey Jr. — even hitting back-to-back homers.", lines:["\"Back-to-back homers with his own son — a baseball first\""] },
  "Fingers": { player:"Rollie Fingers", team:"Oakland Athletics", notes:"Hall of Fame closer, MVP, Cy Young winner, and 3x World Series champion. The iconic handlebar mustache and one of the first dominant relief aces.", lines:["\"MVP, Cy Young, and the most iconic mustache in baseball\""] },
  "Smoltzie": { player:"John Smoltz", team:"Atlanta Braves", notes:"Hall of Famer and the only pitcher with 200+ wins AND 150+ saves. Cy Young winner and World Series champion — dominant as both a starter and closer.", lines:["\"The only pitcher to dominate as both a starter and a closer\""] },
  "Vlad the Impaler": { player:"Vladimir Guerrero Sr.", team:"Anaheim Angels", notes:"Hall of Famer and 2004 AL MVP. A free-swinging force who could hit any pitch — one of the most feared bats of his era and father of Vlad Jr.", lines:["\"Could hit anything thrown near the plate — a Hall of Fame bat\""] },
  "Bells": { player:"George Bell", team:"Toronto Blue Jays", notes:"1987 AL MVP and a feared slugger. The first Dominican-born MVP and a Blue Jays icon.", lines:["\"The first Dominican-born MVP — a Toronto legend\""] },
  "Rainestorm": { player:"Tim Raines", team:"Montreal Expos", notes:"Hall of Famer and one of the greatest leadoff hitters and base stealers ever — 808 career steals. A speed-and-on-base machine.", lines:["\"808 steals — one of the greatest leadoff men ever\""] },
  "The Red Baron": { player:"Rick Sutcliffe", team:"Chicago Cubs", notes:"1984 NL Cy Young winner and Rookie of the Year. A fierce competitor and fan favorite known as the Red Baron.", lines:["\"A Cy Young winner and fierce competitor\""] },
  "Captain Hook": { player:"Jack Morris", team:"Detroit Tigers", notes:"Hall of Famer and the ace of three World Series champions. Famous for his 10-inning shutout in Game 7 of the 1991 World Series — one of the greatest pitching performances ever.", lines:["\"That Game 7 ten-inning shutout is the stuff of legend\""] },
  "Highway to Helton": { player:"Todd Helton", team:"Colorado Rockies", notes:"Hall of Famer and the greatest player in Rockies history. A batting champion and 5x All-Star with a sweet left-handed swing.", lines:["\"The greatest Rockie ever — a Hall of Fame hitter\""] },
  "Tommy G": { player:"Tom Glavine", team:"Atlanta Braves", notes:"Hall of Famer with 305 wins and 2 Cy Young Awards. World Series MVP and a cornerstone of the Braves' historic pitching dynasty.", lines:["\"305 wins, two Cy Youngs, and a World Series MVP\""] },
  "Boston Strongboy": { player:"Jim Rice", team:"Boston Red Sox", notes:"Hall of Famer and 1978 AL MVP. One of the most feared sluggers of his era and a Red Sox icon for his entire career.", lines:["\"An MVP and one of the most feared bats of his time\""] },
  "Robin Hood": { player:"Robin Yount", team:"Milwaukee Brewers", notes:"Hall of Famer, 2x MVP, and a 3,000-hit club member. Spent his entire career with Milwaukee, starring at both shortstop and center field.", lines:["\"Two MVPs, 3,000 hits, and a lifelong Brewer\""] },
  "Mr. Clean": { player:"Steve Garvey", team:"Los Angeles Dodgers", notes:"1974 NL MVP, 10x All-Star, and World Series champion. A model of durability and consistency at first base — once played 1,207 straight games.", lines:["\"An MVP and an iron man at first base\""] },
  "Rollin'": { player:"Scott Rolen", team:"St. Louis Cardinals", notes:"Hall of Famer, World Series champion, and 8x Gold Glover. One of the greatest defensive third basemen ever with a powerful bat.", lines:["\"Eight Gold Gloves and a Hall of Fame glove at third\""] },
  "Macho": { player:"Manny Machado", team:"San Diego Padres", notes:"Multiple-time All-Star and Gold Glover. A power-hitting third baseman and one of the faces of the Padres.", lines:["\"A perennial All-Star and elite two-way third baseman\""] },
  "GAVELER": { player:"Aaron Judge", team:"New York Yankees", notes:"3x AL MVP and the captain of the Yankees. Set the AL single-season home run record with 62. A towering, generational slugger and the face of baseball.", lines:["\"62 home runs — the AL record. The Judge holds court.\"","\"A three-time MVP and the captain of the Yankees\""] },
  "THE IRONMAN": { player:"Cal Ripken Jr.", team:"Baltimore Orioles", notes:"The Iron Man. Played 2,632 consecutive games — a record that may never fall. 2x MVP, 19x All-Star, Hall of Famer who redefined the shortstop position.", lines:["\"2,632 straight games — the most unbreakable record in sports\"","\"The Iron Man redefined what shortstops could be\""] },
  "DONNIE BASEBALL": { player:"Don Mattingly", team:"New York Yankees", notes:"Donnie Baseball. 1985 MVP, 9x Gold Glover, and one of the most beloved Yankees ever. A pure hitter and Captain in pinstripes.", lines:["\"Donnie Baseball — an MVP and a Yankee icon\""] },
  "SKEEE": { player:"Paul Skenes", team:"Pittsburgh Pirates", notes:"Rookie of the Year and an All-Star Game starter in his debut season. The most electric young pitcher in baseball with a triple-digit fastball.", lines:["\"The most electric young arm in baseball — pure heat\""] },
  "SHOWTIME": { player:"Shohei Ohtani", team:"Los Angeles Dodgers", notes:"The greatest two-way player since Babe Ruth — and beyond. Multiple-time MVP, World Series champion, and the first-ever 50-50 season. A global icon redefining what's possible.", lines:["\"50 homers AND 50 steals — nobody has done what Ohtani does\"","\"The greatest two-way player in baseball history\""] },
  "THRILL": { player:"Will Clark", team:"San Francisco Giants", notes:"The Thrill. 6x All-Star with one of the sweetest swings in baseball history. A clutch hitter and Giants legend.", lines:["\"The Thrill — one of the sweetest swings the game has seen\""] },
  "Cruze-Control": { player:"Elly De La Cruz", team:"Cincinnati Reds", notes:"All-Star phenom with jaw-dropping speed and power. One of the most electric young talents in baseball — a true must-watch player.", lines:["\"The most electric young talent in baseball — speed and power for days\""] },
  "Pet Dogs": { player:"Ethan Petry", team:"NCAA / Prospect", notes:"A power-hitting college star and top prospect on the rise. One to watch as he climbs toward the big leagues.", lines:["\"A power-hitting prospect on the rise\""] },
  "Pet-Dog": { player:"Ethan Petry", team:"NCAA / Prospect", notes:"A power-hitting college star and top prospect on the rise. One to watch as he climbs toward the big leagues.", lines:["\"A power-hitting prospect on the rise\""] },
  "二百": { player:"Shohei Ohtani", team:"Los Angeles Dodgers", notes:"The greatest two-way player since Babe Ruth — and beyond. Multiple-time MVP, World Series champion, and the first-ever 50-50 season. A global icon redefining what's possible.", lines:["\"50 homers AND 50 steals — nobody has done what Ohtani does\"","\"The greatest two-way player in baseball history\""] },
  "百八十五": { player:"Shohei Ohtani", team:"Los Angeles Dodgers", notes:"The greatest two-way player since Babe Ruth — and beyond. Multiple-time MVP, World Series champion, and the first-ever 50-50 season. A global icon redefining what's possible.", lines:["\"50 homers AND 50 steals — nobody has done what Ohtani does\"","\"The greatest two-way player in baseball history\""] },
  "百四十": { player:"Shohei Ohtani", team:"Los Angeles Dodgers", notes:"The greatest two-way player since Babe Ruth — and beyond. Multiple-time MVP, World Series champion, and the first-ever 50-50 season. A global icon redefining what's possible.", lines:["\"50 homers AND 50 steals — nobody has done what Ohtani does\"","\"The greatest two-way player in baseball history\""] },
  "百三十五": { player:"Shohei Ohtani", team:"Los Angeles Dodgers", notes:"The greatest two-way player since Babe Ruth — and beyond. Multiple-time MVP, World Series champion, and the first-ever 50-50 season. A global icon redefining what's possible.", lines:["\"50 homers AND 50 steals — nobody has done what Ohtani does\"","\"The greatest two-way player in baseball history\""] },
  "百三十": { player:"Shohei Ohtani", team:"Los Angeles Dodgers", notes:"The greatest two-way player since Babe Ruth — and beyond. Multiple-time MVP, World Series champion, and the first-ever 50-50 season. A global icon redefining what's possible.", lines:["\"50 homers AND 50 steals — nobody has done what Ohtani does\"","\"The greatest two-way player in baseball history\""] },
  "Air Ace": { player:"Ace Bailey", team:"Utah Jazz", notes:"Top draft pick and high-flying wing with elite scoring tools. One of the most exciting young prospects in the NBA.", lines:["\"A high-flying scorer with a sky-high ceiling\""] },
  "Sarrtillery": { player:"Alexandre Sarr", team:"Washington Wizards", notes:"Top draft pick and athletic big man with rim-protecting and shot-making upside. A foundational piece for Washington.", lines:["\"A modern big with a sky-high ceiling\""] },
  "Simon Says": { player:"Anfernee Simons", team:"Boston Celtics", notes:"Explosive scoring guard and one of the best young shot-makers in the league. Deep range and big-time bounce.", lines:["\"One of the most explosive young scorers in the NBA\""] },
  "Unibrow": { player:"Anthony Davis", team:"Dallas Mavericks", notes:"The Brow. NBA champion, multiple-time All-Star and All-Defensive force. A two-way unicorn who anchors both ends — now the centerpiece in Dallas.", lines:["\"The Brow — a champion and one of the best two-way bigs alive\""] },
  "Ant Hill": { player:"Anthony Edwards", team:"Minnesota Timberwolves", notes:"Ant-Man. Explosive All-Star scorer and the electric face of the Timberwolves. One of the most exciting young superstars in basketball.", lines:["\"Ant-Man — explosive, fearless, and pure entertainment\""] },
  "Bammer": { player:"Bam Adebayo", team:"Miami Heat", notes:"All-Star and All-Defensive anchor. One of the most versatile defensive bigs in basketball and the heart of the Heat.", lines:["\"The defensive anchor and engine of Miami's culture\""] },
  "Cicada": { player:"Cade Cunningham", team:"Detroit Pistons", notes:"#1 overall pick and All-Star point guard. The franchise centerpiece who led Detroit's turnaround. A big, smooth lead guard.", lines:["\"A #1 pick who became Detroit's franchise cornerstone\""] },
  "Chiller": { player:"Cam Thomas", team:"Brooklyn Nets", notes:"One of the most gifted pure scorers in the league — a bucket-getter who can erupt for 40+ on any night.", lines:["\"A pure bucket-getter who can drop 40 at any moment\""] },
  "Billhook": { player:"Chauncey Billups", team:"Detroit Pistons", notes:"Mr. Big Shot. NBA champion and Finals MVP who led the 2004 Pistons to a title. A clutch point guard and Hall of Famer.", lines:["\"Mr. Big Shot — a Finals MVP and the ultimate clutch guard\""] },
  "ChetMate": { player:"Chet Holmgren", team:"Oklahoma City Thunder", notes:"NBA champion and All-Rookie unicorn — a shot-blocking, three-point-shooting big who anchored the Thunder's title run.", lines:["\"A unicorn big — rim protection and range in one frame\""] },
  "Boshpit": { player:"Chris Bosh", team:"Miami Heat", notes:"2x NBA champion and 11x All-Star. A skilled, versatile big and the third star of the Heat's championship Big Three. Hall of Famer.", lines:["\"Two rings and a Hall of Fame career — the Heat's versatile star\""] },
  "Mull & Bones": { player:"Chris Mullin", team:"Golden State Warriors", notes:"Hall of Famer and Dream Team member. One of the purest shooters and scorers of his era — a Run TMC legend in Golden State.", lines:["\"A Dream Teamer and one of the purest shooters ever\""] },
  "Amoeba": { player:"Collin Murray-Boyles", team:"Toronto Raptors", notes:"Versatile young two-way forward and a high draft pick. A switchable defender with a developing all-around game.", lines:["\"A versatile two-way forward on the rise\""] },
  "Maverick": { player:"Cooper Flagg", team:"Dallas Mavericks", notes:"#1 overall pick and 2025-26 Rookie of the Year — the first rookie since Michael Jordan to lead his team in points, rebounds, assists, and steals. The future of the Mavericks.", lines:["\"Rookie of the Year and the most hyped prospect since LeBron\"","\"Led his team in points, boards, assists, AND steals as a rookie — Jordan company\""] },
  "Time": { player:"Damian Lillard", team:"Portland Trail Blazers", notes:"Dame Time. Multiple-time All-Star and one of the greatest deep-range shooters and clutch scorers in NBA history. Famous for series-ending logo threes.", lines:["\"Dame Time — the most fearless clutch shooter in the game\""] },
  "Foxhole": { player:"De'Aaron Fox", team:"San Antonio Spurs", notes:"All-Star point guard and the fastest player in the league with the ball. A clutch scorer and electric lead guard.", lines:["\"The fastest player in basketball — and ice cold in the clutch\""] },
  "Thorn": { player:"DeMar DeRozan", team:"Sacramento Kings", notes:"Multiple-time All-Star and one of the premier mid-range scorers of his era. A relentless bucket-getter and respected veteran leader.", lines:["\"The king of the mid-range — a relentless scorer\""] },
  "Librarian": { player:"Devin Booker", team:"Phoenix Suns", notes:"All-Star scoring machine and Olympic gold medalist. A 70-point scorer and one of the best pure shooters and shot-makers in the league.", lines:["\"A 70-point game and one of the smoothest scorers alive\""] },
  "Sabotage": { player:"Domantas Sabonis", team:"Sacramento Kings", notes:"All-Star big man and a triple-double machine — led the NBA in rebounding and double-doubles. One of the best passing bigs in the game.", lines:["\"A triple-double machine and elite passing big\""] },
  "Windmill": { player:"Dominique Wilkins", team:"Atlanta Hawks", notes:"The Human Highlight Film. Hall of Famer, 9x All-Star, and one of the greatest dunkers and scorers ever. A 2x Slam Dunk champion.", lines:["\"The Human Highlight Film — the greatest in-game dunker ever\""] },
  "Spider": { player:"Donovan Mitchell", team:"Cleveland Cavaliers", notes:"Spida. Multiple-time All-Star and explosive scoring guard. A playoff riser who once dropped 71 points in a game.", lines:["\"Spida — a 71-point game and an All-Star scorer\""] },
  "D-Harp": { player:"Dylan Harper", team:"San Antonio Spurs", notes:"Top draft pick and son of an NBA champion. A big, talented lead guard and one of the most promising young players in the league.", lines:["\"NBA bloodlines and a star's skill set\""] },
  "Deep Freeze": { player:"George Gervin", team:"San Antonio Spurs", notes:"The Iceman. Hall of Famer, 4x scoring champion, and inventor of the finger roll. One of the smoothest scorers the game has ever seen.", lines:["\"The Iceman — the smoothest scorer and the finger-roll king\""] },
  "Freakshow": { player:"Giannis Antetokounmpo", team:"Milwaukee Bucks", notes:"The Greek Freak. NBA champion, Finals MVP, and 2x league MVP. A physical marvel and one of the most dominant two-way forces in NBA history.", lines:["\"The Greek Freak — an unstoppable two-way force\"","\"A champion, Finals MVP, and back-to-back league MVP\""] },
  "Jambox": { player:"Ja Morant", team:"Memphis Grizzlies", notes:"All-Star and Most Improved Player. One of the most electrifying, gravity-defying athletes in the league — must-see TV every night.", lines:["\"The most gravity-defying athlete in basketball\""] },
  "Green-Machine": { player:"Jalen Green", team:"Houston Rockets", notes:"Explosive young scoring guard and one of the most athletic players in the league. A high-flying bucket-getter.", lines:["\"Explosive athleticism and big-time scoring pop\""] },
  "Wild Beard": { player:"James Harden", team:"Los Angeles Clippers", notes:"The Beard. Former NBA MVP, 3x scoring champion, and one of the most prolific scorers and playmakers ever. A step-back legend and future Hall of Famer.", lines:["\"The Beard — an MVP and the step-back king\""] },
  "Juice": { player:"Jaylen Brown", team:"Boston Celtics", notes:"NBA champion and Finals MVP. An All-Star two-way wing and one of the most athletic, complete players in the league.", lines:["\"A Finals MVP and a do-it-all two-way star\""] },
  "Taze'em": { player:"Jayson Tatum", team:"Boston Celtics", notes:"NBA champion and multiple-time All-NBA First Teamer. One of the best two-way wings in basketball and the cornerstone of the Celtics.", lines:["\"A champion and one of the best two-way wings in the game\""] },
  "Jeeves": { player:"Jimmy Butler", team:"Golden State Warriors", notes:"Jimmy Buckets. Multiple-time All-Star famous for legendary playoff runs. One of the toughest, most clutch two-way competitors in the league.", lines:["\"Jimmy Buckets — the ultimate playoff riser\""] },
  "Processor": { player:"Joel Embiid", team:"Philadelphia 76ers", notes:"The Process. Former NBA MVP and scoring champion. A dominant, skilled big man and one of the most unstoppable offensive forces in the game.", lines:["\"The Process — an MVP and an unstoppable two-way big\""] },
  "KAT": { player:"Karl-Anthony Towns", team:"New York Knicks", notes:"All-Star big man and one of the greatest shooting bigs ever. A skilled, versatile scorer who stretches the floor.", lines:["\"One of the greatest shooting big men in NBA history\""] },
  "Cobra": { player:"Kevin Durant", team:"Houston Rockets", notes:"One of the greatest scorers in NBA history. 2x champion, 2x Finals MVP, former league MVP. An unguardable 7-footer with a perfect jumper.", lines:["\"One of the purest, most unguardable scorers ever\"","\"Two rings, two Finals MVPs — a walking bucket\""] },
  "Blockhale": { player:"Kevin McHale", team:"Boston Celtics", notes:"Hall of Famer and 3x NBA champion. One of the greatest low-post scorers ever with an unstoppable array of moves.", lines:["\"The greatest post moves the game has ever seen\""] },
  "Triple Threat": { player:"LaMelo Ball", team:"Charlotte Hornets", notes:"All-Star and Rookie of the Year. A flashy, creative playmaker with deep range and highlight-reel passing.", lines:["\"A flashy floor general with limitless range\""] },
  "LeBoss": { player:"LeBron James", team:"Los Angeles Lakers", notes:"The King. 4x NBA champion, 4x MVP, the all-time leading scorer, and a 20+ time All-Star. Widely considered one of the two greatest players ever.", lines:["\"The King — the all-time scoring leader and a forever GOAT contender\"","\"Four rings, four MVPs, and the scoring crown — the King\""] },
  "Donny Buckets": { player:"Luka Dončić", team:"Los Angeles Lakers", notes:"One of the best players on the planet. Multiple-time All-NBA First Teamer and a scoring champion. A triple-double machine now leading the Lakers after a stunning trade.", lines:["\"A walking triple-double and one of the best players alive\"","\"Led the NBA in scoring — now running the show in LA\""] },
  "Muggsy": { player:"Muggsy Bogues", team:"Charlotte Hornets", notes:"At 5'3\", the shortest player in NBA history — and a beloved fan favorite who proved heart beats height. A quick, pesky floor general.", lines:["\"5'3\" and fearless — proof that heart beats height\""] },
  "Mustang": { player:"Nikola Jokić", team:"Denver Nuggets", notes:"The Joker. NBA champion, Finals MVP, and 3x league MVP. The greatest passing big man ever and one of the most skilled players in NBA history.", lines:["\"The Joker — three MVPs and the best passing big ever\"","\"A champion, Finals MVP, and a basketball genius\""] },
  "Bandelero": { player:"Paolo Banchero", team:"Orlando Magic", notes:"#1 overall pick, Rookie of the Year, and All-Star. A big, skilled scoring forward and the cornerstone of the Magic.", lines:["\"A #1 pick and the future of Orlando\""] },
  "Chief": { player:"Robert Parish", team:"Boston Celtics", notes:"The Chief. Hall of Famer and 4x NBA champion. Played a record number of NBA games — the durable anchor of the Celtics dynasty.", lines:["\"The Chief — four rings and the most durable big man ever\""] },
  "Barnacle": { player:"Scottie Barnes", team:"Toronto Raptors", notes:"Rookie of the Year and All-Star. A versatile, do-it-all forward and the cornerstone of the Raptors' future.", lines:["\"A do-it-all forward and Toronto's building block\""] },
  "Shystep": { player:"Shai Gilgeous-Alexander", team:"Oklahoma City Thunder", notes:"NBA champion, Finals MVP, league MVP, and scoring champion. The smooth, unstoppable engine of the Thunder and one of the best players in the world.", lines:["\"An MVP, a champion, and the smoothest bucket-getter in the game\"","\"League MVP and Finals MVP — the face of OKC\""] },
  "Haymaker": { player:"Spencer Haywood", team:"Seattle SuperSonics", notes:"Hall of Famer and NBA champion whose Supreme Court case opened the door for early-entry players. A 4x All-Star and a pioneer of the game.", lines:["\"A Hall of Famer who changed the NBA forever off the court\""] },
  "Spudnick": { player:"Spud Webb", team:"Atlanta Hawks", notes:"At 5'7\", he stunned the world by winning the 1986 Slam Dunk Contest. One of the most beloved underdog stories in NBA history.", lines:["\"5'7\" and a Slam Dunk champion — the ultimate underdog\""] },
  "Midas": { player:"Stephen Curry", team:"Golden State Warriors", notes:"The greatest shooter in basketball history. 4x NBA champion, 2x MVP, the all-time three-point king. He revolutionized how the game is played.", lines:["\"The greatest shooter ever — he changed basketball forever\"","\"Four rings, two MVPs, and the most threes in history\""] },
  "Hard-Drive": { player:"Tim Hardaway", team:"Golden State Warriors", notes:"Hall of Famer famous for the UTEP Two-Step crossover. A 5x All-Star and one of the great point guards of the 90s.", lines:["\"The UTEP Two-Step — the deadliest crossover of its era\""] },
  "Frostbite": { player:"Trae Young", team:"Atlanta Hawks", notes:"All-Star floor general with limitless range and elite passing. One of the most dynamic offensive engines in the league.", lines:["\"Limitless range and elite passing — a scoring point guard\""] },
  "Victory": { player:"Victor Wembanyama", team:"San Antonio Spurs", notes:"Wemby. Rookie of the Year and a generational unicorn — a 7'4\" big who handles, shoots, and blocks everything. Widely seen as a future face of the league.", lines:["\"Wemby — a 7'4\" unicorn unlike anyone the game has ever seen\""] },
  "Risarcher": { player:"Zaccharie Risacher", team:"Atlanta Hawks", notes:"#1 overall pick and a smooth two-way wing. A promising young building block for the Hawks.", lines:["\"A #1 pick and a smooth two-way wing\""] },
  "Laviathan": { player:"Zach LaVine", team:"Sacramento Kings", notes:"2x Slam Dunk champion and All-Star. An explosive leaper and elite scorer with deep range.", lines:["\"A two-time dunk champ and explosive scorer\""] },
  "Big-Z": { player:"Zion Williamson", team:"New Orleans Pelicans", notes:"#1 overall pick and All-Star. A once-in-a-generation athlete — explosive power and finesse in a freakish frame.", lines:["\"A once-in-a-generation blend of power and explosiveness\""] },
  "Dr. J": { player:"Julius Erving", team:"Philadelphia 76ers", notes:"Dr. J. Hall of Famer, NBA champion, and league MVP. The man who took the game above the rim and made the dunk an art form.", lines:["\"Dr. J — the man who made flying above the rim an art form\""] },
  "Fungi": { player:"Kawhi Leonard", team:"Los Angeles Clippers", notes:"The Klaw. 2x NBA champion and 2x Finals MVP with two different teams. One of the best two-way players ever — elite defender and clutch scorer.", lines:["\"The Klaw — two rings, two Finals MVPs, and the best hands in the game\""] },
  "Marksman": { player:"Lauri Markkanen", team:"Utah Jazz", notes:"All-Star and Most Improved Player. A 7-foot sharpshooter who stretches the floor and scores in bunches.", lines:["\"A 7-foot sharpshooter and All-Star scorer\""] },
  "Burner": { player:"Jalen Brunson", team:"New York Knicks", notes:"All-NBA point guard and the heart of the Knicks. A crafty, clutch scorer who became one of the league's best lead guards.", lines:["\"The heart of the Knicks — clutch, crafty, and relentless\""] },
  "Destroya": { player:"Patrick Ewing", team:"New York Knicks", notes:"Hall of Famer, 11x All-Star, and the greatest Knick of all time. A dominant two-way center and Dream Team member.", lines:["\"The greatest Knick ever — a Hall of Fame two-way center\""] },
  "Castler": { player:"Stephon Castle", team:"San Antonio Spurs", notes:"Rookie of the Year and a big, two-way guard. A key young piece next to Wembanyama in San Antonio.", lines:["\"Rookie of the Year and a two-way building block\""] },
  "Haliburst": { player:"Tyrese Haliburton", team:"Indiana Pacers", notes:"All-Star point guard and one of the best passers in the league. A clutch playoff performer who led the Pacers on a deep run.", lines:["\"One of the best passers in the game and a clutch playoff riser\""] },
  "Maxed Out": { player:"Tyrese Maxey", team:"Philadelphia 76ers", notes:"All-Star and Most Improved Player. A lightning-quick scoring guard with deep range and relentless motor.", lines:["\"Lightning-quick and a knockdown scorer\""] },
  "Javalanche": { player:"Jamal Murray", team:"Denver Nuggets", notes:"NBA champion and one of the most clutch playoff performers in the league. The perfect backcourt partner to Jokić.", lines:["\"A champion and one of the great playoff shot-makers\""] },
  "Herro": { player:"Tyler Herro", team:"Miami Heat", notes:"All-Star and former Sixth Man of the Year. A fearless shot-maker and one of Miami's primary scorers.", lines:["\"A fearless shot-maker and Miami's microwave scorer\""] },
  "Dunker": { player:"John Starks", team:"New York Knicks", notes:"Famous for THE Dunk over the Bulls in the playoffs. A fiery, undrafted guard who became a fan-favorite All-Star with the Knicks.", lines:["\"The Dunk — one of the most iconic playoff moments ever\""] },
  "Generator": { player:"V.J. Edgecombe", team:"Philadelphia 76ers", notes:"Explosive young guard and a top draft pick. An athletic, high-upside building block for the Sixers.", lines:["\"An explosive athlete with a bright future\""] },
  "Ante-de-something A": { player:"Alex Antetokounmpo", team:"Milwaukee Bucks", notes:"The youngest of the Antetokounmpo brothers, carving out his own path in pro basketball.", lines:["\"Another Antetokounmpo chasing the family legacy\""] },
  "Ante-de-something T": { player:"Thanasis Antetokounmpo", team:"Milwaukee Bucks", notes:"NBA champion and one of the famous Antetokounmpo brothers. An energetic, high-motor wing who won a title alongside Giannis.", lines:["\"An NBA champion and a brother in the Antetokounmpo dynasty\""] },
  "Ante-de something K": { player:"Kostas Antetokounmpo", team:"Los Angeles Lakers", notes:"An NBA champion and one of the Antetokounmpo brothers — won a title with the Lakers.", lines:["\"A champion and part of basketball's most famous family\""] },
  "GREEN-MACHINE": { player:"Jalen Green", team:"Houston Rockets", notes:"Explosive young scoring guard and one of the most athletic players in the league. A high-flying bucket-getter.", lines:["\"Explosive athleticism and big-time scoring pop\""] },
  "LEBOSS": { player:"LeBron James", team:"Los Angeles Lakers", notes:"The King. 4x NBA champion, 4x MVP, the all-time leading scorer, and a 20+ time All-Star. Widely considered one of the two greatest players ever.", lines:["\"The King — the all-time scoring leader and a forever GOAT contender\"","\"Four rings, four MVPs, and the scoring crown — the King\""] },
  "MUGGSY": { player:"Muggsy Bogues", team:"Charlotte Hornets", notes:"At 5'3\", the shortest player in NBA history — and a beloved fan favorite who proved heart beats height. A quick, pesky floor general.", lines:["\"5'3\" and fearless — proof that heart beats height\""] },
  "CHIEF": { player:"Robert Parish", team:"Boston Celtics", notes:"The Chief. Hall of Famer and 4x NBA champion. Played a record number of NBA games — the durable anchor of the Celtics dynasty.", lines:["\"The Chief — four rings and the most durable big man ever\""] },
  "FROSTBITE": { player:"Trae Young", team:"Atlanta Hawks", notes:"All-Star floor general with limitless range and elite passing. One of the most dynamic offensive engines in the league.", lines:["\"Limitless range and elite passing — a scoring point guard\""] },
  "Frostebite": { player:"Trae Young", team:"Atlanta Hawks", notes:"All-Star floor general with limitless range and elite passing. One of the most dynamic offensive engines in the league.", lines:["\"Limitless range and elite passing — a scoring point guard\""] },
  "VICTORY": { player:"Victor Wembanyama", team:"San Antonio Spurs", notes:"Wemby. Rookie of the Year and a generational unicorn — a 7'4\" big who handles, shoots, and blocks everything. Widely seen as a future face of the league.", lines:["\"Wemby — a 7'4\" unicorn unlike anyone the game has ever seen\""] },
  "Wildbeard": { player:"James Harden", team:"Los Angeles Clippers", notes:"The Beard. Former NBA MVP, 3x scoring champion, and one of the most prolific scorers and playmakers ever. A step-back legend and future Hall of Famer.", lines:["\"The Beard — an MVP and the step-back king\""] },
};

// Case-insensitive bio lookup so spelling/caps variants (Bojax, BoJax, BOJAX) all resolve.
const _PLAYER_NOTES_CI = {};
for (const _k in PLAYER_NOTES) { _PLAYER_NOTES_CI[_k.toLowerCase().replace(/[^a-z0-9]/g,"")] = PLAYER_NOTES[_k]; }
function getPlayerNote(hero) {
  if (!hero) return null;
  return PLAYER_NOTES[hero] || _PLAYER_NOTES_CI[String(hero).toLowerCase().replace(/[^a-z0-9]/g,"")] || null;
}

function SetListView({ cards=[], owned={}, toggleOwned, activeSet, setActiveSet, expandedHero, setExpandedHero }) {
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [playerNotes, setPlayerNotes] = useState({});

  // Load notes from Firestore
  useEffect(()=>{
    const unsub = onSnapshot(collection(db,"player_notes"), snap=>{
      const n = {};
      snap.docs.forEach(d=>{ n[d.id] = d.data(); });
      setPlayerNotes(n);
    });
    return ()=>unsub();
  },[]);

  async function saveNote(hero) {
    const key = hero.replace(/[^a-zA-Z0-9]/g,"_");
    await setDoc(doc(db,"player_notes",key), {
      hero,
      notes: noteText,
      updatedAt: new Date().toISOString()
    });
    setEditingNote(null);
  }

  const sets = [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort();
  const curSet = activeSet || sets[0] || "";
  const heroes = [...new Set(cards.filter(c=>c.setName===curSet).map(c=>c.hero).filter(Boolean))].sort();

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {/* Set tabs */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {sets.map(s=>(
          <button key={s} onClick={()=>{setActiveSet(s);setExpandedHero(null);}}
            style={{background:curSet===s?"rgba(232,49,122,0.15)":"var(--bz-s1)",border:`1.5px solid ${curSet===s?"#E8317A":"var(--bz-s2)"}`,color:curSet===s?"#E8317A":"#888",borderRadius:20,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            {s}
          </button>
        ))}
      </div>

      <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>Tap any player to add broadcaster notes — bio, talking points, hype lines</div>

      {/* Player list */}
      {heroes.length === 0 && <div style={{color:"var(--bz-ink-3)",padding:24,textAlign:"center"}}>No players found in this set</div>}
      {heroes.map(hero => {
        const key = hero.replace(/[^a-zA-Z0-9]/g,"_");
        const isExpanded = expandedHero === hero;
        const hasCard = cards.filter(c=>c.setName===curSet&&c.hero===hero).some(c=>owned[c.id]);
        const saved = playerNotes[key];
        const builtIn = getPlayerNote(hero);

        return (
          <div key={hero} style={{background:"var(--bz-s1)",border:`1px solid ${isExpanded?"#E8317A33":hasCard?"rgba(74,222,128,0.2)":"var(--bz-s2)"}`,borderRadius:10,overflow:"hidden"}}>
            <div onClick={()=>setExpandedHero(isExpanded?null:hero)}
              style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",cursor:"pointer"}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:hasCard?"#4ade80":"var(--bz-line-2)",flexShrink:0}}/>
              <div style={{flex:1,fontSize:14,fontWeight:700,color:"var(--bz-ink)"}}>{hero}</div>
              {(saved?.notes || builtIn) && <span style={{fontSize:10,color:"#E8317A",background:"rgba(232,49,122,0.1)",border:"1px solid rgba(232,49,122,0.2)",borderRadius:6,padding:"2px 8px",fontWeight:700}}>🎙 Notes</span>}
              <span style={{color:"var(--bz-ink-3)",fontSize:11}}>{isExpanded?"▲":"▼"}</span>
            </div>

            {isExpanded && (
              <div style={{borderTop:"1px solid var(--bz-line)",padding:"14px 16px",background:"var(--bz-bg)"}}>
                {editingNote===hero ? (
                  <div style={{display:"flex",gap:8}}>
                    <textarea value={noteText} onChange={e=>setNoteText(e.target.value)}
                      placeholder={`Who is ${hero}? Career highlights, Tecmo legacy, hype lines for stream...`}
                      style={{flex:1,background:"var(--bz-s3)",border:"1px solid var(--bz-line)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 10px",fontSize:13,fontFamily:"inherit",minHeight:100,resize:"vertical",lineHeight:1.6}}/>
                    <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      <button onClick={()=>saveNote(hero)} style={{background:"rgba(74,222,128,0.15)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:6,padding:"6px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>Save</button>
                      <button onClick={()=>setEditingNote(null)} style={{background:"none",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:6,padding:"6px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {(saved?.notes || builtIn) ? <>
                  {builtIn && <>
                    <div style={{fontSize:11,color:"#E8317A",fontWeight:700,marginBottom:4}}>Inspired by: {builtIn.player} — {builtIn.team}</div>
                    <p style={{margin:"0 0 12px",fontSize:13,color:"var(--bz-ink-2)",lineHeight:1.6}}>{builtIn.notes}</p>
                    <div>
                      <div style={{fontSize:10,fontWeight:700,color:"#4ade80",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>🎙 Lines to Use</div>
                      {builtIn.lines.map((l,i)=>(
                        <div key={i} style={{background:"rgba(74,222,128,0.05)",border:"1px solid rgba(74,222,128,0.1)",borderRadius:7,padding:"8px 12px",marginBottom:6,fontSize:13,color:"var(--bz-ink)",fontStyle:"italic"}}>{l}</div>
                      ))}
                    </div>
                  </>}
                  {saved?.notes && <div style={{marginTop:12}}>
                    <div style={{fontSize:10,fontWeight:700,color:"#7B9CFF",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>📝 Your Notes</div>
                    <div style={{fontSize:13,color:"var(--bz-ink-2)",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{saved.notes}</div>
                  </div>}
                </> : <div style={{fontSize:12,color:"var(--bz-ink-3)"}}>No notes yet for {hero}</div>}
                    <button onClick={()=>{setEditingNote(hero);setNoteText(saved?.notes||"");}}
                      style={{background:"rgba(232,49,122,0.1)",border:"1px solid rgba(232,49,122,0.2)",color:"#E8317A",borderRadius:7,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                      {saved?.notes?"✏️ Edit Notes":"+ Add Notes"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
function BroadcasterNotes({ cards=[] }) {
  const [activeSet, setActiveSet]   = useState("");
  const [expandedHero, setExpandedHero] = useState(null);
  const [search, setSearch]         = useState("");
  const debouncedSearch = useDebounce(search, 250);
  const [playerNotes, setPlayerNotes] = useState({});
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText]     = useState("");

  useEffect(()=>{
    const unsub = onSnapshot(collection(db,"player_notes"), snap=>{
      const n={};
      snap.docs.forEach(d=>{ n[d.id]=d.data(); });
      setPlayerNotes(n);
    });
    return()=>unsub();
  },[]);

  async function saveNote(hero) {
    const key = hero.replace(/[^a-zA-Z0-9]/g,"_");
    await setDoc(doc(db,"player_notes",key),{ hero, notes:noteText, updatedAt:new Date().toISOString() });
    setEditingNote(null);
  }

  const sets = [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort();
  const curSet = activeSet || sets[0] || "";
  const allHeroes = [...new Set(cards.filter(c=>c.setName===curSet).map(c=>c.hero).filter(Boolean))].sort();
  const heroes = search ? allHeroes.filter(h=>h.toLowerCase().includes(search.toLowerCase())||(getPlayerNote(h)?.player||"").toLowerCase().includes(search.toLowerCase())) : allHeroes;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14,paddingBottom:40}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:22,fontWeight:900,color:"var(--bz-ink)"}}>🎙 Broadcaster Notes</div>
          <div style={{fontSize:12,color:"var(--bz-ink-3)",marginTop:2}}>Player bios, talking points, and hype lines for every pull</div>
        </div>
      </div>

      {/* Set tabs */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {sets.map(s=>(
          <button key={s} onClick={()=>{setActiveSet(s);setExpandedHero(null);setSearch("");}}
            style={{background:curSet===s?"rgba(232,49,122,0.15)":"var(--bz-s1)",border:`1.5px solid ${curSet===s?"#E8317A":"var(--bz-s2)"}`,color:curSet===s?"#E8317A":"#888",borderRadius:20,padding:"7px 18px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            {s} <span style={{color:"var(--bz-ink-3)",fontSize:10,marginLeft:4}}>({[...new Set(cards.filter(c=>c.setName===s).map(c=>c.hero).filter(Boolean))].length})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <input value={search} onChange={e=>setSearch(e.target.value)}
        placeholder="Search by hero name or player name..."
        style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:10,color:"var(--bz-ink)",padding:"10px 14px",fontSize:13,fontFamily:"inherit",outline:"none",width:"100%",boxSizing:"border-box"}}/>

      {/* Player list */}
      {heroes.length===0&&<div style={{color:"var(--bz-ink-3)",padding:32,textAlign:"center",fontSize:13}}>No players found</div>}
      {heroes.map(hero=>{
        const key = hero.replace(/[^a-zA-Z0-9]/g,"_");
        const isExpanded = expandedHero===hero;
        const builtIn = getPlayerNote(hero);
        const saved = playerNotes[key];
        const hasNotes = !!(builtIn||saved?.notes);

        return (
          <div key={hero} style={{background:"var(--bz-s1)",border:`1px solid ${isExpanded?"rgba(232,49,122,0.4)":"var(--bz-s2)"}`,borderRadius:12,overflow:"hidden",transition:"border-color 0.2s"}}>
            <div onClick={()=>setExpandedHero(isExpanded?null:hero)}
              style={{display:"flex",alignItems:"center",gap:12,padding:"14px 18px",cursor:"pointer"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:800,color:"var(--bz-ink)"}}>{hero}</div>
                {builtIn&&<div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:2}}>Inspired by {builtIn.player} — {builtIn.team}</div>}
              </div>
              {hasNotes&&<span style={{fontSize:10,color:"#E8317A",background:"rgba(232,49,122,0.1)",border:"1px solid rgba(232,49,122,0.2)",borderRadius:6,padding:"2px 8px",fontWeight:700,flexShrink:0}}>🎙 Notes</span>}
              <span style={{color:"var(--bz-ink-3)",fontSize:12,transition:"transform 0.2s",transform:isExpanded?"rotate(180deg)":"none"}}>▼</span>
            </div>

            {isExpanded&&(
              <div style={{borderTop:"1px solid var(--bz-line)",padding:"16px 18px",background:"var(--bz-bg)",display:"flex",flexDirection:"column",gap:14}}>
                {builtIn&&<>
                  <p style={{margin:0,fontSize:13,color:"var(--bz-ink-2)",lineHeight:1.7}}>{builtIn.notes}</p>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:"#4ade80",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>🎙 Lines to Use</div>
                    {builtIn.lines.map((l,i)=>(
                      <div key={i} style={{background:"rgba(74,222,128,0.05)",border:"1px solid rgba(74,222,128,0.12)",borderRadius:8,padding:"9px 12px",marginBottom:6,fontSize:13,color:"var(--bz-ink)",fontStyle:"italic",lineHeight:1.5}}>{l}</div>
                    ))}
                  </div>
                </>}
                {!builtIn&&!saved?.notes&&<div style={{fontSize:12,color:"var(--bz-ink-3)",fontStyle:"italic"}}>No built-in notes for {hero} yet</div>}
                {/* Custom notes */}
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#7B9CFF",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>📝 Your Notes</div>
                  {editingNote===hero?(
                    <div style={{display:"flex",gap:8}}>
                      <textarea value={noteText} onChange={e=>setNoteText(e.target.value)}
                        placeholder="Add your own lines, stories, anything that works on stream..."
                        style={{flex:1,background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",minHeight:90,resize:"vertical",lineHeight:1.6}}/>
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        <button onClick={()=>saveNote(hero)} style={{background:"rgba(74,222,128,0.15)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:7,padding:"7px 12px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>Save</button>
                        <button onClick={()=>setEditingNote(null)} style={{background:"none",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:7,padding:"7px 12px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                      </div>
                    </div>
                  ):(
                    <div onClick={()=>{setEditingNote(hero);setNoteText(saved?.notes||"");}}
                      style={{background:"rgba(123,156,255,0.04)",border:"1px dashed rgba(123,156,255,0.2)",borderRadius:8,padding:"10px 14px",fontSize:13,color:saved?.notes?"var(--bz-ink-2)":"#333",cursor:"pointer",fontStyle:"italic",lineHeight:1.5}}>
                      {saved?.notes||"+ Tap to add your own notes..."}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ── COMPANY DIRECTORY ────────────────────────────────────────────────────────
const DIR_GROUPS = ["Bazooka Breaks","IMC / BoBA","Vendor","Other"];

function SupplyLinks({ user, userRole }) {
  const isAdmin = ["Admin"].includes(userRole?.role);
  const [links,   setLinks]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [name,    setName]    = useState("");
  const [url,     setUrl]     = useState("");
  const [category,setCategory]= useState("Boxes");
  const [note,    setNote]    = useState("");
  const [saving,  setSaving]  = useState(false);
  const [filter,  setFilter]  = useState("All");

  const CATEGORIES = ["Boxes","Mailers","Supplies","Toploaders/Sleeves","Shipping","Other"];

  useEffect(() => {
    const unsub = onSnapshot(collection(db,"supply_links"), snap => {
      setLinks(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||"")));
      setLoading(false);
    });
    return ()=>unsub();
  }, []);

  async function addLink() {
    if(!name.trim()||!url.trim()||saving) return;
    let cleanUrl=url.trim();
    if(!/^https?:\/\//i.test(cleanUrl)) cleanUrl="https://"+cleanUrl;
    setSaving(true);
    try {
      const id=uid();
      await setDoc(doc(db,"supply_links",id),{
        id, name:name.trim(), url:cleanUrl, category, note:note.trim(),
        addedBy:user?.displayName||user?.email||"Someone", addedByUid:user?.uid||"",
        createdAt:new Date().toISOString(),
      });
      setName(""); setUrl(""); setNote(""); setCategory("Boxes");
    } catch(e){ console.error("add supply link failed:",e); }
    setSaving(false);
  }
  async function removeLink(l) {
    if(!(isAdmin || l.addedByUid===user?.uid)) return;
    if(!window.confirm(`Remove "${l.name}"?`)) return;
    try { await deleteDoc(doc(db,"supply_links",l.id)); } catch(e){ console.error(e); }
  }

  const shown = filter==="All" ? links : links.filter(l=>l.category===filter);
  const catColor = { Boxes:"#E8317A", Mailers:"#7B9CFF", Supplies:"#4ade80", "Toploaders/Sleeves":"#FBBF24", Shipping:"#C084FC", Other:"#888" };

  return (
    <div style={{ maxWidth:900, margin:"0 auto" }}>
      <div style={{ marginBottom:6, fontSize:22, fontWeight:900, color:"var(--bz-ink)" }}>🔗 Supply Links</div>
      <div style={{ fontSize:13, color:"var(--bz-ink-2)", marginBottom:20 }}>Shared links to boxes, mailers, and supplies. Anyone on the team can add one — paste a link, name it, and it's here for everyone.</div>

      {/* Add form */}
      <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:12, padding:16, marginBottom:20 }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="Item name (e.g. Bubble Mailers 6x9)" style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line)", borderRadius:8, padding:"10px 12px", fontSize:13, color:"var(--bz-ink)", fontFamily:"inherit" }}/>
          <select value={category} onChange={e=>setCategory(e.target.value)} style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line)", borderRadius:8, padding:"10px 12px", fontSize:13, color:"var(--bz-ink)", fontFamily:"inherit" }}>
            {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <input value={url} onChange={e=>setUrl(e.target.value)} placeholder="Paste the link (Amazon, Uline, etc.)" style={{ width:"100%", background:"var(--bz-s2)", border:"1px solid var(--bz-line)", borderRadius:8, padding:"10px 12px", fontSize:13, color:"var(--bz-ink)", fontFamily:"inherit", marginBottom:10, boxSizing:"border-box" }}/>
        <div style={{ display:"flex", gap:10 }}>
          <input value={note} onChange={e=>setNote(e.target.value)} placeholder="Optional note (size, price, qty per pack…)" style={{ flex:1, background:"var(--bz-s2)", border:"1px solid var(--bz-line)", borderRadius:8, padding:"10px 12px", fontSize:13, color:"var(--bz-ink)", fontFamily:"inherit" }}/>
          <button onClick={addLink} disabled={!name.trim()||!url.trim()||saving} style={{ background:(name.trim()&&url.trim())?"linear-gradient(135deg,var(--bz-pink),var(--bz-violet))":"var(--bz-s2)", color:(name.trim()&&url.trim())?"#fff":"var(--bz-ink-3)", border:"none", borderRadius:8, padding:"10px 24px", fontSize:13, fontWeight:800, cursor:(name.trim()&&url.trim())?"pointer":"not-allowed", fontFamily:"inherit", whiteSpace:"nowrap" }}>{saving?"Adding…":"Add Link"}</button>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" }}>
        {["All",...CATEGORIES].map(c=>(
          <button key={c} onClick={()=>setFilter(c)} style={{ background:filter===c?"var(--bz-pink-dim)":"transparent", color:filter===c?"var(--bz-pink-hot)":"var(--bz-ink-2)", border:`1px solid ${filter===c?"var(--bz-pink-hot)":"var(--bz-line)"}`, borderRadius:20, padding:"5px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{c}</button>
        ))}
      </div>

      {/* List */}
      {loading ? <div style={{ color:"var(--bz-ink-3)", textAlign:"center", padding:30 }}>Loading…</div> :
        shown.length===0 ? <div style={{ color:"var(--bz-ink-3)", textAlign:"center", padding:30 }}>No links yet{filter!=="All"?` in ${filter}`:""}. Add the first one above.</div> :
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {shown.map(l=>(
            <div key={l.id} style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"12px 14px", display:"flex", alignItems:"center", gap:12 }}>
              <span style={{ fontSize:9, fontWeight:800, color:catColor[l.category]||"#888", background:`${catColor[l.category]||"#888"}18`, border:`1px solid ${catColor[l.category]||"#888"}33`, borderRadius:6, padding:"3px 8px", textTransform:"uppercase", letterSpacing:0.5, whiteSpace:"nowrap" }}>{l.category}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:700, color:"var(--bz-ink)" }}>{l.name}</div>
                {l.note && <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>{l.note}</div>}
                <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>Added by {(l.addedBy||"").split(" ")[0]}</div>
              </div>
              <a href={l.url} target="_blank" rel="noopener noreferrer" style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line)", color:"var(--bz-pink-hot)", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:800, textDecoration:"none", whiteSpace:"nowrap" }}>Open ↗</a>
              {(isAdmin||l.addedByUid===user?.uid) && <button onClick={()=>removeLink(l)} title="Remove" style={{ background:"transparent", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:16 }}>×</button>}
            </div>
          ))}
        </div>
      }
    </div>
  );
}

function InternalMessages({ user, userRole }) {
  const [people,    setPeople]    = useState([]);   // team members (from directory + message history)
  const [threads,   setThreads]   = useState([]);   // threads I'm part of
  const [activeId,  setActiveId]  = useState(null);
  const [messages,  setMessages]  = useState([]);
  const [draft,     setDraft]     = useState("");
  const [showNew,   setShowNew]   = useState(false);
  const [newRecipients, setNewRecipients] = useState([]);
  const [newGroupName,  setNewGroupName]  = useState("");
  const meUid = user?.uid;
  const meName = user?.displayName || user?.email || "Me";

  // Load team members — STAFF ONLY. The internal dashboard is gated to @bazookabreaks.com,
  // so internal messages must be too. Emails live on boba_profiles, so we read from there.
  useEffect(() => {
    const unsub = onSnapshot(collection(db,"boba_profiles"), snap => {
      const staff = snap.docs.map(d=>({uid:d.id,...d.data()}))
        .filter(p => p.uid!==meUid && (p.email||"").toLowerCase().endsWith("@bazookabreaks.com"));
      setPeople(staff);
    });
    return ()=>unsub();
  }, [meUid]);

  // Load threads I'm a participant in
  useEffect(() => {
    if(!meUid) return;
    const unsub = onSnapshot(query(collection(db,"dm_threads"), where("participantUids","array-contains",meUid)),
      snap => setThreads(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.lastAt||"").localeCompare(a.lastAt||"")))
    );
    return ()=>unsub();
  }, [meUid]);

  // Opening a thread marks it read. Stamped per-user on the thread doc so it follows you between
  // devices rather than living in local state.
  useEffect(() => {
    if (!activeId || !meUid) return;
    setDoc(doc(db,"dm_threads",activeId), { lastReadBy: { [meUid]: new Date().toISOString() } }, { merge:true })
      .catch(e => console.error("mark read failed:", e));
  }, [activeId, meUid, messages.length]);

  // Load messages for the active thread
  useEffect(() => {
    if(!activeId) { setMessages([]); return; }
    const unsub = onSnapshot(query(collection(db,"dm_messages"), where("threadId","==",activeId)),
      snap => setMessages(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(a.sentAt||"").localeCompare(b.sentAt||"")))
    );
    return ()=>unsub();
  }, [activeId]);

  const activeThread = threads.find(t=>t.id===activeId);

  // A thread is unread when its last message is newer than your read stamp AND you did not send it.
  // Falling back to "unread" when there is no stamp is deliberate: a thread you have never opened
  // should announce itself rather than hide.
  function isUnread(t) {
    if (!t || !t.lastAt) return false;
    if (t.lastBy && t.lastBy === meUid) return false;
    const seen = (t.lastReadBy||{})[meUid];
    return !seen || String(seen) < String(t.lastAt);
  }
  const unreadCount = threads.filter(isUnread).length;

  function threadTitle(t) {
    if(t.name) return t.name;
    const others=(t.participants||[]).filter(p=>p.uid!==meUid);
    return others.map(p=>(p.name||"").split(" ")[0]).join(", ")||"Conversation";
  }

  async function startThread() {
    if(newRecipients.length===0) return;
    const isGroup=newRecipients.length>1;
    // For 1:1, reuse an existing thread if one exists
    if(!isGroup){
      const other=newRecipients[0];
      const existing=threads.find(t=>!t.name && (t.participantUids||[]).length===2 && (t.participantUids||[]).includes(other.uid));
      if(existing){ setActiveId(existing.id); setShowNew(false); setNewRecipients([]); return; }
    }
    const id=uid();
    const participants=[{uid:meUid,name:meName},...newRecipients.map(r=>({uid:r.uid,name:r.name||r.displayName||r.email||"Member"}))];
    await setDoc(doc(db,"dm_threads",id),{
      id, name:isGroup?(newGroupName.trim()||"Group Chat"):"",
      participants, participantUids:participants.map(p=>p.uid),
      createdBy:meUid, createdAt:new Date().toISOString(), lastAt:new Date().toISOString(), lastText:"",
    });
    setActiveId(id); setShowNew(false); setNewRecipients([]); setNewGroupName("");
  }

  async function send() {
    if(!draft.trim()||!activeThread) return;
    const text=draft.trim();
    setDraft("");
    const mid=uid();
    const now=new Date().toISOString();
    try {
      await setDoc(doc(db,"dm_messages",mid),{
        id:mid, threadId:activeId, text,
        fromUid:meUid, fromName:meName,
        participantUids:activeThread.participantUids||[],
        sentAt:now,
      });
      await setDoc(doc(db,"dm_threads",activeId),{ lastAt:now, lastText:text.slice(0,80), lastBy:meUid,
        // Sending is also reading: stamp yourself so your own message never shows as unread.
        lastReadBy:{ ...(activeThread?.lastReadBy||{}), [meUid]: now } },{merge:true});
    } catch(e){ console.error("send failed:",e); setDraft(text); }
  }

  function toggleRecipient(p) {
    setNewRecipients(prev => prev.some(r=>r.uid===p.uid) ? prev.filter(r=>r.uid!==p.uid) : [...prev,p]);
  }

  return (
    <div style={{ maxWidth:1000, margin:"0 auto" }}>
      <div style={{ marginBottom:6, fontSize:22, fontWeight:900, color:"var(--bz-ink)" }}>💬 Messages</div>
      <div style={{ fontSize:13, color:"var(--bz-ink-2)", marginBottom:16 }}>Private and group messages with the team. Direct messages are between you and one person; group chats include everyone you add.</div>

      <div style={{ display:"flex", gap:14, height:520, border:"1px solid var(--bz-line)", borderRadius:14, overflow:"hidden", background:"var(--bz-s1)" }}>
        {/* Thread list */}
        <div style={{ width:280, borderRight:"1px solid var(--bz-line)", display:"flex", flexDirection:"column" }}>
          <div style={{ padding:12, borderBottom:"1px solid var(--bz-line)" }}>
            <button onClick={()=>{ setShowNew(true); setActiveId(null); }} style={{ width:"100%", background:"linear-gradient(135deg,var(--bz-pink),var(--bz-violet))", color:"#fff", border:"none", borderRadius:8, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>+ New Message</button>
          </div>
          <div style={{ flex:1, overflowY:"auto" }}>
            {threads.length===0 ? <div style={{ padding:20, textAlign:"center", color:"var(--bz-ink-3)", fontSize:12 }}>No conversations yet.</div> :
              threads.map(t=>(
                <div key={t.id} onClick={()=>{ setActiveId(t.id); setShowNew(false); }} style={{ padding:"12px 14px", cursor:"pointer", borderBottom:"1px solid var(--bz-line)", background:activeId===t.id?"var(--bz-pink-dim)":(isUnread(t)?"rgba(232,49,122,0.07)":"transparent"), position:"relative" }}>
                  {/* Unread dot. Bold text alone is easy to miss in a list you scan quickly. */}
                  {isUnread(t) && <div style={{ position:"absolute", left:5, top:"50%", transform:"translateY(-50%)", width:7, height:7, borderRadius:"50%", background:"#E8317A", boxShadow:"0 0 6px rgba(232,49,122,0.8)" }}/>}
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ fontSize:13 }}>{t.name?"👥":"👤"}</span>
                    <span style={{ fontSize:13, fontWeight:isUnread(t)?900:700, color:isUnread(t)?"#fff":"var(--bz-ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{threadTitle(t)}</span>
                  </div>
                  {t.lastText && <div style={{ fontSize:11, color:isUnread(t)?"rgba(255,255,255,0.7)":"var(--bz-ink-3)", fontWeight:isUnread(t)?700:400, marginTop:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.lastText}</div>}
                </div>
              ))
            }
          </div>
        </div>

        {/* Main pane */}
        <div style={{ flex:1, display:"flex", flexDirection:"column" }}>
          {showNew ? (
            <div style={{ padding:20, overflowY:"auto" }}>
              <div style={{ fontSize:15, fontWeight:800, color:"var(--bz-ink)", marginBottom:12 }}>New Message</div>
              <div style={{ fontSize:12, color:"var(--bz-ink-2)", marginBottom:8 }}>Pick who to message (select more than one for a group):</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:14, maxHeight:220, overflowY:"auto" }}>
                {people.length===0 ? <div style={{ color:"var(--bz-ink-3)", fontSize:12 }}>No other team members found yet.</div> :
                  people.map(p=>{
                    const sel=newRecipients.some(r=>r.uid===p.uid);
                    const nm=p.displayName||p.name||p.email||"Member";
                    return (
                      <div key={p.uid} onClick={()=>toggleRecipient({uid:p.uid,name:nm})} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", borderRadius:8, cursor:"pointer", background:sel?"var(--bz-pink-dim)":"var(--bz-s2)", border:`1px solid ${sel?"var(--bz-pink-hot)":"var(--bz-line)"}` }}>
                        <span style={{ width:24, height:24, borderRadius:"50%", background:"linear-gradient(135deg,var(--bz-pink),var(--bz-violet))", display:"grid", placeItems:"center", fontSize:11, fontWeight:800, color:"#fff" }}>{nm.charAt(0)}</span>
                        <span style={{ fontSize:13, color:"var(--bz-ink)", fontWeight:sel?700:400 }}>{nm}</span>
                        {sel && <span style={{ marginLeft:"auto", color:"var(--bz-pink-hot)", fontWeight:800 }}>✓</span>}
                      </div>
                    );
                  })
                }
              </div>
              {newRecipients.length>1 && (
                <input value={newGroupName} onChange={e=>setNewGroupName(e.target.value)} placeholder="Group name (optional)" style={{ width:"100%", background:"var(--bz-s2)", border:"1px solid var(--bz-line)", borderRadius:8, padding:"10px 12px", fontSize:13, color:"var(--bz-ink)", fontFamily:"inherit", marginBottom:12, boxSizing:"border-box" }}/>
              )}
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={startThread} disabled={newRecipients.length===0} style={{ background:newRecipients.length?"linear-gradient(135deg,var(--bz-pink),var(--bz-violet))":"var(--bz-s2)", color:newRecipients.length?"#fff":"var(--bz-ink-3)", border:"none", borderRadius:8, padding:"10px 24px", fontSize:13, fontWeight:800, cursor:newRecipients.length?"pointer":"not-allowed", fontFamily:"inherit" }}>Start</button>
                <button onClick={()=>{ setShowNew(false); setNewRecipients([]); }} style={{ background:"transparent", border:"1px solid var(--bz-line)", color:"var(--bz-ink-2)", borderRadius:8, padding:"10px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
              </div>
            </div>
          ) : activeThread ? (
            <>
              <div style={{ padding:"14px 18px", borderBottom:"1px solid var(--bz-line)", display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:15 }}>{activeThread.name?"👥":"👤"}</span>
                <span style={{ fontSize:15, fontWeight:800, color:"var(--bz-ink)" }}>{threadTitle(activeThread)}</span>
                {activeThread.name && <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>· {(activeThread.participants||[]).length} members</span>}
              </div>
              <div style={{ flex:1, overflowY:"auto", padding:18, display:"flex", flexDirection:"column", gap:10 }}>
                {messages.length===0 ? <div style={{ color:"var(--bz-ink-3)", fontSize:12, textAlign:"center", margin:"auto" }}>No messages yet. Say hi 👋</div> :
                  messages.map(m=>{
                    const mine=m.fromUid===meUid;
                    return (
                      <div key={m.id} style={{ alignSelf:mine?"flex-end":"flex-start", maxWidth:"75%" }}>
                        {!mine && activeThread.name && <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginBottom:2, marginLeft:4 }}>{(m.fromName||"").split(" ")[0]}</div>}
                        <div style={{ background:mine?"linear-gradient(135deg,var(--bz-pink),var(--bz-violet))":"var(--bz-s2)", color:mine?"#fff":"var(--bz-ink)", borderRadius:14, padding:"9px 14px", fontSize:13, lineHeight:1.4, wordBreak:"break-word" }}>{m.text}</div>
                        <div style={{ fontSize:9, color:"var(--bz-ink-3)", marginTop:2, textAlign:mine?"right":"left", padding:"0 4px" }}>{m.sentAt?new Date(m.sentAt).toLocaleTimeString([],{hour:"numeric",minute:"2-digit"}):""}</div>
                      </div>
                    );
                  })
                }
              </div>
              <div style={{ padding:12, borderTop:"1px solid var(--bz-line)", display:"flex", gap:8 }}>
                <input value={draft} onChange={e=>setDraft(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); send(); } }} placeholder="Type a message…" style={{ flex:1, background:"var(--bz-s2)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"11px 14px", fontSize:13, color:"var(--bz-ink)", fontFamily:"inherit" }}/>
                <button onClick={send} disabled={!draft.trim()} style={{ background:draft.trim()?"linear-gradient(135deg,var(--bz-pink),var(--bz-violet))":"var(--bz-s2)", color:draft.trim()?"#fff":"var(--bz-ink-3)", border:"none", borderRadius:10, padding:"0 22px", fontSize:14, fontWeight:800, cursor:draft.trim()?"pointer":"not-allowed", fontFamily:"inherit" }}>Send</button>
              </div>
            </>
          ) : (
            <div style={{ flex:1, display:"grid", placeItems:"center", color:"var(--bz-ink-3)", fontSize:13, textAlign:"center", padding:20 }}>
              <div>Select a conversation, or start a new one.<br/><span style={{ fontSize:11 }}>Direct messages and group chats live here.</span></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CompanyDirectory({ userRole }) {
  const isAdmin = ["Admin"].includes(userRole?.role);
  const [people,    setPeople]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [editId,    setEditId]    = useState(null);
  const [search,    setSearch]    = useState("");
  const [groupF,    setGroupF]    = useState("All");
  const [saving,    setSaving]    = useState(false);

  const EMPTY = { name:"", role:"", group:"Bazooka Breaks", phone:"", email:"", discord:"", whatnot:"", notes:"" };
  const [form, setForm] = useState(EMPTY);
  const f = k => v => setForm(p=>({...p,[k]:v}));

  useEffect(() => {
    const unsub = onSnapshot(collection(db,"directory"), snap => {
      setPeople(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(a.name||"").localeCompare(b.name||"")));
      setLoading(false);
    });
    return ()=>unsub();
  }, []);

  async function savePerson() {
    if (!form.name.trim()) return;
    setSaving(true);
    const id = editId || uid();
    await setDoc(doc(db,"directory",id), { ...form, id, updatedAt:new Date().toISOString() }, { merge:true });
    setShowForm(false); setEditId(null); setForm(EMPTY); setSaving(false);
  }

  async function deletePerson(id, name) {
    if (!window.confirm(`Remove ${name} from directory?`)) return;
    await deleteDoc(doc(db,"directory",id));
  }

  function startEdit(p) { setForm({ name:p.name||"", role:p.role||"", group:p.group||"Bazooka Breaks", phone:p.phone||"", email:p.email||"", discord:p.discord||"", whatnot:p.whatnot||"", notes:p.notes||"" }); setEditId(p.id); setShowForm(true); }

  const filtered = people.filter(p =>
    (groupF === "All" || p.group === groupF) &&
    (!search || [p.name,p.role,p.email,p.discord].join(" ").toLowerCase().includes(search.toLowerCase()))
  );

  const grouped = DIR_GROUPS.reduce((acc,g)=>{ acc[g]=filtered.filter(p=>p.group===g); return acc; },{});

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14, padding:20 }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
        <div style={{ fontSize:18, fontWeight:900, color:"var(--bz-ink)" }}>📋 Company Directory</div>
        {isAdmin && <Btn onClick={()=>{ setForm(EMPTY); setEditId(null); setShowForm(true); }}>+ Add Person</Btn>}
      </div>

      {/* Search + group filter */}
      <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, role, email..."
          style={{ ...S.inp, flex:1, minWidth:200 }}/>
        <div style={{ display:"flex", gap:4 }}>
          {["All",...DIR_GROUPS].map(g=>(
            <button key={g} onClick={()=>setGroupF(g)}
              style={{ background:groupF===g?"rgba(232,49,122,0.12)":"var(--bz-s1)", border:`1.5px solid ${groupF===g?"#E8317A":"var(--bz-line-2)"}`, color:groupF===g?"#E8317A":"#888", borderRadius:7, padding:"6px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
              {g}
            </button>
          ))}
        </div>
      </div>

      {/* Add/Edit form */}
      {showForm && isAdmin && (
        <div style={{ ...S.card }}>
          <SectionLabel t={editId?"✏️ Edit Person":"+ Add Person"}/>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:10, marginBottom:14 }}>
            <div><label style={S.lbl}>Name *</label><input value={form.name} onChange={e=>f("name")(e.target.value)} placeholder="Full name" style={S.inp} autoFocus/></div>
            <div><label style={S.lbl}>Role / Title</label><input value={form.role} onChange={e=>f("role")(e.target.value)} placeholder="e.g. Streamer, CFO" style={S.inp}/></div>
            <div><label style={S.lbl}>Group</label>
              <select value={form.group} onChange={e=>f("group")(e.target.value)} style={{ ...S.inp, cursor:"pointer" }}>
                {DIR_GROUPS.map(g=><option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div><label style={S.lbl}>Phone</label><input value={form.phone} onChange={e=>f("phone")(e.target.value)} placeholder="555-555-5555" style={S.inp}/></div>
            <div><label style={S.lbl}>Email</label><input value={form.email} onChange={e=>f("email")(e.target.value)} placeholder="email@example.com" style={S.inp}/></div>
            <div><label style={S.lbl}>Discord</label><input value={form.discord} onChange={e=>f("discord")(e.target.value)} placeholder="@username" style={S.inp}/></div>
            <div><label style={S.lbl}>Whatnot</label><input value={form.whatnot} onChange={e=>f("whatnot")(e.target.value)} placeholder="@username" style={S.inp}/></div>
            <div style={{ gridColumn:"1/-1" }}><label style={S.lbl}>Notes</label><textarea value={form.notes} onChange={e=>f("notes")(e.target.value)} placeholder="Anything useful..." rows={2} style={{ ...S.inp, resize:"vertical" }}/></div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={savePerson} disabled={!form.name.trim()||saving}>{saving?"Saving...":"💾 Save"}</Btn>
            <Btn variant="ghost" onClick={()=>{ setShowForm(false); setEditId(null); }}>Cancel</Btn>
          </div>
        </div>
      )}

      {loading && <div style={{ textAlign:"center", padding:40, color:"var(--bz-ink-3)" }}>Loading...</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ ...S.card, textAlign:"center", padding:"40px 20px" }}>
          <div style={{ fontSize:32, opacity:0.2, marginBottom:10 }}>📋</div>
          <div style={{ fontSize:14, fontWeight:700, color:"var(--bz-ink-3)", marginBottom:6 }}>
            {search || groupF!=="All" ? "No results" : "Directory is empty"}
          </div>
          {isAdmin && !search && groupF==="All" && <Btn onClick={()=>{ setForm(EMPTY); setEditId(null); setShowForm(true); }}>+ Add First Person</Btn>}
        </div>
      )}

      {/* People grouped by category */}
      {DIR_GROUPS.map(group => {
        const list = grouped[group];
        if (!list?.length) return null;
        return (
          <div key={group}>
            <div style={{ fontSize:9, fontWeight:800, color:"#E8317A", textTransform:"uppercase", letterSpacing:"2px", marginBottom:10, display:"flex", alignItems:"center", gap:8 }}>
              {group}
              <div style={{ flex:1, height:1, background:"linear-gradient(90deg,rgba(232,49,122,0.3),transparent)" }}/>
              <span style={{ fontSize:10, color:"var(--bz-ink-3)", fontWeight:500, textTransform:"none", letterSpacing:0 }}>{list.length}</span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:10 }}>
              {list.map(p => (
                <div key={p.id} className="card-hover" style={{ ...S.card, padding:"14px 16px" }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:8 }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:800, color:"var(--bz-ink)" }}>{p.name}</div>
                      {p.role && <div style={{ fontSize:11, color:"#E8317A", marginTop:2 }}>{p.role}</div>}
                    </div>
                    {isAdmin && (
                      <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                        <button onClick={()=>startEdit(p)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:5, padding:"3px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>✏️</button>
                        <button onClick={()=>deletePerson(p.id,p.name)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"#8a8a8a", borderRadius:5, padding:"3px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>✕</button>
                      </div>
                    )}
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                    {p.phone   && <a href={`tel:${p.phone}`}   style={{ fontSize:12, color:"#7B9CFF", textDecoration:"none", display:"flex", alignItems:"center", gap:6 }}>📞 {p.phone}</a>}
                    {p.email   && <a href={`mailto:${p.email}`} style={{ fontSize:12, color:"#7B9CFF", textDecoration:"none", display:"flex", alignItems:"center", gap:6 }}>✉️ {p.email}</a>}
                    {p.discord && <div style={{ fontSize:12, color:"var(--bz-ink-2)", display:"flex", alignItems:"center", gap:6 }}>💬 {p.discord}</div>}
                    {p.whatnot && <div style={{ fontSize:12, color:"var(--bz-ink-2)", display:"flex", alignItems:"center", gap:6 }}>🎥 {p.whatnot}</div>}
                    {p.notes   && <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:4, paddingTop:6, borderTop:"1px solid var(--bz-line)", lineHeight:1.5 }}>{p.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── MANUAL CARD IMAGE UPLOADER ────────────────────────────────────────────────
function ManualCardImage() {
  const [query,    setQuery]    = useState("");
  const [results,  setResults]  = useState([]);
  const [selected, setSelected] = useState(null);
  const [img,      setImg]      = useState(null);
  const [uploading,setUploading]= useState(false);
  const [done,     setDone]     = useState(false);

  async function search() {
    if (!query.trim()) return;
    const q = query.trim().toLowerCase();
    const snap = await getDocs(collection(db,"boba_checklist"));
    const hits = snap.docs.map(d=>({...d.data(), fsId:d.id})).filter(c =>
      [c.hero||"",c.cardNum||"",c.treatment||"",c.setName||""].join(" ").toLowerCase().includes(q)
    ).slice(0,20);
    setResults(hits);
  }

  async function upload() {
    if (!selected || !img) return;
    setUploading(true);
    try {
      const safe = `${selected.setName||"set"}_${selected.cardNum||"x"}_${selected.treatment||"t"}`.replace(/[^a-zA-Z0-9_]/g,"_");
      const storageRef2 = ref(storage, `boba_cards/manual/${safe}.png`);
      await uploadBytes(storageRef2, img);
      const url = await getDownloadURL(storageRef2);
      await setDoc(doc(db,"boba_checklist",selected.fsId), { imageUrl:url }, {merge:true});
      setDone(true); setImg(null); setSelected(null); setResults([]); setQuery("");
      try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
    } catch(e) { alert("Upload failed: "+e.message); }
    setUploading(false);
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
      {done && <div style={{ fontSize:12, color:"#4ade80" }}>✅ Image saved — refresh checklist to see it</div>}

      {/* Search */}
      <div style={{ display:"flex", gap:8 }}>
        <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search hero, card #, treatment..."
          onKeyDown={e=>e.key==="Enter"&&search()}
          style={{ ...S.inp, flex:1, fontSize:12 }}/>
        <Btn onClick={search}>Search</Btn>
      </div>

      {/* Results */}
      {results.length > 0 && !selected && (
        <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:200, overflowY:"auto" }}>
          {results.map(c=>(
            <div key={c.fsId} onClick={()=>{ setSelected(c); setResults([]); setDone(false); }}
              style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:8, padding:"8px 12px", cursor:"pointer", fontSize:12 }}>
              <span style={{ color:"var(--bz-ink)", fontWeight:700 }}>{c.hero}</span>
              <span style={{ color:"var(--bz-ink-3)", marginLeft:8 }}>#{c.cardNum}</span>
              <span style={{ color:"var(--bz-ink-2)", marginLeft:8 }}>{c.treatment}</span>
              {c.imageUrl && <span style={{ color:"#4ade80", marginLeft:8, fontSize:10 }}>has image</span>}
            </div>
          ))}
        </div>
      )}

      {/* Selected card + image drop */}
      {selected && (
        <div style={{ ...S.card }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)" }}>{selected.hero}</div>
              <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>#{selected.cardNum} · {selected.treatment}</div>
            </div>
            <button onClick={()=>{ setSelected(null); setImg(null); }} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:16 }}>✕</button>
          </div>

          {img ? (
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
              <img src={URL.createObjectURL(img)} alt="" style={{ width:60, height:80, objectFit:"cover", borderRadius:6, border:"1px solid var(--bz-line-2)" }}/>
              <div>
                <div style={{ fontSize:12, color:"var(--bz-ink)" }}>{img.name}</div>
                <button onClick={()=>setImg(null)} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:11 }}>remove</button>
              </div>
            </div>
          ) : (
            <div onDragOver={e=>e.preventDefault()} onDrop={e=>{ e.preventDefault(); const f=e.dataTransfer.files[0]; if(f)setImg(f); }}
              style={{ border:"2px dashed var(--bz-line-2)", borderRadius:10, padding:"24px", textAlign:"center", marginBottom:12, cursor:"pointer" }}>
              <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>Drop image here or</div>
              <input type="file" accept="image/*" onChange={e=>setImg(e.target.files[0])} style={{ display:"none" }} id="manual-img"/>
              <label htmlFor="manual-img" style={{ color:"#7B9CFF", fontSize:11, cursor:"pointer", textDecoration:"underline" }}>browse</label>
            </div>
          )}

          <Btn onClick={upload} disabled={!img||uploading} variant="green">
            {uploading ? "Uploading..." : "⬆️ Upload Image"}
          </Btn>
        </div>
      )}
    </div>
  );
}

// ── DATA CLEANUP ──────────────────────────────────────────────────────────────
const CANONICAL_WEAPONS = ["Steel","Brawl","Fire","Ice","Glow","Hex","Gum","Super"];

// SKU availability by treatment
const SKU_MAP = {
  // All SKUs
  "Inspired Ink Battlefoil":         "all",
  "Inspired Ink Metallic Battlefoil": "hobby",
  "Inspired Ink Superfoil":           "all",
  "Alpha Battlefoil":                 "all",
  "Helmet Icon Battlefoil":           "all",
  "Tecmo News Battlefoil":            "all",
  "80's 8-Bit Rad Battlefoil":        "all",
  "Scoreboard Battlefoil":            "all",
  "Logofoil":                         "all",
  "Tecmo Bowl Logofoil":              "all",
  "Battlefoil":                       "all",
  "Paper Plays":                      "all",
  "Sore Thumb Battlefoil":            "all",
  "Rage Quit Battlefoil":             "all",
  "Bonus Plays":                      "double",
  "Base":                             "all",
  "Superfoil":                        "all",
  "Bubble Gum Battlefoil":            "hobby",
  "Blow on it Battlefoil":            "hobby",
  "Gold Coin Flip Battlefoil":        "hobby",
  "Skyline Fire Battlefoil":          "hobby",
  "Skyline Ice Battlefoil":           "hobby",
  "8-Bit Alt Art":                    "hobby",
  "Red Battlefoil":                   "hobby",
  "Silver Battlefoil":                "hobby",
  "Blue Battlefoil":                  "hobby",
  "Orange Battlefoil":                "hobby",
  "Green Battlefoil":                 "hobby",
  "Pink Battlefoil":                  "hobby",
  "Halftime Alt Art":                 "hobby",
  // Double Mega Only
  "Endzone Battlefoil":               "double",
  "Skyline Glow Battlefoil":          "double",
  "Big Pixel Battlefoil":             "double",
  "Silver Coin Flip Battlefoil":      "double",
  "Helmets Battlefoil":               "double",
  // Day One Double Mega
  "Pixel Helmet Alt":                 "dayone",
};

// Where a card can be pulled from. A treatment can appear in more than one pack type, and the
// answer differs BY SET — Bubble Gum might be Hobby-only in one release and Double Mega in the
// next — so this is edited per set in the importer's "Found In" tool and stored in Firestore
// (config/found_in), not hardcoded here.
const PACK_TYPES = [
  { id:"hobby",         label:"Hobby",                 color:"#7B9CFF" },
  { id:"booster",       label:"Booster",               color:"#4ade80" },
  { id:"jumbo",         label:"Jumbo",                 color:"#C084FC" },
  { id:"double",        label:"Double Mega",           color:"#E8317A" },
  { id:"blaster",       label:"Blaster",               color:"#FB923C" },
  { id:"rdblaster",     label:"Release Day Blaster",   color:"#F472B6" },
  { id:"rddouble",      label:"Release Day Double Mega", color:"#FBBF24" },
];
const PACK_BY_ID = Object.fromEntries(PACK_TYPES.map(p => [p.id, p]));

// Where can this card be pulled from? Prefers the live per-set map (config/found_in, edited in the
// importer's "Found In" tool); falls back to the old global SKU_MAP for sets not yet mapped.
function foundInFor(card, foundInMap) {
  const key = `${card.setName}|${card.treatment || "(none)"}`;
  const packs = foundInMap && foundInMap[key];
  if (packs && packs.length) {
    return packs.map(id => PACK_BY_ID[id]).filter(Boolean);
  }
  const sku = SKU_MAP[card.treatment];
  const legacy = sku && SKU_LABEL[sku];
  return legacy ? [{ id: sku, label: legacy.label, color: legacy.color }] : [];
}


// Legacy single-value SKUs, kept so old data still renders while sets get remapped.
const SKU_LABEL = {
  all:    { label:"Hobby & Double Mega", color:"#4ade80", bg:"rgba(74,222,128,0.1)",   border:"rgba(74,222,128,0.25)"  },
  hobby:  { label:"Hobby",              color:"#7B9CFF", bg:"rgba(123,156,255,0.1)",  border:"rgba(123,156,255,0.25)" },
  double: { label:"Double Mega",        color:"#E8317A", bg:"rgba(232,49,122,0.1)",   border:"rgba(232,49,122,0.25)"  },
  dayone: { label:"Day One Double Mega",color:"#FBBF24", bg:"rgba(251,191,36,0.1)",   border:"rgba(251,191,36,0.3)"   },
};

function FixMetallicAutos() {
  const [running, setRunning] = useState(false);
  const [result,  setResult]  = useState(null);

  async function run() {
    if (!window.confirm('Update all cards where cardNum ends in "8" and treatment is wrong → "Inspired Ink Metallic Battlefoil"?')) return;
    setRunning(true); setResult(null);
    const snap = await getDocs(collection(db,"boba_checklist"));
    const toFix = snap.docs.filter(d => {
      const num = String(d.data().cardNum||"");
      const treat = d.data().treatment||"";
      return num.endsWith("8") && treat === "Inspired Ink Battlefoil";
    });
    const CHUNK = 400;
    let fixed = 0;
    for (let i=0; i<toFix.length; i+=CHUNK) {
      const batch = writeBatch(db);
      toFix.slice(i,i+CHUNK).forEach(d => batch.set(doc(db,"boba_checklist",d.id), { treatment:"Inspired Ink Metallic Battlefoil" }, {merge:true}));
      await batch.commit();
      fixed += Math.min(CHUNK, toFix.length-i);
    }
    setRunning(false);
    setResult(fixed);
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      <Btn onClick={run} disabled={running} variant="green">{running ? "Fixing..." : "⚡ Fix Metallic Autos"}</Btn>
      {result !== null && <div style={{ fontSize:12, color:"#4ade80" }}>✅ Fixed {result} cards → "Inspired Ink Metallic Battlefoil"</div>}
    </div>
  );
}

function FixTreatmentTypos() {
  const [running, setRunning] = useState(false);
  const [result,  setResult]  = useState(null);

  // wrong text in DB -> correct text. Add more pairs here anytime.
  const TYPO_FIXES = [
    { from: "Halltime Alts",   to: "Halftime Alts" },
    { from: "Pixel Helemt Alt", to: "Pixel Helmet Alt" },
  ];

  async function run() {
    if (!window.confirm("Scan all cards and fix known treatment typos (Halltime → Halftime, Helemt → Helmet)?")) return;
    setRunning(true); setResult(null);
    const snap = await getDocs(collection(db,"boba_checklist"));
    const map = Object.fromEntries(TYPO_FIXES.map(f=>[f.from, f.to]));
    const toFix = snap.docs.filter(d => map[d.data().treatment||""] !== undefined);
    const CHUNK = 400;
    let fixed = 0;
    for (let i=0; i<toFix.length; i+=CHUNK) {
      const batch = writeBatch(db);
      toFix.slice(i,i+CHUNK).forEach(d => batch.set(doc(db,"boba_checklist",d.id), { treatment: map[d.data().treatment] }, {merge:true}));
      await batch.commit();
      fixed += Math.min(CHUNK, toFix.length-i);
    }
    setRunning(false);
    setResult(fixed);
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      <Btn onClick={run} disabled={running} variant="green">{running ? "Fixing..." : "🔤 Fix Treatment Typos"}</Btn>
      {result !== null && <div style={{ fontSize:12, color:"#4ade80" }}>✅ Fixed {result} cards (Halftime / Pixel Helmet)</div>}
    </div>
  );
}

function FixHeroTypos() {
  const [running, setRunning] = useState(false);
  const [result,  setResult]  = useState(null);

  // wrong hero name in DB -> correct hero name. Add more pairs here anytime.
  const HERO_FIXES = [
    { from: "Boston Stongboy", to: "Boston Strongboy" },
  ];

  async function run() {
    if (!window.confirm("Scan all cards and fix known hero-name typos (Boston Stongboy → Boston Strongboy)?")) return;
    setRunning(true); setResult(null);
    const snap = await getDocs(collection(db,"boba_checklist"));
    const map = Object.fromEntries(HERO_FIXES.map(f=>[f.from, f.to]));
    const toFix = snap.docs.filter(d => map[(d.data().hero||"").trim()] !== undefined);
    const CHUNK = 400;
    let fixed = 0;
    for (let i=0; i<toFix.length; i+=CHUNK) {
      const batch = writeBatch(db);
      toFix.slice(i,i+CHUNK).forEach(d => batch.set(doc(db,"boba_checklist",d.id), { hero: map[(d.data().hero||"").trim()] }, {merge:true}));
      await batch.commit();
      fixed += Math.min(CHUNK, toFix.length-i);
    }
    setRunning(false);
    setResult(fixed);
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      <Btn onClick={run} disabled={running} variant="green">{running ? "Fixing..." : "🦸 Fix Hero Name Typos"}</Btn>
      {result !== null && <div style={{ fontSize:12, color:"#4ade80" }}>✅ Fixed {result} cards (Boston Strongboy)</div>}
    </div>
  );
}

function GenerateMissing8s() {
  const [scanning,  setScanning]  = useState(false);
  const [preview,   setPreview]   = useState(null);  // { toCreate: [] }
  const [creating,  setCreating]  = useState(false);
  const [result,    setResult]    = useState(null);

  async function scan() {
    setScanning(true); setPreview(null); setResult(null);
    const snap = await getDocs(collection(db,"boba_checklist"));
    const all = snap.docs.map(d=>({...d.data(), fsId:d.id}));

    // Find all cards whose cardNum ends with a digit 1-7 (auto variants)
    // Group by prefix (e.g. "DBA" from "DBA3")
    const prefixMap = {}; // prefix → { cards[], existingNums: Set }
    all.forEach(c => {
      const cn = String(c.cardNum||"");
      const m = cn.match(/^([A-Za-z]+)(\d+)$/);
      if (!m) return;
      const prefix = m[1];
      const num    = parseInt(m[2]);
      if (num < 1 || num > 8) return;
      if (!prefixMap[prefix]) prefixMap[prefix] = { sample:c, nums:new Set() };
      prefixMap[prefix].nums.add(num);
    });

    // Find prefixes that have 1-7 variants but NOT an 8
    const toCreate = [];
    Object.entries(prefixMap).forEach(([prefix, {sample, nums}]) => {
      if (!nums.has(8) && (nums.has(1)||nums.has(2)||nums.has(3))) {
        // Find a reference card (prefer the highest num as template)
        const refNum = Math.max(...[...nums]);
        const refCard = all.find(c => c.cardNum === `${prefix}${refNum}`);
        if (refCard) {
          toCreate.push({
            cardNum: `${prefix}8`,
            hero: refCard.hero,
            setName: refCard.setName,
            weapon: refCard.weapon,
            power: refCard.power||0,
            inspiredBy: refCard.inspiredBy||"",
            treatment: "Inspired Ink Metallic Battlefoil",
            notation: "",
            imageUrl: "",
            mktValue: 0,
          });
        }
      }
    });

    setPreview({ toCreate });
    setScanning(false);
  }

  async function create() {
    if (!preview?.toCreate?.length) return;
    setCreating(true);
    const CHUNK = 400;
    let created = 0;
    for (let i=0; i<preview.toCreate.length; i+=CHUNK) {
      const batch = writeBatch(db);
      preview.toCreate.slice(i,i+CHUNK).forEach(card => {
        // The FULL identity is hashed, then a readable prefix is bolted on. The old version sliced
        // the key to 20 chars BEFORE hashing \u2014 and a set name like "2025 Tecmo Bowl" eats 17 of them,
        // so the treatment never survived. Silver Battlefoil and Glow Skyline (both S130) produced
        // the SAME id and collided into one document, silently destroying one of the two cards.
        const fullKey = `${card.setName||""}|${card.cardNum}|${card.hero}|${card.treatment}|${card.weapon||""}`.toLowerCase().replace(/[^a-z0-9|]/g,"");
        const h1 = Math.abs(fullKey.split("").reduce((h,ch)=>((h<<5)-h)+ch.charCodeAt(0)|0,0)).toString(16);
        // Second pass over the reversed string: one 32-bit hash collides too easily across ~31k cards.
        const h2 = Math.abs(fullKey.split("").reverse().reduce((h,ch)=>((h<<5)-h)+ch.charCodeAt(0)|0,0)).toString(16);
        const id = fullKey.replace(/\|/g,"").slice(0,20) + "_" + h1.slice(0,8) + h2.slice(0,4);
        batch.set(doc(db,"boba_checklist",id), {...card, id}, {merge:true});
      });
      await batch.commit();
      created += Math.min(CHUNK, preview.toCreate.length-i);
    }
    setCreating(false);
    setResult({ created });
    setPreview(null);
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      <Btn onClick={scan} disabled={scanning}>{scanning?"Scanning...":"🔍 Find Missing 8s"}</Btn>

      {preview && (
        <div>
          {preview.toCreate.length===0
            ? <div style={{ fontSize:12, color:"#4ade80" }}>✅ No missing 8s found — all metallic variants exist</div>
            : <>
                <div style={{ fontSize:12, color:"#FBBF24", marginBottom:8 }}>Found {preview.toCreate.length} missing Metallic cards:</div>
                <div style={{ maxHeight:160, overflowY:"auto", marginBottom:10, display:"flex", flexWrap:"wrap", gap:4 }}>
                  {preview.toCreate.map(c=>(
                    <span key={c.cardNum} style={{ fontSize:10, background:"rgba(192,192,192,0.1)", border:"1px solid rgba(192,192,192,0.2)", borderRadius:5, padding:"2px 8px", color:"#C0C0C0" }}>
                      {c.cardNum} {c.hero}
                    </span>
                  ))}
                </div>
                <Btn onClick={create} disabled={creating} variant="green">
                  {creating?"Creating...":"⚡ Create All Missing 8s"}
                </Btn>
              </>
          }
        </div>
      )}

      {result && <div style={{ fontSize:12, color:"#4ade80" }}>✅ Created {result.created} Inspired Ink Metallic Battlefoil cards</div>}
    </div>
  );
}

function PrefixTreatmentFixer() {
  const [prefix,    setPrefix]    = useState("BG");
  const [treatment, setTreatment] = useState("Bubble Gum Battlefoil");
  const [running,   setRunning]   = useState(false);
  const [result,    setResult]    = useState(null);

  const COMMON_TREATMENTS = [
    "Base","Battlefoil","Bubble Gum Battlefoil","Pink Battlefoil","Green Battlefoil",
    "Orange Battlefoil","Blue Battlefoil","Red Battlefoil","Silver Battlefoil",
    "Alpha Battlefoil","Scoreboard Battlefoil","Helmets Battlefoil",
    "80's 8-Bit Rad Battlefoil","Sore Thumb Battlefoil","Tecmo News Battlefoil",
    "Gold Coin Flip Battlefoil","Silver Coin Flip Battlefoil","Blow on it Battlefoil",
    "Skyline Battlefoil","Endzone Battlefoil","Big Pixel Battlefoil","Rage Quit Battlefoil",
    "Helmet Icon Battlefoil","Logofoil","Superfoil","Inspired Ink Battlefoil",
    "Inspired Ink Metallic Battlefoil","Inspired Ink Superfoil","Bonus Plays","Paper Plays",
    "Halftime Alt Art","8-Bit Alt Art","Pixel Helmet Alt","Hot Dog",
  ];

  async function run() {
    if (!prefix.trim()||!treatment.trim()) return;
    const p = prefix.trim().toUpperCase();
    if (!window.confirm(`Set treatment = "${treatment}" for all cards where cardNum starts with "${p}"?`)) return;
    setRunning(true); setResult(null);
    const snap = await getDocs(collection(db,"boba_checklist"));
    const toFix = snap.docs.filter(d => String(d.data().cardNum||"").toUpperCase().startsWith(p));
    const CHUNK = 400;
    let fixed = 0;
    for (let i=0; i<toFix.length; i+=CHUNK) {
      const batch = writeBatch(db);
      toFix.slice(i,i+CHUNK).forEach(d => batch.set(doc(db,"boba_checklist",d.id), { treatment }, {merge:true}));
      await batch.commit();
      fixed += Math.min(CHUNK, toFix.length-i);
    }
    setRunning(false); setResult({ fixed, prefix:p, treatment });
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      <div style={{ display:"grid", gridTemplateColumns:"120px 1fr auto", gap:8, alignItems:"center" }}>
        <input value={prefix} onChange={e=>setPrefix(e.target.value)} placeholder="Prefix (e.g. BG)"
          style={{ ...S.inp, fontSize:13, fontWeight:700, textTransform:"uppercase" }}/>
        <select value={treatment} onChange={e=>setTreatment(e.target.value)}
          style={{ ...S.inp, cursor:"pointer", color:treatment?"var(--bz-ink)":"#555" }}>
          <option value="">— Select treatment —</option>
          {COMMON_TREATMENTS.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
        <Btn onClick={run} disabled={!prefix.trim()||!treatment.trim()||running} variant="green">
          {running?"Fixing...":"Fix"}
        </Btn>
      </div>
      {result && <div style={{ fontSize:12, color:"#4ade80" }}>✅ Fixed {result.fixed} cards starting with "{result.prefix}" → "{result.treatment}"</div>}
    </div>
  );
}

function PlaysFixButton() {
  const [running, setRunning] = useState(false);
  const [result,  setResult]  = useState(null);

  async function run() {
    if (!window.confirm("This will update treatment fields for all PL and BPL cards in Firestore. Continue?")) return;
    setRunning(true); setResult(null);
    const snap = await getDocs(collection(db,"boba_checklist"));
    const toFix = snap.docs.map(d=>({...d.data(), fsId:d.id})).filter(c => {
      const cn = String(c.cardNum||"").toUpperCase();
      return cn.startsWith("BPL") || (cn.startsWith("PL") && !cn.startsWith("BPL"));
    });

    let bonus=0, paper=0;
    const CHUNK=400;
    for (let i=0; i<toFix.length; i+=CHUNK) {
      const batch = writeBatch(db);
      toFix.slice(i,i+CHUNK).forEach(c => {
        const cn = String(c.cardNum||"").toUpperCase();
        const treatment = cn.startsWith("BPL") ? "Bonus Plays" : "Paper Plays";
        if (cn.startsWith("BPL")) bonus++;
        else paper++;
        batch.set(doc(db,"boba_checklist",c.fsId), { treatment }, {merge:true});
      });
      await batch.commit();
    }
    setRunning(false);
    setResult({ bonus, paper });
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
  }

  return (
    <div>
      <Btn onClick={run} disabled={running} variant="green">{running?"Fixing...":"⚡ Fix Plays Treatments"}</Btn>
      {result && <div style={{ fontSize:12, color:"#4ade80", marginTop:8 }}>✅ Fixed {result.bonus} Bonus Plays (BPL) + {result.paper} Paper Plays (PL)</div>}
    </div>
  );
}

function BulkDeleter() {
  const [field,    setField]    = useState("treatment");
  const [value,    setValue]    = useState("");
  const [deleting, setDeleting] = useState(false);
  const [result,   setResult]   = useState(null);

  async function run() {
    if (!value.trim()) return;
    if (!window.confirm(`Delete ALL cards where ${field} = "${value}"?\n\nThis cannot be undone.`)) return;
    setDeleting(true); setResult(null);
    const snap = await getDocs(collection(db,"boba_checklist"));
    const toDelete = snap.docs.filter(d => (d.data()[field]||"") === value.trim());
    const CHUNK = 400;
    let deleted = 0;
    for (let i=0; i<toDelete.length; i+=CHUNK) {
      const batch = writeBatch(db);
      toDelete.slice(i,i+CHUNK).forEach(d => batch.delete(doc(db,"boba_checklist",d.id)));
      await batch.commit();
      deleted += Math.min(CHUNK, toDelete.length-i);
    }
    setDeleting(false); setResult({ deleted });
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
    setValue("");
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      <div style={{ display:"grid", gridTemplateColumns:"auto 1fr auto", gap:8, alignItems:"center" }}>
        <select value={field} onChange={e=>setField(e.target.value)} style={{ ...S.inp, cursor:"pointer", fontSize:12 }}>
          <option value="treatment">treatment</option>
          <option value="weapon">weapon</option>
          <option value="setName">setName</option>
        </select>
        <input value={value} onChange={e=>setValue(e.target.value)} placeholder={`Value to delete (exact match)`} style={{ ...S.inp, fontSize:12 }}/>
        <Btn variant="red" onClick={run} disabled={!value.trim()||deleting}>{deleting?"Deleting...":"Delete All"}</Btn>
      </div>
      {result && <div style={{ fontSize:12, color:"#4ade80" }}>✅ Deleted {result.deleted} cards</div>}
    </div>
  );
}

function SwanCityBulkImport() {
  const [cards,   setCards]   = useState([]);
  const [cardsLoaded, setCardsLoaded] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [done,    setDone]    = useState(null);

  // Load the checklist once (this tool is self-contained).
  async function loadCards() {
    const snap = await getDocs(collection(db, "boba_checklist"));
    setCards(snap.docs.map(d => ({...d.data(), id:d.id})));
    setCardsLoaded(true);
  }

  // Swan City column header -> your treatment string.
  //  "__SUPER__" => match weapon === "Super", write to super_claims
  //  null        => ignore the column
  const COLUMN_MAP = {
    "SUPERFOIL":                              "__SUPER__",
    "Inspired Ink/ Highlighted  Superfoil":   "Inspired Ink Superfoil",
    "Pink Hex Battlefoil":                    "Pink Battlefoil",
    "Highlighted Pink Hex Battlefoil":        "Pink Battlefoil",
    "Alpha":                                  "Alpha Battlefoil",
    "Helmets*":                               "Helmets Battlefoil",
    "News*":                                  "Tecmo News Battlefoil",
    "80's Rad":                               "80's 8-Bit Rad Battlefoil",
    "Scoreboard*":                            "Scoreboard Battlefoil",
    "Logofoils":                              "Logofoil",
    "Sore Thumb*":                            "Sore Thumb Battlefoil",
    "Blow On It*":                            "Blow on it Battlefoil",
    "Gold Coinflip*":                         "Gold Coin Flip Battlefoil",
    "Silver Coinflip*":                       "Silver Coin Flip Battlefoil",
    "Tecmo Logo":                             "Tecmo Logo Battlefoil",
    "Big Pixel*":                             "Big Pixel Battlefoil",
    "Helmet Icons*":                          "Helmet Icon Battlefoil",
    "Touchdown":                              "Endzone Battlefoil",
    "Touchdown /34":                          null,
  };

  const DATE_RE = /\b\d{1,2}\/\d{1,2}\/\d{2,4}\b|\b\d{3,4}\/\d{4}\b/;

  function parseCSV(text) {
    const rows = []; let row = [], field = "", q = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (q) {
        if (c === '"' && text[i+1] === '"') { field += '"'; i++; }
        else if (c === '"') q = false;
        else field += c;
      } else {
        if (c === '"') q = true;
        else if (c === ",") { row.push(field); field = ""; }
        else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
        else if (c === "\r") {}
        else field += c;
      }
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    return rows;
  }

  const norm = s => (s || "").toString().trim().toLowerCase();

  function buildPreview() {
    if (!cardsLoaded) { alert("Click \u201cLoad checklist\u201d first."); return; }
    const rows = parseCSV(csvText);
    if (rows.length < 3) { alert("Couldn't read that CSV \u2014 paste the full Swan City tracker including the header row."); return; }

    let hIdx = rows.findIndex(r => r.some(c => norm(c) === "hero"));
    if (hIdx < 0) hIdx = 1;
    const header = rows[hIdx];
    const heroCol = header.findIndex(c => norm(c) === "hero");
    const inspCol = header.findIndex(c => norm(c).includes("inspired"));
    const firstParallel = inspCol >= 0 ? inspCol + 1 : heroCol + 2;

    const byHero = {}, byInspired = {};
    cards.forEach(c => {
      if (c.hero)     (byHero[norm(c.hero)]         = byHero[norm(c.hero)]         || []).push(c);
      if (c.inspired) (byInspired[norm(c.inspired)] = byInspired[norm(c.inspired)] || []).push(c);
    });

    const sf = [], one = [], unmatched = [];
    for (let r = hIdx + 1; r < rows.length; r++) {
      const row = rows[r];
      const hero = (row[heroCol] || "").trim().replace(/\*+$/,"").trim();
      const player = (row[inspCol] || "").trim();
      if (!hero) continue;
      for (let col = firstParallel; col < row.length; col++) {
        const cellV = (row[col] || "").trim();
        if (!cellV || !DATE_RE.test(cellV)) continue;
        const colName = (header[col] || "").trim();
        const mapped = COLUMN_MAP[colName];
        if (mapped === null || mapped === undefined) continue;
        const isSuper = mapped === "__SUPER__";
        let pool = byHero[norm(hero)] || byInspired[norm(player)] || [];
        let card;
        if (isSuper) card = pool.find(c => (c.weapon || "").toUpperCase() === "SUPER");
        else         card = pool.find(c => norm(c.treatment) === norm(mapped));
        const record = { hero, player, colName, mapped, cell: cellV,
                         dateHit: (cellV.match(DATE_RE) || [""])[0],
                         source: cellV.replace(DATE_RE, "").trim() };
        if (card) {
          record.cardId = card.id; record.cardNum = card.cardNum;
          record.cardImage = card.imageUrl || null; record.setName = card.setName || "";
          record.treatment = card.treatment || "";
          (isSuper ? sf : one).push(record);
        } else {
          record.reason = pool.length ? `hero found but no "${isSuper ? "Super" : mapped}" card` : "hero not in checklist";
          unmatched.push(record);
        }
      }
    }
    setPreview({ sf, one, unmatched });
    setDone(null);
  }

  async function runImport() {
    if (!preview) return;
    const total = preview.sf.length + preview.one.length;
    if (!total) { alert("Nothing matched to import."); return; }
    if (!window.confirm(`Write ${total} VERIFIED claims?\n  \u2022 ${preview.sf.length} \u2192 super_claims\n  \u2022 ${preview.one.length} \u2192 oneof1_claims\n\nExisting claims for the same card will be overwritten.`)) return;
    setRunning(true); setDone(null);
    const u = auth.currentUser;
    const admin = u?.displayName || u?.email || "Swan City Import";
    const now = new Date().toISOString();
    let written = 0;
    try {
      for (let i = 0; i < preview.sf.length; i += 400) {
        const batch = writeBatch(db);
        preview.sf.slice(i, i + 400).forEach(h => {
          batch.set(doc(db, "super_claims", h.cardId), {
            cardId: h.cardId, cardName: h.hero, cardNum: h.cardNum,
            setName: h.setName, cardImage: h.cardImage,
            userId: null, userName: h.source || "Swan City", submitterName: h.source || "Swan City",
            story: h.source ? `Imported from Swan City tracker \u2014 ${h.source}` : "Imported from Swan City tracker",
            dateHit: h.dateHit, recordedByAdmin: admin, photoUrl: null, status: "verified",
            createdAt: now, reviewedAt: now, source: "swancity_import",
          }, { merge: true });
        });
        await batch.commit();
        written += Math.min(400, preview.sf.length - i);
      }
      for (let i = 0; i < preview.one.length; i += 400) {
        const batch = writeBatch(db);
        preview.one.slice(i, i + 400).forEach(h => {
          batch.set(doc(db, "oneof1_claims", h.cardId), {
            cardId: h.cardId, cardName: h.hero, cardNum: h.cardNum,
            setName: h.setName, treatment: h.treatment, cardImage: h.cardImage,
            userId: null, submitterName: h.source || "Swan City",
            story: h.source ? `Imported from Swan City tracker \u2014 ${h.source}` : "Imported from Swan City tracker",
            dateHit: h.dateHit, recordedByAdmin: admin, photoUrl: null, status: "verified",
            createdAt: now, reviewedAt: now, source: "swancity_import",
          }, { merge: true });
        });
        await batch.commit();
        written += Math.min(400, preview.one.length - i);
      }
      setDone({ written });
    } catch (e) {
      alert("Import failed: " + (e?.message || e) + "\n\nIf this mentions permissions, the Firestore rules for super_claims / oneof1_claims need to allow admin writes.");
    } finally { setRunning(false); }
  }

  async function undoImport() {
    if (!window.confirm("Delete ALL claims tagged source:\"swancity_import\" from both super_claims and oneof1_claims?")) return;
    setRunning(true);
    try {
      let removed = 0;
      for (const coll of ["super_claims", "oneof1_claims"]) {
        const snap = await getDocs(query(collection(db, coll), where("source", "==", "swancity_import")));
        for (let i = 0; i < snap.docs.length; i += 400) {
          const batch = writeBatch(db);
          snap.docs.slice(i, i + 400).forEach(d => batch.delete(doc(db, coll, d.id)));
          await batch.commit();
        }
        removed += snap.docs.length;
      }
      setDone({ written: 0, removed });
    } catch (e) { alert("Undo failed: " + (e?.message || e)); }
    finally { setRunning(false); }
  }

  const tcell = { padding: "4px 8px", fontSize: 11, borderBottom: "1px solid var(--bz-s2)", whiteSpace: "nowrap" };

  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--bz-ink-3)", marginBottom: 10 }}>
        Paste Swan City's CSV tracker. Only cells with a <strong>date + source</strong> import.
        Superfoils → <strong style={{ color: "#F59E0B" }}>super_claims</strong>, other parallels → <strong style={{ color: "#E8317A" }}>oneof1_claims</strong>, all <strong>verified</strong>. Preview before importing.
      </div>

      {!cardsLoaded ? (
        <button onClick={loadCards}
          style={{ background: "rgba(255,255,255,0.08)", color: "var(--bz-ink)", border: "1px solid rgba(255,255,255,0.15)",
                   borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
          ⬇️ Load checklist
        </button>
      ) : (
        <div style={{ fontSize: 11, color: "#4ade80", marginBottom: 8 }}>✅ {cards.length} cards loaded</div>
      )}

      <textarea value={csvText} onChange={e => setCsvText(e.target.value)} spellCheck={false}
        placeholder="Paste the full Swan City CSV here (including the header row with Hero / Inspired By / SUPERFOIL / ...)"
        style={{ width: "100%", minHeight: 120, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
                 borderRadius: 10, color: "#ddd", padding: 10, fontSize: 11, fontFamily: "monospace", outline: "none", resize: "vertical" }}/>

      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button onClick={buildPreview} disabled={!csvText.trim() || !cardsLoaded}
          style={{ background: "rgba(255,255,255,0.08)", color: "var(--bz-ink)", border: "1px solid rgba(255,255,255,0.15)",
                   borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: (csvText.trim() && cardsLoaded) ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
          🔍 Preview matches
        </button>
        {preview && (preview.sf.length + preview.one.length > 0) && (
          <button onClick={runImport} disabled={running}
            style={{ background: "linear-gradient(135deg,#F59E0B,#E8317A)", color: "#fff", border: "none",
                     borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 800, cursor: running ? "wait" : "pointer", fontFamily: "inherit" }}>
            {running ? "Working\u2026" : `\u2b06\ufe0f Import ${preview.sf.length + preview.one.length} verified`}
          </button>
        )}
        <button onClick={undoImport} disabled={running}
          style={{ background: "rgba(239,68,68,0.12)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.3)",
                   borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: running ? "wait" : "pointer", fontFamily: "inherit" }}>
          🗑️ Undo import
        </button>
      </div>

      {done && <div style={{ fontSize: 13, color: "#4ade80", marginTop: 12, fontWeight: 700 }}>
        {done.removed != null ? `\ud83e\uddf9 Removed ${done.removed} imported claims.` : `\u2705 Imported ${done.written} verified claims. Open the Supers / 1/1 tabs to see them.`}
      </div>}

      {preview && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", gap: 16, fontSize: 12, fontWeight: 700, marginBottom: 8, flexWrap: "wrap" }}>
            <span style={{ color: "#F59E0B" }}>⭐ {preview.sf.length} Superfoils</span>
            <span style={{ color: "#E8317A" }}>🎯 {preview.one.length} Secret 1/1s</span>
            <span style={{ color: preview.unmatched.length ? "#ef4444" : "#555" }}>⚠️ {preview.unmatched.length} unmatched</span>
          </div>
          {[["\u2b50 Superfoils \u2192 super_claims", preview.sf],
            ["\ud83c\udfaf Secret 1/1s \u2192 oneof1_claims", preview.one]].map(([title, list]) => list.length > 0 && (
            <div key={title} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "var(--bz-ink-2)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{title}</div>
              <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid var(--bz-line)", borderRadius: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
                  {list.map((h, i) => (
                    <tr key={i}>
                      <td style={{ ...tcell, color: "var(--bz-ink)", fontWeight: 700 }}>{h.hero}</td>
                      <td style={{ ...tcell, color: "var(--bz-ink-2)" }}>#{h.cardNum}</td>
                      <td style={{ ...tcell, color: "#aaa" }}>{h.colName}</td>
                      <td style={{ ...tcell, color: "#666" }}>{h.dateHit}</td>
                      <td style={{ ...tcell, color: "#666" }}>{h.source}</td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
            </div>
          ))}
          {preview.unmatched.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#ef4444", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                ⚠️ Unmatched — not imported (fix COLUMN_MAP or add the card, then re-preview)
              </div>
              <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #2a1414", borderRadius: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
                  {preview.unmatched.map((h, i) => (
                    <tr key={i}>
                      <td style={{ ...tcell, color: "var(--bz-ink)" }}>{h.hero}</td>
                      <td style={{ ...tcell, color: "#aaa" }}>{h.colName}</td>
                      <td style={{ ...tcell, color: "#ef4444" }}>{h.reason}</td>
                      <td style={{ ...tcell, color: "#666" }}>{h.dateHit}</td>
                    </tr>
                  ))}
                </tbody></table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── DEDUPE ───────────────────────────────────────────────────────────────────
// Finds cards that are the SAME physical card (same setName + cardNum) but exist as multiple
// Firestore docs — which happens when an import's ID hash differs from the original (e.g. a
// treatment/hero spelled differently). Keeps the BEST doc of each group (prefers one with an
// image / market value / more fields) and deletes the rest. Preview before running.
// ── ADMIN: TREATMENT MERGE ───────────────────────────────────────────────────
// Collapse junk treatment variants into the real one. Imports produced colour-prefixed names
// ("Silver Blast", "Orange Blast") that are really just "Blast" — the colour is describing the
// weapon, which the weapon field already records. Two docs for one physical card is the result,
// and the deduper can't catch them because a different treatment is, correctly, a different card.
//
// This does NOT guess. It shows every treatment with its card count, you choose what merges into
// what, and it previews the exact rows before writing anything.
function TreatmentMerge() {
  const [allCards, setAllCards] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [target,   setTarget]   = useState("");        // the treatment to keep
  const [sources,  setSources]  = useState(new Set()); // treatments to fold into it
  const [running,  setRunning]  = useState(false);
  const [done,     setDone]     = useState(null);
  const [err,      setErr]      = useState("");

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db,"boba_checklist"));
        setAllCards(snap.docs.map(d=>({...d.data(), id:d.id})));
      } catch(e) { setErr(e.message); }
      setLoading(false);
    })();
  }, []);

  // Every treatment, with how many cards carry it. Sorted by count so the real ones (many cards)
  // sit at the top and the junk variants (a handful each) are easy to spot.
  const treatments = useMemo(() => {
    const m = {};
    allCards.forEach(c => { const t=(c.treatment||"").trim(); if(t) m[t]=(m[t]||0)+1; });
    return Object.entries(m).sort((a,b)=>b[1]-a[1]);
  }, [allCards]);

  // Cards that would actually change, so you can see the damage before you do it.
  const affected = useMemo(
    () => allCards.filter(c => sources.has((c.treatment||"").trim())),
    [allCards, sources]
  );

  // After the merge, would any two cards collide? That's the whole point — it means the merge is
  // creating the duplicate pairs the deduper can then clean up.
  const collisions = useMemo(() => {
    if (!target || sources.size===0) return [];
    const n = v => String(v||"").trim().toLowerCase();
    const by = {};
    allCards.forEach(c => {
      const t = sources.has((c.treatment||"").trim()) ? target : (c.treatment||"");
      const k = [n(c.setName), n(c.cardNum), n(c.playName)||n(c.hero), n(t), n(c.weapon)].join("|");
      (by[k] = by[k]||[]).push(c);
    });
    return Object.values(by).filter(g => g.length > 1);
  }, [allCards, sources, target]);

  async function run() {
    if (!target || sources.size===0) return;
    if (!window.confirm(
      `Change ${affected.length} cards to treatment "${target}"?\n\n` +
      `Merging: ${[...sources].join(", ")}\n\n` +
      `This rewrites the treatment field on those cards. It can't be undone from here.`
    )) return;
    setRunning(true); setErr(""); setDone(null);
    try {
      let n = 0;
      for (let i=0; i<affected.length; i+=400) {
        const batch = writeBatch(db);
        affected.slice(i, i+400).forEach(c => {
          batch.set(doc(db,"boba_checklist",c.id), { treatment: target }, { merge:true });
          n++;
        });
        await batch.commit();
      }
      setDone(n);
      // Refresh so the counts reflect reality.
      const snap = await getDocs(collection(db,"boba_checklist"));
      setAllCards(snap.docs.map(d=>({...d.data(), id:d.id})));
      setSources(new Set());
    } catch(e) { setErr(e.message); }
    setRunning(false);
  }

  const S = {
    card: { background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:12, padding:18 },
  };

  if (loading) return <div style={S.card}><div style={{fontSize:12,color:"var(--bz-ink-3)"}}>Loading cards{"\u2026"}</div></div>;

  return (
    <div style={S.card}>
      <div style={{fontSize:15,fontWeight:800,color:"var(--bz-ink)",marginBottom:4}}>{"\uD83C\uDFF7"} Merge Treatments</div>
      <div style={{fontSize:12,color:"var(--bz-ink-3)",marginBottom:16,lineHeight:1.6}}>
        Pick the treatment to <strong>keep</strong>, then tick the junk variants to fold into it.
        Nothing is written until you confirm, and you{"\u2019"}ll see exactly how many cards change.
      </div>

      {err && <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.3)",color:"#FCA5A5",borderRadius:8,padding:"10px 13px",fontSize:12,marginBottom:12}}>{err}</div>}
      {done!==null && <div style={{background:"rgba(74,222,128,0.1)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:8,padding:"10px 13px",fontSize:12,fontWeight:700,marginBottom:12}}>{"\u2713"} {done} cards updated. Run the Dedupe scan next to clean up the twins this creates.</div>}

      <div style={{fontSize:11,fontWeight:800,color:"var(--bz-ink-2)",marginBottom:6,letterSpacing:0.5}}>KEEP THIS TREATMENT</div>
      <select value={target} onChange={e=>{setTarget(e.target.value); setSources(prev=>{const n=new Set(prev); n.delete(e.target.value); return n;});}}
        style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"9px 12px",fontSize:13,color:"#fff",fontFamily:"inherit",marginBottom:16,cursor:"pointer"}}>
        <option value="">{"\u2014"} choose {"\u2014"}</option>
        {treatments.map(([t,n])=><option key={t} value={t}>{t} ({n})</option>)}
      </select>

      {target && (
        <>
          <div style={{fontSize:11,fontWeight:800,color:"var(--bz-ink-2)",marginBottom:6,letterSpacing:0.5}}>
            FOLD THESE INTO {"\u201C"}{target}{"\u201D"}
          </div>
          <div style={{border:"1px solid var(--bz-line-2)",borderRadius:10,maxHeight:280,overflowY:"auto",marginBottom:14}}>
            {treatments.filter(([t])=>t!==target).map(([t,n])=>{
              const on = sources.has(t);
              return (
                <label key={t} style={{display:"flex",alignItems:"center",gap:9,padding:"8px 12px",borderBottom:"1px solid var(--bz-s2)",cursor:"pointer",background:on?"rgba(232,49,122,0.1)":"transparent"}}>
                  <input type="checkbox" checked={on} onChange={()=>setSources(prev=>{const s=new Set(prev); s.has(t)?s.delete(t):s.add(t); return s;})} style={{accentColor:"#E8317A",cursor:"pointer"}}/>
                  <span style={{flex:1,fontSize:12.5,fontWeight:on?800:600,color:on?"#E8317A":"#ccc"}}>{t}</span>
                  <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>{n} card{n===1?"":"s"}</span>
                </label>
              );
            })}
          </div>
        </>
      )}

      {sources.size>0 && (
        <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid var(--bz-line-2)",borderRadius:10,padding:"12px 14px",marginBottom:14}}>
          <div style={{fontSize:12.5,fontWeight:800,color:"#fff",marginBottom:6}}>
            {affected.length} card{affected.length===1?"":"s"} would change to {"\u201C"}{target}{"\u201D"}
          </div>
          <div style={{fontSize:11.5,color:"var(--bz-ink-3)",lineHeight:1.6}}>
            {collisions.length>0
              ? <>This creates <strong style={{color:"#FBBF24"}}>{collisions.length} duplicate group{collisions.length===1?"":"s"}</strong> {"\u2014"} which is the point. Run <strong>{"\uD83E\uDDEC"} Dedupe</strong> afterwards to collapse them.</>
              : <>No duplicates would result {"\u2014"} these cards are distinct beyond the treatment name.</>}
          </div>
          {affected.slice(0,6).map(c=>(
            <div key={c.id} style={{fontSize:11,color:"var(--bz-ink-2)",marginTop:6,fontFamily:"monospace"}}>
              {c.cardNum} {"\u00B7"} {c.playName||c.hero} {"\u00B7"} <span style={{color:"#EF4444"}}>{c.treatment}</span> {"\u2192"} <span style={{color:"#4ade80"}}>{target}</span>
            </div>
          ))}
          {affected.length>6 && <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:5}}>{"\u2026and "}{affected.length-6} more</div>}
        </div>
      )}

      <button onClick={run} disabled={!target||sources.size===0||running}
        style={{width:"100%",background:(!target||sources.size===0)?"#222":"linear-gradient(135deg,#E8317A,#7B2FF7)",color:(!target||sources.size===0)?"#666":"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:13.5,fontWeight:800,cursor:(!target||sources.size===0||running)?"not-allowed":"pointer",fontFamily:"inherit"}}>
        {running ? "Merging\u2026" : sources.size===0 ? "Pick treatments to merge" : `Merge ${affected.length} cards into "${target}"`}
      </button>
    </div>
  );
}

// ── ADMIN: JUNK PLAY CLEANUP ─────────────────────────────────────────────────
// A misaligned CSV import shifted a column: on some Play / Bonus Play cards the HOT DOG COST landed
// in the playName field, so the name reads "0.0", "1.0", "2.0" instead of the actual play name.
//
// Those became SEPARATE documents (the doc id is derived from the name, so a different name means a
// different card), which is why the correct play and the junk one both exist. The deduper can't pair
// them for exactly the same reason: to it, they are two different cards.
//
// A real play name is never a bare number. That makes the junk safely identifiable, and the correct
// twin already exists, so deleting them loses nothing. Nothing is deleted until you have seen the list.
function JunkPlayCleanup() {
  const [cards,   setCards]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [done,    setDone]    = useState(null);
  const [err,     setErr]     = useState("");

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db,"boba_checklist"));
        setCards(snap.docs.map(d=>({...d.data(), id:d.id})));
      } catch(e) { setErr(e.message); }
      setLoading(false);
    })();
  }, []);

  // A playName that is nothing but a number ("0", "1.0", "2.00") is the cost, not a name.
  const isJunkName = v => {
    const t = String(v==null?"":v).trim();
    return t !== "" && /^\d+(\.\d+)?$/.test(t);
  };

  const junk = useMemo(() => cards.filter(c => isJunkName(c.playName)), [cards]);

  // For each junk card, does the properly-named twin actually exist? If it does, deleting is safe.
  // If it does NOT, deleting would lose the only copy of that card — so those are held back.
  const analyzed = useMemo(() => {
    const n = v => String(v==null?"":v).trim().toLowerCase();
    const bySetNum = {};
    cards.forEach(c => {
      const k = n(c.setName)+"|"+n(c.cardNum);
      (bySetNum[k] = bySetNum[k]||[]).push(c);
    });
    return junk.map(c => {
      const siblings = (bySetNum[n(c.setName)+"|"+n(c.cardNum)]||[]).filter(x => x.id !== c.id);
      const goodTwin = siblings.find(x => x.playName && !isJunkName(x.playName));
      return { card:c, goodTwin: goodTwin||null };
    });
  }, [junk, cards]);

  const safe   = analyzed.filter(a => a.goodTwin);
  const unsafe = analyzed.filter(a => !a.goodTwin);

  async function run() {
    if (safe.length === 0) return;
    if (!window.confirm(
      `Delete ${safe.length} junk play card${safe.length===1?"":"s"}?\n\n` +
      `Each one has a correctly-named twin that stays. This can't be undone.`
    )) return;
    setRunning(true); setErr(""); setDone(null);
    try {
      let n = 0;
      for (let i=0; i<safe.length; i+=300) {
        const batch = writeBatch(db);
        safe.slice(i, i+300).forEach(a => { batch.delete(doc(db,"boba_checklist",a.card.id)); n++; });
        await batch.commit();
      }
      setDone(n);
      const snap = await getDocs(collection(db,"boba_checklist"));
      setCards(snap.docs.map(d=>({...d.data(), id:d.id})));
    } catch(e) { setErr(e.message); }
    setRunning(false);
  }

  const S = { card:{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:12, padding:18 } };

  if (loading) return <div style={S.card}><div style={{fontSize:12,color:"var(--bz-ink-3)"}}>Scanning cards{"\u2026"}</div></div>;

  return (
    <div style={S.card}>
      <div style={{fontSize:15,fontWeight:800,color:"var(--bz-ink)",marginBottom:4}}>{"\uD83E\uDDF9"} Junk Play Cleanup</div>
      <div style={{fontSize:12,color:"var(--bz-ink-3)",marginBottom:14,lineHeight:1.6}}>
        Finds Play cards whose <strong>name is a number</strong> ({"\u201C"}0.0{"\u201D"}, {"\u201C"}1.0{"\u201D"}{"\u2026"}) {"\u2014"} the hot dog cost
        landed in the name column during a misaligned import, creating a second, broken copy of the card.
      </div>

      {err && <div style={{background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.3)",color:"#FCA5A5",borderRadius:8,padding:"10px 13px",fontSize:12,marginBottom:12}}>{err}</div>}
      {done!==null && <div style={{background:"rgba(74,222,128,0.1)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:8,padding:"10px 13px",fontSize:12,fontWeight:700,marginBottom:12}}>{"\u2713"} {done} junk cards deleted.</div>}

      {junk.length===0 ? (
        <div style={{textAlign:"center",padding:"30px 20px",color:"var(--bz-ink-3)",fontSize:13}}>
          {"\u2713"} No junk plays found {"\u2014"} every play card has a real name.
        </div>
      ) : (
        <>
          <div style={{display:"flex",gap:10,marginBottom:14}}>
            <div style={{flex:1,background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",borderRadius:9,padding:"11px 13px"}}>
              <div style={{fontSize:19,fontWeight:900,color:"#EF4444"}}>{safe.length}</div>
              <div style={{fontSize:10.5,color:"var(--bz-ink-3)",lineHeight:1.4}}>safe to delete {"\u2014"} the real card exists</div>
            </div>
            {unsafe.length>0 && (
              <div style={{flex:1,background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.25)",borderRadius:9,padding:"11px 13px"}}>
                <div style={{fontSize:19,fontWeight:900,color:"#FBBF24"}}>{unsafe.length}</div>
                <div style={{fontSize:10.5,color:"var(--bz-ink-3)",lineHeight:1.4}}>held back {"\u2014"} no good twin found</div>
              </div>
            )}
          </div>

          {unsafe.length>0 && (
            <div style={{background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.25)",borderRadius:9,padding:"11px 13px",fontSize:11.5,color:"#FBBF24",lineHeight:1.6,marginBottom:14}}>
              {"\u26A0\uFE0F"} {unsafe.length} junk card{unsafe.length===1?" has":"s have"} no correctly-named twin, so {unsafe.length===1?"it is":"they are"} the
              only copy of that card. Deleting {unsafe.length===1?"it":"them"} would lose the card entirely, so {unsafe.length===1?"it is":"they are"} left alone.
              You{"\u2019"}ll need to re-import {unsafe.length===1?"it":"them"} with the columns lined up.
            </div>
          )}

          <div style={{border:"1px solid var(--bz-line-2)",borderRadius:10,maxHeight:300,overflowY:"auto",marginBottom:14}}>
            {analyzed.slice(0,150).map(({card:c, goodTwin})=>(
              <div key={c.id} style={{padding:"8px 12px",borderBottom:"1px solid var(--bz-s2)",fontSize:11,fontFamily:"monospace",display:"flex",alignItems:"center",gap:8}}>
                <span style={{color:goodTwin?"#EF4444":"#FBBF24",fontWeight:800,minWidth:52}}>{goodTwin?"DELETE":"KEEP"}</span>
                <span style={{color:"var(--bz-ink-2)",minWidth:130}}>{c.setName}</span>
                <span style={{color:"var(--bz-ink-2)",minWidth:60}}>{c.cardNum}</span>
                <span style={{color:"#EF4444"}}>name: {"\u201C"}{String(c.playName)}{"\u201D"}</span>
                {goodTwin && <span style={{color:"#4ade80",marginLeft:"auto"}}>{"\u2192"} keeping {"\u201C"}{goodTwin.playName}{"\u201D"}</span>}
              </div>
            ))}
            {analyzed.length>150 && <div style={{padding:"8px 12px",fontSize:11,color:"var(--bz-ink-3)"}}>{"\u2026and "}{analyzed.length-150} more</div>}
          </div>

          <button onClick={run} disabled={safe.length===0||running}
            style={{width:"100%",background:safe.length===0?"#222":"#EF4444",color:safe.length===0?"#666":"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:13.5,fontWeight:800,cursor:(safe.length===0||running)?"not-allowed":"pointer",fontFamily:"inherit"}}>
            {running ? "Deleting\u2026" : `Delete ${safe.length} junk play card${safe.length===1?"":"s"}`}
          </button>
        </>
      )}
    </div>
  );
}

function CardDeduper() {
  const [loading, setLoading] = useState(false);
  const [groups,  setGroups]  = useState(null);   // [{key, keep, drop:[...]}]
  const [orphanFloats, setOrphanFloats] = useState([]); // "100.0" cards with no duplicate twin
  const [running, setRunning] = useState(false);
  const [done,    setDone]    = useState(null);
  const [err,     setErr]     = useState("");
  // STRICT groups on every identifying field. LOOSE groups on set + number + name only.
  //
  // Loose exists because a re-import that drifted a treatment or power name forks the card into
  // a second document, and strict can never see those as duplicates — a different treatment is,
  // correctly, a different card. But two docs with the same set, number and hero are the same
  // physical card whatever else disagrees, and that is precisely the mess a bad re-import makes.
  const [mode, setMode] = useState("strict");   // "strict" | "loose"

  // Score a doc: higher = more worth keeping.
  const score = c => {
    let s = 0;
    if (c.imageUrl) s += 100;                 // never throw away card art
    if (c.mktValue || c.marketValue) s += 50; // or market values
    if (c.dbs) s += 20;
    // For plays/HTDs, the copy that actually has its play data is the one worth keeping — a twin
    // missing playName/text is the junk import.
    if (c.playName) s += 60;
    if (c.text)     s += 40;
    // Raw presence, NOT hasPlayCost: this is choosing which duplicate to keep, so any stored cost
    // is evidence the row carried import data \u2014 even on a card that should not display one.
    if (c.playCost !== undefined && c.playCost !== "") s += 20;
    // Prefer the clean card number. An import can leave Excel's float artifact ("100.0") where the
    // real number is "100" — when both exist, the clean one is the keeper.
    if (!/^\d+\.0+$/.test(String(c.cardNum||"").trim())) s += 30;
    s += Object.keys(c).length;               // richer doc wins ties
    return s;
  };

  async function scan() {
    setLoading(true); setErr(""); setGroups(null); setDone(null);
    try {
      const snap = await getDocs(collection(db,"boba_checklist"));
      const all = snap.docs.map(d=>({...d.data(), id:d.id}));
      const by = {};
      all.forEach(c => {
        // A card's true identity. Card number ALONE is not unique — in Tecmo Bowl (and others)
        // the same number is reused across treatments/heroes. Two docs are only the same card if
        // ALL of these match (normalized for case/spacing so spelling variants still collapse).
        const n = v => String(v||"").trim().toLowerCase().replace(/\s+/g," ");
        // Excel turns plain numbers into floats, so an import can produce "100.0" where the real
        // card number is "100". Same card, different string → different id → a duplicate doc.
        // Strip a trailing ".0" (and any other trailing zeros after a decimal point) so those
        // collapse together. Only touches purely numeric values — "S-01/100" is left alone.
        const nNum = v => {
          const s = String(v||"").trim();
          return /^\d+\.0+$/.test(s) ? s.replace(/\.0+$/,"") : n(s);
        };
        // Plays and Home Team Discounts (e.g. "1-4-1", "HTD-16") carry their name in playName,
        // NOT hero — hero is often blank on them. The old key read hero only, so every play card
        // was either skipped outright by the identifiable-check below, or keyed on an empty name.
        // Either way their duplicates were invisible to this scan. They also have no weapon and no
        // power, so the name is nearly all the identity they have; reading it from the wrong field
        // meant they never matched.
        const nameOf = x => n(x.playName) || n(x.hero);
        // LOOSE deliberately still separates treatment. Some sets (Tecmo inserts, for one) reuse a
        // card number across genuinely different cards \u2014 Silver Battlefoil and Glow Skyline can both
        // be S130. Ignoring treatment made those look like duplicates and deleted real cards. Loose
        // now forgives drift in weapon and power only, which is what re-imports actually mangle.
        const key = mode === "loose"
          ? [n(c.setName), nNum(c.cardNum), nameOf(c), n(c.treatment)].join("|")
          : [n(c.setName), nNum(c.cardNum), nameOf(c), n(c.treatment), n(c.weapon), nNum(c.power)].join("|");
        // Identifiable = a card number and SOME name (play name or hero).
        if (!nNum(c.cardNum) || !nameOf(c)) return;
        (by[key] = by[key]||[]).push(c);
      });
      const dupes = Object.entries(by)
        .filter(([,list]) => list.length > 1)
        .map(([key,list]) => {
          const sorted = list.slice().sort((a,b)=>score(b)-score(a));
          return { key, keep: sorted[0], drop: sorted.slice(1) };
        })
        .sort((a,b)=>b.drop.length-a.drop.length);
      setGroups(dupes);

      // Separately: cards carrying Excel's float artifact ("100.0") that have NO duplicate twin.
      // They're not duplicates so the dedupe won't touch them, but the card number is still wrong
      // and needs cleaning.
      const dupIds = new Set(dupes.flatMap(g=>[g.keep.id, ...g.drop.map(d=>d.id)]));
      setOrphanFloats(all.filter(c =>
        /^\d+\.0+$/.test(String(c.cardNum||"").trim()) && !dupIds.has(c.id)
      ));
    } catch(e){ setErr(e.message||String(e)); }
    setLoading(false);
  }

  async function runDedupe() {
    if((!groups?.length && !orphanFloats.length) || running) return;
    const total = groups ? groups.reduce((n,g)=>n+g.drop.length,0) : 0;
    const fixes = orphanFloats.length;
    const msg = [
      total ? `Delete ${total.toLocaleString()} duplicate cards, keeping one of each.` : "",
      fixes ? `Clean up ${fixes.toLocaleString()} card numbers ("100.0" → "100").` : "",
    ].filter(Boolean).join("\n");
    if(!window.confirm(`${msg}\n\nThis cannot be undone. Continue?`)) return;
    setRunning(true);
    let deleted = 0;
    try {
      // Before deleting a duplicate, salvage anything the keeper is MISSING. The keeper is chosen
      // by score (card art wins), but a losing twin may still hold a field the winner never got —
      // a dbs value, a market value, play text. Deleting outright would throw that away for good.
      const SALVAGE = ["imageUrl","dbs","mktValue","marketValue","text","playName","playCost","notation","treatment","weapon","power"];
      const patches = [];
      groups.forEach(g => {
        const patch = {};
        SALVAGE.forEach(f => {
          const missing = g.keep[f]===undefined || g.keep[f]===null || g.keep[f]==="";
          if (!missing) return;
          const donor = g.drop.find(d => d[f]!==undefined && d[f]!==null && d[f]!=="");
          if (donor) patch[f] = donor[f];
        });
        if (Object.keys(patch).length) patches.push([g.keep.id, patch]);
      });
      for (let i=0;i<patches.length;i+=300) {
        const b = writeBatch(db);
        patches.slice(i,i+300).forEach(([id,patch]) => b.set(doc(db,"boba_checklist",id), patch, {merge:true}));
        await b.commit();
      }

      const toDrop = groups.flatMap(g=>g.drop);
      const CHUNK = 300;
      for (let i=0;i<toDrop.length;i+=CHUNK) {
        const batch = writeBatch(db);
        toDrop.slice(i,i+CHUNK).forEach(c => batch.delete(doc(db,"boba_checklist",c.id)));
        await batch.commit();
        deleted += Math.min(CHUNK, toDrop.length - i);
        setDone({ deleted, total, inProgress:true });
      }

      // Repair every card still carrying Excel's float artifact ("100.0" → "100") — both the ones
      // that won a dedupe group (because they held the image) and the orphans that never had a
      // duplicate twin at all. Either way the card number is wrong and should be cleaned.
      const needFix = [
        ...groups.map(g=>g.keep).filter(c => /^\d+\.0+$/.test(String(c.cardNum||"").trim())),
        ...orphanFloats,
      ];
      for (let i=0;i<needFix.length;i+=CHUNK) {
        const batch = writeBatch(db);
        needFix.slice(i,i+CHUNK).forEach(c => {
          batch.update(doc(db,"boba_checklist",c.id), { cardNum: String(c.cardNum).replace(/\.0+$/,"") });
        });
        await batch.commit();
      }
      // Rebuild the snapshot so the app reflects the cleanup.
      const snap2 = await getDocs(collection(db,"boba_checklist"));
      const all2 = snap2.docs.map(d=>({...d.data(), id:d.id}));
      try {
        const blob = new Blob([JSON.stringify(all2)], { type:"application/json" });
        await uploadBytes(ref(storage,"card_data/boba_checklist.json"), blob, { contentType:"application/json", cacheControl:"public,max-age=86400" });
      } catch(e){}
      try { await writeCardSnapshot(all2, 86400); } catch(e){}
      try { await setDoc(doc(db,"meta","cards_version"), { ts: Date.now(), count: all2.length }); } catch(e){}
      try { localStorage.removeItem("boba_checklist_cache"); localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
      try { await idbClearCards(); } catch(e){}
      setDone({ deleted, total, remaining: all2.length, inProgress:false });
      setGroups(null);
    } catch(e){ setErr(e.message||String(e)); }
    setRunning(false);
  }

  const S = { card:{background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",borderRadius:12,padding:18,marginTop:14} };
  const totalDrop = groups ? groups.reduce((n,g)=>n+g.drop.length,0) : 0;

  return (
    <div style={S.card}>
      <div style={{fontSize:15,fontWeight:800,color:"var(--bz-ink)",marginBottom:4}}>🧬 Find & Remove Duplicate Cards</div>
      <div style={{fontSize:12,color:"var(--bz-ink-3)",marginBottom:14,lineHeight:1.6}}>
        Finds cards that are the same physical card (same <strong>set + card number</strong>) but exist as multiple
        database entries — usually from an import where a field was spelled differently. Keeps the best copy of each
        (preferring one with card art or a market value) and deletes the rest.
      </div>
      {/* Strict vs loose. A re-import that drifted one field forks the card into a second doc that
          strict can never match — loose ignores treatment/weapon/power and groups on identity alone. */}
      <div style={{display:"flex",gap:8,marginBottom:12}}>
        {[["strict","Exact match","Same set, number, name, treatment, weapon and power."],
          ["loose","Same card, drifted data","Same set, number, name AND treatment \u2014 forgives weapon and power drift only. Treatment is never ignored: some sets reuse a card number across different inserts."]].map(([m,label,help])=>(
          <button key={m} onClick={()=>{setMode(m); setGroups(null); setDone(null);}} title={help}
            style={{flex:1,textAlign:"left",background:mode===m?"rgba(123,156,255,0.12)":"transparent",border:`1px solid ${mode===m?"#7B9CFF":"var(--bz-line-2)"}`,borderRadius:10,padding:"10px 12px",cursor:"pointer",fontFamily:"inherit"}}>
            <div style={{fontSize:12.5,fontWeight:800,color:mode===m?"#7B9CFF":"#ccc",marginBottom:2}}>{label}</div>
            <div style={{fontSize:10.5,color:"var(--bz-ink-3)",lineHeight:1.45}}>{help}</div>
          </button>
        ))}
      </div>
      {mode==="loose" && (
        <div style={{background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:9,padding:"10px 12px",fontSize:11.5,color:"#FBBF24",lineHeight:1.6,marginBottom:12}}>
          {"\u26A0\uFE0F"} Loose mode treats cards as duplicates even when their treatment or power disagree.
          That is what you want after a re-import forked your cards {"\u2014"} but it would also merge two genuinely
          different treatments that share a card number. <strong>Read the keep/drop list before applying.</strong>
        </div>
      )}


      <button onClick={scan} disabled={loading||running}
        style={{background:(loading||running)?"var(--bz-s2)":"linear-gradient(135deg,#7B9CFF,#7B2FF7)",color:(loading||running)?"#555":"#fff",border:"none",borderRadius:8,padding:"10px 22px",fontSize:13,fontWeight:800,cursor:(loading||running)?"not-allowed":"pointer",fontFamily:"inherit"}}>
        {loading?"Scanning all cards…":"Scan for duplicates"}
      </button>

      {err && <div style={{marginTop:12,color:"#EF4444",fontSize:12}}>⚠ {err}</div>}

      {groups && (
        <div style={{marginTop:16,background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:10,padding:14}}>
          {orphanFloats.length>0 && (
            <div style={{marginBottom:12,padding:"10px 12px",background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:8}}>
              <div style={{fontSize:12.5,fontWeight:800,color:"#FBBF24",marginBottom:3}}>
                🔢 {orphanFloats.length.toLocaleString()} card numbers to clean up
              </div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",lineHeight:1.55}}>
                These carry a spreadsheet artifact — the card number reads <strong>"100.0"</strong> instead of
                <strong> "100"</strong>. That mismatch is what created a lot of the duplicates in the first place.
                Running the cleanup will fix them.
              </div>
            </div>
          )}
          {groups.length===0 && orphanFloats.length===0 ? (
            <div style={{fontSize:13,color:"#4ade80",fontWeight:700}}>✓ Nothing to clean — every card is unique and every card number is clean.</div>
          ) : (
            <>
              {groups.length>0 && (
                <>
                  <div style={{fontSize:14,fontWeight:800,color:"#FBBF24",marginBottom:6}}>
                    Found {groups.length.toLocaleString()} duplicated cards · {totalDrop.toLocaleString()} extra copies to delete
                  </div>
                  <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:10,lineHeight:1.6}}>
                    Every copy below is the <strong>same card</strong> (same set, number, hero, treatment, weapon and power) —
                    only genuinely identical entries are grouped. Check a few before deleting.
                  </div>
                </>
              )}
              {groups.slice(0,8).map(g=>(
                <div key={g.key} style={{fontSize:11,color:"#ccc",padding:"8px 0",borderBottom:"1px solid #222"}}>
                  <div style={{color:"#7B9CFF",fontWeight:700,marginBottom:3}}>
                    {g.keep.setName} · #{g.keep.cardNum} · {g.keep.hero} · {g.keep.treatment||"—"} · {g.keep.weapon||"—"} · {g.keep.power||"—"}⚡
                  </div>
                  <div style={{color:"#4ade80"}}>keep 1{g.keep.imageUrl?" 🖼 (has art)":""}{(g.keep.mktValue||g.keep.marketValue)?" 💲":""}</div>
                  <div style={{color:"#EF4444"}}>delete {g.drop.length} identical {g.drop.length===1?"copy":"copies"}{g.drop.some(d=>d.imageUrl)?" ⚠️ one has art — it will be kept instead":""}</div>
                </div>
              ))}
              <button onClick={runDedupe} disabled={running}
                style={{marginTop:14,background:running?"var(--bz-s2)":"#E8317A",color:running?"#555":"#fff",border:"none",borderRadius:8,padding:"11px 22px",fontSize:13,fontWeight:800,cursor:running?"not-allowed":"pointer",fontFamily:"inherit"}}>
                {running
                  ? `Working… ${done?.deleted||0}/${totalDrop}`
                  : [totalDrop?`Delete ${totalDrop.toLocaleString()} duplicates`:"", orphanFloats.length?`fix ${orphanFloats.length.toLocaleString()} card numbers`:""].filter(Boolean).join(" · ")}
              </button>
            </>
          )}
        </div>
      )}

      {done && !done.inProgress && (
        <div style={{marginTop:16,background:"rgba(74,222,128,0.08)",border:"1px solid rgba(74,222,128,0.3)",borderRadius:10,padding:14,fontSize:12,color:"#4ade80",lineHeight:1.7}}>
          ✅ Deleted {done.deleted.toLocaleString()} duplicates · {done.remaining?.toLocaleString()} cards remain · snapshot rebuilt.
          <div style={{marginTop:6}}><button onClick={()=>window.location.reload()} style={{background:"#4ade80",color:"#062b13",border:"none",borderRadius:6,padding:"6px 14px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Reload app</button></div>
        </div>
      )}
    </div>
  );
}

// ── FOUND IN EDITOR ──────────────────────────────────────────────────────────
// Which pack types a treatment can be pulled from — edited PER SET, because the answer genuinely
// differs between releases (a treatment that's Hobby-only in one set can be Double Mega in the
// next). Stored in config/found_in as { "<set>|<treatment>": ["hobby","jumbo",...] } and read live
// by every client, so a change here updates the "Found In" badge for everyone immediately.
function FoundInEditor() {
  const [allCards, setAllCards] = useState([]);
  const [map,      setMap]      = useState({});   // { "set|treatment": [packIds] }
  const [setName,  setSetName]  = useState("");
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [err,      setErr]      = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [snap, cfg] = await Promise.all([
          getDocs(collection(db,"boba_checklist")),
          getDoc(doc(db,"config","found_in")),
        ]);
        setAllCards(snap.docs.map(d=>({...d.data(), id:d.id})));
        setMap(cfg.exists() ? (cfg.data().map || {}) : {});
      } catch(e){ setErr(e.message||String(e)); }
      setLoading(false);
    })();
  }, []);

  const setNames = useMemo(
    () => Array.from(new Set(allCards.map(c=>c.setName).filter(Boolean))).sort(),
    [allCards]
  );

  // Treatments actually present in the chosen set, with how many cards carry each.
  const treatments = useMemo(() => {
    if (!setName) return [];
    const m = {};
    allCards.filter(c => c.setName === setName).forEach(c => {
      const t = c.treatment || "(none)";
      m[t] = (m[t] || 0) + 1;
    });
    return Object.entries(m).sort((a,b) => b[1]-a[1]);
  }, [allCards, setName]);

  const keyFor = t => `${setName}|${t}`;
  const packsFor = t => map[keyFor(t)] || [];

  const togglePack = (t, packId) => {
    const k = keyFor(t);
    const cur = map[k] || [];
    const next = cur.includes(packId) ? cur.filter(x=>x!==packId) : [...cur, packId];
    setMap(m => ({ ...m, [k]: next }));
    setSaved(false);
  };

  // Apply one treatment's packs to every other treatment in this set — most sets have the same
  // answer for nearly everything, so this saves a lot of clicking.
  const applyToAll = (t) => {
    const packs = packsFor(t);
    if (!packs.length) { alert("Pick at least one pack type first."); return; }
    const labels = packs.map(id=>PACK_BY_ID[id]?.label).join(", ");
    if (!window.confirm(`Set EVERY treatment in "${setName}" to: ${labels}?`)) return;
    const next = { ...map };
    treatments.forEach(([tr]) => { next[`${setName}|${tr}`] = [...packs]; });
    setMap(next); setSaved(false);
  };

  async function save() {
    setSaving(true); setErr("");
    try {
      // Overwrite the map wholesale rather than merging. With merge:true, Firestore deep-merges
      // the nested object, so clearing every pack from a treatment would leave the old value
      // behind instead of emptying it. The editor holds the full map in state, so a plain write
      // is both correct and safe.
      await setDoc(doc(db,"config","found_in"), { map });
      setSaved(true);
      setTimeout(()=>setSaved(false), 2500);
    } catch(e){ setErr(e.message||String(e)); }
    setSaving(false);
  }

  const S = { card:{background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",borderRadius:12,padding:18,marginTop:14} };

  return (
    <div style={S.card}>
      <div style={{fontSize:15,fontWeight:800,color:"var(--bz-ink)",marginBottom:4}}>{"\uD83D\uDCE6"} Found In — which packs contain what</div>
      <div style={{fontSize:12,color:"var(--bz-ink-3)",marginBottom:14,lineHeight:1.6}}>
        Set which pack types each treatment can be pulled from. This drives the <strong>Found In</strong> badge on every
        card. It{"\u2019"}s per set on purpose — a treatment that{"\u2019"}s Hobby-only in one release can be Double Mega in
        the next. Changes go live for everyone as soon as you save.
      </div>

      {loading ? <div style={{fontSize:12,color:"var(--bz-ink-3)"}}>Loading{"\u2026"}</div> : (
        <>
          <select value={setName} onChange={e=>setSetName(e.target.value)}
            style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"10px 12px",fontSize:13,color:"#fff",fontFamily:"inherit",cursor:"pointer",marginBottom:14}}>
            <option value="">{"\u2014 Choose a set \u2014"}</option>
            {setNames.map(s2 => <option key={s2} value={s2}>{s2}</option>)}
          </select>

          {setName && (
            <div style={{border:"1px solid var(--bz-line-2)",borderRadius:10,overflow:"hidden"}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:8,padding:"9px 12px",background:"var(--bz-s2)",borderBottom:"1px solid var(--bz-line-2)",fontSize:10,fontWeight:800,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:0.6}}>
                <span>Treatment</span><span>Found in</span>
              </div>
              {treatments.map(([t,n]) => (
                <div key={t} style={{padding:"10px 12px",borderBottom:"1px solid #1c1c1c"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:7,flexWrap:"wrap"}}>
                    <span style={{fontSize:12.5,fontWeight:800,color:"var(--bz-ink)"}}>{t}</span>
                    <span style={{fontSize:10,color:"var(--bz-ink-3)"}}>{n} cards</span>
                    <button onClick={()=>applyToAll(t)} title="Give every treatment in this set the same packs"
                      style={{marginLeft:"auto",background:"transparent",border:"1px solid #333",color:"var(--bz-ink-3)",borderRadius:6,padding:"2px 9px",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                      apply to all
                    </button>
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {PACK_TYPES.map(pk => {
                      const on = packsFor(t).includes(pk.id);
                      return (
                        <button key={pk.id} onClick={()=>togglePack(t, pk.id)}
                          style={{background:on?`${pk.color}22`:"transparent",border:`1.5px solid ${on?pk.color:"#2f2f2f"}`,color:on?pk.color:"#666",borderRadius:7,padding:"5px 11px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                          {on ? "\u2713 " : ""}{pk.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {err && <div style={{marginTop:12,color:"#EF4444",fontSize:12}}>{"\u26A0"} {err}</div>}

          {setName && (
            <button onClick={save} disabled={saving}
              style={{marginTop:14,background:saving?"var(--bz-s2)":(saved?"#4ade80":"linear-gradient(135deg,#E8317A,#7B2FF7)"),color:saving?"#555":(saved?"#062b13":"#fff"),border:"none",borderRadius:8,padding:"11px 22px",fontSize:13,fontWeight:800,cursor:saving?"not-allowed":"pointer",fontFamily:"inherit"}}>
              {saving ? "Saving\u2026" : saved ? "\u2713 Saved \u2014 live for everyone" : "Save"}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── PREFIX MAPPER ────────────────────────────────────────────────────────────
// Card numbers encode the treatment: in Alpha Edition "BGBF-4" is a Bubble Gum Battlefoil,
// "PL-7" is a Paper Play, "HOD-2" is a Hot Dog. An import that doesn't know those conventions
// gets the treatment wrong (my Plays all came in tagged "Battlefoil"), which then breaks the
// deck builder — it excludes Plays by treatment, so mislabelled Plays leak into hero decks.
//
// Rather than hand-patch each import, define the mapping ONCE per set. Prefixes mean different
// things in different sets, so this is deliberately scoped to one set at a time.
function PrefixMapper() {
  const [allCards, setAllCards] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [setName,  setSetName]  = useState("");
  const [rules,    setRules]    = useState({});   // { prefix: {treatment, cardType} }
  const [running,  setRunning]  = useState(false);
  const [done,     setDone]     = useState(null);
  const [err,      setErr]      = useState("");

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db,"boba_checklist"));
        setAllCards(snap.docs.map(d=>({...d.data(), id:d.id})));
      } catch(e){ setErr(e.message||String(e)); }
      setLoading(false);
    })();
  }, []);

  const setNames = useMemo(
    () => Array.from(new Set(allCards.map(c=>c.setName).filter(Boolean))).sort(),
    [allCards]
  );

  // A card number's prefix is the leading letters before the first digit or dash.
  // "BGBF-4" -> BGBF   "PL-7" -> PL   "S-01/100" -> S   "145" -> "" (no prefix)
  const prefixOf = num => {
    const s = String(num||"").trim().toUpperCase();
    const m = s.match(/^([A-Z]+)/);
    return m ? m[1] : "";
  };

  // Every distinct prefix in the chosen set, with a sample card and the treatments currently on it.
  const prefixes = useMemo(() => {
    if (!setName) return [];
    const m = {};
    allCards.filter(c=>c.setName===setName).forEach(c => {
      const p = prefixOf(c.cardNum);
      if (!m[p]) m[p] = { prefix:p, count:0, treatments:{}, cardTypes:{}, sample:c };
      m[p].count++;
      const t = c.treatment || "—";
      m[p].treatments[t] = (m[p].treatments[t]||0)+1;
      const ct = c.cardType || "—";
      m[p].cardTypes[ct] = (m[p].cardTypes[ct]||0)+1;
    });
    return Object.values(m).sort((a,b)=>b.count-a.count);
  }, [allCards, setName]);

  // Treatments already in use anywhere — so you can pick an existing one rather than retyping it
  // (and risking "Silver Blast" vs "SILVER BLAST", which is what created duplicates before).
  // Treatments that exist IN THIS SET. Offering every treatment in the whole database is worse
  // than useless — it surfaces treatments that belong to other sets, plus any junk a bad import
  // left behind (a mis-parsed Play ability ending up in the treatment column, say). Scope it to
  // the set being edited so the options are only things that legitimately appear there.
  const knownTreatments = useMemo(() => {
    if (!setName) return [];
    return Array.from(new Set(
      allCards
        .filter(c => c.setName === setName)
        .map(c => c.treatment)
        .filter(Boolean)
        // Drop ability text that leaked into the treatment column — never suggest it as a value.
        .filter(t => !String(t).trim().endsWith("."))
    )).sort();
  }, [allCards, setName]);
  const CARD_TYPES = ["Hero","Play","Hot Dog","Bonus Play","Home Team Discount"];

  const setRule = (prefix, field, value) => {
    setRules(r => ({ ...r, [prefix]: { ...(r[prefix]||{}), [field]: value } }));
  };

  const pending = Object.entries(rules).filter(([,v]) => v.treatment || v.cardType);
  const affected = pending.reduce((n,[p]) => {
    const pf = prefixes.find(x=>x.prefix===p);
    return n + (pf?pf.count:0);
  }, 0);

  async function apply() {
    if (!setName || pending.length===0 || running) return;
    const lines = pending.map(([p,v]) => {
      const pf = prefixes.find(x=>x.prefix===p);
      const bits = [v.treatment && `treatment → "${v.treatment}"`, v.cardType && `type → "${v.cardType}"`].filter(Boolean);
      return `  ${p || "(no prefix)"}-*  (${pf?.count||0} cards):  ${bits.join(", ")}`;
    }).join("\n");
    if (!window.confirm(`Update ${affected.toLocaleString()} cards in "${setName}"?\n\n${lines}\n\nThis rewrites those fields on every matching card.`)) return;

    setRunning(true); setErr(""); setDone(null);
    try {
      const targets = allCards.filter(c => {
        if (c.setName !== setName) return false;
        const r = rules[prefixOf(c.cardNum)];
        return r && (r.treatment || r.cardType);
      });
      const CHUNK = 300;
      let updated = 0;
      for (let i=0;i<targets.length;i+=CHUNK) {
        const batch = writeBatch(db);
        targets.slice(i,i+CHUNK).forEach(c => {
          const r = rules[prefixOf(c.cardNum)];
          const patch = {};
          // "(clear)" wipes the field. Plays, Hot Dogs and Home Team Discounts have no treatment —
          // a bad import left their ABILITY TEXT sitting in that column ("Discard your Hero...").
          // Those values then pollute every treatment dropdown in the app, so they need removing,
          // not renaming.
          if (r.treatment === "(clear)") patch.treatment = "";
          else if (r.treatment) patch.treatment = r.treatment;
          if (r.cardType) patch.cardType = r.cardType;
          batch.update(doc(db,"boba_checklist",c.id), patch);
        });
        await batch.commit();
        updated += Math.min(CHUNK, targets.length - i);
        setDone({ updated, total: targets.length, inProgress:true });
      }
      // Republish so the change reaches everyone, and clear the local caches.
      const snap2 = await getDocs(collection(db,"boba_checklist"));
      const all2 = snap2.docs.map(d=>({...d.data(), id:d.id}));
      try { await writeCardSnapshot(all2, 86400); } catch(e){}
      try { await setDoc(doc(db,"meta","cards_version"), { ts: Date.now(), count: all2.length }); } catch(e){}
      try { localStorage.removeItem("boba_checklist_cache"); localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
      try { await idbClearCards(); } catch(e){}
      setDone({ updated, total: targets.length, inProgress:false });
      setAllCards(all2);
      setRules({});
    } catch(e){ setErr(e.message||String(e)); }
    setRunning(false);
  }

  const S = { card:{background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",borderRadius:12,padding:18,marginTop:14} };

  return (
    <div style={S.card}>
      <div style={{fontSize:15,fontWeight:800,color:"var(--bz-ink)",marginBottom:4}}>🏷 Map Card Prefixes → Treatments</div>
      <div style={{fontSize:12,color:"var(--bz-ink-3)",marginBottom:14,lineHeight:1.6}}>
        Card numbers encode what a card is — <strong>BGBF-4</strong> is a Bubble Gum Battlefoil, <strong>PL-7</strong> is a
        Paper Play, <strong>HOD-2</strong> is a Hot Dog. Imports don't know those conventions and get the treatment wrong.
        Map each prefix once and every matching card is corrected. Prefixes mean different things in different sets, so
        pick a set first.
      </div>

      {loading ? <div style={{fontSize:12,color:"var(--bz-ink-3)"}}>Loading checklist…</div> : (
        <>
          <select value={setName} onChange={e=>{setSetName(e.target.value); setRules({});}}
            style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"10px 12px",fontSize:13,color:"#fff",fontFamily:"inherit",cursor:"pointer",marginBottom:14}}>
            <option value="">— Choose a set —</option>
            {setNames.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          {setName && (
            <div style={{border:"1px solid var(--bz-line-2)",borderRadius:10,overflow:"hidden"}}>
              <div style={{display:"grid",gridTemplateColumns:"90px 1fr 150px 130px",gap:8,padding:"9px 12px",background:"var(--bz-s2)",borderBottom:"1px solid var(--bz-line-2)",fontSize:10,fontWeight:800,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:0.6}}>
                <span>Prefix</span><span>Currently</span><span>Set treatment →</span><span>Set type →</span>
              </div>
              {prefixes.map(p => {
                const curT = Object.entries(p.treatments).sort((a,b)=>b[1]-a[1]);
                const r = rules[p.prefix] || {};
                const changed = r.treatment || r.cardType;
                return (
                  <div key={p.prefix} style={{display:"grid",gridTemplateColumns:"90px 1fr 150px 130px",gap:8,padding:"9px 12px",borderBottom:"1px solid #1c1c1c",alignItems:"center",background:changed?"rgba(74,222,128,0.05)":"transparent"}}>
                    <div>
                      <div style={{fontSize:12.5,fontWeight:800,color:"#7B9CFF"}}>{p.prefix || "(none)"}</div>
                      <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>{p.count} cards</div>
                    </div>
                    <div style={{fontSize:11,color:"var(--bz-ink-3)",minWidth:0}}>
                      {/* A treatment that reads like a sentence isn't a treatment — it's a Play/HTD
                          ability that a bad import dropped into the wrong column. Flag it loudly,
                          because these values pollute every treatment dropdown in the app. */}
                      {curT.some(([t])=>String(t).trim().endsWith(".")) && (
                        <div style={{fontSize:10,fontWeight:800,color:"#EF4444",marginBottom:2}}>
                          ⚠ ability text in the treatment field — set this to "(clear)"
                        </div>
                      )}
                      <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {curT.map(([t,n])=>`${t} (${n})`).join(", ")}
                      </div>
                      <div style={{fontSize:10,opacity:0.6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        e.g. #{p.sample.cardNum} · {p.sample.hero||"—"}
                      </div>
                    </div>
                    <div style={{position:"relative"}}>
                      <input list={`treat-${p.prefix}`} value={r.treatment||""} placeholder="—"
                        onChange={e=>setRule(p.prefix,"treatment",e.target.value)}
                        style={{background:"var(--bz-bg)",border:`1px solid ${r.treatment?"#4ade80":"#333"}`,borderRadius:7,padding:"7px 9px",fontSize:12,color:"#fff",fontFamily:"inherit",width:"100%"}}/>
                      {/* Only the treatments that appear in THIS set — and you can still type a name
                          that isn't there yet, which is exactly what you need when a whole prefix is
                          currently mislabelled (e.g. Plays sitting under "Battlefoil"). */}
                      <datalist id={`treat-${p.prefix}`}>
                        <option value="(clear)"/>
                        {knownTreatments.map(t=><option key={t} value={t}/>)}
                      </datalist>
                    </div>
                    <select value={r.cardType||""} onChange={e=>setRule(p.prefix,"cardType",e.target.value)}
                      style={{background:"var(--bz-bg)",border:`1px solid ${r.cardType?"#4ade80":"#333"}`,borderRadius:7,padding:"7px 9px",fontSize:12,color:"#fff",fontFamily:"inherit",width:"100%",cursor:"pointer"}}>
                      <option value="">—</option>
                      {CARD_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          )}

          {err && <div style={{marginTop:12,color:"#EF4444",fontSize:12}}>⚠ {err}</div>}

          {pending.length>0 && (
            <button onClick={apply} disabled={running}
              style={{marginTop:14,background:running?"var(--bz-s2)":"linear-gradient(135deg,#4ade80,#22c55e)",color:running?"#555":"#062b13",border:"none",borderRadius:8,padding:"11px 22px",fontSize:13,fontWeight:800,cursor:running?"not-allowed":"pointer",fontFamily:"inherit"}}>
              {running ? `Updating… ${done?.updated||0}/${done?.total||0}` : `Apply to ${affected.toLocaleString()} cards`}
            </button>
          )}

          {done && !done.inProgress && (
            <div style={{marginTop:14,background:"rgba(74,222,128,0.08)",border:"1px solid rgba(74,222,128,0.3)",borderRadius:10,padding:14,fontSize:12,color:"#4ade80",lineHeight:1.7}}>
              ✅ Updated {done.updated.toLocaleString()} cards · snapshot republished.
              <div style={{marginTop:6}}>
                <button onClick={()=>window.location.reload()} style={{background:"#4ade80",color:"#062b13",border:"none",borderRadius:6,padding:"6px 14px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Reload app</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── SET MERGER ───────────────────────────────────────────────────────────────
// Consolidates legacy sets into a target set WITHOUT losing data. For each card in a
// source set it finds the matching card in the target set (by cardNum), copies over any
// fields the target is missing (imageUrl, mktValue, marketValue, dbs...), then deletes the
// source doc. Always preview before running — the delete is permanent.
function SetMerger() {
  const [allCards, setAllCards] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [loadErr,  setLoadErr]  = useState("");
  const [sources,  setSources]  = useState(new Set());
  const [target,   setTarget]   = useState("");
  const [preview,  setPreview]  = useState(null);
  const [running,  setRunning]  = useState(false);
  const [done,     setDone]     = useState(null);

  // Fields worth carrying over from the old doc if the new one doesn't have them.
  const CARRY = ["imageUrl","mktValue","marketValue","dbs","costPerCard","parallel"];

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db,"boba_checklist"));
        setAllCards(snap.docs.map(d=>({...d.data(), id:d.id})));
      } catch(e){
        console.error("merge: load failed", e);
        setLoadErr(e.message || String(e));
      }
      setLoading(false);
    })();
  }, []);

  const setNames = Array.from(new Set(allCards.map(c=>c.setName).filter(Boolean))).sort();
  const countOf = s => allCards.filter(c=>c.setName===s).length;

  function buildPreview() {
    const targetCards = allCards.filter(c=>c.setName===target);
    const byNum = {};
    targetCards.forEach(c=>{ if(c.cardNum) byNum[String(c.cardNum).toUpperCase()] = c; });

    const matched=[], orphans=[];
    [...sources].forEach(src=>{
      allCards.filter(c=>c.setName===src).forEach(oldC=>{
        const hit = byNum[String(oldC.cardNum||"").toUpperCase()];
        if(hit){
          const carries = CARRY.filter(f => oldC[f] && !hit[f]);
          matched.push({ old:oldC, next:hit, carries });
        } else {
          orphans.push(oldC); // no card with this number in the target — do NOT delete these
        }
      });
    });
    setPreview({ matched, orphans });
    setDone(null);
  }

  async function runMerge() {
    if(!preview || running) return;
    if(!window.confirm(`Copy data onto ${preview.matched.length} target cards, then DELETE ${preview.matched.length} old cards. This cannot be undone. Continue?`)) return;
    setRunning(true);
    let carried=0, deleted=0;
    try {
      const CHUNK=300;
      for(let i=0;i<preview.matched.length;i+=CHUNK){
        const batch = writeBatch(db);
        preview.matched.slice(i,i+CHUNK).forEach(({old:oldC,next,carries})=>{
          if(carries.length){
            const patch={}; carries.forEach(f=>{ patch[f]=oldC[f]; });
            batch.set(doc(db,"boba_checklist",next.id), patch, {merge:true});
            carried++;
          }
          batch.delete(doc(db,"boba_checklist",oldC.id));
          deleted++;
        });
        await batch.commit();
      }
      setDone({ carried, deleted, orphans: preview.orphans.length });
      setPreview(null);
      // reload
      const snap = await getDocs(collection(db,"boba_checklist"));
      setAllCards(snap.docs.map(d=>({...d.data(), id:d.id})));
    } catch(e){ console.error(e); alert("Merge failed: "+e.message); }
    setRunning(false);
  }

  const S={ card:{background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",borderRadius:12,padding:18,marginTop:14} };

  if(loading) return <div style={{...S.card,color:"#888"}}>Loading checklist from the database… (62k+ cards can take 30–60s)</div>;
  if(loadErr) return <div style={{...S.card,color:"#EF4444"}}>Failed to load: {loadErr}</div>;

  return (
    <div style={S.card}>
      <div style={{fontSize:15,fontWeight:800,color:"var(--bz-ink)",marginBottom:4}}>🔀 Merge Sets</div>
      <div style={{fontSize:12,color:"var(--bz-ink-3)",marginBottom:16,lineHeight:1.6}}>
        Fold legacy sets into a target set. Matches cards by <strong>card number</strong>, copies over anything the
        target is missing (images, values), then deletes the old cards. Cards with no match in the target are left
        alone — never deleted.
      </div>

      {/* Source sets */}
      <div style={{fontSize:12,fontWeight:700,color:"#E8317A",marginBottom:8}}>1. Old sets to merge FROM (will be deleted)</div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
        {setNames.map(s=>{
          const on=sources.has(s);
          return <button key={s} onClick={()=>{ setSources(p=>{const n=new Set(p); n.has(s)?n.delete(s):n.add(s); return n;}); setPreview(null); }}
            style={{background:on?"rgba(232,49,122,0.18)":"var(--bz-s2)",border:`1px solid ${on?"#E8317A":"var(--bz-line-2)"}`,color:on?"#E8317A":"#888",borderRadius:14,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            {s} ({countOf(s)})
          </button>;
        })}
      </div>

      {/* Target set */}
      <div style={{fontSize:12,fontWeight:700,color:"#4ade80",marginBottom:8}}>2. Target set to merge INTO (kept)</div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
        {setNames.filter(s=>!sources.has(s)).map(s=>(
          <button key={s} onClick={()=>{ setTarget(s); setPreview(null); }}
            style={{background:target===s?"rgba(74,222,128,0.18)":"var(--bz-s2)",border:`1px solid ${target===s?"#4ade80":"var(--bz-line-2)"}`,color:target===s?"#4ade80":"#888",borderRadius:14,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            {s} ({countOf(s)})
          </button>
        ))}
      </div>

      <button onClick={buildPreview} disabled={!sources.size||!target}
        style={{background:(sources.size&&target)?"linear-gradient(135deg,#E8317A,#7B2FF7)":"var(--bz-s2)",color:(sources.size&&target)?"#fff":"#555",border:"none",borderRadius:8,padding:"10px 22px",fontSize:13,fontWeight:800,cursor:(sources.size&&target)?"pointer":"not-allowed",fontFamily:"inherit"}}>
        Preview merge
      </button>

      {preview && (
        <div style={{marginTop:16,background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:10,padding:14}}>
          <div style={{fontSize:13,fontWeight:800,color:"#fff",marginBottom:8}}>Preview</div>
          <div style={{fontSize:12,color:"#4ade80",marginBottom:4}}>✓ {preview.matched.length} cards matched by card # — old data copied over, then old card deleted.</div>
          <div style={{fontSize:12,color:"#4ade80",marginBottom:4}}>
            ✓ {preview.matched.filter(m=>m.carries.length).length} of those carry data forward ({[...new Set(preview.matched.flatMap(m=>m.carries))].join(", ")||"nothing to carry"}).
          </div>
          {preview.orphans.length>0 && (
            <div style={{fontSize:12,color:"#FBBF24",marginTop:8,lineHeight:1.6}}>
              ⚠️ {preview.orphans.length} old card{preview.orphans.length!==1?"s":""} have no matching card # in "{target}" — these will be <strong>left alone</strong> (not deleted). Examples: {preview.orphans.slice(0,5).map(c=>c.cardNum).join(", ")}
            </div>
          )}
          <button onClick={runMerge} disabled={running||!preview.matched.length}
            style={{marginTop:12,background:running?"var(--bz-s2)":"#E8317A",color:running?"#555":"#fff",border:"none",borderRadius:8,padding:"10px 22px",fontSize:13,fontWeight:800,cursor:running?"not-allowed":"pointer",fontFamily:"inherit"}}>
            {running?"Merging…":`Merge & delete ${preview.matched.length} old cards`}
          </button>
        </div>
      )}

      {done && (
        <div style={{marginTop:16,background:"rgba(74,222,128,0.08)",border:"1px solid rgba(74,222,128,0.3)",borderRadius:10,padding:14,fontSize:12,color:"#4ade80",lineHeight:1.7}}>
          ✅ Done. Carried data onto {done.carried} cards · deleted {done.deleted} old cards
          {done.orphans>0 ? ` · left ${done.orphans} unmatched cards untouched` : ""}.
        </div>
      )}
    </div>
  );
}

function DataCleanup() {
  const [loading,    setLoading]    = useState(false);
  const [fieldData,  setFieldData]  = useState(null); // { weapon: {...}, treatment: {...} }
  const [merging,    setMerging]    = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  const [overrides,  setOverrides]  = useState({}); // { "GLOW": "Glow", "glow": "Glow" }

  async function scan() {
    setLoading(true);
    const snap = await getDocs(collection(db,"boba_checklist"));
    const weapons = {}, treatments = {};
    snap.docs.forEach(d => {
      const data = d.data();
      const w = data.weapon||"(none)";
      if (!weapons[w]) weapons[w] = 0;
      weapons[w]++;
      const t = data.treatment||"(none)";
      if (!treatments[t]) treatments[t] = 0;
      treatments[t]++;
    });
    setFieldData({ weapon: weapons, treatment: treatments });
    // Auto-detect obvious mismatches (same value different case)
    const autoOverrides = {};
    const wKeys = Object.keys(weapons);
    CANONICAL_WEAPONS.forEach(canonical => {
      wKeys.forEach(key => {
        if (key !== canonical && key.toLowerCase() === canonical.toLowerCase()) {
          autoOverrides[key] = canonical;
        }
      });
    });
    setOverrides(autoOverrides);
    setLoading(false);
  }

  async function applyMerge() {
    if (!Object.keys(overrides).length) return;
    setMerging(true);
    const snap = await getDocs(collection(db,"boba_checklist"));
    const toUpdate = snap.docs.filter(d => overrides[d.data().weapon]);
    let fixed = 0;
    const CHUNK = 400;
    for (let i=0; i<toUpdate.length; i+=CHUNK) {
      const batch = writeBatch(db);
      toUpdate.slice(i,i+CHUNK).forEach(d => {
        const newWeapon = overrides[d.data().weapon];
        batch.set(doc(db,"boba_checklist",d.id), { weapon: newWeapon }, { merge:true });
      });
      await batch.commit();
      fixed += Math.min(CHUNK, toUpdate.length-i);
    }
    setMerging(false);
    setMergeResult({ fixed });
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
  }

  const weaponGroups = fieldData ? Object.entries(fieldData.weapon).reduce((acc,[k,v]) => {
    const canonical = CANONICAL_WEAPONS.find(c => c.toLowerCase()===k.toLowerCase()) || "Other";
    if (!acc[canonical]) acc[canonical] = [];
    acc[canonical].push({ key:k, count:v });
    return acc;
  }, {}) : {};

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <div style={{ display:"flex", gap:10, alignItems:"center" }}>
        <Btn onClick={scan} disabled={loading}>{loading?"Scanning...":"🔍 Scan for duplicates"}</Btn>
        {fieldData && Object.keys(overrides).length>0 && (
          <Btn onClick={applyMerge} disabled={merging} variant="green">
            {merging?"Merging...":"✅ Apply fixes"}
          </Btn>
        )}
      </div>

      {mergeResult && <div style={{ fontSize:12, color:"#4ade80" }}>✅ Fixed {mergeResult.fixed} cards — reload checklist to see changes</div>}

      <div style={{ ...S.card }}>
        <SectionLabel t="8️⃣ Generate Missing Metallic Cards"/>
        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>
          Finds all auto cards (e.g. DBA1–DBA7) that are missing their Metallic "8" variant and creates them with treatment <strong style={{color:"#C0C0C0"}}>Inspired Ink Metallic Battlefoil</strong>.
        </div>
        <GenerateMissing8s/>

      {/* Fix Metallic Auto treatments */}
      <div style={{ ...S.card }}>
        <SectionLabel t="⚡ Fix Metallic Auto Treatments"/>
        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>
          Finds all cards where cardNum ends in "8" and treatment is "Inspired Ink Battlefoil" or "Inspired Ink Superfoil" — updates them to "Inspired Ink Metallic Battlefoil".
        </div>
        <FixMetallicAutos/>
      </div>

      {/* Fix treatment typos */}
      <div style={{ ...S.card }}>
        <SectionLabel t="🔤 Fix Treatment Typos"/>
        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>
          Finds cards with misspelled treatments and corrects them: "Halltime Alts" → "Halftime Alts", "Pixel Helemt Alt" → "Pixel Helmet Alt".
        </div>
        <FixTreatmentTypos/>
      </div>

      {/* Fix hero name typos */}
      <div style={{ ...S.card }}>
        <SectionLabel t="🦸 Fix Hero Name Typos"/>
        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>
          Finds cards with misspelled hero names and corrects them everywhere (checklist, /cards, /sell, collector database): "Boston Stongboy" → "Boston Strongboy".
        </div>
        <FixHeroTypos/>
      </div>

      {/* Swan City bulk import */}
      <div style={{ ...S.card }}>
        <SectionLabel t="⭐ Swan City Bulk Import (Supers & 1/1s)"/>
        <SwanCityBulkImport/>
      </div>
      </div>

      <div style={{ ...S.card }}>
        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>
          Sets treatment to <strong style={{color:"#C084FC"}}>Bonus Plays</strong> for BPL cards, and <strong style={{color:"var(--bz-ink-2)"}}>Paper Plays</strong> for PL cards.
        </div>
        <PlaysFixButton/>
      </div>

      {/* Prefix → Treatment fixer */}
      <div style={{ ...S.card }}>
        <SectionLabel t="🔧 Fix cardNum Prefix → Treatment"/>
        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>Bulk-update treatment for cards whose cardNum starts with a specific prefix.</div>
        <PrefixTreatmentFixer/>
      </div>

      {/* Bulk delete */}
      <div style={{ ...S.card }}>
        <SectionLabel t="🗑 Bulk Delete"/>
        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>Delete all cards matching a treatment or weapon from the database. Cannot be undone.</div>
        <BulkDeleter/>
      </div>

      {fieldData && (
        <div style={{ ...S.card }}>
          <SectionLabel t="⚔️ Weapon Variants"/>
          <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>Groups showing variants that will be merged. Add manual overrides for anything not auto-detected.</div>
          {Object.entries(weaponGroups).map(([canonical, variants]) => {
            const hasDup = variants.length > 1 || (variants.length===1 && variants[0].key!==canonical);
            return (
              <div key={canonical} style={{ marginBottom:12 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"var(--bz-ink)", marginBottom:4 }}>{canonical}</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {variants.map(({key,count}) => (
                    <div key={key} style={{ display:"flex", alignItems:"center", gap:6, background: key===canonical?"rgba(74,222,128,0.08)":"rgba(251,191,36,0.08)", border:`1px solid ${key===canonical?"rgba(74,222,128,0.2)":"rgba(251,191,36,0.3)"}`, borderRadius:7, padding:"4px 10px" }}>
                      <span style={{ fontSize:12, color:key===canonical?"#4ade80":"#FBBF24", fontWeight:700 }}>{key}</span>
                      <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{count}</span>
                      {key!==canonical && !overrides[key] && (
                        <button onClick={()=>setOverrides(p=>({...p,[key]:canonical}))}
                          style={{ fontSize:9, color:"#7B9CFF", background:"none", border:"none", cursor:"pointer" }}>merge→{canonical}</button>
                      )}
                      {overrides[key] && <span style={{ fontSize:9, color:"#4ade80" }}>→{overrides[key]}</span>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Manual weapon override */}
          <div style={{ marginTop:14, paddingTop:12, borderTop:"1px solid var(--bz-line)" }}>
            <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:8 }}>Add custom merge (e.g. "FIRE" → "Fire"):</div>
            <WeaponMergeInput overrides={overrides} setOverrides={setOverrides}/>
          </div>
        </div>
      )}
    </div>
  );
}

function WeaponMergeInput({ overrides, setOverrides }) {
  const [from, setFrom] = useState("");
  const [to,   setTo]   = useState("");
  function add() { if(from.trim()&&to.trim()) { setOverrides(p=>({...p,[from.trim()]:to.trim()})); setFrom(""); setTo(""); }}
  return (
    <div style={{ display:"flex", gap:8 }}>
      <input value={from} onChange={e=>setFrom(e.target.value)} placeholder="From (e.g. GLOW)" style={{ ...S.inp, flex:1, fontSize:11 }}/>
      <span style={{ color:"var(--bz-ink-3)", alignSelf:"center" }}>→</span>
      <input value={to} onChange={e=>setTo(e.target.value)} placeholder="To (e.g. Glow)" style={{ ...S.inp, flex:1, fontSize:11 }} onKeyDown={e=>e.key==="Enter"&&add()}/>
      <Btn onClick={add} disabled={!from.trim()||!to.trim()}>Add</Btn>
    </div>
  );
}

// ── MANUAL OVERRIDE EDITOR ───────────────────────────────────────────────────
function ManualOverrideEditor({ overrides, setOverrides }) {
  const [newFile,    setNewFile]    = useState("");
  const [newCardNum, setNewCardNum] = useState("");

  function add() {
    if (!newFile.trim() || !newCardNum.trim()) return;
    setOverrides(p => ({ ...p, [newFile.trim()]: newCardNum.trim() }));
    setNewFile(""); setNewCardNum("");
  }

  function remove(key) {
    const next = { ...overrides };
    delete next[key];
    setOverrides(next);
  }

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, marginBottom:10 }}>
        <input value={newFile} onChange={e=>setNewFile(e.target.value)}
          placeholder="Folder/Filename (e.g. (BJA) - Bo Jackson (12)/TB1)"
          style={{ ...S.inp, fontSize:11 }}/>
        <input value={newCardNum} onChange={e=>setNewCardNum(e.target.value)}
          placeholder="Firestore cardNum (e.g. TBJ-G)"
          style={{ ...S.inp, fontSize:11 }}
          onKeyDown={e=>e.key==="Enter"&&add()}/>
        <Btn onClick={add} disabled={!newFile.trim()||!newCardNum.trim()}>Add</Btn>
      </div>
      {Object.entries(overrides).length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
          {Object.entries(overrides).map(([file,cardNum])=>(
            <div key={file} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"var(--bz-bg)", borderRadius:6, padding:"5px 10px", fontSize:11 }}>
              <span><span style={{ color:"var(--bz-ink-2)" }}>{file}</span> → <span style={{ color:"#7B9CFF", fontWeight:700 }}>{cardNum}</span></span>
              <button onClick={()=>remove(file)} style={{ background:"none", border:"none", color:"#8a8a8a", cursor:"pointer", fontSize:12 }}>✕</button>
            </div>
          ))}
        </div>
      )}
      {Object.entries(overrides).length === 0 && <div style={{ fontSize:11, color:"#8a8a8a" }}>No overrides yet</div>}
    </div>
  );
}

// ── IMAGE COPIER ──────────────────────────────────────────────────────────────
function ImageCopier() {
  const [from,     setFrom]     = useState("");
  const [to,       setTo]       = useState("");
  const [running,  setRunning]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [treatments, setTreatments] = useState([]);

  useEffect(() => {
    getDocs(collection(db,"boba_checklist")).then(snap => {
      const ts = [...new Set(snap.docs.map(d=>d.data().treatment).filter(Boolean))].sort();
      setTreatments(ts);
    });
  }, []);

  async function run() {
    if (!from || !to || from===to) return;
    if (!window.confirm(`Copy all imageUrls from "${from}" → "${to}"?\n\nThis overwrites existing images on ${to} cards.`)) return;
    setRunning(true); setResult(null);
    const snap = await getDocs(collection(db,"boba_checklist"));
    const all = snap.docs.map(d=>({...d.data(), fsId:d.id}));
    const fromCards = all.filter(c=>c.treatment===from && c.imageUrl);
    const toCards   = all.filter(c=>c.treatment===to);

    // Build lookup by cardNum for from cards
    const fromByNum = {};
    fromCards.forEach(c=>{ fromByNum[String(c.cardNum||"").toLowerCase()] = c.imageUrl; });

    let copied = 0, skipped = 0;
    const CHUNK = 400;
    const updates = [];
    toCards.forEach(c => {
      const numKey = String(c.cardNum||"").toLowerCase();
      const url = fromByNum[numKey];
      if (url) updates.push({ fsId:c.fsId, url });
      else skipped++;
    });

    for (let i=0; i<updates.length; i+=CHUNK) {
      const batch = writeBatch(db);
      updates.slice(i,i+CHUNK).forEach(({fsId,url}) => {
        batch.set(doc(db,"boba_checklist",fsId), { imageUrl:url }, {merge:true});
      });
      await batch.commit();
      copied += Math.min(CHUNK, updates.length-i);
    }
    setRunning(false);
    setResult({ copied, skipped });
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr auto", gap:8, alignItems:"center" }}>
        <select value={from} onChange={e=>setFrom(e.target.value)} style={{ ...S.inp, cursor:"pointer", color:from?"var(--bz-ink)":"#555", fontSize:12 }}>
          <option value="">— Copy FROM —</option>
          {treatments.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
        <span style={{ color:"var(--bz-ink-3)", fontSize:18 }}>→</span>
        <select value={to} onChange={e=>setTo(e.target.value)} style={{ ...S.inp, cursor:"pointer", color:to?"var(--bz-ink)":"#555", fontSize:12 }}>
          <option value="">— Copy TO —</option>
          {treatments.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
        <Btn onClick={run} disabled={!from||!to||from===to||running} variant="green">
          {running?"Copying...":"Copy"}
        </Btn>
      </div>
      {result && (
        <div style={{ fontSize:12, color:"#4ade80" }}>
          ✅ Copied {result.copied} imageUrls · {result.skipped} had no match
        </div>
      )}
    </div>
  );
}

// ── TREATMENT CHECKER ─────────────────────────────────────────────────────────
function TreatmentChecker() {
  const [loading,    setLoading]    = useState(false);
  const [treatments, setTreatments] = useState(null);
  const [expanded,   setExpanded]   = useState(null);
  const [lookup,     setLookup]     = useState("");
  const [lookupResult, setLookupResult] = useState(null);

  async function check() {
    setLoading(true);
    const snap = await getDocs(collection(db,"boba_checklist"));
    const map = {};
    snap.docs.forEach(d => {
      const data = d.data();
      const t = data.treatment || "(no treatment)";
      if (!map[t]) map[t] = { count:0, samples:[] };
      map[t].count++;
      if (map[t].samples.length < 5) map[t].samples.push(data.cardNum || "?");
    });
    setTreatments(Object.entries(map).sort((a,b)=>b[1].count-a[1].count));
    setLoading(false);
  }

  async function doLookup() {
    if (!lookup.trim()) return;
    const q1 = lookup.trim();
    const q2 = q1.toLowerCase().replace(/-/g,"");
    const q3 = q2.replace(/^[a-z]+/,"");
    const snap = await getDocs(collection(db,"boba_checklist"));
    const found = snap.docs.map(d=>d.data()).filter(c => {
      const cn = String(c.cardNum||"").toLowerCase().replace(/-/g,"");
      return cn === q2 || cn === q3 || String(c.cardNum||"").toLowerCase() === q1.toLowerCase();
    });
    setLookupResult({ query:q1, found });
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {/* Direct card lookup */}
      <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:8, padding:"12px 14px" }}>
        <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink)", marginBottom:8 }}>🔎 Look up a card number in Firestore</div>
        <div style={{ display:"flex", gap:8 }}>
          <input value={lookup} onChange={e=>setLookup(e.target.value)} placeholder="e.g. RD528 or 528"
            style={{ ...S.inp, flex:1, fontSize:12 }}
            onKeyDown={e=>e.key==="Enter"&&doLookup()}/>
          <Btn variant="ghost" onClick={doLookup}>Search</Btn>
        </div>
        {lookupResult && (
          <div style={{ marginTop:10 }}>
            {lookupResult.found.length === 0
              ? <div style={{ fontSize:11, color:"#EF4444" }}>❌ No cards found for "{lookupResult.query}" — this cardNum doesn't exist in Firestore</div>
              : lookupResult.found.map((c,i) => (
                <div key={i} style={{ fontSize:11, color:"#4ade80", marginTop:4 }}>
                  ✅ cardNum: <strong>{c.cardNum}</strong> | treatment: <strong>{c.treatment}</strong> | hero: {c.hero}
                </div>
              ))
            }
          </div>
        )}
      </div>

      <Btn variant="ghost" onClick={check} disabled={loading}>
        {loading ? "Loading..." : "🔍 Show all treatments + sample card numbers"}
      </Btn>
      {treatments && (
        <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:8, padding:"10px 14px", maxHeight:300, overflowY:"auto" }}>
          {treatments.map(([t,{count,samples}]) => (
            <div key={t} onClick={()=>setExpanded(expanded===t?null:t)}
              style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:11, padding:"5px 0", borderBottom:"1px solid #111", cursor:"pointer" }}>
              <div>
                <span style={{ color:"var(--bz-ink-2)" }}>{t}</span>
                {expanded===t && <div style={{ color:"#7B9CFF", marginTop:3, fontSize:10 }}>Sample cardNums: {samples.join(", ")}</div>}
              </div>
              <span style={{ color:"var(--bz-ink-3)", flexShrink:0, marginLeft:10 }}>{count} cards</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CARD SET IMPORTER ─────────────────────────────────────────────────────────
// The one place a catalog card's document id is decided. Identity is set + number + hero +
// treatment + WEAPON \u2014 weapon matters because Skyline exists as Fire, Ice and Glow on the same card
// number, and leaving it out hashed all three onto a single document, so importing the set wrote
// them over each other and only one survived.
// Normalise first, then derive slug and hash from the SAME normalised string, so a stray space or
// capital in a CSV can never fork a card into a second document.
function checklistCardId(card) {
  const nrm = v => String(v==null?"":v).trim().toLowerCase().replace(/\s+/g," ");
  const key = [nrm(card.setName), nrm(card.cardNum), nrm(card.hero||card.playName), nrm(card.treatment), nrm(card.weapon)].join("|");
  const slug = key.replace(/[^a-z0-9]/g,"").slice(0,20);
  const hash = Math.abs(key.split("").reduce((h,ch)=>((h<<5)-h)+ch.charCodeAt(0)|0,0)).toString(16).slice(0,6);
  return slug + "_" + hash;
}

function CardSetImporter({ userRole }) {
  const isAdmin = ["Admin"].includes(userRole?.role);
  const [mode,      setMode]      = useState("data");   // "data" | "images" | "cleanup" | "single"
  // Skipping cards that already have an image is a RESUME feature — it lets a huge upload be re-run
  // without redoing work. But it also blocks replacing placeholder art, which is a real workflow:
  // drop placeholders in now, upload the real scans later. So it has to be switchable.
  const [imgOverwrite, setImgOverwrite] = useState(false);
  const [newCard, setNewCard] = useState({ hero:"", treatment:"", weapon:"", cardNum:"", setName:"", power:"", playName:"", athlete:"" });
  // Bulk-add a whole insert. Paste rows, preview which are genuinely new, then write only those.
  const [bulkText, setBulkText] = useState("");
  // Checklists copied from the BoBA site have no Set column \u2014 the page already knows which set you
  // are looking at. Rather than make people hand-add a column to every paste, the set can be
  // chosen here and applied to any row that does not carry one.
  const [bulkSetName, setBulkSetName] = useState("");
  const [bulkTreatment, setBulkTreatment] = useState("");
  const [bulkWeapon, setBulkWeapon] = useState("");
  const [bulkPreview, setBulkPreview] = useState(null);   // {create:[], existing:[], bad:[]}
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState(null);

  // Read pasted rows WITHOUT writing anything, and check each id against the database. This is the
  // whole safety story: you see exactly what will be created before it happens, and cards that
  // already exist are listed separately rather than silently overwritten.
  async function previewBulkInsert() {
    setBulkBusy(true); setBulkMsg(null); setBulkPreview(null);
    try {
      const lines = bulkText.split(/\r?\n/).map(l=>l.trim()).filter(Boolean);
      if (!lines.length) { setBulkMsg({ok:false,text:"Nothing pasted."}); setBulkBusy(false); return; }
      // Real CSVs quote any field containing a comma \u2014 and treatments like "Grandma's Linoleum, Fire"
      // do. A naive split on "," tears those rows apart, which is why a clean export can come back
      // as "unusable" on every line. This respects quotes and doubled "" escapes.
      const split = (line) => {
        if (line.includes("\t") && !line.includes('"')) return line.split("\t");
        const out = []; let cur = "", inQ = false;
        for (let i=0; i<line.length; i++) {
          const ch = line[i];
          if (inQ) {
            if (ch === '"') { if (line[i+1] === '"') { cur += '"'; i++; } else inQ = false; }
            else cur += ch;
          } else if (ch === '"') inQ = true;
          else if (ch === "," || ch === "\t") { out.push(cur); cur = ""; }
          else cur += ch;
        }
        out.push(cur);
        return out;
      };
      // Header names are normalised by stripping everything except letters, which turns the very
      // header this tool documents \u2014 "Card #" \u2014 into "card", matching none of the known keys. Every
      // row then failed for a missing card number. Keep digits, and accept the short forms too.
      const head = split(lines[0]).map(h=>h.trim().toLowerCase().replace(/[^a-z0-9]/g,""));
      const known = ["set","setname","cardnum","cardnumber","cardno","card","number","num","no",
                     "hero","treatment","weapon","power","pwr","playname","athlete"];
      const hasHeader = head.some(h=>known.includes(h));
      const cols = hasHeader ? head : null;
      const at = (parts, names) => {
        if (!cols) return null;
        for (const n of names) { const i = cols.indexOf(n); if (i>=0) return (parts[i]||"").trim(); }
        return null;
      };
      const rows = (hasHeader ? lines.slice(1) : lines).map(split);
      const create = [], existing = [], bad = [];
      for (const parts of rows) {
        const card = cols ? {
          setName:   at(parts,["setname","set"]) || "",
          cardNum:   at(parts,["cardnum","cardnumber","cardno","card","number","num","no"]) || "",
          hero:      at(parts,["hero"]) || "",
          treatment: at(parts,["treatment"]) || "",
          weapon:    at(parts,["weapon"]) || "",
          power:     at(parts,["power","pwr"]) || "",
          playName:  at(parts,["playname"]) || "",
          athlete:   at(parts,["athlete"]) || "",
        } : {
          // Positional fallback: Set, CardNum, Hero, Treatment, Weapon, Power
          setName:(parts[0]||"").trim(), cardNum:(parts[1]||"").trim(), hero:(parts[2]||"").trim(),
          treatment:(parts[3]||"").trim(), weapon:(parts[4]||"").trim(), power:(parts[5]||"").trim(),
          playName:"", athlete:"",
        };
        // Say WHICH field is missing. "needs Set, Card # and Hero" on all 133 rows does not tell you
        // whether the header was misread or one column is genuinely blank.
        // Fall back to the pickers above for anything the paste does not carry. A value IN the row
        // always wins \u2014 the picker only fills gaps, so a mixed file still imports correctly.
        if (!card.setName)   card.setName   = bulkSetName;
        if (!card.treatment) card.treatment = bulkTreatment;
        if (!card.weapon)    card.weapon    = bulkWeapon;
        if (!card.hero || !card.setName || !card.cardNum) {
          const miss = [];
          if (!card.setName) miss.push("Set");
          if (!card.cardNum) miss.push("Card #");
          if (!card.hero)    miss.push("Hero");
          bad.push({ card, why: "missing " + miss.join(", ") + (cols ? "" : " (no header row found \u2014 columns read by position)") });
          continue;
        }
        card.id = checklistCardId(card);
        if (create.some(x=>x.id===card.id)) { bad.push({ card, why:"duplicate row in your paste" }); continue; }
        create.push(card);
      }
      // Check what's already in the database, in chunks so a big paste doesn't hammer Firestore.
      const stillNew = [];
      for (let i=0;i<create.length;i+=25) {
        const batch = create.slice(i,i+25);
        const snaps = await Promise.all(batch.map(x=>getDoc(doc(db,"boba_checklist",x.id)).catch(()=>null)));
        snaps.forEach((s,k)=>{ if (s && s.exists()) existing.push({...batch[k], _was:s.data()}); else stillNew.push(batch[k]); });
      }
      setBulkPreview({ create: stillNew, existing, bad, header: cols, rowCount: rows.length });
    } catch(e) {
      setBulkMsg({ok:false,text:"Couldn't read that: "+(e?.message||e)});
    }
    setBulkBusy(false);
  }

  async function applyBulkInsert() {
    if (!bulkPreview?.create?.length) return;
    setBulkBusy(true); setBulkMsg(null);
    try {
      const list = bulkPreview.create;
      const CHUNK = 400;
      for (let i=0;i<list.length;i+=CHUNK) {
        const batch = writeBatch(db);
        list.slice(i,i+CHUNK).forEach(card => {
          const { _was, ...data } = card;
          batch.set(doc(db,"boba_checklist",card.id), {
            ...data,
            power: data.power===""||data.power==null ? 0 : (parseInt(data.power)||0),
            treatment: data.treatment || "Base Set",
            imageUrl: "",
          }, { merge:true });
        });
        await batch.commit();
      }
      setBulkMsg({ok:true,text:`Added ${list.length} card${list.length===1?"":"s"}. Rebuild the card snapshot to see them in the app.`});
      setBulkPreview(null); setBulkText("");
    } catch(e) {
      setBulkMsg({ok:false,text:"Insert failed: "+(e?.message||e)});
    }
    setBulkBusy(false);
  }
  const [addingCard, setAddingCard] = useState(false);
  const [addCardMsg, setAddCardMsg] = useState(null);
  const [files,     setFiles]     = useState([]);
  const [imgFiles,  setImgFiles]  = useState([]);       // flat list of {file, folder, cardNum}
  const [importSet, setImportSet] = useState("");       // restrict image import to this set (avoids cross-set hero collisions)
  const [folderMappings,  setFolderMappings]  = useState({});
  const [folderHeroes,    setFolderHeroes]    = useState({});
  const [manualOverrides, setManualOverrides] = useState({}); // { "folder/cardNum": "firestoreCardNum" }
  const [importing, setImporting] = useState(false);
  const [progress,  setProgress]  = useState(null);
  const [results,   setResults]   = useState(null);
  const [errors,    setErrors]    = useState([]);

  // Add ONE missing card straight into boba_checklist (admin). Uses the SAME idempotent id scheme as
  // the bulk importer, so if this card later comes in via a CSV import it lands on the same doc
  // instead of duplicating. Requires hero + set at minimum.
  async function addSingleCard() {
    const c = newCard;
    if (!c.hero.trim() || !c.setName.trim()) { setAddCardMsg({ ok:false, text:"Hero and Set are required." }); return; }
    setAddingCard(true); setAddCardMsg(null);
    try {
      const id = checklistCardId(c);
      // Don't clobber an existing card — if this id already exists, tell the admin.
      const existing = await getDoc(doc(db,"boba_checklist",id));
      if (existing.exists()) { setAddCardMsg({ ok:false, text:`That card already exists in the database (${existing.data().hero||"?"} ${existing.data().cardNum||""}).` }); setAddingCard(false); return; }
      const cardData = {
        hero: c.hero.trim(),
        treatment: c.treatment.trim() || "Base Set",
        weapon: c.weapon.trim(),
        cardNum: c.cardNum.trim(),
        setName: c.setName.trim(),
        power: c.power.trim(),
        ...(c.playName.trim() ? { playName: c.playName.trim() } : {}),
        ...(c.athlete.trim() ? { athlete: c.athlete.trim() } : {}),
        addedManually: true,
        addedAt: new Date().toISOString(),
      };
      await setDoc(doc(db,"boba_checklist",id), cardData, { merge:true });
      setAddCardMsg({ ok:true, text:`Added ${cardData.hero}${cardData.cardNum?` #${cardData.cardNum}`:""}. Rebuild the card snapshot (Cleanup tab) so it shows for everyone.` });
      setNewCard({ hero:"", treatment:"", weapon:"", cardNum:"", setName: c.setName, power:"", playName:"", athlete:"" }); // keep set for fast multi-add
    } catch(e) {
      setAddCardMsg({ ok:false, text:"Couldn't add card: " + (e?.message||e) });
    } finally { setAddingCard(false); }
  }

  const setList = (()=>{ try { const r=localStorage.getItem("boba_checklist_cache_v3"); if(r){ const {cards}=JSON.parse(r); if(cards?.length){ return [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort(); } } } catch(e){} return []; })();

  if (!isAdmin) return <div style={{ padding:40, textAlign:"center", color:"var(--bz-ink-3)" }}>Admin only.</div>;

  // ── Data import ──────────────────────────────────────────────────────────
  function onJsonDrop(e) {
    e.preventDefault();
    const dropped = [...(e.dataTransfer?.files || e.target.files)].filter(f=>f.name.endsWith(".json"));
    setFiles(dropped); setResults(null); setErrors([]);
  }

  async function runDataImport() {
    if (!files.length) return;
    setImporting(true); setResults(null); setErrors([]);
    let totalWritten = 0, totalSkipped = 0;
    const errs = [];
    for (const file of files) {
      setProgress({ done:0, total:0, label:`Reading ${file.name}...` });
      let cards;
      try {
        const text = await file.text();
        cards = JSON.parse(text);
        if (!Array.isArray(cards)) throw new Error("Expected a JSON array");
      } catch(e) { errs.push(`${file.name}: ${e.message}`); continue; }
      const CHUNK = 400;
      setProgress({ done:0, total:cards.length, label:`Importing ${file.name}...` });
      for (let i=0; i<cards.length; i+=CHUNK) {
        const chunk = cards.slice(i, i+CHUNK);
        try {
          const batch = writeBatch(db);
          chunk.forEach(card => {
            // One shared definition of card identity — see checklistCardId.
            if (!card.id) card.id = checklistCardId(card);
            // Never overwrite imageUrl or mktValue — preserve existing data
            const { imageUrl, mktValue, ...cardData } = card;
            batch.set(doc(db,"boba_checklist",card.id), cardData, {merge:true});
          });
          await batch.commit();
          totalWritten += chunk.length;
        } catch(e) { errs.push(`${file.name} chunk ${Math.floor(i/CHUNK)+1}: ${e.message}`); totalSkipped+=chunk.length; }
        setProgress({ done:Math.min(i+CHUNK,cards.length), total:cards.length, label:`Importing ${file.name}...` });
        await new Promise(r=>setTimeout(r,80));
      }
    }
    setImporting(false); setProgress(null); setErrors(errs);
    setResults({ written:totalWritten, skipped:totalSkipped, files:files.length });
    // Also write a single CDN-served JSON snapshot for fast public page loads.
    // NOTE: the app loads cards from this SNAPSHOT, not from boba_checklist directly. If the
    // snapshot fails to regenerate, imported cards will NOT appear even though the import
    // succeeded. So these failures must be loud, not swallowed.
    if (totalWritten > 0) {
      const snapErrs = [];
      try {
        setProgress({ done:0, total:1, label:"Rebuilding card snapshot (this is what the app reads)..." });
        const snap2 = await getDocs(collection(db,"boba_checklist"));
        const all = snap2.docs.map(d=>({...d.data(), id:d.id}));

        try {
          const blob = new Blob([JSON.stringify(all)], { type:"application/json" });
          await uploadBytes(ref(storage,"card_data/boba_checklist.json"), blob, { contentType:"application/json", cacheControl:"public,max-age=86400" });
        } catch(e) { snapErrs.push(`Snapshot upload failed (Storage): ${e.message}`); }

        try { await writeCardSnapshot(all, 86400); }
        catch(e) { snapErrs.push(`Gzipped snapshot failed: ${e.message}`); }

        try { await setDoc(doc(db,"meta","cards_version"), { ts: Date.now(), count: all.length }); }
        catch(e) { snapErrs.push(`Version bump failed (meta/cards_version): ${e.message} — without this, clients keep serving the OLD card list.`); }

        setProgress(null);
        if (snapErrs.length) setErrors(prev => [...prev, ...snapErrs]);
        else setResults(prev => ({ ...(prev||{}), snapshot: all.length }));
      } catch(e) {
        setProgress(null);
        setErrors(prev => [...prev, `Snapshot rebuild failed: ${e.message}. Cards were written to the database, but the app reads a snapshot — they won't appear until this succeeds.`]);
      }
    }
    // The card list lives in IndexedDB (12MB — too big for localStorage). Both must be
    // cleared or the app keeps serving the pre-import list.
    try { localStorage.removeItem("boba_checklist_cache"); localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
    try { await idbClearCards(); } catch(e) { console.warn("idb clear failed", e); }
  }

  // ── Image import ─────────────────────────────────────────────────────────
  function onFolderDrop(e) {
    e.preventDefault();
    e.currentTarget && (e.currentTarget.style.borderColor = "var(--bz-line-2)");
    const items = [...(e.dataTransfer?.items || [])];
    if (!items.length) return;
    setResults(null); setErrors([]);
    setFolderMappings({});

    const allFiles = [];
    let pending = 0;

    function readEntry(entry, folderName) {
      if (entry.isFile) {
        pending++;
        entry.file(file => {
          const name = file.name.toLowerCase();
          if (name.endsWith(".png")||name.endsWith(".jpg")||name.endsWith(".jpeg")||name.endsWith(".webp")) {
            const cardNum = file.name.replace(/\.[^.]+$/,"");
            allFiles.push({ file, folder: folderName, cardNum });
          }
          pending--;
          if (pending === 0) setImgFiles([...allFiles]);
        });
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        function readBatch() {
          pending++;
          reader.readEntries(entries => {
            pending--;
            if (entries.length === 0) { if (pending===0) setImgFiles([...allFiles]); return; }
            entries.forEach(child => readEntry(child, entry.name));
            readBatch(); // keep reading until exhausted
          });
        }
        readBatch();
      }
    }

    items.forEach(item => {
      const entry = item.webkitGetAsEntry?.();
      if (entry) readEntry(entry, entry.name);
    });
  }

  async function runImageImport() {
    if (!imgFiles.length) return;
    setImporting(true); setResults(null); setErrors([]);
    const errs = [];
    let written = 0, skipped = 0;

    setProgress({ done:0, total:imgFiles.length, label:"Loading card index from Firestore..." });
    const snap = await getDocs(collection(db,"boba_checklist"));
    const allCardsRaw = snap.docs.map(d=>({...d.data(), fsId:d.id}));
    // If a target set is chosen, only match within it (prevents cross-set hero collisions)
    const allCards = importSet ? allCardsRaw.filter(c => c.setName === importSet) : allCardsRaw;
    // RESUME: skip cards that already have an image so re-runs don't re-scan/re-pay.
    const alreadyImaged = new Set(allCards.filter(c => c.imageUrl && String(c.imageUrl).startsWith("http")).map(c => c.fsId));

    // Build a fast lookup: cardNum → cards (all cards)
    const byCardNum = {};
    allCards.forEach(c => {
      const key = String(c.cardNum||"").toLowerCase().replace(/-/g,"");
      if (!byCardNum[key]) byCardNum[key] = [];
      byCardNum[key].push(c);
    });

    // Also build per-treatment lookup for when treatment is manually assigned
    const byTreatment = {};
    allCards.forEach(c => {
      const t = (c.treatment||"").toLowerCase();
      if (!byTreatment[t]) byTreatment[t] = {};
      const key = String(c.cardNum||"").toLowerCase().replace(/-/g,"");
      if (!byTreatment[t][key]) byTreatment[t][key] = [];
      byTreatment[t][key].push(c);
    });

    setProgress({ done:0, total:imgFiles.length, label:`Uploading ${imgFiles.length} images (20 at a time)...` });

    // Process in parallel batches of 20
    const PARALLEL = 4; // lower for Vision API rate limits (every image is scanned)
    let done = 0;

    function fuzzyScore(treatment, folderName) {
      const STOP = new Set(["battlefoil","inspired","ink","the","and","or","of","a","an","alt","art","only","new","first","edition"]);
      function keywords(s) {
        return s.toLowerCase().replace(/['']/g,"").replace(/[^a-z0-9\s]/g," ").split(/\s+/).filter(w=>w.length>1&&!STOP.has(w));
      }
      const tk = keywords(treatment); const fk = keywords(folderName);
      if (!tk.length || !fk.length) return 0;
      const overlap = fk.filter(w => tk.some(t => t.includes(w) || w.includes(t)));
      return overlap.length / Math.max(fk.length, 1);
    }

    async function visionMatch(file) {
      try {
        const base64 = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = (ev) => {
            const img = new Image();
            img.onload = () => {
              const MAX = 1400;
              const scale = Math.min(1, MAX / Math.max(img.width, img.height));
              const canvas = document.createElement("canvas");
              canvas.width = Math.round(img.width * scale);
              canvas.height = Math.round(img.height * scale);
              canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
              res(canvas.toDataURL("image/jpeg", 0.9).split(",")[1]);
            };
            img.onerror = rej;
            img.src = ev.target.result;
          };
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
        const resp = await fetch("/api/scan-card", {
          method:"POST", headers:{ "Content-Type":"application/json" },
          body: JSON.stringify({ imageBase64: base64, mediaType:"image/jpeg" })
        });
        if (!resp.ok) return null;
        const v = await resp.json();
        if (!v || (!v.cardNum && !v.hero)) return null;
        const vNum = String(v.cardNum||"").toLowerCase().replace(/[\s-]/g,"");
        const vHero = (v.hero||"").trim().toLowerCase();
        const vWeapon = (v.weapon||"").trim().toLowerCase();
        const vPower = String(v.power||"");
        const firstWord = s => (s||"").toLowerCase().trim().split(" ")[0];
        // 1. card# + hero (strongest)
        if (vNum && vHero) { const c = allCards.find(c => String(c.cardNum||"").toLowerCase().replace(/[\s-]/g,"")===vNum && firstWord(c.hero)===firstWord(vHero)); if (c) return c; }
        // 2. card# alone — but only if unique, or disambiguate by power
        if (vNum) { const m = allCards.filter(c => String(c.cardNum||"").toLowerCase().replace(/[\s-]/g,"")===vNum); if (m.length===1) return m[0]; if (m.length>1 && vPower) { const byp = m.find(c=>String(c.power||"")===vPower); if (byp) return byp; } }
        // 3. hero + weapon + power (when number unreadable)
        if (vHero && vWeapon && vPower) { const m = allCards.filter(c => firstWord(c.hero)===firstWord(vHero) && (c.weapon||"").toLowerCase()===vWeapon && String(c.power||"")===vPower); if (m.length===1) return m[0]; }
        return null;
      } catch(e) { return null; }
    }

    async function uploadOne(item) {
      // VISION-FIRST: these filenames have no real card numbers, so read the card itself.
      let card = await visionMatch(item.file);
      if (card && !imgOverwrite && alreadyImaged.has(card.fsId)) { written++; return; } // resume, unless overwriting
      if (card) {
        try {
          const ext = (item.file.name.split(".").pop()||"png").toLowerCase();
          const storageRef2 = ref(storage, `boba_cards/${card.fsId}.${ext}`);
          await uploadBytes(storageRef2, item.file);
          const url = await getDownloadURL(storageRef2);
          await setDoc(doc(db,"boba_checklist",card.fsId), { imageUrl:url }, {merge:true});
          written++;
        } catch(e) { errs.push(`${item.file.name}: ${e.message}`); skipped++; }
        return;
      }
      const numKey = String(item.cardNum).toLowerCase().replace(/-/g,"");
      const numStripped = numKey.replace(/^[a-z]+/,"");
      // Handle RD→R mismatch: RD528 strips to 528, but Firestore may have R528
      const numAltPrefix = numStripped ? "r" + numStripped : "";
      const manualTreatment = folderMappings[item.folder];



      // Check manual override first (folder/cardNum → firestoreCardNum)
      const overrideKey = `${item.folder}/${item.cardNum}`;
      const overrideCardNum = manualOverrides[overrideKey];
      if (overrideCardNum) {
        const oc = String(overrideCardNum).toLowerCase().replace(/-/g,"");
        const found = allCards.find(c => String(c.cardNum||"").toLowerCase().replace(/-/g,"") === oc);
        if (found) { 
          try {
            const safeFolderName2 = item.folder.replace(/[^a-zA-Z0-9_\-\.]/g, "_");
            const storagePath2 = `boba_cards/tecmo/${safeFolderName2}/${item.cardNum}.png`;
            const storageRef3 = ref(storage, storagePath2);
            await uploadBytes(storageRef3, item.file);
            const url2 = await getDownloadURL(storageRef3);
            await setDoc(doc(db,"boba_checklist",found.fsId), { imageUrl:url2 }, {merge:true});
            written++;
          } catch(e) { errs.push(`Override ${overrideKey}: ${e.message}`); skipped++; }
          return;
        }
      }

      if (manualTreatment) {
        const treatLookup = byTreatment[manualTreatment.toLowerCase()] || {};
        let matches = treatLookup[numKey] || treatLookup[numStripped] || treatLookup[numAltPrefix] || [];

        // Fallback: match by trailing number + hero name from folder name
        // e.g. "(BJA) - Bo Jackson (12)/BJA2" → hero "Bo Jackson", number "2" → find boja2
        if (!matches.length && numStripped) {
          const heroMatch = item.folder.match(/[-–]\s*([A-Za-z .\']+?)\s*\(/);
          const heroName = heroMatch ? heroMatch[1].trim().toLowerCase() : "";
          const allTreatCards = Object.values(treatLookup).flat();
          if (heroName) {
            matches = allTreatCards.filter(c => {
              const cn = String(c.cardNum||"").toLowerCase().replace(/-/g,"");
              const nameOk = (c.hero||"").toLowerCase().includes(heroName.split(" ")[0]) ||
                             (c.inspiredBy||"").toLowerCase().includes(heroName.split(" ").pop());
              return cn.endsWith(numStripped) && nameOk;
            });
          }
          // Last resort: just match by trailing number within treatment
          if (!matches.length) {
            matches = allTreatCards.filter(c => {
              const cn = String(c.cardNum||"").toLowerCase().replace(/-/g,"");
              return cn.endsWith(numStripped) && cn.length <= numStripped.length + 4;
            });
          }
        }
        card = matches[0];
      } else {
        const matches = byCardNum[numKey] || byCardNum[numStripped] || byCardNum[numAltPrefix] || [];
        card = matches.length === 1 ? matches[0]
          : matches.reduce((best, c2) => {
              const score = fuzzyScore(c2.treatment||"", item.folder);
              return (!best || score > best.score) ? { card:c2, score } : best;
            }, null)?.card || matches[0];
      }

      if (!card) {
        const treatLookupKeys = manualTreatment ? Object.keys(byTreatment[manualTreatment.toLowerCase()]||{}) : [];
        const treatCount = treatLookupKeys.length;
        const sample = treatLookupKeys.slice(0,3).join(", ");
        if (errs.length < 3) {
          errs.push(`No match: ${item.cardNum} → tried "${numKey}", "${numStripped}" | treatment "${manualTreatment||"auto"}" has ${treatCount} cards, sample cardNums: ${sample||"none"}`);
        } else {
          errs.push(`No match: ${item.folder}/${item.cardNum}`);
        }
        skipped++; return;
      }

      try {
        const safeFolderName = item.folder.replace(/[^a-zA-Z0-9_\-\.]/g, "_");
        const storagePath = `boba_cards/tecmo/${safeFolderName}/${item.cardNum}.png`;
        const storageRef2 = ref(storage, storagePath);
        await uploadBytes(storageRef2, item.file);
        const url = await getDownloadURL(storageRef2);
        await setDoc(doc(db,"boba_checklist",card.fsId), { imageUrl:url }, {merge:true});
        written++;
      } catch(e) {
        errs.push(`${item.folder}/${item.cardNum}: ${e.message}`);
        skipped++;
      }
    }

    for (let i = 0; i < imgFiles.length; i += PARALLEL) {
      const batch2 = imgFiles.slice(i, i + PARALLEL);
      await Promise.all(batch2.map(uploadOne));
      done = Math.min(i + PARALLEL, imgFiles.length);
      const folder = batch2[0]?.folder || "";
      setProgress({ done, total:imgFiles.length, label:`Uploading... ${folder} (${done}/${imgFiles.length})` });
    }

    // Regenerate the public snapshot so /cards shows the new images immediately.
    if (written > 0) {
      try {
        setProgress({ done:0, total:1, label:"Updating public card data (so images show on /cards)..." });
        const snap2 = await getDocs(collection(db,"boba_checklist"));
        const all = snap2.docs.map(d=>({...d.data(), id:d.id}));
        const blob = new Blob([JSON.stringify(all)], { type:"application/json" });
        const snapRef = ref(storage, "card_data/boba_checklist.json");
        await uploadBytes(snapRef, blob, { contentType:"application/json", cacheControl:"public,max-age=300" });
        try { await writeCardSnapshot(all, 300); } catch(e) {}
        try { await setDoc(doc(db,"meta","cards_version"), { ts: Date.now(), count: all.length }); } catch(e) {}
        setProgress(null);
      } catch(e) { console.warn("Public snapshot write failed:", e); setProgress(null); }
    }

    setImporting(false); setProgress(null); setErrors(errs);
    setResults({ written, skipped, files: [...new Set(imgFiles.map(f=>f.folder))].length });
    try { localStorage.removeItem("boba_checklist_cache"); localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
  }

  const pct = progress?.total > 0 ? Math.round(progress.done/progress.total*100) : 0;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14, padding:20 }}>
      <div style={{ fontSize:18, fontWeight:900, color:"var(--bz-ink)" }}>⬆️ Card Set Importer</div>

      {/* Mode toggle */}
      <div style={{ display:"flex", gap:8 }}>
        {[["single","➕ Add Card"],["data","📄 Import Data"],["images","🖼 Import by Filename"],["prefix","🏷 Prefixes"],["foundin","📦 Found In"],["treatments","🏷 Merge Treatments"],["junkplays","🧹 Junk Plays"],["dedupe","🧬 Dedupe"],["merge","🔀 Merge Sets"],["cleanup","🧹 Cleanup"],["manual","🎯 Manual Image"]].map(([m,l])=>(
          <button key={m} onClick={()=>{ setMode(m); setResults(null); setErrors([]); }}
            style={{ background:mode===m?"rgba(232,49,122,0.12)":"var(--bz-s1)", border:`1.5px solid ${mode===m?"#E8317A":"var(--bz-line-2)"}`, color:mode===m?"#E8317A":"#888", borderRadius:8, padding:"8px 18px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
            {l}
          </button>
        ))}
      </div>


        {/* ── Add a single missing card ── */}
        {mode==="single" && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontSize:12.5,color:"var(--bz-ink-3)",lineHeight:1.5}}>
              Add one card that's missing from the database. It gets the same ID scheme as a CSV import, so a later bulk import of the same card won't duplicate it. <strong style={{color:"var(--bz-ink)"}}>Hero</strong> and <strong style={{color:"var(--bz-ink)"}}>Set</strong> are required.
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {[["hero","Hero *","e.g. Wrecking Ball"],["setName","Set *","e.g. Tecmo Bowl"],["treatment","Treatment","e.g. Base Set"],["weapon","Weapon","Fire / Ice / Steel / …"],["cardNum","Card #","e.g. TB-42"],["power","Power","e.g. 135"],["playName","Play name (optional)",""],["athlete","Athlete (optional)",""]].map(([k,label,ph])=>(
                <div key={k} style={{display:"flex",flexDirection:"column",gap:4}}>
                  <label style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-3)"}}>{label}</label>
                  <input value={newCard[k]} onChange={e=>setNewCard(v=>({...v,[k]:e.target.value}))} placeholder={ph}
                    style={{background:"var(--bz-s1)",border:"1.5px solid var(--bz-line-2)",color:"var(--bz-ink)",borderRadius:8,padding:"8px 10px",fontSize:13,fontFamily:"inherit"}}/>
                </div>
              ))}
            </div>
            {addCardMsg && (
              <div style={{fontSize:12.5,fontWeight:600,color:addCardMsg.ok?"#4ade80":"#f87171",background:addCardMsg.ok?"rgba(74,222,128,0.08)":"rgba(248,113,113,0.08)",border:`1px solid ${addCardMsg.ok?"rgba(74,222,128,0.3)":"rgba(248,113,113,0.3)"}`,borderRadius:8,padding:"9px 12px"}}>
                {addCardMsg.text}
              </div>
            )}
            <button onClick={addSingleCard} disabled={addingCard}
              style={{alignSelf:"flex-start",background:addingCard?"#333":"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:10,padding:"11px 22px",fontSize:14,fontWeight:800,cursor:addingCard?"wait":"pointer",fontFamily:"inherit"}}>
              {addingCard?"Adding…":"➕ Add Card to Database"}
            </button>
          {/* ── Bulk add a whole insert ──────────────────────────────────────────────────────
              Adding a 100-card insert one card at a time is not realistic. This takes a paste,
              shows exactly what is new versus what already exists, and writes ONLY the new ones —
              so it is safe to run against a set that is partly there, which is the situation when
              recovering cards lost to the old id collision. */}
          <div style={{marginTop:18,paddingTop:16,borderTop:"1px solid var(--bz-line-2)"}}>
            <div style={{fontSize:13,fontWeight:900,color:"#fff",marginBottom:4}}>{"\uD83D\uDCE5"} Add a whole insert</div>
            <div style={{fontSize:11.5,color:"#888",lineHeight:1.6,marginBottom:10}}>
              Paste rows from a spreadsheet (tab or comma separated). Include a header row with
              <strong style={{color:"#bbb"}}> Set, Card #, Hero, Treatment, Weapon, Power</strong> — or paste in that order without one.
              Weapon matters: Skyline Fire, Skyline Ice and Skyline Glow are three different cards.
            </div>
            {/* Apply-to-all pickers. A checklist copied from the BoBA site lists card numbers and
                heroes but no Set column, because the page you copied it from already implied it.
                Anything set here fills only the gaps \u2014 a value present in the row always wins. */}
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
              <div style={{flex:"1 1 160px",minWidth:150}}>
                <div style={{fontSize:10.5,fontWeight:800,color:bulkSetName?"#4ade80":"#FBBF24",marginBottom:4}}>
                  {bulkSetName ? "\u2705 Set" : "\u26a0\ufe0f Set (if not in the file)"}
                </div>
                <input value={bulkSetName} onChange={e=>{setBulkSetName(e.target.value); setBulkPreview(null);}}
                  list="bulk-set-list" placeholder="e.g. 2025 Alpha Update"
                  style={{width:"100%",background:"var(--bz-bg)",border:"1px solid "+(bulkSetName?"rgba(74,222,128,0.5)":"rgba(251,191,36,0.5)"),borderRadius:8,padding:"8px 10px",fontSize:12,color:"#fff",fontFamily:"inherit"}}/>
                <datalist id="bulk-set-list">
                  {[...new Set(cards.map(c=>c.setName).filter(Boolean))].sort().map(s=><option key={s} value={s}/>)}
                </datalist>
              </div>
              <div style={{flex:"1 1 150px",minWidth:140}}>
                <div style={{fontSize:10.5,fontWeight:800,color:"var(--bz-ink-3)",marginBottom:4}}>Treatment (optional)</div>
                <input value={bulkTreatment} onChange={e=>{setBulkTreatment(e.target.value); setBulkPreview(null);}}
                  list="bulk-treat-list" placeholder="e.g. Skyline"
                  style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"8px 10px",fontSize:12,color:"#fff",fontFamily:"inherit"}}/>
                <datalist id="bulk-treat-list">
                  {[...new Set(cards.map(c=>c.treatment).filter(Boolean))].sort().map(t=><option key={t} value={t}/>)}
                </datalist>
              </div>
              <div style={{flex:"1 1 130px",minWidth:120}}>
                <div style={{fontSize:10.5,fontWeight:800,color:"var(--bz-ink-3)",marginBottom:4}}>Weapon (optional)</div>
                <input value={bulkWeapon} onChange={e=>{setBulkWeapon(e.target.value); setBulkPreview(null);}}
                  list="bulk-weap-list" placeholder="e.g. Fire"
                  style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"8px 10px",fontSize:12,color:"#fff",fontFamily:"inherit"}}/>
                <datalist id="bulk-weap-list">
                  {[...new Set(cards.map(c=>c.weapon).filter(Boolean))].sort().map(w=><option key={w} value={w}/>)}
                </datalist>
              </div>
            </div>
            {/* Upload the CSV directly. Pasting a large export into a textarea is where rows get
                mangled \u2014 truncated, re-wrapped, or stripped of quoting by the clipboard \u2014 so reading
                the file itself removes a whole class of "every row is unusable" failures. */}
            <label style={{display:"inline-flex",alignItems:"center",gap:8,background:"rgba(123,156,255,0.12)",border:"1px solid rgba(123,156,255,0.45)",color:"#7B9CFF",borderRadius:8,padding:"8px 13px",fontSize:12.5,fontWeight:800,cursor:"pointer",marginBottom:10}}>
              {"\uD83D\uDCC4"} Choose CSV file
              <input type="file" accept=".csv,.tsv,.txt,text/csv,text/plain" style={{display:"none"}}
                onChange={async e => {
                  const f = e.target.files?.[0];
                  e.target.value = "";               // allow re-picking the same file
                  if (!f) return;
                  try {
                    const text = await f.text();
                    setBulkText(text);
                    setBulkPreview(null);
                    setBulkMsg({ ok:true, text:`Loaded ${f.name} \u2014 press Preview to check it before anything is written.` });
                  } catch (err) {
                    setBulkMsg({ ok:false, text:"Couldn't read that file: " + (err?.message || err) });
                  }
                }}/>
            </label>
            <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:8}}>{"\u2026"}or paste the rows below.</div>
            <textarea value={bulkText} onChange={e=>{setBulkText(e.target.value); setBulkPreview(null);}}
              rows={7} placeholder={"Set\tCard #\tHero\tTreatment\tWeapon\tPower\n2025 Tecmo Bowl\tS130\tMajik Man\tSkyline\tGlow\t80"}
              style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"10px 12px",fontSize:12,color:"#fff",fontFamily:"ui-monospace,monospace",lineHeight:1.5}}/>
            <div style={{display:"flex",gap:8,marginTop:8,flexWrap:"wrap"}}>
              <button onClick={previewBulkInsert} disabled={bulkBusy||!bulkText.trim()}
                style={{background:bulkBusy?"#333":"rgba(123,156,255,0.15)",border:"1px solid rgba(123,156,255,0.5)",color:"#7B9CFF",borderRadius:8,padding:"9px 14px",fontSize:12.5,fontWeight:800,cursor:bulkBusy?"default":"pointer",fontFamily:"inherit"}}>
                {bulkBusy ? "Checking\u2026" : "\uD83D\uDD0D Preview"}
              </button>
              {bulkPreview?.create?.length > 0 && (
                <button onClick={applyBulkInsert} disabled={bulkBusy}
                  style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",border:"none",color:"#fff",borderRadius:8,padding:"9px 14px",fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                  Add {bulkPreview.create.length} new card{bulkPreview.create.length===1?"":"s"}
                </button>
              )}
            </div>

            {bulkMsg && (
              <div style={{marginTop:10,fontSize:12,fontWeight:700,color:bulkMsg.ok?"#4ade80":"#EF4444",lineHeight:1.6}}>{bulkMsg.text}</div>
            )}

            {bulkPreview && (
              <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:10}}>
                {/* Show what the parser actually understood. If the header was misread, every row
                    fails for the same reason and this is the fastest way to see why. */}
                <div style={{fontSize:10.5,color:"var(--bz-ink-3)",lineHeight:1.6}}>
                  {bulkPreview.header
                    ? <>Header detected: <span style={{color:"#7B9CFF",fontWeight:700}}>{bulkPreview.header.join(", ")}</span></>
                    : <span style={{color:"#FBBF24",fontWeight:700}}>No header row found {"\u2014"} columns read by position: Set, Card #, Hero, Treatment, Weapon, Power</span>}
                  {" \u00b7 "}{bulkPreview.rowCount} row{bulkPreview.rowCount===1?"":"s"} read
                </div>
                <div style={{display:"flex",gap:14,flexWrap:"wrap",fontSize:12,fontWeight:800}}>
                  <span style={{color:"#4ade80"}}>{bulkPreview.create.length} new</span>
                  <span style={{color:"#888"}}>{bulkPreview.existing.length} already in database</span>
                  {bulkPreview.bad.length>0 && <span style={{color:"#EF4444"}}>{bulkPreview.bad.length} unusable</span>}
                </div>
                {bulkPreview.create.length>0 && (
                  <div style={{background:"rgba(74,222,128,0.06)",border:"1px solid rgba(74,222,128,0.3)",borderRadius:8,padding:"9px 11px",maxHeight:170,overflowY:"auto"}}>
                    {bulkPreview.create.slice(0,60).map((x,n)=>(
                      <div key={n} style={{fontSize:11,color:"#ddd",lineHeight:1.7,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                        {x.cardNum} {x.hero} {"\u00b7"} {x.treatment||"Base Set"}{x.weapon?` \u00b7 ${x.weapon}`:""}
                      </div>
                    ))}
                    {bulkPreview.create.length>60 && <div style={{fontSize:10.5,color:"#666",marginTop:3}}>{"\u2026"}and {bulkPreview.create.length-60} more</div>}
                  </div>
                )}
                {bulkPreview.bad.length>0 && (
                  <div style={{background:"rgba(239,68,68,0.06)",border:"1px solid rgba(239,68,68,0.3)",borderRadius:8,padding:"9px 11px",maxHeight:120,overflowY:"auto"}}>
                    {bulkPreview.bad.slice(0,20).map((b,n)=>(
                      <div key={n} style={{fontSize:11,color:"#f88",lineHeight:1.7}}>
                        {b.card.cardNum||"?"} {b.card.hero||"?"} {"\u2014"} {b.why}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          </div>


        )}


      {/* ── JSON import ── */}
      {mode==="data" && (
        <>
          <div style={{ fontSize:12, color:"var(--bz-ink-3)" }}>Drop the <code style={{ color:"#7B9CFF" }}>tecmo_cards_final.json</code> file to import all card data into Firestore.</div>
          <div onDragOver={e=>e.preventDefault()} onDrop={onJsonDrop}
            style={{ border:"2px dashed var(--bz-line-2)", borderRadius:14, padding:"40px 24px", textAlign:"center", background:"var(--bz-bg)", cursor:"pointer" }}>
            <div style={{ fontSize:28, marginBottom:10 }}>📂</div>
            <div style={{ fontSize:14, fontWeight:700, color:"var(--bz-ink)", marginBottom:6 }}>
              {files.length>0 ? `${files.length} file${files.length!==1?"s":""} ready` : "Drop JSON files here"}
            </div>
            <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>or click to browse</div>
            <input type="file" accept=".json" multiple onChange={onJsonDrop} style={{ display:"none" }} id="json-input"/>
            <label htmlFor="json-input" style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:7, padding:"7px 18px", fontSize:12, fontWeight:700, cursor:"pointer" }}>Browse</label>
          </div>
          {files.length>0 && (
            <div style={{ ...S.card }}>
              {[...files].map((f,i)=><div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:"1px solid var(--bz-line)", fontSize:13 }}><span style={{ color:"var(--bz-ink)" }}>📄 {f.name}</span><span style={{ color:"var(--bz-ink-3)" }}>{(f.size/1024).toFixed(0)} KB</span></div>)}
              <div style={{ marginTop:12 }}><Btn onClick={runDataImport} disabled={importing} variant="green">{importing?"⏳ Importing...":"⬆️ Import"}</Btn></div>
            </div>
          )}
        </>
      )}

      {/* ── Image import ── */}
      {mode==="images" && (
        <>
          <div style={{ fontSize:11, color:"#4ade80", background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.25)", borderRadius:8, padding:"9px 12px", marginBottom:10, lineHeight:1.5 }}>
            ✅ <strong>Smart matching is ON.</strong> This first tries to match by the card number in the filename. If the filename doesn't have a real card number (e.g. "page-0001" or "set1-1"), it automatically <strong>reads the card number off the image itself</strong> and matches that. So just drop your images and hit Upload — it handles both cases. (Image-reading is slower, so a big batch with no filename numbers will take a while.)
          </div>
          {/* Without this, placeholder art can never be replaced — the resume-skip treats a placeholder
              as "already done" and refuses the real scan. */}
          <label style={{ display:"flex", alignItems:"center", gap:9, cursor:"pointer", background:imgOverwrite?"rgba(232,49,122,0.1)":"rgba(255,255,255,0.03)", border:"1px solid "+(imgOverwrite?"rgba(232,49,122,0.45)":"var(--bz-line-2)"), borderRadius:10, padding:"10px 12px", margin:"10px 0" }}>
            <input type="checkbox" checked={imgOverwrite} onChange={e=>setImgOverwrite(e.target.checked)} style={{ accentColor:"#E8317A", width:16, height:16 }}/>
            <span style={{ fontSize:12.5, fontWeight:800, color:imgOverwrite?"#E8317A":"rgba(255,255,255,0.65)" }}>
              Replace images that already exist
            </span>
            <span style={{ fontSize:10.5, color:"rgba(255,255,255,0.35)" }}>
              {imgOverwrite ? "every match is overwritten — use this to swap out placeholders" : "off: cards that already have an image are skipped (safe to re-run)"}
            </span>
          </label>
          <div style={{ fontSize:12, color:"var(--bz-ink-3)", lineHeight:1.8 }}>
            In Finder, <strong style={{ color:"var(--bz-ink)" }}>select all your image folders</strong> at once (Cmd+A or Cmd+click each one), then <strong style={{ color:"#E8317A" }}>drag them all onto the box below</strong>. All folders load in one go. Uploads 20 at a time.
          </div>
          <div onDragOver={e=>{ e.preventDefault(); e.currentTarget.style.borderColor="#E8317A"; e.currentTarget.style.background="rgba(232,49,122,0.05)"; }}
               onDragLeave={e=>{ e.currentTarget.style.borderColor="var(--bz-line-2)"; e.currentTarget.style.background="var(--bz-s1)"; }}
               onDrop={e=>{ e.currentTarget.style.borderColor="var(--bz-line-2)"; e.currentTarget.style.background="var(--bz-s1)"; onFolderDrop(e); }}
            style={{ border:"2px dashed var(--bz-line-2)", borderRadius:14, padding:"60px 24px", textAlign:"center", background:"var(--bz-bg)", transition:"all 0.2s", cursor:"default" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>📂</div>
            <div style={{ fontSize:15, fontWeight:800, color:"var(--bz-ink)", marginBottom:6 }}>
              {imgFiles.length>0
                ? <span style={{ color:"#4ade80" }}>✓ {imgFiles.length} images from {[...new Set(imgFiles.map(f=>f.folder))].length} folders ready</span>
                : "Drag your folders here"}
            </div>
            <div style={{ fontSize:12, color:"var(--bz-ink-3)" }}>
              {imgFiles.length>0
                ? `Folders: ${[...new Set(imgFiles.map(f=>f.folder))].slice(0,5).join(", ")}${[...new Set(imgFiles.map(f=>f.folder))].length>5?" ...":""}`
                : "Select all folders in Finder → drag onto this box"}
            </div>
          </div>

          {imgFiles.length>0 && (() => {
            const folders = [...new Set(imgFiles.map(f=>f.folder))].sort();
            // Get all unique treatments from Firestore (derive from bobaCards if loaded, else common list)
            const COMMON_TREATMENTS = [
              "Base","Battlefoil","Alpha Battlefoil","Scoreboard Battlefoil","Helmets Battlefoil",
              "80's 8-Bit Rad Battlefoil","Sore Thumb Battlefoil","Tecmo News Battlefoil",
              "Logofoil","Gold Coin Flip Battlefoil","Silver Coin Flip Battlefoil",
              "Blow on it Battlefoil","Skyline Battlefoil","Endzone Battlefoil",
              "Big Pixel Battlefoil","Rage Quit Battlefoil","Helmet Icon Battlefoil",
              "Red Battlefoil","Orange Battlefoil","Blue Battlefoil","Green Battlefoil",
              "Pink Battlefoil","Bubble Gum Battlefoil","Color Battlefoil",
              "Inspired Ink Battlefoil","Inspired Ink Metallic Battlefoil",
              "Inspired Ink Bubble Gum Battlefoil","Inspired Ink Superfoil",
              "Superfoil","Halftime Alt Art","8-Bit Alt Art","Pixel Helmet Alt",
              "Logofoil","Bonus Play","Hot Dog",
            ];
            const allMapped = folders.every(f => folderMappings[f]);
            return (
              <div style={{ ...S.card }}>
                <SectionLabel t={`📁 Assign Treatments — ${folders.length} folders`}/>
                <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>
                  Map each folder to the correct treatment. Auto-detect leaves it to fuzzy matching.
                </div>
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:8 }}>Not sure what treatment name to pick? Check what's actually in Firestore:</div>
                  <TreatmentChecker/>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:14 }}>
                  {folders.map(folder => {
                    const count = imgFiles.filter(f=>f.folder===folder).length;
                    return (
                      <div key={folder} style={{ background:"var(--bz-bg)", borderRadius:8, padding:"8px 12px", border:"1px solid var(--bz-line)" }}>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto", gap:8, alignItems:"center" }}>
                          <div>
                            <div style={{ fontSize:12, fontWeight:700, color:"var(--bz-ink)" }}>{folder}</div>
                            <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{count} images</div>
                          </div>
                          <select value={folderMappings[folder]||""} onChange={e=>setFolderMappings(p=>({...p,[folder]:e.target.value}))}
                            style={{ ...S.inp, fontSize:11, cursor:"pointer", color:folderMappings[folder]?"var(--bz-ink)":"#555" }}>
                            <option value="">— Auto-detect —</option>
                            {COMMON_TREATMENTS.map(t=><option key={t} value={t}>{t}</option>)}
                          </select>
                          <div style={{ fontSize:10, color:"var(--bz-ink-3)", textAlign:"right" }}>{folderMappings[folder] ? "✅" : "⚡"}</div>
                        </div>
                        {/* Per-file overrides for problem files */}
                        {imgFiles.filter(f=>f.folder===folder).some(f=>manualOverrides[`${folder}/${f.cardNum}`]) && (
                          <div style={{ marginTop:8, paddingTop:8, borderTop:"1px solid var(--bz-line)" }}>
                            {imgFiles.filter(f=>f.folder===folder && manualOverrides[`${folder}/${f.cardNum}`]).map(f=>(
                              <div key={f.cardNum} style={{ fontSize:10, color:"#7B9CFF" }}>
                                {f.cardNum} → {manualOverrides[`${folder}/${f.cardNum}`]}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginBottom:12, background:importSet?"rgba(74,222,128,0.06)":"rgba(251,191,36,0.06)", border:`1px solid ${importSet?"rgba(74,222,128,0.3)":"rgba(251,191,36,0.3)"}`, borderRadius:10, padding:"12px 14px" }}>
                  <div style={{ fontSize:12, fontWeight:800, color:importSet?"#4ade80":"#FBBF24", marginBottom:6 }}>{importSet?"✅":"⚠️"} Target Set {importSet?"":"(recommended)"}</div>
                  <div style={{ fontSize:11, color:"#999", marginBottom:8, lineHeight:1.5 }}>Pick which set these images belong to. This restricts matching to that set so a hero that appears in multiple sets (e.g. Bojax) lands on the right card.</div>
                  <select value={importSet} onChange={e=>setImportSet(e.target.value)} style={{ width:"100%", background:"var(--bz-bg)", color:"var(--bz-ink)", border:"1px solid var(--bz-line-2)", borderRadius:8, padding:"9px 12px", fontSize:13, fontFamily:"inherit", cursor:"pointer" }}>
                    <option value="">— All sets (match across everything) —</option>
                    {setList.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <Btn onClick={runImageImport} disabled={importing} variant="green">
                  {importing ? "⏳ Uploading..." : `🖼 Upload ${imgFiles.length} Images${importSet?` → ${importSet}`:""}`}
                </Btn>
              </div>
            );
          })()}
        </>
      )}

      {/* Manual overrides for problem files */}
      {mode==="images" && (
        <div style={{ ...S.card }}>
          <SectionLabel t="🔧 Manual Overrides"/>
          <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:10 }}>When a file name doesn't match the Firestore cardNum (e.g. TB1 → TBJ-G), add an override here.</div>
          <ManualOverrideEditor overrides={manualOverrides} setOverrides={setManualOverrides}/>
        </div>
      )}

      {/* Copy images between treatments */}
      {mode==="images" && (
        <div style={{ ...S.card }}>
          <SectionLabel t="📋 Copy Images Between Treatments"/>
          <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12 }}>Same card art across treatments? Copy imageUrls from one to another without re-uploading.</div>
          <ImageCopier/>
        </div>
      )}

      {/* Progress bar */}
      {importing && progress && (
        <div style={{ ...S.card }}>
          <div style={{ fontSize:12, color:"var(--bz-ink-2)", marginBottom:8 }}>{progress.label}</div>
          <div style={{ background:"var(--bz-s2)", borderRadius:99, height:10, overflow:"hidden", marginBottom:6 }}>
            <div style={{ width:`${pct}%`, height:"100%", background:"linear-gradient(90deg,#E8317A,#7B9CFF)", borderRadius:99, transition:"width 0.3s ease" }}/>
          </div>
          <div style={{ fontSize:12, color:"var(--bz-ink-3)" }}>{progress.done.toLocaleString()} / {progress.total.toLocaleString()} ({pct}%)</div>
        </div>
      )}

      {/* Blast Auto repair. Cards in the Alpha Blast set numbered #BBFA… are autos, but they were
          imported under assorted treatments, so filtering the set surfaced Silver Blast and every
          other blast card together. #BL… cards are genuinely Silver Blast and must NOT be touched.
          PREVIEW FIRST — a bulk treatment rewrite is painful to undo. */}
      {mode==="data" && (
      <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:12, padding:16, marginTop:14 }}>
        <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)", marginBottom:4 }}>💥 Fix Alpha Blast autos</div>
        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12, lineHeight:1.6 }}>
          Sets every <strong>Alpha Blast</strong> card whose card number starts with <strong>BBFA</strong> to the
          treatment <strong>Blast Auto</strong>. Cards numbered <strong>BL…</strong> are left alone — those are
          Silver Blast. Preview shows exactly what would change before anything is written.
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <button onClick={async ()=>{
            setImporting(true); setErrors([]); setResults(null);
            try {
              setProgress({ done:0, total:1, label:"Scanning Alpha Blast\u2026" });
              const snap = await getDocs(collection(db,"boba_checklist"));
              const all = snap.docs.map(d=>({...d.data(), id:d.id}));
              const norm = s => String(s||"").toLowerCase().replace(/[\s\-_.]/g,"");
              // CONTAINS, not equals: the set is really "2025 Alpha Blast", so an exact match on
              // "alphablast" found nothing. Same for card numbers \u2014 a prefix test misses "BB-FA-12"
              // or a set code in front, so look for the token anywhere in the number.
              const inSet = all.filter(c => norm(c.setName).includes("alphablast"));
              const hits  = inSet.filter(c => norm(c.cardNum).includes("bbfa"));
              const already = hits.filter(c => c.treatment==="Blast Auto").length;
              const byTreat = {};
              hits.forEach(c => { const t=c.treatment||"(blank)"; byTreat[t]=(byTreat[t]||0)+1; });
              const bl = inSet.filter(c => norm(c.cardNum).includes("bl") && !norm(c.cardNum).includes("bbfa")).length;
              // If nothing matched, show what IS there rather than a bare zero.
              const setNames = {};
              all.filter(c=>norm(c.setName).includes("blast")).forEach(c=>{ const s=c.setName||"(blank)"; setNames[s]=(setNames[s]||0)+1; });
              const sampleNums = inSet.slice(0,12).map(c=>c.cardNum||"(blank)").join(", ");
              if (!hits.length) {
                setProgress(null); setImporting(false);
                alert(
                  "No BBFA cards found \u2014 nothing has been changed.\n\n" +
                  "Sets containing \u201cblast\u201d:\n" +
                  (Object.keys(setNames).length
                    ? Object.entries(setNames).sort((a,b)=>b[1]-a[1]).map(([s,n])=>"  \u2022 "+s+" \u00d7 "+n).join("\n")
                    : "  (none)") +
                  "\n\nMatched set rows: " + inSet.length +
                  (inSet.length ? "\nExample card numbers: " + sampleNums : "") +
                  "\n\nIf the card numbers look different from BBFA, tell me the real format."
                );
                return;
              }
              setProgress(null); setImporting(false);
              alert(
                "ALPHA BLAST PREVIEW — nothing has been changed.\n\n" +
                inSet.length + " cards in the Alpha Blast set\n" +
                hits.length + " numbered BBFA… (these would become \u201cBlast Auto\u201d)\n" +
                already + " of those are already Blast Auto\n" +
                (hits.length - already) + " would actually change\n\n" +
                "Their current treatments:\n" +
                Object.entries(byTreat).sort((a,b)=>b[1]-a[1]).map(([t,n])=>"  • "+t+" × "+n).join("\n") +
                "\n\n" + bl + " cards numbered BL… will NOT be touched."
              );
            } catch(e){ setImporting(false); setProgress(null); alert("Preview failed: "+(e?.message||e)); }
          }} disabled={importing}
            style={{ background:"transparent", border:"1px solid #7B9CFF", color:"#7B9CFF", borderRadius:9, padding:"9px 14px", fontSize:12, fontWeight:800, cursor:importing?"wait":"pointer", fontFamily:"inherit" }}>
            🔍 Preview
          </button>
          <button onClick={async ()=>{
            setImporting(true); setErrors([]); setResults(null);
            const errs=[];
            try {
              setProgress({ done:0, total:1, label:"Scanning Alpha Blast\u2026" });
              const snap = await getDocs(collection(db,"boba_checklist"));
              const all = snap.docs.map(d=>({...d.data(), id:d.id}));
              const norm = s => String(s||"").toLowerCase().replace(/[\s\-_.]/g,"");
              const todo = all.filter(c => norm(c.setName).includes("alphablast")
                                        && norm(c.cardNum).includes("bbfa")
                                        && c.treatment!=="Blast Auto");
              if (!todo.length) { setImporting(false); setProgress(null); alert("Nothing to change — every BBFA card in Alpha Blast is already Blast Auto."); return; }
              if (!window.confirm("Set treatment to \u201cBlast Auto\u201d on " + todo.length + " card" + (todo.length===1?"":"s") + " in Alpha Blast?\n\nBL… (Silver Blast) cards are not affected.\n\nThis rewrites the shared card database for everyone.")) { setImporting(false); setProgress(null); return; }
              for (let i=0;i<todo.length;i+=300) {
                const batch = writeBatch(db);
                todo.slice(i,i+300).forEach(c => batch.set(doc(db,"boba_checklist",c.id), { treatment:"Blast Auto" }, { merge:true }));
                await batch.commit();
                setProgress({ done:Math.min(i+300,todo.length), total:todo.length, label:"Updating treatments\u2026" });
              }
              // The app serves a prebuilt snapshot, so the edit is invisible until this is rebuilt.
              setProgress({ done:todo.length, total:todo.length, label:"Rebuilding snapshot\u2026" });
              const fresh = await getDocs(collection(db,"boba_checklist"));
              const all2 = fresh.docs.map(d=>({...d.data(), id:d.id}));
              try {
                const blob = new Blob([JSON.stringify(all2)], { type:"application/json" });
                await uploadBytes(ref(storage,"card_data/boba_checklist.json"), blob, { contentType:"application/json", cacheControl:"public,max-age=86400" });
              } catch(e){ errs.push("Storage upload failed: "+e.message); }
              try { await writeCardSnapshot(all2, 86400); } catch(e){ errs.push("Gzipped snapshot failed: "+e.message); }
              try { await setDoc(doc(db,"meta","cards_version"), { ts: Date.now(), count: all2.length }); }
              catch(e){ errs.push("Version bump failed: "+e.message); }
              try { localStorage.removeItem("boba_checklist_cache"); localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
              try { await idbClearCards(); } catch(e){ errs.push("IndexedDB clear failed: "+e.message); }
              setErrors(errs); setProgress(null); setImporting(false);
              alert("Updated " + todo.length + " card" + (todo.length===1?"":"s") + " to Blast Auto." + (errs.length ? "\n\nBut publishing had problems:\n• " + errs.join("\n• ") : "\n\nReloading…"));
              if (!errs.length) window.location.reload();
            } catch(e){ setImporting(false); setProgress(null); alert("Update failed: "+(e?.message||e)); }
          }} disabled={importing}
            style={{ background:"linear-gradient(135deg,#E8317A,#7B2FF7)", border:"none", color:"#fff", borderRadius:9, padding:"9px 14px", fontSize:12, fontWeight:800, cursor:importing?"wait":"pointer", fontFamily:"inherit" }}>
            Apply Blast Auto
          </button>
        </div>
      </div>
      )}

      {/* Rebuild snapshot — the app reads a prebuilt snapshot, NOT boba_checklist directly.
          If an import wrote cards but they don't show up, the snapshot is stale. Fix it here. */}
      {mode==="data" && (
      <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:12, padding:16, marginTop:14 }}>
        <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)", marginBottom:4 }}>🔄 Rebuild Card Snapshot</div>
        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12, lineHeight:1.6 }}>
          The app loads cards from a prebuilt snapshot for speed — not straight from the database. If you imported
          cards and they aren't showing up, the snapshot is stale. This rebuilds it from the live database and tells
          everyone's browser to refetch.
        </div>
        <button onClick={async ()=>{
          setImporting(true); setErrors([]); setResults(null);
          const errs=[];
          try {
            setProgress({ done:0, total:1, label:"Reading all cards from the database..." });
            const snap = await getDocs(collection(db,"boba_checklist"));
            const all = snap.docs.map(d=>({...d.data(), id:d.id}));
            setProgress({ done:0, total:1, label:`Writing snapshot (${all.length.toLocaleString()} cards)...` });
            try {
              const blob = new Blob([JSON.stringify(all)], { type:"application/json" });
              await uploadBytes(ref(storage,"card_data/boba_checklist.json"), blob, { contentType:"application/json", cacheControl:"public,max-age=86400" });
            } catch(e){ errs.push(`Storage upload failed: ${e.message}`); }
            try { await writeCardSnapshot(all, 86400); } catch(e){ errs.push(`Gzipped snapshot failed: ${e.message}`); }

            // VERIFY: read the snapshot back and confirm it has what we just wrote. Every previous
            // "successful" rebuild silently wrote nothing — the app kept serving an old snapshot.
            try {
              const readBack = await readCardSnapshot(true);
              if (!readBack) {
                errs.push("Snapshot verification FAILED: could not read the snapshot back. The app will keep serving stale cards.");
              } else if (readBack.length !== all.length) {
                errs.push(`Snapshot verification FAILED: wrote ${all.length.toLocaleString()} cards but read back ${readBack.length.toLocaleString()}. The upload is not landing — likely a Firebase Storage permission problem on card_data/.`);
              }
            } catch(e) {
              errs.push(`Snapshot verification FAILED: ${e.message}`);
            }
            try { await setDoc(doc(db,"meta","cards_version"), { ts: Date.now(), count: all.length }); }
            catch(e){ errs.push(`Version bump failed: ${e.message} — clients will keep serving the old list.`); }
            // CRITICAL: the 12MB card list lives in IndexedDB (too big for localStorage).
            // Clearing localStorage alone leaves the stale list in place.
            try { localStorage.removeItem("boba_checklist_cache"); localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
            try { await idbClearCards(); } catch(e){ errs.push(`IndexedDB clear failed: ${e.message}`); }
            setErrors(errs);
            if(!errs.length) {
              setResults({ written:0, skipped:0, files:0, snapshot: all.length });
              setProgress(null); setImporting(false);
              // Reload so the fresh snapshot is actually pulled in.
              setTimeout(()=>window.location.reload(), 900);
              return;
            }
          } catch(e){ setErrors([`Rebuild failed: ${e.message}`]); }
          setImporting(false); setProgress(null);
        }} disabled={importing}
          style={{ background:importing?"var(--bz-s2)":"linear-gradient(135deg,#7B9CFF,#7B2FF7)", color:importing?"#555":"#fff", border:"none", borderRadius:8, padding:"10px 22px", fontSize:13, fontWeight:800, cursor:importing?"not-allowed":"pointer", fontFamily:"inherit" }}>
          {importing?"Working…":"Rebuild snapshot from database"}
        </button>
      </div>
      )}

      {/* Prefix mapping mode */}
      {mode==="prefix" && <PrefixMapper/>}

      {/* Which packs contain which treatments, per set */}
      {mode==="foundin" && <FoundInEditor/>}

      {/* Dedupe mode */}
      {/* Collapse junk treatment variants (Silver Blast → Blast) BEFORE running dedupe —
          the merge is what turns them into detectable duplicates in the first place. */}
      {mode==="treatments" && <TreatmentMerge/>}
      {/* Play cards whose name is a number — the cost column landed in the name column. */}
      {mode==="junkplays" && <JunkPlayCleanup/>}
      {mode==="dedupe" && <CardDeduper/>}

      {/* Merge sets mode */}
      {mode==="merge" && <SetMerger/>}

      {/* Cleanup mode */}
      {mode==="cleanup" && <DataCleanup/>}

      {/* Manual image mode */}
      {mode==="manual" && (
        <div style={{ ...S.card }}>
          <SectionLabel t="🎯 Manual Card Image"/>
          <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:14 }}>Search for a specific card, drop an image, upload. Use this for one-offs that didn't match during bulk import.</div>
          <ManualCardImage/>
        </div>
      )}

      {/* Results */}
      {results && (
        <div style={{ ...S.card, borderLeft:`3px solid ${errors.length?"#FBBF24":"#4ade80"}` }}>
          <SectionLabel t={errors.length?"⚠️ Done with warnings":"✅ Done"}/>
          <div style={{ display:"flex", gap:24, marginBottom:10 }}>
            <div><div style={{ fontSize:22, fontWeight:900, color:"#4ade80" }}>{results.written.toLocaleString()}</div><div style={{ fontSize:10, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Written</div></div>
            {results.skipped>0&&<div><div style={{ fontSize:22, fontWeight:900, color:"#FBBF24" }}>{results.skipped.toLocaleString()}</div><div style={{ fontSize:10, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>Skipped</div></div>}
            {results.snapshot>0&&<div><div style={{ fontSize:22, fontWeight:900, color:"#7B9CFF" }}>{results.snapshot.toLocaleString()}</div><div style={{ fontSize:10, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>In Snapshot</div></div>}
          </div>
          {results.snapshot>0 && <div style={{ fontSize:11, color:"#4ade80", marginBottom:6 }}>✓ Card snapshot rebuilt — the new cards will show up for everyone (hard-refresh if you still see the old list).</div>}
          {errors.slice(0,10).map((e,i)=><div key={i} style={{ fontSize:11, color:"#EF4444", marginTop:3 }}>⚠ {e}</div>)}
          {errors.length>10&&<div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>...and {errors.length-10} more</div>}
        </div>
      )}
    </div>
  );
}

function BobaChecklist({ defaultView="cards", userRole, user, onScanUpdate, onChecklistUpdated }) {
  const ownedDocId = user?.uid || "owned";
  const wantsDocId = user?.uid ? `wants_${user.uid}` : "wants";
  const [cards,        setCards]        = useState([]);
  const [imports,      setImports]      = useState([]); // list of {id, setName, filename, importedAt, cardIds}
  const [owned,        setOwned]        = useState({});
  const [loading,      setLoading]      = useState(true);
  const [setsCollapsed, setSetsCollapsed] = useState(false);
  const [importing,    setImporting]    = useState(false);
  const [importProgress, setImportProgress] = useState("");
  const [pendingFile,  setPendingFile]  = useState(null); // file waiting for set name
  const [setNameInput, setSetNameInput] = useState("");
  const [newSetMode,   setNewSetMode]   = useState(false);
  const [search,       setSearch]       = useState("");
  const debouncedSearch = useDebounce(search, 250);
  const [filterTreat,  setFilterTreat]  = useState("");
  const [filterWeapon, setFilterWeapon] = useState("");
  const [filterNote,   setFilterNote]   = useState("");
  const [filterSet,    setFilterSet]    = useState("");
  const [filterPower,  setFilterPower]  = useState(new Set());
  const [filterOwned,    setFilterOwned]    = useState("all");
  const [renamingId,   setRenamingId]   = useState(null);
  const [renameVal,    setRenameVal]    = useState("");
  const [scanPdf,      setScanPdf]      = useState(null);
  const [scanProgress, setScanProgress] = useState(null);
  const _setScanProgress = (v) => { setScanProgress(v); if(onScanUpdate) onScanUpdate(v ? { type:"pdf", ...v } : null); };
  const [imgScanProgress, setImgScanProgress] = useState(null);
  const [imgImportModal, setImgImportModal] = useState(null); // {files} waiting for set selection
  const [imgImportSet, setImgImportSet] = useState("");
  const _setImgScanProgress = (v) => { setImgScanProgress(v); if(onScanUpdate) onScanUpdate(v ? { type:"images", ...v } : null); };
  const [scanConfig,   setScanConfig]   = useState(null); // { file, setName, treatment, weapon }
  const [pendingScan,  setPendingScan]  = useState(null); // file waiting for config
  const [scanPaused,   setScanPaused]   = useState(false);
  const scanPausedRef = useRef(false);
  const [flippedCard,  setFlippedCard]  = useState(null);
  const [viewMode,       setViewMode]       = useState(defaultView);
  useEffect(()=>{setViewMode(defaultView);},[defaultView]);
  // Set list / broadcaster notes states
  const [activeSet,          setActiveSet]          = useState("");
  const [expandedHeroNotes,  setExpandedHeroNotes]  = useState(null);
  const [customNotes,        setCustomNotes]        = useState({});
  const [editingNote,        setEditingNote]        = useState(null);
  const [noteText,           setNoteText]           = useState("");
  const [expandedHero,   setExpandedHero]   = useState(null);
  const [expandedTreat,  setExpandedTreat]  = useState(null);
  const [rainbowFilter,    setRainbowFilter]    = useState("all");
  const [rainbowMode,      setRainbowMode]      = useState("full"); // "full" | "custom"
  const [customTrackers,   setCustomTrackers]   = useState(() => {
    try { const c = localStorage.getItem("customTrackers_v1"); return c ? JSON.parse(c) : []; } catch { return []; }
  });
  const [activeTrackerId,  setActiveTrackerId]  = useState(null);
  const [showTrackerBuilder, setShowTrackerBuilder] = useState(false);
  const [builderName,      setBuilderName]      = useState("");
  const [builderTreatments,setBuilderTreatments]= useState([]);
  const [rainbowSetFilter, setRainbowSetFilter] = useState("");
  // Rainbow stats are expensive — grouping ~30k cards by hero and tallying per-set
  // ownership. This used to run inline on EVERY render (including each keystroke in
  // the rainbow search box), which is what made the tab lag. Memoized so it only
  // recomputes when the cards, ownership, or set filter actually change; search
  // filtering happens cheaply downstream against this result.
  const rainbowStats = useMemo(() => {
    if (!cards.length) return { heroStats: [], availableSets: [], completedRainbows: 0, partialRainbows: 0 };
    const rainbowCards = (rainbowSetFilter ? cards.filter(c => c.setName === rainbowSetFilter) : cards)
      .filter(c => { const t=(c.treatment||"").toLowerCase(); return t!=="plays"&&t!=="bonus plays"&&t!=="home team discount"; });
    const availableSets = [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort();
    const heroCards = {};
    rainbowCards.forEach(c => {
      if(!c.hero) return;
      (heroCards[c.hero] = heroCards[c.hero] || []).push(c);
    });
    const allHeroes = Object.keys(heroCards).sort();
    const heroStats = allHeroes.map(hero => {
      const hcards = heroCards[hero];
      const total = hcards.length;
      let ownedCount = 0;
      const bySets = {};
      hcards.forEach(c => {
        const own = !!owned[c.id];
        if (own) ownedCount++;
        const s = c.setName || "Unknown";
        if(!bySets[s]) bySets[s] = { total:0, owned:0 };
        bySets[s].total++;
        if(own) bySets[s].owned++;
      });
      return { hero, total, ownedCount, complete: total>0 && ownedCount===total, bySets };
    });
    return {
      heroStats, availableSets,
      completedRainbows: heroStats.filter(h => h.complete).length,
      partialRainbows:   heroStats.filter(h => h.ownedCount > 0 && !h.complete).length,
    };
  }, [cards, owned, rainbowSetFilter]);
  const [treatOwnedFilter, setTreatOwnedFilter] = useState("all"); // all | owned | missing
  const [sortBy,           setSortBy]           = useState("cardNum");
  const [weaponSetFilter,  setWeaponSetFilter]  = useState("");
  const [page,           setPage]           = useState(1);
  const sentinelRef = useRef(null);
  // Deck builder state
  const [deckCards,      setDeckCards]      = useState([]); // array of card ids in current deck
  const [deckName,       setDeckName]       = useState("My Deck");
  const [savedDecks,     setSavedDecks]     = useState([]);
  const [deckSearch,     setDeckSearch]     = useState("");
  const [deckFilterWeap, setDeckFilterWeap] = useState("");
  const [deckFilterHero, setDeckFilterHero] = useState("");
  const [deckSaving,     setDeckSaving]     = useState(false);
  const [deckSaved,      setDeckSaved]      = useState(false);
  const [pbSaved,        setPbSaved]        = useState(false);
  const [deckLoadId,     setDeckLoadId]     = useState(null);
  const [deckOwnedOnly,  setDeckOwnedOnly]  = useState(false);
  const [deckSlotSort,   setDeckSlotSort]   = useState("added");
  const [deckType,       setDeckType]       = useState("none");

  const [deckFilterPower, setDeckFilterPower] = useState("");
  const [deckFilterPowers, setDeckFilterPowers] = useState(new Set()); // multi-select powers
  const [deckFilterSet,    setDeckFilterSet]    = useState("");
  const [deckFilterTreat,  setDeckFilterTreat]  = useState("");
  const [dbsImporting,   setDbsImporting]   = useState(false);
  const [dbsStatus,      setDbsStatus]      = useState(null); // {msg, ok}
  const DBS_CAP = 1000; // none | spec | apex
  // Playbook state
  const [pbCards,        setPbCards]        = useState([]); // {id, type: "play"|"bonus"}
  const [pbName,         setPbName]         = useState("My Playbook");
  const [savedPlaybooks, setSavedPlaybooks] = useState([]);
  const [pbLoadId,       setPbLoadId]       = useState(null);
  const [pbSearch,       setPbSearch]       = useState("");
  const [pbOwnedOnly,    setPbOwnedOnly]    = useState(false);
  const [pbSaving,       setPbSaving]       = useState(false);
  const [pbSort,         setPbSort]         = useState("name");
  const [pbFilterSet,    setPbFilterSet]    = useState("");
  const [pbTreatFilter,  setPbTreatFilter]  = useState(""); // name | dbs_asc | dbs_desc
  const PLAY_LIMIT = 30;
  const DECK_SIZE = (DECK_FORMATS[deckType]||DECK_FORMATS.none).size || 60;   // capacity follows the format — Apex Madness is 70, most others 60
  const PAGE_SIZE = 100;
  const isAdmin = ["Admin"].includes(userRole?.role);
  // Super Foil Tracker
  const [superClaims,     setSuperClaims]     = useState([]);
  const [claimModal,      setClaimModal]      = useState(null);
  const [claimPhoto,      setClaimPhoto]      = useState(null);
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimSent,       setClaimSent]       = useState(false);
  const [recordModal,     setRecordModal]     = useState(null); // admin: record a hit for someone
  const [recordName,      setRecordName]      = useState("");
  const [oneOfOneClaims,  setOneOfOneClaims]  = useState([]);
  const [oneModal,        setOneModal]        = useState(null);
  const [onePhoto,        setOnePhoto]        = useState(null);
  const [oneStory,        setOneStory]        = useState("");
  const [oneDate,         setOneDate]         = useState("");
  const [oneName,         setOneName]         = useState("");
  const [oneSubmitting,   setOneSubmitting]   = useState(false);
  const [oneSent,         setOneSent]         = useState(false);

  useEffect(() => {
    // Cards are static after import -- use localStorage cache for instant load
    const CACHE_KEY = "boba_checklist_cache_v3";
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { cards: cachedCards, ts } = JSON.parse(cached);
        if (Date.now() - ts < 10 * 60 * 1000 && cachedCards.length > 0) {
          setCards(cachedCards);
          setLoading(false);
        }
      }
    } catch(e) {}
    // Always fetch fresh in background
    getDocs(collection(db, "boba_checklist")).then(snap => {
      const sorted = snap.docs.map(d=>d.data()).sort((a,b)=>{
        const n1=parseFloat(a.cardNum), n2=parseFloat(b.cardNum);
        if(!isNaN(n1)&&!isNaN(n2)) return n1-n2;
        return String(a.cardNum).localeCompare(String(b.cardNum));
      });
      setCards(sorted);
      setLoading(false);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ cards:sorted, ts:Date.now() })); } catch(e) {}
    });
    // Owned + imports stay realtime
    const u2 = onSnapshot(doc(db,"boba_owned",ownedDocId), snap => {
      if (snap.exists()) setOwned(snap.data()); else setOwned({});
    });
    const uWants = onSnapshot(doc(db,"boba_owned",wantsDocId), snap => { if(snap.exists()) setWantList(snap.data()); else setWantList({}); });
    const uTrackers = onSnapshot(doc(db,"boba_trackers",ownedDocId), snap => {
      const list = (snap.exists() && Array.isArray(snap.data().trackers)) ? snap.data().trackers : [];
      setCustomTrackers(list);
      try { localStorage.setItem("customTrackers_v1", JSON.stringify(list)); } catch {}
    });
    const u3 = onSnapshot(collection(db,"boba_imports"), snap => {
      setImports(snap.docs.map(d=>d.data()).sort((a,b)=>b.importedAt?.localeCompare(a.importedAt)));
    });
    const u4 = onSnapshot(collection(db,"boba_decks"), snap => {
      const uid = user?.uid || "shared";
      setSavedDecks(snap.docs.map(d=>({...d.data(), id:d.id})).filter(d=>d.userId===uid).sort((a,b)=>b.savedAt?.localeCompare(a.savedAt)));
    });
    const u5 = onSnapshot(collection(db,"boba_playbooks"), snap => {
      const uid = user?.uid || "shared";
      setSavedPlaybooks(snap.docs.map(d=>({...d.data(), id:d.id})).filter(d=>d.userId===uid).sort((a,b)=>b.savedAt?.localeCompare(a.savedAt)));
    });
    const uSuper = onSnapshot(collection(db,"super_claims"), snap => {
      setSuperClaims(snap.docs.map(d=>({...d.data(), id:d.id})));
    });
    const uOne = onSnapshot(collection(db,"oneof1_claims"), snap => {
      setOneOfOneClaims(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>b.createdAt?.localeCompare(a.createdAt)));
    });
    return ()=>{ u2(); u3(); u4(); u5(); uWants(); uSuper(); uOne(); uTrackers(); };
  }, []);

  // Infinite scroll -- load more when user scrolls near bottom
  useEffect(() => {
    if (viewMode !== "cards") return;
    function onScroll() {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollHeight - scrollTop - clientHeight < 400) {
        setPage(p => p + 1);
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [viewMode]);

  async function saveDeck() {
    if (!deckName.trim() || deckCards.length === 0) { alert("Name your deck and add at least one card before saving."); return; }
    if (!user) { alert("Please sign in to save your deck."); return; }
    setDeckSaving(true);
    const id = deckLoadId || `deck_${Date.now()}`;
    try {
      await setDoc(doc(db,"boba_decks",id), {
        id, userId: user.uid, name: deckName.trim(),
        cardIds: deckCards, cardCount: deckCards.length,
        deckType, savedAt: new Date().toISOString(),
      }, { merge:true });
      setDeckLoadId(id);
      try { showToast && showToast("Deck saved!"); } catch(e){}
    } catch(e) {
      console.error("save deck failed:", e);
      alert("Couldn't save your deck: " + (e?.message || e) + "\n\nIf this mentions permissions, the Firestore rules need to be published.");
    } finally {
      setDeckSaving(false);
    }
  }

  async function deleteDeck(id) {
    if (!window.confirm("Delete this deck?")) return;
    await deleteDoc(doc(db,"boba_decks",id));
    if (deckLoadId === id) { setDeckLoadId(null); setDeckName("My Deck"); setDeckCards([]); }
  }

  async function toggleRegistered(deck) {
    const next = !deck.registered;
    setSavedDecks(list => list.map(d => d.id === deck.id ? { ...d, registered: next } : d));
    try {
      await updateDoc(doc(db,"boba_decks",deck.id), {
        registered: next,
        registeredAt: next ? new Date().toISOString() : null,
      });
    } catch (e) {
      setSavedDecks(list => list.map(d => d.id === deck.id ? { ...d, registered: !next } : d));
      alert("Couldn't update registration: " + (e?.message || e));
    }
  }

  function loadDeck(deck) {
    setDeckLoadId(deck.id);
    setDeckName(deck.name);
    setDeckCards(deck.cardIds||[]);
    setDeckType(deck.deckType||"none");
    setDeckSearch(""); setDeckFilterWeap(""); setDeckFilterHero(""); setDeckFilterPower("");
  }

  function newDeck() {
    setDeckLoadId(null);
    setDeckName("My Deck");
    setDeckCards([]);
    setDeckSearch(""); setDeckFilterWeap(""); setDeckFilterHero(""); setDeckFilterPower("");
  }

  async function savePlaybook() {
    if (!pbName.trim() || pbCards.length === 0) { alert("Name your playbook and add at least one card before saving."); return; }
    if (!user) { alert("Please sign in to save your playbook."); return; }
    setPbSaving(true);
    const id = pbLoadId || `pb_${Date.now()}`;
    const plays = pbCards.filter(e=>e.type==="play").length;
    const bonus = pbCards.filter(e=>e.type==="bonus").length;
    try {
      await setDoc(doc(db,"boba_playbooks",id), {
        id, userId: user.uid, name: pbName.trim(),
        entries: pbCards, playCount: plays, bonusCount: bonus,
        savedAt: new Date().toISOString(),
      }, { merge:true });
      setPbLoadId(id);
      try { showToast && showToast("Playbook saved!"); } catch(e){}
    } catch(e) {
      console.error("save playbook failed:", e);
      alert("Couldn't save your playbook: " + (e?.message || e) + "\n\nIf this mentions permissions, the Firestore rules need to be published.");
    } finally {
      setPbSaving(false);
    }
  }

  async function deletePlaybook(id) {
    if (!window.confirm("Delete this playbook?")) return;
    await deleteDoc(doc(db,"boba_playbooks",id));
    if (pbLoadId === id) { setPbLoadId(null); setPbName("My Playbook"); setPbCards([]); }
  }

  function loadPlaybook(pb) {
    setPbLoadId(pb.id);
    setPbName(pb.name);
    setPbCards(pb.entries||[]);
    setPbSearch(""); setPbTreatFilter(""); setPbFilterSet("");
  }

  function newPlaybook() {
    setPbLoadId(null);
    setPbName("My Playbook");
    setPbCards([]);
    setPbSearch(""); setPbTreatFilter(""); setPbFilterSet("");
  }

  async function scanPdfForCards(file, setName, treatment, weapon) {
    setScanPdf(file.name);
    _setScanProgress({ current:0, total:0, status:"Loading PDF..." });
    scanPausedRef.current = false;
    setScanPaused(false);

    // Load pdf.js
    if (!window.pdfjsLib) {
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
        s.onload = resolve; s.onerror = reject;
        document.head.appendChild(s);
      });
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
    }

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const total = pdf.numPages;
    _setScanProgress({ current:0, total, status:"Starting scan..." });

    const BATCH_SIZE = 5; // process 5 pages at a time
    let completed = 0;

    function normalize(s) { return (s||"").toLowerCase().replace(/[^a-z0-9\s]/g,"").trim(); }
    function fuzzyMatch(a, b) {
      const na = normalize(a), nb = normalize(b);
      if (na === nb) return true;
      if (na.includes(nb) || nb.includes(na)) return true;
      const wa = na.split(/\s+/), wb = nb.split(/\s+/);
      const shared = wa.filter(w => wb.includes(w)).length;
      return shared > 0 && shared / Math.max(wa.length, wb.length) >= 0.5;
    }

    async function processPage(pageNum) {
      while (scanPausedRef.current) {
        await new Promise(r => setTimeout(r, 500));
      }

      // Render small version for Claude Vision
      const page = await pdf.getPage(pageNum);
      const rawViewport = page.getViewport({ scale: 1 });
      const scale = Math.min(0.8, 800 / rawViewport.width);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
      const base64 = canvas.toDataURL("image/jpeg", 0.5).split(",")[1];

      // Send to Claude Vision
      let identified = null;
      try {
        const resp = await fetch("/api/scan-card", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ imageBase64: base64, treatment, weapon }),
        });
        const data = await resp.json();
        identified = data.identified;
      } catch(e) { console.error(`Page ${pageNum} error:`, e); }

      if (!identified?.hero && !identified?.cardNum) return;

      const heroName = identified.hero ? identified.hero.toLowerCase().replace(/[^a-z0-9\s]/g, "").trim() : null;

      // 1. Card number + set name (most reliable)
      // Normalize card nums -- strip spaces/dashes for comparison: "ALT-4" == "ALT4" == "ALT 4"
      function normalizeCardNum(n) { return String(n||"").toLowerCase().replace(/[\s\-]/g,""); }
      const identifiedNum = normalizeCardNum(identified.cardNum);

      let match = identifiedNum
        ? cards.find(c =>
            normalizeCardNum(c.cardNum) === identifiedNum &&
            (!setName || c.setName === setName)
          )
        : null;

      // 2. Card number only (cross-set fallback)
      if (!match && identifiedNum) {
        match = cards.find(c => normalizeCardNum(c.cardNum) === identifiedNum);
      }

      // 3. Hero + treatment + weapon (fuzzy)
      if (!match && heroName) {
        match = cards.find(c =>
          fuzzyMatch(c.hero, heroName) &&
          (!treatment || c.treatment?.toLowerCase() === treatment.toLowerCase()) &&
          (!weapon   || c.weapon?.toLowerCase()   === weapon.toLowerCase()) &&
          (!setName  || c.setName === setName)
        ) || cards.find(c =>
          fuzzyMatch(c.hero, heroName) &&
          (!treatment || c.treatment?.toLowerCase() === treatment.toLowerCase()) &&
          (!weapon   || c.weapon?.toLowerCase()   === weapon.toLowerCase())
        );
      }

      // 4. Hero + weapon only
      if (!match && heroName) {
        match = cards.find(c =>
          fuzzyMatch(c.hero, heroName) &&
          (!weapon || c.weapon?.toLowerCase() === weapon.toLowerCase())
        );
      }

      if (!match) { console.log(`Page ${pageNum}: no match -- cardNum="${identified.cardNum}" hero="${identified.hero}"`); return; }
      console.log(`Page ${pageNum}: matched ${match.hero} #${match.cardNum}`);

      // Render high quality version for storage -- 3.5x scale, PNG lossless
      const hiCanvas = document.createElement("canvas");
      const hiViewport = page.getViewport({ scale: 3.5 });
      hiCanvas.width = hiViewport.width;
      hiCanvas.height = hiViewport.height;
      const hiCtx = hiCanvas.getContext("2d");
      hiCtx.imageSmoothingEnabled = true;
      hiCtx.imageSmoothingQuality = "high";
      await page.render({ canvasContext: hiCtx, viewport: hiViewport }).promise;
      const imgBlob = await new Promise(res => hiCanvas.toBlob(res, "image/png"));
      const storageRef = ref(storage, `boba_cards/${match.id}.png`);
      await uploadBytes(storageRef, imgBlob);
      await uploadBytes(storageRef, imgBlob);
      const imageUrl = await getDownloadURL(storageRef);
      await setDoc(doc(db,"boba_checklist",match.id), { imageUrl }, { merge:true });
    }

    for (let i = 1; i <= total; i += BATCH_SIZE) {
      while (scanPausedRef.current) {
        await new Promise(r => setTimeout(r, 500));
      }
      const batch = [];
      for (let j = i; j < Math.min(i + BATCH_SIZE, total + 1); j++) {
        batch.push(processPage(j));
      }
      await Promise.all(batch);
      completed = Math.min(i + BATCH_SIZE - 1, total);
      _setScanProgress({ current:completed, total, status:`Scanning pages ${i}-${Math.min(i+BATCH_SIZE-1,total)} of ${total}...` });
    }

    __setScanProgress({ current:total, total, status:"\u2705 Scan complete!" });
    setTimeout(() => { setScanPdf(null); _setScanProgress(null); }, 3000);
  }

  async function scanImagesForCards(files, setName) {
    const fileList = Array.from(files);
    if (fileList.length === 0) return;
    try {
    _setImgScanProgress({ current:0, total:fileList.length, status:"Starting image scan..." });

    let matched = 0, skipped = 0, alreadyDone = 0;
    const skippedFiles = []; // collect names of unmatched files for a report
    // RESUME SUPPORT: cards that already have an image are skipped so a re-run
    // continues where it left off (and doesn't re-pay for Vision calls).
    const alreadyImaged = new Set(
      cards.filter(c => c.imageUrl && String(c.imageUrl).startsWith("http")).map(c => c.id)
    );

    // -- Matching helpers --------------------------------------
    function normNum(n) { return String(n||"").replace(/[\s\-]/g,"").toLowerCase(); }

    // Strict hero match: first word must agree, full strings must be close
    function heroMatch(cardHero, identified) {
      if (!cardHero || !identified) return false;
      const a = cardHero.toLowerCase().trim();
      const b = identified.toLowerCase().trim();
      if (a === b) return true;
      // First word must match exactly
      if (a.split(" ")[0] !== b.split(" ")[0]) return false;
      // And overall similarity must be high (one is substring of other)
      return a.includes(b) || b.includes(a);
    }

    // Extract set-order number from filename e.g. "setorder61" - "61"
    function extractSetOrder(filename) {
      const m = filename.match(/setorder(\d+)/i) || filename.match(/[-_](\d+)\./);
      return m ? m[1] : null;
    }

    // Try filename-based matching first (most reliable, no Vision needed)
    function matchByFilename(filename, setNm) {
      const order = extractSetOrder(filename);
      if (!order) return null;
      const idx = parseInt(order) - 1; // setorder is 1-based
      const setCards = setNm
        ? cards.filter(c => c.setName === setNm)
        : cards;
      // Sort by card number numerically to get set order
      const sorted = [...setCards].sort((a, b) => {
        const na = parseInt(String(a.cardNum||"").replace(/[^0-9]/g,""))||0;
        const nb = parseInt(String(b.cardNum||"").replace(/[^0-9]/g,""))||0;
        return na - nb;
      });
      return sorted[idx] || null;
    }

    const PARALLEL = 8; // process 8 images at a time — much faster than one-by-one
    let processed = 0;
    async function processFile(file, i) {

      try {
        // -- Step 1: Try filename matching first (free, instant, reliable) --
        let match = matchByFilename(file.name, setName);

        // -- Step 2: Vision API if filename didn't work --
        if (!match) {
          const base64 = await new Promise((res, rej) => {
            const reader = new FileReader();
            reader.onload = (ev) => {
              const img = new Image();
              img.onload = () => {
                const MAX = 1200;
                const scale = Math.min(1, MAX / Math.max(img.width, img.height));
                const canvas = document.createElement("canvas");
                canvas.width  = Math.round(img.width  * scale);
                canvas.height = Math.round(img.height * scale);
                canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                res(canvas.toDataURL("image/jpeg", 0.9).split(",")[1]);
              };
              img.onerror = rej;
              img.src = ev.target.result;
            };
            reader.onerror = rej;
            reader.readAsDataURL(file);
          });

          const resp = await fetch("/api/scan-card", {
            method:"POST",
            headers:{ "Content-Type":"application/json" },
            body: JSON.stringify({ imageBase64: base64, mediaType:"image/jpeg", setName }),
          });
          const data = await resp.json();

          if (data.cardNum || data.hero) {
            const identifiedNum = normNum(data.cardNum);
            const heroName  = (data.hero||"").trim().toLowerCase();
            const weapon    = (data.weapon||"").trim().toLowerCase();
            const treatment = (data.treatment||"").trim().toLowerCase();
            const setNm     = setName||"";

            // 1. Card# + set + hero
            if (!match && identifiedNum && heroName) {
              match = cards.find(c =>
                normNum(c.cardNum) === identifiedNum &&
                heroMatch(c.hero, heroName) &&
                (!setNm || c.setName === setNm)
              );
            }
            // 2. Card# + hero (cross-set)
            if (!match && identifiedNum && heroName) {
              match = cards.find(c =>
                normNum(c.cardNum) === identifiedNum &&
                heroMatch(c.hero, heroName)
              );
            }
            // 3. Card# alone (only if set matches)
            if (!match && identifiedNum && setNm) {
              match = cards.find(c =>
                normNum(c.cardNum) === identifiedNum &&
                c.setName === setNm
              );
            }
            // 4. Hero + treatment + weapon (all three, strict)
            if (!match && heroName && treatment && weapon) {
              const cands = cards.filter(c =>
                heroMatch(c.hero, heroName) &&
                c.treatment?.toLowerCase() === treatment &&
                c.weapon?.toLowerCase() === weapon &&
                (!setNm || c.setName === setNm)
              );
              if (cands.length === 1) match = cands[0];
              // If multiple, pick by power
              else if (cands.length > 1 && data.power) {
                match = cands.find(c => String(c.power||"") === String(data.power||"")) || cands[0];
              }
            }
            // 5. Hero + weapon (strict hero matching)
            if (!match && heroName && weapon) {
              const cands = cards.filter(c =>
                heroMatch(c.hero, heroName) &&
                c.weapon?.toLowerCase() === weapon &&
                (!setNm || c.setName === setNm)
              );
              if (cands.length === 1) match = cands[0];
              else if (cands.length > 1 && data.power) {
                match = cands.find(c => String(c.power||"") === String(data.power||""));
              }
            }
          }
        }

        if (!match) { skipped++; skippedFiles.push(file.name); console.log(`No match: ${file.name}`); processed++; _setImgScanProgress({ current:processed, total:fileList.length, status:`No match for ${file.name} (${processed}/${fileList.length})` }); return; }

        // RESUME: if this card already has an image, skip re-uploading it.
        if (alreadyImaged.has(match.id)) { alreadyDone++; processed++; _setImgScanProgress({ current:processed, total:fileList.length, status:`⏭ Already done: ${match.hero} #${match.cardNum} (${processed}/${fileList.length})` }); return; }
        // Upload original file (not re-compressed)
        const imgBlob = new Blob([await file.arrayBuffer()], { type: file.type || "image/webp" });
        const ext = file.name.split(".").pop() || "webp";
        const storageRef = ref(storage, `boba_cards/${match.id}.${ext}`);
        await uploadBytes(storageRef, imgBlob);
        const imageUrl = await getDownloadURL(storageRef);
        await setDoc(doc(db,"boba_checklist",match.id), { imageUrl }, { merge:true });
        matched++; processed++;
        _setImgScanProgress({ current:processed, total:fileList.length, status:`✅ ${match.hero} #${match.cardNum} — ${matched} matched, ${skipped} skipped (${processed}/${fileList.length})` });
        console.log(`\u2705 ${file.name} \u2192 ${match.hero} #${match.cardNum} (${match.setName})`);

      } catch(e) {
        console.error(`Error scanning ${file.name}:`, e);
        skipped++; processed++; skippedFiles.push(file.name + " (error: " + e.message + ")");
        _setImgScanProgress({ current:processed, total:fileList.length, status:`Error on ${file.name} (${processed}/${fileList.length})` });
      }
    }

    // Run in parallel batches of PARALLEL
    for (let i = 0; i < fileList.length; i += PARALLEL) {
      const batch = fileList.slice(i, i + PARALLEL);
      await Promise.all(batch.map((f, j) => processFile(f, i + j)));
    }

    // Build a downloadable report of unmatched files so you know what to retry
    if (skippedFiles.length) {
      try {
        const blob = new Blob([`Unmatched / errored files (${skippedFiles.length}):\n\n` + skippedFiles.join("\n")], { type:"text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `unmatched-images-${new Date().toISOString().slice(0,10)}.txt`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(()=>URL.revokeObjectURL(url), 1000);
      } catch(e) { console.error("Could not generate skip report:", e); }
    }

    _setImgScanProgress({ current:fileList.length, total:fileList.length, status:`✅ Done! ${matched} matched, ${skipped} unmatched${skippedFiles.length?` — skip-list downloaded`:``}` });
    setTimeout(() => _setImgScanProgress(null), 8000);
    } catch(err) { _setImgScanProgress({ current:0, total:0, status:"Error: " + err.message }); console.error("Import error:", err); }
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch(e) {}
  }  // - closes scanImagesForCards

  const [photoScan,    setPhotoScan]    = useState(null); // {status, card}
  const [scanModal,    setScanModal]    = useState(false); // full-screen scan modal open
  const [scanSession,  setScanSession]  = useState([]);    // [{card, qty, addedAt}]
  const [scanQty,      setScanQty]      = useState(1);     // qty selector for pending match
  const scanInputRef = useRef(null);
  const treatmentVisuals = useRef({}); // { treatmentName: ["hint1","hint2",...] }

  // Load treatment visual fingerprints once
  useEffect(() => {
    getDocs(collection(db,"treatment_visuals")).then(snap => {
      const map = {};
      snap.forEach(d => { map[d.id] = d.data().hints || []; });
      treatmentVisuals.current = map;
    }).catch(()=>{});
  }, []);



  async function scanCardPhoto(file) {
    setPhotoScan({ status:"scanning", card:null });
    try {
      // Resize to max 1200px for fast API response
      const base64 = await new Promise((res, rej) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const MAX = 1200;
          const scale = Math.min(1, MAX / Math.max(img.width, img.height));
          const canvas = document.createElement("canvas");
          canvas.width  = Math.round(img.width  * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          res(canvas.toDataURL("image/jpeg", 0.92).split(",")[1]);
        };
        img.onerror = rej;
        img.src = url;
      });

      const resp = await fetch("/api/scan-card", {
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({ imageBase64: base64, mediaType: "image/jpeg" }),
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(()=>({ error: `HTTP ${resp.status}` }));
        const msg = errData.details ? `${errData.error}: ${errData.details.slice(0,120)}` : errData.error || `HTTP ${resp.status}`;
        console.error("scan-card API error:", resp.status, errData);
        setPhotoScan({ status:"error", message: msg });
        return;
      }

      const data = await resp.json();
      if (data.error) {
        console.error("scan-card returned error:", data.error);
        setPhotoScan({ status:"error", message: data.error });
        return;
      }

      if (!data.cardNum && !data.hero) {
        setPhotoScan({ status:"nomatch", card:null });
        setTimeout(() => setPhotoScan(null), 4000);
        return;
      }

      // Match against checklist -- prioritize card number, then strict hero+weapon
      const identifiedNum = (data.cardNum||"").replace(/[\s\-]/g,"").toLowerCase();
      const heroName  = (data.hero||"").trim().toLowerCase();
      const weapon    = (data.weapon||"").trim().toLowerCase();
      const treatment = (data.treatment||"").trim().toLowerCase();

      function normNum(n) { return String(n||"").replace(/[\s\-]/g,"").toLowerCase(); }
      function heroMatch(cardHero, id) {
        if (!cardHero || !id) return false;
        const a = cardHero.toLowerCase(), b = id.toLowerCase();
        if (a === b) return true;
        if (a.includes(b) || b.includes(a)) return true;
        const aF = a.split(" ")[0], bF = b.split(" ")[0];
        return aF === bF && aF.length > 2;
      }

      // Visual tiebreaker -- score how well a treatment's fingerprint matches detected hints
      function visualScore(treatment, detectedHints) {
        if (!detectedHints || !treatment) return 0;
        const fingerprints = treatmentVisuals.current[treatment] || [];
        if (fingerprints.length === 0) return 0;
        const detected = detectedHints.toLowerCase().split(/[\s,]+/).filter(w => w.length > 3);
        let score = 0;
        fingerprints.forEach(hint => {
          const words = hint.toLowerCase().split(/[\s,]+/).filter(w => w.length > 3);
          words.forEach(w => { if (detected.includes(w)) score++; });
        });
        return score / Math.max(detected.length, 1);
      }

      const visualHints = data.visualHints || null;

      let match = null;
      // When the card number maps to MULTIPLE cards (shared set number across
      // weapons/treatments), don't trust a single weapon guess — confirm with
      // treatment too, and show the picker if it can't be pinned cleanly.
      if (identifiedNum) {
        const numMatches = cards.filter(c => normNum(c.cardNum)===identifiedNum && (!heroName || heroMatch(c.hero, heroName)));
        if (numMatches.length > 1) {
          const wpnCanon = weapon ? canonWeapon(weapon) : "";
          const treatLc = treatment || "";
          // score each by how many strong signals agree (weapon + treatment)
          const scored = numMatches.map(c => {
            let s = 0;
            if (wpnCanon && canonWeapon(c.weapon)===wpnCanon) s++;
            if (treatLc && c.treatment?.toLowerCase()===treatLc) s += 2; // treatment is the stronger signal
            return { c, s };
          }).sort((a,b)=>b.s-a.s);
          // accept only if there's a clear, confident winner (treatment agreed, no tie)
          if (scored[0].s >= 2 && scored[0].s > (scored[1]?.s ?? -1)) {
            match = scored[0].c;
          } else {
            // ambiguous — let the user pick among the shared-number cards
            const cands = numMatches.slice().sort((a,b)=>(a.treatment||"").localeCompare(b.treatment||""));
            setPhotoScan({ status:"candidates", candidates: cands, detected: data, scanPhoto: photoScan?.scanPhoto });
            return;
          }
        }
      }
      // 1. Card number + hero + weapon (all three agree -- most reliable)
      if (!match && identifiedNum && heroName) {
        match = cards.find(c =>
          normNum(c.cardNum)===identifiedNum &&
          heroMatch(c.hero, heroName) &&
          (!weapon || c.weapon?.toLowerCase()===weapon)
        );
      }
      // 2. Card number + hero. If multiple cards share this number+hero (e.g. same
      //    set number across weapons), use the read weapon to disambiguate, and if
      //    it's still ambiguous, fall through to the candidate picker rather than
      //    grabbing the wrong one.
      if (!match && identifiedNum && heroName) {
        const numHero = cards.filter(c => normNum(c.cardNum)===identifiedNum && heroMatch(c.hero, heroName));
        if (numHero.length === 1) {
          match = numHero[0];
        } else if (numHero.length > 1) {
          const wpnCanon = weapon ? canonWeapon(weapon) : "";
          const byWeapon = wpnCanon ? numHero.filter(c => canonWeapon(c.weapon)===wpnCanon) : [];
          if (byWeapon.length === 1) {
            match = byWeapon[0];           // weapon breaks the tie cleanly
          } else {
            // still ambiguous — show these as candidates to pick from
            const cands = (byWeapon.length>1 ? byWeapon : numHero)
              .slice().sort((a,b)=>(a.treatment||"").localeCompare(b.treatment||""));
            setPhotoScan({ status:"candidates", candidates: cands, detected: data, scanPhoto: photoScan?.scanPhoto });
            return;
          }
        }
      }
      // 3. Hero + treatment + weapon (all three agree)
      if (!match && heroName && treatment && weapon) {
        match = cards.find(c =>
          heroMatch(c.hero, heroName) &&
          c.treatment?.toLowerCase()===treatment &&
          c.weapon?.toLowerCase()===weapon
        );
      }
      // 4. Hero + weapon -- use visual score to break ties between treatments
      if (!match && heroName && weapon) {
        const cands = cards.filter(c => heroMatch(c.hero, heroName) && c.weapon?.toLowerCase()===weapon);
        if (cands.length === 1) {
          match = cands[0];
        } else if (cands.length > 1) {
          // Try exact hero name first
          const exact = cands.find(c => c.hero.toLowerCase()===heroName);
          if (exact) {
            match = exact;
          } else if (visualHints) {
            // Score each candidate's treatment against visual hints
            const scored = cands.map(c => ({
              card: c,
              score: visualScore(c.treatment, visualHints)
            })).sort((a,b) => b.score - a.score);
            // Only pick visually if there's a clear winner (score > 0)
            if (scored[0].score > 0 && scored[0].score > scored[1]?.score) {
              match = scored[0].card;
            }
            // Otherwise leave as null -- show no-match so user retakes
          }
        }
      }
      // 5. Card number alone -- disambiguate by weapon if several share the number.
      if (!match && identifiedNum) {
        let byNum = cards.filter(c => normNum(c.cardNum)===identifiedNum && (!heroName || heroMatch(c.hero, heroName)));
        if (byNum.length === 1) {
          match = byNum[0];
        } else if (byNum.length > 1) {
          const wpnCanon = weapon ? canonWeapon(weapon) : "";
          const byWeapon = wpnCanon ? byNum.filter(c => canonWeapon(c.weapon)===wpnCanon) : [];
          if (byWeapon.length === 1) {
            match = byWeapon[0];
          } else if (byNum.length > 0) {
            const cands = (byWeapon.length>1 ? byWeapon : byNum)
              .slice().sort((a,b)=>(a.treatment||"").localeCompare(b.treatment||""));
            setPhotoScan({ status:"candidates", candidates: cands, detected: data, scanPhoto: photoScan?.scanPhoto });
            return;
          }
        }
      }
      // 6. Hero only -- only if exactly one card matches
      if (!match && heroName) {
        const cands = cards.filter(c => heroMatch(c.hero, heroName));
        if (cands.length === 1) match = cands[0];
      }

      if (!match) {
        // No single confident match — surface all cards matching the read hero AND
        // weapon so the user can pick the exact treatment.
        const wpn = (weapon||"").toLowerCase().trim();
        const wpnCanon = wpn ? canonWeapon(wpn) : "";
        let cands = [];
        if (heroName && wpn) {
          cands = cards.filter(c => heroMatch(c.hero, heroName) && canonWeapon(c.weapon)===wpnCanon);
        }
        // fallback: if no weapon was read, match on hero alone
        if (cands.length === 0 && heroName && !wpn) {
          cands = cards.filter(c => heroMatch(c.hero, heroName));
        }
        const seen = new Set();
        cands = cands.filter(c => { if(seen.has(c.id)) return false; seen.add(c.id); return true; })
                     .sort((a,b)=> (a.setName||"").localeCompare(b.setName||"") || normNum(a.cardNum).localeCompare(normNum(b.cardNum)));
        if (cands.length > 0) {
          setPhotoScan({ status:"candidates", candidates: cands, detected: data, scanPhoto: photoScan?.scanPhoto });
          return;
        }
        setPhotoScan({ status:"nomatch", card:null, identified: data });
        if (!scanModal) setTimeout(() => setPhotoScan(null), 5000);
        return;
      }

      // In modal mode -- show match for user to confirm qty before adding
      // In header mode -- auto-add 1 and show toast
      if (scanModal) {
        setScanQty(1);
        // Pass detected data too so user can see what Vision read
        setPhotoScan({ status:"matched", card:match, detected: data });
      } else {
        const next = { ...owned, [match.id]: (owned[match.id]||0) + 1 };
        setOwned(next);
        await setDoc(doc(db,"boba_owned",ownedDocId), next);
        setPhotoScan({ status:"matched", card:match });
        setTimeout(() => setPhotoScan(null), 4000);
      }

    } catch(e) {
      console.error(e);
      setPhotoScan({ status:"error", card:null });
      if (!scanModal) setTimeout(() => setPhotoScan(null), 4000);
    }
  }

  async function confirmScanAdd(qty) {
    if (!photoScan?.card) return;
    const match = photoScan.card;
    const q = parseInt(qty) || 1;
    const next = { ...owned, [match.id]: (owned[match.id]||0) + q };
    setOwned(next);
    await setDoc(doc(db,"boba_owned",ownedDocId), next);
    setScanSession(prev => [{ card:match, qty:q, addedAt:new Date().toISOString() }, ...prev]);

    // Save visual fingerprint for this treatment -- builds accuracy over time
    const hints = photoScan.detected?.visualHints;
    if (hints && match.treatment) {
      const treatKey = match.treatment;
      const existing = treatmentVisuals.current[treatKey] || [];
      // Add new hints, keep last 20 confirmed scans per treatment
      const updated = [...existing, hints].slice(-20);
      treatmentVisuals.current[treatKey] = updated;
      // Save to Firestore in background
      setDoc(doc(db,"treatment_visuals",treatKey), {
        hints: updated,
        treatment: treatKey,
        updatedAt: new Date().toISOString(),
        sampleCount: updated.length,
      }, { merge: true }).catch(()=>{});
    }

    setPhotoScan(null);
    setScanQty(1);
    // Mobile: user must tap the scan area again -- no programmatic click needed
  }

  async function toggleWant(cardId) {
    const next = { ...wantList };
    if (next[cardId]) delete next[cardId];
    else next[cardId] = true;
    setWantList(next);
    await setDoc(doc(db,"boba_owned",wantsDocId), next);
  }

  function exportWantList() {
    const wants = cards.filter(c => wantList[c.id]);
    if (!wants.length) { alert("No cards on your want list!"); return; }
    const rows = [["card_num","hero","treatment","weapon","notation","power"]];
    wants.forEach(c => rows.push([c.cardNum, c.hero, c.treatment||"", c.weapon||"", c.notation||"", c.power||""]));
    const csv = rows.map(r => r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download="boba-want-list.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  function exportHaveList() {
    const haves = cards.filter(c => (owned[c.id]||0) > 1);
    if (!haves.length) { alert("No duplicate cards to trade!"); return; }
    const rows = [["card_num","hero","treatment","weapon","notation","quantity","extras"]];
    haves.forEach(c => rows.push([c.cardNum, c.hero, c.treatment||"", c.weapon||"", c.notation||"", owned[c.id], (owned[c.id]||0)-1]));
    const csv = rows.map(r => r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download="boba-have-list.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  async function saveCustomTracker() {
    const name = builderName.trim();
    if (!name || builderTreatments.length === 0) return;
    const tid = (activeTrackerId && showTrackerBuilder==="edit") ? activeTrackerId : uid();
    const tracker = { id: tid, name, treatments: builderTreatments };
    const exists = customTrackers.find(t => t.id === tracker.id);
    const next = exists ? customTrackers.map(t => t.id===tracker.id ? tracker : t) : [...customTrackers, tracker];
    setCustomTrackers(next);
    try { localStorage.setItem("customTrackers_v1", JSON.stringify(next)); } catch {}
    try { await setDoc(doc(db,"boba_trackers",ownedDocId), { trackers: next }); } catch(e) {}
    setShowTrackerBuilder(false); setBuilderName(""); setBuilderTreatments([]);
    setActiveTrackerId(tracker.id);
  }
  async function deleteCustomTracker(id) {
    if (!window.confirm("Delete this tracker?")) return;
    const next = customTrackers.filter(t => t.id !== id);
    setCustomTrackers(next);
    try { localStorage.setItem("customTrackers_v1", JSON.stringify(next)); } catch {}
    try { await setDoc(doc(db,"boba_trackers",ownedDocId), { trackers: next }); } catch(e) {}
    if (activeTrackerId === id) setActiveTrackerId(null);
  }


  async function setOwnedQty(cardId, qty) {
    const next = { ...owned };
    if (qty <= 0) delete next[cardId];
    else next[cardId] = qty;
    setOwned(next);
    await setDoc(doc(db,"boba_owned",ownedDocId), next);
  }

  // Keep toggleOwned as convenience (0-1, 1-0)
  async function toggleOwned(cardId) {
    await setOwnedQty(cardId, owned[cardId] ? 0 : 1);
  }


  function exportCollection() {
    const ownedCards = cards.filter(c => owned[c.id]);
    if (!ownedCards.length) { alert("No cards owned yet!"); return; }
    const rows = [["card_num","hero","treatment","weapon","notation","quantity"]];
    ownedCards.forEach(c => {
      rows.push([c.cardNum, c.hero, c.treatment, c.weapon, c.notation||"", owned[c.id]||1]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download="boba-collection.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  function downloadTemplate() {
    const csv = [
      // Kept in step with downloadImportTemplate() — the live importer. Two templates drifting apart
      // is how someone ends up with a file whose columns the importer no longer recognises.
      ["Name","Set","Card Number","Parallel","Weapon","Power","Quantity","Estimated Value","Purchase Price","Trade Value","Serial","Notes","Legacy ID"],
      ["Bojax","Alpha Edition","RAD-1","80's Rad Battlefoil","Hex","150",1,"","",""],
      ["Maverick","Alpha Edition","1","Base","Fire","120",2,"","21/50","on-card auto"],
    ].map(r => r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download="boba-collection-template.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  // Import collection from CSV
  const [collectionImportResult, setCollectionImportResult] = useState(null);
  const [wantList, setWantList] = useState({}); // { cardId: true }
  async function importCollectionCsv(file) {
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    const headers = lines[0].split(",").map(h => h.replace(/"/g,"").trim().toLowerCase());
    const idx = k => headers.indexOf(k);
    const cardNumIdx = idx("card_num"), heroIdx = idx("hero"),
          treatIdx = idx("treatment"), weaponIdx = idx("weapon"),
          noteIdx = idx("notation"), qtyIdx = idx("quantity");

    if (cardNumIdx === -1) { alert("CSV must have a 'card_num' column"); return; }

    function normalize(s) { return (s||"").toLowerCase().replace(/[^a-z0-9\s]/g,"").trim(); }
    function fuzzy(a,b) {
      const na=normalize(a), nb=normalize(b);
      if(na===nb) return true;
      if(na.includes(nb)||nb.includes(na)) return true;
      const wa=na.split(/\s+/), wb=nb.split(/\s+/);
      const shared=wa.filter(w=>wb.includes(w)).length;
      return shared>0 && shared/Math.max(wa.length,wb.length)>=0.5;
    }

    const next = { ...owned };
    let matched=0, skipped=0, skippedRows=[];

    for (let i=1; i<lines.length; i++) {
      const cols = lines[i].split(",").map(c => c.replace(/^"|"$/g,"").trim());
      const cardNum = cols[cardNumIdx]||"";
      const hero = cols[heroIdx]||"";
      const treatment = treatIdx>=0 ? cols[treatIdx]||"" : "";
      const weapon = weaponIdx>=0 ? cols[weaponIdx]||"" : "";
      const notation = noteIdx>=0 ? cols[noteIdx]||"" : "";
      const qty = Math.max(1, parseInt(cols[qtyIdx])||1);

      if (!cardNum) { skipped++; continue; }

      // Match by card_num first, then narrow by hero/treatment/weapon
      let match = cards.find(c =>
        String(c.cardNum).toLowerCase() === String(cardNum).toLowerCase() &&
        (!hero || fuzzy(c.hero, hero)) &&
        (!treatment || c.treatment?.toLowerCase() === treatment.toLowerCase()) &&
        (!weapon || c.weapon?.toLowerCase() === weapon.toLowerCase())
      );

      // Fallback: card_num only
      if (!match) match = cards.find(c =>
        String(c.cardNum).toLowerCase() === String(cardNum).toLowerCase()
      );

      if (match) {
        next[match.id] = qty;
        matched++;
      } else {
        skipped++;
        skippedRows.push(`Row ${i+1}: card_num="${cardNum}" hero="${hero}"`);
      }
    }

    setOwned(next);
    await setDoc(doc(db,"boba_owned",ownedDocId), next);
    setCollectionImportResult({ matched, skipped, skippedRows });
  }

  async function importDbsCsv(file) {
    if (!file) return;
    setDbsImporting(true);
    setDbsStatus({ msg:"Reading CSV...", ok:null });
    try {
      const text = await file.text();

      // RFC 4180 compliant CSV parser -- handles quoted fields with embedded newlines/commas
      function parseCSV(str) {
        const rows = [];
        let row = [], cur = "", inQ = false, i = 0;
        while (i < str.length) {
          const ch = str[i];
          if (ch === '"') {
            if (inQ && str[i+1] === '"') { cur += '"'; i += 2; continue; }
            inQ = !inQ;
          } else if (ch === ',' && !inQ) {
            row.push(cur.trim()); cur = "";
          } else if ((ch === '\n' || (ch === '\r' && str[i+1] === '\n')) && !inQ) {
            if (ch === '\r') i++;
            row.push(cur.trim()); rows.push(row); row = []; cur = "";
          } else {
            cur += ch;
          }
          i++;
        }
        if (cur || row.length) { row.push(cur.trim()); rows.push(row); }
        return rows;
      }

      const rows = parseCSV(text).filter(r => r.some(c => c));
      if (rows.length < 2) { setDbsStatus({ msg:"\u274C CSV appears empty", ok:false }); setDbsImporting(false); return; }

      const headers = rows[0].map(h => h.replace(/^"|"$/g,"").trim().toLowerCase());
      setDbsStatus({ msg:`Parsing ${rows.length-1} rows...`, ok:null });

      const find = keys => keys.reduce((f,k) => f >= 0 ? f : headers.indexOf(k), -1);
      const cnIdx   = find(["card number","card_num","card#","cardnum","card num"]);
      const dbsIdx  = find(["dbs score","dbs_score","dbs","salary"]);
      const setIdx  = find(["set name","set","edition"]);
      const costIdx = find(["hot dog cost","hd cost","play cost","cost"]);
      const nameIdx = find(["play name","name","play","card name"]);

      if (cnIdx < 0 || dbsIdx < 0) {
        setDbsStatus({ msg:`\u274C Couldn't find required columns. Found: ${headers.join(", ")}`, ok:false });
        setDbsImporting(false); return;
      }

      // Rarity prefix - set name mapping
      const PREFIX_TO_SET = {
        "a": "2024 Alpha Edition",
        "u": "2025 Alpha Update",
        "g": '2026 Edition "The Griffey Set"',
        "htd": "2025 Alpha Blast",
        "t": "Tecmo Bowl Edition",
        "la": "2024 Alpha Edition",
      };

      function stripPrefix(s) {
        // Strip set prefix in any of these forms:
        //  "A - PL-59" -> "PL-59", "G - BPL-3" -> "BPL-3", "T-PL-53" -> "PL-53",
        //  "HTD-46" -> "46", "LA - 20" -> "20"
        let v = String(s||"").trim();
        v = v.replace(/^(htd)\s*-\s*/i, "");          // HTD-46 -> 46
        v = v.replace(/^([A-Za-z]{1,2})\s*-\s*/,"");   // A - / U - / G - / T- / LA -
        return v.trim();
      }
      function playPrefix(s) {
        // Return the leading set token: A, U, G, T, HTD, LA (lowercased)
        const m = String(s||"").trim().match(/^(HTD|[A-Za-z]{1,2})\s*-/i);
        return m ? m[1].toLowerCase() : "";
      }
      function normStr(s) {
        return String(s||"").toLowerCase().replace(/[^a-z0-9]/g,"");
      }

      // Build lookup map: normalized cardNum - [cards]
      const exactMap = {};
      const nameMap = {};
      cards.forEach(c => {
        const k = normStr(c.cardNum);
        if (!exactMap[k]) exactMap[k] = [];
        exactMap[k].push(c);
        const nk = normStr(c.hero); // play name is stored in the hero field for plays
        if (nk) { if(!nameMap[nk]) nameMap[nk]=[]; nameMap[nk].push(c); }
      });

      let updated = 0, skipped = 0;
      const batch = [];

      for (let i = 1; i < rows.length; i++) {
        const cols    = rows[i];
        const rawNum  = (cols[cnIdx]||"").replace(/^"|"$/g,"").trim();
        const rawDbs  = (cols[dbsIdx]||"").replace(/^"|"$/g,"").trim();
        const dbs     = parseFloat(rawDbs);
        if (!rawNum || isNaN(dbs)) { skipped++; continue; }

        const baseNum  = stripPrefix(rawNum);
        const normNum  = normStr(baseNum);
        const csvSet   = setIdx >= 0 ? (cols[setIdx]||"").replace(/^"|"$/g,"").trim() : "";
        const playCost = costIdx >= 0 ? (cols[costIdx]||"").replace(/^"|"$/g,"").trim() : "";
        const rawName  = nameIdx >= 0 ? (cols[nameIdx]||"").replace(/^"|"$/g,"").trim() : "";

        // Resolve the set from prefix (T-, HTD-, A -, etc.) or CSV set column
        const prefix = playPrefix(rawNum);
        const prefixSet = PREFIX_TO_SET[prefix] || "";
        const resolvedSet = csvSet || prefixSet;

        // PRIMARY: match by card number
        let matches = exactMap[normNum] || [];
        // FALLBACK: match by play name if card number found nothing
        let matchedBy = "num";
        if (matches.length === 0 && rawName) {
          matches = nameMap[normStr(rawName)] || [];
          matchedBy = "name";
        }
        if (matches.length === 0) { skipped++; continue; }

        // Narrow by set when multiple candidates (critical for same-name-across-sets)
        if (matches.length > 1 && resolvedSet) {
          const bySet = matches.filter(c => normStr(c.setName||"") === normStr(resolvedSet));
          if (bySet.length > 0) matches = bySet;
        }
        // If a name match still has multiple across sets and we couldn't narrow, skip to avoid
        // writing DBS to the wrong set's copy.
        if (matchedBy === "name" && matches.length > 1) { skipped++; continue; }

        for (const match of matches) {
          const update = { dbs };
          if (playCost) update.playCost = playCost;
          batch.push({ id: match.id, update });
          updated++;
        }
      }

      // Write in batches of 400
      setDbsStatus({ msg:`Writing ${batch.length} cards to Firestore...`, ok:null });
      for (let i = 0; i < batch.length; i += 400) {
        const chunk = batch.slice(i, i + 400);
        await Promise.all(chunk.map(({ id, update }) =>
          setDoc(doc(db, "boba_checklist", id), update, { merge: true })
        ));
        setDbsStatus({ msg:`Writing... ${Math.min(i+400, batch.length)}/${batch.length}`, ok:null });
      }

      // Bust cache and reload
      try { localStorage.removeItem("boba_checklist_cache_v3"); } catch(e2) {}
      const freshSnap = await getDocs(collection(db, "boba_checklist"));
      const freshCards = freshSnap.docs.map(d => ({...d.data(), id:d.id})).sort((a,b) => String(a.cardNum||"").localeCompare(String(b.cardNum||""), undefined, { numeric:true }));
      setCards(freshCards);
      try { localStorage.setItem("boba_checklist_cache_v3", JSON.stringify({ cards: freshCards, ts: Date.now() })); } catch(e2) {}
      setDbsStatus({ msg:`\u2705 Done! Updated ${updated} cards, skipped ${skipped}.`, ok:true });
      setTimeout(() => setDbsStatus(null), 5000);
    } catch(e) {
      console.error(e);
      setDbsStatus({ msg:"\u274C Error: " + e.message, ok:false });
    }
    setDbsImporting(false);
  }

  function handleFileSelect(e) {
    const file = e.target.files[0]; if(!file) return;
    setPendingFile(file);
    setSetNameInput("");
    e.target.value = "";
  }

  async function handleImport() {
    if(!pendingFile || !setNameInput.trim()) return;
    setImporting(true);
    setImportProgress("Reading file...");
    const text = await pendingFile.text();
    const lines = [];
    let cur="", inQ=false;
    for(let ci=0;ci<text.length;ci++){
      const ch=text[ci];
      if(ch==='"') inQ=!inQ;
      if((ch==="\n"||ch==="\r")&&!inQ){ if(cur.trim()) lines.push(cur.replace(/\r/,'')); cur=""; }
      else cur+=ch;
    }
    if(cur.trim()) lines.push(cur);
    const headers = lines[0].split(',').map(h=>h.replace(/"/g,'').trim().toLowerCase());
    const idx = k => headers.indexOf(k);
    const cardNumIdx=idx('card #'), heroIdx=idx('hero'), varIdx=idx('variation'),
          treatIdx=idx('treatment'), weaponIdx=idx('weapon'), noteIdx=idx('notation'),
          powerIdx=idx('power'), athIdx=idx('athlete inspiration'),
          costIdx=idx('play cost'), abilityIdx=idx('play ability');
    // If adding to existing set, reuse its importId so card IDs are consistent
    const existingImport = imports.find(i => i.setName === setNameInput.trim());
    const importId = existingImport ? existingImport.id : uid();
    const setName = setNameInput.trim();
    const cardIds = [];
    const batch = [];
    for(let i=1;i<lines.length;i++){
      const cols=[];let c="",q=false;
      for(const ch of lines[i]){
        if(ch==='"'){q=!q;}else if(ch===','&&!q){cols.push(c.trim());c="";}else c+=ch;
      }
      cols.push(c.trim());
      const cardNum = cols[cardNumIdx]||"";
      if(!cardNum||cardNum==="undefined") continue;
      const id = `${importId}_${cardNum.replace(/[^a-zA-Z0-9]/g,'_')}`;
      cardIds.push(id);
      batch.push({
        id, cardNum, setName, importId,
        hero:cols[heroIdx]||"", variation:cols[varIdx]||"",
        treatment:cols[treatIdx]||"", weapon:cols[weaponIdx]||"",
        notation:cols[noteIdx]||"", power:cols[powerIdx]||"",
        athlete:cols[athIdx]||"", playCost:cols[costIdx]||"",
        playAbility:cols[abilityIdx]||"",
      });
    }
    setImportProgress(`Writing ${batch.length} cards...`);
    for(let i=0;i<batch.length;i+=200){
      const chunk=batch.slice(i,i+200);
      await Promise.all(chunk.map(c=>setDoc(doc(db,"boba_checklist",c.id),c)));
      setImportProgress(`Writing ${Math.min(i+200,batch.length)} / ${batch.length}...`);
    }
    await setDoc(doc(db,"boba_imports",importId), {
      id:importId, setName, filename:pendingFile.name,
      importedAt:new Date().toISOString(), cardCount:batch.length, cardIds,
    });
    setImporting(false);
    setImportProgress("");
    setPendingFile(null);
    setSetNameInput("");
    setNewSetMode(false);
    if (onChecklistUpdated) onChecklistUpdated();
  }

  async function handleDeleteImport(imp) {
    if(!window.confirm(`Delete set "${imp.setName}" (${imp.cardCount} cards)? This cannot be undone.`)) return;
    const chunkSize = 200;
    for(let i=0;i<imp.cardIds.length;i+=chunkSize){
      await Promise.all(imp.cardIds.slice(i,i+chunkSize).map(id=>deleteDoc(doc(db,"boba_checklist",id))));
    }
    const nextOwned = {...owned};
    imp.cardIds.forEach(id=>delete nextOwned[id]);
    await setDoc(doc(db,"boba_owned",ownedDocId), nextOwned);
    await deleteDoc(doc(db,"boba_imports",imp.id));
    if (onChecklistUpdated) onChecklistUpdated();
  }

  async function handleRenameSet(imp, newName) {
    if(!newName.trim()) return;
    // Update import record
    await setDoc(doc(db,"boba_imports",imp.id), { ...imp, setName:newName.trim() }, { merge:true });
    // Update all cards in this import
    const chunkSize = 200;
    for(let i=0;i<imp.cardIds.length;i+=chunkSize){
      await Promise.all(imp.cardIds.slice(i,i+chunkSize).map(id=>
        setDoc(doc(db,"boba_checklist",id), { setName:newName.trim() }, { merge:true })
      ));
    }
    setRenamingId(null);
  }

  const sets = [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort();
  const treatments = [...new Set(cards.filter(c=>!filterSet||c.setName===filterSet).map(c=>c.treatment).filter(Boolean))].sort();
  const weapons    = sortWeapons([...new Set(cards.filter(c=>!filterSet||c.setName===filterSet).map(c=>canonWeapon(c.weapon)).filter(Boolean))]);
  const notations  = [...new Set(cards.filter(c=>!filterSet||c.setName===filterSet).map(c=>c.notation).filter(Boolean))].sort();

  const filtered = cards.filter(c => {
    if(filterSet && c.setName !== filterSet) return false;
    if(search && !`${c.cardNum} ${c.hero} ${c.variation} ${c.athlete}`.toLowerCase().includes(search.toLowerCase())) return false;
    if(filterTreat && c.treatment !== filterTreat) return false;
    if(filterWeapon && c.weapon !== filterWeapon) return false;
    if(filterNote && c.notation !== filterNote) return false;
    if(filterOwned==="owned" && !owned[c.id]) return false;
    if(filterOwned==="missing" && owned[c.id]) return false;
    if(filterPower.size>0 && !filterPower.has(Number(c.power||0))) return false;
    return true;
  }).sort((a,b) => {
    if(sortBy==="cardNum") {
      return String(a.cardNum||"").localeCompare(String(b.cardNum||""), undefined, { numeric:true, sensitivity:"base" });
    }
    if(sortBy==="power_desc") return (parseFloat(b.power)||0)-(parseFloat(a.power)||0);
    if(sortBy==="power_asc")  return (parseFloat(a.power)||0)-(parseFloat(b.power)||0);
    if(sortBy==="hero")       return (a.hero||"").localeCompare(b.hero||"");
    if(sortBy==="treatment")  return (a.treatment||"").localeCompare(b.treatment||"");
    if(sortBy==="weapon")     return (a.weapon||"").localeCompare(b.weapon||"");
    if(sortBy==="owned")      return (owned[b.id]?1:0)-(owned[a.id]?1:0);
    return 0;
  });

  const totalOwned = Object.keys(owned).length; // unique cards
  const totalCollection = Object.values(owned).reduce((s,q)=>s+(q||0),0); // total copies
  const totalCards = cards.length;
  const pct = totalCards > 0 ? Math.round(totalOwned/totalCards*100) : 0;
  const totalPages = Math.ceil(filtered.length/PAGE_SIZE);
  const paginated = filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  const WEAPON_COLORS = { Fire:"#F97316", Ice:"#60A5FA", Steel:"#C0C0C0", Brawl:"#EF4444",
    Glow:"#4ade80", Hex:"#A855F7", Gum:"#F472B6", Metallic:"var(--bz-line)",
    Alt:"#FFFFFF", Super:"#F59E0B", "":"#444" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* Header */}
      <div style={{ ...S.card, padding:"12px 16px" }}>
        {/* Row 1: title + stats + view toggles */}
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom: imports.length > 0 ? 10 : 0, flexWrap:"wrap" }}>
          <span style={{ fontSize:16, fontWeight:900, color:"var(--bz-ink)" }}>{"\uD83C\uDCCF BoBA"}</span>
          <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{totalCards.toLocaleString()} cards</span>
          {totalOwned > 0 && <span style={{ fontSize:11, color:"#4ade80", fontWeight:700 }}>{totalOwned} owned</span>}
          {totalCollection > totalOwned && <span style={{ fontSize:11, color:"#7B9CFF" }}>{totalCollection} copies</span>}
          {pct > 0 && <span style={{ fontSize:11, color:"#FBBF24", fontWeight:700 }}>{pct}%</span>}
          <div style={{ flex:1 }}/>
          {/* View toggles */}
          <div style={{ display:"flex", gap:3, flexWrap:"wrap" }} className="view-mode-row">
            {[["cards","🃏 Cards"],["setlist","📋 Set List"],["treatments","📋 Treatments"],["rainbow","🌈 Rainbow"],["supers","⭐ Supers"],["1of1","💎 1/1s"],["stats","📊 Stats"],["wants","🎯 Wants"],["deck","⚔️ Deck"],["playbook","📖 Playbook"]].map(([v,l])=>(
              <button key={v} onClick={()=>setViewMode(v)} style={{ background:viewMode===v?"#1A1A2E":"transparent", color:viewMode===v?"#E8317A":"var(--bz-ink-2)", border:`1.5px solid ${viewMode===v?"#E8317A":"var(--bz-line-2)"}`, borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>{l}</button>
            ))}
          </div>
          {/* Action buttons -- compact */}
          <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
            {isAdmin && (
              <label style={{ background:"#1A1A2E", color:"#E8317A", border:"1px solid #E8317A44", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                {"\uD83D\uDCC2 Import Cards"}<input type="file" accept=".csv" onChange={handleFileSelect} style={{ display:"none" }}/>
              </label>
            )}
            {isAdmin && (
              <label title="Reads the card number off each image (most accurate) — use this for Christopher's images" style={{ background:"#0a1a0a", color:"#4ade80", border:"1px solid #4ade8044", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:imgScanProgress?"not-allowed":"pointer", fontFamily:"inherit", whiteSpace:"nowrap", opacity:imgScanProgress?0.5:1 }}>
                {"\uD83D\uDD0D Scan & Import Images"}<input type="file" accept=".webp,.jpg,.jpeg,.png" multiple disabled={!!imgScanProgress}
                  onChange={e=>{
                    const files = e.target.files;
                    if (!files || files.length === 0) return;
                    setImgImportSet(filterSet || "");
                    setImgImportModal({ files });
                    e.target.value="";
                  }} style={{ display:"none" }}/>
              </label>
            )}
            {isAdmin && (
              <button title="Force-reload all cards from the database (use after importing images)" onClick={async()=>{ try{localStorage.removeItem("boba_checklist_cache_v3");}catch(e){} setLoading(true); const snap=await getDocs(collection(db,"boba_checklist")); const sorted=snap.docs.map(d=>d.data()).sort((a,b)=>{const n1=parseFloat(a.cardNum),n2=parseFloat(b.cardNum); if(!isNaN(n1)&&!isNaN(n2))return n1-n2; return String(a.cardNum).localeCompare(String(b.cardNum));}); setCards(sorted); setLoading(false); try{localStorage.setItem("boba_checklist_cache_v3",JSON.stringify({cards:sorted,ts:Date.now()}));}catch(e){} }} style={{ background:"#1A1A2E", color:"#60A5FA", border:"1px solid #60A5FA44", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>🔄 Refresh</button>
            )}
            {/* Camera scan -- opens modal on click */}
            <button title="Scan cards into your collection"
              onClick={()=>{ setScanModal(true); setScanSession([]); setPhotoScan(null); }}
              style={{ background:"#1a0a1a", color:"#E8317A", border:"1px solid #E8317A44", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
              {"\uD83D\uDCF7 Scan Card"}</button>
            {isAdmin && (
              <label title="Import DBS salary values (CSV: card_num, dbs)" style={{ background:"#1a0f1a", color:"#A855F7", border:"1px solid #A855F744", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:dbsImporting?"not-allowed":"pointer", fontFamily:"inherit", whiteSpace:"nowrap", opacity:dbsImporting?0.5:1 }}>
                {dbsImporting?"Importing...":"\uD83D\uDCB0 Import DBS"}
                <input type="file" accept=".csv" disabled={dbsImporting} onChange={e=>{ const f=e.target.files[0]; if(f) importDbsCsv(f); e.target.value=""; }} style={{ display:"none" }}/>
              </label>
            )}
            {isAdmin && (
              <button onClick={async()=>{
                if(!window.confirm("Wipe all DBS import data (dbs, playCost, playName) from all play cards so you can re-import clean. Continue?")) return;
                const playcards = cards.filter(c=>{ const t=(c.treatment||"").toLowerCase(); return t==="plays"||t==="bonus plays"||t==="home team discount"; });
                setDbsStatus({ msg:`Wiping DBS data from ${playcards.length} play cards...`, ok:null });
                for(let i=0;i<playcards.length;i+=400){
                  await Promise.all(playcards.slice(i,i+400).map(c=>
                    setDoc(doc(db,"boba_checklist",c.id), { dbs:deleteField(), playCost:deleteField(), playName:deleteField() }, { merge:true })
                  ));
                  setDbsStatus({ msg:`Wiping... ${Math.min(i+400,playcards.length)}/${playcards.length}`, ok:null });
                }
                try { localStorage.removeItem("boba_checklist_cache_v3"); } catch(e){}
                const snap = await getDocs(collection(db,"boba_checklist"));
                const fresh = snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>String(a.cardNum||"").localeCompare(String(b.cardNum||""),undefined,{numeric:true}));
                setCards(fresh);
                try { localStorage.setItem("boba_checklist_cache_v3", JSON.stringify({ cards:fresh, ts:Date.now() })); } catch(e){}
                setDbsStatus({ msg:`\u2705 Wiped ${playcards.length} play cards -- re-import DBS CSV now`, ok:true });
                setTimeout(()=>setDbsStatus(null),8000);
              }} style={{ background:"#1a0a0a", border:"1px solid #E8317A44", color:"#E8317A", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                {"\uD83E\uDDF9 Wipe DBS Data"}</button>
            )}
            {dbsStatus && (
              <span style={{ fontSize:11, fontWeight:700, color:dbsStatus.ok===true?"#4ade80":dbsStatus.ok===false?"#E8317A":"#A855F7", background:dbsStatus.ok===true?"#0a1a0a":dbsStatus.ok===false?"#1a0a0a":"#1a0f1a", border:`1px solid ${dbsStatus.ok===true?"#4ade8044":dbsStatus.ok===false?"#E8317A44":"#A855F744"}`, borderRadius:7, padding:"4px 10px", whiteSpace:"nowrap" }}>
                {dbsStatus.msg}
              </span>
            )}
            {isAdmin && totalOwned === 0 && (
              <button onClick={async()=>{
                if (!window.confirm("Copy your collection from the old shared doc to your personal account?")) return;
                const oldSnap = await getDoc(doc(db,"boba_owned","owned"));
                if (oldSnap.exists() && Object.keys(oldSnap.data()).length > 0) {
                  const data = oldSnap.data();
                  await setDoc(doc(db,"boba_owned",ownedDocId), data);
                  setOwned(data);
                  alert(`\u2705 Imported ${Object.keys(data).length} cards to your account!`);
                } else {
                  alert("No data found in the shared collection.");
                }
              }} style={{ background:"#0a1a0a", border:"1px solid #4ade8044", color:"#4ade80", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                {"\u2191 Restore My Collection"}</button>
            )}
            <label style={{ background:"#0a0f1a", color:"#7B9CFF", border:"1px solid #7B9CFF44", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
              {"\uD83D\uDCE5 Collection"}<input type="file" accept=".csv" onChange={e=>{ const f=e.target.files[0]; if(f) importCollectionCsv(f); e.target.value=""; }} style={{ display:"none" }}/>
            </label>
            <button onClick={downloadTemplate} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDCCB"}</button>
            {totalOwned > 0 && (
              <button onClick={exportCollection} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"#4ade80", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDCE4"}</button>
            )}
            {totalOwned > 0 && (
              <button onClick={async()=>{ if(!window.confirm(`Clear all ${totalOwned} owned cards?`)) return; await setDoc(doc(db,"boba_owned",ownedDocId),{}); setOwned({}); }} style={{ background:"transparent", border:"1px solid #E8317A22", color:"#E8317A", borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\u2715"}</button>
            )}
          </div>
        </div>
        {/* Row 2: per-set progress bars -- collapsible */}
        {imports.length > 0 && (
          <div>
            <button onClick={()=>setSetsCollapsed(p=>!p)}
              style={{ background:"none", border:"none", color:"var(--bz-ink-3)", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit", padding:"4px 0", display:"flex", alignItems:"center", gap:4 }}>
              {setsCollapsed ? "\u25B6 Show sets" : "\u25BC Hide sets"}
            </button>
            {!setsCollapsed && (
              <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                {imports.map(imp => {
                  const setCards = cards.filter(c => c.setName === imp.setName);
                  const setOwned = setCards.filter(c => owned[c.id]).length;
                  const setPct   = setCards.length > 0 ? Math.round(setOwned/setCards.length*100) : 0;
                  const isComplete = setPct === 100;
                  return (
                    <div key={imp.id} style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:10, color:isComplete?"#4ade80":"#666", fontWeight:isComplete?700:400, width:220, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", flexShrink:0 }}>
                        {isComplete?"\uD83C\uDF08 ":""}{imp.setName}
                      </span>
                      <div style={{ flex:1, height:4, background:"var(--bz-s2)", borderRadius:2, overflow:"hidden" }}>
                        <div className="bz-rainbow-fill" style={{ width:`${setPct}%`, height:"100%", borderRadius:2, transition:"width 0.3s" }}/>
                      </div>
                      <span style={{ fontSize:10, color:isComplete?"#4ade80":setPct>0?"#FBBF24":"#333", minWidth:80, textAlign:"right", flexShrink:0 }}>
                        {setOwned}/{setCards.length} · {setPct}%
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Import Set Picker Modal */}
      {imgImportModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={()=>setImgImportModal(null)}>
          <div style={{ background:"var(--bz-s1)", border:"1.5px solid #4ade8044", borderRadius:16, padding:28, width:420, maxWidth:"90vw" }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ fontSize:18, fontWeight:900, color:"var(--bz-ink)", marginBottom:4 }}>
              {"\uD83D\uDDBC Import"}{imgImportModal.files.length}{" Image"}{imgImportModal.files.length !== 1 ? "s" : ""}
            </div>
            <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginBottom:20 }}>Select which set these images belong to. This dramatically improves matching accuracy.</div>
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:8 }}>Set (recommended)</label>
              <select
                value={imgImportSet}
                onChange={e=>setImgImportSet(e.target.value)}
                autoFocus
                style={{ width:"100%", background:"var(--bz-bg)", border:"1.5px solid var(--bz-line-2)", borderRadius:8, color:imgImportSet?"var(--bz-ink)":"#666", padding:"10px 12px", fontSize:13, fontFamily:"inherit", outline:"none", cursor:"pointer" }}>
                <option value="">-- No set filter (filename / Vision only) --</option>
                {sets.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              {imgImportSet && (
                <div style={{ fontSize:11, color:"#4ade80", marginTop:6 }}>
                  {"\u2713 Matching against"}{cards.filter(c=>c.setName===imgImportSet).length}{" cards in "}{imgImportSet}
                </div>
              )}
            </div>
            <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:8, padding:"10px 14px", marginBottom:20, fontSize:11, color:"var(--bz-ink-3)", lineHeight:1.7 }}>
              <div style={{ color:"#4ade80", fontWeight:700, marginBottom:4 }}>Matching priority:</div>
              <div>{"1. Filename set-order number (e.g. setorder61)"}</div>
              <div>{"2. Card # + set + hero name"}</div>
              <div>{"3. Hero + treatment + weapon + power"}</div>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button
                onClick={()=>{ scanImagesForCards(imgImportModal.files, imgImportSet); setImgImportModal(null); }}
                style={{ flex:1, background:"#4ade80", color:"#000", border:"none", borderRadius:10, padding:"12px 0", fontSize:14, fontWeight:900, cursor:"pointer", fontFamily:"inherit" }}>
                Start Import
              </button>
              <button
                onClick={()=>setImgImportModal(null)}
                style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:10, padding:"12px 20px", fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Collection Import Result Modal */}
      {collectionImportResult && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={()=>setCollectionImportResult(null)}>
          <div style={{ background:"var(--bz-s1)", border:"1.5px solid #7B9CFF44", borderRadius:14, padding:"24px", width:420, maxWidth:"90vw" }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontWeight:800, fontSize:15, color:"#7B9CFF", marginBottom:12 }}>{"\uD83D\uDCE5 Import Complete"}</div>
            <div style={{ fontSize:13, color:"#4ade80", marginBottom:4 }}>{"\u2705"}{collectionImportResult.matched} cards matched & imported</div>
            {collectionImportResult.skipped > 0 && (
              <div style={{ fontSize:13, color:"#E8317A", marginBottom:8 }}>{"\u26A0\uFE0F"}{collectionImportResult.skipped} rows skipped (no match found)</div>
            )}
            {collectionImportResult.skippedRows.length > 0 && (
              <div style={{ maxHeight:120, overflowY:"auto", background:"var(--bz-bg)", borderRadius:6, padding:"8px 10px", fontSize:10, color:"#999", marginBottom:12 }}>
                {collectionImportResult.skippedRows.map((r,i)=><div key={i}>{r}</div>)}
              </div>
            )}
            <button onClick={()=>setCollectionImportResult(null)} style={{ background:"#0a0f1a", color:"#7B9CFF", border:"1.5px solid #7B9CFF", borderRadius:8, padding:"8px 18px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Done</button>
          </div>
        </div>
      )}

      {/* Scan Config Modal */}
      {pendingScan && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={()=>setPendingScan(null)}>
          <div style={{ background:"var(--bz-s1)", border:"1.5px solid #7B9CFF44", borderRadius:14, padding:"24px", width:420, maxWidth:"90vw" }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontWeight:800, fontSize:15, color:"#7B9CFF", marginBottom:4 }}>{"\uD83D\uDD0D Scan PDF:"}{pendingScan.file.name}</div>
            <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginBottom:16 }}>Tell Claude what's in this PDF so it only needs to match hero names</div>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={S.lbl}>Treatment</label>
                <select value={pendingScan.treatment||""} onChange={e=>setPendingScan(p=>({...p,treatment:e.target.value}))} style={{ ...S.inp, cursor:"pointer" }}>
                  <option value="">-- Select Treatment --</option>
                  {[...new Set(cards.map(c=>c.treatment).filter(Boolean))].sort().map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={S.lbl}>Weapon</label>
                <select value={pendingScan.weapon||""} onChange={e=>setPendingScan(p=>({...p,weapon:e.target.value}))} style={{ ...S.inp, cursor:"pointer" }}>
                  <option value="">-- Select Weapon --</option>
                  {["Fire","Ice","Steel","Brawl","Glow","Hex","Gum","Super","Alt","Metallic"].map(w=><option key={w} value={w}>{w}</option>)}
                </select>
              </div>
            </div>
            <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:10 }}>
              {pendingScan.treatment && pendingScan.weapon
                ? `Will match hero names to ${pendingScan.treatment} / ${pendingScan.weapon} cards`
                : "Select treatment and weapon to narrow matching"}
            </div>
            <div style={{ display:"flex", gap:8, marginTop:16 }}>
              <button onClick={()=>{ if(!pendingScan.treatment||!pendingScan.weapon){ alert("Please select both treatment and weapon"); return; } scanPdfForCards(pendingScan.file, pendingScan.setName, pendingScan.treatment, pendingScan.weapon); setPendingScan(null); }} style={{ background:"#0a0f1a", color:"#7B9CFF", border:"1.5px solid #7B9CFF", borderRadius:8, padding:"8px 18px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDD0D Start Scan"}</button>
              <button onClick={()=>setPendingScan(null)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"8px 18px", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Scan Progress */}
      {scanProgress && (
        <div style={{ ...S.card, border:"1.5px solid #7B9CFF44", background:"#0a0f1a" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
            <div style={{ fontWeight:700, color:"#7B9CFF", fontSize:14 }}>{"\uD83D\uDD0D Scanning:"}{scanPdf}</div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={()=>{ scanPausedRef.current=!scanPausedRef.current; setScanPaused(p=>!p); }} style={{ background:"var(--bz-s2)", color:"#7B9CFF", border:"1px solid #7B9CFF44", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                {scanPaused ? "\u25B6 Resume" : "\u23F8 Pause"}
              </button>
            </div>
          </div>
          <div style={{ height:8, background:"var(--bz-s2)", borderRadius:4, overflow:"hidden", marginBottom:8 }}>
            <div style={{ width:`${scanProgress.total > 0 ? Math.round(scanProgress.current/scanProgress.total*100) : 0}%`, height:"100%", background:"linear-gradient(90deg,#7B9CFF,#C084FC)", borderRadius:4, transition:"width 0.3s" }}/>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:11 }}>
            <span style={{ color:"var(--bz-ink-2)" }}>{scanProgress.status}</span>
            <span style={{ color:"#7B9CFF", fontWeight:700 }}>{scanProgress.current}/{scanProgress.total} pages</span>
          </div>
        </div>
      )}

      )}

      {/* -- SCAN MODAL -- */}
      {scanModal && (
        <div style={{ position:"fixed", inset:0, background:"#000", zIndex:2000, display:"flex", flexDirection:"column", overflow:"hidden" }}>

          {/* Header bar */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px", background:"var(--bz-bg)", borderBottom:"1px solid var(--bz-line)", flexShrink:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ fontSize:16, fontWeight:900, color:"#E8317A" }}>{"\uD83D\uDCF7 Scan Mode"}</span>
              {scanSession.length > 0 && (
                <span style={{ fontSize:12, background:"#E8317A22", color:"#E8317A", border:"1px solid #E8317A44", borderRadius:20, padding:"2px 10px", fontWeight:700 }}>
                  {scanSession.length} added
                </span>
              )}
            </div>
            <button onClick={()=>{ setScanModal(false); setPhotoScan(null); setScanQty(1); }}
              style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              Done
            </button>
          </div>

          <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column" }}>

            {/* Scan result / camera trigger area */}
            <div style={{ padding:"16px", flexShrink:0 }}>
              {!photoScan && (
                 <label style={{ display:"block", width:"100%", cursor:"pointer" }}>
                   <div style={{ background:"var(--bz-bg)", border:"2px dashed #E8317A44", borderRadius:16, padding:"32px 16px", textAlign:"center" }}>
                     <div style={{ fontSize:48, marginBottom:8 }}>{"\uD83D\uDCF7"}</div>
                     <div style={{ fontSize:16, fontWeight:800, color:"#E8317A", marginBottom:4 }}>Tap to scan a card</div>
                     <div style={{ fontSize:12, color:"var(--bz-ink-3)" }}>Opens camera to identify the card</div>
                   </div>
                   <input type="file" accept="image/*"
                     onChange={e=>{ const f=e.target.files?.[0]; if(f){ setPhotoScan(null); scanCardPhoto(f); } e.target.value=""; }}
                     style={{ position:"absolute", opacity:0, width:1, height:1, pointerEvents:"none" }}/>
                 </label>
              )}

              {photoScan?.status === "scanning" && (
                <div style={{ background:"#0a0f1a", border:"1.5px solid #7B9CFF44", borderRadius:16, padding:"32px 16px", textAlign:"center" }}>
                  <div style={{ width:40, height:40, border:"3px solid #1a1a2e", borderTopColor:"#E8317A", borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 16px" }}/>
                  <div style={{ fontSize:15, fontWeight:700, color:"#7B9CFF" }}>Reading card...</div>
                </div>
              )}

              {photoScan?.status === "matched" && photoScan.card && (() => {
                const c = photoScan.card;
                const wc = { Fire:"#F97316", Ice:"#60A5FA", Steel:"#C0C0C0", Brawl:"#EF4444", Glow:"#4ade80", Hex:"#A855F7", Gum:"#F472B6", Metallic:"var(--bz-line)", Alt:"#FFFFFF", Super:"#F59E0B" }[c.weapon] || "#888";
                return (
                  <div style={{ background:"#0a1a0a", border:"1.5px solid #4ade8044", borderRadius:16, overflow:"hidden" }}>
                    <div style={{ display:"flex", gap:12, padding:"14px" }}>
                      {c.imageUrl
                        ? <img src={c.imageUrl} alt={c.hero} style={{ width:72, height:96, objectFit:"cover", borderRadius:10, flexShrink:0, boxShadow:"0 4px 16px rgba(0,0,0,0.6)" }}/>
                        : <div style={{ width:72, height:96, background:"var(--bz-s2)", borderRadius:10, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, color:"var(--bz-ink-3)", textAlign:"center" }}>{c.hero?.split(" ")[0]}</div>
                      }
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:16, fontWeight:900, color:"var(--bz-ink)", marginBottom:4 }}>{c.hero}</div>
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:6 }}>
                          {c.weapon && <span style={{ fontSize:11, fontWeight:700, color:wc }}>{c.weapon}</span>}
                          {c.treatment && <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{c.treatment}</span>}
                          {c.setName && <span style={{ fontSize:11, color:"var(--bz-ink-3)", fontStyle:"italic" }}>{c.setName}</span>}
                        </div>
                        {c.power && <div style={{ fontSize:28, fontWeight:900, color:wc, lineHeight:1 }}>{c.power}</div>}
                        {c.athlete && <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:4 }}>Inspired by {c.athlete}</div>}
                        {/* Debug: show what Vision actually detected */}
                        {photoScan.detected && (
                          <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:6, borderTop:"1px solid var(--bz-line)", paddingTop:4 }}>
                            <div>Vision read: #{photoScan.detected.cardNum||"?"} · {photoScan.detected.hero||"?"} · {photoScan.detected.weapon||"?"}</div>
                            {photoScan.detected.visualHints && <div style={{ color:"#8a8a8a", marginTop:2 }}>Visual: {photoScan.detected.visualHints}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Qty + confirm */}
                    <div style={{ padding:"12px 14px", borderTop:"1px solid #1a2a1a", display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:0, background:"var(--bz-s1)", borderRadius:8, border:"1px solid var(--bz-line-2)", overflow:"hidden" }}>
                        <button onClick={()=>setScanQty(q=>Math.max(1,q-1))}
                          style={{ background:"none", border:"none", color:"var(--bz-ink-2)", fontSize:18, fontWeight:700, width:40, height:40, cursor:"pointer", fontFamily:"inherit" }}>{"\u2212"}</button>
                        <span style={{ fontSize:16, fontWeight:900, color:"var(--bz-ink)", minWidth:32, textAlign:"center" }}>{scanQty}</span>
                        <button onClick={()=>setScanQty(q=>q+1)}
                          style={{ background:"none", border:"none", color:"var(--bz-ink-2)", fontSize:18, fontWeight:700, width:40, height:40, cursor:"pointer", fontFamily:"inherit" }}>+</button>
                      </div>
                      <button onClick={()=>confirmScanAdd(scanQty)}
                        style={{ flex:1, background:"#4ade80", color:"#000", border:"none", borderRadius:8, padding:"10px 0", fontSize:14, fontWeight:900, cursor:"pointer", fontFamily:"inherit" }}>
                        {"\u2713 Add"}{scanQty > 1 ? `${scanQty}\u00D7` : ""} to Collection
                      </button>
                      <label style={{ background:"var(--bz-s2)", color:"var(--bz-ink-3)", border:"1px solid var(--bz-line-2)", borderRadius:8, padding:"10px 14px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center" }}>
                        Retake
                        <input type="file" accept="image/*" onChange={e=>{ const f=e.target.files[0]; if(f){ setPhotoScan(null); setScanQty(1); scanCardPhoto(f); } e.target.value=""; }} style={{ display:"none" }}/>
                      </label>
                      <button onClick={()=>{ setPhotoScan({ status:"nomatch", card:null, identified: photoScan.detected }); }}
                        style={{ background:"transparent", border:"none", color:"#E8317A", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", padding:"0 4px" }}>
                        Wrong card?
                      </button>
                    </div>
                  </div>
                );
              })()}

              {photoScan?.status === "nomatch" && (
                <div style={{ background:"#1a0a0a", border:"1.5px solid #E8317A44", borderRadius:16, padding:"20px 16px", textAlign:"center" }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>{"\u274C"}</div>
                  <div style={{ fontSize:15, fontWeight:800, color:"#E8317A", marginBottom:4 }}>Card not recognized</div>
                  {photoScan.identified?.hero && <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginBottom:12 }}>Detected: {photoScan.identified.hero} #{photoScan.identified.cardNum}</div>}
                  <label style={{ background:"#E8317A", color:"#fff", border:"none", borderRadius:8, padding:"10px 24px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit", display:"inline-block" }}>
                    Try Again
                    <input type="file" accept="image/*" onChange={e=>{ const f=e.target.files[0]; if(f){ setPhotoScan(null); scanCardPhoto(f); } e.target.value=""; }} style={{ display:"none" }}/>
                  </label>
                </div>
              )}

              {photoScan?.status === "error" && (
                <div style={{ background:"#1a0a0a", border:"1.5px solid #E8317A44", borderRadius:16, padding:"20px 16px", textAlign:"center" }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>{"\u26A0\uFE0F"}</div>
                  <div style={{ fontSize:15, fontWeight:800, color:"#E8317A", marginBottom:8 }}>Scan failed</div>
                  {photoScan.message && <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:12, fontFamily:"monospace" }}>{photoScan.message}</div>}
                  <label style={{ background:"#E8317A", color:"#fff", border:"none", borderRadius:8, padding:"10px 24px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit", display:"inline-block" }}>
                    Try Again
                    <input type="file" accept="image/*" onChange={e=>{ const f=e.target.files?.[0]; if(f){ setPhotoScan(null); scanCardPhoto(f); } e.target.value=""; }} style={{ display:"none" }}/>
                  </label>
                </div>
              )}
            </div>

            {/* Session log */}
            {scanSession.length > 0 && (
              <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column", borderTop:"1px solid var(--bz-line)" }}>
                <div style={{ padding:"10px 16px 6px", fontSize:11, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, flexShrink:0 }}>
                  This Session -- {scanSession.reduce((s,e)=>s+e.qty,0)} cards added
                </div>
                <div style={{ flex:1, overflowY:"auto", padding:"0 16px 16px" }}>
                  {scanSession.map((entry, i) => {
                    const c = entry.card;
                    const wc = { Fire:"#F97316", Ice:"#60A5FA", Steel:"#C0C0C0", Brawl:"#EF4444", Glow:"#4ade80", Hex:"#A855F7", Gum:"#F472B6", Metallic:"var(--bz-line)", Alt:"#FFFFFF", Super:"#F59E0B" }[c.weapon] || "#888";
                    return (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid #111" }}>
                        {c.imageUrl
                          ? <img src={c.imageUrl} alt={c.hero} style={{ width:32, height:42, objectFit:"cover", borderRadius:5, flexShrink:0 }}/>
                          : <div style={{ width:32, height:42, background:"var(--bz-s2)", borderRadius:5, flexShrink:0 }}/>
                        }
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)" }}>{c.hero}</div>
                          <div style={{ fontSize:11, color:wc }}>{c.weapon} · {c.power}</div>
                        </div>
                        <div style={{ fontSize:13, fontWeight:900, color:"#4ade80", flexShrink:0 }}>+{entry.qty}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* First scan prompt if nothing yet */}
            {!photoScan && scanSession.length === 0 && (
              <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", color:"#aaa", fontSize:12, padding:16 }}>
                Session log will appear here after your first scan
              </div>
            )}
          </div>
        </div>
      )}

      {/* Non-modal photoScan toast (used when not in modal) */}
      {!scanModal && photoScan && photoScan.status !== "scanning" && (
        <div style={{ ...S.card, border:`1.5px solid ${photoScan.status==="matched"?"#4ade8044":"#E8317A44"}`, background:photoScan.status==="matched"?"#0a1a0a":"#1a0a0a", display:"flex", alignItems:"center", gap:12 }}>
          {photoScan.status==="matched" && <><span style={{ fontSize:28 }}>{"\u2705"}</span><div><div style={{ fontWeight:800, color:"#4ade80", fontSize:14 }}>Added to collection!</div><div style={{ fontSize:12, color:"var(--bz-ink-2)", marginTop:2 }}>{photoScan.card?.hero} · #{photoScan.card?.cardNum}</div></div></>}
          {photoScan.status==="nomatch" && <><span style={{ fontSize:28 }}>{"\u274C"}</span><div><div style={{ fontWeight:800, color:"#E8317A", fontSize:14 }}>Card not found</div>{photoScan.identified && <div style={{ fontSize:12, color:"var(--bz-ink-2)", marginTop:2 }}>Detected: {photoScan.identified.hero||"?"} #{photoScan.identified.cardNum||"?"}</div>}</div></>}
          {photoScan.status==="error" && <><span style={{ fontSize:28 }}>{"\u26A0\uFE0F"}</span><div style={{ fontWeight:800, color:"#E8317A", fontSize:14 }}>Scan failed -- try again</div></>}
        </div>
      )}

      {imgScanProgress && (
        <div style={{ position:"fixed",bottom:24,right:24,zIndex:9999,background:"#0a1a0a",border:"1.5px solid #4ade8044",borderRadius:12,padding:"16px 20px",minWidth:300,boxShadow:"0 8px 40px rgba(0,0,0,0.8)",fontFamily:"'Trebuchet MS',sans-serif" }}>
          <div style={{ fontWeight:700, color:"#4ade80", fontSize:14, marginBottom:10 }}>{"\uD83D\uDDBC Scanning Images..."}</div>
          <div style={{ height:8, background:"var(--bz-s2)", borderRadius:4, overflow:"hidden", marginBottom:8 }}>
            <div style={{ width:`${imgScanProgress.total > 0 ? Math.round(imgScanProgress.current/imgScanProgress.total*100) : 0}%`, height:"100%", background:"linear-gradient(90deg,#4ade80,#7B9CFF)", borderRadius:4, transition:"width 0.3s" }}/>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:11 }}>
            <span style={{ color:"var(--bz-ink-2)" }}>{imgScanProgress.status}</span>
            <span style={{ color:"#4ade80", fontWeight:700 }}>{imgScanProgress.current}/{imgScanProgress.total} images</span>
          </div>
        </div>
      )}

      {/* Import modal */}
      {pendingFile && (() => {
        const existingSets = [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort();
        return (
          <div style={{ ...S.card, border:"1.5px solid #E8317A44", background:"#0a0005" }}>
            <div style={{ fontWeight:700, color:"var(--bz-ink)", marginBottom:10 }}>{"\uD83D\uDCC2"}{pendingFile.name}</div>
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              {!newSetMode && existingSets.length > 0 ? (
                <>
                  <select
                    value={setNameInput}
                    onChange={e => { if(e.target.value === "__new__") { setNewSetMode(true); setSetNameInput(""); } else setSetNameInput(e.target.value); }}
                    style={{ ...S.inp, flex:1, cursor:"pointer", color:setNameInput?"var(--bz-ink)":"#555" }}
                    autoFocus
                  >
                    <option value="">-- Select existing set --</option>
                    {existingSets.map(s=><option key={s} value={s}>{s}</option>)}
                    <option value="__new__">+ Create new set...</option>
                  </select>
                </>
              ) : (
                <div style={{ display:"flex", gap:8, flex:1, alignItems:"center" }}>
                  {existingSets.length > 0 && (
                    <button onClick={()=>{ setNewSetMode(false); setSetNameInput(""); }}
                      style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:7, padding:"6px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                      {"\u2190 Existing"}</button>
                  )}
                  <input
                    value={setNameInput}
                    onChange={e=>setSetNameInput(e.target.value)}
                    placeholder="New set name (e.g. 2025 Alpha Blast)"
                    style={{ ...S.inp, flex:1 }}
                    onKeyDown={e=>e.key==="Enter"&&handleImport()}
                    autoFocus
                  />
                </div>
              )}
              <Btn onClick={handleImport} variant="green" disabled={!setNameInput.trim()||importing}>
                {importing ? importProgress||"Importing..." : "\u2705 Import"}
              </Btn>
              <Btn onClick={()=>{ setPendingFile(null); setNewSetMode(false); setSetNameInput(""); }} variant="ghost">Cancel</Btn>
            </div>
            {setNameInput && !newSetMode && (
              <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:6 }}>
                Adding cards to existing set: <span style={{ color:"#7B9CFF", fontWeight:700 }}>{setNameInput}</span>
              </div>
            )}
          </div>
        );
      })()}

      {/* Filters */}
      <div style={{ ...S.card, display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
        <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} placeholder="Search card #, hero, athlete..." style={{ ...S.inp, flex:1, minWidth:200 }}/>
        {sets.length > 0 && (
          <select value={filterSet} onChange={e=>{setFilterSet(e.target.value);setFilterTreat("");setFilterWeapon("");setFilterNote("");setPage(1);}} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
            <option value="">All Sets</option>
            {sets.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <select value={filterTreat} onChange={e=>{setFilterTreat(e.target.value);setPage(1);}} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
          <option value="">All Treatments</option>
          {treatments.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterWeapon} onChange={e=>{setFilterWeapon(e.target.value);setPage(1);}} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
          <option value="">All Weapons</option>
          {weapons.map(w=><option key={w} value={w}>{w}</option>)}
        </select>
        <div style={{ display:"flex", gap:3, flexWrap:"wrap", alignItems:"center" }}>
          <span style={{ fontSize:10, color:"var(--bz-ink-3)", marginRight:2 }}>Power:</span>
          {[250,200,195,190,185,180,175,170,165,160,155,150,145,140,135,130,125,120,115,110,105,100,95,90,85,80,75].map(p=>(
            <button key={p} onClick={()=>{setFilterPower(prev=>{const n=new Set(prev);n.has(p)?n.delete(p):n.add(p);return n;});setPage(1);}}
              style={{ background:filterPower.has(p)?"rgba(232,49,122,0.15)":"transparent", color:filterPower.has(p)?"#E8317A":"#555", border:`1px solid ${filterPower.has(p)?"#E8317A":"var(--bz-line-2)"}`, borderRadius:5, padding:"2px 7px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
              {p}
            </button>
          ))}
          {filterPower.size>0 && <button onClick={()=>{setFilterPower(new Set());setPage(1);}} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", fontSize:10, cursor:"pointer" }}>✕</button>}
        </div>
        <select value={filterNote} onChange={e=>{setFilterNote(e.target.value);setPage(1);}} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
          <option value="">All Notations</option>
          {notations.map(n=><option key={n} value={n}>{n}</option>)}
        </select>
        <select value={sortBy} onChange={e=>{setSortBy(e.target.value);setPage(1);}} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
          <option value="cardNum">Sort: Card #</option>
          <option value="hero">{"Sort: Hero A\u2192Z"}</option>
          <option value="power_desc">{"Sort: Power High\u2192Low"}</option>
          <option value="power_asc">{"Sort: Power Low\u2192High"}</option>
          <option value="treatment">Sort: Treatment</option>
          <option value="weapon">Sort: Weapon</option>
          <option value="owned">Sort: Owned First</option>
        </select>
        <div style={{ display:"flex", gap:4 }}>
          {[["all","All"],["owned","\u2705 Owned"],["missing","\u274C Missing"]].map(([v,l])=>(
            <button key={v} onClick={()=>{setFilterOwned(v);setPage(1);}} style={{ background:filterOwned===v?"#1A1A2E":"transparent", color:filterOwned===v?"#E8317A":"var(--bz-ink-2)", border:`1.5px solid ${filterOwned===v?"#E8317A":"#333"}`, borderRadius:7, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{filtered.length.toLocaleString()} cards</span>
      </div>

      {/* Rainbow Tracker */}
      {viewMode === "rainbow" && !loading && cards.length > 0 && (() => {
        // ── Mode toggle: Full Rainbow vs Custom Trackers ──
        const modeToggle = (
          <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
            {[["full","🌈 Full Rainbow"],["custom","🎯 Custom Trackers"]].map(([v,l])=>(
              <button key={v} onClick={()=>setRainbowMode(v)} style={{ background:rainbowMode===v?"var(--bz-pink)":"transparent", color:rainbowMode===v?"#fff":"var(--bz-ink-2)", border:`1.5px solid ${rainbowMode===v?"var(--bz-pink)":"#333"}`, borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>{l}</button>
            ))}
          </div>
        );

        if (rainbowMode === "custom") {
          const allTreatments = [...new Set(cards.map(c=>c.treatment).filter(Boolean))].sort();
          const active = customTrackers.find(t => t.id === activeTrackerId);

          // Build the tracker view: one row per hero, complete if any owned card matches the chosen treatment(s)
          const renderTracker = (tracker) => {
            const treatSet = new Set(tracker.treatments);
            const heroes = [...new Set(cards.filter(c=>c.hero).map(c=>c.hero))].sort();
            const rows = heroes.map(hero => {
              const heroTreatCards = cards.filter(c => c.hero===hero && treatSet.has(c.treatment));
              const ownedMatch = heroTreatCards.filter(c => owned[c.id]);
              return { hero, need: heroTreatCards.length, complete: ownedMatch.length>0, ownedCards: ownedMatch, exists: heroTreatCards.length>0 };
            });
            const inSet = rows.filter(r=>r.exists);
            const done = inSet.filter(r=>r.complete).length;
            const pct = inSet.length ? Math.round(done/inSet.length*100) : 0;
            return (
              <div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, flexWrap:"wrap", gap:8 }}>
                  <div>
                    <div style={{ fontSize:16, fontWeight:900, color:"var(--bz-ink)" }}>{tracker.name}</div>
                    <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{tracker.treatments.join(" · ")} · one per hero</div>
                  </div>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <span style={{ fontSize:20, fontWeight:900, color: done===inSet.length && inSet.length>0 ? "#4ade80" : "#FBBF24" }}>{done}/{inSet.length}</span>
                    <button onClick={()=>{ setBuilderName(tracker.name); setBuilderTreatments(tracker.treatments); setActiveTrackerId(tracker.id); setShowTrackerBuilder("edit"); }} style={{ background:"transparent", border:"1px solid #333", color:"var(--bz-ink-3)", borderRadius:6, padding:"4px 10px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Edit</button>
                    <button onClick={()=>deleteCustomTracker(tracker.id)} style={{ background:"transparent", border:"1px solid rgba(232,49,122,0.3)", color:"#E8317A", borderRadius:6, padding:"4px 10px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Delete</button>
                  </div>
                </div>
                <div style={{ height:8, background:"var(--bz-s2)", borderRadius:4, overflow:"hidden", marginBottom:14 }}>
                  <div className="bz-rainbow-fill" style={{ width:`${pct}%`, height:"100%", transition:"width .3s" }}/>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:8 }}>
                  {inSet.map(r => (
                    <div key={r.hero} style={{ background:r.complete?"rgba(74,222,128,0.08)":"var(--bz-s1)", border:`1.5px solid ${r.complete?"rgba(74,222,128,0.4)":"var(--bz-s2)"}`, borderRadius:9, padding:"10px 12px" }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:6 }}>
                        <span style={{ fontSize:12, fontWeight:800, color:r.complete?"var(--bz-ink)":"#666" }}>{r.complete?"✅ ":""}{r.hero}</span>
                      </div>
                      {r.complete && r.ownedCards[0] && (
                        <div style={{ fontSize:10, color:"#4ade80", marginTop:4 }}>
                          Have: {r.ownedCards.map(c=>[c.weapon,c.cardNum?"#"+c.cardNum:""].filter(Boolean).join(" ")).join(", ")}
                        </div>
                      )}
                      {!r.complete && (
                        <div style={{ fontSize:10, color:"#8a8a8a", marginTop:4 }}>{r.need} option{r.need!==1?"s":""} available</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          };

          return (
            <div>
              {modeToggle}
              {/* Tracker builder modal-ish */}
              {showTrackerBuilder ? (
                <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-pink-line, rgba(232,49,122,0.3))", borderRadius:12, padding:"18px 20px", marginBottom:16 }}>
                  <div style={{ fontSize:14, fontWeight:800, color:"var(--bz-ink)", marginBottom:12 }}>{showTrackerBuilder==="edit"?"Edit Tracker":"New Custom Tracker"}</div>
                  <input value={builderName} onChange={e=>setBuilderName(e.target.value)} placeholder="Tracker name (e.g. Gold Coin — One Per Hero)" style={{ ...S.inp, width:"100%", marginBottom:12 }}/>
                  <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Treatments to count (pick one or more)</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, maxHeight:200, overflowY:"auto", marginBottom:14 }}>
                    {allTreatments.map(t => {
                      const sel = builderTreatments.includes(t);
                      return (
                        <button key={t} onClick={()=>setBuilderTreatments(sel?builderTreatments.filter(x=>x!==t):[...builderTreatments,t])} style={{ background:sel?"var(--bz-pink)":"transparent", color:sel?"#fff":"var(--bz-ink-2)", border:`1px solid ${sel?"var(--bz-pink)":"#333"}`, borderRadius:20, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{sel?"✓ ":""}{t}</button>
                      );
                    })}
                  </div>
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={saveCustomTracker} disabled={!builderName.trim()||builderTreatments.length===0} style={{ background:(!builderName.trim()||builderTreatments.length===0)?"#333":"var(--bz-pink)", color:"#fff", border:"none", borderRadius:8, padding:"8px 18px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>Save Tracker</button>
                    <button onClick={()=>{ setShowTrackerBuilder(false); setBuilderName(""); setBuilderTreatments([]); }} style={{ background:"transparent", border:"1px solid #333", color:"var(--bz-ink-3)", borderRadius:8, padding:"8px 18px", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:16, alignItems:"center" }}>
                  {customTrackers.map(t => (
                    <button key={t.id} onClick={()=>setActiveTrackerId(t.id)} style={{ background:activeTrackerId===t.id?"#1A1A2E":"var(--bz-s1)", color:activeTrackerId===t.id?"#E8317A":"var(--bz-ink-2)", border:`1.5px solid ${activeTrackerId===t.id?"#E8317A":"#333"}`, borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{t.name}</button>
                  ))}
                  <button onClick={()=>{ setShowTrackerBuilder("new"); setBuilderName(""); setBuilderTreatments([]); setActiveTrackerId(null); }} style={{ background:"transparent", border:"1.5px dashed var(--bz-pink)", color:"var(--bz-pink)", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>+ New Tracker</button>
                </div>
              )}
              {!showTrackerBuilder && (
                active ? renderTracker(active) :
                customTrackers.length === 0
                  ? <div style={{ textAlign:"center", padding:"50px 20px", color:"var(--bz-ink-3)", fontSize:13 }}>Build a custom tracker to chase one hero per hero in any treatment — like a gold coin of every hero.</div>
                  : <div style={{ textAlign:"center", padding:"40px", color:"var(--bz-ink-3)", fontSize:13 }}>Pick a tracker above to view your progress.</div>
              )}
            </div>
          );
        }

        // Pulled from the memoized rainbowStats above — no per-render recompute.
        const { heroStats, availableSets, completedRainbows, partialRainbows } = rainbowStats;

        const filteredHeroes = heroStats.filter(h =>
          !search || h.hero.toLowerCase().includes(search.toLowerCase())
        );

        const visibleHeroes = filteredHeroes.filter(h => {
          if(rainbowFilter === "complete") return h.complete;
          if(rainbowFilter === "partial")  return h.ownedCount > 0 && !h.complete;
          if(rainbowFilter === "missing")  return h.ownedCount === 0;
          return true;
        });

        return (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {modeToggle}
            {/* Summary */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
              {[
                { l:"\uD83C\uDF08 Complete Rainbows", v:completedRainbows, c:"#4ade80" },
                { l:"\uD83D\uDD36 In Progress",       v:partialRainbows,   c:"#FBBF24" },
                { l:"\u2B1C Not Started",        v:heroStats.length-completedRainbows-partialRainbows, c:"#555" },
              ].map(({l,v,c})=>(
                <div key={l} style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:12, padding:"12px 16px", textAlign:"center" }}>
                  <div style={{ fontSize:26, fontWeight:900, color:c }}>{v}</div>
                  <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Filter */}
            <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
              {/* Set selector -- dropdown */}
              {availableSets.length > 0 && (
                <select value={rainbowSetFilter} onChange={e=>setRainbowSetFilter(e.target.value)} style={{ ...S.inp, width:"auto", fontSize:11, padding:"5px 10px", cursor:"pointer" }}>
                  <option value="">{"\uD83C\uDF08 All Sets"}</option>
                  {availableSets.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              )}
              {[["all","All Heroes"],["complete","\uD83C\uDF08 Complete"],["partial","\uD83D\uDD36 In Progress"],["missing","\u2B1C Not Started"]].map(([v,l])=>(
                <button key={v} onClick={()=>setRainbowFilter(v)} style={{ background:rainbowFilter===v?"#1A1A2E":"transparent", color:rainbowFilter===v?"#E8317A":"var(--bz-ink-2)", border:`1.5px solid ${rainbowFilter===v?"#E8317A":"#333"}`, borderRadius:7, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{l}</button>
              ))}
              <span style={{ fontSize:11, color:"var(--bz-ink-3)", marginLeft:4 }}>{visibleHeroes.length} heroes</span>
            </div>

            {/* Hero grid */}
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {visibleHeroes.map(({ hero, total, ownedCount, complete, bySets }) => {
                const pct = total > 0 ? Math.round(ownedCount/total*100) : 0;
                const isExpanded = expandedHero === hero;
                const heroCardList = cards.filter(c => c.hero === hero).sort((a,b) => {
                  // Sort by weapon then treatment
                  const WEAPONS = ["Fire","Ice","Steel","Brawl","Glow","Hex","Gum","Super","Alt","Metallic"];
                  const wa = WEAPONS.indexOf(a.weapon), wb = WEAPONS.indexOf(b.weapon);
                  if(wa !== wb) return wa - wb;
                  return (a.treatment||"").localeCompare(b.treatment||"");
                });
                return (
                  <div key={hero} style={{ background:"var(--bz-s1)", border:`1.5px solid ${complete?"#ffffff22":ownedCount>0?"#FBBF2422":"var(--bz-s2)"}`, borderRadius:10, overflow:"hidden" }}>
                    {/* Hero row -- click to expand */}
                    <div onClick={()=>setExpandedHero(isExpanded ? null : hero)} style={{ padding:"12px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: Object.keys(bySets).length > 1 && !rainbowSetFilter ? 6 : 8 }}>
                          <span style={{ fontSize:13, fontWeight:800, color:complete?"var(--bz-ink)":ownedCount>0?"var(--bz-ink)":"#555" }}>
                            {complete && "\uD83C\uDF08 "}{hero}
                          </span>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <span style={{ fontSize:11, fontWeight:700, color:complete?"#4ade80":ownedCount>0?"#FBBF24":"#555" }}>
                              {ownedCount}/{total} cards{complete ? " -- RAINBOW! \uD83C\uDF08" : ""}
                            </span>
                            <span style={{ color:"#8a8a8a", fontSize:12 }}>{isExpanded?"\u25B2":"\u25BC"}</span>
                          </div>
                        </div>
                        {/* Per-set mini progress when on All Sets and hero spans multiple sets */}
                        {!rainbowSetFilter && Object.keys(bySets).length > 1 && (
                          <div style={{ display:"flex", gap:6, marginBottom:6, flexWrap:"wrap" }}>
                            {Object.entries(bySets).map(([setName, sd]) => {
                              const sp = Math.round(sd.owned/sd.total*100);
                              const sc = sd.owned===sd.total?"#4ade80":sd.owned>0?"#FBBF24":"#555";
                              return (
                                <div key={setName} style={{ display:"flex", alignItems:"center", gap:4, background:"var(--bz-s2)", borderRadius:5, padding:"2px 8px" }}>
                                  <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{setName}:</span>
                                  <span style={{ fontSize:10, fontWeight:700, color:sc }}>{sd.owned}/{sd.total}</span>
                                  {sd.owned===sd.total && <span style={{ fontSize:10 }}>{"\uD83C\uDF08"}</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Rainbow progress bar */}
                        <div style={{ height:6, background:"var(--bz-s2)", borderRadius:3, overflow:"hidden" }}>
                          <div className="bz-rainbow-fill" style={{
                            width:`${pct}%`, height:"100%", borderRadius:3, transition:"width 0.3s"
                          }}/>
                        </div>
                      </div>
                    </div>

                    {/* Expanded -- fan out all cards */}
                    {isExpanded && (
                      <div style={{ borderTop:"1px solid var(--bz-line)", padding:"12px 14px", background:"var(--bz-bg)" }}>
                        <div style={{ display:"flex", gap:4, marginBottom:10 }}>
                          {[["all","All"],["owned","\u2705 Have"],["missing","\u274C Missing"]].map(([v,l])=>(
                            <button key={v} onClick={e=>{ e.stopPropagation(); setTreatOwnedFilter(v); }} style={{ background:treatOwnedFilter===v?"#1A1A2E":"transparent", color:treatOwnedFilter===v?"#E8317A":"var(--bz-ink-2)", border:`1.5px solid ${treatOwnedFilter===v?"#E8317A":"#333"}`, borderRadius:7, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{l}</button>
                          ))}
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:6 }}>
                          {heroCardList.filter(c => treatOwnedFilter==="owned" ? owned[c.id] : treatOwnedFilter==="missing" ? !owned[c.id] : true).map(c => {
                            const isOwned = !!owned[c.id];
                            return <BobaCard key={c.id} c={c} isOwned={isOwned} ownedQty={owned[c.id]||0} flippedCard={flippedCard} setFlippedCard={setFlippedCard} toggleOwned={toggleOwned} setOwnedQty={setOwnedQty} toggleWant={toggleWant} wantList={wantList} WEAPON_COLORS={WEAPON_COLORS} isAdmin={isAdmin} onDelete={isAdmin?async()=>{ if(window.confirm("Delete "+c.hero+" "+c.treatment+" from checklist?")) await deleteDoc(doc(db,"boba_checklist",c.id)); }:null} onImageUpload={isAdmin?async(card,file)=>{ 
  try { 
    const safe = (card.treatment||"t").replace(/[^a-zA-Z0-9]/g,"_");
    const r2 = ref(storage, `boba_cards/manual/${card.id}_${safe}.png`);
    const _cfile = await compressImage(file);
    await uploadBytes(r2, _cfile);
    const url = await getDownloadURL(r2);
    await setDoc(doc(db,"boba_checklist",card.id), { imageUrl:url }, {merge:true});
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
  } catch(e) { alert("Upload failed: "+e.message); }
}:null}/>;
                          })}
                        </div>
                        {/* Toggle all for hero */}
                        <div style={{ marginTop:10, display:"flex", gap:8 }}>
                          <button onClick={async e=>{ e.stopPropagation(); const next={...owned}; heroCardList.forEach(c=>next[c.id]=1); setOwned(next); await setDoc(doc(db,"boba_owned",ownedDocId),next); }} style={{ background:"#0a1a0a", border:"1px solid #4ade8044", color:"#4ade80", borderRadius:7, padding:"4px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\u2705 Mark All Owned"}</button>
                          <button onClick={async e=>{ e.stopPropagation(); const next={...owned}; heroCardList.forEach(c=>delete next[c.id]); setOwned(next); await setDoc(doc(db,"boba_owned",ownedDocId),next); }} style={{ background:"#1a0a0a", border:"1px solid #E8317A44", color:"#E8317A", borderRadius:7, padding:"4px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\u2715 Clear All"}</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Treatments View */}
      {viewMode === "treatments" && !loading && cards.length > 0 && (() => {
        // Group filtered cards by treatment
        const treatmentMap = {};
        filtered.forEach(c => {
          const t = c.treatment || "Uncategorized";
          if(!treatmentMap[t]) treatmentMap[t] = [];
          treatmentMap[t].push(c);
        });
        const treatmentList = Object.entries(treatmentMap)
          .map(([t, tcards]) => {
            const ownedCount = tcards.filter(c => owned[c.id]).length;
            const pct = Math.round(ownedCount / tcards.length * 100);
            const complete = ownedCount === tcards.length;
            return { treatment:t, tcards, ownedCount, pct, complete };
          })
          .sort((a,b) => b.pct - a.pct || a.treatment.localeCompare(b.treatment));

        return (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{treatmentList.length} treatments · respects active filters</span>
              <div style={{ display:"flex", gap:4, marginLeft:"auto" }}>
                {[["all","All"],["owned","\u2705 Have"],["missing","\u274C Missing"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setTreatOwnedFilter(v)} style={{ background:treatOwnedFilter===v?"#1A1A2E":"transparent", color:treatOwnedFilter===v?"#E8317A":"var(--bz-ink-2)", border:`1.5px solid ${treatOwnedFilter===v?"#E8317A":"#333"}`, borderRadius:7, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{l}</button>
                ))}
              </div>
            </div>
            {treatmentList.map(({ treatment, tcards, ownedCount, pct, complete }) => {
              const isExp = expandedTreat === treatment;
              // Filter expanded cards by owned status
              const visibleTcards = treatOwnedFilter === "owned" ? tcards.filter(c=>owned[c.id]) : treatOwnedFilter === "missing" ? tcards.filter(c=>!owned[c.id]) : tcards;
              return (
                <div key={treatment} style={{ background:"var(--bz-s1)", border:`1.5px solid ${complete?"#ffffff22":pct>0?"#FBBF2422":"var(--bz-s2)"}`, borderRadius:10, overflow:"hidden" }}>
                  {/* Treatment header row */}
                  <div onClick={()=>setExpandedTreat(isExp ? null : treatment)} style={{ padding:"12px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                        <span style={{ fontSize:13, fontWeight:800, color:complete?"var(--bz-ink)":pct>0?"var(--bz-ink)":"#555" }}>
                          {complete && "\u2705 "}{treatment}
                        </span>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ fontSize:11, fontWeight:700, color:complete?"#4ade80":pct>0?"#FBBF24":"#555" }}>
                            {ownedCount}/{tcards.length} · {pct}%
                          </span>
                          <span style={{ color:"#8a8a8a", fontSize:12 }}>{isExp?"\u25B2":"\u25BC"}</span>
                        </div>
                      </div>
                      <div style={{ height:6, background:"var(--bz-s2)", borderRadius:3, overflow:"hidden" }}>
                        <div className="bz-rainbow-fill" style={{
                          width:`${pct}%`, height:"100%", borderRadius:3, transition:"width 0.3s"
                        }}/>
                      </div>
                    </div>
                  </div>
                  {/* Expanded cards */}
                  {isExp && (
                    <div style={{ borderTop:"1px solid var(--bz-line)", padding:"12px 14px", background:"var(--bz-bg)" }}>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:6 }}>
                        {visibleTcards.sort((a,b)=>String(a.cardNum).localeCompare(String(b.cardNum),undefined,{numeric:true})).map(c => {
                          const isOwned = !!owned[c.id];
                          return <BobaCard key={c.id} c={c} isOwned={isOwned} ownedQty={owned[c.id]||0} flippedCard={flippedCard} setFlippedCard={setFlippedCard} toggleOwned={toggleOwned} setOwnedQty={setOwnedQty} toggleWant={toggleWant} wantList={wantList} WEAPON_COLORS={WEAPON_COLORS} isAdmin={isAdmin} onDelete={isAdmin?async()=>{ if(window.confirm("Delete "+c.hero+" "+c.treatment+" from checklist?")) await deleteDoc(doc(db,"boba_checklist",c.id)); }:null} onImageUpload={isAdmin?async(card,file)=>{ 
  try { 
    const safe = (card.treatment||"t").replace(/[^a-zA-Z0-9]/g,"_");
    const r2 = ref(storage, `boba_cards/manual/${card.id}_${safe}.png`);
    const _cfile = await compressImage(file);
    await uploadBytes(r2, _cfile);
    const url = await getDownloadURL(r2);
    await setDoc(doc(db,"boba_checklist",card.id), { imageUrl:url }, {merge:true});
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
  } catch(e) { alert("Upload failed: "+e.message); }
}:null}/>;
                        })}
                      </div>
                      <div style={{ marginTop:10, display:"flex", gap:8 }}>
                        <button onClick={async e=>{ e.stopPropagation(); const next={...owned}; tcards.forEach(c=>next[c.id]=true); setOwned(next); await setDoc(doc(db,"boba_owned",ownedDocId),next); }} style={{ background:"#0a1a0a", border:"1px solid #4ade8044", color:"#4ade80", borderRadius:7, padding:"4px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\u2705 Mark All Owned"}</button>
                        <button onClick={async e=>{ e.stopPropagation(); const next={...owned}; tcards.forEach(c=>delete next[c.id]); setOwned(next); await setDoc(doc(db,"boba_owned",ownedDocId),next); }} style={{ background:"#1a0a0a", border:"1px solid #E8317A44", color:"#E8317A", borderRadius:7, padding:"4px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\u2715 Clear All"}</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Card grid */}
      {/* Stats View */}
      {viewMode === "supers" && !loading && (() => {
        // All Super weapon cards grouped by set
        const superCards = cards.filter(c => c.weapon === "Super" || (c.weapon||"").toUpperCase() === "SUPER");
        const superSets = [...new Set(superCards.map(c => c.setName).filter(Boolean))].sort();

        // Build claim map: cardId -> claim doc
        const claimMap = {};
        superClaims.forEach(cl => { claimMap[cl.cardId] = cl; });

        async function submitClaim(card, photoBase64) {
          if (!user) return;
          setClaimSubmitting(true);
          try {
            // Upload photo to storage
            const storageRef2 = ref(storage, `super_claims/${card.id}_${Date.now()}.jpg`);
            const byteStr = atob(photoBase64.split(",")[1]);
            const ab = new ArrayBuffer(byteStr.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteStr.length; i++) ia[i] = byteStr.charCodeAt(i);
            await uploadBytes(storageRef2, new Blob([ab], { type: "image/jpeg" }));
            const photoUrl = await getDownloadURL(storageRef2);
            const claimId = card.id;
            await setDoc(doc(db, "super_claims", claimId), {
              cardId: card.id, cardName: card.hero, cardNum: card.cardNum,
              setName: card.setName || "", cardImage: card.imageUrl || null,
              userId: user.uid, userName: user.displayName || user.email,
              photoUrl, status: "pending",
              createdAt: new Date().toISOString(),
            });
            setClaimSent(true);
          } catch(e) { alert("Upload failed: " + e.message); }
          setClaimSubmitting(false);
        }

        async function adminVerify(claimId, approve, reason) {
          await setDoc(doc(db, "super_claims", claimId), {
            status: approve ? "verified" : "denied",
            denialReason: reason || "",
            reviewedAt: new Date().toISOString(),
            reviewedBy: user?.displayName || "Admin",
          }, { merge: true });
        }

        const pendingClaims = superClaims.filter(cl => cl.status === "pending");
        const verifiedCount = superClaims.filter(cl => cl.status === "verified").length;
        const totalSupers = superCards.length;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Claim modal */}
            {claimModal && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
                onClick={() => { if (!claimSubmitting) { setClaimModal(null); setClaimPhoto(null); setClaimSent(false); } }}>
                <div style={{ background: "var(--bz-s1)", border: "2px solid #F59E0B", borderRadius: 20, padding: 28, maxWidth: 420, width: "100%" }} onClick={e => e.stopPropagation()}>
                  {claimSent ? (
                    <div style={{ textAlign: "center", padding: "20px 0" }}>
                      <div style={{ fontSize: 52, marginBottom: 16 }}>⭐</div>
                      <div style={{ fontSize: 20, fontWeight: 900, color: "#F59E0B", marginBottom: 8 }}>Claim Submitted!</div>
                      <div style={{ fontSize: 13, color: "var(--bz-ink-2)", marginBottom: 24 }}>Your Super Foil claim is pending verification. You'll be notified once reviewed.</div>
                      <button onClick={() => { setClaimModal(null); setClaimPhoto(null); setClaimSent(false); }}
                        style={{ background: "#F59E0B", color: "#000", border: "none", borderRadius: 12, padding: "12px 32px", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>Done</button>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 18, fontWeight: 900, color: "#F59E0B", marginBottom: 4 }}>⭐ Claim Super Foil</div>
                      <div style={{ fontSize: 13, color: "var(--bz-ink-2)", marginBottom: 20 }}>{claimModal.hero} #{claimModal.cardNum} · {claimModal.setName}</div>
                      <div style={{ fontSize: 12, color: "var(--bz-ink-3)", marginBottom: 16, lineHeight: 1.6 }}>
                        Super Foils are 1/1 cards. Upload a photo of your card as proof — an admin will verify your claim before it counts toward the community tracker.
                      </div>
                      <label style={{ display: "block", marginBottom: 16 }}>
                        <div style={{ background: claimPhoto ? "#0a1a0a" : "var(--bz-bg)", border: `2px dashed ${claimPhoto ? "#F59E0B" : "var(--bz-line-2)"}`, borderRadius: 12, padding: "20px", textAlign: "center", cursor: "pointer" }}>
                          {claimPhoto ? (
                            <img src={claimPhoto} alt="proof" style={{ maxHeight: 200, maxWidth: "100%", borderRadius: 8, objectFit: "contain" }}/>
                          ) : (
                            <>
                              <div style={{ fontSize: 32, marginBottom: 8 }}>📸</div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#F59E0B" }}>Tap to upload photo</div>
                              <div style={{ fontSize: 11, color: "var(--bz-ink-3)", marginTop: 4 }}>Clear photo of your Super Foil card</div>
                            </>
                          )}
                        </div>
                        <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                          onChange={e => {
                            const f = e.target.files?.[0]; if (!f) return;
                            const reader = new FileReader();
                            reader.onload = ev => setClaimPhoto(ev.target.result);
                            reader.readAsDataURL(f);
                            e.target.value = "";
                          }}/>
                      </label>
                      <div style={{ display: "flex", gap: 10 }}>
                        <button onClick={() => { if (claimPhoto) submitClaim(claimModal, claimPhoto); }}
                          disabled={!claimPhoto || claimSubmitting}
                          style={{ flex: 1, background: claimPhoto ? "#F59E0B" : "var(--bz-s2)", color: claimPhoto ? "#000" : "#555", border: "none", borderRadius: 12, padding: "12px", fontSize: 14, fontWeight: 800, cursor: claimPhoto ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                          {claimSubmitting ? "Submitting..." : "Submit Claim"}
                        </button>
                        <button onClick={() => { setClaimModal(null); setClaimPhoto(null); }}
                          style={{ background: "transparent", border: "1px solid var(--bz-line-2)", color: "var(--bz-ink-2)", borderRadius: 12, padding: "12px 20px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Admin: Record Hit modal (record who pulled it, no ownership needed) */}
            {recordModal && recordModal.type==="super" && (
              <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }} onClick={()=>setRecordModal(null)}>
                <div style={{ background:"var(--bz-s1)", border:"2px solid #F59E0B", borderRadius:20, padding:28, maxWidth:420, width:"100%" }} onClick={e=>e.stopPropagation()}>
                  <div style={{ fontSize:18, fontWeight:900, color:"#F59E0B", marginBottom:4 }}>🎯 Record Super Hit</div>
                  <div style={{ fontSize:13, color:"var(--bz-ink-2)", marginBottom:18 }}>{recordModal.card.hero} #{recordModal.card.cardNum} · {recordModal.card.setName}</div>
                  <div style={{ fontSize:12, color:"#a0a0a0", marginBottom:14, lineHeight:1.6 }}>You're recording this hit as an admin. It goes straight into the tracker as verified — it does NOT add the card to your collection.</div>
                  <label style={{ display:"block", fontSize:12, fontWeight:700, color:"#aaa", marginBottom:6 }}>Who pulled it? (name or Whatnot handle)</label>
                  <input autoFocus value={recordName} onChange={e=>setRecordName(e.target.value)} placeholder="e.g. @collector_mike"
                    style={{ width:"100%", background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"11px 13px", fontSize:14, color:"#fff", fontFamily:"inherit", marginBottom:18, boxSizing:"border-box" }}/>
                  <div style={{ display:"flex", gap:10 }}>
                    <button onClick={async()=>{ if(!recordName.trim())return; const c=recordModal.card; try{ await setDoc(doc(db,"super_claims",c.id),{ cardId:c.id, cardName:c.hero, cardNum:c.cardNum, setName:c.setName||"", cardImage:c.imageUrl||null, userId:null, userName:recordName.trim(), recordedByAdmin:user?.displayName||user?.email||"Admin", photoUrl:null, status:"verified", createdAt:new Date().toISOString(), reviewedAt:new Date().toISOString() },{merge:true}); setRecordModal(null); setRecordName(""); }catch(e){ alert("Failed: "+e.message); } }}
                      disabled={!recordName.trim()} style={{ flex:1, background:recordName.trim()?"linear-gradient(135deg,#F59E0B,#FBBF24)":"var(--bz-s2)", color:recordName.trim()?"#000":"#555", border:"none", borderRadius:12, padding:"12px", fontSize:14, fontWeight:800, cursor:recordName.trim()?"pointer":"not-allowed", fontFamily:"inherit" }}>🏆 Record Hit</button>
                    <button onClick={()=>{ setRecordModal(null); setRecordName(""); }} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:12, padding:"12px 20px", fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Admin verification queue */}
            {isAdmin && pendingClaims.length > 0 && (
              <div style={{ background: "#1a1400", border: "2px solid #F59E0B44", borderRadius: 14, padding: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#F59E0B", marginBottom: 12 }}>🔍 Pending Verification ({pendingClaims.length})</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {pendingClaims.map(cl => (
                    <div key={cl.id} style={{ background: "var(--bz-s1)", border: "1px solid var(--bz-line-2)", borderRadius: 10, padding: 14, display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
                      <img src={cl.photoUrl} alt="proof" style={{ width: 80, height: 107, objectFit: "cover", borderRadius: 8, flexShrink: 0, cursor: "pointer" }} onClick={() => window.open(cl.photoUrl, "_blank")}/>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: "var(--bz-ink)", marginBottom: 4 }}>{cl.cardName} #{cl.cardNum}</div>
                        <div style={{ fontSize: 11, color: "var(--bz-ink-2)", marginBottom: 4 }}>{cl.setName} · claimed by <strong style={{ color: "#F59E0B" }}>{cl.userName}</strong></div>
                        <div style={{ fontSize: 10, color: "var(--bz-ink-3)" }}>{new Date(cl.createdAt).toLocaleDateString()}</div>
                      </div>
                      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                        <button onClick={() => adminVerify(cl.id, true)}
                          style={{ background: "#0a1a0a", border: "1px solid #4ade80", color: "#4ade80", borderRadius: 8, padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>✅ Verify</button>
                        <button onClick={() => { const reason = prompt("Reason for denial (optional):"); adminVerify(cl.id, false, reason || ""); }}
                          style={{ background: "#1a0a0a", border: "1px solid #E8317A", color: "#E8317A", borderRadius: 8, padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>❌ Deny</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overall community stat */}
            <div style={{ background: "var(--bz-s1)", border: "1px solid var(--bz-line-2)", borderRadius: 14, padding: "20px 24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#F59E0B", textTransform: "uppercase", letterSpacing: 2, marginBottom: 4 }}>⭐ Community Super Foil Hunt</div>
                  <div style={{ fontSize: 13, color: "var(--bz-ink-3)" }}>{totalSupers} total Super Foils across all sets · 1/1 cards</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 28, fontWeight: 900, color: "#F59E0B" }}>{verifiedCount}<span style={{ fontSize: 14, color: "var(--bz-ink-3)", fontWeight: 400 }}>/{totalSupers}</span></div>
                  <div style={{ fontSize: 11, color: "var(--bz-ink-3)" }}>found & verified</div>
                </div>
              </div>
              <div style={{ height: 10, background: "var(--bz-s2)", borderRadius: 5, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${totalSupers > 0 ? (verifiedCount / totalSupers * 100) : 0}%`, background: "linear-gradient(90deg, #F59E0B, #FBBF24, #FDE68A, #FBBF24, #F59E0B)", backgroundSize: "200% 100%", animation: "gradientShift 3s ease infinite", borderRadius: 5, transition: "width 0.5s ease" }}/>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                <span style={{ fontSize: 11, color: "var(--bz-ink-3)" }}>0</span>
                <span style={{ fontSize: 11, color: "#F59E0B", fontWeight: 700 }}>{totalSupers > 0 ? (verifiedCount / totalSupers * 100).toFixed(1) : 0}% found</span>
                <span style={{ fontSize: 11, color: "var(--bz-ink-3)" }}>{totalSupers}</span>
              </div>
            </div>

            {/* Per-set trackers */}
            {superSets.map(setName => {
              const setSuperCards = superCards.filter(c => c.setName === setName);
              const setVerified = setSuperCards.filter(c => claimMap[c.id]?.status === "verified");
              const setPending  = setSuperCards.filter(c => claimMap[c.id]?.status === "pending");
              const setUnclaimed = setSuperCards.filter(c => !claimMap[c.id]);
              const verPct = setSuperCards.length > 0 ? (setVerified.length / setSuperCards.length * 100) : 0;
              const isFull = setVerified.length === setSuperCards.length;

              return (
                <div key={setName} style={{ background: "var(--bz-s1)", border: `1px solid ${isFull ? "#F59E0B44" : "var(--bz-line-2)"}`, borderRadius: 14, overflow: "hidden" }}>
                  {/* Set header */}
                  <div style={{ padding: "16px 20px", background: isFull ? "linear-gradient(135deg, #1a1200, #111)" : "var(--bz-s1)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: isFull ? "#F59E0B" : "var(--bz-ink)" }}>
                          {isFull ? "🏆 " : "⭐ "}{setName}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--bz-ink-3)", marginTop: 2 }}>
                          {setVerified.length} verified · {setPending.length} pending · {setUnclaimed.length} unclaimed
                        </div>
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: "#F59E0B" }}>
                        {setVerified.length}<span style={{ fontSize: 12, color: "var(--bz-ink-3)", fontWeight: 400 }}>/{setSuperCards.length}</span>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div style={{ height: 8, background: "var(--bz-s2)", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${verPct}%`, background: isFull ? "linear-gradient(90deg,#F59E0B,#FBBF24,#FDE68A,#FBBF24,#F59E0B)" : "linear-gradient(90deg,#F59E0B,#FBBF24)", backgroundSize: "200% 100%", animation: isFull ? "gradientShift 2s ease infinite" : "none", borderRadius: 4, transition: "width 0.5s ease" }}/>
                    </div>
                  </div>

                  {/* Card list */}
                  <div style={{ padding: "12px 20px 16px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
                    {setSuperCards.sort((a, b) => String(a.cardNum||"").localeCompare(String(b.cardNum||""), undefined, { numeric: true })).map(c => {
                      const claim = claimMap[c.id];
                      const isVerified = claim?.status === "verified";
                      const isPending  = claim?.status === "pending";
                      const isDenied   = claim?.status === "denied";
                      const myOwned    = owned?.[c.id];
                      const myClaim    = claim?.userId === user?.uid;

                      return (
                        <div key={c.id} style={{ background: isVerified ? "#1a1200" : "var(--bz-bg)", border: `1.5px solid ${isVerified ? "#F59E0B" : isPending ? "#F59E0B44" : "var(--bz-s2)"}`, borderRadius: 10, overflow: "hidden", position: "relative" }}>
                          {c.imageUrl && (
                            <div style={{ position: "relative" }}>
                              <img src={c.imageUrl} alt={c.hero} style={{ width: "100%", aspectRatio: "3/4", objectFit: "cover", display: "block", opacity: isVerified ? 1 : 0.6, filter: isVerified ? "none" : "grayscale(30%)" }}/>
                              {/* Status badge */}
                              {isVerified && <div style={{ position: "absolute", top: 6, right: 6, background: "#F59E0B", color: "#000", borderRadius: 20, padding: "3px 10px", fontSize: 10, fontWeight: 800 }}>✅ FOUND</div>}
                              {isPending  && <div style={{ position: "absolute", top: 6, right: 6, background: "#1a1400", border: "1px solid #F59E0B", color: "#F59E0B", borderRadius: 20, padding: "3px 10px", fontSize: 10, fontWeight: 800 }}>⏳ PENDING</div>}
                              {!isVerified && !isPending && <div style={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.7)", color: "var(--bz-ink-3)", borderRadius: 20, padding: "3px 10px", fontSize: 10, fontWeight: 700 }}>UNCLAIMED</div>}
                            </div>
                          )}
                          <div style={{ padding: "10px 12px" }}>
                            <div style={{ fontSize: 13, fontWeight: 800, color: isVerified ? "#F59E0B" : "var(--bz-ink)", marginBottom: 2 }}>{c.hero}</div>
                            <div style={{ fontSize: 10, color: "var(--bz-ink-3)", marginBottom: 6 }}>#{c.cardNum} · 1/1 Super</div>
                            {/* Owner display */}
                            {isVerified && (
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#F59E0B", marginBottom: 6 }}>🏆 {claim.userName}</div>
                            )}
                            {isPending && (
                              <div style={{ fontSize: 11, color: "var(--bz-ink-2)", marginBottom: 6 }}>⏳ {claim.userName} — awaiting review</div>
                            )}
                            {isDenied && myClaim && (
                              <div style={{ fontSize: 11, color: "#E8317A", marginBottom: 6 }}>❌ Claim denied{claim.denialReason ? `: ${claim.denialReason}` : ""}</div>
                            )}
                            {/* Claim button — show if owned, not yet claimed or was denied */}
                            {user && myOwned && !isVerified && !isPending && (
                              <button onClick={() => { setClaimModal(c); setClaimPhoto(null); setClaimSent(false); }}
                                style={{ width: "100%", background: "linear-gradient(135deg,#F59E0B,#FBBF24)", color: "#000", border: "none", borderRadius: 8, padding: "7px 0", fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                                ⭐ Claim This Super
                              </button>
                            )}
                            {/* Admin: record a hit for whoever pulled it (no ownership needed) */}
                            {isAdmin && !isVerified && (
                              <button onClick={() => { setRecordModal({ card: c, type: "super" }); setRecordName(""); }}
                                style={{ width: "100%", marginTop: myOwned&&!isVerified&&!isPending?6:0, background: "rgba(245,158,11,0.12)", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 8, padding: "7px 0", fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                                🎯 {isPending ? "Verify / Record Hit" : "Record Hit (Admin)"}
                              </button>
                            )}
                            {!c.imageUrl && (
                              <div style={{ fontSize: 12, color: "var(--bz-ink-2)", marginBottom: 4 }}>{c.hero} #{c.cardNum}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {superCards.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 0", color: "var(--bz-ink-3)" }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>⭐</div>
                <div style={{ fontSize: 14 }}>No Super Foil cards found in the checklist yet.</div>
                <div style={{ fontSize: 12, marginTop: 8, color: "var(--bz-ink-3)" }}>Import a set CSV with weapon="Super" to get started.</div>
              </div>
            )}
          </div>
        );
      })()}

      {viewMode === "1of1" && (() => {
        const secret1of1Cards = cards.filter(c => c.notation === "Secret 1/1");
        const sets1of1 = [...new Set(secret1of1Cards.map(c=>c.setName).filter(Boolean))].sort();

        const claimMap1 = {};
        oneOfOneClaims.forEach(cl => { claimMap1[cl.cardId] = cl; });

        async function submitOneClaim(card, photoBase64, story, date, name) {
          setOneSubmitting(true);
          try {
            const storageRef2 = ref(storage, `oneof1_claims/${card.id}_${Date.now()}.jpg`);
            const byteStr = atob(photoBase64.split(",")[1]);
            const ab = new ArrayBuffer(byteStr.length);
            const ia = new Uint8Array(ab);
            for (let i=0; i<byteStr.length; i++) ia[i] = byteStr.charCodeAt(i);
            await uploadBytes(storageRef2, new Blob([ab],{type:"image/jpeg"}));
            const photoUrl = await getDownloadURL(storageRef2);
            await setDoc(doc(db,"oneof1_claims",card.id), {
              cardId:card.id, cardName:card.hero, cardNum:card.cardNum,
              setName:card.setName||"", treatment:card.treatment||"",
              cardImage:card.imageUrl||null, photoUrl,
              story:story||"", dateHit:date||"",
              submitterName:name||"Anonymous",
              userId:user?.uid||"anon",
              status:"pending", createdAt:new Date().toISOString(),
            });
            setOneSent(true);
          } catch(e) { alert("Upload failed: "+e.message); }
          setOneSubmitting(false);
        }

        async function adminVerifyOne(claimId, approve) {
          await setDoc(doc(db,"oneof1_claims",claimId), {
            status:approve?"verified":"denied",
            reviewedAt:new Date().toISOString(),
            reviewedBy:user?.displayName||"Admin",
          }, {merge:true});
        }

        const totalCount   = secret1of1Cards.length;
        const verifiedCount1 = oneOfOneClaims.filter(c=>c.status==="verified").length;
        const pendingClaims1 = oneOfOneClaims.filter(c=>c.status==="pending");

        return (
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

            {/* Admin: Record 1/1 Hit modal */}
            {recordModal && recordModal.type==="oneof1" && (
              <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }} onClick={()=>setRecordModal(null)}>
                <div style={{ background:"var(--bz-s1)", border:"2px solid #9333EA", borderRadius:20, padding:28, maxWidth:420, width:"100%" }} onClick={e=>e.stopPropagation()}>
                  <div style={{ fontSize:18, fontWeight:900, color:"#C084FC", marginBottom:4 }}>🎯 Record 1/1 Hit</div>
                  <div style={{ fontSize:13, color:"var(--bz-ink-2)", marginBottom:18 }}>{recordModal.card.hero} #{recordModal.card.cardNum} · {recordModal.card.treatment}</div>
                  <div style={{ fontSize:12, color:"#a0a0a0", marginBottom:14, lineHeight:1.6 }}>You're recording this hit as an admin. It goes straight into the tracker as verified — it does NOT add the card to your collection.</div>
                  <label style={{ display:"block", fontSize:12, fontWeight:700, color:"#aaa", marginBottom:6 }}>Who pulled it? (name or Whatnot handle)</label>
                  <input autoFocus value={recordName} onChange={e=>setRecordName(e.target.value)} placeholder="e.g. @collector_mike"
                    style={{ width:"100%", background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"11px 13px", fontSize:14, color:"#fff", fontFamily:"inherit", marginBottom:18, boxSizing:"border-box" }}/>
                  <div style={{ display:"flex", gap:10 }}>
                    <button onClick={async()=>{ if(!recordName.trim())return; const c=recordModal.card; try{ await setDoc(doc(db,"oneof1_claims",c.id),{ cardId:c.id, cardName:c.hero, cardNum:c.cardNum, setName:c.setName||"", treatment:c.treatment||"", cardImage:c.imageUrl||null, userId:null, submitterName:recordName.trim(), recordedByAdmin:user?.displayName||user?.email||"Admin", photoUrl:null, status:"verified", dateHit:new Date().toLocaleDateString(), createdAt:new Date().toISOString(), reviewedAt:new Date().toISOString() },{merge:true}); setRecordModal(null); setRecordName(""); }catch(e){ alert("Failed: "+e.message); } }}
                      disabled={!recordName.trim()} style={{ flex:1, background:recordName.trim()?"linear-gradient(135deg,#9333EA,#C084FC)":"var(--bz-s2)", color:recordName.trim()?"#fff":"#555", border:"none", borderRadius:12, padding:"12px", fontSize:14, fontWeight:800, cursor:recordName.trim()?"pointer":"not-allowed", fontFamily:"inherit" }}>🏆 Record Hit</button>
                    <button onClick={()=>{ setRecordModal(null); setRecordName(""); }} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:12, padding:"12px 20px", fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Claim modal */}
            {oneModal && (
              <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}
                onClick={()=>{ if(!oneSubmitting){ setOneModal(null); setOnePhoto(null); setOneStory(""); setOneDate(""); setOneName(""); setAdminClaimMode(false); setOneSent(false); setOneEditMode(false); }}}>
                <div style={{ background:"var(--bz-s1)", border:"2px solid #9333EA", borderRadius:20, padding:28, maxWidth:460, width:"100%", maxHeight:"90vh", overflowY:"auto" }} onClick={e=>e.stopPropagation()}>
                  {oneSent ? (
                    <div style={{ textAlign:"center", padding:"20px 0" }}>
                      <div style={{ fontSize:52, marginBottom:16 }}>💎</div>
                      <div style={{ fontSize:20, fontWeight:900, color:"#9333EA", marginBottom:8 }}>Claim Submitted!</div>
                      <div style={{ fontSize:13, color:"var(--bz-ink-2)", marginBottom:24 }}>Your Secret 1/1 claim is pending verification. Congrats on the hit!</div>
                      <button onClick={()=>{ setOneModal(null); setOnePhoto(null); setOneStory(""); setOneDate(""); setOneName(""); setAdminClaimMode(false); setOneSent(false); setOneEditMode(false); }}
                        style={{ background:"#9333EA", color:"#000", border:"none", borderRadius:12, padding:"12px 32px", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>Done</button>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize:18, fontWeight:900, color:"#9333EA", marginBottom:4 }}>💎 Claim Secret 1/1</div>
                      <div style={{ fontSize:13, color:"var(--bz-ink-2)", marginBottom:20 }}>{oneModal.hero} #{oneModal.cardNum} · {oneModal.treatment}</div>

                      {/* Photo */}
                      <label style={{ display:"block", marginBottom:14 }}>
                        <div style={{ background:onePhoto?"#0a0a1a":"var(--bz-bg)", border:`2px dashed ${onePhoto?"#9333EA":"var(--bz-line-2)"}`, borderRadius:12, padding:20, textAlign:"center", cursor:"pointer" }}>
                          {onePhoto
                            ? <img src={onePhoto} alt="" style={{ maxHeight:180, maxWidth:"100%", borderRadius:8, objectFit:"contain" }}/>
                            : <><div style={{ fontSize:32, marginBottom:8 }}>📸</div>
                               <div style={{ fontSize:13, fontWeight:700, color:"#9333EA" }}>Tap to upload photo</div>
                               <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:4 }}>Clear photo of your card</div></>
                          }
                        </div>
                        <input type="file" accept="image/*" capture="environment" style={{ display:"none" }}
                          onChange={e=>{ const f=e.target.files?.[0]; if(!f)return; const r2=new FileReader(); r2.onload=ev=>setOnePhoto(ev.target.result); r2.readAsDataURL(f); e.target.value=""; }}/>
                      </label>

                      {/* Date */}
                      <div style={{ marginBottom:14 }}>
                        <label style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", display:"block", marginBottom:6 }}>Date You Hit It</label>
                        <input type="date" value={oneDate} onChange={e=>setOneDate(e.target.value)}
                          style={{ ...S.inp, fontSize:13, width:"100%", boxSizing:"border-box" }}/>
                      </div>

                      {/* Story */}
                      <div style={{ marginBottom:14 }}>
                        <label style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", display:"block", marginBottom:6 }}>
                          The Story <span style={{ color:"var(--bz-ink-3)", fontWeight:400 }}>(optional)</span>
                        </label>
                        <textarea value={oneStory} onChange={e=>setOneStory(e.target.value)} rows={4}
                          placeholder="Cards are about creating moments of joy… tell us the story about this hit ✨"
                          style={{ ...S.inp, fontSize:12, width:"100%", boxSizing:"border-box", resize:"vertical", lineHeight:1.6 }}/>
                      </div>

                      {/* Name */}
                      <div style={{ marginBottom:20 }}>
                        <label style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", display:"block", marginBottom:6 }}>
                          Your Name <span style={{ color:"var(--bz-ink-3)", fontWeight:400 }}>(leave blank to stay anonymous)</span>
                        </label>
                        <input value={oneName} onChange={e=>setOneName(e.target.value)} placeholder="Anonymous"
                          style={{ ...S.inp, fontSize:13, width:"100%", boxSizing:"border-box" }}/>
                      </div>

                      <div style={{ display:"flex", gap:10 }}>
                        <button onClick={()=>{ if(onePhoto) submitOneClaim(oneModal, onePhoto, oneStory, oneDate, oneName); }}
                          disabled={!onePhoto||oneSubmitting}
                          style={{ flex:1, background:onePhoto?"#9333EA":"var(--bz-s2)", color:onePhoto?"#000":"#555", border:"none", borderRadius:12, padding:14, fontSize:14, fontWeight:800, cursor:onePhoto?"pointer":"not-allowed", fontFamily:"inherit" }}>
                          {oneSubmitting?"Submitting...":"Submit Claim"}
                        </button>
                        <button onClick={()=>{ setOneModal(null); setOnePhoto(null); setOneStory(""); setOneDate(""); setOneName(""); setAdminClaimMode(false); }}
                          style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:12, padding:"14px 20px", fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Global header */}
            <div style={{ background:"linear-gradient(135deg,#1a0a2e,var(--bz-s1))", border:"1px solid rgba(245,158,11,0.2)", borderRadius:16, padding:"20px 24px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:"#9333EA", textTransform:"uppercase", letterSpacing:2, marginBottom:4 }}>💎 Community Secret 1/1 Hunt</div>
                  <div style={{ fontSize:13, color:"var(--bz-ink-3)" }}>{totalCount} Secret 1/1s across all sets</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:28, fontWeight:900, color:"#9333EA" }}>{verifiedCount1}<span style={{ fontSize:14, color:"var(--bz-ink-3)", fontWeight:400 }}>/{totalCount}</span></div>
                  <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>found & verified</div>
                </div>
              </div>
              <div style={{ height:10, background:"var(--bz-s2)", borderRadius:5, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${totalCount>0?(verifiedCount1/totalCount*100):0}%`, background:"linear-gradient(90deg,#6B21A8,#9333EA,#C084FC,#E879F9,#A855F7,#7C3AED,#6B21A8)", backgroundSize:"200% 100%", animation:"gradientShift 3s ease infinite", borderRadius:5, transition:"width 0.5s ease" }}/>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}>
                <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>0</span>
                <span style={{ fontSize:11, color:"#9333EA", fontWeight:700 }}>{totalCount>0?(verifiedCount1/totalCount*100).toFixed(1):0}% found</span>
                <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{totalCount}</span>
              </div>
            </div>

            {/* Admin pending review */}
            {isAdmin && pendingClaims1.length>0 && (
              <div style={{ ...S.card }}>
                <SectionLabel t="⏳ Pending Review"/>
                {pendingClaims1.map(cl=>(
                  <div key={cl.id} style={{ display:"flex", gap:12, alignItems:"flex-start", padding:"12px 0", borderBottom:"1px solid var(--bz-line)" }}>
                    {cl.photoUrl && <img src={cl.photoUrl} alt="" style={{ width:60, height:80, objectFit:"cover", borderRadius:8, flexShrink:0 }}/>}
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)" }}>{cl.cardName} #{cl.cardNum}</div>
                      <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{cl.treatment} · {cl.setName}</div>
                      <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginTop:4 }}>by {cl.submitterName||"Anonymous"}{cl.dateHit?` · ${cl.dateHit}`:""}</div>
                      {cl.story && <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginTop:6, fontStyle:"italic" }}>"{cl.story}"</div>}
                      <div style={{ display:"flex", gap:8, marginTop:8 }}>
                        <button onClick={()=>adminVerifyOne(cl.id,true)} style={{ background:"rgba(74,222,128,0.15)", border:"1px solid rgba(74,222,128,0.3)", color:"#4ade80", borderRadius:7, padding:"4px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>✅ Verify</button>
                        <button onClick={()=>adminVerifyOne(cl.id,false)} style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)", color:"#EF4444", borderRadius:7, padding:"4px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>✕ Deny</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Per-set grids */}
            {sets1of1.map(setName => {
              const setCards = secret1of1Cards.filter(c=>c.setName===setName);
              const setVerified = setCards.filter(c=>claimMap1[c.id]?.status==="verified");
              const setPending  = setCards.filter(c=>claimMap1[c.id]?.status==="pending");
              const setUnclaimed = setCards.filter(c=>!claimMap1[c.id]);
              const verPct = setCards.length>0?(setVerified.length/setCards.length*100):0;
              const isFull = setVerified.length===setCards.length && setCards.length>0;

              return (
                <div key={setName} style={{ background:"var(--bz-s1)", border:`1px solid ${isFull?"#9333EA44":"var(--bz-line-2)"}`, borderRadius:14, overflow:"hidden" }}>
                  <div style={{ padding:"16px 20px", background:isFull?"linear-gradient(135deg,#1a0a2e,var(--bz-s1))":"var(--bz-s1)" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                      <div>
                        <div style={{ fontSize:14, fontWeight:800, color:isFull?"#9333EA":"var(--bz-ink)" }}>{isFull?"🏆 ":"💎 "}{setName}</div>
                        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>{setVerified.length} verified · {setPending.length} pending · {setUnclaimed.length} unclaimed</div>
                      </div>
                      <div style={{ fontSize:22, fontWeight:900, color:"#9333EA" }}>{setVerified.length}<span style={{ fontSize:12, color:"var(--bz-ink-3)", fontWeight:400 }}>/{setCards.length}</span></div>
                    </div>
                    <div style={{ height:8, background:"var(--bz-s2)", borderRadius:4, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${verPct}%`, background:isFull?"linear-gradient(90deg,#6B21A8,#9333EA,#C084FC,#E879F9,#A855F7,#7C3AED,#6B21A8)":"linear-gradient(90deg,#7C3AED,#A855F7,#C084FC)", backgroundSize:"200% 100%", animation:isFull?"gradientShift 2s ease infinite":"none", borderRadius:4, transition:"width 0.5s ease" }}/>
                    </div>
                  </div>

                  <div style={{ padding:"12px 20px 16px", display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:10 }}>
                    {setCards.sort((a,b)=>String(a.cardNum||"").localeCompare(String(b.cardNum||""),undefined,{numeric:true})).map(c=>{
                      const claim = claimMap1[c.id];
                      const isVerified = claim?.status==="verified";
                      const isPending  = claim?.status==="pending";
                      const myOwned    = owned?.[c.id];

                      return (
                        <div key={c.id} style={{ background:isVerified?"#1a0a2e":"var(--bz-bg)", border:`1.5px solid ${isVerified?"#9333EA":isPending?"#9333EA44":"var(--bz-s2)"}`, borderRadius:10, overflow:"hidden", position:"relative" }}>
                          {c.imageUrl && (
                            <div style={{ position:"relative" }}>
                              <img src={c.imageUrl} alt={c.hero} style={{ width:"100%", aspectRatio:"3/4", objectFit:"cover", display:"block", opacity:isVerified?1:0.5, filter:isVerified?"none":"grayscale(40%)" }}/>
                              {isVerified && <div style={{ position:"absolute", top:6, right:6, background:"#9333EA", color:"#000", borderRadius:20, padding:"3px 10px", fontSize:10, fontWeight:800 }}>✅ FOUND</div>}
                              {isPending  && <div style={{ position:"absolute", top:6, right:6, background:"#1a0a2e", border:"1px solid #9333EA", color:"#9333EA", borderRadius:20, padding:"3px 10px", fontSize:10, fontWeight:800 }}>⏳ PENDING</div>}
                              {!isVerified&&!isPending && <div style={{ position:"absolute", top:6, right:6, background:"rgba(0,0,0,0.7)", color:"var(--bz-ink-3)", borderRadius:20, padding:"3px 10px", fontSize:10, fontWeight:700 }}>UNCLAIMED</div>}
                            </div>
                          )}
                          <div style={{ padding:"10px 12px" }}>
                            <div style={{ fontSize:13, fontWeight:800, color:isVerified?"#9333EA":"var(--bz-ink)", marginBottom:2 }}>{c.hero}</div>
                            <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginBottom:4 }}>#{c.cardNum} · {c.treatment}</div>
                            {isVerified && (
                              <>
                                <div style={{ fontSize:11, fontWeight:700, color:"#9333EA", marginBottom:4 }}>🏆 {claim.submitterName||"Anonymous"}</div>
                                {claim.story && <div style={{ fontSize:10, color:"var(--bz-ink-2)", fontStyle:"italic", lineHeight:1.5, marginBottom:4 }}>"{claim.story}"</div>}
                                {claim.dateHit && <div style={{ fontSize:10, color:"#8a8a8a" }}>{claim.dateHit}</div>}
                              </>
                            )}
                            {isPending && <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginBottom:6 }}>⏳ {claim.submitterName||"Anonymous"} — awaiting review</div>}
                            {user && myOwned && !isVerified && !isPending && (
                              <button onClick={()=>{ setOneModal(c); setOnePhoto(null); setOneSent(false); setOneStory(""); setOneDate(""); setOneName(""); }}
                                style={{ width:"100%", background:"linear-gradient(135deg,#9333EA,#C084FC)", color:"#000", border:"none", borderRadius:8, padding:"7px 0", fontSize:11, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
                                💎 Claim This 1/1
                              </button>
                            )}
                            {isAdmin && !isVerified && (
                              <button onClick={()=>{ setRecordModal({ card:c, type:"oneof1" }); setRecordName(""); }}
                                style={{ width:"100%", marginTop:(user&&myOwned&&!isVerified&&!isPending)?6:0, background:"rgba(147,51,234,0.12)", color:"#C084FC", border:"1px solid rgba(147,51,234,0.4)", borderRadius:8, padding:"7px 0", fontSize:11, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
                                🎯 {isPending ? "Verify / Record Hit" : "Record Hit (Admin)"}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {secret1of1Cards.length===0 && (
              <div style={{ textAlign:"center", padding:"60px 0", color:"var(--bz-ink-3)" }}>
                <div style={{ fontSize:32, marginBottom:12 }}>💎</div>
                <div style={{ fontSize:14 }}>No Secret 1/1 cards found in the checklist.</div>
                <div style={{ fontSize:12, marginTop:8, color:"var(--bz-ink-3)" }}>Cards with notation "Secret 1/1" will appear here.</div>
              </div>
            )}
          </div>
        );
      })()}
      {viewMode === "stats" && !loading && cards.length > 0 && (() => {
        const haveList = cards.filter(c => (owned[c.id]||0) > 1);
        const missingCards = cards.filter(c => !owned[c.id]);

        // By treatment
        const byTreat = {};
        cards.forEach(c => {
          const t = c.treatment || "Uncategorized";
          if(!byTreat[t]) byTreat[t] = { total:0, owned:0 };
          byTreat[t].total++;
          if(owned[c.id]) byTreat[t].owned++;
        });
        const treatStats = Object.entries(byTreat)
          .map(([t,s]) => ({ t, ...s, pct:Math.round(s.owned/s.total*100), missing:s.total-s.owned }))
          .sort((a,b) => b.pct-a.pct);

        // By weapon
        const byWeapon = {};
        cards.forEach(c => {
          const w = c.weapon || "Unknown";
          if(!byWeapon[w]) byWeapon[w] = { total:0, owned:0 };
          byWeapon[w].total++;
          if(owned[c.id]) byWeapon[w].owned++;
        });
        const weaponStats = Object.entries(byWeapon)
          .map(([w,s]) => ({ w, ...s, pct:Math.round(s.owned/s.total*100) }))
          .sort((a,b) => b.pct-a.pct);

        // Per-set weapon stats (built separately so we can filter)
        const weaponFilteredCards = weaponSetFilter ? cards.filter(c => c.setName === weaponSetFilter) : cards;
        const byWeaponFiltered = {};
        weaponFilteredCards.forEach(c => {
          const w = c.weapon || "Unknown";
          if(!byWeaponFiltered[w]) byWeaponFiltered[w] = { total:0, owned:0 };
          byWeaponFiltered[w].total++;
          if(owned[c.id]) byWeaponFiltered[w].owned++;
        });
        const weaponStatsFiltered = Object.entries(byWeaponFiltered)
          .map(([w,s]) => ({ w, ...s, pct:Math.round(s.owned/s.total*100) }))
          .sort((a,b) => b.pct-a.pct);
        const availSets = [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort();

        // Heroes with zero cards
        const heroZero = [...new Set(cards.map(c=>c.hero))].filter(h => !cards.some(c=>c.hero===h && owned[c.id])).length;
        // Heroes complete
        const heroComplete = [...new Set(cards.map(c=>c.hero))].filter(h => cards.filter(c=>c.hero===h).every(c=>owned[c.id])).length;
        // Collection value estimate (power as proxy -- not real $)
        const collectionPower = cards.filter(c=>owned[c.id]).reduce((s,c)=>s+(parseFloat(c.power)||0)*( owned[c.id]||1),0);

        return (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {/* Summary cards */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:8 }}>
              {[
                { label:"Unique Owned", value:totalOwned.toLocaleString(), color:"#4ade80" },
                { label:"Total Copies", value:totalCollection.toLocaleString(), color:"#7B9CFF" },
                { label:"Still Missing", value:missingCards.length.toLocaleString(), color:"#E8317A" },
                { label:"Tradeable Extras", value:haveList.length.toLocaleString(), color:"#FBBF24" },
                { label:"Heroes Complete", value:heroComplete, color:"#4ade80" },
                { label:"Heroes Not Started", value:heroZero, color:"var(--bz-ink-3)" },
                { label:"On Want List", value:Object.keys(wantList).length, color:"#FBBF24" },
                { label:"Overall %", value:`${pct}%`, color:pct>75?"#4ade80":pct>40?"#FBBF24":"#E8317A" },
              ].map(({label,value,color})=>(
                <div key={label} style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:12, padding:"12px 14px" }}>
                  <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:4 }}>{label}</div>
                  <div style={{ fontSize:22, fontWeight:900, color }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Completion by Treatment */}
            <div style={{ ...S.card }}>
              <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)", marginBottom:10 }}>Completion by Treatment</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {treatStats.map(({t,total,owned:o,pct:p,missing})=>(
                  <div key={t} style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:11, color:p===100?"#4ade80":"var(--bz-ink-2)", minWidth:200, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p===100?"\uD83C\uDF08 ":""}{t}</span>
                    <div style={{ flex:1, height:5, background:"var(--bz-s2)", borderRadius:3, overflow:"hidden" }}>
                      <div className="bz-rainbow-fill" style={{ width:`${p}%`, height:"100%", borderRadius:3 }}/>
                    </div>
                    <span style={{ fontSize:11, fontWeight:700, color:p===100?"#4ade80":p>0?"#FBBF24":"#555", minWidth:80, textAlign:"right" }}>{o}/{total} ({p}%)</span>
                    {missing > 0 && <span style={{ fontSize:10, color:"#E8317A", minWidth:60, textAlign:"right" }}>-{missing}</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Completion by Weapon */}
            <div style={{ ...S.card }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10, flexWrap:"wrap", gap:8 }}>
                <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)" }}>Completion by Weapon</div>
                {availSets.length > 1 && (
                  <select value={weaponSetFilter} onChange={e=>setWeaponSetFilter(e.target.value)} style={{ ...S.inp, width:"auto", fontSize:11, padding:"4px 10px", cursor:"pointer" }}>
                    <option value="">All Sets</option>
                    {availSets.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                            {pr.chosen && (() => {
                              const ex = (pr.existingQty && pr.existingQty[pr.chosen]) || 0;
                              const add = parseInt(pr.qty) || 1;
                              const total = ex + add;
                              return (
                                <div style={{fontSize:10.5,marginTop:5,color: ex>0 ? "#FBBF24" : "#4ade80"}}>
                                  {ex>0
                                    ? `\u26A0\uFE0F Already have \u00d7${ex} on this card \u2014 healing makes it \u00d7${total} (${ex}+${add}). Uncheck if that would double-count.`
                                    : `\u2713 Restores \u00d7${add} on this card.`}
                                </div>
                              );
                            })()}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:8 }} className="boba-card-grid">
                {weaponStatsFiltered.map(({w,total,owned:o,pct:p})=>{
                  const wc = WEAPON_COLORS[w]||"#444";
                  return (
                    <div key={w} style={{ background:"var(--bz-bg)", borderRadius:8, padding:"10px 12px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                        <span style={{ fontSize:12, fontWeight:700, color:wc }}>{w}</span>
                        <span style={{ fontSize:11, color:p===100?"#4ade80":p>0?"#FBBF24":"#555", fontWeight:700 }}>{o}/{total}</span>
                      </div>
                      <div style={{ height:4, background:"var(--bz-s2)", borderRadius:2, overflow:"hidden" }}>
                        <div style={{ width:`${p}%`, height:"100%", background:wc, borderRadius:2 }}/>
                      </div>
                      <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:4, textAlign:"right" }}>{p}%</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tradeable extras */}
            {haveList.length > 0 && (
              <div style={{ ...S.card }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)" }}>{"\uD83D\uDD04 Tradeable Extras ("}{haveList.length} cards)</div>
                  <button onClick={exportHaveList} style={{ background:"#0a1a0a", border:"1px solid #4ade8044", color:"#4ade80", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDCE4 Export Have List"}</button>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:6 }}>
                  {haveList.sort((a,b)=>(owned[b.id]||0)-(owned[a.id]||0)).map(c=>{
                    const wc2 = WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
                    return (
                      <div key={c.id} style={{ background:"#0a1a0a", border:"1px solid #4ade8022", borderRadius:8, padding:"8px 12px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <div>
                          <div style={{ fontSize:12, fontWeight:800, color:"var(--bz-ink)" }}>{c.hero}</div>
                          <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>#{c.cardNum} · <span style={{color:wc2}}>{c.weapon}</span></div>
                        </div>
                        <div style={{ textAlign:"right" }}>
                          <div style={{ fontSize:14, fontWeight:900, color:"#4ade80" }}>{"\u00D7"}{owned[c.id]||0}</div>
                          <div style={{ fontSize:10, color:"#FBBF24" }}>+{(owned[c.id]||0)-1} extra</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Wants View */}
      {viewMode === "wants" && !loading && (() => {
        const wantedCards = cards.filter(c => wantList[c.id] && !owned[c.id]);
        const wantedOwned = cards.filter(c => wantList[c.id] && owned[c.id]);
        return (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)" }}>{"\uD83C\uDFAF Want List --"}{Object.keys(wantList).length} cards flagged</div>
              <div style={{ display:"flex", gap:8 }}>
                {wantedCards.length > 0 && <button onClick={exportWantList} style={{ background:"#1a0f00", border:"1px solid #FBBF2444", color:"#FBBF24", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDCE4 Export Want List"}</button>}
              </div>
            </div>
            {wantedCards.length === 0 && wantedOwned.length === 0 && (
              <div style={{ ...S.card, color:"var(--bz-ink-3)", fontSize:13, textAlign:"center", padding:32 }}>
                No cards on your want list yet. Click "+ Want" on any card to add it here.
              </div>
            )}
            {wantedCards.length > 0 && (
              <div style={{ ...S.card }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#FBBF24", marginBottom:8 }}>{"\uD83C\uDFAF Still Need ("}{wantedCards.length})</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:6 }}>
                  {wantedCards.map(c => {
                    const isOwned2 = !!owned[c.id];
                    return <BobaCard key={c.id} c={c} isOwned={isOwned2} ownedQty={owned[c.id]||0} flippedCard={flippedCard} setFlippedCard={setFlippedCard} toggleOwned={toggleOwned} setOwnedQty={setOwnedQty} toggleWant={toggleWant} wantList={wantList} WEAPON_COLORS={WEAPON_COLORS}/>;
                  })}
                </div>
              </div>
            )}
            {wantedOwned.length > 0 && (
              <div style={{ ...S.card }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#4ade80", marginBottom:8 }}>{"\u2705 Got It! ("}{wantedOwned.length})</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:6 }}>
                  {wantedOwned.map(c => {
                    const isOwned2 = !!owned[c.id];
                    return <BobaCard key={c.id} c={c} isOwned={isOwned2} ownedQty={owned[c.id]||0} flippedCard={flippedCard} setFlippedCard={setFlippedCard} toggleOwned={toggleOwned} setOwnedQty={setOwnedQty} toggleWant={toggleWant} wantList={wantList} WEAPON_COLORS={WEAPON_COLORS}/>;
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}


      {viewMode === "deck" && (() => {
        const ownedSet = new Set(Object.keys(owned));
        const deckSet  = new Set(deckCards);
        const inDeck   = cards.filter(c => deckSet.has(c.id));
        const empty    = DECK_SIZE - inDeck.length;

        // -- Deck rules --
        const isSpec        = deckType === "spec";
        const isApex        = deckType === "apex";
        const isApexMadness = deckType === "apexmadness";
        const hasRules      = isSpec || isApex || isApexMadness;

        // Duplicate check: same hero + variation + power + weapon = duplicate
        const dupKey = c => `${(c.hero||"").toLowerCase()}|${(c.variation||"").toLowerCase()}|${c.power||""}|${(c.weapon||"").toLowerCase()}`;
        const inDeckDupKeys = new Set(inDeck.map(dupKey));

        // Power level count: how many cards at each power value
        const powerCount = {};
        inDeck.forEach(c => { const p = c.power||"0"; powerCount[p] = (powerCount[p]||0)+1; });

        // Treatment count: how many 115-160 cards per treatment (for Apex Madness unlock)
        const treatCountCore = {};   // treatment - count of 115-160 cards in deck
        const treatCountApex = {};   // treatment - count of >160 cards in deck
        if (isApexMadness) {
          inDeck.forEach(c => {
            const t = (c.treatment||"Unknown").toLowerCase();
            const p = parseFloat(c.power||0);
            if (p >= 115 && p <= 160) treatCountCore[t] = (treatCountCore[t]||0) + 1;
            if (p > 160)              treatCountApex[t]  = (treatCountApex[t]||0)  + 1;
          });
        }

        // Validation errors for cards already in deck
        const deckViolations = hasRules ? inDeck.filter(c => {
          if ((isSpec || isApexMadness) && parseFloat(c.power||0) > 160) {
            // Apex Madness: >160 allowed only if treatment has 10 core cards
            if (isApexMadness) {
              const t = (c.treatment||"Unknown").toLowerCase();
              if ((treatCountCore[t]||0) < 10) return true;
            } else {
              return true;
            }
          }
          if ((powerCount[c.power||"0"]||0) > 6) return true;
          return false;
        }) : [];

        // Can a card be added?
        function canAdd(c) {
          if (deckSet.has(c.id)) return { ok:false, reason:"Already in deck" };
          if (inDeck.length >= DECK_SIZE) return { ok:false, reason:"Deck full" };
          if (hasRules && inDeckDupKeys.has(dupKey(c))) return { ok:false, reason:"Duplicate (same hero, pose, power & weapon)" };
          if (hasRules && (powerCount[c.power||"0"]||0) >= 6) return { ok:false, reason:`Already 6 cards at power ${c.power}` };
          const p = parseFloat(c.power||0);
          if (isSpec && p > 160) return { ok:false, reason:`Power ${c.power} exceeds 160 limit` };
          if (isApexMadness && p > 160) {
            const t = (c.treatment||"Unknown").toLowerCase();
            const coreCount = (treatCountCore[t]||0);
            if (coreCount < 10) return { ok:false, reason:`Need 10 core ${c.treatment} cards first (have ${coreCount}/10)` };
            const apexCount = (treatCountApex[t]||0);
            if (apexCount >= 1) return { ok:false, reason:`Already have 1 unlocked ${c.treatment} apex card` };
          }
          return { ok:true };
        }

        // Stats
        const totalPower  = inDeck.reduce((s,c)=>s+(parseFloat(c.power)||0),0);
        const weaponBreak = {};
        const heroCover   = new Set();
        inDeck.forEach(c => {
          const w = c.weapon||"Unknown";
          weaponBreak[w] = (weaponBreak[w]||0) + 1;
          if(c.hero) heroCover.add(c.hero);
        });
        const weaponEntries = Object.entries(weaponBreak).sort((a,b)=>b[1]-a[1]);

        // Card pool: all cards or owned only -- exclude plays (PL/BPL)
        const cardPool = (deckOwnedOnly ? cards.filter(c => ownedSet.has(c.id)) : cards)
          .filter(c => { const t=(c.treatment||"").toLowerCase(); return t!=="plays"&&t!=="bonus plays"&&t!=="home team discount"; });

        // Available cards to add
        const available = cardPool.filter(c => {
          if (deckSet.has(c.id)) return false;
          if (deckFilterWeap && c.weapon !== deckFilterWeap) return false;
          if (deckFilterHero && c.hero !== deckFilterHero) return false;
          if (deckFilterPowers.size > 0 && !deckFilterPowers.has(String(c.power||""))) return false;
          if (deckFilterSet && c.setName !== deckFilterSet) return false;
          if (deckFilterTreat && c.treatment !== deckFilterTreat) return false;
          if (deckSearch && !`${c.hero} ${c.cardNum} ${c.treatment}`.toLowerCase().includes(deckSearch.toLowerCase())) return false;
          return true;
        }).sort((a,b) => (parseFloat(b.power)||0)-(parseFloat(a.power)||0));

        const deckPowers   = [...new Set(cardPool.map(c=>c.power!=null&&c.power!==""?String(c.power):null).filter(Boolean))].sort((a,b)=>parseFloat(b)-parseFloat(a));
        const deckSets     = [...new Set(cardPool.map(c=>c.setName).filter(Boolean))].sort();
        const deckTreats   = [...new Set(cardPool.map(c=>c.treatment).filter(Boolean))].sort();
        const deckHeroes   = [...new Set(cardPool.map(c=>c.hero).filter(Boolean))].sort();
        const deckWeapons  = sortWeapons([...new Set(cardPool.map(c=>canonWeapon(c.weapon)).filter(Boolean))]);

        return (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

            {/* Header */}
            <div style={{ ...S.card, padding:"12px 16px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                <input value={deckName} onChange={e=>setDeckName(e.target.value)}
                  style={{ ...S.inp, fontSize:15, fontWeight:800, flex:1, minWidth:180 }}
                  placeholder="Deck name..."/>
                <select value={deckType} onChange={e=>setDeckType(e.target.value)}
                  style={{ ...S.inp, width:"auto", fontWeight:700, cursor:"pointer",
                    color: deckType==="spec"?"#FBBF24": deckType==="apex"?"#A855F7": deckType==="apexmadness"?"#E8317A":"#888" }}>
                  {Object.entries(DECK_FORMATS).map(([k,f])=><option key={k} value={k}>{f.label}</option>)}
                </select>
                <span style={{ fontSize:12, color: inDeck.length===DECK_SIZE?"#4ade80":inDeck.length>DECK_SIZE?"#E8317A":"#FBBF24", fontWeight:700 }}>
                  {inDeck.length}/{DECK_SIZE} cards
                </span>
                <button onClick={saveDeck} disabled={deckSaving||deckCards.length===0}
                  style={{ background:"#0a1a0a", border:"1px solid #4ade8044", color:"#4ade80", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  {deckSaving?"Saving...":"\uD83D\uDCBE Save Deck"}
                </button>
                <button onClick={newDeck}
                  style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"6px 12px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  + New Deck
                </button>
              </div>

              {/* Saved decks */}
              {savedDecks.length > 0 && (
                <div style={{ marginTop:10, display:"flex", gap:6, flexWrap:"wrap" }}>
                  {savedDecks.map(d=>(
                    <div key={d.id} style={{ display:"flex", alignItems:"center", gap:4, background:deckLoadId===d.id?"#1A1A2E":"var(--bz-s2)", border:`1px solid ${d.registered?"#4ade80":(deckLoadId===d.id?"#7B9CFF":"var(--bz-line-2)")}`, borderRadius:8, padding:"4px 10px" }}>
                      <button onClick={()=>toggleRegistered(d)} title={d.registered?"Registered — click to unmark":"Mark as registered"}
                        style={{ background:"none", border:"none", cursor:"pointer", fontSize:13, lineHeight:1, padding:"0 2px",
                                 color:d.registered?"#4ade80":"#555" }}>
                        {d.registered?"\u2713":"\u25CB"}
                      </button>
                      <button onClick={()=>loadDeck(d)} style={{ background:"none", border:"none", color:deckLoadId===d.id?"#7B9CFF":"#888", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                        {d.name} <span style={{ color:"var(--bz-ink-3)", fontWeight:400 }}>({d.cardCount})</span>
                      </button>
                      <button onClick={()=>deleteDeck(d.id)} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:13, lineHeight:1, padding:"0 2px" }}>{"\u00D7"}</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) clamp(280px,30%,360px)", gap:14, alignItems:"start" }}>

              {/* LEFT: Card picker */}
              <div style={{ display:"flex", flexDirection:"column", gap:10, minHeight:0 }}>
                {/* Filters */}
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                  {/* Owned only toggle */}
                  <div style={{ display:"flex", alignItems:"center", gap:6, background:"var(--bz-s2)", border:`1px solid ${deckOwnedOnly?"#4ade80":"var(--bz-line-2)"}`, borderRadius:8, padding:"5px 10px", cursor:"pointer" }}
                    onClick={()=>setDeckOwnedOnly(p=>!p)}>
                    <div style={{ width:28, height:16, borderRadius:8, background:deckOwnedOnly?"#4ade80":"#333", position:"relative", transition:"background 0.2s", flexShrink:0 }}>
                      <div style={{ position:"absolute", top:2, left:deckOwnedOnly?12:2, width:12, height:12, borderRadius:"50%", background:"#fff", transition:"left 0.2s" }}/>
                    </div>
                    <span style={{ fontSize:11, fontWeight:700, color:deckOwnedOnly?"#4ade80":"#666", whiteSpace:"nowrap" }}>
                      {deckOwnedOnly?"\u2705 Owned Only":"All Cards"}
                    </span>
                  </div>
                  <input value={deckSearch} onChange={e=>setDeckSearch(e.target.value)}
                    placeholder="Search hero, card #, treatment..."
                    style={{ ...S.inp, flex:1, minWidth:160 }}/>
                  <select value={deckFilterWeap} onChange={e=>setDeckFilterWeap(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
                    <option value="">All Weapons</option>
                    {deckWeapons.map(w=><option key={w} value={w}>{w}</option>)}
                  </select>
                  <select value={deckFilterSet} onChange={e=>setDeckFilterSet(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer", color:deckFilterSet?"#7B9CFF":"#888" }}>
                    <option value="">All Sets</option>
                    {deckSets.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                  <select value={deckFilterTreat} onChange={e=>setDeckFilterTreat(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer", color:deckFilterTreat?"#FBBF24":"#888" }}>
                    <option value="">All Treatments</option>
                    {deckTreats.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                  <select value={deckFilterHero} onChange={e=>setDeckFilterHero(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
                    <option value="">All Heroes</option>
                    {deckHeroes.map(h=><option key={h} value={h}>{h}</option>)}
                  </select>
                  <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{available.length} available</span>
                </div>
                {/* Multi-select power chips */}
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", paddingBottom:6 }}>
                  {deckPowers.map(p => {
                    const sel = deckFilterPowers.has(p);
                    const over = (isSpec||isApexMadness) && parseFloat(p)>160;
                    return (
                      <button key={p} onClick={()=>{
                        setDeckFilterPowers(prev => {
                          const next = new Set(prev);
                          if (next.has(p)) next.delete(p); else next.add(p);
                          return next;
                        });
                      }} style={{ background:sel?(over?"#E8317A":"#FBBF2433"):"var(--bz-s1)", border:`1px solid ${sel?(over?"#E8317A":"#FBBF24"):"var(--bz-line-2)"}`, color:sel?(over?"#E8317A":"#FBBF24"):"#555", borderRadius:6, padding:"3px 9px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                        {p}{over?" \u26A0":""}
                      </button>
                    );
                  })}
                  {deckFilterPowers.size > 0 && (
                    <button onClick={()=>setDeckFilterPowers(new Set())} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:6, padding:"3px 9px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>{"\u2715 Clear"}</button>
                  )}
                </div>

                {/* Available cards list */}
                <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, overflow:"hidden", minHeight:300, maxHeight:"calc(100vh - 280px)", overflowY:"auto" }}>
                  {available.length === 0 ? (
                    <div style={{ padding:"32px", textAlign:"center", color:"var(--bz-ink-3)", fontSize:13 }}>
                      {Object.keys(owned).length === 0 ? "No owned cards -- mark cards as owned in the Checklist first" : "No cards match your filters"}
                    </div>
                  ) : available.map((c,i) => {
                    const wc = WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
                    const full = inDeck.length >= DECK_SIZE;
                    return (
                      (() => {
                        const { ok, reason } = canAdd(c);
                        return (
                          <div key={c.id} onClick={()=>{ if(ok) setDeckCards(p=>[...p,c.id]); }}
                            style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", borderBottom:"1px solid #111", background:i%2===0?"var(--bz-bg)":"var(--bz-s1)", cursor:ok?"pointer":"not-allowed", opacity:ok?1:0.35 }}
                            className="inv-row"
                            title={!ok?reason:""}>
                            {c.imageUrl && <img src={c.imageUrl} alt={c.hero} style={{ width:36, height:48, objectFit:"cover", borderRadius:4, flexShrink:0 }}/>}
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)", lineHeight:1.2 }}>{c.hero}</div>
                              <div style={{ display:"flex", gap:6, marginTop:3, flexWrap:"wrap" }}>
                                <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>#{c.cardNum}</span>
                                {c.weapon && <span style={{ fontSize:10, color:wc, fontWeight:700 }}>{c.weapon}</span>}
                                {c.treatment && <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{c.treatment}</span>}
                                {!deckOwnedOnly && (
                                  <span style={{ fontSize:10, fontWeight:700, color:ownedSet.has(c.id)?"#4ade80":"#333" }}>
                                    {ownedSet.has(c.id)?"\u2713 owned":"not owned"}
                                  </span>
                                )}
                                {!ok && reason && <span style={{ fontSize:10, color:"#E8317A" }}>{reason}</span>}
                              </div>
                            </div>
                            {c.power && <div style={{ fontSize:16, fontWeight:900, color: isSpec&&parseFloat(c.power)>160?"#E8317A":wc, flexShrink:0 }}>{c.power}</div>}
                            {ok && <div style={{ fontSize:18, color:"#4ade80", flexShrink:0 }}>+</div>}
                          </div>
                        );
                      })()
                    );
                  })}
                </div>
              </div>

              {/* RIGHT: Deck + stats */}
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>

                  <div style={{ ...S.card }}>
                  <div style={{ fontSize:12, fontWeight:800, color:"var(--bz-ink)", marginBottom:10 }}>{"\u2694\uFE0F Deck Stats"}{deckType !== "none" && <span style={{ marginLeft:8, fontSize:10, color:deckType==="spec"?"#FBBF24":deckType==="apexmadness"?"#E8317A":deckType==="elite"?"#4ade80":deckType==="specplus"?"#38BDF8":deckType==="blast"?"#F472B6":"#A855F7", fontWeight:700 }}>
                      {fmtOf(deckType).short}
                    </span>}
                  </div>
                  {/* Rule violations */}
                  {deckViolations.length > 0 && (
                    <div style={{ background:"#1a0a0a", border:"1px solid #E8317A44", borderRadius:8, padding:"8px 10px", marginBottom:10 }}>
                      <div style={{ fontSize:10, color:"#E8317A", fontWeight:700, marginBottom:4 }}>{"\u26A0\uFE0F"}{deckViolations.length} Rule Violation{deckViolations.length!==1?"s":""}</div>
                      {deckViolations.slice(0,3).map(c=>(
                        <div key={c.id} style={{ fontSize:10, color:"var(--bz-ink-2)" }}>{"\u2022"}{c.hero} #{c.cardNum} ({c.power})</div>
                      ))}
                      {deckViolations.length > 3 && <div style={{ fontSize:10, color:"var(--bz-ink-3)" }}>...and {deckViolations.length-3} more</div>}
                    </div>
                  )}
                  {/* Rules summary */}
                  {hasRules && (
                    <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:8, padding:"8px 10px", marginBottom:10, fontSize:10, color:"var(--bz-ink-3)", lineHeight:1.8 }}>
                      {isSpec        && <div style={{ color:"#FBBF24" }}>{"\u26A1 Max power: 160"}</div>}
                      {isApexMadness && <div style={{ color:"#A855F7" }}>{"\u26A1 Core deck: 115-160 power only"}</div>}
                      {isApexMadness && <div style={{ color:"#A855F7" }}>{"\u26A1 10 core cards per treatment unlocks 1 apex card (&gt;160) of that treatment"}</div>}
                      <div>{"\u26A1 No duplicates (same hero + pose + power + weapon)"}</div>
                      <div>{"\u26A1 Max 6 cards per power level"}</div>
                    </div>
                  )}

                  {/* Apex Madness treatment unlock tracker */}
                  {isApexMadness && (
                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontSize:10, color:"#A855F7", fontWeight:700, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Treatment Unlocks</div>
                      {(() => {
                        const allTreats = [...new Set(inDeck.map(c=>(c.treatment||"Unknown")))].sort();
                        if (allTreats.length === 0) return <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>Add cards to see unlock progress</div>;
                        return allTreats.map(t => {
                          const tl = t.toLowerCase();
                          const core  = treatCountCore[tl] || 0;
                          const apex  = treatCountApex[tl]  || 0;
                          const unlocked = core >= 10;
                          const pct = Math.min(100, Math.round(core / 10 * 100));
                          return (
                            <div key={t} style={{ marginBottom:8 }}>
                              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                                <span style={{ fontSize:11, color: unlocked ? "#A855F7" : "#888", fontWeight: unlocked ? 700 : 400 }}>
                                  {unlocked ? "\uD83D\uDD13" : "\uD83D\uDD12"} {t}
                                </span>
                                <span style={{ fontSize:11, color: unlocked ? "#A855F7" : "#555" }}>
                                  {core}/10 core{unlocked ? ` · ${apex}/1 apex` : ""}
                                </span>
                              </div>
                              <div style={{ height:4, background:"var(--bz-s2)", borderRadius:2, overflow:"hidden" }}>
                                <div style={{ width:`${pct}%`, height:"100%", background: unlocked ? "#A855F7" : "#333", borderRadius:2, transition:"width 0.3s" }}/>
                              </div>
                            </div>
                          );
                        });
                      })()}
                      <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:6 }}>
                        {Object.values(treatCountCore).filter(v=>v>=10).length} of {[...new Set(inDeck.map(c=>c.treatment).filter(Boolean))].length} treatments unlocked
                      </div>
                    </div>
                  )}
                  {/* Power level breakdown */}
                  {hasRules && Object.keys(powerCount).length > 0 && (
                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontSize:10, color:"var(--bz-ink-3)", fontWeight:700, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Power Level Counts</div>
                      {Object.entries(powerCount).sort((a,b)=>parseFloat(b[0])-parseFloat(a[0])).map(([pwr,cnt])=>{
                        const over160 = parseFloat(pwr) > 160;
                        const bad = isSpec && over160;
                        return (
                          <div key={pwr} style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:2 }}>
                            <span style={{ color: bad ? "#E8317A" : over160 && isApexMadness ? "#A855F7" : "#888" }}>
                              Power {pwr}{over160 && isApexMadness ? " \uD83D\uDD13" : ""}
                            </span>
                            <span style={{ fontWeight:700, color: cnt>=6?"#E8317A":cnt>=4?"#FBBF24":"#4ade80" }}>{cnt}/6</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
                    {[
                      { l:"Cards", v:`${inDeck.length}/${DECK_SIZE}`, c:inDeck.length===DECK_SIZE?"#4ade80":"#FBBF24" },
                      { l:"Total Power", v:Math.round(totalPower).toLocaleString(), c:"#E8317A" },
                      { l:"Heroes", v:heroCover.size, c:"#7B9CFF" },
                      { l:"Avg Power", v:inDeck.length>0?Math.round(totalPower/inDeck.length):0, c:"#FBBF24" },
                    ].map(({l,v,c})=>(
                      <div key={l} style={{ background:"var(--bz-s1)", borderRadius:8, padding:"8px 10px", textAlign:"center" }}>
                        <div style={{ fontSize:18, fontWeight:900, color:c }}>{v}</div>
                        <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>{l}</div>
                      </div>
                    ))}
                  </div>

                  {/* Weapon breakdown */}
                  {weaponEntries.length > 0 && (
                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontSize:10, color:"var(--bz-ink-3)", fontWeight:700, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Weapon Balance</div>
                      {weaponEntries.map(([w,count])=>{
                        const wc = WEAPON_COLORS[w]||"#444";
                        const pct = Math.round(count/inDeck.length*100);
                        return (
                          <div key={w} style={{ marginBottom:5 }}>
                            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                              <span style={{ fontSize:11, color:wc, fontWeight:700 }}>{w}</span>
                              <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{count} ({pct}%)</span>
                            </div>
                            <div style={{ height:4, background:"var(--bz-s2)", borderRadius:2, overflow:"hidden" }}>
                              <div style={{ width:`${pct}%`, height:"100%", background:wc, borderRadius:2 }}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Hero coverage */}
                  {heroCover.size > 0 && (
                    <div>
                      <div style={{ fontSize:10, color:"var(--bz-ink-3)", fontWeight:700, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Heroes ({heroCover.size})</div>
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                        {[...heroCover].sort().map(h=>(
                          <span key={h} style={{ fontSize:10, background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:5, padding:"2px 7px", color:"var(--bz-ink-2)" }}>{h}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* 60-slot grid */}
                <div style={{ ...S.card }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10, flexWrap:"wrap", gap:8 }}>
                    <div style={{ fontSize:12, fontWeight:800, color:"var(--bz-ink)" }}>
                      Deck Slots -- {empty > 0 ? <span style={{ color:"#FBBF24" }}>{empty} empty</span> : <span style={{ color:"#4ade80" }}>{"Full! \u2705"}</span>}
                    </div>
                    <select value={deckSlotSort} onChange={e=>setDeckSlotSort(e.target.value)}
                      style={{ ...S.inp, width:"auto", fontSize:10, padding:"3px 8px", cursor:"pointer" }}>
                      <option value="added">Order Added</option>
                      <option value="power">{"Power \u2193"}</option>
                      <option value="name">{"Name A\u2192Z"}</option>
                      <option value="weapon">Weapon</option>
                    </select>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:4 }}>
                    {(() => {
                      const sorted = [...inDeck].sort((a,b) => {
                        if (deckSlotSort === "power")  return (parseFloat(b.power)||0)-(parseFloat(a.power)||0);
                        if (deckSlotSort === "name")   return (a.hero||"").localeCompare(b.hero||"");
                        if (deckSlotSort === "weapon") return (a.weapon||"").localeCompare(b.weapon||"");
                        return 0; // "added" = natural order
                      });
                      return Array.from({ length: DECK_SIZE }).map((_,i) => {
                        const c = sorted[i];
                      if (c) {
                        const wc = WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
                        return (
                          <div key={i} title={`${c.hero} -- ${c.weapon||""} ${c.power||""}`}
                            onClick={()=>setDeckCards(p=>p.filter(id=>id!==c.id))}
                            style={{ aspectRatio:"3/4", borderRadius:4, overflow:"hidden", position:"relative", cursor:"pointer", border:`1.5px solid ${wc}44`, background:"var(--bz-s2)" }}>
                            {c.imageUrl
                              ? <img src={c.imageUrl} alt={c.hero} loading="lazy" decoding="async" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
                              : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:7, color:wc, fontWeight:700, textAlign:"center", padding:2, lineHeight:1.2 }}>{c.hero?.split(" ")[0]}</div>
                            }
                            <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0)", transition:"background 0.15s" }} className="deck-slot-hover"/>
                          </div>
                        );
                      }
                      return (
                        <div key={i} style={{ aspectRatio:"3/4", borderRadius:4, border:"1px dashed var(--bz-s2)", background:"#080808", display:"flex", alignItems:"center", justifyContent:"center" }}>
                          <span style={{ fontSize:9, color:"#aaa", fontWeight:700 }}>{i+1}</span>
                        </div>
                      );
                    })})()}
                  </div>
                  {inDeck.length > 0 && (
                    <button onClick={()=>{ if(window.confirm("Clear all cards from deck?")) setDeckCards([]); }}
                      style={{ marginTop:10, background:"transparent", border:"1px solid #E8317A22", color:"#E8317A", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", width:"100%" }}>
                      {"\u2715 Clear Deck"}</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {viewMode === "playbook" && (() => {
        const ownedSet  = new Set(Object.keys(owned));
        const pbEntryIds = new Set(pbCards.map(e=>e.id));
        const playCount  = pbCards.filter(e=>e.type==="play").length;
        const bonusCount = pbCards.filter(e=>e.type==="bonus").length;
        const playFull   = playCount >= PLAY_LIMIT;

        // DBS tracking -- cap applies to plays + BPL combined
        const pbResolvedAll = pbCards.map(e=>({ ...e, card: cards.find(c=>c.id===e.id) })).filter(e=>e.card);
        const totalDbs   = pbResolvedAll.reduce((s,e)=>s+(parseFloat(e.card.dbs)||0), 0);
        const dbsLeft    = DBS_CAP - totalDbs;
        const dbsPct     = Math.min(totalDbs / DBS_CAP * 100, 100);
        const dbsOver    = totalDbs > DBS_CAP;

        // All Play cards: PL-xxx = regular plays, BPL-xxx = bonus plays
        const allPlays = cards.filter(c => { const t=(c.treatment||"").toLowerCase(); return t==="plays"||t==="bonus plays"||t==="home team discount"; });
        const isPlay  = c => { const t=(c.treatment||"").toLowerCase(); return t==="plays"||t==="home team discount"; };
        const isBonus = c => (c.treatment||"").toLowerCase()==="bonus plays";
        const pbSets   = [...new Set(allPlays.map(c=>c.setName).filter(Boolean))].sort();
        const pbPool   = pbOwnedOnly ? allPlays.filter(c=>ownedSet.has(c.id)) : allPlays;
        const available = pbPool.filter(c => {
          if (pbEntryIds.has(c.id)) return false;
          if (pbFilterSet && c.setName !== pbFilterSet) return false;
          if (pbTreatFilter && (c.treatment||"").toLowerCase() !== pbTreatFilter.toLowerCase()) return false;
          if (pbSearch && !`${c.hero} ${c.cardNum} ${c.treatment} ${c.playAbility||""}`.toLowerCase().includes(pbSearch.toLowerCase())) return false;
          return true;
        }).sort((a,b) => {
          if (pbSort === "dbs_desc") return (parseFloat(b.dbs)||0) - (parseFloat(a.dbs)||0);
          if (pbSort === "dbs_asc")  return (parseFloat(a.dbs)||0) - (parseFloat(b.dbs)||0);
          return (a.hero||"").localeCompare(b.hero||"");
        });

        // Cards currently in playbook (resolved)
        const pbResolved = pbCards.map(e => ({ ...e, card: cards.find(c=>c.id===e.id) })).filter(e=>e.card);

        return (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

            {/* Header */}
            <div style={{ ...S.card, padding:"12px 16px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                <input value={pbName} onChange={e=>setPbName(e.target.value)}
                  style={{ ...S.inp, fontSize:15, fontWeight:800, flex:1, minWidth:180 }}
                  placeholder="Playbook name..."/>
                <span style={{ fontSize:12, fontWeight:700 }}>
                  <span style={{ color:playFull?"#E8317A":"#4ade80" }}>{playCount}/{PLAY_LIMIT}</span>
                  <span style={{ color:"var(--bz-ink-3)" }}> plays</span>
                  {bonusCount > 0 && <span style={{ color:"#7B9CFF" }}> · {bonusCount} BPL</span>}
                </span>
                <button onClick={savePlaybook} disabled={pbSaving||pbCards.length===0}
                  style={{ background:"#0a1a0a", border:"1px solid #4ade8044", color:"#4ade80", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  {pbSaving?"Saving...":"\uD83D\uDCBE Save Playbook"}
                </button>
                <button onClick={newPlaybook}
                  style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"6px 12px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  + New
                </button>
              </div>

              {/* Saved playbooks */}
              {savedPlaybooks.length > 0 && (
                <div style={{ marginTop:10, display:"flex", gap:6, flexWrap:"wrap" }}>
                  {savedPlaybooks.map(pb=>(
                    <div key={pb.id} style={{ display:"flex", alignItems:"center", gap:4, background:pbLoadId===pb.id?"#1A1A2E":"var(--bz-s2)", border:`1px solid ${pbLoadId===pb.id?"#7B9CFF":"var(--bz-line-2)"}`, borderRadius:8, padding:"4px 10px" }}>
                      <button onClick={()=>loadPlaybook(pb)} style={{ background:"none", border:"none", color:pbLoadId===pb.id?"#7B9CFF":"#888", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                        {pb.name} <span style={{ color:"var(--bz-ink-3)", fontWeight:400 }}>({pb.playCount}P{pb.bonusCount>0?` +${pb.bonusCount}B`:""})</span>
                      </button>
                      <button onClick={()=>deletePlaybook(pb.id)} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:13, lineHeight:1, padding:"0 2px" }}>{"\u00D7"}</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) clamp(280px,30%,380px)", gap:14, alignItems:"start" }}>

              {/* LEFT: Play card picker */}
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                  {/* Owned toggle */}
                  <div style={{ display:"flex", alignItems:"center", gap:6, background:"var(--bz-s2)", border:`1px solid ${pbOwnedOnly?"#4ade80":"var(--bz-line-2)"}`, borderRadius:8, padding:"5px 10px", cursor:"pointer" }}
                    onClick={()=>setPbOwnedOnly(p=>!p)}>
                    <div style={{ width:28, height:16, borderRadius:8, background:pbOwnedOnly?"#4ade80":"#333", position:"relative", transition:"background 0.2s", flexShrink:0 }}>
                      <div style={{ position:"absolute", top:2, left:pbOwnedOnly?12:2, width:12, height:12, borderRadius:"50%", background:"#fff", transition:"left 0.2s" }}/>
                    </div>
                    <span style={{ fontSize:11, fontWeight:700, color:pbOwnedOnly?"#4ade80":"#666", whiteSpace:"nowrap" }}>
                      {pbOwnedOnly?"\u2705 Owned Only":"All Cards"}
                    </span>
                  </div>
                  <input value={pbSearch} onChange={e=>setPbSearch(e.target.value)}
                    placeholder="Search hero, play ability..."
                    style={{ ...S.inp, flex:1 }}/>
                  <select value={pbFilterSet} onChange={e=>setPbFilterSet(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
                    <option value="">All Sets</option>
                    {pbSets.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                  <select value={pbTreatFilter} onChange={e=>setPbTreatFilter(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
                    <option value="">All Types</option>
                    <option value="Plays">{"\u2694\uFE0F Plays"}</option>
                    <option value="Bonus Plays">{"\u2B50 Bonus Plays"}</option>
                    <option value="Home Team Discount">{"\uD83C\uDF2D Home Team Discount"}</option>
                  </select>
                  <select value={pbSort} onChange={e=>setPbSort(e.target.value)} style={{ ...S.inp, width:"auto", cursor:"pointer" }}>
                    <option value="name">Sort: Name</option>
                    <option value="dbs_desc">{"DBS: High \u2192 Low"}</option>
                    <option value="dbs_asc">{"DBS: Low \u2192 High"}</option>
                  </select>
                  <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{available.length} plays available</span>
                </div>

                <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, overflow:"hidden", maxHeight:560, overflowY:"auto" }}>
                  {available.length === 0 ? (
                    <div style={{ padding:"32px", textAlign:"center", color:"var(--bz-ink-3)", fontSize:13 }}>
                      {allPlays.length === 0 ? "No Play cards found -- make sure your CSV has a Treatment or Play Ability column" : "No cards match your search"}
                    </div>
                  ) : available.map((c,i) => {
                    const isOwned = ownedSet.has(c.id);
                    const wc = WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
                    return (
                      <div key={c.id} style={{ borderBottom:"1px solid #111", background:i%2===0?"var(--bz-bg)":"var(--bz-s1)" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px" }}>
                          {c.imageUrl && <img src={c.imageUrl} alt={c.hero} style={{ width:36, height:48, objectFit:"cover", borderRadius:4, flexShrink:0, opacity:isOwned?1:0.4 }}/>}
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                              <span style={{ fontSize:13, fontWeight:800, color:"var(--bz-ink)" }}>{c.hero}</span>
                              {!pbOwnedOnly && <span style={{ fontSize:10, fontWeight:700, color:isOwned?"#4ade80":"#333" }}>{isOwned?"\u2713":"--"}</span>}
                            </div>
                            <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center", marginBottom:c.playAbility?3:0 }}>
                              <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>#{c.cardNum}</span>
                              {c.setName && <span style={{ fontSize:10, color:"#8a8a8a", fontStyle:"italic" }}>{c.setName}</span>}
                              {hasPlayCost(c) && <span style={{ fontSize:10, color:"#FBBF24", fontWeight:700 }}>Cost: {c.playCost}</span>}
                              {c.dbs !== undefined && <span style={{ fontSize:10, color:"#A855F7", fontWeight:700 }}>DBS: {c.dbs}</span>}
                              {c.weapon && <span style={{ fontSize:10, color:wc, fontWeight:700 }}>{c.weapon}</span>}
                            </div>
                            {c.playAbility && <div style={{ fontSize:10, color:"var(--bz-ink-2)", lineHeight:1.4, fontStyle:"italic" }}>{c.playAbility}</div>}
                          </div>
                          <div style={{ display:"flex", flexDirection:"column", gap:4, flexShrink:0 }}>
                            {isPlay(c) && (() => {
                              const wouldExceed = (parseFloat(c.dbs)||0) > 0 && totalDbs + (parseFloat(c.dbs)||0) > DBS_CAP;
                              return (
                                <button onClick={()=>{ if(!playFull && !wouldExceed) setPbCards(p=>[...p,{id:c.id,type:"play"}]); }}
                                  disabled={playFull || wouldExceed}
                                  title={wouldExceed?`Would exceed ${DBS_CAP} DBS cap`:playFull?"Play slots full":""}
                                  style={{ background:"var(--bz-s2)", border:"1px solid #E8317A44", color:(playFull||wouldExceed)?"#333":"#E8317A", borderRadius:6, padding:"3px 8px", fontSize:10, fontWeight:700, cursor:(playFull||wouldExceed)?"not-allowed":"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                                  + Play
                                </button>
                              );
                            })()}
                            {isBonus(c) && (() => {
                              const wouldExceed = (parseFloat(c.dbs)||0) > 0 && totalDbs + (parseFloat(c.dbs)||0) > DBS_CAP;
                              return (
                                <button onClick={()=>{ if(!wouldExceed) setPbCards(p=>[...p,{id:c.id,type:"bonus"}]); }}
                                  disabled={wouldExceed}
                                  title={wouldExceed?`Would exceed ${DBS_CAP} DBS cap`:""}
                                  style={{ background:"#0a0f1a", border:"1px solid #7B9CFF44", color:wouldExceed?"#333":"#7B9CFF", borderRadius:6, padding:"3px 8px", fontSize:10, fontWeight:700, cursor:wouldExceed?"not-allowed":"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                                  + BPL
                                </button>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT: Playbook */}
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>

                {/* Stats */}
                <div style={{ ...S.card }}>
                  <div style={{ fontSize:12, fontWeight:800, color:"var(--bz-ink)", marginBottom:10 }}>{"\uD83D\uDCD6 Playbook"}</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                    {[
                      { l:"Plays", v:`${playCount}/${PLAY_LIMIT}`, c:playFull?"#E8317A":"#4ade80" },
                      { l:"Bonus Plays", v:bonusCount, c:"#7B9CFF" },
                    ].map(({l,v,c})=>(
                      <div key={l} style={{ background:"var(--bz-s1)", borderRadius:8, padding:"8px 10px", textAlign:"center" }}>
                        <div style={{ fontSize:20, fontWeight:900, color:c }}>{v}</div>
                        <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                  {/* Play slots bar */}
                  <div style={{ marginBottom:12 }}>
                    <div style={{ height:6, background:"var(--bz-s2)", borderRadius:3, overflow:"hidden" }}>
                      <div style={{ width:`${Math.min(playCount/PLAY_LIMIT*100,100)}%`, height:"100%", borderRadius:3, background:playFull?"#E8317A":"linear-gradient(90deg,#E8317A,#7B2FF7)", transition:"width 0.3s" }}/>
                    </div>
                    <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:4 }}>{PLAY_LIMIT-playCount} play slots remaining</div>
                  </div>
                  {/* DBS cap */}
                  <div style={{ background: dbsOver?"#1a0a0a":"var(--bz-bg)", border:`1px solid ${dbsOver?"#E8317A44":"var(--bz-line-2)"}`, borderRadius:8, padding:"10px 12px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6 }}>
                      <span style={{ fontSize:11, fontWeight:800, color: dbsOver?"#E8317A":"#A855F7" }}>{"\uD83D\uDCB0 DBS"}</span>
                      <span style={{ fontSize:11, fontWeight:700, color: dbsOver?"#E8317A":dbsPct>80?"#FBBF24":"#4ade80" }}>
                        {Math.round(totalDbs)} / {DBS_CAP}
                      </span>
                    </div>
                    <div style={{ height:8, background:"var(--bz-s2)", borderRadius:4, overflow:"hidden", marginBottom:6 }}>
                      <div style={{ width:`${dbsPct}%`, height:"100%", borderRadius:4, transition:"width 0.3s",
                        background: dbsOver?"#E8317A": dbsPct>80?"linear-gradient(90deg,#FBBF24,#E8317A)":"linear-gradient(90deg,#A855F7,#7B9CFF)" }}/>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:10 }}>
                      <span style={{ color:"var(--bz-ink-3)" }}>Used: {Math.round(totalDbs)}</span>
                      <span style={{ color: dbsOver?"#E8317A":dbsLeft<100?"#FBBF24":"#4ade80", fontWeight:700 }}>
                        {dbsOver ? `\u26A0\uFE0F Over by ${Math.round(totalDbs-DBS_CAP)}` : `${Math.round(dbsLeft)} remaining`}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Plays list */}
                {pbResolved.length > 0 ? (
                  <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
                    {/* Plays */}
                    {pbResolved.filter(e=>e.type==="play").length > 0 && (
                      <div>
                        <div style={{ padding:"10px 14px 6px", fontSize:10, fontWeight:700, color:"#E8317A", textTransform:"uppercase", letterSpacing:1 }}>
                          {"\u2694\uFE0F Plays ("}{pbResolved.filter(e=>e.type==="play").length})
                        </div>
                        {pbResolved.filter(e=>e.type==="play").map((e,i) => {
                          const c = e.card;
                          const wc = WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
                          return (
                            <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 14px", borderTop:"1px solid #111", background:i%2===0?"var(--bz-s1)":"var(--bz-bg)" }}>
                              <div style={{ fontSize:12, color:"var(--bz-ink-3)", width:18, textAlign:"center", flexShrink:0 }}>{i+1}</div>
                              {c.imageUrl && <img src={c.imageUrl} alt={c.hero} style={{ width:28, height:37, objectFit:"cover", borderRadius:3, flexShrink:0 }}/>}
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:12, fontWeight:800, color:"var(--bz-ink)" }}>{c.hero}</div>
                                <div style={{ display:"flex", gap:8, marginTop:2 }}>{hasPlayCost(c) && <span style={{ fontSize:10, color:"#FBBF24", fontWeight:700 }}>Cost: {c.playCost}</span>}{c.dbs !== undefined && <span style={{ fontSize:10, color:"#A855F7", fontWeight:700 }}>DBS: {c.dbs}</span>}{c.setName && <span style={{ fontSize:10, color:"#8a8a8a", fontStyle:"italic" }}>{c.setName}</span>}</div>
                                {c.playAbility && <div style={{ fontSize:10, color:"#999", fontStyle:"italic", lineHeight:1.3, marginTop:2 }}>{c.playAbility}</div>}
                              </div>
                              <button onClick={()=>{ const playEntries=pbCards.filter(x=>x.type==="play"); const globalIdx=pbCards.indexOf(playEntries[i]); const arr=[...pbCards]; arr.splice(globalIdx,1); setPbCards(arr); }}
                                style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:14, padding:"2px 4px", flexShrink:0 }}>{"\u00D7"}</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Bonus plays */}
                    {pbResolved.filter(e=>e.type==="bonus").length > 0 && (
                      <div>
                        <div style={{ padding:"10px 14px 6px", fontSize:10, fontWeight:700, color:"#7B9CFF", textTransform:"uppercase", letterSpacing:1, borderTop:"1px solid var(--bz-line)" }}>
                          {"\u2B50 Bonus Plays ("}{pbResolved.filter(e=>e.type==="bonus").length})
                        </div>
                        {pbResolved.filter(e=>e.type==="bonus").map((e,i) => {
                          const c = e.card;
                          return (
                            <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 14px", borderTop:"1px solid #111", background:i%2===0?"var(--bz-s1)":"var(--bz-bg)" }}>
                              <div style={{ fontSize:12, color:"var(--bz-ink-3)", width:18, textAlign:"center", flexShrink:0 }}>B{i+1}</div>
                              {c.imageUrl && <img src={c.imageUrl} alt={c.hero} style={{ width:28, height:37, objectFit:"cover", borderRadius:3, flexShrink:0 }}/>}
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:12, fontWeight:800, color:"#7B9CFF" }}>{c.hero}</div>
                                <div style={{ display:"flex", gap:8, marginTop:2 }}>{hasPlayCost(c) && <span style={{ fontSize:10, color:"#FBBF24", fontWeight:700 }}>Cost: {c.playCost}</span>}{c.dbs !== undefined && <span style={{ fontSize:10, color:"#A855F7", fontWeight:700 }}>DBS: {c.dbs}</span>}{c.setName && <span style={{ fontSize:10, color:"#8a8a8a", fontStyle:"italic" }}>{c.setName}</span>}</div>
                                {c.playAbility && <div style={{ fontSize:10, color:"#999", fontStyle:"italic", lineHeight:1.3, marginTop:2 }}>{c.playAbility}</div>}
                              </div>
                              <button onClick={()=>{ const entries=[...pbCards]; const bonusEntries=entries.filter(x=>x.type==="bonus"); const target=bonusEntries[i]; const idx=entries.findIndex((x,j)=>x===target); entries.splice(idx,1); setPbCards(entries); }}
                                style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:14, padding:"2px 4px", flexShrink:0 }}>{"\u00D7"}</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div style={{ padding:"10px 14px" }}>
                      <button onClick={()=>{ if(window.confirm("Clear entire playbook?")) setPbCards([]); }}
                        style={{ background:"transparent", border:"1px solid #E8317A22", color:"#E8317A", borderRadius:7, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", width:"100%" }}>
                        {"\u2715 Clear Playbook"}</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ ...S.card, textAlign:"center", color:"var(--bz-ink-3)", padding:32, fontSize:13 }}>
                    Add plays from the left panel.<br/>Max {PLAY_LIMIT} plays + unlimited BPL.
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {viewMode === "setlist" && !loading && <SetListView cards={cards} owned={owned} toggleOwned={toggleOwned} activeSet={activeSet} setActiveSet={setActiveSet} expandedHero={expandedHeroNotes} setExpandedHero={setExpandedHeroNotes} />}

            {viewMode === "cards" && (loading ? (
        <div>
          <style>{`
            @keyframes bzRainbowSlide { 0%{background-position:0% 50%} 100%{background-position:200% 50%} }
            .bz-rainbow-fill {
              background: linear-gradient(90deg,#EF4444,#F97316,#FBBF24,#4ade80,#22D3EE,#60A5FA,#A855F7,#F472B6,#EF4444);
              background-size: 200% 100%;
              animation: bzRainbowSlide 3s linear infinite;
            }
            @keyframes skeletonShimmer {
              0% { background-position: -400px 0; }
              100% { background-position: 400px 0; }
            }
            .skeleton-card {
              background: linear-gradient(90deg, #111 25%, var(--bz-s2) 50%, #111 75%);
              background-size: 800px 100%;
              animation: skeletonShimmer 1.4s infinite linear;
              border-radius: 12px;
            }
          `}</style>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:8 }}>
            {Array.from({length:24}).map((_,i) => (
              <div key={i} className="skeleton-card" style={{ border:"1px solid var(--bz-line)", borderRadius:12, overflow:"hidden", opacity: 1 - (i * 0.025) }}>
                {/* Card image placeholder */}
                <div style={{ width:"100%", paddingTop:"140%", background:"var(--bz-bg)", position:"relative" }}>
                  <div className="skeleton-card" style={{ position:"absolute", inset:0 }}/>
                </div>
                {/* Card info placeholders */}
                <div style={{ padding:"10px 12px", display:"flex", flexDirection:"column", gap:6 }}>
                  <div className="skeleton-card" style={{ height:12, borderRadius:6, width:"70%" }}/>
                  <div className="skeleton-card" style={{ height:10, borderRadius:6, width:"45%" }}/>
                  <div style={{ display:"flex", gap:4, marginTop:2 }}>
                    <div className="skeleton-card" style={{ height:18, borderRadius:10, width:42 }}/>
                    <div className="skeleton-card" style={{ height:18, borderRadius:10, width:36 }}/>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center", padding:"24px 0", fontSize:12, color:"#8a8a8a" }}>
            Loading cards...
          </div>
        </div>
      ) : cards.length === 0 ? (
        <div style={{ ...S.card, textAlign:"center", color:"var(--bz-ink-3)", padding:40 }}>No cards loaded yet. Import a CSV to get started.</div>
      ) : (() => {
        const visibleCards = filtered.slice(0, page * PAGE_SIZE);
        const hasMore = visibleCards.length < filtered.length;
        return (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:8 }}>
              {visibleCards.map(c => {
                const isOwned = !!owned[c.id];
                return <BobaCard key={c.id} c={c} isOwned={isOwned} ownedQty={owned[c.id]||0} flippedCard={flippedCard} setFlippedCard={setFlippedCard} toggleOwned={toggleOwned} setOwnedQty={setOwnedQty} toggleWant={toggleWant} wantList={wantList} WEAPON_COLORS={WEAPON_COLORS} isAdmin={isAdmin} onDelete={isAdmin?async()=>{ if(window.confirm("Delete "+c.hero+" "+c.treatment+" from checklist?")) await deleteDoc(doc(db,"boba_checklist",c.id)); }:null} onImageUpload={isAdmin?async(card,file)=>{ 
  try { 
    const safe = (card.treatment||"t").replace(/[^a-zA-Z0-9]/g,"_");
    const r2 = ref(storage, `boba_cards/manual/${card.id}_${safe}.png`);
    const _cfile = await compressImage(file);
    await uploadBytes(r2, _cfile);
    const url = await getDownloadURL(r2);
    await setDoc(doc(db,"boba_checklist",card.id), { imageUrl:url }, {merge:true});
    try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
  } catch(e) { alert("Upload failed: "+e.message); }
}:null}/>;
              })}
            </div>
            {hasMore && (
              <div style={{ textAlign:"center", padding:"16px 0", color:"var(--bz-ink-3)", fontSize:12 }}>
                <div style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:16, height:16, border:"2px solid var(--bz-line-2)", borderTopColor:"#E8317A", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
                  Loading more... ({visibleCards.length} of {filtered.length.toLocaleString()})
                </div>
              </div>
            )}
            {!hasMore && filtered.length > PAGE_SIZE && (
              <div style={{ textAlign:"center", padding:"12px 0", color:"var(--bz-ink-3)", fontSize:11 }}>
                All {filtered.length.toLocaleString()} cards loaded
              </div>
            )}
          </>
        );
      })())}
      {/* Imported sets */}
      {isAdmin && (
        <div style={{ ...S.card }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
            <SectionLabel t={`Imported Sets (${imports.length})`} />
            {cards.length > 0 && imports.length === 0 && (
              <div style={{ fontSize:11, color:"#FBBF24" }}>{"\u26A0 Cards found but no import record -- use Rename below or re-import"}</div>
            )}
          </div>
          {imports.length === 0 ? (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
              <div style={{ fontSize:12, color:"var(--bz-ink-3)" }}>
                No import records found.
                {cards.length > 0 && (
                  <span style={{ color:"#FBBF24", marginLeft:8 }}>{cards.length.toLocaleString()} cards in Firestore from a previous import -- clear them and re-import below.</span>
                )}
              </div>
              {cards.length > 0 && (
                <button onClick={async () => {
                  if(!window.confirm(`Delete ALL ${cards.length.toLocaleString()} cards and all owned data? This cannot be undone.`)) return;
                  const chunkSize = 200;
                  const allIds = cards.map(c=>c.id);
                  for(let i=0;i<allIds.length;i+=chunkSize){
                    await Promise.all(allIds.slice(i,i+chunkSize).map(id=>deleteDoc(doc(db,"boba_checklist",id))));
                  }
                  await setDoc(doc(db,"boba_owned",ownedDocId), {});
                  setOwned({});
                  try { localStorage.removeItem("boba_checklist_cache_v3"); } catch(e) {}
                }} style={{ background:"#1a0a0a", border:"1.5px solid #E8317A", color:"#E8317A", borderRadius:8, padding:"6px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                  {"\uD83D\uDDD1 Clear All Cards"}</button>
              )}
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {imports.map(imp=>(
                <div key={imp.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", background:"var(--bz-s2)", borderRadius:8, gap:8 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    {renamingId === imp.id ? (
                      <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                        <input autoFocus value={renameVal} onChange={e=>setRenameVal(e.target.value)}
                          onKeyDown={e=>{ if(e.key==="Enter") handleRenameSet(imp,renameVal); if(e.key==="Escape") setRenamingId(null); }}
                          style={{ ...S.inp, flex:1, fontSize:13, padding:"4px 8px" }}/>
                        <button onClick={()=>handleRenameSet(imp,renameVal)} style={{ background:"#166534", color:"#fff", border:"none", borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\u2713"}</button>
                        <button onClick={()=>setRenamingId(null)} style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:13 }}>{"\u2715"}</button>
                      </div>
                    ) : (
                      <>
                        <span style={{ fontWeight:700, color:"var(--bz-ink)", fontSize:13 }}>{imp.setName}</span>
                        <span style={{ fontSize:11, color:"var(--bz-ink-3)", marginLeft:10 }}>{imp.cardCount?.toLocaleString()} cards · {imp.importedAt?.slice(0,10)}</span>
                      </>
                    )}
                  </div>
                  <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                    <button onClick={()=>{ setRenamingId(imp.id); setRenameVal(imp.setName); }} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\u270F\uFE0F Rename"}</button>
                    {!scanPdf && (
                      <label style={{ background:"#0a0f1a", color:"#7B9CFF", border:"1px solid #7B9CFF44", borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                        {"\uD83D\uDD0D Scan PDF"}<input type="file" accept=".pdf" onChange={e=>{ const f=e.target.files[0]; if(f) setPendingScan({ file:f, setName:imp.setName }); e.target.value=""; }} style={{ display:"none" }}/>
                      </label>
                    )}
                    <button onClick={()=>handleDeleteImport(imp)} style={{ background:"none", border:"1px solid #E8317A44", color:"#E8317A", borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\uD83D\uDDD1 Delete"}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {cards.length > 0 && (
            <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid var(--bz-line)", display:"flex", justifyContent:"flex-end" }}>
              <button onClick={async () => {
                if(!window.confirm(`Delete ALL ${cards.length.toLocaleString()} cards, all import records, and all owned data? This cannot be undone.`)) return;
                const chunkSize = 200;
                const allIds = cards.map(c=>c.id);
                for(let i=0;i<allIds.length;i+=chunkSize){
                  await Promise.all(allIds.slice(i,i+chunkSize).map(id=>deleteDoc(doc(db,"boba_checklist",id))));
                }
                await Promise.all(imports.map(imp=>deleteDoc(doc(db,"boba_imports",imp.id))));
                await setDoc(doc(db,"boba_owned",ownedDocId), {});
                setOwned({});
              }} style={{ background:"#1a0a0a", border:"1.5px solid #E8317A44", color:"#E8317A", borderRadius:8, padding:"5px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                {"\uD83D\uDDD1 Clear All & Restart"}</button>
            </div>
          )}
        </div>
      )}

    </div>
  );
}




// --- PUBLIC CARD DATABASE (full community app) ---------------

function MessagesTab({ user, activeThread, setActiveThread, threads, threadMsgs, newMsg, setNewMsg, sendMessage, closeThread, marketSales, inp, WEAPON_COLORS, setSigningIn }) {
  if (!user) return (
    <div style={{textAlign:"center",padding:80}}>
      <div style={{fontSize:48,marginBottom:16}}>{"\uD83D\uDCAC"}</div>
      <button onClick={()=>setSigningIn(true)} style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:14,padding:"12px 28px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Sign in to view messages</button>
    </div>
  );

  if (activeThread) return (
    <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:20,overflow:"hidden",backdropFilter:"blur(10px)"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"16px 20px",borderBottom:"1px solid rgba(255,255,255,0.06)",background:"rgba(0,0,0,0.3)"}}>
        <button onClick={()=>setActiveThread(null)} style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",fontSize:20,padding:0,lineHeight:1}}>{"\u2190"}</button>
        {activeThread.cardImage&&<img src={activeThread.cardImage} alt="" style={{width:32,height:43,objectFit:"cover",borderRadius:5,flexShrink:0}}/>}
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)"}}>{activeThread.cardName}</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>
            {"$"}{(activeThread.agreedPrice||0).toFixed(2)} agreed {"\u00B7"} with {activeThread.sellerUid===user.uid?activeThread.buyerName:activeThread.sellerName}
          </div>
        </div>
        {activeThread.status==="active"&&activeThread.sellerUid===user.uid&&(
          <button onClick={()=>closeThread(activeThread.id)} style={{background:"rgba(74,222,128,0.1)",border:"1px solid rgba(74,222,128,0.2)",color:"#4ade80",borderRadius:10,padding:"6px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{"\u2705 Mark Complete"}</button>
        )}
        {activeThread.status==="completed"&&<span style={{fontSize:11,color:"rgba(74,222,128,0.6)",fontWeight:700}}>{"\u2705 Completed"}</span>}
        <button onClick={()=>{if(window.confirm("Delete this entire conversation?")){
          deleteDoc(doc(db,"deal_threads",activeThread.id));
          setActiveThread(null);
        }}} style={{background:"rgba(232,49,122,0.08)",border:"1px solid rgba(232,49,122,0.2)",color:"rgba(232,49,122,0.6)",borderRadius:8,padding:"4px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginLeft:"auto"}}>{"\uD83D\uDDD1"}</button>
      </div>
      <div style={{height:400,overflowY:"auto",padding:"16px 20px",display:"flex",flexDirection:"column",gap:10}}>
        {threadMsgs.length===0&&<div style={{textAlign:"center",color:"rgba(255,255,255,0.2)",padding:40,fontSize:13}}>No messages yet</div>}
        {threadMsgs.map(m=>{
          const isMe=m.senderUid===user.uid;
          if(m.senderUid==="system") return (
            <div key={m.id} style={{textAlign:"center",fontSize:11,color:"rgba(255,255,255,0.3)",background:"rgba(255,255,255,0.03)",borderRadius:10,padding:"8px 14px"}}>{m.text}</div>
          );
          return (
            <div key={m.id} style={{display:"flex",flexDirection:"column",alignItems:isMe?"flex-end":"flex-start",gap:3}}>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginBottom:2,display:"flex",alignItems:"center",gap:8}}>
                <span>{isMe?"You":m.senderName} {"\u00B7"} {new Date(m.sentAt).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</span>
                {isMe&&<button onClick={()=>updateDoc(doc(db,"deal_threads",activeThread.id),{messages:arrayRemove(m)})} style={{background:"none",border:"none",color:"rgba(255,255,255,0.2)",cursor:"pointer",fontSize:12,padding:"0 2px",lineHeight:1}} title="Delete">{"\u00D7"}</button>}
              </div>
              <div style={{
                background:isMe?"linear-gradient(135deg,rgba(232,49,122,0.3),rgba(123,47,247,0.3))":"rgba(255,255,255,0.06)",
                border:"1px solid "+(isMe?"rgba(232,49,122,0.3)":"rgba(255,255,255,0.08)"),
                borderRadius:isMe?"16px 16px 4px 16px":"16px 16px 16px 4px",
                padding:"10px 14px",maxWidth:"75%",fontSize:13,color:"var(--bz-ink)",lineHeight:1.5,wordBreak:"break-word"
              }}>{m.text}</div>
            </div>
          );
        })}
      </div>
      <div style={{display:"flex",gap:8,padding:"12px 20px",borderTop:"1px solid rgba(255,255,255,0.06)",background:"rgba(0,0,0,0.2)"}}>
        {activeThread.status!=="active"&&<div style={{position:"absolute",bottom:72,left:0,right:0,textAlign:"center",fontSize:11,color:"rgba(255,255,255,0.2)",pointerEvents:"none"}}>Deal marked complete</div>}
        <input value={newMsg} onChange={e=>setNewMsg(e.target.value)} placeholder={activeThread.status==="active"?"Message to coordinate payment...":"Continue the conversation..."} style={{...inp,flex:1}} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMessage();}}}/>
        <button onClick={sendMessage} disabled={!newMsg.trim()} style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:12,padding:"8px 20px",fontSize:13,fontWeight:700,cursor:newMsg.trim()?"pointer":"not-allowed",fontFamily:"inherit",opacity:newMsg.trim()?1:0.4}}>Send</button>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)",marginBottom:16}}>Deal Messages</div>
      {threads.length===0?(
        <div style={{textAlign:"center",padding:80,color:"rgba(255,255,255,0.2)"}}>
          <div style={{fontSize:48,marginBottom:16}}>{"\uD83D\uDCAC"}</div>
          <div style={{fontSize:14,fontWeight:700}}>No deal threads yet</div>
          <div style={{fontSize:12,marginTop:8}}>When you accept or make an offer, a chat opens here</div>
        </div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {threads.map(t=>{
            const isUnread=t.lastReadBy?.[user.uid]<t.lastMessageAt&&t.lastSenderUid!==user.uid;
            const otherName=t.sellerUid===user.uid?t.buyerName:t.sellerName;
            return (
              <div key={t.id} onClick={()=>setActiveThread(t)}
                style={{display:"flex",alignItems:"center",gap:14,
                  background:isUnread?"rgba(232,49,122,0.06)":"rgba(255,255,255,0.02)",
                  border:"1px solid "+(isUnread?"rgba(232,49,122,0.2)":"rgba(255,255,255,0.06)"),
                  borderRadius:14,padding:"14px 18px",cursor:"pointer",transition:"all 0.15s"}}
                onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,0.04)";}}
                onMouseLeave={e=>{e.currentTarget.style.background=isUnread?"rgba(232,49,122,0.06)":"rgba(255,255,255,0.02)";}}>
                {t.cardImage
                  ?<img src={t.cardImage} alt={t.cardName} style={{width:40,height:54,objectFit:"cover",borderRadius:7,flexShrink:0}}/>
                  :<div style={{width:40,height:54,background:"rgba(255,255,255,0.04)",borderRadius:7,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>{"\uD83C\uDCCF"}</div>
                }
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                    <div style={{fontSize:14,fontWeight:isUnread?800:600,color:"var(--bz-ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.cardName}</div>
                    {t.status==="completed"&&<span style={{fontSize:10,color:"#4ade80",fontWeight:700,flexShrink:0}}>Done</span>}
                    {isUnread&&<span style={{width:8,height:8,borderRadius:"50%",background:"#E8317A",flexShrink:0}}/>}
                  </div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:3}}>with {otherName} {"\u00B7"} {"$"}{(t.agreedPrice||0).toFixed(2)}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.25)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.lastMessage||"No messages yet"} <span style={{color:"rgba(232,49,122,0.5)",fontWeight:700}}>{"\u2192 Tap to chat"}</span></div>
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.2)"}}>{t.lastMessageAt?new Date(t.lastMessageAt).toLocaleDateString([],{month:"short",day:"numeric"}):""}</div>
                  <button onClick={e=>{e.stopPropagation();setActiveThread(t);}} style={{background:"rgba(232,49,122,0.15)",border:"1px solid rgba(232,49,122,0.3)",color:"#E8317A",borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Chat</button>
                  <button onClick={e=>{e.stopPropagation();if(window.confirm("Delete this conversation?"))deleteDoc(doc(db,"deal_threads",t.id));}} style={{background:"rgba(232,49,122,0.08)",border:"1px solid rgba(232,49,122,0.2)",color:"rgba(232,49,122,0.6)",borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{"\uD83D\uDDD1 Delete"}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {marketSales.length>0&&(
        <div style={{marginTop:32}}>
          <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)",marginBottom:4}}>Recent Sales</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginBottom:16}}>Price history from completed deals on this platform</div>
          {(()=>{
            const byCard={};
            marketSales.forEach(s=>{
              const key=s.cardName+"|"+(s.cardTreatment||"");
              if(!byCard[key]) byCard[key]={cardName:s.cardName,treatment:s.cardTreatment,weapon:s.cardWeapon,image:s.cardImage,sales:[]};
              byCard[key].sales.push({price:s.price,date:s.soldDate});
            });
            const sorted=Object.values(byCard).sort((a,b)=>b.sales.length-a.sales.length).slice(0,10);
            return (
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:10,marginBottom:24}}>
                {sorted.map((entry,idx)=>{
                  const prices=entry.sales.map(s=>s.price);
                  const avg=prices.reduce((a,b)=>a+b,0)/prices.length;
                  const high=Math.max(...prices),low=Math.min(...prices);
                  const last=entry.sales[0];
                  const wc=WEAPON_COLORS[entry.weapon]||"#444";
                  return (
                    <div key={idx} style={{background:"rgba(255,255,255,0.02)",border:"1px solid "+wc+"22",borderRadius:14,padding:14}}>
                      <div style={{display:"flex",gap:10,marginBottom:10}}>
                        {entry.image?<img src={entry.image} alt={entry.cardName} style={{width:40,height:54,objectFit:"cover",borderRadius:7,flexShrink:0}}/>:<div style={{width:40,height:54,background:"rgba(255,255,255,0.04)",borderRadius:7,flexShrink:0}}/>}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:700,color:"var(--bz-ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{entry.cardName}</div>
                          <div style={{fontSize:10,color:wc,fontWeight:700}}>{entry.weapon}</div>
                        </div>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:8}}>
                        {[{l:"Avg",v:"$"+avg.toFixed(2),c:"#7B9CFF"},{l:"High",v:"$"+high.toFixed(2),c:"#4ade80"},{l:"Low",v:"$"+low.toFixed(2),c:"#FBBF24"}].map(({l,v,c})=>(
                          <div key={l} style={{background:"rgba(0,0,0,0.3)",borderRadius:8,padding:"6px 8px",textAlign:"center"}}>
                            <div style={{fontSize:13,fontWeight:800,color:c}}>{v}</div>
                            <div style={{fontSize:9,color:"rgba(255,255,255,0.3)",marginTop:1}}>{l}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>{entry.sales.length} sale{entry.sales.length!==1?"s":""} {"\u00B7"} Last: {last&&last.date?last.date:"--"}</div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
          <div style={{background:"rgba(0,0,0,0.3)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:14,overflow:"hidden"}}>
            <div style={{padding:"10px 16px",borderBottom:"1px solid rgba(255,255,255,0.05)",fontSize:11,color:"rgba(255,255,255,0.3)",fontWeight:700,textTransform:"uppercase",letterSpacing:1.5}}>Recent Sales Feed</div>
            {marketSales.slice(0,30).map((s,i)=>{
              const wc=WEAPON_COLORS[s.cardWeapon]||"#444";
              return (
                <div key={s.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",borderBottom:"1px solid rgba(255,255,255,0.03)",background:i%2===0?"transparent":"rgba(255,255,255,0.01)"}}>
                  {s.cardImage?<img src={s.cardImage} alt={s.cardName} style={{width:28,height:37,objectFit:"cover",borderRadius:5,flexShrink:0}}/>:<div style={{width:28,height:37,background:"rgba(255,255,255,0.04)",borderRadius:5,flexShrink:0}}/>}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:700,color:"var(--bz-ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.cardName}</div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>{s.cardTreatment} {"\u00B7"} <span style={{color:wc}}>{s.cardWeapon}</span></div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:14,fontWeight:800,color:"#4ade80"}}>{"$"}{(s.price||0).toFixed(2)}</div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.2)"}}>{s.soldDate||"--"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── TRADE VIEW ───────────────────────────────────────────────────────────────
// Who has cards you want? Everyone here explicitly opted in and explicitly flagged these cards —
// nothing is surfaced that its owner didn't choose to surface.
//
// Matches (cards on YOUR want list) lead, because that's the question people actually came to answer.
// Everything else they'd trade sits underneath, since you might want something you never got round to
// adding to your want list.
// ── TRADE OFFER BUILDER ──────────────────────────────────────────────────────
// Pick what you want from them, pick what you're offering from your own trade bait, send.
//
// Deliberately restricted to YOUR trade bait rather than your whole collection: offering a card you
// never marked as tradeable is how people accidentally give away the thing they were chasing.
// ── TRADES PANEL ─────────────────────────────────────────────────────────────
// Offers you've sent, offers you've received, and trades in flight.
//
// The in-flight state is the important one: once both sides accept, each person owes the other a
// package. Cards don't move until each person confirms THEIR package arrived — because the cards move
// in the post, not in the database, and a collection that lies about what you physically hold is
// worse than no tracking at all.
function TradesPanel({ user, tradeOffers, onRespond, onCancel, onTracking, onReceived, cards=[], owned={}, onAddSide, onUnaccept, deckLockedForTrade={} }) {
  const [trackDraft, setTrackDraft] = useState({});
  // Recipient "add my side" modal state.
  const [addSideFor, setAddSideFor] = useState(null);   // the trade offer we are adding our side to
  const [asSel, setAsSel] = useState(new Set());        // card ids I am offering back
  const [asCash, setAsCash] = useState("");             // cash I am adding
  const [asSearch, setAsSearch] = useState("");
  const [asBusy, setAsBusy] = useState(false);
  function openAddSide(t){ setAddSideFor(t); setAsSel(new Set()); setAsCash(""); setAsSearch(""); }
  async function submitAddSide(){
    if (!addSideFor) return;
    setAsBusy(true);
    const chosen = cards.filter(c => asSel.has(c.id));
    await onAddSide(addSideFor, { myCards: chosen, cashFromMe: parseFloat(asCash)||0 });
    setAsBusy(false); setAddSideFor(null);
  }
  const [zoom, setZoom] = useState(null);   // a card from the trade, shown full size

  const mine = tradeOffers.filter(t => t.fromUid === user?.uid || t.toUid === user?.uid);
  const incoming = mine.filter(t => t.status === "pending" && t.toUid === user?.uid);
  const outgoing = mine.filter(t => t.status === "pending" && t.fromUid === user?.uid);
  const active   = mine.filter(t => t.status === "accepted");
  const done     = mine.filter(t => t.status === "complete").slice(0, 10);

  if (mine.length === 0) {
    return (
      <div style={{textAlign:"center",padding:"60px 20px"}}>
        <div style={{fontSize:40,marginBottom:12}}>{"\uD83D\uDD01"}</div>
        <div style={{fontSize:16,fontWeight:900,color:"#fff",marginBottom:6}}>No trades yet</div>
        <div style={{fontSize:12.5,color:"rgba(255,255,255,0.4)",lineHeight:1.6,maxWidth:340,margin:"0 auto"}}>
          Find someone in For Trade who has a card you want, and send them an offer.
        </div>
      </div>
    );
  }

  // You're about to post cards to a stranger based on this. 20px thumbnails and clipped names aren't
  // enough to check it against what's in your hand — so show the actual cards, at a size where a
  // wrong treatment or a wrong parallel is obvious before it's in an envelope.
  const CardRow = ({ list, label, color }) => (
    <div style={{flex:1,minWidth:210}}>
      <div style={{fontSize:10,fontWeight:800,color,letterSpacing:0.5,marginBottom:8}}>
        {label} <span style={{color:"rgba(255,255,255,0.25)"}}>({list.length})</span>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(74px,1fr))",gap:8}}>
        {list.map(c => (
          <div key={c.id} onClick={()=>c.image && setZoom(c)} title={c.image ? "Click to enlarge" : c.name}
            style={{cursor:c.image?"zoom-in":"default"}}>
            {c.image
              ? <img src={c.image} alt="" style={{width:"100%",aspectRatio:"3/4",objectFit:"cover",borderRadius:7,border:`1px solid ${color}44`,display:"block"}}/>
              : <div style={{width:"100%",aspectRatio:"3/4",borderRadius:7,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)"}}/>}
            <div style={{fontSize:10.5,fontWeight:700,color:"#ddd",marginTop:5,lineHeight:1.3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</div>
            {(c.treatment || c.weapon) && (
              <div style={{fontSize:9,color:"rgba(255,255,255,0.35)",lineHeight:1.3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {[c.treatment, c.weapon].filter(Boolean).join(" \u00b7 ")}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Everything below is written from the VIEWER's perspective, which means flipping give/get when
  // you're the recipient. Getting this backwards would show someone the wrong half of their own trade.
  const view = (t) => {
    const iAmSender = t.fromUid === user?.uid;
    return {
      them:      iAmSender ? t.toName : t.fromName,
      iGet:      iAmSender ? t.getting : t.giving,
      iGive:     iAmSender ? t.giving  : t.getting,
      iReceived: iAmSender ? t.fromReceived : t.toReceived,
      theyReceived: iAmSender ? t.toReceived : t.fromReceived,
      myTracking:  iAmSender ? t.fromTracking : t.toTracking,
      theirTracking: iAmSender ? t.toTracking : t.fromTracking,
        iGetCash:  iAmSender ? (t.cashFromRecipient||0) : (t.cashFromSender||0),
        iGiveCash: iAmSender ? (t.cashFromSender||0)    : (t.cashFromRecipient||0),
    };
  };

  const Section = ({ title, list, children }) => list.length === 0 ? null : (
    <div style={{marginBottom:22}}>
      <div style={{fontSize:11,fontWeight:800,color:"var(--bz-ink-2)",letterSpacing:0.5,marginBottom:9}}>
        {title} ({list.length})
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:9}}>{list.map(children)}</div>
    </div>
  );

  return (
    <div>
      <Section title="INCOMING OFFERS" list={incoming}>
        {t => { const v = view(t); return (
          <div key={t.id} style={{background:"rgba(232,49,122,0.05)",border:"1px solid rgba(232,49,122,0.3)",borderRadius:12,padding:14}}>
            <div style={{fontSize:13.5,fontWeight:800,color:"#fff",marginBottom:10}}>
              {v.them} wants to trade
            </div>
            <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:12}}>
              <CardRow list={v.iGet}  label="YOU GET"  color="#4ade80"/>
              {v.iGetCash > 0 && <div style={{fontSize:12,fontWeight:800,color:"#4ade80",alignSelf:"center",background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"6px 10px"}}>+ ${Number(v.iGetCash).toFixed(2)} cash</div>}
              <div style={{display:"flex",alignItems:"center",color:"rgba(255,255,255,0.2)",fontSize:16,fontWeight:800,alignSelf:"stretch"}}>{"\u21C4"}</div>
              <CardRow list={v.iGive} label="YOU GIVE" color="#FBBF24"/>
              {v.iGiveCash > 0 && <div style={{fontSize:12,fontWeight:800,color:"#FBBF24",alignSelf:"center",background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"6px 10px"}}>+ ${Number(v.iGiveCash).toFixed(2)} cash</div>}
            </div>
            {t.note && <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",fontStyle:"italic",marginBottom:12,paddingLeft:10,borderLeft:"2px solid #333"}}>{t.note}</div>}
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {t.needsRecipientSide ? (
                        <button onClick={()=>openAddSide(t)} style={{flex:1,minWidth:150,background:"linear-gradient(135deg,#4ade80,#22c55e)",color:"#06240f",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{"\uD83E\uDD1D"} Add my cards / cash</button>
                      ) : (() => {
                        const mine = t.toAccepted, theirs = t.fromAccepted;
                        if (mine) return (
                          <>
                            <div style={{flex:1,minWidth:150,textAlign:"center",background:"rgba(74,222,128,0.1)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:10,padding:"11px",fontSize:12.5,fontWeight:800}}>{theirs?"Both agreed \u2014 finalizing\u2026":"\u2713 You agreed \u2014 waiting on them"}</div>
                            <button onClick={()=>onUnaccept(t)} style={{background:"transparent",border:"1px solid #444",color:"#999",borderRadius:10,padding:"11px 14px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Undo</button>
                          </>
                        );
                        return (
                          <>
                            <button onClick={()=>onRespond(t,true)} style={{flex:1,minWidth:120,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:10,padding:"11px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{theirs?"Accept deal":"Agree to terms"}</button>
                            <button onClick={()=>openAddSide(t)} style={{background:"rgba(123,156,255,0.12)",border:"1px solid rgba(123,156,255,0.35)",color:"#7B9CFF",borderRadius:10,padding:"11px 14px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Change my side</button>
                            <button onClick={()=>onRespond(t,false)} style={{background:"transparent",border:"1px solid #333",color:"#888",borderRadius:10,padding:"11px 14px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Decline</button>
                          </>
                        );
                      })()}
                    </div>
          </div>
        );}}
      </Section>

      <Section title="SENT OFFERS" list={outgoing}>
        {t => { const v = view(t); return (
          <div key={t.id} style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,gap:10}}>
              <div style={{fontSize:13.5,fontWeight:800,color:"#fff"}}>Offer to {v.them}</div>
                        <span style={{fontSize:10.5,color:"var(--bz-ink-3)",fontWeight:700}}>{t.needsRecipientSide ? "Waiting for their side" : t.fromAccepted ? (t.toAccepted?"Both agreed":"You agreed — waiting on them") : t.toAccepted ? "They agreed — your move" : "Waiting for a reply"}</span>
            </div>
            <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:12}}>
              <CardRow list={v.iGet}  label="YOU GET"  color="#4ade80"/>
              {v.iGetCash > 0 && <div style={{fontSize:12,fontWeight:800,color:"#4ade80",alignSelf:"center",background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"6px 10px"}}>+ ${Number(v.iGetCash).toFixed(2)} cash</div>}
              <div style={{display:"flex",alignItems:"center",color:"rgba(255,255,255,0.2)",fontSize:16,fontWeight:800,alignSelf:"stretch"}}>{"\u21C4"}</div>
              <CardRow list={v.iGive} label="YOU GIVE" color="#FBBF24"/>
              {v.iGiveCash > 0 && <div style={{fontSize:12,fontWeight:800,color:"#FBBF24",alignSelf:"center",background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"6px 10px"}}>+ ${Number(v.iGiveCash).toFixed(2)} cash</div>}
            </div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                      {!t.needsRecipientSide && (() => {
                        const mine = t.fromAccepted, theirs = t.toAccepted;
                        if (mine) return (
                          <button onClick={()=>onUnaccept(t)} style={{flex:1,minWidth:150,background:"rgba(74,222,128,0.1)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:9,padding:"10px",fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{theirs?"Both agreed \u2014 finalizing\u2026":"\u2713 You agreed \u00b7 tap to undo"}</button>
                        );
                        return (
                          <button onClick={()=>onRespond(t,true)} style={{flex:1,minWidth:150,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:9,padding:"10px",fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{theirs?"Accept deal":"Agree to terms"}</button>
                        );
                      })()}
                      <button onClick={()=>onCancel(t)} style={{background:"transparent",border:"1px solid #333",color:"#888",borderRadius:9,padding:"10px 14px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel offer</button>
                    </div>
          </div>
        );}}
      </Section>

      <Section title="IN PROGRESS" list={active}>
        {t => { const v = view(t); const draft = trackDraft[t.id] ?? v.myTracking ?? ""; return (
          <div key={t.id} style={{background:"rgba(74,222,128,0.04)",border:"1px solid rgba(74,222,128,0.28)",borderRadius:12,padding:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:10,flexWrap:"wrap"}}>
                <div style={{fontSize:13.5,fontWeight:800,color:"#4ade80"}}>
                  Trading with {v.them}
                </div>
                {/* Say what's actually needed from YOU. A trade sitting in "accepted" with nothing
                    posted is the state where people quietly stall — so name the next action rather
                    than leaving them to infer it. */}
                <span style={{fontSize:10.5,fontWeight:800,letterSpacing:0.3,padding:"3px 9px",borderRadius:20,whiteSpace:"nowrap",
                  background: v.iReceived ? "rgba(74,222,128,0.15)" : !v.myTracking ? "rgba(251,191,36,0.15)" : "rgba(255,255,255,0.07)",
                  color:      v.iReceived ? "#4ade80"               : !v.myTracking ? "#FBBF24"               : "rgba(255,255,255,0.5)"}}>
                  {v.iReceived        ? (v.theyReceived ? "COMPLETE" : "WAITING ON THEM")
                   : !v.myTracking    ? "SEND YOUR CARDS"
                   : "WAITING ON YOUR PACKAGE"}
                </span>
              </div>
            <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:14}}>
              <CardRow list={v.iGet}  label="INCOMING" color="#4ade80"/>
              {v.iGetCash > 0 && <div style={{fontSize:12,fontWeight:800,color:"#4ade80",alignSelf:"center",background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"6px 10px"}}>+ ${Number(v.iGetCash).toFixed(2)} cash</div>}
              <div style={{display:"flex",alignItems:"center",color:"rgba(255,255,255,0.2)",fontSize:16,fontWeight:800,alignSelf:"stretch"}}>{"\u21C4"}</div>
              <CardRow list={v.iGive} label="OUTGOING" color="#FBBF24"/>
              {v.iGiveCash > 0 && <div style={{fontSize:12,fontWeight:800,color:"#FBBF24",alignSelf:"center",background:"rgba(255,255,255,0.04)",borderRadius:8,padding:"6px 10px"}}>+ ${Number(v.iGiveCash).toFixed(2)} cash</div>}
            </div>

            {/* Your tracking, so they know it's on the way. */}
            <div style={{display:"flex",gap:7,marginBottom:10,flexWrap:"wrap"}}>
              <input value={draft} onChange={e=>setTrackDraft(d=>({...d,[t.id]:e.target.value}))}
                placeholder="Your tracking number\u2026"
                style={{flex:1,minWidth:170,background:"var(--bz-bg)",border:"1px solid #333",borderRadius:7,padding:"8px 11px",fontSize:12,color:"#fff",fontFamily:"inherit"}}/>
              <button onClick={()=>onTracking(t, draft)} disabled={!draft.trim()}
                style={{background:draft.trim()?"rgba(255,255,255,0.08)":"transparent",border:"1px solid #333",color:draft.trim()?"#ccc":"#555",borderRadius:7,padding:"8px 14px",fontSize:11.5,fontWeight:700,cursor:draft.trim()?"pointer":"not-allowed",fontFamily:"inherit"}}>Save</button>
            </div>
            {v.theirTracking && (
              <div style={{fontSize:11.5,color:"rgba(255,255,255,0.5)",marginBottom:10}}>
                Their tracking: <strong style={{color:"#ccc",fontFamily:"monospace"}}>{v.theirTracking}</strong>
              </div>
            )}

            {/* THE moment cards actually change hands. */}
            {v.iReceived ? (
              <div style={{fontSize:12,color:"#4ade80",fontWeight:700,lineHeight:1.6}}>
                {"\u2713"} You've confirmed yours arrived.
                {v.theyReceived ? " Trade complete." : ` Waiting on ${v.them} to confirm theirs.`}
              </div>
            ) : (
              <button onClick={()=>onReceived(t)}
                style={{width:"100%",background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:8,padding:"10px",fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                {"\uD83D\uDCE6"} I received my cards
              </button>
            )}
            <div style={{fontSize:10.5,color:"rgba(255,255,255,0.3)",marginTop:8,lineHeight:1.55}}>
              Your collection updates when you confirm {"\u2014"} the cards you receive are added, the ones you sent are removed.
            </div>
              {/* Trades fall through. Without a way out, a dead trade sits here forever — and its
                  cards stay locked, unable to be offered to anyone else. */}
              <button onClick={()=>onCancel(t)}
                style={{marginTop:10,background:"transparent",border:"1px solid rgba(255,255,255,0.12)",color:"rgba(255,255,255,0.4)",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                Cancel trade
              </button>
          </div>
        );}}
      </Section>

      <Section title="COMPLETED" list={done}>
        {t => { const v = view(t); return (
          <div key={t.id} style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:10,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
            <span style={{fontSize:12.5,color:"rgba(255,255,255,0.55)"}}>
              Traded {v.iGive.length} for {v.iGet.length} with <strong style={{color:"#ccc"}}>{v.them}</strong>
            </span>
            <span style={{fontSize:11,color:"#4ade80",fontWeight:700,whiteSpace:"nowrap"}}>{"\u2713"} Complete</span>
          </div>
        );}}
      </Section>

      {/* Full-size view. The whole reason to open a trade is to check the cards are the ones you
          think they are — before they're in an envelope. */}
      {zoom && (
        <div onClick={()=>setZoom(null)}
          style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",zIndex:14900,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,cursor:"zoom-out"}}>
          <img src={zoom.image} alt={zoom.name} onClick={e=>e.stopPropagation()}
            style={{maxWidth:"min(92vw, 440px)",maxHeight:"76vh",objectFit:"contain",borderRadius:12,boxShadow:"0 20px 60px rgba(0,0,0,0.7)",cursor:"default"}}/>
          <div style={{marginTop:15,textAlign:"center"}}>
            <div style={{fontSize:16,fontWeight:800,color:"#fff"}}>{zoom.name}</div>
            {(zoom.treatment || zoom.weapon || zoom.setName) && (
              <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginTop:3}}>
                {[zoom.treatment, zoom.weapon, zoom.setName].filter(Boolean).join(" \u00b7 ")}
              </div>
            )}
          </div>
          <button onClick={()=>setZoom(null)}
            style={{position:"fixed",top:20,right:20,background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",color:"#fff",width:38,height:38,borderRadius:"50%",fontSize:19,cursor:"pointer",fontFamily:"inherit",lineHeight:1}}>{"\u00D7"}</button>
        </div>
      )}

      {/* ── RECIPIENT: ADD MY SIDE ── search my collection, pick cards + cash, send back. */}
      {addSideFor && (() => {
        const q = asSearch.trim().toLowerCase();
        const mine = cards.filter(c => (owned[c.id]||0) > 0);
        const avail = mine.filter(c => (owned[c.id]||0) > (deckLockedForTrade[c.id]||0));
        const shown = q ? avail.filter(c => (c.hero||c.playName||"").toLowerCase().includes(q) || String(c.cardNum||"").toLowerCase().includes(q) || (c.setName||"").toLowerCase().includes(q)) : avail;
        const chosenCount = asSel.size;
        return (
          <div onClick={()=>!asBusy&&setAddSideFor(null)} style={{position:"fixed",inset:0,zIndex:14000,background:"rgba(0,0,0,0.82)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
            <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:640,maxHeight:"90vh",display:"flex",flexDirection:"column",background:"#14141c",border:"1px solid rgba(74,222,128,0.3)",borderRadius:16,padding:"20px 22px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{fontSize:18,fontWeight:900,color:"#fff"}}>{"\uD83E\uDD1D"} Add your side</div>
                <button onClick={()=>setAddSideFor(null)} style={{background:"transparent",border:"none",color:"#888",fontSize:22,cursor:"pointer",lineHeight:1}}>{"\u00d7"}</button>
              </div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginBottom:12}}>Pick cards from your collection to offer back{addSideFor.fromName?` to ${addSideFor.fromName}`:""}, and/or add cash.</div>
              <input value={asSearch} onChange={e=>setAsSearch(e.target.value)} placeholder="Search your collection\u2026" style={{width:"100%",background:"#0e0e13",color:"#eee",border:"1px solid rgba(255,255,255,0.15)",borderRadius:9,padding:"9px 12px",fontSize:13,fontFamily:"inherit",marginBottom:10,boxSizing:"border-box"}}/>
              <div style={{flex:1,overflowY:"auto",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(92px,1fr))",gap:8,marginBottom:12,minHeight:120}}>
                {shown.slice(0,120).map(c => {
                  const sel = asSel.has(c.id);
                  return (
                    <div key={c.id} onClick={()=>setAsSel(s=>{ const n=new Set(s); n.has(c.id)?n.delete(c.id):n.add(c.id); return n; })}
                      style={{cursor:"pointer",borderRadius:9,overflow:"hidden",border:sel?"2px solid #4ade80":"2px solid transparent",background:"rgba(255,255,255,0.03)",position:"relative"}}>
                      <div style={{aspectRatio:"3/4",background:"#0a0a0f"}}>
                        {c.imageUrl ? <img src={c.imageUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:"#555",textAlign:"center",padding:4}}>{c.hero||c.playName}</div>}
                      </div>
                      {sel && <div style={{position:"absolute",top:4,right:4,width:20,height:20,borderRadius:"50%",background:"#4ade80",color:"#06240f",fontSize:13,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center"}}>{"\u2713"}</div>}
                      <div style={{fontSize:9.5,fontWeight:700,color:"#ddd",padding:"3px 4px",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.hero||c.playName}</div>
                    </div>
                  );
                })}
                {shown.length===0 && <div style={{gridColumn:"1/-1",textAlign:"center",color:"rgba(255,255,255,0.35)",fontSize:12,padding:"24px 0"}}>No matching cards you can offer.</div>}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                <span style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.6)"}}>Cash you add:</span>
                <div style={{display:"flex",alignItems:"center",gap:3}}>
                  <span style={{fontSize:13,color:"rgba(255,255,255,0.5)"}}>$</span>
                  <input type="number" min="0" step="0.01" value={asCash} onChange={e=>setAsCash(e.target.value)} placeholder="0" style={{width:80,background:"#0e0e13",color:"#eee",border:"1px solid rgba(255,255,255,0.15)",borderRadius:7,padding:"6px 9px",fontSize:13,fontFamily:"inherit"}}/>
                </div>
                <span style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginLeft:"auto"}}>{chosenCount} card{chosenCount===1?"":"s"} selected</span>
              </div>
              <div style={{display:"flex",gap:9}}>
                <button onClick={submitAddSide} disabled={asBusy||(chosenCount===0 && (parseFloat(asCash)||0)<=0)} style={{flex:1,background:(chosenCount>0||(parseFloat(asCash)||0)>0)?"linear-gradient(135deg,#4ade80,#22c55e)":"#222",color:(chosenCount>0||(parseFloat(asCash)||0)>0)?"#06240f":"#666",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:800,cursor:asBusy?"wait":"pointer",fontFamily:"inherit"}}>{asBusy?"Sending\u2026":"Send my side back"}</button>
                <button onClick={()=>setAddSideFor(null)} disabled={asBusy} style={{background:"transparent",border:"1px solid #333",color:"#888",borderRadius:10,padding:"12px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}

function TradeOfferModal({ trader, cards, owned, tradeBait, myTrades = [], myUid, deckLocked = {}, onSend, onClose, preselect = null, cashFirst = false }) {
  // preselect: a card the user clicked on directly, so they don't have to hunt for it in the list.
  const [want, setWant] = useState(() => new Set(preselect ? [preselect.id] : []));
  const [give, setGive] = useState(new Set());
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  // Cash can go EITHER WAY: you might add cash to sweeten what you're giving, or ask for cash on
  // top of their card. A card being listed for trade doesn't mean the owner won't take money.
  const [myCash, setMyCash] = useState("");
  const [theirCash, setTheirCash] = useState("");

  const theirCards = trader.theirCards || [];

  // How many copies of each card I've already promised away in a live trade. This is a COUNT, not a
  // flag: if you own three of something and promised one, the other two are still yours to offer.
  const committed = useMemo(() => {
    const n = {};
    for (const t of myTrades) {
      if (t.status !== "pending" && t.status !== "accepted") continue;
      const iAmSender = t.fromUid === myUid;
      const mine = iAmSender ? (t.giving||[]) : (t.getting||[]);   // what I'm handing over
      mine.forEach(c => { n[c.id] = (n[c.id]||0) + 1; });
    }
    return n;
  }, [myTrades, myUid]);

  // What I can offer: owned, flagged as trade bait, and not already spoken for.
  //
  // Without this you can promise the same physical card to two people, and the second trade is one
  // you have already made yourself unable to honour.
  // A copy locked in a deck (mine or family's) is just as unavailable as one already promised in
  // another trade. Both count against what's free to offer.
  const myOfferable = useMemo(
    () => cards.filter(c => tradeBait[c.id]
      && (owned[c.id]||0) > ((committed[c.id]||0) + (deckLocked[c.id]||0))),
    [cards, owned, tradeBait, committed, deckLocked]
  );

  const toggle = (set, setter, id) => setter(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  // You must ask for something (a card and/or cash from them), and you must put something up
  // (a card and/or cash). Requiring a CARD from your side made straight cash offers impossible,
  // which was the whole point of adding the cash fields.
  const iGive = give.size > 0 || (parseFloat(myCash)||0) > 0;
  const iGet  = want.size > 0 || (parseFloat(theirCash)||0) > 0;
  const canSend = iGive && iGet && !sending;

  async function send() {
    setSending(true);
    await onSend({
      toUid: trader.uid, toName: trader.name,
      theirCards: theirCards.filter(c => want.has(c.id)),
      myCards: myOfferable.filter(c => give.has(c.id)),
      note,
      cashFromSender: parseFloat(myCash)||0,
      cashFromRecipient: parseFloat(theirCash)||0,
    });
    setSending(false);
    onClose();
  }

  const Picker = ({ list, sel, setter, empty, accent }) => (
    <div style={{border:"1px solid var(--bz-line-2)",borderRadius:10,maxHeight:220,overflowY:"auto",padding:6}}>
      {list.length === 0
        ? <div style={{padding:"18px 10px",textAlign:"center",fontSize:11.5,color:"var(--bz-ink-3)",lineHeight:1.6}}>{empty}</div>
        : list.map(c => {
            const on = sel.has(c.id);
            return (
              <label key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 7px",borderRadius:7,cursor:"pointer",background:on?`${accent}1a`:"transparent"}}>
                <input type="checkbox" checked={on} onChange={()=>toggle(sel,setter,c.id)} style={{accentColor:accent,cursor:"pointer"}}/>
                {c.imageUrl
                  ? <img src={c.imageUrl} alt="" style={{width:26,height:35,objectFit:"cover",borderRadius:4}}/>
                  : <div style={{width:26,height:35,borderRadius:4,background:"rgba(255,255,255,0.05)"}}/>}
                <div style={{minWidth:0,flex:1}}>
                  <div style={{fontSize:12,fontWeight:800,color:on?accent:"#ddd",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.hero||c.playName||"\u2014"}</div>
                  <div style={{fontSize:10,color:"var(--bz-ink-3)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{[c.treatment,c.weapon].filter(Boolean).join(" \u00b7 ")}</div>
                </div>
              </label>
            );
          })}
    </div>
  );

  return (
    <div onClick={()=>!sending&&onClose()} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:14600,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:16,padding:22,maxWidth:640,width:"100%",maxHeight:"88vh",overflowY:"auto"}}>
        <div style={{fontSize:17,fontWeight:900,color:"#fff",marginBottom:4}}>
          {"\uD83D\uDD01"} Offer a trade to {trader.name}
        </div>
        <div style={{fontSize:12,color:"var(--bz-ink-3)",lineHeight:1.6,marginBottom:18}}>
          Cards only change hands once you've each confirmed the package arrived {"\u2014"} nothing moves
          the moment they accept.
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:16}}>
          <div>
            <div style={{fontSize:11,fontWeight:800,color:"#4ade80",marginBottom:6,letterSpacing:0.4}}>
              YOU GET {want.size > 0 && `(${want.size})`}
            </div>
            <Picker list={theirCards} sel={want} setter={setWant} accent="#4ade80"
              empty="They have nothing listed."/>
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:800,color:"#FBBF24",marginBottom:6,letterSpacing:0.4}}>
              YOU GIVE {give.size > 0 && `(${give.size})`}
            </div>
            <Picker list={myOfferable} sel={give} setter={setGive} accent="#FBBF24"
              empty={(Object.keys(committed).length > 0 || Object.keys(deckLocked).length > 0)
                ? "Nothing left to offer \u2014 the cards you've flagged are all either committed to another trade or locked into a deck (yours or a family member's). They free up when the trade settles or the card comes out of the deck."
                : "You haven't flagged any cards as trade bait yet. Flag some first \u2014 only cards you've marked as tradeable can be offered."}/>
          </div>
        </div>

        {/* Cash on either side. Listing a card for trade doesn't mean the owner won't take money,
            and a straight cash offer is often the easiest deal to say yes to \u2014 previously there was
            no way to make one from here at all. */}
        <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
          <div style={{flex:1,minWidth:130}}>
            <div style={{fontSize:10,color:"#a0a0a0",marginBottom:3,fontWeight:700}}>{"\uD83D\uDCB5"} Cash I'll add</div>
            <input type="number" inputMode="decimal" min="0" value={myCash} onChange={e=>setMyCash(e.target.value)}
              placeholder="0.00" autoFocus={cashFirst}
              style={{width:"100%",background:"var(--bz-bg)",border:"1px solid "+(parseFloat(myCash)>0?"rgba(74,222,128,0.5)":"#333"),borderRadius:8,padding:"9px 11px",fontSize:13,color:"#fff",fontFamily:"inherit"}}/>
          </div>
          <div style={{flex:1,minWidth:130}}>
            <div style={{fontSize:10,color:"#a0a0a0",marginBottom:3,fontWeight:700}}>Cash they'll add</div>
            <input type="number" inputMode="decimal" min="0" value={theirCash} onChange={e=>setTheirCash(e.target.value)}
              placeholder="0.00"
              style={{width:"100%",background:"var(--bz-bg)",border:"1px solid "+(parseFloat(theirCash)>0?"rgba(74,222,128,0.5)":"#333"),borderRadius:8,padding:"9px 11px",fontSize:13,color:"#fff",fontFamily:"inherit"}}/>
          </div>
        </div>
        {(parseFloat(myCash)>0 || parseFloat(theirCash)>0) && give.size===0 && want.size>0 && (
          <div style={{fontSize:11,color:"#4ade80",marginBottom:10,lineHeight:1.5}}>
            Straight cash offer {"\u2014"} no cards from your side.
          </div>
        )}
        <textarea value={note} onChange={e=>setNote(e.target.value)} rows={2}
          placeholder="Add a note (optional)\u2026"
          style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"10px 12px",fontSize:12.5,color:"#fff",fontFamily:"inherit",resize:"vertical",marginBottom:14}}/>

        <div style={{display:"flex",gap:8}}>
          <button disabled={!canSend} onClick={send}
            style={{flex:1,background:canSend?"linear-gradient(135deg,#E8317A,#7B2FF7)":"#222",color:canSend?"#fff":"#666",border:"none",borderRadius:9,padding:"12px",fontSize:13,fontWeight:800,cursor:canSend?"pointer":"not-allowed",fontFamily:"inherit"}}>
            {(() => {
              if (sending) return "Sending\u2026";
              const mc = parseFloat(myCash)||0, tc = parseFloat(theirCash)||0;
              if (!iGet)  return "Pick what you want";
              if (!iGive) return "Add a card or cash";
              // Describe the actual shape of the offer rather than assuming cards on both sides.
              const mine  = [give.size ? `${give.size} card${give.size!==1?"s":""}` : null, mc>0 ? `$${mc.toLocaleString()}` : null].filter(Boolean).join(" + ");
              const yours = [want.size ? `${want.size} card${want.size!==1?"s":""}` : null, tc>0 ? `$${tc.toLocaleString()}` : null].filter(Boolean).join(" + ");
              return `Offer ${mine} for ${yours}`;
            })()}
          </button>
          <button disabled={sending} onClick={onClose}
            style={{flex:1,background:"transparent",border:"1px solid #333",color:"#999",borderRadius:9,padding:"12px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function TradeView({ user, traders, matchCount, wantList, onViewProfile, setActiveTab, setSigningIn, WEAPON_COLORS, onZoom = ()=>{}, onOffer = ()=>{}, onCashOffer = ()=>{} }) {
  const [expanded, setExpanded] = useState(null);

  if (!user) {
    return (
      <div style={{textAlign:"center",padding:"70px 20px"}}>
        <div style={{fontSize:44,marginBottom:14}}>{"\uD83D\uDD01"}</div>
        <div style={{fontSize:18,fontWeight:900,color:"#fff",marginBottom:8}}>Sign in to find trades</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",maxWidth:380,margin:"0 auto 18px",lineHeight:1.6}}>
          Add cards to your want list and we'll show you who has them.
        </div>
        <button onClick={()=>setSigningIn(true)} style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:10,padding:"11px 22px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Sign in</button>
      </div>
    );
  }

  const wantCount = Object.keys(wantList||{}).length;

  if (traders.length === 0) {
    return (
      <div style={{textAlign:"center",padding:"70px 20px"}}>
        <div style={{fontSize:44,marginBottom:14}}>{"\uD83D\uDD01"}</div>
        <div style={{fontSize:18,fontWeight:900,color:"#fff",marginBottom:8}}>Nobody's listed for trade yet</div>
        <div style={{fontSize:13,color:"rgba(255,255,255,0.45)",maxWidth:400,margin:"0 auto",lineHeight:1.6}}>
          Collectors who flag cards as trade bait and switch on trade matching will show up here.
          {" "}Be the first {"\u2014"} flag a few of your duplicates and list yourself.
        </div>
        <button onClick={()=>setActiveTab("tradebait")} style={{marginTop:18,background:"transparent",border:"1px solid rgba(255,255,255,0.2)",color:"rgba(255,255,255,0.6)",borderRadius:10,padding:"10px 20px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
          Go to Trade Bait
        </button>
      </div>
    );
  }

  // Trade cards were 26x35px thumbnails \u2014 far too small to judge a card you are considering
  // trading for. Condition, centring and finish are the whole decision, so the art gets real
  // estate and the actions sit on the card rather than only at the trader level.
  const CardChip = ({ c, isMatch, trader }) => (
    <div style={{width:112,background:isMatch?"rgba(74,222,128,0.08)":"rgba(255,255,255,0.03)",border:`1px solid ${isMatch?"rgba(74,222,128,0.4)":"rgba(255,255,255,0.08)"}`,borderRadius:10,overflow:"hidden",display:"flex",flexDirection:"column"}}>
      <div onClick={()=>c.imageUrl && onZoom({image:c.imageUrl, name:c.hero||c.playName||"", sub:[c.treatment,c.weapon,c.setName].filter(Boolean).join(" \u00b7 ")})}
        title={c.imageUrl ? "Click to enlarge" : `${c.hero||c.playName||""}`}
        style={{position:"relative",cursor:c.imageUrl?"zoom-in":"default",aspectRatio:"3/4",background:"rgba(255,255,255,0.04)"}}>
        {c.imageUrl
          ? <img src={c.imageUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
          : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:"rgba(255,255,255,0.25)",textAlign:"center",padding:6}}>no image</div>}
        {isMatch && (
          <div style={{position:"absolute",top:4,left:4,background:"rgba(74,222,128,0.92)",color:"#04220f",fontSize:8.5,fontWeight:900,padding:"1px 5px",borderRadius:4}}>WANT</div>
        )}
      </div>
      <div style={{padding:"5px 6px 6px"}}>
        <div style={{fontSize:11,fontWeight:800,color:isMatch?"#4ade80":"#ddd",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
          {c.hero || c.playName || "\u2014"}
        </div>
        <div style={{fontSize:9,color:"rgba(255,255,255,0.35)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginTop:1}}>
          {[c.treatment, c.weapon].filter(Boolean).join(" \u00b7 ") || c.cardNum || ""}
        </div>
        {/* Per-card actions. Wanting ONE specific card is the common case; making people open a
            full trade builder just to ask about it was the friction. */}
        {trader && (
          <div style={{display:"flex",gap:4,marginTop:5}}>
            <button onClick={e=>{ e.stopPropagation(); onOffer(trader, c); }} title="Offer a trade for this card"
              style={{flex:1,background:"rgba(232,49,122,0.15)",border:"1px solid rgba(232,49,122,0.45)",color:"#E8317A",borderRadius:6,padding:"3px 0",fontSize:9.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
              {"\uD83D\uDD01"} Trade
            </button>
            <button onClick={e=>{ e.stopPropagation(); onCashOffer && onCashOffer(trader, c); }} title="Offer cash for this card"
              style={{flex:1,background:"rgba(74,222,128,0.13)",border:"1px solid rgba(74,222,128,0.4)",color:"#4ade80",borderRadius:6,padding:"3px 0",fontSize:9.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
              $ Offer
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {/* The headline number. If you have no want list, say so — the feature is useless without one
          and silently showing zero matches would just look broken. */}
      <div style={{background:matchCount>0?"rgba(74,222,128,0.07)":"rgba(255,255,255,0.03)",border:`1px solid ${matchCount>0?"rgba(74,222,128,0.28)":"rgba(255,255,255,0.08)"}`,borderRadius:12,padding:"13px 15px",marginBottom:16}}>
        {wantCount === 0 ? (
          <div style={{fontSize:12.5,color:"rgba(255,255,255,0.55)",lineHeight:1.6}}>
            <strong style={{color:"#fff"}}>Your want list is empty.</strong> Add the cards you're chasing and
            we'll highlight anyone here who has them.
            <button onClick={()=>setActiveTab("wants")} style={{marginLeft:8,background:"transparent",border:"none",color:"#E8317A",fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",padding:0,textDecoration:"underline"}}>Add wants</button>
          </div>
        ) : matchCount > 0 ? (
          <div style={{fontSize:13,fontWeight:800,color:"#4ade80"}}>
            {"\u2713"} {matchCount} card{matchCount===1?"":"s"} from your want list {matchCount===1?"is":"are"} available to trade
          </div>
        ) : (
          <div style={{fontSize:12.5,color:"rgba(255,255,255,0.5)",lineHeight:1.6}}>
            Nobody has your want-list cards right now {"\u2014"} but {traders.length} collector{traders.length===1?" is":"s are"} open
            to trades. Have a look through what they're offering.
          </div>
        )}
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {traders.map(t => {
          const open = expanded === t.uid;
          const others = t.theirCards.filter(c => !wantList[c.id]);
          return (
            <div key={t.uid} style={{background:t.matches.length>0?"rgba(74,222,128,0.04)":"rgba(255,255,255,0.02)",border:`1px solid ${t.matches.length>0?"rgba(74,222,128,0.25)":"rgba(255,255,255,0.07)"}`,borderRadius:12,padding:"14px 16px"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",marginBottom:t.matches.length>0?12:0}}>
                <div style={{flex:1,minWidth:180}}>
                  <div style={{fontSize:14,fontWeight:800,color:"#fff"}}>{t.name || "Collector"}</div>
                  <div style={{fontSize:11.5,color:"rgba(255,255,255,0.4)"}}>
                    {t.matches.length > 0
                      ? <span style={{color:"#4ade80",fontWeight:700}}>{t.matches.length} card{t.matches.length===1?"":"s"} you want</span>
                      : <>{t.theirCards.length} card{t.theirCards.length===1?"":"s"} for trade</>}
                    {t.matches.length > 0 && <> {"\u00b7"} {t.theirCards.length} for trade</>}
                  </div>
                </div>
                <div style={{display:"flex",gap:7}}>
                  <button onClick={()=>onOffer(t)}
                    style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",border:"none",color:"#fff",borderRadius:9,padding:"8px 14px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                    {"\uD83D\uDD01"} Offer trade
                  </button>
                  <button onClick={()=>onViewProfile(t.uid)}
                    style={{background:"transparent",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.55)",borderRadius:9,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                    Profile
                  </button>
                </div>
              </div>

              {/* Matches first — this is what you came for. */}
              {t.matches.length > 0 && (
                <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:others.length>0?10:0}}>
                  {t.matches.map(c => <CardChip key={c.id} c={c} isMatch trader={t}/>)}
                </div>
              )}

              {/* Everything else they'd trade, folded away by default. */}
              {others.length > 0 && (
                <>
                  <button onClick={()=>setExpanded(open ? null : t.uid)}
                    style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.4)",fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",padding:0}}>
                    {open ? "\u25BE" : "\u25B8"} {others.length} other card{others.length===1?"":"s"} they'd trade
                  </button>
                  {open && (
                    <div style={{display:"flex",gap:7,flexWrap:"wrap",marginTop:9}}>
                      {others.map(c => <CardChip key={c.id} c={c} trader={t}/>)}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MarketTab({ onMarkTraded, onEditPackage, onAddSideToTrade, onUnacceptTrade, deckLockedForTrade={}, user, myListings, listings, onViewProfile, WEAPON_COLORS, allMyOffers, marketSales, trackingInputs, setTrackingInputs, setListModal, setOfferModal, setOfferAmt, setOfferNote, setOfferSent, setCounterModal, setCounterAmt, buyNow, respondOffer, unsellListing, saveTracking, setSigningIn, setActiveTab, inp , listType, cards, owned, search, removeListing, tradeIndex=[], wantList={}, tradeBait={}, tradeOffers=[], onSendTrade, onRespondTrade, onCancelTrade, onTradeTracking, onTradeReceived, initialView}) {
  // Sale and trade are the same idea in different currencies, so they sit side by side.
  const [mktView, setMktView] = useState(initialView || "sale");   // "sale" | "trade" | "trades"
  // My trade packages (shareable links) — loaded here so they live in the marketplace too.
  const [mktPkgs, setMktPkgs] = useState(null);
  const [mktPkgsBusy, setMktPkgsBusy] = useState(false);
  const [mktEditId,   setMktEditId]   = useState(null);   // package id being edited
  const [mktEditVals, setMktEditVals] = useState({title:"",note:"",contact:""});
  const [mktEditBusy, setMktEditBusy] = useState(false);
  function mktStartEdit(pk) {
    setMktEditId(pk.id);
    setMktEditVals({ title: pk.title||"", note: pk.note||"", contact: pk.contact||"" });
  }
  async function mktSaveEdit(pid) {
    setMktEditBusy(true);
    try {
      await setDoc(doc(db,"boba_trade_packages",pid), { title: mktEditVals.title.trim()||"Trade package", note: mktEditVals.note.trim(), contact: mktEditVals.contact.trim() }, { merge:true });
      setMktPkgs(list => (list||[]).map(p => p.id===pid ? { ...p, title: mktEditVals.title.trim()||"Trade package", note: mktEditVals.note.trim(), contact: mktEditVals.contact.trim() } : p));
      setMktEditId(null);
    } catch(e){ alert("Couldn't save: "+(e?.message||e)); }
    setMktEditBusy(false);
  }
  useEffect(() => {
    if (mktView !== "packages" || !user || mktPkgs !== null) return;
    setMktPkgsBusy(true);
    (async () => {
      try {
        const snap = await getDocs(query(collection(db,"boba_trade_packages"), where("ownerUid","==",user.uid)));
        const rows = snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>{ const ac=a.status==="completed"?1:0, bc=b.status==="completed"?1:0; if(ac!==bc) return ac-bc; return String(b.createdAt||"").localeCompare(String(a.createdAt||"")); });
        setMktPkgs(rows);
      } catch(e){ console.error("load packages (market) failed:", e); setMktPkgs([]); }
      setMktPkgsBusy(false);
    })();
  }, [mktView, user, mktPkgs]);
  async function mktDeletePkg(pid) {
    if (!window.confirm("Delete this trade package? The share link will stop working.")) return;
    try { await deleteDoc(doc(db,"boba_trade_packages",pid)); setMktPkgs(l=>(l||[]).filter(p=>p.id!==pid)); }
    catch(e){ alert("Couldn't delete: "+(e?.message||e)); }
  }
  const [mktTradingId, setMktTradingId] = useState(null);
  async function mktMarkTraded(pk) {
    const count = (pk.items||[]).reduce((s,it)=>s+(parseInt(it.qty)||1),0);
    if (!window.confirm(`Mark this package as traded?\n\nThis removes ${count} card${count===1?"":"s"} from your collection and logs them to your Vault Report as traded. This can't be undone in bulk.`)) return;
    setMktTradingId(pk.id);
    try {
      await onMarkTraded(pk);
      // Don't DESTROY the package on completion — that killed the share link (both parties got
      // "this trade doesn't exist") and erased the only record of the deal. Instead mark it
      // completed: the link stays alive as a receipt, and it moves to the Completed section below.
      try {
        await updateDoc(doc(db,"boba_trade_packages",pk.id), { status:"completed", completedAt: new Date().toISOString() });
      } catch(e){ console.error("mark package completed failed:", e); }
      setMktPkgs(l=>(l||[]).map(p=>p.id===pk.id ? { ...p, status:"completed", completedAt: new Date().toISOString() } : p));
    } catch(e){ alert("Couldn't complete: "+(e?.message||e)); }
    setMktTradingId(null);
  }

  // A trade notification has to be able to DROP you on the trades view. Without this the View
  // button just opened the marketplace on For Sale, and the offer \u2014 sitting in My Trades \u2014 was
  // nowhere to be seen. Which is exactly the "it worked until I tried to accept" failure.
  useEffect(() => { if (initialView) setMktView(initialView); }, [initialView]);
  // Card thumbnails in listings are 54x72 \u2014 you cannot judge a foil, a print line or a corner at
  // that size, which is a real problem when you're being asked to hand over money for it. Click any
  // card to see it properly.
  const [zoomCard, setZoomCard] = useState(null);   // {image, name, sub}
  const [offerTo,  setOfferTo]  = useState(null);   // trader we are building an offer for
  // A card the user clicked directly, plus whether they came via the "$ Offer" button so the
  // cash field can take focus. Saves hunting for the card in a long list.
  const [offerPreselect, setOfferPreselect] = useState(null);
  const [offerCashFirst, setOfferCashFirst] = useState(false);

  // ── Trade matching ──────────────────────────────────────────────────────────────────────────
  // For each person who opted in, which of their flagged cards are on MY want list? That intersection
  // IS the feature — not "here is a stranger's card list", but "this person has what you need".
  //
  // Traders with no overlap still show, below the matches: you might spot something you want but never
  // got round to adding to your want list.
  const cardById = useMemo(() => {
    const m = new Map();
    for (const c of cards) m.set(c.id, c);
    return m;
  }, [cards]);

  // ── Cards already committed to a live trade ──────────────────────────────────────────────────
  // A card promised in a pending or accepted trade is spoken for. Leaving it on offer means you can
  // promise the same physical card to three people and only be able to honour one — which is a good
  // way to get a reputation you can't undo.
  //
  // Keyed by "uid:cardId" so it locks the card FOR THAT PERSON, not globally: two collectors can
  // each have their own copy of the same card, and one of them trading it shouldn't withdraw the
  // other's.
  //
  // Only pending and accepted lock. Declined, cancelled and complete release the card again.
  const lockedCards = useMemo(() => {
    const locked = new Set();
    for (const t of tradeOffers) {
      if (t.status !== "pending" && t.status !== "accepted") continue;
      (t.giving  || []).forEach(c => locked.add(`${t.fromUid}:${c.id}`));
      (t.getting || []).forEach(c => locked.add(`${t.toUid}:${c.id}`));
    }
    return locked;
  }, [tradeOffers]);

  const traders = useMemo(() => {

    const q = (search||"").toLowerCase();

    return tradeIndex

      .filter(t => t.uid !== user?.uid)                      // your own entry is not a match

      .map(t => {

        // Drop anything they've already committed elsewhere.

        const theirCards = (t.cardIds||[])

          .filter(id => !lockedCards.has(`${t.uid}:${id}`))

          .map(id => cardById.get(id)).filter(Boolean);

        const matches = theirCards.filter(c => wantList[c.id]);

        return { ...t, theirCards, matches };

      })
      .filter(t => t.theirCards.length > 0)
      .filter(t => !q
        || (t.name||"").toLowerCase().includes(q)
        || t.theirCards.some(c => `${c.hero||""} ${c.playName||""} ${c.cardNum||""} ${c.treatment||""} ${c.weapon||""}`.toLowerCase().includes(q)))
      .sort((a,b) => (b.matches.length - a.matches.length) || (b.theirCards.length - a.theirCards.length));
  }, [tradeIndex, cardById, wantList, user, search, lockedCards]);

  const matchCount = traders.reduce((n,t) => n + t.matches.length, 0);

  return (
          <div>
            {/* Sale / trade switch */}
            <div style={{display:"flex",gap:8,marginBottom:18}}>
              {[["sale","\uD83D\uDCB0 For Sale", listings.length],
                ["trade","\uD83D\uDD01 For Trade", matchCount || traders.length],
                ["trades","\uD83D\uDCE6 My Trades", tradeOffers.filter(t=>(t.status==="pending"&&t.toUid===user?.uid)||t.status==="accepted").length],
                ["packages","\uD83E\uDD1D My Packages", 0]].map(([v,label,n])=>(
                <button key={v} onClick={()=>setMktView(v)}
                  style={{flex:1,background:mktView===v?"rgba(232,49,122,0.12)":"transparent",border:`1px solid ${mktView===v?"#E8317A":"rgba(255,255,255,0.1)"}`,color:mktView===v?"#E8317A":"rgba(255,255,255,0.45)",borderRadius:10,padding:"10px 14px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
                  {label}
                  {n>0 && <span style={{fontSize:11,background:mktView===v?"#E8317A":"rgba(255,255,255,0.1)",color:mktView===v?"#fff":"rgba(255,255,255,0.5)",borderRadius:20,padding:"1px 7px",fontWeight:800}}>{n}</span>}
                </button>
              ))}
            </div>

            {mktView==="packages" ? (
              <div>
                <div style={{fontSize:12.5,color:"rgba(255,255,255,0.5)",marginBottom:14,lineHeight:1.5}}>
                  Shareable trade offers you can send to anyone — they don't need a Bazooka account. Build one from your collection with select mode {"\u2192"} {"\uD83E\uDD1D"} Trade Package.
                </div>
                {mktPkgsBusy ? (
                  <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",padding:"24px 0",textAlign:"center"}}>Loading{"\u2026"}</div>
                ) : !mktPkgs || mktPkgs.length===0 ? (
                  <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",padding:"30px 0",textAlign:"center"}}>No trade packages yet.</div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:11}}>
                    {mktPkgs.map(pk => {
                      const url = `${window.location.origin}/trade/${pk.id}`;
                      const count = (pk.items||[]).reduce((s,it)=>s+(parseInt(it.qty)||1),0);
                      const thumbs = (pk.items||[]).filter(it=>it.imageUrl).slice(0,5);
                      return (
                        <div key={pk.id} style={{background:"rgba(0,0,0,0.25)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:11,padding:"13px 15px"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
                            <div style={{minWidth:0}}>
                              <div style={{fontSize:15,fontWeight:800,color:"#fff",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{pk.title||"Trade package"}</div>
                              <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginTop:2}}>{count} card{count===1?"":"s"}{pk.createdAt?` \u00b7 ${new Date(pk.createdAt).toLocaleDateString()}`:""}</div>
                            </div>
                            <div style={{display:"flex",gap:6,flexShrink:0}}>
                              {pk.status !== "completed" && <button onClick={()=>{ onEditPackage(pk); setMktPkgs(null); }} style={{background:"rgba(123,156,255,0.12)",border:"1px solid rgba(123,156,255,0.4)",color:"#7B9CFF",borderRadius:7,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Edit</button>}
                            <button onClick={()=>mktDeletePkg(pk.id)} style={{background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.4)",color:"#f87171",borderRadius:7,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",flexShrink:0}}>Delete</button>
                            </div>
                          </div>
                          {mktEditId===pk.id && (
                            <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid rgba(255,255,255,0.08)",display:"flex",flexDirection:"column",gap:9}}>
                              <div>
                                <label style={{fontSize:10.5,fontWeight:800,color:"#7B9CFF",display:"block",marginBottom:4}}>Title</label>
                                <input value={mktEditVals.title} onChange={e=>setMktEditVals(v=>({...v,title:e.target.value}))} style={{width:"100%",background:"#0e0e13",color:"#eee",border:"1px solid rgba(255,255,255,0.15)",borderRadius:7,padding:"7px 10px",fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
                              </div>
                              <div>
                                <label style={{fontSize:10.5,fontWeight:800,color:"#7B9CFF",display:"block",marginBottom:4}}>Note</label>
                                <textarea value={mktEditVals.note} onChange={e=>setMktEditVals(v=>({...v,note:e.target.value}))} rows={2} style={{width:"100%",background:"#0e0e13",color:"#eee",border:"1px solid rgba(255,255,255,0.15)",borderRadius:7,padding:"7px 10px",fontSize:13,fontFamily:"inherit",boxSizing:"border-box",resize:"vertical"}}/>
                              </div>
                              <div>
                                <label style={{fontSize:10.5,fontWeight:800,color:"#7B9CFF",display:"block",marginBottom:4}}>Contact</label>
                                <input value={mktEditVals.contact} onChange={e=>setMktEditVals(v=>({...v,contact:e.target.value}))} style={{width:"100%",background:"#0e0e13",color:"#eee",border:"1px solid rgba(255,255,255,0.15)",borderRadius:7,padding:"7px 10px",fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
                              </div>
                              <div style={{display:"flex",gap:8}}>
                                <button onClick={()=>mktSaveEdit(pk.id)} disabled={mktEditBusy} style={{background:"#4ade80",color:"#06240f",border:"none",borderRadius:7,padding:"8px 16px",fontSize:12,fontWeight:800,cursor:mktEditBusy?"wait":"pointer",fontFamily:"inherit"}}>{mktEditBusy?"Saving\u2026":"Save changes"}</button>
                                <button onClick={()=>setMktEditId(null)} disabled={mktEditBusy} style={{background:"transparent",border:"1px solid #333",color:"#888",borderRadius:7,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                              </div>
                            </div>
                          )}
                          {thumbs.length>0 && (
                            <div style={{display:"flex",gap:5,marginTop:10}}>
                              {thumbs.map((it,ti)=>(<img key={ti} src={it.imageUrl} alt="" style={{width:38,height:51,objectFit:"cover",borderRadius:5,border:"1px solid rgba(255,255,255,0.1)"}}/>))}
                              {count>thumbs.length && <div style={{width:38,height:51,borderRadius:5,background:"rgba(255,255,255,0.05)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"rgba(255,255,255,0.5)"}}>+{count-thumbs.length}</div>}
                            </div>
                          )}
                          <div style={{display:"flex",gap:7,marginTop:11}}>
                            <input readOnly value={url} onClick={e=>e.target.select()} style={{flex:1,background:"#0e0e13",color:"#7B9CFF",border:"1px solid rgba(123,156,255,0.25)",borderRadius:7,padding:"6px 9px",fontSize:11,fontFamily:"monospace",minWidth:0}}/>
                            <button onClick={async()=>{ try{ await navigator.clipboard.writeText(url);}catch(e){} }} style={{background:"rgba(74,222,128,0.15)",color:"#4ade80",border:"1px solid rgba(74,222,128,0.4)",borderRadius:7,padding:"0 12px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Copy</button>
                            <a href={url} target="_blank" rel="noreferrer" style={{background:"rgba(123,156,255,0.12)",color:"#7B9CFF",border:"1px solid rgba(123,156,255,0.3)",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:800,textDecoration:"none",fontFamily:"inherit",whiteSpace:"nowrap"}}>Open</a>
                          </div>
                      {pk.status === "completed" ? (
                        <div style={{width:"100%",marginTop:8,background:"rgba(74,222,128,0.1)",border:"1px solid rgba(74,222,128,0.3)",borderRadius:8,padding:"9px 12px",fontSize:12.5,fontWeight:700,color:"#4ade80",textAlign:"center"}}>
                          {"\u2705"} Traded{pk.completedAt ? ` \u00b7 ${new Date(pk.completedAt).toLocaleDateString()}` : ""} {"\u00b7"} link kept as receipt
                        </div>
                      ) : (
                        <button onClick={()=>mktMarkTraded(pk)} disabled={mktTradingId===pk.id}
                          style={{width:"100%",marginTop:8,background:mktTradingId===pk.id?"#333":"rgba(74,222,128,0.14)",border:"1px solid rgba(74,222,128,0.45)",color:mktTradingId===pk.id?"#888":"#4ade80",borderRadius:8,padding:"9px",fontSize:12.5,fontWeight:800,cursor:mktTradingId===pk.id?"wait":"pointer",fontFamily:"inherit"}}>
                          {mktTradingId===pk.id ? "Marking traded\u2026" : "\u2705 Mark as Traded (remove from collection)"}
                        </button>
                      )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : mktView==="trades" ? (
            
              <TradesPanel user={user} tradeOffers={tradeOffers}
                onRespond={onRespondTrade} onCancel={onCancelTrade}
                onTracking={onTradeTracking} onReceived={onTradeReceived} cards={cards} owned={owned} onAddSide={onAddSideToTrade} onUnaccept={onUnacceptTrade} deckLockedForTrade={deckLockedForTrade}/>
            ) : mktView==="trade" ? (
              <TradeView user={user} traders={traders} matchCount={matchCount}
                wantList={wantList} onViewProfile={onViewProfile} onZoom={setZoomCard}
                onOffer={(t,card)=>{ setOfferPreselect(card||null); setOfferCashFirst(false); setOfferTo(t); }}
                onCashOffer={(t,card)=>{ setOfferPreselect(card||null); setOfferCashFirst(true); setOfferTo(t); }}
                setActiveTab={setActiveTab} setSigningIn={setSigningIn} WEAPON_COLORS={WEAPON_COLORS}/>
            ) : (
            <>
            {/* My listings */}
            {user&&myListings.length>0&&(
              <div style={{marginBottom:24}}>
                <div style={{fontSize:14,fontWeight:800,color:"#4ade80",marginBottom:12}}>Your Listings ({myListings.length})</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:10}}>
                  {myListings.map(l=>(
                    <div key={l.id} className="market-card" style={{background:"rgba(10,26,10,0.6)",border:"1px solid rgba(74,222,128,0.2)",borderRadius:16,padding:16,backdropFilter:"blur(10px)",transition:"all 0.2s",position:"relative"}}>
                      <div style={{display:"flex",gap:12,marginBottom:10}}>
                        {l.cardImage?<img src={l.cardImage} alt={l.cardName} style={{width:48,height:64,objectFit:"cover",borderRadius:8,flexShrink:0}}/>:<div style={{width:48,height:64,background:"rgba(255,255,255,0.05)",borderRadius:8,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"rgba(255,255,255,0.3)"}}>IMG</div>}
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)"}}>{l.cardName}</div>
                          <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{l.cardTreatment} · {l.cardPower}{"\u26A1"}</div>
                          <div style={{fontSize:12,fontWeight:700,color:"#4ade80",marginTop:4}}>${(l.askingPrice||0).toFixed(2)}</div>
                          <div style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>{l.listType==="trade"?"For Trade":l.listType==="either"?"Sale/Trade":"For Sale"}</div>
                        </div>
                      </div>
                      {l.offerCount>0&&<div style={{fontSize:11,color:"#FBBF24",fontWeight:700,marginBottom:8}}>{"\uD83E\uDD1D"}{l.offerCount} offer{l.offerCount!==1?"s":""}</div>}
                      <button onClick={()=>removeListing(l.id)} style={{width:"100%",background:"transparent",border:"1px solid rgba(232,49,122,0.2)",color:"rgba(232,49,122,0.5)",borderRadius:8,padding:"5px 0",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Remove Listing</button>
                    </div>
                  ))}
                </div>
              </div>
            )}


            {/* Sold Listings */}
            {user&&marketSales.filter(s=>s.sellerUid===user.uid).length>0&&(()=>{
              const mySales=marketSales.filter(s=>s.sellerUid===user.uid);
              const totalRevenue=mySales.reduce((acc,s)=>acc+(s.price||0),0);
              const CARRIERS=["USPS","UPS","FedEx","DHL","Other"];
              return (
                <div style={{marginBottom:24}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                    <div style={{fontSize:14,fontWeight:800,color:"#4ade80"}}>{"\uD83D\uDCB8 Sold"}</div>
                    <span style={{background:"rgba(74,222,128,0.1)",border:"1px solid rgba(74,222,128,0.2)",borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:800,color:"#4ade80"}}>{mySales.length} card{mySales.length!==1?"s":""}</span>
                    <span style={{marginLeft:"auto",fontSize:13,fontWeight:800,color:"#4ade80"}}>{"$"}{totalRevenue.toFixed(2)} total</span>
                  </div>
                  <div style={{background:"rgba(0,0,0,0.3)",border:"1px solid rgba(74,222,128,0.1)",borderRadius:16,overflow:"hidden"}}>
                    {mySales.map((s,i)=>{
                      const ti=trackingInputs[s.id]||{num:s.trackingNumber||"",carrier:s.carrier||"USPS"};
                      const hasTracking=s.trackingNumber;
                      return (
                        <div key={s.soldAt+i} style={{borderBottom:i<mySales.length-1?"1px solid rgba(255,255,255,0.04)":"none",background:i%2===0?"transparent":"rgba(255,255,255,0.01)"}}>
                          <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px"}}>
                            {s.cardImage?<img src={s.cardImage} alt={s.cardName} style={{width:32,height:43,objectFit:"cover",borderRadius:6,flexShrink:0}}/>:<div style={{width:32,height:43,background:"rgba(255,255,255,0.04)",borderRadius:6,flexShrink:0}}/>}
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:700,color:"var(--bz-ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.cardName}</div>
                              <div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>{s.cardTreatment} {"\u00B7"} to {s.buyerName||"buyer"}</div>
                              {hasTracking&&(
                                <div style={{fontSize:10,color:"#7B9CFF",marginTop:2}}>
                                  {s.carrier||"USPS"}: {s.trackingNumber}
                                </div>
                              )}
                            </div>
                            <div style={{textAlign:"right",flexShrink:0}}>
                              <div style={{fontSize:15,fontWeight:900,color:"#4ade80"}}>{"$"}{(s.price||0).toFixed(2)}</div>
                              <div style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>{s.soldDate||"--"}</div>
                              <button onClick={e=>{e.stopPropagation();unsellListing(s);}} style={{marginTop:4,background:"transparent",border:"1px solid rgba(232,49,122,0.2)",color:"rgba(232,49,122,0.4)",borderRadius:6,padding:"2px 8px",fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>Reverse</button>
                            </div>
                          </div>
                          {/* Tracking input row */}
                          {s.id&&(
                            <div style={{display:"flex",gap:6,padding:"0 16px 10px",alignItems:"center"}}>
                              <select value={ti.carrier} onChange={e=>setTrackingInputs(prev=>({...prev,[s.id]:{...ti,carrier:e.target.value}}))}
                                style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:6,padding:"4px 6px",fontSize:11,color:"rgba(255,255,255,0.5)",fontFamily:"inherit",cursor:"pointer"}}>
                                {CARRIERS.map(c=><option key={c} value={c}>{c}</option>)}
                              </select>
                              <input value={ti.num} onChange={e=>setTrackingInputs(prev=>({...prev,[s.id]:{...ti,num:e.target.value}}))}
                                placeholder={hasTracking?"Update tracking #":"Add tracking #"}
                                style={{flex:1,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:6,padding:"4px 10px",fontSize:11,color:"var(--bz-ink)",fontFamily:"inherit",outline:"none"}}/>
                              <button onClick={()=>saveTracking(s.id,ti.num,ti.carrier)}
                                disabled={!ti.num.trim()}
                                style={{background:ti.num.trim()?"rgba(123,156,255,0.15)":"rgba(255,255,255,0.03)",border:"1px solid "+(ti.num.trim()?"rgba(123,156,255,0.3)":"rgba(255,255,255,0.06)"),color:ti.num.trim()?"#7B9CFF":"rgba(255,255,255,0.2)",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:ti.num.trim()?"pointer":"default",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                                {hasTracking?"Update":"Save"}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Bought / Purchases */}
            {user&&marketSales.filter(s=>s.buyerUid===user.uid).length>0&&(()=>{
              const myBuys=marketSales.filter(s=>s.buyerUid===user.uid);
              const totalSpent=myBuys.reduce((acc,s)=>acc+(s.price||0),0);
              return (
                <div style={{marginBottom:24}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                    <div style={{fontSize:14,fontWeight:800,color:"#7B9CFF"}}>{"\uD83D\uDCE6 Purchased"}</div>
                    <span style={{background:"rgba(123,156,255,0.1)",border:"1px solid rgba(123,156,255,0.2)",borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:800,color:"#7B9CFF"}}>{myBuys.length} card{myBuys.length!==1?"s":""}</span>
                    <span style={{marginLeft:"auto",fontSize:13,fontWeight:800,color:"#7B9CFF"}}>{"$"}{totalSpent.toFixed(2)} spent</span>
                  </div>
                  <div style={{background:"rgba(0,0,0,0.3)",border:"1px solid rgba(123,156,255,0.1)",borderRadius:16,overflow:"hidden"}}>
                    {myBuys.map((s,i)=>{
                      const shipped=s.trackingNumber;
                      return (
                        <div key={s.soldAt+"b"+i} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",borderBottom:i<myBuys.length-1?"1px solid rgba(255,255,255,0.04)":"none",background:i%2===0?"transparent":"rgba(255,255,255,0.01)"}}>
                          {s.cardImage?<img src={s.cardImage} alt={s.cardName} style={{width:32,height:43,objectFit:"cover",borderRadius:6,flexShrink:0}}/>:<div style={{width:32,height:43,background:"rgba(255,255,255,0.04)",borderRadius:6,flexShrink:0}}/>}
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:700,color:"var(--bz-ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.cardName}</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>from {s.sellerName||"seller"} {"\u00B7"} {s.soldDate||"--"}</div>
                            {shipped?(
                              <div style={{display:"flex",alignItems:"center",gap:6,marginTop:3}}>
                                <span style={{fontSize:10,fontWeight:700,color:"#4ade80"}}>{"\u2705 Shipped"}</span>
                                <span style={{fontSize:10,color:"#7B9CFF"}}>{s.carrier||""} {s.trackingNumber}</span>
                              </div>
                            ):(
                              <div style={{fontSize:10,color:"rgba(251,191,36,0.6)",marginTop:2}}>{"\u23F3 Awaiting shipment"}</div>
                            )}
                          </div>
                          <div style={{textAlign:"right",flexShrink:0}}>
                            <div style={{fontSize:15,fontWeight:900,color:"#7B9CFF"}}>{"$"}{(s.price||0).toFixed(2)}</div>
                            {shipped&&(
                              <a href={"https://www.google.com/search?q="+encodeURIComponent((s.carrier||"")+" "+s.trackingNumber+" tracking")} target="_blank" rel="noreferrer"
                                style={{fontSize:10,color:"#7B9CFF",textDecoration:"none",display:"block",marginTop:2}}>Track {"\u2192"}</a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Offer Inbox -- all pending offers on my listings grouped by listing */}
            {user&&allMyOffers.length>0&&(()=>{
              // Group offers by listingId
              const byListing={};
              allMyOffers.forEach(o=>{
                if(!byListing[o.listingId]) byListing[o.listingId]={listingId:o.listingId,cardName:o.cardName,cardImage:o.cardImage||null,offers:[]};
                byListing[o.listingId].offers.push(o);
              });
              const groups=Object.values(byListing);
              return (
                <div style={{marginBottom:24}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                    <div style={{fontSize:14,fontWeight:800,color:"#FBBF24"}}>{"\uD83D\uDCE5 Offer Inbox"}</div>
                    <span style={{background:"rgba(251,191,36,0.15)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:800,color:"#FBBF24"}}>{allMyOffers.length}</span>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:16}}>
                    {groups.map(g=>(
                      <div key={g.listingId} style={{background:"rgba(251,191,36,0.03)",border:"1px solid rgba(251,191,36,0.12)",borderRadius:16,overflow:"hidden"}}>
                        {/* Listing header */}
                        <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderBottom:"1px solid rgba(251,191,36,0.08)",background:"rgba(251,191,36,0.04)"}}>
                          {g.cardImage&&<img src={g.cardImage} alt={g.cardName} style={{width:32,height:43,objectFit:"cover",borderRadius:6,flexShrink:0}}/>}
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)"}}>{g.cardName}</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>{g.offers.length} offer{g.offers.length!==1?"s":""} {"\u00B7"} Best: {"$"}{(g.offers[0]?.offerAmount||0).toFixed(2)}</div>
                          </div>
                        </div>
                        {/* Offer rows sorted highest to lowest */}
                        {g.offers.map((o,idx)=>{
                          const isBest=idx===0;
                          const pctOfBest=g.offers[0].offerAmount>0?Math.round(o.offerAmount/g.offers[0].offerAmount*100):100;
                          return (
                            <div key={o.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",borderBottom:idx<g.offers.length-1?"1px solid rgba(255,255,255,0.04)":"none",background:isBest?"rgba(74,222,128,0.04)":"transparent"}}>
                              {/* Rank */}
                              <div style={{width:22,height:22,borderRadius:"50%",background:isBest?"rgba(74,222,128,0.15)":"rgba(255,255,255,0.05)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:isBest?"#4ade80":"rgba(255,255,255,0.3)",flexShrink:0}}>
                                {idx+1}
                              </div>
                              {/* Buyer name */}
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:13,fontWeight:700,color:"var(--bz-ink)"}}>{o.buyerName}</div>
                                {o.note&&<div style={{fontSize:11,color:"rgba(255,255,255,0.35)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:1}}>{o.note}</div>}
                                <div style={{fontSize:10,color:"rgba(255,255,255,0.2)",marginTop:1}}>{o.createdAt?new Date(o.createdAt).toLocaleDateString([],{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}):""}</div>
                              </div>
                              {/* Amount + bar */}
                              <div style={{textAlign:"right",flexShrink:0}}>
                                <div style={{fontSize:16,fontWeight:900,color:isBest?"#4ade80":"var(--bz-ink)"}}>{"$"}{(o.offerAmount||0).toFixed(2)}</div>
                                {!isBest&&<div style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>{pctOfBest}{"% of best"}</div>}
                                {isBest&&<div style={{fontSize:10,color:"#4ade80",fontWeight:700}}>Best offer</div>}
                              </div>
                              {/* Action buttons */}
                              <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0}}>
                                <button onClick={()=>respondOffer(o,"accepted")}
                                  style={{background:"rgba(74,222,128,0.15)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                                  Accept
                                </button>
                                <button onClick={()=>{setCounterModal(o);setCounterAmt("");}}
                                  style={{background:"rgba(251,191,36,0.1)",border:"1px solid rgba(251,191,36,0.2)",color:"#FBBF24",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                                  Counter
                                </button>
                                <button onClick={()=>respondOffer(o,"declined")}
                                  style={{background:"transparent",border:"1px solid rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.3)",borderRadius:7,padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                                  Decline
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Community listings */}
            <div>
              <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)",marginBottom:12}}>
                {listings.length===0?"No active listings right now":"Community Listings"} {listings.length>0&&<span style={{fontSize:12,color:"rgba(255,255,255,0.3)",fontWeight:400}}>({listings.length})</span>}
              </div>
              {listings.length===0?(
                <div style={{textAlign:"center",padding:80,color:"rgba(255,255,255,0.2)"}}>
                  <div style={{fontSize:48,marginBottom:16}}>{"\uD83D\uDCB0"}</div>
                  <div style={{fontSize:16,fontWeight:700}}>No cards listed yet</div>
                  <div style={{fontSize:13,marginTop:8}}>{"List your owned cards from the Cards tab using the \uD83D\uDCB0 button"}</div>
                </div>
              ):(
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:10}}>
                  {listings.filter(l=>l.sellerUid!==user?.uid).map(l=>{
                    const wc=WEAPON_COLORS[l.cardWeapon]||"#444";
                    return (
                      <div key={l.id} className="market-card" style={{background:"rgba(255,255,255,0.02)",border:`1px solid ${wc}22`,borderRadius:16,padding:16,backdropFilter:"blur(10px)",transition:"all 0.2s",cursor:"pointer"}} onClick={()=>{if(!user){setSigningIn(true);return;}setOfferModal(l);setOfferAmt("");setOfferNote("");}}>
                        <div style={{display:"flex",gap:12,marginBottom:10}}>
                          {l.cardImage?<img src={l.cardImage} alt={l.cardName} style={{width:54,height:72,objectFit:"cover",borderRadius:10,flexShrink:0,boxShadow:`0 4px 16px ${wc}33`}}/>:<div style={{width:54,height:72,background:"rgba(255,255,255,0.04)",borderRadius:10,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"rgba(255,255,255,0.3)"}}>IMG</div>}
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)",marginBottom:2}}>{l.cardName}</div>
                            <div style={{fontSize:11,color:wc,fontWeight:700,marginBottom:2}}>{l.cardWeapon}</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>{l.cardTreatment}</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>{l.cardPower}{"\u26A1"} · {l.setName}</div>
                          </div>
                        </div>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                          <div>
                            {l.isOBO?(
                              <div style={{fontSize:15,fontWeight:900,color:"#4ade80",letterSpacing:-0.5}}>{"\uD83D\uDCE8 Best Offer"}</div>
                            ):(
                              <div style={{fontSize:18,fontWeight:900,color:"#4ade80"}}>${(l.askingPrice||0).toFixed(2)}</div>
                            )}
                            <div style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>{l.isOBO?"Offers only":l.listType==="trade"?"Trade only":l.listType==="either"?"Sale or Trade":"For Sale"}</div>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>by <SellerBadge uid={l.sellerUid} name={l.sellerName} marketSales={marketSales} onViewProfile={onViewProfile}/></div>
                            {l.offerCount>0&&<div style={{fontSize:10,color:"#FBBF24"}}>{l.offerCount} offer{l.offerCount!==1?"s":""}</div>}
                          </div>
                        </div>
                        {l.notes&&<div style={{fontSize:11,color:"rgba(255,255,255,0.3)",fontStyle:"italic",marginBottom:10,borderTop:"1px solid rgba(255,255,255,0.05)",paddingTop:8}}>{l.notes}</div>}
                        <div style={{display:"flex",gap:6,marginTop:2}}>
                          {!l.isOBO&&l.askingPrice>0&&l.sellerUid!==user?.uid&&(
                            <button onClick={e=>{e.stopPropagation();buyNow(l);}}
                              style={{flex:1,background:"linear-gradient(135deg,rgba(74,222,128,0.2),rgba(34,197,94,0.15))",border:"1px solid rgba(74,222,128,0.35)",color:"#4ade80",borderRadius:10,padding:"8px 0",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                              {"\uD83D\uDED2 Buy Now"}
                            </button>
                          )}
                          {l.sellerUid!==user?.uid&&(
                            <button onClick={e=>{e.stopPropagation();if(!user){setSigningIn(true);return;}setOfferModal(l);setOfferAmt("");setOfferNote("");setOfferSent(false);}}
                              style={{flex:1,background:"linear-gradient(135deg,rgba(251,191,36,0.15),rgba(245,158,11,0.1))",border:"1px solid rgba(251,191,36,0.2)",color:"#FBBF24",borderRadius:10,padding:"8px 0",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                              {"\uD83E\uDD1D "+(l.isOBO?"Make Offer":"Offer")}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            </>
            )}
      {/* Offer builder — opened from a trader row in For Trade. */}
      {offerTo && (
        <TradeOfferModal trader={offerTo} cards={cards} owned={owned} tradeBait={tradeBait} preselect={offerPreselect} cashFirst={offerCashFirst}
          myTrades={tradeOffers} myUid={user?.uid} deckLocked={deckLockedForTrade}
          onSend={onSendTrade} onClose={()=>{ setOfferTo(null); setOfferPreselect(null); setOfferCashFirst(false); }}/>
      )}


      {/* Card lightbox. A 54x72 thumbnail can't tell you whether a foil is clean or a corner is
          dinged — and that's exactly what you need to know before spending money. */}
      {zoomCard && (
        <div onClick={()=>setZoomCard(null)}
          style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",zIndex:14800,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,cursor:"zoom-out"}}>
          <img src={zoomCard.image} alt={zoomCard.name}
            onClick={e=>e.stopPropagation()}
            style={{maxWidth:"min(92vw, 460px)",maxHeight:"76vh",objectFit:"contain",borderRadius:12,boxShadow:"0 20px 60px rgba(0,0,0,0.7)",cursor:"default"}}/>
          <div style={{marginTop:16,textAlign:"center"}}>
            <div style={{fontSize:16,fontWeight:800,color:"#fff"}}>{zoomCard.name}</div>
            {zoomCard.sub && <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginTop:3}}>{zoomCard.sub}</div>}
          </div>
          <button onClick={()=>setZoomCard(null)}
            style={{position:"fixed",top:20,right:20,background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",color:"#fff",width:38,height:38,borderRadius:"50%",fontSize:19,cursor:"pointer",fontFamily:"inherit",lineHeight:1}}>{"\u00D7"}</button>
        </div>
      )}
          </div>
  );
}

function LendForm({ members, myCards, onLend, inp, WEAPON_COLORS }) {
  const [borrowerUid, setBorrowerUid] = useState("");
  const [cardSearch, setCardSearch] = useState("");
  const [pickedCard, setPickedCard] = useState(null);
  const matches = cardSearch.trim()
    ? myCards.filter(c=>`${c.hero} ${c.cardNum} ${c.treatment} ${c.power}`.toLowerCase().includes(cardSearch.toLowerCase())).slice(0,6)
    : [];
  const borrower = members.find(m=>m.uid===borrowerUid);
  function submit() {
    if(!borrower||!pickedCard) return;
    onLend(borrower, pickedCard);
    setBorrowerUid(""); setCardSearch(""); setPickedCard(null);
  }
  return (
    <div style={{background:"rgba(123,156,255,0.05)",border:"1px solid rgba(123,156,255,0.2)",borderRadius:12,padding:12}}>
      <div style={{fontSize:11,fontWeight:700,color:"#7B9CFF",marginBottom:8}}>Lend one of your cards</div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"flex-start"}}>
        <select value={borrowerUid} onChange={e=>setBorrowerUid(e.target.value)} style={{...inp,fontSize:12,padding:"7px 9px",flex:"1 1 140px"}}>
          <option value="">Lend to…</option>
          {members.map(m=><option key={m.uid} value={m.uid}>{m.displayName}</option>)}
        </select>
        <div style={{flex:"2 1 220px",position:"relative"}}>
          {pickedCard?(
            <div style={{display:"flex",alignItems:"center",gap:8,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,padding:"6px 10px"}}>
              <span style={{fontSize:12,fontWeight:700,color:"#fff",flex:1}}>{pickedCard.hero} {pickedCard.power}⚡ {pickedCard.treatment||""}</span>
              <button onClick={()=>{setPickedCard(null);setCardSearch("");}} style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.4)",cursor:"pointer",fontSize:14}}>×</button>
            </div>
          ):(
            <>
              <input value={cardSearch} onChange={e=>setCardSearch(e.target.value)} placeholder="Search your card…" style={{...inp,fontSize:12,padding:"7px 9px",width:"100%"}}/>
              {matches.length>0&&(
                <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:5,background:"#16161f",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,marginTop:3,maxHeight:180,overflowY:"auto"}}>
                  {matches.map(c=>{const wc=WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";return (
                    <div key={c.id} onClick={()=>{setPickedCard(c);setCardSearch("");}} style={{padding:"7px 10px",fontSize:12,cursor:"pointer",borderBottom:"1px solid rgba(255,255,255,0.04)",color:"#ddd"}}>
                      <span style={{color:wc,fontWeight:700}}>{c.hero}</span> {c.power}⚡ · {c.treatment||"—"} {c.cardNum?`· #${c.cardNum}`:""}
                    </div>
                  );})}
                </div>
              )}
            </>
          )}
        </div>
        <button onClick={submit} disabled={!borrower||!pickedCard} style={{background:borrower&&pickedCard?"linear-gradient(135deg,#7B9CFF,#7B2FF7)":"rgba(255,255,255,0.05)",color:borrower&&pickedCard?"#fff":"rgba(255,255,255,0.3)",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:borrower&&pickedCard?"pointer":"not-allowed",fontFamily:"inherit",flexShrink:0}}>Log Loan</button>
      </div>
    </div>
  );
}

function TeamTab({ user, teams, activeTeam, setActiveTeam, newTeamName, setNewTeamName, inviteEmail, setInviteEmail, inviteStatus, setInviteStatus, teamInvites, moveTeamMember, deleteTeam, respondTeamInvite, createTeam, WEAPON_COLORS, setSigningIn, cards, owned, inp, inviteToTeam, teamDecks, savedDecks, submitDeckToTeam, withdrawTeamDeck, swapOffers, offerSwap, dismissSwapOffer, teamLoans, lendCard, markLoanReturned, deleteLoan }) {
  return (
          <div style={{maxWidth:960,margin:"0 auto"}}>
            {!user?(
              <div style={{textAlign:"center",padding:80}}>
                <div style={{fontSize:48,marginBottom:16}}>{"\uD83C\uDFC6"}</div>
                <button onClick={()=>setSigningIn(true)} style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:14,padding:"12px 28px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Sign in to create a team</button>
              </div>
            ):(
              <>
                {/* Create team form -- only show if no teams */}
                {teams.filter(t=>t.status!=="deleted").length===0&&(
                  <div style={{background:"rgba(168,85,247,0.04)",border:"1px solid rgba(168,85,247,0.2)",borderRadius:16,padding:24,marginBottom:16,backdropFilter:"blur(10px)"}}>
                    <div style={{fontSize:14,fontWeight:800,color:"#A855F7",marginBottom:12}}>{"\uD83C\uDFC6 Create Your Team"}</div>
                    <div style={{display:"flex",gap:8}}>
                      <input value={newTeamName} onChange={e=>setNewTeamName(e.target.value)} placeholder="Team name..." style={{...inp,flex:1}} onKeyDown={e=>e.key==="Enter"&&createTeam()}/>
                      <button onClick={createTeam} style={{background:"linear-gradient(135deg,#A855F7,#7B2FF7)",color:"#fff",border:"none",borderRadius:12,padding:"8px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 16px rgba(168,85,247,0.3)"}}>Create</button>
                    </div>
                  </div>
                )}

                {/* Team selector */}
                {teams.filter(t=>t.status!=="deleted").length>1&&(
                  <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
                    {teams.filter(t=>t.status!=="deleted").map(t=>(
                      <button key={t.id} onClick={()=>setActiveTeam(t)} style={{background:activeTeam?.id===t.id?"rgba(168,85,247,0.15)":"rgba(255,255,255,0.02)",border:`1.5px solid ${activeTeam?.id===t.id?"#A855F7":"rgba(255,255,255,0.08)"}`,color:activeTeam?.id===t.id?"#A855F7":"rgba(255,255,255,0.4)",borderRadius:10,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",backdropFilter:"blur(10px)"}}>
                        {t.name} ({(t.members||[]).length}/6)
                      </button>
                    ))}
                    <button onClick={()=>{setNewTeamName(" ");}} style={{background:"transparent",border:"1px dashed rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.3)",borderRadius:10,padding:"7px 16px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>+ New Team</button>
                  </div>
                )}

                {activeTeam&&activeTeam.status!=="deleted"&&activeTeam.memberUids&&(()=>{
                  const team=activeTeam;
                  const starters=team.starters||team.members?.slice(0,4)||[];
                  const bench=team.bench||team.members?.slice(4)||[];
                  const allMembers=[...starters,...bench];
                  const isOwner=team.createdBy===user.uid;

                  // Apex conflict detection across SUBMITTED DECKS only (never raw collections).
                  const dupKey2=c=>`${(c.hero||"").toLowerCase()}|${(c.variation||"").toLowerCase()}|${c.power||""}|${(c.weapon||"").toLowerCase()}`;
                  // Build each member's submitted apex cards from their team_decks entry.
                  const memberApex={}; // uid -> [cards]
                  allMembers.forEach(m=>{
                    const sub=teamDecks[`${team.id}_${m.uid}`];
                    if(!sub){ memberApex[m.uid]=null; return; } // hasn't submitted a deck
                    const ids=new Set(sub.cardIds||[]);
                    memberApex[m.uid]=cards.filter(c=>ids.has(c.id)&&parseFloat(c.power||0)>160);
                  });
                  const apexMap={};
                  allMembers.forEach(m=>{
                    (memberApex[m.uid]||[]).forEach(c=>{
                      const dk=dupKey2(c);
                      if(!apexMap[dk])apexMap[dk]={card:c,members:[],key:dk};
                      if(!apexMap[dk].members.includes(m.displayName)) apexMap[dk].members.push(m.displayName);
                    });
                  });
                  const conflicts=Object.values(apexMap).filter(x=>x.members.length>1);
                  const submittedCount=allMembers.filter(m=>teamDecks[`${team.id}_${m.uid}`]).length;

                  // My own coach: for each conflict, do I (from MY collection) have an unused apex
                  // alternative for the same weapon slot that isn't already in my submitted deck?
                  const mySub=teamDecks[`${team.id}_${user.uid}`];
                  const mySubIds=new Set(mySub?.cardIds||[]);
                  const myConflictKeys=new Set(conflicts.filter(cf=>cf.members.includes(user.displayName||user.email)).map(cf=>cf.key));
                  const mySwapSuggestions={}; // conflictKey -> [alternative cards I own]
                  conflicts.forEach(cf=>{
                    const wc=(cf.card.weapon||"").toLowerCase();
                    // alternatives: cards I own, apex, same weapon, different dupKey, not in my deck
                    const alts=cards.filter(c=>owned&&owned[c.id]&&parseFloat(c.power||0)>160&&(c.weapon||"").toLowerCase()===wc&&dupKey2(c)!==cf.key&&!mySubIds.has(c.id));
                    if(alts.length) mySwapSuggestions[cf.key]=alts;
                  });

                  // Can I help teammates? For conflicts I'm NOT part of, do I own an unused card
                  // that fills that slot? If so, *I* get prompted to offer it (nobody sees my cards).
                  const canHelpWith=conflicts.filter(cf=>{
                    if(cf.members.includes(user.displayName||user.email)) return false; // that's my own conflict
                    const wc=(cf.card.weapon||"").toLowerCase();
                    return cards.some(c=>owned&&owned[c.id]&&parseFloat(c.power||0)>160&&(c.weapon||"").toLowerCase()===wc&&!mySubIds.has(c.id));
                  });

                  // Drag state
                  let dragUid=null;

                  function MemberCard({m, slot, isDraggable}) {
                    const isMe=m.uid===user.uid;
                    // Only their SUBMITTED deck is visible — never their collection.
                    const sub=teamDecks[`${team.id}_${m.uid}`];
                    const submittedCards=sub?(sub.cardIds||[]).length:0;
                    const subApex=sub?cards.filter(c=>new Set(sub.cardIds||[]).has(c.id)&&parseFloat(c.power||0)>160):[];
                    const wc_starter="#A855F7", wc_bench="#7B9CFF";
                    const borderColor=slot==="starter"?wc_starter:wc_bench;
                    return (
                      <div
                        draggable={isDraggable}
                        onDragStart={e=>{e.dataTransfer.setData("memberUid",m.uid);e.dataTransfer.setData("fromSlot",slot);}}
                        style={{background:isMe?"rgba(168,85,247,0.06)":"rgba(255,255,255,0.02)",border:`1px solid ${isMe?`${borderColor}44`:"rgba(255,255,255,0.06)"}`,borderRadius:14,padding:14,cursor:isDraggable?"grab":"default",transition:"all 0.2s",userSelect:"none"}}
                        onMouseEnter={e=>{if(isDraggable)e.currentTarget.style.borderColor=`${borderColor}88`;}}
                        onMouseLeave={e=>{e.currentTarget.style.borderColor=isMe?`${borderColor}44`:"rgba(255,255,255,0.06)";}}>
                        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                          {m.photoURL?<img src={m.photoURL} alt="" style={{width:36,height:36,borderRadius:"50%",flexShrink:0,border:`2px solid ${isMe?borderColor:"rgba(255,255,255,0.1)"}`}}/>:<div style={{width:36,height:36,borderRadius:"50%",background:"rgba(255,255,255,0.05)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>{"\uD83D\uDC64"}</div>}
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:700,color:isMe?borderColor:"var(--bz-ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.displayName}{isMe?" (you)":""}</div>
                            <div style={{fontSize:11,color:sub?"#4ade80":"rgba(255,255,255,0.3)"}}>{sub?`✓ Deck submitted · ${submittedCards} cards`:"No deck submitted"}</div>
                          </div>
                          {isDraggable&&<span style={{fontSize:14,color:"rgba(255,255,255,0.2)"}}>{"\u283F"}</span>}
                        </div>
                        {subApex.length>0&&(
                          <div>
                            <div style={{fontSize:10,color:"#A855F7",fontWeight:700,marginBottom:4}}>Apex in deck ({subApex.length})</div>
                            <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                              {subApex.slice(0,5).map(c=>{const wc=WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";return <div key={c.id} title={`${c.hero} ${c.power} ${c.treatment}`} style={{background:`${wc}15`,border:`1px solid ${wc}33`,borderRadius:5,padding:"2px 6px",fontSize:9,color:wc,fontWeight:700}}>{c.hero?.split(" ")[0]} {c.power}</div>;})}
                              {subApex.length>5&&<span style={{fontSize:9,color:"rgba(255,255,255,0.2)"}}>+{subApex.length-5}</span>}
                            </div>
                          </div>
                        )}
                        {isMe&&(
                          <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid rgba(255,255,255,0.06)"}}>
                            {sub?(
                              <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                                <span style={{fontSize:11,color:"#4ade80",fontWeight:700}}>✓ {sub.deckName}</span>
                                <button onClick={()=>withdrawTeamDeck(team)} style={{fontSize:10,background:"rgba(232,49,122,0.1)",border:"1px solid rgba(232,49,122,0.3)",color:"#E8317A",borderRadius:6,padding:"3px 8px",cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>Withdraw</button>
                              </div>
                            ):(
                              (savedDecks||[]).length>0?(
                                <div>
                                  <div style={{fontSize:10,fontWeight:700,color:"#A855F7",marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>📥 Submit your deck to the team</div>
                                  <select onChange={e=>{ const d=(savedDecks||[]).find(x=>x.id===e.target.value); if(d) submitDeckToTeam(team,d); e.target.value=""; }} defaultValue="" style={{...inp,fontSize:12,padding:"8px 10px",width:"100%",border:"1px solid rgba(168,85,247,0.4)"}}>
                                    <option value="" disabled>Choose a deck…</option>
                                    {(savedDecks||[]).map(d=><option key={d.id} value={d.id}>{d.name} ({d.cardCount||d.cardIds?.length||0} cards)</option>)}
                                  </select>
                                </div>
                              ):(
                                <div style={{background:"rgba(168,85,247,0.06)",border:"1px solid rgba(168,85,247,0.25)",borderRadius:8,padding:"8px 10px"}}>
                                  <div style={{fontSize:11,color:"#C084FC",fontWeight:700,marginBottom:2}}>No saved decks yet</div>
                                  <div style={{fontSize:10,color:"rgba(255,255,255,0.5)",lineHeight:1.4}}>Go to <strong>Deck Builder</strong>, build a deck, name it, and hit <strong>💾 Save Deck</strong>. It'll show up here to submit.</div>
                                </div>
                              )
                            )}
                          </div>
                        )}
                        {/* Move buttons */}
                        {isOwner&&m.uid!==user.uid&&(
                          <div style={{display:"flex",gap:6,marginTop:10}}>
                            {slot==="starter"&&bench.length<2&&(
                              <button onClick={()=>moveTeamMember(team,m.uid,"to_bench")} style={{fontSize:10,background:"rgba(123,156,255,0.1)",border:"1px solid rgba(123,156,255,0.2)",color:"#7B9CFF",borderRadius:6,padding:"3px 8px",cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>{"\u2192 Bench"}</button>
                            )}
                            {slot==="bench"&&starters.length<4&&(
                              <button onClick={()=>moveTeamMember(team,m.uid,"to_starter")} style={{fontSize:10,background:"rgba(168,85,247,0.1)",border:"1px solid rgba(168,85,247,0.2)",color:"#A855F7",borderRadius:6,padding:"3px 8px",cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>{"\u2192 Starter"}</button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }

                  function DropZone({slot, label, color, members, maxCount}) {
                    const [over,setOver]=useState(false);
                    return (
                      <div
                        onDragOver={e=>{e.preventDefault();setOver(true);}}
                        onDragLeave={()=>setOver(false)}
                        onDrop={e=>{
                          e.preventDefault(); setOver(false);
                          const memberUid=e.dataTransfer.getData("memberUid");
                          const fromSlot=e.dataTransfer.getData("fromSlot");
                          if(fromSlot===slot)return;
                          if(slot==="starter"&&members.length>=4)return;
                          if(slot==="bench"&&members.length>=2)return;
                          moveTeamMember(team,memberUid,slot==="bench"?"to_bench":"to_starter");
                        }}
                        style={{flex:1}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                          <div style={{fontSize:12,fontWeight:800,color,textTransform:"uppercase",letterSpacing:1.5}}>{label}</div>
                          <span style={{fontSize:11,color:"rgba(255,255,255,0.3)",fontWeight:700}}>{members.length}/{maxCount}</span>
                        </div>
                        <div style={{
                          display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10,
                          minHeight:90,borderRadius:14,padding:over?"12px":"0",
                          border:over?`2px dashed ${color}55`:"2px dashed transparent",
                          background:over?`${color}08`:"transparent",
                          transition:"all 0.15s"
                        }}>
                          {members.map(m=>(
                            <MemberCard key={m.uid} m={m} slot={slot} isDraggable={isOwner}/>
                          ))}
                          {members.length<maxCount&&(
                            <div style={{background:"rgba(255,255,255,0.01)",border:`1px dashed ${color}22`,borderRadius:14,minHeight:80,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4}}>
                              <span style={{fontSize:20,opacity:0.3}}>{slot==="bench"?"\uD83E\uDE91":"\u2B50"}</span>
                              <span style={{fontSize:10,color:"rgba(255,255,255,0.15)"}}>Empty {slot} slot</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div>
                      {/* Team header */}
                      <div style={{background:"rgba(168,85,247,0.04)",border:"1px solid rgba(168,85,247,0.15)",borderRadius:20,padding:24,marginBottom:16,backdropFilter:"blur(10px)"}}>
                        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12}}>
                          <div>
                            <div style={{fontSize:22,fontWeight:900,background:"linear-gradient(135deg,#A855F7,#7B9CFF)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>{team.name}</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:4}}>{allMembers.length}/6 members · Apex Madness · Drag players to move between starter/bench</div>
                          </div>
                          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                            {allMembers.length<6&&isOwner&&(
                              <>
                                <input value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="Invite by @username..." style={{...inp,width:200,fontSize:12}} onKeyDown={e=>e.key==="Enter"&&inviteToTeam(team)}/>
                                <button onClick={()=>inviteToTeam(team)} style={{background:"linear-gradient(135deg,#A855F7,#7B2FF7)",color:"#fff",border:"none",borderRadius:10,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Invite</button>
                              </>
                            )}
                            {isOwner&&(
                              <button onClick={()=>deleteTeam(team)} style={{background:"rgba(232,49,122,0.08)",border:"1px solid rgba(232,49,122,0.2)",color:"rgba(232,49,122,0.6)",borderRadius:10,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"all 0.2s"}}
                                onMouseEnter={e=>{e.currentTarget.style.background="rgba(232,49,122,0.15)";e.currentTarget.style.color="#E8317A";}}
                                onMouseLeave={e=>{e.currentTarget.style.background="rgba(232,49,122,0.08)";e.currentTarget.style.color="rgba(232,49,122,0.6)";}}>
                                {"\uD83D\uDDD1 Delete Team"}</button>
                            )}
                          </div>
                        </div>
                        {inviteStatus&&<div style={{fontSize:12,color:inviteStatus.ok?"#4ade80":"#E8317A",marginBottom:12,fontWeight:600}}>{inviteStatus.ok?"\u2705":"\u274C"} {inviteStatus.msg}</div>}

                        {/* Starters + Bench with drag drop */}
                        <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
                          <DropZone slot="starter" label={"\u2B50 Starters"} color="#A855F7" members={starters} maxCount={4}/>
                          <DropZone slot="bench" label={"\uD83E\uDE91 Bench"} color="#7B9CFF" members={bench} maxCount={2}/>
                        </div>
                      </div>

                      {/* Apex conflict panel */}
                      {allMembers.length>1&&(
                        conflicts.length===0?(
                          <div style={{background:"rgba(10,26,10,0.6)",border:"1px solid rgba(74,222,128,0.2)",borderRadius:16,padding:20,textAlign:"center",backdropFilter:"blur(10px)"}}>
                            <div style={{fontSize:24,marginBottom:8}}>{"\u2705"}</div>
                            <div style={{fontSize:14,fontWeight:700,color:"#4ade80"}}>{submittedCount<2?"Waiting on deck submissions":"No apex card conflicts"}</div>
                            <div style={{fontSize:12,color:"rgba(255,255,255,0.3)",marginTop:4}}>{submittedCount<2?`${submittedCount}/${allMembers.length} members have submitted a deck. Conflicts show once at least 2 are in.`:"All submitted decks have unique apex cards"}</div>
                          </div>
                        ):(
                          <div style={{background:"rgba(26,10,10,0.6)",border:"1px solid rgba(232,49,122,0.2)",borderRadius:16,padding:20,backdropFilter:"blur(10px)"}}>
                            <div style={{fontSize:14,fontWeight:800,color:"#E8317A",marginBottom:12}}>{"\u26A0\uFE0F"}{conflicts.length} Apex Conflict{conflicts.length!==1?"s":""}</div>
                            {conflicts.map(({card,members:mems,key},i)=>{
                              const wc=WEAPON_COLORS[card.weapon]||"#444";
                              const myAlts=mySwapSuggestions[key]||[];
                              const iAmInConflict=mems.includes(user.displayName||user.email);
                              const iCanHelp=!iAmInConflict && cards.some(c=>owned&&owned[c.id]&&parseFloat(c.power||0)>160&&(c.weapon||"").toLowerCase()===(card.weapon||"").toLowerCase()&&!mySubIds.has(c.id));
                              const offersForThis=(swapOffers||[]).filter(o=>o.teamId===team.id&&o.conflictKey===key&&o.status==="open");
                              const myHelpCard=iCanHelp?cards.find(c=>owned&&owned[c.id]&&parseFloat(c.power||0)>160&&(c.weapon||"").toLowerCase()===(card.weapon||"").toLowerCase()&&!mySubIds.has(c.id)):null;
                              const iAlreadyOffered=offersForThis.some(o=>o.fromUid===user.uid);
                              return (
                                <div key={i} style={{marginBottom:12,paddingBottom:12,borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                                  <div style={{width:36,height:48,borderRadius:6,overflow:"hidden",flexShrink:0,background:"rgba(255,255,255,0.05)"}}>
                                    {card.imageUrl?<img src={card.imageUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:wc}}>{card.hero?.split(" ")[0]}</div>}
                                  </div>
                                  <div style={{flex:1}}>
                                    <div style={{fontSize:13,fontWeight:700,color:"var(--bz-ink)"}}>{card.hero} · {card.power}{"\u26A1"} · {card.treatment}</div>
                                    <div style={{fontSize:11,color:"#E8317A",marginTop:2}}>Conflict: {mems.join(", ")}</div>
                                  </div>
                                  </div>
                                  {/* My own swap: I'm in this conflict and own an unused alternative */}
                                  {iAmInConflict&&myAlts.length>0&&(
                                    <div style={{marginTop:8,marginLeft:48,background:"rgba(74,222,128,0.08)",border:"1px solid rgba(74,222,128,0.3)",borderRadius:8,padding:"7px 10px"}}>
                                      <span style={{fontSize:11,color:"#4ade80",fontWeight:700}}>💡 You can swap in: </span>
                                      <span style={{fontSize:11,color:"#ddd"}}>{myAlts.slice(0,3).map(c=>`${c.hero} ${c.power}⚡`).join(", ")}</span>
                                    </div>
                                  )}
                                  {/* I'm not in this conflict but could help — I choose to offer */}
                                  {iCanHelp&&myHelpCard&&!iAlreadyOffered&&(
                                    <div style={{marginTop:8,marginLeft:48,background:"rgba(123,156,255,0.08)",border:"1px solid rgba(123,156,255,0.3)",borderRadius:8,padding:"7px 10px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
                                      <span style={{fontSize:11,color:"#7B9CFF"}}>🤝 You own a card that fits this slot. Offer to help the team?</span>
                                      <button onClick={()=>offerSwap(team,key,myHelpCard,null)} style={{fontSize:10,background:"rgba(123,156,255,0.2)",border:"1px solid #7B9CFF",color:"#7B9CFF",borderRadius:6,padding:"4px 10px",cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>Offer my card</button>
                                    </div>
                                  )}
                                  {iAlreadyOffered&&<div style={{marginTop:8,marginLeft:48,fontSize:10,color:"#4ade80"}}>✓ You offered to help with this.</div>}
                                  {/* Offers from teammates for this conflict */}
                                  {offersForThis.filter(o=>o.fromUid!==user.uid).map(o=>(
                                    <div key={o.id} style={{marginTop:8,marginLeft:48,background:"rgba(168,85,247,0.08)",border:"1px solid rgba(168,85,247,0.3)",borderRadius:8,padding:"7px 10px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                                      <span style={{fontSize:11,color:"#C084FC"}}>🎁 <strong>{o.fromName}</strong> can offer {o.cardLabel} for this slot</span>
                                      {isOwner&&<button onClick={()=>dismissSwapOffer(o.id)} style={{fontSize:10,background:"transparent",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.5)",borderRadius:6,padding:"3px 8px",cursor:"pointer",fontFamily:"inherit"}}>Dismiss</button>}
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        )
                      )}

                      {/* Borrowed-card ledger — who lent what to whom, so cards get returned */}
                      {(()=>{
                        const teamLoansList=(teamLoans||[]).filter(l=>l.teamId===team.id);
                        const active=teamLoansList.filter(l=>l.status==="active").sort((a,b)=>(b.lentAt||"").localeCompare(a.lentAt||""));
                        const returned=teamLoansList.filter(l=>l.status==="returned").sort((a,b)=>(b.returnedAt||"").localeCompare(a.returnedAt||""));
                        // Lend form: owner lends one of THEIR owned cards to a teammate
                        const otherMembers=allMembers.filter(m=>m.uid!==user.uid);
                        const myLendableCards=cards.filter(c=>owned&&owned[c.id]).sort((a,b)=>(parseFloat(b.power)||0)-(parseFloat(a.power)||0));
                        return (
                          <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(123,156,255,0.2)",borderRadius:16,padding:20,marginTop:16,backdropFilter:"blur(10px)"}}>
                            <div style={{fontSize:14,fontWeight:800,color:"#7B9CFF",marginBottom:4}}>🔄 Borrowed Card Ledger</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:14}}>Track cards lent between teammates so they get back to the rightful owner.</div>

                            {/* Lend a card */}
                            {otherMembers.length>0&&myLendableCards.length>0&&(
                              <LendForm members={otherMembers} myCards={myLendableCards} onLend={(borrower,card)=>lendCard(team,borrower,card)} inp={inp} WEAPON_COLORS={WEAPON_COLORS}/>
                            )}

                            {/* Active loans */}
                            {active.length>0?(
                              <div style={{marginTop:14}}>
                                <div style={{fontSize:11,fontWeight:700,color:"#FBBF24",textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Out on loan ({active.length})</div>
                                {active.map(l=>{
                                  const iAmLender=l.lenderUid===user.uid;
                                  return (
                                    <div key={l.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.2)",borderRadius:10,marginBottom:6}}>
                                      <div style={{width:30,height:40,borderRadius:5,overflow:"hidden",flexShrink:0,background:"rgba(255,255,255,0.05)"}}>
                                        {l.cardImage?<img src={l.cardImage} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<div style={{width:"100%",height:"100%"}}/>}
                                      </div>
                                      <div style={{flex:1,minWidth:0}}>
                                        <div style={{fontSize:12,fontWeight:700,color:"#fff"}}>{l.cardLabel}</div>
                                        <div style={{fontSize:11,color:"rgba(255,255,255,0.5)"}}><strong style={{color:"#7B9CFF"}}>{l.lenderName}</strong> → <strong style={{color:"#C084FC"}}>{l.borrowerName}</strong>{l.lentAt?` · ${new Date(l.lentAt).toLocaleDateString()}`:""}</div>
                                      </div>
                                      {iAmLender?(
                                        <button onClick={()=>markLoanReturned(l)} style={{fontSize:11,background:"rgba(74,222,128,0.15)",border:"1px solid #4ade80",color:"#4ade80",borderRadius:7,padding:"5px 12px",cursor:"pointer",fontFamily:"inherit",fontWeight:700,flexShrink:0}}>✓ Returned</button>
                                      ):(
                                        <span style={{fontSize:10,color:"rgba(255,255,255,0.3)",flexShrink:0}}>owner marks return</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ):(
                              <div style={{marginTop:14,fontSize:12,color:"rgba(255,255,255,0.3)",textAlign:"center",padding:"10px 0"}}>No cards currently out on loan.</div>
                            )}

                            {/* Returned history */}
                            {returned.length>0&&(
                              <div style={{marginTop:14}}>
                                <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.35)",textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>Returned ({returned.length})</div>
                                {returned.slice(0,8).map(l=>(
                                  <div key={l.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 10px",fontSize:11,color:"rgba(255,255,255,0.4)"}}>
                                    <span style={{color:"#4ade80"}}>✓</span>
                                    <span style={{flex:1}}>{l.cardLabel} · {l.lenderName} → {l.borrowerName}</span>
                                    {l.lenderUid===user.uid&&<button onClick={()=>deleteLoan(l.id)} title="Remove from history" style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.25)",cursor:"pointer",fontSize:13,fontFamily:"inherit"}}>×</button>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
  );
}

// ── COLLECTION INTEGRITY CHECK (admin) ───────────────────────────────────────
// Reads a user's RAW boba_owned doc and compares it to how many of those cards still match the
// current database. If the two differ, cards aren't gone — their IDs stopped matching after a
// database change (a snapshot rename/merge), so the counter can't find them. This is a READ. It
// writes nothing. It exists to tell "the count dropped" apart from "records were deleted".
function OwnedIntegrityCheck({ uid, label, cards }) {
  const [state, setState] = useState(null);   // { raw, matched, orphans:[...] }
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    try {
      const snap = await getDoc(doc(db, "boba_owned", uid));
      const data = snap.exists() ? snap.data() : {};
      const ids = Object.keys(data);
      const cardById = new Set(cards.map(c => c.id));
      const matched = ids.filter(id => cardById.has(id));
      const orphans = ids.filter(id => !cardById.has(id));
      // Total COPIES, not unique cards. "owned" stores a quantity per card (a card owned x3 counts
      // once in the key list but 3 in copies). The number people usually quote as their collection
      // size is the copies total, which runs higher than the unique-card count. If the remembered
      // number matches this, nothing was lost — it's just two different counts of the same data.
      const copies = Object.values(data).reduce((sum, v) => {
        const n = parseInt(v); return sum + (isNaN(n) ? 1 : Math.max(1, n));
      }, 0);
      // Same check for cards ON THE WAY (boba_intransit). Drifted ids show as wrong "on the way"
      // counts even though the marks are intact.
      let transitRaw = 0, transitOrphans = [];
      try {
        const tSnap = await getDoc(doc(db, "boba_intransit", uid));
        const tData = tSnap.exists() ? tSnap.data() : {};
        const tIds = Object.keys(tData);
        transitRaw = tIds.length;
        transitOrphans = tIds.filter(id => !cardById.has(id));
      } catch(e) {}
      setState({ raw: ids.length, matched: matched.length, orphans, copies, transitRaw, transitOrphans });
    } catch(e) {
      setState({ error: e.message });
    }
    setBusy(false);
  }

  // ── HEAL ORPHANS ─────────────────────────────────────────────────────────────────────────────
  // An orphaned owned-ID is hash(set+cardNum+hero+OLD treatment). The same card today lives under
  // hash(set+cardNum+hero+NEW treatment). We can't reverse the hash, but the ID begins with the
  // pre-hash "key" (first 20 chars of set+cardNum+hero+treatment, lowercased, non-alnum stripped).
  // Cards sharing set+cardNum+hero share a long ID prefix; only trailing treatment chars + the
  // 6-char hash differ. We match each orphan to the current card whose stable-field prefix overlaps
  // most. NOTHING is written until you approve. This never deletes a card or a mark; it re-points.
  const [proposals, setProposals] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(0);
  const stableKey = c => `${c.setName||""}${c.cardNum||""}${c.hero||""}`.toLowerCase().replace(/[^a-z0-9]/g,"");

  async function proposeHeal() {
    setBusy(true);
    try {
      const snap = await getDoc(doc(db, "boba_owned", uid));
      const data = snap.exists() ? snap.data() : {};
      const cardById = new Set(cards.map(c => c.id));
      const orphanIds = Object.keys(data).filter(id => !cardById.has(id));
      const byStable = {};
      cards.forEach(c => { const k = stableKey(c).slice(0,20); (byStable[k] = byStable[k] || []).push(c); });
      const byCardNum = buildByCardNum();
      // DEBUG: capture what current cards actually look like so we can see why nothing matches.
      const props = orphanIds.map(oldId => {
        const candidates = matchOldId(oldId, byStable, byCardNum);
        // How many are ALREADY marked on each candidate’s current id. If >0, healing ADDS to it —
        // surface it so a re-marked card isn’t silently double-counted.
        const existingQty = {};
        candidates.forEach(c => { existingQty[c.id] = parseInt(data[c.id]) || 0; });
        return { oldId, qty: data[oldId], candidates, existingQty, chosen: candidates.length ? candidates[0].id : "" };
      });
      setProposals(props);
    } catch(e) { alert("Couldn't build heal preview: " + e.message); }
    setBusy(false);
  }

  async function applyHeal() {
    if (!proposals) return;
    const toApply = proposals.filter(p => p.chosen);
    if (!toApply.length) { alert("Nothing selected to heal."); return; }
    if (!window.confirm(`Re-point ${toApply.length} owned card${toApply.length===1?"":"s"} to their current IDs? This only moves the marks onto the matching cards \u2014 it deletes nothing.`)) return;
    setApplying(true);
    try {
      const snap = await getDoc(doc(db, "boba_owned", uid));
      const data = snap.exists() ? { ...snap.data() } : {};
      let n = 0;
      toApply.forEach(p => {
        if (!(p.oldId in data)) return;
        const prev = parseInt(data[p.chosen]) || 0;
        const add  = parseInt(data[p.oldId]) || 1;
        data[p.chosen] = prev + add;
        delete data[p.oldId];
        n++;
      });
      await setDoc(doc(db, "boba_owned", uid), data);
      setApplied(n); setProposals(null);
      await run();
    } catch(e) { alert("Heal failed: " + e.message); }
    setApplying(false);
  }

  // ── ON-THE-WAY HEAL ──────────────────────────────────────────────────────────────────────────
  // Same drift, for boba_intransit. Re-points "on the way" marks whose card id changed. Preview
  // first (proposeTransit), write on approve (applyTransit). Never deletes a mark.
  const [transitProps, setTransitProps] = useState(null);
  const [transitApplying, setTransitApplying] = useState(false);
  const [transitDone, setTransitDone] = useState(0);

  async function proposeTransit() {
    setBusy(true);
    try {
      const snap = await getDoc(doc(db, "boba_intransit", uid));
      const data = snap.exists() ? snap.data() : {};
      const cardById = new Set(cards.map(c => c.id));
      const dead = Object.keys(data).filter(id => !cardById.has(id));
      const byStable = {};
      cards.forEach(c => { const k = stableKey(c).slice(0,20); (byStable[k] = byStable[k] || []).push(c); });
      const byCardNum = buildByCardNum();
      const props = dead.map(oldId => {
        const cands = matchOldId(oldId, byStable, byCardNum);
        return { oldId, mark: data[oldId], candidates: cands, chosen: cands.length ? cands[0].id : "" };
      });
      setTransitProps(props);
    } catch(e) { alert("Couldn't build on-the-way preview: " + e.message); }
    setBusy(false);
  }

  async function applyTransit() {
    if (!transitProps) return;
    const toApply = transitProps.filter(p => p.chosen);
    if (!toApply.length) { alert("Nothing selected."); return; }
    if (!window.confirm(`Re-point ${toApply.length} on-the-way mark${toApply.length===1?"":"s"}? This only moves the marks onto the matching cards \u2014 it deletes nothing.`)) return;
    setTransitApplying(true);
    try {
      const snap = await getDoc(doc(db, "boba_intransit", uid));
      const data = snap.exists() ? { ...snap.data() } : {};
      let n = 0;
      toApply.forEach(p => {
        if (!(p.oldId in data)) return;
        // Move the transit mark to the new id. If one already exists there, keep the larger qty.
        const prev = data[p.chosen];
        const cur = data[p.oldId];
        if (prev && typeof prev === "object" && typeof cur === "object") {
          data[p.chosen] = { ...cur, qty: Math.max(parseInt(prev.qty)||1, parseInt(cur.qty)||1) };
        } else {
          data[p.chosen] = cur;
        }
        delete data[p.oldId];
        n++;
      });
      await setDoc(doc(db, "boba_intransit", uid), data);
      setTransitDone(n); setTransitProps(null);
      await run();
    } catch(e) { alert("On-the-way heal failed: " + e.message); }
    setTransitApplying(false);
  }


  // ── DECK HEAL ────────────────────────────────────────────────────────────────────────────────
  // Same drift, different place: a saved deck stores cardIds. After the snapshot regen those ids
  // point at cards that no longer exist under that id, so the deck-lock and the "exclude cards
  // already in a deck" logic silently stop working. We re-point each dead cardId to the current
  // card sharing set+cardNum+hero. Preview-first; writes only on approve; never removes a card from
  // a deck (an unmatched id is left as-is so nothing is silently dropped).
  const [deckProps, setDeckProps] = useState(null);
  const [deckApplying, setDeckApplying] = useState(false);
  const [deckDone, setDeckDone] = useState(0);

  // Match a drifted id to current cards. Two id schemes exist:
  //   1. importId_cardNum  (e.g. "mnc6g7h40t4qhqfb3xg_BPL_25")  \u2014 from the DBS set import.
  //      When a set is deleted + re-imported it gets a NEW importId, so every card's id changes but
  //      the cardNum suffix (BPL_25) and setName stay the same. Match by cardNum, scoped to set.
  //   2. bare hash (e.g. "bc791a9ca2bac06ee294")  \u2014 the old importer. No structure to recover
  //      the card from, so we fall back to the stable-field prefix method.
  // byCardNum: normalized "SETNAME|CARDNUM" -> [cards]; byStable: prefix -> [cards] (fallback).
  function matchOldId(oldId, byStable, byCardNum) {
    // Structured id: importId_cardNum (e.g. "mnc6g7h40t4qhqfb3xg_BPL_20"). Strip the importId
    // (everything up to the first underscore), then fully normalize the cardNum tail so "BPL_20"
    // matches a current "BPL20" / "BPL-20". The number+letter code is stable across re-imports even
    // though the id changed.
    const us = String(oldId).indexOf("_");
    if (us > 0 && byCardNum) {
      const tail = String(oldId).slice(us+1);
      const cn = tail.toLowerCase().replace(/[^a-z0-9]/g,"");
      let hits = (byCardNum[cn] || []).slice();
      if (hits.length) {
        // Most Tecmo-era cards live in one set; if several sets share the cardNum, prefer the biggest
        // set (the main checklist) so we don't re-point into a tiny promo set by accident \u2014 but keep
        // the others as pickable alternatives.
        hits.sort((a,b)=>{
          const as=(a.setName||"").toLowerCase(), bs=(b.setName||"").toLowerCase();
          const at=as.includes("tecmo")?1:0, bt=bs.includes("tecmo")?1:0;
          return bt-at;
        });
        const seen=new Set();
        return hits.filter(c=>!seen.has(c.id)&&seen.add(c.id));
      }
    }
    // Fallback for bare-hash old ids (no recoverable cardNum): stable-field prefix overlap.
    const prefix = String(oldId).replace(/_[0-9a-f]{1,6}$/,"");
    let best = [], bestLen = 0;
    Object.entries(byStable).forEach(([k, list]) => {
      let n=0; const mm=Math.min(k.length, prefix.length);
      while(n<mm && k[n]===prefix[n]) n++;
      if (n >= Math.min(10, prefix.length) && n > bestLen) { bestLen=n; best=list.slice(); }
      else if (n === bestLen && bestLen>0) { best = best.concat(list); }
    });
    const seen2=new Set();
    return best.filter(c=>!seen2.has(c.id)&&seen2.add(c.id));
  }

  // Index current cards by normalized SETNAME|CARDNUM.
  // Index current cards by FULLY-normalized cardNum (all non-alphanumerics stripped, lowercased).
  // "BPL-20", "BPL_20", "bpl20" all collapse to "bpl20". Keyed cardNum -> [cards].
  function buildByCardNum() {
    const idx = {};
    cards.forEach(c => {
      const cn = String(c.cardNum||"").toLowerCase().replace(/[^a-z0-9]/g,"");
      if (!cn) return;
      (idx[cn] = idx[cn] || []).push(c);
    });
    return idx;
  }

  async function proposeDeckHeal() {
    setBusy(true);
    try {
      const snap = await getDocs(query(collection(db,"boba_decks"), where("userId","==",uid)));
      const cardById = new Set(cards.map(c => c.id));
      const byStable = {};
      cards.forEach(c => { const k = stableKey(c).slice(0,20); (byStable[k] = byStable[k] || []).push(c); });
      const out = [];
      snap.forEach(d => {
        const data = d.data();
        const ids = data.cardIds || [];
        const dead = ids.filter(id => !cardById.has(id));
        if (!dead.length) return;
        const fixes = dead.map(oldId => {
          const cands = matchOldId(oldId, byStable);
          return { oldId, candidates: cands, chosen: cands.length ? cands[0].id : "" };
        });
        out.push({ deckId: d.id, name: data.name || "Untitled deck", total: ids.length, dead: dead.length, fixes });
      });
      setDeckProps(out);
    } catch(e) { alert("Couldn't build deck preview: " + e.message); }
    setBusy(false);
  }

  async function applyDeckHeal() {
    if (!deckProps) return;
    const chosenCount = deckProps.reduce((s,d)=>s + d.fixes.filter(f=>f.chosen).length, 0);
    if (!chosenCount) { alert("Nothing selected to heal."); return; }
    if (!window.confirm(`Re-point ${chosenCount} card reference${chosenCount===1?"":"s"} across ${deckProps.length} deck${deckProps.length===1?"":"s"}? Unmatched cards are left in place \u2014 nothing is removed from a deck.`)) return;
    setDeckApplying(true);
    try {
      let fixed = 0;
      for (const d of deckProps) {
        const map = {};
        d.fixes.forEach(f => { if (f.chosen) map[f.oldId] = f.chosen; });
        if (!Object.keys(map).length) continue;
        const ref = doc(db,"boba_decks",d.deckId);
        const cur = await getDoc(ref);
        if (!cur.exists()) continue;
        const ids = (cur.data().cardIds || []).map(id => map[id] || id);
        await setDoc(ref, { cardIds: ids }, { merge:true });
        fixed += Object.keys(map).length;
      }
      setDeckDone(fixed); setDeckProps(null);
    } catch(e) { alert("Deck heal failed: " + e.message); }
    setDeckApplying(false);
  }



  return (
    <div style={{background:"rgba(123,156,255,0.05)",border:"1px solid rgba(123,156,255,0.25)",borderRadius:10,padding:"11px 13px",marginBottom:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
        <div style={{fontSize:11.5,fontWeight:800,color:"#7B9CFF"}}>{"\uD83D\uDD0D"} Collection integrity {label ? `\u2014 ${label}` : ""}</div>
        <button onClick={run} disabled={busy} style={{background:"rgba(123,156,255,0.15)",border:"1px solid rgba(123,156,255,0.35)",color:"#7B9CFF",borderRadius:7,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:busy?"wait":"pointer",fontFamily:"inherit"}}>{busy?"Checking\u2026":"Check"}</button>
      </div>
      {state && !state.error && (
        <div style={{marginTop:9,fontSize:11.5,lineHeight:1.7,color:"rgba(255,255,255,0.7)"}}>
          <div><strong style={{color:"#fff"}}>{state.raw}</strong> unique cards actually in the collection (the real data).</div>
          <div><strong style={{color:"#fff"}}>{state.copies}</strong> total copies (a card owned more than once counts each copy — this is usually the bigger "collection size" number).</div>
          <div><strong style={{color:"#4ade80"}}>{state.matched}</strong> currently match a card in the database (what the counter shows).</div>
          {state.orphans.length > 0 ? (
            <div style={{marginTop:7,padding:"8px 10px",background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:8,color:"#FBBF24"}}>
              {"\u26A0\uFE0F"} <strong>{state.orphans.length} owned cards aren't matching the current database.</strong> They are NOT deleted {"\u2014"} their IDs changed when the card data was regenerated, so the counter can't find them. The records are still here (see the {state.raw} above).
            </div>
          ) : (
            <div style={{marginTop:7,color:"#4ade80"}}>{"\u2713"} Every owned record matches a card. Nothing orphaned.</div>
          )}
              {state.orphans.length > 0 && (
                <div style={{marginTop:9}}>
                  {!proposals ? (
                    <button onClick={proposeHeal} disabled={busy} style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:8,padding:"8px 15px",fontSize:12,fontWeight:800,cursor:busy?"wait":"pointer",fontFamily:"inherit"}}>
                      {busy ? "Finding matches\u2026" : `Preview fix for ${state.orphans.length} card${state.orphans.length===1?"":"s"}`}
                    </button>
                  ) : (
                    <div style={{marginTop:4}}>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",marginBottom:8,lineHeight:1.5}}>
                        Proposed matches below. Nothing is written until you press Apply. Each row moves an
                        orphaned mark onto the current card with the same set / number / hero. Rows with more
                        than one candidate need you to pick; leave unsure ones unset and they stay untouched.
                      </div>
                      <div style={{maxHeight:280,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
                        {proposals.map((pr,pi) => (
                          <div key={pr.oldId} style={{background:"rgba(0,0,0,0.25)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,padding:"7px 9px"}}>
                            <div style={{fontSize:9.5,color:"rgba(255,255,255,0.3)",fontFamily:"monospace",marginBottom:4,wordBreak:"break-all"}}>{pr.oldId}{pr.qty>1?`  (\u00d7${pr.qty})`:""}</div>
                            {pr.candidates.length===0 ? (
                              <div style={{fontSize:11,color:"#E8317A"}}>No confident match found — left untouched.</div>
                            ) : (
                              <>
                              {pr.candidates.length>1 && <div style={{fontSize:9.5,color:"#FBBF24",marginBottom:3}}>{pr.candidates.length} sets share this number — defaulted to Tecmo Bowl, change if wrong</div>}
                              <select value={pr.chosen} onChange={e=>{ const v=e.target.value; setProposals(ps=>ps.map((x,xi)=>xi===pi?{...x,chosen:v}:x)); }}
                                style={{width:"100%",background:"#14141a",color:"#eee",border:"1px solid rgba(255,255,255,0.15)",borderRadius:6,padding:"5px 7px",fontSize:11.5,fontFamily:"inherit"}}>
                                <option value="">— don't heal this one —</option>
                                {pr.candidates.map(c => (
                                  <option key={c.id} value={c.id}>{[c.hero,c.treatment,c.cardNum?("#"+c.cardNum):"",c.setName].filter(Boolean).join(" \u00b7 ")}</option>
                                ))}
                              </select>
                              {pr.chosen && (() => { const cc = pr.candidates.find(c=>c.id===pr.chosen); return cc && cc.imageUrl ? <img src={cc.imageUrl} alt={cc.hero} style={{width:54,height:72,objectFit:"cover",borderRadius:5,marginTop:6,border:"1px solid rgba(255,255,255,0.15)"}}/> : null; })()}
                            </>
                            )}
                          </div>
                        ))}
                      </div>
                      <div style={{display:"flex",gap:8,marginTop:10}}>
                        <button onClick={applyHeal} disabled={applying} style={{background:"#4ade80",color:"#06240f",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:800,cursor:applying?"wait":"pointer",fontFamily:"inherit"}}>
                          {applying ? "Applying\u2026" : `Apply ${proposals.filter(p=>p.chosen).length} fix${proposals.filter(p=>p.chosen).length===1?"":"es"}`}
                        </button>
                        <button onClick={()=>setProposals(null)} disabled={applying} style={{background:"transparent",color:"rgba(255,255,255,0.5)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {applied > 0 && <div style={{marginTop:8,fontSize:11.5,color:"#4ade80",fontWeight:700}}>{"\u2713"} Re-pointed {applied} card{applied===1?"":"s"}. Counts refreshed above.</div>}
        </div>
      )}
      {/* On-the-way (in-transit) references. Separate from owned; drifts the same way. */}
      <div style={{marginTop:12,paddingTop:11,borderTop:"1px solid rgba(255,255,255,0.08)"}}>
        <div style={{fontSize:11.5,fontWeight:800,color:"#60A5FA",marginBottom:7}}>{"\uD83D\uDE9A"} On-the-way references{state && typeof state.transitRaw==="number" ? ` (${state.transitRaw} marked, ${state.transitOrphans?.length||0} drifted)` : ""}</div>
        {!transitProps ? (
          <button onClick={proposeTransit} disabled={busy} style={{background:"rgba(96,165,250,0.15)",border:"1px solid rgba(96,165,250,0.4)",color:"#60A5FA",borderRadius:7,padding:"6px 13px",fontSize:11.5,fontWeight:700,cursor:busy?"wait":"pointer",fontFamily:"inherit"}}>
            {busy ? "Checking\u2026" : "Check on-the-way cards for drift"}
          </button>
        ) : transitProps.length === 0 ? (
          <div style={{fontSize:11.5,color:"#4ade80"}}>{"\u2713"} Every on-the-way mark matches a card. Nothing to fix.</div>
        ) : (
          <div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",marginBottom:8,lineHeight:1.5}}>
              These "on the way" marks reference cards whose id drifted. Re-pointing restores the count.
              Nothing is written until you press Apply.
            </div>
            <div style={{maxHeight:240,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
              {transitProps.map((pr,pi) => (
                <div key={pr.oldId} style={{background:"rgba(0,0,0,0.25)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,padding:"7px 9px"}}>
                  <div style={{fontSize:9.5,color:"rgba(255,255,255,0.3)",fontFamily:"monospace",marginBottom:4,wordBreak:"break-all"}}>{pr.oldId}</div>
                  {pr.candidates.length===0 ? (
                    <div style={{fontSize:11,color:"#E8317A"}}>No confident match — left untouched.</div>
                  ) : (
                    <select value={pr.chosen} onChange={e=>{ const v=e.target.value; setTransitProps(ps=>ps.map((x,xi)=>xi===pi?{...x,chosen:v}:x)); }}
                      style={{width:"100%",background:"#14141a",color:"#eee",border:"1px solid rgba(255,255,255,0.15)",borderRadius:6,padding:"4px 7px",fontSize:11,fontFamily:"inherit"}}>
                      <option value="">— leave as-is —</option>
                      {pr.candidates.map(c => (
                        <option key={c.id} value={c.id}>{[c.hero,c.treatment,c.cardNum?("#"+c.cardNum):"",c.setName].filter(Boolean).join(" \u00b7 ")}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:8,marginTop:10}}>
              <button onClick={applyTransit} disabled={transitApplying} style={{background:"#60A5FA",color:"#06203f",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:800,cursor:transitApplying?"wait":"pointer",fontFamily:"inherit"}}>
                {transitApplying ? "Applying\u2026" : `Apply ${transitProps.filter(p=>p.chosen).length} fix${transitProps.filter(p=>p.chosen).length===1?"":"es"}`}
              </button>
              <button onClick={()=>setTransitProps(null)} disabled={transitApplying} style={{background:"transparent",color:"rgba(255,255,255,0.5)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        )}
        {transitDone > 0 && <div style={{marginTop:8,fontSize:11.5,color:"#4ade80",fontWeight:700}}>{"\u2713"} Re-pointed {transitDone} on-the-way mark{transitDone===1?"":"s"}.</div>}
      </div>

      {/* Deck references — separate from owned marks. Available whenever the panel is open, because a
          deck can be broken even when the owned count looks fine. */}
      <div style={{marginTop:12,paddingTop:11,borderTop:"1px solid rgba(255,255,255,0.08)"}}>
        <div style={{fontSize:11.5,fontWeight:800,color:"#7B9CFF",marginBottom:7}}>{"\uD83D\uDCD8"} Deck references</div>
        {!deckProps ? (
          <button onClick={proposeDeckHeal} disabled={busy} style={{background:"rgba(123,156,255,0.15)",border:"1px solid rgba(123,156,255,0.35)",color:"#7B9CFF",borderRadius:7,padding:"6px 13px",fontSize:11.5,fontWeight:700,cursor:busy?"wait":"pointer",fontFamily:"inherit"}}>
            {busy ? "Scanning decks\u2026" : "Check saved decks for drifted cards"}
          </button>
        ) : deckProps.length === 0 ? (
          <div style={{fontSize:11.5,color:"#4ade80"}}>{"\u2713"} Every card in every saved deck matches. Nothing to fix.</div>
        ) : (
          <div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",marginBottom:8,lineHeight:1.5}}>
              These decks reference cards whose ids drifted. Re-pointing restores the deck-lock and the
              "exclude cards already in a deck" behavior. Nothing is written until you press Apply, and an
              unmatched card is left in the deck untouched.
            </div>
            <div style={{maxHeight:300,overflowY:"auto",display:"flex",flexDirection:"column",gap:10}}>
              {deckProps.map((d,di) => (
                <div key={d.deckId} style={{background:"rgba(0,0,0,0.25)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,padding:"8px 10px"}}>
                  <div style={{fontSize:12,fontWeight:800,color:"#eee",marginBottom:6}}>{d.name} <span style={{color:"rgba(255,255,255,0.35)",fontWeight:600}}>— {d.dead} of {d.total} drifted</span></div>
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    {d.fixes.map((f,fi) => (
                      <div key={f.oldId}>
                        {f.candidates.length===0 ? (
                          <div style={{fontSize:10.5,color:"#E8317A"}}>No confident match — left in the deck as-is.</div>
                        ) : (
                          <select value={f.chosen} onChange={e=>{ const v=e.target.value; setDeckProps(ps=>ps.map((x,xi)=>xi===di?{...x,fixes:x.fixes.map((y,yi)=>yi===fi?{...y,chosen:v}:y)}:x)); }}
                            style={{width:"100%",background:"#14141a",color:"#eee",border:"1px solid rgba(255,255,255,0.15)",borderRadius:6,padding:"4px 7px",fontSize:11,fontFamily:"inherit"}}>
                            <option value="">— leave this card as-is —</option>
                            {f.candidates.map(c => (
                              <option key={c.id} value={c.id}>{[c.hero,c.treatment,c.cardNum?("#"+c.cardNum):"",c.setName].filter(Boolean).join(" \u00b7 ")}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",gap:8,marginTop:10}}>
              <button onClick={applyDeckHeal} disabled={deckApplying} style={{background:"#4ade80",color:"#06240f",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:800,cursor:deckApplying?"wait":"pointer",fontFamily:"inherit"}}>
                {deckApplying ? "Applying\u2026" : `Apply ${deckProps.reduce((s,d)=>s+d.fixes.filter(f=>f.chosen).length,0)} fix${deckProps.reduce((s,d)=>s+d.fixes.filter(f=>f.chosen).length,0)===1?"":"es"}`}
              </button>
              <button onClick={()=>setDeckProps(null)} disabled={deckApplying} style={{background:"transparent",color:"rgba(255,255,255,0.5)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        )}
        {deckDone > 0 && <div style={{marginTop:8,fontSize:11.5,color:"#4ade80",fontWeight:700}}>{"\u2713"} Re-pointed {deckDone} card reference{deckDone===1?"":"s"}. Reload the deck builder to see the lock working again.</div>}
      </div>
      {state?.error && <div style={{marginTop:8,fontSize:11,color:"#E8317A"}}>Couldn't read: {state.error}</div>}
    </div>
  );
}

function FriendsTab({ user, friends, friendReqs, sentReqs, addEmail, setAddEmail, addStatus, setAddStatus, friendOwned, viewingFriend, setViewingFriend, respondFriendReq, cards, owned, publicCards, WEAPON_COLORS, setSigningIn , inp, teamInvites, sendFriendRequest, respondTeamInvite, toggleFamily, borrowLedger=[], acceptLoan, declineLoan}) {
  // A family member's saved decks. boba_decks is already public-read (for share links), so this is
  // just a scoped query — no new permissions. Family means you're pooling cards; seeing the decks
  // built from that pool is the natural other half.
  //
  // Fetched on demand for whoever's expanded, and only if they're actually family — a plain friend
  // sharing cards hasn't opted into sharing their deck LIST.
  const [famDecks, setFamDecks] = useState([]);
  const [famDecksLoading, setFamDecksLoading] = useState(false);
  useEffect(() => {
    const f = friends.find(fr => fr.friendUid === viewingFriend);
    if (!viewingFriend || !f?.isFamily) { setFamDecks([]); return; }
    setFamDecksLoading(true);
    const unsub = onSnapshot(
      query(collection(db,"boba_decks"), where("userId","==",viewingFriend)),
      snap => {
        setFamDecks(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.savedAt||"").localeCompare(a.savedAt||"")));
        setFamDecksLoading(false);
      },
      e => { console.error("family decks load failed:", e); setFamDecksLoading(false); }
    );
    return () => { try{unsub();}catch(e){} };
  }, [viewingFriend, friends]);

  return (
          <div style={{maxWidth:700,margin:"0 auto"}}>
            {!user?(
              <div style={{textAlign:"center",padding:80}}>
                <div style={{fontSize:48,marginBottom:16}}>{"\uD83D\uDC65"}</div>
                <button onClick={()=>setSigningIn(true)} style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:14,padding:"12px 28px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 8px 32px rgba(232,49,122,0.4)"}}>Sign in to add friends</button>
              </div>
            ):(
              <>
                <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:16,padding:20,marginBottom:12,backdropFilter:"blur(10px)"}}>
                  <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)",marginBottom:12}}>{"\u2795 Add Friend"}</div>
                  <div style={{display:"flex",gap:8}}>
                    <input value={addEmail} onChange={e=>setAddEmail(e.target.value)} placeholder="Their @username…" style={{...inp,flex:1}} onKeyDown={e=>e.key==="Enter"&&sendFriendRequest()}/>
                    <button onClick={sendFriendRequest} style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:12,padding:"8px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 16px rgba(232,49,122,0.3)"}}>Send</button>
                  </div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginTop:8,lineHeight:1.5}}>
                    Use their <strong style={{color:"rgba(255,255,255,0.55)"}}>username</strong> (from their profile page) or the email they signed up with — any email provider works, not just Gmail.
                  </div>
                  {addStatus&&<div style={{fontSize:12,color:addStatus.ok?"#4ade80":"#E8317A",marginTop:10,fontWeight:600}}>{addStatus.ok?"\u2705":"\u274C"} {addStatus.msg}</div>}
                </div>

                {friendReqs.length>0&&(
                  <div style={{background:"rgba(251,191,36,0.04)",border:"1px solid rgba(251,191,36,0.2)",borderRadius:16,padding:20,marginBottom:12}}>
                    <div style={{fontSize:13,fontWeight:800,color:"#FBBF24",marginBottom:12}}>{"\uD83D\uDCEC Requests ("}{friendReqs.length})</div>
                    {friendReqs.map(r=>(
                      <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                        <div style={{flex:1}}><div style={{fontSize:13,fontWeight:700}}>{r.fromName||"Collector"}</div></div>
                        <button onClick={()=>respondFriendReq(r,true)} style={{background:"rgba(74,222,128,0.15)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:8,padding:"6px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{"\u2705 Accept"}</button>
                        <button onClick={()=>respondFriendReq(r,false)} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.3)",borderRadius:8,padding:"6px 14px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Decline</button>
                      </div>
                    ))}
                  </div>
                )}

                {teamInvites.length>0&&(
                  <div style={{background:"rgba(168,85,247,0.04)",border:"1px solid rgba(168,85,247,0.2)",borderRadius:16,padding:20,marginBottom:12}}>
                    <div style={{fontSize:13,fontWeight:800,color:"#A855F7",marginBottom:12}}>{"\uD83C\uDFC6 Team Invites ("}{teamInvites.length})</div>
                    {teamInvites.map(inv=>(
                      <div key={inv.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                        <div style={{flex:1}}><div style={{fontSize:13,fontWeight:700}}>{inv.teamName}</div><div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>from {inv.fromName}</div></div>
                        <button onClick={()=>respondTeamInvite(inv,true)} style={{background:"rgba(168,85,247,0.15)",border:"1px solid rgba(168,85,247,0.3)",color:"#A855F7",borderRadius:8,padding:"6px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{"\u2705 Join"}</button>
                        <button onClick={()=>respondTeamInvite(inv,false)} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.3)",borderRadius:8,padding:"6px 14px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Decline</button>
                      </div>
                    ))}
                  </div>
                )}

                {sentReqs.length>0&&(
                  <div style={{background:"rgba(255,255,255,0.01)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:12,padding:16,marginBottom:12}}>
                    <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.3)",marginBottom:8}}>Pending sent</div>
                    {sentReqs.map(r=><div key={r.id} style={{fontSize:12,color:"rgba(255,255,255,0.2)",marginBottom:4}}>{"\u23F3"}{r.toName||"Collector"}</div>)}
                  </div>
                )}

                <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:16,padding:20,backdropFilter:"blur(10px)"}}>
                  <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)",marginBottom:12}}>Friends ({friends.length})</div>
                  {friends.length===0?<div style={{fontSize:13,color:"rgba(255,255,255,0.3)"}}>No friends yet -- add someone above!</div>:
                    friends.map(f=>(
                      <div key={f.id} style={{display:"flex",alignItems:"center",gap:12,marginBottom:14,paddingBottom:14,borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                        <div style={{width:40,height:40,borderRadius:"50%",background:"rgba(255,255,255,0.05)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0,border:"1.5px solid rgba(255,255,255,0.1)"}}>{"\uD83D\uDC64"}</div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,fontWeight:700}}>{f.friendName}{f.isFamily&&<span style={{marginLeft:6,fontSize:9,fontWeight:800,color:"#4ade80",background:"rgba(74,222,128,0.12)",border:"1px solid rgba(74,222,128,0.3)",borderRadius:5,padding:"1px 6px"}}>👪 FAMILY</span>}</div>
                          <div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>{friendOwned[f.friendUid]?`${Object.keys(friendOwned[f.friendUid]).length} cards owned`:"Loading..."}</div>
                        </div>
                        {/* Three states, and the difference is the whole point:
                              nothing shared         -> "+ Family"
                              I shared, they haven't -> "Waiting"     (they can't see mine either)
                              they shared, I haven't -> "Share back"
                              both shared            -> "Family"
                            Sharing is an OFFER, not a taking. */}
                        <button onClick={()=>toggleFamily(f)}
                          title={f.isFamily
                            ? "You're sharing cards both ways. Click to stop sharing yours."
                            : f.iShared
                              ? `You've shared your cards with ${f.friendName}. You'll see theirs when they share back. Click to withdraw.`
                              : f.theyShared
                                ? `${f.friendName} shared their cards with you. Share yours back to use theirs in decks.`
                                : "Share your cards with them. You'll see theirs once they share back."}
                          style={{background:f.isFamily?"rgba(74,222,128,0.15)":f.theyShared?"rgba(251,191,36,0.15)":"rgba(255,255,255,0.04)",border:`1px solid ${f.isFamily?"rgba(74,222,128,0.4)":f.theyShared?"rgba(251,191,36,0.45)":"rgba(255,255,255,0.08)"}`,color:f.isFamily?"#4ade80":f.theyShared?"#FBBF24":f.iShared?"rgba(255,255,255,0.55)":"rgba(255,255,255,0.4)",borderRadius:10,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"all 0.2s",whiteSpace:"nowrap"}}>
                          {f.isFamily ? "\uD83D\uDC6A Family"
                            : f.theyShared ? "\uD83D\uDC6A Share back"
                            : f.iShared ? "\u23F3 Waiting"
                            : "+ Family"}
                        </button>
                        <button onClick={()=>{setViewingFriend(viewingFriend===f.friendUid?null:f.friendUid);}}
                          style={{background:viewingFriend===f.friendUid?"rgba(123,156,255,0.2)":"rgba(255,255,255,0.04)",border:`1px solid ${viewingFriend===f.friendUid?"rgba(123,156,255,0.4)":"rgba(255,255,255,0.08)"}`,color:viewingFriend===f.friendUid?"#7B9CFF":"rgba(255,255,255,0.4)",borderRadius:10,padding:"6px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"all 0.2s"}}>
                          {viewingFriend===f.friendUid?"Hide":"\uD83D\uDC41 View"}
                        </button>
                      </div>
                    ))
                  }
                </div>

                {/* Borrowed cards paper trail (family lending) */}
                {/* Pending loan offers sent TO me — accept to pull the card into my
                    collection as borrowed, decline to remove the offer. */}
                {borrowLedger.filter(l=>l.status==="pending" && l.borrowerUid===user.uid).length>0 && (
                  <div style={{marginTop:8,marginBottom:20,background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:14,padding:16}}>
                    <div style={{fontSize:14,fontWeight:800,color:"#FBBF24",marginBottom:4}}>📥 Incoming Loan Offers</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:12}}>Cards a friend wants to lend you. Accept to add them to your collection as borrowed — you can build with them, and they never count as yours.</div>
                    {borrowLedger.filter(l=>l.status==="pending" && l.borrowerUid===user.uid).map(l=>{
                      const rc = cards.find(x=>x.id===l.cardId);
                      const label = rc ? `${rc.hero} ${rc.power}⚡ ${rc.treatment||""}`.trim() : (l.cardLabel||l.cardId);
                      const img = rc?.imageUrl || l.cardImage || "";
                      return (
                      <div key={l.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"rgba(0,0,0,0.2)",borderRadius:10,marginBottom:6}}>
                        <div style={{width:28,height:38,borderRadius:5,overflow:"hidden",flexShrink:0,background:"rgba(255,255,255,0.05)"}}>{img?<img src={img} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:null}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:700,color:"#fff"}}>{label}</div>
                          <div style={{fontSize:11,color:"rgba(255,255,255,0.5)"}}>from <strong style={{color:"#C084FC"}}>{l.ownerName}</strong></div>
                        </div>
                        <button onClick={()=>acceptLoan&&acceptLoan(l)} style={{fontSize:11,background:"rgba(74,222,128,0.15)",border:"1px solid #4ade80",color:"#4ade80",borderRadius:7,padding:"5px 12px",cursor:"pointer",fontFamily:"inherit",fontWeight:700,flexShrink:0}}>Accept</button>
                        <button onClick={()=>declineLoan&&declineLoan(l)} style={{fontSize:11,background:"transparent",border:"1px solid #555",color:"#888",borderRadius:7,padding:"5px 10px",cursor:"pointer",fontFamily:"inherit",fontWeight:700,flexShrink:0}}>Decline</button>
                      </div>
                      );
                    })}
                  </div>
                )}

                {borrowLedger.filter(l=>l.status==="borrowed").length>0 && (
                  <div style={{marginTop:8,marginBottom:20,background:"rgba(192,132,252,0.06)",border:"1px solid rgba(192,132,252,0.25)",borderRadius:14,padding:16}}>
                    <div style={{fontSize:14,fontWeight:800,color:"#C084FC",marginBottom:4}}>👪 Borrowed Cards — Paper Trail</div>
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:12}}>Family cards currently in your decks, and yours in theirs. After a tournament, this tells you whose card is whose.</div>
                    {borrowLedger.filter(l=>l.status==="borrowed").map(l=>{
                      const iBorrowed = l.borrowerUid===user.uid;
                      // Resolve the real card from the local list by id. Older entries
                      // stored the raw card id as the label and no image (a Map-access
                      // bug at send time), so prefer a live lookup and fall back to
                      // whatever was stored.
                      const rc = cards.find(x=>x.id===l.cardId);
                      const label = rc ? `${rc.hero} ${rc.power}⚡ ${rc.treatment||""}`.trim() : (l.cardLabel||l.cardId);
                      const img = rc?.imageUrl || l.cardImage || "";
                      return (
                        <div key={l.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"rgba(0,0,0,0.2)",borderRadius:10,marginBottom:6}}>
                          <div style={{width:28,height:38,borderRadius:5,overflow:"hidden",flexShrink:0,background:"rgba(255,255,255,0.05)"}}>{img?<img src={img} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:null}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:12,fontWeight:700,color:"#fff"}}>{label}</div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.5)"}}>
                              {iBorrowed
                                ? <>You borrowed from <strong style={{color:"#C084FC"}}>{l.ownerName}</strong></>
                                : <><strong style={{color:"#7B9CFF"}}>{l.borrowerName}</strong> borrowed yours</>}
                              {l.deckName?` · ${l.deckName}`:""}
                            </div>
                          </div>
                          {iBorrowed
                            ? <span style={{fontSize:10,color:"rgba(255,255,255,0.3)",flexShrink:0}}>remove from deck to return</span>
                            : <span style={{fontSize:10,color:"#FBBF24",flexShrink:0}}>out on loan</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
                {viewingFriend&&(()=>{
                  const f=friends.find(fr=>fr.friendUid===viewingFriend);
                  const fo=friendOwned[viewingFriend]||{};
                  const friendCards=cards.filter(c=>fo[c.id]);
                  return (
                    <div style={{marginTop:12,background:"rgba(123,156,255,0.03)",border:"1px solid rgba(123,156,255,0.15)",borderRadius:16,padding:20,backdropFilter:"blur(10px)",animation:"floatUp 0.3s ease"}}>
                      <div style={{fontSize:14,fontWeight:800,color:"#7B9CFF",marginBottom:12}}>{f?.friendName}'s Collection ({friendCards.length} cards)</div>
                    {(user?.email||"").includes("@bazookabreaks.com") && (
                      <OwnedIntegrityCheck uid={viewingFriend} label={f?.friendName} cards={cards}/>
                    )}
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:8,maxHeight:500,overflowY:"auto"}}>
                        {friendCards.map(c=>{
                          const wc=WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
                          return (
                            <div key={c.id} style={{background:"rgba(0,0,0,0.4)",border:`1px solid ${wc}22`,borderRadius:12,padding:10,backdropFilter:"blur(4px)"}}>
                              {c.imageUrl&&<img src={c.imageUrl} alt={c.hero} style={{width:"100%",aspectRatio:"3/4",objectFit:"cover",borderRadius:8,marginBottom:6}}/>}
                              <div style={{fontSize:12,fontWeight:700,color:"var(--bz-ink)"}}>{c.hero}</div>
                              <div style={{fontSize:10,color:wc,fontWeight:700}}>{c.weapon}</div>
                              <div style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>{c.treatment}</div>
                              <div style={{fontSize:11,fontWeight:800,color:wc,marginTop:2}}>{c.power}{"\u26A1"}</div>
                              {fo[c.id]>1&&<div style={{fontSize:10,color:"#FBBF24",fontWeight:700}}>{"\u00D7"}{fo[c.id]}</div>}
                            </div>
                          );
                        })}
                      </div>
                      {/* Their saved decks — family only. Read-only: you can see what they built and
                          copy the idea, but it's their list to edit. */}
                      {f?.isFamily && (
                        <div style={{marginTop:16,paddingTop:14,borderTop:"1px solid rgba(255,255,255,0.08)"}}>
                          <div style={{fontSize:12,fontWeight:800,color:"#4ade80",marginBottom:10}}>
                            {"\uD83D\uDCD8"} {f.friendName?.split(" ")[0] || "Their"}'s decks {famDecks.length>0 && `(${famDecks.length})`}
                          </div>
                          {famDecksLoading ? (
                            <div style={{fontSize:11.5,color:"rgba(255,255,255,0.3)"}}>Loading{"\u2026"}</div>
                          ) : famDecks.length === 0 ? (
                            <div style={{fontSize:11.5,color:"rgba(255,255,255,0.3)"}}>No saved decks yet.</div>
                          ) : (
                            <div style={{display:"flex",flexDirection:"column",gap:7}}>
                              {famDecks.map(d => (
                                <div key={d.id} style={{display:"flex",alignItems:"center",gap:10,background:"rgba(0,0,0,0.3)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:9,padding:"9px 12px"}}>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:12.5,fontWeight:700,color:"#eee",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{d.name||"Untitled deck"}</div>
                                    <div style={{fontSize:10.5,color:"rgba(255,255,255,0.35)"}}>
                                      {(d.cardCount ?? (d.cardIds||[]).length)} cards{d.deckType && d.deckType!=="none" ? ` \u00b7 ${d.deckType}` : ""}
                                    </div>
                                  </div>
                                  <a href={`/deck/${d.id}`} target="_blank" rel="noreferrer"
                                    style={{background:"rgba(123,156,255,0.12)",border:"1px solid rgba(123,156,255,0.35)",color:"#7B9CFF",borderRadius:7,padding:"5px 12px",fontSize:11,fontWeight:700,textDecoration:"none",whiteSpace:"nowrap"}}>
                                    View
                                  </a>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
  );
}

// ── Playbook Make-Up categories ────────────────────────────────────────────
// 7 categories matching the legacy tracker. Each play is auto-guessed from its
// name via keyword rules below; admins can override per-play (stored in
// meta/play_categories) and overrides always win.
const PLAYBOOK_CATEGORIES = ["Control","Discard Heroes","Hot Dog Recursion","Misc","Opp. Debuff","Play Recursion","Power Buff"];
const PLAYBOOK_CAT_COLORS = {
  "Control":"#7B9CFF", "Discard Heroes":"#E8317A", "Hot Dog Recursion":"#FBBF24",
  "Misc":"#9ca3af", "Opp. Debuff":"#A855F7", "Play Recursion":"#4ade80", "Power Buff":"#F59E0B",
};
// keyword → category (checked in order; first match wins). Lowercased contains-match.
const PLAYBOOK_CAT_RULES = [
  ["Hot Dog Recursion", ["hot dog","htd","weiner","weenie","dog gone","roller dog","relish","protein","meal","dinner","feast","lunch","sweet relish","gambler's feast","momentum meal","make up meal","victory dinner"]],
  ["Play Recursion",    ["play re-order","re-order","reorder","play reset","deck burn","deep in the playbook","tear a page","roll some plays","roll for","four-flip","flip ya for","plays or dogs","trade-up","toss and trade","copy cat","new plays","more plays","draw","reload","recovery","recover","back from the dumps","clean slate"]],
  ["Power Buff",        ["pump","power pick","high stakes pump","add firepower","heavy swing","big swinger","steel smash","steel roll","steel flipper","steel helmet","might of the underdog","indestructible","line drive","high fastball","firework","glow","buff","boost"]],
  ["Opp. Debuff",       ["forced retreat","emergency shutdown","pulling the plug","ice blast","ice roll","hex flipper","debuff","competitive disadvantage","turn the tide","sack streak","loan shark","cursed coin","dice trap","three strikes","nasty or nada"]],
  ["Discard Heroes",    ["discard","blind substitution","lineup randomizer","sacrifice","sack","burn a hero","dump a hero"]],
  ["Control",           ["crystal ball","change the future","pre-game spy","pre-game ritual","pre-game","good guess","please be low","win the toss","heads-up","heads up","leave it to chance","leave it to fate","press your luck","luck of the draw","spy","scout","look at","peek","control","re-org","last-minute re-org"]],
];
function guessPlayCategory(playName) {
  const n = (playName||"").toLowerCase();
  if (!n) return "Misc";
  for (const [cat, kws] of PLAYBOOK_CAT_RULES) {
    if (kws.some(k => n.includes(k))) return cat;
  }
  return "Misc";
}

function PlaybookTab({ user, pbCards, pbSearch, setPbSearch, pbSort, setPbSort, WEAPON_COLORS, setSigningIn, cards, setCards, owned, inp, isMobile, pbName, setPbName, setPbCards, savedPlaybooks=[], pbSaving, pbSaved, pbLoadId, savePbTab, deletePbTab, loadPbTab, newPbTab, setFanDeck, setFanMode }) {
  const _pbAdmin = (user?.email||"").toLowerCase().endsWith("@bazookabreaks.com");
  const [dbsEdits, setDbsEdits] = useState({});
  // Hover preview. Scaling the tile to 1.28x gets you ~140px \u2014 nowhere near enough to READ a
  // play, which is the entire reason you are looking at it. So show a proper floating panel
  // instead: the card at a readable size, plus the play text spelled out beside it.
  const [pbHover, setPbHover] = useState(null);   // {card, x, y}
  const [pbView, setPbView] = useState("grid"); // grid | list
  const [dbsImporting, setDbsImporting] = useState(false);
  const [dbsImportSet, setDbsImportSet] = useState(""); // "" = auto (use CSV prefix); else force this set
  const [dbsStatus, setDbsStatus] = useState(null);
  async function savePbDbs(cardId, val) {
    const v = val === "" ? "" : (parseFloat(val)||0);
    try { await setDoc(doc(db,"boba_checklist",cardId), { dbs: v }, { merge:true }); } catch(e) { console.error("save dbs failed", e); }
    // Instant-everywhere via the live overrides doc
    try { await setDoc(doc(db,"config","dbs_overrides"), { values: { [cardId]: v } }, { merge:true }); } catch(e){}
    try {
      const nextCards = cards.map(c => c.id===cardId ? { ...c, dbs: v } : c);
      if (setCards) setCards(nextCards);
      try { await writeCardSnapshot(nextCards, 300); } catch(e){}
      try { idbSetCards(nextCards, Date.now()); } catch(e){}
    } catch(e){}
  }
  async function importDbsCsv(file) {
    setDbsImporting(true); setDbsStatus({ msg:"Reading CSV…", ok:null });
    try {
      const text = await file.text();
      const rows = text.split(/\r?\n/).filter(Boolean).map(r => {
        const out=[]; let cur=""; let q=false;
        for (const ch of r){ if(ch==='"') q=!q; else if(ch===',' && !q){ out.push(cur); cur=""; } else cur+=ch; }
        out.push(cur); return out;
      });
      const headers = rows[0].map(h => h.toLowerCase().replace(/^"|"$/g,"").trim());
      const find = keys => keys.reduce((f,k)=> f>=0?f:headers.indexOf(k), -1);
      const cnIdx=find(["card number","card_num","card#","cardnum","card num"]);
      const dbsIdx=find(["dbs score","dbs_score","dbs","salary"]);
      const setIdx=find(["set name","set","edition"]);
      const costIdx=find(["hot dog cost","hd cost","play cost","cost"]);
      const nameIdx=find(["play name","name","play","card name"]);
      if (cnIdx<0 || dbsIdx<0) { setDbsStatus({ msg:`❌ Missing columns. Found: ${headers.join(", ")}`, ok:false }); setDbsImporting(false); return; }
      const PREFIX_TO_SET = { a:"2024 Alpha Edition", u:"2025 Alpha Update", g:'2026 Edition "The Griffey Set"', htd:"2025 Alpha Blast", t:"Tecmo Bowl Edition", la:"2024 Alpha Edition" };
      const stripPrefix = s => { let v=String(s||"").trim(); v=v.replace(/^(htd)\s*-\s*/i,""); v=v.replace(/^([A-Za-z]{1,2})\s*-\s*/,""); return v.trim(); };
      const playPrefix = s => { const m=String(s||"").trim().match(/^(HTD|[A-Za-z]{1,2})\s*-/i); return m?m[1].toLowerCase():""; };
      const normStr = s => String(s||"").toLowerCase().replace(/[^a-z0-9]/g,"");
      // fullMap: keyed by the WHOLE card number incl. prefix (apl55, gpl55, htd38…) — collision-proof.
      // baseMap: keyed by the stripped number (pl55) — fallback only, may collide across sets.
      const fullMap={}, baseMap={}, nameMap={};
      cards.forEach(c => {
        const fk=normStr(c.cardNum); if(fk){ if(!fullMap[fk])fullMap[fk]=[]; fullMap[fk].push(c); }
        const bk=normStr(stripPrefix(c.cardNum)); if(bk){ if(!baseMap[bk])baseMap[bk]=[]; baseMap[bk].push(c); }
        const nk=normStr(c.hero); if(nk){ if(!nameMap[nk])nameMap[nk]=[]; nameMap[nk].push(c);} });
      let updated=0, skipped=0, ambiguous=0; const batch=[]; const missSamples=[];
      // Diagnostic: log DB cardNum format so we can see if it matches the CSV format.
      console.log("[DBS import] sample DB card numbers:", cards.slice(0,8).map(c=>`${c.cardNum} (${c.setName})`));
      for (let i=1;i<rows.length;i++){
        const cols=rows[i];
        const rawNum=(cols[cnIdx]||"").replace(/^"|"$/g,"").trim();
        const dbs=parseFloat((cols[dbsIdx]||"").replace(/^"|"$/g,"").trim());
        if (!rawNum || isNaN(dbs)) { skipped++; continue; }
        const csvSet=setIdx>=0?(cols[setIdx]||"").replace(/^"|"$/g,"").trim():"";
        const playCost=costIdx>=0?(cols[costIdx]||"").replace(/^"|"$/g,"").trim():"";
        const rawName=nameIdx>=0?(cols[nameIdx]||"").replace(/^"|"$/g,"").trim():"";
        const prefixSet = PREFIX_TO_SET[playPrefix(rawNum)] || "";
        // If admin forced a set, ONLY process rows belonging to it. Decide membership by the
        // CSV card-number PREFIX (reliable) rather than comparing set-name strings (which may
        // differ between the CSV/map and the DB, e.g. "Tecmo Bowl" vs "Tecmo Bowl Edition").
        const forcedSet = dbsImportSet || "";
        if (forcedSet) {
          // Which prefixes map to the forced set (via PREFIX_TO_SET)? Row must use one of them.
          const prefixesForForced = Object.keys(PREFIX_TO_SET).filter(pk => normStr(PREFIX_TO_SET[pk])===normStr(forcedSet));
          const rowPrefix = playPrefix(rawNum);
          // If we know the forced set's prefixes and the row's prefix isn't one, skip.
          // If we DON'T know (forced set not in the map), don't skip on prefix — rely on set-scope below.
          if (prefixesForForced.length>0 && rowPrefix && !prefixesForForced.includes(rowPrefix)) { skipped++; continue; }
        }
        const resolvedSet = forcedSet || csvSet || prefixSet || "";
        const inScope = c => !resolvedSet || normStr(c.setName||"")===normStr(resolvedSet);

        // 1) EXACT full-cardNum match (prefix included) — unique, no collision.
        let matches = (fullMap[normStr(rawNum)] || []).filter(inScope);
        // 1b) If forced set, also try the STRIPPED number scoped to that set (handles DBs whose
        //     stored cardNum has no prefix, e.g. "PL-55" living in the forced set).
        if (matches.length===0 && forcedSet) {
          matches = (baseMap[normStr(stripPrefix(rawNum))] || []).filter(inScope);
        }
        // 2) Fallback: stripped number, narrow to one by set.
        if (matches.length===0) {
          const base = (baseMap[normStr(stripPrefix(rawNum))] || []).filter(inScope);
          if (base.length===1) matches = base;
          else if (base.length>1 && resolvedSet) {
            const bySet = base.filter(c=>normStr(c.setName||"")===normStr(resolvedSet));
            if (bySet.length===1) matches = bySet;
            else { ambiguous++; continue; }
          } else if (base.length>1) { ambiguous++; continue; }
        }
        // 3) Fallback: name — resolves to exactly one card (scoped to set)
        let byName=false;
        if (matches.length===0 && rawName){
          let nm = (nameMap[normStr(rawName)] || []).filter(inScope);
          if (nm.length===1){ matches=nm; byName=true; } else if (nm.length>1){ ambiguous++; continue; }
        }
        if (matches.length===0){ skipped++; if(missSamples.length<8) missSamples.push(rawNum); continue; }
        if (matches.length>1){ ambiguous++; continue; }   // never write to multiple cards for one row
        for (const m of matches){ const u={dbs}; if(playCost)u.playCost=playCost; batch.push({id:m.id,update:u}); updated++; }
      }

      // INHERIT PASS: reprints (e.g. Tecmo Bowl or Battle Trainer Kit) share a name with an
      // original play but aren't in the CSV. Give a reprint the DBS of its namesake. When the same
      // name has DIFFERENT dbs across sets (e.g. an Alpha original vs its HTD variant), prefer the
      // NON-HTD original value — that's what a plain reprint copies.
      const idsSetThisImport = new Set(batch.map(b=>b.id));
      const dbsSetByRow = {}; // id -> dbs just written
      batch.forEach(({id,update}) => { if(update.dbs!==undefined) dbsSetByRow[id]=update.dbs; });
      const _isHtdCard = c => { const t=(c.treatment||"").toLowerCase(); const h=(c.hero||"").toLowerCase(); const n=String(c.cardNum||"").replace(/^([A-Za-z]{1,2})\s*-\s*/,"").toUpperCase(); return t.includes("htd")||h.includes("htd")||/^HTD/.test(n); };
      // For each play name: collect candidate dbs values, tagged by whether the source is HTD.
      const nameOriginal = {}; // name -> dbs from a NON-HTD source (preferred)
      const nameHtd = {};      // name -> dbs from an HTD source (fallback)
      cards.forEach(c => {
        if (!c.hero) return;
        const d = dbsSetByRow[c.id]!==undefined ? dbsSetByRow[c.id] : c.dbs;
        if (d===undefined || d==="" || isNaN(parseFloat(d))) return;
        const nk = normStr(c.hero.replace(/\s*-?\s*htd\s*$/i,"")); // strip trailing "- htd" so it groups with its original
        const val = parseFloat(d);
        if (_isHtdCard(c)) { if(nameHtd[nk]===undefined) nameHtd[nk]=val; }
        else { if(nameOriginal[nk]===undefined) nameOriginal[nk]=val; }
      });
      const isPlayCard = c => { const t=(c.treatment||"").toLowerCase(); const n=String(c.cardNum||"").replace(/^([A-Za-z]{1,2})\s*-\s*/,"").toUpperCase(); return t.includes("play")||t==="home team discount"||/^(PL|BPL|HTD)/.test(n); };
      let inherited=0;
      cards.forEach(c => {
        if (!c.hero || idsSetThisImport.has(c.id)) return;   // set directly this import → skip
        if (!isPlayCard(c)) return;
        // INHERIT ONLY FILLS BLANKS — never overwrite a DBS that's already set (protects any
        // hand-tuned values on reprints not in the CSV).
        if (c.dbs!==undefined && c.dbs!=="" && !isNaN(parseFloat(c.dbs))) return;
        const nk = normStr(c.hero.replace(/\s*-?\s*htd\s*$/i,""));
        // A reprint prefers the non-HTD original; an HTD reprint prefers the HTD value.
        const inheritedDbs = _isHtdCard(c) ? (nameHtd[nk] ?? nameOriginal[nk]) : (nameOriginal[nk] ?? nameHtd[nk]);
        if (inheritedDbs===undefined) return;
        batch.push({ id:c.id, update:{ dbs: inheritedDbs } });
        inherited++;
      });

      setDbsStatus({ msg:`Writing ${batch.length} cards…`, ok:null });
      for (let i=0;i<batch.length;i+=400){ const chunk=batch.slice(i,i+400); await Promise.all(chunk.map(({id,update})=>setDoc(doc(db,"boba_checklist",id),update,{merge:true}))); }
      // INSTANT-EVERYWHERE: also write DBS to the live config/dbs_overrides doc that every
      // client subscribes to, so the change propagates immediately (no snapshot cache wait).
      try {
        const dbsMap = {}; batch.forEach(({id,update}) => { if (update.dbs !== undefined) dbsMap["values."+id] = update.dbs; });
        if (Object.keys(dbsMap).length) await setDoc(doc(db,"config","dbs_overrides"), { values: {} }, { merge:true }).then(()=>updateDoc(doc(db,"config","dbs_overrides"), dbsMap));
      } catch(e){ console.warn("dbs_overrides write failed", e); }
      // Also update the local snapshot so a hard reload (cache) is consistent too.
      try {
        const updateById = {}; batch.forEach(({id,update}) => { updateById[id] = { ...(updateById[id]||{}), ...update }; });
        const nextCards = cards.map(c => updateById[c.id] ? { ...c, ...updateById[c.id] } : c);
        if (setCards) setCards(nextCards);
        try { await writeCardSnapshot(nextCards, 300); } catch(e){ console.warn("snapshot rewrite failed", e); }
        try { await idbSetCards(nextCards, Date.now()); } catch(e){}
        try { localStorage.removeItem("boba_checklist_cache_v3"); } catch(e){}
      } catch(e){ console.warn("card refresh after DBS import failed", e); }
      if (missSamples.length) console.log("[DBS import] unmatched CSV card numbers (sample):", missSamples);
      setDbsStatus({ msg:`✅ Updated ${updated}${inherited?` · ${inherited} reprints`:""}${ambiguous?` · ${ambiguous} ambiguous`:""}${skipped?` · ${skipped} no-match`:""}${updated===0?" — 0 matched! Check console for card-number format":""}`, ok: updated>0 });
    } catch(e){ console.error(e); setDbsStatus({ msg:"❌ Import failed: "+(e.message||"error"), ok:false }); }
    setDbsImporting(false);
  }
  const [pbEvent, setPbEvent] = useState("");
  const [pbSetFilter, setPbSetFilter] = useState("");
  const [pbTypeFilter, setPbTypeFilter] = useState(""); // "", "play", "bonus", "paper", "htd"
  const evt = pbEvent ? BOBA_EVENTS[pbEvent] : null;
  const evtPlaySet = evt ? new Set([...(evt.plays||[]), ...(evt.bonusPlays||[])].map(s=>s.toLowerCase().trim())) : null;
  const inEvent = c => !evtPlaySet || evtPlaySet.has((c.hero||"").toLowerCase().trim());
  // A card's number, with any set prefix stripped: "A - PL-79" -> "PL-79", "T-BPL-22" -> "BPL-22", "HTD-46" -> "HTD-46"
  const _pbBaseNum = c => { let v=String(c.cardNum||"").trim(); v=v.replace(/^([A-Za-z]{1,2})\s*-\s*/,""); return v.toUpperCase(); };
  // Bonus play: treatment mentions bonus, OR card number is a BPL
  const isBonus = c => { const t=(c.treatment||"").toLowerCase(); const n=_pbBaseNum(c); return t.includes("bonus") || /^BPL/.test(n); };
  // Regular play: treatment is play-ish (not bonus), OR card number is PL/HTD (not BPL)
  const isPlay = c => {
    const t=(c.treatment||"").toLowerCase(); const n=_pbBaseNum(c);
    if (isBonus(c)) return false;
    return t.includes("play") || t==="home team discount" || /^PL/.test(n) || /^HTD/.test(n);
  };
  const playCards=cards.filter(c=>isPlay(c)||isBonus(c));
  const pbEntryIds=new Set(pbCards.map(e=>e.id));
  const playCount=pbCards.filter(e=>e.type==="play").length;
  const bonusCount=pbCards.filter(e=>e.type==="bonus").length;
  const playFull=playCount>=PUBLIC_PLAY_LIMIT;
  const pbResolved=pbCards.map(e=>({...e,card:cards.find(c=>c.id===e.id)})).filter(e=>e.card);
  const totalDbs=pbResolved.reduce((s,e)=>s+(parseFloat(e.card.dbs)||0),0);
  const dbsLeft=PUBLIC_DBS_CAP-totalDbs, dbsPct=Math.min(totalDbs/PUBLIC_DBS_CAP*100,100), dbsOver=totalDbs>PUBLIC_DBS_CAP;
  // Playbook coach — live, contextual guidance
  const pbCoach=(()=>{
    const tips=[];
    if(pbResolved.length===0) return [{t:"info",m:"Add plays to start building. Tips will appear here as your playbook takes shape."}];
    if(playFull) tips.push({t:"good",m:`Play limit reached — ${PUBLIC_PLAY_LIMIT} plays locked in. 🎉`});
    else tips.push({t:"info",m:`${PUBLIC_PLAY_LIMIT-playCount} play slot${PUBLIC_PLAY_LIMIT-playCount===1?"":"s"} left (Bonus Plays are unlimited).`});
    if(dbsOver) tips.push({t:"warn",m:`Over the DBS cap by ${Math.round(totalDbs-PUBLIC_DBS_CAP)} — remove a high-DBS play to get legal.`});
    else if(dbsPct>80) tips.push({t:"warn",m:`DBS budget ${Math.round(dbsPct)}% used — ${Math.round(dbsLeft)} left, getting tight.`});
    else if(pbResolved.length>0) tips.push({t:"info",m:`DBS budget: ${Math.round(totalDbs)}/${PUBLIC_DBS_CAP} used, ${Math.round(dbsLeft)} remaining.`});
    if(evt) tips.push({t:"info",m:`Building for ${evt.name} — only checklist plays can be added.`});
    if(bonusCount===0&&playCount>5) tips.push({t:"info",m:"No Bonus Plays yet — they're unlimited and add flexibility."});
    return tips.slice(0,5);
  })();

  // ── Playbook Make-Up: category breakdown of the current playbook ──────────
  const pbIsAdmin = user?.email?.toLowerCase().includes("devin") || user?.email?.toLowerCase().includes("derrik") || (user?.email||"").toLowerCase().endsWith("@bazookabreaks.com");
  const [catOverrides, setCatOverrides] = useState({}); // { playName: category }
  const [expandedCat, setExpandedCat] = useState(null);
  const [showPbPickList, setShowPbPickList] = useState(false);
  const [pbPickSort, setPbPickSort] = useState("cardnum");   // cardnum | name | cost | set
  const [pbCopied, setPbCopied] = useState(false);
  const [pbPickChecked, setPbPickChecked] = useState({});   // {playId: true} — pulled plays, resets on close
  const [pinnedCat, setPinnedCat] = useState(null);
  useEffect(() => {
    const unsub = onSnapshot(doc(db,"meta","play_categories"),
      snap => setCatOverrides(snap.exists() ? (snap.data().map || {}) : {}),
      () => {});
    return () => unsub();
  }, []);
  const catOf = card => {
    const key = (card.playName || card.hero || "").trim();
    if (catOverrides[key]) return catOverrides[key];
    return guessPlayCategory(card.playName || card.hero || "");
  };
  async function setPlayCategory(card, category) {
    const key = (card.playName || card.hero || "").trim();
    if (!key) return;
    const next = { ...catOverrides, [key]: category };
    setCatOverrides(next);
    try { await setDoc(doc(db,"meta","play_categories"), { map: next }, { merge:true }); }
    catch(e){ alert("Couldn't save category override: " + (e?.message||e)); }
  }
  const makeup = (() => {
    const counts = {}; PLAYBOOK_CATEGORIES.forEach(c => counts[c] = { count:0, cards:[] });
    pbResolved.forEach(e => { const cat = catOf(e.card); (counts[cat] || (counts[cat]={count:0,cards:[]})).count++; counts[cat].cards.push(e); });
    const total = pbResolved.length || 1;
    const playEntries = pbResolved.filter(e => e.type==="play");
    const bonusEntries = pbResolved.filter(e => e.type==="bonus");
    const dbsVals = pbResolved.map(e => parseFloat(e.card.dbs)||0).filter(v => v>0);
    const costVals = pbResolved.map(e => parseFloat(e.card.playCost)).filter(v => !isNaN(v));
    const avgDbs = dbsVals.length ? dbsVals.reduce((s,v)=>s+v,0)/dbsVals.length : 0;
    const avgCost = costVals.length ? costVals.reduce((s,v)=>s+v,0)/costVals.length : 0;
    return { counts, total, playEntries, bonusEntries, avgDbs, avgCost };
  })();

  // Classify a play by sub-type using treatment + card number prefix
  const pbCardType = (c) => {
    const t=(c.treatment||"").toLowerCase();
    const n=String(c.cardNum||"").replace(/^([A-Za-z]{1,2})\s*-\s*/,"").toUpperCase();
    if (t.includes("bonus") || /^BPL/.test(n)) return "bonus";
    if (t.includes("paper") ) return "paper";
    if (t.includes("htd") || t.includes("home team discount") || /^HTD/.test(n)) return "htd";
    return "play";
  };
  const pbAvail=playCards.filter(c=>{if(pbEntryIds.has(c.id))return false;if(!inEvent(c))return false;if(pbSetFilter&&c.setName!==pbSetFilter)return false;if(pbTypeFilter&&pbCardType(c)!==pbTypeFilter)return false;if(pbSearch&&!`${c.hero} ${c.cardNum} ${c.playAbility||""}`.toLowerCase().includes(pbSearch.toLowerCase()))return false;return true;}).sort((a,b)=>{if(pbSort==="dbs_desc")return(parseFloat(b.dbs)||0)-(parseFloat(a.dbs)||0);if(pbSort==="dbs_asc")return(parseFloat(a.dbs)||0)-(parseFloat(b.dbs)||0);return(a.hero||"").localeCompare(b.hero||"");});
  const pbSets=[...new Set(playCards.map(c=>c.setName).filter(Boolean))].sort();
  return (
          <>
            <div className="deck-pb-layout" style={{display:"flex",flexDirection:isMobile?"column-reverse":"row",gap:16,alignItems:"stretch",height:isMobile?"auto":"calc(100vh - 150px)",minHeight:isMobile?"auto":520}}>
              <div style={{display:"flex",flexDirection:"column",gap:10,flex:1,minWidth:0,minHeight:0}}>
                <div className="filter-bar" style={{display:"flex",gap:8,flexWrap:"wrap",background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:14,padding:"12px 16px",backdropFilter:"blur(10px)"}}>
                  <input value={pbSearch} onChange={e=>setPbSearch(e.target.value)} placeholder="Search play or ability..." style={{...inp,flex:1}}/>
                  <select value={pbSetFilter} onChange={e=>setPbSetFilter(e.target.value)} style={{...inp,width:"auto",cursor:"pointer",fontWeight:700,color:pbSetFilter?"#4ade80":"rgba(255,255,255,0.4)"}}>
                    <option value="">All Sets</option>
                    {pbSets.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                  <select value={pbTypeFilter} onChange={e=>setPbTypeFilter(e.target.value)} style={{...inp,width:"auto",cursor:"pointer",fontWeight:700,color:pbTypeFilter?"#C084FC":"rgba(255,255,255,0.4)"}}>
                    <option value="">All Types</option>
                    <option value="play">Plays</option>
                    <option value="paper">Paper Plays</option>
                    <option value="bonus">Bonus Plays</option>
                    <option value="htd">HTD Plays</option>
                  </select>
                  <select value={pbEvent} onChange={e=>setPbEvent(e.target.value)} style={{...inp,width:"auto",cursor:"pointer",fontWeight:700,color:pbEvent?"#F59E0B":"rgba(255,255,255,0.4)"}}>
                    <option value="">All Plays (no event)</option>
                    <optgroup label="── Events ──">
                      {Object.entries(BOBA_EVENTS).map(([k,ev])=><option key={k} value={k}>🎲 {ev.name}</option>)}
                    </optgroup>
                  </select>
                  <select value={pbSort} onChange={e=>setPbSort(e.target.value)} style={{...inp,width:"auto",cursor:"pointer"}}>
                    <option value="name">{"Name A\u2192Z"}</option>
                    <option value="dbs_desc">{"DBS High\u2192Low"}</option>
                    <option value="dbs_asc">{"DBS Low\u2192High"}</option>
                  </select>
                  <div style={{display:"flex",gap:2,background:"rgba(255,255,255,0.05)",borderRadius:8,padding:2}}>
                    {[["grid","▦"],["list","☰"]].map(([v,ic])=>(
                      <button key={v} onClick={()=>setPbView(v)} title={v==="grid"?"Card view":"List view"} style={{background:pbView===v?"#E8317A":"transparent",color:pbView===v?"#fff":"rgba(255,255,255,0.5)",border:"none",borderRadius:6,padding:"5px 10px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>{ic}</button>
                    ))}
                  </div>
                  {_pbAdmin && (
                    <select value={dbsImportSet} onChange={e=>setDbsImportSet(e.target.value)} title="Force which set the DBS applies to (recommended). Auto uses the CSV's card-number prefix." style={{ background:"#241820", color:"#f6eef2", border:"1px solid #C084FC44", borderRadius:8, padding:"6px 8px", fontSize:11, fontFamily:"inherit", alignSelf:"center", maxWidth:180 }}>
                      <option value="">Auto (by prefix)</option>
                      {[...new Set(cards.map(c=>c.setName).filter(Boolean))].sort().map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                  {_pbAdmin && (
                    <label title="Import DBS values from CSV (Card Number, Play Name, Cost, DBS Score)" style={{ background:"#1a0f1a", color:"#C084FC", border:"1px solid #C084FC44", borderRadius:8, padding:"6px 12px", fontSize:12, fontWeight:700, cursor:dbsImporting?"not-allowed":"pointer", fontFamily:"inherit", whiteSpace:"nowrap", opacity:dbsImporting?0.5:1, alignSelf:"center" }}>
                      {dbsImporting?"Importing…":"💰 Import DBS"}
                      <input type="file" accept=".csv" disabled={dbsImporting} onChange={e=>{ const f=e.target.files[0]; if(f) importDbsCsv(f); e.target.value=""; }} style={{ display:"none" }}/>
                    </label>
                  )}
                  {_pbAdmin && (
                    <button
                      onClick={()=>{
                        const rows=[["Play Name","Card #","Set","Weapon","Play Cost","DBS Value","Ability"]];
                        pbAvail.forEach(c=>rows.push([c.hero||"",c.cardNum||"",c.setName||"",c.weapon||"",c.playCost??"",c.dbs??"",(c.playAbility||"").replace(/\s+/g," ").trim()]));
                        const csv=rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
                        const blob=new Blob([csv],{type:"text/csv;charset=utf-8"});
                        const url=URL.createObjectURL(blob);
                        const a=document.createElement("a");a.href=url;a.download=`plays-dbs-${new Date().toISOString().split("T")[0]}.csv`;
                        document.body.appendChild(a);a.click();document.body.removeChild(a);
                        setTimeout(()=>URL.revokeObjectURL(url),4000);
                      }}
                      title="Export the listed plays with their DBS values to CSV"
                      style={{ background:"#0f1a12", color:"#4ade80", border:"1px solid #4ade8044", borderRadius:8, padding:"6px 12px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", alignSelf:"center" }}>
                      ⬇ Export DBS
                    </button>
                  )}
                  {dbsStatus && <span style={{ fontSize:11, fontWeight:700, color:dbsStatus.ok===true?"#4ade80":dbsStatus.ok===false?"#E8317A":"#FBBF24", alignSelf:"center" }}>{dbsStatus.msg}</span>}
                  <span style={{fontSize:11,color:"rgba(255,255,255,0.2)",alignSelf:"center"}}>{pbAvail.length} plays</span>
                </div>
                {evt && (
                  <div style={{background:`${evt.color}10`,border:`1px solid ${evt.color}40`,borderRadius:12,padding:"12px 14px"}}>
                    <div style={{fontSize:14,fontWeight:900,color:evt.color,display:"flex",alignItems:"center",gap:6}}>🎲 {evt.name} <span style={{fontSize:9,fontWeight:700,background:`${evt.color}22`,borderRadius:4,padding:"2px 6px"}}>CHECKLIST EVENT</span></div>
                    <div style={{fontSize:11.5,color:evt.color,fontWeight:700,fontStyle:"italic",margin:"4px 0 7px"}}>{evt.tagline}</div>
                    <div style={{fontSize:11.5,color:"rgba(255,255,255,0.6)",lineHeight:1.55,marginBottom:9}}>{evt.description}</div>
                    <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:9}}>
                      {evt.rules.map((r,i)=>(<span key={i} style={{fontSize:11.5,color:"rgba(255,255,255,0.6)",display:"flex",alignItems:"flex-start",gap:6}}><span style={{color:evt.color}}>•</span>{r}</span>))}
                    </div>
                    <div style={{fontSize:11,color:evt.color,background:`${evt.color}14`,borderRadius:6,padding:"7px 10px",fontWeight:600,lineHeight:1.45}}>🏆 {evt.region}</div>
                    <div style={{fontSize:10.5,color:"rgba(255,255,255,0.4)",marginTop:8}}>Only the {(evt.plays||[]).length + (evt.bonusPlays||[]).length} Plays on the {evt.name} Checklist are shown below.</div>
                  </div>
                )}
                <div className="deck-pb-cardlist" style={{flex:1,minHeight:0,overflowY:"auto",overflowX:"hidden",paddingRight:4,paddingTop:12,paddingBottom:24}}>
                  {pbAvail.length===0?<div style={{padding:40,textAlign:"center",color:"rgba(255,255,255,0.2)"}}>No plays match{evt?` the ${evt.name} checklist`:" filters"}</div>:
                  pbView==="list"?
                  <div style={{display:"flex",flexDirection:"column",gap:2}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 60px 55px 80px 70px",gap:8,padding:"6px 12px",fontSize:10,fontWeight:800,color:"rgba(255,255,255,0.4)",textTransform:"uppercase",letterSpacing:1,borderBottom:"1px solid rgba(255,255,255,0.1)",position:"sticky",top:0,background:"var(--bz-s1)",zIndex:2}}>
                      <span>Play</span><span style={{textAlign:"center"}}>Type</span><span style={{textAlign:"center"}}>Cost</span><span style={{textAlign:"center"}}>DBS</span><span style={{textAlign:"center"}}>Add</span>
                    </div>
                    {pbAvail.map((c)=>{
                      const playable=isPlay(c), bonus=isBonus(c);
                      const wouldExceed=totalDbs+(parseFloat(c.dbs)||0)>PUBLIC_DBS_CAP;
                      const blocked=(playable&&(playFull||wouldExceed))||(bonus&&wouldExceed);
                      const addIt=()=>{ if(blocked)return; if(playable)setPbCards(p=>[...p,{id:c.id,type:"play"}]); else if(bonus)setPbCards(p=>[...p,{id:c.id,type:"bonus"}]); };
                      return (
                        <div key={c.id} style={{display:"grid",gridTemplateColumns:"1fr 60px 55px 80px 70px",gap:8,alignItems:"center",padding:"8px 12px",borderRadius:8,background:"rgba(255,255,255,0.02)",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                          <div style={{minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:800,color:"#f6eef2",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.hero||"(unnamed play)"}</div>
                            <div style={{display:"flex",gap:6,alignItems:"center",marginTop:1}}>
                              {c.setName&&<span style={{fontSize:9,fontWeight:700,color:"#C084FC",background:"rgba(192,132,252,0.12)",borderRadius:4,padding:"1px 6px",whiteSpace:"nowrap"}}>{c.setName}</span>}
                              {c.cardNum&&<span style={{fontSize:9,color:"rgba(255,255,255,0.35)"}}>#{c.cardNum}</span>}
                            </div>
                            {c.playAbility&&<div style={{fontSize:10,color:"rgba(255,255,255,0.45)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginTop:2}}>{c.playAbility}</div>}
                          </div>
                          <span style={{textAlign:"center",fontSize:10,fontWeight:800,color:bonus?"#7B9CFF":"#E8317A"}}>{bonus?"BPL":"PLAY"}</span>
                          <span style={{textAlign:"center",fontSize:12,color:"#FBBF24",fontWeight:700}}>{hasPlayCost(c)?c.playCost:"—"}</span>
                          <div style={{textAlign:"center"}}>
                            {_pbAdmin
                              ? <input type="number" value={dbsEdits[c.id]!==undefined?dbsEdits[c.id]:(c.dbs!==undefined&&c.dbs!==""?c.dbs:"")}
                                  onChange={e=>setDbsEdits(p=>({...p,[c.id]:e.target.value}))}
                                  onBlur={e=>{ if(dbsEdits[c.id]!==undefined && String(dbsEdits[c.id])!==String(c.dbs??"")) savePbDbs(c.id,e.target.value); }}
                                  onWheel={e=>e.currentTarget.blur()}
                                  placeholder="—"
                                  style={{width:64,background:"rgba(192,132,252,0.12)",border:"1px solid #C084FC66",color:"#C084FC",borderRadius:5,padding:"4px 6px",fontSize:12,fontWeight:800,fontFamily:"inherit",textAlign:"center"}}/>
                              : <span style={{fontSize:12,fontWeight:800,color:"#C084FC"}}>{c.dbs!==undefined&&c.dbs!==""?c.dbs:"—"}</span>}
                          </div>
                          <div style={{textAlign:"center"}}>
                            <button onClick={addIt} disabled={blocked} title={blocked?(playFull?"Play limit reached":"DBS cap reached"):`Add ${c.hero}`} style={{background:blocked?"rgba(255,255,255,0.05)":"rgba(74,222,128,0.15)",border:`1px solid ${blocked?"#333":"rgba(74,222,128,0.5)"}`,color:blocked?"#555":"#4ade80",borderRadius:6,padding:"4px 10px",fontSize:14,fontWeight:900,cursor:blocked?"not-allowed":"pointer",fontFamily:"inherit",lineHeight:1}}>+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  :
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(auto-fill,minmax(95px,1fr))":"repeat(auto-fill,minmax(120px,1fr))",gap:10}}>
                  {pbAvail.map((c)=>{
                    const wc=WEAPON_COLORS[canonWeapon(c.weapon)]||"#A855F7",wouldExceed=totalDbs+(parseFloat(c.dbs)||0)>PUBLIC_DBS_CAP;
                    const playable=isPlay(c), bonus=isBonus(c);
                    const blocked=(playable&&(playFull||wouldExceed))||(bonus&&wouldExceed);
                    const addIt=()=>{ if(blocked)return; if(playable)setPbCards(p=>[...p,{id:c.id,type:"play"}]); else if(bonus)setPbCards(p=>[...p,{id:c.id,type:"bonus"}]); };
                    return (
                      <div key={c.id} onClick={addIt} title={blocked?(playFull?"Play limit reached":"DBS cap reached"):`Add ${c.hero}`}
                        style={{position:"relative",aspectRatio:"3/4",borderRadius:10,overflow:"hidden",cursor:blocked?"not-allowed":"pointer",opacity:blocked?0.4:1,border:`1.5px solid ${blocked?"rgba(232,49,122,0.3)":"rgba(255,255,255,0.08)"}`,background:"var(--bz-s1)",transition:"transform 0.15s ease, border-color 0.15s ease"}}
                        onMouseEnter={e=>{const r=e.currentTarget.getBoundingClientRect(); setPbHover({card:c, x:r.right, y:r.top}); if(!blocked){e.currentTarget.style.transform="scale(1.08)";e.currentTarget.style.borderColor=wc;e.currentTarget.style.zIndex="10";e.currentTarget.style.boxShadow="0 12px 40px rgba(0,0,0,0.7)";}}}
                        onMouseLeave={e=>{setPbHover(null); e.currentTarget.style.transform="none";e.currentTarget.style.borderColor=blocked?"rgba(232,49,122,0.3)":"rgba(255,255,255,0.08)";e.currentTarget.style.zIndex="";e.currentTarget.style.boxShadow="";}}>
                        {c.imageUrl
                          ? <img src={c.imageUrl} alt={c.hero} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                          : <div style={{width:"100%",height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"20px 7px 8px",textAlign:"center",gap:4}}><div style={{fontSize:11,fontWeight:800,color:wc,lineHeight:1.25}}>{c.playName||c.hero}</div>{c.text&&<div style={{fontSize:8.5,color:"rgba(255,255,255,0.55)",lineHeight:1.35,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:5,WebkitBoxOrient:"vertical"}}>{c.text}</div>}<div style={{fontSize:8,color:"rgba(255,255,255,0.28)",marginTop:"auto"}}>{bonus?"Bonus Play":"Play"}</div></div>}
                        <div style={{position:"absolute",inset:0,background:"linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.1) 40%, transparent 62%)",pointerEvents:"none"}}/>
                        <div style={{position:"absolute",left:0,right:0,bottom:0,padding:"7px 8px",pointerEvents:"none"}}>
                          <div style={{fontSize:11,fontWeight:800,color:"#fff",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",textShadow:"0 1px 3px rgba(0,0,0,0.8)"}}>{c.playName||c.hero}</div>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:2,gap:4}}>
                            <span style={{display:"flex",alignItems:"center",gap:5}}><span style={{fontSize:7.5,fontWeight:800,color:"#fff",background:(bonus?"#7B9CFF":"#E8317A")+"dd",borderRadius:3,padding:"1px 5px",letterSpacing:0.3}}>{bonus?"BPL":"PLAY"}</span>{hasPlayCost(c)?<span style={{fontSize:9,color:"#FBBF24",fontWeight:700}}>Cost {c.playCost}</span>:<span/>}</span>
                            {_pbAdmin
                              ? <input type="number"
                                  value={dbsEdits[c.id]!==undefined ? dbsEdits[c.id] : (c.dbs!==undefined&&c.dbs!==""?c.dbs:"")}
                                  onChange={e=>setDbsEdits(p=>({...p,[c.id]:e.target.value}))}
                                  onBlur={e=>{ if(dbsEdits[c.id]!==undefined && String(dbsEdits[c.id])!==String(c.dbs??"")) savePbDbs(c.id,e.target.value); }}
                                  onClick={e=>e.stopPropagation()}
                                  onMouseDown={e=>e.stopPropagation()}
                                  title="DBS (admin editable)"
                                  placeholder="DBS"
                                  style={{width:42,background:"rgba(192,132,252,0.12)",border:"1px solid #C084FC66",color:"#C084FC",borderRadius:4,padding:"1px 3px",fontSize:11,fontWeight:900,fontFamily:"inherit",textAlign:"center",pointerEvents:"auto"}}/>
                              : (c.dbs!==undefined&&c.dbs!==""&&<span style={{fontSize:11,fontWeight:900,color:"#C084FC",textShadow:"0 1px 3px rgba(0,0,0,0.9)"}}>{c.dbs}</span>)}
                          </div>
                        </div>
                        {!blocked&&<div className="deck-add-badge" style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:38,height:38,borderRadius:"50%",background:"rgba(74,222,128,0.95)",color:"#000",fontSize:24,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center",opacity:0,transition:"opacity 0.15s",pointerEvents:"none",boxShadow:"0 4px 16px rgba(0,0,0,0.5)",zIndex:2}}>+</div>}
                      </div>
                    );
                  })}
                  </div>}
                </div>

              {/* Hover preview. The tile itself can't grow enough to be readable — it's locked in a grid
                  and would just shove its neighbours around. A fixed-position panel can be as big as it
                  needs to be, and it can spell the play text out in full rather than clipping it. */}
              {pbHover && !isMobile && (() => {
                const c = pbHover.card;
                const W = 300;
                // Flip to the left of the tile if there isn't room on the right.
                const left = (pbHover.x + 14 + W > window.innerWidth) ? pbHover.x - W - 180 : pbHover.x + 14;
                const top  = Math.max(12, Math.min(pbHover.y - 40, window.innerHeight - 440));
                return (
                  <div style={{position:"fixed",left,top,width:W,zIndex:13000,pointerEvents:"none",
                    background:"#161616",border:"1px solid #333",borderRadius:14,padding:14,
                    boxShadow:"0 24px 70px rgba(0,0,0,0.8)",animation:"fabIn 0.12s ease"}}>
                    {c.imageUrl && (
                      <img src={c.imageUrl} alt="" style={{width:"100%",borderRadius:9,display:"block",marginBottom:11}}/>
                    )}
                    <div style={{fontSize:15,fontWeight:800,color:"#fff",lineHeight:1.3,marginBottom:4}}>
                      {c.playName || c.hero || "\u2014"}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:9,flexWrap:"wrap"}}>
                      {hasPlayCost(c) && (
                        <span style={{fontSize:11,fontWeight:800,color:"#FBBF24"}}>Cost {c.playCost}</span>
                      )}
                      {c.dbs!==undefined && c.dbs!=="" && (
                        <span style={{fontSize:11,fontWeight:900,color:"#C084FC"}}>{c.dbs} DBS</span>
                      )}
                      <span style={{fontSize:10.5,color:"rgba(255,255,255,0.35)"}}>{c.cardNum}</span>
                    </div>
                    {/* The whole point: the play text, in full, at a size a person can actually read. */}
                    {c.text && (
                      <div style={{fontSize:12.5,color:"rgba(255,255,255,0.75)",lineHeight:1.65,
                        background:"rgba(255,255,255,0.03)",borderRadius:8,padding:"10px 11px",
                        borderLeft:"2px solid rgba(255,255,255,0.15)"}}>
                        {c.text}
                      </div>
                    )}
                    {!c.text && !c.imageUrl && (
                      <div style={{fontSize:11.5,color:"rgba(255,255,255,0.3)",fontStyle:"italic"}}>
                        No play text on this card yet.
                      </div>
                    )}
                  </div>
                );
              })()}
              </div>
              <div className="deck-pb-panel" style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:16,padding:"20px 18px",backdropFilter:"blur(10px)",width:isMobile?"100%":"clamp(280px,30%,360px)",flexShrink:0,overflowY:"auto",minHeight:0}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                  <div style={{fontSize:15,fontWeight:800,color:"#fff",letterSpacing:"-0.2px"}}>{pbName||"Playbook"}</div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    {pbResolved.length>0 && setFanDeck && <button onClick={()=>{setFanMode&&setFanMode("grid");setFanDeck({name:pbName||"Playbook",cards:pbResolved.map(e=>e.card)});}} title="Expand to view your playbook" style={{background:"rgba(123,156,255,0.12)",border:"1px solid rgba(123,156,255,0.35)",color:"#7B9CFF",borderRadius:16,padding:"3px 10px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>⛶ Expand</button>}
                    {pbResolved.length>0 && <button onClick={()=>setShowPbPickList(true)} title="Printable pick list" style={{background:"rgba(251,191,36,0.12)",border:"1px solid rgba(251,191,36,0.35)",color:"#FBBF24",borderRadius:16,padding:"3px 10px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>📋 Pick List</button>}
                    <span style={{fontSize:12,fontWeight:800,color:playFull?"#E8317A":"#4ade80",background:playFull?"rgba(232,49,122,0.1)":"rgba(74,222,128,0.1)",borderRadius:20,padding:"3px 11px"}}>{playCount}/{PUBLIC_PLAY_LIMIT}</span>
                  </div>
                </div>
                {savePbTab && (
                  <div style={{marginBottom:14}}>
                    <input value={pbName} onChange={e=>setPbName(e.target.value)} placeholder="Playbook name" style={{...inp,width:"100%",marginBottom:8,fontSize:12,padding:"7px 10px"}}/>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={savePbTab} disabled={pbSaving||pbCards.length===0} style={{flex:1,background:pbSaved?"#0a2a0a":"#0a1a0a",border:`1px solid ${pbSaved?"#4ade80":"#4ade8044"}`,color:"#4ade80",borderRadius:8,padding:"8px 0",fontSize:12,fontWeight:700,cursor:pbCards.length===0?"not-allowed":"pointer",fontFamily:"inherit",opacity:pbCards.length===0?0.4:1}}>
                        {pbSaving?"Saving...":pbSaved?"✓ Saved":"💾 Save"}</button>
                      <button onClick={newPbTab} style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ New</button>
                    </div>
                    {savedPlaybooks.length>0 && (
                      <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:8}}>
                        {savedPlaybooks.map(p=>(
                          <div key={p.id} style={{display:"flex",alignItems:"center",gap:3,background:pbLoadId===p.id?"#1A1A2E":"var(--bz-s2)",border:`1px solid ${pbLoadId===p.id?"#7B9CFF":"var(--bz-line-2)"}`,borderRadius:7,padding:"3px 8px"}}>
                            <button onClick={()=>loadPbTab(p)} style={{background:"none",border:"none",color:pbLoadId===p.id?"#7B9CFF":"#888",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{p.name} <span style={{color:"var(--bz-ink-3)",fontWeight:400}}>({(p.playCount||0)+(p.bonusCount||0)})</span></button>
                            <button onClick={()=>deletePbTab(p.id)} style={{background:"none",border:"none",color:"#8a8a8a",cursor:"pointer",fontSize:13,lineHeight:1,padding:"0 1px"}}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
                  {[{l:"Plays",v:`${playCount}/${PUBLIC_PLAY_LIMIT}`,c:playFull?"#E8317A":"#4ade80"},{l:"Bonus Plays",v:bonusCount,c:"#7B9CFF"},{l:"Total DBS",v:Math.round(totalDbs),c:dbsOver?"#E8317A":"#C084FC"},{l:"Total Cards",v:pbResolved.length,c:"#FBBF24"}].map(({l,v,c})=>(
                    <div key={l} style={{background:"rgba(255,255,255,0.04)",borderRadius:10,padding:"11px 12px"}}>
                      <div style={{fontSize:20,fontWeight:800,color:c,letterSpacing:"-0.5px",lineHeight:1}}>{v}</div>
                      <div style={{fontSize:9.5,color:"rgba(255,255,255,0.35)",marginTop:5,textTransform:"uppercase",letterSpacing:0.8,fontWeight:600}}>{l}</div>
                    </div>
                  ))}
                </div>
                <div style={{marginBottom:12}}>
                  <div style={{height:4,background:"rgba(255,255,255,0.05)",borderRadius:2,overflow:"hidden",marginBottom:4}}>
                    <div style={{width:`${Math.min(playCount/PUBLIC_PLAY_LIMIT*100,100)}%`,height:"100%",background:playFull?"#E8317A":"linear-gradient(90deg,#E8317A,#7B2FF7)",borderRadius:2,transition:"width 0.3s"}}/>
                  </div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>{PUBLIC_PLAY_LIMIT-playCount} play slots left</div>
                </div>
                <div style={{background:dbsOver?"rgba(232,49,122,0.05)":"rgba(0,0,0,0.3)",border:`1px solid ${dbsOver?"rgba(232,49,122,0.3)":"rgba(255,255,255,0.05)"}`,borderRadius:10,padding:"12px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                    <span style={{fontSize:11,fontWeight:800,color:dbsOver?"#E8317A":"#A855F7"}}>{"\uD83D\uDCB0 DBS Budget"}</span>
                    <span style={{fontSize:11,fontWeight:700,color:dbsOver?"#E8317A":dbsPct>80?"#FBBF24":"#4ade80"}}>{Math.round(totalDbs)} / {PUBLIC_DBS_CAP}</span>
                  </div>
                  <div style={{height:6,background:"rgba(255,255,255,0.05)",borderRadius:3,overflow:"hidden",marginBottom:6}}>
                    <div style={{width:`${dbsPct}%`,height:"100%",background:dbsOver?"#E8317A":dbsPct>80?"linear-gradient(90deg,#FBBF24,#E8317A)":"linear-gradient(90deg,#A855F7,#7B9CFF)",borderRadius:3,transition:"width 0.3s"}}/>
                  </div>
                  <div style={{fontSize:10,color:dbsOver?"#E8317A":dbsLeft<100?"#FBBF24":"rgba(255,255,255,0.3)",fontWeight:dbsOver?700:400}}>{dbsOver?`\u26A0\uFE0F Over by ${Math.round(totalDbs-PUBLIC_DBS_CAP)}`:`${Math.round(dbsLeft)} remaining`}</div>
                </div>
                {pbCoach.length>0 && (
                  <div style={{margin:"16px 0",background:"rgba(168,85,247,0.05)",border:"1px solid rgba(168,85,247,0.18)",borderRadius:12,padding:"13px 14px"}}>
                    <div style={{fontSize:10,color:"#C084FC",fontWeight:700,textTransform:"uppercase",letterSpacing:1.2,marginBottom:10,display:"flex",alignItems:"center",gap:6}}>🧠 Coach</div>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {pbCoach.map((tip,i)=>{const col=tip.t==="warn"?"#FBBF24":tip.t==="good"?"#4ade80":"rgba(255,255,255,0.55)";const icon=tip.t==="warn"?"⚠️":tip.t==="good"?"✓":"💡";return(
                        <div key={i} style={{display:"flex",gap:7,alignItems:"flex-start",fontSize:11.5,lineHeight:1.45,color:col}}><span style={{flexShrink:0}}>{icon}</span><span>{tip.m}</span></div>
                      );})}
                    </div>
                  </div>
                )}
                {/* ── Playbook Make-Up ── */}
                {pbResolved.length>0 && (
                  <div onMouseLeave={()=>setExpandedCat(pinnedCat)} style={{margin:"16px 0",background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:"13px 14px"}}>
                    <div style={{fontSize:10,color:"#9ca3af",fontWeight:700,textTransform:"uppercase",letterSpacing:1.2,marginBottom:12,display:"flex",alignItems:"center",gap:6}}>📊 Playbook Make-Up</div>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(86px,1fr))",gap:6,marginBottom:12}}>
                      {PLAYBOOK_CATEGORIES.map(cat=>{
                        const c=makeup.counts[cat]||{count:0,cards:[]};
                        const pct=Math.round((c.count/makeup.total)*100);
                        const col=PLAYBOOK_CAT_COLORS[cat];
                        const isOpen=expandedCat===cat;
                        return (
                          <div key={cat}
                            onClick={()=>{ const next=pinnedCat===cat?null:cat; setPinnedCat(next); setExpandedCat(next); }}
                            onMouseEnter={()=>{ if(!pinnedCat) setExpandedCat(cat); }}
                            title={`${c.count} play${c.count!==1?"s":""} (${pct}%) — hover or click to see which`}
                            style={{background:isOpen?`${col}1f`:"rgba(255,255,255,0.03)",border:`1px solid ${isOpen?col:col+"33"}`,borderRadius:9,padding:"8px 9px",cursor:"pointer",transition:"all 0.15s"}}>
                            <div style={{fontSize:9,fontWeight:800,color:col,lineHeight:1.2,minHeight:22}}>{cat}</div>
                            <div style={{fontSize:22,fontWeight:900,color:c.count>0?"#fff":"#555",lineHeight:1.1,marginTop:2}}>{c.count}<span style={{fontSize:10,color:"#999",fontWeight:700}}> play{c.count!==1?"s":""}</span></div>
                            <div style={{fontSize:9,color:"#999",marginTop:1}}>{pct}% of mix</div>
                          </div>
                        );
                      })}
                    </div>
                    {/* expanded category → its plays (Galileo's request) */}
                    {expandedCat && (
                      <div style={{background:"rgba(0,0,0,0.3)",border:`1px solid ${PLAYBOOK_CAT_COLORS[expandedCat]}33`,borderRadius:9,padding:"8px 10px",marginBottom:12}}>
                        <div style={{fontSize:10,fontWeight:800,color:PLAYBOOK_CAT_COLORS[expandedCat],marginBottom:6}}>{expandedCat} — {(makeup.counts[expandedCat]?.cards||[]).length} play{(makeup.counts[expandedCat]?.cards||[]).length!==1?"s":""}</div>
                        {(makeup.counts[expandedCat]?.cards||[]).length===0
                          ? <div style={{fontSize:11,color:"#999",fontStyle:"italic"}}>No plays in this category yet.</div>
                          : (makeup.counts[expandedCat].cards).map((e,i)=>{
                              const c=e.card;
                              return (
                                <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderTop:i?"1px solid var(--bz-s2)":"none"}}>
                                  {c.imageUrl&&<img src={c.imageUrl} alt={c.hero} style={{width:22,height:29,objectFit:"cover",borderRadius:3,flexShrink:0}}/>}
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:11,fontWeight:700,color:"#eee",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.playName||c.hero}</div>
                                    <div style={{fontSize:9,color:"#999"}}>{e.type==="bonus"?"Bonus Play":"Play"}{c.dbs?` · DBS ${c.dbs}`:""}</div>
                                  </div>
                                  {pbIsAdmin && (
                                    <select value={catOf(c)} onClick={ev=>ev.stopPropagation()} onChange={ev=>setPlayCategory(c,ev.target.value)}
                                      title="Reassign category (admin)"
                                      style={{background:"#0d0d12",color:"#bbb",border:"1px solid var(--bz-line-2)",borderRadius:6,padding:"3px 5px",fontSize:10,fontFamily:"inherit",cursor:"pointer",maxWidth:120}}>
                                      {PLAYBOOK_CATEGORIES.map(cat=><option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                  )}
                                </div>
                              );
                            })}
                      </div>
                    )}
                    {/* stats row */}
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(72px,1fr))",gap:6}}>
                      {[
                        {l:"# Plays",v:makeup.playEntries.length},
                        {l:"# Bonus",v:makeup.bonusEntries.length},
                        {l:"DBS",v:`${Math.round(totalDbs)}/${PUBLIC_DBS_CAP}`},
                        {l:"Avg DBS",v:makeup.avgDbs.toFixed(1)},
                        {l:"Avg Cost",v:makeup.avgCost.toFixed(1)},
                      ].map(s=>(
                        <div key={s.l} style={{background:"rgba(232,49,122,0.06)",border:"1px solid rgba(232,49,122,0.2)",borderRadius:8,padding:"6px 8px",textAlign:"center"}}>
                          <div style={{fontSize:9,fontWeight:700,color:"#E8317A"}}>{s.l}</div>
                          <div style={{fontSize:13,fontWeight:900,color:"#fff",marginTop:1}}>{s.v}</div>
                        </div>
                      ))}
                    </div>
                    {pbIsAdmin && <div style={{fontSize:9,color:"var(--bz-ink-3)",marginTop:8,fontStyle:"italic"}}>Categories auto-guessed from play names. Click a category to lock it open, then use the dropdowns to reassign plays — overrides save for everyone.</div>}
                  </div>
                )}
                {pbResolved.length>0&&(
                  <div style={{marginTop:4}}>
                    {[{type:"play",label:"\u2694\uFE0F Plays",color:"#E8317A"},{type:"bonus",label:"\u2B50 Bonus Plays",color:"#7B9CFF"}].map(({type,label,color})=>{
                      const entries=pbResolved.filter(e=>e.type===type);
                      if(!entries.length)return null;
                      return (
                        <div key={type} style={{marginBottom:12}}>
                          <div style={{fontSize:10,color:color,fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>{label} ({entries.length})</div>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(46px,1fr))",gap:5}}>
                            {entries.map((e,i)=>{
                              const c=e.card;
                              return (
                                <div key={i} title={`${c.hero}${c.dbs?` · DBS ${c.dbs}`:""} — click to remove`} onClick={()=>{const arr=pbCards.filter(x=>x.type===type);const target=arr[i];const gi=pbCards.indexOf(target);const a=[...pbCards];a.splice(gi,1);setPbCards(a);}}
                                  style={{position:"relative",aspectRatio:"3/4",borderRadius:5,overflow:"hidden",border:`1px solid ${color}55`,background:"var(--bz-s1)",cursor:"pointer"}}
                                  onMouseEnter={ev=>{ev.currentTarget.style.borderColor=color;const x=ev.currentTarget.querySelector(".pb-rm");if(x)x.style.opacity="1";}}
                                  onMouseLeave={ev=>{ev.currentTarget.style.borderColor=color+"55";const x=ev.currentTarget.querySelector(".pb-rm");if(x)x.style.opacity="0";}}>
                                  {c.imageUrl
                                    ? <img src={c.imageUrl} alt={c.hero} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                                    : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:7,fontWeight:700,color:color,padding:2,textAlign:"center"}}>{c.hero}</div>}
                                  <div className="pb-rm" style={{position:"absolute",inset:0,background:"rgba(232,49,122,0.6)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:16,fontWeight:900,opacity:0,transition:"opacity 0.15s"}}>×</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    <button onClick={()=>{if(window.confirm("Clear playbook?"))setPbCards([]);}} style={{width:"100%",background:"transparent",border:"1px solid rgba(232,49,122,0.15)",color:"rgba(232,49,122,0.4)",borderRadius:8,padding:"7px 0",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginTop:8}}>{"\u2715 Clear Playbook"}</button>
                  </div>
                )}
              </div>
            </div>

            {showPbPickList && (()=>{
              // Sort the resolved plays for the sheet.
              const cmp = {
                cardnum:(a,b)=>String(a.card.cardNum||"").localeCompare(String(b.card.cardNum||""),undefined,{numeric:true}),
                name:(a,b)=>String(a.card.playName||a.card.hero||"").localeCompare(String(b.card.playName||b.card.hero||"")),
                cost:(a,b)=>(parseFloat(b.card.dbs)||0)-(parseFloat(a.card.dbs)||0),
                set:(a,b)=>String(a.card.setName||"").localeCompare(String(b.card.setName||"")),
              }[pbPickSort] || (()=>0);
              const sorted = [...pbResolved].sort(cmp);
              const line = e => [e.card.setName, e.card.playName||e.card.hero, e.card.cardNum?`#${e.card.cardNum}`:"", (e.card.dbs!=null&&e.card.dbs!=="")?`DBS ${e.card.dbs}`:""].filter(Boolean).join(" · ");
              const totalDbsPick = sorted.reduce((s,e)=>s+(parseFloat(e.card.dbs)||0),0);
              const copyText = sorted.map(line).join("\n") + `\n\nTotal DBS: ${Math.round(totalDbsPick)} · ${sorted.length} plays`;
              const doCopy = ()=>{ navigator.clipboard.writeText(copyText).then(()=>{ setPbCopied(true); setTimeout(()=>setPbCopied(false),1800); }); };
              const openPrint = ()=>{
                const esc = s => String(s==null?"":s).replace(/[&<>"]/g, m=>({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;"}[m]));
                const rows = sorted.map(e=>{
                  const c=e.card; const bonus=e.type==="bonus";
                  return '<tr>'+
                    '<td class="tick"><span class="box"></span></td>'+
                    '<td class="set">'+esc(c.setName||"\u2014")+'</td>'+
                    '<td><div class="pn">'+esc(c.playName||c.hero||"\u2014")+(bonus?' <span class="btag">BONUS</span>':'')+'</div></td>'+
                    '<td class="num">'+esc(c.cardNum||"\u2014")+'</td>'+
                    '<td class="cost">'+esc((c.dbs!=null&&c.dbs!=="")?c.dbs:"\u2014")+'</td>'+
                  '</tr>';
                }).join("");
                const html='<!DOCTYPE html><html><head><meta charset="utf-8"><title>'+esc(pbName||"Playbook")+' \u2014 Pick List</title>'+
                  '<style>*{box-sizing:border-box;}body{font-family:"Trebuchet MS",Arial,Helvetica,sans-serif;margin:0;padding:26px 28px;color:#111;}'+
                  '.eyebrow{font-size:10px;font-weight:800;letter-spacing:1.6px;text-transform:uppercase;color:#B45309;margin-bottom:4px;}'+
                  'h1{font-size:25px;font-weight:900;letter-spacing:-0.6px;margin:0;line-height:1.05;}'+
                  '.stats{display:flex;gap:16px;margin-top:8px;font-size:12px;} .stats b{font-weight:800;} .stats .lt{color:#999;}'+
                  '.rule{border-bottom:3px solid #111;margin:14px 0 0;}'+
                  'table{width:100%;border-collapse:collapse;font-size:13px;margin-top:2px;}thead{display:table-header-group;}'+
                  'th{text-align:left;border-bottom:1.5px solid #111;padding:8px 6px;font-size:9.5px;font-weight:800;letter-spacing:0.8px;text-transform:uppercase;color:#999;}'+
                  'th.r{text-align:right;} th.c{text-align:center;}'+
                  'td{padding:9px 6px;border-bottom:1px solid #ededed;vertical-align:top;}tr{page-break-inside:avoid;}'+
                  '.tick{width:34px;text-align:center;}.box{display:inline-block;width:17px;height:17px;border:1.5px solid #bbb;border-radius:4px;}'+
                  '.set{font-size:11.5px;color:#666;font-weight:700;width:130px;}'+
                  '.pn{font-weight:800;font-size:14px;}.btag{font-size:8.5px;font-weight:900;color:#B45309;background:#FEF3C7;border:1px solid #FDE68A;border-radius:4px;padding:0 4px;margin-left:5px;vertical-align:middle;}'+
                  '.num{font-size:12.5px;font-weight:700;color:#444;width:70px;}'+
                  '.cost{text-align:right;font-size:17px;font-weight:900;font-variant-numeric:tabular-nums;width:70px;}'+
                  '.totalrow td{border-top:2.5px solid #111;border-bottom:none;padding-top:12px;}.totlbl{font-size:11px;font-weight:900;letter-spacing:0.8px;text-transform:uppercase;color:#111;}.tot{text-align:right;font-size:20px;font-weight:900;font-variant-numeric:tabular-nums;}'+
                  '.foot{margin-top:18px;padding-top:11px;border-top:1px solid #eee;font-size:10px;color:#bbb;display:flex;justify-content:space-between;}.foot b{color:#999;font-weight:800;}'+
                  '.btns{margin-bottom:18px;}button{background:#111;color:#fff;border:none;border-radius:9px;padding:9px 18px;font-size:12px;font-weight:800;cursor:pointer;font-family:inherit;}'+
                  '@page{margin:13mm;}@media print{.btns{display:none;}}</style></head><body>'+
                  '<div class="btns"><button onclick="window.print()">Print</button></div>'+
                  '<div class="eyebrow">Pick list</div><h1>'+esc(pbName||"Playbook")+'</h1>'+
                  '<div class="stats"><span><b>'+sorted.length+'</b><span class="lt"> plays</span></span><span class="lt">sorted by '+(pbPickSort==="cardnum"?"play number":pbPickSort)+'</span></div>'+
                  '<div class="rule"></div>'+
                  '<table><thead><tr><th class="c">Got</th><th>Set</th><th>Play</th><th>No.</th><th class="r">DBS</th></tr></thead><tbody>'+rows+
                  '<tr class="totalrow"><td></td><td></td><td class="totlbl">Total DBS</td><td></td><td class="tot">'+Math.round(totalDbsPick)+'</td></tr>'+
                  '</tbody></table>'+
                  '<div class="foot"><b>BAZOOKA DASH</b><span>'+new Date().toLocaleDateString()+'</span></div>'+
                  '</body></html>';
                const w=window.open("","_blank","width=900,height=1000");
                if(!w){ alert("Please allow pop-ups to print or save the pick list."); return; }
                w.document.write(html); w.document.close();
              };
              return (
                <div onClick={()=>setShowPbPickList(false)} style={{position:"fixed",inset:0,zIndex:13000,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(6px)",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:24,overflowY:"auto"}}>
                  <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:680,background:"#fff",color:"var(--bz-s1)",borderRadius:14,padding:"26px 30px 30px",boxShadow:"0 24px 80px rgba(0,0,0,0.6)",margin:"auto"}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",marginBottom:18}}>
                      <div style={{display:"inline-flex",border:"1.5px solid #e3e3e3",borderRadius:9,overflow:"hidden"}}>
                        {[["cardnum","Play no."],["name","Name"],["cost","DBS"],["set","Set"]].map(([k,label],idx)=>(
                          <button key={k} onClick={()=>setPbPickSort(k)}
                            style={{background:pbPickSort===k?"var(--bz-s1)":"#fff",color:pbPickSort===k?"#fff":"#777",border:"none",borderLeft:idx===0?"none":"1px solid #e3e3e3",padding:"8px 13px",fontSize:11.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{label}</button>
                        ))}
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={doCopy} style={{background:pbCopied?"#0a7a3a":"#fff",color:pbCopied?"#fff":"var(--bz-s1)",border:"1.5px solid "+(pbCopied?"#0a7a3a":"#ccc"),borderRadius:9,padding:"8px 16px",fontSize:11.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{pbCopied?"✓ Copied":"📋 Copy List"}</button>
                        <button onClick={openPrint} style={{background:"var(--bz-s1)",color:"#fff",border:"1.5px solid #111",borderRadius:9,padding:"8px 18px",fontSize:11.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Print / PDF</button>
                        <button onClick={()=>setShowPbPickList(false)} style={{background:"#fff",color:"#999",border:"1.5px solid #ddd",borderRadius:9,width:36,height:36,fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"inherit",lineHeight:1}}>{"\u00d7"}</button>
                      </div>
                    </div>
                    <div style={{marginBottom:18,paddingBottom:14,borderBottom:"3px solid #111"}}>
                      <div style={{fontSize:11,fontWeight:800,letterSpacing:"1.6px",textTransform:"uppercase",color:"#B45309",marginBottom:4}}>Pick list</div>
                      <div style={{fontSize:26,fontWeight:900,letterSpacing:"-0.6px",lineHeight:1.05}}>{pbName||"Playbook"}</div>
                      <div style={{marginTop:9,fontSize:12,color:"#999"}}><b style={{color:"var(--bz-s1)"}}>{sorted.length}</b> plays · sorted by {pbPickSort==="cardnum"?"play number":pbPickSort}{(()=>{ const pulled=sorted.filter(e=>pbPickChecked[e.id]).length; return pulled>0?<span style={{color:"#0a7a3a",fontWeight:800}}> · {pulled} pulled</span>:null; })()}</div>
                    </div>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                      <thead><tr>
                        <th style={{width:34,textAlign:"center",borderBottom:"1.5px solid #111",padding:"8px 6px",fontSize:9.5,fontWeight:800,letterSpacing:"0.8px",textTransform:"uppercase",color:"#999"}}>Got</th>
                        <th style={{textAlign:"left",borderBottom:"1.5px solid #111",padding:"8px 6px",fontSize:9.5,fontWeight:800,letterSpacing:"0.8px",textTransform:"uppercase",color:"#999"}}>Set</th>
                        <th style={{textAlign:"left",borderBottom:"1.5px solid #111",padding:"8px 6px",fontSize:9.5,fontWeight:800,letterSpacing:"0.8px",textTransform:"uppercase",color:"#999"}}>Play</th>
                        <th style={{textAlign:"left",borderBottom:"1.5px solid #111",padding:"8px 6px",fontSize:9.5,fontWeight:800,letterSpacing:"0.8px",textTransform:"uppercase",color:"#999"}}>No.</th>
                        <th style={{textAlign:"right",borderBottom:"1.5px solid #111",padding:"8px 6px",fontSize:9.5,fontWeight:800,letterSpacing:"0.8px",textTransform:"uppercase",color:"#999"}}>DBS</th>
                      </tr></thead>
                      <tbody>
                        {sorted.map(e=>{ const c=e.card; const bonus=e.type==="bonus"; const done=!!pbPickChecked[e.id]; return (
                          <tr key={e.id} onClick={()=>setPbPickChecked(p=>({...p,[e.id]:!p[e.id]}))}
                            style={{pageBreakInside:"avoid",cursor:"pointer",background:done?"#fafafa":"transparent",opacity:done?0.45:1,transition:"opacity 140ms ease, background 140ms ease"}}>
                            <td style={{width:34,textAlign:"center",padding:"9px 6px",borderBottom:"1px solid #ededed"}}>
                              <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:19,height:19,borderRadius:5,border:"1.5px solid "+(done?"var(--bz-s1)":"#ccc"),background:done?"var(--bz-s1)":"#fff",color:"#fff",fontSize:12,fontWeight:900,lineHeight:1}}>{done?"\u2713":""}</span>
                            </td>
                            <td style={{fontSize:11.5,color:"#666",fontWeight:700,padding:"9px 6px",borderBottom:"1px solid #ededed"}}>{c.setName||"—"}</td>
                            <td style={{padding:"9px 6px",borderBottom:"1px solid #ededed"}}><span style={{fontWeight:800,fontSize:14}}>{c.playName||c.hero||"—"}</span>{bonus&&<span style={{fontSize:8.5,fontWeight:900,color:"#B45309",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:4,padding:"0 4px",marginLeft:5}}>BONUS</span>}</td>
                            <td style={{fontSize:12.5,fontWeight:700,color:"#444",padding:"9px 6px",borderBottom:"1px solid #ededed"}}>{c.cardNum||"—"}</td>
                            <td style={{textAlign:"right",fontSize:17,fontWeight:900,fontVariantNumeric:"tabular-nums",padding:"9px 6px",borderBottom:"1px solid #ededed"}}>{(c.dbs!=null&&c.dbs!=="")?c.dbs:"—"}</td>
                          </tr>
                        );})}
                        <tr>
                          <td colSpan={4} style={{textAlign:"right",padding:"12px 6px 6px",borderTop:"2.5px solid #111",fontSize:11,fontWeight:900,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--bz-s1)"}}>Total DBS</td>
                          <td style={{textAlign:"right",padding:"12px 6px 6px",borderTop:"2.5px solid #111",fontSize:20,fontWeight:900,fontVariantNumeric:"tabular-nums"}}>{Math.round(totalDbsPick)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </>
  );
}

// ============================================================================
// BO JACKSON BATTLE ARENA — TABLETOP SIMULATOR
// Rookie Mode, live 2-player over Firestore.
//
// PASTE THIS AS A NEW TOP-LEVEL FUNCTION in App.js, next to DeckBuilderTab
// (around line 28840, before `function DeckBuilderTab(`).
//
// DATA MODEL
//   boba_games/{gameId}
//     Public state both players read. Contains ONLY information that is
//     legally public under the rules: whose turn, current zone, trophies,
//     direction, and REVEALED heroes.
//
//   boba_games/{gameId}/private/{uid}
//     That player's face-down lineup + remaining deck. Firestore rules must
//     restrict read to uid only. This is why face-down heroes are actually
//     hidden instead of merely not-rendered — an opponent with devtools open
//     cannot read this doc.
//
// When a zone is revealed, the hero is COPIED from the private doc up into
// the public doc. That copy is the reveal.
// ============================================================================

// ============================================================================
// WEAPON STRIKE ANIMATIONS
//
// Purely cosmetic. The winner's weapon type attacks the loser's card. Nothing
// here touches game state — if every animation failed to render, the game would
// play identically.
//
// Two layers, because the card is `overflow:hidden` with a rectangular image:
//   - ON-CARD effects (frost, gum, glitch, scanlines) render INSIDE the loser's
//     card, clipped to it.
//   - PROJECTILES (flames, gloves, hex bolts) render in an absolutely-positioned
//     overlay ABOVE the whole board so they can travel between rows.
//
// Direction matters: my row is below the zone strip, the opponent's above, so a
// strike travels "up" or "down" depending on who won.
// ============================================================================

const ARENA_FX_CSS = `
@keyframes bzShake {
  0%,100%{transform:translate(0,0) rotate(0deg)} 10%{transform:translate(-7px,4px) rotate(-2deg)}
  22%{transform:translate(7px,-5px) rotate(2.5deg)} 36%{transform:translate(-6px,-4px) rotate(-2deg)}
  50%{transform:translate(6px,5px) rotate(1.5deg)} 64%{transform:translate(-4px,3px) rotate(-1deg)}
  80%{transform:translate(3px,-2px) rotate(0.5deg)}
}
/* Winner pulses forward — the strike should read as coming FROM somewhere. */
@keyframes bzWinnerPulse {
  0%{transform:scale(1)} 22%{transform:scale(1.12)} 55%{transform:scale(0.98)} 100%{transform:scale(1)}
}
/* Full-board impact flash. */
@keyframes bzBoardFlash {
  0%{opacity:0} 8%{opacity:0.85} 100%{opacity:0}
}
/* Debris/spark thrown outward from the point of impact. */
@keyframes bzSpark {
  0%{transform:translate(0,0) scale(1);opacity:1}
  100%{transform:translate(var(--dx),var(--dy)) scale(0.2);opacity:0}
}
/* Big travelling projectile across the whole board height. */
@keyframes bzHurlUp {
  0%{transform:translate(-50%,120px) scale(0.35) rotate(0deg);opacity:0}
  15%{opacity:1}
  85%{opacity:1}
  100%{transform:translate(-50%,-150px) scale(1.9) rotate(var(--spin,0deg));opacity:0}
}
@keyframes bzHurlDown {
  0%{transform:translate(-50%,-120px) scale(0.35) rotate(0deg);opacity:0}
  15%{opacity:1}
  85%{opacity:1}
  100%{transform:translate(-50%,150px) scale(1.9) rotate(var(--spin,0deg));opacity:0}
}
@keyframes bzFlyUp {
  0%{transform:translate(-50%,40px) scale(0.5);opacity:0}
  18%{opacity:1}
  100%{transform:translate(-50%,-110px) scale(1.25);opacity:0}
}
@keyframes bzFlyDown {
  0%{transform:translate(-50%,-40px) scale(0.5);opacity:0}
  18%{opacity:1}
  100%{transform:translate(-50%,110px) scale(1.25);opacity:0}
}
@keyframes bzFadeBurst {
  0%{opacity:0;transform:scale(0.4)}
  25%{opacity:1;transform:scale(1.05)}
  100%{opacity:0;transform:scale(1.4)}
}
@keyframes bzFrost {
  0%{opacity:0}
  30%{opacity:1}
  70%{opacity:1}
  100%{opacity:0}
}
@keyframes bzDrip {
  0%{transform:translateY(-14px);opacity:0}
  25%{opacity:1}
  72%{opacity:1}
  100%{transform:translateY(0);opacity:0}
}
@keyframes bzGlitch {
  0%,100%{transform:translate(0);filter:none}
  20%{transform:translate(-5px,1px);filter:hue-rotate(90deg)}
  40%{transform:translate(4px,-2px);filter:hue-rotate(-70deg)}
  60%{transform:translate(-3px,-1px);filter:invert(0.35)}
  80%{transform:translate(3px,2px);filter:hue-rotate(140deg)}
}
@keyframes bzScan {
  0%{top:-12%;opacity:0} 12%{opacity:1} 88%{opacity:1} 100%{top:104%;opacity:0}
}
@keyframes bzRing {
  0%{transform:translate(-50%,-50%) scale(0.1);opacity:1;border-width:14px}
  100%{transform:translate(-50%,-50%) scale(9);opacity:0;border-width:1px}
}
@keyframes bzBloom {
  0%{opacity:0} 30%{opacity:1} 100%{opacity:0}
}
@keyframes bzSpin {
  0%{transform:translate(-50%,-50%) rotate(0deg) scale(0.4);opacity:0}
  20%{opacity:1}
  100%{transform:translate(-50%,-50%) rotate(420deg) scale(1.5);opacity:0}
}
@keyframes bzCrack {
  0%{opacity:0;transform:scale(0.7)} 28%{opacity:1;transform:scale(1)}
  70%{opacity:1;transform:scale(1)} 100%{opacity:0;transform:scale(1)}
}
@keyframes bzFlash {
  0%{opacity:0} 10%{opacity:0.9} 100%{opacity:0}
}
.bz-fx-layer{position:absolute;inset:0;pointer-events:none;z-index:40;overflow:visible;}
.bz-oncard{position:absolute;inset:0;pointer-events:none;z-index:5;}
`;

// ---- ON-CARD EFFECT -------------------------------------------------------
// Rendered INSIDE the losing card, so it's clipped to the card bounds.
// `w` is the WINNER's weapon — the attack landing on this card.
function ArenaOnCardFx({ w }) {
  if (!w) return null;
  const base = { position:"absolute", inset:0, pointerEvents:"none", zIndex:5 };

  if (w === "Ice") {
    // Frost creeps over the card and stays — the card is frozen.
    return (
      <div style={base}>
        <div style={{position:"absolute",inset:0,animation:"bzFrost 700ms ease-out forwards",
          background:"linear-gradient(160deg,rgba(191,232,255,0.72),rgba(120,190,240,0.35) 45%,rgba(230,248,255,0.66))",
          boxShadow:"inset 0 0 26px rgba(255,255,255,0.9)"}}/>
        <svg viewBox="0 0 50 70" preserveAspectRatio="none"
             style={{position:"absolute",inset:0,width:"100%",height:"100%",
                     animation:"bzCrack 700ms ease-out forwards",opacity:0}}>
          <g stroke="#fff" strokeWidth="0.7" fill="none" opacity="0.95">
            <path d="M25 0 L25 26 L14 40 M25 26 L37 41 M25 40 L25 70"/>
            <path d="M0 22 L16 30 M50 20 L34 29"/>
            <path d="M6 62 L18 48 M44 60 L33 49"/>
          </g>
        </svg>
      </div>
    );
  }

  if (w === "Gum") {
    // Sticky pink splat with drips running down.
    return (
      <div style={base}>
        <div style={{position:"absolute",inset:0,animation:"bzFadeBurst 620ms ease-out forwards",
          background:"radial-gradient(circle at 50% 42%,rgba(255,105,180,0.92) 0%,rgba(255,105,180,0.75) 34%,rgba(255,105,180,0) 62%)"}}/>
        <div style={{position:"absolute",inset:0,animation:"bzDrip 800ms ease-out forwards"}}>
          {[18,38,58,78].map((x,i)=>(
            <div key={i} style={{position:"absolute",left:x+"%",top:"34%",width:7,
              height:16+(i%2)*13,borderRadius:"0 0 60% 60%",background:"rgba(255,105,180,0.9)",
              boxShadow:"0 0 8px rgba(255,105,180,0.7)"}}/>
          ))}
        </div>
      </div>
    );
  }

  if (w === "Alt") {
    // Reality glitch — the card displaces and colour-shifts.
    return (
      <div style={{...base, animation:"bzGlitch 620ms steps(2,end) 2",
        mixBlendMode:"screen", background:"rgba(255,0,110,0.16)"}}/>
    );
  }

  if (w === "Cyber") {
    // Digital lock-on: scanline sweep plus a targeting frame.
    return (
      <div style={base}>
        <div style={{position:"absolute",left:0,right:0,height:"14%",
          animation:"bzScan 900ms linear forwards",
          background:"linear-gradient(180deg,rgba(0,255,200,0) 0%,rgba(0,255,200,0.85) 50%,rgba(0,255,200,0) 100%)",
          boxShadow:"0 0 18px rgba(0,255,200,0.9)"}}/>
        <div style={{position:"absolute",inset:"12%",border:"1.5px solid rgba(0,255,200,0.85)",
          animation:"bzBloom 900ms ease-out forwards"}}/>
        <div style={{position:"absolute",inset:0,opacity:0.35,
          background:"repeating-linear-gradient(0deg,rgba(0,255,200,0.16) 0 1px,transparent 1px 4px)",
          animation:"bzBloom 900ms ease-out forwards"}}/>
      </div>
    );
  }

  if (w === "Glow") {
    // Radiance washes the card out.
    return (
      <div style={{...base, animation:"bzBloom 780ms ease-out forwards",
        background:"radial-gradient(circle at 50% 50%,rgba(255,255,255,0.96) 0%,rgba(255,240,150,0.72) 35%,rgba(255,215,0,0) 72%)",
        boxShadow:"inset 0 0 40px rgba(255,240,160,0.95)"}}/>
    );
  }

  if (w === "Hex") {
    // Purple hex warp, matching the treatment on the cards.
    return (
      <div style={base}>
        <div style={{position:"absolute",inset:0,animation:"bzBloom 820ms ease-out forwards",
          background:"radial-gradient(circle at 50% 50%,rgba(168,85,247,0.85),rgba(88,28,135,0.55) 55%,rgba(88,28,135,0) 78%)",
          mixBlendMode:"screen"}}/>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none"
             style={{position:"absolute",inset:0,width:"100%",height:"100%",
                     animation:"bzSpin 820ms ease-out forwards",transformOrigin:"50% 50%"}}>
          <polygon points="50,8 88,29 88,71 50,92 12,71 12,29" fill="none"
                   stroke="#D8B4FE" strokeWidth="2.5" opacity="0.95"/>
          <polygon points="50,24 74,38 74,62 50,76 26,62 26,38" fill="none"
                   stroke="#F0ABFC" strokeWidth="1.6" opacity="0.8"/>
        </svg>
      </div>
    );
  }

  if (w === "Steel") {
    // Blade slash — a hard diagonal cut with a metal sheen.
    return (
      <div style={base}>
        <div style={{position:"absolute",inset:"-30%",animation:"bzFadeBurst 520ms ease-out forwards",
          background:"linear-gradient(115deg,transparent 42%,rgba(255,255,255,0.98) 49%,rgba(203,213,225,0.9) 51%,transparent 58%)"}}/>
        <svg viewBox="0 0 50 70" preserveAspectRatio="none"
             style={{position:"absolute",inset:0,width:"100%",height:"100%",
                     animation:"bzCrack 640ms ease-out forwards",opacity:0}}>
          <path d="M2 60 L48 12" stroke="#E2E8F0" strokeWidth="2.2" fill="none" opacity="0.95"/>
          <path d="M8 66 L44 20" stroke="#94A3B8" strokeWidth="1.1" fill="none" opacity="0.7"/>
        </svg>
      </div>
    );
  }

  if (w === "Fire") {
    // Scorch left behind after the flames land.
    return (
      <div style={{...base, animation:"bzFadeBurst 700ms ease-out forwards",
        background:"radial-gradient(circle at 50% 60%,rgba(255,140,0,0.9) 0%,rgba(220,38,38,0.6) 38%,rgba(80,10,0,0) 70%)",
        mixBlendMode:"screen"}}/>
    );
  }

  if (w === "Brawl") {
    // Impact bruise where the glove connected.
    return (
      <div style={{...base, animation:"bzFadeBurst 560ms ease-out forwards",
        background:"radial-gradient(circle at 50% 50%,rgba(255,255,255,0.9) 0%,rgba(248,113,113,0.6) 30%,rgba(0,0,0,0) 62%)"}}/>
    );
  }

  if (w === "Medal") {
    // Gold medal shine — a sweeping metallic gleam.
    return (
      <div style={{...base, animation:"bzFadeBurst 640ms ease-out forwards",
        background:"linear-gradient(120deg,transparent 35%,rgba(255,215,0,0.95) 48%,rgba(255,248,200,0.9) 52%,transparent 66%)"}}/>
    );
  }

  if (w === "Metallic") {
    // Chrome ripple.
    return (
      <div style={{...base, animation:"bzFadeBurst 640ms ease-out forwards",
        background:"linear-gradient(100deg,rgba(148,163,184,0) 30%,rgba(226,232,240,0.95) 47%,rgba(100,116,139,0.85) 55%,rgba(148,163,184,0) 70%)"}}/>
    );
  }

  if (w === "Super") {
    // Full white-out — the rarest weapon should feel like the biggest hit.
    return (
      <div style={base}>
        <div style={{position:"absolute",inset:0,animation:"bzFlash 900ms ease-out forwards",
          background:"#fff"}}/>
        <div style={{position:"absolute",inset:0,animation:"bzBloom 900ms ease-out forwards",
          background:"radial-gradient(circle at 50% 50%,rgba(255,215,0,0.9),rgba(255,60,0,0.5) 50%,transparent 76%)",
          mixBlendMode:"screen"}}/>
      </div>
    );
  }

  return null;
}

// ---- PROJECTILE OVERLAY ---------------------------------------------------
// Sits above the whole board so a strike can travel between rows.
// `up` = travelling toward the opponent's row (I won).
function ArenaProjectile({ w, up }) {
  if (!w) return null;
  const fly = up ? "bzHurlUp" : "bzHurlDown";
  const wrap = { position:"absolute", left:"50%", top:"50%", pointerEvents:"none" };

  if (w === "Fire") {
    return (
      <div style={wrap}>
        {[0,1,2].map(i=>(
          <div key={i} style={{position:"absolute",left:(i-1)*26,fontSize:54,
            animation:`${fly} 760ms ease-out ${i*80}ms forwards`,
            filter:"drop-shadow(0 0 12px rgba(255,120,0,0.95))"}}>🔥</div>
        ))}
      </div>
    );
  }
  if (w === "Brawl") {
    return (
      <div style={wrap}>
        <div style={{position:"absolute",fontSize:60,"--spin":"-25deg",
          animation:`${fly} 640ms cubic-bezier(.2,.8,.3,1) forwards`,
          filter:"drop-shadow(0 0 10px rgba(248,113,113,0.9))"}}>🥊</div>
      </div>
    );
  }
  if (w === "Hex") {
    return (
      <div style={wrap}>
        {[0,1].map(i=>(
          <div key={i} style={{position:"absolute",left:(i?18:-18),width:22,height:22,
            animation:`${fly} 780ms ease-out ${i*110}ms forwards`,
            background:"conic-gradient(from 0deg,#A855F7,#F0ABFC,#6D28D9,#A855F7)",
            clipPath:"polygon(50% 0%,93% 25%,93% 75%,50% 100%,7% 75%,7% 25%)",
            boxShadow:"0 0 16px rgba(168,85,247,0.95)"}}/>
        ))}
      </div>
    );
  }
  if (w === "Gum") {
    return (
      <div style={wrap}>
        <div style={{position:"absolute",width:26,height:26,borderRadius:"50%",
          animation:`${fly} 700ms ease-in forwards`,
          background:"radial-gradient(circle at 35% 30%,#FFB6D9,#FF69B4 60%,#DB2777)",
          boxShadow:"0 0 14px rgba(255,105,180,0.9)"}}/>
      </div>
    );
  }
  if (w === "Steel") {
    return (
      <div style={wrap}>
        <div style={{position:"absolute",width:5,height:52,borderRadius:3,
          animation:`${fly} 520ms cubic-bezier(.3,.9,.2,1) forwards`,
          background:"linear-gradient(180deg,#F8FAFC,#94A3B8,#475569)",
          boxShadow:"0 0 16px rgba(226,232,240,0.95)"}}/>
      </div>
    );
  }
  if (w === "Ice") {
    return (
      <div style={wrap}>
        {[0,1,2].map(i=>(
          <div key={i} style={{position:"absolute",left:(i-1)*24,fontSize:46,
            animation:`${fly} 740ms ease-out ${i*70}ms forwards`,
            filter:"drop-shadow(0 0 10px rgba(147,220,255,0.95))"}}>❄️</div>
        ))}
      </div>
    );
  }
  if (w === "Glow") {
    return (
      <div style={wrap}>
        <div style={{position:"absolute",width:30,height:30,borderRadius:"50%",
          animation:`${fly} 700ms ease-out forwards`,
          background:"radial-gradient(circle,#fff,#FDE68A 55%,rgba(253,224,71,0))",
          boxShadow:"0 0 30px rgba(255,240,150,1)"}}/>
      </div>
    );
  }
  if (w === "Cyber") {
    return (
      <div style={wrap}>
        {[0,1,2].map(i=>(
          <div key={i} style={{position:"absolute",left:(i-1)*12,width:3,height:26,
            animation:`${fly} 560ms linear ${i*60}ms forwards`,
            background:"linear-gradient(180deg,rgba(0,255,200,0),#00FFC8)",
            boxShadow:"0 0 12px rgba(0,255,200,0.95)"}}/>
        ))}
      </div>
    );
  }
  if (w === "Alt") {
    return (
      <div style={wrap}>
        {[0,1].map(i=>(
          <div key={i} style={{position:"absolute",left:(i?10:-10),width:20,height:20,
            animation:`${fly} 660ms steps(6,end) ${i*90}ms forwards`,
            background: i? "rgba(255,0,110,0.85)" : "rgba(0,229,255,0.85)",
            mixBlendMode:"screen"}}/>
        ))}
      </div>
    );
  }
  if (w === "Medal" || w === "Metallic") {
    const gold = w === "Medal";
    return (
      <div style={wrap}>
        <div style={{position:"absolute",width:24,height:24,borderRadius:"50%",
          animation:`${fly} 660ms ease-out forwards`,
          background: gold
            ? "radial-gradient(circle at 35% 30%,#FFF7C2,#FFD700 55%,#B8860B)"
            : "radial-gradient(circle at 35% 30%,#F8FAFC,#94A3B8 55%,#475569)",
          boxShadow: gold ? "0 0 16px rgba(255,215,0,0.95)" : "0 0 16px rgba(226,232,240,0.9)"}}/>
      </div>
    );
  }
  if (w === "Super") {
    // Expanding shockwave rings rather than a travelling object — it hits everything.
    return (
      <div style={wrap}>
        {[0,1,2].map(i=>(
          <div key={i} style={{position:"absolute",left:0,top:0,width:60,height:60,
            borderRadius:"50%",border:"8px solid #FFD700",
            transform:"translate(-50%,-50%)",
            animation:`bzRing 900ms ease-out ${i*150}ms forwards`,
            boxShadow:"0 0 30px rgba(255,215,0,0.95)"}}/>
        ))}
      </div>
    );
  }
  return null;
}

// Board-wide strike layer. The per-card effects are clipped to a ~130px slot on a
// 7-wide grid, which is why the first version felt small. This renders ACROSS the
// whole arena: a tint wash in the weapon's colour, a burst at the impact column,
// and sparks thrown outward. Cosmetic only.
// ============================================================================
// CANVAS PARTICLE STRIKE LAYER
//
// Replaces the CSS/emoji effects with a real particle system on a single canvas
// stretched over the arena. Cosmetic only — nothing here touches game state.
//
// Why canvas: CSS keyframes can move a handful of DOM nodes, but fire needs a few
// hundred embers with turbulence and additive blending, and ice needs crystals
// that branch along fracture lines. Those are per-frame maths, not transitions.
//
// One rAF loop drives every live particle. It shuts itself off when the array
// empties, so an idle board costs nothing.
// ============================================================================

// ============================================================================
// SHUFFLE + DEAL CEREMONY
//
// Three phases: riffle -> draw 7 -> deal into the battle zones.
//
// Honest note about what this is: the shuffle already happened in stageMyDeck
// (Fisher-Yates, instant) before this component ever mounts. So the riffle is
// theatre over a settled result — it shows you A shuffle, not THE shuffle. That's
// normal for card games and it's why the deal phase matters more: those really are
// your seven cards, arriving in the order they were drawn.
//
// Pure CSS transforms on a fixed number of nodes (~36), so it stays cheap.
// ============================================================================

const ARENA_SHUFFLE_CSS = `
/* Riffle, done properly. The old version slid two rigid stacks apart and back,
   which read as "two rectangles wobbling". A real riffle has each card lift, arc
   over the bridge, and FALL into the middle at its own moment — so each card gets
   its own delay and a springy landing, and the halves stay visually distinct until
   they interleave. --lift/--tilt are per-card so no two move identically. */
@keyframes bzRiffleL {
  0%   { transform: translate(-50%,-50%) translate(0,0) rotate(0deg); }
  18%  { transform: translate(-50%,-50%) translate(-84px,var(--lift)) rotate(calc(var(--tilt) * -1)); }
  46%  { transform: translate(-50%,-50%) translate(-58px,calc(var(--lift) * 1.5)) rotate(calc(var(--tilt) * -0.6)); }
  72%  { transform: translate(-50%,-50%) translate(-6px,4px) rotate(2deg); }
  86%  { transform: translate(-50%,-50%) translate(2px,-2px) rotate(-1deg); }
  100% { transform: translate(-50%,-50%) translate(0,0) rotate(0deg); }
}
@keyframes bzRiffleR {
  0%   { transform: translate(-50%,-50%) translate(0,0) rotate(0deg); }
  18%  { transform: translate(-50%,-50%) translate(84px,var(--lift)) rotate(var(--tilt)); }
  46%  { transform: translate(-50%,-50%) translate(58px,calc(var(--lift) * 1.5)) rotate(calc(var(--tilt) * 0.6)); }
  72%  { transform: translate(-50%,-50%) translate(6px,4px) rotate(-2deg); }
  86%  { transform: translate(-50%,-50%) translate(-2px,-2px) rotate(1deg); }
  100% { transform: translate(-50%,-50%) translate(0,0) rotate(0deg); }
}
@keyframes bzBridge {
  0%,100% { transform: translate(-50%,-50%) scaleY(1) rotate(0deg); }
  30%     { transform: translate(-50%,-54%) scaleY(1.10) rotate(-0.6deg); }
  62%     { transform: translate(-50%,-50%) scaleY(0.96) rotate(0.4deg); }
}
/* The pack settling after the halves merge — a small squash on impact. */
@keyframes bzSettle {
  0%   { transform: translate(-50%,-50%) scale(1.06,0.92); }
  55%  { transform: translate(-50%,-50%) scale(0.98,1.03); }
  100% { transform: translate(-50%,-50%) scale(1,1); }
}
@keyframes bzDeckPulse {
  0%,100% { box-shadow: 0 0 0 rgba(232,49,122,0); }
  50%     { box-shadow: 0 0 34px rgba(232,49,122,0.75); }
}
@keyframes bzDrawUp {
  0%   { transform: translate(-50%,-50%) scale(0.9); opacity: 0; }
  25%  { opacity: 1; }
  100% { transform: translate(-50%,-190%) scale(1.05); opacity: 1; }
}
@keyframes bzDealOut {
  0%   { transform: translate(var(--fx), var(--fy)) scale(0.5) rotate(var(--spin,-14deg)); opacity: 0; }
  12%  { opacity: 1; }
  72%  { transform: translate(0,-5px) scale(1.04) rotate(2deg); opacity: 1; }
  100% { transform: translate(0,0) scale(1) rotate(0deg); opacity: 1; }
}
@keyframes bzFlipIn {
  0%   { transform: rotateY(90deg); }
  100% { transform: rotateY(0deg); }
}
/* Substitution sequence: outgoing hero flips face-up (opponent sees who was
   benched), slides out to discard, then the incoming hero drops in face-down. */
@keyframes bzSubFlip {
  0%   { transform: rotateY(180deg); }
  100% { transform: rotateY(0deg); }
}
@keyframes bzSubOut {
  0%   { transform: translate(0,0) scale(1) rotate(0deg); opacity: 1; }
  30%  { transform: translate(0,-10px) scale(1.05) rotate(-3deg); opacity: 1; }
  100% { transform: translate(140%, -30%) scale(0.6) rotate(18deg); opacity: 0; }
}
@keyframes bzSubIn {
  0%   { transform: translate(-160%, -40%) scale(0.55) rotate(-16deg); opacity: 0; }
  20%  { opacity: 1; }
  70%  { transform: translate(0,-6px) scale(1.05) rotate(3deg); opacity: 1; }
  100% { transform: translate(0,0) scale(1) rotate(0deg); opacity: 1; }
}
`;

// A fanned hand pinned to the bottom of the screen: overlapping cards tucked like
// a real hand, each lifting on hover so you can read it. Used for BOTH the
// placement hand and the battle bench, so moves never require scrolling.
//   cards:      array of hero snapshots
//   label:      caption ("YOUR HAND" / "YOUR BENCH")
//   accent:     highlight colour
//   heldIndex:  index currently picked up (lifts + outlines), or null
//   onPick:     (index) => void, fired on click/tap
//   onDragStart:(index) => void, for desktop drag
//   renderCard: (card) => node, so each caller draws its own card face
//   right:      optional node shown at the right of the label row (counts, hints)
function FannedHand({ cards, label, accent = "#FBBF24", heldIndex, onPick, onDragStart, renderCard, right, isMobile }) {
  const [hover, setHover] = React.useState(null);
  if (!cards || !cards.length) return null;
  const n = cards.length;
  const cardW = isMobile ? 74 : 92;
  // Overlap so the fan stays on one screen: tighter as the hand grows.
  const overlap = Math.min(cardW * 0.55, (cardW * 0.42) + n * 2);
  const step = cardW - overlap;
  const totalW = step * (n - 1) + cardW;

  return (
    <div style={{position:"fixed", left:0, right:0, bottom:0, zIndex:40,
                 padding:"6px 10px 10px", pointerEvents:"none",
                 background:"linear-gradient(to top, rgba(10,10,14,0.96), rgba(10,10,14,0.72) 60%, transparent)"}}>
      <div style={{display:"flex", justifyContent:"center", alignItems:"center", gap:10, marginBottom:4, pointerEvents:"auto"}}>
        <span style={{fontSize:11,fontWeight:900,letterSpacing:2,color:accent}}>{label}</span>
        {right}
      </div>
      <div style={{position:"relative", height: cardW*1.4 + 18, width: totalW, maxWidth:"100%",
                   margin:"0 auto", pointerEvents:"auto"}}>
        {cards.map((c, i) => {
          const isHeld = heldIndex === i;
          const isHover = hover === i;
          const lift = isHeld ? 26 : isHover ? 20 : 0;
          return (
            <div key={(c.id||"c")+i}
              draggable={!!onDragStart}
              onDragStart={e=>{ onDragStart && onDragStart(i); try{e.dataTransfer.effectAllowed="move";e.dataTransfer.setData("text/plain","f"+i);}catch(err){} }}
              onMouseEnter={()=>setHover(i)} onMouseLeave={()=>setHover(h=>h===i?null:h)}
              onClick={()=>onPick && onPick(i)}
              style={{position:"absolute", left: step*i, bottom:0, width:cardW,
                      transform:`translateY(${-lift}px)`,
                      transition:"transform 130ms ease, box-shadow 130ms ease",
                      cursor: onPick ? "pointer" : "grab",
                      zIndex: isHeld ? 100 : isHover ? 90 : i,
                      filter: isHeld ? `drop-shadow(0 8px 14px ${accent})` : "none",
                      outline: isHeld ? `3px solid ${accent}` : "none", outlineOffset:2, borderRadius:9}}>
              {renderCard(c)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Substitution animation overlay. Shows the benched hero flipping face-up and
// sliding out to discard, then a face-down card dropping in. Centered over the
// board so both players see the same swap. `whose` is "you" or the opponent name.
function SubAnimOverlay({ anim, whose, cardBackUrl, isMobile }) {
  if (!anim) return null;
  const b = anim.benched || {};
  const cardW = isMobile ? 92 : 116;
  return (
    <div style={{position:"fixed", inset:0, zIndex:60, display:"flex", alignItems:"center",
                 justifyContent:"center", pointerEvents:"none",
                 background:"radial-gradient(circle at 50% 50%, rgba(0,0,0,0.55), rgba(0,0,0,0.25))"}}>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:12,fontWeight:900,letterSpacing:2,color:"#FBBF24",marginBottom:14}}>
          {whose} SUBSTITUTED
        </div>
        <div style={{position:"relative", width:cardW, height:cardW*1.4, margin:"0 auto"}}>
          {/* Outgoing hero: flips face-up, then slides out to discard. */}
          <div style={{position:"absolute", inset:0, borderRadius:9, overflow:"hidden",
                       border:"2px solid #F87171", background:"var(--bz-s1)",
                       transformStyle:"preserve-3d",
                       animation:"bzSubFlip 360ms ease-out both, bzSubOut 900ms cubic-bezier(.5,0,.75,0) 620ms forwards"}}>
            {b.img
              ? <img src={b.img} alt={b.hero} style={{width:"100%",height:"100%",objectFit:"cover"}} />
              : <div style={{padding:8,fontSize:12,fontWeight:800,color:"#fff"}}>{b.hero}</div>}
            <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"3px 0",
                         background:"rgba(0,0,0,0.75)",color:"#F87171",fontSize:11,fontWeight:900}}>
              {"\u2192 DISCARD"}
            </div>
          </div>
          {/* Incoming hero: drops in face-down after the outgoing leaves. */}
          <div style={{position:"absolute", inset:0, borderRadius:9, overflow:"hidden",
                       border:"2px solid #dc2626", opacity:0,
                       background: cardBackUrl ? `#111 center/cover no-repeat url(${JSON.stringify(cardBackUrl)})`
                                               : "repeating-linear-gradient(45deg,#7f1d1d,#7f1d1d 6px,#991b1b 6px,#991b1b 12px)",
                       animation:"bzSubIn 620ms cubic-bezier(.2,.8,.3,1) 1500ms forwards"}} />
        </div>
      </div>
    </div>
  );
}

function ArenaShuffleCeremony({ hand, onDone, isMobile }) {
  // phase: "riffle" -> "draw" -> "deal" -> done
  const [phase, setPhase] = React.useState("riffle");
  const [dealt, setDealt] = React.useState(0);
  const cardBackUrl = useCardBack();   // BoBA back image, or null -> CSS fallback

  React.useEffect(() => {
    if (document.getElementById("bz-arena-shuffle")) return;
    const el = document.createElement("style");
    el.id = "bz-arena-shuffle";
    el.textContent = ARENA_SHUFFLE_CSS;
    document.head.appendChild(el);
  }, []);

  React.useEffect(() => {
    const timers = [];
    // Two riffle passes, then the draw, then cards deal out one at a time.
    // Two full riffle passes at 720ms, then a beat before the draw.
    timers.push(setTimeout(() => setPhase("draw"), 1600));
    timers.push(setTimeout(() => setPhase("deal"), 2500));
    for (let i = 1; i <= 7; i++) {
      timers.push(setTimeout(() => setDealt(i), 2500 + i * 200));
    }
    timers.push(setTimeout(() => { onDone && onDone(); }, 2500 + 7 * 200 + 560));
    return () => timers.forEach(clearTimeout);
  }, [onDone]);

  const wrap = {
    position: "relative", width: "100%", height: isMobile ? 300 : 360,
    borderRadius: 12, overflow: "hidden",
    background: "radial-gradient(circle at 50% 62%, rgba(232,49,122,0.10), transparent 62%)",
  };
  const cardBackStyle = {
    position: "absolute", width: isMobile ? 46 : 58, aspectRatio: "5/7", borderRadius: 6,
    overflow: "hidden",
    background: cardBackUrl
      ? `#111 center/cover url(${JSON.stringify(cardBackUrl)})`
      : "repeating-linear-gradient(45deg,#7f1d1d,#7f1d1d 5px,#991b1b 5px,#991b1b 10px)",
    border: "2px solid #dc2626", boxShadow: "0 3px 10px rgba(0,0,0,0.55)",
  };

  return (
    <div style={wrap}>
      <div style={{ position: "absolute", top: 14, left: 0, right: 0, textAlign: "center",
                    fontSize: 11, fontWeight: 900, letterSpacing: 2.5, color: "rgba(255,255,255,0.55)" }}>
        {phase === "riffle" ? "SHUFFLING HERO DECK"
         : phase === "draw" ? "DRAWING 7"
         : "DEALING TO BATTLE ZONES"}
      </div>

      {/* The deck, centred low. Both riffle halves and the draw originate here. */}
      {phase !== "deal" && (
        <div style={{ position: "absolute", left: "50%", top: "62%",
                      animation: phase === "riffle" ? "bzBridge 560ms ease-in-out infinite" : undefined }}>
          {/* Two halves that interleave. Each card carries its own lift height,
              tilt and delay so the fall staggers instead of moving as one block —
              that uniformity was what made the first version look mechanical. */}
          {[0, 1].map(side => (
            <div key={side}>
              {Array.from({ length: 13 }).map((_, i) => {
                // Deterministic pseudo-random per card: same every render, but
                // varied enough that no two cards travel identically.
                const seed = (i * 37 + side * 91) % 100;
                const lift = -(16 + (seed % 13));
                const tilt = 7 + (seed % 9);
                return (
                  <div key={i} style={{
                    ...cardBackStyle, left: 0, top: 0,
                    transform: "translate(-50%,-50%)",
                    marginTop: -i * 1.5,
                    "--lift": `${lift}px`,
                    "--tilt": `${tilt}deg`,
                    animation: phase === "riffle"
                      ? `${side ? "bzRiffleR" : "bzRiffleL"} 720ms cubic-bezier(.34,.9,.4,1) ${i * 34 + side * 17}ms infinite`
                      : undefined,
                    opacity: 1 - i * 0.035,
                    zIndex: side ? i * 2 + 1 : i * 2,   // true interleave, not one stack over the other
                  }} />
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Draw phase: seven backs lift off the deck as a fan. */}
      {phase === "draw" && (
        <div style={{ position: "absolute", left: "50%", top: "62%" }}>
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} style={{
              ...cardBackStyle, left: 0, top: 0,
              transform: "translate(-50%,-50%)",
              marginLeft: (i - 3) * (isMobile ? 26 : 40),
              animation: `bzDrawUp 620ms cubic-bezier(.2,.8,.3,1) ${i * 55}ms both`,
            }} />
          ))}
        </div>
      )}

      {/* Deal phase: the real cards land in their zones, face up, one at a time. */}
      {phase === "deal" && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center",
                      justifyContent: "center", padding: isMobile ? "0 6px" : "0 18px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: isMobile ? 4 : 8, width: "100%" }}>
            {Array.from({ length: 7 }).map((_, i) => {
              const c = hand[i];
              const landed = i < dealt;
              // Each card flies from the deck position (centre-low) to its zone.
              const fromX = `${(3 - i) * (isMobile ? 40 : 74)}px`;
              return (
                <div key={i} style={{ position: "relative" }}>
                  <div style={{
                    width: "100%", aspectRatio: "5/7", borderRadius: 7,
                    border: landed ? "2px solid rgba(255,255,255,0.22)" : "2px dashed rgba(255,255,255,0.13)",
                    background: landed ? "var(--bz-s1)" : "transparent",
                    overflow: "hidden", position: "relative",
                    opacity: landed ? 1 : 0,
                    "--fx": fromX, "--fy": "90px",
                    "--spin": `${-18 + (i * 7) % 26}deg`,
                    animation: landed ? "bzDealOut 460ms cubic-bezier(.22,.85,.3,1) both" : undefined,
                  }}>
                    {landed && c && (c.img
                      ? <img src={c.img} alt={c.hero} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <div style={{ padding: 5, fontSize: 9, fontWeight: 800, color: "#fff" }}>{c.hero}</div>)}
                    {landed && c && (
                      <div style={{ position: "absolute", top: 2, right: 3, background: "rgba(0,0,0,0.82)",
                                    color: "#FBBF24", borderRadius: 4, padding: "0 4px",
                                    fontSize: 10, fontWeight: 900 }}>{c.power}</div>
                    )}
                  </div>
                  <div style={{ textAlign: "center", fontSize: 8.5, fontWeight: 800,
                                color: "rgba(255,255,255,0.3)", marginTop: 3 }}>
                    {i + 1}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button onClick={() => onDone && onDone()}
        style={{ position: "absolute", right: 12, bottom: 10, padding: "6px 14px", borderRadius: 7,
                 border: "1px solid rgba(255,255,255,0.2)", background: "rgba(0,0,0,0.4)",
                 color: "rgba(255,255,255,0.65)", fontSize: 11, fontWeight: 800,
                 cursor: "pointer", fontFamily: "inherit" }}>
        Skip
      </button>
    </div>
  );
}

function ArenaCanvasFx({ strike, onDone }) {
  const canvasRef = React.useRef(null);
  const partsRef  = React.useRef([]);
  const rafRef    = React.useRef(0);
  const runRef    = React.useRef(false);

  // Spawn the particle set for a weapon. (x,y) is the impact point in canvas px,
  // dir is -1 when the strike travels up the board, +1 when it travels down.
  const spawn = React.useCallback((w, x, y, dir, W, H) => {
    const P = partsRef.current;
    const R = (a,b) => a + Math.random()*(b-a);
    const originY = dir < 0 ? H*0.80 : H*0.20;   // the winner's row

    const push = o => P.push(Object.assign({
      x, y, vx:0, vy:0, life:1, decay:0.02, size:3, col:"#fff",
      grav:0, drag:0.99, glow:12, kind:"dot", rot:0, vrot:0, warm:0,
    }, o));

    switch (w) {
      case "Fire": {
        // Travelling fireball: a dense core plus a trailing plume. Embers rise
        // against their own velocity so the flame licks upward on impact.
        for (let i=0;i<26;i++) push({
          x: x + R(-14,14), y: originY + R(-10,10),
          vx: R(-0.7,0.7), vy: dir * R(7,11),
          size: R(7,15), life: 1, decay: R(0.012,0.02),
          col: i%3 ? "#FF7A00" : "#FFD400", kind:"blob", glow: 26, warm: 1,
        });
        for (let i=0;i<160;i++) {
          const d = R(0,1);
          push({
            x: x + R(-26,26), y: y + R(-22,22),
            vx: R(-3.4,3.4), vy: R(-5.2,-0.4) + dir*R(0,1.6),
            size: R(2,7)*(1-d*0.5), life: R(0.65,1), decay: R(0.014,0.03),
            col: d<0.35 ? "#FFF1A8" : d<0.7 ? "#FF8A00" : "#E23B00",
            grav: -0.055, drag: 0.975, kind:"ember", glow: 20, warm: 1,
          });
        }
        for (let i=0;i<34;i++) push({          // smoke, lingers after the flame
          x: x + R(-20,20), y: y + R(-16,16),
          vx: R(-1.1,1.1), vy: R(-2.4,-0.5),
          size: R(9,20), life: R(0.4,0.7), decay: R(0.006,0.012),
          col:"rgba(40,25,20,0.5)", grav:-0.02, drag:0.985, kind:"smoke", glow:0,
        });
        break;
      }
      case "Ice": {
        // Shards fly in, then crystals grow outward along fracture angles.
        for (let i=0;i<70;i++) {
          const a = R(0, Math.PI*2);
          push({
            x, y, vx: Math.cos(a)*R(2,9), vy: Math.sin(a)*R(2,9) + dir*1.5,
            size: R(3,10), life: 1, decay: R(0.012,0.024),
            col: i%4 ? "#BFEFFF" : "#FFFFFF", kind:"shard",
            rot: a, vrot: R(-0.3,0.3), glow: 18,
          });
        }
        for (let i=0;i<40;i++) push({          // frost motes drifting down
          x: x + R(-40,40), y: y + R(-30,30),
          vx: R(-0.8,0.8), vy: R(0.3,1.6),
          size: R(1.5,4), life: 1, decay: R(0.006,0.014),
          col:"#EAF9FF", grav:0.02, drag:0.99, kind:"dot", glow:10,
        });
        break;
      }
      case "Brawl": {
        // Heavy single impact: shockwave plus knuckle debris.
        push({ x, y, size: 8, life:1, decay:0.045, col:"#FFFFFF", kind:"ring", glow:30 });
        push({ x, y, size: 8, life:1, decay:0.03,  col:"#F87171", kind:"ring", glow:24 });
        for (let i=0;i<80;i++) {
          const a = R(0,Math.PI*2), s = R(3,13);
          push({
            x, y, vx: Math.cos(a)*s, vy: Math.sin(a)*s,
            size: R(2,6), life:1, decay: R(0.02,0.04),
            col: i%5 ? "#FCA5A5" : "#FFFFFF", grav:0.28, drag:0.96, kind:"dot", glow:12,
          });
        }
        break;
      }
      case "Steel": {
        // Blade pass: a bright slash line, then sparks that fall with gravity.
        push({ x, y, size: 1, life:1, decay:0.05, col:"#F8FAFC", kind:"slash", rot: -0.72, glow:34 });
        for (let i=0;i<150;i++) {
          const a = -0.72 + R(-0.5,0.5) + (Math.random()<0.5?Math.PI:0);
          const s = R(4,16);
          push({
            x: x + R(-30,30), y: y + R(-30,30),
            vx: Math.cos(a)*s, vy: Math.sin(a)*s,
            size: R(1,3.2), life:1, decay: R(0.018,0.04),
            col: i%4 ? "#FFE9A8" : "#FFFFFF", grav:0.34, drag:0.965, kind:"spark", glow:16,
          });
        }
        break;
      }
      case "Gum": {
        // Wet splat: a blob flies in, bursts, then strands drip downward.
        push({ x, y:originY, vx:0, vy: dir*9, size: 22, life:1, decay:0.02,
               col:"#FF69B4", kind:"blob", glow:22 });
        for (let i=0;i<70;i++) {
          const a = R(0,Math.PI*2);
          push({
            x, y, vx: Math.cos(a)*R(1,7), vy: Math.sin(a)*R(1,7),
            size: R(3,11), life:1, decay: R(0.01,0.022),
            col: i%3 ? "#FF69B4" : "#FFB6D9", grav:0.16, drag:0.97, kind:"blob", glow:14,
          });
        }
        for (let i=0;i<16;i++) push({          // strands
          x: x + R(-30,30), y: y + R(-10,10),
          vx: R(-0.3,0.3), vy: R(0.6,2.2),
          size: R(3,7), life:1, decay: R(0.006,0.012),
          col:"#FF69B4", grav:0.05, kind:"strand", glow:10,
        });
        break;
      }
      case "Hex": {
        // Arcane: counter-rotating hex rings and orbiting motes.
        for (let i=0;i<3;i++) push({
          x, y, size: 10+i*6, life:1, decay: 0.016+i*0.003,
          col: i%2 ? "#D8B4FE" : "#A855F7", kind:"hexring",
          rot: R(0,Math.PI), vrot: (i%2?1:-1)*R(0.04,0.09), glow:28,
        });
        for (let i=0;i<90;i++) {
          const a = R(0,Math.PI*2), r0 = R(6,50);
          push({
            x: x + Math.cos(a)*r0, y: y + Math.sin(a)*r0,
            vx: -Math.cos(a)*R(0.4,2.2), vy: -Math.sin(a)*R(0.4,2.2),
            size: R(2,5), life:1, decay: R(0.012,0.026),
            col: i%3 ? "#C084FC" : "#F0ABFC", drag:0.985, kind:"dot", glow:18,
          });
        }
        break;
      }
      case "Glow": {
        // Radiance: a bloom core with light rays fanning out.
        push({ x, y, size: 10, life:1, decay:0.022, col:"#FFF7CC", kind:"ring", glow:40 });
        for (let i=0;i<22;i++) push({
          x, y, size: R(50,130), life:1, decay: R(0.02,0.035),
          col:"#FDE68A", kind:"ray", rot: (i/22)*Math.PI*2 + R(-0.1,0.1), glow:26,
        });
        for (let i=0;i<110;i++) {
          const a = R(0,Math.PI*2), s = R(1,7);
          push({
            x, y, vx: Math.cos(a)*s, vy: Math.sin(a)*s,
            size: R(1.5,5), life:1, decay: R(0.012,0.028),
            col: i%2 ? "#FFFFFF" : "#FDE68A", drag:0.98, kind:"dot", glow:22,
          });
        }
        break;
      }
      case "Cyber": {
        // Digital: scan sweep, then data-bit squares that fall apart.
        push({ x: W/2, y, size: W, life:1, decay:0.028, col:"#00FFC8", kind:"scan", glow:26 });
        for (let i=0;i<130;i++) push({
          x: x + R(-60,60), y: y + R(-45,45),
          vx: R(-2.5,2.5), vy: R(-2.5,2.5),
          size: R(2,7), life:1, decay: R(0.014,0.032),
          col: i%4 ? "#00FFC8" : "#B9FFF0", drag:0.985, kind:"bit", glow:16,
        });
        break;
      }
      case "Alt": {
        // Glitch: RGB-split bars that tear horizontally.
        for (let i=0;i<26;i++) push({
          x: W/2, y: y + R(-55,55),
          vx: R(-9,9), vy: 0,
          size: R(8,26), life:1, decay: R(0.02,0.05),
          col: i%3===0 ? "rgba(255,0,110,0.85)" : i%3===1 ? "rgba(0,229,255,0.85)" : "rgba(255,255,255,0.8)",
          kind:"tear", glow:0,
        });
        for (let i=0;i<70;i++) {
          const a = R(0,Math.PI*2);
          push({
            x, y, vx: Math.cos(a)*R(2,9), vy: Math.sin(a)*R(2,9),
            size: R(2,6), life:1, decay: R(0.02,0.04),
            col: i%2 ? "#FF006E" : "#00E5FF", kind:"bit", glow:14,
          });
        }
        break;
      }
      case "Super":
      case "Medal":
      case "Metallic": {
        const gold = w !== "Metallic";
        const c1 = gold ? "#FFD700" : "#E2E8F0";
        const c2 = gold ? "#FFF3B0" : "#FFFFFF";
        for (let i=0;i<4;i++) push({
          x, y, size: 12, life:1, decay: 0.012+i*0.002, col: c1, kind:"ring", glow:44,
        });
        for (let i=0;i<24;i++) push({
          x, y, size: R(60,190), life:1, decay: R(0.014,0.026),
          col: c2, kind:"ray", rot: (i/24)*Math.PI*2, glow:30,
        });
        for (let i=0;i<220;i++) {
          const a = R(0,Math.PI*2), s = R(2,16);
          push({
            x, y, vx: Math.cos(a)*s, vy: Math.sin(a)*s,
            size: R(1.5,6), life:1, decay: R(0.01,0.028),
            col: i%3 ? c1 : c2, grav:0.06, drag:0.978, kind:"spark", glow:24,
          });
        }
        break;
      }
      default: {
        for (let i=0;i<90;i++) {
          const a = R(0,Math.PI*2), s = R(2,10);
          push({ x, y, vx:Math.cos(a)*s, vy:Math.sin(a)*s, size:R(2,6),
                 life:1, decay:R(0.015,0.035), col:"#FFFFFF", drag:0.97, kind:"dot", glow:14 });
        }
      }
    }
  }, []);

  // Draw one particle. Kinds differ enough that a switch is clearer than
  // trying to unify them behind a generic renderer.
  const draw = (ctx, p) => {
    const a = Math.max(0, Math.min(1, p.life));
    ctx.globalAlpha = a;
    ctx.shadowBlur = p.glow;
    ctx.shadowColor = p.col;
    ctx.fillStyle = p.col;
    ctx.strokeStyle = p.col;

    switch (p.kind) {
      case "ring":
        ctx.lineWidth = Math.max(1, 9*a);
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size*(1 + (1-a)*7), 0, Math.PI*2); ctx.stroke();
        break;
      case "ray": {
        ctx.lineWidth = Math.max(1, 5*a);
        const len = p.size*(0.5 + (1-a)*1.4);
        ctx.beginPath();
        ctx.moveTo(p.x + Math.cos(p.rot)*8, p.y + Math.sin(p.rot)*8);
        ctx.lineTo(p.x + Math.cos(p.rot)*len, p.y + Math.sin(p.rot)*len);
        ctx.stroke();
        break;
      }
      case "slash": {
        ctx.lineWidth = Math.max(1, 16*a);
        const L = 240*(0.4 + (1-a));
        ctx.beginPath();
        ctx.moveTo(p.x - Math.cos(p.rot)*L, p.y - Math.sin(p.rot)*L);
        ctx.lineTo(p.x + Math.cos(p.rot)*L, p.y + Math.sin(p.rot)*L);
        ctx.stroke();
        break;
      }
      case "scan":
        ctx.lineWidth = Math.max(1, 10*a);
        ctx.beginPath(); ctx.moveTo(p.x - p.size/2, p.y); ctx.lineTo(p.x + p.size/2, p.y); ctx.stroke();
        break;
      case "tear":
        ctx.fillRect(p.x - 200, p.y, 400, p.size*a);
        break;
      case "hexring": {
        ctx.lineWidth = Math.max(1, 4.5*a);
        const r = p.size*(1 + (1-a)*5);
        ctx.beginPath();
        for (let k=0;k<6;k++) {
          const ang = p.rot + k*Math.PI/3;
          const px = p.x + Math.cos(ang)*r, py = p.y + Math.sin(ang)*r;
          k ? ctx.lineTo(px,py) : ctx.moveTo(px,py);
        }
        ctx.closePath(); ctx.stroke();
        break;
      }
      case "shard": {
        ctx.save(); ctx.translate(p.x,p.y); ctx.rotate(p.rot);
        ctx.beginPath();
        ctx.moveTo(0,-p.size); ctx.lineTo(p.size*0.38,0);
        ctx.lineTo(0,p.size); ctx.lineTo(-p.size*0.38,0);
        ctx.closePath(); ctx.fill(); ctx.restore();
        break;
      }
      case "spark": {
        ctx.lineWidth = Math.max(1, p.size);
        ctx.beginPath(); ctx.moveTo(p.x, p.y);
        ctx.lineTo(p.x - p.vx*1.8, p.y - p.vy*1.8); ctx.stroke();
        break;
      }
      case "bit":
        ctx.fillRect(p.x - p.size/2, p.y - p.size/2, p.size, p.size);
        break;
      case "strand":
        ctx.lineWidth = p.size;
        ctx.lineCap = "round";
        ctx.beginPath(); ctx.moveTo(p.x, p.y - 16); ctx.lineTo(p.x, p.y + 16*(1-a)); ctx.stroke();
        break;
      case "smoke":
        ctx.globalAlpha = a*0.5;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size*(1+(1-a)*1.6), 0, Math.PI*2); ctx.fill();
        break;
      default:
        ctx.beginPath(); ctx.arc(p.x, p.y, Math.max(0.5,p.size*a), 0, Math.PI*2); ctx.fill();
    }
  };

  const tick = React.useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) { runRef.current = false; return; }
    const ctx = cv.getContext("2d");
    const P = partsRef.current;
    ctx.clearRect(0,0,cv.width,cv.height);
    ctx.globalCompositeOperation = "lighter";   // additive: fire and sparks glow

    for (let i = P.length - 1; i >= 0; i--) {
      const p = P[i];
      p.vy += p.grav;
      p.vx *= p.drag; p.vy *= p.drag;
      p.x += p.vx; p.y += p.vy;
      p.rot += p.vrot;
      p.life -= p.decay;
      if (p.life <= 0) { P.splice(i,1); continue; }
      draw(ctx, p);
    }
    ctx.globalAlpha = 1;
    ctx.shadowBlur = 0;
    ctx.globalCompositeOperation = "source-over";

    if (P.length) rafRef.current = requestAnimationFrame(tick);
    else { runRef.current = false; onDone && onDone(); }
  }, [onDone]);

  React.useEffect(() => {
    if (!strike) return;
    const cv = canvasRef.current;
    if (!cv) return;
    const rect = cv.parentElement.getBoundingClientRect();
    // Match the backing store to CSS pixels so nothing looks soft on retina.
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width  = Math.max(1, Math.floor(rect.width * dpr));
    cv.height = Math.max(1, Math.floor(rect.height * dpr));
    cv.style.width = rect.width + "px";
    cv.style.height = rect.height + "px";
    const ctx = cv.getContext("2d");
    ctx.setTransform(dpr,0,0,dpr,0,0);

    const W = rect.width, H = rect.height;
    const x = ((strike.zone + 0.5) / 7) * W;
    const y = strike.up ? H*0.22 : H*0.78;
    spawn(strike.weapon, x, y, strike.up ? -1 : 1, W, H);

    if (!runRef.current) { runRef.current = true; rafRef.current = requestAnimationFrame(tick); }
    return () => cancelAnimationFrame(rafRef.current);
  }, [strike, spawn, tick]);

  return (
    <canvas ref={canvasRef}
      style={{position:"absolute",inset:0,pointerEvents:"none",zIndex:46,width:"100%",height:"100%"}} />
  );
}

// ============================================================================
// PERSISTENT DAMAGE
//
// The mark a weapon leaves stays on the losing card for the rest of the game.
// You asked for Ice to freeze the opponent's card — freezing that evaporates
// after 900ms isn't freezing, so every weapon now leaves a lasting scar and the
// board reads as a history of what happened.
//
// Rendered INSIDE the card (which is overflow:hidden), so it clips correctly.
// ============================================================================

function ArenaDamage({ w }) {
  if (!w) return null;
  const base = { position:"absolute", inset:0, pointerEvents:"none", zIndex:4 };

  switch (w) {
    case "Ice":
      return (
        <div style={base}>
          <div style={{position:"absolute",inset:0,
            background:"linear-gradient(160deg,rgba(191,232,255,0.62),rgba(120,190,240,0.30) 45%,rgba(230,248,255,0.58))",
            boxShadow:"inset 0 0 26px rgba(255,255,255,0.85)"}}/>
          <svg viewBox="0 0 50 70" preserveAspectRatio="none"
               style={{position:"absolute",inset:0,width:"100%",height:"100%",opacity:0.9}}>
            <g stroke="#fff" strokeWidth="0.65" fill="none">
              <path d="M25 0 L25 26 L14 40 M25 26 L37 41 M25 40 L25 70"/>
              <path d="M0 22 L16 30 M50 20 L34 29"/>
              <path d="M6 62 L18 48 M44 60 L33 49"/>
              <path d="M14 40 L6 44 M37 41 L45 46"/>
            </g>
          </svg>
        </div>
      );
    case "Fire":
      return (
        <div style={{...base,
          background:"radial-gradient(circle at 50% 62%,rgba(60,20,0,0.72) 0%,rgba(120,40,0,0.42) 34%,rgba(0,0,0,0.30) 68%)",
          boxShadow:"inset 0 -22px 30px rgba(0,0,0,0.55)"}}/>
      );
    case "Gum":
      return (
        <div style={base}>
          <div style={{position:"absolute",left:"14%",top:"22%",width:"72%",height:"46%",
            borderRadius:"48% 52% 44% 56% / 52% 44% 56% 48%",
            background:"radial-gradient(circle at 38% 34%,rgba(255,182,217,0.92),rgba(255,105,180,0.85) 60%,rgba(219,39,119,0.8))",
            boxShadow:"0 0 18px rgba(255,105,180,0.6)"}}/>
          {[20,42,64,80].map((x,i)=>(
            <div key={i} style={{position:"absolute",left:x+"%",top:"56%",width:6,
              height:14+(i%3)*12,borderRadius:"0 0 60% 60%",background:"rgba(255,105,180,0.88)"}}/>
          ))}
        </div>
      );
    case "Steel":
      return (
        <svg viewBox="0 0 50 70" preserveAspectRatio="none" style={{...base,width:"100%",height:"100%"}}>
          <path d="M2 62 L48 10" stroke="#F1F5F9" strokeWidth="2.6" fill="none" opacity="0.92"/>
          <path d="M9 68 L44 20" stroke="#94A3B8" strokeWidth="1.2" fill="none" opacity="0.7"/>
          <path d="M2 62 L48 10" stroke="#000" strokeWidth="5" fill="none" opacity="0.28"/>
        </svg>
      );
    case "Hex":
      return (
        <div style={base}>
          <div style={{position:"absolute",inset:0,
            background:"radial-gradient(circle at 50% 50%,rgba(168,85,247,0.42),rgba(88,28,135,0.30) 58%,transparent 80%)",
            mixBlendMode:"screen"}}/>
          <svg viewBox="0 0 100 100" preserveAspectRatio="none"
               style={{position:"absolute",inset:0,width:"100%",height:"100%",opacity:0.75}}>
            <polygon points="50,8 88,29 88,71 50,92 12,71 12,29" fill="none" stroke="#D8B4FE" strokeWidth="2.2"/>
          </svg>
        </div>
      );
    case "Glow":
      return <div style={{...base,
        background:"radial-gradient(circle at 50% 50%,rgba(255,255,255,0.55) 0%,rgba(255,240,170,0.30) 40%,transparent 74%)"}}/>;
    case "Cyber":
      return (
        <div style={base}>
          <div style={{position:"absolute",inset:0,opacity:0.45,
            background:"repeating-linear-gradient(0deg,rgba(0,255,200,0.20) 0 1px,transparent 1px 4px)"}}/>
          <div style={{position:"absolute",inset:"12%",border:"1.5px solid rgba(0,255,200,0.7)"}}/>
        </div>
      );
    case "Alt":
      return (
        <div style={base}>
          {[18,44,70].map((t,i)=>(
            <div key={i} style={{position:"absolute",left:0,right:0,top:t+"%",height:6+i*3,
              background: i%2 ? "rgba(0,229,255,0.5)" : "rgba(255,0,110,0.5)",
              transform:`translateX(${i%2?6:-6}px)`,mixBlendMode:"screen"}}/>
          ))}
        </div>
      );
    case "Brawl":
      return <div style={{...base,
        background:"radial-gradient(circle at 50% 48%,rgba(0,0,0,0.55) 0%,rgba(120,20,20,0.40) 32%,transparent 66%)",
        boxShadow:"inset 0 0 24px rgba(0,0,0,0.6)"}}/>;
    case "Super":
    case "Medal":
      return <div style={{...base,
        background:"radial-gradient(circle at 50% 50%,rgba(255,215,0,0.40),rgba(255,60,0,0.22) 52%,transparent 78%)",
        boxShadow:"inset 0 0 30px rgba(255,215,0,0.5)"}}/>;
    case "Metallic":
      return <div style={{...base,
        background:"linear-gradient(100deg,transparent 32%,rgba(226,232,240,0.55) 48%,rgba(100,116,139,0.45) 56%,transparent 72%)"}}/>;
    default:
      return null;
  }
}

function ArenaStrikeLayer({ w, up, zone }) {
  if (!w) return null;
  const TONE = {
    Fire:"#FF6A00", Ice:"#7DD3FC", Brawl:"#F87171", Steel:"#E2E8F0",
    Gum:"#FF69B4", Hex:"#A855F7", Glow:"#FDE68A", Cyber:"#00FFC8",
    Alt:"#FF006E", Super:"#FFD700", Medal:"#FFD700", Metallic:"#CBD5E1",
  };
  const tone = TONE[w] || "#fff";
  // Impact column: 7 zones, centre of the struck one.
  const leftPct = ((zone + 0.5) / 7) * 100;
  // The blow lands on the row OPPOSITE the winner.
  const impactTop = up ? "22%" : "78%";

  return (
    <div style={{position:"absolute",inset:0,pointerEvents:"none",zIndex:45,overflow:"hidden",borderRadius:12}}>
      {/* whole-board tint in the weapon colour */}
      <div style={{position:"absolute",inset:0,animation:"bzBoardFlash 620ms ease-out forwards",
        background:`radial-gradient(circle at ${leftPct}% ${up?"25%":"75%"}, ${tone}88 0%, ${tone}22 34%, transparent 68%)`,
        mixBlendMode:"screen"}}/>
      {/* concussion ring at the impact point */}
      {[0,1].map(i=>(
        <div key={i} style={{position:"absolute",left:`${leftPct}%`,top:impactTop,
          width:70,height:70,borderRadius:"50%",border:`6px solid ${tone}`,
          animation:`bzRing ${820+i*140}ms ease-out ${i*110}ms forwards`,
          boxShadow:`0 0 34px ${tone}`}}/>
      ))}
      {/* sparks thrown outward from impact */}
      {Array.from({length:12}).map((_,i)=>{
        const ang = (i/12)*Math.PI*2;
        const dist = 60 + (i%4)*26;
        return (
          <div key={"s"+i} style={{position:"absolute",left:`${leftPct}%`,top:impactTop,
            width:i%3===0?7:4, height:i%3===0?7:4, borderRadius:"50%", background:tone,
            boxShadow:`0 0 12px ${tone}`,
            "--dx":`${Math.cos(ang)*dist}px`, "--dy":`${Math.sin(ang)*dist}px`,
            animation:`bzSpark ${520+(i%5)*90}ms ease-out ${(i%3)*40}ms forwards`}}/>
        );
      })}
    </div>
  );
}

// ── Arena board arranger (ADMIN) ──────────────────────────────────────────
// A full-screen editor: the playmat fills a 16:9 stage, and every placeable
// element is a draggable + resizable box. Positions are stored as PERCENT of the
// stage so they scale to any screen at play time. Drag the body to move; drag the
// bottom-right handle to resize. Saves the whole map to Firestore.
function ArenaArranger({ playmatUrl, initial, onClose, onSave }) {
  const [lay, setLay] = React.useState(() => JSON.parse(JSON.stringify(initial || defaultArenaLayout())));
  const [sel, setSel] = React.useState(null);
  const stageRef = React.useRef(null);
  const drag = React.useRef(null);

  const groups = [];
  // My mat is single-POV: only my own elements. The opponent has their own mat
  // across the table, so nothing of theirs is placed on mine.
  for (let i=0;i<7;i++) groups.push({ id:"my"+i,   label:"My "+(i+1),   color:"#4ade80", kind:"card" });
  for (let i=0;i<7;i++) groups.push({ id:"zone"+i, label:"Zone "+(i+1), color:"#FBBF24", kind:"zone" });
  [["Deck","Hero Deck"],["Playbook","Playbook"],["Hotdogs","Hot Dogs"],["Discard","Discard"]].forEach(([k,lbl])=>{
    groups.push({ id:"my"+k,  label:lbl,  color:"#7CF03A", kind:"card" });
  });

  const onDown = (e, id, mode) => {
    e.preventDefault(); e.stopPropagation();
    const t = e.touches ? e.touches[0] : e;
    setSel(id);
    drag.current = { id, mode, startX:t.clientX, startY:t.clientY, box:{...lay[id]} };
  };
  React.useEffect(() => {
    const move = (e) => {
      if (!drag.current || !stageRef.current) return;
      const t = e.touches ? e.touches[0] : e;
      const r = stageRef.current.getBoundingClientRect();
      const dxP = ((t.clientX - drag.current.startX)/r.width)*100;
      const dyP = ((t.clientY - drag.current.startY)/r.height)*100;
      const b = drag.current.box;
      setLay(prev => {
        const next = {...prev};
        if (drag.current.mode === "move") {
          next[drag.current.id] = { ...next[drag.current.id],
            x: Math.max(0, Math.min(97, b.x + dxP)),
            y: Math.max(0, Math.min(97, b.y + dyP)) };
        } else {
          next[drag.current.id] = { ...next[drag.current.id],
            w: Math.max(3, Math.min(40, (b.w||12) + dxP)) };
          if (next[drag.current.id].h != null)
            next[drag.current.id].h = Math.max(2, Math.min(20, (b.h||5) + dyP));
        }
        return next;
      });
    };
    const up = () => { drag.current = null; };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, {passive:false}); window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move); window.removeEventListener("touchend", up);
    };
  }, []);

  const boxStyle = (g) => {
    const b = lay[g.id] || { x:0, y:0, w:12 };
    const h = g.kind === "zone" ? (b.h||5) : null;
    return {
      position:"absolute", left:b.x+"%", top:b.y+"%", width:b.w+"%",
      ...(h!=null ? { height:h+"%" } : { aspectRatio:"5/7" }),
      border:`2px solid ${g.color}`, borderRadius:6,
      background: sel===g.id ? `${g.color}33` : `${g.color}18`,
      boxShadow: sel===g.id ? `0 0 0 2px ${g.color}` : "none",
      cursor:"move", boxSizing:"border-box", touchAction:"none",
      display:"flex", alignItems:"center", justifyContent:"center",
    };
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:15000,background:"rgba(0,0,0,0.9)",display:"flex",flexDirection:"column",padding:16}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10,flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:15,fontWeight:900,color:"#fff"}}>{"\u270B"} Arrange board — drag to move, corner to resize</div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>setLay(defaultArenaLayout())}
            style={{padding:"8px 14px",borderRadius:8,background:"transparent",border:"1px solid rgba(255,255,255,0.25)",color:"rgba(255,255,255,0.7)",fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Reset</button>
          <button onClick={onClose}
            style={{padding:"8px 14px",borderRadius:8,background:"transparent",border:"1px solid rgba(255,255,255,0.25)",color:"rgba(255,255,255,0.7)",fontWeight:800,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          <button onClick={()=>onSave(lay)}
            style={{padding:"8px 16px",borderRadius:8,background:"#E8317A",border:"none",color:"#fff",fontWeight:900,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Save layout</button>
        </div>
      </div>
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",minHeight:0}}>
        <div ref={stageRef}
          style={{position:"relative",width:"min(100%, 160vh)",aspectRatio:"16/9",
                  background: playmatUrl ? `#111 center/cover no-repeat url("${playmatUrl}")` : "#1a0f14",
                  border:"1px solid rgba(232,49,122,0.4)",borderRadius:10,overflow:"hidden"}}
          onMouseDown={()=>setSel(null)}>
          {groups.map(g => (
            <div key={g.id} style={boxStyle(g)} onMouseDown={(e)=>onDown(e,g.id,"move")} onTouchStart={(e)=>onDown(e,g.id,"move")}>
              <span style={{fontSize:9,fontWeight:900,color:g.color,textShadow:"0 1px 3px rgba(0,0,0,0.9)",pointerEvents:"none",textAlign:"center",lineHeight:1.1}}>{g.label}</span>
              <div onMouseDown={(e)=>onDown(e,g.id,"resize")} onTouchStart={(e)=>onDown(e,g.id,"resize")}
                style={{position:"absolute",right:-6,bottom:-6,width:14,height:14,borderRadius:"50%",
                        background:g.color,border:"2px solid #000",cursor:"nwse-resize"}}/>
            </div>
          ))}
        </div>
      </div>
      <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",textAlign:"center",marginTop:8}}>
        Positions save as percentages, so they scale on phone and desktop. Opponent slots render flipped 180° in-game.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// BAZOOKA FANTASY (/fantasy) — a standings + Bazooka Bucks layer on top of
// DraftKings. DK runs the actual NFL contest; Bazooka just tracks results.
// Each week an admin types in the finishing order (names, from DK). Bucks are
// awarded by finish: 1st = 9, 2nd–10th = 3, 11th–20th = 1, 21st–30th = 0.5.
// Players are tracked BY NAME (no login). Each carries Earned (never drops —
// drives rank), Redeemed, and Balance (Earned − Redeemed). Redemptions are
// admin-processed at 3 Bucks per pack. Data model in Firestore:
//   fantasy_season/current   -> { players: { name: {earned, redeemed} }, weeks: [...] }
//   fantasy_weeks/{weekId}    -> { week, order:[names], awarded:{name:bucks}, enteredAt }
// ═══════════════════════════════════════════════════════════════════════════

// Bucks awarded for a finishing position (1-indexed rank).
function fantasyBucksForRank(rank) {
  if (rank === 1) return 9;
  if (rank >= 2 && rank <= 10) return 3;
  if (rank >= 11 && rank <= 20) return 1;
  if (rank >= 21 && rank <= 30) return 0.5;
  return 0;
}
const REDEEM_COST = 3;   // Bazooka Bucks per pack

function FantasyPage({ user }) {
  const [season, setSeason] = React.useState(null);
  const [weeks, setWeeks] = React.useState([]);
  const [view, setView] = React.useState("standings");   // standings | weeks | redeem
  const [openWeek, setOpenWeek] = React.useState(null);
  const [toast, setToast] = React.useState("");
  const isAdmin = !!(user?.email && user.email.endsWith("@bazookabreaks.com"));

  React.useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  React.useEffect(() => {
    try {
      return onSnapshot(doc(db, "fantasy_season", "current"), snap => {
        setSeason(snap.exists() ? snap.data() : { players: {}, weeks: [] });
      });
    } catch (e) { setSeason({ players: {}, weeks: [] }); }
  }, []);
  React.useEffect(() => {
    try {
      return onSnapshot(query(collection(db, "fantasy_weeks"), orderBy("week", "desc")), snap => {
        setWeeks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });
    } catch (e) { return; }
  }, []);

  const players = (season && season.players) || {};
  const standings = Object.entries(players)
    .map(([name, v]) => ({ name, earned: v.earned || 0, redeemed: v.redeemed || 0, balance: Math.round(((v.earned || 0) - (v.redeemed || 0)) * 100) / 100 }))
    .sort((a, b) => b.earned - a.earned || a.name.localeCompare(b.name));
  const fmt = n => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  const totalPacks = Math.floor(standings.reduce((s,p)=>s+p.balance,0) / REDEEM_COST);
  const totalPlayers = standings.length;
  const weeksPlayed = (season && season.weeks && season.weeks.length) || 0;

  const podium = standings.slice(0, 3);
  const rest = standings.slice(3);

  // Chip — the Bazooka Bucks token, rendered as a coin.
  const Chip = ({ v, size = 15, color = "#FBBF24" }) => (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontWeight:900, color, fontSize:size, fontVariantNumeric:"tabular-nums" }}>
      <span style={{ width:size+3, height:size+3, borderRadius:"50%", background:"radial-gradient(circle at 35% 30%, #ffe9a8, #FBBF24 55%, #b8860b)", boxShadow:"inset 0 0 0 2px rgba(0,0,0,0.25), 0 1px 3px rgba(0,0,0,0.4)", display:"inline-block", flexShrink:0 }} />
      {fmt(v)}
    </span>
  );

  const bg = "radial-gradient(ellipse 100% 60% at 50% -10%, rgba(232,49,122,0.22), transparent 60%), radial-gradient(ellipse 80% 50% at 50% 110%, rgba(124,156,255,0.10), transparent 60%), #08050a";

  return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"'Trebuchet MS','Segoe UI',sans-serif", color:"#f6eef2", padding:"0 0 80px" }}>
      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", top:20, left:"50%", transform:"translateX(-50%)", zIndex:9999, background:"#161016", border:"1px solid rgba(232,49,122,0.4)", color:"#fff", padding:"12px 22px", borderRadius:12, fontWeight:700, fontSize:14, boxShadow:"0 8px 30px rgba(0,0,0,0.5)" }}>{toast}</div>
      )}

      {/* ── HERO ── */}
      <div style={{ position:"relative", overflow:"hidden", borderBottom:"1px solid rgba(232,49,122,0.2)" }}>
        {/* faint yard-line texture */}
        <div style={{ position:"absolute", inset:0, backgroundImage:"repeating-linear-gradient(90deg, transparent, transparent 78px, rgba(255,255,255,0.03) 78px, rgba(255,255,255,0.03) 80px)", pointerEvents:"none" }} />
        <div style={{ maxWidth:960, margin:"0 auto", padding:"42px 20px 34px", position:"relative", textAlign:"center" }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(232,49,122,0.12)", border:"1px solid rgba(232,49,122,0.35)", borderRadius:100, padding:"5px 14px", fontSize:11.5, fontWeight:800, letterSpacing:1.5, color:"#ff7db0", textTransform:"uppercase", marginBottom:16 }}>
            <span style={{ width:6, height:6, borderRadius:"50%", background:"#34d399", boxShadow:"0 0 8px #34d399" }} /> Season Live
          </div>
          <h1 style={{ fontFamily:"'Bangers',system-ui,cursive", fontSize:"clamp(44px, 9vw, 86px)", lineHeight:0.92, margin:"0 0 6px", letterSpacing:2,
                       background:"linear-gradient(180deg, #fff 0%, #ffd0e4 45%, #E8317A 100%)", WebkitBackgroundClip:"text", backgroundClip:"text", color:"transparent",
                       textShadow:"0 0 40px rgba(232,49,122,0.35)", WebkitTextStroke:"1px rgba(0,0,0,0.15)" }}>
            BAZOOKA FANTASY
          </h1>
          <p style={{ fontSize:"clamp(13px, 2.4vw, 16px)", color:"#c9bdc4", maxWidth:560, margin:"0 auto 22px", lineHeight:1.5 }}>
            Draft your squad on <strong style={{color:"#fff"}}>DraftKings</strong>. Finish high on the board, stack <strong style={{color:"#FBBF24"}}>Bazooka Bucks</strong>, and cash them in for packs. The grind is real, the packs are free.
          </p>
          {/* stat rail */}
          <div style={{ display:"inline-flex", gap:0, background:"rgba(0,0,0,0.35)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:14, overflow:"hidden", flexWrap:"wrap", justifyContent:"center" }}>
            {[["Players", totalPlayers], ["Weeks", weeksPlayed], ["Packs in play", totalPacks]].map(([label,val],i)=>(
              <div key={label} style={{ padding:"12px 24px", borderLeft: i? "1px solid rgba(255,255,255,0.08)":"none", minWidth:96 }}>
                <div style={{ fontSize:"clamp(20px,4vw,28px)", fontWeight:900, color:"#fff", fontVariantNumeric:"tabular-nums", lineHeight:1 }}>{val}</div>
                <div style={{ fontSize:10.5, color:"#9a8a94", textTransform:"uppercase", letterSpacing:1, marginTop:4, fontWeight:700 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth:960, margin:"0 auto", padding:"0 16px" }}>
        {/* payout scale ribbon */}
        <div style={{ display:"flex", gap:8, justifyContent:"center", flexWrap:"wrap", margin:"24px 0 8px" }}>
          {[["1st","9","#FBBF24"],["2–10","3","#E8317A"],["11–20","1","#7c9cff"],["21–30","0.5","#9a8a94"]].map(([pos,amt,col])=>(
            <div key={pos} style={{ display:"flex", alignItems:"center", gap:8, background:"rgba(0,0,0,0.35)", border:`1px solid ${col}44`, borderRadius:10, padding:"8px 14px" }}>
              <span style={{ fontSize:12, fontWeight:800, color:"#c9bdc4" }}>{pos}</span>
              <span style={{ width:1, height:14, background:"rgba(255,255,255,0.15)" }} />
              <Chip v={parseFloat(amt)} color={col} size={13} />
            </div>
          ))}
        </div>

        {/* tabs */}
        <div style={{ display:"flex", gap:6, justifyContent:"center", margin:"18px 0 22px", flexWrap:"wrap" }}>
          {[["standings","Standings"],["weeks","Weekly"],["redeem","Redeem"]].map(([v,lbl])=>(
            <button key={v} onClick={()=>setView(v)}
              style={{ padding:"10px 22px", borderRadius:100, fontSize:13.5, fontWeight:800, cursor:"pointer", fontFamily:"inherit", letterSpacing:0.3, transition:"all 0.15s",
                       border:`1px solid ${view===v?"transparent":"rgba(255,255,255,0.14)"}`,
                       background: view===v ? "linear-gradient(135deg,#E8317A,#b8256a)" : "transparent",
                       color: view===v ? "#fff" : "#9a8a94", boxShadow: view===v?"0 4px 16px rgba(232,49,122,0.35)":"none" }}>{lbl}</button>
          ))}
        </div>

        {/* ── STANDINGS ── */}
        {view === "standings" && (
          standings.length === 0 ? (
            <div style={{ textAlign:"center", padding:"60px 20px", color:"#9a8a94" }}>
              <div style={{ fontSize:44, marginBottom:12 }}>{"\uD83C\uDFC8"}</div>
              <div style={{ fontSize:16, fontWeight:700, color:"#c9bdc4" }}>The season hasn't kicked off yet.</div>
              <div style={{ fontSize:13, marginTop:6 }}>Standings post here after Week 1.</div>
            </div>
          ) : (
            <>
              {/* PODIUM — the signature element */}
              {podium.length >= 1 && (
                <div style={{ display:"flex", justifyContent:"center", alignItems:"flex-end", gap:isMobileWidth()?8:16, margin:"10px 0 34px", flexWrap:"nowrap" }}>
                  {/* 2nd */}
                  {podium[1] && <PodiumSpot p={podium[1]} rank={2} Chip={Chip} fmt={fmt} />}
                  {/* 1st */}
                  {podium[0] && <PodiumSpot p={podium[0]} rank={1} Chip={Chip} fmt={fmt} />}
                  {/* 3rd */}
                  {podium[2] && <PodiumSpot p={podium[2]} rank={3} Chip={Chip} fmt={fmt} />}
                </div>
              )}
              {/* the rest */}
              {rest.length > 0 && (
                <div style={{ background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:16, overflow:"hidden" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"52px 1fr repeat(3, minmax(64px, 88px))", gap:4, padding:"12px 16px", fontSize:10.5, fontWeight:800, color:"#7a6a74", textTransform:"uppercase", letterSpacing:0.8, borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                    <span>Rank</span><span>Player</span>
                    <span style={{textAlign:"right"}}>Earned</span>
                    <span style={{textAlign:"right"}}>Spent</span>
                    <span style={{textAlign:"right"}}>Balance</span>
                  </div>
                  {rest.map((p, i) => (
                    <div key={p.name} className="fantasy-row" style={{ display:"grid", gridTemplateColumns:"52px 1fr repeat(3, minmax(64px, 88px))", gap:4, alignItems:"center", padding:"13px 16px", borderBottom:"1px solid rgba(255,255,255,0.04)", fontSize:14, transition:"background 0.12s" }}>
                      <span style={{ fontWeight:900, color:"#7a6a74", fontVariantNumeric:"tabular-nums" }}>{i + 4}</span>
                      <span style={{ fontWeight:700, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.name}</span>
                      <span style={{ textAlign:"right" }}><Chip v={p.earned} size={13} /></span>
                      <span style={{ textAlign:"right", color:"#7a6a74", fontVariantNumeric:"tabular-nums" }}>{fmt(p.redeemed)}</span>
                      <span style={{ textAlign:"right", fontWeight:800, color:"#34d399", fontVariantNumeric:"tabular-nums" }}>{fmt(p.balance)}</span>
                    </div>
                  ))}
                </div>
              )}
              {isAdmin && <AdminWeekEntry panel={cardStyle()} setToast={setToast} nextWeek={(weeks[0]?.week || 0) + 1} />}
            </>
          )
        )}

        {/* ── WEEKLY ── */}
        {view === "weeks" && (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {weeks.length === 0 ? (
              <div style={{ textAlign:"center", padding:"50px", color:"#9a8a94", fontSize:14 }}>No weeks scored yet.</div>
            ) : weeks.map(w => {
              const isOpen = openWeek === w.id;
              const order = w.order || [];
              return (
                <div key={w.id} style={{ background:"rgba(0,0,0,0.3)", border:`1px solid ${isOpen?"rgba(232,49,122,0.3)":"rgba(255,255,255,0.08)"}`, borderRadius:16, overflow:"hidden", transition:"border 0.15s" }}>
                  <div onClick={()=>setOpenWeek(isOpen?null:w.id)} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px 20px", cursor:"pointer" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <span style={{ fontFamily:"'Bangers',cursive", fontSize:26, color:"#E8317A", letterSpacing:1 }}>WK {w.week}</span>
                      {order[0] && <span style={{ fontSize:13, color:"#c9bdc4" }}>{"\uD83D\uDC51"} <strong style={{color:"#fff"}}>{order[0]}</strong></span>}
                    </div>
                    <span style={{ fontSize:12, color:"#9a8a94", fontWeight:700 }}>{order.length} players {isOpen?"\u25B2":"\u25BC"}</span>
                  </div>
                  {isOpen && (
                    <div style={{ borderTop:"1px solid rgba(255,255,255,0.06)" }}>
                      {order.map((name, i) => (
                        <div key={name+i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"11px 20px", borderBottom:"1px solid rgba(255,255,255,0.04)", fontSize:14,
                                                   background: i===0?"rgba(251,191,36,0.05)":"transparent" }}>
                          <span style={{ color:"#fff" }}><strong style={{ color:"#7a6a74", marginRight:12, fontVariantNumeric:"tabular-nums" }}>{i+1}</strong>{i===0?"\uD83D\uDC51 ":""}{name}</span>
                          <Chip v={fantasyBucksForRank(i+1)} size={13} color={fantasyBucksForRank(i+1)>0?"#FBBF24":"#5a4a54"} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── REDEEM ── */}
        {view === "redeem" && (
          <div>
            <div style={{ textAlign:"center", background:"linear-gradient(135deg, rgba(232,49,122,0.12), rgba(124,156,255,0.06))", border:"1px solid rgba(232,49,122,0.3)", borderRadius:20, padding:"34px 24px", marginBottom:20 }}>
              <div style={{ fontSize:52, marginBottom:8 }}>{"\uD83C\uDF81"}</div>
              <div style={{ fontFamily:"'Bangers',cursive", fontSize:38, color:"#fff", letterSpacing:1, lineHeight:1 }}>
                <span style={{color:"#FBBF24"}}>{REDEEM_COST}</span> BUCKS = <span style={{color:"#E8317A"}}>1 PACK</span>
              </div>
              <div style={{ fontSize:14, color:"#c9bdc4", marginTop:12, maxWidth:440, marginLeft:"auto", marginRight:"auto", lineHeight:1.5 }}>
                Every pack costs just 3 Bazooka Bucks. Redeeming spends your balance but <strong style={{color:"#fff"}}>never drops your ranking</strong> — your Earned total holds your spot on the board.
              </div>
            </div>
            {isAdmin ? (
              <AdminRedeem players={players} standings={standings} panel={cardStyle()} setToast={setToast} fmt={fmt} />
            ) : (
              <div style={{ background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:16, padding:"22px 24px", fontSize:14, color:"#c9bdc4", lineHeight:1.7, textAlign:"center" }}>
                Ready to cash in? Message the <strong style={{color:"#fff"}}>Bazooka team</strong> with your name and how many packs you want. They'll process it and ship with your next order. Check your <strong style={{color:"#34d399"}}>Balance</strong> on the standings for what you've got to spend.
              </div>
            )}
          </div>
        )}
      </div>

      {/* footer */}
      <div style={{ textAlign:"center", marginTop:50, fontSize:11, color:"#5a4a54" }}>
        {"\u00A9"} {new Date().getFullYear()} Bazooka Breaks {"\u00B7"} Fantasy is a free game. NFL scoring via DraftKings.
      </div>

      <style>{`.fantasy-row:hover{background:rgba(232,49,122,0.06)!important}`}</style>
    </div>
  );
}

function isMobileWidth() { return typeof window !== "undefined" && window.innerWidth < 640; }
function cardStyle() { return { background:"rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:16, padding:18 }; }

// Podium spot — 1st is tallest/center, gold; 2nd silver; 3rd bronze.
function PodiumSpot({ p, rank, Chip, fmt }) {
  const cfg = {
    1: { h: 132, medal:"\uD83E\uDD47", ring:"#FBBF24", glow:"rgba(251,191,36,0.5)", grad:"linear-gradient(180deg, rgba(251,191,36,0.25), rgba(251,191,36,0.04))" },
    2: { h: 100, medal:"\uD83E\uDD48", ring:"#c9cdd4", glow:"rgba(201,205,212,0.35)", grad:"linear-gradient(180deg, rgba(201,205,212,0.18), rgba(201,205,212,0.03))" },
    3: { h: 82,  medal:"\uD83E\uDD49", ring:"#cd7f32", glow:"rgba(205,127,50,0.35)", grad:"linear-gradient(180deg, rgba(205,127,50,0.18), rgba(205,127,50,0.03))" },
  }[rank];
  const w = isMobileWidth() ? 104 : 150;
  return (
    <div style={{ width:w, display:"flex", flexDirection:"column", alignItems:"center" }}>
      <div style={{ fontSize:rank===1?34:26, marginBottom:6, filter:`drop-shadow(0 0 10px ${cfg.glow})` }}>{cfg.medal}</div>
      <div style={{ fontWeight:800, color:"#fff", fontSize:rank===1?15:13, textAlign:"center", marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", width:"100%", padding:"0 4px", boxSizing:"border-box" }}>{p.name}</div>
      <div style={{ marginBottom:10 }}><Chip v={p.earned} size={rank===1?16:14} /></div>
      <div style={{ width:"100%", height:cfg.h, borderRadius:"12px 12px 0 0", background:cfg.grad, border:`1px solid ${cfg.ring}55`, borderBottom:"none",
                    display:"flex", alignItems:"flex-start", justifyContent:"center", paddingTop:10, position:"relative",
                    boxShadow:`0 -4px 30px ${cfg.glow}` }}>
        <span style={{ fontFamily:"'Bangers',cursive", fontSize:rank===1?40:32, color:cfg.ring, letterSpacing:1, textShadow:`0 0 16px ${cfg.glow}` }}>{rank}</span>
        {rank===1 && <span style={{ position:"absolute", top:-2, left:"50%", transform:"translateX(-50%)", fontSize:11, fontWeight:900, color:"#FBBF24", letterSpacing:1, textTransform:"uppercase" }}></span>}
      </div>
    </div>
  );
}

// Admin: enter a week's finishing order (one name per line, in finish order).
// Awards Bucks by position and folds the totals into the season doc.
function AdminWeekEntry({ panel, setToast, nextWeek }) {
  const [week, setWeek] = React.useState(String(nextWeek || 1));
  const [names, setNames] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => { setWeek(String(nextWeek || 1)); }, [nextWeek]);

  async function submit() {
    const order = names.split("\n").map(s => s.trim()).filter(Boolean);
    if (!order.length) { setToast && setToast("Enter at least one name"); return; }
    const wk = parseInt(week) || 1;
    setBusy(true);
    try {
      // Award map for this week.
      const awarded = {};
      order.forEach((name, i) => { awarded[name] = fantasyBucksForRank(i + 1); });

      // Fold into the season aggregate (Earned only ever goes up).
      const snap = await getDoc(doc(db, "fantasy_season", "current"));
      const cur = snap.exists() ? snap.data() : { players: {}, weeks: [] };
      const players = { ...(cur.players || {}) };
      order.forEach((name, i) => {
        const add = fantasyBucksForRank(i + 1);
        const p = players[name] || { earned: 0, redeemed: 0 };
        players[name] = { earned: Math.round((p.earned + add) * 100) / 100, redeemed: p.redeemed || 0 };
      });
      const weeksList = Array.from(new Set([...(cur.weeks || []), wk])).sort((a, b) => a - b);

      await setDoc(doc(db, "fantasy_weeks", `week_${wk}`), {
        week: wk, order, awarded, enteredAt: new Date().toISOString(),
      });
      await setDoc(doc(db, "fantasy_season", "current"), { players, weeks: weeksList }, { merge: true });
      setToast && setToast(`\u2713 Week ${wk} entered \u2014 ${order.length} players awarded`);
      setNames("");
    } catch (e) {
      setToast && setToast("Couldn't save week \u2014 try again");
    }
    setBusy(false);
  }

  const preview = names.split("\n").map(s => s.trim()).filter(Boolean);

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(251,191,36,0.25)" }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#FBBF24", marginBottom: 10 }}>{"\u2699\uFE0F"} Enter Week Results (admin)</div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>Week</span>
        <input value={week} onChange={e => setWeek(e.target.value)} type="number"
          style={{ width: 70, padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: 13, fontFamily: "inherit" }} />
      </div>
      <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.5)", marginBottom: 6 }}>
        Paste names in finish order from DraftKings — one per line, 1st place first.
      </div>
      <textarea value={names} onChange={e => setNames(e.target.value)} rows={8} placeholder={"1st place name\n2nd place name\n3rd place name\n..."}
        style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: 13, fontFamily: "inherit", resize: "vertical" }} />
      {preview.length > 0 && (
        <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.55)", margin: "8px 0" }}>
          {preview.length} players {"\u00B7"} awards: 1st gets 9, {preview.length >= 2 ? `2\u2013${Math.min(10, preview.length)} get 3` : ""}{preview.length > 10 ? `, 11\u2013${Math.min(20, preview.length)} get 1` : ""}{preview.length > 20 ? `, 21\u2013${Math.min(30, preview.length)} get 0.5` : ""}
        </div>
      )}
      <button onClick={submit} disabled={busy || preview.length === 0}
        style={{ marginTop: 6, padding: "11px 20px", borderRadius: 8, border: "none", fontFamily: "inherit", fontWeight: 900, fontSize: 13,
                 background: preview.length ? "#FBBF24" : "rgba(255,255,255,0.08)", color: preview.length ? "#000" : "rgba(255,255,255,0.35)", cursor: preview.length ? "pointer" : "default" }}>
        {busy ? "Saving\u2026" : `Award Week ${week}`}
      </button>
    </div>
  );
}

// Admin: process a redemption — pick a player, deduct 3 Bucks per pack from
// their Balance (increments Redeemed; Earned untouched so rank is preserved).
function AdminRedeem({ players, standings, panel, setToast, fmt }) {
  const [name, setName] = React.useState("");
  const [packs, setPacks] = React.useState("1");
  const [busy, setBusy] = React.useState(false);

  const sel = name ? standings.find(s => s.name === name) : null;
  const cost = (parseInt(packs) || 0) * REDEEM_COST;
  const canRedeem = sel && cost > 0 && sel.balance >= cost;

  async function doRedeem() {
    if (!canRedeem) return;
    setBusy(true);
    try {
      const snap = await getDoc(doc(db, "fantasy_season", "current"));
      const cur = snap.exists() ? snap.data() : { players: {} };
      const pl = { ...(cur.players || {}) };
      const p = pl[name] || { earned: 0, redeemed: 0 };
      pl[name] = { earned: p.earned || 0, redeemed: Math.round(((p.redeemed || 0) + cost) * 100) / 100 };
      await setDoc(doc(db, "fantasy_season", "current"), { players: pl }, { merge: true });
      await setDoc(doc(db, "fantasy_redemptions", `${name.replace(/[^a-zA-Z0-9]/g, "_")}_${Date.now().toString(36)}`), {
        name, packs: parseInt(packs) || 0, cost, processedAt: new Date().toISOString(),
      });
      setToast && setToast(`\u2713 ${name} redeemed ${packs} pack(s) for ${cost} Bucks`);
      setPacks("1");
    } catch (e) {
      setToast && setToast("Couldn't process redemption");
    }
    setBusy(false);
  }

  return (
    <div style={{ marginTop: 4, paddingTop: 14, borderTop: "1px solid rgba(232,49,122,0.25)" }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#E8317A", marginBottom: 10 }}>{"\u2699\uFE0F"} Process Redemption (admin)</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <select value={name} onChange={e => setName(e.target.value)}
          style={{ flex: 1, minWidth: 160, padding: "9px 10px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: 13, fontFamily: "inherit" }}>
          <option value="">Select player\u2026</option>
          {standings.map(s => <option key={s.name} value={s.name}>{s.name} (bal {fmt(s.balance)})</option>)}
        </select>
        <input value={packs} onChange={e => setPacks(e.target.value)} type="number" min="1"
          style={{ width: 70, padding: "9px 10px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "#fff", fontSize: 13, fontFamily: "inherit" }} />
        <span style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>packs = {"\uD83D\uDCB0"} {cost}</span>
      </div>
      {sel && cost > sel.balance && <div style={{ fontSize: 12, color: "#F87171", marginBottom: 8 }}>Not enough balance ({fmt(sel.balance)} available).</div>}
      <button onClick={doRedeem} disabled={!canRedeem || busy}
        style={{ padding: "11px 20px", borderRadius: 8, border: "none", fontFamily: "inherit", fontWeight: 900, fontSize: 13,
                 background: canRedeem ? "#E8317A" : "rgba(255,255,255,0.08)", color: canRedeem ? "#fff" : "rgba(255,255,255,0.35)", cursor: canRedeem ? "pointer" : "default" }}>
        {busy ? "Processing\u2026" : "Process Redemption"}
      </button>
    </div>
  );
}

function ArenaTab({ user, cards, savedDecks, WEAPON_COLORS, canonWeapon, isMobile, setToast, inp }) {
  // Arena itself is open to every dashboard user, but a few controls (the card-
  // back uploader) stay admin-only. Same check the rest of the app uses.
  const _cardAdmin = (user?.email||"").toLowerCase().endsWith("@bazookabreaks.com");
  const [gameId,    setGameId]    = React.useState(null);
  // CPU games run ENTIRELY in local state, never in Firestore. Two reasons:
  // the CPU has no uid to own a private doc, and more importantly its lineup
  // would have to live somewhere this browser can read — which means devtools
  // can read it too. Keeping it local makes that limitation honest rather than
  // pretending to a secrecy the architecture can't provide. CPU games are
  // practice: they don't persist and don't appear in the recent-games list.
  const [cpu,       setCpu]       = React.useState(null);   // local mirror of `game`
  const [cpuPriv,   setCpuPriv]   = React.useState(null);   // my private doc, local
  const [cpuHidden, setCpuHidden] = React.useState(null);   // CPU's face-down lineup
  const [cpuLevel,  setCpuLevel]  = React.useState("even");
  // Active weapon strike: { zone, weapon, up, key }. Purely cosmetic — cleared on
  // a timer. `key` forces a remount so replaying the same weapon restarts the
  // animation instead of React reusing the finished one.
  const [fx,        setFx]        = React.useState(null);
  // Persistent damage: { [zoneIndex]: weaponThatStruck }. The mark a weapon leaves
  // stays for the rest of the game, so the board reads as a record of what happened
  // rather than resetting to clean cards after every animation.
  const [damage,    setDamage]    = React.useState({});
  // Shuffle ceremony. `shuffleFor` holds the private-doc stamp we last played it
  // for, so it runs once per fresh deal instead of on every snapshot.
  const [shuffleFor, setShuffleFor] = React.useState(null);
  const [showShuffle, setShowShuffle] = React.useState(false);
  // Placement: which card is picked up. { from:"hand"|"zone", index }.
  // Drag-and-drop drives desktop; the same state powers tap-select on touch,
  // where HTML5 drag events don't fire.
  const [held,      setHeld]      = React.useState(null);
  const [overZone,  setOverZone]  = React.useState(null);
  const cardBackUrl = useCardBack();   // BoBA back image, or null -> CSS fallback
  const [backUploading, setBackUploading] = React.useState(false);
  const playmatUrl = usePlaymat();     // arena surface image, or null -> gradient fallback
  const [matUploading, setMatUploading] = React.useState(false);
  const arenaLayout = useArenaLayout();          // saved slot positions (percent)
  const [arrangerOpen, setArrangerOpen] = React.useState(false);
  // Battle spotlight: auto-enlarges the active pair; the player can tap to dismiss
  // back to the full board. Dismissal is per-zone, so advancing to the next battle
  // re-opens the spotlight rather than staying collapsed.
  const [spotlightOff, setSpotlightOff] = React.useState(false);

  // One-time admin action: push a chosen image to the Storage path the whole app
  // reads the card back from. Lives in the arena because that's where backs show
  // and the tab is already admin-gated.
  async function uploadCardBack(file) {
    if (!file) return;
    setBackUploading(true);
    try {
      const r = ref(storage, CARD_BACK_PATH);
      await uploadBytes(r, file, { contentType: file.type || "image/webp",
                                   cacheControl: "public,max-age=86400" });
      const url = await getDownloadURL(r);
      setCardBackUrl(url);                      // push to every card back on screen
      setToast && setToast("\u2713 Card back updated \u2014 shuffle a deck to see it");
    } catch (e) {
      setErr("Couldn't upload the card back: " + (e?.message || e));
    } finally {
      setBackUploading(false);
    }
  }

  // Same idea for the arena playing surface. Admin uploads once; every board picks
  // it up as its background via the usePlaymat() subscription.
  async function uploadPlaymat(file) {
    if (!file) return;
    setMatUploading(true);
    try {
      const r = ref(storage, PLAYMAT_PATH);
      await uploadBytes(r, file, { contentType: file.type || "image/webp",
                                   cacheControl: "public,max-age=86400" });
      const url = await getDownloadURL(r);
      setPlaymatUrl(url);                        // push to every arena board on screen
      setToast && setToast("\u2713 Playmat updated \u2014 start a game to see it");
    } catch (e) {
      setErr("Couldn't upload the playmat: " + (e?.message || e));
    } finally {
      setMatUploading(false);
    }
  }
  const [game,      setGame]      = React.useState(null);   // public doc
  const [myPriv,    setMyPriv]    = React.useState(null);   // my private doc
  const [joinCode,  setJoinCode]  = React.useState("");
  const [pickDeck,  setPickDeck]  = React.useState("");
  // Game mode: rookie (heroes only), substitution (adds Bench + Hot Dogs).
  // Playmaker (adds Plays) comes later; the selector already leaves room for it.
  const [pickMode,  setPickMode]  = React.useState("rookie");
  // Double-Up: optional risk layer (backgammon doubling cube). When on, a coach may
  // "Press" once per game to double that game's match-point value; the opponent
  // accepts (stake doubles) or walks away (concedes the game at the doubled stake).
  const [pickDoubleUp, setPickDoubleUp] = React.useState(false);
  // Which bench slot is armed for a substitution into the current zone.
  const [subOpen,   setSubOpen]   = React.useState(false);
  // Sub animation overlay: { zi, seat, benched } while playing, else null.
  // Driven off commits so BOTH players see the same swap sequence.
  const [subAnim,   setSubAnim]   = React.useState(null);
  const [busy,      setBusy]      = React.useState(false);
  const [err,       setErr]       = React.useState(null);
  const [myGames,   setMyGames]   = React.useState([]);

  const uid = user?.uid;
  const myName = user?.displayName || user?.email?.split("@")[0] || "Player";

  // Keyframes live in a single injected <style>. Injected once per mount rather
  // than shipped in a CSS file because this component is self-contained.
  React.useEffect(() => {
    if (document.getElementById("bz-arena-fx")) return;
    const el = document.createElement("style");
    el.id = "bz-arena-fx";
    el.textContent = ARENA_FX_CSS;
    document.head.appendChild(el);
  }, []);



  // Clear a strike after it plays out, which UNMOUNTS every transient effect.
  //
  // This matters more than it looks: several on-card keyframes use fill-mode
  // `forwards`, so a finished animation holds its last frame forever. If the
  // element is still mounted when that happens, the effect appears frozen on the
  // card. The persistent scar is ArenaDamage's job, not this one — so the window
  // here has to outlast the LONGEST animation (900ms on-card, ~1100ms for the
  // strike layer rings) with margin, then tear everything down.
  React.useEffect(() => {
    if (!fx) return;
    const t = setTimeout(() => setFx(null), 1400);
    return () => clearTimeout(t);
  }, [fx]);

  // ---- card lookup -------------------------------------------------------
  const cardById = React.useMemo(() => {
    const m = {};
    (cards || []).forEach(c => { if (c?.id) m[c.id] = c; });
    return m;
  }, [cards]);

  // Snapshot a card into the game. We freeze power/weapon/art AT GAME START on
  // purpose: an admin editing a card mid-game must not silently change a hero
  // already sitting face-down in a battle zone.
  const snapCard = React.useCallback((id) => {
    const c = cardById[id];
    if (!c) return null;
    return {
      id,
      hero:    c.hero || "Unknown",
      power:   parseFloat(c.power) || 0,
      weapon:  canonWeapon(c.weapon) || "",
      img:     c.imageUrl || "",
      cardNum: c.cardNum != null ? String(c.cardNum) : "",
    };
  }, [cardById, canonWeapon]);

  // Hot Dogs are the energy resource. They're identified in the checklist by a
  // treatment of "hot dog" (any variant — Devin said not to distinguish kinds).
  // We snapshot a shape parallel to heroes so the same rendering works; power
  // and weapon are irrelevant for a Hot Dog and left blank.
  const hotDogPool = React.useMemo(() => {
    return (cards || [])
      .filter(c => /hot ?dog/i.test(c?.treatment || ""))
      .map(c => ({ id: c.id, hero: c.hero || "Hot Dog", img: c.imageUrl || "", isHotDog: true }));
  }, [cards]);

  // Build a 10-card Hot Dog deck by drawing (with replacement if the pool is
  // small) from the pool. A real deck is 10 physical Hot Dogs; if the checklist
  // has fewer distinct ones we repeat, since duplicates are fine as energy.
  const buildHotDogDeck = React.useCallback(() => {
    const pool = hotDogPool;
    if (!pool.length) return [];
    const out = [];
    for (let i = 0; i < 10; i++) {
      const pick = pool[Math.floor(Math.random() * pool.length)];
      // Give each a unique instance id so keys don't collide when repeated.
      out.push({ ...pick, uid: `hd_${i}_${Math.random().toString(36).slice(2,7)}` });
    }
    return out;
  }, [hotDogPool]);

  // ---- deck legality (Rookie: §4.1.1) ------------------------------------
  // 60 cards, no more than 6 sharing a Power value, only 1 copy of each
  // variation. We check against the snapshot, not the raw ids, because two
  // different ids can be the same printed variation.
  function checkLegal(cardIds) {
    const problems = [];
    const snaps = (cardIds || []).map(snapCard);
    const missing = snaps.filter(s => !s).length;
    if (missing) problems.push(`${missing} card${missing>1?"s":""} in this deck no longer exist in the database.`);
    const good = snaps.filter(Boolean);

    if (good.length !== 60) problems.push(`Hero Deck must be exactly 60 cards — this deck has ${good.length}.`);

    const byPower = {};
    good.forEach(s => { const k = String(s.power); byPower[k] = (byPower[k]||0)+1; });
    Object.entries(byPower).forEach(([p,n]) => {
      if (n > 6) problems.push(`${n} Heroes share Power ${p} — the limit is 6.`);
    });

    const seen = {};
    good.forEach(s => { seen[s.id] = (seen[s.id]||0)+1; });
    const dupes = Object.entries(seen).filter(([,n]) => n > 1);
    if (dupes.length) {
      const names = dupes.slice(0,3).map(([id,n]) => `${cardById[id]?.hero||id} ×${n}`).join(", ");
      problems.push(`Only 1 copy of each Hero variation allowed — found ${names}${dupes.length>3?` and ${dupes.length-3} more`:""}.`);
    }
    return { ok: problems.length === 0, problems };
  }

  // ---- shuffle -----------------------------------------------------------
  // Fisher-Yates. §3.4 requires the order be unknown to all players; this runs
  // client-side on your own deck only, so neither player sees the other's.
  function shuffle(arr) {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  // ---- my open games list ------------------------------------------------
  React.useEffect(() => {
    if (!uid) { setMyGames([]); return; }
    const unsub = onSnapshot(collection(db, "boba_games"), snap => {
      const all = snap.docs.map(d => ({ ...d.data(), id: d.id }));
      setMyGames(
        all.filter(g => g.p1?.uid === uid || g.p2?.uid === uid)
           .sort((a,b) => String(b.createdAt||"").localeCompare(String(a.createdAt||"")))
           .slice(0, 8)
      );
    }, e => console.error("games list failed:", e));
    return unsub;
  }, [uid]);

  // ---- subscribe to the active game -------------------------------------
  React.useEffect(() => {
    if (!gameId) { setGame(null); return; }
    const unsub = onSnapshot(doc(db, "boba_games", gameId),
      s => setGame(s.exists() ? { ...s.data(), id: s.id } : null),
      e => { console.error("game sync failed:", e); setErr("Lost sync with the game: " + (e?.message||e)); }
    );
    return unsub;
  }, [gameId]);

  React.useEffect(() => {
    if (!gameId || !uid) { setMyPriv(null); return; }
    const unsub = onSnapshot(doc(db, "boba_games", gameId, "private", uid),
      s => setMyPriv(s.exists() ? s.data() : null),
      e => console.error("private sync failed:", e)
    );
    return unsub;
  }, [gameId, uid]);

  // Play the ceremony when a FRESH deal arrives: 7 in hand, nothing placed yet.
  // Keyed on the private doc's updatedAt so a rematch replays it, but re-renders
  // and placement edits don't.
  React.useEffect(() => {
    const P0 = cpu ? cpuPriv : myPriv;
    if (!P0) return;
    const untouched = (P0.hand || []).length === 7 && !(P0.placed || []).some(Boolean);
    const stamp = P0.updatedAt || (cpu ? "cpu:" + (cpu.gameNumber || 1) : null);
    if (!untouched || !stamp) return;
    if (shuffleFor === stamp) return;
    setShuffleFor(stamp);
    setShowShuffle(true);
  }, [myPriv, cpuPriv, cpu, shuffleFor]);

  // ---- seat helpers ------------------------------------------------------
  // A CPU game substitutes local state for the two Firestore docs. Everything
  // downstream — board render, battle logic, match scoring — reads these two
  // names, so aliasing here is what lets one code path serve both modes.
  const isCpu   = !!cpu;
  const G       = isCpu ? cpu     : game;
  const P       = isCpu ? cpuPriv : myPriv;
  const seat  = !G ? null : (G.p1?.uid === uid ? "p1" : G.p2?.uid === uid ? "p2" : null);
  const foe   = seat === "p1" ? "p2" : "p1";
  const me    = seat ? G[seat] : null;
  const them  = seat ? G[foe]  : null;

  // Re-open the battle spotlight each time the active battle changes or a game ends.
  React.useEffect(() => { setSpotlightOff(false); }, [G?.zoneIndex, G?.status]);

  // NOTE: this effect MUST live below the seat helpers above. Its dependency
  // array references G / isCpu / seat, and a dep array is evaluated immediately
  // when the effect registers — not deferred like the callback body — so having
  // it earlier in the component threw "Cannot access before initialization" the
  // moment the Arena tab mounted.
  // In a LIVE game only the player who pushed the final reveal runs the settle
  // code — the opponent just receives a Firestore update. So BOTH browsers watch
  // for a zone becoming settled and fire the strike locally. Direction is computed
  // from each viewer's own seat, so the two screens show mirrored animations.
  const lastFxZone = React.useRef(null);
  React.useEffect(() => {
    if (isCpu || !G) return;                    // CPU path triggers directly
    const zs = G.zones || [];
    const zi2 = G.zoneIndex;
    const z = zs[zi2];
    if (!z || !z.p1 || !z.p2) return;           // both cards not up yet
    // Wait for the settled result before claiming this zone. `winner` lives inside
    // `zones` while `trophies` is a sibling field, so a snapshot COULD in principle
    // arrive with both heroes present but no winner yet. Marking the zone handled
    // at that point would swallow the animation for this player entirely — so the
    // "already played" stamp is only written once there's a real result to show.
    if (!("winner" in z)) return;
    const stamp = `${G.gameNumber||1}:${zi2}`;
    if (lastFxZone.current === stamp) return;   // already played this one
    lastFxZone.current = stamp;
    if (!z.winner) return;                      // draw — nobody struck
    const w = z[z.winner]?.weapon;
    if (w) {
      setFx({ zone: zi2, weapon: w, up: z.winner === seat, key: Date.now() });
      setDamage(d => ({ ...d, [zi2]: w }));
    }
  }, [G, isCpu, seat]);

  // ---- create ------------------------------------------------------------
  async function createGame() {
    if (!uid) return;
    const deck = (savedDecks||[]).find(d => d.id === pickDeck);
    if (!deck) { setErr("Pick a deck first."); return; }
    const legal = checkLegal(deck.cardIds);
    if (!legal.ok) { setErr(legal.problems.join("\n")); return; }

    setBusy(true); setErr(null);
    try {
      const code = Math.random().toString(36).slice(2, 8).toUpperCase();
      const id   = `game_${Date.now()}_${code}`;
      await setDoc(doc(db, "boba_games", id), {
        id, code, mode: pickMode, status: "waiting",
        p1: { uid, name: myName, deckId: deck.id, deckName: deck.name || "Deck", ready: false },
        p2: null,
        // §4.1.1 — the randomly selected player chooses who goes first and the
        // direction. We randomise the chooser at creation and let them pick at
        // the ready step.
        chooser: null, first: null, direction: null,
        zoneIndex: 0,
        zones: Array.from({length:7}, () => ({ p1: null, p2: null, winner: null })),
        trophies: { p1: 0, p2: 0 },
        // Match-level score across a best-of-3. Individual games reset; this doesn't.
        matchWins: { p1: 0, p2: 0 },
        gameNumber: 1,
        log: [],
        createdAt: new Date().toISOString(),
      });
      await stageMyDeck(id, deck, pickMode);
      setGameId(id);
    } catch(e) {
      console.error("create game failed:", e);
      setErr(String(e?.code||"").includes("permission")
        ? "Permission denied creating the game — the boba_games Firestore rule needs publishing (see the rules block below)."
        : "Couldn't create the game: " + (e?.message||e));
    } finally { setBusy(false); }
  }

  // Shuffle my deck, draw 7, write them to MY private doc only.
  // Substitution mode also draws a 4-hero Bench and builds a 10-card Hot Dog deck.
  async function stageMyDeck(gid, deck, mode) {
    const snaps = (deck.cardIds||[]).map(snapCard).filter(Boolean);
    const shuffled = shuffle(snaps);
    const hand = shuffled.slice(0, 7);      // the 7 that will go into zones
    let rest = shuffled.slice(7);           // remaining Hero Deck
    const priv = {
      uid, deckId: deck.id,
      hand,                 // drawn, not yet placed
      placed: [null,null,null,null,null,null,null],
      deck: rest,
      updatedAt: new Date().toISOString(),
    };
    if (mode === "substitution") {
      // §Substitution — Bench of 4 spare heroes, drawn from the top of the deck
      // after the initial 7. Hot Dog deck of 10; hand starts empty (energy is
      // drawn/tracked as a count via the deck array). Discard for swapped-out heroes.
      priv.bench   = rest.slice(0, 4);
      priv.deck    = rest.slice(4);
      priv.hotdogs = buildHotDogDeck();     // 10 Hot Dogs
      priv.discard = [];
      priv.subUsedThisBattle = false;       // one substitution per battle
    }
    await setDoc(doc(db, "boba_games", gid, "private", uid), priv);
  }

  // Auto-stage each player's deck when a NEW live game begins. The rematch button
  // only re-stages the player who clicks it — so the OTHER player kept last game's
  // hand, which looked exactly like "the deck didn't reshuffle" and left stale Hot
  // Dogs. Keyed on gameNumber so it fires once per game for BOTH players. CPU games
  // stage synchronously in cpuPlayAgain, so they're excluded.
  const stagedForRef = React.useRef(null);
  React.useEffect(() => {
    if (isCpu || !game || !gameId || !uid) return;
    if (game.status !== "setup") return;
    const gnum = game.gameNumber || 1;
    if (gnum <= 1) { stagedForRef.current = gnum; return; }  // game 1 staged by create/join
    if (stagedForRef.current === gnum) return;               // already staged this game
    const deck = (savedDecks||[]).find(d => d.id === me?.deckId);
    if (!deck) return;
    stagedForRef.current = gnum;
    stageMyDeck(gameId, deck, game.mode);
  }, [game?.status, game?.gameNumber, gameId, uid, isCpu]);

  // ---- join --------------------------------------------------------------
  async function joinGame() {
    if (!uid) return;
    const deck = (savedDecks||[]).find(d => d.id === pickDeck);
    if (!deck) { setErr("Pick a deck first."); return; }
    const legal = checkLegal(deck.cardIds);
    if (!legal.ok) { setErr(legal.problems.join("\n")); return; }

    setBusy(true); setErr(null);
    try {
      const code = joinCode.trim().toUpperCase();
      const snap = await getDocs(collection(db, "boba_games"));
      const hit = snap.docs.map(d => ({...d.data(), id:d.id}))
                          .find(g => g.code === code && g.status === "waiting");
      if (!hit)                 throw new Error("No open game with that code.");
      if (hit.p1?.uid === uid)  throw new Error("That's your own game — send the code to your opponent.");
      if (hit.p2)               throw new Error("That game is already full.");

      // Randomly pick the chooser now that both seats are known (§4.1.1).
      const chooser = Math.random() < 0.5 ? "p1" : "p2";
      await updateDoc(doc(db, "boba_games", hit.id), {
        p2: { uid, name: myName, deckId: deck.id, deckName: deck.name || "Deck", ready: false },
        status: "setup",
        chooser,
      });
      await stageMyDeck(hit.id, deck, hit.mode);
      setGameId(hit.id);
    } catch(e) {
      console.error("join failed:", e);
      setErr(e?.message || String(e));
    } finally { setBusy(false); }
  }

  // ---- chooser sets first player + direction (§4.1.1) --------------------
  async function setOrder(first, direction) {
    const patch = { first, direction, zoneIndex: direction === "rtl" ? 6 : 0 };
    if (isCpu) { setCpu(g => ({...g, ...patch})); return; }
    await updateDoc(doc(db, "boba_games", gameId), patch);
  }

  // ---- placing heroes face-down -----------------------------------------
  // Placement writes to MY private doc. The public doc only learns that I'm
  // ready, never what I placed.
  async function placeHero(zoneIdx, handIdx) {
    if (!P) return;
    const placed = (P.placed || []).slice();
    const hand   = (P.hand   || []).slice();
    const card   = hand[handIdx];
    if (!card || placed[zoneIdx]) return;
    placed[zoneIdx] = card;
    hand.splice(handIdx, 1);
    if (isCpu) { setCpuPriv(p => ({...p, placed, hand})); return; }
    await updateDoc(doc(db, "boba_games", gameId, "private", uid), { placed, hand });
  }

  // Move a hero already in a zone to another zone. If the target is occupied the
  // two swap, because that's what you'd do physically with cards on a mat — and
  // making you pull one out first would be a worse version of the same action.
  async function moveZone(fromIdx, toIdx) {
    if (!P || fromIdx === toIdx) return;
    const placed = (P.placed || []).slice();
    const a = placed[fromIdx];
    if (!a) return;
    placed[fromIdx] = placed[toIdx] || null;
    placed[toIdx] = a;
    if (isCpu) { setCpuPriv(p => ({...p, placed})); return; }
    await updateDoc(doc(db, "boba_games", gameId, "private", uid), { placed });
  }

  // Place from hand into a SPECIFIC zone. If that zone is taken, the card there
  // goes back to hand rather than being silently dropped.
  async function placeAt(handIdx, zoneIdx) {
    if (!P) return;
    const placed = (P.placed || []).slice();
    const hand   = (P.hand   || []).slice();
    const card = hand[handIdx];
    if (!card) return;
    const displaced = placed[zoneIdx] || null;
    placed[zoneIdx] = card;
    hand.splice(handIdx, 1);
    if (displaced) hand.push(displaced);
    if (isCpu) { setCpuPriv(p => ({...p, placed, hand})); return; }
    await updateDoc(doc(db, "boba_games", gameId, "private", uid), { placed, hand });
  }

  // One entry point for "the held card lands on zone N", used by both drop and tap.
  function dropOnZone(zoneIdx) {
    if (!held) return;
    if (held.from === "hand") placeAt(held.index, zoneIdx);
    else moveZone(held.index, zoneIdx);
    setHeld(null); setOverZone(null);
  }

  async function unplaceHero(zoneIdx) {
    if (!P) return;
    const placed = (P.placed || []).slice();
    const hand   = (P.hand   || []).slice();
    const card   = placed[zoneIdx];
    if (!card) return;
    placed[zoneIdx] = null;
    hand.push(card);
    if (isCpu) { setCpuPriv(p => ({...p, placed, hand})); return; }
    await updateDoc(doc(db, "boba_games", gameId, "private", uid), { placed, hand });
  }

  // ---- substitution (Substitution mode) ----------------------------------
  // The substitution swap now happens inside commitBattle (below), as part of the
  // sequential commit phase — honors player decides first, opponent responds, then
  // both cards flip. The old "sub anytime before you reveal" path was removed
  // because it let whoever revealed second peek at the first card, inverting the
  // honors advantage the rules give the previous battle's winner.

  // Reset the once-per-battle substitution flag when the battle advances. Called
  // from the same place the zone index moves on.
  async function clearSubFlag() {
    if (G?.mode !== "substitution" || !P?.subUsedThisBattle) return;
    if (isCpu) { setCpuPriv(p => ({ ...p, subUsedThisBattle: false })); return; }
    await updateDoc(doc(db, "boba_games", gameId, "private", uid), { subUsedThisBattle: false });
  }

  async function confirmLineup() {
    const placed = P?.placed || [];
    if (placed.filter(Boolean).length !== 7) { setErr("Place all 7 Heroes first."); return; }
    setErr(null);
    // The CPU is always ready, so locking in your lineup starts the game outright.
    if (isCpu) {
      setCpu(g => ({...g, p1:{...g.p1, ready:true}, status: (g.first && g.direction) ? "playing" : g.status}));
      return;
    }
    const patch = { [`${seat}.ready`]: true };
    // Both ready -> the game is live.
    const otherReady = game?.[foe]?.ready;
    if (otherReady && game?.first && game?.direction) patch.status = "playing";
    await updateDoc(doc(db, "boba_games", gameId), patch);
  }

  // ---- reveal (§4.1.2) ---------------------------------------------------
  // Both players reveal SIMULTANEOUSLY. Each player pushes their own card up
  // into the public doc; the winner is computed once both are present. This
  // ordering matters: if one player could see the other's card before pushing
  // their own, the reveal wouldn't be simultaneous.
  // Fire the winner's weapon at the loser. No-op on a draw: nobody struck.
  // `winner` is a seat ("p1"/"p2"); `up` means the strike travels toward the
  // opponent's row, which is only true when I'm the winner.
  function triggerFx(zoneIdx, z, winner) {
    if (!winner) return;
    const w = z[winner]?.weapon;
    if (!w) return;
    setFx({ zone: zoneIdx, weapon: w, up: winner === seat, key: Date.now() });
    setDamage(d => ({ ...d, [zoneIdx]: w }));   // the scar stays
  }

  // Settle a completed pair. Pulled out of revealMine so the CPU path and the
  // live path score battles with the exact same rules — a second implementation
  // is how the Super tiebreak would end up applying in one mode and not the other.
  function settleZone(z, zi, prevTrophies, prevLog) {
    const a = z.p1.power, b = z.p2.power;
    let winner = a > b ? "p1" : b > a ? "p2" : null;
    let bySuper = false;
    if (!winner) {
      const aS = z.p1.weapon === "Super", bS = z.p2.weapon === "Super";
      if (aS && !bS)      { winner = "p1"; bySuper = true; }
      else if (bS && !aS) { winner = "p2"; bySuper = true; }
    }
    const trophies = { ...(prevTrophies || {p1:0,p2:0}) };
    if (winner) trophies[winner] = (trophies[winner]||0) + 1;
    const log = [ ...(prevLog||[]),
      { zone: zi, p1: z.p1.hero, p1p: a, p2: z.p2.hero, p2p: b, winner, bySuper, at: new Date().toISOString() } ];
    return { winner, bySuper, trophies, log };
  }

  // ---- honors (Substitution mode) ----------------------------------------
  // The player who declares their substitution decision FIRST each battle.
  // Battle 1: the game-start first player. After that: the winner of the
  // previous battle. On a draw, honors stays with whoever held it going in.
  // Returns a seat ("p1"/"p2"). Walks battles in resolution order so draws
  // correctly carry honors forward from the last decisive battle (or the start).
  function honorsSeat() {
    if (!G) return "p1";
    const step = G.direction === "rtl" ? -1 : 1;
    const startZone = G.direction === "rtl" ? 6 : 0;
    let holder = G.first || "p1";           // battle 1 honors
    const zones = G.zones || [];
    // Walk from the first battle up to (but not including) the current zone;
    // each decisive result hands honors to its winner, draws leave it be.
    for (let i = startZone; i !== G.zoneIndex; i += step) {
      if (i < 0 || i > 6) break;
      const w = zones[i]?.winner;
      if (w) holder = w;                    // decisive battle moves honors
      // draw: holder unchanged
    }
    return holder;
  }

  // ---- battle commit phase (Substitution mode) ---------------------------
  // Before either card flips, each player commits "stay" or "sub". The honors
  // player must commit FIRST; the opponent sees whether honors subbed or stayed,
  // then commits in response. Commits are final. Once both are in, reveal fires.
  //
  // The decision is stored per-zone in the PUBLIC doc (commits.{zoneIndex}.{seat})
  // so both clients agree on ordering. Only the DECISION is public — "sub" or
  // "stay" — never the card, which stays in the private doc until the flip.
  async function commitBattle(decision, benchIdx) {
    if (!G || !P || G.mode !== "substitution") return;
    const zi = G.zoneIndex;
    const commits = { ...(G.commits || {}) };
    const zc = { ...(commits[zi] || { p1: null, p2: null }) };
    if (zc[seat]) { setErr("You've already committed this battle."); return; }

    const honors = honorsSeat();
    const iAmHonors = seat === honors;
    const honorsCommitted = !!zc[honors];
    // Enforce order: if I'm NOT honors, honors must have gone first.
    if (!iAmHonors && !honorsCommitted) {
      setErr("Waiting for the honors player to decide first."); return;
    }

    // The commit record. For a sub we ALSO publish the benched hero: subbing
    // reveals which hero you chose not to play. It's safe to make public because
    // it's leaving the game — it goes straight to discard. The incoming hero
    // stays hidden in the private doc until the normal reveal.
    let benchedCard = null;

    // If subbing, perform the swap now (same accounting as substituteHero) and
    // record it so the card is ready at flip time. Costs 2 Hot Dogs.
    if (decision === "sub") {
      const hotdogs = P.hotdogs || [];
      if (hotdogs.length < 2) { setErr("Not enough Hot Dogs — a sub costs 2."); return; }
      const bench = (P.bench || []).slice();
      const benchHero = bench[benchIdx];
      if (!benchHero) { setErr("Pick a Bench hero."); return; }
      const placed = (P.placed || []).slice();
      const outgoing = placed[zi];
      if (!outgoing) { setErr("No hero in this zone."); return; }
      benchedCard = outgoing;            // published below, revealed to opponent
      placed[zi] = benchHero;
      bench.splice(benchIdx, 1);
      const discard = [ ...(P.discard || []), outgoing ];
      const spent = hotdogs.slice(2);
      let deck = (P.deck || []).slice();
      while (bench.length < 4 && deck.length) bench.push(deck.shift());
      const privPatch = { placed, bench, discard, deck, hotdogs: spent,
                          subUsedThisBattle: true, updatedAt: new Date().toISOString() };
      if (isCpu) setCpuPriv(p => ({ ...p, ...privPatch }));
      else await updateDoc(doc(db, "boba_games", gameId, "private", uid), privPatch);
    }

    zc[seat] = decision;         // "stay" | "sub"
    if (decision === "sub") zc[`${seat}Benched`] = benchedCard;   // revealed benched hero
    commits[zi] = zc;
    setErr(null);

    if (isCpu) {
      // vs CPU: the CPU never subs (v1), so it commits "stay" the moment it's
      // its turn. If I'm honors I commit, then the CPU auto-commits "stay".
      setCpu(g => {
        const c2 = { ...(g.commits || {}) };
        const z2 = { ...(c2[zi] || { p1: null, p2: null }) };
        z2[seat] = decision;
        if (decision === "sub") z2[`${seat}Benched`] = benchedCard;
        if (!z2[foe]) z2[foe] = "stay";       // CPU stays
        c2[zi] = z2;
        return { ...g, commits: c2 };
      });
      return;
    }
    await updateDoc(doc(db, "boba_games", gameId), { [`commits.${zi}`]: zc });
  }

  // Both committed? Then the cards may flip. Derived, not stored.
  function bothCommitted() {
    if (G?.mode !== "substitution") return true;   // other modes: no commit gate
    const zc = (G.commits || {})[G?.zoneIndex];
    return !!(zc && zc.p1 && zc.p2);
  }

  async function revealMine() {
    if (!G || !P) return;
    // Substitution mode gates reveal behind the commit phase.
    if (G.mode === "substitution" && !bothCommitted()) {
      setErr("Both players must lock in their sub decision first."); return;
    }
    const zi = G.zoneIndex;
    const zones = (G.zones || []).slice();
    if (zones[zi]?.[seat]) return;                 // already revealed
    const card = P.placed?.[zi];
    if (!card) { setErr("Nothing placed in this zone."); return; }

    // CPU: both cards land at once, since there's no second human to wait for.
    if (isCpu) {
      const cpuCard = cpuHidden?.placed?.[zi];
      if (!cpuCard) { setErr("CPU has nothing in this zone."); return; }
      const z2 = { ...zones[zi], [seat]: card, [foe]: cpuCard };
      const r = settleZone(z2, zi, G.trophies, G.log);
      zones[zi] = { ...z2, winner: r.winner, bySuper: r.bySuper };
      setCpu(g => ({ ...g, zones, trophies: r.trophies, log: r.log }));
      triggerFx(zi, z2, r.winner);
      return;
    }

    const z = { ...zones[zi], [seat]: card };
    zones[zi] = z;

    const patch = { zones };

    // If that completed the pair, settle the battle — shared with the CPU path.
    if (z.p1 && z.p2) {
      const r = settleZone(z, zi, game.trophies, game.log);
      zones[zi] = { ...z, winner: r.winner, bySuper: r.bySuper };
      patch.trophies = r.trophies;
      patch.log = r.log;
      // NOTE: no triggerFx here — the watcher above fires it for BOTH players
      // off the settled zone. Calling it here too would double-play for the
      // person who happened to reveal second.
    }
    await updateDoc(doc(db, "boba_games", gameId), patch);
  }

  // Advance to the next zone in the established direction (§4.1.2).
  // Has someone already won, with battles still unplayed?
  //
  // Four trophies out of seven is a majority and ends it outright. But a lead can
  // also become unassailable BELOW four when draws have burned battles: at 3-1 with
  // one draw, only two battles remain, so the trailing player tops out at 3. Rather
  // than hard-coding "4", this compares each player's trophies against the best the
  // other could still reach — which covers both cases and the draw-heavy ones.
  //
  // Returns "p1" | "p2" | null. Never ends a game that's still live: a 3-3 with one
  // battle left is undecided and must be played.
  // ---- Double-Up (optional doubling-cube layer) -------------------------
  // The "Laundry Phase" is the press window. A coach may Press:
  //   • during setup (after deal, before heroes are placed), or
  //   • before any battle after Battle #1 (i.e. once >=1 battle has resolved and
  //     the upcoming zone hasn't been committed yet).
  // Each coach presses at most once per game; there can't be a second open offer.
  function duBattlesFought(G) {
    const t = G?.trophies || {p1:0,p2:0};
    const draws = (G?.zones||[]).filter(z => z && ("winner" in z) && z.winner === null && z.p1 && z.p2).length;
    return (t.p1||0) + (t.p2||0) + draws;
  }
  // Only the HONORS holder may open a press, and each coach presses at most once
  // per match. The press window is the "Laundry Phase": during setup (after the deal,
  // before Battle 1 placement) the dice-roll winner holds honors; between battles
  // after Battle 1, honors sits with the previous decisive winner.
  function canPressNow(G, seat) {
    if (!G?.doubleUp) return false;
    if (G.press) return false;                       // an offer is already open
    if (G.pressUsed?.[seat]) return false;           // this coach spent their press
    const inWindow = (G.status === "setup" && !!G.first && !!G.direction)
      || (G.status === "playing" && (() => {
           const zc = (G.commits || {})[G.zoneIndex];
           const midBattle = zc && (zc.p1 || zc.p2);
           return duBattlesFought(G) >= 1 && !midBattle;
         })());
    if (!inWindow) return false;
    // Order: the honors holder decides FIRST. The other player may press only after
    // the honors holder has PASSED in this window. `pressPassed` records a pass,
    // keyed by window ("setup" pre-placement, else the zoneIndex).
    const honors = honorsSeat();
    const windowKey = G.status === "setup" ? "setup" : String(G.zoneIndex);
    const passedThisWindow = G.pressPassed && G.pressPassed[windowKey];
    if (seat === honors) return true;
    return passedThisWindow === honors;
  }
  // Honors holder may pass the press turn to the opponent (only before pressing).
  function canPassPress(G, seat) {
    if (!G?.doubleUp || G.press) return false;
    if (G.pressUsed?.[seat]) return false;
    if (honorsSeat() !== seat) return false;
    const windowKey = G.status === "setup" ? "setup" : String(G.zoneIndex);
    if (G.pressPassed && G.pressPassed[windowKey]) return false;
    return canPressNow(G, seat);
  }
  function passPress(seat) {
    const G = isCpu ? cpu : game;
    if (!canPassPress(G, seat)) return;
    const windowKey = G.status === "setup" ? "setup" : String(G.zoneIndex);
    const patch = { pressPassed: { ...(G.pressPassed||{}), [windowKey]: seat } };
    if (isCpu) setCpu(g => ({...g, ...patch}));
    else updateDoc(doc(db, "boba_games", gameId), patch);
  }
  // The honors holder presses: opens an offer the opponent must answer. The stake
  // does NOT change yet — it only doubles if the press is ACCEPTED.
  function pressDouble(seat) {
    const G = isCpu ? cpu : game;
    if (!canPressNow(G, seat)) return;
    const patch = { press: { by: seat, at: new Date().toISOString(), fromStake: (G.stake||1) } };
    if (isCpu) {
      setCpu(g => ({...g, ...patch}));
      if (seat === "p1") setTimeout(() => cpuAnswerPress(), 900);   // CPU answers a human press
    } else {
      updateDoc(doc(db, "boba_games", gameId), patch);
    }
  }
  // Respond to an open press.
  //   ACCEPT -> stake doubles (1→2→4), the presser's one press token is spent, play on.
  //   FOLD   -> the folder concedes; the presser wins the game at the stake AS IT STOOD
  //             BEFORE this press (1 the first time, 2 if folding a press-back). The
  //             presser's token is NOT spent — the game just ends.
  function respondToPress(seat, accept) {
    const G = isCpu ? cpu : game;
    if (!G?.press || G.press.by === seat) return;    // only the offeree answers
    const by = G.press.by;
    if (accept) {
      const patch = {
        stake: (G.stake||1) * 2,
        pressUsed: { ...(G.pressUsed||{p1:false,p2:false}), [by]: true },
        press: null,
      };
      if (isCpu) setCpu(g => ({...g, ...patch}));
      else updateDoc(doc(db, "boba_games", gameId), patch);
    } else {
      // Fold: presser wins at the PRE-press stake, decks reshuffle for the next game.
      const stake = G.press.fromStake || G.stake || 1;
      const mw = { ...(G.matchWins||{p1:0,p2:0}) };
      mw[by] = (mw[by]||0) + stake;
      const patch = {
        press: null, stake,
        status: "finished", result: by, wonStake: stake,
        declined: true, finishedAt: new Date().toISOString(),
      };
      if (isCpu) setCpu(g => ({...g, ...patch, matchWins: mw}));
      else updateDoc(doc(db, "boba_games", gameId), { ...patch, matchWins: mw });
    }
  }
  // CPU confidence: board-trophy lead (CPU is p2) plus deck-power edge.
  function cpuConfidence(G) {
    const t = G?.trophies || {p1:0,p2:0};
    const lead = (t.p2||0) - (t.p1||0);
    const powerEdge = G?.cpuTotal && G?.myTotal ? (G.cpuTotal - G.myTotal) / Math.max(1, G.myTotal) : 0;
    return lead * 0.34 + powerEdge;
  }
  // CPU answers a human's press: accept unless clearly behind (then fold).
  function cpuAnswerPress() {
    setCpu(g => {
      if (!g?.press || g.press.by !== "p1") return g;
      const conf = cpuConfidence(g);
      const accept = conf > -0.5 || Math.random() < 0.25;
      if (accept) {
        return { ...g, stake:(g.stake||1)*2, press:null,
                 pressUsed:{...(g.pressUsed||{p1:false,p2:false}), p1:true} };
      }
      const stake = g.press.fromStake || g.stake || 1;   // fold at pre-press value
      const mw = { ...(g.matchWins||{p1:0,p2:0}) };
      mw.p1 = (mw.p1||0) + stake;
      return { ...g, press:null, stake, status:"finished", result:"p1",
               wonStake:stake, declined:true, matchWins:mw,
               finishedAt:new Date().toISOString() };
    });
  }
  // CPU may open a press on its own turn — only when it holds honors, hasn't pressed,
  // and is confident. This covers both the first press and a later press-back to 4.
  function cpuMaybePress() {
    setCpu(g => {
      if (g.press) return g;
      const honors = (function(){ // inline honors for this state
        return honorsSeat();
      })();
      // If the CPU can press (its window), decide press vs pass.
      if (canPressNow(g, "p2")) {
        const conf = cpuConfidence(g);
        if (conf > 0.5 && Math.random() < 0.6) {
          return { ...g, press: { by:"p2", at:new Date().toISOString(), fromStake:(g.stake||1) } };
        }
        // CPU holds honors but isn't pressing → pass, so the human's window opens.
        if (honors === "p2" && canPassPress(g, "p2")) {
          const windowKey = g.status === "setup" ? "setup" : String(g.zoneIndex);
          return { ...g, pressPassed: { ...(g.pressPassed||{}), [windowKey]: "p2" } };
        }
      }
      return g;
    });
  }

  // During SETUP, the honors holder decides the press first. If that's the CPU, it
  // must act (press or pass) or the human — as the non-honors player — can never
  // get their turn and the Press button stays disabled at the start. This effect
  // fires the CPU's setup decision once order is set and it holds honors.
  React.useEffect(() => {
    if (!isCpu || !cpu?.doubleUp) return;
    if (cpu.status !== "setup") return;
    if (!cpu.first || !cpu.direction) return;          // order not chosen yet
    if (cpu.press) return;                             // an offer is already open
    if (cpu.pressPassed && cpu.pressPassed.setup) return;  // CPU already acted this window
    if (cpu.pressUsed && cpu.pressUsed.p2) return;
    if (honorsSeat() !== "p2") return;                 // only act when CPU holds honors
    const t = setTimeout(() => cpuMaybePress(), 600);
    return () => clearTimeout(t);
  }, [isCpu, cpu?.status, cpu?.first, cpu?.direction, cpu?.press, cpu?.pressPassed, cpu?.pressUsed, cpu?.doubleUp]);

  function clinchedWinner(trophies, zones, zoneIndex, direction) {
    const t = trophies || {p1:0,p2:0};
    const step = direction === "rtl" ? -1 : 1;
    // Battles after the current one, in the established direction.
    let remaining = 0;
    for (let i = zoneIndex + step; i >= 0 && i <= 6; i += step) remaining++;
    const p1 = t.p1||0, p2 = t.p2||0;
    if (p1 > p2 + remaining) return "p1";
    if (p2 > p1 + remaining) return "p2";
    return null;
  }

  async function nextBattle() {
    if (!G) return;
    const step = G.direction === "rtl" ? -1 : 1;
    const next = G.zoneIndex + step;
    const outOfZones = next < 0 || next > 6;
    const t0 = G.trophies || {p1:0,p2:0};
    // Stop as soon as the result can no longer change — playing out dead battles
    // is just clicking through a decided game.
    const clinch = clinchedWinner(t0, G.zones, G.zoneIndex, G.direction);
    const done = outOfZones || !!clinch;

    if (isCpu) {
      if (!done) { setCpu(g => ({...g, zoneIndex: next})); clearSubFlag();
                   // Between battles: CPU may open a Press if it's feeling confident.
                   if (G.doubleUp) setTimeout(() => cpuMaybePress(), 700);
                   return; }
      const res = clinch || (t0.p1 > t0.p2 ? "p1" : t0.p2 > t0.p1 ? "p2" : "tie");
      const mw2 = { ...(G.matchWins || {p1:0,p2:0}) };
      const stake = G.stake || 1;   // Double-Up: an accepted Press makes this game worth 2
      if (res !== "tie") mw2[res] = (mw2[res]||0) + stake;
      setCpu(g => ({...g, status:"finished", result:res, matchWins:mw2, wonStake:stake,
                    clinchedEarly: !!clinch && !outOfZones,
                    finishedAt:new Date().toISOString()}));
      return;
    }
    if (done) {
      const t = game.trophies || {p1:0,p2:0};
      const result = clinch || (t.p1 > t.p2 ? "p1" : t.p2 > t.p1 ? "p2" : "tie");
      // A BoBA match is best of 3, so finishing a GAME isn't finishing the MATCH.
      // matchWins is the running score across games in this match; a tied game
      // advances neither side but still counts as a game played.
      // First to 7: a game is worth its Double-Up stake (1/2/4). Points accumulate
      // across games; the match ends when someone reaches 7 (handled on the finished
      // screen and the rematch gate). A tied game advances neither side.
      const mw = { ...(game.matchWins || {p1:0,p2:0}) };
      const stake = game.doubleUp ? (game.stake || 1) : 1;
      if (result !== "tie") mw[result] = (mw[result]||0) + stake;
      await updateDoc(doc(db, "boba_games", gameId), {
        status: "finished",
        result,
        matchWins: mw,
        wonStake: stake,
        gameNumber: game.gameNumber || 1,
        clinchedEarly: !!clinch && !outOfZones,
        finishedAt: new Date().toISOString(),
      });
    } else {
      await updateDoc(doc(db, "boba_games", gameId), { zoneIndex: next });
      clearSubFlag();
    }
  }

  // ---- rematch -----------------------------------------------------------
  // Both players re-stage from their ORIGINAL saved decks: full reshuffle, new 7,
  // zones and trophies cleared. matchWins carries over — that's what makes it a
  // best-of-3 rather than a fresh match. Either player can start it; the second
  // one to press simply re-stages themselves against the already-reset board.
  //
  // The chooser is re-randomised each game rather than inherited, so going first
  // isn't a standing advantage across the match.
  async function playAgain() {
    if (!game || !uid) return;
    const myDeckId = me?.deckId;
    const deck = (savedDecks||[]).find(d => d.id === myDeckId);
    if (!deck) { setErr("Couldn't find the deck you played with. Exit and start a new game."); return; }
    setBusy(true); setErr(null);
    try {
      // Only reset the shared board once — whoever gets there first.
      if (game.status === "finished") {
        const mw = game.matchWins || {p1:0,p2:0};
        const duTarget = game.doubleUp ? 7 : 2;
        const matchOver = (mw.p1||0) >= duTarget || (mw.p2||0) >= duTarget;
        await updateDoc(doc(db, "boba_games", gameId), {
          status: "setup",
          gameNumber: matchOver ? 1 : (game.gameNumber || 1) + 1,
          matchWins: matchOver ? {p1:0,p2:0} : mw,
          chooser: Math.random() < 0.5 ? "p1" : "p2",
          first: null, direction: null,
          zoneIndex: 0,
          zones: Array.from({length:7}, () => ({ p1: null, p2: null, winner: null })),
          trophies: { p1: 0, p2: 0 },
          sudden: {},
          commits: {},
          result: null,
          // Double-Up resets every game: stake back to 1, both press tokens returned,
          // pass record and any open offer cleared.
          stake: 1, pressUsed: { p1:false, p2:false }, pressPassed: {}, press: null,
          wonStake: 1, declined: false,
          log: [],
          "p1.ready": false,
          "p2.ready": false,
        });
      }
      // Staging is handled by the auto-restage effect (keyed on gameNumber), so
      // BOTH players re-stage when the new game begins — not just whoever clicked.
      setDamage({});   // fresh board, no scars carried over
    } catch(e) {
      console.error("rematch failed:", e);
      setErr("Couldn't start the next game: " + (e?.message||e));
    } finally { setBusy(false); }
  }

  // ---- sudden death (§4.4.3) --------------------------------------------
  // Only reachable on a 3-3 with one draw, etc. Each player flips the top of
  // their remaining Hero Deck; higher Power wins; Super breaks a power tie.
  async function suddenDeath() {
    if (!P) return;
    const deck = (P.deck||[]).slice();
    const top = deck.shift();
    if (!top) { setErr("Your Hero Deck is empty."); return; }
    // CPU flips at the same time — same comparison, Super breaks a power tie.
    if (isCpu) {
      const cdeck = (cpuHidden?.deck||[]).slice();
      const ctop = cdeck.shift();
      if (!ctop) { setErr("CPU's Hero Deck is empty."); return; }
      let w = top.power > ctop.power ? seat : ctop.power > top.power ? foe : null;
      if (!w) {
        const mS = top.weapon === "Super", cS = ctop.weapon === "Super";
        if (mS && !cS) w = seat; else if (cS && !mS) w = foe;
      }
      setCpuPriv(p => ({...p, deck}));
      setCpuHidden(h => ({...h, deck: cdeck}));
      if (w) {
        const mw = { ...(G.matchWins || {p1:0,p2:0}) };
        mw[w] = (mw[w]||0) + 1;
        setCpu(g => ({...g, sudden:{[seat]:top,[foe]:ctop}, result:w, status:"finished", matchWins:mw}));
      } else {
        setCpu(g => ({...g, sudden:{}}));   // still tied, flip again
      }
      return;
    }
    await updateDoc(doc(db, "boba_games", gameId, "private", uid), { deck });
    const sd = { ...(game.sudden || {}) };
    sd[seat] = top;
    const patch = { sudden: sd };
    if (sd.p1 && sd.p2) {
      const a = sd.p1, b = sd.p2;
      let w = a.power > b.power ? "p1" : b.power > a.power ? "p2" : null;
      if (!w) {
        // Super Weapon Type Rule — a Super beats a tie.
        const aS = a.weapon === "Super", bS = b.weapon === "Super";
        if (aS && !bS) w = "p1"; else if (bS && !aS) w = "p2";
      }
      if (w) {
        // Sudden Death decides the GAME, so it feeds the match score the same way
        // a normal finish does — otherwise a game won on a flip wouldn't count
        // toward the best-of-3.
        const mw = { ...(game.matchWins || {p1:0,p2:0}) };
        mw[w] = (mw[w]||0) + 1;
        patch.result = w; patch.status = "finished"; patch.matchWins = mw;
      }
      else   { patch.sudden = {}; }   // still tied — flip again
    }
    await updateDoc(doc(db, "boba_games", gameId), patch);
  }

  // ========================= CPU OPPONENT ==================================
  // Difficulty is RELATIVE to the deck you brought, not a fixed power band, so
  // it scales whether you're testing a 900-power deck or a 1400-power one.
  //   easy  -> ~85% of your total power
  //   even  -> ~100%
  //   hard  -> ~115%
  const CPU_MULT = { easy: 0.85, even: 1.00, hard: 1.15 };

  // Build a legal 60-card CPU deck aimed at a target total power.
  //
  // The 6-per-power-value cap is the thing that makes this non-obvious: it forces
  // any legal 60-card deck across at least 10 distinct power values. So this works
  // in TIERS rather than picking cards one at a time — it starts from the cheapest
  // legal 60 (the achievable floor) and upgrades one card at a time to the highest
  // tier that moves it toward the target.
  //
  // An earlier version picked cards "closest to the target average", which quietly
  // produced the SAME deck at every difficulty: the cap meant it always grabbed six
  // each of the ten power values nearest the pool median, regardless of target.
  // Returns { deck, total, floor, ceiling } so the caller can tell the user when a
  // target simply isn't reachable with the available cards.
  function buildCpuDeck(targetPower, powerCap) {
    // Respect the format's per-hero power cap. Without this, bringing a Spec deck
    // (<=160) would face a CPU fielding 200-power heroes — technically a legal
    // 60-card deck, but not the format you chose to practice.
    const pool = (cards || [])
      .filter(c => c && c.id && (parseFloat(c.power) || 0) > 0)
      .filter(c => !powerCap || (parseFloat(c.power) || 0) <= powerCap)
      .map(c => ({
        id: c.id,
        hero: c.hero || "Unknown",
        power: parseFloat(c.power) || 0,
        weapon: canonWeapon(c.weapon) || "",
        img: c.imageUrl || "",
        cardNum: c.cardNum != null ? String(c.cardNum) : "",
      }));
    if (pool.length < 60) return null;

    // Group by power value. Each value can contribute at most 6 cards (rules), and
    // at most as many distinct variations as actually exist.
    const groups = {};
    for (const c of pool) (groups[c.power] = groups[c.power] || []).push(c);
    const powers = Object.keys(groups).map(Number).sort((a,b)=>a-b);
    const cap = {};
    powers.forEach(p => cap[p] = Math.min(6, groups[p].length));
    if (powers.reduce((s,p)=>s+cap[p],0) < 60) return null;

    // Cheapest legal 60 = the floor. Most expensive legal 60 = the ceiling.
    const counts = {};
    powers.forEach(p => counts[p] = 0);
    let placed = 0;
    for (const p of powers) {
      if (placed >= 60) break;
      const take = Math.min(cap[p], 60 - placed);
      counts[p] = take; placed += take;
    }
    const floor = powers.reduce((s,p)=>s+p*counts[p],0);
    let ceiling = 0, left = 60;
    for (let i = powers.length-1; i >= 0 && left > 0; i--) {
      const t = Math.min(cap[powers[i]], left);
      ceiling += powers[i]*t; left -= t;
    }

    // Upgrade toward the target: move one card between tiers, best improvement first.
    for (let iter = 0; iter < 5000; iter++) {
      const total = powers.reduce((s,p)=>s+p*counts[p],0);
      const gap = targetPower - total;
      if (Math.abs(gap) <= 10) break;
      let best = null;
      if (gap > 0) {
        for (const from of powers) {
          if (!counts[from]) continue;
          for (let i = powers.length-1; i >= 0; i--) {
            const to = powers[i];
            if (to <= from) break;
            if (counts[to] >= cap[to]) continue;
            const after = Math.abs(gap - (to-from));
            if (after < Math.abs(gap) && (!best || after < best.after)) best = {from,to,after};
          }
        }
      } else {
        for (let i = powers.length-1; i >= 0; i--) {
          const from = powers[i];
          if (!counts[from]) continue;
          for (const to of powers) {
            if (to >= from) break;
            if (counts[to] >= cap[to]) continue;
            const after = Math.abs(gap - (to-from));
            if (after < Math.abs(gap) && (!best || after < best.after)) best = {from,to,after};
          }
        }
      }
      if (!best) break;
      counts[best.from]--; counts[best.to]++;
    }

    // Materialise: random distinct variations from each chosen tier.
    const deck = [];
    for (const p of powers) {
      if (!counts[p]) continue;
      deck.push(...shuffle(groups[p]).slice(0, counts[p]));
    }
    if (deck.length !== 60) return null;
    return { deck: shuffle(deck), total: deck.reduce((s,c)=>s+c.power,0), floor, ceiling };
  }

  // Start a practice game against the CPU. Everything lives in local state and
  // uses the SAME doc shape as a live game, so all the board rendering below
  // works without a second code path.
  function startCpuGame() {
    const deck = (savedDecks||[]).find(d => d.id === pickDeck);
    if (!deck) { setErr("Pick a deck first."); return; }
    const legal = checkLegal(deck.cardIds);
    if (!legal.ok) { setErr(legal.problems.join("\n")); return; }

    setBusy(true); setErr(null);
    try {
      const mySnaps = (deck.cardIds||[]).map(snapCard).filter(Boolean);
      const myTotal = mySnaps.reduce((s,c)=>s+c.power, 0);
      const target  = Math.round(myTotal * (CPU_MULT[cpuLevel] || 1));

      const fmt = DECK_FORMATS[deck.deckType || "none"] || {};
      const built = buildCpuDeck(target, fmt.powerCap);
      if (!built) {
        setErr("Not enough cards in the database to build a legal CPU deck.");
        setBusy(false); return;
      }
      const cpuTotal = built.total;
      // The 6-per-power cap puts a hard floor and ceiling on what any legal 60-card
      // deck can total. If the requested difficulty falls outside that range, say so
      // rather than shipping a deck that silently ignores the setting.
      let note = null;
      if (Math.abs(cpuTotal - target) > Math.max(40, target * 0.05)) {
        note = cpuTotal > target
          ? "Card pool floor: the weakest legal 60-card deck is " + Math.round(built.floor)
            + " power, so the CPU is stronger than \u201C" + cpuLevel + "\u201D would normally mean."
          : "Card pool ceiling: the strongest legal 60-card deck is " + Math.round(built.ceiling)
            + " power, below the " + target + " target.";
      }

      // Shuffle and draw for both sides.
      const myShuf  = shuffle(mySnaps);
      const cpuShuf = shuffle(built.deck);
      // CPU places its 7 in random order — no lookahead, no saving bombs for
      // later zones. That's the "luck of the draw" behaviour you asked for.
      const cpuPlaced = shuffle(cpuShuf.slice(0,7));

      const first = Math.random() < 0.5 ? "p1" : "p2";
      setCpu({
        id: "cpu", code: "CPU", status: "setup",
        p1: { uid, name: myName, deckId: deck.id, deckName: deck.name||"Deck", ready:false },
        p2: { uid: "__cpu__", name: `CPU (${cpuLevel})`, deckName: "CPU Deck", ready:true },
        mode: pickMode,
        chooser: "p1",           // you always choose vs the CPU
        first, direction: null,
        zoneIndex: 0,
        zones: Array.from({length:7}, () => ({ p1:null, p2:null, winner:null })),
        trophies: { p1:0, p2:0 },
        matchWins: { p1:0, p2:0 },
        gameNumber: 1,
        // Double-Up state. stake = match points this game is worth (1 or 2).
        // pressUsed = who has spent their one Press. press = an outstanding offer
        // awaiting the opponent's accept/decline: { by, at } or null.
        doubleUp: pickDoubleUp,
        stake: 1,
        pressUsed: { p1:false, p2:false },
        pressPassed: {},
        press: null,
        log: [],
        cpuLevel, cpuTotal, myTotal, cpuNote: note, fmtShort: (fmt.short && fmt.short !== "—") ? fmt.short : null,
        createdAt: new Date().toISOString(),
      });
      const cpuPrivDoc = { uid, deckId: deck.id, hand: myShuf.slice(0,7), placed:[null,null,null,null,null,null,null], deck: myShuf.slice(7), updatedAt: new Date().toISOString() + ":" + Math.random().toString(36).slice(2,7) };
      if (pickMode === "substitution") {
        cpuPrivDoc.bench   = myShuf.slice(7, 11);
        cpuPrivDoc.deck    = myShuf.slice(11);
        cpuPrivDoc.hotdogs = buildHotDogDeck();
        cpuPrivDoc.discard = [];
        cpuPrivDoc.subUsedThisBattle = false;
      }
      setCpuPriv(cpuPrivDoc);
      setCpuHidden({ placed: cpuPlaced, deck: cpuShuf.slice(7) });
      setDamage({});
    } catch(e) {
      console.error("cpu game failed:", e);
      setErr("Couldn't start the CPU game: " + (e?.message||e));
    } finally { setBusy(false); }
  }

  // Rematch vs CPU — rebuilds the CPU deck at the same difficulty so it isn't
  // the identical 60 cards every game, and carries matchWins for the best-of-3.
  function cpuPlayAgain() {
    if (!cpu) return;
    const deck = (savedDecks||[]).find(d => d.id === cpu.p1?.deckId);
    if (!deck) { setErr("Couldn't find your deck."); return; }
    const mySnaps = (deck.cardIds||[]).map(snapCard).filter(Boolean);
    const myTotal = mySnaps.reduce((s,c)=>s+c.power,0);
    const target  = Math.round(myTotal * (CPU_MULT[cpu.cpuLevel] || 1));
    const fmt2 = DECK_FORMATS[deck.deckType || "none"] || {};
    const built = buildCpuDeck(target, fmt2.powerCap);
    if (!built) { setErr("Couldn't rebuild the CPU deck."); return; }
    const cpuDeck = built.deck;

    const mw = cpu.matchWins || {p1:0,p2:0};
    const duTarget = cpu.doubleUp ? 7 : 2;
    const matchOver = (mw.p1||0) >= duTarget || (mw.p2||0) >= duTarget;
    const myShuf  = shuffle(mySnaps);
    const cpuShuf = shuffle(cpuDeck);

    setCpu(g => ({
      ...g,
      status: "setup",
      gameNumber: matchOver ? 1 : (g.gameNumber||1) + 1,
      matchWins: matchOver ? {p1:0,p2:0} : mw,
      first: Math.random() < 0.5 ? "p1" : "p2",
      direction: null,
      zoneIndex: 0,
      zones: Array.from({length:7}, () => ({ p1:null, p2:null, winner:null })),
      trophies: { p1:0, p2:0 },
      stake: 1, pressUsed: { p1:false, p2:false }, pressPassed: {}, press: null,
      sudden: {}, result: null, log: [], commits: {},
      cpuTotal: cpuShuf.reduce((s,c)=>s+c.power,0),
      p1: { ...g.p1, ready:false },
      p2: { ...g.p2, ready:true },
    }));
    const cpuPrivDoc2 = { uid, deckId: deck.id, hand: myShuf.slice(0,7), placed:[null,null,null,null,null,null,null], deck: myShuf.slice(7), updatedAt: new Date().toISOString() + ":" + Math.random().toString(36).slice(2,7) };
    if (cpu.mode === "substitution") {
      cpuPrivDoc2.bench   = myShuf.slice(7, 11);
      cpuPrivDoc2.deck    = myShuf.slice(11);
      cpuPrivDoc2.hotdogs = buildHotDogDeck();
      cpuPrivDoc2.discard = [];
      cpuPrivDoc2.subUsedThisBattle = false;
    }
    setCpuPriv(cpuPrivDoc2);
    setCpuHidden({ placed: shuffle(cpuShuf.slice(0,7)), deck: cpuShuf.slice(7) });
    setDamage({});   // fresh board, no scars carried over
    setErr(null);
  }

  async function leaveGame() { stagedForRef.current = null; setGameId(null); setGame(null); setMyPriv(null); setCpu(null); setCpuPriv(null); setCpuHidden(null); setDamage({}); setFx(null); setErr(null); }

  // Watch commits for a NEW sub (either player) and play the swap animation once.
  // The benched hero is public in the commit, so both clients animate the same
  // thing: outgoing hero flips face-up, slides to discard, incoming drops face-down.
  const seenSubsRef = React.useRef(new Set());
  React.useEffect(() => {
    if (!G || G.mode !== "substitution") return;
    const commits = G.commits || {};
    for (const ziStr of Object.keys(commits)) {
      const zc = commits[ziStr];
      for (const s of ["p1","p2"]) {
        if (zc[s] === "sub" && zc[`${s}Benched`]) {
          const key = `${G.gameNumber||1}:${ziStr}:${s}`;
          if (!seenSubsRef.current.has(key)) {
            seenSubsRef.current.add(key);
            setSubAnim({ zi: Number(ziStr), seat: s, benched: zc[`${s}Benched`] });
            setTimeout(() => setSubAnim(a => (a && a.zi === Number(ziStr) && a.seat === s) ? null : a), 2200);
          }
        }
      }
    }
  }, [G]);

  // vs CPU in Substitution mode: the CPU never subs (v1). If the CPU holds honors
  // this battle, it must commit "stay" FIRST so the human (going second) can then
  // act. This effect fires that auto-commit as soon as a battle needs it.
  React.useEffect(() => {
    if (!isCpu || !cpu || cpu.mode !== "substitution" || cpu.status !== "playing") return;
    const zi = cpu.zoneIndex;
    const zc = (cpu.commits || {})[zi] || { p1:null, p2:null };
    // Compute honors inline (honorsSeat reads G === cpu here).
    const step = cpu.direction === "rtl" ? -1 : 1;
    const startZone = cpu.direction === "rtl" ? 6 : 0;
    let holder = cpu.first || "p1";
    for (let i = startZone; i !== zi; i += step) {
      if (i < 0 || i > 6) break;
      const w = (cpu.zones||[])[i]?.winner;
      if (w) holder = w;
    }
    const cpuSeat = foe;                    // the CPU is the opponent seat
    const cpuHasHonors = holder === cpuSeat;
    // CPU commits "stay" if: it's the CPU's turn to go (it has honors and hasn't
    // committed), OR the human already committed and the CPU still hasn't.
    const humanCommitted = !!zc[seat];
    const needsCpuCommit = !zc[cpuSeat] && (cpuHasHonors || humanCommitted);
    if (needsCpuCommit) {
      setCpu(g => {
        const c2 = { ...(g.commits || {}) };
        const z2 = { ...(c2[zi] || { p1:null, p2:null }) };
        if (!z2[cpuSeat]) z2[cpuSeat] = "stay";
        c2[zi] = z2;
        return { ...g, commits: c2 };
      });
    }
  }, [isCpu, cpu, seat, foe]);

  // ========================= RENDER =======================================
  const cardBack = (label) => (
    <div style={{
      width:"100%", aspectRatio:"5/7", borderRadius:8, overflow:"hidden", position:"relative",
      // Same CSS-background approach the shuffle ceremony uses (which renders
      // correctly). The <img> version failed to show here, so match what works.
      background: cardBackUrl
        ? `#111 center/cover no-repeat url(${JSON.stringify(cardBackUrl)})`
        : CARD_BACK_FALLBACK,
      border:"2px solid #dc2626", display:"flex", alignItems:"center", justifyContent:"center",
      color:"rgba(255,255,255,0.55)", fontSize:10, fontWeight:800, textAlign:"center", padding:4,
    }}>
      {cardBackUrl ? null : label}
    </div>
  );

  // `struck` = the winning weapon landing on THIS card, or null. Cosmetic only.
  const heroCard = (c, dim, struck, fxKey, striking, scar) => {
    if (!c) return null;
    const wc = WEAPON_COLORS?.[c.weapon] || "#666";
    return (
      <div style={{
        width:"100%", aspectRatio:"5/7", borderRadius:8, overflow:"hidden", position:"relative",
        border:`2px solid ${wc}`, background:"var(--bz-s1)", opacity: dim ? 0.55 : 1,
        // Struck cards get hit; the WINNING card lunges forward, so the blow reads as
        // coming from a specific card rather than appearing out of nowhere.
        animation: struck   ? "bzShake 560ms ease-in-out"
                 : striking ? "bzWinnerPulse 520ms ease-out"
                 : undefined,
        boxShadow: striking ? `0 0 26px ${wc}` : undefined,
        zIndex: (struck || striking) ? 5 : undefined,
      }}>
        {c.img
          ? <img src={c.img} alt={c.hero} style={{width:"100%",height:"100%",objectFit:"cover"}} />
          : <div style={{padding:6,fontSize:10,fontWeight:800,color:"#fff"}}>{c.hero}</div>}
        <div style={{position:"absolute",top:3,right:4,background:"rgba(0,0,0,0.8)",color:"#FBBF24",
                     borderRadius:5,padding:"1px 6px",fontSize:12,fontWeight:900}}>{c.power}</div>
        <div style={{position:"absolute",bottom:0,left:0,right:0,background:"rgba(0,0,0,0.82)",
                     padding:"3px 5px",fontSize:9,fontWeight:700,color:"#fff",whiteSpace:"nowrap",
                     overflow:"hidden",textOverflow:"ellipsis"}}>
          {c.hero} <span style={{color:wc}}>{c.weapon}</span>
        </div>
        {/* Lasting mark from whatever last beat this card — persists all game. */}
        {scar && <ArenaDamage w={scar} />}
        {struck && <ArenaOnCardFx key={fxKey} w={struck} />}
      </div>
    );
  };

  const panel = { background:"rgba(0,0,0,0.45)", border:"1px solid rgba(255,255,255,0.12)",
                  borderRadius:12, padding:16, marginBottom:16 };

  // ---------- LOBBY ----------
  if (!G) {
    const legality = pickDeck ? checkLegal((savedDecks||[]).find(d=>d.id===pickDeck)?.cardIds || []) : null;
    return (
      <div style={{maxWidth:900, margin:"0 auto"}}>
        <div style={{...panel, textAlign:"center"}}>
          <div style={{fontSize:24,fontWeight:900,color:"#22C55E",letterSpacing:1}}>THE ARENA</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginTop:4}}>
            {pickMode === "substitution" ? "Substitution Mode" : "Rookie Mode"} {"\u00B7"} 7 Battles {"\u00B7"} Live or CPU
          </div>
        </div>

        <div style={panel}>
          <div style={{fontSize:13,fontWeight:800,color:"#fff",marginBottom:10}}>1. Choose your Hero Deck</div>
          <select value={pickDeck} onChange={e=>{setPickDeck(e.target.value); setErr(null);}}
                  style={{...inp, width:"100%", fontSize:13}}>
            <option value="">— Select a saved deck —</option>
            {(savedDecks||[]).map(d =>
              <option key={d.id} value={d.id}>{d.name} ({d.cardCount || d.cardIds?.length || 0} cards)</option>
            )}
          </select>
          {legality && (
            <div style={{marginTop:10, fontSize:12, color: legality.ok ? "#22C55E" : "#F87171"}}>
              {legality.ok
                ? "\u2713 Legal for " + (pickMode === "substitution" ? "Substitution" : "Rookie") + " Mode."
                : legality.problems.map((p,i) => <div key={i}>{"\u2022 "}{p}</div>)}
            </div>
          )}
        </div>

        {/* Game mode. Rookie = heroes only. Substitution adds a Bench of 4 spare
            heroes and a 10-card Hot Dog deck; spend 2 Hot Dogs to swap a hero
            before it's revealed, once per battle. */}
        <div style={panel}>
          <div style={{fontSize:13,fontWeight:800,color:"#fff",marginBottom:10}}>2. Game mode</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {[
              { id:"rookie", name:"Rookie", desc:"Pure head-to-head. 7 heroes, highest power wins." },
              { id:"substitution", name:"Substitution", desc:"Adds a 4-hero Bench + Hot Dogs. Swap heroes before reveal." },
            ].map(m => {
              const on = pickMode === m.id;
              return (
                <div key={m.id} onClick={()=>setPickMode(m.id)}
                  style={{cursor:"pointer",borderRadius:10,padding:"12px 13px",
                          border: on ? "2px solid #22C55E" : "2px solid rgba(255,255,255,0.12)",
                          background: on ? "rgba(34,197,94,0.10)" : "rgba(255,255,255,0.02)"}}>
                  <div style={{fontSize:13,fontWeight:900,color: on ? "#22C55E" : "#fff"}}>{m.name}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",marginTop:4,lineHeight:1.35}}>{m.desc}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Double-Up — optional risk layer. Off by default; toggling it on arms the
            "Press" (doubling) mechanic for CPU practice games. */}
        <div style={panel}>
          <div onClick={()=>setPickDoubleUp(v=>!v)} style={{display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}>
            <div style={{width:44,height:26,borderRadius:14,padding:3,flexShrink:0,transition:"background 160ms",background:pickDoubleUp?"#E8317A":"rgba(255,255,255,0.14)"}}>
              <div style={{width:20,height:20,borderRadius:10,background:"#fff",transform:pickDoubleUp?"translateX(18px)":"translateX(0)",transition:"transform 160ms"}}/>
            </div>
            <div>
              <div style={{fontSize:13,fontWeight:900,color:pickDoubleUp?"#E8317A":"#fff"}}>⚡ Double-Up {pickDoubleUp?"ON":"OFF"}</div>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",marginTop:2,lineHeight:1.35}}>The honors holder can press once per match to double the game. Accept to play on (2 pts), or press back later to make it 4. Fold and the presser takes the game at its current value.</div>
            </div>
          </div>
        </div>

        <div style={{display:"grid", gridTemplateColumns: isMobile?"1fr":"1fr 1fr", gap:16}}>
          <div style={panel}>
            <div style={{fontSize:13,fontWeight:800,color:"#fff",marginBottom:10}}>2a. Start a game</div>
            <button disabled={busy || !pickDeck} onClick={createGame}
              style={{width:"100%",padding:"12px",borderRadius:9,border:"none",cursor: busy||!pickDeck?"not-allowed":"pointer",
                      background: busy||!pickDeck?"#333":"#22C55E", color:"#000", fontWeight:900, fontSize:14, fontFamily:"inherit"}}>
              {busy ? "Creating\u2026" : "Create Game"}
            </button>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",marginTop:8}}>
              You'll get a 6-character code to send your opponent.
            </div>
          </div>

          <div style={panel}>
            <div style={{fontSize:13,fontWeight:800,color:"#fff",marginBottom:10}}>2b. Join a game</div>
            <input value={joinCode} onChange={e=>setJoinCode(e.target.value.toUpperCase())}
                   placeholder="ABC123" maxLength={6}
                   style={{...inp, width:"100%", fontSize:16, letterSpacing:3, textAlign:"center", fontWeight:900}} />
            <button disabled={busy || !pickDeck || joinCode.length<6} onClick={joinGame}
              style={{width:"100%",marginTop:10,padding:"12px",borderRadius:9,border:"none",
                      cursor: busy||!pickDeck||joinCode.length<6?"not-allowed":"pointer",
                      background: busy||!pickDeck||joinCode.length<6?"#333":"#3B82F6", color:"#fff",
                      fontWeight:900, fontSize:14, fontFamily:"inherit"}}>
              {busy ? "Joining\u2026" : "Join Game"}
            </button>
          </div>
        </div>

        {/* CPU practice. Difficulty is relative to the deck you picked above, so
            "hard" means a deck built ~15% above yours rather than a fixed number. */}
        <div style={panel}>
          <div style={{fontSize:13,fontWeight:800,color:"#fff",marginBottom:4}}>2c. Practice vs CPU</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",marginBottom:12}}>
            The CPU builds a legal 60-card deck scaled to yours. Practice games aren't saved.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:12}}>
            {[["easy","Easy","Weaker deck","\u221215%"],
              ["even","Even","Matched power","\u00B10%"],
              ["hard","Hard","Stronger deck","+15%"]].map(([id,label,sub,pct]) => (
              <button key={id} onClick={()=>setCpuLevel(id)}
                style={{padding:"10px 6px",borderRadius:9,cursor:"pointer",fontFamily:"inherit",
                        border: cpuLevel===id ? "2px solid #22C55E" : "1px solid rgba(255,255,255,0.18)",
                        background: cpuLevel===id ? "rgba(34,197,94,0.14)" : "rgba(255,255,255,0.04)",
                        color:"#fff"}}>
                <div style={{fontSize:13,fontWeight:900}}>{label}</div>
                <div style={{fontSize:9.5,color:"rgba(255,255,255,0.5)",marginTop:2}}>{sub}</div>
                <div style={{fontSize:10,fontWeight:800,color: cpuLevel===id ? "#22C55E":"rgba(255,255,255,0.35)",marginTop:2}}>{pct}</div>
              </button>
            ))}
          </div>
          <button disabled={busy || !pickDeck} onClick={startCpuGame}
            style={{width:"100%",padding:"12px",borderRadius:9,border:"none",
                    cursor: busy||!pickDeck?"not-allowed":"pointer",
                    background: busy||!pickDeck?"#333":"#A855F7", color:"#fff", fontWeight:900, fontSize:14, fontFamily:"inherit"}}>
            {busy ? "Building CPU deck\u2026" : "Play CPU"}
          </button>
        </div>

        {err && <div style={{...panel, borderColor:"#F87171", color:"#F87171", fontSize:12, whiteSpace:"pre-line"}}>{err}</div>}

        {/* Card-back image uploader — ADMIN ONLY. The Arena tab is now open to
            every dashboard user, so this control must gate itself; it writes to a
            shared Storage path that changes the card back for everyone. */}
        {_cardAdmin && (
        <div style={panel}>
          <div style={{fontSize:13,fontWeight:800,color:"#fff",marginBottom:4}}>Card back</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",marginBottom:12}}>
            Used for shuffling and face-down heroes. Upload the BoBA back once; it's stored for everyone.
          </div>
          <div style={{display:"flex",gap:14,alignItems:"center"}}>
            <div style={{width:70,aspectRatio:"5/7",borderRadius:7,overflow:"hidden",flexShrink:0,
                         border:"2px solid #dc2626",
                         background: cardBackUrl ? "var(--bz-s1)" : CARD_BACK_FALLBACK,
                         display:"flex",alignItems:"center",justifyContent:"center"}}>
              {cardBackUrl
                ? <img src={cardBackUrl} alt="current card back"
                       style={{width:"100%",height:"100%",objectFit:"cover"}} />
                : <span style={{fontSize:9,color:"rgba(255,255,255,0.5)",textAlign:"center",padding:4}}>
                    default
                  </span>}
            </div>
            <div>
              <label style={{display:"inline-block",padding:"9px 16px",borderRadius:8,
                             background: backUploading ? "#333" : "#22C55E", color:"#000",
                             fontWeight:900,fontSize:13,cursor: backUploading?"default":"pointer"}}>
                {backUploading ? "Uploading\u2026" : cardBackUrl ? "Replace image" : "Upload back image"}
                <input type="file" accept="image/*" disabled={backUploading}
                       onChange={e => { const f = e.target.files?.[0]; e.target.value=""; uploadCardBack(f); }}
                       style={{display:"none"}} />
              </label>
              <div style={{fontSize:10.5,color:"rgba(255,255,255,0.4)",marginTop:6}}>
                PNG, JPG or WebP {"\u00b7"} 5:7 works best
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Playmat image uploader — ADMIN ONLY, same shared-Storage pattern as the
            card back. Sets the arena playing surface for everyone. */}
        {_cardAdmin && (
        <div style={panel}>
          <div style={{fontSize:13,fontWeight:800,color:"#fff",marginBottom:4}}>Playmat</div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",marginBottom:12}}>
            The arena playing surface. Upload once; it shows behind the board for everyone. A wide (16:9) image works best.
          </div>
          <div style={{display:"flex",gap:14,alignItems:"center"}}>
            <div style={{width:120,aspectRatio:"16/9",borderRadius:7,overflow:"hidden",flexShrink:0,
                         border:"2px solid #E8317A",
                         background: playmatUrl ? "var(--bz-s1)" : "linear-gradient(rgba(10,4,4,0.6),rgba(10,4,4,0.8))",
                         display:"flex",alignItems:"center",justifyContent:"center"}}>
              {playmatUrl
                ? <img src={playmatUrl} alt="current playmat"
                       style={{width:"100%",height:"100%",objectFit:"cover"}} />
                : <span style={{fontSize:9,color:"rgba(255,255,255,0.5)",textAlign:"center",padding:4}}>
                    default (dark)
                  </span>}
            </div>
            <div>
              <label style={{display:"inline-block",padding:"9px 16px",borderRadius:8,
                             background: matUploading ? "#333" : "#E8317A", color:"#fff",
                             fontWeight:900,fontSize:13,cursor: matUploading?"default":"pointer"}}>
                {matUploading ? "Uploading\u2026" : playmatUrl ? "Replace playmat" : "Upload playmat"}
                <input type="file" accept="image/*" disabled={matUploading}
                       onChange={e => { const f = e.target.files?.[0]; e.target.value=""; uploadPlaymat(f); }}
                       style={{display:"none"}} />
              </label>
              <div style={{fontSize:10.5,color:"rgba(255,255,255,0.4)",marginTop:6}}>
                PNG, JPG or WebP {"\u00b7"} 16:9 works best
              </div>
              {playmatUrl && (
                <button onClick={()=>setArrangerOpen(true)}
                  style={{marginTop:10,display:"inline-block",padding:"9px 16px",borderRadius:8,
                          background:"rgba(255,255,255,0.08)",border:"1px solid rgba(232,49,122,0.4)",
                          color:"#fff",fontWeight:900,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
                  {"\u270B"} Arrange board on mat
                </button>
              )}
            </div>
          </div>
        </div>
        )}
        {arrangerOpen && (
          <ArenaArranger playmatUrl={playmatUrl} initial={arenaLayout}
            onClose={()=>setArrangerOpen(false)}
            onSave={(lay)=>{ saveArenaLayout(lay); setArrangerOpen(false); setToast && setToast("\u2713 Board layout saved"); }} />
        )}

        {myGames.length > 0 && (
          <div style={panel}>
            <div style={{fontSize:13,fontWeight:800,color:"#fff",marginBottom:10}}>Recent games</div>
            {myGames.map(g => (
              <div key={g.id} onClick={()=>setGameId(g.id)}
                   style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",
                           borderBottom:"1px solid rgba(255,255,255,0.07)",cursor:"pointer",fontSize:12,color:"#fff"}}>
                <span>
                  <b style={{color:"#22C55E"}}>{g.code}</b>{"  "}
                  {g.p1?.name || "?"} vs {g.p2?.name || "waiting\u2026"}
                </span>
                <span style={{color:"rgba(255,255,255,0.4)"}}>
                  {g.status}{g.status==="finished" && g.result ? ` \u00B7 ${g.result==="tie"?"tie":(g[g.result]?.name||g.result)+" won"}` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- IN GAME ----------
  const zi     = G.zoneIndex;
  const zones  = G.zones || [];
  const t      = G.trophies || {p1:0,p2:0};
  const iAmChooser = G.chooser === seat;
  // Battle NUMBER for a zone index. Battles are numbered in resolution order, so
  // when the chooser picks right-to-left, the rightmost zone is Battle 1 — not
  // zone 7 counting down. The array index never changes; only the label does.
  const battleNo = (i) => G.direction === "rtl" ? 7 - i : i + 1;
  const orderSet   = !!(G.first && G.direction);

  return (
    <div style={{maxWidth:1200, margin:"0 auto",
                 paddingBottom: G.status==="playing" ? (isMobile?110:90) : 0}}>
      {/* Double-Up bar: current stake + Press button during the Laundry Phase. */}
      {G.doubleUp && (G.status==="setup" || G.status==="playing") && (
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap",
                     background: (G.stake||1) > 1 ? "rgba(232,49,122,0.12)" : "rgba(255,255,255,0.04)",
                     border:`1px solid ${(G.stake||1)>1?"#E8317A":"rgba(255,255,255,0.1)"}`,borderRadius:12,padding:"10px 14px",marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:12,fontWeight:900,letterSpacing:0.5,color:(G.stake||1)>1?"#E8317A":"rgba(255,255,255,0.6)"}}>⚡ DOUBLE-UP</span>
            <span style={{fontSize:12,fontWeight:800,color:"#fff"}}>This game: <span style={{color:(G.stake||1)>1?"#E8317A":"#4ade80"}}>{G.stake||1} point{(G.stake||1)>1?"s":""}</span></span>
            {G.pressUsed?.[seat] && <span style={{fontSize:10.5,color:"rgba(255,255,255,0.4)"}}>· your Press spent</span>}
            {!G.pressUsed?.[seat] && honorsSeat()===seat && (G.status==="setup"||G.status==="playing") && <span style={{fontSize:10.5,color:"#FBBF24",fontWeight:700}}>· you hold honors</span>}
          </div>
          {canPressNow(G, seat) && !G.press && (
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>pressDouble(seat)}
                style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",border:"none",color:"#fff",borderRadius:8,padding:"7px 16px",fontSize:12.5,fontWeight:900,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                ⚡ Press (double to {(G.stake||1)*2})
              </button>
              {canPassPress(G, seat) && (
                <button onClick={()=>passPress(seat)}
                  style={{background:"transparent",border:"1px solid rgba(255,255,255,0.25)",color:"rgba(255,255,255,0.6)",borderRadius:8,padding:"7px 14px",fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                  Pass
                </button>
              )}
            </div>
          )}
          {G.press && G.press.by === seat && (
            <span style={{fontSize:11.5,fontWeight:700,color:"#FBBF24"}}>Waiting on {them?.name||"opponent"}…</span>
          )}
        </div>
      )}
      {/* Incoming press — the offeree must accept the doubled stake or walk away. */}
      {G.press && G.press.by !== seat && (
        <div style={{position:"fixed",inset:0,zIndex:14000,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{width:"100%",maxWidth:420,background:"#14141c",border:"1px solid #E8317A55",borderRadius:16,padding:"26px 24px",textAlign:"center"}}>
            <div style={{fontSize:34,marginBottom:6}}>⚡</div>
            <div style={{fontSize:19,fontWeight:900,color:"#E8317A",marginBottom:6}}>{them?.name||"Opponent"} pressed!</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.7)",lineHeight:1.5,marginBottom:20}}>
              Accept to play this game for <strong style={{color:"#fff"}}>{(G.stake||1)*2} points</strong> — or fold and give them the game at <strong style={{color:"#fff"}}>{G.press?.fromStake||G.stake||1} point{(G.press?.fromStake||G.stake||1)>1?"s":""}</strong>.
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>respondToPress(seat,false)}
                style={{flex:1,background:"transparent",border:"1px solid rgba(255,255,255,0.2)",color:"rgba(255,255,255,0.6)",borderRadius:10,padding:"12px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                Fold<br/><span style={{fontSize:10,fontWeight:600,opacity:0.7}}>they get {G.press?.fromStake||G.stake||1} pt{(G.press?.fromStake||G.stake||1)>1?"s":""}</span>
              </button>
              <button onClick={()=>respondToPress(seat,true)}
                style={{flex:1.4,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",border:"none",color:"#fff",borderRadius:10,padding:"12px",fontSize:13,fontWeight:900,cursor:"pointer",fontFamily:"inherit"}}>
                Accept<br/><span style={{fontSize:10,fontWeight:600,opacity:0.85}}>play for {(G.stake||1)*2} pts</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Substitution swap animation, shown to both players when a sub commits. */}
      <SubAnimOverlay anim={subAnim}
        whose={subAnim ? (subAnim.seat === seat ? "YOU" : (them?.name || "OPPONENT").toUpperCase()) : ""}
        cardBackUrl={cardBackUrl} isMobile={isMobile} />
      {/* header */}
      <div style={{...panel, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10}}>
        <div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>{isCpu ? "PRACTICE" : "CODE"}</div>
          <div style={{fontSize:20,fontWeight:900,color: isCpu ? "#A855F7" : "#22C55E",letterSpacing: isCpu?1:3}}>
            {isCpu ? String(G.cpuLevel||"even").toUpperCase() : G.code}
          </div>
          {/* Deck power totals — the whole point of the difficulty setting, so it's
              worth showing what it actually produced rather than trusting the label. */}
          {isCpu && (
            <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",marginTop:2}}>
              you {Math.round(G.myTotal||0)} {"\u00B7"} cpu {Math.round(G.cpuTotal||0)}
              {G.fmtShort && <span style={{marginLeft:6,color:"#A855F7",fontWeight:800}}>{G.fmtShort}</span>}
            </div>
          )}        </div>
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.45)"}}>
            TROPHIES
            <span style={{marginLeft:8,color:"rgba(255,255,255,0.3)"}}>
              {"\u00B7"} GAME {G.gameNumber||1} {"\u00B7"} {G.doubleUp ? "TO 7" : "MATCH"} {(G.matchWins||{})[seat]||0}{"\u2013"}{(G.matchWins||{})[foe]||0}
            </span>
          </div>
          <div style={{fontSize:18,fontWeight:900,color:"#fff"}}>
            <span style={{color:"#3B82F6"}}>{me?.name||"You"} {t[seat]||0}</span>
            {"  \u2014  "}
            <span style={{color:"#F87171"}}>{t[foe]||0} {them?.name||"Opponent"}</span>
          </div>
        </div>
        <button onClick={leaveGame} style={{...inp, cursor:"pointer", fontSize:12, padding:"8px 14px"}}>Exit</button>
      </div>

      {err && <div style={{...panel, borderColor:"#F87171", color:"#F87171", fontSize:12, whiteSpace:"pre-line"}}>{err}</div>}

      {/* Pool-limit note: the difficulty setting couldn't be honoured exactly. */}
      {isCpu && G.cpuNote && (
        <div style={{...panel, borderColor:"rgba(168,85,247,0.5)", color:"#C4B5FD", fontSize:11.5}}>
          {G.cpuNote}
        </div>
      )}

      {/* waiting for opponent */}
      {G.status === "waiting" && (
        <div style={{...panel, textAlign:"center"}}>
          <div style={{fontSize:15,fontWeight:800,color:"#fff"}}>Waiting for an opponent{"…"}</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginTop:6}}>
            Send them the code <b style={{color:"#22C55E"}}>{G.code}</b>.
          </div>
        </div>
      )}

      {/* choose first player + direction */}
      {G.status === "setup" && !orderSet && (
        <div style={{...panel, textAlign:"center"}}>
          {iAmChooser ? (
            <>
              <div style={{fontSize:14,fontWeight:800,color:"#fff",marginBottom:4}}>
                You were randomly selected to choose.
              </div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginBottom:14}}>
                Pick who goes first and which way Battles resolve.
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10}}>
                {[["ltr","Left \u2192 Right"],["rtl","Right \u2192 Left"]].map(([dir,dlabel]) =>
                  [[seat,"Me first"],[foe,"Them first"]].map(([f,flabel]) => (
                    <button key={dir+f} onClick={()=>setOrder(f,dir)}
                      style={{padding:"12px",borderRadius:9,border:"1px solid rgba(255,255,255,0.2)",
                              background:"rgba(255,255,255,0.06)",color:"#fff",fontWeight:800,fontSize:12,
                              cursor:"pointer",fontFamily:"inherit"}}>
                      {flabel} {"\u00B7"} {dlabel}
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            <div style={{fontSize:14,fontWeight:800,color:"#fff"}}>
              {them?.name || "Your opponent"} was randomly selected to choose turn order and direction{"…"}
            </div>
          )}
        </div>
      )}

      {/* lineup placement */}
      {/* Shuffle -> draw -> deal, before you start placing. */}
      {G.status === "setup" && orderSet && !me?.ready && showShuffle && (
        <div style={panel}>
          <ArenaShuffleCeremony
            hand={P?.hand || []}
            isMobile={isMobile}
            onDone={() => setShowShuffle(false)}
          />
        </div>
      )}

      {G.status === "setup" && orderSet && !me?.ready && !showShuffle && (
        <div style={{...panel, paddingBottom: isMobile ? 150 : 180}}>
          <div style={{fontSize:13,fontWeight:800,color:"#fff",marginBottom:4}}>
            Place your 7 Heroes face-down
          </div>
          <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",marginBottom:12}}>
            Battles resolve {G.direction === "rtl" ? "right to left" : "left to right"}.
            {" "}Drag a Hero into any zone {"\u2014"} or tap to pick up, then tap a zone.
            {" "}Drop onto an occupied zone to swap. {(P?.hand||[]).length} left to place.
          </div>
          {/* Zone row. Every zone is a drop target: from hand, or from another zone
              (which swaps). The order you place in is yours to choose — the old
              behaviour filled the leftmost empty slot, which took that decision away. */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:6,marginBottom:14}}>
            {Array.from({length:7}).map((_,i) => {
              const p = P?.placed?.[i];
              const isOver   = overZone === i;
              const isSource = held?.from === "zone" && held.index === i;
              const armed    = !!held;
              return (
                <div key={i}
                  onDragOver={e=>{ e.preventDefault(); setOverZone(i); }}
                  onDragLeave={()=>setOverZone(z => z === i ? null : z)}
                  onDrop={e=>{ e.preventDefault(); dropOnZone(i); }}
                  onClick={()=>{
                    // Tap-to-place for touch, where HTML5 drag events never fire.
                    if (held) { dropOnZone(i); return; }
                    if (p) setHeld({ from:"zone", index:i });
                  }}
                  style={{cursor: (p || held) ? "pointer" : "default"}}>
                  <div style={{fontSize:9,color: isOver ? "#22C55E" : "rgba(255,255,255,0.4)",
                               textAlign:"center",marginBottom:3,fontWeight: isOver?900:400}}>
                    ZONE {battleNo(i)}
                  </div>
                  <div
                    draggable={!!p}
                    onDragStart={e=>{ if(!p) return; setHeld({from:"zone",index:i}); try{e.dataTransfer.effectAllowed="move";e.dataTransfer.setData("text/plain","z"+i);}catch(err){} }}
                    onDragEnd={()=>{ setHeld(null); setOverZone(null); }}
                    style={{
                      borderRadius:8,
                      outline: isOver ? "3px solid #22C55E" : isSource ? "3px solid #FBBF24" : "none",
                      outlineOffset: 2,
                      transform: isOver ? "translateY(-4px)" : "none",
                      transition: "transform 120ms ease, outline-color 120ms ease",
                      opacity: isSource ? 0.5 : 1,
                    }}>
                    {p ? heroCard(p) : (
                      <div style={{width:"100%",aspectRatio:"5/7",borderRadius:8,
                                   border: isOver ? "2px solid #22C55E"
                                         : armed ? "2px dashed rgba(34,197,94,0.55)"
                                         : "2px dashed rgba(255,255,255,0.22)",
                                   background: isOver ? "rgba(34,197,94,0.12)" : "transparent",
                                   display:"flex",alignItems:"center",justifyContent:"center",
                                   color: isOver ? "#22C55E" : "rgba(255,255,255,0.3)",fontSize:20,
                                   transition:"all 120ms ease"}}>+</div>
                    )}
                  </div>
                  {/* Sending a card back to hand shouldn't require a drag. */}
                  {p && !held && (
                    <div onClick={e=>{ e.stopPropagation(); unplaceHero(i); }}
                      style={{textAlign:"center",fontSize:9,fontWeight:800,marginTop:2,
                              color:"rgba(255,255,255,0.35)",cursor:"pointer"}}>
                      remove
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Hand is a fanned bar pinned to the bottom of the screen (below).
              The board above stays put; bottom padding keeps Lock In clear of it. */}
          <FannedHand
            cards={P?.hand || []}
            label="YOUR HAND"
            accent="#FBBF24"
            isMobile={isMobile}
            heldIndex={held?.from === "hand" ? held.index : null}
            onDragStart={(i)=>setHeld({from:"hand",index:i})}
            onPick={(i)=>{ const holding = held?.from==="hand" && held.index===i; if (holding) setHeld(null); else setHeld({from:"hand",index:i}); }}
            renderCard={(c)=>heroCard(c)}
            right={held ? (
              <span style={{fontSize:10,fontWeight:800,color:"#FBBF24"}}>
                holding {"\u2014"} tap a zone to place
                <span onClick={()=>{setHeld(null);setOverZone(null);}}
                      style={{marginLeft:8,color:"rgba(255,255,255,0.45)",cursor:"pointer",textDecoration:"underline"}}>cancel</span>
              </span>
            ) : (
              <span style={{fontSize:10,color:"rgba(255,255,255,0.4)"}}>{(P?.hand||[]).length} to place</span>
            )}
          />
          <button onClick={confirmLineup}
            disabled={(P?.placed||[]).filter(Boolean).length !== 7}
            style={{width:"100%",padding:"12px",borderRadius:9,border:"none",
                    background:(P?.placed||[]).filter(Boolean).length===7?"#22C55E":"#333",
                    color:"#000",fontWeight:900,fontSize:14,
                    cursor:(P?.placed||[]).filter(Boolean).length===7?"pointer":"not-allowed",
                    fontFamily:"inherit"}}>
            Lock In Lineup
          </button>
        </div>
      )}

      {!isCpu && G.status === "setup" && orderSet && me?.ready && (
        <div style={{...panel, textAlign:"center", fontSize:14, fontWeight:800, color:"#fff"}}>
          Lineup locked. Waiting for {them?.name || "your opponent"}{"…"}
        </div>
      )}

      {/* Battle spotlight — auto-enlarges the active pair (opponent card on top,
          zone result in the middle, my card below) so the two heroes in the current
          battle are big and readable. Tap anywhere to dismiss back to the full board;
          it re-opens when the next battle starts. */}
      {G.status === "playing" && !spotlightOff && (() => {
        const zi2 = G.zoneIndex;
        const z = zones[zi2] || {};
        const myC = z[seat];
        const oppC = z[foe];
        const settled = !!(z.p1 && z.p2);
        const resultLabel = !settled ? `BATTLE ${battleNo(zi2)}`
          : z.winner === seat ? "YOU WON" : z.winner === foe ? "YOU LOST" : "DRAW";
        const resultColor = !settled ? "#FBBF24" : z.winner === seat ? "#22C55E" : z.winner === foe ? "#F87171" : "#FBBF24";
        return (
          <div onClick={()=>setSpotlightOff(true)}
            style={{position:"relative",marginBottom:12,borderRadius:14,overflow:"hidden",cursor:"zoom-out",
                    background:"linear-gradient(180deg, rgba(20,8,12,0.6), rgba(8,4,6,0.85))",
                    border:"1px solid rgba(232,49,122,0.3)",padding:"10px 12px"}}>
            <div style={{position:"absolute",top:6,right:10,fontSize:9,fontWeight:800,color:"rgba(255,255,255,0.35)",letterSpacing:1}}>TAP FOR BOARD</div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:isMobile?10:20}}>
              {/* my active hero */}
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <div style={{width:isMobile?"30vw":170,maxWidth:180,aspectRatio:"5/7"}}>
                  {myC ? heroCard(myC, z.winner && z.winner !== seat)
                       : (P?.placed?.[zi2] ? cardBack("PLACED") : cardBack("EMPTY"))}
                </div>
                <div style={{fontSize:9,fontWeight:800,color:"rgba(255,255,255,0.5)",letterSpacing:1}}>{me?.name || "YOU"}</div>
              </div>
              {/* result in the middle */}
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,minWidth:54}}>
                <span style={{fontSize:isMobile?13:15,fontWeight:900,color:resultColor,letterSpacing:0.5,textAlign:"center",textShadow:"0 2px 6px rgba(0,0,0,0.8)"}}>{resultLabel}</span>
                <span style={{fontSize:16,opacity:0.5}}>{"\u2694\uFE0F"}</span>
              </div>
              {/* opponent's active hero */}
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <div style={{width:isMobile?"30vw":170,maxWidth:180,aspectRatio:"5/7"}}>
                  {oppC ? heroCard(oppC, z.winner && z.winner !== foe) : cardBack("FACE\nDOWN")}
                </div>
                <div style={{fontSize:9,fontWeight:800,color:"rgba(255,255,255,0.5)",letterSpacing:1}}>{them?.name || "OPPONENT"}</div>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Show-spotlight hint when dismissed, so it's clear you can re-enlarge. */}
      {G.status === "playing" && spotlightOff && (
        <div style={{textAlign:"center",marginBottom:10}}>
          <button onClick={()=>setSpotlightOff(false)}
            style={{background:"rgba(232,49,122,0.12)",border:"1px solid rgba(232,49,122,0.4)",color:"#E8317A",borderRadius:8,padding:"6px 14px",fontSize:11,fontWeight:800,cursor:"zoom-in",fontFamily:"inherit"}}>
            {"\uD83D\uDD0D"} Zoom in on active battle
          </button>
        </div>
      )}

      {/* THE ARENA */}
      {(G.status === "playing" || G.status === "finished") && (
        <div style={{...panel, position:"relative", overflow:"hidden",
                     ...(playmatUrl ? { background:"#0a0405", border:"1px solid rgba(232,49,122,0.25)" } : {})}}>
          {/* Super is a board-wide shockwave rather than a per-card hit — it is the
              rarest weapon and the tiebreaker, so it should feel like the biggest
              thing that happens in a game. */}
          {/* Board-wide strike for EVERY weapon — the per-card effect alone was too
              small to register on a 7-across grid. */}
          {/* Real particle system. The CSS version couldn't do turbulent embers or
              crystal growth — that needs per-frame maths, not transitions. */}
          <ArenaCanvasFx strike={fx} />
          {fx && <ArenaStrikeLayer key={"sl"+fx.key} w={fx.weapon} up={fx.up} zone={fx.zone} />}
          {fx && fx.weapon === "Super" && (
            <div key={fx.key} className="bz-fx-layer" style={{position:"absolute",inset:0,overflow:"hidden",borderRadius:12}}>
              <div style={{position:"absolute",inset:0,animation:"bzFlash 900ms ease-out forwards",
                background:"radial-gradient(circle at 50% 50%,rgba(255,215,0,0.55),transparent 65%)"}}/>
              {[0,1,2].map(i=>(
                <div key={i} style={{position:"absolute",left:"50%",top:"50%",width:80,height:80,
                  borderRadius:"50%",border:"8px solid #FFD700",
                  animation:`bzRing 1000ms ease-out ${i*160}ms forwards`,
                  boxShadow:"0 0 40px rgba(255,215,0,0.95)"}}/>
              ))}
            </div>
          )}
          {/* When a playmat + saved layout exist, render every slot at its arranged
              position (percent of a 16:9 stage) so cards land on the mat's printed
              slots. Without a mat, fall through to the classic grid below — no
              regression for plain games. Game logic (heroCard/cardBack/FX) is reused
              verbatim; only positioning changes. */}
          {playmatUrl ? (() => {
            const L = arenaLayout || defaultArenaLayout();
            // One positioned slot. `flip` rotates opponent slots 180° to face them.
            const slot = (id, node, flip) => {
              const b = L[id] || { x:0, y:0, w:12 };
              const h = (b.h != null);
              return (
                <div key={id} style={{position:"absolute", left:b.x+"%", top:b.y+"%", width:b.w+"%",
                                      ...(h ? {height:b.h+"%"} : {aspectRatio:"5/7"}),
                                      transform: flip ? "rotate(180deg)" : "none"}}>
                  {node}
                </div>
              );
            };
            const pileNode = (arr, accent, emptyHint) => {
              const n = (arr||[]).length;
              return n > 0 ? (
                <div style={{position:"relative",width:"100%",height:"100%",borderRadius:8,overflow:"hidden",
                             border:`2px solid ${accent}`,
                             background: cardBackUrl ? `#111 center/cover no-repeat url(${JSON.stringify(cardBackUrl)})` : CARD_BACK_FALLBACK}}>
                  <span style={{position:"absolute",bottom:3,right:4,background:"rgba(0,0,0,0.82)",color:accent,borderRadius:5,padding:"1px 6px",fontSize:12,fontWeight:900}}>{n}</span>
                </div>
              ) : (
                <div style={{width:"100%",height:"100%",borderRadius:8,border:`2px dashed ${accent}44`,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",color:`${accent}88`,fontSize:8.5,fontWeight:800,textAlign:"center",padding:4}}>{emptyHint}</div>
              );
            };
            return (
              <>
              {/* OPPONENT'S MAT — the same arranged layout, flipped 180° so it faces
                  them across the table. Shows only THEIR cards (face-down until each
                  battle resolves) and their piles. Your stuff never appears here, and
                  theirs never appears on your mat below. */}
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:800,color:"rgba(255,255,255,0.5)",textAlign:"center",marginBottom:6,letterSpacing:2}}>
                  {them?.name || "OPPONENT"}
                </div>
                <div style={{position:"relative",width:"min(100%, calc(34vh * 16 / 9))",aspectRatio:"16/9",margin:"0 auto",
                             borderRadius:12,overflow:"hidden",
                             background:`linear-gradient(rgba(10,4,4,0.72),rgba(10,4,4,0.82)), #111 center/cover no-repeat url("${playmatUrl}")`}}>
                  {/* opponent battle slots. The mat is NOT rotated — that keeps Battle N
                      in the exact same column as your Battle N below, for ANY arranged
                      layout. Only each card is flipped 180° so the hero faces the
                      opponent across the table. */}
                  {Array.from({length:7}).map((_,i) => {
                    const c = zones[i]?.[foe];
                    const hit = fx && fx.zone === i && fx.up ? fx.weapon : null;
                    const striking = fx && fx.zone === i && !fx.up;
                    const node = (
                      <div style={{position:"relative",width:"100%",height:"100%",transform:"rotate(180deg)"}}>
                        {c ? heroCard(c, zones[i]?.winner && zones[i].winner !== foe, hit, fx?.key, striking, zones[i]?.winner === seat ? damage[i] : null)
                           : cardBack("FACE\nDOWN")}
                        {hit && <div className="bz-fx-layer"><ArenaProjectile key={fx.key} w={fx.weapon} up={true} /></div>}
                      </div>
                    );
                    return slot("my"+i, node, false);   // same column as your zone i
                  })}
                  {/* opponent piles — hidden info, so generic face-down stacks */}
                  {slot("myDeck",     pileNode([1],"#F87171",""), false)}
                  {slot("myPlaybook", pileNode(G.mode==="playmaker"?[1]:[],"#7B9CFF","\u2014"), false)}
                  {slot("myHotdogs",  pileNode(G.mode==="substitution"?[1]:[],"#7CF03A","USED"), false)}
                  {slot("myDiscard",  pileNode([],"#F87171","EMPTY"), false)}
                </div>
              </div>
              {/* YOUR MAT */}
              <div style={{position:"relative",width:"min(100%, calc(34vh * 16 / 9))",aspectRatio:"16/9",margin:"0 auto",borderRadius:12,overflow:"hidden",
                           background:`linear-gradient(rgba(10,4,4,0.72),rgba(10,4,4,0.82)), #111 center/cover no-repeat url("${playmatUrl}")`}}>
                {/* zone-strip numbers (battle result per zone) */}
                {Array.from({length:7}).map((_,i) => {
                  const z = zones[i] || {};
                  const active = i === zi && G.status === "playing";
                  const settled = !!(z.p1 && z.p2);
                  const label = !settled ? `${battleNo(i)}` : z.winner === seat ? "WON" : z.winner === foe ? "LOST" : "DRAW";
                  const color = !settled ? "rgba(255,255,255,0.75)" : z.winner === seat ? "#22C55E" : z.winner === foe ? "#F87171" : "#FBBF24";
                  const node = (
                    <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",
                                 borderRadius:6,fontSize:11,fontWeight:900,color,letterSpacing:0.5,
                                 background: active ? "rgba(34,197,94,0.28)" : "rgba(0,0,0,0.45)",
                                 border: active ? "2px solid #22C55E" : "1px solid rgba(255,255,255,0.15)",backdropFilter:"blur(2px)"}}>{label}</div>
                  );
                  return slot("zone"+i, node, false);
                })}
                {/* my battle slots */}
                {Array.from({length:7}).map((_,i) => {
                  const revealed = zones[i]?.[seat];
                  const mine = P?.placed?.[i];
                  const hit = fx && fx.zone === i && !fx.up ? fx.weapon : null;
                  const striking = fx && fx.zone === i && fx.up;
                  const node = revealed ? (
                    <div style={{position:"relative",width:"100%",height:"100%"}}>
                      {heroCard(revealed, zones[i]?.winner && zones[i].winner !== seat, hit, fx?.key, striking, zones[i]?.winner === foe ? damage[i] : null)}
                      {hit && <div className="bz-fx-layer"><ArenaProjectile key={fx.key} w={fx.weapon} up={false} /></div>}
                    </div>
                  ) : (mine ? cardBack("PLACED") : cardBack("EMPTY"));
                  return slot("my"+i, node, false);
                })}
                {/* my piles */}
                {slot("myDeck",     pileNode(P?.deck,    "#F87171","DRAWN"), false)}
                {slot("myPlaybook", pileNode(P?.plays,   "#7B9CFF","\u2014"), false)}
                {slot("myHotdogs",  pileNode(P?.hotdogs, "#7CF03A","USED"), false)}
                {slot("myDiscard",  pileNode(P?.discard, "#F87171","EMPTY"), false)}
              </div>
              </>
            );
          })() : (<>
          {/* Opponent's half — flipped 180° so it faces THEM across the table, the
              way a real two-player playmat works. Their cards read upside-down to you,
              upright to them. Only the orientation changes; all game logic is intact. */}
          <div style={{transform:"rotate(180deg)"}}>
            <div style={{fontSize:11,fontWeight:800,color:"rgba(255,255,255,0.5)",textAlign:"center",marginBottom:10,letterSpacing:2}}>
              {them?.name || "OPPONENT"}
            </div>
            {/* opponent row */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:6,marginBottom:8}}>
              {Array.from({length:7}).map((_,i) => {
                const c = zones[i]?.[foe];
                const hit = fx && fx.zone === i && fx.up ? fx.weapon : null;
                const striking = fx && fx.zone === i && !fx.up;
                return (
                  <div key={i} style={{position:"relative"}}>
                    {c ? heroCard(c, zones[i]?.winner && zones[i].winner !== foe, hit, fx?.key, striking,
                                  zones[i]?.winner === seat ? damage[i] : null)
                       : cardBack("FACE\nDOWN")}
                    {hit && <div className="bz-fx-layer"><ArenaProjectile key={fx.key} w={fx.weapon} up={true} /></div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Arena title band — echoes the playmat's centre header with sword bookends. */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12,margin:"2px 0 8px"}}>
            <span style={{flex:1,height:2,maxWidth:120,background:"linear-gradient(90deg,transparent,rgba(255,255,255,0.35))"}}/>
            <span style={{fontSize:16,opacity:0.85}}>{"\u2694\uFE0F"}</span>
            <span style={{fontFamily:"'Bangers',system-ui,sans-serif",fontSize:20,fontWeight:900,letterSpacing:2,
                          color:"#7CF03A",textShadow:"0 2px 8px rgba(0,0,0,0.8), 0 0 20px rgba(124,240,58,0.4)",
                          WebkitTextStroke:"1px rgba(0,0,0,0.6)"}}>THE ARENA</span>
            <span style={{fontSize:16,opacity:0.85,transform:"scaleX(-1)"}}>{"\u2694\uFE0F"}</span>
            <span style={{flex:1,height:2,maxWidth:120,background:"linear-gradient(90deg,rgba(255,255,255,0.35),transparent)"}}/>
          </div>

          {/* zone strip */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:6,margin:"10px 0"}}>
            {Array.from({length:7}).map((_,i) => {
              const z = zones[i] || {};
              const active = i === zi && G.status === "playing";
              const settled = !!(z.p1 && z.p2);
              const label = !settled ? `${battleNo(i)}`
                          : z.winner === seat ? "WON"
                          : z.winner === foe  ? "LOST" : "DRAW";
              const color = !settled ? "rgba(255,255,255,0.55)"
                          : z.winner === seat ? "#22C55E"
                          : z.winner === foe  ? "#F87171" : "#FBBF24";
              return (
                <div key={i} style={{
                  textAlign:"center", padding:"6px 2px", borderRadius:7, fontSize:10, fontWeight:900,
                  color, letterSpacing:0.5,
                  background: active ? "rgba(34,197,94,0.22)" : "rgba(0,0,0,0.35)",
                  border: active ? "2px solid #22C55E" : "1px solid rgba(255,255,255,0.12)",
                  backdropFilter:"blur(2px)",
                }}>{label}</div>
              );
            })}
          </div>

          {/* my row */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:6,marginTop:8}}>
            {Array.from({length:7}).map((_,i) => {
              const revealed = zones[i]?.[seat];
              const mine     = P?.placed?.[i];
              // My card is struck when the strike travels downward (opponent won).
              const hit = fx && fx.zone === i && !fx.up ? fx.weapon : null;
              const striking = fx && fx.zone === i && fx.up;    // I struck upward
              if (revealed) return (
                <div key={i} style={{position:"relative"}}>
                  {heroCard(revealed, zones[i]?.winner && zones[i].winner !== seat, hit, fx?.key, striking,
                            zones[i]?.winner === foe ? damage[i] : null)}
                  {hit && <div className="bz-fx-layer"><ArenaProjectile key={fx.key} w={fx.weapon} up={false} /></div>}
                </div>
              );
              // Once I place a hero it's face-down to ME too. Knowing my own lineup
              // before reveal defeats the memory element of the game — I have to
              // remember which hero went where, same as the physical game.
              return <div key={i}>{mine ? cardBack("PLACED") : cardBack("EMPTY")}</div>;
            })}
          </div>
          <div style={{fontSize:11,fontWeight:800,color:"rgba(255,255,255,0.5)",textAlign:"center",marginTop:10,letterSpacing:2}}>
            {me?.name || "YOU"}
          </div>

          {/* Live piles on the mat — the four labelled zones from the playmat, now
              real stacks driven by my private doc. A pile shows a face-down top card
              (or an empty slot) with a live count. Playbook only carries cards in
              Playmaker mode; it shows 0 in Rookie/Substitution. */}
          {(() => {
            const pile = (label, arr, accent, emptyHint) => {
              const n = (arr || []).length;
              return (
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5,minWidth:0}}>
                  <div style={{width:"100%",maxWidth:74,aspectRatio:"5/7",borderRadius:8,position:"relative"}}>
                    {n > 0 ? (
                      <>
                        {/* stacked shadow cards behind the top for depth */}
                        {n > 1 && <div style={{position:"absolute",inset:0,transform:"translate(3px,3px)",borderRadius:8,background:"var(--bz-bg)",border:`1px solid ${accent}55`}}/>}
                        {n > 2 && <div style={{position:"absolute",inset:0,transform:"translate(6px,6px)",borderRadius:8,background:"#080808",border:`1px solid ${accent}33`}}/>}
                        <div style={{position:"relative",width:"100%",height:"100%",borderRadius:8,overflow:"hidden",
                                     border:`2px solid ${accent}`,
                                     background: cardBackUrl ? `#111 center/cover no-repeat url(${JSON.stringify(cardBackUrl)})` : CARD_BACK_FALLBACK}}>
                          <span style={{position:"absolute",bottom:3,right:4,background:"rgba(0,0,0,0.82)",color:accent,
                                        borderRadius:5,padding:"1px 6px",fontSize:12,fontWeight:900}}>{n}</span>
                        </div>
                      </>
                    ) : (
                      <div style={{width:"100%",height:"100%",borderRadius:8,border:`2px dashed ${accent}44`,
                                   background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",
                                   color:`${accent}88`,fontSize:8.5,fontWeight:800,textAlign:"center",padding:4}}>
                        {emptyHint || "EMPTY"}
                      </div>
                    )}
                  </div>
                  <span style={{fontSize:8.5,fontWeight:900,letterSpacing:0.8,color:"rgba(255,255,255,0.7)",textAlign:"center",lineHeight:1.2}}>{label}</span>
                </div>
              );
            };
            return (
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,maxWidth:360,margin:"16px auto 4px",
                           paddingTop:14,borderTop:"1px solid rgba(255,255,255,0.08)"}}>
                {pile("HERO DECK", P?.deck, "#F87171", "DRAWN")}
                {pile("PLAYBOOK", P?.plays, "#7B9CFF", "\u2014")}
                {pile("HOT DOGS", P?.hotdogs, "#7CF03A", "USED")}
                {pile("DISCARD", P?.discard, "#F87171", "EMPTY")}
              </div>
            );
          })()}
          </>)}
        </div>
      )}

      {/* Bench as a fanned bar pinned to the bottom, mirroring the placement hand.
          Always reachable during a substitution game without scrolling. During
          the actual sub pick, clicking a bench card commits the sub (handled by
          the commit panel); here the fan is the always-on view of what's benched. */}
      {G.status === "playing" && G.mode === "substitution" && (P?.bench||[]).length > 0 && (
        <FannedHand
          cards={P?.bench || []}
          label="YOUR BENCH"
          accent="#FBBF24"
          isMobile={isMobile}
          heldIndex={null}
          onPick={()=>{}}
          renderCard={(b)=>(
            <div style={{width:"100%",aspectRatio:"5/7",borderRadius:8,overflow:"hidden",
                         border:"2px solid rgba(255,255,255,0.18)",background:"var(--bz-s1)",position:"relative"}}>
              {b.img
                ? <img src={b.img} alt={b.hero} style={{width:"100%",height:"100%",objectFit:"cover"}} />
                : <div style={{padding:6,fontSize:9,fontWeight:800,color:"#fff"}}>{b.hero}</div>}
              <div style={{position:"absolute",top:3,right:4,background:"rgba(0,0,0,0.8)",color:"#FBBF24",
                           borderRadius:4,padding:"0 5px",fontSize:11,fontWeight:900}}>{b.power}</div>
            </div>
          )}
          right={(
            <span style={{fontSize:11,fontWeight:800,color:"rgba(255,255,255,0.7)"}}>
              {"\uD83C\uDF2D"} {(P?.hotdogs||[]).length}
              {"  \u00B7  "}{"\uD83D\uDDD1"} {(P?.discard||[]).length}
            </span>
          )}
        />
      )}

      {G.status === "playing" && (
        <div style={{...panel, textAlign:"center"}}>
          {!zones[zi]?.[seat] ? (
            <>
              <div style={{fontSize:14,fontWeight:800,color:"#fff",marginBottom:10}}>
                Battle {battleNo(zi)} {"\u2014"} {G.mode === "substitution" && !bothCommitted() ? "sub decision" : "reveal your Hero"}
              </div>

              {/* Substitution commit phase. Honors player decides first (stay or
                  sub); the opponent sees THAT decision, then responds. Neither
                  card flips until both have committed. */}
              {G.mode === "substitution" && !bothCommitted() && (() => {
                const zc = (G.commits || {})[zi] || { p1:null, p2:null };
                const honors = honorsSeat();
                const iAmHonors = seat === honors;
                const iCommitted = !!zc[seat];
                const oppCommitted = !!zc[foe];
                const honorsDone = !!zc[honors];
                const bench = P?.bench || [];
                const hotdogs = P?.hotdogs || [];
                const canAfford = hotdogs.length >= 2;
                const placed = P?.placed?.[zi];
                const canSub = canAfford && bench.length && placed;
                // My turn to act? Honors always may; non-honors only after honors commits.
                const myTurn = !iCommitted && (iAmHonors || honorsDone);

                return (
                  <div style={{marginBottom:14,padding:"12px 14px",borderRadius:10,
                               border:"1px solid rgba(251,191,36,0.35)",background:"rgba(251,191,36,0.06)"}}>
                    <div style={{fontSize:11,fontWeight:800,color:"#FBBF24",marginBottom:8,letterSpacing:0.5}}>
                      {iAmHonors ? "\u2605 YOU HAVE HONORS \u2014 decide first" : "You go second this battle"}
                      {"  \u00B7  "}{"\uD83C\uDF2D"} {hotdogs.length}
                    </div>

                    {/* What the opponent has declared, once they have. This is the
                        second-player advantage: you see stay/sub before you choose. */}
                    {!iAmHonors && (
                      <div style={{fontSize:12,marginBottom:10,padding:"7px 10px",borderRadius:7,
                                   background:"rgba(255,255,255,0.04)",
                                   color: honorsDone ? "#fff" : "rgba(255,255,255,0.4)"}}>
                        {honorsDone
                          ? (zc[honors] === "sub"
                              ? "\uD83D\uDD01 Opponent SUBBED \u2014 they likely brought in power."
                              : "\u2713 Opponent STAYED with their placed hero.")
                          : "Waiting for opponent to decide\u2026"}
                      </div>
                    )}

                    {iCommitted ? (
                      <div style={{fontSize:12,fontWeight:700,color:"#22C55E"}}>
                        {"\u2713 You "}{zc[seat] === "sub" ? "substituted" : "stayed"}.
                        {" "}{oppCommitted ? "Both locked in \u2014 reveal below." : "Waiting for opponent\u2026"}
                      </div>
                    ) : myTurn ? (
                      <div>
                        {!subOpen ? (
                          <div style={{display:"flex",gap:8}}>
                            <button onClick={()=>commitBattle("stay")}
                              style={{flex:1,padding:"11px",borderRadius:8,border:"none",background:"#22C55E",
                                      color:"#000",fontWeight:900,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
                              Stay
                            </button>
                            <button onClick={()=> canSub ? setSubOpen(true) : setErr(canAfford ? "Bench is empty." : "Need 2 Hot Dogs to sub.")}
                              disabled={!canSub}
                              style={{flex:1,padding:"11px",borderRadius:8,border:"none",
                                      background: canSub ? "#FBBF24" : "#333",
                                      color: canSub ? "#000" : "rgba(255,255,255,0.4)",
                                      fontWeight:900,fontSize:13,cursor: canSub?"pointer":"not-allowed",fontFamily:"inherit"}}>
                              Substitute (2 {"\uD83C\uDF2D"})
                            </button>
                          </div>
                        ) : (
                          <div>
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.6)",marginBottom:6}}>
                              Bring in for {placed?.hero} (discarded). This locks your decision:
                            </div>
                            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
                              {bench.map((b,bi) => (
                                <div key={b.id+bi}
                                  onClick={()=>{ commitBattle("sub", bi); setSubOpen(false); }}
                                  style={{cursor:"pointer",borderRadius:6,overflow:"hidden",
                                          border:"2px solid rgba(251,191,36,0.4)",background:"var(--bz-s1)",position:"relative"}}>
                                  {b.img
                                    ? <img src={b.img} alt={b.hero} style={{width:"100%",aspectRatio:"5/7",objectFit:"cover",display:"block"}} />
                                    : <div style={{padding:6,fontSize:9,fontWeight:800,color:"#fff"}}>{b.hero}</div>}
                                  <div style={{position:"absolute",top:2,right:3,background:"rgba(0,0,0,0.8)",color:"#FBBF24",
                                               borderRadius:4,padding:"0 4px",fontSize:10,fontWeight:900}}>{b.power}</div>
                                </div>
                              ))}
                            </div>
                            <button onClick={()=>setSubOpen(false)}
                              style={{marginTop:8,padding:"6px 12px",borderRadius:7,border:"1px solid rgba(255,255,255,0.2)",
                                      background:"transparent",color:"rgba(255,255,255,0.6)",
                                      fontWeight:800,fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>
                              Back
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.5)"}}>
                        Waiting for the honors player to decide first{"\u2026"}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Reveal only unlocks once the commit phase is done (or in Rookie). */}
              {bothCommitted() && (
                <button onClick={revealMine}
                  style={{padding:"14px 40px",borderRadius:9,border:"none",background:"#22C55E",
                          color:"#000",fontWeight:900,fontSize:15,cursor:"pointer",fontFamily:"inherit"}}>
                  REVEAL
                </button>
              )}
            </>
          ) : !zones[zi]?.[foe] ? (
            <div style={{fontSize:14,fontWeight:800,color:"#fff"}}>
              Revealed. Waiting for {them?.name || "opponent"}{"…"}
            </div>
          ) : (
            <>
              <div style={{fontSize:16,fontWeight:900,marginBottom:10,
                           color: zones[zi].winner === seat ? "#22C55E"
                                : zones[zi].winner === foe  ? "#F87171" : "#FBBF24"}}>
                {zones[zi].winner === seat ? "You win this battle!"
                 : zones[zi].winner === foe ? "Opponent takes this one."
                 : "Draw \u2014 no trophy."}
                <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.6)",marginTop:4}}>
                  {zones[zi][seat].hero} ({zones[zi][seat].power}) vs {zones[zi][foe].hero} ({zones[zi][foe].power})
                </div>
                {/* A Super winning an equal-Power battle looks like a bug unless it says so. */}
                {zones[zi].bySuper && (
                  <div style={{fontSize:11,fontWeight:800,color:"#FBBF24",marginTop:3,letterSpacing:0.5}}>
                    Power tied {"\u2014"} Super Weapon wins
                  </div>
                )}
              </div>
              <button onClick={nextBattle}
                style={{padding:"12px 34px",borderRadius:9,border:"none",background:"#3B82F6",
                        color:"#fff",fontWeight:900,fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>
                {/* Once the result is locked in, stop offering another battle. */}
                {(clinchedWinner(G.trophies, G.zones, zi, G.direction)
                  || (G.direction==="rtl" ? zi-1 < 0 : zi+1 > 6)) ? "Finish Game" : "Next Battle"}
              </button>
            </>
          )}
        </div>
      )}

      {/* FLOATING ACTION BAR — the current primary action, pinned to the bottom of
          the screen so you never scroll to reveal, advance, or press. Mirrors the
          inline handlers; the detailed sub/bench UI stays inline above. */}
      {G.status === "playing" && (() => {
        const revealed = !!zones[zi]?.[seat];
        const oppRevealed = !!zones[zi]?.[foe];
        const settled = revealed && oppRevealed;
        const showReveal = !revealed && bothCommitted();
        const showNext = settled;
        const canPress = canPressNow(G, seat) && !G.press;
        const finishing = clinchedWinner(G.trophies, G.zones, zi, G.direction)
          || (G.direction==="rtl" ? zi-1 < 0 : zi+1 > 6);
        if (!showReveal && !showNext && !canPress && !(G.press && G.press.by !== seat)) return null;
        return (
          <div style={{position:"fixed",left:0,right:0,bottom:0,zIndex:9000,
                       display:"flex",gap:8,justifyContent:"center",alignItems:"center",flexWrap:"wrap",
                       padding:"10px 12px calc(10px + env(safe-area-inset-bottom))",
                       background:"linear-gradient(180deg, rgba(8,4,6,0), rgba(8,4,6,0.92) 40%)"}}>
            {canPress && (
              <>
                <button onClick={()=>pressDouble(seat)}
                  style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",border:"none",color:"#fff",borderRadius:10,padding:"13px 20px",fontSize:14,fontWeight:900,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 16px rgba(232,49,122,0.4)"}}>
                  ⚡ Press (to {(G.stake||1)*2})
                </button>
                {canPassPress(G, seat) && (
                  <button onClick={()=>passPress(seat)}
                    style={{background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.25)",color:"#fff",borderRadius:10,padding:"13px 18px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit",backdropFilter:"blur(6px)"}}>
                    Pass
                  </button>
                )}
              </>
            )}
            {G.press && G.press.by !== seat && (
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>respondToPress(seat, true)}
                  style={{background:"#22C55E",border:"none",color:"#000",borderRadius:10,padding:"13px 20px",fontSize:14,fontWeight:900,cursor:"pointer",fontFamily:"inherit"}}>
                  Accept (stake {(G.stake||1)*2})
                </button>
                <button onClick={()=>respondToPress(seat, false)}
                  style={{background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.25)",color:"#fff",borderRadius:10,padding:"13px 18px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                  Fold
                </button>
              </div>
            )}
            {showReveal && !canPress && !G.press && (
              <button onClick={revealMine}
                style={{background:"#22C55E",border:"none",color:"#000",borderRadius:10,padding:"14px 32px",fontSize:15,fontWeight:900,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 16px rgba(34,197,94,0.35)"}}>
                REVEAL
              </button>
            )}
            {showNext && (
              <button onClick={nextBattle}
                style={{background:"#3B82F6",border:"none",color:"#fff",borderRadius:10,padding:"14px 32px",fontSize:15,fontWeight:900,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 16px rgba(59,130,246,0.35)"}}>
                {finishing ? "Finish Game" : "Next Battle →"}
              </button>
            )}
          </div>
        );
      })()}

      {/* finished */}
      {G.status === "finished" && (() => {
        const mw      = G.matchWins || {p1:0,p2:0};
        const gameNo  = G.gameNumber || 1;
        // Double-Up match is FIRST TO 7 POINTS — each game is worth its stake (1/2/4)
        // and points accumulate. A non-Double-Up game just plays a single game.
        const target = G.doubleUp ? 7 : 2;
        const matchOver = (mw[seat]||0) >= target || (mw[foe]||0) >= target;
        const wonMatch  = (mw[seat]||0) >= target;
        return (
        <div style={{...panel, textAlign:"center"}}>
          <div style={{fontSize:11,fontWeight:800,color:"rgba(255,255,255,0.4)",letterSpacing:2,marginBottom:4}}>
            GAME {gameNo} {G.doubleUp ? "\u00B7 FIRST TO 7" : ""}
          </div>
          <div style={{fontSize:26,fontWeight:900,
                       color: matchOver ? (wonMatch ? "#22C55E" : "#F87171")
                            : G.result === seat ? "#22C55E"
                            : G.result === "tie" ? "#FBBF24" : "#F87171"}}>
            {matchOver
              ? (wonMatch ? "MATCH WON" : "MATCH LOST")
              : G.result === seat ? "GAME WON"
              : G.result === "tie" ? "TIE GAME" : "GAME LOST"}
          </div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",marginTop:6}}>
            {t[seat]||0} {"\u2014"} {t[foe]||0} on trophies
          </div>
          {G.doubleUp && (G.wonStake||1) > 1 && (
            <div style={{fontSize:12,fontWeight:900,color:"#E8317A",marginTop:5,letterSpacing:0.5}}>
              ⚡ {G.declined ? `${G.result===seat?"OPPONENT WALKED AWAY":"YOU WALKED AWAY"} \u2014 ` : ""}DOUBLE-UP {"\u00B7"} worth {G.wonStake} points
            </div>
          )}
          {/* Explains why zones are still face-down: the lead became unassailable. */}
          {G.clinchedEarly && (
            <div style={{fontSize:11,fontWeight:800,color:"#FBBF24",marginTop:4,letterSpacing:0.5}}>
              CLINCHED {"\u2014"} remaining battles couldn{"\u2019"}t change the result
            </div>
          )}
          {/* Match score. This is the number that actually matters between games. */}
          <div style={{fontSize:15,fontWeight:900,marginTop:10,color:"#fff"}}>
            {G.doubleUp ? "Points" : "Match"} {mw[seat]||0} {"\u2014"} {mw[foe]||0}{G.doubleUp ? " (first to 7)" : ""}
            <span style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.45)",marginLeft:8}}>
              {me?.name||"You"} vs {them?.name||"Opponent"}
            </span>
          </div>

          {/* Sudden Death (\u00a74.4.3). Shown while the game is TIED, and kept on screen
              after it resolves \u2014 the flip decides the game, so hiding the two cards the
              instant a winner is set means you never see what beat you. `sudden` holds
              the last pair flipped; it's cleared only when both tie and you flip again. */}
          {(G.result === "tie" || (G.sudden && (G.sudden[seat] || G.sudden[foe]))) && (
            <div style={{marginTop:16,padding:"14px 12px",borderRadius:10,
                         background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.35)"}}>
              <div style={{fontSize:11,fontWeight:900,letterSpacing:2,color:"#FBBF24",marginBottom:8}}>
                SUDDEN DEATH
              </div>
              {(G.sudden?.[seat] || G.sudden?.[foe]) ? (
                <>
                  <div style={{display:"flex",gap:14,justifyContent:"center",alignItems:"center",flexWrap:"wrap"}}>
                    {[[seat, me?.name||"You"], [foe, them?.name||"Opponent"]].map(([s2,label]) => {
                      const card = G.sudden?.[s2];
                      const won  = G.result === s2;
                      return (
                        <div key={s2} style={{minWidth:120,textAlign:"center"}}>
                          <div style={{fontSize:10,fontWeight:800,color:"rgba(255,255,255,0.5)",marginBottom:4}}>{label}</div>
                          {card ? (
                            <div style={{padding:"8px 10px",borderRadius:8,
                                         border:`2px solid ${won ? "#22C55E" : "rgba(255,255,255,0.15)"}`,
                                         background: won ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.04)"}}>
                              <div style={{fontSize:22,fontWeight:900,color:"#FBBF24",lineHeight:1}}>{card.power}</div>
                              <div style={{fontSize:11,fontWeight:800,color:"#fff",marginTop:3}}>{card.hero}</div>
                              <div style={{fontSize:10,color: WEAPON_COLORS?.[card.weapon] || "#888",fontWeight:700}}>{card.weapon}</div>
                            </div>
                          ) : (
                            <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",padding:"14px 0"}}>waiting{"\u2026"}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {G.result !== "tie" && G.sudden?.[seat] && G.sudden?.[foe] && (
                    <div style={{fontSize:12,fontWeight:800,marginTop:10,
                                 color: G.result === seat ? "#22C55E" : "#F87171"}}>
                      {G.result === seat ? "You take it on the flip." : "They take it on the flip."}
                      {G.sudden[seat].power === G.sudden[foe].power && (
                        <span style={{color:"#FBBF24"}}> {"\u2014"} Power tied, Super Weapon decides</span>
                      )}
                    </div>
                  )}
                  {G.result === "tie" && G.sudden?.[seat] && !G.sudden?.[foe] && (
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",marginTop:8}}>Waiting for their flip{"\u2026"}</div>
                  )}
                </>
              ) : (
                <>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.65)",marginBottom:10}}>
                    3{"\u2013"}3 on trophies. Flip the top of your Hero Deck {"\u2014"} highest Power wins the game.
                  </div>
                  <button onClick={suddenDeath}
                    style={{padding:"12px 30px",borderRadius:9,border:"none",background:"#FBBF24",
                            color:"#000",fontWeight:900,fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>
                    Flip Top Card
                  </button>
                </>
              )}
              {/* Both flipped equal Power with no Super \u2014 rules say flip again. */}
              {G.result === "tie" && !G.sudden?.[seat] && !G.sudden?.[foe] && false}
            </div>
          )}

          {/* Play again is offered whether or not the match is decided: mid-match it
              continues the best-of-3, and after it's decided it starts a fresh one
              against the same opponent without going back to codes. */}
          <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap",marginTop:18}}>
            <button onClick={isCpu ? cpuPlayAgain : playAgain} disabled={busy}
              style={{padding:"12px 30px",borderRadius:9,border:"none",
                      background: busy ? "#333" : "#22C55E", color:"#000", fontWeight:900, fontSize:14,
                      cursor: busy?"not-allowed":"pointer", fontFamily:"inherit"}}>
              {busy ? "Shuffling\u2026" : matchOver ? "New Match" : `Play Game ${gameNo+1}`}
            </button>
            <button onClick={leaveGame}
              style={{padding:"12px 28px",borderRadius:9,border:"1px solid rgba(255,255,255,0.25)",
                      background:"transparent",color:"#fff",fontWeight:800,fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
              Back to Lobby
            </button>
          </div>
        </div>
        );
      })()}

      {/* battle log */}
      {(G.log||[]).length > 0 && (
        <div style={panel}>
          <div style={{fontSize:12,fontWeight:800,color:"rgba(255,255,255,0.55)",marginBottom:8}}>BATTLE LOG</div>
          {(G.log||[]).map((l,i) => (
            <div key={i} style={{fontSize:11,color:"rgba(255,255,255,0.7)",padding:"4px 0",
                                 borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
              <b>Zone {l.zone+1}:</b> {l.p1} ({l.p1p}) vs {l.p2} ({l.p2p}) {"\u2014 "}
              <span style={{color: l.winner ? (l.winner===seat?"#22C55E":"#F87171") : "#FBBF24", fontWeight:800}}>
                {l.winner ? `${G[l.winner]?.name||l.winner} wins${l.bySuper ? " (Super)" : ""}` : "draw"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DeckBuilderTab({ user, deckCards, setDeckCards, deckName, setDeckName, deckType, setDeckType, deckSearch, setDeckSearch, deckSearchDebounced="", deckFilterW, setDeckFilterW, deckFilterP, setDeckFilterP, deckFilterS, setDeckFilterS, deckFilterT, setDeckFilterT, WEAPON_COLORS, setSigningIn, cards, owned, lots=[], foilDogs=0, setFoilDogs=()=>{}, kidGroups=[], kidOfCopy=null, otherDeckUse={}, proxyCards={}, onToggleProxy=null, proxyNote=null, isProxyCopy=null, anyProxy=null, copyForDeck=null, inp, familyOwnerByCard={}, familyOwnsCard={}, deckOwnedMerged={}, canAddToDeck, isMobile, savedDecks=[], familyDecks=[], deckSaving, deckSaved, deckLoadId, saveDeckTab, deleteDeckTab, loadDeckTab, newDeckTab, toggleDeckRegistered=()=>{}, openRegSheetPrint=()=>{}, regSheetOpen=false, setRegSheetOpen=()=>{}, regInfo={}, setRegInfo=()=>{}, savedPlaybooks=[], giveDeckToFamily, takeBackDeck, familyList=[], givenDecks=[], setFanDeck, setFanMode, deckProgress, deckGoalW, setDeckGoalW, deckGoalT, setDeckGoalT, deckGoalSets, setDeckGoalSets, deckMaxMode, setDeckMaxMode, deckSource="both", setDeckSource, deckFamilyMember="all", setDeckFamilyMember=()=>{}, computeDeckProgress, listings=[], setActiveTab, deckLegality={ok:true,problems:[],empty:true} }) {
  const weapons    = sortWeapons([...new Set(cards.map(c=>canonWeapon(c.weapon)).filter(Boolean))]);
  const sets       = [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort();
  const treatments = [...new Set(cards.map(c=>c.treatment).filter(Boolean))].sort();
  // Highest power first \u2014 alphabetical would put 100 above 95, and you pick power by tier.
  const powers = [...new Set(cards.map(c=>c.power!=null&&c.power!==""?String(c.power):null).filter(Boolean))]
    .sort((a,b)=>parseFloat(b)-parseFloat(a));
  const DECK_SIZE = (DECK_FORMATS[deckType]||DECK_FORMATS.none).size || 60;   // capacity follows format (Apex Madness = 70)
  const [deckOwnedOnly, setDeckOwnedOnly] = useState(false);
  const [deckFamilyOnly, setDeckFamilyOnly] = useState(false);
  // When "My collection" is picked (as opposed to "Mine + family"), exclude borrowable family cards.
  const [deckMineOnly, setDeckMineOnly] = useState(false);
  const [sharedDeckId, setSharedDeckId] = useState(null);
  const [setsOpen, setSetsOpen] = useState(false);   // set picker collapsed by default
  // Which filter picker is expanded in the manual builder. One flag each so opening weapons
  // does not collapse treatments.
  const [filtWOpen, setFiltWOpen] = useState(false); // weapons
  const [filtSOpen, setFiltSOpen] = useState(false); // sets
  const [filtTOpen, setFiltTOpen] = useState(false); // treatments
  const [filtPOpen, setFiltPOpen] = useState(false); // manual builder: power multi-select picker
  // Shared by all four filter pickers. It used to live inside the IIFE that renders the first
  // three, so the power picker \u2014 which sits outside that block \u2014 crashed on render with
  // "tabBtn is not defined".
  const [goalWOpen,  setGoalWOpen]  = useState(false); // goal picker: weapons expanded
  const [giveDeckFor, setGiveDeckFor] = useState(null); // deck id whose "give to family" row is open
  const tabBtn = (open, active) => ({...inp, width:"auto", cursor:"pointer", textAlign:"left",
    color: active ? "#fff" : "rgba(255,255,255,0.4)"});
  const [goalTOpen, setGoalTOpen] = useState(false); // treatment picker
  const [progressExpanded, setProgressExpanded] = useState(false);
  // Quick Build starts collapsed: most time in this tab is manual building, and the card grid is
  // the thing that needs the room. Expanding it is one click when you do want it.
  const [quickOpen, setQuickOpen] = useState(false);
  const [deckPage, setDeckPage] = useState(1);
  const [showPickList, setShowPickList] = useState(false);   // printable pick-list modal
  const [pickSort, setPickSort] = useState("power");         // "power" | "cardnum" | "weapon"
  const [pickChecked, setPickChecked] = useState({});        // {cardId: true} — pulled cards (view-only, resets on close)
  const DECK_PAGE_SIZE = 60;
  const ownedCount = owned ? Object.keys(owned).filter(id=>owned[id]).length : 0;
  const deckSet = useMemo(()=>new Set(deckCards), [deckCards]);
  // Highest power first. The deck inherited whatever order the catalog happened to be in, which
  // makes it hard to scan a built deck and confirm it is right \u2014 and power is the thing you are
  // actually checking. Every view (grid, expand, fan) derives from this array, so sorting once
  // here orders all of them. Ties fall back to hero name so the order is stable rather than
  // shuffling between renders.
  const inDeck = useMemo(()=>{
    const p = c => { const n = parseFloat(c?.power); return isNaN(n) ? -Infinity : n; };
    return cards
      .filter(c=>deckSet.has(c.id))
      .slice()
      .sort((a,b)=> p(b)-p(a) || String(a.hero||"").localeCompare(String(b.hero||"")));
  }, [cards, deckSet]);
  const isSpec = deckType==="spec"||deckType==="vegasbaby", isAM = deckType==="apexmadness";
  const [deckListView, setDeckListView] = useState("mine");   // "mine" | "family"
  const dupKey = c=>`${(c.hero||"").toLowerCase()}|${(c.variation||"").toLowerCase()}|${c.power||""}|${(c.weapon||"").toLowerCase()}`;
  const inDeckDupKeys = new Set(inDeck.map(dupKey));
  const powerCount = {}; inDeck.forEach(c=>{const p=c.power||"0"; powerCount[p]=(powerCount[p]||0)+1;});
  const treatCore={}, treatApex={};
  if(isAM){inDeck.forEach(c=>{if(!isInsertTreatment(c))return;const t=insertKey(c),p=parseFloat(c.power||0);if(p<=160){treatCore[t]=(treatCore[t]||0)+1;}else if(p>160){treatApex[t]=(treatApex[t]||0)+1;}});}
  const dbTotalPower = inDeck.reduce((s,c)=>s+(parseFloat(c.power)||0),0);
  const dbAvgPower = inDeck.length>0 ? Math.round(dbTotalPower/inDeck.length) : 0;
  const dbHeroes = new Set(inDeck.map(c=>(c.hero||"").toLowerCase()).filter(Boolean)).size;
  const dbWeaponBreak = (()=>{ const m={}; inDeck.forEach(c=>{ if(c.weapon) m[c.weapon]=(m[c.weapon]||0)+1; }); return Object.entries(m).sort((a,b)=>b[1]-a[1]); })();
  const dbPowerCurve = (()=>{ const buckets=[["≤95",0],["100-130",0],["135-160",0],["165-200",0],["200+",0]]; inDeck.forEach(c=>{const p=parseFloat(c.power)||0; if(p<=95)buckets[0][1]++; else if(p<=130)buckets[1][1]++; else if(p<=160)buckets[2][1]++; else if(p<=200)buckets[3][1]++; else buckets[4][1]++;}); return buckets; })();
  const dbMaxCurve = Math.max(1, ...dbPowerCurve.map(b=>b[1]));
  // Live coaching: format-aware guidance about the whole deck
  const dbCoach = (()=>{
    const tips=[];
    const remaining = DECK_SIZE - inDeck.length;
    if(inDeck.length===0) return [{t:"info",m:"Add cards to start building. Tips will appear here as your deck takes shape."}];
    // power-cap warnings (6 per power level)
    Object.entries(powerCount).forEach(([p,n])=>{ if(n===6) tips.push({t:"warn",m:`You're at the max 6 cards at power ${p} — that level is full.`}); else if(n===5) tips.push({t:"info",m:`5 cards at power ${p}. One more hits the 6-card cap.`}); });
    // format-specific
    if(isAM){
      const treatments=[...new Set(inDeck.map(c=>c.treatment).filter(Boolean))];
      // Iterate INSERTS (Inspired Ink variants collapse to one), not raw treatment strings.
      // Keys come from the DECK, not from the treatment list: a set-level insert like Alpha Blast
      // has no single treatment to derive it from, so listing treatments would split it back apart.
      Array.from(new Set(inDeck.map(insertKey))).forEach(tl=>{ const t=insertLabel(tl, inDeck.find(x=>insertKey(x)===tl)?.treatment||tl),core=treatCore[tl]||0,apex=treatApex[tl]||0; if(apex>0&&core<10) tips.push({t:"warn",m:`${t}: apex card in deck but only ${core}/10 core cards — needs 10 core to be legal.`}); else if(core>=10&&apex===0) tips.push({t:"good",m:`${t} unlocked (${core} core) — you can add 1 apex card.`}); else if(core>0&&core<10){ const need=10-core; const ownedEligible = owned ? cards.filter(cc=>!deckSet.has(cc.id)&&insertKey(cc.treatment)===tl&&((parseFloat(cc.power)||0)>=115)&&((parseFloat(cc.power)||0)<=160)&&owned[cc.id]).length : 0; tips.push({t:"info",m:`${t}: ${core}/10 core toward an apex unlock.${owned?(ownedEligible>=need?` You own ${ownedEligible} eligible — enough to finish!`:` You own ${ownedEligible} of ${need} more needed.`):""}`}); } });
      if(treatments.length===0) tips.push({t:"info",m:"Apex Madness: build 10 core cards (115–160) of a treatment to unlock its apex card."});
      // Foil Hot Dogs aren't part of the quick build (they depend on foils you own, not the deck), so
      // surface them here as a manual next step once the auto-built deck is on the table.
      tips.push({t:"info",m:"Hot Dogs: add 4 DIFFERENT Foil Hot Dogs by hand and you unlock 4 more Apex Heroes — and those four can be from any insert."});
    }
    if(isSpec){ const over=inDeck.filter(c=>(parseFloat(c.power)||0)>160).length; if(over>0) tips.push({t:"warn",m:`${over} card(s) over 160 power — not legal in a Spec deck.`}); }
    // balance coaching
    if(inDeck.length>=10){
      if(dbWeaponBreak.length===1) tips.push({t:"info",m:`Every card is ${dbWeaponBreak[0][0]} — consider mixing weapons for flexibility.`});
      const topW=dbWeaponBreak[0]; if(topW && topW[1]/inDeck.length>0.7 && dbWeaponBreak.length>1) tips.push({t:"info",m:`${Math.round(topW[1]/inDeck.length*100)}% of your deck is ${topW[0]} — fairly weapon-heavy.`});
    }
    // size
    if(inDeck.length===DECK_SIZE) tips.push({t:"good",m:`Deck is full at ${DECK_SIZE} cards. 🎉`});
    else if(remaining<=10) tips.push({t:"info",m:`${remaining} slots left to reach ${DECK_SIZE}.`});
    // collection-aware availability
    if(owned && inDeck.length<DECK_SIZE){
      const ownedAvail = cards.filter(c=>!deckSet.has(c.id)&&owned[c.id]&&(()=>{const t=(c.treatment||"").toLowerCase();return t!=="plays"&&t!=="bonus plays"&&t!=="home team discount";})()).length;
      if(ownedAvail===0 && inDeck.length>0) tips.push({t:"info",m:"You've used every eligible card from your collection in this deck."});
      else if(ownedAvail>0) tips.push({t:"info",m:`${ownedAvail} more card${ownedAvail===1?"":"s"} from your collection available to add.`});
    }
    if(tips.length===0) tips.push({t:"good",m:"Looking clean — no issues with your deck so far."});
    return tips.slice(0,5);
  })();
  // Actionable "what do I need" shortcuts — filter the card list to eligible cards (never auto-adds)
  const dbNeeds = (()=>{
    const needs=[];
    if(isAM){
      const treatments=[...new Set(inDeck.map(c=>c.treatment).filter(Boolean))];
      Array.from(new Set(inDeck.map(insertKey))).forEach(tl=>{ const t=insertLabel(tl, inDeck.find(x=>insertKey(x)===tl)?.treatment||tl),core=treatCore[tl]||0; if(core>0&&core<10){ const corePowers=["115","120","125","130","135","140","145","150","155","160"]; needs.push({ label:`Find core ${t} (${core}/10)`, t, p:corePowers }); } });
    }
    return needs.slice(0,4);
  })();
  const applyNeedFilter = (n)=>{ setDeckSearch(""); setDeckFilterW(""); setDeckFilterS(""); setDeckFilterT(n.t||""); setDeckFilterP(n.p?new Set(n.p):new Set()); if(user&&owned){ const hasOwned = cards.some(c=>!deckSet.has(c.id)&&(c.treatment||"").toLowerCase()===(n.t||"").toLowerCase()&&n.p&&n.p.includes(String(c.power||""))&&owned[c.id]); if(hasOwned) setDeckOwnedOnly(true); } };
  // PERF: prebuild the searchable text once per card (see the note in the card database) so the
  // deck search is a plain substring test instead of rebuilding 31k strings per keystroke.
  const deckSearchIndex = useMemo(() => {
    const m = new Map();
    for (const c of cards) {
      const hay = [c.hero,c.cardNum,c.treatment,c.weapon,c.power,c.setName,c.variation]
        .filter(Boolean).join(" ").toLowerCase();
      // Second copy with spacing/punctuation stripped, so "powerglove" finds "Power Glove".
      m.set(c.id, { hay, tight: hay.replace(/[\s\-_.'’]/g, "") });
    }
    return m;
  }, [cards]);
  const deckSearchTerms = useMemo(
    () => (deckSearchDebounced||"").toLowerCase().split(/\s+/).filter(Boolean)
            .map(t => ({ t, tight: t.replace(/[\s\-_.'’]/g, "") })),
    [deckSearchDebounced]
  );
  const deckSearchTight = useMemo(
    () => (deckSearchDebounced||"").toLowerCase().replace(/[\s\-_.'’]/g, ""),
    [deckSearchDebounced]
  );

  // PERF: filters + sorts the full 31k checklist. Memoized so hovering/adding a card doesn't
  // re-scan every card in the game.
  // How many copies of a card are actually IN HAND. Owned quantity is not the same thing: a copy
  // lent to someone is still yours and still counts toward your collection, but you cannot put it
  // in a deck. A borrowed copy is the opposite \u2014 you have it, so it can be decked.
  const availableCount = useMemo(() => {
    const byCard = {};
    (lots||[]).forEach(l => {
      if (!l?.cardId) return;
      const st = l.lendState || "";
      byCard[l.cardId] = byCard[l.cardId] || { lots:0, lent:0, borrowed:0 };
      byCard[l.cardId].lots++;
      if (st === "lent") byCard[l.cardId].lent++;
      if (st === "borrowed") byCard[l.cardId].borrowed++;
    });
    return (cardId) => {
      const q = parseInt(owned?.[cardId]) || 0;
      const b = byCard[cardId];
      if (!b) return q;                       // no per-copy records: everything is in hand
      // Only subtract lends we actually have a record for. If someone owns 3 but has only made
      // one lot, we must not assume the other two are missing.
      return Math.max(0, q - b.lent) + b.borrowed;
    };
  }, [lots, owned]);

  const deckAvail = useMemo(() => cards.filter(c=>{
    if(deckSet.has(c.id)) return false;
    if(deckFamilyOnly && !familyOwnerByCard[c.id]) return false;
    // "My collection" = my cards only. "Mine + family" = my cards plus borrowable family cards.
    if(deckOwnedOnly){
      const isMine = !!(owned && owned[c.id]);
      const isFam  = !!familyOwnerByCard[c.id];
      if(deckMineOnly ? !isMine : (!isMine && !isFam)) return false;
      // Every copy is lent out — owned, but not available to build with.
      if(isMine && availableCount(c.id) <= 0) return false;
    }
    if(deckFilterW.size && !deckFilterW.has(canonWeapon(c.weapon))) return false;   // multi-select
    if(deckFilterP && deckFilterP.size>0 && !deckFilterP.has(String(c.power||""))) return false;
    if(deckFilterS.size && !deckFilterS.has(c.setName)) return false;               // multi-select
    if(deckFilterT.size && !deckFilterT.has(c.treatment)) return false;             // multi-select
    if(deckSearchTerms.length){
      const idx = deckSearchIndex.get(c.id);
      if(!idx) return false;
      const whole = deckSearchTight && idx.tight.includes(deckSearchTight);
      if(!whole && !deckSearchTerms.every(t=>idx.hay.includes(t.t) || idx.tight.includes(t.tight))) return false;
    }
    const t=(c.treatment||"").toLowerCase();
    if(t==="plays"||t==="bonus plays"||t==="home team discount") return false;
    return true;
  }).sort((a,b)=>(parseFloat(b.power)||0)-(parseFloat(a.power)||0)),
  // `owned` only matters here when the "My collection" filter is on — see the note on `filtered`.
  [cards, deckType, deckSet, deckFamilyOnly, deckOwnedOnly, deckMineOnly, familyOwnerByCard, deckFilterW, deckFilterP, deckFilterS, deckFilterT, deckSearchTerms, deckSearchTight, deckSearchIndex,
   deckOwnedOnly ? owned : null]);
  const deckVisible = useMemo(()=>deckAvail.slice(0, deckPage*DECK_PAGE_SIZE), [deckAvail, deckPage]);
  useEffect(()=>{ setDeckPage(1); }, [deckSearch, deckFilterW, deckFilterS, deckFilterT, deckOwnedOnly, deckFamilyOnly, deckMineOnly, deckType]);
  return (
        <>
          <div className="deck-pb-layout" style={{display:"flex",flexDirection:isMobile?"column":"row",gap:16,alignItems:"stretch",height:isMobile?"auto":"calc(100vh - 150px)",minHeight:isMobile?"auto":520}}>
            <div style={{display:"flex",flexDirection:"column",gap:10,flex:1,minWidth:0,minHeight:0,overflowY:isMobile?"visible":"auto",paddingRight:isMobile?0:6}}>
              {/* The quick builder and the manual builder are alternative MODES, not companions \u2014 you
                  use one or the other. Leaving the quick panel permanently expanded left the manual
                  card grid squeezed into whatever was left, which is no use when the whole point is
                  looking at cards. Collapsing it hands the full page to the manual builder. */}
              {user && deckProgress && (
                <button onClick={()=>setQuickOpen(v=>!v)}
                  style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,width:"100%",
                    background:"rgba(255,255,255,0.03)",border:"1px solid var(--bz-line)",borderRadius:10,
                    padding:"9px 12px",cursor:"pointer",fontFamily:"inherit",color:"var(--bz-ink)"}}>
                  <span style={{fontSize:12.5,fontWeight:800,display:"flex",alignItems:"center",gap:7}}>
                    {"\u26A1"} Quick Build
                    {!quickOpen && <span style={{fontSize:10.5,fontWeight:600,color:"var(--bz-ink-3)"}}>build a deck from your collection automatically</span>}
                  </span>
                  <span style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-3)"}}>{quickOpen ? "\u25B4 Hide" : "\u25BE Show"}</span>
                </button>
              )}
              {quickOpen && user && deckProgress && (() => {
                const pct = Math.min(100, Math.round((deckProgress.have/deckProgress.need)*100));
                const done = deckProgress.have >= deckProgress.need;
                const weapons = sortWeapons(Array.from(new Set(cards.map(c=>canonWeapon(c.weapon)).filter(Boolean))));
                const treatments = Array.from(new Set(cards.map(c=>c.treatment).filter(t=>t&&!["plays","bonus plays","home team discount"].includes(t.toLowerCase())))).sort();
                // Sets, not strings: "Hex + Gum Prismatic" rather than "Hex Prismatic".
                // Weapons are a PRIORITY order (first pick fills the deck first), so show them with › rather
                // than "+", which reads as an unordered mix.
                const goalLabel = [Array.from(deckGoalW).join(" › "), Array.from(deckGoalT).join(" + ")].filter(Boolean).join(" ");
                const dtLabel = deckType==="spec"?"Spec (≤160)":deckType==="vegasbaby"?"Vegas Baby (≤160)":deckType==="apex"?"Apex":deckType==="apexmadness"?"Apex Madness":deckType==="none"?"":deckType;
                // Apex Madness has too many interacting constraints for a reliable one-tap build:
                // 10 core per insert (11 with the apex), the whole 60-card core drawn from at most 6
                // inserts, insert-matched apex unlocks, and foiled Hot Dog slots on top. An auto-build
                // that gets any of those subtly wrong is worse than none, so point people at the
                // manual builder, where the rules tracker guides them card by card.
                if (deckType === "apexmadness") {
                  return (
                    <div style={{background:"rgba(232,49,122,0.06)",border:"1px solid rgba(232,49,122,0.25)",borderRadius:12,padding:"14px 16px"}}>
                      <div style={{fontSize:13,fontWeight:900,color:"#fff",marginBottom:6,display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>🔥 Apex Madness <span style={{fontSize:10,fontWeight:800,color:"#FBBF24",background:"rgba(251,191,36,0.12)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:6,padding:"2px 7px"}}>build manually</span></div>
                      <div style={{fontSize:12,color:"#bbb",lineHeight:1.6,marginBottom:10}}>Apex Madness has too many moving parts (10 core per insert, a 6-insert core, insert-matched apex unlocks, foiled Hot Dog slots) for a reliable one-tap build. Add cards below and the <strong>Apex Madness rules tracker</strong> will guide you — it shows which inserts you've unlocked and what each apex needs.</div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                        <span style={{fontSize:11,color:"#a0a0a0",fontWeight:700}}>Goal:</span>
                        <select value={deckType} onChange={e=>{ const v=e.target.value; setDeckType(v); if(!(v==="spec"||v==="vegasbaby"||v==="apex")) setDeckMaxMode(false); }} style={{...inp,width:"auto",cursor:"pointer"}}>
                          {Object.entries(DECK_FORMATS).map(([k,f])=><option key={k} value={k}>{f.label}</option>)}
                        </select>
                      </div>
                    </div>
                  );
                }
                return (
                  <div style={{background:done?"linear-gradient(135deg,rgba(74,222,128,0.12),rgba(34,197,94,0.06))":"rgba(255,255,255,0.02)",border:`1px solid ${done?"rgba(74,222,128,0.4)":"rgba(232,49,122,0.25)"}`,borderRadius:14,padding:"14px 16px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:10}}>
                      <div style={{fontSize:13,fontWeight:900,color:done?"#4ade80":"#fff",display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
                        {done ? "✅ You can build a complete" : "🎯 Progress to a complete"} {goalLabel?`${goalLabel} `:""}deck
                        {dtLabel && <span style={{fontSize:10,fontWeight:800,color:"#FBBF24",background:"rgba(251,191,36,0.12)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:6,padding:"2px 7px"}}>{dtLabel}</span>}
                      </div>
                      <div style={{fontSize:13,fontWeight:900,color:done?"#4ade80":"#E8317A"}}>{deckProgress.have} / {deckProgress.need}</div>
                    </div>
                    <div style={{height:10,background:"rgba(0,0,0,0.4)",borderRadius:6,overflow:"hidden",marginBottom:10}}>
                      <div style={{height:"100%",width:`${pct}%`,background:done?"linear-gradient(90deg,#4ade80,#22c55e)":"linear-gradient(90deg,#E8317A,#7B2FF7)",borderRadius:6,transition:"width 0.4s"}}/>
                    </div>
                    <div style={{fontSize:11,color:"#999",marginBottom:10,lineHeight:1.5}}>
                      {done
                        ? `You own enough unique cards to fill a 60-card${goalLabel?` ${goalLabel}`:""} deck (max 6 per power level). 🎉`
                        : `You need ${deckProgress.need-deckProgress.have} more eligible card${deckProgress.need-deckProgress.have!==1?"s":""}. Counts unique cards you own, max 6 per power level${goalLabel?`, ${goalLabel} only`:""}.`}
                    </div>
                    {deckProgress.am && (
                      <div style={{background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.2)",borderRadius:10,padding:"10px 12px",marginBottom:10,fontSize:11,lineHeight:1.7,color:"#ddd"}}>
                        <div style={{fontWeight:800,color:"#FBBF24",marginBottom:4}}>Apex Madness build (up to 70) — smart pick</div>
                        <div>• <strong>{deckProgress.am.coreCount}/60</strong> Spec core · <strong>{deckProgress.am.insertUnlocks}/6</strong> Apex unlocked from Inserts · <strong>{deckProgress.am.hotDogSlots}/4</strong> from Foil Hot Dogs</div>
                        <div>• <strong>{deckProgress.am.apexCount}</strong> Apex Heroes in deck ({deckProgress.am.apexAdded}/{deckProgress.am.apexSlots} slots filled) — ceiling {deckProgress.am.ceiling}</div>
                        {deckProgress.am.insertList && deckProgress.am.insertList.length>0 && (
                          <div style={{marginTop:4}}>• Inserts in deck: {deckProgress.am.insertList.map(x=>`${x.insert} ${x.count}${x.unlocks?` → ${x.unlocks} apex`:""}`).join(" · ")}</div>
                        )}
                        {deckProgress.am.nearInserts && deckProgress.am.nearInserts.length>0 && (
                          <div style={{marginTop:4,color:"#60A5FA"}}>💡 Closest to another unlock: {deckProgress.am.nearInserts.map(x=>`${x.insert} (${x.have}/${x.need})`).join(" · ")}</div>
                        )}
                        <div style={{color:"#999",marginTop:4}}>• Foil Hot Dogs: {Math.min(4,parseInt(foilDogs)||0)}/4 — each one unlocks an extra Apex Hero</div>
                      </div>
                    )}
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                      <span style={{fontSize:11,color:"#a0a0a0",fontWeight:700}}>Goal:</span>
                      <select value={deckType} onChange={e=>{ const v=e.target.value; setDeckType(v); if(!(v==="spec"||v==="vegasbaby"||v==="apex")) setDeckMaxMode(false); }} style={{...inp,width:"auto",fontSize:11,padding:"5px 8px",cursor:"pointer",fontWeight:700,color:deckType==="none"?"rgba(255,255,255,0.5)":"#FBBF24"}}>
                        {Object.entries(DECK_FORMATS).map(([k,f])=><option key={k} value={k}>{f.label}</option>)}
                      </select>
                      {/* Weapon + treatment goals are multi-select: an "all Hex and Gum" Apex deck is
                          a normal thing to want. Same collapsed-picker pattern as the set filter, so
                          a long list of weapons doesn't turn back into chip soup. */}
                      {(() => {
                        const toggleW = w => setDeckGoalW(prev => { const n=new Set(prev); n.has(w)?n.delete(w):n.add(w); return n; });
                        const toggleT = t => setDeckGoalT(prev => { const n=new Set(prev); n.has(t)?n.delete(t):n.add(t); return n; });
                        const label = (sel, none, one) => sel.size===0 ? none : sel.size===1 ? `${Array.from(sel)[0]} ${one}` : `${sel.size} ${none.split(" ")[1]}s`;
                        return (
                          <>
                            <button onClick={()=>setGoalWOpen(v=>!v)} style={{...inp,width:"auto",fontSize:11,padding:"5px 10px",cursor:"pointer",textAlign:"left"}}>
                              {label(deckGoalW,"Any weapon","only")} <span style={{fontSize:9,opacity:0.7}}>{goalWOpen?"\u25B2":"\u25BC"}</span>
                            </button>
                            <button onClick={()=>setGoalTOpen(v=>!v)} style={{...inp,width:"auto",fontSize:11,padding:"5px 10px",cursor:"pointer",textAlign:"left"}}>
                              {label(deckGoalT,"Any treatment","only")} <span style={{fontSize:9,opacity:0.7}}>{goalTOpen?"\u25B2":"\u25BC"}</span>
                            </button>
                            {(deckGoalW.size>0||deckGoalT.size>0) && (
                              <button onClick={()=>{setDeckGoalW(new Set());setDeckGoalT(new Set());}}
                                style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:8,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Clear</button>
                            )}
                            {goalWOpen && (
                              <div style={{width:"100%",marginTop:6,padding:"10px 12px",background:"rgba(0,0,0,0.25)",border:"1px solid var(--bz-line)",borderRadius:10,display:"flex",flexWrap:"wrap",gap:6}}>
                                <div style={{width:"100%",fontSize:10,color:"var(--bz-ink-3)",marginBottom:4}}>
                            Tap in priority order — the deck fills from your first pick, then tops up with the next.
                          </div>
                          <div style={{width:"100%",display:"flex",justifyContent:"flex-end",gap:6,marginBottom:4}}>
                            <button onClick={()=>setDeckGoalW(new Set(weapons))} style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:6,padding:"3px 9px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Select all</button>
                            <button onClick={()=>setDeckGoalW(new Set())} style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:6,padding:"3px 9px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>None</button>
                          </div>
                          <div style={{width:"100%",maxHeight:210,overflowY:"auto",overscrollBehavior:"contain",border:"1px solid var(--bz-line)",borderRadius:8}}>
                            {weapons.map(w=>{
                              const rank = Array.from(deckGoalW).indexOf(w);   // Sets keep insertion order
                              const on = rank>=0;
                              return (
                                <label key={w} onClick={ev=>{ev.preventDefault(); toggleW(w);}}
                                  style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",cursor:"pointer",background:on?"rgba(232,49,122,0.10)":"transparent",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                                  <span style={{width:16,height:16,flexShrink:0,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",border:`1.5px solid ${on?"#E8317A":"var(--bz-line-2)"}`,background:on?"#E8317A":"transparent",color:"#fff",fontSize:9.5,fontWeight:900,lineHeight:1}}>{on?(rank+1):""}</span>
                                  <span style={{fontSize:11.5,fontWeight:on?800:600,color:on?"#fff":"var(--bz-ink-2)"}}>{w}</span>
                                </label>
                              );
                            })}
                          </div>
                              </div>
                            )}
                            {goalTOpen && (
                              <div style={{width:"100%",marginTop:6,background:"rgba(0,0,0,0.25)",border:"1px solid var(--bz-line)",borderRadius:9,overflow:"hidden"}}>
                                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"7px 10px",borderBottom:"1px solid var(--bz-line)",background:"rgba(255,255,255,0.03)"}}>
                                  <span style={{fontSize:10.5,fontWeight:800,color:"var(--bz-ink-3)"}}>
                                    {deckGoalT.size===0 ? "Any treatment" : `${deckGoalT.size} of ${treatments.length} selected`}
                                  </span>
                                  <span style={{display:"flex",gap:6}}>
                                    <button onClick={()=>setDeckGoalT(new Set(treatments))} style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:6,padding:"3px 9px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Select all</button>
                                    <button onClick={()=>setDeckGoalT(new Set())} style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:6,padding:"3px 9px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>None</button>
                                  </span>
                                </div>
                                <div style={{maxHeight:210,overflowY:"auto",overscrollBehavior:"contain"}}>
                                  {treatments.map(t=>{
                                    const on = deckGoalT.has(t);
                                    return (
                                      <label key={t} onClick={ev=>{ev.preventDefault(); toggleT(t);}}
                                        style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",cursor:"pointer",background:on?"rgba(232,49,122,0.10)":"transparent",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                                        <span style={{width:14,height:14,flexShrink:0,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",border:`1.5px solid ${on?"#E8317A":"var(--bz-line-2)"}`,background:on?"#E8317A":"transparent",color:"#fff",fontSize:10,fontWeight:900,lineHeight:1}}>{on?"\u2713":""}</span>
                                        <span style={{fontSize:11.5,fontWeight:on?800:600,color:on?"#fff":"var(--bz-ink-2)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{t}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    {/* Card source for the quick build — default to your own cards. If you
                        have borrowable family cards, you can opt to include them. */}
                    <div style={{display:"flex",gap:6,alignItems:"center",marginTop:10,flexWrap:"wrap"}}>
                      <span style={{fontSize:11,color:"#a0a0a0",fontWeight:700}}>Build with:</span>
                      {[["mine","My cards only"], ...(familyList.length>0 ? [["both","Mine + family"],["family","\uD83D\uDC6A Family only"]] : [])].map(([val,lbl])=>{
                        const on = deckSource===val;
                        const col = val==="family" ? "#C084FC" : val==="both" ? "#4ade80" : "#7B9CFF";
                        return (
                          <button key={val} onClick={()=>setDeckSource(val)}
                            style={{background:on?`${col}22`:"transparent",border:`1px solid ${on?col:"var(--bz-line-2)"}`,color:on?col:"rgba(255,255,255,0.55)",borderRadius:14,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                            {lbl}
                          </button>
                        );
                      })}
                    </div>
                    {(deckType==="apex"||deckType==="spec"||deckType==="vegasbaby") && (
                      <div style={{display:"flex",gap:8,alignItems:"center",marginTop:10,flexWrap:"wrap"}}>
                        <div onClick={()=>setDeckMaxMode(v=>!v)} style={{display:"flex",alignItems:"center",gap:7,background:deckMaxMode?"rgba(251,191,36,0.14)":"rgba(255,255,255,0.03)",border:`1px solid ${deckMaxMode?"#FBBF24":"var(--bz-line-2)"}`,borderRadius:8,padding:"6px 11px",cursor:"pointer",whiteSpace:"nowrap"}}>
                          <div style={{width:28,height:16,borderRadius:8,background:deckMaxMode?"#FBBF24":"#333",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                            <div style={{position:"absolute",top:2,left:deckMaxMode?14:2,width:12,height:12,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
                          </div>
                          <span style={{fontSize:12,fontWeight:800,color:deckMaxMode?"#FBBF24":"rgba(255,255,255,0.6)"}}>⚡ Build max deck</span>
                        </div>
                        <span style={{fontSize:10.5,color:"var(--bz-ink-3)",lineHeight:1.5}}>
                          {deckMaxMode
                            ? <>Only <strong style={{color:"#FBBF24"}}>{deckType==="apex"?"155+":"115–160"}</strong> power cards{deckType==="apexmadness"?" in the 60-card core":""} — the legal {deckType==="apex"?"Apex":deckType==="apexmadness"?"Madness core":"Spec"} window. Won't pad with weaker cards.</>
                            : <>Off: takes your top 60 regardless of power. Turn on to enforce the legal {deckType==="apex"?"Apex (155+)":deckType==="apexmadness"?"Madness core (115–160)":"Spec (115–160)"} window.</>}
                        </span>
                      </div>
                    )}
                    {deckProgress.maxMode && deckProgress.maxShort>0 && (
                      <div style={{marginTop:10,background:"rgba(232,49,122,0.08)",border:"1px solid rgba(232,49,122,0.3)",borderRadius:10,padding:"9px 12px",fontSize:11,color:"#ffb3d0",lineHeight:1.5}}>
                        ⚠️ <strong>{deckProgress.have}/{DECK_SIZE}</strong> — max mode only draws from the {deckProgress.maxWindowLabel} power window, and you own just {deckProgress.have} eligible cards there. <strong>60 is required</strong>, so this deck can't be played as it stands. <strong>Turn off max mode</strong> to fill the remaining {deckProgress.maxShort} from outside the window — you'll get a legal 60 at slightly lower average power.
                      </div>
                    )}

                    {/* WHAT'S MISSING — expressed as POWER SLOTS, because any legal card at that
                        power fills the slot equally well. The marketplace lookup therefore searches
                        for ANY card at that power in a legal set, rather than one arbitrary card. */}
                    {/* ELITE (total-power) — there are NO per-power-level quotas. The only requirement is
                  that the deck's summed power reaches the cap (e.g. 8,250). Any cards get you there,
                  so showing "×6 at 250⚡ needed" is nonsense here. Show the power GAP instead. */}
              {fmtOf(deckType).totalPower && deckProgress && (() => {
                const F = fmtOf(deckType);
                const SIZE = F.size||60;
                const CAP = F.totalPower;
                const cur = deckProgress.totalPower||0;
                const have = deckProgress.have||0;
                const cardsGap = Math.max(0, SIZE - have);
                const headroom = CAP - cur;
                // EFFICIENCY = how much of the power budget a FULL deck is using. Only a full
                // 60-card deck can be judged for efficiency; before that, the meter tracks fill.
                const full = have >= SIZE;
                const pct = full ? Math.min(100, Math.round((cur/CAP)*100)) : Math.round((have/SIZE)*100);
                const over = cur > CAP;
                // Upgrade suggestions: swap a deck card for a higher-power card you already own
                // (or a family member's, via deckOwnedMerged) that keeps the deck legal —
                // ≤6 at the new power level, no duplicate identity, and total staying ≤ CAP.
                const inDeckIds = new Set(deckCards);
                const dupId = c => `${(c.hero||"").toLowerCase()}|${(c.variation||"").toLowerCase()}|${c.power||""}|${(c.weapon||"").toLowerCase()}`;
                const deckDupKeys = new Set(inDeck.map(dupId));
                const lvlCount = {}; inDeck.forEach(c=>{const p=String(c.power||"0");lvlCount[p]=(lvlCount[p]||0)+1;});
                // Candidate pool: cards you can field that aren't already in the deck.
                const pool = cards.filter(c => !inDeckIds.has(c.id) && (deckOwnedMerged[c.id]||owned[c.id]));
                // Best single swaps, greedily: highest power gain first.
                const suggestions = [];
                const usedOut = new Set(), usedIn = new Set();
                inDeck.slice().sort((a,b)=>(parseFloat(a.power)||0)-(parseFloat(b.power)||0)).forEach(low => {
                  if (usedOut.has(low.id)) return;
                  const lowP = parseFloat(low.power)||0;
                  // Find the highest-power legal replacement.
                  let best=null;
                  for (const c of pool) {
                    if (usedIn.has(c.id)) continue;
                    const cP = parseFloat(c.power)||0;
                    if (cP <= lowP) continue;                          // must be an upgrade
                    if (F.powerCap && cP > F.powerCap) continue;       // respect a hard per-card cap
                    // level room after removing `low` from its level
                    const newLvl = String(c.power||"0");
                    const roomAtNew = (lvlCount[newLvl]||0) - (newLvl===String(low.power||"0")?1:0);
                    if (roomAtNew >= (F.perPower||6)) continue;        // that level would overflow
                    if (deckDupKeys.has(dupId(c)) && dupId(c)!==dupId(low)) continue; // no dup identity
                    if (cur - lowP + cP > CAP) continue;               // stays under total cap
                    if (!best || cP > (parseFloat(best.power)||0)) best = c;
                  }
                  if (best) {
                    suggestions.push({ out:low, in:best, gain:(parseFloat(best.power)||0)-lowP });
                    usedOut.add(low.id); usedIn.add(best.id);
                  }
                });
                suggestions.sort((a,b)=>b.gain-a.gain);
                const potentialGain = suggestions.reduce((s,x)=>s+x.gain,0);
                const barColor = over?"#F87171":full?(pct>=97?"#4ade80":pct>=90?"#A3E635":"#FBBF24"):"#7B9CFF";
                const applySwap = (sw)=>{ setDeckCards(prev => prev.map(id => id===sw.out.id ? sw.in.id : id)); };
                return (
                  <div style={{background:"rgba(255,255,255,0.03)",border:`1px solid ${barColor}44`,borderRadius:12,padding:"14px 16px",marginTop:10}}>
                    <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:8}}>
                      <span style={{fontSize:13,fontWeight:900,color:"var(--bz-ink)"}}>⚡ Efficiency</span>
                      <span style={{fontSize:12,fontWeight:800,color:barColor}}>
                        {over ? "Over cap" : full ? `${pct}% of budget` : `${have}/${SIZE} cards`}
                      </span>
                    </div>
                    {/* Meter */}
                    <div style={{height:12,borderRadius:8,background:"rgba(255,255,255,0.07)",overflow:"hidden",position:"relative"}}>
                      <div style={{width:`${Math.min(100,pct)}%`,height:"100%",background:`linear-gradient(90deg,${barColor}bb,${barColor})`,transition:"width 240ms ease"}}/>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:10.5,color:"var(--bz-ink-3)",marginTop:4}}>
                      <span><strong style={{color:"var(--bz-ink)"}}>{cur.toLocaleString()}</strong> power</span>
                      <span>cap {CAP.toLocaleString()}{!over && headroom>0 && ` · ${headroom.toLocaleString()} headroom`}</span>
                    </div>

                    {over && (
                      <div style={{fontSize:11.5,color:"#F87171",marginTop:8,lineHeight:1.5}}>
                        Total power is over the {CAP.toLocaleString()} cap. Swap a high-power card for a lower one to get legal.
                      </div>
                    )}
                    {!over && !full && (
                      <div style={{fontSize:11.5,color:"var(--bz-ink-3)",marginTop:8,lineHeight:1.5}}>
                        Add {cardsGap} more card{cardsGap===1?"":"s"} to fill the deck. Efficiency scores once all {SIZE} are in.
                      </div>
                    )}

                    {suggestions.length>0 && (
                      <div style={{marginTop:12}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                          <span style={{fontSize:11.5,fontWeight:800,color:"#4ade80"}}>↑ Optimize</span>
                          <span style={{fontSize:10.5,color:"var(--bz-ink-3)"}}>{suggestions.length} swap{suggestions.length===1?"":"s"} available · +{potentialGain.toLocaleString()} power</span>
                        </div>
                        <div style={{display:"flex",flexDirection:"column",gap:6}}>
                          {suggestions.slice(0,6).map((sw,i)=>(
                            <div key={i} style={{display:"flex",alignItems:"center",gap:8,background:"rgba(74,222,128,0.05)",border:"1px solid rgba(74,222,128,0.18)",borderRadius:8,padding:"6px 9px"}}>
                              <div style={{flex:1,minWidth:0,fontSize:11.5}}>
                                <span style={{color:"var(--bz-ink-3)",textDecoration:"line-through"}}>{sw.out.hero||sw.out.playName} <span style={{fontWeight:700}}>{sw.out.power}</span></span>
                                <span style={{color:"var(--bz-ink-3)",margin:"0 5px"}}>→</span>
                                <span style={{color:"var(--bz-ink)",fontWeight:700}}>{sw.in.hero||sw.in.playName} <span style={{color:"#4ade80"}}>{sw.in.power}</span></span>
                              </div>
                              <span style={{fontSize:10.5,fontWeight:800,color:"#4ade80",whiteSpace:"nowrap"}}>+{sw.gain}</span>
                              <button onClick={()=>applySwap(sw)} style={{background:"rgba(74,222,128,0.15)",border:"1px solid #4ade80",color:"#4ade80",borderRadius:6,padding:"3px 10px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>Swap</button>
                            </div>
                          ))}
                        </div>
                        {suggestions.length>1 && (
                          <button onClick={()=>{ setDeckCards(prev=>{ let next=prev.slice(); suggestions.forEach(sw=>{ next=next.map(id=>id===sw.out.id?sw.in.id:id); }); return next; }); }}
                            style={{marginTop:8,width:"100%",background:"linear-gradient(135deg,#4ade80,#22c55e)",border:"none",color:"#04240f",borderRadius:8,padding:"9px",fontSize:12,fontWeight:900,cursor:"pointer",fontFamily:"inherit"}}>
                            Apply all {suggestions.length} swaps · +{potentialGain.toLocaleString()} power
                          </button>
                        )}
                      </div>
                    )}
                    {full && !over && suggestions.length===0 && (
                      <div style={{fontSize:11.5,color:"#4ade80",marginTop:10,fontWeight:700}}>✓ Fully optimized — no legal upgrade would raise your power.</div>
                    )}
                  </div>
                );
              })()}
              {false && fmtOf(deckType).totalPower && deckProgress && deckProgress.have < (fmtOf(deckType).size||60) && (() => {
                const F = fmtOf(deckType);
                const SIZE = F.size||60;
                const cardsGap = Math.max(0, SIZE - (deckProgress.have||0));
                const cur = deckProgress.totalPower||0;
                const headroom = F.totalPower - cur;
                // If there's no power headroom left, the deck is stuck: it hit the cap before 60 cards.
                const stuck = headroom <= 0;
                return (
                  <div style={{background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.25)",borderRadius:10,padding:"12px 14px",marginTop:10}}>
                    <div style={{fontSize:12.5,fontWeight:800,color:"#FBBF24",marginBottom:4}}>{"\uD83D\uDD0E"} What you're missing</div>
                    <div style={{fontSize:12.5,color:"var(--bz-ink)",lineHeight:1.5}}>
                      Elite needs <strong>{SIZE} cards</strong> whose power sums to <strong>≤ {F.totalPower.toLocaleString()}</strong> (max 6 at any one power level, no duplicates).
                    </div>
                    <div style={{fontSize:12.5,color:"var(--bz-ink-3)",marginTop:6}}>
                      You have <strong style={{color:"var(--bz-ink)"}}>{deckProgress.have}</strong> cards at <strong style={{color:"var(--bz-ink)"}}>{cur.toLocaleString()}</strong> power.{" "}
                      {stuck
                        ? `You've reached the ${F.totalPower.toLocaleString()} cap but only have ${deckProgress.have} cards — to fit all ${SIZE}, swap some high-power cards for lower ones so ${SIZE} cards fit under the cap. Hit "Auto-build" to do this for you.`
                        : `Add ${cardsGap} more card${cardsGap===1?"":"s"} (you have ${headroom.toLocaleString()} power of headroom). Keep the total ≤ ${F.totalPower.toLocaleString()}.`}
                    </div>
                  </div>
                );
              })()}
              {!fmtOf(deckType).totalPower && deckProgress?.missingSlots?.length > 0 && (() => {
                      const cardById = {};
                      cards.forEach(c => { cardById[c.id] = c; });

                      // For each missing power slot, find the cheapest live listing of ANY legal card
                      // at that power. That's what actually completes the deck.
                      const slots = deckProgress.missingSlots.map(slot => {
                        const setOk = new Set(slot.sets);
                        const matches = (listings||[])
                          .map(l => {
                            const c = cardById[l.cardId];
                            if (!c) return null;
                            if ((parseFloat(c.power)||0) !== slot.power) return null;
                            if (!setOk.has(c.setName)) return null;
                            return { listing:l, card:c, price: parseFloat(l.askingPrice)||0 };
                          })
                          .filter(Boolean)
                          .sort((a,b)=>a.price-b.price);
                        return { ...slot, matches };
                      });
                      const totalNeeded = slots.reduce((n,s)=>n+s.count, 0);
                      const slotsWithStock = slots.filter(s=>s.matches.length>0).length;

                      return (
                        <div style={{marginTop:10,background:"rgba(123,156,255,0.06)",border:"1px solid rgba(123,156,255,0.25)",borderRadius:10,padding:"12px 14px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:8}}>
                            <span style={{fontSize:12.5,fontWeight:800,color:"#7B9CFF"}}>🔎 What you're missing</span>
                            <span style={{fontSize:11,color:"var(--bz-ink-3)"}}>
                              {totalNeeded} more card{totalNeeded!==1?"s":""} would complete this deck — any card at these power levels will do
                            </span>
                            {slotsWithStock>0 && (
                              <span style={{fontSize:11,fontWeight:800,color:"#4ade80",background:"rgba(74,222,128,0.12)",border:"1px solid rgba(74,222,128,0.35)",borderRadius:12,padding:"2px 9px"}}>
                                🛒 {slotsWithStock} available on the marketplace
                              </span>
                            )}
                          </div>
                          <div style={{display:"flex",flexDirection:"column",gap:6}}>
                            {slots.map(slot=>{
                              const best = slot.matches[0];
                              return (
                                <div key={slot.power} style={{padding:"8px 10px",background:best?"rgba(74,222,128,0.07)":"rgba(255,255,255,0.02)",border:`1px solid ${best?"rgba(74,222,128,0.25)":"rgba(255,255,255,0.05)"}`,borderRadius:8}}>
                                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                                    <span style={{fontSize:14,fontWeight:900,color:"#FBBF24",whiteSpace:"nowrap"}}>{slot.power}⚡</span>
                                    <span style={{fontSize:12,fontWeight:700,color:"var(--bz-ink)"}}>
                                      ×{slot.count} needed
                                    </span>
                                    <span style={{fontSize:10.5,color:"var(--bz-ink-3)",minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                      any card at this power
                                    </span>
                                    {best ? (
                                      <button onClick={()=>setActiveTab&&setActiveTab("market")}
                                        style={{marginLeft:"auto",background:"#4ade80",color:"#062b13",border:"none",borderRadius:6,padding:"4px 11px",fontSize:10.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                                        🛒 {slot.matches.length} listed · from ${best.price.toFixed(0)}
                                      </button>
                                    ) : (
                                      <span style={{marginLeft:"auto",fontSize:10,color:"var(--bz-ink-3)",whiteSpace:"nowrap"}}>none listed</span>
                                    )}
                                  </div>
                                  {best && (
                                    <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                      cheapest: {best.card.hero} · #{best.card.cardNum} · {best.card.treatment} — ${best.price.toFixed(2)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                    {/* Card source — which collection(s) the quick builder may draw from. Only shown
                        when you actually have family cards available to borrow. */}
                    {/* Set restriction — pick specific sets (e.g. Alpha Trilogy = Alpha-era only). Empty = all sets. */}
                    {(() => {
                      const allSets = Array.from(new Set(cards.map(c=>c.setName).filter(Boolean))).sort();
                      if(allSets.length===0) return null;
                      const toggleSet = s => setDeckGoalSets(prev => { const n=new Set(prev); if(n.has(s)) n.delete(s); else n.add(s); return n; });
                      // A wall of set chips was the biggest source of clutter here. Collapse it to a
                      // single line saying what's selected; the checklist only opens if you want it.
                      const setLabel = deckGoalSets.size===0 ? "All sets"
                        : deckGoalSets.size===1 ? Array.from(deckGoalSets)[0]
                        : `${deckGoalSets.size} sets`;
                      return (
                        <div style={{marginTop:10}}>
                          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                            <span style={{fontSize:11,color:"var(--bz-ink-3)",fontWeight:700}}>Sets:</span>
                            <button onClick={()=>setSetsOpen(v=>!v)}
                              style={{...inp,width:"auto",fontSize:11,padding:"5px 10px",cursor:"pointer",fontWeight:700,color:deckGoalSets.size?"#7B9CFF":"rgba(255,255,255,0.55)"}}>
                              {setLabel} <span style={{fontSize:9,opacity:0.7}}>{setsOpen?"\u25B2":"\u25BC"}</span>
                            </button>
                            {deckGoalSets.size>0 && (
                              <button onClick={()=>setDeckGoalSets(new Set())}
                                style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:8,padding:"5px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Clear</button>
                            )}
                          </div>
                          {setsOpen && (
                            <div style={{marginTop:8,padding:"10px 12px",background:"rgba(0,0,0,0.3)",border:"1px solid var(--bz-line-2)",borderRadius:10,display:"flex",gap:6,flexWrap:"wrap"}}>
                              {allSets.map(s2=>{
                                const on=deckGoalSets.has(s2);
                                return <button key={s2} onClick={()=>toggleSet(s2)} style={{background:on?"rgba(123,156,255,0.18)":"transparent",border:`1px solid ${on?"#7B9CFF":"var(--bz-line-2)"}`,color:on?"#7B9CFF":"var(--bz-ink-3)",borderRadius:14,padding:"4px 11px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{on?"\u2713 ":""}{s2}</button>;
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {deckProgress.have>0 && (
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginTop:12,paddingTop:12,borderTop:"1px solid rgba(255,255,255,0.06)"}}>
                        <button onClick={()=>setProgressExpanded(v=>!v)} style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.12)",color:"#ccc",borderRadius:8,padding:"7px 13px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                          {progressExpanded?"▲ Hide":"▼ Show"} the {deckProgress.have} card{deckProgress.have!==1?"s":""}
                        </button>
                        <button onClick={()=>{
                          const ids=(deckProgress.cards||[]).map(c=>c.id);
                          setDeckCards(ids);
                          const nm=[Array.from(deckGoalW).join(" + "), Array.from(deckGoalT).join(" + ")].filter(Boolean).join(" ");
                          setDeckName(nm?`My ${nm} Deck`:(done?"My Complete Deck":"My Deck"));
                          if(typeof window!=="undefined") window.scrollTo({top:0,behavior:"smooth"});
                        }} style={{background:done?"linear-gradient(135deg,#4ade80,#22c55e)":"linear-gradient(135deg,#E8317A,#7B2FF7)",border:"none",color:done?"#062b13":"#fff",borderRadius:8,padding:"7px 15px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                          ⚡ {done?"Build this deck":`Build with these ${deckProgress.have}`}
                        </button>
                        <span style={{fontSize:10.5,color:"#999"}}>loads them into My Deck → name it → Save</span>
                        {deckProgress.familyCount>0 && <span style={{fontSize:10.5,color:"#C084FC",fontWeight:700}}>👪 includes {deckProgress.familyCount} borrowed family card{deckProgress.familyCount!==1?"s":""} (auto-logged to your paper trail)</span>}
                        {deckProgress.treatUsed && deckProgress.treatUsed.length>0 && (
                          <div style={{fontSize:10.5,color:"var(--bz-ink-3)",width:"100%",marginTop:4,lineHeight:1.6}}>
                            {deckProgress.avgPower>0 && <><strong style={{color:"#4ade80"}}>Avg power: {deckProgress.avgPower}</strong>{" · "}<strong style={{color:"#4ade80"}}>Total: {deckProgress.totalPower.toLocaleString()}</strong>{" — "}</>}
                            <span style={{color:"var(--bz-ink-3)"}}>Inserts: {deckProgress.treatUsed.slice(0,4).map(x=>`${x.treatment} (${x.count})`).join(" · ")}{deckProgress.treatUsed.length>4?` +${deckProgress.treatUsed.length-4} more`:""}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {progressExpanded && deckProgress.cards && (
                      <div style={{marginTop:12,maxHeight:280,overflowY:"auto",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(78px,1fr))",gap:6,padding:"4px 2px"}}>
                        {deckProgress.cards.map(c=>(
                          <div key={c.id} title={`${c.hero} · ${c.power} · ${c.weapon||""} · ${c.treatment||""}`} style={{position:"relative",borderRadius:6,overflow:"hidden",border:"1px solid rgba(255,255,255,0.1)",aspectRatio:"3/4",background:"var(--bz-bg)"}}>
                            {c.imageUrl
                              ? <img src={c.imageUrl} alt={c.hero} loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                              : <div style={{width:"100%",height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:3,textAlign:"center"}}><div style={{fontSize:9,fontWeight:800,color:"#aaa",lineHeight:1.1}}>{c.hero}</div></div>}
                            <div style={{position:"absolute",bottom:0,left:0,right:0,background:"rgba(0,0,0,0.75)",fontSize:8,fontWeight:800,color:"#fff",padding:"1px 3px",textAlign:"center"}}>{c.power}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="filter-bar" style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:14,padding:"12px 16px",backdropFilter:"blur(10px)"}}>
                <input value={deckSearch} onChange={e=>setDeckSearch(e.target.value)} placeholder="Search hero, treatment..." style={{...inp,flex:1,minWidth:160}}/>
                <select value={deckType} onChange={e=>{ const v=e.target.value; setDeckType(v); if(!(v==="spec"||v==="vegasbaby"||v==="apex")) setDeckMaxMode(false); }} style={{...inp,width:"auto",cursor:"pointer",fontWeight:700,color:deckType==="spec"?"#FBBF24":deckType==="apexmadness"?"#E8317A":deckType==="apex"?"#A855F7":deckType==="elite"?"#4ade80":deckType==="specplus"?"#38BDF8":deckType==="blast"?"#F472B6":"rgba(255,255,255,0.4)"}}>
                  {/* Generated from DECK_FORMATS so this can't drift out of sync with the rules. */}
                  {Object.entries(DECK_FORMATS).map(([k,f])=><option key={k} value={k}>{f.label}</option>)}
                  <optgroup label="── Events ──">
                    <option value="vegasbaby">🎲 Vegas Baby (SPEC)</option>
                  </optgroup>
                </select>
                {/* Multi-select filters — same pattern as the quick builder: a toggle button that
                    opens a pill grid, so you can pick SEVERAL weapons / sets / treatments at once
                    instead of one at a time. */}
                {(() => {
                  const toggleIn = (setter) => (v) => setter(prev => { const n=new Set(prev); n.has(v)?n.delete(v):n.add(v); return n; });
                  const tW = toggleIn(setDeckFilterW), tS = toggleIn(setDeckFilterS), tT = toggleIn(setDeckFilterT), tP = toggleIn(setDeckFilterP);
                  // Checkbox rows, not pills. "Select everything, then untick the one I don't want"
                  // is the actual workflow, and pills make that a hunt \u2014 you cannot tell selected from
                  // unselected at a glance across 30 of them, and there is no way to select all.
                  const filterPanel = (items, sel, setter, toggle) => (
                    <div style={{width:"100%",marginTop:6,background:"rgba(0,0,0,0.25)",border:"1px solid var(--bz-line)",borderRadius:9,overflow:"hidden"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"7px 10px",borderBottom:"1px solid var(--bz-line)",background:"rgba(255,255,255,0.03)"}}>
                        <span style={{fontSize:10.5,fontWeight:800,color:"var(--bz-ink-3)"}}>
                          {sel.size===0 ? "Showing all" : `${sel.size} of ${items.length} selected`}
                        </span>
                        <span style={{display:"flex",gap:6}}>
                          <button onClick={()=>setter(new Set(items))}
                            style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:6,padding:"3px 9px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                            Select all
                          </button>
                          <button onClick={()=>setter(new Set())}
                            style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:6,padding:"3px 9px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                            None
                          </button>
                        </span>
                      </div>
                      <div style={{maxHeight:210,overflowY:"auto",overscrollBehavior:"contain"}}>
                        {items.map(v => {
                          const on = sel.has(v);
                          return (
                            <label key={v} onClick={e=>{e.preventDefault(); toggle(v);}}
                              style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",cursor:"pointer",
                                background:on?"rgba(232,49,122,0.10)":"transparent",
                                borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                              <span style={{width:14,height:14,flexShrink:0,borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center",
                                border:`1.5px solid ${on?"#E8317A":"var(--bz-line-2)"}`,background:on?"#E8317A":"transparent",
                                color:"#fff",fontSize:10,fontWeight:900,lineHeight:1}}>{on?"\u2713":""}</span>
                              <span style={{fontSize:11.5,fontWeight:on?800:600,color:on?"#fff":"var(--bz-ink-2)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{v}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                  const lab = (sel, none, one) => sel.size===0 ? none : sel.size===1 ? `${Array.from(sel)[0]} ${one}` : `${sel.size} ${none.replace(/^All /,"")}`;
                  return (
                    <>
                      <button onClick={()=>setFiltWOpen(v=>!v)} style={tabBtn(filtWOpen, deckFilterW.size>0)}>
                        {lab(deckFilterW,"All Weapons","only")} <span style={{fontSize:9,opacity:0.7}}>{filtWOpen?"▲":"▼"}</span>
                      </button>
                      <button onClick={()=>setFiltSOpen(v=>!v)} style={tabBtn(filtSOpen, deckFilterS.size>0)}>
                        {lab(deckFilterS,"All Sets","only")} <span style={{fontSize:9,opacity:0.7}}>{filtSOpen?"▲":"▼"}</span>
                      </button>
                      <button onClick={()=>setFiltTOpen(v=>!v)} style={tabBtn(filtTOpen, deckFilterT.size>0)}>
                        {lab(deckFilterT,"All Treatments","only")} <span style={{fontSize:9,opacity:0.7}}>{filtTOpen?"▲":"▼"}</span>
                      </button>
                      {/* Power belongs in this row with the others. It used to live ~150 lines further
                          down in a separate block, so it rendered detached from the filter bar and
                          read as missing. Same button, same panel helper, same Clear behaviour. */}
                      <button onClick={()=>setFiltPOpen(v=>!v)} style={tabBtn(filtPOpen, deckFilterP.size>0)}>
                        {lab(deckFilterP,"All Power","only")} <span style={{fontSize:9,opacity:0.7}}>{filtPOpen?"\u25B2":"\u25BC"}</span>
                      </button>
                      {(deckFilterW.size>0||deckFilterS.size>0||deckFilterT.size>0||deckFilterP.size>0) && (
                        <button onClick={()=>{setDeckFilterW(new Set());setDeckFilterS(new Set());setDeckFilterT(new Set());setDeckFilterP(new Set());}}
                          style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:8,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Clear filters</button>
                      )}
                      {filtWOpen && filterPanel(weapons, deckFilterW, setDeckFilterW, tW)}
                      {filtSOpen && filterPanel(sets, deckFilterS, setDeckFilterS, tS)}
                      {filtTOpen && filterPanel(treatments, deckFilterT, setDeckFilterT, tT)}
                      {/* Same shared panel as the others, so Power looks and behaves identically
                          rather than being a bespoke one-off. Sorted high to low. */}
                      {filtPOpen && filterPanel(powers, deckFilterP, setDeckFilterP, tP)}
                    </>
                  );
                })()}
                {/* Card source — matches the quick builder's selector. One choice, no conflicting
                    toggles: All cards / My collection / Mine + family / Family only. */}
                {(() => {
                  const famCount = Object.keys(familyOwnerByCard).length;
                  // Derive the current mode from the two underlying flags.
                  const mode = deckFamilyOnly ? "family" : (deckOwnedOnly ? (deckMineOnly ? "mine" : "both") : "all");
                  const setMode = (m) => {
                    if(!user && m!=="all"){ setSigningIn(true); return; }
                    if(m==="all")    { setDeckOwnedOnly(false); setDeckFamilyOnly(false); }
                    if(m==="mine")   { setDeckOwnedOnly(true);  setDeckFamilyOnly(false); setDeckMineOnly(true); }
                    if(m==="both")   { setDeckOwnedOnly(true);  setDeckFamilyOnly(false); setDeckMineOnly(false); }
                    if(m==="family") { setDeckOwnedOnly(false); setDeckFamilyOnly(true); }
                  };
                  const opts = [["all","All cards"], ["mine","My collection"]];
                  if(famCount>0) opts.push(["both",`Mine + family`], ["family",`👪 Family (${famCount})`]);
                  return (
                    <div style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap"}}>
                      {opts.map(([id,label])=>{
                        const on = mode===id;
                        const col = id==="family" ? "#C084FC" : id==="all" ? "rgba(255,255,255,0.5)" : "#4ade80";
                        return (
                          <button key={id} onClick={()=>setMode(id)}
                            style={{background:on?(id==="all"?"rgba(255,255,255,0.08)":`${col}22`):"transparent",border:`1px solid ${on?col:"var(--bz-line-2)"}`,color:on?col:"rgba(255,255,255,0.45)",borderRadius:14,padding:"6px 12px",fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                            {label}{on && id==="mine" && ownedCount>0 ? ` (${ownedCount})` : ""}
                          </button>
                        );
                      })}
                      {/* Which family member to borrow from — only when family is in the mix. */}
                      {(mode==="both"||mode==="family") && familyList.length>=1 && (
                        <select value={deckFamilyMember} onChange={e=>setDeckFamilyMember(e.target.value)}
                          style={{...inp,width:"auto",fontSize:11,padding:"5px 8px",cursor:"pointer",fontWeight:700,color:deckFamilyMember==="all"?"var(--bz-ink-2)":"#C084FC",marginLeft:2}}>
                          <option value="all">From: all family</option>
                          {familyList.map(f=>(<option key={f.uid} value={f.uid}>From: {f.name}</option>))}
                        </select>
                      )}
                    </div>
                  );
                })()}
                <span style={{fontSize:11,color:"rgba(255,255,255,0.2)"}}>{deckAvail.length}</span>
              </div>
              {(()=>{
                if(deckType==="vegasbaby"){
                  const ev=BOBA_EVENTS.vegasbaby;
                  return (
                    <div style={{background:`${ev.color}10`,border:`1px solid ${ev.color}40`,borderRadius:10,padding:"12px 14px"}}>
                      <div style={{fontSize:13,fontWeight:900,color:ev.color,display:"flex",alignItems:"center",gap:6}}>🎲 {ev.name} <span style={{fontSize:9,fontWeight:700,background:`${ev.color}22`,color:ev.color,borderRadius:4,padding:"2px 6px"}}>EVENT · SPEC</span></div>
                      <div style={{fontSize:11,color:ev.color,fontWeight:700,fontStyle:"italic",margin:"4px 0 7px"}}>{ev.tagline}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.55)",lineHeight:1.5,marginBottom:8}}>{ev.description}</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:"4px 14px",marginBottom:8}}>
                        {ev.rules.map((r,i)=>(<span key={i} style={{fontSize:11,color:"rgba(255,255,255,0.55)",display:"flex",alignItems:"center",gap:5}}><span style={{color:ev.color}}>•</span>{r}</span>))}
                      </div>
                      <div style={{fontSize:10.5,color:ev.color,background:`${ev.color}14`,borderRadius:6,padding:"6px 9px",fontWeight:600,lineHeight:1.4}}>🏆 {ev.region}</div>
                    </div>
                  );
                }
                let fmt = {
                  none:{c:"rgba(255,255,255,0.5)",icon:"\uD83D\uDCCB",title:"No Restrictions",rules:["Up to 60 cards","No duplicate cards","Max 6 cards at any power level"]},
                  spec:{c:"#FBBF24",icon:"\uD83D\uDEE1\uFE0F",title:"Spec",rules:["Up to 60 cards","Power ceiling: 160 max","No power floor","Max 6 cards at any power level"]},
                  apex:{c:"#A855F7",icon:"\u26A1",title:"Apex",rules:["Up to 60 cards","No power cap","Max 6 cards at any power level"]},
                  specplus:{c:"#38BDF8",icon:"\u2795",title:"Spec+",rules:[
                    "Up to 70 Heroes",
                    "60 Heroes at \u2264160 power are REQUIRED \u2014 this Spec-legal core must be complete",
                    "Then up to 10 higher Heroes: 165 (\u00D72), 170 (\u00D72), 175/180/185/190/195/200 (\u00D71 each)",
                    "Nothing above 200 power",
                    "Max 6 cards at any power level",
                  ]},
                  elite:{c:"#4ade80",icon:"\uD83D\uDC8E",title:"Elite",rules:[
                    "Up to 60 cards",
                    "8,250 TOTAL power \u2014 the whole deck must sum to 8,250 or less",
                    "No Trainer cards (2024 Battle Trainer Kit is excluded)",
                    "Starter cards allowed",
                    "Max 6 cards at any power level",
                  ]},
                  brawl:{c:"#EF4444",icon:"\uD83E\uDD4A",title:"Brawl",rules:["Up to 60 cards","Every card must have the Brawl weapon","Max 6 cards at any power level"]},
                  gghilo:{c:"#C084FC",icon:"\uD83D\uDC75",title:"GG HiLo (Captain)",rules:[
                    "Up to 60 cards",
                    "No power cap",
                    "ONLY these three groups are legal:",
                    "\u2003\u2022 Grandma\u2019s Linoleum Battlefoil (min 10)",
                    "\u2003\u2022 Great Grandma Linoleum Battlefoil (min 10)",
                    "\u2003\u2022 Any card with the Gum weapon (min 10) \u2014 any insert, not just Bubblegum",
                    "Max 6 cards at any power level",
                  ]},
                  gghilo_coach:{c:"#60A5FA",icon:"\uD83D\uDCCB",title:"GG HiLo (Coach)",rules:[
                    "Up to 60 cards",
                    "Same legal pool as Captain \u2014 auto-build picks your LOWEST power cards",
                    "ONLY these three groups are legal:",
                    "\u2003\u2022 Grandma\u2019s Linoleum Battlefoil (min 10)",
                    "\u2003\u2022 Great Grandma Linoleum Battlefoil (min 10)",
                    "\u2003\u2022 Any card with the Gum weapon (min 10) \u2014 any insert, not just Bubblegum",
                    "Max 6 cards at any power level",
                  ]},
                  powerglove:{c:"#FB923C",icon:"\uD83E\uDDE4",title:"Power Glove",rules:[
                    "Exactly 60 cards",
                    "Power Glove treatment only",
                    "Every card must be UNIQUE \u2014 no duplicates at all",
                    "Max 6 cards at any power level",
                  ]},
                  blast:{c:"#F472B6",icon:"\uD83D\uDCA5",title:"Blast",rules:[
                    "30-card deck",
                    "2025 Alpha Blast cards only",
                    "Max 3 Heroes at any power level",
                  ]},
                  tecmohilo:{c:"#FBBF24",icon:"\uD83C\uDFC8",title:"Tecmo HiLo \u2014 Captain",rules:[
                    "Up to 60 cards",
                    "Tecmo Bowl cards only",
                    "No power cap \u2014 build the HIGHEST power deck you can",
                    "Max 6 cards at any power level",
                  ]},
                  tecmohilo_coach:{c:"#7B9CFF",icon:"\uD83C\uDFC8",title:"Tecmo HiLo \u2014 Coach",rules:[
                    "Up to 60 cards",
                    "Tecmo Bowl cards only",
                    "No power cap \u2014 build the LOWEST power deck you can",
                    "Max 6 cards at any power level",
                  ]},
                  apexmadness:{c:"#E8317A",icon:"\uD83D\uDD25",title:"Apex Madness",rules:[
                    "Start from a 60-card Spec deck (\u2264160 power)",
                    "Every 10 matching Inserts in your deck unlocks one Apex Hero (>160) \u2014 max 6",
                    "Each Foil Hot Dog you add (up to 4) unlocks one more Apex Hero",
                    "A fully optimised deck is 70 Heroes (60 + 6 + 4)",
                    "Max 6 cards at any power level",
                  ]},
                }[deckType] || {};
                // A format missing from the table above used to fall through to {} and then crash on
                // fmt.rules.map. Fall back to the shared rule summaries, and bail out entirely if even
                // those are absent \u2014 a missing blurb must never take down the deck builder.
                if (!fmt.rules) {
                  const shared = SHARED_DECK_RULES[deckType];
                  if (!shared) return null;
                  fmt = { c: fmt.c || "#E8317A", label: fmtOf(deckType).label, rules: shared };
                }
                return (
                  <div style={{background:`${fmt.c}11`,border:`1px solid ${fmt.c}33`,borderRadius:10,padding:"10px 13px"}}>
                    <div style={{fontSize:11.5,fontWeight:800,color:fmt.c,marginBottom:6,display:"flex",alignItems:"center",gap:6}}>{fmt.icon} {fmt.title} — rules</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:"4px 14px"}}>
                      {fmt.rules.map((r,i)=>(
                        <span key={i} style={{fontSize:11,color:"rgba(255,255,255,0.55)",display:"flex",alignItems:"center",gap:5}}><span style={{color:fmt.c}}>•</span>{r}</span>
                      ))}
                    </div>
                    {/* Live legality. Blocking illegal ADDS isn't enough — a deck can be illegal by
                        what it's MISSING (Spec+ without its core, GG HiLo without 10 of each). */}
                    {!deckLegality.empty && (
                      deckLegality.ok ? (
                        <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${fmt.c}22`,fontSize:11.5,fontWeight:800,color:"#4ade80"}}>
                          ✅ Legal {fmt.title} deck
                          {deckLegality.total>0 && <span style={{color:"rgba(255,255,255,0.4)",fontWeight:600,marginLeft:8}}>{deckLegality.total.toLocaleString()} total power</span>}
                        </div>
                      ) : (
                        <div style={{marginTop:8,paddingTop:8,borderTop:"1px solid rgba(239,68,68,0.25)"}}>
                          <div style={{fontSize:11.5,fontWeight:800,color:"#EF4444",marginBottom:3}}>⚠️ Not legal yet</div>
                          {deckLegality.problems.map((p,i)=>(
                            <div key={i} style={{fontSize:11,color:"#FCA5A5",lineHeight:1.6}}>• {p}</div>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                );
              })()}
                {/* Foil Hot Dog counter \u2014 Madness only. Entered by hand because these cards can't be
                    reliably detected and don't have to differ from each other. Each one unlocks an
                    Apex slot, capped at 4. */}
                {isAM && (
                  <div style={{marginTop:10,background:"rgba(232,49,122,0.08)",border:"1px solid rgba(232,49,122,0.3)",borderRadius:10,padding:"11px 13px"}}>
                    <div style={{fontSize:12,fontWeight:800,color:"#E8317A",marginBottom:3}}>{"\uD83C\uDF2D"} Foil Hot Dogs in this deck</div>
                    <div style={{fontSize:10.5,color:"var(--bz-ink-3)",marginBottom:8}}>Each one unlocks an extra Apex Hero slot (max 4).</div>
                    <div style={{display:"flex",gap:6}}>
                      {[0,1,2,3,4].map(n=>{
                        const on = (parseInt(foilDogs)||0) === n;
                        return (
                          <button key={n} onClick={()=>setFoilDogs(n)}
                            style={{flex:1,background:on?"#E8317A":"transparent",border:"1px solid "+(on?"#E8317A":"#444"),
                              color:on?"#fff":"#999",borderRadius:8,padding:"8px 0",fontSize:14,fontWeight:900,
                              cursor:"pointer",fontFamily:"inherit"}}>
                            {n}
                          </button>
                        );
                      })}
                    </div>
                    {(parseInt(foilDogs)||0) > 0 && (
                      <div style={{fontSize:10.5,color:"#4ade80",marginTop:7,fontWeight:700}}>
                        +{Math.min(4,parseInt(foilDogs)||0)} Apex slot{(parseInt(foilDogs)||0)===1?"":"s"} unlocked
                      </div>
                    )}
                  </div>
                )}
              {/* The card list scrolls ITSELF rather than growing the page. It used to have no height
                  limit, so picking a treatment shrank the list, the page got shorter, and the browser
                  clamped your scroll position back to the top \u2014 which reads as "it jumped me to the
                  top every time I select a filter". Owning the scroll keeps your place. */}
              {/* Generous cap so the cards are actually big enough to see. An earlier fix capped this
                  at calc(100vh - 300px), but this list is in normal page flow with a tall filter panel
                  above it, so on shorter windows almost nothing was left. minHeight guarantees a
                  usable window regardless; the scroll still keeps filtering from moving the page. */}
              <div className="deck-pb-cardlist" style={{paddingRight:4,minHeight:420,maxHeight:isMobile?"none":"78vh",overflowY:isMobile?"visible":"auto",overflowX:"hidden",overscrollBehavior:"contain"}}>
                {deckAvail.length===0?<div style={{padding:40,textAlign:"center",color:"rgba(255,255,255,0.2)"}}>No cards match filters</div>:
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(auto-fill,minmax(95px,1fr))":"repeat(auto-fill,minmax(120px,1fr))",gap:10}}>
                  {deckVisible.map((c)=>{
                    const {ok,reason,free,committed}=canAddToDeck(c),wc=WEAPON_COLORS[canonWeapon(c.weapon)]||"#444",isOwned=owned&&owned[c.id];
                    const _fam = familyOwnerByCard[c.id];
                    return (
                      <div key={c.id} onClick={()=>{if(ok)setDeckCards(p=>[...p,c.id]);}} title={!ok?reason:(_fam?`Borrow ${c.hero} from ${_fam.name}`:`Add ${c.hero}`)}
                        style={{position:"relative",aspectRatio:"3/4",borderRadius:10,overflow:"hidden",cursor:ok?"pointer":"not-allowed",opacity:ok?1:0.4,border:`1.5px solid ${ok?(isOwned?"#4ade8055":_fam?"#C084FC66":"rgba(255,255,255,0.08)"):"rgba(232,49,122,0.3)"}`,background:"var(--bz-s1)",transition:"transform 0.15s ease, border-color 0.15s ease"}}
                        onMouseEnter={e=>{if(ok){e.currentTarget.style.transform="translateY(-4px)";e.currentTarget.style.borderColor=wc;}}}
                        onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.borderColor=ok?(isOwned?"#4ade8055":_fam?"#C084FC66":"rgba(255,255,255,0.08)"):"rgba(232,49,122,0.3)";}}>
                        {_fam && <div style={{position:"absolute",top:5,left:5,zIndex:5,background:"rgba(192,132,252,0.92)",color:"#fff",borderRadius:6,padding:"2px 6px",fontSize:8,fontWeight:800,backdropFilter:"blur(4px)"}}>👪 {(_fam.name||"").split(" ")[0]}</div>}
                        {ok && committed>0 && free>0 && <div style={{position:"absolute",top:5,right:5,zIndex:5,background:"rgba(74,222,128,0.9)",color:"#06240f",fontSize:9,fontWeight:800,borderRadius:5,padding:"2px 5px"}}>{free} free</div>}
                        {c.imageUrl
                          ? <img src={c.imageUrl} alt={c.hero} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                          : <div style={{width:"100%",height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:6,textAlign:"center"}}><div style={{fontSize:11,fontWeight:800,color:wc}}>{c.hero}</div><div style={{fontSize:8,color:"rgba(255,255,255,0.3)",marginTop:3}}>{c.treatment}</div></div>}
                        {/* gradient + info overlay */}
                        <div style={{position:"absolute",inset:0,background:"linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.1) 38%, transparent 60%)",pointerEvents:"none"}}/>
                        <div style={{position:"absolute",left:0,right:0,bottom:0,padding:"7px 8px",pointerEvents:"none"}}>
                          <div style={{fontSize:11,fontWeight:800,color:"#fff",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",textShadow:"0 1px 3px rgba(0,0,0,0.8)"}}>{c.hero}</div>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:2}}>
                            <span style={{fontSize:9,color:"rgba(255,255,255,0.6)"}}>#{c.cardNum}</span>
                            <span style={{fontSize:13,fontWeight:900,color:wc,textShadow:"0 1px 3px rgba(0,0,0,0.9)"}}>{c.power}</span>
                          </div>
                        </div>
                        {/* top badges */}
                        {c.weapon&&<div style={{position:"absolute",top:6,left:6,fontSize:8,fontWeight:800,color:"#fff",background:wc+"cc",borderRadius:4,padding:"2px 6px",pointerEvents:"none"}}>{c.weapon}</div>}
                        {isOwned&&<div style={{position:"absolute",top:6,right:6,fontSize:11,pointerEvents:"none",filter:"drop-shadow(0 1px 2px rgba(0,0,0,0.8))"}}>{String.fromCharCode(9989)}</div>}
                        {/* add affordance on hover */}
                        {ok&&<div className="deck-add-badge" style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:38,height:38,borderRadius:"50%",background:"rgba(74,222,128,0.95)",color:"#000",fontSize:24,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center",opacity:0,transition:"opacity 0.15s",pointerEvents:"none",boxShadow:"0 4px 16px rgba(0,0,0,0.5)"}}>+</div>}
                        {!ok&&<div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",fontSize:9,fontWeight:700,color:"#fff",background:"rgba(232,49,122,0.9)",borderRadius:6,padding:"4px 8px",textAlign:"center",maxWidth:"90%",lineHeight:1.3,pointerEvents:"none"}}>{/^In /.test(reason||"")?"\uD83D\uDD12 ":""}{reason}</div>}
                      </div>
                    );
                  })}
                  </div>
                }
                {deckVisible.length<deckAvail.length && (
                  <button onClick={()=>setDeckPage(p=>p+1)} style={{width:"100%",marginTop:12,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.6)",borderRadius:10,padding:"11px 0",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Load more ({deckAvail.length-deckVisible.length} more)</button>
                )}
              </div>
            </div>
            <div className="deck-pb-panel" style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:16,padding:"20px 18px",backdropFilter:"blur(10px)",width:isMobile?"100%":"clamp(280px,30%,360px)",flexShrink:0,overflowY:"auto",minHeight:0}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
                <div style={{fontSize:15,fontWeight:800,color:"#fff",letterSpacing:"-0.2px"}}>{deckName}</div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  {inDeck.length>0 && <button onClick={()=>{setFanMode("grid");setFanDeck({name:deckName,cards:inDeck});}} title="Expand to view your deck" style={{background:"rgba(123,156,255,0.12)",border:"1px solid rgba(123,156,255,0.35)",color:"#7B9CFF",borderRadius:16,padding:"3px 10px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>⛶ Expand</button>}
                  {inDeck.length>0 && <button onClick={()=>setRegSheetOpen(true)} title="Generate BoBA registration sheet" style={{background:"rgba(232,49,122,0.12)",border:"1px solid rgba(232,49,122,0.35)",color:"#E8317A",borderRadius:16,padding:"3px 10px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>📋 Reg Sheet</button>}
                  <span style={{fontSize:12,fontWeight:800,color:inDeck.length===DECK_SIZE?"#4ade80":"#FBBF24",background:inDeck.length===DECK_SIZE?"rgba(74,222,128,0.1)":"rgba(251,191,36,0.1)",borderRadius:20,padding:"3px 11px"}}>{inDeck.length}/{DECK_SIZE}</span>
                </div>
              </div>
              {saveDeckTab && (
                <div style={{marginBottom:14}}>
                  <input value={deckName} onChange={e=>setDeckName(e.target.value)} placeholder="Deck name" style={{...inp,width:"100%",marginBottom:8,fontSize:12,padding:"7px 10px"}}/>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={saveDeckTab} disabled={deckSaving||deckCards.length===0} style={{flex:1,background:deckSaved?"#0a2a0a":"#0a1a0a",border:`1px solid ${deckSaved?"#4ade80":"#4ade8044"}`,color:"#4ade80",borderRadius:8,padding:"8px 0",fontSize:12,fontWeight:700,cursor:deckCards.length===0?"not-allowed":"pointer",fontFamily:"inherit",opacity:deckCards.length===0?0.4:1}}>
                      {deckSaving?"Saving...":deckSaved?"✓ Saved":"💾 Save Deck"}</button>
                    <button onClick={newDeckTab} style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ New</button>
                  </div>
                  {inDeck.length>0 && (
                    <button onClick={()=>{ setPickChecked({}); setShowPickList(true); }} style={{width:"100%",marginTop:8,background:"rgba(123,156,255,0.1)",border:"1px solid rgba(123,156,255,0.4)",color:"#7B9CFF",borderRadius:8,padding:"8px 0",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>📋 Pick List ({inDeck.length})</button>
                  )}
                  {(savedDecks.length>0 || familyDecks.length>0) && (
                    <>
                      {familyDecks.length>0 && (
                        <div style={{display:"flex",gap:6,marginTop:10,marginBottom:2}}>
                          {[["mine","My decks"],["family","\uD83D\uDC6A Family"]].map(([v,l])=>(
                            <button key={v} onClick={()=>setDeckListView(v)}
                              style={{background:deckListView===v?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${deckListView===v?"#E8317A":"rgba(255,255,255,0.1)"}`,color:deckListView===v?"#E8317A":"rgba(255,255,255,0.45)",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
                          ))}
                        </div>
                      )}
                      {deckListView==="family" && familyDecks.length>0 ? (
                        <div style={{display:"flex",flexDirection:"column",gap:12,marginTop:8}}>
                          {familyDecks.map(g => (
                            <div key={g.friendUid}>
                              <div style={{fontSize:10.5,fontWeight:800,color:"#4ade80",marginBottom:6}}>{g.friendName}</div>
                              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                                {g.decks.map(d => (
                                  <a key={d.id} href={`/deck/${d.id}`} target="_blank" rel="noreferrer"
                                    title={`${(d.cardCount ?? (d.cardIds||[]).length)} cards \u2014 opens read-only`}
                                    style={{display:"flex",alignItems:"center",gap:5,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,padding:"4px 9px",textDecoration:"none"}}>
                                    <span style={{color:"#ccc",fontSize:11.5,fontWeight:700,maxWidth:130,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.name||"Untitled"}</span>
                                    <span style={{fontSize:9.5,color:"rgba(255,255,255,0.3)"}}>{(d.cardCount ?? (d.cardIds||[]).length)}</span>
                                  </a>
                                ))}
                              </div>
                            </div>
                          ))}
                          <div style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>Family decks open read-only in a new tab.</div>
                        </div>
                      ) : (
                    <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:8}}>
                      {savedDecks.length>0 && (()=>{
                        const reg = savedDecks.filter(d=>d.registered).length;
                        const pending = savedDecks.length - reg;
                        return pending===0
                          ? <span style={{width:"100%",fontSize:10.5,color:"#4ade80",fontWeight:700}}>{"\u2713 all "+savedDecks.length+" registered"}</span>
                          : <span style={{width:"100%",fontSize:10.5,color:"#FBBF24",fontWeight:700}}>{reg+"/"+savedDecks.length+" registered \u00B7 "+pending+" to register"}</span>;
                      })()}
                      {savedDecks.map(d=>(
                        <div key={d.id} style={{display:"flex",alignItems:"center",gap:3,background:deckLoadId===d.id?"#1A1A2E":"var(--bz-s2)",border:`1px solid ${d.registered?"#4ade80":(deckLoadId===d.id?"#7B9CFF":"var(--bz-line-2)")}`,borderRadius:7,padding:"3px 8px"}}>
                          <button onClick={()=>toggleDeckRegistered(d)} title={d.registered?"Registered — click to unmark":"Mark as registered"}
                            style={{background:"none",border:"none",cursor:"pointer",fontSize:12,lineHeight:1,padding:"0 2px",color:d.registered?"#4ade80":"#555"}}>
                            {d.registered?"\u2713":"\u25CB"}
                          </button>
                          <button onClick={()=>loadDeckTab(d)} style={{background:"none",border:"none",color:deckLoadId===d.id?"#7B9CFF":"#888",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{d.name} <span style={{color:"var(--bz-ink-3)",fontWeight:400}}>({d.cardCount})</span></button>
                          <button onClick={()=>{ const objs=(d.cardIds||[]).map(id=>cards.find(c=>c.id===id)).filter(Boolean); if(objs.length){ setFanMode("grid"); setFanDeck({name:d.name,cards:objs}); } }} title="Expand to view this deck" style={{background:"none",border:"none",color:"#7B9CFF",cursor:"pointer",fontSize:12,lineHeight:1,padding:"0 1px"}}>⛶</button>
                          {/* Share — copies a public link. Anyone can open it, no login, and it shows
                              the deck plus the format rules it's built to and whether it's legal. */}
                          <button onClick={()=>{
                            const url = `${window.location.origin}/deck/${d.id}`;
                            navigator.clipboard.writeText(url).then(()=>{
                              setSharedDeckId(d.id);
                              setTimeout(()=>setSharedDeckId(null), 2000);
                            });
                          }} title="Copy a shareable link to this deck"
                            style={{background:"none",border:"none",color: sharedDeckId===d.id ? "#4ade80" : "#8a8a8a",cursor:"pointer",fontSize:12,lineHeight:1,padding:"0 2px"}}>
                            {sharedDeckId===d.id ? "✓" : "🔗"}
                          </button>
                          {familyList.length>0 && (
                            <div style={{position:"relative"}}>
                              <button onClick={()=>setGiveDeckFor(giveDeckFor===d.id?null:d.id)} title="Give this deck to a family member (moves it to their account)"
                                style={{background:"none",border:"none",color: giveDeckFor===d.id ? "#C084FC" : "#8a8a8a",cursor:"pointer",fontSize:12,padding:"0 2px"}}>🎁</button>
                              {giveDeckFor===d.id && (
                                <div style={{position:"absolute",right:0,top:22,zIndex:50,background:"var(--bz-s2)",border:"1px solid #3a3a3a",borderRadius:8,padding:6,minWidth:150,boxShadow:"0 8px 24px rgba(0,0,0,0.5)"}}>
                                  <div style={{fontSize:9.5,fontWeight:700,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:0.6,padding:"3px 6px 5px"}}>Give deck to…</div>
                                  {familyList.map(fm => (
                                    <button key={fm.uid} onClick={()=>{ setGiveDeckFor(null); giveDeckToFamily && giveDeckToFamily(d, fm.uid, fm.name); }}
                                      style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",color:"var(--bz-ink)",fontSize:12,fontWeight:600,padding:"7px 8px",borderRadius:6,cursor:"pointer",fontFamily:"inherit"}}
                                      onMouseEnter={e=>e.currentTarget.style.background="rgba(192,132,252,0.15)"} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                                      🎁 {fm.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          <button onClick={()=>deleteDeckTab(d.id)} style={{background:"none",border:"none",color:"#8a8a8a",cursor:"pointer",fontSize:13,lineHeight:1,padding:"0 1px"}}>×</button>
                        </div>
                      ))}
                    </div>
                      )}
                  {givenDecks.length>0 && (
                    <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid rgba(255,255,255,0.08)"}}>
                      <div style={{fontSize:9.5,fontWeight:700,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:0.6,marginBottom:6}}>Given away</div>
                      {givenDecks.map(d=>(
                        <div key={d.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6,padding:"3px 0"}}>
                          <span style={{fontSize:11,color:"#8a8a8a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                            {d.name} <span style={{color:"var(--bz-ink-3)"}}>({d.cardCount})</span>
                          </span>
                          <button onClick={()=>takeBackDeck && takeBackDeck(d)} title="Take this deck back to your account"
                            style={{background:"none",border:"none",color:"#C084FC",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit",whiteSpace:"nowrap",padding:"0 2px"}}>
                            ↩ Take back
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                    </>
                  )}
                </div>
              )}
              {inDeck.length>0 && (
                <div style={{marginBottom:16}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
                    {[{l:"Total Power",v:Math.round(dbTotalPower).toLocaleString(),c:"#E8317A"},{l:"Avg Power",v:dbAvgPower,c:"#FBBF24"},{l:"Unique Heroes",v:dbHeroes,c:"#7B9CFF"},{l:"Cards",v:`${inDeck.length}/${DECK_SIZE}`,c:inDeck.length===DECK_SIZE?"#4ade80":"#E8E8E8"}].map(({l,v,c})=>(
                      <div key={l} style={{background:"rgba(255,255,255,0.04)",borderRadius:10,padding:"11px 12px"}}>
                        <div style={{fontSize:20,fontWeight:800,color:c,letterSpacing:"-0.5px",lineHeight:1}}>{v}</div>
                        <div style={{fontSize:9.5,color:"rgba(255,255,255,0.35)",marginTop:5,textTransform:"uppercase",letterSpacing:0.8,fontWeight:600}}>{l}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",fontWeight:700,textTransform:"uppercase",letterSpacing:1.2,marginBottom:9}}>Power Curve</div>
                  <div style={{display:"flex",alignItems:"flex-end",gap:5,height:54,marginBottom:12,padding:"0 2px"}}>
                    {dbPowerCurve.map(([label,n])=>(
                      <div key={label} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                        <div style={{fontSize:9.5,color:n>0?"#FBBF24":"#3a3a3a",fontWeight:800}}>{n||"·"}</div>
                        <div style={{width:"100%",height:`${Math.max(4,(n/dbMaxCurve)*40)}px`,background:n>0?"#E8317A":"#1c1c1c",borderRadius:"4px 4px 2px 2px",transition:"height 0.3s ease"}}/>
                        <div style={{fontSize:8,color:"rgba(255,255,255,0.3)",textAlign:"center",whiteSpace:"nowrap",fontWeight:600}}>{label}</div>
                      </div>
                    ))}
                  </div>
                  {dbWeaponBreak.length>0 && (
                    <div style={{marginTop:16}}>
                      <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",fontWeight:700,textTransform:"uppercase",letterSpacing:1.2,marginBottom:9}}>Weapons</div>
                      {dbWeaponBreak.map(([w,cnt])=>{const wc=WEAPON_COLORS[w]||"#444";const pct=Math.round(cnt/inDeck.length*100);return(
                        <div key={w} style={{marginBottom:8}}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span style={{fontSize:11,color:wc,fontWeight:700}}>{w}</span><span style={{fontSize:10.5,color:"rgba(255,255,255,0.4)",fontWeight:600}}>{cnt} · {pct}%</span></div>
                          <div style={{height:5,background:"rgba(255,255,255,0.05)",borderRadius:3}}><div style={{width:`${pct}%`,height:"100%",background:wc,borderRadius:3,transition:"width 0.3s ease"}}/></div>
                        </div>
                      );})}
                    </div>
                  )}
                </div>
              )}
              {dbCoach.length>0 && (
                <div style={{marginBottom:16,background:"rgba(168,85,247,0.05)",border:"1px solid rgba(168,85,247,0.18)",borderRadius:12,padding:"13px 14px"}}>
                  <div style={{fontSize:10,color:"#C084FC",fontWeight:700,textTransform:"uppercase",letterSpacing:1.2,marginBottom:10,display:"flex",alignItems:"center",gap:6}}>🧠 Coach</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {dbCoach.map((tip,i)=>{ const col=tip.t==="warn"?"#FBBF24":tip.t==="good"?"#4ade80":"rgba(255,255,255,0.55)"; const icon=tip.t==="warn"?"⚠️":tip.t==="good"?"✓":"💡"; return (
                      <div key={i} style={{display:"flex",gap:7,alignItems:"flex-start",fontSize:11.5,lineHeight:1.45,color:col}}>
                        <span style={{flexShrink:0}}>{icon}</span><span>{tip.m}</span>
                      </div>
                    );})}
                  </div>
                  {dbNeeds.length>0 && (
                    <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:10,paddingTop:10,borderTop:"1px solid rgba(255,255,255,0.06)"}}>
                      {dbNeeds.map((n,i)=>(
                        <button key={i} onClick={()=>applyNeedFilter(n)} style={{background:"rgba(168,85,247,0.12)",border:"1px solid rgba(168,85,247,0.4)",color:"#C084FC",borderRadius:7,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>🔎 {n.label}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {isAM&&inDeck.length>0&&(
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,color:"#C084FC",fontWeight:700,textTransform:"uppercase",letterSpacing:1.2,marginBottom:9}}>Treatment Unlocks</div>
                  {(() => {
                    // Group the deck's treatments into INSERTS (Inspired Ink variants collapse into
                    // one), then show one unlock row per insert rather than per treatment string.
                    const byKey = {};
                    inDeck.forEach(c => {
                      if (!c.treatment) return;
                      if (!isInsertTreatment(c)) return;   // plain Battlefoil isn't an insert
                      const k = insertKey(c);
                      if (!byKey[k]) byKey[k] = c.treatment;   // first-seen name as the fallback label
                    });
                    return Object.keys(byKey).sort().map(tl => {
                      const t = insertLabel(tl, byKey[tl]);
                      const core=treatCore[tl]||0, apex=treatApex[tl]||0, unlocked=core>=10;
                      return (
                      <div key={t} style={{marginBottom:7}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                          <span style={{fontSize:11,color:unlocked?"#A855F7":"rgba(255,255,255,0.4)",fontWeight:unlocked?700:400}}>{unlocked?"\uD83D\uDD13":"\uD83D\uDD12"} {t}</span>
                          <span style={{fontSize:11,color:unlocked?"#A855F7":"rgba(255,255,255,0.3)"}}>{core}/10{unlocked?` · ${apex}/1`:""}</span>
                        </div>
                        <div style={{height:3,background:"rgba(255,255,255,0.06)",borderRadius:2}}>
                          <div style={{width:`${Math.min(100,core/10*100)}%`,height:"100%",background:unlocked?"linear-gradient(90deg,#A855F7,#7B2FF7)":"rgba(255,255,255,0.15)",borderRadius:2,transition:"width 0.3s"}}/>
                        </div>
                      </div>
                    );
                    });
                  })()}
                </div>
              )}
              {(!isMobile || inDeck.length>0) && <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:3,marginBottom:10}}>
                {Array.from({length:DECK_SIZE}).map((_,i)=>{
                  const c=inDeck[i];
                  if(c){const wc=WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";return(<div key={i} title={`${c.hero} ${c.power} — click to remove`} onClick={()=>setDeckCards(p=>p.filter(id=>id!==c.id))} style={{position:"relative",aspectRatio:"3/4",borderRadius:4,overflow:"hidden",cursor:"pointer",border:`1.5px solid ${wc}33`,background:"var(--bz-s1)"}} onMouseEnter={ev=>{ev.currentTarget.style.borderColor=wc;const x=ev.currentTarget.querySelector(".deck-rm");if(x)x.style.opacity="1";}} onMouseLeave={ev=>{ev.currentTarget.style.borderColor=wc+"33";const x=ev.currentTarget.querySelector(".deck-rm");if(x)x.style.opacity="0";}}>{c.imageUrl?<img src={c.imageUrl} alt={c.hero} style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:7,color:wc,fontWeight:700,textAlign:"center",padding:2}}>{c.hero?.split(" ")[0]}</div>}<div className="deck-rm" style={{position:"absolute",inset:0,background:"rgba(232,49,122,0.6)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:18,fontWeight:900,opacity:0,transition:"opacity 0.15s"}}>×</div></div>);}
                  return <div key={i} style={{aspectRatio:"3/4",borderRadius:4,border:"1px dashed rgba(255,255,255,0.05)",background:"rgba(255,255,255,0.01)",display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{fontSize:8,color:"rgba(255,255,255,0.1)",fontWeight:700}}>{i+1}</span></div>;
                })}
              </div>}
              {inDeck.length>0&&<button onClick={()=>{if(window.confirm("Clear deck?"))setDeckCards([]);}} style={{width:"100%",background:"transparent",border:"1px solid rgba(232,49,122,0.15)",color:"rgba(232,49,122,0.5)",borderRadius:10,padding:"6px 0",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"all 0.2s"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(232,49,122,0.4)";e.currentTarget.style.color="#E8317A";}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(232,49,122,0.15)";e.currentTarget.style.color="rgba(232,49,122,0.5)";}}>
                {"\u2715 Clear deck"}</button>}
            </div>
          </div>
          {showPickList && (()=>{
            const pulled = inDeck.filter(c=>pickChecked[c.id]).length;
            const sorted = [...inDeck].sort((a,b)=>{
              if(pickSort==="power") return (parseFloat(b.power)||0)-(parseFloat(a.power)||0) || String(a.hero||"").localeCompare(String(b.hero||""));
              if(pickSort==="cardnum") return String(a.cardNum||"").localeCompare(String(b.cardNum||""),undefined,{numeric:true});
              if(pickSort==="weapon") return String(a.weapon||"").localeCompare(String(b.weapon||"")) || (parseFloat(b.power)||0)-(parseFloat(a.power)||0);
              if(pickSort==="treatment") return String(a.treatment||"").localeCompare(String(b.treatment||"")) || (parseFloat(b.power)||0)-(parseFloat(a.power)||0);
              return 0;
            });
            // Build clean printable HTML and open it in a popup window. The browser print dialog is
            // where both printing and "save as PDF" happen, so this one path serves both.
            const openPickPrint = (autoPrint) => {
              const esc = s => String(s==null?"":s).replace(/[&<>"]/g, m=>({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;"}[m]));
              const dash = "\u2014", dot = "\u00b7";
              // Who physically has a copy, and how many are actually free to pull. Owning a copy used
              // to short-circuit the whole check, so a card where you have one and Derrik has one
              // read as simply "You" \u2014 hiding both that a second copy exists and whose it is. Every
              // holder is listed, and copies already committed to a deck are called out, because a
              // committed copy is not one you can go and fetch.
            // ONE holder \u2014 the answer to "where do I get this". Your own copy wins when you have both,
            // because that is the one you can actually reach.
            const ownLabel = c => {
              const myQty = (owned && owned[c.id]) ? (parseInt(owned[c.id]) || 0) : 0;
              if (myQty > 0) {
                const who = [];
                for (let i = 0; i < myQty; i++) {
                  const k = kidOfCopy ? kidOfCopy(c.id, i) : null;
                  const name = k ? ((kidGroups.find(g=>g.id===k)||{}).name || "Kid") : "You";
                  if (!who.includes(name)) who.push(name);
                }
                const label = who.includes("You") ? "You" : who[0];
                return '<span style="color:'+(label==="You"?"#1a7a3a":"#B45309")+';font-weight:700">'+esc(label)+'</span>';
              }
              const fam = (familyOwnerByCard && familyOwnerByCard[c.id]) || (familyOwnsCard && familyOwnsCard[c.id]);
              if (fam) return '<span style="color:#7B2FF7;font-weight:700">'+esc(fam.name||"Family")+'</span>';
              return '<span style="color:#c0392b;font-weight:700">Not owned</span>';
            };
            // The printed sheet is the SAME document as the on-screen one \u2014 same two-line rows, same
            // power figure, same amber proxy marker. A print that looks nothing like the screen makes
            // you re-learn the layout at the worst moment, standing at a binder holding the paper.
            // Expand to one row PER PHYSICAL COPY, matching the screen. Two copies of the
            // same card get two rows so each can carry its own note — which is the whole
            // reason this sheet matters: "the vault one" and "the in-hand one" are
            // different physical cards even though they share a card ID.
            // One row per card, using THIS DECK's assigned copy \u2014 mirrors the screen.
            // A deck holds one copy; which one is decided by deck age (see deckCopyIndex).
            const copyRows = sorted.map((c)=>{
              const q  = Math.max(1, parseInt(owned?.[c.id]) || 0);
              const ci = copyForDeck ? copyForDeck(c.id, deckLoadId) : 0;
              return { c, ci, q };
            });
            const rows = copyRows.map(({c, ci, q})=>{
              const meta = [c.cardNum, c.weapon, c.treatment, c.setName].filter(Boolean).map(esc).join(' <span class="dim">\u00b7</span> ');
              const pxNote = proxyNote ? proxyNote(c.id, ci) : "";
              const flagged = isProxyCopy ? isProxyCopy(c.id, ci) : !!proxyCards[c.id];
              const copyTag = q > 1 ? ' <span class="copy">COPY '+(ci+1)+' OF '+q+'</span>' : '';
              return '<tr>'+
                '<td class="tick" style="border-left:3px solid '+((WEAPON_COLORS[canonWeapon(c.weapon)]||"#e8e8e8"))+'"><span class="box"></span></td>'+
                '<td class="px">'+(flagged?'<span class="proxy">\u25C6</span>':'<span class="noproxy">\u25C7</span>')+'</td>'+
                '<td><div class="hero">'+esc(c.hero||dash)+copyTag+'</div><div class="meta">'+meta+'</div>'+(pxNote?'<div class="pxnote">'+esc(pxNote)+'</div>':'')+'</td>'+
                '<td class="var">'+esc(c.variation||dash)+'</td>'+
                '<td class="pw">'+esc(c.power??dash)+'</td>'+
                '<td class="who">'+ownLabel(c)+'</td>'+
              '</tr>';
            }).join("");
            const proxyCount = copyRows.filter(({c,ci})=> isProxyCopy ? isProxyCopy(c.id,ci) : !!proxyCards[c.id]).length;
            const script = autoPrint ? '<scr'+'ipt>window.onload=function(){setTimeout(function(){window.print();},250);};</scr'+'ipt>' : '';
            const html = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>'+esc(deckName||"My Deck")+' '+dash+' Pick List</title>'+
              '<style>*{box-sizing:border-box;} body{font-family:"Trebuchet MS",Arial,Helvetica,sans-serif;margin:0;padding:26px 28px;color:#111;}'+
              '.eyebrow{font-size:10px;font-weight:800;letter-spacing:1.6px;text-transform:uppercase;color:#B45309;margin-bottom:4px;}'+
              'h1{font-size:25px;font-weight:900;letter-spacing:-0.6px;margin:0;line-height:1.05;}'+
              '.stats{display:flex;gap:16px;margin-top:8px;font-size:12px;flex-wrap:wrap;}'+
              '.stats b{font-weight:800;} .stats .lt{color:#999;font-weight:600;} .stats .px{color:#B45309;font-weight:800;}'+
              '.rule{border-bottom:3px solid #111;margin:14px 0 0;}'+
              'table{width:100%;border-collapse:collapse;font-size:13px;margin-top:2px;} thead{display:table-header-group;}'+
              'th{text-align:left;border-bottom:1.5px solid #111;padding:8px 6px;font-size:9.5px;font-weight:800;'+
                'letter-spacing:0.8px;text-transform:uppercase;color:#999;}'+
              'th.r{text-align:right;} th.c{text-align:center;}'+
              'td{padding:9px 6px;border-bottom:1px solid #ededed;vertical-align:top;} tr{page-break-inside:avoid;}'+
              '.tick{width:34px;text-align:center;}'+
              '.box{display:inline-block;width:17px;height:17px;border:1.5px solid #bbb;border-radius:4px;}'+
              '.px{width:40px;text-align:center;}'+
              '.proxy{color:#B45309;font-size:15px;} .noproxy{color:#ddd;font-size:15px;}'+
              '.hero{font-weight:800;font-size:14px;letter-spacing:-0.2px;}'+
              '.meta{font-size:10.5px;color:#999;margin-top:2px;} .dim{color:#ddd;}'+
              '.pxnote{font-size:10px;color:#B45309;font-weight:700;margin-top:2px;}'+
              '.copy{font-size:8.5px;font-weight:900;color:#B45309;background:#FEF3C7;border:1px solid #FDE68A;border-radius:4px;padding:0 4px;margin-left:5px;vertical-align:middle;}'+
              // Variation = pose. IMC needs this to know WHICH print to pull: two Ice BoJax with
              // different poses are different cards, so "BoJax, Ice" alone is ambiguous on a pick
              // sheet. Given its own column rather than tucked into the meta line, because this is
              // the field someone is scanning down when they're filling the order.
              '.var{font-size:11.5px;font-weight:700;color:#444;width:120px;}'+
              '.pw{text-align:right;font-size:18px;font-weight:900;letter-spacing:-0.5px;font-variant-numeric:tabular-nums;width:70px;}'+
              '.who{font-size:11.5px;font-weight:700;width:130px;}'+
              '.foot{margin-top:18px;padding-top:11px;border-top:1px solid #eee;font-size:10px;color:#bbb;'+
                'display:flex;justify-content:space-between;letter-spacing:0.3px;}'+
              '.foot b{color:#999;font-weight:800;}'+
              '.btns{margin-bottom:18px;} button{background:#111;color:#fff;border:none;border-radius:9px;padding:9px 18px;'+
                'font-size:12px;font-weight:800;cursor:pointer;font-family:inherit;margin-right:8px;}'+
              '@page{margin:13mm;} @media print{.btns{display:none;}}</style></head><body>'+
              '<div class="btns"><button onclick="window.print()">Print</button></div>'+
              '<div class="eyebrow">Pick list</div>'+
              '<h1>'+esc(deckName||"My Deck")+'</h1>'+
              '<div class="stats"><span><b>'+sorted.length+'</b><span class="lt"> cards</span></span>'+
                (proxyCount?'<span class="px">'+proxyCount+' to proxy</span>':'')+
                '<span class="lt">sorted by '+(pickSort==="cardnum"?"card number":pickSort)+'</span></div>'+
              '<div class="rule"></div>'+
              '<table><thead><tr><th class="c">Got</th><th class="c">Proxy</th><th>Card</th><th>Variation</th><th class="r">Power</th><th>Who has it</th></tr></thead>'+
              '<tbody>'+rows+'</tbody></table>'+
              '<div class="foot"><b>BAZOOKA DASH</b><span>'+new Date().toLocaleDateString()+'</span></div>'+script+
              '</body></html>';
              const w = window.open("","_blank","width=900,height=1000");
              if (!w) { alert("Please allow pop-ups for this site to print or save the pick list."); return; }
              w.document.write(html); w.document.close();
            };
            return (
            <div onClick={()=>setShowPickList(false)} className="pick-list-overlay" style={{position:"fixed",inset:0,zIndex:13000,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(6px)",display:"flex",alignItems:"flex-start",justifyContent:"center",padding:24,overflowY:"auto"}}>
              <div onClick={e=>e.stopPropagation()} className="pick-list-sheet" style={{width:"100%",maxWidth:760,background:"#fff",color:"var(--bz-s1)",borderRadius:14,padding:"26px 30px 30px",boxShadow:"0 24px 80px rgba(0,0,0,0.6)",position:"relative",margin:"auto"}}>
                {/* Screen-only controls (hidden when printing) */}
                <div className="pick-list-controls" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",marginBottom:18}}>
                  {/* Sort is a segmented control rather than loose buttons: the options are mutually
                      exclusive, and grouping them says so without needing a label. */}
                  <div style={{display:"inline-flex",border:"1.5px solid #e3e3e3",borderRadius:9,overflow:"hidden"}}>
                    {[["power","Power"],["cardnum","Card no."],["weapon","Weapon"],["treatment","Treatment"]].map(([k,label],idx)=>(
                      <button key={k} onClick={()=>setPickSort(k)}
                        style={{background:pickSort===k?"var(--bz-s1)":"#fff",color:pickSort===k?"#fff":"#777",
                          border:"none",borderLeft: idx===0?"none":"1px solid #e3e3e3",
                          padding:"8px 13px",fontSize:11.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",
                          letterSpacing:"0.2px",transition:"background 120ms ease"}}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    {/* One button. Print and "Save PDF" opened the identical window — the only
                        difference was whether the dialog fired automatically. Saving a PDF is a choice
                        inside that dialog on every platform, so a separate button promised an outcome
                        that never existed. */}
                    <button onClick={()=>openPickPrint(true)}
                      style={{background:"var(--bz-s1)",color:"#fff",border:"1.5px solid #111",borderRadius:9,padding:"8px 18px",fontSize:11.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                      Print / PDF
                    </button>
                    <button onClick={()=>setShowPickList(false)} aria-label="Close pick list"
                      style={{background:"#fff",color:"#999",border:"1.5px solid #ddd",borderRadius:9,width:36,height:36,fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:"inherit",lineHeight:1}}>
                      {"\u00d7"}
                    </button>
                  </div>
                </div>
                {/* Header. The deck name is the title of a working document, so it is set large and
                    tight; the counts underneath are the only status that matters while pulling. */}
                <div style={{marginBottom:18,paddingBottom:14,borderBottom:"3px solid #111"}}>
                  <div style={{fontSize:11,fontWeight:800,letterSpacing:"1.6px",textTransform:"uppercase",color:"#B45309",marginBottom:4}}>Pick list</div>
                  <div style={{fontSize:26,fontWeight:900,letterSpacing:"-0.6px",lineHeight:1.05}}>{deckName||"My Deck"}</div>
                  <div style={{display:"flex",gap:16,marginTop:9,flexWrap:"wrap",fontSize:12}}>
                    <span style={{fontWeight:800}}>{pulled}<span style={{color:"#999",fontWeight:600}}> / {inDeck.length} pulled</span></span>
                    {(()=>{ const n = inDeck.reduce((s,x)=>{ const q=Math.max(1,parseInt(owned?.[x.id])||0); let k=0; for(let ci=0;ci<q;ci++) if(isProxyCopy?isProxyCopy(x.id,ci):!!proxyCards[x.id]) k++; return s+k; },0); return n>0 && (
                      <span style={{fontWeight:800,color:"#B45309"}}>
                        {n}<span style={{fontWeight:600}}> to proxy</span>
                      </span>
                    ); })()}
                    <span style={{color:"#999"}}>sorted by {pickSort==="cardnum"?"card number":pickSort}</span>
                  </div>
                  {/* Progress. A thin rule rather than a bar: it reads at a glance without taking space
                      from the list, which is the part you are actually working from. */}
                  <div style={{height:3,background:"#eee",borderRadius:2,marginTop:11,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${inDeck.length?Math.round(pulled/inDeck.length*100):0}%`,
                      background:"var(--bz-s1)",transition:"width 220ms ease"}}/>
                  </div>
                </div>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead>
                    <tr style={{borderBottom:"1.5px solid #111",textAlign:"left"}}>
                      <th style={{padding:"0 6px 8px",width:38,textAlign:"center",fontSize:10,fontWeight:800,letterSpacing:"0.8px",textTransform:"uppercase",color:"#999"}}>Got</th>
                      <th style={{padding:"0 6px 8px",width:44,textAlign:"center",fontSize:10,fontWeight:800,letterSpacing:"0.8px",textTransform:"uppercase",color:"#999"}}>Proxy</th>
                      <th style={{padding:"0 6px 8px",fontSize:10,fontWeight:800,letterSpacing:"0.8px",textTransform:"uppercase",color:"#999"}}>Card</th>
                      <th style={{padding:"0 6px 8px",width:120,fontSize:10,fontWeight:800,letterSpacing:"0.8px",textTransform:"uppercase",color:"#999"}}>Variation</th>
                      <th style={{padding:"0 6px 8px",textAlign:"right",fontSize:10,fontWeight:800,letterSpacing:"0.8px",textTransform:"uppercase",color:"#999"}}>Power</th>
                      <th style={{padding:"0 6px 8px",fontSize:10,fontWeight:800,letterSpacing:"0.8px",textTransform:"uppercase",color:"#999"}}>Who has it</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* ONE row per card \u2014 a pick list is for ONE deck, and a deck holds
                        exactly one copy of a card. When you own several, copyForDeck says
                        WHICH physical copy this deck was assigned (oldest deck gets copy 1,
                        next gets copy 2, and so on).

                        This is what stops a proxy flag leaking between decks: marking the
                        vault copy as needing a proxy used to make EVERY deck holding that
                        card demand one, including the deck holding the copy you physically
                        have in hand. Now each deck reads the flag for its own copy. */}
                    {sorted.map((c)=>{
                      const qty     = Math.max(1, parseInt(owned?.[c.id]) || 0);
                      const ci      = copyForDeck ? copyForDeck(c.id, deckLoadId) : 0;
                      const rowKey  = qty > 1 ? `${c.id}#${ci}` : c.id;
                      const done    = !!pickChecked[rowKey];
                      const isProxy = isProxyCopy ? isProxyCopy(c.id, ci) : !!proxyCards[c.id];
                      const i = ci;
                      return (
                      <tr key={rowKey} onClick={()=>setPickChecked(p=>({...p,[rowKey]:!p[rowKey]}))}
                        style={{borderBottom:"1px solid #ededed",cursor:"pointer",
                          background: done ? "#fafafa" : "transparent",
                          opacity: done ? 0.45 : 1, transition:"opacity 140ms ease, background 140ms ease"}}>
                        {/* Pulled. The whole row is the hit area \\u2014 you are tapping this one-handed while
                            holding cards, so precision targeting is the wrong ask. */}
                        <td style={{padding:"10px 6px",textAlign:"center",borderLeft:`3px solid ${WEAPON_COLORS[canonWeapon(c.weapon)]||"#e8e8e8"}`}}>
                          <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",
                            width:19,height:19,borderRadius:5,
                            border:"1.5px solid " + (done ? "var(--bz-s1)" : "#ccc"),
                            background: done ? "var(--bz-s1)" : "#fff",
                            color:"#fff",fontSize:12,fontWeight:900,lineHeight:1}}>
                            {done ? "\u2713" : ""}
                          </span>
                        </td>
                        {/* Proxy. Second action on the row, so it gets its own column and a real tap
                            target \\u2014 amber only when set, so an unflagged sheet stays calm. */}
                        <td style={{padding:"10px 6px",textAlign:"center"}}>
                          <button onClick={e=>{ e.stopPropagation(); onToggleProxy && onToggleProxy(c.id, undefined, ci); }}
                            title={isProxy ? "Proxy \\u2014 tap to clear" : "Mark as proxy"}
                            aria-pressed={isProxy}
                            style={{cursor:"pointer",fontFamily:"inherit",borderRadius:8,
                              width:34,height:34,display:"inline-flex",alignItems:"center",justifyContent:"center",
                              fontSize:13,fontWeight:900,lineHeight:1,padding:0,
                              background: isProxy ? "#B45309" : "#fff",
                              border: "1.5px solid " + (isProxy ? "#B45309" : "#ddd"),
                              color: isProxy ? "#fff" : "#bbb",
                              transition:"all 120ms ease"}}>
                            {isProxy ? "\u25C6" : "\u25C7"}
                          </button>
                        </td>
                        {/* Card identity. Hero leads because that is what you read off the card; the
                            number and finish sit underneath as the confirming detail. */}
                        <td style={{padding:"10px 6px"}}>
                          <div style={{fontWeight:800,fontSize:14,letterSpacing:"-0.2px",
                            textDecoration: done ? "line-through" : "none"}}>
                            {c.hero||"\u2014"}
                            {qty > 1 && (
                              <span style={{marginLeft:7,fontSize:10,fontWeight:900,color:"#B45309",
                                background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:5,
                                padding:"1px 5px",verticalAlign:"middle"}}>
                                THIS DECK: COPY {ci+1} OF {qty}
                              </span>
                            )}
                          </div>
                          <div style={{fontSize:11,color:"#999",marginTop:2,display:"flex",gap:7,flexWrap:"wrap"}}>
                            <span style={{fontFamily:"ui-monospace,monospace",color:"#666"}}>{c.cardNum||"\u2014"}</span>
                            {c.weapon && <span>{c.weapon}</span>}
                            {c.treatment && <span>{c.treatment}</span>}
                            {c.setName && <span style={{color:"#bbb"}}>{c.setName}</span>}
                          </div>
                          {/* The note is OPTIONAL and secondary: flagging must stay one tap, so adding a
                              note is its own small action rather than a prompt in the way. Only offered
                              once a card is actually a proxy. */}
                          {isProxy && (
                            <div onClick={e=>{
                                e.stopPropagation();
                                if (!onToggleProxy) return;
                                const cur = proxyNote ? proxyNote(c.id, ci) : "";
                                const note = window.prompt(
                                  qty > 1
                                    ? `Note for copy ${ci+1} of ${qty} \u2014 e.g. "Blokpax vault" or "in hand"`
                                    : "Proxy note (optional)",
                                  cur);
                                if (note === null) return;
                                onToggleProxy(c.id, note, ci);
                              }}
                              style={{fontSize:10.5,marginTop:3,fontWeight:700,cursor:"text",
                                color: (proxyNote && proxyNote(c.id, ci)) ? "#B45309" : "#ccc"}}>
                              {(proxyNote && proxyNote(c.id, ci)) || "+ note"}
                            </div>
                          )}
                        </td>
                        {/* Variation = pose. Its own column so it can be scanned down the page when
                            filling an order — two Ice BoJax with different poses are different cards,
                            so hero + weapon alone doesn't identify which print to pull. */}
                        <td style={{padding:"10px 6px",fontSize:12,fontWeight:700,color:"#444",verticalAlign:"top",
                          textDecoration: done ? "line-through" : "none"}}>
                          {c.variation || "\u2014"}
                        </td>
                        {/* Power is the one number that matters in BoBA, so it is set as a figure, not
                            table data. */}
                        <td style={{padding:"10px 6px",textAlign:"right",verticalAlign:"top"}}>
                          <span style={{fontSize:19,fontWeight:900,letterSpacing:"-0.5px",
                            fontVariantNumeric:"tabular-nums"}}>{c.power ?? "\u2014"}</span>
                        </td>
                        <td style={{padding:"10px 6px",fontSize:12,fontWeight:700,verticalAlign:"top"}}>{(() => {
                          // ONE answer: where do I get this card. If a copy is yours, that is the answer
                          // and a relative's copy is irrelevant \u2014 listing both sent you reading a name you
                          // have no reason to act on. Family only shows when you do not have one yourself.
                          const myQty = (owned && owned[c.id]) ? (parseInt(owned[c.id]) || 0) : 0;
                          if (myQty > 0) {
                            const who = [];
                            for (let i = 0; i < myQty; i++) {
                              const k = kidOfCopy ? kidOfCopy(c.id, i) : null;
                              const nm = k ? ((kidGroups.find(g=>g.id===k)||{}).name || "Kid") : "You";
                              if (!who.includes(nm)) who.push(nm);
                            }
                            // Prefer your own copy when you have both: it is the one you can reach.
                            const label = who.includes("You") ? "You" : who[0];
                            return <span style={{color: label==="You" ? "#1a7a3a" : "#B45309"}}>{label}</span>;
                          }
                          const famHolder = (familyOwnerByCard && familyOwnerByCard[c.id]) || (familyOwnsCard && familyOwnsCard[c.id]);
                          if (famHolder) return <span style={{color:"#7B2FF7"}}>{famHolder.name||"Family"}</span>;
                          return <span style={{color:"#c0392b"}}>Not owned</span>;
                        })()}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{marginTop:20,paddingTop:12,borderTop:"1px solid #eee",fontSize:10.5,color:"#bbb",display:"flex",justifyContent:"space-between",letterSpacing:"0.3px"}}><span style={{fontWeight:800,color:"#999"}}>BAZOOKA DASH</span><span>{new Date().toLocaleDateString()}</span></div>
              </div>
            </div>
            );
          })()}

          {regSheetOpen && (
            <div onClick={()=>setRegSheetOpen(false)} style={{position:"fixed",inset:0,zIndex:13000,background:"rgba(0,0,0,0.82)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
              <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:440,background:"var(--bz-s1,#14141c)",border:"1px solid var(--bz-line-2)",borderRadius:16,padding:22}}>
                <div style={{fontSize:16,fontWeight:900,color:"var(--bz-ink)",marginBottom:3}}>Registration Sheet</div>
                <div style={{fontSize:11.5,color:"var(--bz-ink-3)",marginBottom:16}}>Autofills the BoBA SPEC · Playmaker format from your hero deck and a playbook.</div>
                {[["player","Player name"],["whatnot","Carde.io username"],["event","Event"]].map(([k,ph])=>(
                  <input key={k} value={regInfo[k]||""} onChange={e=>setRegInfo({...regInfo,[k]:e.target.value})} placeholder={ph}
                    style={{...inp,width:"100%",marginBottom:9}} />
                ))}
                <input type="date" value={regInfo.date||""} onChange={e=>setRegInfo({...regInfo,date:e.target.value})}
                  style={{...inp,width:"100%",marginBottom:9,colorScheme:"dark"}} />
                <div style={{fontSize:10.5,color:"var(--bz-ink-3)",fontWeight:700,margin:"6px 0 4px"}}>SORT HERO DECK BY</div>
                <div style={{display:"flex",gap:6,marginBottom:12}}>
                  {[["power","Power"],["weapon","Weapon"]].map(([v,l])=>(
                    <button key={v} onClick={()=>setRegInfo({...regInfo,heroSort:v})}
                      style={{flex:1,background:(regInfo.heroSort||"power")===v?"rgba(232,49,122,0.18)":"transparent",border:`1.5px solid ${(regInfo.heroSort||"power")===v?"#E8317A":"var(--bz-line-2)"}`,color:(regInfo.heroSort||"power")===v?"#E8317A":"var(--bz-ink-3)",borderRadius:8,padding:"8px 4px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
                  ))}
                </div>
                <div style={{fontSize:10.5,color:"var(--bz-ink-3)",fontWeight:700,margin:"6px 0 4px"}}>PLAYBOOK</div>
                <select value={regInfo.playbookId||""} onChange={e=>setRegInfo({...regInfo,playbookId:e.target.value})}
                  style={{...inp,width:"100%",marginBottom:16}}>
                  <option value="">— No playbook —</option>
                  {(savedPlaybooks||[]).map(p=>(<option key={p.id} value={p.id}>{p.name} ({(p.entries||[]).length})</option>))}
                </select>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setRegSheetOpen(false)} style={{flex:1,background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                  <button onClick={()=>{ openRegSheetPrint(); }} style={{flex:2,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",border:"none",color:"#fff",borderRadius:10,padding:"11px",fontSize:13,fontWeight:900,cursor:"pointer",fontFamily:"inherit"}}>Generate sheet</button>
                </div>
              </div>
            </div>
          )}
        </>
  );
}

function OfferModal({ offerModal, offerSent, setOfferModal, offerAmt, setOfferAmt, offerNote, setOfferNote, setOfferSent, submitOffer, inp }) {
  if (!offerModal) return null;
  return (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}
          onClick={()=>{if(offerSent){setOfferModal(null);setOfferAmt("");setOfferNote("");setOfferSent(false);}}}>
          <div style={{background:"linear-gradient(135deg,var(--bz-s1),#1a1400)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:20,padding:28,maxWidth:400,width:"100%"}} onClick={e=>e.stopPropagation()}>
            {offerSent?(
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{fontSize:52,marginBottom:16}}>{"\uD83E\uDD1D"}</div>
                <div style={{fontSize:20,fontWeight:900,color:"#4ade80",marginBottom:8}}>Offer Sent!</div>
                <div style={{fontSize:14,color:"rgba(255,255,255,0.6)",marginBottom:6}}>
                  Your offer of <strong style={{color:"#FBBF24"}}>{"$"}{parseFloat(offerAmt||0).toFixed(2)}</strong> on
                </div>
                <div style={{fontSize:15,fontWeight:800,color:"var(--bz-ink)",marginBottom:6}}>
                  {offerModal.cardName}
                </div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.35)",marginBottom:24}}>
                  has been sent to the seller. {"\u2022"} {"\u2022"} {"\u2022"}<br/>
                  {"You'll be notified when they respond."}
                </div>
                <button
                  onClick={()=>{setOfferModal(null);setOfferAmt("");setOfferNote("");setOfferSent(false);}}
                  style={{background:"linear-gradient(135deg,#FBBF24,#F59E0B)",color:"#000",border:"none",borderRadius:12,padding:"12px 32px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                  Done
                </button>
              </div>
            ):(
              <>
                <div style={{fontSize:18,fontWeight:900,color:"#FBBF24",marginBottom:4}}>{"\uD83E\uDD1D Make an Offer"}</div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginBottom:16}}>{offerModal.cardName} {"\u00B7"} {offerModal.sellerName}</div>

                {!offerModal?.isOBO&&<div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:"12px 14px",marginBottom:14}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <span style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>Asking price</span>
                    <span style={{fontSize:16,fontWeight:900,color:"var(--bz-ink)"}}>{"$"}{(offerModal.askingPrice||0).toFixed(2)}</span>
                  </div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginBottom:6}}>Quick offer</div>
                  <div style={{display:"flex",gap:6}}>
                    {[100,95,90,85,80].map(pct=>{
                      const qAmt=((offerModal.askingPrice||0)*pct/100).toFixed(2);
                      const active=offerAmt===qAmt;
                      return (
                        <button key={pct} onClick={()=>setOfferAmt(qAmt)}
                          style={{flex:1,background:active?"rgba(251,191,36,0.25)":"rgba(255,255,255,0.05)",
                            border:"1px solid "+(active?"rgba(251,191,36,0.5)":"rgba(255,255,255,0.08)"),
                            borderRadius:8,padding:"5px 0",fontSize:11,fontWeight:700,
                            color:active?"#FBBF24":"rgba(255,255,255,0.5)",
                            cursor:"pointer",fontFamily:"inherit"}}>
                          {pct}{"%"}
                        </button>
                      );
                    })}
                  </div>
                </div>}

                <div style={{marginBottom:12}}>
                  <div style={{display:"flex",alignItems:"center",marginBottom:8}}>
                    <span style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(251,191,36,0.2)",borderRight:"none",borderRadius:"10px 0 0 10px",padding:"10px 14px",fontSize:15,color:"rgba(255,255,255,0.4)",fontWeight:700}}>$</span>
                    <input value={offerAmt} onChange={e=>setOfferAmt(e.target.value)} placeholder="0.00" type="number" min="0" step="0.01"
                      style={{flex:1,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(251,191,36,0.2)",borderLeft:"none",borderRight:"none",padding:"10px 14px",fontSize:15,color:"var(--bz-ink)",fontFamily:"inherit",outline:"none"}}/>
                    <span style={{
                      background:"rgba(255,255,255,0.05)",border:"1px solid rgba(251,191,36,0.2)",borderLeft:"none",
                      borderRadius:"0 10px 10px 0",padding:"10px 12px",fontSize:13,fontWeight:800,minWidth:52,textAlign:"center",
                      color:"rgba(255,255,255,0.3)"}}>
                      {offerModal?.isOBO||!offerModal?.askingPrice?"--":offerAmt&&!isNaN(parseFloat(offerAmt))?Math.round(parseFloat(offerAmt)/(offerModal.askingPrice||1)*100)+"%":"--"}
                    </span>
                  </div>
                  {offerAmt&&!isNaN(parseFloat(offerAmt))&&!offerModal?.isOBO&&offerModal?.askingPrice>0&&(()=>{
                    const oa=parseFloat(offerAmt);
                    const ask=offerModal.askingPrice||0;
                    const pct=ask>0?oa/ask*100:0;
                    const savings=ask-oa;
                    const barColor=pct>=95?"#4ade80":pct>=85?"#FBBF24":pct>=75?"#F97316":"#f87171";
                    const verdict=pct>=100?"Full ask":pct>=95?"Strong offer":pct>=85?"Fair offer":pct>=75?"Low offer":"Very low";
                    return (
                      <div>
                        <div style={{height:4,background:"rgba(255,255,255,0.06)",borderRadius:4,marginBottom:6,overflow:"hidden"}}>
                          <div style={{height:"100%",width:Math.min(pct,100)+"%",background:barColor,borderRadius:4,transition:"width 0.2s"}}/>
                        </div>
                        <div style={{display:"flex",justifyContent:"space-between"}}>
                          <span style={{fontSize:11,color:barColor,fontWeight:700}}>{verdict}</span>
                          {savings>0&&<span style={{fontSize:11,color:"rgba(255,255,255,0.35)"}}>{"$"}{savings.toFixed(2)} off ask</span>}
                          {savings<0&&<span style={{fontSize:11,color:"#f87171"}}>{"$"}{Math.abs(savings).toFixed(2)} over ask</span>}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <textarea value={offerNote} onChange={e=>setOfferNote(e.target.value)} placeholder="Message to seller (optional)" rows={2}
                  style={{width:"100%",marginBottom:16,boxSizing:"border-box",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,padding:"10px 14px",fontSize:13,color:"var(--bz-ink)",fontFamily:"inherit",outline:"none",resize:"none"}}/>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={submitOffer} disabled={!offerAmt||isNaN(parseFloat(offerAmt))} style={{flex:1,background:"linear-gradient(135deg,#FBBF24,#F59E0B)",color:"#000",border:"none",borderRadius:12,padding:"12px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit",opacity:offerAmt&&!isNaN(parseFloat(offerAmt))?1:0.4}}>Send Offer</button>
                  <button onClick={()=>setOfferModal(null)} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.4)",borderRadius:12,padding:"12px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
  );
}

function CounterModal({ counterModal, counterSent, setCounterModal, counterAmt, setCounterAmt, setCounterSent, counterOffer, negHistory }) {
  if (!counterModal) return null;
  return (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}
          onClick={()=>{if(counterSent){setCounterModal(null);setCounterAmt("");setCounterSent(false);}}}>
          <div style={{background:"#0E0E14",border:"1px solid rgba(251,191,36,0.3)",borderRadius:20,padding:28,maxWidth:420,width:"100%"}} onClick={e=>e.stopPropagation()}>
            {counterSent?(
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{fontSize:52,marginBottom:16}}>{"\uD83E\uDD1D"}</div>
                <div style={{fontSize:20,fontWeight:900,color:"#4ade80",marginBottom:8}}>Counter Sent!</div>
                <div style={{fontSize:14,color:"rgba(255,255,255,0.6)",marginBottom:6}}>
                  Your counter of <strong style={{color:"#FBBF24"}}>{"$"}{parseFloat(counterAmt||0).toFixed(2)}</strong> on
                </div>
                <div style={{fontSize:15,fontWeight:800,color:"var(--bz-ink)",marginBottom:6}}>{counterModal.cardName}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.35)",marginBottom:24}}>
                  has been sent. {"\u2022"} {"\u2022"} {"\u2022"}<br/>
                  {"You'll be notified when they respond."}
                </div>
                <button
                  onClick={()=>{setCounterModal(null);setCounterAmt("");setCounterSent(false);}}
                  style={{background:"linear-gradient(135deg,#FBBF24,#F59E0B)",color:"#000",border:"none",borderRadius:12,padding:"12px 32px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                  Done
                </button>
              </div>
            ):(
              <>
                <div style={{fontSize:16,fontWeight:800,color:"var(--bz-ink)",marginBottom:4}}>Counter Offer</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:20}}>{counterModal.cardName}</div>
                <div style={{background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.15)",borderRadius:12,padding:"12px 16px",marginBottom:20,maxHeight:160,overflowY:"auto"}}>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:8,fontWeight:700,textTransform:"uppercase",letterSpacing:1}}>Negotiation history</div>
                  {negHistory.length>0?negHistory.map((h,idx)=>{
                    const isCounter=h.action==="counter";
                    const isAccept=h.action==="accepted";
                    const isDecline=h.action==="declined";
                    const color=isAccept?"#4ade80":isDecline?"#f87171":isCounter?"#7B9CFF":"#FBBF24";
                    const label=isAccept?"Accepted":isDecline?"Declined":isCounter?"Countered":"Offered";
                    return (
                      <div key={idx} style={{display:"flex",alignItems:"center",gap:8,marginBottom:idx<negHistory.length-1?6:0}}>
                        <div style={{width:6,height:6,borderRadius:"50%",background:color,flexShrink:0}}/>
                        <span style={{fontSize:12,color:"rgba(255,255,255,0.5)",flex:1}}>{h.fromName}</span>
                        <span style={{fontSize:12,fontWeight:700,color:color}}>{label} ${(h.amount||0).toFixed(2)}</span>
                      </div>
                    );
                  }):(
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:6,height:6,borderRadius:"50%",background:"#FBBF24",flexShrink:0}}/>
                      <span style={{fontSize:12,color:"rgba(255,255,255,0.5)",flex:1}}>{counterModal.buyerName||"Buyer"}</span>
                      <span style={{fontSize:12,fontWeight:700,color:"#FBBF24"}}>Offered ${(counterModal.offerAmount||0).toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div style={{marginBottom:20}}>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginBottom:8,fontWeight:700}}>Your counter amount</div>
                  <div style={{display:"flex",alignItems:"center",gap:0}}>
                    <span style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRight:"none",borderRadius:"8px 0 0 8px",padding:"10px 14px",fontSize:15,color:"rgba(255,255,255,0.4)",fontWeight:700}}>$</span>
                    <input
                      value={counterAmt}
                      onChange={e=>setCounterAmt(e.target.value.replace(/[^0-9.]/g,""))}
                      placeholder="0.00"
                      style={{flex:1,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderLeft:"none",borderRadius:"0 8px 8px 0",padding:"10px 14px",fontSize:15,color:"var(--bz-ink)",fontFamily:"inherit",outline:"none"}}
                      autoFocus
                    />
                  </div>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button
                    onClick={()=>counterOffer(counterModal,counterAmt)}
                    disabled={!counterAmt||isNaN(parseFloat(counterAmt))}
                    style={{flex:1,background:"linear-gradient(135deg,rgba(251,191,36,0.8),rgba(245,158,11,0.8))",color:"#000",border:"none",borderRadius:12,padding:"12px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit",opacity:counterAmt&&!isNaN(parseFloat(counterAmt))?1:0.4}}>
                    Send Counter
                  </button>
                  <button
                    onClick={()=>setCounterModal(null)}
                    style={{background:"transparent",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.4)",borderRadius:12,padding:"12px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

  );
}

function ScanModal({ scanModal, setScanModal, photoScan, setPhotoScan, scanSession, setScanSession, scanQty, setScanQty, user, db, owned, setOwned, cards, inp , confirmScan, scanCardPhoto, scanHint="", scanHintOn=false, toggleScanHint=()=>{}, WEAPON_COLORS={}}) {
  // LIVE CAMERA. On a phone, accept+capture opens the camera app, but on a laptop it does nothing —
  // you get a file picker. So for desktop we open the webcam inline with getUserMedia and grab a
  // frame to a canvas, which is the same JPEG the file path would have produced.
  const [camOn, setCamOn] = useState(false);
  const [camErr, setCamErr] = useState("");
  const videoRef = useRef(null);
  const camStreamRef = useRef(null);
  const stopCam = () => {
    try { camStreamRef.current && camStreamRef.current.getTracks().forEach(t=>t.stop()); } catch(_){}
    camStreamRef.current = null; setCamOn(false);
  };
  useEffect(() => () => stopCam(), []);   // always release the camera when the modal unmounts
  // Stop the hint mic too when the scanner closes — a mic left listening is worse than a camera
  // light left on, because there is no visible indicator on most machines.
  useEffect(() => () => { if (scanHintOn) toggleScanHint(); }, []);
  async function startCam() {
    setCamErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video:{ facingMode:"environment", width:{ideal:1920} }, audio:false });
      camStreamRef.current = stream; setCamOn(true);
      setTimeout(()=>{ if(videoRef.current){ videoRef.current.srcObject = stream; videoRef.current.play().catch(()=>{}); } }, 50);
    } catch(e) {
      setCamErr(e?.name === "NotAllowedError" ? "Camera access was blocked. Allow it for this site and try again."
              : e?.name === "NotFoundError" ? "No camera found on this device."
              : "Couldn't start the camera: " + (e?.message||e));
    }
  }
  function shootCam() {
    const v = videoRef.current; if (!v || !v.videoWidth) return;
    // Capture what the user actually FRAMED. The preview is a 4:5 window onto a 16:9 sensor
    // (object-fit: cover), so grabbing the raw frame would hand the scanner a wide image full of
    // desk either side of the card \u2014 more background for the OCR to wade through, and not what the
    // guide box promised. Crop the centre to the same 4:5 the preview showed.
    const TARGET = 4/5;
    const sw = v.videoWidth, sh = v.videoHeight;
    let cw = sw, ch = Math.round(sw / TARGET);
    if (ch > sh) { ch = sh; cw = Math.round(sh * TARGET); }
    const sx = Math.round((sw - cw) / 2), sy = Math.round((sh - ch) / 2);
    const cv = document.createElement("canvas");
    cv.width = cw; cv.height = ch;
    cv.getContext("2d").drawImage(v, sx, sy, cw, ch, 0, 0, cw, ch);
    cv.toBlob(b => {
      if (!b) return;
      stopCam();
      setPhotoScan(null);
      scanCardPhoto(new File([b], "camera.jpg", { type:"image/jpeg" }));
    }, "image/jpeg", 0.92);
  }
  const [keepPhoto, setKeepPhoto] = useState(true);
  const [acqMethod, setAcqMethod] = useState("break");
  const [acqCost, setAcqCost] = useState("");
  const [acqNotes, setAcqNotes] = useState("");
  const [savingScan, setSavingScan] = useState(false);
  // Reset acquisition fields whenever a new card is matched
  useEffect(() => {
    if (photoScan?.status === "matched") { setKeepPhoto(true); setAcqMethod("break"); setAcqCost(""); setAcqNotes(""); }
  }, [photoScan?.card?.id, photoScan?.status]);
  if (!scanModal) return null;
  return (
        <div style={{position:"fixed",inset:0,background:"#000",zIndex:9997,display:"flex",flexDirection:"column"}}>
          <style>{`
            @keyframes scanBeam { 0%{top:6%} 50%{top:90%} 100%{top:6%} }
            @keyframes scanDots { 0%,20%{opacity:0.2} 50%{opacity:1} 100%{opacity:0.2} }
            @keyframes spin { to { transform:rotate(360deg); } }
            @keyframes floatUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
          `}</style>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px",background:"rgba(10,10,10,0.95)",backdropFilter:"blur(20px)",borderBottom:"1px solid rgba(232,49,122,0.2)"}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:18,fontWeight:900,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>{"\uD83D\uDCF7 Scan Mode"}</span>
              {scanSession.length>0&&<span style={{fontSize:12,background:"rgba(232,49,122,0.15)",color:"#E8317A",border:"1px solid rgba(232,49,122,0.3)",borderRadius:20,padding:"3px 12px",fontWeight:700}}>{scanSession.length} added</span>}
            </div>
            <button onClick={()=>{setScanModal(false);setPhotoScan(null);}} style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.6)",borderRadius:10,padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",backdropFilter:"blur(10px)"}}>Done</button>
          </div>
          <div style={{flex:1,padding:20,overflowY:"auto"}}>
            {!photoScan&&(
              <label style={{display:"block",cursor:"pointer"}}>
                <div style={{background:"linear-gradient(135deg,rgba(232,49,122,0.05),rgba(123,47,247,0.05))",border:"2px dashed rgba(232,49,122,0.3)",borderRadius:20,padding:"50px 20px",textAlign:"center",transition:"all 0.2s"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(232,49,122,0.6)"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(232,49,122,0.3)"}>
                  <div style={{fontSize:56,marginBottom:12}}>{"\uD83D\uDCF7"}</div>
                  <div style={{fontSize:18,fontWeight:800,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",marginBottom:6}}>Tap to scan a card</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.3)"}}>Opens your camera -- identify any BoBA card instantly</div>
                </div>
                <div style={{marginTop:14,background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.2)",borderRadius:14,padding:"13px 16px"}}>
                  <div style={{fontSize:11,fontWeight:800,color:"#FBBF24",letterSpacing:0.5,marginBottom:8}}>📸 For the best results</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",display:"flex",gap:8,alignItems:"flex-start"}}><span>💡</span><span>Use good lighting and avoid glare on the card</span></div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",display:"flex",gap:8,alignItems:"flex-start"}}><span>🎯</span><span>Fill the frame with the card and hold steady — blurry shots read poorly</span></div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",display:"flex",gap:8,alignItems:"flex-start"}}><span>🔢</span><span>Keep the card number (bottom corner) visible for the best match</span></div>
                  </div>
                </div>
                <input type="file" accept="image/*" capture="environment" onChange={e=>{const f=e.target.files?.[0];if(f){setPhotoScan(null);scanCardPhoto(f);}e.target.value="";}} style={{position:"absolute",opacity:0,width:1,height:1,pointerEvents:"none"}}/>
              </label>
            )}
            {/* Laptop/desktop path: a live webcam view. capture="environment" only opens the camera
                on mobile; on a laptop it just shows a file picker. Releasing the stream on unmount
                matters, or the camera light stays on after the modal closes. */}
            {!photoScan && !camOn && (
              <button onClick={startCam} style={{width:"100%",marginTop:10,background:"rgba(123,156,255,0.1)",border:"1px solid rgba(123,156,255,0.4)",color:"#7B9CFF",borderRadius:12,padding:"11px 0",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                {"\uD83D\uDCF7"} Use my camera instead
              </button>
            )}
            {camErr && <div style={{marginTop:8,fontSize:11.5,color:"#E8317A",lineHeight:1.5}}>{camErr}</div>}
            {/* Speak-while-you-scan. OCR is reliable on the hero and card number but loses the small
                print (treatment, weapon) to glare — which is exactly what leaves variants tied. Saying
                "moose 80's rad" narrows the pool before matching, so the photo only has to settle the
                rest. The hint is consumed by the next scan, never carried to the one after. */}
            {!photoScan && (
              <div style={{marginTop:10,background:scanHintOn?"rgba(168,85,247,0.1)":"rgba(255,255,255,0.03)",border:"1px solid "+(scanHintOn?"rgba(168,85,247,0.45)":"rgba(255,255,255,0.1)"),borderRadius:12,padding:"10px 12px"}}>
                <div style={{display:"flex",alignItems:"center",gap:9}}>
                  <button onClick={toggleScanHint} style={{background:scanHintOn?"rgba(168,85,247,0.25)":"transparent",border:"1px solid "+(scanHintOn?"rgba(168,85,247,0.6)":"rgba(255,255,255,0.2)"),color:scanHintOn?"#C084FC":"rgba(255,255,255,0.6)",borderRadius:9,padding:"7px 12px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                    {scanHintOn ? "\uD83C\uDFA4 Listening" : "\uD83C\uDFA4 Say what it is"}
                  </button>
                  <div style={{fontSize:10.5,color:"rgba(255,255,255,0.4)",lineHeight:1.4,flex:1,minWidth:0}}>
                    {scanHint
                      ? <span style={{color:"#C084FC",fontWeight:700}}>heard: “{scanHint}”</span>
                      : scanHintOn ? "Say the treatment or weapon, then take the photo."
                      : "Optional — say e.g. “80’s rad” while you shoot to help it match."}
                  </div>
                </div>
              </div>
            )}
            {camOn && (
              <div style={{marginTop:10}}>
                {/* A webcam is 16:9 but a card is portrait, so filling the width gave a wide letterbox
                    with the card tiny in the middle. Fix the FRAME to a card-ish 4:5 and let the video
                    cover it \u2014 you lose the useless left/right edges, and the card fills the view.
                    The capture still grabs the full sensor frame, so nothing is actually cropped away. */}
                <div style={{position:"relative",width:"100%",maxWidth:340,aspectRatio:"4/5",margin:"0 auto",borderRadius:14,overflow:"hidden",background:"#000",border:"2px solid rgba(232,49,122,0.35)"}}>
                  <video ref={videoRef} playsInline muted style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                  <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
                    <div style={{height:"84%",aspectRatio:"5/7",border:"2px dashed rgba(255,255,255,0.55)",borderRadius:10}}/>
                  </div>
                </div>
                <div style={{display:"flex",gap:8,marginTop:10}}>
                  <button onClick={shootCam} style={{flex:2,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",border:"none",color:"#fff",borderRadius:12,padding:"12px 0",fontSize:14,fontWeight:900,cursor:"pointer",fontFamily:"inherit"}}>
                    {"\uD83D\uDCF8"} Capture
                  </button>
                  <button onClick={stopCam} style={{flex:1,background:"transparent",border:"1px solid rgba(255,255,255,0.2)",color:"rgba(255,255,255,0.6)",borderRadius:12,padding:"12px 0",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
            {photoScan?.status==="scanning"&&(
              <div style={{textAlign:"center",padding:"40px 20px"}}>
                {/* Animated scan frame */}
                <div style={{position:"relative",width:160,height:213,margin:"0 auto 24px",borderRadius:14,overflow:"hidden",background:"linear-gradient(135deg,rgba(232,49,122,0.08),rgba(123,47,247,0.08))",border:"2px solid rgba(232,49,122,0.3)"}}>
                  {/* card silhouette */}
                  <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:48,opacity:0.25}}>🃏</div>
                  {/* scanning beam */}
                  <div style={{position:"absolute",left:0,right:0,height:3,background:"linear-gradient(90deg,transparent,#E8317A,#FBBF24,#E8317A,transparent)",boxShadow:"0 0 16px rgba(232,49,122,0.9)",animation:"scanBeam 1.6s ease-in-out infinite"}}/>
                  {/* corner brackets */}
                  <div style={{position:"absolute",top:8,left:8,width:20,height:20,borderTop:"2px solid #E8317A",borderLeft:"2px solid #E8317A"}}/>
                  <div style={{position:"absolute",top:8,right:8,width:20,height:20,borderTop:"2px solid #E8317A",borderRight:"2px solid #E8317A"}}/>
                  <div style={{position:"absolute",bottom:8,left:8,width:20,height:20,borderBottom:"2px solid #E8317A",borderLeft:"2px solid #E8317A"}}/>
                  <div style={{position:"absolute",bottom:8,right:8,width:20,height:20,borderBottom:"2px solid #E8317A",borderRight:"2px solid #E8317A"}}/>
                </div>
                <div style={{fontSize:16,fontWeight:800,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",marginBottom:6}}>Identifying your card…</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>Reading hero, weapon &amp; treatment <span style={{display:"inline-block",animation:"scanDots 1.4s infinite"}}>●●●</span></div>
              </div>
            )}
            {photoScan?.status==="matched"&&photoScan.card&&(()=>{
              const c=photoScan.card,wc=WEAPON_COLORS[canonWeapon(c.weapon)]||"#888";
              return (
                <div style={{background:`linear-gradient(135deg,rgba(10,26,10,0.8),rgba(0,0,0,0.8))`,border:`1.5px solid rgba(74,222,128,0.3)`,borderRadius:20,padding:20,backdropFilter:"blur(10px)",animation:"floatUp 0.3s ease"}}>
                  <div style={{display:"flex",gap:14,marginBottom:16}}>
                    {c.imageUrl?<img src={c.imageUrl} alt={c.hero} style={{width:80,height:107,objectFit:"cover",borderRadius:10,flexShrink:0,boxShadow:"0 8px 32px rgba(0,0,0,0.5)"}}/>:<div style={{width:80,height:107,background:"var(--bz-s2)",borderRadius:10,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"var(--bz-ink-3)"}}>{c.hero}</div>}
                    <div>
                      <div style={{fontSize:18,fontWeight:900,color:"var(--bz-ink)",marginBottom:4}}>{c.hero}</div>
                      <div style={{fontSize:12,color:wc,fontWeight:700,marginBottom:2}}>{c.weapon}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>{c.treatment}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>#{c.cardNum} · {c.setName}</div>
                      <div style={{fontSize:20,fontWeight:900,color:wc,marginTop:6}}>{c.power}{"\u26A1"}</div>
                    </div>
                  </div>
                  {/* Keep photo toggle */}
                  {photoScan.scanPhoto && (
                    <div onClick={()=>setKeepPhoto(k=>!k)} style={{display:"flex",alignItems:"center",gap:12,background:keepPhoto?"rgba(74,222,128,0.08)":"rgba(255,255,255,0.03)",border:`1px solid ${keepPhoto?"rgba(74,222,128,0.35)":"rgba(255,255,255,0.1)"}`,borderRadius:12,padding:"10px 12px",marginBottom:12,cursor:"pointer"}}>
                      <img src={photoScan.scanPhoto} alt="" style={{width:44,height:59,objectFit:"cover",borderRadius:7,flexShrink:0}}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:13,fontWeight:800,color:keepPhoto?"#4ade80":"#ccc"}}>📸 Keep this photo</div>
                        <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>Saves your actual card pic — list it later without re-shooting</div>
                      </div>
                      <div style={{width:44,height:26,borderRadius:13,background:keepPhoto?"#4ade80":"rgba(255,255,255,0.15)",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                        <div style={{position:"absolute",top:3,left:keepPhoto?21:3,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
                      </div>
                    </div>
                  )}
                  {/* Acquisition info */}
                  <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:"12px 14px",marginBottom:12}}>
                    <div style={{fontSize:11,fontWeight:800,color:"rgba(255,255,255,0.5)",letterSpacing:1,textTransform:"uppercase",marginBottom:10}}>How'd you get it?</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
                      {LOT_METHODS.map(m=>(
                        <button key={m.v} onClick={()=>setAcqMethod(m.v)} style={{background:acqMethod===m.v?"rgba(232,49,122,0.2)":"rgba(255,255,255,0.04)",border:`1px solid ${acqMethod===m.v?"#E8317A":"rgba(255,255,255,0.1)"}`,color:acqMethod===m.v?"#E8317A":"#aaa",borderRadius:8,padding:"6px 10px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{m.l}</button>
                      ))}
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <input value={acqCost} onChange={e=>setAcqCost(e.target.value)} type="number" step="0.01" placeholder="Cost (optional)" style={{...inp,flex:1,fontSize:13}}/>
                      <input value={acqNotes} onChange={e=>setAcqNotes(e.target.value)} placeholder="Notes (optional)" style={{...inp,flex:1.4,fontSize:13}}/>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:10,alignItems:"center",marginBottom:12}}>
                    <button onClick={()=>setScanQty(q=>Math.max(1,q-1))} style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",color:"var(--bz-ink)",borderRadius:8,width:36,height:36,fontSize:20,cursor:"pointer",fontFamily:"inherit"}}>{"\u2212"}</button>
                    <span style={{fontSize:20,fontWeight:900,minWidth:40,textAlign:"center"}}>{scanQty}</span>
                    <button onClick={()=>setScanQty(q=>q+1)} style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",color:"var(--bz-ink)",borderRadius:8,width:36,height:36,fontSize:20,cursor:"pointer",fontFamily:"inherit"}}>+</button>
                    <button disabled={savingScan} onClick={async()=>{ setSavingScan(true); await confirmScan(c,scanQty,{ keepPhoto, scanPhoto:photoScan.scanPhoto, method:acqMethod, cost:acqCost, notes:acqNotes }); setSavingScan(false); }} style={{flex:1,background:"linear-gradient(135deg,#4ade80,#22c55e)",color:"#000",border:"none",borderRadius:10,padding:"10px 0",fontSize:14,fontWeight:900,cursor:savingScan?"wait":"pointer",fontFamily:"inherit",boxShadow:"0 4px 16px rgba(74,222,128,0.4)",opacity:savingScan?0.7:1}}>{savingScan?"Saving…":`\u2705 Add ${scanQty>1?scanQty:""}`}</button>
                  </div>
                  <button onClick={()=>setPhotoScan(null)} style={{width:"100%",background:"transparent",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.4)",borderRadius:10,padding:"8px 0",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Scan different card</button>
                </div>
              );
            })()}
            {photoScan?.status==="candidates"&&photoScan.candidates&&(
              <div style={{background:"rgba(13,13,13,0.9)",border:"1.5px solid rgba(251,191,36,0.35)",borderRadius:20,padding:"22px 20px",animation:"floatUp 0.3s ease"}}>
                <div style={{textAlign:"center",marginBottom:6}}>
                  <div style={{fontSize:30,marginBottom:6}}>🔍</div>
                  <div style={{fontSize:16,fontWeight:800,color:"#FBBF24",marginBottom:4}}>Did you mean one of these?</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:4}}>Couldn't be 100% sure — tap the right card</div>
                  {(photoScan.detected?.hero||photoScan.detected?.cardNum)&&<div style={{fontSize:10,color:"rgba(255,255,255,0.25)",marginBottom:14}}>Read: {photoScan.detected?.hero||"?"}{photoScan.detected?.cardNum?` · #${photoScan.detected.cardNum}`:""}</div>}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:10,marginBottom:16}}>
                  {photoScan.candidates.map(c=>{
                    const wc=WEAPON_COLORS[canonWeapon(c.weapon)]||"#888";
                    return (
                      <button key={c.id} onClick={()=>{ setPhotoScan({status:"matched",card:c,detected:photoScan.detected,scanPhoto:photoScan.scanPhoto}); setScanQty(1); }}
                        style={{background:"rgba(255,255,255,0.03)",border:`1.5px solid ${wc}55`,borderRadius:12,overflow:"hidden",cursor:"pointer",padding:0,fontFamily:"inherit",textAlign:"left",transition:"transform 0.15s,border-color 0.15s"}}
                        onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.borderColor=wc;}}
                        onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.borderColor=wc+"55";}}>
                        {c.imageUrl&&<img src={c.imageUrl} alt="" style={{width:"100%",aspectRatio:"3/4",objectFit:"cover",display:"block"}}/>}
                        <div style={{padding:"7px 9px"}}>
                          <div style={{fontSize:12,fontWeight:800,color:"var(--bz-ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{c.hero}</div>
                          <div style={{fontSize:9,color:"#999"}}>#{c.cardNum} · {c.treatment}</div>
                          {c.weapon&&<span style={{fontSize:9,color:wc,fontWeight:700}}>{c.weapon}</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div style={{display:"flex",gap:10,justifyContent:"center"}}>
                  <label style={{background:"transparent",color:"rgba(255,255,255,0.5)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:12,padding:"10px 20px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"inline-block"}}>
                    None — Rescan<input type="file" accept="image/*" capture="environment" onChange={e=>{const f=e.target.files?.[0];if(f){setPhotoScan(null);scanCardPhoto(f);}e.target.value="";}} style={{display:"none"}}/>
                  </label>
                </div>
              </div>
            )}
            {photoScan?.status==="nomatch"&&(
              <div style={{background:"rgba(26,10,10,0.8)",border:"1.5px solid rgba(232,49,122,0.3)",borderRadius:20,padding:28,textAlign:"center",animation:"floatUp 0.3s ease"}}>
                <div style={{fontSize:36,marginBottom:10}}>{"\u274C"}</div>
                <div style={{fontSize:16,fontWeight:800,color:"#E8317A",marginBottom:8}}>Card not recognized</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:16,lineHeight:1.6}}>Tip: fill the frame with the card, avoid glare,<br/>and keep the card number visible</div>
                <label style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:12,padding:"12px 28px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",display:"inline-block",boxShadow:"0 4px 20px rgba(232,49,122,0.4)"}}>
                  Try Again<input type="file" accept="image/*" capture="environment" onChange={e=>{const f=e.target.files?.[0];if(f){setPhotoScan(null);scanCardPhoto(f);}e.target.value="";}} style={{display:"none"}}/>
                </label>
              </div>
            )}
            {photoScan?.status==="error"&&(
              <div style={{background:"rgba(26,10,10,0.8)",border:"1.5px solid rgba(232,49,122,0.3)",borderRadius:20,padding:28,textAlign:"center",animation:"floatUp 0.3s ease"}}>
                <div style={{fontSize:36,marginBottom:10}}>{"\u26A0\uFE0F"}</div>
                <div style={{fontSize:16,fontWeight:800,color:"#E8317A",marginBottom:6}}>Scan failed</div>
                {photoScan.message&&<div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginBottom:16,fontFamily:"monospace"}}>{photoScan.message}</div>}
                <label style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:12,padding:"12px 28px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",display:"inline-block"}}>
                  Try Again<input type="file" accept="image/*" capture="environment" onChange={e=>{const f=e.target.files?.[0];if(f){setPhotoScan(null);scanCardPhoto(f);}e.target.value="";}} style={{display:"none"}}/>
                </label>
              </div>
            )}
            {scanSession.length>0&&(
              <div style={{marginTop:20}}>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",fontWeight:700,textTransform:"uppercase",letterSpacing:1.5,marginBottom:10}}>Added this session</div>
                {scanSession.slice(0,5).map((s,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                    <span style={{fontSize:20}}>{"\u2705"}</span>
                    <div style={{flex:1}}><div style={{fontSize:13,fontWeight:700}}>{s.card.hero}</div><div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>{s.card.treatment} · {s.card.power}{"\u26A1"}</div></div>
                    <span style={{fontSize:14,fontWeight:800,color:"#4ade80"}}>+{s.qty}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

  );
}

function CompModal({ compCard, setCompCard, marketSales, WEAPON_COLORS , cards, listings}) {
  if (!compCard) return null;
  const c = compCard;
  const wc = WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
  const exactSales = marketSales.filter(s=>s.cardId===c.id);
  const nearSales = marketSales.filter(s=>s.cardId!==c.id&&(
  (s.cardName===c.hero&&s.cardTreatment===c.treatment)||
  (s.cardName===c.hero&&s.cardWeapon===c.weapon&&s.cardPower===c.power)||
  (s.cardName===c.hero&&s.cardTreatment===c.treatment&&s.cardPower!==c.power)
  ));
  function stats(sales) {
  if(!sales.length) return null;
  const prices = sales.map(s=>s.price).sort((a,b)=>a-b);
  const avg = prices.reduce((a,b)=>a+b,0)/prices.length;
  const last = sales.sort((a,b)=>b.soldDate?.localeCompare(a.soldDate||"")||0)[0];
  return { avg, high:Math.max(...prices), low:Math.min(...prices), count:prices.length, last };
  }
  const exact = stats(exactSales);
  const near = stats(nearSales);
  return (
  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.9)",zIndex:12500,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(20px)",padding:16}} onClick={()=>setCompCard(null)}>
    <div style={{background:"linear-gradient(135deg,var(--bz-s1),#0a0d1a)",border:`1px solid ${wc}33`,borderRadius:24,width:"100%",maxWidth:560,maxHeight:"90vh",overflowY:"auto",boxShadow:`0 40px 120px ${wc}22`,animation:"floatUp 0.3s ease"}} onClick={e=>e.stopPropagation()}>
      {/* Header */}
      <div style={{display:"flex",gap:14,padding:"24px 24px 20px",borderBottom:`1px solid ${wc}22`}}>
        {c.imageUrl?<img src={c.imageUrl} alt={c.hero} style={{width:64,height:85,objectFit:"cover",borderRadius:10,flexShrink:0,boxShadow:`0 8px 24px ${wc}44`}}/>:<div style={{width:64,height:85,background:"rgba(255,255,255,0.04)",borderRadius:10,flexShrink:0}}/>}
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:`${wc}`,fontWeight:700,textTransform:"uppercase",letterSpacing:2,marginBottom:4}}>{"\uD83D\uDCCA Card Comp"}</div>
          <div style={{fontSize:20,fontWeight:900,color:"var(--bz-ink)",marginBottom:4}}>{c.hero}</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            <span style={{background:`${wc}22`,color:wc,borderRadius:6,padding:"2px 8px",fontSize:11,fontWeight:700}}>{c.weapon}</span>
            <span style={{background:"rgba(255,255,255,0.06)",color:"rgba(255,255,255,0.5)",borderRadius:6,padding:"2px 8px",fontSize:11}}>{c.treatment}</span>
            {c.power&&<span style={{background:"rgba(255,255,255,0.06)",color:"rgba(255,255,255,0.5)",borderRadius:6,padding:"2px 8px",fontSize:11}}>{c.power}{"\u26A1"}</span>}
            {c.setName&&<span style={{background:"rgba(255,255,255,0.04)",color:"rgba(255,255,255,0.3)",borderRadius:6,padding:"2px 8px",fontSize:10}}>{c.setName}</span>}
          </div>
        </div>
        <button onClick={()=>setCompCard(null)} style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.3)",cursor:"pointer",fontSize:22,padding:0,alignSelf:"flex-start",lineHeight:1}}>{"\u00D7"}</button>
      </div>
      <div style={{padding:24}}>
        {/* Exact match comps */}
        <div style={{marginBottom:24}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:800,color:"#4ade80"}}>{"\u2705 Exact Match Sales"}</div>
            <span style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>Same card, same treatment, same power</span>
          </div>
          {exact?(
            <>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:12}}>
                {[{l:"Avg Sale",v:`$${exact.avg.toFixed(2)}`,c:"#7B9CFF"},{l:"High",v:`$${exact.high.toFixed(2)}`,c:"#4ade80"},{l:"Low",v:`$${exact.low.toFixed(2)}`,c:"#FBBF24"},{l:"# Sales",v:exact.count,c:"rgba(255,255,255,0.6)"}].map(({l,v,c:col})=>(
                  <div key={l} style={{background:"rgba(0,0,0,0.4)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:"10px 8px",textAlign:"center"}}>
                    <div style={{fontSize:16,fontWeight:900,color:col}}>{v}</div>
                    <div style={{fontSize:9,color:"rgba(255,255,255,0.3)",marginTop:3,textTransform:"uppercase",letterSpacing:1}}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginBottom:8}}>Last sale: <span style={{color:"#4ade80",fontWeight:700}}>${(exact.last?.price||0).toFixed(2)}</span> on {exact.last?.soldDate||"--"}</div>
              {/* Sales list */}
              <div style={{background:"rgba(0,0,0,0.3)",borderRadius:12,overflow:"hidden"}}>
                {exactSales.sort((a,b)=>b.soldDate?.localeCompare(a.soldDate||"")||0).slice(0,8).map((s,i)=>(
                  <div key={s.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 14px",borderBottom:"1px solid rgba(255,255,255,0.03)",background:i%2===0?"transparent":"rgba(255,255,255,0.01)"}}>
                    <span style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>{s.soldDate||"--"}</span>
                    <span style={{fontSize:14,fontWeight:800,color:"#4ade80"}}>${(s.price||0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </>
          ):(
            <div style={{background:"rgba(255,255,255,0.02)",border:"1px dashed rgba(255,255,255,0.08)",borderRadius:12,padding:"24px",textAlign:"center",color:"rgba(255,255,255,0.3)"}}>
              <div style={{fontSize:14,marginBottom:4}}>No exact sales yet on this platform</div>
              <div style={{fontSize:11}}>Be the first -- list this card in the Market tab</div>
            </div>
          )}
        </div>
        {/* Similar cards */}
        <div style={{marginBottom:24}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:800,color:"#FBBF24"}}>{"\uD83D\uDD0D Similar Card Sales"}</div>
            <span style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>Same hero, similar treatment or power</span>
          </div>
          {near?(
            <>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:12}}>
                {[{l:"Avg",v:`$${near.avg.toFixed(2)}`,c:"#7B9CFF"},{l:"High",v:`$${near.high.toFixed(2)}`,c:"#4ade80"},{l:"# Sales",v:near.count,c:"rgba(255,255,255,0.6)"}].map(({l,v,c:col})=>(
                  <div key={l} style={{background:"rgba(0,0,0,0.4)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:"10px 8px",textAlign:"center"}}>
                    <div style={{fontSize:16,fontWeight:900,color:col}}>{v}</div>
                    <div style={{fontSize:9,color:"rgba(255,255,255,0.3)",marginTop:3,textTransform:"uppercase",letterSpacing:1}}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{background:"rgba(0,0,0,0.3)",borderRadius:12,overflow:"hidden"}}>
                {nearSales.sort((a,b)=>b.soldDate?.localeCompare(a.soldDate||"")||0).slice(0,6).map((s,i)=>(
                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 14px",borderBottom:"1px solid rgba(255,255,255,0.03)",background:i%2===0?"transparent":"rgba(255,255,255,0.01)"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.cardTreatment} · {s.cardPower}{"\u26A1"}</div>
                      <div style={{fontSize:10,color:"rgba(255,255,255,0.25)"}}>{s.soldDate||"--"}</div>
                    </div>
                    <span style={{fontSize:14,fontWeight:800,color:"#FBBF24",flexShrink:0}}>${(s.price||0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </>
          ):(
            <div style={{background:"rgba(255,255,255,0.02)",border:"1px dashed rgba(255,255,255,0.08)",borderRadius:12,padding:"16px",textAlign:"center",color:"rgba(255,255,255,0.3)",fontSize:12}}>
              No similar card sales yet
            </div>
          )}
        </div>
        {/* eBay sold listings — opens real eBay search */}
        {(()=>{
          const q = encodeURIComponent(`Bo Battle Arena ${c.hero||""} ${c.weapon||""} ${c.treatment||""}`.replace(/\s+/g," ").trim());
          const soldUrl = `https://www.ebay.com/sch/i.html?_nkw=${q}&_sacat=0&LH_Sold=1&LH_Complete=1&_sop=13`;
          const activeUrl = `https://www.ebay.com/sch/i.html?_nkw=${q}&_sacat=0&_sop=15`;
          return (
            <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:"16px 18px"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <div style={{fontSize:13,fontWeight:800,color:"#7B9CFF"}}>🛒 Wider Market Comps</div>
                <span style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>Check the broader market</span>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <a href={soldUrl} target="_blank" rel="noopener noreferrer" style={{flex:"1 1 130px",textAlign:"center",textDecoration:"none",background:"rgba(74,222,128,0.12)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:10,padding:"10px 12px",fontSize:12,fontWeight:800}}>eBay Sold ↗</a>
                <a href={activeUrl} target="_blank" rel="noopener noreferrer" style={{flex:"1 1 130px",textAlign:"center",textDecoration:"none",background:"rgba(123,156,255,0.12)",border:"1px solid rgba(123,156,255,0.3)",color:"#7B9CFF",borderRadius:10,padding:"10px 12px",fontSize:12,fontWeight:800}}>eBay Active ↗</a>
              </div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.25)",marginTop:10,lineHeight:1.5}}>Opens a live search for this card on eBay. Niche BoBA cards may have limited results — your best comps are the community sales above.</div>
            </div>
          );
        })()}
      </div>
    </div>
  </div>
  );
}

// ── PACK RIP SIMULATOR ─────────────────────────────────────────
const PACK_PRODUCTS = {
  "Alpha Edition - Blaster":  { set:"Alpha", foilSlots:1, paperSlots:5, foilIsHit:false },
  "Alpha Edition - Booster":  { set:"Alpha", foilSlots:1, paperSlots:5, foilIsHit:false },
  "Alpha Edition - Hobby":    { set:"Alpha", foilSlots:3, paperSlots:3, foilIsHit:true  },
  "Alpha Edition - Jumbo":    { set:"Alpha", foilSlots:3, paperSlots:3, foilIsHit:true  },
  "Alpha Update - Blaster":   { set:"Alpha", foilSlots:1, paperSlots:5, foilIsHit:false },
  "Alpha Update - Booster":   { set:"Alpha", foilSlots:1, paperSlots:5, foilIsHit:false },
  "Alpha Update - Hobby":     { set:"Alpha", foilSlots:3, paperSlots:3, foilIsHit:true  },
  "Alpha Update - Jumbo":     { set:"Alpha", foilSlots:3, paperSlots:3, foilIsHit:true  },
  "Alpha Blast - Blast Box":  { set:"Alpha", foilSlots:2, paperSlots:4, foilIsHit:true  },
  "Griffey 2026 - Blaster":   { set:"Griffey", foilSlots:1, paperSlots:5, foilIsHit:false },
  "Griffey 2026 - Double Mega":{ set:"Griffey", foilSlots:3, paperSlots:3, foilIsHit:true },
  "Griffey 2026 - Hobby":     { set:"Griffey", foilSlots:3, paperSlots:3, foilIsHit:true  },
  "Griffey 2026 - Jumbo":     { set:"Griffey", foilSlots:3, paperSlots:3, foilIsHit:true  },
  "Tecmo Bowl - Double Mega": { set:"Tecmo",   foilSlots:3, paperSlots:3, foilIsHit:true  },
  "Tecmo Bowl - Hobby":       { set:"Tecmo",   foilSlots:3, paperSlots:3, foilIsHit:true  },
};

function rollTreatment(product) {
  const r = Math.random();
  const isBooster = product.includes("Booster") || product.includes("Blaster");
  const isHobby   = product.includes("Hobby");
  const isJumbo   = product.includes("Jumbo");
  if (r < 1/600)  return "Superfoil";
  if (r < 2/600)  return "Pink Battlefoil";
  if (isBooster && r < 2/600 + 1/120) return "Hex";
  if (isBooster && r < 2/600 + 2/120) return "Gum";
  if (isBooster && r < 2/600 + 3/120) return "Green Battlefoil";
  if (isHobby   && r < 2/600 + 1/20)  return "Auto";
  if (isJumbo   && r < 2/600 + 3/50)  return "Auto";
  if (isBooster && r < 2/600 + 2/120 + 2/120) return "Auto";
  if (r < 0.50) return "Silver";
  const nice = ["Rainbow","Holo","Gold","Prizm","Refractor","Color Match","Optic"];
  return nice[Math.floor(Math.random() * nice.length)];
}

function pickCard(pool, usedIds) {
  const avail = pool.filter(c => !usedIds.has(c.id));
  if (!avail.length) return null;
  return avail[Math.floor(Math.random() * avail.length)];
}

function ripPack(product, cards) {
  const cfg = PACK_PRODUCTS[product];
  const setCards  = cards.filter(c => c.setName && c.setName.toLowerCase().includes(cfg.set.toLowerCase()) && c.weapon !== "Super");
  const hotdogs   = cards.filter(c => (c.treatment||"").toLowerCase().includes("hot dog") || (c.hero||"").toLowerCase().includes("hot dog"));
  const plays     = cards.filter(c => (c.treatment||"").toLowerCase().includes("play") || (c.cardType||"").toLowerCase().includes("play"));
  const baseCards = setCards.filter(c => !c.treatment || c.treatment.toLowerCase() === "base" || c.treatment.toLowerCase() === "silver");
  const usedIds   = new Set();
  const pulled    = [];

  for (let i = 0; i < cfg.paperSlots; i++) {
    const c = pickCard(baseCards, usedIds);
    if (c) { pulled.push({...c, slotType:"paper"}); usedIds.add(c.id); }
  }
  for (let i = 0; i < cfg.foilSlots; i++) {
    const isHit = cfg.foilIsHit && i === cfg.foilSlots - 1;
    const treatment = rollTreatment(product);
    const pool = setCards.filter(c => (c.treatment||"").toLowerCase() === treatment.toLowerCase());
    const c = pickCard(pool.length ? pool : setCards, usedIds);
    if (c) { pulled.push({...c, treatment, slotType: isHit ? "hit" : "foil", isHit}); usedIds.add(c.id); }
  }
  for (let i = 0; i < 3; i++) {
    const c = pickCard(plays, usedIds);
    if (c) { pulled.push({...c, slotType:"play"}); usedIds.add(c.id); }
  }
  if (hotdogs.length) {
    const hd = hotdogs[Math.floor(Math.random() * hotdogs.length)];
    pulled.push({...hd, slotType:"hotdog"});
  }
  return pulled;
}

function rarityScore(card) {
  const t = (card.treatment||"").toLowerCase();
  const w = (card.weapon||"").toLowerCase();
  if (t.includes("superfoil"))       return 100;
  if (t.includes("pink battlefoil")) return 90;
  if (t.includes("auto"))            return 85;
  if (t.includes("green battlefoil"))return 80;
  if (w === "hex" || w === "gum")    return 75;
  if (t.includes("rainbow"))         return 60;
  if (t.includes("holo"))            return 55;
  if (t.includes("gold"))            return 50;
  if (t.includes("prizm"))           return 45;
  if (t.includes("silver"))          return 20;
  return 10;
}

const WEAPON_GLOW = { Fire:"#F97316", Ice:"#60A5FA", Steel:"#C0C0C0", Brawl:"#EF4444",
  Glow:"#4ade80", Hex:"#A855F7", Gum:"#F472B6", Metallic:"var(--bz-line)", Alt:"#FFFFFF", Super:"#F59E0B" };

// ── CARD FX OVERLAY ── hunt (wanted) + caught (collected) animations ──────────
function CardFxOverlay({ fx, onDone }) {
  const { type, card } = fx;
  React.useEffect(() => {
    const t = setTimeout(onDone, type === "caught" ? 2300 : 1900);
    return () => clearTimeout(t);
  }, [type, onDone]);

  const img = card?.imageUrl;
  const wrap = { position:"fixed", inset:0, zIndex:100000, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none", overflow:"hidden" };

  if (type === "hunt") {
    return (
      <div style={wrap} onClick={onDone}>
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at center, rgba(251,191,36,0.18), rgba(0,0,0,0.82) 70%)", animation:"fxBackdrop 1.9s ease forwards" }}/>
        <div style={{ position:"relative", width:"min(260px,60vw)", aspectRatio:"3/4", animation:"huntCardIn 1.9s cubic-bezier(0.22,1,0.36,1) forwards" }}>
          {/* pulsing target rings */}
          {[0,0.25,0.5].map((d,i)=>(
            <div key={i} style={{ position:"absolute", inset:-30, borderRadius:"50%", border:"2px solid rgba(251,191,36,0.7)", animation:`huntRing 1.6s ease ${d}s forwards` }}/>
          ))}
          {/* rotating reticle */}
          <div style={{ position:"absolute", inset:-48, animation:"huntReticle 3s linear infinite" }}>
            {[0,90,180,270].map(deg=>(
              <div key={deg} style={{ position:"absolute", top:"50%", left:"50%", width:18, height:3, background:"#FBBF24", borderRadius:2, transform:`rotate(${deg}deg) translateX(calc(min(130px,30vw) + 24px))`, transformOrigin:"left center", boxShadow:"0 0 8px rgba(251,191,36,0.8)" }}/>
            ))}
          </div>
          {/* card */}
          <div style={{ position:"absolute", inset:0, borderRadius:12, overflow:"hidden", border:"2.5px solid #FBBF24", boxShadow:"0 0 40px rgba(251,191,36,0.6), inset 0 0 30px rgba(251,191,36,0.15)" }}>
            {img ? <img src={img} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/> : <div style={{ width:"100%", height:"100%", background:"var(--bz-s2)" }}/>}
            {/* scan line */}
            <div style={{ position:"absolute", left:0, right:0, height:"45%", background:"linear-gradient(180deg, transparent, rgba(251,191,36,0.35), transparent)", animation:"huntScan 1.6s ease forwards" }}/>
            {/* targeting corners */}
            <div style={{ position:"absolute", top:8, left:8, width:22, height:22, borderTop:"3px solid #FBBF24", borderLeft:"3px solid #FBBF24", animation:"huntCorner 1.6s ease 0s forwards", opacity:0 }}/>
            <div style={{ position:"absolute", top:8, right:8, width:22, height:22, borderTop:"3px solid #FBBF24", borderRight:"3px solid #FBBF24", animation:"huntCorner 1.6s ease 0.1s forwards", opacity:0 }}/>
            <div style={{ position:"absolute", bottom:8, left:8, width:22, height:22, borderBottom:"3px solid #FBBF24", borderLeft:"3px solid #FBBF24", animation:"huntCorner 1.6s ease 0.2s forwards", opacity:0 }}/>
            <div style={{ position:"absolute", bottom:8, right:8, width:22, height:22, borderBottom:"3px solid #FBBF24", borderRight:"3px solid #FBBF24", animation:"huntCorner 1.6s ease 0.3s forwards", opacity:0 }}/>
          </div>
        </div>
        {/* text */}
        <div style={{ position:"absolute", bottom:"18%", textAlign:"center", animation:"huntText 1.9s ease forwards" }}>
          <div style={{ fontSize:"clamp(20px,4vw,32px)", fontWeight:900, color:"#FBBF24", letterSpacing:6, textShadow:"0 0 20px rgba(251,191,36,0.8)", fontFamily:"'Trebuchet MS',sans-serif" }}>🎯 TARGET ACQUIRED</div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.7)", letterSpacing:2, marginTop:8, textTransform:"uppercase" }}>Now hunting · {card?.hero}</div>
        </div>
      </div>
    );
  }

  // CAUGHT
  const confettiColors = ["#E8317A","#FBBF24","#7B2FF7","#4ade80","#7B9CFF","#F472B6"];
  return (
    <div style={wrap} onClick={onDone}>
      <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at center, rgba(232,49,122,0.22), rgba(0,0,0,0.85) 72%)", animation:"fxBackdrop 2.3s ease forwards" }}/>
      {/* white flash */}
      <div style={{ position:"absolute", inset:0, background:"#fff", animation:"caughtFlash 2.3s ease forwards" }}/>
      {/* confetti */}
      {Array.from({length:50}).map((_,i)=>{
        const left = Math.random()*100;
        const delay = Math.random()*0.5;
        const dur = 1.6 + Math.random()*1.2;
        const size = 6 + Math.random()*8;
        const color = confettiColors[i % confettiColors.length];
        const round = Math.random() > 0.5;
        return <div key={i} style={{ position:"absolute", top:-20, left:`${left}%`, width:size, height:round?size:size*0.5, background:color, borderRadius:round?"50%":2, animation:`confettiFall ${dur}s ease ${delay}s forwards`, boxShadow:`0 0 6px ${color}` }}/>;
      })}
      {/* burst rings */}
      {[0,0.15,0.3].map((d,i)=>(
        <div key={i} style={{ position:"absolute", width:"min(280px,65vw)", aspectRatio:"1", borderRadius:"50%", border:"3px solid rgba(232,49,122,0.6)", animation:`caughtBurst 1.4s ease ${d}s forwards` }}/>
      ))}
      {/* pop stars */}
      {[["20%","22%"],["78%","20%"],["18%","72%"],["80%","74%"],["50%","14%"]].map(([t,l],i)=>(
        <div key={i} style={{ position:"absolute", top:t, left:l, fontSize:"clamp(24px,5vw,40px)", animation:`starPop 1.3s ease ${0.2+0.12*i}s forwards`, opacity:0 }}>⭐</div>
      ))}
      {/* card */}
      <div style={{ position:"relative", width:"min(250px,58vw)", aspectRatio:"3/4", animation:"caughtCardIn 2.3s cubic-bezier(0.34,1.56,0.64,1) forwards", borderRadius:14, overflow:"hidden", border:"3px solid #4ade80", boxShadow:"0 0 50px rgba(74,222,128,0.7), 0 0 100px rgba(232,49,122,0.4)" }}>
        {img ? <img src={img} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/> : <div style={{ width:"100%", height:"100%", background:"var(--bz-s2)" }}/>}
        <div style={{ position:"absolute", top:8, right:8, background:"#4ade80", color:"#000", borderRadius:20, padding:"4px 12px", fontSize:13, fontWeight:900 }}>✅ GOT IT</div>
      </div>
      {/* text */}
      <div style={{ position:"absolute", bottom:"16%", textAlign:"center", animation:"caughtText 2.3s ease forwards" }}>
        <div style={{ fontSize:"clamp(22px,4.5vw,38px)", fontWeight:900, color:"#4ade80", letterSpacing:3, textShadow:"0 0 24px rgba(74,222,128,0.9)", fontFamily:"'Trebuchet MS',sans-serif" }}>ADDED TO COLLECTION!</div>
        <div style={{ fontSize:14, color:"#fff", letterSpacing:2, marginTop:8, textTransform:"uppercase", fontWeight:700 }}>{card?.hero} · {card?.treatment}</div>
      </div>
    </div>
  );
}

// ── RAINBOW COMPLETE — GRAND CELEBRATION ──────────────────────────────────────
function RainbowCelebration({ fx, onDone }) {
  const { name, cards = [] } = fx;
  React.useEffect(() => {
    const t = setTimeout(onDone, 5200);
    return () => clearTimeout(t);
  }, [onDone]);

  const rainbowColors = ["#F97316","#FBBF24","#4ade80","#60A5FA","#A855F7","#F472B6","#EF4444"];
  const confettiColors = [...rainbowColors, "#FFFFFF", "#E8317A"];
  // fan out up to 7 card images
  const fanCards = cards.filter(c=>c.imageUrl).slice(0,7);
  const n = fanCards.length || 1;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:100001, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none", overflow:"hidden" }} onClick={onDone}>
      {/* dark backdrop */}
      <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at center, rgba(20,0,30,0.6), rgba(0,0,0,0.92) 75%)", animation:"rbBackdrop 5.2s ease forwards" }}/>
      {/* rotating rainbow god-rays */}
      <div style={{ position:"absolute", width:"180vmax", height:"180vmax", animation:"rbRayspin 8s linear infinite, rbBackdrop 5.2s ease forwards", background:"repeating-conic-gradient(from 0deg, rgba(249,115,22,0.10) 0deg 12deg, transparent 12deg 24deg, rgba(168,85,247,0.10) 24deg 36deg, transparent 36deg 48deg)", pointerEvents:"none" }}/>
      {/* shockwave rings */}
      {[0,0.3,0.6].map((d,i)=>(
        <div key={i} style={{ position:"absolute", width:"40vmin", height:"40vmin", borderRadius:"50%", border:"4px solid rgba(168,85,247,0.5)", animation:`rbShockwave 1.6s ease ${d}s forwards` }}/>
      ))}
      {/* confetti storm */}
      {Array.from({length:90}).map((_,i)=>{
        const left=Math.random()*100, delay=Math.random()*1.2, dur=2.2+Math.random()*2;
        const size=7+Math.random()*10, color=confettiColors[i%confettiColors.length], round=Math.random()>0.5;
        return <div key={i} style={{ position:"absolute", top:-30, left:`${left}%`, width:size, height:round?size:size*0.45, background:color, borderRadius:round?"50%":2, animation:`rbConfetti ${dur}s linear ${delay}s forwards`, boxShadow:`0 0 8px ${color}` }}/>;
      })}
      {/* fanned cards */}
      <div style={{ position:"relative", width:"min(220px,50vw)", aspectRatio:"3/4", marginTop:"-4vh" }}>
        {fanCards.map((c,i)=>{
          const spread=14; // deg between cards
          const rot=(i-(n-1)/2)*spread;
          const offset=(i-(n-1)/2)*30;
          return (
            <div key={c.id} style={{ position:"absolute", inset:0, transform:`translateX(${offset}px)`, "--rb-rot":`${rot}deg`, "--rb-rot-start":`${rot*2}deg`, animation:`rbCardFan 5.2s cubic-bezier(0.34,1.56,0.64,1) ${0.3+i*0.08}s both`, transformOrigin:"bottom center" }}>
              <div style={{ width:"100%", height:"100%", borderRadius:12, overflow:"hidden", border:"3px solid rgba(255,255,255,0.9)", boxShadow:`0 0 30px ${rainbowColors[i%rainbowColors.length]}, 0 10px 40px rgba(0,0,0,0.6)` }}>
                <img src={c.imageUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
              </div>
            </div>
          );
        })}
      </div>
      {/* big rainbow badge */}
      <div style={{ position:"absolute", top:"14%", fontSize:"clamp(60px,14vw,130px)", animation:"rbBadge 5.2s ease 0.2s both, rbHue 3s linear infinite" }}>🌈</div>
      {/* title */}
      <div style={{ position:"absolute", bottom:"20%", textAlign:"center", animation:"rbTitle 5.2s ease 0.3s both", padding:"0 20px" }}>
        <div style={{ fontSize:"clamp(28px,6vw,56px)", fontWeight:900, letterSpacing:2, fontFamily:"'Trebuchet MS',sans-serif", background:"linear-gradient(90deg,#F97316,#FBBF24,#4ade80,#60A5FA,#A855F7,#F472B6,#EF4444)", backgroundSize:"200% auto", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", textShadow:"0 0 40px rgba(168,85,247,0.4)", animation:"rbHue 4s linear infinite" }}>RAINBOW COMPLETE!</div>
      </div>
      {/* subtitle */}
      <div style={{ position:"absolute", bottom:"13%", textAlign:"center", animation:"rbSub 5.2s ease 0.6s both", padding:"0 20px" }}>
        <div style={{ fontSize:"clamp(16px,3vw,24px)", fontWeight:800, color:"#fff", letterSpacing:1, textShadow:"0 2px 12px rgba(0,0,0,0.8)" }}>{name}</div>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.6)", letterSpacing:2, marginTop:8, textTransform:"uppercase" }}>All {cards.length} cards collected · tap to dismiss</div>
      </div>
    </div>
  );
}

// ── PUBLIC HOMEPAGE ── the front door at bazookadash.com ──────────────────────
function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive:true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top:0, behavior:"smooth" })}
      title="Back to top"
      style={{
        position:"fixed", bottom:24, right:24, zIndex:900,
        width:48, height:48, borderRadius:"50%",
        background:"linear-gradient(135deg,#E8317A,#7B2FF7)",
        border:"none", color:"#fff", fontSize:20, cursor:"pointer",
        boxShadow:"0 6px 24px rgba(232,49,122,0.45)",
        display:"flex", alignItems:"center", justifyContent:"center",
        transition:"transform 0.15s, opacity 0.2s",
      }}
      onMouseEnter={e=>e.currentTarget.style.transform="translateY(-3px)"}
      onMouseLeave={e=>e.currentTarget.style.transform=""}>
      ↑
    </button>
  );
}

function ComingSoon() {
  const [signingIn, setSigningIn] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailMode, setEmailMode] = useState("signup"); // signup | login
  const [emEmail, setEmEmail] = useState("");
  const [emPass, setEmPass] = useState("");
  const [emName, setEmName] = useState("");
  const [emBusy, setEmBusy] = useState(false);
  const [emErr, setEmErr] = useState("");
  const [emOk, setEmOk] = useState("");   // the form said NOTHING on success — that was the bug
  async function earlyAccessLogin() {
    setSigningIn(true);
    try { await signInWithPopup(auth, googleProvider); }
    catch { setSigningIn(false); }
  }
  function emErrMsg(code){
    const m={"auth/email-already-in-use":"That email already has an account — try logging in instead.","auth/invalid-email":"That doesn't look like a valid email.","auth/weak-password":"Password must be at least 6 characters.","auth/wrong-password":"Incorrect password.","auth/user-not-found":"No account with that email — try signing up.","auth/invalid-credential":"Email or password is incorrect."};
    return m[code]||"Something went wrong. Try again.";
  }
  async function submitEmail(){
    setEmErr(""); setEmOk("");
    const email=emEmail.trim().toLowerCase();
    if(!email){ setEmErr("Enter your email."); return; }
    if(!emPass||emPass.length<6){ setEmErr("Password must be at least 6 characters."); return; }
    setEmBusy(true);
    try {
      if(emailMode==="signup"){
        const cred=await createUserWithEmailAndPassword(auth,email,emPass);
        if(emName.trim()){ try{ await updateProfile(cred.user,{displayName:emName.trim()}); }catch(e){} }
        setEmOk("Account created \u2014 taking you in\u2026");
      } else {
        await signInWithEmailAndPassword(auth,email,emPass);
        setEmOk("Signed in \u2014 taking you in\u2026");
      }
      // Deliberately DON'T clear emBusy. On success the button stays locked through the hand-off,
      // so it can't be pressed a second time.
      //
      // Previously the success path did nothing at all: no message, no redirect, button re-enabled.
      // The account WAS created, but the form looked dead, so people pressed "Create Account" again
      // and got told the email was already in use. Awful first impression, and entirely avoidable.
      //
      // Then take them straight to the card database. Landing a brand-new user back on a marketing
      // page after they've just signed up is a dead end — they came here for the cards, so show
      // them the cards.
      setTimeout(() => { window.location.href = "/cards"; }, 700);
      return;
    } catch(e){ setEmErr(emErrMsg(e.code)); setEmBusy(false); }
  }
  return (
    <div style={{ minHeight:"100vh", background:"#08000a", color:"var(--bz-ink)", fontFamily:"'Trebuchet MS',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", padding:24, position:"relative", overflow:"hidden" }}>
      <div style={{ position:"fixed", inset:0, pointerEvents:"none", background:"radial-gradient(ellipse 80% 50% at 50% 0%, rgba(232,49,122,0.18), transparent 70%), radial-gradient(ellipse 60% 50% at 80% 60%, rgba(123,47,247,0.12), transparent 70%)" }}/>
      <div style={{ position:"relative", zIndex:2, textAlign:"center", maxWidth:540 }}>
        <img src="/Bazooka_Logo_cropped.png" alt="Bazooka" style={{ height:"clamp(80px,14vw,130px)", width:"auto", margin:"0 auto 32px", display:"block", filter:"drop-shadow(0 8px 30px rgba(232,49,122,0.45))" }}/>
        <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(232,49,122,0.12)", border:"1px solid rgba(232,49,122,0.35)", borderRadius:30, padding:"6px 16px", marginBottom:24 }}>
          <span style={{ width:8, height:8, borderRadius:"50%", background:"#E8317A", boxShadow:"0 0 8px #E8317A" }}/>
          <span style={{ fontSize:12, fontWeight:900, color:"#E8317A", letterSpacing:1.5 }}>LAUNCHING SOON</span>
        </div>
        <h1 style={{ fontSize:"clamp(30px,6vw,52px)", fontWeight:900, lineHeight:1.1, margin:"0 0 18px", letterSpacing:"-1px" }}>
          Available <span style={{ background:"linear-gradient(90deg,#E8317A,#FBBF24,#A855F7)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>June 18th</span>
        </h1>
        <p style={{ fontSize:"clamp(15px,2.5vw,18px)", color:"rgba(255,255,255,0.6)", lineHeight:1.6, margin:"0 0 36px" }}>
          We're putting the finishing touches on the ultimate BoBA card vault. The full database, collection tracker, and marketplace open to everyone on June 18th. Catch us live until then.
        </p>
        <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap", marginBottom:28 }}>
          <a href="https://www.whatnot.com/user/bazookavault" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", display:"inline-flex", alignItems:"center", gap:8, background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff", borderRadius:30, padding:"13px 28px", fontSize:14, fontWeight:800, boxShadow:"0 4px 24px rgba(232,49,122,0.4)" }}>📺 Bazooka Vault</a>
          <a href="https://www.whatnot.com/user/bazookabreaks" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", display:"inline-flex", alignItems:"center", gap:8, background:"rgba(255,255,255,0.05)", color:"rgba(255,255,255,0.85)", border:"1.5px solid rgba(255,255,255,0.18)", borderRadius:30, padding:"13px 28px", fontSize:14, fontWeight:800 }}>📺 Bazooka Breaks</a>
        </div>
        <a href="/" style={{ fontSize:13, color:"rgba(255,255,255,0.4)", textDecoration:"none", fontWeight:600, display:"block", marginBottom:28 }}>← Back to home</a>
        <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:22 }}>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", marginBottom:12, fontWeight:600 }}>Got early access?</div>
          <button onClick={earlyAccessLogin} disabled={signingIn} style={{ display:"inline-flex", alignItems:"center", gap:10, background:"rgba(255,255,255,0.06)", color:"#fff", border:"1px solid rgba(255,255,255,0.18)", borderRadius:30, padding:"11px 24px", fontSize:14, fontWeight:800, cursor:signingIn?"wait":"pointer", fontFamily:"inherit" }}>
            <svg width="17" height="17" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
            {signingIn ? "Signing in…" : "Sign in with Google"}
          </button>
          <div style={{ marginTop:14 }}>
            {!showEmail ? (
              <button onClick={()=>setShowEmail(true)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.5)", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", textDecoration:"underline", textUnderlineOffset:3 }}>
                Don't have Gmail? Create an account with your email →
              </button>
            ) : (
              <div style={{ maxWidth:340, margin:"4px auto 0", textAlign:"left", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(232,49,122,0.25)", borderRadius:16, padding:"20px 18px" }}>
                <div style={{ display:"flex", gap:6, marginBottom:16, background:"rgba(0,0,0,0.3)", borderRadius:10, padding:4 }}>
                  {[["signup","Sign Up"],["login","Log In"]].map(([v,l])=>(
                    <button key={v} onClick={()=>{setEmailMode(v);setEmErr("");}} style={{ flex:1, background:emailMode===v?"linear-gradient(135deg,#E8317A,#7B2FF7)":"transparent", color:emailMode===v?"#fff":"rgba(255,255,255,0.5)", border:"none", borderRadius:8, padding:"9px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>{l}</button>
                  ))}
                </div>
                {emailMode==="signup" && (
                  <input value={emName} onChange={e=>setEmName(e.target.value)} placeholder="Display name" style={{ width:"100%", background:"var(--bz-bg)", color:"#fff", border:"1px solid var(--bz-line-2)", borderRadius:9, padding:"11px 13px", fontSize:14, fontFamily:"inherit", marginBottom:10, boxSizing:"border-box" }}/>
                )}
                <input value={emEmail} onChange={e=>setEmEmail(e.target.value)} type="email" placeholder="Email" style={{ width:"100%", background:"var(--bz-bg)", color:"#fff", border:"1px solid var(--bz-line-2)", borderRadius:9, padding:"11px 13px", fontSize:14, fontFamily:"inherit", marginBottom:10, boxSizing:"border-box" }}/>
                <input value={emPass} onChange={e=>setEmPass(e.target.value)} type="password" placeholder="Password (6+ characters)" onKeyDown={e=>e.key==="Enter"&&submitEmail()} style={{ width:"100%", background:"var(--bz-bg)", color:"#fff", border:"1px solid var(--bz-line-2)", borderRadius:9, padding:"11px 13px", fontSize:14, fontFamily:"inherit", marginBottom:emErr?8:14, boxSizing:"border-box" }}/>
                {emErr && <div style={{ fontSize:12, color:"#ff6b9d", marginBottom:12, lineHeight:1.4 }}>{emErr}</div>}
                {/* Confirm success. The whole reason people double-submitted was that this didn't exist. */}
                {emOk && (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, fontSize:13, fontWeight:800, color:"#4ade80", background:"rgba(74,222,128,0.1)", border:"1px solid rgba(74,222,128,0.3)", borderRadius:10, padding:"10px 14px", marginBottom:12 }}>
                    <span>{"\u2705"}</span>{emOk}
                  </div>
                )}
                <button onClick={submitEmail} disabled={emBusy} style={{ width:"100%", background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff", border:"none", borderRadius:10, padding:"13px", fontSize:14, fontWeight:900, cursor:emBusy?"wait":"pointer", fontFamily:"inherit", boxShadow:"0 4px 18px rgba(232,49,122,0.35)" }}>
                  {emOk ? "\u2713 Taking you in\u2026" : emBusy ? "Creating your account\u2026" : emailMode==="signup" ? "Create Account" : "Log In"}
                </button>
                <button onClick={()=>setShowEmail(false)} style={{ width:"100%", background:"none", border:"none", color:"rgba(255,255,255,0.35)", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", marginTop:10 }}>← Back to Google sign-in</button>
              </div>
            )}
          </div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", marginTop:12, lineHeight:1.5 }}>Early-access accounts only. Everyone else, see you June 18th!</div>
        </div>
      </div>
    </div>
  );
}

function PublicHomepage() {
  const [cardCount, setCardCount] = useState(null);
  const heroVideoRef = useRef(null);
  const [videoMuted, setVideoMuted] = useState(true);
  function toggleVideoSound() {
    const v = heroVideoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) { v.play().catch(()=>{}); } // ensure it's playing when unmuted
    setVideoMuted(v.muted);
  }
  const [liveListings, setLiveListings] = useState([]);
  useEffect(() => {
    try {
      const r = localStorage.getItem("boba_checklist_cache_v3");
      if (r) { const { cards } = JSON.parse(r); if (cards?.length) setCardCount(cards.length); }
    } catch(e) {}
    (async () => {
      try {
        const res = await fetch("/cards-data.json");
        if (res.ok) { const all = await res.json(); if (Array.isArray(all)) setCardCount(all.length); }
      } catch(e) {}
    })();
    // Recent active marketplace listings to show the marketplace is alive
    (async () => {
      try {
        const snap = await getDocs(query(collection(db,"marketplace"), where("status","==","active")));
        const items = snap.docs.map(d=>({...d.data(), id:d.id}))
          .sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||""))
          .slice(0,8);
        setLiveListings(items);
      } catch(e) { console.error("homepage listings failed:", e); }
    })();
  }, []);

  const go = (path) => { window.location.href = path; };

  const features = [
    { icon:"🃏", title:"Full Card Database", desc:"Browse every BoBA card across all sets — heroes, treatments, weapons, and power levels. Search and filter to find exactly what you're hunting." },
    { icon:"📸", title:"Scan to Collect", desc:"Snap a photo of any card and add it to your collection instantly. No manual entry — point, shoot, done." },
    { icon:"🌈", title:"Rainbow Tracking", desc:"Chase complete rainbows. See exactly which variants you own and which you're still missing, with live progress bars." },
    { icon:"⭐", title:"Super Foil & 1/1 Hunts", desc:"Track community Super Foil and Secret 1/1 hits. See what's been claimed and what's still out there waiting." },
    { icon:"💎", title:"Your Collection, Your Way", desc:"Mark cards owned, build want lists, and watch your collection value grow. Keep cards private or share with friends." },
    { icon:"🤝", title:"Trade & Connect", desc:"List cards for sale or trade, add collector friends, and see each other's collections. Built for the community." },
  ];

  return (
    <div style={{ position:"relative", minHeight:"100vh", background:"#08000a", color:"var(--bz-ink)", fontFamily:"'Trebuchet MS',sans-serif", overflowX:"hidden" }}>
      <div style={{ position:"relative", zIndex:50, background:"linear-gradient(135deg,#E8317A,#7B2FF7)", padding:"11px 18px", textAlign:"center", display:"flex", alignItems:"center", justifyContent:"center", gap:10, flexWrap:"wrap" }}>
        <span style={{ fontSize:14, fontWeight:700, color:"#fff" }}>🚧 We're building the best collector's tool BoBA has ever seen.</span>
        <a href="https://bazookavault.com" style={{ fontSize:13, fontWeight:800, color:"#fff", background:"rgba(0,0,0,0.25)", border:"1px solid rgba(255,255,255,0.4)", borderRadius:20, padding:"5px 14px", textDecoration:"none", whiteSpace:"nowrap" }}>Visit the current collector's database → bazookavault.com</a>
      </div>
      <style>{`
        @keyframes bzRainbowSlide { 0%{background-position:0% 50%} 100%{background-position:200% 50%} }
        .bz-rainbow-fill {
          background: linear-gradient(90deg,#EF4444,#F97316,#FBBF24,#4ade80,#22D3EE,#60A5FA,#A855F7,#F472B6,#EF4444);
          background-size: 200% 100%;
          animation: bzRainbowSlide 3s linear infinite;
        }
        @keyframes homeFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes homeGlow { 0%,100%{opacity:0.5} 50%{opacity:0.9} }
        @keyframes homeFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes homeGrad { 0%{background-position:0% 50%} 100%{background-position:200% 50%} }
        .home-feat:hover { transform:translateY(-4px); border-color:rgba(232,49,122,0.5)!important; }
        .home-cta:hover { transform:translateY(-2px); box-shadow:0 8px 30px rgba(232,49,122,0.5)!important; }
        .home-cta2:hover { border-color:#E8317A!important; color:#fff!important; }
      `}</style>

      {/* Ambient glow background */}
      <div style={{ position:"fixed", inset:0, pointerEvents:"none", background:"radial-gradient(ellipse 80% 50% at 50% 0%, rgba(232,49,122,0.18), transparent 70%), radial-gradient(ellipse 60% 50% at 80% 60%, rgba(123,47,247,0.12), transparent 70%)" }}/>

      {/* Hero background video */}
      <div style={{ position:"absolute", top:0, left:0, right:0, height:"min(820px,100vh)", overflow:"hidden", zIndex:0 }}>
        <video ref={heroVideoRef} autoPlay loop muted playsInline poster="/bazooka-hero-poster.jpg"
          style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", minWidth:"100%", minHeight:"100%", width:"auto", height:"auto", objectFit:"cover", opacity:0.55, pointerEvents:"none" }}>
          <source src="/bazooka-hero.mp4" type="video/mp4" />
        </video>
        {/* Medium overlay so hero text pops */}
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(8,0,10,0.55) 0%, rgba(8,0,10,0.45) 40%, rgba(8,0,10,0.85) 85%, #08000a 100%)", pointerEvents:"none" }}/>
      </div>

      {/* Tap-to-unmute control — top-level so it sits above the hero */}
      <button onClick={toggleVideoSound} title={videoMuted?"Tap for sound":"Mute"}
        style={{ position:"absolute", top:80, right:24, zIndex:50, display:"flex", alignItems:"center", gap:8, background:"rgba(8,0,10,0.65)", border:"1px solid rgba(232,49,122,0.5)", color:"#fff", borderRadius:30, padding:videoMuted?"9px 18px":"9px", height:40, cursor:"pointer", fontFamily:"inherit", fontSize:13, fontWeight:800, backdropFilter:"blur(10px)", boxShadow:"0 4px 20px rgba(232,49,122,0.25)", transition:"all 0.2s" }}
        onMouseEnter={e=>{e.currentTarget.style.borderColor="#E8317A";e.currentTarget.style.transform="translateY(-1px)";}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(232,49,122,0.5)";e.currentTarget.style.transform="";}}>
        {videoMuted ? <>🔊 <span>Tap for sound</span></> : <>🔇 <span>Mute</span></>}
      </button>

      {/* Nav bar */}
      <div style={{ position:"relative", zIndex:2, maxWidth:1200, margin:"0 auto", padding:"20px 24px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <img src="/Bazooka_Logo_cropped.png" alt="Bazooka" style={{ height:42, width:"auto", filter:"drop-shadow(0 2px 10px rgba(232,49,122,0.4))" }}/>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <button onClick={()=>go("/cards")} className="home-cta2" style={{ background:"transparent", color:"rgba(255,255,255,0.7)", border:"1.5px solid rgba(255,255,255,0.15)", borderRadius:24, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", transition:"all 0.2s" }}>Browse Cards</button>
        </div>
      </div>

      {/* HERO */}
      <div style={{ position:"relative", zIndex:2, maxWidth:1000, margin:"0 auto", padding:"60px 24px 40px", textAlign:"center" }}>
        <div style={{ animation:"homeFadeUp 0.7s ease both" }}>
          <img src="/Bazooka_Logo_cropped.png" alt="Bazooka" style={{ height:"clamp(90px,16vw,150px)", width:"auto", margin:"0 auto 28px", display:"block", animation:"homeFloat 5s ease-in-out infinite", filter:"drop-shadow(0 8px 30px rgba(232,49,122,0.45))" }}/>
        </div>
        <h1 style={{ fontSize:"clamp(34px,6vw,68px)", fontWeight:900, lineHeight:1.05, margin:"0 0 20px", letterSpacing:"-1px", animation:"homeFadeUp 0.7s ease 0.1s both" }}>
          The home for every<br/>
          <span style={{ background:"linear-gradient(90deg,#E8317A,#FBBF24,#A855F7,#E8317A)", backgroundSize:"200% auto", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", animation:"homeGrad 4s linear infinite" }}>BoBA card you collect</span>
        </h1>
        <p style={{ fontSize:"clamp(15px,2.5vw,20px)", color:"rgba(255,255,255,0.6)", maxWidth:620, margin:"0 auto 36px", lineHeight:1.6, animation:"homeFadeUp 0.7s ease 0.2s both" }}>
          Browse the complete Bo Jackson Battle Arena database, track your collection, chase rainbows, and scan cards straight into your vault — all in one place.
        </p>
        <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap", animation:"homeFadeUp 0.7s ease 0.3s both" }}>
          <button onClick={()=>go("/cards")} className="home-cta" style={{ background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff", border:"none", borderRadius:30, padding:"16px 38px", fontSize:16, fontWeight:800, cursor:"pointer", fontFamily:"inherit", boxShadow:"0 4px 24px rgba(232,49,122,0.4)", transition:"all 0.2s" }}>🃏 Browse the Database</button>
          <button onClick={()=>go("/cards")} className="home-cta2" style={{ background:"rgba(255,255,255,0.04)", color:"rgba(255,255,255,0.8)", border:"1.5px solid rgba(255,255,255,0.18)", borderRadius:30, padding:"16px 38px", fontSize:16, fontWeight:800, cursor:"pointer", fontFamily:"inherit", transition:"all 0.2s" }}>Start Your Collection</button>
        </div>
        {cardCount && (
          <div style={{ marginTop:32, fontSize:13, color:"rgba(255,255,255,0.35)", letterSpacing:1, animation:"homeFadeUp 0.7s ease 0.4s both" }}>
            <span style={{ color:"#E8317A", fontWeight:900, fontSize:18 }}>{cardCount.toLocaleString()}</span> cards catalogued and counting
          </div>
        )}
      </div>

      {/* WHATNOT LIVE BANNER */}
      <div style={{ position:"relative", zIndex:2, maxWidth:1000, margin:"0 auto", padding:"0 24px" }}>
        <div style={{ background:"linear-gradient(135deg, rgba(232,49,122,0.14), rgba(123,47,247,0.12))", border:"1px solid rgba(232,49,122,0.3)", borderRadius:22, padding:"30px 28px", textAlign:"center", animation:"homeFadeUp 0.7s ease 0.5s both", position:"relative", overflow:"hidden" }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(239,68,68,0.15)", border:"1px solid rgba(239,68,68,0.4)", borderRadius:30, padding:"5px 14px", marginBottom:16 }}>
            <span style={{ width:8, height:8, borderRadius:"50%", background:"#EF4444", boxShadow:"0 0 8px #EF4444", animation:"homeGlow 1.4s ease-in-out infinite" }}/>
            <span style={{ fontSize:11, fontWeight:900, color:"#EF4444", letterSpacing:1.5 }}>LIVE ON WHATNOT</span>
          </div>
          <div style={{ fontSize:"clamp(22px,3.5vw,30px)", fontWeight:900, color:"#fff", marginBottom:10 }}>Catch us live, rip with the crew</div>
          <div style={{ fontSize:"clamp(14px,2vw,16px)", color:"rgba(255,255,255,0.6)", maxWidth:560, margin:"0 auto 24px", lineHeight:1.6 }}>
            Live breaks, giveaways, and the best energy in the hobby. Join the Bazooka community on Whatnot and be part of the action.
          </div>
          <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap" }}>
            <a href="https://www.whatnot.com/user/bazookavault" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", display:"inline-flex", alignItems:"center", gap:10, background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff", borderRadius:30, padding:"14px 30px", fontSize:15, fontWeight:800, boxShadow:"0 4px 24px rgba(232,49,122,0.4)", transition:"transform 0.2s" }}
              onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform=""}>
              📺 Bazooka Vault
            </a>
            <a href="https://www.whatnot.com/user/bazookabreaks" target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", display:"inline-flex", alignItems:"center", gap:10, background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff", borderRadius:30, padding:"14px 30px", fontSize:15, fontWeight:800, boxShadow:"0 4px 24px rgba(232,49,122,0.4)", transition:"transform 0.2s" }}
              onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"} onMouseLeave={e=>e.currentTarget.style.transform=""}>
              📺 Bazooka Breaks
            </a>
          </div>
        </div>
      </div>

      {/* FEATURES */}
      <div style={{ position:"relative", zIndex:2, maxWidth:1100, margin:"0 auto", padding:"40px 24px 60px" }}>
        {liveListings.length>0 && (
          <div style={{ marginBottom:48 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, flexWrap:"wrap", gap:8 }}>
              <div>
                <div style={{ display:"inline-flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  <span style={{ width:8, height:8, borderRadius:"50%", background:"#4ade80", boxShadow:"0 0 8px #4ade80", animation:"homeGlow 1.6s ease-in-out infinite" }}/>
                  <span style={{ fontSize:11, fontWeight:900, color:"#4ade80", letterSpacing:1.5 }}>LIVE ON THE MARKETPLACE</span>
                </div>
                <div style={{ fontSize:"clamp(20px,3vw,26px)", fontWeight:900, color:"#fff" }}>Cards up for grabs right now</div>
              </div>
              <button onClick={()=>go("/market")} style={{ background:"rgba(74,222,128,0.12)", border:"1px solid rgba(74,222,128,0.35)", color:"#4ade80", borderRadius:24, padding:"9px 20px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>Browse all →</button>
            </div>
            <div style={{ display:"flex", gap:14, overflowX:"auto", paddingBottom:8, scrollbarWidth:"none" }} className="nav-bar">
              {liveListings.map(l=>(
                <div key={l.id} onClick={()=>go("/market")} style={{ flex:"0 0 auto", width:160, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:14, overflow:"hidden", cursor:"pointer", transition:"transform 0.2s, border-color 0.2s" }}
                  onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-4px)";e.currentTarget.style.borderColor="rgba(74,222,128,0.4)";}}
                  onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.borderColor="rgba(255,255,255,0.08)";}}>
                  <div style={{ width:"100%", aspectRatio:"3/4", background:"var(--bz-bg)", overflow:"hidden" }}>
                    {(l.sellerPhotos&&l.sellerPhotos[0])||l.cardImage
                      ? <img src={(l.sellerPhotos&&l.sellerPhotos[0])||l.cardImage} alt={l.cardName} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                      : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:32 }}>🃏</div>}
                  </div>
                  <div style={{ padding:"10px 12px" }}>
                    <div style={{ fontSize:12, fontWeight:800, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{l.cardName}</div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", marginTop:1 }}>{l.cardTreatment||l.setName||""}</div>
                    <div style={{ fontSize:14, fontWeight:900, color:"#4ade80", marginTop:5 }}>{l.isOBO||!l.askingPrice ? "Best Offer" : `$${(l.askingPrice||0).toFixed(2)}`}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:18 }}>
          {features.map((f,i)=>(
            <div key={i} className="home-feat" style={{ background:"rgba(255,255,255,0.03)", border:"1.5px solid rgba(255,255,255,0.07)", borderRadius:18, padding:"26px 24px", backdropFilter:"blur(10px)", transition:"all 0.25s", cursor:"default" }}>
              <div style={{ fontSize:34, marginBottom:14 }}>{f.icon}</div>
              <div style={{ fontSize:18, fontWeight:800, marginBottom:8, color:"#fff" }}>{f.title}</div>
              <div style={{ fontSize:14, color:"rgba(255,255,255,0.5)", lineHeight:1.6 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA BAND */}
      <div style={{ position:"relative", zIndex:2, maxWidth:900, margin:"0 auto 60px", padding:"0 24px" }}>
        <div style={{ background:"linear-gradient(135deg, rgba(232,49,122,0.15), rgba(123,47,247,0.12))", border:"1.5px solid rgba(232,49,122,0.25)", borderRadius:24, padding:"48px 32px", textAlign:"center", backdropFilter:"blur(10px)" }}>
          <div style={{ fontSize:"clamp(24px,4vw,38px)", fontWeight:900, marginBottom:14 }}>Ready to track your vault?</div>
          <div style={{ fontSize:16, color:"rgba(255,255,255,0.55)", marginBottom:28, maxWidth:480, margin:"0 auto 28px", lineHeight:1.6 }}>Jump in, sign in with Google, and start marking the cards you own. It's free.</div>
          <button onClick={()=>go("/cards")} className="home-cta" style={{ background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff", border:"none", borderRadius:30, padding:"16px 42px", fontSize:16, fontWeight:800, cursor:"pointer", fontFamily:"inherit", boxShadow:"0 4px 24px rgba(232,49,122,0.4)", transition:"all 0.2s" }}>Enter the Vault →</button>
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ position:"relative", zIndex:2, borderTop:"1px solid rgba(255,255,255,0.06)", padding:"32px 24px", textAlign:"center" }}>
        <img src="/Bazooka_Logo_cropped.png" alt="Bazooka" style={{ height:30, width:"auto", opacity:0.5, marginBottom:14 }}/>
        <div style={{ display:"flex", gap:20, justifyContent:"center", flexWrap:"wrap", marginBottom:16 }}>
          <a href="/cards" style={{ color:"rgba(255,255,255,0.5)", fontSize:13, textDecoration:"none" }}>Card Database</a>
          <a href="/cards#wants" style={{ color:"rgba(255,255,255,0.5)", fontSize:13, textDecoration:"none" }}>Want List</a>
          <a href="/sell" style={{ color:"rgba(255,255,255,0.5)", fontSize:13, textDecoration:"none" }}>Sell a Card</a>
        </div>
        <div style={{ fontSize:12, color:"rgba(255,255,255,0.25)" }}>© {new Date().getFullYear()} Bazooka Breaks · Bo Jackson Battle Arena Collector Database</div>
      </div>
    </div>
  );
}

// ── PUBLIC BOBA CARD ── Dedicated card component for /cards page ──────────────
const LOT_METHODS = [
  { v:"purchased", l:"💵 Purchased" },
  { v:"break",     l:"📦 Hit in a Break" },
  { v:"pull",      l:"🎴 Pack Pull" },
  { v:"trade",     l:"🔄 Trade" },
  { v:"gift",      l:"🎁 Gift" },
  { v:"other",     l:"❓ Other" },
];

// Loan-by-photo confirm step. Given a scanned card (or nothing, for manual entry),
// pick lend vs borrow and type who. Built for speed at a table: two taps + a name.
// entry = { card, mode } where mode is the pre-chosen "lent"/"borrowed", or null.
function LoanScanConfirm({ entry, cards, friends=[], inp, onCancel, onConfirm }) {
  const [card, setCard] = React.useState(entry?.card || null);
  const [mode, setMode] = React.useState(entry?.mode || "lent");
  const [who, setWho]   = React.useState("");
  const [friendUid, setFriendUid] = React.useState("");   // picked app-friend, if any
  const [returnBy, setReturnBy] = React.useState("");     // optional return-by date
  const [q, setQ]       = React.useState("");
  // Manual search when the scan missed (or to correct a wrong match).
  const results = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return (cards||[]).filter(c =>
      (c.hero||"").toLowerCase().includes(s) ||
      String(c.cardNum||"").toLowerCase().includes(s) ||
      (c.setName||"").toLowerCase().includes(s)
    ).slice(0, 8);
  }, [q, cards]);
  const friend = (friends||[]).find(f => f.friendUid === friendUid) || null;
  // The name to record: a picked friend's name wins, else the free-text field.
  const effectiveWho = friend ? (friend.friendName || friend.friendUid) : who.trim();

  return (
    <div onClick={onCancel} style={{position:"fixed",inset:0,zIndex:14000,background:"rgba(0,0,0,0.8)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s1,#14141c)",border:"1px solid var(--bz-line-2)",borderRadius:16,width:"100%",maxWidth:420,padding:18}}>
        <div style={{fontSize:15,fontWeight:900,color:"var(--bz-ink)",marginBottom:12}}>
          {card ? "Confirm loan" : "Which card?"}
        </div>

        {card ? (
          <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:14,padding:"10px",background:"rgba(255,255,255,0.03)",borderRadius:10}}>
            <div style={{width:44,height:60,borderRadius:6,overflow:"hidden",flexShrink:0,background:"rgba(255,255,255,0.05)"}}>
              {card.imageUrl ? <img src={card.imageUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : null}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)"}}>{card.hero||card.playName}</div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>{[card.treatment,card.weapon,card.setName].filter(Boolean).join(" · ")}</div>
              <button onClick={()=>{ setCard(null); setQ(""); }} style={{marginTop:4,background:"none",border:"none",color:"#60A5FA",fontSize:11,fontWeight:700,cursor:"pointer",padding:0,fontFamily:"inherit"}}>Wrong card? Search</button>
            </div>
          </div>
        ) : (
          <div style={{marginBottom:14}}>
            <input autoFocus value={q} onChange={e=>setQ(e.target.value)} placeholder="Type hero, number, or set…"
              style={{...inp,width:"100%",marginBottom:8}} />
            {results.map(c=>(
              <div key={c.id} onClick={()=>{ setCard(c); setQ(""); }}
                style={{display:"flex",gap:8,alignItems:"center",padding:"7px 8px",borderRadius:8,cursor:"pointer",background:"rgba(255,255,255,0.02)",marginBottom:4}}>
                <div style={{width:26,height:36,borderRadius:4,overflow:"hidden",flexShrink:0,background:"rgba(255,255,255,0.05)"}}>
                  {c.imageUrl ? <img src={c.imageUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : null}
                </div>
                <div style={{fontSize:12.5,fontWeight:700,color:"var(--bz-ink)"}}>{c.hero||c.playName}
                  <span style={{color:"var(--bz-ink-3)",fontWeight:600}}> {c.cardNum?`#${c.cardNum}`:""}</span></div>
              </div>
            ))}
          </div>
        )}

        {/* Lend vs borrow */}
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          {[["lent","📤 Lending out"],["borrowed","📥 Borrowing"]].map(([v,l])=>(
            <button key={v} onClick={()=>setMode(v)}
              style={{flex:1,background:mode===v?(v==="lent"?"rgba(251,191,36,0.18)":"rgba(123,156,255,0.18)"):"transparent",
                      border:`1.5px solid ${mode===v?(v==="lent"?"#FBBF24":"#7B9CFF"):"var(--bz-line-2)"}`,
                      color:mode===v?(v==="lent"?"#FBBF24":"#7B9CFF"):"var(--bz-ink-3)",
                      borderRadius:9,padding:"9px 4px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
          ))}
        </div>

        {/* Who — pick an app friend (so the loan can actually be sent and they can
            accept), or type any name for a non-app person. Picking a friend clears
            the free-text and vice-versa. */}
        {(friends||[]).length > 0 && (
          <select value={friendUid} onChange={e=>{ setFriendUid(e.target.value); if(e.target.value) setWho(""); }}
            style={{...inp,width:"100%",marginBottom:8}}>
            <option value="">{mode==="lent"?"Pick a friend to send to…":"Pick a friend…"}</option>
            {(friends||[]).map(f=>(
              <option key={f.friendUid} value={f.friendUid}>{f.friendName||f.friendUid}</option>
            ))}
          </select>
        )}
        <input value={who} onChange={e=>{ setWho(e.target.value); if(e.target.value) setFriendUid(""); }}
          disabled={!!friendUid}
          placeholder={friendUid ? "" : (friends||[]).length>0 ? "…or type a name (not on the app)" : (mode==="lent"?"Who has it?":"Who lent it to you?")}
          style={{...inp,width:"100%",marginBottom:14,opacity: friendUid?0.5:1}} />

        {mode==="lent" && friend && (
          <div style={{fontSize:10.5,color:"#60A5FA",marginTop:-8,marginBottom:12}}>
            This will send {card?.hero||"the card"} to {friend.friendName} — they accept on their end.
          </div>
        )}

        {/* Optional return-by date — drives the reminder + calendar. */}
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
          <span style={{fontSize:11,color:"var(--bz-ink-3)",whiteSpace:"nowrap"}}>{mode==="lent"?"Want it back by":"Return by"}</span>
          <input type="date" value={returnBy} onChange={e=>setReturnBy(e.target.value)}
            style={{...inp,flex:1,colorScheme:"dark"}} />
          {returnBy && <button onClick={()=>setReturnBy("")} style={{background:"none",border:"none",color:"var(--bz-ink-3)",fontSize:16,cursor:"pointer",fontFamily:"inherit",padding:0}}>×</button>}
        </div>

        <div style={{display:"flex",gap:8}}>
          <button onClick={onCancel} style={{flex:1,background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          <button disabled={!card || !effectiveWho}
            onClick={()=>onConfirm(card, mode, effectiveWho, friend, returnBy)}
            style={{flex:2,background:(!card||!effectiveWho)?"#333":"linear-gradient(135deg,#E8317A,#7B2FF7)",border:"none",color:"#fff",borderRadius:10,padding:"11px",fontSize:13,fontWeight:900,cursor:(!card||!effectiveWho)?"default":"pointer",fontFamily:"inherit"}}>
            {mode==="lent" ? (friend ? "Lend & send" : "Mark lent") : "Mark borrowed"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Bulk loan modal — same friend-picker + return-date flow as the single-card loan,
// but applied to a batch. mode is "lent" or "borrowed"; count is how many cards.
function BulkLoanModal({ mode, count, friends=[], inp, onCancel, onConfirm }) {
  const [who, setWho]   = React.useState("");
  const [friendUid, setFriendUid] = React.useState("");
  const [returnBy, setReturnBy]   = React.useState("");
  const friend = (friends||[]).find(f => f.friendUid === friendUid) || null;
  const effectiveWho = friend ? (friend.friendName || friend.friendUid) : who.trim();
  const lent = mode === "lent";
  return (
    <div onClick={onCancel} style={{position:"fixed",inset:0,zIndex:14000,background:"rgba(0,0,0,0.8)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s1,#14141c)",border:"1px solid var(--bz-line-2)",borderRadius:16,width:"100%",maxWidth:420,padding:18}}>
        <div style={{fontSize:15,fontWeight:900,color:"var(--bz-ink)",marginBottom:4}}>
          {lent ? "Loan out" : "Borrow"} {count} card{count===1?"":"s"}
        </div>
        <div style={{fontSize:11.5,color:"var(--bz-ink-3)",marginBottom:14}}>
          {lent ? "Same friend + return date applies to all selected." : "All marked borrowed from the same person."}
        </div>
        {(friends||[]).length > 0 && (
          <select value={friendUid} onChange={e=>{ setFriendUid(e.target.value); if(e.target.value) setWho(""); }}
            style={{...inp,width:"100%",marginBottom:8}}>
            <option value="">{lent?"Pick a friend to send to…":"Pick a friend…"}</option>
            {(friends||[]).map(f=>(<option key={f.friendUid} value={f.friendUid}>{f.friendName||f.friendUid}</option>))}
          </select>
        )}
        <input value={who} onChange={e=>{ setWho(e.target.value); if(e.target.value) setFriendUid(""); }}
          disabled={!!friendUid}
          placeholder={friendUid ? "" : (friends||[]).length>0 ? "…or type a name (not on the app)" : (lent?"Who gets them?":"Who lent them?")}
          style={{...inp,width:"100%",marginBottom:14,opacity: friendUid?0.5:1}} />
        {lent && friend && (
          <div style={{fontSize:10.5,color:"#60A5FA",marginTop:-8,marginBottom:12}}>
            Sends all {count} to {friend.friendName} — they accept on their end.
          </div>
        )}
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
          <span style={{fontSize:11,color:"var(--bz-ink-3)",whiteSpace:"nowrap"}}>{lent?"Want them back by":"Return by"}</span>
          <input type="date" value={returnBy} onChange={e=>setReturnBy(e.target.value)} style={{...inp,flex:1,colorScheme:"dark"}} />
          {returnBy && <button onClick={()=>setReturnBy("")} style={{background:"none",border:"none",color:"var(--bz-ink-3)",fontSize:16,cursor:"pointer",fontFamily:"inherit",padding:0}}>×</button>}
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={onCancel} style={{flex:1,background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
          <button disabled={!effectiveWho}
            onClick={()=>onConfirm(effectiveWho, friend, returnBy)}
            style={{flex:2,background:!effectiveWho?"#333":"linear-gradient(135deg,#E8317A,#7B2FF7)",border:"none",color:"#fff",borderRadius:10,padding:"11px",fontSize:13,fontWeight:900,cursor:!effectiveWho?"default":"pointer",fontFamily:"inherit"}}>
            {lent ? (friend ? "Lend & send all" : "Mark lent") : "Mark borrowed"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LotModal({ card, lots, onAdd, onUpdate, onRemove, onClose, inp, onUploadPhoto }) {
  // Everything here is a fact about YOUR PARTICULAR COPY rather than about the card, which is why it
  // lives on the lot: purchase date, condition, grading, where the card physically is, whether it's
  // locked from trade, and front/back photos.
  // cost = CASH actually paid. tradeValue = what the cards you gave up were worth to you. Real
  // acquisition cost is the two together — recording only cash understates what a card cost and
  // quietly inflates every ROI number built on top of it.
  // Lending lives on the COPY, not the card. Own three Ajax Gums and lend one, and only that one
  // becomes unavailable \u2014 the other two still build decks normally.
  //   lendState: "" (in hand) | "lent" (mine, with someone else) | "borrowed" (theirs, with me)
  const blank = { cost:"", tradeValue:"", value:"", version:"", method:"purchased", date:todayLocal(), notes:"", serial:"",
                  purchaseDate:"", condition:"", grade:"", gradeCompany:"", certNum:"",
                  location:"", locked:false,
                  lendState:"", lendWho:"", lendWhy:"", lendDate:"" };
  const [draft, setDraft] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [busySide, setBusySide] = useState(null);   // "front" | "back" while uploading
  // Optional sections start closed. They open automatically when editing a copy that already has
  // those details, so nothing you saved is hidden from you.
  const [showGrading, setShowGrading] = useState(false);
  const [showWhere,   setShowWhere]   = useState(false);
  const CONDITIONS = ["", "Mint", "Near Mint", "Excellent", "Very Good", "Good", "Played", "Damaged"];
  const GRADERS    = ["", "PSA", "BGS", "SGC", "CGC", "TAG", "Other"];

  function commit() {
    const data = {
      cost: draft.cost==="" ? null : parseFloat(draft.cost),
      value: draft.value==="" ? null : parseFloat(draft.value),
      method: draft.method, date: draft.date, notes: draft.notes.trim(),
      serial: (draft.serial||"").trim(),   // e.g. "21/50" — which numbered copy this is
      tradeValue: draft.tradeValue===""||draft.tradeValue==null ? null : (parseFloat(draft.tradeValue)||0),
      version: (draft.version||"").trim() || null,
      lendState: draft.lendState || "",
      lendWho:  (draft.lendWho||"").trim(),
      lendWhy:  (draft.lendWhy||"").trim(),
      lendDate:  draft.lendDate || "",
      purchaseDate: draft.purchaseDate||"",
      condition: draft.condition||"",
      grade: (draft.grade||"").trim(),
      gradeCompany: draft.gradeCompany||"",
      certNum: (draft.certNum||"").trim(),
      location: (draft.location||"").trim(),
      locked: !!draft.locked,
    };
    if (editId) { onUpdate(editId, data); } else { onAdd(card.id, data); }
    setDraft(blank); setEditId(null);
  }
  function startEdit(l) { setEditId(l.id); setDraft({ cost:l.cost??"", tradeValue:l.tradeValue??"", value:l.value??"", version:l.version||"", method:l.method||"purchased", date:l.date||blank.date, notes:l.notes||"", serial:l.serial||"",
      purchaseDate:l.purchaseDate||"", condition:l.condition||"", grade:l.grade||"", gradeCompany:l.gradeCompany||"",
      certNum:l.certNum||"", location:l.location||"", locked:!!l.locked , lendState:l.lendState||"", lendWho:l.lendWho||"", lendWhy:l.lendWhy||"", lendDate:l.lendDate||""});
    // Reveal whatever this copy already has, so editing never hides saved data behind a collapse.
    setShowGrading(!!(l.gradeCompany || l.grade || l.certNum || l.condition));
    setShowWhere(!!l.location); }

  // Total cost must include trade value or the summary contradicts the per-copy total above it.
  const totalCash  = lots.reduce((s,l)=>s+(Number(l.cost)||0),0);
  const totalTrade = lots.reduce((s,l)=>s+(Number(l.tradeValue)||0),0);
  const totalCost  = totalCash + totalTrade;
  const totalVal  = lots.reduce((s,l)=>s+(l.value||0),0);

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:13500, background:"rgba(0,0,0,0.75)", display:"flex", alignItems:"center", justifyContent:"center", padding:16, backdropFilter:"blur(4px)" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:16, width:"min(460px,100%)", maxHeight:"86vh", overflowY:"auto", padding:"20px 22px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
          <div>
            <div style={{ fontSize:11, color:"#999", fontWeight:700, letterSpacing:1, textTransform:"uppercase" }}>Collection Details</div>
            <div style={{ fontSize:18, fontWeight:900, color:"#fff" }}>{card.hero}</div>
            <div style={{ fontSize:11, color:"#c9bdc4" }}>#{card.cardNum}{card.treatment?` · ${card.treatment}`:""}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--bz-ink-2)", fontSize:22, cursor:"pointer", lineHeight:1 }}>×</button>
        </div>

        {lots.length>0 && (
          <div style={{ display:"flex", gap:10, margin:"12px 0 4px" }}>
            <div style={{ flex:1, background:"rgba(232,49,122,0.08)", border:"1px solid rgba(232,49,122,0.2)", borderRadius:8, padding:"8px 10px" }}>
              <div style={{ fontSize:9, color:"var(--bz-ink-2)", fontWeight:700, textTransform:"uppercase" }}>Total Cost</div>
              <div style={{ fontSize:18, fontWeight:900, color:"#E8317A" }}>${totalCost.toLocaleString(undefined,{maximumFractionDigits:2})}</div>
              {totalTrade > 0 && <div style={{ fontSize:9, color:"#888", marginTop:2 }}>${totalCash.toLocaleString(undefined,{maximumFractionDigits:0})} cash + ${totalTrade.toLocaleString(undefined,{maximumFractionDigits:0})} trade</div>}
            </div>
            <div style={{ flex:1, background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.2)", borderRadius:8, padding:"8px 10px" }}>
              <div style={{ fontSize:9, color:"var(--bz-ink-2)", fontWeight:700, textTransform:"uppercase" }}>Total Value</div>
              <div style={{ fontSize:18, fontWeight:900, color:"#4ade80" }}>${totalVal.toLocaleString(undefined,{maximumFractionDigits:2})}</div>
            </div>
          </div>
        )}

        {/* Existing copies */}
        {lots.length>0 && (
          <div style={{ margin:"12px 0" }}>
            <div style={{ fontSize:11, color:"#999", fontWeight:700, marginBottom:6 }}>Your copies ({lots.length})</div>
            {lots.map((l,i)=>(
              <div key={l.id} style={{ display:"flex", alignItems:"center", gap:8, background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:8, padding:"8px 10px", marginBottom:6 }}>
                {/* Front / back thumbnails. photoUrl is the original single-photo field (the front);
                    photoBackUrl was added later so existing scans keep working untouched. */}
                {(l.photoUrl || l.photoBackUrl) && (
                  <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                  {[["front", l.photoUrl], ["back", l.photoBackUrl]].filter(x=>x[1]).map(([side,url])=>(
                    <div key={side} style={{ position:"relative" }}>
                      <img src={url} alt={side} title={side} style={{ width:38, height:51, objectFit:"cover", borderRadius:6, border:"1px solid var(--bz-line-2)" }}/>
                      <div style={{ position:"absolute", bottom:-1, left:0, right:0, textAlign:"center", fontSize:7, fontWeight:800, color:"#fff", background:"rgba(0,0,0,0.6)", borderRadius:"0 0 5px 5px" }}>{side}</div>
                      <button onClick={()=>{ if(window.confirm(`Delete the ${side} photo?`)) onUpdate(l.id, { ...l, [side==="front"?"photoUrl":"photoBackUrl"]:"" }); }} title={`Delete ${side} photo`} style={{ position:"absolute", top:-6, right:-6, background:"var(--bz-s2)", border:"1px solid #5a2a2a", color:"#E8317A", borderRadius:"50%", width:18, height:18, fontSize:11, cursor:"pointer", lineHeight:1, padding:0, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
                    </div>
                  ))}
                  </div>
                )}
                
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, color:"#ddd", fontWeight:700 }}>
                    {LOT_METHODS.find(m=>m.v===l.method)?.l || l.method}
                    {l.cost!=null && <span style={{ color:"#E8317A" }}> · ${l.cost}</span>}
                    {l.value!=null && <span style={{ color:"#4ade80" }}> → ${l.value}</span>}
                  </div>
                  <div style={{ fontSize:10, color:"#b0b0b0" }}>{l.date}{l.notes?` · ${l.notes}`:""}{l.photoUrl?" · 📸":""}</div>
                  {/* Add photos after the fact — quick-added and imported copies have no scan. */}
                  <div style={{ display:"flex", gap:6, marginTop:4, flexWrap:"wrap" }}>
                    {["front","back"].map(side => {
                      const key = side==="front" ? "photoUrl" : "photoBackUrl";
                      const busy = busySide === l.id+side;
                      return (
                        <label key={side} style={{ fontSize:9.5, fontWeight:700, color: busy?"rgba(255,255,255,0.35)":"#7B9CFF", border:"1px solid rgba(123,156,255,0.35)", borderRadius:6, padding:"2px 7px", cursor: busy?"default":"pointer" }}>
                          {busy ? "uploading…" : (l[key] ? `replace ${side}` : `+ ${side} photo`)}
                          <input type="file" accept="image/*" style={{display:"none"}} disabled={busy}
                            onChange={async e => {
                              const f = e.target.files && e.target.files[0]; e.target.value = "";
                              if (!f || !onUploadPhoto) return;
                              setBusySide(l.id+side);
                              const url = await onUploadPhoto(l.cardId, f, side);
                              setBusySide(null);
                              if (url) onUpdate(l.id, { ...l, [key]: url });
                            }}/>
                        </label>
                      );
                    })}
                  </div>
                  {l.serial && <div style={{ fontSize:11, fontWeight:800, color:"#FBBF24", marginTop:2 }}>#{l.serial}</div>}
                </div>
                <button onClick={()=>startEdit(l)} style={{ background:"none", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:5, padding:"2px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>Edit</button>
                <button onClick={()=>onRemove(l.id)} style={{ background:"none", border:"1px solid #5a2a2a", color:"#E8317A", borderRadius:5, padding:"2px 8px", fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>×</button>
              </div>
            ))}
          </div>
        )}

        {/* Add / edit form */}
        <div style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"12px 14px", marginTop:8 }}>
          <div style={{ fontSize:12, fontWeight:800, color:"#fff", marginBottom:10 }}>{editId?"Edit copy":"+ Add a copy"}</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:8 }}>
            {LOT_METHODS.map(m=>(
              <button key={m.v} onClick={()=>setDraft({...draft,method:m.v})} style={{ background:draft.method===m.v?"rgba(232,49,122,0.18)":"transparent", color:draft.method===m.v?"#E8317A":"#888", border:`1px solid ${draft.method===m.v?"#E8317A":"#333"}`, borderRadius:20, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{m.l}</button>
            ))}
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:8 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>Cash paid ($)</div>
              <input type="number" inputMode="decimal" value={draft.cost} onChange={e=>setDraft({...draft,cost:e.target.value})} placeholder={draft.method==="purchased"||draft.method==="break"||draft.method==="pull"?"0.00":"—"} style={{...inp,width:"100%"}}/>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>Est. value ($)</div>
              <input type="number" inputMode="decimal" value={draft.value} onChange={e=>setDraft({...draft,value:e.target.value})} placeholder="0.00" style={{...inp,width:"100%"}}/>
            </div>
          {/* Cards given in trade are not free — you paid for them once already. Recording only the
              cash makes a $1,600-plus-three-cards deal look like a $1,600 card, which throws off every
              average built on it. This is your own estimate; nothing here tries to derive it, because
              in a real negotiation the credited value is never actually stated. */}
          <div style={{ display:"flex", gap:8, marginBottom:8, alignItems:"flex-end" }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>Trade value ($)</div>
              <input type="number" inputMode="decimal" value={draft.tradeValue}
                onChange={e=>setDraft({...draft,tradeValue:e.target.value})}
                placeholder="cards you gave" style={{...inp,width:"100%"}}/>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>Total cost</div>
              <div style={{ ...inp, width:"100%", display:"flex", alignItems:"center", background:"rgba(232,49,122,0.08)", borderColor:"rgba(232,49,122,0.3)", color:"#E8317A", fontWeight:800 }}>
                {(() => {
                  const cash = parseFloat(draft.cost)||0, tr = parseFloat(draft.tradeValue)||0;
                  const t = cash + tr;
                  return t > 0 ? `$${t.toLocaleString(undefined,{maximumFractionDigits:2})}` : "\u2014";
                })()}
              </div>
            </div>
          </div>
          </div>
          {/* Everything below the money row is optional detail. It was thirteen fields in one flat
              stack, which made the common case (log what I paid) feel like paperwork. Essentials stay
              visible; grading, storage and provenance collapse until wanted. */}
          {(() => {
            // Only for cards printed more than once \u2014 everyone else never sees this field.
            const vspec = versionSpecFor(card);
            if (!vspec) return null;
            return (
              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>{vspec.label}</div>
                <select value={draft.version||""} onChange={e=>setDraft({...draft,version:e.target.value})} style={{...inp,width:"100%",cursor:"pointer"}}>
                  <option value="">{"\u2014 not recorded"}</option>
                  {vspec.options.map(o=><option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            );
          })()}
          {/* Lending. Kept right at the top because it changes whether the copy can be decked at all,
              which matters more day-to-day than cost or grading. */}
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:4 }}>Where is this copy?</div>
            <div style={{ display:"flex", gap:6, marginBottom:draft.lendState?8:0 }}>
              {[["","\uD83C\uDFE0 In hand"],["lent","\uD83D\uDCE4 Lent out"],["borrowed","\uD83D\uDCE5 Borrowed"]].map(([v,label])=>{
                const on = (draft.lendState||"") === v;
                const col = v==="lent" ? "#FBBF24" : v==="borrowed" ? "#7B9CFF" : "#4ade80";
                return (
                  <button key={v} onClick={()=>setDraft({...draft, lendState:v,
                    // Starting a loan stamps today so "how long has Jason had this" is answerable
                    // without anyone having to remember to fill in a date.
                    lendDate: v && !draft.lendDate ? todayLocal() : (v ? draft.lendDate : ""),
                    lendWho: v ? draft.lendWho : "", lendWhy: v ? draft.lendWhy : ""})}
                    style={{flex:1,background:on?col+"22":"transparent",border:"1px solid "+(on?col:"#333"),
                      color:on?col:"#888",borderRadius:8,padding:"7px 4px",fontSize:11,fontWeight:800,
                      cursor:"pointer",fontFamily:"inherit"}}>
                    {label}
                  </button>
                );
              })}
            </div>
            {draft.lendState && (
              <div style={{ display:"flex", gap:8 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>
                    {draft.lendState==="lent" ? "Who has it" : "Who lent it to you"}
                  </div>
                  <input value={draft.lendWho} onChange={e=>setDraft({...draft,lendWho:e.target.value})}
                    placeholder="Name" style={{...inp, width:"100%"}}/>
                </div>
                <div style={{ flex:1.3 }}>
                  <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>What for</div>
                  <input value={draft.lendWhy} onChange={e=>setDraft({...draft,lendWhy:e.target.value})}
                    placeholder="e.g. BJBA league night" style={{...inp, width:"100%"}}/>
                </div>
              </div>
            )}
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:10 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>Date acquired</div>
              <input type="date" value={draft.date} onChange={e=>setDraft({...draft,date:e.target.value,purchaseDate:e.target.value})} style={{...inp,width:"100%"}}/>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>Serial</div>
              <input value={draft.serial} onChange={e=>setDraft({...draft,serial:e.target.value})} placeholder="21/50" style={{...inp,width:"100%"}}/>
            </div>
          </div>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>Notes</div>
            <input value={draft.notes} onChange={e=>setDraft({...draft,notes:e.target.value})} placeholder="e.g. from John's break" style={{...inp,width:"100%"}}/>
          </div>

          {/* Locking is a decision, not paperwork \u2014 it stays where you can't miss it. */}
          <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:12, fontWeight:700, color: draft.locked ? "#FBBF24" : "rgba(255,255,255,0.5)", marginBottom:10, background: draft.locked ? "rgba(251,191,36,0.08)" : "transparent", border:"1px solid "+(draft.locked?"rgba(251,191,36,0.35)":"rgba(255,255,255,0.08)"), borderRadius:8, padding:"8px 10px" }}>
            <input type="checkbox" checked={!!draft.locked} onChange={e=>setDraft({...draft,locked:e.target.checked})} style={{ accentColor:"#FBBF24", width:15, height:15 }}/>
            {"\uD83D\uDD12"} Never trade or sell
          </label>

          {(() => {
            const anyGrade = draft.gradeCompany || draft.grade || draft.certNum || draft.condition;
            const anyWhere = draft.location;
            const sec = (open, set, label, filled) => (
              <button onClick={()=>set(v=>!v)} style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between", background:"transparent", border:"none", borderTop:"1px solid rgba(255,255,255,0.07)", color:"rgba(255,255,255,0.55)", padding:"9px 2px", fontSize:11.5, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
                <span>{label}{filled && <span style={{ color:"#4ade80", marginLeft:6, fontSize:10 }}>{"\u2713"}</span>}</span>
                <span style={{ opacity:0.5 }}>{open ? "\u25B4" : "\u25BE"}</span>
              </button>
            );
            return (
              <>
                {sec(showGrading, setShowGrading, "Condition & grading", anyGrade)}
                {showGrading && (
                  <div style={{ paddingBottom:8 }}>
                    <div style={{ marginBottom:8 }}>
                      <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>Condition</div>
                      <select value={draft.condition} onChange={e=>setDraft({...draft,condition:e.target.value})} style={{...inp,width:"100%",cursor:"pointer"}}>
                        {CONDITIONS.map(x=><option key={x} value={x}>{x||"\u2014"}</option>)}
                      </select>
                    </div>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      <div style={{ flex:1, minWidth:110 }}>
                        <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>Grader</div>
                        <select value={draft.gradeCompany} onChange={e=>setDraft({...draft,gradeCompany:e.target.value})} style={{...inp,width:"100%",cursor:"pointer"}}>
                          {GRADERS.map(x=><option key={x} value={x}>{x||"Raw"}</option>)}
                        </select>
                      </div>
                      <div style={{ flex:1, minWidth:70 }}>
                        <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>Grade</div>
                        <input value={draft.grade} onChange={e=>setDraft({...draft,grade:e.target.value})} placeholder="9.5" style={{...inp,width:"100%"}}/>
                      </div>
                      <div style={{ flex:1, minWidth:110 }}>
                        <div style={{ fontSize:10, color:"#a0a0a0", marginBottom:3 }}>Cert #</div>
                        <input value={draft.certNum} onChange={e=>setDraft({...draft,certNum:e.target.value})} placeholder="cert number" style={{...inp,width:"100%"}}/>
                      </div>
                    </div>
                  </div>
                )}
                {sec(showWhere, setShowWhere, "Storage location", anyWhere)}
                {showWhere && (
                  <div style={{ paddingBottom:10 }}>
                    <input value={draft.location} onChange={e=>setDraft({...draft,location:e.target.value})} placeholder="e.g. Binder 3 page 12, safe, slab case" style={{...inp,width:"100%"}}/>
                  </div>
                )}
              </>
            );
          })()}
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={commit} style={{ flex:1, background:"linear-gradient(135deg,#E8317A,#FBBF24)", border:"none", color:"#fff", borderRadius:8, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>{editId?"Save changes":"Add copy"}</button>
            {editId && <button onClick={()=>{setEditId(null);setDraft(blank);}} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"10px 16px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>}
          </div>
        </div>
        <div style={{ fontSize:10, color:"#9a8a94", marginTop:12, textAlign:"center" }}>Adding cost & value helps build BoBA's live comp data 📊</div>
      </div>
    </div>
  );
}

function ReviewModal({ sale, onSubmit, onClose, inp }) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  if (!sale) return null;
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, zIndex:10005, background:"rgba(0,0,0,0.8)", display:"flex", alignItems:"center", justifyContent:"center", padding:16, backdropFilter:"blur(6px)" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:18, width:"min(420px,100%)", padding:"24px 26px" }}>
        <div style={{ fontSize:11, color:"#999", fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>Rate your seller</div>
        <div style={{ fontSize:18, fontWeight:900, color:"#fff", marginBottom:2 }}>{sale.sellerName||"Seller"}</div>
        <div style={{ fontSize:12, color:"#a0a0a0", marginBottom:18 }}>{sale.cardName}{sale.price?` · $${Number(sale.price).toFixed(2)}`:""}</div>

        <div style={{ display:"flex", gap:6, justifyContent:"center", marginBottom:16 }}>
          {[1,2,3,4,5].map(n=>(
            <button key={n} onMouseEnter={()=>setHover(n)} onMouseLeave={()=>setHover(0)} onClick={()=>setStars(n)}
              style={{ background:"none", border:"none", cursor:"pointer", fontSize:38, lineHeight:1, padding:0, filter:(hover||stars)>=n?"none":"grayscale(1) opacity(0.35)", transform:(hover||stars)>=n?"scale(1.1)":"scale(1)", transition:"transform 0.1s" }}>⭐</button>
          ))}
        </div>
        <div style={{ textAlign:"center", fontSize:12, color:"var(--bz-ink-2)", marginBottom:16, minHeight:16 }}>
          {["","Poor","Fair","Good","Great","Excellent"][hover||stars] || "Tap a star to rate"}
        </div>

        <textarea value={text} onChange={e=>setText(e.target.value)} placeholder="Add a review (optional) — how was the transaction?" rows={3} maxLength={600} style={{...inp, width:"100%", resize:"none", marginBottom:14 }}/>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={()=>stars>0 && onSubmit(sale, stars, text)} disabled={stars===0}
            style={{ flex:1, background:stars>0?"linear-gradient(135deg,#E8317A,#FBBF24)":"var(--bz-line-2)", border:"none", color:stars>0?"#fff":"#666", borderRadius:10, padding:"11px", fontSize:14, fontWeight:800, cursor:stars>0?"pointer":"not-allowed", fontFamily:"inherit" }}>Submit Review</button>
          <button onClick={onClose} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:10, padding:"11px 18px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Later</button>
        </div>
      </div>
    </div>
  );
}

function SellerBadge({ uid, name, marketSales=[], inline=true, onViewProfile }) {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(null); // { collectionCount, deals, rating, reviewCount, reviews }
  const [loading, setLoading] = useState(false);

  async function loadStats() {
    if (stats || loading || !uid) return;
    setLoading(true);
    try {
      const [ownSnap, revSnap, uSnap] = await Promise.all([
        getDoc(doc(db,"boba_owned",uid)),
        getDocs(query(collection(db,"boba_reviews"), where("sellerUid","==",uid))),
        getDoc(doc(db,"users",uid)),
      ]);
      const owned = ownSnap.exists() ? ownSnap.data() : {};
      const collectionCount = Object.values(owned).reduce((s,q)=>s+(q||1),0);
      const deals = marketSales.filter(s => s.sellerUid===uid || s.buyerUid===uid).length;
      const reviews = revSnap.docs.map(d=>d.data()).sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||""));
      const reviewCount = reviews.length;
      const rating = reviewCount>0 ? reviews.reduce((s,r)=>s+(r.stars||0),0)/reviewCount : null;
      const handle = uSnap.exists() && uSnap.data().username ? `@${uSnap.data().username}` : null;
      setStats({ collectionCount, deals, rating, reviewCount, reviews, handle });
    } catch(e) { console.error("seller stats failed:", e); setStats({ collectionCount:0, deals:0, rating:null, reviewCount:0, reviews:[] }); }
    setLoading(false);
  }

  const written = stats ? stats.reviews.filter(r=>r.text && r.text.trim()) : [];

  return (
    <span style={{ position:"relative", display:"inline-block" }}
      onMouseEnter={()=>{ setOpen(true); loadStats(); }}
      onMouseLeave={()=>setOpen(false)}
      onClick={e=>{ e.stopPropagation(); setOpen(o=>!o); loadStats(); }}>
      <span style={{ color:"#7B9CFF", fontWeight:700, cursor:"pointer", textDecoration:"underline", textDecorationStyle:"dotted", textUnderlineOffset:2 }}>{name||"Collector"}</span>
      {open && (
        <div onClick={e=>e.stopPropagation()} style={{ position:"absolute", bottom:"calc(100% + 8px)", left:0, zIndex:10010, width:250, maxHeight:340, overflowY:"auto", background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:12, boxShadow:"0 12px 40px rgba(0,0,0,0.7)", padding:"14px 16px", cursor:"default" }}>
          <div style={{ fontSize:14, fontWeight:900, color:"#fff", marginBottom:2 }}>{name||"Collector"}</div>
          {onViewProfile && uid && <button onClick={e=>{ e.stopPropagation(); onViewProfile(uid); setOpen(false); }} style={{ background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff", border:"none", borderRadius:7, padding:"5px 12px", fontSize:11, fontWeight:800, cursor:"pointer", fontFamily:"inherit", marginBottom:8, width:"100%" }}>View Full Profile →</button>}
          {stats?.handle && <div style={{ fontSize:11, color:"#7B9CFF", fontWeight:700, marginBottom:8 }}>{stats.handle}</div>}
          {!stats ? (
            <div style={{ fontSize:12, color:"#999" }}>Loading…</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Cards in collection</span>
                <span style={{ fontSize:13, fontWeight:800, color:"#4ade80" }}>{stats.collectionCount.toLocaleString()}</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Deals done</span>
                <span style={{ fontSize:13, fontWeight:800, color:"#7B9CFF" }}>{stats.deals.toLocaleString()}</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontSize:12, color:"var(--bz-ink-2)" }}>Seller rating</span>
                {stats.rating!=null ? (
                  <span style={{ fontSize:13, fontWeight:800, color:"#FBBF24" }}>{stats.rating.toFixed(1)} ⭐ <span style={{ color:"#999", fontWeight:600, fontSize:11 }}>· {stats.reviewCount}</span></span>
                ) : (
                  <span style={{ fontSize:11, fontWeight:700, color:"#7B9CFF", background:"rgba(123,156,255,0.12)", border:"1px solid rgba(123,156,255,0.3)", borderRadius:20, padding:"2px 8px" }}>New collector</span>
                )}
              </div>
              {written.length>0 && (
                <div style={{ marginTop:6, borderTop:"1px solid var(--bz-line-2)", paddingTop:10 }}>
                  <div style={{ fontSize:10, color:"#999", fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:8 }}>Recent reviews</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {written.slice(0,6).map((r,i)=>(
                      <div key={i} style={{ background:"var(--bz-s2)", borderRadius:8, padding:"8px 10px" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3 }}>
                          <span style={{ fontSize:11, color:"#FBBF24", fontWeight:700 }}>{"⭐".repeat(r.stars||0)}</span>
                          <span style={{ fontSize:9, color:"var(--bz-ink-3)" }}>{(r.createdAt||"").split("T")[0]}</span>
                        </div>
                        <div style={{ fontSize:11, color:"#bbb", lineHeight:1.4 }}>{r.text}</div>
                        <div style={{ fontSize:9, color:"var(--bz-ink-3)", marginTop:3 }}>— {r.buyerName||"buyer"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </span>
  );
}

function AccountingLedger({ lots=[], marketSales=[], user, cards=[] }) {
  const cardName = (id) => { const c = cards.find(x=>x.id===id); return c ? `${c.hero}${c.treatment?` · ${c.treatment}`:""}` : "Card"; };
  // Purchases: lots with a cost (any acquisition with money out)
  const purchases = lots
    .filter(l => l.cost != null && l.cost > 0)
    .map(l => ({ ...l, when: l.date || "", name: cardName(l.cardId) }))
    .sort((a,b)=>(b.when||"").localeCompare(a.when||""));
  // Sales: market_sales where this user is the seller
  const sales = marketSales
    .filter(s => s.sellerUid === user?.uid && s.price)
    .map(s => ({ ...s, when: (s.soldDate || (s.soldAt||"").split("T")[0] || ""), name: s.cardName || "Card" }))
    .sort((a,b)=>(b.when||"").localeCompare(a.when||""));

  const totalPurchases = purchases.reduce((s,p)=>s+(p.cost||0),0);
  const totalSales     = sales.reduce((s,x)=>s+(Number(x.price)||0),0);
  const netProfit      = totalSales - totalPurchases;
  const currentValue   = lots.reduce((s,l)=>s+(l.value||0),0);

  // ---- PORTFOLIO MATH -------------------------------------------------------------------
  // The distinction that matters: REALISED gain is money you actually banked (sales minus what
  // those cards cost you). UNREALISED is paper — what you hold is worth more than you paid, but
  // you have not sold it. Lumping them together is how people fool themselves about performance.
  // Borrowed copies are excluded from valuation: they are in your hands but they are someone else's
  // property, so counting their cost or value would report another person's cards as your assets.
  const heldLots  = lots.filter(l => !l.soldAt && !l.sold && l.lendState !== "borrowed");
  // Cost basis = cash + the value of cards traded away. Cash is a hard fact; trade value is the
  // user's own estimate of what they gave up, because in a real negotiation the credited value is
  // never formally stated. Both are kept separately so a cash-only figure stays available \u2014 one is
  // measured, the other estimated, and blending them silently would hide that.
  const heldCash  = heldLots.reduce((s,l)=>s+(Number(l.cost)||0),0);
  const heldTrade = heldLots.reduce((s,l)=>s+(Number(l.tradeValue)||0),0);
  const costBasis = heldCash + heldTrade;
  const heldValue = heldLots.reduce((s,l)=>s+(Number(l.value)||0),0);     // what they're worth now
  const unrealised = heldValue - costBasis;
  const unrealisedPct = costBasis > 0 ? (unrealised / costBasis) * 100 : null;

  // Cost basis of things actually sold, so realised profit is real rather than "all sales are profit".
  const soldCostById = {};
  lots.filter(l => l.soldAt || l.sold).forEach(l => { soldCostById[l.cardId] = (soldCostById[l.cardId]||0) + (Number(l.cost)||0) + (Number(l.tradeValue)||0); });
  const realisedCost = Object.values(soldCostById).reduce((s,n)=>s+n,0);
  const realised = totalSales - realisedCost;
  const realisedPct = realisedCost > 0 ? (realised / realisedCost) * 100 : null;

  // Overall ROI on every dollar you have put in, banked and on paper together.
  const totalIn  = costBasis + realisedCost;
  const totalOut = heldValue + totalSales;
  const roiPct   = totalIn > 0 ? ((totalOut - totalIn) / totalIn) * 100 : null;

  // How much of the collection has a cost recorded. Without this the numbers look authoritative
  // when they may be built on a fraction of the data — the single most misleading thing a
  // financial screen can do.
  const lotsWithCost = heldLots.filter(l => (Number(l.cost)||0) + (Number(l.tradeValue)||0) > 0).length;
  const costCoverage = heldLots.length ? (lotsWithCost / heldLots.length) * 100 : 0;
  const lotsWithValue = heldLots.filter(l => Number(l.value) > 0).length;
  const valueCoverage = heldLots.length ? (lotsWithValue / heldLots.length) * 100 : 0;

  // Per-card performance — the "positions" view. Group held lots by card.
  const byCard = {};
  heldLots.forEach(l => {
    const k = l.cardId;
    if (!byCard[k]) byCard[k] = { cardId:k, name:cardName(k), qty:0, cost:0, value:0 };
    byCard[k].qty   += 1;
    byCard[k].cost  += (Number(l.cost)||0) + (Number(l.tradeValue)||0);
    byCard[k].value += Number(l.value)||0;
  });
  const positions = Object.values(byCard).map(p => ({
    ...p,
    gain: p.value - p.cost,
    pct: p.cost > 0 ? ((p.value - p.cost) / p.cost) * 100 : null,
  }));
  const movers = positions.filter(p => p.cost > 0).sort((a,b)=>b.gain-a.gain);
  const topGainers = movers.slice(0,5);
  const topLosers  = movers.slice(-5).reverse().filter(p => p.gain < 0);
  const biggest = [...positions].sort((a,b)=>b.value-a.value).slice(0,5);

  // Insurance view: replacement value, and the pieces significant enough to schedule individually
  // (most policies want anything over a threshold listed by name rather than lumped in).
  const INSURE_THRESHOLD = 250;
  const scheduled = positions.filter(p => (p.value/Math.max(1,p.qty)) >= INSURE_THRESHOLD)
                             .sort((a,b)=>b.value-a.value);
  const scheduledValue = scheduled.reduce((s,p)=>s+p.value,0);

  // Monthly P&L
  const months = {};
  const monthKey = (d) => (d||"").slice(0,7); // YYYY-MM
  purchases.forEach(p=>{ const k=monthKey(p.when); if(!k)return; (months[k]=months[k]||{buy:0,sell:0}).buy += p.cost||0; });
  sales.forEach(s=>{ const k=monthKey(s.when); if(!k)return; (months[k]=months[k]||{buy:0,sell:0}).sell += Number(s.price)||0; });
  const monthRows = Object.keys(months).sort((a,b)=>b.localeCompare(a)).map(k=>({ month:k, ...months[k], net:(months[k].sell-months[k].buy) }));
  const fmtMonth = (k) => { try { const [y,m]=k.split("-"); return new Date(y,m-1,1).toLocaleDateString(undefined,{month:"short",year:"numeric"}); } catch { return k; } };
  const money = (n) => `${n<0?"-":""}$${Math.abs(n).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`;

  const card = { background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:14, padding:"16px 18px" };

  if (!user) return <div style={{textAlign:"center",padding:60,color:"rgba(255,255,255,0.4)"}}>Sign in to see your Vault Report.</div>;

  return (
    <div style={{ maxWidth:820, margin:"0 auto" }}>
      <div style={{ fontSize:22, fontWeight:900, color:"#fff", marginBottom:4 }}>📊 Vault Report</div>
      <div style={{ fontSize:13, color:"rgba(255,255,255,0.45)", marginBottom:16 }}>What your collection cost, what it's worth, and how it's performing.</div>

      {/* How complete the underlying data is. Every number below is only as good as the costs and
          values entered, so say so up front rather than presenting confident-looking totals built
          on a third of the collection. */}
      {heldLots.length > 0 && (costCoverage < 95 || valueCoverage < 95) && (
        <div style={{ background:"rgba(251,191,36,0.08)", border:"1px solid rgba(251,191,36,0.3)", borderRadius:12, padding:"11px 14px", marginBottom:16, fontSize:12, color:"#FBBF24", lineHeight:1.6 }}>
          <strong>Based on partial data.</strong>{" "}
          {Math.round(costCoverage)}% of your copies have a purchase price and {Math.round(valueCoverage)}% have a value.
          Anything missing counts as $0, so the totals below understate reality. Add costs under {"\uD83D\uDCB0"} Details on a card.
        </div>
      )}

      {/* Top-line numbers */}
      {/* HEADLINE: what it's worth, what it cost, and the gap between them. */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))", gap:12, marginBottom:12 }}>
        <div style={{ ...card, borderColor:"rgba(123,156,255,0.3)" }}>
          <div style={{ fontSize:11, color:"var(--bz-ink-2)", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Vault Value</div>
          <div style={{ fontSize:26, fontWeight:900, color:"#7B9CFF", marginTop:4 }}>{money(heldValue)}</div>
          <div style={{ fontSize:11, color:"#999", marginTop:2 }}>{heldLots.length} cop{heldLots.length===1?"y":"ies"} held</div>
        </div>
        <div style={{ ...card, borderColor:"rgba(232,49,122,0.25)" }}>
          <div style={{ fontSize:11, color:"var(--bz-ink-2)", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Cost Basis</div>
          <div style={{ fontSize:26, fontWeight:900, color:"#E8317A", marginTop:4 }}>{money(costBasis)}</div>
          <div style={{ fontSize:11, color:"#999", marginTop:2 }}>
            {heldTrade > 0
              ? <>{money(heldCash)} cash + {money(heldTrade)} trade</>
              : "what you paid for them"}
          </div>
        </div>
        <div style={{ ...card, borderColor:unrealised>=0?"rgba(74,222,128,0.3)":"rgba(239,68,68,0.3)" }}>
          <div style={{ fontSize:11, color:"var(--bz-ink-2)", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Unrealised</div>
          <div style={{ fontSize:26, fontWeight:900, color:unrealised>=0?"#4ade80":"#EF4444", marginTop:4 }}>
            {unrealised>=0?"+":""}{money(unrealised)}
          </div>
          <div style={{ fontSize:11, color:"#999", marginTop:2 }}>
            {unrealisedPct==null ? "on paper \u2014 not sold" : `${unrealisedPct>=0?"+":""}${unrealisedPct.toFixed(1)}% on paper`}
          </div>
        </div>
        <div style={{ ...card, borderColor:realised>=0?"rgba(74,222,128,0.3)":"rgba(239,68,68,0.3)" }}>
          <div style={{ fontSize:11, color:"var(--bz-ink-2)", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Realised</div>
          <div style={{ fontSize:26, fontWeight:900, color:realised>=0?"#4ade80":"#EF4444", marginTop:4 }}>
            {realised>=0?"+":""}{money(realised)}
          </div>
          <div style={{ fontSize:11, color:"#999", marginTop:2 }}>
            {realisedPct==null ? "banked from sales" : `${realisedPct>=0?"+":""}${realisedPct.toFixed(1)}% banked`}
          </div>
        </div>
      </div>

      {/* ROI across everything \u2014 held and sold together. */}
      <div style={{ ...card, marginBottom:16, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12,
                    borderColor:(roiPct??0)>=0?"rgba(74,222,128,0.3)":"rgba(239,68,68,0.3)" }}>
        <div>
          <div style={{ fontSize:11, color:"var(--bz-ink-2)", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Collection ROI</div>
          <div style={{ fontSize:13, color:"#999", marginTop:3 }}>{money(totalIn)} in {"\u2192"} {money(totalOut)} out (held value + sales)</div>
        </div>
        <div style={{ fontSize:34, fontWeight:900, color:(roiPct??0)>=0?"#4ade80":"#EF4444", lineHeight:1 }}>
          {roiPct==null ? <span style={{fontSize:15,color:"#999",fontWeight:700}}>Add purchase prices to see ROI</span>
                        : `${roiPct>=0?"+":""}${roiPct.toFixed(1)}%`}
        </div>
      </div>

      {/* POSITIONS \u2014 which cards are actually carrying the collection. */}
      {positions.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:12, marginBottom:16 }}>
          <div style={card}>
            <div style={{ fontSize:14, fontWeight:800, color:"#fff", marginBottom:10 }}>{"\uD83D\uDCC8"} Biggest Movers</div>
            {topGainers.length===0 ? (
              <div style={{ fontSize:12, color:"#999" }}>Add purchase prices and values to see which cards are up.</div>
            ) : topGainers.map(p=>(
              <div key={p.cardId} style={{ display:"flex", justifyContent:"space-between", gap:10, padding:"6px 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                <div style={{ fontSize:12, color:"var(--bz-ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {p.name}{p.qty>1 && <span style={{color:"#666"}}> {"\u00d7"}{p.qty}</span>}
                </div>
                <div style={{ fontSize:12, fontWeight:800, color:p.gain>=0?"#4ade80":"#EF4444", whiteSpace:"nowrap" }}>
                  {p.gain>=0?"+":""}{money(p.gain)}{p.pct!=null && <span style={{opacity:0.6,fontWeight:600}}> ({p.pct>=0?"+":""}{p.pct.toFixed(0)}%)</span>}
                </div>
              </div>
            ))}
            {topLosers.length>0 && (
              <>
                <div style={{ fontSize:11, fontWeight:800, color:"#EF4444", marginTop:12, marginBottom:6, textTransform:"uppercase", letterSpacing:0.5 }}>Down</div>
                {topLosers.map(p=>(
                  <div key={p.cardId} style={{ display:"flex", justifyContent:"space-between", gap:10, padding:"5px 0" }}>
                    <div style={{ fontSize:12, color:"var(--bz-ink-2)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</div>
                    <div style={{ fontSize:12, fontWeight:800, color:"#EF4444", whiteSpace:"nowrap" }}>
                      {money(p.gain)}{p.pct!=null && <span style={{opacity:0.6,fontWeight:600}}> ({p.pct.toFixed(0)}%)</span>}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          <div style={card}>
            <div style={{ fontSize:14, fontWeight:800, color:"#fff", marginBottom:10 }}>{"\uD83D\uDC8E"} Top Holdings</div>
            {biggest.map(p=>{
              const share = heldValue>0 ? (p.value/heldValue)*100 : 0;
              return (
                <div key={p.cardId} style={{ padding:"6px 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", gap:10 }}>
                    <div style={{ fontSize:12, color:"var(--bz-ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {p.name}{p.qty>1 && <span style={{color:"#666"}}> {"\u00d7"}{p.qty}</span>}
                    </div>
                    <div style={{ fontSize:12, fontWeight:800, color:"#7B9CFF", whiteSpace:"nowrap" }}>{money(p.value)}</div>
                  </div>
                  {/* Concentration matters: if one card is a third of the vault, that is the risk. */}
                  <div style={{ height:3, background:"rgba(255,255,255,0.07)", borderRadius:2, marginTop:4, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${Math.min(100,share)}%`, background:"#7B9CFF" }}/>
                  </div>
                  <div style={{ fontSize:10, color:"#666", marginTop:2 }}>{share.toFixed(1)}% of vault value</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* INSURANCE \u2014 what it would cost to replace, and what a policy will want listed by name. */}
      {heldValue > 0 && (
        <div style={{ ...card, marginBottom:16, borderColor:"rgba(251,191,36,0.25)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10, marginBottom:10 }}>
            <div style={{ fontSize:14, fontWeight:800, color:"#fff" }}>{"\uD83D\uDEE1"} Insurance Summary</div>
            <div style={{ fontSize:20, fontWeight:900, color:"#FBBF24" }}>{money(heldValue)}</div>
          </div>
          <div style={{ fontSize:12, color:"var(--bz-ink-2)", lineHeight:1.6, marginBottom:10 }}>
            Replacement value of {heldLots.length} cop{heldLots.length===1?"y":"ies"} across {positions.length} card{positions.length===1?"":"s"}.
            {scheduled.length>0 && <> {scheduled.length} piece{scheduled.length===1?"":"s"} worth {money(INSURE_THRESHOLD)}+ each
            {"("}{money(scheduledValue)} total) would usually need scheduling individually on a policy.</>}
          </div>
          {scheduled.length>0 && (
            <div style={{ maxHeight:170, overflowY:"auto" }}>
              {scheduled.map(p=>(
                <div key={p.cardId} style={{ display:"flex", justifyContent:"space-between", gap:10, padding:"4px 0", fontSize:12 }}>
                  <span style={{ color:"var(--bz-ink-2)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {p.name}{p.qty>1 && <span style={{color:"#666"}}> {"\u00d7"}{p.qty}</span>}
                  </span>
                  <span style={{ color:"#FBBF24", fontWeight:700, whiteSpace:"nowrap" }}>{money(p.value)}</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize:10.5, color:"#666", marginTop:10, lineHeight:1.5 }}>
            Based on the values you've entered \u2014 not a professional appraisal. Insurers usually want
            documented values and photos, both of which you can export from here.
          </div>
        </div>
      )}

      {/* Lifetime totals kept, but demoted \u2014 they are history, not position. */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:12, marginBottom:16 }}>
        <div style={card}>
          <div style={{ fontSize:11, color:"var(--bz-ink-2)", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Lifetime Spent</div>
          <div style={{ fontSize:20, fontWeight:900, color:"#E8317A", marginTop:4 }}>{money(totalPurchases)}</div>
          <div style={{ fontSize:11, color:"#999", marginTop:2 }}>{purchases.length} buy{purchases.length===1?"":"s"}</div>
        </div>
        <div style={card}>
          <div style={{ fontSize:11, color:"var(--bz-ink-2)", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Lifetime Sales</div>
          <div style={{ fontSize:20, fontWeight:900, color:"#4ade80", marginTop:4 }}>{money(totalSales)}</div>
          <div style={{ fontSize:11, color:"#999", marginTop:2 }}>{sales.length} sale{sales.length===1?"":"s"}</div>
        </div>
        <div style={card}>
          <div style={{ fontSize:11, color:"var(--bz-ink-2)", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Cash Position</div>
          <div style={{ fontSize:20, fontWeight:900, color:netProfit>=0?"#4ade80":"#EF4444", marginTop:4 }}>{money(netProfit)}</div>
          <div style={{ fontSize:11, color:"#999", marginTop:2 }}>sales {"\u2212"} all purchases</div>
        </div>
      </div>

      {/* Month-by-month cash flow. */}
      <div style={{ ...card, marginBottom:16 }}>
        <div style={{ fontSize:14, fontWeight:800, color:"#fff", marginBottom:12 }}>Month-by-Month P&L</div>
        {monthRows.length===0 ? (
          <div style={{ fontSize:13, color:"#999" }}>No activity yet. Add card costs (💰 Details) and make marketplace sales to populate this.</div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ color:"var(--bz-ink-2)", textAlign:"right" }}>
                  <th style={{ textAlign:"left", padding:"6px 8px", fontWeight:700 }}>Month</th>
                  <th style={{ padding:"6px 8px", fontWeight:700 }}>Purchases</th>
                  <th style={{ padding:"6px 8px", fontWeight:700 }}>Sales</th>
                  <th style={{ padding:"6px 8px", fontWeight:700 }}>Net</th>
                </tr>
              </thead>
              <tbody>
                {monthRows.map(r=>(
                  <tr key={r.month} style={{ borderTop:"1px solid #1f1f1f" }}>
                    <td style={{ textAlign:"left", padding:"8px", color:"#ddd", fontWeight:700 }}>{fmtMonth(r.month)}</td>
                    <td style={{ textAlign:"right", padding:"8px", color:"#E8317A" }}>{money(r.buy)}</td>
                    <td style={{ textAlign:"right", padding:"8px", color:"#4ade80" }}>{money(r.sell)}</td>
                    <td style={{ textAlign:"right", padding:"8px", fontWeight:800, color:r.net>=0?"#4ade80":"#EF4444" }}>{money(r.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent transactions */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:16 }}>
        <div style={card}>
          <div style={{ fontSize:14, fontWeight:800, color:"#E8317A", marginBottom:10 }}>Purchases</div>
          {purchases.length===0 ? <div style={{ fontSize:12, color:"#999" }}>No purchases logged. Use 💰 Details on a card to add cost.</div> :
            purchases.slice(0,20).map(p=>(
              <div key={p.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderTop:"1px solid var(--bz-line)" }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:12, color:"#ddd", fontWeight:600, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.name}</div>
                  <div style={{ fontSize:10, color:"#999" }}>{(LOT_METHODS.find(m=>m.v===p.method)?.l||p.method)} · {p.when}</div>
                </div>
                <div style={{ fontSize:13, fontWeight:800, color:"#E8317A", whiteSpace:"nowrap", marginLeft:8 }}>{money(p.cost)}</div>
              </div>
            ))
          }
        </div>
        <div style={card}>
          <div style={{ fontSize:14, fontWeight:800, color:"#4ade80", marginBottom:10 }}>Sales</div>
          {sales.length===0 ? <div style={{ fontSize:12, color:"#999" }}>No marketplace sales yet.</div> :
            sales.slice(0,20).map(s=>(
              <div key={s.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderTop:"1px solid var(--bz-line)" }}>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:12, color:"#ddd", fontWeight:600, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{s.name}</div>
                  <div style={{ fontSize:10, color:"#999" }}>to {s.buyerName||"buyer"} · {s.when}</div>
                </div>
                <div style={{ fontSize:13, fontWeight:800, color:"#4ade80", whiteSpace:"nowrap", marginLeft:8 }}>{money(Number(s.price))}</div>
              </div>
            ))
          }
        </div>
      </div>
      <div style={{ fontSize:10, color:"#8a8a8a", marginTop:16, textAlign:"center" }}>Purchases come from costs you enter on your cards. Sales come from completed marketplace transactions where you were the seller. Trades and gifts (no cost) are excluded from spend.</div>
    </div>
  );
}

function OnboardingModal({ user, onComplete, inp }) {
  const [username, setUsername] = useState("");
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState(null); // null | "available" | "taken" | "invalid"
  const [saving, setSaving] = useState(false);
  const [photoURL, setPhotoURL] = useState(user?.photoURL || "");
  const [uploading, setUploading] = useState(false);
  const [discordName, setDiscordName] = useState("");
  const [whatnotName, setWhatnotName] = useState("");
  const [step, setStep] = useState(1); // 1 = username, 2 = pic + socials

  const clean = (s) => s.toLowerCase().replace(/[^a-z0-9_]/g,"").slice(0,20);

  // Debounced availability check
  useEffect(() => {
    const u = clean(username);
    if (u.length < 3) { setStatus(u.length===0 ? null : "invalid"); return; }
    setChecking(true); setStatus(null);
    const t = setTimeout(async () => {
      try {
        const snap = await getDoc(doc(db,"usernames",u));
        if (!snap.exists()) { setStatus("available"); }
        else if (snap.data().uid === user?.uid) { setStatus("mine"); } // already reserved to me — let me finish claiming it
        else { setStatus("taken"); }
      } catch(e) { setStatus(null); }
      setChecking(false);
    }, 400);
    return () => clearTimeout(t);
  }, [username]);

  async function claimUsername() {
    const u = clean(username);
    if (u.length < 3 || (status !== "available" && status !== "mine") || !user) return;
    setSaving(true);
    try {
      // Write the PROFILE first. These were the other way round, and the two writes are not atomic:
      // if the reservation succeeded and the profile write did not, the handle was locked to this
      // uid while users/{uid} still had no username \u2014 so the app kept asking for a username on every
      // other device, even though the person had already claimed one. Profile first means a partial
      // failure leaves the account WORKING, with only the reservation missing.
      await setDoc(doc(db,"users",user.uid), { username: u }, { merge:true });
      await setDoc(doc(db,"usernames",u), { uid: user.uid, createdAt: new Date().toISOString() }, { merge:true });
      // Save locally too — this is the reliable backup that survives any Firestore flakiness
      try { localStorage.setItem("bazooka_username_" + user.uid, u); } catch(e) {}
      setStep(2);
    } catch(e) { console.error("claim failed:", e); alert("Couldn't save username: "+(e.message||"unknown error")); }
    setSaving(false);
  }

  async function uploadPic(file) {
    if (!file || !user) return;
    setUploading(true);
    try {
      const sref = ref(storage, `profile_pics/${user.uid}/${uid()}.jpg`);
      const _cfile = await compressImage(file);
      await uploadBytes(sref, _cfile);
      const url = await getDownloadURL(sref);
      await setDoc(doc(db,"users",user.uid), { photoURL: url }, { merge:true });
      try { localStorage.setItem("bazooka_photo_" + user.uid, url); } catch(e) {}
      setPhotoURL(url);
    } catch(e) { console.error("pic upload failed:", e); alert("Photo upload failed."); }
    setUploading(false);
  }

  function finish() {
    const d = discordName.trim(), w = whatnotName.trim();
    if (d || w) {
      setDoc(doc(db,"users",user.uid), { ...(d?{discordName:d}:{}), ...(w?{whatnotName:w}:{}) }, { merge:true }).catch(()=>{});
    }
    onComplete(clean(username), photoURL);
  }

  return (
    <div style={{ position:"fixed", inset:0, zIndex:10020, background:"rgba(0,0,0,0.9)", display:"flex", alignItems:"center", justifyContent:"center", padding:16, backdropFilter:"blur(10px)" }}>
      <div style={{ background:"linear-gradient(135deg,var(--bz-s1),#140810)", border:"1px solid rgba(232,49,122,0.3)", borderRadius:22, width:"min(440px,100%)", padding:"32px 30px", boxShadow:"0 40px 120px rgba(232,49,122,0.2)" }}>
        {/* progress */}
        <div style={{ display:"flex", gap:6, marginBottom:22 }}>
          <div style={{ flex:1, height:4, borderRadius:2, background: step>=1 ? "#E8317A" : "var(--bz-line-2)" }}/>
          <div style={{ flex:1, height:4, borderRadius:2, background: step>=2 ? "#E8317A" : "var(--bz-line-2)" }}/>
        </div>

        {step===1 ? (
          <>
            <div style={{ fontSize:26, marginBottom:6 }}>👋</div>
            <div style={{ fontSize:22, fontWeight:900, color:"#fff", marginBottom:6 }}>Claim your username</div>
            {/* Signing in on a second device and landing here is alarming \u2014 it reads like the account
                did not carry over. Naming the signed-in account makes it obvious this is the same
                one, and that picking a handle adds to it rather than starting over. */}
            {user?.email && (
              <div style={{ fontSize:12, color:"#4ade80", marginBottom:10, fontWeight:700 }}>
                {"\u2713"} Signed in as {user.email}
              </div>
            )}
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", marginBottom:20, lineHeight:1.6 }}>This is your handle across Bazooka. You only pick it once — after this it follows your account onto any device.</div>

            <div style={{ position:"relative", marginBottom:8 }}>
              <span style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", fontSize:16, color:"#999", fontWeight:700 }}>@</span>
              <input autoFocus value={username} onChange={e=>setUsername(clean(e.target.value))} placeholder="yourhandle" maxLength={20}
                style={{...inp, width:"100%", paddingLeft:32, fontSize:16, boxSizing:"border-box",
                  borderColor: status==="available"?"#4ade80":status==="taken"?"#EF4444":undefined }}/>
            </div>
            <div style={{ minHeight:20, fontSize:12, fontWeight:700, marginBottom:18 }}>
              {checking && <span style={{ color:"var(--bz-ink-2)" }}>Checking…</span>}
              {!checking && status==="available" && <span style={{ color:"#4ade80" }}>✓ @{clean(username)} is available</span>}
              {!checking && status==="mine" && <span style={{ color:"#4ade80" }}>✓ @{clean(username)} is already yours — finish claiming it</span>}
              {!checking && status==="taken" && <span style={{ color:"#EF4444" }}>✗ That handle's taken — try another</span>}
              {!checking && status==="invalid" && <span style={{ color:"#FBBF24" }}>At least 3 characters (letters, numbers, _)</span>}
            </div>

            {(()=>{ const ok = status==="available"||status==="mine"; return (
            <button onClick={claimUsername} disabled={!ok||saving}
              style={{ width:"100%", background: ok?"linear-gradient(135deg,#E8317A,#7B2FF7)":"var(--bz-line-2)", color: ok?"#fff":"#666", border:"none", borderRadius:12, padding:"13px", fontSize:15, fontWeight:800, cursor: ok?"pointer":"not-allowed", fontFamily:"inherit" }}>
              {saving ? "Claiming…" : "Claim it →"}
            </button>
            ); })()}
          </>
        ) : (
          <>
            <div style={{ fontSize:22, fontWeight:900, color:"#fff", marginBottom:6 }}>Finish your profile</div>
            <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", marginBottom:20, lineHeight:1.6 }}>Add a photo and link your community handles — all optional, but they help people recognize you. You can change these anytime.</div>

            <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}>
              <label style={{ position:"relative", cursor: uploading?"wait":"pointer" }}>
                {photoURL
                  ? <img src={photoURL} alt="" style={{ width:96, height:96, borderRadius:"50%", objectFit:"cover", border:"3px solid #E8317A", boxShadow:"0 0 24px rgba(232,49,122,0.4)" }}/>
                  : <div style={{ width:96, height:96, borderRadius:"50%", background:"var(--bz-s2)", border:"2px dashed rgba(255,255,255,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:32, color:"var(--bz-ink-3)" }}>{uploading?"…":"📷"}</div>}
                <div style={{ position:"absolute", bottom:0, right:0, background:"#E8317A", borderRadius:"50%", width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, border:"2px solid var(--bz-s1)" }}>✎</div>
                <input type="file" accept="image/*" style={{ display:"none" }} disabled={uploading} onChange={e=>{const f=e.target.files?.[0]; if(f)uploadPic(f); e.target.value="";}}/>
              </label>
            </div>

            {/* Discord + Whatnot handles for credibility */}
            <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"14px 14px 4px", marginBottom:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <div style={{ fontSize:12, fontWeight:800, color:"rgba(255,255,255,0.6)", letterSpacing:0.5 }}>Link your community handles <span style={{fontWeight:600,color:"rgba(255,255,255,0.35)"}}>(optional)</span></div>
                {clean(username) && <button onClick={()=>{ setDiscordName(clean(username)); setWhatnotName(clean(username)); }} style={{ background:"rgba(123,156,255,0.12)", border:"1px solid rgba(123,156,255,0.3)", color:"#7B9CFF", borderRadius:7, padding:"4px 10px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>Same as @{clean(username)}</button>}
              </div>
              <div style={{ marginBottom:10 }}>
                <label style={{ fontSize:11, color:"#8b9cff", fontWeight:700, display:"block", marginBottom:4 }}>💬 Discord</label>
                <input value={discordName} onChange={e=>setDiscordName(e.target.value)} placeholder="your discord username" maxLength={40} style={{...inp, width:"100%", boxSizing:"border-box", fontSize:13}}/>
              </div>
              <div style={{ marginBottom:10 }}>
                <label style={{ fontSize:11, color:"#FBBF24", fontWeight:700, display:"block", marginBottom:4 }}>🛍️ Whatnot</label>
                <input value={whatnotName} onChange={e=>setWhatnotName(e.target.value)} placeholder="your whatnot username" maxLength={40} style={{...inp, width:"100%", boxSizing:"border-box", fontSize:13}}/>
              </div>
            </div>

            <button onClick={finish} style={{ width:"100%", background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff", border:"none", borderRadius:12, padding:"13px", fontSize:15, fontWeight:800, cursor:"pointer", fontFamily:"inherit", marginBottom:10 }}>
              {photoURL ? "All set — let's go 🚀" : "Finish"}
            </button>
            <button onClick={finish} style={{ width:"100%", background:"transparent", border:"none", color:"rgba(255,255,255,0.4)", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
              Skip for now
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const LB_TIERS = [[0,"Rookie","#7B9CFF"],[25,"Collector","#4ade80"],[100,"Veteran","#FBBF24"],[300,"Elite","#E8317A"],[750,"Legend","#A855F7"],[1500,"Hall of Fame","#FFD700"]];
function lbTier(count){ return [...LB_TIERS].reverse().find(t=>count>=t[0]) || LB_TIERS[0]; }

function Leaderboard({ user, marketSales=[], onViewProfile }) {
  const [rows, setRows] = useState(null);
  const [cat, setCat] = useState("collectionCount");
  const [userSearch, setUserSearch] = useState("");
  const CATS = [
    { key:"collectionCount", label:"📦 Total Cards" },
    { key:"uniqueCount",     label:"🃏 Unique Cards" },
    { key:"rainbowCount",    label:"🌈 Rainbows" },
    { key:"oneOfOneCount",   label:"💎 1/1s" },
    { key:"deals",           label:"🤝 Deals Done" },
  ];

  // Fetch the user list ONCE, not on every marketSales change.
  //
  // This reads every doc in /users with no limit. It used to depend on [marketSales] — a live
  // Firestore listener — so every marketplace event refetched the entire user collection, and the
  // tab sat blank for as long as that took. The deal counts are derived from marketSales, but that
  // is a cheap local tally: it does not need a refetch, so it is computed separately below.
  const [loadingLb, setLoadingLb] = useState(true);
  const [lbError,   setLbError]   = useState("");   // surface the real reason instead of a blank tab
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingLb(true);
      try {
        const snap = await getDocs(collection(db,"users"));
        const list = snap.docs.map(d => { const x=d.data(); return {
          uid: d.id,
          name: x.username ? `@${x.username}` : (x.displayName ? x.displayName.split(" ")[0] : "Collector"),
          photoURL: x.photoURL || "",
          collectionCount: x.collectionCount||0,
          uniqueCount: x.uniqueCount||0,
          rainbowCount: x.rainbowCount||0,
          oneOfOneCount: x.oneOfOneCount||0,
        }; });
        if (alive) setRows(list);
      } catch(e) { console.error("leaderboard load failed:", e); if(alive){ setRows([]); setLbError(e.code || e.message || String(e)); } }
      if (alive) setLoadingLb(false);
    })();
    return () => { alive = false; };
  }, []);

  // Deal counts come from marketSales, which updates live. Tally locally instead of refetching.
  const dealCounts = useMemo(() => {
    const m = {};
    marketSales.forEach(s => { if(s.sellerUid) m[s.sellerUid] = (m[s.sellerUid]||0)+1; });
    return m;
  }, [marketSales]);

  const rowsWithDeals = useMemo(
    () => (rows||[]).map(r => ({ ...r, deals: dealCounts[r.uid]||0 })),
    [rows, dealCounts]
  );

  const ranked = rowsWithDeals.filter(r => (r[cat]||0) > 0).sort((a,b)=>(b[cat]||0)-(a[cat]||0));
  const top = ranked.slice(0,100);
  const myRank = user ? ranked.findIndex(r=>r.uid===user.uid) : -1;
  const myRow = user ? rowsWithDeals.find(r=>r.uid===user.uid) : null;
  const medal = (i)=> i===0?"🥇":i===1?"🥈":i===2?"🥉":`${i+1}`;

  // Show something while the user list loads. Previously `rows` began as null with no loading
  // state, so the whole tab rendered blank until the fetch finished — which read as "broken".
  if (loadingLb) return (
    <div style={{ maxWidth:720, margin:"0 auto", padding:"60px 20px", textAlign:"center" }}>
      <div style={{ fontSize:28, marginBottom:12 }}>{"\uD83C\uDFC6"}</div>
      <div style={{ fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.7)" }}>Loading the leaderboard{"\u2026"}</div>
    </div>
  );

  return (
    <div style={{ maxWidth:720, margin:"0 auto" }}>
      <div style={{ fontSize:22, fontWeight:900, color:"#fff", marginBottom:4 }}>🏆 Leaderboard</div>
      <div style={{ fontSize:13, color:"rgba(255,255,255,0.45)", marginBottom:18 }}>The top collectors in the Bazooka community. Climb the ranks.</div>

      {/* Username search — find friends and view their profile */}
      <div style={{ position:"relative", marginBottom:18 }}>
        <input
          value={userSearch}
          onChange={e=>setUserSearch(e.target.value)}
          placeholder="🔍 Search collectors by username…"
          style={{ width:"100%", background:"#241820", border:"1px solid rgba(255,255,255,0.18)", borderRadius:12, color:"#f6eef2", padding:"11px 16px", fontSize:14, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}/>
        {userSearch.trim() && (() => {
          const q = userSearch.trim().toLowerCase().replace(/^@/,"");
          // Fuzzy match: exact > prefix > substring > subsequence (chars in order).
          const fuzzyScore = (name) => {
            const n = name.toLowerCase().replace(/^@/,"");
            if (n === q) return 100;
            if (n.startsWith(q)) return 80;
            if (n.includes(q)) return 60;
            // subsequence: all query chars appear in order
            let qi = 0;
            for (let i = 0; i < n.length && qi < q.length; i++) { if (n[i] === q[qi]) qi++; }
            return qi === q.length ? 30 : 0;
          };
          const matches = (rows||[])
            .map(r => ({ r, score: fuzzyScore(r.name) }))
            .filter(x => x.score > 0)
            .sort((a,b) => b.score - a.score || (b.r.collectionCount||0) - (a.r.collectionCount||0))
            .slice(0,12)
            .map(x => x.r);
          return (
            <div style={{ marginTop:8, background:"#1a1218", border:"1px solid rgba(255,255,255,0.12)", borderRadius:12, overflow:"hidden" }}>
              {matches.length === 0
                ? <div style={{ padding:"16px", textAlign:"center", color:"rgba(255,255,255,0.4)", fontSize:13 }}>No collectors found matching "{userSearch}"</div>
                : matches.map(r => (
                  <div key={r.uid} onClick={()=>{ onViewProfile&&onViewProfile(r.uid); setUserSearch(""); }} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", cursor:"pointer", borderBottom:"1px solid rgba(255,255,255,0.05)" }} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    {r.photoURL
                      ? <img src={r.photoURL} alt="" style={{ width:34, height:34, borderRadius:"50%", objectFit:"cover" }}/>
                      : <div style={{ width:34, height:34, borderRadius:"50%", background:"#222", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15 }}>🃏</div>}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:14, fontWeight:800, color:"#fff" }}>{r.name}</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>{(r.collectionCount||0).toLocaleString()} cards</div>
                    </div>
                    <span style={{ fontSize:11, color:"#E8317A", fontWeight:700 }}>View →</span>
                  </div>
                ))}
            </div>
          );
        })()}
      </div>

      {/* Category toggle */}
      <div className="nav-bar" style={{ display:"flex", gap:8, marginBottom:18, overflowX:"auto", paddingBottom:4 }}>
        {CATS.map(c=>(
          <button key={c.key} onClick={()=>setCat(c.key)} style={{ whiteSpace:"nowrap", background:cat===c.key?"rgba(232,49,122,0.15)":"transparent", color:cat===c.key?"#E8317A":"rgba(255,255,255,0.5)", border:`1px solid ${cat===c.key?"#E8317A":"rgba(255,255,255,0.12)"}`, borderRadius:20, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{c.label}</button>
        ))}
      </div>

      {!rows ? (
        <div style={{ textAlign:"center", padding:60, color:"#999" }}>Loading rankings…</div>
      ) : top.length===0 ? (
        <div style={{ textAlign:"center", padding:60, color:"#999" }}>No rankings yet in this category. Be the first!</div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          {top.map((r,i)=>{
            const isMe = user && r.uid===user.uid;
            const tier = lbTier(r.collectionCount);
            return (
              <div key={r.uid} onClick={()=>onViewProfile&&onViewProfile(r.uid)} style={{ display:"flex", alignItems:"center", gap:12, background:isMe?"rgba(232,49,122,0.1)":"var(--bz-s1)", border:`1px solid ${isMe?"rgba(232,49,122,0.4)":"#222"}`, borderRadius:12, padding:"10px 14px", cursor:onViewProfile?"pointer":"default", transition:"background 0.15s" }} onMouseEnter={e=>{if(onViewProfile)e.currentTarget.style.background=isMe?"rgba(232,49,122,0.16)":"var(--bz-s2)";}} onMouseLeave={e=>{e.currentTarget.style.background=isMe?"rgba(232,49,122,0.1)":"var(--bz-s1)";}}>
                <div style={{ width:30, textAlign:"center", fontSize:i<3?20:14, fontWeight:900, color:i<3?"#FBBF24":"#666" }}>{medal(i)}</div>
                {r.photoURL
                  ? <img src={r.photoURL} alt="" style={{ width:36, height:36, borderRadius:"50%", objectFit:"cover", border:"1px solid var(--bz-line-2)" }}/>
                  : <div style={{ width:36, height:36, borderRadius:"50%", background:"#222", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>🃏</div>}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:800, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.name}{isMe&&<span style={{color:"#E8317A",fontSize:11,marginLeft:6}}>YOU</span>}</div>
                  <div style={{ fontSize:10, fontWeight:700, color:tier[2] }}>{tier[1]}</div>
                </div>
                <div style={{ fontSize:18, fontWeight:900, color:"#E8317A" }}>{(r[cat]||0).toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      )}
      {/* Your standing — always shown when signed in */}
      {user && rows && (
        myRank>=0 && myRank>=100 ? (
          <div style={{ marginTop:14, display:"flex", alignItems:"center", gap:12, background:"rgba(232,49,122,0.1)", border:"1px solid rgba(232,49,122,0.4)", borderRadius:12, padding:"10px 14px" }}>
            <div style={{ width:30, textAlign:"center", fontSize:14, fontWeight:900, color:"#E8317A" }}>{myRank+1}</div>
            <div style={{ flex:1, fontSize:14, fontWeight:800, color:"#fff" }}>{myRow?.name||"You"} <span style={{color:"#E8317A",fontSize:11,marginLeft:6}}>YOU</span></div>
            <div style={{ fontSize:18, fontWeight:900, color:"#E8317A" }}>{(myRow?.[cat]||0).toLocaleString()}</div>
          </div>
        ) : myRank===-1 ? (
          <div style={{ textAlign:"center", marginTop:16, fontSize:12, color:"#999" }}>You're not ranked in this category yet — start collecting to climb on. 🚀</div>
        ) : null
      )}
    </div>
  );
}

// Profile modal — click a name on the leaderboard/marketplace to view someone's
// public profile: collection count, review history, public rainbows, and
// friend/message actions.
function ProfileModal({ profileUid, onClose, currentUser, cards=[], onAddFriend, onMessage, isFriend, friendPending }) {
  const [data, setData] = useState(null);
  const [pubCards, setPubCards] = useState({});
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [uSnap, pubSnap, revSnap] = await Promise.all([
          getDoc(doc(db,"users",profileUid)),
          getDoc(doc(db,"boba_public",profileUid)),
          getDocs(query(collection(db,"boba_reviews"), where("sellerUid","==",profileUid))),
        ]);
        if (!alive) return;
        setData(uSnap.exists() ? uSnap.data() : {});
        setPubCards(pubSnap.exists() ? pubSnap.data() : {});
        setReviews(revSnap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||"")));
      } catch(e) { if(alive){ setData({}); } }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [profileUid]);

  const name = data?.username ? `@${data.username}` : (data?.displayName ? data.displayName.split(" ")[0] : "Collector");
  const photoURL = data?.photoURL || "";
  const collectionCount = data?.collectionCount || 0;
  const rainbowCount = data?.rainbowCount || 0;
  const oneOfOneCount = data?.oneOfOneCount || 0;
  const isSelf = currentUser && currentUser.uid === profileUid;
  const avgRating = reviews.length ? (reviews.reduce((s,r)=>s+(r.rating||0),0)/reviews.length) : 0;

  // Public cards grouped for a public "rainbow" style show — just card thumbnails/heroes owned publicly
  const pubCardIds = Object.keys(pubCards).filter(id=>pubCards[id]);
  const pubCardObjs = pubCardIds.map(id => cards.find(c=>c.id===id)).filter(Boolean);
  const pubByHero = {};
  pubCardObjs.forEach(c => { if(!c.hero) return; (pubByHero[c.hero]=pubByHero[c.hero]||[]).push(c); });

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:2147483600, display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"40px 16px", overflowY:"auto", backdropFilter:"blur(6px)" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"#130d11", border:"1px solid rgba(232,49,122,0.25)", borderRadius:18, maxWidth:540, width:"100%", padding:"24px", boxShadow:"0 20px 60px rgba(0,0,0,0.6)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            {photoURL
              ? <img src={photoURL} alt="" style={{ width:56, height:56, borderRadius:"50%", objectFit:"cover", border:"2px solid rgba(232,49,122,0.4)" }}/>
              : <div style={{ width:56, height:56, borderRadius:"50%", background:"linear-gradient(135deg,#E8317A,#7B2FF7)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, fontWeight:900, color:"#fff" }}>{name.replace("@","").charAt(0).toUpperCase()}</div>}
            <div>
              <div style={{ fontSize:20, fontWeight:900, color:"#fff" }}>{name}{isSelf && <span style={{ fontSize:11, color:"#4ade80", marginLeft:8 }}>(you)</span>}</div>
              {reviews.length > 0 && <div style={{ fontSize:12, color:"#FBBF24", marginTop:2 }}>{"★".repeat(Math.round(avgRating))}{"☆".repeat(5-Math.round(avgRating))} <span style={{ color:"rgba(255,255,255,0.4)" }}>({reviews.length} review{reviews.length!==1?"s":""})</span></div>}
            </div>
          </div>
          <button onClick={onClose} style={{ background:"transparent", border:"none", color:"rgba(255,255,255,0.5)", fontSize:22, cursor:"pointer", lineHeight:1 }}>×</button>
        </div>

        {loading ? (
          <div style={{ textAlign:"center", padding:"40px", color:"rgba(255,255,255,0.4)" }}>Loading profile…</div>
        ) : (
          <>
            {/* Stat row */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:18 }}>
              {[["Cards",collectionCount,"#E8317A"],["Rainbows",rainbowCount,"#FBBF24"],["1/1s",oneOfOneCount,"#C084FC"]].map(([l,v,c])=>(
                <div key={l} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:10, padding:"12px", textAlign:"center" }}>
                  <div style={{ fontSize:22, fontWeight:900, color:c }}>{v}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1 }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Actions */}
            {!isSelf && currentUser && (
              <div style={{ display:"flex", gap:10, marginBottom:18 }}>
                <button onClick={()=>onAddFriend(profileUid, data)} disabled={isFriend||friendPending} style={{ flex:1, background:isFriend?"rgba(74,222,128,0.15)":friendPending?"rgba(255,255,255,0.06)":"linear-gradient(135deg,#E8317A,#7B2FF7)", color:isFriend?"#4ade80":"#fff", border:"none", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:(isFriend||friendPending)?"default":"pointer", fontFamily:"inherit" }}>
                  {isFriend ? "✓ Friends" : friendPending ? "Request Sent" : "+ Add Friend"}
                </button>
                <button onClick={()=>onMessage(profileUid, data)} style={{ flex:1, background:"rgba(123,156,255,0.15)", color:"#7B9CFF", border:"1px solid rgba(123,156,255,0.4)", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>💬 Message</button>
              </div>
            )}

            {/* Public collection (rainbows they've made public) */}
            {pubCardObjs.length > 0 && (
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:11, fontWeight:800, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>Public Collection · {pubCardObjs.length} cards</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(64px,1fr))", gap:6, maxHeight:240, overflowY:"auto" }}>
                  {pubCardObjs.slice(0,60).map(c => (
                    <div key={c.id} title={[c.hero,c.treatment,c.weapon].filter(Boolean).join(" · ")} style={{ aspectRatio:"3/4", borderRadius:6, overflow:"hidden", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)" }}>
                      {c.imageUrl ? <img src={c.imageUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontSize:9, color:"rgba(255,255,255,0.4)", padding:2, textAlign:"center" }}>{c.hero}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Review history */}
            <div>
              <div style={{ fontSize:11, fontWeight:800, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>Review History</div>
              {reviews.length === 0 ? (
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.3)", fontStyle:"italic", padding:"8px 0" }}>No reviews yet.</div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:8, maxHeight:200, overflowY:"auto" }}>
                  {reviews.slice(0,20).map(r => (
                    <div key={r.id} style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:8, padding:"10px 12px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <span style={{ fontSize:12, color:"#FBBF24" }}>{"★".repeat(r.rating||0)}{"☆".repeat(5-(r.rating||0))}</span>
                        <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ""}</span>
                      </div>
                      {r.comment && <div style={{ fontSize:12, color:"rgba(255,255,255,0.7)", marginTop:4 }}>{r.comment}</div>}
                      {r.buyerName && <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginTop:3 }}>— {r.buyerName}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PublicCardDatabase({ swancity = false } = {}) {
  // -- Core state --
  const [toast, setToast] = useState(null);
  // Prominent in-app notification popup — slides in when a NEW alert arrives while you're using the app.
  const [notifPop, setNotifPop] = useState(null);   // { title, sub, image, view }
  const notifSeenRef = useRef(null);          // ids we've already popped (null = not primed yet)
  // Auto-dismiss. There are a dozen setToast() calls in here and NOT ONE of them ever cleared it,
  // so any toast simply stayed on screen forever \u2014 covering the buttons underneath it. One effect
  // here beats remembering a setTimeout at every call site (which is how it got missed).
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);
  const [fanDeck, setFanDeck] = useState(null); // {name, cards:[cardObjs]} for hand-fan view
  const [fanMode, setFanMode] = useState("grid"); // "grid" | "fan"
  const [fanGroup, setFanGroup] = useState("none"); // "none" | "power" | "insert"
  const [cardSize, setCardSize] = useState(200); // grid card min-width in px, adjustable via slider
  const showToast = (msg) => { try { setToast(msg); setTimeout(()=>{ try{setToast(null);}catch(e){} }, 3500); } catch(e){} };


  const [cards,         setCards]         = useState(()=>{ try { const r=localStorage.getItem("boba_checklist_cache_v3"); if(r){const{cards:cc}=JSON.parse(r);if(cc?.length>0)return cc;} } catch(e){} return []; });
  const [foundInMap,    setFoundInMap]    = useState(()=>{ try { const r=localStorage.getItem("found_in_v1"); return r?JSON.parse(r):{}; } catch(e){ return {}; } }); // live {set|treatment: [packIds]}
  const [dbsOverrides,  setDbsOverrides]  = useState(()=>{ try { const r=localStorage.getItem("dbs_overrides_v1"); return r?JSON.parse(r):{}; } catch(e){ return {}; } }); // live {cardId: dbs} — instant DBS updates for everyone
  const [loading, setLoading] = useState(()=>{ try { const r=localStorage.getItem("boba_checklist_cache_v3"); if(r){const{cards:cc}=JSON.parse(r);if(cc?.length>0)return false;} } catch(e){} return true; });
  const [user,          setUser]          = useState(null);
  const [earlyAccessList, setEarlyAccessList] = useState([]);
  const [eaInput, setEaInput] = useState("");
  const [earlyAccessModal, setEarlyAccessModal] = useState(false);
  const [eaCopied, setEaCopied] = useState("");
  function buildInviteMessage(em) {
    return `You've got early access to Bazooka Dash! 🎉

Head to bazookadash.com and sign in with this email (${em}) — you'll be able to track your BoBA collection, build rainbows, and more.

See you in there!
— Bazooka`;
  }
  function copyInvite(em) {
    const msg = buildInviteMessage(em);
    try {
      navigator.clipboard.writeText(msg).then(()=>{ setEaCopied(em); setTimeout(()=>setEaCopied(""),2000); });
    } catch(e) {
      // Fallback for older browsers
      const ta=document.createElement("textarea"); ta.value=msg; document.body.appendChild(ta); ta.select();
      try{document.execCommand("copy"); setEaCopied(em); setTimeout(()=>setEaCopied(""),2000);}catch(_){}
      document.body.removeChild(ta);
    }
  }
  async function addEarlyAccess() {
    const em = eaInput.toLowerCase().trim();
    if (!em || !em.includes("@")) { alert("Enter a valid email."); return; }
    if (earlyAccessList.includes(em)) { setEaInput(""); return; }
    const next = [...earlyAccessList, em];
    try { await setDoc(doc(db,"config","early_access"), { emails: next }, { merge:true }); setEaInput(""); copyInvite(em); } catch(e){ alert("Failed to save: "+e.message); }
  }
  async function removeEarlyAccess(em) {
    const next = earlyAccessList.filter(x=>x!==em);
    try { await setDoc(doc(db,"config","early_access"), { emails: next }, { merge:true }); } catch(e){ alert("Failed to remove: "+e.message); }
  }
  const [owned,         setOwned]         = useState(()=>{ try { const c=localStorage.getItem("boba_owned_cache_v1"); if(c){ const p=JSON.parse(c); return p&&p.owned?p.owned:{}; } } catch(e){} return {}; });
  const [publicCards,   setPublicCards]   = useState({});
  const [trackerAutoPublic, setTrackerAutoPublic] = useState(() => { try { const c=localStorage.getItem("trackerAutoPublic_v1"); return c?JSON.parse(c):{}; } catch { return {}; } }); // cards made public because a tracker covering them is public
  const [tradeBait,     setTradeBait]     = useState({}); // {cardId: true} manually flagged for trade

  // Cards you own but won't play with \u2014 typically graded slabs. Flagged here so the deck list can
  // show them as proxies and IMC can be sent a print list. Same shape as tradeBait: a simple
  // {cardId:true} map in its own doc, so a flag survives independently of quantity or lots.
  const [proxyCards,    setProxyCards]    = useState({});
  const [lots,          setLots]          = useState([]); // [{id,cardId,cost,value,method,date,notes}]

  const [foilDogs, setFoilDogs] = useState(0);  // Foil Hot Dog count for Madness (manual, 0-4)
  // A borrowed card is in your hands but is NOT yours. It must not count toward owned totals, set
  // completion, or vault value \u2014 otherwise the collection reports someone else's cards as assets.
  // `owned` stays the raw physical count (what is in the box); this is the ownership-adjusted view
  // that anything financial or completion-related should use.
  const borrowedByCard = useMemo(() => {
    const m = {};
    (lots||[]).forEach(l => { if (l?.cardId && l.lendState === "borrowed") m[l.cardId] = (m[l.cardId]||0) + 1; });
    return m;
  }, [lots]);
  // Cards I've lent out: count per card, plus who has the most recent one (for the
  // card badge tooltip). Drives the one-click lend/return button on the card face.
  const lentByCard = useMemo(() => {
    const m = {};
    (lots||[]).forEach(l => { if (l?.cardId && l.lendState === "lent") m[l.cardId] = (m[l.cardId]||0) + 1; });
    return m;
  }, [lots]);
  const lentWhoByCard = useMemo(() => {
    const m = {};
    (lots||[]).forEach(l => {
      if (l?.cardId && l.lendState === "lent") {
        if (!m[l.cardId] || (l.lendDate||"") >= (m[l.cardId].date||"")) m[l.cardId] = { who: l.lendWho||"", date: l.lendDate||"" };
      }
    });
    return m;
  }, [lots]);
  const ownedNet = useMemo(() => {
    if (!Object.keys(borrowedByCard).length) return owned || {};
    const out = { ...(owned||{}) };
    Object.entries(borrowedByCard).forEach(([id, n]) => {
      const left = (parseInt(out[id]) || 0) - n;
      if (left > 0) out[id] = left; else delete out[id];
    });
    return out;
  }, [owned, borrowedByCard]);
  const [myReviews,     setMyReviews]     = useState([]); // reviews this buyer has left (by saleId)
  const [myUsername,    setMyUsername]    = useState("");
  const usernameClaimedThisSession = useRef(false);
  const [myPhotoURL,    setMyPhotoURL]    = useState("");
  const [myDiscord,     setMyDiscord]     = useState("");
  const [myWhatnot,     setMyWhatnot]     = useState("");
  const [onboarding,    setOnboarding]    = useState(false);
  const [reviewModal,   setReviewModal]   = useState(null); // { sale } when rating a seller
  const [lotModal,      setLotModal]      = useState(null); // { card } when open
  // Asked when adding a card that exists in more than one print (see CARD_VERSIONS).
  const [versionAsk,    setVersionAsk]    = useState(null); // { card, spec, qty }
  const [ownedDocId,    setOwnedDocId]    = useState(null);
  // Cache the owned collection locally so it paints INSTANTLY on next load instead of
  // waiting for Firestore. Keyed by uid so a different account never sees stale data.
  useEffect(()=>{
    if(!user) return;
    try { localStorage.setItem("boba_owned_cache_v1", JSON.stringify({ uid:user.uid, owned })); } catch(e){}
  }, [owned, user]);
  // On sign-in, if the cached collection belongs to a different account, drop it immediately.
  useEffect(()=>{
    if(!user) return;
    try { const c=localStorage.getItem("boba_owned_cache_v1"); if(c){ const p=JSON.parse(c); if(p && p.uid && p.uid!==user.uid){ setOwned({}); localStorage.removeItem("boba_owned_cache_v1"); } } } catch(e){}
  }, [user]);
  const [signingIn,     setSigningIn]     = useState(false);
  // -- UI state persistence (per browser session) --
  const UI_STATE_KEY = "bazooka_vault_ui_v1";
  const loadUI = () => { try { return JSON.parse(sessionStorage.getItem(UI_STATE_KEY)||"{}"); } catch(e) { return {}; } };
  const savedUI = (typeof window !== "undefined") ? loadUI() : {};
  const VALID_TABS = ["cards","rainbow","supers","1of1","bojax34","wants","tradebait","intransit","deck","playbook","arena","market","messages","friends","team","ledger","leaderboard"];
  const [activeTab,     setActiveTab]     = useState(()=>{ if(swancity) return "supers"; const p=(window.location.pathname||"").toLowerCase(); const PATH_TO_TAB={ "/cards":"cards","/rainbow":"rainbow","/supers":"supers","/1of1":"1of1","/34":"bojax34","/wants":"wants","/tradebait":"tradebait","/market":"market","/messages":"messages","/friends":"friends","/team":"team","/ledger":"ledger","/leaderboard":"leaderboard" }; if(PATH_TO_TAB[p]) return PATH_TO_TAB[p]; const h=(window.location.hash||"").replace("#","").trim(); if(VALID_TABS.includes(h)) return h; if(savedUI.activeTab && VALID_TABS.includes(savedUI.activeTab)) return savedUI.activeTab; return "cards"; });
  const [headerLoaded,  setHeaderLoaded]  = useState(false);
  const [windowWidth,   setWindowWidth]   = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize, { passive:true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = windowWidth < 768;

  // -- Cards/filter state --
  const [search,        setSearch]        = useState(savedUI.search ?? "");
  // PERF: `search` drives a filter+sort over the full 31k-card checklist. Running that on every
  // keystroke makes the input stutter — you type and the characters lag behind. So the text field
  // is bound to `search` (instant, no lag) while the actual filtering keys off `searchDebounced`,
  // which settles ~180ms after you stop typing.
  const [searchDebounced, setSearchDebounced] = useState(savedUI.search ?? "");
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 60);
    return () => clearTimeout(t);
  }, [search]);
  const debouncedSearch = useDebounce(search, 250);
  // Set / weapon / treatment are MULTI-SELECT now, the way filterPower already was.
  // Empty Set = no filter. savedUI may hold an old single string, so tolerate both shapes.
  const [filterSet,      setFilterSet]      = useState(()=> new Set(Array.isArray(savedUI.filterSet) ? savedUI.filterSet : (savedUI.filterSet ? [savedUI.filterSet] : [])));
  const [filterSubSet,  setFilterSubSet]  = useState(""); // e.g. L.A. / Philadelphia / Oklahoma City within a set
  const [filterWeapon,   setFilterWeapon]   = useState(()=> new Set(Array.isArray(savedUI.filterWeapon) ? savedUI.filterWeapon : (savedUI.filterWeapon ? [savedUI.filterWeapon] : [])));
  const [filterTreat,    setFilterTreat]    = useState(()=> new Set(Array.isArray(savedUI.filterTreat) ? savedUI.filterTreat : (savedUI.filterTreat ? [savedUI.filterTreat] : [])));
  const [filterPower,   setFilterPower]   = useState(()=> new Set(Array.isArray(savedUI.filterPower) ? savedUI.filterPower : []));
  const [powerMenuOpen, setPowerMenuOpen] = useState(false);
  const [multiOpen,     setMultiOpen]     = useState(null); // "set" | "treat" | "weapon" | null
  const filterBarRef = useRef(null);   // for click-outside to close filter dropdowns
  // Close the set/treatment/weapon/power filter dropdowns when clicking anywhere
  // outside the filter bar — so you can go straight to searching or the grid without
  // having to click the same filter button again to dismiss it.
  useEffect(() => {
    if (!multiOpen && !powerMenuOpen) return;
    const onDown = (e) => {
      if (filterBarRef.current && !filterBarRef.current.contains(e.target)) {
        setMultiOpen(null); setPowerMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [multiOpen, powerMenuOpen]);
  const [navMenu,       setNavMenu]       = useState(null); // "collect" | "play" | null (hover dropdowns)
  const navGroupItems = useRef({});
  const [msgPanelOpen,  setMsgPanelOpen]  = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editPicUploading, setEditPicUploading] = useState(false);
  const [filterOwned,   setFilterOwned]   = useState(savedUI.filterOwned ?? "all");
  const [cardView,      setCardView]      = useState(savedUI.cardView ?? "grid"); // "grid" | "list"
  const [animsOn,       setAnimsOn]       = useState(savedUI.animsOn ?? true); // card add/entrance animations
  const [tourStep,      setTourStep]      = useState(-1); // -1 = not running
  const [superRecord,   setSuperRecord]   = useState(null); // admin: manually record a super/1of1 hit {card,type}
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [superRecordName, setSuperRecordName] = useState("");
  const [superRecordPhoto, setSuperRecordPhoto] = useState(null); // {file, preview}
  const [superRecordUploading, setSuperRecordUploading] = useState(false);
  const [filterNoImg,   setFilterNoImg]   = useState(false); // admin: show only cards missing an image
  const [sortBy,        setSortBy]        = useState(savedUI.sortBy ?? "cardNum");
  const [page,          setPage]          = useState(savedUI.page ?? 1);
  const [flippedCard,   setFlippedCard]   = useState(null);
  const [expandedCard,  setExpandedCard]  = useState(null); // card object shown in big modal
  const [cardEditMode,  setCardEditMode]  = useState(false); // admin editing card fields
  const [cardEditDraft, setCardEditDraft] = useState({});
  const [resubmitClaim,   setResubmitClaim]   = useState(null); // {coll, card} for re-uploading a better claim photo
  const [resubmitting,    setResubmitting]    = useState(false);
  const [modalFoilView, setModalFoilView] = useState("paper"); // "paper" | "foil" for dual-treatment cards
  const [myPhotoIdx,    setMyPhotoIdx]    = useState(0); // which of my own scan photos is showing in the modal
  const [viewMyScan,    setViewMyScan]    = useState(false); // in the modal, show my scanned copy instead of the official art
  useEffect(()=>{ setModalFoilView("paper"); setCardEditMode(false); setMyPhotoIdx(0); setViewMyScan(false); }, [expandedCard]); // reset toggle each time a card opens

  // -- Auto flip a card back to front ~6s after last interaction with the flipped card --
  const flipTimerRef = useRef(null);
  const resetFlipTimer = () => {
    if (flipTimerRef.current) clearTimeout(flipTimerRef.current);
    flipTimerRef.current = setTimeout(() => setFlippedCard(null), 6000);
  };
  useEffect(() => {
    if (!flippedCard) { if (flipTimerRef.current) clearTimeout(flipTimerRef.current); return; }
    resetFlipTimer();
    return () => { if (flipTimerRef.current) clearTimeout(flipTimerRef.current); };
  }, [flippedCard]);

  // -- Keep the URL in sync with the active tab (flat top-level URLs e.g. /supers) --
  // Tabs that get their own flat path. deck/playbook are excluded (they have standalone pages).
  const TAB_PATHS = { cards:"/cards", rainbow:"/rainbow", supers:"/supers", "1of1":"/1of1", bojax34:"/34", wants:"/wants", tradebait:"/tradebait", intransit:"/intransit", market:"/market", messages:"/messages", friends:"/friends", team:"/team", ledger:"/ledger", leaderboard:"/leaderboard", fantasy:"/fantasy" };
  useEffect(() => {
    if (swancity) return; // swancity stays on /swancity, tabs don't rewrite the URL
    const target = TAB_PATHS[activeTab] || "/cards";
    if (window.location.pathname !== target) {
      window.history.pushState({ tab: activeTab }, "", target);
    }
  }, [activeTab]);
  useEffect(() => {
    const onPop = () => {
      const p = window.location.pathname || "/cards";
      const found = Object.keys(TAB_PATHS).find(k => TAB_PATHS[k] === p);
      setActiveTab(found || "cards");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // -- Persist UI state to sessionStorage whenever it changes --
  useEffect(() => {
    try {
      const prev = loadUI();
      sessionStorage.setItem(UI_STATE_KEY, JSON.stringify({
        ...prev,
        activeTab, search,
        // Sets don't survive JSON.stringify — serialize like filterPower already did.
        filterSet:    Array.from(filterSet),
        filterWeapon: Array.from(filterWeapon),
        filterTreat:  Array.from(filterTreat),
        filterPower:  Array.from(filterPower), filterOwned, sortBy, page, cardView, animsOn,
      }));
    } catch(e) {}
  }, [activeTab, search, filterSet, filterWeapon, filterTreat, filterPower, filterOwned, sortBy, page, cardView, animsOn]);

  // -- Save scroll position continuously, restore once after cards render --
  useEffect(() => {
    const saveScroll = () => { try { const u = loadUI(); u.scrollY = window.scrollY; sessionStorage.setItem(UI_STATE_KEY, JSON.stringify(u)); } catch(e) {} };
    window.addEventListener("scroll", saveScroll, { passive:true });
    return () => window.removeEventListener("scroll", saveScroll);
  }, []);
  // Hold the scroll position across a filter change. Narrowing the filters resets pagination to page
  // 1, which shrinks the rendered list \u2014 the page then becomes shorter than the current scroll offset
  // and the browser snaps to the top, losing your place in a 36k-card grid. Re-applying the offset
  // after paint keeps you where you were whenever the page is still tall enough to hold it.
  const keepScrollRef = useRef(null);
  const preserveScroll = () => { keepScrollRef.current = window.scrollY; };
  useLayoutEffect(() => {
    const y = keepScrollRef.current;
    if (y == null) return;
    keepScrollRef.current = null;
    requestAnimationFrame(() => {
      // Only restore if the content still reaches that far; otherwise sit at the new bottom rather
      // than fighting the browser for a position that no longer exists.
      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      window.scrollTo(0, Math.min(y, max));
    });
  });
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (scrollRestoredRef.current) return;
    if (loading) return;
    const y = savedUI.scrollY;
    if (typeof y === "number" && y > 0) {
      requestAnimationFrame(() => { requestAnimationFrame(() => { window.scrollTo(0, y); }); });
    }
    scrollRestoredRef.current = true;
  }, [loading]);
  const PAGE_SIZE = 100;
  // Never paint more than this many pages. Without a ceiling, scrolling mounts every one of the
  // 35k cards and never unmounts them — every later state change then re-renders the whole lot,
  // which is what took ~6 SECONDS per render.
  //
  // Even capped, ~1,500 cards was still ~850ms per render — that much DOM (and that many images)
  // is simply heavy no matter how well memoized. 600 keeps interactions snappy, and nobody finds a
  // card by scrolling past 600 anyway; they filter. The cap notice tells them so.
  // A CEILING, not a wall. Mounting all 35k cards at once took render from 15ms to 5,808ms, so
  // the default stops at 600. But "you may not see more" is a hostile answer — so the Load More
  // button below raises this, and the user decides whether the slowdown is worth it. Their call.
  const [maxPages, setMaxPages] = useState(6);
  const MAX_PAGES = maxPages;
  // Drop the ceiling back to its default whenever the result set changes. Otherwise "Show all"
  // on a 40-card filter would silently stay in force when you clear that filter — and the app
  // would then try to paint all 35k, which is the exact thing the cap exists to prevent.
  useEffect(() => { setMaxPages(6); }, [search, filterSet, filterWeapon, filterTreat, filterPower, filterOwned, filterSubSet, sortBy]);

  // -- Rainbow Tracker --
  const [rainbowFilter,    setRainbowFilter]    = useState("all");
  const [rainbowSetFilter, setRainbowSetFilter] = useState("");
  const [rainbowGroupBy,   setRainbowGroupBy]   = useState("hero"); // hero | treatment | treatmentWeapon | custom
  const [customTrackers,   setCustomTrackers]   = useState(() => { try { const c = localStorage.getItem("customTrackers_v1"); return c ? JSON.parse(c) : []; } catch { return []; } });
  const [activeTrackerId,  setActiveTrackerId]  = useState(null);
  const [showTrackerBuilder, setShowTrackerBuilder] = useState(false);
  const [builderName,      setBuilderName]      = useState("");
  const [builderTreatments,setBuilderTreatments]= useState([]);
  const [expandedTrackerHero, setExpandedTrackerHero] = useState(null);
  const [trackerStatusFilter, setTrackerStatusFilter] = useState("all"); // all | owned | transit | missing
  const [zoomCard, setZoomCard] = useState(null);
  const [zoomFlipped, setZoomFlipped] = useState(false);
  const [expandedHero,     setExpandedHero]     = useState(null);
  const [treatOwnedFilter, setTreatOwnedFilter] = useState("all");
  const [superSetFilter,   setSuperSetFilter]   = useState("");
  const [secret1SetFilter, setSecret1SetFilter] = useState("");
  const [secret1TreatFilter, setSecret1TreatFilter] = useState({}); // { [setName]: treatment }
  const [superStatusFilter,  setSuperStatusFilter]  = useState("all"); // all | claimed | unclaimed
  const [secret1StatusFilter,setSecret1StatusFilter]= useState("all"); // all | claimed | unclaimed
  const [secret1GroupBy,     setSecret1GroupBy]     = useState("set"); // set | treatment | weapon | hero

  // -- Custom Rainbows --
  const [rbName,              setRbName]              = useState("");
  const [rbTreatment,         setRbTreatment]         = useState("");
  const [rbWeapon,            setRbWeapon]            = useState("");
  const [rbSet,               setRbSet]               = useState("");
  const [rbHero,              setRbHero]              = useState("");
  const [rbSaving,            setRbSaving]            = useState(false);

  // -- Wants --
  const [wantList,      setWantList]      = useState({});

  // ── KID COLLECTIONS ────────────────────────────────────────────────────────────────────────
  // Cards you own can be tagged to a kid's collection. They're still YOUR cards (owned counts and
  // deck building are unchanged) — the tag just records whose binder a card lives in.
  //   groups: [{ id, name, color }]      assign: { [cardId]: groupId }
  const [kidGroups, setKidGroups] = useState([]);
  const [kidAssign, setKidAssign] = useState({});
  const [kidFilter, setKidFilter] = useState("all"); // "all" | "mine" | <groupId>
  const [kidsModal, setKidsModal] = useState(false);
  const [sellModal, setSellModal] = useState(null);   // the card being sold/traded
  const [soldView, setSoldView] = useState(false);    // the "formerly owned" list
  const [soldSearch, setSoldSearch] = useState("");   // filter Formerly-owned by partner name / card

  // ── FORMERLY OWNED (sold / traded / gifted) ────────────────────────────────────────────────
  // Selling a card decrements the owned quantity (2 → 1) rather than deleting it, and writes a
  // ledger entry. Sold cards do NOT count toward your active collection — but the record survives,
  // so "did I ever have that Gronk?" is always answerable.
  //   [{ id, cardId, qty, reason, note, price, date }]
  const [soldLog, setSoldLog] = useState([]);

  // Save plumbing for the owned map. Declared up here because the boba_owned snapshot listener
  // (set up in the auth effect below) needs to know whether a local write is still pending.
  const ownedSaveRef    = useRef(null);   // debounce timer
  const pendingOwnedRef = useRef(null);   // the map waiting to be written
  const flushingRef     = useRef(false);  // a write is in flight right now
  const [inTransit,     setInTransit]     = useState({}); // {cardId: {qty, note, date}} cards bought & on the way
  const transitLoadedRef = useRef(false);   // true once boba_intransit has loaded at least once; blocks writes until then so a write can't overwrite the doc with an empty/stale map

  // Live listener for on-the-way cards. The initial load uses a one-time getDoc (batched with the
  // rest of the collection), which means a later write \u2014 e.g. the integrity heal re-pointing drifted
  // transit ids \u2014 wouldn't show until a full reload. This keeps inTransit in sync with the doc.
  useEffect(() => {
    if (!user) { setInTransit({}); return; }
    const unsub = onSnapshot(doc(db,"boba_intransit",user.uid),
      snap => { transitLoadedRef.current = true; setInTransit(snap.exists() ? snap.data() : {}); },
      e => console.error("intransit listen failed:", e));
    return () => { try{unsub();}catch(e){} };
  }, [user]);


  // -- Card FX (hunt / caught animations) --
  const [cardFx,        setCardFx]        = useState(null); // { type:"hunt"|"caught", card }
  const [milestone,     setMilestone]     = useState(null); // { label, sub }
  const lastMilestoneRef = useRef(null);
  const [rainbowFx,     setRainbowFx]     = useState(null); // { name, cards:[] }
  const completedRainbowsRef = useRef(null); // snapshot of complete rainbow keys
  const ownedUnsubRef = useRef(null); // live subscription to the user's collection

  // -- Playbook --
  const [pbCards,       setPbCards]       = useState([]);
  const [pbSearch,      setPbSearch]      = useState("");
  const [pbSort,        setPbSort]        = useState("name");
  const [pbName,        setPbName]        = useState("My Playbook");
  const [savedPlaybooks,setSavedPlaybooks]= useState([]);
  const [pbLoadId,      setPbLoadId]      = useState(null);
  const [pbSaving,      setPbSaving]      = useState(false);
  const [pbSaved,       setPbSaved]       = useState(false);
  useEffect(() => {
    if (!user) { setSavedPlaybooks([]); return; }
    const unsub = onSnapshot(query(collection(db, "boba_playbooks"), where("userId","==",user.uid)), snap => {
      setSavedPlaybooks(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.savedAt||"").localeCompare(a.savedAt||"")));
    }, e=>console.error("load playbooks failed:", e));
    return unsub;
  }, [user]);
  async function savePbTab() {
    if (!pbName.trim() || pbCards.length === 0) { alert("Name your playbook and add at least one card before saving."); return; }
    if (!user) { setSigningIn(true); return; }
    setPbSaving(true);
    const id = pbLoadId || `pb_${Date.now()}`;
    const plays = pbCards.filter(e=>e.type==="play").length;
    const bonus = pbCards.filter(e=>e.type==="bonus").length;
    try {
      await setDoc(doc(db,"boba_playbooks",id), { id, userId: user.uid, name: pbName.trim(), entries: pbCards, playCount: plays, bonusCount: bonus, savedAt: new Date().toISOString() }, { merge:true });
      setPbLoadId(id); setPbSaved(true); setTimeout(()=>setPbSaved(false), 1800);
    } catch(e) { console.error("save playbook failed:", e); alert("Couldn't save your playbook: " + (e?.message || e)); }
    finally { setPbSaving(false); }
  }
  async function deletePbTab(id) {
    if (!window.confirm("Delete this playbook?")) return;
    try { await deleteDoc(doc(db,"boba_playbooks",id)); } catch(e){ alert("Delete failed: "+(e?.message||e)); return; }
    if (pbLoadId === id) { setPbLoadId(null); setPbName("My Playbook"); setPbCards([]); }
  }
  function loadPbTab(p) { setPbLoadId(p.id); setPbName(p.name||"My Playbook"); setPbCards(p.entries||[]); }
  function newPbTab() { setPbLoadId(null); setPbName("My Playbook"); setPbCards([]); }

  // -- Scan --
  const [scanModal,     setScanModal]     = useState(false);
  const [importModal,   setImportModal]   = useState(false);
  const [missingCardsModal, setMissingCardsModal] = useState(false);
  const [missingCards,  setMissingCards]  = useState([]);
  const [userMissing,   setUserMissing]   = useState([]); // this collector's own unmatched imports
  const [userMissingModal, setUserMissingModal] = useState(false);
  const [importRows,    setImportRows]    = useState(null); // parsed+matched rows for preview
  const [importRaw,     setImportRaw]     = useState(null); // { hdr, records } before set mapping
  const [importSetMap,  setImportSetMap]  = useState({});   // { csvSetName: yourSetName }
  const [importColMap,  setImportColMap]  = useState(null); // { Name:idx, Set:idx, "Card Number":idx, Parallel:idx, Weapon:idx, Power:idx, Quantity:idx, "Estimated Value":idx }
  const [colMapConfirmed, setColMapConfirmed] = useState(false);
  const [importing,     setImporting]     = useState(false);
  const [photoScan,     setPhotoScan]     = useState(null);
  // Multi-card (binder page) scan: one photo -> many detected cards, shown in a deselect grid.
  const [pageScan, setPageScan] = useState(null);   // { status:"scanning"|"review"|"error", rows:[...], message }
  const [pageSel,  setPageSel]  = useState(new Set());  // matched-card ids currently checked to add
  const [pageBusy, setPageBusy] = useState(false);
  const [pageGrid, setPageGrid] = useState({ rows:3, cols:3 });   // binder page layout (3x3 default)
  // Voice-to-add: speak a card, confirm the match, add it.
  const [voiceState, setVoiceState] = useState(null);  // { status:"listening"|"result"|"nomatch"|"error", transcript, matches, picked, message }
  // Narrowing the pool before speaking is the single biggest accuracy win: "brawl 80's rad" as a
  // filter turns 31k candidates into a few hundred, so a half-heard hero name still lands. It also
  // means you can rattle off names without re-stating the treatment every time.
  const [voiceFilter, setVoiceFilter] = useState({ set:"", treatment:"", weapon:"" });
  const voiceRecRef = useRef(null);
  const scanInFlight = useRef(false);
  // What the user said while the camera was open, e.g. "moose 80's rad". A ref, not state, because
  // scanCardPhoto reads it mid-flight and must see the latest value without re-rendering.
  const scanHintRef = useRef("");
  const [scanHint, setScanHint] = useState("");        // mirrored for display
  const [scanHintOn, setScanHintOn] = useState(false); // is the mic listening
  const scanHintRecRef = useRef(null);
  const [scanSession,   setScanSession]   = useState([]);
  const [scanQty,       setScanQty]       = useState(1);
  // Loan-by-photo: chosen mode ("lent"/"borrowed") before the camera opens, and
  // the matched card awaiting the who-prompt. A camera input ref feeds the scan.
  const [scanLoanMode,  setScanLoanMode]  = useState(null);
  const [loanScanCard,  setLoanScanCard]  = useState(null);
  const [bulkLoanOpen,  setBulkLoanOpen]  = useState(null);   // "lent" | "borrowed" | null
  const loanScanInputRef = useRef(null);

  // -- Friends --
  const [friends,       setFriends]       = useState([]);
  // Family members (mutual family link) — used for handing a deck over to their account.
  const familyList = useMemo(() => (friends||[]).filter(f => f.isFamily).map(f => ({ uid: f.friendUid, name: f.friendName || "Family" })), [friends]);
  const [friendReqs,    setFriendReqs]    = useState([]);
  const [sentReqs,      setSentReqs]      = useState([]);
  const [addEmail,      setAddEmail]      = useState("");
  const [addStatus,     setAddStatus]     = useState(null);
  const [friendOwned,   setFriendOwned]   = useState({});
  const [viewingFriend, setViewingFriend] = useState(null);
  // Family members' cards available to borrow: {famUid: {ownerName, cardIds:Set-as-array}}.
  // = cards they own that are NOT locked in any of their saved decks.
  const [familyAvail,   setFamilyAvail]   = useState({});
  const [familyOwnedRaw, setFamilyOwnedRaw] = useState({});   // provenance: cardId -> {uid,name}, all owned
  const [borrowLedger,  setBorrowLedger]  = useState([]); // family card borrows involving me

  // -- Teams --
  const [teams,         setTeams]         = useState([]);
  const [activeTeam,    setActiveTeam]    = useState(null);
  // Submitted team decks: each member submits ONE deck. Keyed doc id `${teamId}_${uid}`.
  // This is the ONLY card data teammates can see about each other — collections stay private.
  const [teamDecks,     setTeamDecks]     = useState({}); // { uid: { cardIds:[], deckName, submittedAt } }
  const [swapOffers,    setSwapOffers]    = useState([]); // offers I've made or received to resolve conflicts
  const [teamLoans,     setTeamLoans]     = useState([]); // borrowed-card ledger for my teams
  const [newTeamName,   setNewTeamName]   = useState("");
  const [inviteEmail,   setInviteEmail]   = useState("");
  const [inviteStatus,  setInviteStatus]  = useState(null);
  const [teamInvites,   setTeamInvites]   = useState([]);

  // -- Deck builder --
  const [deckCards,     setDeckCards]     = useState([]);
  const [deckName,      setDeckName]      = useState("My Deck");
  const [deckType,      setDeckType]      = useState("none");
  const [savedDecks,    setSavedDecks]    = useState([]);
  const [deckLoadId,    setDeckLoadId]    = useState(null);
  const [deckSaving,    setDeckSaving]    = useState(false);
  const [givenDecks,    setGivenDecks]    = useState([]);  // decks I gave to family (reclaimable)
  const [deckSaved,     setDeckSaved]     = useState(false);
  useEffect(() => {
    if (!user) { setSavedDecks([]); return; }
    // Only MY decks, filtered server-side — was reading every user's decks and
    // filtering on the client, which downloaded the entire collection on load.
    const unsubMine = onSnapshot(query(collection(db, "boba_decks"), where("userId","==",user.uid)), snap => {
      const mine = snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.savedAt||"").localeCompare(a.savedAt||""));
      setSavedDecks(mine);
    }, e => {
      console.error("load decks failed:", e);
      if (String(e?.code||"").includes("permission")) {
        setToast("Couldn't load your decks \u2014 permission denied. The boba_decks rule may need publishing.");
      }
    });
    // Decks I handed to a family member and could still take back — a separate,
    // small server-side query rather than scanning everyone's decks.
    const unsubGiven = onSnapshot(query(collection(db, "boba_decks"), where("transferredFrom","==",user.uid)), snap => {
      setGivenDecks(snap.docs.map(d=>({...d.data(), id:d.id})).filter(d => d.userId !== user.uid));
    }, ()=>{});
    return () => { unsubMine(); unsubGiven(); };
  }, [user]);
  // Mark a saved deck as registered (real-world tournament registration done) or
  // clear it. Merges onto the same boba_decks doc so it syncs and the flag shows
  // in the saved-decks list. Optimistic local update with rollback on failure.
  // Generate a BoBA-style registration sheet (hero deck + playbook) as a printable
  // page — mirrors the official SPEC/PLAYMAKER upload sheet, autofilled from the
  // current hero deck and a chosen saved playbook.
  function openRegSheetPrint() {
    const esc = s => String(s==null?"":s).replace(/[&<>"]/g, m=>({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;"}[m]));
    const hSort = regInfo.heroSort || "power";
    const heroCmp = hSort==="weapon"
      ? (a,b)=> String(a.weapon||"").localeCompare(String(b.weapon||"")) || (parseFloat(b.power)||0)-(parseFloat(a.power)||0) || String(a.hero||"").localeCompare(String(b.hero||""))
      : (a,b)=> (parseFloat(b.power)||0)-(parseFloat(a.power)||0) || String(a.hero||"").localeCompare(String(b.hero||""));
    const heroObjs = (deckCards||[]).map(id => cards.find(c=>c.id===id)).filter(Boolean).sort(heroCmp);
    const heroRows = heroObjs.map(c=>
      '<tr><td class="n">'+esc(c.cardNum||"\u2014")+'</td><td class="nm">'+esc(c.hero||"\u2014")+'</td>'+
      '<td class="v">'+esc(c.variation||"\u2014")+'</td>'+
      '<td class="w">'+esc(c.weapon||"\u2014")+'</td><td class="p">'+esc(c.power??"\u2014")+'</td>'+
      '<td class="s">'+esc(c.setName||"\u2014")+'</td></tr>'
    ).join("");
    const pb = (savedPlaybooks||[]).find(p=>p.id===regInfo.playbookId);
    const playObjs = ((pb?.entries)||[]).map(e=>({ ...e, card: cards.find(c=>c.id===e.id) })).filter(e=>e.card)
      .sort((a,b)=>(parseFloat(b.card.dbs)||0)-(parseFloat(a.card.dbs)||0));
    const playDbs = playObjs.reduce((s,e)=>s+(parseFloat(e.card.dbs)||0),0);
    const playRows = playObjs.map(e=>{ const c=e.card; const bonus=e.type==="bonus";
      return '<tr><td class="n">'+esc(c.cardNum||"\u2014")+'</td><td class="nm">'+esc(c.playName||c.hero||"\u2014")+(bonus?' <span class="bt">BONUS</span>':'')+'</td>'+
        '<td class="s">'+esc(c.setName||"\u2014")+'</td><td class="d">'+esc(c.dbs??"\u2014")+'</td></tr>';
    }).join("");
    const grand = playDbs;   // heroes have no DBS; the cap is the playbook total
    const over = grand > PUBLIC_DBS_CAP;
    const html = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>'+esc(regInfo.player||"Bazooka")+' \u2014 Registration</title>'+
      '<style>*{box-sizing:border-box;}body{font-family:"Trebuchet MS",Arial,Helvetica,sans-serif;margin:0;padding:24px 30px;color:#111;}'+
      '.top{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:4px solid #111;padding-bottom:10px;}'+
      '.brandwrap{display:flex;flex-direction:column;gap:4px;}.logo{height:52px;width:auto;object-fit:contain;}'+
      '.spec{font-size:13px;font-weight:900;letter-spacing:2px;color:#111;text-transform:uppercase;}'+
      '.fmt{text-align:right;font-size:11px;font-weight:800;letter-spacing:0.5px;color:#999;}'+
      '.info{display:flex;margin:14px 0 6px;border:1.5px solid #111;border-radius:8px;overflow:hidden;}'+
      '.info .cell{flex:1;padding:7px 12px;border-right:1px solid #ddd;}.info .cell:last-child{border-right:none;}'+
      '.info .lbl{font-size:8px;font-weight:900;letter-spacing:1.2px;color:#999;text-transform:uppercase;}'+
      '.info .val{font-size:14px;font-weight:800;margin-top:2px;min-height:18px;}'+
      'h2{font-size:14px;font-weight:900;letter-spacing:1.5px;text-transform:uppercase;margin:20px 0 6px;display:flex;justify-content:space-between;align-items:baseline;}'+
      'h2 .ct{font-size:11px;color:#999;font-weight:800;letter-spacing:0.5px;}'+
      'table{width:100%;border-collapse:collapse;font-size:12.5px;}'+
      'th{text-align:left;background:#111;color:#fff;padding:6px 8px;font-size:9px;font-weight:800;letter-spacing:0.8px;text-transform:uppercase;}'+
      'th.r,td.d,td.p{text-align:right;}td{padding:6px 8px;border-bottom:1px solid #eee;}tr{page-break-inside:avoid;}'+
      'td.n{width:44px;color:#666;font-weight:700;}td.nm{font-weight:800;}td.v{color:#555;font-size:11.5px;}td.w{width:84px;color:#555;}td.p{width:52px;font-weight:900;}td.s{width:110px;color:#777;font-size:11px;}td.d{width:56px;font-weight:900;font-variant-numeric:tabular-nums;}'+
      '.bt{font-size:8px;font-weight:900;color:#B45309;background:#FEF3C7;border:1px solid #FDE68A;border-radius:3px;padding:0 3px;}'+
      '.subtot td{border-top:2.5px solid #111;font-weight:900;padding-top:8px;}.subtot .l{text-align:right;font-size:10px;letter-spacing:1px;text-transform:uppercase;}'+
      '.grand{margin-top:18px;display:flex;justify-content:space-between;align-items:center;background:'+(over?"#FEE2E2":"var(--bz-s1)")+';color:'+(over?"#991B1B":"#fff")+';border-radius:10px;padding:12px 18px;}'+
      '.grand .gl{font-size:12px;font-weight:900;letter-spacing:1.5px;text-transform:uppercase;}.grand .gv{font-size:24px;font-weight:900;font-variant-numeric:tabular-nums;}'+
      '.cap{font-size:10px;opacity:0.7;margin-left:8px;font-weight:700;}'+
      '.btns{margin-bottom:16px;}button{background:#111;color:#fff;border:none;border-radius:9px;padding:9px 18px;font-size:12px;font-weight:800;cursor:pointer;font-family:inherit;}'+
      '@page{margin:12mm;}@media print{.btns{display:none;}}</style></head><body>'+
      '<div class="btns"><button onclick="window.print()">Print / Save PDF</button></div>'+
      '<div class="top"><div class="brandwrap"><img src="'+location.origin+'/Bazooka_Logo_cropped.png" class="logo" alt="Bazooka"/><div class="spec">'+(esc(regInfo.event)||"REGISTRATION")+'</div></div>'+
      '<div class="fmt">'+esc(deckName||"Untitled deck")+'<br>'+(pb?esc(pb.name):"\u2014 no playbook \u2014")+'</div></div>'+
      '<div class="info">'+
        '<div class="cell"><div class="lbl">Player</div><div class="val">'+esc(regInfo.player)+'</div></div>'+
        '<div class="cell"><div class="lbl">Carde.io Account</div><div class="val">'+esc(regInfo.whatnot)+'</div></div>'+
        '<div class="cell"><div class="lbl">Date</div><div class="val">'+esc(regInfo.date)+'</div></div>'+
        '<div class="cell"><div class="lbl">Event</div><div class="val">'+esc(regInfo.event)+'</div></div>'+
      '</div>'+
      '<h2>Hero Deck <span class="ct">'+heroObjs.length+' cards</span></h2>'+
      '<table><thead><tr><th>#</th><th>Hero</th><th>Variation</th><th>Weapon</th><th class="r">Pwr</th><th>Set</th></tr></thead><tbody>'+
        heroRows+
      '</tbody></table>'+
      '<h2>Playbook <span class="ct">'+playObjs.length+' plays</span></h2>'+
      (playObjs.length?('<table><thead><tr><th>#</th><th>Play</th><th>Set</th><th class="r">DBS</th></tr></thead><tbody>'+
        playRows+'<tr class="subtot"><td></td><td class="l" colspan="2">Playbook DBS</td><td class="d">'+Math.round(playDbs)+'</td></tr></tbody></table>')
        :'<div style="font-size:12px;color:#999;padding:8px 0;">No playbook selected.</div>')+
      '<div class="grand"><span class="gl">Total DBS'+(over?' \u2014 OVER CAP':'')+'</span><span class="gv">'+Math.round(grand)+'<span class="cap">/ '+PUBLIC_DBS_CAP+'</span></span></div>'+
      '</body></html>';
    const w = window.open("","_blank","width=900,height=1100");
    if(!w){ alert("Please allow pop-ups to open the registration sheet."); return; }
    w.document.write(html); w.document.close();
  }

  async function toggleDeckRegistered(deck) {
    if (!user || !deck?.id) return;
    const next = !deck.registered;
    setSavedDecks(list => list.map(d => d.id === deck.id ? { ...d, registered: next } : d));
    try {
      await setDoc(doc(db,"boba_decks",deck.id), {
        registered: next,
        registeredAt: next ? new Date().toISOString() : null,
      }, { merge:true });
    } catch (e) {
      console.error("toggle registered failed:", e);
      setSavedDecks(list => list.map(d => d.id === deck.id ? { ...d, registered: !next } : d));
      showToast("Couldn't update registration \u2014 try again.");
    }
  }
  async function saveDeckTab() {
    if (!deckName.trim() || deckCards.length === 0) { alert("Name your deck and add at least one card before saving."); return; }
    if (!user) { setSigningIn(true); return; }
    // Last gate: you can still SAVE an illegal deck (a work-in-progress is legitimate), but you
    // shouldn't be able to do it without knowing. Nobody should turn up to the National and find
    // out at the table.
    if (deckLegality && !deckLegality.ok && deckLegality.problems.length) {
      const ok = window.confirm(
        `This deck isn't legal for ${fmtOf(deckType).short}:\n\n` +
        deckLegality.problems.map(p=>`  • ${p}`).join("\n") +
        `\n\nSave it anyway? (You can keep building — it just won't be tournament-legal as it stands.)`
      );
      if (!ok) return;
    }
    setDeckSaving(true);
    const id = deckLoadId || `deck_${Date.now()}`;
    try {
      await setDoc(doc(db,"boba_decks",id), { id, userId: user.uid, name: deckName.trim(), cardIds: deckCards, cardCount: deckCards.length, deckType, savedAt: new Date().toISOString() }, { merge:true });
      // Confirm the write actually reached the SERVER, not just the local offline cache. A cached
      // write makes the deck appear saved on this machine while never syncing — which looks exactly
      // like "my decks don't follow me between computers". getDocFromServer throws if it can't reach
      // Firestore, so a silent local-only save becomes a visible error instead.
      try {
        const check = await getDocFromServer(doc(db,"boba_decks",id));
        if (!check.exists()) throw new Error("The deck didn't reach the server.");
      } catch(verr) {
        console.error("deck save did not sync:", verr);
        alert("Your deck saved on THIS device but may not have synced to your account.\n\n"
            + "Check your connection and save again. If it keeps happening, the boba_decks "
            + "Firestore rule may need publishing.");
      }
      setDeckLoadId(id); setDeckSaved(true); setTimeout(()=>setDeckSaved(false), 1800);
    } catch(e) {
      console.error("save deck failed:", e);
      const msg = String(e?.code||e?.message||e);
      alert(msg.includes("permission")
        ? "Couldn't save: your account doesn't have permission to write decks. The boba_decks Firestore rule needs to be published."
        : "Couldn't save your deck: " + (e?.message || e));
    }
    finally { setDeckSaving(false); }
  }
  async function deleteDeckTab(id) {
    if (!window.confirm("Delete this deck?")) return;
    try { await deleteDoc(doc(db,"boba_decks",id)); } catch(e){ alert("Delete failed: "+(e?.message||e)); return; }
    if (deckLoadId === id) { setDeckLoadId(null); setDeckName("My Deck"); setDeckCards([]); }
  }
  // Hand a deck over to a family member's account. This MOVES the deck: it leaves your account and
  // becomes theirs (only the deck LIST moves — card ownership is untouched). Works in one write
  // because the boba_decks update rule checks the CURRENT owner (you) == you, and the new owner can
  // be anyone. After this, you can't edit it and they can, load it, and share it as their own.
  async function giveDeckToFamily(deck, toUid, toName) {
    if (!deck || !toUid) return;
    if (!window.confirm(`Give "${deck.name||"this deck"}" to ${toName||"them"}? It will move to their account and leave yours. (Your cards are not affected — only the deck list moves.)`)) return;
    try {
      await updateDoc(doc(db,"boba_decks",deck.id), {
        userId: toUid,
        transferredFrom: user.uid,
        transferredFromName: (user.displayName || user.email || "A family member"),
        transferredAt: new Date().toISOString(),
      });
      // It's no longer mine — drop it from my open editor if it was loaded, and from my list.
      if (deckLoadId === deck.id) { setDeckLoadId(null); setDeckName("My Deck"); setDeckCards([]); }
      setSavedDecks(list => (list||[]).filter(d => d.id !== deck.id));
      setToast(`Deck given to ${toName||"them"} — it's on their account now.`);
    } catch(e) {
      alert("Couldn't give the deck: " + (e?.message||e));
    }
  }
  // Take back a deck you previously gave to a family member. Requires the boba_decks rule to allow
  // the ORIGINAL giver (recorded as transferredFrom) to write — see the rule note shipped with this.
  // Without that rule, only the current owner can move it, so they'd have to hand it back instead.
  async function takeBackDeck(deck) {
    if (!deck || !user) return;
    if (!window.confirm(`Take back "${deck.name||"this deck"}"? It will move back to your account and leave theirs.`)) return;
    try {
      await updateDoc(doc(db,"boba_decks",deck.id), {
        userId: user.uid,
        takenBackAt: new Date().toISOString(),
        transferredFrom: deleteField(),
        transferredFromName: deleteField(),
      });
      setToast("Deck taken back — it's on your account again.");
    } catch(e) {
      alert("Couldn't take the deck back: " + (e?.message||e) + "\n\nIf this says permission denied, the Firestore rule for boba_decks needs the take-back clause, or ask them to give it back from their account.");
    }
  }
  function loadDeckTab(d) { setDeckLoadId(d.id); setDeckName(d.name||"My Deck"); setDeckCards(d.cardIds||[]); setDeckType(d.deckType||"none"); }
  function newDeckTab() { setDeckLoadId(null); setDeckName("My Deck"); setDeckCards([]); }
  const [deckSearch,    setDeckSearch]    = useState("");
  // Same debounce as the card-database search — see note above.
  const [deckSearchDebounced, setDeckSearchDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDeckSearchDebounced(deckSearch), 60);
    return () => clearTimeout(t);
  }, [deckSearch]);
  const [deckFilterW,   setDeckFilterW]   = useState(new Set());   // multi-select, like the quick builder
  const [deckFilterP,   setDeckFilterP]   = useState(new Set());
  const [deckFilterS,   setDeckFilterS]   = useState(new Set());   // multi-select
  const [deckFilterT,   setDeckFilterT]   = useState(new Set());   // multi-select

  // -- Marketplace --
  const [listings,      setListings]      = useState([]);
  const [myListings,    setMyListings]    = useState([]);
  const [listModal,     setListModal]     = useState(null); // card being listed
  // -- Bulk select --
  const [selectMode,    setSelectMode]    = useState(false);
  const [bulkMoreOpen,  setBulkMoreOpen]  = useState(false);  // consolidated bulk-action menu
  // Close the bulk "More" menu on any outside click — a menu you can only dismiss by re-tapping the
  // button feels stuck, especially on mobile where it covers the cards you're trying to see.
  useEffect(() => {
    if (!bulkMoreOpen) return;
    const close = () => setBulkMoreOpen(false);
    // Defer so the click that opened it doesn't immediately close it.
    const t = setTimeout(() => document.addEventListener("click", close), 0);
    return () => { clearTimeout(t); document.removeEventListener("click", close); };
  }, [bulkMoreOpen]);
  const [bulkQty, setBulkQty] = useState(1);   // last value set from the bulk dial
  const [restoreModal, setRestoreModal] = useState(null); // {file, preview} | {busy} | {report}
  const [selectedIds,   setSelectedIds]   = useState(()=>new Set());
  // What the bulk dial displays. Derived from the selection rather than stored, so it can never
  // drift out of step with the cards. Mixed counts show the highest, which is what you would then
  // step up or down from.
  const selQty = useMemo(() => {
    if (!selectedIds.size) return bulkQty;
    let max = 0;
    selectedIds.forEach(id => { const q = parseInt(owned[id]) || 0; if (q > max) max = q; });
    return max || 1;
  }, [selectedIds, owned, bulkQty]);
  const [bulkListModal, setBulkListModal] = useState(false);
  const [importLockAll, setImportLockAll] = useState(false);  // lock every copy in this import
  const [importFaqOpen, setImportFaqOpen] = useState(false);
  const [bulkListPrice, setBulkListPrice] = useState("");
  const [bulkListNote,  setBulkListNote]  = useState("");
  const [bulkListType,  setBulkListType]  = useState("sale");
  const [bulkBusy,      setBulkBusy]      = useState(false);
  // -- Trade package (shareable link to a set of owned cards, for non-Bazooka users) --
  const [tpModal,    setTpModal]    = useState(false);   // create-package modal open
  const [tpTitle,    setTpTitle]    = useState("");
  const [tpNote,     setTpNote]     = useState("");
  const [tpContact,  setTpContact]  = useState("");
  const [tpValues,   setTpValues]   = useState({});   // cardId -> dollar value for the package
  const [tpEditId,   setTpEditId]   = useState(null);   // when set, the modal edits this package instead of creating
  const [tpEditIds,  setTpEditIds]  = useState([]);     // card ids being edited (from the package, not selection)
  const [tpRemoved,  setTpRemoved]  = useState(new Set());   // cards removed while editing a package
  // Open the package editor: load its cards, values, and text into the create modal in "edit" mode.
  function openTradeEditor(pk) {
    setTpEditId(pk.id);
    setTpEditIds((pk.items||[]).map(it=>it.id));
    const vals = {}; (pk.items||[]).forEach(it=>{ if(Number(it.value)>0) vals[it.id]=String(it.value); });
    setTpValues(vals);
    setTpTitle(pk.title||""); setTpNote(pk.note||""); setTpContact(pk.contact||"");
    setTpLink(""); setTpRemoved(new Set()); setTpModal(true);
  }
  const [tpBusy,     setTpBusy]     = useState(false);
  const [tpLink,     setTpLink]     = useState("");        // resulting share URL after create
  const [tpCopied,   setTpCopied]   = useState(false);
  const [tpListOpen, setTpListOpen] = useState(false);   // "my trade packages" manager
  const [tpList,     setTpList]     = useState(null);    // loaded packages | null=not loaded
  const [tpListBusy, setTpListBusy] = useState(false);

  // Load this user's trade packages on demand (when they open the manager).
  async function loadMyTradePackages() {
    if (!user) return;
    setTpListBusy(true);
    try {
      const q = query(collection(db, "boba_trade_packages"), where("ownerUid", "==", user.uid));
      const snap = await getDocs(q);
      const rows = snap.docs.map(d => ({...d.data(), id:d.id}))
        .sort((a,b) => String(b.createdAt||"").localeCompare(String(a.createdAt||"")));
      setTpList(rows);
    } catch(e) { console.error("load trade packages failed:", e); alert("Couldn't load your trade packages: " + (e?.message||e)); setTpList([]); }
    setTpListBusy(false);
  }

  async function deleteTradePackage(pid) {
    if (!window.confirm("Delete this trade package? The share link will stop working.")) return;
    try {
      await deleteDoc(doc(db, "boba_trade_packages", pid));
      setTpList(list => (list||[]).filter(p => p.id !== pid));
    } catch(e) { alert("Couldn't delete: " + (e?.message||e)); }
  }
  // -- Messages --
  const [threads,       setThreads]       = useState([]);
  const [activeThread,  setActiveThread]  = useState(null);
  const [threadMsgs,    setThreadMsgs]    = useState([]);
  const [newMsg,        setNewMsg]        = useState("");
  const [unreadThreads, setUnreadThreads] = useState(0);
  // -- Market sales --
  const [marketSales,   setMarketSales]   = useState([]);
  // ── TRADES ───────────────────────────────────────────────────────────────────────────────────
  // A trade is a two-sided sale, and it has one constraint a sale doesn't: Firestore rules only let
  // you write your OWN boba_owned. I cannot remove a card from your collection, and you cannot remove
  // one from mine \u2014 correctly so. That rules out an automatic swap on acceptance.
  //
  // So settlement is TWO-SIDED. When you mark a package received, YOUR client applies YOUR half: adds
  // what you got, removes what you gave. The other person does the same when theirs arrives. That's
  // also just honest \u2014 the cards move in the post, not in the database, and pretending ownership
  // changes the instant someone clicks Accept would leave collections lying about reality.
  //
  // status: pending \u2192 accepted \u2192 (each side ships + confirms) \u2192 complete
  //         declined / cancelled are terminal.
  const [tradeOffers,   setTradeOffers]   = useState([]);
  // -- Comp modal --
  const [compCard,      setCompCard]      = useState(null); // card being comped
  const [viewProfileUid, setViewProfileUid] = useState(null); // uid of profile being viewed
  const [privacyAnim,   setPrivacyAnim]   = useState(null); // cardId showing lock animation
  const [listPrice,     setListPrice]     = useState("");
  const [listType,      setListType]      = useState("sale"); // sale|trade|either
  const [listNotes,     setListNotes]     = useState("");
  const [listPayment,  setListPayment]  = useState(""); // seller payment info
  const [listPhotos,    setListPhotos]    = useState([]); // {url} uploaded photos of the actual card
  const [listUploading, setListUploading] = useState(false);
  const [listPhotoAutofilled, setListPhotoAutofilled] = useState(false);
  // When the list modal opens, auto-fill with the saved scan photo for that card (most recent lot that has one)
  useEffect(() => {
    if (!listModal) { setListPhotoAutofilled(false); return; }
    const cardLots = lots.filter(l => l.cardId===listModal.id && l.photoUrl);
    const saved = cardLots.length ? cardLots[cardLots.length-1].photoUrl : "";
    if (saved) { setListPhotos([{ url: saved }]); setListPhotoAutofilled(true); }
  }, [listModal]); // eslint-disable-line
  const [paymentPopup,  setPaymentPopup]  = useState(null);
  const [offerModal,    setOfferModal]    = useState(null); // listing being offered on
  const [offerAmt,      setOfferAmt]      = useState("");
  const [offerNote,     setOfferNote]     = useState("");
  const [offerSent,     setOfferSent]     = useState(false);
  const [marketNotifs,  setMarketNotifs]  = useState([]);
  const [allMyOffers,   setAllMyOffers]   = useState([]);
  const [counterModal,  setCounterModal]  = useState(null); // offer being countered
  const [counterAmt,    setCounterAmt]    = useState("");
  const [counterSent,   setCounterSent]   = useState(false);
  const [negHistory,    setNegHistory]    = useState([]);
  const [wantNotifs,    setWantNotifs]    = useState([]);

  // Watch for NEW notifications and pop a prominent, impossible-to-miss card. On first load we just
  // record what's already there (no pop for a backlog); after that, any newly-arrived alert pops.
  useEffect(() => {
    const ids = new Set(wantNotifs.map(n=>n.id));
    if (notifSeenRef.current === null) { notifSeenRef.current = ids; return; }   // prime, don't pop backlog
    const fresh = wantNotifs.filter(n => !notifSeenRef.current.has(n.id));
    notifSeenRef.current = ids;
    if (!fresh.length) return;
    const n = fresh[0];
    const type = String(n.type||"");
    const isTrade = type.startsWith("trade_");
    const title = type==="trade_offer" ? `${n.fromName||"Someone"} sent you a trade`
      : type==="trade_accepted" ? `${n.fromName||"Someone"} accepted your trade`
      : type==="trade_agreed" ? `${n.fromName||"Someone"} agreed \u2014 your move`
      : type==="trade_countered" ? `${n.fromName||"Someone"} added their side`
      : type==="trade_declined" ? `${n.fromName||"Someone"} declined your trade`
      : type==="want_tradeable" ? `A card you want is up for trade`
      : `${n.cardName||"A card"} you want is on the marketplace`;
    const sub = fresh.length>1 ? `+${fresh.length-1} more notification${fresh.length-1===1?"":"s"}` : (isTrade ? "Tap to open My Trades" : "Tap to view it");
    setNotifPop({ title, sub, image: n.cardImage||null, view: isTrade?"trades":"sale" });
    const t = setTimeout(()=>setNotifPop(null), 7000);
    return () => clearTimeout(t);
  }, [wantNotifs]);
  const [trackingInputs, setTrackingInputs] = useState({}); // {saleId: {num,carrier}}

  // -- Super Foil Tracker --
  const [superClaims,     setSuperClaims]     = useState([]);
  const [claimModal,      setClaimModal]      = useState(null);
  const [claimPhoto,      setClaimPhoto]      = useState(null);
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [claimSent,       setClaimSent]       = useState(false);
  const [claimStory,      setClaimStory]      = useState("");
  const [claimName,       setClaimName]       = useState("");
  const [claimDate,       setClaimDate]       = useState("");
  const [adminClaimMode,  setAdminClaimMode]  = useState(false); // admin recording a hit via the normal claim form
  const [editClaimMode,   setEditClaimMode]   = useState(false); // admin editing an existing verified claim
  const [oneEditMode,     setOneEditMode]     = useState(false); // admin editing an existing 1/1 claim
  const [superCelebration, setSuperCelebration] = useState(null); // {cardName, cardImage, type} -> fires celebration
  useEffect(() => {
    if (!superCelebration) return;
    const t = setTimeout(() => setSuperCelebration(null), 4200);
    return () => clearTimeout(t);
  }, [superCelebration]);
  const [collapsedSuperSets, setCollapsedSuperSets] = useState({});
  const [expandedOneGroups, setExpandedOneGroups] = useState({});
  const [superSearch,     setSuperSearch]     = useState("");
  const [secret1Search,   setSecret1Search]   = useState("");
  const [flippedClaim,    setFlippedClaim]    = useState(null);

  // -- 1/1 Tracker --
  const [oneOfOneClaims,  setOneOfOneClaims]  = useState([]);
  const [bojax34Claims,   setBojax34Claims]   = useState([]);
  const [bojaxModal,      setBojaxModal]      = useState(null); // serial number being claimed
  const [bojaxName,       setBojaxName]       = useState("");
  const [bojaxStory,      setBojaxStory]      = useState("");
  const [bojaxDate,       setBojaxDate]       = useState("");
  const [bojaxPhoto,      setBojaxPhoto]      = useState(null);
  const [bojaxSubmitting, setBojaxSubmitting] = useState(false);
  const [bojax34Uploading, setBojax34Uploading] = useState(null);
  const [oneModal,        setOneModal]        = useState(null);
  const [onePhoto,        setOnePhoto]        = useState(null);
  const [oneStory,        setOneStory]        = useState("");
  const [oneDate,         setOneDate]         = useState("");
  const [oneName,         setOneName]         = useState("");
  const [oneSubmitting,   setOneSubmitting]   = useState(false);
  const [oneSent,         setOneSent]         = useState(false);

  const WEAPON_COLORS = { Fire:"#F97316", Ice:"#60A5FA", Steel:"#C0C0C0", Brawl:"#EF4444",
    Glow:"#4ade80", Hex:"#A855F7", Gum:"#F472B6", Metallic:"var(--bz-line)", Alt:"#FFFFFF", Super:"#F59E0B" };
  const DECK_SIZE = (DECK_FORMATS[deckType]||DECK_FORMATS.none).size || 60;   // capacity follows format (Apex Madness = 70)

  const inp = { background:"#241820", border:"1px solid rgba(255,255,255,0.18)", borderRadius:10,
    color:"#f6eef2", padding:"9px 14px", fontSize:13, fontFamily:"'Trebuchet MS',sans-serif", outline:"none" };

  // Animate header in
  useEffect(() => { setTimeout(()=>setHeaderLoaded(true), 100); }, []);

  // Auto-open latest active thread when switching to messages tab
  useEffect(() => {
    if (activeTab === "messages" && !activeThread && threads.length > 0) {
      // Find the most recently active thread
      const latest = threads.slice().sort((a,b) => (b.lastMessageAt||"") > (a.lastMessageAt||"") ? 1 : -1)[0];
      if (latest) setActiveThread(latest);
    }
  }, [activeTab, threads.length]);


  // Load negotiation history when counter modal opens
  useEffect(() => {
    if (!counterModal) { setNegHistory([]); return; }
    if (!user?.uid) { setNegHistory([]); return; }
    const rootId = counterModal.parentOfferId||counterModal.id;
    getDocs(query(
      collection(db,"negotiation_history"),
      where("rootOfferId","==",rootId),
      where("participantUids","array-contains",user.uid)
    )).then(snap => setNegHistory(snap.docs.map(d=>d.data()).sort((a,b)=>(a.timestamp||"").localeCompare(b.timestamp||""))))
      .catch(() => {
        // Fallback: show at least the current offer as first entry
        setNegHistory([{
          action:"offer",
          fromName:counterModal.buyerName||"Buyer",
          amount:counterModal.offerAmount||0,
          timestamp:counterModal.createdAt||"",
        }]);
      });
  }, [counterModal?.id]);



  // -- Super claims (public, no auth needed) --
  useEffect(() => {
    const unsub = onSnapshot(collection(db,"super_claims"), snap => {
      setSuperClaims(snap.docs.map(d=>({...d.data(), id:d.id})));
    });
    return ()=>unsub();
  }, []);

  // -- 1/1 claims (public) --
  // Cards with any live DBS overrides applied on top of the base snapshot.
  const cardsWithDbs = useMemo(() => {
    if (!dbsOverrides || Object.keys(dbsOverrides).length === 0) return cards;
    return cards.map(c => dbsOverrides[c.id] !== undefined ? { ...c, dbs: dbsOverrides[c.id] } : c);
  }, [cards, dbsOverrides]);

  // -- Live DBS overrides — a single tiny doc {cardId: dbs} that every client subscribes to,
  // so admin DBS edits/imports show up INSTANTLY for everyone (no 5-min snapshot cache wait). --
  useEffect(() => {
    const unsub = onSnapshot(doc(db,"config","dbs_overrides"), snap => {
      const data = snap.exists() ? (snap.data().values || snap.data() || {}) : {};
      setDbsOverrides(data);
      try { localStorage.setItem("dbs_overrides_v1", JSON.stringify(data)); } catch(e){}
    });
    return ()=>unsub();
  }, []);

  // -- Live "Found In" map: which pack types contain which treatment, per set. One small doc that
  // every client subscribes to, so remapping a set updates the badge for everyone immediately. --
  useEffect(() => {
    const unsub = onSnapshot(doc(db,"config","found_in"), snap => {
      const data = snap.exists() ? (snap.data().map || {}) : {};
      setFoundInMap(data);
      try { localStorage.setItem("found_in_v1", JSON.stringify(data)); } catch(e){}
    });
    return ()=>unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db,"oneof1_claims"), snap => {
      setOneOfOneClaims(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>b.createdAt?.localeCompare(a.createdAt||"")));
    });
    return ()=>unsub();
  }, []);

  // -- /34 BoJax claims (public) — TBJ-B serialized 1/34..34/34 --
  useEffect(() => {
    const unsub = onSnapshot(collection(db,"bojax34_claims"), snap => {
      setBojax34Claims(snap.docs.map(d=>({...d.data(), id:d.id})));
    });
    return ()=>unsub();
  }, []);

  // -- Public marketplace (loads without login) --
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db,"marketplace"), where("status","==","active")),
      snap => {
        const all = snap.docs.map(d=>({...d.data(),id:d.id}));
        setListings(all);
      }
    );
    return ()=>unsub();
  }, []);

  // -- Load cards (instant, hang-proof: IndexedDB cache → gzip → plain file → Firestore) --
  useEffect(() => {
    const CACHE_TTL = 30 * 60 * 1000;
    const withTimeout = (p, ms) => Promise.race([ p, new Promise((_,rej)=>setTimeout(()=>rej(new Error("timeout")), ms)) ]);
    let cancelled = false;
    let attempt = 0;
    let gotCards = false;

    const runLoad = () => {
    let done = false;
    const finish = (all) => {
      if (done) return; done = true;
      if (all && all.length) { gotCards = true; setCards(all); }
      // Only stop the spinner if we actually have cards, OR we've exhausted retries.
      if (gotCards || attempt >= 3) setLoading(false);
      // If we ended with NO cards and still have retries, try again (mobile slow-network recovery).
      if (!gotCards && attempt < 3 && !cancelled) {
        attempt++;
        setTimeout(() => { if (!cancelled) runLoad(); }, 1200 * attempt);
      }
    };

    // Absolute safety net. This used to fire at 12s, which was SHORTER than a first-time user
    // needs to pull the ~12MB card list on a phone or a slow line. So the gzip download would be
    // abandoned mid-flight and this would start fetching the UNCOMPRESSED file instead — bigger,
    // slower, and throwing away everything already downloaded. A first load could never win.
    //
    // 45s is generous, but it only ever matters on a genuinely cold, genuinely slow first load,
    // and the alternative is what we had: guaranteeing that load fails.
    const hardStop = setTimeout(async () => {
      if (done) return;
      try {
        const url = await getDownloadURL(ref(storage, "card_data/boba_checklist.json"));
        const r = await fetch(url + "?v=" + Date.now());
        const all = await r.json();
        if (Array.isArray(all) && all.length) { finish(all); return; }
      } catch(e) {}
      finish(null); // stop the spinner no matter what
    }, 45000);

    (async () => {
      // 1. INSTANT: the cached real card list (real doc ids + images). This is what makes the page
      // feel instant on a return visit — owned counts and art are correct immediately.
      let cacheHasCards = false;
      let cacheTsLocal = 0;
      try {
        const cached = await withTimeout(idbGetCards(), 2500);
        if (cached && Array.isArray(cached.cards) && cached.cards.length > 0) {
          setCards(cached.cards); setLoading(false);
          cacheHasCards = true;
          cacheTsLocal = cached.ts || 0;
        }
      } catch(e) {}

      // NOTE: we deliberately do NOT paint the bundled /cards-data.json here. That file lacks
      // imageUrl and doesn't carry the real Firestore doc ids, so rendering it shows art-less
      // cards and a wrong owned count (`owned` is keyed by doc id). A brief spinner is far better
      // than briefly showing the user incorrect data. The real snapshot below is the only source.

      // FRESH-CACHE TRUST WINDOW: if the cache was written recently, trust it outright and skip
      // the version read AND the multi-MB refresh entirely. The version-doc comparison below is a
      // good freshness signal, but if that doc is ever missing, stale, or its count is off by one,
      // EVERY client re-downloads ~12MB on EVERY refresh — which is exactly the "slow for everyone"
      // symptom. A recent cache is almost certainly current (imports are infrequent), so within
      // this window we keep what we painted and don't touch the network for cards at all. A
      // background refresh still happens once the window lapses.
      const FRESH_MS = 6 * 60 * 60 * 1000; // 6 hours
      if (cacheHasCards && cacheTsLocal && (Date.now() - cacheTsLocal) < FRESH_MS) {
        clearTimeout(hardStop);
        done = true;
        setLoading(false);
        // Quietly verify in the background; only re-cache if the server count truly differs.
        (async () => {
          try {
            const vSnap = await withTimeout(getDoc(doc(db,"meta","cards_version")), 6000);
            const serverCount = vSnap.exists() ? (vSnap.data().count || 0) : 0;
            const cached = await idbGetCards();
            if (serverCount > 0 && cached?.cards?.length && cached.cards.length !== serverCount) {
              // Genuinely stale — refresh in the background without blocking or flashing a spinner.
              const all = await withTimeout(readCardSnapshot(false), 20000);
              if (all && all.length>0 && !cancelled) { setCards(all); idbSetCards(all, Date.now()); }
            }
          } catch(e) { /* background refresh is best-effort */ }
        })();
        return;
      }

      // 2. THE LIVE SNAPSHOT IS THE SOURCE OF TRUTH. It comes from Firestore, so it carries the
      // real doc ids (which `owned` is keyed by) and every imageUrl. Always pull it and let it
      // replace whatever we rendered above.
      //
      // FAST PATH: if our cached list already matches the server's card count, it's current —
      // skip the multi-MB snapshot download entirely and keep what we already rendered. This is
      // what makes a return visit instant instead of flashing wrong data for a few seconds while
      // the whole card list re-downloads. (We publish `count` next to the version on every
      // import/dedupe/rebuild, so this is a single tiny read.)
      if (cacheHasCards) {
        try {
          const cached = await withTimeout(idbGetCards(), 1500);
          const vSnap  = await withTimeout(getDoc(doc(db,"meta","cards_version")), 4000);
          const serverCount = vSnap.exists() ? (vSnap.data().count || 0) : 0;
          if (serverCount > 0 && cached?.cards?.length === serverCount) {
            clearTimeout(hardStop);
            done = true;
            setLoading(false);
            return; // cache is current — nothing to download
          }
        } catch(e) { /* couldn't verify — fall through and refresh */ }
      }

      try {
        // NOT cache-busted. Staleness is already handled above by comparing the cached card count
        // against meta/cards_version, so appending ?v=Date.now() just defeats the CDN and forces
        // every cache-miss to re-pull ~12MB from origin instead of a nearby edge.
        const all = await withTimeout(readCardSnapshot(false), 20000);
        if (all && all.length>0) { clearTimeout(hardStop); finish(all); idbSetCards(all, Date.now()); return; }
      } catch(e) {}
      // 4. Plain uncompressed snapshot file.
      try {
        const url = await getDownloadURL(ref(storage, "card_data/boba_checklist.json"));
        const r2 = await fetch(url + "?v=" + Date.now());
        const all = await r2.json();
        if (Array.isArray(all) && all.length>0) { clearTimeout(hardStop); finish(all); idbSetCards(all, Date.now()); return; }
      } catch(e) {}
      // 5. Firestore direct (last resort).
      try {
        const snap = await getDocs(collection(db,"boba_checklist"));
        const all = snap.docs.map(d=>({...d.data(), id:d.id}));
        if (all.length>0) { clearTimeout(hardStop); finish(all); idbSetCards(all, Date.now()); return; }
      } catch(e) {}
      clearTimeout(hardStop); finish(cacheHasCards ? null : []); // ensure spinner stops
    })();
    return hardStop;
    }; // end runLoad

    const ht = runLoad();
    return () => { cancelled = true; clearTimeout(ht); };
  }, []);
  // (legacy load effect retained but never runs)
  useEffect(() => {
    const _dead = () => {};
  }, []);
  // (legacy localStorage cache no longer used for cards — IndexedDB holds the full set)
  useEffect(() => {
    const _dead = () => {
    const CACHE_KEY = "boba_checklist_cache_v3";
    const CACHE_TTL = 10 * 60 * 1000; // 10 min — short so imports appear soon, but still fast within a session
    let cacheHasCards = false;
    let cacheTs = 0;
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        cacheHasCards = Array.isArray(parsed.cards) && parsed.cards.length > 0;
        cacheTs = parsed.ts || 0;
        if (cacheHasCards) setLoading(false); // show cached immediately while we verify freshness
      }
    } catch(e) {}
    // Cheap freshness check: read one tiny version doc. If the data was updated (import)
    // after our cache, pull fresh; otherwise trust the cache. This makes imports appear
    // instantly for everyone without re-reading 31k docs on every load.
    (async () => {
      let needFresh = !cacheHasCards;
      try {
        const vSnap = await getDoc(doc(db,"meta","cards_version"));
        const serverTs = vSnap.exists() ? (vSnap.data().ts || 0) : 0;
        if (serverTs > cacheTs) needFresh = true;
      } catch(e) { if (Date.now() - cacheTs > CACHE_TTL) needFresh = true; }
      if (!needFresh) { setLoading(false); return; }
      // Data changed. Pull the GZIPPED snapshot first — ~1.5MB instead of ~13MB.
      try {
        const all = await readCardSnapshot(true);
        if (all && all.length>0) {
          setCards(all); setLoading(false);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify({cards:all, ts:Date.now()})); } catch(e) {}
          return;
        }
      } catch(e) {}
      // Fallback: old uncompressed snapshot file (pre-gzip).
      try {
        const url = await getDownloadURL(ref(storage, "card_data/boba_checklist.json"));
        const r2 = await fetch(url + (url.includes("?")?"&":"?") + "v=" + Date.now());
        const all = await r2.json();
        if (Array.isArray(all) && all.length>0) {
          setCards(all); setLoading(false);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify({cards:all, ts:Date.now()})); } catch(e) {}
          return;
        }
      } catch(e) {}
      // Last resort: read Firestore directly (slow, but correct).
      try {
        const snap = await getDocs(collection(db,"boba_checklist"));
        const all = snap.docs.map(d=>({...d.data(), id:d.id}));
        if (all.length>0) {
          setCards(all); setLoading(false);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify({cards:all, ts:Date.now()})); } catch(e) {}
          return;
        }
      } catch(e) {}
      // Last resort: repo static file (oldest).
      try {
        const r = await fetch("/cards-data.json");
        if (r.ok) {
          const all = await r.json();
          if (Array.isArray(all) && all.length>0) {
            setCards(all); setLoading(false);
            try { localStorage.setItem(CACHE_KEY, JSON.stringify({cards:all, ts:Date.now()})); } catch(e) {}
            return;
          }
        }
      } catch(e) {}
      setLoading(false);
    })();
    }; // end _dead (never called)
  }, []);
  // -- Auth + owned + wants + private --
  useEffect(() => {
    // Keep the dynamic early-access allowlist fresh (admin-editable, no redeploy).
    const _eaUnsub = onSnapshot(doc(db,"config","early_access"), snap => {
      if (snap.exists()) {
        const list = snap.data().emails || [];
        setDynamicEarlyAccess(list, true);   // Firestore is now authoritative
        try { localStorage.setItem("early_access_v1", JSON.stringify(list)); } catch(e){}
        setEarlyAccessList(list);
      } else {
        // No doc yet → show the founding emails as the starting list (still editable).
        setDynamicEarlyAccess(EARLY_ACCESS_EMAILS, false);
        setEarlyAccessList([...EARLY_ACCESS_EMAILS]);
      }
    }, ()=>{});
    return () => { try{_eaUnsub();}catch(e){} };
  }, []);
  useEffect(() => {
    return onAuthStateChanged(auth, async u => {
      setUser(u);
      if (u) {
        // --- Access pause: only @bazookabreaks.com team until launch (flip ACCESS_PAUSED to false to re-open) ---
        // The /swancity moderator (swancitycards@gmail.com) must ALWAYS stay signed in,
        // on any path/instance. This gate runs BEFORE any Firestore read so a flaky
        // server read on mobile can never bounce an allowed user back to signed-out.
        const ACCESS_PAUSED = false; // launch is live — anyone can sign in
        const emailNorm = (u.email || "").toLowerCase().trim();
        const isSwancityMod = emailNorm === "swancitycards@gmail.com";
        const isTeam = hasEarlyAccess(u.email) || isSwancityMod || swancity;
        if (ACCESS_PAUSED && !isTeam) {
          alert("We're putting some finishing touches on things and aren't quite open yet — please check back soon. Thanks for your patience!");
          await signOut(auth);
          return;
        }
        // The Swan City moderator belongs on the /swancity page. If she signs in
        // anywhere else on the site, send her there (once) so the right UI loads.
        if (isSwancityMod && !swancity) {
          const path = (window.location.pathname || "").toLowerCase();
          if (path !== "/swancity") { window.location.replace("/swancity"); return; }
        }
        // Record this user for admin user-count (firstSeen set once, lastSeen updated each visit)
        try {
          const uref = doc(db,"users",u.uid);
          let usnap;
          // Track whether we actually HEARD from the server. A failed read used to fall through to
          // a fake empty snapshot, which is indistinguishable from "this account has no username" \u2014
          // so a flaky mobile connection would bounce an existing user to the claim-a-username
          // screen. Not knowing must never be treated as knowing the answer is "no".
          let readOk = true;
          try { usnap = await getDocFromServer(uref); }
          catch(se) {
            try { usnap = await getDoc(uref); }
            catch(se2) { readOk = false; usnap = { exists:()=>false, data:()=>({}) }; }
          }
          await setDoc(uref, {
            email: u.email || "",
            displayName: u.displayName || "",
            lastSeen: Date.now(),
            ...(usnap.exists() ? {} : { firstSeen: Date.now() }),
          }, { merge:true });
          // If no username claimed yet, trigger onboarding
          const existing = usnap.exists() ? usnap.data() : {};
          // localStorage backup keyed to this user — survives Firestore read flakiness
          let lsUsername = "", lsPhoto = "";
          try { lsUsername = localStorage.getItem("bazooka_username_" + u.uid) || ""; } catch(e) {}
          try { lsPhoto = localStorage.getItem("bazooka_photo_" + u.uid) || ""; } catch(e) {}
          let effectiveUsername = existing.username || lsUsername;
          // Self-heal: an earlier version reserved the handle BEFORE writing the profile, so a
          // partial failure could leave usernames/{handle} pointing at this uid while
          // users/{uid}.username was empty \u2014 the account looked unnamed on every other device.
          // If the profile has no username, look for a reservation owned by this uid and restore it.
          if (readOk && !effectiveUsername) {
            try {
              const mine = await getDocs(query(collection(db,"usernames"), where("uid","==",u.uid), limit(1)));
              const found = mine.docs[0]?.id;
              if (found) {
                effectiveUsername = found;
                setDoc(doc(db,"users",u.uid), { username: found }, { merge:true }).catch(()=>{});
                try { localStorage.setItem("bazooka_username_" + u.uid, found); } catch(e) {}
              }
            } catch(e) { /* recovery is best-effort; never block sign-in on it */ }
          }
          const effectivePhoto = existing.photoURL || lsPhoto;
          // If the server somehow lost it but we have it locally, re-write it to the server
          if (!existing.username && lsUsername) {
            setDoc(doc(db,"users",u.uid), { username: lsUsername }, { merge:true }).catch(()=>{});
          }
          if (!existing.photoURL && lsPhoto) {
            setDoc(doc(db,"users",u.uid), { photoURL: lsPhoto }, { merge:true }).catch(()=>{});
          }
          setMyUsername(effectiveUsername);
          setMyPhotoURL(effectivePhoto);
          setMyDiscord(existing.discordName || "");
          setMyWhatnot(existing.whatnotName || "");
          if (effectiveUsername) usernameClaimedThisSession.current = true;
          // Only prompt when we genuinely know there is no username. If the read failed we simply
          // do not ask \u2014 a returning user keeps using the app, and the next successful load will
          // prompt if they really are new.
          if (readOk && !effectiveUsername && !usernameClaimedThisSession.current) setOnboarding(true);
        } catch(e) { console.error("user record failed:", e); }
        try {
          // allSettled, NOT all. These six are independent, and Promise.all is all-or-nothing:
          // if any ONE of them is denied, the whole block throws and NONE of the six get applied.
          // That's how a user could have their cards load fine but their scan photos silently not
          // (lots stays []), because setOwned ran before the throw and setLots never did.
          //
          // An admin never sees this: the catch-all rule (`match /{document=**} if isAdmin()`) means
          // every read succeeds for @bazookabreaks.com, so the failure is invisible in testing and
          // only shows up for real users. Each fetch now stands or falls on its own, and any denial
          // is named in the console instead of vanishing into one generic catch.
          const _names = ["boba_owned","boba_wants","boba_public","boba_lots","boba_kids","boba_sold"];
          const _res = await Promise.allSettled([
            getDoc(doc(db,"boba_owned",u.uid)),
            getDoc(doc(db,"boba_wants",u.uid)),
            getDoc(doc(db,"boba_public",u.uid)),
            getDoc(doc(db,"boba_lots",u.uid)),
            getDoc(doc(db,"boba_kids",u.uid)),
            getDoc(doc(db,"boba_sold",u.uid)),
          ]);
          _res.forEach((r,i) => { if (r.status === "rejected") console.error(`read DENIED: ${_names[i]} \u2014`, r.reason); });
          const _snap = i => _res[i].status === "fulfilled" ? _res[i].value : null;
          const _ok   = s2 => !!s2 && s2.exists();
          const ownSnap=_snap(0), wSnap=_snap(1), prvSnap=_snap(2), lotSnap=_snap(3), kidSnap=_snap(4), soldSnap=_snap(5);
          const kd = _ok(kidSnap) ? kidSnap.data() : {};
          setKidGroups(Array.isArray(kd.groups) ? kd.groups : []);
          setKidAssign(kd.assign && typeof kd.assign==="object" ? kd.assign : {});
          const sd = _ok(soldSnap) ? soldSnap.data() : {};
          setSoldLog(Array.isArray(sd.entries) ? sd.entries : []);
          setOwned(_ok(ownSnap) ? ownSnap.data() : {});
          setOwnedDocId(u.uid);
          // Live subscription so cards scanned on any device (e.g. phone) appear in
          // the collection here instantly, without a refresh.
          if (ownedUnsubRef.current) { try { ownedUnsubRef.current(); } catch(e){} }
          ownedUnsubRef.current = onSnapshot(doc(db,"boba_owned",u.uid), snap => {
            const newOwned = snap.exists() ? snap.data() : {};
            // CRITICAL: do NOT clobber local state while we still have unsaved changes queued.
            // Adding cards updates `owned` immediately and writes on a debounce — if a snapshot
            // lands in that window it carries the OLD server map, and blindly applying it wipes
            // the cards you just added (they'd appear, then vanish back into "missing"). Skip the
            // echo; our own pending write is the newer truth and will land momentarily.
            if (pendingOwnedRef.current || flushingRef.current) return;
            setOwned(newOwned);
            // If a card that was "on the way" is now owned (e.g. just scanned), clear its transit flag.
            setInTransit(prev => {
              const ids = Object.keys(prev).filter(id => newOwned[id]);
              if (ids.length === 0) return prev;
              const next = {...prev}; ids.forEach(id => delete next[id]);
              if (u) { try { setDoc(doc(db,"boba_intransit",u.uid), next); } catch(e){} }
              return next;
            });
          });
          try {
            const trkSnap = await getDoc(doc(db,"boba_trackers",u.uid));
            const list = (trkSnap.exists() && Array.isArray(trkSnap.data().trackers)) ? trkSnap.data().trackers : [];
            setCustomTrackers(list);
            try { localStorage.setItem("customTrackers_v1", JSON.stringify(list)); } catch {}
          } catch(e){}
          setWantList(_ok(wSnap) ? wSnap.data() : {});
          try { const trSnap = await getDoc(doc(db,"boba_intransit",u.uid)); setInTransit(trSnap.exists() ? trSnap.data() : {}); transitLoadedRef.current = true; } catch(e){}
          setPublicCards(_ok(prvSnap) ? prvSnap.data() : {});
          try { const tbSnap = await getDoc(doc(db,"boba_tradebait",u.uid)); setTradeBait(tbSnap.exists() ? tbSnap.data() : {}); } catch(e){}
          try { const pxSnap = await getDoc(doc(db,"boba_proxy",u.uid)); setProxyCards(pxSnap.exists() ? (pxSnap.data()||{}) : {}); } catch(e) {}
          // Are they listed as a trade partner? Read the index doc itself rather than a flag on the
          // profile \u2014 the doc IS the truth, and a stale flag saying "public" when no index exists
          // (or vice versa) would be worse than no flag at all.
          try { const tiSnap = await getDoc(doc(db,"trade_index",u.uid)); setTradePublic(tiSnap.exists()); } catch(e){ setTradePublic(false); }
          setLots(_ok(lotSnap) && Array.isArray(lotSnap.data().lots) ? lotSnap.data().lots : []);
          try { const umSnap = await getDoc(doc(db,"user_missing",u.uid)); setUserMissing(umSnap.exists() && Array.isArray(umSnap.data().cards) ? umSnap.data().cards : []); } catch(e){}
          try {
            const revSnap = await getDocs(query(collection(db,"boba_reviews"), where("buyerUid","==",u.uid)));
            setMyReviews(revSnap.docs.map(d=>({...d.data(),id:d.id})));
          } catch(re) { console.error("reviews load failed:", re); }
        } catch (e) {
          console.error("Collection load failed (check Firestore rules for boba_owned/wants/private/lots):", e);
          setOwnedDocId(u.uid);
        }
      } else {
        if (ownedUnsubRef.current) { try { ownedUnsubRef.current(); } catch(e){} ownedUnsubRef.current = null; }
        setOwned({}); setOwnedDocId(null); setWantList({}); setPublicCards({}); setLots([]); setMyReviews([]); setMyUsername(""); setMyPhotoURL(""); usernameClaimedThisSession.current=false; setUserMissing([]);
        try { localStorage.removeItem("boba_owned_cache_v1"); } catch(e){}
      }
    });
  }, []);

  // -- Friends/teams listeners --
  useEffect(() => {
    if (!user) return;
    const uid2 = user.uid;
    const unsubs = [
      onSnapshot(query(collection(db,"friend_requests"), where("from","==",uid2), where("status","==","accepted")),
        snap => setFriends(prev => { const ff=snap.docs.map(d=>{ const fb=d.data().familyBy||[]; const them=d.data().to;
          // isFamily needs BOTH uids. familyBy alone used to be enough \u2014 that was the hole.
          return {...d.data(),id:d.id,friendUid:them,friendName:d.data().toName||d.data().toEmail,
                  isFamily: fb.includes(uid2) && fb.includes(them),
                  iShared: fb.includes(uid2), theyShared: fb.includes(them)}; }); return [...ff,...prev.filter(f=>f._dir==="to")]; })
      ),
      onSnapshot(query(collection(db,"friend_requests"), where("to","==",uid2), where("status","==","accepted")),
        snap => setFriends(prev => { const tf=snap.docs.map(d=>{ const fb=d.data().familyBy||[]; const them=d.data().from;
          return {...d.data(),id:d.id,friendUid:them,friendName:d.data().fromName||d.data().fromEmail,_dir:"to",
                  isFamily: fb.includes(uid2) && fb.includes(them),
                  iShared: fb.includes(uid2), theyShared: fb.includes(them)}; }); return [...prev.filter(f=>f._dir!=="to"),...tf]; })
      ),
      onSnapshot(query(collection(db,"friend_requests"), where("to","==",uid2), where("status","==","pending")),
        snap => setFriendReqs(snap.docs.map(d=>({...d.data(),id:d.id})))
      ),
      onSnapshot(query(collection(db,"friend_requests"), where("from","==",uid2), where("status","==","pending")),
        snap => setSentReqs(snap.docs.map(d=>({...d.data(),id:d.id})))
      ),
      onSnapshot(query(collection(db,"teams"), where("memberUids","array-contains",uid2)),
        snap => { const t=snap.docs.map(d=>({...d.data(),id:d.id})); setTeams(t); const live=t.filter(x=>x.status!=="deleted"); setActiveTeam(prev=>{ if(prev && prev.status!=="deleted" && live.some(x=>x.id===prev.id)) return prev; return live[0]||null; }); }
      ),
      // Submitted team decks for any team I'm on (privacy-safe: only decks members chose to submit)
      onSnapshot(query(collection(db,"team_decks"), where("memberUids","array-contains",uid2)),
        snap => { const m={}; snap.docs.forEach(d=>{ const v=d.data(); m[`${v.teamId}_${v.uid}`]=v; }); setTeamDecks(m); }
      ),
      // Swap offers involving my teams (I can help, or someone offered to help me)
      onSnapshot(query(collection(db,"swap_offers"), where("memberUids","array-contains",uid2)),
        snap => setSwapOffers(snap.docs.map(d=>({...d.data(),id:d.id})))
      ),
      // Borrowed-card ledger for my teams (who lent what to whom)
      onSnapshot(query(collection(db,"team_loans"), where("memberUids","array-contains",uid2)),
        snap => setTeamLoans(snap.docs.map(d=>({...d.data(),id:d.id})))
      ),
      onSnapshot(query(collection(db,"team_invites"), where("toUid","==",uid2), where("status","==","pending")),
        snap => setTeamInvites(snap.docs.map(d=>({...d.data(),id:d.id})))
      ),
      // Marketplace: my listings only (logged-in user)
      onSnapshot(query(collection(db,"marketplace"), where("status","==","active"), where("sellerUid","==",uid2)),
        snap => setMyListings(snap.docs.map(d=>({...d.data(),id:d.id})))
      ),
      // Marketplace notifications for me (offers on my listings)
      onSnapshot(query(collection(db,"market_offers"), where("awaitingUid","==",uid2), where("notified","==",false)),
        snap => setMarketNotifs(snap.docs.map(d=>({...d.data(),id:d.id})))
      ),
      onSnapshot(query(collection(db,"market_offers"), where("awaitingUid","==",uid2), where("status","==","pending")),
        snap => setAllMyOffers(snap.docs.map(d=>({...d.data(),id:d.id})).sort((a,b)=>(b.offerAmount||0)-(a.offerAmount||0)))
      ),
      // Want notifications -- someone listed a card I want
      onSnapshot(query(collection(db,"market_notifs"), where("toUid","==",uid2), where("read","==",false)),
        snap => setWantNotifs(snap.docs.map(d=>({...d.data(),id:d.id})))
      ),
      // Deal threads
      onSnapshot(query(collection(db,"deal_threads"), where("memberUids","array-contains",uid2)),
        snap => {
          const threads = snap.docs
            .map(d=>({...d.data(),id:d.id}))
            .sort((a,b)=>(b.lastMessageAt||"").localeCompare(a.lastMessageAt||""));
          setThreads(threads);
          // A thread never opened has NO read stamp, and `undefined < "2026-..."` is false in JS \u2014 so
          // brand-new conversations, the ones you most need telling about, never counted as unread.
          // Treat a missing stamp as "not read yet".
          setUnreadThreads(threads.filter(t => {
            if (!t.lastMessageAt) return false;
            if (t.lastSenderUid === uid2) return false;      // your own message isn't news
            const seen = t.lastReadBy?.[uid2];
            return !seen || String(seen) < String(t.lastMessageAt);
          }).length);
        }
      ),
      // Recent market sales (last 200)
      onSnapshot(query(collection(db,"market_sales"), orderBy("soldAt","desc"), limit(200)),
        snap => setMarketSales(snap.docs.map(d=>({...d.data(),id:d.id})))
      ),
      // Trades where I'm either side. Two listeners because Firestore can't OR across fields.
      onSnapshot(query(collection(db,"trade_offers"), where("fromUid","==",uid2)),
        snap => setTradeOffers(prev => {
          const mine = snap.docs.map(d=>({...d.data(),id:d.id}));
          return [...mine, ...prev.filter(t => t.toUid === uid2)];
        })
      ),
      onSnapshot(query(collection(db,"trade_offers"), where("toUid","==",uid2)),
        snap => setTradeOffers(prev => {
          const theirs = snap.docs.map(d=>({...d.data(),id:d.id}));
          return [...prev.filter(t => t.fromUid === uid2), ...theirs];
        })
      ),
    ];
    return () => unsubs.forEach(u=>u());
  }, [user]);

  // Load friend owned maps
  useEffect(() => {
    if (!friends.length) return;
    friends.forEach(async f => {
      if (friendOwned[f.friendUid]) return;
      const snap = await getDoc(doc(db,"boba_owned",f.friendUid));
      if (snap.exists()) setFriendOwned(prev=>({...prev,[f.friendUid]:snap.data()}));
    });
  }, [friends]);

  // Family members' BORROWABLE cards, computed LIVE = owned but not in any of their saved decks.
  // Uses real-time subscriptions so scanning a card (updates boba_owned) or editing a deck
  // (updates boba_decks) instantly recomputes what's available to borrow — just like your own
  // collection updates live.
  const familyRawRef = useRef({}); // { famUid: { owned:{}, locked:{}, name } }
  const familyRecomputeRef = useRef(null); // debounce the availability rebuild during the load burst
  useEffect(() => {
    const familyMembers = friends.filter(f => f.isFamily);
    if (!familyMembers.length) { familyRawRef.current = {}; setFamilyAvail({}); setFamilyOwnedRaw({}); return; }

    // Recompute the derived availability map from whatever raw data we currently have.
    const recompute = () => {
      const next = {};
      const ownProv = {};   // provenance: who OWNS each card, regardless of lock/deck status
      Object.entries(familyRawRef.current).forEach(([famUid, raw]) => {
        const theirOwned = raw.owned || {};
        const locked = raw.locked || {};
        const avail = {};
        Object.keys(theirOwned).forEach(cid => {
          const cleanId = String(cid).replace(/::foil$/,"");
          const copies = parseInt(theirOwned[cid])||1;
          const lockedN = locked[cleanId]||0;
          if (copies > lockedN) avail[cleanId] = copies - lockedN;
          // Provenance keeps EVERY owned card (first owner wins), even fully-locked ones,
          // so the pick list can say who has a card even when it isn't free to borrow.
          if (copies > 0 && !ownProv[cleanId]) ownProv[cleanId] = { uid: famUid, name: raw.name };
        });
        next[famUid] = { ownerName: raw.name, cards: avail };
      });
      setFamilyAvail(next);
      setFamilyOwnedRaw(ownProv);
    };
    // Coalesce the flurry of listener callbacks on first load into a single rebuild.
    const scheduleRecompute = () => {
      if (familyRecomputeRef.current) clearTimeout(familyRecomputeRef.current);
      familyRecomputeRef.current = setTimeout(recompute, 60);
    };

    // Seed raw entries and subscribe two live listeners per family member.
    const unsubs = [];
    familyMembers.forEach(f => {
      const uid2 = f.friendUid;
      if (!familyRawRef.current[uid2]) familyRawRef.current[uid2] = { owned:{}, locked:{}, name:f.friendName };
      else familyRawRef.current[uid2].name = f.friendName;

      // Live: their owned collection
      unsubs.push(onSnapshot(doc(db,"boba_owned",uid2), snap => {
        familyRawRef.current[uid2] = { ...(familyRawRef.current[uid2]||{name:f.friendName,locked:{}}), owned: snap.exists()?snap.data():{} };
        scheduleRecompute();
      }));
      // Live: their saved decks → which card copies are locked
      unsubs.push(onSnapshot(query(collection(db,"boba_decks"), where("userId","==",uid2)), snap => {
        const lockedCount = {};
        snap.forEach(d => { const seen=new Set(); (d.data().cardIds||[]).forEach(cid=>{ if(seen.has(cid))return; seen.add(cid); lockedCount[cid]=(lockedCount[cid]||0)+1; }); });
        familyRawRef.current[uid2] = { ...(familyRawRef.current[uid2]||{name:f.friendName,owned:{}}), locked: lockedCount };
        scheduleRecompute();
      }));
    });

    // Drop raw entries for anyone no longer family.
    const stillFamily = new Set(familyMembers.map(f=>f.friendUid));
    Object.keys(familyRawRef.current).forEach(uid2 => { if(!stillFamily.has(uid2)) delete familyRawRef.current[uid2]; });
    scheduleRecompute();

    return () => { unsubs.forEach(u => { try{u();}catch(e){} }); if(familyRecomputeRef.current) clearTimeout(familyRecomputeRef.current); };
  }, [friends.filter(f=>f.isFamily).map(f=>f.friendUid).sort().join(",")]);// eslint-disable-line react-hooks/exhaustive-deps
  // ── Cards of MINE that are locked into a deck ────────────────────────────────────────────────
  // A card committed to a deck — yours OR a family member's — shouldn't be tradeable out from under
  // that deck. Trading it away silently breaks the deck; if it's in a FAMILY deck, you've pulled a
  // card out from under someone else who was counting on it.
  //
  // deckLockedForTrade: cardId -> how many of my copies are spoken for by decks. It counts each
  // deck as using one copy, and dedupes a card appearing twice in the same list. Family decks are
  // readable (boba_decks is public-read), so we just watch all of theirs plus all of mine.
  const [deckLockedForTrade, setDeckLockedForTrade] = useState({});
  // Same lock, but WHICH decks. cardId -> [{name, owner, mine}] so we can tell you the deck by name
  // instead of just "it's in a deck somewhere, go find it".
  const [deckLockNames, setDeckLockNames] = useState({});
  useEffect(() => {
    if (!user) { setDeckLockedForTrade({}); setDeckLockNames({}); return; }
    const famUids = friends.filter(f => f.isFamily).map(f => f.friendUid);
    const owners = [user.uid, ...famUids];
    const famName = uid => (friends.find(f=>f.friendUid===uid)?.friendName) || "Family";
    const perOwner = {};   // ownerUid -> { cardId: usedCount }
    const perOwnerDecks = {};   // ownerUid -> { cardId: [deckName,...] }
    const recount = () => {
      const total = {};
      const names = {};
      // My own cards, whoever's deck they sit in. Only MY cards can be locked FOR ME — a family
      // member's own cards in their own deck aren't mine to trade anyway.
      Object.entries(perOwner).forEach(([ouid, map]) => {
        Object.entries(map).forEach(([cid, n]) => {
          if (!owned[cid]) return;   // only count cards I actually own
          total[cid] = (total[cid] || 0) + n;
          const mine = ouid === user.uid;
          (perOwnerDecks[ouid]?.[cid] || []).forEach(dn => {
            (names[cid] = names[cid] || []).push({ name: dn, owner: mine ? null : famName(ouid), mine });
          });
        });
      });
      setDeckLockedForTrade(total);
      setDeckLockNames(names);
    };
    const unsubs = owners.map(ouid =>
      onSnapshot(query(collection(db,"boba_decks"), where("userId","==",ouid)), snap => {
        const map = {};
        const decks = {};
        snap.forEach(d => {
          const dn = d.data().name || "Untitled deck";
          const seen = new Set();
          (d.data().cardIds || []).forEach(cid => {
            if (seen.has(cid)) return;   // a deck uses at most one copy of a card
            seen.add(cid);
            map[cid] = (map[cid] || 0) + 1;
            (decks[cid] = decks[cid] || []).push(dn);
          });
        });
        perOwner[ouid] = map;
        perOwnerDecks[ouid] = decks;
        recount();
      }, e => console.error("deck-lock listen failed:", e))
    );
    return () => unsubs.forEach(u => { try{u();}catch(e){} });
  }, [user, friends.filter(f=>f.isFamily).map(f=>f.friendUid).sort().join(","), owned]);

  // Copies NOT available to trade away, per card. A card is un-tradeable if it's
  // committed to a deck, lent out (physically not in your hands), or borrowed (not
  // yours to trade at all). This is the count the "free to trade" logic subtracts
  // from owned — so lent and borrowed copies stop showing as free, and a card whose
  // every copy is spoken for drops out of trade entirely.
  //   deck-locked + lent-out + borrowed  >= owned  ->  nothing free
  // Borrowed copies inflate `owned` (addBorrowedCopy bumps it so the card shows in
  // the grid), so counting them here cancels that back out for trade purposes.
  const tradeLocked = useMemo(() => {
    const out = { ...(deckLockedForTrade || {}) };
    Object.entries(lentByCard || {}).forEach(([id,n]) => { out[id] = (out[id]||0) + n; });
    Object.entries(borrowedByCard || {}).forEach(([id,n]) => { out[id] = (out[id]||0) + n; });
    return out;
  }, [deckLockedForTrade, lentByCard, borrowedByCard]);

  // Family members' saved decks, grouped by member, for the "Family" toggle in the deck builder.
  // boba_decks is public-read, so this is a scoped watch per family member. Read-only for us.
  const [familyDecks, setFamilyDecks] = useState([]);
  useEffect(() => {
    if (!user) { setFamilyDecks([]); return; }
    const fam = friends.filter(f => f.isFamily);
    if (!fam.length) { setFamilyDecks([]); return; }
    const byUid = {};
    const unsubs = fam.map(f =>
      onSnapshot(query(collection(db,"boba_decks"), where("userId","==",f.friendUid)), snap => {
        byUid[f.friendUid] = { friendUid:f.friendUid, friendName:f.friendName||"Family",
          decks: snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.savedAt||"").localeCompare(a.savedAt||"")) };
        setFamilyDecks(Object.values(byUid).filter(g => g.decks.length > 0));
      }, e => console.error("family decks listen failed:", e))
    );
    return () => unsubs.forEach(u => { try{u();}catch(e){} });
  }, [user, friends.filter(f=>f.isFamily).map(f=>f.friendUid).sort().join(",")]);



  // Borrow ledger: card loans between me and family (as borrower or owner).
  useEffect(() => {
    if(!user) return;
    const unsub = onSnapshot(query(collection(db,"borrow_ledger"), where("participantUids","array-contains",user.uid)),
      snap => setBorrowLedger(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.borrowedAt||"").localeCompare(a.borrowedAt||"")))
    );
    return ()=>unsub();
  }, [user]);

  // Auto paper-trail: whenever the working deck contains a family member's card, ensure a
  // borrow_ledger entry exists (borrower=me, owner=them). Remove entries for family cards no
  // longer in the deck. Keyed by borrower+card so it's idempotent.
  useEffect(() => {
    if (!user) return;
    // Build owner lookup fresh (familyAvail may have updated). A card counts as "borrowed from
    // family" when you have no FREE copy of your own — i.e. you don't own it, or all your copies
    // are committed to your OTHER saved decks — matching the deck builder's rule.
    const usedInOtherDecks = {};
    (savedDecks||[]).forEach(d => {
      if (d.id === deckLoadId) return;
      const seen = new Set();
      (d.cardIds||[]).forEach(cid => { if(seen.has(cid))return; seen.add(cid); usedInOtherDecks[cid]=(usedInOtherDecks[cid]||0)+1; });
    });
    const ownerByCard = {};
    Object.entries(familyAvail).forEach(([famUid, info]) => {
      Object.keys(info.cards||{}).forEach(cid => {
        if (ownerByCard[cid]) return;
        const myCopies = (owned && owned[cid]) ? (parseInt(owned[cid])||1) : 0;
        const myFree = Math.max(0, myCopies - (usedInOtherDecks[cid]||0));
        if (myFree <= 0) ownerByCard[cid] = { uid:famUid, name:info.ownerName };
      });
    });
    const t = setTimeout(async () => {
      try {
        // Family cards currently in my deck
        const inDeckFamily = deckCards.filter(cid => ownerByCard[cid]);
        // Desired ledger doc ids
        const want = new Set(inDeckFamily.map(cid => `${user.uid}_${cid}`));
        // Read my current active borrow entries
        const snap = await getDocs(query(collection(db,"borrow_ledger"), where("borrowerUid","==",user.uid), where("status","==","borrowed")));
        const have = new Set();
        snap.forEach(d => {
          have.add(d.id);
          if (!want.has(d.id)) { // no longer in deck → mark returned
            setDoc(doc(db,"borrow_ledger",d.id),{status:"returned",returnedAt:new Date().toISOString()},{merge:true}).catch(()=>{});
          }
        });
        // Add new borrows
        for (const cid of inDeckFamily) {
          const id = `${user.uid}_${cid}`;
          if (have.has(id)) continue;
          const card = cards.find(x=>x.id===cid);
          const owner = ownerByCard[cid];
          await setDoc(doc(db,"borrow_ledger",id),{
            id, borrowerUid:user.uid, borrowerName:user.displayName||user.email,
            ownerUid:owner.uid, ownerName:owner.name,
            cardId:cid, cardLabel: card?`${card.hero} ${card.power}⚡ ${card.treatment||""}`.trim():cid,
            cardImage: card?.imageUrl||"",
            deckName: deckName||"(unsaved deck)",
            status:"borrowed", borrowedAt:new Date().toISOString(),
            participantUids:[user.uid, owner.uid],
          },{merge:true});
        }
      } catch(e){ console.error("borrow ledger sync failed:",e); }
    }, 800);
    return ()=>clearTimeout(t);
  }, [deckCards, familyAvail, user, deckName, savedDecks, deckLoadId]);

  // Upsert profile on login
  useEffect(() => {
    if (!user) return;
    setDoc(doc(db,"boba_profiles",user.uid), {displayName:user.displayName||"Collector",photoURL:user.photoURL||"",lastSeen:new Date().toISOString()},{merge:true});
  }, [user]);

  // PERF: a single id → card lookup, built once per card load. Several effects below need to check
  // "is this owned id a real card?" for every owned key — doing that with cards.find() is an O(n×m)
  // scan through 36k cards per key, which is what made adding cards crawl. Map lookups are O(1).
  const cardById = useMemo(() => {
    const m = new Map();
    for (const c of cards) m.set(c.id, c);
    return m;
  }, [cards]);
  const baseCardId = id => String(id).replace(/::foil$/,"");
  // Cards grouped by hero (for rainbow completion). Built once per card load, not per owned change.
  const heroGroups = useMemo(() => {
    const g = {};
    for (const c of cards) {
      if (!c.hero) continue;
      if ((c.treatment||"").toLowerCase() === "home team discount") continue;
      (g[c.hero] = g[c.hero] || []).push(c);
    }
    return Object.values(g);
  }, [cards]);

  // Write denormalized collection stats to the user doc (powers the leaderboard) — debounced
  useEffect(() => {
    if (!user || !cards.length) return;
    const t = setTimeout(async () => {
      try {
        const ownedIds = Object.keys(owned).filter(id => cardById.has(baseCardId(id)));
        const collectionCount = ownedIds.reduce((s,id)=>s+(owned[id]||1),0);
        const uniqueCount = ownedIds.length;
        // What counts as a 1/1. This used to look only for "1/1" in the notation or card number,
        // which missed two whole categories that are one-of-ones by definition:
        //   • Super weapon — Superfoil is a one-of-one by design (see the weapon guide)
        //   • Pink Battlefoil — that insert is a 1/1
        // Kept as a named helper so the leaderboard and any future tracker agree on the rule.
        const oneOfOneCount = ownedIds.filter(id => isOneOfOne(cardById.get(baseCardId(id)))).length;
        // rainbow groups fully owned (heroGroups is prebuilt once per card load — see above)
        let rainbowCount = 0;
        heroGroups.forEach(g => { if(g.length>=2 && g.every(c=>owned[c.id])) rainbowCount++; });
        await setDoc(doc(db,"users",user.uid), { collectionCount, uniqueCount, oneOfOneCount, rainbowCount, statsUpdatedAt: Date.now() }, { merge:true });
      } catch(e) { console.error("stats denorm failed:", e); }
    }, 1500);
    return () => clearTimeout(t);
  }, [owned, cardById, heroGroups, user]);

  // Detect collection milestones -> celebratory toast
  useEffect(() => {
    if (!user || !cards.length) return;
    const owNow = Object.keys(owned).filter(id=>cardById.has(baseCardId(id))).reduce((sum,id)=>sum+(owned[id]||1),0);
    const COUNT_MILES = [10,25,50,100,150,200,250,300,400,500,750,1000];
    const pct = cards.length>0 ? (owNow/cards.length*100) : 0;
    const PCT_MILES = [10,25,50,75,90,100];
    // First run: snapshot, don't fire
    if (lastMilestoneRef.current === null) { lastMilestoneRef.current = { count:owNow, pct }; return; }
    const prev = lastMilestoneRef.current;
    let hit = null;
    for (const m of COUNT_MILES) { if (prev.count < m && owNow >= m) hit = { label:`${m} CARDS!`, sub:"Your collection is growing 🔥" }; }
    for (const m of PCT_MILES) { if (prev.pct < m && pct >= m) hit = { label:`${m}% COMPLETE!`, sub: m>=100 ? "You collected them ALL 🏆" : "Keep hunting 🎯" }; }
    lastMilestoneRef.current = { count:owNow, pct };
    if (hit) { setMilestone(hit); setTimeout(()=>setMilestone(null), 3200); }
  }, [owned, cards, user]);

  // Detect rainbow completion -> grand celebration
  useEffect(() => {
    if (!cards.length) return;
    // Match the rainbow tab's hero-grouping: exclude "home team discount", group by hero
    const groups = {};
    cards.forEach(c => {
      const t = (c.treatment||"").toLowerCase();
      if (t === "home team discount") return;
      const key = c.hero;
      if (!key) return;
      (groups[key] = groups[key] || []).push(c);
    });
    // A real rainbow needs at least 2 cards to be meaningful
    const nowComplete = new Set();
    Object.entries(groups).forEach(([key, gcards]) => {
      if (gcards.length < 2) return;
      if (gcards.every(c => owned[c.id])) nowComplete.add(key);
    });
    const prev = completedRainbowsRef.current;
    // First run: just snapshot, don't celebrate existing completions
    if (prev === null) { completedRainbowsRef.current = nowComplete; return; }
    // Find newly completed
    let newlyDone = null;
    nowComplete.forEach(k => { if (!prev.has(k)) newlyDone = k; });
    completedRainbowsRef.current = nowComplete;
    if (newlyDone) {
      setCardFx(null); // suppress the small "caught" FX in favor of the grand celebration
      setRainbowFx({ name: newlyDone, cards: groups[newlyDone] });
    }
  }, [owned, cards]);

  // Infinite scroll (rAF-throttled; stops bumping page once everything is shown).
  // Reads the current filtered length from a ref so this effect never depends on
  // `filtered` (which is declared later — referencing it here caused a TDZ crash).
  //
  // HARD CEILING: without one, scrolling through 35k cards mounts 35k card components and never
  // unmounts any of them. Every subsequent state change then has to re-render the lot — which is
  // what made the page take ~6 SECONDS to render, getting worse the longer you scrolled. Cap the
  // number of pages we'll ever paint; past that, narrow the search instead of scrolling forever.
  const filteredLenRef = useRef(0);
  useEffect(() => {
    let ticking = false;
    function check() {
      ticking = false;
      if (document.documentElement.scrollHeight-window.scrollY-window.innerHeight<600) {
        setPage(p => {
          if (p >= MAX_PAGES) return p;                       // don't grow the DOM without bound
          if (p*PAGE_SIZE >= filteredLenRef.current) return p; // everything's already shown
          return p+1;
        });
      }
    }
    function onScroll() { if(!ticking){ ticking=true; requestAnimationFrame(check); } }
    window.addEventListener("scroll",onScroll,{passive:true});
    return ()=>window.removeEventListener("scroll",onScroll);
  }, []);

  // -- Helpers --
  async function saveCustomTracker() {
    const name = builderName.trim();
    if (!name || builderTreatments.length === 0) return;
    const tid = (activeTrackerId && showTrackerBuilder==="edit") ? activeTrackerId : (Date.now().toString(36)+Math.random().toString(36).slice(2,7));
    const existing = customTrackers.find(t => t.id === tid);
    const tracker = { id: tid, name, treatments: builderTreatments, public: existing?.public || false };
    const exists = !!existing;
    const next = exists ? customTrackers.map(t => t.id===tracker.id ? tracker : t) : [...customTrackers, tracker];
    setCustomTrackers(next);
    try { localStorage.setItem("customTrackers_v1", JSON.stringify(next)); } catch {}
    if (user) { try { await setDoc(doc(db,"boba_trackers",user.uid), { trackers: next }); } catch(e) {} }
    setShowTrackerBuilder(false); setBuilderName(""); setBuilderTreatments([]);
    setActiveTrackerId(tracker.id);
  }
  // Update ONLY a tracker's treatment list (used by the orphan re-match). Never touches boba_owned
  // or boba_intransit — it just teaches the tracker which treatments to gather, so cards whose
  // treatment text drifted (e.g. a snapshot rename) come back into the count. The marks were safe
  // the whole time; they were attached to cards that stopped matching, not deleted.
  async function saveTrackerTreatments(id, treatments) {
    const next = customTrackers.map(t => t.id===id ? { ...t, treatments } : t);
    setCustomTrackers(next);
    try { localStorage.setItem("customTrackers_v1", JSON.stringify(next)); } catch {}
    if (user) { try { await setDoc(doc(db,"boba_trackers",user.uid), { trackers: next }); } catch(e){ console.error("save tracker treatments failed:", e); } }
  }
  async function toggleTrackerPublic(id) {
    const next = customTrackers.map(t => t.id===id ? { ...t, public: !t.public } : t);
    setCustomTrackers(next);
    try { localStorage.setItem("customTrackers_v1", JSON.stringify(next)); } catch {}
    if (user) { try { await setDoc(doc(db,"boba_trackers",user.uid), { trackers: next }); } catch(e) {} }

    // Making a rainbow/tracker public should make the cards it covers public too — that's the
    // whole point. Recompute the set of "owned cards covered by any PUBLIC tracker" and union
    // it with cards the user made public individually.
    try {
      const treatmentsOfPublicTrackers = new Set();
      next.filter(t=>t.public).forEach(t => (t.treatments||[]).forEach(tr => treatmentsOfPublicTrackers.add(tr)));
      // Which currently-public cards were auto-added by a tracker (so we can drop them when
      // the tracker goes private). We tag them by keeping a separate record.
      const trackerCoveredIds = new Set(
        cards.filter(c => owned[c.id] && treatmentsOfPublicTrackers.has(c.treatment)).map(c=>c.id)
      );
      const manualIds = new Set(Object.keys(publicCards).filter(pid => publicCards[pid] && !trackerAutoPublic[pid]));
      const nextPublic = {};
      manualIds.forEach(pid => { nextPublic[pid] = true; });
      trackerCoveredIds.forEach(pid => { nextPublic[pid] = true; });
      // Remember which ids are tracker-driven so future toggles can distinguish them from manual.
      const nextAuto = {}; trackerCoveredIds.forEach(pid => { nextAuto[pid] = true; });
      setTrackerAutoPublic(nextAuto);
      try { localStorage.setItem("trackerAutoPublic_v1", JSON.stringify(nextAuto)); } catch {}
      setPublicCards(nextPublic);
      await persistPublicCards(nextPublic);
    } catch(e){ console.error("tracker auto-public failed:", e); }
  }
  async function deleteCustomTracker(id) {
    if (!window.confirm("Delete this tracker?")) return;
    const next = customTrackers.filter(t => t.id !== id);
    setCustomTrackers(next);
    try { localStorage.setItem("customTrackers_v1", JSON.stringify(next)); } catch {}
    if (user) { try { await setDoc(doc(db,"boba_trackers",user.uid), { trackers: next }); } catch(e) {} }
    if (activeTrackerId === id) setActiveTrackerId(null);
  }

  // PERF: adding cards fast was laggy because EVERY tap awaited a Firestore write of the whole
  // owned map (hundreds of entries re-uploaded per tap). Now the UI updates instantly and the
  // save is debounced — rapid input coalesces into one write ~600ms after you stop.
  function queueOwnedSave(nextOwned) {
    if (!user) return;
    pendingOwnedRef.current = nextOwned;
    if (ownedSaveRef.current) clearTimeout(ownedSaveRef.current);
    ownedSaveRef.current = setTimeout(() => { flushOwnedSave(); }, 500);
  }

  // Record the chosen print, then add the card. The version lives on a per-copy LOT rather than on
  // the card, so one card can hold two originals and an update at the same time.
  // Takes a map of {version: howMany}, so one prompt can record "4 Alpha and 3 Update" in a single
  // pass. Each copy gets its own lot carrying its version, which is what lets the detail view show
  // the split later.
  async function confirmVersion(counts) {
    const ask = versionAsk;
    if (!ask) return;
    const opts = ask.spec.options;
    const picked = opts
      .map(o => ({ version:o, n: Math.max(0, parseInt(counts?.[o]) || 0) }))
      .filter(x => x.n > 0);
    const adding = picked.reduce((s,x)=>s+x.n, 0);
    if (!adding) return;
    setVersionAsk(null);

    const cardId = ask.card.id;
    let saved = null;
    setOwned(prev => {
      const next = { ...prev };
      next[cardId] = (parseInt(next[cardId]) || 0) + adding;
      saved = next;
      return next;
    });
    if (saved) queueOwnedSave(saved);

    const newLots = [];
    picked.forEach(({version, n}) => {
      for (let i=0; i<n; i++) {
        newLots.push({ id: uid(), cardId, version, cost:null, value:null,
                       method:"purchased", date: todayLocal(), notes:"", serial:"" });
      }
    });
    const nextLots = [...(lots||[]), ...newLots];
    setLots(nextLots);
    if (user) { try { await setDoc(doc(db,"boba_lots",user.uid), { lots: nextLots }); } catch(e){ console.error("save version lots failed:", e); } }
    setCardFx({ type:"caught", card: ask.card });
  }

  // Write whatever's pending, right now. Safe to call repeatedly — it no-ops when there's nothing
  // queued. Retries once on failure so a flaky mobile connection doesn't silently eat a card.
  async function flushOwnedSave(attempt = 0) {
    if (!user || flushingRef.current) return;
    const payload = pendingOwnedRef.current;
    if (!payload) return;
    flushingRef.current = true;
    if (ownedSaveRef.current) { clearTimeout(ownedSaveRef.current); ownedSaveRef.current = null; }
    try {
      await setDoc(doc(db,"boba_owned",user.uid), payload);
      // Only clear the pending payload if it's still the one we just wrote — otherwise a newer
      // change came in while the write was in flight and must not be discarded.
      if (pendingOwnedRef.current === payload) pendingOwnedRef.current = null;
    } catch(e) {
      console.error("save owned failed:", e);
      flushingRef.current = false;
      // A permission-denied is not a connection problem and will never succeed on
      // retry — it means this account isn't approved for early access (or the
      // allowlist doesn't include their email). Tell them that plainly and stop.
      const denied = e && (e.code === "permission-denied" || /permission/i.test(e.message||""));
      if (denied) {
        pendingOwnedRef.current = null;   // don't keep retrying a write that can't succeed
        alert("Your account doesn't have access to save changes yet. Bazooka Dash is in early access — if you think this is a mistake, contact Bazooka Breaks to be added.");
        return;
      }
      if (attempt < 2) { setTimeout(()=>flushOwnedSave(attempt+1), 800 * (attempt+1)); return; }
      // Out of retries on a genuine network error — tell the user rather than silently losing their card.
      alert("Couldn't save your collection — check your connection. Your last few cards may not have saved.");
      return;
    }
    flushingRef.current = false;
    // A change arrived while we were writing — flush again so it isn't stranded.
    if (pendingOwnedRef.current) flushOwnedSave();
  }

  // Flush on ANY exit path. `beforeunload` is unreliable on mobile (iOS Safari and Android Chrome
  // largely ignore it), which was dropping writes when the app was backgrounded mid-burst.
  // `visibilitychange` → hidden is the one event mobile browsers do fire reliably.
  useEffect(() => {
    if (!user) return;
    const flush = () => { if (pendingOwnedRef.current) flushOwnedSave(); };
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
      flush();
    };
  }, [user]);

  async function toggleOwned(cardId) {
    if (!user) { setSigningIn(true); return; }
    const wasOwned = !!owned[cardId];
    // Adding a card that was printed twice? Ask which one before recording it. Only on the way IN \u2014
    // removing never needs a prompt, and asking again on every later copy would be tedious.
    if (!wasOwned) {
      const card = cards.find(x=>x.id===cardId);
      const spec = versionSpecFor(card);
      if (spec) { setVersionAsk({ card, spec, counts:{} }); return; }
    }
    // Functional update for the same reason as setOwnedQty: adding two cards in quick succession
    // must not have the second write built from a snapshot taken before the first.
    let saved = null;
    setOwned(prev => {
      const next = { ...prev };
      if (next[cardId]) delete next[cardId]; else next[cardId] = 1;
      saved = next;
      return next;
    });
    if (!wasOwned) { const card = cards.find(c=>c.id===cardId) || {id:cardId}; setCardFx({ type:"caught", card }); }
    if (saved) queueOwnedSave(saved);
  }
  async function setOwnedQty(cardId, qty) {
    if (!user) return;
    const wasOwned = !!owned[cardId];
    // The stepper "+" comes through here rather than toggleOwned, so the print question has to be
    // asked here too \u2014 otherwise adding from the grid stepper silently skips it. Only when the count
    // is going UP, and only for cards that have more than one print.
    const prevQty = parseInt(owned[cardId]) || 0;
    if (qty > prevQty) {
      const card = cards.find(x=>x.id===cardId);
      const spec = versionSpecFor(card);
      // Carry the target so a jump from 1 to 3 records the right number of copies, not just one.
      // The stepper may be jumping several at once; the prompt decides the split, so it just
      // opens empty rather than pre-filling a guess.
      if (spec) { setVersionAsk({ card, spec, counts:{} }); return; }
    }
    // FUNCTIONAL UPDATE, not `{...owned}`. Reading `owned` from the render closure meant two quick
    // clicks on different cards both built from the SAME stale snapshot \u2014 so adding Ice right after
    // Fire wrote a map that never had Fire in it, silently wiping the first card. Building from the
    // previous state guarantees each write sees every earlier one.
    let saved = null;
    setOwned(prev => {
      const next = { ...prev };
      if (qty <= 0) delete next[cardId]; else next[cardId] = qty;
      saved = next;
      return next;
    });
    if (!wasOwned && qty>0) { const card = cards.find(c=>c.id===cardId) || {id:cardId}; setCardFx({ type:"caught", card }); }
    // saved is assigned synchronously by the updater above, so this persists the merged map.
    if (saved) queueOwnedSave(saved);
  }

  // ── KID COLLECTIONS ────────────────────────────────────────────────────────────────────────
  async function saveKids(groups, assign) {
    setKidGroups(groups); setKidAssign(assign);
    if (!user) return;
    try { await setDoc(doc(db,"boba_kids",user.uid), { groups, assign }); }
    catch(e){ console.error("save kids failed:", e); }
  }
  function addKidGroup(name) {
    const clean = (name||"").trim();
    if (!clean) return null;
    const COLORS = ["#7B9CFF","#F472B6","#4ade80","#FBBF24","#C084FC","#38BDF8"];
    const g = { id:`kid_${Date.now()}`, name: clean, color: COLORS[kidGroups.length % COLORS.length] };
    saveKids([...kidGroups, g], kidAssign);
    return g.id;
  }
  function renameKidGroup(id, name) {
    saveKids(kidGroups.map(g=>g.id===id?{...g,name:(name||"").trim()||g.name}:g), kidAssign);
  }
  function removeKidGroup(id) {
    // Deleting a kid's collection only drops the TAGS — the cards themselves stay in your collection.
    const nextAssign = {...kidAssign};
    Object.keys(nextAssign).forEach(cid => { if (nextAssign[cid]===id) delete nextAssign[cid]; });
    saveKids(kidGroups.filter(g=>g.id!==id), nextAssign);
    if (kidFilter===id) setKidFilter("all");
  }
  // Tag a card to a kid (or pass null to make it yours again).
  // KEYED PER COPY, not per card: if you own 2 of something, one can be yours and one a kid's.
  // The key is `${cardId}#${copyIndex}` (0-based). A bare cardId key from before the per-copy
  // change is read as copy 0, so older tags still work.
  const copyKey = (cardId, i=0) => `${cardId}#${i}`;
  const kidOfCopy = (cardId, i=0) => kidAssign[copyKey(cardId,i)] ?? (i===0 ? (kidAssign[cardId] ?? null) : null);
  function assignCardToKid(cardId, groupId, copyIndex = 0) {
    const next = {...kidAssign};
    delete next[cardId];                                   // drop any legacy per-card key
    const k = copyKey(cardId, copyIndex);
    if (groupId) next[k] = groupId; else delete next[k];
    saveKids(kidGroups, next);
  }

  // ── SELL / TRADE AWAY ──────────────────────────────────────────────────────────────────────
  // Decrements the owned quantity (2 copies → 1) instead of deleting the card, and records how it
  // left. Only ACTIVE cards count toward your collection — but the ledger keeps the provenance, so
  // "did I ever own that?" always has an answer.
  async function sellCard(cardId, { qty=1, reason="sold", note="", price=null } = {}) {
    if (!user) { setSigningIn(true); return; }
    const have = parseInt(owned[cardId]) || 0;
    if (have <= 0) return;
    const n = Math.min(Math.max(1, parseInt(qty)||1), have);

    // 1. Drop the quantity (or remove the card entirely when the last copy goes).
    let nextOwned = null;
    setOwned(prev => {
      const nx = { ...prev };
      if (have - n <= 0) delete nx[cardId]; else nx[cardId] = have - n;
      nextOwned = nx;
      return nx;
    });
    if (nextOwned) queueOwnedSave(nextOwned);

    // 2. Keep the per-copy kid tags in step. Copies are indexed 0..qty-1, so when some leave we
    // drop the tags for the copies that no longer exist. (We remove from the end — the tags that
    // remain stay attached to the copies that remain.)
    const remaining = have - n;
    const nextTags = {...kidAssign};
    delete nextTags[cardId];                       // legacy per-card key
    for (let i = remaining; i < have; i++) delete nextTags[`${cardId}#${i}`];
    saveKids(kidGroups, nextTags);

    // 3. Write the paper trail.
    const entry = {
      id: `sold_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
      cardId, qty: n, reason,
      note: (note||"").trim(),
      price: price === null || price === "" ? null : (parseFloat(price)||0),
      date: new Date().toISOString(),
    };
    const nextLog = [entry, ...soldLog];
    setSoldLog(nextLog);
    try { await setDoc(doc(db,"boba_sold",user.uid), { entries: nextLog }); }
    catch(e){ console.error("save sold log failed:", e); }
  }

  // Mark an entire trade package as TRADED: log every card to the sold ledger and remove it from the
  // active collection in a SINGLE batched write (see the note inside about why we don't loop sellCard).
  async function markPackageTraded(pkg) {
    if (!pkg || !Array.isArray(pkg.items)) return;
    if (!user) { setSigningIn(true); return; }
    const note = pkg.title ? `Traded: ${pkg.title}` : "Traded (package)";
    // IMPORTANT: do NOT call sellCard() in a loop here. sellCard reads `owned`/`soldLog` from the
    // component closure, which does not update between iterations of a for-await loop — so each call
    // starts from the ORIGINAL state and the last setOwned/setSoldLog wins, logging out only one card.
    // Instead, apply every item to a single working copy of state and write once.
    const nextOwned = { ...owned };
    const nextTags  = { ...kidAssign };
    const newEntries = [];
    for (const it of pkg.items) {
      const cardId = it.id;
      const have = parseInt(nextOwned[cardId]) || 0;
      if (have <= 0) continue; // nothing to remove for this card
      const n = Math.min(Math.max(1, parseInt(it.qty) || 1), have);
      const remaining = have - n;
      if (remaining <= 0) delete nextOwned[cardId]; else nextOwned[cardId] = remaining;
      // Keep per-copy kid tags in step (drop tags for copies that no longer exist).
      delete nextTags[cardId];
      for (let i = remaining; i < have; i++) delete nextTags[`${cardId}#${i}`];
      newEntries.push({
        id: `sold_${Date.now()}_${Math.random().toString(36).slice(2,7)}_${cardId}`,
        cardId, qty: n, reason: "traded",
        note,
        price: (Number(it.value) || 0) || null,
        date: new Date().toISOString(),
      });
    }
    if (!newEntries.length) return; // nothing was actually owned
    // Apply once.
    setOwned(nextOwned);
    queueOwnedSave(nextOwned);
    saveKids(kidGroups, nextTags);
    const nextLog = [...newEntries, ...soldLog];
    setSoldLog(nextLog);
    try { await setDoc(doc(db,"boba_sold",user.uid), { entries: nextLog }); }
    catch(e){ console.error("save sold log (package) failed:", e); }
  }

  // Undo — puts a card back in your collection and removes the ledger entry.
  async function undoSell(entryId) {
    const entry = soldLog.find(e=>e.id===entryId);
    if (!entry || !user) return;
    const nextOwned = {...owned};
    nextOwned[entry.cardId] = (parseInt(nextOwned[entry.cardId])||0) + (entry.qty||1);
    setOwned(nextOwned);
    queueOwnedSave(nextOwned);
    const nextLog = soldLog.filter(e=>e.id!==entryId);
    setSoldLog(nextLog);
    try { await setDoc(doc(db,"boba_sold",user.uid), { entries: nextLog }); }
    catch(e){ console.error("undo sell failed:", e); }
  }

  const wantSaveRef = useRef(null);
  async function toggleWant(cardId) {
    if (!user) { setSigningIn(true); return; }
    const next = {...wantList};
    const wasWanted = !!next[cardId];
    if (next[cardId]) delete next[cardId]; else next[cardId]=1;
    setWantList(next);
    if (!wasWanted) { const card = cards.find(c=>c.id===cardId) || {id:cardId}; setCardFx({ type:"hunt", card }); }
    // Debounced, same reason as owned: don't await a full-map write on every tap.
    if (wantSaveRef.current) clearTimeout(wantSaveRef.current);
    wantSaveRef.current = setTimeout(async () => {
      try { await setDoc(doc(db,"boba_wants",user.uid), next); } catch(e){ console.error("save want failed:", e); }
    }, 600);
  }
  async function setTransit(cardId, data) {
    if (!user) { setSigningIn(true); return; }
    // Guard: never write until the doc has loaded at least once. Writing before load could push a
    // near-empty in-memory map over a full doc and wipe every mark (this is what ate cards before).
    if (!transitLoadedRef.current) {
      console.warn("setTransit blocked: in-transit not loaded yet");
      alert("Still loading your on-the-way cards \u2014 give it a second and try again.");
      return;
    }
    // Optimistic local update.
    const nextLocal = { ...inTransit };
    if (data === null) { delete nextLocal[cardId]; }
    else { nextLocal[cardId] = { qty: data.qty||1, from: data.from||"", note: data.note||"", date: data.date||new Date().toISOString().slice(0,10) }; }
    setInTransit(nextLocal);
    // Durable write: re-read the live doc and merge onto THAT, so a stale local map can't clobber
    // marks made elsewhere/recently. Only the single card we touched is changed.
    try {
      const snap = await getDoc(doc(db,"boba_intransit",user.uid));
      const remote = snap.exists() ? { ...snap.data() } : {};
      if (data === null) { delete remote[cardId]; }
      else { remote[cardId] = { qty: data.qty||1, from: data.from||"", note: data.note||"", date: data.date||new Date().toISOString().slice(0,10) }; }
      await setDoc(doc(db,"boba_intransit",user.uid), remote);
    } catch(e){ console.error("save transit failed:", e); alert("Couldn't save that on-the-way mark \u2014 check your connection and try again."); }
  }
  // Mark an in-transit card as arrived: bump owned qty and clear the transit flag
  async function markTransitArrived(cardId) {
    if (!user) return;
    const t = inTransit[cardId];
    const addQty = t?.qty || 1;
    const nextOwned = {...owned};
    nextOwned[cardId] = (parseInt(nextOwned[cardId])||0) + addQty;
    setOwned(nextOwned);
    try { await setDoc(doc(db,"boba_owned",user.uid), nextOwned); } catch(e){}
    await setTransit(cardId, null);
  }
  // Backfill: existing users who made cards public before denormalization get their
  // boba_public_cards snapshot written once, so their profile loads instantly.
  // Keep each user's denormalized public-cards snapshot fresh automatically. The public profile
  // page reads this snapshot (never the private collection), so anything not baked in here is
  // invisible to visitors. This runs on the user's OWN client — the only place their scan photos
  // (private boba_lots) are readable — and rewrites the snapshot whenever it's missing an image we
  // can now supply from a scan. No user action, no "refresh your collection" ask: it self-heals the
  // next time each person opens the app. Guarded so it only WRITES when something actually changed.
  const publicSnapshotSig = useRef("");
  useEffect(() => {
    if (!user || !cards.length) return;
    const publicIds = Object.keys(publicCards).filter(id=>publicCards[id]);
    if (publicIds.length === 0) return;
    // Index the catalog once (O(1) lookups). Doing cards.find() per public id was O(publicIds × 35k)
    // and re-ran on every cards/owned/publicCards/lots change — a real drag during deck building.
    const byId = {};
    for (const c of cards) byId[c.id] = c;
    // Build a lookup of my scan photos by card id (first photo wins).
    const scanByCard = {};
    for (const l of (lots||[])) { if (l && l.cardId && l.photoUrl && !scanByCard[l.cardId]) scanByCard[l.cardId] = l.photoUrl; }
    const enriched = publicIds.map(id => {
      const c = byId[id] || {};
      const img = c.imageUrl || scanByCard[id] || "";   // official art, else my scan, else nothing
      return { id, hero:c.hero||"", treatment:c.treatment||"", weapon:c.weapon||"", cardNum:c.cardNum||"", setName:c.setName||"", imageUrl:img };
    });
    // Only write if the content actually changed (avoids a write on every render / app open).
    const sig = JSON.stringify(enriched);
    if (sig === publicSnapshotSig.current) return;
    publicSnapshotSig.current = sig;
    (async () => {
      try { await setDoc(doc(db,"boba_public_cards",user.uid), { cards: enriched, updatedAt: new Date().toISOString() }); }
      catch(e){ /* non-fatal: snapshot just stays as-is until next load */ }
    })();
  }, [user, cards, publicCards, lots]);

  // Write both the public flags and the denormalized snapshot for a given publicCards map.
  async function persistPublicCards(nextPublic) {
    if (!user) return;
    await setDoc(doc(db,"boba_public",user.uid), nextPublic);
    try {
      const publicIds = Object.keys(nextPublic).filter(id=>nextPublic[id]);
      const enriched = publicIds.map(id => {
        const c = cards.find(x=>x.id===id) || {};
        // No official image? Use my scanned copy's photo so the public profile shows a real card.
        const scan = (lots||[]).find(l => l && l.cardId===id && l.photoUrl);
        const img = c.imageUrl || (scan ? scan.photoUrl : "");
        return { id, hero:c.hero||"", treatment:c.treatment||"", weapon:c.weapon||"", cardNum:c.cardNum||"", setName:c.setName||"", imageUrl:img };
      });
      await setDoc(doc(db,"boba_public_cards",user.uid), { cards: enriched, updatedAt: new Date().toISOString() });
    } catch(e){ console.error("public cards snapshot failed:", e); }
  }

  async function togglePrivate(cardId) {
    if (!user) return;
    const next = {...publicCards};
    if (next[cardId]) delete next[cardId]; else next[cardId]=true;
    setPublicCards(next);
    await persistPublicCards(next);
  }
  // -- Bulk selection helpers --
  function toggleSelect(cardId) {
    setSelectedIds(prev => { const n=new Set(prev); if(n.has(cardId)) n.delete(cardId); else n.add(cardId); return n; });
  }
  function clearSelection(){ setSelectedIds(new Set()); }
  function exitSelectMode(){
    setBulkQty(1);   // do not carry a quantity into the next selection
 setSelectMode(false); setSelectedIds(new Set()); setDragAnchor(null); }

  // ── Drag-select and shift-click ──────────────────────────────────────────────────────────────
  // Clicking 200 cards one at a time is nobody's idea of a good time. Two additions:
  //
  //   • DRAG  — press on a card and sweep across others; everything you pass over gets selected.
  //   • SHIFT — click one card, shift-click another, and the whole run between them is selected.
  //
  // Both work off the VISIBLE order (visibleCards), so a range means what you see on screen, not
  // some underlying array order. Dragging always ADDS — it never deselects — because a sweep that
  // silently unpicked things you'd already chosen would be maddening.
  const [dragging, setDragging] = useState(false);
  const [dragAnchor, setDragAnchor] = useState(null);   // last card clicked, for shift-ranges

  // Release the drag wherever the mouse comes up — including outside the grid, or off-window.
  useEffect(() => {
    if (!dragging) return;
    const end = () => setDragging(false);
    window.addEventListener("mouseup", end);
    window.addEventListener("mouseleave", end);
    return () => { window.removeEventListener("mouseup", end); window.removeEventListener("mouseleave", end); };
  }, [dragging]);

  function selectRangeTo(cardId, list) {
    if (!dragAnchor) { toggleSelect(cardId); setDragAnchor(cardId); return; }
    const ids = list.map(c => c.id);
    const a = ids.indexOf(dragAnchor), b = ids.indexOf(cardId);
    if (a === -1 || b === -1) { toggleSelect(cardId); setDragAnchor(cardId); return; }
    const [lo, hi] = a < b ? [a, b] : [b, a];
    setSelectedIds(prev => { const n = new Set(prev); for (let i=lo; i<=hi; i++) n.add(ids[i]); return n; });
  }
  // Bulk mark the selected cards as OWNED (or remove them). The single biggest time-saver in the
  // app: someone who has a full insert can filter to it, Select shown, and claim the lot in one tap
  // instead of clicking 60 cards one at a time. Adding only sets cards you don't already have, so
  // re-running it never inflates quantities on copies you'd already logged.
  // Flag or unflag the selected cards as proxies. Same select-then-act flow as bulk-owning, because
  // proxying is usually decided in batches — you get a run of slabs graded and none of them can be
  // played with.
  async function bulkSetProxy(makeProxy) {
    if (!user) { setSigningIn(true); return; }
    if (selectedIds.size === 0) return;
    // Bulk flagging works on EVERY copy you own of each selected card. Proxying is
    // decided in batches ("none of these graded slabs can be played"), and that
    // applies to the slab, not to one arbitrary copy. Notes stay per copy, so this
    // never overwrites a note someone already wrote.
    const next = {...proxyCards};
    let changed = 0;
    selectedIds.forEach(id => {
      const q = Math.max(1, parseInt(owned[id]) || 0);
      // Fold a legacy flat key into copy 0 so it can't linger alongside the new keys.
      if (next[id] !== undefined) {
        const k0 = `${id}#0`;
        if (next[k0] === undefined) next[k0] = next[id];
        delete next[id];
      }
      for (let ci = 0; ci < q; ci++) {
        const k = `${id}#${ci}`;
        if (makeProxy) { if (!next[k]) { next[k] = true; changed++; } }
        else { if (next[k]) { delete next[k]; changed++; } }
      }
    });
    setProxyCards(next);
    try { await setDoc(doc(db,"boba_proxy",user.uid), next); }
    catch(e) { alert("Couldn't save proxy flags: " + (e.message||e)); return; }
    setToast(changed
      ? `\u2713 ${changed} cop${changed===1?"y":"ies"} ${makeProxy?"marked as proxy":"no longer proxies"}`
      : "No changes \u2014 those were already set that way");
    clearSelection();
  }

  // Single-card toggle, for when you are looking at one card rather than working through a batch.
  // The stored value doubles as the note: `true` for a plain flag, or the note text itself. Every
  // existing check is a truthiness test, so a non-empty string keeps them all working while carrying
  // the extra detail \u2014 no migration, and old flags stay valid.
  // Proxy flags and notes are stored PER PHYSICAL COPY, not per card.
  //
  // You can own two of the same card — one in the Blokpax vault, one in hand — and the
  // whole point of a note is telling those two apart. Keying on cardId alone meant both
  // copies shared one note: writing "in the vault" on one made it appear on the other.
  //
  // The key is `${cardId}#${copyIndex}` (0-based), matching how kid tags already work.
  // A bare cardId key from before this change is read as copy 0, so existing notes
  // survive untouched and get rewritten to the new form the next time they're edited.
  const proxyKey = (cardId, i=0) => `${cardId}#${i}`;

  // Raw stored value for a copy: undefined | true | "note"
  const proxyRaw = (cardId, i=0) => {
    const v = proxyCards[proxyKey(cardId,i)];
    if (v !== undefined) return v;
    return i === 0 ? proxyCards[cardId] : undefined;   // legacy flat key = copy 0
  };

  // Is this specific copy flagged as a proxy?
  const isProxyCopy = (cardId, i=0) => !!proxyRaw(cardId, i);

  // Does ANY copy of this card carry a proxy flag? Used where a card is shown once
  // (the card grid, bulk selection) rather than per copy.
  const anyProxy = (cardId) => {
    if (proxyCards[cardId]) return true;
    // Scan the ACTUAL keys rather than guessing an upper bound on copy count. A fixed
    // ceiling (this was 12) silently hides a flag stored above it, which then drops the
    // card out of the IMC report entirely.
    const pre = cardId + "#";
    for (const k in proxyCards) {
      if (k.startsWith(pre) && proxyCards[k]) return true;
    }
    return false;
  };

  async function toggleProxy(cardId, note, copyIndex = 0) {
    if (!user) { setSigningIn(true); return; }
    const next = {...proxyCards};
    const k = proxyKey(cardId, copyIndex);
    // Fold any legacy flat key into copy 0 so a card is never flagged twice under
    // two different key shapes.
    if (copyIndex === 0 && next[cardId] !== undefined) {
      if (next[k] === undefined) next[k] = next[cardId];
      delete next[cardId];
    }
    if (next[k] && note === undefined) delete next[k];
    else next[k] = (note && String(note).trim()) ? String(note).trim() : true;
    setProxyCards(next);
    try { await setDoc(doc(db,"boba_proxy",user.uid), next); }
    catch(e) { alert("Couldn't save: " + (e.message||e)); }
  }

  // Read the note back for a specific copy. Guards against the plain `true` flag.
  const proxyNote = (cardId, i=0) => {
    const v = proxyRaw(cardId, i);
    return (typeof v === "string" && v.trim()) ? v : "";
  };

  async function bulkSetOwned(add) {
    if (!user) { setSigningIn(true); return; }
    if (selectedIds.size === 0) return;
    const next = {...owned};
    let changed = 0;
    if (add) {
      // Cards whose quantity you already set with the on-card stepper are ALREADY saved — the
      // stepper writes as you tap. So this only fills in the ones still at zero. Reporting just
      // that number read as "1 card added" when the user had clearly queued up four copies, so
      // report total copies across the selection instead of only the rows this call touched.
      selectedIds.forEach(id => { if (!next[id]) { next[id] = 1; changed++; } });
      if (changed === 0) { alert("You already own every card selected."); return; }
      const totalCopies = [...selectedIds].reduce((s,id) => s + (parseInt(next[id])||0), 0);
      const titles = selectedIds.size;
      setOwned(next);
      queueOwnedSave(next);
      setToast(`Added ${totalCopies} cop${totalCopies===1?"y":"ies"} across ${titles} card${titles!==1?"s":""}.`);
      return;
    } else {
      const have = [...selectedIds].filter(id => next[id]);
      if (have.length === 0) { alert("None of the selected cards are in your collection."); return; }
      if (!window.confirm(`Remove ${have.length} card${have.length!==1?"s":""} from your collection? This clears every copy — use "Mark as sold" instead if you want it kept in your formerly-owned ledger.`)) return;
      have.forEach(id => { delete next[id]; changed++; });
    }
    setOwned(next);
    queueOwnedSave(next);
    setToast(add
      ? `Added ${changed} card${changed!==1?"s":""} to your collection.`
      : `Removed ${changed} card${changed!==1?"s":""} from your collection.`);
  }
  // Set every selected card to the SAME quantity in one go. The on-card stepper is per-card, so
  // putting fifty commons at 3 meant fifty separate interactions. Deliberately a "set to", not an
  // "add" \u2014 "make these all 3" is the thing people actually mean, and it's predictable when some
  // of the selection is already owned at different counts.
  // Clear artwork on every selected card in one pass. The per-card clear rebuilds the whole
  // checklist snapshot each time, which is fine for one card and unusable for fifty \u2014 so this
  // writes all the deletes in batches and rebuilds the snapshot ONCE at the end.
  async function bulkClearImages() {
    if (!_cardAdmin) return;
    const chosen = cards.filter(c => selectedIds.has(c.id) && c.imageUrl);
    if (!chosen.length) { alert("None of the selected cards have an image to clear."); return; }
    if (!window.confirm(
      `Clear artwork on ${chosen.length} card${chosen.length===1?"":"s"}?\n\n` +
      `This removes the image from the database so you can re-import the right ones. ` +
      `Ownership, values and notes are untouched.\n\nThis cannot be undone.`
    )) return;

    setBulkMoreOpen(false);
    try {
      const CHUNK = 400;
      for (let i=0; i<chosen.length; i+=CHUNK) {
        const batch = writeBatch(db);
        chosen.slice(i, i+CHUNK).forEach(card => {
          batch.set(doc(db,"boba_checklist", card.fsId || card.id), { imageUrl: deleteField() }, { merge:true });
        });
        await batch.commit();
      }
      const cleared = new Set(chosen.map(c=>c.id));
      setCards(cs => cs.map(c => cleared.has(c.id) ? { ...c, imageUrl:null } : c));

      // One snapshot rebuild for the whole operation, not one per card. Mirrors the rebuild the
      // other admin tools do: plain JSON, the gzipped copy the app actually reads, the version
      // stamp, and the local caches \u2014 miss any of those and the change appears not to have saved.
      let snapNote = "";
      try {
        const snap2 = await getDocs(collection(db,"boba_checklist"));
        const all2 = snap2.docs.map(d=>({...d.data(), id:d.id}));
        try {
          const blob = new Blob([JSON.stringify(all2)], { type:"application/json" });
          await uploadBytes(ref(storage,"card_data/boba_checklist.json"), blob, { contentType:"application/json", cacheControl:"public,max-age=86400" });
        } catch(e){}
        await writeCardSnapshot(all2, 86400);
        try { await setDoc(doc(db,"meta","cards_version"), { ts: Date.now(), count: all2.length }); } catch(e){}
        try { localStorage.removeItem("boba_checklist_cache"); localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
        try { await idbClearCards(); } catch(e){}
      } catch(e) {
        console.error("snapshot rebuild failed:", e);
        snapNote = "\n\nThe images were cleared, but the shared snapshot did not rebuild \u2014 run " +
                   "\u201cRebuild Card Snapshot\u201d in Admin so everyone sees the change.";
      }
      clearSelection();
      alert(`Cleared artwork on ${chosen.length} card${chosen.length===1?"":"s"}.${snapNote}`);
    } catch(e) {
      console.error("bulk clear images failed:", e);
      alert("Couldn't clear the images: " + (e?.message || e));
    }
  }

  async function bulkSetQuantity() {
    if (!user) { setSigningIn(true); return; }
    if (selectedIds.size === 0) return;
    const raw = window.prompt(
      `Set quantity for ${selectedIds.size} selected card${selectedIds.size!==1?"s":""}.\n\n` +
      `Every one of them will be set to this number \u2014 not added to what you already own.\n` +
      `Enter 0 to remove them from your collection.`, "1");
    if (raw === null) return;
    const qty = parseInt(String(raw).trim(), 10);
    if (isNaN(qty) || qty < 0) { alert("Enter a whole number of 0 or more."); return; }
    if (qty > 999) { alert("That's higher than this is meant for \u2014 keep it under 1000."); return; }

    // Flag the destructive direction explicitly rather than silently wiping counts.
    const losing = [...selectedIds].filter(id => (parseInt(owned[id])||0) > qty);
    if (qty === 0) {
      if (!window.confirm(`Remove all ${selectedIds.size} selected card${selectedIds.size!==1?"s":""} from your collection?`)) return;
    } else if (losing.length) {
      if (!window.confirm(`${losing.length} of the selected card${losing.length!==1?"s are":" is"} currently at a HIGHER count and will be reduced to ${qty}.\n\nContinue?`)) return;
    }

    const next = { ...owned };
    selectedIds.forEach(id => { if (qty <= 0) delete next[id]; else next[id] = qty; });
    setOwned(next);
    queueOwnedSave(next);
    setBulkMoreOpen(false);
    setToast(qty === 0
      ? `Removed ${selectedIds.size} card${selectedIds.size!==1?"s":""} from your collection.`
      : `Set ${selectedIds.size} card${selectedIds.size!==1?"s":""} to ${qty} cop${qty===1?"y":"ies"} each.`);
  }

  // ADD n copies to every selected card. Additive rather than "set to n": with a mixed selection
  // that is the predictable behaviour, and it can never silently reduce a card you already own
  // more of. One write for the whole map regardless of how many cards are selected.
  // SET every selected card to n. The bottom dial calls this on every tap so the on-card numbers
  // track it live. It has to be "set", not "add": if it added on each tap, holding + would run the
  // count away from what the dial reads and the two would disagree.
  async function bulkApplyQty(n) {
    if (!user) { setSigningIn(true); return; }
    if (selectedIds.size === 0) return;
    const qty = Math.max(0, parseInt(n) || 0);
    const next = { ...owned };
    selectedIds.forEach(id => { if (qty <= 0) delete next[id]; else next[id] = qty; });
    setOwned(next);
    queueOwnedSave(next);
  }


  // Bulk make public / private — one write via the whole map.
  async function bulkSetPublic(makePublic) {
    if (!user || selectedIds.size===0) return;
    const next = {...publicCards};
    selectedIds.forEach(id => { if(makePublic) next[id]=true; else delete next[id]; });
    setPublicCards(next);
    await persistPublicCards(next);
  }

  // Bulk-assign the selected cards to a kid's collection (or back to you with groupId=null).
  // One write for the whole batch rather than one per card.
  async function bulkAssignKid(groupId) {
    if (!user || selectedIds.size===0) return;
    const next = {...kidAssign};
    let n = 0;
    selectedIds.forEach(id => {
      const qty = parseInt(owned[id]) || 0;
      if (!qty) return;                     // only ever tags cards you actually own
      delete next[id];                      // drop any legacy per-card key
      for (let i=0; i<qty; i++) {
        const k = `${id}#${i}`;
        if (groupId) next[k] = groupId; else delete next[k];
      }
      n += qty;                             // bulk tags EVERY copy of the selected cards
    });
    if (n === 0) { alert("None of the selected cards are in your collection."); return; }
    setKidAssign(next);
    try { await setDoc(doc(db,"boba_kids",user.uid), { groups: kidGroups, assign: next }); }
    catch(e){ console.error("bulk assign kid failed:", e); }
    clearSelection();
  }

  // Bulk mark selected cards as sold / traded away. Decrements ONE copy of each and logs it.
  async function bulkSellSelected(reason="sold") {
    if (!user || selectedIds.size===0) return;
    let ids = [...selectedIds].filter(id => (parseInt(owned[id])||0) > 0);
    if (ids.length === 0) { alert("None of the selected cards are in your collection."); return; }
    const _allowed = filterLocked(ids, reason==="traded" ? "traded" : "sold");
    if (!_allowed) return;
    ids = _allowed;
    if (!window.confirm(`Mark ${ids.length} card${ids.length!==1?"s":""} as ${reason}? One copy of each leaves your collection \u2014 the record is kept.`)) return;

    const nextOwned = {...owned};
    const nextAssign = {...kidAssign};
    const entries = [];
    const now = new Date().toISOString();
    ids.forEach(id => {
      const have = parseInt(nextOwned[id])||0;
      const remaining = have - 1;
      if (remaining <= 0) delete nextOwned[id]; else nextOwned[id] = remaining;
      // Drop the tag for the copy that just left (copies are indexed 0..qty-1).
      delete nextAssign[id];                                  // legacy per-card key
      for (let i = Math.max(0, remaining); i < have; i++) delete nextAssign[`${id}#${i}`];
      entries.push({
        id:`sold_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
        cardId:id, qty:1, reason, note:"", price:null, date:now,
      });
    });
    setOwned(nextOwned);
    queueOwnedSave(nextOwned);
    setKidAssign(nextAssign);
    const nextLog = [...entries, ...soldLog];
    setSoldLog(nextLog);
    try {
      await setDoc(doc(db,"boba_sold",user.uid), { entries: nextLog });
      await setDoc(doc(db,"boba_kids",user.uid), { groups: kidGroups, assign: nextAssign });
    } catch(e){ console.error("bulk sell failed:", e); }
    clearSelection();
  }
  // Bulk list selected owned cards for sale/trade at one price. Skips already-listed cards.
  async function bulkListSelected() {
    if (!user || selectedIds.size===0 || bulkBusy) return;
    const _candidates = [...selectedIds].filter(id => owned[id] && scanPhotoByCard[id] && !myListings.find(l=>l.cardId===id));
    // Check the lock BEFORE the busy spinner, so cancelling doesn't leave the button stuck.
    const _allowed = filterLocked(_candidates, "listed");
    if (!_allowed) return;
    setBulkBusy(true);
    try {
      const ids = _allowed;
      for (const id of ids) {
        const c = cards.find(x=>x.id===id);
        if(!c) continue;
        const photoUrl = scanPhotoByCard[id];
        const lid = uid();
        await setDoc(doc(db,"marketplace",lid),{
          id:lid, isOBO:bulkListType==="offer", cardId:c.id, cardName:c.hero, cardNum:c.cardNum,
          cardTreatment:c.treatment, cardWeapon:c.weapon, cardPower:c.power, cardImage:c.imageUrl||null,
          sellerPhotos:photoUrl?[photoUrl]:[], setName:c.setName, sellerUid:user.uid, sellerName:user.displayName||user.email,
          askingPrice:bulkListType==="sale"?(parseFloat(bulkListPrice)||0):0,
          listType:bulkListType, notes:bulkListNote.trim(), paymentInfo:"",
          status:"active", createdAt:new Date().toISOString(), offerCount:0,
        });
      }
      setBulkListModal(false); setBulkListPrice(""); setBulkListNote(""); setBulkListType("sale");
      exitSelectMode();
    } catch(e){ console.error("bulk list failed:",e); }
    setBulkBusy(false);
  }

  // Create a shareable TRADE PACKAGE from the selected owned cards. Writes a public-read doc to
  // boba_trade_packages; anyone with the /trade/{id} link can view it (no login), see the cards, and
  // get your contact line to reply. We snapshot a little card detail into the doc so the public page
  // renders fast and still works even if a card is later re-imported (id drift) \u2014 the snapshot holds.
  async function createTradePackage() {
    if (!user) { setSigningIn(true); return; }
    const _base = tpEditId ? tpEditIds : [...selectedIds].filter(id => owned[id]);
    const _extra = tpEditId ? [...selectedIds].filter(id => owned[id] && !tpEditIds.includes(id)) : [];
    const ids = [...new Set([..._base, ..._extra])].filter(id => !tpRemoved.has(id) && owned[id]);   // only cards you own
    if (!ids.length) { alert("Select some cards you own first."); return; }
    const allowed = filterLocked(ids, "added to the package");
    if (!allowed) return;
    ids.length = 0; ids.push(...allowed);
    setTpBusy(true);
    try {
      const pick = new Set(ids);
      const items = cards.filter(c => pick.has(c.id)).map(c => ({
        id: c.id, hero: c.hero||"", playName: c.playName||"", cardNum: c.cardNum||"",
        treatment: c.treatment||"", setName: c.setName||"", weapon: c.weapon||"",
        power: c.power||"", imageUrl: c.imageUrl || scanPhotoByCard[c.id] || "", isMyPhoto: !c.imageUrl && !!scanPhotoByCard[c.id], qty: parseInt(owned[c.id])||1, value: (parseFloat(tpValues[c.id])||0)
      }));
      const pid = tpEditId || uid();
      await setDoc(doc(db, "boba_trade_packages", pid), {
        id: pid,
        ownerUid: user.uid,
        ownerName: (user.displayName || (myUsername ? "@"+myUsername : "") || (user.email||"").split("@")[0] || "A Bazooka collector"),
        title: tpTitle.trim() || "Trade package",
        note: tpNote.trim(),
        contact: tpContact.trim(),
        items,
        cardIds: ids,
        totalValue: ids.reduce((s,id)=>s+(parseFloat(tpValues[id])||0),0),
        ...(tpEditId ? { updatedAt: new Date().toISOString() } : { createdAt: new Date().toISOString() })
      }, { merge: true });
      const url = `${window.location.origin}/trade/${pid}`;
      setTpLink(url);
      try { await navigator.clipboard.writeText(url); setTpCopied(true); setTimeout(()=>setTpCopied(false), 2500); } catch(e){}
    } catch(e) {
      console.error("create trade package failed:", e);
      alert("Couldn't create the trade package: " + (e?.message||e));
    }
    setTpBusy(false);
  }

  // Keep public cards in sync with public trackers: any owned card whose treatment is covered
  // by a PUBLIC tracker stays public automatically (e.g. after scanning a new one in).
  const trackerSyncRef = useRef("");
  useEffect(() => {
    if (!user || !cards.length) return;
    const publicTrackerTreatments = new Set();
    customTrackers.filter(t=>t.public).forEach(t => (t.treatments||[]).forEach(tr => publicTrackerTreatments.add(tr)));
    if (publicTrackerTreatments.size === 0) return;
    const shouldBePublic = cards.filter(c => owned[c.id] && publicTrackerTreatments.has(c.treatment)).map(c=>c.id);
    // Only act if something new needs publishing
    const missing = shouldBePublic.filter(id => !publicCards[id]);
    if (missing.length === 0) return;
    const sig = shouldBePublic.sort().join(",");
    if (trackerSyncRef.current === sig) return;
    trackerSyncRef.current = sig;
    const nextPublic = {...publicCards};
    const nextAuto = {...trackerAutoPublic};
    missing.forEach(id => { nextPublic[id] = true; nextAuto[id] = true; });
    setPublicCards(nextPublic);
    setTrackerAutoPublic(nextAuto);
    try { localStorage.setItem("trackerAutoPublic_v1", JSON.stringify(nextAuto)); } catch {}
    persistPublicCards(nextPublic);
  }, [user, cards, owned, customTrackers, publicCards]);

  async function toggleTradeBait(cardId) {
    if (!user) { setSigningIn(true); return; }
    const wasFlagged = !!tradeBait[cardId];
    // Don't advertise a locked card as available to trade. Un-flagging is always allowed.
    if (!wasFlagged && lockedCardIds([cardId]).length) {
      const c0 = cards.find(x=>x.id===cardId);
      alert("\uD83D\uDD12 " + (c0?.hero||"This card") + " is marked “never trade or sell”, so it won't be added to your trade bait.\n\nUnlock it in the copy details first.");
      return;
    }
    // Don't advertise a card you can't hand over: borrowed cards aren't yours, and
    // a card whose every copy is lent out isn't in your hands. Un-flagging is fine.
    if (!wasFlagged) {
      const c0 = cards.find(x=>x.id===cardId);
      if ((borrowedByCard[cardId]||0) > 0) {
        alert("\uD83D\uDCE5 " + (c0?.hero||"This card") + " is borrowed — it isn't yours to trade.");
        return;
      }
      const free = (owned[cardId]||0) - (lentByCard[cardId]||0);
      if ((lentByCard[cardId]||0) > 0 && free <= 0) {
        alert("\uD83D\uDCE4 " + (c0?.hero||"This card") + " is lent out. Get it back before flagging it for trade.");
        return;
      }
    }
    const next = {...tradeBait};
    if (next[cardId]) delete next[cardId]; else next[cardId]=true;
    setTradeBait(next);
    try {
      await setDoc(doc(db,"boba_tradebait",user.uid), next);
      // Keep the public index in step \u2014 but ONLY if they've opted in. See publishTradeIndex.
      if (tradePublic) {
        await publishTradeIndex(next, true);
        // Newly flagged AND publicly listed: tell the people who want this card. Same shape as the
        // marketplace want-notification, so it lands in the same inbox they already watch.
        if (!wasFlagged) await notifyWanters(cardId);
      }
    } catch(e){ console.error("save tradebait failed:", e); }
  }

  // Anyone with this card on their want list hears about it. Mirrors the createListing() fan-out.
  //
  // Only fires when the card is flagged AND the user is publicly listed \u2014 flagging a card while
  // opted OUT notifies nobody, which is the point of the opt-in being meaningful.
  async function notifyWanters(cardId) {
    if (!user) return;
    const card = cards.find(c => c.id === cardId);
    if (!card) return;
    try {
      const wantSnaps = await getDocs(collection(db,"boba_wants"));
      const batch = [];
      wantSnaps.forEach(wsnap => {
        if (wsnap.id === user.uid) return;              // don't notify yourself
        if (!wsnap.data()[cardId]) return;              // they don't want it
        batch.push(setDoc(doc(db,"market_notifs",uid()), {
          toUid: wsnap.id,
          cardId,
          cardName: card.hero || card.playName || "",
          cardImage: card.imageUrl || null,
          traderUid: user.uid,
          traderName: user.displayName || user.email,
          type: "want_tradeable",                       // distinct from "want_listed" \u2014 it's a trade, not a sale
          read: false,
          createdAt: new Date().toISOString(),
        }));
      });
      await Promise.all(batch);
    } catch(e) { console.error("trade notification error:", e); }
  }

  // ── Public trade index ───────────────────────────────────────────────────────────────────────
  // boba_tradebait is PRIVATE to its owner, and it stays that way. People flagged cards as trade
  // bait long before this feature existed, under the reasonable assumption nobody else could see
  // them \u2014 silently republishing that would be a betrayal of exactly the kind we just fixed in
  // family sharing.
  //
  // So the public side is a SEPARATE, opt-in doc. Toggling "list me as a trade partner" copies the
  // card ids into trade_index/{uid}; toggling it off deletes that doc outright. Opting out means the
  // record is GONE, not merely flagged hidden \u2014 there is no lingering copy of your list on a server
  // you thought you had left.
  //
  // The index carries only what a match needs: who you are, and which cards you'd trade. Not your
  // collection, not quantities, not what you paid.
  const [tradePublic,  setTradePublic]  = useState(false);
  // Everyone who opted in to trade matching. Public read by design \u2014 the doc holds only a name
  // and the card ids they explicitly flagged, never their collection.
  const [tradeIndex,   setTradeIndex]   = useState([]);
  const [mktInitView,  setMktInitView]  = useState(null);   // which marketplace view to open on
  useEffect(() => {
    const unsub = onSnapshot(collection(db,"trade_index"),
      snap => setTradeIndex(snap.docs.map(d => ({...d.data(), id:d.id}))),
      e => console.error("trade index listen failed:", e));
    return () => { try{unsub();}catch(e){} };
  }, []);
  const [tradePubBusy, setTradePubBusy] = useState(false);

  async function publishTradeIndex(bait, on) {
    if (!user) return;
    const ref = doc(db,"trade_index",user.uid);
    if (!on) { try { await deleteDoc(ref); } catch(e){ console.error("trade index delete failed:", e); } return; }
    const ids = Object.keys(bait||{}).filter(k => bait[k]);
    await setDoc(ref, {
      uid: user.uid,
      name: user.displayName || (user.email||"").split("@")[0],
      cardIds: ids,
      count: ids.length,
      updatedAt: new Date().toISOString(),
    });
  }

  async function toggleTradePublic() {
    if (!user || tradePubBusy) return;
    setTradePubBusy(true);
    const on = !tradePublic;
    try {
      await publishTradeIndex(tradeBait, on);
      setTradePublic(on);
      // Switching ON with cards already flagged: those become visible now, so notify for them too.
      // Without this, the common path (flag a few cards, THEN opt in) would notify nobody at all.
      if (on) {
        const flagged = Object.keys(tradeBait).filter(k => tradeBait[k]);
        for (const cid of flagged) await notifyWanters(cid);
      }
      await setDoc(doc(db,"boba_profiles",user.uid), { tradePublic: on }, { merge:true });
      setToast(on
        ? `\u2713 You're listed as a trade partner \u2014 ${Object.keys(tradeBait).length} card${Object.keys(tradeBait).length===1?"":"s"} visible`
        : "\u2713 Removed from trade matching \u2014 your list is private again");
    } catch(e) {
      alert("Couldn't update: " + e.message);
    }
    setTradePubBusy(false);
  }
  // -- Lot (per-copy cost/value) tracking --
  // -- Collection CSV import --
  function parseCSV(text) {
    const rows = [];
    let row = [], field = "", inQ = false;
    for (let i=0;i<text.length;i++) {
      const ch = text[i], nx = text[i+1];
      if (inQ) {
        if (ch === '"' && nx === '"') { field += '"'; i++; }
        else if (ch === '"') inQ = false;
        else field += ch;
      } else {
        if (ch === '"') inQ = true;
        else if (ch === ",") { row.push(field); field = ""; }
        else if (ch === "\n") { row.push(field); rows.push(row); row=[]; field=""; }
        else if (ch === "\r") { /* skip */ }
        else field += ch;
      }
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    return rows.filter(r => r.some(c => c.trim() !== ""));
  }

  function matchImportRow(rec, hdr, setMap, colMap) {
    const cell = (field) => { const idx = colMap ? colMap[field] : hdr.findIndex(h => h.toLowerCase().trim() === field.toLowerCase()); return (idx!=null && idx>=0) ? (rec[idx]||"").trim() : ""; };
    const heroRaw = cell("Name");
    const csvSet = cell("Set");
    const mappedSet = setMap ? setMap[csvSet] : undefined;
    const forceSkip = mappedSet === "__SKIP__"; // user marked this set as not in our database
    const setName = (mappedSet && !forceSkip) ? mappedSet : csvSet; // use mapped set if provided
    const cardNum = cell("Card Number");
    const parallel = cell("Parallel");   // maps to treatment
    const weapon = cell("Weapon");
    const power = cell("Power");
    const qty = parseInt(cell("Quantity")) || 1;
    const value = parseFloat(String(cell("Estimated Value")).replace(/[^0-9.]/g,"")) || null;
    // Per-copy detail: which numbered copy this is (e.g. "21/50") and any note about it. Both are
    // properties of YOUR copy, not of the card in the catalogue, so they ride along on the lot.
    // Accept several header spellings so people don't have to guess the exact column name.
    // Serial is stored as free text, so any of "14", "14/25", "#14/25" or "14 of 25" is accepted.
    // We only tidy the common shapes into "14/25" so the display is consistent; a bare number
    // stays as-is because we can't invent a print run we weren't told.
    const serial = (() => {
      let s = (cell("Serial") || cell("Serial Number") || cell("Numbered") || cell("Serial #") || "").trim();
      if (!s) return "";
      s = s.replace(/^#/, "").trim();
      const m = s.match(/^(\d+)\s*(?:of|\/|out of)\s*(\d+)$/i);
      return m ? `${m[1]}/${m[2]}` : s;
    })();
    const notes  = cell("Notes") || cell("Note") || cell("Comment") || cell("Comments") || "";
    // What YOU paid, as distinct from Estimated Value (what it's worth). Both matter: cost drives
    // profit/loss, value drives collection worth. Strip currency symbols and thousands commas.
    const cost = parseFloat(String(cell("Purchase Price")).replace(/[^0-9.]/g,"")) || null;
    // Value of cards given in trade as part of the same deal — added to cash for true cost basis.
    const tradeValue = parseFloat(String(cell("Trade Value")).replace(/[^0-9.]/g,"")) || null;
    // Opaque pointer back to whatever system this row came from. Never shown to collectors and
    // never used for matching — it exists so a migration can be audited, re-run or reconciled
    // later without guessing which imported copy corresponds to which source record.
    const legacyId = cell("Legacy ID") || "";
    const purchaseDate = cell("Purchase Date") || "";
    const condition    = cell("Condition") || "";
    const grade        = cell("Grade") || "";
    const gradeCompany = cell("Grading Company") || "";
    const certNum      = cell("Cert #") || "";
    const location     = cell("Location") || "";
    // Locked = "never trade or sell". Accept the spellings people actually type in a spreadsheet
    // rather than demanding a strict TRUE, and treat a blank cell as unlocked.
    const _lockRaw = (cell("Locked") || "").trim().toLowerCase();
    const locked = ["y","yes","true","1","x","lock","locked"].includes(_lockRaw);
    if (!heroRaw && !cardNum) return null;
    const norm = s => String(s||"").replace(/[\s\-_.]/g,"").toLowerCase();
    // Hero name variants: full string, the quoted nickname (BoBA hero name), and the string with the quoted part removed
    const heroVariants = (() => {
      const v = new Set();
      if (heroRaw) v.add(heroRaw);
      const q = heroRaw.match(/"([^"]+)"|'([^']+)'/); // pull a quoted nickname like "Cutback"
      if (q) { v.add(q[1]||q[2]); v.add(heroRaw.replace(/["']/g,"").trim()); }
      return [...v];
    })();
    const heroMatches = c => heroVariants.some(h => norm(c.hero)===norm(h));
    const setMatches = c => !setName || norm(c.setName)===norm(setName) || norm(c.setName).includes(norm(setName)) || norm(setName).includes(norm(c.setName));

    let match = null;
    if (forceSkip) { return { csv:{hero:heroRaw,setName:csvSet,csvSet,cardNum,parallel,weapon,power,qty,value,cost,tradeValue,serial,notes,legacyId,purchaseDate,condition,grade,gradeCompany,certNum,location,locked}, match:null }; }
    // With sets explicitly mapped, the set MUST match — card numbers repeat across sets,
    // so a set-blind match would grab the wrong card. Every tier below requires the set.
    // 1) cardNum + set (most precise — number is unique within a set)
    if (cardNum) match = cards.find(c => norm(c.cardNum)===norm(cardNum) && setMatches(c));
    // 2) hero + treatment + weapon + power + set
    if (!match) match = cards.find(c => heroMatches(c) && norm(c.treatment)===norm(parallel) && norm(c.weapon)===norm(weapon) && (!power||String(c.power)===String(power)) && setMatches(c));
    // 3) hero + treatment + weapon + set (no power)
    if (!match) match = cards.find(c => heroMatches(c) && norm(c.treatment)===norm(parallel) && norm(c.weapon)===norm(weapon) && setMatches(c));
    // 4) hero + cardNum + set (covers treatment/weapon spelling differences)
    if (!match && cardNum) match = cards.find(c => heroMatches(c) && norm(c.cardNum)===norm(cardNum) && setMatches(c));
    // No set-blind fallback — if the set doesn't match, we'd rather report "not found"
    // than import the wrong card. The user mapped the set, so this stays strict.
    return { csv:{hero:heroRaw,setName,csvSet,cardNum,parallel,weapon,power,qty,value,cost,tradeValue,serial,notes,legacyId,purchaseDate,condition,grade,gradeCompany,certNum,location,locked}, match:match||null };
  }

  // Common column-name aliases from other collection tools (CardLadder, eBay, COMC, Collectr, custom sheets, etc.)
  const COL_ALIASES = {
    "Name": ["name","player","card name","cardname","hero","player name","title","subject","athlete","card","description"],
    "Set": ["set","set name","setname","series","release","edition","product","brand/set"],
    "Card Number": ["card number","cardnumber","card #","card#","number","no","no.","#","card no","cardno","card num","cardnum"],
    "Parallel": ["parallel","treatment","variation","variant","finish","foil","insert","subset","sub set","parallel/insert","type"],
    "Weapon": ["weapon","weapon type"],
    "Power": ["power","power level","pwr"],
    "Quantity": ["quantity","qty","count","amount","# owned","owned","copies"],
    "Estimated Value": ["estimated value","value","fair market value","fmv","market value","price","est value","est. value","worth","comp"],
    // Per-copy detail. Without these the columns were never mapped, so a typed note silently
    // fell back to the default "Imported".
    "Serial": ["serial","serial number","serial #","numbered","numbering","serial no"],
    "Notes": ["notes","note","comment","comments","remarks","description"],
    "Legacy ID": ["legacy id","external id","external card id","source id","legacy card id","old id","record id","import id"],
    "Trade Value": ["trade value","trade","trade comps","comps","traded value","trade credit","cards given"],
    "Purchase Price": ["purchase price","cost","paid","price paid","buy price","acquisition cost","cost basis"],
    "Purchase Date": ["purchase date","date purchased","bought","date acquired","acquired"],
    "Condition": ["condition","cond","card condition"],
    "Grade": ["grade","grading","numeric grade"],
    "Grading Company": ["grading company","grader","grading co","graded by","company"],
    "Cert #": ["cert","cert #","cert number","certification","certification number","slab #"],
    "Location": ["location","physical location","stored","storage","where","binder"],
    "Locked": ["locked","lock","never trade","never sell","do not trade","keep","protected"],
  };
  function autoDetectColumns(hdr) {
    const norm = s => String(s||"").replace(/[\s\-_.()/#]/g,"").toLowerCase();
    const map = {};
    const usedIdx = new Set();
    Object.keys(COL_ALIASES).forEach(field => {
      const aliases = COL_ALIASES[field];
      // exact normalized match first
      let idx = hdr.findIndex((h,i) => !usedIdx.has(i) && aliases.some(a => norm(a)===norm(h)));
      // then contains match
      if (idx < 0) idx = hdr.findIndex((h,i) => !usedIdx.has(i) && aliases.some(a => norm(h).includes(norm(a)) || norm(a).includes(norm(h))));
      map[field] = idx;
      if (idx >= 0) usedIdx.add(idx);
    });
    return map;
  }

  function handleImportFile(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const rows = parseCSV(e.target.result);
        if (rows.length < 2) { alert("That file looks empty. Make sure it has a header row and at least one card."); return; }
        const hdr = rows[0];
        const records = rows.slice(1).filter(r => r.some(c => c.trim()!==""));
        if (!records.length) { alert("Couldn't read any cards from that file. Check that it has card rows below the header."); return; }
        // Auto-detect which CSV column maps to each field, then let the user confirm/fix it.
        const detected = autoDetectColumns(hdr);
        setImportRaw({ hdr, records });
        setImportColMap(detected);
        setColMapConfirmed(false);
        setImportSetMap({});
        setImportRows(null);
      } catch(err) { console.error(err); alert("Couldn't read that CSV. Make sure it's a valid comma-separated file."); }
    };
    reader.readAsText(file);
  }

  // Called after the user confirms the column mapping → derive set list + pre-fill obvious set matches
  function confirmColumnMapAndProceed() {
    if (!importRaw || !importColMap) return;
    const { records } = importRaw;
    const setColIdx = importColMap["Set"];
    const csvSets = setColIdx>=0 ? [...new Set(records.map(r => (r[setColIdx]||"").trim()).filter(Boolean))].sort() : [];
    const ourSets = [...new Set(cards.map(c=>c.setName).filter(Boolean))];
    const norm = s => String(s||"").replace(/[\s\-_.]/g,"").toLowerCase();
    const initialMap = {};
    csvSets.forEach(cs => { const hit = ourSets.find(os => norm(os)===norm(cs) || norm(os).includes(norm(cs)) || norm(cs).includes(norm(os))); if (hit) initialMap[cs]=hit; });
    setImportSetMap(initialMap);
    setColMapConfirmed(true);
  }

  function applySetMappingAndMatch() {
    if (!importRaw) return;
    const { hdr, records } = importRaw;
    const parsed = records.map(r => matchImportRow(r, hdr, importSetMap, importColMap)).filter(Boolean);
    setImportRows(parsed);
  }

  async function loadMissingCards() {
    try {
      const snap = await getDocs(collection(db,"missing_cards"));
      const rows = snap.docs.map(d=>({...d.data(), id:d.id}))
        .sort((a,b)=>(b.lastRequestedAt||"").localeCompare(a.lastRequestedAt||""));
      setMissingCards(rows);
    } catch(e) { console.error("load missing cards failed:", e); setMissingCards([]); }
  }
  async function dismissMissingCard(id) {
    try { await deleteDoc(doc(db,"missing_cards",id)); setMissingCards(prev=>prev.filter(m=>m.id!==id)); }
    catch(e) { console.error("dismiss missing failed:", e); }
  }

  async function runImport() {
    if (!user) { setSigningIn(true); return; }
    if (!importRows) return;
    setImporting(true);
    try {
      const matched = importRows.filter(r => r.match);
      const nextOwned = { ...owned };
      const newLots = [];
      for (const r of matched) {
        const id = r.match.id;
        const q = r.csv.qty || 1;
        nextOwned[id] = (nextOwned[id]||0) + q;
        // One lot per copy. Serial + notes are per-copy facts, so if a row brings a serial we put it
        // on the FIRST copy only — a "21/50" can't describe three different cards. Extra copies from
        // the same row get the note but no serial, and can be edited individually afterwards.
        for (let i=0;i<q;i++) {
          newLots.push({
            id: uid(), cardId:id, cost: r.csv.cost, tradeValue: r.csv.tradeValue, value: r.csv.value||null,
            method: r.csv.cost!=null ? "purchased" : "other",
            date:todayLocal(),
            serial: i===0 ? (r.csv.serial||"") : "",
            notes: r.csv.notes || "Imported",
            purchaseDate: r.csv.purchaseDate||"",
            condition: r.csv.condition||"",
            grade: r.csv.grade||"",
            gradeCompany: r.csv.gradeCompany||"",
            certNum: i===0 ? (r.csv.certNum||"") : "",   // a cert number identifies ONE slab
            location: r.csv.location||"",
            locked: importLockAll || !!r.csv.locked,
            // Source-system pointer. Multi-quantity rows suffix the copy index so each lot is
            // still traceable to the row without two lots sharing an identical id.
            ...(r.csv.legacyId ? { legacyId: q>1 ? `${r.csv.legacyId}#${i+1}` : r.csv.legacyId } : {}),
            photoUrl:"", photoBackUrl:"",
          });
        }
      }
      setOwned(nextOwned);
      await setDoc(doc(db,"boba_owned",user.uid), nextOwned);
      const mergedLots = [...lots, ...newLots];
      setLots(mergedLots);
      try { await setDoc(doc(db,"boba_lots",user.uid), { lots: mergedLots }); } catch(e){}

      // Log unmatched cards so admins know what to add to the database
      const unmatched = importRows.filter(r => !r.match);
      if (unmatched.length) {
        // Per-user list so the collector can see what didn't import (merged with any prior unmatched)
        const unmatchedClean = unmatched.map(r => ({ hero:r.csv.hero||"", setName:r.csv.setName||"", cardNum:r.csv.cardNum||"", treatment:r.csv.parallel||"", weapon:r.csv.weapon||"", power:r.csv.power||"", qty:r.csv.qty||1 }));
        const dedupKey = c => [c.hero,c.setName,c.cardNum,c.treatment,c.weapon].join("|").toLowerCase();
        const existingKeys = new Set(userMissing.map(dedupKey));
        const mergedMissing = [...userMissing, ...unmatchedClean.filter(c => !existingKeys.has(dedupKey(c)))];
        setUserMissing(mergedMissing);
        try { await setDoc(doc(db,"user_missing",user.uid), { cards: mergedMissing }); } catch(e){}
        // Admin-wide log
        try {
          const batch = writeBatch(db);
          unmatched.forEach(r => {
            const c = r.csv;
            const key = ["miss", (c.hero||""), (c.setName||""), (c.cardNum||""), (c.parallel||""), (c.weapon||"")].join("|").replace(/[^a-zA-Z0-9|]/g,"_").slice(0,180);
            const ref2 = doc(db,"missing_cards",key);
            batch.set(ref2, {
              hero:c.hero||"", setName:c.setName||"", cardNum:c.cardNum||"", treatment:c.parallel||"", weapon:c.weapon||"", power:c.power||"",
              requestedBy: arrayUnion(user.email||user.uid),
              lastRequestedAt: new Date().toISOString(),
            }, { merge:true });
          });
          await batch.commit();
        } catch(e) { console.error("log missing cards failed:", e); }
      }

      showToast(`Imported ${matched.length} card${matched.length!==1?"s":""}${unmatched.length?` · ${unmatched.length} not in database yet`:""}!`);
      setImportModal(false); setImportRows(null); setImportRaw(null); setImportSetMap({}); setImportColMap(null); setColMapConfirmed(false); setImportLockAll(false);
    } catch(e) { console.error("import failed:", e); alert("Import failed. Please try again."); }
    setImporting(false);
  }

  function downloadImportTemplate() {
    const headers = ["Name","Set","Card Number","Parallel","Weapon","Power","Quantity","Estimated Value","Purchase Price","Trade Value","Serial","Notes","Legacy ID","Purchase Date","Condition","Grading Company","Grade","Cert #","Location","Locked"];
    // Set is REQUIRED — the same hero can appear in multiple sets
    const ex1 = ["Bo Jackson","Tecmo Bowl Edition","TB1","Tecmo Bowl","","250","1","500","320","","","first pull of the box","V1-00412","2026-03-14","Near Mint","","","","Binder 1 page 4",""];
    const ex2 = ["Cutback","Alpha Update","BFA-5","Inspired Ink Battlefoil","Hex","200","1","2500","1800","700","21/50","on-card auto","V1-00877","2026-05-02","Mint","PSA","10","82736451","Slab case","YES"];
    const csv = headers.join(",") + "\n" + ex1.join(",") + "\n" + ex2.join(",") + "\n";
    const blob = new Blob([csv], { type:"text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download="bazooka-collection-template.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  async function clearCollection() {
    if (!user) return;
    const count = Object.keys(owned).length;
    if (!window.confirm(`Clear your entire collection? This removes all ${count} card${count!==1?"s":""} and their saved details/photos. This can't be undone.`)) return;
    if (!window.confirm("Are you absolutely sure? Type-of-no-return — your whole collection will be wiped.")) return;
    let ok = false;
    try {
      setOwned({});
      await setDoc(doc(db,"boba_owned",user.uid), {});
      setLots([]);
      await setDoc(doc(db,"boba_lots",user.uid), { lots: [] });
      ok = true;
    } catch(e) { console.error("clear collection failed:", e); alert("Couldn't clear collection. Try again."); }
    if (ok) { showToast("Collection cleared."); setEditProfileOpen(false); }
  }

  async function updateProfilePic(file) {
    if (!file || !user) return;
    setEditPicUploading(true);
    try {
      // Compress to ~400px square-ish for a profile pic
      const dataUrl = await new Promise((res,rej)=>{
        const img=new Image(), url=URL.createObjectURL(file);
        img.onload=()=>{ URL.revokeObjectURL(url);
          const MAX=400, scale=Math.min(1,MAX/Math.max(img.width,img.height));
          const w=Math.round(img.width*scale), h=Math.round(img.height*scale);
          const c=document.createElement("canvas"); c.width=w; c.height=h;
          c.getContext("2d").drawImage(img,0,0,w,h);
          res(c.toDataURL("image/jpeg",0.85));
        };
        img.onerror=rej; img.src=url;
      });
      const sref = ref(storage, `profile_pics/${user.uid}/${uid()}.jpg`);
      await uploadString(sref, dataUrl, "data_url");
      const url = await getDownloadURL(sref);
      await setDoc(doc(db,"users",user.uid), { photoURL: url }, { merge:true });
      try { localStorage.setItem("bazooka_photo_" + user.uid, url); } catch(e) {}
      setMyPhotoURL(url);
      showToast("Profile picture updated!");
    } catch(e) { console.error("profile pic update failed:", e); alert("Couldn't update photo. Try again."); }
    setEditPicUploading(false);
  }
  async function saveLots(nextLots) {
    setLots(nextLots);
    if (!user) return;
    try { await setDoc(doc(db,"boba_lots",user.uid), { lots: nextLots }); } catch(e){ console.error("save lots failed:", e); }
  }
  function addLot(cardId, data) {
    const lot = { id: uid(), cardId, cost: data.cost??null, value: data.value??null, method: data.method||"purchased", date: data.date||new Date().toISOString().split("T")[0], notes: data.notes||"" };
    saveLots([...lots, lot]);
  }
  // Add a card you're BORROWING in one step. Creates the copy already tagged as borrowed \u2014 so it
  // never counts as yours \u2014 and bumps the physical count so it appears in the grid and can be decked.
  async function addBorrowedCopy(cardId, who, why) {
    const lot = { id: uid(), cardId, cost:null, value:null, method:"borrowed",
      date: todayLocal(), notes:"", lendState:"borrowed",
      lendWho:(who||"").trim(), lendWhy:(why||"").trim(), lendDate: todayLocal() };
    saveLots([...lots, lot]);
    await setOwnedQty(cardId, (parseInt(owned?.[cardId])||0) + 1);
  }
  function updateLot(lotId, data) {
    saveLots(lots.map(l => l.id===lotId ? { ...l, ...data } : l));
  }

  // One-click lend: flag ONE in-hand copy of a card as lent to someone, without
  // opening the lot editor. Reuses the same lot/lendState the editor writes, so it
  // shows in the Lent Out list and blocks decking exactly the same way.
  //   - If an in-hand lot already exists, tag that one (don't create duplicates).
  //   - Otherwise create a lot for this copy so the loan has somewhere to live.
  // Returning it is the inverse (markCardReturned): clears the lend fields on the
  // most-recently-lent copy.
  async function markCardLent(cardId, who, returnBy="") {
    const name = (who||"").trim();
    if (!name) return;
    const stamp = todayLocal();
    // Prefer an existing in-hand lot for this card.
    const idx = lots.findIndex(l => l.cardId===cardId && (l.lendState||"")==="");
    if (idx >= 0) {
      const next = lots.slice();
      next[idx] = { ...next[idx], lendState:"lent", lendWho:name, lendDate:stamp, returnBy: returnBy||"" };
      saveLots(next);
    } else {
      const lot = { id: uid(), cardId, cost:null, value:null, method:"owned",
        date: stamp, notes:"", lendState:"lent", lendWho:name, lendWhy:"", lendDate:stamp, returnBy: returnBy||"" };
      saveLots([...lots, lot]);
    }
  }
  // Return the most recently lent copy of a card (clears the lend flags).
  async function markCardReturned(cardId) {
    // Find the newest lent lot for this card.
    let target = null;
    for (const l of lots) {
      if (l.cardId===cardId && (l.lendState||"")==="lent") {
        if (!target || (l.lendDate||"") >= (target.lendDate||"")) target = l;
      }
    }
    if (!target) return;
    saveLots(lots.map(l => l.id===target.id ? { ...l, lendState:"", lendWho:"", lendWhy:"", lendDate:"" } : l));
  }

  // ---- send lent cards to a friend ---------------------------------------
  // Push a batch of my lent cards to a friend as PENDING loan offers. The friend
  // accepts on their side, which flips the entry to "borrowed" and pulls the card
  // into their collection as borrowed-from-me. Nothing lands on their side without
  // their accept — I only ever write the shared borrow_ledger doc I'm a party to.
  async function sendLoanToFriend(friend, cardIds, returnBy="") {
    if (!user || !friend?.friendUid || !cardIds?.length) return;
    const stamp = new Date().toISOString();
    const myName = user.displayName || user.email || "Someone";
    const writes = cardIds.map(cid => {
      const card = cardById.get(cid);   // cardById is a Map — .get(), not [cid]
      const id = `${friend.friendUid}_${cid}_${user.uid}`;   // borrower_card_owner, idempotent
      return setDoc(doc(db, "borrow_ledger", id), {
        id,
        borrowerUid: friend.friendUid, borrowerName: friend.friendName || friend.friendUid,
        ownerUid: user.uid, ownerName: myName,
        cardId: cid,
        cardLabel: card ? `${card.hero} ${card.power}⚡ ${card.treatment||""}`.trim() : cid,
        cardImage: card?.imageUrl || "",
        deckName: "",
        returnBy: returnBy || "",          // owner's requested return date, carried to borrower on accept
        status: "pending",                 // becomes "borrowed" on accept
        offeredAt: stamp,
        participantUids: [user.uid, friend.friendUid],
      });
    });
    try { await Promise.all(writes); return { ok:true, count:writes.length }; }
    catch (e) { console.error("send loan failed:", e); return { ok:false, error:e?.message||String(e) }; }
  }

  // Borrower accepts a pending loan -> it becomes a real borrow on their side, AND
  // a borrowed lot is created so the card shows in their database as borrowed-from
  // the owner (buildable, but never counted as theirs).
  async function acceptLoan(entry) {
    if (!user || entry?.borrowerUid !== user.uid) return;
    try {
      await setDoc(doc(db, "borrow_ledger", entry.id),
        { status:"borrowed", borrowedAt:new Date().toISOString() }, { merge:true });
      // Create the borrowed copy on my side (mirrors addBorrowedCopy). Idempotent:
      // skip if a borrowed lot from this owner for this card already exists.
      const already = (lots||[]).some(l => l.cardId===entry.cardId && l.lendState==="borrowed" && l.lendWho===entry.ownerName);
      if (!already) {
        const lot = { id: uid(), cardId: entry.cardId, cost:null, value:null, method:"borrowed",
          date: todayLocal(), notes:"", lendState:"borrowed",
          lendWho: entry.ownerName || "", lendWhy:"", lendDate: todayLocal(),
          returnBy: entry.returnBy || "",   // carry the owner's return-by date so my reminder fires
          loanId: entry.id };   // link back so return can find it
        saveLots([...(lots||[]), lot]);
        await setOwnedQty(entry.cardId, (parseInt(owned?.[entry.cardId])||0) + 1);
      }
    } catch (e) { console.error("accept loan failed:", e); }
  }

  // Decline (borrower) or cancel (owner) a pending loan -> remove it.
  async function declineLoan(entry) {
    if (!user || !(entry?.participantUids||[]).includes(user.uid)) return;
    try { await deleteDoc(doc(db, "borrow_ledger", entry.id)); }
    catch (e) { console.error("decline loan failed:", e); }
  }

  // Set (or clear) a return-by date on a loan lot. If this card was SENT to a
  // friend through the app, mirror the date onto the shared ledger entry so the
  // BORROWER's reminder fires too — their borrowed lot picks it up on accept, and
  // for already-accepted loans we patch the ledger directly. Pass "" to clear.
  async function setLoanReturnBy(lotId, dateStr) {
    const lot = (lots||[]).find(l => l.id===lotId);
    if (!lot) return;
    saveLots(lots.map(l => l.id===lotId ? { ...l, returnBy: dateStr || "" } : l));
    // Sync to the ledger for a lent copy I sent to a friend (ownerUid === me).
    if (user && lot.lendState === "lent") {
      try {
        const snap = await getDocs(query(collection(db,"borrow_ledger"),
          where("ownerUid","==",user.uid), where("cardId","==",lot.cardId)));
        await Promise.all(snap.docs
          .filter(d => ["pending","borrowed"].includes(d.data().status))
          .map(d => setDoc(doc(db,"borrow_ledger",d.id), { returnBy: dateStr || "" }, { merge:true })));
      } catch (e) { console.error("sync return date failed:", e); }
    }
  }

  function removeLot(lotId) {
    saveLots(lots.filter(l => l.id!==lotId));
  }
  function lotsForCard(cardId) { return lots.filter(l => l.cardId===cardId); }
  // A card counts as LOCKED only when every copy you own is locked — if you own three and locked one,
  // the other two are still yours to move. Every path a card can leave by (sell, trade, list, package)
  // funnels through this, so the lock can't be honoured in one place and forgotten in another.
  function lockedCardIds(ids) {
    return ids.filter(id => {
      const ls = lots.filter(l => l.cardId === id);
      return ls.length > 0 && ls.every(l => l.locked);
    });
  }
  // Returns the ids that may proceed, or null if the user cancelled / nothing is left.
  function filterLocked(ids, actionLabel) {
    const locked = lockedCardIds(ids);
    if (!locked.length) return ids;
    const names = locked.slice(0,5).map(id => (cards.find(c=>c.id===id)?.hero) || id).join(", ");
    const rest = ids.filter(id => !locked.includes(id));
    if (!rest.length) {
      alert(`\uD83D\uDD12 Locked — nothing was ${actionLabel}.\n\n${names}${locked.length>5?` and ${locked.length-5} more`:""} ${locked.length===1?"is":"are"} marked “never trade or sell”. Unlock in the copy details first.`);
      return null;
    }
    if (!window.confirm(`\uD83D\uDD12 ${locked.length} locked card${locked.length===1?"":"s"} will be SKIPPED (${names}${locked.length>5?"…":""}).\n\nContinue with the other ${rest.length}?`)) return null;
    return rest;
  }
  // Upload a photo for one specific COPY. Quick-added and imported cards have no scan, so this is
  // how you attach pictures after the fact — and front/back are separate because the back is where
  // serial numbers, cert labels and damage usually are.
  async function uploadLotPhoto(cardId, file, side) {
    if (!user) { setSigningIn(true); return null; }
    if (!file) return null;
    try {
      // Downscale before upload; phone photos are many MB and slow the collection to a crawl.
      const dataUrl = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => {
          const img = new Image();
          img.onload = () => {
            const MAX = 1400;
            const scale = Math.min(1, MAX / Math.max(img.width, img.height));
            const cv = document.createElement("canvas");
            cv.width = Math.round(img.width * scale); cv.height = Math.round(img.height * scale);
            cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
            res(cv.toDataURL("image/jpeg", 0.85));
          };
          img.onerror = rej; img.src = fr.result;
        };
        fr.onerror = rej; fr.readAsDataURL(file);
      });
      const sref = ref(storage, `scan_photos/${user.uid}/${cardId}_${side}_${uid()}.jpg`);
      await uploadString(sref, dataUrl, "data_url");
      return await getDownloadURL(sref);
    } catch(e) {
      console.error("lot photo upload failed:", e);
      alert("Photo upload failed: " + (e?.code || e?.message || e));
      return null;
    }
  }
  // Per-card lot COUNT is rendered for every card in the grid. Calling lotsForCard() per card
  // was an O(cards × lots) full-scan on every render (hover, page bump, etc.) — a real perf drag.
  // Build the count map once per lots change; each card then does an O(1) lookup.
  const lotCountByCard = useMemo(() => {
    const m = {};
    for (const l of lots) { if(l && l.cardId) m[l.cardId] = (m[l.cardId]||0) + 1; }
    return m;
  }, [lots]);
  // First scanned photo per card (private) — used as the tile image when there's no official art.
  const scanPhotoByCard = useMemo(() => {
    const m = {};
    for (const l of lots) { if(l && l.cardId && l.photoUrl && !m[l.cardId]) m[l.cardId] = l.photoUrl; }
    return m;
  }, [lots]);

  // Export collection to CSV
  // FULL BACKUP. The CSV export is for reading in a spreadsheet; this is for not losing anything.
  // It captures the things that would actually hurt to lose and that no CSV column covers: decks you
  // built, per-copy financial lots, scan photo URLs, want list, trade flags, in-transit, listings.
  // JSON rather than CSV because this data is nested \u2014 flattening it would lose the structure that
  // makes it restorable.
  // RESTORE FROM BACKUP. An export nobody can import is not a backup, it is a document about a loss.
  //
  // Two rules shape this: it MERGES rather than overwrites by default, and it shows you exactly what
  // will change before writing anything. A restore that silently wipes what you already have is a
  // second disaster, and people reach for this while already panicking.
  //
  // Cards are re-matched by IDENTITY (set + number + treatment + hero), never by stored id, because
  // catalog ids can change between export and restore \u2014 which is exactly when you would need this.
  async function restoreFromBackup(file, mode) {
    if (!user) { setSigningIn(true); return null; }
    const text = await file.text();
    let data;
    try { data = JSON.parse(text); }
    catch { throw new Error("That file isn't valid JSON. Pick the .json backup file."); }
    if (data._format !== "bazooka-dash-backup") {
      throw new Error("That doesn't look like a Bazooka backup. Expected a file exported by \u201cFull Backup\u201d.");
    }

    // Re-match every backed-up card against the CURRENT catalog.
    const norm = s => String(s||"").toLowerCase().replace(/[\s\-_.]/g,"");
    const idIndex = new Map(cards.map(c => [c.id, c]));
    const identIndex = new Map();
    cards.forEach(c => {
      identIndex.set([norm(c.setName),norm(c.cardNum),norm(c.treatment),norm(c.hero)].join("|"), c);
    });
    const resolve = (rec) => {
      if (!rec) return null;
      const byIdent = identIndex.get([norm(rec.setName),norm(rec.cardNum),norm(rec.treatment),norm(rec.hero)].join("|"));
      if (byIdent) return byIdent;
      return idIndex.get(rec.id) || null;   // fall back to the raw id
    };

    const report = { cards:0, copies:0, decks:0, wants:0, tradeBait:0, missing:[], mode };

    // ---- collection ----
    const nextOwned = mode === "replace" ? {} : { ...owned };
    (data.collection || []).forEach(rec => {
      const c = resolve(rec);
      if (!c) { report.missing.push(`${rec.hero||"?"} ${rec.cardNum||""} (${rec.setName||"?"})`.trim()); return; }
      const qty = parseInt(rec.qty) || 1;
      // Merge keeps the HIGHER count: restoring must never reduce a collection you have since grown.
      nextOwned[c.id] = mode === "replace" ? qty : Math.max(parseInt(nextOwned[c.id])||0, qty);
      report.cards++;
    });

    // ---- per-copy lots ----
    const existingLotIds = new Set((lots||[]).map(l => l.id));
    const restoredLots = [];
    (data.copies || []).forEach(rec => {
      const c = resolve(rec.card) || (rec.cardId ? idIndex.get(rec.cardId) : null);
      if (!c) return;
      if (mode !== "replace" && existingLotIds.has(rec.id)) return;   // already there, don't duplicate
      const { card, ...lot } = rec;
      restoredLots.push({ ...lot, id: lot.id || uid(), cardId: c.id });
      report.copies++;
    });
    const nextLots = mode === "replace" ? restoredLots : [...(lots||[]), ...restoredLots];

    // ---- flags ----
    const nextWants = mode === "replace" ? {} : { ...wantList };
    (data.wantList || []).forEach(rec => { const c = resolve(rec); if (c) { nextWants[c.id] = true; report.wants++; } });
    const nextBait = mode === "replace" ? {} : { ...tradeBait };
    (data.tradeBait || []).forEach(rec => { const c = resolve(rec); if (c) { nextBait[c.id] = true; report.tradeBait++; } });

    // ---- write ----
    setOwned(nextOwned);
    queueOwnedSave(nextOwned);
    setLots(nextLots);
    try { await setDoc(doc(db,"boba_lots",user.uid), { lots: nextLots }); } catch(e){ console.error("restore lots failed:", e); }
    setWantList(nextWants);
    try { await setDoc(doc(db,"boba_wants",user.uid), nextWants); } catch(e){ console.error("restore wants failed:", e); }
    setTradeBait(nextBait);
    try { await setDoc(doc(db,"boba_tradebait",user.uid), nextBait); } catch(e){ console.error("restore bait failed:", e); }

    // ---- decks (each its own document) ----
    for (const d of (data.decks || [])) {
      const ids = (d.cards || []).map(resolve).filter(Boolean).map(c => c.id);
      if (!ids.length) continue;
      const did = d.id || `deck_${Date.now()}_${Math.random().toString(36).slice(2,7)}`;
      try {
        await setDoc(doc(db,"boba_decks",did), {
          id: did, userId: user.uid, name: d.name || "Restored deck",
          cardIds: ids, updatedAt: Date.now(), restoredAt: Date.now(),
        }, { merge: true });
        report.decks++;
      } catch(e){ console.error("restore deck failed:", e); }
    }
    return report;
  }

  // Read a backup WITHOUT writing anything, so the user can see what they're about to do.
  async function previewBackup(file) {
    const text = await file.text();
    let data;
    try { data = JSON.parse(text); }
    catch { throw new Error("That file isn't valid JSON."); }
    if (data._format !== "bazooka-dash-backup") throw new Error("Not a Bazooka backup file.");
    const norm = s => String(s||"").toLowerCase().replace(/[\s\-_.]/g,"");
    const identIndex = new Set(cards.map(c => [norm(c.setName),norm(c.cardNum),norm(c.treatment),norm(c.hero)].join("|")));
    let matched = 0, missing = 0;
    (data.collection || []).forEach(rec => {
      identIndex.has([norm(rec.setName),norm(rec.cardNum),norm(rec.treatment),norm(rec.hero)].join("|")) ? matched++ : missing++;
    });
    return {
      exportedAt: data.exportedAt || "unknown",
      account: data.account?.email || data.account?.uid || "unknown",
      cards: (data.collection||[]).length,
      copies: (data.copies||[]).length,
      decks: (data.decks||[]).length,
      photos: (data.scanPhotos||[]).length,
      wants: (data.wantList||[]).length,
      matched, missing,
    };
  }

  function exportFullBackup() {
    if (!user) { setSigningIn(true); return; }
    try {
      const ownedIds = Object.keys(owned || {});   // every card id you own
      // Card details are denormalised into each record on purpose. If this file is opened in five
      // years, or the catalog has changed, the backup still says what the card WAS.
      const cardInfo = id => {
        const c = cards.find(x => x.id === id) || {};
        return { id, hero:c.hero||"", setName:c.setName||"", treatment:c.treatment||"",
                 weapon:c.weapon||"", cardNum:c.cardNum||"", power:c.power??null };
      };
      const backup = {
        _format: "bazooka-dash-backup",
        _version: 1,
        exportedAt: new Date().toISOString(),
        account: { uid: user.uid, email: user.email || "", displayName: user.displayName || "" },

        // What you own, with quantities.
        collection: ownedIds.map(id => ({ ...cardInfo(id), qty: parseInt(owned[id]) || 1 })),

        // Per-copy records \u2014 cost, trade value, serial, grading, location, photos, locks.
        copies: (lots || []).map(l => ({ ...l, card: cardInfo(l.cardId) })),

        // Decks are hand-built and would be painful to reconstruct from memory.
        decks: (savedDecks || []).map(d => ({
          id: d.id, name: d.name, createdAt: d.createdAt ?? null, updatedAt: d.updatedAt ?? null,
          cards: (d.cardIds || []).map(cardInfo),
        })),

        // Your own photos of your own cards. URLs only \u2014 the images live in storage, but without
        // these you would not know which photo belonged to which card.
        scanPhotos: Object.entries(scanPhotoByCard || {}).map(([id, url]) => ({ ...cardInfo(id), url })),

        wantList:   Object.keys(wantList || {}).filter(k => wantList[k]).map(cardInfo),
        tradeBait:  Object.keys(tradeBait || {}).filter(k => tradeBait[k]).map(cardInfo),
        publicCards: Object.keys(publicCards || {}).filter(k => publicCards[k]),
        inTransit:  Object.entries(inTransit || {}).map(([id, v]) => ({ ...cardInfo(id), ...v })),
        listings:   (myListings || []).map(l => ({ ...l, card: cardInfo(l.cardId) })),
        sales:      (marketSales || []).filter(s => s.sellerUid === user.uid),
      };

      const counts = {
        cards: backup.collection.length, copies: backup.copies.length, decks: backup.decks.length,
        photos: backup.scanPhotos.length, wants: backup.wantList.length,
      };
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type:"application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bazooka-backup-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setToast(`Backed up ${counts.cards} cards, ${counts.copies} copies, ${counts.decks} decks, ${counts.photos} photos.`);
    } catch(e) {
      console.error("backup failed:", e);
      alert("Backup failed: " + (e?.message || e));
    }
  }

  // On-loan report. Answers two questions that are painful to reconstruct from memory: what have I
  // lent out and to whom, and what am I holding that belongs to someone else. Opens a clean printable
  // page rather than a CSV, because this is a list you hand to someone or check against a stack of
  // cards \u2014 not something you load into a spreadsheet.
  // On-loan manager state. A printable report answers "what is out?" but not "I got it back" \u2014
  // returning a card meant hunting for the right copy inside the card's Details modal, and a
  // mis-tagged card had no way back at all. This is the working list: see everything on loan and
  // act on it in one place.
  const [loanMgr, setLoanMgr] = useState(null);   // null | "borrowed" | "lent" | "all"
  // Multi-select for sending lent cards to a friend from the loan manager.
  const [loanSel, setLoanSel] = useState(new Set());   // set of lot ids checked
  const [loanSendTo, setLoanSendTo] = useState("");    // friendUid to send to
  const [loanSending, setLoanSending] = useState(false);

  // Clear the loan status on one copy, leaving the copy itself intact. Used for "returned to me"
  // and for undoing a mis-tag.
  function clearLoanOn(lotId) {
    saveLots((lots||[]).map(l => l.id === lotId
      ? { ...l, lendState:"", lendWho:"", lendWhy:"", lendDate:"" }
      : l));
  }

  // A borrowed card was never yours, so giving it back means the copy leaves your collection
  // entirely \u2014 unlike a lent card coming home, which just clears its status.
  async function returnBorrowedCopy(lot) {
    // Write BOTH updates directly rather than going through setOwnedQty. That helper re-reads
    // `owned` from the render closure and can re-prompt for a print version, which is wrong here \u2014
    // and the stale read meant the count sometimes never reached zero, so the card stayed in the
    // collection after being returned. Functional updater keeps it correct under rapid clicks.
    const nextLots = (lots||[]).filter(l => l.id !== lot.id);
    saveLots(nextLots);
    let saved = null;
    setOwned(prev => {
      const next = { ...prev };
      const left = (parseInt(next[lot.cardId]) || 0) - 1;
      if (left > 0) next[lot.cardId] = left; else delete next[lot.cardId];
      saved = next;
      return next;
    });
    if (saved) queueOwnedSave(saved);
  }

  // Proxy print list for IMC. Deliberately a clean printable page rather than a CSV: this gets sent
  // to someone who has to read it and print from it, not loaded into a spreadsheet. Deduped by card,
  // because the same slab proxied into three decks still only needs printing once.
  function printProxyReport() {
    if (!user) { setSigningIn(true); return; }
    // One row per FLAGGED COPY. This used to render one row per CARD with a Qty column,
    // which meant two copies of the same card shared a single note line \u2014 exactly the
    // thing the notes exist to prevent. A card you own twice, with only the vault copy
    // flagged, now prints one row for that copy and says which one it is.
    // One row per FLAGGED COPY, driven by what is ACTUALLY STORED rather than by the
    // owned count.
    //
    // The previous version scanned copy indices 0..owned-1, which silently dropped any
    // note living at an index at or beyond the current owned quantity — a note written
    // when you had three copies vanished from this report the moment the count fell to
    // two, taking the row with it. Notes are the whole reason this sheet exists, so the
    // report must never quietly discard one: read the real keys for this card, and treat
    // the owned count as a display hint only.
    const flaggedCopies = (cid) => {
      const idxs = new Set();
      if (proxyCards[cid] !== undefined) idxs.add(0);            // legacy flat key
      const pre = cid + "#";
      Object.keys(proxyCards).forEach(k => {
        if (!k.startsWith(pre)) return;
        const n = parseInt(k.slice(pre.length), 10);
        if (!isNaN(n) && proxyCards[k]) idxs.add(n);
      });
      return [...idxs].sort((a,b) => a-b);
    };
    const rows = cards
      .filter(c => anyProxy ? anyProxy(c.id) : proxyCards[c.id])
      .flatMap(c => {
        const owns = parseInt(owned[c.id]) || 0;
        const idxs = flaggedCopies(c.id);
        // q is what the "copy X of Y" label reads. It is the wider of what you own and
        // the highest flagged index, so a note is never hidden just because the owned
        // count is lower — which is normal: a proxy usually means you do NOT have the
        // physical card.
        const q = Math.max(owns, ...idxs.map(i => i+1), 1);
        return idxs.map(ci => ({ c, ci, q }));
      })
      // Any flagged copy whose CARD isn't in the loaded checklist would otherwise
      // vanish without trace — the card list can lag or a card id can change. Emit a
      // stub row so the note still reaches IMC and the gap is visible instead of silent.
      .concat((() => {
        const known = new Set(cards.map(c => c.id));
        const seen = new Set();
        const out = [];
        Object.keys(proxyCards).forEach(k => {
          if (!proxyCards[k]) return;
          const cid = k.includes("#") ? k.slice(0, k.lastIndexOf("#")) : k;
          if (known.has(cid)) return;
          const ci = k.includes("#") ? (parseInt(k.slice(k.lastIndexOf("#")+1), 10) || 0) : 0;
          const sig = cid + "#" + ci;
          if (seen.has(sig)) return;
          seen.add(sig);
          out.push({ c: { id: cid, hero: "(card not in checklist)", cardNum: cid }, ci, q: 1, notInChecklist: true });
        });
        return out;
      })())
      .sort((x,y) => String(x.c.setName||"").localeCompare(String(y.c.setName||""))
                  || String(x.c.cardNum||"").localeCompare(String(y.c.cardNum||""), undefined, {numeric:true})
                  || x.ci - y.ci);
    if (!rows.length) { alert("No cards are marked as proxies yet.\n\nSelect cards in the grid, then use \u201cMark as proxy\u201d."); return; }
    const esc = s => String(s==null?"":s).replace(/[&<>"]/g, ch => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;"}[ch]));
    // Which decks each proxy is for. Built here rather than reusing deckLocationText because that
    // helper phrases things for the deck builder ("your copy is in \u2026"); on a print list you just
    // want the deck names, and a card used in three decks should say so.
    // Which deck holds a SPECIFIC copy. Previously this listed every deck using the
    // card on every copy's row, so a card in two decks printed "Deck A, Deck B" twice
    // — telling IMC nothing about which physical card each line refers to. Copies are
    // assigned by deck age (oldest deck gets copy 0), matching deckCopyIndex.
    const deckForCopy = (cid, ci) => {
      const byAge = (savedDecks||[]).slice().sort((a,b) =>
        String(a.savedAt||"").localeCompare(String(b.savedAt||"")) ||
        String(a.id||"").localeCompare(String(b.id||""))
      );
      let i = 0;
      for (const d of byAge) {
        if (!(d.cardIds||[]).includes(cid)) continue;
        if (i === ci) return d.name || "Untitled deck";
        i++;
      }
      return "";   // this copy isn't committed to any deck
    };
    const body = rows.map(({c, ci, q, notInChecklist}, i) => `
      <tr>
        <td class="num">${i+1}</td>
        <td class="mono">${esc(c.cardNum||"\u2014")}</td>
        <td><strong>${esc(c.hero||c.playName||"\u2014")}</strong>${q>1?` <span class="copy">copy ${ci+1}/${q}</span>`:""}${notInChecklist?` <span class="orphan">card not found in checklist</span>`:""}</td>
        <td>${esc(c.variation||"\u2014")}</td>
        <td>${esc(c.treatment||"\u2014")}</td>
        <td>${esc(c.weapon||"\u2014")}</td>
        <td class="r">${esc(c.power??"")}</td>
        <td class="set">${esc(c.setName||"\u2014")}</td>
        <td class="note">${esc(deckForCopy(c.id, ci) || "\u2014 not in a deck")}</td>
        <td class="note">${esc(proxyNote ? proxyNote(c.id, ci) : "")}</td>
      </tr>`).join("");
    const totalCopies = rows.length;
    // Reconciliation. Count every note actually stored, so the sheet can prove it is
    // showing all of them rather than asking you to take it on trust.
    const storedNotes = Object.keys(proxyCards).filter(k => {
      const v = proxyCards[k];
      return typeof v === "string" && v.trim();
    }).length;
    const shownNotes = rows.filter(({c,ci}) => (proxyNote ? proxyNote(c.id, ci) : "").trim()).length;
    // CSV built from the SAME rows as the printed table, so the two can't drift.
    // RFC4180 quoting: wrap every field and double any embedded quote. Notes are
    // free text and will contain commas, quotes and line breaks eventually.
    const csvCell = v => '"' + String(v == null ? "" : v).replace(/"/g, '""') + '"';
    const csvHeader = ["#","Card #","Hero","Copy","Variation","Treatment","Weapon","Power","Set","Used in","Note"];
    const csvRows = rows.map(({c, ci, q}, i) => [
      i + 1,
      c.cardNum || "",
      c.hero || c.playName || "",
      q > 1 ? `copy ${ci+1} of ${q}` : "",
      c.variation || "",
      c.treatment || "",
      c.weapon || "",
      c.power == null ? "" : c.power,
      c.setName || "",
      deckForCopy(c.id, ci) || "",
      proxyNote ? proxyNote(c.id, ci) : "",
    ].map(csvCell).join(","));
    // BOM so Excel opens UTF-8 correctly — without it, card names with accents or
    // the \u2014 dash arrive as mojibake.
    const csvText = "\ufeff" + [csvHeader.map(csvCell).join(","), ...csvRows].join("\r\n");
    const csvFile = `proxy-list-${new Date().toISOString().slice(0,10)}.csv`;

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Proxy Print List</title>
      <style>
        body{font-family:'Trebuchet MS',sans-serif;margin:28px;color:#111;}
        h1{font-size:19px;margin:0 0 2px;}
        .sub{color:#666;font-size:12px;margin-bottom:16px;}
        table{border-collapse:collapse;width:100%;font-size:12.5px;}
        th{text-align:left;border-bottom:2px solid #111;padding:6px 8px;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;}
        td{border-bottom:1px solid #ddd;padding:7px 8px;}
        .mono{font-family:ui-monospace,monospace;}
        .copy{font-size:9.5px;font-weight:900;color:#B45309;background:#FEF3C7;border:1px solid #FDE68A;border-radius:4px;padding:0 4px;}
        .orphan{font-size:9.5px;font-weight:900;color:#991B1B;background:#FEE2E2;border:1px solid #FCA5A5;border-radius:4px;padding:0 4px;margin-left:4px;}
        .num{color:#999;width:34px;}
        .r{text-align:right;font-weight:700;}
        .set{color:#777;}
        .note{color:#555;font-size:11.5px;max-width:220px;}
        @media print{ body{margin:0;} .noprint{display:none;} }
      </style></head><body>
      <h1>Proxy Print List</h1>
      <div class="sub"><strong>${totalCopies} proxy card${totalCopies===1?"":"s"} to print</strong> \u00b7 ${new Set(rows.map(r=>r.c.id)).size} distinct card${new Set(rows.map(r=>r.c.id)).size===1?"":"s"} \u00b7 ${shownNotes} note${shownNotes===1?"":"s"}${shownNotes<storedNotes?` <span style="color:#991B1B;font-weight:800">(${storedNotes-shownNotes} stored note${storedNotes-shownNotes===1?"":"s"} not shown \u2014 tell Claude)</span>`:""} \u00b7 ${esc(myUsername||user.email||"")} \u00b7 ${new Date().toLocaleDateString()}</div>
      <table>
        <thead><tr><th>#</th><th>Card #</th><th>Hero</th><th>Variation</th><th>Treatment</th><th>Weapon</th><th class="r">Power</th><th>Set</th><th>Used in</th><th>Note</th></tr></thead>
        <tbody>${body}</tbody>
      </table>
      <p class="noprint" style="margin-top:18px;display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
        <button onclick="window.print()" style="padding:9px 18px;font-size:13px;font-weight:700;cursor:pointer;border-radius:7px;border:1px solid #111;background:#111;color:#fff;">Print / Save as PDF</button>
        <button id="dlcsv" style="padding:9px 18px;font-size:13px;font-weight:700;cursor:pointer;border-radius:7px;border:1px solid #111;background:#fff;color:#111;">Download CSV</button>
        <span style="font-size:11.5px;color:#777;">For a PDF, choose <strong>Save as PDF</strong> as the destination in the print dialog.</span>
      </p>
      <script>
        // The CSV is embedded as base64 so the string survives being written into this
        // document — raw text with quotes and newlines would terminate the script early.
        (function(){
          var b64 = "${(typeof btoa === "function"
              ? btoa(unescape(encodeURIComponent(csvText)))
              : Buffer.from(csvText, "utf8").toString("base64"))}";
          var bin = atob(b64);
          var bytes = new Uint8Array(bin.length);
          for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          var blob = new Blob([bytes], { type: "text/csv;charset=utf-8;" });
          document.getElementById("dlcsv").addEventListener("click", function(){
            var a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = ${JSON.stringify(csvFile)};
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            setTimeout(function(){ URL.revokeObjectURL(a.href); }, 2000);
          });
        })();
      <\/script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Your browser blocked the report window. Allow pop-ups for this site and try again."); return; }
    w.document.write(html); w.document.close();
  }


  function printLoanReport(which) {
    if (!user) { setSigningIn(true); return; }
    const want = which === "borrowed" ? "borrowed" : which === "lent" ? "lent" : null;
    const rows = (lots||[])
      .filter(l => l.lendState && (!want || l.lendState === want))
      .map(l => {
        const card = cards.find(x => x.id === l.cardId) || {};
        return {
          state: l.lendState,
          who:   l.lendWho || "\u2014",
          why:   l.lendWhy || "",
          since: l.lendDate || "",
          hero:  card.hero || card.playName || "(unknown card)",
          set:   card.setName || "",
          treat: [card.treatment, card.weapon].filter(Boolean).join(" \u00b7 "),
          num:   card.cardNum || "",
          value: l.value != null && l.value !== "" ? parseFloat(l.value) : null,
        };
      })
      // Group by person, then oldest loan first \u2014 the ones out longest are the ones to chase.
      .sort((a,b) => a.who.localeCompare(b.who) || String(a.since).localeCompare(String(b.since)));

    if (!rows.length) {
      alert(want === "borrowed" ? "You aren't holding any borrowed cards."
          : want === "lent"     ? "You haven't lent any cards out."
          : "Nothing is on loan in either direction.");
      return;
    }

    const esc = s => String(s==null?"":s).replace(/[&<>"]/g, ch => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;"}[ch]));
    const title = want === "borrowed" ? "Cards I'm Borrowing"
                : want === "lent"     ? "Cards I've Lent Out"
                : "Cards On Loan";
    const totalVal = rows.reduce((s,r)=>s+(r.value||0), 0);
    const body = rows.map(r => `
      <tr>
        <td class="who">${esc(r.who)}</td>
        <td><strong>${esc(r.hero)}</strong>${r.num?` <span class="dim">#${esc(r.num)}</span>`:""}
            ${r.treat?`<div class="dim">${esc(r.treat)}</div>`:""}
            ${r.set?`<div class="dim">${esc(r.set)}</div>`:""}</td>
        <td>${esc(r.why)}</td>
        <td class="mono">${esc(r.since)}</td>
        ${want ? "" : `<td>${r.state === "lent" ? "Lent out" : "Borrowed"}</td>`}
        <td class="mono num">${r.value!=null?"$"+r.value.toFixed(2):""}</td>
      </tr>`).join("");

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
      <style>
        body{font-family:'Trebuchet MS',sans-serif;margin:28px;color:#111;}
        h1{font-size:19px;margin:0 0 2px;}
        .sub{color:#666;font-size:12px;margin-bottom:16px;}
        table{border-collapse:collapse;width:100%;font-size:12px;}
        th{text-align:left;border-bottom:2px solid #111;padding:6px 8px;font-size:11px;text-transform:uppercase;letter-spacing:0.4px;}
        td{border-bottom:1px solid #ddd;padding:7px 8px;vertical-align:top;}
        .who{font-weight:700;white-space:nowrap;}
        .dim{color:#777;font-size:11px;}
        .mono{font-family:ui-monospace,monospace;white-space:nowrap;}
        .num{text-align:right;}
        tfoot td{border:none;padding-top:10px;font-weight:700;}
        @media print{ body{margin:0;} .noprint{display:none;} }
      </style></head><body>
      <h1>${esc(title)}</h1>
      <div class="sub">${rows.length} card${rows.length===1?"":"s"} \u00b7 generated ${new Date().toLocaleDateString()}</div>
      <table>
        <thead><tr>
          <th>${want === "borrowed" ? "From" : want === "lent" ? "With" : "Person"}</th>
          <th>Card</th><th>Reason</th><th>Since</th>${want?"":"<th>Direction</th>"}<th class="num">Value</th>
        </tr></thead>
        <tbody>${body}</tbody>
        ${totalVal>0?`<tfoot><tr><td colspan="${want?4:5}">Total declared value</td><td class="num">$${totalVal.toFixed(2)}</td></tr></tfoot>`:""}
      </table>
      <p class="noprint" style="margin-top:18px;">
        <button onclick="window.print()" style="padding:8px 16px;font-size:13px;cursor:pointer;">Print</button>
      </p>
      </body></html>`;

    const w = window.open("", "_blank");
    if (!w) { alert("Your browser blocked the report window. Allow pop-ups for this site and try again."); return; }
    w.document.write(html);
    w.document.close();
  }

  function exportCollection() {
    if (!user) { setSigningIn(true); return; }
    const csvEsc = v => { const s = v===null||v===undefined ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s; };
    // Respect whatever the grid is currently filtered to. Exporting the whole collection when you
    // have deliberately narrowed it to two treatments is not the report you asked for, and
    // filtering a 30k-row CSV afterwards defeats the point of filtering in the app.
    // `sorted` is the full filtered list — visibleCards is paginated and would silently export
    // only the first page. It is declared below this function, but a const is initialised long
    // before any click can reach here.
    const _filteredIds = new Set(sorted.map(x => x.id));
    const _narrowed = _filteredIds.size > 0 && _filteredIds.size < cards.length;
    const ownedIds = Object.keys(owned)
      .filter(id => owned[id])
      .filter(id => !_narrowed || _filteredIds.has(id));
    if (!ownedIds.length) {
      alert(_narrowed ? "You do not own any cards matching the current filters." : "No cards owned yet.");
      return;
    }
    let totalSpent = 0, totalValue = 0;
    const rows = [];
    ownedIds.forEach(id => {
      const c = cards.find(x => x.id===id) || {};
      const qty = parseInt(owned[id]) || 1;
      const cardLots = lotsForCard(id);
      if (cardLots.length > 0) {
        // One row per lot (an owner may have multiple copies acquired differently)
        cardLots.forEach(l => {
          const cost = parseFloat(l.cost)||0, value = parseFloat(l.value)||0;
          totalSpent += cost; totalValue += value;
          rows.push([c.hero||"", c.setName||"", c.treatment||"", c.weapon||"", c.cardNum||"", c.power??"", 1,
            l.cost??"", l.value??"", (value-cost).toFixed(2), l.method||"", l.date||"", l.notes||""]);
        });
      } else {
        // Owned but no financial lot recorded
        rows.push([c.hero||"", c.setName||"", c.treatment||"", c.weapon||"", c.cardNum||"", c.power??"", qty, "", "", "", "", "", ""]);
      }
    });
    const header = ["Hero","Set","Treatment","Weapon","Card #","Power","Qty","Cost","Value","Gain/Loss","Acquisition","Date","Notes"];
    const totalCards = ownedIds.reduce((s,id)=>s+(parseInt(owned[id])||1),0);
    const summary = [
      [], ["SUMMARY"],
      ["Total Cards", totalCards],
      ["Total Spent", totalSpent.toFixed(2)],
      ["Total Value", totalValue.toFixed(2)],
      ["Total Gain/Loss", (totalValue-totalSpent).toFixed(2)],
      ["Exported", new Date().toLocaleString()],
    ];
    const lines = [header, ...rows, ...summary].map(r => r.map(csvEsc).join(","));
    const blob = new Blob([lines.join("\n")], { type:"text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bazooka-collection-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const _cardAdmin = (user?.email||"").toLowerCase().endsWith("@bazookabreaks.com");

  // Permanently delete selected cards from the SHARED checklist. Admin only, and deliberately
  // heavier than the other bulk actions: everything else here changes the current user's own data,
  // but this destroys cards for every collector on the platform. Hence the typed confirmation —
  // a misclick on 400 selected cards is not something a yes/no dialog should be able to do.
  // ── Bulk edit ────────────────────────────────────────────────────────────────────────────────
  // A typo in a hero name ("King Truck" for "King Tuck") is wrong on every card that hero appears
  // on. Fixing that one card at a time is absurd. Select them, set the field once, done.
  //
  // Deliberately a REPLACE, not a blind overwrite: you see exactly which distinct values are about
  // to change and what they become, before anything is written. Selecting a mixed batch and setting
  // hero="King Tuck" would otherwise silently flatten several different heroes into one.
  const [bulkEditOpen,  setBulkEditOpen]  = useState(false);
  // {fieldId: newValue} for every field you've switched on. A typo like "Kyle Trucker" usually lives
  // in BOTH hero and inspiredBy, so fixing one field at a time means running the same selection twice.
  const [bulkEdits,     setBulkEdits]     = useState({});
  const [bulkImgUploading, setBulkImgUploading] = useState(false);
  const [bulkImgOverwrite, setBulkImgOverwrite] = useState(false);  // replace existing/placeholder art
  // Match on hero name when the card number in the filename does not line up with the checklist.
  // On by default: a wrong card number is common, and a skipped file is more annoying than a
  // match you can undo.
  const [bulkImgHeroMatch, setBulkImgHeroMatch] = useState(true);
  const [bulkEditing,   setBulkEditing]   = useState(false);

  const BULK_FIELDS = [
    { id:"hero",       label:"Hero" },
    { id:"treatment",  label:"Treatment" },
    { id:"weapon",     label:"Weapon" },
    { id:"setName",    label:"Set" },
    { id:"notation",   label:"Notation" },
    { id:"variation",  label:"Variation" },
    { id:"inspiredBy", label:"Inspired By" },
    { id:"playName",   label:"Play Name" },
    { id:"power",      label:"Power" },
    { id:"imageUrl",   label:"Image URL (placeholder)", isImage:true },
  ];

  async function bulkEditCards() {
    const fields = Object.keys(bulkEdits);
    if (!_cardAdmin || selectedIds.size===0 || fields.length===0) return;
    setBulkEditing(true);
    try {
      // Build the patch once. Power stays NUMERIC \u2014 sorting, power caps and deck legality all compare
      // it as a number, and a string "150" would quietly break every one of them.
      const patch = {};
      fields.forEach(f => {
        const raw = String(bulkEdits[f] ?? "").trim();
        patch[f] = f === "power" ? (raw === "" ? null : Number(raw)) : raw;
      });

      const ids = [...selectedIds];
      const byId = new Map(cards.map(c=>[c.id,c]));
      const now = new Date().toISOString();
      const editor = user?.email || user?.displayName || "admin";
      // Power edits get extra bookkeeping, PER CARD: remember what the power was before the very
      // first change (so a buff/nerf can be shown as "was 150"), and stamp who changed it and when.
      // originalPower is only ever written once — re-editing a card later must not overwrite the
      // true original with an already-adjusted value.
      const patchFor = (id) => {
        if (!("power" in patch)) return patch;
        const c = byId.get(id) || {};
        const prev = c.power;
        const extra = { powerEditedBy: editor, powerEditedAt: now };
        if (c.originalPower === undefined || c.originalPower === null) {
          if (prev !== undefined && prev !== null && Number(prev) !== Number(patch.power)) {
            extra.originalPower = Number(prev);
          }
        }
        return { ...patch, ...extra };
      };

      for (let i=0; i<ids.length; i+=300) {
        const batch = writeBatch(db);
        ids.slice(i, i+300).forEach(id =>
          batch.set(doc(db,"boba_checklist",id), patchFor(id), { merge:true })
        );
        await batch.commit();
      }
      // Reflect locally so the grid updates without a reload.
      setCards(prev => prev.map(c => selectedIds.has(c.id) ? { ...c, ...patchFor(c.id) } : c));
      setToast(`\u2713 Updated ${fields.length} field${fields.length===1?"":"s"} on ${ids.length} card${ids.length===1?"":"s"}`);
      setBulkEditOpen(false); setBulkEdits({}); clearSelection();
    } catch(e) {
      alert("Bulk edit failed: " + e.message);
    }
    setBulkEditing(false);
  }

  const [bulkDelOpen, setBulkDelOpen] = useState(false);
  const [bulkDelText, setBulkDelText] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Bulk add/remove from the want list. One write for the whole selection rather than one per card.
  //
  // Removing only ever touches cards that are actually ON the list, so sweeping a mixed selection
  // with "Remove" won't quietly add the rest.
  async function bulkWant(add) {
    if (!user || selectedIds.size===0) return;
    const next = {...wantList};
    let n = 0;
    selectedIds.forEach(id => {
      if (add) { if (!next[id]) { next[id] = 1; n++; } }
      else     { if (next[id])  { delete next[id]; n++; } }
    });
    if (n === 0) {
      showToast(add ? "Already on your want list" : "None of those were on your want list");
      return;
    }
    setWantList(next);
    try {
      await setDoc(doc(db,"boba_wants",user.uid), next);
      showToast(add
        ? `\u2713 ${n} card${n===1?"":"s"} added to your want list`
        : `\u2713 ${n} card${n===1?"":"s"} removed from your want list`);
      clearSelection();
    } catch(e) {
      alert("Couldn't update your want list: " + e.message);
    }
  }

  // Bulk-mark the selected cards as lent out to one person. One prompt for the
  // name, applied to every selected card you own. Tags an in-hand copy where one
  // exists, else creates a lot — same as the single-card markCardLent, batched
  // into a single save.
  // Bulk lend/borrow now open a modal (friend picker + return date) instead of a
  // bare prompt, matching the single-card flow.
  function bulkMarkLent() {
    if (!user || selectedIds.size===0) return;
    const ids = [...selectedIds].filter(id => owned[id]);
    if (!ids.length) { showToast("Select cards you own to lend out."); return; }
    setBulkLoanOpen("lent");
  }
  function bulkMarkBorrowed() {
    if (!user || selectedIds.size===0) return;
    setBulkLoanOpen("borrowed");
  }

  // Commit a bulk lend. who = name (friend name wins), friend = app-friend object
  // (triggers send), returnBy = optional date stamped on each lot + ledger.
  async function commitBulkLent(who, friend, returnBy) {
    if (!user) return;
    const ids = [...selectedIds].filter(id => owned[id]);
    if (!ids.length) return;
    const name = (who||"").trim();
    if (!name) return;
    const stamp = todayLocal();
    let next = lots.slice();
    let n = 0;
    ids.forEach(cardId => {
      const idx = next.findIndex(l => l.cardId===cardId && (l.lendState||"")==="");
      if (idx >= 0) { next[idx] = { ...next[idx], lendState:"lent", lendWho:name, lendDate:stamp, returnBy: returnBy||"" }; n++; }
      else { next = [...next, { id: uid(), cardId, cost:null, value:null, method:"owned",
             date:stamp, notes:"", lendState:"lent", lendWho:name, lendWhy:"", lendDate:stamp, returnBy: returnBy||"" }]; n++; }
    });
    await saveLots(next);
    if (friend?.friendUid) {
      const res = await sendLoanToFriend(friend, ids, returnBy);
      showToast(res?.ok ? `\uD83D\uDCE4 Lent ${n} to ${name} \u2014 sent to accept` : `\uD83D\uDCE4 Lent ${n}, but send failed`);
    } else {
      showToast(`\uD83D\uDCE4 Marked ${n} card${n===1?"":"s"} lent to ${name}`);
    }
    setBulkLoanOpen(null); clearSelection();
  }

  async function commitBulkBorrowed(who, _friend, returnBy) {
    if (!user) return;
    const ids = [...selectedIds];
    const name = (who||"").trim();
    if (!name || !ids.length) return;
    const stamp = todayLocal();
    let next = lots.slice();
    const ownedNext = { ...(owned||{}) };
    ids.forEach(cardId => {
      next = [...next, { id: uid(), cardId, cost:null, value:null, method:"borrowed",
             date:stamp, notes:"", lendState:"borrowed", lendWho:name, lendWhy:"", lendDate:stamp, returnBy: returnBy||"" }];
      ownedNext[cardId] = (parseInt(ownedNext[cardId])||0) + 1;
    });
    await saveLots(next);
    setOwned(ownedNext);
    try { await setDoc(doc(db,"boba_owned",user.uid), ownedNext); } catch(e){ console.error("bulk borrow owned save failed:", e); }
    showToast(`\uD83D\uDCE5 Marked ${ids.length} card${ids.length===1?"":"s"} borrowed from ${name}`);
    setBulkLoanOpen(null); clearSelection();
  }

  async function bulkDeleteCards() {
    if (!_cardAdmin || selectedIds.size===0) return;
    setBulkDeleting(true);
    try {
      const ids = [...selectedIds];
      for (let i=0; i<ids.length; i+=300) {
        const batch = writeBatch(db);
        ids.slice(i, i+300).forEach(id => batch.delete(doc(db,"boba_checklist",id)));
        await batch.commit();
      }
      // Drop them from the local list too, so the grid reflects it without a reload.
      setCards(prev => prev.filter(c => !selectedIds.has(c.id)));
      setToast(`\u2713 ${ids.length} card${ids.length===1?"":"s"} deleted from the database`);
      clearSelection();
      setBulkDelOpen(false); setBulkDelText("");
    } catch(e) {
      alert("Delete failed: " + e.message);
    }
    setBulkDeleting(false);
  }
  const [bulkImg, setBulkImg] = useState(null); // {files, setName} | null
  const [bulkProg, setBulkProg] = useState(null); // {done,total,matched,skipped,status}
  const [showBetaWelcome, setShowBetaWelcome] = useState(false);
  useEffect(() => {
    if (swancity) {
      try { if (!localStorage.getItem("bz_swancity_tour_done")) setTimeout(()=>setTourStep(0), 600); } catch(e){}
      return;
    }
    try { if (!localStorage.getItem("bz_beta_welcomed")) setShowBetaWelcome(true); } catch(e){}
  }, []);
  function dismissBetaWelcome() {
    try { localStorage.setItem("bz_beta_welcomed", "1"); } catch(e){}
    setShowBetaWelcome(false);
    try { if (!localStorage.getItem("bz_tour_done")) setTimeout(()=>setTourStep(0), 350); } catch(e){}
  }
  const [resetModal, setResetModal] = useState(false); // marketplace reset confirmation
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetting, setResetting] = useState(null); // status string while wiping
  async function handleCardImageUpload(card, file) {
    if (!card || !file) return;
    try {
      const fsId = card.fsId || card.id;
      const safe = `${card.setName||"set"}_${card.cardNum||"x"}_${card.treatment||"t"}`.replace(/[^a-zA-Z0-9_]/g,"_");
      // Cache-bust the storage path so the new image URL is unique (avoids stale CDN/browser cache showing the old pic)
      const storageRef2 = ref(storage, `boba_cards/manual/${safe}_${Date.now()}.png`);
      const _cfile = await compressImage(file);
      await uploadBytes(storageRef2, _cfile);
      const url = await getDownloadURL(storageRef2);
      await setDoc(doc(db,"boba_checklist",fsId), { imageUrl:url }, { merge:true });
      // Update the UI immediately — this page and the open modal
      setCards(cs => cs.map(c => c.id===card.id ? { ...c, imageUrl:url } : c));
      setExpandedCard(ec => ec && ec.id===card.id ? { ...ec, imageUrl:url } : ec);
      // Refresh the shared snapshot so it shows for everyone, not just this device
      // Rebuild the shared snapshot. These used to be silently swallowed, which produced the worst
      // possible failure: Firestore has the new value, but every client (including this one, on the
      // next load) keeps reading the STALE .gz snapshot — so the edit looks like it never saved.
      const snapProblems = [];
      try {
        const snap2 = await getDocs(collection(db,"boba_checklist"));
        const all = snap2.docs.map(d=>({...d.data(), id:d.id}));
        const blob = new Blob([JSON.stringify(all)],{type:"application/json"});
        try { await uploadBytes(ref(storage,"card_data/boba_checklist.json"), blob, {contentType:"application/json",cacheControl:"public,max-age=300"}); }
        catch(e){ snapProblems.push("plain JSON snapshot: "+(e?.message||e)); }
        try { await writeCardSnapshot(all, 300); } catch(e) { snapProblems.push("gzip snapshot (this is the one clients read): "+(e?.message||e)); }
        try { await setDoc(doc(db,"meta","cards_version"),{ts:Date.now()}); } catch(e){ snapProblems.push("version bump: "+(e?.message||e)); }
        try { idbSetCards(all, Date.now()); } catch(e){ snapProblems.push("local cache: "+(e?.message||e)); }
      } catch(e) { snapProblems.push("reading boba_checklist: "+(e?.message||e)); }
      if (snapProblems.length) {
        console.error("[card edit] snapshot rebuild problems", snapProblems);
        alert("The card was saved to the database, but publishing it failed:\n\n• " + snapProblems.join("\n• ") +
              "\n\nUntil the snapshot rebuilds, the app will keep showing the OLD value. Try the \u201cRebuild Card Snapshot\u201d button in the importer.");
      }
      try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
      setToast("✅ Image updated");
    } catch(e) { alert("Upload failed: "+(e?.message||e)); }
  }

  async function handleCardImageClear(card) {
    if (!card) return;
    try {
      const fsId = card.fsId || card.id;
      await setDoc(doc(db,"boba_checklist",fsId), { imageUrl: deleteField() }, { merge:true });
      // update this page immediately
      setCards(cs => cs.map(c => c.id===card.id ? { ...c, imageUrl:null } : c));
      // refresh snapshot + version so it clears everywhere
      // Rebuild the shared snapshot. These used to be silently swallowed, which produced the worst
      // possible failure: Firestore has the new value, but every client (including this one, on the
      // next load) keeps reading the STALE .gz snapshot — so the edit looks like it never saved.
      const snapProblems = [];
      try {
        const snap2 = await getDocs(collection(db,"boba_checklist"));
        const all = snap2.docs.map(d=>({...d.data(), id:d.id}));
        const blob = new Blob([JSON.stringify(all)],{type:"application/json"});
        try { await uploadBytes(ref(storage,"card_data/boba_checklist.json"), blob, {contentType:"application/json",cacheControl:"public,max-age=300"}); }
        catch(e){ snapProblems.push("plain JSON snapshot: "+(e?.message||e)); }
        try { await writeCardSnapshot(all, 300); } catch(e) { snapProblems.push("gzip snapshot (this is the one clients read): "+(e?.message||e)); }
        try { await setDoc(doc(db,"meta","cards_version"),{ts:Date.now()}); } catch(e){ snapProblems.push("version bump: "+(e?.message||e)); }
        try { idbSetCards(all, Date.now()); } catch(e){ snapProblems.push("local cache: "+(e?.message||e)); }
      } catch(e) { snapProblems.push("reading boba_checklist: "+(e?.message||e)); }
      if (snapProblems.length) {
        console.error("[card edit] snapshot rebuild problems", snapProblems);
        alert("The card was saved to the database, but publishing it failed:\n\n• " + snapProblems.join("\n• ") +
              "\n\nUntil the snapshot rebuilds, the app will keep showing the OLD value. Try the \u201cRebuild Card Snapshot\u201d button in the importer.");
      }
      setToast("🧹 Image cleared — you can add the correct one now");
    } catch(e) { alert("Clear failed: "+(e?.message||e)); }
  }

  // -- Admin manual edit of card fields (power, weapon, treatment, etc.) right on /cards --
  async function handleCardFieldEdit(card, updates) {
    if (!card) return;
    try {
      const fsId = card.fsId || card.id;
      // BUFF / NERF PROVENANCE. The first time a card's power actually changes, remember what it was
      // so the card can publicly show "was 150". Written once only — editing power again later must
      // not overwrite the true original with an already-adjusted value. Also stamp who/when, which
      // only admins see.
      if (Object.prototype.hasOwnProperty.call(updates, "power")) {
        const prev = card.power, next = updates.power;
        const changed = prev != null && next != null && Number(prev) !== Number(next);
        if (changed) {
          // A power change is not cosmetic: decks store card IDs and look power up live, so this
          // silently re-decides legality for every saved deck holding this card. Crossing 160 makes
          // a card Apex-eligible; dropping below 155 can break someone's Max Apex deck. Surface the
          // blast radius BEFORE writing, because after the write it just looks like their deck broke.
          const p1 = Number(prev), p2 = Number(next);
          const crossings = [];
          if (p1 <= 160 && p2 > 160) crossings.push("goes over 160, so it is no longer legal in Spec / Vegas Baby and now counts as an Apex Hero in Madness");
          if (p1 > 160 && p2 <= 160) crossings.push("drops to 160 or under, so it becomes Spec-legal and stops counting as a Madness Apex Hero");
          if (p1 >= 155 && p2 < 155) crossings.push("falls below the 155 Max Apex floor");
          if (p1 < 155 && p2 >= 155) crossings.push("reaches the 155 Max Apex floor");
          if (p1 >= 115 && p2 < 115) crossings.push("falls below the 115 Max Spec floor");
          let affected = 0;
          try {
            const dsnap = await getDocs(collection(db,"boba_decks"));
            dsnap.docs.forEach(d => { const ids = d.data()?.cardIds || []; if (ids.includes(card.id)) affected++; });
          } catch(e) { /* if we can't count, still warn about the rule change */ }
          const lines = [`Change ${card.hero||"this card"} from ${p1} to ${p2} power?`];
          if (crossings.length) lines.push("", "This crosses a format boundary — the card " + crossings.join("; ") + ".");
          if (affected > 0) lines.push("", `${affected} saved deck${affected===1?"":"s"} already contain${affected===1?"s":""} this card. Their legality is recalculated live, so some may become illegal.`);
          lines.push("", "Continue?");
          if (!window.confirm(lines.join("\n"))) return;
          if (card.originalPower === undefined || card.originalPower === null) {
            updates = { ...updates, originalPower: Number(prev) };
          }
          updates = { ...updates,
            powerEditedBy: (user?.email || user?.displayName || "admin"),
            powerEditedAt: new Date().toISOString() };
        }
      }
      const clean = {};
      Object.entries(updates).forEach(([k,v]) => { clean[k] = (v===""||v==null) ? deleteField() : v; });
      // Log exactly what we're writing and where. When an edit silently reverts, the cause is almost
      // always that the doc ID doesn't exist (so merge:true creates a NEW doc that nothing reads) or
      // the rules reject the write — both are invisible without this.
      console.log("[card edit] writing to boba_checklist/" + fsId, clean);
      const _ref = doc(db,"boba_checklist",fsId);
      const _existing = await getDoc(_ref);
      if (!_existing.exists()) {
        alert("Save aborted: no card document found at boba_checklist/" + fsId +
              "\n\nThe card in the app has id \"" + card.id + "\"" +
              (card.fsId ? " and fsId \"" + card.fsId + "\"" : "") +
              ", which doesn't match a Firestore document. Writing would create an orphan the app never reads.");
        return;
      }
      await setDoc(_ref, clean, { merge:true });
      // Update this page immediately (replace deleteField sentinels with the actual value/blank)
      const localUpdates = {}; Object.entries(updates).forEach(([k,v])=>{ localUpdates[k] = (v===""||v==null)?null:v; });
      setCards(cs => cs.map(c => c.id===card.id ? { ...c, ...localUpdates } : c));
      setExpandedCard(ec => ec && ec.id===card.id ? { ...ec, ...localUpdates } : ec);
      // Refresh the shared snapshot so the edit shows for everyone
      // Rebuild the shared snapshot. These used to be silently swallowed, which produced the worst
      // possible failure: Firestore has the new value, but every client (including this one, on the
      // next load) keeps reading the STALE .gz snapshot — so the edit looks like it never saved.
      const snapProblems = [];
      try {
        const snap2 = await getDocs(collection(db,"boba_checklist"));
        const all = snap2.docs.map(d=>({...d.data(), id:d.id}));
        const blob = new Blob([JSON.stringify(all)],{type:"application/json"});
        try { await uploadBytes(ref(storage,"card_data/boba_checklist.json"), blob, {contentType:"application/json",cacheControl:"public,max-age=300"}); }
        catch(e){ snapProblems.push("plain JSON snapshot: "+(e?.message||e)); }
        try { await writeCardSnapshot(all, 300); } catch(e) { snapProblems.push("gzip snapshot (this is the one clients read): "+(e?.message||e)); }
        try { await setDoc(doc(db,"meta","cards_version"),{ts:Date.now()}); } catch(e){ snapProblems.push("version bump: "+(e?.message||e)); }
        try { idbSetCards(all, Date.now()); } catch(e){ snapProblems.push("local cache: "+(e?.message||e)); }
      } catch(e) { snapProblems.push("reading boba_checklist: "+(e?.message||e)); }
      if (snapProblems.length) {
        console.error("[card edit] snapshot rebuild problems", snapProblems);
        alert("The card was saved to the database, but publishing it failed:\n\n• " + snapProblems.join("\n• ") +
              "\n\nUntil the snapshot rebuilds, the app will keep showing the OLD value. Try the \u201cRebuild Card Snapshot\u201d button in the importer.");
      }
      try { localStorage.removeItem("boba_checklist_cache_v3"); } catch {}
      setToast("✅ Card updated");
      setCardEditMode(false);
    } catch(e) { alert("Save failed: "+(e?.message||e)); }
  }

  // -- Re-submit a better photo for an existing claim (super or 1/1). Replaces just the photo. --
  async function resubmitClaimPhoto(coll, card, file) {
    if (!coll || !card || !file) return;
    setResubmitting(true);
    try {
      const sref = ref(storage, `${coll}/${card.id}_${Date.now()}.jpg`);
      const _cfile = await compressImage(file);
      await uploadBytes(sref, _cfile);
      const url = await getDownloadURL(sref);
      await setDoc(doc(db, coll, card.id), { photoUrl: url, photoUpdatedAt: new Date().toISOString() }, { merge: true });
      setToast("📸 Photo updated — thank you!");
      setResubmitClaim(null);
    } catch(e) { alert("Photo update failed: "+(e?.message||e)); }
    setResubmitting(false);
  }

  // -- Submit a /34 BoJax claim for a specific serial number (doc id = serial). --
  async function submitBojaxClaim(serial, photoBase64, name, story, date, adminMode) {
    if (!user && !swancity) { setSigningIn(true); return; }
    setBojaxSubmitting(true);
    try {
      const bjCard = cards.find(c => (c.cardNum||"").toUpperCase()==="TBJ-B") || {};
      let photoUrl = null;
      if (photoBase64 && typeof photoBase64==="string" && photoBase64.startsWith("data:")) {
        const sref = ref(storage, `bojax34_claims/${serial}_${Date.now()}.jpg`);
        const bs = atob(photoBase64.split(",")[1]); const ab = new ArrayBuffer(bs.length); const ia = new Uint8Array(ab);
        for (let i=0;i<bs.length;i++) ia[i]=bs.charCodeAt(i);
        await uploadBytes(sref, new Blob([ab],{type:"image/jpeg"}));
        photoUrl = await getDownloadURL(sref);
      }
      await setDoc(doc(db,"bojax34_claims",String(serial)), {
        serial: Number(serial),
        cardName: bjCard.hero || "BoJax",
        cardNum: "TBJ-B",
        cardImage: bjCard.imageUrl || null,
        photoUrl,
        submitterName: name || "Anonymous",
        story: story || "",
        dateHit: date || "",
        userId: adminMode ? null : (user?.uid || "anon"),
        status: adminMode ? "verified" : "pending",
        ...(adminMode ? { recordedByAdmin: user?.displayName||user?.email||"Admin", reviewedAt:new Date().toISOString() } : {}),
        createdAt: new Date().toISOString(),
      }, { merge: true });
      setBojaxModal(null); setBojaxPhoto(null); setBojaxName(""); setBojaxStory(""); setBojaxDate("");
      if (!adminMode) { setSuperCelebration({ cardName:`BoJax ${serial}/34`, cardImage: bjCard.imageUrl||null, type:"oneof1" }); }
    } catch(e) { alert("Claim failed: "+(e?.message||e)); }
    setBojaxSubmitting(false);
  }

  async function verifyBojaxClaim(serial, verified) {
    try { await setDoc(doc(db,"bojax34_claims",String(serial)), { status: verified?"verified":"denied", reviewedAt:new Date().toISOString() }, { merge:true }); } catch(e){ alert("Failed: "+e.message); }
  }
  async function removeBojaxClaim(serial) {
    if (!window.confirm(`Remove the claim for BoJax #${serial}/34?`)) return;
    try { await deleteDoc(doc(db,"bojax34_claims",String(serial))); } catch(e){ alert("Failed: "+e.message); }
  }

  // -- Bulk upload all /34 BoJax hit photos. Files mapped to serial by the number
  //    in the filename (e.g. "12.jpg" -> serial 12, "BoJax_07_of_34.png" -> 7).
  async function bulkUploadBojax34(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const bjCard = cards.find(c => (c.cardNum||"").toUpperCase()==="TBJ-B") || {};
    const admin = user?.displayName || user?.email || "Admin";
    const now = new Date().toISOString();
    // map each file to a serial 1..34 from the first number in its name
    const mapped = [];
    for (const f of files) {
      const m = (f.name||"").match(/\d{1,2}/g);
      let serial = null;
      if (m) { for (const n of m) { const v = Number(n); if (v>=1 && v<=34) { serial = v; break; } } }
      mapped.push({ file:f, serial });
    }
    const unresolved = mapped.filter(x => !x.serial).map(x=>x.file.name);
    const seen = {};
    const toUpload = [];
    for (const x of mapped) {
      if (!x.serial) continue;
      if (seen[x.serial]) continue;
      seen[x.serial] = true;
      toUpload.push(x);
    }
    if (!toUpload.length) { alert("No files could be matched to a serial 1-34. Name them like 1.jpg ... 34.jpg"); return; }
    let msg = `Upload ${toUpload.length} photo(s) and mark those serials as VERIFIED hits?`;
    if (unresolved.length) msg += `\n\nSkipped (no serial 1-34 in filename):\n` + unresolved.slice(0,8).join("\n") + (unresolved.length>8?`\n…+${unresolved.length-8} more`:"");
    if (!window.confirm(msg)) return;
    setBojax34Uploading({ done:0, total:toUpload.length });
    try {
      let done = 0;
      for (const { file, serial } of toUpload) {
        const sref = ref(storage, `bojax34_claims/${serial}_${Date.now()}.jpg`);
        const _cfile = await compressImage(file);
        await uploadBytes(sref, _cfile);
        const photoUrl = await getDownloadURL(sref);
        await setDoc(doc(db,"bojax34_claims",String(serial)), {
          serial: Number(serial),
          cardName: bjCard.hero || "BoJax",
          cardNum: "TBJ-B",
          cardImage: bjCard.imageUrl || null,
          photoUrl,
          submitterName: "Swan City",
          story: "",
          dateHit: "",
          userId: null,
          status: "verified",
          recordedByAdmin: admin,
          reviewedAt: now,
          source: "bojax34_bulk",
          createdAt: now,
        }, { merge: true });
        done++;
        setBojax34Uploading({ done, total:toUpload.length });
      }
      setBojax34Uploading(null);
      alert(`Done — ${toUpload.length} BoJax /34 photos uploaded and marked verified.`);
    } catch(e) {
      setBojax34Uploading(null);
      alert("Bulk upload failed: " + (e?.message||e) + "\n\nIf this mentions permissions, the Storage/Firestore rules for bojax34_claims need admin write access.");
    }
  }

  // -- BULK image import, right here on /cards (admin only). Writes to the SAME data /cards reads. --
  // ── MARKETPLACE + MESSAGES RESET (admin, pre-launch fresh start) ──
  // HARD ALLOWLIST: only these collections can ever be touched. Collection data
  // (boba_owned, boba_wants, users, profiles, decks) is NOT in this list and
  // is therefore impossible to delete here.
  const RESET_COLLECTIONS = ["marketplace","market_offers","market_sales","market_notifs","deal_threads","negotiation_history"];
  async function runMarketplaceReset() {
    setResetting("Starting…");
    let totalDeleted = 0;
    try {
      for (const collName of RESET_COLLECTIONS) {
        // safety: never allow a collection-data name through, even by mistake
        if (["boba_owned","boba_wants","users","boba_profiles","boba_decks","boba_playbooks","boba_checklist"].includes(collName)) continue;
        setResetting(`Clearing ${collName}…`);
        const snap = await getDocs(collection(db, collName));
        let batch = writeBatch(db); let n = 0;
        for (const d of snap.docs) {
          batch.delete(doc(db, collName, d.id)); n++; totalDeleted++;
          if (n % 400 === 0) { await batch.commit(); batch = writeBatch(db); }
        }
        if (n % 400 !== 0) await batch.commit();
      }
      setResetting(null);
      setResetModal(false);
      setResetConfirmText("");
      setToast(`✅ Marketplace & messages reset — ${totalDeleted} records cleared. Collections untouched.`);
    } catch(e) {
      setResetting(null);
      alert("Reset failed: " + (e?.message||e) + "\n\nNothing in collections was touched.");
    }
  }

  async function runBulkImageImport(files, setName, treatment, weapon) {
    const list = Array.from(files||[]);
    if (!list.length) return;
    const firstWord = s => (s||"").toLowerCase().trim().split(" ")[0];
    const norm = n => String(n||"").toLowerCase().replace(/[\s-]/g,"");
    // Available treatments for this set (to match a subfolder name to a real treatment)
    const setTreatments = [...new Set((setName?cards.filter(c=>c.setName===setName):cards).map(c=>c.treatment).filter(Boolean))];
    // Given a file, figure out its treatment: explicit treatment arg wins; otherwise read the
    // immediate subfolder it sits in (from webkitRelativePath) and match it to a real treatment.
    function treatmentForFile(file){
      if (treatment) return treatment;
      const rel = file.webkitRelativePath || file.relativePath || "";
      const parts = rel.split("/").filter(Boolean);
      if (parts.length < 2) return ""; // file sits directly in the picked folder — no treatment subfolder
      const folder = parts[parts.length-2]; // the subfolder the file is in
      const nf = norm(folder);
      // exact, then contains, match against this set's real treatments
      let t = setTreatments.find(tt=>norm(tt)===nf);
      if (!t) t = setTreatments.find(tt=>norm(tt).includes(nf)||nf.includes(norm(tt)));
      return t || "";
    }
    const alreadyImaged = new Set(cards.filter(c=>c.imageUrl&&String(c.imageUrl).startsWith("http")).map(c=>c.id));
    let matched=0, skipped=0, done=0, kept=0;   // kept = already had art, left alone
    window._bulkErrShown = false;
    const skippedNames=[];
    const diagLines=[];   // why each unmatched file failed
    setBulkProg({done:0,total:list.length,matched:0,skipped:0,status:"Starting…"});

    // Build the candidate pool for a specific treatment (used per-file)
    function poolFor(fileTreatment){
      let pool = setName ? cards.filter(c=>c.setName===setName) : cards;
      if (fileTreatment) pool = pool.filter(c=>(c.treatment||"")===fileTreatment);
      // Weapon is part of a card's identity here: Fire, Ice and Glow share a card number, a hero
      // and a treatment, so without this the matcher has three indistinguishable candidates and
      // will attach the art to whichever it happens to rank first.
      if (weapon) pool = pool.filter(c=>(c.weapon||"")===weapon);
      return pool;
    }

    async function visionFind(file, pool, fileTreatment){
      const tightPool = !!(setName && fileTreatment);
      try {
        const base64 = await new Promise((res,rej)=>{ const rd=new FileReader(); rd.onload=ev=>{ const img=new Image(); img.onload=()=>{ const MAX=1400; const sc=Math.min(1,MAX/Math.max(img.width,img.height)); const cv=document.createElement("canvas"); cv.width=Math.round(img.width*sc); cv.height=Math.round(img.height*sc); cv.getContext("2d").drawImage(img,0,0,cv.width,cv.height); res(cv.toDataURL("image/jpeg",0.9).split(",")[1]); }; img.onerror=rej; img.src=ev.target.result; }; rd.onerror=rej; rd.readAsDataURL(file); });
        const resp = await fetch("/api/scan-card",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({imageBase64:base64,mediaType:"image/jpeg",setName,heroOnly:tightPool})});
        if(!resp.ok) return null;
        const v = await resp.json();
        if(!v||(!v.cardNum&&!v.hero)) return null;
        const vNum=norm(v.cardNum), vHero=(v.hero||"").toLowerCase().trim(), vWeapon=(v.weapon||"").toLowerCase().trim(), vTreat=(v.treatment||"").toLowerCase().trim(), vPower=String(v.power||"");
        const heroOk=(c)=>{ const h=(c.hero||"").toLowerCase().trim(); return h===vHero || firstWord(h)===firstWord(vHero) || h.replace(/\s/g,"")===vHero.replace(/\s/g,""); };
        const wOk=(c)=>!vWeapon||(c.weapon||"").toLowerCase()===vWeapon;
        const tOk=(c)=>!vTreat||(c.treatment||"").toLowerCase()===vTreat||(c.treatment||"").toLowerCase().includes(vTreat)||vTreat.includes((c.treatment||"").toLowerCase());
        const pOk=(c)=>!vPower||String(c.power||"")===vPower;
        // ── SET + TREATMENT LOCKED: hero alone identifies the card. Just read the hero banner. ──
        if(tightPool && vHero){
          const m = pool.filter(heroOk);
          if(m.length===1) return m[0];
          // multiple cards share that hero in this set+treatment → use power/number to pick
          if(m.length>1){ const mp=m.filter(pOk); if(mp.length===1) return mp[0]; const mn=m.filter(c=>norm(c.cardNum)===vNum); if(mn.length===1) return mn[0]; return m[0]; }
          // hero didn't match anything in the pool → fall through to number-based below
        }
        // 1. card# + hero + weapon (strongest — distinguishes same-number cards like Joe Cool 275 vs Skyline S75)
        if(vNum&&vHero){ const m=pool.filter(c=>norm(c.cardNum)===vNum&&heroOk(c)); if(m.length===1) return m[0]; if(m.length>1){ const w=m.filter(wOk); if(w.length===1) return w[0]; const wt=w.filter(tOk); if(wt.length===1) return wt[0]; const wtp=wt.filter(pOk); if(wtp.length===1) return wtp[0]; if(wtp.length>1) return wtp[0]; } }
        // 2. card# + weapon + treatment (no clear hero, but element/finish narrows it)
        if(vNum){ let m=pool.filter(c=>norm(c.cardNum)===vNum); if(m.length===1) return m[0]; if(m.length>1){ const w=m.filter(wOk); if(w.length===1) return w[0]; const wt=(w.length?w:m).filter(tOk); if(wt.length===1) return wt[0]; const wtp=(wt.length?wt:w.length?w:m).filter(pOk); if(wtp.length===1) return wtp[0]; if(vHero){ const h=(wtp.length?wtp:m).filter(heroOk); if(h.length===1) return h[0]; } return null; } }
        // 3. hero + weapon + power (number unreadable)
        if(vHero&&vWeapon&&vPower){ const m=pool.filter(c=>heroOk(c)&&wOk(c)&&pOk(c)); if(m.length===1) return m[0]; }
        return null;
      } catch(e){ return null; }
    }

    // Match a card by pulling its card-number out of the filename, scoped to the given pool.
    function filenameFind(fname, pool){
      const base = String(fname||"").replace(/\.[a-z0-9]+$/i,"").toLowerCase(); // strip extension
      // Page/export-order filenames (e.g. "Steel-Paper_page-0001", "scan_0002") carry NO real
      // card number — only an export sequence. Matching their digits to a cardNum is wrong, so
      // skip filename matching entirely and let vision read the hero off the card.
      if (/(?:^|[_\s-])(?:page|scan|img|image|export|slide|sheet)?[_\s-]*0*\d{1,4}$/i.test(base)
          && /(page|scan|img|image|export|slide|sheet)/i.test(base)) {
        return null;
      }
      const flat = base.replace(/[\s_-]/g,""); // "2026griffeybbf1"
      // For each card in the pool, see if its cardNum appears in the filename.
      // Prefer the LONGEST cardNum match so "bbf1" wins over "1".
      let best=null, bestLen=0;
      for(const c of pool){
        const cn = String(c.cardNum||"").toLowerCase().replace(/[\s-]/g,""); // "bbf-1" -> "bbf1"
        if(!cn) continue;
        // find cn in the flattened filename, bounded so "bbf1" doesn't match inside "bbf10"
        const idx = flat.indexOf(cn);
        if(idx === -1) continue;
        const after = flat[idx+cn.length]; // char right after the match
        // if the cardNum ends in a digit, the next char must NOT be a digit (avoids bbf1 ⊂ bbf10)
        const lastIsDigit = /[0-9]/.test(cn[cn.length-1]);
        if(lastIsDigit && /[0-9]/.test(after||"")) continue;
        if(cn.length > bestLen){ best=c; bestLen=cn.length; }
      }
      return best;
    }

    // Fall back to the HERO NAME in the filename. Card numbers in the checklist are not always
    // right, and when they are wrong a number-only match silently skips the file \u2014 which is why
    // whole folders of images import as "no match". Once the set (and usually the treatment) is
    // pinned by the picker, the hero name on its own is enough to identify a card.
    //
    // Only accepts an UNAMBIGUOUS hit: if two cards in the pool could match the filename, it
    // returns nothing rather than guessing and attaching art to the wrong card.
    // Match on the HERO NAME in the filename, using treatment and weapon to break ties.
    //
    // The hero alone is often NOT unique: Skyline exists as Fire, Ice and Glow on the same card
    // number with the same hero, so "does this filename contain a hero name" matches three cards
    // at once. Rejecting every tie meant a whole folder matched nothing. Instead, when several
    // cards share the hero, the filename's other words (weapon, treatment) pick between them, and
    // only a tie that survives THAT is refused.
    function heroFind(filename, pool){
      const flat = filename.replace(/\.[^.]+$/,"").toLowerCase().replace(/[^a-z0-9]/g,"");
      if(!flat) return null;
      const norm = s => String(s||"").toLowerCase().replace(/[^a-z0-9]/g,"");

      let hits = [];
      for(const c of pool){
        const h = norm(c.hero || c.playName);
        if(h.length < 3) continue;              // too short to be a safe signal
        if(flat.includes(h)) hits.push({ card:c, len:h.length });
      }
      if(!hits.length) return null;

      // Keep only the longest hero match ("bojackson" beats a card merely named "bo").
      const maxLen = Math.max(...hits.map(x=>x.len));
      hits = hits.filter(x=>x.len === maxLen);
      if(hits.length === 1) return hits[0].card;

      // Several cards share that hero. Score each by how much of the REST of its identity also
      // appears in the filename \u2014 weapon first, since that is what distinguishes Skyline Fire from
      // Skyline Ice, then treatment, then card number.
      const scored = hits.map(({card}) => {
        let score = 0;
        const w = norm(card.weapon);
        const t = norm(card.treatment);
        const n = norm(card.cardNum);
        if(w && w.length >= 3 && flat.includes(w)) score += 3;
        if(t && t.length >= 3 && flat.includes(t)) score += 2;
        if(n && n.length >= 2 && flat.includes(n)) score += 1;
        return { card, score };
      }).sort((a,b)=>b.score-a.score);

      // Still tied, or nothing in the filename distinguishes them -> refuse rather than guess.
      if(scored[0].score === 0) return null;
      if(scored.length > 1 && scored[1].score === scored[0].score) return null;
      return scored[0].card;
    }

    async function one(file){
      try {
        const fileTreatment = treatmentForFile(file);
        const pool = poolFor(fileTreatment);
        // When a folder comes back "0 matched" the summary cannot say WHY \u2014 wrong set, empty pool,
        // or names the matchers do not recognise. Record the decision inputs per file so the
        // downloaded report explains it without needing developer tools.
        const diag = [];
        diag.push(`FILE: ${file.name}`);
        diag.push(`  set picked : ${setName || "(all sets)"}`);
        diag.push(`  treatment  : ${fileTreatment || "(none \u2014 not filtered)"}`);
        diag.push(`  candidates : ${pool.length}`);
        if(pool.length === 0){
          diag.push("  >> NOTHING TO MATCH AGAINST. The set or treatment filter excluded every card.");
        } else {
          diag.push("  examples   : " + pool.slice(0,3).map(c=>`${c.hero} / ${c.treatment} / ${c.weapon||"-"} / #${c.cardNum}`).join("  |  "));
        }
        // Try filename FIRST — it usually contains the exact card number (e.g. "2026-Griffey-BBF-1.jpg" → BBF-1).
        let card = filenameFind(file.name, pool);
        if(card) diag.push(`  MATCHED by card number -> ${card.hero} #${card.cardNum}`);
        if(!card && bulkImgHeroMatch){
          card = heroFind(file.name, pool);
          if(card) diag.push(`  MATCHED by hero name -> ${card.hero} ${card.weapon||""} #${card.cardNum}`);
          else diag.push("  hero name: no match");
        }
        if(!card){
          card = await visionFind(file, pool, fileTreatment);
          if(card) diag.push(`  MATCHED by photo scan -> ${card.hero} #${card.cardNum}`);
          else diag.push("  photo scan: no match");
        }
        if(!card) diagLines.push(diag.join("\n"));
        if(!card){ skipped++; skippedNames.push(file.name); done++; setBulkProg({done,total:list.length,matched,skipped,status:`No match: ${file.name}`}); return; }
        // Counting these as "matched" made the summary claim success while nothing was written —
        // the report said it worked and the images never changed. Tracked separately now.
        if(!bulkImgOverwrite && alreadyImaged.has(card.id)){ kept++; done++; setBulkProg({done,total:list.length,matched,skipped,kept,status:`⏭ Already had image: ${card.hero}`}); return; }
        const fsId = card.fsId || card.id;
        const ext = (file.name.split(".").pop()||"png").toLowerCase();
        const storageRef2 = ref(storage, `boba_cards/${fsId}.${ext}`);
        const _cfile = await compressImage(file);
        await uploadBytes(storageRef2, _cfile);
        const url = await getDownloadURL(storageRef2);
        await setDoc(doc(db,"boba_checklist",fsId), { imageUrl:url }, { merge:true });
        matched++; done++;
        setBulkProg({done,total:list.length,matched,skipped,status:`✅ ${card.hero}${card.weapon?` · ${card.weapon}`:""} #${card.cardNum} (${matched} done)`});
      } catch(e){ skipped++; done++; const msg=(e&&e.code)?e.code:(e&&e.message)?e.message:String(e); skippedNames.push(file.name+" (err: "+msg+")"); setBulkProg({done,total:list.length,matched,skipped,status:`⚠️ ${msg.includes("permission")?"PERMISSION DENIED — check Firestore rules / login":"Error"}: ${file.name}`}); if(!window._bulkErrShown && msg.toLowerCase().includes("permission")){ window._bulkErrShown=true; alert("Write blocked by Firestore rules.\n\nThe import can't save images because the database is rejecting the write (permission-denied).\n\nThis usually means you're not logged in as an @bazookabreaks.com admin in THIS browser, or the security rules need updating. Error: "+msg); } }
    }

    const PAR=4;
    for(let i=0;i<list.length;i+=PAR){ await Promise.all(list.slice(i,i+PAR).map(one)); }

    // Regenerate the snapshot + bump version so EVERY viewer sees it, and reload THIS page's cards now.
    // Work out the single most useful sentence about why nothing matched, so the on-screen panel
    // can explain it without the person opening the report.
    let why = null;
    if (matched === 0 && list.length > 0) {
      const poolNow = poolFor(treatment || "");
      if (poolNow.length === 0) {
        why = setName
          ? `No cards found in "${setName}"` + (treatment ? ` with treatment "${treatment}".` : ".") +
            `\n\nCheck the Set (and Treatment) picker \u2014 the names have to match the checklist exactly.`
          : "No cards matched the filters, so there was nothing to attach images to.";
      } else {
        why = `Found ${poolNow.length} candidate card${poolNow.length===1?"":"s"}, but none of the filenames ` +
              `matched a card number or hero name.\n\nA report has downloaded showing what each file was compared against.`;
      }
    }
    setBulkProg({done:list.length,total:list.length,matched,skipped,why,status:"Saving & refreshing…"});

    try {
      const snap2 = await getDocs(collection(db,"boba_checklist"));
      const all = snap2.docs.map(d=>({...d.data(), id:d.id}));
      const blob = new Blob([JSON.stringify(all)],{type:"application/json"});
      await uploadBytes(ref(storage,"card_data/boba_checklist.json"), blob, {contentType:"application/json",cacheControl:"public,max-age=300"});
      try { await writeCardSnapshot(all, 300); } catch(e) {}
      try { await setDoc(doc(db,"meta","cards_version"),{ts:Date.now()}); } catch(e){}
      try { idbSetCards(all, Date.now()); } catch(e){}
      setCards(all); // <-- update THIS page immediately
    } catch(e){ console.warn("snapshot/refresh failed",e); }

    if(skippedNames.length){ try{ const b=new Blob([`Unmatched (${skippedNames.length}):\n\n`+skippedNames.join("\n")+"\n\n\n=== WHY EACH FILE DID NOT MATCH ===\n\n"+diagLines.join("\n\n")],{type:"text/plain"}); const u=URL.createObjectURL(b); const a=document.createElement("a"); a.href=u; a.download=`unmatched-${new Date().toISOString().slice(0,10)}.txt`; a.click(); URL.revokeObjectURL(u);}catch(e){} }
    // Keep `why` on the final state, or the explanation is wiped the moment the run ends.
    setBulkProg({done:list.length,total:list.length,matched,skipped,why,
      status: matched>0 ? `✅ Done — ${matched} imported, ${skipped} skipped`
                        : `⚠️ Nothing matched — ${skipped} file${skipped===1?"":"s"} skipped`});
    setToast(matched>0 ? `✅ Imported ${matched} images${skipped?`, ${skipped} skipped`:""}`
                       : `⚠️ No images matched — see the panel for why`);
    // When it worked, get out of the way. When it did not, stay open so the reason can be read.
    if (matched > 0) setTimeout(()=>setBulkProg(null), 6000);
  }
  // -- Reviews (buyer rates seller, tied to a completed sale) --
  const reviewedSaleIds = new Set(myReviews.map(r=>r.saleId));
  async function submitReview(sale, stars, text) {
    if (!user || !sale) return;
    const revId = `${sale.id}_${user.uid}`; // one review per sale per buyer
    const review = {
      id: revId, saleId: sale.id,
      sellerUid: sale.sellerUid, sellerName: sale.sellerName||"",
      buyerUid: user.uid, buyerName: user.displayName||user.email||"",
      cardName: sale.cardName||"", stars: Math.max(1,Math.min(5,stars)),
      text: (text||"").trim().slice(0,600),
      createdAt: new Date().toISOString(),
    };
    try {
      await setDoc(doc(db,"boba_reviews",revId), review);
      setMyReviews(prev => [...prev.filter(r=>r.id!==revId), review]);
      setReviewModal(null);
      showToast("Thanks for your review!");
    } catch(e) { console.error("submit review failed:", e); alert("Could not submit review — please try again."); }
  }
  // Completed sales where THIS user was the buyer and hasn't reviewed yet
  const pendingReviews = marketSales.filter(s => s.buyerUid===user?.uid && s.sellerUid && s.sellerUid!==user?.uid && !reviewedSaleIds.has(s.id));
  function signInGoogle() {
    const provider = new GoogleAuthProvider();
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      signInWithRedirect(auth, provider).catch(console.error);
    } else {
      signInWithPopup(auth, provider)
        .then(()=>setSigningIn(false))
        .catch(e=>{
          if(e.code==="auth/popup-blocked"||e.code==="auth/popup-closed-by-user") {
            signInWithRedirect(auth, provider).catch(console.error);
          } else { console.error(e); }
        });
    }
  }

  // Handle redirect result on page load
  useEffect(() => {
    getRedirectResult(auth).then(result => {
      if (result?.user) setSigningIn(false);
    }).catch(console.error);
  }, []);

  // --- Email/password auth ---
  const [authMode, setAuthMode] = useState("signin"); // signin | signup | reset
  const [authEmail, setAuthEmail] = useState("");
  const [authPass, setAuthPass] = useState("");
  const [authName, setAuthName] = useState("");
  const [authErr, setAuthErr] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [authMsg, setAuthMsg] = useState("");
  function authErrMsg(code){
    const m = {
      "auth/invalid-email":"That doesn't look like a valid email.",
      "auth/user-not-found":"No account with that email. Try signing up instead.",
      "auth/wrong-password":"Incorrect password. If you signed up with Google, use the Google button above instead — or reset your password.",
      "auth/invalid-credential":"Email or password is incorrect. If you originally signed up with Google, use the Google button above instead.",
      "auth/email-already-in-use":"An account already exists with that email. Try signing in.",
      "auth/weak-password":"Password should be at least 6 characters.",
      "auth/too-many-requests":"Too many attempts. Wait a moment and try again.",
      "auth/operation-not-allowed":"Email sign-in isn't enabled yet. Contact support.",
    };
    return m[code] || "Something went wrong. Please try again.";
  }
  async function handleEmailAuth() {
    setAuthErr(""); setAuthMsg("");
    const email = authEmail.trim().toLowerCase();
    if (!email) { setAuthErr("Enter your email."); return; }
    if (authMode === "reset") {
      setAuthBusy(true);
      try { await sendPasswordResetEmail(auth, email); setAuthMsg("If this email has a password, a reset link is on its way — check your inbox (and spam). Signed up with Google? No email will come — just use the Google button instead."); }
      catch(e){ setAuthErr(authErrMsg(e.code)); }
      finally { setAuthBusy(false); }
      return;
    }
    if (!authPass || authPass.length < 6) { setAuthErr("Password must be at least 6 characters."); return; }
    setAuthBusy(true);
    try {
      if (authMode === "signup") {
        const cred = await createUserWithEmailAndPassword(auth, email, authPass);
        if (authName.trim()) { try { await updateProfile(cred.user, { displayName: authName.trim() }); } catch(e){} }
      } else {
        await signInWithEmailAndPassword(auth, email, authPass);
      }
      setSigningIn(false); setAuthEmail(""); setAuthPass(""); setAuthName("");
    } catch(e){ setAuthErr(authErrMsg(e.code)); }
    finally { setAuthBusy(false); }
  }
  // --- Passwordless magic-link ---
  async function sendMagicLink() {
    setAuthErr(""); setAuthMsg("");
    const email = authEmail.trim().toLowerCase();
    if (!email) { setAuthErr("Enter your email to get a sign-in link."); return; }
    setAuthBusy(true);
    try {
      await sendSignInLinkToEmail(auth, email, { url: window.location.origin + window.location.pathname, handleCodeInApp: true });
      try { window.localStorage.setItem("bz_emailForSignIn", email); } catch(e){}
      setAuthMsg("Check your email — we sent you a sign-in link!");
    } catch(e){ setAuthErr(authErrMsg(e.code)); }
    finally { setAuthBusy(false); }
  }
  // Complete magic-link sign-in when the user returns via the emailed link
  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    let email = "";
    try { email = window.localStorage.getItem("bz_emailForSignIn") || ""; } catch(e){}
    if (!email) { email = window.prompt("Confirm your email to finish signing in:") || ""; }
    if (!email) return;
    signInWithEmailLink(auth, email.trim().toLowerCase(), window.location.href)
      .then(() => {
        try { window.localStorage.removeItem("bz_emailForSignIn"); } catch(e){}
        setSigningIn(false);
        // clean the magic-link params out of the URL
        try { window.history.replaceState({}, document.title, window.location.pathname); } catch(e){}
      })
      .catch(e => { console.error("magic link sign-in failed:", e); });
  }, []);

  // -- Scan --
  // Pure single-card matcher extracted from the scan flow: Vision result -> {match, candidates}.
  // Used by both the single scan and the multi-card binder scan. No UI side-effects.
  function matchOne(data) {
    if(!data || (!data.cardNum && !data.hero)) return { match:null, candidates:[] };
      const normNum=n=>String(n||"").replace(/[\s\-_.#]/g,"").toLowerCase();
      const clean=s=>String(s||"").toLowerCase().replace(/[^a-z0-9]/g,"");
      // Levenshtein distance for fuzzy text comparison
      const lev=(a,b)=>{a=clean(a);b=clean(b);if(!a||!b)return Math.max(a.length,b.length);const m=[];for(let i=0;i<=b.length;i++)m[i]=[i];for(let j=0;j<=a.length;j++)m[0][j]=j;for(let i=1;i<=b.length;i++)for(let j=1;j<=a.length;j++)m[i][j]=b[i-1]===a[j-1]?m[i-1][j-1]:Math.min(m[i-1][j-1]+1,m[i][j-1]+1,m[i-1][j]+1);return m[b.length][a.length];};
      // Fuzzy hero match: exact, contains, first-name, or close edit distance
      const heroMatch=(cardHero,id)=>{
        if(!cardHero||!id)return false;
        const a=clean(cardHero),b=clean(id);
        if(!a||!b)return false;
        if(a===b)return true;
        if(a.includes(b)||b.includes(a))return true;
        const aF=cardHero.toLowerCase().split(/\s+/)[0],bF=id.toLowerCase().split(/\s+/)[0];
        if(clean(aF)===clean(bF)&&aF.length>2)return true;
        // tolerate OCR typos: allow ~20% character errors
        const dist=lev(a,b),tol=Math.max(1,Math.floor(Math.max(a.length,b.length)*0.22));
        return dist<=tol;
      };

      const iNum=normNum(data.cardNum),iHero=(data.hero||"").toLowerCase(),iWeap=(data.weapon||"").toLowerCase(),iTreat=(data.treatment||"").toLowerCase();
      const iPow=String(data.power||"").replace(/[^0-9]/g,"");
      let match=null;

      // SPOKEN HINT. OCR reads the hero and card number reliably; what it loses is the small print —
      // treatment and weapon — under glare or at an angle, which is exactly what leaves several
      // variants of one hero tied. So if the user said something while scanning ("moose 80's rad"),
      // use it to shrink the pool BEFORE matching. It applies at every stage below rather than as a
      // late tie-break, and if the hint eliminates everything we ignore it rather than fail the scan.
      const hintPool = (() => {
        const raw = String(scanHintRef.current||"").toLowerCase().replace(/[^a-z0-9\s]/g," ").replace(/\s+/g," ").trim();
        if (!raw) return null;
        const squash = s => String(s||"").toLowerCase().replace(/[^a-z0-9]/g,"");
        // Match WORD BY WORD, not as one squashed blob. The old version glued the phrase into
        // "moose80srad" and asked whether it contained the treatment — it never did, while the hero
        // name alone let every Moose card through. So "moose 80s rad" kept Moose Red Battlefoil and
        // the treatment did no narrowing at all.
        const STOP = new Set(["the","a","an","and","of","card","battlefoil","foil"]);
        const words = raw.split(" ").filter(w => w.length>1 && !STOP.has(w));
        if (!words.length) return null;
        // "80s" should match "80's", "1980s" etc. Compare on digits+letters with a loose contains.
        const wordHitsField = (w, field) => {
          const f = squash(field); if (!f) return false;
          const s = squash(w);     if (!s) return false;
          return f.includes(s) || s.includes(f);
        };
        // Score each card: how many spoken words it can account for, and where. Treatment and weapon
        // matches are what we actually need (OCR loses those), so weight them above the hero.
        const scored = cards.map(card => {
          let score = 0, treatHits = 0;
          for (const w of words) {
            if (wordHitsField(w, card.treatment)) { score += 3; treatHits++; }
            else if (wordHitsField(w, card.weapon)) score += 2;
            else if (wordHitsField(w, card.setName)) score += 2;
            else if (wordHitsField(w, card.hero)) score += 1;
          }
          return { card, score, treatHits };
        }).filter(x => x.score > 0);
        if (!scored.length) return null;
        // Keep only the best-scoring cards. If anything matched on treatment, drop everything that
        // didn't — that is the whole point of saying the treatment out loud.
        const anyTreat = scored.some(x => x.treatHits > 0);
        const pool = anyTreat ? scored.filter(x => x.treatHits > 0) : scored;
        const top = Math.max(...pool.map(x => x.score));
        const best = pool.filter(x => x.score === top).map(x => x.card);
        return best.length ? best : null;
      })();
      const scanPool = hintPool || cards;
      // Consume the hint: it described THIS card, not the next one.
      const spokenHint = scanHintRef.current;
      scanHintRef.current = ""; setScanHint("");
      // If what you said pinned it to ONE card and the photo agrees on the hero, take it. Otherwise a
      // bad treatment read can still drag the match onto the wrong variant even from a narrowed pool.
      if (hintPool && hintPool.length === 1) {
        const only = hintPool[0];
        if (!iHero || heroMatch(only.hero, iHero)) {
          setPhotoScan({ status:"matched", card:only, detected:{...data, viaHint:spokenHint}, scanPhoto:display });
          setScanQty(1);
          scanInFlight.current = false;
          return;
        }
      }

      // HERO-FIRST: hero name is the most reliable read. Find all cards for this hero, then pin the variant.
      const heroCards = iHero ? scanPool.filter(c=>heroMatch(c.hero,iHero)) : [];

      if(heroCards.length){
        // 1. Hero + exact card number. If several cards share this number (same
        //    set number across weapons/treatments), disambiguate by treatment
        //    (strong signal) then weapon — only auto-pick a confident winner.
        if(iNum){
          const numCards=heroCards.filter(c=>normNum(c.cardNum)===iNum);
          if(numCards.length===1){ match=numCards[0]; }
          else if(numCards.length>1){
            // Try each strong signal in order; take it if it resolves to exactly one card.
            const byTreat = iTreat ? numCards.filter(c=>c.treatment?.toLowerCase()===iTreat) : [];
            const byWeap  = iWeap  ? numCards.filter(c=>canonWeapon(c.weapon)===canonWeapon(iWeap)) : [];
            const byPow   = iPow   ? numCards.filter(c=>String(c.power||"").replace(/[^0-9]/g,"")===iPow) : [];
            // Weapon/power read more reliably than the tiny treatment text, so trust them first.
            if(byWeap.length===1) match=byWeap[0];
            else if(byPow.length===1) match=byPow[0];
            else if(byTreat.length===1) {
              // Treatment is the flakiest read on a page scan. Auto-accept only if nothing
              // contradicts it (no weapon read, or the weapon agrees). Otherwise leave the
              // variants as candidates so the user confirms rather than taking the wrong foil.
              const tCard = byTreat[0];
              if(!iWeap || canonWeapon(tCard.weapon)===canonWeapon(iWeap)) match=tCard;
            }
            else {
              const wt = numCards.filter(c=>(!iWeap||canonWeapon(c.weapon)===canonWeapon(iWeap))&&(!iTreat||c.treatment?.toLowerCase()===iTreat));
              if(wt.length===1) match=wt[0];
            }
          }
        }
        // 2. Hero + fuzzy card number (off by 1 char — handles a misread digit)
        if(!match&&iNum){const cands=heroCards.filter(c=>lev(normNum(c.cardNum),iNum)<=1);if(cands.length===1)match=cands[0];}
        // 3. Hero + treatment + weapon all agree (no number needed)
        if(!match&&iTreat&&iWeap) match=heroCards.find(c=>c.treatment?.toLowerCase()===iTreat&&c.weapon?.toLowerCase()===iWeap);
        // 4. Hero + treatment + power agree
        if(!match&&iTreat&&iPow){const cands=heroCards.filter(c=>c.treatment?.toLowerCase()===iTreat&&String(c.power||"").replace(/[^0-9]/g,"")===iPow);if(cands.length===1)match=cands[0];}
        // 5. Hero + treatment, single candidate
        if(!match&&iTreat){const cands=heroCards.filter(c=>c.treatment?.toLowerCase()===iTreat);if(cands.length===1)match=cands[0];}
        // 6. Hero + weapon + power, single candidate
        if(!match&&iWeap&&iPow){const cands=heroCards.filter(c=>c.weapon?.toLowerCase()===iWeap&&String(c.power||"").replace(/[^0-9]/g,"")===iPow);if(cands.length===1)match=cands[0];}
        // 7. Hero + weapon, single candidate
        if(!match&&iWeap){const cands=heroCards.filter(c=>c.weapon?.toLowerCase()===iWeap);if(cands.length===1)match=cands[0];}
        // Weapon->power correlation: when hero+weapon yields several candidates (card number unreadable),
        // the weapon usually pins a power level. Narrow by power, then treatment, to resolve to one card.
        if(!match&&iWeap){
          const wCands=heroCards.filter(c=>c.weapon?.toLowerCase()===iWeap);
          if(wCands.length>1){
            let narrow = iPow ? wCands.filter(c=>String(c.power||"").replace(/[^0-9]/g,"")===iPow) : wCands;
            if(narrow.length===0) narrow = wCands;
            if(iTreat){ const t=narrow.filter(c=>c.treatment?.toLowerCase()===iTreat); if(t.length) narrow=t; }
            if(narrow.length===1) match=narrow[0];
            else if(narrow.length>1){
              const sig=new Set(narrow.map(c=>`${(c.treatment||"").toLowerCase()}|${c.power}`));
              if(sig.size===1) match=narrow[0];
            }
          }
        }
        // 8. Hero has only one card total
        if(!match&&heroCards.length===1) match=heroCards[0];
      }

      // Fallback: if hero wasn't read at all, exact number + (if present) hero agreement — never number-alone across heroes
      if(!match&&!iHero&&iNum){const cands=scanPool.filter(c=>normNum(c.cardNum)===iNum);if(cands.length===1)match=cands[0];}

      if(match){ return { match, candidates: [] }; }   // pure: no UI side-effects

      // No single confident match -- suggest candidates. Prefer this hero's cards (weapon/treatment/power ranking).
      let pool = heroCards.length ? heroCards : cards;
      const scored=pool.map(c=>{
        let s=0;
        if(iHero&&heroMatch(c.hero,iHero))s+=60;          // hero is king
        if(iNum&&normNum(c.cardNum)===iNum)s+=50;
        else if(iNum&&lev(normNum(c.cardNum),iNum)<=1)s+=20;
        if(iTreat&&c.treatment?.toLowerCase()===iTreat)s+=35; // treatment strongly narrows
        if(iWeap&&c.weapon?.toLowerCase()===iWeap)s+=30;      // weapon narrows the rainbow
        if(iPow&&String(c.power||"").replace(/[^0-9]/g,"")===iPow)s+=15;
        return {card:c,score:s};
      }).filter(x=>x.score>0).sort((a,b)=>b.score-a.score);

      const candidates=scored.slice(0,8).map(x=>x.card);
    return { match, candidates };
  }

  // ── VOICE MATCHER ──────────────────────────────────────────────────────────────────────────
  // Take a spoken phrase like "alpha update bojax green battlefoil" and rank cards by how well
  // the card's own words (set, hero, weapon, treatment) appear in what was said. Speech mangles
  // unusual hero names ("bojax" -> "bo jacks"), so matching is word-overlap + fuzzy, not exact.
  function voiceMatchCards(transcript) {
    const said = String(transcript||"").toLowerCase().replace(/[^a-z0-9\s]/g," ").replace(/\s+/g," ").trim();
    if (!said) return [];
    const saidWords = said.split(" ").filter(w => w.length > 1);
    const saidSet = new Set(saidWords);
    const saidJoined = saidWords.join("");

    // Levenshtein for fuzzy single-word compare (handles a misheard syllable).
    const lev = (a,b) => {
      if (!a||!b) return Math.max((a||"").length,(b||"").length);
      const m=[]; for(let i=0;i<=b.length;i++) m[i]=[i];
      for(let j=0;j<=a.length;j++) m[0][j]=j;
      for(let i=1;i<=b.length;i++) for(let j=1;j<=a.length;j++)
        m[i][j] = b[i-1]===a[j-1] ? m[i-1][j-1] : Math.min(m[i-1][j-1],m[i][j-1],m[i-1][j])+1;
      return m[b.length][a.length];
    };
    // Does any spoken word (fuzzily) match this target word?
    const wordHit = (target) => {
      const t = String(target||"").toLowerCase().replace(/[^a-z0-9]/g,"");
      if (!t || t.length < 2) return false;
      if (saidSet.has(t)) return true;
      if (saidJoined.includes(t) && t.length >= 4) return true;   // ran-together speech
      const tol = Math.max(1, Math.floor(t.length * 0.25));
      return saidWords.some(w => { const ww=w.replace(/[^a-z0-9]/g,""); return ww.length>2 && lev(ww,t) <= tol; });
    };
    // Score every phrase field: fraction of its words that were heard, weighted.
    const fieldScore = (val, weight) => {
      const words = String(val||"").toLowerCase().replace(/[^a-z0-9\s]/g," ").split(/\s+/).filter(w=>w.length>1);
      if (!words.length) return 0;
      const hits = words.filter(wordHit).length;
      return (hits / words.length) * weight;
    };

    // Respect the voice filter, if one is set. Empty entries mean "no restriction".
    const pool = cards.filter(c =>
      (!voiceFilter.set       || c.setName === voiceFilter.set) &&
      (!voiceFilter.treatment || c.treatment === voiceFilter.treatment) &&
      (!voiceFilter.weapon    || canonWeapon(c.weapon) === voiceFilter.weapon)
    );
    const scored = pool.map(c => {
      let s = 0;
      s += fieldScore(c.hero,      60);   // hero is the anchor
      s += fieldScore(c.weapon,    28);
      s += fieldScore(c.treatment, 26);
      s += fieldScore(c.setName,   22);
      // Spoken bare number ("number forty seven" is rare; digits more likely) — light bonus.
      if (c.cardNum && saidSet.has(String(c.cardNum).toLowerCase())) s += 20;
      return { card:c, score:s };
    }).filter(x => x.score > 20)          // require at least a solid hero-ish hit
      .sort((a,b) => b.score - a.score);

    // Dedup by card id, keep the top handful.
    const seen = new Set();
    return scored.filter(x => { if(seen.has(x.card.id)) return false; seen.add(x.card.id); return true; })
                 .slice(0, 6).map(x => x.card);
  }


  // Listen continuously while scanning. Everything heard becomes a hint for the NEXT photo, so the
  // flow is "say what it is, then shoot" \u2014 no button juggling between the two.
  function toggleScanHint() {
    const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
    if (!SR) { setScanHint("Voice isn't supported in this browser"); return; }
    if (scanHintOn) {
      try { scanHintRecRef.current && scanHintRecRef.current.stop(); } catch(_){}
      scanHintRecRef.current = null; setScanHintOn(false);
      return;
    }
    try {
      const rec = new SR();
      rec.lang = "en-US"; rec.continuous = true; rec.interimResults = true; rec.maxAlternatives = 1;
      rec.onresult = (ev) => {
        let txt = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) txt += ev.results[i][0].transcript + " ";
        txt = txt.trim();
        if (txt) { scanHintRef.current = txt; setScanHint(txt); }
      };
      rec.onerror = (ev) => {
        if (ev.error === "not-allowed") { setScanHint("Mic blocked \u2014 allow it for this site"); setScanHintOn(false); }
      };
      // Chrome stops continuous recognition after a silence; restart while the toggle is still on.
      rec.onend = () => { if (scanHintRecRef.current === rec) { try { rec.start(); } catch(_){} } };
      scanHintRecRef.current = rec; setScanHintOn(true);
      rec.start();
    } catch(e) { setScanHint("Couldn't start the mic"); setScanHintOn(false); }
  }

  async function scanCardPhoto(file) {
    if (scanInFlight.current) return; // prevent overlapping scans when scanning back-to-back
    scanInFlight.current = true;
    setPhotoScan({status:"scanning"});
    try {
      // Build full-card image + a zoomed bottom-left corner crop (where the tiny card number lives)
      const { full, corner, display } = await new Promise((res,rej) => {
        const img=new Image(), url=URL.createObjectURL(file);
        img.onload=()=>{
          URL.revokeObjectURL(url);
          const MAX=1600, scale=Math.min(1,MAX/Math.max(img.width,img.height));
          const fw=Math.round(img.width*scale), fh=Math.round(img.height*scale);
          const fc=document.createElement("canvas"); fc.width=fw; fc.height=fh;
          fc.getContext("2d").drawImage(img,0,0,fw,fh);
          const fullB64=fc.toDataURL("image/jpeg",0.9).split(",")[1];
          // Bottom-left corner: left ~40% width, bottom ~22% height, upscaled ~2.2x for legibility
          const cropW=Math.round(img.width*0.42), cropH=Math.round(img.height*0.22);
          const sx=0, sy=img.height-cropH;
          const ZOOM=2.2; const cw=Math.round(cropW*ZOOM), ch=Math.round(cropH*ZOOM);
          const cc=document.createElement("canvas"); cc.width=cw; cc.height=ch;
          const ctx=cc.getContext("2d"); ctx.imageSmoothingEnabled=true; ctx.imageSmoothingQuality="high";
          ctx.drawImage(img, sx,sy,cropW,cropH, 0,0,cw,ch);
          const cornerB64=cc.toDataURL("image/jpeg",0.92).split(",")[1];
          // Compressed display copy (for saving to collection / marketplace) — max 700px, modest quality
          const DMAX=700, dscale=Math.min(1,DMAX/Math.max(img.width,img.height));
          const dw=Math.round(img.width*dscale), dh=Math.round(img.height*dscale);
          const dc=document.createElement("canvas"); dc.width=dw; dc.height=dh;
          dc.getContext("2d").drawImage(img,0,0,dw,dh);
          const displayDataUrl=dc.toDataURL("image/jpeg",0.8);
          res({ full:fullB64, corner:cornerB64, display:displayDataUrl });
        };
        img.onerror=rej; img.src=url;
      });
      const resp=await fetch("/api/scan-card",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({imageBase64:full,cornerBase64:corner,mediaType:"image/jpeg"})});
      if(!resp.ok){const e=await resp.json().catch(()=>({}));setPhotoScan({status:"error",message:e.error||`HTTP ${resp.status}`});return;}
      const data=await resp.json();
      if(!data.cardNum&&!data.hero){ if(scanLoanMode){ setLoanScanCard({card:null,mode:scanLoanMode}); setPhotoScan(null); setScanModal(false); return; } setPhotoScan({status:"nomatch"});setTimeout(()=>setPhotoScan(null),3000);return;}

      const normNum=n=>String(n||"").replace(/[\s\-_.#]/g,"").toLowerCase();
      const clean=s=>String(s||"").toLowerCase().replace(/[^a-z0-9]/g,"");
      // Levenshtein distance for fuzzy text comparison
      const lev=(a,b)=>{a=clean(a);b=clean(b);if(!a||!b)return Math.max(a.length,b.length);const m=[];for(let i=0;i<=b.length;i++)m[i]=[i];for(let j=0;j<=a.length;j++)m[0][j]=j;for(let i=1;i<=b.length;i++)for(let j=1;j<=a.length;j++)m[i][j]=b[i-1]===a[j-1]?m[i-1][j-1]:Math.min(m[i-1][j-1]+1,m[i][j-1]+1,m[i-1][j]+1);return m[b.length][a.length];};
      // Fuzzy hero match: exact, contains, first-name, or close edit distance
      const heroMatch=(cardHero,id)=>{
        if(!cardHero||!id)return false;
        const a=clean(cardHero),b=clean(id);
        if(!a||!b)return false;
        if(a===b)return true;
        if(a.includes(b)||b.includes(a))return true;
        const aF=cardHero.toLowerCase().split(/\s+/)[0],bF=id.toLowerCase().split(/\s+/)[0];
        if(clean(aF)===clean(bF)&&aF.length>2)return true;
        // tolerate OCR typos: allow ~20% character errors
        const dist=lev(a,b),tol=Math.max(1,Math.floor(Math.max(a.length,b.length)*0.22));
        return dist<=tol;
      };

      const iNum=normNum(data.cardNum),iHero=(data.hero||"").toLowerCase(),iWeap=(data.weapon||"").toLowerCase(),iTreat=(data.treatment||"").toLowerCase();
      const iPow=String(data.power||"").replace(/[^0-9]/g,"");
      let match=null;

      // HERO-FIRST: hero name is the most reliable read. Find all cards for this hero, then pin the variant.
      const heroCards = iHero ? cards.filter(c=>heroMatch(c.hero,iHero)) : [];

      if(heroCards.length){
        // 1. Hero + exact card number. If several cards share this number (same
        //    set number across weapons/treatments), disambiguate by treatment
        //    (strong signal) then weapon — only auto-pick a confident winner.
        if(iNum){
          const numCards=heroCards.filter(c=>normNum(c.cardNum)===iNum);
          if(numCards.length===1){ match=numCards[0]; }
          else if(numCards.length>1){
            // Try each strong signal in order; take it if it resolves to exactly one card.
            const byTreat = iTreat ? numCards.filter(c=>c.treatment?.toLowerCase()===iTreat) : [];
            const byWeap  = iWeap  ? numCards.filter(c=>canonWeapon(c.weapon)===canonWeapon(iWeap)) : [];
            const byPow   = iPow   ? numCards.filter(c=>String(c.power||"").replace(/[^0-9]/g,"")===iPow) : [];
            if(byTreat.length===1) match=byTreat[0];
            else if(byWeap.length===1) match=byWeap[0];
            else if(byPow.length===1) match=byPow[0];
            else {
              // combine weapon+treatment to narrow, then weapon+power
              const wt = numCards.filter(c=>(!iWeap||canonWeapon(c.weapon)===canonWeapon(iWeap))&&(!iTreat||c.treatment?.toLowerCase()===iTreat));
              if(wt.length===1) match=wt[0];
              // else leave null -> candidate suggester shows the shared-number cards
            }
          }
        }
        // 2. Hero + fuzzy card number (off by 1 char — handles a misread digit)
        if(!match&&iNum){const cands=heroCards.filter(c=>lev(normNum(c.cardNum),iNum)<=1);if(cands.length===1)match=cands[0];}
        // 3. Hero + treatment + weapon all agree (no number needed)
        if(!match&&iTreat&&iWeap) match=heroCards.find(c=>c.treatment?.toLowerCase()===iTreat&&c.weapon?.toLowerCase()===iWeap);
        // 4. Hero + treatment + power agree
        if(!match&&iTreat&&iPow){const cands=heroCards.filter(c=>c.treatment?.toLowerCase()===iTreat&&String(c.power||"").replace(/[^0-9]/g,"")===iPow);if(cands.length===1)match=cands[0];}
        // 5. Hero + treatment, single candidate
        if(!match&&iTreat){const cands=heroCards.filter(c=>c.treatment?.toLowerCase()===iTreat);if(cands.length===1)match=cands[0];}
        // 6. Hero + weapon + power, single candidate
        if(!match&&iWeap&&iPow){const cands=heroCards.filter(c=>c.weapon?.toLowerCase()===iWeap&&String(c.power||"").replace(/[^0-9]/g,"")===iPow);if(cands.length===1)match=cands[0];}
        // 7. Hero + weapon, single candidate
        if(!match&&iWeap){const cands=heroCards.filter(c=>c.weapon?.toLowerCase()===iWeap);if(cands.length===1)match=cands[0];}
        // Weapon->power correlation: when hero+weapon yields several candidates (card number unreadable),
        // the weapon usually pins a power level. Narrow by power, then treatment, to resolve to one card.
        if(!match&&iWeap){
          const wCands=heroCards.filter(c=>c.weapon?.toLowerCase()===iWeap);
          if(wCands.length>1){
            let narrow = iPow ? wCands.filter(c=>String(c.power||"").replace(/[^0-9]/g,"")===iPow) : wCands;
            if(narrow.length===0) narrow = wCands;
            if(iTreat){ const t=narrow.filter(c=>c.treatment?.toLowerCase()===iTreat); if(t.length) narrow=t; }
            if(narrow.length===1) match=narrow[0];
            else if(narrow.length>1){
              const sig=new Set(narrow.map(c=>`${(c.treatment||"").toLowerCase()}|${c.power}`));
              if(sig.size===1) match=narrow[0];
            }
          }
        }
        // 8. Hero has only one card total
        if(!match&&heroCards.length===1) match=heroCards[0];
      }

      // Fallback: if hero wasn't read at all, exact number + (if present) hero agreement — never number-alone across heroes
      if(!match&&!iHero&&iNum){const cands=cards.filter(c=>normNum(c.cardNum)===iNum);if(cands.length===1)match=cands[0];}

      if(match){
        // Loan-by-photo: a match in loan mode goes to the who-prompt, not collection.
        if(scanLoanMode){ setLoanScanCard({card:match,mode:scanLoanMode}); setPhotoScan(null); setScanModal(false); return; }
        setPhotoScan({status:"matched",card:match,detected:data,scanPhoto:display}); setScanQty(1); return;
      }

      // No single confident match -- suggest candidates. Prefer this hero's cards (weapon/treatment/power ranking).
      let pool = heroCards.length ? heroCards : cards;
      const scored=pool.map(c=>{
        let s=0;
        if(iHero&&heroMatch(c.hero,iHero))s+=60;          // hero is king
        if(iNum&&normNum(c.cardNum)===iNum)s+=50;
        else if(iNum&&lev(normNum(c.cardNum),iNum)<=1)s+=20;
        if(iTreat&&c.treatment?.toLowerCase()===iTreat)s+=35; // treatment strongly narrows
        if(iWeap&&c.weapon?.toLowerCase()===iWeap)s+=30;      // weapon narrows the rainbow
        if(iPow&&String(c.power||"").replace(/[^0-9]/g,"")===iPow)s+=15;
        return {card:c,score:s};
      }).filter(x=>x.score>0).sort((a,b)=>b.score-a.score);

      const candidates=scored.slice(0,8).map(x=>x.card);
      // Loan mode: even a fuzzy result opens the who-prompt with manual search,
      // rather than the collection candidate picker — keep in-person loans fast.
      if(scanLoanMode){ setLoanScanCard({card: candidates[0]||null, mode:scanLoanMode}); setPhotoScan(null); setScanModal(false); return; }
      if(candidates.length>0){ setPhotoScan({status:"candidates",candidates,detected:data,scanPhoto:display}); return; }

      setPhotoScan({status:"nomatch",identified:data});
    } catch(e){
      // In loan mode, don't dead-end on a scan error — open manual entry.
      if (scanLoanMode) { setLoanScanCard({card:null,mode:scanLoanMode}); setPhotoScan(null); setScanModal(false); }
      else setPhotoScan({status:"error",message:e.message});
    }
    finally { scanInFlight.current = false; }
  }
  // ── MULTI-CARD BINDER SCAN ──────────────────────────────────────────────────────────────────
  // One photo with several cards -> detect all, match each against the checklist, show a deselect
  // grid. Everything matched is pre-checked; the user unchecks misses and taps "Add".
  async function scanPagePhoto(file) {
    setPageScan({ status:"scanning" });
    setPageSel(new Set());
    try {
      // Resize bigger than a single scan (a page of 9 needs detail to read each card). Max 2000px.
      // Slice the page into a GRID and send one crop PER card — each crop is a full-detail image of a
      // single card, which the model reads far more accurately than nine tiny cards in one photo.
      // Default 3x3 (a standard binder page). gridRows/gridCols come from state.
      const gRows = pageGrid.rows, gCols = pageGrid.cols;
      const crops = await new Promise((res,rej) => {
        const img=new Image(), url=URL.createObjectURL(file);
        img.onload=()=>{ URL.revokeObjectURL(url);
          const cellW = img.width / gCols, cellH = img.height / gRows;
          // Upscale each cell so small text (card number, treatment) stays legible.
          const TARGET_H = 1100;
          // Trim a small margin off each cell so a neighbor bleeding across the pocket line
          // doesn't pollute the read (treatment text lives at the bottom and is the first casualty).
          const INSET = 0.04; // 4% inward on each side
          const out = [];
          for (let r=0; r<gRows; r++) {
            for (let cc=0; cc<gCols; cc++) {
              const sx = (cc + INSET) * cellW, sy = (r + INSET) * cellH;
              const sw = cellW * (1 - INSET*2), sh = cellH * (1 - INSET*2);
              const scale = Math.min(2.4, TARGET_H / sh);
              const cw = Math.round(sw * scale), ch = Math.round(sh * scale);
              const cv = document.createElement("canvas"); cv.width=cw; cv.height=ch;
              const ctx = cv.getContext("2d"); ctx.imageSmoothingEnabled=true; ctx.imageSmoothingQuality="high";
              ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
              out.push(cv.toDataURL("image/jpeg", 0.92).split(",")[1]);
            }
          }
          res(out);
        };
        img.onerror=rej; img.src=url;
      });
      const resp = await fetch("/api/scan-page", {
        method:"POST", headers:{ "Content-Type":"application/json" },
        body: JSON.stringify({ crops }),
      });
      if (!resp.ok) {
        const e = await resp.json().catch(()=>({}));
        setPageScan({ status:"error", message: (e.details ? `${e.error}: ${String(e.details).slice(0,140)}` : e.error) || `HTTP ${resp.status}` });
        return;
      }
      const data = await resp.json();
      const detected = Array.isArray(data.cards) ? data.cards : [];
      if (!detected.length) { setPageScan({ status:"error", message:"Couldn't read any cards. Make sure the page fills the frame straight-on (each pocket lands in its own grid cell), with good light and no glare." }); return; }
      // Match each detected card against the checklist.
      const rows = detected.map((d, idx) => {
        const { match, candidates } = matchOne(d);
        return {
          key: `r${idx}`,
          detected: d,
          match: match || null,                          // resolved card, or null
          candidates: (candidates||[]).slice(0,6),       // if ambiguous, offer a pick
          picked: match ? match.id : null,               // which card id this row will add
        };
      });
      // Pre-select every row that resolved to a confident match.
      const sel = new Set(rows.filter(r=>r.picked).map(r=>r.key));
      setPageSel(sel);
      setPageScan({ status:"review", rows });
    } catch(e) {
      setPageScan({ status:"error", message: e?.message || String(e) });
    }
  }

  // Add all checked rows to the collection in one write.
  async function addScannedPage() {
    if (!user) { setSigningIn(true); return; }
    const rows = (pageScan?.rows||[]).filter(r => pageSel.has(r.key) && r.picked);
    if (!rows.length) { alert("Nothing selected to add."); return; }
    setPageBusy(true);
    try {
      const next = { ...owned };
      rows.forEach(r => { next[r.picked] = (parseInt(next[r.picked])||0) + 1; });
      setOwned(next);
      await setDoc(doc(db,"boba_owned",user.uid), next);
      // Log each as a lot (acquisition), mirroring single-scan bookkeeping — no photo per card here.
      const newLots = rows.map(r => ({ id: uid(), cardId: r.picked, cost:null, value:null, method:"scan", date:new Date().toISOString().split("T")[0], notes:"binder page scan", photoUrl:"" }));
      const mergedLots = [...lots, ...newLots];
      setLots(mergedLots);
      try { await setDoc(doc(db,"boba_lots",user.uid), { lots: mergedLots }); } catch(_){}
      showToast(`Added ${rows.length} card${rows.length===1?"":"s"} from the page`);
      setPageScan(null); setPageSel(new Set());
    } catch(e) {
      alert("Couldn't add cards: " + (e?.message||e));
    } finally { setPageBusy(false); }
  }

  // Start listening for a spoken card. Uses the browser's built-in speech recognition (on-device,
  // no server). Not supported in every browser — we tell the user if it's unavailable.
  function startVoiceAdd() {
    if (!user) { setSigningIn(true); return; }
    const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
    if (!SR) {
      setVoiceState({ status:"error", message:"Your browser doesn't support voice input. Chrome or Safari on your phone works best." });
      return;
    }
    try {
      // Stop any previous session.
      if (voiceRecRef.current) { try { voiceRecRef.current.abort(); } catch(_){} }
      const rec = new SR();
      rec.lang = "en-US";
      rec.interimResults = false;
      rec.maxAlternatives = 3;
      rec.continuous = false;
      voiceRecRef.current = rec;
      setVoiceState({ status:"listening" });
      rec.onresult = (ev) => {
        // Try each alternative transcript, keep the one that yields the best matches.
        let best = [], bestT = "";
        for (let i = 0; i < (ev.results[0]?.length || 0); i++) {
          const t = ev.results[0][i].transcript;
          const m = voiceMatchCards(t);
          if (m.length && (!best.length || m.length > best.length)) { best = m; bestT = t; }
          if (!bestT) bestT = t;
        }
        if (best.length) setVoiceState({ status:"result", transcript: bestT, matches: best, picked: best[0].id });
        else setVoiceState({ status:"nomatch", transcript: bestT });
      };
      rec.onerror = (ev) => {
        const msg = ev.error === "not-allowed" ? "Microphone access was blocked. Enable it for this site and try again."
          : ev.error === "no-speech" ? "Didn't catch anything — tap the mic and try again."
          : `Voice error: ${ev.error}`;
        setVoiceState({ status:"error", message: msg });
      };
      rec.onend = () => { setVoiceState(s => (s && s.status === "listening") ? { status:"nomatch", transcript:"" } : s); };
      rec.start();
    } catch(e) {
      setVoiceState({ status:"error", message: e?.message || "Couldn't start voice input." });
    }
  }

  // Add the confirmed voice-matched card to the collection.
  async function addVoiceCard() {
    const st = voiceState;
    if (!st || !st.picked) return;
    const card = cards.find(c => c.id === st.picked);
    if (!card) return;
    try {
      const next = { ...owned, [card.id]: (parseInt(owned[card.id])||0) + 1 };
      setOwned(next);
      await setDoc(doc(db,"boba_owned",user.uid), next);
      const newLot = { id: uid(), cardId: card.id, cost:null, value:null, method:"voice", date:new Date().toISOString().split("T")[0], notes:"voice add", photoUrl:"" };
      const mergedLots = [...lots, newLot];
      setLots(mergedLots);
      try { await setDoc(doc(db,"boba_lots",user.uid), { lots: mergedLots }); } catch(_){}
      showToast(`Added ${card.hero}`);
      setVoiceState(null);
    } catch(e) {
      alert("Couldn't add: " + (e?.message||e));
    }
  }



  async function confirmScan(card,qty,opts={}) {
    if(!user){setSigningIn(true);return;}
    const next={...owned,[card.id]:(owned[card.id]||0)+qty};
    setOwned(next);
    await setDoc(doc(db,"boba_owned",user.uid),next);

    // Optionally upload the scan photo to Storage
    let photoUrl = "";
    if (opts.keepPhoto && opts.scanPhoto) {
      try {
        const sref = ref(storage, `scan_photos/${user.uid}/${card.id}_${uid()}.jpg`);
        await uploadString(sref, opts.scanPhoto, "data_url");
        photoUrl = await getDownloadURL(sref);
      } catch(e) { console.error("scan photo upload failed:", e); alert("⚠️ Your card was added, but the PHOTO couldn't be saved: " + (e?.code || e?.message || e) + "\n\nThis is usually a storage-permissions issue. The card is safe; the photo just didn't upload."); }
    }

    // Log acquisition as a lot (one per copy), carrying cost/method/notes + the saved photo
    const cost = opts.cost!==undefined && opts.cost!=="" ? parseFloat(opts.cost) : null;
    const newLots = [];
    for (let i=0;i<qty;i++) {
      newLots.push({ id: uid(), cardId: card.id, cost, value:null, method: opts.method||"break", date: new Date().toISOString().split("T")[0], notes: opts.notes||"", photoUrl });
    }
    const mergedLots = [...lots, ...newLots];
    setLots(mergedLots);
    try { await setDoc(doc(db,"boba_lots",user.uid), { lots: mergedLots }); } catch(e){ console.error("save scan lots failed:", e); }

    setScanSession(prev=>[{card,qty,addedAt:new Date().toISOString(),photoUrl},...prev]);
    setPhotoScan(null); setScanQty(1);
  }

  // -- Friends --
  async function addFriendByUid(toUid, toProfile) {
    if (!user || !toUid || toUid === user.uid) return;
    try {
      const existSnap = await getDocs(query(collection(db,"friend_requests"), where("from","==",user.uid), where("to","==",toUid)));
      if (!existSnap.empty) return;
      const id = uid();
      await setDoc(doc(db,"friend_requests",id), { id, from:user.uid, fromName:user.displayName||"Collector", to:toUid, toName:toProfile?.displayName||toProfile?.username||"Collector", status:"pending", createdAt:new Date().toISOString() });
      await setDoc(doc(db,"boba_profiles",user.uid), { displayName:user.displayName||"Collector", photoURL:user.photoURL||"" }, { merge:true });
    } catch(e) { console.error("add friend failed", e); }
  }
  async function messageUser(toUid, toProfile) {
    if (!user || !toUid) return;
    const threadId = ["dm", ...[user.uid, toUid].sort()].join("_");
    try {
      const tSnap = await getDoc(doc(db,"deal_threads",threadId));
      if (!tSnap.exists()) {
        await setDoc(doc(db,"deal_threads",threadId), {
          id: threadId, kind:"dm",
          participants:[user.uid, toUid],
          participantNames:{ [user.uid]: user.displayName||user.email, [toUid]: toProfile?.username?`@${toProfile.username}`:(toProfile?.displayName||"Collector") },
          messages:[], createdAt:new Date().toISOString(), status:"open",
        });
      }
      const full = (await getDoc(doc(db,"deal_threads",threadId))).data();
      setActiveThread({ id:threadId, ...full });
      setViewProfileUid(null);
      setActiveTab("messages");
    } catch(e) { console.error("open dm failed", e); }
  }

  async function sendFriendRequest() {
    if(!user||!addEmail.trim())return;
    setAddStatus(null);
    // Accept EITHER a username (@handle) or an email address of any kind. This used to say "Google
    // sign-in email", which was wrong twice over: the lookup was never Google-specific (it matches
    // whatever address someone signed up with, including email/password accounts), and plenty of
    // people don't have or don't remember the email at all — but they do know each other's handles.
    const raw = addEmail.trim();
    const term = raw.replace(/^@/,"").toLowerCase();

    // Friending is by USERNAME only. Email was removed from public profiles (it
    // exposed every user's address), so there's no email->uid lookup anymore.
    let toProfile=null, toUid=null;
    if (term.includes("@") && term.includes(".")) {
      setAddStatus({ok:false,msg:"Add collectors by their username now, not email — ask them for their @handle."});
      return;
    }
    try {
      const uSnap = await getDoc(doc(db,"usernames",term));
      if (uSnap.exists()) {
        toUid = uSnap.data().uid;
        const pSnap = await getDoc(doc(db,"boba_profiles",toUid));
        toProfile = pSnap.exists() ? pSnap.data() : { username: term };
      }
    } catch(e){ /* fall through to the not-found message */ }
    if(!toUid){
      setAddStatus({ok:false,msg:`No collector found with the username "${term}" — usernames are set on their profile page.`});
      return;
    }
    if(toUid===user.uid){setAddStatus({ok:false,msg:"That's you!"});return;}
    if(friends.find(f=>f.friendUid===toUid)){setAddStatus({ok:false,msg:"Already friends"});return;}
    const existSnap=await getDocs(query(collection(db,"friend_requests"),where("from","==",user.uid),where("to","==",toUid)));
    if(!existSnap.empty){setAddStatus({ok:false,msg:"Request already sent"});return;}
    const id=uid();
    await setDoc(doc(db,"friend_requests",id),{id,from:user.uid,fromName:user.displayName||"Collector",to:toUid,toName:toProfile?.displayName||toProfile?.username||"Collector",status:"pending",createdAt:new Date().toISOString()});
    await setDoc(doc(db,"boba_profiles",user.uid),{displayName:user.displayName||"Collector",photoURL:user.photoURL||""},{merge:true});
    setAddEmail(""); setAddStatus({ok:true,msg:`Request sent to ${toProfile?.displayName||toProfile?.username||"collector"}!`});
  }
  async function respondFriendReq(req,accept) {
    await setDoc(doc(db,"friend_requests",req.id),{status:accept?"accepted":"declined"},{merge:true});
    if(accept) await setDoc(doc(db,"boba_profiles",user.uid),{displayName:user.displayName||"Collector",photoURL:user.photoURL||""},{merge:true});
  }
  // Mark/unmark a friend as family. Writes my uid into the relationship's familyBy array,
  // which grants ME access to THEIR available cards in the deck builder.
  // Family sharing is MUTUAL. It has to be.
  //
  // familyBy used to be a single one-sided array: I add my uid, and that ALONE granted me read
  // access to their entire collection \u2014 every card, every quantity. They were never asked and never
  // told. Anyone could help themselves to anyone else's collection with one click.
  //
  // Now familyBy means "I have OFFERED to share with this person". Access is granted only where BOTH
  // parties appear in it. Marking someone as family shares YOUR cards with THEM; you see theirs when
  // they do the same. Consent runs both ways or it is not consent.
  async function toggleFamily(f) {
    if(!user||!f?.id) return;
    try {
      const snap = await getDoc(doc(db,"friend_requests",f.id));
      const cur = snap.exists() ? (snap.data().familyBy||[]) : [];
      const mine = cur.includes(user.uid);
      const next = mine ? cur.filter(u=>u!==user.uid) : [...cur, user.uid];
      await setDoc(doc(db,"friend_requests",f.id),{familyBy:next},{merge:true});
      if (!mine) {
        const theirs = cur.includes(f.friendUid);
        setToast(theirs
          ? `\u{1F46A} You and ${f.friendName} are now sharing cards`
          : `\u2713 Your cards are shared with ${f.friendName} \u2014 you'll see theirs once they share back`);
      }
    } catch(e){ console.error("toggleFamily failed:",e); }
  }

  // -- Teams --
  async function createTeam() {
    if(!user||!newTeamName.trim())return;
    const id=uid();
    const me={uid:user.uid,displayName:user.displayName||"Collector",photoURL:user.photoURL||""};
    await setDoc(doc(db,"teams",id),{id,name:newTeamName.trim(),starters:[me],bench:[],members:[me],memberUids:[user.uid],createdBy:user.uid,createdAt:new Date().toISOString()});
    setNewTeamName("");
  }
  // Submit ONE of my saved decks to a team. Writes only the deck's cardIds — no collection data.
  // memberUids mirrors the team roster so teammates' listeners can read it under Firestore rules.
  async function submitDeckToTeam(team, deck) {
    if(!user||!team||!deck)return;
    const docId=`${team.id}_${user.uid}`;
    await setDoc(doc(db,"team_decks",docId),{
      id:docId, teamId:team.id, uid:user.uid,
      displayName:user.displayName||user.email,
      deckId:deck.id, deckName:deck.name||"Deck",
      cardIds:deck.cardIds||[],
      memberUids:team.memberUids||[],
      submittedAt:new Date().toISOString(),
    });
  }
  async function withdrawTeamDeck(team) {
    if(!user||!team)return;
    try { await deleteDoc(doc(db,"team_decks",`${team.id}_${user.uid}`)); } catch(e){ console.error("withdraw deck failed:",e); }
  }
  // I volunteer a card from MY collection to resolve a team conflict. Teammates never saw my
  // collection — my own client detected I could help, and I chose to offer this specific card.
  async function offerSwap(team, conflictKey, card, forUid) {
    if(!user||!team||!card)return;
    const id=uid();
    await setDoc(doc(db,"swap_offers",id),{
      id, teamId:team.id, conflictKey,
      fromUid:user.uid, fromName:user.displayName||user.email,
      toUid:forUid||null,
      cardId:card.id, cardLabel:`${card.hero} ${card.power}⚡ ${card.treatment||""}`.trim(),
      memberUids:team.memberUids||[],
      status:"open", createdAt:new Date().toISOString(),
    });
  }
  async function dismissSwapOffer(offerId) {
    try { await deleteDoc(doc(db,"swap_offers",offerId)); } catch(e){ console.error("dismiss offer failed:",e); }
  }
  // Owner logs "I lent this card to a teammate." Only the lender can create/return their loans.
  async function lendCard(team, borrower, card) {
    if(!user||!team||!borrower||!card)return;
    const id=uid();
    await setDoc(doc(db,"team_loans",id),{
      id, teamId:team.id,
      lenderUid:user.uid, lenderName:user.displayName||user.email,
      borrowerUid:borrower.uid, borrowerName:borrower.displayName||borrower.email,
      cardId:card.id, cardLabel:`${card.hero} ${card.power}⚡ ${card.treatment||""}`.trim(),
      cardImage:card.imageUrl||"",
      status:"active", lentAt:new Date().toISOString(), returnedAt:null,
      memberUids:team.memberUids||[],
    });
  }
  async function markLoanReturned(loan) {
    if(!user||!loan)return;
    try { await setDoc(doc(db,"team_loans",loan.id),{status:"returned",returnedAt:new Date().toISOString()},{merge:true}); }
    catch(e){ console.error("mark returned failed:",e); }
  }
  async function deleteLoan(loanId) {
    try { await deleteDoc(doc(db,"team_loans",loanId)); } catch(e){ console.error("delete loan failed:",e); }
  }
  async function inviteToTeam(team) {
    if(!inviteEmail.trim())return;
    const handle=inviteEmail.trim().replace(/^@/,"").toLowerCase();
    if(handle.includes("@")&&handle.includes(".")){setInviteStatus({ok:false,msg:"Invite by @username now, not email."});return;}
    const uSnap=await getDoc(doc(db,"usernames",handle));
    if(!uSnap.exists()){setInviteStatus({ok:false,msg:`No collector with the username "${handle}"`});return;}
    const toUid=uSnap.data().uid;
    const pSnap=await getDoc(doc(db,"boba_profiles",toUid));
    const toProfile=pSnap.exists()?pSnap.data():{username:handle};
    if(team.memberUids?.includes(toUid)){setInviteStatus({ok:false,msg:"Already on team"});return;}
    if((team.members||[]).length>=6){setInviteStatus({ok:false,msg:"Team is full (4 starters + 2 bench max)"});return;}
    const invId=uid();
    await setDoc(doc(db,"team_invites",invId),{id:invId,teamId:team.id,teamName:team.name,fromUid:user.uid,fromName:user.displayName||"Collector",toUid,toName:toProfile.displayName||toProfile.username||"Collector",status:"pending",createdAt:new Date().toISOString()});
    setInviteEmail(""); setInviteStatus({ok:true,msg:`Invite sent to ${toProfile.displayName||toProfile.username||"collector"}`});
  }

  async function deleteTeam(team) {
    if(!window.confirm(`Delete team "${team.name}"? This cannot be undone.`))return;
    await setDoc(doc(db,"teams",team.id),{status:"deleted"},{merge:true});
    setActiveTeam(null);
  }

  async function moveTeamMember(team, memberUid, direction) {
    // direction: "to_bench" or "to_starter"
    const starters = [...(team.starters||[])];
    const bench    = [...(team.bench||[])];
    if(direction==="to_bench") {
      if(bench.length>=2){return;} // bench full
      const idx = starters.findIndex(m=>m.uid===memberUid);
      if(idx<0)return;
      const [moved] = starters.splice(idx,1);
      bench.push(moved);
    } else {
      if(starters.length>=4){return;} // starters full
      const idx = bench.findIndex(m=>m.uid===memberUid);
      if(idx<0)return;
      const [moved] = bench.splice(idx,1);
      starters.push(moved);
    }
    await setDoc(doc(db,"teams",team.id),{starters,bench},{merge:true});
    setActiveTeam(prev=>prev?{...prev,starters,bench}:prev);
  }
  async function respondTeamInvite(invite,accept) {
    await setDoc(doc(db,"team_invites",invite.id),{status:accept?"accepted":"declined"},{merge:true});
    if(accept){
      const tsnap=await getDoc(doc(db,"teams",invite.teamId));
      if(tsnap.exists()){
        const t=tsnap.data();
        const me={uid:user.uid,displayName:user.displayName||"Collector",photoURL:user.photoURL||""};
        const starters=t.starters||[];
        const bench=t.bench||[];
        // Put in starters if room, else bench
        if(starters.length<4) starters.push(me);
        else bench.push(me);
        await setDoc(doc(db,"teams",invite.teamId),{
          starters,bench,
          members:[...(t.members||[]),me],
          memberUids:[...(t.memberUids||[]),user.uid]
        },{merge:true});
      }
    }
  }

  // -- Marketplace --
  async function uploadListingPhoto(file) {
    if (!user || !file) return;
    setListUploading(true);
    try {
      const path = `marketplace_photos/${user.uid}/${uid()}_${(file.name||"photo").replace(/[^a-zA-Z0-9.]/g,"_")}`;
      const sref = ref(storage, path);
      const _cfile = await compressImage(file);
      await uploadBytes(sref, _cfile);
      const url = await getDownloadURL(sref);
      setListPhotos(prev => [...prev, { url }]);
    } catch(e) { console.error("photo upload failed:", e); alert("Photo upload failed — please try again."); }
    setListUploading(false);
  }
  async function createListing() {
    if(!user||!listModal)return;
    // Trust & safety: require at least one real photo, and a price for any sale-type listing
    if(listPhotos.length===0){ alert("Please add at least one photo of your actual card before listing."); return; }
    if(listType!=="offer"&&listType!=="trade"&&(!listPrice||parseFloat(listPrice)<=0)){ alert("Please set an asking price to list this card for sale."); return; }
    const id=uid();
    const card=listModal;
    // Locked cards are never listed, however you got here.
    if (lockedCardIds([card.id]).length) {
      alert("\uD83D\uDD12 " + (card.hero||"This card") + " is marked “never trade or sell”.\n\nUnlock it in the copy details if you really want to list it.");
      return;
    }
    await setDoc(doc(db,"marketplace",id),{id,isOBO:listType==="offer",cardId:card.id,cardName:card.hero,cardNum:card.cardNum,cardTreatment:card.treatment,cardWeapon:card.weapon,cardPower:card.power,cardImage:card.imageUrl||null,sellerPhotos:listPhotos.map(p=>p.url),setName:card.setName,sellerUid:user.uid,sellerName:user.displayName||user.email,askingPrice:parseFloat(listPrice)||0,listType,notes:listNotes,paymentInfo:listPayment,status:"active",createdAt:new Date().toISOString(),offerCount:0});
    // Notify users who have this card on their want list
    try {
      const wantSnaps = await getDocs(collection(db,"boba_wants"));
      const notifBatch = [];
      wantSnaps.forEach(wsnap => {
        const wantData = wsnap.data();
        if (wsnap.id !== user.uid && wantData[card.id]) {
          notifBatch.push(setDoc(doc(db,"market_notifs",uid()), {
            toUid: wsnap.id, cardId: card.id, cardName: card.hero,
            cardImage: card.imageUrl||null, listingId: id,
            sellerName: user.displayName||user.email,
            askingPrice: parseFloat(listPrice)||0,
            type: "want_listed", read: false,
            createdAt: new Date().toISOString()
          }));
        }
      });
      await Promise.all(notifBatch);
    } catch(e) { console.error("Want notification error:", e); }
    setListModal(null); setListPrice(""); setListNotes(""); setListPayment(""); setListType("sale"); setListPhotos([]);
  }
  async function removeListing(id) {
    if(!window.confirm("Remove this listing?"))return;
    await setDoc(doc(db,"marketplace",id),{status:"removed"},{merge:true});
  }
  async function unsellListing(sale) {
    if(!window.confirm("Reverse this sale? The listing will reopen and the card will be removed from the buyer's collection.")) return;
    // Reopen the listing
    try {
      if(sale.listingId) await setDoc(doc(db,"marketplace",sale.listingId),{status:"active",soldAt:null,soldTo:null,soldFor:null},{merge:true});
    } catch(e){ console.warn("Listing reopen failed:",e); }
    // Remove from buyer collection
    try {
      if(sale.cardId&&sale.buyerUid) {
        const buyerSnap=await getDoc(doc(db,"boba_owned",sale.buyerUid));
        if(buyerSnap.exists()) {
          const bo=buyerSnap.data(); const newQty=(bo[sale.cardId]||1)-1;
          const next={...bo}; if(newQty<=0) delete next[sale.cardId]; else next[sale.cardId]=newQty;
          await setDoc(doc(db,"boba_owned",sale.buyerUid),next);
          if(user?.uid===sale.buyerUid) setOwned(next);
        }
      }
    } catch(e){ console.warn("Buyer reversal failed:",e); }
    // Restore seller collection
    try {
      if(sale.cardId&&sale.sellerUid) {
        const sellerSnap=await getDoc(doc(db,"boba_owned",sale.sellerUid));
        const so=sellerSnap.exists()?sellerSnap.data():{};
        const next={...so,[sale.cardId]:(so[sale.cardId]||0)+1};
        await setDoc(doc(db,"boba_owned",sale.sellerUid),next);
        if(user?.uid===sale.sellerUid) setOwned(next);
      }
    } catch(e){ console.warn("Seller restore failed:",e); }
    // Delete the sale doc by ID
    try {
      if(sale.id) {
        await deleteDoc(doc(db,"market_sales",sale.id));
      } else {
        // fallback: query by soldAt+sellerUid
        const saleSnap=await getDocs(query(collection(db,"market_sales"),where("sellerUid","==",sale.sellerUid),where("soldAt","==",sale.soldAt)));
        await Promise.all(saleSnap.docs.map(d=>deleteDoc(doc(db,"market_sales",d.id))));
      }
    } catch(e){ console.warn("Sale doc delete failed:",e); }
    showToast("Sale reversed.");
  }

  async function saveTracking(saleId, trackingNum, carrier) {
    await setDoc(doc(db,"market_sales",saleId),{trackingNumber:trackingNum,carrier:carrier||"",shippedAt:new Date().toISOString()},{merge:true});
    showToast("Tracking saved!");
  }
  async function submitOffer() {
    if(!user||!offerModal||!offerAmt)return;
    const offId=uid();
    await setDoc(doc(db,"market_offers",offId),{id:offId,cardId:offerModal.cardId||offerModal.id,listingId:offerModal.id,cardName:offerModal.cardName,cardImage:offerModal.cardImage||null,cardTreatment:offerModal.cardTreatment||"",cardWeapon:offerModal.cardWeapon||"",setName:offerModal.setName||"",sellerUid:offerModal.sellerUid,awaitingUid:offerModal.sellerUid,sellerName:offerModal.sellerName,buyerUid:user.uid,buyerName:user.displayName||user.email,buyerEmail:user.email,offerAmount:parseFloat(offerAmt)||0,note:offerNote,status:"pending",notified:false,createdAt:new Date().toISOString()});
    // Increment offer count
    await setDoc(doc(db,"marketplace",offerModal.id),{offerCount:(offerModal.offerCount||0)+1},{merge:true});
    setOfferSent(true);
  }

  // ── Trade actions ────────────────────────────────────────────────────────────────────────────
async function sendTradeOffer({ toUid, toName, theirCards=[], myCards=[], note, cashFromSender=0, cashFromRecipient=0 }) {
    if (!user || !toUid) return;
    if (myCards.length === 0 && (parseFloat(cashFromSender)||0) <= 0 && theirCards.length === 0 && (parseFloat(cashFromRecipient)||0) <= 0) { alert("Add at least one card or some cash to the offer."); return; }

    // Re-check at SEND time, not just at render time. The card list you're looking at could be
    // seconds old — someone else may have locked the same card in the meantime, and the client-side
    // filter would never know. Better to fail loudly here than to promise a card that's gone.
    const live = tradeOffers.filter(t => t.status === "pending" || t.status === "accepted");
    const theirsLocked = new Set();
    live.forEach(t => {
      (t.giving  || []).forEach(c => theirsLocked.add(`${t.fromUid}:${c.id}`));
      (t.getting || []).forEach(c => theirsLocked.add(`${t.toUid}:${c.id}`));
    });
    const clash = theirCards.find(c => theirsLocked.has(`${toUid}:${c.id}`))
               || myCards.find(c => theirsLocked.has(`${user.uid}:${c.id}`));
    if (clash) {
      alert(`"${clash.hero || clash.playName || "That card"}" is already committed to another trade. Refresh and try again.`);
      return;
    }
    // A card that's lent out or borrowed isn't yours to trade — lent copies are
    // physically with someone else, borrowed copies aren't yours at all. Block
    // before the deck check so the message is accurate.
    const loanClash = myCards.find(c => {
      if ((borrowedByCard[c.id]||0) > 0) return true;              // borrowed — not yours
      const free = (owned[c.id]||0) - (lentByCard[c.id]||0);       // owned minus lent-out
      return (lentByCard[c.id]||0) > 0 && free <= 0;               // every copy is lent
    });
    if (loanClash) {
      const isBorrowed = (borrowedByCard[loanClash.id]||0) > 0;
      alert(`"${loanClash.hero || loanClash.playName || "That card"}" is ${isBorrowed ? "borrowed — it isn't yours to trade" : "lent out — get it back before trading it away"}.`);
      return;
    }
    // Also block cards of mine that are locked into a deck (mine or family's). The offer builder
    // already hides these, but re-check at send time in case a deck changed under me.
    const deckClash = myCards.find(c => (owned[c.id]||0) <= (deckLockedForTrade[c.id]||0));
    if (deckClash) {
      const dn = (deckLockNames[deckClash.id] || []).map(d => d.owner ? `${d.name} (${d.owner})` : d.name);
      const where = dn.length ? `\n\nIt's in: ${dn.join(", ")}` : "";
      alert(`"${deckClash.hero || deckClash.playName || "That card"}" is in a deck. Remove it from the deck before trading it away.${where}`);
      return;
    }
    const id = uid();
    const now = new Date().toISOString();
    const slim = c => ({ id:c.id, name:c.hero||c.playName||"", image:c.imageUrl||null,
                         treatment:c.treatment||"", weapon:c.weapon||"", setName:c.setName||"" });
    try {
      await setDoc(doc(db,"trade_offers",id), {
        id,
        fromUid: user.uid, fromName: user.displayName || user.email || "Collector",
        toUid, toName: toName || "Collector",
        // "give" and "get" are always from the SENDER's point of view. Naming them relative to the
        // viewer would mean flipping them everywhere and getting it wrong somewhere.
        giving: myCards.map(slim),      // what the SENDER hands over
        getting: theirCards.map(slim),  // what the SENDER receives
        cashFromSender: parseFloat(cashFromSender)||0,
        cashFromRecipient: parseFloat(cashFromRecipient)||0,
        needsRecipientSide: theirCards.length===0 && (parseFloat(cashFromRecipient)||0)<=0,  // recipient still to add their side
        fromAccepted: false, toAccepted: false,
        note: (note||"").trim(),
        status: "pending",
        // Settlement is per-side: each person confirms when THEIR package arrives, and only then
        // does their own collection change. Neither can write the other's.
        fromReceived: false, toReceived: false,
        fromTracking: "", toTracking: "",
        participantUids: [user.uid, toUid],
        createdAt: now,
      });
      await setDoc(doc(db,"market_notifs",uid()), {
        toUid, type:"trade_offer", tradeId:id,
        fromName: user.displayName || user.email,
        read:false, createdAt:now,
      });
      showToast("Trade offer sent");
    } catch(e) { alert("Couldn't send offer: " + e.message); }
  }

  async function respondTradeOffer(t, accept) {
    if (!user || (t.fromUid !== user.uid && t.toUid !== user.uid)) return;   // only participants
    const iAmSender = t.fromUid === user.uid;
    try {
      if (!accept) {
        // Decline / cancel the offer outright.
        await setDoc(doc(db,"trade_offers",t.id), { status:"declined", respondedAt:new Date().toISOString() }, { merge:true });
        await setDoc(doc(db,"market_notifs",uid()), { toUid: iAmSender?t.toUid:t.fromUid, type:"trade_declined", tradeId:t.id, fromName:user.displayName||user.email, read:false, createdAt:new Date().toISOString() });
        showToast("Offer declined");
        return;
      }
      // MUTUAL ACCEPT: set my own accept flag. If both are now true, the deal is on.
      const myField = iAmSender ? "fromAccepted" : "toAccepted";
      const otherAccepted = iAmSender ? t.toAccepted : t.fromAccepted;
      const patch = { [myField]: true, respondedAt:new Date().toISOString() };
      if (otherAccepted) patch.status = "accepted";       // both in agreement -> live trade
      await setDoc(doc(db,"trade_offers",t.id), patch, { merge:true });
      await setDoc(doc(db,"market_notifs",uid()), { toUid: iAmSender?t.toUid:t.fromUid, type: otherAccepted?"trade_accepted":"trade_agreed", tradeId:t.id, fromName:user.displayName||user.email, read:false, createdAt:new Date().toISOString() });
      showToast(otherAccepted ? "Trade accepted \u2014 send your cards" : "You agreed \u2014 waiting on them to agree too");
    } catch(e) { alert("Couldn't respond: " + (e?.message||e)); }
  }
  // Pull back your acceptance (unfreezes the cards so terms can change again).
  async function unacceptTradeOffer(t) {
    if (!user || (t.fromUid !== user.uid && t.toUid !== user.uid)) return;
    const iAmSender = t.fromUid === user.uid;
    const myField = iAmSender ? "fromAccepted" : "toAccepted";
    try {
      await setDoc(doc(db,"trade_offers",t.id), { [myField]:false, status:"pending" }, { merge:true });
      showToast("Acceptance pulled back \u2014 you can change the terms now");
    } catch(e){ alert("Couldn't undo: "+(e?.message||e)); }
  }

  // Recipient fills in THEIR side of a one-sided offer (cards from their collection + optional cash),
  // then it's sent back to the original sender to accept. Only allowed while the offer is pending and
  // only by the recipient. We re-check locks at submit time, same as sendTradeOffer.
  async function addSideToTrade(t, { myCards=[], cashFromMe=0 }) {
    if (!user || t.toUid !== user.uid) return;           // only the recipient adds their side
    if (t.status !== "pending") { alert("This offer isn't open anymore."); return; }
    const slim = c => ({ id:c.id, name:c.hero||c.playName||"", image:c.imageUrl||null,
      treatment:c.treatment||"", weapon:c.weapon||"", setName:c.setName||"" });
    // Re-check: none of my chosen cards already committed elsewhere or locked in a deck.
    const live = tradeOffers.filter(x => (x.status==="pending"||x.status==="accepted") && x.id!==t.id);
    const locked = new Set();
    live.forEach(x => { (x.giving||[]).forEach(c=>locked.add(`${x.fromUid}:${c.id}`)); (x.getting||[]).forEach(c=>locked.add(`${x.toUid}:${c.id}`)); });
    const clash = myCards.find(c => locked.has(`${user.uid}:${c.id}`));
    if (clash) { alert(`"${clash.hero||clash.playName||"A card"}" is already in another trade. Refresh and try again.`); return; }
    const loanClash = myCards.find(c => {
      if ((borrowedByCard[c.id]||0) > 0) return true;
      const free = (owned[c.id]||0) - (lentByCard[c.id]||0);
      return (lentByCard[c.id]||0) > 0 && free <= 0;
    });
    if (loanClash) {
      const isBorrowed = (borrowedByCard[loanClash.id]||0) > 0;
      alert(`"${loanClash.hero||loanClash.playName||"A card"}" is ${isBorrowed ? "borrowed — it isn't yours to trade" : "lent out — get it back first"}.`);
      return;
    }
    const deckClash = myCards.find(c => (owned[c.id]||0) <= (deckLockedForTrade[c.id]||0));
    if (deckClash) { alert(`"${deckClash.hero||deckClash.playName||"A card"}" is in a deck. Remove it from the deck first.`); return; }
    try {
      // The recipient's cards become the sender's "getting" (offer is stored from the SENDER's POV).
      await setDoc(doc(db,"trade_offers",t.id), {
        getting: myCards.map(slim),
        cashFromRecipient: parseFloat(cashFromMe)||0,
        needsRecipientSide: false,
        fromAccepted: false, toAccepted: false,   // terms changed -> any prior acceptance resets
        recipientRespondedAt: new Date().toISOString(),
      }, { merge:true });
      await setDoc(doc(db,"market_notifs",uid()), {
        toUid: t.fromUid, type:"trade_countered", tradeId:t.id,
        fromName: user.displayName || user.email, read:false, createdAt:new Date().toISOString(),
      });
      showToast("Your side sent back \u2014 waiting on them to accept");
    } catch(e){ alert("Couldn't send your side: " + (e?.message||e)); }
  }


  // Back out of a trade. Deals fall through — someone goes quiet, a card turns out to be damaged,
  // people change their minds — and an app that won't let you cancel just leaves dead trades sitting
  // there forever, holding cards hostage in the lock.
  //
  // The awkward case is cancelling AFTER someone has confirmed receipt: their collection has already
  // changed, and no status flag can un-post a package. So say that plainly rather than implying a
  // clean undo.
  async function cancelTradeOffer(t) {
    if (!user || (t.fromUid !== user.uid && t.toUid !== user.uid)) return;

    const iAmSender  = t.fromUid === user.uid;
    const iReceived  = iAmSender ? t.fromReceived : t.toReceived;
    const theyGot    = iAmSender ? t.toReceived   : t.fromReceived;
    const anySettled = iReceived || theyGot;
    const accepted   = t.status === "accepted";

    let msg;
    if (anySettled) {
      // Cards have already moved in someone's collection. Cancelling does NOT reverse that.
      msg = "Cards have already changed hands in this trade.\n\n"
          + "Cancelling will close it, but it will NOT undo any collection changes or return anything "
          + "that has been posted. Sort that out between yourselves.\n\nClose this trade anyway?";
    } else if (accepted) {
      msg = "Cancel this agreed trade?\n\n"
          + "The other person has already accepted, and may have posted their cards. Make sure "
          + "you've spoken to them first.\n\nYour cards will be released back for trading.";
    } else {
      msg = "Withdraw this offer?";
    }
    if (!window.confirm(msg)) return;

    try {
      await setDoc(doc(db,"trade_offers",t.id),
        { status:"cancelled", cancelledBy:user.uid, cancelledAt:new Date().toISOString() },
        { merge:true });
      // Tell the other side. A trade vanishing with no explanation is worse than a decline.
      const other = iAmSender ? t.toUid : t.fromUid;
      await setDoc(doc(db,"market_notifs",uid()), {
        toUid: other, type:"trade_cancelled", tradeId:t.id,
        fromName: user.displayName || user.email,
        read:false, createdAt:new Date().toISOString(),
      });
      showToast("Trade cancelled");
    } catch(e) { alert("Couldn't cancel: " + e.message); }
  }

  async function saveTradeTracking(t, tracking) {
    if (!user) return;
    const iAmSender = t.fromUid === user.uid;
    try {
      await setDoc(doc(db,"trade_offers",t.id),
        { [iAmSender ? "fromTracking" : "toTracking"]: tracking.trim() }, { merge:true });
      showToast("Tracking saved");
    } catch(e) { alert("Couldn't save tracking: " + e.message); }
  }

  // Confirm YOUR package arrived. This is the only point at which cards actually change hands.
  //
  // Each side settles its OWN collection, because Firestore rules (rightly) forbid writing anyone
  // else's. So: add what I received, remove what I sent. The other person does the mirror when their
  // package lands. If they never do, my collection is still correct — I have what I have.
  async function confirmTradeReceived(t) {
    if (!user) return;
    const iAmSender = t.fromUid === user.uid;
    const iReceive  = iAmSender ? t.getting : t.giving;   // what's coming TO me
    const iGaveAway = iAmSender ? t.giving  : t.getting;  // what I posted OFF

    if (!window.confirm(
      `Confirm you received ${iReceive.length} card${iReceive.length===1?"":"s"}?\n\n` +
      `This adds them to your collection and removes the ${iGaveAway.length} card${iGaveAway.length===1?"":"s"} you sent.`
    )) return;

    try {
      const snap = await getDoc(doc(db,"boba_owned", user.uid));
      const next = snap.exists() ? {...snap.data()} : {};
      iReceive.forEach(c => { next[c.id] = (next[c.id] || 0) + 1; });
      iGaveAway.forEach(c => {
        const have = next[c.id] || 0;
        if (have > 1) next[c.id] = have - 1;
        else delete next[c.id];
      });
      await setDoc(doc(db,"boba_owned", user.uid), next);
      setOwned(next);

      // Flag my side done. When both sides are in, the trade is complete.
      const field = iAmSender ? "fromReceived" : "toReceived";
      const bothDone = iAmSender ? t.toReceived : t.fromReceived;
      await setDoc(doc(db,"trade_offers",t.id), {
        [field]: true,
        ...(bothDone ? { status:"complete", completedAt:new Date().toISOString() } : {}),
      }, { merge:true });

      showToast(bothDone ? "Trade complete!" : "Received \u2014 waiting on them to confirm theirs");
    } catch(e) { alert("Couldn't complete: " + e.message); }
  }

  async function buyNow(listing) {
    if (!user) { setSigningIn(true); return; }
    if (!listing.askingPrice) { alert("No asking price set on this listing."); return; }
    const now = new Date().toISOString();
    const threadId = uid();
    const saleId = uid();
    const sysText = "\u2705 Purchase complete! "+listing.cardName+" for $"+listing.askingPrice.toFixed(2)+(listing.paymentInfo?" | Payment: "+listing.paymentInfo:"");
    const sysMsg = {id:uid(),text:sysText,senderUid:"system",senderName:"System",sentAt:now};
    try {
      // Write deal thread (buyer creates - needs auth write permission on deal_threads)
      await setDoc(doc(db,"deal_threads",threadId),{
        id:threadId, memberUids:[listing.sellerUid,user.uid],
        sellerUid:listing.sellerUid, sellerName:listing.sellerName||"Seller",
        buyerUid:user.uid, buyerName:user.displayName||user.email||"Buyer",
        cardName:listing.cardName, cardImage:listing.cardImage||null, cardId:listing.cardId||"",
        agreedPrice:listing.askingPrice, status:"active",
        messages:[sysMsg],
        lastMessage:sysText, lastMessageAt:now, lastSenderUid:"system",
        lastReadBy:{[user.uid]:now}, createdAt:now,
      });
    } catch(e) { alert("Could not create deal thread: "+e.message); return; }
    try {
      // Log sale
      await setDoc(doc(db,"market_sales",saleId),{
        id:saleId, listingId:listing.id,
        cardId:listing.cardId||"", cardName:listing.cardName,
        cardTreatment:listing.cardTreatment||"", cardWeapon:listing.cardWeapon||"",
        cardImage:listing.cardImage||null, setName:listing.setName||"",
        price:listing.askingPrice, soldAt:now, soldDate:now.split("T")[0],
        sellerUid:listing.sellerUid, sellerName:listing.sellerName||"",
        buyerUid:user.uid, buyerName:user.displayName||user.email||"",
      });
    } catch(e) { alert("Could not log sale: "+e.message); return; }
    try {
      // Add card to buyer's own collection (buyer writing to their own doc = always allowed)
      if(listing.cardId){
        const buyerSnap=await getDoc(doc(db,"boba_owned",user.uid));
        const bo=buyerSnap.exists()?buyerSnap.data():{};
        const next={...bo,[listing.cardId]:(bo[listing.cardId]||0)+1};
        await setDoc(doc(db,"boba_owned",user.uid),next);
        setOwned(next);
      }
    } catch(e) { alert("Could not update your collection: "+e.message); return; }
    try {
      // Mark listing sold (buyer writing to marketplace doc - may need permissive rules)
      await setDoc(doc(db,"marketplace",listing.id),{status:"sold",soldAt:now,soldTo:user.uid,soldFor:listing.askingPrice},{merge:true});
    } catch(e) { alert("Could not close listing (check Firestore rules for marketplace): "+e.message); }
    // Navigate to thread
    setActiveTab("messages");
    setActiveThread({id:threadId,memberUids:[listing.sellerUid,user.uid],sellerUid:listing.sellerUid,sellerName:listing.sellerName||"Seller",buyerUid:user.uid,buyerName:user.displayName||"Buyer",cardName:listing.cardName,cardImage:listing.cardImage||null,agreedPrice:listing.askingPrice,status:"active",messages:[sysMsg],lastMessageAt:now,lastSenderUid:"system",lastReadBy:{[user.uid]:now},createdAt:now});
    showToast("Purchase complete!");
    if(listing.paymentInfo){
      setPaymentPopup({cardName:listing.cardName,price:listing.askingPrice,paymentInfo:listing.paymentInfo,sellerName:listing.sellerName});
    }
  }


  async function logNegotiationHistory(offer, action, amount) {
    try {
      await setDoc(doc(db,"negotiation_history",uid()), {
        rootOfferId: offer.parentOfferId||offer.id,
        offerId: offer.id,
        listingId: offer.listingId||"",
        cardName: offer.cardName,
        cardImage: offer.cardImage||null,
        action,
        amount: amount||offer.offerAmount||0,
        fromUid: user.uid,
        fromName: user.displayName||user.email||"Unknown",
        sellerUid: offer.sellerUid,
        buyerUid: offer.buyerUid,
        participantUids: [offer.sellerUid, offer.buyerUid].filter(Boolean),
        timestamp: new Date().toISOString(),
      });
    } catch(e) { console.warn("History log failed:", e); }
  }

  async function respondOffer(offer, action) {
    const now = new Date().toISOString();
    await setDoc(doc(db,"market_offers",offer.id),{status:action,notified:true,respondedAt:now},{merge:true});
    logNegotiationHistory(offer, action, offer.offerAmount||0);
    if (action==="accepted") {
      // 1. Close the listing
      await setDoc(doc(db,"marketplace",offer.listingId),{status:"sold",soldAt:now,soldTo:offer.buyerUid,soldFor:offer.offerAmount||0},{merge:true});
      // 2. Auto-decline all OTHER pending offers on this listing
      try {
        const othersSnap = await getDocs(query(collection(db,"market_offers"),where("listingId","==",offer.listingId),where("status","==","pending")));
        await Promise.all(othersSnap.docs.filter(d=>d.id!==offer.id).map(d=>setDoc(doc(db,"market_offers",d.id),{status:"declined",notified:true,respondedAt:now},{merge:true})));
      } catch(e){ console.warn("Auto-decline failed:",e); }
      // 3. Decrement seller owned qty by 1
      // Resolve cardId -- older offer docs may not have it, fall back to listing
      let cardId = offer.cardId;
      if (!cardId) {
        try {
          const listSnap = await getDoc(doc(db,"marketplace",offer.listingId));
          if (listSnap.exists()) cardId = listSnap.data().cardId;
        } catch(e){}
      }
      try {
        const sellerSnap = await getDoc(doc(db,"boba_owned",offer.sellerUid));
        if (sellerSnap.exists()&&cardId) {
          const so=sellerSnap.data(); const newQty=(so[cardId]||1)-1;
          const next={...so}; if(newQty<=0) delete next[cardId]; else next[cardId]=newQty;
          await setDoc(doc(db,"boba_owned",offer.sellerUid),next);
          if(user?.uid===offer.sellerUid) setOwned(next);
        }
      } catch(e){ console.warn("Seller qty failed:",e); }
      // 4. Add to buyer collection
      try {
        const buyerSnap = await getDoc(doc(db,"boba_owned",offer.buyerUid));
        const bo=buyerSnap.exists()?buyerSnap.data():{};
        if(cardId) {
          const next={...bo,[cardId]:(bo[cardId]||0)+1};
          await setDoc(doc(db,"boba_owned",offer.buyerUid),next);
          if(user?.uid===offer.buyerUid) setOwned(next);
        } else {
          console.warn("respondOffer: no cardId found, buyer collection not updated");
        }
      } catch(e){ console.warn("Buyer collection failed:",e); }
      // 5. Create deal thread
      const threadId=uid();
      await setDoc(doc(db,"deal_threads",threadId),{
        id:threadId, memberUids:[offer.sellerUid,offer.buyerUid],
        sellerUid:offer.sellerUid, sellerName:offer.sellerName||"Seller",
        buyerUid:offer.buyerUid, buyerName:offer.buyerName||"Buyer",
        cardName:offer.cardName, cardImage:offer.cardImage||null, cardId:offer.cardId||"",
        agreedPrice:offer.offerAmount||0, status:"active",
        lastMessage:"Deal accepted! Coordinate payment details here.",
        lastMessageAt:now, lastSenderUid:"system",
        lastReadBy:{[offer.sellerUid]:now}, createdAt:now,
      });
      await setDoc(doc(db,"deal_threads",threadId),{
        messages: arrayUnion({id:uid(),text:"\u2705 Deal agreed! "+offer.cardName+" for $"+(offer.offerAmount||0).toFixed(2)+(offer.paymentInfo?" | Payment: "+offer.paymentInfo:"")+" | Card added to buyer's collection automatically.",senderUid:"system",senderName:"System",sentAt:now}),
      },{merge:true});
      // 6. Log sale
      await setDoc(doc(db,"market_sales",uid()),{
        id:uid(), listingId:offer.listingId||"",
        cardId:offer.cardId||"", cardName:offer.cardName,
        cardTreatment:offer.cardTreatment||"", cardWeapon:offer.cardWeapon||"",
        cardPower:offer.cardPower||"", cardImage:offer.cardImage||null,
        setName:offer.setName||"", price:offer.offerAmount||0,
        soldAt:now, soldDate:now.split("T")[0],
        sellerUid:offer.sellerUid, sellerName:offer.sellerName||"",
        buyerUid:offer.buyerUid, buyerName:offer.buyerName||"",
      });
      // 7. Switch to Messages tab and open the thread
      showToast("Deal accepted! Opening chat to coordinate payment...");
      setActiveTab("messages");
      setActiveThread({
        id:threadId, memberUids:[offer.sellerUid,offer.buyerUid],
        sellerUid:offer.sellerUid, sellerName:offer.sellerName||"Seller",
        buyerUid:offer.buyerUid, buyerName:offer.buyerName||"Buyer",
        cardName:offer.cardName, cardImage:offer.cardImage||null,
        agreedPrice:offer.offerAmount||0, status:"active",
        lastMessage:"Deal accepted! Coordinate payment details here.",
        lastMessageAt:now, lastSenderUid:"system",
        lastReadBy:{[offer.sellerUid]:now}, createdAt:now,
      });
    }
  }

  async function counterOffer(offer, counterAmount) {
    if (!counterAmount || isNaN(parseFloat(counterAmount))) return;
    const now = new Date().toISOString();
    const amt = parseFloat(counterAmount);
    try {
      // Mark original offer as countered + dismiss from current user's bar
      await setDoc(doc(db,"market_offers",offer.id), {
        status: "countered", notified: true, respondedAt: now,
      }, {merge:true});
      // Write paper trail to negotiation_history collection
      try {
        await setDoc(doc(db,"negotiation_history",uid()), {
          rootOfferId: offer.parentOfferId||offer.id,
          offerId: offer.id,
          listingId: offer.listingId||"",
          cardName: offer.cardName,
          cardImage: offer.cardImage||null,
          action: "counter",
          amount: amt,
          previousAmount: offer.offerAmount||0,
          fromUid: user.uid,
          fromName: user.displayName||user.email||"Unknown",
          sellerUid: offer.sellerUid,
          buyerUid: offer.buyerUid,
          participantUids: [offer.sellerUid, offer.buyerUid].filter(Boolean),
          timestamp: now,
        });
      } catch(e) { console.warn("History write failed (non-fatal):", e); }
      // Create a NEW offer doc with roles flipped so it lands in the
      // other party's marketNotifs feed (they query where sellerUid==theirUid)
      const newOffId = uid();
      const iAmSeller = offer.sellerUid === user.uid;
      await setDoc(doc(db,"market_offers",newOffId), {
        id: newOffId,
        parentOfferId: offer.id,
        listingId: offer.listingId,
        cardId: offer.cardId||"",
        cardName: offer.cardName,
        cardImage: offer.cardImage||null,
        cardTreatment: offer.cardTreatment||"",
        cardWeapon: offer.cardWeapon||"",
        sellerUid:  offer.sellerUid,
        sellerName: offer.sellerName,
        buyerUid:   offer.buyerUid,
        buyerName:  offer.buyerName,
        awaitingUid: (offer.sellerUid === user.uid) ? offer.buyerUid : offer.sellerUid,   // the OTHER party responds next
        offerAmount: amt,
        isCounter: true,
        status: "pending",
        notified: false,
        createdAt: now,
      });
    } catch(e) {
      console.error("counterOffer error:", e);
    } finally {
      // Always show confirmation regardless of errors
      setCounterSent(true);
    }
  }


  // -- Thread messaging --
  useEffect(() => {
    if (!activeThread) { setThreadMsgs([]); return; }
    const unsub = onSnapshot(
      doc(db,"deal_threads",activeThread.id),
      snap => {
        if (snap.exists()) {
          const msgs = (snap.data().messages||[]).slice().sort((a,b)=>a.sentAt>b.sentAt?1:-1);
          setThreadMsgs(msgs);
        }
      }
    );
    if (user) setDoc(doc(db,"deal_threads",activeThread.id),{lastReadBy:{[user.uid]:new Date().toISOString()}},{merge:true});
    return ()=>unsub();
  }, [activeThread?.id]);

  async function sendMessage() {
    if (!newMsg.trim()||!activeThread||!user) return;
    const now = new Date().toISOString();
    const msg = {id:uid(), text:newMsg.trim(), senderUid:user.uid, senderName:user.displayName||user.email, sentAt:now};
    await setDoc(doc(db,"deal_threads",activeThread.id),{
      messages: arrayUnion(msg),
      lastMessage:msg.text, lastMessageAt:now, lastSenderUid:user.uid,
      lastReadBy:{[user.uid]:now},
    },{merge:true});
    setNewMsg("");
  }

  async function closeThread(threadId) {
    await setDoc(doc(db,"deal_threads",threadId),{status:"completed"},{merge:true});
    setActiveThread(null);
  }

  // -- Computed --
  const sets          = [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort();
  const filteredBySet = filterSet.size>0 ? cards.filter(c=>filterSet.has(c.setName)) : cards;
  // Canonicalize BEFORE deduping. The data holds both "FIRE" and "Fire", so mapping the raw
  // c.weapon into a Set kept both as separate filter options — while the filter itself compares
  // with canonWeapon(), so the list and the filter disagreed. Steel is Steel however it was typed.
  // sortWeapons() then orders them the way the game does rather than alphabetically.
  const weapons       = sortWeapons([...new Set(filteredBySet.map(c=>canonWeapon(c.weapon)).filter(Boolean))]);
  const treatments    = [...new Set(filteredBySet.map(c=>c.treatment).filter(Boolean))].sort();
  const powers        = [...new Set(filteredBySet.map(c=>Number(c.power)).filter(Boolean))].sort((a,b)=>b-a);

  // PERF: this filters AND sorts the full 31k-card checklist. It was re-running on every single
  // render — so every hover/flip in the grid re-filtered and re-sorted 31k cards. That was the
  // source of the laggy hover and rough scrolling. Memoized to its real inputs.
  // PERF: the search used to build + lowercase a string for EVERY one of the 31k cards on every
  // keystroke (~31k string allocations per character typed) — that's what made results lag behind
  // the input. Build the searchable text ONCE per card here; searching then becomes a plain
  // substring test against a ready-made string.
  const searchIndex = useMemo(() => {
    const m = new Map();
    for (const c of cards) {
      const hay = [c.hero,c.playName,c.cardNum,c.athlete,c.weapon,c.treatment,c.setName,c.variation,c.notation,c.power,synonymsFor(c)]
        .filter(Boolean).join(" ").toLowerCase();
      // Store a SECOND copy with all spacing/punctuation stripped. Matching against both makes
      // spacing irrelevant in either direction: "power glove" and "powerglove" both hit "Power
      // Glove", and "joecool" finds "Joe Cool". Without this, only the spaced form matched, because
      // "powerglove" is not a substring of "power glove".
      m.set(c.id, { hay, tight: hay.replace(/[\s\-_.'']/g, "") });
    }
    return m;
  }, [cards]);

  // Split the query once per search — not once per card. Each term is kept in both forms so it can
  // be tested against the spaced haystack or the stripped one.
  const searchTerms = useMemo(
    () => searchDebounced.toLowerCase().split(/\s+/).filter(Boolean)
            .map(t => ({ t, tight: t.replace(/[\s\-_.'']/g, "") })),
    [searchDebounced]
  );
  // A term matches if it appears in the normal text OR in the space-stripped text. The whole query
  // stripped of spaces is also tried, so "powerglove" matches even though it's one term and the
  // source is two words.
  const searchTight = useMemo(
    () => searchDebounced.toLowerCase().replace(/[\s\-_.'']/g, ""),
    [searchDebounced]
  );

  const filtered = useMemo(() => cards.filter(c=>{
    if(filterSet.size>0    && !filterSet.has(c.setName)) return false;
    if(filterSubSet && c.subSet!==filterSubSet)  return false;
    if(filterWeapon.size>0 && !filterWeapon.has(canonWeapon(c.weapon))) return false;
    if(filterTreat.size>0  && !filterTreat.has(c.treatment)) return false;
    if(filterOwned==="owned"   && !owned[c.id])  return false;
    // Proxy-only view. This is what makes the IMC print list possible: filter to proxies, then the
    // existing filtered export produces exactly the list of cards that need printing.
    if(filterOwned==="proxy"   && !anyProxy(c.id)) return false;   // any copy flagged
    // Kid collections: a card shows under a kid if ANY of its copies is tagged to them; it shows
    // under "Mine" if any copy is untagged. (You can own 2 and have one be Brooks' — so the same
    // card can legitimately appear under both.)
    if(kidFilter!=="all"){
      const qty = parseInt(owned[c.id]) || 0;
      if(!qty) return false;                              // only ever filters your own cards
      let match = false;
      for (let i=0; i<qty; i++) {
        const tag = kidAssign[`${c.id}#${i}`] ?? (i===0 ? (kidAssign[c.id] ?? null) : null);
        if (kidFilter==="mine" ? !tag : tag===kidFilter) { match = true; break; }
      }
      if(!match) return false;
    }
    if(filterOwned==="missing" &&  owned[c.id])  return false;
      // "Free to trade" vs "In a deck" — the availability question, answered against the same lock
      // the trade builder uses. Free = you own a copy that ISN'T committed to any deck (yours or
      // family). In a deck = you own it and every copy is spoken for.
      if(filterOwned==="free"){
        if(!owned[c.id]) return false;
        // Free = a copy that isn't in a deck, lent out, or borrowed. tradeLocked
        // folds all three together, so a lent or borrowed copy is never "free".
        if((owned[c.id]||0) <= (tradeLocked[c.id]||0)) return false;   // all copies spoken for
      }
      if(filterOwned==="indeck"){
        if(!owned[c.id]) return false;
        if((deckLockedForTrade[c.id]||0) === 0) return false;                 // none in a deck
      }
      // Borrowed = a copy I'm holding that belongs to someone else. Lent = a copy
      // of mine that's out with someone. Both read the per-copy lot lendState maps.
      if(filterOwned==="borrowed" && !(borrowedByCard[c.id]>0)) return false;
      if(filterOwned==="lent"     && !(lentByCard[c.id]>0))     return false;
    if(filterNoImg && c.imageUrl && String(c.imageUrl).startsWith("http")) return false;
    if(filterPower.size>0 && !filterPower.has(Number(c.power||0))) return false;
    // Multi-word search: every word must match SOMEWHERE on the card, in any order and across
    // different fields. So "gronk steel" finds hero=Gronk + weapon=Steel, and "gronk helmet"
    // finds hero=Gronk + treatment=...Helmet... A single substring match on the joined string
    // couldn't do this, because the words live in separate fields and never sit next to each other.
    if(searchTerms.length){
      const idx = searchIndex.get(c.id);
      if(!idx) return false;
      // Every term must match, but a term counts as matched if it appears in the normal text
      // OR in the space-stripped text. Also allow the ENTIRE query, spaces removed, to match
      // the stripped text — that is what lets a single token like "powerglove" find "Power
      // Glove", where no individual term would.
      if (searchTight && idx.tight.includes(searchTight)) return true;
      return searchTerms.every(t => idx.hay.includes(t.t) || idx.tight.includes(t.tight));
    }
    return true;
  // PERF: `owned` only changes the result when the Owned/Missing filter is active, so gate it —
  // otherwise every tap while adding cards re-filters the entire 31k checklist.
  }), [cards, filterSet, filterSubSet, filterWeapon, filterTreat, filterOwned, filterNoImg, filterPower, searchTerms, searchTight, searchIndex,
       kidFilter, kidAssign,
       (filterOwned === "owned" || filterOwned === "missing" || filterOwned === "free" || filterOwned === "indeck" || kidFilter !== "all") ? owned : null,
       (filterOwned === "free" || filterOwned === "indeck") ? deckLockedForTrade : null,
       filterOwned === "free" ? tradeLocked : null,
       filterOwned === "borrowed" ? borrowedByCard : null,
       filterOwned === "lent" ? lentByCard : null]);

  // PERF: THIS SORT WAS THE BOTTLENECK. It ran on every keystroke over the whole result set and
  // called localeCompare() inside the comparator — full Unicode collation, ~500k+ times on a big
  // list. It also re-ran parseFloat/parseInt/regex per comparison instead of once per card.
  //
  // Now: decorate each card with a precomputed primitive sort key, sort on that (plain number or
  // string compare), then undecorate. Same order, a fraction of the cost.
  const sorted = useMemo(() => {
    const keyed = filtered.map(c => {
      let k;
      if (sortBy === "power" || sortBy === "powerAsc") k = parseFloat(c.power) || 0;
      else if (sortBy === "cardNum") k = parseInt(String(c.cardNum||"").replace(/[^0-9]/g,"")||"0", 10);
      else k = String(c[sortBy] ?? "").toLowerCase();
      return { c, k };
    });
    const desc = (sortBy === "power");
    keyed.sort((x, y) => {
      if (x.k < y.k) return desc ? 1 : -1;
      if (x.k > y.k) return desc ? -1 : 1;
      return 0;
    });
    return keyed.map(o => o.c);
  }, [filtered, sortBy]);

  // Clamp at the slice, not just in the scroll handler — `page` can be restored from saved UI state
  // or set elsewhere, and if it ever exceeds the cap we'd paint the whole grid again.
  const visibleCards = useMemo(
    () => sorted.slice(0, Math.min(page, MAX_PAGES) * PAGE_SIZE),
    [sorted, page]
  );
  filteredLenRef.current = sorted.length;
  const _baseId = (k)=> String(k).replace(/::foil$/,"");
  // PERF: THIS WAS THE 389ms. `_ownedKeyValid` did a linear `cards.find()` through all ~36k cards
  // for EVERY owned key — and it ran twice, on EVERY render. With 332 owned cards that's ~24
  // million comparisons per keystroke, which is exactly why typing felt frozen even when zero
  // cards were on screen. Build an id Set once and make the lookup O(1).
  const cardIdSet = useMemo(() => new Set(cards.map(c=>c.id)), [cards]);
  // Kid chip counts — by COPY, not by card. If you own 2 of something and one is Brooks', that's
  // 1 for you and 1 for Brooks. Computed once per owned/assign change.
  const kidCounts = useMemo(() => {
    const m = { __mine: 0 };
    for (const cid of Object.keys(owned)) {
      const qty = parseInt(owned[cid]) || 1;
      for (let i=0; i<qty; i++) {
        const tag = kidAssign[`${cid}#${i}`] ?? (i===0 ? (kidAssign[cid] ?? null) : null);
        if (tag) m[tag] = (m[tag]||0) + 1;
        else m.__mine += 1;
      }
    }
    return m;
  }, [owned, kidAssign]);

  // cardId -> [tagForCopy0, tagForCopy1, ...]. Memoized so the grid can render each tile's kid
  // badge without recomputing per card per render.
  const kidTagsByCard = useMemo(() => {
    const m = {};
    if (kidGroups.length === 0) return m;
    for (const cid of Object.keys(owned)) {
      const qty = parseInt(owned[cid]) || 1;
      const arr = [];
      for (let i=0; i<qty; i++) {
        arr.push(kidAssign[`${cid}#${i}`] ?? (i===0 ? (kidAssign[cid] ?? null) : null));
      }
      m[cid] = arr;
    }
    return m;
  }, [owned, kidAssign, kidGroups]);
  // Counts what is actually YOURS. ownedNet is `owned` minus borrowed copies \u2014 a card you are
  // holding for someone else should never show up in your collection totals or set completion.
  const { uniqueOwned, totalOwned } = useMemo(() => {
    let uniq = 0, total = 0;
    for (const k of Object.keys(ownedNet)) {
      if (!cardIdSet.has(_baseId(k))) continue;
      uniq += 1;
      total += (ownedNet[k] || 1);
    }
    return { uniqueOwned: uniq, totalOwned: total };
  }, [ownedNet, cardIdSet]);
  const collectionValue = useMemo(() => {
    // Build avg sale price per cardId from marketSales
    const priceMap={};
    marketSales.forEach(s=>{
      if(!s.cardId||!s.price) return;
      if(!priceMap[s.cardId]) priceMap[s.cardId]={total:0,count:0};
      priceMap[s.cardId].total+=s.price;
      priceMap[s.cardId].count+=1;
    });
    // ownedNet: valuing a borrowed card would report someone else's property as your net worth.
    return Object.keys(ownedNet).reduce((sum,cid)=>{
      const qty=ownedNet[cid]||1;
      const base=String(cid).replace(/::foil$/,"");
      const avg=priceMap[base]?priceMap[base].total/priceMap[base].count:0;
      return sum+avg*qty;
    },0);
  }, [marketSales, ownedNet]);

  // -- Deck logic --
  // PERF: all of these were recomputing on every render (inDeck filters the full 36k checklist).
  const deckSet = useMemo(()=>new Set(deckCards), [deckCards]);
  const inDeck = useMemo(()=>cards.filter(c=>deckSet.has(c.id)), [cards, deckSet]);
  const isSpec=deckType==="spec"||deckType==="vegasbaby",isAM=deckType==="apexmadness";

  // ── LIVE LEGALITY ────────────────────────────────────────────────────────────────────────────
  // Blocking illegal ADDS isn't enough on its own: a deck can be illegal by what it's MISSING
  // (Spec+ without its 60-card core, GG HiLo without 10 of each insert). Check the whole deck
  // continuously so you always know where you stand before you turn up to play.
  const deckLegality = useMemo(() => {
    const F = fmtOf(deckType);
    const problems = [];
    if (inDeck.length === 0) return { ok:true, problems, empty:true };

    const total = inDeck.reduce((s,c)=>s+(parseFloat(c.power)||0), 0);

    // Deck size: F.size is the MAX. The MINIMUM required is F.coreSize when the format has
    // an optional bonus tier (Spec+: 60 core required, up to 70 total — the last 10 are an
    // unlock, not a requirement). Otherwise the deck must be exactly F.size.
    const SZ = F.size || 60;
    const MIN = F.coreSize || SZ;
    if (inDeck.length > SZ)
      problems.push(`${inDeck.length} cards — ${F.short} allows ${SZ}`);
    if (inDeck.length < MIN)
      problems.push(`${inDeck.length}/${MIN} cards — ${MIN} are required`);

    if (F.totalPower && total > F.totalPower)
      problems.push(`${total.toLocaleString()} total power — ${F.short} caps at ${F.totalPower.toLocaleString()}`);

    // Spec+ : the core is mandatory, not optional.
    if (F.coreSize && F.coreCap) {
      const core = inDeck.filter(c=>(parseFloat(c.power)||0) <= F.coreCap).length;
      const high = inDeck.length - core;
      if (high > 0 && core < F.coreSize)
        problems.push(`Spec+ core incomplete: ${core}/${F.coreSize} at ≤${F.coreCap}, but ${high} high-power Hero${high!==1?"es":""} in deck`);
    }

    // GG HiLo : every card must be in an allowed group, and each group has a minimum.
    if (F.groups) {
      const stray = inDeck.filter(c => !F.groups.some(g=>g.match(c))).length;
      if (stray > 0) problems.push(`${stray} card${stray!==1?"s":""} outside the allowed inserts`);
      for (const g of F.groups) {
        if (!g.min) continue;
        const have = inDeck.filter(c=>g.match(c)).length;
        if (have < g.min) problems.push(`${have}/${g.min} ${g.label}`);
      }
    }

    // Power Glove : every card unique.
    if (F.unique) {
      const ids = inDeck.map(c=>c.id);
      if (new Set(ids).size !== ids.length) problems.push("Duplicate cards — Power Glove requires all 60 to be unique");
    }

    return { ok: problems.length===0, problems, total, empty:false };
  }, [inDeck, deckType]);
  const dupKey=c=>`${(c.hero||"").toLowerCase()}|${(c.variation||"").toLowerCase()}|${c.power||""}|${(c.weapon||"").toLowerCase()}`;
  const inDeckDupKeys = useMemo(()=>new Set(inDeck.map(dupKey)), [inDeck]);
  const powerCount = useMemo(()=>{ const m={}; inDeck.forEach(c=>{const p=c.power||"0"; m[p]=(m[p]||0)+1;}); return m; }, [inDeck]);
  const treatCore={},treatApex={};
  if(isAM){inDeck.forEach(c=>{if(!isInsertTreatment(c))return;const t=insertKey(c),p=parseFloat(c.power||0);if(p<=160)treatCore[t]=(treatCore[t]||0)+1;if(p>160)treatApex[t]=(treatApex[t]||0)+1;});}
  // -- Tournament rule: a physical copy can't be in two decks at once. --
  // Count how many of the user's OTHER saved decks already commit each cardId (excluding the
  // deck currently being edited). A card locks only when other decks have used up every copy
  // you own — own 3 and up to 3 decks can each run one.
  // PERF: these were recomputing on EVERY render (every hover, flip, scroll) and iterating the
  // full family collections each time — that made the card grid janky. Memoized so they only
  // rebuild when their real inputs change.
  const otherDeckUse = useMemo(() => {
    // Count each card across every saved deck (one copy per deck max), then reconcile the deck that's
    // OPEN in the editor: swap its saved snapshot for the LIVE editor contents (deckCards). This is
    // the correct source of truth — using a stale deckLoadId to fully exclude a deck let a just-saved
    // deck's cards look free again, while ignoring the editor let a card removed on screen stay locked.
    const m = {};
    const editing = deckLoadId ? (savedDecks||[]).find(d => d.id === deckLoadId) : null;
    (savedDecks||[]).forEach(d => {
      // For the open deck, skip its saved snapshot here; we re-add its LIVE contents below.
      if (editing && d.id === deckLoadId) return;
      const seenInThisDeck = new Set();
      (d.cardIds||[]).forEach(cid => {
        if (seenInThisDeck.has(cid)) return;
        seenInThisDeck.add(cid);
        m[cid] = (m[cid]||0) + 1;
      });
    });
    // Add the open editor's current cards as that deck's contribution (live, not saved).
    if (editing) {
      const seen = new Set();
      (deckCards||[]).forEach(cid => {
        if (seen.has(cid)) return; seen.add(cid);
        m[cid] = (m[cid]||0) + 1;
      });
    }
    return m;
  }, [savedDecks, deckLoadId, deckCards]);

  // Cards of MINE that a FAMILY member has committed to one of THEIR decks. otherDeckUse only sees
  // my own decks, so without this, a card your father-in-law slotted into his deck still shows as
  // free in your builder. familyDecks holds each member's decks; we count my owned cards appearing
  // in them (one copy per deck, same rule as my own decks).
  const familyDeckUse = useMemo(() => {
    const m = {};
    (familyDecks||[]).forEach(group => {
      (group.decks||[]).forEach(d => {
        const seen = new Set();
        (d.cardIds||[]).forEach(cid => {
          if (seen.has(cid)) return;
          seen.add(cid);
          // A family member using THEIR OWN copy must not lock mine. Two people owning the same card
          // share one card id, so "this id is in Derrik's deck" said nothing about whose physical
          // card it was \u2014 and my copy got marked unavailable while his sat in his own deck.
          const theirs = (friendOwned[group.friendUid] || {})[cid];
          if (owned[cid] && !theirs) m[cid] = (m[cid]||0) + 1;
        });
      });
    });
    return m;
  }, [familyDecks, owned, friendOwned]);

  // Where each card is committed, so the builder can TELL you where a locked card lives instead of
  // just greying it out. Maps cardId -> [{ where:"mine"|"family", deck:"<deck name>", who:"<name>" }].
  const deckLocations = useMemo(() => {
    const m = {};
    const push = (cid, entry) => { (m[cid] = m[cid] || []).push(entry); };
    (savedDecks||[]).forEach(d => {
      const seen = new Set();
      (d.cardIds||[]).forEach(cid => { if(seen.has(cid))return; seen.add(cid); push(cid, { where:"mine", deck: d.name || "Untitled deck", who: "You" }); });
    });
    (familyDecks||[]).forEach(group => {
      (group.decks||[]).forEach(d => {
        const seen = new Set();
        (d.cardIds||[]).forEach(cid => {
          // Only surface family placements of cards I own (those are the ones that lock me out).
          if(seen.has(cid))return; seen.add(cid);
          // Same rule for the "where is it" label: only surface a family placement when it is
          // genuinely MY copy sitting in their deck.
          const theirsToo = (friendOwned[group.friendUid] || {})[cid];
          if(owned[cid] && !theirsToo) push(cid, { where:"family", deck: d.name || "Untitled deck", who: group.friendName || "Family" });
        });
      });
    });
    return m;
  }, [savedDecks, familyDecks, owned, friendOwned]);
  // Human-readable "where is this card" string for a locked card.
  const deckLocationText = (cid) => {
    const locs = deckLocations[cid] || [];
    if(!locs.length) return "";
    // Name whose COPY is committed, not just whose deck it sits in. When two people own the same
    // card these read identically otherwise \u2014 "in Alpha Trilogy Playmaker" on both sides, with no
    // way to tell which physical card is spoken for.
    return locs.map(l => l.where==="mine"
      ? `your copy is in your "${l.deck}"`
      : `your copy is in ${l.who}'s "${l.deck}"`).join(", ");
  };


  // ── AUTOMATIC COPY ASSIGNMENT ──────────────────────────────────────────────
  // A deck stores bare card IDs, so when you own two of the same card and both are
  // committed to decks, nothing recorded WHICH physical copy each deck holds. That
  // matters for proxies: flagging the vault copy as "needs a proxy" was making the
  // OTHER deck — which holds the copy you physically have — also demand a proxy.
  //
  // Rule from the game: one copy per deck, and a physical card can only be in one
  // deck at a time. So copies are handed out deterministically in deck age order:
  // the OLDEST deck using a card gets copy 0, the next gets copy 1, and so on.
  //
  // Age order (not the display order, which is newest-first) is deliberate: it means
  // creating or deleting a NEWER deck never reshuffles the assignments of older ones.
  // Deleting an old deck does shift things down, which is the honest behaviour —
  // that copy genuinely became free.
  //
  // Returns { "cardId|deckId": copyIndex }.
  const deckCopyIndex = useMemo(() => {
    const byAge = (savedDecks || []).slice().sort((a,b) =>
      String(a.savedAt||"").localeCompare(String(b.savedAt||"")) ||
      String(a.id||"").localeCompare(String(b.id||""))
    );
    const nextIdx = {};   // cardId -> next copy index to hand out
    const out = {};
    byAge.forEach(d => {
      // The deck open in the editor contributes its LIVE contents, not its saved
      // snapshot — same reasoning as otherDeckUse above.
      const ids = (deckLoadId && d.id === deckLoadId) ? (deckCards || []) : (d.cardIds || []);
      const seen = new Set();
      ids.forEach(cid => {
        if (seen.has(cid)) return;      // a deck can only hold one copy of a card
        seen.add(cid);
        const i = nextIdx[cid] || 0;
        out[`${cid}|${d.id}`] = i;
        nextIdx[cid] = i + 1;
      });
    });
    return out;
  }, [savedDecks, deckLoadId, deckCards]);

  // Which copy the given deck holds of a card. Unsaved/new decks fall back to the
  // first copy not already claimed by a saved deck.
  // useMemo rather than useCallback purely because useCallback is not imported in
  // this file — same effect, one stable function identity per deckCopyIndex.
  const copyForDeck = useMemo(() => (cardId, deckId) => {
    if (deckId) {
      const k = deckCopyIndex[`${cardId}|${deckId}`];
      if (k !== undefined) return k;
    }
    let used = 0;
    Object.keys(deckCopyIndex).forEach(key => {
      if (key.slice(0, key.lastIndexOf("|")) === cardId) used++;
    });
    return used;
  }, [deckCopyIndex]);

  // Same as otherDeckUse but counting EVERY saved deck, including the one open in the editor.
  // The quick builder is proposing a brand-new deck, so a card sitting in the currently-loaded
  // deck is just as unavailable as one in any other deck. (otherDeckUse deliberately excludes the
  // open deck, because the manual builder must still show you the cards already in it.)
  const allDeckUse = useMemo(() => {
    const m = {};
    (savedDecks||[]).forEach(d => {
      const seen = new Set();
      (d.cardIds||[]).forEach(cid => {
        if (seen.has(cid)) return;
        seen.add(cid);
        m[cid] = (m[cid]||0) + 1;
      });
    });
    return m;
  }, [savedDecks]);

  // Which family member to borrow from in the deck builder ("all" or a specific uid).
  // Declared here because the family-merge memos below reference it.
  const [deckFamilyMember, setDeckFamilyMember] = useState("all");
  // Family cards you can borrow, merged for the deck builder. A family copy surfaces when EITHER
  // you don't own the card, OR you own it but all your copies are committed to other decks — so
  // borrowing genuinely adds capacity. familyOwnerByCard: cardId -> {uid,name,copies}.
  const { familyOwnerByCard, deckOwnedMerged } = useMemo(() => {
    const fam = {};
    const merged = { ...owned };
    Object.entries(familyAvail).forEach(([famUid, info]) => {
      // If a specific family member is selected, only their cards are borrowable.
      if (deckFamilyMember !== "all" && famUid !== deckFamilyMember) return;
      Object.entries(info.cards||{}).forEach(([cid, copies]) => {
        if (fam[cid]) return; // first available family owner wins
        const myCopies = (owned && owned[cid]) ? (parseInt(owned[cid])||1) : 0;
        const myFree = Math.max(0, myCopies - (otherDeckUse[cid]||0));
        // How many copies of this card I've ALREADY borrowed into my own decks. familyAvail's
        // `copies` only nets out the OWNER's deck usage, not mine — so without this subtraction, a
        // family card I already slotted into one of my decks would keep showing as available to
        // borrow again. My borrow count = my deck usage beyond my own owned copies.
        const myUseInDecks = allDeckUse[cid] || 0;
        const myBorrowsUsed = Math.max(0, myUseInDecks - myCopies);
        const famFreeToLend = Math.max(0, copies - myBorrowsUsed);
        // Surface the family copy only if I have no free copy of my own AND there's an unborrowed
        // family copy left.
        if (myFree <= 0 && famFreeToLend > 0) {
          fam[cid] = { uid: famUid, name: info.ownerName, copies: famFreeToLend };
          merged[cid] = (merged[cid]||0) + famFreeToLend;
        }
      });
    });
    return { familyOwnerByCard: fam, deckOwnedMerged: merged };
  }, [owned, familyAvail, otherDeckUse, allDeckUse, deckFamilyMember]);

  // PROVENANCE (not availability): who OWNS each card, regardless of whether it's still free to lend.
  // familyOwnerByCard drops a card once it's been borrowed into a deck (correct for availability), but
  // the pick list needs to show "Derrik owns this" even after it's committed. This map never nets out
  // borrows — it's purely "does a family member's collection contain this card id."
  const familyOwnsCard = useMemo(() => familyOwnedRaw || {}, [familyOwnedRaw]);
  function canAddToDeck(c){
    const F = fmtOf(deckType);                    // this format's rules — see DECK_FORMATS
    const SIZE = F.size || 60;
    const PER  = F.perPower || 6;
    const p = parseFloat(c.power||0);

    if(deckSet.has(c.id))return{ok:false,reason:"Already in deck"};
    if(inDeck.length>=SIZE)return{ok:false,reason:`Deck full (${SIZE} max)`};
    // Power Glove needs every card to be unique, so a duplicate card is out regardless.
    if(F.unique && deckSet.has(c.id))return{ok:false,reason:"Power Glove: every card must be unique"};
    if(inDeckDupKeys.has(dupKey(c)))return{ok:false,reason:"Duplicate card"};

    // Cross-deck copy lock. Keep YOUR copies and BORROWABLE family copies separate — merging them
    // (as deckOwnedMerged does) let a card you own exactly one of, and have already slotted into a
    // saved deck, still look addable just because a relative also owns one. A copy already committed
    // to ANY deck (yours or a relative's) is spent and can't be added again.
    // Copies I physically HAVE, which is not the same as copies I own: a card lent to someone else
    // is still mine but is not in my hands, so it cannot go in a deck. A borrowed copy is the
    // reverse \u2014 not mine, but here, so it counts. Only loans with an actual per-copy record are
    // subtracted; partial data must never hide a card you really do have.
    const _lentOut   = (lots||[]).filter(l => l.cardId === c.id && l.lendState === "lent").length;
    const _myOwned   = Math.max(0, ((owned && owned[c.id]) ? (parseInt(owned[c.id]) || 0) : 0) - _lentOut);
    // My copies that are free = what I own, minus copies committed to another of MY decks
    // (otherDeckUse) AND minus copies a FAMILY member has put in one of THEIR decks (familyDeckUse
    // only ever counts cards I own). If Derrik slots my card into his deck, it's spoken for.
    const _myFree    = Math.max(0, _myOwned - (otherDeckUse[c.id] || 0) - (familyDeckUse[c.id] || 0));
    // Family copies genuinely free to borrow. familyOwnerByCard only surfaces a lender when you had
    // no free copy of your own, and its `copies` is already the borrowable remainder.
    const _famFree   = (familyOwnerByCard && familyOwnerByCard[c.id]) ? (familyOwnerByCard[c.id].copies || 0) : 0;
    const _totalFree = _myFree + _famFree;
    // Is this card one that belongs to me or a family member at all? (owned by me, committed by me to
    // decks, owned/committed by family). If so and there's no free copy left anywhere, it's spent.
    const _relevant  = _myOwned > 0 || (otherDeckUse[c.id]||0) > 0 || (familyDeckUse[c.id]||0) > 0 || (allDeckUse[c.id]||0) > 0 || _famFree > 0;
    if(_relevant && _totalFree <= 0){
      const _loc = deckLocationText(c.id);
      // Name the loan explicitly. "In a deck" would be wrong and confusing when the real reason the
      // card is unavailable is that someone else has it.
      if (_lentOut > 0 && _myOwned <= 0) {
        const who = (lots||[]).find(l => l.cardId === c.id && l.lendState === "lent")?.lendWho;
        return {ok:false, reason: who ? `Lent to ${who}` : "Lent out"};
      }
      return {ok:false, reason: _loc ? `In ${_loc}` : ((familyDeckUse[c.id]||0) > 0 ? "In a deck (yours or family)" : _myOwned===1 ? "In another deck" : `All ${_myOwned} copies are in other decks`)};
    }

    // ── Format restrictions ──────────────────────────────────────────────────────────────────
    if(F.set && c.setName !== F.set)
      return {ok:false, reason:`${F.short}: ${F.set} cards only`};
    if(F.excludeSets && F.excludeSets.includes(c.setName))
      return {ok:false, reason:`${F.short}: no Trainer cards`};
    if(F.treatment && c.treatment !== F.treatment)
      return {ok:false, reason:`${F.short}: ${F.treatment} only`};
    if(F.weapon && canonWeapon(c.weapon) !== canonWeapon(F.weapon))
      return {ok:false, reason:`${F.short}: ${F.weapon} weapon only`};
    if(F.groups && !F.groups.some(g=>g.match(c)))
      return {ok:false, reason:`${F.short}: only ${F.groups.map(g=>g.label).join(", ")}`};
    if(F.powerCap && p > F.powerCap)
      return {ok:false, reason:`Power ${c.power} exceeds ${F.powerCap}`};

    // Per-power cap (6 normally, 3 for Blast).
    if((powerCount[c.power||"0"]||0) >= PER)
      return {ok:false, reason:`Already ${PER} at power ${c.power}`};

    // ELITE — the deck's SUMMED power can't exceed 8,250.
    if(F.totalPower){
      const used = inDeck.reduce((s,x)=>s+(parseFloat(x.power)||0),0);
      if(used + p > F.totalPower)
        return {ok:false, reason:`Would exceed ${F.totalPower.toLocaleString()} total power (${used.toLocaleString()} used, this is ${c.power})`};
    }

    // SPEC+ — the 60-card ≤160 core is REQUIRED and must be complete before any high Hero counts.
    // Above the core, each power level has a hard cap (165 ×2, 170 ×2, one each of 175–200).
    if(F.coreSize && F.highTiers){
      if(p > F.coreCap){
        const coreHave = inDeck.filter(x=>(parseFloat(x.power)||0) <= F.coreCap).length;
        if(coreHave < F.coreSize)
          return {ok:false, reason:`Spec+ needs a full ${F.coreSize}-card ≤${F.coreCap} core first (${coreHave}/${F.coreSize})`};
        const cap = F.highTiers[p];
        if(!cap) return {ok:false, reason:`Spec+ doesn't allow power ${c.power}`};
        const atThisPower = inDeck.filter(x=>(parseFloat(x.power)||0) === p).length;
        if(atThisPower >= cap)
          return {ok:false, reason:`Spec+ allows only ${cap} at power ${c.power}`};
      }
    }

    // MADNESS — Apex Heroes (>160) only go in once you've unlocked a slot: every 10 matching
    // Inserts in the deck unlocks one (max 6), plus four different Foil Hot Dogs unlock four more.
    if(deckType==="apexmadness" && p > 160){
      const tally = {};
      inDeck.filter(x=>(parseFloat(x.power)||0)<=160 && isInsertTreatment(x)).forEach(x=>{ const t=insertKey(x)||"—"; tally[t]=(tally[t]||0)+1; });
      const insertUnlocks = Math.min(6, Object.values(tally).reduce((n,v)=>n+Math.floor(v/10), 0));
      // Foil Hot Dogs are entered by hand (the foilDogs count). Auto-detecting them by treatment was
      // wrong twice over: they don't carry a distinct "foil" treatment, and they don't have to differ
      // from each other \u2014 so counting distinct "hot dog" treatments collapsed four identical dogs to a
      // single slot. A clamped manual count is both correct and simpler.
      const foilHotDogs = Math.max(0, Math.min(4, parseInt(foilDogs) || 0));
      const slots = insertUnlocks + Math.min(4, foilHotDogs);
      const apexIn = inDeck.filter(x=>(parseFloat(x.power)||0)>160).length;
      if(apexIn >= slots){
        return {ok:false, reason: slots===0
          ? "No Apex slots unlocked — 10 matching Inserts unlocks one"
          : `All ${slots} Apex slots used (${insertUnlocks} from Inserts + ${Math.min(4,foilHotDogs)} from Foil Hot Dogs)`};
      }
    }
    const _committed = (otherDeckUse[c.id] || 0) + (familyDeckUse[c.id] || 0);
    return{ok:true, free:_totalFree, copies:(_myOwned + _famFree), committed:_committed};
  }
  // PERF: filters AND sorts the full 36k checklist — was running on every render of the whole
  // collector app, including while you type in the card database.
  const deckAvail = useMemo(() => cards.filter(c=>{
    if(deckSet.has(c.id))return false;
    // Format restrictions — cards that could never be legal in this format shouldn't be offered
    // at all. Blocking them only on click means scrolling a list that's mostly unusable.
    const F = fmtOf(deckType);
    if(F.set && c.setName !== F.set) return false;                                 // Blast
    if(F.excludeSets && F.excludeSets.includes(c.setName)) return false;           // Elite: no Trainers
    if(F.treatment && c.treatment !== F.treatment) return false;                   // Power Glove
    if(F.weapon && canonWeapon(c.weapon) !== canonWeapon(F.weapon)) return false;  // Brawl
    if(F.groups && !F.groups.some(g=>g.match(c))) return false;                    // GG HiLo
    if(F.powerCap && (parseFloat(c.power)||0) > F.powerCap) return false;          // Spec
    // Spec+ has no card above 200 at any point.
    if(F.highTiers){
      const p = parseFloat(c.power)||0;
      if(p > F.coreCap && !F.highTiers[p]) return false;
    }
    if(deckFilterW.size&&!deckFilterW.has(canonWeapon(c.weapon)))return false;   // multi-select
    if(deckFilterP.size>0&&!deckFilterP.has(String(c.power||"")))return false;
    if(deckFilterS.size&&!deckFilterS.has(c.setName))return false;               // multi-select
    if(deckFilterT.size&&!deckFilterT.has(c.treatment))return false;             // multi-select
    if(deckSearch&&!`${c.hero} ${c.cardNum} ${c.treatment}`.toLowerCase().includes(deckSearch.toLowerCase()))return false;
    const t=(c.treatment||"").toLowerCase();
    if(t==="plays"||t==="bonus plays"||t==="home team discount")return false;
    return true;
  }).sort((a,b)=>(parseFloat(b.power)||0)-(parseFloat(a.power)||0)),
  [cards, deckSet, deckFilterW, deckFilterP, deckFilterS, deckFilterT, deckSearch]);

  // -- "How close am I to a deck?" — from OWNED cards, respecting deck type + weapon/treatment filter --
  function computeDeckProgress(weaponFilter, treatmentFilter, dtype, setFilter, maxMode, source="both") {
    // weaponFilter / treatmentFilter are Sets. Empty (or absent) means "no restriction".
    // Tolerate a bare string too, so any older call site still behaves.
    const asSet = v => v instanceof Set ? v : (v ? new Set([v]) : new Set());
    const wSet = asSet(weaponFilter), tSet = asSet(treatmentFilter);
    const weaponOk = c => wSet.size===0 || wSet.has(canonWeapon(c.weapon));
    // WEAPON PRIORITY. Selecting several weapons used to be a flat "any of these" filter, after which
    // cards were ordered purely by power — so if your Gum is stronger than your Glow, a Glow+Gum goal
    // built a mostly-Gum deck. That's backwards: the extra weapons are there to TOP UP the first one,
    // not compete with it. A Set preserves insertion order, so the order you picked them IS the
    // priority: exhaust weapon #1, then #2, and so on. Lower rank = higher priority.
    const wPriority = Array.from(wSet);
    const weaponRank = c => {
      if (wPriority.length === 0) return 0;              // no goal weapons — everything ties
      const i = wPriority.indexOf(canonWeapon(c.weapon));
      return i === -1 ? wPriority.length : i;            // unlisted weapons sort last
    };
    // Use in place of a bare power sort whenever a goal weapon order exists.
    const weaponFirst = (a,b) => weaponRank(a) - weaponRank(b);
    const treatOk  = c => tSet.size===0 || tSet.has(c.treatment);
    const isPlayCard = c => { const t=(c.treatment||"").toLowerCase(); return t==="plays"||t==="bonus plays"||t==="home team discount"; };
    const F = fmtOf(dtype);                       // this format's rules — see DECK_FORMATS
    const DECK_SIZE  = F.size || 60;
    const PER_POWER  = F.perPower || 6;
    const specCap    = !!F.powerCap;              // Spec's ≤160 ceiling
    const isApexType = (dtype==="apex");
    const isMadness  = (dtype==="apexmadness");
    // MAX DECK: enforce the format's real legal power window instead of just taking your top 60.
    //   Max Apex → 155+ only (155–200, plus 250s). Never pads with sub-155 cards.
    //   Max Spec → 115–160 only (hard floor at 115).
    //   Max Madness → same as Spec: the 60-card core is 115–160 only. In REGULAR mode the core is
    //     just "≤160" with no floor, so a thinner collection can still field a legal deck.
    // If you can't fill 60 inside the window, the deck comes up short (we surface the gap).
    const inMaxWindow = c => {
      if(!maxMode) return true;
      const p = parseFloat(c.power)||0;
      if(isApexType) return p >= 155;
      if(isMadness)  return (p >= 115 && p <= 160) || p > 160;  // core floor; apex heroes still allowed
      if(specCap)    return p >= 115 && p <= (F.powerCap||160);
      return true; // max mode has no defined window for other types
    };
    const maxWindowLabel = !maxMode ? "" : isApexType ? "155+" : isMadness ? "115–160 core" : specCap ? `115–${F.powerCap||160}` : "";
    const powerOf = c => parseFloat(c.power)||0;
    // Ownership now spans YOUR cards + FAMILY cards you can borrow.
    // Card source: "mine" = only my cards, "family" = only borrowable family cards,
    // "both" = mine first, family fills the gaps (the default).
    const _srcMine   = (source !== "family");
    const _srcFamily = (source !== "mine");
    const isMine = c => _srcMine ? (owned[c.id] || owned[c.id+"::foil"]) : false;
    const famOwner = c => {
      if (!_srcFamily) return null;
      // In "family only" mode we surface their cards regardless of whether I also own it.
      if (source === "family") return familyOwnerByCard[c.id] || null;
      return (!isMine(c) && familyOwnerByCard[c.id]) ? familyOwnerByCard[c.id] : null;
    };
    const isOwned = c => isMine(c) || !!famOwner(c);
    // Cross-deck lock: available copies = your copies + borrowable family copies, minus copies
    // already committed to OTHER decks. Prefer your own; family fills the gap.
    // Uses allDeckUse (not otherDeckUse): the quick builder proposes a NEW deck, so a card sitting
    // in the deck currently open in the editor is unavailable too.
    const isAvailable = c => {
      // Keep YOUR copies and BORROWABLE family copies separate (same rule as the manual builder's
      // canAddToDeck): a copy already committed to any deck is spent. Merging the two totals would
      // let a card you own one of — and already used in a saved deck — look available just because
      // a relative also owns one.
      const myOwned = (_srcMine && owned && owned[c.id]) ? (parseInt(owned[c.id])||1) : 0;
      // My free copies = owned minus what's in any of MY saved decks (allDeckUse) minus what a
      // family member has committed to THEIR deck (familyDeckUse counts only cards I own).
      const myFree  = Math.max(0, myOwned - (allDeckUse[c.id] || 0) - (familyDeckUse[c.id] || 0));
      const famFree = famOwner(c) ? (famOwner(c).copies||1) : 0;         // famOwner already nets out the relative's own deck usage
      return (myFree + famFree) > 0;
    };
    // Tiebreak sorter: same-priority cards ordered YOURS first, then family.
    const mineFirst = (a,b) => (isMine(b)?1:0) - (isMine(a)?1:0);
    // Power preference: Captain builds want the HIGHEST power first; Coach (lowPower) the LOWEST.
    const powerCmp = (a,b) => F.lowPower ? (powerOf(a)-powerOf(b)) : (powerOf(b)-powerOf(a));

    // ── APEX MADNESS: SMART structured build ──
    // Work backwards from your best apex cards. For each apex (highest power first), check if you
    // own 10 core (<160) of that same insert to unlock it. If yes, lock that insert in (10 core + apex).
    // If no, skip to your next-best apex. Stop at 6 fully-unlockable inserts. Never pad with partials.
    // Then each owned FOILED HOT DOG adds 1 more apex from any insert (highest left), capped at 4. Ceiling 70.
    if (dtype === "apexmadness") {
      const base = cards.filter(c => isOwned(c) && isAvailable(c) && !isPlayCard(c)
        && weaponOk(c)
        && (!setFilter || setFilter.size===0 || setFilter.has(c.setName)));
      const insertOf = c => (c.treatment || "—");

      // Pre-group core (<160) and apex (>160) by insert.
      const coreByInsert = {}; const apexByInsert = {};
      base.forEach(c => {
        const t = insertOf(c);
        if (powerOf(c) > 160) (apexByInsert[t] = apexByInsert[t]||[]).push(c);
        else (coreByInsert[t] = coreByInsert[t]||[]).push(c);
      });

      // Helper: pick up to N unique core for an insert (dedup, max 6 per power level).
      const pickCore = (insert, n, usedKeys) => {
        const list = (coreByInsert[insert]||[]).slice().sort((a,b)=>(powerOf(b)-powerOf(a))||mineFirst(a,b));
        const seen=new Set(); const perPower={}; const picked=[];
        for (const c of list) {
          const k=dupKey(c); if(seen.has(k)||usedKeys.has(k)) continue;
          const p=String(c.power||"0"); if((perPower[p]||0)>=6) continue;
          seen.add(k); perPower[p]=(perPower[p]||0)+1; picked.push(c);
          if(picked.length>=n) break;
        }
        return picked;
      };
      // Count how many unique core an insert *could* field (for the "closest" hint).
      const coreAvail = insert => pickCore(insert, 999, new Set()).length;

      // Rank all apexes best-first (used for hot-dog slots + tiebreaks). Each apex points at its insert.
      const allApex = base.filter(c=>powerOf(c)>160).sort((a,b)=>(powerOf(b)-powerOf(a))||mineFirst(a,b));

      const chosen = [];
      const usedKeys = new Set();
      const apexUsed = new Set();
      const completedInserts = [];   // {insert, apexPower}
      const lockedInserts = new Set();

      // MADNESS — the quick build. The rule, plainly: a 60-card core where every card is 115-160
      // power (Spec-style), and every complete block of 10 cards from ONE INSERT unlocks one Apex
      // Hero (>160) FROM THAT SAME INSERT. Max 6 insert unlocks.
      //
      // Naively taking the strongest 60 spreads them thin across many inserts and completes few (or
      // no) blocks of 10 — a strong core that unlocks nothing. So the build is insert-aware:
      //   1. Work out which inserts you could actually complete a block of 10 in, AND have a
      //      matching Apex Hero for (a block with no apex to claim is worth nothing here).
      //   2. Seat those blocks first, strongest inserts first, up to 6 blocks / 60 cards.
      //   3. Fill any leftover core slots with your strongest remaining legal cards.
      //   4. Claim one matching-insert Apex per completed block.
      // Foil Hot Dog unlocks are deliberately NOT part of the quick build — they're a manual-builder
      // addition (see the coach note), since they depend on foils you own rather than the deck.
      // Core power window. MAX mode enforces the competitive 115–160 core (same as Max Spec);
      // REGULAR mode is just "≤160" with no floor, so a thinner collection can still field 60.
      const CORE_MIN = maxMode ? 115 : 0, CORE_MAX = 160, BLOCK = 10, MAX_UNLOCKS = 6;
      const inRange = c => { const p = powerOf(c); return p >= CORE_MIN && p <= CORE_MAX; };

      // Pool the eligible core cards and the apex heroes, both grouped by insert.
      const coreByIns = {}, apexByIns = {};
      base.forEach(c => {
        // Plain "Battlefoil" is a base parallel, not an insert — it can't earn an unlock or be one
        // of the six inserts the core is drawn from, so keep it out of the pools entirely.
        if (!isInsertTreatment(c)) return;
        const k = insertKey(c) || "—";
        if (inRange(c)) (coreByIns[k] = coreByIns[k]||[]).push(c);
        else if (powerOf(c) > CORE_MAX) (apexByIns[k] = apexByIns[k]||[]).push(c);
      });
      Object.values(coreByIns).forEach(a => a.sort((x,y)=>(powerOf(y)-powerOf(x))||mineFirst(x,y)));
      Object.values(apexByIns).forEach(a => a.sort((x,y)=>(powerOf(y)-powerOf(x))||mineFirst(x,y)));

      // THE REAL CONSTRAINT: the whole 60-card core must come from AT MOST 6 inserts, and every
      // complete block of 10 within an insert unlocks one matching-insert Apex. So this is not
      // "seat some blocks then fill from anywhere" — filling from anywhere scattered the core over
      // a dozen inserts, most of which contributed nothing. Instead: pick the best <=6 inserts that
      // can collectively seat 60, and draw the ENTIRE core from only those.
      //
      // How many cards can an insert really seat? Not its raw count — seating dedups by
      // hero+power+weapon AND caps 6 per power level, so a 14-card insert may only seat 7.
      const depthOf = (k) => {
        const seenK = new Set(), perP = {};
        let n = 0;
        for (const c of (coreByIns[k]||[])) {
          const dk = dupKey(c); if (seenK.has(dk)) continue;
          const pk = String(c.power||"0");
          if ((perP[pk]||0) >= (F.perPower||6)) continue;
          seenK.add(dk); perP[pk] = (perP[pk]||0)+1; n++;
        }
        return n;
      };
      // Rank inserts by what they're worth to this deck: how many complete blocks of 10 they can
      // yield (that's unlocks), then raw depth (fills the core), then the apex you'd win.
      const ranked = Object.keys(coreByIns)
        .map(k => {
          const depth = depthOf(k);
          const apexAvail = (apexByIns[k]||[]).length;
          // An insert can contribute at most 10 core cards no matter how deep your collection runs,
          // so "usable" is what actually counts toward the 60. Depth beyond 10 is dead weight here.
          const usable = Math.min(depth, BLOCK);
          return {
            insert: k, depth, usable, apexAvail,
            complete: usable >= BLOCK && apexAvail > 0,   // a full 10 with an apex to claim
            bestApex: (apexByIns[k]||[])[0] ? powerOf(apexByIns[k][0]) : 0,
          };
        })
        .filter(x => x.depth > 0)
        // Prefer inserts that can complete a full 10 AND have an apex waiting; then by how much of
        // the core they can fill; then by the apex you'd win.
        .sort((a,b)=>(Number(b.complete)-Number(a.complete))||(b.usable-a.usable)||(b.bestApex-a.bestApex));

      // The core is 60 cards from at most 6 inserts, and each insert caps at 10 — so a full deck is
      // exactly 6 inserts contributing 10 apiece. Take the best 6 that can reach 60 between them.
      const picked = [];
      let covered = 0;
      for (const r of ranked) {
        if (picked.length >= MAX_UNLOCKS) break;
        picked.push(r); covered += r.usable;
        if (covered >= 60) break;              // 6 x 10 — nothing more can be added legally
      }
      const pickedKeys = new Set(picked.map(x=>x.insert));

      const perPowerAM = {};
      const perInsAM = {};   // core cards seated per insert — HARD CAP of 10 (the apex makes 11)
      const seat = (c) => {
        const k = dupKey(c); if (usedKeys.has(k)) return false;
        const pk = String(c.power||"0"); if ((perPowerAM[pk]||0) >= (F.perPower||6)) return false;
        // An insert tops out at 10 core cards. Beyond that the cards are illegal, not just wasted —
        // the top-up used to pour 16 of one insert into the deck. 6 inserts x 10 = the full 60.
        const ik = insertKey(c) || "—";
        if ((perInsAM[ik]||0) >= BLOCK) return false;
        usedKeys.add(k); perPowerAM[pk] = (perPowerAM[pk]||0)+1;
        perInsAM[ik] = (perInsAM[ik]||0)+1;
        chosen.push(c); return true;
      };

      // 2. Seat complete blocks of 10 first, so unlocks are locked in before the core fills up.
      //    Ordered by the apex won, so the best heroes are the ones that get claimed.
      const blockOrder = picked.filter(x=>x.complete).sort((a,b)=>b.bestApex-a.bestApex);
      for (const cand of blockOrder) {
        if (completedInserts.length >= MAX_UNLOCKS) break;
        if (chosen.length + BLOCK > 60) break;
        const staged = [];
        for (const c of (coreByIns[cand.insert]||[])) {
          if (staged.length >= BLOCK) break;
          const k = dupKey(c); if (usedKeys.has(k)) continue;
          const pk = String(c.power||"0");
          const projected = (perPowerAM[pk]||0) + staged.filter(x=>String(x.power||"0")===pk).length;
          if (projected >= (F.perPower||6)) continue;
          staged.push(c);
        }
        if (staged.length < BLOCK) continue;
        staged.forEach(seat);
        const apex = (apexByIns[cand.insert]||[]).find(a => !apexUsed.has(dupKey(a)));
        completedInserts.push({ insert: insertLabel(cand.insert, cand.insert), apexPower: apex?powerOf(apex):0, apexCard: apex||null });
      }

      // 3. Top the core up to 60 — but ONLY from the inserts we picked. This is the fix: pulling
      //    from any insert here is what scattered the core and wasted slots.
      const restCore = base
        .filter(c => inRange(c) && pickedKeys.has(insertKey(c) || "—"))
        .sort((a,b)=>(powerOf(b)-powerOf(a))||mineFirst(a,b));
      for (const c of restCore) { if (chosen.length >= 60) break; seat(c); }

      // 3b. REPAIR PASS — the greedy fill above can strand the deck a few cards short. Classic case:
      // you need one more card from insert X, you own four of them, but they're all 145 power and the
      // deck already holds 6 at 145. The slot is blocked — yet one of those sitting 145s may belong to
      // an insert where you have a spare at a DIFFERENT power. Swap that card for its same-insert
      // alternative and the 145 slot opens up for the card you actually need.
      //
      // So for each card we still want: find an in-deck card at that same power whose insert has an
      // unused alternative at some other power, swap it out, and seat the wanted card. The donor
      // insert keeps its count (critical — it must not lose a completed block of 10).
      if (chosen.length < 60) {
        const inDeckIds = () => new Set(chosen.map(c=>c.id));
        // Cards we'd still like, from our picked inserts, strongest first.
        const wanted = base
          .filter(c => inRange(c) && pickedKeys.has(insertKey(c) || "—"))
          .filter(c => !usedKeys.has(dupKey(c)))
          .sort((a,b)=>(powerOf(b)-powerOf(a))||mineFirst(a,b));

        for (const want of wanted) {
          if (chosen.length >= 60) break;
          // If this card's own insert is already at its 10-card ceiling, no power swap can help.
          if ((perInsAM[insertKey(want) || "—"]||0) >= BLOCK) continue;
          const wp = String(want.power||"0");
          if ((perPowerAM[wp]||0) < (F.perPower||6)) { seat(want); continue; }  // not actually blocked

          // Blocked at this power. Find a donor: an in-deck card at the SAME power that we can
          // replace with another card from the donor's own insert at a DIFFERENT power.
          const ids = inDeckIds();
          let swapped = false;
          for (let i = 0; i < chosen.length && !swapped; i++) {
            const donor = chosen[i];
            if (String(donor.power||"0") !== wp) continue;                 // must free THIS power
            if (donor.id === want.id) continue;
            const donorIns = insertKey(donor.treatment) || "—";
            // A replacement from the donor's insert, at a different power that still has room.
            const repl = (coreByIns[donorIns]||[]).find(r => {
              if (ids.has(r.id) || usedKeys.has(dupKey(r))) return false;
              const rp = String(r.power||"0");
              if (rp === wp) return false;                                  // wouldn't free the slot
              return (perPowerAM[rp]||0) < (F.perPower||6);
            });
            if (!repl) continue;
            // Perform the swap: donor out, repl in (donor's insert count is unchanged), then seat want.
            usedKeys.delete(dupKey(donor));
            perPowerAM[wp] = Math.max(0, (perPowerAM[wp]||0) - 1);
            chosen[i] = repl;
            usedKeys.add(dupKey(repl));
            const rp = String(repl.power||"0");
            perPowerAM[rp] = (perPowerAM[rp]||0) + 1;
            // Now the wanted card fits.
            if (!seat(want)) {
              // Shouldn't happen, but undo cleanly if it does.
              usedKeys.delete(dupKey(repl));
              perPowerAM[rp] = Math.max(0, (perPowerAM[rp]||0) - 1);
              chosen[i] = donor;
              usedKeys.add(dupKey(donor));
              perPowerAM[wp] = (perPowerAM[wp]||0) + 1;
            } else {
              swapped = true;
            }
          }
        }
      }

      // A late block can complete once the top-up lands more of an insert — sweep once more.
      if (completedInserts.length < MAX_UNLOCKS) {
        const tally = {};
        chosen.filter(inRange).forEach(c => { const t = insertKey(c)||"—"; tally[t]=(tally[t]||0)+1; });
        for (const [k,count] of Object.entries(tally)) {
          if (completedInserts.length >= MAX_UNLOCKS) break;
          if (count < BLOCK) continue;
          if (completedInserts.some(ci => ci.insert === insertLabel(k,k))) continue;
          const apex = (apexByIns[k]||[]).find(a => !apexUsed.has(dupKey(a)));
          if (!apex) continue;
          completedInserts.push({ insert: insertLabel(k,k), apexPower: powerOf(apex), apexCard: apex });
        }
      }


      // 4. Claim one matching-insert Apex Hero per completed block.
      completedInserts.forEach(ci => {
        if (!ci.apexCard) return;
        const k = dupKey(ci.apexCard);
        if (usedKeys.has(k) || apexUsed.has(k)) return;
        usedKeys.add(k); apexUsed.add(k); chosen.push(ci.apexCard);
      });

      const insertTally = {};
      chosen.filter(inRange).forEach(c => { const t = insertKey(c) || "—"; insertTally[t] = (insertTally[t]||0)+1; });
      const insertList = Object.entries(insertTally)
        .map(([insert,count]) => ({ insert: insertLabel(insert, insert), count, unlocks: Math.floor(count/BLOCK) }))
        .sort((a,b)=>b.count-a.count);
      const insertUnlocks = Math.min(MAX_UNLOCKS, completedInserts.length);
      const hotDogSlots = 0;   // manual-builder only — see coach note
      const foiledHotDogs = 0; // not counted in the quick build
      const apexSlots = insertUnlocks;                       // one slot per completed insert block
      const apexAdded = chosen.filter(c=>powerOf(c)>CORE_MAX).length;

      // "Closest to another unlock" — inserts sitting just short of the next block of 10.
      const nearInserts = insertList
        .map(x => ({ insert:x.insert, have:x.count, need:(Math.floor(x.count/10)+1)*10 }))
        .filter(x => x.need - x.have <= 5 && x.have >= 5)
        .sort((a,b)=>(a.need-a.have)-(b.need-b.have))
        .slice(0,4);

      const coreCount = chosen.filter(c=>powerOf(c)<=160).length;
      const apexCount = chosen.filter(c=>powerOf(c)>160).length;
      return {
        have: chosen.length, need: 70,
        cards: chosen, perPower: perPowerAM, ownedEligible: base.length,
        familyCount: chosen.filter(c=>!isMine(c)&&famOwner(c)).length,
        am:{
          coreCount, apexCount,
          insertUnlocks, insertList: insertList.filter(x=>x.count>=5),
          foiledHotDogs, hotDogSlots, apexAdded, apexSlots,
          nearInserts, ceiling:70,
        },
      };
    }

    // ── Other deck types: cluster by TREATMENT (fill one insert as deep as possible, then move on) ──
    // Format rules only — ownership is checked separately, so the "what's missing" pass below can
    // reuse the exact same eligibility test against cards you DON'T own.
    const formatEligible = c => {
      if(isPlayCard(c)) return false;
      if(!weaponOk(c)) return false;
      if(!treatOk(c)) return false;
      if(setFilter && setFilter.size>0 && !setFilter.has(c.setName)) return false; // set restriction
      if(!inMaxWindow(c)) return false; // max deck power window
      // ── Format restrictions (see DECK_FORMATS) ──
      if(F.powerCap && powerOf(c) > F.powerCap) return false;                     // Spec's ≤160
      if(F.set && c.setName !== F.set) return false;                              // Blast → Alpha Blast only
      if(F.excludeSets && F.excludeSets.includes(c.setName)) return false;        // Elite bans Trainer cards
      if(F.treatment && c.treatment !== F.treatment) return false;                // Power Glove
      if(F.weapon && canonWeapon(c.weapon) !== canonWeapon(F.weapon)) return false; // Brawl
      // GG HiLo: the deck may ONLY contain cards from the allowed groups.
      if(F.groups && !F.groups.some(g=>g.match(c))) return false;
      // Plays, Bonus Plays and Hot Dogs are not Heroes \u2014 they are support cards you play alongside a
      // deck, so an auto-build that fills slots with them produces a deck you cannot actually field.
      // Only the QUICK builder skips them; manual building still allows anything, because a Madness
      // deck genuinely wants Foil Hot Dogs and only you know which ones you are bringing.
      if(isSupportCard(c)) return false;
      return true;
    };
    const ownedCards = cards.filter(c => isOwned(c) && isAvailable(c) && formatEligible(c));

    // POWER FIRST. Always build the strongest legal deck. Treatment is only a TIEBREAK: among
    // cards of the SAME power, prefer the insert we're already deepest in (free clustering that
    // never costs a point of power), then prefer your own card over a borrowed one.
    const treatCount = {};
    const seen = new Set(); const perPower = {}; const usable = [];
    const seenCardIds = new Set();      // Power Glove: no duplicate CARDS, not just hero+power
    let runningPower = 0;               // Elite: 8,250 total-power budget

    // Can this card still go in, given everything already picked?
    const canTake = c => {
      const k = dupKey(c);
      if (seen.has(k)) return false;                                   // no duplicate hero+power+weapon
      if (F.unique && seenCardIds.has(c.id)) return false;             // Power Glove: no duplicate card
      const pk = String(c.power||"0");
      if ((perPower[pk]||0) >= PER_POWER) return false;                // max N at any one power level
      if (F.totalPower && runningPower + powerOf(c) > F.totalPower) return false; // Elite's budget
      return true;
    };
    const take = c => {
      seen.add(dupKey(c));
      seenCardIds.add(c.id);
      const pk = String(c.power||"0");
      perPower[pk] = (perPower[pk]||0)+1;
      treatCount[c.treatment||"—"] = (treatCount[c.treatment||"—"]||0)+1;
      runningPower += powerOf(c);
      usable.push(c);
    };

    // ── SUPERS FIRST ─────────────────────────────────────────────────────────────────────────
    // A Super is a Superfoil 1/1 — the rarest card there is. If you've picked Super among your
    // weapons (e.g. "Super + Fire"), any deck that CAN include your Supers should, so we seat every
    // eligible Super before the normal power-tier fill runs. They still have to be legal for the
    // format (per-power caps, dup rules, budget) — canTake enforces that — but among legal cards
    // they jump the queue. The rest of the deck then fills around them exactly as before.
    if (wSet.has("Super")) {
      const supers = ownedCards
        .filter(c => canonWeapon(c.weapon) === "Super")
        .sort((a,b)=>(powerOf(b)-powerOf(a))||mineFirst(a,b));
      for (const c of supers) {
        if (usable.length >= DECK_SIZE) break;
        if (!canTake(c)) continue;
        take(c);
      }
    }


    // GG HILO — each group has a REQUIRED minimum, so satisfy those quotas first. Taking the
    // strongest cards blindly would happily fill 60 slots from one group and leave the deck
    // illegal, so the quotas come before raw power.
    if (F.groups) {
      for (const g of F.groups) {
        if (!g.min) continue;
        const pool = ownedCards
          .filter(c => g.match(c))
          .sort((a,b)=>powerCmp(a,b)||mineFirst(a,b));
        let got = 0;
        for (const c of pool) {
          if (got >= g.min || usable.length >= DECK_SIZE) break;
          if (!canTake(c)) continue;
          take(c); got++;
        }
      }
    }

    // SPEC+ — the 60-card Spec-legal core (≤160) is MANDATORY and must be complete before any
    // high-power Hero counts. Build it first, strongest-first, then layer the high tiers on top
    // with their per-power caps (165 ×2, 170 ×2, one each of 175–200, nothing above 200).
    if (F.coreSize && F.highTiers) {
      const core = ownedCards
        .filter(c => powerOf(c) <= F.coreCap)
        .sort((a,b)=>(powerOf(b)-powerOf(a))||mineFirst(a,b));
      for (const c of core) {
        if (usable.length >= F.coreSize) break;
        if (!canTake(c)) continue;
        take(c);
      }
      // Only once the core is COMPLETE do the high slots open up.
      if (usable.length >= F.coreSize) {
        const highUsed = {};
        const highs = ownedCards
          .filter(c => { const p = powerOf(c); return p > F.coreCap && F.highTiers[p]; })
          .sort((a,b)=>(powerOf(b)-powerOf(a))||mineFirst(a,b));
        for (const c of highs) {
          if (usable.length >= DECK_SIZE) break;
          const p = powerOf(c);
          const cap = F.highTiers[p] || 0;
          if ((highUsed[p]||0) >= cap) continue;   // e.g. only one 180 allowed
          if (!canTake(c)) continue;
          take(c); highUsed[p] = (highUsed[p]||0)+1;
        }
      }
    } else if (F.totalPower) {
      // ── ELITE: exactly 60 cards, total ≤ 8,250, as strong as possible ──────────────────────────
      // Rule: 60 cards AND total ≤ 8,250 (both required), max 6 per power level, no dupes. Grabbing
      // the highest-power cards first overshoots the budget before reaching 60 (the deck strands at
      // ~51 cards @ 8,250 with no legal way to add more). So instead:
      //   1) TAKE THE 60 CHEAPEST legal cards — guarantees a full 60 at the LOWEST possible total.
      //      If even that total exceeds 8,250, no legal 60-card deck exists for this collection.
      //   2) UPGRADE within the leftover budget — repeatedly swap a card already in the deck for a
      //      stronger legal card you own, as long as it keeps the total ≤ 8,250 and respects max-6.
      //      This spends the headroom on power without ever breaking the 60-card count.
      const cheapFirst = ownedCards.slice().sort((a,b)=>(powerOf(a)-powerOf(b)) || mineFirst(a,b));
      for (const c of cheapFirst) {
        if (usable.length >= DECK_SIZE) break;
        if (!canTake(c)) continue;          // canTake enforces per-power≤6, dedup, eligibility
        take(c);                            // NOTE: canTake also blocks if it would exceed budget
      }
      // If we couldn't even seat 60 cheapest under budget, that's the best legal deck possible.
      // Otherwise, spend remaining budget upgrading weak cards to stronger ones.
      if (usable.length === DECK_SIZE) {
        // Spend remaining budget upgrading weak cards to stronger ones — but cheaply. The old version
        // re-scanned all cards × all slots × a per-power filter inside a 400-iteration while loop,
        // which could hit hundreds of millions of ops and freeze the builder. This version keeps a
        // live per-power tally and does a single pass of upgrades, each O(deck size).
        const inSet = new Set(usable.map(c=>c.id));
        const powCount = {};
        usable.forEach(c=>{ const pk=String(c.power||"0"); powCount[pk]=(powCount[pk]||0)+1; });
        // Strongest candidate upgrades you own but haven't used, high→low.
        const upgrades = ownedCards.filter(c=>!inSet.has(c.id)).sort((a,b)=>powerOf(b)-powerOf(a));
        // Keep the deck's weak cards handy, weakest first, so each upgrade swaps the cheapest slot.
        const weakOrder = () => usable.map((c,idx)=>({idx,p:powerOf(c)})).sort((a,b)=>a.p-b.p);
        let order = weakOrder();
        for (const up of upgrades) {
          const upP = powerOf(up), upk = String(up.power||"0");
          if ((powCount[upk]||0) >= (F.perPower||6)) continue;       // already 6 at this power
          // Find the weakest in-deck card weaker than `up` whose swap keeps total ≤ cap.
          let did = false;
          for (const slot of order) {
            const cur = usable[slot.idx];
            const curP = powerOf(cur);
            if (curP >= upP) break;                                   // order is weakest-first; no gain past here
            if (runningPower - curP + upP > F.totalPower) continue;   // would exceed budget
            // swap
            const curk = String(cur.power||"0");
            powCount[curk] = Math.max(0,(powCount[curk]||0)-1);
            powCount[upk] = (powCount[upk]||0)+1;
            runningPower = runningPower - curP + upP;
            inSet.delete(cur.id); inSet.add(up.id);
            usable[slot.idx] = up;
            did = true; break;
          }
          if (did) order = weakOrder();                              // refresh weak order after a swap
        }
      }
    } else {
      // Everything else (Spec, etc.): bucket by power, walk tiers strongest→weakest (or lowest first
      // for lowPower / Coach formats).
      //
      // WEAPON PRIORITY: if you picked several goal weapons, they're a priority order, not a flat
      // "any of these". Fill from weapon #1 as deep as it will go, then #2 tops up what's left, etc.
      // Without this, the tier walk below just takes the strongest cards regardless of weapon, so a
      // "Glow, then Gum" goal produced a mostly-Gum deck whenever Gum ran stronger.
      const fillFrom = (pool) => {
        const tiers = {};
        pool.forEach(c => { const p = powerOf(c); (tiers[p] = tiers[p]||[]).push(c); });
        const tierPowers = Object.keys(tiers).map(Number).sort((a,b)=> F.lowPower ? (a-b) : (b-a));
        for (const p of tierPowers) {
          if (usable.length >= DECK_SIZE) break;
          const tier = tiers[p].slice().sort((a,b)=>{
            const ct=(treatCount[a.treatment||"—"]||0), bt=(treatCount[b.treatment||"—"]||0);
            if (ct !== bt) return bt - ct;
            return mineFirst(a,b);
          });
          for (const c of tier) {
            if (usable.length >= DECK_SIZE) break;
            if (!canTake(c)) continue;
            take(c);
          }
        }
      };
      if (wPriority.length > 1) {
        // One pass per goal weapon, in the order they were picked.
        for (const w of wPriority) {
          if (usable.length >= DECK_SIZE) break;
          fillFrom(ownedCards.filter(c => canonWeapon(c.weapon) === w));
        }
        // Anything still short gets topped up from whatever else is legal.
        if (usable.length < DECK_SIZE) fillFrom(ownedCards);
      } else {
        fillFrom(ownedCards);
      }
    }

    const treatUsed = Object.entries(treatCount)
      .map(([treatment,count])=>({treatment,count}))
      .sort((a,b)=>b.count-a.count);

    // ── WHAT'S MISSING ──────────────────────────────────────────────────────────────────────
    // Expressed as POWER SLOTS, not specific cards. Any legal card at a given power fills that slot
    // equally well, so naming one particular card ("Deep Freeze #BFA-148") is arbitrary and makes
    // the marketplace check almost useless — it'd only match that exact card. What you actually
    // need is "a 150, another 150, and a 145", and then ANY card at that power in an allowed set
    // will do.
    //
    // Slots are worked out by walking power levels high → low and taking whatever the per-power cap
    // still allows, so the suggestion is the strongest legal deck you could reach.
    let missingSlots = [];   // [{ power, count, sets:[...] }]
    const shortBy = Math.max(0, DECK_SIZE - usable.length);
    if (shortBy > 0) {
      // Which sets are legal for this format/filters? The marketplace search is scoped to these.
      const legalSets = [...new Set(
        cards.filter(c => formatEligible(c)).map(c => c.setName).filter(Boolean)
      )];

      // Every power level that has at least one legal card.
      // For a PER-CARD-cap format (Spec) we want the STRONGEST legal deck, so walk high→low.
      // For a TOTAL-power format (Elite: ≤8,250 summed), grabbing the highest powers blows the
      // budget instantly and demands cards you'd never legally fit — so walk LOW→high and stop once
      // the remaining power budget is used up. That suggests a set of cards that actually completes
      // a legal Elite deck instead of an impossible "6× 250-power" wishlist.
      const isTotalCap = !!F.totalPower;
      const powersAvail = [...new Set(
        cards.filter(c => formatEligible(c)).map(c => powerOf(c)).filter(p => p > 0)
      )].sort((a,b)=> isTotalCap ? (a-b) : (b-a));

      const perPowerM = { ...perPower };
      const bySlot = {};
      let need = shortBy;
      // Remaining total-power budget (only meaningful for total-cap formats).
      let budget = isTotalCap ? Math.max(0, F.totalPower - usable.reduce((s,c)=>s+powerOf(c),0)) : Infinity;
      for (const p of powersAvail) {
        if (need <= 0) break;
        let room = PER_POWER - (perPowerM[String(p)] || 0);   // how many more can sit at this power
        if (room <= 0) continue;
        let take = Math.min(room, need);
        // For a total-power format, don't suggest more cards at this power than the budget allows.
        if (isTotalCap && p > 0) take = Math.min(take, Math.floor(budget / p));
        if (take <= 0) continue;
        bySlot[p] = (bySlot[p] || 0) + take;
        perPowerM[String(p)] = (perPowerM[String(p)] || 0) + take;
        need -= take;
        if (isTotalCap) budget -= take * p;
      }
      missingSlots = Object.entries(bySlot)
        .map(([power,count]) => ({ power: Number(power), count, sets: legalSets }))
        .sort((a,b)=>b.power-a.power);
    }

    return {
      have: usable.length, need: DECK_SIZE, perPower, ownedEligible: ownedCards.length, cards: usable,
      familyCount: usable.filter(c=>!isMine(c)&&famOwner(c)).length,
      treatUsed, // which inserts the deck ended up drawing from
      maxMode: !!maxMode, maxWindowLabel,
      maxShort: maxMode ? Math.max(0, DECK_SIZE - usable.length) : 0,
      missingSlots, // the power slots you still need to fill, and which sets can fill them
      avgPower: usable.length ? Math.round(usable.reduce((s,c)=>s+powerOf(c),0)/usable.length) : 0,
      totalPower: usable.reduce((s,c)=>s+powerOf(c),0),
    };
  }
  // Weapon and treatment goals are SETS, not single strings — an "all Hex and Gum" Apex deck is a
  // perfectly normal thing to want to build. Empty set = no restriction (same as deckGoalSets).
  const [deckGoalW, setDeckGoalW] = useState(()=>new Set());
  const [deckGoalT, setDeckGoalT] = useState(()=>new Set());
  const [deckGoalSets, setDeckGoalSets] = useState(()=>new Set()); // empty = all sets allowed
  const [deckMaxMode, setDeckMaxMode] = useState(false); // enforce the format's legal power window
  // Which cards the quick builder may draw from: "mine" | "both" | "family"
  const [deckSource, setDeckSource] = useState("mine");
  const [regSheetOpen, setRegSheetOpen] = useState(false);   // BoBA-style registration sheet generator
  const [regInfo, setRegInfo] = useState({ player:"", whatnot:"", event:"", date:"", playbookId:"" });
  // PERF: computeDeckProgress scans the full 31k-card checklist (filter + sort + bucket). It was
  // running on EVERY render of the whole collector app — so hovering a card in the Card Database
  // re-scanned 31k cards. Memoized, and only computed when the deck tab is actually open.
  const deckProgress = useMemo(
    () => (activeTab === "deck" ? computeDeckProgress(deckGoalW, deckGoalT, deckType, deckGoalSets, deckMaxMode, deckSource) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab, deckGoalW, deckGoalT, deckType, deckGoalSets, deckMaxMode, deckSource, owned, familyAvail, otherDeckUse, allDeckUse, cards]
  );


  // Rainbow stats. This USED TO RUN INLINE inside {activeTab==="rainbow" && (()=>{...})()},
  // meaning it re-ran on EVERY render of the whole component — every keystroke, every hover,
  // every unrelated state change. It filters 35k cards, allocates a fresh object for each one
  // (`{...c, treatment}`), buckets them all, then walks every bucket again. Tens of thousands of
  // allocations per render. That is what made the app feel unusable on a phone.
  //
  // Now it only recomputes when something it actually depends on changes. Note `search` is NOT a
  // dependency: the search filter is applied to the RESULT further down, where it is cheap.
    const rainbowBase = useMemo(() => {
    if (activeTab !== "rainbow" || cards.length === 0) return null;
    const normalizeTreatment = c => {
      const t = (c.treatment||"").toLowerCase();
      const num = String(c.cardNum||"").trim().toUpperCase();
      // Any "plays" treatment OR a PL/BPL cardNum prefix collapses into one of two rainbows
      const isPlays = t.includes("plays") || /^B?PL[\s\-]?\d/.test(num) || num.startsWith("BPL");
      if(isPlays) {
        if(num.startsWith("BPL") || t.includes("bonus")) return "Bonus Plays";
        return "Plays";
      }
      // Hotdogs collapse into one rainbow
      if(t.includes("hotdog") || t.includes("hot dog")) return "Hotdogs";
      return c.treatment;
    };
    const rainbowCards = (rainbowSetFilter ? cards.filter(c => c.setName === rainbowSetFilter) : cards)
      .filter(c => { const t=(c.treatment||"").toLowerCase(); return t!=="home team discount"; })
      .map(c => ({ ...c, treatment: normalizeTreatment(c) }));
    const availableSets = [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort();

    // -- Build group stats (shared logic for hero / treatment / treatment+weapon) --
    const groupKeyOf = c => {
      // Plays, Bonus Plays & Hotdogs always collapse into a single rainbow each, in every mode
      if(c.treatment === "Plays" || c.treatment === "Bonus Plays" || c.treatment === "Hotdogs") return c.treatment;
      if(rainbowGroupBy === "hero") return c.hero;
      if(rainbowGroupBy === "treatmentWeapon") {
        const tr = c.treatment || "", wp = c.weapon || "";
        return (tr || wp) ? `${tr}${tr&&wp?" ":""}${wp}`.trim() : "";
      }
      return c.treatment;
    };
    const groupMap = {};
    rainbowCards.forEach(c => {
      const key = groupKeyOf(c);
      if(!key) return;
      if(!groupMap[key]) groupMap[key] = [];
      groupMap[key].push(c);
    });
    const allGroups = Object.keys(groupMap).sort();
    // Everything above is independent of what you OWN — it only depends on the card set and the
    // grouping mode. Splitting it out means toggling a card no longer re-spreads 35k objects and
    // re-buckets every group; only the ownership tally below re-runs. That was the lag that made it
    // look like your cards had vanished.
    return { rainbowCards, availableSets, groupKeyOf, groupMap, allGroups };
  }, [activeTab, cards, rainbowSetFilter, rainbowGroupBy]);

  const rainbowStats = useMemo(() => {
    if (!rainbowBase) return null;
    const { rainbowCards, availableSets, groupKeyOf, groupMap, allGroups } = rainbowBase;
    const groupStats = allGroups.map(key => {
      const gcards = groupMap[key];
      const total = gcards.length;
      // ownedNet, not owned: a borrowed card must not count toward completing a set you do not own.
      const ownedCount = gcards.filter(c => ownedNet[c.id]).length;
      const complete = total > 0 && ownedCount === total;
      // secondary breakdown tags
      const bySecondary = {};
      gcards.forEach(c => {
        const s = c.setName || "Unknown";
        if(!bySecondary[s]) bySecondary[s] = { total:0, owned:0 };
        bySecondary[s].total++;
        if(owned[c.id]) bySecondary[s].owned++;
      });
      return { key, total, ownedCount, complete, bySecondary };
    });
    const completedRainbows = groupStats.filter(g => g.complete).length;
    const partialRainbows   = groupStats.filter(g => g.ownedCount > 0 && !g.complete).length;
    return { groupStats, completedRainbows, partialRainbows, availableSets, allGroups, rainbowCards, groupKeyOf };
  }, [rainbowBase, owned]);

  // Loans due back today or overdue — reminder feed for the bell. Reads my own
  // lots (whether I'm the lender chasing a card, or the borrower who needs to
  // return one). A loan qualifies once returnBy <= today.
  const loansDue = useMemo(() => {
    const today = todayLocal();
    return (lots||[])
      .filter(l => l.lendState && l.returnBy && l.returnBy <= today)
      .map(l => {
        const c = cardById.get(l.cardId);
        const days = Math.ceil((new Date(l.returnBy+"T00:00:00") - new Date(today+"T00:00:00"))/86400000);
        return { lot:l, card:c, days,
                 borrowed:l.lendState==="borrowed",
                 hero:(c?.hero)||l.lendWho||"a card", who:l.lendWho||"" };
      })
      .sort((a,b)=>a.days-b.days);
  }, [lots, cardById]);

  // Pending loan offers where I'm the borrower — someone sent me cards to accept.
  // Surfaced in the notification bell so I don't have to dig into the Friends tab.
  const incomingLoans = useMemo(() => {
    if (!user) return [];
    return (borrowLedger||[])
      .filter(l => l.status==="pending" && l.borrowerUid===user.uid)
      .map(l => {
        const c = cardById.get(l.cardId);
        return { entry:l, hero: (c?.hero) || l.cardLabel || "a card", from: l.ownerName || "someone", image: c?.imageUrl || l.cardImage || "" };
      });
  }, [borrowLedger, user, cardById]);

  const totalNotifs = friendReqs.length+teamInvites.length+marketNotifs.length+wantNotifs.length+unreadThreads+loansDue.length+incomingLoans.length;

  if(loading) {
    const baseUrls = LOADING_CARD_IMAGES.urls.length > 0
      ? LOADING_CARD_IMAGES.urls
      : (() => { // fallback: try localStorage cache
          try {
            const raw = localStorage.getItem("boba_checklist_cache_v3");
            if (raw) {
              const { cards: cc } = JSON.parse(raw);
              return (cc||[]).filter(c=>c.imageUrl&&(c.hero||"").toLowerCase().includes("bo"))
                .sort(()=>Math.random()-0.5).slice(0,40).map(c=>c.imageUrl);
            }
          } catch(e) {}
          return [];
        })();
    // How many flying cards to show.
    //
    // A FIRST-TIME user is downloading the ~12MB card list on this very screen. Every card image the
    // loading animation pulls is bandwidth stolen from the thing they are waiting for, and 55 animated
    // elements is real CPU on a phone on top of that — the loading screen was actively competing with
    // the load it exists to cover, which is why it felt endless for exactly the people it should have
    // been reassuring.
    //
    // So: no cached checklist means no cached images either, means a genuinely cold first load. Show
    // the animation only when the images are ALREADY cached (a returning user, where it costs nothing
    // and the wait is short anyway). New users get the clean spinner instead, and all the bandwidth.
    const hasCachedCards = (() => {
      try { return !!localStorage.getItem("boba_checklist_cache_v3"); } catch(e) { return false; }
    })();
    const TARGET_CARDS = hasCachedCards ? 55 : 0;
    const floatUrls = (baseUrls.length > 0 && TARGET_CARDS > 0)
      ? Array.from({length:TARGET_CARDS},(_,i)=>baseUrls[i % baseUrls.length]).sort(()=>Math.random()-0.5)
      : [];
    const firstRun = !hasCachedCards;
    return (
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:"#000",fontFamily:"'Trebuchet MS',sans-serif",overflow:"hidden",position:"relative"}}>
        <style>{`
          @keyframes floatAcross {
            0%   { transform: translateX(-220px) translateY(var(--y-start)) rotate(var(--rot-start)); opacity:0; }
            5%   { opacity: 0.85; }
            95%  { opacity: 0.85; }
            100% { transform: translateX(calc(100vw + 220px)) translateY(var(--y-end)) rotate(var(--rot-end)); opacity:0; }
          }
          @keyframes dot { 0%,80%,100%{transform:scale(0.6);opacity:0.3} 40%{transform:scale(1);opacity:1} }
          @keyframes logoGlow { 0%,100%{opacity:0.7} 50%{opacity:1} }
          @keyframes foilSheen { 0%{background-position:0% 50%} 100%{background-position:200% 50%} }
          @keyframes superShineSweep { 0%{background-position:200% 50%} 100%{background-position:-100% 50%} }
          @keyframes fxBackdrop { 0%{opacity:0} 8%{opacity:1} 82%{opacity:1} 100%{opacity:0} }
          @keyframes caughtFlash { 0%{opacity:0} 3%{opacity:0.55} 12%{opacity:0} 100%{opacity:0} }
          @keyframes caughtCardIn { 0%{opacity:0;transform:scale(0.4) rotate(-12deg)} 14%{opacity:1;transform:scale(1.18) rotate(4deg)} 24%{transform:scale(0.96) rotate(-2deg)} 32%{transform:scale(1.06) rotate(0deg)} 40%{transform:scale(1)} 86%{opacity:1;transform:scale(1)} 100%{opacity:0;transform:scale(1.08)} }
          @keyframes caughtBurst { 0%{transform:scale(0.2);opacity:0} 22%{opacity:1} 100%{transform:scale(2.8);opacity:0} }
          @keyframes caughtText { 0%{opacity:0;transform:scale(0.6) translateY(10px)} 16%{opacity:1;transform:scale(1.1) translateY(0)} 24%{transform:scale(1)} 86%{opacity:1} 100%{opacity:0;transform:scale(0.95)} }
          @keyframes confettiFall { 0%{transform:translateY(-20px) rotate(0deg);opacity:0} 8%{opacity:1} 70%{opacity:1} 100%{transform:translateY(105vh) rotate(720deg);opacity:0} }
          @keyframes starPop { 0%{transform:scale(0) rotate(0deg);opacity:0} 35%{transform:scale(1.3) rotate(180deg);opacity:1} 100%{transform:scale(0.6) rotate(360deg);opacity:0} }
          @keyframes superGoldFall { 0%{transform:translateY(-40px) rotate(0deg);opacity:0} 10%{opacity:1} 100%{transform:translateY(105vh) rotate(720deg);opacity:0} }
          @keyframes superCardPop { 0%{transform:scale(0.3) rotateY(0deg);opacity:0} 40%{transform:scale(1.15) rotateY(360deg);opacity:1} 60%{transform:scale(0.95) rotateY(360deg)} 100%{transform:scale(1) rotateY(360deg);opacity:1} }
          @keyframes superRays { 0%{transform:rotate(0deg) scale(1.2);opacity:0.7} 100%{transform:rotate(360deg) scale(1.2);opacity:0.7} }
          @keyframes superTextGlow { 0%,100%{text-shadow:0 0 20px rgba(245,158,11,0.8),0 0 40px rgba(245,158,11,0.5)} 50%{text-shadow:0 0 35px rgba(251,191,36,1),0 0 70px rgba(245,158,11,0.8)} }
          @keyframes superTextGlowP { 0%,100%{text-shadow:0 0 20px rgba(168,85,247,0.8),0 0 40px rgba(147,51,234,0.5)} 50%{text-shadow:0 0 35px rgba(192,132,252,1),0 0 70px rgba(168,85,247,0.8)} }
          @keyframes superTextIn { 0%{transform:scale(0.5) translateY(20px);opacity:0} 60%{transform:scale(1.1)} 100%{transform:scale(1) translateY(0);opacity:1} }
          @keyframes superFadeBg { 0%{opacity:0} 15%{opacity:1} 85%{opacity:1} 100%{opacity:0} }
          @keyframes superRingPulse { 0%{transform:scale(0.6);opacity:0.9} 100%{transform:scale(2.4);opacity:0} }
          .vticker-wrap { overflow:hidden; }
          .vticker-track { display:flex; flex-direction:column; animation:vtickerScroll 18s linear infinite; }
          .vticker-wrap:hover .vticker-track { animation-play-state:paused; }
          .super-shine { opacity:1 !important; animation:superShineSweep 4.5s linear infinite; }
          .super-shine-card:hover .super-shine { animation-duration:1.5s; }
          .super-shine-card:hover img { filter:none !important; opacity:1 !important; transition:filter 0.3s ease, opacity 0.3s ease; }
        `}</style>

        {/* Floating cards */}
        {floatUrls.map((url,i)=>{
          const yStart  = Math.random()*90+5;
          const yEnd    = Math.random()*90+5;
          const rotS    = (Math.random()-0.5)*40;
          const rotE    = (Math.random()-0.5)*40;
          const dur     = 5 + Math.random()*10;
          const delay   = -(Math.random()*dur);
          const scale   = 0.4 + Math.random()*0.7;
          return (
            <div key={i} style={{
              position:"absolute",
              left:0, top:0,
              width:140*scale,
              aspectRatio:"3/4",
              "--y-start":`${yStart}vh`,
              "--y-end":`${yEnd}vh`,
              "--rot-start":`${rotS}deg`,
              "--rot-end":`${rotE}deg`,
              animation:`floatAcross ${dur}s linear ${delay}s infinite`,
              borderRadius:10*scale,
              overflow:"hidden",
              border:"2px solid rgba(232,49,122,0.3)",
              boxShadow:"0 8px 32px rgba(0,0,0,0.6), 0 0 20px rgba(232,49,122,0.15)",
              pointerEvents:"none",
              zIndex: Math.floor(scale*10),
            }}>
              <img src={url} alt="" style={{width:"100%",height:"100%",objectFit:"cover",display:"block",pointerEvents:"none"}}/>
            </div>
          );
        })}

        {/* Dark overlay so cards don't overpower the logo */}
        <div style={{position:"absolute",inset:0,background:"radial-gradient(ellipse 60% 60% at 50% 50%, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 100%)",pointerEvents:"none",zIndex:10}}/>

        {/* Center logo */}
        <div style={{position:"relative",zIndex:20,textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:20}}>
          <div style={{animation:"logoGlow 2s ease-in-out infinite"}}>
            <img src="/BazookaLogo.png" alt="Bazooka" style={{width:"min(280px,60vw)",height:"auto",display:"block",margin:"0 auto",filter:"drop-shadow(0 8px 24px rgba(232,49,122,0.35))"}}/>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.25)",letterSpacing:4,textTransform:"uppercase",marginTop:8}}>BoBA Collector Database</div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",marginTop:8}}>
            {[0,0.2,0.4].map((delay,i)=>(
              <div key={i} style={{width:6,height:6,borderRadius:"50%",background:"#E8317A",animation:`dot 1.4s ease-in-out ${delay}s infinite`}}/>
            ))}
          </div>
            {/* Tell a first-time user WHY they're waiting. Bouncing dots alone, for 20+ seconds, on a
                cold 12MB download, reads as broken. Saying it happens once reads as setup. */}
            {firstRun && (
              <div style={{marginTop:18,maxWidth:320,textAlign:"center"}}>
                <div style={{fontSize:13.5,fontWeight:800,color:"rgba(255,255,255,0.75)",marginBottom:6}}>
                  Setting up your card database{"\u2026"}
                </div>
                <div style={{fontSize:11.5,color:"rgba(255,255,255,0.35)",lineHeight:1.6}}>
                  Downloading all 35,000 BoBA cards. This only happens once {"\u2014"} after this it opens instantly.
                </div>
              </div>
            )}
        </div>
      </div>
    );
  }

  // -- Render helpers --
  const tabBtn=(id,label,badge)=>(
    <button key={id} onClick={()=>setActiveTab(id)} className="nav-tab" data-active={activeTab===id?"1":"0"} style={{
      background:"transparent",
      color:activeTab===id?"#fff":"rgba(255,255,255,0.5)",
      border:"none",
      borderBottom:`2px solid ${activeTab===id?"#E8317A":"transparent"}`,
      padding:"12px 2px", fontSize:13.5, fontWeight:activeTab===id?800:600,
      cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", position:"relative",
      letterSpacing:0.2, transition:"color 0.15s ease, border-color 0.15s ease",
      display:"inline-flex", alignItems:"center", gap:7,
    }}>
      {label}
      {badge>0&&<span style={{background:"#E8317A",color:"#fff",borderRadius:10,minWidth:18,height:18,padding:"0 5px",fontSize:10,fontWeight:900,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{badge}</span>}
    </button>
  );

  // Single top-level nav item (Card Database, Marketplace, Leaderboard)
  const navItem=(id,label,badge)=>(
    <button key={id} onClick={()=>setActiveTab(id)} className="nav-tab" data-active={activeTab===id?"1":"0"} style={{
      background:"transparent", color:activeTab===id?"#fff":"rgba(255,255,255,0.55)", border:"none",
      borderBottom:`2px solid ${activeTab===id?"#E8317A":"transparent"}`,
      padding:"14px 2px", fontSize:14, fontWeight:activeTab===id?800:600, cursor:"pointer", fontFamily:"inherit",
      whiteSpace:"nowrap", letterSpacing:0.2, transition:"color 0.15s, border-color 0.15s",
      display:"inline-flex", alignItems:"center", gap:6,
    }}>
      {label}
      {badge>0&&<span style={{background:"#E8317A",color:"#fff",borderRadius:10,minWidth:18,height:18,padding:"0 5px",fontSize:10,fontWeight:900,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{badge}</span>}
    </button>
  );

  // Hover dropdown group (Collectibility, Gameplay)
  const navGroup=(key,label,items)=>{
    const childActive = items.some(it=>it.id===activeTab);
    const open = navMenu===key;
    // Stash items so the root-level mobile overlay can render them outside the scroll container
    navGroupItems.current[key] = { label, items };
    return (
      <div key={key} style={{position:"relative"}} {...(isMobile ? {} : { onMouseEnter:()=>setNavMenu(key), onMouseLeave:()=>setNavMenu(m=>m===key?null:m) })}>
        <button className="nav-tab" data-active={childActive?"1":"0"} onClick={()=>setNavMenu(o=>o===key?null:key)} style={{
          background:"transparent", color:childActive?"#fff":"rgba(255,255,255,0.55)", border:"none",
          borderBottom:`2px solid ${childActive?"#E8317A":"transparent"}`,
          padding:"14px 2px", fontSize:14, fontWeight:childActive?800:600, cursor:"pointer", fontFamily:"inherit",
          whiteSpace:"nowrap", letterSpacing:0.2, transition:"color 0.15s, border-color 0.15s",
          display:"inline-flex", alignItems:"center", gap:6,
        }}>
          {label} <span style={{fontSize:9,opacity:0.6,transform:open?"rotate(180deg)":"none",transition:"transform 0.15s"}}>▼</span>
        </button>
        {open && !isMobile && (
          <div style={{position:"absolute",top:"100%",left:0,minWidth:190,background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:12,boxShadow:"0 12px 40px rgba(0,0,0,0.7)",padding:6,zIndex:700}}>
            {items.map(it=>(
              <button key={it.id} onClick={()=>{setActiveTab(it.id);setNavMenu(null);}} style={{
                display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",gap:10,
                background:activeTab===it.id?"rgba(232,49,122,0.12)":"transparent",border:"none",
                color:activeTab===it.id?"#E8317A":"#ddd",borderRadius:8,padding:"9px 12px",fontSize:13,fontWeight:700,
                cursor:"pointer",fontFamily:"inherit",textAlign:"left",transition:"background 0.12s",
              }}
                onMouseEnter={e=>{if(activeTab!==it.id)e.currentTarget.style.background="rgba(255,255,255,0.05)";}}
                onMouseLeave={e=>{if(activeTab!==it.id)e.currentTarget.style.background="transparent";}}>
                <span>{it.label}</span>
                {it.badge>0&&<span style={{background:"#E8317A",color:"#fff",borderRadius:10,minWidth:18,height:18,padding:"0 5px",fontSize:10,fontWeight:900,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{it.badge}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{minHeight:"100vh",background:"#000",color:"var(--bz-ink)",fontFamily:"'Trebuchet MS',sans-serif"}}>
      {fanDeck && (()=>{
        // Sort here too rather than trusting every caller. This view is opened from the deck
        // builder, the playbook, and the saved-deck list, and a saved deck is rebuilt from stored
        // ids in whatever order they were written \u2014 so relying on the caller would leave some
        // entry points unsorted.
        const cards = (()=>{
          const pw = x => { const n = parseFloat(x?.power); return isNaN(n) ? -Infinity : n; };
          return (fanDeck.cards||[]).slice()
            .sort((a,b)=> pw(b)-pw(a) || String(a.hero||"").localeCompare(String(b.hero||"")));
        })();
        const n = cards.length;
        const mid = (n-1)/2;
        // Fan geometry: spread WIDE left-to-right. Cards overlap horizontally,
        // each rotated a little, arcing up at the edges.
        const totalArc = Math.min(38, 8 + n*0.9); // total degrees — gentle, readable arc
        const stepAng = n>1 ? totalArc/(n-1) : 0;
        // horizontal spacing so the fan spans the screen width
        const spanPx = Math.min(typeof window!=="undefined"?window.innerWidth*0.82:1100, 90 + n*28);
        const stepX = n>1 ? spanPx/(n-1) : 0;
        const cardImg = (c,big)=> c.imageUrl
          ? <img src={c.imageUrl} alt={c.hero} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
          : <div style={{width:"100%",height:"100%",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:10,textAlign:"center"}}><div style={{fontSize:big?15:13,fontWeight:800,color:PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)]||"#888"}}>{c.hero}</div><div style={{fontSize:10,color:"rgba(255,255,255,0.4)",marginTop:4}}>{c.treatment}</div><div style={{fontSize:18,fontWeight:900,color:PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)]||"#888",marginTop:8}}>{c.power}</div></div>;
        return (
          <div style={{position:"fixed",inset:0,zIndex:11200,background:"radial-gradient(ellipse at center, rgba(20,8,18,0.98), rgba(0,0,0,0.99))",backdropFilter:"blur(14px)",display:"flex",flexDirection:"column",overflow:"hidden"}}>
            {/* Header */}
            <div style={{flexShrink:0,padding:"20px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
              <div>
                <div style={{fontSize:22,fontWeight:900,color:"#fff"}}>{fanDeck.name}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginTop:2}}>{n} card{n!==1?"s":""}{fanMode==="fan"?(isMobile?" · tap to lift":" · hover to lift"):""}</div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <div style={{display:"flex",background:"rgba(255,255,255,0.06)",borderRadius:24,padding:3}}>
                  <button onClick={()=>setFanMode("grid")} style={{background:fanMode==="grid"?"rgba(255,255,255,0.15)":"transparent",border:"none",color:fanMode==="grid"?"#fff":"rgba(255,255,255,0.5)",borderRadius:20,padding:"7px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>▦ Grid</button>
                  <button onClick={()=>setFanMode("fan")} style={{background:fanMode==="fan"?"rgba(255,255,255,0.15)":"transparent",border:"none",color:fanMode==="fan"?"#fff":"rgba(255,255,255,0.5)",borderRadius:20,padding:"7px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>🖐 Fan</button>
                </div>
                {/* Group the grid by power tier (all 115s, 120s…) or by insert/treatment
                    (mixtapes, linoleums…) — for reading a complex set cleanly. */}
                {fanMode==="grid" && (
                  <div style={{display:"flex",background:"rgba(255,255,255,0.06)",borderRadius:24,padding:3}}>
                    {[["none","Flat"],["power","By power"],["insert","By insert"]].map(([v,l])=>(
                      <button key={v} onClick={()=>setFanGroup(v)} style={{background:fanGroup===v?"rgba(255,255,255,0.15)":"transparent",border:"none",color:fanGroup===v?"#fff":"rgba(255,255,255,0.5)",borderRadius:20,padding:"7px 14px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
                    ))}
                  </div>
                )}
                <button onClick={()=>{setFanDeck(null);setFanMode("grid");}} style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.2)",color:"#fff",borderRadius:24,width:40,height:40,fontSize:18,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
              </div>
            </div>
            {/* Body */}
            {fanMode==="grid" ? (
              <div style={{flex:1,minHeight:0,overflowY:"auto",padding:"8px 24px 32px"}}>
                {(()=>{
                  const tile = (c,i)=>{const wc=PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";return(
                    <div key={i} style={{aspectRatio:"3/4",borderRadius:12,overflow:"hidden",border:`2px solid ${wc}55`,background:"var(--bz-s1)",boxShadow:"0 8px 24px rgba(0,0,0,0.5)",transition:"transform 0.15s",cursor:"default"}}
                      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-6px) scale(1.03)";e.currentTarget.style.borderColor=wc;}}
                      onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.borderColor=wc+"55";}}>
                      {cardImg(c,true)}
                    </div>
                  );};
                  const gridWrap = kids => <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:16,maxWidth:1400,margin:"0 auto"}}>{kids}</div>;
                  if (fanGroup==="none") return gridWrap(cards.map(tile));
                  // Group into ordered buckets. Power: high→low. Insert: canonicalized.
                  // The plain color battlefoils (Red/Silver/Blue/Orange/Green/Pink Battlefoil)
                  // are one insert family — fold them into a single "Color Battlefoil" group
                  // instead of six separate colour buckets. Named inserts (80's Rad, Grandma's
                  // Linoleum, Mixtape, etc.) keep their own group.
                  const COLORS = ["red","silver","blue","orange","green","pink","gold","purple","black","white","yellow"];
                  const canonInsert = (tRaw) => {
                    const t = String(tRaw||"").trim();
                    if (!t) return "No insert";
                    const low = t.toLowerCase();
                    // "<color> Battlefoil" (and nothing else) -> Color Battlefoil
                    const m = low.match(/^([a-z]+)\s+battlefoil$/);
                    if (m && COLORS.includes(m[1])) return "Color Battlefoil";
                    if (low === "battlefoil" || low === "color battlefoil" || low === "color battlefoils (all)") return "Color Battlefoil";
                    return t;
                  };
                  const groups = {};
                  cards.forEach(c=>{
                    const key = fanGroup==="power"
                      ? ((c.power!=null&&c.power!=="")?String(c.power):"No power")
                      : canonInsert(c.treatment);
                    (groups[key]=groups[key]||[]).push(c);
                  });
                  const keys = Object.keys(groups).sort((a,b)=>{
                    if (fanGroup==="power") {
                      const na=parseFloat(a), nb=parseFloat(b);
                      if (isNaN(na)) return 1; if (isNaN(nb)) return -1;
                      return nb-na;   // highest power first
                    }
                    return a.localeCompare(b);
                  });
                  return (
                    <div style={{maxWidth:1400,margin:"0 auto"}}>
                      {keys.map(k=>(
                        <div key={k} style={{marginBottom:30}}>
                          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,position:"sticky",top:0,background:"linear-gradient(rgba(10,4,9,0.96),rgba(10,4,9,0.7))",padding:"6px 0",zIndex:2}}>
                            <span style={{fontSize:16,fontWeight:900,color:"#fff"}}>{fanGroup==="power"&&k!=="No power"?`${k} POWER`:k}</span>
                            <span style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.4)"}}>{groups[k].length}</span>
                            <div style={{flex:1,height:1,background:"rgba(255,255,255,0.1)"}}/>
                          </div>
                          {gridWrap(groups[k].map(tile))}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ) : isMobile ? (
              <div style={{flex:1,minHeight:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginBottom:10}}>← swipe through your hand →</div>
                <div className="fan-scroll" style={{display:"flex",overflowX:"auto",overflowY:"hidden",width:"100%",padding:"40px 50vw 40px 8vw",WebkitOverflowScrolling:"touch",scrollSnapType:"x proximity"}}>
                  {cards.map((c,i)=>{
                    const wc=PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
                    return (
                      <div key={i} onClick={e=>{e.stopPropagation();const el=e.currentTarget;const lifted=el.getAttribute("data-lift")==="1";document.querySelectorAll(".fan-mcard").forEach(x=>{x.style.transform="";x.style.zIndex="";x.setAttribute("data-lift","0");});if(!lifted){el.style.transform="translateY(-34px) scale(1.12)";el.style.zIndex="999";el.setAttribute("data-lift","1");}}}
                        className="fan-mcard" data-lift="0"
                        style={{flexShrink:0,width:"min(58vw,260px)",aspectRatio:"3/4",marginLeft:i===0?0:"-34vw",borderRadius:14,overflow:"hidden",border:`2px solid ${wc}77`,background:"var(--bz-s1)",boxShadow:"0 10px 30px rgba(0,0,0,0.7)",transition:"transform 0.25s cubic-bezier(0.34,1.2,0.5,1)",scrollSnapAlign:"center",position:"relative"}}>
                        {cardImg(c,true)}
                      </div>
                    );
                  })}
                </div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:10}}>tap a card to lift it</div>
              </div>
            ) : (
              <div style={{flex:1,minHeight:0,position:"relative",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <div style={{position:"relative",width:"100%",height:"100%"}}>
                  {cards.map((c,i)=>{
                    const angle=(i-mid)*stepAng;
                    const x=(i-mid)*stepX;
                    const yArc=Math.pow(Math.abs(i-mid),2)*0.35; // gentle arc, edges dip slightly
                    const wc=PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)]||"#444";
                    return (
                      <div key={i} className="fan-card" onClick={e=>e.stopPropagation()}
                        onMouseEnter={e=>{e.currentTarget.style.transform=`translate(-50%,-50%) translateX(${x}px) translateY(${yArc-70}px) rotate(0deg) scale(1.15)`;e.currentTarget.style.zIndex="999";}}
                        onMouseLeave={e=>{e.currentTarget.style.transform=`translate(-50%,-50%) translateX(${x}px) translateY(${yArc}px) rotate(${angle}deg)`;e.currentTarget.style.zIndex=String(100+i);}}
                        style={{
                          position:"absolute", top:"50%", left:"50%",
                          width:"clamp(120px,15vw,200px)", aspectRatio:"3/4",
                          transformOrigin:"center bottom",
                          transform:`translate(-50%,-50%) translateX(${x}px) translateY(${yArc}px) rotate(${angle}deg)`,
                          transition:"transform 0.3s cubic-bezier(0.34,1.2,0.5,1), filter 0.2s",
                          zIndex:100+i, borderRadius:13, overflow:"hidden",
                          boxShadow:"0 12px 40px rgba(0,0,0,0.6)", border:`2px solid ${wc}55`,
                          background:"var(--bz-s1)", cursor:"pointer"
                        }}>
                        {cardImg(c)}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}
      {earlyAccessModal && (
        <div onClick={()=>setEarlyAccessModal(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:2147483640,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#15101a",border:"1px solid rgba(74,222,128,0.3)",borderRadius:16,padding:24,maxWidth:440,width:"100%",maxHeight:"80vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div style={{fontSize:18,fontWeight:900,color:"#fff"}}>🔑 Early Access List</div>
              <button onClick={()=>setEarlyAccessModal(false)} style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.5)",fontSize:22,cursor:"pointer"}}>×</button>
            </div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginBottom:16,lineHeight:1.5}}>People here can access the app while it's in early access. Your whole @bazookabreaks.com team always has access automatically — this list is extra individual emails you can add or remove.</div>
            <div style={{display:"flex",gap:8,marginBottom:16}}>
              <input value={eaInput} onChange={e=>setEaInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addEarlyAccess();}} placeholder="email@example.com" style={{flex:1,background:"#241820",border:"1px solid rgba(255,255,255,0.18)",borderRadius:9,color:"#f6eef2",padding:"10px 12px",fontSize:13,fontFamily:"inherit",outline:"none"}}/>
              <button onClick={addEarlyAccess} style={{background:"rgba(74,222,128,0.15)",border:"1px solid rgba(74,222,128,0.5)",color:"#4ade80",borderRadius:9,padding:"10px 16px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>+ Add</button>
            </div>
            <div style={{fontSize:11,color:"#7c9cff",marginTop:-8,marginBottom:16}}>💡 Adding someone auto-copies a ready-to-send invite message — just paste it into a text or email to them.</div>
            {earlyAccessList.length===0 ? (
              <div style={{fontSize:13,color:"rgba(255,255,255,0.35)",textAlign:"center",padding:"20px 0"}}>No extra emails yet. Add one above.</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {earlyAccessList.map(em => (
                  <div key={em} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:9,padding:"9px 12px"}}>
                    <span style={{fontSize:13,color:"#f6eef2",wordBreak:"break-all"}}>{em}</span>
                    <div style={{display:"flex",gap:6,flexShrink:0}}>
                      <button onClick={()=>copyInvite(em)} style={{background:eaCopied===em?"rgba(74,222,128,0.15)":"rgba(124,156,255,0.12)",border:`1px solid ${eaCopied===em?"rgba(74,222,128,0.5)":"rgba(124,156,255,0.4)"}`,color:eaCopied===em?"#4ade80":"#7c9cff",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{eaCopied===em?"✓ Copied":"📋 Copy invite"}</button>
                      <button onClick={()=>removeEarlyAccess(em)} style={{background:"transparent",border:"1px solid rgba(232,49,122,0.4)",color:"#E8317A",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginTop:16,lineHeight:1.5}}>Changes apply within a few seconds — no redeploy needed.</div>
          </div>
        </div>
      )}
      {resetModal && (
        <div onClick={()=>!resetting&&setResetModal(false)} style={{position:"fixed",inset:0,zIndex:14000,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#16161f",border:"1.5px solid rgba(232,49,122,0.4)",borderRadius:16,padding:26,maxWidth:480,width:"100%"}}>
            <div style={{fontSize:19,fontWeight:900,color:"#fff",marginBottom:8}}>🧹 Reset Marketplace & Messages</div>
            <div style={{fontSize:13,color:"#bbb",lineHeight:1.6,marginBottom:14}}>
              This permanently deletes <strong style={{color:"#fff"}}>all marketplace listings, offers, sales, notifications, and deal messages</strong> for a clean launch.
            </div>
            <div style={{background:"rgba(74,222,128,0.08)",border:"1px solid rgba(74,222,128,0.3)",borderRadius:9,padding:"10px 13px",marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:800,color:"#4ade80",marginBottom:4}}>✅ Safe — does NOT touch:</div>
              <div style={{fontSize:11.5,color:"#9ca3af",lineHeight:1.5}}>People's collections (owned cards), want lists, profiles, decks, or accounts. Those are completely untouched.</div>
            </div>
            <div style={{fontSize:12,color:"#999",marginBottom:6}}>Clears: marketplace · offers · sales · notifications · deal threads · negotiation history</div>
            {resetting ? (
              <div style={{textAlign:"center",padding:"14px 0"}}>
                <div style={{fontSize:14,fontWeight:800,color:"#E8317A",marginBottom:6}}>⏳ Resetting…</div>
                <div style={{fontSize:12,color:"var(--bz-ink-2)"}}>{resetting}</div>
              </div>
            ) : (
              <>
                <div style={{fontSize:12,color:"#bbb",marginBottom:6,marginTop:8}}>Type <strong style={{color:"#E8317A"}}>RESET</strong> to confirm:</div>
                <input value={resetConfirmText} onChange={e=>setResetConfirmText(e.target.value)} placeholder="RESET" style={{width:"100%",background:"var(--bz-bg)",color:"#fff",border:"1px solid var(--bz-line-2)",borderRadius:8,padding:"10px 12px",fontSize:14,fontFamily:"inherit",marginBottom:16,letterSpacing:2}}/>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>setResetModal(false)} style={{flex:1,background:"transparent",border:"1px solid var(--bz-line-2)",color:"#999",borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                  <button disabled={resetConfirmText!=="RESET"} onClick={runMarketplaceReset} style={{flex:2,background:resetConfirmText==="RESET"?"linear-gradient(135deg,#E8317A,#b91c5c)":"var(--bz-line-2)",border:"none",color:resetConfirmText==="RESET"?"#fff":"#666",borderRadius:10,padding:"11px",fontSize:13,fontWeight:900,cursor:resetConfirmText==="RESET"?"pointer":"not-allowed",fontFamily:"inherit"}}>Wipe Marketplace & Messages</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {superCelebration && animsOn && (() => {
        const isOne = superCelebration.type === "oneof1";
        const C = isOne
          ? { main:"#A855F7", light:"#C084FC", deep:"#7C3AED", bg:"rgba(124,58,237,0.22)", glow:"0 0 50px rgba(168,85,247,0.7), 0 0 100px rgba(147,51,234,0.4)", title:"SECRET 1/1!", sub:"💎 A true Secret 1/1 — the holy grail 💎",
              confetti:["#A855F7","#C084FC","#9333EA","#E9D5FF","#7C3AED","#D8B4FE"] }
          : { main:"#FBBF24", light:"#FFE08A", deep:"#F59E0B", bg:"rgba(245,158,11,0.22)", glow:"0 0 50px rgba(251,191,36,0.75), 0 0 100px rgba(245,158,11,0.4)", title:"SUPER HIT!", sub:"⭐ One of the hardest pulls in the hobby ⭐",
              confetti:["#FFD700","#FBBF24","#F59E0B","#FFF3B0","#FFAA00","#FFE08A"] };
        const img = superCelebration.cardImage;
        return (
          <div style={{ position:"fixed", inset:0, zIndex:100000, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none", overflow:"hidden" }}>
            {/* backdrop */}
            <div style={{ position:"absolute", inset:0, background:`radial-gradient(ellipse at center, ${C.bg}, rgba(0,0,0,0.86) 72%)`, animation:"fxBackdrop 4s ease forwards" }}/>
            {/* white flash */}
            <div style={{ position:"absolute", inset:0, background:"#fff", animation:"caughtFlash 4s ease forwards" }}/>
            {/* confetti */}
            {Array.from({length:55}).map((_,i)=>{
              const left=Math.random()*100, delay=Math.random()*0.5, dur=1.8+Math.random()*1.4, size=6+Math.random()*9;
              const color=C.confetti[i%C.confetti.length], round=Math.random()>0.5;
              return <div key={i} style={{ position:"absolute", top:-20, left:`${left}%`, width:size, height:round?size:size*0.5, background:color, borderRadius:round?"50%":2, animation:`confettiFall ${dur}s ease ${delay}s forwards`, boxShadow:`0 0 6px ${color}` }}/>;
            })}
            {/* burst rings */}
            {[0,0.15,0.3].map((d,i)=>(
              <div key={i} style={{ position:"absolute", width:"min(300px,68vw)", aspectRatio:"1", borderRadius:"50%", border:`3px solid ${C.main}99`, animation:`caughtBurst 1.5s ease ${d}s forwards` }}/>
            ))}
            {/* pop stars */}
            {[["20%","22%"],["78%","20%"],["18%","72%"],["80%","74%"],["50%","13%"]].map(([t,l],i)=>(
              <div key={i} style={{ position:"absolute", top:t, left:l, fontSize:"clamp(24px,5vw,40px)", animation:`starPop 1.4s ease ${0.2+0.12*i}s forwards`, opacity:0 }}>{isOne?"💎":"⭐"}</div>
            ))}
            {/* card */}
            {img && (
              <div style={{ position:"relative", width:"min(250px,58vw)", aspectRatio:"3/4", animation:"caughtCardIn 4s cubic-bezier(0.34,1.56,0.64,1) forwards", borderRadius:14, overflow:"hidden", border:`3px solid ${C.main}`, boxShadow:C.glow }}>
                <img src={img} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
              </div>
            )}
            {/* text */}
            <div style={{ position:"absolute", bottom:"15%", textAlign:"center", animation:"caughtText 4s ease forwards", padding:"0 16px" }}>
              <div style={{ fontSize:"clamp(30px,8vw,60px)", fontWeight:900, color:C.main, letterSpacing:2, textShadow:`0 0 30px ${C.main}, 0 0 60px ${C.deep}`, fontFamily:"'Trebuchet MS',sans-serif", lineHeight:1 }}>{C.title}</div>
              <div style={{ fontSize:"clamp(16px,4vw,22px)", color:C.light, letterSpacing:1, marginTop:8, fontWeight:800 }}>{superCelebration.cardName}</div>
              <div style={{ fontSize:13, color:"rgba(255,255,255,0.75)", letterSpacing:1, marginTop:8, fontWeight:700 }}>{C.sub}</div>
            </div>
          </div>
        );
      })()}
      {tourStep >= 0 && (() => {
        const steps = swancity ? [
          { icon:"⭐", title:"The Super Foil Hunt", body:"Every Super Foil is a 1-of-1 — the rarest, hardest pull in Bo Jackson Battle Arena. This page tracks every one that's been hit across the community." },
          { icon:"📊", title:"See what's been pulled", body:"The bar up top shows how many Supers have been claimed so far. Each card is unique — once it's hit, it's gone forever." },
          { icon:"🔍", title:"Browse & filter", body:"Use the Set dropdown to jump to a release, and Show: Claimed / Available to see what's already been pulled vs. what's still out there waiting." },
          { icon:"✨", title:"Tap a card", body:"Greyed-out cards are still available. Verified hits glow gold — tap one to flip it and see the actual photo of the pull and the collector's story." },
          { icon:"🏆", title:"Hit one? Claim it!", body:"Pulled a Super? Tap 'Claim This Super', add a photo of your card, your name, and the story. No account needed — submit and it goes in for verification." },
          { icon:"💎", title:"Supers & Secret 1/1s", body:"Switch between the Supers and Secret 1/1s tabs up top to track both. Good luck out there — may your next rip be a 1-of-1!" },
        ] : [
          { icon:"🃏", title:"Welcome to the Vault", body:"This is the home base for every BoBA card. Let's take 30 seconds to show you around — tap Next to go through it." },
          { icon:"🔍", title:"Find any card", body:"Search by hero, card number, athlete, or treatment to find any card instantly." },
          { icon:"🎛️", title:"Filter & sort", body:"Narrow down by set, weapon, treatment, power, or what you own. Toggle between grid and list view, and resize cards with the slider." },
          { icon:"➕", title:"Build your collection", body:"Hover (or tap) any card and hit ➕ Add to Collection — no need to open it. Tap a card to see full details, the athlete it's based on, and finish options like Paper vs Battlefoil." },
          { icon:"🏆", title:"Supers & Secret 1/1s", body:"The Collectibility menu tracks every Super and Secret 1/1 hit in the community — see who pulled what, and claim your own when you land one." },
          { icon:"🤝", title:"Marketplace & decks", body:"List cards, make offers, and build decks with other collectors. Check the Marketplace and Deck Builder tabs up top whenever you're ready." },
          { icon:"🐛", title:"You're in the beta!", body:"We're still polishing things. If anything looks broken or weird, tap 🐛 Report Bug in the corner — that's how you help us launch this right. Enjoy the Vault!" },
        ];
        const s = steps[tourStep];
        const last = tourStep === steps.length-1;
        const end = () => { try{localStorage.setItem(swancity?"bz_swancity_tour_done":"bz_tour_done","1");}catch(e){} setTourStep(-1); };
        return (
          <div style={{ position:"fixed", inset:0, zIndex:14600, background:"rgba(0,0,0,0.8)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
            <div style={{ background:"linear-gradient(160deg,#1a0f1f,#120a16)", border:"1px solid rgba(232,49,122,0.35)", borderRadius:22, padding:"30px 26px", maxWidth:420, width:"100%", textAlign:"center", boxShadow:"0 24px 70px rgba(232,49,122,0.3)" }}>
              <div style={{ fontSize:46, marginBottom:14 }}>{s.icon}</div>
              <div style={{ fontSize:22, fontWeight:900, color:"#fff", marginBottom:10 }}>{s.title}</div>
              <div style={{ fontSize:14, color:"#c8c8d4", lineHeight:1.6, marginBottom:22 }}>{s.body}</div>
              <div style={{ display:"flex", justifyContent:"center", gap:6, marginBottom:20 }}>
                {steps.map((_,i)=>(<div key={i} style={{ width:i===tourStep?20:7, height:7, borderRadius:4, background:i===tourStep?"#E8317A":"rgba(255,255,255,0.2)", transition:"all 0.2s" }}/>))}
              </div>
              <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                {tourStep>0 && <button onClick={()=>setTourStep(tourStep-1)} style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:12, padding:"12px 18px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Back</button>}
                <button onClick={()=>last?end():setTourStep(tourStep+1)} style={{ flex:1, background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff", border:"none", borderRadius:12, padding:"13px", fontSize:15, fontWeight:900, cursor:"pointer", fontFamily:"inherit", boxShadow:"0 4px 20px rgba(232,49,122,0.4)" }}>{last?"Let's go 🚀":"Next"}</button>
              </div>
              {!last && <button onClick={end} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", fontSize:12, cursor:"pointer", fontFamily:"inherit", marginTop:14 }}>Skip tour</button>}
            </div>
          </div>
        );
      })()}
      {showBetaWelcome && (
        <div onClick={dismissBetaWelcome} style={{position:"fixed",inset:0,zIndex:14500,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"linear-gradient(160deg,#1a0f1f,#120a16)",border:"1px solid rgba(232,49,122,0.35)",borderRadius:20,padding:"30px 26px",maxWidth:420,width:"100%",textAlign:"center",boxShadow:"0 20px 60px rgba(232,49,122,0.25)"}}>
            <div style={{display:"inline-flex",alignItems:"center",gap:7,background:"rgba(251,191,36,0.12)",border:"1px solid rgba(251,191,36,0.4)",borderRadius:30,padding:"5px 14px",marginBottom:18}}>
              <span style={{width:7,height:7,borderRadius:"50%",background:"#FBBF24",boxShadow:"0 0 8px #FBBF24"}}/>
              <span style={{fontSize:11,fontWeight:900,color:"#FBBF24",letterSpacing:1.5}}>BETA</span>
            </div>
            <div style={{fontSize:23,fontWeight:900,color:"#fff",marginBottom:12,lineHeight:1.2}}>Welcome to the Vault! 🎉</div>
            <div style={{fontSize:14,color:"#c8c8d4",lineHeight:1.6,marginBottom:10}}>
              You're getting early access while we're still in <strong style={{color:"#FBBF24"}}>beta</strong> — which means your job is to <strong style={{color:"#fff"}}>break it</strong>.
            </div>
            <div style={{fontSize:14,color:"#c8c8d4",lineHeight:1.6,marginBottom:22}}>
              See something broken, weird, slow, or just off? Hit the <strong style={{color:"#E8317A"}}>🐛 Report Bug</strong> button in the corner — it takes two seconds and helps us launch this thing right.
            </div>
            <button onClick={dismissBetaWelcome} style={{width:"100%",background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:15,fontWeight:900,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 20px rgba(232,49,122,0.4)"}}>Let's go 🚀</button>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:14}}>Thanks for helping build the home base for BoBA collectors.</div>
          </div>
        </div>
      )}
      {/* Sits ABOVE the select toolbar (which is at bottom:20), not on top of it — a toast that
          covers the buttons you are trying to press is worse than no toast. */}
      {toast && <div style={{position:"fixed",bottom:96,left:"50%",transform:"translateX(-50%)",zIndex:11000,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",padding:"12px 22px",borderRadius:12,fontSize:14,fontWeight:700,boxShadow:"0 8px 32px rgba(232,49,122,0.4)",maxWidth:"90vw",textAlign:"center"}}>{toast}</div>}
      {bulkImg && !bulkProg && (()=>{
        const sets = [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort();
        const treatPool = bulkImg.setName ? cards.filter(c=>c.setName===bulkImg.setName) : cards;
        const treatments = [...new Set(treatPool.map(c=>c.treatment).filter(Boolean))].sort();
        // Detect the subfolders present in this batch (each = a treatment)
        const subFolders = bulkImg.byFolder ? [...new Set(bulkImg.files.map(f=>{ const p=(f.webkitRelativePath||"").split("/").filter(Boolean); return p.length>2?p[p.length-2]:null; }).filter(Boolean))] : [];
        return (
          <div onClick={()=>setBulkImg(null)} style={{position:"fixed",inset:0,zIndex:13000,background:"rgba(0,0,0,0.8)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"#16161f",border:"1px solid var(--bz-line-2)",borderRadius:16,padding:24,maxWidth:460,width:"100%",maxHeight:"90vh",overflowY:"auto"}}>
              <div style={{fontSize:18,fontWeight:900,color:"#fff",marginBottom:6}}>🖼 Import {bulkImg.files.length} Images</div>
              {/* Same reason as the admin importer: without this, a placeholder blocks the real scan. */}
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:bulkImgOverwrite?"rgba(232,49,122,0.1)":"rgba(255,255,255,0.03)",border:"1px solid "+(bulkImgOverwrite?"rgba(232,49,122,0.45)":"rgba(255,255,255,0.1)"),borderRadius:9,padding:"9px 11px",marginBottom:10}}>
                <input type="checkbox" checked={bulkImgOverwrite} onChange={e=>setBulkImgOverwrite(e.target.checked)} style={{accentColor:"#E8317A",width:15,height:15}}/>
                <span style={{fontSize:12,fontWeight:800,color:bulkImgOverwrite?"#E8317A":"rgba(255,255,255,0.65)"}}>Replace existing images</span>
                <span style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>{bulkImgOverwrite?"overwrites placeholders":"skips cards that have one"}</span>
              </label>
              {/* Card numbers in the checklist are not always right, and a number-only match skips the
                  file silently when they are wrong. With the set pinned above, the hero name in the
                  filename is enough to place the card. Ambiguous filenames are still skipped. */}
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",background:bulkImgHeroMatch?"rgba(74,222,128,0.1)":"rgba(255,255,255,0.03)",border:"1px solid "+(bulkImgHeroMatch?"rgba(74,222,128,0.45)":"rgba(255,255,255,0.1)"),borderRadius:9,padding:"9px 11px",marginBottom:10}}>
                <input type="checkbox" checked={bulkImgHeroMatch} onChange={e=>setBulkImgHeroMatch(e.target.checked)} style={{width:15,height:15,accentColor:"#4ade80",cursor:"pointer"}}/>
                <span style={{fontSize:12,fontWeight:800,color:bulkImgHeroMatch?"#4ade80":"rgba(255,255,255,0.65)"}}>Match on hero name</span>
                <span style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>{bulkImgHeroMatch?"used when the card number does not match":"card number only"}</span>
              </label>
              {bulkImg.byFolder ? (
                <div style={{fontSize:12,color:"#999",lineHeight:1.5,marginBottom:16}}>Detected <strong style={{color:"#4ade80"}}>{subFolders.length} treatment subfolder{subFolders.length!==1?"s":""}</strong>. Each image will be assigned the treatment of the subfolder it's in — just confirm the Set below.</div>
              ) : (
                <div style={{fontSize:12,color:"#999",lineHeight:1.5,marginBottom:16}}>Pick the Set and Treatment below. When BOTH are set, the importer just reads the hero name off each card (the big easy-to-read banner) and assigns it — perfect for noisy foils like Linoleum where the card number is hard to read.</div>
              )}
              <div style={{fontSize:11,fontWeight:800,color:bulkImg.setName?"#4ade80":"#FBBF24",marginBottom:6}}>{bulkImg.setName?"✅":"⚠️"} Target Set</div>
              <select value={bulkImg.setName} onChange={e=>setBulkImg(b=>({...b,setName:e.target.value,treatment:""}))} style={{width:"100%",background:"var(--bz-bg)",color:"var(--bz-ink)",border:"1px solid var(--bz-line-2)",borderRadius:8,padding:"10px 12px",fontSize:13,fontFamily:"inherit",cursor:"pointer",marginBottom:14}}>
                <option value="">— All sets —</option>
                {sets.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              {bulkImg.byFolder ? (
                <div style={{marginBottom:18}}>
                  <div style={{fontSize:11,fontWeight:800,color:"#4ade80",marginBottom:6}}>✅ Treatments (auto-detected from subfolders)</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {subFolders.map(sf=>{ const known=bulkImg.setName?treatments.some(t=>t.replace(/[\s-]/g,"").toLowerCase()===sf.replace(/[\s-]/g,"").toLowerCase()||t.replace(/[\s-]/g,"").toLowerCase().includes(sf.replace(/[\s-]/g,"").toLowerCase())):true; return <span key={sf} style={{fontSize:11,fontWeight:700,color:known?"#4ade80":"#FBBF24",background:known?"rgba(74,222,128,0.1)":"rgba(251,191,36,0.1)",border:`1px solid ${known?"#4ade8044":"#FBBF2444"}`,borderRadius:6,padding:"3px 9px"}}>{known?"":"⚠️ "}{sf}</span>; })}
                  </div>
                  <div style={{fontSize:10,color:"#999",marginTop:6}}>⚠️ = folder name doesn't match a known treatment in this set; those images fall back to name/vision matching.</div>
                </div>
              ) : (<>
              <div style={{fontSize:11,fontWeight:800,color:bulkImg.treatment?"#4ade80":"#888",marginBottom:6}}>{bulkImg.treatment?"✅":"○"} Treatment {bulkImg.treatment?"":"(optional — helps for noisy foils)"}</div>
              <select value={bulkImg.treatment||""} onChange={e=>setBulkImg(b=>({...b,treatment:e.target.value}))} style={{width:"100%",background:"var(--bz-bg)",color:"var(--bz-ink)",border:"1px solid var(--bz-line-2)",borderRadius:8,padding:"10px 12px",fontSize:13,fontFamily:"inherit",cursor:"pointer",marginBottom:18}}>
                <option value="">— Any treatment —</option>
                {treatments.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
              </>)}
              {/* Weapon picker. Skyline and Grandma's Linoleum both exist as Fire / Ice / Glow on the
                  SAME card number with the same hero, so set+treatment alone leaves three identical
                  candidates and the importer has to guess between them \u2014 which is how a folder of
                  Fire images ended up attached to the Glow cards. Pinning the weapon removes the
                  guess entirely. */}
              {(() => {
                const pool = cards.filter(c =>
                  (!bulkImg.setName   || c.setName === bulkImg.setName) &&
                  (!bulkImg.treatment || c.treatment === bulkImg.treatment));
                const weapons = [...new Set(pool.map(c=>c.weapon).filter(Boolean))].sort();
                if (weapons.length < 2) return null;   // nothing to disambiguate
                return (
                  <>
                    <div style={{fontSize:11,fontWeight:800,color:bulkImg.weapon?"#4ade80":"#FBBF24",marginBottom:6}}>
                      {bulkImg.weapon ? `\u2705 Weapon: ${bulkImg.weapon}` : `\u26a0\ufe0f Weapon \u2014 ${weapons.length} variants share these card numbers`}
                    </div>
                    <select value={bulkImg.weapon||""} onChange={e=>setBulkImg(b=>({...b,weapon:e.target.value}))}
                      style={{width:"100%",background:"var(--bz-bg)",color:"var(--bz-ink)",border:"1px solid "+(bulkImg.weapon?"rgba(74,222,128,0.5)":"rgba(251,191,36,0.5)"),borderRadius:8,padding:"10px 12px",fontSize:13,fontFamily:"inherit",cursor:"pointer",marginBottom:14}}>
                      <option value="">{"\u2014 Any weapon (may pick the wrong variant) \u2014"}</option>
                      {weapons.map(w=><option key={w} value={w}>{w}</option>)}
                    </select>
                  </>
                );
              })()}
              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>setBulkImg(null)} style={{flex:1,background:"transparent",border:"1px solid var(--bz-line-2)",color:"#999",borderRadius:10,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                <button onClick={()=>{ const {files,setName,treatment,weapon,byFolder}=bulkImg; setBulkImg(null); runBulkImageImport(files,setName,byFolder?"":(treatment||""),weapon||""); }} style={{flex:2,background:"linear-gradient(135deg,#4ade80,#22c55e)",border:"none",color:"#000",borderRadius:10,padding:"11px",fontSize:13,fontWeight:900,cursor:"pointer",fontFamily:"inherit"}}>Start Import{bulkImg.byFolder?` → ${subFolders.length} treatments`:bulkImg.treatment?` → ${bulkImg.treatment}${bulkImg.weapon?` · ${bulkImg.weapon}`:""}`:bulkImg.setName?` → ${bulkImg.setName}`:""}</button>
              </div>
            </div>
          </div>
        );
      })()}
      {bulkProg && (
        <div style={{position:"fixed",inset:0,zIndex:13000,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#16161f",border:"1px solid var(--bz-line-2)",borderRadius:16,padding:28,maxWidth:440,width:"100%",textAlign:"center"}}>
            <div style={{fontSize:16,fontWeight:900,color:"#fff",marginBottom:14}}>Importing Images…</div>
            <div style={{height:10,background:"var(--bz-bg)",borderRadius:6,overflow:"hidden",marginBottom:10}}>
              <div style={{height:"100%",width:`${bulkProg.total?Math.round(bulkProg.done/bulkProg.total*100):0}%`,background:"linear-gradient(90deg,#E8317A,#4ade80)",transition:"width 0.3s"}}/>
            </div>
            <div style={{fontSize:13,color:"#ccc",marginBottom:6}}>{bulkProg.done} / {bulkProg.total} — <span style={{color:"#4ade80"}}>{bulkProg.matched} matched</span>{bulkProg.skipped?<span style={{color:"#FBBF24"}}>, {bulkProg.skipped} skipped</span>:null}</div>
            <div style={{fontSize:11,color:"#a0a0a0",minHeight:16,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis"}}>{bulkProg.status}</div>
            {/* When nothing matches, say WHY on screen. The downloaded report has the per-file detail,
                but the single most common cause \u2014 an empty candidate pool \u2014 should not require opening
                a text file to discover. */}
            {bulkProg.why && (
              <>
                <div style={{marginTop:12,background:"rgba(251,191,36,0.1)",border:"1px solid rgba(251,191,36,0.4)",borderRadius:9,padding:"10px 12px",fontSize:11.5,color:"#FBBF24",lineHeight:1.6,whiteSpace:"pre-wrap"}}>
                  {bulkProg.why}
                </div>
                <button onClick={()=>setBulkProg(null)}
                  style={{marginTop:12,width:"100%",background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:9,padding:"9px",fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {expandedCard && (()=>{
        const c = expandedCard;
        const wc = WEAPON_COLORS[canonWeapon(c.weapon)] || "#E8317A";
        const bio = getPlayerNote(c.hero);
        const ath = c.inspiredBy || c.athlete;
        const sport = ath ? athleteSport(ath) : null;
        const sportEmoji={"MLB":"⚾","NFL":"🏈","NBA":"🏀","WNBA":"🏀","NHL":"🏒","PGA":"⛳","WTA":"🎾","ATP":"🎾","Boxing":"🥊","MMA":"🥊","MLS":"⚽","USMNT":"⚽","USWNT":"⚽","Music":"🎤","Acting":"🎬"};
        const emoji = sport ? (sport.split(/[\/\s]/).map(s=>sportEmoji[s]).find(Boolean)||"🏅") : "🏅";
        // The expanded modal had its OWN hardcoded SKU_MAP lookup, bypassing the live per-set
        // map entirely — so editing "Found In" updated the tiles but never this. Same helper now.
        const foundPacks = foundInFor(c, foundInMap);
        const _tl = (c.treatment||"").toLowerCase();
        const isDualTreatment = _tl.includes("paper") && (_tl.includes("battlefoil")||_tl.includes("foil"));
        const showFoil = isDualTreatment && modalFoilView === "foil";
        const isSecret1of1 = c.notation === "Secret 1/1";
        const isSuper = (c.weapon||"").toUpperCase() === "SUPER";
        const is1of1 = isSecret1of1 || isSuper;
        const claim1of1 = isSecret1of1
          ? (oneOfOneClaims||[]).find(cl => cl.cardId === c.id || cl.id === c.id)
          : isSuper
            ? (superClaims||[]).find(cl => cl.cardId === c.id || cl.id === c.id)
            : null;
        const claimVerified = claim1of1 && claim1of1.status === "verified";
        const claimPending = claim1of1 && claim1of1.status === "pending";
        const oneOfOneLabel = isSuper ? "⭐ Super 1/1" : "💎 Secret 1/1";
        const claimerName = claim1of1 ? (claim1of1.submitterName || claim1of1.userName || "Anonymous") : "";
        // My own scanned photos for this card (private — sourced from my lots, never shown to others).
        // Official admin imageUrl always takes priority; these only fill in when there's no official image.
        const myPhotos = (lots||[]).filter(l => l.cardId === c.id && l.photoUrl).map(l => l.photoUrl);
        const safePhotoIdx = myPhotos.length ? Math.min(myPhotoIdx, myPhotos.length-1) : 0;
        const hasMyPhotos = myPhotos.length > 0;
        // Show my own scan when there's no official art, OR when I've toggled to "my copy".
        const showMyPhoto = hasMyPhotos && (!c.imageUrl || viewMyScan);
        return (
          <div onClick={()=>setExpandedCard(null)} style={{ position:"fixed", inset:0, zIndex:12000, background:"rgba(0,0,0,0.85)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
            <div onClick={e=>e.stopPropagation()} style={{ display:"flex", flexWrap:"wrap", gap:24, maxWidth:980, width:"100%", maxHeight:"90vh", background:"linear-gradient(160deg,#16161f,#0d0d12)", border:`1.5px solid ${wc}44`, borderRadius:18, padding:24, boxShadow:`0 24px 80px rgba(0,0,0,0.7)`, overflowY:"auto", position:"relative" }}>
              <button onClick={()=>setExpandedCard(null)} style={{ position:"absolute", top:14, right:16, background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", color:"#fff", borderRadius:8, width:34, height:34, fontSize:18, cursor:"pointer", fontFamily:"inherit", zIndex:2 }}>×</button>
              {/* Big image */}
              {/* Top-aligned, not centred. The columns are a wrapping flex row, so when the right
                  side grows (per-copy rows, deck notes, kid assignment) the row gets taller and a
                  CENTRED image drifts down — which reads as "the image moved because I own more". */}
              <div style={{ flex:"1 1 320px", minWidth:280, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-start", gap:12 }}>
                {showMyPhoto ? (
                  <div style={{ position:"relative", width:"100%", maxWidth:420, borderRadius:14, overflow:"hidden", boxShadow:`0 12px 50px ${wc}33` }}>
                    <img src={myPhotos[safePhotoIdx]} alt={c.hero} style={{ width:"100%", display:"block", aspectRatio:"3/4", objectFit:"cover" }}/>
                    {/* "Your scan" badge so it's clear this is your private photo, not the official art */}
                    <div style={{ position:"absolute", top:10, left:10, background:"rgba(0,0,0,0.7)", border:"1px solid rgba(74,222,128,0.5)", color:"#4ade80", borderRadius:8, padding:"4px 10px", fontSize:11, fontWeight:800, letterSpacing:0.5, backdropFilter:"blur(4px)" }}>📸 Your scan{myPhotos.length>1?` · ${safePhotoIdx+1}/${myPhotos.length}`:""}</div>
                    {myPhotos.length > 1 && (
                      <>
                        <button onClick={e=>{ e.stopPropagation(); setMyPhotoIdx(i => (i-1+myPhotos.length)%myPhotos.length); }} style={{ position:"absolute", top:"50%", left:8, transform:"translateY(-50%)", background:"rgba(0,0,0,0.6)", border:"1px solid rgba(255,255,255,0.2)", color:"#fff", borderRadius:"50%", width:38, height:38, fontSize:20, fontWeight:900, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(4px)" }}>‹</button>
                        <button onClick={e=>{ e.stopPropagation(); setMyPhotoIdx(i => (i+1)%myPhotos.length); }} style={{ position:"absolute", top:"50%", right:8, transform:"translateY(-50%)", background:"rgba(0,0,0,0.6)", border:"1px solid rgba(255,255,255,0.2)", color:"#fff", borderRadius:"50%", width:38, height:38, fontSize:20, fontWeight:900, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(4px)" }}>›</button>
                        <div style={{ position:"absolute", bottom:10, left:0, right:0, display:"flex", justifyContent:"center", gap:6, pointerEvents:"none" }}>
                          {myPhotos.map((_,i)=>(
                            <div key={i} style={{ width:7, height:7, borderRadius:"50%", background:i===safePhotoIdx?"#4ade80":"rgba(255,255,255,0.4)" }}/>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : c.imageUrl ? (
                  <div style={{ position:"relative", width:"100%", maxWidth:420, borderRadius:14, overflow:"hidden", boxShadow:`0 12px 50px ${wc}33` }}>
                    <img src={c.imageUrl} alt={c.hero} style={{ width:"100%", display:"block", aspectRatio:"3/4", objectFit:"cover" }}/>
                    {showFoil && (
                      <>
                        <div style={{ position:"absolute", inset:0, background:"linear-gradient(115deg, transparent 10%, rgba(255,255,255,0.25) 22%, rgba(255,220,100,0.45) 35%, rgba(100,255,180,0.4) 48%, rgba(100,200,255,0.45) 60%, rgba(200,100,255,0.42) 72%, rgba(255,100,180,0.4) 84%, transparent 95%)", backgroundSize:"250% 250%", mixBlendMode:"screen", pointerEvents:"none", animation:"foilSheen 3s linear infinite" }}/>
                        <div style={{ position:"absolute", inset:0, background:"repeating-linear-gradient(115deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0) 3px, rgba(255,255,255,0.06) 6px)", mixBlendMode:"overlay", pointerEvents:"none" }}/>
                      </>
                    )}
                  </div>
                ) : (
                  <div style={{ width:"100%", maxWidth:420, aspectRatio:"3/4", borderRadius:14, background:"var(--bz-s1)", border:"2px dashed #333", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", color:"rgba(255,255,255,0.3)" }}>
                    <div style={{ fontSize:48, marginBottom:8 }}>🃏</div>
                    <div style={{ fontSize:12, fontWeight:800, letterSpacing:1.5, textTransform:"uppercase" }}>Image coming soon</div>
                  </div>
                )}
                {/* Physical copy count. A stack of card edges under the art conveys "you hold four of
                    these" instantly, in a way a number does not. Capped at 5 edges because beyond that
                    the stack stops being readable; the exact count is spelled out below anyway. */}
                {(() => {
                  const q = parseInt(owned[c.id]) || 0;
                  if (q < 2) return null;
                  const edges = Math.min(q - 1, 5);
                  return (
                    <div style={{ position:"relative", width:"100%", maxWidth:420, height:0, order:2 }}>
                      {Array.from({length:edges}).map((_,k)=>(
                        <div key={k} aria-hidden="true" style={{
                          position:"absolute", left:(k+1)*5, right:-(k+1)*5, top:(k+1)*4,
                          height:7, borderRadius:"0 0 14px 14px",
                          background:`rgba(74,222,128,${0.22 - k*0.035})`,
                          border:"1px solid rgba(74,222,128,0.3)", borderTop:"none",
                        }}/>
                      ))}
                    </div>
                  );
                })()}
                {c.imageUrl && hasMyPhotos && (
                  <div style={{ display:"flex", gap:6, background:"rgba(0,0,0,0.4)", borderRadius:12, padding:5, border:"1px solid rgba(255,255,255,0.1)" }}>
                    <button onClick={()=>setViewMyScan(false)} style={{ background:!viewMyScan?"linear-gradient(135deg,#E8317A,#7B2FF7)":"transparent", color:!viewMyScan?"#fff":"rgba(255,255,255,0.55)", border:"none", borderRadius:8, padding:"8px 18px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>🖼 Official</button>
                    <button onClick={()=>setViewMyScan(true)} style={{ background:viewMyScan?"linear-gradient(135deg,#4ade80,#22c55e)":"transparent", color:viewMyScan?"#062b13":"rgba(255,255,255,0.55)", border:"none", borderRadius:8, padding:"8px 18px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>📸 My copy{myPhotos.length>1?` (${myPhotos.length})`:""}</button>
                  </div>
                )}
                {isDualTreatment && c.imageUrl && (
                  <div style={{ display:"flex", gap:6, background:"rgba(0,0,0,0.4)", borderRadius:12, padding:5, border:"1px solid rgba(255,255,255,0.1)" }}>
                    <button onClick={()=>setModalFoilView("paper")} style={{ background:modalFoilView==="paper"?"linear-gradient(135deg,#E8317A,#7B2FF7)":"transparent", color:modalFoilView==="paper"?"#fff":"rgba(255,255,255,0.55)", border:"none", borderRadius:8, padding:"8px 18px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>📄 Paper</button>
                    <button onClick={()=>setModalFoilView("foil")} style={{ background:modalFoilView==="foil"?"linear-gradient(135deg,#FBBF24,#E8317A)":"transparent", color:modalFoilView==="foil"?"#fff":"rgba(255,255,255,0.55)", border:"none", borderRadius:8, padding:"8px 18px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>✨ Battlefoil</button>
                  </div>
                )}
                {_cardAdmin && (
                  <div style={{ display:"flex", gap:8, marginTop:2 }}>
                    <label onClick={e=>e.stopPropagation()} style={{ background:"rgba(74,222,128,0.1)", border:"1px solid rgba(74,222,128,0.35)", color:"#4ade80", borderRadius:9, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                      🖼 {c.imageUrl?"Replace Image":"Add Image"}
                      <input type="file" accept="image/*" style={{ display:"none" }} onChange={e=>{ const f=e.target.files?.[0]; if(f){ handleCardImageUpload(c,f); } e.target.value=""; }}/>
                    </label>
                    {c.imageUrl && (
                      <button onClick={()=>{ if(window.confirm(`Clear the image on ${c.hero}${c.cardNum?` #${c.cardNum}`:""}? This removes the wrong image so you can add the right one.`)){ handleCardImageClear(c); setExpandedCard({...c, imageUrl:null}); } }} style={{ background:"rgba(251,191,36,0.1)", border:"1px solid rgba(251,191,36,0.4)", color:"#FBBF24", borderRadius:9, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>🧹 Clear Image</button>
                    )}
                    <button onClick={()=>{ setCardEditDraft({ hero:c.hero||"", power:c.power!=null?String(c.power):"", weapon:c.weapon||"", treatment:c.treatment||"", cardNum:c.cardNum||"", setName:c.setName||"", notation:c.notation||"", variation:c.variation||"", inspiredBy:c.inspiredBy||c.athlete||"", isError:!!c.isError, errorNote:c.errorNote||"" }); setCardEditMode(m=>!m); }} style={{ background:"rgba(123,156,255,0.1)", border:"1px solid rgba(123,156,255,0.4)", color:"#7B9CFF", borderRadius:9, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{cardEditMode?"✕ Cancel Edit":"✏️ Edit Info"}</button>
                    {/* TEMP DIAGNOSTIC — reports what is actually stored for this card, so we can see
                        whether isError reached Firestore, the snapshot, or neither. */}
                    <button onClick={async()=>{
                      try {
                        const live = await getDoc(doc(db,"boba_checklist", c.fsId || c.id));
                        const d = live.exists() ? live.data() : null;
                        let snapVal = "(not checked)";
                        try {
                          const all = await readCardSnapshot(true);
                          const hit = (all||[]).find(x=>x.id===(c.fsId||c.id));
                          snapVal = hit ? String(hit.isError) : "(card not in snapshot)";
                        } catch(e){ snapVal = "(snapshot read failed: "+(e?.message||e)+")"; }
                        alert(
                          "CARD: " + (c.hero||"?") + "\ndoc id used: " + (c.fsId || c.id) +
                          "\n\nFIRESTORE isError: " + (d ? String(d.isError) : "(no document!)") +
                          "\nFIRESTORE power: " + (d ? String(d.power) : "-") +
                          "\nFIRESTORE originalPower: " + (d ? String(d.originalPower) : "-") +
                          "\n\nSNAPSHOT isError: " + snapVal +
                          "\n\nIN-APP c.isError: " + String(c.isError)
                        );
                      } catch(e){ alert("Diagnostic failed: "+(e?.message||e)); }
                    }} style={{ background:"transparent", border:"1px dashed rgba(255,255,255,0.25)", color:"rgba(255,255,255,0.5)", borderRadius:9, padding:"8px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>🔍 Debug flag</button>
                  </div>
                )}
                {_cardAdmin && cardEditMode && (
                  <div onClick={e=>e.stopPropagation()} style={{ width:"100%", maxWidth:420, background:"rgba(123,156,255,0.05)", border:"1px solid rgba(123,156,255,0.25)", borderRadius:12, padding:14, marginTop:4, display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                    {[
                      {k:"hero",label:"Hero Name",full:true},
                      {k:"power",label:"⚡ Power",type:"number"},
                      {k:"cardNum",label:"Card #"},
                      {k:"weapon",label:"Weapon"},
                      {k:"treatment",label:"Treatment"},
                      {k:"setName",label:"Set Name",full:true},
                      {k:"notation",label:"Notation"},
                      {k:"variation",label:"Variation"},
                      {k:"inspiredBy",label:"Inspired By",full:true},
                    ].map(f=>(
                      <div key={f.k} style={{ gridColumn: f.full?"1 / -1":"auto" }}>
                        <label style={{ fontSize:10, color:"#7B9CFF", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, display:"block", marginBottom:3 }}>{f.label}</label>
                        <input type={f.type||"text"} value={cardEditDraft[f.k]??""} onChange={e=>setCardEditDraft(d=>({...d,[f.k]:e.target.value}))} style={{ width:"100%", background:"#0d0d12", border:"1px solid rgba(255,255,255,0.15)", color:"#fff", borderRadius:7, padding:"7px 9px", fontSize:13, fontFamily:"inherit", boxSizing:"border-box" }}/>
                      </div>
                    ))}
                    {/* ERROR CARD. Lives here rather than in the bulk tool because flagging a misprint
                        or a buff/nerf is a per-card judgement, made while you're looking at the card.
                        originalPower is captured on save the first time power actually changes, so the
                        badge can show "was 150" without anyone typing it. */}
                    <div style={{ gridColumn:"1 / -1", borderTop:"1px solid rgba(255,255,255,0.1)", paddingTop:10, marginTop:2 }}>
                      <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:12.5, fontWeight:700, color: cardEditDraft.isError ? "#FBBF24" : "rgba(255,255,255,0.6)" }}>
                        <input type="checkbox" checked={!!cardEditDraft.isError}
                          onChange={e=>setCardEditDraft(d=>({...d,isError:e.target.checked}))}
                          style={{ accentColor:"#FBBF24", width:16, height:16 }}/>
                        {"\u26A0\uFE0F"} Error card — show a public badge
                      </label>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", marginTop:4, lineHeight:1.45 }}>
                        For misprints, or when power has been officially buffed or nerfed. If you change Power above, the original is recorded automatically and shown as “was {c.originalPower!=null ? c.originalPower : (c.power!=null?c.power:"—")}”.
                      </div>
                    </div>
                    <div style={{ gridColumn:"1 / -1" }}>
                      <label style={{ fontSize:10, color:"#7B9CFF", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, display:"block", marginBottom:3 }}>Error note (public)</label>
                      <input value={cardEditDraft.errorNote??""} onChange={e=>setCardEditDraft(d=>({...d,errorNote:e.target.value}))}
                        placeholder="e.g. Misprint — power corrected per BoBA ruling"
                        style={{ width:"100%", background:"#0d0d12", border:"1px solid rgba(255,255,255,0.15)", color:"#fff", borderRadius:7, padding:"7px 9px", fontSize:13, fontFamily:"inherit", boxSizing:"border-box" }}/>
                    </div>
                    <button onClick={()=>{ const upd={...cardEditDraft}; if(upd.power!=="")upd.power=parseInt(upd.power)||0; handleCardFieldEdit(c, upd); }} style={{ gridColumn:"1 / -1", background:"linear-gradient(135deg,#7B9CFF,#7B2FF7)", color:"#fff", border:"none", borderRadius:9, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit", marginTop:4 }}>💾 Save Changes</button>
                  </div>
                )}
              </div>
              {/* Info panel */}
              <div style={{ flex:"1 1 320px", minWidth:280, display:"flex", flexDirection:"column", gap:12 }}>
                <div>
                  <div style={{ fontSize:13, color:wc, fontWeight:700, marginBottom:2 }}>{c.setName||""}</div>
                  <div style={{ fontSize:30, fontWeight:900, color:"#fff", lineHeight:1.05 }}>{c.hero}</div>
                  {ath && <div style={{ fontSize:14, color:"#999", marginTop:4 }}>🏅 Inspired by <strong style={{ color:"#ccc" }}>{ath}</strong>{sport && <span style={{ display:"inline-flex", alignItems:"center", gap:3, marginLeft:8, background:"rgba(232,49,122,0.12)", border:"1px solid rgba(232,49,122,0.3)", borderRadius:6, padding:"1px 8px", fontSize:12, fontWeight:700, color:"#E8317A" }}>{emoji} {sport}</span>}</div>}
                </div>
                {/* Error-card flag. This existed only on the small card's BACK face, so opening the
                    full detail view \u2014 the place you go precisely to check a card \u2014 showed nothing. If a
                    card is a known misprint or has had its power corrected, that belongs here. */}
                {/* Borrow / Lend — open the full loan flow (friend picker or free-text,
                    plus a return-by date), pre-loaded with this card. */}
                {user && (
                  <button onClick={()=>{ setScanLoanMode("borrowed"); setLoanScanCard({ card:c, mode:"borrowed" }); setExpandedCard(null); }}
                    style={{width:"100%",background:"rgba(123,156,255,0.12)",border:"1px solid rgba(123,156,255,0.45)",
                    color:"#7B9CFF",borderRadius:9,padding:"9px",fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                    {"\uD83D\uDCE5"} I want to borrow this card
                  </button>
                )}
                {user && owned[c.id] && (
                  <button onClick={()=>{ setScanLoanMode("lent"); setLoanScanCard({ card:c, mode:"lent" }); setExpandedCard(null); }}
                    style={{width:"100%",marginTop:8,background:"rgba(251,191,36,0.12)",border:"1px solid rgba(251,191,36,0.45)",
                    color:"#FBBF24",borderRadius:9,padding:"9px",fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                    {"\uD83D\uDCE4"} Loan this card out
                  </button>
                )}
                {(() => {
                  // Lending status. Shown prominently because it answers "why can I not deck this?"
                  // and "who has my card?" \u2014 both of which are otherwise invisible.
                  const mine = (lots||[]).filter(l => l.cardId === c.id && l.lendState);
                  if (!mine.length) return null;
                  return (
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {mine.map((l,i)=>{
                        const out = l.lendState === "lent";
                        const col = out ? "#FBBF24" : "#7B9CFF";
                        return (
                          <div key={i} style={{background:col+"1a",border:`1px solid ${col}66`,borderRadius:10,padding:"8px 11px"}}>
                            <div style={{fontSize:12,fontWeight:800,color:col}}>
                              {out ? "\uD83D\uDCE4 Lent out" : "\uD83D\uDCE5 Borrowed"}
                              {l.lendWho ? (out ? ` to ${l.lendWho}` : ` from ${l.lendWho}`) : ""}
                            </div>
                            {l.lendWhy && <div style={{fontSize:11,color:"var(--bz-ink-2)",marginTop:2}}>{l.lendWhy}</div>}
                            {l.lendDate && <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:2}}>Since {l.lendDate}</div>}
                            {out && <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:3}}>This copy is excluded from deck building.</div>}
                            {out && (
                              <button onClick={async (e)=>{ e.stopPropagation(); await markCardReturned(c.id); setToast("\u2713 Marked as back in hand"); }}
                                style={{marginTop:7,background:"rgba(74,222,128,0.15)",border:"1px solid rgba(74,222,128,0.45)",
                                        color:"#4ade80",borderRadius:7,padding:"5px 12px",fontSize:11,fontWeight:800,
                                        cursor:"pointer",fontFamily:"inherit"}}>
                                Got it back
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
                {(() => {
                  // Version breakdown for multi-print cards: the grid shows one tile, so this is the
                  // only place the Alpha / Alpha Update split is visible.
                  const vspec = versionSpecFor(c);
                  if (!vspec || !owned[c.id]) return null;
                  const mine = (lots||[]).filter(l => l.cardId === c.id);
                  const counts = {};
                  vspec.options.forEach(o => counts[o] = 0);
                  let unknown = 0;
                  mine.forEach(l => { if (l.version && counts[l.version]!=null) counts[l.version]++; else unknown++; });
                  const untracked = Math.max(0, (parseInt(owned[c.id])||0) - mine.length) + unknown;
                  return (
                    <div style={{ background:"rgba(123,156,255,0.08)", border:"1px solid rgba(123,156,255,0.3)", borderRadius:10, padding:"9px 12px" }}>
                      <div style={{ fontSize:11, fontWeight:800, color:"#7B9CFF", marginBottom:5 }}>{vspec.label}</div>
                      <div style={{ display:"flex", gap:14, flexWrap:"wrap", fontSize:12.5, color:"var(--bz-ink)" }}>
                        {vspec.options.map(o=>(
                          <span key={o}><strong style={{color:counts[o]?"#fff":"rgba(255,255,255,0.35)"}}>{counts[o]}</strong> {o}</span>
                        ))}
                        {untracked>0 && <span style={{color:"rgba(255,255,255,0.4)"}}>{untracked} not recorded</span>}
                      </div>
                      {untracked>0 && (
                        <div style={{ fontSize:10.5, color:"rgba(255,255,255,0.35)", marginTop:4 }}>
                          Set the print on each copy under {"\uD83D\uDCB0"} Details.
                        </div>
                      )}
                    </div>
                  );
                })()}
                {c.isError && (
                  <div style={{ display:"flex", alignItems:"flex-start", gap:8, background:"rgba(251,191,36,0.1)", border:"1px solid rgba(251,191,36,0.4)", borderRadius:10, padding:"9px 12px" }}>
                    <span style={{ fontSize:14, lineHeight:1.3 }}>{"\u26A0\uFE0F"}</span>
                    <div>
                      <div style={{ fontSize:12.5, fontWeight:900, color:"#FBBF24" }}>Error card</div>
                      {c.errorNote && <div style={{ fontSize:11.5, color:"rgba(255,255,255,0.65)", lineHeight:1.55, marginTop:2 }}>{c.errorNote}</div>}
                      {c.originalPower!=null && c.originalPower!=="" && String(c.originalPower)!==String(c.power) && (
                        <div style={{ fontSize:11, color:"rgba(255,255,255,0.45)", marginTop:3 }}>
                          Power was {c.originalPower}, now {c.power}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {c.power && <div style={{ fontSize:15, fontWeight:900, color:wc, background:wc+"18", border:`1px solid ${wc}44`, borderRadius:8, padding:"4px 12px" }}>{c.power}⚡ POWER</div>}
                  {c.dbs!==undefined && c.dbs!=="" && <div style={{ fontSize:15, fontWeight:900, color:"#C084FC", background:"rgba(192,132,252,0.12)", border:"1px solid #C084FC44", borderRadius:8, padding:"4px 12px" }}>DBS {c.dbs}</div>}
                  {hasPlayCost(c) && <div style={{ fontSize:15, fontWeight:900, color:"#FBBF24", background:"rgba(251,191,36,0.12)", border:"1px solid #FBBF2444", borderRadius:8, padding:"4px 12px" }}>Cost {c.playCost}</div>}
                  {c.cardNum && <div style={{ fontSize:13, fontWeight:700, color:"#aaa", background:"var(--bz-s2)", borderRadius:8, padding:"5px 12px" }}>#{c.cardNum}</div>}
                </div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {c.weapon && <span style={{ fontSize:12, color:wc, background:wc+"22", borderRadius:6, padding:"3px 10px", fontWeight:700 }}>{c.weapon}</span>}
                  {c.treatment && <span style={{ fontSize:12, color:"#bbb", background:"var(--bz-s2)", borderRadius:6, padding:"3px 10px" }}>{c.treatment}</span>}
                  {c.notation && <span style={{ fontSize:12, color:"#FBBF24", background:"#FBBF2422", borderRadius:6, padding:"3px 10px", fontWeight:700 }}>{c.notation}</span>}
                  {c.variation && <span style={{ fontSize:12, color:"var(--bz-ink-2)", background:"var(--bz-s2)", borderRadius:6, padding:"3px 10px" }}>{c.variation}</span>}
                </div>
                {is1of1 && (
                  <div style={{ background: claimVerified ? "rgba(147,51,234,0.12)" : "rgba(74,222,128,0.08)", border:`1.5px solid ${claimVerified ? "#9333EA" : claimPending ? "#FBBF24" : "#4ade80"}66`, borderRadius:12, padding:"14px 16px" }}>
                    <div style={{ fontSize:11, fontWeight:800, letterSpacing:1.5, textTransform:"uppercase", color:"#9333EA", marginBottom:8 }}>{oneOfOneLabel} · One of One</div>
                    {claimVerified ? (
                      <>
                        <div style={{ fontSize:16, fontWeight:900, color:"#c084fc", marginBottom:4 }}>🏆 Claimed</div>
                        <div style={{ fontSize:13, color:"#ddd" }}>Pulled by <strong style={{ color:"#fff" }}>{claimerName}</strong>{claim1of1.dateHit?` · ${claim1of1.dateHit}`:""}</div>
                        {claim1of1.story && <div style={{ fontSize:12, color:"#999", marginTop:8, lineHeight:1.5, fontStyle:"italic" }}>"{claim1of1.story}"</div>}
                        {claim1of1.photoUrl && <img src={claim1of1.photoUrl} alt="claim proof" style={{ width:"100%", maxWidth:200, borderRadius:8, marginTop:10, display:"block" }}/>}
                        {(swancity || _cardAdmin) && (
                          <label onClick={e=>e.stopPropagation()} style={{ display:"inline-block", marginTop:10, background:"rgba(192,132,252,0.12)", border:"1px solid rgba(192,132,252,0.4)", color:"#c084fc", borderRadius:9, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:resubmitting?"wait":"pointer", fontFamily:"inherit" }}>
                            {resubmitting ? "Uploading…" : "📸 Re-submit better photo"}
                            <input type="file" accept="image/*" style={{ display:"none" }} disabled={resubmitting} onChange={e=>{ const f=e.target.files?.[0]; if(f){ resubmitClaimPhoto(isSecret1of1?"oneof1_claims":"super_claims", c, f); } e.target.value=""; }}/>
                          </label>
                        )}
                      </>
                    ) : claimPending ? (
                      <>
                        <div style={{ fontSize:16, fontWeight:900, color:"#FBBF24", marginBottom:4 }}>⏳ Claim Pending</div>
                        <div style={{ fontSize:13, color:"#bbb" }}>Someone reported hitting this — verification in progress.</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize:16, fontWeight:900, color:"#4ade80", marginBottom:4 }}>🔓 Unclaimed</div>
                        <div style={{ fontSize:13, color:"#bbb" }}>This one-of-one hasn't been hit yet. Could be out there waiting…</div>
                      </>
                    )}
                  </div>
                )}
                {bio && (bio.team||bio.notes) && (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px" }}>
                    {bio.team && <div style={{ fontSize:13, fontWeight:800, color:"#7B9CFF", marginBottom:6 }}>🏟️ {bio.team}</div>}
                    {bio.notes && <div style={{ fontSize:13, color:"#bbb", lineHeight:1.6 }}>{bio.notes}</div>}
                  </div>
                )}
                {foundPacks.length>0 && <div style={{ display:"inline-flex", alignItems:"center", gap:6, flexWrap:"wrap", alignSelf:"flex-start" }}><span style={{ color:"#a0a0a0", fontSize:12, fontWeight:700 }}>Found In:</span>{foundPacks.map(pk => <span key={pk.id} style={{ background:`${pk.color}1a`, border:`1px solid ${pk.color}44`, borderRadius:8, padding:"4px 12px", fontSize:12, fontWeight:700, color:pk.color }}>{pk.label}</span>)}</div>}
                <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:4 }}>
                  {user && (isDualTreatment ? (
                    <div style={{ flex:1, minWidth:200, display:"flex", flexDirection:"column", gap:6 }}>
                      <div style={{ fontSize:10, fontWeight:800, color:"#a0a0a0", textTransform:"uppercase", letterSpacing:1 }}>Add to collection — choose finish</div>
                      <div style={{ display:"flex", gap:6 }}>
                        {owned[c.id] ? (
                          <div style={{ flex:1, display:"flex", alignItems:"center", background:"rgba(74,222,128,0.15)", border:"1.5px solid #4ade80", borderRadius:10, overflow:"hidden" }}>
                            <button onClick={()=>setOwnedQty(c.id,Math.max(0,(owned[c.id]||1)-1))} style={{ background:"transparent",border:"none",color:"#4ade80",fontSize:18,fontWeight:900,cursor:"pointer",padding:"8px 12px",fontFamily:"inherit",lineHeight:1 }}>−</button>
                            <div style={{ flex:1,textAlign:"center",color:"#4ade80",fontSize:12,fontWeight:800 }}>📄{owned[c.id]>1?` ×${owned[c.id]}`:""}</div>
                            <button onClick={()=>setOwnedQty(c.id,(owned[c.id]||1)+1)} style={{ background:"transparent",border:"none",color:"#4ade80",fontSize:18,fontWeight:900,cursor:"pointer",padding:"8px 12px",fontFamily:"inherit",lineHeight:1 }}>+</button>
                          </div>
                        ) : (
                          <button onClick={()=>toggleOwned(c.id)} style={{ flex:1, background:"rgba(255,255,255,0.04)", border:"1.5px solid #333", color:"#ccc", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>📄 Add Paper</button>
                        )}
                        {owned[c.id+"::foil"] ? (
                          <div style={{ flex:1, display:"flex", alignItems:"center", background:"rgba(251,191,36,0.15)", border:"1.5px solid #FBBF24", borderRadius:10, overflow:"hidden" }}>
                            <button onClick={()=>setOwnedQty(c.id+"::foil",Math.max(0,(owned[c.id+"::foil"]||1)-1))} style={{ background:"transparent",border:"none",color:"#FBBF24",fontSize:18,fontWeight:900,cursor:"pointer",padding:"8px 12px",fontFamily:"inherit",lineHeight:1 }}>−</button>
                            <div style={{ flex:1,textAlign:"center",color:"#FBBF24",fontSize:12,fontWeight:800 }}>✨{owned[c.id+"::foil"]>1?` ×${owned[c.id+"::foil"]}`:""}</div>
                            <button onClick={()=>setOwnedQty(c.id+"::foil",(owned[c.id+"::foil"]||1)+1)} style={{ background:"transparent",border:"none",color:"#FBBF24",fontSize:18,fontWeight:900,cursor:"pointer",padding:"8px 12px",fontFamily:"inherit",lineHeight:1 }}>+</button>
                          </div>
                        ) : (
                          <button onClick={()=>toggleOwned(c.id+"::foil")} style={{ flex:1, background:"rgba(255,255,255,0.04)", border:"1.5px solid #333", color:"#ccc", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>✨ Add Battlefoil</button>
                        )}
                      </div>
                    </div>
                  ) : (
                    owned[c.id] ? (
                      <div style={{ flex:1, minWidth:120, display:"flex", alignItems:"center", justifyContent:"center", gap:0, background:"rgba(74,222,128,0.15)", border:"1.5px solid #4ade80", borderRadius:10, overflow:"hidden" }}>
                        <button onClick={()=>setOwnedQty(c.id, Math.max(0,(owned[c.id]||1)-1))} title="Remove one" style={{ background:"transparent", border:"none", color:"#4ade80", fontSize:20, fontWeight:900, cursor:"pointer", padding:"8px 16px", fontFamily:"inherit", lineHeight:1 }}>−</button>
                        <div style={{ flex:1, textAlign:"center", color:"#4ade80", fontSize:13, fontWeight:800 }}>{owned[c.id]>1?`✅ Owned ×${owned[c.id]}`:"✅ Owned"}</div>
                        <button onClick={()=>setOwnedQty(c.id,(owned[c.id]||1)+1)} title="Add another copy" style={{ background:"transparent", border:"none", color:"#4ade80", fontSize:20, fontWeight:900, cursor:"pointer", padding:"8px 16px", fontFamily:"inherit", lineHeight:1 }}>+</button>
                      </div>
                    ) : (
                      <button onClick={()=>toggleOwned(c.id)} style={{ flex:1, minWidth:120, background:"rgba(255,255,255,0.04)", border:"1.5px solid #333", color:"#ccc", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>+ Add to Collection</button>
                    )
                  ))}
                  <button onClick={()=>toggleWant(c.id)} style={{ flex:1, minWidth:120, background:wantList[c.id]?"#1a0f00":"rgba(255,255,255,0.04)", border:`1.5px solid ${wantList[c.id]?"#FBBF24":"#333"}`, color:wantList[c.id]?"#FBBF24":"#ccc", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>{wantList[c.id]?"🎯 Wanted":"+ Want"}</button>
                </div>

                {/* In-transit: warn if one's already on the way, and let you mark/unmark */}
                {inTransit[c.id] ? (
                  <div style={{ marginTop:12, background:"rgba(96,165,250,0.12)", border:"1.5px solid #60A5FA", borderRadius:10, padding:"12px 14px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                      <span style={{ fontSize:15 }}>🚚</span>
                      <span style={{ fontSize:14, fontWeight:900, color:"#60A5FA" }}>{inTransit[c.id].qty>1?`${inTransit[c.id].qty} on the way!`:"One's already on the way!"}</span>
                    </div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.6)", marginBottom:10 }}>Marked {inTransit[c.id].date}{inTransit[c.id].note?` · ${inTransit[c.id].note}`:""} — don't double-buy.</div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={()=>markTransitArrived(c.id)} style={{ flex:1, background:"rgba(74,222,128,0.15)", border:"1px solid rgba(74,222,128,0.5)", color:"#4ade80", borderRadius:8, padding:"7px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>✅ It arrived</button>
                      <button onClick={()=>setTransit(c.id,null)} style={{ flex:1, background:"transparent", border:"1px solid rgba(255,255,255,0.2)", color:"rgba(255,255,255,0.6)", borderRadius:8, padding:"7px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Cancel order</button>
                    </div>
                  </div>
                ) : (
                  <>
                  <button onClick={()=>{ const from=window.prompt("Who is it coming from? (seller / @handle) — leave blank to skip:","")||""; const note=window.prompt("Optional note (ETA, tracking…). Leave blank to skip:","")||""; const qtyStr=window.prompt("How many are on the way?","1"); const qty=Math.max(1,parseInt(qtyStr)||1); setTransit(c.id,{qty,from,note}); }} style={{ width:"100%", marginTop:12, background:"rgba(96,165,250,0.1)", border:"1.5px solid rgba(96,165,250,0.4)", color:"#60A5FA", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>🚚 Mark as On the Way (bought, incoming)</button>
                  <button onClick={()=>setCompCard(c)} style={{ width:"100%", marginTop:10, background:"rgba(123,156,255,0.12)", border:"1.5px solid rgba(123,156,255,0.45)", color:"#7B9CFF", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>📊 View Sold Comps (eBay + in-app)</button>
                  {owned[c.id] && (<>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:10 }}>
                    <button onClick={()=>togglePrivate(c.id)} title={publicCards[c.id]?"Shown on your public profile — click to hide":"Only you can see this — click to show it off"} style={{ background:publicCards[c.id]?"rgba(74,222,128,0.14)":"rgba(251,191,36,0.1)", border:`1.5px solid ${publicCards[c.id]?"#4ade80":"rgba(251,191,36,0.55)"}`, color:publicCards[c.id]?"#4ade80":"#FBBF24", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>{publicCards[c.id]?"👁 Public":"🔒 Private"}</button>
                    {myListings.find(l=>l.cardId===c.id) ? (
                      <button disabled style={{ background:"rgba(74,222,128,0.08)", border:"1.5px solid rgba(74,222,128,0.3)", color:"#4ade80", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, fontFamily:"inherit", cursor:"default" }}>💰 Listed</button>
                    ) : (
                      <button onClick={()=>setListModal(c)} style={{ background:"rgba(232,49,122,0.1)", border:"1.5px solid rgba(232,49,122,0.45)", color:"#E8317A", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>💰 List for Sale</button>
                    )}
                  </div>
                  <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:6, textAlign:"center" }}>{publicCards[c.id]?"Shown on your public profile.":"Only you can see this card. Make it public to show it off."}</div>
                  </>)}
                  </>
                )}

                {/* Trade Bait flag + per-copy breakdown */}
                {owned[c.id] && (
                  <div style={{ marginTop:12 }}>
                      {/* Deck lock. If copies of this card are committed to decks — yours or a family
                          member's — say so here, where you'd be deciding to list or trade it. Better to
                          know before you flag it than to find out when the offer builder hides it. */}
                      {(() => {
                        const inDecks = deckLockedForTrade[c.id] || 0;
                        if (!inDecks) return null;
                        const qty = parseInt(owned[c.id]) || 1;
                        const free = Math.max(0, qty - inDecks);
                        const decks = deckLockNames[c.id] || [];
                        return (
                          <div style={{marginBottom:10,background:"rgba(123,156,255,0.06)",border:"1px solid rgba(123,156,255,0.25)",borderRadius:9,padding:"9px 11px",display:"flex",alignItems:"center",gap:8}}>
                            <span style={{fontSize:15}}>{"\uD83D\uDCD8"}</span>
                            <div style={{fontSize:11.5,color:"rgba(255,255,255,0.7)",lineHeight:1.5}}>
                              {inDecks >= qty
                                ? <><strong style={{color:"#7B9CFF"}}>In a deck.</strong> {qty>1?`All ${qty} copies are`:"This is"} committed to a deck (yours or family) {"\u2014"} not available to trade until freed.</>
                                : <><strong style={{color:"#7B9CFF"}}>{inDecks} of {qty} in a deck.</strong> {free} still free to trade or list.</>}
                            {decks.length > 0 && (
                              <div style={{marginTop:6,display:"flex",flexWrap:"wrap",gap:5}}>
                                {decks.map((d,di) => (
                                  <span key={di} style={{fontSize:10,fontWeight:700,color:"#7B9CFF",background:"rgba(123,156,255,0.12)",border:"1px solid rgba(123,156,255,0.28)",borderRadius:6,padding:"2px 7px"}}>
                                    {"\uD83D\uDCD8"} {d.name}{d.owner ? ` \u00b7 ${d.owner}` : ""}
                                  </span>
                                ))}
                              </div>
                            )}
                            </div>
                          </div>
                        );
                      })()}
                    {/* Whose card is it? With one copy this is a single choice. With several, each
                        copy is tagged on its own — you can own 2 and have one be a kid's. */}
                    {kidGroups.length>0 && (()=>{
                      const qty = parseInt(owned[c.id]) || 1;
                      const opts = [{id:null,name:"Mine",color:"#4ade80"}].concat(kidGroups);
                      return (
                        <div style={{ marginBottom:10 }}>
                          <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", fontWeight:700, marginBottom:6 }}>
                            {qty>1 ? `Whose are they? (you own ${qty})` : "Whose card is this?"}
                          </div>
                          {Array.from({length:qty}).map((_,i)=>(
                            <div key={i} style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:qty>1?6:0 }}>
                              {qty>1 && <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)", fontWeight:700, minWidth:52 }}>Copy {i+1}</span>}
                              {opts.map(g=>{
                                const on = kidOfCopy(c.id, i) === g.id;
                                return (
                                  <button key={g.id||"mine"} onClick={()=>assignCardToKid(c.id, g.id, i)}
                                    style={{ background:on?`${g.color}22`:"rgba(255,255,255,0.04)", border:`1.5px solid ${on?g.color:"#333"}`, color:on?g.color:"#999", borderRadius:9, padding:"6px 12px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
                                    {g.id?"🧒 ":""}{g.name}
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    {kidGroups.length===0 && (
                      <button onClick={()=>setKidsModal(true)}
                        style={{ width:"100%", background:"transparent", border:"1.5px dashed #333", color:"rgba(255,255,255,0.35)", borderRadius:10, padding:"9px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", marginBottom:10 }}>
                        🧒 Collecting with a kid? Set up their collection
                      </button>
                    )}
                    <button onClick={()=>toggleTradeBait(c.id)} style={{ width:"100%", background:tradeBait[c.id]?"rgba(251,191,36,0.15)":"rgba(255,255,255,0.04)", border:`1.5px solid ${tradeBait[c.id]?"#FBBF24":"#333"}`, color:tradeBait[c.id]?"#FBBF24":"#ccc", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>{tradeBait[c.id]?"🔁 In Trade Bait":"🔁 Flag as Trade Bait"}</button>
                    {/* Moved it on? Drops it from the active collection but keeps the record. */}
                    <button onClick={()=>{ setExpandedCard(null); setSellModal(c); }}
                      title="Sold, traded, or gave this away — keeps a record of having owned it"
                      style={{ width:"100%", marginTop:8, background:"rgba(255,255,255,0.04)", border:"1.5px solid #333", color:"#ccc", borderRadius:10, padding:"10px", fontSize:13, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
                      ↗ Sold / Traded Away
                    </button>
                    {(() => {
                      const cardLots = lotsForCard(c.id);
                      const qty = owned[c.id]||1;
                      if (cardLots.length === 0) return (
                        <div style={{ marginTop:8, textAlign:"center" }}>
                          <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginBottom:8 }}>You own {qty}. Add cost & acquisition details to track each copy.</div>
                          <button onClick={()=>setLotModal({card:c})} style={{ background:"rgba(74,222,128,0.15)", border:"1px solid rgba(74,222,128,0.5)", color:"#4ade80", borderRadius:8, padding:"7px 16px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>💰 Add copy details</button>
                        </div>
                      );
                      const fmt$ = n => n===null||n===undefined||n===""?"—":`$${(parseFloat(n)||0).toFixed(2)}`;
                      return (
                        <div style={{ marginTop:10, background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, overflow:"hidden" }}>
                          <div style={{ fontSize:10, fontWeight:800, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1, padding:"8px 12px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>Your Copies ({cardLots.length}) · tap to edit</div>
                          {cardLots.map((l,i) => (
                            <div key={l.id||i} onClick={()=>setLotModal({card:c})} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, padding:"8px 12px", borderBottom:"1px solid rgba(255,255,255,0.04)", fontSize:12, cursor:"pointer" }} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ color:"#fff", fontWeight:700 }}>
                                  Copy {i+1}
                                  {l.serial && <span style={{ color:"#FBBF24", fontWeight:900, marginLeft:6 }}>#{l.serial}</span>}
                                  {l.locked && <span title="Locked — never trade or sell" style={{ marginLeft:6 }}>{"\uD83D\uDD12"}</span>}
                                  {l.value?` · worth ${fmt$(l.value)}`:""}
                                </div>
                                {/* Per-copy detail lives on the back of the card in the modal. */}
                                {(l.gradeCompany || l.grade || l.condition) && (
                                  <div style={{ fontSize:10.5, color:"#4ade80", fontWeight:700, marginTop:2 }}>
                                    {[l.gradeCompany, l.grade].filter(Boolean).join(" ")}
                                    {(l.gradeCompany||l.grade) && l.condition ? " · " : ""}
                                    {l.condition}
                                    {l.certNum ? <span style={{ color:"rgba(255,255,255,0.4)", fontWeight:600 }}>{" · cert "+l.certNum}</span> : null}
                                  </div>
                                )}
                                {l.location && <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>{"\uD83D\uDCCD "}{l.location}</div>}
                                {l.purchaseDate && <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:2 }}>bought {l.purchaseDate}</div>}
                                {/* Legacy ID is deliberately admin-only: it means nothing to a collector,
                                    but it is the thread back to the source record when auditing a migration. */}
                                {_cardAdmin && l.legacyId && <div style={{ fontSize:9, color:"rgba(255,255,255,0.28)", marginTop:2, fontFamily:"monospace" }}>legacy: {l.legacyId}</div>}
                                <div style={{ color:"rgba(255,255,255,0.4)", fontSize:10 }}>Paid {fmt$(l.cost)}{l.method?` · ${l.method}`:""}{l.date?` · ${l.date}`:""}{l.notes?` · ${l.notes}`:""}</div>
                              </div>
                              {l.value&&l.cost&&<span style={{ fontSize:11, fontWeight:800, color:(parseFloat(l.value)-parseFloat(l.cost))>=0?"#4ade80":"#E8317A" }}>{(parseFloat(l.value)-parseFloat(l.cost))>=0?"+":""}{fmt$(parseFloat(l.value)-parseFloat(l.cost))}</span>}
                              <span style={{ fontSize:11, color:"rgba(255,255,255,0.3)" }}>✎</span>
                            </div>
                          ))}
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 12px" }}>
                            <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)", fontStyle:"italic" }}>💡 Trade the higher-value copy to keep cost low.</span>
                            <button onClick={()=>setLotModal({card:c})} style={{ background:"rgba(74,222,128,0.12)", border:"1px solid rgba(74,222,128,0.4)", color:"#4ade80", borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>+ Add copy</button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      {/* Mobile nav dropdown overlay — rendered at root so the scrolling nav can't clip it */}
      {isMobile && navMenu && navGroupItems.current[navMenu] && (
        <div onClick={()=>setNavMenu(null)} style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:90}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"calc(100% - 24px)",maxWidth:400,background:"#161616",border:"1px solid var(--bz-line-2)",borderRadius:14,boxShadow:"0 16px 48px rgba(0,0,0,0.85)",padding:8}}>
            <div style={{fontSize:11,fontWeight:800,color:"rgba(255,255,255,0.4)",letterSpacing:1,textTransform:"uppercase",padding:"8px 12px 10px"}}>{navGroupItems.current[navMenu].label}</div>
            {navGroupItems.current[navMenu].items.map(it=>(
              <button key={it.id} onClick={()=>{setActiveTab(it.id);setNavMenu(null);}} style={{
                display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",gap:10,
                background:activeTab===it.id?"rgba(232,49,122,0.14)":"transparent",border:"none",
                color:activeTab===it.id?"#E8317A":"#eee",borderRadius:10,padding:"15px 14px",fontSize:16,fontWeight:700,
                cursor:"pointer",fontFamily:"inherit",textAlign:"left",
              }}>
                <span>{it.label}</span>
                {it.badge>0&&<span style={{background:"#E8317A",color:"#fff",borderRadius:10,minWidth:20,height:20,padding:"0 6px",fontSize:11,fontWeight:900,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{it.badge}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      <style>{`
        select option { background:#1a1218 !important; color:#f6eef2 !important; }
        select { color:#f6eef2; }
        @keyframes lockPulse { 0%{transform:scale(0.5);opacity:0} 40%{transform:scale(1.3);opacity:1} 70%{transform:scale(0.95);opacity:1} 100%{transform:scale(1);opacity:1} }
        @keyframes lockFadeOut { 0%{opacity:1} 70%{opacity:1} 100%{opacity:0} }
        @keyframes cardDim { 0%{opacity:1} 30%{opacity:0.35} 100%{opacity:1} }
        @keyframes floatUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
        @keyframes glowPulse { 0%,100%{opacity:0.5} 50%{opacity:1} }
        @keyframes cardEntrance { from{opacity:0;transform:translateY(20px) scale(0.95)} to{opacity:1;transform:translateY(0) scale(1)} }
        .pub-tab:hover{background:rgba(232,49,122,0.1)!important;color:rgba(255,255,255,0.8)!important;transform:translateY(-1px)}
        .pub-card-grid > * { animation: cardEntrance 0.4s ease both; }
        .pub-card-grid.no-anim > * { animation: none !important; }
        .boba-flip-pill { opacity: 0; transform: translateY(4px); transition: opacity 0.18s ease, transform 0.18s ease; }
        .boba-card-hover:hover .boba-flip-pill { opacity: 1; transform: translateY(0); }
        .boba-card-hover:hover .boba-quickadd { opacity: 1 !important; }
        @media (hover: none) { .boba-quickadd { opacity: 1 !important; } }
        .deck-pb-cardlist > div > div:hover .deck-add-badge { opacity: 1; }
        .fan-card:hover { filter: brightness(1.12); z-index: 999 !important; box-shadow: 0 24px 70px rgba(0,0,0,0.85) !important; }
        @media (hover: none) { .boba-flip-pill { opacity: 0.55; transform: none; } }
        .pub-card-grid > *:nth-child(2n){animation-delay:0.05s}
        .pub-card-grid > *:nth-child(3n){animation-delay:0.1s}
        .pub-card-grid > *:nth-child(4n){animation-delay:0.15s}
        .market-card:hover{transform:translateY(-4px)!important;box-shadow:0 20px 60px rgba(232,49,122,0.3)!important}
        .filter-bar select,.filter-bar input{transition:border-color 0.2s ease,box-shadow 0.2s ease}
        /* Mobile filter bar. Every control was flex:1 with minWidth:140, so on a phone five dropdowns
           plus a search box plus a sort select all fought for the same row and each ended up a useless
           sliver. Two per row instead — wide enough to actually read the label. Search gets its own
           full-width row because it's the one people reach for most. */
        @media (max-width: 640px) {
          .filter-bar { padding: 12px !important; gap: 7px !important; }
          .filter-bar > div, .filter-bar > select {
            flex: 1 1 calc(50% - 4px) !important;
            min-width: 0 !important;
          }
          .filter-bar > input { flex: 1 1 100% !important; min-width: 0 !important; }
          .filter-bar button { min-height: 40px; }
          .filter-bar select { min-height: 40px; }
        }
        /* Dropdown panels: on a phone, anchor them to the screen rather than the button they hang off,
           or a right-hand dropdown opens half off the edge. */
        @media (max-width: 640px) {
          .filter-menu {
            position: fixed !important;
            left: 12px !important; right: 12px !important;
            top: auto !important;
            min-width: 0 !important;
            max-height: 50vh !important;
          }
        }
        .filter-bar select:focus,.filter-bar input:focus{border-color:#E8317A!important;box-shadow:0 0 0 3px rgba(232,49,122,0.15)!important}

        /* ── HUNT FX (mark wanted) ── */
        @keyframes fxBackdrop { 0%{opacity:0} 12%{opacity:1} 80%{opacity:1} 100%{opacity:0} }
        @keyframes huntCardIn { 0%{opacity:0;transform:translateY(40px) scale(0.7) rotate(-8deg)} 30%{opacity:1;transform:translateY(0) scale(1.05) rotate(0deg)} 75%{opacity:1;transform:scale(1)} 100%{opacity:0;transform:translateY(-30px) scale(0.9)} }
        @keyframes huntRing { 0%{transform:scale(0.3);opacity:0} 25%{opacity:0.9} 100%{transform:scale(2.4);opacity:0} }
        @keyframes huntScan { 0%{transform:translateY(-100%);opacity:0} 20%{opacity:1} 80%{opacity:1} 100%{transform:translateY(220%);opacity:0} }
        @keyframes huntReticle { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @keyframes huntText { 0%{opacity:0;transform:translateY(14px);letter-spacing:2px} 25%{opacity:1;transform:translateY(0);letter-spacing:8px} 80%{opacity:1} 100%{opacity:0} }
        @keyframes huntCorner { 0%{opacity:0;transform:scale(1.4)} 30%{opacity:1;transform:scale(1)} 80%{opacity:1} 100%{opacity:0} }

        /* ── CAUGHT FX (add to collection) ── */
        @keyframes caughtCardIn { 0%{opacity:0;transform:scale(0.4) rotate(-12deg)} 25%{opacity:1;transform:scale(1.18) rotate(4deg)} 40%{transform:scale(0.96) rotate(-2deg)} 55%{transform:scale(1.06) rotate(0deg)} 70%{transform:scale(1)} 88%{opacity:1;transform:scale(1)} 100%{opacity:0;transform:scale(1.1)} }
        @keyframes caughtFlash { 0%{opacity:0} 18%{opacity:0.95} 40%{opacity:0} 100%{opacity:0} }
        @keyframes caughtBurst { 0%{transform:scale(0.2);opacity:0} 22%{opacity:1} 100%{transform:scale(2.8);opacity:0} }
        @keyframes caughtText { 0%{opacity:0;transform:scale(0.6) translateY(10px)} 30%{opacity:1;transform:scale(1.1) translateY(0)} 45%{transform:scale(1)} 85%{opacity:1} 100%{opacity:0;transform:scale(0.95)} }
        @keyframes confettiFall { 0%{transform:translateY(-20px) rotate(0deg);opacity:0} 12%{opacity:1} 100%{transform:translateY(620px) rotate(720deg);opacity:0} }
        @keyframes milestonePop { 0%{transform:translateX(-50%) translateY(-30px) scale(0.8);opacity:0} 60%{transform:translateX(-50%) translateY(4px) scale(1.05);opacity:1} 100%{transform:translateX(-50%) translateY(0) scale(1);opacity:1} }
        @keyframes wantPulse { 0%,100%{transform:scale(1);opacity:0.9} 50%{transform:scale(1.12);opacity:1} }
        @keyframes slideInRight { from{transform:translateX(100%)} to{transform:translateX(0)} }
        .nav-tab:hover { color:#fff !important; }
        .nav-tab[data-active="0"]:hover { border-bottom-color: rgba(232,49,122,0.4) !important; }
        .nav-bar::-webkit-scrollbar { height:0; display:none; }
        .nav-bar { scrollbar-width:none; -ms-overflow-style:none; }
        @keyframes starPop { 0%{transform:scale(0) rotate(0deg);opacity:0} 35%{transform:scale(1.3) rotate(180deg);opacity:1} 100%{transform:scale(0.6) rotate(360deg);opacity:0} }

        /* ── RAINBOW COMPLETE — GRAND CELEBRATION ── */
        @keyframes rbBackdrop { 0%{opacity:0} 8%{opacity:1} 88%{opacity:1} 100%{opacity:0} }
        @keyframes rbRayspin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes rbTitle { 0%{opacity:0;transform:scale(0.3) translateY(40px)} 18%{opacity:1;transform:scale(1.15) translateY(0)} 30%{transform:scale(0.95)} 42%{transform:scale(1.05)} 55%{transform:scale(1)} 88%{opacity:1} 100%{opacity:0;transform:scale(0.9)} }
        @keyframes rbSub { 0%{opacity:0;transform:translateY(20px)} 25%{opacity:1;transform:translateY(0)} 88%{opacity:1} 100%{opacity:0} }
        @keyframes rbHue { from{filter:hue-rotate(0deg)} to{filter:hue-rotate(360deg)} }
        @keyframes rbCardFan { 0%{opacity:0;transform:translateY(80px) scale(0.5) rotate(var(--rb-rot-start))} 30%{opacity:1} 45%{transform:translateY(0) scale(1) rotate(var(--rb-rot))} 88%{opacity:1;transform:translateY(0) scale(1) rotate(var(--rb-rot))} 100%{opacity:0;transform:translateY(-40px) scale(0.9) rotate(var(--rb-rot))} }
        @keyframes rbConfetti { 0%{transform:translateY(-30px) rotate(0deg);opacity:0} 10%{opacity:1} 100%{transform:translateY(110vh) rotate(900deg);opacity:0} }
        @keyframes rbShockwave { 0%{transform:scale(0.1);opacity:0.9} 100%{transform:scale(3.5);opacity:0} }
        @keyframes rbBadge { 0%{transform:scale(0) rotate(-20deg);opacity:0} 40%{transform:scale(1.2) rotate(8deg);opacity:1} 60%{transform:scale(0.95) rotate(-3deg)} 75%{transform:scale(1.05)} 88%{transform:scale(1);opacity:1} 100%{opacity:0} }
      `}</style>

      {/* Comp Modal */}
      {zoomCard && (() => {
        const sibs = (zoomCard.siblings||[]).filter(s=>s.imageUrl);
        const idx = Math.max(0, sibs.findIndex(s=>s.id===zoomCard.card.id));
        const cur = sibs[idx] || zoomCard.card;
        const have = !!owned[cur.id];
        const onWay = !have && !!inTransit[cur.id];
        const go = (delta) => { const n = (idx+delta+sibs.length)%sibs.length; setZoomFlipped(false); setZoomCard({ ...zoomCard, card: sibs[n] }); };
        // Collection photo: a user-uploaded lot photo of this card, if any
        const myPhoto = (lotsForCard(cur.id).find(l=>l.photoUrl)||{}).photoUrl || null;
        const canFlip = have && myPhoto;
        return (
        <div onClick={()=>{ setZoomCard(null); setZoomFlipped(false); }} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:2147483600, display:"flex", alignItems:"center", justifyContent:"center", padding:"24px", cursor:"zoom-out" }}>
          {sibs.length>1 && <button onClick={e=>{e.stopPropagation();go(-1);}} style={{ position:"fixed", left:16, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,0.1)", border:"none", color:"#fff", fontSize:30, width:52, height:52, borderRadius:"50%", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>}
          {sibs.length>1 && <button onClick={e=>{e.stopPropagation();go(1);}} style={{ position:"fixed", right:16, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,0.1)", border:"none", color:"#fff", fontSize:30, width:52, height:52, borderRadius:"50%", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>›</button>}
          <div onClick={e=>e.stopPropagation()} style={{ maxWidth:"min(460px,92vw)", maxHeight:"92vh", display:"flex", flexDirection:"column", alignItems:"center", gap:12 }}>
            {/* Card (click to flip to your collection photo if owned) */}
            <div onClick={()=>{ if(canFlip) setZoomFlipped(f=>!f); }} style={{ position:"relative", cursor:canFlip?"pointer":"default", perspective:"1400px" }} title={canFlip?"Click to see your card photo":""}>
              <div style={{ position:"relative", transformStyle:"preserve-3d", transition:"transform 0.5s cubic-bezier(0.4,0,0.2,1)", transform:zoomFlipped?"rotateY(180deg)":"rotateY(0deg)" }}>
                {/* Front: official art */}
                <img src={cur.imageUrl} alt={cur.hero} style={{ maxWidth:"100%", maxHeight:"72vh", objectFit:"contain", borderRadius:14, boxShadow:"0 20px 60px rgba(0,0,0,0.7)", filter:have?"drop-shadow(0 0 20px rgba(74,222,128,0.5))":onWay?"drop-shadow(0 0 20px rgba(251,191,36,0.6)) grayscale(35%)":"none", backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden", display:"block" }}/>
                {/* Back: your card photo — pre-rotated 180° so it reads correctly (not mirrored) after the flip */}
                {myPhoto && <img src={myPhoto} alt="Your card" style={{ position:"absolute", top:0, left:0, width:"100%", height:"100%", objectFit:"contain", borderRadius:14, boxShadow:"0 20px 60px rgba(0,0,0,0.7)", border:"2px solid rgba(74,222,128,0.6)", backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden", transform:"rotateY(180deg)" }}/>}
              </div>
              {canFlip && <div style={{ position:"absolute", bottom:10, right:10, background:"rgba(0,0,0,0.75)", color:"#4ade80", borderRadius:20, padding:"3px 10px", fontSize:10, fontWeight:800 }}>🔄 {zoomFlipped?"official art":"your card"}</div>}
            </div>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:16, fontWeight:900, color:"#fff" }}>{cur.hero}</div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.6)" }}>{[cur.treatment, cur.weapon, cur.cardNum?`#${cur.cardNum}`:""].filter(Boolean).join(" · ")} · {have?"✓ Have":onWay?"🚚 On the way":"Missing"}{sibs.length>1?` · ${idx+1}/${sibs.length}`:""}</div>
            </div>
            {/* Mark on the way / arrived (only when you don't own it yet) */}
            {!have && (
              onWay ? (
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={e=>{ e.stopPropagation(); markTransitArrived(cur.id); }} style={{ background:"rgba(74,222,128,0.15)", border:"1px solid rgba(74,222,128,0.5)", color:"#4ade80", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>✅ Got it</button>
                  <button onClick={e=>{ e.stopPropagation(); setTransit(cur.id,null); }} style={{ background:"transparent", border:"1px solid rgba(255,255,255,0.2)", color:"rgba(255,255,255,0.6)", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                </div>
              ) : (
                <button onClick={e=>{ e.stopPropagation(); setTransit(cur.id,{qty:1,note:""}); }} style={{ background:"rgba(251,191,36,0.15)", border:"1px solid rgba(251,191,36,0.5)", color:"#FBBF24", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>🚚 Mark as On the Way</button>
              )
            )}
            {/* Thumbnail strip of the hero's other cards */}
            {sibs.length>1 && (
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", justifyContent:"center", maxWidth:"100%" }}>
                {sibs.map((s,i)=>{
                  const sHave = !!owned[s.id];
                  const sTransit = !sHave && !!inTransit[s.id];
                  return (
                    <div key={s.id} onClick={e=>{ e.stopPropagation(); setZoomFlipped(false); setZoomCard({ ...zoomCard, card:s }); }} style={{ width:44, aspectRatio:"3/4", borderRadius:5, overflow:"hidden", cursor:"pointer", border:`2px solid ${i===idx?"#E8317A":sHave?"rgba(74,222,128,0.5)":sTransit?"#FBBF24":"rgba(255,255,255,0.15)"}`, opacity:sHave?1:sTransit?0.7:0.5 }}>
                      <img src={s.imageUrl} alt={s.weapon} style={{ width:"100%", height:"100%", objectFit:"cover", filter:sHave?"none":sTransit?"grayscale(40%) sepia(25%)":"grayscale(70%)" }}/>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <button onClick={()=>{ setZoomCard(null); setZoomFlipped(false); }} style={{ position:"fixed", top:20, right:24, background:"rgba(255,255,255,0.1)", border:"none", color:"#fff", fontSize:26, width:44, height:44, borderRadius:"50%", cursor:"pointer" }}>×</button>
        </div>
        );
      })()}
      {viewProfileUid && (
        <ProfileModal
          profileUid={viewProfileUid}
          onClose={()=>setViewProfileUid(null)}
          currentUser={user}
          cards={cards}
          onAddFriend={addFriendByUid}
          onMessage={messageUser}
          isFriend={friends.some(f => f.from===viewProfileUid || f.to===viewProfileUid)}
          friendPending={sentReqs.some(r => r.to===viewProfileUid)}
        />
      )}
      <CompModal compCard={compCard} setCompCard={setCompCard} marketSales={marketSales} WEAPON_COLORS={WEAPON_COLORS} cards={cards}
          listings={listings}
          />

      {/* ── BULK ACTION BAR ── shows when in select mode with cards chosen */}
      {selectMode && (
        <div style={{position:"fixed",bottom:20,left:"50%",transform:"translateX(-50%)",zIndex:9000,background:"rgba(18,18,20,0.97)",backdropFilter:"blur(12px)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:14,padding:"10px 14px",display:"flex",alignItems:"center",gap:9,flexWrap:"wrap",maxWidth:"min(94vw,900px)",boxShadow:"0 12px 40px rgba(0,0,0,0.55)"}}>
          <span style={{fontSize:13,fontWeight:800,color:"#7B9CFF",whiteSpace:"nowrap"}}>{selectedIds.size} selected</span>
          {selectedIds.size===0 && <span style={{fontSize:10.5,color:"rgba(255,255,255,0.3)",whiteSpace:"nowrap",lineHeight:1.3}}>drag to sweep</span>}
          <button onClick={()=>{ const vis=visibleCards.map(c=>c.id); setSelectedIds(new Set(vis)); }} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.65)",borderRadius:8,padding:"6px 11px",fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Select shown</button>
          <button onClick={clearSelection} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.5)",borderRadius:8,padding:"6px 11px",fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Clear</button>
          {/* Done lives here as well as up in the header. The header copy is often a long scroll away
              once you have worked down a big set, and every other action you are using is in this
              bar — having to scroll back to the top just to leave select mode was the whole gripe. */}
          <button onClick={exitSelectMode} title="Leave select mode"
            style={{background:"rgba(123,156,255,0.18)",border:"1px solid rgba(123,156,255,0.5)",color:"#7B9CFF",borderRadius:8,padding:"6px 13px",fontSize:11.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{"✕ Done"}</button>
          <div style={{width:1,height:24,background:"rgba(255,255,255,0.12)"}}/>

          {/* THE THREE EVERYDAY ACTIONS. Everything else lives in the More menu below — this bar had
              grown to 13 buttons at equal weight, which made the common case hard to find. */}
          {(() => null)()}
          {/* The dial IS the quantity. Moving it writes straight through to every selected card, so
              the number on each card tracks it live \u2014 no separate "apply" press. Setting rather than
              adding is what makes that possible: the dial has to mean one definite end state.
              It reads from the SELECTION, not from its own memory, so it never disagrees with the
              cards: if everything selected is at 3 it shows 3, and mixed counts show the highest. */}
          <div style={{display:"flex",alignItems:"center",gap:0,background:selectedIds.size?"rgba(74,222,128,0.14)":"rgba(255,255,255,0.04)",border:"1px solid "+(selectedIds.size?"rgba(74,222,128,0.5)":"rgba(255,255,255,0.08)"),borderRadius:8,overflow:"hidden"}}>
            <button disabled={selectedIds.size===0}
              onClick={()=>{ const v=Math.max(0,selQty-1); setBulkQty(v); bulkApplyQty(v); }}
              title="One fewer copy of every selected card"
              style={{background:"transparent",border:"none",color:selectedIds.size?"#4ade80":"rgba(255,255,255,0.25)",padding:"7px 12px",fontSize:16,fontWeight:900,cursor:selectedIds.size?"pointer":"default",fontFamily:"inherit",lineHeight:1}}>
              {"\u2212"}
            </button>
            <span style={{color:selectedIds.size?"#4ade80":"rgba(255,255,255,0.25)",fontSize:12.5,fontWeight:800,fontFamily:"inherit",whiteSpace:"nowrap",padding:"0 4px",minWidth:60,textAlign:"center"}}>
              {selQty} each
            </span>
            <button disabled={selectedIds.size===0}
              onClick={()=>{ const v=Math.min(99,selQty+1); setBulkQty(v); bulkApplyQty(v); }}
              title="One more copy of every selected card"
              style={{background:"transparent",border:"none",color:selectedIds.size?"#4ade80":"rgba(255,255,255,0.25)",padding:"7px 12px",fontSize:16,fontWeight:900,cursor:selectedIds.size?"pointer":"default",fontFamily:"inherit",lineHeight:1}}>
              +
            </button>
          </div>
          <button disabled={selectedIds.size===0} onClick={()=>bulkSetPublic(true)} title="Show these on your public profile"
            style={{background:selectedIds.size?"rgba(74,222,128,0.12)":"rgba(255,255,255,0.04)",border:"1px solid "+(selectedIds.size?"rgba(74,222,128,0.35)":"rgba(255,255,255,0.08)"),color:selectedIds.size?"#4ade80":"rgba(255,255,255,0.25)",borderRadius:8,padding:"7px 13px",fontSize:12,fontWeight:700,cursor:selectedIds.size?"pointer":"default",fontFamily:"inherit",whiteSpace:"nowrap"}}>👁 Make Public</button>
          <button disabled={selectedIds.size===0} onClick={()=>setBulkListModal(true)} title="List the selected cards for sale"
            style={{background:selectedIds.size?"linear-gradient(135deg,#E8317A,#7B2FF7)":"rgba(255,255,255,0.04)",border:"none",color:selectedIds.size?"#fff":"rgba(255,255,255,0.25)",borderRadius:8,padding:"7px 13px",fontSize:12,fontWeight:800,cursor:selectedIds.size?"pointer":"default",fontFamily:"inherit",whiteSpace:"nowrap"}}>💰 List for Sale</button>

          {/* MORE — everything else, grouped by what it does. */}
          <div style={{position:"relative"}} onClick={e=>e.stopPropagation()}>
            <button onClick={()=>setBulkMoreOpen(v=>!v)}
              style={{background:bulkMoreOpen?"rgba(123,156,255,0.18)":"transparent",border:"1px solid rgba(123,156,255,0.35)",color:"#7B9CFF",borderRadius:8,padding:"7px 13px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>More ▾</button>
            {bulkMoreOpen && (
              <div style={{position:"absolute",bottom:"calc(100% + 8px)",right:0,minWidth:230,maxHeight:"60vh",overflowY:"auto",background:"rgba(22,22,26,0.99)",border:"1px solid rgba(255,255,255,0.14)",borderRadius:12,padding:6,boxShadow:"0 16px 48px rgba(0,0,0,0.6)",zIndex:9100}}>
                {(() => {
                  const hdr = (t) => <div style={{fontSize:9.5,fontWeight:800,color:"rgba(255,255,255,0.35)",textTransform:"uppercase",letterSpacing:0.7,padding:"8px 9px 4px"}}>{t}</div>;
                  const item = (label, fn, opts={}) => (
                    <button disabled={opts.needSel!==false && selectedIds.size===0}
                      onClick={()=>{ setBulkMoreOpen(false); fn(); }}
                      style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",color:(opts.needSel!==false && selectedIds.size===0)?"rgba(255,255,255,0.25)":(opts.danger?"#ff8095":"var(--bz-ink)"),fontSize:12.5,fontWeight:600,padding:"8px 9px",borderRadius:7,cursor:(opts.needSel!==false && selectedIds.size===0)?"default":"pointer",fontFamily:"inherit"}}
                      onMouseEnter={e=>{ if(!(opts.needSel!==false && selectedIds.size===0)) e.currentTarget.style.background="rgba(255,255,255,0.07)"; }}
                      onMouseLeave={e=>e.currentTarget.style.background="none"}>{label}</button>
                  );
                  return (
                    <>
                      {hdr("Collection")}
                      {item("🔢 Set quantity…", bulkSetQuantity)}
                      {item("➖ Remove from collection", ()=>bulkSetOwned(false))}
                      {item("\uD83C\uDFF7\ufe0f Mark as proxy", ()=>bulkSetProxy(true))}
                      {item("\u21a9\ufe0f Not a proxy", ()=>bulkSetProxy(false))}
                      {item("↗ Mark as sold", ()=>bulkSellSelected("sold"))}
                      {item("↗ Mark as traded", ()=>bulkSellSelected("traded"))}
                      {hdr("Visibility")}
                      {item("🔒 Make private", ()=>bulkSetPublic(false))}
                      {hdr("Want list")}
                      {item("❤️ Add to wants", ()=>bulkWant(true))}
                      {item("✕ Remove from wants", ()=>bulkWant(false))}
                      {hdr("Loans")}
                      {item("\uD83D\uDCE4 Mark as lent out…", bulkMarkLent)}
                      {item("\uD83D\uDCE5 Mark as borrowed…", bulkMarkBorrowed)}
                      {hdr("Trading")}
                      {item("🤝 New trade package", ()=>{ setTpTitle(""); setTpNote(""); setTpContact(""); setTpLink(""); setTpValues({}); setTpRemoved(new Set()); setTpEditId(null); setTpModal(true); })}
                      {item("📦 My packages", ()=>{ setTpListOpen(true); loadMyTradePackages(); }, {needSel:false})}
                      {kidGroups.length>0 && <>
                        {hdr("Assign to")}
                        {kidGroups.map(g=>(
                          <button key={g.id} disabled={selectedIds.size===0} onClick={()=>{ setBulkMoreOpen(false); bulkAssignKid(g.id); }}
                            style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",color:selectedIds.size===0?"rgba(255,255,255,0.25)":"var(--bz-ink)",fontSize:12.5,fontWeight:600,padding:"8px 9px",borderRadius:7,cursor:selectedIds.size?"pointer":"default",fontFamily:"inherit"}}
                            onMouseEnter={e=>{ if(selectedIds.size) e.currentTarget.style.background="rgba(255,255,255,0.07)"; }}
                            onMouseLeave={e=>e.currentTarget.style.background="none"}>{g.name}</button>
                        ))}
                        {item("Mine (unassign)", ()=>bulkAssignKid(null))}
                      </>}
                      {_cardAdmin && <>
                        {hdr("Admin")}
                        {item("✏️ Bulk edit cards", ()=>{setBulkEdits({});setBulkEditOpen(true);})}
                        {item("🧹 Clear artwork", bulkClearImages)}
                        {item("🗑 Delete from database", ()=>{setBulkDelText("");setBulkDelOpen(true);}, {danger:true})}
                      </>}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TRADE PACKAGE MODAL ── build a shareable link from selected owned cards. */}
      {tpModal && (()=>{
                const baseIds = tpEditId ? tpEditIds : [...selectedIds].filter(id => owned[id]);
                // Let the user fold in any currently-selected cards while editing, too.
                const extraIds = tpEditId ? [...selectedIds].filter(id => owned[id] && !tpEditIds.includes(id)) : [];
                const ids = [...new Set([...baseIds, ...extraIds])].filter(id => !tpRemoved.has(id));
        const n = ids.length;
        return (
          <div onClick={()=>!tpBusy&&(setTpModal(false),setTpEditId(null))} style={{position:"fixed",inset:0,zIndex:13500,background:"rgba(0,0,0,0.8)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:440,background:"#15151c",border:"1px solid rgba(74,222,128,0.25)",borderRadius:16,padding:"24px 26px",maxHeight:"88vh",overflowY:"auto"}}>
              <div style={{fontSize:19,fontWeight:900,color:"#fff",marginBottom:3}}>{"\uD83E\uDD1D"} {tpEditId ? "Edit Trade Package" : "Trade Package"}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginBottom:18}}>{n} card{n===1?"":"s"} you own — shareable with anyone, no login needed.</div>
              {!tpLink ? (
                <>
                  <label style={{fontSize:11,fontWeight:800,color:"#4ade80",display:"block",marginBottom:5}}>Title</label>
                  <input value={tpTitle} onChange={e=>setTpTitle(e.target.value)} placeholder="e.g. Gold Coin doubles for trade" style={{width:"100%",background:"#0e0e13",color:"#eee",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,padding:"9px 11px",fontSize:13,fontFamily:"inherit",marginBottom:14,boxSizing:"border-box"}}/>
                  <label style={{fontSize:11,fontWeight:800,color:"#4ade80",display:"block",marginBottom:5}}>Note to the other person <span style={{color:"rgba(255,255,255,0.3)",fontWeight:400}}>(optional)</span></label>
                  <textarea value={tpNote} onChange={e=>setTpNote(e.target.value)} placeholder="What you're looking for in return, condition notes, etc." rows={3} style={{width:"100%",background:"#0e0e13",color:"#eee",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,padding:"9px 11px",fontSize:13,fontFamily:"inherit",marginBottom:14,boxSizing:"border-box",resize:"vertical"}}/>
                  <label style={{fontSize:11,fontWeight:800,color:"#4ade80",display:"block",marginBottom:5}}>Card values <span style={{color:"rgba(255,255,255,0.3)",fontWeight:400}}>(optional — shows a total on the link)</span></label>
                  <div style={{maxHeight:170,overflowY:"auto",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,padding:"6px 8px",marginBottom:8}}>
                    {ids.map(cid => {
                      const cc = cards.find(x=>x.id===cid);
                      if (!cc) return null;
                      return (
                        <div key={cid} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0"}}>
                          <div style={{flex:1,minWidth:0,fontSize:12,color:"#ddd",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{cc.hero} {cc.cardNum?`\u00b7 #${cc.cardNum}`:""}</div>
                          <div style={{display:"flex",alignItems:"center",gap:3}}>
                            <span style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>$</span>
                            <input type="number" min="0" step="0.01" value={tpValues[cid]??""} onChange={e=>setTpValues(v=>({...v,[cid]:e.target.value}))} placeholder="0" style={{width:64,background:"#0e0e13",color:"#eee",border:"1px solid rgba(255,255,255,0.15)",borderRadius:6,padding:"4px 7px",fontSize:12,fontFamily:"inherit"}}/>
                          </div>
                          <button onClick={()=>setTpRemoved(s=>{ const n=new Set(s); n.add(cid); return n; })} title="Remove from package" style={{background:"transparent",border:"none",color:"rgba(239,68,68,0.7)",fontSize:16,cursor:"pointer",lineHeight:1,padding:"0 2px"}}>{"\u00d7"}</button>
                        </div>
                      );
                    })}
                  </div>
                  {(() => { const total = ids.reduce((s,cid)=>s+(parseFloat(tpValues[cid])||0),0); return total>0 ? (
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"rgba(74,222,128,0.08)",border:"1px solid rgba(74,222,128,0.25)",borderRadius:8,padding:"8px 12px",marginBottom:14}}>
                      <span style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.6)"}}>Package total</span>
                      <span style={{fontSize:17,fontWeight:900,color:"#4ade80"}}>${total.toFixed(2)}</span>
                    </div>
                  ) : <div style={{marginBottom:6}}/>; })()}

                  <label style={{fontSize:11,fontWeight:800,color:"#4ade80",display:"block",marginBottom:5}}>How they reach you</label>
                  <input value={tpContact} onChange={e=>setTpContact(e.target.value)} placeholder="Whatnot @, phone, email, Discord\u2026" style={{width:"100%",background:"#0e0e13",color:"#eee",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,padding:"9px 11px",fontSize:13,fontFamily:"inherit",marginBottom:6,boxSizing:"border-box"}}/>
                  <div style={{fontSize:10.5,color:"rgba(255,255,255,0.35)",marginBottom:18}}>Shown on the shared page so they can reply. Leave blank to hide.</div>
                  <div style={{display:"flex",gap:9}}>
                    <button onClick={createTradePackage} disabled={tpBusy||n===0} style={{flex:1,background:n?"linear-gradient(135deg,#4ade80,#22c55e)":"#222",color:n?"#06240f":"#666",border:"none",borderRadius:9,padding:"11px",fontSize:13.5,fontWeight:800,cursor:tpBusy?"wait":(n?"pointer":"not-allowed"),fontFamily:"inherit"}}>
                      {tpBusy ? "Creating\u2026" : (tpEditId ? `Save changes (${n} card${n===1?"":"s"})` : `Create link for ${n} card${n===1?"":"s"}`)}
                    </button>
                    <button onClick={()=>{ setTpModal(false); setTpEditId(null); }} disabled={tpBusy} style={{background:"transparent",border:"1px solid #333",color:"#888",borderRadius:9,padding:"11px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{fontSize:13,fontWeight:800,color:"#4ade80",marginBottom:8}}>{"\u2713"} {tpEditId ? "Trade package updated" : "Your trade package is live"}</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.55)",marginBottom:10}}>Send this link to anyone — they don't need an account.</div>
                  <div style={{display:"flex",gap:8,marginBottom:16}}>
                    <input readOnly value={tpLink} onClick={e=>e.target.select()} style={{flex:1,background:"#0e0e13",color:"#7B9CFF",border:"1px solid rgba(123,156,255,0.3)",borderRadius:8,padding:"9px 11px",fontSize:12,fontFamily:"monospace"}}/>
                    <button onClick={async()=>{ try{ await navigator.clipboard.writeText(tpLink); setTpCopied(true); setTimeout(()=>setTpCopied(false),2000);}catch(e){} }} style={{background:tpCopied?"#22c55e":"rgba(74,222,128,0.15)",color:tpCopied?"#06240f":"#4ade80",border:"1px solid rgba(74,222,128,0.4)",borderRadius:8,padding:"0 14px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{tpCopied?"Copied!":"Copy"}</button>
                  </div>
                  <button onClick={()=>{ setTpModal(false); setTpEditId(null); setTpLink(""); setTpListOpen(true); loadMyTradePackages(); }} style={{width:"100%",background:"transparent",border:"none",color:"#7B9CFF",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginBottom:8,textDecoration:"underline"}}>See all my trade packages</button>
                  <button onClick={()=>{ setTpModal(false); setTpEditId(null); setTpLink(""); }} style={{width:"100%",background:"transparent",border:"1px solid #333",color:"#888",borderRadius:9,padding:"10px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Done</button>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── MY TRADE PACKAGES MANAGER ── list / copy / delete your active share links. */}
      {tpListOpen && (
        <div onClick={()=>setTpListOpen(false)} style={{position:"fixed",inset:0,zIndex:13500,background:"rgba(0,0,0,0.8)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:520,background:"#15151c",border:"1px solid rgba(123,156,255,0.25)",borderRadius:16,padding:"24px 26px",maxHeight:"88vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div style={{fontSize:19,fontWeight:900,color:"#fff"}}>{"\uD83E\uDD1D"} My Trade Packages</div>
              <button onClick={()=>setTpListOpen(false)} style={{background:"transparent",border:"none",color:"#888",fontSize:22,cursor:"pointer",lineHeight:1}}>{"\u00d7"}</button>
            </div>
            {tpListBusy ? (
              <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",padding:"20px 0",textAlign:"center"}}>Loading{"\u2026"}</div>
            ) : !tpList || tpList.length===0 ? (
              <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",padding:"24px 0",textAlign:"center"}}>No trade packages yet. Select some owned cards and hit {"\uD83E\uDD1D"} Trade Package to make one.</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {tpList.map(pk => {
                  const url = `${window.location.origin}/trade/${pk.id}`;
                  const count = (pk.items||[]).reduce((s,it)=>s+(parseInt(it.qty)||1),0);
                  return (
                    <div key={pk.id} style={{background:"rgba(0,0,0,0.25)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,padding:"12px 14px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10}}>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:14,fontWeight:800,color:"#fff",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{pk.title||"Trade package"}</div>
                          <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginTop:2}}>{count} card{count===1?"":"s"}{pk.createdAt?` \u00b7 ${new Date(pk.createdAt).toLocaleDateString()}`:""}</div>
                        </div>
                        <button onClick={()=>deleteTradePackage(pk.id)} style={{background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.4)",color:"#f87171",borderRadius:7,padding:"5px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",flexShrink:0}}>Delete</button>
                      </div>
                      <div style={{display:"flex",gap:7,marginTop:10}}>
                        <input readOnly value={url} onClick={e=>e.target.select()} style={{flex:1,background:"#0e0e13",color:"#7B9CFF",border:"1px solid rgba(123,156,255,0.25)",borderRadius:7,padding:"6px 9px",fontSize:11,fontFamily:"monospace",minWidth:0}}/>
                        <button onClick={async()=>{ try{ await navigator.clipboard.writeText(url);}catch(e){} }} style={{background:"rgba(74,222,128,0.15)",color:"#4ade80",border:"1px solid rgba(74,222,128,0.4)",borderRadius:7,padding:"0 12px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Copy</button>
                        <a href={url} target="_blank" rel="noreferrer" style={{background:"rgba(123,156,255,0.12)",color:"#7B9CFF",border:"1px solid rgba(123,156,255,0.3)",borderRadius:7,padding:"6px 12px",fontSize:11,fontWeight:800,textDecoration:"none",fontFamily:"inherit",whiteSpace:"nowrap"}}>Open</a>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}


      {/* ── BULK LIST MODAL ── */}
      {/* Bulk edit. Switch on any number of fields and set them all in ONE write — a typo like
          "Kyle Trucker" usually lives in both hero AND inspiredBy, and fixing one field at a time
          means running the same selection twice.

          Each field shows the DISTINCT values currently across the selection, so if you've grabbed
          three different heroes by accident you see that BEFORE you flatten them into one. */}
      {bulkEditOpen && (()=>{
        const sel = cards.filter(c => selectedIds.has(c.id));
        const enabled = Object.keys(bulkEdits);

        // What's actually in a given field right now, across everything selected.
        const distinctFor = (fid) => {
          const counts = {};
          sel.forEach(c => {
            const v = c[fid];
            const k = (v===undefined||v===null||v==="") ? "(empty)" : String(v);
            counts[k] = (counts[k]||0) + 1;
          });
          return Object.entries(counts).sort((a,b)=>b[1]-a[1]);
        };

        const toggle = (fid) => setBulkEdits(prev => {
          const n = {...prev};
          if (fid in n) delete n[fid];
          else {
            // Prefill with the current value when there IS only one \u2014 you're usually correcting a
            // typo, not typing from scratch.
            const d = distinctFor(fid);
            n[fid] = (d.length === 1 && d[0][0] !== "(empty)") ? d[0][0] : "";
          }
          return n;
        });

        return (
          <div onClick={()=>!bulkEditing&&setBulkEditOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.82)",zIndex:14000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:14,padding:22,maxWidth:560,width:"100%",maxHeight:"85vh",overflowY:"auto"}}>
              <div style={{fontSize:16,fontWeight:900,color:"#7B9CFF",marginBottom:6}}>
                {"\u270F\uFE0F"} Edit {sel.length} card{sel.length===1?"":"s"}
              </div>
              <div style={{fontSize:12,color:"var(--bz-ink-3)",lineHeight:1.6,marginBottom:16}}>
                Tick any fields you want to change. They're all written together in one pass, and it
                changes the shared database {"\u2014"} so it applies for everyone.
              </div>

              <div style={{border:"1px solid var(--bz-line-2)",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                {BULK_FIELDS.map(f => {
                  const on = f.id in bulkEdits;
                  const d = distinctFor(f.id);
                  const mixed = d.length > 1;
                  return (
                    <div key={f.id} style={{borderBottom:"1px solid #1c1c1c",background:on?"rgba(123,156,255,0.05)":"transparent"}}>
                      <label style={{display:"flex",alignItems:"center",gap:9,padding:"10px 12px",cursor:"pointer"}}>
                        <input type="checkbox" checked={on} onChange={()=>toggle(f.id)} disabled={bulkEditing} style={{accentColor:"#7B9CFF",cursor:"pointer"}}/>
                        <span style={{fontSize:12.5,fontWeight:on?800:600,color:on?"#7B9CFF":"#bbb",minWidth:86}}>{f.label}</span>
                        <span style={{flex:1,fontSize:11,color:mixed?"#FBBF24":"var(--bz-ink-3)",fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {mixed
                            ? `\u26A0\uFE0F ${d.length} different values`
                            : d.length===1 ? d[0][0] : "\u2014"}
                        </span>
                      </label>
                      {on && (
                        <div style={{padding:"0 12px 11px 42px"}}>
                          <input
                            value={bulkEdits[f.id]}
                            onChange={e=>setBulkEdits(prev=>({...prev,[f.id]:e.target.value}))}
                            disabled={bulkEditing}
                            type={f.id==="power" ? "number" : "text"}
                            placeholder={f.id==="power" ? "e.g. 150 \u2014 blank to clear" : `New ${f.label.toLowerCase()}\u2026`}
                            style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:7,padding:"8px 11px",fontSize:13,color:"#fff",fontFamily:"inherit"}}/>
                        {f.isImage && (
                          <div style={{marginTop:8,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                            <label style={{fontSize:11.5,fontWeight:700,color:"#7B9CFF",cursor:bulkImgUploading?"wait":"pointer",background:"rgba(123,156,255,0.1)",border:"1px solid rgba(123,156,255,0.35)",borderRadius:7,padding:"7px 12px"}}>
                              {bulkImgUploading ? "Uploading\u2026" : "\uD83D\uDCF7 Upload placeholder image"}
                              <input type="file" accept="image/*" disabled={bulkImgUploading} style={{display:"none"}}
                                onChange={async e=>{
                                  const file = e.target.files && e.target.files[0];
                                  if(!file) return;
                                  setBulkImgUploading(true);
                                  try {
                                    const path = `card_data/placeholders/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g,"_")}`;
                                    await uploadBytes(ref(storage, path), file, { contentType:file.type||"image/jpeg", cacheControl:"public,max-age=31536000" });
                                    const url = await getDownloadURL(ref(storage, path));
                                    setBulkEdits(prev=>({...prev, imageUrl:url}));
                                  } catch(err) {
                                    alert("Upload failed: " + (err?.message||err));
                                  } finally { setBulkImgUploading(false); e.target.value=""; }
                                }}/>
                            </label>
                            {bulkEdits.imageUrl ? (
                              <img src={bulkEdits.imageUrl} alt="" style={{width:34,height:47,objectFit:"cover",borderRadius:4,border:"1px solid #333"}}/>
                            ) : null}
                            <span style={{fontSize:10.5,color:"var(--bz-ink-3)"}}>Upload once — it's applied to all {selectedIds.size} selected card{selectedIds.size===1?"":"s"}.</span>
                          </div>
                        )}
                          {mixed && (
                            <div style={{fontSize:10.5,color:"#FBBF24",lineHeight:1.5,marginTop:6}}>
                              {d.slice(0,4).map(([v,n])=>`${v} (${n})`).join("  \u00B7  ")}
                              {d.length>4 ? `  \u00B7  +${d.length-4} more` : ""}
                              <div style={{marginTop:3}}>All of these become the value above. Narrow the selection if that's not what you meant.</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div style={{display:"flex",gap:8}}>
                <button disabled={enabled.length===0||bulkEditing} onClick={bulkEditCards}
                  style={{flex:1,background:enabled.length?"linear-gradient(135deg,#E8317A,#7B2FF7)":"#222",color:enabled.length?"#fff":"#666",border:"none",borderRadius:9,padding:"11px",fontSize:13,fontWeight:800,cursor:(enabled.length&&!bulkEditing)?"pointer":"not-allowed",fontFamily:"inherit"}}>
                  {bulkEditing ? "Saving\u2026"
                    : enabled.length===0 ? "Pick a field to change"
                    : `Update ${enabled.length} field${enabled.length===1?"":"s"} on ${sel.length} card${sel.length===1?"":"s"}`}
                </button>
                <button disabled={bulkEditing} onClick={()=>{setBulkEditOpen(false);setBulkEdits({});}}
                  style={{flex:1,background:"transparent",border:"1px solid #333",color:"#999",borderRadius:9,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}


      {/* Typed confirmation. This deletes cards for EVERY user, so a yes/no dialog isn't enough —
          typing the count forces you to actually look at how many are selected before it happens. */}
      {bulkDelOpen && (()=>{
        const n = selectedIds.size;
        const sample = cards.filter(c => selectedIds.has(c.id)).slice(0, 8);
        const confirmed = bulkDelText.trim() === String(n);
        return (
          <div onClick={()=>!bulkDeleting&&setBulkDelOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.82)",zIndex:14000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s2)",border:"1px solid #7f1d1d",borderRadius:14,padding:22,maxWidth:540,width:"100%",maxHeight:"85vh",overflowY:"auto"}}>
              <div style={{fontSize:17,fontWeight:900,color:"#EF4444",marginBottom:8}}>
                {"\u26A0\uFE0F"} Delete {n} card{n===1?"":"s"} from the database?
              </div>
              <div style={{fontSize:12.5,color:"#ccc",lineHeight:1.7,marginBottom:12}}>
                This removes {n===1?"this card":"these cards"} from the <strong>shared checklist</strong> {"\u2014"} for every
                collector, not just you. Anyone who owns {n===1?"it":"them"} will lose {n===1?"it":"them"} from their collection.
                <strong style={{color:"#EF4444"}}> This cannot be undone.</strong>
              </div>
              <div style={{background:"var(--bz-bg)",border:"1px solid var(--bz-line-2)",borderRadius:9,padding:"10px 12px",marginBottom:14,maxHeight:180,overflowY:"auto"}}>
                {sample.map(c=>(
                  <div key={c.id} style={{fontSize:11,fontFamily:"monospace",color:"var(--bz-ink-2)",padding:"3px 0",display:"flex",gap:8}}>
                    <span style={{minWidth:110,color:"var(--bz-ink-3)"}}>{c.setName}</span>
                    <span style={{minWidth:52}}>{c.cardNum}</span>
                    <span style={{color:"#ccc"}}>{c.playName||c.hero}</span>
                    {c.treatment && <span style={{color:"var(--bz-ink-3)"}}>{"\u00B7"} {c.treatment}</span>}
                  </div>
                ))}
                {n>8 && <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:5}}>{"\u2026and "}{n-8} more</div>}
              </div>
              <div style={{fontSize:11.5,color:"#999",marginBottom:6}}>
                Type <strong style={{color:"#EF4444"}}>{n}</strong> to confirm:
              </div>
              <input value={bulkDelText} onChange={e=>setBulkDelText(e.target.value)} autoFocus disabled={bulkDeleting}
                style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"10px 12px",fontSize:14,color:"#fff",fontFamily:"monospace",marginBottom:12}}/>
              <div style={{display:"flex",gap:8}}>
                <button disabled={!confirmed||bulkDeleting} onClick={bulkDeleteCards}
                  style={{flex:1,background:confirmed?"#EF4444":"#2a1515",color:confirmed?"#fff":"#666",border:"none",borderRadius:9,padding:"11px",fontSize:13,fontWeight:800,cursor:(confirmed&&!bulkDeleting)?"pointer":"not-allowed",fontFamily:"inherit"}}>
                  {bulkDeleting ? "Deleting\u2026" : `Delete ${n} card${n===1?"":"s"}`}
                </button>
                <button disabled={bulkDeleting} onClick={()=>setBulkDelOpen(false)}
                  style={{flex:1,background:"transparent",border:"1px solid #333",color:"#999",borderRadius:9,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}


      {bulkListModal && (()=>{
        const eligibleCount = [...selectedIds].filter(id=>owned[id]&&scanPhotoByCard[id]&&!myListings.find(l=>l.cardId===id)&&!lockedCardIds([id]).length).length;
        return (
        <div onClick={()=>!bulkBusy&&setBulkListModal(false)} style={{position:"fixed",inset:0,zIndex:9500,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#14141c",border:"1px solid rgba(123,156,255,0.3)",borderRadius:16,padding:24,maxWidth:420,width:"100%"}}>
            <div style={{fontSize:18,fontWeight:900,color:"#fff",marginBottom:6}}>List {eligibleCount} cards</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginBottom:16}}>Applies one price and note to all selected cards. Each is listed with your scan photo. Cards without your photo, or already listed, are skipped.</div>
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              {[["sale","💵 For Sale"],["trade","🔄 Trade"],["offer","📩 Open to Offers"]].map(([v,l])=>(
                <button key={v} onClick={()=>setBulkListType(v)} style={{flex:1,background:bulkListType===v?"rgba(123,156,255,0.2)":"transparent",border:`1px solid ${bulkListType===v?"#7B9CFF":"#333"}`,color:bulkListType===v?"#7B9CFF":"#888",borderRadius:8,padding:"8px 4px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
              ))}
            </div>
            {bulkListType==="sale" && (
              <input value={bulkListPrice} onChange={e=>setBulkListPrice(e.target.value)} type="number" placeholder="Asking price (each) $" style={{width:"100%",background:"#0d0d12",border:"1px solid rgba(255,255,255,0.15)",color:"#fff",borderRadius:8,padding:"11px 12px",fontSize:14,fontFamily:"inherit",marginBottom:12,boxSizing:"border-box"}}/>
            )}
            <textarea value={bulkListNote} onChange={e=>setBulkListNote(e.target.value)} placeholder="Optional note applied to all (condition, terms…)" rows={2} style={{width:"100%",background:"#0d0d12",border:"1px solid rgba(255,255,255,0.15)",color:"#fff",borderRadius:8,padding:"11px 12px",fontSize:13,fontFamily:"inherit",marginBottom:8,boxSizing:"border-box",resize:"vertical"}}/>
            <div style={{fontSize:11,color:"#4ade80",background:"rgba(74,222,128,0.08)",border:"1px solid rgba(74,222,128,0.25)",borderRadius:8,padding:"8px 10px",marginBottom:16,lineHeight:1.4}}>📸 Only cards where you've uploaded your own scan photo can be bulk-listed — each listing uses that photo. To list a card without a scan, use the single "List for Sale" flow and add a photo there.</div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={bulkListSelected} disabled={bulkBusy||eligibleCount===0||(bulkListType==="sale"&&!bulkListPrice)} style={{flex:1,background:(bulkBusy||eligibleCount===0||(bulkListType==="sale"&&!bulkListPrice))?"rgba(255,255,255,0.06)":"linear-gradient(135deg,#E8317A,#7B2FF7)",color:(bulkBusy||eligibleCount===0||(bulkListType==="sale"&&!bulkListPrice))?"#555":"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:800,cursor:(bulkBusy||eligibleCount===0||(bulkListType==="sale"&&!bulkListPrice))?"not-allowed":"pointer",fontFamily:"inherit"}}>{bulkBusy?"Listing…":eligibleCount===0?"No eligible cards":"List All"}</button>
              <button onClick={()=>setBulkListModal(false)} disabled={bulkBusy} style={{background:"transparent",border:"1px solid #333",color:"#888",borderRadius:10,padding:"12px 20px",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── CARD FX OVERLAY ── */}
      {animsOn && cardFx && <CardFxOverlay fx={cardFx} onDone={()=>setCardFx(null)} />}

      {/* ── KIDS MANAGER ──────────────────────────────────────────────────────────────────── */}
      {kidsModal && (
        <div onClick={()=>setKidsModal(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(3px)",zIndex:13000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s2)",border:"1px solid #2f2f2f",borderRadius:16,padding:22,width:"100%",maxWidth:420,boxShadow:"0 24px 70px rgba(0,0,0,0.7)"}}>
            <div style={{fontSize:16,fontWeight:800,color:"#fff",marginBottom:4}}>🧒 Kids' Collections</div>
            <div style={{fontSize:11.5,color:"#9a9a9a",marginBottom:16,lineHeight:1.6}}>
              Tag cards you own to a kid's binder. The cards stay in <strong>your</strong> collection and still count
              toward your totals and decks — the tag just tracks whose they are.
            </div>
            {kidGroups.map(g=>(
              <div key={g.id} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <span style={{width:10,height:10,borderRadius:"50%",background:g.color,flexShrink:0}}/>
                <input defaultValue={g.name} onBlur={e=>renameKidGroup(g.id, e.target.value)}
                  style={{flex:1,background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"7px 10px",fontSize:13,color:"#fff",fontFamily:"inherit"}}/>
                <span style={{fontSize:11,color:"#9a9a9a",whiteSpace:"nowrap"}}>
                  {kidCounts[g.id]||0} cards
                </span>
                <button onClick={()=>{ if(window.confirm(`Remove ${g.name}'s collection? The cards stay in your collection — only the tags are removed.`)) removeKidGroup(g.id); }}
                  style={{background:"none",border:"none",color:"#8a8a8a",cursor:"pointer",fontSize:16,padding:"0 4px"}}>×</button>
              </div>
            ))}
            <form onSubmit={e=>{e.preventDefault(); const v=e.target.kidname.value; if(addKidGroup(v)) e.target.reset();}} style={{display:"flex",gap:8,marginTop:12}}>
              <input name="kidname" placeholder="Add a kid's name…" autoComplete="off"
                style={{flex:1,background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"9px 12px",fontSize:13,color:"#fff",fontFamily:"inherit"}}/>
              <button type="submit" style={{background:"#7B9CFF",color:"#04122e",border:"none",borderRadius:8,padding:"9px 16px",fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Add</button>
            </form>
            <button onClick={()=>setKidsModal(false)} style={{marginTop:16,width:"100%",background:"transparent",border:"1px solid #333",color:"#c9c9c9",borderRadius:8,padding:"9px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Done</button>
          </div>
        </div>
      )}

      {/* ── SELL / TRADE AWAY ─────────────────────────────────────────────────────────────── */}
      {sellModal && (()=>{
        const c = sellModal;
        const have = parseInt(owned[c.id])||0;
        return (
          <div onClick={()=>setSellModal(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(3px)",zIndex:13000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s2)",border:"1px solid #2f2f2f",borderRadius:16,padding:22,width:"100%",maxWidth:400,boxShadow:"0 24px 70px rgba(0,0,0,0.7)"}}>
              <div style={{fontSize:16,fontWeight:800,color:"#fff",marginBottom:2}}>Move this card on</div>
              <div style={{fontSize:12.5,color:"#c9c9c9",marginBottom:4}}>{c.hero} · #{c.cardNum} · {c.treatment}</div>
              <div style={{fontSize:11,color:"#9a9a9a",marginBottom:16,lineHeight:1.6}}>
                This drops it from your active collection but keeps the record — you'll still be able to see you
                once owned it. You have <strong>{have}</strong> {have===1?"copy":"copies"}.
              </div>
              <form onSubmit={e=>{
                e.preventDefault();
                const f=e.target;
                sellCard(c.id, { qty:f.qty.value, reason:f.reason.value, note:f.note.value, price:f.price.value });
                setSellModal(null);
              }}>
                <div style={{display:"flex",gap:8,marginBottom:10}}>
                  <div style={{flex:1}}>
                    <label style={{fontSize:10.5,color:"#9a9a9a",fontWeight:700,display:"block",marginBottom:4}}>How many</label>
                    <input name="qty" type="number" min="1" max={have} defaultValue="1"
                      style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"8px 10px",fontSize:13,color:"#fff",fontFamily:"inherit"}}/>
                  </div>
                  <div style={{flex:2}}>
                    <label style={{fontSize:10.5,color:"#9a9a9a",fontWeight:700,display:"block",marginBottom:4}}>What happened</label>
                    <select name="reason" defaultValue="sold"
                      style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"8px 10px",fontSize:13,color:"#fff",fontFamily:"inherit",cursor:"pointer"}}>
                      <option value="sold">Sold</option>
                      <option value="traded">Traded</option>
                      <option value="gifted">Gifted</option>
                      <option value="lost">Lost / damaged</option>
                    </select>
                  </div>
                </div>
                <label style={{fontSize:10.5,color:"#9a9a9a",fontWeight:700,display:"block",marginBottom:4}}>Price (optional)</label>
                <input name="price" type="number" step="0.01" placeholder="e.g. 45.00"
                  style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"8px 10px",fontSize:13,color:"#fff",fontFamily:"inherit",marginBottom:10}}/>
                <label style={{fontSize:10.5,color:"#9a9a9a",fontWeight:700,display:"block",marginBottom:4}}>Note (optional)</label>
                <input name="note" placeholder="Who to, what for…"
                  style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:8,padding:"8px 10px",fontSize:13,color:"#fff",fontFamily:"inherit",marginBottom:16}}/>
                <div style={{display:"flex",gap:8}}>
                  <button type="button" onClick={()=>setSellModal(null)} style={{flex:1,background:"transparent",border:"1px solid #333",color:"#c9c9c9",borderRadius:8,padding:"10px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                  <button type="submit" style={{flex:1,background:"#E8317A",border:"none",color:"#fff",borderRadius:8,padding:"10px",fontSize:12.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Confirm</button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* ── FORMERLY OWNED ────────────────────────────────────────────────────────────────── */}
      {soldView && (
        <div onClick={()=>setSoldView(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(3px)",zIndex:13000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s2)",border:"1px solid #2f2f2f",borderRadius:16,padding:22,width:"100%",maxWidth:560,maxHeight:"80vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 70px rgba(0,0,0,0.7)"}}>
            <div style={{fontSize:16,fontWeight:800,color:"#fff",marginBottom:2}}>📜 Formerly owned</div>
            <div style={{fontSize:11.5,color:"#9a9a9a",marginBottom:12}}>
              Cards you've sold, traded, or given away. These don't count toward your collection — the record is just here so nothing gets lost. Search a trade partner's name to see everything that went to them.
            </div>
            {soldLog.length > 0 && (
              <input value={soldSearch} onChange={e=>setSoldSearch(e.target.value)} placeholder="Search partner name or card (e.g. Avalogaming)"
                style={{width:"100%",background:"var(--bz-bg)",border:"1px solid #333",color:"#fff",borderRadius:8,padding:"9px 12px",fontSize:13,fontFamily:"inherit",marginBottom:12,boxSizing:"border-box"}}/>
            )}
            {soldLog.length===0 ? (
              <div style={{fontSize:12.5,color:"#9a9a9a",textAlign:"center",padding:"30px 0"}}>Nothing here yet.</div>
            ) : (() => {
              const q = soldSearch.trim().toLowerCase();
              const rows = soldLog.filter(e => {
                if (!q) return true;
                const c = cards.find(x=>x.id===e.cardId) || {};
                const hay = [e.note||"", c.hero||"", c.treatment||"", c.weapon||"", c.setName||"", c.cardNum||""].join(" ").toLowerCase();
                return hay.includes(q);
              });
              // When searching, tally how many cards match — useful for "how many went to Avalogaming".
              const matchCount = rows.reduce((s,e)=>s+(e.qty||1),0);
              return (
              <div style={{overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
                {q && <div style={{fontSize:11,color:"#4ade80",fontWeight:700,marginBottom:2}}>{matchCount} card{matchCount===1?"":"s"} match "{soldSearch.trim()}"</div>}
                {rows.length===0 && <div style={{fontSize:12,color:"#9a9a9a",textAlign:"center",padding:"20px 0"}}>No matches. Note: only cards with a partner name typed in their trade note are searchable by partner.</div>}
                {rows.map(e=>{
                  const c = cards.find(x=>x.id===e.cardId) || {};
                  const REASON = { sold:"Sold", traded:"Traded", gifted:"Gifted", lost:"Lost" };
                  return (
                    <div key={e.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 11px",background:"var(--bz-bg)",border:"1px solid #333",borderRadius:9}}>
                      {c.imageUrl && <img src={c.imageUrl} alt="" style={{width:32,height:44,objectFit:"cover",borderRadius:4,flexShrink:0}}/>}
                      <div style={{minWidth:0,flex:1}}>
                        <div style={{fontSize:12.5,fontWeight:700,color:"#fff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {c.hero || "Unknown card"} {c.cardNum?<span style={{color:"#9a9a9a",fontWeight:400}}>#{c.cardNum}</span>:null}
                        </div>
                        <div style={{fontSize:10.5,color:"#9a9a9a",marginTop:2}}>
                          {REASON[e.reason]||e.reason}
                          {e.qty>1?` ×${e.qty}`:""}
                          {e.price?` · $${e.price.toFixed(2)}`:""}
                          {" · "}{new Date(e.date).toLocaleDateString()}
                          {e.note?` · ${e.note}`:""}
                        </div>
                      </div>
                      <button onClick={()=>{ if(window.confirm("Put this card back in your collection?")) undoSell(e.id); }}
                        title="Undo — add it back to your collection"
                        style={{background:"transparent",border:"1px solid #333",color:"#9a9a9a",borderRadius:6,padding:"4px 9px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Undo</button>
                    </div>
                  );
                })}
                    </div>
                    ); })()}
            <button onClick={()=>setSoldView(false)} style={{marginTop:14,width:"100%",background:"transparent",border:"1px solid #333",color:"#c9c9c9",borderRadius:8,padding:"9px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
          </div>
        </div>
      )}
      {/* RESTORE. Deliberately a three-step flow: pick a file, SEE what's in it, then choose merge or
          replace. People reach for this while panicking, and a restore that silently wipes their
          current data would be a second disaster on top of the first. */}
      {restoreModal && (
        <div onClick={()=>!restoreModal.busy && setRestoreModal(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",borderRadius:16,padding:20,maxWidth:460,width:"100%",maxHeight:"85vh",overflowY:"auto"}}>
            <div style={{fontSize:17,fontWeight:900,color:"#fff",marginBottom:6}}>{"\u267B\uFE0F"} Restore from Backup</div>

            {restoreModal.report ? (
              <>
                <div style={{fontSize:13,color:"#4ade80",fontWeight:800,marginBottom:10}}>Restore complete.</div>
                <div style={{fontSize:12.5,color:"var(--bz-ink-2)",lineHeight:1.9,marginBottom:12}}>
                  {restoreModal.report.cards} cards{"\u00b7"} {restoreModal.report.copies} copies{"\u00b7"} {restoreModal.report.decks} decks<br/>
                  {restoreModal.report.wants} want-list{"\u00b7"} {restoreModal.report.tradeBait} trade flags
                </div>
                {restoreModal.report.missing.length > 0 && (
                  <div style={{background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:10,padding:"10px 12px",marginBottom:12}}>
                    <div style={{fontSize:11.5,color:"#FBBF24",fontWeight:800,marginBottom:4}}>
                      {restoreModal.report.missing.length} card{restoreModal.report.missing.length===1?"":"s"} couldn't be matched
                    </div>
                    <div style={{fontSize:10.5,color:"rgba(255,255,255,0.5)",lineHeight:1.6,maxHeight:110,overflowY:"auto"}}>
                      {restoreModal.report.missing.slice(0,25).join(", ")}
                      {restoreModal.report.missing.length>25 && ` \u2026and ${restoreModal.report.missing.length-25} more`}
                    </div>
                    <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginTop:6}}>
                      These aren't in the card database any more, so there was nothing to restore them onto.
                    </div>
                  </div>
                )}
                <button onClick={()=>setRestoreModal(null)} style={{width:"100%",background:"linear-gradient(135deg,#4ade80,#22c55e)",color:"#04220f",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:900,cursor:"pointer",fontFamily:"inherit"}}>Done</button>
              </>
            ) : restoreModal.preview ? (
              <>
                <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,padding:"12px 14px",marginBottom:14,fontSize:12.5,color:"var(--bz-ink-2)",lineHeight:1.9}}>
                  <div style={{color:"#fff",fontWeight:800,marginBottom:4}}>This backup contains</div>
                  {restoreModal.preview.cards} cards{"\u00b7"} {restoreModal.preview.copies} copies{"\u00b7"} {restoreModal.preview.decks} decks{"\u00b7"} {restoreModal.preview.wants} wants<br/>
                  <span style={{color:"rgba(255,255,255,0.4)",fontSize:11}}>
                    From {restoreModal.preview.account} {"\u00b7"} {String(restoreModal.preview.exportedAt).slice(0,10)}
                  </span>
                  {restoreModal.preview.missing > 0 && (
                    <div style={{color:"#FBBF24",fontSize:11,marginTop:6}}>
                      {restoreModal.preview.missing} card{restoreModal.preview.missing===1?"":"s"} in this file are no longer in the database and will be skipped.
                    </div>
                  )}
                </div>
                <div style={{fontSize:12,color:"var(--bz-ink-2)",marginBottom:8,fontWeight:700}}>How should this be applied?</div>
                <button disabled={restoreModal.busy}
                  onClick={async()=>{
                    setRestoreModal(m=>({...m,busy:true}));
                    try { const rep = await restoreFromBackup(restoreModal.file,"merge"); setRestoreModal({report:rep}); }
                    catch(e){ alert("Restore failed: "+(e?.message||e)); setRestoreModal(null); }
                  }}
                  style={{width:"100%",background:"rgba(74,222,128,0.15)",border:"1px solid rgba(74,222,128,0.5)",color:"#4ade80",borderRadius:10,padding:"11px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",marginBottom:8,textAlign:"left"}}>
                  {restoreModal.busy ? "Restoring\u2026" : "\u2795 Merge (recommended)"}
                  <div style={{fontSize:10.5,color:"rgba(255,255,255,0.45)",fontWeight:600,marginTop:2}}>
                    Adds what's missing. Keeps anything you own more of. Nothing is removed.
                  </div>
                </button>
                <button disabled={restoreModal.busy}
                  onClick={async()=>{
                    if (!window.confirm("REPLACE wipes your current collection, copies, wants and trade flags, then restores this file.\n\nThis cannot be undone. Continue?")) return;
                    setRestoreModal(m=>({...m,busy:true}));
                    try { const rep = await restoreFromBackup(restoreModal.file,"replace"); setRestoreModal({report:rep}); }
                    catch(e){ alert("Restore failed: "+(e?.message||e)); setRestoreModal(null); }
                  }}
                  style={{width:"100%",background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.4)",color:"#EF4444",borderRadius:10,padding:"11px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",marginBottom:10,textAlign:"left"}}>
                  {"\u26A0"} Replace everything
                  <div style={{fontSize:10.5,color:"rgba(255,255,255,0.4)",fontWeight:600,marginTop:2}}>
                    Wipes what you have now first. Only for a true from-scratch restore.
                  </div>
                </button>
                <button disabled={restoreModal.busy} onClick={()=>setRestoreModal(null)} style={{width:"100%",background:"transparent",border:"1px solid #333",color:"#999",borderRadius:10,padding:"10px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              </>
            ) : (
              <>
                <div style={{fontSize:12.5,color:"var(--bz-ink-2)",lineHeight:1.7,marginBottom:14}}>
                  Pick a <strong style={{color:"#fff"}}>Full Backup</strong> .json file. You'll see what's in it before anything changes.
                </div>
                <label style={{display:"block",background:"rgba(123,156,255,0.1)",border:"1px dashed rgba(123,156,255,0.45)",borderRadius:12,padding:"22px 14px",textAlign:"center",cursor:"pointer",marginBottom:10}}>
                  <div style={{fontSize:13,fontWeight:800,color:"#7B9CFF"}}>Choose backup file</div>
                  <div style={{fontSize:10.5,color:"rgba(255,255,255,0.35)",marginTop:3}}>bazooka-backup-....json</div>
                  <input type="file" accept="application/json,.json" style={{display:"none"}}
                    onChange={async e=>{
                      const f = e.target.files?.[0]; e.target.value="";
                      if (!f) return;
                      try { const pv = await previewBackup(f); setRestoreModal({ file:f, preview:pv }); }
                      catch(err){ alert(err?.message || String(err)); }
                    }}/>
                </label>
                <button onClick={()=>setRestoreModal(null)} style={{width:"100%",background:"transparent",border:"1px solid #333",color:"#999",borderRadius:10,padding:"10px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              </>
            )}
          </div>
        </div>
      )}
      {/* Which print, and how many of each. Entering a stack means "4 Alpha and 3 Update" in one
          go \u2014 forcing one card at a time through a two-button prompt would be worse than no prompt.
          Quantities start at 0 so nothing is recorded that you didn't ask for. */}
      {versionAsk && (
        <div onClick={()=>setVersionAsk(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:12500,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",borderRadius:16,padding:20,maxWidth:400,width:"100%"}}>
            <div style={{fontSize:16,fontWeight:900,color:"#fff",marginBottom:4}}>{versionAsk.spec.label}</div>
            <div style={{fontSize:12.5,color:"var(--bz-ink-2)",lineHeight:1.6,marginBottom:14}}>
              <strong style={{color:"#fff"}}>{versionAsk.card.hero || versionAsk.card.playName}</strong>
              {versionAsk.card.cardNum ? ` \u00b7 ${versionAsk.card.cardNum}` : ""}
              {versionAsk.spec.help ? <> {"\u2014"} {versionAsk.spec.help}</> : null}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {versionAsk.spec.options.map(opt => {
                const n = versionAsk.counts?.[opt] || 0;
                const bump = d => setVersionAsk(v => ({ ...v, counts: { ...(v.counts||{}), [opt]: Math.max(0, (v.counts?.[opt]||0) + d) } }));
                return (
                  <div key={opt} style={{display:"flex",alignItems:"center",gap:10,background:n>0?"rgba(74,222,128,0.1)":"rgba(255,255,255,0.03)",border:"1px solid "+(n>0?"rgba(74,222,128,0.45)":"var(--bz-line-2)"),borderRadius:10,padding:"8px 10px"}}>
                    <span style={{flex:1,fontSize:13.5,fontWeight:800,color:n>0?"#4ade80":"var(--bz-ink-2)"}}>{opt}</span>
                    <button onClick={()=>bump(-1)} disabled={n===0}
                      style={{background:"transparent",border:"none",color:n>0?"#4ade80":"rgba(255,255,255,0.2)",fontSize:19,fontWeight:900,cursor:n>0?"pointer":"default",padding:"2px 10px",fontFamily:"inherit",lineHeight:1}}>
                      {"\u2212"}
                    </button>
                    <span style={{minWidth:22,textAlign:"center",fontSize:15,fontWeight:900,color:n>0?"#fff":"rgba(255,255,255,0.3)"}}>{n}</span>
                    <button onClick={()=>bump(1)}
                      style={{background:"transparent",border:"none",color:"#4ade80",fontSize:19,fontWeight:900,cursor:"pointer",padding:"2px 10px",fontFamily:"inherit",lineHeight:1}}>
                      +
                    </button>
                  </div>
                );
              })}
              {(() => {
                const counts = versionAsk.counts || {};
                const total = versionAsk.spec.options.reduce((s,o)=>s+(counts[o]||0), 0);
                return (
                  <>
                    <button onClick={()=>confirmVersion(counts)} disabled={total===0}
                      style={{width:"100%",marginTop:4,background:total?"linear-gradient(135deg,#E8317A,#7B2FF7)":"#222",color:total?"#fff":"#666",border:"none",borderRadius:10,padding:"12px",fontSize:13.5,fontWeight:900,cursor:total?"pointer":"default",fontFamily:"inherit"}}>
                      {total===0 ? "Choose how many" : `Add ${total} card${total===1?"":"s"}`}
                    </button>
                    <button onClick={()=>setVersionAsk(null)}
                      style={{width:"100%",background:"transparent",border:"1px solid #333",color:"#999",borderRadius:10,padding:"10px",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                      Cancel
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {/* On-loan manager. The list you actually work from: everything currently lent or borrowed,
          with the action to resolve it right on the row. */}
      {loanMgr && (()=>{
        const all = (lots||[]).filter(l => l.lendState);
        const rows = all.filter(l => loanMgr==="all" ? true : l.lendState===loanMgr)
          .map(l => ({ lot:l, card: cards.find(x=>x.id===l.cardId) || {} }))
          .sort((a,b)=> String(a.lot.lendWho||"").localeCompare(String(b.lot.lendWho||""))
                     || String(a.lot.lendDate||"").localeCompare(String(b.lot.lendDate||"")));
        // Send status per card, from the shared borrow_ledger. For loans I sent, an
        // entry exists where ownerUid === me: "pending" = sent, awaiting accept;
        // "borrowed" = the friend accepted. Keyed by cardId (a card sent to one
        // friend has one active entry). Lets the row show accepted/awaiting, and
        // stops re-sending something already out.
        const sentStatusByCard = {};
        (borrowLedger||[]).forEach(l => {
          if (user && l.ownerUid === user.uid && (l.status==="pending" || l.status==="borrowed")) {
            const prev = sentStatusByCard[l.cardId];
            // Prefer "borrowed" (accepted) over "pending" if somehow both exist.
            if (!prev || l.status==="borrowed") {
              sentStatusByCard[l.cardId] = { status:l.status, to:l.borrowerName||"friend" };
            }
          }
        });
        const tab = (k,label)=>(
          <button key={k} onClick={()=>setLoanMgr(k)}
            style={{flex:1,background:loanMgr===k?"rgba(232,49,122,0.15)":"transparent",
              border:"1px solid "+(loanMgr===k?"#E8317A":"var(--bz-line-2)"),
              color:loanMgr===k?"#E8317A":"var(--bz-ink-2)",borderRadius:8,padding:"7px 4px",
              fontSize:11.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{label}</button>
        );
        return (
          <div onClick={()=>{setLoanMgr(null);setLoanSel(new Set());setLoanSendTo("");}} style={{position:"fixed",inset:0,zIndex:14000,background:"rgba(0,0,0,0.72)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
            <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s1,#14141c)",border:"1px solid var(--bz-line-2)",borderRadius:14,width:"100%",maxWidth:620,maxHeight:"84vh",display:"flex",flexDirection:"column",overflow:"hidden"}}>
              <div style={{padding:"14px 16px 10px",borderBottom:"1px solid var(--bz-line)"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <div>
                    <div style={{fontSize:15,fontWeight:900,color:"var(--bz-ink)"}}>Cards on loan</div>
                    {/* What the app actually holds. If a card looks borrowed on screen but is not
                        listed here, it has no per-copy record \u2014 which is what drives every borrow
                        behaviour, so nothing downstream can work. */}
                    <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:2}}>
                      {(lots||[]).length} cop{(lots||[]).length===1?"y":"ies"} tracked {"\u00b7"} {all.length} on loan
                      {" \u00b7 "}{(lots||[]).filter(l=>l.lendState==="borrowed").length} borrowed
                    </div>
                  </div>
                  <button onClick={()=>setLoanMgr(null)} style={{background:"none",border:"none",color:"var(--bz-ink-3)",fontSize:20,cursor:"pointer",lineHeight:1,fontFamily:"inherit"}}>{"\u00d7"}</button>
                </div>
                <div style={{display:"flex",gap:6}}>
                  {tab("borrowed", "\uD83D\uDCE5 Borrowing")}
                  {tab("lent", "\uD83D\uDCE4 Lent out")}
                  {tab("all", "All")}
                  <button onClick={()=>printLoanReport(loanMgr==="all"?null:loanMgr)}
                    title="Print this list"
                    style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:8,padding:"7px 11px",fontSize:11.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
                    {"\uD83D\uDDA8"}
                  </button>
                  {/* Loan by photo — snap a card, scanner IDs it, then pick who. Fast
                      in-person loans. Falls to manual search if the scan misses. */}
                  <button onClick={()=>{ setScanLoanMode(loanMgr==="borrowed"?"borrowed":"lent"); loanScanInputRef.current?.click(); }}
                    title="Scan a card to loan it"
                    style={{background:"rgba(232,49,122,0.12)",border:"1px solid rgba(232,49,122,0.4)",color:"#E8317A",borderRadius:8,padding:"7px 11px",fontSize:11.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
                    {"\uD83D\uDCF7"} Scan
                  </button>
                </div>
              </div>
              <div style={{overflowY:"auto",padding:"10px 12px 14px"}}>
                {rows.length===0 ? (
                  <div style={{textAlign:"center",color:"var(--bz-ink-3)",fontSize:12.5,padding:"26px 10px"}}>
                    {loanMgr==="borrowed" ? "You aren\u2019t borrowing anything right now."
                      : loanMgr==="lent" ? "You haven\u2019t lent anything out."
                      : "Nothing is on loan."}
                  </div>
                ) : rows.map(({lot,card})=>{
                  const borrowed = lot.lendState==="borrowed";
                  const col = borrowed ? "#7B9CFF" : "#FBBF24";
                  const sent = !borrowed ? sentStatusByCard[card.id] : null;   // send status for lent cards
                  return (
                    <div key={lot.id} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"10px 2px",borderBottom:"1px solid var(--bz-line)"}}>
                      {/* Select checkbox — only on LENT rows NOT already sent to a friend. */}
                      {!borrowed && !sent && (
                        <input type="checkbox" checked={loanSel.has(lot.id)}
                          onChange={e=>{ const n=new Set(loanSel); if(e.target.checked)n.add(lot.id); else n.delete(lot.id); setLoanSel(n); }}
                          style={{marginTop:3,width:16,height:16,accentColor:"#E8317A",cursor:"pointer",flexShrink:0}} />
                      )}
                      {/* Already sent — a spacer keeps rows aligned, no re-send checkbox. */}
                      {!borrowed && sent && <div style={{width:16,flexShrink:0}} />}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12.5,fontWeight:800,color:"var(--bz-ink)"}}>
                          {card.hero || card.playName || "(unknown card)"}
                          {card.cardNum ? <span style={{color:"var(--bz-ink-3)",fontWeight:600}}> #{card.cardNum}</span> : null}
                        </div>
                        <div style={{fontSize:10.5,color:"var(--bz-ink-3)",marginTop:1}}>
                          {[card.treatment, card.weapon, card.setName].filter(Boolean).join(" \u00b7 ")}
                        </div>
                        <div style={{fontSize:11,color:col,marginTop:3,fontWeight:700}}>
                          {borrowed ? "\uD83D\uDCE5 From" : "\uD83D\uDCE4 With"} {lot.lendWho || "\u2014"}
                          {lot.lendWhy ? <span style={{color:"var(--bz-ink-3)",fontWeight:600}}> {"\u00b7"} {lot.lendWhy}</span> : null}
                        </div>
                        {lot.lendDate && <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:1}}>Since {lot.lendDate}</div>}
                        {/* Return-by date + reminder. Setting it stamps the lot (and syncs
                            to the friend's side if this was sent through the app). */}
                        <div style={{display:"flex",alignItems:"center",gap:6,marginTop:5,flexWrap:"wrap"}}>
                          <span style={{fontSize:10,color:"var(--bz-ink-3)"}}>Return by</span>
                          <input type="date" value={lot.returnBy||""}
                            onChange={e=>setLoanReturnBy(lot.id, e.target.value)}
                            style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink)",
                                    borderRadius:6,padding:"3px 6px",fontSize:11,fontFamily:"inherit"}} />
                          {lot.returnBy && (()=>{
                            const days = Math.ceil((new Date(lot.returnBy+"T00:00:00") - new Date(todayLocal()+"T00:00:00"))/86400000);
                            const overdue = days < 0, soon = days>=0 && days<=2;
                            return (
                              <span style={{fontSize:10,fontWeight:800,color: overdue?"#F87171":soon?"#FBBF24":"var(--bz-ink-3)"}}>
                                {overdue ? `${-days}d overdue` : days===0 ? "due today" : `${days}d left`}
                              </span>
                            );
                          })()}
                          {lot.returnBy && (
                            <button onClick={()=>{
                              const dt = new Date(lot.returnBy+"T09:00:00");
                              const end = new Date(dt.getTime()+30*60000);
                              const who = lot.lendWho || (borrowed?"owner":"borrower");
                              const title = borrowed ? `Return ${card.hero||"card"} to ${who}` : `Get ${card.hero||"card"} back from ${who}`;
                              const fmt = d => d.toISOString().replace(/[-:]/g,"").split(".")[0]+"Z";
                              const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${fmt(dt)}/${fmt(end)}&details=${encodeURIComponent("Bazooka Vault loan reminder")}`;
                              window.open(url,"_blank");
                            }}
                              title="Add to calendar"
                              style={{background:"rgba(96,165,250,0.12)",border:"1px solid rgba(96,165,250,0.4)",color:"#60A5FA",
                                      borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                              📅 Add
                            </button>
                          )}
                        </div>
                        {sent && (
                          <div style={{marginTop:4,display:"inline-flex",alignItems:"center",gap:5,
                                       fontSize:10.5,fontWeight:800,borderRadius:6,padding:"2px 8px",
                                       background: sent.status==="borrowed" ? "rgba(74,222,128,0.14)" : "rgba(96,165,250,0.14)",
                                       border: `1px solid ${sent.status==="borrowed" ? "rgba(74,222,128,0.5)" : "rgba(96,165,250,0.5)"}`,
                                       color: sent.status==="borrowed" ? "#4ade80" : "#60A5FA"}}>
                            {sent.status==="borrowed"
                              ? <>{"\u2713"} Accepted by {sent.to}</>
                              : <>{"\u23F3"} Sent to {sent.to} {"\u00b7"} awaiting accept</>}
                          </div>
                        )}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",gap:5,flexShrink:0}}>
                        <button onClick={async ()=>{
                          if (borrowed) {
                            if (!window.confirm(`Give ${card.hero||"this card"} back to ${lot.lendWho||"its owner"}?\n\nIt will be removed from your collection.`)) return;
                            await returnBorrowedCopy(lot);
                            setToast("\u2713 Returned \u2014 removed from your collection");
                          } else {
                            clearLoanOn(lot.id);
                            setToast("\u2713 Marked as back in hand");
                          }
                        }} style={{background:"rgba(74,222,128,0.12)",border:"1px solid rgba(74,222,128,0.45)",color:"#4ade80",borderRadius:8,padding:"6px 10px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                          {borrowed ? "Gave it back" : "Got it back"}
                        </button>
                        {/* Two different intentions, so two buttons. For a BORROWED copy "remove"
                            means the card leaves the collection (it was never yours); clearing the
                            tag instead would silently claim someone else's card as owned. */}
                        {borrowed && (
                          <button onClick={async ()=>{
                            if (!window.confirm(`Remove ${card.hero||"this card"} from your collection?\n\nUse this if you no longer have it.`)) return;
                            await returnBorrowedCopy(lot);
                            setToast("\u2713 Removed from your collection");
                          }} style={{background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.45)",color:"#EF4444",borderRadius:8,padding:"6px 10px",fontSize:10.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                            Remove card
                          </button>
                        )}
                        <button onClick={()=>{ clearLoanOn(lot.id); setToast(borrowed ? "Now marked as yours" : "Loan tag removed"); }}
                          title={borrowed ? "Keep the card and treat it as yours" : "Clear the loan tag"}
                          style={{background:"none",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:8,padding:"6px 10px",fontSize:10.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                          {borrowed ? "It\u2019s mine now" : "Not on loan"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Send selected lent cards to a friend. Only shows on the Lent tab
                  with a selection. The friend accepts on their side before the
                  cards appear in their collection as borrowed-from-you. */}
              {loanMgr==="lent" && loanSel.size>0 && (
                <div style={{borderTop:"1px solid var(--bz-line)",padding:"12px 14px",background:"rgba(232,49,122,0.04)"}}>
                  <div style={{fontSize:11,fontWeight:800,color:"var(--bz-ink-2)",marginBottom:8}}>
                    Send {loanSel.size} selected card{loanSel.size===1?"":"s"} to a friend
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <select value={loanSendTo} onChange={e=>setLoanSendTo(e.target.value)}
                      style={{...inp,flex:"1 1 160px",fontSize:12,padding:"8px 10px"}}>
                      <option value="">Choose a friend…</option>
                      {(friends||[]).map(f=>(
                        <option key={f.friendUid} value={f.friendUid}>{f.friendName||f.friendUid}</option>
                      ))}
                    </select>
                    <button disabled={!loanSendTo || loanSending}
                      onClick={async ()=>{
                        const friend = (friends||[]).find(f=>f.friendUid===loanSendTo);
                        if (!friend) return;
                        // Map selected lot ids -> their cardIds.
                        const cardIds = [...loanSel].map(id => (lots||[]).find(l=>l.id===id)?.cardId).filter(Boolean)
                          // Never re-send a card already out to a friend (pending or accepted).
                          .filter(cid => !sentStatusByCard[cid]);
                        if (!cardIds.length) return;
                        setLoanSending(true);
                        const res = await sendLoanToFriend(friend, cardIds);
                        setLoanSending(false);
                        if (res?.ok) {
                          setToast(`\u2713 Sent ${res.count} card${res.count===1?"":"s"} to ${friend.friendName||"friend"} \u2014 they'll accept on their end`);
                          setLoanSel(new Set()); setLoanSendTo("");
                        } else {
                          setToast("Couldn't send: " + (res?.error||"try again"));
                        }
                      }}
                      style={{background: loanSendTo?"#E8317A":"#333",border:"none",color:"#fff",borderRadius:8,
                              padding:"8px 16px",fontSize:12,fontWeight:800,cursor:loanSendTo?"pointer":"not-allowed",
                              fontFamily:"inherit",flexShrink:0}}>
                      {loanSending ? "Sending…" : "📤 Send"}
                    </button>
                    <button onClick={()=>{setLoanSel(new Set());setLoanSendTo("");}}
                      style={{background:"none",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:8,
                              padding:"8px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
                      Clear
                    </button>
                  </div>
                  {(friends||[]).length===0 && (
                    <div style={{fontSize:10.5,color:"var(--bz-ink-3)",marginTop:6}}>
                      Add a friend first (Friends tab) to send cards.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {/* ── LOAN BY PHOTO ── hidden camera input; a match opens the who-prompt. */}
      <input ref={loanScanInputRef} type="file" accept="image/*" capture="environment"
        onChange={e=>{ const f=e.target.files?.[0]; if(f){ setPhotoScan(null); scanCardPhoto(f); } e.target.value=""; }}
        style={{display:"none"}} />

      {/* Who-prompt after a scan match (or the manual-entry fallback). */}
      {/* Loan scan in progress — same animated "Identifying…" indicator the
          collection scanner shows, so a loan scan doesn't look like it stalled. */}
      {scanLoanMode && photoScan?.status==="scanning" && (
        <div style={{position:"fixed",inset:0,zIndex:14000,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{textAlign:"center",padding:"40px 20px"}}>
            <div style={{position:"relative",width:160,height:213,margin:"0 auto 24px",borderRadius:14,overflow:"hidden",background:"linear-gradient(135deg,rgba(232,49,122,0.08),rgba(123,47,247,0.08))",border:"2px solid rgba(232,49,122,0.3)"}}>
              <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:48,opacity:0.25}}>🃏</div>
              <div style={{position:"absolute",left:0,right:0,height:3,background:"linear-gradient(90deg,transparent,#E8317A,#FBBF24,#E8317A,transparent)",boxShadow:"0 0 16px rgba(232,49,122,0.9)",animation:"scanBeam 1.6s ease-in-out infinite"}}/>
              <div style={{position:"absolute",top:8,left:8,width:20,height:20,borderTop:"2px solid #E8317A",borderLeft:"2px solid #E8317A"}}/>
              <div style={{position:"absolute",top:8,right:8,width:20,height:20,borderTop:"2px solid #E8317A",borderRight:"2px solid #E8317A"}}/>
              <div style={{position:"absolute",bottom:8,left:8,width:20,height:20,borderBottom:"2px solid #E8317A",borderLeft:"2px solid #E8317A"}}/>
              <div style={{position:"absolute",bottom:8,right:8,width:20,height:20,borderBottom:"2px solid #E8317A",borderRight:"2px solid #E8317A"}}/>
            </div>
            <div style={{fontSize:16,fontWeight:800,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",marginBottom:6}}>Identifying your card…</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>Reading hero, weapon &amp; treatment <span style={{display:"inline-block",animation:"scanDots 1.4s infinite"}}>●●●</span></div>
          </div>
        </div>
      )}

      {loanScanCard && (
        <LoanScanConfirm
          entry={loanScanCard}
          cards={cards}
          friends={friends}
          inp={inp}
          onCancel={()=>{ setLoanScanCard(null); setScanLoanMode(null); }}
          onConfirm={async (card, mode, who, friend, returnBy)=>{
            if (mode==="lent") {
              await markCardLent(card.id, who, returnBy);
              // Picked an app friend -> also send the loan so they can accept it.
              if (friend?.friendUid) {
                const res = await sendLoanToFriend(friend, [card.id], returnBy);
                setToast(res?.ok
                  ? `\uD83D\uDCE4 Lent ${card.hero||"card"} to ${who} \u2014 sent for them to accept`
                  : `\uD83D\uDCE4 Marked lent, but send failed: ${res?.error||"try again"}`);
              } else {
                setToast(`\uD83D\uDCE4 Lent ${card.hero||"card"} to ${who}`);
              }
            } else {
              await addBorrowedCopy(card.id, who, "");
              setToast(`\uD83D\uDCE5 Borrowed ${card.hero||"card"} from ${who}`);
            }
            setLoanScanCard(null); setScanLoanMode(null);
          }} />
      )}

      {bulkLoanOpen && (
        <BulkLoanModal
          mode={bulkLoanOpen}
          count={bulkLoanOpen==="lent" ? [...selectedIds].filter(id=>owned[id]).length : selectedIds.size}
          friends={friends}
          inp={inp}
          onCancel={()=>setBulkLoanOpen(null)}
          onConfirm={(who, friend, returnBy)=>{
            if (bulkLoanOpen==="lent") commitBulkLent(who, friend, returnBy);
            else commitBulkBorrowed(who, friend, returnBy);
          }} />
      )}

      {lotModal && <LotModal card={lotModal.card} lots={lotsForCard(lotModal.card.id)} onAdd={addLot} onUpdate={updateLot} onRemove={removeLot} onClose={()=>setLotModal(null)} inp={inp} onUploadPhoto={uploadLotPhoto} />}
      {reviewModal && <ReviewModal sale={reviewModal.sale} onSubmit={submitReview} onClose={()=>setReviewModal(null)} inp={inp} />}
      <BackToTop />
      {onboarding && user && <OnboardingModal user={user} inp={inp} onComplete={(uname,purl)=>{ setMyUsername(uname); if(purl)setMyPhotoURL(purl); usernameClaimedThisSession.current=true; try{localStorage.setItem("bazooka_username_"+user.uid,uname); if(purl)localStorage.setItem("bazooka_photo_"+user.uid,purl);}catch(e){} setOnboarding(false); showToast(`Welcome, @${uname}!`); }} />}
      {userMissingModal && (
        <div onClick={()=>setUserMissingModal(false)} style={{position:"fixed",inset:0,zIndex:10003,background:"rgba(0,0,0,0.8)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(6px)"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s2)",border:"1px solid #333",borderRadius:18,width:"min(600px,100%)",maxHeight:"88vh",overflowY:"auto",padding:"24px 24px 22px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <div style={{fontSize:19,fontWeight:900,color:"#fff"}}>⏳ Pending Cards</div>
              <button onClick={()=>setUserMissingModal(false)} style={{background:"none",border:"none",color:"#c9c9c9",fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",lineHeight:1.5,marginBottom:18}}>These cards from your import aren't in our database yet, so they're not in your collection. We've flagged them for our team to add — once they're in, you can add them to your collection. Thanks for your patience!</div>
            {userMissing.length===0 ? (
              <div style={{textAlign:"center",padding:"40px 20px",color:"rgba(255,255,255,0.4)"}}>
                <div style={{fontSize:36,marginBottom:10}}>✅</div>
                <div style={{fontSize:14,fontWeight:700}}>Nothing pending — all your imports matched.</div>
              </div>
            ) : (
              <>
                {userMissing.map((m,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:12,background:"rgba(251,191,36,0.05)",border:"1px solid rgba(251,191,36,0.2)",borderRadius:10,padding:"10px 14px",marginBottom:8}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:800,color:"#f6eef2"}}>{m.hero||"(unknown)"}{m.weapon?<span style={{color:"#FBBF24",fontWeight:700}}> · {m.weapon}</span>:""}</div>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.65)"}}>{[m.treatment,m.power?`${m.power}⚡`:"",m.cardNum?`#${m.cardNum}`:"",m.setName].filter(Boolean).join(" · ")}</div>
                    </div>
                    {m.qty>1 && <span style={{fontSize:12,color:"#FBBF24",fontWeight:800,flexShrink:0}}>×{m.qty}</span>}
                  </div>
                ))}
                <button onClick={async()=>{ if(window.confirm("Clear your pending list? This just removes them from this list — it won't affect your collection.")){ setUserMissing([]); try{ await setDoc(doc(db,"user_missing",user.uid),{cards:[]}); }catch(e){} } }} style={{width:"100%",background:"transparent",border:"1px solid rgba(255,255,255,0.12)",color:"rgba(255,255,255,0.4)",borderRadius:10,padding:"10px 0",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginTop:6}}>Clear pending list</button>
              </>
            )}
          </div>
        </div>
      )}
      {missingCardsModal && (
        <div onClick={()=>setMissingCardsModal(false)} style={{position:"fixed",inset:0,zIndex:10003,background:"rgba(0,0,0,0.8)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(6px)"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s2)",border:"1px solid #333",borderRadius:18,width:"min(620px,100%)",maxHeight:"88vh",overflowY:"auto",padding:"24px 24px 22px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <div style={{fontSize:19,fontWeight:900,color:"#fff"}}>🗂️ Missing Cards</div>
              <button onClick={()=>setMissingCardsModal(false)} style={{background:"none",border:"none",color:"var(--bz-ink-2)",fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",lineHeight:1.5,marginBottom:18}}>Cards collectors tried to import but aren't in the database yet. Add these to your set data, then dismiss them here. {missingCards.length>0 && <span style={{color:"#FBBF24",fontWeight:700}}>{missingCards.length} to add.</span>}</div>
            {missingCards.length===0 ? (
              <div style={{textAlign:"center",padding:"40px 20px",color:"rgba(255,255,255,0.4)"}}>
                <div style={{fontSize:36,marginBottom:10}}>✅</div>
                <div style={{fontSize:14,fontWeight:700}}>All caught up — nothing missing.</div>
              </div>
            ) : (
              <div>
                {missingCards.map(m=>(
                  <div key={m.id} style={{display:"flex",alignItems:"center",gap:12,background:"rgba(255,255,255,0.02)",border:"1px solid var(--bz-line-2)",borderRadius:10,padding:"10px 14px",marginBottom:8}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:800,color:"#f6eef2"}}>{m.hero||"(unknown)"}{m.weapon?<span style={{color:"#7B9CFF",fontWeight:700}}> · {m.weapon}</span>:""}</div>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.65)"}}>{[m.treatment,m.power?`${m.power}⚡`:"",m.cardNum?`#${m.cardNum}`:"",m.setName].filter(Boolean).join(" · ")}</div>
                      {Array.isArray(m.requestedBy)&&m.requestedBy.length>0 && <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginTop:2}}>Requested by {m.requestedBy.length} collector{m.requestedBy.length!==1?"s":""}</div>}
                    </div>
                    <button onClick={()=>dismissMissingCard(m.id)} title="Mark as added / dismiss" style={{background:"rgba(74,222,128,0.1)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>✓ Done</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {importModal && (
        <div onClick={()=>{ if(!importing){ setImportModal(false); setImportRows(null); setImportRaw(null); setImportSetMap({}); setImportColMap(null); setColMapConfirmed(false); } }} style={{position:"fixed",inset:0,zIndex:10003,background:"rgba(0,0,0,0.8)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(6px)"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:18,width:"min(560px,100%)",maxHeight:"88vh",overflowY:"auto",padding:"24px 24px 22px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div style={{fontSize:19,fontWeight:900,color:"#fff"}}>Import Your Collection</div>
              <button onClick={()=>{ if(!importing){ setImportModal(false); setImportRows(null); setImportRaw(null); setImportSetMap({}); setImportColMap(null); setColMapConfirmed(false); } }} style={{background:"none",border:"none",color:"var(--bz-ink-2)",fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",lineHeight:1.5,marginBottom:12}}>Already track your collection somewhere else? Export it as a CSV and drop it in here — we'll match your cards automatically. Or download our template, fill it out, and import.</div>
            <div style={{fontSize:12,color:"#FBBF24",background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.25)",borderRadius:10,padding:"10px 12px",marginBottom:18,lineHeight:1.5}}>⚠️ <strong>The "Set" column is required.</strong> The same hero can appear in multiple sets, so we need the set name to match the right card. Make sure every card has its set filled in before importing.</div>

            {importRows ? (()=>{
              const matched = importRows.filter(r=>r.match);
              const unmatched = importRows.filter(r=>!r.match);
              const totalCards = matched.reduce((s,r)=>s+(r.csv.qty||1),0);
              return (
                <>
                  <div style={{display:"flex",gap:10,marginBottom:16}}>
                    <div style={{flex:1,background:"rgba(74,222,128,0.08)",border:"1px solid rgba(74,222,128,0.25)",borderRadius:10,padding:"10px 12px"}}>
                      <div style={{fontSize:22,fontWeight:900,color:"#4ade80"}}>{matched.length}</div>
                      <div style={{fontSize:10,color:"var(--bz-ink-2)",fontWeight:700,textTransform:"uppercase"}}>Matched ({totalCards} cards)</div>
                    </div>
                    <div style={{flex:1,background:"rgba(251,191,36,0.07)",border:"1px solid rgba(251,191,36,0.25)",borderRadius:10,padding:"10px 12px"}}>
                      <div style={{fontSize:22,fontWeight:900,color:"#FBBF24"}}>{unmatched.length}</div>
                      <div style={{fontSize:10,color:"var(--bz-ink-2)",fontWeight:700,textTransform:"uppercase"}}>Not found</div>
                    </div>
                  </div>
                  {unmatched.length>0 && (
                    <div style={{marginBottom:14}}>
                      <div style={{fontSize:11,color:"#FBBF24",fontWeight:700,marginBottom:6}}>These couldn't be matched and will be skipped:</div>
                      <div style={{maxHeight:120,overflowY:"auto",background:"rgba(255,255,255,0.02)",borderRadius:8,padding:8}}>
                        {unmatched.slice(0,40).map((r,i)=>(
                          <div key={i} style={{fontSize:11,color:"#999",padding:"2px 0"}}>{r.csv.hero||"?"} · {r.csv.parallel||""} {r.csv.weapon?`(${r.csv.weapon})`:""} {r.csv.cardNum?`#${r.csv.cardNum}`:""}{r.csv.setName?` — ${r.csv.setName}`:""}</div>
                        ))}
                        {unmatched.length>40 && <div style={{fontSize:11,color:"#999",marginTop:4}}>…and {unmatched.length-40} more</div>}
                      </div>
                    </div>
                  )}
                  {/* Bulk lock — easier than adding a Locked column when the whole import is
                      keepers (a graded slab batch, a PC binder). The CSV column still wins per row. */}
                  <label style={{display:"flex",alignItems:"center",gap:9,cursor:"pointer",background:importLockAll?"rgba(251,191,36,0.1)":"rgba(255,255,255,0.03)",border:"1px solid "+(importLockAll?"rgba(251,191,36,0.4)":"rgba(255,255,255,0.1)"),borderRadius:10,padding:"10px 12px",marginBottom:10}}>
                    <input type="checkbox" checked={importLockAll} onChange={e=>setImportLockAll(e.target.checked)} style={{accentColor:"#FBBF24",width:16,height:16}}/>
                    <span style={{fontSize:12.5,fontWeight:800,color:importLockAll?"#FBBF24":"rgba(255,255,255,0.65)"}}>
                      {"\uD83D\uDD12"} Lock every card in this import
                    </span>
                    <span style={{fontSize:10.5,color:"rgba(255,255,255,0.35)"}}>never trade or sell</span>
                  </label>
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={()=>setImportRows(null)} disabled={importing} style={{flex:1,background:"transparent",border:"1px solid rgba(255,255,255,0.15)",color:"#ccc",borderRadius:12,padding:"13px 0",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Back</button>
                    <button onClick={runImport} disabled={importing||matched.length===0} style={{flex:2,background:matched.length?"linear-gradient(135deg,#4ade80,#22c55e)":"#333",color:matched.length?"#000":"#666",border:"none",borderRadius:12,padding:"13px 0",fontSize:14,fontWeight:900,cursor:importing?"wait":matched.length?"pointer":"not-allowed",fontFamily:"inherit"}}>{importing?"Importing…":`Import ${totalCards} card${totalCards!==1?"s":""}`}</button>
                  </div>
                </>
              );
            })() : (importRaw && !colMapConfirmed) ? (()=>{
              const FIELDS = [
                {key:"Name", label:"Card / Player Name", required:true, hint:"The hero or player name"},
                {key:"Set", label:"Set", required:true, hint:"Required — which release the card is from"},
                {key:"Card Number", label:"Card Number", required:false, hint:"Best for exact matching"},
                {key:"Parallel", label:"Parallel / Treatment", required:false, hint:"e.g. Battlefoil, Hex"},
                {key:"Weapon", label:"Weapon", required:false, hint:"Weapon type, if your file has it"},
                {key:"Power", label:"Power", required:false, hint:"Power level"},
                {key:"Quantity", label:"Quantity", required:false, hint:"How many you own (defaults to 1)"},
                {key:"Estimated Value", label:"Value", required:false, hint:"Market / estimated value"},
                {key:"Purchase Price", label:"Purchase price", required:false, hint:"What you paid"},
                {key:"Trade Value", label:"Trade value", required:false, hint:"Value of cards you gave in the deal"},
                {key:"Serial", label:"Serial", required:false, hint:"e.g. 21/50 — goes on the first copy"},
                {key:"Notes", label:"Notes", required:false, hint:"Your note about this copy"},
                {key:"Legacy ID", label:"Legacy ID", required:false, hint:"Your old system’s record id — stored, never shown"},
                {key:"Purchase Date", label:"Purchase date", required:false, hint:"When you got it"},
                {key:"Condition", label:"Condition", required:false, hint:"Mint, Near Mint…"},
                {key:"Grading Company", label:"Grading co.", required:false, hint:"PSA, BGS, SGC…"},
                {key:"Grade", label:"Grade", required:false, hint:"e.g. 9.5"},
                {key:"Cert #", label:"Cert #", required:false, hint:"Slab certification number"},
                {key:"Location", label:"Location", required:false, hint:"Binder, safe, slab case…"},
                {key:"Locked", label:"Locked", required:false, hint:"YES = never trade or sell"},
              ];
              const cm = importColMap || {};
              const nameOk = cm["Name"]>=0, setOk = cm["Set"]>=0;
              const canContinue = nameOk && setOk;
              const detectedCount = Object.values(cm).filter(v=>v>=0).length;
              return (
                <>
                  <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",lineHeight:1.5,marginBottom:8}}>We looked at your file's columns and matched them up automatically. <strong style={{color:"#4ade80"}}>Check they're right</strong> and fix any that are off, then continue.</div>
                  <div style={{fontSize:12,color:"#7B9CFF",background:"rgba(123,156,255,0.08)",border:"1px solid rgba(123,156,255,0.25)",borderRadius:10,padding:"9px 12px",marginBottom:16,lineHeight:1.5}}>✨ Auto-matched {detectedCount} column{detectedCount!==1?"s":""} from your file. Anything set to “— not in my file —” just means we'll skip it.</div>
                  <div style={{maxHeight:340,overflowY:"auto",marginBottom:16}}>
                    {FIELDS.map(f=>{
                      const idx = cm[f.key];
                      const isSet = idx>=0;
                      const missingReq = f.required && !isSet;
                      const borderC = missingReq?"rgba(232,49,122,0.4)":isSet?"rgba(74,222,128,0.25)":"rgba(255,255,255,0.1)";
                      return (
                        <div key={f.key} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,background:"rgba(255,255,255,0.02)",border:`1px solid ${borderC}`,borderRadius:10,padding:"10px 12px"}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)"}}>{f.label}{f.required&&<span style={{color:"#E8317A",marginLeft:4}}>*</span>}</div>
                            <div style={{fontSize:10,color:"#999"}}>{f.hint}</div>
                          </div>
                          <span style={{color:"var(--bz-ink-3)",fontSize:14}}>←</span>
                          <select value={idx>=0?String(idx):""} onChange={e=>{ const v=e.target.value===""?-1:parseInt(e.target.value); setImportColMap(prev=>({...prev,[f.key]:v})); }} style={{...inp,flex:1.2,minWidth:0,cursor:"pointer",fontSize:12,borderColor:missingReq?"rgba(232,49,122,0.5)":isSet?"rgba(74,222,128,0.4)":"rgba(255,255,255,0.15)"}}>
                            <option value="">— not in my file —</option>
                            {importRaw.hdr.map((h,i)=><option key={i} value={i}>{h||`Column ${i+1}`}</option>)}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                  {!canContinue && <div style={{fontSize:11,color:"#E8317A",marginBottom:12,textAlign:"center"}}>Name and Set are required — pick which of your columns hold those.</div>}
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={()=>{ setImportRaw(null); setImportColMap(null); }} style={{flex:1,background:"transparent",border:"1px solid rgba(255,255,255,0.15)",color:"#ccc",borderRadius:12,padding:"13px 0",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Back</button>
                    <button onClick={confirmColumnMapAndProceed} disabled={!canContinue} style={{flex:2,background:canContinue?"linear-gradient(135deg,#7B9CFF,#4ade80)":"#333",color:canContinue?"#000":"#666",border:"none",borderRadius:12,padding:"13px 0",fontSize:14,fontWeight:900,cursor:canContinue?"pointer":"not-allowed",fontFamily:"inherit"}}>{canContinue?"Continue →":"Map Name & Set"}</button>
                  </div>
                </>
              );
            })() : importRaw ? (()=>{
              const setIdx = importColMap ? importColMap["Set"] : importRaw.hdr.findIndex(h=>h.toLowerCase().trim()==="set");
              const csvSets = [...new Set(importRaw.records.map(r=>(r[setIdx]||"").trim()))].sort((a,b)=>(a||"").localeCompare(b||""));
              const ourSets = [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort();
              // Every set needs a choice — a real set OR "skip" (not in our database)
              const allMapped = csvSets.every(cs => cs==="" ? true : !!importSetMap[cs]);
              const skipCount = csvSets.filter(cs=>cs!==""&&importSetMap[cs]==="__SKIP__").reduce((s,cs)=>s+importRaw.records.filter(r=>(r[setIdx]||"").trim()===cs).length,0);
              const countFor = cs => importRaw.records.filter(r=>(r[setIdx]||"").trim()===cs).length;
              return (
                <>
                  <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",lineHeight:1.5,marginBottom:14}}>Match each set in your file to a Bazooka set. We pre-filled obvious matches. If we don't carry a set yet, choose <strong style={{color:"#FBBF24"}}>"Not in database"</strong> — those cards will be saved to your Pending list so we know to add them.</div>
                  <div style={{maxHeight:320,overflowY:"auto",marginBottom:16}}>
                    {csvSets.filter(cs=>cs!=="").map(cs=>{
                      const isSkip = importSetMap[cs]==="__SKIP__";
                      const isMapped = importSetMap[cs] && !isSkip;
                      const borderC = isMapped?"rgba(74,222,128,0.25)":isSkip?"rgba(123,156,255,0.3)":"rgba(251,191,36,0.3)";
                      return (
                      <div key={cs} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,background:"rgba(255,255,255,0.02)",border:`1px solid ${borderC}`,borderRadius:10,padding:"10px 12px"}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{cs}</div>
                          <div style={{fontSize:10,color:"#999"}}>{countFor(cs)} card{countFor(cs)!==1?"s":""}</div>
                        </div>
                        <span style={{color:"var(--bz-ink-3)",fontSize:14}}>→</span>
                        <select value={importSetMap[cs]||""} onChange={e=>setImportSetMap(prev=>({...prev,[cs]:e.target.value}))} style={{...inp,flex:1.2,minWidth:0,cursor:"pointer",fontSize:12,borderColor:isMapped?"rgba(74,222,128,0.4)":isSkip?"rgba(123,156,255,0.4)":"rgba(251,191,36,0.4)"}}>
                          <option value="">— pick a set —</option>
                          {ourSets.map(os=><option key={os} value={os}>{os}</option>)}
                          <option value="__SKIP__">⊘ Not in database (skip)</option>
                        </select>
                      </div>
                      );
                    })}
                  </div>
                  {skipCount>0 && <div style={{fontSize:11,color:"#7B9CFF",marginBottom:12,textAlign:"center"}}>{skipCount} card{skipCount!==1?"s":""} will go to your Pending list (not in our database yet)</div>}
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={()=>{ setColMapConfirmed(false); setImportSetMap({}); }} style={{flex:1,background:"transparent",border:"1px solid rgba(255,255,255,0.15)",color:"#ccc",borderRadius:12,padding:"13px 0",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Back</button>
                    <button onClick={applySetMappingAndMatch} disabled={!allMapped} style={{flex:2,background:allMapped?"linear-gradient(135deg,#7B9CFF,#4ade80)":"#333",color:allMapped?"#000":"#666",border:"none",borderRadius:12,padding:"13px 0",fontSize:14,fontWeight:900,cursor:allMapped?"pointer":"not-allowed",fontFamily:"inherit"}}>{allMapped?"Continue →":"Choose for every set"}</button>
                  </div>
                </>
              );
            })() : (
              <>
                <label style={{display:"block",cursor:"pointer",marginBottom:12}}>
                  <div style={{background:"linear-gradient(135deg,rgba(123,156,255,0.06),rgba(74,222,128,0.05))",border:"2px dashed rgba(123,156,255,0.35)",borderRadius:14,padding:"34px 20px",textAlign:"center"}}>
                    <div style={{fontSize:40,marginBottom:8}}>{"\uD83D\uDCC4"}</div>
                    <div style={{fontSize:16,fontWeight:800,color:"#7B9CFF",marginBottom:4}}>Choose a CSV file</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>Next you'll match your sets to ours, then import</div>
                  </div>
                  <input type="file" accept=".csv,text/csv" style={{display:"none"}} onChange={e=>{ const f=e.target.files?.[0]; if(f) handleImportFile(f); e.target.value=""; }}/>
                </label>
                <button onClick={downloadImportTemplate} style={{width:"100%",background:"transparent",border:"1px solid rgba(255,255,255,0.15)",color:"#ccc",borderRadius:12,padding:"12px 0",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{"\u2B07 Download blank template"} <span style={{opacity:0.5,fontWeight:600}}>(incl. Serial &amp; Notes)</span></button>

                {/* IMPORTER FAQ. Every answer here reflects what runImport()/matchImportRow() actually
                    do — people were going to ask, and a wrong FAQ is worse than none. */}
                <button onClick={()=>setImportFaqOpen(v=>!v)} style={{width:"100%",background:"transparent",border:"1px solid rgba(255,255,255,0.12)",color:"rgba(255,255,255,0.6)",borderRadius:12,padding:"10px 0",fontSize:12.5,fontWeight:700,cursor:"pointer",fontFamily:"inherit",marginTop:8}}>
                  {importFaqOpen ? "\u25B4" : "\u25BE"} Import FAQ — how matching, locking &amp; copies work
                </button>
                {importFaqOpen && (
                  <div style={{marginTop:8,background:"rgba(0,0,0,0.25)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:12,padding:"14px 16px",fontSize:12,color:"var(--bz-ink-2)",lineHeight:1.65,maxHeight:"46vh",overflowY:"auto"}}>
                    {[
                      ["Which columns do I actually need?",
                       "Name, Set and Card Number \u2014 everything else is optional. Those three are what pin a row to one specific card: the same hero shows up in several sets, and the same set has many cards, so leaving one out makes a row much harder to place."],
                      ["How does it know which card my row is?",
                       "It looks for the one card that fits everything you gave it \u2014 card number, set, hero, parallel, weapon. The more of those you fill in, the more certain the match. If it can't find a confident match it does NOT guess: the row is listed as unmatched and left out of your collection."],
                      ["What happens to rows it can't match?",
                       "They're shown to you before you import, and logged so an admin can add the missing cards to the database. They are NOT added to your collection."],
                      ["What does Quantity do?",
                       "It creates that many separate copies \u2014 three means three individual records you can price, photograph, grade and lock independently. If your copies differ (different serials, one graded, one locked), give each its own row with Quantity 1 instead; that way every detail lands on the right copy."],
                      ["Why did my serial only land on one copy?",
                       "A serial identifies one specific card, so \u201c21/50\u201d can't describe three copies. On a multi-quantity row the serial (and cert number) go on the first copy only. If every copy has its own serial, the cleanest fix is to give each card its own row with Quantity 1 \u2014 otherwise open the card afterwards and edit each copy."],
                      ["What does \uD83D\uDD12 Locked actually do?",
                       "It marks a copy \u201cnever trade or sell\u201d. Locked cards are blocked from bulk sold/traded, bulk listing, single listings, trade packages and trade bait \u2014 so you can't part with them by accident."],
                      ["I own three of a card and locked one. Is the card locked?",
                       "No. A card only counts as locked when EVERY copy you own is locked. Lock one of three and the other two can still be sold or traded \u2014 which is usually what you want."],
                      ["Lock toggle vs the Locked column?",
                       "The toggle locks everything in the import \u2014 good for a slab batch or PC binder. The column locks individual rows (YES, Y, TRUE, 1, X). Use either, or both: the toggle locks all, and the column can lock extras."],
                      ["Can I unlock later?",
                       "Yes. Open the card, hit \u201cAdd copy details\u201d, and untick the lock on that copy. Locking is never permanent."],
                      ["What if I import the same file twice?",
                       "You'll get duplicate copies \u2014 the importer adds, it doesn't reconcile. If you re-import, remove the rows you already brought in, or clean up afterwards with the card's copy list."],
                      ["Does importing overwrite what I already own?",
                       "No. Quantities are added to your existing counts, and existing copies are left untouched."],
                      ["Can I add photos in the CSV?",
                       "No \u2014 photos upload from your device. After importing, open a card and use \u201c+ front photo\u201d / \u201c+ back photo\u201d on any copy."],
                      ["Grading columns \u2014 what goes where?",
                       "Grading Company is the grader (PSA, BGS, SGC, CGC, TAG), Grade is the number (e.g. 9.5), Cert # is the slab's certification number. Leave all three blank for raw cards."],
                      ["Do Set, Parallel, Weapon and Condition have to match your exact wording?",
                       "No. Matching ignores case, spaces, hyphens and dots, and Set also matches partially \u2014 so \u201cTemo Bowl\u201d, \u201cTecmo Bowl\u201d and \u201ctecmobowl\u201d all land the same way. A row is only rejected if nothing in the database fits; it's never silently mapped to the wrong card. Condition is free text and never rejects a row \u2014 it's your note about the copy, not a lookup."],
                      ["Which of my fields should go in Parallel?",
                       "Parallel is the card's treatment \u2014 Silver Blast, Inspired Ink Battlefoil, 80's Rad Battlefoil. If your export has a subset that isn't a finish (a themed insert grouping, say), leave Parallel blank and rely on Card Number + Set. A wrong Parallel is worse than a blank one: blank falls through to the card-number match, wrong actively blocks it."],
                      ["What Serial formats work?",
                       "All of these: 14, 14/25, #14/25, and \u201c14 of 25\u201d. Anything in the shape of a number over a number is tidied to 14/25; a bare number is kept as you wrote it, since we can't invent a print run you didn't give us. Serial is free text, so odd formats are preserved rather than rejected."],
                      ["What counts as Locked?",
                       "YES, Y, TRUE, 1, X, LOCK or LOCKED (any case) all lock the copy. Blank, NO, FALSE or 0 leave it unlocked. There's also a toggle on the import screen to lock the whole batch without touching the spreadsheet."],
                      ["Can Quantity be more than 1 if I've filled in notes, condition or price?",
                       "Yes, but everything on that row is copied to every copy \u2014 same condition, same location, same price, same notes. Only Serial and Cert # are treated as unique to one card, landing on the first copy. If your copies genuinely differ, give each its own row with Quantity 1."],
                      ["I paid cash AND traded cards. How do I record that?",
                       "Put the cash in Purchase Price and your estimate of the cards you gave up in Trade Value. Cost basis is the two added together. Pay $1,600 plus three cards you'd value at $600, and the card cost you $2,200 \u2014 recording only the cash would make it look like a $1,600 card and throw off every average built on it. Nobody formally states what trade cards were credited at, so this is your call; a rough number beats leaving it blank."],
                      ["Purchase Price vs Estimated Value \u2014 what's the difference?",
                       "Purchase Price is what you paid; Estimated Value is what it's worth now. Both import, and both matter \u2014 cost drives profit and loss, value drives what your collection is worth. A row with a purchase price is logged as a purchase automatically."],
                      ["What's Legacy ID for?",
                       "If you're moving from another tracker, put its record id in this column and it's stored quietly against each copy. It's never shown to collectors and never used for matching \u2014 it exists so a migration can be audited or reconciled later, and so support can trace a copy back to where it came from. On a multi-quantity row each copy gets the id with a #1, #2 suffix so every record stays individually traceable."],
                      ["My columns are named differently. Do I have to rename them?",
                       "Usually not. Common variations are detected automatically (qty/count for Quantity, price/value for Estimated Value, and so on). Whatever it can't work out, you can map by hand on the next screen before importing."],
                    ].map(([q,a],i)=>(
                      <div key={i} style={{marginBottom:11}}>
                        <div style={{fontWeight:800,color:"#fff",marginBottom:2}}>{q}</div>
                        <div style={{color:"rgba(255,255,255,0.55)"}}>{a}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",textAlign:"center",marginTop:10}}>Columns: Name, Set, Card Number, Parallel, Weapon, Power, Quantity, Estimated Value, Purchase Price, Trade Value, Serial, Notes, Legacy ID, Purchase Date, Condition, Grading Company, Grade, Cert #, Location, Locked</div>
              </>
            )}
          </div>
        </div>
      )}
      {editProfileOpen && user && (
        <div onClick={()=>setEditProfileOpen(false)} style={{position:"fixed",inset:0,zIndex:10003,background:"rgba(0,0,0,0.8)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(6px)"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:18,width:"min(420px,100%)",padding:"24px 24px 22px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
              <div style={{fontSize:18,fontWeight:900,color:"#fff"}}>Edit Profile</div>
              <button onClick={()=>setEditProfileOpen(false)} style={{background:"none",border:"none",color:"var(--bz-ink-2)",fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:14}}>
              {(myPhotoURL||user.photoURL)
                ? <img src={myPhotoURL||user.photoURL} alt="" style={{width:96,height:96,borderRadius:"50%",objectFit:"cover",border:"3px solid #E8317A",boxShadow:"0 0 24px rgba(232,49,122,0.4)"}}/>
                : <div style={{width:96,height:96,borderRadius:"50%",background:"linear-gradient(135deg,#E8317A,#7B2FF7)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:36,fontWeight:900,color:"#fff"}}>{(myUsername||user.displayName||user.email||"?").charAt(myUsername?1:0).toUpperCase()}</div>}
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:15,fontWeight:800,color:"#fff"}}>{myUsername?`@${myUsername}`:(user.displayName||"Collector")}</div>
                <div style={{fontSize:12,color:"#999"}}>{user.email}</div>
              </div>
              <label style={{display:"block",width:"100%",cursor:editPicUploading?"wait":"pointer"}}>
                <div style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",borderRadius:12,padding:"12px 0",fontSize:14,fontWeight:800,textAlign:"center",opacity:editPicUploading?0.6:1}}>
                  {editPicUploading?"Uploading…":(myPhotoURL||user.photoURL)?"📷 Change Picture":"📷 Add Picture"}
                </div>
                <input type="file" accept="image/*" disabled={editPicUploading} style={{display:"none"}} onChange={e=>{ const f=e.target.files?.[0]; if(f) updateProfilePic(f); e.target.value=""; }}/>
              </label>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",textAlign:"center"}}>Your username can't be changed once claimed.</div>
              <div style={{width:"100%",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:"14px 14px 4px",marginTop:6}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <div style={{fontSize:12,fontWeight:800,color:"rgba(255,255,255,0.6)"}}>Community handles</div>
                  {myUsername && <button onClick={()=>{ setMyDiscord(myUsername); setMyWhatnot(myUsername); }} style={{background:"rgba(123,156,255,0.12)",border:"1px solid rgba(123,156,255,0.3)",color:"#7B9CFF",borderRadius:7,padding:"4px 10px",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Same as @{myUsername}</button>}
                </div>
                <div style={{marginBottom:10}}>
                  <label style={{fontSize:11,color:"#8b9cff",fontWeight:700,display:"block",marginBottom:4}}>💬 Discord</label>
                  <input value={myDiscord} onChange={e=>setMyDiscord(e.target.value)} placeholder="your discord username" maxLength={40} style={{...inp,width:"100%",boxSizing:"border-box",fontSize:13}}/>
                </div>
                <div style={{marginBottom:10}}>
                  <label style={{fontSize:11,color:"#FBBF24",fontWeight:700,display:"block",marginBottom:4}}>🛍️ Whatnot</label>
                  <input value={myWhatnot} onChange={e=>setMyWhatnot(e.target.value)} placeholder="your whatnot username" maxLength={40} style={{...inp,width:"100%",boxSizing:"border-box",fontSize:13}}/>
                </div>
                <button onClick={async()=>{ let ok=false; try{ await setDoc(doc(db,"users",user.uid),{discordName:myDiscord.trim(),whatnotName:myWhatnot.trim()},{merge:true}); ok=true; }catch(e){ console.error(e); alert("Couldn't save. Try again."); } if(ok) showToast("Handles saved!"); }} style={{width:"100%",background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:10,padding:"10px 0",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",marginBottom:10}}>Save handles</button>
              </div>
              <div style={{width:"100%",borderTop:"1px solid var(--bz-line)",marginTop:6,paddingTop:14}}>
                <button onClick={clearCollection} style={{width:"100%",background:"transparent",border:"1px solid rgba(239,68,68,0.4)",color:"#EF4444",borderRadius:12,padding:"11px 0",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>🗑 Clear my collection</button>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",textAlign:"center",marginTop:6}}>Removes all owned cards and saved details. Can't be undone.</div>
              </div>
            </div>
          </div>
        </div>
      )}
      {milestone && (
        <div style={{position:"fixed",top:24,left:"50%",transform:"translateX(-50%)",zIndex:10001,pointerEvents:"none",animation:"milestonePop 0.5s cubic-bezier(0.34,1.56,0.64,1)"}}>
          <div style={{background:"linear-gradient(135deg,#E8317A,#FBBF24)",borderRadius:14,padding:"14px 28px",boxShadow:"0 8px 40px rgba(232,49,122,0.6)",textAlign:"center",border:"2px solid rgba(255,255,255,0.3)"}}>
            <div style={{fontSize:20,fontWeight:900,color:"#fff",letterSpacing:1,textShadow:"0 2px 8px rgba(0,0,0,0.3)"}}>🎉 {milestone.label}</div>
            <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.9)",marginTop:2}}>{milestone.sub}</div>
          </div>
        </div>
      )}

      {/* ── RAINBOW COMPLETE CELEBRATION ── */}
      {rainbowFx && <RainbowCelebration fx={rainbowFx} onDone={()=>setRainbowFx(null)} />}

      {/* Sign-in modal */}
      {signingIn&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.9)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(20px)"}} onClick={()=>setSigningIn(false)}>
          <div style={{background:"linear-gradient(135deg,var(--bz-s1),#1a0a12)",border:"1px solid rgba(232,49,122,0.3)",borderRadius:24,padding:40,textAlign:"center",maxWidth:380,boxShadow:"0 40px 120px rgba(232,49,122,0.2)",animation:"floatUp 0.4s ease"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:48,marginBottom:16}}>{"\uD83C\uDCCF"}</div>
            <div style={{fontSize:22,fontWeight:900,color:"var(--bz-ink)",marginBottom:8}}>Join the Community</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",marginBottom:28,lineHeight:1.6}}>Track your collection, scan cards, build decks, and connect with BoBA collectors worldwide.</div>
            <button onClick={signInGoogle} style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:14,padding:"14px 32px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit",width:"100%",boxShadow:"0 8px 32px rgba(232,49,122,0.4)",transition:"transform 0.2s,box-shadow 0.2s"}}
              onMouseEnter={e=>{e.target.style.transform="translateY(-2px)";e.target.style.boxShadow="0 16px 48px rgba(232,49,122,0.5)";}}
              onMouseLeave={e=>{e.target.style.transform="";e.target.style.boxShadow="0 8px 32px rgba(232,49,122,0.4)";}}>
              Sign in with Google
            </button>
            <div style={{display:"flex",alignItems:"center",gap:10,margin:"18px 0"}}>
              <div style={{flex:1,height:1,background:"rgba(255,255,255,0.1)"}}/>
              <span style={{fontSize:11,color:"rgba(255,255,255,0.3)",fontWeight:700}}>OR</span>
              <div style={{flex:1,height:1,background:"rgba(255,255,255,0.1)"}}/>
            </div>
            {authMode==="signup" && (
              <input value={authName} onChange={e=>setAuthName(e.target.value)} placeholder="Display name (optional)" style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:12,padding:"12px 14px",fontSize:14,color:"#fff",fontFamily:"inherit",marginBottom:10,outline:"none",boxSizing:"border-box"}}/>
            )}
            <input type="email" value={authEmail} onChange={e=>setAuthEmail(e.target.value)} placeholder="Email" autoComplete="email" style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:12,padding:"12px 14px",fontSize:14,color:"#fff",fontFamily:"inherit",marginBottom:10,outline:"none",boxSizing:"border-box"}}/>
            {authMode!=="reset" && authMode!=="magic" && (
              <input type="password" value={authPass} onChange={e=>setAuthPass(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")handleEmailAuth();}} placeholder="Password" autoComplete={authMode==="signup"?"new-password":"current-password"} style={{width:"100%",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:12,padding:"12px 14px",fontSize:14,color:"#fff",fontFamily:"inherit",marginBottom:10,outline:"none",boxSizing:"border-box"}}/>
            )}
            {authErr && <div style={{fontSize:12,color:"#f87171",marginBottom:10,textAlign:"left"}}>{authErr}</div>}
            {authMsg && <div style={{fontSize:12,color:"#4ade80",marginBottom:10,textAlign:"left"}}>{authMsg}</div>}
            <button onClick={authMode==="magic"?sendMagicLink:handleEmailAuth} disabled={authBusy} style={{width:"100%",background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.2)",color:"#fff",borderRadius:12,padding:"12px 0",fontSize:14,fontWeight:800,cursor:authBusy?"wait":"pointer",fontFamily:"inherit",marginBottom:12}}>
              {authBusy?"Please wait…":authMode==="signup"?"Create Account":authMode==="reset"?"Send Reset Link":authMode==="magic"?"Email me a sign-in link":"Sign In"}</button>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",lineHeight:1.8}}>
              {authMode==="signin" && <>New here? <span onClick={()=>{setAuthMode("signup");setAuthErr("");setAuthMsg("");}} style={{color:"#E8317A",fontWeight:700,cursor:"pointer"}}>Create an account</span><br/><span onClick={()=>{setAuthMode("magic");setAuthErr("");setAuthMsg("");}} style={{color:"rgba(255,255,255,0.6)",cursor:"pointer"}}>Email me a sign-in link instead</span> · <span onClick={()=>{setAuthMode("reset");setAuthErr("");setAuthMsg("");}} style={{color:"rgba(255,255,255,0.5)",cursor:"pointer"}}>Forgot password?</span></>}
              {authMode==="signup" && <>Already have an account? <span onClick={()=>{setAuthMode("signin");setAuthErr("");setAuthMsg("");}} style={{color:"#E8317A",fontWeight:700,cursor:"pointer"}}>Sign in</span></>}
              {authMode==="reset" && <><span onClick={()=>{setAuthMode("signin");setAuthErr("");setAuthMsg("");}} style={{color:"#E8317A",fontWeight:700,cursor:"pointer"}}>← Back to sign in</span><br/><span style={{color:"rgba(255,255,255,0.35)",fontSize:11}}>Signed up with Google? You don't have a password — just use the Google button above.</span></>}
              {authMode==="magic" && <><span style={{color:"rgba(255,255,255,0.5)"}}>We'll email you a link — click it to sign in, no password needed.</span><br/><span onClick={()=>{setAuthMode("signin");setAuthErr("");setAuthMsg("");}} style={{color:"#E8317A",fontWeight:700,cursor:"pointer"}}>← Back to sign in</span></>}
            </div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.35)",marginTop:16,lineHeight:1.5}}>
              By signing in, you agree to our <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{color:"#E8317A",fontWeight:700,textDecoration:"none"}}>Privacy Policy</a>.
            </div>
          </div>
        </div>
      )}

      {/* Listing modal */}
      {listModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:13000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(20px)"}} onClick={()=>setListModal(null)}>
          <div style={{background:"linear-gradient(135deg,var(--bz-s1),#0a1a0a)",border:"1px solid rgba(74,222,128,0.3)",borderRadius:24,padding:32,width:420,maxWidth:"90vw",boxShadow:"0 40px 120px rgba(74,222,128,0.15)",animation:"floatUp 0.3s ease"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:18,fontWeight:900,color:"#4ade80",marginBottom:4}}>{"\uD83D\uDCB0 List for Sale/Trade"}</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",marginBottom:20}}>{listModal.hero} · {listModal.treatment} · {listModal.power}{"\u26A1"}</div>
            <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
              {[["sale","For Sale"],["trade","For Trade"],["either","Sale or Trade"],["offer","Best Offer"]].map(([v,l])=>(
                <button key={v} onClick={()=>setListType(v)} style={{flex:1,minWidth:"45%",background:listType===v?"rgba(74,222,128,0.15)":"transparent",border:"1.5px solid "+(listType===v?"#4ade80":"rgba(255,255,255,0.1)"),color:listType===v?"#4ade80":"rgba(255,255,255,0.4)",borderRadius:10,padding:"8px 0",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
              ))}
            </div>

            {listType==="offer"?(
              <div style={{background:"rgba(74,222,128,0.05)",border:"1px solid rgba(74,222,128,0.15)",borderRadius:10,padding:"12px 14px",marginBottom:10}}>
                <div style={{fontSize:13,fontWeight:700,color:"#4ade80",marginBottom:4}}>{"\uD83D\uDCE8 Accepting offers only"}</div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.4)"}}>No asking price set. Buyers can submit any amount and you negotiate from there.</div>
              </div>
            ):(
              <div style={{position:"relative",marginBottom:10}}>
                <input value={listPrice} onChange={e=>setListPrice(e.target.value)} placeholder="Asking price ($)" type="number" step="0.01" style={{...inp,width:"100%",paddingRight:90,boxSizing:"border-box"}}/>
                {listPrice&&!isNaN(parseFloat(listPrice))&&(
                  <span style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",fontSize:11,color:"rgba(255,255,255,0.3)"}}>or best offer</span>
                )}
              </div>
            )}
            <textarea value={listNotes} onChange={e=>setListNotes(e.target.value)} placeholder="Notes (condition, trades wanted, etc.)" rows={2} style={{...inp,width:"100%",marginBottom:10,resize:"none"}}/>
            {/* Required real-card photos */}
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:listPhotos.length?"#4ade80":"#FBBF24",marginBottom:6}}>
                {listPhotos.length?`📸 ${listPhotos.length} photo${listPhotos.length>1?"s":""} added${listPhotoAutofilled?" · auto-filled from your scan ✨":""}`:"📸 Photos required — add a pic of your actual card"}
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                {listPhotos.map((p,i)=>(
                  <div key={i} style={{position:"relative",width:54,height:72,borderRadius:6,overflow:"hidden",border:"1px solid var(--bz-line-2)"}}>
                    <img src={p.url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    <button onClick={()=>setListPhotos(prev=>prev.filter((_,j)=>j!==i))} style={{position:"absolute",top:0,right:0,background:"rgba(0,0,0,0.7)",border:"none",color:"#E8317A",fontSize:14,width:18,height:18,cursor:"pointer",lineHeight:1,padding:0}}>×</button>
                  </div>
                ))}
                <label style={{width:54,height:72,borderRadius:6,border:"1.5px dashed rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",cursor:listUploading?"wait":"pointer",fontSize:20,color:"rgba(255,255,255,0.4)"}}>
                  {listUploading?"…":"+"}
                  <input type="file" accept="image/*" style={{display:"none"}} disabled={listUploading} onChange={e=>{const f=e.target.files?.[0]; if(f)uploadListingPhoto(f); e.target.value="";}}/>
                </label>
              </div>
            </div>
                <input value={listPayment} onChange={e=>setListPayment(e.target.value)} placeholder="Payment info (e.g. Venmo @handle, PayPal friends, Cash App $tag)" style={{...inp,width:"100%",marginBottom:16,boxSizing:"border-box",fontSize:12}}/>
            <div style={{display:"flex",gap:10}}>
              <button onClick={createListing} style={{flex:1,background:"linear-gradient(135deg,#4ade80,#22c55e)",color:"#000",border:"none",borderRadius:12,padding:"12px 0",fontSize:14,fontWeight:900,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 8px 24px rgba(74,222,128,0.3)"}}>List Card</button>
              <button onClick={()=>setListModal(null)} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.4)",borderRadius:12,padding:"12px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Offer modal */}
      <OfferModal offerModal={offerModal} offerSent={offerSent} setOfferModal={setOfferModal} offerAmt={offerAmt} setOfferAmt={setOfferAmt} offerNote={offerNote} setOfferNote={setOfferNote} setOfferSent={setOfferSent} submitOffer={submitOffer} inp={inp}/>
      {/* Payment info popup */}
      {paymentPopup&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}} onClick={()=>setPaymentPopup(null)}>
          <div style={{background:"linear-gradient(135deg,#0a1a0a,var(--bz-s1))",border:"1px solid rgba(74,222,128,0.4)",borderRadius:20,padding:28,maxWidth:400,width:"100%"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:24,textAlign:"center",marginBottom:8}}>{"\uD83C\uDF89"}</div>
            <div style={{fontSize:18,fontWeight:900,color:"#4ade80",textAlign:"center",marginBottom:4}}>Purchase Complete!</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.5)",textAlign:"center",marginBottom:20}}>{paymentPopup.cardName} {"\u00B7"} {"$"}{(paymentPopup.price||0).toFixed(2)}</div>
            <div style={{background:"rgba(74,222,128,0.06)",border:"1px solid rgba(74,222,128,0.15)",borderRadius:12,padding:"14px 16px",marginBottom:20}}>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Payment Instructions from {paymentPopup.sellerName}</div>
              <div style={{fontSize:14,color:"var(--bz-ink)",fontWeight:600,lineHeight:1.5}}>{paymentPopup.paymentInfo}</div>
            </div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",textAlign:"center",marginBottom:16}}>Send payment and use the Messages tab to confirm with the seller.</div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>{setPaymentPopup(null);setActiveTab("messages");}}
                style={{flex:1,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:12,padding:"11px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                Go to Messages
              </button>
              <button onClick={()=>setPaymentPopup(null)}
                style={{background:"transparent",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.4)",borderRadius:12,padding:"11px 18px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Counter offer modal */}
      <CounterModal counterModal={counterModal} counterSent={counterSent} setCounterModal={setCounterModal} counterAmt={counterAmt} setCounterAmt={setCounterAmt} setCounterSent={setCounterSent} counterOffer={counterOffer} negHistory={negHistory}/>
      {/* Scan modal */}
      {scanModal&&<ScanModal scanModal={scanModal} setScanModal={setScanModal} photoScan={photoScan} setPhotoScan={setPhotoScan} scanSession={scanSession} setScanSession={setScanSession} scanQty={scanQty} setScanQty={setScanQty} user={user} db={db} owned={owned} setOwned={setOwned} cards={cards} inp={inp} confirmScan={confirmScan} WEAPON_COLORS={WEAPON_COLORS}
          scanCardPhoto={scanCardPhoto}
          scanHint={scanHint} scanHintOn={scanHintOn} toggleScanHint={toggleScanHint}
          />}
      {/* HERO HEADER */}
      <div style={{
        position:"relative", overflow:"hidden",
        background:"linear-gradient(135deg,#0d0005,#0a000d,#050015,#000d1a)",
        backgroundSize:"400% 400%", animation:"gradientShift 12s ease infinite",
        borderBottom:"1px solid rgba(232,49,122,0.15)", paddingBottom:0
      }}>
        {/* Glow orbs */}
        <div style={{position:"absolute",top:-100,left:"20%",width:400,height:400,background:"radial-gradient(circle,rgba(232,49,122,0.12),transparent 70%)",pointerEvents:"none",animation:"glowPulse 4s ease infinite"}}/>
        <div style={{position:"absolute",top:-80,right:"15%",width:300,height:300,background:"radial-gradient(circle,rgba(123,47,247,0.1),transparent 70%)",pointerEvents:"none",animation:"glowPulse 6s ease infinite 2s"}}/>
        <div style={{position:"absolute",bottom:0,left:"40%",width:500,height:200,background:"radial-gradient(ellipse,rgba(123,156,255,0.06),transparent 70%)",pointerEvents:"none"}}/>

        <div style={{maxWidth:1400,margin:"0 auto",padding:isMobile?"16px 14px 0":"24px 24px 0",position:"relative"}}>
          <div style={{display:"flex",alignItems:isMobile?"center":"flex-end",justifyContent:"space-between",flexWrap:"wrap",gap:12,rowGap:14,marginBottom:isMobile?16:24}}>
            <div style={{opacity:headerLoaded?1:0,transform:headerLoaded?"none":"translateY(20px)",transition:"all 0.6s cubic-bezier(0.22,1,0.36,1)",minWidth:0}}>
              <img src="/Bazooka_Logo_cropped.png" alt="Bazooka" style={{height:"clamp(40px,7vw,84px)",width:"auto",maxWidth:"min(420px,60vw)",objectFit:"contain",display:"block",filter:"drop-shadow(0 4px 16px rgba(232,49,122,0.3))"}}/>
              {!isMobile && <div style={{fontSize:11,fontWeight:700,color:"#E8317A",letterSpacing:4,textTransform:"uppercase",marginTop:6}}>BoBA Collector's Database</div>}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:isMobile?8:10,opacity:headerLoaded?1:0,transition:"opacity 0.8s ease 0.2s",flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
              {swancity ? (
                user ? null : (
                  <button onClick={()=>setSigningIn(true)} style={{background:"rgba(245,158,11,0.12)",color:"#F59E0B",border:"1px solid rgba(245,158,11,0.4)",borderRadius:12,padding:isMobile?"9px 14px":"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Sign in</button>
                )
              ) : user?(
                <>
                  {!isMobile && <div>
                    <div style={{fontSize:13,fontWeight:700}}>{user.displayName?.split(" ")[0]}</div>
                    <div style={{fontSize:11,color:"#4ade80"}}>{totalOwned} owned</div>
                  </div>}
                <div style={{position:"relative"}}>
                  <button onClick={()=>setQuickAddOpen(v=>!v)} title="Add cards to your collection" style={{background:"linear-gradient(135deg,rgba(232,49,122,0.2),rgba(123,47,247,0.2))",color:"#E8317A",border:"1px solid rgba(232,49,122,0.4)",borderRadius:12,padding:isMobile?"9px 12px":"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",backdropFilter:"blur(10px)",whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",gap:5}}>
                    {isMobile ? "\u2795" : "\u2795 Quick Add"} <span style={{fontSize:9,opacity:0.7}}>{quickAddOpen?"\u25B2":"\u25BC"}</span>
                  </button>
                  {quickAddOpen && (
                    <>
                      <div onClick={()=>setQuickAddOpen(false)} style={{position:"fixed",inset:0,zIndex:2147483600}}/>
                      <div style={{position:"fixed",top:isMobile?92:116,right:isMobile?12:24,zIndex:2147483601,background:"#141018",border:"1px solid rgba(232,49,122,0.3)",borderRadius:12,boxShadow:"0 12px 40px rgba(0,0,0,0.5)",padding:6,minWidth:210}}>
                        <div style={{fontSize:10,fontWeight:800,color:"#71717a",textTransform:"uppercase",letterSpacing:1,padding:"6px 10px 4px"}}>Add to collection</div>
                        <button onClick={()=>{ setQuickAddOpen(false); setScanModal(true); }} style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:"transparent",border:"none",color:"#eee",borderRadius:8,padding:"10px 10px",fontSize:13.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <span style={{fontSize:16}}>{"\uD83D\uDCF7"}</span> Scan a Card
                        </button>
                        <label style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:"transparent",border:"none",color:"#eee",borderRadius:8,padding:"10px 10px",fontSize:13.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <span style={{fontSize:16}}>{"\uD83D\uDCD2"}</span> Scan a Page
                          <input type="file" accept="image/*" style={{display:"none"}}
                            onChange={e=>{ const f=e.target.files?.[0]; if(f){ setQuickAddOpen(false); scanPagePhoto(f); } e.target.value=""; }}/>
                        </label>
                        <div style={{display:"flex",alignItems:"center",gap:6,padding:"2px 10px 8px",fontSize:11,color:"rgba(255,255,255,0.4)"}}>
                          <span>Page layout:</span>
                          {[{r:3,c:3,label:"3\u00d73"},{r:4,c:3,label:"4\u00d73"},{r:2,c:2,label:"2\u00d72"}].map(g=>(
                            <button key={g.label} onClick={()=>setPageGrid({rows:g.r,cols:g.c})} style={{background:pageGrid.rows===g.r&&pageGrid.cols===g.c?"rgba(96,165,250,0.25)":"rgba(255,255,255,0.05)",border:"1px solid "+(pageGrid.rows===g.r&&pageGrid.cols===g.c?"rgba(96,165,250,0.5)":"rgba(255,255,255,0.1)"),color:pageGrid.rows===g.r&&pageGrid.cols===g.c?"#60A5FA":"#999",borderRadius:6,padding:"3px 8px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{g.label}</button>
                          ))}
                        </div>
                        <button onClick={()=>{ setQuickAddOpen(false); startVoiceAdd(); }} style={{display:"flex",alignItems:"center",gap:10,width:"100%",background:"transparent",border:"none",color:"#eee",borderRadius:8,padding:"10px 10px",fontSize:13.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <span style={{fontSize:16}}>{"\uD83C\uDFA4"}</span> Say a Card
                        </button>
                      </div>
                    </>
                  )}
                </div>
                  <button onClick={async ()=>{ if(_cardAdmin){ try{ setToast("Regenerating fast snapshot…"); const snap2=await getDocs(collection(db,"boba_checklist")); const all=snap2.docs.map(d=>({...d.data(), id:d.id})); await writeCardSnapshot(all,300); const blob=new Blob([JSON.stringify(all)],{type:"application/json"}); await uploadBytes(ref(storage,"card_data/boba_checklist.json"),blob,{contentType:"application/json",cacheControl:"public,max-age=300"}); try{await setDoc(doc(db,"meta","cards_version"),{ts:Date.now(),count:all.length});}catch(e){} }catch(e){} } try{localStorage.removeItem("boba_checklist_cache_v3");}catch(e){} await idbClearCards(); window.location.reload(); }} title={_cardAdmin?"Regenerate fast snapshot & refresh":"Refresh"}
                    style={{background:"rgba(255,255,255,0.05)",color:"rgba(255,255,255,0.6)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:12,padding:isMobile?"9px 13px":"8px 14px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",backdropFilter:"blur(10px)",whiteSpace:"nowrap"}}>
                    {"\uD83D\uDD04"}</button>
                  {_cardAdmin && (
                    <div style={{position:"relative"}}>
                      <button onClick={()=>setAdminMenuOpen(v=>!v)} title="Admin tools" style={{background:adminMenuOpen?"rgba(232,49,122,0.2)":"rgba(232,49,122,0.1)",color:"#E8317A",border:"1px solid rgba(232,49,122,0.4)",borderRadius:12,padding:isMobile?"9px 13px":"8px 15px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6}}>
                        {isMobile ? "⚙️" : "⚙️ Admin"} <span style={{fontSize:9,opacity:0.7}}>{adminMenuOpen?"▲":"▼"}</span>
                      </button>
                      {adminMenuOpen && (
                        <>
                          <div onClick={()=>setAdminMenuOpen(false)} style={{position:"fixed",inset:0,zIndex:2147483600}}/>
                          <div style={{position:"fixed",top:isMobile?96:120,right:isMobile?12:24,zIndex:2147483601,background:"#15101a",border:"1px solid rgba(232,49,122,0.3)",borderRadius:14,padding:8,minWidth:220,boxShadow:"0 16px 50px rgba(0,0,0,0.7)"}}>
                            <div style={{fontSize:10,fontWeight:800,color:"#71717a",textTransform:"uppercase",letterSpacing:1,padding:"6px 10px 8px"}}>Admin Tools</div>
                            <label style={{display:"flex",alignItems:"center",gap:9,width:"100%",background:"transparent",borderRadius:9,padding:"10px 11px",fontSize:13,fontWeight:700,color:"#E8317A",cursor:"pointer",fontFamily:"inherit"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                              🖼 Import Image Folder
                              <input type="file" accept=".webp,.jpg,.jpeg,.png" multiple webkitdirectory="" directory="" style={{display:"none"}} onChange={e=>{ const f=Array.from(e.target.files||[]).filter(x=>/\.(webp|jpe?g|png)$/i.test(x.name)); if(f.length){ const hasSub=f.some(x=>(x.webkitRelativePath||"").split("/").filter(Boolean).length>2); setBulkImg({files:f, setName:(filterSet.size===1 ? [...filterSet][0] : ""), byFolder:hasSub}); setAdminMenuOpen(false); } e.target.value=""; }}/>
                            </label>
                            <label style={{display:"flex",alignItems:"center",gap:9,width:"100%",background:"transparent",borderRadius:9,padding:"10px 11px",fontSize:13,fontWeight:700,color:"#b794f6",cursor:"pointer",fontFamily:"inherit"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                              📄 Import Image Files
                              <input type="file" accept=".webp,.jpg,.jpeg,.png" multiple style={{display:"none"}} onChange={e=>{ const f=e.target.files; if(f&&f.length){ setBulkImg({files:Array.from(f), setName:(filterSet.size===1 ? [...filterSet][0] : "")}); setAdminMenuOpen(false); } e.target.value=""; }}/>
                            </label>
                            <button onClick={async()=>{ setAdminMenuOpen(false); if(!window.confirm("Permanently fix weapon casing in the database? (ALT→Alt, FIRE→Fire, etc.) This rewrites affected card records.")) return; try{ setToast("Scanning for weapon casing dupes…"); const snap=await getDocs(collection(db,"boba_checklist")); let fixed=0; let batch=writeBatch(db); let inBatch=0; for(const d of snap.docs){ const w=d.data().weapon; if(!w) continue; const cw=canonWeapon(w); if(cw!==w){ batch.update(doc(db,"boba_checklist",d.id),{weapon:cw}); fixed++; inBatch++; if(inBatch>=400){ await batch.commit(); batch=writeBatch(db); inBatch=0; } } } if(inBatch>0) await batch.commit(); setToast(`✅ Fixed ${fixed} cards. Click 🔄 Refresh to reload.`); }catch(e){ alert("Failed: "+e.message); } }} style={{display:"flex",alignItems:"center",gap:9,width:"100%",background:"transparent",border:"none",borderRadius:9,padding:"10px 11px",fontSize:13,fontWeight:700,color:"#a1a1aa",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>🔧 Fix Weapon Casing</button>
                            <button onClick={()=>{ setEarlyAccessModal(true); setAdminMenuOpen(false); }} style={{display:"flex",alignItems:"center",gap:9,width:"100%",background:"transparent",border:"none",borderRadius:9,padding:"10px 11px",fontSize:13,fontWeight:700,color:"#4ade80",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>🔑 Early Access List</button>
                            <button onClick={()=>{ setResetConfirmText(""); setResetModal(true); setAdminMenuOpen(false); }} style={{display:"flex",alignItems:"center",gap:9,width:"100%",background:"transparent",border:"none",borderRadius:9,padding:"10px 11px",fontSize:13,fontWeight:700,color:"#a1a1aa",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>🧹 Reset Marketplace</button>
                            {(user?.email?.toLowerCase().includes("devin")||user?.email?.toLowerCase().includes("derrik")) && cards.length>0 && (
                              <button onClick={()=>{ try{ const blob=new Blob([JSON.stringify(cards)],{type:"application/json"}); const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download="cards-data.json"; a.click(); URL.revokeObjectURL(url); }catch(e){alert("Export failed: "+e.message);} setAdminMenuOpen(false); }} style={{display:"flex",alignItems:"center",gap:9,width:"100%",background:"transparent",border:"none",borderRadius:9,padding:"10px 11px",fontSize:13,fontWeight:700,color:"#71717a",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>⬇ Export cards-data.json</button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {!isMobile && <button onClick={()=>signOut(auth)} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.3)",borderRadius:10,padding:"7px 14px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Sign out</button>}
                </>
              ):(
                <button onClick={()=>setSigningIn(true)} style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:14,padding:isMobile?"9px 18px":"10px 24px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 8px 32px rgba(232,49,122,0.4)",transition:"all 0.2s",whiteSpace:"nowrap"}}
                  onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 16px 48px rgba(232,49,122,0.5)";}}
                  onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="0 8px 32px rgba(232,49,122,0.4)";}}>
                  Sign in
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky website-style nav */}
      <div style={{position:"sticky",top:0,zIndex:500,background:"rgba(8,0,10,0.85)",backdropFilter:"blur(16px)",WebkitBackdropFilter:"blur(16px)",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
        <div style={{maxWidth:1400,margin:"0 auto",padding:isMobile?"0 12px":"0 24px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:isMobile?8:16}}>
          {/* Always-visible logo (brand stays on screen while scrolling) */}
          <a href="/" style={{display:"flex",alignItems:"center",flexShrink:0,textDecoration:"none",marginRight:isMobile?4:8}}>
            <img src="/Bazooka_Logo_cropped.png" alt="Bazooka" style={{height:isMobile?26:34,width:"auto",objectFit:"contain",display:"block",filter:"drop-shadow(0 2px 8px rgba(232,49,122,0.4))"}}/>
          </a>
          <span title="This app is in beta — found a bug? Tap the 🐛 button!" style={{flexShrink:0,fontSize:isMobile?9:10,fontWeight:900,letterSpacing:1,color:"#FBBF24",background:"rgba(251,191,36,0.12)",border:"1px solid rgba(251,191,36,0.4)",borderRadius:6,padding:isMobile?"2px 5px":"3px 8px",marginRight:isMobile?4:8,textTransform:"uppercase"}}>Beta</span>
            {/* Persistent notification bell — always visible (outside the horizontally-scrolling
                 nav), so mobile users see they have alerts even when the Marketplace tab is scrolled
                 off-screen. Tapping jumps to where the alerts live. */}
            {totalNotifs > 0 && (
              <button onClick={()=>{ setMktInitView(wantNotifs.some(n=>String(n.type||"").startsWith("trade_"))?"trades":"sale"); setActiveTab("market"); }}
                title={`${totalNotifs} notification${totalNotifs===1?"":"s"}`}
                style={{flexShrink:0,position:"relative",background:"transparent",border:"none",cursor:"pointer",fontSize:isMobile?20:22,padding:"4px 6px",marginRight:isMobile?4:10,lineHeight:1}}>
                {/* Halo sits BEHIND the glyph and expands outward on each beat \u2014 it reads as a soft
                    ping rather than a hard flash, which is what makes it noticeable in a busy header
                    without being irritating. */}
                <span aria-hidden="true" className="bz-bell-halo" style={{position:"absolute",inset:-4,borderRadius:"50%",background:"radial-gradient(circle, rgba(232,49,122,0.55) 0%, rgba(232,49,122,0) 70%)",animation:"bellHalo 2.6s ease-out infinite",pointerEvents:"none"}}/>
                <span className="bz-bell-live" style={{display:"inline-block",position:"relative"}}>{"\uD83D\uDD14"}</span>
                {/* key on the count so React remounts this node when the number changes, which
                    replays the pop \u2014 a new message feels different from a count just sitting there. */}
                <span key={totalNotifs} style={{animation:"numPop 0.35s ease-out",position:"absolute",top:-2,right:-2,background:"#E8317A",color:"#fff",borderRadius:10,minWidth:16,height:16,fontSize:10,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 4px",boxSizing:"border-box"}}>{totalNotifs>99?"99+":totalNotifs}</span>
              </button>
            )}

            {/* Mobile hamburger — opens the full nav menu. Replaces the cramped scrolling tab row. */}
            {isMobile && (
              <button onClick={()=>setMobileNavOpen(true)} title="Menu" style={{flexShrink:0,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:9,width:40,height:40,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4,cursor:"pointer",marginLeft:"auto"}}>
                <span style={{width:18,height:2,background:"#fff",borderRadius:2}}/>
                <span style={{width:18,height:2,background:"#fff",borderRadius:2}}/>
                <span style={{width:18,height:2,background:"#fff",borderRadius:2}}/>
              </button>
            )}

          {/* Left: primary nav */}
          <div className="nav-bar" style={{display:isMobile?"none":"flex",gap:isMobile?16:28,alignItems:"center",flex:1,minWidth:0,overflowX:isMobile?"auto":"visible",overflowY:"visible",WebkitOverflowScrolling:"touch"}}>
            {swancity ? (<>
              {navItem("supers","⭐ Supers",0)}
              {navItem("1of1","💎 Secret 1/1s",0)}
              {navItem("bojax34","🅱️ /34 BoJax",0)}
            </>) : (<>
            {navItem("cards","Card Database",0)}
            {navGroup("collect","Collectibility",[
              {id:"rainbow",label:"🌈 Rainbow Progress",badge:0},
              {id:"supers",label:"⭐ Supers",badge:0},
              {id:"1of1",label:"💎 Secret 1/1s",badge:0},
              {id:"bojax34",label:"🅱️ /34 BoJax",badge:0},
              {id:"wants",label:"🎯 Want List",badge:Object.keys(wantList).length},
              {id:"tradebait",label:"🔁 Trade Bait",badge:Object.keys(owned).filter(id=>owned[id]>1||tradeBait[id]).length},
              {id:"intransit",label:"🚚 On the Way",badge:Object.keys(inTransit).length},
            ])}
            {navGroup("play","Deck Builder",[
              {id:"deck",label:"⚔️ Hero Deck",badge:0},
              {id:"playbook",label:"📖 Playbook",badge:0},
              {id:"arena",label:"🏟️ Arena",badge:0},
              ...(user?[{id:"team",label:"🏆 Team",badge:0}]:[]),
            ])}
            {/* The badge used to count ONLY unread notifications, so once you dismissed the
                "they accepted" toast there was nothing left telling you a trade needed you.
                A notification is transient; owing someone a package is a STATE. Count that. */}
            {navItem("market","Marketplace", wantNotifs.length + tradeOffers.filter(t =>
              (t.status==="pending"  && t.toUid===user?.uid) ||                       // waiting on you to decide
              (t.status==="accepted" && !(t.fromUid===user?.uid ? t.fromReceived : t.toReceived)) // still owe / awaiting
            ).length)}
            {navItem("leaderboard","Leaderboard",0)}
            </>)}
          </div>

          {/* Right: messages + profile */}
          <div style={{display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
            {user&&!swancity&&(
              <button onClick={()=>setMsgPanelOpen(true)} title="Messages" style={{position:"relative",background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,width:38,height:38,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>
                💬
                {unreadThreads>0&&<span style={{position:"absolute",top:-5,right:-5,background:"#E8317A",color:"#fff",borderRadius:10,minWidth:18,height:18,padding:"0 5px",fontSize:10,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center"}}>{unreadThreads}</span>}
              </button>
            )}
            {user&&(
              <div style={{position:"relative"}} {...(isMobile ? {} : { onMouseEnter:()=>setProfileMenuOpen(true), onMouseLeave:()=>setProfileMenuOpen(false) })}>
                <button onClick={()=>setProfileMenuOpen(o=>!o)} style={{background:"none",border:"none",cursor:"pointer",padding:0,display:"flex",alignItems:"center"}}>
                  {(myPhotoURL||user.photoURL)
                    ? <img src={myPhotoURL||user.photoURL} alt="" style={{width:38,height:38,borderRadius:"50%",objectFit:"cover",border:"2px solid #E8317A"}}/>
                    : <div style={{width:38,height:38,borderRadius:"50%",background:"linear-gradient(135deg,#E8317A,#7B2FF7)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:900,color:"#fff",border:"2px solid #E8317A"}}>{(myUsername||user.displayName||user.email||"?").charAt(myUsername?1:0).toUpperCase()}</div>}
                </button>
                {profileMenuOpen&&(
                  <div style={{position:"absolute",top:"100%",right:0,minWidth:200,paddingTop:10,zIndex:600}}>
                  <div style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:12,boxShadow:"0 12px 40px rgba(0,0,0,0.7)",padding:6}}>
                    <div style={{padding:"8px 12px 10px",borderBottom:"1px solid var(--bz-line)",marginBottom:4}}>
                      <div style={{fontSize:13,fontWeight:800,color:"#fff",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{myUsername?`@${myUsername}`:(user.displayName||"Collector")}</div>
                      <div style={{fontSize:10,color:"#999",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{user.email}</div>
                    </div>
                    {[
                      {label:"✏️ Edit Profile",act:()=>setEditProfileOpen(true)},
                      ...(userMissing.length>0 ? [{label:"⏳ Pending Cards",badge:userMissing.length,act:()=>setUserMissingModal(true)}] : []),
                      ...((user?.email||"").toLowerCase().endsWith("@bazookabreaks.com") ? [{label:"🗂️ Missing Cards",act:()=>{ setMissingCardsModal(true); loadMissingCards(); }}] : []),
                      {label:"🖼️ My Collection",act:()=>{ window.open(`/showcase?uid=${user.uid}`,"_blank"); }},
                  {label:"\u2b07\ufe0f Import Cards",act:()=>{ setImportModal(true); setImportRows(null); setImportRaw(null); setImportSetMap({}); setImportColMap(null); setColMapConfirmed(false); }},
                  // Name the scope so it is obvious the filters apply \u2014 otherwise you cannot tell
                  // whether you are about to get 40 rows or 4,000.
                  {label: (filterTreat.size>0||filterSet.size>0||filterWeapon.size>0||filterPower.size>0)
                    ? "\u2b06\ufe0f Export filtered cards" : "\u2b06\ufe0f Export Collection", act:exportCollection},
                  // Opens the manager rather than a print view: seeing the list and acting on it
                  // are the same job, and printing is available from inside it.
                  ...((lots||[]).some(l=>l.lendState)
                    ? [{label:"\uD83D\uDD01 Cards on loan", act:()=>setLoanMgr(
                        (lots||[]).some(l=>l.lendState==="borrowed") ? "borrowed" : "lent")}] : []),
                  // Only when something is actually flagged — an empty print list is not worth a menu slot.
                  ...(Object.keys(proxyCards).length
                    ? [{label:"\uD83C\uDFF7\ufe0f Proxy print list (IMC)", act:printProxyReport}] : []),
                  {label:"\uD83D\uDCBE Full Backup (JSON)",act:exportFullBackup},
                  {label:"\u267B\uFE0F Restore from Backup",act:()=>setRestoreModal({pick:true})},
                  {label:"\uD83D\uDD17 Share Collection",act:()=>{ const url=`${window.location.origin}/showcase?uid=${user.uid}`; if(navigator.share){navigator.share({title:"My Bazooka Collection",url}).catch(()=>{});} else { navigator.clipboard.writeText(url).then(()=>showToast("Collection link copied!")).catch(()=>{}); } }},
                      {label:"👥 Friends",badge:(friendReqs.length+teamInvites.length),act:()=>setActiveTab("friends")},
                      {label:"📊 Vault Report",act:()=>setActiveTab("ledger")},
                      {label:"🧭 App Tour",act:()=>{ setActiveTab("cards"); setTimeout(()=>setTourStep(0),100); }},
                    ].map((it,idx)=>(
                      <button key={idx} onClick={()=>{it.act();setProfileMenuOpen(false);}} style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",gap:10,background:"transparent",border:"none",color:"#ddd",borderRadius:8,padding:"9px 12px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}
                        onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <span>{it.label}</span>
                        {it.badge>0&&<span style={{background:"#E8317A",color:"#fff",borderRadius:10,minWidth:18,height:18,padding:"0 5px",fontSize:10,fontWeight:900,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{it.badge}</span>}
                      </button>
                    ))}
                    <div style={{borderTop:"1px solid var(--bz-line)",marginTop:4,paddingTop:4}}>
                      <button onClick={()=>{signOut(auth);setProfileMenuOpen(false);}} style={{width:"100%",background:"transparent",border:"none",color:"var(--bz-ink-2)",borderRadius:8,padding:"9px 12px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}
                        onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.05)"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>Sign out</button>
                    </div>
                  </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages slide-in panel */}

      {/* ── VOICE-TO-ADD ── say a card, confirm the match, add it. */}
      {voiceState && (
        <div style={{position:"fixed",inset:0,zIndex:14300,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{width:"100%",maxWidth:460,background:"#101016",border:"1px solid rgba(168,85,247,0.35)",borderRadius:16,padding:"22px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:18,fontWeight:900,color:"#fff"}}>{"\uD83C\uDFA4"} Say a Card</div>
              <button onClick={()=>{ try{voiceRecRef.current&&voiceRecRef.current.abort();}catch(_){} setVoiceState(null); }} style={{background:"transparent",border:"none",color:"#888",fontSize:24,cursor:"pointer",lineHeight:1}}>{"\u00d7"}</button>
            </div>

            {/* Narrow the pool before speaking. Set it once, then rattle off hero names without
                repeating the treatment each time. */}
            {(()=>{
              const sets = [...new Set(cards.map(c=>c.setName).filter(Boolean))].sort();
              const tPool = voiceFilter.set ? cards.filter(c=>c.setName===voiceFilter.set) : cards;
              const treatments = [...new Set(tPool.map(c=>c.treatment).filter(Boolean))].sort();
              const weapons = [...new Set(cards.map(c=>canonWeapon(c.weapon)).filter(Boolean))].sort();
              const poolSize = cards.filter(c =>
                (!voiceFilter.set||c.setName===voiceFilter.set) &&
                (!voiceFilter.treatment||c.treatment===voiceFilter.treatment) &&
                (!voiceFilter.weapon||canonWeapon(c.weapon)===voiceFilter.weapon)).length;
              const any = voiceFilter.set||voiceFilter.treatment||voiceFilter.weapon;
              const sel = {background:"#0b0b0f",border:"1px solid rgba(255,255,255,0.15)",color:"#fff",borderRadius:8,padding:"6px 8px",fontSize:11.5,fontFamily:"inherit",cursor:"pointer",flex:1,minWidth:0};
              return (
                <div style={{marginBottom:14,background:any?"rgba(168,85,247,0.08)":"rgba(255,255,255,0.03)",border:"1px solid "+(any?"rgba(168,85,247,0.35)":"rgba(255,255,255,0.1)"),borderRadius:10,padding:"10px 11px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:7}}>
                    <span style={{fontSize:11,fontWeight:800,color:any?"#C084FC":"rgba(255,255,255,0.5)"}}>Narrow it down (optional)</span>
                    {any && <button onClick={()=>setVoiceFilter({set:"",treatment:"",weapon:""})} style={{background:"none",border:"none",color:"rgba(255,255,255,0.4)",fontSize:10.5,cursor:"pointer",fontFamily:"inherit",textDecoration:"underline"}}>clear</button>}
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    <select value={voiceFilter.set} onChange={e=>setVoiceFilter(f=>({...f,set:e.target.value,treatment:""}))} style={sel}>
                      <option value="">Any set</option>
                      {sets.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={voiceFilter.weapon} onChange={e=>setVoiceFilter(f=>({...f,weapon:e.target.value}))} style={sel}>
                      <option value="">Any weapon</option>
                      {weapons.map(w=><option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                  <select value={voiceFilter.treatment} onChange={e=>setVoiceFilter(f=>({...f,treatment:e.target.value}))} style={{...sel,width:"100%",marginTop:6}}>
                    <option value="">Any treatment</option>
                    {treatments.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",marginTop:6}}>
                    {any ? `Listening against ${poolSize.toLocaleString()} cards \u2014 just say the hero name.` : "Searching all cards. Pick a set or treatment to make matching much more accurate."}
                  </div>
                </div>
              );
            })()}

            {voiceState.status==="listening" && (
              <div style={{padding:"30px 0",textAlign:"center"}}>
                <div style={{width:76,height:76,borderRadius:"50%",background:"radial-gradient(circle,rgba(168,85,247,0.35),rgba(168,85,247,0.05))",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",animation:"bzPulse 1.2s ease-in-out infinite"}}>
                  <span style={{fontSize:34}}>{"\uD83C\uDFA4"}</span>
                </div>
                <style>{`@keyframes bzPulse{0%,100%{box-shadow:0 0 0 0 rgba(168,85,247,0.4);}50%{box-shadow:0 0 0 16px rgba(168,85,247,0);}}`}</style>
                <div style={{fontSize:15,fontWeight:800,color:"#fff",marginBottom:6}}>Listening…</div>
                <div style={{fontSize:12.5,color:"rgba(255,255,255,0.5)"}}>Say the set, hero, weapon and treatment — e.g. "Alpha Update BoJax Green Battlefoil"</div>
              </div>
            )}

            {voiceState.status==="result" && (() => {
              const picked = cards.find(c => c.id === voiceState.picked) || voiceState.matches[0];
              return (
                <div>
                  <div style={{fontSize:11.5,color:"rgba(255,255,255,0.4)",marginBottom:10}}>Heard: "{voiceState.transcript}"</div>
                  <div style={{display:"flex",gap:12,alignItems:"center",background:"rgba(168,85,247,0.08)",border:"1px solid rgba(168,85,247,0.3)",borderRadius:12,padding:12,marginBottom:12}}>
                    <div style={{width:54,height:72,borderRadius:7,overflow:"hidden",background:"#0a0a0f",flexShrink:0}}>
                      {picked.imageUrl ? <img src={picked.imageUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:"#555",textAlign:"center",padding:3}}>{picked.hero}</div>}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:15,fontWeight:800,color:"#fff"}}>{picked.hero}</div>
                      <div style={{fontSize:12,color:"rgba(255,255,255,0.5)"}}>{[picked.treatment,picked.weapon,picked.cardNum?`#${picked.cardNum}`:""].filter(Boolean).join(" \u00b7 ")}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.35)"}}>{picked.setName}</div>
                    </div>
                  </div>
                  {voiceState.matches.length > 1 && (
                    <div style={{marginBottom:12}}>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:6}}>Not right? Pick another:</div>
                      <div style={{display:"flex",flexDirection:"column",gap:5}}>
                        {voiceState.matches.map(c => (
                          <button key={c.id} onClick={()=>setVoiceState(s=>({...s,picked:c.id}))}
                            style={{display:"flex",alignItems:"center",gap:8,textAlign:"left",background:c.id===voiceState.picked?"rgba(168,85,247,0.15)":"rgba(255,255,255,0.03)",border:"1px solid "+(c.id===voiceState.picked?"rgba(168,85,247,0.4)":"rgba(255,255,255,0.08)"),borderRadius:8,padding:"7px 10px",cursor:"pointer",fontFamily:"inherit"}}>
                            <span style={{fontSize:12.5,fontWeight:700,color:"#fff"}}>{c.hero}</span>
                            <span style={{fontSize:10.5,color:"rgba(255,255,255,0.45)"}}>{[c.treatment,c.weapon,c.cardNum?`#${c.cardNum}`:""].filter(Boolean).join(" \u00b7 ")}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{display:"flex",gap:9}}>
                    <button onClick={addVoiceCard} style={{flex:1,background:"linear-gradient(135deg,#4ade80,#22c55e)",color:"#06240f",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{"\u2705"} Add this card</button>
                    <button onClick={startVoiceAdd} title="Try again" style={{background:"rgba(168,85,247,0.15)",border:"1px solid rgba(168,85,247,0.35)",color:"#C084FC",borderRadius:10,padding:"12px 14px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{"\uD83C\uDFA4"}</button>
                  </div>
                </div>
              );
            })()}

            {voiceState.status==="nomatch" && (
              <div style={{padding:"20px 6px",textAlign:"center"}}>
                <div style={{fontSize:28,marginBottom:10}}>{"\uD83E\uDD14"}</div>
                <div style={{fontSize:14,fontWeight:800,color:"#fff",marginBottom:6}}>No match found</div>
                {voiceState.transcript ? <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginBottom:16}}>Heard: "{voiceState.transcript}"</div> : <div style={{fontSize:12,color:"rgba(255,255,255,0.45)",marginBottom:16}}>Didn't catch that.</div>}
                <button onClick={startVoiceAdd} style={{background:"linear-gradient(135deg,#A855F7,#E8317A)",color:"#fff",border:"none",borderRadius:10,padding:"11px 22px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>{"\uD83C\uDFA4"} Try again</button>
              </div>
            )}

            {voiceState.status==="error" && (
              <div style={{padding:"20px 6px",textAlign:"center"}}>
                <div style={{fontSize:28,marginBottom:10}}>{"\u26a0\ufe0f"}</div>
                <div style={{fontSize:12.5,color:"rgba(255,255,255,0.6)",marginBottom:16,lineHeight:1.5}}>{voiceState.message}</div>
                <button onClick={()=>setVoiceState(null)} style={{background:"rgba(255,255,255,0.08)",border:"1px solid #333",color:"#fff",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}


      {/* ── MULTI-CARD PAGE SCAN: review + deselect grid ── */}
      {pageScan && (
        <div style={{position:"fixed",inset:0,zIndex:14200,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(6px)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{width:"100%",maxWidth:760,maxHeight:"92vh",display:"flex",flexDirection:"column",background:"#101016",border:"1px solid rgba(96,165,250,0.3)",borderRadius:16,padding:"18px 20px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
              <div style={{fontSize:18,fontWeight:900,color:"#fff"}}>{"\uD83D\uDCD2"} Scan a Page</div>
              <button onClick={()=>{ if(!pageBusy){ setPageScan(null); setPageSel(new Set()); } }} style={{background:"transparent",border:"none",color:"#888",fontSize:24,cursor:"pointer",lineHeight:1}}>{"\u00d7"}</button>
            </div>

            {pageScan.status==="scanning" && (
              <div style={{padding:"48px 0",textAlign:"center"}}>
                <div style={{fontSize:34,marginBottom:12}}>{"\uD83D\uDD0D"}</div>
                <div style={{fontSize:15,fontWeight:800,color:"#fff",marginBottom:6}}>Reading the cards…</div>
                <div style={{fontSize:12.5,color:"rgba(255,255,255,0.5)"}}>Reading each card in your {pageGrid.rows}×{pageGrid.cols} page. For best results, fill the frame with the page, straight-on, no glare.</div>
              </div>
            )}

            {pageScan.status==="error" && (
              <div style={{padding:"40px 10px",textAlign:"center"}}>
                <div style={{fontSize:30,marginBottom:12}}>{"\u26a0\ufe0f"}</div>
                <div style={{fontSize:14,fontWeight:800,color:"#fff",marginBottom:8}}>Couldn't scan that page</div>
                <div style={{fontSize:12.5,color:"rgba(255,255,255,0.55)",marginBottom:18,lineHeight:1.5}}>{pageScan.message}</div>
                <button onClick={()=>{ setPageScan(null); setPageSel(new Set()); }} style={{background:"rgba(255,255,255,0.08)",border:"1px solid #333",color:"#fff",borderRadius:10,padding:"10px 20px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Close</button>
              </div>
            )}

            {pageScan.status==="review" && (() => {
              const rows = pageScan.rows||[];
              const matched = rows.filter(r=>r.picked);
              const unmatched = rows.filter(r=>!r.picked);
              const selCount = rows.filter(r=>pageSel.has(r.key)&&r.picked).length;
              return (
                <>
                  <div style={{fontSize:12.5,color:"rgba(255,255,255,0.55)",marginBottom:12}}>
                    Found <strong style={{color:"#fff"}}>{rows.length}</strong> card{rows.length===1?"":"s"} — <strong style={{color:"#4ade80"}}>{matched.length}</strong> matched. Everything matched is checked; uncheck any that are wrong, then add.
                  </div>
                  <div style={{flex:1,overflowY:"auto",display:"grid",gridTemplateColumns:`repeat(${pageGrid.cols}, 1fr)`,gap:10,marginBottom:14}}>
                    {rows.map(r => {
                      const on = pageSel.has(r.key) && !!r.picked;
                      const card = r.match;
                      const d = r.detected;
                      return (
                        <div key={r.key} onClick={()=>{ if(!r.picked) return; setPageSel(s=>{ const n=new Set(s); n.has(r.key)?n.delete(r.key):n.add(r.key); return n; }); }}
                          style={{border:on?"2px solid #4ade80":r.picked?"2px solid rgba(255,255,255,0.12)":"2px dashed rgba(251,191,36,0.4)",borderRadius:10,padding:8,background:on?"rgba(74,222,128,0.06)":"rgba(255,255,255,0.02)",cursor:r.picked?"pointer":"default",position:"relative",opacity:r.picked?1:0.75}}>
                          {r.picked && <div style={{position:"absolute",top:6,right:6,width:20,height:20,borderRadius:"50%",background:on?"#4ade80":"rgba(255,255,255,0.15)",color:on?"#06240f":"#666",fontSize:13,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center"}}>{on?"\u2713":""}</div>}
                          <div style={{aspectRatio:"3/4",borderRadius:6,overflow:"hidden",background:"#0a0a0f",marginBottom:6}}>
                            {card?.imageUrl ? <img src={card.imageUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#555",textAlign:"center",padding:6}}>{card?.hero||d?.hero||"?"}</div>}
                          </div>
                          {r.picked ? (
                            <>
                              <div style={{fontSize:12,fontWeight:800,color:"#fff",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{card.hero}</div>
                              <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{[card.treatment,card.weapon,card.cardNum?`#${card.cardNum}`:""].filter(Boolean).join(" \u00b7 ")}</div>
                              <div style={{fontSize:9,color:"rgba(255,255,255,0.28)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",marginTop:1}} title="What the scanner read">read: {d?.hero||"?"}{d?.treatment?` \u00b7 ${d.treatment}`:""}{d?.cardNum?` #${d.cardNum}`:""}</div>
                            </>
                          ) : (
                            <>
                              <div style={{fontSize:11.5,fontWeight:800,color:"#FBBF24"}}>{r.candidates.length>0?"Pick variant":"Not matched"}</div>
                              <div style={{fontSize:10,color:"rgba(255,255,255,0.4)"}}>Read: {d?.hero||"?"}{d?.cardNum?` #${d.cardNum}`:""}</div>
                              {r.candidates.length>0 && (
                                <select value="" onChange={e=>{ const cid=e.target.value; if(!cid) return; setPageScan(ps=>({...ps, rows: ps.rows.map(x=>x.key===r.key?{...x,picked:cid,match:cards.find(c=>c.id===cid)||x.match}:x)})); setPageSel(s=>new Set(s).add(r.key)); }}
                                  onClick={e=>e.stopPropagation()} style={{marginTop:5,width:"100%",fontSize:10,background:"#0e0e13",color:"#ccc",border:"1px solid #333",borderRadius:6,padding:"4px"}}>
                                  <option value="">Pick the right card…</option>
                                  {r.candidates.map(c=><option key={c.id} value={c.id}>{c.hero} {c.treatment?`\u00b7 ${c.treatment}`:""} {c.cardNum?`#${c.cardNum}`:""}</option>)}
                                </select>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{display:"flex",gap:9,alignItems:"center"}}>
                    <span style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginRight:"auto"}}>{selCount} selected{unmatched.length?` \u00b7 ${unmatched.length} unmatched`:""}</span>
                    <button onClick={()=>{ setPageScan(null); setPageSel(new Set()); }} disabled={pageBusy} style={{background:"transparent",border:"1px solid #333",color:"#888",borderRadius:10,padding:"11px 16px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                    <button onClick={addScannedPage} disabled={pageBusy||selCount===0} style={{background:selCount>0?"linear-gradient(135deg,#4ade80,#22c55e)":"#222",color:selCount>0?"#06240f":"#666",border:"none",borderRadius:10,padding:"11px 20px",fontSize:13.5,fontWeight:800,cursor:pageBusy?"wait":"pointer",fontFamily:"inherit"}}>{pageBusy?"Adding\u2026":`Add ${selCount} card${selCount===1?"":"s"}`}</button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}


      {/* ── PROMINENT NOTIFICATION POPUP ── slides in top-center; impossible to miss. */}
      {notifPop && (
        <div style={{position:"fixed",top:isMobile?12:18,left:"50%",transform:"translateX(-50%)",zIndex:2147483000,width:"calc(100% - 24px)",maxWidth:420,animation:"bzNotifIn 0.35s cubic-bezier(0.2,0.9,0.3,1.3)"}}>
          <style>{`@keyframes bzNotifIn{from{opacity:0;transform:translateX(-50%) translateY(-24px);}to{opacity:1;transform:translateX(-50%) translateY(0);}}`}</style>
          <div onClick={()=>{ setMktInitView(notifPop.view); setActiveTab("market"); setNotifPop(null); }}
            style={{display:"flex",alignItems:"center",gap:12,background:"linear-gradient(135deg,#1c0d16,#180f22)",border:"1px solid rgba(232,49,122,0.5)",borderRadius:14,padding:"12px 14px",cursor:"pointer",boxShadow:"0 10px 40px rgba(232,49,122,0.35)"}}>
            <div style={{fontSize:22,flexShrink:0}}>{"\uD83D\uDD14"}</div>
            {notifPop.image && <img src={notifPop.image} alt="" style={{width:36,height:48,objectFit:"cover",borderRadius:6,flexShrink:0}}/>}
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:14,fontWeight:800,color:"#fff",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{notifPop.title}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.55)"}}>{notifPop.sub}</div>
            </div>
            <button onClick={e=>{ e.stopPropagation(); setNotifPop(null); }} style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.4)",fontSize:20,cursor:"pointer",flexShrink:0,lineHeight:1,padding:"0 2px"}}>{"\u00d7"}</button>
          </div>
        </div>
      )}


      {/* ── MOBILE NAV DRAWER ── full menu so nothing is cut off on a phone. */}
      {isMobile && mobileNavOpen && (
        <div onClick={()=>setMobileNavOpen(false)} style={{position:"fixed",inset:0,zIndex:14500,background:"rgba(0,0,0,0.75)",backdropFilter:"blur(4px)",display:"flex",justifyContent:"flex-end"}}>
          <div onClick={e=>e.stopPropagation()} style={{width:"82%",maxWidth:340,height:"100%",background:"#0e0b12",borderLeft:"1px solid rgba(232,49,122,0.25)",overflowY:"auto",padding:"18px 16px",boxSizing:"border-box"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
              <span style={{fontSize:12,fontWeight:800,letterSpacing:2,color:"#E8317A"}}>MENU</span>
              <button onClick={()=>setMobileNavOpen(false)} style={{background:"transparent",border:"none",color:"#888",fontSize:26,cursor:"pointer",lineHeight:1}}>{"\u00d7"}</button>
            </div>
            {(() => {
              const go = (id) => { setActiveTab(id); setMobileNavOpen(false); };
              const marketBadge = wantNotifs.length + tradeOffers.filter(t => (t.status==="pending"&&t.toUid===user?.uid) || (t.status==="accepted" && !(t.fromUid===user?.uid?t.fromReceived:t.toReceived))).length;
              const items = [
                { id:"cards", label:"Card Database", badge:0 },
                { section:"Collectibility" },
                { id:"rainbow", label:"\uD83C\uDF08 Rainbow Progress", badge:0 },
                { id:"supers", label:"\u2B50 Supers", badge:0 },
                { id:"1of1", label:"\uD83D\uDC8E Secret 1/1s", badge:0 },
                { id:"bojax34", label:"\uD83C\uDD71\uFE0F /34 BoJax", badge:0 },
                { id:"wants", label:"\uD83C\uDFAF Want List", badge:Object.keys(wantList).length },
                { id:"tradebait", label:"\uD83D\uDD01 Trade Bait", badge:0 },
                { id:"intransit", label:"\uD83D\uDE9A On the Way", badge:Object.keys(inTransit).length },
                { section:"Deck Builder" },
                { id:"deck", label:"\u2694\uFE0F Hero Deck", badge:0 },
                { id:"playbook", label:"\uD83D\uDCD6 Playbook", badge:0 },
                { id:"arena", label:"\uD83C\uDFDF\uFE0F Arena", badge:0 },
                ...(user?[{ id:"team", label:"\uD83C\uDFC6 Team", badge:0 }]:[]),
                { section:"More" },
                { id:"market", label:"\uD83E\uDD1D Marketplace", badge:marketBadge },
                { id:"leaderboard", label:"\uD83E\uDD47 Leaderboard", badge:0 },
                ...(user?[{ id:"friends", label:"\uD83D\uDC65 Friends", badge:friendReqs.length+teamInvites.length }, { id:"ledger", label:"\uD83D\uDCCA Vault Report", badge:0 }]:[]),
              ];
              return items.map((it,idx) => it.section ? (
                <div key={"s"+idx} style={{fontSize:10.5,fontWeight:800,letterSpacing:1.5,color:"rgba(255,255,255,0.35)",textTransform:"uppercase",margin:"16px 0 6px",paddingLeft:4}}>{it.section}</div>
              ) : (
                <button key={it.id} onClick={()=>go(it.id)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",background:activeTab===it.id?"rgba(232,49,122,0.12)":"transparent",border:"1px solid "+(activeTab===it.id?"rgba(232,49,122,0.4)":"transparent"),borderRadius:10,padding:"13px 14px",fontSize:15,fontWeight:700,color:activeTab===it.id?"#fff":"rgba(255,255,255,0.75)",cursor:"pointer",fontFamily:"inherit",marginBottom:2}}>
                  <span>{it.label}</span>
                  {it.badge>0 && <span style={{background:"#E8317A",color:"#fff",borderRadius:10,minWidth:20,height:20,fontSize:11,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 6px"}}>{it.badge}</span>}
                </button>
              ));
            })()}
          </div>
        </div>
      )}

      {msgPanelOpen&&user&&(
        <>
          <div onClick={()=>setMsgPanelOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,backdropFilter:"blur(2px)"}}/>
          <div style={{position:"fixed",top:0,right:0,bottom:0,width:"min(460px,100%)",background:"var(--bz-bg)",borderLeft:"1px solid var(--bz-line-2)",zIndex:1001,boxShadow:"-8px 0 40px rgba(0,0,0,0.6)",display:"flex",flexDirection:"column",animation:"slideInRight 0.25s ease"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px",borderBottom:"1px solid var(--bz-line)"}}>
              <div style={{fontSize:16,fontWeight:900,color:"#fff"}}>💬 Messages</div>
              <button onClick={()=>setMsgPanelOpen(false)} style={{background:"none",border:"none",color:"var(--bz-ink-2)",fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
            <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
              {pendingReviews.length>0&&(
                <div style={{ background:"linear-gradient(135deg, rgba(232,49,122,0.12), rgba(251,191,36,0.08))", border:"1px solid rgba(232,49,122,0.25)", borderRadius:14, padding:"14px 16px", marginBottom:16 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"#fff", marginBottom:8 }}>⭐ Rate your recent {pendingReviews.length===1?"purchase":"purchases"}</div>
                  {pendingReviews.slice(0,4).map(s=>(
                    <div key={s.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, background:"rgba(0,0,0,0.25)", borderRadius:10, padding:"8px 12px", marginBottom:6 }}>
                      <div style={{ minWidth:0 }}><div style={{ fontSize:13, fontWeight:700, color:"#eee", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{s.cardName}</div><div style={{ fontSize:11, color:"var(--bz-ink-2)" }}>from {s.sellerName||"seller"}</div></div>
                      <button onClick={()=>setReviewModal({sale:s})} style={{ background:"linear-gradient(135deg,#E8317A,#FBBF24)", border:"none", color:"#fff", borderRadius:20, padding:"6px 14px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>Rate</button>
                    </div>
                  ))}
                </div>
              )}
              <MessagesTab
                user={user}
                activeThread={activeThread}
                setActiveThread={setActiveThread}
                threads={threads}
                threadMsgs={threadMsgs}
                newMsg={newMsg}
                setNewMsg={setNewMsg}
                sendMessage={sendMessage}
                closeThread={closeThread}
                marketSales={marketSales}
                inp={inp}
                WEAPON_COLORS={WEAPON_COLORS}
                setSigningIn={setSigningIn}
              />
            </div>
          </div>
        </>
      )}

      {/* Market notifications bar */}
      {marketNotifs.length>0&&(
        <div style={{background:"linear-gradient(135deg,rgba(251,191,36,0.1),rgba(245,158,11,0.05))",borderBottom:"1px solid rgba(251,191,36,0.2)",padding:"10px 24px"}}>
          <div style={{maxWidth:1400,margin:"0 auto",display:"flex",gap:12,flexWrap:"wrap"}}>
            {marketNotifs.map(n=>(
              <div key={n.id} style={{display:"flex",alignItems:"center",gap:10,background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.2)",borderRadius:12,padding:"8px 14px"}}>
                <span style={{fontSize:14}}>{"\uD83E\uDD1D"}</span>
                <span style={{fontSize:12,color:"#FBBF24",fontWeight:700}}>{n.buyerName} {n.isCounter?"countered":"offered"} <strong>${(n.offerAmount||0).toFixed(2)}</strong> for {n.cardName}</span>
                {n.isCounter?(
                  <>
                    <button onClick={()=>respondOffer(n,"accepted")} style={{background:"rgba(74,222,128,0.15)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Accept</button>
                    <button onClick={()=>{setCounterModal(n);setCounterAmt("");}} style={{background:"rgba(251,191,36,0.15)",border:"1px solid rgba(251,191,36,0.3)",color:"#FBBF24",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Counter</button>
                    <button onClick={()=>respondOffer(n,"declined")} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.3)",borderRadius:7,padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Decline</button>
                  </>
                ):(
                  <>
                    <button onClick={()=>respondOffer(n,"accepted")} style={{background:"rgba(74,222,128,0.15)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Accept</button>
                    <button onClick={()=>{setCounterModal(n);setCounterAmt("");}} style={{background:"rgba(251,191,36,0.15)",border:"1px solid rgba(251,191,36,0.3)",color:"#FBBF24",borderRadius:7,padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>Counter</button>
                    <button onClick={()=>respondOffer(n,"declined")} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.3)",borderRadius:7,padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Decline</button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Incoming loan offers — someone sent me cards to accept. Accept/decline
          right here, like the want-list bar, so no trip to the Friends tab. */}
      {incomingLoans.length>0&&(
        <div style={{background:"linear-gradient(135deg,rgba(74,222,128,0.1),rgba(96,165,250,0.06))",borderBottom:"1px solid rgba(74,222,128,0.2)",padding:"10px 24px"}}>
          <div style={{maxWidth:1400,margin:"0 auto",display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
            <span style={{fontSize:12,color:"#4ade80",fontWeight:800,whiteSpace:"nowrap"}}>
              📥 {incomingLoans.length} card{incomingLoans.length===1?"":"s"} loaned to you
            </span>
            {incomingLoans.slice(0,4).map(({entry,hero,from,image})=>(
              <span key={entry.id} style={{display:"inline-flex",alignItems:"center",gap:8,background:"rgba(0,0,0,0.25)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,padding:"4px 8px"}}>
                {image ? <img src={image} alt="" style={{width:18,height:25,borderRadius:3,objectFit:"cover",flexShrink:0}}/> : null}
                <span style={{fontSize:11.5,color:"rgba(255,255,255,0.8)",whiteSpace:"nowrap"}}>{hero} <span style={{color:"rgba(255,255,255,0.45)"}}>from {from}</span></span>
                <button onClick={()=>acceptLoan(entry)} style={{fontSize:10.5,background:"rgba(74,222,128,0.18)",border:"1px solid #4ade80",color:"#4ade80",borderRadius:6,padding:"3px 9px",cursor:"pointer",fontFamily:"inherit",fontWeight:800,flexShrink:0}}>Accept</button>
                <button onClick={()=>declineLoan(entry)} style={{fontSize:10.5,background:"transparent",border:"1px solid #555",color:"#999",borderRadius:6,padding:"3px 8px",cursor:"pointer",fontFamily:"inherit",fontWeight:700,flexShrink:0}}>Decline</button>
              </span>
            ))}
            {incomingLoans.length>4 && (
              <span onClick={()=>setActiveTab("friends")} style={{fontSize:11,color:"rgba(255,255,255,0.5)",cursor:"pointer",textDecoration:"underline"}}>
                +{incomingLoans.length-4} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Loan return reminders — due today or overdue. */}
      {loansDue.length>0&&(
        <div style={{background:"linear-gradient(135deg,rgba(251,191,36,0.1),rgba(248,113,113,0.06))",borderBottom:"1px solid rgba(251,191,36,0.2)",padding:"10px 24px"}}>
          <div style={{maxWidth:1400,margin:"0 auto",display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
            <span style={{fontSize:12,color:"#FBBF24",fontWeight:800,whiteSpace:"nowrap"}}>
              ⏰ {loansDue.length} loan{loansDue.length===1?"":"s"} to settle
            </span>
            {loansDue.slice(0,4).map(({lot,hero,who,days,borrowed})=>(
              <span key={lot.id} onClick={()=>setLoanMgr(borrowed?"borrowed":"lent")}
                style={{fontSize:11.5,color:"rgba(255,255,255,0.75)",cursor:"pointer",background:"rgba(0,0,0,0.25)",
                        border:"1px solid rgba(255,255,255,0.1)",borderRadius:8,padding:"4px 10px",whiteSpace:"nowrap"}}>
                {borrowed ? `Return ${hero}${who?` to ${who}`:""}` : `Get ${hero} back${who?` from ${who}`:""}`}
                <strong style={{marginLeft:6,color:days<0?"#F87171":"#FBBF24"}}>{days<0?`${-days}d late`:"today"}</strong>
              </span>
            ))}
            {loansDue.length>4 && (
              <span onClick={()=>setLoanMgr("all")} style={{fontSize:11,color:"rgba(255,255,255,0.5)",cursor:"pointer",textDecoration:"underline"}}>
                +{loansDue.length-4} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Want notifications bar */}
      {wantNotifs.length>0&&(
        <div style={{background:"linear-gradient(135deg,rgba(232,49,122,0.08),rgba(123,47,247,0.05))",borderBottom:"1px solid rgba(232,49,122,0.15)",padding:"10px 24px"}}>
          <div style={{maxWidth:1400,margin:"0 auto",display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
            {/* The header said "cards from your want list" for EVERYTHING — including trade
                offers, which have nothing to do with a want list. Count them separately. */}
            <span style={{fontSize:12,color:"rgba(255,255,255,0.4)",fontWeight:700,whiteSpace:"nowrap"}}>
              {(() => {
                const trades = wantNotifs.filter(n=>String(n.type||"").startsWith("trade_")).length;
                const wants  = wantNotifs.length - trades;
                if (trades && wants) return `\uD83D\uDD14 ${trades} trade${trades===1?"":"s"} \u00b7 ${wants} want-list hit${wants===1?"":"s"}`;
                if (trades) return `\uD83D\uDD01 ${trades} trade update${trades===1?"":"s"}`;
                return `\uD83C\uDF89 ${wants} card${wants===1?"":"s"} from your want list`;
              })()}
            </span>
            {wantNotifs.map(n=>(
              <div key={n.id} style={{display:"flex",alignItems:"center",gap:8,background:"rgba(232,49,122,0.08)",border:"1px solid rgba(232,49,122,0.2)",borderRadius:12,padding:"6px 12px"}}>
                {n.cardImage&&<img src={n.cardImage} alt={n.cardName} style={{width:24,height:32,objectFit:"cover",borderRadius:4}}/>}
                <span style={{fontSize:12,color:"#E8317A",fontWeight:700}}>
                  {n.type==="trade_offer"    ? `${n.fromName||"Someone"} offered you a trade`
                   : n.type==="trade_accepted" ? `${n.fromName||"Someone"} accepted your trade`
                   : n.type==="trade_declined" ? `${n.fromName||"Someone"} declined your trade`
                   : n.type==="trade_cancelled" ? `${n.fromName||"Someone"} cancelled a trade`
                        : n.type==="trade_agreed"   ? `${n.fromName||"Someone"} agreed \u2014 your move`
                        : n.type==="trade_countered" ? `${n.fromName||"Someone"} added their side`
                   : n.cardName}
                </span>
                {/* Every notification type gets its own line. This used to branch only on
                    want_tradeable, so a trade_offer fell through to the SALE text and rendered
                    "by undefined · $0.00" — the price and seller a trade doesn't have. */}
                <span style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>
                  {n.type==="trade_offer"      ? <>Open My Trades to accept or decline</>
                   : n.type==="trade_accepted" ? <>Send your cards and add tracking</>
                   : n.type==="trade_declined" ? <>No cards changed hands</>
                   : n.type==="trade_cancelled" ? <>Your cards are released back for trading</>
                          : n.type==="trade_agreed"   ? <>They agreed to the terms — open My Trades to accept</>
                          : n.type==="trade_countered" ? <>They added their side — review it in My Trades</>
                   : n.type==="want_tradeable" ? <>{n.traderName} will trade it</>
                   : <>by {n.sellerName||"Seller"} {"\u00b7"} ${(n.askingPrice||0).toFixed(2)}</>}
                </span>
                <button onClick={()=>{
                  // Route by type. A trade offer lives in My Trades \u2014 dropping someone on
                  // For Sale and letting them hunt for it is how "I can't find it to accept"
                  // happens.
                  setMktInitView(String(n.type||"").startsWith("trade_") ? "trades" : "sale");
                  setActiveTab("market");
                  setDoc(doc(db,"market_notifs",n.id),{read:true},{merge:true});
                }} style={{background:"rgba(232,49,122,0.15)",border:"1px solid rgba(232,49,122,0.3)",color:"#E8317A",borderRadius:6,padding:"3px 8px",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>View</button>
                <button onClick={()=>setDoc(doc(db,"market_notifs",n.id),{read:true},{merge:true})} style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.2)",cursor:"pointer",fontSize:14,padding:"0 2px"}}>{"\u00D7"}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB CONTENT */}
      <div style={{maxWidth:1400,margin:"0 auto",padding:20}}>

        {/* 1/1 TAB — reuse same logic as internal checklist */}
        {activeTab==="1of1" && (() => {
          const secret1of1Cards = cards.filter(c => c.notation === "Secret 1/1");
          const sets1of1 = [...new Set(secret1of1Cards.map(c=>c.setName).filter(Boolean))].sort();
          const claimMap1 = {};
          oneOfOneClaims.forEach(cl => { claimMap1[cl.cardId] = cl; });
          // Scope community counters to the selected set (or all sets when none selected)
          const scoped1of1 = secret1SetFilter ? secret1of1Cards.filter(c => c.setName === secret1SetFilter) : secret1of1Cards;
          const scoped1of1Ids = new Set(scoped1of1.map(c => c.id));
          const totalCount    = scoped1of1.length;
          const verifiedCount1 = oneOfOneClaims.filter(c=>c.status==="verified" && scoped1of1Ids.has(c.cardId)).length;
          const pendingClaims1 = oneOfOneClaims.filter(c=>c.status==="pending");
          const isAdminUser   = user?.email?.toLowerCase().includes("devin") || user?.email?.toLowerCase().includes("derrik") || user?.email?.toLowerCase()==="swancitycards@gmail.com";
          const recent1of1 = (() => {
            const seen = new Set();
            return oneOfOneClaims
              .filter(c=>c.status==="verified")
              .sort((a,b)=>new Date(b.reviewedAt||b.createdAt||0)-new Date(a.reviewedAt||a.createdAt||0))
              .filter(cl => { const k = cl.cardId || `${cl.cardName}|${cl.cardNum}`; if (seen.has(k)) return false; seen.add(k); return true; })
              .slice(0,12);
          })();

          return (
            <div style={{ display:"flex", flexDirection:"column", gap:16, paddingBottom:40 }}>
              {/* Claim modal */}
              {oneModal && (
                <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}
                  onClick={()=>{ if(!oneSubmitting){ setOneModal(null); setOnePhoto(null); setOneStory(""); setOneDate(""); setOneName(""); setAdminClaimMode(false); setOneSent(false); setOneEditMode(false); }}}>
                  <div style={{ background:"var(--bz-s1)", border:"2px solid #9333EA", borderRadius:20, padding:28, maxWidth:460, width:"100%", maxHeight:"90vh", overflowY:"auto" }} onClick={e=>e.stopPropagation()}>
                    {oneSent ? (
                      <div style={{ textAlign:"center", padding:"20px 0" }}>
                        <div style={{ fontSize:52, marginBottom:16 }}>💎</div>
                        <div style={{ fontSize:20, fontWeight:900, color:"#9333EA", marginBottom:8 }}>Claim Submitted!</div>
                        <div style={{ fontSize:13, color:"var(--bz-ink-2)", marginBottom:24 }}>Pending admin verification. Congrats on the hit!</div>
                        <button onClick={()=>{ setOneModal(null); setOnePhoto(null); setOneStory(""); setOneDate(""); setOneName(""); setAdminClaimMode(false); setOneSent(false); setOneEditMode(false); }}
                          style={{ background:"#9333EA", color:"#fff", border:"none", borderRadius:12, padding:"12px 32px", fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>Done</button>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize:18, fontWeight:900, color:"#9333EA", marginBottom:4 }}>{oneEditMode?"✏️ Edit Claim":adminClaimMode?"🎯 Record Secret 1/1 Hit (Admin)":"💎 Claim Secret 1/1"}</div>
                        <div style={{ fontSize:13, color:"var(--bz-ink-2)", marginBottom:20 }}>{oneModal.hero} #{oneModal.cardNum} · {oneModal.treatment}</div>
                        <label style={{ display:"block", marginBottom:14 }}>
                          <div style={{ background:onePhoto?"#0a0a1a":"var(--bz-bg)", border:`2px dashed ${onePhoto?"#9333EA":"var(--bz-line-2)"}`, borderRadius:12, padding:20, textAlign:"center", cursor:"pointer" }}>
                            {onePhoto ? <img src={(onePhoto&&onePhoto.preview)?onePhoto.preview:onePhoto} alt="" style={{ maxHeight:180, maxWidth:"100%", borderRadius:8, objectFit:"contain" }}/> : <><div style={{ fontSize:32, marginBottom:8 }}>📸</div><div style={{ fontSize:13, fontWeight:700, color:"#9333EA" }}>Tap to upload photo</div></>}
                          </div>
                          <input type="file" accept="image/*" capture="environment" style={{ display:"none" }}
                            onChange={e=>{ const f=e.target.files?.[0]; if(!f)return; const r2=new FileReader(); r2.onload=ev=>setOnePhoto(ev.target.result); r2.readAsDataURL(f); e.target.value=""; }}/>
                        </label>
                        <div style={{ marginBottom:14 }}>
                          <label style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", display:"block", marginBottom:6 }}>Date You Hit It</label>
                          <input type="date" value={oneDate} onChange={e=>setOneDate(e.target.value)} onClick={e=>{try{e.target.showPicker&&e.target.showPicker();}catch(_){}}} style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:8, color:"var(--bz-ink)", padding:"12px", fontSize:16, fontFamily:"inherit", outline:"none", width:"100%", boxSizing:"border-box", cursor:"pointer" }}/>
                        </div>
                        <div style={{ marginBottom:14 }}>
                          <label style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", display:"block", marginBottom:6 }}>The Story <span style={{ color:"var(--bz-ink-3)", fontWeight:400 }}>(optional)</span></label>
                          <textarea value={oneStory} onChange={e=>setOneStory(e.target.value)} rows={4}
                            placeholder="Cards are about creating moments of joy… tell us the story about this hit ✨"
                            style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:8, color:"var(--bz-ink)", padding:"10px 12px", fontSize:12, fontFamily:"inherit", outline:"none", width:"100%", boxSizing:"border-box", resize:"vertical", lineHeight:1.6 }}/>
                        </div>
                        <div style={{ marginBottom:20 }}>
                          <label style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-2)", display:"block", marginBottom:6 }}>Your Name <span style={{ color:"var(--bz-ink-3)", fontWeight:400 }}>(leave blank to stay anonymous)</span></label>
                          <input value={oneName} onChange={e=>setOneName(e.target.value)} placeholder="Anonymous"
                            style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:8, color:"var(--bz-ink)", padding:"10px 12px", fontSize:13, fontFamily:"inherit", outline:"none", width:"100%", boxSizing:"border-box" }}/>
                        </div>
                        <div style={{ display:"flex", gap:10 }}>
                          <button onClick={async()=>{
                            if((!onePhoto && !oneEditMode)||oneSubmitting) return;
                            setOneSubmitting(true);
                            try {
                              // EDIT MODE: update existing claim, keep photo unless a new one was picked
                              if (oneEditMode) {
                                let newPu = null;
                                if (onePhoto && typeof onePhoto === "string" && onePhoto.startsWith("data:")) {
                                  const sr2=ref(storage,`oneof1_claims/${oneModal.id}_${Date.now()}.jpg`);
                                  const bs2=atob(onePhoto.split(",")[1]); const ab2=new ArrayBuffer(bs2.length); const ia2=new Uint8Array(ab2); for(let i=0;i<bs2.length;i++)ia2[i]=bs2.charCodeAt(i);
                                  await uploadBytes(sr2,new Blob([ab2],{type:"image/jpeg"}));
                                  newPu=await getDownloadURL(sr2);
                                }
                                await setDoc(doc(db,"oneof1_claims",oneModal.id),{ submitterName:oneName||"Anonymous", story:oneStory||"", dateHit:oneDate||"", ...(newPu?{photoUrl:newPu}:{}), editedByAdmin:user?.displayName||user?.email||"Admin", editedAt:new Date().toISOString() },{merge:true});
                                setOneModal(null); setOnePhoto(null); setOneStory(""); setOneDate(""); setOneName(""); setAdminClaimMode(false); setOneEditMode(false);
                                setOneSubmitting(false);
                                return;
                              }
                              const sr=ref(storage,`oneof1_claims/${oneModal.id}_${Date.now()}.jpg`);
                              const bs=atob(onePhoto.split(",")[1]); const ab=new ArrayBuffer(bs.length); const ia=new Uint8Array(ab); for(let i=0;i<bs.length;i++)ia[i]=bs.charCodeAt(i);
                              await uploadBytes(sr,new Blob([ab],{type:"image/jpeg"}));
                              const pu=await getDownloadURL(sr);
                              await setDoc(doc(db,"oneof1_claims",oneModal.id),{ cardId:oneModal.id, cardName:oneModal.hero, cardNum:oneModal.cardNum, setName:oneModal.setName||"", treatment:oneModal.treatment||"", photoUrl:pu, story:oneStory||"", dateHit:oneDate||"", submitterName:oneName||"Anonymous", userId:user?.uid||"anon", status:adminClaimMode?"verified":"pending", ...(adminClaimMode?{recordedByAdmin:user?.displayName||user?.email||"Admin", reviewedAt:new Date().toISOString()}:{}), createdAt:new Date().toISOString() });
                              setOneSent(true);
                              setSuperCelebration({ cardName: oneModal.hero, cardImage: oneModal.imageUrl || null, type: "oneof1" });
                            } catch(e){ alert("Upload failed: "+e.message); }
                            setOneSubmitting(false);
                          }} disabled={(!onePhoto && !oneEditMode)||oneSubmitting}
                            style={{ flex:1, background:(onePhoto||oneEditMode)?"#9333EA":"var(--bz-s2)", color:(onePhoto||oneEditMode)?"#fff":"#555", border:"none", borderRadius:12, padding:14, fontSize:14, fontWeight:800, cursor:(onePhoto||oneEditMode)?"pointer":"not-allowed", fontFamily:"inherit" }}>
                            {oneSubmitting?"Saving...":(oneEditMode?"💾 Save Changes":"Submit Claim")}
                          </button>
                          <button onClick={()=>{ setOneModal(null); setOnePhoto(null); setOneStory(""); setOneDate(""); setOneName(""); setAdminClaimMode(false); setOneEditMode(false); }}
                            style={{ background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:12, padding:"14px 20px", fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Header */}
              <div style={{ background:"linear-gradient(135deg,#1a0a2e,var(--bz-s1))", border:"1px solid rgba(147,51,234,0.2)", borderRadius:16, padding:"20px 24px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:"#9333EA", textTransform:"uppercase", letterSpacing:2, marginBottom:4 }}>💎 Community Secret 1/1 Hunt</div>
                    <div style={{ fontSize:13, color:"var(--bz-ink-3)" }}>{totalCount} Secret 1/1s{secret1SetFilter?` in ${secret1SetFilter}`:" across all sets"}</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:28, fontWeight:900, color:"#9333EA" }}>{verifiedCount1}<span style={{ fontSize:14, color:"var(--bz-ink-3)", fontWeight:400 }}>/{totalCount}</span></div>
                    <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>found & verified</div>
                  </div>
                </div>
                <div style={{ height:10, background:"var(--bz-s2)", borderRadius:5, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${totalCount>0?(verifiedCount1/totalCount*100):0}%`, background:"linear-gradient(90deg,#6B21A8,#9333EA,#C084FC,#E879F9,#A855F7,#7C3AED,#6B21A8)", backgroundSize:"200% 100%", animation:"gradientShift 3s ease infinite", borderRadius:5, transition:"width 0.5s ease" }}/>
                </div>
              </div>

              {/* Recent claims ticker */}
              {recent1of1.length>0 && (
                <div style={{ background:"var(--bz-bg)", border:"1px solid rgba(147,51,234,0.25)", borderRadius:12, overflow:"hidden", display:"flex", alignItems:"stretch" }}>
                  <div style={{ flexShrink:0, background:"#9333EA", color:"#fff", fontSize:10, fontWeight:800, padding:"0 12px", letterSpacing:0.5, display:"flex", alignItems:"center", writingMode:"vertical-rl", transform:"rotate(180deg)" }}>💎 LATEST HITS</div>
                  <div className="vticker-wrap" style={{ flex:1, height:96 }}>
                    <div className="vticker-track" style={{ animationDuration:`${Math.max(8,recent1of1.length*3)}s` }}>
                      {[...recent1of1,...recent1of1].map((cl,i)=>(
                        <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 14px", height:32, boxSizing:"border-box" }}>
                          {cl.photoUrl && <img src={cl.photoUrl} alt="" style={{ width:18, height:24, objectFit:"cover", borderRadius:3, flexShrink:0 }}/>}
                          <span style={{ fontSize:12, fontWeight:700, color:"#C084FC", whiteSpace:"nowrap" }}>{cl.cardName}</span>
                          <span style={{ fontSize:11, color:"#999", whiteSpace:"nowrap" }}>#{cl.cardNum}</span>
                          <span style={{ fontSize:11, color:"var(--bz-ink-2)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>by {cl.submitterName||"Anonymous"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Admin pending review */}
              {isAdminUser && pendingClaims1.length>0 && (
                <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:14, padding:"16px 20px" }}>
                  <div style={{ fontSize:12, fontWeight:700, color:"#FBBF24", marginBottom:12 }}>⏳ Pending Review ({pendingClaims1.length})</div>
                  {pendingClaims1.map(cl=>(
                    <div key={cl.id} style={{ display:"flex", gap:12, padding:"10px 0", borderBottom:"1px solid var(--bz-line)" }}>
                      {cl.photoUrl && <img src={cl.photoUrl} alt="" style={{ width:50, height:66, objectFit:"cover", borderRadius:6, flexShrink:0 }}/>}
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)" }}>{cl.cardName} #{cl.cardNum}</div>
                        <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>by {cl.submitterName||"Anonymous"}</div>
                        {cl.story && <div style={{ fontSize:11, color:"var(--bz-ink-2)", fontStyle:"italic", marginTop:4 }}>"{cl.story}"</div>}
                        <div style={{ display:"flex", gap:8, marginTop:8 }}>
                          <button onClick={async()=>{ await setDoc(doc(db,"oneof1_claims",cl.id),{status:"verified",reviewedAt:new Date().toISOString()},{merge:true}); }}
                            style={{ background:"rgba(74,222,128,0.15)", border:"1px solid rgba(74,222,128,0.3)", color:"#4ade80", borderRadius:7, padding:"4px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>✅ Verify</button>
                          <button onClick={async()=>{ await setDoc(doc(db,"oneof1_claims",cl.id),{status:"denied",reviewedAt:new Date().toISOString()},{merge:true}); }}
                            style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.3)", color:"#EF4444", borderRadius:7, padding:"4px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>✕ Deny</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Set filter — primary */}
              {sets1of1.length > 1 && (
                <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                  <span style={{ fontSize:12, fontWeight:700, color:"#9333EA" }}>{"\uD83D\uDC8E Set:"}</span>
                  <select value={secret1SetFilter} onChange={e=>{setSecret1SetFilter(e.target.value);setExpandedOneGroups({});}} style={{ background:"#130a1a", color:"#C084FC", border:"1.5px solid rgba(147,51,234,0.4)", borderRadius:10, padding:"8px 14px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", minWidth:200 }}>
                    <option value="">All Sets</option>
                    {sets1of1.map(s=>(<option key={s} value={s}>{s}</option>))}
                  </select>
                </div>
              )}

              {/* Group-by dropdown */}
              <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                <span style={{ fontSize:12, fontWeight:700, color:"#9333EA" }}>{"\uD83D\uDCC2 Group by:"}</span>
                <select value={secret1GroupBy} onChange={e=>{setSecret1GroupBy(e.target.value);setExpandedOneGroups({});}} style={{ background:"#130a1a", color:"#C084FC", border:"1.5px solid rgba(147,51,234,0.4)", borderRadius:10, padding:"8px 14px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", minWidth:180 }}>
                  <option value="set">💎 Set</option>
                  <option value="treatment">🎨 Treatment</option>
                  <option value="weapon">⚔️ Weapon</option>
                  <option value="hero">🦸 Hero</option>
                </select>
                <div style={{ flex:1 }}/>
                <button onClick={()=>setExpandedOneGroups({})} style={{ background:"transparent", color:"rgba(255,255,255,0.45)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:16, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Collapse all</button>
                <button onClick={()=>{ const all={}; (secret1SetFilter?secret1of1Cards.filter(c=>c.setName===secret1SetFilter):secret1of1Cards).forEach(c=>{ const k=secret1GroupBy==="set"?(c.setName||"Unknown"):secret1GroupBy==="treatment"?(c.treatment||"Unknown"):secret1GroupBy==="weapon"?(c.weapon||"Unknown"):(c.hero||"Unknown"); all[`${secret1GroupBy}:${k}`]=true; }); setExpandedOneGroups(all); }} style={{ background:"transparent", color:"rgba(255,255,255,0.45)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:16, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Expand all</button>
              </div>

              {/* Status filter */}
              <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                <span style={{ fontSize:12, fontWeight:700, color:"#9333EA" }}>{"\uD83C\uDFAF Show:"}</span>
                {[["all","All"],["claimed","🏆 Claimed"],["unclaimed","💎 Available"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setSecret1StatusFilter(v)} style={{ background:secret1StatusFilter===v?"rgba(147,51,234,0.18)":"transparent", color:secret1StatusFilter===v?"#C084FC":"rgba(255,255,255,0.5)", border:`1.5px solid ${secret1StatusFilter===v?"#9333EA":"rgba(255,255,255,0.1)"}`, borderRadius:20, padding:"6px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{l}</button>
                ))}
              </div>

              {/* Hero search */}
              <div style={{ position:"relative" }}>
                <input value={secret1Search} onChange={e=>setSecret1Search(e.target.value)} placeholder="🔍 Search hero or card #..."
                  style={{ width:"100%", background:"var(--bz-bg)", border:"1px solid rgba(147,51,234,0.3)", borderRadius:10, color:"var(--bz-ink)", padding:"10px 14px", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}/>
                {secret1Search && <button onClick={()=>setSecret1Search("")} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"var(--bz-ink-2)", cursor:"pointer", fontSize:16 }}>✕</button>}
              </div>

              {/* Grouped trackers */}
              {(()=>{
                const keyOf = c => secret1GroupBy==="set" ? (c.setName||"Unknown") : secret1GroupBy==="treatment" ? (c.treatment||"Unknown") : secret1GroupBy==="weapon" ? (c.weapon||"Unknown") : (c.hero||"Unknown");
                // Set filter is primary — everything else groups within the selected set
                const q = secret1Search.trim().toLowerCase();
                const matchesSearch = c => !q || `${c.hero||""} ${c.cardNum||""}`.toLowerCase().includes(q);
                const sourceCards = (secret1SetFilter ? secret1of1Cards.filter(c=>c.setName===secret1SetFilter) : secret1of1Cards).filter(matchesSearch);
                const groupMap={};
                sourceCards.forEach(c=>{ const k=keyOf(c); if(!k)return; (groupMap[k]=groupMap[k]||[]).push(c); });
                const groupKeys=Object.keys(groupMap).sort();
                if(groupKeys.length===0) return <div style={{ textAlign:"center", padding:40, color:"rgba(255,255,255,0.3)", fontSize:14 }}>No Secret 1/1s match your filters</div>;
                return groupKeys.map(groupKey=>{
                const setCards=groupMap[groupKey];
                const setName=groupKey;
                const setVerified=setCards.filter(c=>claimMap1[c.id]?.status==="verified");
                const setUnclaimed=setCards.filter(c=>!claimMap1[c.id]);
                const setPending=setCards.filter(c=>claimMap1[c.id]?.status==="pending");
                const verPct=setCards.length>0?(setVerified.length/setCards.length*100):0;
                const isFull=setVerified.length===setCards.length&&setCards.length>0;
                const visibleCards=secret1StatusFilter==="claimed"?setCards.filter(c=>!!claimMap1[c.id]):secret1StatusFilter==="unclaimed"?setCards.filter(c=>!claimMap1[c.id]):setCards;
                if(secret1StatusFilter!=="all" && visibleCards.length===0) return null;
                const collapseKey=`${secret1GroupBy}:${setName}`;
                const isCollapsed=q ? false : !expandedOneGroups[collapseKey];
                return (
                  <div key={setName} style={{ background:"var(--bz-s1)", border:`1px solid ${isFull?"#9333EA44":"var(--bz-line-2)"}`, borderRadius:14, overflow:"hidden" }}>
                    <div onClick={()=>setExpandedOneGroups(p=>({...p,[collapseKey]:!p[collapseKey]}))} style={{ padding:"16px 20px", background:isFull?"linear-gradient(135deg,#1a0a2e,var(--bz-s1))":"var(--bz-s1)", cursor:"pointer", userSelect:"none" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                        <div>
                          <div style={{ fontSize:14, fontWeight:800, color:isFull?"#9333EA":"var(--bz-ink)" }}>{isFull?"🏆 ":"💎 "}{setName}</div>
                          <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>{setVerified.length} verified · {setPending.length} pending · <span style={{ color:setUnclaimed.length>0?"#C084FC":"#555", fontWeight:setUnclaimed.length>0?800:400 }}>{setUnclaimed.length} available</span></div>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                          <div style={{ fontSize:22, fontWeight:900, color:"#9333EA" }}>{setVerified.length}<span style={{ fontSize:12, color:"var(--bz-ink-3)", fontWeight:400 }}>/{setCards.length}</span></div>
                          <span style={{ color:"rgba(255,255,255,0.3)", fontSize:14 }}>{isCollapsed?"▼":"▲"}</span>
                        </div>
                      </div>
                      <div style={{ height:8, background:"var(--bz-s2)", borderRadius:4, overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${verPct}%`, background:"linear-gradient(90deg,#7C3AED,#A855F7,#C084FC)", borderRadius:4, transition:"width 0.5s ease" }}/>
                      </div>
                    </div>
                    {!isCollapsed && (
                    <div style={{ padding:"12px 20px 16px", display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:10 }}>
                      {visibleCards.sort((a,b)=>String(a.cardNum||"").localeCompare(String(b.cardNum||""),undefined,{numeric:true})).map(c=>{
                        const claim=claimMap1[c.id]; const isV=claim?.status==="verified"; const isP=claim?.status==="pending";
                        return (
                          <div key={c.id} style={{ background:isV?"rgba(147,51,234,0.08)":"rgba(0,0,0,0.4)", border:`1.5px solid ${isV?"rgba(147,51,234,0.6)":isP?"rgba(147,51,234,0.25)":"rgba(255,255,255,0.08)"}`, borderRadius:14, overflow:"hidden", backdropFilter:"blur(4px)" }}>
                            {c.imageUrl && (()=>{
                              const isFlipped = isV && flippedClaim===`one_${c.id}` && (claim.photoUrl || claim.story);
                              const canFlip = isV && (claim.photoUrl || claim.story);
                              return (
                              <div style={{ position:"relative", aspectRatio:"3/4", cursor:canFlip?"pointer":"default" }} onClick={()=>{ if(canFlip) setFlippedClaim(p=>p===`one_${c.id}`?null:`one_${c.id}`); }}>
                                <div style={{ position:"relative", width:"100%", height:"100%", transformStyle:"preserve-3d", transition:"transform 0.5s cubic-bezier(0.4,0,0.2,1)", transform:isFlipped?"perspective(700px) rotateY(180deg)":"perspective(700px)", willChange:"transform" }}>
                                  <div style={{ position:"absolute", inset:0, backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden", overflow:"hidden", borderRadius:10 }}>
                                    <img src={c.imageUrl} alt={c.hero} style={{ width:"100%", height:"100%", objectFit:"cover", display:"block", opacity:isV?1:isP?0.6:0.5, filter:isV?"drop-shadow(0 0 12px rgba(147,51,234,0.6))":isP?"grayscale(45%)":"grayscale(70%)" }}/>
                                    {canFlip && <div style={{ position:"absolute", bottom:6, right:6, background:"rgba(0,0,0,0.7)", color:"#C084FC", borderRadius:20, padding:"2px 8px", fontSize:9, fontWeight:700 }}>🔄 the hit</div>}
                                  </div>
                                  {canFlip && (
                                    <div style={{ position:"absolute", inset:0, backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden", transform:"rotateY(180deg)", overflow:"hidden", borderRadius:10, background:"var(--bz-bg)", display:"flex", flexDirection:"column" }}>
                                      {claim.photoUrl && <img src={claim.photoUrl} alt="the hit" style={{ width:"100%", flex:1, objectFit:"cover", display:"block", minHeight:0 }}/>}
                                      {claim.story && <div style={{ padding:"8px 10px", fontSize:10, color:"#C084FC", fontStyle:"italic", lineHeight:1.5, maxHeight:"40%", overflowY:"auto" }}>"{claim.story}"</div>}
                                      <div style={{ position:"absolute", top:6, left:6, background:"rgba(0,0,0,0.7)", color:"#C084FC", borderRadius:20, padding:"2px 8px", fontSize:9, fontWeight:700 }}>🔄 card</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              );
                            })()}
                            <div style={{ padding:"10px 12px" }}>
                              <div style={{ fontSize:13, fontWeight:800, color:isV?"#9333EA":"var(--bz-ink)", marginBottom:2 }}>{c.hero}</div>
                              <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginBottom:4 }}>#{c.cardNum} · {c.treatment}</div>
                              {isV && <>
                                <div style={{ fontSize:11, fontWeight:700, color:"#9333EA", marginBottom:claim.story?4:6 }}>🏆 {claim.submitterName||claim.userName||"Anonymous"}</div>
                                {claim.story && <div style={{ fontSize:10, color:"var(--bz-ink-2)", fontStyle:"italic", lineHeight:1.5, marginBottom:4 }}>"{claim.story}"</div>}
                                {claim.dateHit && <div style={{ fontSize:10, color:"#8a8a8a", marginBottom:6 }}>{claim.dateHit}</div>}
                              </>}
                              {isP && <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginBottom:6 }}>⏳ {claim.submitterName||claim.userName||"Anonymous"}</div>}
                              {!isV&&!isP && <div style={{ fontSize:10, fontWeight:700, color:"#C084FC", marginBottom:6, letterSpacing:0.5 }}>💎 Available</div>}
                              {((swancity && !isV && !isP) || (user && owned?.[c.id] && !isV && !isP)) && (
                                <button onClick={()=>{ setOneModal(c); setOnePhoto(null); setOneSent(false); setOneStory(""); setOneDate(""); setOneName(""); }}
                                  style={{ width:"100%", background:"linear-gradient(135deg,#7C3AED,#A855F7)", color:"#fff", border:"none", borderRadius:8, padding:"7px 0", fontSize:11, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
                                  💎 Claim This 1/1
                                </button>
                              )}
                              {isAdminUser && !isV && (
                                <button onClick={()=>{ setAdminClaimMode(true); setOneModal(c); setOnePhoto(null); setOneSent(false); setOneStory(""); setOneDate(""); setOneName(""); }}
                                  style={{ width:"100%", marginTop:6, background:"rgba(245,158,11,0.12)", color:"#F59E0B", border:"1px solid rgba(245,158,11,0.4)", borderRadius:8, padding:"6px 0", fontSize:11, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
                                  🎯 Record Hit (Admin)
                                </button>
                              )}
                              {isAdminUser && claim && (
                                <button onClick={()=>{ setOneEditMode(true); setAdminClaimMode(true); setOneModal(c); setOnePhoto(claim.photoUrl?{preview:claim.photoUrl,existing:true}:null); setOneSent(false); setOneStory(claim.story||""); setOneDate(claim.dateHit||""); setOneName(claim.submitterName||claim.userName||""); }}
                                  style={{ width:"100%", marginTop:6, background:"rgba(96,165,250,0.1)", color:"#60A5FA", border:"1px solid rgba(96,165,250,0.3)", borderRadius:8, padding:"5px 0", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                                  ✏️ Edit Claim
                                </button>
                              )}
                              {isAdminUser && claim && (
                                <button onClick={async()=>{ if(window.confirm(`Remove the claim for ${c.hero} #${c.cardNum}? This cannot be undone.`)) { await deleteDoc(doc(db,"oneof1_claims",c.id)); } }}
                                  style={{ width:"100%", marginTop:6, background:"rgba(232,49,122,0.08)", color:"#E8317A", border:"1px solid rgba(232,49,122,0.2)", borderRadius:8, padding:"5px 0", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                                  🗑️ Remove Claim
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                );
              });
              })()}
            </div>
          );
        })()}

        {/* /34 BOJAX TAB */}
        {activeTab==="bojax34" && (() => {
          const isAdminUser = user?.email?.toLowerCase().includes("devin") || user?.email?.toLowerCase().includes("derrik") || user?.email?.toLowerCase()==="swancitycards@gmail.com";
          const bjCard = cards.find(c => (c.cardNum||"").toUpperCase()==="TBJ-B");
          const bySerial = {};
          (bojax34Claims||[]).forEach(cl => { bySerial[Number(cl.serial)] = cl; });
          const verifiedCount = (bojax34Claims||[]).filter(c=>c.status==="verified").length;
          const pendingCount  = (bojax34Claims||[]).filter(c=>c.status==="pending").length;
          const pct = Math.round((verifiedCount/34)*100);
          return (
            <div style={{ maxWidth:1100, margin:"0 auto", padding:"0 4px" }}>
              {/* Header + progress bar */}
              <div style={{ textAlign:"center", marginBottom:18 }}>
                <div style={{ fontSize:26, fontWeight:900, color:"#F59E0B", letterSpacing:0.5 }}>🅱️ /34 BoJax Tracker</div>
                <div style={{ fontSize:13, color:"var(--bz-ink-2)", marginTop:4 }}>The BoJax (TBJ-B) is serial-numbered to just <strong style={{color:"#ccc"}}>34 copies</strong>. Track which ones have surfaced.</div>
              </div>
              <div style={{ background:"var(--bz-s1)", border:"1px solid #F59E0B33", borderRadius:14, padding:"16px 20px", marginBottom:20 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:8 }}>
                  <span style={{ fontSize:13, fontWeight:800, color:"#F59E0B", textTransform:"uppercase", letterSpacing:1 }}>Found</span>
                  <span style={{ fontSize:22, fontWeight:900, color:"#fff" }}>{verifiedCount}<span style={{ fontSize:14, color:"#999" }}> / 34</span></span>
                </div>
                <div style={{ height:12, background:"var(--bz-bg)", borderRadius:6, overflow:"hidden", border:"1px solid var(--bz-line)" }}>
                  <div style={{ width:`${pct}%`, height:"100%", background:"linear-gradient(90deg,#F59E0B,#E8317A)", borderRadius:6, transition:"width 0.6s ease" }}/>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", marginTop:8, fontSize:11, color:"#999" }}>
                  <span>{pct}% accounted for</span>
                  {pendingCount>0 && <span style={{ color:"#FBBF24" }}>⏳ {pendingCount} pending verification</span>}
                  <span>{34-verifiedCount} still out there</span>
                </div>
              </div>
              {/* Admin: bulk upload all 34 serialized hit photos */}
              {isAdminUser && (
                <div style={{ background:"rgba(245,158,11,0.06)", border:"1px solid #F59E0B44", borderRadius:14, padding:"14px 18px", marginBottom:20, display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
                  <div style={{ flex:1, minWidth:200 }}>
                    <div style={{ fontSize:13, fontWeight:800, color:"#F59E0B" }}>📦 Bulk upload /34 photos</div>
                    <div style={{ fontSize:11, color:"var(--bz-ink-2)", marginTop:2 }}>Select up to 34 images. Each is mapped to a serial by the number in its filename (e.g. <strong style={{color:"#bbb"}}>1.jpg … 34.jpg</strong>) and marked as a verified hit.</div>
                  </div>
                  <label style={{ display:"inline-block", background: bojax34Uploading ? "var(--bz-s2)" : "linear-gradient(135deg,#F59E0B,#E8317A)", color: bojax34Uploading ? "#666" : "#fff", borderRadius:10, padding:"10px 18px", fontSize:13, fontWeight:800, cursor: bojax34Uploading ? "wait" : "pointer", fontFamily:"inherit" }}>
                    {bojax34Uploading ? `Uploading ${bojax34Uploading.done}/${bojax34Uploading.total}…` : "⬆️ Choose images"}
                    <input type="file" accept="image/*" multiple style={{ display:"none" }} disabled={!!bojax34Uploading}
                      onChange={e=>{ const fs=e.target.files; if(fs&&fs.length) bulkUploadBojax34(fs); e.target.value=""; }}/>
                  </label>
                </div>
              )}
              {/* 34-slot grid */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))", gap:12 }}>
                {Array.from({length:34}, (_,idx)=>{
                  const serial = idx+1;
                  const cl = bySerial[serial];
                  const verified = cl && cl.status==="verified";
                  const pending  = cl && cl.status==="pending";
                  const stateColor = verified ? "#F59E0B" : pending ? "#FBBF24" : "#333";
                  return (
                    <div key={serial} style={{ background: verified?"rgba(245,158,11,0.1)":pending?"rgba(251,191,36,0.06)":"var(--bz-s1)", border:`1.5px solid ${stateColor}${verified||pending?"":"55"}`, borderRadius:12, padding:"12px", textAlign:"center", position:"relative" }}>
                      <div style={{ fontSize:18, fontWeight:900, color: verified?"#F59E0B":pending?"#FBBF24":"#666" }}>{serial}<span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>/34</span></div>
                      {(() => {
                        const baseArt = bjCard?.imageUrl || cl?.cardImage || null;
                        const hitPhoto = (verified||pending) ? (cl?.photoUrl || null) : null;
                        const canFlip = !!(baseArt && hitPhoto);
                        const isFlipped = canFlip && flippedClaim===`bojax34_${serial}`;
                        if (!baseArt && !hitPhoto) return null;
                        return (
                          <div style={{ position:"relative", aspectRatio:"3/4", margin:"8px 0", cursor:canFlip?"pointer":"default" }}
                               onClick={()=>{ if(canFlip) setFlippedClaim(p=>p===`bojax34_${serial}`?null:`bojax34_${serial}`); }}>
                            <div style={{ position:"relative", width:"100%", height:"100%", transformStyle:"preserve-3d", transition:"transform 0.5s cubic-bezier(0.4,0,0.2,1)", transform:isFlipped?"perspective(700px) rotateY(180deg)":"none" }}>
                              {/* FRONT: base BoJax art */}
                              <div style={{ position:"absolute", inset:0, backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden", overflow:"hidden", borderRadius:8 }}>
                                <img src={baseArt || hitPhoto} alt={`#${serial}`} style={{ width:"100%", height:"100%", objectFit:"cover", display:"block", opacity:verified?1:pending?0.7:0.55, filter:verified?"drop-shadow(0 0 10px rgba(245,158,11,0.5))":pending?"grayscale(35%)":"grayscale(60%)" }}/>
                                {canFlip && <div style={{ position:"absolute", bottom:6, right:6, background:"rgba(0,0,0,0.7)", color:"#F59E0B", borderRadius:20, padding:"2px 8px", fontSize:9, fontWeight:700 }}>🔄 the hit</div>}
                              </div>
                              {/* BACK: the actual serialized hit photo */}
                              {canFlip && (
                                <div style={{ position:"absolute", inset:0, backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden", transform:"rotateY(180deg)", overflow:"hidden", borderRadius:8, background:"var(--bz-bg)" }}>
                                  <img src={hitPhoto} alt="the hit" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
                                  <div style={{ position:"absolute", top:6, left:6, background:"rgba(0,0,0,0.7)", color:"#F59E0B", borderRadius:20, padding:"2px 8px", fontSize:9, fontWeight:700 }}>🔄 card</div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                      {verified ? (
                        <>
                          <div style={{ fontSize:12, fontWeight:800, color:"#fff", marginTop:4 }}>🏆 {cl.submitterName||"Found"}</div>
                          {cl.dateHit && <div style={{ fontSize:10, color:"var(--bz-ink-2)" }}>{cl.dateHit}</div>}
                        </>
                      ) : pending ? (
                        <div style={{ fontSize:11, fontWeight:700, color:"#FBBF24", marginTop:4 }}>⏳ Pending</div>
                      ) : (
                        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:4 }}>🔓 Unclaimed</div>
                      )}
                      {/* Claim button */}
                      {!verified && (
                        <button onClick={()=>{ if(!user && !swancity){ setSigningIn(true); return; } setBojaxModal(serial); setBojaxName(""); setBojaxStory(""); setBojaxDate(""); setBojaxPhoto(null); }}
                          style={{ marginTop:8, width:"100%", background: pending?"rgba(251,191,36,0.12)":"linear-gradient(135deg,#F59E0B,#E8317A)", color: pending?"#FBBF24":"#fff", border:"none", borderRadius:8, padding:"6px", fontSize:11, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>
                          {pending ? "Claim anyway" : "I pulled this"}
                        </button>
                      )}
                      {/* Admin/swancity controls */}
                      {isAdminUser && cl && (
                        <div style={{ display:"flex", gap:4, marginTop:6 }}>
                          {pending && <button onClick={()=>verifyBojaxClaim(serial,true)} style={{ flex:1, background:"rgba(74,222,128,0.15)", border:"1px solid #4ade8055", color:"#4ade80", borderRadius:6, padding:"4px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>✓ Verify</button>}
                          <button onClick={()=>removeBojaxClaim(serial)} style={{ flex:1, background:"rgba(239,68,68,0.12)", border:"1px solid #ef444455", color:"#ef4444", borderRadius:6, padding:"4px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>🗑</button>
                        </div>
                      )}
                      {isAdminUser && !cl && (
                        <button onClick={()=>{ setBojaxModal(serial); setBojaxName(""); setBojaxStory(""); setBojaxDate(""); setBojaxPhoto(null); }} style={{ marginTop:6, width:"100%", background:"rgba(245,158,11,0.08)", border:"1px solid #F59E0B44", color:"#F59E0B", borderRadius:6, padding:"4px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>🎯 Record Hit</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* /34 BOJAX CLAIM MODAL */}
        {bojaxModal!=null && (()=>{
          const isAdminUser = user?.email?.toLowerCase().includes("devin") || user?.email?.toLowerCase().includes("derrik") || user?.email?.toLowerCase()==="swancitycards@gmail.com";
          const adminMode = isAdminUser;
          return (
            <div onClick={()=>setBojaxModal(null)} style={{ position:"fixed", inset:0, zIndex:13000, background:"rgba(0,0,0,0.85)", backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
              <div onClick={e=>e.stopPropagation()} style={{ background:"linear-gradient(160deg,#1a1206,#0d0d12)", border:"1.5px solid #F59E0B55", borderRadius:18, padding:24, maxWidth:440, width:"100%", maxHeight:"90vh", overflowY:"auto" }}>
                <div style={{ fontSize:20, fontWeight:900, color:"#F59E0B", marginBottom:4 }}>{adminMode?"🎯 Record Hit":"🅱️ Claim BoJax"} #{bojaxModal}/34</div>
                <div style={{ fontSize:12, color:"var(--bz-ink-2)", marginBottom:16 }}>{adminMode?"Record this serial as found.":"Submit your pull — an admin will verify it."}</div>
                <label style={{ fontSize:11, color:"#F59E0B", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Your Name / Handle</label>
                <input value={bojaxName} onChange={e=>setBojaxName(e.target.value)} placeholder="Anonymous" style={{ width:"100%", background:"#0d0d12", border:"1px solid rgba(255,255,255,0.15)", color:"#fff", borderRadius:8, padding:"9px 11px", fontSize:14, fontFamily:"inherit", boxSizing:"border-box", marginTop:4, marginBottom:12 }}/>
                <label style={{ fontSize:11, color:"#F59E0B", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Date Hit (optional)</label>
                <input type="date" value={bojaxDate} onClick={e=>e.target.showPicker&&e.target.showPicker()} onChange={e=>setBojaxDate(e.target.value)} style={{ width:"100%", background:"#0d0d12", border:"1px solid rgba(255,255,255,0.15)", color:"#fff", borderRadius:8, padding:"9px 11px", fontSize:16, fontFamily:"inherit", boxSizing:"border-box", marginTop:4, marginBottom:12 }}/>
                <label style={{ fontSize:11, color:"#F59E0B", fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>Story (optional)</label>
                <textarea value={bojaxStory} onChange={e=>setBojaxStory(e.target.value)} placeholder="How'd you land it?" rows={2} style={{ width:"100%", background:"#0d0d12", border:"1px solid rgba(255,255,255,0.15)", color:"#fff", borderRadius:8, padding:"9px 11px", fontSize:14, fontFamily:"inherit", boxSizing:"border-box", marginTop:4, marginBottom:12, resize:"vertical" }}/>
                <label style={{ display:"block", background: bojaxPhoto?"#0a1a0a":"var(--bz-bg)", border:`2px dashed ${bojaxPhoto?"#F59E0B":"var(--bz-line-2)"}`, borderRadius:12, padding:"18px", textAlign:"center", cursor:"pointer", marginBottom:14 }}>
                  {bojaxPhoto ? <img src={bojaxPhoto} alt="proof" style={{ maxHeight:180, maxWidth:"100%", borderRadius:8 }}/> : <div style={{ color:"#999", fontSize:13 }}>📸 Tap to add a photo of your card</div>}
                  <input type="file" accept="image/*" style={{ display:"none" }} onChange={e=>{ const f=e.target.files?.[0]; if(f){ const r=new FileReader(); r.onload=ev=>setBojaxPhoto(ev.target.result); r.readAsDataURL(f); } }}/>
                </label>
                <div style={{ display:"flex", gap:10 }}>
                  <button onClick={()=>setBojaxModal(null)} style={{ flex:1, background:"transparent", border:"1px solid rgba(255,255,255,0.15)", color:"var(--bz-ink-2)", borderRadius:12, padding:"12px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                  <button onClick={()=>submitBojaxClaim(bojaxModal, bojaxPhoto, bojaxName, bojaxStory, bojaxDate, adminMode)} disabled={bojaxSubmitting} style={{ flex:2, background:"linear-gradient(135deg,#F59E0B,#E8317A)", color:"#fff", border:"none", borderRadius:12, padding:"12px", fontSize:14, fontWeight:800, cursor:bojaxSubmitting?"wait":"pointer", fontFamily:"inherit" }}>{bojaxSubmitting?"Submitting…":adminMode?"🎯 Record Hit":"🅱️ Submit Claim"}</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* SUPERS TAB */}
        {activeTab==="supers" && (()=>{
          const isAdminUser = user?.email?.toLowerCase().includes("devin") || user?.email?.toLowerCase().includes("derrik") || user?.email?.toLowerCase()==="swancitycards@gmail.com";
          const superCards = cards.filter(c => c.weapon === "Super" || (c.weapon||"").toUpperCase() === "SUPER");
          const superSets = [...new Set(superCards.map(c => c.setName).filter(Boolean))].sort();
          const claimMap = {};
          superClaims.forEach(cl => { claimMap[cl.cardId] = cl; });
          const pendingClaims = superClaims.filter(cl => cl.status === "pending");
          // Scope the community overview to the selected set (or all sets when none selected)
          const scopedSupers = superSetFilter ? superCards.filter(c => c.setName === superSetFilter) : superCards;
          const scopedSuperIds = new Set(scopedSupers.map(c => c.id));
          const verifiedCount = superClaims.filter(cl => cl.status === "verified" && scopedSuperIds.has(cl.cardId)).length;
          const totalSupers = scopedSupers.length;
          const recentSupers = (() => {
            const seen = new Set();
            return superClaims
              .filter(cl => cl.status === "verified")
              .sort((a,b)=>new Date(b.reviewedAt||b.createdAt||0)-new Date(a.reviewedAt||a.createdAt||0))
              .filter(cl => { const k = cl.cardId || `${cl.cardName}|${cl.cardNum}`; if (seen.has(k)) return false; seen.add(k); return true; })
              .slice(0,12);
          })();

          async function submitClaim(card, photoBase64) {
            if (!user && !swancity) { setSigningIn(true); return; }
            setClaimSubmitting(true);
            try {
              // EDIT MODE: just update the existing claim's fields (keep photo unless a new one was picked)
              if (editClaimMode) {
                let newPhotoUrl = null;
                if (photoBase64 && typeof photoBase64 === "string" && photoBase64.startsWith("data:")) {
                  const storageRefE = ref(storage, `super_claims/${card.id}_${Date.now()}.jpg`);
                  const bs = atob(photoBase64.split(",")[1]); const ab2 = new ArrayBuffer(bs.length); const ia2 = new Uint8Array(ab2);
                  for (let i=0;i<bs.length;i++) ia2[i]=bs.charCodeAt(i);
                  await uploadBytes(storageRefE, new Blob([ab2],{type:"image/jpeg"}));
                  newPhotoUrl = await getDownloadURL(storageRefE);
                }
                await setDoc(doc(db, "super_claims", card.id), {
                  submitterName: claimName || "Anonymous", userName: claimName || "Anonymous",
                  story: claimStory || "", dateHit: claimDate || "",
                  ...(newPhotoUrl ? { photoUrl: newPhotoUrl } : {}),
                  editedByAdmin: user?.displayName || user?.email || "Admin", editedAt: new Date().toISOString(),
                }, { merge: true });
                setClaimModal(null); setClaimPhoto(null); setClaimStory(""); setClaimName(""); setClaimDate(""); setAdminClaimMode(false); setEditClaimMode(false);
                setClaimSubmitting(false);
                return;
              }
              const storageRef2 = ref(storage, `super_claims/${card.id}_${Date.now()}.jpg`);
              const byteStr = atob(photoBase64.split(",")[1]);
              const ab = new ArrayBuffer(byteStr.length);
              const ia = new Uint8Array(ab);
              for (let i = 0; i < byteStr.length; i++) ia[i] = byteStr.charCodeAt(i);
              await uploadBytes(storageRef2, new Blob([ab], { type: "image/jpeg" }));
              const photoUrl = await getDownloadURL(storageRef2);
              await setDoc(doc(db, "super_claims", card.id), {
                cardId: card.id, cardName: card.hero, cardNum: card.cardNum,
                setName: card.setName || "", cardImage: card.imageUrl || null,
                userId: adminClaimMode ? null : (user?.uid || null), userName: adminClaimMode ? (claimName||"Anonymous") : (user?.displayName || user?.email || claimName || "Anonymous"),
                submitterName: claimName || "Anonymous", story: claimStory || "", dateHit: claimDate || "",
                photoUrl, status: adminClaimMode ? "verified" : "pending",
                ...(adminClaimMode ? { recordedByAdmin: user?.displayName || user?.email || "Admin", reviewedAt: new Date().toISOString() } : {}),
                createdAt: new Date().toISOString(),
              });
              setClaimSent(true);
              setSuperCelebration({ cardName: card.hero, cardImage: card.imageUrl || null, type: "super" });
            } catch(e) { alert("Upload failed: " + e.message); }
            setClaimSubmitting(false);
          }

          async function adminVerify(claimId, approve, reason) {
            await setDoc(doc(db, "super_claims", claimId), {
              status: approve ? "verified" : "denied",
              denialReason: reason || "",
              reviewedAt: new Date().toISOString(),
              reviewedBy: user?.displayName || "Admin",
            }, { merge: true });
          }

          return (
            <div style={{display:"flex",flexDirection:"column",gap:16}}>

              {/* Claim modal */}
              {claimModal && (
                <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}
                  onClick={()=>{ if(!claimSubmitting){setClaimModal(null);setClaimPhoto(null);setClaimSent(false);setClaimStory("");setClaimName("");setClaimDate("");setAdminClaimMode(false);setEditClaimMode(false);} }}>
                  <div style={{background:"var(--bz-s1)",border:"2px solid #F59E0B",borderRadius:20,padding:28,maxWidth:420,width:"100%"}} onClick={e=>e.stopPropagation()}>
                    {claimSent?(
                      <div style={{textAlign:"center",padding:"20px 0"}}>
                        <div style={{fontSize:52,marginBottom:16}}>⭐</div>
                        <div style={{fontSize:20,fontWeight:900,color:"#F59E0B",marginBottom:8}}>Claim Submitted!</div>
                        <div style={{fontSize:13,color:"var(--bz-ink-2)",marginBottom:24}}>Your Super Foil claim is pending admin verification. You'll be notified once reviewed.</div>
                        <button onClick={()=>{setClaimModal(null);setClaimPhoto(null);setClaimSent(false);setClaimStory("");setClaimName("");setClaimDate("");setAdminClaimMode(false);}} style={{background:"#F59E0B",color:"#000",border:"none",borderRadius:12,padding:"12px 32px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>Done</button>
                      </div>
                    ):(
                      <>
                        <div style={{fontSize:18,fontWeight:900,color:"#F59E0B",marginBottom:4}}>{editClaimMode?"✏️ Edit Claim":adminClaimMode?"🎯 Record Super Hit (Admin)":"⭐ Claim Super Foil"}</div>
                        <div style={{fontSize:13,color:"var(--bz-ink-2)",marginBottom:16}}>{claimModal.hero} #{claimModal.cardNum} · {claimModal.setName}</div>
                        <div style={{fontSize:12,color:"var(--bz-ink-3)",marginBottom:16,lineHeight:1.6}}>{adminClaimMode?"Recording on behalf of the puller — this goes straight in as verified. Photo required.":"Super Foils are 1/1 cards. Upload a clear photo as proof — an admin will verify before it counts on the tracker."}</div>
                        <label style={{display:"block",marginBottom:16}}>
                          <div style={{background:claimPhoto?"#0a1a0a":"var(--bz-bg)",border:`2px dashed ${claimPhoto?"#F59E0B":"var(--bz-line-2)"}`,borderRadius:12,padding:"20px",textAlign:"center",cursor:"pointer"}}>
                            {claimPhoto?(
                              <img src={(claimPhoto&&claimPhoto.preview)?claimPhoto.preview:claimPhoto} alt="proof" style={{maxHeight:200,maxWidth:"100%",borderRadius:8,objectFit:"contain"}}/>
                            ):(
                              <>
                                <div style={{fontSize:32,marginBottom:8}}>📸</div>
                                <div style={{fontSize:13,fontWeight:700,color:"#F59E0B"}}>Tap to upload photo</div>
                                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:4}}>Clear photo of your Super Foil card</div>
                              </>
                            )}
                          </div>
                          <input type="file" accept="image/*" capture="environment" style={{display:"none"}}
                            onChange={e=>{const f=e.target.files?.[0];if(!f)return;const r=new FileReader();r.onload=ev=>setClaimPhoto(ev.target.result);r.readAsDataURL(f);e.target.value="";}}/>
                        </label>
                        <div style={{marginBottom:14}}>
                          <label style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-2)",display:"block",marginBottom:6}}>Date You Hit It</label>
                          <input type="date" value={claimDate} onChange={e=>setClaimDate(e.target.value)} onClick={e=>{try{e.target.showPicker&&e.target.showPicker();}catch(_){}}} style={{background:"var(--bz-bg)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"12px",fontSize:16,fontFamily:"inherit",outline:"none",width:"100%",boxSizing:"border-box",cursor:"pointer"}}/>
                        </div>
                        <div style={{marginBottom:14}}>
                          <label style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-2)",display:"block",marginBottom:6}}>The Story <span style={{color:"var(--bz-ink-3)",fontWeight:400}}>(optional)</span></label>
                          <textarea value={claimStory} onChange={e=>setClaimStory(e.target.value)} rows={4}
                            placeholder="Cards are about creating moments of joy… tell us the story about this hit ✨"
                            style={{background:"var(--bz-bg)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"10px 12px",fontSize:12,fontFamily:"inherit",outline:"none",width:"100%",boxSizing:"border-box",resize:"vertical",lineHeight:1.6}}/>
                        </div>
                        <div style={{marginBottom:20}}>
                          <label style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-2)",display:"block",marginBottom:6}}>Your Name <span style={{color:"var(--bz-ink-3)",fontWeight:400}}>(leave blank to stay anonymous)</span></label>
                          <input value={claimName} onChange={e=>setClaimName(e.target.value)} placeholder="Anonymous"
                            style={{background:"var(--bz-bg)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"10px 12px",fontSize:13,fontFamily:"inherit",outline:"none",width:"100%",boxSizing:"border-box"}}/>
                        </div>
                        <div style={{display:"flex",gap:10}}>
                          <button onClick={()=>{ const pd=(claimPhoto&&claimPhoto.existing)?null:(claimPhoto&&claimPhoto.preview?claimPhoto.preview:claimPhoto); if(editClaimMode||claimPhoto){submitClaim(claimModal,pd);} }} disabled={(!editClaimMode&&!claimPhoto)||claimSubmitting}
                            style={{flex:1,background:(editClaimMode||claimPhoto)?"#F59E0B":"var(--bz-s2)",color:(editClaimMode||claimPhoto)?"#000":"#555",border:"none",borderRadius:12,padding:"12px",fontSize:14,fontWeight:800,cursor:(editClaimMode||claimPhoto)?"pointer":"not-allowed",fontFamily:"inherit"}}>
                            {claimSubmitting?"Saving...":(editClaimMode?"💾 Save Changes":"Submit Claim")}
                          </button>
                          <button onClick={()=>{setClaimModal(null);setClaimPhoto(null);setClaimStory("");setClaimName("");setClaimDate("");setAdminClaimMode(false);setEditClaimMode(false);}} style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-2)",borderRadius:12,padding:"12px 20px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Admin verification queue */}
              {isAdminUser && pendingClaims.length > 0 && (
                <div style={{background:"#1a1400",border:"2px solid #F59E0B44",borderRadius:14,padding:20}}>
                  <div style={{fontSize:14,fontWeight:800,color:"#F59E0B",marginBottom:12}}>🔍 Pending Verification ({pendingClaims.length})</div>
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {pendingClaims.map(cl=>(
                      <div key={cl.id} style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line-2)",borderRadius:10,padding:14,display:"flex",gap:14,alignItems:"flex-start",flexWrap:"wrap"}}>
                        <img src={cl.photoUrl} alt="proof" style={{width:80,height:107,objectFit:"cover",borderRadius:8,flexShrink:0,cursor:"pointer"}} onClick={()=>window.open(cl.photoUrl,"_blank")}/>
                        <div style={{flex:1,minWidth:200}}>
                          <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)",marginBottom:4}}>{cl.cardName} #{cl.cardNum}</div>
                          <div style={{fontSize:11,color:"var(--bz-ink-2)",marginBottom:4}}>{cl.setName} · claimed by <strong style={{color:"#F59E0B"}}>{cl.userName}</strong></div>
                          <div style={{fontSize:10,color:"var(--bz-ink-3)"}}>{new Date(cl.createdAt).toLocaleDateString()}</div>
                        </div>
                        <div style={{display:"flex",gap:8,flexShrink:0}}>
                          <button onClick={()=>adminVerify(cl.id,true)} style={{background:"#0a1a0a",border:"1px solid #4ade80",color:"#4ade80",borderRadius:8,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>✅ Verify</button>
                          <button onClick={()=>{const reason=prompt("Reason for denial (optional):");adminVerify(cl.id,false,reason||"");}} style={{background:"#1a0a0a",border:"1px solid #E8317A",color:"#E8317A",borderRadius:8,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>❌ Deny</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Community overview */}
              <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(245,158,11,0.2)",borderRadius:20,padding:"24px 28px",backdropFilter:"blur(10px)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:12}}>
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:"#F59E0B",textTransform:"uppercase",letterSpacing:3,marginBottom:6}}>⭐ Community Super Foil Hunt</div>
                    <div style={{fontSize:22,fontWeight:900,color:"var(--bz-ink)"}}>How many 1/1s have been pulled?</div>
                    <div style={{fontSize:13,color:"rgba(255,255,255,0.3)",marginTop:4}}>{totalSupers} total Super Foils{superSetFilter?` in ${superSetFilter}`:""} · each is a unique 1/1</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:48,fontWeight:900,color:"#F59E0B",lineHeight:1}}>{verifiedCount}</div>
                    <div style={{fontSize:13,color:"rgba(255,255,255,0.3)"}}>of {totalSupers} verified</div>
                  </div>
                </div>
                <div style={{height:12,background:"rgba(255,255,255,0.06)",borderRadius:6,overflow:"hidden",marginBottom:8}}>
                  <div style={{height:"100%",width:`${totalSupers>0?(verifiedCount/totalSupers*100):0}%`,background:"linear-gradient(90deg,#F59E0B,#FBBF24,#FDE68A,#FBBF24,#F59E0B)",backgroundSize:"200% 100%",animation:"gradientShift 3s ease infinite",borderRadius:6,transition:"width 0.5s ease",boxShadow:"0 0 20px rgba(245,158,11,0.5)"}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <span style={{fontSize:11,color:"rgba(255,255,255,0.2)"}}>0 found</span>
                  <span style={{fontSize:12,fontWeight:700,color:"#F59E0B"}}>{totalSupers>0?(verifiedCount/totalSupers*100).toFixed(1):0}% of {superSetFilter?superSetFilter:"all"} Supers claimed</span>
                  <span style={{fontSize:11,color:"rgba(255,255,255,0.2)"}}>{totalSupers} total</span>
                </div>
              </div>

              {/* Recent claims ticker */}
              {recentSupers.length>0 && (
                <div style={{ background:"var(--bz-bg)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:12, overflow:"hidden", display:"flex", alignItems:"stretch" }}>
                  <div style={{ flexShrink:0, background:"#F59E0B", color:"#000", fontSize:10, fontWeight:800, padding:"0 12px", letterSpacing:0.5, display:"flex", alignItems:"center", writingMode:"vertical-rl", transform:"rotate(180deg)" }}>⭐ LATEST HITS</div>
                  <div className="vticker-wrap" style={{ flex:1, height:96 }}>
                    <div className="vticker-track" style={{ animationDuration:`${Math.max(8,recentSupers.length*3)}s` }}>
                      {[...recentSupers,...recentSupers].map((cl,i)=>(
                        <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 14px", height:32, boxSizing:"border-box" }}>
                          {cl.photoUrl && <img src={cl.photoUrl} alt="" style={{ width:18, height:24, objectFit:"cover", borderRadius:3, flexShrink:0 }}/>}
                          <span style={{ fontSize:12, fontWeight:700, color:"#FBBF24", whiteSpace:"nowrap" }}>{cl.cardName}</span>
                          <span style={{ fontSize:11, color:"#999", whiteSpace:"nowrap" }}>#{cl.cardNum}</span>
                          <span style={{ fontSize:11, color:"var(--bz-ink-2)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>by {cl.submitterName||cl.userName||"Anonymous"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Set filter */}
              {superSets.length > 1 && (
                <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                  <span style={{ fontSize:12, fontWeight:700, color:"#F59E0B" }}>{"\u2B50 Set:"}</span>
                  <select value={superSetFilter} onChange={e=>setSuperSetFilter(e.target.value)} style={{ background:"#15100a", color:"#F59E0B", border:"1.5px solid rgba(245,158,11,0.4)", borderRadius:10, padding:"8px 14px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", minWidth:200 }}>
                    <option value="">All Sets</option>
                    {superSets.map(s=>(<option key={s} value={s}>{s}</option>))}
                  </select>
                </div>
              )}

              {/* Status filter */}
              <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                <span style={{ fontSize:12, fontWeight:700, color:"#F59E0B" }}>{"\uD83C\uDFAF Show:"}</span>
                {[["all","All"],["claimed","🏆 Claimed"],["unclaimed","⭐ Available"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setSuperStatusFilter(v)} style={{ background:superStatusFilter===v?"rgba(245,158,11,0.15)":"transparent", color:superStatusFilter===v?"#F59E0B":"rgba(255,255,255,0.5)", border:`1.5px solid ${superStatusFilter===v?"#F59E0B":"rgba(255,255,255,0.1)"}`, borderRadius:20, padding:"6px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{l}</button>
                ))}
                <div style={{ flex:1 }}/>
                <button onClick={()=>{ const all={}; superSets.forEach(s=>{ all[s]=true; }); setCollapsedSuperSets(all); }} style={{ background:"transparent", color:"rgba(255,255,255,0.45)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:16, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Collapse all</button>
                <button onClick={()=>setCollapsedSuperSets({})} style={{ background:"transparent", color:"rgba(255,255,255,0.45)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:16, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Expand all</button>
              </div>

              {/* Hero search */}
              <div style={{ position:"relative" }}>
                <input value={superSearch} onChange={e=>setSuperSearch(e.target.value)} placeholder="🔍 Search hero or card #..."
                  style={{ width:"100%", background:"var(--bz-bg)", border:"1px solid rgba(245,158,11,0.3)", borderRadius:10, color:"var(--bz-ink)", padding:"10px 14px", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}/>
                {superSearch && <button onClick={()=>setSuperSearch("")} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"var(--bz-ink-2)", cursor:"pointer", fontSize:16 }}>✕</button>}
              </div>

              {/* Per-set trackers */}
              {(()=>{
                const sq=superSearch.trim().toLowerCase();
                const matchSuper=c=>!sq||`${c.hero||""} ${c.cardNum||""}`.toLowerCase().includes(sq);
                const visibleSets=superSets.filter(setName => !superSetFilter || setName === superSetFilter);
                const rendered=visibleSets.map(setName=>{
                const setSuperCards=superCards.filter(c=>c.setName===setName && matchSuper(c));
                if(setSuperCards.length===0) return null;
                const setVerified=setSuperCards.filter(c=>claimMap[c.id]?.status==="verified");
                const setPending=setSuperCards.filter(c=>claimMap[c.id]?.status==="pending");
                const setUnclaimed=setSuperCards.filter(c=>!claimMap[c.id]);
                const verPct=setSuperCards.length>0?(setVerified.length/setSuperCards.length*100):0;
                const isFull=setVerified.length===setSuperCards.length&&setSuperCards.length>0;
                const isCollapsed=sq ? false : !!collapsedSuperSets[setName];
                const visibleSuperCards=superStatusFilter==="claimed"?setSuperCards.filter(c=>!!claimMap[c.id]):superStatusFilter==="unclaimed"?setSuperCards.filter(c=>!claimMap[c.id]):setSuperCards;
                if(superStatusFilter!=="all" && visibleSuperCards.length===0) return null;
                return (
                  <div key={setName} style={{background:"rgba(255,255,255,0.02)",border:`1px solid ${isFull?"rgba(245,158,11,0.4)":"rgba(255,255,255,0.06)"}`,borderRadius:20,overflow:"hidden",backdropFilter:"blur(10px)"}}>
                    <div onClick={()=>setCollapsedSuperSets(prev=>({...prev,[setName]:!prev[setName]}))} style={{padding:"20px 24px",background:isFull?"linear-gradient(135deg,rgba(245,158,11,0.08),transparent)":"transparent",cursor:"pointer",userSelect:"none"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                        <div>
                          <div style={{fontSize:16,fontWeight:800,color:isFull?"#F59E0B":"var(--bz-ink)"}}>{isFull?"🏆 ":"⭐ "}{setName}</div>
                          <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:3}}>
                            {setVerified.length} verified · {setPending.length} pending · <span style={{color:setUnclaimed.length>0?"#FBBF24":"rgba(255,255,255,0.3)",fontWeight:setUnclaimed.length>0?800:400}}>{setUnclaimed.length} available</span>
                          </div>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:12}}>
                          <div style={{fontSize:28,fontWeight:900,color:"#F59E0B"}}>
                            {setVerified.length}<span style={{fontSize:14,color:"rgba(255,255,255,0.3)",fontWeight:400}}>/{setSuperCards.length}</span>
                          </div>
                          <span style={{color:"rgba(255,255,255,0.3)",fontSize:14}}>{isCollapsed?"▼":"▲"}</span>
                        </div>
                      </div>
                      <div style={{height:8,background:"rgba(255,255,255,0.06)",borderRadius:4,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${verPct}%`,background:isFull?"linear-gradient(90deg,#F59E0B,#FBBF24,#FDE68A,#FBBF24,#F59E0B)":"linear-gradient(90deg,#F59E0B,#FBBF24)",backgroundSize:"200% 100%",animation:isFull?"gradientShift 2s ease infinite":"none",borderRadius:4,transition:"width 0.5s ease"}}/>
                      </div>
                    </div>
                    {!isCollapsed&&(
                    <div style={{padding:"12px 24px 20px",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
                      {visibleSuperCards.sort((a,b)=>String(a.cardNum||"").localeCompare(String(b.cardNum||""),undefined,{numeric:true})).map(c=>{
                        const claim=claimMap[c.id];
                        const isVerified=claim?.status==="verified";
                        const isPending=claim?.status==="pending";
                        const isDenied=claim?.status==="denied";
                        const myOwned=owned?.[c.id];
                        const myClaim=claim?.userId===user?.uid;
                        return (
                          <div key={c.id} style={{background:isVerified?"rgba(245,158,11,0.08)":"rgba(0,0,0,0.4)",border:`1.5px solid ${isVerified?"rgba(245,158,11,0.6)":isPending?"rgba(245,158,11,0.25)":"rgba(255,255,255,0.08)"}`,borderRadius:14,overflow:"hidden",backdropFilter:"blur(4px)"}}>
                            {c.imageUrl&&(()=>{
                              const isFlipped = isVerified && flippedClaim===`super_${c.id}` && (claim.photoUrl || claim.story);
                              const canFlip = isVerified && (claim.photoUrl || claim.story);
                              return (
                              <div className="super-shine-card" style={{position:"relative",aspectRatio:"3/4",cursor:canFlip?"pointer":"default"}} onClick={()=>{ if(canFlip) setFlippedClaim(p=>p===`super_${c.id}`?null:`super_${c.id}`); }}>
                                <div style={{position:"relative",width:"100%",height:"100%",transformStyle:"preserve-3d",transition:"transform 0.5s cubic-bezier(0.4,0,0.2,1)",transform:isFlipped?"perspective(700px) rotateY(180deg)":"none",willChange:isFlipped?"transform":"auto"}}>
                                  <div style={{position:"absolute",inset:0,backfaceVisibility:"hidden",WebkitBackfaceVisibility:"hidden",overflow:"hidden",borderRadius:12}}>
                                    <img src={c.imageUrl} alt={c.hero} style={{width:"100%",height:"100%",objectFit:"cover",display:"block",opacity:isVerified?1:isPending?0.6:0.5,filter:isVerified?"drop-shadow(0 0 12px rgba(245,158,11,0.6))":isPending?"grayscale(45%)":"grayscale(70%)"}}/>
                                    {/* interactive foil shine — sits over the art, works even on greyed/unclaimed cards */}
                                    <div className="super-shine" style={{position:"absolute",inset:0,zIndex:5,borderRadius:12,pointerEvents:"none",opacity:0.5,background:"linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.22) 42%, rgba(255,220,120,0.4) 48%, rgba(120,220,255,0.45) 54%, rgba(210,130,255,0.4) 60%, transparent 72%)",backgroundSize:"300% 300%",mixBlendMode:"screen"}}/>
                                    {canFlip && <div style={{position:"absolute",bottom:6,right:6,background:"rgba(0,0,0,0.7)",color:"#F59E0B",borderRadius:20,padding:"2px 8px",fontSize:9,fontWeight:700}}>🔄 the hit</div>}
                                  </div>
                                  {canFlip && (
                                    <div style={{position:"absolute",inset:0,backfaceVisibility:"hidden",WebkitBackfaceVisibility:"hidden",transform:"rotateY(180deg)",overflow:"hidden",borderRadius:12,background:"var(--bz-bg)",display:"flex",flexDirection:"column"}}>
                                      {claim.photoUrl && <img src={claim.photoUrl} alt="the hit" style={{width:"100%",flex:1,objectFit:"cover",display:"block",minHeight:0}}/>}
                                      {claim.story && <div style={{padding:"8px 10px",fontSize:10,color:"#FBBF24",fontStyle:"italic",lineHeight:1.5,maxHeight:"40%",overflowY:"auto"}}>"{claim.story}"</div>}
                                      <div style={{position:"absolute",top:6,left:6,background:"rgba(0,0,0,0.7)",color:"#F59E0B",borderRadius:20,padding:"2px 8px",fontSize:9,fontWeight:700}}>🔄 card</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              );
                            })()}
                            <div style={{padding:"10px 12px"}}>
                              <div style={{fontSize:12,fontWeight:800,color:isVerified?"#F59E0B":"var(--bz-ink)",marginBottom:2}}>{c.hero}</div>
                              <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginBottom:6}}>#{c.cardNum} · 1/1 Super</div>
                              {isVerified&&<div style={{fontSize:11,fontWeight:700,color:"#F59E0B",marginBottom:claim.story?4:6}}>🏆 {claim.submitterName||claim.userName||"Anonymous"}</div>}
                              {isVerified&&claim.story&&<div style={{fontSize:10,color:"var(--bz-ink-2)",fontStyle:"italic",lineHeight:1.5,marginBottom:4}}>"{claim.story}"</div>}
                              {isVerified&&claim.dateHit&&<div style={{fontSize:10,color:"#8a8a8a",marginBottom:6}}>{claim.dateHit}</div>}
                              {isPending&&<div style={{fontSize:10,color:"rgba(255,255,255,0.4)",marginBottom:6}}>⏳ {claim.submitterName||claim.userName||"Anonymous"}</div>}
                              {!isVerified&&!isPending&&<div style={{fontSize:10,fontWeight:700,color:"#FBBF24",marginBottom:6,letterSpacing:0.5}}>⭐ Available</div>}
                              {isDenied&&myClaim&&<div style={{fontSize:10,color:"#E8317A",marginBottom:6}}>❌ Denied{claim.denialReason?`: ${claim.denialReason}`:""}</div>}
                              {((swancity && !isVerified && !isPending) || (user&&myOwned&&!isVerified&&!isPending)) && (
                                <button onClick={()=>{setClaimModal(c);setClaimPhoto(null);setClaimSent(false);setClaimStory("");setClaimName("");setClaimDate("");}}
                                  style={{width:"100%",background:"linear-gradient(135deg,#F59E0B,#FBBF24)",color:"#000",border:"none",borderRadius:8,padding:"6px 0",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 12px rgba(245,158,11,0.3)"}}>
                                  ⭐ Claim This Super
                                </button>
                              )}
                              {!swancity && !user&&!isVerified&&!isPending&&(
                                <button onClick={()=>setSigningIn(true)}
                                  style={{width:"100%",background:"rgba(245,158,11,0.1)",color:"#F59E0B",border:"1px solid rgba(245,158,11,0.3)",borderRadius:8,padding:"6px 0",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                                  Sign in to claim
                                </button>
                              )}
                              {isAdminUser && !isVerified && (
                                <button onClick={()=>{ setAdminClaimMode(true); setClaimModal(c); setClaimPhoto(null); setClaimSent(false); setClaimStory(""); setClaimName(""); setClaimDate(""); }}
                                  style={{width:"100%",marginTop:6,background:"rgba(245,158,11,0.12)",color:"#F59E0B",border:"1px solid rgba(245,158,11,0.4)",borderRadius:8,padding:"6px 0",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                                  🎯 Record Hit (Admin)
                                </button>
                              )}
                              {isAdminUser && claim && (
                                <button onClick={()=>{ setEditClaimMode(true); setAdminClaimMode(true); setClaimModal(c); setClaimPhoto(claim.photoUrl?{preview:claim.photoUrl,existing:true}:null); setClaimSent(false); setClaimStory(claim.story||""); setClaimName(claim.submitterName||claim.userName||""); setClaimDate(claim.dateHit||""); }}
                                  style={{width:"100%",marginTop:6,background:"rgba(96,165,250,0.1)",color:"#60A5FA",border:"1px solid rgba(96,165,250,0.3)",borderRadius:8,padding:"5px 0",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                                  ✏️ Edit Claim
                                </button>
                              )}
                              {isAdminUser && claim && (
                                <button onClick={async()=>{ if(window.confirm(`Remove the claim for ${c.hero} #${c.cardNum}? This cannot be undone.`)) { await deleteDoc(doc(db,"super_claims",c.id)); } }}
                                  style={{width:"100%",marginTop:6,background:"rgba(232,49,122,0.08)",color:"#E8317A",border:"1px solid rgba(232,49,122,0.2)",borderRadius:8,padding:"5px 0",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                                  🗑️ Remove Claim
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                );
              });
                const anyRendered=rendered.some(Boolean);
                if(!anyRendered && (superSearch.trim()||superStatusFilter!=="all")) return <div style={{ textAlign:"center", padding:40, color:"rgba(255,255,255,0.3)", fontSize:14 }}>No Super Foils match your filters</div>;
                return rendered;
              })()}

              {superCards.length===0&&(
                <div style={{textAlign:"center",padding:"80px 0",color:"rgba(255,255,255,0.2)"}}>
                  <div style={{fontSize:48,marginBottom:16}}>⭐</div>
                  <div style={{fontSize:16,fontWeight:700}}>No Super Foil cards found yet</div>
                  <div style={{fontSize:12,marginTop:8}}>Super Foil cards need weapon="Super" in the checklist</div>
                </div>
              )}
            </div>
          );
        })()}

        {/* CARDS TAB */}
        {activeTab==="cards" && cards.length>0 && (
          <>
            {user && cards.length>0 && (()=>{
              // Next-milestone framing so the bar always feels close & moving
              const MILES = [10,25,50,100,150,200,300,500,750,1000,1500,2000,3000,5000];
              const nextMile = MILES.find(m => m > totalOwned) || (Math.ceil(totalOwned/1000)*1000 + 1000);
              const prevMile = [...MILES].reverse().find(m => m <= totalOwned) || 0;
              const span = nextMile - prevMile;
              const intoSpan = totalOwned - prevMile;
              const milePct = span>0 ? (intoSpan/span*100) : 0;
              // Collector tier by count
              const TIERS = [[0,"Rookie","#7B9CFF"],[25,"Collector","#4ade80"],[100,"Veteran","#FBBF24"],[300,"Elite","#E8317A"],[750,"Legend","#A855F7"],[1500,"Hall of Fame","#FFD700"]];
              const tier = [...TIERS].reverse().find(t => totalOwned >= t[0]) || TIERS[0];
              const toNext = nextMile - totalOwned;
              return (
                <div style={{background:"linear-gradient(135deg, rgba(232,49,122,0.10), rgba(123,47,247,0.08))",border:"1px solid rgba(232,49,122,0.2)",borderRadius:16,padding:"18px 22px",marginBottom:16,backdropFilter:"blur(10px)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,flexWrap:"wrap",gap:10}}>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                        <span style={{fontSize:11,color:"rgba(255,255,255,0.5)",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase"}}>Your Collection</span>
                        <span style={{fontSize:10,fontWeight:800,color:tier[2],background:`${tier[2]}1a`,border:`1px solid ${tier[2]}44`,borderRadius:20,padding:"2px 10px",letterSpacing:0.5}}>{tier[1]}</span>
                      </div>
                      <div style={{display:"flex",alignItems:"baseline",gap:8}}>
                        <span style={{fontSize:44,fontWeight:900,color:"#fff",lineHeight:1,textShadow:"0 0 30px rgba(232,49,122,0.4)"}}>{totalOwned.toLocaleString()}</span>
                        <span style={{fontSize:15,color:"rgba(255,255,255,0.45)",fontWeight:700}}>card{totalOwned===1?"":"s"}</span>
                      </div>
                    </div>
                    {collectionValue>0 && (
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>Est. Value</div>
                        <div style={{fontSize:26,fontWeight:900,color:"#4ade80",lineHeight:1.1,marginTop:3,textShadow:"0 0 20px rgba(74,222,128,0.4)"}}>${collectionValue.toLocaleString(undefined,{maximumFractionDigits:0})}</div>
                      </div>
                    )}
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <span style={{fontSize:11,color:"rgba(255,255,255,0.4)",fontWeight:600}}>Next goal: {nextMile.toLocaleString()} cards</span>
                    <span style={{fontSize:11,color:"#FBBF24",fontWeight:800}}>{toNext.toLocaleString()} to go</span>
                  </div>
                  <div style={{height:10,background:"rgba(255,255,255,0.06)",borderRadius:6,overflow:"hidden",position:"relative"}}>
                    <div style={{height:"100%",width:`${Math.min(milePct,100)}%`,background:"linear-gradient(90deg,#E8317A,#FBBF24)",borderRadius:6,transition:"width 0.8s cubic-bezier(0.22,1,0.36,1)",boxShadow:"0 0 12px rgba(232,49,122,0.6)"}}/>
                  </div>
                  {/* Cards you've moved on from. Lives with the collection stats — it's a fact about
                      your collection's history, not a filter, so it doesn't belong in the filter row. */}
                  {user && soldLog.length>0 && (
                    <div style={{marginTop:10,display:"flex",justifyContent:"flex-end"}}>
                      <button onClick={()=>setSoldView(true)} title="Cards you've sold, traded or given away"
                        style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(255,255,255,0.5)",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                        📜 Formerly owned ({soldLog.length})
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
            <div ref={filterBarRef} className="filter-bar" style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:16,padding:"14px 18px",marginBottom:16,backdropFilter:"blur(10px)",display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",position:"relative",zIndex:(powerMenuOpen||multiOpen)?10000:"auto"}}>
              <input value={search} onFocus={()=>{ if(multiOpen||powerMenuOpen){ setMultiOpen(null); setPowerMenuOpen(false); } }} onChange={e=>{setSearch(e.target.value);preserveScroll();setPage(1);}} placeholder="Search hero, card #, athlete, treatment..." style={{...inp,flex:2,minWidth:200}}/>
              {/* Multi-select dropdown — set, treatment and weapon all work like the power filter now.
                  Same markup for each, so it's one helper rather than three near-identical blocks. */}
              {(() => {
                const MultiFilter = ({ label, options, sel, setSel, openKey }) => {
                  const open = multiOpen === openKey;
                  const text = sel.size===0 ? label : sel.size===1 ? [...sel][0] : `${sel.size} selected`;
                  return (
                    <div style={{position:"relative",zIndex:open?10000:"auto",flex:1,minWidth:140}}>
                      <button onClick={()=>setMultiOpen(open?null:openKey)}
                        style={{...inp,width:"100%",height:"auto",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:4}}>
                        <span style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",color:sel.size?"#E8317A":undefined,fontWeight:sel.size?700:undefined}}>{text}</span>
                        <span style={{fontSize:10,opacity:0.5,marginLeft:4}}>{"\u25BE"}</span>
                      </button>
                      {open && (
                        <>
                          {/* Full-screen invisible catcher: any click off the menu closes it.
                              zIndex sits below the menu (9999) but above page content. */}
                          <div onPointerDown={(e)=>{ e.stopPropagation(); setMultiOpen(null); }}
                               style={{position:"fixed",inset:0,zIndex:9990}}/>
                          <div className="filter-menu" style={{position:"absolute",top:"calc(100% + 4px)",left:0,zIndex:9999,background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:10,padding:6,minWidth:200,maxHeight:320,overflowY:"auto",boxShadow:"0 10px 30px rgba(0,0,0,0.6)"}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 8px 6px",borderBottom:"1px solid #222",marginBottom:4}}>
                              <span style={{fontSize:10,color:"var(--bz-ink-2)",fontWeight:700,letterSpacing:0.5}}>{label.toUpperCase()}</span>
                              {sel.size>0 && <button onClick={()=>{setSel(new Set());preserveScroll();setPage(1);}} style={{background:"none",border:"none",color:"var(--bz-ink-3)",fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>Clear</button>}
                            </div>
                            {options.map(o=>{
                              const on=sel.has(o);
                              return (
                                <label key={o} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:6,cursor:"pointer",background:on?"rgba(232,49,122,0.12)":"transparent"}}
                                  onMouseEnter={e=>{if(!on)e.currentTarget.style.background="rgba(255,255,255,0.04)";}}
                                  onMouseLeave={e=>{if(!on)e.currentTarget.style.background="transparent";}}>
                                  <input type="checkbox" checked={on} onChange={()=>{setSel(prev=>{const n=new Set(prev); n.has(o)?n.delete(o):n.add(o); return n;}); preserveScroll();setPage(1);}} style={{accentColor:"#E8317A",cursor:"pointer"}}/>
                                  <span style={{fontSize:12,fontWeight:700,color:on?"#E8317A":"#ccc"}}>{o}</span>
                                </label>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  );
                };
                return (
                  <>
                    <MultiFilter label="All Sets"       options={sets}       sel={filterSet}    setSel={setFilterSet}    openKey="set"/>
                    <MultiFilter label="All Treatments" options={treatments} sel={filterTreat}  setSel={setFilterTreat}  openKey="treat"/>
                    <MultiFilter label="All Weapons"    options={weapons}    sel={filterWeapon} setSel={setFilterWeapon} openKey="weapon"/>
                  </>
                );
              })()}
              {/* Per-print set progress. Alpha Blast Hot Dogs are two 30-card runs that share card
                  numbers, so "how many Hot Dogs do I have" is ambiguous \u2014 you can hold 30 cards and
                  still be missing half of each print. Counts DISTINCT names per version, because the
                  goal is completing a run, not stacking duplicates. */}
              {(() => {
                if (filterSet.size !== 1) return null;
                const theSet = [...filterSet][0];
                const pool = cards.filter(c => c.setName === theSet);
                const spec = pool.map(versionSpecFor).find(Boolean);
                if (!spec) return null;
                const tracked = pool.filter(c => versionSpecFor(c));
                const nameOf = c => (c.hero || c.playName || "").trim().toLowerCase();
                const target = new Set(tracked.map(nameOf).filter(Boolean)).size;
                if (!target) return null;
                const byVersion = {};
                spec.options.forEach(o => byVersion[o] = new Set());
                let unassigned = 0;
                tracked.forEach(card => {
                  if (!owned[card.id]) return;
                  const seen = new Set((lots||[]).filter(l => l.cardId === card.id).map(l => l.version).filter(Boolean));
                  if (seen.size === 0) { unassigned++; return; }
                  seen.forEach(v => { if (byVersion[v]) byVersion[v].add(nameOf(card)); });
                });
                return (
                  <div style={{display:"flex",gap:10,flexWrap:"wrap",width:"100%",marginTop:4}}>
                    {spec.options.map(opt => {
                      const have = byVersion[opt].size;
                      const pct  = target ? Math.round((have/target)*100) : 0;
                      const done = have >= target;
                      return (
                        <div key={opt} style={{flex:"1 1 180px",minWidth:170,background:done?"rgba(74,222,128,0.1)":"rgba(255,255,255,0.03)",border:"1px solid "+(done?"rgba(74,222,128,0.45)":"rgba(255,255,255,0.1)"),borderRadius:10,padding:"9px 11px"}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:8,marginBottom:5}}>
                            <span style={{fontSize:11.5,fontWeight:800,color:done?"#4ade80":"var(--bz-ink)"}}>{done?"\u2705 ":""}{opt}</span>
                            <span style={{fontSize:12.5,fontWeight:900,color:done?"#4ade80":"#E8317A"}}>{have}/{target}</span>
                          </div>
                          <div style={{height:5,background:"rgba(0,0,0,0.4)",borderRadius:3,overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${pct}%`,background:done?"#4ade80":"linear-gradient(90deg,#E8317A,#7B2FF7)",borderRadius:3}}/>
                          </div>
                          {!done && <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:4}}>{target-have} to go</div>}
                        </div>
                      );
                    })}
                    {unassigned>0 && (
                      <div style={{flex:"1 1 100%",fontSize:10.5,color:"#FBBF24",lineHeight:1.5}}>
                        {unassigned} card{unassigned===1?"":"s"} you own {unassigned===1?"has":"have"} no print recorded {"\u2014"} set it under {"\uD83D\uDCB0"} Details so it counts toward a run.
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* Sub-set chips (e.g. World Champions Series → L.A. / Philadelphia / Oklahoma City) */}
              {(() => {
                // Sub-sets only make sense within a single set, so only show these chips when
                // exactly one set is selected.
                if(filterSet.size !== 1) return null;
                const theSet = [...filterSet][0];
                const subs = Array.from(new Set(cards.filter(c=>c.setName===theSet && c.subSet).map(c=>c.subSet))).sort();
                if(subs.length<2) return null;
                return (
                  <div style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap"}}>
                    <button onClick={()=>{setFilterSubSet("");preserveScroll();setPage(1);}} style={{background:!filterSubSet?"rgba(74,222,128,0.15)":"transparent",border:`1px solid ${!filterSubSet?"#4ade80":"rgba(255,255,255,0.12)"}`,color:!filterSubSet?"#4ade80":"rgba(255,255,255,0.45)",borderRadius:14,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>All</button>
                    {subs.map(s=>{
                      const on=filterSubSet===s;
                      const n=cards.filter(c=>c.setName===theSet && c.subSet===s).length;
                      return <button key={s} onClick={()=>{setFilterSubSet(on?"":s);preserveScroll();setPage(1);}} style={{background:on?"rgba(123,156,255,0.18)":"transparent",border:`1px solid ${on?"#7B9CFF":"rgba(255,255,255,0.12)"}`,color:on?"#7B9CFF":"rgba(255,255,255,0.45)",borderRadius:14,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{s} ({n})</button>;
                    })}
                  </div>
                );
              })()}
              {/* Power multi-select dropdown */}
              <div style={{position:"relative",zIndex:powerMenuOpen?10000:"auto",flex:1,minWidth:140}}>
                <button onClick={()=>setPowerMenuOpen(o=>!o)}
                  style={{...inp,width:"100%",height:"auto",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",gap:6,textAlign:"left",color:filterPower.size>0?"#E8317A":"var(--bz-ink)",borderColor:filterPower.size>0?"#E8317A":"rgba(255,255,255,0.1)"}}>
                  <span style={{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{filterPower.size>0?`Power (${filterPower.size})`:"All Power"}</span>
                  <span style={{fontSize:10,opacity:0.5,marginLeft:4}}>▾</span>
                </button>
                {powerMenuOpen && (
                  <>
                    <div onPointerDown={(e)=>{ e.stopPropagation(); setPowerMenuOpen(false); }}
                         style={{position:"fixed",inset:0,zIndex:9990}}/>
                    <div className="filter-menu" style={{position:"absolute",top:"calc(100% + 4px)",left:0,zIndex:9999,background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.8)",width:180,maxHeight:280,overflowY:"auto",padding:6}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 8px 6px",borderBottom:"1px solid var(--bz-line-2)",marginBottom:4}}>
                        <span style={{fontSize:10,color:"var(--bz-ink-2)",fontWeight:700,letterSpacing:1}}>POWER LEVEL</span>
                        {filterPower.size>0 && <button onClick={()=>{setFilterPower(new Set());preserveScroll();setPage(1);}} style={{background:"none",border:"none",color:"#E8317A",fontSize:10,cursor:"pointer",fontFamily:"inherit"}}>Clear</button>}
                      </div>
                      {[250,200,195,190,185,180,175,170,165,160,155,150,145,140,135,130,125,120,115,110,105,100,95,90,85,80,75].map(p=>{
                        const on=filterPower.has(p);
                        return (
                          <label key={p} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:6,cursor:"pointer",background:on?"rgba(232,49,122,0.12)":"transparent"}}
                            onMouseEnter={e=>{if(!on)e.currentTarget.style.background="rgba(255,255,255,0.04)";}}
                            onMouseLeave={e=>{if(!on)e.currentTarget.style.background="transparent";}}>
                            <input type="checkbox" checked={on} onChange={()=>{setFilterPower(prev=>{const n=new Set(prev);n.has(p)?n.delete(p):n.add(p);return n;});preserveScroll();setPage(1);}} style={{accentColor:"#E8317A",cursor:"pointer"}}/>
                            <span style={{fontSize:12,fontWeight:700,color:on?"#E8317A":"#ccc"}}>{p}</span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              {/* Clear every filter at once. With five multi-selects it's easy to end up with a
                  narrow result and not remember which one caused it — one button beats hunting. */}
              {(() => {
                const active = filterSet.size + filterTreat.size + filterWeapon.size + filterPower.size
                             + (filterSubSet?1:0) + (search?1:0) + (filterOwned!=="all"?1:0);
                if(!active) return null;
                return (
                  <button onClick={()=>{
                    setFilterSet(new Set()); setFilterTreat(new Set()); setFilterWeapon(new Set());
                    setFilterPower(new Set()); setFilterSubSet(""); setSearch(""); setFilterOwned("all");
                    setMultiOpen(null); setPowerMenuOpen(false); preserveScroll();setPage(1);
                  }} style={{background:"rgba(232,49,122,0.1)",border:"1px solid #E8317A55",color:"#E8317A",borderRadius:8,padding:"7px 12px",fontSize:11.5,fontWeight:800,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                    {"\u2715"} Clear {active} filter{active>1?"s":""}
                  </button>
                );
              })()}
              <select value={sortBy} onChange={e=>{setSortBy(e.target.value);preserveScroll();setPage(1);}} style={{...inp,width:isMobile?"auto":150,minWidth:110,cursor:"pointer"}}>
                <option value="cardNum">Card #</option>
                <option value="hero">{"Hero A\u2192Z"}</option>
                <option value="power">{"Power (High \u2192 Low)"}</option>
                <option value="powerAsc">{"Power (Low \u2192 High)"}</option>
                <option value="setName">Set</option>
              </select>
              <div style={{display:"flex",gap:0,border:"1px solid rgba(255,255,255,0.12)",borderRadius:8,overflow:"hidden"}}>
                <button onClick={()=>setCardView("grid")} title="Card grid" style={{background:cardView==="grid"?"rgba(232,49,122,0.2)":"transparent",color:cardView==="grid"?"#E8317A":"rgba(255,255,255,0.4)",border:"none",padding:"6px 11px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>▦</button>
                <button onClick={()=>setCardView("list")} title="List view" style={{background:cardView==="list"?"rgba(232,49,122,0.2)":"transparent",color:cardView==="list"?"#E8317A":"rgba(255,255,255,0.4)",border:"none",padding:"6px 11px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>☰</button>
              </div>
              <button onClick={()=>setAnimsOn(v=>!v)} title={animsOn?"Animations on — tap to turn off the add-card celebration & effects":"Animations off — tap to turn back on"} style={{background:animsOn?"rgba(232,49,122,0.12)":"transparent",color:animsOn?"#E8317A":"rgba(255,255,255,0.35)",border:`1.5px solid ${animsOn?"rgba(232,49,122,0.4)":"rgba(255,255,255,0.1)"}`,borderRadius:20,padding:"6px 13px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{animsOn?"✨ Animations On":"✨ Animations Off"}</button>
              {user && <button onClick={()=>{ if(selectMode) exitSelectMode(); else setSelectMode(true); }} title="Select multiple cards for bulk actions" style={{background:selectMode?"rgba(123,156,255,0.2)":"transparent",color:selectMode?"#7B9CFF":"rgba(255,255,255,0.35)",border:`1.5px solid ${selectMode?"#7B9CFF":"rgba(255,255,255,0.1)"}`,borderRadius:20,padding:"6px 13px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{selectMode?"✕ Done":"☑️ Select"}</button>}
              {cardView==="grid" && !isMobile && <div style={{display:"flex",alignItems:"center",gap:7,padding:"0 4px"}} title="Drag to resize cards">
                <span style={{fontSize:13,color:"rgba(255,255,255,0.3)"}}>🃏</span>
                <input type="range" min="110" max="360" step="10" value={cardSize} onChange={e=>setCardSize(Number(e.target.value))} style={{width:90,accentColor:"#E8317A",cursor:"pointer"}}/>
                <span style={{fontSize:18,color:"rgba(255,255,255,0.3)"}}>🃏</span>
              </div>}
              {user&&(
                <select value={filterNoImg ? "noimg" : filterOwned}
                  onChange={e=>{
                    const v = e.target.value;
                    preserveScroll(); setPage(1);
                    if (v === "noimg") { setFilterNoImg(true); }
                    else { setFilterNoImg(false); setFilterOwned(v); }
                  }}
                  style={{background:"#14141c",color:(filterOwned!=="all"||filterNoImg)?"#E8317A":"rgba(255,255,255,0.6)",
                          border:`1.5px solid ${(filterOwned!=="all"||filterNoImg)?"#E8317A":"rgba(255,255,255,0.12)"}`,
                          borderRadius:20,padding:"6px 12px",fontSize:11.5,fontWeight:700,cursor:"pointer",
                          fontFamily:"inherit",outline:"none"}}>
                  <option value="all">All cards</option>
                  <option value="owned">{"\u2705"} Owned</option>
                  <option value="missing">{"\u274C"} Missing</option>
                  <option value="free">{"\uD83D\uDD13"} Free to trade</option>
                  <option value="indeck">{"\uD83D\uDCD8"} In a deck</option>
                  <option value="lent">{"\uD83D\uDCE4"} Lent out</option>
                  <option value="borrowed">{"\uD83D\uDCE5"} Borrowed</option>
                  <option value="proxy">{"\uD83C\uDFF7\uFE0F"} Proxies</option>
                  {_cardAdmin && <option value="noimg">{"\uD83D\uDDBC"} No image (admin)</option>}
                </select>
              )}
              {/* Kid collections — whose binder a card lives in. Visually separated from the
                  owned/missing filters above, since they answer a different question (and both
                  rows had an "All" chip, which read as a duplicate). */}
              {user && kidGroups.length>0 && (
                <>
                  <div style={{width:1,height:20,background:"rgba(255,255,255,0.1)",margin:"0 2px"}}/>
                  <span style={{fontSize:10.5,color:"rgba(255,255,255,0.3)",fontWeight:700,whiteSpace:"nowrap"}}>Whose:</span>
                  {[["all","Everyone"],["mine","Mine"]].concat(kidGroups.map(g=>[g.id,g.name])).map(([id,label])=>{
                    const on = kidFilter===id;
                    const g = kidGroups.find(x=>x.id===id);
                    const col = g ? g.color : (id==="mine" ? "#4ade80" : "#7B9CFF");
                    const count = id==="all" ? null : (id==="mine" ? (kidCounts.__mine||0) : (kidCounts[id]||0));
                    return (
                      <button key={id} onClick={()=>{setKidFilter(id);preserveScroll();setPage(1);}}
                        style={{background:on?`${col}26`:"transparent",color:on?col:"rgba(255,255,255,0.4)",border:`1.5px solid ${on?col:"rgba(255,255,255,0.08)"}`,borderRadius:20,padding:"6px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"all 0.2s",whiteSpace:"nowrap"}}>
                        {g ? "🧒 " : ""}{label}{count!==null?` (${count})`:""}
                      </button>
                    );
                  })}
                  <button onClick={()=>setKidsModal(true)} title="Manage kids' collections"
                    style={{background:"transparent",color:"rgba(255,255,255,0.35)",border:"1.5px dashed rgba(255,255,255,0.12)",borderRadius:20,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                    ⚙
                  </button>
                </>
              )}
              {/* No kids set up yet — offer to create the first one. */}
              {user && kidGroups.length===0 && (
                <button onClick={()=>setKidsModal(true)} title="Collecting with your kids? Give them their own collection"
                  style={{background:"transparent",color:"rgba(255,255,255,0.3)",border:"1.5px dashed rgba(255,255,255,0.1)",borderRadius:20,padding:"6px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                  🧒 Add a kid
                </button>
              )}
              <span style={{fontSize:12,color:"rgba(255,255,255,0.2)",marginLeft:"auto"}}>{filtered.length.toLocaleString()} cards</span>
            </div>
            {cardView==="list" ? (
              <div style={{overflowX:"auto",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead>
                    <tr style={{background:"rgba(255,255,255,0.03)",textAlign:"left"}}>
                      <th style={{padding:"10px 14px",color:"rgba(255,255,255,0.5)",fontWeight:700,whiteSpace:"nowrap"}}>Card #</th>
                      <th style={{padding:"10px 14px",color:"rgba(255,255,255,0.5)",fontWeight:700}}>Hero</th>
                      <th style={{padding:"10px 14px",color:"rgba(255,255,255,0.5)",fontWeight:700}}>Treatment</th>
                      <th style={{padding:"10px 14px",color:"rgba(255,255,255,0.5)",fontWeight:700}}>Weapon</th>
                      <th style={{padding:"10px 14px",color:"rgba(255,255,255,0.5)",fontWeight:700}}>Notation</th>
                      <th style={{padding:"10px 14px",color:"rgba(255,255,255,0.5)",fontWeight:700,textAlign:"right"}}>PWR</th>
                      <th style={{padding:"10px 14px",color:"rgba(255,255,255,0.5)",fontWeight:700}}>Athlete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCards.map(c=>{
                      const wc = WEAPON_COLORS[canonWeapon(c.weapon)] || "#888";
                      return (
                        <tr key={c.id} onClick={()=>setExpandedCard(c)} style={{borderTop:"1px solid rgba(255,255,255,0.05)",cursor:"pointer"}}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <td style={{padding:"10px 14px",color:"#ccc",fontWeight:700,whiteSpace:"nowrap"}}>{c.cardNum}</td>
                          <td style={{padding:"10px 14px"}}>
                            <div style={{color:"#fff",fontWeight:700}}>{c.hero}</div>
                            {c.inspiredBy && <div style={{color:"rgba(255,255,255,0.35)",fontSize:11}}>{c.variation||""}</div>}
                          </td>
                          <td style={{padding:"10px 14px",color:"rgba(255,255,255,0.6)"}}>{c.treatment||""}</td>
                          <td style={{padding:"10px 14px"}}>{c.weapon && <span style={{color:wc,background:wc+"22",borderRadius:5,padding:"2px 9px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>{c.weapon}</span>}</td>
                          <td style={{padding:"10px 14px",color:"#FBBF24",fontWeight:700}}>{c.notation||""}</td>
                          <td style={{padding:"10px 14px",color:"#fff",fontWeight:800,textAlign:"right"}}>{c.power||""}</td>
                          <td style={{padding:"10px 14px",color:"rgba(255,255,255,0.7)",whiteSpace:"nowrap"}}>{c.inspiredBy||c.athlete||""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
            <div className={animsOn?"pub-card-grid":"pub-card-grid no-anim"} style={{display:"grid",gridTemplateColumns:`repeat(auto-fill,minmax(${cardSize}px,1fr))`,gap:10}}>
              {visibleCards.map(c=>(
                <div key={c.id} style={{position:"relative"}}>
                  {/* Everything that pins to the ARTWORK lives in here. Without this the cell is the
                      positioning context, and since the cell now includes the name strip below the card,
                      a bottom-anchored badge (the privacy lock) landed on the quantity instead of the art. */}
                  <div style={{position:"relative", aspectRatio:"3/4"}}>
                  {selectMode && (
                    <div
                      onMouseDown={e=>{ e.preventDefault(); e.stopPropagation();
                        if (e.shiftKey) { selectRangeTo(c.id, visibleCards); return; }
                        setDragging(true); setDragAnchor(c.id); toggleSelect(c.id);
                      }}
                      onMouseEnter={()=>{ if (dragging) setSelectedIds(prev => prev.has(c.id) ? prev : new Set(prev).add(c.id)); }}
                      onTouchStart={e=>{ e.stopPropagation(); toggleSelect(c.id); setDragAnchor(c.id); }}
                      style={{position:"absolute",top:0,left:0,right:0,aspectRatio:"3/4",zIndex:30,borderRadius:10,cursor:"pointer",userSelect:"none",border:selectedIds.has(c.id)?"3px solid #7B9CFF":"3px solid transparent",background:selectedIds.has(c.id)?"rgba(123,156,255,0.18)":"rgba(0,0,0,0.15)",transition:"all 0.12s"}}>
                      <div style={{position:"absolute",top:8,left:8,width:26,height:26,borderRadius:"50%",background:selectedIds.has(c.id)?"#7B9CFF":"rgba(0,0,0,0.6)",border:"2px solid #fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:900,color:"#fff",boxShadow:"0 2px 6px rgba(0,0,0,0.5)"}}>{selectedIds.has(c.id)?"✓":""}</div>
                      {/* Way into a card WITHOUT leaving select mode. The overlay swallows normal clicks
                          (that is what makes drag-select work), so opening needs its own target that
                          stops the event the same way the quantity stepper does. Selection survives. */}
                      <button title="Open this card" aria-label="Open this card"
                        onMouseDown={e=>{ e.preventDefault(); e.stopPropagation(); }}
                        onTouchStart={e=>{ e.stopPropagation(); }}
                        onClick={e=>{ e.preventDefault(); e.stopPropagation(); setExpandedCard(c); }}
                        style={{position:"absolute",top:6,right:6,zIndex:6,width:26,height:26,borderRadius:"50%",
                          background:"rgba(0,0,0,0.66)",border:"1.5px solid rgba(255,255,255,0.55)",color:"#fff",
                          fontSize:13,fontWeight:900,cursor:"pointer",fontFamily:"inherit",lineHeight:1,padding:0,
                          display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(3px)",
                          boxShadow:"0 2px 6px rgba(0,0,0,0.5)"}}>
                        {"\u2197"}
                      </button>
                      {/* Quantity stepper, right on the card while selecting. Logging four of the same
                          card used to mean opening it, bumping the counter, backing out \u2014 per card.
                          stopPropagation everywhere so tapping \u2212/+ never toggles the selection. */}
                      {(() => {
                        // Only on cards you've actually ticked — showing a stepper on every owned card
                        // clutters the grid and invites changing quantities you never meant to touch.
                        if (!selectedIds.has(c.id)) return null;
                        // Selecting a card implies at least one copy, so a selected unowned card reads 1
                        // rather than 0. `actual` stays the stored value so the +/− handlers below can
                        // tell "displayed 1 because selected" apart from "really own 1".
                        const actual = parseInt(owned[c.id]) || 0;
                        const q = actual || 1;
                        const btn = {background:"rgba(0,0,0,0.75)",border:"1px solid rgba(255,255,255,0.3)",color:"#fff",width:24,height:24,borderRadius:6,fontSize:15,fontWeight:900,cursor:"pointer",fontFamily:"inherit",lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center",padding:0};
                        const stop = e => { e.preventDefault(); e.stopPropagation(); };
                        return (
                          <div onMouseDown={stop} onTouchStart={stop} onClick={stop}
                            style={{position:"absolute",top:"72%",left:"50%",transform:"translate(-50%,-50%)",display:"flex",alignItems:"center",gap:6,background:"rgba(0,0,0,0.72)",border:"1px solid rgba(255,255,255,0.18)",borderRadius:10,padding:"5px 7px",backdropFilter:"blur(4px)",boxShadow:"0 3px 10px rgba(0,0,0,0.5)"}}>
                            <button onMouseDown={stop} onTouchStart={stop}
                              onClick={e=>{ stop(e);
                                // Stepping below 1 on an unowned card means "I don't want this after all",
                                // so drop the selection instead of leaving it ticked at 0.
                                if (q <= 1) { if (actual) setOwnedQty(c.id, 0); toggleSelect(c.id); }
                                else setOwnedQty(c.id, q-1);
                              }} style={btn}>{"\u2212"}</button>
                            <span title={actual ? `You own ${actual}` : "Not owned yet — selecting implies 1"}
                              style={{minWidth:22,textAlign:"center",fontSize:14,fontWeight:900,
                                color: actual ? "#4ade80" : "rgba(255,255,255,0.45)",
                                fontStyle: actual ? "normal" : "italic"}}>{q}</span>
                            <button onMouseDown={stop} onTouchStart={stop}
                              onClick={e=>{ stop(e);
                                // The displayed 1 on an unowned card is a PROMISE, not a stored value:
                                // owned is still 0 until Mark Owned runs. So the first + must commit
                                // that promised 1 rather than jumping straight to 2.
                                setOwnedQty(c.id, actual ? actual+1 : 1);
                              }} style={btn}>+</button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  <BobaCard c={c} isOwned={!!owned[c.id]} ownedQty={owned[c.id]||0} isBorrowed={(borrowedByCard[c.id]||0) > 0}
                    flippedCard={flippedCard} setFlippedCard={setFlippedCard} onExpand={setExpandedCard}
                    toggleOwned={()=>{if(!user){setSigningIn(true);return;} toggleOwned(c.id);}}
                    setOwnedQty={(id,qty)=>setOwnedQty(id,qty)}
                    toggleWant={()=>toggleWant(c.id)} wantList={wantList} WEAPON_COLORS={PUBLIC_WEAPON_COLORS}
                    onComp={c=>setCompCard(c)} onLotEdit={user?()=>setLotModal({card:c}):null} lotCount={lotCountByCard[c.id]||0} myScanPhoto={scanPhotoByCard[c.id]} onCardActivity={resetFlipTimer} isAdmin={_cardAdmin} onImageUpload={handleCardImageUpload} onImageClear={handleCardImageClear} kidGroups={kidGroups} kidTags={kidTagsByCard[c.id]} onAssignKid={assignCardToKid} onSell={user?()=>setSellModal(c):null} foundInMap={foundInMap}
                    lentCount={lentByCard[c.id]||0} lentWho={lentWhoByCard[c.id]?.who||""}
                    onLend={user?()=>{const who=window.prompt("Lend "+c.hero+" to whom?");if(who&&who.trim())markCardLent(c.id,who);}:null}
                    onReturn={user?()=>markCardReturned(c.id):null}/>
                  {/* Lock animation overlay */}
                  {privacyAnim===c.id&&(
                    <div style={{position:"absolute",inset:0,borderRadius:10,zIndex:20,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",pointerEvents:"none",animation:"lockFadeOut 1.2s ease forwards",background:"rgba(0,0,0,0.55)"}}>
                      <div style={{fontSize:48,animation:"lockPulse 0.6s cubic-bezier(0.34,1.56,0.64,1) forwards",lineHeight:1,marginBottom:6}}>
                        {publicCards[c.id]?"\uD83D\uDC41":"\uD83D\uDD12"}
                      </div>
                      <div style={{fontSize:11,fontWeight:800,color:"#fff",textAlign:"center",textShadow:"0 2px 8px rgba(0,0,0,0.8)",letterSpacing:0.5}}>
                        {publicCards[c.id]?"Public — visible on your profile":"Private — only you can see"}
                      </div>
                    </div>
                  )}
                  {/* Owned cards are private by default — subtle dim + lock until made public */}
                  {/* Private used to throw a 28% black wash over the whole card. Private is the DEFAULT,
                      so in practice that dimmed a user's entire collection \u2014 the cards they worked for
                      looked switched off. Convey it with a small lock instead and leave the art alone. */}
                  {/* Anchored to the card wrapper above, not the grid cell — the cell now includes the
                      name strip, so bottom:6 was landing on the quantity instead of the artwork. */}
                  {owned[c.id]&&!publicCards[c.id]&&privacyAnim!==c.id&&(
                    <div title="Private \u2014 only you can see this" style={{position:"absolute",left:6,bottom:6,zIndex:5,pointerEvents:"none",width:19,height:19,borderRadius:"50%",background:"rgba(0,0,0,0.55)",backdropFilter:"blur(3px)",border:"1px solid rgba(251,191,36,0.5)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9.5,lineHeight:1}}>
                      {"\uD83D\uDD12"}
                    </div>
                  )}
                  {/* In-transit indicator stays on the tile (at-a-glance, avoid double-buying).
                      Public/private toggle + list-for-sale moved to the expanded card modal. */}
                  {owned[c.id]&&inTransit[c.id]&&(
                    <div style={{position:"absolute",top:6,left:6,zIndex:10}}>
                      <div title={`${inTransit[c.id].qty||1} on the way — don't double-buy`} style={{background:"rgba(96,165,250,0.92)",borderRadius:6,padding:"3px 6px",fontSize:11,color:"#fff",fontWeight:800,backdropFilter:"blur(4px)"}}>🚚{inTransit[c.id].qty>1?inTransit[c.id].qty:""}</div>
                    </div>
                  )}
                  </div>
                  {/* Name BELOW the card rather than over it. Some art is blank or fails to load, which
                      left tiles you could not identify without opening them. Putting it outside the
                      card means nothing covers the artwork and the foil/hover effects are untouched.
                      The select overlay above is inset:0 on the CARD, so this strip stays clickable
                      and readable in select mode too. */}
                  {/* pointerEvents:none keeps the strip from stealing clicks meant for the card, but the
                      + button inside re-enables them on itself. */}
                  <div style={{ padding:"5px 2px 0", pointerEvents:"none" }}>
                    <div style={{ display:"flex", alignItems:"baseline", gap:5 }}>
                      <div style={{ fontSize:11, fontWeight:800, color:owned[c.id]?"#4ade80":"var(--bz-ink)",
                        lineHeight:1.2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", flex:1, minWidth:0 }}>
                        {c.hero || c.playName || "\u2014"}
                      </div>
                      {/* Borrowed marker. A borrowed card sits in the grid so you can see and deck it,
                          but it is not yours \u2014 without a tag it is indistinguishable from a card you
                          own, which is exactly the confusion this whole feature exists to prevent. */}
                      {(borrowedByCard[c.id]||0) > 0 && (
                        <span title="Borrowed \u2014 not yours" style={{ fontSize:9.5, fontWeight:900, color:"#7B9CFF",
                          background:"rgba(123,156,255,0.15)", border:"1px solid rgba(123,156,255,0.45)",
                          borderRadius:5, padding:"0 4px", lineHeight:1.5, flexShrink:0 }}>
                          {"\uD83D\uDCE5"} BORROWED
                        </span>
                      )}
                      {/* Count lives here now instead of on the artwork. Shown from 1 upward: it sits
                          beside the + button, so hiding it at exactly one copy left the row looking
                          like nothing was owned right after you added a card. */}
                      {(parseInt(owned[c.id])||0) >= 1 && (()=>{
                        // Split the count into what is yours and what you are holding for someone
                        // else. A flat green "x1" on a borrowed card is the single most misleading
                        // thing on the grid \u2014 it is the same badge an owned card gets.
                        const total = parseInt(owned[c.id]) || 0;
                        const bor   = borrowedByCard[c.id] || 0;
                        const mine  = Math.max(0, total - bor);
                        const col   = mine > 0 ? "#4ade80" : "#7B9CFF";
                        const label = mine > 0 && bor > 0 ? `${mine}+${bor}` : String(total);
                        return (
                          <span title={mine>0 && bor>0 ? `${mine} yours, ${bor} borrowed`
                                     : bor>0 ? `${bor} borrowed \u2014 not yours` : `You own ${total}`}
                            style={{ fontSize:10, fontWeight:900, color:col,
                              background: mine>0 ? "rgba(74,222,128,0.13)" : "rgba(123,156,255,0.15)",
                              border:"1px solid " + (mine>0 ? "rgba(74,222,128,0.4)" : "rgba(123,156,255,0.5)"),
                              borderRadius:5, padding:"0 4px", lineHeight:1.5, flexShrink:0 }}>
                            {"\u00d7"}{label}
                          </span>
                        );
                      })()}
                      {/* Quick-add lives here, off the artwork. On the card it had to vanish once you
                          owned a copy (it sat where the count needed to go), which meant adding a
                          second copy required opening the card. Down here it can simply stay. */}
                      {user && (
                        <button onClick={e=>{ e.stopPropagation(); setOwnedQty(c.id, (parseInt(owned[c.id])||0) + 1); }}
                          title={owned[c.id] ? "Add another copy" : "Add to your collection"}
                          style={{ pointerEvents:"auto", flexShrink:0, cursor:"pointer", fontFamily:"inherit",
                            width:17, height:17, borderRadius:5, lineHeight:1, fontSize:12, fontWeight:900,
                            display:"flex", alignItems:"center", justifyContent:"center",
                            background: owned[c.id] ? "rgba(74,222,128,0.13)" : "rgba(255,255,255,0.06)",
                            border: "1px solid " + (owned[c.id] ? "rgba(74,222,128,0.4)" : "var(--bz-line-2)"),
                            color: owned[c.id] ? "#4ade80" : "var(--bz-ink-2)", padding:0 }}>
                          +
                        </button>
                      )}
                    </div>
                    {(c.treatment || c.cardNum) && (
                      <div style={{ fontSize:9, fontWeight:600, color:"var(--bz-ink-3)", lineHeight:1.25,
                        whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", marginTop:1 }}>
                        {[c.treatment, c.cardNum].filter(Boolean).join(" \u00b7 ")}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            )}
            {/* Display cap — we deliberately stop painting past MAX_PAGES. Mounting all 35k cards
                is what made a render take ~6 SECONDS. Say so plainly rather than silently stopping. */}
            {page >= MAX_PAGES && sorted.length > visibleCards.length && (() => {
              const remaining = sorted.length - visibleCards.length;
              const nextChunk = Math.min(remaining, 6 * PAGE_SIZE);
              return (
                <div style={{marginTop:18,padding:"16px",background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.22)",borderRadius:12,textAlign:"center"}}>
                  <div style={{fontSize:12.5,fontWeight:800,color:"#FBBF24",marginBottom:4}}>
                    Showing the first {visibleCards.length.toLocaleString()} of {sorted.length.toLocaleString()} cards
                  </div>
                  <div style={{fontSize:11.5,color:"rgba(255,255,255,0.45)",lineHeight:1.6,marginBottom:12,maxWidth:400,margin:"0 auto 12px"}}>
                    Filtering is faster, but you can keep going {"\u2014"} the page just gets heavier the more it paints.
                  </div>
                  <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
                    <button onClick={()=>{ setMaxPages(m => m + 6); setPage(p => p + 6); }}
                      style={{background:"rgba(251,191,36,0.15)",border:"1px solid #FBBF24",color:"#FBBF24",borderRadius:9,padding:"9px 16px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                      Load {nextChunk.toLocaleString()} more
                    </button>
                    {remaining > 6 * PAGE_SIZE && (
                      <button onClick={()=>{ const all = Math.ceil(sorted.length / PAGE_SIZE); setMaxPages(all); setPage(all); }}
                        title="This will be slow with a lot of cards"
                        style={{background:"transparent",border:"1px solid rgba(255,255,255,0.15)",color:"rgba(255,255,255,0.5)",borderRadius:9,padding:"9px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                        Show all {sorted.length.toLocaleString()}
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
            {/* First run. A new user has to pull the full ~12MB card list once, which can take a
                while on a phone. With no feedback at all a blank screen just reads as broken — so
                say what's happening, and say it only happens once. */}
            {loading && cards.length===0 && (
              <div style={{textAlign:"center",padding:"70px 20px"}}>
                <div style={{width:38,height:38,margin:"0 auto 18px",border:"3px solid rgba(255,255,255,0.1)",borderTopColor:"#E8317A",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
                <div style={{fontSize:15,fontWeight:800,color:"#fff",marginBottom:7}}>Setting up your card database{"\u2026"}</div>
                <div style={{fontSize:12.5,color:"rgba(255,255,255,0.4)",lineHeight:1.65,maxWidth:330,margin:"0 auto"}}>
                  Downloading every BoBA card {"\u2014"} all 35,000 of them. This only happens once;
                  after this it loads instantly.
                </div>
              </div>
            )}
            {/* Loading with a cache present — quieter, since they already have cards on screen. */}
            {loading && cards.length>0 && (
              <div style={{textAlign:"center",padding:"14px 20px",fontSize:11.5,color:"rgba(255,255,255,0.3)"}}>
                {"\u21BB"} Checking for new cards{"\u2026"}
              </div>
            )}

            {cards.length===0 && !loading && (
              <div style={{textAlign:"center",padding:"60px 20px",color:"rgba(255,255,255,0.5)"}}>
                <div style={{fontSize:40,marginBottom:14}}>📭</div>
                <div style={{fontSize:15,fontWeight:700,marginBottom:8}}>Cards didn't load</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.35)",marginBottom:18,lineHeight:1.6}}>This can happen on a slow or spotty connection.</div>
                <button onClick={async ()=>{ try{localStorage.removeItem("boba_checklist_cache_v3");}catch(e){} await idbClearCards(); window.location.reload(); }}
                  style={{background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:12,padding:"12px 28px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 20px rgba(232,49,122,0.4)"}}>
                  🔄 Reload Cards
                </button>
              </div>
            )}
            {cards.length>0 && filtered.length===0 && (
              <div style={{textAlign:"center",padding:"60px 20px",color:"rgba(255,255,255,0.4)"}}>
                <div style={{fontSize:32,marginBottom:12}}>🔍</div>
                <div style={{fontSize:14,fontWeight:700}}>No cards match your filters</div>
              </div>
            )}
            {visibleCards.length<filtered.length&&<div style={{textAlign:"center",padding:32,color:"rgba(255,255,255,0.2)",fontSize:12}}>Scroll to load more...</div>}
          </>
        )}

        {/* RAINBOW TRACKER TAB */}
        {activeTab==="rainbow" && !loading && cards.length > 0 && (() => {
          // Heavy work is memoized above; only the cheap per-keystroke filtering happens here.
          const { groupStats, completedRainbows, partialRainbows, availableSets, allGroups, rainbowCards, groupKeyOf } = rainbowStats || { groupStats:[], completedRainbows:0, partialRainbows:0, availableSets:[], allGroups:[], rainbowCards:[], groupKeyOf:()=>"" };
          const searchPlaceholder = rainbowGroupBy === "hero" ? "Search hero..." : rainbowGroupBy === "treatmentWeapon" ? "Search treatment + weapon..." : rainbowGroupBy === "custom" ? "🔍 Search hero..." : "Search treatment...";
          const filteredGroups = groupStats.filter(g => !search || g.key.toLowerCase().includes(search.toLowerCase()));
          const visibleGroups = filteredGroups.filter(g => {
            if(rainbowFilter === "complete") return g.complete;
            if(rainbowFilter === "partial")  return g.ownedCount > 0 && !g.complete;
            if(rainbowFilter === "missing")  return g.ownedCount === 0;
            return true;
          });

          return (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {/* Summary KPIs */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
                {[
                  { l:"\uD83C\uDF08 Complete", v:completedRainbows, c:"#4ade80" },
                  { l:"\uD83D\uDD36 In Progress", v:partialRainbows, c:"#FBBF24" },
                  { l:"\u2B1C Not Started", v:allGroups.length-completedRainbows-partialRainbows, c:"rgba(255,255,255,0.2)" },
                ].map(({l,v,c})=>(
                  <div key={l} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:12, padding:"14px 16px", textAlign:"center", backdropFilter:"blur(10px)" }}>
                    <div style={{ fontSize:28, fontWeight:900, color:c }}>{v}</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", marginTop:4 }}>{l}</div>
                  </div>
                ))}
              </div>

              {/* Group-by toggle + Search + Filters */}
              <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                {/* Group-by toggle */}
                <div style={{ display:"flex", background:"rgba(255,255,255,0.04)", borderRadius:20, padding:3, gap:2 }}>
                  {[["hero","\uD83E\uDDB8 By Hero"],["treatment","\uD83C\uDFA8 By Treatment"],["treatmentWeapon","\u2694\uFE0F Treatment + Weapon"],["custom","\uD83C\uDFAF Custom"]].map(([v,l])=>(
                    <button key={v} onClick={()=>{ setRainbowGroupBy(v); setExpandedHero(null); setSearch(""); }} style={{ background:rainbowGroupBy===v?"rgba(232,49,122,0.9)":"transparent", color:rainbowGroupBy===v?"#fff":"rgba(255,255,255,0.4)", border:"none", borderRadius:17, padding:"6px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", transition:"all 0.2s" }}>{l}</button>
                  ))}
                </div>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={searchPlaceholder} style={{...inp, flex:1, minWidth:140}}/>
                {availableSets.length > 0 && (
                  <select value={rainbowSetFilter} onChange={e=>setRainbowSetFilter(e.target.value)} style={{...inp, width:"auto", fontSize:11, padding:"5px 10px", cursor:"pointer"}}>
                    <option value="">{"\uD83C\uDF08 All Sets"}</option>
                    {availableSets.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                {[["all","All"],["complete","\uD83C\uDF08 Complete"],["partial","\uD83D\uDD36 In Progress"],["missing","\u2B1C Not Started"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setRainbowFilter(v)} style={{ background:rainbowFilter===v?"rgba(232,49,122,0.15)":"transparent", color:rainbowFilter===v?"#E8317A":"rgba(255,255,255,0.4)", border:`1.5px solid ${rainbowFilter===v?"#E8317A":"rgba(255,255,255,0.08)"}`, borderRadius:20, padding:"6px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", transition:"all 0.2s" }}>{l}</button>
                ))}
                <span style={{ fontSize:11, color:"rgba(255,255,255,0.2)" }}>{visibleGroups.length} {rainbowGroupBy === "hero" ? "heroes" : rainbowGroupBy === "treatmentWeapon" ? "rainbows" : "treatments"}</span>
              </div>

              {/* Custom tracker mode: one-per-hero in chosen treatment(s) */}
              {rainbowGroupBy === "custom" && (() => {
                const allTreatments = [...new Set(cards.map(c=>c.treatment).filter(Boolean))].sort();
                const active = customTrackers.find(t => t.id === activeTrackerId);
                const renderTracker = (tracker) => {
                  const treatSet = new Set(tracker.treatments);
                  // Match treatments LOOSELY: ignore case, spacing and punctuation so a rename like
                  // "Gold Coinflip*" vs "Gold Coin Flip Battlefoil" still counts your owned cards.
                  const _normT = s => String(s||"").toLowerCase().replace(/[^a-z0-9]/g,"");
                  const treatSetN = new Set([...treatSet].map(_normT));
                  const treatMatch = c => treatSet.has(c.treatment) || treatSetN.has(_normT(c.treatment));
                  const heroes = [...new Set(cards.filter(c=>c.hero).map(c=>c.hero))].sort();
                  const rows = heroes.map(hero => {
                    const cardNumVal = c => { const m = String(c.cardNum||"").match(/\d+/); return m ? parseInt(m[0],10) : Number.MAX_SAFE_INTEGER; };
                    const hc = cards.filter(c => c.hero===hero && treatMatch(c))
                      .sort((a,b)=>{ const d = cardNumVal(a)-cardNumVal(b); return d!==0 ? d : String(a.cardNum||"").localeCompare(String(b.cardNum||""),undefined,{numeric:true}); });
                    const ownedMatch = hc.filter(c => owned[c.id]);
                    const transitMatch = hc.filter(c => !owned[c.id] && inTransit[c.id]);
                    return { hero, need: hc.length, complete: ownedMatch.length>0, ownedCards: ownedMatch, transitCards: transitMatch, hasTransit: transitMatch.length>0, allCards: hc, exists: hc.length>0 };
                  });
                  const inSet = rows.filter(r=>r.exists);
                  const done = inSet.filter(r=>r.complete).length;
                  const transitCount = inSet.filter(r=>!r.complete && r.hasTransit).length;
                  const pct = inSet.length ? Math.round(done/inSet.length*100) : 0;
                  const q = (search||"").toLowerCase();
                  const shown = inSet.filter(r => {
                    if (q && !r.hero.toLowerCase().includes(q)) return false;
                    if (trackerStatusFilter==="owned"   && !r.complete) return false;
                    if (trackerStatusFilter==="transit" && !(r.hasTransit && !r.complete)) return false;
                    if (trackerStatusFilter==="missing" && (r.complete || r.hasTransit)) return false;
                    return true;
                  });
                  const missingCount = inSet.filter(r=>!r.complete && !r.hasTransit).length;
    // ── Orphan check ─────────────────────────────────────────────────────────────────────────
    // A tracker matches cards by EXACT treatment string. If a snapshot regeneration renamed a
    // treatment, cards you own quietly stop matching — they leave the tracker and take their
    // have/in-transit status with them. Nothing is deleted; the marks still exist on cards that no
    // longer match. This finds them: cards you own or have in transit, whose treatment is CLOSE to
    // one the tracker wants but not exact.
    const orphans = (() => { try {
      const wanted = tracker.treatments || [];
      const wantedLc = wanted.map(t => (t||"").toLowerCase().replace(/\s+/g," ").trim());
      const out = [];
      cards.forEach(c => {
        if (!owned[c.id] && !inTransit[c.id]) return;             // only YOUR cards
        const t = c.treatment || "";
        if (treatMatch(c)) return;                              // already matching — fine
        const lc = t.toLowerCase().replace(/\s+/g," ").trim();
        // Near-miss = the SAME treatment differing only by case / spacing / punctuation. We do NOT
        // treat a substring as a match ("Battlefoil" is NOT "Gold Coin Flip Battlefoil"), which was
        // pulling in unrelated cards. Compare with every separator stripped.
        const norm = s => s.toLowerCase().replace(/[^a-z0-9]/g,"");
        const lcN = norm(lc);
        const near = wantedLc.some(w => norm(w) === lcN);
        if (near) out.push({ hero:c.hero, treatment:t, owned:!!owned[c.id], transit:!!inTransit[c.id] });
      });
      return out;
    } catch(e){ console.error("orphan check failed:", e); return []; } })();

                  return (
                    <div>
                        {orphans.length > 0 && (
                        <div style={{background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.35)",borderRadius:12,padding:"12px 15px",marginBottom:14}}>
                        <div style={{fontSize:13,fontWeight:800,color:"#FBBF24",marginBottom:5}}>
                        {"\u26A0\uFE0F"} {orphans.length} of your card{orphans.length===1?"":"s"} {orphans.length===1?"is":"are"} marked but not matching this tracker
                        </div>
                        <div style={{fontSize:11.5,color:"rgba(255,255,255,0.6)",lineHeight:1.6,marginBottom:8}}>
                        These are cards you own or have in transit whose treatment name drifted (likely a database
                        change), so the tracker stopped counting them. Your marks are safe {"\u2014"} they just need the
                        treatment re-matched.
                        </div>
                        <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                        {[...new Set(orphans.map(o=>o.treatment))].map(t => (
                        <span key={t} style={{fontSize:10.5,fontWeight:700,color:"#FBBF24",background:"rgba(251,191,36,0.12)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:6,padding:"2px 8px"}}>
                        {t} ({orphans.filter(o=>o.treatment===t).length})
                        </span>
                        ))}
                        </div>
                        <button onClick={()=>{
                        // Add every drifted treatment to this tracker so the cards match again. This edits the
                        // tracker's treatment list only — it does NOT touch your owned or in-transit marks.
                        const add = [...new Set(orphans.map(o=>o.treatment))];
                        const merged = [...new Set([...(tracker.treatments||[]), ...add])];
                        saveTrackerTreatments(tracker.id, merged);
                        }} style={{marginTop:10,background:"linear-gradient(135deg,#E8317A,#7B2FF7)",color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
                        Re-match {orphans.length} card{orphans.length===1?"":"s"} to this tracker
                        </button>
                        </div>
                        )}
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, flexWrap:"wrap", gap:8 }}>
                        <div>
                          <div style={{ fontSize:16, fontWeight:900 }}>{tracker.name}</div>
                          <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>{tracker.treatments.join(" · ")} · one per hero · tap a hero to see every card</div>
                          {transitCount>0 && <div style={{ fontSize:11, color:"#FBBF24", fontWeight:700, marginTop:3 }}>🚚 {transitCount} on the way <span style={{ color:"rgba(255,255,255,0.35)", fontWeight:400 }}>(not counted as owned until received)</span></div>}
                        </div>
                        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                          <span style={{ fontSize:20, fontWeight:900, color: done===inSet.length && inSet.length>0 ? "#4ade80" : "#FBBF24" }}>{done}/{inSet.length}</span>
                          <button onClick={()=>toggleTrackerPublic(tracker.id)} title={tracker.public?"Public — shows on your profile. Click to make private.":"Private. Click to make public on your profile."} style={{ background:tracker.public?"rgba(74,222,128,0.15)":"transparent", border:`1px solid ${tracker.public?"rgba(74,222,128,0.5)":"rgba(255,255,255,0.15)"}`, color:tracker.public?"#4ade80":"rgba(255,255,255,0.5)", borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{tracker.public?"👁 Public":"🔒 Private"}</button>
                          <button onClick={()=>{ setBuilderName(tracker.name); setBuilderTreatments(tracker.treatments); setActiveTrackerId(tracker.id); setShowTrackerBuilder("edit"); }} style={{ background:"transparent", border:"1px solid rgba(255,255,255,0.15)", color:"rgba(255,255,255,0.5)", borderRadius:6, padding:"4px 10px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Edit</button>
                          <button onClick={()=>deleteCustomTracker(tracker.id)} style={{ background:"transparent", border:"1px solid rgba(232,49,122,0.3)", color:"#E8317A", borderRadius:6, padding:"4px 10px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Delete</button>
                        </div>
                      </div>
                      <div style={{ height:8, background:"rgba(255,255,255,0.06)", borderRadius:4, overflow:"hidden", marginBottom:14 }}>
                        <div className="bz-rainbow-fill" style={{ width:`${pct}%`, height:"100%", transition:"width .3s" }}/>
                      </div>
                      <div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" }}>
                        {[
                          ["all", `All (${inSet.length})`, "#f6eef2"],
                          ["owned", `✅ Have (${done})`, "#4ade80"],
                          ["transit", `🚚 On the way (${transitCount})`, "#FBBF24"],
                          ["missing", `❌ Missing (${missingCount})`, "#E8317A"],
                        ].map(([key,label,col]) => (
                          <button key={key} onClick={()=>setTrackerStatusFilter(key)} style={{ background: trackerStatusFilter===key ? col : "transparent", border:`1px solid ${trackerStatusFilter===key ? col : "rgba(255,255,255,0.15)"}`, color: trackerStatusFilter===key ? (key==="all"||key==="owned"||key==="transit"?"#0b0709":"#fff") : "rgba(255,255,255,0.6)", borderRadius:20, padding:"5px 13px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit", transition:"all .15s" }}>{label}</button>
                        ))}
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))", gap:10 }}>
                        {shown.length===0 && <div style={{ gridColumn:"1 / -1", textAlign:"center", padding:"30px 10px", color:"rgba(255,255,255,0.4)", fontSize:13 }}>{trackerStatusFilter==="missing"?"🎉 Nothing missing in this view!":trackerStatusFilter==="transit"?"Nothing on the way right now.":trackerStatusFilter==="owned"?"None owned yet in this set.":"No heroes match."}</div>}
                        {shown.map(r => {
                          const isExp = expandedTrackerHero === r.hero;
                          // Representative card for the tile: prefer an owned one, else first
                          const rep = r.ownedCards[0] || r.allCards[0];
                          return (
                            <div key={r.hero} style={{ gridColumn: isExp ? "1 / -1" : "auto" }}>
                              {!isExp ? (
                                <div onClick={()=>{ const start = (r.ownedCards[0] || r.allCards[0]); if(start && start.imageUrl){ setZoomFlipped(false); setZoomCard({ card:start, siblings:r.allCards, hero:r.hero }); } }} style={{ cursor:"pointer", background:r.complete?"rgba(74,222,128,0.06)":r.hasTransit?"rgba(251,191,36,0.08)":"rgba(255,255,255,0.02)", border:`1.5px solid ${r.complete?"rgba(74,222,128,0.45)":r.hasTransit?"rgba(251,191,36,0.6)":"rgba(255,255,255,0.06)"}`, borderRadius:12, overflow:"hidden" }}>
                                  <div style={{ position:"relative", aspectRatio:"3/4", background:"rgba(0,0,0,0.4)" }}>
                                    {rep?.imageUrl
                                      ? <img src={rep.imageUrl} alt={r.hero} style={{ width:"100%", height:"100%", objectFit:"cover", opacity:r.complete?1:r.hasTransit?0.8:0.72, filter:r.complete?"none":r.hasTransit?"grayscale(30%) sepia(25%)":"grayscale(55%) brightness(0.9)" }}/>
                                      : <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontSize:11, color:"rgba(255,255,255,0.4)", padding:6, textAlign:"center" }}>{r.hero}</div>}
                                    <div style={{ position:"absolute", top:6, right:6, background:r.complete?"rgba(74,222,128,0.9)":r.hasTransit?"rgba(251,191,36,0.95)":"rgba(0,0,0,0.7)", color:r.hasTransit&&!r.complete?"#000":"#fff", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:800 }}>{r.complete?"✅":r.hasTransit?"🚚":"—"}</div>
                                    <div style={{ position:"absolute", bottom:0, left:0, right:0, background:"linear-gradient(transparent,rgba(0,0,0,0.85))", padding:"14px 8px 6px" }}>
                                      <div style={{ fontSize:11, fontWeight:800, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.hero}</div>
                                      <div style={{ fontSize:9, color:r.complete?"#4ade80":r.hasTransit?"#FBBF24":"rgba(255,255,255,0.4)" }}>{r.ownedCards.length}/{r.need} owned{r.hasTransit?` · ${r.transitCards.length} 🚚`:""}</div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ background:"rgba(255,255,255,0.03)", border:"1.5px solid rgba(232,49,122,0.3)", borderRadius:14, padding:"14px 16px", marginBottom:4 }}>
                                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                                    <div style={{ fontSize:15, fontWeight:900, color:"#fff" }}>{r.complete?"✅ ":""}{r.hero} <span style={{ fontSize:12, fontWeight:600, color:r.complete?"#4ade80":"rgba(255,255,255,0.4)" }}>· {r.ownedCards.length}/{r.need} owned</span></div>
                                    <button onClick={()=>setExpandedTrackerHero(null)} style={{ background:"transparent", border:"1px solid rgba(255,255,255,0.15)", color:"rgba(255,255,255,0.5)", borderRadius:6, padding:"3px 10px", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>✕ Close</button>
                                  </div>
                                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))", gap:10 }}>
                                    {r.allCards.map(c => {
                                      const have = !!owned[c.id];
                                      return (
                                        <div key={c.id} onClick={()=>c.imageUrl&&setZoomCard({ card:c, siblings:r.allCards, hero:r.hero })} style={{ background:have?"rgba(74,222,128,0.08)":"rgba(0,0,0,0.4)", border:`1.5px solid ${have?"rgba(74,222,128,0.5)":"rgba(255,255,255,0.08)"}`, borderRadius:12, overflow:"hidden", cursor:c.imageUrl?"zoom-in":"default" }}>
                                          <div style={{ position:"relative", aspectRatio:"3/4" }}>
                                            {c.imageUrl
                                              ? <img src={c.imageUrl} alt={c.hero} style={{ width:"100%", height:"100%", objectFit:"cover", opacity:have?1:0.72, filter:have?"drop-shadow(0 0 10px rgba(74,222,128,0.4))":"grayscale(55%) brightness(0.9)" }}/>
                                              : <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontSize:10, color:"rgba(255,255,255,0.4)", padding:6, textAlign:"center" }}>{[c.weapon,c.cardNum?"#"+c.cardNum:""].filter(Boolean).join(" ")}</div>}
                                            <div style={{ position:"absolute", top:5, right:5, background:have?"rgba(74,222,128,0.9)":"rgba(0,0,0,0.75)", color:"#fff", borderRadius:20, padding:"1px 7px", fontSize:9, fontWeight:800 }}>{have?"✓ Have":"Missing"}</div>
                                          </div>
                                          <div style={{ padding:"6px 8px", fontSize:10, fontWeight:700, color:have?"#fff":"rgba(255,255,255,0.4)", textAlign:"center" }}>{c.weapon||"—"}{c.cardNum?` #${c.cardNum}`:""}</div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                };
                return (
                  <div>
                    {showTrackerBuilder ? (
                      <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(232,49,122,0.3)", borderRadius:12, padding:"18px 20px", marginBottom:16 }}>
                        <div style={{ fontSize:14, fontWeight:800, marginBottom:12 }}>{showTrackerBuilder==="edit"?"Edit Tracker":"New Custom Tracker"}</div>
                        <input value={builderName} onChange={e=>setBuilderName(e.target.value)} placeholder="Tracker name (e.g. Gold Coin — One Per Hero)" style={{...inp, width:"100%", marginBottom:12}}/>
                        <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Treatments to count (pick one or more)</div>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6, maxHeight:220, overflowY:"auto", marginBottom:14 }}>
                          {allTreatments.map(t => { const sel = builderTreatments.includes(t); return (
                            <button key={t} onClick={()=>setBuilderTreatments(sel?builderTreatments.filter(x=>x!==t):[...builderTreatments,t])} style={{ background:sel?"#E8317A":"transparent", color:sel?"#fff":"rgba(255,255,255,0.5)", border:`1px solid ${sel?"#E8317A":"rgba(255,255,255,0.15)"}`, borderRadius:20, padding:"4px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{sel?"✓ ":""}{t}</button>
                          ); })}
                        </div>
                        <div style={{ display:"flex", gap:8 }}>
                          <button onClick={saveCustomTracker} disabled={!builderName.trim()||builderTreatments.length===0} style={{ background:(!builderName.trim()||builderTreatments.length===0)?"rgba(255,255,255,0.1)":"#E8317A", color:"#fff", border:"none", borderRadius:8, padding:"8px 18px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>Save Tracker</button>
                          <button onClick={()=>{ setShowTrackerBuilder(false); setBuilderName(""); setBuilderTreatments([]); }} style={{ background:"transparent", border:"1px solid rgba(255,255,255,0.15)", color:"rgba(255,255,255,0.5)", borderRadius:8, padding:"8px 18px", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:16, alignItems:"center" }}>
                        {customTrackers.map(t => (
                          <button key={t.id} onClick={()=>setActiveTrackerId(t.id)} style={{ background:activeTrackerId===t.id?"rgba(232,49,122,0.15)":"rgba(255,255,255,0.03)", color:activeTrackerId===t.id?"#E8317A":"rgba(255,255,255,0.5)", border:`1.5px solid ${activeTrackerId===t.id?"#E8317A":"rgba(255,255,255,0.1)"}`, borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{t.name}</button>
                        ))}
                        <button onClick={()=>{ setShowTrackerBuilder("new"); setBuilderName(""); setBuilderTreatments([]); setActiveTrackerId(null); }} style={{ background:"transparent", border:"1.5px dashed #E8317A", color:"#E8317A", borderRadius:8, padding:"6px 14px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit" }}>+ New Tracker</button>
                    {(user?.email||"").includes("@bazookabreaks.com") && user && (
                      <div style={{width:"100%",marginBottom:16}}>
                        <OwnedIntegrityCheck uid={user.uid} label="my collection" cards={cards}/>
                      </div>
                    )}
                      </div>
                    )}
                    {!showTrackerBuilder && (
                      active ? renderTracker(active)
                        : customTrackers.length === 0
                          ? <div style={{ textAlign:"center", padding:"50px 20px", color:"rgba(255,255,255,0.4)", fontSize:13 }}>Build a custom tracker to chase one hero per hero in any treatment — like a gold coin of every hero.</div>
                          : <div style={{ textAlign:"center", padding:"40px", color:"rgba(255,255,255,0.4)", fontSize:13 }}>Pick a tracker above to view your progress.</div>
                    )}
                  </div>
                );
              })()}

              {/* Rows */}
              {rainbowGroupBy !== "custom" && (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {visibleGroups.map(({ key, total, ownedCount, complete, bySecondary }) => {
                  const pct = total > 0 ? Math.round(ownedCount/total*100) : 0;
                  const isExpanded = expandedHero === key;
                  const groupCardList = rainbowCards.filter(c => groupKeyOf(c) === key)
                    .sort((a,b) => {
                      if(rainbowGroupBy === "hero") {
                        const WEAPONS = ["Fire","Ice","Steel","Brawl","Glow","Hex","Gum","Super","Alt","Metallic"];
                        const wa = WEAPONS.indexOf(a.weapon), wb = WEAPONS.indexOf(b.weapon);
                        if(wa !== wb) return wa - wb;
                        return (a.treatment||"").localeCompare(b.treatment||"");
                      }
                      if(rainbowGroupBy === "treatmentWeapon") {
                        return (a.hero||"").localeCompare(b.hero||"");
                      }
                      return (a.hero||"").localeCompare(b.hero||"");
                    });
                  const showSecondary = Object.keys(bySecondary).length > 1 && !rainbowSetFilter;
                  return (
                    <div key={key} style={{ background:"rgba(255,255,255,0.02)", border:`1.5px solid ${complete?"rgba(74,222,128,0.2)":ownedCount>0?"rgba(251,191,36,0.15)":"rgba(255,255,255,0.05)"}`, borderRadius:12, overflow:"hidden", backdropFilter:"blur(10px)" }}>
                      <div onClick={()=>setExpandedHero(isExpanded ? null : key)} style={{ padding:"14px 16px", cursor:"pointer" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: showSecondary ? 6 : 8 }}>
                          <span style={{ fontSize:13, fontWeight:800, color:complete?"var(--bz-ink)":ownedCount>0?"var(--bz-ink)":"rgba(255,255,255,0.7)" }}>
                            {complete && "\uD83C\uDF08 "}{key}
                          </span>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <span style={{ fontSize:11, fontWeight:700, color:complete?"#4ade80":ownedCount>0?"#FBBF24":"rgba(255,255,255,0.2)" }}>
                              {ownedCount}/{total}{complete ? " \u2014 COMPLETE! \uD83C\uDF08" : ""}
                            </span>
                            <span style={{ color:"rgba(255,255,255,0.2)", fontSize:12 }}>{isExpanded?"\u25B2":"\u25BC"}</span>
                          </div>
                        </div>
                        {showSecondary && (
                          <div style={{ display:"flex", gap:5, marginBottom:6, flexWrap:"wrap" }}>
                            {Object.entries(bySecondary).map(([label, sd]) => {
                              const sc = sd.owned===sd.total?"#4ade80":sd.owned>0?"#FBBF24":"rgba(255,255,255,0.2)";
                              return (
                                <div key={label} style={{ display:"flex", alignItems:"center", gap:3, background:"rgba(255,255,255,0.04)", borderRadius:5, padding:"2px 7px" }}>
                                  <span style={{ fontSize:10, color:"rgba(255,255,255,0.55)" }}>{label}:</span>
                                  <span style={{ fontSize:10, fontWeight:700, color:sc }}>{sd.owned}/{sd.total}</span>
                                  {sd.owned===sd.total && <span style={{ fontSize:9 }}>{"\uD83C\uDF08"}</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div style={{ height:6, background:"rgba(255,255,255,0.06)", borderRadius:3, overflow:"hidden" }}>
                          <div style={{ width:`${pct}%`, height:"100%", borderRadius:3, transition:"width 0.3s", background:"linear-gradient(90deg,#F97316,#FBBF24,#4ade80,#60A5FA,#A855F7,#F472B6,#EF4444,#F97316)" }}/>
                        </div>
                      </div>
                      {isExpanded && (
                        <div style={{ borderTop:"1px solid rgba(255,255,255,0.06)", padding:"14px 16px", background:"rgba(0,0,0,0.3)" }}>
                          <div style={{ display:"flex", gap:4, marginBottom:10 }}>
                            {[["all","All"],["owned","\u2705 Have"],["missing","\u274C Missing"]].map(([v,l])=>(
                              <button key={v} onClick={e=>{ e.stopPropagation(); setTreatOwnedFilter(v); }} style={{ background:treatOwnedFilter===v?"rgba(232,49,122,0.15)":"transparent", color:treatOwnedFilter===v?"#E8317A":"rgba(255,255,255,0.4)", border:`1.5px solid ${treatOwnedFilter===v?"#E8317A":"rgba(255,255,255,0.08)"}`, borderRadius:20, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{l}</button>
                            ))}
                          </div>
                          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:6 }}>
                            {groupCardList.filter(c => treatOwnedFilter==="owned" ? owned[c.id] : treatOwnedFilter==="missing" ? !owned[c.id] : true).map(c => (
                              <BobaCard key={c.id} c={c} isOwned={!!owned[c.id]} ownedQty={owned[c.id]||0} isBorrowed={(borrowedByCard[c.id]||0) > 0}
                                flippedCard={flippedCard} setFlippedCard={setFlippedCard} onExpand={setExpandedCard}
                                toggleOwned={()=>{ if(!user){setSigningIn(true);return;} toggleOwned(c.id); }}
                                setOwnedQty={(id,qty)=>setOwnedQty(id,qty)}
                                toggleWant={()=>toggleWant(c.id)} wantList={wantList} WEAPON_COLORS={PUBLIC_WEAPON_COLORS}
                                onComp={c=>setCompCard(c)} onLotEdit={user?()=>setLotModal({card:c}):null} lotCount={lotCountByCard[c.id]||0} myScanPhoto={scanPhotoByCard[c.id]} onCardActivity={resetFlipTimer} isAdmin={_cardAdmin} onImageUpload={handleCardImageUpload} onImageClear={handleCardImageClear} kidGroups={kidGroups} kidTags={kidTagsByCard[c.id]} onAssignKid={assignCardToKid} onSell={user?()=>setSellModal(c):null} foundInMap={foundInMap}
                    lentCount={lentByCard[c.id]||0} lentWho={lentWhoByCard[c.id]?.who||""}
                    onLend={user?()=>{const who=window.prompt("Lend "+c.hero+" to whom?");if(who&&who.trim())markCardLent(c.id,who);}:null}
                    onReturn={user?()=>markCardReturned(c.id):null}/>
                            ))}
                          </div>
                          {user && (
                            <div style={{ marginTop:10, display:"flex", gap:8 }}>
                              <button onClick={async e=>{ e.stopPropagation(); const next={...owned}; groupCardList.forEach(c=>next[c.id]=1); setOwned(next); await setDoc(doc(db,"boba_owned",user.uid),next); }} style={{ background:"rgba(74,222,128,0.1)", border:"1px solid rgba(74,222,128,0.25)", color:"#4ade80", borderRadius:8, padding:"5px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\u2705 Mark All Owned"}</button>
                              <button onClick={async e=>{ e.stopPropagation(); const next={...owned}; groupCardList.forEach(c=>delete next[c.id]); setOwned(next); await setDoc(doc(db,"boba_owned",user.uid),next); }} style={{ background:"rgba(232,49,122,0.08)", border:"1px solid rgba(232,49,122,0.2)", color:"#E8317A", borderRadius:8, padding:"5px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{"\u2715 Clear All"}</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {visibleGroups.length === 0 && (
                  <div style={{ textAlign:"center", padding:60, color:"rgba(255,255,255,0.2)" }}>
                    <div style={{ fontSize:40, marginBottom:12 }}>{"\uD83C\uDF08"}</div>
                    <div style={{ fontSize:15, fontWeight:700 }}>No {rainbowGroupBy === "hero" ? "heroes" : rainbowGroupBy === "treatmentWeapon" ? "rainbows" : "treatments"} match your filters</div>
                  </div>
                )}
              </div>
              )}
            </div>
          );
        })()}

        {/* WANTS TAB */}
        {activeTab==="intransit" && (() => {
          const transitIds = Object.keys(inTransit);
          const transitCards = transitIds.map(id => ({ card: cards.find(c=>c.id===id) || {id, hero:"(unknown card)"}, info: inTransit[id] }))
            .sort((a,b)=>(a.card.hero||"").localeCompare(b.card.hero||""));
          const q = (search||"").toLowerCase();
          const shown = transitCards.filter(x => !q || `${x.card.hero} ${x.card.cardNum} ${x.card.treatment} ${x.info.note||""}`.toLowerCase().includes(q));
          const totalQty = transitIds.reduce((s,id)=>s+(parseInt(inTransit[id].qty)||1),0);
          return (
          <div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6, flexWrap:"wrap", gap:10 }}>
              <div>
                <div style={{ fontSize:20, fontWeight:900, color:"#fff" }}>🚚 On the Way</div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>Cards you've bought and are waiting on · {transitIds.length} card{transitIds.length!==1?"s":""} · {totalQty} total incoming</div>
              </div>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search…" style={{ background:"#241820", border:"1px solid rgba(255,255,255,0.18)", borderRadius:10, color:"#f6eef2", padding:"8px 12px", fontSize:13, fontFamily:"inherit", outline:"none" }}/>
            </div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", fontStyle:"italic", marginBottom:14 }}>Not counted as owned until you mark them received.</div>
            {transitIds.length===0 ? (
              <div style={{ textAlign:"center", padding:"60px 20px" }}>
                <div style={{ fontSize:44, marginBottom:14 }}>🚚</div>
                <div style={{ fontSize:18, fontWeight:900, color:"#fff", marginBottom:8 }}>Nothing on the way</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,0.45)", maxWidth:400, margin:"0 auto", lineHeight:1.6 }}>When you buy a card, open it and hit "🚚 Mark as On the Way" (or use the truck button in the rainbow tracker). Everything you're waiting on shows up here so you don't double-buy.</div>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {shown.map(({card:c, info}) => (
                  <div key={c.id} style={{ display:"flex", alignItems:"center", gap:12, background:"rgba(96,165,250,0.06)", border:"1px solid rgba(96,165,250,0.25)", borderRadius:12, padding:"10px 12px", flexWrap:"wrap" }}>
                    <div style={{ width:44, aspectRatio:"3/4", borderRadius:6, overflow:"hidden", background:"rgba(0,0,0,0.4)", flexShrink:0 }}>
                      {c.imageUrl ? <img src={c.imageUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontSize:14 }}>🃏</div>}
                    </div>
                    <div style={{ flex:1, minWidth:120 }}>
                      <div style={{ fontSize:13, fontWeight:800, color:"#fff" }}>{c.hero}</div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)" }}>{[c.treatment, c.weapon, c.cardNum?`#${c.cardNum}`:""].filter(Boolean).join(" · ")} · since {info.date||"?"}</div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                      <label style={{ fontSize:10, color:"rgba(255,255,255,0.5)" }}>Qty</label>
                      <input type="number" min="1" value={info.qty||1} onWheel={e=>e.currentTarget.blur()}
                        onChange={e=>setTransit(c.id,{ ...info, qty: Math.max(1,parseInt(e.target.value)||1) })}
                        style={{ width:50, background:"#241820", border:"1px solid rgba(255,255,255,0.18)", color:"#f6eef2", borderRadius:6, padding:"5px 6px", fontSize:12, fontFamily:"inherit", textAlign:"center" }}/>
                    </div>
                    <input value={info.from||""} placeholder="From (seller / @handle)"
                      onChange={e=>setTransit(c.id,{ ...info, from: e.target.value })}
                      style={{ width:150, background:"#241820", border:"1px solid rgba(255,255,255,0.18)", color:"#f6eef2", borderRadius:6, padding:"5px 8px", fontSize:12, fontFamily:"inherit" }}/>
                    <input value={info.note||""} placeholder="tracking / seller / ETA…"
                      onChange={e=>setTransit(c.id,{ ...info, note: e.target.value })}
                      style={{ flex:1, minWidth:140, background:"#241820", border:"1px solid rgba(255,255,255,0.18)", color:"#f6eef2", borderRadius:6, padding:"6px 10px", fontSize:12, fontFamily:"inherit" }}/>
                    <button onClick={()=>markTransitArrived(c.id)} title="Mark received — adds to your collection" style={{ background:"rgba(74,222,128,0.15)", border:"1px solid rgba(74,222,128,0.5)", color:"#4ade80", borderRadius:8, padding:"6px 12px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>✅ Arrived</button>
                    <button onClick={()=>setTransit(c.id,null)} title="Remove (order fell through)" style={{ background:"transparent", border:"1px solid rgba(255,255,255,0.15)", color:"rgba(255,255,255,0.5)", borderRadius:8, padding:"6px 10px", fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          );
        })()}
        {activeTab==="tradebait" && (() => {
          const baitCards = cards.filter(c => (owned[c.id]>1) || tradeBait[c.id]).sort((a,b)=>(a.hero||"").localeCompare(b.hero||""));
          const q = (search||"").toLowerCase();
          const shown = baitCards.filter(c => !q || `${c.hero} ${c.cardNum} ${c.treatment} ${c.weapon}`.toLowerCase().includes(q));
          return (
          <div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:10 }}>
              <div>
                <div style={{ fontSize:20, fontWeight:900, color:"#fff" }}>🔁 Trade Bait</div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>Your duplicates and cards you've flagged for trade · {baitCards.length} cards</div>
              </div>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search…" style={{ background:"#241820", border:"1px solid rgba(255,255,255,0.18)", borderRadius:10, color:"#f6eef2", padding:"8px 12px", fontSize:13, fontFamily:"inherit", outline:"none" }}/>
            </div>
              {/* Opt-in. The tab shows duplicates AND manual flags, but only the MANUAL flags are ever
                  published — owning two of something isn't the same as offering to trade it away. */}
              {(() => {
                const flagged = Object.keys(tradeBait).filter(k => tradeBait[k]).length;
                return (
                  <div style={{background:tradePublic?"rgba(74,222,128,0.07)":"rgba(255,255,255,0.03)",border:`1px solid ${tradePublic?"rgba(74,222,128,0.3)":"rgba(255,255,255,0.08)"}`,borderRadius:12,padding:"13px 15px",marginBottom:14,display:"flex",alignItems:"center",gap:13,flexWrap:"wrap"}}>
                    <div style={{flex:1,minWidth:220}}>
                      <div style={{fontSize:13,fontWeight:800,color:tradePublic?"#4ade80":"#fff",marginBottom:3}}>
                        {tradePublic ? "\u2713 Listed as a trade partner" : "Get found for trades"}
                      </div>
                      <div style={{fontSize:11.5,color:"rgba(255,255,255,0.45)",lineHeight:1.6}}>
                        {tradePublic
                          ? <>Collectors looking for one of your <strong style={{color:"#4ade80"}}>{flagged}</strong> flagged card{flagged===1?"":"s"} can find you. Only the cards you flagged are shown {"\u2014"} never your collection.</>
                          : <>Flag cards as trade bait, then switch this on and collectors who need them will see you. <strong>Only the cards you flag</strong> are ever public {"\u2014"} not your collection, not your quantities.</>}
                      </div>
                    </div>
                    <button onClick={toggleTradePublic} disabled={tradePubBusy || (!tradePublic && flagged===0)}
                      title={!tradePublic && flagged===0 ? "Flag at least one card as trade bait first" : ""}
                      style={{background:tradePublic?"transparent":(flagged===0?"#222":"linear-gradient(135deg,#E8317A,#7B2FF7)"),border:tradePublic?"1px solid rgba(255,255,255,0.2)":"none",color:tradePublic?"rgba(255,255,255,0.6)":(flagged===0?"#666":"#fff"),borderRadius:9,padding:"9px 16px",fontSize:12,fontWeight:800,cursor:(tradePubBusy||(!tradePublic&&flagged===0))?"not-allowed":"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>
                      {tradePubBusy ? "\u2026" : tradePublic ? "Stop listing" : "List me"}
                    </button>
                  </div>
                );
              })()}
            {baitCards.length===0 ? (
              <div style={{ textAlign:"center", padding:"60px 20px" }}>
                <div style={{ fontSize:44, marginBottom:14 }}>🔁</div>
                <div style={{ fontSize:18, fontWeight:900, color:"#fff", marginBottom:8 }}>No trade bait yet</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,0.45)", maxWidth:380, margin:"0 auto", lineHeight:1.6 }}>Any card you own more than one of shows up here automatically. You can also flag any card as trade bait from your collection using the 🔁 button.</div>
              </div>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(auto-fill,minmax(100px,1fr))":"repeat(auto-fill,minmax(140px,1fr))", gap:12 }}>
                {shown.map(c => {
                  const dupCount = owned[c.id]>1 ? owned[c.id]-1 : 0; // extras beyond the one you keep
                  const manual = !!tradeBait[c.id];
                  return (
                    <div key={c.id} style={{ background:"var(--bz-s1)", border:"1.5px solid rgba(251,191,36,0.3)", borderRadius:12, overflow:"hidden" }}>
                      <div style={{ position:"relative", aspectRatio:"3/4", background:"rgba(0,0,0,0.4)" }}>
                        {c.imageUrl
                          ? <img src={c.imageUrl} alt={c.hero} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                          : <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontSize:11, color:"rgba(255,255,255,0.4)", padding:6, textAlign:"center" }}>{c.hero}</div>}
                        {dupCount>0 && <div style={{ position:"absolute", top:6, left:6, background:"rgba(74,222,128,0.9)", color:"#000", borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:900 }}>×{owned[c.id]} own</div>}
                        {manual && <div style={{ position:"absolute", top:6, right:6, background:"rgba(251,191,36,0.9)", color:"#000", borderRadius:20, padding:"2px 8px", fontSize:9, fontWeight:900 }}>flagged</div>}
                      </div>
                      <div style={{ padding:"8px 10px" }}>
                        <div style={{ fontSize:12, fontWeight:800, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{c.hero}</div>
                        <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginBottom:6 }}>{[c.weapon, c.cardNum?`#${c.cardNum}`:""].filter(Boolean).join(" · ")}{dupCount>0?` · ${dupCount} extra`:""}</div>
                        <button onClick={()=>toggleTradeBait(c.id)} style={{ width:"100%", background:manual?"rgba(251,191,36,0.15)":"transparent", border:`1px solid ${manual?"rgba(251,191,36,0.5)":"rgba(255,255,255,0.15)"}`, color:manual?"#FBBF24":"rgba(255,255,255,0.5)", borderRadius:6, padding:"4px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{manual?"✓ Flagged":"🔁 Flag"}</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          );
        })()}
        {activeTab==="wants" && (
          <div>
            {Object.keys(wantList).length===0?(
              <div style={{textAlign:"center",padding:"70px 20px"}}>
                <div style={{fontSize:56,marginBottom:18,animation:"wantPulse 2s ease-in-out infinite"}}>{"\uD83C\uDFAF"}</div>
                <div style={{fontSize:20,fontWeight:900,color:"#fff",marginBottom:8}}>Start your hunt</div>
                <div style={{fontSize:14,color:"rgba(255,255,255,0.45)",maxWidth:340,margin:"0 auto",lineHeight:1.6}}>Tap the {"\uD83C\uDFAF"} on any card to add it to your want list. Track the cards you're chasing and never lose sight of the ones that got away.</div>
                <button onClick={()=>setActiveTab("cards")} style={{marginTop:22,background:"linear-gradient(135deg,#E8317A,#FBBF24)",border:"none",color:"#fff",borderRadius:24,padding:"11px 28px",fontSize:14,fontWeight:800,cursor:"pointer",fontFamily:"inherit",boxShadow:"0 4px 20px rgba(232,49,122,0.4)"}}>Browse Cards →</button>
              </div>
            ):(
              <div className="pub-card-grid" style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10}}>
                {cards.filter(c=>wantList[c.id]).map(c=>(
                  <BobaCard key={c.id} c={c} isOwned={!!owned[c.id]} ownedQty={owned[c.id]||0} isBorrowed={(borrowedByCard[c.id]||0) > 0}
                    flippedCard={flippedCard} setFlippedCard={setFlippedCard} onExpand={setExpandedCard}
                    toggleOwned={()=>toggleOwned(c.id)} setOwnedQty={(id,qty)=>setOwnedQty(id,qty)}
                    toggleWant={()=>toggleWant(c.id)} wantList={wantList} WEAPON_COLORS={PUBLIC_WEAPON_COLORS}
                    onComp={c=>setCompCard(c)} onLotEdit={user?()=>setLotModal({card:c}):null} lotCount={lotCountByCard[c.id]||0} myScanPhoto={scanPhotoByCard[c.id]} isAdmin={_cardAdmin} onImageUpload={handleCardImageUpload} onImageClear={handleCardImageClear}/>
                ))}
              </div>
            )}

          </div>
        )}

        {/* DECK BUILDER TAB */}
        {activeTab==="deck" && (
          <DeckBuilderTab
            user={user} deckCards={deckCards} setDeckCards={setDeckCards}
            deckName={deckName} setDeckName={setDeckName}
            deckType={deckType} setDeckType={setDeckType}
            deckSearch={deckSearch} setDeckSearch={setDeckSearch} deckSearchDebounced={deckSearchDebounced}
            deckFilterW={deckFilterW} setDeckFilterW={setDeckFilterW}
            deckFilterP={deckFilterP} setDeckFilterP={setDeckFilterP}
            deckFilterS={deckFilterS} setDeckFilterS={setDeckFilterS}
            deckFilterT={deckFilterT} setDeckFilterT={setDeckFilterT}
            WEAPON_COLORS={WEAPON_COLORS} setSigningIn={setSigningIn}
            cards={cards} owned={owned} inp={inp}
            familyOwnerByCard={familyOwnerByCard} familyOwnsCard={familyOwnsCard} deckOwnedMerged={deckOwnedMerged}
            canAddToDeck={canAddToDeck} isMobile={isMobile} lots={lots} foilDogs={foilDogs} setFoilDogs={setFoilDogs}
            kidGroups={kidGroups} kidOfCopy={kidOfCopy} otherDeckUse={otherDeckUse} proxyCards={proxyCards} onToggleProxy={toggleProxy} proxyNote={proxyNote} isProxyCopy={isProxyCopy} anyProxy={anyProxy} copyForDeck={copyForDeck}
            savedDecks={savedDecks} familyDecks={familyDecks} deckSaving={deckSaving} deckSaved={deckSaved} deckLoadId={deckLoadId}
            saveDeckTab={saveDeckTab} deleteDeckTab={deleteDeckTab} loadDeckTab={loadDeckTab} newDeckTab={newDeckTab} toggleDeckRegistered={toggleDeckRegistered} openRegSheetPrint={openRegSheetPrint} regSheetOpen={regSheetOpen} setRegSheetOpen={setRegSheetOpen} regInfo={regInfo} setRegInfo={setRegInfo} savedPlaybooks={savedPlaybooks} giveDeckToFamily={giveDeckToFamily} takeBackDeck={takeBackDeck} familyList={familyList} givenDecks={givenDecks} setFanDeck={setFanDeck} setFanMode={setFanMode}
            deckProgress={deckProgress} deckGoalW={deckGoalW} setDeckGoalW={setDeckGoalW} deckGoalT={deckGoalT} setDeckGoalT={setDeckGoalT} deckGoalSets={deckGoalSets} setDeckGoalSets={setDeckGoalSets} deckMaxMode={deckMaxMode} setDeckMaxMode={setDeckMaxMode} deckSource={deckSource} setDeckSource={setDeckSource} deckFamilyMember={deckFamilyMember} setDeckFamilyMember={setDeckFamilyMember} computeDeckProgress={computeDeckProgress} listings={listings} setActiveTab={setActiveTab} deckLegality={deckLegality}
          />
        )}

        {/* PLAYBOOK TAB */}
        {activeTab==="playbook" && (
          <PlaybookTab
            user={user} pbCards={pbCards} pbSearch={pbSearch} setPbSearch={setPbSearch}
            pbSort={pbSort} setPbSort={setPbSort} WEAPON_COLORS={WEAPON_COLORS}
            setSigningIn={setSigningIn} cards={cardsWithDbs} setCards={setCards} owned={owned}
            inp={inp} isMobile={isMobile}
            pbName={pbName} setPbName={setPbName} setPbCards={setPbCards}
            savedPlaybooks={savedPlaybooks} pbSaving={pbSaving} pbSaved={pbSaved} pbLoadId={pbLoadId}
            savePbTab={savePbTab} deletePbTab={deletePbTab} loadPbTab={loadPbTab} newPbTab={newPbTab} setFanDeck={setFanDeck} setFanMode={setFanMode}
          />
        )}

        {/* ARENA TAB -- admin only. The _cardAdmin check here is the real gate;
            hiding the nav button alone would not stop someone typing #arena. */}
        {activeTab==="arena" && (
          <ArenaTab
            user={user}
            cards={cards}
            savedDecks={savedDecks}
            WEAPON_COLORS={WEAPON_COLORS}
            canonWeapon={canonWeapon}
            isMobile={isMobile}
            setToast={setToast}
            inp={inp}
          />
        )}

        {/* FANTASY DFS TAB */}
        {/* MARKET TAB */}
        {activeTab==="market" && (
          <MarketTab
            user={user} myListings={myListings} listings={listings}
            onViewProfile={setViewProfileUid}
            WEAPON_COLORS={WEAPON_COLORS} allMyOffers={allMyOffers}
            marketSales={marketSales} trackingInputs={trackingInputs}
            setTrackingInputs={setTrackingInputs} setListModal={setListModal}
            setOfferModal={setOfferModal} setOfferAmt={setOfferAmt}
            setOfferNote={setOfferNote} setOfferSent={setOfferSent}
            setCounterModal={setCounterModal} setCounterAmt={setCounterAmt}
            buyNow={buyNow} respondOffer={respondOffer}
            unsellListing={unsellListing} saveTracking={saveTracking}
            setSigningIn={setSigningIn} setActiveTab={setActiveTab}
            inp={inp} listType={listType} cards={cards} owned={owned} search={search}
          removeListing={removeListing}
            tradeIndex={tradeIndex} wantList={wantList} tradeBait={tradeBait}
            tradeOffers={tradeOffers} onSendTrade={sendTradeOffer} onRespondTrade={respondTradeOffer}
            onCancelTrade={cancelTradeOffer} onTradeTracking={saveTradeTracking} onTradeReceived={confirmTradeReceived}
            initialView={mktInitView}
                onMarkTraded={markPackageTraded}
                onEditPackage={openTradeEditor}
                onAddSideToTrade={addSideToTrade}
                onUnacceptTrade={unacceptTradeOffer}
                deckLockedForTrade={tradeLocked}
              />
        )}

        {/* MESSAGES TAB */}
        {/* MESSAGES TAB - always mounted so thread listener stays active */}
        <div style={{maxWidth:800,margin:"0 auto",display:activeTab==="messages"?"block":"none"}}>
            {pendingReviews.length>0 && (
              <div style={{ background:"linear-gradient(135deg, rgba(232,49,122,0.12), rgba(251,191,36,0.08))", border:"1px solid rgba(232,49,122,0.25)", borderRadius:14, padding:"14px 18px", marginBottom:16 }}>
                <div style={{ fontSize:13, fontWeight:800, color:"#fff", marginBottom:4 }}>⭐ Rate your recent {pendingReviews.length===1?"purchase":"purchases"}</div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.5)", marginBottom:10 }}>Help the community by reviewing sellers you've bought from. It keeps the marketplace trustworthy.</div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {pendingReviews.slice(0,4).map(s=>(
                    <div key={s.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, background:"rgba(0,0,0,0.25)", borderRadius:10, padding:"8px 12px" }}>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:"#eee", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{s.cardName}</div>
                        <div style={{ fontSize:11, color:"var(--bz-ink-2)" }}>from {s.sellerName||"seller"}{s.price?` · $${Number(s.price).toFixed(2)}`:""}</div>
                      </div>
                      <button onClick={()=>setReviewModal({sale:s})} style={{ background:"linear-gradient(135deg,#E8317A,#FBBF24)", border:"none", color:"#fff", borderRadius:20, padding:"6px 16px", fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>Rate seller</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <MessagesTab
              user={user}
              activeThread={activeThread}
              setActiveThread={setActiveThread}
              threads={threads}
              threadMsgs={threadMsgs}
              newMsg={newMsg}
              setNewMsg={setNewMsg}
              sendMessage={sendMessage}
              closeThread={closeThread}
              marketSales={marketSales}
              inp={inp}
              WEAPON_COLORS={WEAPON_COLORS}
              setSigningIn={setSigningIn}
            />
        </div>{/* end messages tab */}


        {/* FRIENDS TAB */}
        {activeTab==="friends" && (
          <FriendsTab
            user={user} friends={friends} friendReqs={friendReqs} sentReqs={sentReqs}
            addEmail={addEmail} setAddEmail={setAddEmail}
            addStatus={addStatus} setAddStatus={setAddStatus}
            friendOwned={friendOwned} viewingFriend={viewingFriend} setViewingFriend={setViewingFriend}
            respondFriendReq={respondFriendReq} toggleFamily={toggleFamily} borrowLedger={borrowLedger}
            acceptLoan={acceptLoan} declineLoan={declineLoan}
            sendFriendRequest={sendFriendRequest}
            cards={cards} owned={owned} publicCards={publicCards}
            WEAPON_COLORS={WEAPON_COLORS} setSigningIn={setSigningIn}
            inp={inp} teamInvites={teamInvites}
          respondTeamInvite={respondTeamInvite}
          />
        )}

        {/* TEAM TAB */}
        {activeTab==="team" && (
          <TeamTab
            user={user} teams={teams} activeTeam={activeTeam} setActiveTeam={setActiveTeam}
            newTeamName={newTeamName} setNewTeamName={setNewTeamName}
            inviteEmail={inviteEmail} setInviteEmail={setInviteEmail}
            inviteStatus={inviteStatus} setInviteStatus={setInviteStatus}
            teamInvites={teamInvites} moveTeamMember={moveTeamMember}
            deleteTeam={deleteTeam} respondTeamInvite={respondTeamInvite}
            createTeam={createTeam}
            WEAPON_COLORS={WEAPON_COLORS} setSigningIn={setSigningIn}
            cards={cards} owned={owned}
            teamDecks={teamDecks} savedDecks={savedDecks} submitDeckToTeam={submitDeckToTeam} withdrawTeamDeck={withdrawTeamDeck}
            swapOffers={swapOffers} offerSwap={offerSwap} dismissSwapOffer={dismissSwapOffer}
            teamLoans={teamLoans} lendCard={lendCard} markLoanReturned={markLoanReturned} deleteLoan={deleteLoan}
            inp={inp} inviteToTeam={inviteToTeam}
          />
        )}
        {activeTab==="ledger" && (
          <AccountingLedger lots={lots} marketSales={marketSales} user={user} cards={cards} />
        )}
        {activeTab==="leaderboard" && (
          <Leaderboard user={user} marketSales={marketSales} onViewProfile={setViewProfileUid} />
        )}
      </div>{/* end tab content */}
    </div>
  );
}


// Local YYYY-MM-DD for "today" — avoids UTC rollover that makes evening-logged dates jump a day
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function parseLocalDate(dateStr) {
  if (!dateStr) return new Date(0);
  const parts = String(dateStr).split("T")[0].split("-");
  if (parts.length === 3) return new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]), 12, 0, 0);
  return new Date(dateStr);
}


// --- PUBLIC QUOTE PAGE --------------------------------------
// ── PUBLIC CHASE TRACKER (/chases) ───────────────────────────────────────────
function PublicChaseTracker() {
  const [chases,      setChases]      = useState([]);
  const [bobaCards,   setBobaCards]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeChase, setActiveChase] = useState(null);
  const [submitForm,  setSubmitForm]  = useState(null);
  const [formData,    setFormData]    = useState({ discord:"", message:"", photo:null });
  const [submitting,  setSubmitting]  = useState(false);
  const [submitted,   setSubmitted]   = useState(false);

  useEffect(() => {
    // Load chases
    const u1 = onSnapshot(collection(db,"chases"), snap => {
      const all = snap.docs.map(d=>({...d.data(), id:d.id}))
        .filter(c=>c.active!==false && (c.cards||[]).some(x=>!x.owned));
      setChases(all.sort((a,b)=>(a.breaker||"").localeCompare(b.breaker||"")));
      setLoading(false);
    });
    // Load card database for live treatment/image lookup
    getDocs(collection(db,"boba_checklist")).then(snap => {
      setBobaCards(snap.docs.map(d=>({...d.data(), id:d.id})));
    }).catch(()=>{});
    return ()=>u1();
  }, []);

  // Build lookup map
  const cardLookup = useMemo(() => {
    const m = {};
    bobaCards.forEach(c=>{ if(c.id) m[c.id]=c; });
    return m;
  }, [bobaCards]);

  async function submitHaveCard() {
    if (!submitForm) return;
    setSubmitting(true);
    let photoUrl = null;
    if (formData.photo) {
      try {
        const ref2 = ref(storage, `chase-submissions/${Date.now()}-${formData.photo.name}`);
        await uploadBytes(ref2, formData.photo);
        photoUrl = await getDownloadURL(ref2);
      } catch(e) { console.error(e); }
    }
    await setDoc(doc(db,"chase_submissions",uid()), {
      chaseId:submitForm.chaseId, cardName:submitForm.cardName, breaker:submitForm.breaker,
      discord:formData.discord.trim(), message:formData.message.trim(), photoUrl,
      submittedAt:new Date().toISOString(), status:"pending",
    });
    setSubmitting(false); setSubmitted(true);
    setTimeout(()=>{ setSubmitForm(null); setSubmitted(false); setFormData({discord:"",message:"",photo:null}); }, 3500);
  }

  const BC = { Dev:"#3B82F6", Dre:"#A855F7", Krystal:"#22C55E", BigU:"#F97316", Vinny:"#EF4444", Stephen:"#FBBF24" };
  const grouped = chases.reduce((acc,c)=>{ const b=c.breaker||"Unknown"; if(!acc[b])acc[b]=[]; acc[b].push(c); return acc; },{});
  const WEAPON_COLORS = { Fire:"#F97316", Ice:"#38BDF8", Glow:"#4ade80", Hex:"#A78BFA", Gum:"#EC4899", Super:"#FBBF24", Steel:"#94A3B8", Brawl:"#EF4444" };
  const getWeaponColor = (name) => { for(const [w,c] of Object.entries(WEAPON_COLORS)) if(name.toLowerCase().includes(w.toLowerCase())) return c; return "#888"; };

  return (
    <div style={{ minHeight:"100vh", background:"#000", fontFamily:"'Trebuchet MS',sans-serif", color:"var(--bz-ink)", overflowX:"hidden" }}>

      {/* Inject keyframes */}
      <style>{`
        @keyframes chaseGlow { 0%,100%{opacity:0.4} 50%{opacity:1} }
        @keyframes chaseFadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        .chase-card { transition:transform 0.2s cubic-bezier(0.22,1,0.36,1),box-shadow 0.2s ease,border-color 0.2s ease !important; }
        .chase-card:hover { transform:translateY(-4px) !important; box-shadow:0 20px 60px rgba(0,0,0,0.7) !important; }
        .card-row { transition:background 0.12s ease,transform 0.12s ease !important; cursor:pointer; }
        .card-row:hover { background:rgba(255,255,255,0.05) !important; transform:translateX(3px) !important; }
        .have-btn { transition:all 0.15s cubic-bezier(0.34,1.56,0.64,1) !important; }
        .have-btn:hover { transform:scale(1.05) !important; }
        .have-btn:active { transform:scale(0.96) !important; }
        @media(max-width:600px) { .chase-grid { grid-template-columns:1fr !important; } }
      `}</style>

      {/* Hero header */}
      <div style={{ position:"relative", overflow:"hidden", background:"linear-gradient(135deg,var(--bz-bg) 0%,#0d0010 50%,var(--bz-bg) 100%)", borderBottom:"1px solid var(--bz-line)", padding:"48px 24px 40px" }}>
        {/* Ambient orbs */}
        <div style={{ position:"absolute", top:-60, left:"10%", width:300, height:300, borderRadius:"50%", background:"radial-gradient(circle,rgba(232,49,122,0.08) 0%,transparent 70%)", pointerEvents:"none" }}/>
        <div style={{ position:"absolute", top:-40, right:"15%", width:200, height:200, borderRadius:"50%", background:"radial-gradient(circle,rgba(123,156,255,0.07) 0%,transparent 70%)", pointerEvents:"none" }}/>

        <div style={{ maxWidth:900, margin:"0 auto", position:"relative" }}>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", flexWrap:"wrap", gap:16 }}>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:"#E8317A", textTransform:"uppercase", letterSpacing:"3px", marginBottom:10, opacity:0.8 }}>Bazooka Breaks</div>
              <h1 style={{ margin:0, fontSize:"clamp(28px,5vw,44px)", fontWeight:900, color:"#fff", letterSpacing:"-0.5px", lineHeight:1.1 }}>
                Bazooka Chase Tracker
              </h1>
              <p style={{ margin:"10px 0 0", fontSize:14, color:"var(--bz-ink-3)", lineHeight:1.6, maxWidth:480 }}>
                Cards we're hunting. If you have one, hit the button — we want to hear from you.
              </p>
            </div>
            <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
              {Object.entries(BC).map(([name,color])=>(
                chases.some(c=>c.breaker===name) && (
                  <div key={name} style={{ textAlign:"center" }}>
                    <div style={{ width:44, height:44, borderRadius:"50%", background:`linear-gradient(135deg,${color}33,${color}11)`, border:`2px solid ${color}55`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:900, color, margin:"0 auto 4px", boxShadow:`0 0 20px ${color}22` }}>{name[0]}</div>
                    <div style={{ fontSize:9, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1 }}>{name}</div>
                  </div>
                )
              ))}
            </div>
          </div>

          {/* Stats bar removed — per-chase progress shown on each card */}
        </div>
      </div>

      {/* Main content */}
      <div style={{ maxWidth:900, margin:"0 auto", padding:"32px 16px 60px" }}>

        {loading && (
          <div style={{ textAlign:"center", padding:80 }}>
            <div style={{ width:40, height:40, borderRadius:"50%", border:"3px solid var(--bz-s2)", borderTopColor:"#E8317A", animation:"spin 0.8s linear infinite", margin:"0 auto 16px" }}/>
            <div style={{ color:"var(--bz-ink-3)", fontSize:13 }}>Loading chases...</div>
          </div>
        )}

        {!loading && chases.length === 0 && (
          <div style={{ textAlign:"center", padding:"80px 24px", animation:"chaseFadeUp 0.4s ease forwards" }}>
            <div style={{ fontSize:56, marginBottom:20, opacity:0.15 }}>🃏</div>
            <div style={{ fontSize:20, fontWeight:800, color:"var(--bz-ink-3)", marginBottom:8 }}>No active chases</div>
            <div style={{ fontSize:14, color:"var(--bz-ink-3)" }}>Check back soon — we're always hunting.</div>
          </div>
        )}

        {/* Breaker sections */}
        {Object.entries(grouped).map(([breaker, breakerChases], bi) => {
          const color = BC[breaker]||"#E8317A";
          return (
            <div key={breaker} style={{ marginBottom:48, animation:`chaseFadeUp 0.4s ease ${bi*0.08}s both` }}>
              {/* Breaker header */}
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
                <div style={{ width:36, height:36, borderRadius:"50%", background:`linear-gradient(135deg,${color}44,${color}11)`, border:`2px solid ${color}66`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:900, color, boxShadow:`0 0 24px ${color}33`, flexShrink:0 }}>{breaker[0]}</div>
                <div>
                  <div style={{ fontSize:20, fontWeight:900, color }}>{breaker}</div>
                  <div style={{ fontSize:11, color:"#8a8a8a" }}>{breakerChases.length} active chase{breakerChases.length!==1?"s":""} · {breakerChases.reduce((s,c)=>s+(c.cards||[]).filter(x=>!x.owned).length,0)} cards needed</div>
                </div>
                <div style={{ flex:1, height:1, background:`linear-gradient(90deg,${color}33,transparent)`, marginLeft:8 }}/>
              </div>

              {/* Chase cards grid */}
              <div className="chase-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:14 }}>
                {breakerChases.map((chase,ci) => {
                  const cards  = chase.cards||[];
                  const needed = cards.filter(c=>!c.owned);
                  const owned  = cards.filter(c=>c.owned);
                  const pct    = cards.length ? (owned.length/cards.length*100) : 0;
                  const isOpen = activeChase===chase.id;

                  return (
                    <div key={chase.id} className="chase-card"
                      style={{ background:"linear-gradient(160deg,var(--bz-s1) 0%,var(--bz-s1) 100%)", border:`1px solid ${isOpen?color+"44":"var(--bz-s2)"}`, borderRadius:16, overflow:"hidden", boxShadow:isOpen?`0 0 40px ${color}11`:"none" }}>

                      {/* Accent line */}
                      <div style={{ height:3, background:`linear-gradient(90deg,${color},${color}44,transparent)` }}/>

                      {/* Card header — always clickable */}
                      <div onClick={()=>setActiveChase(isOpen?null:chase.id)} style={{ padding:"16px 18px", cursor:"pointer" }}>
                        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:12 }}>
                          <div>
                            <div style={{ fontSize:15, fontWeight:800, color:"var(--bz-ink)", marginBottom:3 }}>{chase.setName}</div>
                            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                              <span style={{ fontSize:10, color, background:`${color}18`, borderRadius:5, padding:"2px 8px", fontWeight:700 }}>{chase.chaseType}</span>
                              {chase.description&&<span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>{chase.description}</span>}
                            </div>
                          </div>
                          <div style={{ textAlign:"right", flexShrink:0, marginLeft:8 }}>
                            <div style={{ fontSize:18, fontWeight:900, color:pct===100?"#FBBF24":pct>=85?"#EC4899":pct>=71?"#A78BFA":pct>=57?"#4ade80":pct>=42?"#38BDF8":pct>=28?"#F97316":pct>=14?"#EF4444":"#94A3B8" }}>{pct.toFixed(0)}%</div>
                            <div style={{ fontSize:9, color:"#8a8a8a", textTransform:"uppercase", letterSpacing:1 }}>{owned.length}/{cards.length}</div>
                          </div>
                        </div>

                        {/* Rainbow progress bar */}
                        <div style={{ background:"var(--bz-s2)", borderRadius:99, height:8, overflow:"hidden", position:"relative" }}>
                          {/* Full rainbow track (always rendered, clipped by width) */}
                          <div style={{ position:"absolute", inset:0, borderRadius:99, background:"linear-gradient(90deg,#94A3B8 0%,#EF4444 14%,#F97316 28%,#38BDF8 42%,#4ade80 57%,#A78BFA 71%,#EC4899 85%,#FBBF24 100%)", opacity:0.15 }}/>
                          {/* Filled portion */}
                          <div style={{ width:`${pct}%`, height:"100%", background:"linear-gradient(90deg,#94A3B8 0%,#EF4444 14%,#F97316 28%,#38BDF8 42%,#4ade80 57%,#A78BFA 71%,#EC4899 85%,#FBBF24 100%)", borderRadius:99, transition:"width 0.8s cubic-bezier(0.22,1,0.36,1)", boxShadow: pct>0?`0 0 12px ${pct===100?"#FBBF24":pct>=70?"#A78BFA":pct>=50?"#4ade80":pct>=30?"#F97316":"#94A3B8"}66`:"none" }}/>
                        </div>

                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:8 }}>
                          <span style={{ fontSize:11, color:"var(--bz-ink-3)" }}>{needed.length} still needed</span>
                          <span style={{ fontSize:11, color:color, fontWeight:700 }}>{isOpen?"▲ Hide":"▼ Show cards"}</span>
                        </div>
                      </div>

                      {/* Expanded card list */}
                      {isOpen && (
                        <div style={{ borderTop:`1px solid ${color}22`, padding:"14px 18px 18px" }}>
                          {needed.length > 0 && (
                            <div style={{ marginBottom:14 }}>
                              <div style={{ fontSize:9, fontWeight:800, color:"#E8317A", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:8 }}>Needed — {needed.length}</div>
                              <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                                {needed.map((card,i)=>{
                                  const live = cardLookup[card.cardId]||{};
                                  const weapon    = card.weapon    || live.weapon    || "";
                                  const treatment = card.treatment || live.treatment || "";
                                  const cardNum   = card.cardNum   || live.cardNum   || "";
                                  const imageUrl  = card.imageUrl  || live.imageUrl  || null;
                                  const hero      = card.hero      || live.hero      || card.name;
                                  const WCOLORS = { Fire:"#F97316",Ice:"#38BDF8",Glow:"#4ade80",Hex:"#A78BFA",Gum:"#EC4899",Super:"#FBBF24",Steel:"#94A3B8",Brawl:"#EF4444" };
                                  const wc = WCOLORS[weapon] || getWeaponColor(card.name);
                                  return (
                                    <div key={i} className="card-row"
                                      style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"rgba(255,255,255,0.03)", border:`1px solid ${wc}22`, borderRadius:10, padding:"8px 12px", gap:10 }}>
                                      <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
                                        {/* Card image */}
                                        {imageUrl
                                          ? <div className="card-img-wrap" style={{ width:44, height:60, borderRadius:6, flexShrink:0 }}><img src={imageUrl} alt="" style={{ width:44, height:60, objectFit:"cover", borderRadius:6, border:`1px solid ${wc}44`, boxShadow:`0 2px 8px rgba(0,0,0,0.5)`, display:"block" }}/></div>
                                          : <div style={{ width:44, height:60, borderRadius:6, background:`${wc}18`, border:`1px solid ${wc}22`, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                                              <div style={{ fontSize:10, color:wc, fontWeight:900 }}>{(weapon||"?")[0]}</div>
                                            </div>
                                        }
                                        <div style={{ minWidth:0 }}>
                                          <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginBottom:4 }}>{hero}</div>
                                          <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                                            {weapon && <span style={{ fontSize:10, fontWeight:800, color:wc, background:`${wc}18`, borderRadius:4, padding:"2px 8px" }}>{weapon}</span>}
                                            {treatment && <span style={{ fontSize:10, color:"var(--bz-ink-2)", background:"rgba(255,255,255,0.06)", borderRadius:4, padding:"2px 8px" }}>{treatment}</span>}
                                            {cardNum && <span style={{ fontSize:10, color:"var(--bz-ink-3)" }}>#{cardNum}</span>}
                                          </div>
                                        </div>
                                      </div>
                                      <button className="have-btn"
                                        onClick={()=>setSubmitForm({ chaseId:chase.id, cardName:card.name, breaker:chase.breaker })}
                                        style={{ background:`linear-gradient(135deg,${color}22,${color}11)`, border:`1px solid ${color}44`, color, borderRadius:7, padding:"6px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", flexShrink:0 }}>
                                        I have this →
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {owned.length > 0 && (
                            <>
                              <div style={{ fontSize:9, fontWeight:800, color:"#4ade80", textTransform:"uppercase", letterSpacing:"1.5px", marginBottom:8 }}>Got it ✓ — {owned.length}</div>
                              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                                {owned.map((card,i)=>(
                                  <span key={i} style={{ background:"rgba(74,222,128,0.05)", border:"1px solid rgba(74,222,128,0.1)", color:"#4ade80", borderRadius:6, padding:"3px 10px", fontSize:11, textDecoration:"line-through", opacity:0.45 }}>{card.name}</span>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ borderTop:"1px solid #111", padding:"24px", textAlign:"center" }}>
        <div style={{ fontSize:13, fontWeight:700, color:"#E8317A" }}>Bazooka Breaks</div>
        <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:4 }}>Live card breaking on Whatnot · Discord community · bazookabreaks.com</div>
      </div>

      {/* Submit modal */}
      {submitForm && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center", padding:20, backdropFilter:"blur(8px)" }}>
          <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:20, padding:"28px 24px", width:"100%", maxWidth:420, boxShadow:"0 40px 80px rgba(0,0,0,0.8)", animation:"chaseFadeUp 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards" }}>

            {submitted ? (
              <div style={{ textAlign:"center", padding:"20px 0" }}>
                <div style={{ fontSize:52, marginBottom:16 }}>✅</div>
                <div style={{ fontSize:20, fontWeight:900, color:"#4ade80", marginBottom:8 }}>Sent!</div>
                <div style={{ fontSize:13, color:"var(--bz-ink-3)" }}>We'll reach out on Discord if we're interested. Thank you!</div>
              </div>
            ) : (
              <>
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:11, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:"2px", marginBottom:6 }}>You have</div>
                  <div style={{ fontSize:20, fontWeight:900, color:"var(--bz-ink)" }}>{submitForm.cardName}</div>
                  <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginTop:4 }}>Letting <span style={{ color:BC[submitForm.breaker]||"#E8317A", fontWeight:700 }}>{submitForm.breaker}</span> know</div>
                </div>

                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                  <div>
                    <label style={{ fontSize:10, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:"1.2px", display:"block", marginBottom:6 }}>Discord Username *</label>
                    <input value={formData.discord} onChange={e=>setFormData(p=>({...p,discord:e.target.value}))}
                      placeholder="@yourusername"
                      style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"11px 14px", color:"var(--bz-ink)", fontSize:14, fontFamily:"inherit", outline:"none", width:"100%", boxSizing:"border-box", transition:"border-color 0.15s" }}
                      onFocus={e=>e.target.style.borderColor="#E8317A"}
                      onBlur={e=>e.target.style.borderColor="var(--bz-line-2)"}/>
                  </div>
                  <div>
                    <label style={{ fontSize:10, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:"1.2px", display:"block", marginBottom:6 }}>Message <span style={{ color:"var(--bz-ink-3)" }}>(optional)</span></label>
                    <textarea value={formData.message} onChange={e=>setFormData(p=>({...p,message:e.target.value}))}
                      placeholder="Condition, asking price, open to trade, etc."
                      rows={3}
                      style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"11px 14px", color:"var(--bz-ink)", fontSize:13, fontFamily:"inherit", outline:"none", width:"100%", boxSizing:"border-box", resize:"none", lineHeight:1.6, transition:"border-color 0.15s" }}
                      onFocus={e=>e.target.style.borderColor="#E8317A"}
                      onBlur={e=>e.target.style.borderColor="var(--bz-line-2)"}/>
                  </div>
                  <div>
                    <label style={{ fontSize:10, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:"1.2px", display:"block", marginBottom:6 }}>Photo <span style={{ color:"var(--bz-ink-3)" }}>(optional)</span></label>
                    <label style={{ display:"flex", alignItems:"center", gap:10, background:"var(--bz-bg)", border:"1px dashed var(--bz-line-2)", borderRadius:10, padding:"12px 14px", cursor:"pointer" }}>
                      <span style={{ fontSize:18 }}>📷</span>
                      <span style={{ fontSize:12, color:formData.photo?"#4ade80":"#555" }}>{formData.photo?formData.photo.name:"Tap to add a photo"}</span>
                      <input type="file" accept="image/*" style={{ display:"none" }} onChange={e=>setFormData(p=>({...p,photo:e.target.files[0]||null}))}/>
                    </label>
                  </div>
                </div>

                <div style={{ display:"flex", gap:10, marginTop:22 }}>
                  <button onClick={submitHaveCard} disabled={!formData.discord.trim()||submitting}
                    style={{ flex:1, background:(!formData.discord.trim()||submitting)?"var(--bz-s2)":"linear-gradient(135deg,#E8317A,#c41e5a)", border:"none", color:(!formData.discord.trim()||submitting)?"#444":"#fff", borderRadius:10, padding:"13px", fontSize:14, fontWeight:800, cursor:(!formData.discord.trim()||submitting)?"not-allowed":"pointer", fontFamily:"inherit", transition:"all 0.15s", boxShadow:(!formData.discord.trim()||submitting)?"none":"0 8px 24px rgba(232,49,122,0.35)" }}>
                    {submitting ? "Sending..." : "Send →"}
                  </button>
                  <button onClick={()=>{ setSubmitForm(null); setFormData({discord:"",message:"",photo:null}); }}
                    style={{ background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", color:"#999", borderRadius:10, padding:"13px 18px", fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>
                    ✕
                  </button>
                </div>

                <div style={{ textAlign:"center", marginTop:14, fontSize:11, color:"var(--bz-ink-3)" }}>Discord handle required so we can reach out</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── INTERNAL CHASE MANAGER ────────────────────────────────────────────────────
function ChaseManager({ user, userRole, bobaCards=[] }) {
  const isAdmin   = ["Admin"].includes(userRole?.role);
  const myBreaker = user?.displayName || "";

  const [chases,      setChases]      = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [showForm,    setShowForm]    = useState(false);
  const [editId,      setEditId]      = useState(null);
  const [activeChase, setActiveChase] = useState(null);
  const [chaseSorts,  setChaseSorts]  = useState({});
  const [saving,      setSaving]      = useState(false);

  // Form state — DB-driven
  const [fBreaker,  setFBreaker]  = useState(BREAKERS[0]);
  const [fSet,      setFSet]      = useState("");
  const [fMode,     setFMode]     = useState("hero"); // "hero" | "weapon" | "treatment" | "hero_weapon" | "fullset"
  const [fHero,     setFHero]     = useState("");
  const [fWeapon,   setFWeapon]   = useState("");
  const [fTreat,    setFTreat]    = useState("");
  const [fDesc,     setFDesc]     = useState("");

  useEffect(() => {
    const u1 = onSnapshot(collection(db,"chases"), snap =>
      setChases(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(a.breaker||"").localeCompare(b.breaker||"")))
    );
    const u2 = onSnapshot(collection(db,"chase_submissions"), snap =>
      setSubmissions(snap.docs.map(d=>({...d.data(), id:d.id})))
    );
    return ()=>{ u1(); u2(); };
  }, []);

  const myChases = isAdmin ? chases : chases.filter(c => c.breaker === myBreaker);

  // Derived options from bobaCards
  const WEAPON_ORDER = ["Steel","Brawl","Fire","Ice","Glow","Hex","Gum","Super"];
  const allSets      = [...new Set(bobaCards.map(c=>c.setName).filter(Boolean))].sort();
  const setCards     = fSet ? bobaCards.filter(c=>c.setName===fSet) : [];
  const setHeroes    = [...new Set(setCards.map(c=>c.hero).filter(Boolean))].sort();
  const setWeapons   = sortWeapons([...new Set(setCards.map(c=>canonWeapon(c.weapon)).filter(Boolean))]);
  const setTreats    = [...new Set(setCards.map(c=>c.treatment).filter(Boolean))].sort();
  const heroWeapons  = fHero ? sortWeapons([...new Set(setCards.filter(c=>c.hero===fHero).map(c=>canonWeapon(c.weapon)).filter(Boolean))]) : [];

  const CHASE_MODES = [
    { id:"hero",            label:"🦸 Hero Rainbow",              desc:"Every card for one hero — all weapons, all treatments" },
    { id:"treatment",       label:"✨ Insert/Treatment Rainbow",   desc:"Every card of one treatment across all heroes" },
    { id:"treatment_weapon",label:"🎯 Insert + Weapon Rainbow",    desc:"One treatment × one weapon type" },
  ];

  const previewCards = useMemo(() => {
    if (!fSet) return [];
    const byWeapon = (a,b) => WEAPON_ORDER.indexOf(a.weapon) - WEAPON_ORDER.indexOf(b.weapon) || (a.hero||"").localeCompare(b.hero||"");
    switch(fMode) {
      case "hero":
        return fHero ? setCards.filter(c=>c.hero===fHero).sort(byWeapon) : [];
      case "treatment":
        return fTreat ? setCards.filter(c=>c.treatment===fTreat).sort(byWeapon) : [];
      case "treatment_weapon":
        // Compare canonically: the dropdown now offers "Fire", but a card may be tagged "FIRE".
        return (fTreat&&fWeapon) ? setCards.filter(c=>c.treatment===fTreat&&canonWeapon(c.weapon)===canonWeapon(fWeapon)).sort((a,b)=>(a.hero||"").localeCompare(b.hero||"")) : [];
      default: return [];
    }
  }, [fSet, fMode, fHero, fWeapon, fTreat, bobaCards]);

  const chaseLabel = () => {
    switch(fMode) {
      case "hero":             return fHero ? `${fHero} Rainbow` : "";
      case "treatment":        return fTreat ? `${fTreat} Rainbow` : "";
      case "treatment_weapon": return (fTreat&&fWeapon) ? `${fTreat} ${fWeapon} Rainbow` : "";
      default: return "";
    }
  };

  async function saveChase() {
    if (!previewCards.length) return;
    setSaving(true);
    const cards = previewCards.map(c => {
      const existing = editId
        ? (chases.find(x=>x.id===editId)?.cards||[]).find(x=>x.cardId===c.id)
        : null;
      return {
        cardId: c.id,
        name: [c.hero,c.weapon,c.treatment,c.cardNum?"#"+c.cardNum:""].filter(Boolean).join(" — "),
        hero: c.hero||"",
        weapon: c.weapon||"",
        treatment: c.treatment||"",
        cardNum: c.cardNum||"",
        notation: c.notation||"",
        imageUrl: c.imageUrl||null,
        owned: existing?.owned||false
      };
    });
    const chaseType = chaseLabel() || fMode;
    const data = { breaker:fBreaker, setName:fSet, chaseType, description:fDesc.trim(), cards, active:true, updatedAt:new Date().toISOString() };
    if (editId) await setDoc(doc(db,"chases",editId), data, { merge:true });
    else        await setDoc(doc(db,"chases",uid()), { ...data, createdAt:new Date().toISOString() });
    setSaving(false); setShowForm(false); setEditId(null);
    setFHero(""); setFInsert(""); setFSet(""); setFDesc("");
  }

  async function toggleOwned(chaseId, cardId, owned) {
    const chase = chases.find(c=>c.id===chaseId);
    if (!chase) return;
    const cards = (chase.cards||[]).map(c => c.cardId===cardId ? {...c,owned:!owned} : c);
    await setDoc(doc(db,"chases",chaseId), { cards }, { merge:true });
  }

  async function deleteChase(id) {
    if (!window.confirm("Delete this chase?")) return;
    await deleteDoc(doc(db,"chases",id));
  }

  async function updateSub(id, status) {
    await setDoc(doc(db,"chase_submissions",id), { status }, { merge:true });
  }

  function startEdit(chase) {
    setFBreaker(chase.breaker||BREAKERS[0]);
    setFSet(chase.setName||"");
    setFDesc(chase.description||"");
    const ct = chase.chaseType||"";
    const parts = ct.replace(" Rainbow","").split(" ");
    // Detect mode: if ends with a weapon name it's treatment_weapon, if it's a hero it's hero, else treatment
    const lastWord = parts[parts.length-1];
    const isWeapon = WEAPON_ORDER.includes(lastWord);
    if (isWeapon && parts.length > 1) {
      setFMode("treatment_weapon");
      setFWeapon(lastWord);
      setFTreat(parts.slice(0,-1).join(" "));
      setFHero("");
    } else if (ct.endsWith("Rainbow") && !isWeapon) {
      // Could be hero or treatment — we'll default to hero if it matches a known pattern
      setFMode("hero"); setFHero(parts.join(" ")); setFWeapon(""); setFTreat("");
    } else {
      setFMode("treatment"); setFTreat(parts.join(" ")); setFHero(""); setFWeapon("");
    }
    setEditId(chase.id); setShowForm(true);
  }

  const WEAPON_COLORS = { Fire:"#F97316",Ice:"#38BDF8",Glow:"#4ade80",Hex:"#A78BFA",Gum:"#EC4899",Super:"#FBBF24",Steel:"#94A3B8",Brawl:"#EF4444" };
  const BC2 = { Dev:"#7B9CFF", Dre:"#C084FC", Krystal:"#2DD4BF", BigU:"#FB923C" };
  // Live lookup so old saves always show correct treatment/cardNum from database
  const cardLookup = useMemo(() => {
    const m = {};
    bobaCards.forEach(c=>{ if(c.id) m[c.id]=c; });
    return m;
  }, [bobaCards]);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14, padding:20 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:900, color:"var(--bz-ink)" }}>🎯 Chase Tracker</div>
          <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>Public at <a href="/chases" target="_blank" style={{ color:"#7B9CFF" }}>bazookadash.com/chases</a></div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <a href="/chases" target="_blank" rel="noreferrer" style={{ background:"rgba(123,156,255,0.1)", border:"1px solid rgba(123,156,255,0.3)", color:"#7B9CFF", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700, textDecoration:"none" }}>🔗 Public Page</a>
          <Btn onClick={()=>{ setShowForm(true); setEditId(null); setFBreaker(isAdmin?BREAKERS[0]:myBreaker); setFSet(""); setFHero(""); setFWeapon(""); setFTreat(""); setFDesc(""); setFMode("hero"); }}>+ New Chase</Btn>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ ...S.card }}>
          <SectionLabel t={editId?"✏️ Edit Chase":"+ New Chase"}/>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:10, marginBottom:14 }}>
            <div>
              <label style={S.lbl}>Breaker</label>
              <select value={fBreaker} onChange={e=>setFBreaker(e.target.value)} style={{ ...S.inp, cursor:"pointer" }}>
                {BREAKERS.map(b=><option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label style={S.lbl}>Set</label>
              <select value={fSet} onChange={e=>{ setFSet(e.target.value); setFHero(""); setFWeapon(""); setFTreat(""); }} style={{ ...S.inp, cursor:"pointer", color:fSet?"var(--bz-ink)":"#555" }}>
                <option value="">— Choose set —</option>
                {allSets.map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Chase mode pills */}
          <div style={{ marginBottom:14 }}>
            <label style={S.lbl}>Chase Type</label>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {CHASE_MODES.map(m=>(
                <button key={m.id} onClick={()=>{ setFMode(m.id); setFHero(""); setFWeapon(""); setFTreat(""); }}
                  title={m.desc}
                  style={{ background:fMode===m.id?"rgba(232,49,122,0.12)":"var(--bz-s1)", border:`1.5px solid ${fMode===m.id?"#E8317A":"var(--bz-line-2)"}`, color:fMode===m.id?"#E8317A":"#888", borderRadius:8, padding:"7px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                  {m.label}
                </button>
              ))}
            </div>
            {fMode && <div style={{ fontSize:10, color:"var(--bz-ink-3)", marginTop:5 }}>{CHASE_MODES.find(m=>m.id===fMode)?.desc}</div>}
          </div>

          {/* Contextual selectors */}
          {fSet && (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:10, marginBottom:14 }}>
              {fMode==="hero" && (
                <div>
                  <label style={S.lbl}>Hero</label>
                  <select value={fHero} onChange={e=>setFHero(e.target.value)} style={{ ...S.inp, cursor:"pointer", color:fHero?"var(--bz-ink)":"#555" }}>
                    <option value="">— Choose hero —</option>
                    {setHeroes.map(h=><option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              )}
              {(fMode==="treatment"||fMode==="treatment_weapon") && (
                <div>
                  <label style={S.lbl}>Treatment / Insert</label>
                  <select value={fTreat} onChange={e=>{ setFTreat(e.target.value); setFWeapon(""); }} style={{ ...S.inp, cursor:"pointer", color:fTreat?"var(--bz-ink)":"#555" }}>
                    <option value="">— Choose treatment —</option>
                    {setTreats.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              )}
              {fMode==="treatment_weapon" && fTreat && (
                <div>
                  <label style={S.lbl}>Weapon Type</label>
                  <select value={fWeapon} onChange={e=>setFWeapon(e.target.value)} style={{ ...S.inp, cursor:"pointer", color:fWeapon?"var(--bz-ink)":"#555" }}>
                    <option value="">— Choose weapon —</option>
                    {sortWeapons([...new Set(setCards.filter(c=>c.treatment===fTreat).map(c=>canonWeapon(c.weapon)).filter(Boolean))]).map(w=>(
                      <option key={w} value={w}>{w}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label style={S.lbl}>Notes (optional)</label>
                <input value={fDesc} onChange={e=>setFDesc(e.target.value)} placeholder="e.g. Still need the /10" style={S.inp}/>
              </div>
            </div>
          )}

          {/* Preview */}
          {previewCards.length > 0 && (
            <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line)", borderRadius:10, padding:"12px 14px", marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:"#4ade80", marginBottom:10 }}>
                ✓ {previewCards.length} cards found from {fSet} database
              </div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {previewCards.map(c=>{
                  const wc = WEAPON_COLORS[canonWeapon(c.weapon)]||"#888";
                  const label = [c.hero, c.weapon, c.treatment, c.cardNum?"#"+c.cardNum:""].filter(Boolean).join(" — ");
                  return (
                    <div key={c.id} style={{ display:"flex", alignItems:"center", gap:5, background:"var(--bz-s1)", border:`1px solid ${wc}22`, borderRadius:6, padding:"4px 10px" }}>
                      <div style={{ width:6, height:6, borderRadius:"50%", background:wc, flexShrink:0 }}/>
                      <span style={{ fontSize:11, color:"var(--bz-ink-2)" }}>{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {fSet && ((fMode==="hero"&&!fHero)||(fMode==="insert"&&!fInsert)) && (
            <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginBottom:14 }}>← Choose a {fMode==="hero"?"hero":"weapon type"} to see the cards</div>
          )}

          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={saveChase} disabled={!previewCards.length||saving}>{saving?"Saving...":"💾 Save Chase"}</Btn>
            <Btn variant="ghost" onClick={()=>{ setShowForm(false); setEditId(null); }}>Cancel</Btn>
          </div>
        </div>
      )}

      {/* Chase list */}
      {myChases.length===0 && !showForm && (
        <div style={{ ...S.card, textAlign:"center", padding:"40px 20px" }}>
          <div style={{ fontSize:32, opacity:0.3, marginBottom:10 }}>🎯</div>
          <div style={{ fontSize:14, fontWeight:700, color:"var(--bz-ink-3)", marginBottom:10 }}>No chases yet</div>
          <Btn onClick={()=>{ setShowForm(true); setFBreaker(isAdmin?BREAKERS[0]:myBreaker); setFSet(""); setFHero(""); setFWeapon(""); setFTreat(""); setFDesc(""); setFMode("hero"); }}>+ Create First Chase</Btn>
        </div>
      )}

      {myChases.map(chase => {
        const cards   = chase.cards||[];
        const needed  = cards.filter(c=>!c.owned);
        const owned2  = cards.filter(c=>c.owned);
        const pct     = cards.length ? (owned2.length/cards.length*100) : 0;
        const subs    = submissions.filter(s=>s.chaseId===chase.id);
        const pending = subs.filter(s=>s.status==="pending");
        const isOpen  = activeChase===chase.id;
        const bc      = BC2[chase.breaker]||"#E8317A";

        return (
          <div key={chase.id} style={{ ...S.card, borderLeft:`3px solid ${bc}` }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10, marginBottom:12 }}>
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <span style={{ fontSize:15, fontWeight:900, color:"var(--bz-ink)" }}>{chase.setName}</span>
                  <span style={{ fontSize:11, color:bc, background:`${bc}18`, borderRadius:5, padding:"2px 8px", fontWeight:700 }}>{chase.breaker}</span>
                  <span style={{ fontSize:11, color:"var(--bz-ink-2)", background:"var(--bz-s2)", borderRadius:5, padding:"2px 8px" }}>{chase.chaseType}</span>
                  {pending.length>0&&<span style={{ background:"rgba(232,49,122,0.15)", border:"1px solid rgba(232,49,122,0.3)", color:"#E8317A", borderRadius:10, padding:"1px 8px", fontSize:11, fontWeight:700 }}>🔔 {pending.length} new</span>}
                </div>
                {chase.description&&<div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:3 }}>{chase.description}</div>}
              </div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <span style={{ fontSize:13, fontWeight:800, color:pct===100?"#4ade80":pct>=50?"#FBBF24":"#E8317A" }}>{owned2.length}/{cards.length}</span>
                <Btn variant="ghost" onClick={()=>startEdit(chase)}>✏️</Btn>
                <Btn variant="red" onClick={()=>deleteChase(chase.id)}>Delete</Btn>
              </div>
            </div>

            {/* Rainbow progress */}
            <div style={{ background:"var(--bz-s2)", borderRadius:99, height:8, overflow:"hidden", position:"relative", marginBottom:8 }}>
              <div style={{ position:"absolute", inset:0, background:"linear-gradient(90deg,#94A3B8,#EF4444,#F97316,#38BDF8,#4ade80,#A78BFA,#EC4899,#FBBF24)", opacity:0.12, borderRadius:99 }}/>
              <div style={{ width:`${pct}%`, height:"100%", background:"linear-gradient(90deg,#94A3B8,#EF4444,#F97316,#38BDF8,#4ade80,#A78BFA,#EC4899,#FBBF24)", borderRadius:99, transition:"width 0.6s cubic-bezier(0.22,1,0.36,1)" }}/>
            </div>

            <button onClick={()=>setActiveChase(isOpen?null:chase.id)}
              style={{ background:"none", border:"none", color:"var(--bz-ink-3)", cursor:"pointer", fontSize:11, fontFamily:"inherit", padding:"0 0 10px" }}>
              {isOpen?"▲ Hide":"▼ Show"} {needed.length} needed · {owned2.length} owned
            </button>

            {/* Card grid — checklist style */}
            {isOpen && (() => {
              const sortKey = chaseSorts[chase.id] || "weapon";
              const WEAPON_ORDER2 = ["Steel","Brawl","Fire","Ice","Glow","Hex","Gum","Super"];
              const sorted = [...cards].sort((a,b) => {
                const la = cardLookup[a.cardId]||{};
                const lb = cardLookup[b.cardId]||{};
                const wa = a.weapon||la.weapon||""; const wb = b.weapon||lb.weapon||"";
                const ta = a.treatment||la.treatment||""; const tb = b.treatment||lb.treatment||"";
                const pa = la.power||0; const pb = lb.power||0;
                const ha = a.hero||la.hero||""; const hb = b.hero||lb.hero||"";
                if (sortKey==="weapon")    return WEAPON_ORDER2.indexOf(wa)-WEAPON_ORDER2.indexOf(wb) || ha.localeCompare(hb);
                if (sortKey==="treatment") return ta.localeCompare(tb) || WEAPON_ORDER2.indexOf(wa)-WEAPON_ORDER2.indexOf(wb);
                if (sortKey==="power")     return pb-pa || ha.localeCompare(hb);
                if (sortKey==="hero")      return ha.localeCompare(hb) || WEAPON_ORDER2.indexOf(wa)-WEAPON_ORDER2.indexOf(wb);
                return 0;
              });
              return (
                <>
                  <div style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap" }}>
                    {[["weapon","⚔️ Weapon"],["treatment","✨ Treatment"],["power","💪 Power"],["hero","🦸 Hero"]].map(([k,l])=>(
                      <button key={k} onClick={()=>setChaseSorts(p=>({...p,[chase.id]:k}))}
                        style={{ background:sortKey===k?"rgba(232,49,122,0.12)":"var(--bz-s1)", border:`1px solid ${sortKey===k?"#E8317A":"var(--bz-line-2)"}`, color:sortKey===k?"#E8317A":"#555", borderRadius:6, padding:"4px 10px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                        {l}
                      </button>
                    ))}
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:6 }}>
                    {sorted.map(card=>{
                  // Merge live DB data so treatment/cardNum always shows even on old saves
                  const live = cardLookup[card.cardId] || {};
                  const treatment = card.treatment || live.treatment || "";
                  const cardNum   = card.cardNum   || live.cardNum   || "";
                  const imageUrl  = card.imageUrl  || live.imageUrl  || live.imageUrl || null;
                  const weapon    = card.weapon    || live.weapon    || "";
                  const hero      = card.hero      || live.hero      || card.name;
                  const wc = WEAPON_COLORS[weapon]||"#888";
                  return (
                    <div key={card.cardId||card.name} onClick={()=>toggleOwned(chase.id,card.cardId,card.owned)}
                      style={{ display:"flex", alignItems:"center", gap:10, background:card.owned?"rgba(74,222,128,0.05)":"var(--bz-s1)", border:`1px solid ${card.owned?"rgba(74,222,128,0.2)":`${wc}33`}`, borderRadius:10, padding:"10px", cursor:"pointer", transition:"all 0.12s ease", position:"relative", overflow:"hidden" }}
                      className="inv-row">
                      {/* Owned overlay */}
                      {card.owned && <div style={{ position:"absolute", inset:0, background:"rgba(74,222,128,0.04)", borderRadius:10, pointerEvents:"none" }}/>}

                      {/* Card image */}
                      <div style={{ width:52, height:72, flexShrink:0, borderRadius:6, overflow:"hidden", background:"var(--bz-s2)", border:`1px solid ${wc}33`, position:"relative" }}>
                        {imageUrl
                          ? <img src={imageUrl} alt={hero} style={{ width:"100%", height:"100%", objectFit:"cover", opacity:card.owned?0.5:1, filter:card.owned?"grayscale(0.3)":"none", transition:"opacity 0.2s" }}/>
                          : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center" }}>
                              <div style={{ width:20, height:20, borderRadius:"50%", background:`${wc}33`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:wc, fontWeight:900 }}>{(weapon||"?")[0]}</div>
                            </div>}
                        {card.owned && (
                          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(74,222,128,0.15)" }}>
                            <div style={{ fontSize:20, color:"#4ade80", fontWeight:900 }}>✓</div>
                          </div>
                        )}
                      </div>
                      {/* Card info */}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:800, color:card.owned?"#4ade80":"var(--bz-ink)", textDecoration:card.owned?"line-through":"none", opacity:card.owned?0.7:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginBottom:5 }}>
                          {hero}
                        </div>
                        <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                          {weapon && <span style={{ fontSize:10, fontWeight:800, color:wc, background:`${wc}18`, borderRadius:4, padding:"2px 7px" }}>{weapon}</span>}
                          {treatment && <span style={{ fontSize:10, color:"var(--bz-ink-2)", background:"var(--bz-s2)", borderRadius:4, padding:"2px 7px" }}>{treatment}</span>}
                        </div>
                        {cardNum && <div style={{ fontSize:10, color:"#8a8a8a", marginTop:3 }}>#{cardNum}</div>}
                      </div>

                      {/* Checkbox */}
                      <div style={{ width:20, height:20, borderRadius:5, border:`2px solid ${card.owned?"#4ade80":wc}`, background:card.owned?"#4ade80":"transparent", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, flexShrink:0, color:"#000", fontWeight:900 }}>
                        {card.owned&&"✓"}
                      </div>
                    </div>
                  );
                })}
                  </div>
                </>
              );
            })()}

            {/* Submissions */}
            {subs.length>0 && (
              <div style={{ marginTop:14, borderTop:"1px solid var(--bz-line)", paddingTop:12 }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#E8317A", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Submissions ({subs.length})</div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {subs.sort((a,b)=>new Date(b.submittedAt)-new Date(a.submittedAt)).map(sub=>(
                    <div key={sub.id} style={{ background:"var(--bz-bg)", border:`1px solid ${sub.status==="pending"?"rgba(232,49,122,0.2)":sub.status==="acquired"?"rgba(74,222,128,0.15)":"var(--bz-s2)"}`, borderRadius:8, padding:"10px 14px" }}>
                      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:10 }}>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)" }}>{sub.cardName}</div>
                          <div style={{ fontSize:11, color:"#7B9CFF", marginTop:2 }}>Discord: {sub.discord||"—"}</div>
                          {sub.message&&<div style={{ fontSize:11, color:"var(--bz-ink-2)", marginTop:2 }}>{sub.message}</div>}
                          {sub.photoUrl&&<a href={sub.photoUrl} target="_blank" rel="noreferrer" style={{ fontSize:11, color:"#E8317A", marginTop:4, display:"block" }}>📷 View photo</a>}
                        </div>
                        <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                          {sub.status==="pending"&&<>
                            <button onClick={()=>updateSub(sub.id,"contacted")} style={{ background:"rgba(123,156,255,0.1)", border:"1px solid rgba(123,156,255,0.3)", color:"#7B9CFF", borderRadius:6, padding:"3px 10px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Contacted</button>
                            <button onClick={()=>updateSub(sub.id,"acquired")} style={{ background:"rgba(74,222,128,0.1)", border:"1px solid rgba(74,222,128,0.3)", color:"#4ade80", borderRadius:6, padding:"3px 10px", fontSize:10, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Acquired ✓</button>
                          </>}
                          {sub.status!=="pending"&&<span style={{ fontSize:10, color:sub.status==="acquired"?"#4ade80":"#7B9CFF", fontWeight:700 }}>{sub.status}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PublicPrivacyPolicy() {
  const S = {
    wrap:{ minHeight:"100vh", background:"#08000a", color:"#e8e8e8", fontFamily:"'Trebuchet MS',-apple-system,sans-serif" },
    inner:{ maxWidth:760, margin:"0 auto", padding:"48px 24px 80px" },
    h1:{ fontSize:34, fontWeight:900, color:"#fff", marginBottom:6, letterSpacing:-0.5 },
    updated:{ fontSize:13, color:"rgba(255,255,255,0.4)", marginBottom:36 },
    h2:{ fontSize:18, fontWeight:800, color:"#E8317A", margin:"32px 0 10px" },
    p:{ fontSize:15, lineHeight:1.7, color:"rgba(255,255,255,0.75)", marginBottom:12 },
    li:{ fontSize:15, lineHeight:1.7, color:"rgba(255,255,255,0.75)", marginBottom:8 },
    a:{ color:"#E8317A", fontWeight:700, textDecoration:"none" },
    back:{ display:"inline-block", marginBottom:28, color:"#E8317A", fontWeight:700, textDecoration:"none", fontSize:14 },
  };
  return (
    <div style={S.wrap}>
      <div style={S.inner}>
        <a href="/cards" style={S.back}>← Back to Bazooka</a>
        <h1 style={S.h1}>Privacy Policy</h1>
        <div style={S.updated}>Last updated: June 15, 2026</div>

        <h2 style={S.h2}>1. Introduction / Who We Are</h2>
        <p style={S.p}>Bazooka ("Bazooka Vault," "we," "us," or "our") is operated by Bazooka Breaks LLC. This Privacy Policy explains how we collect, use, and protect your information when you use our website, the Bazooka Vault collection platform, and related services (the "Service").</p>

        <h2 style={S.h2}>2. Information We Collect</h2>
        <p style={S.p}>We may collect the following information:</p>
        <ul>
          <li style={S.li}><strong>Account data:</strong> when you sign in with Google, we receive your name, email address, and Google profile identifier. We do not see or store your Google password.</li>
          <li style={S.li}><strong>Collection data:</strong> the cards, quantities, wants, decks, costs, values, and acquisition details you add to your account.</li>
          <li style={S.li}><strong>Marketplace & sale submissions:</strong> listings, offers, messages, and sale details you create, including payment handles (e.g., PayPal or Zelle usernames) you choose to share to complete a transaction.</li>
          <li style={S.li}><strong>Sell-page submissions:</strong> name, contact info, and card details you provide when submitting cards for a quote.</li>
          <li style={S.li}><strong>Usage data:</strong> basic technical information such as device and browser type, and activity within the Service used to operate and improve it.</li>
        </ul>

        <h2 style={S.h2}>3. How We Use Your Information</h2>
        <p style={S.p}>We use your information to operate and improve the Service, maintain your collection and account, enable community and marketplace features, respond to inquiries and quote submissions, send account-related communications, and comply with legal obligations.</p>

        <h2 style={S.h2}>4. Payments Between Users</h2>
        <p style={S.p}>Bazooka does not process payments directly. Marketplace and sale transactions are arranged peer-to-peer between users using third-party payment services such as PayPal or Zelle. Any payment handle you share is provided to the other party to complete a transaction. We do not collect, store, or have access to your full financial account numbers, and we are not a party to transactions between users. Your use of any third-party payment service is governed by that provider's own terms and privacy policy.</p>

        <h2 style={S.h2}>5. Third-Party Services</h2>
        <p style={S.p}>We rely on trusted third-party services to operate the Service, including Google Firebase (authentication, database, and file storage) and Google Sign-In. These providers process data on our behalf and maintain their own privacy policies governing their use of that data. Marketplace transactions may involve third-party payment services as described in Section 4.</p>

        <h2 style={S.h2}>6. Cookies and Similar Technologies</h2>
        <p style={S.p}>We use cookies and similar browser technologies that are necessary to operate the Service, such as keeping you signed in and remembering your place in the app. If we add analytics or advertising technologies in the future, we will update this policy accordingly.</p>

        <h2 style={S.h2}>7. Data Retention and Security</h2>
        <p style={S.p}>We retain your data for as long as your account is active or as needed to provide the Service and fulfill the purposes described in this policy. We use reasonable technical and organizational measures to protect your information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.</p>

        <h2 style={S.h2}>8. Your Rights</h2>
        <p style={S.p}>Depending on where you live, you may have the right to access, correct, delete, or export your personal data, or to object to or restrict certain processing. To exercise these rights, please contact us using the information below. If you are located in the European Economic Area, the United Kingdom, or California, additional disclosures and rights may apply.</p>

        <h2 style={S.h2}>9. Children's Privacy</h2>
        <p style={S.p}>The Service is not directed to children under 13, and we do not knowingly collect personal information from children under 13. If you believe a child under 13 has provided us personal information, please contact us so we can remove it.</p>

        <h2 style={S.h2}>10. Changes to This Policy</h2>
        <p style={S.p}>We may update this Privacy Policy from time to time. We will post the updated version on this page and revise the "Last updated" date above. Your continued use of the Service after changes take effect constitutes acceptance of the updated policy.</p>

        <h2 style={S.h2}>11. Contact</h2>
        <p style={S.p}>For privacy-related questions or to exercise your rights, please contact us at <a href="mailto:support@bazookabreaks.com" style={S.a}>support@bazookabreaks.com</a>.</p>
      </div>
    </div>
  );
}

function PublicSellPage() {
  const [seller, setSeller]   = useState({ name:"", contact:"", source:"Discord", payment:"", paymentHandle:"" });
  const [rows, setRows]       = useState([{ id:uid(), name:"", qty:"1", askingPrice:"" }]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [quoteLink, setQuoteLink]   = useState("");
  const [copied, setCopied]         = useState(false);
  const [error, setError]           = useState("");
  const [acOpen, setAcOpen]         = useState(null);
  const [acQuery, setAcQuery]       = useState({});
  const [bobaCards, setBobaCards]   = useState([]);
  const [photos, setPhotos]         = useState([]); // {file, preview, url}
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    getDocs(collection(db, "boba_checklist")).then(snap => {
      setBobaCards(snap.docs.map(d=>({...d.data(), id:d.id})));
    }).catch(()=>{});
  }, []);

  function updRow(id,f,v) { setRows(p=>p.map(r=>r.id===id?{...r,[f]:v}:r)); }
  function addRow() { setRows(p=>[...p,{id:uid(),name:"",qty:"1",askingPrice:""}]); }
  function removeRow(id) { setRows(p=>p.filter(r=>r.id!==id)); }

  function handlePhotoSelect(e) {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (!file.type.startsWith("image/")) return;
      setPhotos(p => {
        if (p.length >= 4) return p;
        const reader = new FileReader();
        reader.onload = ev => setPhotos(prev => prev.length >= 4 ? prev : [...prev, { file, preview:ev.target.result, url:null }]);
        reader.readAsDataURL(file);
        return p;
      });
    });
    e.target.value = "";
  }

  function removePhoto(idx) { setPhotos(p=>p.filter((_,i)=>i!==idx)); }

  function compressPhoto(file) {
    return new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX = 800;
        let w = img.width, h = img.height;
        if (w > MAX) { h = Math.round(h * MAX / w); w = MAX; }
        if (h > MAX) { w = Math.round(w * MAX / h); h = MAX; }
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.src = url;
    });
  }

  async function uploadPhotos(qId) {
    const urls = [];
    for (let i=0; i<photos.length; i++) {
      const compressed = await compressPhoto(photos[i].file);
      urls.push(compressed);
      setUploadProgress(Math.round((i+1)/photos.length*100));
    }
    return urls;
  }

  async function handleSubmit() {
    if (!seller.name.trim() || !seller.contact.trim()) { setError("Please fill in your name and contact info."); return; }
    const validRows = rows.filter(r=>r.name.trim());
    if (validRows.length === 0) { setError("Please add at least one card."); return; }
    setError(""); setSubmitting(true);
    try {
      const qId = uid();
      const cards = validRows.map(r=>({ cardName:r.name.trim(), name:r.name.trim(), cardType:"", qty:parseInt(r.qty)||1, mktVal:parseFloat(r.askingPrice)||0 }));
      // Upload photos to Firebase Storage
      let photoUrls = [];
      if (photos.length > 0) {
        try { photoUrls = await uploadPhotos(qId); } catch(pe) { console.warn("Photo upload failed", pe); }
      }
      await setDoc(doc(db,"quotes",qId), {
        id: qId,
        // Seller PII intentionally lives in quotes_private/{qId} (create-only for the
        // submitter, admin-read) so the public /quote/<id> doc carries no name, contact,
        // or payment handle. Keep a display source only.
        cards,
        photoUrls,
        status: "pending",
        source: seller.source,
        dispOffer: 0,
        currentOffer: 0,
        allowCounter: true,
        createdAt: new Date().toISOString(),
        notified: false,
        submittedBySeller: true,
        hasPrivate: true,
      });
      // Seller contact/payment — written once, never exposed on the public link.
      try {
        await setDoc(doc(db,"quotes_private",qId), {
          quoteId: qId,
          seller: { name:seller.name.trim(), contact:seller.contact.trim(), source:seller.source, payment:seller.payment, paymentHandle:seller.paymentHandle },
          createdAt: new Date().toISOString(),
        });
      } catch(pe) { console.warn("Private quote write failed", pe); }
      const link = `${window.location.origin}/quote/${qId}`;
      setQuoteLink(link);
      setSubmitted(true);
    } catch(e) {
      setError("Something went wrong. Please try again or contact us on Discord.");
    }
    setSubmitting(false);
  }

  const S2 = {
    inp: { background:"rgba(0,0,0,0.4)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:10, color:"var(--bz-ink)", padding:"12px 15px", fontSize:14, fontFamily:"inherit", outline:"none", width:"100%", boxSizing:"border-box", transition:"border-color 0.15s" },
    lbl: { fontSize:10.5, fontWeight:800, color:"rgba(255,255,255,0.45)", textTransform:"uppercase", letterSpacing:1.2, display:"block", marginBottom:7 },
  };

  if (submitted) return (
    <div style={{ minHeight:"100vh", background:"#000", fontFamily:"'Trebuchet MS','Segoe UI',sans-serif", color:"var(--bz-ink)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <style>{`:root{ --bz-bg:#0b0709; --bz-s1:#130d11; --bz-s2:#1a1218; --bz-s3:#241820; --bz-line:rgba(255,255,255,0.06); --bz-line-2:rgba(255,255,255,0.11); --bz-ink:#f6eef2; --bz-ink-2:#b9a9b2; --bz-ink-3:#7a6b74; --bz-pink:#E8317A; --bz-pink-hot:#ff4d94; --bz-green:#34d399; --bz-amber:#fbbf24; --bz-blue:#7c9cff; --bz-violet:#a78bfa; }`}</style>
      <div style={{ maxWidth:520, width:"100%", textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🎉</div>
        <div style={{ fontSize:24, fontWeight:900, color:"#4ade80", marginBottom:8 }}>Lot Submitted!</div>
        <div style={{ fontSize:14, color:"var(--bz-ink-3)", marginBottom:24 }}>We'll review your cards and send you an offer soon. Bookmark or copy the link below to check your quote status anytime.</div>
        <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"14px 18px", marginBottom:16, wordBreak:"break-all", fontSize:13, color:"#7B9CFF" }}>{quoteLink}</div>
        <button onClick={()=>{ navigator.clipboard?.writeText(quoteLink); setCopied(true); setTimeout(()=>setCopied(false),2500); }}
          style={{ background:"#4ade80", color:"#000", border:"none", borderRadius:10, padding:"12px 32px", fontSize:15, fontWeight:900, cursor:"pointer", fontFamily:"inherit" }}>
          {copied?"✅ Copied!":"📋 Copy My Quote Link"}
        </button>
        <div style={{ marginTop:16, fontSize:12, color:"var(--bz-ink-3)" }}>You'll receive an offer at the link above once we've reviewed your lot.</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:"radial-gradient(ellipse 90% 50% at 50% 0%, rgba(232,49,122,0.12), transparent 60%), radial-gradient(ellipse 70% 50% at 80% 30%, rgba(123,47,247,0.1), transparent 60%), #08000a", fontFamily:"'Trebuchet MS','Segoe UI',sans-serif", color:"var(--bz-ink)", padding:"40px 16px 60px" }}>
      <style>{`:root{
        --bz-bg:#0b0709; --bz-s1:#130d11; --bz-s2:#1a1218; --bz-s3:#241820;
        --bz-line:rgba(255,255,255,0.06); --bz-line-2:rgba(255,255,255,0.11);
        --bz-ink:#f6eef2; --bz-ink-2:#b9a9b2; --bz-ink-3:#7a6b74;
        --bz-pink:#E8317A; --bz-pink-hot:#ff4d94; --bz-pink-dim:rgba(232,49,122,0.14); --bz-pink-line:rgba(232,49,122,0.30);
        --bz-green:#34d399; --bz-amber:#fbbf24; --bz-blue:#7c9cff; --bz-violet:#a78bfa;
        --bz-radius:16px; --bz-radius-sm:11px;
      }`}</style>
      <div style={{ maxWidth:680, margin:"0 auto" }}>

        {/* Header */}
        <div style={{ position:"relative", background:"linear-gradient(160deg, rgba(232,49,122,0.08), rgba(123,47,247,0.05) 60%, rgba(255,255,255,0.02))", borderRadius:24, padding:"40px 32px 34px", marginBottom:24, textAlign:"center", border:"1px solid rgba(232,49,122,0.18)", overflow:"hidden", boxShadow:"0 20px 60px rgba(232,49,122,0.12)" }}>
          <div style={{ position:"absolute", top:-60, left:"50%", transform:"translateX(-50%)", width:300, height:120, background:"radial-gradient(ellipse, rgba(232,49,122,0.35), transparent 70%)", pointerEvents:"none" }}/>
          <img src="/Bazooka_Logo_cropped.png" alt="Bazooka" style={{ position:"relative", height:"clamp(52px,9vw,76px)", width:"auto", maxWidth:"82vw", objectFit:"contain", display:"block", margin:"0 auto 14px", filter:"drop-shadow(0 4px 20px rgba(232,49,122,0.45))" }}/>
          <div style={{ position:"relative", fontSize:12, fontWeight:800, color:"#E8317A", letterSpacing:2.5, textTransform:"uppercase", marginBottom:14 }}>Bo Jackson Battle Arena · Sell Your Cards</div>
          <div style={{ position:"relative", fontSize:15, color:"#C8C8C8", lineHeight:1.7, maxWidth:480, margin:"0 auto" }}>Fill out the form below with your cards and we'll send you an offer. <span style={{color:"#fff",fontWeight:700}}>No obligation</span> — you can accept, decline, or counter.</div>
        </div>

        {/* Seller info */}
        <div style={{ background:"rgba(255,255,255,0.025)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:16, padding:"24px 26px", marginBottom:16, backdropFilter:"blur(8px)" }}>
          <div style={{ fontSize:15, fontWeight:800, color:"#fff", marginBottom:18, display:"flex", alignItems:"center", gap:8 }}>{String.fromCodePoint(0x1F464)} Your Info</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <div>
              <label style={S2.lbl}>Name / Username *</label>
              <input value={seller.name} onChange={e=>setSeller(p=>({...p,name:e.target.value}))} placeholder="WhatNot or Discord username" style={S2.inp}/>
            </div>
            <div>
              <label style={S2.lbl}>Contact (email or Discord) *</label>
              <input value={seller.contact} onChange={e=>setSeller(p=>({...p,contact:e.target.value}))} placeholder="email or Discord handle" style={S2.inp}/>
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div>
              <label style={S2.lbl}>Preferred Payment</label>
              <select value={seller.payment} onChange={e=>setSeller(p=>({...p,payment:e.target.value}))} style={{ ...S2.inp, cursor:"pointer", color:seller.payment?"var(--bz-ink)":"#555" }}>
                <option value="">Select method...</option>
                {["PayPal","Zelle"].map(m=><option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={S2.lbl}>Payment Handle (optional)</label>
              <input value={seller.paymentHandle} onChange={e=>setSeller(p=>({...p,paymentHandle:e.target.value}))} placeholder={seller.payment==="PayPal"?"email or username":seller.payment==="Zelle"?"email or phone number":"email or account info"} style={S2.inp}/>
            </div>
          </div>
        </div>

        {/* Card list */}
        <div style={{ background:"rgba(255,255,255,0.025)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:16, padding:"24px 26px", marginBottom:16, backdropFilter:"blur(8px)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:15, fontWeight:800, color:"#fff", display:"flex", alignItems:"center", gap:8 }}>{String.fromCodePoint(0x1F0CF)} Your Cards</div>
            <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>Search by hero name, card #, or treatment</div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"3fr 1fr 1fr auto", gap:8, marginBottom:8 }}>
            {["Card Name","Qty","Your Asking Price",""].map(h=><div key={h} style={{ fontSize:10, fontWeight:700, color:"#8a8a8a", textTransform:"uppercase", letterSpacing:1 }}>{h}</div>)}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {rows.map((r,i)=>(
              <div key={r.id} style={{ display:"grid", gridTemplateColumns:"3fr 1fr 1fr auto", gap:8, alignItems:"start" }}>
                <div style={{ position:"relative" }}>
                  <input
                    value={acOpen===r.id?(acQuery[r.id]??r.name):r.name}
                    onChange={e=>{ setAcOpen(r.id); setAcQuery(q=>({...q,[r.id]:e.target.value})); updRow(r.id,"name",e.target.value); }}
                    onFocus={()=>{ setAcOpen(r.id); setAcQuery(q=>({...q,[r.id]:r.name})); }}
                    onBlur={()=>setTimeout(()=>setAcOpen(p=>p===r.id?null:p),150)}
                    placeholder="e.g. Bo Jackson Base #1"
                    style={S2.inp}
                  />
                  {acOpen===r.id&&(acQuery[r.id]||"").length>=1&&(()=>{
                    const raw=(acQuery[r.id]||"").toLowerCase();
                    const terms=raw.trim().split(/\s+/).filter(Boolean);
                    const hits=bobaCards.filter(c=>terms.every(t=>[c.hero||"",c.weapon||"",c.treatment||"",String(c.cardNum||""),c.notation||"",c.setName||""].join(" ").toLowerCase().includes(t))).slice(0,10);
                    if(!hits.length) return null;
                    return (
                      <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"var(--bz-s1)", border:"1px solid var(--bz-line-2)", borderRadius:8, zIndex:999, boxShadow:"0 8px 24px rgba(0,0,0,0.8)", maxHeight:260, overflowY:"auto" }}>
                        <div style={{ padding:"4px 10px", fontSize:10, color:"var(--bz-ink-3)", borderBottom:"1px solid var(--bz-line)" }}>{hits.length} match{hits.length!==1?"es":""}</div>
                        {hits.map(c=>{
                          const wc=PUBLIC_WEAPON_COLORS[canonWeapon(c.weapon)]||"#888";
                          const label=[c.hero,c.treatment,c.weapon?"("+c.weapon+")":"",c.cardNum?"#"+c.cardNum:""].filter(Boolean).join(" — ");
                          return (
                            <div key={c.id} onMouseDown={()=>{ updRow(r.id,"name",label); setAcOpen(null); }}
                              style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", cursor:"pointer", borderBottom:"1px solid #111" }}
                              className="inv-row">
                              {c.imageUrl
                                ? <img src={c.imageUrl} alt={c.hero} style={{ width:32, height:42, objectFit:"cover", borderRadius:4, flexShrink:0 }}/>
                                : <div style={{ width:32, height:42, background:"var(--bz-line-2)", borderRadius:4, flexShrink:0 }}/>
                              }
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)" }}>{c.hero}</div>
                                <div style={{ fontSize:11, color:"var(--bz-ink-3)" }}>
                                  {c.treatment&&<span style={{ marginRight:8 }}>{c.treatment}</span>}
                                  {c.weapon&&<span style={{ color:wc, fontWeight:700, marginRight:8 }}>{c.weapon}</span>}
                                  {c.cardNum&&<span>#{c.cardNum}</span>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                <input type="number" min="1" value={r.qty} onChange={e=>updRow(r.id,"qty",e.target.value)} placeholder="1" style={S2.inp}/>
                <input type="number" step="0.01" value={r.askingPrice} onChange={e=>updRow(r.id,"askingPrice",e.target.value)} placeholder="0.00" style={{ ...S2.inp, color:"#4ade80" }}/>
                <button onClick={()=>removeRow(r.id)} disabled={rows.length===1}
                  style={{ background:"none", border:"none", color:rows.length===1?"#222":"#555", cursor:rows.length===1?"default":"pointer", fontSize:18, padding:"10px 4px", lineHeight:1 }}>✕</button>
              </div>
            ))}
          </div>
          <button onClick={addRow} style={{ marginTop:12, background:"transparent", border:"1.5px dashed var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:8, padding:"10px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit", width:"100%" }}>
            + Add Another Card
          </button>
        </div>

        {/* Notes */}
        <div style={{ background:"rgba(255,255,255,0.025)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:16, padding:"20px 26px", marginBottom:16, backdropFilter:"blur(8px)" }}>
          <label style={S2.lbl}>Condition Notes (optional)</label>
          <textarea value={seller.notes||""} onChange={e=>setSeller(p=>({...p,notes:e.target.value}))} placeholder="Any condition notes, damage, or anything else we should know about..." rows={3}
            style={{ ...S2.inp, resize:"vertical", lineHeight:1.5 }}/>
        </div>

        {/* Photo Upload */}
        <div style={{ background:"rgba(255,255,255,0.025)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:16, padding:"20px 26px", marginBottom:16, backdropFilter:"blur(8px)" }}>
          <label style={S2.lbl}>📸 Lot Photos (optional but recommended)</label>
          <div style={{ fontSize:13, color:"var(--bz-ink-3)", marginBottom:12 }}>Take a photo of your lot spread out so we can see the cards. Helps us make a faster and more accurate offer.</div>
          {/* Upload button */}
          <label style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(232,49,122,0.1)", border:"1px dashed rgba(232,49,122,0.4)", borderRadius:10, padding:"12px 20px", cursor: photos.length >= 4 ? "not-allowed" : "pointer", fontSize:14, fontWeight:700, color: photos.length >= 4 ? "#555" : "#E8317A", opacity: photos.length >= 4 ? 0.5 : 1 }}>
            📷 Add Photos
            <input type="file" accept="image/*" multiple onChange={handlePhotoSelect} disabled={photos.length >= 4} style={{ display:"none" }}/>
          </label>
          <span style={{ fontSize:11, color:"var(--bz-ink-3)", marginLeft:12 }}>{photos.length}/4 photos · Works with camera or photo library</span>
          {/* Photo previews */}
          {photos.length > 0 && (
            <div style={{ display:"flex", flexWrap:"wrap", gap:10, marginTop:14 }}>
              {photos.map((p,i)=>(
                <div key={i} style={{ position:"relative", width:100, height:100 }}>
                  <img src={p.preview} alt="" style={{ width:100, height:100, objectFit:"cover", borderRadius:8, border:"1px solid var(--bz-line-2)" }}/>
                  <button onClick={()=>removePhoto(i)}
                    style={{ position:"absolute", top:-6, right:-6, width:20, height:20, borderRadius:"50%", background:"#E8317A", border:"none", color:"#fff", fontSize:12, fontWeight:900, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>✕</button>
                </div>
              ))}
              {photos.length < 4 && (
                <label style={{ width:100, height:100, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", border:"1px dashed var(--bz-line-2)", borderRadius:8, cursor:"pointer", color:"var(--bz-ink-3)", fontSize:12, gap:4 }}>
                  <span style={{ fontSize:24 }}>+</span>Add more
                  <input type="file" accept="image/*" multiple onChange={handlePhotoSelect} style={{ display:"none" }}/>
                </label>
              )}
            </div>
          )}
        </div>

        {error && <div style={{ background:"#1a0a0a", border:"1px solid #E8317A44", borderRadius:8, padding:"10px 14px", marginBottom:12, fontSize:13, color:"#E8317A" }}>{error}</div>}

        <button onClick={handleSubmit} disabled={submitting}
          style={{ width:"100%", background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff", border:"none", borderRadius:14, padding:"19px 0", fontSize:18, fontWeight:900, cursor:submitting?"not-allowed":"pointer", fontFamily:"inherit", opacity:submitting?0.7:1, boxShadow:"0 12px 40px rgba(232,49,122,0.4)", transition:"transform 0.2s, box-shadow 0.2s", letterSpacing:0.3 }}
          onMouseEnter={e=>{if(!submitting){e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 18px 52px rgba(232,49,122,0.55)";}}}
          onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="0 12px 40px rgba(232,49,122,0.4)";}}>
          {submitting ? (uploadProgress > 0 ? `Uploading photos... ${uploadProgress}%` : "Submitting...") : "🎯 Submit My Cards for an Offer"}
        </button>

        <div style={{ marginTop:16, textAlign:"center", fontSize:12, color:"var(--bz-ink-3)" }}>
          By submitting you agree to have Bazooka Breaks review your cards. No obligation to sell.
        </div>
      </div>
    </div>
  );
}

function PublicQuote({ quoteId }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState("");
  const [counterAmt, setCounterAmt] = useState("");
  const [payment, setPayment] = useState("");
  const [paymentHandle, setPaymentHandle] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!quoteId) { setLoading(false); return; }
    getDoc(doc(db, "quotes", quoteId)).then(snap => {
      if (snap.exists()) {
        setQuote({ id: snap.id, ...snap.data() });
        // Track view — best effort, never let a denied write break the page
        setDoc(doc(db, "quotes", quoteId), {
          viewCount: (snap.data().viewCount || 0) + 1,
          lastViewedAt: new Date().toISOString(),
        }, { merge: true }).catch(()=>{});
      }
      setLoading(false);
    }).catch(err => {
      console.error("Quote load error:", err);
      setLoading(false);
    });
  }, [quoteId]);

  if (loading) return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", background:"#000", color:"#E8317A", fontFamily:"'Trebuchet MS',sans-serif", fontSize:16, fontWeight:700 }}>Loading quote...</div>;
  if (!quote) return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", background:"#000", color:"var(--bz-ink-2)", fontFamily:"'Trebuchet MS',sans-serif", fontSize:14 }}>Quote not found or has expired.</div>;

  // Expiry: default is 7 days from creation, but an admin can extend it (expiresAt)
  // or make the link never expire (neverExpires). Either override wins.
  const isExpired = quote.neverExpires ? false
    : (quote.expiresAt
        ? new Date() > new Date(quote.expiresAt)
        : new Date() > new Date(new Date(quote.createdAt).getTime() + 7*24*60*60*1000));
  const isClosed  = quote.status === "closed";
  const totalMkt  = (quote.cards||[]).reduce((s,c)=>(s+(parseFloat(c.mktVal)||0)*(parseInt(c.qty)||1)),0);
  const offer     = parseFloat(quote.currentOffer || quote.dispOffer) || 0;
  const offerPct  = totalMkt > 0 ? (offer/totalMkt*100).toFixed(1) : null;
  // An offer only counts as "made" once Bazooka has set a price (currentOffer/dispOffer > 0).
  // Seller-submitted lots start at 0 until reviewed — don't show "$0.00" as if we offered nothing.
  const sellerResponded = ["accepted","declined","countered"].includes(quote.status);
  const hasOffer  = offer > 0;
  const awaitingReview = !hasOffer && !sellerResponded;

  async function submitResponse(type) {
    setSubmitError("");
    try {
      const payMethod  = quote.seller?.payment  || payment;
      const payHandle  = quote.seller?.paymentHandle || paymentHandle;
      if (type === "accepted") {
        await setDoc(doc(db,"quotes",quote.id), { status:"accepted", sellerPayment:payMethod, sellerHandle:payHandle, notified:false, respondedAt:new Date().toISOString() }, { merge:true });
      } else if (type === "declined") {
        await setDoc(doc(db,"quotes",quote.id), { status:"declined", notified:false, respondedAt:new Date().toISOString() }, { merge:true });
      } else if (type === "countered") {
        const amt = parseFloat(counterAmt);
        if (!amt) return;
        await setDoc(doc(db,"quotes",quote.id), { status:"countered", sellerCounter:amt, notified:false, respondedAt:new Date().toISOString(), history:[...(quote.history||[]),{type:"seller_counter",amount:amt,timestamp:new Date().toISOString()}] }, { merge:true });
      }
      setSubmitted(true);
      setQuote(prev => ({ ...prev, status: type }));
    } catch(e) {
      console.error("Submit error:", e);
      setSubmitError("Something went wrong — please try again or contact Bazooka Breaks directly.");
    }
  }

  const PAYMENT_METHODS = ["PayPal","Zelle"];

  return (
    <div style={{ minHeight:"100vh", background:"#000", fontFamily:"'Trebuchet MS','Segoe UI',sans-serif", color:"var(--bz-ink)", padding:"24px 16px" }}>
      <style>{`:root{
        --bz-bg:#0b0709; --bz-s1:#130d11; --bz-s2:#1a1218; --bz-s3:#241820;
        --bz-line:rgba(255,255,255,0.06); --bz-line-2:rgba(255,255,255,0.11);
        --bz-ink:#f6eef2; --bz-ink-2:#b9a9b2; --bz-ink-3:#7a6b74;
        --bz-pink:#E8317A; --bz-pink-hot:#ff4d94; --bz-pink-dim:rgba(232,49,122,0.14); --bz-pink-line:rgba(232,49,122,0.30);
        --bz-green:#34d399; --bz-amber:#fbbf24; --bz-blue:#7c9cff; --bz-violet:#a78bfa;
        --bz-radius:16px; --bz-radius-sm:11px;
      }`}</style>
      <div style={{ maxWidth:680, margin:"0 auto" }}>
        {/* Header */}
        <div style={{ background:"var(--bz-bg)", borderRadius:16, padding:"28px 32px", marginBottom:16, textAlign:"center", border:"1px solid var(--bz-line)" }}>
          <img src="/Bazooka_Logo_cropped.png" alt="Bazooka" style={{ height:"clamp(40px,7vw,60px)", width:"auto", maxWidth:"80%", objectFit:"contain", display:"block", margin:"0 auto 8px", filter:"drop-shadow(0 4px 16px rgba(232,49,122,0.35))" }} onError={e=>{ e.currentTarget.style.display="none"; e.currentTarget.nextSibling.style.display="block"; }}/>
          <div style={{ display:"none", fontSize:32, fontWeight:900, color:"#E8317A", letterSpacing:4, marginBottom:4 }}>BAZOOKA</div>
          <div style={{ fontSize:11, color:"var(--bz-ink-3)", fontStyle:"italic" }}>Bo Jackson Battle Arena · Lot Purchase Offer</div>
        </div>

        {(isExpired || isClosed) && (
          <div style={{ background:"#1a0a0a", border:"1px solid #E8317A44", borderRadius:12, padding:"16px 20px", marginBottom:16, textAlign:"center" }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#E8317A" }}>{isClosed ? "This quote has been closed." : "This quote has expired (7-day limit)."}</div>
            <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginTop:4 }}>Please contact Bazooka Breaks for a new offer.</div>
          </div>
        )}

        {/* Status badge */}
        {quote.status && quote.status !== "pending" && (
          <div style={{ marginBottom:16, textAlign:"center" }}>
            {{ accepted:<span style={{background:"#0a1a0a",color:"#4ade80",border:"1px solid #4ade8044",borderRadius:20,padding:"6px 20px",fontSize:13,fontWeight:700}}>{"\u2705 You accepted this offer"}</span>, declined:<span style={{background:"#1a0a0a",color:"#E8317A",border:"1px solid #E8317A44",borderRadius:20,padding:"6px 20px",fontSize:13,fontWeight:700}}>{"\u274C You declined this offer"}</span>, countered:<span style={{background:"#1a1400",color:"#FBBF24",border:"1px solid #FBBF2444",borderRadius:20,padding:"6px 20px",fontSize:13,fontWeight:700}}>{"\uD83E\uDD1D Counter offer sent"}</span> }[quote.status]}
          </div>
        )}

        {/* Seller info */}
        <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:12, padding:"14px 18px", marginBottom:12, display:"grid", gridTemplateColumns:"1fr 1fr" }}>
          <div><span style={{ color:"var(--bz-ink-3)", fontSize:11 }}>Prepared for: </span><strong style={{ color:"var(--bz-ink)" }}>{quote.seller?.name || "--"}</strong></div>
          <div style={{ textAlign:"right" }}><span style={{ color:"var(--bz-ink-3)", fontSize:11 }}>Date: </span><strong style={{ color:"var(--bz-ink)" }}>{quote.seller?.date || new Date(quote.createdAt).toLocaleDateString()}</strong></div>
        </div>

        {/* Cards table */}
        {quote.cards && quote.cards.length > 0 && (
          <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:12, overflow:"hidden", marginBottom:12 }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"var(--bz-bg)" }}>
                  {["#","Card Name","Qty","Value/Card","Offer/Card"].map(h=>(
                    <th key={h} style={{ padding:"10px 12px", fontSize:10, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, textAlign:"left", borderBottom:"1px solid var(--bz-line)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {quote.cards.map((c,i) => {
                  const mv = parseFloat(c.mktVal)||0;
                  const qty = parseInt(c.qty)||1;
                  const dispPct = totalMkt > 0 ? offer/totalMkt : 0;
                  const cardOffer = c.offerPerCard != null ? parseFloat(c.offerPerCard) : mv*dispPct;
                  const isCustom = c.pctOverride || (c.offerPerCard != null && Math.abs(cardOffer - mv*dispPct) > 0.01);
                  return (
                    <tr key={i} style={{ borderBottom:"1px solid var(--bz-line)" }}>
                      <td style={{ padding:"10px 12px", color:"var(--bz-ink-3)", fontSize:12 }}>{i+1}</td>
                      <td style={{ padding:"10px 12px", fontWeight:700, color:"var(--bz-ink)", fontSize:13 }}>{c.name}</td>
                      <td style={{ padding:"10px 12px", color:"var(--bz-ink-2)", fontSize:12, textAlign:"center" }}>{qty}</td>
                      <td style={{ padding:"10px 12px", color:"var(--bz-ink-2)", fontSize:12 }}>${mv.toFixed(2)}</td>
                      <td style={{ padding:"10px 12px", color: isCustom?"#A78BFA":"#E8317A", fontWeight:700, fontSize:12 }}>
                        {hasOffer ? <>${cardOffer.toFixed(2)}{isCustom && <span style={{ fontSize:9, color:"#A78BFA", marginLeft:4 }}>★</span>}</> : <span style={{ color:"var(--bz-ink-3)" }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes */}
        {quote.custNote && (
          <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderLeft:"3px solid #E8317A", borderRadius:8, padding:"12px 16px", marginBottom:12 }}>
            <div style={{ fontSize:10, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1.5, marginBottom:6 }}>Notes from Bazooka</div>
            <p style={{ margin:0, fontSize:13, color:"var(--bz-ink-2)", lineHeight:1.6, whiteSpace:"pre-wrap" }}>{quote.custNote}</p>
          </div>
        )}

        {/* Offer box */}
        <div style={{ background:"var(--bz-s1)", border:`2px solid ${hasOffer?"#E8317A33":"#7B9CFF33"}`, borderRadius:12, padding:"20px 24px", marginBottom:12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontSize:16, fontWeight:800, color:hasOffer?"#E8317A":"#7B9CFF" }}>{hasOffer?"Bazooka's Offer":"Bazooka's Offer"}</span>
            {hasOffer
              ? <span style={{ fontSize:32, fontWeight:900, color:"var(--bz-ink)" }}>${offer.toFixed(2)}</span>
              : <span style={{ fontSize:18, fontWeight:800, color:"#7B9CFF" }}>⏳ Under Review</span>}
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:6 }}>
            <div style={{ fontSize:12, color:"var(--bz-ink-3)" }}>{quote.totalCards || (quote.cards||[]).reduce((s,c)=>s+(parseInt(c.qty)||1),0)} cards total</div>
            {quote.quoteRef && <div style={{ fontSize:11, fontWeight:700, color:"#7B9CFF", background:"rgba(123,156,255,0.08)", border:"1px solid rgba(123,156,255,0.2)", borderRadius:6, padding:"2px 10px", letterSpacing:1 }}>Ref: {quote.quoteRef}</div>}
          </div>
          {!hasOffer && (
            <div style={{ marginTop:12, fontSize:13, color:"var(--bz-ink-2)", lineHeight:1.6 }}>
              We've received your lot and our team is reviewing your cards. You'll see your offer here as soon as it's ready — keep this link handy and check back soon.
            </div>
          )}
        </div>

        {/* -- RESPONSE AREA -- only show once Bazooka has actually made an offer -- */}
        {!isExpired && !isClosed && !submitted && hasOffer && (
          <div style={{ background:"var(--bz-s1)", border:"2px solid #4ade8044", borderRadius:12, padding:"20px", marginBottom:12 }}>
            <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink-2)", marginBottom:14 }}>How would you like to respond?</div>

            {/* Payment method — required before accepting */}
            {quote.seller?.payment ? (
              <div style={{ background:"#0a1a0a", border:"1px solid #4ade8022", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:12, color:"#4ade80" }}>
                💳 Payment via <strong>{quote.seller.payment}</strong>{quote.seller.paymentHandle ? ` — ${quote.seller.paymentHandle}` : ""} <span style={{ color:"var(--bz-ink-3)", fontWeight:400 }}>(provided when you submitted)</span>
              </div>
            ) : (
              <div style={{ marginBottom:16, background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:12, padding:"14px 16px" }}>
                <div style={{ fontSize:12, fontWeight:800, color:"var(--bz-ink)", marginBottom:4 }}>How should we pay you? <span style={{ color:"#E8317A" }}>*</span></div>
                <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:10 }}>Required before accepting — we'll send payment here as soon as possible.</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  <select value={payment} onChange={e=>setPayment(e.target.value)}
                    style={{ background:"var(--bz-bg)", border:`1.5px solid ${payment?"#4ade80":"#E8317A"}`, borderRadius:8, color:payment?"var(--bz-ink)":"#666", padding:"10px 12px", fontSize:13, fontFamily:"inherit", outline:"none" }}>
                    <option value="">Select method...</option>
                    {PAYMENT_METHODS.map(m=><option key={m} value={m}>{m}</option>)}
                  </select>
                  <input value={paymentHandle} onChange={e=>setPaymentHandle(e.target.value)}
                    placeholder={payment==="PayPal"?"PayPal email or @username":payment==="Zelle"?"Zelle email or phone":"your handle / account"}
                    style={{ background:"var(--bz-bg)", border:`1.5px solid ${paymentHandle?"#4ade80":"var(--bz-line-2)"}`, borderRadius:8, color:"var(--bz-ink)", padding:"10px 12px", fontSize:13, fontFamily:"inherit", outline:"none" }}/>
                </div>
                {payment && !paymentHandle && <div style={{ fontSize:11, color:"#FBBF24", marginTop:6 }}>⚠ Please enter your {payment} email or handle</div>}
              </div>
            )}

            {/* Big Accept button — disabled until payment is filled */}
            <button
              onClick={()=>{ if(!quote.seller?.payment && (!payment||!paymentHandle)) return; submitResponse("accepted"); }}
              disabled={!quote.seller?.payment && (!payment||!paymentHandle)}
              style={{ width:"100%", background:(!quote.seller?.payment && (!payment||!paymentHandle))?"var(--bz-s2)":"#4ade80", color:(!quote.seller?.payment && (!payment||!paymentHandle))?"#444":"#000", border:"none", borderRadius:12, padding:"18px 0", fontSize:18, fontWeight:900, cursor:(!quote.seller?.payment && (!payment||!paymentHandle))?"not-allowed":"pointer", fontFamily:"inherit", marginBottom:10, transition:"all 0.15s" }}>
              {(!quote.seller?.payment && (!payment||!paymentHandle))
                ? "Enter payment method above to accept"
                : `✅ Accept Offer — $${offer.toFixed(2)}`}
            </button>
            {submitError && (
              <div style={{ background:"#1a0a0a", border:"1px solid #E8317A44", borderRadius:8, padding:"10px 14px", marginBottom:10, fontSize:13, color:"#E8317A" }}>
                {submitError}
              </div>
            )}

            {/* Counter offer */}
            {quote.allowCounter && (
              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Or send a counter offer</div>
                <div style={{ display:"flex", gap:8 }}>
                  <input type="number" step="0.01" value={counterAmt} onChange={e=>setCounterAmt(e.target.value)}
                    placeholder={`Your counter (e.g. $${(offer*1.1).toFixed(0)})`}
                    style={{ flex:1, background:"var(--bz-bg)", border:"1px solid #FBBF2444", borderRadius:8, color:"#FBBF24", padding:"10px 12px", fontSize:13, fontFamily:"inherit", outline:"none" }}/>
                  <button onClick={()=>submitResponse("countered")} disabled={!counterAmt}
                    style={{ background:"#1a1400", border:"2px solid #FBBF24", color:"#FBBF24", borderRadius:8, padding:"10px 18px", fontSize:13, fontWeight:700, cursor:counterAmt?"pointer":"not-allowed", fontFamily:"inherit", opacity:counterAmt?1:0.4, whiteSpace:"nowrap" }}>
                    {"\uD83E\uDD1D Counter"}</button>
                </div>
              </div>
            )}

            {/* Decline -- subtle */}
            <button onClick={()=>{ if(window.confirm("Decline this offer?")) submitResponse("declined"); }}
              style={{ width:"100%", background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-3)", borderRadius:8, padding:"10px 0", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
              {"\u274C Decline this offer"}</button>
          </div>
        )}

        {/* Ship-to — only show before acceptance */}
        {quote.status !== "accepted" && !submitted && (
        <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:12, padding:"14px 18px", marginBottom:12 }}>
          <div style={{ fontSize:10, fontWeight:700, color:"var(--bz-ink-3)", textTransform:"uppercase", letterSpacing:1.5, marginBottom:8 }}>Ship Cards To</div>
          <div style={{ fontSize:13, color:"var(--bz-ink)", fontWeight:700, lineHeight:1.8 }}>
            Devin -- Bazooka<br/>
            2150 N Pointe Dr<br/>
            Warsaw, IN 46582
          </div>
        </div>
        )}

        {/* Already accepted — show ship-to reminder */}
        {quote.status === "accepted" && !submitted && (
          <div style={{ background:"#0a1a0a", border:"1px solid rgba(74,222,128,0.2)", borderRadius:14, padding:"20px 22px", marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:800, color:"#4ade80", textTransform:"uppercase", letterSpacing:"2px", marginBottom:14 }}>✅ You already accepted this offer</div>
            <div style={{ fontSize:14, color:"var(--bz-ink-2)", marginBottom:14 }}>Here's a reminder of what to do next:</div>
            <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:8, padding:"14px 16px", marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"var(--bz-ink-3)", marginBottom:6, textTransform:"uppercase", letterSpacing:1 }}>📦 Ship your cards to</div>
              <div style={{ fontSize:14, color:"var(--bz-ink)", fontWeight:700, lineHeight:1.8 }}>Devin — Bazooka Breaks<br/>2150 N Pointe Dr<br/>Warsaw, IN 46582</div>
            </div>
            <div style={{ fontSize:13, color:"#999", lineHeight:1.7 }}>
              Use top loaders and a tracked shipping method. Once sent, share your tracking number with us on Discord at <strong style={{ color:"var(--bz-ink-2)" }}>BubbleGumKing</strong> or via Whatnot.<br/><br/>
              Payment of <strong style={{ color:"#4ade80" }}>${offer.toFixed(2)}</strong> will be sent as soon as possible — thank you!
            </div>
          </div>
        )}

        {submitted && quote.status === "accepted" && (
          <div style={{ marginBottom:16 }}>
            {/* Big confirmation */}
            <div style={{ background:"#0a1a0a", border:"2px solid #4ade80", borderRadius:16, padding:"28px 24px", textAlign:"center", marginBottom:16 }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🎉</div>
              <div style={{ fontSize:22, fontWeight:900, color:"#4ade80", marginBottom:6 }}>Offer Accepted!</div>
              <div style={{ fontSize:14, color:"var(--bz-ink-2)", marginBottom:16 }}>You've locked in <strong style={{ color:"var(--bz-ink)" }}>${offer.toFixed(2)}</strong> for your {quote.totalCards || (quote.cards||[]).length} cards.</div>
              <div style={{ background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.15)", borderRadius:10, padding:"12px 16px", fontSize:13, color:"#4ade80", fontWeight:700 }}>
                Bazooka Breaks has been notified and will follow up shortly.
              </div>
            </div>

            {/* Step-by-step next steps */}
            <div style={{ background:"var(--bz-s1)", border:"1px solid var(--bz-line)", borderRadius:14, padding:"20px 22px", marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:800, color:"#E8317A", textTransform:"uppercase", letterSpacing:"2px", marginBottom:16 }}>What Happens Next</div>

              {[
                {
                  step:"1",
                  icon:"📦",
                  title:"Pack your cards carefully",
                  body:"Use top loaders or card sleeves for each card. Put them in a small box or padded envelope — avoid using plain envelopes alone. Include a note with your name inside the package.",
                  color:"#7B9CFF"
                },
                {
                  step:"2",
                  icon:"🚚",
                  title:"Ship to Bazooka Breaks",
                  body:"Send your cards to the address below. We recommend using USPS First Class or Priority Mail with tracking so you can confirm delivery. Once shipped, send us your tracking number on Discord or via the contact below.",
                  color:"#FBBF24",
                  extra: (
                    <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:8, padding:"12px 14px", marginTop:8 }}>
                      <div style={{ fontSize:11, color:"var(--bz-ink-3)", fontWeight:700, marginBottom:4, textTransform:"uppercase", letterSpacing:1 }}>Ship To</div>
                      <div style={{ fontSize:14, color:"var(--bz-ink)", fontWeight:700, lineHeight:1.8 }}>
                        Devin — Bazooka Breaks<br/>
                        2150 N Pointe Dr<br/>
                        Warsaw, IN 46582
                      </div>
                    </div>
                  )
                },
                {
                  step:"3",
                  icon:"✅",
                  title:"We receive and verify your cards",
                  body:"Once your package arrives we'll verify everything matches the lot. This usually takes 1–2 business days after delivery.",
                  color:"#4ade80"
                },
                {
                  step:"4",
                  icon:"💸",
                  title:"You get paid",
                  body:`Payment will be sent via ${(quote.seller?.payment || payment) || "your selected method"} after we confirm the cards. Keep an eye on your ${(quote.seller?.payment || payment) === "PayPal" ? "PayPal inbox" : (quote.seller?.payment || payment) === "Zelle" ? "Zelle / bank account" : "payment account"}.`,
                  color:"#4ade80"
                },
              ].map(({ step, icon, title, body, color, extra }) => (
                <div key={step} style={{ display:"flex", gap:14, paddingBottom:16, marginBottom:16, borderBottom:"1px solid var(--bz-line)" }}>
                  <div style={{ width:36, height:36, borderRadius:"50%", background:`${color}18`, border:`1.5px solid ${color}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{icon}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                      <span style={{ fontSize:10, fontWeight:800, color:color, background:`${color}18`, borderRadius:10, padding:"1px 8px" }}>Step {step}</span>
                      <span style={{ fontSize:14, fontWeight:700, color:"var(--bz-ink)" }}>{title}</span>
                    </div>
                    <div style={{ fontSize:13, color:"#999", lineHeight:1.6 }}>{body}</div>
                    {extra}
                  </div>
                </div>
              ))}

              {/* Final reassurance row */}
              <div style={{ display:"flex", alignItems:"center", gap:10, background:"rgba(123,156,255,0.06)", border:"1px solid rgba(123,156,255,0.15)", borderRadius:10, padding:"12px 14px" }}>
                <span style={{ fontSize:20 }}>💬</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:"#7B9CFF", marginBottom:2 }}>Questions? Reach out anytime.</div>
                  <div style={{ fontSize:12, color:"var(--bz-ink-3)" }}>Find us on Discord as <strong style={{ color:"var(--bz-ink-2)" }}>BubbleGumKing</strong> or message us on Whatnot.</div>
                </div>
              </div>
            </div>

            {/* Summary card */}
            <div style={{ background:"var(--bz-bg)", border:"1px solid var(--bz-line-2)", borderRadius:10, padding:"14px 18px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
              <div>
                <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:2 }}>Your payout</div>
                <div style={{ fontSize:22, fontWeight:900, color:"#4ade80" }}>${offer.toFixed(2)}</div>
              </div>
              {(quote.seller?.payment || payment) && (
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginBottom:2 }}>Via</div>
                  <div style={{ fontSize:14, fontWeight:700, color:"var(--bz-ink)" }}>{quote.seller?.payment || payment}</div>
                  {(quote.seller?.paymentHandle || paymentHandle) && (
                    <div style={{ fontSize:12, color:"var(--bz-ink-2)" }}>{quote.seller?.paymentHandle || paymentHandle}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {submitted && quote.status !== "accepted" && (
          <div style={{ background:"#0a1a0a", border:"2px solid #4ade80", borderRadius:12, padding:"20px", textAlign:"center", marginBottom:16 }}>
            <div style={{ fontSize:28, marginBottom:8 }}>✅</div>
            <div style={{ fontSize:16, fontWeight:800, color:"#4ade80" }}>Response sent!</div>
            <div style={{ fontSize:12, color:"var(--bz-ink-3)", marginTop:6 }}>Bazooka Breaks will be in touch soon.</div>
          </div>
        )}

        <div style={{ marginTop:20, textAlign:"center", color:"var(--bz-ink-3)", fontSize:11, fontStyle:"italic" }}>
          This offer is valid for 7 days. Thank you for bringing your collection to Bazooka!
        </div>
      </div>
    </div>
  );
}

// ── FINANCE / CASHFLOW ───────────────────────────────────────────────────────
const EXPENSE_CATEGORIES = [
  "Inventory / Cards","Shipping Supplies","Packaging","MagPros / Top Loaders",
  "Payroll / Commission","Whatnot Fees","IMC Split","Platform / Software",
  "Marketing","Rent / Storage","Equipment","Miscellaneous"
];

// ── SHIPPING HUB ──────────────────────────────────────────────────────────────
const ISSUE_TYPES = [
  { id:"transit",    label:"Stuck in Transit",   emoji:"📦", color:"#FBBF24" },
  { id:"missing",    label:"Missing Item",        emoji:"❓", color:"#E8317A" },
  { id:"wrong_card", label:"Wrong Card Sent",     emoji:"🔄", color:"#C084FC" },
  { id:"damaged",    label:"Damaged Package",     emoji:"💔", color:"#ef4444" },
  { id:"other",      label:"Other",               emoji:"⚠️", color:"var(--bz-ink-2)" },
];
const RESOLUTIONS = [
  { id:"delivered",      label:"Package Delivered" },
  { id:"replacement",    label:"Replacement Card Sent" },
  { id:"refund",         label:"Refunded Buyer" },
  { id:"partial",        label:"Partial Resolution" },
  { id:"unresolved",     label:"Unresolved" },
];

function ShippingHub({ userRole, streams=[] }) {
  const isAdmin = userRole?.role === "Admin";
  const [issues, setIssues]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState("issues");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const [filter, setFilter]   = useState("open");

  const EMPTY = { orderNumber:"", streamDate:"", buyerUsername:"", issueType:"transit", description:"", status:"open", resolution:"", resolutionNotes:"", loggedBy:"" };
  const [form, setForm] = useState(EMPTY);
  const rf = k => v => setForm(p=>({...p,[k]:v}));

  useEffect(()=>{
    const unsub = onSnapshot(collection(db,"shipping_issues"), snap=>{
      setIssues(snap.docs.map(d=>({...d.data(),id:d.id})).sort((a,b)=>b.loggedAt?.localeCompare(a.loggedAt)||0));
      setLoading(false);
    });
    return ()=>unsub();
  },[]);

  async function save() {
    if (!form.orderNumber||!form.issueType) return;
    setSaving(true);
    const data = {...form, updatedAt:new Date().toISOString(), loggedAt:form.loggedAt||new Date().toISOString()};
    const id = editId || uid();
    await setDoc(doc(db,"shipping_issues",id),{...data,id});
    setForm(EMPTY); setShowForm(false); setEditId(null); setSaving(false);
  }

  async function deleteIssue(id) {
    if (!window.confirm("Delete this issue?")) return;
    await deleteDoc(doc(db,"shipping_issues",id));
  }

  function startEdit(issue) {
    setForm({...EMPTY,...issue}); setEditId(issue.id); setShowForm(true);
  }

  const filtered = issues.filter(i => filter==="all" ? true : filter==="open" ? i.status==="open" : i.status==="resolved");
  const openCount = issues.filter(i=>i.status==="open").length;
  const resolvedCount = issues.filter(i=>i.status==="resolved").length;

  const issueTypeCfg = id => ISSUE_TYPES.find(t=>t.id===id)||ISSUE_TYPES[4];

  const fmt = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—";

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16,paddingBottom:40}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:22,fontWeight:900,color:"var(--bz-ink)"}}>📦 Shipping Hub</div>
          <div style={{fontSize:12,color:"var(--bz-ink-3)",marginTop:2}}>Track transit issues, missing items, and shipping errors</div>
        </div>
        <button onClick={()=>{setForm(EMPTY);setEditId(null);setShowForm(p=>!p);}}
          style={{background:"linear-gradient(135deg,#E8317A,#c02060)",color:"#fff",border:"none",borderRadius:10,padding:"9px 20px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit"}}>
          + Log Issue
        </button>
      </div>

      {/* KPI tiles */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        {[
          {l:"Open Issues",    v:openCount,     c:"#E8317A"},
          {l:"Resolved",       v:resolvedCount, c:"#4ade80"},
          {l:"Total Logged",   v:issues.length, c:"var(--bz-ink)"},
          {l:"Resolution Rate",v:issues.length?Math.round(resolvedCount/issues.length*100)+"%":"—", c:resolvedCount/Math.max(1,issues.length)>0.8?"#4ade80":"#FBBF24"},
        ].map(({l,v,c})=>(
          <div key={l} style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"14px 16px",textAlign:"center"}}>
            <div style={{fontSize:26,fontWeight:900,color:c}}>{v}</div>
            <div style={{fontSize:10,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginTop:4}}>{l}</div>
          </div>
        ))}
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div style={{background:"var(--bz-bg)",border:"2px solid #E8317A33",borderRadius:12,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:14}}>{editId?"✏️ Edit Issue":"🚨 Log New Issue"}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Order Number *</div>
              <input value={form.orderNumber} onChange={e=>rf("orderNumber")(e.target.value)} placeholder="Whatnot order #"
                style={{width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Stream Date</div>
              <input type="date" value={form.streamDate} onChange={e=>rf("streamDate")(e.target.value)}
                style={{width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Buyer Username</div>
              <input value={form.buyerUsername} onChange={e=>rf("buyerUsername")(e.target.value)} placeholder="@username"
                style={{width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:8}}>Issue Type *</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {ISSUE_TYPES.map(t=>(
                <button key={t.id} onClick={()=>rf("issueType")(t.id)}
                  style={{background:form.issueType===t.id?`${t.color}22`:"transparent",border:`1.5px solid ${form.issueType===t.id?t.color:"var(--bz-ink-3)"}`,color:form.issueType===t.id?t.color:"var(--bz-ink-3)",borderRadius:20,padding:"6px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
                  {t.emoji} {t.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Description</div>
            <textarea value={form.description} onChange={e=>rf("description")(e.target.value)} placeholder="What happened? Include any relevant details..."
              style={{width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",boxSizing:"border-box",minHeight:80,resize:"vertical"}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Status</div>
              <select value={form.status} onChange={e=>rf("status")(e.target.value)}
                style={{width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",cursor:"pointer",boxSizing:"border-box"}}>
                <option value="open">🔴 Open</option>
                <option value="resolved">✅ Resolved</option>
              </select>
            </div>
            {form.status==="resolved" && (
              <div>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Resolution</div>
                <select value={form.resolution} onChange={e=>rf("resolution")(e.target.value)}
                  style={{width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",cursor:"pointer",boxSizing:"border-box"}}>
                  <option value="">-- Select outcome --</option>
                  {RESOLUTIONS.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
            )}
          </div>
          {form.status==="resolved" && (
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Resolution Notes</div>
              <input value={form.resolutionNotes} onChange={e=>rf("resolutionNotes")(e.target.value)} placeholder="What was the outcome? Card sent, tracking delivered, etc."
                style={{width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
          )}
          <div style={{display:"flex",gap:10}}>
            <button onClick={save} disabled={saving||!form.orderNumber}
              style={{background:"linear-gradient(135deg,#E8317A,#c02060)",color:"#fff",border:"none",borderRadius:8,padding:"9px 24px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",opacity:saving||!form.orderNumber?0.5:1}}>
              {saving?"Saving...":editId?"Update":"Save Issue"}
            </button>
            <button onClick={()=>{setShowForm(false);setEditId(null);setForm(EMPTY);}}
              style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:8,padding:"9px 18px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{display:"flex",gap:8}}>
        {[["open","🔴 Open"],["resolved","✅ Resolved"],["all","All"]].map(([id,l])=>(
          <button key={id} onClick={()=>setFilter(id)} style={{background:filter===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${filter===id?"#E8317A":"#333"}`,color:filter===id?"#E8317A":"#555",borderRadius:16,padding:"5px 16px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
        ))}
        <div style={{marginLeft:"auto",fontSize:11,color:"var(--bz-ink-3)",display:"flex",alignItems:"center"}}>{filtered.length} issue{filtered.length!==1?"s":""}</div>
      </div>

      {/* Issues list */}
      {loading ? <div style={{color:"var(--bz-ink-3)",fontSize:12,padding:20}}>Loading...</div>
      : filtered.length===0 ? (
        <div style={{textAlign:"center",color:"var(--bz-ink-3)",padding:"40px 0",fontSize:13}}>
          {filter==="open" ? "🎉 No open issues — shipping is clean!" : "No issues logged yet"}
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {filtered.map(issue=>{
            const tc = issueTypeCfg(issue.issueType);
            const res = RESOLUTIONS.find(r=>r.id===issue.resolution);
            return (
              <div key={issue.id} style={{background:"var(--bz-s1)",border:`1px solid ${issue.status==="open"?tc.color+"33":"rgba(74,222,128,0.2)"}`,borderRadius:12,padding:"14px 18px"}}>
                <div style={{display:"flex",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                  {/* Type badge */}
                  <div style={{background:`${tc.color}15`,border:`1px solid ${tc.color}33`,borderRadius:8,padding:"4px 10px",fontSize:11,fontWeight:700,color:tc.color,whiteSpace:"nowrap"}}>
                    {tc.emoji} {tc.label}
                  </div>
                  {/* Status */}
                  <div style={{fontSize:11,fontWeight:700,color:issue.status==="open"?"#E8317A":"#4ade80",padding:"4px 10px",background:issue.status==="open"?"rgba(232,49,122,0.1)":"rgba(74,222,128,0.1)",borderRadius:8}}>
                    {issue.status==="open"?"🔴 Open":"✅ Resolved"}
                  </div>
                  <div style={{flex:1}}/>
                  {/* Actions */}
                  <div style={{display:"flex",gap:6}}>
                    {issue.status==="open" && (
                      <button onClick={()=>{ setForm({...EMPTY,...issue,status:"resolved"}); setEditId(issue.id); setShowForm(true); }}
                        style={{background:"rgba(74,222,128,0.1)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>
                        Mark Resolved
                      </button>
                    )}
                    <button onClick={()=>startEdit(issue)} style={{background:"none",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Edit</button>
                    <button onClick={()=>deleteIssue(issue.id)} style={{background:"none",border:"1px solid rgba(239,68,68,0.3)",color:"#ef4444",borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
                  </div>
                </div>
                {/* Details */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginTop:12}}>
                  <div><div style={{fontSize:10,color:"var(--bz-ink-3)",marginBottom:2}}>ORDER</div><div style={{fontSize:13,fontWeight:700,color:"var(--bz-ink)"}}>{issue.orderNumber||"—"}</div></div>
                  <div><div style={{fontSize:10,color:"var(--bz-ink-3)",marginBottom:2}}>STREAM DATE</div><div style={{fontSize:13,color:"var(--bz-ink-2)"}}>{issue.streamDate||"—"}</div></div>
                  <div><div style={{fontSize:10,color:"var(--bz-ink-3)",marginBottom:2}}>BUYER</div><div style={{fontSize:13,color:"var(--bz-ink-2)"}}>{issue.buyerUsername||"—"}</div></div>
                </div>
                {issue.description && <div style={{marginTop:10,fontSize:12,color:"var(--bz-ink-2)",lineHeight:1.5,borderTop:"1px solid var(--bz-line)",paddingTop:10}}>{issue.description}</div>}
                {issue.status==="resolved" && (
                  <div style={{marginTop:10,padding:"8px 12px",background:"rgba(74,222,128,0.05)",border:"1px solid rgba(74,222,128,0.15)",borderRadius:8}}>
                    <div style={{fontSize:11,fontWeight:700,color:"#4ade80",marginBottom:2}}>Resolution: {res?.label||issue.resolution}</div>
                    {issue.resolutionNotes && <div style={{fontSize:12,color:"var(--bz-ink-2)"}}>{issue.resolutionNotes}</div>}
                  </div>
                )}
                <div style={{marginTop:8,fontSize:10,color:"var(--bz-ink-3)"}}>Logged {fmt(issue.loggedAt)}{issue.loggedBy?` by ${issue.loggedBy}`:""}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── WEEKLY REPORT ─────────────────────────────────────────────────────────────
function WeeklyReport({ streams=[], userRole }) {
  const isAdmin = userRole?.role === "Admin";
  const now = new Date();
  const [weekOffset, setWeekOffset] = useState(0);
  const [suggestions, setSuggestions] = useState({});
  const [editingSug, setEditingSug] = useState(null);
  const [editText, setEditText] = useState("");
  const fmt = v => "$"+Number(v||0).toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0});
  const mmColor = v => v>=1.7?"#4ade80":v>=1.5?"#86efac":v>=1.4?"#FBBF24":"#E8317A";

  // Calculate week range
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - (now.getDay()===0?6:now.getDay()-1) - weekOffset*7);
  weekStart.setHours(0,0,0,0);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate()+6);
  weekEnd.setHours(23,59,59,999);
  const weekKey = weekStart.toISOString().split("T")[0];

  // Previous week for comparison
  const prevStart = new Date(weekStart); prevStart.setDate(prevStart.getDate()-7);
  const prevEnd = new Date(weekEnd); prevEnd.setDate(prevEnd.getDate()-7);

  function weekStreams(wStart, wEnd) {
    return streams.filter(s=>{ const d=parseLocalDate(s.date); return d>=wStart&&d<=wEnd; });
  }

  const thisWeek = weekStreams(weekStart, weekEnd);
  const prevWeek = weekStreams(prevStart, prevEnd);

  function repStats(slist, targetBreaker=null) {
    if (!slist.length) return {gross:0,mm:0,newBuyers:0,comm:0,streams:0,trueNet:0};
    const own = targetBreaker ? slist.filter(s=>s.breaker===targetBreaker) : slist;
    const gross = own.reduce((s,x)=>s+(parseFloat(x.grossRevenue)||0),0);
    const mmList = own.filter(x=>parseFloat(x.marketMultiple)>0);
    const mm = mmList.length?mmList.reduce((s,x)=>s+(parseFloat(x.marketMultiple)||0),0)/mmList.length:0;
    const newBuyers = own.reduce((s,x)=>s+(parseInt(x.newBuyers)||0),0);
    const comm = targetBreaker
      ? slist.reduce((s,x)=>s+calcStream(x,targetBreaker).myComm,0)
      : slist.reduce((s,x)=>{ const c=calcStream(x); return s+c.primaryCommAmt-c.repExpShare+c.salesBonus+c.tips+c.splitRepAmt+c.eventStaffAmt; },0);
    const trueNet = own.reduce((s,x)=>s+(calcStream(x).bazTrueNet||0),0);
    return {gross,mm,newBuyers,comm,streams:own.length,trueNet};
  }

  const teamStats    = repStats(thisWeek);
  const teamStatsPrev = repStats(prevWeek);

  const repData = BREAKERS.map(b=>({
    b,
    this: repStats(thisWeek.filter(s=>s.breaker===b||(s.eventStaff||[]).some(es=>es.breaker===b)||s.splitRep===b), b),
    prev: repStats(prevWeek.filter(s=>s.breaker===b||(s.eventStaff||[]).some(es=>es.breaker===b)||s.splitRep===b), b),
  })).filter(r=>r.this.streams>0||r.prev.streams>0);

  // Auto-generate suggestions from trends
  function generateSuggestions() {
    const sug = [];
    repData.forEach(({b,this:t,prev:p})=>{
      if (!t.streams) return;
      if (t.mm>0 && p.mm>0 && t.mm < p.mm - 0.15) sug.push({id:`${b}-mm-drop`,rep:b,text:`${b}'s market multiple dropped ${(p.mm-t.mm).toFixed(2)}x week over week (${p.mm.toFixed(2)}x → ${t.mm.toFixed(2)}x). Review product mix and pricing strategy.`});
      if (t.mm>0 && t.mm>=1.7) sug.push({id:`${b}-mm-strong`,rep:b,text:`${b} is hitting ${t.mm.toFixed(2)}x this week — strong performance. Consider increasing stream frequency if schedule allows.`});
      if (t.newBuyers>0 && p.newBuyers>0 && t.newBuyers < p.newBuyers*0.6) sug.push({id:`${b}-nb-drop`,rep:b,text:`${b}'s new buyer count dropped significantly (${p.newBuyers} → ${t.newBuyers}). Check if stream timing or marketing could be adjusted.`});
      if (t.streams===0 && p.streams>0) sug.push({id:`${b}-no-streams`,rep:b,text:`${b} had no streams this week. Confirm scheduling for next week.`});
    });
    if (teamStats.mm>0 && teamStatsPrev.mm>0 && teamStats.mm < teamStatsPrev.mm - 0.1) sug.push({id:"team-mm",rep:"Team",text:`Team avg market multiple dropped from ${teamStatsPrev.mm.toFixed(2)}x to ${teamStats.mm.toFixed(2)}x. Consider reviewing product selection across all reps.`});
    if (teamStats.newBuyers > teamStatsPrev.newBuyers * 1.2) sug.push({id:"team-nb-up",rep:"Team",text:`Great week for new buyer acquisition — up ${((teamStats.newBuyers/Math.max(1,teamStatsPrev.newBuyers)-1)*100).toFixed(0)}% week over week. Double down on what's working.`});
    return sug;
  }

  const autoSuggestions = generateSuggestions();
  const savedSuggestions = suggestions[weekKey] || {};
  // Merge: auto suggestions that haven't been deleted
  const activeSuggestions = [
    ...autoSuggestions.filter(s=>savedSuggestions[s.id]!=="deleted").map(s=>({...s, text:savedSuggestions[s.id]||s.text})),
    ...Object.entries(savedSuggestions).filter(([k,v])=>v!=="deleted"&&!autoSuggestions.find(s=>s.id===k)).map(([k,v])=>({id:k,rep:"Custom",text:v}))
  ];

  function deleteSug(id) { setSuggestions(p=>({...p,[weekKey]:{...(p[weekKey]||{}), [id]:"deleted"}})); }
  function saveSugEdit(id) { setSuggestions(p=>({...p,[weekKey]:{...(p[weekKey]||{}), [id]:editText}})); setEditingSug(null); }
  function addCustomSug() {
    const id=`custom-${uid()}`;
    setSuggestions(p=>({...p,[weekKey]:{...(p[weekKey]||{}), [id]:"New suggestion — click to edit"}}));
  }

  const change = (a,b,key) => a[key]&&b[key] ? ((a[key]-b[key])/Math.max(0.01,b[key])*100).toFixed(0) : null;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16,paddingBottom:40}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:22,fontWeight:900,color:"var(--bz-ink)"}}>📋 Weekly Report</div>
          <div style={{fontSize:12,color:"var(--bz-ink-3)",marginTop:2}}>
            {weekStart.toLocaleDateString("en-US",{month:"short",day:"numeric"})} – {weekEnd.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={()=>setWeekOffset(p=>p+1)} style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontFamily:"inherit",fontSize:12}}>← Prev</button>
          {weekOffset>0&&<button onClick={()=>setWeekOffset(0)} style={{background:"var(--bz-s2)",border:"1px solid #E8317A",color:"#E8317A",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:700}}>This Week</button>}
          <button onClick={()=>setWeekOffset(p=>Math.max(0,p-1))} disabled={weekOffset===0} style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",color:weekOffset===0?"#333":"#555",borderRadius:8,padding:"6px 12px",cursor:weekOffset===0?"default":"pointer",fontFamily:"inherit",fontSize:12}}>Next →</button>
          <button onClick={()=>window.print()} style={{background:"rgba(232,49,122,0.1)",border:"1px solid rgba(232,49,122,0.3)",color:"#E8317A",borderRadius:8,padding:"6px 16px",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:700}}>🖨 Print</button>
        </div>
      </div>

      {thisWeek.length===0 && <div style={{textAlign:"center",color:"var(--bz-ink-3)",padding:"40px",fontSize:13}}>No streams logged for this week</div>}

      {thisWeek.length>0 && <>
        {/* Team summary */}
        <div style={{background:"linear-gradient(135deg,#0d0d1a,var(--bz-s1))",border:"1px solid #1a1a2e",borderRadius:14,padding:"20px 24px"}}>
          <div style={{fontSize:14,fontWeight:800,color:"var(--bz-ink)",marginBottom:14}}>🏆 Team Summary</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:12}}>
            {[
              {l:"Total Gross",     v:fmt(teamStats.gross),   prev:fmt(teamStatsPrev.gross),   chg:change(teamStats,teamStatsPrev,"gross"),  c:"#E8317A"},
              {l:"Avg MM",          v:teamStats.mm?teamStats.mm.toFixed(2)+"x":"--", prev:teamStatsPrev.mm?teamStatsPrev.mm.toFixed(2)+"x":"--", chg:change(teamStats,teamStatsPrev,"mm"), c:mmColor(teamStats.mm)},
              {l:"New Buyers",      v:teamStats.newBuyers,    prev:teamStatsPrev.newBuyers,    chg:change(teamStats,teamStatsPrev,"newBuyers"),c:"#4ade80"},
              {l:"Commission Owed", v:fmt(teamStats.comm),    prev:fmt(teamStatsPrev.comm),    chg:null,                                      c:"#4ade80"},
              {l:"Bazooka True Net",v:fmt(teamStats.trueNet), prev:fmt(teamStatsPrev.trueNet), chg:change(teamStats,teamStatsPrev,"trueNet"), c:"#A78BFA"},
              {l:"Streams",         v:teamStats.streams,      prev:teamStatsPrev.streams,      chg:null,                                      c:"var(--bz-ink)"},
            ].map(({l,v,prev,chg,c})=>(
              <div key={l} style={{background:"var(--bz-bg)",borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
                <div style={{fontSize:20,fontWeight:900,color:c}}>{v}</div>
                <div style={{fontSize:9,color:"var(--bz-ink-3)",textTransform:"uppercase",letterSpacing:1,marginTop:4}}>{l}</div>
                {prev&&<div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:2}}>prev: {prev}{chg!==null?<span style={{color:chg>=0?"#4ade80":"#E8317A",marginLeft:4}}>{chg>=0?"+":""}{chg}%</span>:null}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Per-rep cards */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12}}>
          {repData.map(({b,this:t,prev:p})=>{
            const bc = BC[b]?.text||"#E8317A";
            const mmChg = t.mm&&p.mm ? t.mm-p.mm : null;
            const splitEarnings = thisWeek.filter(s=>(s.splitRep===b||((s.eventStaff||[]).some(es=>es.breaker===b)&&s.breaker!==b)));
            return (
              <div key={b} style={{background:"var(--bz-s1)",border:`1px solid ${bc}22`,borderRadius:12,padding:"16px 18px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div style={{fontSize:15,fontWeight:900,color:bc}}>{b}</div>
                  <div style={{fontSize:11,color:"var(--bz-ink-3)"}}>{t.streams} stream{t.streams!==1?"s":""}
                    {splitEarnings.length>0&&<span style={{color:"#FBBF24",marginLeft:6}}>+{splitEarnings.length} split/event</span>}
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
                  {[
                    {l:"Gross",        v:fmt(t.gross),          pv:fmt(p.gross)},
                    {l:"Avg MM",       v:t.mm?t.mm.toFixed(2)+"x":"--", pv:p.mm?p.mm.toFixed(2)+"x":"--", color:t.mm?mmColor(t.mm):"#555"},
                    {l:"New Buyers",   v:t.newBuyers,           pv:p.newBuyers},
                    {l:"Commission",   v:fmt(t.comm),           pv:fmt(p.comm)},
                    {l:"Baz True Net", v:fmt(t.trueNet),        pv:fmt(p.trueNet), color:"#A78BFA"},
                  ].map(({l,v,pv,color})=>(
                    <div key={l} style={{background:"var(--bz-bg)",borderRadius:8,padding:"8px 10px",textAlign:"center"}}>
                      <div style={{fontSize:14,fontWeight:900,color:color||"var(--bz-ink)"}}>{v}</div>
                      <div style={{fontSize:9,color:"var(--bz-ink-3)",textTransform:"uppercase",marginTop:2}}>{l}</div>
                      {pv!==undefined&&<div style={{fontSize:9,color:"var(--bz-ink-3)",marginTop:1}}>prev: {pv}</div>}
                    </div>
                  ))}
                </div>
                {mmChg!==null&&<div style={{marginTop:8,fontSize:11,color:mmChg>=0?"#4ade80":"#E8317A",fontWeight:700}}>{mmChg>=0?"▲":"▼"} {Math.abs(mmChg).toFixed(2)}x vs last week</div>}
              </div>
            );
          })}
        </div>

        {/* Suggestions */}
        <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)"}}>💡 Coaching Notes & Action Items</div>
            <button onClick={addCustomSug} style={{background:"rgba(232,49,122,0.1)",border:"1px solid rgba(232,49,122,0.2)",color:"#E8317A",borderRadius:8,padding:"4px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>+ Add Note</button>
          </div>
          {activeSuggestions.length===0 ? <div style={{color:"var(--bz-ink-3)",fontSize:12,textAlign:"center",padding:"16px 0"}}>No notes for this week — add one above</div> :
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {activeSuggestions.map(s=>{
              const bc = s.rep&&BC[s.rep]?.text||"#FBBF24";
              return (
                <div key={s.id} style={{display:"flex",gap:12,alignItems:"flex-start",padding:"10px 14px",background:"var(--bz-bg)",borderRadius:8,border:`1px solid ${bc}22`}}>
                  {s.rep&&s.rep!=="Team"&&s.rep!=="Custom"&&<div style={{fontSize:11,fontWeight:700,color:bc,minWidth:60,paddingTop:2}}>{s.rep}</div>}
                  {editingSug===s.id ? (
                    <div style={{flex:1,display:"flex",gap:8}}>
                      <textarea value={editText} onChange={e=>setEditText(e.target.value)} style={{flex:1,background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:6,color:"var(--bz-ink)",padding:"6px 10px",fontSize:12,fontFamily:"inherit",minHeight:60,resize:"vertical"}}/>
                      <div style={{display:"flex",flexDirection:"column",gap:4}}>
                        <button onClick={()=>saveSugEdit(s.id)} style={{background:"rgba(74,222,128,0.15)",border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>Save</button>
                        <button onClick={()=>setEditingSug(null)} style={{background:"none",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{flex:1,fontSize:12,color:"var(--bz-ink-2)",lineHeight:1.6}}>{s.text}</div>
                      <div style={{display:"flex",gap:4,flexShrink:0}}>
                        <button onClick={()=>{setEditingSug(s.id);setEditText(s.text);}} style={{background:"none",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:6,padding:"3px 8px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>✏️</button>
                        <button onClick={()=>deleteSug(s.id)} style={{background:"none",border:"1px solid rgba(239,68,68,0.3)",color:"#ef4444",borderRadius:6,padding:"3px 8px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>}
        </div>
      </>}
    </div>
  );
}

// ── SHARED UI COMPONENTS ──────────────────────────────────────────────────────
function BrandFilter({ brand, setBrand }) {
  return (
    <div style={{display:"flex",gap:4}}>
      {[["boba","🃏 BoBA"],["wotf","🐉 WotF"],["all","All"]].map(([id,l])=>(
        <button key={id} onClick={()=>setBrand(id)} style={{background:brand===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${brand===id?"#E8317A":"#333"}`,color:brand===id?"#E8317A":"#555",borderRadius:16,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
      ))}
    </div>
  );
}

function PeriodFilter({ period, setPeriod, options=[["week","Week"],["month","Month"],["quarter","Quarter"],["year","Year"],["all","All Time"]] }) {
  return (
    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
      {options.map(([id,l])=>(
        <button key={id} onClick={()=>setPeriod(id)} style={{background:period===id?"rgba(232,49,122,0.15)":"transparent",border:`1px solid ${period===id?"#E8317A":"#333"}`,color:period===id?"#E8317A":"#555",borderRadius:16,padding:"5px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
      ))}
    </div>
  );
}

function repColor(b) { return BC[b]?.text || "#E8317A"; }

function Finance({ streams=[], userRole, quotes=[] }) {
  const [expenses,    setExpenses]    = useState([]);
  const [lots,        setLots]        = useState([]);   // card-purchase lots (boba_lots/{uid})
  const [loading,     setLoading]     = useState(true);
  const [showAdd,     setShowAdd]     = useState(false);
  const [editId,      setEditId]      = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [selMonth,    setSelMonth]    = useState(() => {
    const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`;
  });
  const EMPTY_EXP = { date:"", category:"Inventory / Cards", description:"", amount:"", notes:"" };
  const [form, setForm] = useState(EMPTY_EXP);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "cash_expenses"), snap => {
      setExpenses(snap.docs.map(d => ({...d.data(), id:d.id})).sort((a,b)=>b.date.localeCompare(a.date)));
      setLoading(false);
    });
    return () => unsub();
  }, []);
  // Card-purchase lots live in boba_lots/{uid} for the signed-in admin. Every lot with a cost is a
  // business purchase, so it feeds cash-out below (deduped against accepted quotes for the same buy).
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) { setLots([]); return; }
    const unsub = onSnapshot(doc(db, "boba_lots", uid), snap => {
      setLots(snap.exists() ? (snap.data().lots || []) : []);
    }, e => { console.error("finance lots load failed:", e); setLots([]); });
    return () => unsub();
  }, []);

  const months = [...new Set([
    selMonth,
    ...expenses.map(e => e.date?.slice(0,7)).filter(Boolean)
  ])].sort((a,b) => b.localeCompare(a));

  // Month data
  const monthExp    = expenses.filter(e => e.date?.startsWith(selMonth));
  const monthStreams = streams.filter(s => s.date?.startsWith(selMonth));
  const grossIn     = monthStreams.reduce((s,str) => s + (parseFloat(str.grossRevenue)||0), 0);
  const totalFees   = monthStreams.reduce((s,str) => s + (parseFloat(str.whatnotFees)||0), 0);
  const totalCoupons= monthStreams.reduce((s,str) => s + (parseFloat(str.coupons)||0), 0);
  const totalPromo  = monthStreams.reduce((s,str) => s + (parseFloat(str.whatnotPromo)||0), 0);
  const netRevIn    = grossIn - totalFees - totalCoupons - totalPromo;

  // Auto: accepted lot purchases this month
  const lotPurchases = quotes.filter(q => {
    if (q.status !== "accepted") return false;
    if (parseFloat(q.currentOffer||q.dispOffer||0) <= 0) return false;
    const dateStr = q.respondedAt || q.importedAt || q.createdAt || "";
    // If no date at all, show it (don't hide it)
    if (!dateStr) return true;
    return dateStr.startsWith(selMonth);
  }).map(q => ({
    key: q.id,
    label: `Lot — ${q.seller?.name||"Seller"} (${(q.cards||[]).length} cards)`,
    date: (q.respondedAt||q.importedAt||q.createdAt||"—").slice(0,10),
    amount: parseFloat(q.currentOffer||q.dispOffer||0),
  }));
  const totalLotPurchases = lotPurchases.reduce((s,l)=>s+l.amount,0);

  // Card lots imported WITH a cost are business purchases too. Sum this month's lot costs, but avoid
  // double-counting a buy that was ALSO logged as an accepted quote above: we drop any lot cost whose
  // amount matches an accepted-quote total on the same day (that's the same purchase entered twice).
  const _quoteDayAmt = new Set(lotPurchases.map(q => `${q.date}|${Math.round(q.amount)}`));
  const monthLots = (lots||[]).filter(l => {
    const c = parseFloat(l.cost||0);
    if (c <= 0) return false;
    const d = (l.date||"").slice(0,7);
    if (d && d !== selMonth) return false;      // no date -> still counted
    return true;
  });
  // Group lot costs by day so a multi-card lot bought together dedupes as one purchase against a quote.
  const _lotByDay = {};
  monthLots.forEach(l => { const day=(l.date||"").slice(0,10)||"—"; _lotByDay[day]=(_lotByDay[day]||0)+parseFloat(l.cost||0); });
  let totalLotCostOut = 0;
  const lotCostRows = [];
  Object.entries(_lotByDay).forEach(([day, amt]) => {
    if (_quoteDayAmt.has(`${day}|${Math.round(amt)}`)) return; // same buy already counted as a quote
    totalLotCostOut += amt;
    lotCostRows.push({ key:`lot-${day}`, label:`Card lot purchase`, date:day, amount:amt });
  });

  const totalAutoOut = totalLotPurchases + totalLotCostOut;
  const totalManualOut = monthExp.reduce((s,e) => s + (parseFloat(e.amount)||0), 0);
  const totalOut    = totalAutoOut + totalManualOut;
  const cashFlow    = netRevIn - totalOut;

  const byCategory = EXPENSE_CATEGORIES.map(cat => ({
    cat, total: monthExp.filter(e=>e.category===cat).reduce((s,e)=>s+(parseFloat(e.amount)||0),0)
  })).filter(c=>c.total>0).sort((a,b)=>b.total-a.total);

  const fmt = v => "$"+Number(v||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});

  async function saveExpense() {
    if (!form.date || !form.amount) return;
    setSaving(true);
    const data = { ...form, amount:parseFloat(form.amount)||0, updatedAt:new Date().toISOString() };
    if (editId) {
      await setDoc(doc(db,"cash_expenses",editId), {...data, id:editId});
    } else {
      const id = uid();
      await setDoc(doc(db,"cash_expenses",id), {...data, id});
    }
    setForm(EMPTY_EXP); setShowAdd(false); setEditId(null); setSaving(false);
  }

  async function deleteExpense(id) {
    if (!window.confirm("Delete this expense?")) return;
    await deleteDoc(doc(db,"cash_expenses",id));
  }

  function startEdit(e) {
    setForm({ date:e.date, category:e.category, description:e.description||"", amount:String(e.amount), notes:e.notes||"" });
    setEditId(e.id); setShowAdd(true);
  }

  const cashColor = cashFlow >= 0 ? "#4ade80" : "#ef4444";

  // Monthly summary for chart (last 6 months)
  const last6 = months.slice(0,6).reverse();
  const chartData = last6.map(m => {
    const mStreams = streams.filter(s=>s.date?.startsWith(m));
    const mIn = mStreams.reduce((s,str)=>s+(parseFloat(str.grossRevenue)||0)-(parseFloat(str.whatnotFees)||0)-(parseFloat(str.coupons)||0)-(parseFloat(str.whatnotPromo)||0),0);
    const mOut = expenses.filter(e=>e.date?.startsWith(m)).reduce((s,e)=>s+(parseFloat(e.amount)||0),0);
    const label = new Date(m+"-15").toLocaleDateString("en-US",{month:"short",year:"2-digit"});
    return { m, label, mIn, mOut, net:mIn-mOut };
  });
  const maxVal = Math.max(...chartData.map(d=>Math.max(d.mIn,d.mOut,1)));

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16,padding:"0 0 40px"}}>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:20,fontWeight:900,color:"var(--bz-ink)",letterSpacing:"-0.02em",display:"flex",alignItems:"center",gap:10}}><span className="bzx-h-accent" style={{height:22}} />Cash Flow</div>
          <div style={{fontSize:12,color:"var(--bz-ink-3)",marginTop:3,marginLeft:20}}>CEO / CFO only · gross revenue in vs. cash out</div>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <select value={selMonth} onChange={e=>setSelMonth(e.target.value)}
            style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:9,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",cursor:"pointer"}}>
            {months.map(m=><option key={m} value={m}>{new Date(m+"-15").toLocaleDateString("en-US",{month:"long",year:"numeric"})}</option>)}
          </select>
          <button className="bzx-btn bzx-btn-primary" onClick={()=>{setForm(EMPTY_EXP);setEditId(null);setShowAdd(p=>!p);}} style={{padding:"9px 18px",fontSize:13}}>
            + Add Expense
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12,marginBottom:4}}>
        <div className="bzx-card bzx-card-lift">
          <div className="bzx-stat-l">Gross Revenue</div>
          <div style={{fontSize:28,fontWeight:900,color:"var(--bz-ink)",letterSpacing:"-0.02em",marginTop:5}}>{fmt(grossIn)}</div>
          <div style={{fontSize:11,color:"var(--bz-ink-3)",marginTop:4}}>{monthStreams.length} streams</div>
          <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:4}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:11}}><span style={{color:"var(--bz-ink-3)"}}>− Whatnot Fees</span><span style={{color:"#ff6b8a"}}>−{fmt(totalFees)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:11}}><span style={{color:"var(--bz-ink-3)"}}>− Coupons</span><span style={{color:"#ff6b8a"}}>−{fmt(totalCoupons)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:11}}><span style={{color:"var(--bz-ink-3)"}}>− Whatnot Promo</span><span style={{color:"#ff6b8a"}}>−{fmt(totalPromo)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:12,fontWeight:800,borderTop:"1px solid var(--bz-line-2)",paddingTop:6,marginTop:3}}><span style={{color:"var(--bz-green)"}}>= Net Revenue In</span><span style={{color:"var(--bz-green)"}}>{fmt(netRevIn)}</span></div>
          </div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div className="bzx-card bzx-card-lift">
            <div className="bzx-stat-l">Total Cash Out</div>
            <div style={{fontSize:26,fontWeight:900,color:"var(--bz-pink)",letterSpacing:"-0.02em",marginTop:5}}>{fmt(totalOut)}</div>
            <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:4}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11}}><span style={{color:"var(--bz-ink-3)"}}>Lot Purchases</span><span style={{color:"var(--bz-pink-hot)"}}>−{fmt(totalLotPurchases)}</span></div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11}}><span style={{color:"var(--bz-ink-3)"}}>Other Expenses</span><span style={{color:"var(--bz-pink-hot)"}}>−{fmt(totalManualOut)}</span></div>
            </div>
          </div>
          <div className="bzx-card bzx-card-lift" style={{border:`1px solid ${cashFlow>=0?"rgba(52,211,153,0.35)":"rgba(239,68,68,0.35)"}`,textAlign:"center"}}>
            <div className="bzx-stat-l">Net Cash Flow</div>
            <div style={{fontSize:26,fontWeight:900,color:cashFlow>=0?"var(--bz-green)":"#ef4444",letterSpacing:"-0.02em",marginTop:5}}>{fmt(cashFlow)}</div>
            <div style={{fontSize:11,color:cashFlow>=0?"var(--bz-green)":"#ef4444",marginTop:4,fontWeight:700}}>{cashFlow>=0?"Positive ✓":"Negative ⚠"}</div>
          </div>
        </div>
      </div>

      {/* Bar chart */}
      {chartData.length > 1 && (
        <div className="bzx-card">
          <div className="bzx-h" style={{marginBottom:16}}><span className="bzx-h-accent" />Monthly Cash Flow Trend</div>
          <div style={{display:"flex",gap:8,alignItems:"flex-end",height:160}}>
            {chartData.map(d=>(
              <div key={d.m} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                <div style={{width:"100%",display:"flex",gap:3,alignItems:"flex-end",height:130}}>
                  <div title={`Revenue: ${fmt(d.mIn)}`} style={{flex:1,background:"rgba(74,222,128,0.4)",borderRadius:"3px 3px 0 0",height:`${(d.mIn/maxVal)*100}%`,minHeight:2}}/>
                  <div title={`Expenses: ${fmt(d.mOut)}`} style={{flex:1,background:"rgba(232,49,122,0.4)",borderRadius:"3px 3px 0 0",height:`${(d.mOut/maxVal)*100}%`,minHeight:2}}/>
                </div>
                <div style={{fontSize:9,color:"var(--bz-ink-3)",textAlign:"center"}}>{d.label}</div>
                <div style={{fontSize:9,fontWeight:700,color:d.net>=0?"#4ade80":"#ef4444"}}>{d.net>=0?"+":""}{fmt(d.net)}</div>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:16,justifyContent:"center",marginTop:8}}>
            <div style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"var(--bz-ink-3)"}}><div style={{width:10,height:10,background:"rgba(74,222,128,0.4)",borderRadius:2}}/> Revenue In</div>
            <div style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"var(--bz-ink-3)"}}><div style={{width:10,height:10,background:"rgba(232,49,122,0.4)",borderRadius:2}}/> Cash Out</div>
          </div>
        </div>
      )}

      {/* Add/Edit form */}
      {showAdd && (
        <div style={{background:"var(--bz-bg)",border:"2px solid #E8317A33",borderRadius:12,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:14}}>{editId?"✏️ Edit Expense":"+ New Expense"}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Date *</div>
              <input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))}
                style={{width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Category *</div>
              <select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}
                style={{width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",cursor:"pointer",boxSizing:"border-box"}}>
                {EXPENSE_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Amount *</div>
              <input type="number" step="0.01" value={form.amount} onChange={e=>setForm(p=>({...p,amount:e.target.value}))} placeholder="0.00"
                style={{width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"#E8317A",padding:"8px 12px",fontSize:13,fontFamily:"inherit",fontWeight:700,boxSizing:"border-box"}}/>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Description</div>
              <input value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="e.g. Alpha Edition restock from Discord..."
                style={{width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
            <div>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",marginBottom:4}}>Notes</div>
              <input value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="Optional..."
                style={{width:"100%",background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:8,color:"var(--bz-ink)",padding:"8px 12px",fontSize:13,fontFamily:"inherit",boxSizing:"border-box"}}/>
            </div>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={saveExpense} disabled={saving||!form.date||!form.amount}
              style={{background:"linear-gradient(135deg,#E8317A,#c02060)",color:"#fff",border:"none",borderRadius:8,padding:"9px 24px",fontSize:13,fontWeight:800,cursor:"pointer",fontFamily:"inherit",opacity:saving||!form.date||!form.amount?0.5:1}}>
              {saving?"Saving...":editId?"Update":"Save Expense"}
            </button>
            <button onClick={()=>{setShowAdd(false);setEditId(null);setForm(EMPTY_EXP);}}
              style={{background:"transparent",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:8,padding:"9px 18px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Category breakdown */}
      {byCategory.length > 0 && (
        <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:12}}>Expenses by Category — {new Date(selMonth+"-15").toLocaleDateString("en-US",{month:"long",year:"numeric"})}</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {byCategory.map(({cat,total})=>{
              const pct = totalOut>0?total/totalOut*100:0;
              return (
                <div key={cat} style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:140,fontSize:12,color:"var(--bz-ink-2)",flexShrink:0}}>{cat}</div>
                  <div style={{flex:1,height:6,background:"var(--bz-s2)",borderRadius:3,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pct}%`,background:"#E8317A",borderRadius:3}}/>
                  </div>
                  <div style={{fontSize:12,fontWeight:700,color:"#E8317A",minWidth:90,textAlign:"right"}}>{fmt(total)}</div>
                  <div style={{fontSize:11,color:"var(--bz-ink-3)",minWidth:40,textAlign:"right"}}>{pct.toFixed(0)}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Auto-deductions */}
      {(lotPurchases.length > 0 || lotCostRows.length > 0) && (
        <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px"}}>
          <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:14}}>⚡ Auto-Calculated Expenses</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {/* Lot purchases (accepted seller quotes) */}
            {lotPurchases.map(l=>(
              <div key={l.key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:"var(--bz-s2)",borderRadius:8}}>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:"var(--bz-ink)"}}>📦 {l.label}</div>
                  <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:1}}>Accepted lot · {l.date}</div>
                </div>
                <div style={{fontSize:14,fontWeight:900,color:"#FBBF24"}}>−{fmt(l.amount)}</div>
              </div>
            ))}
            {/* Card lots imported with a cost (deduped against the accepted quotes above) */}
            {lotCostRows.map(l=>(
              <div key={l.key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:"var(--bz-s2)",borderRadius:8}}>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:"var(--bz-ink)"}}>🃏 {l.label}</div>
                  <div style={{fontSize:10,color:"var(--bz-ink-3)",marginTop:1}}>Imported lot cost · {l.date}</div>
                </div>
                <div style={{fontSize:14,fontWeight:900,color:"#FBBF24"}}>−{fmt(l.amount)}</div>
              </div>
            ))}
            <div style={{display:"flex",justifyContent:"space-between",padding:"6px 12px",borderTop:"1px solid var(--bz-line)",marginTop:2}}>
              <div style={{fontSize:11,color:"var(--bz-ink-3)",fontWeight:700}}>Auto Expenses Total</div>
              <div style={{fontSize:13,fontWeight:900,color:"#FBBF24"}}>−{fmt(totalAutoOut)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Expense Log */}
      <div style={{background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:12,padding:"18px 20px"}}>
        <div style={{fontSize:13,fontWeight:800,color:"var(--bz-ink)",marginBottom:14,display:"flex",alignItems:"center"}}>
          📋 Other Expenses — {new Date(selMonth+"-15").toLocaleDateString("en-US",{month:"long",year:"numeric"})}
          {monthExp.length>0&&<span style={{fontSize:11,color:"var(--bz-ink-3)",marginLeft:8}}>{monthExp.length} entries</span>}
        </div>
        {loading ? <div style={{color:"var(--bz-ink-3)",fontSize:12}}>Loading...</div>
        : monthExp.length===0 ? <div style={{textAlign:"center",color:"var(--bz-ink-3)",padding:"30px 0",fontSize:12}}>No expenses logged for this month yet</div>
        : <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {monthExp.map(e=>(
              <div key={e.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:"var(--bz-bg)",border:"1px solid var(--bz-line)",borderRadius:8,flexWrap:"wrap"}}>
                <div style={{fontSize:11,color:"var(--bz-ink-3)",minWidth:70}}>{new Date(e.date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</div>
                <div style={{fontSize:11,fontWeight:700,color:"#E8317A",background:"rgba(232,49,122,0.08)",borderRadius:4,padding:"2px 8px",whiteSpace:"nowrap"}}>{e.category}</div>
                <div style={{flex:1,fontSize:13,color:"var(--bz-ink)"}}>{e.description||<span style={{color:"var(--bz-ink-3)"}}>—</span>}</div>
                {e.notes&&<div style={{fontSize:11,color:"var(--bz-ink-3)"}}>{e.notes}</div>}
                <div style={{fontSize:14,fontWeight:900,color:"#E8317A",minWidth:90,textAlign:"right"}}>{fmt(e.amount)}</div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>startEdit(e)} style={{background:"none",border:"1px solid var(--bz-line-2)",color:"var(--bz-ink-3)",borderRadius:6,padding:"3px 8px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Edit</button>
                  <button onClick={()=>deleteExpense(e.id)} style={{background:"none",border:"1px solid #E8317A33",color:"#E8317A",borderRadius:6,padding:"3px 8px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>✕</button>
                </div>
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
}

function BugAdmin({ user }) {
  const [bugs, setBugs] = useState([]);
  const [filter, setFilter] = useState("open");
  const isAdmin = (user?.email||"").toLowerCase().endsWith("@bazookabreaks.com");
  useEffect(() => {
    if (!isAdmin) return;
    const unsub = onSnapshot(collection(db,"bug_reports"), snap => {
      setBugs(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||"")));
    });
    return ()=>unsub();
  }, [isAdmin]);
  async function setStatus(id, status) { try { await setDoc(doc(db,"bug_reports",id), { status }, { merge:true }); } catch(e){} }
  if (!isAdmin) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100vh",background:"#08000a",color:"#E8317A",fontFamily:"'Trebuchet MS',sans-serif",fontWeight:700,padding:24,textAlign:"center",gap:10}}>
      <div style={{fontSize:18}}>Admins only.</div>
      <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",fontWeight:600,maxWidth:420,lineHeight:1.5}}>
        {user ? <>You're signed in as <strong style={{color:"#fff"}}>{user.email}</strong>, which isn't an admin account. The bug list is only visible to <strong style={{color:"#fff"}}>@bazookabreaks.com</strong> accounts — sign in with yours to see reports.</> : <>You're not signed in. Sign in with your <strong style={{color:"#fff"}}>@bazookabreaks.com</strong> account to view bug reports.</>}
      </div>
    </div>
  );
  const shown = bugs.filter(b => filter==="all" ? true : (b.status||"open")===filter);
  const sevColor = {minor:"#4ade80",annoying:"#FBBF24",broken:"#E8317A"};
  const counts = { open: bugs.filter(b=>(b.status||"open")==="open").length, resolved: bugs.filter(b=>b.status==="resolved").length };
  return (
    <div style={{minHeight:"100vh",background:"#0a0a0f",color:"var(--bz-ink)",fontFamily:"'Trebuchet MS',sans-serif",padding:"24px 18px",maxWidth:780,margin:"0 auto"}}>
      <div style={{fontSize:26,fontWeight:900,marginBottom:4}}>🐛 Bug Reports</div>
      <div style={{fontSize:13,color:"var(--bz-ink-2)",marginBottom:18}}>{counts.open} open · {counts.resolved} resolved · {bugs.length} total</div>
      <div style={{display:"flex",gap:8,marginBottom:18}}>
        {[["open","Open"],["resolved","Resolved"],["all","All"]].map(([v,l])=>(
          <button key={v} onClick={()=>setFilter(v)} style={{background:filter===v?"rgba(232,49,122,0.15)":"transparent",color:filter===v?"#E8317A":"#888",border:`1.5px solid ${filter===v?"#E8317A":"rgba(255,255,255,0.1)"}`,borderRadius:20,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
        ))}
      </div>
      {shown.length===0 && <div style={{color:"var(--bz-ink-3)",textAlign:"center",padding:40}}>No {filter==="all"?"":filter} reports.</div>}
      {shown.map(b=>(
        <div key={b.id} style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:"14px 16px",marginBottom:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:8}}>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              <span style={{fontSize:11,fontWeight:800,color:sevColor[b.severity]||"#888",background:(sevColor[b.severity]||"#888")+"22",borderRadius:5,padding:"2px 9px",textTransform:"uppercase"}}>{b.severity||"?"}</span>
              <span style={{fontSize:12,color:"var(--bz-ink-2)"}}>{b.reporterName||b.reporterEmail}</span>
              {b.status==="resolved" && <span style={{fontSize:11,color:"#4ade80"}}>✓ resolved</span>}
            </div>
            <button onClick={()=>setStatus(b.id, b.status==="resolved"?"open":"resolved")} style={{background:"transparent",border:`1px solid ${b.status==="resolved"?"#666":"#4ade80"}`,color:b.status==="resolved"?"#888":"#4ade80",borderRadius:7,padding:"4px 11px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>{b.status==="resolved"?"Reopen":"Mark fixed"}</button>
          </div>
          <div style={{fontSize:14,color:"#fff",lineHeight:1.5,marginBottom:10,whiteSpace:"pre-wrap"}}>{b.description}</div>
          {b.screenshotUrl && (
            <a href={b.screenshotUrl} target="_blank" rel="noopener noreferrer" style={{display:"block",marginBottom:10}}>
              <img src={b.screenshotUrl} alt="screenshot" style={{maxWidth:"100%",maxHeight:260,borderRadius:8,border:"1px solid rgba(255,255,255,0.12)",display:"block"}}/>
              <span style={{fontSize:10.5,color:"#7B9CFF"}}>📸 Click to open full screenshot</span>
            </a>
          )}
          <div style={{fontSize:10.5,color:"#999",lineHeight:1.6,borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:8}}>
            📍 {b.page||"?"} · 📱 {b.screen||"?"} · {new Date(b.createdAt).toLocaleString()}<br/>
            <span style={{color:"#8a8a8a"}}>{b.userAgent}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── HELP ─────────────────────────────────────────────────────────────────────
// Every question here is one the app CAN answer but currently doesn't, so people have to ask a
// human — or, more likely, quietly assume the app is broken and stop using it.
//
// Most of these came straight out of real confusion: family sharing that looked one-sided, a first
// load that seemed frozen, trade bait that appeared to do nothing. If a question keeps reaching a
// person, that's a gap in the product, not a gap in the support team.
function HelpPanel({ onClose }) {
  const [openQ, setOpenQ] = useState(null);

  const QA = [
    {
      q: "Why is the app slow the first time I open it?",
      a: "The first visit downloads the entire card database \u2014 all 35,000 BoBA cards \u2014 so it can work fast afterwards. That's a one-off. Every visit after this loads instantly from your device."
    },
    {
      q: "How does family sharing work?",
      a: "It's mutual. Marking someone as family shares YOUR cards with THEM \u2014 it doesn't take theirs. You'll see their cards once they share back with you. Until then the button shows \u201CWaiting\u201D, which means they haven't reciprocated yet. Nobody can see your collection without your say-so."
    },
    {
      q: "I flagged cards as trade bait. Why isn't anything happening?",
      a: "Flagging a card is private on its own. To be found for trades you also have to switch on \u201CList me\u201D at the top of the Trade Bait tab. Once you do, the cards you've flagged show up in the marketplace under For Trade, and anyone with one of them on their want list gets notified. Switch it off and your list goes private again immediately."
    },
    {
      q: "What's the difference between a duplicate and trade bait?",
      a: "Any card you own more than one of shows up in your Trade Bait tab automatically \u2014 but that's just for your own reference. Nothing is offered to anyone until you explicitly flag it AND list yourself. Owning a spare isn't the same as wanting to trade it."
    },
    {
      q: "Why can't I see all 35,000 cards at once?",
      a: "The grid paints 600 at a time, because rendering all of them at once locks up the page for several seconds. Use the filters or search to narrow things down \u2014 or hit \u201CLoad more\u201D / \u201CShow all\u201D at the bottom if you really want the lot."
    },
    {
      q: "Why won't a card go into my deck?",
      a: "Hover the card and it'll tell you \u2014 usually it's already in the deck, the deck is full, or the format's rules don't allow a second copy of that hero. Different formats have different limits."
    },
    {
      q: "Who can see my collection?",
      a: "Only you, unless you choose otherwise. Cards you mark public show on your profile. Family sharing is mutual and consented. Trade bait is private until you list yourself. Your want list is visible so collectors can be notified when someone has a card you're chasing."
    },
    {
      q: "I found a bug. What do I do?",
      a: "Hit \uD83D\uDC1B Report Bug \u2014 it's next to this button. Tick \u201Cattach screenshot\u201D and it captures the page automatically, which makes it far easier to fix. It genuinely helps."
    },
  ];

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.82)",zIndex:14500,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line-2)",borderRadius:16,padding:24,maxWidth:580,width:"100%",maxHeight:"85vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontSize:18,fontWeight:900,color:"#fff"}}>{"\u2753"} Help</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#666",fontSize:22,cursor:"pointer",lineHeight:1,fontFamily:"inherit"}}>{"\u00D7"}</button>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:7}}>
          {QA.map((item,i)=>{
            const open = openQ === i;
            return (
              <div key={i} style={{border:`1px solid ${open?"#333":"#242424"}`,borderRadius:10,overflow:"hidden",background:open?"rgba(255,255,255,0.02)":"transparent"}}>
                <button onClick={()=>setOpenQ(open?null:i)}
                  style={{width:"100%",textAlign:"left",background:"none",border:"none",padding:"12px 14px",cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:11,color:"#666",flexShrink:0}}>{open?"\u25BE":"\u25B8"}</span>
                  <span style={{fontSize:13,fontWeight:700,color:open?"#fff":"#ccc",lineHeight:1.4}}>{item.q}</span>
                </button>
                {open && (
                  <div style={{padding:"0 14px 14px 34px",fontSize:12.5,color:"rgba(255,255,255,0.55)",lineHeight:1.7}}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{marginTop:18,paddingTop:14,borderTop:"1px solid #242424",fontSize:11.5,color:"rgba(255,255,255,0.35)",lineHeight:1.6,textAlign:"center"}}>
          Still stuck? Hit {"\uD83D\uDC1B"} Report Bug and tell us what happened {"\u2014"} we read every one.
        </div>
      </div>
    </div>
  );
}

function BugReporter({ user }) {
  const [open, setOpen] = useState(false);
  // Help sits next to Report Bug on purpose: someone who is confused reaches for the same corner
  // as someone who thinks something is broken \u2014 and most of the time they are the same person.
  // Answering the question first means they never file the bug at all.
  const [helpOpen, setHelpOpen] = useState(false);
  const [fabOpen,  setFabOpen]  = useState(false);   // the fan-out, collapsed by default
  const [desc, setDesc] = useState("");
  const [severity, setSeverity] = useState("annoying");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [attachShot, setAttachShot] = useState(true);

  function loadHtml2Canvas() {
    return new Promise((resolve, reject) => {
      if (window.html2canvas) return resolve(window.html2canvas);
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
      s.onload = () => resolve(window.html2canvas);
      s.onerror = () => reject(new Error("Couldn't load screenshot library"));
      document.body.appendChild(s);
    });
  }

  async function submit() {
    if (!desc.trim()) return;
    setSending(true);
    try {
      // Optionally grab a screenshot of the page BEFORE we open/cover anything.
      let screenshotUrl = null;
      if (attachShot) {
        try {
          setOpen(false); // hide the modal so it isn't in the shot
          await new Promise(r => setTimeout(r, 250));
          const h2c = await loadHtml2Canvas();
          const canvas = await h2c(document.body, { scale: 0.6, logging:false, useCORS:true, windowWidth: document.documentElement.clientWidth });
          const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
          const shotRef = ref(storage, `bug_shots/${Date.now()}_${Math.random().toString(36).slice(2,8)}.jpg`);
          await uploadString(shotRef, dataUrl, "data_url");
          screenshotUrl = await getDownloadURL(shotRef);
        } catch(e) { /* screenshot is best-effort; never block the report */ screenshotUrl = null; }
        setOpen(true);
      }
      const ctx = {
        description: desc.trim(),
        severity,
        screenshotUrl,
        page: typeof window !== "undefined" ? window.location.pathname + window.location.search : "",
        url: typeof window !== "undefined" ? window.location.href : "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        screen: typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : "",
        platform: typeof navigator !== "undefined" ? (navigator.platform||"") : "",
        reporterEmail: user?.email || "anonymous",
        reporterName: user?.displayName || user?.email || "anonymous",
        userId: user?.uid || null,
        status: "open",
        createdAt: new Date().toISOString(),
      };
      await setDoc(doc(collection(db, "bug_reports")), ctx);
      setSent(true);
      setDesc("");
      setTimeout(() => { setSent(false); setOpen(false); }, 2200);
    } catch(e) {
      alert("Couldn't send report: " + (e?.message||e));
    }
    setSending(false);
  }

  return (
    <>
      {/* One small button, not two banners. Two permanently-parked pills over the app is clutter —
          and the whole point of the feedback we got is that the app shouldn't shout at you. Tap it
          and the two real actions fan out; tap anywhere else and they fold away again. */}
      <div style={{position:"fixed", bottom:18, left:18, zIndex:11500}}>
        {fabOpen && (
          <>
            {/* Click-catcher so tapping anywhere closes the fan. */}
            <div onClick={()=>setFabOpen(false)} style={{position:"fixed",inset:0,zIndex:-1}}/>
            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:10,animation:"fabIn 0.15s ease"}}>
              <button onClick={()=>{setHelpOpen(true);setFabOpen(false);}} style={{
                background:"rgba(20,20,20,0.95)", color:"rgba(255,255,255,0.75)",
                border:"1px solid rgba(255,255,255,0.15)", borderRadius:30,
                padding:"10px 16px", fontSize:12.5, fontWeight:800, cursor:"pointer",
                fontFamily:"inherit", backdropFilter:"blur(8px)", whiteSpace:"nowrap",
                boxShadow:"0 6px 24px rgba(0,0,0,0.5)", display:"flex", alignItems:"center", gap:7
              }}>{"\u2753"} Help</button>
              <button onClick={()=>{setOpen(true);setFabOpen(false);}} style={{
                background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff",
                border:"none", borderRadius:30, padding:"10px 16px", fontSize:12.5, fontWeight:800,
                cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap",
                boxShadow:"0 6px 24px rgba(232,49,122,0.45)", display:"flex", alignItems:"center", gap:7
              }}>{"\uD83D\uDC1B"} Report Bug</button>
            </div>
          </>
        )}
        <button onClick={()=>setFabOpen(o=>!o)} title={fabOpen?"Close":"Help & feedback"} style={{
          width:44, height:44, borderRadius:"50%",
          background: fabOpen ? "rgba(30,30,30,0.95)" : "rgba(20,20,20,0.75)",
          color: fabOpen ? "#fff" : "rgba(255,255,255,0.5)",
          border:"1px solid rgba(255,255,255,0.14)",
          fontSize:17, fontWeight:800, cursor:"pointer", fontFamily:"inherit",
          backdropFilter:"blur(8px)", boxShadow:"0 4px 18px rgba(0,0,0,0.4)",
          display:"flex", alignItems:"center", justifyContent:"center",
          transition:"all 0.18s ease",
          transform: fabOpen ? "rotate(45deg)" : "none"
        }}>{fabOpen ? "\u00D7" : "\u2753"}</button>
      </div>

      {helpOpen && <HelpPanel onClose={()=>setHelpOpen(false)}/>}

      {open && (
        <div onClick={()=>!sending&&setOpen(false)} style={{position:"fixed",inset:0,zIndex:12500,background:"rgba(0,0,0,0.8)",backdropFilter:"blur(6px)",display:"flex",alignItems:"flex-end",justifyContent:"center",padding:0}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#16161f",border:"1px solid rgba(232,49,122,0.3)",borderTopLeftRadius:18,borderTopRightRadius:18,padding:"22px 20px 28px",maxWidth:480,width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,0.6)"}}>
            {sent ? (
              <div style={{textAlign:"center",padding:"24px 0"}}>
                <div style={{fontSize:40,marginBottom:10}}>🎯</div>
                <div style={{fontSize:17,fontWeight:900,color:"#4ade80",marginBottom:5}}>Bug reported — thank you!</div>
                <div style={{fontSize:12,color:"#999"}}>You're helping make the Vault better.</div>
              </div>
            ) : (
              <>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                  <div style={{fontSize:18,fontWeight:900,color:"#fff"}}>🐛 Report a Bug</div>
                  <button onClick={()=>setOpen(false)} style={{background:"none",border:"none",color:"#999",fontSize:22,cursor:"pointer",fontFamily:"inherit"}}>×</button>
                </div>
                <div style={{fontSize:12,color:"#999",marginBottom:14,lineHeight:1.5}}>What broke, what did you expect, and what happened instead? Your page & device info are captured automatically.</div>
                <div style={{fontSize:11,fontWeight:700,color:"var(--bz-ink-2)",marginBottom:6}}>How bad is it?</div>
                <div style={{display:"flex",gap:7,marginBottom:14}}>
                  {[["minor","🟢 Minor","#4ade80"],["annoying","🟡 Annoying","#FBBF24"],["broken","🔴 Broken","#E8317A"]].map(([v,l,c])=>(
                    <button key={v} onClick={()=>setSeverity(v)} style={{flex:1,background:severity===v?c+"22":"transparent",border:`1.5px solid ${severity===v?c:"rgba(255,255,255,0.1)"}`,color:severity===v?c:"#888",borderRadius:9,padding:"8px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>{l}</button>
                  ))}
                </div>
                <textarea value={desc} onChange={e=>setDesc(e.target.value)} autoFocus placeholder="e.g. I clicked a card on /cards and the back was cut off, or the buy button did nothing…" style={{width:"100%",minHeight:110,background:"var(--bz-bg)",color:"#fff",border:"1px solid var(--bz-line-2)",borderRadius:10,padding:"12px",fontSize:14,fontFamily:"inherit",resize:"vertical",lineHeight:1.5}}/>
                <label onClick={()=>setAttachShot(v=>!v)} style={{display:"flex",alignItems:"center",gap:9,marginTop:14,cursor:"pointer",padding:"10px 12px",background:attachShot?"rgba(74,222,128,0.08)":"rgba(255,255,255,0.03)",border:`1px solid ${attachShot?"rgba(74,222,128,0.3)":"rgba(255,255,255,0.1)"}`,borderRadius:9}}>
                  <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${attachShot?"#4ade80":"#555"}`,background:attachShot?"#4ade80":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{attachShot&&<span style={{color:"#000",fontSize:12,fontWeight:900}}>✓</span>}</div>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:attachShot?"#4ade80":"#ccc"}}>📸 Attach a screenshot</div>
                    <div style={{fontSize:11,color:"var(--bz-ink-2)"}}>Snaps a picture of this page so we can see what you see</div>
                  </div>
                </label>
                <button disabled={!desc.trim()||sending} onClick={submit} style={{width:"100%",marginTop:12,background:desc.trim()&&!sending?"linear-gradient(135deg,#E8317A,#7B2FF7)":"var(--bz-line-2)",border:"none",color:desc.trim()&&!sending?"#fff":"#666",borderRadius:10,padding:"13px",fontSize:14,fontWeight:900,cursor:desc.trim()&&!sending?"pointer":"not-allowed",fontFamily:"inherit"}}>{sending?(attachShot?"Capturing & sending…":"Sending…"):"Send Report"}</button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

class AppErrorBoundary extends React.Component {
  constructor(props){ super(props); this.state={ err:null }; }
  static getDerivedStateFromError(err){ return { err }; }
  componentDidCatch(err, info){ console.error("App crashed:", err, info); }
  render(){
    if (this.state.err) {
      return (
        <div style={{ minHeight:"100vh", background:"var(--bz-bg)", color:"#eee", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"system-ui,sans-serif", padding:24 }}>
          <div style={{ maxWidth:480, textAlign:"center" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>⚠️</div>
            <div style={{ fontSize:18, fontWeight:800, marginBottom:8, color:"#E8317A" }}>Something hit a snag</div>
            <div style={{ fontSize:13, color:"#aaa", lineHeight:1.6, marginBottom:20 }}>The dashboard ran into an error while rendering, but your data is safe. Reloading usually clears it.</div>
            <div style={{ fontSize:11, color:"var(--bz-ink-3)", background:"var(--bz-s2)", border:"1px solid var(--bz-line-2)", borderRadius:8, padding:"10px 12px", marginBottom:20, textAlign:"left", wordBreak:"break-word", fontFamily:"monospace" }}>{String(this.state.err && this.state.err.message || this.state.err)}</div>
            <button onClick={()=>{ this.setState({err:null}); window.location.reload(); }} style={{ background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff", border:"none", borderRadius:10, padding:"12px 28px", fontSize:14, fontWeight:800, cursor:"pointer" }}>Reload Dashboard</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Public profile page at bazookadash.com/{username} — resolves the handle to a
// uid, then shows the collector's public profile (stats, public cards, reviews).
function PublicProfilePage({ username }) {
  const [state, setState] = useState({ loading:true, uid:null, data:null, pubCards:{}, reviews:[], cards:[] });
  const [openTrackerId, setOpenTrackerId] = useState(null);
  const [trackerSearch, setTrackerSearch] = useState("");
  const [profileZoom, setProfileZoom] = useState(null); // {card, siblings, hero}
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const uname = String(username||"").toLowerCase().replace(/[^a-z0-9_]/g,"");
        let uid = null; let permErr = false; const diag = [];
        // 1) usernames/{handle} doc
        try { const s = await getDoc(doc(db,"usernames",uname)); if (s.exists()){ uid = s.data().uid; diag.push("usernames:FOUND"); } else diag.push("usernames:empty"); }
        catch(e) { permErr = permErr || /permission|insufficient/i.test(e.message||""); diag.push("usernames:ERR("+(e.code||e.message||"?")+")"); }
        // 2) users where username == handle (lowercase)
        if (!uid) { try { const s = await getDocs(query(collection(db,"users"), where("username","==",uname))); if(!s.empty){ uid = s.docs[0].id; diag.push("users.lc:FOUND"); } else diag.push("users.lc:empty"); }
          catch(e) { permErr = permErr || /permission|insufficient/i.test(e.message||""); diag.push("users.lc:ERR("+(e.code||e.message||"?")+")"); } }
        // 3) users where username == raw casing
        if (!uid && username && username !== uname) { try { const s = await getDocs(query(collection(db,"users"), where("username","==",username))); if(!s.empty){ uid = s.docs[0].id; diag.push("users.raw:FOUND"); } else diag.push("users.raw:empty"); } catch(e){ diag.push("users.raw:ERR"); } }
        // 4) boba_profiles fallback (some flows write handle here)
        if (!uid) { try { const s = await getDocs(query(collection(db,"boba_profiles"), where("username","==",uname))); if(!s.empty){ uid = s.docs[0].id; diag.push("profiles:FOUND"); } else diag.push("profiles:empty"); } catch(e){ permErr = permErr || /permission|insufficient/i.test(e.message||""); diag.push("profiles:ERR("+(e.code||"?")+")"); } }
        console.log("[profile lookup]", uname, "→", diag.join(" | "));
        if (!uid) { if(alive) setState(s=>({...s, loading:false, uid:null, permErr, diag: diag.join(" · ") })); return; }
        const [userSnap, pubSnap, pubCardsSnap, revSnap, trkSnap, listSnap, transitSnap] = await Promise.all([
          getDoc(doc(db,"users",uid)),
          getDoc(doc(db,"boba_public",uid)),
          getDoc(doc(db,"boba_public_cards",uid)),
          getDocs(query(collection(db,"boba_reviews"), where("sellerUid","==",uid))),
          getDoc(doc(db,"boba_trackers",uid)),
          getDocs(query(collection(db,"marketplace"), where("status","==","active"), where("sellerUid","==",uid))),
          getDoc(doc(db,"boba_intransit",uid)).catch(()=>null),
        ]);
        const transitData = (transitSnap && transitSnap.exists) ? (transitSnap.exists() ? transitSnap.data() : {}) : {};
        const listings = listSnap.docs.map(d=>({...d.data(), id:d.id}));
        const allTrackers = (trkSnap.exists() && Array.isArray(trkSnap.data().trackers)) ? trkSnap.data().trackers : [];
        const publicTrackers = allTrackers.filter(t => t.public);
        const pubData = pubSnap.exists() ? pubSnap.data() : {};
        // Denormalized public cards — already carry image + metadata, so NO card-DB fetch.
        const enrichedCards = (pubCardsSnap.exists() && Array.isArray(pubCardsSnap.data().cards)) ? pubCardsSnap.data().cards : [];

        // Full card DB — needed so public TRACKERS can compute their totals (e.g. how many
        // gold-coin heroes exist). Try localStorage cache, then the CDN static file.
        let fullCards = [];
        try { const raw = localStorage.getItem("boba_checklist_cache_v3"); if (raw) { const p = JSON.parse(raw); fullCards = Array.isArray(p) ? p : (p.cards||[]); } } catch(e){}
        if (fullCards.length === 0) {
          try { const r = await fetch("/cards-data.json"); if (r.ok) fullCards = await r.json(); } catch(e){}
        }

        // Everything needed is here in a handful of tiny doc reads — render instantly.
        if(alive) setState({
          loading:false, uid,
          data: userSnap.exists() ? userSnap.data() : {},
          pubCards: pubData,
          reviews: revSnap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||"")),
          cards: enrichedCards,
          transit: transitData,
          fullCards,
          trackers: publicTrackers,
          ownedPub: pubData,
          listings,
        });
      } catch(e) { if(alive) setState(s=>({...s, loading:false })); }
    })();
    return () => { alive = false; };
  }, [username]);

  const TOKENS = ":root{ --bz-ink:#f6eef2; --bz-ink-2:#b9a9b2; --bz-ink-3:#7a6b74; --bz-line:rgba(255,255,255,0.08); }";
  const wrap = (children) => (
    <div style={{ minHeight:"100vh", background:"radial-gradient(ellipse 90% 50% at 50% 0%, rgba(232,49,122,0.12), transparent 60%), #08000a", fontFamily:"'Trebuchet MS','Segoe UI',sans-serif", color:"#f6eef2", padding:"32px 16px 60px" }}>
      <style>{TOKENS}</style>
      <div style={{ maxWidth:620, margin:"0 auto" }}>
        <a href="/cards" style={{ display:"inline-flex", alignItems:"center", gap:8, marginBottom:20, textDecoration:"none" }}>
          <img src="/Bazooka_Logo_cropped.png" alt="Bazooka" style={{ height:44, width:"auto", filter:"drop-shadow(0 4px 16px rgba(232,49,122,0.35))" }} onError={e=>{e.currentTarget.style.display="none";}}/>
        </a>
        {children}
      </div>
    </div>
  );

  if (state.loading) return wrap(<div style={{ textAlign:"center", padding:"60px", color:"rgba(255,255,255,0.5)" }}>Loading profile…</div>);
  if (!state.uid || !state.data) return wrap(
    <div style={{ textAlign:"center", padding:"60px 20px" }}>
      <div style={{ fontSize:40, marginBottom:12 }}>🔍</div>
      <div style={{ fontSize:18, fontWeight:800, marginBottom:6 }}>No collector found</div>
      <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)", marginBottom:20 }}>We couldn't find a collector named "{username}".</div>
      {state.permErr && <div style={{ fontSize:12, color:"#FBBF24", marginBottom:20, maxWidth:380, margin:"0 auto 20px", lineHeight:1.5 }}>⚠️ Public profiles are blocked by the database security rules. Firestore rules need to allow public read of the <b>usernames</b> and <b>users</b> collections.</div>}
      {state.diag && <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", marginBottom:20, fontFamily:"monospace" }}>lookup: {state.diag}</div>}
      <a href="/leaderboard" style={{ background:"linear-gradient(135deg,#E8317A,#7B2FF7)", color:"#fff", textDecoration:"none", borderRadius:10, padding:"10px 22px", fontSize:13, fontWeight:800 }}>Browse the Leaderboard →</a>
    </div>
  );

  const d = state.data;
  const name = d.username ? ("@"+d.username) : (d.displayName ? d.displayName.split(" ")[0] : "Collector");
  const photoURL = d.photoURL || "";
  const avgRating = state.reviews.length ? (state.reviews.reduce((s,r)=>s+(r.rating||0),0)/state.reviews.length) : 0;
  const pubIds = Object.keys(state.pubCards).filter(id=>state.pubCards[id]);
  const pubObjs = pubIds.map(id => state.cards.find(c=>c.id===id)).filter(Boolean);

  return wrap(
    <>
      <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:22 }}>
        {photoURL
          ? <img src={photoURL} alt="" style={{ width:72, height:72, borderRadius:"50%", objectFit:"cover", border:"2px solid rgba(232,49,122,0.4)" }}/>
          : <div style={{ width:72, height:72, borderRadius:"50%", background:"linear-gradient(135deg,#E8317A,#7B2FF7)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, fontWeight:900, color:"#fff" }}>{name.replace("@","").charAt(0).toUpperCase()}</div>}
        <div>
          <div style={{ fontSize:26, fontWeight:900 }}>{name}</div>
          {state.reviews.length > 0 && <div style={{ fontSize:13, color:"#FBBF24", marginTop:3 }}>{"★".repeat(Math.round(avgRating))}{"☆".repeat(5-Math.round(avgRating))} <span style={{ color:"rgba(255,255,255,0.4)" }}>({state.reviews.length} review{state.reviews.length!==1?"s":""})</span></div>}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:22 }}>
        {[["Cards",d.collectionCount||0,"#E8317A"],["Rainbows",d.rainbowCount||0,"#FBBF24"],["1/1s",d.oneOfOneCount||0,"#C084FC"]].map(([l,v,c])=>(
          <div key={l} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"16px", textAlign:"center" }}>
            <div style={{ fontSize:26, fontWeight:900, color:c }}>{v}</div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1 }}>{l}</div>
          </div>
        ))}
      </div>

      {(state.trackers||[]).length > 0 && (
        <div style={{ marginBottom:22 }}>
          <div style={{ fontSize:12, fontWeight:800, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>Public Trackers</div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {state.trackers.map(t => {
              const treatSet = new Set(t.treatments||[]);
              const db = (state.fullCards && state.fullCards.length) ? state.fullCards : state.cards;
              const pubIdSet = new Set(Object.keys(state.ownedPub||{}).filter(id=>state.ownedPub[id]));
              const cardNumVal = c => { const m = String(c.cardNum||"").match(/\d+/); return m ? parseInt(m[0],10) : Number.MAX_SAFE_INTEGER; };
              // Build per-hero rows: which cards exist in this treatment, which are owned (public).
              const heroes = [...new Set(db.filter(c=>c.hero && treatSet.has(c.treatment)).map(c=>c.hero))].sort();
              const rows = heroes.map(hero => {
                const hc = db.filter(c => c.hero===hero && treatSet.has(c.treatment))
                  .sort((a,b)=>{ const d=cardNumVal(a)-cardNumVal(b); return d!==0?d:String(a.cardNum||"").localeCompare(String(b.cardNum||""),undefined,{numeric:true}); });
                const ownedCards = hc.filter(c => pubIdSet.has(c.id));
                const transitCards = hc.filter(c => !pubIdSet.has(c.id) && (state.transit||{})[c.id]);
                return { hero, allCards:hc, ownedCards, complete: ownedCards.length>0, hasTransit: transitCards.length>0, need: hc.length };
              });
              const done = rows.filter(r=>r.complete).length;
              const total = rows.length;
              const pct = total ? Math.round(done/total*100) : 0;
              const isOpen = openTrackerId === t.id;
              return (
                <div key={t.id} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, overflow:"hidden" }}>
                  <div onClick={()=>{ setOpenTrackerId(isOpen?null:t.id); setTrackerSearch(""); }} style={{ padding:"14px 16px", cursor:"pointer" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                      <div>
                        <div style={{ fontSize:14, fontWeight:800, color:"#fff" }}>{isOpen?"▾ ":"▸ "}{t.name}</div>
                        <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>{(t.treatments||[]).join(" · ")} · tap to see what's needed</div>
                      </div>
                      <span style={{ fontSize:18, fontWeight:900, color:done===total&&total>0?"#4ade80":"#FBBF24" }}>{done}/{total}</span>
                    </div>
                    <div style={{ height:8, background:"rgba(255,255,255,0.06)", borderRadius:4, overflow:"hidden" }}>
                      <div style={{ width:`${pct}%`, height:"100%", background:"linear-gradient(90deg,#F97316,#FBBF24,#4ade80)" }}/>
                    </div>
                  </div>
                  {isOpen && (
                    <div style={{ padding:"0 12px 14px" }}>
                      <input value={trackerSearch} onChange={e=>setTrackerSearch(e.target.value)} onClick={e=>e.stopPropagation()} placeholder="🔍 Search hero…" style={{ width:"100%", background:"#241820", border:"1px solid rgba(255,255,255,0.18)", borderRadius:8, color:"#f6eef2", padding:"8px 12px", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box", marginBottom:10 }}/>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(84px,1fr))", gap:8 }}>
                        {rows.filter(r => !trackerSearch.trim() || r.hero.toLowerCase().includes(trackerSearch.trim().toLowerCase())).map(r => {
                          const rep = r.ownedCards[0] || r.allCards[0];
                          return (
                            <div key={r.hero} onClick={()=>{ if(rep?.imageUrl) setProfileZoom({ card:rep, siblings:r.allCards, hero:r.hero, ownedSet:pubIdSet }); }} style={{ background:r.complete?"rgba(74,222,128,0.06)":r.hasTransit?"rgba(251,191,36,0.08)":"rgba(255,255,255,0.02)", border:`1.5px solid ${r.complete?"rgba(74,222,128,0.4)":r.hasTransit?"rgba(251,191,36,0.6)":"rgba(255,255,255,0.06)"}`, borderRadius:9, overflow:"hidden", cursor:rep?.imageUrl?"pointer":"default" }}>
                              <div style={{ position:"relative", aspectRatio:"3/4", background:"rgba(0,0,0,0.4)" }}>
                                {rep?.imageUrl
                                  ? <img src={rep.imageUrl} alt={r.hero} style={{ width:"100%", height:"100%", objectFit:"cover", opacity:r.complete?1:r.hasTransit?0.8:0.72, filter:r.complete?"none":r.hasTransit?"grayscale(30%) sepia(25%)":"grayscale(55%) brightness(0.9)" }}/>
                                  : <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontSize:9, color:"rgba(255,255,255,0.4)", padding:4, textAlign:"center" }}>{r.hero}</div>}
                                <div style={{ position:"absolute", top:4, right:4, background:r.complete?"rgba(74,222,128,0.9)":r.hasTransit?"rgba(251,191,36,0.95)":"rgba(0,0,0,0.7)", color:r.hasTransit&&!r.complete?"#000":"#fff", borderRadius:20, padding:"1px 6px", fontSize:9, fontWeight:800 }}>{r.complete?"✓":r.hasTransit?"🚚":"—"}</div>
                                <div style={{ position:"absolute", bottom:0, left:0, right:0, background:"linear-gradient(transparent,rgba(0,0,0,0.85))", padding:"10px 5px 4px" }}>
                                  <div style={{ fontSize:9, fontWeight:800, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.hero}</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", marginTop:8, textAlign:"center" }}>Green = has it · Greyed = still needs it. Tap a hero to see the cards.</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ fontSize:10, color:"rgba(255,255,255,0.25)", marginTop:6, fontStyle:"italic" }}>Progress reflects cards this collector has made public.</div>
        </div>
      )}

      {(state.listings||[]).length > 0 && (
        <div style={{ marginBottom:22 }}>
          <div style={{ fontSize:12, fontWeight:800, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>🛒 Available in Marketplace · {state.listings.length}</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(90px,1fr))", gap:8 }}>
            {state.listings.map(l => (
              <a key={l.id} href="/market" style={{ textDecoration:"none", background:"rgba(74,222,128,0.06)", border:"1px solid rgba(74,222,128,0.25)", borderRadius:8, overflow:"hidden", display:"block" }}>
                <div style={{ aspectRatio:"3/4", background:"rgba(0,0,0,0.4)" }}>
                  {l.cardImage ? <img src={l.cardImage} alt={l.cardName} style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontSize:10, color:"rgba(255,255,255,0.4)", padding:4, textAlign:"center" }}>{l.cardName}</div>}
                </div>
                <div style={{ padding:"5px 7px" }}>
                  <div style={{ fontSize:10, fontWeight:800, color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{l.cardName||"Card"}</div>
                  <div style={{ fontSize:11, fontWeight:800, color:"#4ade80" }}>{l.listType==="offer"?"OBO":`$${(l.askingPrice||0).toFixed(0)}`}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {pubObjs.length > 0 && (
        <div style={{ marginBottom:22 }}>
          <div style={{ fontSize:12, fontWeight:800, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>Public Collection · {pubObjs.length} cards</div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(72px,1fr))", gap:6 }}>
            {pubObjs.slice(0,80).map(c => (
              <div key={c.id} title={[c.hero,c.treatment,c.weapon].filter(Boolean).join(" · ")} style={{ aspectRatio:"3/4", borderRadius:6, overflow:"hidden", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)" }}>
                {c.imageUrl ? <img src={c.imageUrl} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", fontSize:9, color:"rgba(255,255,255,0.4)", padding:2, textAlign:"center" }}>{c.hero}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div style={{ fontSize:12, fontWeight:800, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>Review History</div>
        {state.reviews.length === 0
          ? <div style={{ fontSize:13, color:"rgba(255,255,255,0.3)", fontStyle:"italic" }}>No reviews yet.</div>
          : <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {state.reviews.slice(0,20).map(r => (
                <div key={r.id} style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:8, padding:"10px 14px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}>
                    <span style={{ fontSize:12, color:"#FBBF24" }}>{"★".repeat(r.rating||0)}{"☆".repeat(5-(r.rating||0))}</span>
                    <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ""}</span>
                  </div>
                  {r.comment && <div style={{ fontSize:12, color:"rgba(255,255,255,0.7)", marginTop:4 }}>{r.comment}</div>}
                  {r.buyerName && <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginTop:3 }}>— {r.buyerName}</div>}
                </div>
              ))}
            </div>}
      </div>

      <div style={{ marginTop:28, textAlign:"center" }}>
        <a href="/cards" style={{ color:"#E8317A", textDecoration:"none", fontSize:13, fontWeight:700 }}>Start your own collection on Bazooka →</a>
      </div>

      {profileZoom && (() => {
        const sibs = (profileZoom.siblings||[]).filter(s=>s.imageUrl);
        const idx = Math.max(0, sibs.findIndex(s=>s.id===profileZoom.card.id));
        const cur = sibs[idx] || profileZoom.card;
        const ownedSet = profileZoom.ownedSet || new Set();
        const has = ownedSet.has(cur.id);
        const go = (delta) => { const n=(idx+delta+sibs.length)%sibs.length; setProfileZoom({ ...profileZoom, card: sibs[n] }); };
        return (
        <div onClick={()=>setProfileZoom(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:2147483600, display:"flex", alignItems:"center", justifyContent:"center", padding:"24px", cursor:"zoom-out" }}>
          {sibs.length>1 && <button onClick={e=>{e.stopPropagation();go(-1);}} style={{ position:"fixed", left:16, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,0.1)", border:"none", color:"#fff", fontSize:30, width:52, height:52, borderRadius:"50%", cursor:"pointer" }}>‹</button>}
          {sibs.length>1 && <button onClick={e=>{e.stopPropagation();go(1);}} style={{ position:"fixed", right:16, top:"50%", transform:"translateY(-50%)", background:"rgba(255,255,255,0.1)", border:"none", color:"#fff", fontSize:30, width:52, height:52, borderRadius:"50%", cursor:"pointer" }}>›</button>}
          <div onClick={e=>e.stopPropagation()} style={{ maxWidth:"min(440px,92vw)", maxHeight:"92vh", display:"flex", flexDirection:"column", alignItems:"center", gap:12 }}>
            <img src={cur.imageUrl} alt={cur.hero} style={{ maxWidth:"100%", maxHeight:"70vh", objectFit:"contain", borderRadius:14, boxShadow:"0 20px 60px rgba(0,0,0,0.7)", filter:has?"drop-shadow(0 0 20px rgba(74,222,128,0.5))":"grayscale(75%) opacity(0.6)" }}/>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:16, fontWeight:900, color:"#fff" }}>{cur.hero}</div>
              <div style={{ fontSize:12, color:has?"#4ade80":"rgba(255,255,255,0.6)" }}>{[cur.treatment, cur.weapon, cur.cardNum?`#${cur.cardNum}`:""].filter(Boolean).join(" · ")} · {has?"✓ Has it":"❌ Still needs this"}{sibs.length>1?` · ${idx+1}/${sibs.length}`:""}</div>
            </div>
            {sibs.length>1 && (
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", justifyContent:"center", maxWidth:"100%" }}>
                {sibs.map((s,i)=>{ const sHas=ownedSet.has(s.id); return (
                  <div key={s.id} onClick={e=>{ e.stopPropagation(); setProfileZoom({ ...profileZoom, card:s }); }} style={{ width:42, aspectRatio:"3/4", borderRadius:5, overflow:"hidden", cursor:"pointer", border:`2px solid ${i===idx?"#E8317A":sHas?"rgba(74,222,128,0.5)":"rgba(255,255,255,0.15)"}`, opacity:sHas?1:0.5 }}>
                    <img src={s.imageUrl} alt={s.weapon} style={{ width:"100%", height:"100%", objectFit:"cover", filter:sHas?"none":"grayscale(70%)" }}/>
                  </div>
                ); })}
              </div>
            )}
          </div>
          <button onClick={()=>setProfileZoom(null)} style={{ position:"fixed", top:20, right:24, background:"rgba(255,255,255,0.1)", border:"none", color:"#fff", fontSize:26, width:44, height:44, borderRadius:"50%", cursor:"pointer" }}>×</button>
        </div>
        );
      })()}
    </>
  );
}

export default function App() {
  return (
    <AppErrorBoundary>
      <AppInner />
    </AppErrorBoundary>
  );
}

function AppInner() {
  // Stop the browser navigating away when an image is dropped anywhere OUTSIDE a card \u2014 a missed
  // drop otherwise replaces the page with the raw image file, which reads as a crash.
  //
  // This must NOT blanket-preventDefault. React dispatches its synthetic events from the root
  // container, so a capture-phase preventDefault on window marks the drop handled before the
  // card's own onDrop ever runs, and the upload silently never happens. Instead, check whether
  // the drop landed on a card and leave those alone; only cancel the genuine misses. Bubble
  // phase, so the card handler has already had its turn by the time this runs.
  useEffect(()=>{
    const onCard = e => !!(e.target && e.target.closest && e.target.closest("[data-card-drop]"));
    const stop = e => { if (!onCard(e)) e.preventDefault(); };
    window.addEventListener("dragover", stop);
    window.addEventListener("drop", stop);
    return () => {
      window.removeEventListener("dragover", stop);
      window.removeEventListener("drop", stop);
    };
  },[]);
  const [tab,           setTab]           = useState("dashboard");
  const [gSearch,       setGSearch]       = useState("");
  const [gOpen,         setGOpen]         = useState(false);
  const [user,          setUser]          = useState(null);
  const [authReady,     setAuthReady]     = useState(false);
  // The early-access allowlist has to be loaded HERE, in the component that owns the gate.
  // It used to be loaded inside PublicCardDatabase — which only ever mounts once you are ALREADY
  // past the gate. So for a beta tester the sequence was: sign in → gate checks an empty list →
  // ComingSoon renders → PublicCardDatabase never mounts → the list never loads → gate stays shut.
  // A deadlock: the listener that would let them in only ran after they were let in. Admins never
  // hit it because hasEarlyAccess() short-circuits on @bazookabreaks.com before reading the list.
  //
  // It also has to be STATE, not just the module variable. hasEarlyAccess() reads a plain module
  // array, and mutating that fires no re-render — so even once the list arrived, the gate would
  // keep showing whatever it decided on first paint. Bumping this state is what re-runs the check.
  const [eaLoaded,      setEaLoaded]      = useState(0);
  const [viewAs,        setViewAs]        = useState("");
  const [inventory,     setInventory]     = useState(() => { try { const c = localStorage.getItem("bz_inventory_v1"); return c ? JSON.parse(c) : []; } catch { return []; } });
  const [breaks,        setBreaks]        = useState(() => { try { const c = localStorage.getItem("bz_breaks_v1"); return c ? JSON.parse(c) : []; } catch { return []; } });
  const [streams,       setStreams]       = useState(() => { try { const c = localStorage.getItem("bz_streams_v1"); return c ? JSON.parse(c) : []; } catch { return []; } });
  const [plannedStreams, setPlannedStreams] = useState([]);
  const [comps,         setComps]         = useState([]);
  const [quotes,        setQuotes]        = useState([]);
  const [buyers,        setBuyers]        = useState([]);
  const [csvImports,    setCsvImports]    = useState([]);
  const [shipments,     setShipments]     = useState([]);
  const [productUsage,  setProductUsage]  = useState([]);
  const [skuPrices,     setSkuPrices]     = useState(() => { try { const c = localStorage.getItem("bz_skuprices_v1"); return c ? JSON.parse(c) : {}; } catch { return {}; } });
  const [skuPriceHistory,setSkuPriceHistory]=useState([]);
  const [cardPools,     setCardPools]     = useState([]);
  const [lotTracking,   setLotTracking]   = useState({});
  const [lotNotes,      setLotNotes]      = useState({});
  const [historicalData,setHistoricalData]=useState(() => { try { const c = localStorage.getItem("bz_histdata_v1"); return c ? JSON.parse(c) : []; } catch { return []; } });
  const [payStubs,      setPayStubs]      = useState([]);
  const [imcFormUrl,         setImcFormUrl]         = useState("");
  const [imcAdjustmentsData, setImcAdjustmentsData] = useState(() => { try { const c = localStorage.getItem("bz_imcadj_v1"); return c ? JSON.parse(c) : {}; } catch { return {}; } });
  const [bobaCards,     setBobaCards]     = useState([]);
  const [toast,         setToast]         = useState(null);
  const [scanStatus,    setScanStatus]    = useState(null);
  const [activeScan,    setActiveScan]    = useState(null);
  const [hoverTab,      setHoverTab]  = useState(null);
  const [sideOpen,      setSideOpen]  = useState(false);
  const [invTabDefault,  setInvTabDefault]  = useState("cards");
  const [buyerTabDefault,setBuyerTabDefault]= useState("table");
  const [periodDefault,  setPeriodDefault]  = useState("month");
  const [perfTabDefault, setPerfTabDefault] = useState("stats");
  const [checklistDefault,setChecklistDefault]=useState("cards");
  const [streamTabDefault,setStreamTabDefault] = useState("recap");
  const [compMode,      setCompMode]      = useState("builder");

  const BOBA_CACHE_KEY = "boba_checklist_cache_v3";
  const BOBA_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

  // Load bobaCards at startup — localStorage → CDN → Firestore
  useEffect(() => {
    async function loadBobaCards() {
      // 1. localStorage first (instant)
      try {
        const raw = localStorage.getItem(BOBA_CACHE_KEY);
        if (raw) {
          const { cards: cc, ts } = JSON.parse(raw);
          if (cc?.length > 0 && Date.now() - ts < BOBA_CACHE_TTL) {
            setBobaCards(cc); return;
          }
          if (cc?.length > 0) setBobaCards(cc); // stale but show something
        }
      } catch(e) {}
      // 2. Static file from Vercel CDN (fastest — no auth, pure edge)
      try {
        const r = await fetch("/cards-data.json");
        if (r.ok) {
          const cards = await r.json();
          setBobaCards(cards);
          try { localStorage.setItem(BOBA_CACHE_KEY, JSON.stringify({ cards, ts: Date.now() })); } catch(e) {}
          return;
        }
      } catch(e) {}
      // 3. CDN JSON from Firebase Storage
      try {
        const url = await getDownloadURL(ref(storage, "card_data/boba_checklist.json"));
        const r = await fetch(url);
        const cards = await r.json();
        setBobaCards(cards);
        try { localStorage.setItem(BOBA_CACHE_KEY, JSON.stringify({ cards, ts: Date.now() })); } catch(e) {}
        return;
      } catch(e) {}
      // 3. Firestore fallback
      try {
        const snap = await getDocs(collection(db, "boba_checklist"));
        const cards = snap.docs.map(d => ({...d.data(), id:d.id}));
        setBobaCards(cards);
        try { localStorage.setItem(BOBA_CACHE_KEY, JSON.stringify({ cards, ts: Date.now() })); } catch(e) {}
      } catch(e) {}
    }
    loadBobaCards();
  }, []);

  // Auth listener
  useEffect(() => {
    // Safety net: on mobile/slow networks, Firebase's first onAuthStateChanged callback
    // can be badly delayed (Safari cookie throttling, cold connections). Never let that
    // block the UI — flip authReady after 3.5s no matter what. The real callback still
    // runs and updates `user` when it eventually arrives.
    // 3.5s was short enough to fire routinely on a phone, and releasing the UI with `user` still
    // null makes a signed-in person look signed-out: wrong avatar, then a bounce to the
    // claim-a-username screen. Give a real session room to arrive before giving up on it.
    const authTimeout = setTimeout(() => setAuthReady(true), 10000);
    const unsub = onAuthStateChanged(auth, u => { setUser(u); setAuthReady(true); clearTimeout(authTimeout); });
    return () => { clearTimeout(authTimeout); unsub(); };
  }, []);

  const [dataLoaded, setDataLoaded] = useState({}); // tracks which tab groups have been subscribed
  const persistentUnsubs = useRef([]); // keep lazy-loaded listeners alive across tab changes

  // Always-on listeners — needed by dashboard and multiple tabs
  useEffect(() => {
    if (!user) return;
    // Hard guard: never attempt to load internal/financial data for non-team emails.
    // (Firestore rules also block this server-side; this prevents the attempt + console errors.)
    if (!(user.email||"").toLowerCase().trim().endsWith("@bazookabreaks.com")) {
      // Wipe any locally-cached financial data so a non-team user can't view a stale copy.
      try {
        ["bz_inventory_v1","bz_breaks_v1","bz_streams_v1","bz_histdata_v1","bz_skuprices_v1","bz_imcadj_v1"].forEach(k=>localStorage.removeItem(k));
      } catch(e) {}
      // Also clear any cache-seeded state so a non-team user can't see a stale copy.
      setInventory([]); setBreaks([]); setStreams([]); setHistoricalData([]); setSkuPrices({}); setImcAdjustmentsData({});
      return;
    }
    const INV_CACHE = "bz_inventory_v1";
    const BRK_CACHE = "bz_breaks_v1";
    const STR_CACHE = "bz_streams_v1";

    // Streams load window: trailing ~400 days covers the widest dashboard view
    // (year-to-date + prior-year comparison) without shipping the entire archive
    // on every update. Older streams live in historical_data / can be loaded on demand.
    const STREAMS_WINDOW_START = (() => {
      const d = new Date(); d.setDate(d.getDate() - 400);
      return d.toISOString().split("T")[0];
    })();

    // Seed from cache immediately so dashboard renders fast
    try {
      const ci = localStorage.getItem(INV_CACHE); if (ci) setInventory(JSON.parse(ci));
      const cb = localStorage.getItem(BRK_CACHE);  if (cb) setBreaks(JSON.parse(cb));
      const cs = localStorage.getItem(STR_CACHE);  if (cs) setStreams(JSON.parse(cs));
    } catch(e) {}

    const unsubs = [
      onSnapshot(query(collection(db,"inventory"), orderBy("dateAdded","asc")), snap => {
        const data = snap.docs.map(d=>({...d.data(), id:d.id}));
        setInventory(data);
        try { localStorage.setItem(INV_CACHE, JSON.stringify(data)); } catch(e) {}
      }),
      onSnapshot(query(collection(db,"breaks"), orderBy("dateAdded","asc")), snap => {
        const data = snap.docs.map(d=>({...d.data(), id:d.id}));
        setBreaks(data);
        try { localStorage.setItem(BRK_CACHE, JSON.stringify(data)); } catch(e) {}
      }),
      onSnapshot(query(collection(db,"streams"), where("date",">=", STREAMS_WINDOW_START), orderBy("date","asc")), snap => {
        const data = snap.docs.map(d=>({...d.data(), id:d.id}));
        setStreams(data);
        try { localStorage.setItem(STR_CACHE, JSON.stringify(data)); } catch(e) {}
      }),
      // Load all quotes
      onSnapshot(collection(db,"quotes"), snap => {
        const pub = snap.docs.map(d=>({...d.data(), id:d.id}));
        // Merge in seller PII from the admin-only companion collection so the queue,
        // notifications, and payment display keep working. Public visitors never load
        // this — quotes_private is admin-read only.
        setQuotes(prev => pub.map(q => {
          const existing = prev.find(p=>p.id===q.id);
          return existing?.seller ? { ...q, seller: existing.seller } : q;
        }));
        getDocs(collection(db,"quotes_private")).then(psnap => {
          const byId = {}; psnap.docs.forEach(d=>{ byId[d.id] = d.data().seller; });
          setQuotes(cur => cur.map(q => byId[q.id] ? { ...q, seller: byId[q.id] } : q));
        }).catch(()=>{});
      }),
      // historical_data at startup so Dashboard YTD is correct immediately
      onSnapshot(collection(db,"historical_data"), snap => { const hd = snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(a.yearMonth||"").localeCompare(b.yearMonth||"")); setHistoricalData(hd); try { localStorage.setItem("bz_histdata_v1", JSON.stringify(hd)); } catch(e) {} }),
      onSnapshot(doc(db,"config","skuPrices"), snap => { if(snap.exists()){ setSkuPrices(snap.data()); try { localStorage.setItem("bz_skuprices_v1", JSON.stringify(snap.data())); } catch(e) {} } }),
      onSnapshot(doc(db,"config","imcAdjustments"), snap => { if(snap.exists()){ setImcAdjustmentsData(snap.data()); try { localStorage.setItem("bz_imcadj_v1", JSON.stringify(snap.data())); } catch(e) {} } }),
    ];
    return () => unsubs.forEach(u => u());
  }, [user]);

  // Lazy listeners — load when tab is first visited
  useEffect(() => {
    if (!user) return;
    const unsubs = [];

    if ((tab === "comp" || tab === "dashboard" || tab === "history") && !dataLoaded.comp) {
      setDataLoaded(p=>({...p, comp:true}));
      unsubs.push(onSnapshot(collection(db,"comps"), snap => setComps(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>new Date(b.dateAdded||0)-new Date(a.dateAdded||0)))));
      unsubs.push(onSnapshot(collection(db,"card_pools"), snap => setCardPools(snap.docs.map(d=>({...d.data(), id:d.id})))));
      unsubs.push(onSnapshot(collection(db,"planned_streams"), snap => setPlannedStreams(snap.docs.map(d=>({...d.data(), id:d.id})))));
    }

    if ((tab === "buyers" || tab === "performance" || tab === "streams" || tab === "campaigns") && !dataLoaded.buyers) {
      setDataLoaded(p=>({...p, buyers:true}));
      unsubs.push(onSnapshot(collection(db,"buyers"), snap => setBuyers(snap.docs.map(d=>({...d.data(), id:d.id})))));
      unsubs.push(onSnapshot(collection(db,"csv_imports"), snap => setCsvImports(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.importedAt||"").localeCompare(a.importedAt||"")))));
    }

    if (tab === "shipping" && !dataLoaded.shipping) {
      setDataLoaded(p=>({...p, shipping:true}));
      unsubs.push(onSnapshot(collection(db,"shipping_issues"), snap => setShippingIssues?.(snap.docs.map(d=>({...d.data(), id:d.id})))));
    }

    if (tab === "inventory" && !dataLoaded.inventory) {
      setDataLoaded(p=>({...p, inventory:true}));
      unsubs.push(onSnapshot(collection(db,"shipments"), snap => setShipments(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>new Date(b.date||0)-new Date(a.date||0)))));
      unsubs.push(onSnapshot(collection(db,"product_usage"), snap => setProductUsage(snap.docs.map(d=>({...d.data(), id:d.id})))));
      unsubs.push(onSnapshot(collection(db,"sku_price_history"), snap => setSkuPriceHistory(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(a.date||"").localeCompare(b.date||"")))));
      unsubs.push(onSnapshot(doc(db,"config","lotTracking"), snap => { if(snap.exists()) setLotTracking(snap.data()); }));
      unsubs.push(onSnapshot(doc(db,"config","lotNotes"), snap => { if(snap.exists()) setLotNotes(snap.data()); }));
    }

    if (tab === "streams" && !dataLoaded.streams) {
      setDataLoaded(p=>({...p, streams:true}));
      // historical_data already loaded at startup
      unsubs.push(onSnapshot(doc(db,"config","imcFormUrl"), snap => { if(snap.exists()) setImcFormUrl(snap.data().url||""); }));
      unsubs.push(onSnapshot(collection(db,"pay_stubs"), snap => setPayStubs(snap.docs.map(d=>({...d.data(), id:d.id})).sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||"")))));
    }

    if (unsubs.length === 0) return;
    // Keep these listeners alive for the rest of the session instead of tearing
    // them down on every tab change (which was killing already-loaded data like
    // saved comps). They're cleaned up when the app unmounts.
    persistentUnsubs.current.push(...unsubs);
  }, [user, tab]);

  // Clean up all persistent listeners on unmount only.
  useEffect(() => () => { persistentUnsubs.current.forEach(u => { try { u(); } catch(e){} }); persistentUnsubs.current = []; }, []);

  // Keyboard shortcut for global search
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "/") { e.preventDefault(); setGOpen(p=>!p); }
      if (e.key === "Escape") { setGOpen(false); setGSearch(""); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function showToast(msg) { setToast(msg); setTimeout(()=>setToast(null), 3500); }

  const userRole    = getUserRole(user);
  const effectiveRole = viewAs ? ROLES[viewAs] || userRole : userRole;
  const effectiveUser = viewAs ? { displayName: viewAs, email: viewAs + "@bazooka.com", uid: viewAs } : user;

  // -- HANDLERS ------------------------------------------------

  async function handleAccept(cards, seller, u, custNote) {
    for (const card of cards) {
      await setDoc(doc(db,"inventory",card.id), { ...card, addedBy: u?.displayName||"Unknown", dateAdded: new Date().toISOString(), cardStatus:"in_transit" });
    }
    // Mark the quote as accepted with timestamp so Finance can pick it up
    if (seller?.quoteId || seller?.id) {
      const qId = seller?.quoteId || seller?.id;
      await setDoc(doc(db,"quotes",qId), { status:"accepted", respondedAt:new Date().toISOString(), notified:false }, { merge:true });
    }
    showToast(`✅ ${cards.length} card${cards.length!==1?"s":""} added -- marked In Transit until delivered`);
    setTab("inventory");
  }

  async function handleRemove(id) { await deleteDoc(doc(db,"inventory",id)); }

  async function handleBulkRemove(ids) {
    await Promise.all(ids.map(id => deleteDoc(doc(db,"inventory",id))));
    showToast(`\uD83D\uDDD1 $"}{ids.length} card${ids.length!==1?"s":""} deleted`);
  }

  async function handleSaveCardCost(id, cost) {
    await setDoc(doc(db,"inventory",id), { costPerCard: cost }, { merge:true });
  }

  async function handlePutBack(id) {
    await deleteDoc(doc(db,"breaks", breaks.find(b=>b.inventoryId===id)?.id || "noop"));
  }

  async function handleAddBreak(entry) {
    await setDoc(doc(db,"breaks",entry.id), { ...entry });
    showToast(`\u2705 ${entry.cardName} logged out`);
  }

  async function handleBulkAddBreak(entries) {
    await Promise.all(entries.map(e => setDoc(doc(db,"breaks",e.id), e)));
    showToast(`\u2705 $"}{entries.length} cards logged out`);
  }

  async function handleDeleteBreak(id) { await deleteDoc(doc(db,"breaks",id)); }

  async function handleSaveStream(stream) {
    await setDoc(doc(db,"streams",stream.id), stream, { merge:true });
  }

  async function handleDeleteStream(id) {
    if (!id) { showToast("❌ Can't delete: stream has no ID"); return; }
    try {
      // Delete the stream itself FIRST — this is the part that matters.
      await deleteDoc(doc(db,"streams",id));
      showToast("\uD83D\uDDD1 Stream deleted");
      // Then best-effort clean up any breaks logged under this stream (don't let this block the delete).
      try {
        const st = streams.find(s=>s.id===id);
        const streamBreaks = breaks.filter(b => b && b.id && (b.streamId === id || (st && b.streamId === st.breaker_date)));
        await Promise.all(streamBreaks.map(b => deleteDoc(doc(db,"breaks",b.id)).catch(()=>{})));
      } catch(cleanupErr) { console.warn("Break cleanup after stream delete:", cleanupErr); }
    } catch(e) {
      console.error("Delete stream error:", e);
      showToast("❌ Delete failed: " + e.message);
    }
  }

  async function handleSaveComp(comp) {
    try {
      const id = comp.id || uid();
      await setDoc(doc(db,"comps",id), {
        ...comp,
        id,
        dateAdded: comp.dateAdded || new Date().toISOString(),
        savedBy: user?.displayName || "Unknown",
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      showToast("\uD83D\uDCBE Comp saved");
    } catch(err) {
      console.error("Save comp error:", err);
      showToast("❌ Failed to save comp — check connection");
    }
  }

  async function handleDeleteComp(id) { await deleteDoc(doc(db,"comps",id)); showToast("\uD83D\uDDD1 Comp deleted"); }

  async function handleSaveQuote(quoteData) {
    const existingId = quoteData.existingId;
    const id = existingId || uid();
    const quotedBy = user?.displayName || user?.email || "Admin";
    if (existingId) {
      await setDoc(doc(db,"quotes",id), {
        ...quoteData,
        currentOffer: quoteData.dispOffer,
        status: "pending",
        notified: false,
        updatedAt: new Date().toISOString(),
        lastUpdatedBy: quotedBy,
      }, { merge:true });
      return { id };
    } else {
      const now = new Date();
      const yy = String(now.getFullYear()).slice(2);
      const mm = String(now.getMonth()+1).padStart(2,"0");
      const existing = quotes.filter(q => q.quoteRef?.startsWith(`BZ-${yy}${mm}-`));
      const nextNum = String(existing.length + 1).padStart(3,"0");
      const quoteRef = `BZ-${yy}${mm}-${nextNum}`;
      await setDoc(doc(db,"quotes",id), { ...quoteData, id, quoteRef, quotedBy, status:"pending", createdAt:now.toISOString(), viewCount:0, notified:true, history:[] });
      return { id, quoteRef };
    }
  }

  async function handleCloseQuote(id) { await setDoc(doc(db,"quotes",id), { bazookaClosed:true }, { merge:true }); }
  // Reopen a quote link: clear the closed flag, and either extend the expiry 7 days
  // from now or (never=true) make it never expire.
  async function handleReopenQuote(id, never) {
    const patch = never
      ? { bazookaClosed:false, neverExpires:true }
      : { bazookaClosed:false, neverExpires:false, expiresAt: new Date(Date.now()+7*24*60*60*1000).toISOString() };
    await setDoc(doc(db,"quotes",id), patch, { merge:true });
  }

  async function handleBazookaCounter(quoteId, amount, history) {
    await setDoc(doc(db,"quotes",quoteId), { status:"pending", currentOffer:amount, history:[...history,{type:"bazooka_counter",amount,timestamp:new Date().toISOString()}], notified:false }, { merge:true });
  }

  async function handleDismissQuoteNotif(id) { await setDoc(doc(db,"quotes",id), { notified:true }, { merge:true }); }

  async function handleSaveLotTracking(key, data) {
    const next = { ...lotTracking, [key]: data };
    setLotTracking(next);
    await setDoc(doc(db,"config","lotTracking"), next);
    // Update cardStatus on all inventory cards in this lot
    const lotCards = inventory.filter(c => `${c.seller||"Unknown"}__${c.date||"Unknown"}` === key);
    if (lotCards.length > 0) {
      const newStatus = data.status === "Delivered" ? "available" : "in_transit";
      await Promise.all(lotCards.map(c =>
        setDoc(doc(db,"inventory",c.id), { cardStatus: newStatus }, { merge:true })
      ));
    }
  }

  async function handleSaveLotNotes(key, notes) {
    const next = { ...lotNotes, [key]: { notes } };
    setLotNotes(next);
    await setDoc(doc(db,"config","lotNotes"), next);
  }

  async function handleDeleteLot(key, cardIds) {
    if (!window.confirm(`Delete this entire lot and all ${cardIds.length} cards? Cannot be undone.`)) return;
    await Promise.all(cardIds.map(id => deleteDoc(doc(db,"inventory",id))));
    showToast("\uD83D\uDDD1 Lot deleted");
  }

  async function handleSaveShipment(shipment) { await setDoc(doc(db,"shipments",shipment.id), shipment); }
  async function handleDeleteShipment(id) { await deleteDoc(doc(db,"shipments",id)); }

  async function handleSaveProductUsage(usage) { await setDoc(doc(db,"product_usage",usage.id), usage); }
  async function handleDeleteProductUsage(id) { await deleteDoc(doc(db,"product_usage",id)); }

  async function handleSaveSkuPrices(prices) {
    await setDoc(doc(db,"config","skuPrices"), prices, { merge:true });
    // Save snapshot to price history
    const histId = uid();
    await setDoc(doc(db,"sku_price_history",histId), { id:histId, date:todayLocal(), prices, savedAt:new Date().toISOString() });
    showToast("\uD83D\uDCBE SKU prices saved");
  }

  async function handleSavePool(pool) { await setDoc(doc(db,"card_pools",pool.id||uid()), { ...pool, id:pool.id||uid() }); }
  async function handleDeletePool(id) { await deleteDoc(doc(db,"card_pools",id)); }

  async function handleLogPoolOut(poolId, qty, breaker, date, usage) {
    const pool = cardPools.find(p=>p.id===poolId);
    if (!pool) return;
    const newUsed = (parseInt(pool.usedQty)||0) + qty;
    await setDoc(doc(db,"card_pools",poolId), { usedQty: newUsed }, { merge:true });
    // Log a break entry for tracking
    const breakId = uid();
    await setDoc(doc(db,"breaks",breakId), { id:breakId, date, breaker, inventoryId:poolId, cardName:pool.cardName, cardType:pool.cardType, usage, notes:`Pool log: ${qty} units`, dateAdded:new Date().toISOString(), loggedBy:user?.displayName||"Unknown", isPoolLog:true, qty });
    showToast(`\u2705 Logged ${qty} ${pool.cardName}`);
  }

  async function handleAddToPool(poolId, qty) {
    const pool = cardPools.find(p=>p.id===poolId);
    if (!pool) return;
    await setDoc(doc(db,"card_pools",poolId), { totalQty:(parseInt(pool.totalQty)||0)+qty }, { merge:true });
    showToast(`\u2705 Added ${qty} to ${pool.cardName}`);
  }

  async function handleSaveHistorical(entry) {
    await setDoc(doc(db,"historical_data",entry.id), entry);
    showToast("\uD83D\uDCBE Historical data saved");
  }

  async function handleDeleteHistorical(id) {
    await deleteDoc(doc(db,"historical_data",id));
    showToast("\uD83D\uDDD1 Entry deleted");
  }

  async function handleSavePayStub(stub) {
    const id = uid();
    await setDoc(doc(db,"pay_stubs",id), { ...stub, id, createdAt:new Date().toISOString(), createdBy:user?.displayName||"Admin", read:false });
    showToast(`\uD83D\uDCE4 Pay stub sent to ${stub.breaker}`);
  }

  async function handleDismissPayStub(id) { await setDoc(doc(db,"pay_stubs",id), { read:true }, { merge:true }); }
  async function handleDeletePayStub(id) { await deleteDoc(doc(db,"pay_stubs",id)); showToast("\uD83D\uDDD1 Pay stub deleted"); }

  async function handleSaveImcFormUrl(url) {
    await setDoc(doc(db,"config","imcFormUrl"), { url });
    showToast("\uD83D\uDCBE IMC Form URL saved");
  }

  async function handleSaveImcAdjustments(adjustments) {
    await setDoc(doc(db,"config","imcAdjustments"), adjustments);
  }

  async function handleUpsertBuyers(buyerList, streamId, filename, contentHash) {
    // Duplicate file guard — block silently if exact same file content was already imported
    if (contentHash) {
      const alreadyImported = csvImports.some(i => i.contentHash === contentHash);
      if (alreadyImported) {
        showToast("⚠️ This exact CSV file was already imported — skipped to prevent duplicates");
        return;
      }
    }
    // Merge buyers -- accumulate spend and orders
    const existing = [...buyers];
    const updates = {};
    buyerList.forEach(b => {
      const prev = existing.find(x => x.username === b.username);
      updates[b.username] = {
        id: prev?.id || uid(),
        username: b.username,
        fullName: b.fullName || prev?.fullName || "",
        city: b.city || prev?.city || "",
        state: b.state || prev?.state || "",
        zip: b.zip || prev?.zip || "",
        totalSpend: (prev?.totalSpend || 0) + b.spend,
        orderCount: (prev?.orderCount || 0) + b.orders,
        couponCount: (prev?.couponCount || 0) + (b.couponCount||0),
        couponDiscount: (prev?.couponDiscount || 0) + (b.couponDiscount||0),
        couponsUsed: [...new Set([...(prev?.couponsUsed||[]), ...(b.couponsUsed||[])])],
        streams: [...new Set([...(prev?.streams||[]), streamId])],
        // Per-stream spend history for period filtering
        spendHistory: [
          ...(prev?.spendHistory||[]).filter(h=>h.streamId!==streamId),
          { streamId, date: b.date || new Date().toISOString().split("T")[0], spend: b.spend, orders: b.orders }
        ],
        firstSeen: prev?.firstSeen || b.date || new Date().toISOString().split("T")[0],
        lastSeen: b.date || new Date().toISOString().split("T")[0],
        isNew: !prev,
        updatedAt: new Date().toISOString(),
      };
    });
    await Promise.all(Object.values(updates).map(b => setDoc(doc(db,"buyers",b.id), b, { merge:true })));
    // Save CSV import record
    const impId = uid();
    await setDoc(doc(db,"csv_imports",impId), { id:impId, streamId, filename, contentHash:contentHash||null, importedAt:new Date().toISOString(), rowCount:buyerList.length });
    showToast(`\u2705 ${buyerList.length} buyers imported`);
  }

  async function handleClearAllBuyers() {
    if (!window.confirm("Delete ALL buyer data and CSV imports? This cannot be undone.")) return;
    await Promise.all(buyers.map(b => deleteDoc(doc(db,"buyers",b.id))));
    await Promise.all(csvImports.map(i => deleteDoc(doc(db,"csv_imports",i.id))));
    showToast("\uD83D\uDDD1 All buyer data cleared");
  }

  async function handleDeleteCsvImport(id) { await deleteDoc(doc(db,"csv_imports",id)); }

  async function handleDeleteImport(id) { await deleteDoc(doc(db,"csv_imports",id)); }

  function handleOnChecklistUpdated() {
    try { localStorage.removeItem(BOBA_CACHE_KEY); } catch(e) {}
    getDocs(collection(db,"boba_checklist")).then(snap => {
      const cards = snap.docs.map(d=>({...d.data(), id:d.id}));
      setBobaCards(cards);
      try { localStorage.setItem(BOBA_CACHE_KEY, JSON.stringify({cards,ts:Date.now()})); } catch(e) {}
    });
  }

  // -- NAV TABS -------------------------------------------------
  const ALL_TABS = [
    { id:"dashboard",  label:"Dashboard",   icon:"\uD83D\uDCCA", roles:["Admin","Streamer","Procurement","Shipping","Viewer"] },
    { id:"comp",       label:"Lot Comp",     icon:"\uD83E\uDDEE", roles:["Admin","Procurement","Viewer","Streamer"] },
    { id:"inventory",  label:"Inventory",   icon:"\uD83D\uDCE6", roles:["Admin","Procurement","Shipping","Viewer"] },
    { id:"history",    label:"History",      icon:"\uD83D\uDCCB", roles:["Streamer"] },
    { id:"streams",    label:"Streams",      icon:"\uD83C\uDFAF", roles:["Admin","Streamer","StreamerLite"] },
    { id:"buyers",     label:"Buyers",       icon:"\uD83D\uDC65", roles:["Admin"] },
    { id:"campaigns",  label:"Campaigns",    icon:"🎯",           roles:["Admin"] },
    { id:"chases",     label:"Chases",       icon:"🃏",           roles:["Admin"] },
    { id:"performance",label:"Performance",  icon:"\uD83D\uDCC8", roles:["Admin"] },
    { id:"finance",    label:"Finance",      icon:"\uD83D\uDCB0", roles:["Admin"] },
    { id:"shipping",   label:"Shipping",     icon:"\uD83D\uDCE6", roles:["Admin","Shipping"] },
    { id:"broadcaster",label:"Broadcaster",  icon:"🎙", roles:["Admin","StreamerLite"] },
    { id:"directory",  label:"Directory",    icon:"📋", roles:["Admin"] },
    { id:"messages",   label:"Messages",     icon:"💬", roles:["Admin","Streamer","StreamerLite","Procurement","Shipping","Viewer"] },
    { id:"supply",     label:"Supply Links", icon:"🔗", roles:["Admin","Streamer","StreamerLite","Procurement","Shipping","Viewer"] },
    { id:"importer",   label:"Import",       icon:"⬆️", roles:["Admin"] },
  ].filter(t => t.roles.includes(effectiveRole?.role));

  // Load the early-access allowlist BEFORE the gate below runs. Bumping eaLoaded forces a
  // re-render so the gate re-evaluates hasEarlyAccess() with the list actually in hand.
  useEffect(() => {
    const unsub = onSnapshot(doc(db,"config","early_access"), snap => {
      if (snap.exists()) {
        const list = snap.data().emails || [];
        setDynamicEarlyAccess(list, true);
        try { localStorage.setItem("early_access_v1", JSON.stringify(list)); } catch(e){}
      } else {
        setDynamicEarlyAccess(EARLY_ACCESS_EMAILS, false);
      }
      setEaLoaded(v => v + 1);
    }, () => { setEaLoaded(v => v + 1); });   // even on error, stop waiting
    // Hard backstop. The gate holds gated pages until eaLoaded > 0, so if this listener were ever
    // to hang (offline, rules change), those pages would spin forever. Fail open to the cached
    // list rather than trapping people on a spinner.
    const t = setTimeout(() => setEaLoaded(v => v || 1), 4000);
    return () => { clearTimeout(t); try{unsub();}catch(e){} };
  }, []);


  // -- PUBLIC ROUTES -- no auth required, check FIRST --
  const _path = window.location.pathname;

  // -- Pre-launch wall: inner pages locked until June 18 except @bazookabreaks.com team --
  const ACCESS_PAUSED = false; // launch is live — collector app open to everyone
  // eaLoaded is read here on purpose: hasEarlyAccess() consults a module-level array, so this
  // line must re-run once the list arrives. Referencing the state is what ties them together.
  const _isTeam = (eaLoaded, hasEarlyAccess(user?.email));
  const _gatedPaths = ["/cards","/rainbow","/supers","/1of1","/wants","/market","/messages","/friends","/team","/ledger","/leaderboard","/deck","/playbook","/chases","/showcase"];
  if (ACCESS_PAUSED && authReady && eaLoaded>0 && !_isTeam && _gatedPaths.includes(_path)) {
    return <ComingSoon />;
  }
  // Hold gated pages until BOTH auth and the allowlist have resolved — deciding early is what
  // showed a beta tester the ComingSoon wall even though they were on the list.
  if (ACCESS_PAUSED && (!authReady || eaLoaded===0) && _gatedPaths.includes(_path)) {
    return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"#08000a", fontFamily:"'Trebuchet MS',sans-serif", fontSize:16, fontWeight:700, color:"#E8317A" }}>Loading...</div>;
  }
  if (typeof document !== "undefined") {
    const titles = { "/":"Bazooka — BoBA Card Collector Database", "/cards":"Card Database — Bazooka", "/sell":"Sell a Card — Bazooka", "/deck":"Deck Builder — Bazooka", "/playbook":"Playbook — Bazooka", "/chases":"Chase Tracker — Bazooka", "/dashboard":"Team Dashboard — Bazooka", "/fantasy":"Bazooka Fantasy — DFS" };
    document.title = titles[_path] || "Bazooka — BoBA Card Collector Database";
  }
  const quoteMatch = window.location.pathname.match(/^\/quote\/([a-zA-Z0-9]+)$/);
  if (quoteMatch) return <PublicQuote quoteId={quoteMatch[1]} />;

  if (window.location.pathname === "/showcase") {
    const params = new URLSearchParams(window.location.search);
    const uid2 = params.get("uid");
    return <BobaShowcase uid={uid2} />;
  }

  // Shared deck — /deck/<id>. Read-only, no login needed, shows the deck plus the format rules
  // it's built to and whether it's legal. This is what you send someone who isn't in the app.
  const sharedDeck = window.location.pathname.match(/^\/deck\/([A-Za-z0-9_-]+)$/);
  if (sharedDeck) return <SharedDeck deckId={sharedDeck[1]} />;
  const sharedTrade = window.location.pathname.match(/^\/trade\/([A-Za-z0-9_-]+)$/);
  if (sharedTrade) return <SharedTradePackage packageId={sharedTrade[1]} />;

  if (window.location.pathname === "/deck")     return <PublicDeckBuilder />;
  if (window.location.pathname === "/bugs")     return <BugAdmin user={user} />;
  if (window.location.pathname === "/playbook") return <PublicPlaybookBuilder />;
  // Swancity public tracker — ALWAYS public (Super + Secret 1/1), no login, no access wall.
  if (window.location.pathname === "/swancity") return <PublicCardDatabase swancity={true} />;
  // Card database tabs as flat top-level URLs (e.g. /supers, /rainbow, /wants)
  const CARD_DB_PATHS = ["/cards","/rainbow","/supers","/1of1","/34","/wants","/tradebait","/intransit","/market","/messages","/friends","/team","/ledger","/leaderboard"];
  if (CARD_DB_PATHS.includes(window.location.pathname)) return <><PublicCardDatabase /><BugReporter user={user} /></>;
  if (window.location.pathname === "/sell")     return <PublicSellPage />;
  if (window.location.pathname === "/fantasy")  return <FantasyPage user={user} />;
  if (window.location.pathname === "/privacy")  return <PublicPrivacyPolicy />;
  if (window.location.pathname === "/chases")   return <PublicChaseTracker />;

  // Homepage at root — the public front door
  if (window.location.pathname === "/" || window.location.pathname === "") return <PublicHomepage />;

  // bazookadash.com/{username} — public profile page. Must come after all known
  // routes so it only catches a bare single-segment path that isn't reserved.
  {
    const RESERVED = new Set(["cards","rainbow","supers","1of1","34","wants","market","messages","friends","team","ledger","leaderboard","deck","bugs","playbook","swancity","sell","privacy","chases","showcase","dashboard","quote","login","home","index","fantasy","tradebait","intransit","trade"]);
    const seg = (window.location.pathname||"").replace(/^\/+|\/+$/g,"");
    if (seg && !seg.includes("/") && !RESERVED.has(seg.toLowerCase())) {
      return <PublicProfilePage username={seg} />;
    }
  }

  // Any unknown path that isn't the dashboard → send to homepage
  if (window.location.pathname !== "/dashboard") return <PublicHomepage />;

  // /dashboard → internal team app (auth-gated below)
  // Auth gate -- only for the main app
  if (!authReady) return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"var(--bz-s1)", fontFamily:"'Trebuchet MS',sans-serif", fontSize:18, fontWeight:700, color:"#E8317A" }}>Loading...</div>;

  if (!user) return <LoginScreen />;

  // Block anyone not on the team
  const _dashEmail = (user?.email || "").toLowerCase().trim();
  const _isTeamEmail = _dashEmail.endsWith("@bazookabreaks.com");
  if (!_isTeamEmail || !userRole) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"#000", fontFamily:"'Trebuchet MS',sans-serif", flexDirection:"column", gap:16, padding:24, textAlign:"center" }}>
      <div style={{ fontSize:32 }}>🚫</div>
      <div style={{ fontSize:18, fontWeight:800, color:"#E8317A" }}>Access Denied</div>
      <div style={{ fontSize:13, color:"var(--bz-ink-3)", maxWidth:340 }}>This dashboard is for Bazooka Breaks team members only. If you think this is a mistake, contact Devin.</div>
      <button onClick={()=>signOut(auth)} style={{ marginTop:8, background:"transparent", border:"1px solid var(--bz-line-2)", color:"var(--bz-ink-2)", borderRadius:8, padding:"8px 20px", fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>Sign Out</button>
    </div>
  );

  return (
    <div style={{ background:"var(--bz-bg)", minHeight:"100vh", fontFamily:"'Inter','Helvetica Neue','Segoe UI',sans-serif", color:"var(--bz-ink)", overflowX:"hidden" }}>
      <GlobalStyles />
      <BugReporter user={user} />

      {/* Global search overlay */}
      {gOpen && (() => {
        const usedIds = new Set(breaks.map(b=>b.inventoryId));
        const q = gSearch.toLowerCase().trim();
        const results = q.length < 2 ? [] : inventory.filter(c => {
          return (
            c.cardName?.toLowerCase().includes(q) ||
            c.seller?.toLowerCase().includes(q) ||
            c.cardType?.toLowerCase().includes(q) ||
            c.source?.toLowerCase().includes(q) ||
            (usedIds.has(c.id) ? "used" : c.cardStatus==="in_transit" ? "in transit" : "available").includes(q)
          );
        });
        const getStatus = c => usedIds.has(c.id) ? { l:"Used", bg:"#FEE2E2", c:"#991b1b" } : c.cardStatus==="in_transit" ? { l:"In Transit", bg:"#EEF0FB", c:"#7B9CFF" } : { l:"Available", bg:"#0a1a0a", c:"#4ade80" };
        return (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:99999, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-start", paddingTop:80 }}
            onClick={()=>{ setGOpen(false); setGSearch(""); }}>
            <div style={{ background:"var(--bz-s1)", borderRadius:14, width:"100%", maxWidth:620, boxShadow:"0 24px 80px rgba(0,0,0,0.8)", border:"1.5px solid var(--bz-line-2)", overflow:"hidden" }}
              onClick={e=>e.stopPropagation()}>
              <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", borderBottom:"1px solid var(--bz-line)" }}>
                <span style={{ fontSize:20 }}>{"\uD83D\uDD0D"}</span>
                <input autoFocus value={gSearch} onChange={e=>setGSearch(e.target.value)} placeholder="Search inventory by name, seller, type, status..." style={{ flex:1, background:"transparent", border:"none", color:"var(--bz-ink)", fontSize:15, fontFamily:"inherit", outline:"none" }}/>
                <span style={{ fontSize:11, color:"#8a8a8a" }}>ESC to close</span>
              </div>
              <div style={{ maxHeight:400, overflowY:"auto" }}>
                {q.length < 2 ? <div style={{ padding:"24px", textAlign:"center", color:"var(--bz-ink-3)", fontSize:12 }}>Type at least 2 characters to search</div> :
                  results.length === 0 ? <div style={{ padding:"24px", textAlign:"center", color:"var(--bz-ink-3)", fontSize:12 }}>No matches for "{gSearch}"</div> :
                  results.slice(0,20).map((c,i) => {
                    const st = getStatus(c);
                    const cc = CC[c.cardType]||{bg:"#F3F4F6",text:"var(--bz-ink-3)"};
                    return (
                      <div key={c.id} onClick={()=>{ setTab("inventory"); setGOpen(false); setGSearch(""); }} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 16px", borderBottom:"1px solid #111", cursor:"pointer", background:i%2===0?"var(--bz-s1)":"var(--bz-s1)" }} className="inv-row">
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:700, color:"var(--bz-ink)" }}>{c.cardName}</div>
                          <div style={{ fontSize:11, color:"var(--bz-ink-3)", marginTop:2 }}>{c.seller||"--"} · {c.source||"--"} · {c.date||"--"}</div>
                        </div>
                        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:3 }}>
                          {c.marketValue > 0 && <span style={{ fontSize:11, fontWeight:700, color:"#FBBF24" }}>${(c.marketValue||0).toFixed(2)} mkt</span>}
                          {c.costPerCard > 0 && <span style={{ fontSize:11, fontWeight:700, color:"#4ade80" }}>${(c.costPerCard||0).toFixed(2)} cost</span>}
                        </div>
                        <span style={{ background:cc.bg, color:cc.text, fontSize:10, fontWeight:700, borderRadius:5, padding:"2px 7px" }}>{c.cardType}</span>
                        <span style={{ background:st.bg, color:st.c, fontSize:10, fontWeight:700, borderRadius:5, padding:"2px 7px" }}>{st.l}</span>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          </div>
        );
      })()}

      {/* Toast */}
      {toast && (
        <div className="toast" style={{ position:"fixed", bottom:24, right:24, background:"var(--bz-s1)", border:"1.5px solid #4ade80", borderRadius:12, padding:"12px 20px", fontSize:13, fontWeight:700, color:"#4ade80", zIndex:999, boxShadow:"0 8px 32px rgba(0,0,0,0.5)" }}>
          {toast}
        </div>
      )}

      {/* Active scan indicator (when scanning from non-BoBA tab) */}
      {activeScan && activeScan.type === "images" && (
        <div style={{position:"fixed",bottom:24,right:24,zIndex:9999,background:"#0a1a0a",border:"1.5px solid #4ade8044",borderRadius:12,padding:"16px 20px",minWidth:300,boxShadow:"0 8px 40px rgba(0,0,0,0.8)",fontFamily:"'Trebuchet MS',sans-serif"}}>
          <div style={{fontWeight:700,color:"#4ade80",fontSize:13,marginBottom:10}}>{"\uD83D\uDDBC"} {activeScan.type==="images"?"Importing Images...":"Scan active"}</div>
          {activeScan.total > 0 && (
            <div style={{height:6,background:"var(--bz-s2)",borderRadius:3,overflow:"hidden",marginBottom:8}}>
              <div style={{width:`${Math.round(activeScan.current/activeScan.total*100)}%`,height:"100%",background:"linear-gradient(90deg,#4ade80,#7B9CFF)",borderRadius:3,transition:"width 0.3s"}}/>
            </div>
          )}
          <div style={{display:"flex",justifyContent:"space-between",fontSize:11}}>
            <span style={{color:"var(--bz-ink-2)",flex:1,marginRight:8}}>{activeScan.status}</span>
            {activeScan.total > 0 && <span style={{color:"#4ade80",fontWeight:700,flexShrink:0}}>{activeScan.current}/{activeScan.total}</span>}
          </div>
        </div>
      )}

      {/* Nav */}
      {/* ===== FACELIFT SIDEBAR SHELL ===== */}
      <div className="bz-shell">
        {sideOpen && <div onClick={()=>setSideOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:45}} className="bz-overlay"/>}
        {/* Sidebar */}
        <aside className={"bz-side"+(sideOpen?" open":"")}>
          <div className="bz-brand">
            <img src="/Bazooka_Logo_cropped.png" alt="Bazooka" style={{height:30,width:"auto",objectFit:"contain",filter:"drop-shadow(0 3px 12px rgba(232,49,122,0.3))"}}/>
            <span style={{marginLeft:"auto",width:7,height:7,borderRadius:"50%",background:"var(--bz-green)",boxShadow:"0 0 8px var(--bz-green)"}}/>
          </div>
          {(() => {
            const SECTIONS = [
              { title:"Overview", ids:["dashboard","comp","inventory","streams","buyers"] },
              { title:"Money",    ids:["campaigns","chases","performance","finance"] },
              { title:"Ops",      ids:["shipping","broadcaster","messages","supply","checklist","directory","importer"] },
            ];
            const tabById = Object.fromEntries(ALL_TABS.map(t=>[t.id,t]));
            return SECTIONS.map(sec => {
              const items = sec.ids.map(id=>tabById[id]).filter(Boolean);
              if (!items.length) return null;
              return (
                <div key={sec.title}>
                  <div className="bz-navsec">{sec.title}</div>
                  {items.map(t=>(
                    <button key={t.id} className={"bz-navitem"+(tab===t.id?" on":"")}
                      onClick={()=>{ if(t.id==="checklist"){ window.open("/cards","_blank"); } else { setTab(t.id); setSideOpen(false); } }}>
                      <span className="bz-ic">{t.icon}</span>{t.label}
                    </button>
                  ))}
                </div>
              );
            });
          })()}
          <div style={{marginTop:18,paddingTop:14,borderTop:"1px solid var(--bz-line)"}}>
            <button className="bz-navitem" onClick={()=>signOut(auth)} style={{color:"var(--bz-ink-3)"}}>
              <span className="bz-ic">⏻</span>Sign out
            </button>
          </div>
        </aside>

        {/* Main column */}
        <div className="bz-main">
          {/* Top bar */}
          <div className="bz-topbar">
            <div style={{display:"flex",alignItems:"center",gap:12,minWidth:0}}>
              <button className="bz-mobile-menu" onClick={()=>setSideOpen(o=>!o)} style={{background:"var(--bz-s2)",border:"1px solid var(--bz-line)",borderRadius:9,color:"var(--bz-ink)",width:34,height:34,cursor:"pointer",fontSize:16,alignItems:"center",justifyContent:"center"}}>☰</button>
              <div style={{fontSize:17,fontWeight:700,letterSpacing:"-0.02em",color:"var(--bz-ink)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                {(ALL_TABS.find(t=>t.id===tab)?.label)||"Dashboard"}
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <button onClick={()=>setGOpen(p=>!p)} className="bz-search"
                style={{display:"flex",alignItems:"center",gap:7,background:"var(--bz-s1)",border:"1px solid var(--bz-line)",borderRadius:10,padding:"7px 12px",fontSize:12,color:"var(--bz-ink-3)",cursor:"pointer",fontFamily:"inherit"}}>
                <span style={{fontSize:13}}>{"🔍"}</span><span>Search</span>
                <kbd style={{background:"var(--bz-s3)",borderRadius:4,padding:"1px 6px",fontSize:10,color:"var(--bz-ink-3)",fontFamily:"inherit"}}>K</kbd>
              </button>
              {userRole.role === "Admin" && (
                <select value={viewAs} onChange={e=>setViewAs(e.target.value)} style={{background:viewAs?"var(--bz-pink)":"var(--bz-s1)",border:`1px solid ${viewAs?"var(--bz-pink-hot)":"var(--bz-line)"}`,borderRadius:8,color:viewAs?"#fff":"var(--bz-ink-2)",fontSize:11,fontWeight:viewAs?800:400,padding:"6px 8px",fontFamily:"inherit",cursor:"pointer"}} title={viewAs?"Previewing as another role — select Real Role to exit":"Preview another role"}>
                  <option value="">-- Real Role --</option>
                  {Object.entries(ROLES).map(([k,v])=><option key={k} value={k}>{viewAs?"👁 ":""}{v.label} ({k})</option>)}
                </select>
              )}
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{width:26,height:26,borderRadius:"50%",background:"linear-gradient(135deg,var(--bz-pink),var(--bz-violet))",display:"grid",placeItems:"center",fontSize:11,fontWeight:800,color:"#fff"}}>{(user?.displayName||"?").charAt(0)}</span>
                <span style={{fontSize:12,fontWeight:700,color:"var(--bz-ink-2)"}} className="bz-hide-sm">{user?.displayName?.split(" ")[0]}</span>
                <span style={{background:"var(--bz-pink-dim)",color:"var(--bz-pink-hot)",borderRadius:20,padding:"2px 10px",fontSize:10,fontWeight:800,letterSpacing:"0.5px"}}>{effectiveRole.label}</span>
              </div>
            </div>
          </div>

      {/* Tab content */}
      <div className="tab-content" style={{ padding:"16px", maxWidth:1500, margin:"0 auto", position:"relative", zIndex:1 }}>
        {tab==="dashboard"  && <Dashboard   inventory={inventory} breaks={breaks} user={effectiveUser} userRole={effectiveRole} streams={streams} historicalData={historicalData} onSaveHistorical={handleSaveHistorical} onDeleteHistorical={handleDeleteHistorical} payStubs={payStubs} onDismissPayStub={handleDismissPayStub} quotes={quotes} onDismissQuoteNotif={handleDismissQuoteNotif} cardPools={cardPools} imcAdjustmentsData={imcAdjustmentsData} onSaveImcAdjustments={handleSaveImcAdjustments} plannedStreams={plannedStreams}/>}
        {tab==="comp"       && (CAN_VIEW_LOT_COMP.includes(effectiveRole.role) ? <LotComp defaultMode={compMode} onAccept={handleAccept} onSaveComp={handleSaveComp} onDeleteComp={handleDeleteComp} comps={comps} user={effectiveUser} userRole={effectiveRole} onSaveQuote={handleSaveQuote} quotes={quotes} onCloseQuote={handleCloseQuote} onReopenQuote={handleReopenQuote} onBazookaCounter={handleBazookaCounter} cardPools={cardPools} onDismissQuoteNotif={handleDismissQuoteNotif} bobaCards={bobaCards}/> : <AccessDenied msg="Lot Comp is for Admin and Procurement only." />)}
        {tab==="history"    && <RepQuoteHistory comps={comps} user={effectiveUser} userRole={effectiveRole} />}
        {tab==="inventory"  && <Inventory defaultTab={invTabDefault}   inventory={inventory} breaks={breaks} onRemove={handleRemove} onBulkRemove={handleBulkRemove} onSaveCardCost={handleSaveCardCost} onPutBack={handlePutBack} user={effectiveUser} userRole={effectiveRole} lotTracking={lotTracking} onSaveLotTracking={handleSaveLotTracking} lotNotes={lotNotes} onSaveLotNotes={handleSaveLotNotes} onDeleteLot={handleDeleteLot} shipments={shipments} productUsage={productUsage} onSaveShipment={handleSaveShipment} onDeleteShipment={handleDeleteShipment} skuPrices={skuPrices} onSaveSkuPrices={handleSaveSkuPrices} skuPriceHistory={skuPriceHistory} onDeleteProductUsage={handleDeleteProductUsage} cardPools={cardPools} onSavePool={handleSavePool} onDeletePool={handleDeletePool} onLogPoolOut={handleLogPoolOut} onAddToPool={handleAddToPool} onAdd={handleAddBreak} onBulkAdd={handleBulkAddBreak} streams={streams} bobaCards={bobaCards}/>}
        {tab==="streams"    && <Streams defaultStreamTab={streamTabDefault}     inventory={inventory} breaks={breaks} onAdd={handleAddBreak} onBulkAdd={handleBulkAddBreak} onDeleteBreak={handleDeleteBreak} user={effectiveUser} userRole={effectiveRole} streams={streams} onSaveStream={handleSaveStream} onDeleteStream={handleDeleteStream} productUsage={productUsage} onSaveProductUsage={handleSaveProductUsage} shipments={shipments} skuPrices={skuPrices} historicalData={historicalData} onSavePayStub={handleSavePayStub} onUpsertBuyers={handleUpsertBuyers} payStubs={payStubs} onDeletePayStub={handleDeletePayStub} cardPools={cardPools} imcFormUrl={imcFormUrl} onSaveImcFormUrl={handleSaveImcFormUrl} plannedStreams={plannedStreams} bobaCards={bobaCards} csvImports={csvImports}/>}
        {tab==="buyers"     && <BuyersCRM defaultTab={buyerTabDefault}   buyers={buyers} csvImports={csvImports} onDeleteImport={handleDeleteCsvImport} onClearAll={handleClearAllBuyers} userRole={effectiveRole} streams={streams}/>}
        {tab==="campaigns"  && <CampaignTracker buyers={buyers} streams={streams}/>}
        {tab==="chases"     && <ChaseManager user={user} userRole={effectiveRole} bobaCards={bobaCards}/>}
        {tab==="performance"&& <Performance defaultPeriod={periodDefault} defaultPerfTab={perfTabDefault} breaks={breaks} user={effectiveUser} userRole={effectiveRole} streams={streams} buyers={buyers} historicalData={historicalData}/>}
        {tab==="finance"    && <Finance streams={streams} userRole={effectiveRole} quotes={quotes}/>}
        {tab==="shipping"   && <ShippingHub userRole={effectiveRole} streams={streams}/>}
        {tab==="broadcaster" && <BroadcasterNotes cards={bobaCards} />}
        {tab==="directory"  && <CompanyDirectory userRole={effectiveRole}/>}
        {tab==="messages"   && <InternalMessages user={user} userRole={effectiveRole}/>}
        {tab==="supply"     && <SupplyLinks user={user} userRole={effectiveRole}/>}
        {tab==="importer"   && <CardSetImporter userRole={effectiveRole}/>}
      </div>
        </div>{/* /bz-main */}
      </div>{/* /bz-shell */}
    </div>
  );
}
